/* ===== app-utils.js ===== */
const { useState, useEffect, useCallback, useRef, useMemo } = React;
window.useState = useState; window.useEffect = useEffect; window.useCallback = useCallback; window.useRef = useRef; window.useMemo = useMemo;

// System-wide error reporting. Posts to /api/errors/log fire-and-forget;
// the server dedupes (10-min, per-hash+user) and rate-caps (20/hr/subscriber)
// before sending the system_error alert email. Safe to call from anywhere.
window.__reportError = function(info) {
  try {
    // Rate-limit (429) and auth (401) failures are operational noise, not bugs — and each
    // report BURNS the same per-IP request budget that's already exhausted during a 429
    // storm (2026-07-13 lockout: logged-out restored tabs + their /errors/log posts starved
    // the login endpoint). Skip them, and cap reporting at one post per 5s per tab — the
    // __reportingError flag below only stops CONCURRENT posts, not rapid sequential ones.
    var sc = info && info.status_code;
    if (sc === 429 || sc === 401) return;
    var now = Date.now();
    if (window.__lastErrorReportAt && (now - window.__lastErrorReportAt) < 5000) return;
    var body = {
      area: info && info.area || window.__currentPage || (location.hash || location.pathname || 'unknown'),
      source: info && info.source || 'client',
      message: info && info.message != null ? String(info.message) : '',
      stack: info && info.stack ? String(info.stack) : null,
      url: location.href,
      method: info && info.method || null,
      status_code: info && info.status_code || null,
    };
    if (window.__reportingError) return;
    window.__reportingError = true;
    window.__lastErrorReportAt = now;
    fetch('/api/errors/log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    }).catch(function() { /* reporting is best-effort — a failed report must never become its own unhandled rejection */ })
      .finally(function() { window.__reportingError = false; });
  } catch (e) { /* swallow — never let error reporting break the UI */ }
};

window.addEventListener('error', function(ev) {
  if (ev && ev.target && ev.target !== window) return; // skip resource-load failures
  window.__reportError({
    source: 'unhandled',
    message: ev && ev.message ? ev.message : 'window.onerror',
    stack: ev && ev.error && ev.error.stack ? ev.error.stack : null,
  });
});

window.addEventListener('unhandledrejection', function(ev) {
  var reason = ev && ev.reason;
  var msg = reason && reason.message ? reason.message : String(reason || 'unhandledrejection');
  var stack = reason && reason.stack ? reason.stack : null;
  var info = { source: 'unhandled', message: msg, stack: stack };
  // If thrown by api() / apiUpload(), surface which call failed so error_log
  // captures the endpoint, method, and status — minified stacks alone don't.
  if (reason && reason.apiPath) {
    info.method = reason.apiMethod;
    info.status_code = reason.apiStatus;
    info.message = reason.apiMethod + ' ' + reason.apiPath + ' → ' + msg;
  }
  window.__reportError(info);
});

// Chart.js hover fix under the app-wide body zoom (index.html). Chrome reports
// MouseEvent offsetX/offsetY in visual (zoomed) px, but Chart.js hit-tests them against
// its layout-px canvas size, so tooltips/highlights land ~25% right/below the cursor and
// the far edge of every chart goes dead. Normalize offsets on canvas-targeted mouse
// events in the capture phase, before Chart.js's own canvas listeners read them:
// local = (client − rect) × (layoutSize / visualSize). Self-measuring (no APP_ZOOM
// dependency) and a no-op at zoom 1 or on browsers that already report layout px.
(function() {
  function fixCanvasOffsets(e) {
    var t = e.target;
    if (!t || t.tagName !== 'CANVAS') return;
    var rect = t.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    var sx = t.offsetWidth / rect.width, sy = t.offsetHeight / rect.height;
    if (Math.abs(sx - 1) < 0.001 && Math.abs(sy - 1) < 0.001) return;
    var ox = (e.clientX - rect.left) * sx, oy = (e.clientY - rect.top) * sy;
    Object.defineProperty(e, 'offsetX', { get: function() { return ox; }, configurable: true });
    Object.defineProperty(e, 'offsetY', { get: function() { return oy; }, configurable: true });
  }
  ['mousemove', 'mouseout', 'click', 'mousedown', 'mouseup'].forEach(function(type) {
    document.addEventListener(type, fixCanvasOffsets, true);
  });
})();

// APP_ZOOM self-heal: the global is inlined in index.html, but a session running a
// STALE cached index.html (served after the body-zoom CSS landed but before the global
// did) gets the CURRENT bundle with APP_ZOOM undefined — every ÷APP_ZOOM popover
// compensation then no-ops and fixed menus land ~25% down/right of their button.
// Derive it from the computed body zoom so the bundle never depends on the HTML copy.
if (!window.APP_ZOOM) {
  var _bodyZoom = parseFloat(getComputedStyle(document.body).zoom);
  window.APP_ZOOM = (_bodyZoom && isFinite(_bodyZoom)) ? _bodyZoom : 1;
}

// Render chart canvases at visual resolution: with the body zoomed 1.25×, Chart.js's
// default backing store (layout px × devicePixelRatio) gets upscaled by the zoom and
// text goes soft. Hit-testing is unaffected (Chart.js divides canvas.width back out).
if (window.Chart) {
  Chart.defaults.devicePixelRatio = (window.devicePixelRatio || 1) * (window.APP_ZOOM || 1);
}

// React error boundary — catches render/lifecycle crashes in the wrapped subtree,
// reports the REAL error + React componentStack (immune to the cross-origin "Script error."
// mask), and shows a non-fatal fallback instead of white-screening the app. Wrapped
// per-page and keyed on the route, so navigating away clears a tripped boundary.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: null };
    this.handleRetry = this.handleRetry.bind(this);
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, message: (error && error.message) ? error.message : 'Unexpected error' };
  }
  componentDidCatch(error, info) {
    try {
      var cs = (info && info.componentStack) ? info.componentStack : '';
      var stk = (error && error.stack ? error.stack : String(error)) + (cs ? '\n--- React componentStack ---' + cs : '');
      if (window.__reportError) window.__reportError({
        source: 'unhandled',
        area: this.props.area || window.__currentPage || 'unknown',
        message: (error && error.message) ? error.message : String(error),
        stack: stk,
      });
    } catch (e) { /* the boundary's own reporting must never throw */ }
  }
  handleRetry() { this.setState({ hasError: false, message: null }); }
  render() {
    if (!this.state.hasError) return this.props.children;
    var btn = { padding: '8px 16px', borderRadius: 8, border: '1px solid #2A2E3A', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 600 };
    return React.createElement('div', { style: { padding: 24, maxWidth: 560, margin: '40px auto', background: '#171A23', border: '1px solid #2A2E3A', borderRadius: 12, color: '#E8EAF0' } },
      React.createElement('div', { style: { fontSize: 18, fontWeight: 700, marginBottom: 8 } }, 'Something went wrong on this page'),
      React.createElement('div', { style: { fontSize: 14, color: '#9AA0B5', marginBottom: 18, wordBreak: 'break-word' } }, this.state.message || 'An unexpected error occurred. The team has been notified.'),
      React.createElement('div', { style: { display: 'flex', gap: 10 } },
        React.createElement('button', { onClick: this.handleRetry, style: Object.assign({}, btn, { background: '#4F6EF7', borderColor: '#4F6EF7', color: '#fff' }) }, 'Try again'),
        React.createElement('button', { onClick: function() { location.reload(); }, style: Object.assign({}, btn, { background: 'transparent', color: '#E8EAF0' }) }, 'Reload page')
      )
    );
  }
}
window.ErrorBoundary = ErrorBoundary;

function LIcon({ name, size, color, spin, style: extraStyle }) {
  var pascal = name.split('-').map(function(s) { return s.charAt(0).toUpperCase() + s.slice(1); }).join('');
  var Icon = window.LucideReact && window.LucideReact[pascal];
  if (!Icon) return null;
  var s = spin ? { animation: 'spin 1s linear infinite', ...(extraStyle || {}) } : extraStyle;
  return React.createElement(Icon, { size: size || 16, color: color, style: s });
}
window.LIcon = LIcon;

// Custom hover tooltip that renders as real DOM (not the native `title`
// overlay). Visible in screen captures and streams; doesn't disappear on
// modifier-key press. Use this in place of `title=` whenever the user
// might need to screenshot or share the tooltip content.
function Tooltip({ text, children, style, wrapperStyle }) {
  var [visible, setVisible] = useState(false);
  var [pos, setPos] = useState({ x: 0, y: 0 });
  if (!text) return React.createElement('span', { style: style }, children);
  function show(e) {
    var rect = e.currentTarget.getBoundingClientRect();
    // gBCR returns visual px; fixed-position styles on the zoomed body are in zoomed px.
    var z = window.APP_ZOOM || 1;
    setPos({ x: (rect.left + rect.width / 2) / z, y: rect.top / z });
    setVisible(true);
  }
  function hide() { setVisible(false); }
  var spanStyle = Object.assign({ display: 'inline-block', position: 'relative' }, style || {});
  return React.createElement('span', { onMouseEnter: show, onMouseLeave: hide, onFocus: show, onBlur: hide, style: spanStyle },
    children,
    visible && React.createElement('div', {
      role: 'tooltip',
      style: Object.assign({
        position: 'fixed',
        top: pos.y - 8, left: pos.x,
        transform: 'translate(-50%, -100%)',
        background: '#1F2937', color: '#F9FAFB',
        padding: '8px 12px', borderRadius: 6,
        border: '1px solid #374151',
        fontSize: 12, lineHeight: 1.5,
        whiteSpace: 'pre-line', textAlign: 'left',
        maxWidth: 360, minWidth: 60,
        zIndex: 10000, pointerEvents: 'none',
        boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
        fontWeight: 400,
      }, wrapperStyle || {})
    }, text)
  );
}
window.Tooltip = Tooltip;

const API_BASE = '/api';

var themes = {
  dark: {
    bg: '#0F1117', surface: '#1A1D27', surfaceHover: '#22252F',
    border: '#2A2D3A', borderFocus: '#4F6EF7', cardShadow: 'none',
    text: '#E8EAF0', textMuted: '#8B8FA3', textDim: '#5C6078',
    accent: '#4F6EF7', accentHover: '#3D5CE5', accentSoft: 'rgba(79,110,247,0.12)',
    success: '#34D399', successSoft: 'rgba(52,211,153,0.12)',
    warning: '#FBBF24', warningSoft: 'rgba(251,191,36,0.12)',
    danger: '#F87171', dangerSoft: 'rgba(248,113,113,0.12)',
    purple: '#A78BFA', purpleSoft: 'rgba(167,139,250,0.12)',
  },
  light: {
    bg: '#F6F7F9', surface: '#FFFFFF', surfaceHover: '#F3F4F6',
    border: '#E5E7EB', borderFocus: '#4F6EF7', cardShadow: '0 1px 3px rgba(16,24,40,0.06), 0 1px 2px rgba(16,24,40,0.04)',
    text: '#111827', textMuted: '#4B5563', textDim: '#6B7280',
    accent: '#4F6EF7', accentHover: '#3D5CE5', accentSoft: 'rgba(79,110,247,0.10)',
    success: '#059669', successSoft: 'rgba(5,150,105,0.10)',
    warning: '#D97706', warningSoft: 'rgba(217,119,6,0.10)',
    danger: '#DC2626', dangerSoft: 'rgba(220,38,38,0.10)',
    purple: '#7C3AED', purpleSoft: 'rgba(124,58,237,0.10)',
  }
};

// ─── SIDENAV: shared sidebar style kit ───
// Single source of truth for the left nav across ALL shells (main SPA, ads.html,
// ship.html). The bundles concatenate app-utils.js first with shared top-level
// scope, so every shell must reference these instead of hand-copied style
// objects — copies drifted apart until normalized 2026-08-11. The sidebar is
// deliberately light-themed in every shell (hardcoded colors, not C.*).
// Sidebar widths — defined ONCE here; anything that positions against the
// sidebar (full-screen product/vendor editor overlays) must use these, never a literal.
var SIDENAV_NAV_W = 216;   // pinned sidebar + hover drawer (216 so 15px 'FBA Reconciliation' fits on one line)
var SIDENAV_RAIL_W = 56;   // unpinned icon rail
var SIDENAV = {
  ICON: 16,        // icon size in nav rows
  CHEVRON: 14,     // chevron-down icon size
  NAV_W: SIDENAV_NAV_W,
  RAIL_W: SIDENAV_RAIL_W,
  nav: { width: SIDENAV_NAV_W, background: '#FFFFFF', borderRight: '1px solid #E5E7EB', padding: '16px 10px', display: 'flex', flexDirection: 'column', flexShrink: 0, overflow: 'hidden', zIndex: 10 },
  rail: { width: SIDENAV_RAIL_W, background: '#FFFFFF', borderRight: '1px solid #E5E7EB', padding: '16px 6px', display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, overflow: 'hidden', zIndex: 10 },
  drawer: function(hover) { return { position: 'fixed', top: 0, left: 0, bottom: 0, width: SIDENAV_NAV_W, background: '#FFFFFF', borderRight: '1px solid #E5E7EB', padding: '16px 10px', display: 'flex', flexDirection: 'column', overflow: 'hidden', zIndex: 1100, boxShadow: hover ? '4px 0 16px rgba(0,0,0,0.1)' : 'none', opacity: hover ? 1 : 0, transition: 'opacity 0.15s ease, box-shadow 0.15s ease', pointerEvents: hover ? 'auto' : 'none' }; },
  logoBox: { padding: '0 12px 12px', borderBottom: '1px solid #E5E7EB', marginBottom: 10, textAlign: 'center', minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  logoImg: { height: 44, maxWidth: '100%', objectFit: 'contain', display: 'block' },
  railLogoBox: { padding: '0 0 12px', borderBottom: '1px solid #E5E7EB', marginBottom: 10, width: '100%', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 44 },
  railLogoImg: { width: 44, height: 44, objectFit: 'contain', borderRadius: 6 },
  sectionLabel: { padding: '0 10px 8px', fontSize: 10, fontWeight: 500, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.6 },
  scroller: { flex: 1, overflowY: 'auto', overflowX: 'hidden', minHeight: 0 },
  railList: { flex: 1, display: 'flex', flexDirection: 'column', width: '100%' },
  item: function(active) { return { display: 'flex', alignItems: 'center', gap: 8, padding: '5px 9px', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 15, lineHeight: '21px', fontWeight: 500, fontFamily: 'inherit', width: '100%', textAlign: 'left', marginBottom: 3, background: active ? 'rgba(79,110,247,0.10)' : 'transparent', color: active ? '#4F6EF7' : '#374151', transition: 'background 0.2s', textDecoration: 'none' }; },
  // minHeight 31 = item's 21px line + 10px padding, so a collapsed rail row sits on the same 34px pitch as an expanded row (9-05)
  railItem: function(active) { return { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '5px 0', minHeight: 31, boxSizing: 'border-box', border: 'none', borderRadius: 8, cursor: 'pointer', width: '100%', marginBottom: 3, background: active ? 'rgba(79,110,247,0.10)' : 'transparent', color: active ? '#4F6EF7' : '#374151', textDecoration: 'none' }; },
  chevron: function(expanded) { return { fontSize: 10, color: '#9CA3AF', transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', padding: '2px 4px', cursor: 'pointer', display: 'flex', alignItems: 'center' }; },
  childWrap: function(expanded) { return { overflow: 'hidden', transition: 'max-height 0.3s cubic-bezier(0.4,0,0.2,1), margin 0.3s cubic-bezier(0.4,0,0.2,1)', maxHeight: expanded ? 500 : 0, paddingLeft: 18, marginBottom: expanded ? 4 : 0 }; },
  child: function(active) { return { display: 'flex', alignItems: 'center', gap: 7, padding: '4px 9px', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: active ? 600 : 400, fontFamily: 'inherit', width: '100%', textAlign: 'left', marginBottom: 2, background: active ? 'rgba(79,110,247,0.08)' : 'transparent', color: active ? '#4F6EF7' : '#4B5563', borderLeft: active ? '2px solid #4F6EF7' : '2px solid transparent', textDecoration: 'none' }; },
  bottomWrap: { borderTop: '1px solid #E5E7EB', paddingTop: 8, marginTop: 8 },
  railBottomWrap: { borderTop: '1px solid #E5E7EB', paddingTop: 8, marginTop: 8, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  bottomBtn: function(opts) { opts = opts || {}; return { display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 500, fontFamily: 'inherit', width: '100%', textAlign: 'left', background: 'transparent', color: opts.active ? '#4F6EF7' : '#374151', textDecoration: 'none', marginTop: opts.topGap ? 4 : 0 }; },
  railBottomBtn: function(opts) { opts = opts || {}; return { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '5px 0', border: 'none', borderRadius: 8, cursor: 'pointer', width: '100%', background: 'transparent', color: opts.active ? '#4F6EF7' : '#374151', textDecoration: 'none', marginTop: opts.topGap ? 4 : 0 }; },
  divider: { borderTop: '1px solid #E5E7EB', margin: '10px 0' },
  // the "opens a new tab" arrow (arrow-up-right, 13px) at the right edge of a sidebar row that is a
  // real target=_blank link: Customer Service / Advertising / Shipping in the main app, AtharvaToolz
  // in the three sub-app shells (Anthony 2026-10-13)
  newTab: { color: '#9CA3AF', flexShrink: 0, marginLeft: 'auto' },
};
// Hover/transition rules for the kit's rows — one source for the main SPA, ads and shipping
// shells (ads had no hover at all before 9-05). Rows opt in with className side-nav-btn /
// side-nav-child / side-bottom-btn.
(function() {
  if (typeof document === 'undefined' || document.getElementById('sidenav-kit-styles')) return;
  var st = document.createElement('style');
  st.id = 'sidenav-kit-styles';
  st.textContent =
    '.side-nav-btn{transition:background 0.15s ease!important}' +
    '.side-nav-btn:hover{background:rgba(79,110,247,0.06)!important}' +
    '.side-nav-child{transition:background 0.15s ease!important}' +
    '.side-nav-child:hover{background:rgba(79,110,247,0.06)!important}' +
    '.side-bottom-btn{transition:background 0.15s ease!important}' +
    '.side-bottom-btn:hover{background:rgba(0,0,0,0.04)!important}';
  document.head.appendChild(st);
})();

// ─── Responsive: phones and tablets (UX review item 1, 2026-09-27) ───
// Below VIEWPORT_NARROW_MAX every shell drops the pinned sidebar / 56px icon rail for a 48px
// top bar + hamburger drawer (NarrowNav below), and pages stack their card grids. Widths are
// the CSS px the LAYOUT sees: window.innerWidth is visual px and the body is zoomed by
// APP_ZOOM (index.html), so layout width = innerWidth / zoom. Wide (>= 900) renders exactly
// as before — every narrow branch is behind isNarrow, and the rg-* classes below only act
// under html.vp-narrow.
var VIEWPORT_NARROW_MAX = 900;
var VIEWPORT_PHONE_MAX = 600;
function viewportFlags(innerWidth, zoom) {
  var z = (typeof zoom === 'number' && isFinite(zoom) && zoom > 0) ? zoom : 1;
  // no usable width (hidden iframe, test harness): assume a desktop so nothing changes
  var w = (typeof innerWidth === 'number' && isFinite(innerWidth) && innerWidth > 0) ? innerWidth / z : 1280;
  return { width: w, isNarrow: w < VIEWPORT_NARROW_MAX, isPhone: w < VIEWPORT_PHONE_MAX };
}
function _readViewport() {
  return viewportFlags(typeof window !== 'undefined' ? window.innerWidth : 0, typeof window !== 'undefined' ? (window.APP_ZOOM || 1) : 1);
}
// One module-level listener (resize + orientationchange, throttled to 150 ms) feeds every
// useViewport() and keeps html.vp-narrow / html.vp-phone in sync for the CSS helpers.
var _vpState = _readViewport();
var _vpSubs = [];
function _vpApply() {
  var n = _readViewport();
  var changed = n.isNarrow !== _vpState.isNarrow || n.isPhone !== _vpState.isPhone;
  _vpState = n;
  try {
    var cl = document.documentElement.classList;
    cl.toggle('vp-narrow', n.isNarrow);
    cl.toggle('vp-phone', n.isPhone);
  } catch (e) {}
  if (changed) _vpSubs.slice().forEach(function(fn) { try { fn(n); } catch (e) {} });
}
(function() {
  if (typeof window === 'undefined' || !window.addEventListener) return;
  var timer = null;
  var onChange = function() {
    if (timer) return;
    timer = setTimeout(function() { timer = null; _vpApply(); }, 150);
  };
  window.addEventListener('resize', onChange);
  window.addEventListener('orientationchange', onChange);
  _vpApply();
})();
// Re-renders only when a breakpoint flag flips (not on every pixel of a resize drag), so
// `width` is the layout width at the last flip — use the flags for layout decisions.
function useViewport() {
  var _s = useState(_vpState); var vp = _s[0], setVp = _s[1];
  useEffect(function() {
    var fn = function(n) { setVp(n); };
    _vpSubs.push(fn);
    if (_vpState.isNarrow !== vp.isNarrow || _vpState.isPhone !== vp.isPhone) setVp(_vpState);
    return function() { var i = _vpSubs.indexOf(fn); if (i !== -1) _vpSubs.splice(i, 1); };
  }, []);
  return vp;
}
window.viewportFlags = viewportFlags;
window.useViewport = useViewport;
// Non-hook read for code that positions against the sidebar (full-screen editors): on narrow
// widths there is no sidebar, so they start at left 0.
function isNarrowViewport() { return !!_vpState.isNarrow; }
window.isNarrowViewport = isNarrowViewport;

// Top-bar title from document.title ("AtharvaToolz | PO Detail" → "PO Detail",
// "Batch 12 · AtharvaToolz Shipping" → "Batch 12").
function shortAppTitle(t) {
  var s = String(t || '').replace(/^AtharvaToolz\s*\|\s*/, '').replace(/\s*·\s*AtharvaToolz\b.*$/, '');
  if (/^AtharvaToolz\b/.test(s)) s = s.replace(/^AtharvaToolz\s*/, '');
  return s || 'AtharvaToolz';
}
function useDocTitle() {
  var _t = useState(function() { return typeof document !== 'undefined' ? document.title : ''; });
  var title = _t[0], setTitle = _t[1];
  useEffect(function() {
    var sync = function() { setTitle(document.title); };
    sync();
    var el = document.querySelector('title');
    if (!el || typeof MutationObserver === 'undefined') return;
    var mo = new MutationObserver(sync);
    mo.observe(el, { childList: true, characterData: true, subtree: true });
    return function() { mo.disconnect(); };
  }, []);
  return title;
}

// Narrow-width chrome shared by every shell: a 48px top bar (hamburger "Open Menu", the page
// title, the shell's own top-area controls in `right`) and the full sidebar content as a
// slide-in drawer over a dimmed backdrop. Backdrop tap, Escape, a tapped link, or any change of
// `navKey` (= navigation) closes it. The pin preference is never touched here.
var NARROW_TOPBAR_H = 48;
function narrowDrawerStyle() {
  return { position: 'fixed', top: 0, left: 0, bottom: 0, width: 288, maxWidth: 'calc(86vw * var(--invzoom, 1))', background: '#FFFFFF', borderRight: '1px solid #E5E7EB', padding: '14px 10px', display: 'flex', flexDirection: 'column', overflow: 'hidden', zIndex: 1160, boxShadow: '6px 0 24px rgba(0,0,0,0.25)', boxSizing: 'border-box', animation: 'narrowDrawerIn 0.18s ease' };
}
function NarrowNav(props) {
  var _o = useState(false); var open = _o[0], setOpen = _o[1];
  var docTitle = useDocTitle();
  useEffect(function() { setOpen(false); }, [props.navKey]);
  useEffect(function() {
    if (!open) return;
    var onKey = function(e) { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return function() { document.removeEventListener('keydown', onKey); };
  }, [open]);
  var close = function() { setOpen(false); };
  var title = props.title || shortAppTitle(docTitle);
  return React.createElement(React.Fragment, null,
    React.createElement('header', { className: 'narrow-topbar', style: { height: NARROW_TOPBAR_H, minHeight: NARROW_TOPBAR_H, display: 'flex', alignItems: 'center', gap: 6, padding: '0 8px 0 4px', background: C.surface, borderBottom: '1px solid ' + C.border, flexShrink: 0, boxSizing: 'border-box', position: 'relative', zIndex: 20 } },
      React.createElement('button', { type: 'button', 'aria-label': 'Open Menu', 'aria-expanded': open, onClick: function() { setOpen(true); },
        style: { width: 44, height: 44, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', borderRadius: 8, cursor: 'pointer', color: C.text, padding: 0, flexShrink: 0 } },
        React.createElement(LIcon, { name: 'menu', size: 22 })),
      React.createElement('div', { style: { flex: 1, minWidth: 0, fontSize: 16, fontWeight: 600, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }, title),
      props.right || null),
    open && React.createElement('div', { className: 'narrow-backdrop', onClick: close, 'aria-hidden': true, style: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1150 } }),
    open && React.createElement('nav', { className: 'narrow-drawer', role: 'dialog', 'aria-modal': true, 'aria-label': 'Menu',
      // a tapped link navigates (or opens a new tab): close; group headers are buttons and stay open
      onClickCapture: function(e) { var a = e.target && e.target.closest ? e.target.closest('a[href]') : null; if (a) setOpen(false); },
      style: narrowDrawerStyle() },
      React.createElement('button', { type: 'button', 'aria-label': 'Close Menu', onClick: close,
        style: { position: 'absolute', top: 8, right: 8, width: 36, height: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', borderRadius: 8, cursor: 'pointer', color: '#6B7280', zIndex: 2 } },
        React.createElement(LIcon, { name: 'x', size: 18 })),
      props.drawer));
}
window.NarrowNav = NarrowNav;
window.shortAppTitle = shortAppTitle;
// Style of a shell's <main> on narrow widths: full width, 12px sides, no horizontal page
// scroll (tables keep their own scroll containers).
function narrowMainStyle(extra) {
  return Object.assign({ flex: 1, minWidth: 0, minHeight: 0, width: '100%', padding: 12, boxSizing: 'border-box', overflowX: 'hidden', overflowY: 'auto' }, extra || {});
}
window.narrowMainStyle = narrowMainStyle;
(function() {
  if (typeof document === 'undefined' || document.getElementById('narrow-layout-styles')) return;
  var st = document.createElement('style');
  st.id = 'narrow-layout-styles';
  st.textContent =
    '@keyframes narrowDrawerIn{from{transform:translateX(-100%)}to{transform:translateX(0)}}' +
    // touch targets: every drawer row at least 40px tall
    '.narrow-drawer .side-nav-btn,.narrow-drawer .side-nav-child,.narrow-drawer .side-bottom-btn,.narrow-drawer .snav{min-height:40px!important;box-sizing:border-box}' +
    // open groups: the 500px max-height cap (SIDENAV.childWrap) would clip taller 40px rows
    '.narrow-drawer div[style*="max-height: 500px"]{max-height:none!important}' +
    // page helpers — only under html.vp-narrow, so wide layouts are untouched
    '.vp-narrow .rg-stack{grid-template-columns:minmax(0,1fr)!important}' +
    '.vp-narrow .rg-2x2{grid-template-columns:repeat(2,minmax(0,1fr))!important}' +
    '.vp-narrow .rg-wrap{flex-wrap:wrap!important}' +
    '.vp-narrow .rg-scrollx{overflow-x:auto!important;overflow-y:hidden!important}' +
    // tabs keep their width and their underline (the -2px overlap would be clipped by the scroller)
    '.vp-narrow .rg-scrollx>*{flex-shrink:0!important;white-space:nowrap!important;margin-bottom:0!important}';
  document.head.appendChild(st);
})();

// C is a mutable reference that always points to current theme
var C = Object.assign({}, themes.dark);
var _currentTheme = 'dark';

function setThemeColors(name) {
  _currentTheme = name;
  // Expose the active theme on <html> so plain CSS can adapt (e.g. scrollbar colors: white in
  // dark mode, dark in light mode).
  try { document.documentElement.setAttribute('data-theme', name); } catch (e) {}
  var t = themes[name] || themes.dark;
  Object.keys(t).forEach(function(k) { C[k] = t[k]; });
  // Update statusColors in-place
  var sc = {
    draft: { bg: C.warningSoft, text: C.warning },
    confirmed: { bg: 'rgba(20,184,166,0.12)', text: '#14B8A6' },
    shipped: { bg: C.accentSoft, text: C.accent },
    'checked-in': { bg: C.purpleSoft, text: C.purple },
    received: { bg: C.successSoft, text: C.success },
    closed: { bg: 'rgba(139,143,163,0.12)', text: C.textMuted },
    cancelled: { bg: C.dangerSoft, text: C.danger },
  };
  Object.keys(sc).forEach(function(k) { statusColors[k] = sc[k]; });
  _updateDtStyles();
}

// Inject interactive styles for DataTable hover/focus effects
var _dtStyleEl = document.createElement('style');
_dtStyleEl.id = 'dt-interactive-styles';
document.head.appendChild(_dtStyleEl);
function _updateDtStyles() {
  _dtStyleEl.textContent = [
    '.dt-input:focus { border-color: ' + C.accent + ' !important; box-shadow: 0 0 0 2px ' + C.accentSoft + ' !important; outline: none; }',
    '.dt-select:hover, .dt-btn:hover { border-color: ' + C.accent + ' !important; background: ' + C.surfaceHover + ' !important; }',
    '.dt-select:focus { border-color: ' + C.accent + ' !important; box-shadow: 0 0 0 2px ' + C.accentSoft + ' !important; outline: none; }',
    '.dt-row-btn:hover { background: ' + C.surfaceHover + ' !important; }',
    '.dt-th:hover { color: ' + C.accent + ' !important; }',
    '.dt-filter-btn:hover { background: ' + C.surfaceHover + ' !important; opacity: 1 !important; }',
    '.filter-pill:hover { background: ' + C.accentSoft + ' !important; border-color: ' + C.accent + ' !important; }',
  ].join('\n');
}
_updateDtStyles();

function getThemeName() { return _currentTheme; }

function getStatusColors() {
  return {
    draft: { bg: C.warningSoft, text: C.warning },
    submitted: { bg: 'rgba(59,130,246,0.12)', text: '#3B82F6' },
    approved: { bg: 'rgba(34,197,94,0.12)', text: '#22C55E' },
    confirmed: { bg: 'rgba(20,184,166,0.12)', text: '#14B8A6' },
    next: { bg: 'rgba(6,182,212,0.12)', text: '#06B6D4' },
    shipped: { bg: C.accentSoft, text: C.accent },
    'checked-in': { bg: C.purpleSoft, text: C.purple },
    receiving: { bg: 'rgba(245,158,11,0.12)', text: '#F59E0B' },
    received: { bg: C.successSoft, text: C.success },
    closed: { bg: 'rgba(139,143,163,0.12)', text: C.textMuted },
    cancelled: { bg: C.dangerSoft, text: C.danger },
  };
}

var statusColors = getStatusColors();

// Auth token management (cookie-based — kept for backward compat during transition)
var _authToken = null;
// Auth is cookie-based (httpOnly po_session, sent via credentials:'include'). The token is
// kept in-memory only — never in localStorage, where XSS could exfiltrate it. We also clear
// any token persisted by older builds.
function setAuthToken(token) { _authToken = token; try { localStorage.removeItem('po_token'); } catch (e) {} }
function getAuthToken() { return _authToken; }

async function api(path, options = {}) {
  var method = (options && options.method) || 'GET';
  var headers = { 'Content-Type': 'application/json', ...options.headers };
  const res = await fetch(`${API_BASE}${path}`, { ...options, headers, credentials: 'include' });
  if (res.status === 401) {
    setAuthToken(null); window.location.reload();
    // reload() is async — never resolve, so pending .then(setState) chains can't run
    // with undefined during the teardown window (crashed the PO list — error_log 295).
    return new Promise(function() {});
  }
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({ error: res.statusText }));
    const err = new Error(errBody.error || 'Request failed');
    err.apiPath = path; err.apiMethod = method; err.apiStatus = res.status;
    err.apiBody = errBody;
    throw err;
  }
  return res.json();
}

async function apiUpload(path, file) {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API_BASE}${path}`, { method: 'POST', body: form, credentials: 'include' });
  if (res.status === 401) {
    setAuthToken(null); window.location.reload();
    // reload() is async — never resolve, so pending .then(setState) chains can't run
    // with undefined during the teardown window (crashed the PO list — error_log 295).
    return new Promise(function() {});
  }
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({}));
    const err = new Error(errBody.error || 'Upload failed');
    err.apiPath = path; err.apiMethod = 'POST'; err.apiStatus = res.status;
    throw err;
  }
  return res.json();
}

// Permission helpers
function hasPermission(user, area, level) {
  if (!user) return false;
  if (user.role === 'admin') return true;
  var perms = user.permissions;
  if (!perms || !perms[area]) return false;
  return !!perms[area][level || 'read'];
}
// Legacy role helpers (now permission-aware)
function canEdit(roleOrUser) {
  if (typeof roleOrUser === 'string') return ['admin', 'manager'].includes(roleOrUser);
  return hasPermission(roleOrUser, 'purchase_orders', 'write');
}
function canManageUsers(roleOrUser) {
  if (typeof roleOrUser === 'string') return roleOrUser === 'admin';
  return hasPermission(roleOrUser, 'settings', 'write');
}
function canUpdatePOStatus(roleOrUser, newStatus) {
  if (typeof roleOrUser === 'string') {
    if (roleOrUser === 'admin' || roleOrUser === 'manager') return true;
    if (roleOrUser === 'warehouse' && ['checked-in', 'received'].includes(newStatus)) return true;
    return false;
  }
  return hasPermission(roleOrUser, 'purchase_orders', 'write');
}

function Badge({ status }) {
  var sc = getStatusColors();
  const c = sc[status] || sc.draft || { bg: C.warningSoft, text: C.warning };
  return React.createElement('span', {
    style: { background: c.bg, color: c.text, padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }
  }, status);
}

function Btn({ children, variant = 'default', size = 'md', onClick, disabled, style, className }) {
  const base = { border: 'none', cursor: disabled ? 'not-allowed' : 'pointer', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 0.15s', fontFamily: 'inherit', opacity: disabled ? 0.5 : 1 };
  const sizes = { sm: { padding: '6px 12px', fontSize: 12, borderRadius: 6 }, md: { padding: '8px 16px', fontSize: 13, borderRadius: 8 } };
  const variants = {
    default: { background: C.surface, color: C.text, border: '1px solid ' + C.border },
    primary: { background: C.accent, color: '#fff' },
    danger: { background: C.dangerSoft, color: C.danger },
    ghost: { background: 'transparent', color: C.textMuted },
    success: { background: C.successSoft, color: C.success },
  };
  var v = variant || 'default';
  var hoverHandlers = disabled ? {} : {
    onMouseOver: function(e) { e.currentTarget.style.filter = 'brightness(1.15)'; e.currentTarget.style.transform = 'translateY(-1px)'; },
    onMouseOut: function(e) { e.currentTarget.style.filter = ''; e.currentTarget.style.transform = ''; }
  };
  return React.createElement('button', { onClick, disabled, className, ...hoverHandlers, style: { ...base, ...sizes[size], ...variants[v], ...style } }, children);
}

// Per-SKU "max units per PO" (products.max_po_qty) → the largest whole-case
// multiple at or below it; a max under one case still allows one case. Mirrors
// server/services/po-cap.js — keep the two in sync. Returns null when unset.
function effectivePoCap(maxPoQty, caseQty) {
  var max = parseInt(maxPoQty) || 0;
  if (max <= 0) return null;
  var cq = parseInt(caseQty) || 1;
  if (cq <= 1) return max;
  var whole = Math.floor(max / cq) * cq;
  return whole > 0 ? whole : cq;
}

function Inp({ label, style: extraStyle, ...props }) {
  var baseStyle = { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', outline: 'none', minWidth: 0, transition: 'border-color 0.15s, box-shadow 0.15s' };
  return React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 } },
    label && React.createElement('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' } }, label),
    React.createElement('input', {
      // Keep Chrome's password manager out of credential/settings fields — 'new-password'
      // is the only value it reliably honors on password inputs. Login uses its own inputs.
      autoComplete: props.type === 'password' ? 'new-password' : 'off',
      style: Object.assign({}, baseStyle, extraStyle || {}),
      onFocus: function(e) { e.target.style.borderColor = C.accent; e.target.style.boxShadow = '0 0 0 2px ' + C.accent + '33'; },
      onBlur: function(e) { e.target.style.borderColor = C.border; e.target.style.boxShadow = 'none'; },
      ...props
    })
  );
}

function Sel({ label, options, value, onChange }) {
  return React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 } },
    label && React.createElement('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' } }, label),
    React.createElement('select', {
      style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', outline: 'none', minWidth: 0, transition: 'border-color 0.15s, box-shadow 0.15s' },
      onFocus: function(e) { e.target.style.borderColor = C.accent; e.target.style.boxShadow = '0 0 0 2px ' + C.accent + '33'; },
      onBlur: function(e) { e.target.style.borderColor = C.border; e.target.style.boxShadow = 'none'; },
      value: value || '', onChange
    },
      React.createElement('option', { value: '' }, '— Select —'),
      options.map(o => React.createElement('option', { key: o.value, value: o.value }, o.label))
    )
  );
}

// ─── SEARCHABLE SELECT ───
// Custom dropdown with a type-to-filter box. Native <select> popups misbehave under the
// app-wide body zoom (Chrome keeps snapping the popup's scroll back to the selected option
// on long lists), so browsable pickers use this instead — the list is our own scroll
// container, unaffected by zoom. Hover moves the highlight but never scrolls the list;
// only keyboard navigation scrolls, so wheel-scrolling is never fought over.
// Props: options [{value, label, sub?}], value, onChange(value), placeholder, disabled, style
function SearchSelect({ options, value, onChange, placeholder, disabled, style: extraStyle }) {
  var _o = React.useState(false); var open = _o[0], setOpen = _o[1];
  var _s = React.useState(''); var search = _s[0], setSearch = _s[1];
  var _h = React.useState(-1); var highlight = _h[0], setHighlight = _h[1];
  var rootRef = React.useRef(null);
  var listRef = React.useRef(null);
  var inputRef = React.useRef(null);

  var selected = null;
  for (var i = 0; i < options.length; i++) {
    if (String(options[i].value) === String(value)) { selected = options[i]; break; }
  }

  // Rank matches: labels starting with the query first, then any word starting with it,
  // then substring matches anywhere (label or sub) — so typing "v" jumps straight to the
  // V's instead of surfacing every name that merely contains a v.
  var q = search.trim().toLowerCase();
  var filtered;
  if (!q) {
    filtered = options;
  } else {
    var starts = [], wordStarts = [], contains = [];
    options.forEach(function(o) {
      var label = String(o.label).toLowerCase();
      if (label.indexOf(q) === 0) { starts.push(o); return; }
      var words = label.split(/[^a-z0-9]+/);
      for (var w = 0; w < words.length; w++) {
        if (words[w] && words[w].indexOf(q) === 0) { wordStarts.push(o); return; }
      }
      if (label.indexOf(q) !== -1 || (o.sub && String(o.sub).toLowerCase().indexOf(q) !== -1)) contains.push(o);
    });
    filtered = starts.concat(wordStarts, contains);
  }

  React.useEffect(function() {
    if (!open) return;
    var onDown = function(e) { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return function() { document.removeEventListener('mousedown', onDown); };
  }, [open]);

  // On open: focus the filter box and bring the current selection into view
  React.useEffect(function() {
    if (!open) return;
    if (inputRef.current) inputRef.current.focus();
    var idx = -1;
    for (var j = 0; j < options.length; j++) {
      if (String(options[j].value) === String(value)) { idx = j; break; }
    }
    setHighlight(idx);
    if (idx >= 0 && listRef.current) {
      var el = listRef.current.querySelector('[data-idx="' + idx + '"]');
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  }, [open]);

  var pick = function(opt) {
    setOpen(false); setSearch('');
    if (onChange) onChange(opt.value);
  };

  var moveHighlight = function(delta) {
    if (!filtered.length) return;
    var next = Math.max(0, Math.min(filtered.length - 1, highlight + delta));
    setHighlight(next);
    if (listRef.current) {
      var el = listRef.current.querySelector('[data-idx="' + next + '"]');
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  };
  var onKeyDown = function(e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); moveHighlight(1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); moveHighlight(-1); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlight >= 0 && filtered[highlight]) pick(filtered[highlight]);
      else if (filtered.length === 1) pick(filtered[0]);
    }
    else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); setSearch(''); }
  };

  var triggerStyle = Object.assign({
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, width: '100%',
    background: C.bg, border: '1px solid ' + (open ? C.accent : C.border), color: C.text,
    padding: '8px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', textAlign: 'left',
    cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.55 : 1,
    boxShadow: open ? '0 0 0 2px ' + C.accent + '33' : 'none', transition: 'border-color 0.15s, box-shadow 0.15s'
  }, extraStyle || {});

  return React.createElement('div', { ref: rootRef, style: { position: 'relative', minWidth: 0 } },
    React.createElement('button', {
      type: 'button', disabled: disabled,
      onClick: function() { if (!disabled) { setOpen(!open); setSearch(''); } },
      style: triggerStyle
    },
      React.createElement('span', { style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: selected ? C.text : C.textDim } },
        selected ? selected.label : (placeholder || 'Select…')),
      React.createElement(LIcon, { name: 'chevron-down', size: 14, color: C.textMuted })
    ),
    open && React.createElement('div', {
      style: { position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 1200, background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.35)', overflow: 'hidden' }
    },
      React.createElement('input', {
        ref: inputRef, value: search,
        onChange: function(e) { setSearch(e.target.value); setHighlight(0); },
        onKeyDown: onKeyDown,
        placeholder: 'Type to search…',
        style: { width: '100%', background: C.bg, border: 'none', borderBottom: '1px solid ' + C.border, color: C.text, padding: '9px 12px', fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }
      }),
      React.createElement('div', { ref: listRef, style: { maxHeight: 280, overflowY: 'auto' } },
        filtered.length === 0
          ? React.createElement('div', { style: { padding: 12, color: C.textDim, fontSize: 12 } }, 'No matches')
          : filtered.map(function(o, idx) {
              var isSel = selected && String(o.value) === String(selected.value);
              return React.createElement('div', {
                key: o.value, 'data-idx': idx,
                onClick: function() { pick(o); },
                onMouseEnter: function() { setHighlight(idx); },
                style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 12px', cursor: 'pointer', fontSize: 13, color: isSel ? C.accent : C.text, background: idx === highlight ? C.surfaceHover : 'transparent' }
              },
                React.createElement('div', { style: { minWidth: 0, flex: 1 } },
                  React.createElement('div', { style: { fontWeight: isSel ? 600 : 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, o.label),
                  o.sub ? React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, o.sub) : null
                ),
                isSel ? React.createElement(LIcon, { name: 'check', size: 14, color: C.accent }) : null
              );
            })
      )
    )
  );
}

function Card({ children, style, onClick }) {
  return React.createElement('div', {
    onClick,
    style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 10, padding: 20, boxShadow: C.cardShadow, ...style }
  }, children);
}

// ─── In-app dialogs (Anthony 2026-09-29: "for any confirmation toasts can we make them system
// specific in the center of the screen instead of the chrome default?"): promise-based replacements
// for window.confirm / prompt / alert, drawn like the app's own Modal and hosted once per shell by
// AppDialogHost. Enter = OK, Escape = cancel, the prompt's input autofocuses; several requests queue
// one after another. With no host mounted (a bare print window, a test) they fall back to the
// browser dialogs so nothing can hang.
//   appConfirm(message, { title, okLabel, cancelLabel, danger })  -> Promise<boolean>
//   appPrompt(message, defaultValue, { title, okLabel, placeholder }) -> Promise<string|null>
//   appAlert(message, { title })                                  -> Promise<void>
//   appChoice(message, ['Label', ...], { title, cancelLabel })     -> Promise<index | -1>
var _appDialogQueue = [];
var _appDialogSetter = null;
function _appDialogNext() { if (_appDialogSetter) _appDialogSetter(_appDialogQueue[0] || null); }
function _appDialog(kind, message, opts) {
  opts = opts || {};
  var text = String(message == null ? '' : message);
  if (!_appDialogSetter) {
    if (kind === 'confirm') return Promise.resolve(window.confirm(text));
    if (kind === 'prompt') return Promise.resolve(window.prompt(text, opts.defaultValue == null ? '' : String(opts.defaultValue)));
    window.alert(text);
    return Promise.resolve(kind === 'choice' ? -1 : undefined);
  }
  return new Promise(function(resolve) {
    var done = false;
    var item = { kind: kind, message: text, opts: opts, resolve: function(v) {
      if (done) return; done = true;
      var at = _appDialogQueue.indexOf(item); if (at !== -1) _appDialogQueue.splice(at, 1);
      resolve(v); _appDialogNext();
    } };
    _appDialogQueue.push(item);
    if (_appDialogQueue.length === 1) _appDialogNext();
  });
}
function appConfirm(message, opts) { return _appDialog('confirm', message, opts); }
function appPrompt(message, defaultValue, opts) { return _appDialog('prompt', message, Object.assign({ defaultValue: defaultValue }, opts || {})); }
function appAlert(message, opts) { return _appDialog('alert', message, opts); }
function appChoice(message, choices, opts) { return _appDialog('choice', message, Object.assign({ choices: choices || [] }, opts || {})); }
function AppDialogHost() {
  var ds = useState(null); var d = ds[0], setD = ds[1];
  var vs = useState(''); var value = vs[0], setValue = vs[1];
  var cardRef = useRef(null);
  var prevFocusRef = useRef(null); // what had the focus before the dialog: it gets it back afterwards
  useEffect(function() { _appDialogSetter = setD; _appDialogNext(); return function() { if (_appDialogSetter === setD) _appDialogSetter = null; }; }, []);
  useEffect(function() {
    if (!d) {
      var back = prevFocusRef.current; prevFocusRef.current = null;
      if (back && back.isConnected && typeof back.focus === 'function') { try { back.focus(); } catch (e) {} }
      return;
    }
    var act = document.activeElement;
    if (!prevFocusRef.current && act && act !== document.body && !(act.closest && act.closest('[data-app-dialog]'))) prevFocusRef.current = act;
    if (d.kind === 'prompt') setValue(d.opts.defaultValue == null ? '' : String(d.opts.defaultValue));
    var el = cardRef.current; if (!el) return;
    var btns = el.querySelectorAll('button');
    var f = el.querySelector('input') || (d.kind === 'choice' ? btns[1] : btns[btns.length - 1]) || el;
    setTimeout(function() { try { f.focus(); if (f.select) f.select(); } catch (e) {} }, 0);
  }, [d]);
  if (!d) return null;
  var o = d.opts;
  var cancel = function() { d.resolve(d.kind === 'confirm' ? false : d.kind === 'prompt' ? null : d.kind === 'choice' ? -1 : undefined); };
  var ok = function() { d.resolve(d.kind === 'confirm' ? true : d.kind === 'prompt' ? value : undefined); };
  var title = o.title || (d.kind === 'alert' ? 'Notice' : d.kind === 'prompt' ? 'Enter a value' : 'Please confirm');
  // an OK button that reads "Cancel order" beside a plain "Cancel" is a trap: the way out says "Go back"
  var cancelText = o.cancelLabel || (/^cancel/i.test(o.okLabel || '') ? 'Go back' : 'Cancel');
  var onKey = function(e) {
    if (e.key === 'Escape') { e.preventDefault(); cancel(); }
    else if (e.key === 'Enter' && d.kind !== 'choice' && !(e.target && e.target.tagName === 'BUTTON' && e.target !== cardRef.current.querySelectorAll('button')[cardRef.current.querySelectorAll('button').length - 1])) { e.preventDefault(); ok(); }
  };
  // A choice keeps every button on ONE line (the card grows to fit them) and can ask for a red
  // Cancel (`cancelDanger`) — Anthony 2026-09-29, the case-pack question.
  var oneLine = { whiteSpace: 'nowrap', flexShrink: 0 };
  var buttons = d.kind === 'choice'
    ? [React.createElement(Btn, { key: 'c', variant: o.cancelDanger ? 'danger' : 'default', onClick: cancel, style: oneLine }, cancelText)].concat((o.choices || []).map(function(label, i) {
        return React.createElement(Btn, { key: 'b' + i, variant: i === 0 ? 'primary' : 'default', onClick: function() { d.resolve(i); }, style: oneLine }, label);
      }))
    : [d.kind !== 'alert' && React.createElement(Btn, { key: 'c', variant: o.cancelDanger ? 'danger' : 'default', onClick: cancel }, cancelText),
       React.createElement(Btn, { key: 'ok', variant: o.danger ? 'danger' : 'primary', onClick: ok }, o.okLabel || 'OK')];
  return React.createElement('div', {
    // data-app-dialog: the pages' click-away / blur auto-saves treat anything inside as "not away"
    'data-app-dialog': '',
    style: { position: 'fixed', inset: 0, zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' },
    onMouseDown: function(e) { if (e.target === e.currentTarget && d.kind !== 'alert') cancel(); }
  },
    // No focus ring on the dialog's buttons at all (Anthony 2026-09-29: "little circles around Round
    // Up", then "we don't need a ring around that button"). Enter and Escape still work, and the
    // default button is the filled one.
    React.createElement('style', null, '[data-app-dialog] button,[data-app-dialog] button:focus,[data-app-dialog] button:focus-visible{outline:none;box-shadow:none}'),
    React.createElement('div', { ref: cardRef, role: 'dialog', 'aria-modal': true, tabIndex: -1, onKeyDown: onKey, onMouseDown: function(e) { e.stopPropagation(); },
      style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 12, width: d.kind === 'choice' ? 'max-content' : 440, minWidth: d.kind === 'choice' ? 440 : undefined, maxWidth: 'calc(100vw - 40px)', padding: 22, boxShadow: '0 20px 60px rgba(0,0,0,0.45)', outline: 'none' } },
      React.createElement('div', { style: { fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 8 } }, title),
      React.createElement('div', { style: { fontSize: 13, color: C.textMuted, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '50vh', overflowY: 'auto' } }, d.message),
      d.kind === 'prompt' && React.createElement('div', { style: { marginTop: 12 } },
        React.createElement(Inp, { value: value, placeholder: o.placeholder || '', onChange: function(e) { setValue(e.target.value); }, style: { width: '100%' } })),
      // no overflow on this row: a scrolling row clips the focus ring top and bottom and leaves only
      // its rounded ends showing (the "little circles"); a choice wraps only on a phone-width screen
      React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18, flexWrap: d.kind === 'choice' && window.innerWidth >= 600 ? 'nowrap' : 'wrap' } }, buttons)
    )
  );
}

// Escape closes the top-most open modal (polish 2026-10-11; the Orders detail modal only had the X
// and the backdrop). The stack keeps a dialog opened on top of a modal (appConfirm inside Order
// Details) from closing both: only the last-opened modal's listener acts, a key a dialog or a
// picker already handled (defaultPrevented) is left alone, and a key pressed on a <select> is
// ignored because Escape there closes its dropdown.
var _modalStack = [];
function Modal({ open, onClose, title, width, maxHeight, children }) {
  var onCloseRef = useRef(onClose); onCloseRef.current = onClose;
  useEffect(function() {
    if (!open) return;
    var me = {};
    _modalStack.push(me);
    var onKey = function(e) {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (e.target && e.target.tagName === 'SELECT') return;
      if (_modalStack[_modalStack.length - 1] !== me) return;
      var close = onCloseRef.current;
      if (!close) return;
      // The apps mount through the legacy ReactDOM.render root, where state set from a native key
      // listener renders after EACH setter: a close that clears the selected order and then its
      // detail rendered in between with the order gone and the detail still there (crash on the
      // first live Escape). One render for the whole close, as a React event handler would get.
      var batched = typeof ReactDOM !== 'undefined' && ReactDOM && ReactDOM.unstable_batchedUpdates;
      if (batched) batched(function() { close(); }); else close();
    };
    document.addEventListener('keydown', onKey);
    return function() {
      document.removeEventListener('keydown', onKey);
      var i = _modalStack.indexOf(me);
      if (i >= 0) _modalStack.splice(i, 1);
    };
  }, [open]);
  if (!open) return null;
  return React.createElement('div', {
    style: { position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' },
    onMouseDown: function(e) { if (e.target === e.currentTarget) onClose(); }
  },
    React.createElement('div', {
      style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 12, width: width || 560, maxWidth: 'calc(calc(100vw * var(--invzoom, 1)) - 40px)', maxHeight: maxHeight || 'calc(85vh * var(--invzoom, 1))', overflowX: 'hidden', overflowY: 'auto', padding: 24, boxSizing: 'border-box' },
      onMouseDown: function(e) { e.stopPropagation(); }
    },
      React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 } },
        React.createElement('h3', { style: { margin: 0, fontSize: 16, fontWeight: 600, color: C.text } }, title),
        React.createElement('button', { onClick: onClose,
          onMouseOver: function(e) { e.currentTarget.style.background = C.danger + '20'; e.currentTarget.style.color = C.danger; },
          onMouseOut: function(e) { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = C.textMuted; },
          style: { background: 'none', border: 'none', color: C.textMuted, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', padding: 6, borderRadius: 6, transition: 'all 0.15s' } }, React.createElement(LIcon, { name: 'x', size: 18 }))
      ),
      children
    )
  );
}

// fill: the empty state takes the table's remaining height and centres in it (a fill-height table,
// Anthony 2026-09-23: "center the message")
function Empty({ title, sub, fill }) {
  return React.createElement('div', { style: Object.assign({ textAlign: 'center', padding: '60px 20px', color: C.textDim },
    fill ? { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px' } : {}) },
    React.createElement('div', { style: { fontSize: 15, fontWeight: 500, color: C.textMuted, marginBottom: sub ? 4 : 0 } }, title),
    sub ? React.createElement('div', { style: { fontSize: 13 } }, sub) : null
  );
}

function Toast({ message, type, onClose }) {
  // Per-type timeout: errors and warnings carry data the user may need to read/copy,
  // so they stay longer than success toasts.
  var timeout = type === 'error' ? 12000 : type === 'warning' ? 7000 : 3500;
  useEffect(() => { const t = setTimeout(onClose, timeout); return () => clearTimeout(t); }, [onClose, timeout]);
  var toastRef = React.useRef(null);
  // Errors do NOT close on outside-click \u2014 Paula needs time to read/copy before clicking elsewhere.
  useEffect(() => {
    if (type === 'error') return;
    var handler = function(e) { if (toastRef.current && toastRef.current.contains(e.target)) return; onClose(); };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [onClose, type]);
  // Detect MSKU-like tokens in error/warning messages (2-5 caps, dash, alphanumeric with
  // optional segments). Match real seller SKUs like ALRE-852, MIAM-105699-3PK, VDIM-142.
  var skus = (type === 'error' || type === 'warning') && typeof message === 'string'
    ? Array.from(new Set(message.match(/\b[A-Z]{2,5}-[A-Z0-9]+(?:-[A-Z0-9]+){0,3}\b/g) || []))
    : [];
  var [copied, setCopied] = useState(false);
  var doCopy = function(e) {
    e.stopPropagation();
    if (!skus.length) return;
    var text = skus.join(', ');
    var done = function() { setCopied(true); setTimeout(function() { setCopied(false); }, 2000); };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(function() {});
    } else {
      var ta = document.createElement('textarea'); ta.value = text;
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); done(); } catch (e2) {}
      document.body.removeChild(ta);
    }
  };
  const bg = type === 'error' ? C.dangerSoft : type === 'warning' ? C.warningSoft : C.successSoft;
  const clr = type === 'error' ? C.danger : type === 'warning' ? C.warning : C.success;
  return React.createElement('div', { ref: toastRef,
    style: { position: 'fixed', top: 20, right: 20, zIndex: 2000, background: bg, color: clr, padding: '12px 20px', borderRadius: 8, fontSize: 13, fontWeight: 500, border: '1px solid ' + clr + '33', boxShadow: '0 8px 30px rgba(0,0,0,0.3)', animation: 'slideIn 0.2s ease', display: 'flex', alignItems: 'center', gap: 10, maxWidth: 600 }
  }, React.createElement('span', { style: { wordBreak: 'break-word' } }, message),
    skus.length > 0 && React.createElement('button', {
      onClick: doCopy, title: 'Copy SKUs: ' + skus.join(', '),
      style: { background: clr + '22', border: '1px solid ' + clr + '55', color: clr, cursor: 'pointer', fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 4, whiteSpace: 'nowrap', flexShrink: 0, fontFamily: 'inherit' }
    }, copied ? 'Copied!' : 'Copy ' + skus.length + ' SKU' + (skus.length > 1 ? 's' : '')),
    React.createElement('button', { onClick: onClose,
      style: { background: 'none', border: 'none', color: clr, cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 0, opacity: 0.7, flexShrink: 0 }
    }, '\u00D7')
  );
}

function TH({ children, style }) {
  return React.createElement('th', { style: { padding: '8px 12px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', ...style } }, children);
}

function TD({ children, style, onClick }) {
  return React.createElement('td', { style: { padding: '8px 12px', wordBreak: 'break-word', ...style }, onClick: onClick }, children);
}

// Column preferences — loaded from server, saved back on change. Saves send
// ONLY the keys that changed since load (diff vs baseline): the main app and
// the ship/ads apps each hold a copy, and full-object saves let a stale copy
// revert the other app's layouts.
var _colPrefs = {};
var _colPrefsBaseline = {};
var _colPrefsLoaded = false;
var _colPrefsSaveTimer = null;

function loadColumnPrefs() {
  if (_colPrefsLoaded) return Promise.resolve(_colPrefs);
  return fetch('/api/auth/preferences', { credentials: 'include' })
    .then(function(r) { return r.ok ? r.json() : {}; })
    .then(function(prefs) {
      _colPrefs = (prefs && prefs.columns) || {};
      try { _colPrefsBaseline = JSON.parse(JSON.stringify(_colPrefs)); } catch (e) { _colPrefsBaseline = {}; }
      _colPrefsLoaded = true;
      return _colPrefs;
    })
    .catch(function() { return {}; });
}

function saveColumnPrefs() {
  clearTimeout(_colPrefsSaveTimer);
  _colPrefsSaveTimer = setTimeout(function() {
    var dirty = {};
    Object.keys(_colPrefs).forEach(function(k) {
      if (JSON.stringify(_colPrefs[k]) !== JSON.stringify(_colPrefsBaseline[k])) dirty[k] = _colPrefs[k];
    });
    // Deleted keys (e.g. reset column order) go up as null — loaders treat
    // null as unset, and the server merge keeps explicit nulls
    Object.keys(_colPrefsBaseline).forEach(function(k) {
      if (!(k in _colPrefs)) dirty[k] = null;
    });
    if (!Object.keys(dirty).length) return;
    fetch('/api/auth/preferences', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ columns: dirty })
    }).then(function(r) {
      if (!r.ok) return;
      Object.keys(dirty).forEach(function(k) {
        if (dirty[k] === null) delete _colPrefsBaseline[k];
        else { try { _colPrefsBaseline[k] = JSON.parse(JSON.stringify(dirty[k])); } catch (e) {} }
      });
    }).catch(function(e) { console.error('[silent-catch]', e); });
  }, 500);
}

function resetColumnPrefsCache() {
  _colPrefs = {};
  _colPrefsLoaded = false;
}

// scrollMode (opt-in): fixed-height viewport with scroll-to-load-more instead of page
// buttons — pageSize becomes the batch loaded per scroll (client mode only).
// compact (opt-in): smaller cell + header type for dense side-panel tables.
// enableViews (opt-in, needs storageKey): "View: Default" dropdown in the toolbar — saves
// the current table state (column filters, sort, order, visibility, page size) as a named
// per-user view; the active view is remembered and re-applied on the next visit.
// ── Column filter kinds (2026-09-05) ─────────────────────────────────────────────
// Every column filters as one of: 'list' (checkbox list of the actual values), 'number'
// (comparisons only), 'date' (on / before / after) or 'text' (contains / equals / …).
// A column can pin its kind with `filterKind`; `filterOptions` (array or 'auto') implies
// 'list'. Otherwise the kind is inferred from the data. Server-paged tables only see one
// page, so they never infer 'list' — pass `filterOptions` explicitly for those.
var DT_IN_SEP = '\u001f'; // 'in' filter separator (values may contain commas); legacy = ','
function _dtSplitIn(v) { v = v || ''; return (v.indexOf(DT_IN_SEP) !== -1 ? v.split(DT_IN_SEP) : v.split(',')).map(function(x) { return x.trim(); }).filter(function(x) { return x !== ''; }); }
function _dtColValue(col, row) {
  try {
    if (col.filterValue) return col.filterValue(row);
    if (row[col.key] !== undefined) return row[col.key];
    if (col.sortValue) return col.sortValue(row);
    if (col.render) { var r = col.render(row); if (typeof r === 'string' || typeof r === 'number') return r; }
  } catch (e) {}
  return undefined;
}
function _dtIsNumLike(v) {
  if (typeof v === 'number') return !isNaN(v);
  if (typeof v !== 'string') return false;
  return /^[-+]?\$?\s?[\d,]*\.?\d+\s?%?$/.test(v.trim());
}
function _dtToNum(v) {
  if (typeof v === 'number') return v;
  if (v == null) return NaN;
  var s = String(v).replace(/[^0-9.\-]/g, '');
  return s === '' || s === '-' || s === '.' ? NaN : Number(s);
}
// Numeric column-filter comparison (gt/gte/lt/lte/eq). Both sides go through
// _dtToNum so "$1,234" / "12%" compare like the displayed numbers. A missing
// value (null/'') is NOT zero and never satisfies a numeric comparison.
function _dtNumCompare(mode, rawVal, search) {
  search = search == null ? '' : String(search).trim();
  if (search === '' || rawVal == null || rawVal === '') return false;
  var n = _dtToNum(rawVal), s = _dtToNum(search);
  if (isNaN(n) || isNaN(s)) return false;
  switch (mode) {
    case 'gt': return n > s;
    case 'gte': return n >= s;
    case 'lt': return n < s;
    case 'lte': return n <= s;
    case 'eq': return n === s;
    default: return false;
  }
}
window._dtNumCompare = _dtNumCompare;
function _dtIsDateLike(v) {
  if (v instanceof Date) return !isNaN(v);
  if (typeof v !== 'string') return false;
  return /^\d{4}-\d{2}-\d{2}([T ]|$)/.test(v.trim()) && !isNaN(new Date(v).getTime());
}
// Calendar day as YYYYMMDD, matching the day the cell shows. A date-only value (YYYY-MM-DD, the
// pg DATE -> JSON round-trip at UTC midnight, or a naive date-time with no zone) uses its leading
// date, the way fmtDateOnly prints it; a zoned timestamp (TIMESTAMPTZ, "...T23:30:00.000Z") uses
// the viewer's local day, the way the date cells print it. Review 2026-09-28, medium #30: zoned
// timestamps were filtered on their UTC day, so after 8 PM ET "on today" missed rows the table
// showed under today.
function _dtToDay(v) {
  if (v == null || v === '') return null;
  var s = typeof v === 'string' ? v.trim() : v;
  var m = typeof s === 'string' && s.match(/^(\d{4})-(\d{2})-(\d{2})(.*)$/);
  if (m) {
    var zoned = /(Z|[+-]\d{2}:?\d{2})$/i.test(m[4]);
    var utcMidnight = /^[T ]00:00:00(\.0+)?(Z|\+00:?00)$/i.test(m[4]);
    if (!zoned || utcMidnight) return Number(m[1]) * 10000 + Number(m[2]) * 100 + Number(m[3]);
  }
  var d = s instanceof Date ? s : new Date(s);
  if (isNaN(d.getTime())) return null;
  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
}
function _dtModesForKind(kind) {
  if (kind === 'list') return ['in'];
  if (kind === 'number') return ['eq', 'gt', 'gte', 'lt', 'lte', 'blank', 'not_blank'];
  if (kind === 'date') return ['on', 'before', 'after', 'blank', 'not_blank'];
  return ['contains', 'equals', 'starts', 'ends', 'blank', 'not_blank'];
}
function _dtInferFilterKind(col, rows, serverMode) {
  if (col.filterKind) return col.filterKind;
  if (col.filterOptions) return 'list';
  var vals = [];
  for (var i = 0; i < rows.length && vals.length < 400; i++) {
    var v = _dtColValue(col, rows[i]);
    if (v != null && String(v).trim() !== '') vals.push(v);
  }
  if (!vals.length) return 'text';
  var nums = 0, dates = 0, seen = {}, distinct = 0;
  vals.forEach(function(v) {
    if (_dtIsNumLike(v)) nums++;
    else if (_dtIsDateLike(v)) dates++;
    var k = String(v); if (!seen[k]) { seen[k] = true; distinct++; }
  });
  if (nums / vals.length >= 0.9) return 'number';
  if (dates / vals.length >= 0.9) return 'date';
  if (!serverMode && distinct <= 200 && distinct <= Math.max(12, vals.length * 0.5)) return 'list';
  return 'text';
}
function _dtListOptions(col, rows) {
  if (Array.isArray(col.filterOptions)) return col.filterOptions.map(function(o) { return String(o); });
  var seen = {}, out = [];
  for (var i = 0; i < rows.length; i++) {
    var v = _dtColValue(col, rows[i]);
    if (v == null) continue;
    var s = String(v).trim(); if (s === '' || seen[s]) continue;
    seen[s] = true; out.push(s);
    if (out.length >= 500) break;
  }
  var allNum = out.length > 0 && out.every(_dtIsNumLike);
  out.sort(allNum ? function(a, b) { return _dtToNum(a) - _dtToNum(b); } : function(a, b) { return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true }); });
  return out;
}

// ── Invisible reCAPTCHA v2 → one-shot token for Firebase text-message codes (2026-09-05) ──
// Google will not text a verification code without proof the request came from a browser.
// The site key comes from the server (Firebase project); the widget renders once into a
// fixed host div, is reset before every execute, and its badge is hidden (attribution text
// is shown next to the phone step instead, per Google's terms).
var _rcLoad = null, _rcWidget = null, _rcSiteKey = null, _rcPending = null;
function loadRecaptchaApi() {
  if (window.grecaptcha && window.grecaptcha.render) return Promise.resolve();
  if (_rcLoad) return _rcLoad;
  _rcLoad = new Promise(function(resolve, reject) {
    var cb = '__rcOnload' + Date.now();
    var timer = setTimeout(function() { _rcLoad = null; reject(new Error('The verification widget did not load. Check your connection or use email.')); }, 12000);
    window[cb] = function() { clearTimeout(timer); resolve(); };
    if (!document.getElementById('rc-badge-style')) {
      var st = document.createElement('style'); st.id = 'rc-badge-style';
      st.textContent = '.grecaptcha-badge{visibility:hidden}';
      document.head.appendChild(st);
    }
    var sc = document.createElement('script');
    sc.src = 'https://www.google.com/recaptcha/api.js?onload=' + cb + '&render=explicit';
    sc.async = true; sc.defer = true;
    sc.onerror = function() { clearTimeout(timer); _rcLoad = null; reject(new Error('The verification widget could not be loaded.')); };
    document.head.appendChild(sc);
  });
  return _rcLoad;
}
function getRecaptchaToken(siteKey) {
  if (!siteKey) return Promise.reject(new Error('Text-message codes are not available right now.'));
  return loadRecaptchaApi().then(function() {
    return new Promise(function(resolve, reject) {
      var host = document.getElementById('app-recaptcha-host');
      if (!host) { host = document.createElement('div'); host.id = 'app-recaptcha-host'; document.body.appendChild(host); }
      if (_rcWidget !== null && _rcSiteKey !== siteKey) { host.innerHTML = ''; _rcWidget = null; }
      if (_rcPending) { try { _rcPending.reject(new Error('Superseded')); } catch (e) {} _rcPending = null; }
      var timer = setTimeout(function() { done('err', new Error('The verification challenge timed out. Try again or use email.')); }, 90000);
      function done(kind, arg) { clearTimeout(timer); var p = _rcPending; _rcPending = null; if (!p) return; if (kind === 'ok') p.resolve(arg); else p.reject(arg); }
      _rcPending = { resolve: resolve, reject: reject };
      if (_rcWidget === null) {
        var el = document.createElement('div'); host.appendChild(el);
        _rcSiteKey = siteKey;
        _rcWidget = window.grecaptcha.render(el, {
          sitekey: siteKey, size: 'invisible', badge: 'bottomleft',
          callback: function(token) { done('ok', token); },
          'expired-callback': function() { done('err', new Error('The verification challenge expired. Try again.')); },
          'error-callback': function() { done('err', new Error('The verification challenge could not run. Try again or use email.')); },
        });
      } else {
        try { window.grecaptcha.reset(_rcWidget); } catch (e) {}
      }
      try { window.grecaptcha.execute(_rcWidget); } catch (e) { done('err', e); }
    });
  });
}
window.getRecaptchaToken = getRecaptchaToken;
// Load the Google script ahead of the first execute (login page mount); errors surface later, on use.
window.warmRecaptcha = function() { try { loadRecaptchaApi().catch(function() {}); } catch (e) {} };
var RECAPTCHA_NOTICE = 'Protected by reCAPTCHA. Google\u2019s Privacy Policy and Terms of Service apply.';
window.RECAPTCHA_NOTICE = RECAPTCHA_NOTICE;

// ── System checkbox (2026-09-05) — the rounded-square look the table selection columns use,
// for menus/popovers that used to fall back to the browser's native checkbox. Purely visual:
// the parent handles the click.
function SysCheckbox(props) {
  var size = props.size || 16;
  var on = !!props.checked, mixed = !!props.indeterminate && !on;
  return React.createElement('span', { 'aria-checked': mixed ? 'mixed' : on, role: 'checkbox', style: Object.assign({
    width: size, height: size, borderRadius: 4, flex: '0 0 auto', boxSizing: 'border-box',
    border: '2px solid ' + (on || mixed ? C.accent : C.textDim), background: on ? C.accent : 'transparent',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.12s',
  }, props.style || {}) },
    on ? React.createElement(LIcon, { name: 'check', size: Math.max(10, size - 5), color: '#fff' }) : null,
    mixed ? React.createElement('span', { style: { width: size - 8, height: 2, background: C.accent, borderRadius: 1 } }) : null);
}
window.SysCheckbox = SysCheckbox;

// Selection columns (the checkbox column every list adds as _select / select / _sel) never sort:
// clicking beside the box used to sort the rows by ticked state (Anthony 2026-09-07).
function colNoSort(col) { return !!(col && (col.noSort || /^_?sel(ect)?$/.test(String(col.key || '')))); }

function DataTable({ columns, data, onRowClick, rowHref, rowStyle, onAction, emptyTitle, emptySub, storageKey, defaultSort, defaultSortDir, onSortedData, onPageData, bulkActions, extraToolbar, serverTotal, onServerFetch, serverSearch, initialPage, onPageChange, initialScroll, onScrollChange, scrollMode, scrollHeight, compact, enableViews, viewsPortalEl, noColumnFilters, fontSize, cellPadding, defaultPageSize }) {
  // columns: [{ key, label, render?, defaultVisible?, style?, sortKey?, locked?, stopClick?, noFilter? }]
  // defaultPageSize: rows per page until the user picks one (their pick is saved per storageKey and wins)

  var initVisible = {};
  columns.forEach(function(col) {
    initVisible[col.key] = col.defaultVisible !== false;
  });

  if (storageKey && _colPrefs[storageKey]) {
    var saved = _colPrefs[storageKey];
    columns.forEach(function(col) {
      if (saved[col.key] !== undefined) {
        initVisible[col.key] = saved[col.key];
      }
    });
  }

  // Locked columns are always visible
  columns.forEach(function(col) {
    if (col.locked) initVisible[col.key] = true;
  });

  var rowOptions = [25, 50, 100, 200, 400, 800];
  var initPageSize = (defaultPageSize && defaultPageSize > 0) ? defaultPageSize : 50;
  if (storageKey && _colPrefs[storageKey + '_pageSize']) {
    initPageSize = _colPrefs[storageKey + '_pageSize'];
  }

  const [visible, setVisible] = useState(initVisible);
  // Per-user column order (drag a header onto another to reorder; persists via /auth/preferences)
  const [colOrder, setColOrder] = useState((storageKey && _colPrefs[storageKey + '_order']) || null);
  const [dragCol, setDragCol] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  // Named per-user views (enableViews): [{ name, state }] under storageKey + '_views'
  const [userViews, setUserViews] = useState((storageKey && _colPrefs[storageKey + '_views']) || []);
  const [activeView, setActiveView] = useState((storageKey && _colPrefs[storageKey + '_activeView']) || null);
  // Starred view auto-applied every time this table mounts (per user)
  const [defaultView, setDefaultView] = useState((storageKey && _colPrefs[storageKey + '_defaultView']) || null);
  const [viewMenuOpen, setViewMenuOpen] = useState(false);
  const [viewName, setViewName] = useState('');
  const viewMenuRef = useRef(null);
  const [prefsReady, setPrefsReady] = useState(_colPrefsLoaded);
  const [showColMenu, setShowColMenu] = useState(false);
  // Last manually-clicked sort persists per user under storageKey + '_sort' (same prefs
  // store as visibility/order/pageSize); defaultSort only applies until the first click.
  var _savedSort = (storageKey && _colPrefs[storageKey + '_sort']) || null;
  const [sortCol, setSortCol] = useState((_savedSort && _savedSort.col) || defaultSort || null);
  const [sortDir, setSortDir] = useState((_savedSort && _savedSort.col && _savedSort.dir) || defaultSortDir || 'asc');
  const [currentPage, _setCurrentPage] = useState(initialPage || 0);
  var setCurrentPage = function(p) { _setCurrentPage(p); if (onPageChange) onPageChange(p); };
  const [pageSize, setPageSize] = useState(initPageSize);
  const menuRef = useRef(null);
  const scrollRef = useRef(null);
  const scrollRestoredRef = useRef(false);
  useEffect(function() {
    // Restore scroll position once data is available (not on initial empty mount)
    if (!scrollRestoredRef.current && scrollRef.current && initialScroll && data && data.length > 0) {
      scrollRef.current.scrollTop = initialScroll;
      scrollRestoredRef.current = true;
    }
  }, [data]);
  var _scrollTotalRef = useRef(0);
  var handleScroll = function() {
    if (onScrollChange && scrollRef.current) onScrollChange(scrollRef.current.scrollTop);
    if (scrollMode && !onServerFetch && scrollRef.current) {
      var el = scrollRef.current;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 240
          && (currentPage + 1) * pageSize < _scrollTotalRef.current) {
        setCurrentPage(currentPage + 1); // reveal the next batch
      }
    }
  };

  // Column filters: { colKey: { mode: 'contains'|'equals'|'starts'|'blank'|'not_blank', value: '' } }
  const [colFilters, setColFilters] = useState({});
  const [filterMenuCol, setFilterMenuCol] = useState(null);
  const filterMenuRef = useRef(null);
  const filterInputRef = useRef(null);

  var activeFilterCount = Object.keys(colFilters).filter(function(k) {
    var f = colFilters[k];
    if (!f || !f.mode) return false;
    if (f.mode === 'blank' || f.mode === 'not_blank') return true;
    return f.value && f.value.trim() !== '';
  }).length;

  // Pending filter edits (not yet applied)
  const [pendingFilter, setPendingFilter] = useState(null); // { mode, value, matchCase } for current filterMenuCol

  var openFilterMenu = function(colKey) {
    var existing = colFilters[colKey] || {};
    var col = columns.find(function(c) { return c.key === colKey; });
    var kind = col ? _dtInferFilterKind(col, data, !!onServerFetch) : 'text';
    var modes = _dtModesForKind(kind);
    var mode = existing.mode && modes.indexOf(existing.mode) !== -1 ? existing.mode : modes[0];
    setPendingFilter({ kind: kind, mode: mode, value: existing.value || '', matchCase: existing.matchCase || false, q: '' });
    setFilterMenuCol(colKey);
    setShowColMenu(false);
    setShowBulkMenu(false);
  };
  var applyFilter = function(colKey) {
    if (!pendingFilter) return;
    var next = Object.assign({}, colFilters);
    if (!pendingFilter.mode || pendingFilter.mode === '') { delete next[colKey]; }
    else { next[colKey] = { mode: pendingFilter.mode, value: pendingFilter.value || '', matchCase: pendingFilter.matchCase || false }; }
    setColFilters(next);
    setCurrentPage(0);
    setFilterMenuCol(null);
    setPendingFilter(null);
  };
  var clearFilter = function(colKey) {
    var next = Object.assign({}, colFilters);
    delete next[colKey];
    setColFilters(next);
    setCurrentPage(0);
    setFilterMenuCol(null);
    setPendingFilter(null);
  };
  var clearAllFilters = function() { setColFilters({}); setCurrentPage(0); setPendingFilter(null); setFilterMenuCol(null); };

  // Rebuild visibility + page size from saved prefs for the CURRENT storageKey/columns.
  // Reads through a latest-ref because storageKey can change while this instance stays
  // mounted (the PO list swaps its key per status tab) — mount-time init alone would keep
  // serving the previous tab's state and cross-pollute saves under the new key.
  var _latestRef = useRef();
  _latestRef.current = { storageKey: storageKey, columns: columns, defaultSort: defaultSort, defaultSortDir: defaultSortDir };
  var syncFromPrefs = function() {
    var key = _latestRef.current.storageKey;
    var saved = (key && _colPrefs[key]) || {};
    var next = {};
    _latestRef.current.columns.forEach(function(col) {
      next[col.key] = saved[col.key] !== undefined ? saved[col.key] : (col.defaultVisible !== false);
      if (col.locked) next[col.key] = true;
    });
    setVisible(next);
    if (key && _colPrefs[key + '_pageSize']) setPageSize(_colPrefs[key + '_pageSize']);
    setColOrder((key && _colPrefs[key + '_order']) || null);
    // Restore the user's last clicked sort; a saved view applied below still wins
    var savedSort = key && _colPrefs[key + '_sort'];
    if (savedSort && savedSort.col) {
      setSortCol(savedSort.col);
      setSortDir(savedSort.dir || 'asc');
    } else {
      setSortCol(_latestRef.current.defaultSort || null);
      setSortDir(_latestRef.current.defaultSortDir || 'asc');
    }
    // Saved views: refresh the list, then apply the starred default view (wins), else
    // whichever view was active last visit
    var vlist = (key && _colPrefs[key + '_views']) || [];
    setUserViews(vlist);
    var dvName = key && _colPrefs[key + '_defaultView'];
    setDefaultView(dvName || null);
    var avName = key && _colPrefs[key + '_activeView'];
    var pick = (dvName && vlist.find(function(v) { return v.name === dvName; }))
      || (avName && vlist.find(function(v) { return v.name === avName; })) || null;
    setActiveView(pick ? pick.name : null);
    if (pick) applyViewState(pick.state);
  };

  // Load prefs from server on first render
  useEffect(function() {
    if (!_colPrefsLoaded) {
      loadColumnPrefs().then(function() {
        syncFromPrefs();
        setPrefsReady(true);
      });
    }
  }, []);

  // Re-sync when the storage key changes on a live instance (e.g. PO list tab switch)
  var _prevKeyRef = useRef(storageKey);
  useEffect(function() {
    if (_prevKeyRef.current === storageKey) return;
    _prevKeyRef.current = storageKey;
    syncFromPrefs();
  }, [storageKey]);

  // Reset to page 0 when data length changes substantially (client mode only)
  var _prevDataLen = useRef(data.length);
  var _hadDataRef = useRef(data.length > 0);
  useEffect(function() {
    if (!onServerFetch && data.length !== _prevDataLen.current) {
      // Skip reset on initial load (empty → populated) — respect caller's initialPage
      var isInitialLoad = !_hadDataRef.current && data.length > 0;
      if (data.length > 0) _hadDataRef.current = true;
      if (!isInitialLoad) {
        // Only reset page if the data set changed significantly (>10% or full reload)
        var diff = Math.abs(data.length - _prevDataLen.current);
        if (diff > Math.max(5, _prevDataLen.current * 0.1) || _prevDataLen.current === 0) {
          setCurrentPage(0);
        }
      }
      _prevDataLen.current = data.length;
    }
  }, [data.length]);

  // Server-side pagination: call onServerFetch when state changes
  var _serverFiltersKey = onServerFetch ? JSON.stringify(colFilters) : '';
  var _fetchIdRef = useRef(0);
  useEffect(function() {
    if (!onServerFetch) return;
    _fetchIdRef.current++;
    var myId = _fetchIdRef.current;
    var timer = setTimeout(function() {
      if (myId === _fetchIdRef.current) {
        onServerFetch({ page: currentPage, pageSize: pageSize, sortCol: sortCol, sortDir: sortDir, filters: colFilters, search: serverSearch || '' });
      }
    }, 10);
    return function() { clearTimeout(timer); };
  }, [currentPage, pageSize, sortCol, sortDir, _serverFiltersKey, serverSearch]);

  // Server-side: reset page when search changes
  var _prevServerSearch = useRef(serverSearch);
  useEffect(function() {
    if (onServerFetch && serverSearch !== _prevServerSearch.current) {
      _prevServerSearch.current = serverSearch;
      setCurrentPage(0);
    }
  }, [serverSearch]);
  // Server-side: a filter change that shrinks the result set below the current page used to strand
  // the table on an empty page with the pager hidden (review M6 #76; Orders channel/status/date
  // filters, Catalog filter). Snap back to page 0 — the client-mode clamp above is server-exempt.
  useEffect(function() {
    if (onServerFetch && currentPage > 0 && data.length === 0 && (serverTotal || 0) > 0) setCurrentPage(0);
  }, [data.length, serverTotal]);

  var [showBulkMenu, setShowBulkMenu] = useState(false);
  var bulkMenuRef = useRef(null);
  var bulkBtnRef = useRef(null);
  var [bulkMenuPos, setBulkMenuPos] = useState({ top: 0, right: 0 });

  var toggleBulkMenu = function() {
    if (!showBulkMenu && bulkBtnRef.current) {
      var rect = bulkBtnRef.current.getBoundingClientRect();
      var z = window.APP_ZOOM || 1; // gBCR/innerWidth are visual px; the fixed menu positions in zoomed px
      setBulkMenuPos({ top: (rect.bottom + 4) / z, right: (window.innerWidth - rect.right) / z });
    }
    setShowBulkMenu(!showBulkMenu);
    setShowColMenu(false);
  };

  useEffect(function() {
    function handleClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowColMenu(false);
      if (bulkMenuRef.current && !bulkMenuRef.current.contains(e.target)) setShowBulkMenu(false);
      if (filterMenuRef.current && !filterMenuRef.current.contains(e.target)) setFilterMenuCol(null);
    }
    if (showColMenu || showBulkMenu || filterMenuCol) document.addEventListener('mousedown', handleClick);
    return function() { document.removeEventListener('mousedown', handleClick); };
  }, [showColMenu, showBulkMenu, filterMenuCol]);

  // The actions menu is position:fixed with coordinates captured at open — re-anchor it
  // to the button on scroll/resize (capture phase catches inner scrollers too), otherwise
  // it detaches and floats as the page scrolls.
  useEffect(function() {
    if (!showBulkMenu) return;
    function reposition() {
      if (!bulkBtnRef.current) return;
      var rect = bulkBtnRef.current.getBoundingClientRect();
      var z = window.APP_ZOOM || 1;
      setBulkMenuPos({ top: (rect.bottom + 4) / z, right: (window.innerWidth - rect.right) / z });
    }
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return function() { window.removeEventListener('scroll', reposition, true); window.removeEventListener('resize', reposition); };
  }, [showBulkMenu]);

  // Auto-focus filter input when menu opens
  useEffect(function() {
    if (filterMenuCol && filterInputRef.current) filterInputRef.current.focus();
  }, [filterMenuCol]);

  var toggleCol = function(key) {
    var next = Object.assign({}, visible);
    next[key] = !next[key];
    var anyVisible = Object.keys(next).some(function(k) { return next[k]; });
    if (!anyVisible) return;
    setVisible(next);
    if (storageKey) { _colPrefs[storageKey] = next; saveColumnPrefs(); }
  };

  var changePageSize = function(newSize) {
    setPageSize(newSize);
    setCurrentPage(0);
    if (storageKey) { _colPrefs[storageKey + '_pageSize'] = newSize; saveColumnPrefs(); }
  };

  // Display order for a column list under the saved order. Columns the saved order
  // doesn't know (added to the app after the user last dragged) anchor right after the
  // nearest preceding known column from the definition order — NOT at the end of the
  // table, which would strand every newly shipped column on the far right.
  var orderCols = function(cols) {
    if (!colOrder || !colOrder.length) return cols;
    var idx = {};
    colOrder.forEach(function(k, i) { idx[k] = i; });
    var lastKnown = -1;
    var keyed = cols.map(function(c, i) {
      if (idx[c.key] !== undefined) { lastKnown = idx[c.key]; return [c, lastKnown, i]; }
      return [c, lastKnown + (i + 1) / (cols.length + 1), i];
    });
    var out = keyed.sort(function(a, b) { return a[1] - b[1] || a[2] - b[2]; })
      .map(function(x) { return x[0]; });
    // Sticky (left-pinned) columns only work at the front — keep them there regardless
    var stk = out.filter(function(c) { return c.sticky; });
    return stk.length ? stk.concat(out.filter(function(c) { return !c.sticky; })) : out;
  };

  var dropColumn = function(targetKey) {
    var dk = dragCol;
    setDragCol(null);
    setDragOverCol(null);
    if (!dk || dk === targetKey) return;
    // Build the full current display order (all non-subRow columns, hidden ones included,
    // so a hidden column keeps a sane slot when toggled back on)
    var order = orderCols(columns.filter(function(c) { return !c.subRow; }))
      .map(function(c) { return c.key; });
    var from = order.indexOf(dk), to = order.indexOf(targetKey);
    if (from < 0 || to < 0 || from === to) return;
    order.splice(from, 1);
    order.splice(to, 0, dk);
    setColOrder(order);
    if (storageKey) { _colPrefs[storageKey + '_order'] = order; saveColumnPrefs(); }
  };

  var resetColOrder = function() {
    setColOrder(null);
    if (storageKey) { delete _colPrefs[storageKey + '_order']; saveColumnPrefs(); }
  };

  // ── Saved views (enableViews) ──
  var currentViewState = function() {
    return { filters: colFilters, sortCol: sortCol, sortDir: sortDir, visible: visible, order: colOrder, pageSize: pageSize };
  };
  var applyViewState = function(st) {
    st = st || {};
    setColFilters(st.filters || {});
    setSortCol(st.sortCol !== undefined ? st.sortCol : (defaultSort || null));
    setSortDir(st.sortDir || defaultSortDir || 'asc');
    var nextVis = {};
    columns.forEach(function(col) {
      nextVis[col.key] = st.visible && st.visible[col.key] !== undefined ? st.visible[col.key] : (col.defaultVisible !== false);
      if (col.locked) nextVis[col.key] = true;
    });
    setVisible(nextVis);
    setColOrder(st.order || null);
    if (st.pageSize) setPageSize(st.pageSize);
    setCurrentPage(0);
  };
  var selectView = function(v) {
    setViewMenuOpen(false);
    if (!v) {
      // "Default" = factory state; also clear the base prefs so a reload matches what's shown
      setActiveView(null);
      applyViewState(null);
      if (storageKey) {
        delete _colPrefs[storageKey + '_activeView'];
        delete _colPrefs[storageKey];
        delete _colPrefs[storageKey + '_order'];
        delete _colPrefs[storageKey + '_sort'];
        saveColumnPrefs();
      }
      return;
    }
    setActiveView(v.name);
    applyViewState(v.state);
    if (storageKey) { _colPrefs[storageKey + '_activeView'] = v.name; saveColumnPrefs(); }
  };
  var saveCurrentView = function() {
    var name = (viewName || '').trim();
    if (!name || !storageKey) return;
    var next = userViews.filter(function(v) { return v.name !== name; });
    next.push({ name: name, state: currentViewState() });
    setUserViews(next);
    setActiveView(name);
    setViewName('');
    setViewMenuOpen(false);
    _colPrefs[storageKey + '_views'] = next;
    _colPrefs[storageKey + '_activeView'] = name;
    saveColumnPrefs();
  };
  var deleteUserView = function(v) {
    if (!window.confirm('Delete the view "' + v.name + '"?')) return;
    var next = userViews.filter(function(x) { return x.name !== v.name; });
    setUserViews(next);
    if (activeView === v.name) setActiveView(null);
    if (defaultView === v.name) setDefaultView(null);
    if (storageKey) {
      _colPrefs[storageKey + '_views'] = next;
      if (activeView === v.name) delete _colPrefs[storageKey + '_activeView'];
      if (defaultView === v.name) delete _colPrefs[storageKey + '_defaultView'];
      saveColumnPrefs();
    }
  };
  var toggleDefaultView = function(v) {
    var next = defaultView === v.name ? null : v.name;
    setDefaultView(next);
    if (storageKey) {
      if (next) _colPrefs[storageKey + '_defaultView'] = next;
      else delete _colPrefs[storageKey + '_defaultView'];
      saveColumnPrefs();
    }
  };
  useEffect(function() {
    if (!viewMenuOpen) return;
    var onDoc = function(e) { if (viewMenuRef.current && !viewMenuRef.current.contains(e.target)) setViewMenuOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return function() { document.removeEventListener('mousedown', onDoc); };
  }, [viewMenuOpen]);

  // Apply the starred default view (or the remembered active one) on mount. syncFromPrefs
  // only runs on the first-ever prefs load or a storageKey change — a remount with prefs
  // already cached would otherwise show the view's name without its filters/sort.
  useEffect(function() {
    if (!enableViews || !storageKey || !_colPrefsLoaded) return;
    var vlist = _colPrefs[storageKey + '_views'] || [];
    var dvName = _colPrefs[storageKey + '_defaultView'];
    var avName = _colPrefs[storageKey + '_activeView'];
    var pick = (dvName && vlist.find(function(v) { return v.name === dvName; }))
      || (avName && vlist.find(function(v) { return v.name === avName; })) || null;
    if (pick) { setActiveView(pick.name); applyViewState(pick.state); }
  }, []);

  var handleSort = function(col) {
    var nextDir = 'asc';
    if (sortCol === col.key) {
      nextDir = sortDir === 'asc' ? 'desc' : 'asc';
      setSortDir(nextDir);
    } else {
      setSortCol(col.key);
      setSortDir('asc');
    }
    // Remember the last clicked sort per user — restored on the next visit
    if (storageKey) { _colPrefs[storageKey + '_sort'] = { col: col.key, dir: nextDir }; saveColumnPrefs(); }
  };
  // A column that stops being sortable cannot stay the sort key (the Orders list sorts by anything
  // only inside a search; outside one the server sorts by date whatever is asked): the arrow would
  // sit on a column the server ignores. Back to the default sort (polish 2026-10-11). The saved
  // per-user sort is left alone so it applies again once the column is sortable.
  var _noSortKeys = columns.filter(colNoSort).map(function(c) { return c.key; }).join('|');
  useEffect(function() {
    if (!sortCol) return;
    var cur = columns.find(function(c) { return c.key === sortCol; });
    if (!cur || !colNoSort(cur)) return;
    setSortCol(defaultSort || null);
    setSortDir(defaultSortDir || 'asc');
  }, [_noSortKeys, sortCol]);

  var visibleCols = columns.filter(function(col) { return visible[col.key]; });

  // Separate normal columns from subRow columns
  var normalCols = orderCols(visibleCols.filter(function(col) { return !col.subRow; }));
  var subRowCols = visibleCols.filter(function(col) { return col.subRow; });

  // Dynamic font size based on normal column count (subRow cols don't affect width)
  var colCount = normalCols.length;
  var tableFontSize = fontSize || (compact ? 12 : (colCount <= 6 ? 14 : colCount <= 10 ? 14 : 13));
  var cellPad = cellPadding || '6px 8px';

  // Apply column filters
  var filteredData = data;
  var filterKeys = Object.keys(colFilters);
  if (filterKeys.length > 0) {
    var _filterColByKey = {};
    columns.forEach(function(c) { _filterColByKey[c.key] = c; });
    filteredData = data.filter(function(row) {
      return filterKeys.every(function(colKey) {
        var f = colFilters[colKey];
        if (!f || !f.mode) return true;
        // filterValue → raw field → sortValue → render() when it returns plain text, so
        // computed columns (margin, vendor name, ROI…) filter on what the user sees.
        var cd = _filterColByKey[colKey];
        var rawVal = cd ? _dtColValue(cd, row) : row[colKey];
        var caseSensitive = f.matchCase;
        var val = rawVal == null ? '' : String(rawVal);
        var search = (f.value || '').trim();
        if (!caseSensitive) { val = val.toLowerCase(); search = search.toLowerCase(); }
        switch (f.mode) {
          case 'contains': return search === '' || val.includes(search);
          case 'equals': return search === '' || val === search;
          case 'starts': return search === '' || val.startsWith(search);
          case 'ends': return search === '' || val.endsWith(search);
          case 'blank': return rawVal == null || String(rawVal).trim() === '';
          case 'not_blank': return rawVal != null && String(rawVal).trim() !== '';
          // Numeric modes: a missing value (null/'') is NOT zero — it can never satisfy
          // a numeric comparison. Number(null) === 0 would silently include such rows.
          case 'gt': case 'lt': case 'gte': case 'lte': case 'eq':
            return _dtNumCompare(f.mode, rawVal, search);
          case 'on': case 'before': case 'after': {
            var _d = _dtToDay(rawVal), _q = _dtToDay(search);
            if (_d == null || _q == null) return false;
            return f.mode === 'on' ? _d === _q : f.mode === 'before' ? _d < _q : _d > _q;
          }
          case 'in': {
            if (!search) return true;
            var inVals = _dtSplitIn(search).map(function(v) { return v.toLowerCase(); });
            return inVals.includes(String(rawVal == null ? '' : rawVal).trim().toLowerCase());
          }
          default: return true;
        }
      });
    });
  }

  // Sort the data
  var sortedData = filteredData;
  if (sortCol) {
    var col = columns.find(function(c) { return c.key === sortCol; });
    sortedData = [].concat(filteredData).sort(function(a, b) {
      var aVal, bVal;
      if (col && typeof col.sortValue === 'function') {
        aVal = col.sortValue(a); bVal = col.sortValue(b);
      } else {
        var sk = (col && col.sortKey) || sortCol;
        aVal = a[sk]; bVal = b[sk];
      }
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      var aNum = Number(aVal), bNum = Number(bVal);
      if (!isNaN(aNum) && !isNaN(bNum) && String(aVal) !== '' && String(bVal) !== '') {
        return sortDir === 'asc' ? aNum - bNum : bNum - aNum;
      }
      var aStr = String(aVal).toLowerCase(), bStr = String(bVal).toLowerCase();
      if (aStr < bStr) return sortDir === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }

  // Paginate
  var totalRows = sortedData.length;
  var totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  // Only reset page if we have data and it truly overflowed (avoid resetting during loading)
  if (!onServerFetch && totalRows > 0 && currentPage >= totalPages) setCurrentPage(Math.max(0, totalPages - 1));
  var pageData = sortedData.slice(currentPage * pageSize, (currentPage + 1) * pageSize);
  var showingFrom = totalRows === 0 ? 0 : currentPage * pageSize + 1;
  var showingTo = Math.min((currentPage + 1) * pageSize, totalRows);
  if (scrollMode && !onServerFetch) {
    // Accumulate batches from the top: currentPage = batches loaded − 1
    pageData = sortedData.slice(0, (currentPage + 1) * pageSize);
    showingFrom = totalRows === 0 ? 0 : 1;
    showingTo = Math.min((currentPage + 1) * pageSize, totalRows);
  }
  _scrollTotalRef.current = totalRows;

  // Server-side override: data is already filtered/sorted/paginated by server
  if (onServerFetch) {
    filteredData = data; sortedData = data; pageData = data;
    totalRows = serverTotal || 0;
    totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
    showingFrom = totalRows === 0 ? 0 : currentPage * pageSize + 1;
    showingTo = Math.min((currentPage + 1) * pageSize, totalRows);
  }

  // Expose sorted data to parent for shift-select.
  // No deps array: fires every render so the parent's ref stays in sync with the current sortedData
  // (data prop reference can change without length/sortCol/sortDir changing).
  useEffect(function() {
    if (onSortedData) onSortedData(sortedData);
    if (onPageData) onPageData(pageData);
  });

  var _isEmpty = data.length === 0;

  if (filteredData.length === 0 && activeFilterCount > 0) {
    return React.createElement(Card, { style: { padding: 0, overflow: 'auto' } },
      React.createElement('div', { style: { textAlign: 'center', padding: '40px 20px' } },
        React.createElement('div', { style: { fontSize: 14, color: C.textMuted, marginBottom: 8 } }, 'No rows match the active filters (' + activeFilterCount + ' filter' + (activeFilterCount > 1 ? 's' : '') + ')'),
        React.createElement('button', { onClick: clearAllFilters, style: { background: C.accent, border: 'none', color: '#fff', padding: '6px 14px', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500 } }, React.createElement(LIcon, { name: 'x', size: 12 }), ' Clear All Filters')
      )
    );
  }

  return React.createElement(Card, { style: Object.assign({ padding: 0, overflow: 'visible', position: 'relative' },
    (scrollHeight === 'fill') ? { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 } : {}) },
    React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 14px', borderBottom: '1px solid ' + C.border + '44', position: 'relative', flexWrap: 'wrap', gap: 8 } },
      // Rows per page
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 6 } },
        React.createElement('span', { style: { fontSize: 11, color: C.textMuted } }, scrollMode ? 'Load' : 'Show'),
        rowOptions.map(function(n) {
          return React.createElement('button', {
            key: n, onClick: function() { changePageSize(n); },
            className: pageSize !== n ? 'dt-row-btn' : '',
            style: {
              padding: '2px 8px', fontSize: 11, borderRadius: 4, border: 'none', cursor: 'pointer',
              fontFamily: 'inherit', fontWeight: pageSize === n ? 600 : 400,
              background: pageSize === n ? C.accent : 'transparent',
              color: pageSize === n ? '#fff' : C.textMuted,
              transition: 'background 0.15s'
            }
          }, String(n));
        }),
        React.createElement('span', { style: { fontSize: 11, color: C.textMuted } }, scrollMode ? 'rows per scroll' : 'rows')
      ),
      // Active filters indicator
      activeFilterCount > 0 && React.createElement('button', {
        onClick: clearAllFilters,
        style: { background: C.warningSoft, border: '1px solid ' + C.warning + '44', color: C.warning, padding: '3px 10px', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }
      }, React.createElement(LIcon, { name: 'zap', size: 12 }), ' ' + activeFilterCount + ' filter' + (activeFilterCount > 1 ? 's' : '') + ' active — Clear'),
      // Bulk Actions + Columns toggle
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 6 } },
        // Saved views dropdown (opt-in) — portals to viewsPortalEl when the page wants it
        // rendered outside the table toolbar (state stays in this component either way)
        (function() {
          if (!enableViews || !storageKey) return null;
          var ui = React.createElement('div', { key: '__views', ref: viewMenuRef, style: { position: 'relative', display: 'inline-block' } },
          React.createElement('button', {
            onClick: function() { setViewMenuOpen(!viewMenuOpen); },
            className: 'dt-btn',
            title: 'Saved views — save the current filters, sort, column order and visibility under a name',
            style: { background: 'none', border: '1px solid ' + (activeView ? C.accent + '77' : C.border), color: activeView ? C.accent : C.textMuted, cursor: 'pointer', padding: '4px 10px', borderRadius: 4, fontSize: 11, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4, fontWeight: activeView ? 600 : 400, maxWidth: 220 }
          },
            React.createElement(LIcon, { name: 'bookmark', size: 12 }),
            React.createElement('span', { style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, 'View: ' + (activeView || 'Default')),
            React.createElement(LIcon, { name: 'chevron-down', size: 11 })),
          viewMenuOpen && React.createElement('div', {
            style: Object.assign({ position: 'absolute', top: '100%', zIndex: 120, marginTop: 4, background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: 6, minWidth: 240, boxShadow: '0 8px 30px rgba(0,0,0,0.4)' }, viewsPortalEl ? { left: 0 } : { right: 0 })
          },
            [{ v: null, label: 'Default' }].concat(userViews.map(function(v) { return { v: v, label: v.name }; })).map(function(it) {
              var isActive = it.v === null ? !activeView : activeView === it.v.name;
              var isStarred = it.v !== null && defaultView === it.v.name;
              return React.createElement('div', {
                key: it.v === null ? '__default' : it.v.name,
                onClick: function() { selectView(it.v); },
                style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '6px 8px', borderRadius: 5, cursor: 'pointer', fontSize: 12, color: isActive ? C.accent : C.text, fontWeight: isActive ? 600 : 400 },
                onMouseEnter: function(e) { e.currentTarget.style.background = C.bg; },
                onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; }
              },
                React.createElement('span', { style: { display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } },
                  isActive && React.createElement(LIcon, { name: 'check', size: 12, color: C.accent }), it.label),
                it.v !== null && React.createElement('span', { style: { display: 'inline-flex', gap: 2, flexShrink: 0 } },
                  React.createElement('span', {
                    title: isStarred ? 'Default view for every list — click to unset' : 'Make this my default view for every list',
                    onClick: function(e) { e.stopPropagation(); toggleDefaultView(it.v); },
                    style: { display: 'inline-flex', padding: 2, opacity: isStarred ? 1 : 0.45 }
                  }, React.createElement(LIcon, { name: 'star', size: 12, color: isStarred ? '#F59E0B' : C.textMuted })),
                  React.createElement('span', {
                    title: 'Delete view',
                    onClick: function(e) { e.stopPropagation(); deleteUserView(it.v); },
                    style: { display: 'inline-flex', padding: 2, opacity: 0.55 }
                  }, React.createElement(LIcon, { name: 'x', size: 12, color: C.textMuted }))));
            }),
            React.createElement('div', { style: { borderTop: '1px solid ' + C.border + '66', marginTop: 6, paddingTop: 8, display: 'flex', gap: 6, alignItems: 'center' } },
              React.createElement('input', {
                value: viewName,
                onChange: function(e) { setViewName(e.target.value); },
                onKeyDown: function(e) { if (e.key === 'Enter') saveCurrentView(); },
                placeholder: 'Save current as…',
                style: { flex: 1, minWidth: 0, background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '5px 8px', borderRadius: 5, fontSize: 12, fontFamily: 'inherit' }
              }),
              React.createElement('button', {
                onClick: saveCurrentView,
                disabled: !viewName.trim(),
                style: { background: viewName.trim() ? C.accent : C.border, border: 'none', color: '#fff', padding: '5px 10px', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: viewName.trim() ? 'pointer' : 'default', fontFamily: 'inherit' }
              }, 'Save'))));
          return viewsPortalEl && window.ReactDOM && ReactDOM.createPortal
            ? ReactDOM.createPortal(ui, viewsPortalEl) : ui;
        })(),
        extraToolbar,
        // Bulk Actions dropdown
        bulkActions && (bulkActions.count > 0 || bulkActions.alwaysShow) && React.createElement('div', { style: { position: 'relative' } },
          React.createElement('button', {
            ref: bulkBtnRef,
            onClick: toggleBulkMenu,
            // Same hover as a primary Btn — this was the only toolbar button with no hover feedback
            onMouseOver: function(e) { e.currentTarget.style.filter = 'brightness(1.15)'; e.currentTarget.style.transform = 'translateY(-1px)'; },
            onMouseOut: function(e) { e.currentTarget.style.filter = ''; e.currentTarget.style.transform = ''; },
            style: { background: C.accent, border: 'none', color: '#fff', cursor: 'pointer', padding: '4px 10px', borderRadius: 4, fontSize: 11, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600, transition: 'all 0.15s' }
          }, React.createElement(LIcon, { name: 'zap', size: 12 }),
            // Fixed-width count slot so the button (and everything left of it)
            // doesn't jitter when the selection count appears/changes
            React.createElement('span', { style: { display: 'inline-block', minWidth: 52, textAlign: 'left' } }, ' Actions' + (bulkActions.count > 0 ? ' (' + bulkActions.count + ')' : ''))),
          showBulkMenu && React.createElement('div', {
            ref: bulkMenuRef,
            style: { position: 'fixed', top: bulkMenuPos.top, right: bulkMenuPos.right, zIndex: 10000, background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: '6px 0', minWidth: 220, boxShadow: '0 8px 30px rgba(0,0,0,0.4)', maxHeight: 'calc(calc(100vh * var(--invzoom, 1)) - ' + bulkMenuPos.top + 'px - 10px)', overflowY: 'auto' }
          },
            (bulkActions.items || []).map(function(item, idx) {
              if (item.divider) return React.createElement('div', { key: 'div-' + idx, style: { height: 1, background: C.border + '44', margin: '4px 0' } });
              if (item.section) return React.createElement('div', { key: 'sec-' + idx, style: { padding: '6px 14px 4px', fontSize: 9, fontWeight: 700, color: C.textDim, textTransform: 'uppercase', letterSpacing: '0.05em' } }, item.section);
              if (item.header) return React.createElement('div', { key: 'hdr-' + idx, style: { padding: '6px 14px', fontSize: 11, color: C.textMuted, fontWeight: 600, borderBottom: '1px solid ' + C.border + '44', marginBottom: 4 } }, item.header);
              return React.createElement('button', {
                key: item.label + '-' + idx,
                disabled: !!item.disabled,
                title: item.title || undefined,
                onClick: function() { if (item.disabled) return; setShowBulkMenu(false); item.onClick(); },
                style: { display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '7px 14px', cursor: item.disabled ? 'not-allowed' : 'pointer', fontSize: 12, color: item.disabled ? C.textDim : (item.danger ? C.danger : C.text), opacity: item.disabled ? 0.5 : 1, fontFamily: 'inherit', borderRadius: 0 },
                onMouseEnter: function(e) { if (!item.disabled) e.currentTarget.style.background = C.surfaceHover; },
                onMouseLeave: function(e) { e.currentTarget.style.background = 'none'; }
              }, item.icon ? React.createElement(LIcon, { name: item.icon, size: 14, color: item.disabled ? C.textDim : (item.danger ? C.danger : C.textMuted) }) : null, item.label);
            }),
            bulkActions.count > 0 && bulkActions.onClear && React.createElement('div', { style: { height: 1, background: C.border + '44', margin: '4px 0' } }),
            bulkActions.count > 0 && bulkActions.onClear && React.createElement('button', {
              onClick: function() { setShowBulkMenu(false); bulkActions.onClear(); },
              style: { display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '7px 14px', cursor: 'pointer', fontSize: 12, color: C.textMuted, fontFamily: 'inherit', borderRadius: 0 },
              onMouseEnter: function(e) { e.currentTarget.style.background = C.surfaceHover; },
              onMouseLeave: function(e) { e.currentTarget.style.background = 'none'; }
            }, React.createElement(LIcon, { name: 'x', size: 14, color: C.textMuted }), 'Clear selection')
          )
        ),
        // Columns toggle
        React.createElement('div', { style: { position: 'relative' } },
        React.createElement('button', {
          onClick: function() { setShowColMenu(!showColMenu); },
          className: 'dt-btn',
          style: { background: 'none', border: '1px solid ' + C.border, color: C.textMuted, cursor: 'pointer', padding: '4px 10px', borderRadius: 4, fontSize: 11, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4, transition: 'border-color 0.15s, background 0.15s' }
        }, React.createElement(LIcon, { name: 'settings', size: 12 }), ' Columns'),
        showColMenu && React.createElement('div', {
          ref: menuRef,
          style: { position: 'absolute', top: '100%', right: 0, zIndex: 100, background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: 8, minWidth: 180, boxShadow: '0 8px 30px rgba(0,0,0,0.4)', maxHeight: 'calc(calc(100vh * var(--invzoom, 1)) - 240px)', overflowY: 'auto' }
        },
          columns.filter(function(col) { return !col.locked; })
            .slice().sort(function(a, b) { return String(a.label || a.key).localeCompare(String(b.label || b.key)); })
            .map(function(col) {
            return React.createElement('div', {
              key: col.key,
              onClick: function() { toggleCol(col.key); },
              style: { display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', cursor: 'pointer', borderRadius: 4, fontSize: 12, color: visible[col.key] ? C.text : C.textDim, userSelect: 'none' }
            },
              React.createElement(SysCheckbox, { checked: !!visible[col.key], size: 16 }),
              col.label
            );
          }),
          colOrder && colOrder.length > 0 && React.createElement('button', {
            onClick: function() { resetColOrder(); setShowColMenu(false); },
            style: { display: 'flex', alignItems: 'center', gap: 6, width: '100%', marginTop: 6, background: 'none', border: 'none', borderRadius: 0, padding: '8px 8px 4px', borderTop: '1px solid ' + C.border + '66', cursor: 'pointer', fontSize: 12, color: C.textMuted, fontFamily: 'inherit' }
          }, React.createElement(LIcon, { name: 'undo-2', size: 13 }), 'Reset column order')
        )
      )
      ) // close bulk actions + columns wrapper
    ),
    // Table — scrollHeight 'fill' makes the scroll area flex-fill its parent
    // (the DataTable root becomes a flex column; page owns the height).
    // Works with pagination (default) or scrollMode batches.
    React.createElement('div', { ref: scrollRef, onScroll: handleScroll, style: scrollHeight === 'fill'
      ? { overflow: 'auto', flex: _isEmpty ? '0 0 auto' : 1, minHeight: filterMenuCol ? 340 : 0 } // empty: the height goes to the centred empty state
      : scrollMode
      ? { overflow: 'auto', height: scrollHeight || 600, maxHeight: 'calc(calc(100vh * var(--invzoom, 1)) - 210px)', minHeight: filterMenuCol ? 340 : 0, transition: 'min-height 0.15s' }
      : { overflow: 'auto', maxHeight: 'calc(calc(100vh * var(--invzoom, 1)) - 210px)', minHeight: filterMenuCol ? 340 : 0, transition: 'min-height 0.15s' } },
    React.createElement('table', { style: { width: '100%', fontSize: tableFontSize, tableLayout: 'auto' } },
      React.createElement('thead', null,
        React.createElement('tr', { style: { borderBottom: '1px solid ' + C.border } },
          normalCols.map(function(col) {
            var isSorted = sortCol === col.key;
            var arrow = isSorted ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
            var hasFilter = colFilters[col.key] && colFilters[col.key].mode;
            var isFilterOpen = filterMenuCol === col.key;
            var isFilterable = !noColumnFilters && !col.locked && !col.noFilter && col.key.charAt(0) !== '_';
            var colWidth = col.width || col.colWidth;
            return React.createElement('th', {
              key: col.key,
              className: 'dt-th',
              onClick: colNoSort(col) ? undefined : function() { handleSort(col); },
              style: {
                padding: cellPad, textAlign: 'left', fontWeight: 600, fontSize: compact ? 10 : (colCount <= 8 ? 11 : 10),
                color: isSorted ? C.accent : C.textMuted,
                textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap',
                cursor: colNoSort(col) ? 'default' : 'pointer', userSelect: 'none', transition: 'color 0.15s', position: 'sticky', top: 0, zIndex: col.sticky ? 21 : 20, background: C.bg,
                left: col.sticky ? 0 : undefined,
                width: colWidth,
                boxShadow: dragOverCol === col.key && dragCol !== col.key ? 'inset 3px 0 0 ' + C.accent : undefined,
                opacity: dragCol === col.key ? 0.35 : 1
              },
              // Drag a header onto another to reorder columns (saved per user via storageKey)
              draggable: !col.sticky,
              onDragStart: function(e) {
                setDragCol(col.key);
                try { e.dataTransfer.setData('text/plain', col.key); } catch (err) {}
                e.dataTransfer.effectAllowed = 'move';
              },
              onDragOver: function(e) {
                if (!dragCol || dragCol === col.key) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                if (dragOverCol !== col.key) setDragOverCol(col.key);
              },
              onDragLeave: function() { if (dragOverCol === col.key) setDragOverCol(null); },
              onDrop: function(e) { e.preventDefault(); dropColumn(col.key); },
              onDragEnd: function() { setDragCol(null); setDragOverCol(null); }
            },
              React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 4 } },
                col.headerRender
                  ? React.createElement('span', { style: { display: 'inline-flex', alignItems: 'center', gap: 4 } }, col.headerRender(), colNoSort(col) ? null : arrow)
                  : React.createElement('span', null, col.label + (colNoSort(col) ? '' : arrow)),
                // Filter button — its own 22px hit target so a near miss never sorts the column
                isFilterable && React.createElement('span', {
                  className: 'dt-filter-btn',
                  onClick: function(e) { e.stopPropagation(); if (isFilterOpen) { setFilterMenuCol(null); setPendingFilter(null); } else { openFilterMenu(col.key); } },
                  onMouseDown: function(e) { e.stopPropagation(); },
                  style: { cursor: 'pointer', opacity: (hasFilter || isFilterOpen) ? 1 : 0.55, marginLeft: 6, width: 22, height: 22, borderRadius: 6, flex: '0 0 auto',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: (hasFilter || isFilterOpen) ? C.accentSoft : 'transparent' }
                }, React.createElement(LIcon, { name: 'filter', size: 13, color: hasFilter ? C.accent : C.textMuted }))
              ),
              // Filter dropdown — kind-aware (list / number / date / text)
              isFilterOpen && pendingFilter && (function() {
                var kind = pendingFilter.kind || 'text';
                var setP = function(patch) { setPendingFilter(Object.assign({}, pendingFilter, patch)); };
                var valueOff = pendingFilter.mode === 'blank' || pendingFilter.mode === 'not_blank';
                var pillStyle = function(active) {
                  return { padding: '3px 9px', fontSize: 11, borderRadius: 99, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontWeight: active ? 600 : 400,
                    background: active ? C.accent : C.bg, color: active ? '#fff' : C.textMuted, transition: 'all 0.12s' };
                };
                var pillRow = function(modes, mb) {
                  return React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: mb || 6 } },
                    modes.map(function(m) {
                      return React.createElement('button', { key: m.val, onClick: function() { setP({ mode: m.val }); }, style: pillStyle(pendingFilter.mode === m.val) }, m.lbl);
                    }));
                };
                var actionBtns = function() {
                  return [
                    React.createElement('button', { key: 'clr', onClick: function() { clearFilter(col.key); },
                      style: { background: 'none', border: 'none', color: C.textMuted, padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' } }, 'Clear'),
                    React.createElement('button', { key: 'ok', onClick: function() { applyFilter(col.key); },
                      style: { background: C.accent, border: 'none', color: '#fff', padding: '5px 14px', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 } }, 'Apply')
                  ];
                };
                var body;
                if (kind === 'list') {
                  var options = _dtListOptions(col, data);
                  var q = (pendingFilter.q || '').trim().toLowerCase();
                  var shown = q ? options.filter(function(o) { return o.toLowerCase().indexOf(q) !== -1; }) : options;
                  var selectedVals = _dtSplitIn(pendingFilter.value);
                  var isSel = function(o) { return selectedVals.indexOf(o) !== -1; };
                  body = React.createElement(React.Fragment, null,
                    options.length > 8 && React.createElement('input', {
                      ref: filterInputRef, type: 'text', placeholder: 'Search ' + options.length + ' values…', value: pendingFilter.q || '',
                      onChange: function(e) { setP({ q: e.target.value }); },
                      onKeyDown: function(e) { if (e.key === 'Enter') applyFilter(col.key); if (e.key === 'Escape') { setFilterMenuCol(null); setPendingFilter(null); } },
                      className: 'dt-input',
                      style: { width: '100%', boxSizing: 'border-box', background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '6px 10px', borderRadius: 6, fontSize: 12, marginBottom: 8, fontFamily: 'inherit', outline: 'none' }
                    }),
                    React.createElement('div', { style: { display: 'flex', gap: 10, marginBottom: 8, alignItems: 'center' } },
                      React.createElement('button', {
                        onClick: function() { var merged = selectedVals.slice(); shown.forEach(function(o) { if (merged.indexOf(o) === -1) merged.push(o); }); setP({ mode: 'in', value: merged.join(DT_IN_SEP) }); },
                        style: { background: 'none', border: 'none', color: C.accent, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600, padding: 0 }
                      }, q ? 'Select shown' : 'All'),
                      React.createElement('button', {
                        onClick: function() { setP({ mode: 'in', value: '' }); },
                        style: { background: 'none', border: 'none', color: C.textMuted, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }
                      }, 'Clear'),
                      selectedVals.length > 0 && React.createElement('span', { style: { marginLeft: 'auto', fontSize: 11, color: C.textMuted } }, selectedVals.length + ' selected')
                    ),
                    React.createElement('div', { style: { maxHeight: 220, overflowY: 'auto', marginBottom: 10 } },
                      shown.length === 0 && React.createElement('div', { style: { fontSize: 12, color: C.textDim, padding: '6px 0' } }, options.length === 0 ? 'No values in this column' : 'No matches'),
                      shown.map(function(opt) {
                        var checked = isSel(opt);
                        return React.createElement('div', {
                          key: opt, role: 'option', 'aria-selected': checked,
                          onClick: function() {
                            var next = checked ? selectedVals.filter(function(v) { return v !== opt; }) : selectedVals.concat([opt]);
                            setP({ mode: 'in', value: next.join(DT_IN_SEP) });
                          },
                          style: { display: 'flex', alignItems: 'center', gap: 8, padding: '4px 2px', cursor: 'pointer', fontSize: 12, color: C.text, userSelect: 'none', borderRadius: 4 }
                        },
                          React.createElement(SysCheckbox, { checked: checked, size: 16 }),
                          React.createElement('span', { style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, opt)
                        );
                      })
                    ),
                    React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } }, actionBtns())
                  );
                } else {
                  var modes = kind === 'number'
                    ? [{ val: 'eq', lbl: '=' }, { val: 'gt', lbl: '>' }, { val: 'gte', lbl: '≥' }, { val: 'lt', lbl: '<' }, { val: 'lte', lbl: '≤' }]
                    : kind === 'date'
                      ? [{ val: 'on', lbl: 'On' }, { val: 'before', lbl: 'Before' }, { val: 'after', lbl: 'After' }]
                      : [{ val: 'contains', lbl: 'Contains' }, { val: 'equals', lbl: 'Equals' }, { val: 'starts', lbl: 'Starts with' }, { val: 'ends', lbl: 'Ends with' }];
                  body = React.createElement(React.Fragment, null,
                    React.createElement('input', {
                      ref: filterInputRef,
                      type: kind === 'date' ? 'date' : 'text',
                      inputMode: kind === 'number' ? 'decimal' : undefined,
                      placeholder: kind === 'number' ? 'Enter a number' : kind === 'date' ? '' : 'Type to filter...',
                      value: pendingFilter.value || '',
                      onChange: function(e) { setP({ value: e.target.value }); },
                      onKeyDown: function(e) { if (e.key === 'Enter') applyFilter(col.key); if (e.key === 'Escape') { setFilterMenuCol(null); setPendingFilter(null); } },
                      disabled: valueOff,
                      className: 'dt-input',
                      style: { width: '100%', boxSizing: 'border-box', background: C.bg, border: '1px solid ' + C.border, color: valueOff ? C.textDim : C.text, padding: '7px 10px', borderRadius: 6, fontSize: 12, marginBottom: 8, fontFamily: 'inherit', outline: 'none', colorScheme: kind === 'date' ? (getThemeName() === 'dark' ? 'dark' : 'light') : undefined }
                    }),
                    pillRow(modes),
                    pillRow([{ val: 'blank', lbl: 'Is blank' }, { val: 'not_blank', lbl: 'Not blank' }], 10),
                    React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
                      kind === 'text' && !valueOff
                        ? React.createElement('div', { onClick: function() { setP({ matchCase: !pendingFilter.matchCase }); }, style: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: C.textMuted, cursor: 'pointer', userSelect: 'none', flex: 1 } },
                            React.createElement(SysCheckbox, { checked: !!pendingFilter.matchCase, size: 14 }),
                            'Aa')
                        : React.createElement('div', { style: { flex: 1 } }),
                      actionBtns()
                    )
                  );
                }
                return React.createElement('div', {
                  ref: filterMenuRef,
                  onClick: function(e) { e.stopPropagation(); },
                  style: { position: 'absolute', top: '100%', left: 0, zIndex: 200, background: C.surface, border: '1px solid ' + C.border, borderRadius: 10, padding: '12px 14px', minWidth: 250, maxWidth: 340, boxShadow: '0 8px 30px rgba(0,0,0,0.35)', textTransform: 'none', letterSpacing: 'normal', fontWeight: 400, maxHeight: 'calc(calc(100vh * var(--invzoom, 1)) - 240px)', overflowY: 'auto', cursor: 'default' }
                },
                  React.createElement('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 } },
                    'Filter ' + col.label, React.createElement('span', { style: { fontWeight: 400, color: C.textDim, marginLeft: 6 } }, kind === 'list' ? 'values' : kind)),
                  body
                );
              })()
            );
          }),
          onAction && React.createElement('th', { style: { width: 40, padding: cellPad } })
        )
      ),
      React.createElement('tbody', null,
        pageData.map(function(row, ri) {
          var totalNormalCols = normalCols.length + (onAction ? 1 : 0);
          var _rowHref = rowHref ? rowHref(row) : null;
          var _rs = rowStyle ? (rowStyle(row) || null) : null;
          var _rbg = (_rs && _rs.background) || 'transparent';
          var mainRow = React.createElement('tr', {
            key: row.id || ri,
            // Suppress Chrome's auto-scroll on middle-click so onAuxClick can open a new tab.
            onMouseDown: (_rowHref || onRowClick) ? function(e) { if (_rowHref && e.button === 1) e.preventDefault(); if (onRowClick && e.shiftKey) e.preventDefault(); } : undefined,
            onClick: onRowClick ? function(e) { if (_rowHref && (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1)) { window.open(_rowHref, '_blank'); return; } onRowClick(row, e); } : undefined,
            onAuxClick: onRowClick && _rowHref ? function(e) { if (e.button === 1) { e.preventDefault(); window.open(_rowHref, '_blank'); } } : undefined,
            style: Object.assign({ borderBottom: subRowCols.length > 0 ? 'none' : '1px solid ' + C.border, cursor: onRowClick ? 'pointer' : 'default', userSelect: 'none' }, _rs || {}),
            onMouseEnter: function(e) { var next = e.currentTarget.nextElementSibling; e.currentTarget.style.background = C.surfaceHover; if (next && next.dataset.subrow) next.style.background = C.surfaceHover; },
            onMouseLeave: function(e) { var next = e.currentTarget.nextElementSibling; e.currentTarget.style.background = _rbg; if (next && next.dataset.subrow) next.style.background = _rbg; }
          },
            normalCols.map(function(col) {
              var content = col.render ? col.render(row) : (row[col.key] != null ? String(row[col.key]) : '');
              if (col.copyable) {
                var copyVal = typeof col.copyValue === 'function' ? col.copyValue(row) : row[col.key];
                if (copyVal != null && String(copyVal).trim() !== '') {
                  content = React.createElement(CopyCell, { value: copyVal }, content);
                }
              }
              var baseStyle = Object.assign({ padding: cellPad, whiteSpace: col.wrap ? 'normal' : 'nowrap' }, col.style || { color: C.textMuted }, col.sticky ? { position: 'sticky', left: 0, zIndex: 10, background: C.bg } : {});
              if (col.width || col.colWidth) { baseStyle.width = col.width || col.colWidth; baseStyle.minWidth = col.width || col.colWidth; baseStyle.maxWidth = col.width || col.colWidth; }
              var tdProps = { key: col.key, style: baseStyle };
              if (col.stopClick) tdProps.onClick = function(e) { e.stopPropagation(); };
              return React.createElement(TD, tdProps, content);
            }),
            onAction && React.createElement(TD, { style: { padding: cellPad } },
              React.createElement('button', {
                onClick: function(e) { e.stopPropagation(); onAction('delete', row); },
                style: { background: 'none', border: 'none', color: C.textDim, cursor: 'pointer' }
              }, React.createElement(LIcon, { name: 'trash-2', size: 14 }))
            )
          );
          if (subRowCols.length === 0) return mainRow;
          var subRow = React.createElement('tr', {
            key: (row.id || ri) + '_sub', 'data-subrow': 'true',
            onMouseDown: (_rowHref || onRowClick) ? function(e) { if (_rowHref && e.button === 1) e.preventDefault(); if (onRowClick && e.shiftKey) e.preventDefault(); } : undefined,
            onClick: onRowClick ? function(e) { if (_rowHref && (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1)) { window.open(_rowHref, '_blank'); return; } onRowClick(row, e); } : undefined,
            onAuxClick: onRowClick && _rowHref ? function(e) { if (e.button === 1) { e.preventDefault(); window.open(_rowHref, '_blank'); } } : undefined,
            style: { borderBottom: '1px solid ' + C.border, cursor: onRowClick ? 'pointer' : 'default', userSelect: 'none' },
            onMouseEnter: function(e) { var prev = e.currentTarget.previousElementSibling; e.currentTarget.style.background = C.surfaceHover; if (prev) prev.style.background = C.surfaceHover; },
            onMouseLeave: function(e) { var prev = e.currentTarget.previousElementSibling; e.currentTarget.style.background = 'transparent'; if (prev) prev.style.background = 'transparent'; }
          },
            React.createElement('td', { colSpan: totalNormalCols, style: { padding: '0 ' + (cellPad.split(' ')[1] || '12px') + ' 6px', fontSize: tableFontSize - 1 } },
              React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: '2px 16px' } },
                subRowCols.map(function(col) {
                  var content = col.render ? col.render(row) : (row[col.key] != null ? String(row[col.key]) : '');
                  if (!content && content !== 0) return null;
                  return React.createElement('span', { key: col.key, style: Object.assign({ color: C.textMuted }, col.style) },
                    React.createElement('span', { style: { fontSize: tableFontSize - 2, color: C.textDim, marginRight: 4 } }, col.label + ':'),
                    content
                  );
                })
              )
            )
          );
          return React.createElement(React.Fragment, { key: row.id || ri }, mainRow, subRow);
        })
      )
    ),
    ), // close table wrapper div
    // Empty state
    _isEmpty && React.createElement(Empty, { title: emptyTitle || 'No data', sub: emptySub || '', fill: scrollHeight === 'fill' }),
    // Pagination footer (scroll mode: progress line only — batches load on scroll)
    !_isEmpty && scrollMode && React.createElement('div', {
      style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 14px', borderTop: '1px solid ' + C.border + '44', fontSize: 12, color: C.textMuted }
    },
      React.createElement('span', null, 'Showing ' + showingTo.toLocaleString() + ' of ' + totalRows.toLocaleString() + (activeFilterCount > 0 ? ' (filtered from ' + data.length.toLocaleString() + ')' : '')),
      showingTo < totalRows && React.createElement('span', { style: { color: C.textDim } }, 'scroll to load ' + Math.min(pageSize, totalRows - showingTo) + ' more')
    ),
    !_isEmpty && !scrollMode && totalRows > pageSize && React.createElement('div', {
      style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderTop: '1px solid ' + C.border + '44', fontSize: 13, color: C.textMuted }
    },
      React.createElement('span', null, 'Showing ' + showingFrom + '–' + showingTo + ' of ' + totalRows.toLocaleString() + (activeFilterCount > 0 ? ' (filtered from ' + data.length.toLocaleString() + ')' : '')),
      React.createElement('div', { style: { display: 'flex', gap: 4 } },
        React.createElement('button', {
          onClick: function() { setCurrentPage(0); }, disabled: currentPage === 0,
          style: { padding: '5px 10px', fontSize: 12, borderRadius: 4, border: '1px solid ' + C.border, background: 'transparent', color: currentPage === 0 ? C.textDim : C.textMuted, cursor: currentPage === 0 ? 'default' : 'pointer', fontFamily: 'inherit' }
        }, '« First'),
        React.createElement('button', {
          onClick: function() { setCurrentPage(Math.max(0, currentPage - 1)); }, disabled: currentPage === 0,
          style: { padding: '5px 10px', fontSize: 12, borderRadius: 4, border: '1px solid ' + C.border, background: 'transparent', color: currentPage === 0 ? C.textDim : C.textMuted, cursor: currentPage === 0 ? 'default' : 'pointer', fontFamily: 'inherit' }
        }, '‹ Prev'),
        React.createElement('span', { style: { padding: '5px 10px', fontSize: 12, color: C.text, fontWeight: 500 } }, 'Page ' + (currentPage + 1) + ' of ' + totalPages),
        React.createElement('button', {
          onClick: function() { setCurrentPage(Math.min(totalPages - 1, currentPage + 1)); }, disabled: currentPage >= totalPages - 1,
          style: { padding: '5px 10px', fontSize: 12, borderRadius: 4, border: '1px solid ' + C.border, background: 'transparent', color: currentPage >= totalPages - 1 ? C.textDim : C.textMuted, cursor: currentPage >= totalPages - 1 ? 'default' : 'pointer', fontFamily: 'inherit' }
        }, 'Next ›'),
        React.createElement('button', {
          onClick: function() { setCurrentPage(totalPages - 1); }, disabled: currentPage >= totalPages - 1,
          style: { padding: '5px 10px', fontSize: 12, borderRadius: 4, border: '1px solid ' + C.border, background: 'transparent', color: currentPage >= totalPages - 1 ? C.textDim : C.textMuted, cursor: currentPage >= totalPages - 1 ? 'default' : 'pointer', fontFamily: 'inherit' }
        }, 'Last »')
      )
    )
  );
}

// CSV download helpers
function downloadCSV(filename, csvContent) {
  var blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  var url = URL.createObjectURL(blob);
  var a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// One CSV cell rule for every export in the app (review 2026-09-28, medium #31): quoted when it
// holds a quote, comma or line break, and defused when it would start a spreadsheet formula
// (= + - @ tab CR) — a marketplace title or note like "=HYPERLINK(...)" ran as a formula when the
// file was opened in Excel. Plain numbers ("-3.5") stay numbers. Same rule as server/services/csv.js
// and the shipping app's shipCsvCell.
function escapeCSV(val) {
  if (val == null) return '';
  var s = String(val);
  if (/^[=+\-@\t\r]/.test(s) && !/^[-+]?\d+(\.\d+)?$/.test(s)) s = "'" + s;
  return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}

function arrayToCSV(headers, rows) {
  var lines = [headers.map(escapeCSV).join(',')];
  rows.forEach(function(row) {
    lines.push(headers.map(function(h) { return escapeCSV(row[h]); }).join(','));
  });
  return lines.join('\n');
}

function downloadTemplate(filename, headers) {
  downloadCSV(filename, headers.join(',') + '\n');
}

// ─── MARKETPLACE CHANNEL ICONS (SVG logos) ───
function channelIcon(channel, size) {
  var s = size || 20;
  var svgProps = { width: s, height: s, viewBox: '0 0 24 24', style: { display: 'block', flexShrink: 0 } };

  if (channel === 'walmart') {
    return React.createElement('svg', Object.assign({}, svgProps, { viewBox: '0 0 24 24' }),
      React.createElement('circle', { cx: 12, cy: 12, r: 12, fill: '#0071DC' }),
      React.createElement('path', { d: 'M12 4.5 L12 9.5 M12 14.5 L12 19.5 M5.2 8 L9.5 10.5 M14.5 13.5 L18.8 16 M18.8 8 L14.5 10.5 M9.5 13.5 L5.2 16', stroke: '#FFC220', strokeWidth: 2.2, strokeLinecap: 'round', fill: 'none' })
    );
  }
  if (channel === 'amazon') {
    return React.createElement('svg', Object.assign({}, svgProps, { viewBox: '0 0 24 24' }),
      React.createElement('circle', { cx: 12, cy: 12, r: 12, fill: '#232F3E' }),
      React.createElement('path', { d: 'M7 13.5 Q12 17 17 13.5', stroke: '#FF9900', strokeWidth: 2, strokeLinecap: 'round', fill: 'none' }),
      React.createElement('path', { d: 'M15.5 12 L17 13.5 L15.5 15', stroke: '#FF9900', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round', fill: 'none' }),
      React.createElement('text', { x: 12, y: 11.5, textAnchor: 'middle', fill: '#FFFFFF', fontSize: 7, fontWeight: 700, fontFamily: 'Arial' }, 'a')
    );
  }
  if (channel === 'ebay') {
    return React.createElement('svg', Object.assign({}, svgProps, { viewBox: '0 0 24 24' }),
      React.createElement('circle', { cx: 12, cy: 12, r: 12, fill: '#FFFFFF' }),
      React.createElement('circle', { cx: 12, cy: 12, r: 11.5, fill: 'none', stroke: '#E0E0E0', strokeWidth: 0.5 }),
      React.createElement('text', { x: 3.5, y: 15.5, fill: '#E53238', fontSize: 8.5, fontWeight: 700, fontFamily: 'Arial' }, 'e'),
      React.createElement('text', { x: 8, y: 15.5, fill: '#0064D2', fontSize: 8.5, fontWeight: 700, fontFamily: 'Arial' }, 'b'),
      React.createElement('text', { x: 12.5, y: 15.5, fill: '#F5AF02', fontSize: 8.5, fontWeight: 700, fontFamily: 'Arial' }, 'a'),
      React.createElement('text', { x: 17.2, y: 15.5, fill: '#86B817', fontSize: 8.5, fontWeight: 700, fontFamily: 'Arial' }, 'y')
    );
  }
  if (channel === 'mercadolibre') {
    return React.createElement('svg', Object.assign({}, svgProps, { viewBox: '0 0 24 24' }),
      React.createElement('circle', { cx: 12, cy: 12, r: 12, fill: '#FFE600' }),
      React.createElement('ellipse', { cx: 12, cy: 12, rx: 9, ry: 6.5, fill: 'none', stroke: '#2D3277', strokeWidth: 1.4 }),
      React.createElement('path', { d: 'M6.5 12.5 Q9 9.5 11 11.5 Q12 12.5 13 11.5 Q15 9.5 17.5 12.5', stroke: '#2D3277', strokeWidth: 1.3, strokeLinecap: 'round', fill: 'none' })
    );
  }
  if (channel === 'shopify') {
    return React.createElement('svg', Object.assign({}, svgProps, { viewBox: '0 0 24 24' }),
      React.createElement('circle', { cx: 12, cy: 12, r: 12, fill: '#96BF48' }),
      React.createElement('path', { d: 'M14.5 5.5 L14.2 6.5 C14.2 6.5 13.5 6.3 12.7 6.4 C11.5 6.5 11.4 7.2 11.4 7.5 C11.5 8.5 14.6 8.8 14.8 11.3 C14.9 13.2 13.7 14.5 11.8 14.6 C9.6 14.7 8.4 13.4 8.4 13.4 L8.9 11.5 C8.9 11.5 10.1 12.5 11.1 12.4 C11.7 12.4 11.9 11.9 11.9 11.6 C11.8 10.3 9.3 10.4 9.1 8.1 C8.9 6.2 10.3 5.2 12.1 5 C12.9 4.9 13.3 5.1 13.3 5.1 L14.5 5.5 Z M10.5 18 L15 17 L15 7', stroke: '#FFFFFF', strokeWidth: 0.3, fill: '#FFFFFF', fillOpacity: 0.9 })
    );
  }
  // Fallback icons for non-marketplace channels
  var fallbacks = { email: 'mail', phone: 'phone', chat: 'message-square', other: 'pin' };
  return React.createElement(LIcon, { name: fallbacks[channel] || 'pin', size: s * 0.8 });
}

// ─── FILL BAR (color-coded progress) ───
function FillBar({ pct }) {
  var p = Math.min(Math.max(Number(pct) || 0, 0), 100);
  var color = p >= 90 ? C.danger : p >= 70 ? C.warning : p >= 40 ? C.accent : C.success;
  return React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 6 } },
    React.createElement('div', { style: { flex: 1, height: 6, background: C.bg, borderRadius: 3, minWidth: 40, overflow: 'hidden' } },
      React.createElement('div', { style: { width: p + '%', height: '100%', background: color, borderRadius: 3, transition: 'width 0.3s' } })
    ),
    React.createElement('span', { style: { fontSize: 11, fontWeight: 600, color: color, minWidth: 32, textAlign: 'right' } }, p.toFixed(0) + '%')
  );
}

// ─── BIN BADGE (type/status colored badge) ───
function BinBadge({ type, value }) {
  var colors = {};
  if (type === 'bin_type') {
    colors = { shelf: { bg: C.accentSoft, text: C.accent }, rack: { bg: C.purpleSoft, text: C.purple }, floor: { bg: C.warningSoft, text: C.warning }, pick: { bg: C.successSoft, text: C.success }, overstock: { bg: 'rgba(139,143,163,0.12)', text: C.textMuted }, staging: { bg: 'rgba(6,182,212,0.12)', text: '#06B6D4' }, dock: { bg: 'rgba(245,158,11,0.12)', text: '#F59E0B' } };
  } else {
    colors = { active: { bg: C.successSoft, text: C.success }, full: { bg: C.dangerSoft, text: C.danger }, unsellable: { bg: C.accentSoft, text: C.accent }, damaged: { bg: C.warningSoft, text: C.warning }, inactive: { bg: 'rgba(139,143,163,0.12)', text: C.textMuted } };
  }
  var c = colors[value] || { bg: C.warningSoft, text: C.warning };
  return React.createElement('span', {
    style: { background: c.bg, color: c.text, padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }
  }, value);
}

// ─── CODE 128B BARCODE SVG GENERATOR ───
var _c128Patterns = [
  '11011001100','11001101100','11001100110','10010011000','10010001100',
  '10001001100','10011001000','10011000100','10001100100','11001001000',
  '11001000100','11000100100','10110011100','10011011100','10011001110',
  '10111001100','10011101100','10011100110','11001110010','11001011100',
  '11001001110','11011100100','11001110100','11100101100','11100100110',
  '11101100100','11100110100','11100110010','11011011000','11011000110',
  '11000110110','10100011000','10001011000','10001000110','10110001000',
  '10001101000','10001100010','11010001000','11000101000','11000100010',
  '10110111000','10110001110','10001101110','10111011000','10111000110',
  '10001110110','11101110110','11010001110','11000101110','11011101000',
  '11011100010','11011101110','11101011000','11101000110','11100010110',
  '11101101000','11101100010','11100011010','11101111010','11001000010',
  '11110001010','10100110000','10100001100','10010110000','10010000110',
  '10000101100','10000100110','10110010000','10110000100','10011010000',
  '10011000010','10000110100','10000110010','11000010010','11001010000',
  '11110111010','11000010100','10001111010','10100111100','10010111100',
  '10010011110','10111100100','10011110100','10011110010','11110100100',
  '11110010100','11110010010','11011011110','11011110110','11110110110',
  '10101111000','10100011110','10001011110','10111101000','10111100010',
  '10001111100','11110101000','11110100010','10111011110','10111101110',
  '11101011110','11110101110','11010000100','11010010000','11010011100','11000111010',
  '1100011101011'
];

function generateCode128SVG(text, barHeight, moduleWidth) {
  barHeight = barHeight || 60;
  moduleWidth = moduleWidth || 2;
  var startCode = 104; // Code B
  var codes = [startCode];
  var checksum = startCode;
  for (var i = 0; i < text.length; i++) {
    var val = text.charCodeAt(i) - 32;
    if (val < 0 || val > 94) val = 0;
    codes.push(val);
    checksum += val * (i + 1);
  }
  codes.push(checksum % 103);
  codes.push(106); // stop
  var bits = '';
  for (var j = 0; j < codes.length; j++) bits += _c128Patterns[codes[j]];
  var totalWidth = bits.length * moduleWidth;
  var bars = '';
  for (var k = 0; k < bits.length; k++) {
    if (bits[k] === '1') {
      bars += '<rect x="' + (k * moduleWidth) + '" y="0" width="' + moduleWidth + '" height="' + barHeight + '"/>';
    }
  }
  return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + totalWidth + ' ' + barHeight + '" width="100%">' + bars + '</svg>';
}

// Print-optimized: renders in mm units so barcode prints at exact physical size with no CSS scaling
function generateCode128SVGmm(text, targetWidthMm, barHeightMm) {
  barHeightMm = barHeightMm || 10;
  var startCode = 104;
  var codes = [startCode];
  var checksum = startCode;
  for (var i = 0; i < text.length; i++) {
    var val = text.charCodeAt(i) - 32;
    if (val < 0 || val > 94) val = 0;
    codes.push(val);
    checksum += val * (i + 1);
  }
  codes.push(checksum % 103);
  codes.push(106);
  var bits = '';
  for (var j = 0; j < codes.length; j++) bits += _c128Patterns[codes[j]];
  var modW = targetWidthMm / bits.length;
  var bars = '';
  for (var k = 0; k < bits.length; k++) {
    if (bits[k] === '1') {
      bars += '<rect x="' + (k * modW).toFixed(4) + '" y="0" width="' + modW.toFixed(4) + '" height="' + barHeightMm + '"/>';
    }
  }
  return '<svg xmlns="http://www.w3.org/2000/svg" width="' + targetWidthMm + 'mm" height="' + barHeightMm + 'mm" viewBox="0 0 ' + targetWidthMm + ' ' + barHeightMm + '">' + bars + '</svg>';
}

// Generate barcode as data URL image using bwip-js (reliable Code 128)
function generateBarcodeImg(text, widthMm, heightMm) {
  if (!text || !window.bwipjs) return '';
  try {
    var canvas = document.createElement('canvas');
    bwipjs.toCanvas(canvas, {
      bcid: 'code128',
      text: text,
      scale: 3,
      height: Math.max(6, Math.round(heightMm * 0.8)),
      includetext: false,
      textxalign: 'center',
    });
    var dataUrl = canvas.toDataURL('image/png');
    return '<img src="' + dataUrl + '" style="width:' + widthMm + 'mm;height:' + heightMm + 'mm;image-rendering:pixelated;" />';
  } catch (e) {
    console.error('Barcode generation failed:', e);
    return '';
  }
}

// HTML-escape for values interpolated into print popups (document.write) and any other markup
// string (review M6 #71). Vendor titles, SKUs, barcodes, PO numbers and bin codes all come from
// vendor confirmations, CSV imports and channel syncs — a bare '&' broke label layout and
// markup would run with the user's session in the same-origin popup.
function escHtml(s) {
  return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// Calendar-date formatter for DATE columns (review M6 #75). pg returns a DATE as a JS Date at
// server-local midnight, serialised "YYYY-MM-DDT00:00:00.000Z" (prod Node runs in UTC), so
// `new Date(v).toLocaleDateString()` in an Eastern browser printed the PREVIOUS day. Read the
// civil date off the string and build a local Date from its parts; never parse it as an instant.
// Returns '' for empty input so exports stay blank; callers add their own '—'.
function fmtDateOnly(v, opts, locale) {
  if (v == null || v === '') return '';
  var m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
  var d = m ? new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10)) : new Date(v);
  if (isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString(locale || undefined, opts || undefined);
}

// ─── Product label rendering shared by the label printers (Mercado Libre labels, 2026-09-29) ───
// A label = { code, title, sku, condition, expiry }; `tpl` is a label_templates row, null falls
// back to the 2.25 x 1.25 in roll the older printers assumed. The markup mirrors the FBA / FBM
// printers (barcode, code in bold, title, bottom row) so a MELI label on the same stock looks
// like an FNSKU label. `opts.skuPrefix` prints "SKU: " before the SKU, as Mercado Libre's own
// label file does.
function labelTemplateGeometry(tpl) {
  return {
    widthMm: tpl ? parseFloat(tpl.width_mm) : 57.15,
    heightMm: tpl ? parseFloat(tpl.height_mm) : 31.75,
    fontSize: tpl ? (tpl.font_size || 12) : 12,
    fields: tpl ? (tpl.fields || ['fnsku', 'sku', 'title']) : ['fnsku', 'sku', 'title'],
    showBarcode: tpl ? tpl.show_barcode !== false : true,
    isSheet: tpl ? tpl.label_type === 'sheet' : false,
    cols: tpl ? (tpl.columns || 1) : 1,
    rowsPerSheet: tpl ? (tpl.rows_per_sheet || 1) : 1,
    pageW: tpl && tpl.page_width_mm ? parseFloat(tpl.page_width_mm) : 215.9,
    pageH: tpl && tpl.page_height_mm ? parseFloat(tpl.page_height_mm) : 279.4,
  };
}

function productLabelBottomLine(label, fields, opts) {
  var parts = [];
  if (fields.indexOf('condition') >= 0 && label.condition) parts.push(label.condition);
  if (fields.indexOf('sku') >= 0 && label.sku) parts.push((opts && opts.skuPrefix ? 'SKU: ' : '') + label.sku);
  if (fields.indexOf('expiry') >= 0 && label.expiry) parts.push('Exp: ' + label.expiry);
  return parts.join(' | ');
}

// The title line of every product label: up to two lines, the way Mercado Libre's own label wraps
// a long title (Anthony 2026-09-29: "some of the titles are cut off", then "fix those too" for
// the FBA, FBM and WFS printers). A 2 x 1 label has the room: barcode, code, two title lines and
// the SKU line add up to about 18 of its 21 mm.
function productLabelTitleCss(pt, lines) {
  if (lines === 1) return 'font-size:' + pt + 'pt;color:#333;text-align:center;line-height:1.1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100%;';
  return 'font-size:' + pt + 'pt;color:#333;text-align:center;line-height:1.15;overflow:hidden;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;max-width:100%;word-break:break-word;';
}

// How many title lines the label has room for between the code line and the bottom row, and at
// what size: two when two fit (the type comes down half a point at a time, to 6 pt, to make them
// fit), else one at the template's size. A 2 x 1 label at 12 pt has room for two lines at 6 pt;
// the FBA 2 x 1 at 16 pt does not, its title stays on one line unless the template's type is
// made smaller. Sizes in mm; a text line is 1.15 of its type size.
function productLabelTitleFit(heightMm, fontSize, barcodeHeightMm) {
  var pad = 2, gap = 0.3;
  var mm = function(pt) { return pt * 0.3528; };
  var codePt = Math.max(5, fontSize - 4), bottomPt = Math.max(4, fontSize - 6);
  var titlePt = Math.max(4, fontSize - 6);
  var used = pad * 2 + (barcodeHeightMm || 0) + gap + mm(codePt) * 1.2 + gap + mm(bottomPt) * 1.2 + gap;
  var room = heightMm - used;
  for (var pt = titlePt; pt >= 6; pt -= 0.5) {
    if (mm(pt) * 1.15 * 2 <= room) return { lines: 2, pt: pt };
  }
  return { lines: 1, pt: titlePt };
}

function productLabelHtml(label, tpl, opts) {
  var g = labelTemplateGeometry(tpl);
  var code = label.code || '';
  var small = Math.max(4, g.fontSize - 6);
  var fit = productLabelTitleFit(g.heightMm, g.fontSize, g.showBarcode ? Math.max(4, g.heightMm * 0.28) : 0);
  var lbl = '<div style="width:' + g.widthMm + 'mm;height:' + g.heightMm + 'mm;box-sizing:border-box;padding:2mm;display:flex;flex-direction:column;justify-content:center;align-items:center;overflow:hidden;page-break-inside:avoid;">';
  if (g.showBarcode && code) {
    lbl += '<div style="text-align:center;margin-bottom:0.3mm;">' + generateBarcodeImg(code, g.widthMm - 4, Math.max(4, g.heightMm * 0.28)) + '</div>';
  }
  if (code) lbl += '<div style="font-family:monospace;font-size:' + Math.max(5, g.fontSize - 4) + 'pt;font-weight:bold;letter-spacing:0.5px;margin-bottom:0.3mm;text-align:center;">' + escHtml(code) + '</div>';
  if (label.title && g.fields.indexOf('title') >= 0) {
    lbl += '<div style="' + productLabelTitleCss(fit.pt, fit.lines) + '">' + escHtml(label.title) + '</div>';
  }
  var bottom = productLabelBottomLine(label, g.fields, opts);
  if (bottom) lbl += '<div style="font-size:' + small + 'pt;color:#333;text-align:center;margin-top:0.3mm;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100%;">' + escHtml(bottom) + '</div>';
  return lbl + '</div>';
}

// Lays the labels out: pages of cols x rows for a sheet template, a plain flow for a roll.
function productLabelPagesHtml(labelHtmls, tpl) {
  var g = labelTemplateGeometry(tpl);
  if (!g.isSheet) return '<div style="display:flex;flex-wrap:wrap;gap:0;justify-content:center;">' + labelHtmls.join('') + '</div>';
  var mH = (g.pageW - (g.cols * g.widthMm)) / (g.cols + 1);
  var mV = (g.pageH - (g.rowsPerSheet * g.heightMm)) / (g.rowsPerSheet + 1);
  var perPage = g.cols * g.rowsPerSheet;
  var pages = [];
  for (var p = 0; p < labelHtmls.length; p += perPage) {
    pages.push('<div style="width:' + g.pageW + 'mm;height:' + g.pageH + 'mm;box-sizing:border-box;padding:' + mV + 'mm ' + mH + 'mm;display:flex;flex-wrap:wrap;align-content:flex-start;gap:0;page-break-after:always;"><div style="display:grid;grid-template-columns:repeat(' + g.cols + ',' + g.widthMm + 'mm);grid-template-rows:repeat(' + g.rowsPerSheet + ',' + g.heightMm + 'mm);gap:0;width:100%;">' + labelHtmls.slice(p, p + perPage).join('') + '</div></div>');
  }
  return pages.join('');
}

// Opens the print window and drives printing from the opener: the page CSP blocks an inline
// script in the popup and window.onload is unreliable for document.write popups. Returns false
// when the browser blocked the popup.
function openLabelPrintWindow(bodyHtml, title, tpl) {
  var g = labelTemplateGeometry(tpl);
  var pw = window.open('', '_blank', 'width=600,height=800');
  if (!pw) return false;
  pw.document.write('<!DOCTYPE html><html><head><title>' + escHtml(title || 'Labels') + '</title><style>@media print{body{margin:0;}@page{size:' + (g.isSheet ? g.pageW + 'mm ' + g.pageH + 'mm' : g.widthMm + 'mm ' + g.heightMm + 'mm') + ';margin:0;}div{page-break-inside:avoid;}}</style></head><body style="font-family:Arial,sans-serif;margin:0;padding:0;">' + bodyHtml + '</body></html>');
  pw.document.close();
  setTimeout(function() { try { pw.focus(); pw.print(); } catch (e) { /* popup closed */ } }, 500);
  return true;
}

// Splits a title into at most two lines that fit `maxMm` at `pt`, shrinking the type half a
// point at a time (never under 4 pt) before cutting. `doc` is a jsPDF document with the font
// already set; its splitTextToSize measures the real glyph widths.
function productLabelTitleLines(doc, title, maxMm, pt, maxLines) {
  var cap = maxLines === 1 ? 1 : 2;
  var lines = [];
  for (;;) {
    doc.setFontSize(pt);
    lines = doc.splitTextToSize(String(title), maxMm);
    if (lines.length <= cap || pt <= 4) break;
    pt = Math.max(4, pt - 0.5);
  }
  if (lines.length > cap) {
    lines = lines.slice(0, cap);
    lines[cap - 1] = lines[cap - 1].slice(0, Math.max(0, lines[cap - 1].length - 2)) + '…';
  }
  return { lines: lines, pt: pt };
}

// The same labels as one PDF (jsPDF + bwip-js), for a vendor who applies the labels: one page
// per label on a roll, cols x rows per page on a sheet. Returns the jsPDF document, or null when
// a library is missing.
function productLabelsPdf(labels, tpl, opts) {
  var jspdfNs = window.jspdf || window.jsPDF;
  var JsPDFCtor = jspdfNs && (jspdfNs.jsPDF || jspdfNs);
  if (!JsPDFCtor || !window.bwipjs) return null;
  var g = labelTemplateGeometry(tpl);
  var pad = 2;
  var barcodeHeightMm = Math.max(4, g.heightMm * 0.28);
  var barcodeWidthMm = g.widthMm - pad * 2;
  var ptToMm = function(pt) { return pt * 0.3528; };
  var clip = function(str, maxMm, sizePt) {
    if (!str) return '';
    var maxChars = Math.floor(maxMm / (ptToMm(sizePt) * 0.5));
    return str.length > maxChars ? str.slice(0, Math.max(0, maxChars - 1)) + '…' : str;
  };
  var renderAt = function(doc, label, x, y) {
    var cursorY = y + pad;
    var code = label.code || '';
    if (g.showBarcode && code) {
      var canvas = document.createElement('canvas');
      window.bwipjs.toCanvas(canvas, { bcid: 'code128', text: code, scale: 3, height: barcodeHeightMm, includetext: false, paddingleft: 4, paddingright: 4 });
      doc.addImage(canvas.toDataURL('image/png'), 'PNG', x + pad, cursorY, barcodeWidthMm, barcodeHeightMm);
      cursorY += barcodeHeightMm + 0.5;
    }
    if (code) {
      var codePt = Math.max(5, g.fontSize - 4);
      doc.setFont('courier', 'bold'); doc.setFontSize(codePt);
      cursorY += ptToMm(codePt) * 0.75;
      doc.text(code, x + g.widthMm / 2, cursorY, { align: 'center' });
      cursorY += 0.4;
    }
    if (label.title && g.fields.indexOf('title') >= 0) {
      // Up to two lines, measured with the real font (Anthony 2026-09-29: "some of the titles are
      // cut off"): the type shrinks half a point at a time before anything is cut, and only a
      // title that still needs a third line at 4 pt loses its tail.
      var fit = productLabelTitleFit(g.heightMm, g.fontSize, g.showBarcode ? barcodeHeightMm : 0);
      var titlePt = fit.pt;
      doc.setFont('helvetica', 'normal');
      var titleLines = productLabelTitleLines(doc, label.title, g.widthMm - pad * 2, titlePt, fit.lines);
      titlePt = titleLines.pt;
      doc.setFontSize(titlePt);
      titleLines.lines.forEach(function(line, idx) {
        cursorY += ptToMm(titlePt) * (idx === 0 ? 0.75 : 1.15);
        doc.text(line, x + g.widthMm / 2, cursorY, { align: 'center' });
      });
    }
    var bottom = productLabelBottomLine(label, g.fields, opts);
    if (bottom) {
      var botPt = Math.max(4, g.fontSize - 6);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(botPt);
      doc.text(clip(bottom, g.widthMm - pad * 2, botPt), x + g.widthMm / 2, y + g.heightMm - pad, { align: 'center' });
    }
  };
  var doc;
  if (g.isSheet) {
    var perPage = g.cols * g.rowsPerSheet;
    var mH = (g.pageW - g.cols * g.widthMm) / (g.cols + 1);
    var mV = (g.pageH - g.rowsPerSheet * g.heightMm) / (g.rowsPerSheet + 1);
    var pageOrient = g.pageW >= g.pageH ? 'landscape' : 'portrait';
    doc = new JsPDFCtor({ orientation: pageOrient, unit: 'mm', format: [g.pageW, g.pageH] });
    labels.forEach(function(label, i) {
      var slot = i % perPage;
      if (i > 0 && slot === 0) doc.addPage([g.pageW, g.pageH], pageOrient);
      renderAt(doc, label, mH + (slot % g.cols) * (g.widthMm + mH), mV + Math.floor(slot / g.cols) * (g.heightMm + mV));
    });
  } else {
    var rollOrient = g.widthMm >= g.heightMm ? 'landscape' : 'portrait';
    doc = new JsPDFCtor({ orientation: rollOrient, unit: 'mm', format: [g.widthMm, g.heightMm] });
    labels.forEach(function(label, i) {
      if (i > 0) doc.addPage([g.widthMm, g.heightMm], rollOrient);
      renderAt(doc, label, 0, 0);
    });
  }
  return doc;
}
window.labelTemplateGeometry = labelTemplateGeometry; window.productLabelHtml = productLabelHtml; window.productLabelPagesHtml = productLabelPagesHtml;
window.productLabelTitleCss = productLabelTitleCss; window.productLabelTitleLines = productLabelTitleLines; window.productLabelTitleFit = productLabelTitleFit;
window.openLabelPrintWindow = openLabelPrintWindow; window.productLabelsPdf = productLabelsPdf;

function printBinLabel(binCode) {
  var barcodeSVG = generateCode128SVG(binCode, 80, 3);
  var html = '<!DOCTYPE html><html><head><title>Bin Label</title><style>' +
    '@page { size: 4in 4in; margin: 0; }' +
    'body { margin: 0; padding: 0; width: 4in; height: 4in; display: flex; flex-direction: column; align-items: center; justify-content: center; font-family: Arial, Helvetica, sans-serif; }' +
    '.bin-code { font-size: 48pt; font-weight: 800; letter-spacing: 2px; margin-bottom: 16px; text-align: center; }' +
    '.barcode { margin-bottom: 12px; }' +
    '.barcode svg { max-width: 3.4in; height: auto; }' +
    '.sub-text { font-size: 14pt; color: #333; letter-spacing: 1px; }' +
    '</style></head><body>' +
    '<div class="bin-code">' + escHtml(binCode) + '</div>' +
    '<div class="barcode">' + barcodeSVG + '</div>' +
    '<div class="sub-text">' + escHtml(binCode) + '</div>' +
    '</body></html>';
  var win = window.open('', '_blank', 'width=420,height=420');
  if (!win) return;
  win.document.write(html);
  win.document.close();
  // Drive printing from the opener via a timer — win.onload set after document.write does not
  // reliably fire, leaving the label stuck in preview. The barcode is inline SVG so 300ms is ample.
  setTimeout(function() { try { win.focus(); win.print(); } catch (e) { /* popup closed */ } }, 300);
}

// ─── PDF417 barcode generation (via bwip-js) ───
function renderPDF417Canvas(data, opts) {
  // Throws if the data exceeds the symbol's capacity (bwip-js auto-grows rows
  // for a given column count but hits PDF417's 90-row ceiling).
  var canvas = document.createElement('canvas');
  var bwipOpts = {
    bcid: 'pdf417',
    text: data,
    scale: opts.scale || 2,
    columns: opts.columns || 4,
    rowmult: opts.rowmult || 2,
    eclevel: opts.eclevel || 6,
    padding: 0,
  };
  if (opts.rows) bwipOpts.rows = opts.rows;
  bwipjs.toCanvas(canvas, bwipOpts);
  // If rotate option, rotate 90° CW by drawing to a new canvas with swapped dimensions
  if (opts.rotate) {
    var rotCanvas = document.createElement('canvas');
    rotCanvas.width = canvas.height;
    rotCanvas.height = canvas.width;
    var ctx = rotCanvas.getContext('2d');
    ctx.translate(rotCanvas.width, 0);
    ctx.rotate(Math.PI / 2);
    ctx.drawImage(canvas, 0, 0);
    canvas = rotCanvas;
  }
  return canvas;
}

function pdf417CanvasImg(canvas, imgStyle) {
  return '<img src="' + canvas.toDataURL('image/png') + '" style="' + (imgStyle || 'width:100%;height:auto;image-rendering:pixelated;') + '" />';
}

function generatePDF417SVG(data, opts) {
  opts = opts || {};
  try {
    return pdf417CanvasImg(renderPDF417Canvas(data, opts), opts.imgStyle);
  } catch (e) {
    console.error('PDF417 generation failed:', e);
    return '<div style="color:red;font-size:10px;">Barcode error</div>';
  }
}

// FBA case-label content barcode. The payload grows with each distinct FNSKU in
// the box, so a single fixed column count overflows on large mixed boxes (6
// columns tops out near 25 SKUs). Step up through wider symbols until the data
// fits AND the printed height stays within the label's vertical budget (aspect
// ≤ 0.63 of the 3.5in print width ≈ 2.2in tall). PDF417 itself caps out near
// 1,800 chars (~75 SKUs/box) — past that no symbol size can encode the box.
var CASE_PDF417_PRESETS = [
  { columns: 6, rows: 24, rowmult: 3, eclevel: 4 }, // typical boxes — same symbol as always
  { columns: 8, rowmult: 2, eclevel: 5 },
  { columns: 10, rowmult: 2, eclevel: 5 },
  { columns: 12, rowmult: 2, eclevel: 5 },
  { columns: 14, rowmult: 2, eclevel: 5 },
  { columns: 16, rowmult: 2, eclevel: 5 },
];
function generateCasePDF417SVG(data, imgStyle) {
  var best = null;
  for (var i = 0; i < CASE_PDF417_PRESETS.length; i++) {
    var p = CASE_PDF417_PRESETS[i];
    var canvas;
    try {
      canvas = renderPDF417Canvas(data, { scale: 4, columns: p.columns, rows: p.rows, rowmult: p.rowmult, eclevel: p.eclevel });
    } catch (e) { continue; }
    var aspect = canvas.height / canvas.width;
    if (aspect <= 0.63) return pdf417CanvasImg(canvas, imgStyle);
    if (!best || aspect < best.aspect) best = { canvas: canvas, aspect: aspect };
  }
  // Encodable but over the height budget at every size — a tall barcode beats none.
  if (best) return pdf417CanvasImg(best.canvas, imgStyle);
  console.error('PDF417 case barcode: data exceeds symbology capacity (' + data.length + ' chars)');
  return '<div style="color:red;font-size:10px;">Barcode error</div>';
}

// Build Amazon FBA 2D barcode data string
// Format: AMZN,PO:{shipmentId},FNSKU:{fnsku},QTY:{qty}[,EXP:{YYMMDD}]
// Multiple items repeat the FNSKU/QTY/EXP block
function buildAmazonCaseBarcodeData(shipmentId, items) {
  var parts = ['AMZN', 'PO:' + (shipmentId || '').toUpperCase()];
  (items || []).forEach(function(item) {
    parts.push('FNSKU:' + (item.fnsku || '').toUpperCase());
    parts.push('QTY:' + (item.qty || 1));
    if (item.expiry) parts.push('EXP:' + item.expiry); // YYMMDD
  });
  return parts.join(',');
}

// ─── SEARCH INPUT (with inline clear button) ───
function SearchInput(props) {
  var value = props.value || '';
  var onChange = props.onChange;
  var mergedStyle = Object.assign({ width: '100%', boxSizing: 'border-box' }, props.style || {}, { paddingRight: value ? 32 : (props.style && props.style.paddingRight) || 14 });
  var wrapStyle = { position: 'relative', width: mergedStyle.width, flex: props.flex };
  var inputProps = Object.assign({}, props, { style: mergedStyle, flex: undefined });
  delete inputProps.flex;
  return React.createElement('div', { style: wrapStyle },
    React.createElement('input', inputProps),
    value ? React.createElement('button', {
      type: 'button', 'aria-label': 'Clear search',
      onClick: function() { if (onChange) onChange({ target: { value: '' } }); },
      style: { position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: C.textMuted, cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center', borderRadius: 4 },
      onMouseEnter: function(e) { e.currentTarget.style.background = C.surfaceHover; e.currentTarget.style.color = C.text; },
      onMouseLeave: function(e) { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = C.textMuted; }
    }, React.createElement(LIcon, { name: 'x', size: 14 })) : null
  );
}

// ─── BIN FORMAT HELPER ───
// Normalize bin location: "A100" → "A-100", "A1" → "A-01", "A100/B5" → "A-100/B-05"
function formatSingleBin(val) {
  // LOC* bins (e.g. LOC800) are staging/location codes that carry no dash — leave them as typed.
  // Only the single-letter zone bins get the dash + zero-pad treatment (B80 -> B-80).
  var m = val.match(/^([A-Z]+)-?(\d+)$/);
  if (m && m[1] !== 'LOC') {
    var num = m[2].length < 2 ? ('0' + m[2]) : m[2];
    return m[1] + '-' + num;
  }
  return val;
}
function formatBin(raw) {
  if (!raw) return '';
  var val = raw.trim().toUpperCase();
  if (!val) return '';
  return val.split('/').map(function(part) { return formatSingleBin(part.trim()); }).join('/');
}
window.formatBin = formatBin;
// ─── Country list (ISO 3166-1 alpha-2) ───
// One source for every country dropdown in the app. app-utils.js is the first file
// in all four bundles, so app-pages / app-main / app-cs can all reach these.
// app-shipping.js keeps its own SHIP_ISO_COUNTRIES (shipping bundle, carrier-scoped).
var ISO_COUNTRIES = ('AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW').split(' ');
var _isoRegionNames = null;
// Names come from Intl.DisplayNames rather than a 250-row label table; a code the
// browser cannot name falls back to the code itself.
function countryName(code) {
  if (!code) return '';
  try {
    if (!_isoRegionNames) _isoRegionNames = new Intl.DisplayNames(['en'], { type: 'region' });
    return _isoRegionNames.of(String(code).toUpperCase()) || code;
  } catch (e) { return code; }
}
// Sorted once by display name; every dropdown reuses the same array.
function countryOptions() {
  if (!window._isoCountriesSorted) {
    window._isoCountriesSorted = ISO_COUNTRIES.slice().sort(function(a, b) { return countryName(a).localeCompare(countryName(b)); });
  }
  return window._isoCountriesSorted;
}
// Options for a country SearchSelect (type to filter, or scroll). The ISO code rides in
// the label so typing "US" still surfaces United States — SearchSelect ranks a word start
// ("(US)") above a bare substring, which a name-only label would not give us. A legacy or
// free-text value that is not a known code keeps its own entry, so opening an old record
// does not silently rewrite it.
function countryOptionList(current) {
  var opts = countryOptions().map(function(c) { return { value: c, label: countryName(c) + ' (' + c + ')' }; });
  if (current && ISO_COUNTRIES.indexOf(String(current).toUpperCase()) === -1) {
    opts.unshift({ value: current, label: current + ' (saved)' });
  }
  return opts;
}
window.ISO_COUNTRIES = ISO_COUNTRIES; window.countryName = countryName;
window.countryOptions = countryOptions; window.countryOptionList = countryOptionList;
// ─── Font size for the rich-text toolbars ───
// Shared by the PO "Email Vendor" modal (app-main.js) and the PO e-mail template editor
// (app-pages.js), so a size set in either place behaves identically.
//
// execCommand('fontSize') only speaks the legacy 1-7 scale and emits <font size=N>, which
// e-mail clients render inconsistently; turning on styleWithCSS yields CSS keywords
// ('xx-large'), not px. So size 7 is applied purely as a sentinel and those exact <font>
// nodes are then rewritten into spans carrying a real px size. server/services/sanitize.js
// allowlists font-size, so the span survives the outbound sanitiser and reaches the vendor.
var FONT_SIZES = [
  { px: 12, label: 'Small' },
  { px: 14, label: 'Normal' },
  { px: 16, label: 'Medium' },
  { px: 18, label: 'Large' },
  { px: 24, label: 'Huge' },
];

function applyFontSize(rootEl, px) {
  if (!rootEl || !px) return;
  document.execCommand('fontSize', false, '7');
  // Only the nodes this call just created carry size="7" — anything the user genuinely
  // wanted at the legacy maximum would have come in through a pasted template, and those
  // are rewritten here too rather than left as <font>, which is the better outcome anyway.
  var marked = rootEl.querySelectorAll('font[size="7"]');
  for (var i = 0; i < marked.length; i++) {
    var f = marked[i];
    var span = document.createElement('span');
    span.style.fontSize = px + 'px';
    while (f.firstChild) span.appendChild(f.firstChild);
    if (f.parentNode) f.parentNode.replaceChild(span, f);
  }
}

// The <select> has to be clickable, so unlike the toolbar buttons it cannot preventDefault
// on mousedown — focusing it blurs the editor and drops the selection. Stash the range on
// the way in and put it back before applying.
function FontSizeSelect({ targetRef, size }) {
  var rangeRef = React.useRef(null);
  var small = size === 'sm';
  return React.createElement('select', {
    value: '',
    title: 'Font size',
    onMouseDown: function() {
      var sel = window.getSelection();
      rangeRef.current = (sel && sel.rangeCount) ? sel.getRangeAt(0).cloneRange() : null;
    },
    onChange: function(e) {
      var px = parseInt(e.target.value, 10);
      e.target.value = '';
      if (!px) return;
      var el = targetRef && targetRef.current;
      if (!el) return;
      el.focus();
      var r = rangeRef.current;
      if (r) {
        var sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(r);
      }
      applyFontSize(el, px);
    },
    style: {
      padding: small ? '2px 4px' : '3px 6px', fontSize: small ? 11 : 12, borderRadius: 4,
      border: 'none', background: C.bg, color: C.text, cursor: 'pointer',
      fontFamily: 'inherit', outline: 'none',
      // Without an explicit colour-scheme the browser paints both the closed control and
      // its native option popup light, so the picker read as a white block on a dark
      // toolbar. A transparent background made the closed control worse still.
      colorScheme: getThemeName() === 'light' ? 'light' : 'dark'
    }
  },
    React.createElement('option', { value: '' }, 'Size'),
    FONT_SIZES.map(function(f) { return React.createElement('option', { key: f.px, value: f.px }, f.label + ' (' + f.px + ')'); })
  );
}
window.FONT_SIZES = FONT_SIZES; window.applyFontSize = applyFontSize; window.FontSizeSelect = FontSizeSelect;

// ─── COPY CELL COMPONENT ───
function CopyCell({ value, children }) {
  var [copied, setCopied] = React.useState(false);
  return React.createElement('div', {
    style: { display: 'inline-flex', alignItems: 'center', gap: 4, position: 'relative' },
    className: 'copy-cell'
  },
    children || value,
    React.createElement('span', {
      className: 'copy-icon',
      onClick: function(e) { e.stopPropagation(); navigator.clipboard.writeText(String(value || '')); setCopied(true); setTimeout(function() { setCopied(false); }, 1500); },
      style: { cursor: 'pointer', opacity: 0, transition: 'opacity 0.15s', display: 'inline-flex', alignItems: 'center' }
    }, React.createElement(LIcon, { name: copied ? 'check' : 'clipboard', size: 12, color: copied ? C.success : C.textMuted }))
  );
}

// ─── IMAGE LIGHTBOX ───
// Reusable full-screen image viewer. Pass `images` (string[]), `index` (current),
// `onIndexChange` (setter for index), `onClose`. Returns null when not active.
function ImageLightbox(props) {
  var images = props.images || [];
  var index = props.index;
  var onIndexChange = props.onIndexChange || function() {};
  var onClose = props.onClose || function() {};
  if (!images.length || index == null || index < 0 || index >= images.length) return null;
  var prev = function(e) { e && e.stopPropagation && e.stopPropagation(); onIndexChange((index - 1 + images.length) % images.length); };
  var next = function(e) { e && e.stopPropagation && e.stopPropagation(); onIndexChange((index + 1) % images.length); };
  return React.createElement('div', {
    onClick: onClose,
    style: { position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }
  },
    React.createElement('button', {
      onClick: function(e) { e.stopPropagation(); onClose(); },
      onMouseEnter: function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.transform = 'scale(1.1)'; },
      onMouseLeave: function(e) { e.currentTarget.style.background = 'none'; e.currentTarget.style.transform = 'scale(1)'; },
      style: { position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 28, lineHeight: 1, width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10002, transition: 'background 0.15s, transform 0.15s' }
    }, '×'),
    images.length > 1 && React.createElement('div', {
      style: { position: 'absolute', top: 20, left: '50%', transform: 'translateX(-50%)', color: '#fff', fontSize: 13, fontWeight: 600, zIndex: 10002 }
    }, (index + 1) + ' / ' + images.length),
    images.length > 1 && React.createElement('button', {
      onClick: prev,
      onMouseEnter: function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.35)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)'; },
      onMouseLeave: function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1)'; },
      style: { position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 24, fontWeight: 700, width: 44, height: 44, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10002, transition: 'background 0.15s, transform 0.15s' }
    }, '‹'),
    images.length > 1 && React.createElement('button', {
      onClick: next,
      onMouseEnter: function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.35)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)'; },
      onMouseLeave: function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1)'; },
      style: { position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 24, fontWeight: 700, width: 44, height: 44, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10002, transition: 'background 0.15s, transform 0.15s' }
    }, '›'),
    React.createElement('img', {
      src: images[index],
      onClick: function(e) { e.stopPropagation(); },
      style: { maxWidth: 'calc(85vw * var(--invzoom, 1))', maxHeight: 'calc(85vh * var(--invzoom, 1))', objectFit: 'contain', borderRadius: 8, cursor: 'default' }
    }),
    images.length > 1 && React.createElement('div', {
      onClick: function(e) { e.stopPropagation(); },
      style: { position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 8, padding: '8px 12px', background: 'rgba(0,0,0,0.6)', borderRadius: 8 }
    },
      images.map(function(url, i) {
        return React.createElement('img', { key: i, src: url, onClick: function() { onIndexChange(i); },
          style: { width: 52, height: 52, objectFit: 'cover', borderRadius: 6, border: '2px solid ' + (i === index ? '#fff' : 'transparent'), cursor: 'pointer', opacity: i === index ? 1 : 0.6, transition: 'all 0.15s' } });
      })
    )
  );
}
window.ImageLightbox = ImageLightbox;

// ─── PHOTO UPLOAD PANEL (QR-code phone-camera flow) ───
// Generates a token, shows QR + URL for the warehouse user to scan with their phone,
// polls for uploaded photos every 2s, displays thumbnails. Pass token back to parent
// via onTokenChange so the parent can include it when submitting the receive.
// Props:
//   onTokenChange(token|null) — fires when token becomes available or is reset
//   onPhotosChange(photos)    — optional, fires whenever photos array updates
//   resetSignal               — change this prop value to force a reset (e.g. after submit)
function PhotoUploadPanel(props) {
  var _o = React.useState(false); var open = _o[0], setOpen = _o[1];
  var _t = React.useState(null); var token = _t[0], setToken = _t[1];
  var _p = React.useState([]); var photos = _p[0], setPhotos = _p[1];
  var _l = React.useState(false); var loading = _l[0], setLoading = _l[1];
  var lastSignal = React.useRef(props.resetSignal);

  // Reset on signal change
  React.useEffect(function() {
    if (props.resetSignal !== lastSignal.current) {
      lastSignal.current = props.resetSignal;
      setOpen(false); setToken(null); setPhotos([]);
      if (props.onTokenChange) props.onTokenChange(null);
      if (props.onPhotosChange) props.onPhotosChange([]);
    }
  }, [props.resetSignal]);

  // Generate token when opening
  function handleOpen() {
    if (open) return;
    setOpen(true);
    setLoading(true);
    api('/receiving/photo-token', { method: 'POST', body: JSON.stringify({}) }).then(function(r) {
      setToken(r.token);
      if (props.onTokenChange) props.onTokenChange(r.token);
    }).catch(function() {})
      .finally(function() { setLoading(false); });
  }

  // Poll every 2s
  React.useEffect(function() {
    if (!token || !open) return;
    var alive = true;
    var fetchOnce = function() {
      api('/receiving/photo-status/' + token).then(function(r) {
        if (!alive) return;
        var next = r.photos || [];
        setPhotos(next);
        if (props.onPhotosChange) props.onPhotosChange(next);
      }).catch(function() {});
    };
    fetchOnce();
    var int = setInterval(fetchOnce, 2000);
    return function() { alive = false; clearInterval(int); };
  }, [token, open]);

  function deletePhoto(id) {
    api('/receiving/photo/' + id, { method: 'DELETE' }).then(function() {
      var next = photos.filter(function(p) { return p.id !== id; });
      setPhotos(next);
      if (props.onPhotosChange) props.onPhotosChange(next);
    }).catch(function() {});
  }

  // Render QR as a fixed-size <img> (data URL). A raw createSvgTag <svg> kept its intrinsic
  // pixel size — which grows with the URL length — and overflowed its 140px box, drawing on top
  // of the instructions and uploaded thumbnails to its right. An <img> is clamped to the box.
  var qrSrc = '';
  var phoneUrl = '';
  if (token && open) {
    phoneUrl = window.location.origin + '/u/' + token;
    try {
      var qr = window.qrcode(0, 'M');
      qr.addData(phoneUrl);
      qr.make();
      qrSrc = qr.createDataURL(6, 2);
    } catch (e) { qrSrc = ''; }
  }

  if (!open) {
    // Caller marks photos required (e.g. items with no catalog match must have a photo). Reflect
    // that in the label + amber styling instead of always saying "(optional)".
    var req = !!props.required;
    var edge = req ? '#F59E0B' : C.border, hoverEdge = req ? '#F59E0B' : C.accent;
    return React.createElement('button', {
      onClick: handleOpen,
      onMouseEnter: function(e) { e.currentTarget.style.background = hoverEdge + '22'; e.currentTarget.style.borderColor = hoverEdge; },
      onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = edge; },
      style: { background: 'transparent', border: '1px dashed ' + edge, color: req ? '#B45309' : C.textMuted, padding: '8px 14px', borderRadius: 6, fontSize: 12, fontWeight: req ? 600 : 500, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 0.15s ease' }
    }, React.createElement(LIcon, { name: 'camera', size: 14 }), req ? 'Add photos (required)' : 'Add photos (optional)');
  }

  return React.createElement('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: 12, marginBottom: 8 } },
    React.createElement('div', { style: { display: 'flex', alignItems: 'flex-start', gap: 12 } },
      // QR + caption (left)
      React.createElement('div', { style: { flexShrink: 0, textAlign: 'center' } },
        loading
          ? React.createElement('div', { style: { width: 140, height: 140, background: C.bg, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textDim, fontSize: 11 } }, 'Generating…')
          : (qrSrc
              ? React.createElement('img', { src: qrSrc, alt: 'Scan to upload photos', style: { width: 140, height: 140, boxSizing: 'border-box', background: '#fff', padding: 4, borderRadius: 6, display: 'block' } })
              : React.createElement('div', { style: { width: 140, height: 140, background: '#fff', borderRadius: 6 } })),
        React.createElement('div', { style: { fontSize: 10, color: C.textMuted, marginTop: 4 } }, 'Scan with phone camera')
      ),
      // Right: instructions, link, photos
      React.createElement('div', { style: { flex: 1, minWidth: 0 } },
        React.createElement('div', { style: { fontSize: 12, color: C.text, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 } },
          React.createElement(LIcon, { name: 'camera', size: 14, color: C.accent }),
          React.createElement('span', { style: { fontWeight: 600 } }, 'Take photos with your phone'),
          React.createElement('button', { onClick: function() { setOpen(false); setToken(null); setPhotos([]); if (props.onTokenChange) props.onTokenChange(null); if (props.onPhotosChange) props.onPhotosChange([]); },
            style: { marginLeft: 'auto', background: 'none', border: 'none', color: C.textMuted, cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' } },
            React.createElement(LIcon, { name: 'x', size: 14 }))
        ),
        React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginBottom: 6 } },
          'Scan the QR with your phone camera, then take one or more photos. They appear here as they upload.'),
        phoneUrl && React.createElement('div', { style: { fontSize: 10, color: C.textDim, fontFamily: 'inherit', marginBottom: 8, wordBreak: 'break-all' } }, phoneUrl),
        photos.length > 0 && React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(54px, 1fr))', gap: 4 } },
          photos.map(function(p) {
            return React.createElement('div', { key: p.id, style: { position: 'relative', aspectRatio: '1', background: C.bg, borderRadius: 4, overflow: 'hidden', border: '1px solid ' + C.border } },
              React.createElement('img', { src: p.url, style: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' } }),
              React.createElement('button', { onClick: function() { deletePhoto(p.id); },
                title: 'Remove',
                style: { position: 'absolute', top: 1, right: 1, background: 'rgba(220,38,38,0.85)', border: 'none', color: '#fff', cursor: 'pointer', borderRadius: 2, padding: 0, width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, lineHeight: 1 } }, '×')
            );
          })
        ),
        photos.length === 0 && React.createElement('div', { style: { fontSize: 11, color: C.textDim, fontStyle: 'italic' } }, 'Waiting for photos…')
      )
    )
  );
}
window.PhotoUploadPanel = PhotoUploadPanel;

// ─── UNEXPECTED ITEM MODAL ───
// Used by both FBA and WFS "Receive Unexpected Item" flows.
// Props:
//   open, onClose, onSuccess(result), lookupUrl, submitUrl
function UnexpectedItemModal(props) {
  var open = props.open, onClose = props.onClose, onSuccess = props.onSuccess;
  var lookupUrl = props.lookupUrl, submitUrl = props.submitUrl;
  // Optional: navigate to a sibling plan (FBA only). When provided, the split-shipment banner
  // renders the plan name as a link. Other callers (WFS/receiving) omit it → plain text.
  var onOpenPlan = props.onOpenPlan;

  var _st = React.useState('scan'); var step = _st[0], setStep = _st[1]; // 'scan' | 'receive' | 'manual'
  var _b = React.useState(''); var barcode = _b[0], setBarcode = _b[1];
  var _l = React.useState(null); var lookup = _l[0], setLookup = _l[1];
  var _sr = React.useState(false); var searching = _sr[0], setSearching = _sr[1];
  var _q = React.useState(''); var qty = _q[0], setQty = _q[1];
  var _bn = React.useState(''); var bin = _bn[0], setBin = _bn[1];
  var _n = React.useState(''); var notes = _n[0], setNotes = _n[1];
  var _t = React.useState(''); var manualTitle = _t[0], setManualTitle = _t[1];
  var _vs = React.useState(''); var manualVsku = _vs[0], setManualVsku = _vs[1];
  var _p = React.useState(''); var selectedPo = _p[0], setSelectedPo = _p[1];
  var _s = React.useState(false); var submitting = _s[0], setSubmitting = _s[1];
  var _e = React.useState(null); var error = _e[0], setError = _e[1];
  var _pp = React.useState(false); var needsPoPick = _pp[0], setNeedsPoPick = _pp[1];
  var _fu = React.useState(false); var forceUnexpected = _fu[0], setForceUnexpected = _fu[1];
  var _li = React.useState(-1); var lightboxIdx = _li[0], setLightboxIdx = _li[1];
  var _pt = React.useState(null); var photoToken = _pt[0], setPhotoToken = _pt[1];
  var _prs = React.useState(0); var photoResetSignal = _prs[0], setPhotoResetSignal = _prs[1];
  var _pc = React.useState(0); var photoCount = _pc[0], setPhotoCount = _pc[1];
  var lookupTimer = React.useRef(null);
  var lookupSeq = React.useRef(0);
  var binEdited = React.useRef(false);
  var titleEdited = React.useRef(false);
  var vskuEdited = React.useRef(false);
  var scanInputRef = React.useRef(null);

  React.useEffect(function() {
    if (!open) {
      setStep('scan'); setBarcode(''); setLookup(null); setSearching(false);
      setQty(''); setBin(''); setNotes('');
      setManualTitle(''); setManualVsku(''); setSelectedPo(''); setError(null);
      setNeedsPoPick(false); setForceUnexpected(false); setLightboxIdx(-1);
      setPhotoToken(null); setPhotoCount(0); setPhotoResetSignal(function(s) { return s + 1; });
      binEdited.current = false; titleEdited.current = false; vskuEdited.current = false;
    } else {
      // Auto-focus the scan input when modal opens
      setTimeout(function() { if (scanInputRef.current) scanInputRef.current.focus(); }, 100);
    }
  }, [open]);

  // Pick FBM bin first (MIA), then RETURNS, then any. Mirrors the receiving modal's logic.
  var pickBinForProduct = function(prod) {
    if (!prod) return '';
    if (Array.isArray(prod.bin_quantities) && prod.bin_quantities.length > 0) {
      var fbmBin = prod.bin_quantities.find(function(b) { return b.warehouse === 'FBM' && b.bin && b.bin !== 'DEFAULT'; });
      if (fbmBin) return fbmBin.bin;
      var anyBin = prod.bin_quantities.find(function(b) { return b.bin && b.bin !== 'DEFAULT'; });
      if (anyBin) return anyBin.bin;
    }
    if (prod.current_bins) {
      var first = String(prod.current_bins).split(',')[0].trim();
      if (first && first !== 'DEFAULT') return first;
    }
    return '';
  };

  // Apply a catalog-result product as the active match: pre-fill bin/title/vsku and advance to receive step.
  var pickCatalogMatch = function(prod) {
    var fakeLookup = Object.assign({}, lookup || {}, { product: prod });
    setLookup(fakeLookup);
    if (!binEdited.current) {
      var b = pickBinForProduct(prod);
      if (b) setBin(b);
    }
    if (!titleEdited.current) setManualTitle(prod.vendor_title || prod.title || prod.sku || '');
    if (!vskuEdited.current && prod.vendor_sku) setManualVsku(prod.vendor_sku);
    if (prod.vendor_id && lookup && Array.isArray(lookup.plan_pos)) {
      var matched = lookup.plan_pos.find(function(p) { return p.vendor_id === prod.vendor_id; });
      if (matched) setSelectedPo(String(matched.id));
    }
    setStep('receive');
  };

  React.useEffect(function() {
    if (!open || step !== 'scan') return;
    if (!barcode || !barcode.trim()) { setLookup(null); setSearching(false); return; }
    if (lookupTimer.current) clearTimeout(lookupTimer.current);
    setSearching(true);
    var seq = ++lookupSeq.current; // review M6 #79: only the newest lookup may fill the form or auto-advance
    lookupTimer.current = setTimeout(function() {
      api(lookupUrl + '?q=' + encodeURIComponent(barcode.trim())).then(function(r) {
        if (seq !== lookupSeq.current) return;
        setLookup(r);
        // Existing exact-match auto-fill behavior
        if (r && r.product) {
          if (r.fbm_bin && r.fbm_bin.bin_code && !binEdited.current) setBin(r.fbm_bin.bin_code);
          else if (!binEdited.current) {
            var b = pickBinForProduct(r.product);
            if (b) setBin(b);
          }
          if (r.matched_po) setSelectedPo(String(r.matched_po.id));
          if (!titleEdited.current) setManualTitle(r.product.vendor_title || r.product.title || r.product.sku || '');
          if (!vskuEdited.current && r.product.vendor_sku) setManualVsku(r.product.vendor_sku);
        } else if (r && Array.isArray(r.catalog_results) && r.catalog_results.length === 1) {
          // Single fuzzy match — auto-pick it
          pickCatalogMatch(r.catalog_results[0]);
        }
      }).catch(function() { if (seq === lookupSeq.current) setLookup(null); })
      .finally(function() { if (seq === lookupSeq.current) setSearching(false); });
    }, 250);
    // Cleanup: a timer still pending when the barcode changes, the step advances or the modal
    // closes must never fire (it used to auto-pick a stale single match and jump to Receive).
    return function() { clearTimeout(lookupTimer.current); lookupSeq.current++; setSearching(false); };
  }, [barcode, open, step]);

  if (!open) return null;

  var product = lookup && lookup.product;
  var fbmBin = lookup && lookup.fbm_bin;
  var matchedPo = lookup && lookup.matched_po;
  // POs on this shipment, sorted alphabetically by vendor name (then PO#) for the picker dropdowns.
  var planPos = ((lookup && lookup.plan_pos) || []).slice().sort(function(a, b) {
    var an = (a.vendor_name || '').toLowerCase(), bn = (b.vendor_name || '').toLowerCase();
    if (an !== bn) return an < bn ? -1 : 1;
    return String(a.po_number || '').localeCompare(String(b.po_number || ''), undefined, { numeric: true });
  });
  var showPoPicker = needsPoPick || (!!product && !matchedPo && planPos.length > 1);
  var showManualFields = !product && barcode.trim().length > 0;
  // Sibling-split guard (FBA only — other lookups never return this field). If the scanned item
  // is still NEEDED on a sibling plan from the same split group, it isn't unexpected: it belongs
  // to that plan. Block the receive and point the packer there.
  var siblingMemberships = (lookup && lookup.sibling_memberships) || [];
  var siblingBlocking = siblingMemberships.filter(function(s) { return s && s.remaining > 0; });
  var siblingBlocked = siblingBlocking.length > 0;

  var ps = function(bg, fg) { return { fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: bg, color: fg, letterSpacing: '0.03em', whiteSpace: 'nowrap', display: 'inline-block' }; };

  var submit = async function() {
    if (siblingBlocked) {
      var sb = siblingBlocking[0];
      setError('This item belongs to split plan "' + (sb.plan_name || ('Plan ' + sb.plan_id)) + '" (' + sb.remaining + ' still to pack) — pack it there, it is not unexpected.');
      return;
    }
    var qtyN = parseInt(qty) || 0;
    var bc = barcode.trim();
    // Empty barcode is allowed — server uses a placeholder SKU. Require either a barcode or a title.
    if (!bc && !manualTitle.trim()) { setError('Enter a barcode or a vendor title'); return; }
    if (qtyN <= 0) { setError('Quantity must be greater than 0'); return; }
    if (!bin.trim()) { setError('Bin location is required'); return; }
    // If no barcode + no catalog match, confirm the item really has no printed barcode
    if (!bc && !product) {
      if (!(await appConfirm('No barcode entered. Confirm this item has no printed barcode (UPC/EAN) on the package?'))) return;
    }
    // Photos are mandatory for manually-entered unexpected items (not catalog-matched)
    if (!product && photoCount === 0) {
      setError('At least one photo is required for unexpected items not in our catalog. Use "Add photos" to scan the QR.');
      return;
    }

    setSubmitting(true); setError(null);
    var body = {
      barcode: barcode.trim(), qty: qtyN, bin_location: bin.trim(),
      // Send the resolved catalog product so the server matches the exact item the user
      // confirmed on screen (incl. fuzzy/keyword picks) instead of re-deriving from the
      // barcode — prevents catalog-matched extras filing as orphan UNEXPECTED-<keyword> lines.
      product_id: product ? product.id : null,
      notes: notes.trim() || null,
      vendor_title: manualTitle.trim() || null,
      vendor_sku: manualVsku.trim() || null,
      po_id: selectedPo ? parseInt(selectedPo) : null,
      force_unexpected: forceUnexpected,
      photo_token: photoToken || null,
    };
    api(submitUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      .then(function(resp) { onSuccess(resp); onClose(); })
      .catch(function(e) {
        var msg = e && e.message ? e.message : 'Failed to receive';
        if (msg.indexOf('Select which PO') !== -1) setNeedsPoPick(true);
        setError(msg);
      })
      .finally(function() { setSubmitting(false); });
  };

  var label = function(txt) { return React.createElement('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', display: 'block', marginBottom: 4 } }, txt); };
  var inp = function(value, onChange, placeholder, extraStyle) {
    return React.createElement('input', { value: value || '', onChange: function(e) { onChange(e.target.value); }, placeholder: placeholder,
      style: Object.assign({ width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid ' + C.border, borderRadius: 6, background: C.bg, color: C.text, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }, extraStyle || {}) });
  };

  var productImages = (product && product.images && product.images.length) ? product.images : (product && product.image_1_url ? [product.image_1_url] : []);
  var productCard = product && React.createElement('div', { style: { display: 'flex', gap: 10, padding: 10, background: C.surface, borderRadius: 6, marginBottom: 12, border: '1px solid ' + C.border } },
    productImages.length > 0
      ? React.createElement('img', {
          src: productImages[0],
          onClick: function() { setLightboxIdx(0); },
          title: productImages.length > 1 ? 'Click to view ' + productImages.length + ' images' : 'Click to enlarge',
          style: { width: 56, height: 56, borderRadius: 6, objectFit: 'cover', flexShrink: 0, cursor: 'pointer', transition: 'transform 0.1s' }
        })
      : React.createElement('div', { style: { width: 56, height: 56, borderRadius: 6, background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } }, React.createElement(LIcon, { name: 'package', size: 24, color: C.textDim })),
    React.createElement('div', { style: { flex: 1, minWidth: 0 } },
      React.createElement('div', { style: { fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 4 } }, product.vendor_title || product.title || product.sku),
      React.createElement('div', { style: { display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 4 } },
        product.sku && React.createElement('span', { style: ps(C.accent + '14', C.accent) }, 'SKU: ' + product.sku),
        product.vendor_sku && React.createElement('span', { style: ps(C.accent + '14', C.accent) }, 'VSKU: ' + product.vendor_sku),
        product.barcode && React.createElement('span', { style: ps('#06B6D4' + '18', '#06B6D4') }, 'UPC: ' + product.barcode),
        product.fnsku && React.createElement('span', { style: ps('#8B5CF6' + '18', '#8B5CF6') }, 'FNSKU: ' + product.fnsku),
        product.warehouse_code && React.createElement('span', { style: ps(product.warehouse_code === 'FBM' ? '#10B981' + '18' : C.textDim + '18', product.warehouse_code === 'FBM' ? '#10B981' : C.textMuted) }, product.warehouse_code)
      ),
      fbmBin && React.createElement('div', { style: { fontSize: 11, color: C.success, fontWeight: 600, marginTop: 2 } },
        React.createElement(LIcon, { name: 'map-pin', size: 11, color: C.success }), ' Bin: ', fbmBin.bin_code, ' (' + fbmBin.quantity + ' on hand)'
      ),
      matchedPo && React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 2 } },
        'PO: ', React.createElement('span', { style: { fontWeight: 600, color: C.accent, fontFamily: 'inherit' } }, matchedPo.po_number), ' — ', matchedPo.vendor_name
      )
    )
  );

  var qtyBinNotes = [
    // Qty + Bin
    React.createElement('div', { key: 'qbin', style: { display: 'flex', gap: 8, marginBottom: 12 } },
      React.createElement('div', { style: { flex: 1 } },
        label('Qty *'),
        inp(qty, setQty, '0')
      ),
      React.createElement('div', { style: { flex: 2 } },
        label('Bin Location *'),
        React.createElement('input', {
          value: bin || '',
          onChange: function(e) { binEdited.current = true; setBin((e.target.value || '').toUpperCase()); },
          onBlur: function() { setBin(function(v) { return formatBin(v) || v; }); },
          placeholder: 'A-100',
          style: { width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid ' + C.border, borderRadius: 6, background: C.bg, color: C.text, outline: 'none', boxSizing: 'border-box' }
        })
      )
    ),
    // Notes
    React.createElement('div', { key: 'notes', style: { marginBottom: 12 } },
      label('Notes (optional)'),
      inp(notes, setNotes, 'Appears in the PO receiving history')
    ),
    // Photo upload via QR
    React.createElement('div', { key: 'photo', style: { marginBottom: 14 } },
      React.createElement(window.PhotoUploadPanel, { required: !product, onTokenChange: setPhotoToken, onPhotosChange: function(p) { setPhotoCount(p.length); }, resetSignal: photoResetSignal })
    ),
  ];

  var poPicker = showPoPicker && React.createElement('div', { style: { marginBottom: 12 } },
    label('Purchase Order *'),
    React.createElement('select', { value: selectedPo || '', onChange: function(e) { setSelectedPo(e.target.value); },
      style: { width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid ' + C.border, borderRadius: 6, background: C.bg, color: C.text, outline: 'none', fontFamily: 'inherit' } },
      React.createElement('option', { value: '' }, 'Select a PO…'),
      planPos.map(function(p) {
        return React.createElement('option', { key: p.id, value: String(p.id) }, p.po_number + ' — ' + (p.vendor_name || 'Unknown vendor'));
      })
    )
  );

  var errorBox = error && React.createElement('div', { style: { padding: 10, background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 6, marginBottom: 12, fontSize: 12, color: '#F87171' } }, error);

  var siblingBanner = siblingBlocked && React.createElement('div', { style: { padding: 10, background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.5)', borderRadius: 6, marginBottom: 12, fontSize: 12.5 } },
    React.createElement('div', { style: { fontWeight: 700, color: '#F59E0B', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 } },
      React.createElement(LIcon, { name: 'alert-triangle', size: 14, color: '#F59E0B' }),
      'Not an unexpected item — it belongs to a split shipment'),
    siblingBlocking.map(function(s, i) {
      var planLabel = s.plan_name || ('Plan ' + s.plan_id);
      var planEl = onOpenPlan
        ? React.createElement('a', {
            href: '#',
            onClick: function(e) { e.preventDefault(); onOpenPlan(s.plan_id); },
            title: 'Open ' + planLabel,
            style: { fontWeight: 700, color: C.accent, textDecoration: 'underline', cursor: 'pointer' }
          }, planLabel)
        : React.createElement('span', { style: { fontWeight: 700 } }, planLabel);
      return React.createElement('div', { key: i, style: { color: C.text, marginTop: 2 } },
        'Pack it in ', planEl,
        ' — shipment #', String(s.shipment_id), ': ',
        React.createElement('span', { style: { fontWeight: 700, color: '#F59E0B' } }, s.remaining + ' of ' + s.quantity + ' still to pack'), '.');
    }),
    React.createElement('div', { style: { color: C.textMuted, marginTop: 6 } },
      'Open that plan and scan this item into a box. Only use "Not a match? Receive manually" if you genuinely have an extra unit beyond that plan.')
  );

  // ─── STEP: Scan ───
  var scanStep = step === 'scan' && React.createElement('div', null,
    React.createElement('div', { style: { marginBottom: 6 } },
      label('Scan Barcode or FNSKU'),
      React.createElement('div', { style: { position: 'relative' } },
        React.createElement('input', {
          ref: scanInputRef,
          value: barcode,
          onChange: function(e) { setBarcode(e.target.value); },
          placeholder: 'Scan Barcode/FNSKU or type to search catalog..',
          style: { width: '100%', padding: '10px 36px 10px 12px', fontSize: 13, border: '1px solid ' + C.border, borderRadius: 6, background: C.bg, color: C.text, outline: 'none', boxSizing: 'border-box' }
        }),
        barcode && React.createElement('button', {
          onClick: function() {
            setBarcode(''); setLookup(null); setSearching(false);
            setManualTitle(''); setManualVsku(''); setBin(''); setSelectedPo('');
            binEdited.current = false; titleEdited.current = false; vskuEdited.current = false;
            if (scanInputRef.current) scanInputRef.current.focus();
          },
          title: 'Clear',
          onMouseEnter: function(e) { e.currentTarget.style.background = C.border; e.currentTarget.style.color = C.text; },
          onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = C.textMuted; },
          style: { position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, width: 24, height: 24, borderRadius: 4, color: C.textMuted, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.15s, color 0.15s' }
        }, React.createElement(LIcon, { name: 'x', size: 14 }))
      )
    ),
    React.createElement('div', { style: { fontSize: 11, color: C.textDim, marginBottom: 14 } }, 'We\'ll search the catalog, preferring FBM items so the current bin shows up.'),

    // Searching…
    searching && React.createElement('div', { style: { padding: '20px 0', textAlign: 'center', color: C.textMuted, fontSize: 12 } }, 'Searching catalog…'),

    // Reusable "Not a match? Receive manually." button — danger pill styling, clears all fields
    (function() { return null; })(),

    // Match found (single product — exact or auto-picked single fuzzy)
    !searching && product && React.createElement('div', null,
      React.createElement('div', { style: { marginBottom: 10, display: 'flex', justifyContent: 'flex-end' } },
        React.createElement('button', {
          onClick: function() {
            // Keep the scanned barcode — it carries into the manual form's Barcode field so the
            // user doesn't have to re-type it (it's the unexpected item's real barcode).
            // Preserve plan_pos so the manual step can still render its required "Purchase Order"
            // dropdown. Wiping the whole lookup left planPos empty, so the dropdown never showed,
            // selectedPo could never be set, and the Receive button stayed permanently greyed out.
            setLookup(lookup && lookup.plan_pos ? { plan_pos: lookup.plan_pos } : null); setBin(''); setManualTitle(''); setManualVsku(''); setSelectedPo(''); setNotes(''); setQty('');
            binEdited.current = false; titleEdited.current = false; vskuEdited.current = false;
            setForceUnexpected(true); setStep('manual');
          },
          title: 'Not a match — fill the unexpected item form manually',
          onMouseEnter: function(e) { e.currentTarget.style.background = C.danger + '33'; e.currentTarget.style.borderColor = C.danger; },
          onMouseLeave: function(e) { e.currentTarget.style.background = C.danger + '18'; e.currentTarget.style.borderColor = C.danger + '44'; },
          style: { background: C.danger + '18', border: '1px solid ' + C.danger + '44', color: C.danger, padding: '6px 12px', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', transition: 'all 0.15s ease' }
        }, 'Not a match? Receive manually.')
      ),
      siblingBanner,
      productCard,
      siblingBlocked
        ? null
        : React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 } },
            React.createElement(Btn, { variant: 'primary', onClick: function() { setStep('receive'); }, style: { minWidth: 220, justifyContent: 'center' } },
              React.createElement(LIcon, { name: 'arrow-right', size: 14 }), ' Continue with this item'
            )
          )
    ),

    // Multi-match pick list (no exact, but multiple fuzzy results)
    !searching && !product && lookup && Array.isArray(lookup.catalog_results) && lookup.catalog_results.length > 1 && React.createElement('div', null,
      React.createElement('div', { style: { marginBottom: 10, display: 'flex', justifyContent: 'flex-end' } },
        React.createElement('button', {
          onClick: function() {
            // Keep the scanned barcode — it carries into the manual form's Barcode field so the
            // user doesn't have to re-type it (it's the unexpected item's real barcode).
            // Preserve plan_pos so the manual step can still render its required "Purchase Order"
            // dropdown. Wiping the whole lookup left planPos empty, so the dropdown never showed,
            // selectedPo could never be set, and the Receive button stayed permanently greyed out.
            setLookup(lookup && lookup.plan_pos ? { plan_pos: lookup.plan_pos } : null); setBin(''); setManualTitle(''); setManualVsku(''); setSelectedPo(''); setNotes(''); setQty('');
            binEdited.current = false; titleEdited.current = false; vskuEdited.current = false;
            setForceUnexpected(true); setStep('manual');
          },
          title: 'None of these match — fill the unexpected item form manually',
          onMouseEnter: function(e) { e.currentTarget.style.background = C.danger + '33'; e.currentTarget.style.borderColor = C.danger; },
          onMouseLeave: function(e) { e.currentTarget.style.background = C.danger + '18'; e.currentTarget.style.borderColor = C.danger + '44'; },
          style: { background: C.danger + '18', border: '1px solid ' + C.danger + '44', color: C.danger, padding: '6px 12px', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', transition: 'all 0.15s ease' }
        }, 'No matches? Receive manually.')
      ),
      React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 6 } },
        lookup.catalog_capped
          ? 'Showing the first ' + lookup.catalog_results.length + ' of ' + lookup.catalog_total + ' catalog matches for "' + barcode.trim() + '". Narrow your search to see the rest.'
          : lookup.catalog_results.length + ' catalog matches for "' + barcode.trim() + '" — pick one to receive'),
      React.createElement('div', { style: { maxHeight: 360, overflow: 'auto', borderRadius: 8, border: '1px solid ' + C.border } },
        lookup.catalog_results.map(function(item, idx) {
          var imgUrl = (item.images && item.images.length) ? item.images[0] : item.image_1_url;
          var isFbm = (item.wh_code && /^FBM/i.test(item.wh_code)) || (item.sku && /-FBM(?:-|$)/i.test(item.sku));
          return React.createElement('div', { key: item.id,
            onClick: function() { pickCatalogMatch(item); },
            style: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', cursor: 'pointer',
              borderBottom: idx < lookup.catalog_results.length - 1 ? '1px solid ' + C.border + '44' : 'none',
              background: 'transparent', transition: 'background 0.1s' },
            onMouseEnter: function(e) { e.currentTarget.style.background = C.surface; },
            onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; }
          },
            React.createElement(ProductThumb, { src: imgUrl, size: 40, iconSize: 16 }),
            React.createElement('div', { style: { flex: 1, minWidth: 0 } },
              React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 6 } },
                React.createElement('span', { style: { fontSize: 13, fontWeight: 600, fontFamily: 'inherit', color: C.accent } }, item.sku),
                isFbm && React.createElement('span', { style: { background: C.success + '22', color: C.success, fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4, letterSpacing: '0.05em', textTransform: 'uppercase' } }, 'FBM'),
                item.wh_code && !isFbm && React.createElement('span', { style: { background: C.textDim + '22', color: C.textMuted, fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4, letterSpacing: '0.05em', textTransform: 'uppercase' } }, item.wh_code)
              ),
              React.createElement('div', { style: { fontSize: 12, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 } }, item.vendor_title || item.title || ''),
              item.barcode && React.createElement('div', { style: { fontSize: 11, color: C.textMuted, fontFamily: 'inherit', marginTop: 2 } }, 'UPC: ', React.createElement('span', { style: { color: C.textDim } }, item.barcode))
            ),
            React.createElement(LIcon, { name: 'chevron-right', size: 16, color: C.textDim })
          );
        })
      )
    ),

    // No match at all
    !searching && !product && barcode.trim().length > 0 && (!lookup || !Array.isArray(lookup.catalog_results) || lookup.catalog_results.length === 0) && React.createElement('div', null,
      React.createElement('div', { style: { padding: 12, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 6, marginBottom: 12, fontSize: 13, color: '#B45309' } },
        React.createElement(LIcon, { name: 'alert-triangle', size: 13, color: '#B45309' }), ' No catalog match for ',
        React.createElement('span', { style: { fontFamily: 'inherit', fontWeight: 600 } }, barcode.trim()),
        '. Enter the item details manually.'
      ),
      React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } },
        React.createElement('button', {
          onClick: function() {
            // Keep the scanned barcode — it carries into the manual form's Barcode field so the
            // user doesn't have to re-type it (it's the unexpected item's real barcode).
            // Preserve plan_pos so the manual step can still render its required "Purchase Order"
            // dropdown. Wiping the whole lookup left planPos empty, so the dropdown never showed,
            // selectedPo could never be set, and the Receive button stayed permanently greyed out.
            setLookup(lookup && lookup.plan_pos ? { plan_pos: lookup.plan_pos } : null); setBin(''); setManualTitle(''); setManualVsku(''); setSelectedPo(''); setNotes(''); setQty('');
            binEdited.current = false; titleEdited.current = false; vskuEdited.current = false;
            setForceUnexpected(true); setStep('manual');
          },
          onMouseEnter: function(e) { e.currentTarget.style.background = C.danger + '33'; e.currentTarget.style.borderColor = C.danger; },
          onMouseLeave: function(e) { e.currentTarget.style.background = C.danger + '18'; e.currentTarget.style.borderColor = C.danger + '44'; },
          style: { background: C.danger + '18', border: '1px solid ' + C.danger + '44', color: C.danger, padding: '6px 12px', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', transition: 'all 0.15s ease' }
        }, 'No matches? Receive manually.')
      )
    )
  );

  var backToScanBtn = React.createElement(Btn, { onClick: function() { setStep('scan'); setTimeout(function() { if (scanInputRef.current) scanInputRef.current.focus(); }, 50); } },
    React.createElement(LIcon, { name: 'arrow-left', size: 14 }), ' Back'
  );

  // ─── STEP: Receive (catalog match) ───
  var receiveStep = step === 'receive' && React.createElement('div', null,
    productCard,
    siblingBanner,
    poPicker,
    qtyBinNotes,
    errorBox,
    React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', gap: 8 } },
      backToScanBtn,
      React.createElement(Btn, { variant: 'primary', onClick: submit, disabled: submitting || !(parseInt(qty) > 0) || !bin.trim() || siblingBlocked },
        submitting ? 'Receiving…' : React.createElement('span', null, React.createElement(LIcon, { name: 'check', size: 14 }), ' Receive')
      )
    )
  );

  // ─── STEP: Manual (no catalog match) ───
  var manualStep = step === 'manual' && React.createElement('div', null,
    React.createElement('div', { style: { padding: 10, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 6, marginBottom: 12, fontSize: 12, color: '#B45309' } },
      React.createElement(LIcon, { name: 'alert-triangle', size: 12, color: '#B45309' }), ' No catalog match — details below will be stored on the PO line.'
    ),
    React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 } },
      React.createElement('div', null,
        label('Barcode'),
        inp(barcode, setBarcode, 'Scan or enter barcode. If the item has no barcode leave BLANK.')
      ),
      React.createElement('div', null,
        label('Vendor SKU'),
        inp(manualVsku, function(v) { vskuEdited.current = true; setManualVsku(v); }, 'Vendor item #…')
      )
    ),
    React.createElement('div', { style: { marginBottom: 12 } },
      label('Item Name'),
      inp(manualTitle, function(v) { titleEdited.current = true; setManualTitle(v); }, 'Item name / description')
    ),
    poPicker || (planPos.length > 0 && React.createElement('div', { style: { marginBottom: 12 } },
      label('Purchase Order *'),
      React.createElement('select', { value: selectedPo || '', onChange: function(e) { setSelectedPo(e.target.value); },
        style: { width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid ' + C.border, borderRadius: 6, background: C.bg, color: C.text, outline: 'none', fontFamily: 'inherit' } },
        React.createElement('option', { value: '' }, 'Select a PO…'),
        planPos.map(function(p) {
          return React.createElement('option', { key: p.id, value: String(p.id) }, p.po_number + ' — ' + (p.vendor_name || 'Unknown vendor'));
        })
      )
    )),
    qtyBinNotes,
    errorBox,
    React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', gap: 8 } },
      backToScanBtn,
      React.createElement(Btn, { variant: 'primary', onClick: submit, disabled: submitting || !barcode.trim() || !(parseInt(qty) > 0) || !bin.trim() || !selectedPo },
        submitting ? 'Receiving…' : React.createElement('span', null, React.createElement(LIcon, { name: 'check', size: 14 }), ' Receive')
      )
    )
  );

  var lightboxOverlay = lightboxIdx >= 0 && productImages.length > 0 && React.createElement('div', {
    onClick: function() { setLightboxIdx(-1); },
    style: { position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }
  },
    React.createElement('button', {
      onClick: function(e) { e.stopPropagation(); setLightboxIdx(-1); },
      onMouseEnter: function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.transform = 'scale(1.1)'; },
      onMouseLeave: function(e) { e.currentTarget.style.background = 'none'; e.currentTarget.style.transform = 'scale(1)'; },
      style: { position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 28, lineHeight: 1, width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10002, transition: 'background 0.15s, transform 0.15s' }
    }, '×'),
    productImages.length > 1 && React.createElement('div', { style: { position: 'absolute', top: 20, left: '50%', transform: 'translateX(-50%)', color: '#fff', fontSize: 13, fontWeight: 600, zIndex: 10002 } },
      (lightboxIdx + 1) + ' / ' + productImages.length),
    productImages.length > 1 && React.createElement('button', {
      onClick: function(e) { e.stopPropagation(); setLightboxIdx((lightboxIdx - 1 + productImages.length) % productImages.length); },
      onMouseEnter: function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.35)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)'; },
      onMouseLeave: function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1)'; },
      style: { position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 24, fontWeight: 700, width: 44, height: 44, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10002, transition: 'background 0.15s, transform 0.15s' }
    }, '‹'),
    productImages.length > 1 && React.createElement('button', {
      onClick: function(e) { e.stopPropagation(); setLightboxIdx((lightboxIdx + 1) % productImages.length); },
      onMouseEnter: function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.35)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)'; },
      onMouseLeave: function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1)'; },
      style: { position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 24, fontWeight: 700, width: 44, height: 44, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10002, transition: 'background 0.15s, transform 0.15s' }
    }, '›'),
    React.createElement('img', {
      src: productImages[lightboxIdx],
      onClick: function(e) { e.stopPropagation(); },
      style: { maxWidth: 'calc(85vw * var(--invzoom, 1))', maxHeight: 'calc(85vh * var(--invzoom, 1))', objectFit: 'contain', borderRadius: 8, cursor: 'default' }
    }),
    productImages.length > 1 && React.createElement('div', {
      onClick: function(e) { e.stopPropagation(); },
      style: { position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 8, padding: '8px 12px', background: 'rgba(0,0,0,0.6)', borderRadius: 8 }
    },
      productImages.map(function(url, i) {
        return React.createElement('img', { key: i, src: url, onClick: function() { setLightboxIdx(i); },
          style: { width: 52, height: 52, objectFit: 'cover', borderRadius: 6, border: '2px solid ' + (i === lightboxIdx ? '#fff' : 'transparent'), cursor: 'pointer', opacity: i === lightboxIdx ? 1 : 0.6, transition: 'all 0.15s' } });
      })
    )
  );

  var title = step === 'scan' ? 'Scan barcode or FNSKU' : (step === 'manual' ? 'Enter item manually' : 'Receive unexpected item');
  return React.createElement(React.Fragment, null,
    React.createElement(Modal, { open: open, onClose: onClose, title: title, width: 640 },
      React.createElement('div', null, scanStep, receiveStep, manualStep)
    ),
    lightboxOverlay
  );
}

window.UnexpectedItemModal = UnexpectedItemModal;

// ─── @mention autocomplete for note inputs ───
// Cache the mentionable roster once per session; lookups happen as the user types.
var _mentionableUsers = null;
var _mentionableLoading = null;
function loadMentionableUsers() {
  if (_mentionableUsers) return Promise.resolve(_mentionableUsers);
  if (_mentionableLoading) return _mentionableLoading;
  _mentionableLoading = api('/auth/users/mentionable')
    .then(function(rows) { _mentionableUsers = rows || []; return _mentionableUsers; })
    .catch(function(e) { _mentionableLoading = null; console.error('[mentions] load failed', e); return []; });
  return _mentionableLoading;
}
window.invalidateMentionableUsers = function() { _mentionableUsers = null; _mentionableLoading = null; };

// MentionInput — drop-in textarea with @-autocomplete.
// Props:
//   value, onChange(value), placeholder, rows, style, disabled,
//   onKeyDown (forwarded after the popup has had its chance to consume the key)
function MentionInput(props) {
  var value = props.value || '';
  var onChange = props.onChange;
  var inputRef = useRef(null);
  var [users, setUsers] = useState([]);
  var [popup, setPopup] = useState(null); // { query, start, end, anchor: {top, left}, items, index }
  useEffect(function() { loadMentionableUsers().then(setUsers); }, []);

  // Detect the @token the cursor is currently in (if any) and rebuild the popup.
  function rebuildPopup(text, caret) {
    if (caret == null) { setPopup(null); return; }
    // Walk left from caret to find an @, breaking on whitespace.
    var i = caret;
    while (i > 0) {
      var ch = text.charAt(i - 1);
      if (ch === '@') {
        // Must be at string start or preceded by whitespace.
        if (i - 2 < 0 || /\s/.test(text.charAt(i - 2))) {
          var query = text.slice(i, caret);
          // Don't fire on whitespace-only or trailing space.
          if (/\s/.test(query)) { setPopup(null); return; }
          var q = query.toLowerCase();
          var filtered = users.filter(function(u) {
            var dn = (u.display_name || '').toLowerCase();
            var un = (u.username || '').toLowerCase();
            return dn.indexOf(q) !== -1 || un.indexOf(q) !== -1;
          }).slice(0, 8);
          if (!filtered.length) { setPopup(null); return; }
          setPopup({ query: query, start: i - 1, end: caret, items: filtered, index: 0 });
          return;
        }
        setPopup(null);
        return;
      }
      if (/\s/.test(ch)) { setPopup(null); return; }
      i--;
    }
    setPopup(null);
  }

  function handleChange(e) {
    var v = e.target.value;
    if (onChange) onChange(v);
    setTimeout(function() {
      var el = inputRef.current; if (!el) return;
      rebuildPopup(v, el.selectionStart);
    }, 0);
  }

  function pickUser(u) {
    if (!popup || !inputRef.current) return;
    var before = value.slice(0, popup.start);
    var after = value.slice(popup.end);
    // Insert the display name (spaces collapsed to dots) so the chip reads as a
    // human name rather than an email-style username.
    var token = (u.display_name || u.username || '').replace(/\s+/g, '.');
    var inserted = '@' + token;
    var newVal = before + inserted + ' ' + after;
    if (onChange) onChange(newVal);
    setPopup(null);
    setTimeout(function() {
      var el = inputRef.current; if (!el) return;
      var pos = before.length + inserted.length + 1;
      el.focus();
      try { el.setSelectionRange(pos, pos); } catch (e) {}
    }, 0);
  }

  function handleKeyDown(e) {
    if (popup) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setPopup(Object.assign({}, popup, { index: (popup.index + 1) % popup.items.length })); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setPopup(Object.assign({}, popup, { index: (popup.index - 1 + popup.items.length) % popup.items.length })); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pickUser(popup.items[popup.index]); return; }
      if (e.key === 'Escape') { e.preventDefault(); setPopup(null); return; }
    }
    if (props.onKeyDown) props.onKeyDown(e);
  }

  return React.createElement('div', { style: { position: 'relative' } },
    React.createElement('textarea', Object.assign({}, {
      ref: inputRef,
      value: value,
      onChange: handleChange,
      onKeyDown: handleKeyDown,
      onSelect: function() { var el = inputRef.current; if (el) rebuildPopup(value, el.selectionStart); },
      onPaste: props.onPaste,
      placeholder: props.placeholder,
      rows: props.rows || 2,
      disabled: props.disabled,
      style: Object.assign({ width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }, props.style || {}),
    })),
    popup && React.createElement('div', {
      style: { position: 'absolute', left: 0, bottom: '100%', marginBottom: 4, zIndex: 50, background: C.surface, border: '1px solid ' + C.border, borderRadius: 6, boxShadow: '0 6px 24px rgba(0,0,0,0.4)', minWidth: 200, maxWidth: 320, padding: 4 }
    },
      popup.items.map(function(u, idx) {
        var active = idx === popup.index;
        return React.createElement('div', {
          key: u.id,
          onMouseDown: function(e) { e.preventDefault(); pickUser(u); },
          onMouseEnter: function() { setPopup(Object.assign({}, popup, { index: idx })); },
          style: { padding: '6px 10px', borderRadius: 4, cursor: 'pointer', background: active ? C.accent + '22' : 'transparent', fontSize: 13, color: C.text, fontWeight: 600 }
        }, u.display_name || u.username);
      })
    )
  );
}
window.MentionInput = MentionInput;

// Renders note body text with @mentions visually highlighted as inline chips
// and URLs as clickable new-tab links. Render-time only — notes stay plain text.
function renderNoteBody(text) {
  var parts = [];
  var re = /(@[a-zA-Z0-9_.-]+)|(https?:\/\/[^\s<>"']+)|(\bwww\.[^\s<>"']+)/g;
  var last = 0; var match;
  var i = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    if (match[1]) {
      parts.push(React.createElement('span', { key: 'm' + (i++), style: { background: 'rgba(251,191,36,0.22)', color: '#FBBF24', padding: '1px 6px', borderRadius: 4, fontWeight: 700, border: '1px solid rgba(251,191,36,0.45)' } }, match[0]));
    } else {
      // Trailing sentence punctuation belongs to the prose, not the URL;
      // a trailing ")" stays only when the URL itself contains a "(".
      var url = match[0];
      var trail = '';
      while (/[.,;:!?"']$/.test(url) || (url.slice(-1) === ')' && url.indexOf('(') === -1)) {
        trail = url.slice(-1) + trail;
        url = url.slice(0, -1);
      }
      // color:inherit — own-note bubbles are white-on-accent, a default blue link would vanish
      parts.push(React.createElement('a', {
        key: 'u' + (i++),
        href: match[2] ? url : 'https://' + url,
        target: '_blank', rel: 'noopener noreferrer',
        style: { color: 'inherit', textDecoration: 'underline', textUnderlineOffset: 2, fontWeight: 600, wordBreak: 'break-all' }
      }, url));
      if (trail) parts.push(trail);
    }
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}
window.renderNoteBody = renderNoteBody;

// Renders a note's attachments inside its bubble: image thumbnails (click opens
// full size in a new tab) and download chips for documents. Notes fetched from
// GET /purchase-orders/:poId/notes carry an attachments array + po_id.
function renderNoteAttachments(note) {
  var atts = note.attachments || [];
  if (!atts.length) return null;
  function fmtSize(b) {
    if (!b) return '';
    if (b < 1024) return b + ' B';
    if (b < 1024 * 1024) return Math.round(b / 1024) + ' KB';
    return (b / (1024 * 1024)).toFixed(1) + ' MB';
  }
  return React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: note.message ? 6 : 0 } },
    atts.map(function(a) {
      var url = API_BASE + '/purchase-orders/' + note.po_id + '/notes/attachment/' + a.id;
      if (/^image\//i.test(a.mime_type || '')) {
        return React.createElement('a', { key: a.id, href: url, target: '_blank', rel: 'noopener noreferrer', style: { display: 'block' } },
          React.createElement('img', { src: url, alt: a.name, title: a.name,
            style: { maxWidth: 180, maxHeight: 140, borderRadius: 8, display: 'block', border: '1px solid rgba(0,0,0,0.15)' } }));
      }
      return React.createElement('a', { key: a.id, href: url, target: '_blank', rel: 'noopener noreferrer', title: a.name,
        style: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 8,
          background: 'rgba(0,0,0,0.12)', color: 'inherit', textDecoration: 'none', fontSize: 12, fontWeight: 600, maxWidth: 240 } },
        React.createElement(LIcon, { name: 'paperclip', size: 12 }),
        React.createElement('span', { style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, a.name),
        a.file_size ? React.createElement('span', { style: { opacity: 0.7, fontWeight: 400, flexShrink: 0 } }, fmtSize(a.file_size)) : null);
    })
  );
}
window.renderNoteAttachments = renderNoteAttachments;

// Deep link to a bill in the Bill.com web app. app.bill.com redirects to the
// org's regional host (app02.us.…) and lands on Bill Details — verified live 2026-08-13.
function bdcBillUrl(bdcId) { return 'https://app.bill.com/neo/bills/' + encodeURIComponent(bdcId); }
window.bdcBillUrl = bdcBillUrl;

// Hover wrapper for Bill.com-linked elements (native title= can't hold links).
// Wraps `children` and shows the shared card on hover: muted tip line, then either
// one row per bill in `bills` ([{bdc_id, invoice_number, amount, payment_status}] —
// each its own Bill.com link) or the single detail/bdcBillId pair. Used by the
// PO Billed badge and the accounting cash-cycle BILL.COM pill — one look everywhere.
function BdcBillHover({ tip, detail, bdcBillId, bills, children }) {
  var _o = useState(false), open = _o[0], setOpen = _o[1];
  var _p = useState(null), pos = _p[0], setPos = _p[1];
  var closeTimer = useRef(null);
  var show = function(e) {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    var r = e.currentTarget.getBoundingClientRect();
    setPos({ x: r.left + r.width / 2, y: r.bottom + 6 });
    setOpen(true);
  };
  // Grace delay so the pointer can travel from the pill into the card.
  var hide = function() { closeTimer.current = setTimeout(function() { setOpen(false); }, 250); };
  var cancelHide = function() { if (closeTimer.current) clearTimeout(closeTimer.current); };
  // Portaled to <body>: cash-cycle "complete" rows fade themselves with opacity 0.6,
  // which would fade any in-row descendant — including this fixed-position card.
  return React.createElement('span', { onMouseEnter: show, onMouseLeave: hide, style: { display: 'inline-flex' } },
    children,
    open && pos && ReactDOM.createPortal(React.createElement('div', {
      onMouseEnter: cancelHide, onMouseLeave: hide,
      onClick: function(e) { e.stopPropagation(); },
      style: { position: 'fixed', left: pos.x, top: pos.y, transform: 'translateX(-50%)', zIndex: 10500,
        background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: '9px 12px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.25)', minWidth: 190, maxWidth: 300, cursor: 'default',
        fontSize: 12, fontWeight: 400, color: C.text, textTransform: 'none', letterSpacing: 'normal', textAlign: 'left', lineHeight: 1.45, textDecoration: 'none' }
    },
      React.createElement('div', { style: { color: C.textMuted, fontSize: 11 } }, tip),
      (bills && bills.length > 0)
        ? bills.map(function(bill) {
            var label = 'Invoice ' + (bill.invoice_number || '—')
              + (bill.amount != null ? ' · $' + Number(bill.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '')
              + (bill.payment_status === 'paid' ? ' · paid' : '');
            return React.createElement('a', {
              key: bill.bdc_id,
              href: bdcBillUrl(bill.bdc_id), target: '_blank', rel: 'noopener noreferrer',
              onClick: function(e) { e.stopPropagation(); },
              onMouseDown: function(e) { e.stopPropagation(); },
              style: { display: 'flex', alignItems: 'center', gap: 5, marginTop: 5, color: C.accent, fontWeight: 600, fontSize: 12, textDecoration: 'none', whiteSpace: 'nowrap' }
            }, label, React.createElement(LIcon, { name: 'external-link', size: 11 }));
          })
        : [
            detail && React.createElement('div', { key: 'd', style: { marginTop: 5, fontWeight: 600 } }, detail),
            bdcBillId && React.createElement('a', {
              key: 'l',
              href: bdcBillUrl(bdcBillId), target: '_blank', rel: 'noopener noreferrer',
              onClick: function(e) { e.stopPropagation(); },
              onMouseDown: function(e) { e.stopPropagation(); },
              style: { display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 6, color: C.accent, fontWeight: 600, fontSize: 12, textDecoration: 'none' }
            }, 'Open in Bill.com', React.createElement(LIcon, { name: 'external-link', size: 12 }))
          ]
    ), document.body)
  );
}
window.BdcBillHover = BdcBillHover;

// Billed/Unbilled pill for POs. big=true renders the PO-detail-header size.
// bills = full linked_bills array (several bills can invoice one PO); falls back
// to the single bdcBillId/invoiceNumber/amount fields when the array is absent.
function BilledBadge({ billed, isTransfer, source, bdcBillId, invoiceNumber, amount, bills, big }) {
  var b = billed || isTransfer;
  var n = bills ? bills.length : 0;
  var tip = isTransfer ? 'Internal transfer — no Bill.com bill needed'
    : !b ? 'No Bill.com bill linked yet'
    : n > 1 ? n + ' Bill.com bills are linked to this PO' + (source ? ' (' + source + ')' : '')
    : 'A Bill.com bill is linked to this PO' + (source ? ' (' + source + ')' : '');
  var detail = bdcBillId
    ? 'Invoice ' + (invoiceNumber || '—') + (amount != null ? ' · $' + Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '')
    : null;
  var pillStyle = big
    ? { padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600 }
    : { padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 600 };
  return React.createElement(BdcBillHover, { tip: tip, detail: detail, bdcBillId: bdcBillId, bills: bills },
    React.createElement('span', { style: Object.assign({ background: (b ? C.success : C.textMuted) + '22', color: b ? C.success : C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }, pillStyle) }, b ? 'Billed' : 'Unbilled')
  );
}
window.BilledBadge = BilledBadge;

// Global paste sanitizer — strips leading/trailing whitespace from pastes into any search input.
// Catches stray spaces from copy/paste (e.g. UPC "844828086538 " from another system).
(function() {
  function isSearchInput(el) {
    if (!el || el.tagName !== 'INPUT') return false;
    var t = (el.type || '').toLowerCase();
    if (t && t !== 'text' && t !== 'search') return false;
    var ph = (el.placeholder || '').toLowerCase();
    return ph.indexOf('search') !== -1;
  }
  document.addEventListener('paste', function(e) {
    var el = e.target;
    if (!isSearchInput(el)) return;
    var cd = e.clipboardData || window.clipboardData;
    if (!cd) return;
    var pasted = cd.getData('text');
    if (pasted == null) return;
    var trimmed = pasted.trim();
    if (trimmed === pasted) return; // nothing to fix
    e.preventDefault();
    var start = el.selectionStart || 0;
    var end = el.selectionEnd || 0;
    var newVal = el.value.substring(0, start) + trimmed + el.value.substring(end);
    // Use the native value setter so React's controlled-input change detection fires
    var nativeSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
    nativeSetter.call(el, newVal);
    el.dispatchEvent(new Event('input', { bubbles: true }));
    var caret = start + trimmed.length;
    try { el.setSelectionRange(caret, caret); } catch(_) {}
  }, true);
})();

// Session heartbeat — ping server when user is active to prevent timeout
(function() {
  var HEARTBEAT_INTERVAL = 5 * 60 * 1000; // 5 minutes
  var _lastUserActivity = Date.now();
  var _heartbeatTimer = null;

  function onUserActivity() { _lastUserActivity = Date.now(); }

  ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'].forEach(function(evt) {
    document.addEventListener(evt, onUserActivity, { passive: true });
  });

  function sendHeartbeat() {
    // Only ping if user was active in the last interval
    if (Date.now() - _lastUserActivity < HEARTBEAT_INTERVAL) {
      fetch('/api/auth/heartbeat', { credentials: 'include' }).catch(function(e) { console.error('[silent-catch]', e); });
    }
  }

  _heartbeatTimer = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);
})();

// ─── Number inputs: no mousewheel spin ───
// Chrome increments/decrements a FOCUSED <input type=number> when the wheel fires over it —
// scrolling the page past a focused qty/cost field silently changes its value. While a number
// input is focused, swallow wheel events on that element (attached per-focused-input so normal
// page scrolling stays on the passive fast path). Typing and arrow keys are unaffected.
(function() {
  function preventSpin(e) { e.preventDefault(); }
  document.addEventListener('focusin', function(e) {
    var el = e.target;
    if (el.tagName === 'INPUT' && el.type === 'number') el.addEventListener('wheel', preventSpin, { passive: false });
  });
  document.addEventListener('focusout', function(e) {
    var el = e.target;
    if (el.tagName === 'INPUT' && el.type === 'number') el.removeEventListener('wheel', preventSpin);
  });
})();

// ─── Global Undo (Ctrl+Z) for React controlled inputs ───
(function() {
  var undoStacks = new WeakMap();
  var nativeSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value').set;
  var textareaSetter = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value').set;

  // Track previous values on focus and before each change
  document.addEventListener('focusin', function(e) {
    var el = e.target;
    if (el.tagName !== 'INPUT' && el.tagName !== 'TEXTAREA') return;
    if (!undoStacks.has(el)) undoStacks.set(el, []);
  }, true);

  document.addEventListener('input', function(e) {
    var el = e.target;
    if (el.tagName !== 'INPUT' && el.tagName !== 'TEXTAREA') return;
    if (!undoStacks.has(el)) undoStacks.set(el, []);
    var stack = undoStacks.get(el);
    // Store the value before this input event (use a short delay to capture after React re-render)
    if (!el._undoPrev && el._undoPrev !== '') el._undoPrev = '';
    if (stack.length === 0 || stack[stack.length - 1] !== el._undoPrev) {
      stack.push(el._undoPrev);
      if (stack.length > 50) stack.shift();
    }
    el._undoPrev = el.value;
  }, true);

  // Capture the value before input changes
  document.addEventListener('keydown', function(e) {
    var el = e.target;
    if (el.tagName !== 'INPUT' && el.tagName !== 'TEXTAREA') return;
    el._undoPrev = el.value;

    // Handle Ctrl+Z
    if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
      var stack = undoStacks.get(el);
      if (!stack || stack.length === 0) return;
      e.preventDefault();
      var prev = stack.pop();
      var setter = el.tagName === 'TEXTAREA' ? textareaSetter : nativeSetter;
      setter.call(el, prev);
      el._undoPrev = prev;
      el.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }, true);
})();

// Expose all shared symbols to window for cross-file access
window.C = C; window.themes = themes; window.statusColors = statusColors;
window.setThemeColors = setThemeColors; window.getThemeName = getThemeName; window.getStatusColors = getStatusColors;
window.api = api; window.apiUpload = apiUpload;

// thumbUrl(originUrl, width?) — proxy external product images through our local
// resize+cache route. Returns originUrl unchanged for empty/relative/local URLs
// or hosts we don't proxy (the route's allowlist is the source of truth, but the
// client matches it here so we don't waste a round-trip for non-allowlisted hosts).
// width should be one of [72, 144, 256, 512]; omit for the full-size original.
var THUMB_PROXY_HOSTS = { 'images.zentail.com': 1, 'm.media-amazon.com': 1, 'cloudinary.images-iherb.com': 1 };
function thumbUrl(originUrl, width) {
  if (!originUrl || typeof originUrl !== 'string') return originUrl;
  if (originUrl.indexOf('/api/img') === 0) return originUrl;
  var host;
  try { host = new URL(originUrl).hostname; } catch (e) { return originUrl; }
  if (!THUMB_PROXY_HOSTS[host]) return originUrl;
  var qs = 'u=' + encodeURIComponent(originUrl);
  if (width) qs += '&w=' + width;
  return '/api/img?' + qs;
}
window.thumbUrl = thumbUrl;

// ProductThumb — a product image that falls back to the package icon when the bytes never
// arrive (0-byte object at the origin, dead URL, 404 from /api/img — error_log 761,
// 2026-10-06). Draws the same square the inline img/placeholder pairs drew: `size` is the
// side, `radius` the corner, `iconSize` the placeholder icon; `onClick` makes it a pointer.
function ProductThumb(props) {
  var st = React.useState(false), failed = st[0], setFailed = st[1];
  var src = props.src || null;
  React.useEffect(function() { setFailed(false); }, [src]);
  var size = props.size || 40, radius = props.radius || 6;
  var box = Object.assign({ width: size, height: size, borderRadius: radius, flexShrink: 0 }, props.style || {});
  if (!src || failed) {
    return React.createElement('div', { style: Object.assign({}, box, { background: C.surface, display: 'flex', alignItems: 'center', justifyContent: 'center' }) },
      React.createElement(LIcon, { name: 'package', size: props.iconSize || Math.round(size * 0.4), color: C.textDim }));
  }
  return React.createElement('img', { src: src, alt: props.alt || '', loading: 'lazy', decoding: 'async', title: props.title, onClick: props.onClick,
    onError: function() { setFailed(true); },
    style: Object.assign({}, box, { objectFit: 'cover', border: '1px solid ' + C.border, cursor: props.onClick ? 'pointer' : undefined }) });
}
window.ProductThumb = ProductThumb;
window.setAuthToken = setAuthToken; window.getAuthToken = getAuthToken;
window.hasPermission = hasPermission; window.canEdit = canEdit; window.canManageUsers = canManageUsers; window.canUpdatePOStatus = canUpdatePOStatus;
// ─── State / province dropdown shared by every address form (Anthony 2026-09-19) ───
// US (50 states, DC, the territories and the military codes), Canada and Australia get a
// select of "code — name" and store the code; any other country keeps a text field. A value
// already saved that is not in the list (e.g. "Florida" spelled out) stays visible as
// "(saved)" until someone picks the code. onChange receives an event-shaped { target: { value } }
// so the forms' existing handlers work unchanged.
var STATE_LISTS = {
  US: [['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'], ['CA', 'California'], ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'],
    ['DC', 'District of Columbia'], ['FL', 'Florida'], ['GA', 'Georgia'], ['HI', 'Hawaii'], ['ID', 'Idaho'], ['IL', 'Illinois'], ['IN', 'Indiana'], ['IA', 'Iowa'],
    ['KS', 'Kansas'], ['KY', 'Kentucky'], ['LA', 'Louisiana'], ['ME', 'Maine'], ['MD', 'Maryland'], ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'],
    ['MS', 'Mississippi'], ['MO', 'Missouri'], ['MT', 'Montana'], ['NE', 'Nebraska'], ['NV', 'Nevada'], ['NH', 'New Hampshire'], ['NJ', 'New Jersey'], ['NM', 'New Mexico'],
    ['NY', 'New York'], ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'], ['OK', 'Oklahoma'], ['OR', 'Oregon'], ['PA', 'Pennsylvania'], ['RI', 'Rhode Island'],
    ['SC', 'South Carolina'], ['SD', 'South Dakota'], ['TN', 'Tennessee'], ['TX', 'Texas'], ['UT', 'Utah'], ['VT', 'Vermont'], ['VA', 'Virginia'], ['WA', 'Washington'],
    ['WV', 'West Virginia'], ['WI', 'Wisconsin'], ['WY', 'Wyoming'],
    ['PR', 'Puerto Rico'], ['VI', 'U.S. Virgin Islands'], ['GU', 'Guam'], ['AS', 'American Samoa'], ['MP', 'Northern Mariana Islands'],
    ['AA', 'Armed Forces Americas'], ['AE', 'Armed Forces Europe'], ['AP', 'Armed Forces Pacific']],
  CA: [['AB', 'Alberta'], ['BC', 'British Columbia'], ['MB', 'Manitoba'], ['NB', 'New Brunswick'], ['NL', 'Newfoundland and Labrador'], ['NS', 'Nova Scotia'],
    ['NT', 'Northwest Territories'], ['NU', 'Nunavut'], ['ON', 'Ontario'], ['PE', 'Prince Edward Island'], ['QC', 'Quebec'], ['SK', 'Saskatchewan'], ['YT', 'Yukon']],
  AU: [['ACT', 'Australian Capital Territory'], ['NSW', 'New South Wales'], ['NT', 'Northern Territory'], ['QLD', 'Queensland'], ['SA', 'South Australia'],
    ['TAS', 'Tasmania'], ['VIC', 'Victoria'], ['WA', 'Western Australia']],
};
var STATE_COUNTRY_ALIASES = { USA: 'US', 'UNITED STATES': 'US', 'UNITED STATES OF AMERICA': 'US', CAN: 'CA', CANADA: 'CA', AUS: 'AU', AUSTRALIA: 'AU' };
function stateListFor(country) {
  var c = String(country == null || country === '' ? 'US' : country).trim().toUpperCase();
  return STATE_LISTS[STATE_COUNTRY_ALIASES[c] || c] || null;
}
function stateCodesFor(country) { var l = stateListFor(country); return l ? l.map(function(x) { return x[0]; }) : null; }
function StateSelect({ label, value, onChange, country, style: extraStyle, placeholder, compact, disabled, title }) {
  var list = stateListFor(country);
  if (!list) return React.createElement(Inp, { label: label, value: value || '', onChange: onChange, style: extraStyle, placeholder: placeholder || 'State / province', disabled: disabled, title: title });
  var cur = String(value || '').trim().toUpperCase();
  var known = list.some(function(x) { return x[0] === cur; });
  var baseStyle = { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: compact ? '4px 8px' : '8px 12px', borderRadius: compact ? 4 : 6, fontSize: compact ? 11 : 13,
    fontFamily: 'inherit', outline: 'none', minWidth: 0, width: '100%', boxSizing: 'border-box', cursor: disabled ? 'default' : 'pointer', transition: 'border-color 0.15s, box-shadow 0.15s' };
  var select = React.createElement('select', {
    value: cur, disabled: disabled, title: title,
    onChange: function(e) { if (onChange) onChange({ target: { value: e.target.value } }); },
    onFocus: function(e) { e.target.style.borderColor = C.accent; e.target.style.boxShadow = '0 0 0 2px ' + C.accent + '33'; },
    onBlur: function(e) { e.target.style.borderColor = C.border; e.target.style.boxShadow = 'none'; },
    style: Object.assign({}, baseStyle, extraStyle || {})
  },
    React.createElement('option', { value: '' }, placeholder || '— State —'),
    (cur && !known) ? React.createElement('option', { value: cur }, cur + ' (saved)') : null,
    list.map(function(x) { return React.createElement('option', { key: x[0], value: x[0] }, x[0] + ' — ' + x[1]); }));
  if (!label) return select;
  return React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 } },
    React.createElement('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' } }, label),
    select);
}
window.STATE_LISTS = STATE_LISTS; window.stateListFor = stateListFor; window.stateCodesFor = stateCodesFor; window.StateSelect = StateSelect;
window.Badge = Badge; window.Btn = Btn; window.Inp = Inp; window.Sel = Sel; window.SearchSelect = SearchSelect;
window.Card = Card; window.Modal = Modal; window.Empty = Empty; window.Toast = Toast;
window.AppDialogHost = AppDialogHost; window.appConfirm = appConfirm; window.appPrompt = appPrompt; window.appAlert = appAlert; window.appChoice = appChoice;
window.TH = TH; window.TD = TD; window.DataTable = DataTable; window.CopyCell = CopyCell; window.SearchInput = SearchInput;
window.loadColumnPrefs = loadColumnPrefs; window.saveColumnPrefs = saveColumnPrefs; window.resetColumnPrefsCache = resetColumnPrefsCache;
window.downloadCSV = downloadCSV; window.escapeCSV = escapeCSV; window.arrayToCSV = arrayToCSV; window.downloadTemplate = downloadTemplate;
window.channelIcon = channelIcon;
window.FillBar = FillBar; window.BinBadge = BinBadge;

// Gold "TOP" chip for products tagged by the nightly top-revenue job — pass any row that
// carries the product's top_revenue_* columns (catalog rows, PO line items).
function TopRevChip(row) {
  if (!row || !row.is_top_revenue) return null;
  var amt = row.top_revenue_amount != null ? '$' + Math.round(Number(row.top_revenue_amount)).toLocaleString() : '';
  var tip = 'Top revenue' + (row.top_revenue_rank ? ' #' + row.top_revenue_rank : '') +
            (amt ? ' · ' + amt + ' / ' + (row.top_revenue_window_days || 90) + 'd' : '');
  return React.createElement('span', { title: tip, style: { fontSize: 9, fontWeight: 700, padding: '1px 5px',
    borderRadius: 6, background: '#EAB30818', color: '#EAB308', marginLeft: 6, verticalAlign: 'middle',
    letterSpacing: '0.03em', whiteSpace: 'nowrap' } }, 'TOP');
}
window.TopRevChip = TopRevChip;

// Amazon Transparency program logo — shown in every Transparency pill/chip app-wide.
var TRANSPARENCY_LOGO_SRC = '/img/transparency-logo.png';
// Purple "T-CODE" chip for products enrolled in Amazon Transparency — every unit needs a
// serialized Transparency label (from the brand's code pool) in addition to the FNSKU.
function TransparencyChip(row) {
  if (!row || !row.requires_transparency) return null;
  return React.createElement('span', {
    title: 'Amazon Transparency — every unit needs a serialized Transparency label in addition to the FNSKU label',
    style: { fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 6, background: '#8B5CF618', color: '#8B5CF6',
      marginLeft: 6, verticalAlign: 'middle', letterSpacing: '0.03em', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 3 } },
    React.createElement('img', { src: TRANSPARENCY_LOGO_SRC, alt: '', style: { width: 10, height: 10, borderRadius: 2, display: 'block' } }),
    'T-CODE');
}
window.TransparencyChip = TransparencyChip;
// Standard row/list pill with the Transparency logo — use anywhere an item/PO needs the
// Transparency marker (FBA pack lists, create-shipment PO list, ...).
function TransparencyPill(props) {
  return React.createElement('span', {
    title: 'Amazon Transparency — every unit needs a serialized Transparency label in addition to the FNSKU label',
    style: Object.assign({ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, padding: '3px 9px', borderRadius: 4, background: '#A78BFA' + '1A', color: '#A78BFA', letterSpacing: '0.03em', whiteSpace: 'nowrap' }, (props && props.style) || {}) },
    React.createElement('img', { src: TRANSPARENCY_LOGO_SRC, alt: '', style: { width: 13, height: 13, borderRadius: 2, display: 'block' } }),
    'TRANSPARENCY');
}
window.TransparencyPill = TransparencyPill;

// Carrier display names + carrier tracking-page URLs — shared by the shipping module
// and the Orders detail modal (moved from app-shipping.js so every bundle can render
// clickable tracking numbers).
var SHIP_CARRIER_DISPLAY = { dhl_global_mail: 'DHL', dhl_ecommerce_us: 'DHL', stamps_com: 'USPS', usps: 'USPS', globalpost: 'GlobalPost', gp_plus: 'GlobalPost', fedex: 'FedEx', ups: 'UPS', amazon_shipping: 'Amazon Shipping', other: 'Other' };
function shipCarrierLabel(c) {
  if (!c) return c;
  return SHIP_CARRIER_DISPLAY[String(c).toLowerCase()] || c;
}
function shipTrackingUrl(carrier, tracking) {
  var c = String(carrier || '').toLowerCase();
  var t = encodeURIComponent(tracking || '');
  if (c.indexOf('dhl') !== -1) return 'https://webtrack.dhlecs.com/?trackingNumber=' + t;
  if (c.indexOf('usps') !== -1 || c.indexOf('stamps') !== -1) return 'https://tools.usps.com/go/TrackConfirmAction?tLabels=' + t;
  if (c.indexOf('globalpost') !== -1 || c === 'gp_plus') return 'https://t.17track.net/en#nums=' + t;
  if (c.indexOf('ups') === 0) return 'https://www.ups.com/track?tracknum=' + t;
  if (c.indexOf('fedex') !== -1) return 'https://www.fedex.com/fedextrack/?trknbr=' + t;
  return 'https://t.17track.net/en#nums=' + t;
}
// Service codes → display names: globalmail_parcel_direct_ddp →
// "Globalmail Parcel Direct DDP". Strings that already look like display
// text (no underscores, mixed case) pass through untouched.
var SHIP_SERVICE_ACRONYMS = { ddp: 'DDP', ddu: 'DDU', usps: 'USPS', dhl: 'DHL' };
function shipServiceLabel(code) {
  if (!code) return code;
  var s = String(code);
  if (s.indexOf('_') === -1 && s !== s.toLowerCase()) return s;
  return s.split('_').map(function(w) {
    var lw = w.toLowerCase();
    if (SHIP_SERVICE_ACRONYMS[lw]) return SHIP_SERVICE_ACRONYMS[lw];
    return w.charAt(0).toUpperCase() + w.slice(1);
  }).join(' ');
}
window.shipServiceLabel = shipServiceLabel;
window.shipCarrierLabel = shipCarrierLabel; window.shipTrackingUrl = shipTrackingUrl;
window.generateCode128SVG = generateCode128SVG; window.generateCode128SVGmm = generateCode128SVGmm; window.generateBarcodeImg = generateBarcodeImg; window.printBinLabel = printBinLabel;
window.generatePDF417SVG = generatePDF417SVG; window.generateCasePDF417SVG = generateCasePDF417SVG; window.buildAmazonCaseBarcodeData = buildAmazonCaseBarcodeData;

// ─── ReportParamSelect — shared multi-select dropdown (moved here from
// app-reports.js so the shipping/ads bundles can use it too). Two modes:
// preloaded list (default; client-side filtered) and search mode (`search`
// prop, high-cardinality fields) querying fetchReportFieldValues (defined in
// app-reports.js — only bundles that pass search:true need it). Selected
// values stay pinned at the top so they can always be deselected.
// The menu's reset row names the plural of the filter's label — "All batches", "All countries" —
// where a bare "s" used to be appended ("All batchs" on the Shipments Batch filter, Anthony 2026-10-13).
function pluralLower(word) {
  var w = String(word || '').trim().toLowerCase();
  if (/(ss|us|is|x|z|ch|sh)$/.test(w)) return w + 'es';
  // Already plural: the rule and auto-batch editors pass 'Tags', 'Countries', 'Carriers' ("All tagses", Anthony 2026-10-24)
  if (/s$/.test(w)) return w;
  if (/[^aeiou]y$/.test(w)) return w.slice(0, -1) + 'ies';
  return w + 's';
}
function ReportParamSelect({ label, values, loading, selected, onChange, search: searchable, source, fieldKey, onCreate, createLabel, valueColors, onManage, manageLabel }) {
  var h = React.createElement;
  var [open, setOpen] = useState(false);
  var [search, setSearch] = useState('');
  var [results, setResults] = useState(null);
  var [searching, setSearching] = useState(false);
  var [menuPos, setMenuPos] = useState(null);
  var searchSeq = useRef(0);
  var ref = useRef(null);
  var menuRef = useRef(null);

  // The menu portals to <body> (position: fixed) so it floats above modals and
  // never clips inside their scroll containers. Capture-phase listener: modal
  // content stopPropagation()s bubbling mousedowns, which used to keep the
  // menu open on click-away.
  var measure = function() {
    if (!ref.current) return;
    var r = ref.current.getBoundingClientRect();
    var z = window.APP_ZOOM || 1; // gBCR is visual px; fixed positions in zoomed px
    setMenuPos({ top: (r.bottom + 4) / z, left: r.left / z, minWidth: Math.max(260, r.width / z) });
  };
  useEffect(function() {
    if (!open) return;
    var onDown = function(e) {
      if (ref.current && ref.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown, true);
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return function() {
      document.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [open]);

  // Search mode: debounced live lookup once 2+ characters are typed
  useEffect(function() {
    if (!searchable || !open) return;
    var term = search.trim();
    if (term.length < 2) { setResults(null); setSearching(false); return; }
    var seq = ++searchSeq.current;
    setSearching(true);
    var t = setTimeout(function() {
      fetchReportFieldValues(source, fieldKey, term).then(function(vals) {
        if (seq !== searchSeq.current) return;
        setResults(vals); setSearching(false);
      }).catch(function() {
        if (seq !== searchSeq.current) return;
        setResults([]); setSearching(false);
      });
    }, 300);
    return function() { clearTimeout(t); };
  }, [search, open, searchable, source, fieldKey]);

  var sel = selected || [];
  var summary = sel.length === 0 ? 'All'
    : sel.length === 1 ? sel[0]
    : sel.length + ' selected';
  var active = sel.length > 0;

  var list;
  if (searchable) {
    var found = results || [];
    list = sel.concat(found.filter(function(v) { return sel.indexOf(v) === -1; }));
  } else {
    list = (values || []).filter(function(v) {
      return !search || String(v).toLowerCase().indexOf(search.toLowerCase()) !== -1;
    });
  }

  var toggle = function(v) {
    if (sel.indexOf(v) !== -1) onChange(sel.filter(function(x) { return x !== v; }));
    else onChange(sel.concat([v]));
  };

  return h('div', { ref: ref, style: { position: 'relative', display: 'inline-block' } },
    h('button', {
      className: 'fbtn',
      onClick: function() { if (!open) measure(); setOpen(!open); setSearch(''); },
      style: {
        background: active ? C.accentSoft : C.bg,
        border: '1px solid ' + (active ? C.accent : C.border),
        color: active ? C.accent : C.text,
        padding: '7px 12px', borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'inherit',
      }
    },
      h('span', { style: { color: active ? C.accent : C.textMuted, fontWeight: 600 } }, label + ':'),
      summary,
      h(LIcon, { name: 'chevron-down', size: 12 })
    ),
    open && menuPos && ReactDOM.createPortal(h('div', {
      ref: menuRef,
      className: 'rps-menu', // on <body>, outside every enclosing menu: their click-away handlers look for this (shipping "Other" menu, 9-22)
      style: {
        position: 'fixed', top: menuPos.top, left: menuPos.left, zIndex: 5000,
        background: C.surface, border: '1px solid ' + C.border, borderRadius: 8,
        boxShadow: '0 8px 24px rgba(0,0,0,0.3)', minWidth: menuPos.minWidth, maxHeight: 'min(320px, calc(calc(100vh * var(--invzoom, 1)) - ' + Math.round(menuPos.top + 20) + 'px))', overflowY: 'auto',
      }
    },
      h('input', {
        autoFocus: true,
        value: search,
        onChange: function(e) { setSearch(e.target.value); },
        placeholder: searchable ? 'Type 2+ characters to search…' : 'Search…',
        style: { width: '100%', background: C.bg, border: 'none', borderBottom: '1px solid ' + C.border, color: C.text, padding: '8px 12px', fontSize: 13, outline: 'none', boxSizing: 'border-box' }
      }),
      h('div', {
        onClick: function() { onChange([]); setOpen(false); },
        onMouseOver: function(e) { e.currentTarget.style.background = C.surfaceHover; },
        onMouseOut: function(e) { e.currentTarget.style.background = 'transparent'; },
        style: { padding: '8px 12px', cursor: 'pointer', fontSize: 13, color: sel.length === 0 ? C.accent : C.text, fontWeight: sel.length === 0 ? 600 : 400, borderBottom: '1px solid ' + C.border }
      }, 'All ' + pluralLower(label)),
      onCreate && h('div', {
        onClick: function() { setOpen(false); onCreate(); },
        onMouseOver: function(e) { e.currentTarget.style.background = C.surfaceHover; },
        onMouseOut: function(e) { e.currentTarget.style.background = 'transparent'; },
        style: { padding: '8px 12px', cursor: 'pointer', fontSize: 13, color: C.accent, fontWeight: 600, borderBottom: '1px solid ' + C.border, display: 'flex', alignItems: 'center', gap: 6 }
      }, h(LIcon, { name: 'plus', size: 13 }), createLabel || 'Create new'),
      onManage && h('div', {
        onClick: function() { setOpen(false); onManage(); },
        onMouseOver: function(e) { e.currentTarget.style.background = C.surfaceHover; },
        onMouseOut: function(e) { e.currentTarget.style.background = 'transparent'; },
        style: { padding: '8px 12px', cursor: 'pointer', fontSize: 13, color: C.accent, fontWeight: 600, borderBottom: '1px solid ' + C.border, display: 'flex', alignItems: 'center', gap: 6 }
      }, h(LIcon, { name: 'settings', size: 13 }), manageLabel || 'Manage'),
      (searchable ? searching : loading)
        ? h('div', { style: { padding: 12, color: C.textDim, fontSize: 12 } }, searchable ? 'Searching…' : 'Loading values…')
        : list.length === 0
          ? h('div', { style: { padding: 12, color: C.textDim, fontSize: 12 } },
              searchable
                ? (search.trim().length < 2 ? 'Type at least 2 characters to search your data' : 'No matches')
                : (values && values.length ? 'No matches' : 'No values in your data yet'))
          : list.map(function(v) {
              var isSel = sel.indexOf(v) !== -1;
              return h('div', {
                key: v,
                onClick: function() { toggle(v); },
                onMouseOver: function(e) { e.currentTarget.style.background = C.surfaceHover; },
                onMouseOut: function(e) { e.currentTarget.style.background = 'transparent'; },
                style: { padding: '7px 12px', cursor: 'pointer', fontSize: 13, color: C.text, display: 'flex', alignItems: 'center', gap: 8 }
              },
                h('div', {
                  style: {
                    width: 15, height: 15, borderRadius: 4, flexShrink: 0,
                    border: '1px solid ' + (isSel ? C.accent : C.border),
                    background: isSel ? C.accent : 'transparent',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  }
                }, isSel && h(LIcon, { name: 'check', size: 11, color: '#fff' })),
                valueColors && valueColors[v] && h('span', { style: { width: 10, height: 10, borderRadius: 3, background: valueColors[v], flexShrink: 0 } }),
                h('span', { style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, v)
              );
            })
    ), document.body)
  );
}
window.ReportParamSelect = ReportParamSelect;

// ─── Sidebar pin state: server-side preference ───
// The pin (collapsed) state is stored in users.preferences.sidebar_collapsed so
// it follows the user across devices AND across the three app origins (main /
// ads / ship have separate localStorage). localStorage stays as the fast path
// for first paint; the server value wins once it arrives after login.
function syncSidebarPref(apply) {
  fetch('/api/auth/preferences', { credentials: 'include' })
    .then(function(r) { return r.ok ? r.json() : null; })
    .then(function(p) {
      if (p && typeof p.sidebar_collapsed === 'boolean') {
        try { localStorage.setItem('sidebar_collapsed', String(p.sidebar_collapsed)); } catch (e) {}
        apply(p.sidebar_collapsed);
      }
    })
    .catch(function() {});
}
function saveSidebarPref(collapsed) {
  try { localStorage.setItem('sidebar_collapsed', String(collapsed)); } catch (e) {}
  fetch('/api/auth/preferences', {
    method: 'PUT', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sidebar_collapsed: collapsed })
  }).catch(function() {});
}
window.syncSidebarPref = syncSidebarPref;
window.saveSidebarPref = saveSidebarPref;

// ─── HOURLY CAT PARADE (easter egg) ───
// Once per hour: a pixel cat gallops in along the bottom of the page, stops at
// center, faces the user and waves ("Hola" bubble) — then a rat streaks past
// ("!" above the cat), the cat bolts after it, a dog chases the cat, and a
// grandmother with a raised broom chases all three. Shared across tabs via
// localStorage 'cat_last_run'; only visible tabs fire. Every element is
// pointer-events: none — it can never intercept clicks. window.catRun() = demo.
// The hourly clock now plays the INVOICE STORM scene (further down; before it
// the FORKLIFT FUMBLE), or the TRICK-OR-TREAT PARADE during Oct 24-31; the cat
// parade and the forklift stay available as manual demos and the rig
// (catSpawn/catGo/catTwoFrames/catbub) is shared.
// Sprites are ASCII pixel grids: '.' transparent, 'f' fur (cat color variant),
// other chars index the actor's palette. One <rect> per horizontal run.
var CAT_PAL = { F: '#00000033', w: '#F1F5F9', p: '#F472B6', e: '#1F2937' };
var RAT_PAL = { f: '#78716C', p: '#F9A8D4', e: '#1F2937', w: '#D6D3D1' };
var DOG_PAL = { f: '#C9974C', d: '#8B5E2F', w: '#F1F5F9', e: '#1F2937', r: '#DC2626', p: '#F472B6' };
var CAT_RUN_A = [
".....................ff....ff...",
".....................fpf...fpf..",
"....................fffffffffff.",
".FF.................fffffffffff.",
".ff.................ffffffeefff.",
".ff.................ffffffeewwww",
"..ff................ffffffffwwwp",
"...ff.ffffffffffffffffffffwwww..",
".....ffffFFffFFffFFfffffff......",
".....ffffFFffFFffFFfwwwww.......",
".....ffffFFffFFffFFfwwwww.......",
".....ffffffffffffffffwwww.......",
".....fffffffffffffffwww.........",
"......ffffffffffffffff..........",
"......ff...ff...ff..ff..........",
".....ff....ff...ff...ff.........",
"....ff.....ff...ff....ff........",
"....ff.....ff...ff....ff........",
"....ww.....ww...ww....ww........",
"................................"
];
var CAT_RUN_B = [
".....................ff....ff...",
".....................fpf...fpf..",
"....................fffffffffff.",
"....................fffffffffff.",
"....................ffffffeefff.",
".FF.................ffffffeewwww",
".ff.................ffffffffwwwp",
"..ff..ffffffffffffffffffffwwww..",
".....ffffFFffFFffFFfffffff......",
".....ffffFFffFFffFFfwwwww.......",
".....ffffFFffFFffFFfwwwww.......",
".....ffffffffffffffffwwww.......",
".....fffffffffffffffwww.........",
"......ffffffffffffffff..........",
"........ff..ff....ff..ff........",
"........ff..ff....ff..ff........",
"........ww..ww....ww..ww........",
"................................",
"................................",
"................................"
];
var CAT_WAVE_A = [
"..ff..........ff........",
".fppf........fppf.......",
".ffffffffffffffff.......",
".ffffffffffffffff.ww....",
".fffeefffffeeffff.ww....",
".fffeefffffeeffff.ff....",
".ffffffppffffffff.ff....",
".ffffwwwwwwwfffff.ff....",
".ffffwwweewwfffff.ff....",
"..ffffffffffffffffff....",
"..ffffffffffffffff......",
"f.fffwwwwwwwffff........",
"f.fffwwwwwwwffff........",
"f.fffwwwwwwwffff........",
"f.fffwwwwwwwffff........",
".ffffwwwwwwwffff........",
"....ff....ff............",
"....ww....ww............",
"....ww....ww............",
"........................"
];
var CAT_WAVE_B = [
"..ff..........ff........",
".fppf........fppf.......",
".ffffffffffffffff.......",
".ffffffffffffffff.......",
".fffeefffffeeffff.......",
".fffeefffffeeffff.......",
".ffffffppffffffff.ww....",
".ffffwwwwwwwfffff.ww....",
".ffffwwweewwfffff.ff....",
"..ffffffffffffffffff....",
"..ffffffffffffffff......",
"f.fffwwwwwwwffff........",
"f.fffwwwwwwwffff........",
"f.fffwwwwwwwffff........",
"f.fffwwwwwwwffff........",
".ffffwwwwwwwffff........",
"....ff....ff............",
"....ww....ww............",
"....ww....ww............",
"........................"
];
var RAT_A = [
"..................ff......",
"ppp...............fpf.....",
"...pp....ffffffff.fff.....",
".....ppffffffffffffffffff.",
"......fffffffffffffffefff.",
"......fffffffffffffffffffp",
"......ffwwwwwwwffffffffff.",
"......ffwwwwwwwffffffff...",
".......fffffffffffff......",
".........ff......ff.......",
"........pp......pp........",
".........................."
];
var RAT_B = [
"..................ff......",
"..................fpf.....",
"ppppp....ffffffff.fff.....",
".....ppffffffffffffffffff.",
"......fffffffffffffffefff.",
"......fffffffffffffffffffp",
"......ffwwwwwwwffffffffff.",
"......ffwwwwwwwffffffff...",
".......fffffffffffff......",
"...........ff..ff.........",
"...........pp..pp.........",
".........................."
];
var DOG_A = [
"...........................ffffffff.....",
"..........................ddffffffff....",
"...dd.....................ddfffffefff...",
"...ff.....................ddfffffefff...",
"...ff.....................ddffffffffffee",
"....ff....................ddffffwwwwwww.",
"....ff....................dffffffwwww...",
"......ffffffffffffffffffffffffffff.pp...",
"......fffffffffffffffffffffrrrrrrr.pp...",
"......ffffffffffffffffffwwwwwww.........",
"......ffffffffffffffffffwwwwwww.........",
"......ffffffffffffffffffwwwwwww.........",
"......ffffffffffffffffffwwwwww..........",
".......ffffffffffffffffffwwww...........",
".......fffffffffffffffffffff............",
"........ffffffffffffffffff..............",
"........ff...ff......ff...ff............",
".......ff....ff......ff....ff...........",
"......ff.....ff......ff.....ff..........",
"......ww.....ww......ww.....ww..........",
"........................................",
"........................................"
];
var DOG_B = [
"...........................ffffffff.....",
"..........................ddffffffff....",
"..........................ddfffffefff...",
"...dd.....................ddfffffefff...",
"...ff.....................ddffffffffffee",
"...ff.....................ddffffwwwwwww.",
"....ff....................dffffffwwww...",
"......ffffffffffffffffffffffffffff.pp...",
"......fffffffffffffffffffffrrrrrrr.pp...",
"......ffffffffffffffffffwwwwwww.........",
"......ffffffffffffffffffwwwwwww.........",
"......ffffffffffffffffffwwwwwww.........",
"......ffffffffffffffffffwwwwww..........",
".......ffffffffffffffffffwwww...........",
".......fffffffffffffffffffff............",
"........ffffffffffffffffff..............",
".........ff...ff....ff...ff.............",
".........ff...ff....ff...ff.............",
".........ww...ww....ww...ww.............",
"........................................",
"........................................",
"........................................"
];
var LADY_PAL = { s: '#D9A574', g: '#E5E7EB', d: '#0D9488', D: '#0A5A54', w: '#F1F5F9', b: '#92400E', y: '#EAB308', e: '#1F2937', p: '#F9A8D4' };
var LADY_BASE = [
"..................................yyyyyyyy..",
"......................gggggggggg..yyyyyyyy..",
"..................ggggggggggggggg.yyyyyyyy..",
"..................ggggggggggggggg.yyyyyyyy..",
"..................ggggssssssssssg.yyyyyyyy..",
"......................ssssssssssg....bb.....",
"......................sssssseess.....bb.....",
"......................sssssseess.....bb.....",
"......................ssssssssss.....bb.....",
"......................ssppssssss.....bb.....",
"......................sssssseees.....bb.....",
"......................ssssssssss....sss.....",
"..........................sss.......sss.....",
"....................ddddddddddddddddd.......",
"..................ddddddddddddddddddd.......",
"..................dddddwwwwwwwwddd..........",
"....................dddwwwwwwwwddd..........",
".................ssddddwwwwwwwwddd..........",
"....................dddwwwwwwwwddd..........",
"....................dddwwwwwwwwddd..........",
"...................ddddwwwwwwwwdddd.........",
"...................ddddwwwwwwwwdddd.........",
"...................ddddwwDDDDwwdddd.........",
"...................ddddwwDDDDwwdddd.........",
"..................dddddwwwwwwwwddddd........",
"..................dddddwwwwwwwwddddd........",
"..................dddDdddddddddDdddd........",
"..................dddddddddddddddddd........",
"........................sss..sss............",
"........................sss..sss............"
];
var LADY_A = LADY_BASE.concat([
".....................eeee.....eeee..........",
".....................eeee.....eeee.........."
]);
// Frame B: broom bobs down 1px, free hand swings up, legs pass mid-stride.
var LADY_B = LADY_A.slice();
LADY_B[0] = "............................................";
LADY_B[5] = "......................ssssssssssg.yyyyyyyy..";
LADY_B[15] = "................ssdddddwwwwwwwwddd..........";
LADY_B[17] = "....................dddwwwwwwwwddd..........";
LADY_B[28] = ".........................sss.sss............";
LADY_B[29] = ".........................sss.sss............";
LADY_B[30] = ".......................eeee..eeee...........";
LADY_B[31] = ".......................eeee..eeee...........";
function catAsciiRects(rows, pal, fur, dx) {
  var out = '';
  rows.forEach(function(row, y) {
    var x = 0;
    while (x < row.length) {
      var c = row[x];
      if (c === '.') { x++; continue; }
      var x0 = x;
      while (x < row.length && row[x] === c) x++;
      var fill;
      if (c === 'f') fill = fur;
      else if (c === '1' && !pal['1']) fill = catShadeHex(fur, 0.68);
      else if (c === '3' && !pal['3']) fill = catShadeHex(fur, 1.30);
      else fill = pal[c] || fur;
      if (c === 'F') out += '<rect x="' + (x0 + (dx || 0)) + '" y="' + y + '" width="' + (x - x0) + '" height="1" fill="' + fur + '"/><rect x="' + (x0 + (dx || 0)) + '" y="' + y + '" width="' + (x - x0) + '" height="1" fill="' + pal.F + '"/>';
      else out += '<rect x="' + (x0 + (dx || 0)) + '" y="' + y + '" width="' + (x - x0) + '" height="1" fill="' + fill + '"/>';
    }
  });
  return out;
}
// ── Detail pipeline: 2x upscale + dark outline + auto-shading + accent
// patches (eye glints, whiskers, collar tag, broom texture). Runs once,
// lazily, on the base sprites above; only the processed frames render.
function catShadeHex(hex, k) {
  var n = parseInt(hex.slice(1), 16);
  var r = Math.min(255, Math.round(((n >> 16) & 255) * k));
  var g = Math.min(255, Math.round(((n >> 8) & 255) * k));
  var b = Math.min(255, Math.round((n & 255) * k));
  return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}
function catDetail(rows, opts) {
  opts = opts || {};
  var W = rows[0].length;
  var g = [];
  var blank = function() { var a = []; for (var i = 0; i < W * 2 + 2; i++) a.push('.'); return a; };
  g.push(blank());
  rows.forEach(function(r) {
    var d = ['.'];
    for (var i = 0; i < r.length; i++) { d.push(r[i], r[i]); }
    d.push('.');
    g.push(d, d.slice());
  });
  g.push(blank());
  (opts.patches || []).forEach(function(p) {
    var bx = 2 * p[0] + 1, by = 2 * p[1] + 1, q = p[2], ch = p[3];
    var cells = q === 'full' ? [[bx, by], [bx + 1, by], [bx, by + 1], [bx + 1, by + 1]]
      : q === 'tl' ? [[bx, by]] : q === 'tr' ? [[bx + 1, by]] : q === 'bl' ? [[bx, by + 1]] : [[bx + 1, by + 1]];
    cells.forEach(function(c) { if (g[c[1]] && g[c[1]][c[0]] !== undefined) g[c[1]][c[0]] = ch; });
  });
  var y, x;
  for (y = 0; y < g.length; y++) for (x = 0; x < g[0].length; x++) {
    if (g[y][x] !== '.') continue;
    var nb = [g[y - 1] && g[y - 1][x], g[y + 1] && g[y + 1][x], g[y][x - 1], g[y][x + 1]];
    if (nb.some(function(c) { return c && c !== '.' && c !== 'O'; })) g[y][x] = 'O';
  }
  var out = g.map(function(r) { return r.slice(); });
  for (y = 1; y < g.length - 1; y++) for (x = 0; x < g[0].length; x++) {
    var c = g[y][x];
    if (opts.dark && opts.dark[c] && g[y + 1][x] === 'O') out[y][x] = opts.dark[c];
    else if (opts.light && opts.light[c] && g[y - 1][x] === 'O') out[y][x] = opts.light[c];
  }
  return out.map(function(r) { return r.join(''); });
}
CAT_PAL.O = '#141A23'; CAT_PAL['2'] = '#C7CDD6';
RAT_PAL.O = '#141A23'; RAT_PAL['1'] = '#57524E'; RAT_PAL['3'] = '#948E88';
DOG_PAL.O = '#141A23'; DOG_PAL['1'] = '#A0763B'; DOG_PAL['3'] = '#DDB06A'; DOG_PAL['2'] = '#C9CFD8'; DOG_PAL.y = '#EAB308';
LADY_PAL.O = '#141A23'; LADY_PAL['1'] = '#0A5A54'; LADY_PAL['3'] = '#16B3A5'; LADY_PAL['4'] = '#B9884F'; LADY_PAL['5'] = '#C3C9D1'; LADY_PAL['6'] = '#F5F7FA'; LADY_PAL['2'] = '#C9CFD8'; LADY_PAL['7'] = '#B45309';
var _catSprites = null;
function catSprites() {
  if (_catSprites) return _catSprites;
  var runOpts = { dark: { f: '1', w: '2' }, light: { f: '3' }, patches: [[27,4,'tl','w'],[30,5,'br','e'],[30,6,'tr','e']] };
  var waveOpts = { dark: { f: '1', w: '2' }, light: { f: '3' }, patches: [[5,4,'tl','w'],[12,4,'tl','w'],[5,7,'bl','e'],[11,7,'br','e'],[4,18,'br','O'],[10,18,'br','O']] };
  var ratOpts = { dark: { f: '1' }, light: { f: '3' }, patches: [[21,4,'tl','w']] };
  var dogOpts = { dark: { f: '1', w: '2' }, light: { f: '3' }, patches: [[33,2,'tl','w'],[38,4,'tl','w'],[30,9,'full','y']] };
  var ladyPatches = [[24,1,'full','5'],[27,2,'full','5'],[21,3,'full','5']];
  [35,37,39,41].forEach(function(cx) { for (var cy = 0; cy < 5; cy++) ladyPatches.push([cx,cy,'full','7']); });
  var ladyOpts = { dark: { d: '1', s: '4', g: '5', w: '2', y: '7' }, light: { d: '3', g: '6' }, patches: ladyPatches };
  _catSprites = {
    runA: catDetail(CAT_RUN_A, runOpts), runB: catDetail(CAT_RUN_B, runOpts),
    waveA: catDetail(CAT_WAVE_A, waveOpts), waveB: catDetail(CAT_WAVE_B, waveOpts),
    ratA: catDetail(RAT_A, ratOpts), ratB: catDetail(RAT_B, ratOpts),
    dogA: catDetail(DOG_A, dogOpts), dogB: catDetail(DOG_B, dogOpts),
    ladyA: catDetail(LADY_A, ladyOpts), ladyB: catDetail(LADY_B, ladyOpts)
  };
  return _catSprites;
}
function catSvgWrap(inner, vwb, vhb, w, h, cls) {
  return '<svg class="' + cls + '" viewBox="0 0 ' + vwb + ' ' + vhb + '" width="' + w + '" height="' + h + '" shape-rendering="crispEdges">' + inner + '</svg>';
}
function catTwoFrames(fn0, fn1, extra) {
  return fn0('catfr' + (extra || '')) + fn1('catfr f2' + (extra || ''));
}
function catHtml(kind, fur, fast) {
  var S = catSprites();
  var mk = function(rows, dx) { return function(cls) { return catSvgWrap(catAsciiRects(rows, CAT_PAL, fur, dx), 66, 42, 99, 63, cls); }; };
  if (kind === 'w') return catTwoFrames(mk(S.waveA, 8), mk(S.waveB, 8), ' wv');
  return catTwoFrames(mk(S.runA), mk(S.runB), fast ? ' fast' : '');
}
function catRatHtml() {
  var S = catSprites();
  var mk = function(rows) { return function(cls) { return catSvgWrap(catAsciiRects(rows, RAT_PAL, RAT_PAL.f), 54, 26, 81, 39, cls); }; };
  return catTwoFrames(mk(S.ratA), mk(S.ratB), ' fast');
}
function catDogHtml() {
  var S = catSprites();
  var mk = function(rows) { return function(cls) { return catSvgWrap(catAsciiRects(rows, DOG_PAL, DOG_PAL.f), 82, 46, 123, 69, cls); }; };
  return catTwoFrames(mk(S.dogA), mk(S.dogB), ' fast');
}
function catLadyHtml() {
  var S = catSprites();
  var mk = function(rows) { return function(cls) { return catSvgWrap(catAsciiRects(rows, LADY_PAL, LADY_PAL.d), 90, 66, 180, 132, cls); }; };
  return catTwoFrames(mk(S.ladyA), mk(S.ladyB), ' wv');
}
function catSpawn(html, w, h, x0, flip) {
  var el = document.createElement('div');
  el.style.cssText = 'position:fixed;bottom:4px;left:0;width:' + w + 'px;height:' + h + 'px;z-index:99999;pointer-events:none;will-change:transform;';
  el.style.transform = 'translateX(' + x0 + 'px)' + flip;
  el.innerHTML = html;
  document.body.appendChild(el);
  el.getBoundingClientRect();
  return el;
}
function catGo(el, x, ms, flip) {
  el.style.transition = 'transform ' + ms + 'ms linear';
  el.style.transform = 'translateX(' + x + 'px)' + flip;
}
function catEnsureStyle() {
  if (document.getElementById('cat-style')) return;
  var st = document.createElement('style');
  st.id = 'cat-style';
  st.textContent =
    '@keyframes catblink { 0%, 49.9% { opacity: 1; } 50%, 100% { opacity: 0; } }' +
    '.catfr { position: absolute; inset: 0; animation: catblink 0.32s steps(1) infinite; }' +
    '.catfr.f2 { animation-delay: -0.16s; }' +
    '.catfr.wv { animation-duration: 0.5s; }' +
    '.catfr.f2.wv { animation-delay: -0.25s; }' +
    '.catfr.fast { animation-duration: 0.2s; }' +
    '.catfr.f2.fast { animation-delay: -0.1s; }' +
    '.catbub { position: fixed; z-index: 99999; pointer-events: none; background: #fff; color: #111; border: 2px solid #111; border-radius: 7px; font: 600 14px "DM Sans", system-ui, sans-serif; padding: 4px 10px; }' +
    '.catbub:after { content: ""; position: absolute; left: 14px; bottom: -7px; width: 9px; height: 9px; background: #fff; border-right: 2px solid #111; border-bottom: 2px solid #111; transform: rotate(45deg); }';
  document.head.appendChild(st);
}
function catRun() {
  try {
    catEnsureStyle();
    var fur = ['#64748B', '#D97706', '#9CA3AF', '#8B5A2B'][Math.floor(Math.random() * 4)];
    var vw = window.innerWidth;
    var lr = Math.random() < 0.5;
    var flip = lr ? '' : ' scaleX(-1)';
    var enter = function(w) { return lr ? -w - 20 : vw + 20; };
    var exit = function(w) { return lr ? vw + 20 : -w - 20; };
    var mid = Math.round(vw / 2 - 50);
    var d1 = 2600, hold = 2600;
    var cat = catSpawn(catHtml('r', fur), 99, 63, enter(99), flip);
    catGo(cat, mid, d1, flip);
    var actors = [cat], bub = null, exc = null;
    setTimeout(function() {
      try {
        cat.innerHTML = catHtml('w', fur);
        bub = document.createElement('div');
        bub.className = 'catbub';
        bub.textContent = 'Hola';
        bub.style.left = (mid + (lr ? 55 : -8)) + 'px';
        bub.style.bottom = '75px';
        document.body.appendChild(bub);
      } catch (e) {}
    }, d1 + 60);
    setTimeout(function() {
      try {
        var rat = catSpawn(catRatHtml(), 81, 39, enter(81), flip);
        actors.push(rat);
        catGo(rat, exit(81), 2600, flip);
      } catch (e) {}
    }, d1 + hold - 600);
    setTimeout(function() {
      try {
        if (bub) { bub.remove(); bub = null; }
        exc = document.createElement('div');
        exc.textContent = '!';
        exc.style.cssText = 'position:fixed;z-index:99999;pointer-events:none;left:' + (mid + (lr ? 55 : 30)) + 'px;bottom:75px;font:800 30px "DM Sans", system-ui, sans-serif;color:#FBBF24;text-shadow:2px 0 #111,-2px 0 #111,0 2px #111,0 -2px #111;';
        document.body.appendChild(exc);
      } catch (e) {}
    }, d1 + hold + 900);
    setTimeout(function() {
      try {
        if (exc) { exc.remove(); exc = null; }
        cat.innerHTML = catHtml('r', fur, true);
        catGo(cat, exit(99), 1900, flip);
      } catch (e) {}
    }, d1 + hold + 1800);
    setTimeout(function() {
      try {
        var dog = catSpawn(catDogHtml(), 123, 69, enter(123), flip);
        actors.push(dog);
        catGo(dog, exit(123), 2600, flip);
      } catch (e) {}
    }, d1 + hold + 2300);
    setTimeout(function() {
      try {
        var lady = catSpawn(catLadyHtml(), 180, 132, enter(180), flip);
        actors.push(lady);
        catGo(lady, exit(180), 4200, flip);
      } catch (e) {}
    }, d1 + hold + 2950);
    setTimeout(function() {
      try {
        actors.forEach(function(a) { try { a.remove(); } catch (e) {} });
        if (bub) bub.remove();
        if (exc) exc.remove();
      } catch (e) {}
    }, d1 + hold + 2950 + 4200 + 700);
  } catch (e) {}
}
function startCatClock() {
  var check = function() {
    try {
      if (document.hidden) return;
      var last = parseInt(localStorage.getItem('cat_last_run') || '0', 10);
      var now = Date.now();
      if (now - last < 3600 * 1000) return;
      // Claim the hour BEFORE the async auth probe (multi-tab dedupe), then
      // release it if this tab turns out to be logged out (login screen) —
      // the parade is for authenticated users only.
      localStorage.setItem('cat_last_run', String(now));
      fetch('/api/auth/me', { credentials: 'include' })
        .then(function(r) {
          if (r.ok) hourlyScene()();
          else localStorage.setItem('cat_last_run', String(last));
        })
        .catch(function() { try { localStorage.setItem('cat_last_run', String(last)); } catch (e) {} });
    } catch (e) {}
  };
  setInterval(check, 60000 + Math.floor(Math.random() * 5000));
  setTimeout(check, 15000 + Math.floor(Math.random() * 30000));
}
window.catRun = catRun;
try { if (typeof document !== 'undefined' && typeof localStorage !== 'undefined') startCatClock(); } catch (e) {}

// ─── FORKLIFT FUMBLE (the hourly cartoon until 2026-10-05; now a manual demo) ───
// A yellow forklift hauls a wobbling pallet of boxes in, stops at center, the
// top box slides off ("!"), the driver panics and reverses back out the way
// it came ("beep! beep!") — a warehouse worker sprints in chasing it and
// scoops the dropped box mid-run without breaking stride, then a portly
// supervisor waving paperwork storms after them both. Reuses the cat rig
// (catSpawn/catGo/catTwoFrames, catbub styles). Redrawn 2026-09-04 (Anthony:
// "same art style as the agents office") as smooth vector art — rounded flat
// shapes, The Office's palette, soft ground shadows — instead of the pixel
// sprite grids. Geometry stays in the old sprite's unit space (lift 126×70
// at 1.5 px/unit, worker 58×48, supervisor 62×50, box 24×14) so
// forkliftRun's drop/scoop math is unchanged: the front box stack sits at
// x 94-116 and box i occupies y [40-12i, 52-12i). Hard-hat color is random
// per run. window.forkliftRun() = demo.
var FORK_PAL = { h: '#F1F5F9' }; // driver's hard hat, randomised per run
var FORK_SCALE = 1.25; // Anthony 2026-09-04: "increase their size a little bit" — every px size and offset reads this
function fkPx(n) { return Math.round(n * FORK_SCALE); }
var FK = {
  body: '#EAB308', bodyDk: '#CA8A04', cage: '#2A333F', seat: '#39445A', mast: '#56617A', steel: '#8A94A2',
  skin: '#E8B98A', hair: '#4A3B2F', tire: '#0B0F16', hub: '#8A94A2', beacon: '#FBBF24',
  wood: '#B08968', woodDk: '#8F6B4E', tape: '#F1F5F9', vest: '#F97316', stripe: '#F1F5F9',
  pantsA: '#2A4879', pantsB: '#31548F', shirt: '#4C5A70', hat: '#FBBF24',
  suit: '#2E3950', shirtW: '#E5E7EB', tie: '#B91C1C', paper: '#F8FAFC', ink: '#94A3B8',
  shadow: 'rgba(0,0,0,0.28)', eye: '#1F2937',
};
function forkSvgWrap(inner, vwb, vhb, w, h, cls) {
  return '<svg class="' + cls + '" viewBox="0 0 ' + vwb + ' ' + vhb + '" width="' + w + '" height="' + h + '">' + inner + '</svg>';
}
function fkRect(x, y, w, h, r, fill, extra) {
  return '<rect x="' + x + '" y="' + y + '" width="' + w + '" height="' + h + '" rx="' + r + '" fill="' + fill + '"' + (extra || '') + '/>';
}
function fkCircle(cx, cy, r, fill, extra) { return '<circle cx="' + cx + '" cy="' + cy + '" r="' + r + '" fill="' + fill + '"' + (extra || '') + '/>'; }
function fkEllipse(cx, cy, rx, ry, fill) { return '<ellipse cx="' + cx + '" cy="' + cy + '" rx="' + rx + '" ry="' + ry + '" fill="' + fill + '"/>'; }
function fkG(inner, transform) { return '<g' + (transform ? ' transform="' + transform + '"' : '') + '>' + inner + '</g>'; }
// Cardboard box: wood-toned carton, darker top edge, a tape stripe, a label.
function fkBox(x, y, w, h) {
  return fkRect(x, y, w, h, 2, FK.wood) + fkRect(x, y, w, 2.4, 1.2, FK.woodDk) +
    fkRect(x + w / 2 - 1.3, y, 2.6, h, 0.9, FK.tape) + fkRect(x + 2.5, y + h - 4.5, 6, 2.6, 0.8, FK.tape, ' opacity="0.7"');
}
// Head with short hair and a hard hat (color per actor), facing right.
function fkHead(cx, cy, hat) {
  return fkCircle(cx, cy, 6.5, FK.skin) +
    '<path d="M ' + (cx - 6.5) + ',' + (cy - 1) + ' Q ' + (cx - 6) + ',' + (cy - 8) + ' ' + cx + ',' + (cy - 7.5) +
    ' Q ' + (cx + 6) + ',' + (cy - 8) + ' ' + (cx + 6.5) + ',' + (cy - 1) + ' L ' + (cx + 4.5) + ',' + (cy - 1) +
    ' Q ' + (cx + 3) + ',' + (cy - 5) + ' ' + cx + ',' + (cy - 5) + ' Q ' + (cx - 3) + ',' + (cy - 5) + ' ' + (cx - 4.5) + ',' + (cy - 1) + ' Z" fill="' + FK.hair + '"/>' +
    fkCircle(cx + 3, cy - 0.5, 1, FK.eye) +
    fkRect(cx - 7.5, cy - 12, 15, 6.5, 3.2, hat) + fkRect(cx - 9, cy - 7, 18.5, 2.2, 1.1, hat);
}
// The lift itself. mode 'run' (top front box leans on frame B) | 'wob'
// (stacks teeter). hts = [rearHeight, frontHeight] in boxes (0-4 each).
var FORK_STACK_X = [70, 94];
function forkliftFrame(mode, hts, which) {
  var B = which === 'B';
  var k = [];
  k.push(fkEllipse(62, 68.5, 50, 3, FK.shadow));
  // rear roll-cage post (behind the driver), roof, beacon
  k.push(fkRect(13, 3, 3, 40, 1.5, FK.cage));
  k.push(fkRect(11, 1.5, 44, 4, 2, FK.cage));
  k.push(fkRect(29.5, 0, 6, 2.6, 1.3, FK.beacon));
  // seat
  k.push(fkRect(19, 28, 14, 13, 3.5, FK.seat));
  // driver (bobs one unit on frame B)
  var dy = B ? -1 : 0;
  var driver =
    fkRect(33, 37, 13, 5, 2.5, FK.pantsA) + fkRect(42, 37, 5, 9, 2.2, FK.pantsB) +
    fkRect(22, 19, 14, 20, 6, FK.shirt) + fkRect(24, 20, 10, 15, 4.5, FK.vest) + fkRect(24, 27, 10, 2, 1, FK.stripe) +
    fkRect(33, 24, 13, 4.2, 2.1, FK.shirt, ' transform="rotate(12 33 26)"') + fkCircle(46, 27.5, 2.4, FK.skin) +
    fkHead(29, 13, FORK_PAL.h);
  k.push(fkG(driver, dy ? 'translate(0,' + dy + ')' : ''));
  // steering column + wheel
  k.push(fkRect(46, 27, 3, 12, 1.5, FK.cage));
  k.push('<circle cx="47.5" cy="27" r="4" fill="none" stroke="' + FK.cage + '" stroke-width="2"/>');
  // body: main deck, counterweight block, lower skirt, front bumper
  k.push(fkRect(8, 40, 58, 20, 6, FK.body));
  k.push(fkRect(8, 40, 12, 20, 6, FK.bodyDk));
  k.push(fkRect(8, 52, 58, 8, 4, FK.bodyDk, ' opacity="0.55"'));
  k.push(fkRect(60, 44, 8, 12, 3, FK.cage));
  // front roll-cage post (in front of the driver)
  k.push(fkRect(49, 3, 3, 38, 1.5, FK.cage));
  // wheels: tires, hubs, spokes (spokes turn 45° between frames)
  [[24, 61], [56, 61]].forEach(function(w) {
    k.push(fkCircle(w[0], w[1], 9, FK.tire));
    k.push(fkCircle(w[0], w[1], 3.6, FK.hub));
    var rot = B ? 45 : 0;
    k.push('<g transform="rotate(' + rot + ' ' + w[0] + ' ' + w[1] + ')">' +
      fkRect(w[0] - 6.5, w[1] - 0.9, 13, 1.8, 0.9, FK.hub) + fkRect(w[0] - 0.9, w[1] - 6.5, 1.8, 13, 0.9, FK.hub) + '</g>');
  });
  // mast rails + crossbars, carriage, fork tines
  k.push(fkRect(62, 4, 3, 58, 1.5, FK.mast) + fkRect(68, 4, 3, 58, 1.5, FK.mast));
  [12, 30, 46].forEach(function(y) { k.push(fkRect(62, y, 9, 2, 1, FK.steel)); });
  k.push(fkRect(60, 50, 12, 9, 2.5, FK.cage));
  k.push(fkRect(64, 57, 58, 3, 1.5, FK.steel));
  // pallet: deck on the forks, three blocks beneath
  k.push(fkRect(68, 52, 52, 4.5, 1.2, FK.wood));
  [70, 92, 114].forEach(function(x) { k.push(fkRect(x, 56.5, 6, 3.5, 1, FK.woodDk)); });
  // box stacks (bottom box on the deck; box i at y 40-12i)
  hts.forEach(function(hgt, s) {
    for (var i = 0; i < hgt; i++) {
      var off = 0;
      if (mode === 'wob') off = !B ? Math.min(i, 2) * 2 : (i === 0 ? 0 : -2);
      else if (B && i === hgt - 1 && s === 1) off = 2;
      k.push(fkBox(FORK_STACK_X[s] + off, 40 - 12 * i, 22, 12));
    }
  });
  return k.join('');
}
function forkliftHtml(mode, hts, fast) {
  var mk = function(which) { return function(cls) { return forkSvgWrap(forkliftFrame(mode, hts, which), 126, 70, fkPx(189), fkPx(105), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), fast ? ' fast' : '');
}
// Warehouse worker (58×48), hi-vis vest and hard hat, sprinting right;
// carry frames hold the rescued box out front.
function forkWorkerFrame(carry, which) {
  var B = which === 'B';
  var k = [];
  k.push(fkEllipse(30, 47, 15, 2.5, FK.shadow));
  // legs: a running stride that swaps each frame
  k.push(fkRect(24, 33, 6, 14, 3, FK.pantsA, ' transform="rotate(' + (B ? 22 : -22) + ' 27 34)"'));
  k.push(fkRect(30, 33, 6, 14, 3, FK.pantsB, ' transform="rotate(' + (B ? -22 : 22) + ' 33 34)"'));
  // torso + vest + stripe
  k.push(fkRect(21, 14, 17, 21, 7, FK.shirt));
  k.push(fkRect(23, 15, 13, 16, 5, FK.vest) + fkRect(23, 22, 13, 2.2, 1.1, FK.stripe));
  if (carry) {
    k.push(fkRect(33, 19, 11, 4.4, 2.2, FK.shirt) + fkRect(33, 25, 11, 4.4, 2.2, FK.shirt));
    k.push(fkBox(36, 15, 21, 12));
    k.push(fkCircle(44, 21.2, 2.3, FK.skin) + fkCircle(44, 27.2, 2.3, FK.skin));
  } else {
    k.push(fkRect(34, 17, 12, 4.4, 2.2, FK.shirt, ' transform="rotate(' + (B ? 30 : -28) + ' 35 19)"'));
    k.push(fkRect(13, 17, 12, 4.4, 2.2, FK.shirt, ' transform="rotate(' + (B ? -30 : 28) + ' 24 19)"'));
  }
  k.push(fkHead(29.5, 9.5, FK.hat));
  return k.join('');
}
function forkWorkerHtml(carry) {
  var mk = function(which) { return function(cls) { return forkSvgWrap(forkWorkerFrame(carry, which), 58, 48, fkPx(87), fkPx(72), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), ' fast');
}
// Portly supervisor (62×50): dark suit, white shirt, red tie, waving a sheet
// of paper overhead — a slower two-frame stomp.
function forkSupFrame(which) {
  var B = which === 'B';
  var k = [];
  k.push(fkEllipse(31, 49, 17, 2.6, FK.shadow));
  k.push(fkRect(24, 35, 7, 13, 3.5, FK.suit, ' transform="rotate(' + (B ? 14 : -14) + ' 27 36)"'));
  k.push(fkRect(32, 35, 7, 13, 3.5, FK.suit, ' transform="rotate(' + (B ? -14 : 14) + ' 35 36)"'));
  k.push(fkRect(17, 14, 28, 25, 12, FK.suit)); // the belly
  k.push(fkRect(27, 15, 9, 17, 3.5, FK.shirtW));
  k.push(fkRect(30, 16, 3, 12, 1.2, FK.tie));
  // waving arm + paper (higher on frame A)
  var ang = B ? 40 : 15;
  k.push(fkRect(41, 6, 4.4, 15, 2.2, FK.suit, ' transform="rotate(' + ang + ' 43 20)"'));
  var px = B ? 50 : 44, py = B ? 8 : 1.5;
  k.push(fkRect(px, py, 9, 11, 1.2, FK.paper) + fkRect(px + 1.5, py + 2.5, 6, 1.1, 0.5, FK.ink) +
    fkRect(px + 1.5, py + 5, 6, 1.1, 0.5, FK.ink) + fkRect(px + 1.5, py + 7.5, 4, 1.1, 0.5, FK.ink));
  // other arm pumping
  k.push(fkRect(12, 20, 11, 4.4, 2.2, FK.suit, ' transform="rotate(' + (B ? -20 : 20) + ' 20 22)"'));
  // head: bald on top, hair at the sides, glasses
  k.push(fkCircle(31, 9, 6.8, FK.skin));
  k.push(fkRect(24.5, 7, 3.2, 5, 1.6, FK.hair) + fkRect(34.3, 7, 3.2, 5, 1.6, FK.hair));
  k.push('<circle cx="34" cy="9" r="2.2" fill="none" stroke="' + FK.eye + '" stroke-width="0.9"/>' + fkCircle(34.6, 9, 0.9, FK.eye));
  return k.join('');
}
function forkSupHtml() {
  var mk = function(which) { return function(cls) { return forkSvgWrap(forkSupFrame(which), 62, 50, fkPx(93), fkPx(75), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), ' wv');
}
function forkBoxHtml() {
  return forkSvgWrap(fkBox(1, 1, 22, 12), 24, 14, fkPx(36), fkPx(21), '');
}
function forkliftRun() {
  try {
    catEnsureStyle();
    // Rear stack is always 4 boxes; front stack 3 or 4. The FRONT stack's top
    // box is the one that falls.
    var hf = Math.random() < 0.5 ? 4 : 3;
    FORK_PAL.h = ['#F1F5F9', '#FBBF24', '#3B82F6'][Math.floor(Math.random() * 3)];
    var vw = window.innerWidth;
    var lr = Math.random() < 0.5;
    var flip = lr ? '' : ' scaleX(-1)';
    var chaseFlip = lr ? ' scaleX(-1)' : '';
    // Actor boxes in page px (unit sizes × FORK_SCALE)
    var LW = fkPx(189), LH = fkPx(105), WW = fkPx(87), WH = fkPx(72), SW = fkPx(93), SH = fkPx(75), BW = fkPx(36), BH = fkPx(21);
    // The forklift enters from one side and later REVERSES back out the same
    // side; the chasers enter from the opposite side and exit where it fled.
    var enter = function(w) { return lr ? -w - 20 : vw + 20; };
    var chaseEnter = function(w) { return lr ? vw + 20 : -w - 20; };
    var mid = Math.round(vw / 2 - fkPx(95));
    // Pacing (Anthony 2026-09-04, "slow them down"): travels ~40% longer than
    // the pixel version, and the beep sounds BEFORE the lift starts reversing.
    var d1 = 3600, dRev = 2700, dWorker = 3600, dSup = 5800;
    var tWob = d1 + 60, tDrop = d1 + 900, tBeep = d1 + 1700, tRev = d1 + 2300, tBeepOff = d1 + 3300, tWorker = d1 + 2600, tSup = d1 + 3400;
    var lift = catSpawn(forkliftHtml('run', [4, hf]), LW, LH, enter(LW), flip);
    catGo(lift, mid, d1, flip);
    var actors = [lift], bub = null, exc = null, boxEl = null;
    setTimeout(function() {
      try { lift.innerHTML = forkliftHtml('wob', [4, hf]); } catch (e) {}
    }, tWob);
    setTimeout(function() {
      try {
        exc = document.createElement('div');
        exc.textContent = '!';
        exc.style.cssText = 'position:fixed;z-index:99999;pointer-events:none;left:' + (mid + fkPx(lr ? 52 : 121)) + 'px;bottom:' + fkPx(112) + 'px;font:800 ' + fkPx(30) + 'px "DM Sans", system-ui, sans-serif;color:#FBBF24;text-shadow:2px 0 #111,-2px 0 #111,0 2px #111,0 -2px #111;';
        document.body.appendChild(exc);
        var spawnBottom = fkPx(hf === 4 ? 81 : 63);
        var fall = spawnBottom - fkPx(6);
        boxEl = document.createElement('div');
        boxEl.style.cssText = 'position:fixed;bottom:' + spawnBottom + 'px;left:' + (mid + fkPx(lr ? 142 : 11)) + 'px;width:' + BW + 'px;height:' + BH + 'px;z-index:99999;pointer-events:none;will-change:transform;';
        boxEl.innerHTML = forkBoxHtml();
        document.body.appendChild(boxEl);
        boxEl.getBoundingClientRect();
        boxEl.style.transition = 'transform 340ms cubic-bezier(0.5,0,1,1)';
        boxEl.style.transform = 'translateY(' + fall + 'px)';
        setTimeout(function() { try { if (boxEl) { boxEl.style.transition = 'transform 110ms ease-out'; boxEl.style.transform = 'translateY(' + (fall - fkPx(8)) + 'px)'; } } catch (e) {} }, 360);
        setTimeout(function() { try { if (boxEl) { boxEl.style.transition = 'transform 110ms ease-in'; boxEl.style.transform = 'translateY(' + fall + 'px)'; } } catch (e) {} }, 480);
        lift.innerHTML = forkliftHtml('wob', [4, hf - 1]);
      } catch (e) {}
    }, tDrop);
    // "beep! beep!" — the driver notices, THEN backs out
    setTimeout(function() {
      try {
        if (exc) { exc.remove(); exc = null; }
        bub = document.createElement('div');
        bub.className = 'catbub';
        bub.textContent = 'beep! beep!';
        bub.style.left = (mid + fkPx(lr ? 44 : 113)) + 'px';
        bub.style.bottom = fkPx(116) + 'px';
        document.body.appendChild(bub);
      } catch (e) {}
    }, tBeep);
    setTimeout(function() {
      try {
        lift.innerHTML = forkliftHtml('run', [4, hf - 1], true);
        catGo(lift, enter(LW), dRev, flip);
      } catch (e) {}
    }, tRev);
    setTimeout(function() { try { if (bub) { bub.remove(); bub = null; } } catch (e) {} }, tBeepOff);
    setTimeout(function() {
      try {
        var worker = catSpawn(forkWorkerHtml(false), WW, WH, chaseEnter(WW), chaseFlip);
        actors.push(worker);
        catGo(worker, enter(WW), dWorker, chaseFlip);
        // Scoop the dropped box the moment the worker's leading edge reaches
        // it: swap to the carrying frames and remove the ground box.
        var bx = mid + fkPx(lr ? 142 : 11);
        var wx0 = chaseEnter(WW), wx1 = enter(WW);
        var grabX = lr ? bx + fkPx(30) : bx - fkPx(81);
        var frac = (grabX - wx0) / (wx1 - wx0);
        var scoopMs = Math.max(200, Math.min(dWorker - 200, Math.round(dWorker * frac)));
        setTimeout(function() {
          try {
            if (boxEl) { boxEl.remove(); boxEl = null; }
            worker.innerHTML = forkWorkerHtml(true);
          } catch (e) {}
        }, scoopMs);
      } catch (e) {}
    }, tWorker);
    setTimeout(function() {
      try {
        var sup = catSpawn(forkSupHtml(), SW, SH, chaseEnter(SW), chaseFlip);
        actors.push(sup);
        catGo(sup, enter(SW), dSup, chaseFlip);
      } catch (e) {}
    }, tSup);
    setTimeout(function() {
      try {
        actors.forEach(function(a) { try { a.remove(); } catch (e) {} });
        if (bub) bub.remove();
        if (exc) exc.remove();
        if (boxEl) boxEl.remove();
      } catch (e) {}
    }, tSup + dSup + 700);
  } catch (e) {}
}
window.forkliftRun = forkliftRun;

// ─── INVOICE STORM (the hourly cartoon) ───
// A gust of wind blows a flurry of invoices along the bottom of the page.
// Danny, the invoice-filing agent from The Office (his desk look: yellow
// shirt, cropped hair, beard), sprints in after them with an open folder
// ("my invoices!") and snaps them up one at a time. The last sheet slaps onto
// his face (no bubble: Anthony 2026-10-05 dropped the "mmf!") and he staggers
// off blind. One invoice fluttered to the
// floor behind him: the Filing floor's gray cat creeps up, wiggles, pounces
// and trots off with it. Took over the hourly clock 2026-10-05 (Anthony's
// pick); forkliftRun() and catRun() stay as manual demos. Same rig (catbub
// bubbles, catTwoFrames) and the forklift's vector helpers. Positions are
// planned along the wind: s runs from the upwind edge, and X(s, w) mirrors it
// when the wind blows right-to-left. window.stormRun() = demo.
var STORM_SCALE = 1.25;
function stPx(n) { return Math.round(n * STORM_SCALE); }
var ST = {
  shirt: '#EAB308', shirtDk: '#CA8A04', skin: '#9C6644', hair: '#1F1A17', // Danny as officeLook() draws 'invoice-filing'
  cat: '#8A94A2', // officePetFor('Filing')
  manila: '#E2B04A', manilaDk: '#C9952F', paper: '#FFFFFF', edge: '#64748B', line: '#CBD5E1', ink: '#475569', gust: '#94A3B8',
  accents: ['#14B8A6', '#3B82F6', '#F43F5E', '#A855F7', '#F59E0B', '#10B981', '#6366F1'],
};
function stCrescent(cx, cy, ro, ri, a0, a1) { // officeCrescent: filled band between two radii (degrees, 270 = top)
  var P = function(rad, a) { var t = a * Math.PI / 180; return (cx + Math.cos(t) * rad).toFixed(2) + ',' + (cy + Math.sin(t) * rad).toFixed(2); };
  var large = Math.abs(a1 - a0) > 180 ? 1 : 0;
  return 'M ' + P(ro, a0) + ' A ' + ro + ',' + ro + ' 0 ' + large + ' 1 ' + P(ro, a1) + ' L ' + P(ri, a1) + ' A ' + ri + ',' + ri + ' 0 ' + large + ' 0 ' + P(ri, a0) + ' Z';
}
// Danny's head at the Office walker scale (r 11.5): eye, beard, cropped cap
function stDannyHead(cx, cy) {
  var r = 11.5;
  return fkCircle(cx, cy, r, ST.skin) + fkCircle(cx + 0.5 * r, cy - 0.05 * r, 0.14 * r, FK.eye) +
    '<path d="M ' + (cx - 0.3 * r) + ',' + (cy + 0.55 * r) + ' A ' + r + ',' + r + ' 0 0 0 ' + (cx + 0.9 * r) + ',' + (cy + 0.35 * r) +
    '" stroke="' + ST.hair + '" stroke-width="' + (0.3 * r) + '" fill="none" stroke-linecap="round"/>' +
    '<path d="' + stCrescent(cx, cy, 1.03 * r, 0.82 * r, 188, 352) + '" fill="' + ST.hair + '"/>';
}
// One invoice, 18×23: vendor color block, line items, bold total
function stInvoice(accent) {
  return fkRect(0, 0, 18, 23, 1.5, ST.paper, ' stroke="' + ST.edge + '" stroke-width="0.9"') +
    fkRect(2.2, 2.2, 7, 2.6, 0.7, accent) + fkRect(12, 2.6, 3.8, 1.4, 0.6, ST.line) +
    fkRect(2.2, 7.5, 13.6, 1.3, 0.6, ST.line) + fkRect(2.2, 10.4, 10.5, 1.3, 0.6, ST.line) +
    fkRect(2.2, 13.3, 13.6, 1.3, 0.6, ST.line) + fkRect(2.2, 16.2, 8, 1.3, 0.6, ST.line) +
    fkRect(10.2, 19.2, 5.6, 1.9, 0.7, ST.ink);
}
// Open folder held out front like a scoop, tipped forward: tall back panel
// with its tab, shorter front panel, k caught sheets standing up between.
function stFolderOpen(k) {
  var s = [fkRect(21, -68, 18, 25, 1.8, ST.manila) + fkRect(22, -71, 7, 4.5, 1.4, ST.manila)];
  for (var i = 0; i < k; i++) s.push(fkRect(23 + (i % 3) * 2.2, -67 + (i % 2) * 1.6 - Math.floor(i / 2) * 1.4, 13, 9, 0.9, ST.paper, ' stroke="' + ST.edge + '" stroke-width="0.7" transform="rotate(' + (-8 + i * 4) + ' 29 -62)"'));
  s.push('<path d="M 20.5,-58 L 39.5,-60 L 39.5,-43.8 Q 39.5,-42 37.7,-42 L 22.3,-42 Q 20.5,-42 20.5,-43.8 Z" fill="' + ST.manilaDk + '"/>');
  return fkG(s.join(''), 'rotate(16 26 -42)');
}
// Closed folder gripped at (hx, hy), k sheets peeking out of the top
function stFolderClosed(k, hx, hy, rot) {
  var s = [];
  for (var i = 0; i < k; i++) s.push(fkRect(2 + i * 1.6, -21 - (i % 2) * 1.2, 9, 6, 0.8, ST.paper, ' stroke="' + ST.edge + '" stroke-width="0.6"'));
  s.push(fkRect(0, -17, 15, 18, 1.5, ST.manila) + fkRect(9, -19, 5, 3, 1, ST.manila));
  return fkG(s.join(''), 'translate(' + (hx - 3) + ',' + (hy + 8) + ') rotate(' + rot + ' 3 -8)');
}
// Danny (84×100, feet at 30,97) sprinting, leaning in, open folder out front
function stDannyRun(k, which) {
  var B = which === 'B';
  var p = [fkEllipse(0, 1, 17, 2.6, FK.shadow),
    fkRect(-4, -28, 8, 28, 4, FK.pantsA, ' transform="rotate(' + (B ? 30 : -30) + ' 0 -26)"'),
    fkRect(-4, -28, 8, 28, 4, FK.pantsB, ' transform="rotate(' + (B ? -30 : 30) + ' 0 -26)"')];
  var u = [fkRect(-2, -54, 28, 7.5, 3.75, ST.shirtDk, ' transform="rotate(6 -1 -50)"'), // far arm
    fkRect(-13, -58, 26, 36, 10, ST.shirt),
    fkRect(-1, -52, 28, 8, 4, ST.shirt, ' transform="rotate(12 0 -48)"'), // near arm
    stDannyHead(0, -68), stFolderOpen(k),
    fkCircle(25.5, -47, 3.6, ST.skin) + fkCircle(26.5, -42.5, 3.6, ST.skin)];
  p.push(fkG(u.join(''), 'translate(0,' + (B ? -1.5 : 0) + ') rotate(9 0 -28)'));
  return fkG(p.join(''), 'translate(30,97)');
}
// Danny blinded: the last invoice plastered over his face, staggering, one
// arm flailing overhead, the closed folder dangling from the other
function stDannySlap(k, which) {
  var B = which === 'B';
  var p = [fkEllipse(0, 1, 17, 2.6, FK.shadow),
    fkRect(-4, -28, 8, 28, 4, FK.pantsA, ' transform="rotate(' + (B ? -10 : 14) + ' 0 -26)"'),
    fkRect(-4, -28, 8, 28, 4, FK.pantsB, ' transform="rotate(' + (B ? 16 : -8) + ' 0 -26)"')];
  var arm = function(ang, fill, sy) { return fkRect(-2, sy - 3.75, 27, 7.5, 3.75, fill, ' transform="rotate(' + ang + ' -1 ' + sy + ')"'); };
  var hand = function(ang, sy) { var t = ang * Math.PI / 180; return [-1 + Math.cos(t) * 26, sy + Math.sin(t) * 26]; };
  var farA = B ? -150 : -125, nearA = B ? 25 : 10;
  var fh = hand(farA, -51), nh = hand(nearA, -49);
  var u = [arm(farA, ST.shirtDk, -51), fkCircle(fh[0], fh[1], 3.6, ST.skin),
    fkRect(-13, -58, 26, 36, 10, ST.shirt), stDannyHead(0, -68),
    fkG(stInvoice(ST.accents[6]), 'translate(-1,-79) rotate(' + (B ? 6 : -6) + ' 9 11.5)'),
    arm(nearA, ST.shirt, -49), stFolderClosed(k, nh[0], nh[1], B ? 18 : -10), fkCircle(nh[0], nh[1], 3.6, ST.skin)];
  p.push(fkG(u.join(''), 'translate(0,' + (B ? -1 : 0) + ') rotate(-7 0 -28)'));
  return fkG(p.join(''), 'translate(30,97)');
}
// The Filing floor's gray cat (officeCatFig's shape, 58×44, feet at 26,41):
// walk, carry (the rescued invoice in its mouth), crouch (butt wiggle), leap
function stCatEars(dx, dy) {
  return '<path d="M ' + (6.5 + dx) + ',' + (-27 + dy) + ' L ' + (8 + dx) + ',' + (-33 + dy) + ' L ' + (11.5 + dx) + ',' + (-27.5 + dy) + ' Z" fill="' + ST.cat + '"/>' +
    '<path d="M ' + (12.5 + dx) + ',' + (-28 + dy) + ' L ' + (15.5 + dx) + ',' + (-33.5 + dy) + ' L ' + (17 + dx) + ',' + (-27 + dy) + ' Z" fill="' + ST.cat + '"/>';
}
function stCatFrame(pose, which) {
  var B = which === 'B', c = ST.cat, p = [];
  var tail = function(d) { return '<path d="' + d + '" stroke="' + c + '" stroke-width="4.5" fill="none" stroke-linecap="round"/>'; };
  if (pose === 'leap') {
    p.push(tail('M -14,-17 Q -21,-19 -25,-14'));
    p.push(fkRect(-19, -14, 11, 4.2, 2.1, c, ' transform="rotate(18 -9 -12)"'));
    p.push(fkRect(8, -17, 12, 4.2, 2.1, c, ' transform="rotate(22 9 -15)"'));
    p.push('<ellipse cx="-1" cy="-18" rx="15" ry="6.5" fill="' + c + '" transform="rotate(-8 -1 -18)"/>');
    p.push(fkCircle(13, -25, 7, c) + stCatEars(1, -3) + fkCircle(15.5, -25.5, 1.3, FK.eye));
  } else if (pose === 'crouch') {
    p.push(fkEllipse(0, 1.2, 15, 2.2, FK.shadow));
    p.push(tail(B ? 'M -13,-6 Q -24,-9 -21,-19' : 'M -13,-6 Q -21,-13 -15,-21'));
    p.push(fkG(fkRect(-12, -7, 5, 7, 2.5, c) + fkEllipse(-1, -9, 13.5, 6.5, c), 'rotate(' + (B ? -7 : -3) + ' 9 -5)'));
    p.push(fkRect(6, -4.5, 7, 4.5, 2.2, c));
    p.push(fkCircle(13, -13, 7, c) + stCatEars(1, 9) + fkCircle(15.5, -13.5, 1.4, FK.eye));
  } else {
    p.push(fkEllipse(0, 1.2, 15, 2.2, FK.shadow));
    p.push(tail(B ? 'M -13,-9 Q -22,-10 -22,-22' : 'M -13,-9 Q -21,-12 -19,-25'));
    p.push(fkRect(-10, -9, 4.5, 9.5, 2.2, c, ' transform="rotate(' + (B ? 22 : -22) + ' -7.75 -8)"'));
    p.push(fkRect(4, -9, 4.5, 9.5, 2.2, c, ' transform="rotate(' + (B ? -22 : 22) + ' 6.25 -8)"'));
    p.push(fkEllipse(-1, -14, 13, 7.5, c));
    p.push(fkCircle(12, -22, 7, c) + stCatEars(0, 0) + fkCircle(14.5, -22.5, 1.1, FK.eye));
    if (pose === 'carry') p.push(fkG(stInvoice(ST.accents[1]), 'translate(16,-25) rotate(24) scale(0.5)'));
  }
  return fkG(p.join(''), 'translate(26,41)');
}
function stDannyHtml(pose, k) {
  var mk = function(which) { return function(cls) { return forkSvgWrap(pose === 'slap' ? stDannySlap(k, which) : stDannyRun(k, which), 84, 100, stPx(84), stPx(100), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), pose === 'slap' ? ' wv' : ' fast');
}
function stCatHtml(pose) {
  var mk = function(which) { return function(cls) { return forkSvgWrap(stCatFrame(pose, which), 58, 44, stPx(58), stPx(44), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), pose === 'crouch' ? ' wv' : ' fast');
}
function stPaperSvg(accent) { return forkSvgWrap(fkG(stInvoice(accent), 'translate(1,1)'), 20, 25, stPx(28), stPx(35), ''); }
function stGustSvg() {
  return forkSvgWrap('<path d="M 4,24 C 40,16 78,30 114,19 C 128,14 138,6 130,3.5 C 122,1.5 120,11 127,13" stroke="' + ST.gust + '" stroke-width="2.6" fill="none" stroke-linecap="round"/>' +
    '<path d="M 26,32 C 52,27 80,34 104,28" stroke="' + ST.gust + '" stroke-width="2" fill="none" stroke-linecap="round" opacity="0.7"/>', 150, 36, stPx(150), stPx(36), 'stgust');
}
function stEnsureStyle() {
  catEnsureStyle();
  if (document.getElementById('storm-style')) return;
  var st = document.createElement('style');
  st.id = 'storm-style';
  st.textContent =
    '@keyframes stFlut { 0%, 100% { transform: translateY(0) rotate(-14deg) scaleX(1); } 25% { transform: translateY(-10px) rotate(5deg) scaleX(0.5); } 50% { transform: translateY(-3px) rotate(18deg) scaleX(0.95); } 75% { transform: translateY(-12px) rotate(2deg) scaleX(0.45); } }' +
    '.stfl { position: absolute; inset: 0; animation: stFlut 1.4s ease-in-out infinite; }' +
    '.stflat { position: absolute; inset: 0; transform-origin: 50% 100%; transform: rotate(-3deg) scaleY(0.42); }' +
    '@keyframes stGust { 0% { opacity: 0; } 15%, 75% { opacity: 0.9; } 100% { opacity: 0; } }' +
    '.stgust { animation: stGust 1.4s linear forwards; }';
  document.head.appendChild(st);
}
// Actors here move on both axes (papers rise and fall, the cat pounces), so
// they use translate(x, y) instead of catSpawn/catGo's translateX.
function stSpawn(html, w, h, bottom, x, y, flip) {
  var el = document.createElement('div');
  el.style.cssText = 'position:fixed;bottom:' + bottom + 'px;left:0;width:' + w + 'px;height:' + h + 'px;z-index:99999;pointer-events:none;will-change:transform;';
  el.style.transform = 'translate(' + x + 'px,' + y + 'px)' + (flip || '');
  el.innerHTML = html;
  document.body.appendChild(el);
  el.getBoundingClientRect();
  return el;
}
function stMove(el, x, y, ms, ease, flip) {
  el.style.transition = 'transform ' + ms + 'ms ' + (ease || 'linear');
  el.style.transform = 'translate(' + x + 'px,' + y + 'px)' + (flip || '');
}
function stBubble(text, x, bottom) {
  var b = document.createElement('div');
  b.className = 'catbub';
  b.textContent = text;
  b.style.left = '0px';
  b.style.bottom = bottom + 'px';
  b.style.transform = 'translateX(' + x + 'px)';
  document.body.appendChild(b);
  b.getBoundingClientRect();
  return b;
}
function stormRun() {
  try {
    stEnsureStyle();
    var vw = window.innerWidth;
    var lr = Math.random() < 0.5; // which way the wind blows
    var flip = lr ? '' : ' scaleX(-1)', back = lr ? ' scaleX(-1)' : '';
    var X = function(s, w) { return Math.round(lr ? s : vw - s - w); };
    var DW = stPx(84), DH = stPx(100), CW = stPx(58), CH = stPx(44), PW = stPx(28), PH = stPx(35), GW = stPx(150), GH = stPx(36);
    var els = [];
    var keep = function(el) { els.push(el); return el; };
    var drop = function(el) { try { if (el) el.remove(); } catch (e) {} };
    var at = function(ms, fn) { setTimeout(function() { try { fn(); } catch (e) {} }, ms); };
    var accents = ST.accents.slice().sort(function() { return Math.random() - 0.5; });

    // The gust: three wind streaks race across ahead of the paper
    [[0, 70], [180, 128], [380, 34]].forEach(function(g) {
      at(g[0], function() {
        var el = keep(stSpawn(stGustSvg(), GW, GH, g[1], X(-GW, GW), 0, flip));
        stMove(el, X(vw + 20, GW), 0, 1400, 'linear', flip);
      });
    });

    // Seven invoices ride it in to a hover spot, then drift on slowly,
    // fluttering. [spot as a share of the width, bottom px, role]: catches
    // hover at folder height, the stray sinks to the floor, the face-slapper
    // hovers at head height and goes last.
    var tIn = 1400, drift = 0.025; // px per ms once settled
    var sheets = [[0.20, 40, 'catch'], [0.31, 150, 'stray'], [0.41, 44, 'catch'], [0.51, 38, 'catch'], [0.61, 46, 'catch'], [0.71, 40, 'catch'], [0.84, 64, 'face']]
      .map(function(d, i) { var t0 = 150 + 140 * i; return { i: i, spot: Math.round(vw * d[0]), bottom: d[1], role: d[2], t0: t0, tS: t0 + tIn, el: null }; });
    var stray = sheets[1], face = sheets[6];
    sheets.forEach(function(p) {
      at(p.t0, function() {
        var fl = '<div class="stfl" style="animation-duration:' + (1.15 + (p.i % 3) * 0.22).toFixed(2) + 's;animation-delay:-' + (p.i * 0.37).toFixed(2) + 's">' + stPaperSvg(accents[p.i]) + '</div>';
        p.el = keep(stSpawn(fl, PW, PH, p.bottom, X(-PW - 10, PW), -40 - (p.i % 3) * 18, ''));
        stMove(p.el, X(p.spot, PW), 0, tIn, 'cubic-bezier(0.2,0.7,0.3,1)');
      });
      if (p.role === 'stray') {
        at(p.tS, function() { stMove(p.el, X(p.spot + 70, PW), p.bottom - 4, 1700, 'cubic-bezier(0.45,0,0.75,1)'); });
        at(p.tS + 1700, function() { var f = p.el.firstChild; f.style.animation = 'none'; f.className = 'stflat'; });
      } else {
        at(p.tS, function() { stMove(p.el, X(p.spot + drift * 16000, PW), 0, 16000, 'linear'); });
      }
    });

    // Danny sprints in once they've all settled. A sheet is caught when the
    // folder's mouth (fOff px from his back edge) reaches its center; the
    // face-slapper when his face (faceOff) does. Both move linearly, so each
    // meeting time is solved up front.
    var tD = 2700, sD0 = -DW - 20, v = (vw + DW + 20) / 4400;
    var fOff = stPx(72), faceOff = stPx(46);
    var meetAt = function(p, off) { return Math.round((p.spot + PW / 2 - off - sD0 + v * tD - drift * p.tS) / (v - drift)); };
    var tSlap = meetAt(face, faceOff);
    var sSlap = sD0 + v * (tSlap - tD), sStop = sSlap + stPx(10);
    var headX = function(s) { return lr ? X(s, DW) + stPx(36) : X(s, DW) + DW - stPx(36); };
    var bubBottom = 4 + stPx(90);
    var danny = null, caught = 0;
    at(tD, function() {
      danny = keep(stSpawn(stDannyHtml('run', 0), DW, DH, 4, X(sD0, DW), 0, flip));
      stMove(danny, X(sSlap, DW), 0, tSlap - tD, 'linear', flip);
    });
    // "my invoices!" rides along over his head for the first stretch
    at(tD + 500, function() {
      var b = keep(stBubble('my invoices!', headX(sD0 + v * 500) - 16, bubBottom));
      b.style.transition = 'transform 1600ms linear';
      b.style.transform = 'translateX(' + (headX(sD0 + v * 2100) - 16) + 'px)';
      at(1600, function() { drop(b); });
    });
    sheets.forEach(function(p) {
      if (p.role !== 'catch') return;
      at(meetAt(p, fOff), function() {
        drop(p.el);
        caught++;
        if (danny) danny.innerHTML = stDannyHtml('run', caught);
      });
    });
    at(tSlap, function() {
      drop(face.el);
      danny.innerHTML = stDannyHtml('slap', caught);
      stMove(danny, X(sStop, DW), 0, 260, 'ease-out', flip);
    });
    // ...then staggers off blind at a slower pace
    at(tSlap + 1500, function() { stMove(danny, X(vw + 20, DW), 0, Math.round((vw + 20 - sStop) / (v * 0.6)), 'linear', flip); });

    // The cat creeps in behind him toward the stray on the floor, crouches,
    // wiggles, pounces (mouth lands on the sheet) and trots back out with it.
    var sLand = stray.spot + 70 + PW / 2 - stPx(40), sCrouch = sLand - stPx(60), sC0 = -CW - 20;
    var tCat = tSlap + 300, dCatIn = Math.max(1000, Math.min(4000, Math.round((sCrouch - sC0) / 0.26)));
    var tPounce = tCat + dCatIn + 1100, tLand = tPounce + 480, dCatOut = Math.round((sLand - sC0) / 0.3);
    var cat = null;
    at(tCat, function() {
      cat = keep(stSpawn(stCatHtml('walk'), CW, CH, 4, X(sC0, CW), 0, flip));
      stMove(cat, X(sCrouch, CW), 0, dCatIn, 'linear', flip);
    });
    at(tCat + dCatIn, function() { cat.innerHTML = stCatHtml('crouch'); });
    at(tPounce, function() {
      cat.innerHTML = stCatHtml('leap');
      stMove(cat, X((sCrouch + sLand) / 2, CW), -stPx(28), 240, 'ease-out', flip);
    });
    at(tPounce + 240, function() { stMove(cat, X(sLand, CW), 0, 240, 'ease-in', flip); });
    at(tLand, function() { drop(stray.el); cat.innerHTML = stCatHtml('carry'); });
    at(tLand + 450, function() {
      cat.style.transition = 'none';
      cat.style.transform = 'translate(' + X(sLand, CW) + 'px,0px)' + back;
      cat.getBoundingClientRect();
      stMove(cat, X(sC0, CW), 0, dCatOut, 'linear', back);
    });
    at(Math.max(tLand + 450 + dCatOut, tSlap + 1500 + Math.round((vw + 20 - sStop) / (v * 0.6))) + 600, function() { els.forEach(drop); });
  } catch (e) {}
}
window.stormRun = stormRun;

// ─── TRICK-OR-TREAT PARADE (the hourly cartoon Oct 24-31, every year) ───
// Bats flutter past, then the Office agents cross the bottom of the page in
// costume, each with their desk look: Emma (Customer Support) as a witch
// with a broom, Danny as a mummy holding out a pumpkin pail, Sage as a
// vampire whose cape swishes, the Filing floor's gray cat in a pumpkin suit,
// and last a bedsheet ghost that stops at center, turns to the screen
// ("boo!") and fades away. Anthony 2026-10-05: plays on every hourly run in
// the week before Halloween (the viewer's local date); the Invoice Storm
// plays the rest of the year. Same rig and helpers as the Invoice Storm.
// window.paradeRun() = demo.
var HW = {
  emma: { hair: '#B8BCC4', skin: '#E8B98A', shirt: '#14B8A6', headset: true }, // officeLook('reamaze-cs'): ponytail, stud
  sage: { hair: '#8C3B22', skin: '#6B4530', shirt: '#84CC16', glasses: true }, // officeLook('shopify-faq'): bob, round glasses, stud
  witch: '#2E1A47', band: '#7C3AED', stocking: '#7C3AED', broom: '#8B5A2B', straw: '#D4A24C', strawDk: '#A97C2F',
  cape: '#1C1A26', lining: '#B91C1C', pants: '#1F2937', pantsB: '#2B3444', rim: '#8B93A7', gold: '#F2CF6B',
  wrap: '#EDE6D6', wrapDk: '#D9D0BC', wrapLn: '#B9AE96', pumpkin: '#F97316', pumpkinDk: '#C2410C', stem: '#4D7C0F', leaf: '#65A30D',
  ghost: '#F8FAFC', ghostLn: '#94A3B8', bat: '#2E1065', batLn: '#A78BFA', batEye: '#FBBF24', ink: '#1F2937',
};
// OFC_HAIR_SHORT_WALKER: the short cap over a walker-scale head at (0,-68), r 11.5
var HW_CAP_SHORT = 'M 9.5,-74.8 Q 5.4,-81.2 -0.7,-81.5 Q -9.5,-81.2 -13.2,-74.8 Q -14.9,-70.7 -14.9,-65.3 Q -15,-61.2 -12.9,-58.9 Q -10.5,-60.6 -10.5,-63.9 Q -10.8,-70.7 -6.1,-74.4 Q -0.7,-77.8 4.7,-76.8 Q 8.1,-76.1 9.5,-74.8 Z';
function hwRim(c) { return ' stroke="' + (c || HW.rim) + '" stroke-width="0.9"'; }
// officeHeadGroup at walker scale for the ponytail and bob styles (stud earrings, optional glasses/headset)
function hwHead(lk, style) {
  var r = 11.5, cx = 0, cy = -68, k = [];
  if (style === 'ponytail') k.push('<ellipse cx="' + (cx - 1.02 * r) + '" cy="' + (cy + 0.35 * r) + '" rx="' + (0.3 * r) + '" ry="' + (0.9 * r) + '" fill="' + lk.hair + '" transform="rotate(10 ' + (cx - 1.02 * r) + ' ' + (cy + 0.35 * r) + ')"/>');
  if (style === 'bob') k.push(fkRect(cx - 1.1 * r, cy - 0.62 * r, 1.45 * r, 1.58 * r, 0.42 * r, lk.hair));
  k.push(fkCircle(cx, cy, r, lk.skin) + fkCircle(cx + 0.5 * r, cy - 0.05 * r, 0.14 * r, FK.eye));
  k.push('<path d="' + HW_CAP_SHORT + '" fill="' + lk.hair + '"/>');
  if (lk.glasses) {
    var gx = cx + 0.52 * r, gy = cy - 0.05 * r, gr = 0.3 * r;
    k.push('<circle cx="' + gx + '" cy="' + gy + '" r="' + gr + '" fill="rgba(255,255,255,0.10)" stroke="#2B3444" stroke-width="' + (0.09 * r) + '"/>' +
      '<path d="M ' + (gx - gr) + ',' + gy + ' L ' + (cx - 0.55 * r) + ',' + (cy - 0.1 * r) + '" stroke="#2B3444" stroke-width="' + (0.08 * r) + '" fill="none"/>');
  }
  k.push(fkCircle(cx - 0.48 * r, cy + 0.58 * r, 0.09 * r, HW.gold));
  if (lk.headset) {
    var hs = '#222B3A';
    k.push('<path d="M ' + (cx - r) + ',' + (cy - 0.35 * r) + ' A ' + (1.03 * r) + ',' + (1.03 * r) + ' 0 0 1 ' + (cx + r) + ',' + (cy - 0.35 * r) + '" stroke="' + hs + '" stroke-width="' + (0.2 * r) + '" fill="none" stroke-linecap="round"/>' +
      fkCircle(cx - 0.88 * r, cy + 0.06 * r, 0.29 * r, hs) +
      '<path d="M ' + (cx - 0.88 * r) + ',' + (cy + 0.35 * r) + ' Q ' + (cx - 0.7 * r) + ',' + (cy + 0.82 * r) + ' ' + (cx - 0.12 * r) + ',' + (cy + 0.88 * r) + '" stroke="' + hs + '" stroke-width="' + (0.15 * r) + '" fill="none" stroke-linecap="round"/>' +
      fkCircle(cx - 0.06 * r, cy + 0.88 * r, 0.13 * r, hs));
  }
  return k.join('');
}
// Walker legs (Office stroll proportions), swinging on two frames; extra = trim drawn on each leg
function hwLegs(B, fillA, fillB, swing, trimA, trimB) {
  return fkG(fkRect(-9.5, -28, 8, 28, 4, fillA) + (trimA || ''), 'rotate(' + (B ? swing : -swing) + ' -5.5 -26)') +
    fkG(fkRect(1.5, -28, 8, 28, 4, fillB) + (trimB || ''), 'rotate(' + (B ? -swing : swing) + ' 5.5 -26)');
}
// Jack-o'-lantern candy pail hanging from a hand at (x, y)
function hwPail(x, y) {
  return '<path d="M ' + (x - 6) + ',' + (y + 6) + ' Q ' + x + ',' + (y - 3) + ' ' + (x + 6) + ',' + (y + 6) + '" stroke="' + HW.ink + '" stroke-width="1.1" fill="none"/>' +
    '<ellipse cx="' + x + '" cy="' + (y + 11) + '" rx="8" ry="6.8" fill="' + HW.pumpkin + '"/>' +
    '<ellipse cx="' + x + '" cy="' + (y + 11) + '" rx="3.4" ry="6.8" fill="none" stroke="' + HW.pumpkinDk + '" stroke-width="0.9"/>' +
    '<path d="M ' + (x - 4.4) + ',' + (y + 10) + ' L ' + (x - 2.4) + ',' + (y + 7.4) + ' L ' + (x - 0.8) + ',' + (y + 10) + ' Z M ' + (x + 0.8) + ',' + (y + 10) + ' L ' + (x + 2.4) + ',' + (y + 7.4) + ' L ' + (x + 4.4) + ',' + (y + 10) + ' Z" fill="' + HW.ink + '"/>' +
    '<path d="M ' + (x - 4.5) + ',' + (y + 12.8) + ' Q ' + x + ',' + (y + 17.5) + ' ' + (x + 4.5) + ',' + (y + 12.8) + ' Z" fill="' + HW.ink + '"/>';
}
// Emma as a witch (90×112, feet at 40,106): pointed hat, cape, striped stockings, broom
function hwEmmaFrame(which) {
  var B = which === 'B', lk = HW.emma, k = [fkEllipse(0, 1.5, 18, 2.8, FK.shadow)];
  var stripes = function(x) { return [-22, -15, -8].map(function(y) { return fkRect(x, y, 8, 2.6, 0.6, HW.stocking); }).join(''); };
  k.push(hwLegs(B, HW.witch, HW.witch, 16, stripes(-9.5), stripes(1.5)));
  var u = [];
  u.push('<path d="M -8,-58 Q ' + (B ? -26 : -22) + ',-34 ' + (B ? -24 : -20) + ',-12 L -4,-14 Q -6,-36 2,-57 Z" fill="' + HW.witch + '"' + hwRim() + '/>'); // cape
  u.push('<path d="M -13,-36 L 13,-36 L 17,-20 L -17,-20 Z" fill="' + HW.witch + '"' + hwRim() + '/>'); // skirt
  u.push(fkRect(-13, -58, 26, 26, 10, lk.shirt));
  u.push(hwHead(lk, 'ponytail'));
  u.push('<ellipse cx="1" cy="-77.5" rx="17" ry="3.2" fill="' + HW.witch + '"' + hwRim() + '/>' +
    '<path d="M -9,-78 L 10,-78 L 3,-94 Q 0,-101 -9,-99 Q -3,-96 -2,-92 Z" fill="' + HW.witch + '"' + hwRim() + '/>' +
    fkRect(-8, -82.5, 17, 3.6, 1, HW.band) + fkRect(-0.8, -82.5, 3.6, 3.6, 0.6, HW.gold));
  // broom carried across the body: straw trailing low behind her
  u.push('<line x1="33" y1="-52" x2="-27" y2="-12" stroke="' + HW.broom + '" stroke-width="2.6" stroke-linecap="round"/>' +
    '<path d="M -23,-15 L -39,-9 Q -40,-3 -35,0 L -21,-11 Z" fill="' + HW.straw + '"/>' +
    '<path d="M -26,-12 L -37,-5 M -24,-11 L -33,-1" stroke="' + HW.strawDk + '" stroke-width="0.9"/>');
  u.push(fkRect(2, -53, 20, 7.5, 3.75, lk.shirt, ' transform="rotate(18 4 -49)"') + fkCircle(21, -43.5, 4.2, lk.skin));
  k.push(fkG(u.join(''), B ? 'translate(0,-1.2)' : ''));
  return fkG(k.join(''), 'translate(40,106)');
}
// Danny as a mummy: bandaged head to toe, a slit for his eyes, arms out, stiff shuffle, pumpkin pail
function hwMummyFrame(which) {
  var B = which === 'B', W = HW.wrap, k = [fkEllipse(0, 1.5, 18, 2.8, FK.shadow)];
  var ln = function(x1, y1, x2, y2) { return '<line x1="' + x1 + '" y1="' + y1 + '" x2="' + x2 + '" y2="' + y2 + '" stroke="' + HW.wrapLn + '" stroke-width="1.1" stroke-linecap="round"/>'; };
  var legWraps = function(x) { return [-22, -14, -6].map(function(y) { return ln(x + 1, y + 1.5, x + 7, y - 1.5); }).join(''); };
  k.push(hwLegs(B, W, W, 8, legWraps(-9.5), legWraps(1.5)));
  var u = [];
  u.push('<path d="M -12,-46 Q -22,' + (B ? -40 : -48) + ' -31,' + (B ? -35 : -43) + '" stroke="' + W + '" stroke-width="3.2" fill="none" stroke-linecap="round"/>'); // loose end
  u.push(fkRect(-1, -55, 28, 7.5, 3.75, HW.wrapDk, ' transform="rotate(' + (B ? -6 : -2) + ' 0 -51)"')); // far arm
  u.push(fkRect(-13, -58, 26, 36, 10, W) + ln(-9, -50, 9, -54) + ln(-11, -43, 11, -47) + ln(-11, -36, 11, -40) + ln(-9, -29, 9, -33));
  u.push(fkCircle(0, -68, 11.5, W) + ln(-8, -74, 8, -77) + ln(-10, -59.5, 9, -62.5) + ln(-11, -65, -2, -66.5));
  u.push(fkRect(-1, -71, 12, 4.4, 2.2, ST.skin) + fkCircle(6, -68.8, 1.6, FK.eye)); // eye slit
  u.push(fkG(fkRect(0, -52, 28, 8, 4, W) + ln(9, -51, 8, -45) + ln(17, -51, 16, -45), 'rotate(' + (B ? 0 : -4) + ' 1 -48)')); // near arm
  u.push(hwPail(27, B ? -48 : -50));
  k.push(fkG(u.join(''), B ? 'translate(0,-1)' : ''));
  return fkG(k.join(''), 'translate(40,106)');
}
// Sage as a vampire: high collar, black cape with a red lining that swishes, fangs, pumpkin pail
function hwSageFrame(which) {
  var B = which === 'B', lk = HW.sage, k = [fkEllipse(0, 1.5, 18, 2.8, FK.shadow)];
  var u = [];
  u.push('<path d="M -6,-60 Q ' + (B ? -34 : -26) + ',-34 ' + (B ? -34 : -27) + ',-4 L -8,-8 Q -12,-34 0,-58 Z" fill="' + HW.cape + '"' + hwRim() + '/>' +
    '<path d="M -7,-57 Q ' + (B ? -28 : -21) + ',-34 ' + (B ? -28 : -22) + ',-7 L -10,-9 Q -13,-34 -3,-56 Z" fill="' + HW.lining + '"/>');
  u.push('<path d="M -12,-56 L -23,-84 L -15,-79 L -8,-86 L -3,-61 Z" fill="' + HW.cape + '"' + hwRim() + '/>' +
    '<path d="M -11,-57.5 L -20,-80 L -14.5,-76.5 L -8.5,-81.5 L -5,-62 Z" fill="' + HW.lining + '"/>'); // stand-up collar
  k.push(fkG(u.join(''), B ? 'translate(0,-1)' : ''));
  k.push(hwLegs(B, HW.pants, HW.pantsB, 16));
  var v = [];
  v.push(fkRect(-13, -58, 26, 36, 10, lk.shirt));
  v.push(hwHead(lk, 'bob'));
  v.push('<path d="M 8.6,-62.4 L 10.4,-62.6 L 9.6,-59.6 Z" fill="#FFFFFF"/>'); // fang
  v.push(fkRect(2, -53, 20, 7.5, 3.75, lk.shirt, ' transform="rotate(40 4 -49)"') + hwPail(18, -38) + fkCircle(18, -37.5, 4.2, lk.skin));
  k.push(fkG(v.join(''), B ? 'translate(0,-1)' : ''));
  return fkG(k.join(''), 'translate(40,106)');
}
// The Filing floor's gray cat in a pumpkin suit (58×46, feet at 26,43), stem cap between its ears
function hwCatFrame(which) {
  var B = which === 'B', c = ST.cat, k = [fkEllipse(0, 1.2, 15, 2.2, FK.shadow)];
  k.push('<path d="' + (B ? 'M -14,-12 Q -23,-14 -21,-26' : 'M -14,-12 Q -22,-16 -18,-28') + '" stroke="' + c + '" stroke-width="4.5" fill="none" stroke-linecap="round"/>');
  k.push(fkRect(-10, -9, 4.5, 9.5, 2.2, c, ' transform="rotate(' + (B ? 22 : -22) + ' -7.75 -8)"'));
  k.push(fkRect(4, -9, 4.5, 9.5, 2.2, c, ' transform="rotate(' + (B ? -22 : 22) + ' 6.25 -8)"'));
  k.push('<ellipse cx="-1" cy="-16" rx="15" ry="11.5" fill="' + HW.pumpkin + '"/>' +
    '<ellipse cx="-1" cy="-16" rx="8.5" ry="11.5" fill="none" stroke="' + HW.pumpkinDk + '" stroke-width="1.2"/>' +
    '<ellipse cx="-1" cy="-16" rx="2.6" ry="11.5" fill="none" stroke="' + HW.pumpkinDk + '" stroke-width="1.2"/>');
  k.push(fkCircle(12, -26, 7, c) + stCatEars(0, -4) + fkCircle(14.5, -26.5, 1.1, FK.eye));
  k.push(fkRect(11, -38.5, 2.4, 6, 1, HW.stem) + '<path d="M 13.2,-36.5 Q 18,-40 19.5,-35.5 Q 15.5,-34.5 13.2,-35.2 Z" fill="' + HW.leaf + '"/>');
  return fkG(k.join(''), 'translate(26,43)');
}
// Bedsheet ghost (50×54, ground at 25,52), floating; 'side' drifts right, 'front' faces the screen
function hwGhostFrame(view, which) {
  var B = which === 'B', k = [fkEllipse(0, 1, 13, 2.2, 'rgba(0,0,0,0.18)')];
  var d = view === 'front'
    ? 'M 15,-10 C 18,-30 12,-47 0,-47 C -12,-47 -18,-30 -15,-10 Q -11,' + (B ? -4 : -6) + ' -7.5,-9 Q -4,' + (B ? -6 : -4) + ' 0,-9 Q 4,' + (B ? -4 : -6) + ' 7.5,-9 Q 11,' + (B ? -6 : -4) + ' 15,-10 Z'
    : 'M 14,-10 C 17,-28 13,-46 -1,-47 C -14,-47 -17,-32 -16,-18 Q -18,-10 -22,-6 Q -17,' + (B ? -2 : -4) + ' -12,-8 Q -8,' + (B ? -4 : -2) + ' -4,-8 Q 0,' + (B ? -2 : -4) + ' 4,-8 Q 8,' + (B ? -4 : -2) + ' 10,-8 Q 12,-6 14,-10 Z';
  k.push('<path d="' + d + '" fill="' + HW.ghost + '" stroke="' + HW.ghostLn + '" stroke-width="1.1"/>');
  if (view === 'front') k.push('<ellipse cx="-5" cy="-33" rx="2.4" ry="3.4" fill="' + HW.ink + '"/><ellipse cx="5" cy="-33" rx="2.4" ry="3.4" fill="' + HW.ink + '"/><ellipse cx="0" cy="-24" rx="2.6" ry="3.4" fill="' + HW.ink + '"/>');
  else k.push('<ellipse cx="6" cy="-34" rx="2" ry="3" fill="' + HW.ink + '"/><ellipse cx="11" cy="-34.5" rx="1.5" ry="2.8" fill="' + HW.ink + '"/>');
  return fkG(k.join(''), 'translate(25,52)');
}
// Bat (34×18, front view): wings up on frame A, down on frame B
function hwBatFrame(which) {
  var wings = which === 'B'
    ? 'M 14,8 Q 8,10 3,16 Q 6,12 9,15 Q 10,11 14,11 Z M 20,8 Q 26,10 31,16 Q 28,12 25,15 Q 24,11 20,11 Z'
    : 'M 14,8 L 4,1 Q 6,6 1,6 Q 5,9 3,12 Q 9,9 14,10 Z M 20,8 L 30,1 Q 28,6 33,6 Q 29,9 31,12 Q 25,9 20,10 Z';
  return '<path d="' + wings + '" fill="' + HW.bat + '"' + hwRim(HW.batLn) + '/>' +
    '<path d="M 14.2,6.5 L 14.6,3.2 L 16.2,5.8 Z M 19.8,6.5 L 19.4,3.2 L 17.8,5.8 Z" fill="' + HW.bat + '"' + hwRim(HW.batLn) + '/>' +
    '<ellipse cx="17" cy="9" rx="4" ry="3.4" fill="' + HW.bat + '"' + hwRim(HW.batLn) + '/>' +
    fkCircle(15.7, 8.3, 0.75, HW.batEye) + fkCircle(18.3, 8.3, 0.75, HW.batEye);
}
function hwHtml(frameFn, vw, vh, speed) {
  var mk = function(which) { return function(cls) { return forkSvgWrap(frameFn(which), vw, vh, stPx(vw), stPx(vh), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), speed);
}
function hwEnsureStyle() {
  stEnsureStyle();
  if (document.getElementById('halloween-style')) return;
  var st = document.createElement('style');
  st.id = 'halloween-style';
  st.textContent =
    '@keyframes hwBob { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-7px); } }' +
    '.hwbob { position: absolute; inset: 0; animation: hwBob 1.6s ease-in-out infinite; }' +
    '@keyframes hwFlap { 0%, 100% { transform: translateY(0); } 30% { transform: translateY(-16px); } 65% { transform: translateY(6px); } }' +
    '.hwflap { position: absolute; inset: 0; animation: hwFlap 1.3s ease-in-out infinite; }';
  document.head.appendChild(st);
}
// Halloween week = Oct 24-31 by the viewer's own clock, every year
function hwIsHalloweenWeek(d) {
  d = d || new Date();
  return d.getMonth() === 9 && d.getDate() >= 24;
}
function paradeRun() {
  try {
    hwEnsureStyle();
    var vw = window.innerWidth;
    var lr = Math.random() < 0.5; // which way the parade walks
    var flip = lr ? '' : ' scaleX(-1)';
    var X = function(s, w) { return Math.round(lr ? s : vw - s - w); };
    var els = [];
    var keep = function(el) { els.push(el); return el; };
    var drop = function(el) { try { if (el) el.remove(); } catch (e) {} };
    var at = function(ms, fn) { setTimeout(function() { try { fn(); } catch (e) {} }, ms); };
    var end = 900 + 4200; // the last bat is gone by then

    // Bats first: [start ms, bottom px, crossing ms, flap period s]
    var BW = stPx(34), BH = stPx(18);
    [[0, 160, 3400, 1.2], [250, 205, 3900, 1.5], [600, 125, 3600, 1.1], [900, 235, 4200, 1.4]].forEach(function(b, i) {
      at(b[0], function() {
        var html = '<div class="hwflap" style="animation-duration:' + b[3] + 's;animation-delay:-' + (i * 0.4).toFixed(1) + 's">' + hwHtml(hwBatFrame, 34, 18, ' fast') + '</div>';
        var el = keep(stSpawn(html, BW, BH, b[1], X(-BW - 20, BW), 0, ''));
        stMove(el, X(vw + 20, BW), 0, b[2], 'linear');
      });
    });

    // The costumed agents walk across at one pace, about 1.3 s apart
    var PW = stPx(90), PH = stPx(112), CW = stPx(58), CH = stPx(46);
    var v = (vw + PW + 40) / 9000; // px per ms
    var walkers = [[700, hwEmmaFrame, PW, PH, 90, 112, ' wv'], [2000, hwMummyFrame, PW, PH, 90, 112, ' wv'],
      [3300, hwSageFrame, PW, PH, 90, 112, ' wv'], [4500, hwCatFrame, CW, CH, 58, 46, '']];
    walkers.forEach(function(a) {
      at(a[0], function() {
        var el = keep(stSpawn(hwHtml(a[1], a[4], a[5], a[6]), a[2], a[3], 4, X(-a[2] - 20, a[2]), 0, flip));
        var ms = Math.round((vw + a[2] + 40) / v);
        stMove(el, X(vw + 20, a[2]), 0, ms, 'linear', flip);
      });
      end = Math.max(end, a[0] + Math.round((vw + a[2] + 40) / v));
    });

    // The ghost drifts in last, stops at center, turns to the screen, "boo!", fades away
    var GW = stPx(50), GH = stPx(54);
    // (same pace as the walkers so it keeps its distance, easing only over the last stretch)
    var sG = Math.round(vw / 2 - GW / 2), sEase = sG - stPx(40), tG = 5800, dG = Math.round((sEase + GW + 20) / v), tStop = tG + dG + 600;
    var ghost = null, ghostFrames = function(view) { return '<div class="hwbob">' + hwHtml(function(w) { return hwGhostFrame(view, w); }, 50, 54, ' wv') + '</div>'; };
    at(tG, function() {
      ghost = keep(stSpawn(ghostFrames('side'), GW, GH, 4, X(-GW - 20, GW), 0, flip));
      stMove(ghost, X(sEase, GW), 0, dG, 'linear', flip);
    });
    at(tG + dG, function() { stMove(ghost, X(sG, GW), 0, 600, 'ease-out', flip); });
    at(tStop + 250, function() { ghost.innerHTML = ghostFrames('front'); });
    at(tStop + 450, function() {
      var b = keep(stBubble('boo!', X(sG, GW) + Math.round(GW / 2) - 16, 4 + GH + 8));
      at(1300, function() { drop(b); });
    });
    at(tStop + 1750, function() { ghost.style.transition = 'opacity 700ms ease-in'; ghost.style.opacity = '0'; });
    end = Math.max(end, tStop + 2450);
    at(end + 600, function() { els.forEach(drop); });
  } catch (e) {}
}
window.paradeRun = paradeRun;

// ─── HOURLY SEASON CALENDAR (Anthony 2026-11-01) ───
// Which cartoon the hourly clock plays, by the viewer's own date, every
// year: the Trick-or-Treat Parade in Halloween week (Oct 24-31); the Leaf
// Pile from Nov 1 up to the week before Thanksgiving; the Thanksgiving
// Potluck over the seven days ending on Thanksgiving (the fourth Thursday of
// November); the Black Friday Stampede from Black Friday through Cyber
// Monday; the Holiday Sleigh Dec 18-31; Danny's Invoice Storm the rest of
// the year. A season whose scene isn't drawn yet plays the Storm.
function thanksgivingDay(year) { return 22 + ((11 - new Date(year, 10, 1).getDay()) % 7); } // the 4th Thursday of November
function hourlySceneName(d) {
  d = d || new Date();
  var m = d.getMonth(), day = d.getDate();
  if (hwIsHalloweenWeek(d)) return 'parade';
  if (m === 10) {
    var tg = thanksgivingDay(d.getFullYear());
    if (day < tg - 6) return 'leaf';
    if (day <= tg) return 'potluck';
    if (day <= tg + 4) return 'stampede';
    return 'storm';
  }
  if (m === 11 && day >= 18) return 'sleigh';
  return 'storm';
}
function hourlyScene(d) {
  var scenes = { parade: paradeRun, leaf: leafRun, potluck: potluckRun, stampede: stampedeRun, sleigh: sleighRun, storm: stormRun };
  return scenes[hourlySceneName(d)] || stormRun;
}
window.hourlySceneName = hourlySceneName;

// ─── LEAF PILE (the hourly cartoon from Nov 1 to the week before Thanksgiving) ───
// A leaf-blower gust tumbles maple leaves along the bottom of the page and
// they settle on the floor. Danny (the Office's invoice-filing agent, in his
// desk look) walks in behind a rake and sweeps them into a pile that grows
// ahead of him, parks it, steps back and leans on the rake ("phew!"). The
// Filing floor's gray cat creeps in from the other side, crouches, and leaps
// into the pile: leaves burst everywhere and the cat sits in the wreck with a
// leaf on its head. "!" — Danny drops the rake, throws up his hands and
// stomps off the way he came; the cat trots off with a leaf in its mouth.
// Anthony 2026-11-01 (picked over Fall Back Monday / Diwali Lights / Runaway
// Parcel). Same rig and helpers as the Invoice Storm. window.leafRun() = demo.
var LF = {
  leaves: ['#DC2626', '#EA580C', '#F59E0B', '#D97706', '#B45309', '#C2410C'],
  edge: '#7C2D12', vein: 'rgba(255,255,255,0.35)', handle: '#8B5A2B', tine: '#9CA3AF', mound: '#B45309',
};
var LF_LEAN = { rake: 10, lean: 0, drop: -6, walk: 4 }; // how far Danny's upper body tips forward in each pose
// One maple leaf, 20×22, stem down
function lfLeaf(fill) {
  return '<path d="M 10,0.5 L 12.3,5.6 L 18.5,4 L 16.6,9.2 L 19.5,13.6 L 14.6,13.4 L 13,17.6 L 10,15.8 L 7,17.6 L 5.4,13.4 L 0.5,13.6 L 3.4,9.2 L 1.5,4 L 7.7,5.6 Z" fill="' + fill + '" stroke="' + LF.edge + '" stroke-width="0.5" stroke-linejoin="round"/>' +
    '<path d="M 10,15.8 L 10,1.5 M 10,11.5 L 17.5,4.8 M 10,11.5 L 2.5,4.8 M 10,13 L 18.6,13.2 M 10,13 L 1.4,13.2" stroke="' + LF.vein + '" stroke-width="0.6" fill="none"/>' +
    '<path d="M 10,15.8 L 10.4,22" stroke="' + LF.edge + '" stroke-width="1" stroke-linecap="round"/>';
}
function lfLeafSvg(i) { return forkSvgWrap(lfLeaf(LF.leaves[i % LF.leaves.length]), 20, 22, stPx(24), stPx(26), ''); }
function lfRx(k) { return 7 + 3 * k; } // half-width of a pile of k leaves
// k leaves heaped at (cx, base): a mound with leaves tumbled over it
function lfMound(k, cx, base) {
  if (k <= 0) return '';
  var rx = lfRx(k), ry = 4 + 1.7 * k, s = [];
  s.push('<path d="M ' + (cx - rx) + ',' + base + ' Q ' + cx + ',' + (base - 2 * ry) + ' ' + (cx + rx) + ',' + base + ' Z" fill="' + LF.mound + '"/>');
  for (var i = 0; i < k; i++) {
    var fx = (((i * 37) % 11) - 5) / 5.5, top = ry * (1 - fx * fx); // the mound's height under this leaf
    var y = base - 1 - top * (0.25 + 0.65 * ((i * 53) % 7) / 7);
    s.push(fkG(lfLeaf(LF.leaves[i % LF.leaves.length]), 'translate(' + (cx + fx * rx * 0.85).toFixed(1) + ',' + y.toFixed(1) + ') rotate(' + ((i * 97) % 360) + ') scale(0.9) translate(-10,-11)'));
  }
  return s.join('');
}
function lfPileSvg(k) { return forkSvgWrap(lfMound(k, 55, 45), 110, 48, stPx(110), stPx(48), ''); }
// The pile after the cat lands in it: leaves lying flat all around
function lfScatterSvg() {
  var s = [];
  for (var i = 0; i < 7; i++) {
    var x = 55 + (((i * 43) % 13) - 6) * 6.2;
    s.push(fkG(lfLeaf(LF.leaves[(i + 1) % LF.leaves.length]), 'translate(' + x.toFixed(1) + ',44.5) scale(1,0.45) rotate(' + ((i * 71) % 360) + ') scale(0.9) translate(-10,-11)'));
  }
  return forkSvgWrap(s.join(''), 110, 48, stPx(110), stPx(48), '');
}
// A rake: handle from (hx, hy) down to its head on the floor at (ex, 0), tines fanned out
function lfRake(hx, hy, ex) {
  var t = [];
  for (var i = 0; i < 7; i++) t.push('M ' + (ex - 1.5) + ',-6 Q ' + (ex - 9.5 + 3.2 * i) + ',-3.5 ' + (ex - 10.5 + 3.5 * i) + ',0.4');
  return '<line x1="' + hx + '" y1="' + hy + '" x2="' + (ex - 1.5) + '" y2="-6" stroke="' + LF.handle + '" stroke-width="2.4" stroke-linecap="round"/>' +
    '<path d="' + t.join(' ') + '" stroke="' + LF.tine + '" stroke-width="1.1" fill="none" stroke-linecap="round"/>' +
    '<line x1="' + (ex - 10) + '" y1="-3.2" x2="' + (ex + 10) + '" y2="-3.2" stroke="' + LF.tine + '" stroke-width="1.4" stroke-linecap="round"/>';
}
function lfRakeSvg() { return forkSvgWrap(fkG(lfRake(14, -62, 14), 'translate(0,69)'), 70, 70, stPx(70), stPx(70), ''); }
// An arm from the shoulder (sx, sy) to the hand (hx, hy): a rounded bar and the hand
function lfArm(sx, sy, hx, hy, fill, skin) {
  var dx = hx - sx, dy = hy - sy, L = Math.sqrt(dx * dx + dy * dy), a = Math.atan2(dy, dx) * 180 / Math.PI;
  return fkRect(sx - 1, sy - 3.75, L + 1, 7.5, 3.75, fill, ' transform="rotate(' + a.toFixed(1) + ' ' + sx + ' ' + sy + ')"') + fkCircle(hx, hy, 3.6, skin || ST.skin);
}
// Where the shoulder (-1,-50) lands once the upper body tips `lean` degrees about the hips (0,-28)
function lfShoulder(lean) {
  var t = lean * Math.PI / 180;
  return [-Math.cos(t) + 22 * Math.sin(t), -28 - Math.sin(t) - 22 * Math.cos(t)];
}
// Danny (160×100, feet at 30,97) in the Storm's desk look: 'rake' walks behind the rake with k
// leaves piled ahead of it, 'lean' rests on the upright rake wiping his brow, 'drop' throws both
// hands up, 'walk' stomps off empty-handed
function lfDannyFrame(pose, k, which) {
  var B = which === 'B', lean = LF_LEAN[pose] || 0, S = lfShoulder(lean);
  var swing = pose === 'rake' ? 20 : pose === 'walk' ? 22 : 4;
  var p = [fkEllipse(0, 1, 17, 2.6, FK.shadow)];
  if (pose === 'drop') p.push(fkRect(-4, -28, 8, 28, 4, FK.pantsA, ' transform="rotate(-12 0 -26)"') + fkRect(-4, -28, 8, 28, 4, FK.pantsB, ' transform="rotate(12 0 -26)"'));
  else p.push(fkRect(-4, -28, 8, 28, 4, FK.pantsA, ' transform="rotate(' + (B ? swing : -swing) + ' 0 -26)"') + fkRect(-4, -28, 8, 28, 4, FK.pantsB, ' transform="rotate(' + (B ? -swing : swing) + ' 0 -26)"'));
  var torso = fkG(fkRect(-13, -58, 26, 36, 10, ST.shirt) + stDannyHead(0, -68), 'rotate(' + lean + ' 0 -28)');
  var H1, H2, held = ''; // near hand, far hand, whatever is held between the arms
  if (pose === 'rake') { H1 = [20, -44]; H2 = [28, -33]; held = lfRake(20, -44, B ? 53 : 52); }
  else if (pose === 'lean') { H1 = [12, B ? -79 : -78]; H2 = [16, -58]; held = lfRake(16, -64, 16); }
  else if (pose === 'drop') { H1 = [-16, B ? -79 : -80]; H2 = [14, B ? -81 : -82]; }
  else { H1 = B ? [-7, -32] : [9, -30]; H2 = B ? [9, -30] : [-7, -32]; }
  var u = lfArm(S[0], S[1], H2[0], H2[1], ST.shirtDk) + torso + held + lfArm(S[0], S[1], H1[0], H1[1], ST.shirt);
  p.push(fkG(u, 'translate(0,' + (B && pose !== 'drop' ? -1.5 : 0) + ')'));
  if (pose === 'rake') p.push(lfMound(k, 54 + lfRx(k), 0));
  return fkG(p.join(''), 'translate(30,97)');
}
function lfDannyHtml(pose, k) {
  var mk = function(which) { return function(cls) { return forkSvgWrap(lfDannyFrame(pose, k, which), 160, 100, stPx(160), stPx(100), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), pose === 'lean' || pose === 'drop' ? ' wv' : '');
}
// The gray cat: the Storm's walk / crouch / leap, plus 'sit' (facing the screen, a leaf on its
// head, 58×60) and 'carry' (a leaf in its mouth)
function lfCatFrame(pose, which) {
  var B = which === 'B', c = ST.cat;
  if (pose === 'sit') {
    var p = [fkEllipse(0, 1.2, 14, 2.2, FK.shadow),
      '<path d="' + (B ? 'M 6,-4 Q 18,-4 20,-14' : 'M 6,-4 Q 19,-6 18,-16') + '" stroke="' + c + '" stroke-width="4.5" fill="none" stroke-linecap="round"/>',
      '<ellipse cx="-2" cy="-13" rx="11" ry="12.5" fill="' + c + '"/>',
      fkRect(-9, -12, 4.5, 12, 2.2, c) + fkRect(1, -12, 4.5, 12, 2.2, c),
      fkCircle(-1, -29, 7.5, c) + stCatEars(-13, -7) + fkCircle(-3.6, -30, 1.2, FK.eye) + fkCircle(1.6, -30, 1.2, FK.eye),
      fkG(lfLeaf(LF.leaves[0]), 'translate(-4,-37) rotate(' + (B ? -24 : -18) + ') scale(0.7) translate(-10,-22)')];
    return fkG(p.join(''), 'translate(26,57)');
  }
  var base = stCatFrame(pose === 'carry' ? 'walk' : pose, which);
  if (pose === 'carry') base += fkG(lfLeaf(LF.leaves[2]), 'translate(26,41) translate(17,-21) rotate(62) scale(0.6) translate(-10,-11)');
  return base;
}
function lfCatHtml(pose) {
  var vh = pose === 'sit' ? 60 : 44;
  var mk = function(which) { return function(cls) { return forkSvgWrap(lfCatFrame(pose, which), 58, vh, stPx(58), stPx(vh), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), pose === 'crouch' || pose === 'sit' ? ' wv' : ' fast');
}
// A leaf that has landed: stop tumbling and lie flat
function lfFlat(el, i) {
  var f = el && el.firstChild;
  if (!f) return;
  f.style.animation = 'none';
  f.className = 'lfflat';
  f.style.transform = 'rotate(' + ((i * 53) % 50 - 25) + 'deg) scaleY(0.55)';
}
function lfEnsureStyle() {
  stEnsureStyle();
  if (document.getElementById('leaf-style')) return;
  var st = document.createElement('style');
  st.id = 'leaf-style';
  st.textContent =
    '.lfflat { position: absolute; inset: 0; transform-origin: 50% 100%; }' +
    '.lfrake { position: absolute; inset: 0; transform-origin: 20% 100%; transition: transform 380ms cubic-bezier(0.55,0,1,0.6); }';
  document.head.appendChild(st);
}
function leafRun() {
  try {
    lfEnsureStyle();
    var vw = window.innerWidth;
    var lr = Math.random() < 0.5; // which way the leaves blow
    var flip = lr ? '' : ' scaleX(-1)', back = lr ? ' scaleX(-1)' : '';
    var X = function(s, w) { return Math.round(lr ? s : vw - s - w); };
    var DW = stPx(160), DH = stPx(100), CW = stPx(58), CH = stPx(44), LW = stPx(24), LH = stPx(26), PW = stPx(110), PH = stPx(48), GW = stPx(150), GH = stPx(36), RW = stPx(70), RH = stPx(70);
    var els = [];
    var keep = function(el) { els.push(el); return el; };
    var drop = function(el) { try { if (el) el.remove(); } catch (e) {} };
    var at = function(ms, fn) { setTimeout(function() { try { fn(); } catch (e) {} }, ms); };
    var floor = 6; // landed leaves lie on the figures' floor line

    // The gust: three wind streaks low along the floor
    [[0, 40], [180, 96], [380, 18]].forEach(function(g) {
      at(g[0], function() {
        var el = keep(stSpawn(stGustSvg(), GW, GH, g[1], X(-GW, GW), 0, flip));
        stMove(el, X(vw + 20, GW), 0, 1400, 'linear', flip);
      });
    });

    // Nine leaves tumble in and settle on the floor between 12% and 64% of the width
    var leaves = [0.12, 0.19, 0.27, 0.33, 0.40, 0.46, 0.52, 0.58, 0.64].map(function(f, i) {
      var t0 = 150 + 120 * i, tIn = 1500 + (i % 3) * 150;
      return { i: i, spot: Math.round(vw * f), t0: t0, tS: t0 + tIn, tIn: tIn, el: null };
    });
    leaves.forEach(function(p) {
      at(p.t0, function() {
        var fl = '<div class="stfl" style="animation-duration:' + (1.05 + (p.i % 3) * 0.2).toFixed(2) + 's;animation-delay:-' + (p.i * 0.31).toFixed(2) + 's">' + lfLeafSvg(p.i) + '</div>';
        p.el = keep(stSpawn(fl, LW, LH, floor, X(-LW - 10, LW), -50 - (p.i % 4) * 22, ''));
        stMove(p.el, X(p.spot, LW), 0, p.tIn, 'cubic-bezier(0.2,0.7,0.3,1)');
      });
      at(p.tS, function() { lfFlat(p.el, p.i); });
    });

    // Danny walks in behind the rake once they're down. A leaf is swept the moment the rake head
    // (rOff px from his back edge) reaches its center; the pile ahead of the rake grows by one.
    var tD = 3000, sD0 = -DW - 20, v = (vw + DW + 20) / 6500; // px per ms: a working walk, not the Storm's sprint
    var rOff = stPx(82), K = leaves.length;
    var sStop = Math.round(vw * 0.74) - rOff, tStop = tD + Math.round((sStop - sD0) / v);
    var sPileC = sStop + stPx(84 + lfRx(K)), sLean = sStop - stPx(26); // the pile's center once parked; where he steps back to
    var headX = function(s) { return lr ? X(s, DW) + stPx(30) : X(s, DW) + DW - stPx(30); };
    var danny = null, k = 0;
    at(tD, function() {
      danny = keep(stSpawn(lfDannyHtml('rake', 0), DW, DH, 4, X(sD0, DW), 0, flip));
      stMove(danny, X(sStop, DW), 0, tStop - tD, 'linear', flip);
    });
    leaves.forEach(function(p) {
      at(tD + Math.round((p.spot + LW / 2 - rOff - sD0) / v), function() {
        drop(p.el);
        k++;
        if (danny) danny.innerHTML = lfDannyHtml('rake', k);
      });
    });
    // He parks the pile, steps back and leans on the rake
    var pile = null;
    at(tStop, function() {
      pile = keep(stSpawn(lfPileSvg(k), PW, PH, 4, X(sPileC - PW / 2, PW), 0, ''));
      danny.innerHTML = lfDannyHtml('lean', 0);
      stMove(danny, X(sLean, DW), 0, 600, 'ease-out', flip);
    });
    at(tStop + 800, function() {
      var b = keep(stBubble('phew!', headX(sLean) - 12, 4 + stPx(96)));
      at(1500, function() { drop(b); });
    });

    // The cat creeps in from the far side, crouches beside the pile, and leaps into it
    var sC0 = vw + 20, sCrouch = sPileC + stPx(lfRx(K) + 12), sLand = sPileC - CW / 2;
    var tCat = tStop + 900, dCatIn = Math.max(1000, Math.min(4500, Math.round((sC0 - sCrouch) / 0.26)));
    var tCrouch = tCat + dCatIn, tPounce = tCrouch + 1100, tLand = tPounce + 520;
    var cat = null;
    at(tCat, function() {
      cat = keep(stSpawn(lfCatHtml('walk'), CW, CH, 4, X(sC0, CW), 0, back));
      stMove(cat, X(sCrouch, CW), 0, dCatIn, 'linear', back);
    });
    at(tCrouch, function() { cat.innerHTML = lfCatHtml('crouch'); });
    at(tPounce, function() {
      cat.innerHTML = lfCatHtml('leap');
      stMove(cat, X((sCrouch + sLand) / 2, CW), -stPx(34), 270, 'ease-out', back);
    });
    at(tPounce + 270, function() { stMove(cat, X(sLand, CW), 0, 250, 'ease-in', back); });
    // Splash: the pile flattens, eight leaves fly out and flutter down, the cat sits up in the wreck
    at(tLand, function() {
      pile.innerHTML = lfScatterSvg();
      cat.style.height = stPx(60) + 'px';
      cat.innerHTML = lfCatHtml('sit');
      for (var i = 0; i < 8; i++) (function(i) {
        var dx = (i % 2 ? 1 : -1) * stPx(36 + ((i * 41) % 5) * 26), up = stPx(34 + ((i * 29) % 4) * 18);
        var fl = '<div class="stfl" style="animation-duration:' + (0.9 + (i % 3) * 0.15).toFixed(2) + 's;animation-delay:-' + (i * 0.23).toFixed(2) + 's">' + lfLeafSvg(i + 3) + '</div>';
        var el = keep(stSpawn(fl, LW, LH, floor, X(sPileC - LW / 2, LW), -stPx(8), ''));
        stMove(el, X(sPileC - LW / 2 + dx, LW), -up, 420, 'cubic-bezier(0.2,0.6,0.4,1)');
        at(420, function() { stMove(el, X(sPileC - LW / 2 + dx * 1.3, LW), 0, 800 + i * 70, 'cubic-bezier(0.5,0,0.8,0.6)'); });
        at(1220 + i * 70, function() { lfFlat(el, i + 5); });
      })(i);
    });
    // "!" — Danny drops the rake (it stands for a beat, then keels over) and throws up his hands
    var exc = null;
    at(tLand + 120, function() {
      exc = keep(document.createElement('div'));
      exc.textContent = '!';
      exc.style.cssText = 'position:fixed;z-index:99999;pointer-events:none;left:' + (headX(sLean) - 6) + 'px;bottom:' + (4 + stPx(98)) + 'px;font:800 ' + stPx(30) + 'px "DM Sans", system-ui, sans-serif;color:#FBBF24;text-shadow:2px 0 #111,-2px 0 #111,0 2px #111,0 -2px #111;';
      document.body.appendChild(exc);
    });
    at(tLand + 1000, function() { drop(exc); });
    at(tLand + 450, function() {
      danny.innerHTML = lfDannyHtml('drop', 0);
      var rake = keep(stSpawn('<div class="lfrake">' + lfRakeSvg() + '</div>', RW, RH, 4, X(sLean + stPx(32), RW), 0, flip));
      at(80, function() { rake.firstChild.style.transform = 'rotate(80deg)'; });
    });
    // He turns and stomps off the way he came (the element shifts so his feet stay put when it
    // mirrors); the cat picks a leaf and trots off its own way
    var tWalk = tLand + 1600, sTurn = sLean - stPx(100), dWalk = Math.round((sTurn - sD0) / (v * 0.85));
    at(tWalk, function() {
      danny.innerHTML = lfDannyHtml('walk', 0);
      danny.style.transition = 'none';
      danny.style.transform = 'translate(' + X(sTurn, DW) + 'px,0px)' + back;
      danny.getBoundingClientRect();
      stMove(danny, X(sD0, DW), 0, dWalk, 'linear', back);
    });
    var tCatOut = tLand + 2000, dCatOut = Math.round((sC0 - sLand) / 0.3);
    at(tCatOut, function() {
      cat.style.height = CH + 'px';
      cat.innerHTML = lfCatHtml('carry');
      cat.style.transition = 'none';
      cat.style.transform = 'translate(' + X(sLand, CW) + 'px,0px)' + flip;
      cat.getBoundingClientRect();
      stMove(cat, X(sC0, CW), 0, dCatOut, 'linear', flip);
    });
    var tEnd = Math.max(tWalk + dWalk, tCatOut + dCatOut) + 300;
    at(tEnd - 700, function() { els.forEach(function(el) { if (el === danny || el === cat) return; el.style.transition = 'opacity 600ms ease-in'; el.style.opacity = '0'; }); });
    at(tEnd, function() { els.forEach(drop); });
  } catch (e) {}
}
window.leafRun = leafRun;

// ─── THANKSGIVING POTLUCK (the hourly cartoon over the seven days ending on Thanksgiving) ───
// Emma, Sage and Danny stroll across the bottom of the page toward the break room, each
// carrying their dish: Emma a pumpkin pie, Sage a casserole, Danny the big silver platter
// with a dome over the turkey. Danny stops mid-screen when the dome starts to jiggle; it
// pops off and lands upside-down behind him, and the roast turkey — drumsticks for legs —
// hops off the platter ("gobble!") and sprints back the way they came. "!" — Danny drops
// the platter (the garnish drumstick bounces off it), turns and chases the bird; Emma and
// Sage walk on, none the wiser. The Filing floor's gray cat creeps in, sniffs the
// drumstick on the floor and trots off with it. Anthony 2026-11-01. Same rig and helpers
// as the Storm, the parade and the Leaf Pile. window.potluckRun() = demo.
var TG = {
  turkey: '#B45309', turkeyHi: '#D97706', bone: '#F5F5F4', silver: '#CBD5E1', silverDk: '#94A3B8', silverHi: '#E5E7EB', dome: '#D1D5DB',
  crust: '#D9A066', pumpkin: '#C2410C', cream: '#FFFBEB', dish: '#F8FAFC', dishLn: '#CBD5E1', lid: '#E5E7EB',
};
var TG_WHO = {
  emma: { skin: HW.emma.skin, shirt: '#14B8A6', shirtDk: '#0F9488', pantsA: '#374151', pantsB: '#3F4A5A', head: function() { return hwHead(HW.emma, 'ponytail'); } },
  sage: { skin: HW.sage.skin, shirt: '#84CC16', shirtDk: '#65A30D', pantsA: HW.pants, pantsB: HW.pantsB, head: function() { return hwHead(HW.sage, 'bob'); } },
  danny: { skin: ST.skin, shirt: ST.shirt, shirtDk: ST.shirtDk, pantsA: FK.pantsA, pantsB: FK.pantsB, head: function() { return stDannyHead(0, -68); } },
};
// A cloche: half-disc of radius 14 standing on (cx, by), knob on top
function tgDome(cx, by) {
  return '<path d="M ' + (cx - 14) + ',' + by + ' A 14,14 0 0 1 ' + (cx + 14) + ',' + by + ' Z" fill="' + TG.dome + '" stroke="' + TG.silverDk + '" stroke-width="0.9"/>' +
    '<path d="M ' + (cx - 9) + ',' + (by - 6) + ' A 11,11 0 0 1 ' + (cx - 2) + ',' + (by - 12.5) + '" stroke="' + TG.silverHi + '" stroke-width="1.6" fill="none" stroke-linecap="round"/>' +
    fkRect(cx - 1, by - 17, 2, 3.5, 0.8, TG.silverDk) + fkCircle(cx, by - 17.5, 2.4, TG.silverDk);
}
function tgDomeSvg() { return forkSvgWrap(tgDome(16, 21), 32, 22, stPx(32), stPx(22), ''); }
function tgPlatter(cx, cy) {
  return '<ellipse cx="' + cx + '" cy="' + cy + '" rx="17" ry="3.8" fill="' + TG.silver + '" stroke="' + TG.silverDk + '" stroke-width="0.8"/><ellipse cx="' + cx + '" cy="' + (cy - 0.4) + '" rx="13" ry="2.2" fill="' + TG.silverHi + '"/>';
}
function tgPlatterSvg() { return forkSvgWrap(tgPlatter(20, 5), 40, 10, stPx(40), stPx(10), ''); }
// A drumstick (16×12): meaty end at the left, bone and knuckles to the right
function tgDrumstick() {
  return '<ellipse cx="6" cy="6.5" rx="5.2" ry="3.8" fill="' + TG.turkey + '" transform="rotate(-20 6 6.5)"/><line x1="9.5" y1="5" x2="13.5" y2="2.8" stroke="' + TG.bone + '" stroke-width="1.9" stroke-linecap="round"/>' +
    fkCircle(14, 2.3, 1.6, TG.bone) + fkCircle(13.2, 4.4, 1.3, TG.bone);
}
function tgDrumSvg() { return forkSvgWrap(tgDrumstick(), 16, 12, stPx(16), stPx(12), ''); }
// What each of them carries, held out front at chest height (figure coords, feet at 0,0)
function tgDish(who, withDome) {
  if (who === 'emma') return '<ellipse cx="26" cy="-44" rx="13" ry="4.2" fill="' + TG.crust + '"/><ellipse cx="26" cy="-45" rx="10.8" ry="2.8" fill="' + TG.pumpkin + '"/>' + fkCircle(26, -47.2, 2.2, TG.cream);
  if (who === 'sage') return '<ellipse cx="11.5" cy="-45" rx="2.4" ry="1.7" fill="' + TG.dish + '"/><ellipse cx="40.5" cy="-45" rx="2.4" ry="1.7" fill="' + TG.dish + '"/>' +
    fkRect(13, -49, 26, 8, 2.5, TG.dish, ' stroke="' + TG.dishLn + '" stroke-width="0.8"') + fkRect(14.5, -52, 23, 3.6, 1.6, TG.lid) + fkCircle(26, -53.5, 1.7, TG.silverDk);
  return tgPlatter(26, -44) + (withDome ? tgDome(26, -45) : '');
}
// A walker (90×112, feet at 40,106): 'carry' strolls with the dish, 'stand' holds it still,
// 'drop' throws both hands up, 'chase' sprints with both arms reaching ahead
function tgFigure(who, pose, which, withDome) {
  var B = which === 'B', lk = TG_WHO[who], k = [fkEllipse(0, 1.5, 18, 2.8, FK.shadow)];
  if (pose === 'drop') k.push(fkRect(-9.5, -28, 8, 28, 4, lk.pantsA, ' transform="rotate(-8 -5.5 -26)"') + fkRect(1.5, -28, 8, 28, 4, lk.pantsB, ' transform="rotate(8 5.5 -26)"'));
  else k.push(hwLegs(B, lk.pantsA, lk.pantsB, pose === 'carry' ? 16 : pose === 'chase' ? 30 : 3));
  var lean = pose === 'chase' ? 12 : pose === 'drop' ? -6 : 0, S = lfShoulder(lean);
  var torso = fkG(fkRect(-13, -58, 26, 36, 10, lk.shirt) + lk.head(), 'rotate(' + lean + ' 0 -28)');
  var H1, H2, held = '';
  if (pose === 'carry' || pose === 'stand') { H1 = [21, -42]; H2 = [19, -46]; held = tgDish(who, withDome); }
  else if (pose === 'drop') { H1 = [-16, B ? -79 : -80]; H2 = [14, B ? -81 : -82]; }
  else { H1 = [25, B ? -52 : -50]; H2 = [27, B ? -56 : -58]; }
  var u = lfArm(S[0], S[1], H2[0], H2[1], lk.shirtDk, lk.skin) + torso + held + lfArm(S[0], S[1], H1[0], H1[1], lk.shirt, lk.skin);
  k.push(fkG(u, 'translate(0,' + (B && (pose === 'carry' || pose === 'chase') ? -1.2 : 0) + ')'));
  return fkG(k.join(''), 'translate(40,106)');
}
function tgHtml(who, pose, withDome) {
  var mk = function(which) { return function(cls) { return forkSvgWrap(tgFigure(who, pose, which, withDome), 90, 112, stPx(90), stPx(112), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), pose === 'chase' ? ' fast' : ' wv');
}
// The roast turkey (40×28, floor at 27): 'sit' on the platter with its drumsticks tucked up
// behind, 'run' with the drumsticks scissoring underneath
function tgTurkeyFrame(pose, which) {
  var B = which === 'B', k = [];
  var leg = function(x, y, ang) {
    return fkG('<ellipse cx="0" cy="0" rx="5" ry="3.6" fill="' + TG.turkey + '"/><line x1="3" y1="1" x2="9" y2="7" stroke="' + TG.bone + '" stroke-width="2" stroke-linecap="round"/>' + fkCircle(9.5, 7.5, 1.7, TG.bone) + fkCircle(8, 9.2, 1.4, TG.bone),
      'translate(' + x + ',' + y + ') rotate(' + ang + ')');
  };
  if (pose === 'sit') {
    k.push(leg(9, 13, -150) + leg(10, 17, -168));
    k.push('<ellipse cx="20" cy="18" rx="13" ry="8.5" fill="' + TG.turkey + '"/><ellipse cx="16" cy="14" rx="6.5" ry="3" fill="' + TG.turkeyHi + '"/>');
  } else {
    k.push(fkEllipse(20, 27, 12, 1.8, FK.shadow));
    k.push(leg(16, 19, B ? 40 : 95) + leg(23, 19, B ? 95 : 40));
    k.push(fkG('<ellipse cx="20" cy="14" rx="13" ry="8" fill="' + TG.turkey + '"/><ellipse cx="16" cy="10" rx="6.5" ry="3" fill="' + TG.turkeyHi + '"/>', 'rotate(' + (B ? 5 : 9) + ' 20 14)'));
  }
  return k.join('');
}
function tgTurkeyHtml(pose) {
  var mk = function(which) { return function(cls) { return forkSvgWrap(tgTurkeyFrame(pose, which), 40, 28, stPx(34), stPx(24), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), pose === 'sit' ? ' wv' : ' fast');
}
function tgCatHtml(pose) {
  var mk = function(which) { return function(cls) {
    var g = stCatFrame(pose === 'carry' ? 'walk' : pose, which);
    if (pose === 'carry') g += fkG(tgDrumstick(), 'translate(26,41) translate(13,-25) rotate(25) scale(0.9)');
    return forkSvgWrap(g, 58, 44, stPx(58), stPx(44), cls);
  }; };
  return catTwoFrames(mk('A'), mk('B'), pose === 'crouch' ? ' wv' : ' fast');
}
function tgEnsureStyle() {
  stEnsureStyle();
  if (document.getElementById('potluck-style')) return;
  var st = document.createElement('style');
  st.id = 'potluck-style';
  st.textContent =
    '@keyframes tgBob { 0%, 100% { transform: translateY(0) rotate(0); } 50% { transform: translateY(-5px) rotate(-9deg); } }' +
    '.tgbob { position: absolute; inset: 0; animation: tgBob 0.45s ease-in-out infinite; }' +
    '.tgspin { position: absolute; inset: 0; transform: rotate(170deg); transition: transform 800ms ease-out; }' +
    '.tgtilt { position: absolute; inset: 0; transition: transform 380ms ease-in; }';
  document.head.appendChild(st);
}
function potluckRun() {
  try {
    tgEnsureStyle();
    var vw = window.innerWidth;
    var lr = Math.random() < 0.5; // which way the break room is
    var flip = lr ? '' : ' scaleX(-1)', back = lr ? ' scaleX(-1)' : '';
    var X = function(s, w) { return Math.round(lr ? s : vw - s - w); };
    var W = stPx(90), H = stPx(112), CW = stPx(58), CH = stPx(44), TW = stPx(34), TH = stPx(24), DMW = stPx(32), DMH = stPx(22), PLW = stPx(40), PLH = stPx(10), DRW = stPx(16), DRH = stPx(12);
    var els = [];
    var keep = function(el) { els.push(el); return el; };
    var drop = function(el) { try { if (el) el.remove(); } catch (e) {} };
    var at = function(ms, fn) { setTimeout(function() { try { fn(); } catch (e) {} }, ms); };
    var floorY = function(bottom) { return bottom - (4 + stPx(6)); }; // translateY that puts an element's bottom edge on the figures' floor line
    var v = (vw + W + 40) / 9000; // the Office stroll, as in the parade
    var tCross = Math.round((vw + W + 40) / v);

    // Emma and Sage stroll straight across with their dishes
    [[0, 'emma'], [1300, 'sage']].forEach(function(a) {
      at(a[0], function() {
        var el = keep(stSpawn(tgHtml(a[1], 'carry'), W, H, 4, X(-W - 20, W), 0, flip));
        stMove(el, X(vw + 20, W), 0, tCross, 'linear', flip);
      });
    });

    // Danny brings up the rear with the turkey under its dome and stops mid-screen
    var tD = 2600, sD0 = -W - 20, sStop = Math.round(vw / 2 - W / 2), tStop = tD + Math.round((sStop - sD0) / v);
    var headX = function(s) { return lr ? X(s, W) + stPx(40) : X(s, W) + W - stPx(40); };
    var danny = null;
    at(tD, function() {
      danny = keep(stSpawn(tgHtml('danny', 'carry', true), W, H, 4, X(sD0, W), 0, flip));
      stMove(danny, X(sStop, W), 0, tStop - tD, 'linear', flip);
    });
    at(tStop, function() { danny.innerHTML = tgHtml('danny', 'stand', true); });
    // The dome starts to jiggle: it leaves his frame for its own element, bobbing
    var sDome = sStop + stPx(50), domeBottom = 4 + stPx(50), dome = null;
    at(tStop + 500, function() {
      danny.innerHTML = tgHtml('danny', 'stand', false);
      dome = keep(stSpawn('<div class="tgbob">' + tgDomeSvg() + '</div>', DMW, DMH, domeBottom, X(sDome, DMW), 0, flip));
    });
    // ...then pops off, flips over in the air and lands upside-down behind him
    var tPop = tStop + 1500, sDomeLand = sStop - stPx(34);
    at(tPop, function() {
      dome.firstChild.className = 'tgspin';
      stMove(dome, X((sDome + sDomeLand) / 2, DMW), -stPx(46), 380, 'cubic-bezier(0.2,0.6,0.4,1)', flip);
    });
    at(tPop + 380, function() { stMove(dome, X(sDomeLand, DMW), floorY(domeBottom), 420, 'cubic-bezier(0.5,0,0.9,0.6)', flip); });
    at(tPop + 800, function() { stMove(dome, X(sDomeLand, DMW), floorY(domeBottom) - stPx(6), 120, 'ease-out', flip); });
    at(tPop + 920, function() { stMove(dome, X(sDomeLand, DMW), floorY(domeBottom), 120, 'ease-in', flip); });

    // The turkey sits up on the platter facing the way they came, hops down, "gobble!", and sprints off
    var sTurk = sStop + stPx(66) - TW / 2, turkBottom = 4 + stPx(50), sTurkDown = sStop - stPx(44), turkey = null;
    at(tPop, function() { turkey = keep(stSpawn(tgTurkeyHtml('sit'), TW, TH, turkBottom, X(sTurk, TW), 0, back)); });
    var tHop = tPop + 350;
    at(tHop, function() {
      turkey.innerHTML = tgTurkeyHtml('run');
      stMove(turkey, X((sTurk + sTurkDown) / 2, TW), -stPx(14), 200, 'ease-out', back);
    });
    at(tHop + 200, function() { stMove(turkey, X(sTurkDown, TW), floorY(turkBottom), 260, 'ease-in', back); });
    at(tHop + 460, function() {
      var b = keep(stBubble('gobble!', X(sTurkDown, TW) + Math.round(TW / 2) - 22, 4 + stPx(40)));
      at(1100, function() { drop(b); });
    });
    var tRun = tHop + 800, dTurk = Math.round((sTurkDown + TW + 20) / 0.55);
    at(tRun, function() { stMove(turkey, X(-TW - 20, TW), floorY(turkBottom), dTurk, 'linear', back); });

    // "!" — Danny drops the platter (the garnish drumstick bounces off it), then turns and gives chase
    var exc = null;
    at(tHop + 150, function() {
      exc = keep(document.createElement('div'));
      exc.textContent = '!';
      exc.style.cssText = 'position:fixed;z-index:99999;pointer-events:none;left:' + (headX(sStop) - 6) + 'px;bottom:' + (4 + stPx(114)) + 'px;font:800 ' + stPx(30) + 'px "DM Sans", system-ui, sans-serif;color:#FBBF24;text-shadow:2px 0 #111,-2px 0 #111,0 2px #111,0 -2px #111;';
      document.body.appendChild(exc);
    });
    at(tHop + 1050, function() { drop(exc); });
    var sPlat = sStop + stPx(46), platBottom = 4 + stPx(45), sDrum = sStop + stPx(66) + stPx(30) - DRW / 2, drumBottom = 4 + stPx(50), drum = null;
    at(tHop + 500, function() {
      danny.innerHTML = tgHtml('danny', 'drop');
      var plat = keep(stSpawn('<div class="tgtilt">' + tgPlatterSvg() + '</div>', PLW, PLH, platBottom, X(sPlat, PLW), 0, flip));
      drum = keep(stSpawn(tgDrumSvg(), DRW, DRH, drumBottom, X(sStop + stPx(66) - DRW / 2, DRW), 0, flip));
      at(30, function() {
        plat.firstChild.style.transform = 'rotate(-18deg)';
        stMove(plat, X(sPlat + stPx(6), PLW), floorY(platBottom), 380, 'cubic-bezier(0.5,0,1,1)', flip);
        stMove(drum, X(sDrum, DRW), -stPx(22), 220, 'ease-out', flip);
      });
      at(250, function() { stMove(drum, X(sDrum, DRW), floorY(drumBottom), 300, 'ease-in', flip); });
      at(410, function() { plat.firstChild.style.transition = 'transform 160ms ease-out'; plat.firstChild.style.transform = 'rotate(3deg)'; });
    });
    var tChase = tHop + 1200, sTurn = sStop - stPx(10), dChase = Math.round((sTurn + W + 20) / 0.42);
    at(tChase, function() {
      danny.innerHTML = tgHtml('danny', 'chase');
      danny.style.transition = 'none';
      danny.style.transform = 'translate(' + X(sTurn, W) + 'px,0px)' + back;
      danny.getBoundingClientRect();
      stMove(danny, X(-W - 20, W), 0, dChase, 'linear', back);
    });

    // The cat creeps in from the far side, sniffs the drumstick, and trots off with it
    var sC0 = vw + 20, sSniff = sDrum + DRW + stPx(4), tCat = tHop + 1900, dCatIn = Math.max(900, Math.min(4500, Math.round((sC0 - sSniff) / 0.26)));
    var tSniff = tCat + dCatIn, tTake = tSniff + 700, dCatOut = Math.round((sC0 - sSniff) / 0.3), cat = null;
    at(tCat, function() {
      cat = keep(stSpawn(tgCatHtml('walk'), CW, CH, 4, X(sC0, CW), 0, back));
      stMove(cat, X(sSniff, CW), 0, dCatIn, 'linear', back);
    });
    at(tSniff, function() { cat.innerHTML = tgCatHtml('crouch'); });
    at(tTake, function() {
      drop(drum);
      cat.innerHTML = tgCatHtml('carry');
      cat.style.transition = 'none';
      cat.style.transform = 'translate(' + X(sSniff, CW) + 'px,0px)' + flip;
      cat.getBoundingClientRect();
      stMove(cat, X(sC0, CW), 0, dCatOut, 'linear', flip);
    });
    var tEnd = Math.max(1300 + tCross, tChase + dChase, tRun + dTurk, tTake + dCatOut) + 300;
    at(tEnd - 700, function() { els.forEach(function(el) { if (el === danny || el === cat || el === turkey) return; el.style.transition = 'opacity 600ms ease-in'; el.style.opacity = '0'; }); });
    at(tEnd, function() { els.forEach(drop); });
  } catch (e) {}
}
window.potluckRun = potluckRun;

// ─── BLACK FRIDAY STAMPEDE (the hourly cartoon from Black Friday through Cyber Monday) ───
// Emma walks in and plants herself by the edge, headset on: "ding!" "ding!" "ding!" — the
// orders are pouring in. A tidal wave of parcels and two runaway shopping carts races
// across the bottom of the page. Tracy (Logistics, her desk look) stands mid-screen facing
// the flood with a sheet of labels and slaps one onto every box as it rushes past. The
// forklift crawls through behind the wave with both stacks piled seven high and teetering
// ("beep! beep!"); Emma tags along behind it; last of all the Filing floor's gray cat
// rides a box out, and Tracy calls it a day. Anthony 2026-11-01. Same rig as the Storm;
// the boxes and the lift are the Forklift Fumble's. window.stampedeRun() = demo.
var BF = {
  tracy: { hair: '#B8BCC4', skin: '#E8B98A', shirt: '#0EA5E9', shirtDk: '#0284C7', pantsA: '#1F2937', pantsB: '#2B3444' }, // officeLook('tracking-claims'): curly, hoop earrings
  emma: { shirt: '#14B8A6', shirtDk: '#0F9488', pantsA: '#374151', pantsB: '#3F4A5A' },
  label: '#FFFFFF', labelLn: '#94A3B8', bar: '#1F2937', cart: '#9CA3AF', cartDk: '#6B7280', tire: '#0B0F16', hoop: '#F2CF6B',
};
function bfBarcode(x, y, w, h) { var s = ''; for (var i = 0; i < 6; i++) s += fkRect(x + i * (w / 6), y, i % 3 === 0 ? 1.1 : 0.6, h, 0, BF.bar); return s; }
function bfLabel() { return fkRect(0, 0, 10, 6, 0.8, BF.label, ' stroke="' + BF.labelLn + '" stroke-width="0.5"') + bfBarcode(1.2, 1.3, 7.5, 3.2); }
// A parcel from the forklift's stacks, with Tracy's label slapped on once she's had it
function bfBoxHtml(labeled) {
  return forkSvgWrap(fkBox(1, 1, 22, 12) + (labeled ? fkG(bfLabel(), 'translate(5.5,3.4) scale(0.95)') : ''), 24, 14, fkPx(36), fkPx(21), '');
}
// Tracy's head at the walker scale: a halo of curls, the fringe cap, a hoop earring
function bfTracyHead() {
  var lk = BF.tracy, r = 11.5, cx = 0, cy = -68, k = [];
  [[-10, -8], [-6, -11.5], [0, -12.5], [6, -11.5], [10, -8], [-12.5, -2], [12.5, -2], [-12, 4.5], [12, 4.5]].forEach(function(p) { k.push(fkCircle(cx + p[0], cy + p[1], 4.4, lk.hair)); });
  k.push(fkCircle(cx, cy, r, lk.skin) + fkCircle(cx + 0.5 * r, cy - 0.05 * r, 0.14 * r, FK.eye));
  k.push('<path d="' + HW_CAP_SHORT + '" fill="' + lk.hair + '"/>');
  k.push('<circle cx="' + (cx - 0.6 * r) + '" cy="' + (cy + 0.95 * r) + '" r="1.7" fill="none" stroke="' + BF.hoop + '" stroke-width="0.9"/>');
  return k.join('');
}
// Tracy (90×112, feet at 40,106): 'walk' in, 'stand' facing the flood with her label sheet,
// 'slap' with the near hand down on a passing box
function bfTracyFrame(pose, which) {
  var B = which === 'B', lk = BF.tracy, k = [fkEllipse(0, 1.5, 18, 2.8, FK.shadow)];
  k.push(hwLegs(B, lk.pantsA, lk.pantsB, pose === 'walk' ? 16 : pose === 'slap' ? 10 : 2));
  var lean = pose === 'slap' ? 16 : 0, S = lfShoulder(lean);
  var torso = fkG(fkRect(-13, -58, 26, 36, 10, lk.shirt) + bfTracyHead(), 'rotate(' + lean + ' 0 -28)');
  var sheet = fkG(fkRect(0, 0, 13, 17, 1.2, BF.label, ' stroke="' + BF.labelLn + '" stroke-width="0.7"') + bfBarcode(2, 3, 9, 4) + bfBarcode(2, 10, 9, 4), 'translate(13,-55) rotate(-12)');
  var H1 = pose === 'slap' ? [30, B ? -25 : -27] : pose === 'walk' ? (B ? [-7, -32] : [9, -30]) : [18, B ? -34 : -35];
  var H2 = pose === 'walk' ? (B ? [9, -30] : [-7, -32]) : [15, -47];
  var u = lfArm(S[0], S[1], H2[0], H2[1], lk.shirtDk, lk.skin) + torso + (pose === 'walk' ? '' : sheet) + lfArm(S[0], S[1], H1[0], H1[1], lk.shirt, lk.skin);
  if (pose === 'slap') u += fkG(bfLabel(), 'translate(' + (H1[0] + 1) + ',' + (H1[1] - 2) + ') rotate(22)');
  k.push(fkG(u, 'translate(0,' + (B && pose === 'walk' ? -1.2 : 0) + ')'));
  return fkG(k.join(''), 'translate(40,106)');
}
function bfTracyHtml(pose) {
  var mk = function(which) { return function(cls) { return forkSvgWrap(bfTracyFrame(pose, which), 90, 112, stPx(90), stPx(112), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), pose === 'walk' ? '' : ' wv');
}
// Emma (desk look, headset): 'walk', or 'stand' with a hand to her earpiece as the orders ding in
function bfEmmaFrame(pose, which) {
  var B = which === 'B', lk = BF.emma, k = [fkEllipse(0, 1.5, 18, 2.8, FK.shadow)];
  k.push(hwLegs(B, lk.pantsA, lk.pantsB, pose === 'walk' ? 16 : 2));
  var S = lfShoulder(0);
  var torso = fkRect(-13, -58, 26, 36, 10, lk.shirt) + hwHead(HW.emma, 'ponytail');
  var H1 = pose === 'walk' ? (B ? [-7, -32] : [9, -30]) : [-8, B ? -65 : -64], H2 = pose === 'walk' ? (B ? [9, -30] : [-7, -32]) : [-6, -31];
  var u = lfArm(S[0], S[1], H2[0], H2[1], lk.shirtDk, HW.emma.skin) + torso + lfArm(S[0], S[1], H1[0], H1[1], lk.shirt, HW.emma.skin);
  k.push(fkG(u, 'translate(0,' + (B && pose === 'walk' ? -1.2 : 0) + ')'));
  return fkG(k.join(''), 'translate(40,106)');
}
function bfEmmaHtml(pose) {
  var mk = function(which) { return function(cls) { return forkSvgWrap(bfEmmaFrame(pose, which), 90, 112, stPx(90), stPx(112), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), pose === 'walk' ? '' : ' wv');
}
// A runaway shopping cart (60×44, floor at 43) heaped with parcels, handle at the back
function bfCartFrame(which) {
  var B = which === 'B', k = [fkEllipse(31, 43, 26, 2.2, FK.shadow)];
  k.push(fkBox(17, 4, 16, 9) + fkBox(32, 6, 14, 8) + fkBox(24, -3, 13, 8));
  k.push('<path d="M 12,12 L 52,12 L 48,32 L 16,32 Z" fill="' + BF.cart + '" opacity="0.45" stroke="' + BF.cartDk + '" stroke-width="1.4" stroke-linejoin="round"/>');
  [18, 24, 29].forEach(function(y) { k.push('<line x1="' + (13.5 + (y - 12) * 0.2).toFixed(1) + '" y1="' + y + '" x2="' + (50.5 - (y - 12) * 0.2).toFixed(1) + '" y2="' + y + '" stroke="' + BF.cartDk + '" stroke-width="0.8"/>'); });
  [22, 32, 42].forEach(function(x) { k.push('<line x1="' + x + '" y1="12" x2="' + (x - (x - 32) * 0.1).toFixed(1) + '" y2="32" stroke="' + BF.cartDk + '" stroke-width="0.8"/>'); });
  k.push('<path d="M 12,12 L 4,2" stroke="' + BF.cartDk + '" stroke-width="2.2" stroke-linecap="round"/>' + fkRect(0, 0.5, 9, 3, 1.5, BF.cartDk));
  k.push('<path d="M 16,32 L 16,38 M 48,32 L 48,38" stroke="' + BF.cartDk + '" stroke-width="1.6"/>');
  [[16, 39.5], [48, 39.5]].forEach(function(w) { k.push(fkCircle(w[0], w[1], 3.6, BF.tire) + fkG(fkRect(w[0] - 3, w[1] - 0.5, 6, 1, 0.5, BF.cart), 'rotate(' + (B ? 45 : 0) + ' ' + w[0] + ' ' + w[1] + ')') + fkCircle(w[0], w[1], 1.1, BF.cart)); });
  return k.join('');
}
function bfCartHtml() {
  var mk = function(which) { return function(cls) { return forkSvgWrap(bfCartFrame(which), 60, 48, fkPx(90), fkPx(72), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), ' fast');
}
// The Forklift Fumble's lift with both stacks seven boxes high, teetering ('wob' frames),
// in a viewBox tall enough to hold them
function bfForkliftHtml() {
  var extra = 38;
  var mk = function(which) { return function(cls) { return forkSvgWrap(fkG(forkliftFrame('wob', [7, 7], which), 'translate(0,' + extra + ')'), 126, 70 + extra, fkPx(189), fkPx(Math.round(105 * (70 + extra) / 70)), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), '');
}
// The gray cat sitting on a parcel, facing the screen (58×68, box on the floor)
function bfCatRideHtml() {
  var mk = function(which) { return function(cls) {
    var B = which === 'B', c = ST.cat, p = [
      '<path d="' + (B ? 'M 6,-4 Q 18,-4 20,-14' : 'M 6,-4 Q 19,-6 18,-16') + '" stroke="' + c + '" stroke-width="4.5" fill="none" stroke-linecap="round"/>',
      '<ellipse cx="-2" cy="-13" rx="11" ry="12.5" fill="' + c + '"/>',
      fkRect(-9, -12, 4.5, 12, 2.2, c) + fkRect(1, -12, 4.5, 12, 2.2, c),
      fkCircle(-1, -29, 7.5, c) + stCatEars(-13, -7) + fkCircle(-3.6, -30, 1.2, FK.eye) + fkCircle(1.6, -30, 1.2, FK.eye)];
    return forkSvgWrap(fkBox(5, 46, 36, 20) + fkG(p.join(''), 'translate(23,46)'), 58, 68, stPx(58), stPx(68), cls);
  }; };
  return catTwoFrames(mk('A'), mk('B'), ' wv');
}
function bfEnsureStyle() {
  stEnsureStyle();
  if (document.getElementById('stampede-style')) return;
  var st = document.createElement('style');
  st.id = 'stampede-style';
  st.textContent =
    '@keyframes bfHop { 0%, 100% { transform: translateY(0) rotate(0); } 50% { transform: translateY(-9px) rotate(-4deg); } }' +
    '.bfhop { position: absolute; inset: 0; animation: bfHop 0.42s ease-in-out infinite; }';
  document.head.appendChild(st);
}
function stampedeRun() {
  try {
    bfEnsureStyle();
    FORK_PAL.h = ['#F1F5F9', '#FBBF24', '#3B82F6'][Math.floor(Math.random() * 3)];
    var vw = window.innerWidth;
    var lr = Math.random() < 0.5; // which way the flood runs
    var flip = lr ? '' : ' scaleX(-1)', back = lr ? ' scaleX(-1)' : '';
    var X = function(s, w) { return Math.round(lr ? s : vw - s - w); };
    var W = stPx(90), H = stPx(112), BW = fkPx(36), BH = fkPx(21), KW = fkPx(90), KH = fkPx(72), LW = fkPx(189), LH = fkPx(162), RW = stPx(58), RH = stPx(68);
    var els = [];
    var keep = function(el) { els.push(el); return el; };
    var drop = function(el) { try { if (el) el.remove(); } catch (e) {} };
    var at = function(ms, fn) { setTimeout(function() { try { fn(); } catch (e) {} }, ms); };
    var hop = function(html, period, delay) { return '<div class="bfhop" style="animation-duration:' + period.toFixed(2) + 's;animation-delay:-' + delay.toFixed(2) + 's">' + html + '</div>'; };

    // Emma walks in to the edge and takes the calls: "ding!" x3
    var v = (vw + W + 40) / 9000, sE = Math.round(vw * 0.06), tE = Math.round((sE + W + 20) / v), emma = null;
    var headXE = function(s) { return lr ? X(s, W) + stPx(40) : X(s, W) + W - stPx(40); };
    at(0, function() {
      emma = keep(stSpawn(bfEmmaHtml('walk'), W, H, 4, X(-W - 20, W), 0, flip));
      stMove(emma, X(sE, W), 0, tE, 'linear', flip);
    });
    at(tE, function() { emma.innerHTML = bfEmmaHtml('stand'); });
    [0, 1, 2].forEach(function(i) {
      at(tE + 400 + 480 * i, function() {
        var b = keep(stBubble('ding!', headXE(sE) - 24 + i * 30, 4 + stPx(114) + i * 12));
        at(1500, function() { drop(b); });
      });
    });

    // Tracy hurries in from the far side and stands mid-screen facing the flood, label sheet ready
    var sT = Math.round(vw / 2) - W + stPx(70), sHand = sT + W - stPx(70), tT = Math.round((vw + 20 - sT) / 0.4), tracy = null;
    at(0, function() {
      tracy = keep(stSpawn(bfTracyHtml('walk'), W, H, 4, X(vw + 20, W), 0, back));
      stMove(tracy, X(sT, W), 0, tT, 'linear', back);
    });
    at(tT, function() { tracy.innerHTML = bfTracyHtml('stand'); });
    var slapTimer = 0;
    var slap = function() {
      if (!tracy) return;
      tracy.innerHTML = bfTracyHtml('slap');
      slapTimer++;
      var mine = slapTimer;
      at(190, function() { if (slapTimer === mine) tracy.innerHTML = bfTracyHtml('stand'); });
    };

    // The wave: fourteen parcels and two carts race through; each box gets its label as it passes her hand
    var sBox0 = -BW - 20;
    for (var i = 0; i < 14; i++) (function(i) {
      var t0 = 1800 + 110 * i + ((i * 53) % 4) * 40, sp = 0.5 + ((i * 7) % 5) * 0.05, cross = Math.round((vw + BW + 40) / sp);
      var box = null;
      at(t0, function() {
        box = keep(stSpawn(hop(bfBoxHtml(false), 0.36 + (i % 3) * 0.05, i * 0.13), BW, BH, 4, X(sBox0, BW), 0, flip));
        stMove(box, X(vw + 20, BW), 0, cross, 'linear', flip);
      });
      at(t0 + Math.round((sHand - BW / 2 - sBox0) / sp), function() {
        if (box) box.firstChild.innerHTML = bfBoxHtml(true);
        slap();
      });
    })(i);
    [3000, 4300].forEach(function(t0, i) {
      at(t0, function() {
        var cart = keep(stSpawn(hop(bfCartHtml(), 0.5, i * 0.2), KW, KH, 4, X(-KW - 20, KW), 0, flip));
        stMove(cart, X(vw + 20, KW), 0, Math.round((vw + KW + 40) / 0.62), 'linear', flip);
      });
    });

    // The forklift crawls through behind the wave, stacks seven high and teetering, "beep! beep!"
    var tL = 7800, vL = 0.28, dL = Math.round((vw + LW + 40) / vL), lift = null;
    var sLift = function(t) { return -LW - 20 + vL * (t - tL); };
    at(tL, function() {
      lift = keep(stSpawn(bfForkliftHtml(), LW, LH, 4, X(-LW - 20, LW), 0, flip));
      stMove(lift, X(vw + 20, LW), 0, dL, 'linear', flip);
    });
    at(tL + 2000, function() {
      var cabX = function(s) { return lr ? X(s, LW) + fkPx(50) : X(s, LW) + LW - fkPx(50); };
      var b = keep(stBubble('beep! beep!', cabX(sLift(tL + 2000)) - 20, 4 + LH + 6));
      b.style.transition = 'transform 1400ms linear';
      b.style.transform = 'translateX(' + (cabX(sLift(tL + 3400)) - 20) + 'px)';
      at(1400, function() { drop(b); });
    });
    // Emma tags along behind the lift once it has gone by
    var tEmmaOff = tL + Math.round((sE + W + 20 + LW) / vL) + 400, dEmmaOff = Math.round((vw + 20 - sE) / 0.3);
    at(tEmmaOff, function() { emma.innerHTML = bfEmmaHtml('walk'); stMove(emma, X(vw + 20, W), 0, dEmmaOff, 'linear', flip); });

    // Last of all the cat rides a parcel out; Tracy turns and calls it a day
    var tCat = tL + dL - 1700, dCat = Math.round((vw + RW + 40) / 0.45);
    at(tCat, function() {
      var ride = keep(stSpawn(hop(bfCatRideHtml(), 0.5, 0), RW, RH, 4, X(-RW - 20, RW), 0, ''));
      stMove(ride, X(vw + 20, RW), 0, dCat, 'linear');
    });
    var tTracyOff = tCat + Math.round((sT + RW + 20 + W) / 0.45) + 500, sTurn = sT + W - stPx(90), dTracyOff = Math.round((vw + 20 - sTurn) / 0.3);
    at(tTracyOff, function() {
      tracy.innerHTML = bfTracyHtml('walk');
      tracy.style.transition = 'none';
      tracy.style.transform = 'translate(' + X(sTurn, W) + 'px,0px)' + flip;
      tracy.getBoundingClientRect();
      stMove(tracy, X(vw + 20, W), 0, dTracyOff, 'linear', flip);
    });
    var tEnd = Math.max(tEmmaOff + dEmmaOff, tCat + dCat, tTracyOff + dTracyOff, tL + dL) + 400;
    at(tEnd, function() { els.forEach(drop); });
  } catch (e) {}
}
window.stampedeRun = stampedeRun;

// ─── HOLIDAY SLEIGH (the hourly cartoon Dec 18-31) ───
// Snow drifts down over the bottom of the page. The Office agents cross in holiday trim,
// each with their desk look: Emma in an elf hat, Danny with a candy cane over his
// shoulder, Sage hugging a wrapped gift. Then the Filing floor's gray cat, in antlers
// and a red nose, trots through pulling a red sleigh — Santa at the reins, parcels
// heaped in the back — which eases to a stop at center. Santa waves: "ho ho ho!", and
// sleigh, cat and all fade away like the Halloween ghost. A Christmas tree waits in the
// corner they're all headed for (Anthony 2026-11-01, "in the corner of where they're all
// headed"). Same rig and helpers as the Storm and the parade. window.sleighRun() = demo.
var SL = {
  red: '#B91C1C', redDk: '#991B1B', gold: '#F2CF6B', white: '#F8FAFC', green: '#16A34A', greenDk: '#15803D', belt: '#1F2937',
  santaSkin: '#F3D2B3', antler: '#8B5A2B', nose: '#DC2626', wrap: '#DC2626', ribbon: '#F2CF6B', cane: '#F8FAFC', caneStripe: '#DC2626', snow: '#F8FAFC',
};
var SL_WHO = {
  emma: { skin: HW.emma.skin, shirt: '#14B8A6', shirtDk: '#0F9488', pantsA: '#374151', pantsB: '#3F4A5A', head: function() { return hwHead(HW.emma, 'ponytail'); } },
  sage: { skin: HW.sage.skin, shirt: '#84CC16', shirtDk: '#65A30D', pantsA: HW.pants, pantsB: HW.pantsB, head: function() { return hwHead(HW.sage, 'bob'); } },
  danny: { skin: ST.skin, shirt: ST.shirt, shirtDk: ST.shirtDk, pantsA: FK.pantsA, pantsB: FK.pantsB, head: function() { return stDannyHead(0, -68); } },
};
// Elf hat over a walker head at (0,-68): white brim, green cone, the tip flopping forward with a pom
function slElfHat() {
  return '<path d="M -11.5,-77 L 11.5,-77 L 4,-95 Q 1,-103 9,-104 Z" fill="' + SL.green + '"/>' +
    '<path d="M -4,-80 L 4,-95" stroke="' + SL.greenDk + '" stroke-width="1" fill="none"/>' +
    '<ellipse cx="0" cy="-77" rx="12.5" ry="3" fill="' + SL.white + '"/>' + fkCircle(10, -104.5, 3, SL.white);
}
// Candy cane held in a hand at (hx, hy), rising over the shoulder, hook curling forward
function slCandyCane(hx, hy) {
  var d = 'M ' + hx + ',' + (hy + 6) + ' L ' + (hx + 2) + ',' + (hy - 30) + ' Q ' + (hx + 3) + ',' + (hy - 40) + ' ' + (hx + 11) + ',' + (hy - 39) + ' Q ' + (hx + 16) + ',' + (hy - 38) + ' ' + (hx + 16) + ',' + (hy - 32);
  return '<path d="' + d + '" stroke="' + SL.cane + '" stroke-width="4.6" fill="none" stroke-linecap="round"/>' +
    '<path d="' + d + '" stroke="' + SL.caneStripe + '" stroke-width="4.6" fill="none" stroke-linecap="butt" stroke-dasharray="3.2 3.8"/>';
}
// A wrapped gift, 16×14, held out front with its bow on top
function slGift(x, y) {
  return fkRect(x, y, 16, 14, 1.5, SL.wrap) + fkRect(x + 6.5, y, 3, 14, 0.5, SL.ribbon) + fkRect(x, y + 5.5, 16, 3, 0.5, SL.ribbon) +
    '<path d="M ' + (x + 8) + ',' + (y - 1) + ' Q ' + (x + 2) + ',' + (y - 7) + ' ' + (x + 4) + ',' + (y - 1) + ' Z M ' + (x + 8) + ',' + (y - 1) + ' Q ' + (x + 14) + ',' + (y - 7) + ' ' + (x + 12) + ',' + (y - 1) + ' Z" fill="' + SL.ribbon + '"/>';
}
// The three walkers (90×112, feet at 40,106) in their holiday trim
function slFigure(who, which) {
  var B = which === 'B', lk = SL_WHO[who], k = [fkEllipse(0, 1.5, 18, 2.8, FK.shadow)];
  k.push(hwLegs(B, lk.pantsA, lk.pantsB, 16));
  var S = lfShoulder(0), H1, H2, held = '', hat = '';
  if (who === 'emma') { H1 = B ? [-7, -32] : [9, -30]; H2 = B ? [9, -30] : [-7, -32]; hat = slElfHat(); }
  else if (who === 'danny') { H1 = [12, -52]; H2 = B ? [-8, -31] : [6, -30]; }
  else { H1 = [21, -42]; H2 = [19, -46]; held = slGift(18, -53); }
  var torso = fkRect(-13, -58, 26, 36, 10, lk.shirt) + lk.head() + hat;
  var u = lfArm(S[0], S[1], H2[0], H2[1], lk.shirtDk, lk.skin) + torso + held + lfArm(S[0], S[1], H1[0], H1[1], lk.shirt, lk.skin);
  if (who === 'danny') u += slCandyCane(12, -52) + fkCircle(12, -52, 3.6, lk.skin);
  k.push(fkG(u, 'translate(0,' + (B ? -1.2 : 0) + ')'));
  return fkG(k.join(''), 'translate(40,106)');
}
function slHtml(who) {
  var mk = function(which) { return function(cls) { return forkSvgWrap(slFigure(who, which), 90, 112, stPx(90), stPx(112), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), ' wv');
}
// The gray cat in antlers with a red nose, trotting (the Storm's walk frames)
function slCatHtml() {
  var mk = function(which) { return function(cls) {
    var g = stCatFrame('walk', which) +
      fkG('<path d="M 9,-28 L 7,-37 M 7.5,-33 L 3.5,-36 M 7.2,-34.5 L 10,-39 M 15,-28 L 17.5,-37 M 17,-33 L 21,-36 M 17.3,-34.5 L 14.5,-39" stroke="' + SL.antler + '" stroke-width="1.5" fill="none" stroke-linecap="round"/>' + fkCircle(18.6, -20.8, 1.7, SL.nose), 'translate(26,41)');
    return forkSvgWrap(g, 58, 44, stPx(58), stPx(44), cls);
  }; };
  return catTwoFrames(mk('A'), mk('B'), ' fast');
}
// The sleigh (170×98, floor at 97): red body on a gold runner, parcels in the back, Santa at the
// reins ('ride') or waving ('wave'); the harness runs ahead to the cat
function slSleighFrame(pose, which) {
  var B = which === 'B', k = [];
  var arm = function(ang) {
    var t = ang * Math.PI / 180;
    return fkRect(50, 8, 16, 5, 2.5, SL.red, ' transform="rotate(' + ang + ' 50 10.5)"') + fkCircle(50 + Math.cos(t) * 16, 10.5 + Math.sin(t) * 16, 2.6, SL.santaSkin);
  };
  k.push(fkEllipse(64, 82, 56, 2.6, FK.shadow));
  k.push('<path d="M 118,50 L 166,42" stroke="' + SL.antler + '" stroke-width="1.6" stroke-linecap="round"/>'); // harness to the cat
  k.push('<path d="M 10,62 Q 6,62 7,56 M 10,64 L 100,64 Q 118,64 121,50" stroke="' + SL.gold + '" stroke-width="2.6" fill="none" stroke-linecap="round"/>');
  k.push('<path d="M 30,54 L 30,63 M 95,54 L 95,63" stroke="' + SL.gold + '" stroke-width="2.2"/>');
  k.push(fkBox(72, 6, 18, 11) + fkBox(90, 2, 16, 10) + fkBox(80, -4, 14, 9)); // parcels heaped behind the seat
  if (pose === 'ride') k.push('<path d="M 66,10 Q 100,20 124,46" stroke="' + SL.antler + '" stroke-width="1.3" fill="none"/>'); // reins
  k.push(fkRect(34, 6, 22, 26, 8, SL.red) + fkRect(34, 22, 22, 4, 1, SL.belt) + fkRect(43, 21.5, 5, 5, 1, SL.gold)); // Santa's coat and belt
  k.push(fkCircle(45, -2, 8, SL.santaSkin) + '<ellipse cx="45" cy="5.5" rx="7.8" ry="6" fill="' + SL.white + '"/>' + fkCircle(48, -3, 1, FK.eye) + fkCircle(51, -0.5, 1.6, '#F28B82'));
  k.push('<path d="M 37,-6.5 Q 45,-20 58,-12 L 54,-6 Z" fill="' + SL.red + '"/>' + fkRect(36, -8, 19, 3.6, 1.6, SL.white) + fkCircle(58.5, -12.5, 2.8, SL.white)); // hat
  k.push(arm(pose === 'wave' ? (B ? -118 : -95) : 8));
  k.push('<path d="M 18,54 L 14,30 Q 12,14 34,16 L 96,16 Q 118,16 116,36 L 110,54 Z" fill="' + SL.red + '" stroke="' + SL.gold + '" stroke-width="1.8" stroke-linejoin="round"/>' +
    '<path d="M 24,50 L 21,32 Q 20,22 34,22 L 94,22 Q 108,22 106,34 L 103,50 Z" fill="' + SL.redDk + '" opacity="0.5"/>'); // the body hides Santa's legs
  return fkG(k.join(''), 'translate(0,28)');
}
function slSleighHtml(pose) {
  var mk = function(which) { return function(cls) { return forkSvgWrap(slSleighFrame(pose, which), 170, 98, stPx(170), stPx(98), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), ' wv');
}
// The Christmas tree waiting in the corner they're all headed for (80×110, floor at 108):
// three tiers with garlands and baubles, a star on top, two gifts underneath
function slTreeSvg() {
  var k = [fkEllipse(40, 108, 34, 2.6, FK.shadow)];
  k.push(fkRect(35, 90, 10, 18, 2, SL.antler));
  [[4, 96, 52], [10, 70, 32], [16, 48, 12]].forEach(function(t) {
    k.push('<path d="M ' + t[0] + ',' + t[1] + ' L ' + (80 - t[0]) + ',' + t[1] + ' L 40,' + t[2] + ' Z" fill="' + SL.green + '" stroke="' + SL.greenDk + '" stroke-width="1.2" stroke-linejoin="round"/>');
  });
  k.push('<path d="M 14,88 Q 40,80 66,90 M 20,64 Q 40,57 60,66 M 26,42 Q 40,37 54,43" stroke="' + SL.gold + '" stroke-width="1.4" fill="none" opacity="0.9"/>');
  [[22, 90, SL.red], [40, 84, '#3B82F6'], [58, 91, SL.gold], [30, 66, SL.gold], [50, 62, SL.red], [40, 72, SL.white], [34, 44, '#3B82F6'], [46, 40, SL.red], [40, 54, SL.gold]].forEach(function(b) { k.push(fkCircle(b[0], b[1], 2.7, b[2])); });
  k.push('<path d="M 40,2 L 42.4,8.2 L 49,8.6 L 43.8,12.8 L 45.6,19.2 L 40,15.6 L 34.4,19.2 L 36.2,12.8 L 31,8.6 L 37.6,8.2 Z" fill="' + SL.gold + '"/>');
  k.push(slGift(2, 94) + fkG(slGift(0, 0), 'translate(62,96) scale(0.85)'));
  return forkSvgWrap(k.join(''), 80, 110, stPx(80), stPx(110), '');
}
function slFlakeSvg(r) { return forkSvgWrap(fkCircle(5, 5, r, SL.snow, ' opacity="' + (0.5 + r * 0.12).toFixed(2) + '"'), 10, 10, 12, 12, ''); }
function sleighRun() {
  try {
    stEnsureStyle();
    var vw = window.innerWidth;
    var lr = Math.random() < 0.5; // which way the sleigh runs
    var flip = lr ? '' : ' scaleX(-1)';
    var X = function(s, w) { return Math.round(lr ? s : vw - s - w); };
    var W = stPx(90), H = stPx(112), CW = stPx(58), CH = stPx(44), SW = stPx(170), SH = stPx(98);
    var els = [];
    var keep = function(el) { els.push(el); return el; };
    var drop = function(el) { try { if (el) el.remove(); } catch (e) {} };
    var at = function(ms, fn) { setTimeout(function() { try { fn(); } catch (e) {} }, ms); };

    // The Christmas tree waits in the corner they're all headed for; it fades in first
    var TRW = stPx(80), TRH = stPx(110);
    at(0, function() {
      var tree = keep(stSpawn(slTreeSvg(), TRW, TRH, 4, X(vw - TRW - 12, TRW), 0, ''));
      tree.style.opacity = '0';
      tree.getBoundingClientRect();
      tree.style.transition = 'opacity 700ms ease-out';
      tree.style.opacity = '1';
    });

    // Snow: two dozen flakes drift down the bottom strip, each falling three times
    var fall = 240;
    for (var i = 0; i < 24; i++) (function(i) {
      var x = Math.round(vw * ((i * 61) % 97) / 97), r = 1.6 + ((i * 7) % 4) * 0.7, dur = 4200 + ((i * 13) % 5) * 300, sway = ((i % 2) ? 1 : -1) * (14 + (i % 4) * 8);
      var el = null;
      [0, 1, 2].forEach(function(round) {
        at(i * 170 + round * (dur + 150), function() {
          if (!el) el = keep(stSpawn(slFlakeSvg(r), 12, 12, 0, x, -fall, ''));
          el.style.transition = 'none';
          el.style.transform = 'translate(' + x + 'px,' + (-fall) + 'px)';
          el.getBoundingClientRect();
          stMove(el, x + sway, 0, dur, 'linear');
        });
      });
    })(i);

    // The agents cross in their holiday trim, about 1.3 s apart
    var v = (vw + W + 40) / 9000, tCross = Math.round((vw + W + 40) / v);
    [[600, 'emma'], [1900, 'danny'], [3200, 'sage']].forEach(function(a) {
      at(a[0], function() {
        var el = keep(stSpawn(slHtml(a[1]), W, H, 4, X(-W - 20, W), 0, flip));
        stMove(el, X(vw + 20, W), 0, tCross, 'linear', flip);
      });
    });

    // The cat pulls the sleigh in and they ease to a stop at center
    var vS = 0.26, tS = 6500, sStop = Math.round(vw / 2 - stPx(65)), sEase = sStop - stPx(40), catOff = stPx(152);
    var dS = Math.round((sEase + SW + 20) / vS), tStop = tS + dS + 600, sleigh = null, cat = null;
    at(tS, function() {
      sleigh = keep(stSpawn(slSleighHtml('ride'), SW, SH, 4, X(-SW - 20, SW), 0, flip));
      cat = keep(stSpawn(slCatHtml(), CW, CH, 4, X(-SW - 20 + catOff, CW), 0, flip));
      stMove(sleigh, X(sEase, SW), 0, dS, 'linear', flip);
      stMove(cat, X(sEase + catOff, CW), 0, dS, 'linear', flip);
    });
    at(tS + dS, function() {
      stMove(sleigh, X(sStop, SW), 0, 600, 'ease-out', flip);
      stMove(cat, X(sStop + catOff, CW), 0, 600, 'ease-out', flip);
    });
    at(tStop + 200, function() { cat.innerHTML = slCatHtml().replace(/catfr f2 fast/g, 'catfr f2 wv').replace(/catfr fast/g, 'catfr wv'); });
    // Santa waves: "ho ho ho!" — then sleigh, cat and all fade away
    at(tStop + 300, function() { sleigh.innerHTML = slSleighHtml('wave'); });
    var santaX = function(s) { return lr ? X(s, SW) + stPx(46) : X(s, SW) + SW - stPx(46); };
    at(tStop + 500, function() {
      var b = keep(stBubble('ho ho ho!', santaX(sStop) - 20, 4 + SH + 6));
      at(1500, function() { drop(b); });
    });
    at(tStop + 2100, function() {
      [sleigh, cat].forEach(function(el) { el.style.transition = 'opacity 800ms ease-in'; el.style.opacity = '0'; });
    });
    var tEnd = Math.max(3200 + tCross, tStop + 3000) + 300;
    at(tEnd - 800, function() { els.forEach(function(el) { if (el === sleigh || el === cat) return; el.style.transition = 'opacity 700ms ease-in'; el.style.opacity = '0'; }); });
    at(tEnd, function() { els.forEach(drop); });
  } catch (e) {}
}
window.sleighRun = sleighRun;

// ─── Marketplace order page (Anthony 2026-09-23, to-do 15): the seller-side page for a channel order
//     id — Amazon by its 3-7-7 id (Seller Central of the channel's country), eBay by its 2-5-5 id,
//     Walmart by channel name. Null when we know no page (Faire, Shopify relays, SHEIN …). ───
function marketplaceOrderUrl(channel, channelOrderId) {
  var id = String(channelOrderId || '').trim();
  var ch = String(channel || '').toLowerCase();
  if (!id) return null;
  var enc = encodeURIComponent(id);
  if (/^\d{3}-\d{7}-\d{7}$/.test(id)) {
    var host = /canada|\bca\b/.test(ch) ? 'sellercentral.amazon.ca' : /mexico|\bmx\b/.test(ch) ? 'sellercentral.amazon.com.mx' : /australia|\bau\b/.test(ch) ? 'sellercentral.amazon.com.au' : /brazil|\bbr\b/.test(ch) ? 'sellercentral.amazon.com.br' : 'sellercentral.amazon.com';
    return 'https://' + host + '/orders-v3/order/' + enc;
  }
  if (/^\d{2}-\d{5}-\d{5}$/.test(id) || /ebay/.test(ch)) return 'https://www.ebay.com/sh/ord/details?orderid=' + enc;
  if (/walmart/.test(ch)) return 'https://seller.walmart.com/order-management/details?purchaseOrderId=' + enc;
  return null;
}
// The id as a new-tab link when we know its page, else plain text (app-utils has no global h)
function marketplaceOrderLink(channel, channelOrderId, style) {
  var url = marketplaceOrderUrl(channel, channelOrderId);
  var text = channelOrderId == null || channelOrderId === '' ? '—' : String(channelOrderId);
  if (!url) return React.createElement('span', { style: style || null }, text);
  return React.createElement('a', { href: url, target: '_blank', rel: 'noopener', title: 'Open this order on the marketplace',
    onClick: function(e) { e.stopPropagation(); },
    style: Object.assign({ color: C.accent, textDecoration: 'none' }, style || {}) }, text);
}
window.marketplaceOrderUrl = marketplaceOrderUrl;
window.marketplaceOrderLink = marketplaceOrderLink;

;
/* ===== app-i18n.js ===== */
// ─── UI TRANSLATOR (window.I18N) ───
// Live DOM translation for users whose profile language isn't English.
//
// REACT-SAFETY INVARIANT — the reason this exists instead of Chrome's page
// translate: we ONLY ever write text via node.nodeValue and attributes via
// setAttribute. We never insert, remove, wrap, or re-parent nodes. Chrome's
// translator wraps text nodes in <font> elements, and the next React commit
// that touches them throws "removeChild ... not a child of this node"
// (error_log 334/335). Keep it that way: any change to this file that
// mutates DOM *structure* re-introduces that crash.
//
// Mechanics: digit-bearing tokens (counts, SKUs, dates, prices) are swapped
// for ⟦N⟧ placeholders before dictionary lookup, so volatile values never
// reach the server and can never be mangled by translation. Lookups hit an
// in-memory Map seeded from GET /api/i18n/bundle; misses batch-POST to
// /api/i18n/translate (debounced), then the document is re-walked.
// When React re-renders a translated label, the new English text appears for
// one frame and the MutationObserver re-translates it.

(function() {
  var state = {
    lang: null,
    dict: new Map(),      // placeholderized source -> translation (null = gave up this session)
    pending: new Set(),   // keys awaiting server translation
    attempts: new Map(),  // key -> failed batch count
    observer: null,
    timer: null,
    inflight: false,
  };
  // Guards against reacting to our own writes: MutationObserver records arrive
  // async, so suppression flags don't work — instead remember what we wrote
  // per node/attribute and skip mutations that match it.
  var lastWritten = new WeakMap();     // Text node -> last value we wrote
  var lastWrittenAttr = new WeakMap(); // Element -> { attrName: last value we wrote }
  // What each node said BEFORE we translated it — lets a switch back to
  // English restore the page live instead of asking for a refresh.
  var originals = new WeakMap();       // Text node -> original value
  var originalAttrs = new WeakMap();   // Element -> { attrName: original value }

  var SKIP_TAGS = { SCRIPT: 1, STYLE: 1, NOSCRIPT: 1, TEXTAREA: 1, IFRAME: 1, CANVAS: 1 };
  var ATTRS = ['placeholder', 'title', 'aria-label'];

  function skipElement(el) {
    for (var e = el; e && e.nodeType === 1; e = e.parentElement) {
      if (SKIP_TAGS[e.tagName]) return true;
      if (e.isContentEditable) return true;
      if (e.hasAttribute && (e.hasAttribute('data-no-i18n') || e.getAttribute('translate') === 'no')) return true;
      if (e.classList && e.classList.contains('notranslate')) return true;
    }
    return false;
  }

  // Swap every whitespace-delimited run containing a digit for ⟦N⟧ — "Box 12"
  // → "Box ⟦0⟧", "MFK-ROUGE-540" → "⟦0⟧", "8.5x11 in" → "⟦0⟧ in". Keeps the
  // dictionary low-cardinality and guarantees codes survive verbatim.
  function keyFor(text) {
    var tokens = [];
    var key = text.replace(/\S*\d\S*/g, function(m) { tokens.push(m); return '⟦' + (tokens.length - 1) + '⟧'; });
    return { key: key, tokens: tokens };
  }
  function restore(translated, tokens) {
    return translated.replace(/⟦(\d+)⟧/g, function(m, i) {
      return tokens[+i] !== undefined ? tokens[+i] : m;
    });
  }
  function eligible(key) {
    if (!key || key.length < 2 || key.length > 250) return false;
    if (!/[A-Za-z]{2,}/.test(key)) return false;         // pure numbers/symbols/single letters
    if (/^https?:\/\/|@.*\./.test(key)) return false;    // URLs, email-ish
    return true;
  }

  function translateText(raw, node, write) {
    var m = raw.match(/^(\s*)([\s\S]*?)(\s*)$/);
    var lead = m[1], core = m[2], trail = m[3];
    if (!core) return;
    var pk = keyFor(core);
    if (!eligible(pk.key)) return;
    var tr = state.dict.get(pk.key);
    if (tr === undefined) {
      state.pending.add(pk.key);
      scheduleFlush();
    } else if (tr !== null) {
      var out = lead + restore(tr, pk.tokens) + trail;
      if (out !== raw) write(out);
    }
  }

  function applyToTextNode(node) {
    if (!state.lang || !node.parentElement || skipElement(node.parentElement)) return;
    var raw = node.nodeValue;
    if (raw == null || lastWritten.get(node) === raw) return;
    translateText(raw, node, function(out) {
      originals.set(node, raw);
      lastWritten.set(node, out);
      node.nodeValue = out; // nodeValue write only — never restructure (see header)
    });
  }

  function applyToAttr(el, attr) {
    if (!state.lang || skipElement(el)) return;
    var raw = el.getAttribute && el.getAttribute(attr);
    if (!raw) return;
    var mine = lastWrittenAttr.get(el);
    if (mine && mine[attr] === raw) return;
    translateText(raw, el, function(out) {
      var oa = originalAttrs.get(el);
      if (!oa) { oa = {}; originalAttrs.set(el, oa); }
      oa[attr] = raw;
      if (!mine) { mine = {}; lastWrittenAttr.set(el, mine); }
      mine[attr] = out;
      el.setAttribute(attr, out);
    });
  }

  function walk(root) {
    if (!root) return;
    if (root.nodeType === 3) { applyToTextNode(root); return; }
    if (root.nodeType !== 1 && root.nodeType !== 9) return;
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, null);
    var n = root.nodeType === 1 ? root : walker.nextNode();
    if (root.nodeType === 1) {
      for (var a = 0; a < ATTRS.length; a++) if (root.hasAttribute(ATTRS[a])) applyToAttr(root, ATTRS[a]);
      n = walker.nextNode();
    }
    while (n) {
      if (n.nodeType === 3) applyToTextNode(n);
      else for (var i = 0; i < ATTRS.length; i++) if (n.hasAttribute(ATTRS[i])) applyToAttr(n, ATTRS[i]);
      n = walker.nextNode();
    }
  }

  function scheduleFlush() {
    if (state.timer) return;
    state.timer = setTimeout(function() { state.timer = null; flush(); }, 500);
  }

  // One POST of up to 200 pending keys. Resolves after the response is merged
  // and re-applied (never rejects — failures feed the retry/give-up counters).
  function flushBatch() {
    if (!state.lang || !state.pending.size) return Promise.resolve();
    var batch = Array.from(state.pending).slice(0, 200);
    batch.forEach(function(k) { state.pending.delete(k); });
    return fetch('/api/i18n/translate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ lang: state.lang, strings: batch }),
    }).then(function(r) {
      if (!r.ok) throw new Error('translate ' + r.status);
      return r.json();
    }).then(function(data) {
      var entries = (data && data.entries) || {};
      batch.forEach(function(k) {
        if (entries[k] !== undefined) { state.dict.set(k, entries[k]); state.attempts.delete(k); }
      });
      walk(document.body);
    }).catch(function() {
      // Transient failure: retry each key once more, then give up for the
      // session (dict=null) so a broken endpoint can't hot-loop requests.
      batch.forEach(function(k) {
        var n = (state.attempts.get(k) || 0) + 1;
        state.attempts.set(k, n);
        if (n >= 2) state.dict.set(k, null);
        else state.pending.add(k);
      });
    });
  }

  // Background path: one batch at a time, re-scheduled while misses remain.
  function flush() {
    if (!state.lang || state.inflight) { if (state.pending.size) scheduleFlush(); return; }
    if (!state.pending.size) return;
    state.inflight = true;
    flushBatch().finally(function() {
      state.inflight = false;
      if (state.pending.size) scheduleFlush();
    });
  }

  // Blocking path: translate every pending key NOW, batch after batch, until
  // the queue is dry or the deadline passes (leftovers finish in background).
  function drain(deadline) {
    if (!state.lang || !state.pending.size || Date.now() > deadline) return Promise.resolve();
    if (state.inflight) { // background flush racing us — wait a beat, retry
      return new Promise(function(res) { setTimeout(res, 150); }).then(function() { return drain(deadline); });
    }
    state.inflight = true;
    return flushBatch().finally(function() { state.inflight = false; })
      .then(function() { return drain(deadline); });
  }

  // Full-screen progress overlay for interactive language switches. Appended
  // directly to document.body — OUTSIDE React's root, so inserting/removing it
  // never touches React-managed nodes (same pattern as download anchors).
  var overlayEl = null;
  function showOverlay() {
    if (overlayEl) return;
    overlayEl = document.createElement('div');
    overlayEl.setAttribute('data-no-i18n', 'true');
    overlayEl.style.cssText = 'position:fixed;inset:0;z-index:100000;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:14px;background:rgba(10,12,18,0.78);backdrop-filter:blur(2px);color:#E8EAF0;font-family:inherit;';
    var spinner = document.createElement('div');
    spinner.style.cssText = 'width:34px;height:34px;border-radius:50%;border:3px solid rgba(255,255,255,0.25);border-top-color:#4F6EF7;animation:i18nspin 0.8s linear infinite;';
    var label = document.createElement('div');
    label.style.cssText = 'font-size:14px;font-weight:600;';
    label.textContent = 'Traduciendo la interfaz…';
    var sub = document.createElement('div');
    sub.style.cssText = 'font-size:12px;opacity:0.7;';
    sub.textContent = 'Translating the interface…';
    var style = document.createElement('style');
    style.textContent = '@keyframes i18nspin{to{transform:rotate(360deg)}}';
    overlayEl.appendChild(style);
    overlayEl.appendChild(spinner);
    overlayEl.appendChild(label);
    overlayEl.appendChild(sub);
    document.body.appendChild(overlayEl);
  }
  function hideOverlay() {
    if (overlayEl && overlayEl.parentNode) overlayEl.parentNode.removeChild(overlayEl);
    overlayEl = null;
  }

  function start(lang, block) {
    state.lang = lang;
    if (block) showOverlay();
    fetch('/api/i18n/bundle?lang=' + encodeURIComponent(lang), { credentials: 'include' })
      .then(function(r) { return r.ok ? r.json() : { entries: [] }; })
      .then(function(data) {
        ((data && data.entries) || []).forEach(function(pair) { state.dict.set(pair[0], pair[1]); });
      })
      .catch(function() { /* dictionary warms via /translate misses instead */ })
      .finally(function() {
        if (state.lang !== lang) { hideOverlay(); return; } // switched off while bundle loaded
        walk(document.body);
        if (block) {
          // Hold the overlay until every visible string is translated (or the
          // deadline passes — leftovers then finish via the background path).
          drain(Date.now() + 20000).finally(hideOverlay);
        }
        if (!state.observer) {
          state.observer = new MutationObserver(function(muts) {
            for (var i = 0; i < muts.length; i++) {
              var mu = muts[i];
              if (mu.type === 'characterData') applyToTextNode(mu.target);
              else if (mu.type === 'attributes') applyToAttr(mu.target, mu.attributeName);
              else if (mu.type === 'childList') {
                for (var j = 0; j < mu.addedNodes.length; j++) walk(mu.addedNodes[j]);
              }
            }
          });
          state.observer.observe(document.body, {
            subtree: true, childList: true, characterData: true,
            attributes: true, attributeFilter: ATTRS,
          });
        }
      });
  }

  // Put every node we translated back to its original text — same
  // nodeValue/setAttribute-only writes as translation itself.
  function restoreAll() {
    if (!document.body) return;
    var walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, null);
    for (var n = walker.nextNode(); n; n = walker.nextNode()) {
      if (n.nodeType === 3) {
        var orig = originals.get(n);
        if (orig !== undefined) { n.nodeValue = orig; originals.delete(n); lastWritten.delete(n); }
      } else {
        var oa = originalAttrs.get(n);
        if (oa) {
          for (var a in oa) n.setAttribute(a, oa[a]);
          originalAttrs.delete(n); lastWrittenAttr.delete(n);
        }
      }
    }
  }

  function stop() {
    state.lang = null;
    hideOverlay();
    // Disconnect BEFORE restoring so the observer never sees our own reverts
    if (state.observer) { state.observer.disconnect(); state.observer = null; }
    if (state.timer) { clearTimeout(state.timer); state.timer = null; }
    state.pending.clear();
    restoreAll();
  }

  // Idempotent entry point — call with the user's preference ('es', 'en',
  // null/undefined). English or empty turns the translator off. Pass
  // opts.block=true (interactive switches) to hold a progress overlay until
  // the visible page is fully translated; boot-time calls stay non-blocking.
  function setLanguage(lang, opts) {
    if (!lang || lang === 'en') { stop(); return; }
    if (lang === state.lang) return;
    stop();
    start(lang, !!(opts && opts.block));
  }

  // Self-contained boot for shells that don't already fetch preferences:
  // reads the logged-in user's saved language and applies it.
  function boot() {
    fetch('/api/auth/preferences', { credentials: 'include' })
      .then(function(r) { return r.ok ? r.json() : {}; })
      .then(function(prefs) { setLanguage(prefs && prefs.language); })
      .catch(function() { /* stay in English */ });
  }

  window.I18N = { setLanguage: setLanguage, boot: boot };
})();

;
/* ===== app-pages.js ===== */
// ─── FULL-SCREEN PRODUCT EDITOR ───
function ProductEditorFullScreen({ product, editing, form, setForm, vendors, warehouses, products, onSave, onClose, showToast, initialTab, loadState, loadError, onRetryLoad, onStatusChange }) {
  // loadState (sweep 9-27 H3): 'loading' | 'error' | 'ready'. Callers that do not pass it
  // (the PO page product modal) already hold the full product, so it defaults to ready.
  var fullLoaded = !loadState || loadState === 'ready';
  var [activeTab, setActiveTabRaw] = useState(initialTab || 'details');
  // Active / Inactive toggle in the header (Anthony 2026-10-22): applies at once through
  // PUT /products/:id/active, independent of the Save button; onStatusChange lets the page
  // behind the modal refresh its list.
  var [statusBusy, setStatusBusy] = useState(false);
  var setActiveTab = function(t) {
    setActiveTabRaw(t);
    var path = window.location.pathname;
    if (path.startsWith('/catalog/')) {
      history.replaceState(history.state, '', path + (t && t !== 'details' ? '?tab=' + t : ''));
    }
  };
  var [attributes, setAttributes] = useState([]);
  var [attrsLoading, setAttrsLoading] = useState(false);
  var [zentailProduct, setZentailProduct] = useState(null);
  var [newAttrKey, setNewAttrKey] = useState('');
  var [newAttrVal, setNewAttrVal] = useState('');
  var [editingAttr, setEditingAttr] = useState(null);
  var [showAllAttrs, setShowAllAttrs] = useState(false);
  var [editorFieldMappings, setEditorFieldMappings] = useState([]);
  var [images, setImages] = useState([]);
  var [imagesLoading, setImagesLoading] = useState(false);
  var [newImageUrl, setNewImageUrl] = useState('');
  var [productInventory, setProductInventory] = useState([]);
  var [inventoryLoading, setInventoryLoading] = useState(false);
  var [invSubTab, setInvSubTab] = useState('overview');
  useEffect(function() { try { var qi = new URLSearchParams(window.location.search).get('inv'); if (qi) setInvSubTab(qi); } catch (e) {} }, []);
  var [invSummary, setInvSummary] = useState({ po_lines: [], fba_items: [], wfs_items: [] });
  var [invSummaryLoading, setInvSummaryLoading] = useState(false);
  var [activityLog, setActivityLog] = useState([]);
  var [activityTotal, setActivityTotal] = useState(0);
  var [activityLoading, setActivityLoading] = useState(false);
  var [activityLoaded, setActivityLoaded] = useState(false);
  var [activityFilter, setActivityFilter] = useState('');
  var [activityUserFilter, setActivityUserFilter] = useState('');
  var [activityStartDate, setActivityStartDate] = useState('');
  var [activityEndDate, setActivityEndDate] = useState('');
  var [activityUsers, setActivityUsers] = useState([]);
  var [activityOffset, setActivityOffset] = useState(0);
  var [tabFields, setTabFields] = useState([]);
  var [availableFields, setAvailableFields] = useState([]);
  var [fieldPickerTab, setFieldPickerTab] = useState(null);
  var [fieldPickerLabel, setFieldPickerLabel] = useState('');
  var [fieldPickerField, setFieldPickerField] = useState('');
  var [lightbox, setLightbox] = useState(null);
  var [analyticsData, setAnalyticsData] = useState(null);
  var [analyticsLoading, setAnalyticsLoading] = useState(false);
  var [analyticsLoaded, setAnalyticsLoaded] = useState(false);
  var [advertisingData, setAdvertisingData] = useState(null);
  var [advertisingLoading, setAdvertisingLoading] = useState(false);
  var [advertisingLoaded, setAdvertisingLoaded] = useState(false);
  var [advertisingDays, setAdvertisingDays] = useState(30);
  var [analyticsView, setAnalyticsView] = useState('sales');
  var [analyticsPeriod, setAnalyticsPeriod] = useState(12);
  var [analyticsGranularity, setAnalyticsGranularity] = useState('monthly');
  var [bundleContents, setBundleContents] = useState([]);
  var [bundleParents, setBundleParents] = useState([]);
  var [kitChildren, setKitChildren] = useState([]);
  var [aliasChildren, setAliasChildren] = useState([]);
  var [bundleLoading, setBundleLoading] = useState(false);
  var [bundleSearch, setBundleSearch] = useState('');
  var [bundleSearchResults, setBundleSearchResults] = useState([]);
  var [bundleSearching, setBundleSearching] = useState(false);
  var [bundleAddQty, setBundleAddQty] = useState(1);

  var F = function(key) { return { value: form[key] != null ? form[key] : '', onChange: function(e) { setForm(Object.assign({}, form, { [key]: e.target.value })); } }; };

  // Load attributes when editing
  useEffect(function() {
    if (editing && editing.id) {
      setAttrsLoading(true);
      api('/products/' + editing.id + '/attributes').then(function(r) { setAttributes(r); setAttrsLoading(false); }).catch(function() { setAttrsLoading(false); });
      // Load images
      setImagesLoading(true);
      api('/products/' + editing.id + '/images').then(function(r) { setImages(r || []); setImagesLoading(false); }).catch(function() { setImagesLoading(false); });
      // Load Zentail product match and field mappings
      api('/integrations/zentail/products?search=' + encodeURIComponent(editing.sku || '')).then(function(r) {
        var match = r && r.find(function(zp) { return zp.zentail_sku === editing.sku; });
        setZentailProduct(match || null);
      }).catch(function(e) { console.error('[silent-catch]', e); });
      api('/integrations/zentail/field-mappings').then(function(r) { setEditorFieldMappings(r || []); }).catch(function(e) { console.error('[silent-catch]', e); });
      // Load tab field assignments
      api('/products/tab-fields').then(function(r) { setTabFields(r || []); }).catch(function(e) { console.error('[silent-catch]', e); });
      api('/products/available-fields').then(function(r) { setAvailableFields(r || []); }).catch(function(e) { console.error('[silent-catch]', e); });
      // Load inventory
      setInventoryLoading(true);
      api('/products/' + editing.id + '/inventory').then(function(r) { setProductInventory(r || []); setInventoryLoading(false); }).catch(function() { setInventoryLoading(false); });
      // Load inventory summary (PO + FBA)
      setInvSummaryLoading(true);
      api('/products/' + editing.id + '/inventory-summary').then(function(r) { setInvSummary(r || { po_lines: [], fba_items: [] }); setInvSummaryLoading(false); }).catch(function() { setInvSummaryLoading(false); });
      // Load bundle contents + parents
      setBundleLoading(true);
      Promise.all([
        api('/products/bundles/by-sku/' + encodeURIComponent(editing.sku)),
        api('/products/bundles/contains/' + encodeURIComponent(editing.sku))
      ]).then(function(r) {
        setBundleContents(r[0] || []);
        setBundleParents(r[1] || []);
        setBundleLoading(false);
      }).catch(function() { setBundleLoading(false); });
      // Load kit children and alias children (can't rely on paginated product list)
      api('/products/kit-children/' + encodeURIComponent(editing.sku)).then(function(r) { setKitChildren(r || []); }).catch(function(e) { console.error('[silent-catch]', e); });
      api('/products/alias-children/' + encodeURIComponent(editing.sku)).then(function(r) { setAliasChildren(r || []); }).catch(function(e) { console.error('[silent-catch]', e); });
    }
  // Keyed on the product identity, not the object: a host that re-creates the object on
  // every render (the PO page passes its form object) re-ran all 12 loads per keystroke (9-11).
  }, [editing && editing.id, editing && editing.sku]);

  // Auto-poll all inventory data every 15s while the Inventory tab is open
  useEffect(function() {
    if (!editing || !editing.id || activeTab !== 'inventory') return;
    var pid = editing.id;
    var poll = function() {
      api('/products/' + pid + '/inventory').then(function(r) { setProductInventory(r || []); }).catch(function(e) { console.error('[silent-catch]', e); });
      api('/products/' + pid + '/inventory-summary').then(function(r) { setInvSummary(r || { po_lines: [], fba_items: [] }); }).catch(function(e) { console.error('[silent-catch]', e); });
    };
    poll();
    var interval = setInterval(poll, 15000);
    return function() { clearInterval(interval); };
  }, [editing && editing.id, activeTab]);

  var saveAttributes = async function() {
    if (!editing || !editing.id) return;
    try {
      var result = await api('/products/' + editing.id + '/attributes', {
        method: 'PUT', body: JSON.stringify({ attributes: attributes.map(function(a) { return { key: a.attr_key, value: a.attr_value, source: a.source || 'manual' }; }) })
      });
      setAttributes(result);
      showToast('Attributes saved');
    } catch (e) { showToast(e.message, 'error'); }
  };

  var addAttribute = function() {
    if (!newAttrKey.trim()) return;
    var exists = attributes.find(function(a) { return a.attr_key === newAttrKey.trim(); });
    if (exists) { showToast('Attribute "' + newAttrKey + '" already exists', 'error'); return; }
    setAttributes(attributes.concat([{ attr_key: newAttrKey.trim(), attr_value: newAttrVal, source: 'manual' }]));
    setNewAttrKey(''); setNewAttrVal('');
  };

  var removeAttribute = function(key) {
    setAttributes(attributes.filter(function(a) { return a.attr_key !== key; }));
  };

  var updateAttributeValue = function(key, newVal) {
    setAttributes(attributes.map(function(a) { return a.attr_key === key ? Object.assign({}, a, { attr_value: newVal }) : a; }));
  };

  var addTabField = async function(tabKey) {
    if (!fieldPickerField) return;
    try {
      var r = await api('/products/tab-fields', { method: 'POST', body: JSON.stringify({ tab_key: tabKey, field_name: fieldPickerField, display_label: fieldPickerLabel || fieldPickerField }) });
      setTabFields(tabFields.concat([r]));
      setAvailableFields(availableFields.filter(function(f) { return f !== fieldPickerField; }));
      setFieldPickerField(''); setFieldPickerLabel('');
    } catch (e) { showToast(e.message, 'error'); }
  };

  var removeTabField = async function(tfId, fieldName) {
    try {
      await api('/products/tab-fields/' + tfId, { method: 'DELETE' });
      setTabFields(tabFields.filter(function(tf) { return tf.id !== tfId; }));
      setAvailableFields(availableFields.concat([fieldName]).sort());
    } catch (e) { showToast(e.message, 'error'); }
  };

  var getTabCustomFields = function(tabKey) {
    return tabFields.filter(function(tf) { return tf.tab_key === tabKey; }).sort(function(a, b) { return (a.sort_order || 0) - (b.sort_order || 0); });
  };

  var editableTabs = ['details', 'identifiers', 'sourcing', 'inventory', 'images'];

  var tabs = [
    { key: 'details', label: 'Product Details', icon: 'package' },
    { key: 'identifiers', label: 'Identifiers', icon: 'hash' },
    { key: 'sourcing', label: 'Sourcing & Packaging', icon: 'truck' },
  ];
  if (editing) {
    tabs.push({ key: 'bundle', label: 'Kits, Bundles & Alias', icon: 'layers' });
    tabs.push({ key: 'inventory', label: 'Inventory', icon: 'box' });
    tabs.push({ key: 'images', label: 'Images', icon: 'image' });
    tabs.push({ key: 'amazon', label: 'Amazon', icon: 'shopping-bag' });
    tabs.push({ key: 'zentail', label: 'Zentail', icon: 'link' });
    tabs.push({ key: 'analytics', label: 'Analytics', icon: 'bar-chart-2' });
    tabs.push({ key: 'advertising', label: 'Advertising', icon: 'megaphone' });
    tabs.push({ key: 'activity', label: 'Activity', icon: 'clock' });
  }

  var sidebarPinned = true;
  try { var sc = localStorage.getItem('sidebar_collapsed'); sidebarPinned = sc === null ? false : sc !== 'true'; } catch(e) {}
  // narrow widths have no sidebar (top bar + drawer, 9-27): the editor starts at the left edge
  var sideLeft = isNarrowViewport() ? 0 : sidebarPinned ? SIDENAV.NAV_W : SIDENAV.RAIL_W;
  var overlayStyle = { position: 'fixed', top: 0, left: sideLeft, right: 0, bottom: 0, zIndex: 1000, background: C.bg, display: 'flex', flexDirection: 'column', overflow: 'hidden' };

  // Hidden fields (needed before top bar for identifier pills)
  var hiddenFields = new Set();
  editorFieldMappings.forEach(function(m) {
    if (m.visible_in_editor === false) hiddenFields.add(m.product_field);
  });
  var isFieldVisible = function(key) { return !hiddenFields.has(key); };

  // Active / Inactive toggle — header, under the identifier pills. Existing items only; one
  // click applies (PUT /products/:id/active), deactivating asks first. The form's own
  // is_active follows so a later Save does not undo it.
  var statusToggle = (function() {
    if (!editing || !editing.id) return null;
    var isActive = form.is_active !== false;
    var seg = function(on, color, extra) {
      return Object.assign({ fontSize: 12, fontWeight: 600, padding: '3px 10px', letterSpacing: '0.03em', whiteSpace: 'nowrap', userSelect: 'none',
        cursor: statusBusy ? 'wait' : (on ? 'default' : 'pointer'), background: on ? color + '22' : 'transparent', color: on ? color : C.textMuted, transition: 'all 0.15s' }, extra || {});
    };
    var setStatus = async function(next) {
      if (statusBusy || next === isActive || !fullLoaded) return;
      if (!next && !(await appConfirm('Mark ' + (form.sku || 'this item') + ' inactive? It leaves the active catalog and purchasing until it is turned back on.', { danger: true, okLabel: 'Deactivate' }))) return;
      setStatusBusy(true);
      try {
        var r = await api('/products/' + editing.id + '/active', { method: 'PUT', body: JSON.stringify({ is_active: next }) });
        setForm(Object.assign({}, form, { is_active: r.is_active }));
        showToast((form.sku || 'Item') + (r.is_active ? ' is now active' : ' is now inactive'), 'success');
        if (onStatusChange) onStatusChange(r);
      } catch (e) { showToast(e.message, 'error'); }
      finally { setStatusBusy(false); }
    };
    var hover = function(on) { return on ? {} : {
      onMouseEnter: function(e) { if (!statusBusy) e.currentTarget.style.background = C.border + '66'; },
      onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; }
    }; };
    return React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 } },
      React.createElement('div', { role: 'group', 'aria-label': 'Item status', title: statusBusy ? 'Saving…' : 'Click to change the item status',
        style: { display: 'inline-flex', border: '1px solid ' + C.border, borderRadius: 5, overflow: 'hidden', background: C.surface, opacity: statusBusy ? 0.6 : 1 } },
        React.createElement('span', Object.assign({ onClick: function() { setStatus(true); }, style: seg(isActive, C.success) }, hover(isActive)), 'ACTIVE'),
        React.createElement('span', Object.assign({ onClick: function() { setStatus(false); }, style: seg(!isActive, C.danger, { borderLeft: '1px solid ' + C.border }) }, hover(!isActive)), 'INACTIVE')
      )
    );
  })();

  // Top bar
  var topBar = React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 28px', borderBottom: '1px solid ' + C.border, background: C.surface, flexShrink: 0 } },
    React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 16 } },
      (function() { var primaryImg = images.length > 0 ? images[0].image_url : null; return primaryImg ? React.createElement('img', { src: primaryImg, alt: 'Product', onClick: function() { setLightbox({ images: images.map(function(im) { return im.image_url; }), index: 0 }); }, title: images.length > 1 ? 'Click to view all ' + images.length + ' images' : 'Click to enlarge', style: { width: 80, height: 80, borderRadius: 10, objectFit: 'contain', background: '#fff', border: '1px solid ' + C.border, cursor: 'pointer' } }) : React.createElement('div', { style: { width: 80, height: 80, borderRadius: 10, background: C.accentSoft || (C.accent + '18'), display: 'flex', alignItems: 'center', justifyContent: 'center' } }, React.createElement(LIcon, { name: editing ? 'package' : 'sparkles', size: 34 })); })(),
      React.createElement('div', null,
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' } },
          React.createElement('span', { style: { fontSize: 15, fontWeight: 600, color: C.text } }, editing ? 'Edit Product' : 'New Product'),
          editing && form.title && React.createElement('span', { title: form.title, style: { fontSize: 14, color: C.textMuted, fontWeight: 400, maxWidth: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, '— ' + form.title),
          (function() {
            var ps = function(bg, fg) { return { fontSize: 12, fontWeight: 600, padding: '3px 9px', borderRadius: 5, background: bg, color: fg, letterSpacing: '0.03em', whiteSpace: 'nowrap' }; };
            var aliasPills = [];
            if (form.alias_of) aliasPills.push(React.createElement('span', { key: 'alias-child', style: ps('#FF990018', '#FF9900'), title: 'Alias of ' + form.alias_of }, 'ALIAS CHILD'));
            if (aliasChildren.length) aliasPills.push(React.createElement('span', { key: 'alias-parent', style: ps('#6C63FF18', '#6C63FF'), title: aliasChildren.map(function(a) { return a.sku; }).join(', ') }, 'ALIAS PARENT'));
            if (form.kit_parent) aliasPills.push(React.createElement('span', { key: 'kit-child', style: ps('#00B8D918', '#00B8D9'), title: 'Kit child of ' + form.kit_parent + (form.kit_qty ? ' (qty: ' + form.kit_qty + ')' : '') }, 'KIT CHILD' + (form.kit_qty ? ' x' + form.kit_qty : '')));
            if (kitChildren.length) aliasPills.push(React.createElement('span', { key: 'kit-parent', style: ps('#00875A18', '#00875A'), title: kitChildren.map(function(a) { return a.sku; }).join(', ') }, 'KIT PARENT'));
            if (bundleContents.length) aliasPills.push(React.createElement('span', { key: 'bundle-parent', style: ps('#E0740018', '#E07400'), title: bundleContents.map(function(bc) { return bc.component_sku; }).join(', ') }, 'BUNDLE'));

            return aliasPills;
          })()
        ),
        (function() {
          var pills = [];
          var pillStyle = function(bg, fg) { return { fontSize: 12, fontWeight: 600, padding: '3px 9px', borderRadius: 5, background: bg, color: fg, letterSpacing: '0.03em', whiteSpace: 'nowrap' }; };
          // FBA copyBadge parity: whole pill copies on click, brightness hover, toast feedback
          var copyPill = function(label, value) {
            return React.createElement('span', {
              key: label,
              onClick: function(e) { e.stopPropagation(); var v = String(value); if (navigator.clipboard && navigator.clipboard.writeText) { navigator.clipboard.writeText(v).then(function() { showToast('Copied ' + v, 'success'); }).catch(function() { showToast('Copy failed', 'error'); }); } },
              onMouseEnter: function(e) { e.currentTarget.style.filter = 'brightness(1.25)'; },
              onMouseLeave: function(e) { e.currentTarget.style.filter = 'brightness(1)'; },
              title: 'Click to copy',
              style: Object.assign({}, pillStyle(C.accent + '14', C.accent), { cursor: 'pointer', transition: 'filter 0.15s' })
            }, label + ': ' + value);
          };
          var idPill = function(label, value) { return { label: label, value: value }; };
          var hardcoded = new Set(['sku', 'fnsku', 'asin', 'barcode']);
          var fieldOnPage = function(key) { return isFieldVisible(key) && (hardcoded.has(key) || tabFields.some(function(tf) { return tf.field_name === key; })); };
          if (form.sku) pills.push(copyPill('SKU', form.sku));
          var identifiers = [
            fieldOnPage('barcode') && idPill('BARCODE', form.barcode),
            fieldOnPage('fnsku') && idPill('FNSKU', form.fnsku),
            fieldOnPage('asin') && idPill('ASIN', form.asin),
            fieldOnPage('fnetid') && idPill('FNETID', form.fnetid),
            fieldOnPage('vendor_sku') && idPill('VENDOR SKU', form.vendor_sku)
          ].filter(Boolean);
          identifiers.forEach(function(id) {
            if (!id.value) return;
            if (id.label === 'ASIN') {
              var amzSuffix = /-CA$/i.test(form.sku || '') ? '.ca' : '.com';
              pills.push(React.createElement('a', {
                key: id.label, href: 'https://www.amazon' + amzSuffix + '/dp/' + id.value, target: '_blank', rel: 'noopener noreferrer',
                title: 'Open on Amazon', onClick: function(e) { e.stopPropagation(); },
                style: Object.assign({}, pillStyle(C.accent + '14', C.accent), { textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 })
              }, id.label + ': ' + id.value, React.createElement(LIcon, { name: 'external-link', size: 10 })));
              return;
            }
            pills.push(copyPill(id.label, id.value));
          });
          if (!pills.length) return null;
          return React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 4 } }, pills);
        })(),
        statusToggle
      )
    ),
    React.createElement('div', { style: { display: 'flex', gap: 8, alignItems: 'center' } },
      React.createElement(Btn, { variant: 'primary', disabled: !!editing && !fullLoaded, title: !!editing && !fullLoaded ? (loadState === 'error' ? 'Product did not load. Retry first.' : 'Loading product...') : undefined, onClick: function() { if (editing && !fullLoaded) return; onSave(); } }, React.createElement(LIcon, { name: 'check', size: 14 }), editing ? (fullLoaded ? ' Save' : loadState === 'error' ? ' Save' : ' Loading...') : ' Create'),
      React.createElement('button', { onClick: onClose, title: 'Close',
        onMouseEnter: function(e) { e.currentTarget.style.background = C.danger + '33'; e.currentTarget.style.borderColor = C.danger; },
        onMouseLeave: function(e) { e.currentTarget.style.background = C.danger + '18'; e.currentTarget.style.borderColor = C.danger + '44'; },
        style: { background: C.danger + '18', border: '1px solid ' + C.danger + '44', cursor: 'pointer', padding: 6, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.danger, transition: 'all 0.15s ease' }
      }, React.createElement(LIcon, { name: 'x', size: 20 }))
    )
  );

  // Tab bar
  var pid = (editing && editing.id) || (product && product.id) || (form && form.id);
  var tabBar = React.createElement('div', { style: { display: 'flex', gap: 0, borderBottom: '1px solid ' + C.border, background: C.surface, paddingLeft: 24, flexShrink: 0 } },
    tabs.map(function(tab) {
      var active = activeTab === tab.key;
      var isEditable = editableTabs.includes(tab.key);
      return React.createElement('div', { key: tab.key, style: { display: 'flex', alignItems: 'center', marginBottom: -1 } },
        React.createElement('a', { href: pid ? ('/catalog/' + pid + (tab.key !== 'details' ? '?tab=' + tab.key : '')) : undefined,
          onClick: function(e) { if (pid && (e.ctrlKey || e.metaKey || e.shiftKey)) return; e.preventDefault(); setActiveTab(tab.key); },
          onMouseOver: function(e) { if (!active) { e.currentTarget.style.color = C.text; e.currentTarget.style.background = C.border + '44'; } },
          onMouseOut: function(e) { if (!active) { e.currentTarget.style.color = C.textMuted; e.currentTarget.style.background = 'transparent'; } },
          style: { display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', fontSize: 14, fontWeight: active ? 600 : 400,
            color: active ? C.accent : C.textMuted, background: 'transparent', border: 'none', borderBottom: active ? '2px solid ' + C.accent : '2px solid transparent',
            cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s', borderRadius: '6px 6px 0 0', textDecoration: 'none' }
        }, React.createElement(LIcon, { name: tab.icon, size: 15 }), tab.label),
        active && isEditable && editing && React.createElement('button', {
          onClick: function(e) { e.stopPropagation(); setFieldPickerTab(tab.key); setFieldPickerField(''); setFieldPickerLabel(''); },
          title: 'Manage custom fields on this tab',
          style: { background: 'none', border: 'none', cursor: 'pointer', padding: '4px 4px', marginLeft: -4, marginRight: 4, borderRadius: 4, display: 'flex', alignItems: 'center', transition: 'opacity 0.15s', opacity: 0.5 },
          onMouseOver: function(e) { e.currentTarget.style.opacity = '1'; },
          onMouseOut: function(e) { e.currentTarget.style.opacity = '0.5'; }
        }, React.createElement(LIcon, { name: 'settings', size: 13, color: C.accent }))
      );
    })
  );

  // Content area
  var contentStyle = { flex: 1, overflowY: 'auto', padding: '24px 32px' };
  var sectionStyle = { maxWidth: 700, margin: '0 auto' };

  var content = null;

  // Build reverse mapping: product_field -> zentail_attr
  var fieldSourceMap = {};
  editorFieldMappings.forEach(function(m) {
    fieldSourceMap[m.product_field] = m.zentail_attr;
  });
  var pillStyle = function(color) {
    return { fontSize: 9, fontWeight: 600, padding: '1px 6px', borderRadius: 8, background: color + '18', color: color, marginLeft: 6, verticalAlign: 'middle', letterSpacing: '0.03em', textTransform: 'uppercase' };
  };
  var sourcePill = function(fieldKey) {
    if (!editing || !form[fieldKey]) return null;
    // Check dedicated source columns (gtin_source, fnsku_source)
    var dedicatedSources = { gtin: 'gtin_source', fnsku: 'fnsku_source' };
    if (dedicatedSources[fieldKey] && editing[dedicatedSources[fieldKey]] && editing[dedicatedSources[fieldKey]] !== 'manual') {
      var src = editing[dedicatedSources[fieldKey]];
      var srcColor = src === 'walmart' ? '#0071DC' : C.accent;
      return React.createElement('span', { style: pillStyle(srcColor), title: 'Imported from ' + src }, src);
    }
    // Check tracked field_sources first (persisted per-field source)
    var fs = editing.field_sources || {};
    if (fs[fieldKey] && fs[fieldKey] !== 'manual') {
      var fsSrc = fs[fieldKey];
      var fsSrcColor = fsSrc === 'walmart' ? '#0071DC' : fsSrc === 'mercadolibre' ? '#3483FA' : C.accent;
      return React.createElement('span', { style: pillStyle(fsSrcColor), title: 'Imported from ' + fsSrc }, fsSrc);
    }
    if (fs[fieldKey] === 'manual') {
      return React.createElement('span', { style: pillStyle(C.textMuted), title: 'Manually entered' }, 'manual');
    }
    // Fallback: check live Zentail attributes
    var zentailAttr = fieldSourceMap[fieldKey];
    var isZentail = false;
    if (zentailProduct && zentailAttr) {
      var zentailAttrs = zentailProduct.attributes || {};
      var zentailVal = zentailAttrs[zentailAttr];
      if (zentailVal && String(zentailVal).trim() !== '' && String(zentailVal) !== '0') isZentail = true;
    }
    if (isZentail) return React.createElement('span', { style: pillStyle(C.accent), title: 'Mapped from: ' + zentailAttr }, 'zentail');
    return React.createElement('span', { style: pillStyle(C.textMuted), title: 'Manually entered' }, 'manual');
  };
  // Wrap label + source pill
  var labelWithSource = function(label, fieldKey) {
    return React.createElement('span', null, label, sourcePill(fieldKey));
  };

  // Render custom fields for a tab, optionally excluding some field names
  var renderCustomFields = function(tabKey, excludeFields) {
    var fields = getTabCustomFields(tabKey);
    if (excludeFields) fields = fields.filter(function(tf) { return !excludeFields.includes(tf.field_name); });
    fields = fields.filter(function(tf) { return isFieldVisible(tf.field_name); });
    if (fields.length === 0) return null;
    return React.createElement('div', { style: { display: 'grid', gridTemplateColumns: fields.length === 1 ? '1fr' : '1fr 1fr', gap: 12 } },
      fields.map(function(tf) {
        return React.createElement(Inp, { key: tf.field_name, label: labelWithSource(tf.display_label || tf.field_name, tf.field_name), ...F(tf.field_name) });
      })
    );
  };

  // Get custom fields matching a pattern (e.g. bullet points) for inline rendering
  var getMatchingCustomFields = function(tabKey, pattern) {
    return getTabCustomFields(tabKey).filter(function(tf) { return pattern.test(tf.field_name); });
  };

  if (activeTab === 'details') {
    var bulletFields = [
      { key: 'bullet_point_1', label: 'Bullet Point 1' },
      { key: 'bullet_point_2', label: 'Bullet Point 2' },
      { key: 'bullet_point_3', label: 'Bullet Point 3' },
      { key: 'bullet_point_4', label: 'Bullet Point 4' },
      { key: 'bullet_point_5', label: 'Bullet Point 5' }
    ];
    // Merge any custom bullet_point fields (6, 7, 8, etc.) into the bullet points section
    var extraBullets = getMatchingCustomFields('details', /^bullet_point_\d+$/);
    var extraBulletNames = extraBullets.map(function(tf) { return tf.field_name; });
    extraBullets.forEach(function(tf) {
      var num = tf.field_name.replace('bullet_point_', '');
      bulletFields.push({ key: tf.field_name, label: tf.display_label || ('Bullet Point ' + num) });
    });
    // Sort by number
    bulletFields.sort(function(a, b) {
      var na = parseInt(a.key.replace('bullet_point_', '')) || 0;
      var nb = parseInt(b.key.replace('bullet_point_', '')) || 0;
      return na - nb;
    });
    var visibleBullets = bulletFields.filter(function(bf) { return isFieldVisible(bf.key) && (!editing || form[bf.key]); });
    // Exclude bullet fields from the generic custom fields block at bottom
    var detailsExclude = extraBulletNames;
    content = React.createElement('div', { style: sectionStyle },
      React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 16 } },
        React.createElement(Inp, { label: labelWithSource('Title', 'title'), ...F('title') }),
        React.createElement(Inp, { label: labelWithSource('SKU', 'sku'), ...F('sku') }),
        React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4 } },
          React.createElement('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' } }, labelWithSource('Description', 'description')),
          React.createElement('textarea', {
            placeholder: 'Product description...',
            value: form.description || '',
            onChange: function(e) { setForm(Object.assign({}, form, { description: e.target.value })); },
            onFocus: function(e) { e.target.style.borderColor = C.accent; e.target.style.boxShadow = '0 0 0 2px ' + C.accent + '33'; },
            onBlur: function(e) { e.target.style.borderColor = C.border; e.target.style.boxShadow = 'none'; },
            style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '10px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', minHeight: 80, resize: 'vertical', outline: 'none', width: '100%', boxSizing: 'border-box', transition: 'border-color 0.15s, box-shadow 0.15s' }
          })
        ),
        visibleBullets.length > 0 && React.createElement('div', { style: { background: C.surface, borderRadius: 8, padding: '14px 16px', border: '1px solid ' + C.border + '44' } },
          React.createElement('div', { style: { fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 } }, 'Bullet Points'),
          React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 10 } },
            visibleBullets.map(function(bf) {
              return React.createElement(Inp, { key: bf.key, label: labelWithSource(bf.label, bf.key), ...F(bf.key) });
            })
          )
        ),
        renderCustomFields('details', detailsExclude)
      )
    );
  } else if (activeTab === 'identifiers') {
    content = React.createElement('div', { style: sectionStyle },
      React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 16 } },
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 } },
          React.createElement(Inp, { label: labelWithSource('SKU', 'sku'), ...F('sku'), disabled: true }),
          isFieldVisible('fnsku') && React.createElement(Inp, { label: labelWithSource('FNSKU', 'fnsku'), ...F('fnsku') }),
          isFieldVisible('asin') && React.createElement(Inp, { label: labelWithSource('ASIN', 'asin'), ...F('asin') }),
          isFieldVisible('barcode') && React.createElement(Inp, { label: labelWithSource('Barcode', 'barcode'), ...F('barcode') })
        ),
        renderCustomFields('identifiers')
      )
    );
  } else if (activeTab === 'bundle') {
    // Bundle search handler
    var doBundleSearch = function(q) {
      setBundleSearch(q);
      if (q.length < 2) { setBundleSearchResults([]); return; }
      setBundleSearching(true);
      api('/products?search=' + encodeURIComponent(q) + '&limit=10&page=0&active=true').then(function(r) {
        var rows = (r.rows || r || []).filter(function(p) { return p.sku !== form.sku; });
        setBundleSearchResults(rows);
        setBundleSearching(false);
      }).catch(function() { setBundleSearching(false); });
    };
    var addBundleComponent = function(componentSku) {
      api('/products/bundles', { method: 'POST', body: JSON.stringify({ bundle_sku: form.sku, component_sku: componentSku, quantity: bundleAddQty }) })
        .then(function(r) {
          // Refresh bundle contents
          api('/products/bundles/by-sku/' + encodeURIComponent(form.sku)).then(function(res) { setBundleContents(res || []); });
          setBundleSearch(''); setBundleSearchResults([]); setBundleAddQty(1);
          showToast('Component added to bundle');
        }).catch(function(e) { showToast(e.message || 'Error adding component', 'error'); });
    };
    var removeBundleComponent = function(id) {
      api('/products/bundles/' + id, { method: 'DELETE' }).then(function() {
        setBundleContents(bundleContents.filter(function(bc) { return bc.id !== id; }));
        showToast('Component removed');
      }).catch(function(e) { showToast(e.message || 'Error', 'error'); });
    };
    var updateBundleQty = function(id, qty) {
      api('/products/bundles/' + id, { method: 'PUT', body: JSON.stringify({ quantity: qty }) }).then(function(r) {
        setBundleContents(bundleContents.map(function(bc) { return bc.id === id ? Object.assign({}, bc, { quantity: r.quantity }) : bc; }));
      }).catch(function(e) { showToast(e.message || 'Error', 'error'); });
    };
    var bundleCost = bundleContents.reduce(function(s, bc) { return s + (Number(bc.unit_cost) || 0) * (bc.quantity || 1); }, 0);

    content = React.createElement('div', { style: sectionStyle },
      React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 16 } },
        // Alias section
        React.createElement('div', { style: { background: C.surface, borderRadius: 8, padding: '14px 16px', border: '1px solid ' + C.border + '44' } },
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 } },
            React.createElement(LIcon, { name: 'link', size: 16, color: '#FF9900' }),
            React.createElement('span', { style: { fontSize: 13, fontWeight: 600, color: C.text, textTransform: 'uppercase', letterSpacing: '0.05em' } }, 'Alias')
          ),
          React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12 } }, 'Different SKUs that share the same inventory. Synced automatically from Zentail.'),
          // Alias child banner
          form.alias_of && React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, background: '#FF990015', border: '1px solid #FF990040' } },
            React.createElement(LIcon, { name: 'link', size: 15, color: '#FF9900' }),
            React.createElement('span', { style: { fontSize: 13, color: '#FF9900', fontWeight: 500 } }, 'Alias of '),
            React.createElement('span', { style: { fontSize: 13, fontWeight: 600, color: C.text, fontFamily: 'inherit' } }, form.alias_of),
            React.createElement('span', { style: { fontSize: 11, color: C.textMuted, marginLeft: 4 } }, '(shares inventory with parent SKU)')
          ),
          // Alias parent banner
          aliasChildren.length > 0 ? React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, background: '#6C63FF15', border: '1px solid #6C63FF40', flexWrap: 'wrap' } }, React.createElement(LIcon, { name: 'git-branch', size: 15, color: '#6C63FF' }), React.createElement('span', { style: { fontSize: 13, color: '#6C63FF', fontWeight: 500 } }, 'Alias Parent'), React.createElement('span', { style: { fontSize: 11, color: C.textMuted } }, '\u2014 ' + aliasChildren.length + ' alias SKU' + (aliasChildren.length > 1 ? 's' : '') + ':'), aliasChildren.map(function(a) { return React.createElement('span', { key: a.sku, style: { fontSize: 13, fontWeight: 600, color: C.text, fontFamily: 'inherit', background: C.bg, padding: '2px 8px', borderRadius: 4, border: '1px solid ' + C.border } }, a.sku); })) : null,
          !form.alias_of && aliasChildren.length === 0 && React.createElement('div', { style: { padding: '8px 0', color: C.textMuted, fontSize: 13 } }, 'No alias relationships for this product.')
        ),
        // Kit (Multipack) section
        React.createElement('div', { style: { background: C.surface, borderRadius: 8, padding: '14px 16px', border: '1px solid ' + C.border + '44' } },
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 } },
            React.createElement(LIcon, { name: 'package', size: 16, color: '#00B8D9' }),
            React.createElement('span', { style: { fontSize: 13, fontWeight: 600, color: C.text, textTransform: 'uppercase', letterSpacing: '0.05em' } }, 'Kit (Multipack)')
          ),
          React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12 } }, 'For multipacks \u2014 when this SKU is a pack of another single SKU.'),
          // Kit child banner
          form.kit_parent && React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, background: '#00B8D915', border: '1px solid #00B8D940', marginBottom: 12 } },
            React.createElement(LIcon, { name: 'package', size: 15, color: '#00B8D9' }),
            React.createElement('span', { style: { fontSize: 13, color: '#00B8D9', fontWeight: 500 } }, 'Kit child of '),
            React.createElement('span', { style: { fontSize: 13, fontWeight: 600, color: C.text, fontFamily: 'inherit' } }, form.kit_parent),
            form.kit_qty && React.createElement('span', { style: { fontSize: 13, fontWeight: 600, color: '#00B8D9' } }, ' x' + form.kit_qty),
            React.createElement('span', { style: { fontSize: 11, color: C.textMuted, marginLeft: 4 } }, '(component of a kit SKU)')
          ),
          // Kit parent banner
          kitChildren.length > 0 ? React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, background: '#00875A15', border: '1px solid #00875A40', marginBottom: 12, flexWrap: 'wrap' } }, React.createElement(LIcon, { name: 'layers', size: 15, color: '#00875A' }), React.createElement('span', { style: { fontSize: 13, color: '#00875A', fontWeight: 500 } }, 'Kit Parent'), React.createElement('span', { style: { fontSize: 11, color: C.textMuted } }, '\u2014 ' + kitChildren.length + ' kit SKU' + (kitChildren.length > 1 ? 's' : '') + ':'), kitChildren.map(function(k) { return React.createElement('span', { key: k.sku, style: { fontSize: 13, fontWeight: 600, color: C.text, fontFamily: 'inherit', background: C.bg, padding: '2px 8px', borderRadius: 4, border: '1px solid ' + C.border } }, k.sku, k.kit_qty ? React.createElement('span', { style: { fontSize: 11, color: '#00875A', marginLeft: 4 } }, 'x' + k.kit_qty) : null); })) : null,
          React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 } },
            React.createElement(Inp, { label: 'Kit Parent SKU', value: form.kit_parent || '', onChange: function(e) { setForm(Object.assign({}, form, { kit_parent: e.target.value })); }, placeholder: 'Parent SKU if this is a kit component' }),
            React.createElement(Inp, { label: 'Kit Quantity', value: form.kit_qty != null ? form.kit_qty : '', onChange: function(e) { setForm(Object.assign({}, form, { kit_qty: e.target.value ? parseInt(e.target.value) || '' : '' })); }, placeholder: 'Units per parent', type: 'number' })
          )
        ),
        // Bundle Parent banner
        bundleParents.length > 0 && React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, background: '#E0740015', border: '1px solid #E0740040', marginBottom: 4, flexWrap: 'wrap' } },
          React.createElement(LIcon, { name: 'package', size: 15, color: '#E07400' }),
          React.createElement('span', { style: { fontSize: 13, color: '#E07400', fontWeight: 500 } }, 'Component of ' + bundleParents.length + ' bundle' + (bundleParents.length > 1 ? 's' : '') + ':'),
          bundleParents.map(function(bp) {
            return React.createElement('span', { key: bp.bundle_sku, style: { fontSize: 13, fontWeight: 600, color: C.text, fontFamily: 'inherit', background: C.surface, padding: '2px 8px', borderRadius: 4, border: '1px solid ' + C.border } },
              bp.bundle_sku, React.createElement('span', { style: { fontSize: 11, color: '#E07400', marginLeft: 4 } }, 'x' + bp.quantity));
          })
        ),
        // Bundle Contents section
        React.createElement('div', { style: { background: C.surface, borderRadius: 8, padding: '14px 16px', border: '1px solid ' + C.border + '44' } },
          React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 } },
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
              React.createElement(LIcon, { name: 'layers', size: 16, color: '#E07400' }),
              React.createElement('span', { style: { fontSize: 13, fontWeight: 600, color: C.text, textTransform: 'uppercase', letterSpacing: '0.05em' } }, 'Bundle Contents')
            ),
            bundleContents.length > 0 && React.createElement('span', { style: { fontSize: 12, color: '#E07400', fontWeight: 600 } }, 'Cost: $' + bundleCost.toFixed(2))
          ),
          React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12 } }, 'A bundle of different items sold together as one SKU.'),
          // Component list
          bundleContents.length > 0 && React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 } },
            bundleContents.map(function(bc) {
              return React.createElement('div', { key: bc.id, style: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 6, background: C.bg, border: '1px solid ' + C.border + '44' } },
                React.createElement('div', { style: { flex: 1, minWidth: 0 } },
                  React.createElement('div', { style: { fontSize: 13, fontWeight: 600, color: C.accent, fontFamily: 'inherit' } }, bc.component_sku),
                  React.createElement('div', { style: { fontSize: 12, color: C.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, bc.vendor_title || bc.title || '')
                ),
                bc.unit_cost != null && React.createElement('span', { style: { fontSize: 12, color: C.textMuted } }, '$' + Number(bc.unit_cost).toFixed(2)),
                React.createElement('span', { style: { fontSize: 11, color: C.textMuted } }, '\u00d7'),
                React.createElement('input', { type: 'number', value: bc.quantity, min: 1, style: { width: 50, padding: '4px 6px', background: C.surface, border: '1px solid ' + C.border, color: C.text, borderRadius: 4, fontSize: 13, textAlign: 'center', fontFamily: 'inherit' },
                  onChange: function(e) { var v = parseInt(e.target.value); if (v > 0) updateBundleQty(bc.id, v); }
                }),
                bc.unit_cost != null && React.createElement('span', { style: { fontSize: 12, color: C.text, fontWeight: 500, minWidth: 60, textAlign: 'right' } }, '= $' + (Number(bc.unit_cost) * bc.quantity).toFixed(2)),
                React.createElement('button', { onClick: function() { removeBundleComponent(bc.id); }, title: 'Remove', style: { background: 'none', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 4, color: C.textMuted, display: 'flex' }, onMouseOver: function(e) { e.currentTarget.style.color = '#E5534B'; }, onMouseOut: function(e) { e.currentTarget.style.color = C.textMuted; } },
                  React.createElement(LIcon, { name: 'trash-2', size: 14 })
                )
              );
            })
          ),
          bundleContents.length === 0 && React.createElement('div', { style: { padding: '20px 0', textAlign: 'center', color: C.textMuted, fontSize: 13 } }, 'No components added yet. Search below to add products to this bundle.'),
          // Add component search
          React.createElement('div', { style: { borderTop: '1px solid ' + C.border + '44', paddingTop: 12 } },
            React.createElement('div', { style: { display: 'flex', gap: 8, alignItems: 'flex-end' } },
              React.createElement('div', { style: { flex: 1, position: 'relative' } },
                React.createElement('input', { placeholder: 'Search SKU or product name to add...', value: bundleSearch, onChange: function(e) { doBundleSearch(e.target.value); },
                  style: { width: '100%', padding: '8px 12px', background: C.bg, border: '1px solid ' + C.border, color: C.text, borderRadius: 6, fontSize: 13, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' } }),
                bundleSearchResults.length > 0 && React.createElement('div', { style: { position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, background: C.surface, border: '1px solid ' + C.border, borderRadius: 6, marginTop: 2, maxHeight: 200, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' } },
                  bundleSearchResults.map(function(p) {
                    var alreadyAdded = bundleContents.some(function(bc) { return bc.component_sku === p.sku; });
                    return React.createElement('div', { key: p.sku, onClick: function() { if (!alreadyAdded) addBundleComponent(p.sku); },
                      style: { padding: '8px 12px', cursor: alreadyAdded ? 'default' : 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', opacity: alreadyAdded ? 0.5 : 1, borderBottom: '1px solid ' + C.border + '44' },
                      onMouseOver: function(e) { if (!alreadyAdded) e.currentTarget.style.background = C.bg; },
                      onMouseOut: function(e) { e.currentTarget.style.background = 'transparent'; }
                    },
                      React.createElement('div', null,
                        React.createElement('span', { style: { fontSize: 13, fontWeight: 600, color: C.accent, fontFamily: 'inherit' } }, p.sku),
                        React.createElement('span', { style: { fontSize: 12, color: C.textMuted, marginLeft: 8 } }, p.vendor_title || p.title || '')
                      ),
                      React.createElement('span', { style: { fontSize: 12, color: C.textMuted } }, alreadyAdded ? 'Added' : (p.unit_cost ? '$' + Number(p.unit_cost).toFixed(2) : ''))
                    );
                  })
                )
              ),
              React.createElement('input', { type: 'number', value: bundleAddQty, min: 1, onChange: function(e) { setBundleAddQty(parseInt(e.target.value) || 1); },
                style: { width: 55, padding: '8px 6px', background: C.bg, border: '1px solid ' + C.border, color: C.text, borderRadius: 6, fontSize: 13, textAlign: 'center', fontFamily: 'inherit' }, title: 'Quantity' })
            )
          )
        )
      )
    );
  } else if (activeTab === 'sourcing') {
    content = React.createElement('div', { style: sectionStyle },
      React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 16 } },
        isFieldVisible('vendor_title') && React.createElement(Inp, { label: labelWithSource('Vendor Title', 'vendor_title'), ...F('vendor_title') }),
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 } },
          React.createElement(Sel, { label: labelWithSource('Vendor', 'vendor_name'), options: vendors.map(function(v) { return { value: v.id, label: v.vendor_name }; }), value: form.vendor_id || '', onChange: function(e) { setForm(Object.assign({}, form, { vendor_id: e.target.value ? Number(e.target.value) : null })); } }),
          isFieldVisible('vendor_sku') && React.createElement(Inp, { label: labelWithSource('Vendor SKU', 'vendor_sku'), ...F('vendor_sku') }),
          React.createElement(Sel, { label: labelWithSource('Default Warehouse', 'default_warehouse'), options: warehouses.map(function(w) { return { value: w.id, label: w.warehouse_id + ' \u2014 ' + w.name }; }), value: form.warehouse_id || '', onChange: function(e) { setForm(Object.assign({}, form, { warehouse_id: e.target.value ? Number(e.target.value) : null })); } }),
          isFieldVisible('unit_cost') && React.createElement(Inp, { label: labelWithSource('Unit Cost ($)', 'unit_cost'), type: 'number', step: '0.01', ...F('unit_cost') })
        ),
        (function() {
          var caseFields = [
            { key: 'case_qty', label: 'Qty/Case', type: 'number' },
            { key: 'max_po_qty', label: 'Max Units / PO', type: 'number', step: '1', min: '0', placeholder: 'No limit' },
            { key: 'weight', label: 'Case Weight (lbs)', type: 'number', step: '0.01' },
            { key: 'length', label: 'Case Length (in)', type: 'number', step: '0.01' },
            { key: 'width', label: 'Case Width (in)', type: 'number', step: '0.01' },
            { key: 'height', label: 'Case Height (in)', type: 'number', step: '0.01' }
          ].filter(function(f) { return isFieldVisible(f.key); });
          if (caseFields.length === 0) return null;
          // Max Units / PO hint — warns when the max isn't a whole number of cases, since
          // the effective cap rounds DOWN to whole cases (effectivePoCap, app-utils.js).
          var maxPo = parseInt(form.max_po_qty) || 0, cqForCap = parseInt(form.case_qty) || 0;
          var maxNotWholeCases = maxPo > 0 && cqForCap > 1 && (maxPo % cqForCap !== 0);
          var maxPoHint = isFieldVisible('max_po_qty') ? React.createElement('div', { style: { fontSize: 11, color: maxNotWholeCases ? C.warning : C.textDim, marginTop: 10 } },
            'Max Units / PO caps how many units of this SKU go on one PO. Bigger needs are split across several draft POs by the forecast auto-fill, Add to PO and bulk SKU+Qty. Leave blank for no limit.' +
            (maxNotWholeCases ? ' ⚠ ' + maxPo + ' is not a multiple of the ' + cqForCap + '-unit case pack, so the effective cap is ' + effectivePoCap(maxPo, cqForCap) + '.' : '')
          ) : null;
          return React.createElement('div', { style: { background: C.surface, borderRadius: 8, padding: '14px 16px', border: '1px solid ' + C.border + '44' } },
            React.createElement('div', { style: { fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 } }, 'Case Information'),
            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 } },
              caseFields.map(function(f) { return React.createElement(Inp, { key: f.key, label: labelWithSource(f.label, f.key), type: f.type, step: f.step, min: f.min, placeholder: f.placeholder, ...F(f.key) }); })
            ),
            maxPoHint
          );
        })(),
        (function() {
          var pkgFields = [
            { key: 'package_weight', label: 'Package Weight (lbs)' },
            { key: 'package_length', label: 'Package Length (in)' },
            { key: 'package_width', label: 'Package Width (in)' },
            { key: 'package_height', label: 'Package Height (in)' }
          ].filter(function(f) { return isFieldVisible(f.key); });
          var epf = getMatchingCustomFields('sourcing', /^package_/).filter(function(tf) { return isFieldVisible(tf.field_name); });
          if (pkgFields.length === 0 && epf.length === 0) return null;
          return React.createElement('div', { style: { background: C.surface, borderRadius: 8, padding: '14px 16px', border: '1px solid ' + C.border + '44' } },
            React.createElement('div', { style: { fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 } }, 'Unit Package Dimensions'),
            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 } },
              pkgFields.map(function(f) { return React.createElement(Inp, { key: f.key, label: labelWithSource(f.label, f.key), ...F(f.key) }); }),
              epf.map(function(tf) { return React.createElement(Inp, { key: tf.field_name, label: labelWithSource(tf.display_label || tf.field_name, tf.field_name), ...F(tf.field_name) }); })
            )
          );
        })(),
        React.createElement('div', { style: { background: C.surface, borderRadius: 8, padding: '14px 16px', border: '1px solid ' + C.border + '44' } },
          React.createElement('div', { style: { fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 } }, 'Amazon Transparency'),
          React.createElement('label', { style: { fontSize: 13, color: C.text, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' } },
            React.createElement('input', { type: 'checkbox', checked: form.requires_transparency === true || form.requires_transparency === 'true', onChange: function(e) { setForm(Object.assign({}, form, { requires_transparency: e.target.checked })); } }),
            'Requires Transparency labels'
          ),
          React.createElement('div', { style: { fontSize: 11, color: C.textDim, marginTop: 6 } }, 'Every unit shipped to Amazon needs a serialized Transparency label in addition to the FNSKU label. Surfaces on work orders and PO lines; auto-set when label files are uploaded to a PO.')
        ),
        renderCustomFields('sourcing')
      )
    );
  } else if (activeTab === 'inventory') {
    // Group inventory by warehouse
    var warehouseGroups = {};
    productInventory.forEach(function(inv) {
      // Hide zentail-zone bins with 0 quantity (stale FBA data)
      if (inv.zone === 'zentail' && (inv.quantity || 0) === 0) return;
      var key = inv.wh_code || 'Unknown';
      if (!warehouseGroups[key]) warehouseGroups[key] = { name: inv.warehouse_name, wh_code: inv.wh_code, bins: [], total: 0 };
      warehouseGroups[key].bins.push(inv);
      warehouseGroups[key].total += (inv.quantity || 0);
    });
    var whKeys = Object.keys(warehouseGroups).filter(function(k) {
      var wh = warehouseGroups[k];
      if (wh.total > 0) return true;
      return wh.bins.some(function(b) { return b.bin_code && b.bin_code !== 'DEFAULT'; });
    }).sort();
    var visibleBinCount = whKeys.reduce(function(s, k) { return s + warehouseGroups[k].bins.length; }, 0);
    var grandTotal = productInventory.reduce(function(s, inv) { return s + (inv.quantity || 0); }, 0);

    // Compute summary stats
    // 'received' POs await put-away/closure — count qty_received as still incoming (mirrors analysis.js)
    var poRemaining = function(li) {
      return li.po_status === 'received' ? (li.qty_received || 0) : Math.max(0, (li.qty_ordered || 0) - (li.qty_received || 0));
    };
    var totalOnOrder = invSummary.po_lines.reduce(function(s, li) { return s + poRemaining(li); }, 0);
    var totalFbaInTransit = invSummary.fba_items.reduce(function(s, fi) { return s + (fi.quantity_packed || 0); }, 0);
    var totalWfsInbound = (invSummary.wfs_items || []).reduce(function(s, wi) { return s + (wi.quantity_packed || 0); }, 0);
    var totalTransfers = totalFbaInTransit + totalWfsInbound;

    // Sub-tab pill style
    var invSubTabStyle = function(tab) {
      var active = invSubTab === tab;
      return { padding: '6px 14px', fontSize: 12, fontWeight: 500, fontFamily: 'inherit', borderRadius: 6, border: '1px solid ' + (active ? C.accent : C.border), background: active ? C.accent + '22' : 'transparent', color: active ? C.accent : C.textMuted, cursor: 'pointer', transition: 'all 0.15s', textDecoration: 'none' };
    };

    // Alias/kit banners
    var invBanners = React.createElement(React.Fragment, null,
      form.alias_of && React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, background: '#FF990015', border: '1px solid #FF990040', marginBottom: 12 } },
        React.createElement(LIcon, { name: 'link', size: 15, color: '#FF9900' }),
        React.createElement('span', { style: { fontSize: 13, color: '#FF9900', fontWeight: 500 } }, 'Alias of '),
        React.createElement('span', { style: { fontSize: 13, fontWeight: 600, color: C.text, fontFamily: 'inherit' } }, form.alias_of),
        React.createElement('span', { style: { fontSize: 11, color: C.textMuted, marginLeft: 4 } }, '(shares inventory with parent SKU)')
      ),
      aliasChildren.length > 0 ? React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, background: '#6C63FF15', border: '1px solid #6C63FF40', marginBottom: 12, flexWrap: 'wrap' } }, React.createElement(LIcon, { name: 'git-branch', size: 15, color: '#6C63FF' }), React.createElement('span', { style: { fontSize: 13, color: '#6C63FF', fontWeight: 500 } }, 'Alias Parent'), React.createElement('span', { style: { fontSize: 11, color: C.textMuted } }, '\u2014 ' + aliasChildren.length + ' alias SKU' + (aliasChildren.length > 1 ? 's' : '') + ':'), aliasChildren.map(function(a) { return React.createElement('span', { key: a.sku, style: { fontSize: 13, fontWeight: 600, color: C.text, fontFamily: 'inherit', background: C.surface, padding: '2px 8px', borderRadius: 4, border: '1px solid ' + C.border } }, a.sku); })) : null,
      form.kit_parent && React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, background: '#00B8D915', border: '1px solid #00B8D940', marginBottom: 12 } },
        React.createElement(LIcon, { name: 'package', size: 15, color: '#00B8D9' }),
        React.createElement('span', { style: { fontSize: 13, color: '#00B8D9', fontWeight: 500 } }, 'Kit child of '),
        React.createElement('span', { style: { fontSize: 13, fontWeight: 600, color: C.text, fontFamily: 'inherit' } }, form.kit_parent),
        form.kit_qty && React.createElement('span', { style: { fontSize: 13, fontWeight: 600, color: '#00B8D9' } }, ' x' + form.kit_qty),
        React.createElement('span', { style: { fontSize: 11, color: C.textMuted, marginLeft: 4 } }, '(component of a kit SKU)')
      ),
      kitChildren.length > 0 ? React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, background: '#00875A15', border: '1px solid #00875A40', marginBottom: 12, flexWrap: 'wrap' } }, React.createElement(LIcon, { name: 'layers', size: 15, color: '#00875A' }), React.createElement('span', { style: { fontSize: 13, color: '#00875A', fontWeight: 500 } }, 'Kit Parent'), React.createElement('span', { style: { fontSize: 11, color: C.textMuted } }, '\u2014 ' + kitChildren.length + ' kit SKU' + (kitChildren.length > 1 ? 's' : '') + ':'), kitChildren.map(function(k) { return React.createElement('span', { key: k.sku, style: { fontSize: 13, fontWeight: 600, color: C.text, fontFamily: 'inherit', background: C.surface, padding: '2px 8px', borderRadius: 4, border: '1px solid ' + C.border } }, k.sku, k.kit_qty ? React.createElement('span', { style: { fontSize: 11, color: '#00875A', marginLeft: 4 } }, 'x' + k.kit_qty) : null); })) : null
    );

    // Sub-tab content
    var invSubContent = null;

    if (invSubTab === 'overview') {
      // Overview: stat cards + quick summary
      var statCard = function(icon, label, value, color) {
        return React.createElement('div', { style: { flex: '1 1 140px', padding: '16px', background: C.surface, borderRadius: 8, border: '1px solid ' + C.border + '44', display: 'flex', flexDirection: 'column', gap: 6 } },
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 6 } },
            React.createElement(LIcon, { name: icon, size: 14, color: color || C.textMuted }),
            React.createElement('span', { style: { fontSize: 11, color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' } }, label)
          ),
          React.createElement('div', { style: { fontSize: 22, fontWeight: 700, color: color || C.text } }, value)
        );
      };
      invSubContent = React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 16 } },
        React.createElement('div', { style: { display: 'flex', gap: 12, flexWrap: 'wrap' } },
          statCard('box', 'On Hand', grandTotal.toLocaleString(), C.accent),
          statCard('shopping-cart', 'On Order', totalOnOrder.toLocaleString(), '#f59e0b'),
          statCard('truck', 'Warehouse Transfers', totalTransfers.toLocaleString(), '#8b5cf6'),
          statCard('layers', 'Total Pipeline', (grandTotal + totalOnOrder + totalTransfers).toLocaleString(), C.success)
        ),
        // Breakdown by warehouse
        whKeys.length > 0 && React.createElement('div', { style: { background: C.surface, borderRadius: 8, border: '1px solid ' + C.border + '44', overflow: 'hidden' } },
          React.createElement('div', { style: { padding: '10px 16px', borderBottom: '1px solid ' + C.border + '44', fontSize: 12, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em' } }, 'On Hand by Warehouse'),
          whKeys.map(function(whKey) {
            var wh = warehouseGroups[whKey];
            var pct = grandTotal > 0 ? Math.round((wh.total / grandTotal) * 100) : 0;
            return React.createElement('div', { key: whKey, style: { display: 'flex', alignItems: 'center', gap: 12, padding: '8px 16px', borderBottom: '1px solid ' + C.border + '22' } },
              React.createElement(LIcon, { name: 'home', size: 13, color: C.textMuted }),
              React.createElement('span', { style: { fontSize: 13, fontWeight: 500, color: C.text, minWidth: 120 } }, whKey),
              React.createElement('div', { style: { flex: 1, height: 6, background: C.border + '44', borderRadius: 3, overflow: 'hidden' } },
                React.createElement('div', { style: { width: pct + '%', height: '100%', background: C.accent, borderRadius: 3, transition: 'width 0.3s' } })
              ),
              React.createElement('span', { style: { fontSize: 13, fontWeight: 600, color: C.text, minWidth: 60, textAlign: 'right' } }, wh.total.toLocaleString()),
              React.createElement('span', { style: { fontSize: 11, color: C.textMuted, minWidth: 36, textAlign: 'right' } }, pct + '%')
            );
          })
        ),
        // Active POs summary
        invSummary.po_lines.length > 0 && React.createElement('div', { style: { background: C.surface, borderRadius: 8, border: '1px solid ' + C.border + '44', padding: '12px 16px' } },
          React.createElement('div', { style: { fontSize: 12, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 } }, 'Active Purchase Orders'),
          React.createElement('div', { style: { fontSize: 13, color: C.text } },
            (function() {
              var poMap = {};
              invSummary.po_lines.forEach(function(li) { poMap[li.po_number] = li.po_status; });
              return Object.keys(poMap).map(function(pon) {
                var st = poMap[pon];
                var stColor = st === 'shipped' ? '#8b5cf6' : st === 'confirmed' ? C.success : st === 'submitted' ? '#3b82f6' : '#f59e0b';
                return React.createElement('span', { key: pon, style: { display: 'inline-flex', alignItems: 'center', gap: 6, marginRight: 12, marginBottom: 4 } },
                  React.createElement('span', { style: { fontFamily: 'inherit', fontWeight: 600, fontSize: 12 } }, pon),
                  React.createElement('span', { style: { fontSize: 10, padding: '1px 6px', borderRadius: 8, background: stColor + '22', color: stColor, fontWeight: 600, textTransform: 'capitalize' } }, st)
                );
              });
            })()
          )
        ),
        // Active FBA summary
        invSummary.fba_items.length > 0 && React.createElement('div', { style: { background: C.surface, borderRadius: 8, border: '1px solid ' + C.border + '44', padding: '12px 16px' } },
          React.createElement('div', { style: { fontSize: 12, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 } }, 'Active FBA Shipments'),
          React.createElement('div', { style: { fontSize: 13, color: C.text } },
            (function() {
              // Group by shipment_id so each FBA shipment ID shows separately —
              // grouping by plan_id would hide that a plan can split across FCs.
              var shipMap = {};
              // Amazon only issues the FBA… ID after transport confirmation. Pre-placement
              // plans have only a raw sh… UUID — surface those as "Pending — wf…" so the
              // chip stays informative without faking an FBA ID we don't have.
              var fbaLabel = function(fi) {
                if (fi.shipment_confirmation_id) return fi.shipment_confirmation_id;
                if (fi.amazon_shipment_id && /^FBA/i.test(fi.amazon_shipment_id)) return fi.amazon_shipment_id;
                return 'Pending — ' + (fi.amazon_plan_id || 'plan #' + fi.plan_id);
              };
              invSummary.fba_items.forEach(function(fi) {
                var key = fi.shipment_id || fi.plan_id;
                if (!shipMap[key]) shipMap[key] = { name: fbaLabel(fi), status: fi.shipment_status || fi.plan_status, qty: 0 };
                shipMap[key].qty += fi.quantity || 0;
              });
              return Object.keys(shipMap).map(function(sid) {
                var p = shipMap[sid];
                var stColor = p.status === 'shipped' ? '#8b5cf6' : p.status === 'packing' ? '#f59e0b' : '#3b82f6';
                return React.createElement('span', { key: sid, style: { display: 'inline-flex', alignItems: 'center', gap: 6, marginRight: 12, marginBottom: 4 } },
                  React.createElement('span', { style: { fontFamily: 'inherit', fontWeight: 600, fontSize: 12 } }, p.name || 'Shipment #' + sid),
                  React.createElement('span', { style: { fontSize: 10, padding: '1px 6px', borderRadius: 8, background: stColor + '22', color: stColor, fontWeight: 600, textTransform: 'capitalize' } }, p.status),
                  React.createElement('span', { style: { fontSize: 11, color: C.textMuted } }, p.qty + ' units')
                );
              });
            })()
          )
        )
      );
    } else if (invSubTab === 'warehouse') {
      // Warehouse Stock: existing bin-level inventory
      invSubContent = inventoryLoading ? React.createElement('div', { style: { textAlign: 'center', padding: 40, color: C.textMuted } }, 'Loading inventory...') :
        productInventory.length === 0 ?
          React.createElement('div', { style: { textAlign: 'center', padding: '40px 20px', color: C.textMuted, fontSize: 13 } },
            React.createElement(LIcon, { name: 'box', size: 24 }),
            React.createElement('div', { style: { marginTop: 8 } }, 'No warehouse inventory found.')
          ) :
        React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 16 } },
          React.createElement('div', { style: { display: 'flex', gap: 16, alignItems: 'center', padding: '12px 16px', background: C.surface, borderRadius: 8, border: '1px solid ' + C.border + '44' } },
            React.createElement('div', { style: { fontSize: 13, color: C.textMuted } }, 'Total Quantity:'),
            React.createElement('div', { style: { fontSize: 18, fontWeight: 700, color: C.accent } }, grandTotal.toLocaleString()),
            React.createElement('div', { style: { fontSize: 13, color: C.textMuted, marginLeft: 'auto' } }, whKeys.length + ' warehouse' + (whKeys.length !== 1 ? 's' : '') + ', ' + visibleBinCount + ' bin' + (visibleBinCount !== 1 ? 's' : ''))
          ),
          whKeys.map(function(whKey) {
            var wh = warehouseGroups[whKey];
            return React.createElement('div', { key: whKey, style: { background: C.surface, borderRadius: 8, border: '1px solid ' + C.border + '44', overflow: 'hidden' } },
              React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid ' + C.border + '44' } },
                React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
                  React.createElement(LIcon, { name: 'home', size: 14, color: C.textMuted }),
                  React.createElement('span', { style: { fontSize: 13, fontWeight: 600, color: C.text } }, wh.wh_code + ' \u2014 ' + wh.name)
                ),
                React.createElement(Badge, { color: C.accent }, wh.total.toLocaleString() + ' units')
              ),
              React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '2fr 80px 140px 80px', gap: 0, padding: '6px 16px', fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid ' + C.border + '33' } },
                React.createElement('div', null, 'Bin'),
                React.createElement('div', null, 'Type'),
                React.createElement('div', null, 'Status'),
                React.createElement('div', null, 'Qty')
              ),
              wh.bins.map(function(inv) {
                var is3pl = inv.warehouse_type === '3pl';
                var isAmazon = inv.zone === 'amazon';
                var binDisplay = isAmazon ? inv.bin_code : is3pl ? (inv.bin_display_name || inv.bin_code) : inv.bin_code + (inv.bin_label ? ' (' + inv.bin_label + ')' : '');
                var typeDisplay = isAmazon ? 'FBA' : is3pl ? '3PL' : (inv.bin_type || '\u2014');
                var statusLabel = isAmazon ? inv.bin_code.toLowerCase() : (inv.bin_status || 'unknown');
                var statusColor = isAmazon
                  ? (inv.bin_code === 'ON HAND' ? C.success : inv.bin_code === 'RESERVED' ? '#3b82f6' : inv.bin_code === 'FC TRANSFER' ? '#f59e0b' : inv.bin_code === 'UNFULFILLABLE' ? C.danger : C.textMuted)
                  : (inv.bin_status === 'active' ? C.success : inv.bin_status === 'full' ? '#f59e0b' : inv.bin_status === 'damaged' || inv.bin_status === 'unsellable' ? C.danger : C.textMuted);
                return React.createElement('div', { key: inv.id || wh.wh_code, style: { display: 'grid', gridTemplateColumns: '2fr 80px 140px 80px', gap: 0, padding: '8px 16px', borderBottom: '1px solid ' + C.border + '22', alignItems: 'center', fontSize: 13 } },
                  React.createElement('div', { style: { fontWeight: 500, fontFamily: 'inherit', fontSize: 12 } }, binDisplay),
                  React.createElement('div', { style: { color: C.textMuted, fontSize: 12, textTransform: 'capitalize' } }, typeDisplay),
                  React.createElement('div', null, React.createElement('span', { style: { fontSize: 11, padding: '2px 8px', borderRadius: 10, background: statusColor + '22', color: statusColor, fontWeight: 600, textTransform: 'capitalize' } }, statusLabel)),
                  React.createElement('div', { style: { fontWeight: 600, fontSize: 14 } }, (inv.quantity || 0).toLocaleString())
                );
              })
            );
          })
        );
    } else if (invSubTab === 'on_order') {
      // On Order: PO line items
      invSubContent = invSummaryLoading ? React.createElement('div', { style: { textAlign: 'center', padding: 40, color: C.textMuted } }, 'Loading...') :
        invSummary.po_lines.length === 0 ?
          React.createElement('div', { style: { textAlign: 'center', padding: '40px 20px', color: C.textMuted, fontSize: 13 } },
            React.createElement(LIcon, { name: 'shopping-cart', size: 24 }),
            React.createElement('div', { style: { marginTop: 8 } }, 'No active purchase orders for this product.')
          ) :
        React.createElement('div', { style: { background: C.surface, borderRadius: 8, border: '1px solid ' + C.border + '44', overflow: 'hidden' } },
          React.createElement('div', { style: { display: 'flex', gap: 16, alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid ' + C.border + '44' } },
            React.createElement('span', { style: { fontSize: 13, color: C.textMuted } }, 'Total on order:'),
            React.createElement('span', { style: { fontSize: 16, fontWeight: 700, color: '#f59e0b' } }, totalOnOrder.toLocaleString() + ' units'),
            React.createElement('span', { style: { fontSize: 12, color: C.textMuted, marginLeft: 'auto' } }, invSummary.po_lines.length + ' line' + (invSummary.po_lines.length !== 1 ? 's' : '') + ' across ' + (function() { var s = {}; invSummary.po_lines.forEach(function(l) { s[l.po_id] = 1; }); return Object.keys(s).length; })() + ' PO' + ((function() { var s = {}; invSummary.po_lines.forEach(function(l) { s[l.po_id] = 1; }); return Object.keys(s).length; })() !== 1 ? 's' : ''))
          ),
          // Table header
          React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '100px 1fr 100px 80px 80px 80px', gap: 0, padding: '6px 16px', fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid ' + C.border + '33' } },
            React.createElement('div', null, 'PO #'),
            React.createElement('div', null, 'Vendor'),
            React.createElement('div', null, 'Status'),
            React.createElement('div', { style: { textAlign: 'right' } }, 'Ordered'),
            React.createElement('div', { style: { textAlign: 'right' } }, 'Received'),
            React.createElement('div', { style: { textAlign: 'right' } }, 'Remaining')
          ),
          invSummary.po_lines.map(function(li) {
            var remaining = poRemaining(li);
            var stColor = li.po_status === 'shipped' ? '#8b5cf6' : li.po_status === 'confirmed' ? C.success : li.po_status === 'submitted' ? '#3b82f6' : '#f59e0b';
            return React.createElement('div', { key: li.id, style: { display: 'grid', gridTemplateColumns: '100px 1fr 100px 80px 80px 80px', gap: 0, padding: '8px 16px', borderBottom: '1px solid ' + C.border + '22', alignItems: 'center', fontSize: 13 } },
              // PO # opens that exact PO in a new tab (Anthony 9-27; same look as the vendor page's extLink)
              React.createElement('div', { style: { fontWeight: 600, fontFamily: 'inherit', fontSize: 12 } },
                li.po_id ? React.createElement('a', { href: '/po/' + li.po_id, target: '_blank', rel: 'noopener', title: 'Open PO in new tab',
                  onClick: function(e) { e.stopPropagation(); },
                  onMouseEnter: function(e) { e.currentTarget.style.textDecoration = 'underline'; },
                  onMouseLeave: function(e) { e.currentTarget.style.textDecoration = 'none'; },
                  style: { color: C.accent, textDecoration: 'none', textUnderlineOffset: '2px', cursor: 'pointer', whiteSpace: 'nowrap' } },
                  li.po_number,
                  React.createElement(LIcon, { name: 'external-link', size: 11, style: { marginLeft: 3, verticalAlign: 'middle', opacity: 0.7 } }))
                : li.po_number),
              React.createElement('div', { style: { color: C.textMuted, fontSize: 12 } }, li.vendor_name || '\u2014'),
              React.createElement('div', null, React.createElement('span', { style: { fontSize: 10, padding: '2px 8px', borderRadius: 8, background: stColor + '22', color: stColor, fontWeight: 600, textTransform: 'capitalize' } }, li.po_status)),
              React.createElement('div', { style: { textAlign: 'right', fontWeight: 500 } }, (li.qty_ordered || 0).toLocaleString()),
              React.createElement('div', { style: { textAlign: 'right', fontWeight: 500, color: C.success } }, (li.qty_received || 0).toLocaleString()),
              React.createElement('div', { style: { textAlign: 'right', fontWeight: 700, color: remaining > 0 ? '#f59e0b' : C.success } }, remaining.toLocaleString())
            );
          })
        );
    } else if (invSubTab === 'fba') {
      // FBA + WFS Shipments
      var hasAny = invSummary.fba_items.length > 0 || (invSummary.wfs_items || []).length > 0;
      invSubContent = invSummaryLoading ? React.createElement('div', { style: { textAlign: 'center', padding: 40, color: C.textMuted } }, 'Loading...') :
        !hasAny ?
          React.createElement('div', { style: { textAlign: 'center', padding: '40px 20px', color: C.textMuted, fontSize: 13 } },
            React.createElement(LIcon, { name: 'truck', size: 24 }),
            React.createElement('div', { style: { marginTop: 8 } }, 'No active shipments for this product.')
          ) :
        React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 16 } },
          // FBA section
          invSummary.fba_items.length > 0 && React.createElement('div', { style: { background: C.surface, borderRadius: 8, border: '1px solid ' + C.border + '44', overflow: 'hidden' } },
            React.createElement('div', { style: { display: 'flex', gap: 16, alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid ' + C.border + '44' } },
              React.createElement('span', { style: { fontSize: 13, fontWeight: 600, color: C.text } }, 'FBA Inbound'),
              React.createElement('span', { style: { fontSize: 14, fontWeight: 700, color: '#8b5cf6' } }, totalFbaInTransit.toLocaleString() + ' units'),
              React.createElement('span', { style: { fontSize: 12, color: C.textMuted, marginLeft: 'auto' } }, invSummary.fba_items.length + ' shipment' + (invSummary.fba_items.length !== 1 ? 's' : ''))
            ),
            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 80px 130px 100px 70px 70px', gap: 0, padding: '6px 16px', fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid ' + C.border + '33' } },
              React.createElement('div', null, 'Plan'),
              React.createElement('div', null, 'Dest FC'),
              React.createElement('div', null, 'Status'),
              React.createElement('div', null, 'Tracking'),
              React.createElement('div', { style: { textAlign: 'right' } }, 'Qty'),
              React.createElement('div', { style: { textAlign: 'right' } }, 'Packed')
            ),
            invSummary.fba_items.map(function(fi) {
              var stColor = fi.shipment_status === 'shipped' ? '#8b5cf6' : fi.shipment_status === 'packed' ? C.success : fi.shipment_status === 'packing' ? '#f59e0b' : '#3b82f6';
              var stLabel = (fi.shipment_status || '').replace(/_/g, ' ');
              return React.createElement('div', { key: fi.id, style: { display: 'grid', gridTemplateColumns: '1fr 80px 130px 100px 70px 70px', gap: 0, padding: '8px 16px', borderBottom: '1px solid ' + C.border + '22', alignItems: 'center', fontSize: 13 } },
                React.createElement('div', { style: { fontWeight: 600, fontSize: 12 } }, (fi.shipment_confirmation_id || (fi.amazon_shipment_id && /^FBA/i.test(fi.amazon_shipment_id) ? fi.amazon_shipment_id : null)) || ('Pending — ' + (fi.amazon_plan_id || 'plan #' + fi.plan_id))),
                React.createElement('div', { style: { fontFamily: 'inherit', fontSize: 12, color: C.textMuted } }, fi.destination_fc || '\u2014'),
                React.createElement('div', null, React.createElement('span', { style: { fontSize: 10, padding: '2px 8px', borderRadius: 8, background: stColor + '22', color: stColor, fontWeight: 600, textTransform: 'capitalize', whiteSpace: 'nowrap' } }, stLabel)),
                React.createElement('div', { style: { fontSize: 11, color: C.textMuted } }, fi.tracking_id ? React.createElement('span', { style: { fontFamily: 'inherit' } }, fi.tracking_id) : '\u2014'),
                React.createElement('div', { style: { textAlign: 'right', fontWeight: 600 } }, (fi.quantity || 0).toLocaleString()),
                React.createElement('div', { style: { textAlign: 'right', fontWeight: 500, color: (fi.quantity_packed || 0) >= (fi.quantity || 0) ? C.success : '#f59e0b' } }, (fi.quantity_packed || 0).toLocaleString())
              );
            })
          ),
          // WFS section
          (invSummary.wfs_items || []).length > 0 && React.createElement('div', { style: { background: C.surface, borderRadius: 8, border: '1px solid ' + C.border + '44', overflow: 'hidden' } },
            React.createElement('div', { style: { display: 'flex', gap: 16, alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid ' + C.border + '44' } },
              React.createElement('span', { style: { fontSize: 13, fontWeight: 600, color: C.text } }, 'WFS Inbound'),
              React.createElement('span', { style: { fontSize: 14, fontWeight: 700, color: '#0891b2' } }, totalWfsInbound.toLocaleString() + ' units'),
              React.createElement('span', { style: { fontSize: 12, color: C.textMuted, marginLeft: 'auto' } }, (invSummary.wfs_items || []).length + ' shipment' + ((invSummary.wfs_items || []).length !== 1 ? 's' : ''))
            ),
            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 80px 130px 100px 70px 70px 70px', gap: 0, padding: '6px 16px', fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid ' + C.border + '33' } },
              React.createElement('div', null, 'Shipment ID'),
              React.createElement('div', null, 'Dest FC'),
              React.createElement('div', null, 'Status'),
              React.createElement('div', null, 'Tracking'),
              React.createElement('div', { style: { textAlign: 'right' } }, 'Qty'),
              React.createElement('div', { style: { textAlign: 'right' } }, 'Packed'),
              React.createElement('div', { style: { textAlign: 'right' } }, 'Received')
            ),
            (invSummary.wfs_items || []).map(function(wi) {
              var stColor = wi.plan_status === 'shipped' ? '#8b5cf6' : wi.plan_status === 'delivered' ? C.success : wi.plan_status === 'labels_ready' ? '#0891b2' : wi.plan_status === 'packed' ? '#14b8a6' : '#f59e0b';
              var stLabel = (wi.plan_status || '').replace(/_/g, ' ');
              var showReceived = ['shipped', 'delivered'].indexOf(wi.plan_status) >= 0;
              var itemReceived = wi.quantity_received || 0;
              return React.createElement('div', { key: wi.id, style: { display: 'grid', gridTemplateColumns: '1fr 80px 130px 100px 70px 70px 70px', gap: 0, padding: '8px 16px', borderBottom: '1px solid ' + C.border + '22', alignItems: 'center', fontSize: 13 } },
                React.createElement('div', { style: { fontWeight: 600, fontSize: 12, fontFamily: 'inherit' } }, wi.walmart_shipment_id || 'Plan #' + wi.plan_id),
                React.createElement('div', { style: { fontFamily: 'inherit', fontSize: 12, color: C.textMuted } }, wi.destination_fc || '\u2014'),
                React.createElement('div', null, React.createElement('span', { style: { fontSize: 10, padding: '2px 8px', borderRadius: 8, background: stColor + '22', color: stColor, fontWeight: 600, textTransform: 'capitalize', whiteSpace: 'nowrap' } }, stLabel)),
                React.createElement('div', { style: { fontSize: 11, color: C.textMuted } }, wi.tracking_number ? React.createElement('span', { style: { fontFamily: 'inherit' } }, wi.tracking_number) : '\u2014'),
                React.createElement('div', { style: { textAlign: 'right', fontWeight: 600 } }, (wi.quantity || 0).toLocaleString()),
                React.createElement('div', { style: { textAlign: 'right', fontWeight: 500, color: (wi.quantity_packed || 0) >= (wi.quantity || 0) ? C.success : '#f59e0b' } }, (wi.quantity_packed || 0).toLocaleString()),
                React.createElement('div', { style: { textAlign: 'right', fontWeight: 600, color: showReceived && itemReceived > 0 ? C.success : C.textDim } },
                  showReceived ? itemReceived.toLocaleString() : '\u2014')
              );
            })
          )
        );
    }

    content = React.createElement('div', { style: sectionStyle },
      invBanners,
      // Sub-tab pills
      React.createElement('div', { style: { display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' } },
        React.createElement('a', { href: pid ? ('/catalog/' + pid + '?tab=inventory') : undefined, onClick: function(e) { if (pid && (e.ctrlKey || e.metaKey || e.shiftKey)) return; e.preventDefault(); setInvSubTab('overview'); }, style: invSubTabStyle('overview') }, 'Overview'),
        React.createElement('a', { href: pid ? ('/catalog/' + pid + '?tab=inventory&inv=warehouse') : undefined, onClick: function(e) { if (pid && (e.ctrlKey || e.metaKey || e.shiftKey)) return; e.preventDefault(); setInvSubTab('warehouse'); }, style: invSubTabStyle('warehouse') }, 'Warehouse Stock'),
        React.createElement('a', { href: pid ? ('/catalog/' + pid + '?tab=inventory&inv=on_order') : undefined, onClick: function(e) { if (pid && (e.ctrlKey || e.metaKey || e.shiftKey)) return; e.preventDefault(); setInvSubTab('on_order'); }, style: invSubTabStyle('on_order') }, 'On Order (' + totalOnOrder.toLocaleString() + ')'),
        React.createElement('a', { href: pid ? ('/catalog/' + pid + '?tab=inventory&inv=fba') : undefined, onClick: function(e) { if (pid && (e.ctrlKey || e.metaKey || e.shiftKey)) return; e.preventDefault(); setInvSubTab('fba'); }, style: invSubTabStyle('fba') }, 'Warehouse Transfers (3PL,FBA,WFS)' + (totalTransfers > 0 ? ' (' + totalTransfers.toLocaleString() + ')' : ''))
      ),
      invSubContent,
      renderCustomFields('inventory')
    );
  } else if (activeTab === 'images') {
    content = React.createElement('div', { style: sectionStyle },
      imagesLoading ? React.createElement('div', { style: { textAlign: 'center', padding: 40, color: C.textMuted } }, 'Loading images...') :
      React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 16 } },
        React.createElement('div', { style: { fontSize: 11, color: C.textDim, display: 'flex', alignItems: 'center', gap: 6 } },
          React.createElement(LIcon, { name: 'info', size: 12 }), 'Images are managed by Zentail and synced automatically.'),
        // Image grid
        images.length === 0 ?
          React.createElement('div', { style: { textAlign: 'center', padding: '50px 20px', color: C.textMuted, fontSize: 13 } },
            React.createElement(LIcon, { name: 'image', size: 28 }),
            React.createElement('div', { style: { marginTop: 8 } }, 'No images yet. Images will appear after the next Zentail sync.')
          ) :
          React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 } },
            images.map(function(img) {
              return React.createElement('div', { key: img.id, style: { position: 'relative', borderRadius: 8, overflow: 'hidden', border: '1px solid ' + C.border, background: C.surface } },
                React.createElement('img', { src: img.image_url, alt: img.alt_text || '', style: { width: '100%', height: 160, objectFit: 'contain', display: 'block', background: '#fff', cursor: 'pointer' },
                  onClick: function() { setLightbox({ images: images.map(function(im) { return im.image_url; }), index: images.indexOf(img) }); },
                  onError: function(e) { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; } }),
                React.createElement('div', { style: { display: 'none', width: '100%', height: 160, alignItems: 'center', justifyContent: 'center', background: C.bg, color: C.textMuted, fontSize: 11 } }, 'Failed to load'),
                React.createElement('div', { style: { padding: '6px 8px' } },
                  React.createElement('span', { style: pillStyle(C.accent) }, 'zentail')
                )
              );
            })
          ),
        // Image count
        images.length > 0 && React.createElement('div', { style: { fontSize: 11, color: C.textDim } }, images.length + ' image' + (images.length !== 1 ? 's' : '')),
        renderCustomFields('images')
      )
    );
  } else if (activeTab === 'amazon') {
    var amazonAttrs = attributes.filter(function(a) { return a.source === 'amazon'; });
    content = React.createElement('div', { style: sectionStyle },
      React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 16 } },
        // Amazon identifiers
        React.createElement('div', { style: { background: C.surface, borderRadius: 8, padding: '16px 20px', border: '1px solid ' + C.border + '44' } },
          React.createElement('div', { style: { fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 } }, 'Amazon Identifiers'),
          React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 } },
            React.createElement('div', null,
              React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginBottom: 2 } }, 'ASIN'),
              React.createElement('div', { style: { fontSize: 14, fontWeight: 500, color: C.text, fontFamily: 'inherit' } }, form.asin ? React.createElement(CopyCell, { value: form.asin }, form.asin) : '\u2014')
            ),
            React.createElement('div', null,
              React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginBottom: 2 } }, 'FNSKU'),
              React.createElement('div', { style: { fontSize: 14, fontWeight: 500, color: C.text, fontFamily: 'inherit' } }, form.fnsku ? React.createElement(CopyCell, { value: form.fnsku }, form.fnsku) : '\u2014')
            )
          )
        ),
        // Amazon attributes
        amazonAttrs.length > 0 && React.createElement('div', { style: { background: C.surface, borderRadius: 8, padding: '16px 20px', border: '1px solid ' + C.border + '44' } },
          React.createElement('div', { style: { fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 } }, 'Amazon Attributes'),
          amazonAttrs.map(function(a) {
            return React.createElement('div', { key: a.attr_key, style: { display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid ' + C.border + '44', fontSize: 13 } },
              React.createElement('span', { style: { color: C.textMuted } }, a.attr_key),
              React.createElement('span', { style: { color: C.text, fontWeight: 500 } }, a.attr_value || '\u2014')
            );
          })
        ),
        amazonAttrs.length === 0 && !form.asin && React.createElement('div', { style: { textAlign: 'center', padding: '40px 20px', color: C.textMuted, fontSize: 13 } },
          React.createElement(LIcon, { name: 'shopping-bag', size: 24 }),
          React.createElement('div', { style: { marginTop: 8 } }, 'No Amazon data. Add an ASIN in the Details tab.')
        )
      )
    );
  } else if (activeTab === 'zentail') {
    var zentailAttrs = attributes.filter(function(a) { return a.source === 'zentail'; });
    content = React.createElement('div', { style: sectionStyle },
      zentailProduct ? React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 16 } },
        // Zentail product info
        React.createElement('div', { style: { background: C.surface, borderRadius: 8, padding: '16px 20px', border: '1px solid ' + C.border + '44' } },
          React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 } },
            React.createElement('div', { style: { fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em' } }, 'Zentail Product'),
            React.createElement(Badge, { color: C.success }, 'Matched')
          ),
          React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 } },
            React.createElement('div', null,
              React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginBottom: 2 } }, 'Zentail SKU'),
              React.createElement('div', { style: { fontSize: 13, fontWeight: 500, color: C.text, fontFamily: 'inherit' } }, React.createElement(CopyCell, { value: zentailProduct.zentail_sku }, zentailProduct.zentail_sku))
            ),
            React.createElement('div', null,
              React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginBottom: 2 } }, 'Title'),
              React.createElement('div', { style: { fontSize: 13, color: C.text } }, zentailProduct.title || '\u2014')
            ),
            React.createElement('div', null,
              React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginBottom: 2 } }, 'Brand'),
              React.createElement('div', { style: { fontSize: 13, color: C.text } }, zentailProduct.brand || '\u2014')
            ),
            React.createElement('div', null,
              React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginBottom: 2 } }, 'Category'),
              React.createElement('div', { style: { fontSize: 13, color: C.text } }, zentailProduct.category || '\u2014')
            ),
            zentailProduct.asin && React.createElement('div', null,
              React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginBottom: 2 } }, 'ASIN'),
              React.createElement('div', { style: { fontSize: 13, fontWeight: 500, color: C.text, fontFamily: 'inherit' } }, React.createElement(CopyCell, { value: zentailProduct.asin }, zentailProduct.asin))
            ),
            zentailProduct.upc && React.createElement('div', null,
              React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginBottom: 2 } }, 'Barcode'),
              React.createElement('div', { style: { fontSize: 13, fontWeight: 500, color: C.text, fontFamily: 'inherit' } }, React.createElement(CopyCell, { value: zentailProduct.upc }, zentailProduct.upc))
            )
          ),
          React.createElement('div', { style: { marginTop: 12, fontSize: 11, color: C.textDim } }, 'Last synced: ' + (zentailProduct.synced_at ? new Date(zentailProduct.synced_at).toLocaleString() : 'Never'))
        ),
        // Zentail attributes with mapping badges
        zentailAttrs.length > 0 && React.createElement('div', { style: { background: C.surface, borderRadius: 8, padding: '16px 20px', border: '1px solid ' + C.border + '44' } },
          React.createElement('div', { style: { fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 } }, 'Zentail Attributes (' + zentailAttrs.length + ')'),
          zentailAttrs.map(function(a) {
            var mapping = editorFieldMappings.find(function(m) { return m.zentail_attr === a.attr_key; });
            return React.createElement('div', { key: a.attr_key, style: { display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, padding: '6px 0', borderBottom: '1px solid ' + C.border + '44', fontSize: 13, alignItems: 'center' } },
              React.createElement('span', { style: { color: C.textMuted, fontFamily: 'inherit', fontSize: 12 } }, a.attr_key),
              React.createElement('span', { style: { color: C.text, fontWeight: 500 } }, a.attr_value || '\u2014'),
              mapping
                ? React.createElement('span', { style: pillStyle(C.success) }, 'mapped \u2192 ' + mapping.product_field)
                : React.createElement('span', { style: pillStyle('#FF9900') }, 'unmapped')
            );
          })
        )
      ) :
      React.createElement('div', { style: { textAlign: 'center', padding: '60px 20px', color: C.textMuted, fontSize: 13 } },
        React.createElement(LIcon, { name: 'link', size: 28 }),
        React.createElement('div', { style: { marginTop: 8, fontSize: 14, fontWeight: 500 } }, 'Not matched to Zentail'),
        React.createElement('div', { style: { marginTop: 4 } }, 'Run a Zentail product sync from Settings > Integrations to match products by SKU.')
      )
    );
  } else if (activeTab === 'analytics') {
    if (!analyticsLoaded && !analyticsLoading && editing) {
      setAnalyticsLoading(true);
      api('/products/' + editing.id + '/analytics?months=120').then(function(r) {
        setAnalyticsData(r);
        setAnalyticsLoading(false);
        setAnalyticsLoaded(true);
      }).catch(function() { setAnalyticsLoading(false); setAnalyticsLoaded(true); });
    }
    // Filter by period then aggregate by granularity
    var filteredTrend = (function() {
      var raw = analyticsData && analyticsData.daily_trend ? analyticsData.daily_trend : [];
      var cutoff = new Date();
      cutoff.setMonth(cutoff.getMonth() - analyticsPeriod);
      var filtered = raw.filter(function(m) { return new Date(m.day) >= cutoff; });
      if (analyticsGranularity === 'daily') return filtered.map(function(m) { return { date: m.day, units_sold: m.units_sold, revenue: m.revenue }; });
      // Group into buckets
      var buckets = {};
      filtered.forEach(function(m) {
        // m.day is the ET business date 'YYYY-MM-DD' (server); parsed as UTC midnight, so every
        // bucket reads UTC fields (local getDay()/getFullYear() slid a day/year back in ET).
        var d = new Date(String(m.day).slice(0, 10) + 'T00:00:00Z');
        var key;
        if (analyticsGranularity === 'weekly') {
          var day = d.getUTCDay();
          var mon = new Date(d); mon.setUTCDate(mon.getUTCDate() - ((day + 6) % 7));
          key = mon.toISOString().slice(0, 10);
        } else if (analyticsGranularity === 'yearly') {
          key = d.getUTCFullYear() + '-01-01';
        } else {
          key = d.toISOString().slice(0, 7) + '-01';
        }
        if (!buckets[key]) buckets[key] = { date: key, units_sold: 0, revenue: 0 };
        buckets[key].units_sold += parseInt(m.units_sold);
        buckets[key].revenue += parseFloat(m.revenue);
      });
      return Object.keys(buckets).sort().map(function(k) { return buckets[k]; });
    })();

    var money = function(v) { return v != null ? '$' + Number(v).toFixed(2) : '—'; };
    var pct = function(v) { return v != null ? Number(v).toFixed(1) + '%' : '—'; };
    var num = function(v) { return v != null ? Number(v).toLocaleString() : '—'; };

    // Chart component using useRef + useEffect
    var AnalyticsChart = function(props) {
      var canvasRef = useRef(null);
      var chartRef = useRef(null);
      useEffect(function() {
        if (!canvasRef.current || !props.config) return;
        if (chartRef.current) chartRef.current.destroy();
        var ctx = canvasRef.current.getContext('2d');
        chartRef.current = new Chart(ctx, props.config);
        return function() { if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; } };
      }, [props.config]);
      return React.createElement('canvas', { ref: canvasRef, style: { width: '100%', maxHeight: props.height || 320 } });
    };

    var chartDefaults = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { labels: { color: C.textMuted, font: { family: 'DM Sans', size: 11 }, boxWidth: 12, padding: 16 } },
        tooltip: {
          backgroundColor: C.surface, titleColor: C.text, bodyColor: C.textMuted,
          borderColor: C.border, borderWidth: 1, padding: 10, cornerRadius: 8,
          titleFont: { family: 'DM Sans', size: 12, weight: 600 },
          bodyFont: { family: 'DM Sans', size: 11 },
          callbacks: {}
        }
      },
      scales: {
        x: { ticks: { color: C.textDim, font: { family: 'DM Sans', size: 10 } }, grid: { color: C.border + '33' }, border: { color: C.border + '66' } },
        y: { ticks: { color: C.textDim, font: { family: 'DM Sans', size: 10 } }, grid: { color: C.border + '33' }, border: { color: C.border + '66' } }
      }
    };

    var buildSalesChart = function(trend) {
      var gran = analyticsGranularity;
      var labels = trend.map(function(m) {
        var d = new Date(m.date);
        // Use UTC — buckets are keyed by UTC day/month; local-TZ formatting would shift midnight UTC to the previous day locally
        if (gran === 'daily') return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
        if (gran === 'weekly') return 'W ' + d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
        if (gran === 'yearly') return d.getUTCFullYear().toString();
        return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' });
      });
      return {
        type: 'bar',
        data: {
          labels: labels,
          datasets: [
            { label: 'Units Sold', data: trend.map(function(m) { return parseInt(m.units_sold); }),
              backgroundColor: C.accent + 'CC', borderColor: C.accent, borderWidth: 1, borderRadius: trend.length > 60 ? 1 : 4, yAxisID: 'y', order: 2 },
            { label: 'Revenue', data: trend.map(function(m) { return parseFloat(m.revenue); }),
              type: 'line', borderColor: C.success, backgroundColor: C.success + '22', pointBackgroundColor: C.success,
              pointRadius: trend.length > 60 ? 0 : 4, pointHoverRadius: 5, borderWidth: 2, fill: true, tension: 0.3, yAxisID: 'y1', order: 1 }
          ]
        },
        options: Object.assign({}, chartDefaults, {
          plugins: Object.assign({}, chartDefaults.plugins, {
            tooltip: Object.assign({}, chartDefaults.plugins.tooltip, {
              callbacks: {
                label: function(ctx) {
                  if (ctx.dataset.label === 'Revenue') return 'Revenue: $' + ctx.parsed.y.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                  return 'Units: ' + ctx.parsed.y.toLocaleString();
                }
              }
            })
          }),
          scales: Object.assign({}, chartDefaults.scales, {
            x: Object.assign({}, chartDefaults.scales.x, { ticks: Object.assign({}, chartDefaults.scales.x.ticks, { maxTicksLimit: 20, maxRotation: 45 }) }),
            y: Object.assign({}, chartDefaults.scales.y, { position: 'left', title: { display: true, text: 'Units', color: C.textDim, font: { family: 'DM Sans', size: 10 } } }),
            y1: { position: 'right', grid: { drawOnChartArea: false }, ticks: { color: C.textDim, font: { family: 'DM Sans', size: 10 }, callback: function(v) { return '$' + v.toLocaleString(); } }, border: { color: C.border + '66' }, title: { display: true, text: 'Revenue', color: C.textDim, font: { family: 'DM Sans', size: 10 } } }
          })
        })
      };
    };

    var buildVelocityChart = function(d) {
      var labels = ['7 Days', '30 Days', '90 Days'];
      return {
        type: 'bar',
        data: {
          labels: labels,
          datasets: [
            { label: 'Daily Velocity', data: [d.velocity.d7, d.velocity.d30, d.velocity.d90],
              backgroundColor: C.warning + 'CC', borderColor: C.warning, borderWidth: 1, borderRadius: 4 }
          ]
        },
        options: Object.assign({}, chartDefaults, {
          plugins: Object.assign({}, chartDefaults.plugins, {
            tooltip: Object.assign({}, chartDefaults.plugins.tooltip, {
              callbacks: {
                label: function(ctx) {
                  return 'Velocity: ' + ctx.parsed.y.toFixed(2) + '/day';
                }
              }
            })
          }),
          scales: Object.assign({}, chartDefaults.scales, {
            y: Object.assign({}, chartDefaults.scales.y, { title: { display: true, text: 'Units/Day', color: C.textDim, font: { family: 'DM Sans', size: 10 } } })
          })
        })
      };
    };

    var buildProfitChart = function(d) {
      if (!d.profitability) return null;
      var p = d.profitability;
      return {
        type: 'doughnut',
        data: {
          labels: ['Profit', 'Unit Cost', 'Referral Fee', 'FBA Fee', 'Closing Fee', 'Storage Fee'],
          datasets: [{
            data: [Math.max(0, p.profit), p.unit_cost, p.referral_fee, p.fba_fee, p.closing_fee, p.storage_fee],
            backgroundColor: [C.success + 'CC', C.accent + 'CC', C.warning + 'CC', C.danger + 'CC', '#A78BFA' + 'CC', C.textDim + 'CC'],
            borderColor: C.surface, borderWidth: 2, hoverOffset: 6
          }]
        },
        options: {
          responsive: true, maintainAspectRatio: false,
          cutout: '55%',
          plugins: {
            legend: { position: 'right', labels: { color: C.textMuted, font: { family: 'DM Sans', size: 11 }, boxWidth: 12, padding: 12 } },
            tooltip: {
              backgroundColor: C.surface, titleColor: C.text, bodyColor: C.textMuted,
              borderColor: C.border, borderWidth: 1, padding: 10, cornerRadius: 8,
              titleFont: { family: 'DM Sans', size: 12, weight: 600 },
              bodyFont: { family: 'DM Sans', size: 11 },
              callbacks: { label: function(ctx) { return ctx.label + ': $' + ctx.parsed.toFixed(2); } }
            }
          }
        }
      };
    };

    var buildInventoryChart = function(d) {
      var hist = d.inventory_history || [];
      var cutoff = new Date();
      cutoff.setMonth(cutoff.getMonth() - analyticsPeriod);
      var filtered = hist.filter(function(h) { return new Date(h.date) >= cutoff; });
      var labels = filtered.map(function(h) { return new Date(h.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: filtered.length > 180 ? undefined : '2-digit' }); });
      return {
        type: 'line',
        data: {
          labels: labels,
          datasets: [{
            label: 'Total Stock',
            data: filtered.map(function(h) { return parseInt(h.total_qty); }),
            borderColor: C.accent, backgroundColor: C.accent + '18',
            pointRadius: filtered.length > 90 ? 0 : 3, pointHoverRadius: 5,
            pointBackgroundColor: C.accent, borderWidth: 2, fill: true, tension: 0.3
          }]
        },
        options: Object.assign({}, chartDefaults, {
          plugins: Object.assign({}, chartDefaults.plugins, { legend: { display: false },
            tooltip: Object.assign({}, chartDefaults.plugins.tooltip, {
              callbacks: { label: function(ctx) { return 'Total Stock: ' + ctx.parsed.y.toLocaleString() + ' units'; } }
            })
          }),
          scales: Object.assign({}, chartDefaults.scales, {
            x: Object.assign({}, chartDefaults.scales.x, { ticks: Object.assign({}, chartDefaults.scales.x.ticks, { maxTicksLimit: 20, maxRotation: 45 }) }),
            y: Object.assign({}, chartDefaults.scales.y, { beginAtZero: true, title: { display: true, text: 'Units', color: C.textDim, font: { family: 'DM Sans', size: 10 } } })
          })
        })
      };
    };

    var miniTable = function(headers, rows) {
      return React.createElement('div', { style: { border: '1px solid ' + C.border, borderRadius: 8, overflow: 'hidden', fontSize: 12 } },
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: headers.map(function(h) { return h.width || '1fr'; }).join(' '), background: C.surface, borderBottom: '1px solid ' + C.border, padding: '6px 10px', fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase' } },
          headers.map(function(h, i) { return React.createElement('div', { key: i, style: { textAlign: h.align || 'left' } }, h.label); })
        ),
        rows.length === 0
          ? React.createElement('div', { style: { padding: '20px 10px', textAlign: 'center', color: C.textDim, fontSize: 12 } }, 'No data')
          : rows.map(function(row, ri) {
              return React.createElement('div', { key: ri, style: { display: 'grid', gridTemplateColumns: headers.map(function(h) { return h.width || '1fr'; }).join(' '), borderBottom: '1px solid ' + C.border + '33', padding: '6px 10px', alignItems: 'center' } },
                row.map(function(cell, ci) { return React.createElement('div', { key: ci, style: { textAlign: headers[ci].align || 'left', color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, cell); })
              );
            })
      );
    };

    var views = [
      { key: 'sales', label: 'Sales Trend', icon: 'trending-up' },
      { key: 'velocity', label: 'Velocity', icon: 'zap' },
      { key: 'profit', label: 'Profitability', icon: 'dollar-sign' },
      { key: 'inventory', label: 'Inventory', icon: 'package' }
    ];

    var kpiStrip = function(d) {
      var yoyUnits = d.yoy.units;
      var curUnits = d.sales.d30.units;
      var yoyChange = yoyUnits > 0 ? ((curUnits - yoyUnits) / yoyUnits * 100) : null;
      var ret = d.returns || {};
      var rr = ret.return_rate_pct;
      var rrColor = rr == null ? C.textMuted : (rr >= 10 ? C.danger : rr >= 5 ? C.warning : C.success);
      var rrTitle = ret.returned_365d != null && ret.sold_365d != null
        ? ret.returned_365d + ' returned \u00f7 ' + ret.sold_365d + ' sold (365d, includes aliases/kits/bundles)'
        : '';
      var kpis = [
        { label: '30d Units', value: num(d.sales.d30.units), color: C.text },
        { label: '30d Revenue', value: money(d.sales.d30.revenue), color: C.success },
        { label: 'Velocity', value: (d.velocity.effective != null ? d.velocity.effective : d.velocity.d30).toFixed(2) + '/day' + (d.velocity.effective_window && d.velocity.effective_window !== '30d' ? ' (' + d.velocity.effective_window + ')' : ''), color: C.accent },
        { label: 'YoY', value: yoyChange != null ? (yoyChange >= 0 ? '+' : '') + yoyChange.toFixed(0) + '%' : '—', color: yoyChange != null ? (yoyChange >= 0 ? C.success : C.danger) : C.textMuted },
        { label: 'On Hand', value: num(d.inventory.on_hand), color: C.text },
        { label: 'Days of Stock', value: d.inventory.days_of_stock != null ? (d.inventory.days_of_stock >= 999 ? '∞' : d.inventory.days_of_stock + 'd') : '—', color: d.inventory.days_of_stock != null ? (d.inventory.days_of_stock >= 999 ? C.textMuted : d.inventory.days_of_stock < 14 ? C.danger : d.inventory.days_of_stock > 90 ? C.warning : C.success) : C.textMuted },
        { label: 'Margin', value: d.profitability ? pct(d.profitability.margin) : '—', color: d.profitability ? (d.profitability.margin > 0 ? C.success : C.danger) : C.textMuted },
        { label: 'ROI', value: d.profitability ? pct(d.profitability.roi) : '—', color: d.profitability ? (d.profitability.roi > 0 ? C.success : C.danger) : C.textMuted },
        { label: 'Return Rate', value: rr != null ? (ret.returned_365d + '/' + ret.sold_365d + ' (' + rr + '%)') : '—', color: rrColor, title: rrTitle }
      ];
      return React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 1, background: C.border + '44', borderRadius: 8, overflow: 'hidden', marginBottom: 16 } },
        kpis.map(function(k, i) {
          return React.createElement('div', { key: i, title: k.title || '', style: { flex: '1 1 100px', background: C.surface, padding: '10px 12px', textAlign: 'center', minWidth: 100 } },
            React.createElement('div', { style: { fontSize: 9, fontWeight: 600, color: C.textDim, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3, whiteSpace: 'nowrap' } }, k.label),
            React.createElement('div', { style: { fontSize: 15, fontWeight: 700, color: k.color, whiteSpace: 'nowrap' } }, k.value)
          );
        })
      );
    };

    var chartConfig = null;
    var chartHeight = 500;
    var sidePanel = null;

    if (analyticsData) {
      if (analyticsView === 'sales') {
        chartConfig = filteredTrend.length > 0 ? buildSalesChart(filteredTrend) : null;
      } else if (analyticsView === 'velocity') {
        chartConfig = buildVelocityChart(analyticsData);
        // Side info for velocity view
        sidePanel = React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 } },
          React.createElement('div', { style: { display: 'flex', gap: 10, flexWrap: 'wrap' } },
            [7, 30, 90].map(function(w) {
              var sd = analyticsData.sales['d' + w];
              var directUnits = sd.units - (sd.contributed_units || 0);
              return React.createElement('div', { key: w, style: { flex: 1, background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: '10px 12px', minWidth: 100, textAlign: 'center' } },
                React.createElement('div', { style: { fontSize: 10, color: C.textDim, textTransform: 'uppercase', marginBottom: 2 } }, w + 'd Sales'),
                React.createElement('div', { style: { fontSize: 16, fontWeight: 700, color: C.text } }, num(sd.units) + ' units'),
                sd.contributed_units > 0 && React.createElement('div', { style: { fontSize: 10, color: C.accent } }, num(directUnits) + ' direct + ' + num(sd.contributed_units) + ' contributed'),
                React.createElement('div', { style: { fontSize: 11, color: C.textMuted } }, money(sd.revenue) + ' (direct)')
              );
            })
          ),
          analyticsData.rollup && React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, background: C.accent + '10', border: '1px solid ' + C.accent + '30', fontSize: 11, color: C.accent, flexWrap: 'wrap' } },
            React.createElement(LIcon, { name: 'git-merge', size: 13 }),
            React.createElement('span', { style: { fontWeight: 600 } }, 'Units include contributions from:'),
            analyticsData.rollup.sources.map(function(s) {
              return React.createElement('span', { key: s.sku, style: { fontFamily: 'inherit', fontSize: 11, background: C.surface, padding: '1px 6px', borderRadius: 4, border: '1px solid ' + C.border } }, s.sku + (s.multiplier > 1 ? ' x' + s.multiplier : '') + ' (' + s.type + ')');
            })
          )
        );
      } else if (analyticsView === 'profit') {
        chartConfig = buildProfitChart(analyticsData);
        chartHeight = 420;
        if (analyticsData.profitability) {
          var p = analyticsData.profitability;
          sidePanel = React.createElement('div', { style: { display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' } },
            [{ l: 'Buy Box', v: money(p.buy_box_price) }, { l: 'Unit Cost', v: money(p.unit_cost) }, { l: 'Total Fees', v: money(p.total_fees) },
             { l: 'Profit/Unit', v: money(p.profit), c: p.profit > 0 ? C.success : C.danger }, { l: 'Margin', v: pct(p.margin), c: p.margin > 0 ? C.success : C.danger }, { l: 'ROI', v: pct(p.roi), c: p.roi > 0 ? C.success : C.danger }
            ].map(function(item, i) {
              return React.createElement('div', { key: i, style: { flex: '1 1 0', background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: '10px 12px', minWidth: 90, textAlign: 'center' } },
                React.createElement('div', { style: { fontSize: 10, color: C.textDim, textTransform: 'uppercase', marginBottom: 2 } }, item.l),
                React.createElement('div', { style: { fontSize: 16, fontWeight: 700, color: item.c || C.text } }, item.v)
              );
            })
          );
        }
      } else if (analyticsView === 'inventory') {
        chartConfig = buildInventoryChart(analyticsData);
        sidePanel = React.createElement('div', { style: { display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' } },
          [
            { l: 'Warehouses', v: analyticsData.inventory.warehouse_count },
            { l: 'Days of Stock', v: analyticsData.inventory.days_of_stock != null ? (analyticsData.inventory.days_of_stock >= 999 ? '∞ (no sales yet)' : analyticsData.inventory.days_of_stock + ' days') : '—', c: analyticsData.inventory.days_of_stock != null ? (analyticsData.inventory.days_of_stock >= 999 ? C.textMuted : analyticsData.inventory.days_of_stock < 14 ? C.danger : analyticsData.inventory.days_of_stock > 90 ? C.warning : C.success) : C.textMuted },
            { l: 'PO Cost Avg', v: analyticsData.po_summary.total_ordered > 0 ? money(analyticsData.po_summary.avg_cost) : '—' },
            { l: 'Total Ordered', v: analyticsData.po_summary.total_ordered > 0 ? num(analyticsData.po_summary.total_ordered) + ' units' : '—' }
          ].map(function(item, i) {
            return React.createElement('div', { key: i, style: { flex: '1 1 0', background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: '10px 12px', minWidth: 90, textAlign: 'center' } },
              React.createElement('div', { style: { fontSize: 10, color: C.textDim, textTransform: 'uppercase', marginBottom: 2 } }, item.l),
              React.createElement('div', { style: { fontSize: 16, fontWeight: 700, color: item.c || C.text } }, item.v)
            );
          })
        );
      }
    }

    content = analyticsLoading
      ? React.createElement('div', { style: { textAlign: 'center', padding: 60, color: C.textMuted } }, React.createElement(LIcon, { name: 'loader-2', size: 20, spin: true }), React.createElement('div', { style: { marginTop: 8 } }, 'Loading analytics...'))
      : !analyticsData
      ? React.createElement('div', { style: { textAlign: 'center', padding: 60, color: C.textMuted } }, 'No analytics data available')
      : React.createElement('div', { style: sectionStyle },
          // ─── KPI Strip ───
          kpiStrip(analyticsData),

          // ─── View Toggle + Date Range ───
          React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 } },
            React.createElement('div', { style: { display: 'flex', gap: 6 } },
              views.map(function(v) {
                var active = analyticsView === v.key;
                return React.createElement('button', {
                  key: v.key,
                  onClick: function() { setAnalyticsView(v.key); },
                  style: {
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '8px 16px', border: '1px solid ' + (active ? C.accent : C.border),
                    borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'DM Sans',
                    background: active ? C.accent + '22' : C.surface, color: active ? C.accent : C.textMuted,
                    transition: 'all 0.15s ease'
                  }
                },
                  React.createElement(LIcon, { name: v.icon, size: 14 }),
                  v.label
                );
              })
            ),
            React.createElement('div', { style: { display: 'flex', gap: 4, alignItems: 'center' } },
              [{ label: 'D', key: 'daily' }, { label: 'W', key: 'weekly' }, { label: 'M', key: 'monthly' }, { label: 'Y', key: 'yearly' }].map(function(g) {
                var active = analyticsGranularity === g.key;
                return React.createElement('button', {
                  key: g.key,
                  onClick: function() { setAnalyticsGranularity(g.key); },
                  style: {
                    padding: '6px 10px', border: '1px solid ' + (active ? C.accent : C.border),
                    borderRadius: 5, cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: 'DM Sans',
                    background: active ? C.accent + '22' : C.surface, color: active ? C.accent : C.textMuted,
                    transition: 'all 0.15s ease', minWidth: 30
                  }
                }, g.label);
              }),
              React.createElement('div', { style: { width: 1, height: 20, background: C.border, margin: '0 4px' } }),
              [{ label: '1M', months: 1 }, { label: '3M', months: 3 }, { label: '6M', months: 6 }, { label: '1Y', months: 12 }, { label: '2Y', months: 24 }, { label: '5Y', months: 60 }, { label: '10Y', months: 120 }].map(function(r) {
                var active = analyticsPeriod === r.months;
                return React.createElement('button', {
                  key: r.months,
                  onClick: function() { setAnalyticsPeriod(r.months); },
                  style: {
                    padding: '6px 10px', border: '1px solid ' + (active ? C.accent : C.border),
                    borderRadius: 5, cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: 'DM Sans',
                    background: active ? C.accent + '22' : C.surface, color: active ? C.accent : C.textMuted,
                    transition: 'all 0.15s ease', minWidth: 36
                  }
                }, r.label);
              })
            )
          ),

          // ─── Main Chart ───
          chartConfig
            ? React.createElement('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 10, padding: 20, position: 'relative', height: chartHeight } },
                React.createElement(AnalyticsChart, { config: chartConfig, height: chartHeight })
              )
            : React.createElement('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 10, padding: '40px 20px', textAlign: 'center', color: C.textMuted, fontSize: 13 } },
                analyticsView === 'sales' ? 'No monthly sales data available yet' :
                analyticsView === 'profit' && !analyticsData.profitability ? (
                  !analyticsData.product.asin ? 'No ASIN — pricing data unavailable' :
                  !analyticsData.pricing ? 'No Keepa/pricing data cached for this ASIN' :
                  !analyticsData.product.unit_cost ? 'No unit cost set — cannot calculate margins' :
                  'Insufficient data for profitability analysis'
                ) : 'No data available'
              ),

          // ─── Side Panel (contextual details below chart) ───
          sidePanel,

        );

  } else if (activeTab === 'advertising') {
    // Per-product ad performance (docs/14) — data from the Advertising module's
    // /api/advertising/product/:id/summary; fetch gated on tab activation.
    if (!advertisingLoaded && !advertisingLoading && editing) {
      setAdvertisingLoading(true);
      api('/advertising/product/' + editing.id + '/summary?days=' + advertisingDays).then(function(r) {
        setAdvertisingData(r); setAdvertisingLoading(false); setAdvertisingLoaded(true);
      }).catch(function(e) { setAdvertisingData({ error: e.message }); setAdvertisingLoading(false); setAdvertisingLoaded(true); });
    }
    var adMoney = function(v, cc) { return v != null ? (cc === 'CAD' ? 'C$' : '$') + Number(v).toFixed(2) : '—'; };
    var adPct = function(v) { return v != null ? Number(v).toFixed(1) + '%' : '—'; };
    var adNum = function(v) { return v != null ? Number(v).toLocaleString() : '—'; };
    var AdTrendChart = function(props2) {
      var canvasRef = useRef(null);
      var chartRef = useRef(null);
      useEffect(function() {
        if (!canvasRef.current || !props2.days || !props2.days.length) return;
        if (chartRef.current) chartRef.current.destroy();
        chartRef.current = new Chart(canvasRef.current.getContext('2d'), {
          type: 'bar',
          data: {
            labels: props2.days.map(function(d) { return d.date.slice(5); }),
            datasets: [
              { type: 'bar', label: 'Spend', data: props2.days.map(function(d) { return d.cost; }), backgroundColor: C.accent + '99', borderRadius: 3 },
              { type: 'line', label: 'Ad Sales', data: props2.days.map(function(d) { return d.sales; }), borderColor: C.success, tension: 0.3, pointRadius: 0, borderWidth: 2 },
            ],
          },
          options: { responsive: true, maintainAspectRatio: false, interaction: { mode: 'index', intersect: false },
            plugins: { legend: { labels: { color: C.textMuted, font: { family: 'DM Sans', size: 11 }, boxWidth: 12 } },
              tooltip: { backgroundColor: C.surface, titleColor: C.text, bodyColor: C.textMuted, borderColor: C.border, borderWidth: 1 } },
            scales: { x: { ticks: { color: C.textDim, font: { size: 10 }, maxTicksLimit: 14 }, grid: { display: false } },
              y: { beginAtZero: true, ticks: { color: C.textDim, font: { size: 10 } }, grid: { color: C.border + '33' } } } },
        });
        return function() { if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; } };
      }, [props2.days]);
      return React.createElement('div', { style: { height: 200 } }, React.createElement('canvas', { ref: canvasRef, style: { width: '100%', maxHeight: 200 } }));
    };
    var advBody;
    if (!advertisingLoaded) {
      advBody = React.createElement('div', { style: { padding: 40, textAlign: 'center' } }, React.createElement(LIcon, { name: 'loader-2', size: 20, spin: true, style: { color: C.textMuted } }));
    } else if (advertisingData && advertisingData.error) {
      advBody = React.createElement('div', { style: { fontSize: 13, color: C.textMuted, padding: 20 } }, 'Advertising data unavailable: ' + advertisingData.error);
    } else {
      var adv = advertisingData || {};
      var mps = adv.by_marketplace || [];
      var advCampaigns = adv.campaigns || [];
      advBody = React.createElement('div', null,
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 } },
          [14, 30, 60, 90].map(function(p) {
            var active = advertisingDays === p;
            return React.createElement('button', { key: p, onClick: function() { setAdvertisingDays(p); setAdvertisingLoaded(false); setAdvertisingData(null); },
              style: { padding: '6px 12px', fontSize: 12, borderRadius: 6, border: '1px solid ' + (active ? C.accent + '40' : C.border),
                background: active ? C.accent + '20' : C.surface, color: active ? C.accent : C.textMuted,
                cursor: 'pointer', fontWeight: 500, fontFamily: 'inherit', transition: 'all 0.15s' } }, p + 'd');
          }),
          React.createElement('div', { style: { flex: 1 } }),
          React.createElement('a', { href: 'https://ads.atharvatoolz.com', target: '_blank', rel: 'noopener', style: { fontSize: 12, color: C.accent, display: 'inline-flex', alignItems: 'center', gap: 5 } },
            React.createElement(LIcon, { name: 'external-link', size: 13 }), 'Open Advertising app')),
        mps.length === 0
          ? React.createElement('div', { style: { fontSize: 13, color: C.textMuted, padding: '10px 0 20px' } },
              'No ad-attributed activity for this SKU in the window' + (advCampaigns.length ? '' : ' — and no campaigns advertise it yet') + '.')
          : React.createElement('div', { style: { display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 } },
              mps.map(function(m) {
                return React.createElement(Card, { key: m.marketplace, style: { padding: '12px 16px', flex: '1 1 260px' } },
                  React.createElement('div', { style: { fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 8 } }, 'Amazon ' + m.marketplace),
                  React.createElement('div', { style: { display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12.5, color: C.textMuted } },
                    React.createElement('span', null, 'Spend ', React.createElement('b', { style: { color: C.text } }, adMoney(m.cost, m.currency))),
                    React.createElement('span', null, 'Sales ', React.createElement('b', { style: { color: C.text } }, adMoney(m.sales, m.currency))),
                    React.createElement('span', null, 'ACoS ', React.createElement('b', { style: { color: C.text } }, adPct(m.acos))),
                    React.createElement('span', null, 'Orders ', React.createElement('b', { style: { color: C.text } }, adNum(m.orders))),
                    React.createElement('span', null, 'Clicks ', React.createElement('b', { style: { color: C.text } }, adNum(m.clicks)))));
              })),
        (adv.days || []).length > 0 && React.createElement(Card, { style: { padding: 16, marginBottom: 14 } },
          React.createElement(AdTrendChart, { days: adv.days })),
        React.createElement('div', { style: { fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 8 } }, 'Campaigns advertising this SKU'),
        advCampaigns.length === 0
          ? React.createElement('div', { style: { fontSize: 12.5, color: C.textMuted } }, 'None synced.')
          : React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 6 } },
              advCampaigns.map(function(cm) {
                return React.createElement('div', { key: cm.id + '-' + cm.marketplace, style: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', border: '1px solid ' + C.border, borderRadius: 8, fontSize: 12.5 } },
                  React.createElement('a', { href: 'https://ads.atharvatoolz.com/' + String(cm.marketplace || 'us').toLowerCase() + '/campaign/' + cm.id, target: '_blank', rel: 'noopener', style: { color: C.accent, fontWeight: 500, flex: 1 } }, cm.name || ('#' + cm.id)),
                  React.createElement('span', { style: { color: C.textMuted } }, cm.marketplace),
                  React.createElement('span', { style: { color: C.textMuted } }, cm.targeting_type || ''),
                  React.createElement('span', { style: { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: cm.state === 'enabled' ? C.success : cm.state === 'paused' ? C.warning : C.textDim } }, cm.state || '?'));
              })));
    }
    content = React.createElement('div', { style: sectionStyle }, advBody);
  } else if (activeTab === 'activity') {
    var ACTIVITY_LIMIT = 50;
    var loadActivity = function(filter, offset, userFilter, startDate, endDate) {
      setActivityLoading(true);
      var url = '/products/' + editing.id + '/activity?limit=' + ACTIVITY_LIMIT + '&offset=' + (offset || 0);
      if (filter) url += '&action=' + encodeURIComponent(filter);
      if (userFilter) url += '&username=' + encodeURIComponent(userFilter);
      if (startDate) url += '&start_date=' + encodeURIComponent(startDate);
      if (endDate) url += '&end_date=' + encodeURIComponent(endDate);
      api(url).then(function(r) {
        setActivityLog(r.rows || []);
        setActivityTotal(r.total || 0);
        if (r.users) setActivityUsers(r.users);
        setActivityLoading(false);
        setActivityLoaded(true);
      }).catch(function() { setActivityLoading(false); setActivityLoaded(true); });
    };
    if (!activityLoaded && !activityLoading) loadActivity(activityFilter, 0, activityUserFilter, activityStartDate, activityEndDate);

    var actionColors = {
      product_created: C.success, created: C.success, product_updated: C.accent, product_deactivated: C.danger, product_reactivated: C.success,
      attribute_updated: '#8b5cf6', image_added: '#06b6d4', image_removed: '#f97316',
      zentail_sync: '#3b82f6', zentail_create: '#3b82f6', zentail_mapped: '#6366f1', zentail_clear: '#64748b',
      added_to_po: '#10b981', received: '#22c55e', added_to_fba: '#f59e0b',
      inventory_update: '#14b8a6', import_update: '#06b6d4', kit_imported: '#8b5cf6',
      fba_received: '#16a34a', fba_undo_receive: '#f97316', removed_from_po: '#ef4444',
      added_to_wfs: '#0ea5e9', wfs_received: '#0284c7', wfs_undo_receive: '#f97316', removed_from_wfs: '#ef4444',
      bundle_component_added: '#a855f7', bundle_component_removed: '#f43f5e'
    };
    var actionLabels = {
      product_created: 'Created', created: 'Created', product_updated: 'Updated', product_deactivated: 'Deactivated', product_reactivated: 'Reactivated',
      attribute_updated: 'Attr Changed', image_added: 'Image Added', image_removed: 'Image Removed',
      zentail_sync: 'Zentail Sync', zentail_create: 'Zentail Created', zentail_mapped: 'Zentail Mapped', zentail_clear: 'Zentail Clear',
      added_to_po: 'Added to PO', received: 'Received', added_to_fba: 'Added to FBA',
      inventory_update: 'Inventory', import_update: 'Import Update', kit_imported: 'Kit Imported',
      fba_received: 'FBA Received', fba_undo_receive: 'FBA Undo', removed_from_po: 'PO Removed',
      added_to_wfs: 'Added to WFS', wfs_received: 'WFS Received', wfs_undo_receive: 'WFS Undo', removed_from_wfs: 'WFS Removed',
      bundle_component_added: 'Bundle Added', bundle_component_removed: 'Bundle Removed'
    };
    var filterOptions = [
      { value: '', label: 'All Activity' },
      { value: 'product_updated', label: 'Product Updated' },
      { value: 'zentail_sync', label: 'Zentail Sync' },
      { value: 'inventory_update', label: 'Inventory Update' },
      { value: 'zentail_mapped', label: 'Zentail Mapped' },
      { value: 'zentail_clear', label: 'Zentail Clear' },
      { value: 'added_to_po', label: 'Added to PO' },
      { value: 'received', label: 'Received' },
      { value: 'added_to_fba', label: 'Added to FBA' },
      { value: 'fba_received', label: 'FBA Received' },
      { value: 'fba_undo_receive', label: 'FBA Undo Receive' },
      { value: 'added_to_wfs', label: 'Added to WFS' },
      { value: 'wfs_received', label: 'WFS Received' },
      { value: 'wfs_undo_receive', label: 'WFS Undo Receive' },
      { value: 'removed_from_wfs', label: 'Removed from WFS' },
      { value: 'bundle_component_added', label: 'Bundle Component Added' },
      { value: 'bundle_component_removed', label: 'Bundle Component Removed' },
      { value: 'kit_imported', label: 'Kit Imported' },
      { value: 'attribute_updated', label: 'Attribute Changed' },
      { value: 'image_added', label: 'Image Added' },
      { value: 'image_removed', label: 'Image Removed' },
      { value: 'zentail_create', label: 'Zentail Created' },
      { value: 'product_created', label: 'Created' },
      { value: 'product_deactivated', label: 'Deactivated' },
      { value: 'product_reactivated', label: 'Reactivated' }
    ];
    var totalPages = Math.ceil(activityTotal / ACTIVITY_LIMIT);
    var currentPage = Math.floor(activityOffset / ACTIVITY_LIMIT) + 1;

    var reloadActivity = function() { loadActivity(activityFilter, 0, activityUserFilter, activityStartDate, activityEndDate); };
    content = React.createElement('div', { style: sectionStyle },
      React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 12 } },
        // Toolbar
        React.createElement('div', { style: { display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' } },
          React.createElement('div', { style: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' } },
            React.createElement('select', {
              value: activityFilter,
              onChange: function(e) { setActivityFilter(e.target.value); setActivityOffset(0); loadActivity(e.target.value, 0, activityUserFilter, activityStartDate, activityEndDate); },
              style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '6px 10px', borderRadius: 6, fontSize: 12, outline: 'none' }
            }, filterOptions.map(function(fo) { return React.createElement('option', { key: fo.value, value: fo.value }, fo.label); })),
            React.createElement('select', {
              value: activityUserFilter,
              onChange: function(e) { setActivityUserFilter(e.target.value); setActivityOffset(0); loadActivity(activityFilter, 0, e.target.value, activityStartDate, activityEndDate); },
              style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '6px 10px', borderRadius: 6, fontSize: 12, outline: 'none' }
            }, React.createElement('option', { value: '' }, 'All Users'),
               activityUsers.map(function(u) {
                 var un = (u && typeof u === 'object') ? u.username : u;
                 var dn = (u && typeof u === 'object' && u.display_name) ? u.display_name : un;
                 return React.createElement('option', { key: un, value: un }, dn);
               })),
            React.createElement('input', {
              type: 'date', value: activityStartDate, placeholder: 'Start date',
              onChange: function(e) { setActivityStartDate(e.target.value); setActivityOffset(0); loadActivity(activityFilter, 0, activityUserFilter, e.target.value, activityEndDate); },
              style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '5px 8px', borderRadius: 6, fontSize: 12, outline: 'none', colorScheme: 'dark', cursor: 'pointer' },
              title: 'Start date'
            }),
            React.createElement('span', { style: { fontSize: 11, color: C.textDim } }, 'to'),
            React.createElement('input', {
              type: 'date', value: activityEndDate, placeholder: 'End date',
              onChange: function(e) { setActivityEndDate(e.target.value); setActivityOffset(0); loadActivity(activityFilter, 0, activityUserFilter, activityStartDate, e.target.value); },
              style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '5px 8px', borderRadius: 6, fontSize: 12, outline: 'none', colorScheme: 'dark', cursor: 'pointer' },
              title: 'End date'
            }),
            (activityUserFilter || activityStartDate || activityEndDate) && React.createElement('button', {
              onClick: function() { setActivityUserFilter(''); setActivityStartDate(''); setActivityEndDate(''); setActivityOffset(0); loadActivity(activityFilter, 0, '', '', ''); },
              style: { background: 'none', border: 'none', color: C.danger, cursor: 'pointer', fontSize: 11, padding: '4px 6px', fontFamily: 'inherit' }
            }, 'Clear'),
            React.createElement('span', { style: { fontSize: 12, color: C.textMuted } }, activityTotal + ' entries')
          ),
          React.createElement('a', {
            href: '/api/products/' + editing.id + '/activity/csv' + (activityFilter ? '?action=' + activityFilter : ''),
            target: '_blank',
            style: { display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: C.accent, textDecoration: 'none', padding: '5px 10px', borderRadius: 6, border: '1px solid ' + C.accent + '44', background: C.accent + '11' }
          }, React.createElement(LIcon, { name: 'download', size: 12 }), ' Export CSV')
        ),
        // Table
        activityLoading ?
          React.createElement('div', { style: { textAlign: 'center', padding: 40, color: C.textMuted } }, 'Loading activity...') :
        activityLog.length === 0 ?
          React.createElement('div', { style: { textAlign: 'center', padding: '40px 20px', color: C.textMuted, fontSize: 13 } },
            React.createElement(LIcon, { name: 'clock', size: 24 }),
            React.createElement('div', { style: { marginTop: 8 } }, 'No activity recorded yet.')
          ) :
        React.createElement('div', { style: { border: '1px solid ' + C.border, borderRadius: 8, overflow: 'hidden' } },
          // Header
          React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '140px 110px 110px 1fr', gap: 0, background: C.surface, borderBottom: '1px solid ' + C.border, padding: '8px 12px', fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' } },
            React.createElement('div', null, 'Date'),
            React.createElement('div', null, 'User'),
            React.createElement('div', null, 'Action'),
            React.createElement('div', null, 'Details')
          ),
          // Rows
          activityLog.map(function(entry) {
            var aColor = actionColors[entry.action] || C.textMuted;
            var aLabel = actionLabels[entry.action] || String(entry.action || '').replace(/_/g, ' ');
            var meta = entry.metadata;
            var detailText = entry.details || '';
            // Show changed fields from metadata
            if (meta && meta.changed && !detailText) {
              detailText = Object.keys(meta.changed).join(', ') + ' changed';
            }
            return React.createElement('div', { key: entry.id, style: { display: 'grid', gridTemplateColumns: '140px 110px 110px 1fr', gap: 0, borderBottom: '1px solid ' + C.border + '44', padding: '7px 12px', alignItems: 'center', fontSize: 12 } },
              React.createElement('div', { style: { color: C.textMuted, fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, entry.created_at ? new Date(entry.created_at).toLocaleString() : ''),
              React.createElement('div', { style: { color: C.text, fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 8 }, title: entry.username || '' }, entry.user_display_name || entry.username || 'system'),
              React.createElement('div', { style: { overflow: 'hidden', paddingRight: 8 } }, React.createElement('span', { style: { fontSize: 10, padding: '2px 7px', borderRadius: 8, background: aColor + '18', color: aColor, fontWeight: 600, display: 'inline-block', maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle' }, title: aLabel }, aLabel)),
              React.createElement('div', { style: { color: C.text, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }, title: detailText }, detailText)
            );
          })
        ),
        // Pagination
        totalPages > 1 && React.createElement('div', { style: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, paddingTop: 8 } },
          React.createElement(Btn, { size: 'sm', disabled: currentPage <= 1, onClick: function() { var off = activityOffset - ACTIVITY_LIMIT; setActivityOffset(off); loadActivity(activityFilter, off, activityUserFilter, activityStartDate, activityEndDate); } }, '\u2190 Prev'),
          React.createElement('span', { style: { fontSize: 12, color: C.textMuted } }, 'Page ' + currentPage + ' of ' + totalPages),
          React.createElement(Btn, { size: 'sm', disabled: currentPage >= totalPages, onClick: function() { var off = activityOffset + ACTIVITY_LIMIT; setActivityOffset(off); loadActivity(activityFilter, off, activityUserFilter, activityStartDate, activityEndDate); } }, 'Next \u2192')
        )
      )
    );
  }

  // Field picker modal
  var tabLabels = { details: 'Product Details', identifiers: 'Identifiers', sourcing: 'Sourcing & Packaging', inventory: 'Inventory', images: 'Images' };
  var fieldPickerModal = React.createElement(Modal, {
    open: !!fieldPickerTab,
    title: 'Manage Custom Fields — ' + (tabLabels[fieldPickerTab] || fieldPickerTab || ''),
    onClose: function() { setFieldPickerTab(null); },
    width: 480
  },
    React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 16 } },
      // Current fields on this tab
      React.createElement('div', null,
        React.createElement('div', { style: { fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 } }, 'Assigned Fields'),
        getTabCustomFields(fieldPickerTab).length === 0 ?
          React.createElement('div', { style: { fontSize: 12, color: C.textMuted, padding: '8px 0' } }, 'No custom fields on this tab yet.') :
          React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4 } },
            getTabCustomFields(fieldPickerTab).map(function(tf) {
              return React.createElement('div', { key: tf.id, style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', background: C.bg, borderRadius: 6, border: '1px solid ' + C.border + '44' } },
                React.createElement('div', null,
                  React.createElement('span', { style: { fontSize: 13, color: C.text, fontWeight: 500 } }, tf.display_label || tf.field_name),
                  React.createElement('span', { style: { fontSize: 11, color: C.textMuted, marginLeft: 8, fontFamily: 'inherit' } }, tf.field_name)
                ),
                React.createElement('button', {
                  onClick: function() { removeTabField(tf.id, tf.field_name); },
                  style: { background: 'none', border: 'none', color: C.textMuted, cursor: 'pointer', padding: '2px 6px', fontSize: 16, borderRadius: 4 },
                  onMouseOver: function(e) { e.currentTarget.style.color = C.danger; },
                  onMouseOut: function(e) { e.currentTarget.style.color = C.textMuted; },
                  title: 'Remove from tab'
                }, '\u00D7')
              );
            })
          )
      ),
      // Add new field
      React.createElement('div', { style: { borderTop: '1px solid ' + C.border, paddingTop: 12 } },
        React.createElement('div', { style: { fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 } }, 'Add Field'),
        availableFields.length === 0 ?
          React.createElement('div', { style: { fontSize: 12, color: C.textMuted } }, 'No available fields to add.') :
          React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 8 } },
            React.createElement(Sel, {
              label: 'Field', value: fieldPickerField,
              options: availableFields.map(function(f) { return { value: f, label: f }; }),
              onChange: function(e) { setFieldPickerField(e.target.value); if (!fieldPickerLabel) setFieldPickerLabel(e.target.value.replace(/_/g, ' ').replace(/\b\w/g, function(c) { return c.toUpperCase(); })); }
            }),
            React.createElement(Inp, {
              label: 'Display Label', value: fieldPickerLabel,
              onChange: function(e) { setFieldPickerLabel(e.target.value); },
              placeholder: 'e.g. Material Type'
            }),
            React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end' } },
              React.createElement(Btn, {
                variant: 'primary', size: 'sm',
                disabled: !fieldPickerField,
                onClick: function() { addTabField(fieldPickerTab); }
              }, React.createElement(LIcon, { name: 'plus', size: 14 }), ' Add to Tab')
            )
          )
      )
    )
  );

  var lightboxEl = lightbox && React.createElement('div', { onClick: function() { setLightbox(null); },
    style: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.92)', zIndex: 10100, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' } },
    React.createElement('button', { onClick: function() { setLightbox(null); },
      style: { position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: '#fff', fontSize: 28, cursor: 'pointer', zIndex: 10101 } }, '\u00d7'),
    lightbox.images.length > 1 && React.createElement('div', { style: { position: 'absolute', top: 20, left: '50%', transform: 'translateX(-50%)', color: '#fff', fontSize: 13, fontWeight: 600, zIndex: 10101 } },
      (lightbox.index + 1) + ' / ' + lightbox.images.length),
    lightbox.images.length > 1 && React.createElement('button', { onClick: function(e) { e.stopPropagation(); setLightbox(function(prev) { return { images: prev.images, index: (prev.index - 1 + prev.images.length) % prev.images.length }; }); },
      style: { position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', fontSize: 28, width: 44, height: 44, borderRadius: '50%', cursor: 'pointer', zIndex: 10101 } }, '\u2039'),
    lightbox.images.length > 1 && React.createElement('button', { onClick: function(e) { e.stopPropagation(); setLightbox(function(prev) { return { images: prev.images, index: (prev.index + 1) % prev.images.length }; }); },
      style: { position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', fontSize: 28, width: 44, height: 44, borderRadius: '50%', cursor: 'pointer', zIndex: 10101 } }, '\u203a'),
    React.createElement('img', { src: lightbox.images[lightbox.index], onClick: function(e) { e.stopPropagation(); },
      style: { maxWidth: 'calc(85vw * var(--invzoom, 1))', maxHeight: 'calc(85vh * var(--invzoom, 1))', objectFit: 'contain', borderRadius: 8 } }),
    lightbox.images.length > 1 && React.createElement('div', { onClick: function(e) { e.stopPropagation(); },
      style: { position: 'absolute', bottom: 20, display: 'flex', gap: 8, justifyContent: 'center' } },
      lightbox.images.map(function(url, i) {
        return React.createElement('img', { key: i, src: url, onClick: function() { setLightbox(function(prev) { return { images: prev.images, index: i }; }); },
          style: { width: 52, height: 52, objectFit: 'cover', borderRadius: 6, border: '2px solid ' + (i === lightbox.index ? '#fff' : 'transparent'), cursor: 'pointer', opacity: i === lightbox.index ? 1 : 0.6, transition: 'all 0.15s' } });
      })
    )
  );

  return React.createElement('div', { style: overlayStyle },
    topBar, tabBar,
    React.createElement('div', { style: contentStyle },
      !editing || fullLoaded ? content
        : loadState === 'error' ? React.createElement('div', { style: { maxWidth: 520, margin: '60px auto', textAlign: 'center', padding: 24, border: '1px solid ' + C.danger + '44', background: C.danger + '12', borderRadius: 10 } },
            React.createElement(LIcon, { name: 'alert-triangle', size: 22, color: C.danger }),
            React.createElement('div', { style: { fontSize: 15, fontWeight: 600, color: C.text, marginTop: 8 } }, 'Could Not Load This Product'),
            React.createElement('div', { style: { fontSize: 13, color: C.textMuted, margin: '6px 0 16px' } }, (loadError || 'The request failed.') + ' Editing is disabled so a save cannot blank fields that did not load.'),
            onRetryLoad && React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: function() { onRetryLoad(); } }, React.createElement(LIcon, { name: 'refresh-cw', size: 13 }), ' Retry'))
        : React.createElement('div', { style: { maxWidth: 520, margin: '60px auto', textAlign: 'center', color: C.textMuted, fontSize: 13 } },
            React.createElement(LIcon, { name: 'loader-2', size: 20, spin: true }),
            React.createElement('div', { style: { marginTop: 8 } }, 'Loading Product...'))),
    fieldPickerModal,
    lightboxEl
  );
}
window.ProductEditorFullScreen = ProductEditorFullScreen;

// ─── DASHBOARD PAGE ───
function DashboardPage({ onNavigate }) {
  // Widget framework: GET /dashboard/widgets returns which widgets this user
  // sees (their saved picks or role defaults — permission-filtered server-side).
  // Sales/inventory payloads are only fetched when a widget needs them; the
  // rest are independent tiles that each fetch /dashboard/widget/:key.
  var [widgetCfg, setWidgetCfg] = useState(null);
  var [salesData, setSalesData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [range, setRange] = useState('30');
  var [customStart, setCustomStart] = useState('');
  var [customEnd, setCustomEnd] = useState('');
  var chartRef = useRef(null);
  var chartInstance = useRef(null);
  var invChartRef = useRef(null);
  var invChartInstance = useRef(null);
  var [invData, setInvData] = useState(null);
  // Load errors are shown, never rendered as an empty dashboard / "Loading..." forever
  var [cfgErr, setCfgErr] = useState(null);
  var [salesErr, setSalesErr] = useState(null);
  // Request sequence: only the LATEST range request may write state (a slow 90d
  // response landing after a later 7d click used to overwrite the 7d numbers)
  var salesSeq = useRef(0);

  var selectedKeys = widgetCfg ? widgetCfg.selected : [];
  var hasW = function(k) { return selectedKeys.indexOf(k) !== -1; };
  var needsSales = hasW('revenue_chart') || hasW('revenue_compare') || hasW('revenue_today');
  var needsInv = hasW('inventory_value');

  // Business days are New York days (the server buckets orders by ET day)
  var etYmd = function(daysAgo) { return new Date(Date.now() - (daysAgo || 0) * 86400000).toLocaleDateString('en-CA', { timeZone: 'America/New_York' }); };

  var loadSales = function(start, end) {
    if (!widgetCfg) return;
    var seq = ++salesSeq.current;
    if (!needsSales && !needsInv) { setLoading(false); return; }
    setLoading(true);
    setSalesErr(null);
    var params = '';
    if (start && end) params = '?start=' + start + '&end=' + end;
    else if (range !== 'custom') {
      var days = parseInt(range) || 30;
      params = '?start=' + etYmd(days) + '&end=' + etYmd(0);
    }
    Promise.all([
      needsSales ? api('/dashboard/sales' + params) : Promise.resolve(null),
      needsInv ? api('/dashboard/inventory' + params) : Promise.resolve(null)
    ]).then(function(r) {
      if (seq !== salesSeq.current) return;
      setSalesData(r[0]); setInvData(r[1]); setLoading(false);
    }).catch(function(e) {
      if (seq !== salesSeq.current) return;
      setSalesErr((e && e.message) || 'Failed to load dashboard data');
      setSalesData(null); setInvData(null); // never leave the previous range's numbers under the new range
      setLoading(false);
    });
  };

  var loadCfg = function() {
    setCfgErr(null);
    api('/dashboard/widgets').then(setWidgetCfg).catch(function(e) { setCfgErr((e && e.message) || 'Failed to load dashboard'); });
  };
  useEffect(function() { loadCfg(); }, []);
  useEffect(function() { if (widgetCfg) loadSales(); }, [widgetCfg]);

  var applyRange = function(val) {
    setRange(val);
    if (val !== 'custom') {
      var days = parseInt(val) || 30;
      loadSales(etYmd(days), etYmd(0));
    }
  };

  var applyCustom = function() { if (customStart && customEnd) loadSales(customStart, customEnd); };

  // Render chart
  useEffect(function() {
    if (!salesData || !salesData.days || !salesData.days.length || typeof Chart === 'undefined') return;
    // Small delay to ensure canvas is mounted
    var timer = setTimeout(function() {
    if (!chartRef.current) return;
    if (chartInstance.current) chartInstance.current.destroy();
    var days = salesData.days || [];
    var labels = days.map(function(d) { return d.date.slice(5); }); // MM-DD
    chartInstance.current = new Chart(chartRef.current, {
      type: 'bar',
      data: {
        labels: labels,
        datasets: [
          { label: 'Amazon US', data: days.map(function(d) { return Math.round(d.amazon_us * 100) / 100; }), backgroundColor: '#3B82F6', stack: 'sales', borderRadius: 2 },
          { label: 'Amazon CA', data: days.map(function(d) { return Math.round(d.amazon_ca * 100) / 100; }), backgroundColor: '#60A5FA', stack: 'sales', borderRadius: 2 },
          { label: 'Walmart', data: days.map(function(d) { return Math.round(d.walmart * 100) / 100; }), backgroundColor: '#0071DC', stack: 'sales', borderRadius: 2 },
          { label: 'eBay', data: days.map(function(d) { return Math.round(d.ebay * 100) / 100; }), backgroundColor: '#E53E3E', stack: 'sales', borderRadius: 2 },
          { label: 'Shopify/DTC', data: days.map(function(d) { return Math.round(d.shopify * 100) / 100; }), backgroundColor: '#38A169', stack: 'sales', borderRadius: 2 },
          { label: 'Mercado Libre', data: days.map(function(d) { return Math.round((d.mercadolibre || 0) * 100) / 100; }), backgroundColor: '#FFE600', stack: 'sales', borderRadius: 2 },
          { label: 'Other', data: days.map(function(d) { return Math.round(d.other * 100) / 100; }), backgroundColor: '#8B5CF6', stack: 'sales', borderRadius: 2 }
        ]
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: {
          legend: { position: 'top', labels: { color: C.textMuted, font: { size: 11, family: 'DM Sans' }, boxWidth: 12, padding: 16 } },
          tooltip: { mode: 'index', intersect: false, callbacks: {
            label: function(ctx) { return ctx.dataset.label + ': $' + ctx.parsed.y.toLocaleString(undefined, { minimumFractionDigits: 2 }); },
            footer: function(items) { var total = items.reduce(function(s, i) { return s + i.parsed.y; }, 0); return 'Total: $' + total.toLocaleString(undefined, { minimumFractionDigits: 2 }); }
          }, footerFont: { weight: 'bold' } }
        },
        scales: {
          x: { stacked: true, grid: { color: C.border + '40' }, ticks: { color: C.textMuted, font: { size: 10 }, maxRotation: 45 } },
          y: { stacked: true, grid: { color: C.border + '40' }, ticks: { color: C.textMuted, font: { size: 10 }, callback: function(v) { return '$' + (v >= 1000 ? (v / 1000).toFixed(0) + 'k' : v); } } }
        },
        interaction: { mode: 'index', intersect: false }
      }
    });
    }, 100);
    return function() { clearTimeout(timer); if (chartInstance.current) chartInstance.current.destroy(); };
  }, [salesData]);

  // Render inventory chart
  useEffect(function() {
    if (!invData || !invData.days || !invData.days.length || typeof Chart === 'undefined') return;
    var timer = setTimeout(function() {
      if (!invChartRef.current) return;
      if (invChartInstance.current) invChartInstance.current.destroy();
      var days = invData.days;
      var labels = days.map(function(d) { return d.date.slice(5); });
      var dateList = days.map(function(d) { return d.date; });

      // Build per-warehouse datasets from warehouse_daily + warehouse_breakdown (live for today)
      var whColors = ['#10B981', '#06B6D4', '#F97316', '#A855F7', '#EF4444', '#84CC16', '#E879F9', '#14B8A6', '#FB923C', '#6366F1'];
      var whDatasets = [];
      var wb = invData.warehouse_breakdown || [];
      var whDaily = invData.warehouse_daily || [];

      // Get unique warehouse IDs (from daily history + current breakdown)
      var whMap = {};
      whDaily.forEach(function(r) { if (!whMap[r.warehouse_id]) whMap[r.warehouse_id] = r.warehouse_id; });
      wb.forEach(function(r) { if (!whMap[r.warehouse_id]) whMap[r.warehouse_id] = r.warehouse_id; });
      var whIds = Object.keys(whMap);

      // Build daily lookup: { date: { warehouse_id: value } }
      var dailyLookup = {};
      whDaily.forEach(function(r) {
        if (!dailyLookup[r.date]) dailyLookup[r.date] = {};
        dailyLookup[r.date][r.warehouse_id] = r.value;
      });
      // Inject today's live breakdown data
      var today = etYmd(0);
      if (!dailyLookup[today]) dailyLookup[today] = {};
      wb.forEach(function(r) { dailyLookup[today][r.warehouse_id] = r.value; });

      if (whIds.length > 0) {
        whIds.forEach(function(whId, i) {
          whDatasets.push({
            label: whMap[whId],
            data: dateList.map(function(d) { return (dailyLookup[d] && dailyLookup[d][whId]) || null; }),
            borderColor: whColors[i % whColors.length],
            backgroundColor: whColors[i % whColors.length] + '15',
            fill: true,
            tension: 0.3, pointRadius: 2, borderWidth: 1.5, yAxisID: 'y',
            spanGaps: false
          });
        });
      } else {
        // Fallback: no warehouse data yet, show aggregate On Hand
        whDatasets.push({ label: 'On Hand', data: days.map(function(d) { return d.on_hand_value; }), borderColor: '#10B981', backgroundColor: '#10B98110', fill: true, tension: 0.3, pointRadius: 2, borderWidth: 1.5, yAxisID: 'y' });
      }

      invChartInstance.current = new Chart(invChartRef.current, {
        type: 'line',
        data: {
          labels: labels,
          datasets: [
            { label: 'Total Value', data: days.map(function(d) { return d.total_value; }), borderColor: '#3B82F6', backgroundColor: '#3B82F615', fill: true, tension: 0.3, pointRadius: 2, borderWidth: 2, yAxisID: 'y' }
          ].concat(whDatasets).concat([
            { label: 'Shipped (In Transit)', data: days.map(function(d) { return d.shipped_value; }), borderColor: '#F59E0B', backgroundColor: 'transparent', borderDash: [4, 4], tension: 0.3, pointRadius: 0, borderWidth: 1.5, yAxisID: 'y' },
            { label: 'Checked In', data: days.map(function(d) { return d.checked_in_value; }), borderColor: '#8B5CF6', backgroundColor: 'transparent', borderDash: [4, 4], tension: 0.3, pointRadius: 0, borderWidth: 1.5, yAxisID: 'y' },
            { label: 'FBA/WFS Inbound', data: days.map(function(d) { return d.inbound_value; }), borderColor: '#EC4899', backgroundColor: 'transparent', borderDash: [4, 4], tension: 0.3, pointRadius: 0, borderWidth: 1.5, yAxisID: 'y' }
          ])
        },
        options: {
          responsive: true, maintainAspectRatio: false,
          plugins: {
            legend: { position: 'top', labels: { color: C.textMuted, font: { size: 11, family: 'DM Sans' }, boxWidth: 12, padding: 16 } },
            tooltip: { mode: 'index', intersect: false, callbacks: {
              label: function(ctx) { if (ctx.parsed.y == null) return null; return ctx.dataset.label + ': $' + ctx.parsed.y.toLocaleString(undefined, { minimumFractionDigits: 2 }); }
            } }
          },
          scales: {
            x: { grid: { color: C.border + '40' }, ticks: { color: C.textMuted, font: { size: 10 }, maxRotation: 45 } },
            y: { grid: { color: C.border + '40' }, ticks: { color: C.textMuted, font: { size: 10 }, callback: function(v) { return '$' + (v >= 1000000 ? (v / 1000000).toFixed(1) + 'M' : v >= 1000 ? (v / 1000).toFixed(0) + 'k' : v); } } }
          },
          interaction: { mode: 'index', intersect: false }
        }
      });
    }, 150);
    return function() { clearTimeout(timer); if (invChartInstance.current) invChartInstance.current.destroy(); };
  }, [invData]);

  var fmtCurrency = function(v) { return '$' + Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }); };
  var summary = salesData ? salesData.summary : {};

  var widgetByKey = {};
  (widgetCfg ? widgetCfg.widgets : []).forEach(function(w) { widgetByKey[w.key] = w; });
  var tileKeys = selectedKeys.filter(function(k) { var w = widgetByKey[k]; return w && w.source === 'widget'; });

  if (!widgetCfg && cfgErr) return React.createElement('div', { style: { padding: 40, color: C.danger, fontSize: 13 } },
    'Could not load the dashboard: ' + cfgErr + ' ',
    React.createElement(Btn, { size: 'sm', onClick: loadCfg }, 'Retry'));
  if (!widgetCfg) return React.createElement('div', { style: { padding: 40, color: C.textMuted } }, 'Loading dashboard…');
  // Sales/inventory payload failed: shown in place of the charts (never an empty chart
  // or "Loading..." forever)
  var salesErrBox = function(h) {
    return React.createElement('div', { style: { height: h, display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center', justifyContent: 'center', color: C.danger, fontSize: 13 } },
      'Could not load: ' + salesErr,
      React.createElement(Btn, { size: 'sm', onClick: function() { range === 'custom' ? applyCustom() : applyRange(range); } }, 'Retry'));
  };

  return React.createElement('div', null,
    React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 } },
      React.createElement('h2', { style: { margin: 0, fontSize: 20, fontWeight: 600, color: C.text } }, 'Dashboard'),
      (needsSales || needsInv) && React.createElement('div', { style: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' } },
        ['1', '7', '14', '30', '60', '90'].map(function(d) {
          return React.createElement('button', { key: d, onClick: function() { applyRange(d); },
            style: { padding: '6px 12px', fontSize: 12, borderRadius: 6, border: '1px solid ' + (range === d ? C.accent + '40' : C.border),
              background: range === d ? C.accent + '20' : C.surface, color: range === d ? C.accent : C.textMuted,
              cursor: 'pointer', fontWeight: 500, fontFamily: 'inherit', transition: 'all 0.15s' } }, d + 'd');
        }),
        React.createElement('button', { onClick: function() { applyRange('custom'); },
          style: { padding: '6px 12px', fontSize: 12, borderRadius: 6, border: '1px solid ' + (range === 'custom' ? C.accent + '40' : C.border),
            background: range === 'custom' ? C.accent + '20' : C.surface, color: range === 'custom' ? C.accent : C.textMuted,
            cursor: 'pointer', fontWeight: 500, fontFamily: 'inherit' } }, 'Custom'),
        range === 'custom' && React.createElement('input', { type: 'date', value: customStart, onChange: function(e) { setCustomStart(e.target.value); },
          style: { padding: '5px 8px', fontSize: 12, borderRadius: 6, border: '1px solid ' + C.border, background: C.bg, color: C.text, fontFamily: 'inherit', colorScheme: 'dark' } }),
        range === 'custom' && React.createElement('input', { type: 'date', value: customEnd, onChange: function(e) { setCustomEnd(e.target.value); },
          style: { padding: '5px 8px', fontSize: 12, borderRadius: 6, border: '1px solid ' + C.border, background: C.bg, color: C.text, fontFamily: 'inherit', colorScheme: 'dark' } }),
        range === 'custom' && React.createElement(Btn, { size: 'sm', variant: 'primary', onClick: applyCustom }, 'Apply')
      )
    ),
    selectedKeys.length === 0 && React.createElement('div', { style: { background: C.surface, border: '1px dashed ' + C.border, borderRadius: 8, padding: 40, textAlign: 'center', color: C.textMuted, fontSize: 13 } },
      'No dashboard widgets selected. Open your Profile (bottom of the side menu) to pick some.'),
    salesErr && !loading && !hasW('revenue_chart') && !hasW('inventory_value') && React.createElement('div', { style: { background: C.surface, border: '1px solid ' + C.danger + '60', borderRadius: 8, marginBottom: 20 } }, salesErrBox(90)),
    // Today's revenue ticker (standalone widget)
    hasW('revenue_today') && salesData && summary.comparisons && summary.comparisons.dod && (function() {
      var dod = summary.comparisons.dod;
      var pct = dod.revenue_pct;
      var color = !pct ? C.textMuted : pct > 0 ? C.success : C.danger;
      return React.createElement('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: 16, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' } },
        React.createElement('div', null,
          React.createElement('div', { style: { fontSize: 11, color: C.textMuted, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 } }, "Today's Revenue"),
          React.createElement('div', { style: { fontSize: 26, fontWeight: 700, color: C.accent, fontFamily: 'inherit' } }, fmtCurrency(dod.revenue_today))
        ),
        React.createElement('div', { style: { fontSize: 13, color: color, fontWeight: 600 } },
          (pct > 0 ? '▲ ' : pct < 0 ? '▼ ' : '') + Math.abs(pct || 0).toFixed(1) + '%'),
        React.createElement('div', { style: { fontSize: 11, color: C.textDim } },
          (dod.orders_today || 0) + ' orders • same time yesterday: ' + fmtCurrency(dod.revenue_yesterday) + ' / ' + (dod.orders_yesterday || 0) + ' orders')
      );
    })(),
    // Summary cards with comparisons
    hasW('revenue_compare') && salesData && React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 20 } },
      [
        { label: 'Total Revenue', value: fmtCurrency(summary.total_revenue), color: C.accent },
        { label: 'Orders', value: (summary.total_orders || 0).toLocaleString(), color: C.text },
        { label: 'Avg Daily', value: fmtCurrency(summary.avg_daily), color: C.success },
      ].map(function(card) {
        return React.createElement('div', { key: card.label, style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: 16 } },
          React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginBottom: 4, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' } }, card.label),
          React.createElement('div', { style: { fontSize: 20, fontWeight: 700, color: card.color, fontFamily: 'inherit' } }, card.value)
        );
      })
    ),
    // Comparison stats: D/D (unless the standalone ticker covers it), M/M, Y/Y
    hasW('revenue_compare') && salesData && summary.comparisons && React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 20 } },
      (function() {
        var comp = summary.comparisons;
        var skipDod = hasW('revenue_today');
        var fmtCompRange = function(p) {
          if (!p || !p.start) return 'prev period';
          var f = function(s) { var d = new Date(s + 'T00:00:00'); return (d.getMonth() + 1) + '/' + d.getDate() + '/' + String(d.getFullYear()).slice(2); };
          return f(p.start) + '–' + f(p.end);
        };
        var pctBadge = function(pct) {
          if (pct === 0 && !pct) return null;
          var positive = pct > 0;
          var color = pct === 0 ? C.textMuted : positive ? C.success : C.danger;
          var bg = pct === 0 ? 'transparent' : positive ? C.successSoft : C.dangerSoft;
          var arrow = pct === 0 ? '' : positive ? '\u25B2 ' : '\u25BC ';
          return React.createElement('span', { style: { fontSize: 11, fontWeight: 600, color: color, background: bg, padding: '2px 6px', borderRadius: 4 } }, arrow + Math.abs(pct).toFixed(1) + '%');
        };
        var sections = [];
        // D/D
        if (!skipDod) if (comp.dod) {
          sections.push(React.createElement('div', { key: 'dod', style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: 16 } },
            React.createElement('div', { style: { fontSize: 11, color: C.textMuted, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 } }, 'Day / Day'),
            React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 } },
              React.createElement('span', { style: { fontSize: 12, color: C.textMuted } }, 'Revenue'),
              React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 6 } },
                React.createElement('span', { style: { fontSize: 13, fontWeight: 600, color: C.text, fontFamily: 'inherit' } }, fmtCurrency(comp.dod.revenue_today)),
                pctBadge(comp.dod.revenue_pct)
              )
            ),
            React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' } },
              React.createElement('span', { style: { fontSize: 12, color: C.textMuted } }, 'Orders'),
              React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 6 } },
                React.createElement('span', { style: { fontSize: 13, fontWeight: 600, color: C.text, fontFamily: 'inherit' } }, (comp.dod.orders_today || 0).toLocaleString()),
                pctBadge(comp.dod.orders_pct)
              )
            ),
            React.createElement('div', { style: { fontSize: 10, color: C.textDim, marginTop: 6 } },
              (comp.dod.live ? 'today so far vs same time yesterday: ' : 'vs prior day: ')
              + fmtCurrency(comp.dod.revenue_yesterday) + ' / ' + (comp.dod.orders_yesterday || 0) + ' orders')
          ));
        } else {
          sections.push(React.createElement('div', { key: 'dod', style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textDim, fontSize: 12 } }, 'D/D: Not Enough Data'));
        }
        // M/M (previous period)
        sections.push(React.createElement('div', { key: 'mom', style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: 16 } },
          React.createElement('div', { style: { fontSize: 11, color: C.textMuted, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 } }, 'Period / Period'),
          React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 } },
            React.createElement('span', { style: { fontSize: 12, color: C.textMuted } }, 'Revenue'),
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 6 } },
              React.createElement('span', { style: { fontSize: 13, fontWeight: 600, color: C.text, fontFamily: 'inherit' } }, fmtCurrency(summary.total_revenue)),
              pctBadge(comp.prev_period.revenue_pct)
            )
          ),
          React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' } },
            React.createElement('span', { style: { fontSize: 12, color: C.textMuted } }, 'Orders'),
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 6 } },
              React.createElement('span', { style: { fontSize: 13, fontWeight: 600, color: C.text, fontFamily: 'inherit' } }, (summary.total_orders || 0).toLocaleString()),
              pctBadge(comp.prev_period.orders_pct)
            )
          ),
          React.createElement('div', { style: { fontSize: 10, color: C.textDim, marginTop: 6 } },
            'vs ' + fmtCompRange(comp.prev_period) + (comp.prev_period.to_date ? ' (to same point)' : '') + ': '
            + fmtCurrency(comp.prev_period.revenue) + ' / ' + (comp.prev_period.orders || 0).toLocaleString() + ' orders')
        ));
        // Y/Y
        sections.push(React.createElement('div', { key: 'yoy', style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: 16 } },
          React.createElement('div', { style: { fontSize: 11, color: C.textMuted, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 } }, 'Year / Year'),
          React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 } },
            React.createElement('span', { style: { fontSize: 12, color: C.textMuted } }, 'Revenue'),
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 6 } },
              React.createElement('span', { style: { fontSize: 13, fontWeight: 600, color: C.text, fontFamily: 'inherit' } }, fmtCurrency(summary.total_revenue)),
              pctBadge(comp.yoy.revenue_pct)
            )
          ),
          React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' } },
            React.createElement('span', { style: { fontSize: 12, color: C.textMuted } }, 'Orders'),
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 6 } },
              React.createElement('span', { style: { fontSize: 13, fontWeight: 600, color: C.text, fontFamily: 'inherit' } }, (summary.total_orders || 0).toLocaleString()),
              pctBadge(comp.yoy.orders_pct)
            )
          ),
          React.createElement('div', { style: { fontSize: 10, color: C.textDim, marginTop: 6 } },
            'vs ' + fmtCompRange(comp.yoy) + (comp.yoy.to_date ? ' (to same date & time)' : '') + ': '
            + fmtCurrency(comp.yoy.revenue) + ' / ' + (comp.yoy.orders || 0).toLocaleString() + ' orders')
        ));
        return sections;
      })()
    ),
    // Independent tile widgets (each fetches its own /dashboard/widget/:key)
    tileKeys.length > 0 && React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12, marginBottom: 20 } },
      tileKeys.map(function(k) {
        return React.createElement(DashWidget, { key: k, def: widgetByKey[k], onNavigate: onNavigate });
      })
    ),
    // Chart
    hasW('revenue_chart') && React.createElement('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: 20 } },
      React.createElement('div', { style: { fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 12 } }, 'Daily Sales by Channel'),
      loading ? React.createElement('div', { style: { height: 350, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textMuted } }, 'Loading...') :
      salesErr ? salesErrBox(350) :
        React.createElement('div', { style: { height: 350 } },
          React.createElement('canvas', { ref: chartRef })
        )
    ),
    // Inventory Value Chart
    hasW('inventory_value') && React.createElement('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: 20, marginTop: 16 } },
      React.createElement('div', { style: { fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 12 } }, 'Inventory Value & Units'),
      salesErr && !loading ? salesErrBox(300) :
      !invData || loading ? React.createElement('div', { style: { height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textMuted } }, 'Loading...') :
        React.createElement('div', { style: { height: 300 } },
          React.createElement('canvas', { ref: invChartRef })
        )
    )
  );
}

// ─── Dashboard widget tile — independent fetch + per-key renderer ─────────
var dwMoney = function(v) { return '$' + Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 }); };
var dwMoney2 = function(v) { return '$' + Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }); };
var dwRow = function(key, left, right, opts) {
  opts = opts || {};
  return React.createElement('div', { key: key, style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 12, cursor: opts.onClick ? 'pointer' : 'default' }, onClick: opts.onClick },
    React.createElement('span', { style: { color: opts.onClick ? C.accent : C.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 } }, left),
    React.createElement('span', { style: { color: C.text, fontWeight: 600, fontFamily: 'inherit', flexShrink: 0 } }, right)
  );
};
var dwBig = function(value, label, color) {
  return React.createElement('div', { style: { marginBottom: 8 } },
    React.createElement('div', { style: { fontSize: 24, fontWeight: 700, color: color || C.text, fontFamily: 'inherit' } }, value),
    label && React.createElement('div', { style: { fontSize: 11, color: C.textMuted } }, label)
  );
};

var DASH_WIDGET_RENDERERS = {
  profit_month: function(d) {
    return React.createElement('div', null,
      React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 } },
        dwBig(dwMoney(d.revenue), 'Revenue', C.accent),
        dwBig(dwMoney(d.cogs), 'COGS'),
        dwBig(dwMoney(d.gross_profit), 'Gross Profit', C.success),
        dwBig(d.margin_pct != null ? d.margin_pct.toFixed(1) + '%' : '—', 'Margin')
      )
    );
  },
  top_sellers: function(d) {
    return React.createElement('div', null, (d.items || []).map(function(x, i) {
      return dwRow(i, (i + 1) + '. ' + x.sku, dwMoney(x.revenue) + ' · ' + x.units + 'u');
    }));
  },
  bills_due: function(d) {
    return React.createElement('div', null,
      dwBig(dwMoney2(d.total), d.count + ' bill' + (d.count === 1 ? '' : 's') + (d.overdue ? ' · ' + d.overdue + ' overdue' : ''), d.overdue ? C.danger : C.warning),
      (d.items || []).map(function(x, i) { return dwRow(i, (x.vendor || '?') + ' · due ' + String(x.due_date).slice(0, 10), dwMoney2(x.amount)); })
    );
  },
  po_pipeline: function(d) {
    return React.createElement('div', null, (d.pipeline || []).map(function(x) {
      return dwRow(x.status, x.status + ' (' + x.count + ')', dwMoney(x.total));
    }));
  },
  unbilled_pos: function(d, onNavigate) {
    return React.createElement('div', null,
      dwBig(dwMoney(d.total), d.count + ' PO' + (d.count === 1 ? '' : 's') + ' without a bill (90d)', C.warning),
      (d.items || []).map(function(x, i) {
        return dwRow(i, x.po_number + ' · ' + (x.vendor || '?'), dwMoney(x.total),
          { onClick: function() { onNavigate('po-detail', String(x.po_id), { from: 'dashboard' }); } });
      })
    );
  },
  fba_recon: function(d) {
    var dl = d.next_deadline ? new Date(d.next_deadline) : null;
    var daysLeft = dl ? Math.ceil((dl - new Date()) / 86400000) : null;
    return React.createElement('div', null,
      dwBig(dwMoney2(d.open_exposure), d.open_items + ' claimable item' + (d.open_items === 1 ? '' : 's'), C.danger),
      dl && dwRow('dl', 'Nearest Filing Deadline', dl.toLocaleDateString('en-CA', { timeZone: 'America/New_York' }) + ' (' + daysLeft + 'd)'),
      dwRow('if', 'Still in Flight (Watching)', dwMoney(d.in_flight_exposure))
    );
  },
  stockout_lost: function(d) {
    return React.createElement('div', null, (d.items || []).map(function(x, i) {
      return dwRow(i, x.sku + ' · ' + x.days_oos + 'd OOS', dwMoney(x.lost_profit));
    }));
  },
  cs_open: function(d) {
    var age = d.oldest ? Math.round((Date.now() - new Date(d.oldest).getTime()) / 3600000) : null;
    return React.createElement('div', null,
      dwBig(String(d.total), 'open ticket' + (d.total === 1 ? '' : 's'), d.total > 0 ? C.warning : C.success),
      (d.by_priority || []).map(function(x) { return dwRow(x.priority, x.priority, String(x.count)); }),
      age != null && dwRow('old', 'Oldest Waiting', age >= 48 ? Math.round(age / 24) + 'd' : age + 'h')
    );
  },
  api_health: function(d) {
    return React.createElement('div', null,
      dwBig(String(d.total_errors), 'errors in ' + Number(d.total_calls || 0).toLocaleString() + ' calls (24h)', d.total_errors > 0 ? C.danger : C.success),
      (d.channels || []).map(function(x) { return dwRow(x.channel, x.channel, x.errors + ' / ' + x.calls); })
    );
  },
  inbound_transit: function(d) {
    var line = function(k, label, x) { return dwRow(k, label + ' (' + x.shipments + ')', x.units.toLocaleString() + 'u · ' + dwMoney(x.value)); };
    return React.createElement('div', null,
      line('fba', 'FBA', d.fba), line('wfs', 'WFS', d.wfs), line('meli', 'MELI', d.meli)
    );
  },
  returns_week: function(d) {
    var delta = d.last_week > 0 ? (d.this_week - d.last_week) / d.last_week * 100 : null;
    return React.createElement('div', null,
      dwBig(String(d.this_week), 'units returned (7d)', delta != null && delta > 20 ? C.danger : C.text),
      dwRow('lw', 'Prior 7 Days', String(d.last_week)),
      delta != null && dwRow('d', 'Change', (delta > 0 ? '▲ ' : delta < 0 ? '▼ ' : '') + Math.abs(delta).toFixed(0) + '%')
    );
  },
  receiving_today: function(d) {
    return React.createElement('div', null,
      dwBig(d.total.toLocaleString(), 'units received today', C.success),
      (d.by_user || []).map(function(x) { return dwRow(x.user, x.user, x.units.toLocaleString()); })
    );
  },
  pos_awaiting: function(d, onNavigate) {
    return React.createElement('div', null,
      dwBig(String(d.count), 'POs checked-in / receiving'),
      (d.items || []).map(function(x, i) {
        var days = x.checked_in_date ? Math.floor((Date.now() - new Date(x.checked_in_date).getTime()) / 86400000) : null;
        return dwRow(i, x.po_number + ' · ' + (x.vendor || '?'), days != null ? days + 'd' : x.status,
          { onClick: function() { onNavigate('po-detail', String(x.po_id), { from: 'dashboard' }); } });
      })
    );
  },
  fba_inprogress: function(d) {
    return React.createElement('div', null, dwBig(String(d.shipments), 'shipments being packed'), dwRow('u', 'Units Packed So Far', d.units.toLocaleString()));
  },
  wfs_inprogress: function(d) {
    return React.createElement('div', null, dwBig(String(d.shipments), 'plans in progress'), dwRow('u', 'Units Packed So Far', d.units.toLocaleString()));
  },
  meli_inprogress: function(d) {
    return React.createElement('div', null, dwBig(String(d.shipments), 'shipments in draft/packed/filed'), dwRow('u', 'Units', d.units.toLocaleString()));
  },
  missing_week: function(d) {
    return React.createElement('div', null,
      dwBig(String(d.total), 'units marked missing (7d)', d.total > 0 ? C.warning : C.success),
      (d.by_user || []).map(function(x) { return dwRow(x.user, x.user, String(x.units)); })
    );
  },
  my_activity: function(d) {
    return React.createElement('div', null,
      dwBig(d.today.toLocaleString(), 'units received today', C.accent),
      dwRow('w', 'Last 7 Days', d.week.toLocaleString()),
      dwRow('p', 'POs Touched Today', String(d.pos_today))
    );
  },
};

function DashWidget({ def, onNavigate }) {
  var [data, setData] = useState(null);
  var [err, setErr] = useState(null);
  useEffect(function() {
    var alive = true;
    setData(null); setErr(null);
    api('/dashboard/widget/' + def.key)
      .then(function(d) { if (alive) setData(d); })
      .catch(function(e) { if (alive) setErr(e.message || 'Failed to load'); });
    return function() { alive = false; };
  }, [def.key]);

  var render = DASH_WIDGET_RENDERERS[def.key];
  return React.createElement('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: 16, minHeight: 110 } },
    React.createElement('div', { style: { fontSize: 11, color: C.textMuted, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 } }, def.label),
    err ? React.createElement('div', { style: { fontSize: 12, color: C.danger } }, err)
      : !data ? React.createElement('div', { style: { fontSize: 12, color: C.textDim } }, 'Loading…')
      : render ? render(data, onNavigate)
      : React.createElement('div', { style: { fontSize: 12, color: C.textDim } }, 'No renderer')
  );
}

// ─── PROFILE PAGE — user info + dashboard widget picker ───────────────────
// ── My Profile → Two-factor authentication (2026-09-05) ──
// Email codes work whenever an email is on file; text-message codes need a phone verified
// here (or entered by an admin). Adding or removing a phone asks for the password again.
// The phone is only bound to the account after a correct code, which Google texts to the
// NEW number via Firebase (server-side; the browser just supplies a reCAPTCHA token).
function TwoFactorCard({ showToast }) {
  var [info, setInfo] = useState(null);
  var [mode, setMode] = useState(null); // null | 'add' | 'remove'
  var [pw, setPw] = useState('');
  var [phone, setPhone] = useState('');
  var [code, setCode] = useState('');
  var [enrollToken, setEnrollToken] = useState(null);
  var [pendingHint, setPendingHint] = useState('');
  var [busy, setBusy] = useState(false);
  var [err, setErr] = useState('');
  var [loadErr, setLoadErr] = useState(null); // shown instead of "Loading…" forever (9-27 sweep L17)
  var load = useCallback(function() {
    setLoadErr(null);
    api('/auth/me/two-factor').then(setInfo).catch(function(e) { setLoadErr((e && e.message) || 'Failed to load'); });
  }, []);
  useEffect(function() { load(); }, [load]);
  var reset = function() { setMode(null); setPw(''); setPhone(''); setCode(''); setEnrollToken(null); setPendingHint(''); setErr(''); setBusy(false); };
  var sendCode = async function() {
    if (busy) return; // Enter / double click while a request is in flight (9-26)
    if (!pw) { setErr('Enter your password'); return; }
    if (!phone.trim()) { setErr('Enter a phone number'); return; }
    setBusy(true); setErr('');
    try {
      var token = await window.getRecaptchaToken(info && info.recaptcha_site_key);
      var r = await api('/auth/me/phone/start', { method: 'POST', body: JSON.stringify({ password: pw, phone: phone, recaptcha_token: token }) });
      setEnrollToken(r.enroll_token); setPendingHint(r.phone_hint || '');
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };
  var confirmChoice = async function() {
    if (busy) return;
    if (code.length < 6) { setErr('Enter the 6-digit code'); return; }
    setBusy(true); setErr('');
    try {
      await api('/auth/me/phone/confirm', { method: 'POST', body: JSON.stringify({ enroll_token: enrollToken, code: code }) });
      showToast('Phone verified. You can now get sign-in codes by text.', 'success');
      reset(); load();
    } catch (e) { setErr(e.message); setBusy(false); }
  };
  var remove = async function() {
    if (busy) return;
    if (!pw) { setErr('Enter your password'); return; }
    setBusy(true); setErr('');
    try {
      await api('/auth/me/phone', { method: 'DELETE', body: JSON.stringify({ password: pw }) });
      showToast('Phone removed. Sign-in codes go to your email.', 'success');
      reset(); load();
    } catch (e) { setErr(e.message); setBusy(false); }
  };
  var setDefault = function(v) {
    api('/auth/me/two-factor', { method: 'PUT', body: JSON.stringify({ default_channel: v || null }) })
      .then(function() {
        showToast(v === 'sms' ? 'Sign-in codes will be texted by default' : v === 'email' ? 'Sign-in codes will be emailed by default' : 'You will be asked at your next sign-in', 'success');
        load();
      })
      .catch(function(e) { showToast(e.message, 'error'); });
  };
  var inputStyle = { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 10px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' };
  var field = function(label, props) {
    return React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 160 } },
      React.createElement('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' } }, label),
      React.createElement('input', Object.assign({ style: inputStyle }, props)));
  };
  var pill = function(text, color) {
    return React.createElement('span', { style: { fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 99, background: color + '22', color: color, textTransform: 'uppercase', letterSpacing: '0.04em' } }, text);
  };
  var row = function(label, value, extra) {
    return React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 } },
      React.createElement('span', { style: { color: C.textMuted, minWidth: 100 } }, label),
      React.createElement('span', { style: { color: C.text, fontWeight: 500 } }, value), extra || null);
  };
  var panelStyle = { marginTop: 12, padding: 12, background: C.bg, borderRadius: 8, border: '1px solid ' + C.border, display: 'flex', flexDirection: 'column', gap: 10 };
  var errEl = err ? React.createElement('div', { style: { color: C.danger, fontSize: 12 } }, err) : null;
  var body;
  if (!info && loadErr) {
    body = React.createElement('div', { style: { color: C.danger, fontSize: 12, display: 'flex', alignItems: 'center', gap: 10 } },
      'Could not load your sign-in settings: ' + loadErr,
      React.createElement(Btn, { size: 'sm', onClick: load }, 'Retry'));
  } else if (!info) {
    body = React.createElement('div', { style: { color: C.textMuted, fontSize: 12 } }, 'Loading…');
  } else {
    var form = null;
    if (mode === 'add') {
      form = React.createElement('div', { style: panelStyle },
        !enrollToken
          ? React.createElement(React.Fragment, null,
              React.createElement('div', { style: { display: 'flex', gap: 10, flexWrap: 'wrap' } },
                field('Password', { type: 'password', value: pw, autoComplete: 'current-password', onChange: function(e) { setPw(e.target.value); } }),
                field('Phone Number', { type: 'tel', value: phone, autoComplete: 'tel', placeholder: '(555) 123-4567 or +52 55 1234 5678', onChange: function(e) { setPhone(e.target.value); }, onKeyDown: function(e) { if (e.key === 'Enter') sendCode(); } })),
              React.createElement('div', { style: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' } },
                React.createElement(Btn, { size: 'sm', variant: 'primary', onClick: sendCode, disabled: busy }, busy ? 'Sending…' : 'Text Me a Code'),
                React.createElement(Btn, { size: 'sm', onClick: reset, disabled: busy }, 'Cancel'),
                React.createElement('span', { style: { fontSize: 10.5, color: C.textDim, marginLeft: 'auto' } }, window.RECAPTCHA_NOTICE)))
          : React.createElement(React.Fragment, null,
              React.createElement('div', { style: { fontSize: 13, color: C.text } }, 'We texted a code to ', React.createElement('strong', null, pendingHint), '. Enter it to finish.'),
              React.createElement('div', { style: { display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' } },
                field('Verification Code', { type: 'text', value: code, inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: 6,
                  onChange: function(e) { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); },
                  onKeyDown: function(e) { if (e.key === 'Enter') confirmChoice(); },
                  style: Object.assign({}, inputStyle, { letterSpacing: 6, fontWeight: 700, fontSize: 18, maxWidth: 180 }) }),
                React.createElement(Btn, { size: 'sm', variant: 'primary', onClick: confirmChoice, disabled: busy }, busy ? 'Verifying…' : 'Verify'),
                React.createElement(Btn, { size: 'sm', onClick: function() { setEnrollToken(null); setCode(''); setErr(''); }, disabled: busy }, 'Use a Different Number'))),
        errEl);
    } else if (mode === 'remove') {
      form = React.createElement('div', { style: panelStyle },
        React.createElement('div', { style: { fontSize: 13, color: C.text } }, 'Remove ', React.createElement('strong', null, info.phone_hint), '? Sign-in codes will go to your email only.'),
        React.createElement('div', { style: { display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' } },
          field('Password', { type: 'password', value: pw, autoComplete: 'current-password', onChange: function(e) { setPw(e.target.value); }, onKeyDown: function(e) { if (e.key === 'Enter') remove(); } }),
          React.createElement(Btn, { size: 'sm', variant: 'primary', onClick: remove, disabled: busy }, busy ? 'Removing…' : 'Remove Phone'),
          React.createElement(Btn, { size: 'sm', onClick: reset, disabled: busy }, 'Cancel')),
        errEl);
    }
    body = React.createElement(React.Fragment, null,
      React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 6 } },
        row('Email Codes', info.email_hint || 'No email on file', info.email_hint ? pill('On', C.success) : pill('Off', C.textDim)),
        row('Text Codes', info.phone_hint || 'No phone on file', (info.phone_hint && info.phone_verified) ? pill('On', C.success) : pill('Off', C.textDim)),
        row('Default', React.createElement(Sel, {
          value: info.default_channel || '',
          onChange: function(e) { setDefault(e.target.value); },
          options: [{ value: '', label: 'Ask Me at Next Sign-In' }, { value: 'email', label: 'Email' }]
            .concat(info.phone_hint && info.phone_verified ? [{ value: 'sms', label: 'Text Message' + (info.sms_available ? '' : ' (Not Set Up Yet)') }] : [])
        })),
        !info.sms_available ? React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 2 } },
          'Text-message codes are not enabled yet. An admin can turn them on under Settings → Integrations → Firebase.') : null),
      info.sms_available && !mode ? React.createElement('div', { style: { display: 'flex', gap: 8, marginTop: 12 } },
        React.createElement(Btn, { size: 'sm', variant: 'primary', onClick: function() { reset(); setMode('add'); } }, info.phone_hint ? 'Change Phone' : 'Add Phone'),
        info.phone_hint ? React.createElement(Btn, { size: 'sm', onClick: function() { reset(); setMode('remove'); } }, 'Remove Phone') : null) : null,
      form);
  }
  return React.createElement('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: 16, marginBottom: 16 } },
    React.createElement('div', { style: { fontSize: 14, fontWeight: 600, color: C.text } }, 'Two-Factor Authentication'),
    React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 2, marginBottom: 12 } },
      'Sign-in codes go to your default channel. The first time you sign in with a phone on file you are asked which one; change it here any time.'),
    body);
}

// ── My Profile → Change Password (9-27 sweep L17) ──
// PUT /auth/change-password answers 400 on a wrong current password (never 401, which would read
// as "signed out"), and signs out every OTHER session + forgets trusted devices on success.
function ChangePasswordCard({ showToast }) {
  var [cur, setCur] = useState('');
  var [next, setNext] = useState('');
  var [again, setAgain] = useState('');
  var [busy, setBusy] = useState(false);
  var [err, setErr] = useState('');
  var submit = async function() {
    if (busy) return;
    if (!cur) { setErr('Enter your current password'); return; }
    if (next.length < 8) { setErr('The new password must be at least 8 characters'); return; }
    if (next !== again) { setErr('The new passwords do not match'); return; }
    if (next === cur) { setErr('The new password is the same as the current one'); return; }
    setBusy(true); setErr('');
    try {
      var r = await api('/auth/change-password', { method: 'PUT', body: JSON.stringify({ current_password: cur, new_password: next }) });
      var others = r && r.other_sessions_signed_out ? r.other_sessions_signed_out : 0;
      showToast('Password changed' + (others ? '. ' + others + ' other session' + (others === 1 ? ' was' : 's were') + ' signed out.' : '.'), 'success');
      setCur(''); setNext(''); setAgain('');
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };
  var inputStyle = { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 10px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' };
  var field = function(label, value, set, autoComplete) {
    return React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 160 } },
      React.createElement('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' } }, label),
      React.createElement('input', { type: 'password', value: value, autoComplete: autoComplete, style: inputStyle,
        onChange: function(e) { set(e.target.value); setErr(''); },
        onKeyDown: function(e) { if (e.key === 'Enter') submit(); } }));
  };
  return React.createElement('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: 16, marginBottom: 16 } },
    React.createElement('div', { style: { fontSize: 14, fontWeight: 600, color: C.text } }, 'Change Password'),
    React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 2, marginBottom: 12 } },
      'At least 8 characters. Your other sessions are signed out and trusted devices will ask for a sign-in code again.'),
    React.createElement('div', { style: { display: 'flex', gap: 10, flexWrap: 'wrap' } },
      field('Current Password', cur, setCur, 'current-password'),
      field('New Password', next, setNext, 'new-password'),
      field('Confirm New Password', again, setAgain, 'new-password')),
    err ? React.createElement('div', { style: { color: C.danger, fontSize: 12, marginTop: 8 } }, err) : null,
    React.createElement('div', { style: { display: 'flex', gap: 8, marginTop: 12 } },
      React.createElement(Btn, { size: 'sm', variant: 'primary', onClick: submit, disabled: busy || !cur || !next || !again }, busy ? 'Saving…' : 'Change Password')));
}

function ProfilePage({ showToast, currentUser }) {
  var [cfg, setCfg] = useState(null);
  var [sel, setSel] = useState([]);
  var [saving, setSaving] = useState(false);
  var [lang, setLang] = useState('en');
  var [widgetsErr, setWidgetsErr] = useState(null); // widget picker load error (shown, not "Loading…" forever)

  var load = useCallback(function() {
    setWidgetsErr(null);
    api('/dashboard/widgets').then(function(c) { setCfg(c); setSel(c.selected || []); })
      .catch(function(e) { setWidgetsErr((e && e.message) || 'Failed to load widgets'); });
    api('/auth/preferences').then(function(p) { setLang((p && p.language) || 'en'); })
      .catch(function(e) { console.error('[silent-catch]', e); });
  }, []);
  useEffect(function() { load(); }, [load]);

  var saveLang = function(v) {
    var prev = lang;
    setLang(v);
    api('/auth/preferences', { method: 'PUT', body: JSON.stringify({ language: v }) })
      .then(function() {
        if (window.I18N) window.I18N.setLanguage(v, { block: true });
        showToast(v === 'en' ? 'Language set to English' : 'Idioma cambiado a Español', 'success');
      })
      .catch(function(e) { setLang(prev); showToast(e.message, 'error'); });
  };

  var toggle = function(k) {
    setSel(sel.indexOf(k) !== -1 ? sel.filter(function(x) { return x !== k; }) : sel.concat([k]));
  };
  var save = function() {
    setSaving(true);
    api('/auth/me/dashboard-widgets', { method: 'PUT', body: JSON.stringify({ widgets: sel }) })
      .then(function(r) { setSel(r.selected); showToast('Dashboard updated', 'success'); load(); })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setSaving(false); });
  };
  var reset = function() {
    setSaving(true);
    api('/auth/me/dashboard-widgets', { method: 'PUT', body: JSON.stringify({ reset: true }) })
      .then(function(r) { setSel(r.selected); showToast('Reset to your role defaults', 'success'); load(); })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setSaving(false); });
  };

  var u = currentUser || {};
  return React.createElement('div', { style: { maxWidth: 720 } },
    React.createElement('h2', { style: { margin: '0 0 16px', fontSize: 20, fontWeight: 600, color: C.text } }, 'My Profile'),
    React.createElement('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: 16, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 14 } },
      React.createElement('div', { style: { width: 44, height: 44, borderRadius: 22, background: C.accentSoft, color: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, fontWeight: 700 } },
        ((u.display_name || u.username || '?').charAt(0) || '?').toUpperCase()),
      React.createElement('div', null,
        React.createElement('div', { style: { fontSize: 15, fontWeight: 600, color: C.text } }, u.display_name || u.username),
        React.createElement('div', { style: { fontSize: 12, color: C.textMuted } }, (u.username || '') + ' · Role: ' + (u.role || '?'))
      )
    ),
    React.createElement('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: 16, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14, flexWrap: 'wrap' }, 'data-no-i18n': true },
      React.createElement('div', null,
        React.createElement('div', { style: { fontSize: 14, fontWeight: 600, color: C.text } }, 'Language / Idioma'),
        React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 2 } },
          'Interface language for your account. Translations are machine-generated. / Idioma de la interfaz para tu cuenta. Las traducciones son automáticas.')
      ),
      React.createElement(Sel, {
        options: [{ value: 'en', label: 'English' }, { value: 'es', label: 'Español (Spanish)' }],
        value: lang,
        onChange: function(e) { if (e.target.value) saveLang(e.target.value); }
      })
    ),
    React.createElement(TwoFactorCard, { showToast: showToast }),
    React.createElement(ChangePasswordCard, { showToast: showToast }),
    React.createElement('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: 16 } },
      React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, gap: 10, flexWrap: 'wrap' } },
        React.createElement('div', null,
          React.createElement('div', { style: { fontSize: 14, fontWeight: 600, color: C.text } }, 'My Dashboard Widgets'),
          React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 2 } },
            cfg && cfg.customized ? 'Customized — your picks override the role defaults.' : 'Currently showing your role\'s default set.')
        ),
        React.createElement('div', { style: { display: 'flex', gap: 8 } },
          // Disabled until the picker has loaded: Save with nothing loaded would store an EMPTY selection
          React.createElement(Btn, { size: 'sm', onClick: reset, disabled: saving || !cfg }, 'Reset to Defaults'),
          React.createElement(Btn, { size: 'sm', variant: 'primary', onClick: save, disabled: saving || !cfg }, saving ? 'Saving…' : 'Save')
        )
      ),
      !cfg && widgetsErr ? React.createElement('div', { style: { padding: 20, color: C.danger, fontSize: 13, display: 'flex', alignItems: 'center', gap: 10 } },
          'Could not load widgets: ' + widgetsErr,
          React.createElement(Btn, { size: 'sm', onClick: load }, 'Retry'))
      : !cfg ? React.createElement('div', { style: { padding: 20, color: C.textMuted, fontSize: 13 } }, 'Loading…')
        : React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4, marginTop: 10 } },
            cfg.widgets.map(function(w) {
              var on = sel.indexOf(w.key) !== -1;
              return React.createElement('label', { key: w.key, style: { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 10px', borderRadius: 8, cursor: 'pointer', background: on ? C.accentSoft : 'transparent', border: '1px solid ' + (on ? C.accent + '40' : 'transparent') } },
                React.createElement('input', { type: 'checkbox', checked: on, onChange: function() { toggle(w.key); }, style: { cursor: 'pointer', marginTop: 2, accentColor: C.accent, width: 14, height: 14 } }),
                React.createElement('div', { style: { flex: 1 } },
                  React.createElement('div', { style: { fontSize: 13, fontWeight: 600, color: C.text } }, w.label,
                    w.chart && React.createElement('span', { style: { fontSize: 10, color: C.textDim, fontWeight: 400 } }, '  (chart)')),
                  React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 1 } }, w.description)
                ),
                React.createElement('span', { style: { fontSize: 10, color: C.textDim, padding: '2px 8px', background: C.bg, borderRadius: 4, whiteSpace: 'nowrap' } }, w.area)
              );
            })
          ),
      React.createElement('div', { style: { fontSize: 11, color: C.textDim, marginTop: 12 } },
        'Only widgets your permissions allow are listed — grants are managed by an admin in Settings → Roles.')
    )
  );
}
window.ProfilePage = ProfilePage;

// ─── VENDORS PAGE ───
// ─── Vendor analytics helpers ───
// Themed Chart.js canvas wrapper (rebuilds on config change, destroys on unmount).
function VendorSpendChart(props) {
  var canvasRef = useRef(null);
  var chartRef = useRef(null);
  useEffect(function() {
    if (!canvasRef.current || !props.config) return;
    if (chartRef.current) chartRef.current.destroy();
    chartRef.current = new Chart(canvasRef.current.getContext('2d'), props.config);
    return function() { if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; } };
  }, [props.config]);
  return React.createElement('div', { style: { position: 'relative', height: props.height || 300 } },
    React.createElement('canvas', { ref: canvasRef })
  );
}

// Roll all-time monthly spend rows ([{month:'YYYY-MM', spend, units, po_count}]) into the chosen
// granularity, keeping only the last `rangeMonths` months (0 = all). Returns [{key,label,spend,units,po_count}].
function aggregateSpend(monthly, gran, rangeMonths) {
  var rows = (monthly || []).slice();
  if (rangeMonths > 0 && rows.length) {
    var c = new Date(); c.setMonth(c.getMonth() - rangeMonths + 1);
    var cutoff = c.getFullYear() + '-' + String(c.getMonth() + 1).padStart(2, '0');
    rows = rows.filter(function(m) { return m.month >= cutoff; });
  }
  if (gran === 'monthly') {
    return rows.map(function(m) {
      var p = m.month.split('-');
      return { key: m.month, label: p[1] + '/' + p[0].slice(2), spend: parseFloat(m.spend) || 0, units: parseInt(m.units) || 0, po_count: parseInt(m.po_count) || 0 };
    });
  }
  var buckets = {};
  rows.forEach(function(m) {
    var y = m.month.slice(0, 4), mo = parseInt(m.month.slice(5, 7)), key, label;
    if (gran === 'yearly') { key = y; label = y; }
    else { var q = Math.ceil(mo / 3); key = y + '-Q' + q; label = 'Q' + q + " '" + y.slice(2); }
    if (!buckets[key]) buckets[key] = { key: key, label: label, spend: 0, units: 0, po_count: 0 };
    buckets[key].spend += parseFloat(m.spend) || 0;
    buckets[key].units += parseInt(m.units) || 0;
    buckets[key].po_count += parseInt(m.po_count) || 0;
  });
  return Object.keys(buckets).sort().map(function(k) { return buckets[k]; });
}

// Shift a 'YYYY-MM' month string by `delta` months (negative = earlier).
function shiftMonth(ym, delta) {
  var y = parseInt(ym.slice(0, 4), 10), m = parseInt(ym.slice(5, 7), 10) + delta;
  while (m <= 0) { m += 12; y -= 1; }
  while (m > 12) { m -= 12; y += 1; }
  return y + '-' + String(m).padStart(2, '0');
}

// Build a single-sheet .xlsx from header labels + array-of-arrays rows and trigger a download.
function downloadXlsxReport(filename, sheetName, headers, rows) {
  var ws = XLSX.utils.aoa_to_sheet([headers].concat(rows));
  var wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, (sheetName || 'Report').slice(0, 31));
  XLSX.writeFile(wb, filename);
}

function VendorsPage({ showToast }) {
  const [vendors, setVendors] = useState([]);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [vendorTab, setVendorTab] = useState('info');
  const [vendorAnalytics, setVendorAnalytics] = useState(null);
  const [vendorActivity, setVendorActivity] = useState([]);
  const [vSort, setVSort] = useState({ top: { key: 'total_spend', dir: 'desc' }, open: { key: 'po_date', dir: 'desc' }, recent: { key: 'po_date', dir: 'desc' }, activity: { key: 'created_at', dir: 'desc' } });
  const [aSub, setASub] = useState('spend');          // active analytics sub-tab: spend | top | open | recent
  const [spendGran, setSpendGran] = useState('monthly'); // monthly | quarterly | yearly
  const [spendRange, setSpendRange] = useState(12);   // months to show, 0 = all
  const fileRef = useRef(null);

  const [ltStats, setLtStats] = useState(null); // { [vendor_id]: { total_pos, fbaus?, fbaca?, fbm?, wfs? } } from /vendors/lead-time-stats
  const load = useCallback(async () => { setVendors(await api('/vendors?active=true')); }, []);
  useEffect(() => { load(); api('/vendors/lead-time-stats').then(setLtStats).catch(function(e) { console.error('[silent-catch]', e); }); }, [load]);

  const filtered = vendors.filter(v => !search || v.vendor_name?.toLowerCase().includes(search.toLowerCase()) || v.email?.toLowerCase().includes(search.toLowerCase()));
  // Lead-time stats merged flat onto each row so DataTable sorting/filtering work on plain keys
  const tableRows = filtered.map(function(v) {
    var s = (ltStats && ltStats[v.id]) || {};
    var flat = { total_pos: s.total_pos || 0 };
    ['fbaus', 'fbaca', 'fbm', 'wfs'].forEach(function(g) {
      var st = s[g] || {};
      flat['lt_' + g + '_actual'] = st.actual != null ? st.actual : null;
      flat['lt_' + g + '_est'] = st.estimated != null ? st.estimated : null;
      flat['lt_' + g + '_n'] = st.n || 0;
    });
    return Object.assign({}, v, flat);
  });
  const openNew = () => { setForm({}); setEditing(null); setVendorTab('info'); setVendorAnalytics(null); setVendorActivity([]); setModalOpen(true); };
  const openEdit = (v, initialTab) => {
    setForm(v); setEditing(v); setVendorTab(initialTab || 'info'); setVendorAnalytics(null); setVendorActivity([]);
    setModalOpen(true);
    if (v.id) {
      api('/vendors/' + v.id + '/analytics').then(setVendorAnalytics).catch(function(e) { console.error('[silent-catch]', e); });
      api('/vendors/' + v.id + '/activity').then(setVendorActivity).catch(function(e) { console.error('[silent-catch]', e); });
    }
  };
  // Deep link: /settings/vendors?vendor=<id>[&vtab=info|analytics|activity] opens that vendor (new-tab targets).
  useEffect(function() {
    try {
      var sp = new URLSearchParams(window.location.search);
      var qvid = sp.get('vendor');
      if (qvid) {
        var qtab = sp.get('vtab');
        var qasub = sp.get('asub');
        if (qasub) setASub(qasub);
        api('/vendors/' + qvid).then(function(v) { openEdit(v, qtab || 'info'); }).catch(function(e) { console.error('[silent-catch]', e); });
        history.replaceState(history.state, '', '/settings/vendors');
      }
    } catch (e) {}
  }, []);

  const save = async () => {
    try {
      if (editing) { await api('/vendors/' + editing.id, { method: 'PUT', body: JSON.stringify(form) }); showToast('Vendor updated'); }
      else { await api('/vendors', { method: 'POST', body: JSON.stringify(form) }); showToast('Vendor created'); }
      setModalOpen(false); load();
    } catch (e) { showToast(e.message, 'error'); }
  };

  const remove = async (id) => {
    if (!(await appConfirm('Deactivate this vendor?', { danger: true, okLabel: 'Deactivate' }))) return;
    await api('/vendors/' + id, { method: 'DELETE' }); showToast('Vendor deactivated'); load();
  };

  const handleImport = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    try { const r = await apiUpload('/import/vendors', file); showToast('Imported ' + r.imported + ' vendors' + (r.skipped ? ' (' + r.skipped + ' skipped' + (r.errors && r.errors.length ? ': ' + r.errors.slice(0, 3).map(function(x) { return x.error || x; }).join('; ') : '') + ')' : ''), r.skipped ? 'warning' : undefined); load(); }
    catch (e) { showToast(e.message, 'error'); }
    e.target.value = '';
  };

  const F = (key) => ({ value: form[key] || '', onChange: e => setForm({ ...form, [key]: e.target.value }) });

  const fields = [
    ['vendor_name', 'Vendor Name'], ['contact_name', 'Contact'], ['address1', 'Address 1'], ['address2', 'Address 2'],
    ['city', 'City'], ['state', 'State'], ['zip', 'ZIP'], ['country', 'Country'],
    ['email', 'Email'], ['phone', 'Phone'],
    ['shipping_method', 'Shipping Method'], ['payment_method', 'Payment Method'], ['po_minimum', 'PO Minimum', 'number'],
  ];

  // Actual lead-time cell: avg days (sample size) — green when <= the estimate, amber when
  // over, red when 50%+ over. Estimate = product-derived expected lead time for those POs
  // (products.purchase_order_lead_time_days, fallback Settings default) — same as on-time calc.
  var ltActual = function(v, g) {
    var a = v['lt_' + g + '_actual'];
    if (a == null) return React.createElement('span', { style: { color: C.textDim } }, '—');
    var est = v['lt_' + g + '_est'];
    var color = est != null ? (a <= est ? '#22C55E' : a >= est * 1.5 ? '#EF4444' : '#FBBF24') : C.text;
    return React.createElement('span', { style: { whiteSpace: 'nowrap' } },
      React.createElement('span', { style: { fontWeight: 600, color: color } }, a + 'd'),
      React.createElement('span', { style: { color: C.textDim, fontSize: 10 } }, ' (' + v['lt_' + g + '_n'] + ')')
    );
  };
  var ltEst = function(v, g) {
    var e = v['lt_' + g + '_est'];
    return e != null ? e + 'd' : React.createElement('span', { style: { color: C.textDim } }, '—');
  };

  var vendorCols = [
    { key: 'vendor_name', label: 'Name', style: { fontWeight: 500, color: C.text } },
    { key: 'contact_name', label: 'Contact' },
    { key: 'address1', label: 'Address 1' },
    { key: 'address2', label: 'Address 2', defaultVisible: false },
    { key: 'city_state', label: 'City/State', render: function(v) { return [v.city, v.state].filter(Boolean).join(', '); } },
    { key: 'zip', label: 'ZIP', defaultVisible: false },
    { key: 'country', label: 'Country', defaultVisible: false },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'shipping_method', label: 'Shipping', defaultVisible: false },
    { key: 'payment_method', label: 'Payment', defaultVisible: false },
    { key: 'po_minimum', label: 'PO Min', render: function(v) { return '$' + Number(v.po_minimum || 0).toLocaleString(); } },
    { key: 'lt_fbaus_actual', label: 'FBAUS Actual', render: function(v) { return ltActual(v, 'fbaus'); } },
    { key: 'lt_fbaus_est', label: 'FBAUS Est.', render: function(v) { return ltEst(v, 'fbaus'); } },
    { key: 'lt_fbaca_actual', label: 'FBACA Actual', render: function(v) { return ltActual(v, 'fbaca'); } },
    { key: 'lt_fbaca_est', label: 'FBACA Est.', render: function(v) { return ltEst(v, 'fbaca'); } },
    { key: 'lt_fbm_actual', label: 'FBM Actual', render: function(v) { return ltActual(v, 'fbm'); } },
    { key: 'lt_fbm_est', label: 'FBM Est.', render: function(v) { return ltEst(v, 'fbm'); } },
    { key: 'lt_wfs_actual', label: 'WFS Actual', defaultVisible: false, render: function(v) { return ltActual(v, 'wfs'); } },
    { key: 'lt_wfs_est', label: 'WFS Est.', defaultVisible: false, render: function(v) { return ltEst(v, 'wfs'); } },
    { key: 'total_pos', label: '# POs' },
    { key: 'notes', label: 'Notes', defaultVisible: false },
  ];

  var vendorCSVHeaders = ['vendor_name','contact_name','address1','address2','city','state','zip','country','email','phone','shipping_method','payment_method','po_minimum','notes'];

  var exportVendors = function() {
    downloadCSV('vendors_export.csv', arrayToCSV(vendorCSVHeaders, vendors));
  };

  return React.createElement('div', null,
    React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 } },
      React.createElement('div', null,
        React.createElement('h2', { style: { margin: 0, fontSize: 20, fontWeight: 600, color: C.text } }, 'Vendors'),
        React.createElement('p', { style: { margin: '4px 0 0', fontSize: 13, color: C.textMuted } }, vendors.length + ' active vendors')
      ),
      React.createElement('div', { style: { display: 'flex', gap: 8, flexWrap: 'wrap' } },
        React.createElement(Btn, { size: 'sm', onClick: function() { downloadTemplate('vendors_template.csv', vendorCSVHeaders); } }, React.createElement(LIcon, { name: 'file-text', size: 14 }), ' Template'),
        React.createElement('input', { type: 'file', accept: '.csv', ref: fileRef, onChange: handleImport, style: { display: 'none' } }),
        React.createElement(Btn, { size: 'sm', onClick: () => fileRef.current.click() }, React.createElement(LIcon, { name: 'upload', size: 14 }), ' Import CSV'),
        React.createElement(Btn, { size: 'sm', onClick: exportVendors, disabled: vendors.length === 0 }, React.createElement(LIcon, { name: 'download', size: 14 }), ' Export CSV'),
        React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: openNew }, '+ Add Vendor')
      )
    ),
    React.createElement('div', { style: { marginBottom: 16 } }, React.createElement(SearchInput, { placeholder: 'Search vendors...', value: search, onChange: e => setSearch(e.target.value),
      style: { width: '100%', background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '10px 14px', borderRadius: 8, fontSize: 13, outline: 'none' } })),
    React.createElement(DataTable, {
      columns: vendorCols, data: tableRows, storageKey: 'vendors',
      defaultSort: 'vendor_name', defaultSortDir: 'asc',
      rowHref: function(v) { return '/settings/vendors?vendor=' + v.id; },
      onRowClick: function(v) { openEdit(v); },
      onAction: function(action, v) { if (action === 'delete') remove(v.id); },
      emptyTitle: 'No vendors yet', emptySub: 'Add vendors or import CSV'
    }),
    modalOpen && React.createElement('div', { style: { position: 'fixed', top: 0, left: (function() { if (isNarrowViewport()) return 0; try { var sc = localStorage.getItem('sidebar_collapsed'); return (sc === null ? false : sc !== 'true') ? SIDENAV.NAV_W : SIDENAV.RAIL_W; } catch(e) { return SIDENAV.NAV_W; } })(), right: 0, bottom: 0, zIndex: 1000, background: C.bg, display: 'flex', flexDirection: 'column', overflow: 'hidden' } },
      // ─── Top Bar ───
      React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 28px', borderBottom: '1px solid ' + C.border, background: C.surface, flexShrink: 0 } },
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 16 } },
          React.createElement('div', { style: { width: 48, height: 48, borderRadius: 10, background: C.accent + '18', display: 'flex', alignItems: 'center', justifyContent: 'center' } },
            React.createElement(LIcon, { name: 'truck', size: 24, color: C.accent })
          ),
          React.createElement('div', null,
            React.createElement('div', { style: { fontSize: 16, fontWeight: 600, color: C.text } }, editing ? (form.vendor_name || 'Edit Vendor') : 'New Vendor'),
            editing && React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 2 } },
              form.contact_name ? form.contact_name : '', form.email ? (form.contact_name ? ' \u00B7 ' : '') + form.email : ''
            )
          )
        ),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
          React.createElement(Btn, { variant: 'primary', onClick: save }, React.createElement(LIcon, { name: 'check', size: 14 }), editing ? ' Update' : ' Create'),
          React.createElement('button', { onClick: function() { setModalOpen(false); },
            onMouseOver: function(e) { e.currentTarget.style.background = C.border + '44'; e.currentTarget.style.color = C.text; },
            onMouseOut: function(e) { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = C.textMuted; },
            style: { background: 'none', border: 'none', cursor: 'pointer', padding: 8, color: C.textMuted, borderRadius: 6, display: 'flex', alignItems: 'center', transition: 'all 0.15s' } },
            React.createElement(LIcon, { name: 'x', size: 20 })
          )
        )
      ),
      // ─── Tab Bar ───
      React.createElement('div', { style: { display: 'flex', gap: 0, borderBottom: '1px solid ' + C.border, background: C.surface, paddingLeft: 24, flexShrink: 0 } },
        (editing ? [{ key: 'info', label: 'Vendor Information', icon: 'user' }, { key: 'analytics', label: 'Vendor Analytics', icon: 'bar-chart-2' }, { key: 'activity', label: 'Activity Log', icon: 'activity' }] : [{ key: 'info', label: 'Vendor Information', icon: 'user' }]).map(function(t) {
          var active = vendorTab === t.key;
          var tabHref = (editing && editing.id) ? ('/settings/vendors?vendor=' + editing.id + '&vtab=' + t.key) : undefined;
          return React.createElement('a', { key: t.key, href: tabHref,
            onClick: function(e) { if (tabHref && (e.ctrlKey || e.metaKey || e.shiftKey)) return; e.preventDefault(); setVendorTab(t.key); },
            onMouseOver: function(e) { if (!active) { e.currentTarget.style.color = C.text; e.currentTarget.style.background = C.border + '44'; } },
            onMouseOut: function(e) { if (!active) { e.currentTarget.style.color = C.textMuted; e.currentTarget.style.background = 'none'; } },
            style: { padding: '10px 18px', border: 'none', borderBottom: active ? '2px solid ' + C.accent : '2px solid transparent', background: 'none', color: active ? C.accent : C.textMuted, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6, textDecoration: 'none', transition: 'all 0.15s', borderRadius: '6px 6px 0 0' }
          }, React.createElement(LIcon, { name: t.icon, size: 14 }), t.label);
        })
      ),
      // ─── Content Area ───
      React.createElement('div', { style: { flex: 1, overflowY: 'auto', padding: '24px 32px' } },
        // ─── Vendor Information Tab ───
        vendorTab === 'info' && React.createElement('div', { style: { maxWidth: 700, margin: '0 auto' } },
          React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 } },
            // Country is a picker, not free text. Three ship-from builders (app-fba.js,
            // app-wfs.js and routes/fba-shipments.js) already do country.substring(0, 2) to
            // get an ISO code for Amazon/Walmart, so a vendor typed as "China" was being sent
            // as CH — Switzerland. The picker makes that assumption true.
            ...fields.map(function(f) {
              if (f[0] === 'state') return React.createElement(StateSelect, { key: 'state', label: 'State', country: form.country || 'US', ...F('state') });
              var key = f[0], label = f[1], type = f[2];
              if (key !== 'country') return React.createElement(Inp, { key: key, label: label, type: type || 'text', ...F(key) });
              return React.createElement('div', { key: key, style: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 } },
                React.createElement('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' } }, label),
                React.createElement(SearchSelect, {
                  value: form.country || '',
                  onChange: function(val) { setForm(Object.assign({}, form, { country: val })); },
                  options: [{ value: '', label: 'Not set' }].concat(countryOptionList(form.country || '')),
                  placeholder: 'Not set'
                })
              );
            }),
            React.createElement('div', { style: { gridColumn: 'span 2' } }, React.createElement(Inp, { label: 'Notes', ...F('notes') }))
          ),
          editing && React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12, marginTop: 20, padding: '12px 16px', background: C.surface, borderRadius: 8, border: '1px solid ' + C.border } },
            React.createElement('label', { style: { fontSize: 13, color: C.text, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' } },
              React.createElement('input', { type: 'checkbox', checked: form.is_active !== false, onChange: function(e) { setForm(Object.assign({}, form, { is_active: e.target.checked })); } }),
              'Vendor Active'
            ),
            React.createElement('span', { style: { fontSize: 11, color: C.textDim } }, 'Inactive vendors are hidden from dropdowns and lists')
          ),
          editing && React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12, marginTop: 10, padding: '12px 16px', background: C.surface, borderRadius: 8, border: '1px solid ' + C.border } },
            React.createElement('label', { style: { fontSize: 13, color: C.text, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' } },
              React.createElement('input', { type: 'checkbox', checked: !!form.accounting_active, onChange: function(e) { setForm(Object.assign({}, form, { accounting_active: e.target.checked })); } }),
              'Pin to Accounting AP list (Admin only)'
            ),
            React.createElement('span', { style: { fontSize: 11, color: C.textDim } }, 'Pinned vendors auto-populate in every new cash cycle')
          ),
          editing && React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12, marginTop: 10, padding: '12px 16px', background: C.surface, borderRadius: 8, border: '1px solid ' + C.border } },
            React.createElement('label', { style: { fontSize: 13, color: C.text, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' } },
              React.createElement('input', { type: 'checkbox', checked: form.transparency_default === true || form.transparency_default === 'true', onChange: function(e) { setForm(Object.assign({}, form, { transparency_default: e.target.checked })); } }),
              'Amazon Transparency brand'
            ),
            React.createElement('span', { style: { fontSize: 11, color: C.textDim } }, 'Enabling flags ALL this vendor’s products as requiring per-unit Transparency labels (one-way backfill)')
          )
        ),
        // ─── Vendor Analytics Tab ───
        vendorTab === 'analytics' && (function() {
          if (!vendorAnalytics) return React.createElement('div', { style: { padding: '40px 0', textAlign: 'center', color: C.textDim, fontSize: 13 } }, 'Loading analytics...');
          var a = vendorAnalytics;
          var statCard = function(label, value, sub, accent) {
            return React.createElement(Card, { style: { padding: '14px 8px', textAlign: 'center', overflow: 'hidden' } },
              React.createElement('div', { style: { fontSize: 17, fontWeight: 700, color: accent || C.text, whiteSpace: 'nowrap' } }, value),
              React.createElement('div', { style: { fontSize: 10, color: C.textMuted, marginTop: 4, textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.03em' } }, label),
              sub ? React.createElement('div', { style: { fontSize: 10, color: C.textDim, marginTop: 2 } }, sub) : null
            );
          };
          var onTimeRate = a.on_time_total > 0 ? Math.round(a.on_time_count / a.on_time_total * 100) : null;
          var sectionTitle = function(text) {
            return React.createElement('div', { style: { fontSize: 12, fontWeight: 600, color: C.textMuted, marginBottom: 10, marginTop: 24, textTransform: 'uppercase', letterSpacing: '0.05em' } }, text);
          };
          var toggleSort = function(table, key) {
            setVSort(function(prev) {
              var cur = prev[table];
              return Object.assign({}, prev, { [table]: { key: key, dir: cur.key === key && cur.dir === 'asc' ? 'desc' : cur.key === key ? 'asc' : 'desc' } });
            });
          };
          var sortRows = function(rows, table, computeMap) {
            var s = vSort[table]; if (!s || !rows) return rows;
            return rows.slice().sort(function(a, b) {
              var av = computeMap ? computeMap(a, s.key) : a[s.key];
              var bv = computeMap ? computeMap(b, s.key) : b[s.key];
              if (av == null) return 1; if (bv == null) return -1;
              if (typeof av === 'string') { var c = av.localeCompare(bv); return s.dir === 'asc' ? c : -c; }
              return s.dir === 'asc' ? av - bv : bv - av;
            });
          };
          var STH = function(table, key, label, style) {
            var s = vSort[table]; var active = s && s.key === key;
            return React.createElement('th', { onClick: function() { toggleSort(table, key); },
              style: Object.assign({ padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: active ? C.accent : C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }, style || {})
            }, label, active ? (s.dir === 'asc' ? ' \u25B2' : ' \u25BC') : '');
          };
          // ── Excel download button + sub-tab scaffolding ──
          var vName = ((editing && editing.vendor_name ? editing.vendor_name : 'vendor').replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '')) || 'vendor';
          var dlBtn = function(onClick) {
            return React.createElement(Btn, { size: 'sm', onClick: onClick },
              React.createElement(LIcon, { name: 'download', size: 13 }), ' Download Excel');
          };
          // Underlined link with a "new tab" icon (SKU -> catalog, PO# -> PO detail).
          var extLink = function(href, label, style, title) {
            return React.createElement('a', { href: href, target: '_blank', rel: 'noopener noreferrer', title: title || 'Open in new tab',
              style: Object.assign({ color: C.accent, textDecoration: 'underline', textUnderlineOffset: '2px', cursor: 'pointer', whiteSpace: 'nowrap' }, style || {}) },
              label,
              React.createElement(LIcon, { name: 'external-link', size: 11, style: { marginLeft: 3, verticalAlign: 'middle', opacity: 0.7 } })
            );
          };
          var pillBtn = function(on, label, onClick, minW) {
            return React.createElement('button', { key: label, onClick: onClick,
              onMouseOver: function(e) { if (!on) { e.currentTarget.style.borderColor = C.accent; e.currentTarget.style.color = C.text; } },
              onMouseOut: function(e) { if (!on) { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.textMuted; } },
              style: { padding: '5px 10px', border: '1px solid ' + (on ? C.accent : C.border), borderRadius: 5, cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: 'DM Sans', background: on ? C.accent + '22' : C.surface, color: on ? C.accent : C.textMuted, minWidth: minW || 'auto', transition: 'all 0.15s' } }, label);
          };
          var subNav = React.createElement('div', { style: { display: 'flex', gap: 6, borderBottom: '1px solid ' + C.border, margin: '20px 0 16px', flexWrap: 'wrap' } },
            [{ key: 'spend', label: 'Monthly Spend' }, { key: 'top', label: 'Top Products' }, { key: 'open', label: 'Open POs' }, { key: 'recent', label: 'Recent POs' }, { key: 'leadtime', label: 'Lead Time' }].map(function(t) {
              var on = aSub === t.key;
              var aHref = (editing && editing.id) ? ('/settings/vendors?vendor=' + editing.id + '&vtab=analytics&asub=' + t.key) : undefined;
              return React.createElement('a', { key: t.key, href: aHref, onClick: function(e) { if (aHref && (e.ctrlKey || e.metaKey || e.shiftKey)) return; e.preventDefault(); setASub(t.key); },
                onMouseOver: function(e) { if (!on) { e.currentTarget.style.color = C.text; e.currentTarget.style.background = C.border + '44'; } },
                onMouseOut: function(e) { if (!on) { e.currentTarget.style.color = C.textMuted; e.currentTarget.style.background = 'none'; } },
                style: { padding: '8px 14px', border: 'none', borderBottom: '2px solid ' + (on ? C.accent : 'transparent'), background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'DM Sans', color: on ? C.accent : C.textMuted, marginBottom: -1, textDecoration: 'none', transition: 'all 0.15s', borderRadius: '6px 6px 0 0' } }, t.label);
            })
          );

          // ── Monthly Spend sub-tab: granularity + range, Chart.js bar (spend) + line (units) ──
          var spendData = aggregateSpend(a.monthly_spend, spendGran, spendRange);
          var spendChartConfig = {
            type: 'bar',
            data: {
              labels: spendData.map(function(d) { return d.label; }),
              datasets: [
                { label: 'Spend', data: spendData.map(function(d) { return d.spend; }), backgroundColor: C.accent + 'CC', borderColor: C.accent, borderWidth: 1, borderRadius: 4, yAxisID: 'y', order: 2 },
                { label: 'Units', type: 'line', data: spendData.map(function(d) { return d.units; }), borderColor: '#22C55E', backgroundColor: '#22C55E22', pointBackgroundColor: '#22C55E', pointRadius: spendData.length > 40 ? 0 : 3, pointHoverRadius: 5, borderWidth: 2, fill: false, tension: 0.3, yAxisID: 'y1', order: 1 }
              ]
            },
            options: {
              responsive: true, maintainAspectRatio: false, interaction: { mode: 'index', intersect: false },
              plugins: {
                legend: { labels: { color: C.textMuted, font: { family: 'DM Sans', size: 11 }, boxWidth: 12, padding: 14 } },
                tooltip: { backgroundColor: C.surface, titleColor: C.text, bodyColor: C.textMuted, borderColor: C.border, borderWidth: 1, padding: 10, cornerRadius: 8,
                  callbacks: { label: function(ctx) { return ctx.dataset.label === 'Spend' ? 'Spend: $' + ctx.parsed.y.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'Units: ' + ctx.parsed.y.toLocaleString(); } } }
              },
              scales: {
                x: { ticks: { color: C.textDim, font: { family: 'DM Sans', size: 10 }, maxTicksLimit: 16, maxRotation: 45 }, grid: { color: C.border + '33' }, border: { color: C.border + '66' } },
                y: { position: 'left', ticks: { color: C.textDim, font: { family: 'DM Sans', size: 10 }, callback: function(v) { return '$' + (v >= 1000 ? Math.round(v / 1000) + 'k' : v); } }, grid: { color: C.border + '33' }, border: { color: C.border + '66' }, title: { display: true, text: 'Spend', color: C.textDim, font: { family: 'DM Sans', size: 10 } } },
                y1: { position: 'right', ticks: { color: C.textDim, font: { family: 'DM Sans', size: 10 } }, grid: { drawOnChartArea: false }, border: { color: C.border + '66' }, title: { display: true, text: 'Units', color: C.textDim, font: { family: 'DM Sans', size: 10 } } }
              }
            }
          };
          var spendTab = React.createElement('div', null,
            React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 } },
              React.createElement('div', { style: { display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' } },
                React.createElement('div', { style: { display: 'flex', gap: 4 } },
                  [{ label: 'Monthly', key: 'monthly' }, { label: 'Quarterly', key: 'quarterly' }, { label: 'Yearly', key: 'yearly' }].map(function(g) { return pillBtn(spendGran === g.key, g.label, function() { setSpendGran(g.key); }); })
                ),
                React.createElement('div', { style: { display: 'flex', gap: 4 } },
                  [{ label: '6M', m: 6 }, { label: '1Y', m: 12 }, { label: '2Y', m: 24 }, { label: '5Y', m: 60 }, { label: 'All', m: 0 }].map(function(r) { return pillBtn(spendRange === r.m, r.label, function() { setSpendRange(r.m); }, 34); })
                )
              ),
              spendData.length > 0 && dlBtn(function() {
                var fullMap = {}; (a.monthly_spend || []).forEach(function(m) { fullMap[m.month] = parseFloat(m.spend) || 0; });
                var chg = function(cur, ym, delta) { var prev = fullMap[shiftMonth(ym, delta)]; return (prev != null && prev > 0) ? Math.round((cur - prev) / prev * 1000) / 10 : ''; };
                var months = aggregateSpend(a.monthly_spend, 'monthly', spendRange);
                downloadXlsxReport(vName + '_Monthly_Spend.xlsx', 'Monthly Spend',
                  ['Month', 'Spend', 'Units', 'POs', 'M/M %', 'Q/Q %', 'Y/Y %'],
                  months.map(function(d) { return [d.key, d.spend, d.units, d.po_count, chg(d.spend, d.key, -1), chg(d.spend, d.key, -3), chg(d.spend, d.key, -12)]; }));
              })
            ),
            spendData.length > 0
              ? React.createElement(Card, { style: { padding: '16px 20px' } }, React.createElement(VendorSpendChart, { config: spendChartConfig, height: 300 }))
              : React.createElement(Card, { style: { padding: 40, textAlign: 'center', color: C.textDim, fontSize: 13 } }, 'No confirmed spend in this range')
          );

          // ── Top Products sub-tab: full ranked list, clickable SKU opens /catalog/:id ──
          var topRows = sortRows(a.top_products, 'top', function(p, k) { return k === 'total_spend' ? parseFloat(p.total_spend) || 0 : k === 'total_units' ? parseInt(p.total_units) || 0 : k === 'po_count' ? parseInt(p.po_count) || 0 : (p[k] || '').toString().toLowerCase(); });
          var topTab = React.createElement('div', null,
            React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 } },
              React.createElement('div', { style: { fontSize: 12, color: C.textMuted } }, (a.top_products ? a.top_products.length : 0) + ' products ranked by spend'),
              a.top_products && a.top_products.length > 0 && dlBtn(function() {
                downloadXlsxReport(vName + '_Top_Products.xlsx', 'Top Products', ['SKU', 'Title', 'Spend', 'Units', 'POs'], topRows.map(function(p) { return [p.sku, p.vendor_title || '', parseFloat(p.total_spend) || 0, parseInt(p.total_units) || 0, p.po_count || 0]; }));
              })
            ),
            a.top_products && a.top_products.length > 0
              ? React.createElement(Card, { style: { padding: 0, overflow: 'hidden' } },
                  React.createElement('div', { style: { overflow: 'auto', maxHeight: 460 } },
                    React.createElement('table', { style: { width: '100%', fontSize: 11, borderCollapse: 'collapse' } },
                      React.createElement('thead', null,
                        React.createElement('tr', { style: { borderBottom: '1px solid ' + C.border, background: C.surface, position: 'sticky', top: 0, zIndex: 1 } },
                          STH('top', 'sku', 'SKU'), STH('top', 'vendor_title', 'Title'), STH('top', 'total_spend', 'Spend', { textAlign: 'right' }), STH('top', 'total_units', 'Units', { textAlign: 'right' }), STH('top', 'po_count', 'POs', { textAlign: 'right' })
                        )
                      ),
                      React.createElement('tbody', null,
                        topRows.map(function(p, i) {
                          return React.createElement('tr', { key: p.id || i, onMouseEnter: function(e) { e.currentTarget.style.background = C.surfaceHover; }, onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; }, style: { borderBottom: '1px solid ' + C.border + '22', transition: 'background 0.12s' } },
                            React.createElement(TD, { style: { padding: '6px 10px' } }, p.id
                              ? extLink('/catalog/' + p.id, p.sku, { fontFamily: 'inherit', fontSize: 10, fontWeight: 600 }, 'Open in catalog')
                              : React.createElement('span', { style: { fontFamily: 'inherit', fontSize: 10, fontWeight: 600, color: C.text } }, p.sku)),
                            React.createElement(TD, { style: { padding: '6px 10px', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 11 } }, p.vendor_title || '—'),
                            React.createElement(TD, { style: { padding: '6px 10px', textAlign: 'right', fontWeight: 600 } }, '$' + Number(p.total_spend || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })),
                            React.createElement(TD, { style: { padding: '6px 10px', textAlign: 'right' } }, Number(p.total_units || 0).toLocaleString()),
                            React.createElement(TD, { style: { padding: '6px 10px', textAlign: 'right' } }, p.po_count || 0)
                          );
                        })
                      )
                    )
                  )
                )
              : React.createElement(Card, { style: { padding: 20, textAlign: 'center', color: C.textDim, fontSize: 12 } }, 'No product data')
          );

          // ── Open / Recent PO tables: clickable PO# opens /po/:id ──
          var poLink = function(po) { return extLink('/po/' + po.id, po.po_number, { fontWeight: 600 }, 'Open PO'); };
          var openRows = sortRows(a.open_pos, 'open', function(po, k) { return k === 'total_cost' ? parseFloat(po.total_cost) || 0 : k === 'total_units' ? parseInt(po.total_units) || 0 : k === 'po_date' ? new Date(po.po_date).getTime() : (po[k] || '').toString().toLowerCase(); });
          var openTab = React.createElement('div', null,
            React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 } },
              React.createElement('div', { style: { fontSize: 12, color: C.textMuted } }, (a.open_pos ? a.open_pos.length : 0) + ' open POs (includes unconfirmed)'),
              a.open_pos && a.open_pos.length > 0 && dlBtn(function() {
                downloadXlsxReport(vName + '_Open_POs.xlsx', 'Open POs', ['PO#', 'Date', 'Status', 'Location', 'Units', 'Total'], openRows.map(function(po) { return [po.po_number, fmtDateOnly(po.po_date), po.status, po.warehouse_location || '', po.total_units || 0, parseFloat(po.total_cost) || 0]; }));
              })
            ),
            a.open_pos && a.open_pos.length > 0
              ? React.createElement(Card, { style: { padding: 0, overflow: 'hidden' } },
                  React.createElement('div', { style: { overflow: 'auto', maxHeight: 460 } },
                    React.createElement('table', { style: { width: '100%', fontSize: 11, borderCollapse: 'collapse' } },
                      React.createElement('thead', null,
                        React.createElement('tr', { style: { borderBottom: '1px solid ' + C.border, background: C.surface, position: 'sticky', top: 0, zIndex: 1 } },
                          STH('open', 'po_number', 'PO#'), STH('open', 'po_date', 'Date'), STH('open', 'status', 'Status'), STH('open', 'warehouse_location', 'Location'), STH('open', 'total_units', 'Units', { textAlign: 'right' }), STH('open', 'total_cost', 'Total', { textAlign: 'right' })
                        )
                      ),
                      React.createElement('tbody', null,
                        openRows.map(function(po) {
                          return React.createElement('tr', { key: po.id, onMouseEnter: function(e) { e.currentTarget.style.background = C.surfaceHover; }, onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; }, style: { borderBottom: '1px solid ' + C.border + '22', transition: 'background 0.12s' } },
                            React.createElement(TD, { style: { padding: '6px 10px' } }, poLink(po)),
                            React.createElement(TD, { style: { padding: '6px 10px' } }, fmtDateOnly(po.po_date)),
                            React.createElement(TD, { style: { padding: '6px 10px' } }, React.createElement(Badge, { status: po.status })),
                            React.createElement(TD, { style: { padding: '6px 10px', fontSize: 10, color: C.textMuted } }, po.warehouse_location || '—'),
                            React.createElement(TD, { style: { padding: '6px 10px', textAlign: 'right' } }, po.total_units || 0),
                            React.createElement(TD, { style: { padding: '6px 10px', textAlign: 'right', fontWeight: 600 } }, '$' + Number(po.total_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
                          );
                        })
                      )
                    )
                  )
                )
              : React.createElement(Card, { style: { padding: 20, textAlign: 'center', color: C.textDim, fontSize: 12 } }, 'No open POs')
          );

          var recentRows = sortRows(a.recent_pos, 'recent', function(po, k) {
            if (k === 'lead_time') return po.submitted_date && po.checked_in_date ? Math.round((new Date(po.checked_in_date) - new Date(po.submitted_date)) / 86400000) : null;
            if (k === 'total_cost') return parseFloat(po.total_cost) || 0;
            if (k === 'total_units' || k === 'sku_count') return parseInt(po[k]) || 0;
            if (k === 'po_date') return new Date(po.po_date).getTime();
            return (po[k] || '').toString().toLowerCase();
          });
          var ltOf = function(po) { return po.submitted_date && po.checked_in_date ? Math.round((new Date(po.checked_in_date) - new Date(po.submitted_date)) / 86400000) : null; };
          var recentTab = React.createElement('div', null,
            React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 } },
              React.createElement('div', { style: { fontSize: 12, color: C.textMuted } }, (a.recent_pos ? a.recent_pos.length : 0) + ' most recent confirmed+ POs'),
              a.recent_pos && a.recent_pos.length > 0 && dlBtn(function() {
                downloadXlsxReport(vName + '_Recent_POs.xlsx', 'Recent POs', ['PO#', 'Date', 'Status', 'SKUs', 'Units', 'Total', 'Lead Time (d)'], recentRows.map(function(po) { var lt = ltOf(po); return [po.po_number, fmtDateOnly(po.po_date), po.status, po.sku_count || 0, po.total_units || 0, parseFloat(po.total_cost) || 0, lt != null ? lt : '']; }));
              })
            ),
            a.recent_pos && a.recent_pos.length > 0
              ? React.createElement(Card, { style: { padding: 0, overflow: 'hidden' } },
                  React.createElement('div', { style: { overflow: 'auto', maxHeight: 460 } },
                    React.createElement('table', { style: { width: '100%', fontSize: 11, borderCollapse: 'collapse' } },
                      React.createElement('thead', null,
                        React.createElement('tr', { style: { borderBottom: '1px solid ' + C.border, background: C.surface, position: 'sticky', top: 0, zIndex: 1 } },
                          STH('recent', 'po_number', 'PO#'), STH('recent', 'po_date', 'Date'), STH('recent', 'status', 'Status'),
                          STH('recent', 'sku_count', 'SKUs', { textAlign: 'right' }), STH('recent', 'total_units', 'Units', { textAlign: 'right' }), STH('recent', 'total_cost', 'Total', { textAlign: 'right' }), STH('recent', 'lead_time', 'Lead Time', { textAlign: 'right' })
                        )
                      ),
                      React.createElement('tbody', null,
                        recentRows.map(function(po) {
                          var lt = ltOf(po);
                          return React.createElement('tr', { key: po.id, onMouseEnter: function(e) { e.currentTarget.style.background = C.surfaceHover; }, onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; }, style: { borderBottom: '1px solid ' + C.border + '22', transition: 'background 0.12s' } },
                            React.createElement(TD, { style: { padding: '6px 10px' } }, poLink(po)),
                            React.createElement(TD, { style: { padding: '6px 10px' } }, fmtDateOnly(po.po_date)),
                            React.createElement(TD, { style: { padding: '6px 10px' } }, React.createElement(Badge, { status: po.status })),
                            React.createElement(TD, { style: { padding: '6px 10px', textAlign: 'right' } }, po.sku_count || '—'),
                            React.createElement(TD, { style: { padding: '6px 10px', textAlign: 'right' } }, po.total_units || 0),
                            React.createElement(TD, { style: { padding: '6px 10px', textAlign: 'right', fontWeight: 600 } }, '$' + Number(po.total_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })),
                            React.createElement(TD, { style: { padding: '6px 10px', textAlign: 'right', color: lt != null ? (lt > 14 ? '#FBBF24' : C.text) : C.textDim } }, lt != null ? lt + 'd' : '—')
                          );
                        })
                      )
                    )
                  )
                )
              : React.createElement(Card, { style: { padding: 20, textAlign: 'center', color: C.textDim, fontSize: 12 } }, 'No recent POs')
          );

          // ── Lead Time sub-tab: overall metrics + per-warehouse breakdown ──
          var ltWh = a.lead_time_by_warehouse || [];
          var whOnTime = function(o, t) { return t > 0 ? Math.round(o / t * 100) : null; };
          var leadTimeTab = React.createElement('div', null,
            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(165px, 1fr))', gap: 10, marginBottom: 18 } },
              statCard('Avg Lead Time', a.avg_lead_time != null ? a.avg_lead_time + 'd' : '—', a.lead_time_po_count ? 'from ' + a.lead_time_po_count + ' POs' : 'submitted → received/receiving'),
              statCard('Submitted → Received', a.avg_sub_to_received != null ? a.avg_sub_to_received + 'd' : '—', a.sub_to_received_count ? 'from ' + a.sub_to_received_count + ' POs' : null),
              statCard('On-Time Rate', onTimeRate != null ? onTimeRate + '%' : '—', a.on_time_total > 0 ? a.on_time_count + ' of ' + a.on_time_total + ' POs' : null, onTimeRate != null ? (onTimeRate >= 80 ? '#22C55E' : onTimeRate >= 50 ? '#FBBF24' : '#EF4444') : undefined)
            ),
            React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 } },
              React.createElement('div', { style: { fontSize: 12, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' } }, 'By Warehouse'),
              ltWh.length > 0 && dlBtn(function() {
                downloadXlsxReport(vName + '_Lead_Time_By_Warehouse.xlsx', 'Lead Time', ['Warehouse', 'POs', 'Avg Lead Time (d)', 'Submitted-Received (d)', 'On-Time %'], ltWh.map(function(r) { var ot = whOnTime(r.on_time, r.on_time_total); return [r.warehouse, r.po_count, r.avg_lead_time != null ? r.avg_lead_time : '', r.avg_sub_to_received != null ? r.avg_sub_to_received : '', ot != null ? ot : '']; }));
              })
            ),
            ltWh.length > 0
              ? React.createElement(Card, { style: { padding: 0, overflow: 'hidden' } },
                  React.createElement('div', { style: { overflow: 'auto', maxHeight: 420 } },
                    React.createElement('table', { style: { width: '100%', fontSize: 11, borderCollapse: 'collapse' } },
                      React.createElement('thead', null,
                        React.createElement('tr', { style: { borderBottom: '1px solid ' + C.border, background: C.surface, position: 'sticky', top: 0, zIndex: 1 } },
                          ['Warehouse', 'POs', 'Avg Lead Time', 'Submitted → Received', 'On-Time'].map(function(h, i) {
                            return React.createElement('th', { key: i, style: { padding: '8px 10px', textAlign: i === 0 ? 'left' : 'right', fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' } }, h);
                          })
                        )
                      ),
                      React.createElement('tbody', null,
                        ltWh.map(function(r, i) {
                          var ot = whOnTime(r.on_time, r.on_time_total);
                          return React.createElement('tr', { key: i, onMouseEnter: function(e) { e.currentTarget.style.background = C.surfaceHover; }, onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; }, style: { borderBottom: '1px solid ' + C.border + '22', transition: 'background 0.12s' } },
                            React.createElement(TD, { style: { padding: '6px 10px', fontWeight: 600, color: C.text } }, r.warehouse),
                            React.createElement(TD, { style: { padding: '6px 10px', textAlign: 'right' } }, r.po_count),
                            React.createElement(TD, { style: { padding: '6px 10px', textAlign: 'right', fontWeight: 600 } }, r.avg_lead_time != null ? r.avg_lead_time + 'd' : '—'),
                            React.createElement(TD, { style: { padding: '6px 10px', textAlign: 'right' } }, r.avg_sub_to_received != null ? r.avg_sub_to_received + 'd' : '—'),
                            React.createElement(TD, { style: { padding: '6px 10px', textAlign: 'right', color: ot != null ? (ot >= 80 ? '#22C55E' : ot >= 50 ? '#FBBF24' : '#EF4444') : C.textDim } }, ot != null ? ot + '%' : '—')
                          );
                        })
                      )
                    )
                  )
                )
              : React.createElement(Card, { style: { padding: 20, textAlign: 'center', color: C.textDim, fontSize: 12 } }, 'No lead-time data')
          );

          return React.createElement('div', { style: { maxWidth: 1100, margin: '0 auto' } },
            // Stat cards row
            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 10, marginBottom: 8 } },
              statCard('Total POs', a.total_pos || 0),
              statCard('Total Spend', '$' + Number(a.total_spend || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })),
              statCard('Total Units', Number(a.total_units || 0).toLocaleString()),
              statCard('Active SKUs', a.active_skus || 0),
              statCard('Avg Lead Time', a.avg_lead_time != null ? a.avg_lead_time + 'd' : '\u2014', a.lead_time_po_count ? 'from ' + a.lead_time_po_count + ' POs' : null),
              statCard('Avg PO Value', a.total_pos > 0 ? '$' + Math.round(a.total_spend / a.total_pos).toLocaleString() : '\u2014'),
              statCard('On-Time Rate', onTimeRate != null ? onTimeRate + '%' : '\u2014', a.on_time_total > 0 ? a.on_time_count + ' of ' + a.on_time_total + ' POs' : null, onTimeRate != null ? (onTimeRate >= 80 ? '#22C55E' : onTimeRate >= 50 ? '#FBBF24' : '#EF4444') : undefined)
            ),
            // Analytics sub-tab content (Monthly Spend / Top Products / Open POs / Recent POs)
            subNav,
            aSub === 'spend' && spendTab,
            aSub === 'top' && topTab,
            aSub === 'open' && openTab,
            aSub === 'recent' && recentTab,
            aSub === 'leadtime' && leadTimeTab
          );
        })(),
        // ─── Activity Log Tab ───
        vendorTab === 'activity' && (function() {
          var actToggleSort = function(key) {
            setVSort(function(prev) {
              var cur = prev.activity;
              return Object.assign({}, prev, { activity: { key: key, dir: cur.key === key && cur.dir === 'asc' ? 'desc' : cur.key === key ? 'asc' : 'desc' } });
            });
          };
          var actSTH = function(key, label) {
            var s = vSort.activity; var active = s && s.key === key;
            return React.createElement('th', { onClick: function() { actToggleSort(key); },
              style: { padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: active ? C.accent : C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }
            }, label, active ? (s.dir === 'asc' ? ' \u25B2' : ' \u25BC') : '');
          };
          var sortedActivity = vendorActivity.slice().sort(function(a, b) {
            var s = vSort.activity; if (!s) return 0;
            var av = s.key === 'created_at' ? new Date(a.created_at).getTime() : (a[s.key] || '').toString().toLowerCase();
            var bv = s.key === 'created_at' ? new Date(b.created_at).getTime() : (b[s.key] || '').toString().toLowerCase();
            if (av == null) return 1; if (bv == null) return -1;
            if (typeof av === 'string') { var c = av.localeCompare(bv); return s.dir === 'asc' ? c : -c; }
            return s.dir === 'asc' ? av - bv : bv - av;
          });
          return vendorActivity.length > 0
            ? React.createElement('div', { style: { maxWidth: 1100, margin: '0 auto' } },
                React.createElement('table', { style: { width: '100%', fontSize: 12 } },
                  React.createElement('thead', null,
                    React.createElement('tr', { style: { borderBottom: '1px solid ' + C.border } },
                      actSTH('created_at', 'Date'), actSTH('username', 'User'), actSTH('po_number', 'PO#'),
                      actSTH('action', 'Action'), actSTH('details', 'Details')
                    )
                  ),
                  React.createElement('tbody', null,
                    sortedActivity.map(function(a) {
                      var ts = new Date(a.created_at);
                      var dateStr = ts.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ' ' + ts.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                      return React.createElement('tr', { key: a.id, style: { borderBottom: '1px solid ' + C.border + '22' } },
                        React.createElement(TD, { style: { whiteSpace: 'nowrap', fontSize: 11, color: C.textMuted } }, dateStr),
                        React.createElement(TD, { style: { fontSize: 11 } }, a.username || 'system'),
                        React.createElement(TD, { style: { fontWeight: 600, color: C.accent, fontFamily: 'inherit', fontSize: 11 } }, a.po_number || '\u2014'),
                        React.createElement(TD, null, React.createElement('span', { style: { padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: C.accent + '22', color: C.accent } }, (a.action || '').replace(/_/g, ' '))),
                        React.createElement(TD, { style: { fontSize: 11, color: C.textMuted, maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, a.details)
                      );
                    })
                  )
                )
              )
            : React.createElement('div', { style: { padding: '40px 0', textAlign: 'center', color: C.textDim, fontSize: 13 } }, 'No activity recorded for this vendor');
        })()
      )
    )
  );
}

// ─── WAREHOUSES PAGE ───
function WarehousesPage({ showToast, onNavigate }) {
  const [warehouses, setWarehouses] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [emailTemplates, setEmailTemplates] = useState([]);
  const fileRef = useRef(null);

  const load = useCallback(async () => { setWarehouses(await api('/warehouses?active=true')); }, []);
  useEffect(() => { load(); api('/purchase-orders/meta/templates').then(setEmailTemplates).catch(function(e) { console.error('[silent-catch]', e); }); }, [load]);

  const openNew = () => { setForm({}); setEditing(null); setModalOpen(true); };
  const openEdit = (w) => { setForm(w); setEditing(w); setModalOpen(true); };

  const save = async () => {
    try {
      const body = { ...form, label_required: form.label_required === true || form.label_required === 'true' };
      if (editing) { await api('/warehouses/' + editing.id, { method: 'PUT', body: JSON.stringify(body) }); showToast('Warehouse updated'); }
      else { await api('/warehouses', { method: 'POST', body: JSON.stringify(body) }); showToast('Warehouse created'); }
      setModalOpen(false); load();
    } catch (e) { showToast(e.message, 'error'); }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    try { const r = await apiUpload('/import/warehouses', file); showToast('Imported ' + r.imported + ' warehouses' + (r.skipped ? ' (' + r.skipped + ' skipped' + (r.errors && r.errors.length ? ': ' + r.errors.slice(0, 3).map(function(x) { return x.error || x; }).join('; ') : '') + ')' : ''), r.skipped ? 'warning' : undefined); load(); }
    catch (e) { showToast(e.message, 'error'); }
    e.target.value = '';
  };

  const F = (key) => ({ value: form[key] || '', onChange: e => setForm({ ...form, [key]: e.target.value }) });

  var whCSVHeaders = ['warehouse_id','name','address','address2','city','state','zip','country','phone','email','contact_name','label_required'];

  var exportWarehouses = function() {
    downloadCSV('warehouses_export.csv', arrayToCSV(whCSVHeaders, warehouses));
  };

  return React.createElement('div', null,
    React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 } },
      React.createElement('div', null,
        React.createElement('h2', { style: { margin: 0, fontSize: 20, fontWeight: 600, color: C.text } }, 'Warehouses'),
        React.createElement('p', { style: { margin: '4px 0 0', fontSize: 13, color: C.textMuted } }, warehouses.length + ' active')
      ),
      React.createElement('div', { style: { display: 'flex', gap: 8, flexWrap: 'wrap' } },
        React.createElement(Btn, { size: 'sm', onClick: function() { downloadTemplate('warehouses_template.csv', whCSVHeaders); } }, React.createElement(LIcon, { name: 'file-text', size: 14 }), ' Template'),
        React.createElement('input', { type: 'file', accept: '.csv', ref: fileRef, onChange: handleImport, style: { display: 'none' } }),
        React.createElement(Btn, { size: 'sm', onClick: () => fileRef.current.click() }, React.createElement(LIcon, { name: 'upload', size: 14 }), ' Import CSV'),
        React.createElement(Btn, { size: 'sm', onClick: exportWarehouses, disabled: warehouses.length === 0 }, React.createElement(LIcon, { name: 'download', size: 14 }), ' Export CSV'),
        React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: openNew }, '+ Add Warehouse')
      )
    ),
    React.createElement(DataTable, {
      columns: [
        { key: 'warehouse_id', label: 'ID', locked: true, style: { fontWeight: 600, color: C.accent, fontFamily: 'inherit', fontSize: 12 } },
        { key: 'name', label: 'Name', style: { color: C.text, fontWeight: 500 } },
        { key: 'address', label: 'Address', defaultVisible: true },
        { key: 'address2', label: 'Address 2', defaultVisible: false },
        { key: 'city', label: 'City' },
        { key: 'state', label: 'State' },
        { key: 'zip', label: 'ZIP', defaultVisible: false },
        { key: 'phone', label: 'Phone', defaultVisible: false },
        { key: 'email', label: 'Email', defaultVisible: false },
        { key: 'contact_name', label: 'Contact', defaultVisible: false },
        { key: 'bin_count', label: 'Bins', render: function(w) { return Number(w.bin_count || 0).toLocaleString(); } },
        { key: 'sku_count', label: 'SKUs', render: function(w) { return Number(w.sku_count || 0).toLocaleString(); } },
        { key: 'unit_count', label: 'Units', render: function(w) { return Number(w.unit_count || 0).toLocaleString(); } },
        { key: 'total_cost', label: 'Total Cost', render: function(w) { return '$' + Number(w.total_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }); } },
        { key: 'fba_direct', label: 'FBA Direct', render: function(w) {
          return w.fba_direct ? 'Yes' : '\u2014';
        }},
        { key: 'wfs_direct', label: 'WFS Direct', render: function(w) {
          return w.wfs_direct ? 'Yes' : '\u2014';
        }},
        { key: 'meli_direct', label: 'MELI Direct', render: function(w) {
          return w.meli_direct ? 'Yes' : '\u2014';
        }},
        { key: 'label_required', label: 'Case Labels', render: function(w) {
          return w.label_required ? 'Yes' : 'No';
        }},
        { key: '_actions', label: '', locked: true, noFilter: true, stopClick: true, render: function(w) {
          return React.createElement('button', {
            onClick: function(e) { e.stopPropagation(); openEdit(w); },
            style: { background: 'none', border: '1px solid ' + C.border, color: C.textMuted, cursor: 'pointer', padding: '4px 8px', borderRadius: 4, fontSize: 12, lineHeight: 1 },
            title: 'Edit warehouse'
          }, '\u270F');
        }}
      ],
      data: warehouses, storageKey: 'warehouses',
      defaultSort: 'warehouse_id', defaultSortDir: 'asc',
      rowHref: function(w) { return '/warehouse/' + w.id; },
      onRowClick: function(w) { if (onNavigate) onNavigate('warehouse-detail', w.id); else openEdit(w); },
      emptyTitle: 'No warehouses', emptySub: 'Add or import warehouses'
    }),
    React.createElement(Modal, { open: modalOpen, onClose: () => setModalOpen(false), title: editing ? 'Edit Warehouse' : 'New Warehouse' },
      React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 } },
        React.createElement(Inp, { label: 'Warehouse ID', placeholder: 'e.g. WH-01', ...F('warehouse_id') }),
        React.createElement(Inp, { label: 'Name', ...F('name') }),
        React.createElement('div', { style: { gridColumn: 'span 2' } }, React.createElement(Inp, { label: 'Address', ...F('address') })),
        React.createElement('div', { style: { gridColumn: 'span 2' } }, React.createElement(Inp, { label: 'Address 2', placeholder: 'Suite / Unit / Floor', ...F('address2') })),
        React.createElement(Inp, { label: 'City', ...F('city') }),
        React.createElement(StateSelect, { label: 'State', country: form.country || 'US', ...F('state') }),
        React.createElement(Inp, { label: 'ZIP', ...F('zip') }),
        React.createElement('div', null,
          // Label restyled to match the Inp fields around it; SearchSelect's trigger already
          // carries Inp's own padding/radius/font size, so the row lines up without overrides.
          React.createElement('label', { style: { display: 'block', fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 } }, 'Country'),
          React.createElement(SearchSelect, {
            value: form.country || 'US',
            onChange: function(val) { setForm(Object.assign({}, form, { country: val })); },
            options: countryOptionList(form.country || 'US'),
            placeholder: 'Select country'
          })
        ),
        React.createElement(Inp, { label: 'Phone', ...F('phone') }),
        React.createElement(Inp, { label: 'Email', ...F('email') }),
        React.createElement(Inp, { label: 'Contact', ...F('contact_name') }),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, gridColumn: 'span 2' } },
          React.createElement('input', { type: 'checkbox', checked: form.label_required === true || form.label_required === 'true', onChange: e => setForm({ ...form, label_required: e.target.checked }) }),
          React.createElement('span', { style: { fontSize: 13, color: C.textMuted } }, 'Case labels required')
        ),
        React.createElement('div', { style: { gridColumn: 'span 2' } },
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
            React.createElement('input', { type: 'checkbox', checked: form.fba_direct === true || form.fba_direct === 'true', onChange: e => setForm({ ...form, fba_direct: e.target.checked }) }),
            React.createElement('span', { style: { fontSize: 13, color: C.textMuted } }, 'FBA Direct')
          ),
          React.createElement('div', { style: { fontSize: 11, color: C.textDim, marginTop: 2, marginLeft: 24 } }, 'Vendor ships directly to Amazon. POs for this warehouse skip check-in and go straight from Submitted to FBA Shipment creation.')
        ),
        React.createElement('div', { style: { gridColumn: 'span 2' } },
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
            React.createElement('input', { type: 'checkbox', checked: form.wfs_direct === true || form.wfs_direct === 'true', onChange: e => setForm({ ...form, wfs_direct: e.target.checked }) }),
            React.createElement('span', { style: { fontSize: 13, color: C.textMuted } }, 'WFS Direct')
          ),
          React.createElement('div', { style: { fontSize: 11, color: C.textDim, marginTop: 2, marginLeft: 24 } }, 'Vendor ships directly to Walmart WFS. POs for this warehouse skip check-in and go straight from Submitted to WFS Shipment creation.')
        ),
        // MELI Direct (Anthony 2026-09-29), the same switch for Mercado Libre Full
        React.createElement('div', { style: { gridColumn: 'span 2' } },
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
            React.createElement('input', { type: 'checkbox', checked: form.meli_direct === true || form.meli_direct === 'true', onChange: e => setForm({ ...form, meli_direct: e.target.checked }) }),
            React.createElement('span', { style: { fontSize: 13, color: C.textMuted } }, 'MELI Direct')
          ),
          React.createElement('div', { style: { fontSize: 11, color: C.textDim, marginTop: 2, marginLeft: 24 } }, 'Vendor ships directly to Mercado Libre. POs for this warehouse skip check-in and go straight from Submitted to MELI Shipment creation. The warehouse ID must start with MELI.')
        ),
        React.createElement('div', { style: { gridColumn: 'span 2' } },
          React.createElement('label', { style: { display: 'block', fontSize: 12, color: C.textMuted, marginBottom: 4 } }, 'Default Email Template'),
          React.createElement('select', {
            value: form.default_email_template_id || '',
            onChange: function(e) { setForm({ ...form, default_email_template_id: e.target.value ? Number(e.target.value) : null }); },
            style: { width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid ' + C.border, background: C.bg, color: C.text, fontSize: 13, outline: 'none' }
          },
            React.createElement('option', { value: '' }, '— Use global default —'),
            emailTemplates.map(function(t) {
              return React.createElement('option', { key: t.id, value: t.id }, t.name + (t.is_default ? ' (global default)' : ''));
            })
          )
        )
      ),
      React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 } },
        React.createElement(Btn, { onClick: () => setModalOpen(false) }, 'Cancel'),
        React.createElement(Btn, { variant: 'primary', onClick: save }, React.createElement(LIcon, { name: 'check', size: 14 }), editing ? ' Update' : ' Create')
      )
    )
  );
}

// ─── WAREHOUSE DETAIL PAGE (bins management) ───
function WarehouseDetailPage({ warehouseId, showToast, onNavigate }) {
  var [warehouse, setWarehouse] = useState(null);
  var [bins, setBins] = useState([]);
  var [binSearch, setBinSearch] = useState('');
  var [binModalOpen, setBinModalOpen] = useState(false);
  var [editingBin, setEditingBin] = useState(null);
  var [binForm, setBinForm] = useState({});
  var [invModalOpen, setInvModalOpen] = useState(false);
  var [invBin, setInvBin] = useState(null);
  var [inventory, setInventory] = useState([]);
  var [binActivity, setBinActivity] = useState([]);
  var [invTab, setInvTab] = useState('contents');
  var binFileRef = useRef(null);

  var binCSVHeaders = ['bin_code','label','bin_type','status','length','width','height','max_weight','zone','notes'];

  var exportBins = function() {
    var rows = bins.map(function(b) {
      return { bin_code: b.bin_code, label: b.label, bin_type: b.bin_type, status: b.status, length: b.length, width: b.width, height: b.height, max_weight: b.max_weight, zone: b.zone, notes: b.notes };
    });
    downloadCSV('bins_' + (warehouse ? warehouse.warehouse_id : 'export') + '.csv', arrayToCSV(binCSVHeaders, rows));
  };

  var handleBinImport = async function(e) {
    var file = e.target.files[0]; if (!file) return;
    try {
      var r = await apiUpload('/import/bins/' + warehouseId, file);
      var msg = 'Imported ' + r.imported + ' bins';
      if (r.skipped) msg += ' (' + r.skipped + ' skipped)';
      showToast(msg, r.imported === 0 ? 'error' : undefined);
      loadBins();
    } catch (err) { showToast(err.message, 'error'); }
    e.target.value = '';
  };

  var loadWarehouse = useCallback(function() {
    return api('/warehouses/' + warehouseId).then(setWarehouse).catch(function(e) { showToast(e.message, 'error'); });
  }, [warehouseId]);

  var loadBins = useCallback(function() {
    return api('/warehouses/' + warehouseId + '/bins').then(setBins).catch(function(e) { showToast(e.message, 'error'); });
  }, [warehouseId]);

  useEffect(function() { loadWarehouse(); loadBins(); }, [loadWarehouse, loadBins]);

  // Bin modal
  var openNewBin = function() { setBinForm({ bin_type: 'shelf', status: 'active' }); setEditingBin(null); setBinModalOpen(true); };
  var openEditBin = function(b) { setBinForm(b); setEditingBin(b); setBinModalOpen(true); };
  // Deep link: /warehouse/<id>?bin=<binId> opens that bin once the bins list has loaded.
  var binDeepLinkRef = useRef(false);
  useEffect(function() {
    if (binDeepLinkRef.current || !bins || !bins.length) return;
    try {
      var qbin = new URLSearchParams(window.location.search).get('bin');
      if (qbin) {
        var b = bins.find(function(x) { return String(x.id) === String(qbin); });
        if (b) { binDeepLinkRef.current = true; openEditBin(b); history.replaceState(history.state, '', '/warehouse/' + warehouseId); }
      }
    } catch (e) {}
  }, [bins]);

  // Deep link: /warehouse/<id>?invbin=<binId>[&invtab=contents|activity] opens that bin's inventory modal.
  var invBinDeepLinkRef = useRef(false);
  useEffect(function() {
    if (invBinDeepLinkRef.current || !bins || !bins.length) return;
    try {
      var sp = new URLSearchParams(window.location.search);
      var qib = sp.get('invbin');
      if (qib) {
        var b = bins.find(function(x) { return String(x.id) === String(qib); });
        if (b) { invBinDeepLinkRef.current = true; openInventory(b); var qit = sp.get('invtab'); if (qit) setInvTab(qit); history.replaceState(history.state, '', '/warehouse/' + warehouseId); }
      }
    } catch (e) {}
  }, [bins]);

  var saveBin = async function() {
    try {
      if (editingBin) {
        await api('/warehouses/' + warehouseId + '/bins/' + editingBin.id, { method: 'PUT', body: JSON.stringify(binForm) });
        showToast('Bin updated');
      } else {
        await api('/warehouses/' + warehouseId + '/bins', { method: 'POST', body: JSON.stringify(binForm) });
        showToast('Bin created');
      }
      setBinModalOpen(false); loadBins();
    } catch (e) { showToast(e.message, 'error'); }
  };

  var deleteBin = async function(bin) {
    if (!(await appConfirm('Deactivate bin ' + bin.bin_code + '?', { danger: true, okLabel: 'Deactivate' }))) return;
    try {
      await api('/warehouses/' + warehouseId + '/bins/' + bin.id, { method: 'DELETE' });
      showToast('Bin deactivated'); loadBins();
    } catch (e) { showToast(e.message, 'error'); }
  };

  // Inventory modal (read-only)
  var openInventory = async function(bin) {
    setInvBin(bin);
    setInvTab('contents');
    try {
      var inv = await api('/warehouses/' + warehouseId + '/bins/' + bin.id + '/inventory');
      setInventory(inv);
    } catch (e) { setInventory([]); }
    try {
      var act = await api('/warehouses/' + warehouseId + '/bins/' + bin.id + '/activity');
      setBinActivity(act);
    } catch (e) { setBinActivity([]); }
    setInvModalOpen(true);
  };

  var BF = function(key) { return { value: binForm[key] || '', onChange: function(e) { setBinForm(Object.assign({}, binForm, { [key]: e.target.value })); } }; };

  if (!warehouse) return React.createElement('div', { style: { padding: 40, textAlign: 'center', color: C.textMuted } }, 'Loading...');

  // Summary stats
  var totalBins = bins.length;
  var activeBins = bins.filter(function(b) { return b.status === 'active'; }).length;
  var totalUnits = bins.reduce(function(s, b) { return s + (b.total_units || 0); }, 0);
  var avgFill = totalBins > 0 ? (bins.reduce(function(s, b) { return s + (b.fill_pct || 0); }, 0) / totalBins) : 0;

  var filteredBins = bins.filter(function(b) {
    if (!binSearch) return true;
    var s = binSearch.toLowerCase();
    return (b.bin_code || '').toLowerCase().includes(s) || (b.label || '').toLowerCase().includes(s) || (b.zone || '').toLowerCase().includes(s) || (b.bin_type || '').toLowerCase().includes(s) || (b.notes || '').toLowerCase().includes(s);
  });

  var binTypeOpts = [
    { value: 'shelf', label: 'Shelf' }, { value: 'staging', label: 'Staging' }
  ];
  var binStatusOpts = [
    { value: 'active', label: 'Active' }, { value: 'full', label: 'Full' }, { value: 'unsellable', label: 'Unsellable' },
    { value: 'damaged', label: 'Damaged' }, { value: 'inactive', label: 'Inactive' }
  ];

  return React.createElement('div', null,
    // Back button + header
    React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 } },
      React.createElement('button', {
        onClick: function() { onNavigate('settings-warehouses'); },
        style: { background: 'none', border: '1px solid ' + C.border, color: C.textMuted, cursor: 'pointer', padding: '6px 10px', borderRadius: 6, fontSize: 14, lineHeight: 1 }
      }, '\u2190'),
      React.createElement('div', null,
        React.createElement('h2', { style: { margin: 0, fontSize: 20, fontWeight: 600, color: C.text } }, warehouse.warehouse_id + ' — ' + warehouse.name),
        React.createElement('p', { style: { margin: '2px 0 0', fontSize: 12, color: C.textMuted } },
          [warehouse.address, warehouse.address2, warehouse.city, warehouse.state, warehouse.zip].filter(Boolean).join(', ')
            + (warehouse.country && warehouse.country !== 'US' ? ', ' + countryName(warehouse.country) : '')
        )
      )
    ),

    // Summary stats row
    React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12, marginBottom: 20 } },
      [
        { label: 'Total Bins', value: totalBins },
        { label: 'Active Bins', value: activeBins },
        { label: 'Total Units', value: totalUnits.toLocaleString() },
        { label: 'Avg Fill %', value: avgFill.toFixed(1) + '%' }
      ].map(function(s) {
        return React.createElement(Card, { key: s.label, style: { padding: 14, textAlign: 'center' } },
          React.createElement('div', { style: { fontSize: 20, fontWeight: 700, color: C.text } }, s.value),
          React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 2, textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.05em' } }, s.label)
        );
      })
    ),

    // Bin toolbar
    React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' } },
      React.createElement(SearchInput, { value: binSearch, onChange: function(e) { setBinSearch(e.target.value); }, placeholder: 'Search bins...',
        style: { width: 240, fontSize: 12, background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '6px 10px', borderRadius: 6 } }),
      React.createElement('div', { style: { display: 'flex', gap: 8 } },
        React.createElement(Btn, { size: 'sm', onClick: function() { downloadTemplate('bins_template.csv', binCSVHeaders); } }, React.createElement(LIcon, { name: 'file-text', size: 14 }), ' Template'),
        React.createElement('input', { type: 'file', accept: '.csv', ref: binFileRef, onChange: handleBinImport, style: { display: 'none' } }),
        React.createElement(Btn, { size: 'sm', onClick: function() { binFileRef.current.click(); } }, React.createElement(LIcon, { name: 'upload', size: 14 }), ' Import CSV'),
        React.createElement(Btn, { size: 'sm', onClick: exportBins, disabled: bins.length === 0 }, React.createElement(LIcon, { name: 'download', size: 14 }), ' Export CSV'),
        React.createElement('span', { title: 'Exports all bins with empty/full status (splits multi-bin codes)' },
          React.createElement(Btn, { size: 'sm', disabled: bins.length === 0, onClick: function() {
            fetch('/api/warehouses/' + warehouseId + '/bins/export-status', { credentials: 'include' })
              .then(function(r) { if (!r.ok) throw new Error('Failed'); return r.blob(); })
              .then(function(blob) { var url = URL.createObjectURL(blob); var a = document.createElement('a'); a.href = url; a.download = 'bin_status_' + (warehouse ? warehouse.warehouse_id : 'export') + '.csv'; a.click(); URL.revokeObjectURL(url); })
              .catch(function(e) { showToast('Failed to export: ' + e.message, 'error'); });
          } }, React.createElement(LIcon, { name: 'scan-line', size: 14 }), ' Bin Status')
        ),
        React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: openNewBin }, '+ Add Bin')
      )
    ),

    // Bins DataTable
    React.createElement(DataTable, {
      columns: [
        { key: 'bin_code', label: 'Bin Code', locked: true, style: { fontWeight: 600, color: C.accent, fontFamily: 'inherit', fontSize: 12 } },
        { key: 'label', label: 'Label' },
        { key: 'bin_type', label: 'Type', render: function(b) { return React.createElement(BinBadge, { type: 'bin_type', value: b.bin_type }); } },
        { key: 'status', label: 'Status', render: function(b) { return React.createElement(BinBadge, { type: 'status', value: b.status }); } },
        { key: 'zone', label: 'Zone' },
        { key: 'dimensions', label: 'Dimensions', noFilter: true, render: function(b) {
          return (b.length && b.width && b.height) ? b.length + ' x ' + b.width + ' x ' + b.height + '"' : '\u2014';
        }},
        { key: 'fill_pct', label: 'Fill %', render: function(b) { return React.createElement(FillBar, { pct: b.fill_pct }); } },
        { key: 'product_count', label: 'Products' },
        { key: 'total_units', label: 'Units' },
        { key: 'max_weight', label: 'Max Wt (lbs)', defaultVisible: false },
        { key: 'notes', label: 'Notes', defaultVisible: false },
        { key: '_actions', label: 'Actions', locked: true, noFilter: true, stopClick: true, render: function(b) {
          return React.createElement('div', { style: { display: 'flex', gap: 4 } },
            React.createElement(Btn, { size: 'sm', onClick: function() { openInventory(b); } }, 'Contents'),
            React.createElement(Btn, { size: 'sm', onClick: function() { printBinLabel(b.bin_code); } }, 'Label'),
            React.createElement('button', {
              onClick: function() { deleteBin(b); },
              style: { background: 'none', border: 'none', color: C.textDim, cursor: 'pointer', fontSize: 14 }
            }, '\uD83D\uDDD1')
          );
        }}
      ],
      data: filteredBins, storageKey: 'warehouse-bins',
      rowHref: function(b) { return '/warehouse/' + warehouseId + '?bin=' + b.id; },
      onRowClick: function(b) { openEditBin(b); },
      emptyTitle: 'No bins', emptySub: 'Add bins to this warehouse'
    }),

    // ─── Bin Add/Edit Modal ───
    React.createElement(Modal, { open: binModalOpen, onClose: function() { setBinModalOpen(false); }, title: editingBin ? 'Edit Bin' : 'New Bin' },
      React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 } },
        React.createElement(Inp, { label: 'Bin Code', placeholder: 'e.g. A-01-01', ...BF('bin_code') }),
        React.createElement(Inp, { label: 'Label', placeholder: 'Descriptive name', ...BF('label') }),
        React.createElement(Sel, { label: 'Type', options: binTypeOpts, value: binForm.bin_type || 'shelf', onChange: function(e) { setBinForm(Object.assign({}, binForm, { bin_type: e.target.value })); } }),
        React.createElement(Sel, { label: 'Status', options: binStatusOpts, value: binForm.status || 'active', onChange: function(e) { setBinForm(Object.assign({}, binForm, { status: e.target.value })); } }),
        React.createElement(Inp, { label: 'Length (in)', type: 'number', ...BF('length') }),
        React.createElement(Inp, { label: 'Width (in)', type: 'number', ...BF('width') }),
        React.createElement(Inp, { label: 'Height (in)', type: 'number', ...BF('height') }),
        React.createElement(Inp, { label: 'Max Weight (lbs)', type: 'number', ...BF('max_weight') }),
        React.createElement(Inp, { label: 'Zone', placeholder: 'e.g. Zone A', ...BF('zone') }),
        React.createElement('div', { style: { gridColumn: 'span 2' } }, React.createElement(Inp, { label: 'Notes', ...BF('notes') }))
      ),
      React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 } },
        React.createElement(Btn, { onClick: function() { setBinModalOpen(false); } }, 'Cancel'),
        React.createElement(Btn, { variant: 'primary', onClick: saveBin }, React.createElement(LIcon, { name: 'check', size: 14 }), editingBin ? ' Update' : ' Create')
      )
    ),

    // ─── Inventory Modal (read-only) ───
    React.createElement(Modal, { open: invModalOpen, onClose: function() { setInvModalOpen(false); }, title: invBin ? invBin.bin_code : 'Bin', width: 900 },
      // Tabs
      React.createElement('div', { style: { display: 'flex', gap: 0, borderBottom: '1px solid ' + C.border, marginBottom: 12 } },
        [{ key: 'contents', label: 'Contents', icon: 'package' }, { key: 'activity', label: 'Activity Log', icon: 'activity' }].map(function(t) {
          var active = invTab === t.key;
          var ivHref = (invBin && invBin.id) ? ('/warehouse/' + warehouseId + '?invbin=' + invBin.id + (t.key !== 'contents' ? '&invtab=' + t.key : '')) : undefined;
          return React.createElement('a', { key: t.key, href: ivHref, onClick: function(e) { if (ivHref && (e.ctrlKey || e.metaKey || e.shiftKey)) return; e.preventDefault(); setInvTab(t.key); },
            style: { padding: '8px 16px', border: 'none', borderBottom: active ? '2px solid ' + C.accent : '2px solid transparent', background: 'none', color: active ? C.accent : C.textMuted, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6, textDecoration: 'none' }
          }, React.createElement(LIcon, { name: t.icon, size: 14 }), t.label);
        })
      ),
      // Contents tab
      invTab === 'contents' && (function() {
        var isStaging = invBin && invBin.bin_type === 'staging';
        var downloadBinXls = function() {
          if (!inventory.length || !invBin) return;
          var headers = ['SKU', 'Product'];
          if (isStaging) headers.push('PO#');
          headers.push('Qty');
          var rows = inventory.map(function(item) {
            var row = [item.sku, item.vendor_title];
            if (isStaging) row.push(item.po_number || '');
            row.push(item.quantity);
            return row;
          });
          var escape = escapeCSV; // shared cell rule, formula-safe (review 2026-09-28, medium #31)
          var csv = headers.map(escape).join(',') + '\n' + rows.map(function(r) { return r.map(escape).join(','); }).join('\n');
          var blob = new Blob([csv], { type: 'text/csv' });
          var url = URL.createObjectURL(blob);
          var a = document.createElement('a'); a.href = url; a.download = 'Bin_' + invBin.bin_code + '_Contents.csv'; a.click();
          URL.revokeObjectURL(url);
        };
        return inventory.length > 0
          ? React.createElement('div', null,
              React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', marginBottom: 8 } },
                React.createElement(Btn, { size: 'sm', onClick: downloadBinXls }, React.createElement(LIcon, { name: 'download', size: 14 }), ' Export CSV')
              ),
              React.createElement('div', { style: { overflow: 'auto', maxHeight: 'calc(calc(100vh * var(--invzoom, 1)) - 300px)' } },
                React.createElement('table', { style: { width: '100%', fontSize: 13, tableLayout: 'auto' } },
                  React.createElement('thead', null,
                    React.createElement('tr', { style: { borderBottom: '1px solid ' + C.border } },
                      React.createElement(TH, null, 'SKU'),
                      React.createElement(TH, null, 'Product'),
                      isStaging ? React.createElement(TH, null, 'PO#') : null,
                      React.createElement(TH, null, 'Qty')
                    )
                  ),
                  React.createElement('tbody', null,
                    inventory.map(function(item, idx) {
                      return React.createElement('tr', { key: idx, style: { borderBottom: '1px solid ' + C.border + '22' } },
                        React.createElement(TD, { style: { fontFamily: 'inherit', fontSize: 12, fontWeight: 600, color: C.accent } }, item.sku),
                        React.createElement(TD, { style: { color: C.text, wordBreak: 'break-word' } }, item.vendor_title),
                        isStaging ? React.createElement(TD, { style: { fontFamily: 'inherit', fontSize: 12 } }, item.po_number || '\u2014') : null,
                        React.createElement(TD, { style: { fontWeight: 600 } }, item.quantity)
                      );
                    }),
                    React.createElement('tr', { style: { borderTop: '2px solid ' + C.border } },
                      React.createElement(TD, { style: { fontWeight: 600, color: C.textMuted, fontSize: 11, textTransform: 'uppercase' } }, 'Total'),
                      React.createElement(TD, null),
                      isStaging ? React.createElement(TD, null) : null,
                      React.createElement(TD, { style: { fontWeight: 700, color: C.text } }, inventory.reduce(function(s, i) { return s + (i.quantity || 0); }, 0))
                    )
                  )
                )
              )
            )
          : React.createElement('div', { style: { padding: '24px 0', textAlign: 'center', color: C.textDim, fontSize: 13 } }, 'No products in this bin');
      })(),
      // Activity log tab
      invTab === 'activity' && (function() {
        var actionColors = { checkin: C.accent, receive: C.success, undo_receive: C.warning, fba_pack: C.info || C.accent, fba_unpack: C.warning, manual_adjust: C.textMuted, manual_remove: C.danger, cleared: C.textDim, missing: C.warning, missing_undo: C.accent, zentail_sync: '#8b5cf6' };
        var actionLabels = { checkin: 'Check-in', receive: 'Received', undo_receive: 'Undo Receive', fba_pack: 'FBA Pack', fba_unpack: 'FBA Unpack', manual_adjust: 'Manual Adjust', manual_remove: 'Removed', cleared: 'Cleared', missing: 'Marked Missing', missing_undo: 'Missing Undone', zentail_sync: 'Zentail Sync' };
        return binActivity.length > 0
          ? React.createElement('div', { style: { overflow: 'auto', maxHeight: 'calc(calc(100vh * var(--invzoom, 1)) - 300px)' } },
              React.createElement('table', { style: { width: '100%', fontSize: 12, tableLayout: 'auto' } },
                React.createElement('thead', null,
                  React.createElement('tr', { style: { borderBottom: '1px solid ' + C.border } },
                    React.createElement(TH, null, 'Date'),
                    React.createElement(TH, null, 'User'),
                    React.createElement(TH, null, 'Action'),
                    React.createElement(TH, null, 'SKU'),
                    React.createElement(TH, null, 'Change'),
                    React.createElement(TH, null, 'Details')
                  )
                ),
                React.createElement('tbody', null,
                  binActivity.map(function(a) {
                    var ts = new Date(a.created_at);
                    var dateStr = ts.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' + ts.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                    var meta = a.metadata || {};
                    var changeStr = '';
                    if (a.qty_change != null) changeStr = (a.qty_change > 0 ? '+' : '') + a.qty_change;
                    var detailParts = [];
                    if (meta.po_number) detailParts.push('PO ' + meta.po_number);
                    if (meta.fba_plan_id) detailParts.push('FBA #' + meta.fba_plan_id);
                    if (a.qty_before != null && a.qty_after != null) detailParts.push(a.qty_before + ' \u2192 ' + a.qty_after);
                    return React.createElement('tr', { key: a.id, style: { borderBottom: '1px solid ' + C.border + '22' } },
                      React.createElement(TD, { style: { whiteSpace: 'nowrap', fontSize: 11, color: C.textMuted } }, dateStr),
                      React.createElement(TD, { style: { fontSize: 11 } }, a.username || 'system'),
                      React.createElement(TD, null, React.createElement('span', { style: { padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: (actionColors[a.action] || C.textDim) + '22', color: actionColors[a.action] || C.textDim } }, actionLabels[a.action] || a.action)),
                      React.createElement(TD, { style: { fontFamily: 'inherit', fontSize: 11, fontWeight: 600, color: C.accent } }, a.sku || '\u2014'),
                      React.createElement(TD, { style: { fontWeight: 600, fontFamily: 'inherit', fontSize: 12, color: a.qty_change > 0 ? C.success : a.qty_change < 0 ? C.danger : C.text } }, changeStr),
                      React.createElement(TD, { style: { fontSize: 11, color: C.textMuted } }, detailParts.join(' \u2022 '))
                    );
                  })
                )
              )
            )
          : React.createElement('div', { style: { padding: '24px 0', textAlign: 'center', color: C.textDim, fontSize: 13 } }, 'No activity recorded for this bin yet');
      })()
    )
  );
}

// ─── PRODUCTS PAGE ───
function ProductsPage({ showToast, editProductId, onNavigate, returnTo, returnOpts, initialProductTab }) {
  const [products, setProducts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('active');
  const [totalProducts, setTotalProducts] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [productTab, setProductTab] = useState('details');
  const editProductIdHandled = useRef(null);
  const searchTimerRef = useRef(null);
  const fetchGenRef = useRef(0);
  const lastFetchParamsRef = useRef(null);
  // Full-product load state for the editor (sweep 9-27 H3): the list row lacks description,
  // bullets, package dims, etc. Save stays disabled until the full GET lands; a failed GET
  // shows an error with Retry instead of an editable half-empty form.
  const [editLoad, setEditLoad] = useState('ready'); // 'ready' | 'loading' | 'error'
  const [editLoadError, setEditLoadError] = useState('');
  const editLoadGenRef = useRef(0);
  var loadFullProduct = function(id) {
    var gen = ++editLoadGenRef.current;
    setEditLoad('loading'); setEditLoadError('');
    return api('/products/' + id).then(function(full) {
      if (gen !== editLoadGenRef.current) return null; // a newer open/close superseded this load
      setForm(full); setEditing(full); setEditLoad('ready');
      return full;
    }).catch(function(e) {
      if (gen !== editLoadGenRef.current) return null;
      console.error('[product-load]', e);
      setEditLoadError((e && e.message) || 'Could not load this product');
      setEditLoad('error');
      return null;
    });
  };

  // Load vendors and warehouses on mount (editor pick-lists). Per-call catch: a role
  // without the list permission degrades to empty dropdowns, not an unhandled alert.
  useEffect(function() {
    Promise.all([
      api('/vendors?active=true').catch(function(e) { console.error('[silent-catch]', e); return []; }),
      api('/warehouses?active=true').catch(function(e) { console.error('[silent-catch]', e); return []; }),
    ]).then(function(r) { setVendors(r[0]); setWarehouses(r[1]); });
  }, []);

  // Deep-link to edit a specific product
  useEffect(function() {
    if (editProductId && editProductIdHandled.current !== editProductId) {
      editProductIdHandled.current = editProductId;
      var dlGen = ++editLoadGenRef.current;
      api('/products/' + editProductId).then(function(full) {
        if (dlGen !== editLoadGenRef.current) return;
        setForm(full); setEditing(full); setEditLoad('ready'); setProductTab('details'); setModalOpen(true);
        if (full.sku) history.replaceState(history.state, '', '/catalog/' + encodeURIComponent(full.sku));
      }).catch(function(e) {
        console.error('[product-load]', e);
        if (dlGen === editLoadGenRef.current) showToast('Could not load product ' + editProductId + ': ' + ((e && e.message) || 'request failed'), 'error');
      });
    }
  }, [editProductId]);

  // Server-side fetch handler for DataTable
  var handleServerFetch = function(params) {
    lastFetchParamsRef.current = params;
    fetchGenRef.current++;
    var myGen = fetchGenRef.current;
    var qs = 'page=' + params.page + '&limit=' + params.pageSize;
    if (params.sortCol) qs += '&sort=' + params.sortCol + '&sort_dir=' + params.sortDir;
    if (params.search) qs += '&search=' + encodeURIComponent(params.search);
    if (params.filters && Object.keys(params.filters).length > 0) qs += '&filters=' + encodeURIComponent(JSON.stringify(params.filters));
    if (activeFilter === 'active') qs += '&active=true';
    else if (activeFilter === 'inactive') qs += '&active=false';
    else qs += '&active=all';
    api('/products?' + qs).then(function(r) {
      if (myGen === fetchGenRef.current) { setProducts(r.rows); setTotalProducts(r.total); }
    }).catch(function(e) { console.error('[silent-catch]', e); });
  };

  var reloadProducts = function() {
    if (lastFetchParamsRef.current) handleServerFetch(lastFetchParamsRef.current);
  };

  // Re-fetch when active filter changes
  useEffect(function() { reloadProducts(); }, [activeFilter]);

  var handleSearch = function(val) {
    setSearch(val);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(function() { setDebouncedSearch(val); }, 300);
  };

  const openNew = () => { editLoadGenRef.current++; setEditLoad('ready'); setForm({ case_qty: 1 }); setEditing(null); setProductTab('details'); setModalOpen(true); };
  const openEdit = (p) => {
    setForm(p); setEditing(p); setProductTab('details'); setModalOpen(true);
    if (p.sku) history.replaceState(history.state, '', '/catalog/' + encodeURIComponent(p.sku));
    return loadFullProduct(p.id);
  };

  const save = async () => {
    // Never PUT the light list row: it would blank every field the list does not carry.
    if (editing && editLoad !== 'ready') { showToast(editLoad === 'error' ? 'Product did not load. Retry before saving.' : 'Product is still loading. Try again in a moment.', 'error'); return; }
    try {
      if (editing) { await api('/products/' + editing.id, { method: 'PUT', body: JSON.stringify(form) }); showToast('Product updated'); reloadProducts(); }
      else { await api('/products', { method: 'POST', body: JSON.stringify(form) }); showToast('Product created'); setModalOpen(false); reloadProducts(); }
    } catch (e) { showToast(e.message, 'error'); }
  };

  const F = (key) => ({ value: form[key] ?? '', onChange: e => setForm({ ...form, [key]: e.target.value }) });

  return React.createElement('div', null,
    React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 } },
      React.createElement('div', null,
        React.createElement('h2', { style: { margin: 0, fontSize: 20, fontWeight: 600, color: C.text } }, 'Products'),
        React.createElement('p', { style: { margin: '4px 0 0', fontSize: 13, color: C.textMuted } }, totalProducts + ' products')
      )
    ),
    // Filter pills share a row with + Add Product (right-aligned) — 9-05
    React.createElement('div', { style: { display: 'flex', gap: 6, marginBottom: 12, alignItems: 'center' } },
      ['all', 'active', 'inactive'].map(function(f) {
        var isActive = activeFilter === f;
        var label = f === 'all' ? 'All' : f === 'active' ? 'Active' : 'Inactive';
        return React.createElement('button', { key: f, onClick: function() { setActiveFilter(f); },
          className: isActive ? '' : 'filter-pill',
          style: { padding: '5px 14px', borderRadius: 6, border: '1px solid transparent', cursor: 'pointer', fontSize: 12, fontWeight: 600,
            background: isActive ? C.accent : C.surface, color: isActive ? '#fff' : C.textMuted, transition: 'all 0.2s' }
        }, label);
      }),
      React.createElement('div', { style: { flex: 1 } }),
      React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: openNew }, '+ Add Product')
    ),
    React.createElement('div', { style: { marginBottom: 16 } }, React.createElement(SearchInput, { placeholder: 'Search SKU, name, FNSKU, vendor SKU, ASIN, UPC, barcode, Walmart item ID...', value: search, onChange: function(e) { handleSearch(e.target.value); },
      style: { width: '100%', background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '10px 14px', borderRadius: 8, fontSize: 13, outline: 'none' } })),
    React.createElement(DataTable, {
      columns: [
        { key: 'sku', label: 'SKU', copyable: true, style: { fontWeight: 600, color: C.accent, fontFamily: 'inherit', fontSize: 12 },
          render: function(p) { var bp = function(bg, fg) { return { fontSize: 9, fontWeight: 600, padding: '1px 5px', borderRadius: 6, background: bg, color: fg, marginLeft: 6, verticalAlign: 'middle', letterSpacing: '0.03em', textTransform: 'uppercase' }; }; return React.createElement('span', null, p.sku, p.alias_of ? React.createElement('span', { style: bp('#FF990018', '#FF9900'), title: 'Alias of ' + p.alias_of }, 'ALIAS CHILD') : null, p.is_alias_parent ? React.createElement('span', { style: bp('#6C63FF18', '#6C63FF'), title: 'Has alias SKUs pointing to this product' }, 'ALIAS PARENT') : null, p.kit_parent ? React.createElement('span', { style: bp('#00B8D918', '#00B8D9'), title: 'Kit child of ' + p.kit_parent + (p.kit_qty ? ' (qty: ' + p.kit_qty + ')' : '') }, 'KIT CHILD' + (p.kit_qty ? ' x' + p.kit_qty : '')) : null, p.is_kit_parent ? React.createElement('span', { style: bp('#00875A18', '#00875A'), title: 'Has kit child SKUs' }, 'KIT PARENT') : null, p.is_bundle_parent ? React.createElement('span', { style: bp('#E0740018', '#E07400'), title: 'Bundle with multiple components' }, 'BUNDLE') : null, TopRevChip(p), TransparencyChip(p)); } },
        { key: 'vendor_title', label: 'Product', style: { color: C.text } },
        { key: 'unit_cost', label: 'Cost', render: function(p) { return '$' + Number(p.unit_cost || 0).toFixed(2); } },
        { key: 'fnsku', label: 'FNSKU', copyable: true },
        { key: 'asin', label: 'ASIN', copyable: true },
        { key: 'barcode', label: 'Barcode', defaultVisible: false, copyable: true },
        { key: 'vendor', label: 'Vendor', filterOptions: vendors.map(function(v) { return v.vendor_name; }).filter(Boolean), render: function(p) { return p.v_name || p.vendor_name || ''; } },
        { key: 'vendor_sku', label: 'Vendor SKU', copyable: true },
        { key: 'case_qty', label: 'Case Qty' },
        { key: 'max_po_qty', label: 'Max/PO', defaultVisible: false, render: function(p) { return p.max_po_qty ? String(p.max_po_qty) : ''; } },
        { key: 'length', label: 'Length', defaultVisible: false },
        { key: 'width', label: 'Width', defaultVisible: false },
        { key: 'height', label: 'Height', defaultVisible: false },
        { key: 'weight', label: 'Weight', defaultVisible: false },
        { key: 'top_revenue_rank', label: 'Rev Rank', defaultVisible: false, render: function(p) { return p.top_revenue_rank ? '#' + p.top_revenue_rank : ''; } },
        { key: 'total_qty', label: 'Total Qty', render: function(p) { return Number(p.total_qty || 0).toLocaleString(); } },
        { key: 'warehouse', label: 'Warehouse', filterOptions: warehouses.map(function(w) { return w.warehouse_id; }).filter(Boolean), render: function(p) { return p.wh_code || p.default_warehouse || ''; } },
      ],
      data: products, storageKey: 'products',
      defaultSort: 'sku', defaultSortDir: 'asc',
      rowHref: function(p) { return '/catalog/' + p.id; },
      onRowClick: function(p) { openEdit(p); },
      emptyTitle: 'No products', emptySub: 'Add or import',
      onServerFetch: handleServerFetch, serverTotal: totalProducts, serverSearch: debouncedSearch
    }),
    modalOpen && React.createElement(ProductEditorFullScreen, {
      product: editing, editing: editing, form: form, setForm: setForm,
      vendors: vendors, warehouses: warehouses, products: products, onSave: save,
      loadState: editing ? editLoad : 'ready', loadError: editLoadError,
      onRetryLoad: function() { if (editing && editing.id) loadFullProduct(editing.id); },
      onStatusChange: function(p) { setEditing(function(prev) { return prev && prev.id === p.id ? Object.assign({}, prev, { is_active: p.is_active }) : prev; }); reloadProducts(); },
      onClose: function() { editLoadGenRef.current++; setModalOpen(false); if (returnTo && onNavigate && editProductId) { onNavigate(returnTo, (returnOpts && returnOpts.returnArg) || null, returnOpts || null); } else { history.replaceState(history.state, '', '/catalog'); } }, showToast: showToast,
      initialTab: initialProductTab
    })
  );
}

// ─── USERS & ROLES WRAPPER ───
function UsersAndRolesTab({ showToast, currentUser }) {
  var _st = useState('users'), subTab = _st[0], setSubTab = _st[1];
  // Deep link: /settings/users?subtab=users|roles selects the sub-tab on load (new-tab target).
  useEffect(function() {
    try {
      var qs = new URLSearchParams(window.location.search).get('subtab');
      if (qs === 'users' || qs === 'roles') { setSubTab(qs); history.replaceState(history.state, '', '/settings/users'); }
    } catch (e) {}
  }, []);
  var tabStyle = function(key) {
    var active = subTab === key;
    return { padding: '8px 20px', cursor: 'pointer', fontSize: 13, fontWeight: active ? 600 : 400, color: active ? C.accent : C.textMuted, borderBottom: active ? '2px solid ' + C.accent : '2px solid transparent', transition: 'all 0.15s', textDecoration: 'none' };
  };
  var tabLink = function(key, label) {
    return React.createElement('a', { href: '/settings/users?subtab=' + key, onClick: function(e) { if (e.ctrlKey || e.metaKey || e.shiftKey) return; e.preventDefault(); setSubTab(key); }, style: tabStyle(key) }, label);
  };
  return React.createElement('div', { style: { marginTop: 16 } },
    React.createElement('div', { style: { display: 'flex', borderBottom: '1px solid ' + C.border, marginBottom: 16 } },
      tabLink('users', 'Users'),
      tabLink('roles', 'Roles & Permissions')
    ),
    subTab === 'users' && React.createElement(Card, null, React.createElement(UserManagement, { showToast: showToast })),
    subTab === 'roles' && React.createElement(RolesPermissionsTab, { showToast: showToast, currentUser: currentUser })
  );
}
window.UsersAndRolesTab = UsersAndRolesTab;

// ─── ROLES & PERMISSIONS TAB ───
var AREA_LABELS = { dashboard: 'Dashboard', products: 'Products / Catalog', purchase_orders: 'Purchase Orders', orders: 'Orders (Marketplace)', vendors: 'Vendors', warehouses: 'Warehouses', receiving: 'Receiving', settings: 'Settings', email_templates: 'Email Templates', cs_tickets: 'CS Tickets (Legacy)', customer_service: 'Customer Service (CS App)', fba_shipments: 'FBA Shipments', fba_reconciliation: 'FBA Reconciliation', wfs_shipments: 'WFS Shipments', meli_shipments: 'MELI Shipments', meli_research: 'MELI Product Research', product_research: 'Product Research', analysis: 'Analysis', integrations: 'Integrations', reports: 'Reports', import_export: 'Import / Export', shipping: 'Shipping (FBM)', accounting: 'Accounting', advertising: 'Advertising', agent_office: 'The Office (AI Agents)', chat: 'Ask AI' };
// Must list every server SYSTEM_AREAS entry (routes/auth.js) — an area missing here could not be
// granted from Settings, and saving a role wrote it back as no-access (9-27 sweep M7).
var AREA_ORDER = ['dashboard','products','purchase_orders','orders','vendors','warehouses','receiving','settings','email_templates','cs_tickets','customer_service','fba_shipments','fba_reconciliation','wfs_shipments','meli_shipments','meli_research','product_research','analysis','integrations','reports','import_export','shipping','accounting','advertising','agent_office','chat'];

function RolesPermissionsTab({ showToast }) {
  var _s = useState, _e = useEffect, _cb = useCallback;
  var _roles = _s([]), roles = _roles[0], setRoles = _roles[1];
  var _sel = _s(null), selectedRoleId = _sel[0], setSelectedRoleId = _sel[1];
  var _modal = _s(false), createModal = _modal[0], setCreateModal = _modal[1];
  var _newRole = _s({ name: '', display_name: '' }), newRole = _newRole[0], setNewRole = _newRole[1];
  var _saving = _s(false), saving = _saving[0], setSaving = _saving[1];

  var load = _cb(async function() {
    try { var r = await api('/auth/roles'); setRoles(r); if (!selectedRoleId && r.length) setSelectedRoleId(r[0].id); } catch(e) { showToast(e.message, 'error'); }
  }, []);
  _e(function() { load(); }, [load]);

  var selectedRole = roles.find(function(r) { return r.id === selectedRoleId; });
  var isAdmin = selectedRole && selectedRole.name === 'admin';

  var togglePerm = async function(area, level) {
    if (!selectedRole || isAdmin) return;
    var perms = Object.assign({}, selectedRole.permissions);
    if (!perms[area]) perms[area] = { read: false, write: false, delete: false };
    perms[area][level] = !perms[area][level];
    // Cascade: write requires read, delete requires write+read
    if (level === 'write' && perms[area].write) perms[area].read = true;
    if (level === 'delete' && perms[area].delete) { perms[area].write = true; perms[area].read = true; }
    if (level === 'read' && !perms[area].read) { perms[area].write = false; perms[area].delete = false; }
    if (level === 'write' && !perms[area].write) perms[area].delete = false;
    // Update local state immediately
    setRoles(roles.map(function(r) { return r.id === selectedRoleId ? Object.assign({}, r, { permissions: perms }) : r; }));
    // Save to server
    try {
      await api('/auth/roles/' + selectedRoleId + '/permissions', { method: 'PUT', body: JSON.stringify({ permissions: perms }) });
    } catch(e) { showToast(e.message, 'error'); load(); }
  };

  var createRole = async function() {
    if (!newRole.name) { showToast('Role name required', 'error'); return; }
    setSaving(true);
    try {
      var r = await api('/auth/roles', { method: 'POST', body: JSON.stringify(newRole) });
      setCreateModal(false); setNewRole({ name: '', display_name: '' }); setSelectedRoleId(r.id); load();
      showToast('Role created');
    } catch(e) { showToast(e.message, 'error'); }
    setSaving(false);
  };

  var deleteRole = async function() {
    if (!selectedRole || selectedRole.is_system) return;
    if (!(await appConfirm('Delete role "' + (selectedRole.display_name || selectedRole.name) + '"?', { danger: true, okLabel: 'Delete' }))) return;
    try {
      await api('/auth/roles/' + selectedRoleId, { method: 'DELETE' });
      setSelectedRoleId(roles[0] ? roles[0].id : null); load();
      showToast('Role deleted');
    } catch(e) { showToast(e.message, 'error'); }
  };

  var chk = function(area, level) {
    var p = selectedRole && selectedRole.permissions && selectedRole.permissions[area];
    var checked = p ? !!p[level] : false;
    return React.createElement('td', { key: level, style: { textAlign: 'center', padding: '8px 12px' } },
      React.createElement('input', {
        type: 'checkbox', checked: checked, disabled: isAdmin,
        onChange: function() { togglePerm(area, level); },
        style: { width: 16, height: 16, accentColor: C.accent, cursor: isAdmin ? 'default' : 'pointer' }
      })
    );
  };

  return React.createElement('div', { style: { marginTop: 16 } },
    React.createElement('div', { style: { display: 'flex', gap: 20 } },
      // Left: Role list
      React.createElement(Card, { style: { width: 220, flexShrink: 0, padding: 0 } },
        React.createElement('div', { style: { padding: '12px 16px', borderBottom: '1px solid ' + C.border, display: 'flex', justifyContent: 'space-between', alignItems: 'center' } },
          React.createElement('span', { style: { fontSize: 12, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase' } }, 'Roles'),
          React.createElement(Btn, { size: 'sm', variant: 'primary', onClick: function() { setCreateModal(true); } }, '+ New')
        ),
        roles.map(function(r) {
          var active = r.id === selectedRoleId;
          return React.createElement('div', {
            key: r.id, onClick: function() { setSelectedRoleId(r.id); },
            style: { padding: '10px 16px', cursor: 'pointer', background: active ? C.accentSoft : 'transparent', borderLeft: active ? '3px solid ' + C.accent : '3px solid transparent', display: 'flex', alignItems: 'center', gap: 8, transition: 'background 0.15s' },
            onMouseEnter: function(e) { if (!active) e.currentTarget.style.background = C.surfaceHover; },
            onMouseLeave: function(e) { if (!active) e.currentTarget.style.background = 'transparent'; }
          },
            r.is_system && React.createElement(LIcon, { name: 'lock', size: 12, style: { color: C.textMuted, flexShrink: 0 } }),
            React.createElement('span', { style: { fontSize: 13, fontWeight: active ? 600 : 400, color: active ? C.accent : C.text } }, r.display_name || r.name)
          );
        })
      ),
      // Right: Permission matrix
      React.createElement(Card, { style: { flex: 1, padding: 0 } },
        selectedRole ? React.createElement('div', null,
          React.createElement('div', { style: { padding: '12px 16px', borderBottom: '1px solid ' + C.border, display: 'flex', justifyContent: 'space-between', alignItems: 'center' } },
            React.createElement('div', null,
              React.createElement('span', { style: { fontSize: 15, fontWeight: 600, color: C.text } }, selectedRole.display_name || selectedRole.name),
              selectedRole.is_system && React.createElement('span', { style: { marginLeft: 8, fontSize: 10, color: C.textMuted, background: C.bg, padding: '2px 6px', borderRadius: 3 } }, 'SYSTEM'),
              isAdmin && React.createElement('span', { style: { marginLeft: 8, fontSize: 10, color: C.warning, background: C.warningSoft, padding: '2px 6px', borderRadius: 3 } }, 'FULL ACCESS')
            ),
            !selectedRole.is_system && React.createElement(Btn, { size: 'sm', variant: 'danger', onClick: deleteRole }, 'Delete Role')
          ),
          React.createElement('table', { style: { width: '100%', fontSize: 13, borderCollapse: 'collapse', fontFamily: 'inherit' } },
            React.createElement('thead', null,
              React.createElement('tr', { style: { borderBottom: '1px solid ' + C.border } },
                React.createElement('th', { style: { textAlign: 'left', padding: '10px 16px', fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase' } }, 'Area'),
                React.createElement('th', { style: { textAlign: 'center', padding: '10px 12px', fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', width: 80 } }, 'Read'),
                React.createElement('th', { style: { textAlign: 'center', padding: '10px 12px', fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', width: 80 } }, 'Write'),
                React.createElement('th', { style: { textAlign: 'center', padding: '10px 12px', fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', width: 80 } }, 'Delete')
              )
            ),
            React.createElement('tbody', null,
              AREA_ORDER.map(function(area, i) {
                return React.createElement('tr', { key: area, style: { borderBottom: '1px solid ' + C.border + '44', background: i % 2 === 0 ? 'transparent' : C.bg + '33' } },
                  React.createElement('td', { style: { padding: '10px 16px', color: C.text, fontWeight: 500 } }, AREA_LABELS[area]),
                  chk(area, 'read'), chk(area, 'write'), chk(area, 'delete')
                );
              })
            )
          )
        ) : React.createElement('div', { style: { padding: 40, textAlign: 'center', color: C.textMuted } }, 'Select a role to edit permissions')
      )
    ),
    // Create role modal
    React.createElement(Modal, { open: createModal, onClose: function() { setCreateModal(false); }, title: 'Create Custom Role', width: 400 },
      React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 12 } },
        React.createElement(Inp, { label: 'Role Name', value: newRole.name, onChange: function(e) { setNewRole(Object.assign({}, newRole, { name: e.target.value })); }, placeholder: 'e.g. purchasing_agent' }),
        React.createElement(Inp, { label: 'Display Name', value: newRole.display_name, onChange: function(e) { setNewRole(Object.assign({}, newRole, { display_name: e.target.value })); }, placeholder: 'e.g. Purchasing Agent' }),
        React.createElement('p', { style: { fontSize: 11, color: C.textMuted, margin: 0 } }, 'Permissions can be configured after creation.')
      ),
      React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 } },
        React.createElement(Btn, { onClick: function() { setCreateModal(false); } }, 'Cancel'),
        React.createElement(Btn, { variant: 'primary', onClick: createRole, disabled: saving }, saving ? 'Creating...' : 'Create Role')
      )
    )
  );
}
window.RolesPermissionsTab = RolesPermissionsTab;

// ─── SETTINGS PAGE ───
function SettingsPage({ showToast, onLogoChange, activeSettingsTab, onNavigate, currentUser }) {
  const [settings, setSettings] = useState({});
  const [logoUrl, setLogoUrl] = useState(null);
  const [iconLogoUrl, setIconLogoUrl] = useState(null);
  const logoRef = useRef(null);
  const iconLogoRef = useRef(null);
  var activeTab = activeSettingsTab || 'company';

  // PO Email Templates state
  var [poTemplates, setPoTemplates] = useState([]);
  var [editPoTpl, setEditPoTpl] = useState(null);
  var [poTplForm, setPoTplForm] = useState({ name: '', subject: '', body: '' });
  var poTplBodyRef = useRef(null);
  var [poTplMode, setPoTplMode] = useState('visual'); // 'visual' or 'source'

  var loadPoTemplates = function() {
    api('/purchase-orders/meta/templates').then(setPoTemplates).catch(function(e) { console.error('[silent-catch]', e); });
  };
  useEffect(function() { if (activeTab === 'templates') loadPoTemplates(); }, [activeTab]);

  // Label Templates state
  var [labelTemplates, setLabelTemplates] = useState([]);
  var [editLabelTpl, setEditLabelTpl] = useState(null);
  var [labelTplCategory, setLabelTplCategory] = useState('fba');
  var [labelTplForm, setLabelTplForm] = useState({
    name: '', label_type: 'thermal', width_mm: '', height_mm: '', columns: 1, rows_per_sheet: 1,
    page_width_mm: '', page_height_mm: '', fields: ['fnsku', 'sku', 'title'], show_barcode: true, font_size: 12, is_default: false, category: 'fba'
  });
  var defaultLabelForm = { name: '', label_type: 'thermal', width_mm: '', height_mm: '', columns: 1, rows_per_sheet: 1, page_width_mm: '', page_height_mm: '', fields: ['fnsku', 'sku', 'title'], show_barcode: true, font_size: 12, is_default: false, category: 'fba' };

  var labelFieldOptions = [
    { value: 'title', label: 'Product Title' },
    { value: 'condition', label: 'Condition' },
    { value: 'sku', label: 'SKU / MSKU' },
    { value: 'expiry', label: 'Expiry Date' },
  ];
  // The sample code the preview draws: an FNSKU for FBA, an ML Code for MELI (Mercado Libre's
  // Full inventory id, 2026-09-29), a UPC for the rest.
  var labelPreviewCode = function(category) {
    if (category === 'fba') return 'X00ABCDEF1';
    if (category === 'meli') return 'CLLU14840';
    return '012345678901';
  };

  var labelPresets = [
    { name: '2" × 1" Thermal', width_mm: 50.8, height_mm: 25.4, label_type: 'thermal', columns: 1, rows_per_sheet: 1 },
    { name: '2.125" × 1" Thermal', width_mm: 53.98, height_mm: 25.4, label_type: 'thermal', columns: 1, rows_per_sheet: 1 },
    { name: '2.25" × 1.25" Thermal', width_mm: 57.15, height_mm: 31.75, label_type: 'thermal', columns: 1, rows_per_sheet: 1 },
    { name: '2.5" × 1" Thermal', width_mm: 63.5, height_mm: 25.4, label_type: 'thermal', columns: 1, rows_per_sheet: 1 },
    { name: '2.62" × 1" Thermal', width_mm: 66.55, height_mm: 25.4, label_type: 'thermal', columns: 1, rows_per_sheet: 1 },
    { name: '3" × 1" Thermal', width_mm: 76.2, height_mm: 25.4, label_type: 'thermal', columns: 1, rows_per_sheet: 1 },
    { name: '3" × 2" Thermal', width_mm: 76.2, height_mm: 50.8, label_type: 'thermal', columns: 1, rows_per_sheet: 1 },
    { name: '3.5" × 1.12" Thermal', width_mm: 88.9, height_mm: 28.45, label_type: 'thermal', columns: 1, rows_per_sheet: 1 },
    { name: '2.62" × 1" — 30 per Sheet', width_mm: 66.55, height_mm: 25.4, label_type: 'sheet', columns: 3, rows_per_sheet: 10, page_width_mm: 215.9, page_height_mm: 279.4 },
    { name: '27-up Labels (63.5mm × 29.6mm, A4)', width_mm: 63.5, height_mm: 29.6, label_type: 'sheet', columns: 3, rows_per_sheet: 9, page_width_mm: 210, page_height_mm: 297 },
    { name: '40-up Labels (52.5mm × 29.7mm, A4)', width_mm: 52.5, height_mm: 29.7, label_type: 'sheet', columns: 4, rows_per_sheet: 10, page_width_mm: 210, page_height_mm: 297 },
  ];

  var loadLabelTemplates = function() {
    api('/settings/label-templates').then(setLabelTemplates).catch(function(e) { console.error('[silent-catch]', e); });
  };
  useEffect(function() { if (activeTab === 'templates') loadLabelTemplates(); }, [activeTab]);

  var saveLabelTpl = async function() {
    if (!labelTplForm.name || !labelTplForm.width_mm || !labelTplForm.height_mm) { showToast('Name, width, and height are required', 'error'); return; }
    try {
      if (editLabelTpl) {
        await api('/settings/label-templates/' + editLabelTpl.id, { method: 'PUT', body: JSON.stringify(labelTplForm) });
      } else {
        await api('/settings/label-templates', { method: 'POST', body: JSON.stringify(labelTplForm) });
      }
      showToast(editLabelTpl ? 'Label template updated' : 'Label template created');
      setEditLabelTpl(null); setLabelTplForm(Object.assign({}, defaultLabelForm, { category: labelTplCategory }));
      loadLabelTemplates();
    } catch (e) { showToast(e.message || 'Failed to save', 'error'); }
  };

  var deleteLabelTpl = async function(id) {
    if (!(await appConfirm('Delete this label template?', { danger: true, okLabel: 'Delete' }))) return;
    try {
      await api('/settings/label-templates/' + id, { method: 'DELETE' });
      showToast('Label template deleted');
      if (editLabelTpl && editLabelTpl.id === id) { setEditLabelTpl(null); setLabelTplForm(Object.assign({}, defaultLabelForm, { category: labelTplCategory })); }
      loadLabelTemplates();
    } catch (e) { showToast(e.message || 'Failed to delete', 'error'); }
  };

  var toggleLabelField = function(field) {
    setLabelTplForm(function(f) {
      var fields = (f.fields || []).slice();
      var idx = fields.indexOf(field);
      if (idx >= 0) fields.splice(idx, 1);
      else fields.push(field);
      return Object.assign({}, f, { fields: fields });
    });
  };

  var applyPreset = function(preset) {
    setLabelTplForm(function(f) {
      return Object.assign({}, f, {
        name: f.name || preset.name,
        label_type: preset.label_type,
        width_mm: preset.width_mm,
        height_mm: preset.height_mm,
        columns: preset.columns,
        rows_per_sheet: preset.rows_per_sheet,
        page_width_mm: preset.page_width_mm || '',
        page_height_mm: preset.page_height_mm || ''
      });
    });
  };

  // ─── Case Label Templates state ───
  var [caseLabelTemplates, setCaseLabelTemplates] = useState([]);
  var [editCaseTpl, setEditCaseTpl] = useState(null);
  var [caseTplCategory, setCaseTplCategory] = useState('po');
  var defaultCaseForm = { name: '', label_type: 'thermal', width_mm: '', height_mm: '', columns: 1, rows_per_sheet: 1, page_width_mm: '', page_height_mm: '', fields: ['vendor', 'warehouse'], show_barcode: true, font_size: 12, is_default: false, category: 'po' };
  var [caseTplForm, setCaseTplForm] = useState(Object.assign({}, defaultCaseForm));

  // FBA case labels follow Amazon's fixed format — no custom field selection.
  // PO and WFS allow user-selected fields.
  var caseFieldOptionsByCategory = {
    po: [
      { value: 'vendor', label: 'Ship From (Vendor)' },
      { value: 'warehouse', label: 'Ship To (Warehouse)' },
    ],
    fba: null, // Amazon-mandated format: PDF417 with AMZN,PO:,FNSKU:,QTY:,EXP:
    wfs: [
      { value: 'po_number', label: 'PO Number' },
      { value: 'sku', label: 'SKU' },
      { value: 'title', label: 'Product Title' },
      { value: 'qty', label: 'Quantity' },
      { value: 'vendor', label: 'Vendor' },
      { value: 'warehouse', label: 'Warehouse' },
    ],
  };

  var casePresets = [
    { name: '4" × 6" Thermal', width_mm: 101.6, height_mm: 152.4, label_type: 'thermal', columns: 1, rows_per_sheet: 1 },
    { name: '4" × 4" Thermal', width_mm: 101.6, height_mm: 101.6, label_type: 'thermal', columns: 1, rows_per_sheet: 1 },
    { name: '4" × 3" Thermal', width_mm: 101.6, height_mm: 76.2, label_type: 'thermal', columns: 1, rows_per_sheet: 1 },
    { name: '3" × 2" Thermal', width_mm: 76.2, height_mm: 50.8, label_type: 'thermal', columns: 1, rows_per_sheet: 1 },
    { name: '4" × 6" — 2 per Sheet', width_mm: 101.6, height_mm: 152.4, label_type: 'sheet', columns: 2, rows_per_sheet: 1, page_width_mm: 215.9, page_height_mm: 279.4 },
  ];

  var loadCaseLabelTemplates = function() {
    api('/settings/case-label-templates').then(setCaseLabelTemplates).catch(function(e) { console.error('[silent-catch]', e); });
  };
  useEffect(function() { if (activeTab === 'templates') loadCaseLabelTemplates(); }, [activeTab]);

  var saveCaseTpl = async function() {
    if (!caseTplForm.name || !caseTplForm.width_mm || !caseTplForm.height_mm) { showToast('Name, width, and height are required', 'error'); return; }
    try {
      if (editCaseTpl) {
        await api('/settings/case-label-templates/' + editCaseTpl.id, { method: 'PUT', body: JSON.stringify(caseTplForm) });
      } else {
        await api('/settings/case-label-templates', { method: 'POST', body: JSON.stringify(caseTplForm) });
      }
      showToast(editCaseTpl ? 'Case label template updated' : 'Case label template created');
      setEditCaseTpl(null); setCaseTplForm(Object.assign({}, defaultCaseForm, { category: caseTplCategory }));
      loadCaseLabelTemplates();
    } catch (e) { showToast(e.message || 'Failed to save', 'error'); }
  };

  var deleteCaseTpl = async function(id) {
    if (!(await appConfirm('Delete this case label template?', { danger: true, okLabel: 'Delete' }))) return;
    try {
      await api('/settings/case-label-templates/' + id, { method: 'DELETE' });
      showToast('Case label template deleted');
      if (editCaseTpl && editCaseTpl.id === id) { setEditCaseTpl(null); setCaseTplForm(Object.assign({}, defaultCaseForm, { category: caseTplCategory })); }
      loadCaseLabelTemplates();
    } catch (e) { showToast(e.message || 'Failed to delete', 'error'); }
  };

  var toggleCaseField = function(field) {
    setCaseTplForm(function(f) {
      var fields = (f.fields || []).slice();
      var idx = fields.indexOf(field);
      if (idx >= 0) fields.splice(idx, 1);
      else fields.push(field);
      return Object.assign({}, f, { fields: fields });
    });
  };

  var applyCasePreset = function(preset) {
    setCaseTplForm(function(f) {
      return Object.assign({}, f, {
        name: f.name || preset.name,
        label_type: preset.label_type,
        width_mm: preset.width_mm,
        height_mm: preset.height_mm,
        columns: preset.columns,
        rows_per_sheet: preset.rows_per_sheet,
        page_width_mm: preset.page_width_mm || '',
        page_height_mm: preset.page_height_mm || ''
      });
    });
  };

  var savePoTpl = async function() {
    // Sync contentEditable body before saving
    var formToSave = Object.assign({}, poTplForm);
    if (poTplMode === 'visual' && poTplBodyRef.current) {
      formToSave.body = poTplBodyRef.current.innerHTML;
    }
    if (!formToSave.name || !formToSave.body) { showToast('Name and body are required', 'error'); return; }
    try {
      if (editPoTpl) {
        await api('/purchase-orders/meta/templates/' + editPoTpl.id, { method: 'PUT', body: JSON.stringify(formToSave) });
      } else {
        await api('/purchase-orders/meta/templates', { method: 'POST', body: JSON.stringify(formToSave) });
      }
      showToast(editPoTpl ? 'Template updated' : 'Template created');
      setEditPoTpl(null); setPoTplForm({ name: '', subject: '', body: '' });
      loadPoTemplates();
    } catch (e) { showToast(e.message || 'Failed to save', 'error'); }
  };

  var deletePoTpl = async function(id) {
    if (!(await appConfirm('Delete this template?', { danger: true, okLabel: 'Delete' }))) return;
    try {
      await api('/purchase-orders/meta/templates/' + id, { method: 'DELETE' });
      showToast('Template deleted');
      if (editPoTpl && editPoTpl.id === id) { setEditPoTpl(null); setPoTplForm({ name: '', subject: '', body: '' }); }
      loadPoTemplates();
    } catch (e) { showToast(e.message || 'Failed to delete', 'error'); }
  };

  const load = useCallback(async () => { setSettings(await api('/settings')); }, []);
  useEffect(() => { load(); }, [load]);

  // Load current logos
  useEffect(function() {
    fetch('/api/settings/logo', { credentials: 'include' }).then(function(r) {
      if (r.ok) setLogoUrl('/api/settings/logo?t=' + Date.now());
      else setLogoUrl(null);
    }).catch(function() { setLogoUrl(null); });
    fetch('/api/settings/logo-icon', { credentials: 'include' }).then(function(r) {
      if (r.ok) setIconLogoUrl('/api/settings/logo-icon?t=' + Date.now());
      else setIconLogoUrl(null);
    }).catch(function() { setIconLogoUrl(null); });
  }, []);

  const save = async () => {
    try { await api('/settings', { method: 'PUT', body: JSON.stringify(settings) }); showToast('Settings saved'); }
    catch (e) { showToast(e.message, 'error'); }
  };

  var handleLogoUpload = function(e) {
    var file = e.target.files[0];
    if (!file) return;
    var form = new FormData();
    form.append('logo', file);
    fetch('/api/settings/logo', { method: 'POST', body: form, credentials: 'include' }).then(function(r) {
      if (r.ok) {
        var newUrl = '/api/settings/logo?t=' + Date.now();
        setLogoUrl(newUrl);
        if (onLogoChange) onLogoChange(newUrl);
        showToast('Logo updated');
      } else { showToast('Upload failed', 'error'); }
    });
    e.target.value = '';
  };

  var handleLogoRemove = function() {
    fetch('/api/settings/logo', { method: 'DELETE', credentials: 'include' }).then(function() {
      setLogoUrl(null);
      if (onLogoChange) onLogoChange(null);
      showToast('Logo removed');
    });
  };

  var handleIconLogoUpload = function(e) {
    var file = e.target.files[0];
    if (!file) return;
    var form = new FormData();
    form.append('logo', file);
    fetch('/api/settings/logo-icon', { method: 'POST', body: form, credentials: 'include' }).then(function(r) {
      if (r.ok) {
        var newUrl = '/api/settings/logo-icon?t=' + Date.now();
        setIconLogoUrl(newUrl);
        showToast('Icon logo updated');
      } else { showToast('Upload failed', 'error'); }
    });
    e.target.value = '';
  };

  var handleIconLogoRemove = function() {
    fetch('/api/settings/logo-icon', { method: 'DELETE', credentials: 'include' }).then(function() {
      setIconLogoUrl(null);
      showToast('Icon logo removed');
    });
  };

  const F = (key) => ({ value: settings[key] ?? '', onChange: e => setSettings({ ...settings, [key]: e.target.value }) });

  // ─── PLACEHOLDER TAB ───
  var placeholderTab = function(title, description) {
    return React.createElement(Card, { style: { marginTop: 16 } },
      React.createElement('div', { style: { textAlign: 'center', padding: '48px 20px' } },
        React.createElement('div', { style: { fontSize: 40, marginBottom: 12, opacity: 0.4 } }, React.createElement(LIcon, { name: 'construction', size: 40 })),
        React.createElement('div', { style: { fontSize: 16, fontWeight: 600, color: C.text, marginBottom: 6 } }, title),
        React.createElement('div', { style: { fontSize: 13, color: C.textDim, maxWidth: 400, margin: '0 auto' } }, description)
      )
    );
  };

  // FBA Settings state
  var [fbaShipFromAddrs, setFbaShipFromAddrs] = useState([]);
  var [fbaEditAddr, setFbaEditAddr] = useState(null);
  var [fbaShowForm, setFbaShowForm] = useState(false);
  var [fbaAddrForm, setFbaAddrForm] = useState({ label: '', name: '', address_line1: '', city: '', state: '', postal_code: '', country_code: 'US', phone: '' });

  var loadFbaAddrs = function() {
    api('/fba-shipments/ship-from-addresses').then(setFbaShipFromAddrs).catch(function(e) { console.error('[silent-catch]', e); });
  };
  useEffect(function() { if (activeTab === 'fba') loadFbaAddrs(); }, [activeTab]);

  var saveFbaAddr = function() {
    if (!fbaAddrForm.label || !fbaAddrForm.address_line1) { showToast('Label and address are required', 'error'); return; }
    var method = fbaEditAddr ? 'PUT' : 'POST';
    var url = fbaEditAddr ? '/fba-shipments/ship-from-addresses/' + fbaEditAddr.id : '/fba-shipments/ship-from-addresses';
    api(url, { method: method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(fbaAddrForm) })
      .then(function() { showToast(fbaEditAddr ? 'Address updated' : 'Address created'); setFbaEditAddr(null); setFbaShowForm(false); setFbaAddrForm({ label: '', name: '', address_line1: '', city: '', state: '', postal_code: '', country_code: 'US', phone: '' }); loadFbaAddrs(); })
      .catch(function(e) { showToast(e.message, 'error'); });
  };

  var deleteFbaAddr = async function(id) {
    if (!(await appConfirm('Delete this ship-from address?', { danger: true, okLabel: 'Delete' }))) return;
    api('/fba-shipments/ship-from-addresses/' + id, { method: 'DELETE' }).then(function() { showToast('Address deleted'); loadFbaAddrs(); }).catch(function(e) { showToast(e.message, 'error'); });
  };

  // Auto-Fill Drafts state (Forecasting tab section)
  var [autoFillRuns, setAutoFillRuns] = useState([]);
  var [autoFillRunning, setAutoFillRunning] = useState(false);

  var loadAutoFillRuns = function() {
    api('/admin/auto-fill-drafts/runs?limit=10').then(function(r) { setAutoFillRuns(r.runs || []); }).catch(function(e) { console.error('[silent-catch]', e); });
  };
  useEffect(function() { if (activeTab === 'forecasting' && currentUser && currentUser.role === 'admin') loadAutoFillRuns(); }, [activeTab, currentUser]);
  // Refresh Recent Runs the moment a run finishes (fired by AutoFillProgressCard).
  useEffect(function() {
    if (!(currentUser && currentUser.role === 'admin')) return;
    var handler = function() { loadAutoFillRuns(); };
    window.addEventListener('autofill:done', handler);
    return function() { window.removeEventListener('autofill:done', handler); };
  }, [currentUser]);

  var runAutoFillNow = async function() {
    if (autoFillRunning) return;
    if (!(await appConfirm('Trigger an auto-fill run now? This will refresh all auto-managed lines on draft POs against the latest forecast.'))) return;
    setAutoFillRunning(true);
    api('/admin/auto-fill-drafts/run-now', { method: 'POST' })
      .then(function(r) {
        // Hand off to the global AutoFillProgressCard for live progress. It
        // polls on its own and survives page navigation. The button briefly
        // shows "Running..." then resets; subsequent runs are blocked
        // server-side by the in-progress precheck.
        window.dispatchEvent(new CustomEvent('autofill:start', { detail: { runId: r.run_id } }));
        setTimeout(function() {
          setAutoFillRunning(false);
          loadAutoFillRuns(); // initial refresh so the row appears in "Recent Runs"
        }, 2000);
        // Final refresh fires automatically via the 'autofill:done' listener above
        // the moment the run completes.
      })
      .catch(function(e) { setAutoFillRunning(false); showToast(e.message, 'error'); });
  };

  var tabTitles = { company: 'Company Settings', users: 'Users & Roles', vendors: 'Vendor Settings', templates: 'Templates', integrations: 'Integrations', analysis: 'Analysis Settings', forecasting: 'Forecasting Settings', fba: 'FBA Shipments', mobile: 'Mobile App' };

  return React.createElement('div', null,
    tabTitles[activeTab] && React.createElement('h2', { style: { margin: '0 0 20px', fontSize: 20, fontWeight: 600, color: C.text } }, tabTitles[activeTab]),

    // ─── TAB: COMPANY SETTINGS ───
    activeTab === 'company' && React.createElement('div', { style: { marginTop: 16 } },
      // Logo section
      React.createElement(Card, null,
        React.createElement('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 12 } }, 'Company Logos'),
        React.createElement('input', { type: 'file', accept: 'image/*', ref: logoRef, onChange: handleLogoUpload, style: { display: 'none' } }),
        React.createElement('input', { type: 'file', accept: 'image/*', ref: iconLogoRef, onChange: handleIconLogoUpload, style: { display: 'none' } }),
        React.createElement('div', { style: { display: 'flex', gap: 24, flexWrap: 'wrap' } },
          // Full logo
          React.createElement('div', { style: { flex: '1 1 280px' } },
            React.createElement('div', { style: { fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 8 } }, 'Sidebar Logo (expanded)'),
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 16 } },
              logoUrl
                ? React.createElement('div', { style: { background: C.bg, border: '1px solid ' + C.border, borderRadius: 8, padding: 12, display: 'inline-block' } },
                    React.createElement('img', { src: logoUrl, alt: 'Logo', style: { maxWidth: 200, maxHeight: 80, objectFit: 'contain', display: 'block' } })
                  )
                : React.createElement('div', {
                    style: { background: C.bg, border: '2px dashed ' + C.border, borderRadius: 8, padding: '24px 32px', textAlign: 'center', color: C.textDim }
                  },
                    React.createElement('div', { style: { fontSize: 24, marginBottom: 4 } }, React.createElement(LIcon, { name: 'image', size: 24 })),
                    React.createElement('div', { style: { fontSize: 12 } }, 'No logo set')
                  ),
              React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 6 } },
                React.createElement(Btn, { size: 'sm', onClick: function() { logoRef.current.click(); } }, React.createElement(LIcon, { name: logoUrl ? 'refresh-cw' : 'upload', size: 14 }), logoUrl ? ' Change' : ' Upload'),
                logoUrl && React.createElement(Btn, { size: 'sm', variant: 'danger', onClick: handleLogoRemove }, React.createElement(LIcon, { name: 'trash-2', size: 14 }), ' Remove')
              )
            ),
            React.createElement('p', { style: { fontSize: 11, color: C.textDim, marginTop: 8 } }, 'Optimal: 400 × 100px, PNG or SVG, max 5MB')
          ),
          // Icon logo
          React.createElement('div', { style: { flex: '0 0 auto' } },
            React.createElement('div', { style: { fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 8 } }, 'Sidebar Logo (collapsed)'),
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 16 } },
              iconLogoUrl
                ? React.createElement('div', { style: { background: C.bg, border: '1px solid ' + C.border, borderRadius: 8, padding: 8, display: 'inline-block' } },
                    React.createElement('img', { src: iconLogoUrl, alt: 'Icon', style: { width: 40, height: 40, objectFit: 'contain', display: 'block' } })
                  )
                : React.createElement('div', {
                    style: { background: C.bg, border: '2px dashed ' + C.border, borderRadius: 8, width: 56, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textDim }
                  },
                    React.createElement('div', { style: { fontSize: 20 } }, React.createElement(LIcon, { name: 'file-text', size: 20 }))
                  ),
              React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 6 } },
                React.createElement(Btn, { size: 'sm', onClick: function() { iconLogoRef.current.click(); } }, React.createElement(LIcon, { name: iconLogoUrl ? 'refresh-cw' : 'upload', size: 14 }), iconLogoUrl ? ' Change' : ' Upload'),
                iconLogoUrl && React.createElement(Btn, { size: 'sm', variant: 'danger', onClick: handleIconLogoRemove }, React.createElement(LIcon, { name: 'trash-2', size: 14 }), ' Remove')
              )
            ),
            React.createElement('p', { style: { fontSize: 11, color: C.textDim, marginTop: 8 } }, 'Optimal: 100 × 100px, square PNG or SVG')
          )
        )
      ),
      // PO Config
      React.createElement(Card, { style: { marginTop: 12 } },
        React.createElement('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 12 } }, 'PO Configuration'),
        React.createElement('div', { style: { maxWidth: 300 } }, React.createElement(Inp, { label: 'Next PO Number', type: 'number', ...F('next_po_number') }))
      ),
      // Company Info
      React.createElement(Card, { style: { marginTop: 12 } },
        React.createElement('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 12 } }, 'Company Information'),
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 } },
          React.createElement(Inp, { label: 'Company Name', ...F('company_name') }),
          React.createElement(Inp, { label: 'Email', ...F('company_email') }),
          React.createElement(Inp, { label: 'PO From Name', ...F('po_from_name'), placeholder: 'e.g. Atharva Purchasing' }),
          React.createElement(Inp, { label: 'PO From Email', ...F('po_from_email'), placeholder: 'e.g. purchasing@company.com' }),
          React.createElement(Inp, { label: 'PO Reply-To Email', ...F('po_reply_to_email'), placeholder: 'e.g. purchasing@company.com' }),
          React.createElement(Inp, { label: 'Address', ...F('company_address') }),
          React.createElement(Inp, { label: 'City', ...F('company_city') }),
          React.createElement(StateSelect, { label: 'State', country: 'US', ...F('company_state') }),
          React.createElement(Inp, { label: 'ZIP', ...F('company_zip') }),
          React.createElement(Inp, { label: 'Phone', ...F('company_phone') })
        ),
        React.createElement('div', { style: { marginTop: 16, display: 'flex', justifyContent: 'flex-end' } },
          React.createElement(Btn, { variant: 'primary', onClick: save }, React.createElement(LIcon, { name: 'check', size: 14 }), ' Save Settings')
        )
      )
    ),

    // ─── TAB: USERS & ROLES ───
    activeTab === 'users' && React.createElement(UsersAndRolesTab, { showToast: showToast, currentUser: currentUser }),

    // ─── TAB: PURCHASING ───
    activeTab === 'purchasing' && placeholderTab('Purchasing, Forecasting & Analysis', 'Configure purchasing rules, reorder points, demand forecasting settings, and analysis preferences. Coming soon.'),

    // ─── TAB: RECEIVING ───
    activeTab === 'receiving' && placeholderTab('Receiving Settings', 'Configure receiving workflows, barcode scanning preferences, partial receiving rules, and quality check requirements. Coming soon.'),

    // ─── TAB: CATALOG ───
    activeTab === 'catalog' && placeholderTab('Catalog Settings', 'Manage product categories, default fields, SKU naming conventions, and catalog display preferences. Coming soon.'),

    // ─── TAB: VENDORS ───
    activeTab === 'vendors' && React.createElement('div', { style: { marginTop: 16 } },
      React.createElement(VendorsPage, { showToast: showToast, userRole: 'admin' })
    ),

    // ─── TAB: WAREHOUSES ───
    activeTab === 'warehouses' && React.createElement('div', { style: { marginTop: 16 } },
      React.createElement(WarehousesPage, { showToast: showToast, userRole: 'admin', onNavigate: onNavigate })
    ),

    // ─── TAB: TEMPLATES ───
    activeTab === 'templates' && React.createElement('div', { style: { marginTop: 16 } },
      React.createElement(Card, null,
        React.createElement('div', { style: { fontSize: 16, fontWeight: 600, color: C.text, marginBottom: 16 } }, 'PO Email Templates'),
        React.createElement('div', { style: { display: 'flex', gap: 20, minHeight: 400 } },
          // Left panel: template list
          React.createElement('div', { style: { width: 240, flexShrink: 0, borderRight: '1px solid ' + C.border, paddingRight: 16 } },
            React.createElement('button', { onClick: function() { setEditPoTpl(null); setPoTplForm({ name: '', subject: '', body: '' }); },
              style: { width: '100%', padding: '6px 10px', marginBottom: 10, fontSize: 12, borderRadius: 6, border: '1px dashed ' + C.border, background: 'transparent', color: C.accent, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500 }
            }, '+ New Template'),
            poTemplates.length === 0
              ? React.createElement('div', { style: { fontSize: 12, color: C.textMuted, fontStyle: 'italic' } }, 'No templates yet')
              : poTemplates.map(function(tpl) {
                var isActive = editPoTpl && editPoTpl.id === tpl.id;
                return React.createElement('div', { key: tpl.id, style: { display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 } },
                  React.createElement('button', { onClick: function() { setEditPoTpl(tpl); setPoTplForm({ name: tpl.name, subject: tpl.subject || '', body: tpl.body }); },
                    style: { flex: 1, textAlign: 'left', padding: '6px 8px', fontSize: 12, borderRadius: 4, border: 'none',
                      background: isActive ? C.accent + '12' : 'transparent', color: isActive ? C.accent : C.text, cursor: 'pointer', fontFamily: 'inherit',
                      fontWeight: isActive ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
                  }, tpl.name, tpl.is_default ? React.createElement('span', { style: { fontSize: 9, color: C.textMuted, marginLeft: 6 } }, '(default)') : null),
                  React.createElement('button', { onClick: function() { deletePoTpl(tpl.id); },
                    style: { padding: '2px 6px', fontSize: 10, borderRadius: 4, border: 'none', background: 'transparent', color: C.danger, cursor: 'pointer', fontFamily: 'inherit', opacity: 0.6, display: 'inline-flex', alignItems: 'center' }
                  }, React.createElement(LIcon, { name: 'x', size: 10 }))
                );
              })
          ),
          // Right panel: edit form
          React.createElement('div', { style: { flex: 1 } },
            React.createElement('div', { style: { fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', marginBottom: 8 } },
              editPoTpl ? 'Edit Template' : 'New Template'),
            React.createElement(Inp, { label: 'Template Name', placeholder: 'e.g. Standard PO, Rush Order',
              value: poTplForm.name, onChange: function(e) { setPoTplForm(Object.assign({}, poTplForm, { name: e.target.value })); } }),
            React.createElement('div', { style: { marginTop: 10 } },
              React.createElement(Inp, { label: 'Email Subject', placeholder: 'e.g. Purchase Order {po_number} — {company_name}',
                value: poTplForm.subject, onChange: function(e) { setPoTplForm(Object.assign({}, poTplForm, { subject: e.target.value })); } })
            ),
            React.createElement('div', { style: { marginTop: 10 } },
              React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 } },
                React.createElement('label', { style: { fontSize: 12, fontWeight: 500, color: C.textMuted } }, 'Email Body'),
                React.createElement('button', { onClick: function() {
                    if (poTplMode === 'visual') {
                      // Sync from contentEditable before switching to source
                      if (poTplBodyRef.current) setPoTplForm(Object.assign({}, poTplForm, { body: poTplBodyRef.current.innerHTML }));
                    }
                    setPoTplMode(poTplMode === 'visual' ? 'source' : 'visual');
                  },
                  style: { padding: '2px 8px', fontSize: 10, borderRadius: 4, border: '1px solid ' + C.border, background: 'transparent', color: C.textMuted, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500 }
                }, poTplMode === 'visual' ? '< > Source' : 'Visual Editor')
              ),
              // Formatting toolbar (visual mode only)
              poTplMode === 'visual' && React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 2, padding: '6px 8px', background: C.bg, border: '1px solid ' + C.border, borderBottom: 'none', borderRadius: '8px 8px 0 0' } },
                React.createElement(FontSizeSelect, { targetRef: poTplBodyRef }),
                React.createElement('div', { style: { width: 1, height: 20, background: C.border, margin: '0 4px', alignSelf: 'center' } }),
                [
                  { cmd: 'bold', icon: 'bold', tip: 'Bold' },
                  { cmd: 'italic', icon: 'italic', tip: 'Italic' },
                  { cmd: 'underline', icon: 'underline', tip: 'Underline' },
                  { cmd: 'strikeThrough', icon: 'strikethrough', tip: 'Strikethrough' },
                  { divider: true },
                  { cmd: 'hiliteColor', icon: 'highlighter', tip: 'Highlight', arg: '#FFEB3B' },
                  { cmd: 'foreColor', icon: 'palette', tip: 'Red Text', arg: '#EF4444' },
                  { cmd: 'removeFormat', icon: 'eraser', tip: 'Clear Formatting' },
                  { divider: true },
                  { cmd: 'formatBlock', icon: 'heading', tip: 'Heading', arg: 'h2' },
                  { cmd: 'formatBlock', icon: 'pilcrow', tip: 'Paragraph', arg: 'p' },
                  { divider: true },
                  { cmd: 'insertUnorderedList', icon: 'list', tip: 'Bullet List' },
                  { cmd: 'insertOrderedList', icon: 'list-ordered', tip: 'Numbered List' },
                  { divider: true },
                  { cmd: 'createLink', icon: 'link', tip: 'Insert Link' },
                  { cmd: 'insertHorizontalRule', icon: 'minus', tip: 'Horizontal Line' },
                ].map(function(btn, idx) {
                  if (btn.divider) return React.createElement('div', { key: 'div-' + idx, style: { width: 1, height: 20, background: C.border, margin: '0 4px', alignSelf: 'center' } });
                  return React.createElement('button', { key: btn.cmd + '-' + idx, title: btn.tip,
                    onMouseDown: function(e) { e.preventDefault(); },
                    onClick: async function() {
                      if (poTplBodyRef.current) poTplBodyRef.current.focus();
                      if (btn.cmd === 'createLink') {
                        // The in-app prompt takes focus, so keep the editor selection and put it back before linking.
                        var sel = window.getSelection();
                        var range = sel && sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
                        var url = await appPrompt('Enter URL:');
                        if (!url) return;
                        if (poTplBodyRef.current) poTplBodyRef.current.focus();
                        if (range) { sel.removeAllRanges(); sel.addRange(range); }
                        document.execCommand('createLink', false, url);
                      } else {
                        document.execCommand(btn.cmd, false, btn.arg || null);
                      }
                    },
                    style: { padding: '4px 7px', fontSize: 12, borderRadius: 4, border: 'none', background: 'transparent', color: C.text, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }
                  }, React.createElement(LIcon, { name: btn.icon, size: 14 }));
                })
              ),
              // Visual editor (contentEditable)
              poTplMode === 'visual' && React.createElement('div', {
                key: 'editor-' + (editPoTpl ? editPoTpl.id : 'new') + '-' + (editPoTpl ? editPoTpl.updated_at : ''),
                ref: poTplBodyRef,
                contentEditable: true,
                suppressContentEditableWarning: true,
                dangerouslySetInnerHTML: { __html: (typeof DOMPurify !== 'undefined' ? DOMPurify.sanitize(poTplForm.body) : '') },
                onBlur: function() {
                  if (poTplBodyRef.current) setPoTplForm(function(prev) { return Object.assign({}, prev, { body: poTplBodyRef.current.innerHTML }); });
                },
                style: { width: '100%', padding: '12px 14px', borderRadius: poTplMode === 'visual' ? '0 0 8px 8px' : 8, border: '1px solid ' + C.border, background: '#fff', color: '#333', fontSize: 13, fontFamily: 'Arial, sans-serif', minHeight: 280, maxHeight: 400, overflow: 'auto', boxSizing: 'border-box', outline: 'none', lineHeight: 1.6 }
              }),
              // Source editor (textarea)
              poTplMode === 'source' && React.createElement('textarea', { value: poTplForm.body, onChange: function(e) { setPoTplForm(Object.assign({}, poTplForm, { body: e.target.value })); },
                placeholder: '<div style="font-family:Arial,sans-serif">Your PO email content with {placeholders}...</div>',
                rows: 14, style: { width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid ' + C.border, background: C.surface, color: C.text, fontSize: 12, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', minHeight: 280 } })
            ),
            // Placeholder reference
            React.createElement('div', { style: { marginTop: 10, padding: '8px 10px', borderRadius: 6, background: C.surface, border: '1px solid ' + C.border } },
              React.createElement('div', { style: { fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', marginBottom: 4 } }, 'Available Placeholders — click to insert'),
              React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 4 } },
                ['{po_number}', '{po_date}', '{vendor_name}', '{vendor_contact}', '{ship_to}', '{item_count}', '{unit_count}', '{grand_total}', '{vendor_notes}', '{vendor_notes_html}', '{company_name}'].map(function(ph) {
                  return React.createElement('button', { key: ph, onClick: function() {
                      if (poTplMode === 'visual' && poTplBodyRef.current) {
                        poTplBodyRef.current.focus();
                        document.execCommand('insertText', false, ph);
                      } else {
                        var el = document.querySelector('textarea');
                        if (el) {
                          var pos = el.selectionStart || 0;
                          var before = poTplForm.body.slice(0, pos);
                          var after = poTplForm.body.slice(pos);
                          setPoTplForm(Object.assign({}, poTplForm, { body: before + ph + after }));
                          setTimeout(function() { el.focus(); el.selectionStart = el.selectionEnd = pos + ph.length; }, 0);
                        } else {
                          setPoTplForm(Object.assign({}, poTplForm, { body: poTplForm.body + ph }));
                        }
                      }
                    },
                    style: { padding: '2px 8px', fontSize: 10, borderRadius: 4, border: '1px solid ' + C.border, background: C.accent + '08', color: C.accent, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500 }
                  }, ph);
                })
              )
            ),
            React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 } },
              React.createElement(Btn, { size: 'sm', onClick: function() { setEditPoTpl(null); setPoTplForm({ name: '', subject: '', body: '' }); } }, 'Clear'),
              React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: savePoTpl, disabled: !poTplForm.name || !poTplForm.body }, editPoTpl ? [React.createElement(LIcon, { key: 'icon', name: 'save', size: 14 }), ' Update'] : '+ Create')
            )
          )
        )
      ),

      // ─── LABEL TEMPLATES SECTION ───
      React.createElement(Card, { style: { marginTop: 20 } },
        React.createElement('div', { style: { fontSize: 16, fontWeight: 600, color: C.text, marginBottom: 12 } }, 'Product Label Templates'),
        // Category tabs
        React.createElement('div', { style: { display: 'flex', gap: 4, marginBottom: 16 } },
          [{ key: 'fba', label: 'FBA' }, { key: 'fbm', label: 'FBM' }, { key: 'wfs', label: 'WFS' }, { key: 'meli', label: 'MELI' }].map(function(cat) {
            var active = labelTplCategory === cat.key;
            return React.createElement('button', { key: cat.key, onClick: function() {
                setLabelTplCategory(cat.key); setEditLabelTpl(null); setLabelTplForm(Object.assign({}, defaultLabelForm, { category: cat.key }));
              },
              style: { padding: '6px 16px', fontSize: 12, fontWeight: active ? 600 : 400, borderRadius: 6, border: '1px solid ' + (active ? C.accent : C.border), background: active ? C.accent + '12' : 'transparent', color: active ? C.accent : C.text, cursor: 'pointer', fontFamily: 'inherit' }
            }, cat.label);
          })
        ),
        React.createElement('div', { style: { display: 'flex', gap: 20, minHeight: 360 } },
          // Left panel: template list (filtered by category)
          React.createElement('div', { style: { width: 240, flexShrink: 0, borderRight: '1px solid ' + C.border, paddingRight: 16 } },
            React.createElement('button', { onClick: function() { setEditLabelTpl(null); setLabelTplForm(Object.assign({}, defaultLabelForm, { category: labelTplCategory })); },
              style: { width: '100%', padding: '6px 10px', marginBottom: 10, fontSize: 12, borderRadius: 6, border: '1px dashed ' + C.border, background: 'transparent', color: C.accent, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500 }
            }, '+ New Label Template'),
            (function() { var catTpls = labelTemplates.filter(function(t) { return (t.category || 'fba') === labelTplCategory; }); return catTpls.length === 0
              ? React.createElement('div', { style: { fontSize: 12, color: C.textMuted, fontStyle: 'italic' } }, 'No label templates yet')
              : catTpls.map(function(tpl) {
                var isActive = editLabelTpl && editLabelTpl.id === tpl.id;
                return React.createElement('div', { key: tpl.id, style: { display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 } },
                  React.createElement('button', { onClick: function() {
                    setEditLabelTpl(tpl);
                    setLabelTplForm({ name: tpl.name, label_type: tpl.label_type, width_mm: tpl.width_mm, height_mm: tpl.height_mm, columns: tpl.columns || 1, rows_per_sheet: tpl.rows_per_sheet || 1, page_width_mm: tpl.page_width_mm || '', page_height_mm: tpl.page_height_mm || '', fields: tpl.fields || ['fnsku','sku','title'], show_barcode: tpl.show_barcode !== false, font_size: tpl.font_size || 12, is_default: tpl.is_default || false, category: tpl.category || 'fba' });
                  },
                    style: { flex: 1, textAlign: 'left', padding: '6px 8px', fontSize: 12, borderRadius: 4, border: 'none',
                      background: isActive ? C.accent + '12' : 'transparent', color: isActive ? C.accent : C.text, cursor: 'pointer', fontFamily: 'inherit',
                      fontWeight: isActive ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
                  },
                    tpl.name,
                    tpl.is_default ? React.createElement('span', { style: { fontSize: 9, color: C.textMuted, marginLeft: 6 } }, '(default)') : null,
                    React.createElement('div', { style: { fontSize: 10, color: C.textMuted, marginTop: 2 } },
                      tpl.label_type === 'sheet' ? tpl.columns + '×' + tpl.rows_per_sheet + ' per sheet' : parseFloat(tpl.width_mm).toFixed(1) + ' × ' + parseFloat(tpl.height_mm).toFixed(1) + 'mm')
                  ),
                  React.createElement('button', { onClick: function() { deleteLabelTpl(tpl.id); },
                    style: { padding: '2px 6px', fontSize: 10, borderRadius: 4, border: 'none', background: 'transparent', color: C.danger, cursor: 'pointer', fontFamily: 'inherit', opacity: 0.6, display: 'inline-flex', alignItems: 'center' }
                  }, React.createElement(LIcon, { name: 'x', size: 10 }))
                );
              }); })()
          ),
          // Right panel: edit form
          React.createElement('div', { style: { flex: 1 } },
            React.createElement('div', { style: { fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', marginBottom: 8 } },
              editLabelTpl ? 'Edit Label Template' : 'New Label Template'),

            // Presets
            React.createElement('div', { style: { marginBottom: 12 } },
              React.createElement('div', { style: { fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 6 } }, 'Quick Presets'),
              React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 4 } },
                labelPresets.map(function(preset) {
                  return React.createElement('button', { key: preset.name, onClick: function() { applyPreset(preset); },
                    style: { padding: '3px 8px', fontSize: 10, borderRadius: 4, border: '1px solid ' + C.border, background: C.surface, color: C.text, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500 }
                  }, preset.name);
                })
              )
            ),

            // Name + type
            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, marginBottom: 10 } },
              React.createElement(Inp, { label: 'Template Name', value: labelTplForm.name, onChange: function(e) { setLabelTplForm(Object.assign({}, labelTplForm, { name: e.target.value })); } }),
              React.createElement('div', null,
                React.createElement('label', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted, display: 'block', marginBottom: 4 } }, 'Type'),
                React.createElement('select', { value: labelTplForm.label_type, onChange: function(e) { setLabelTplForm(Object.assign({}, labelTplForm, { label_type: e.target.value })); },
                  style: { padding: '8px 12px', borderRadius: 6, border: '1px solid ' + C.border, background: C.bg, color: C.text, fontSize: 12 } },
                  React.createElement('option', { value: 'thermal' }, 'Thermal (single)'),
                  React.createElement('option', { value: 'sheet' }, 'Sheet (multi-up)')
                )
              )
            ),

            // Label dimensions
            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 } },
              React.createElement(Inp, { label: 'Label Width (mm)', type: 'number', step: '0.1', value: labelTplForm.width_mm, onChange: function(e) { setLabelTplForm(Object.assign({}, labelTplForm, { width_mm: e.target.value })); } }),
              React.createElement(Inp, { label: 'Label Height (mm)', type: 'number', step: '0.1', value: labelTplForm.height_mm, onChange: function(e) { setLabelTplForm(Object.assign({}, labelTplForm, { height_mm: e.target.value })); } }),
              React.createElement(Inp, { label: 'Font Size (pt)', type: 'number', value: labelTplForm.font_size, onChange: function(e) { setLabelTplForm(Object.assign({}, labelTplForm, { font_size: parseInt(e.target.value) || 12 })); } })
            ),

            // Sheet layout (only for sheet type)
            labelTplForm.label_type === 'sheet' && React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, marginBottom: 10 } },
              React.createElement(Inp, { label: 'Columns', type: 'number', min: 1, value: labelTplForm.columns, onChange: function(e) { setLabelTplForm(Object.assign({}, labelTplForm, { columns: parseInt(e.target.value) || 1 })); } }),
              React.createElement(Inp, { label: 'Rows per Sheet', type: 'number', min: 1, value: labelTplForm.rows_per_sheet, onChange: function(e) { setLabelTplForm(Object.assign({}, labelTplForm, { rows_per_sheet: parseInt(e.target.value) || 1 })); } }),
              React.createElement(Inp, { label: 'Page Width (mm)', type: 'number', step: '0.1', value: labelTplForm.page_width_mm, onChange: function(e) { setLabelTplForm(Object.assign({}, labelTplForm, { page_width_mm: e.target.value })); } }),
              React.createElement(Inp, { label: 'Page Height (mm)', type: 'number', step: '0.1', value: labelTplForm.page_height_mm, onChange: function(e) { setLabelTplForm(Object.assign({}, labelTplForm, { page_height_mm: e.target.value })); } })
            ),

            // Fields to include
            React.createElement('div', { style: { marginBottom: 10 } },
              React.createElement('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 6 } }, 'Fields to Show on Label'),
              React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 6 } },
                labelFieldOptions.map(function(opt) {
                  var checked = (labelTplForm.fields || []).indexOf(opt.value) >= 0;
                  return React.createElement('button', { key: opt.value, onClick: function() { toggleLabelField(opt.value); },
                    style: { display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 6,
                      border: '1px solid ' + (checked ? C.accent : C.border), background: checked ? C.accent + '12' : 'transparent',
                      color: checked ? C.accent : C.text, cursor: 'pointer', fontSize: 11, fontFamily: 'inherit', fontWeight: 500 }
                  },
                    React.createElement('div', { style: { width: 16, height: 16, borderRadius: 3, border: '2px solid ' + (checked ? C.accent : C.textDim), background: checked ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' } },
                      checked && React.createElement(LIcon, { name: 'check', size: 10, color: '#fff' })
                    ),
                    opt.label
                  );
                })
              )
            ),

            // Default toggle
            React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 } },
              React.createElement('button', { onClick: function() { setLabelTplForm(Object.assign({}, labelTplForm, { is_default: !labelTplForm.is_default })); },
                style: { display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 6,
                  border: '1px solid ' + (labelTplForm.is_default ? C.accent : C.border), background: labelTplForm.is_default ? C.accent + '12' : 'transparent',
                  color: labelTplForm.is_default ? C.accent : C.text, cursor: 'pointer', fontSize: 11, fontFamily: 'inherit', fontWeight: 500 } },
                React.createElement('div', { style: { width: 16, height: 16, borderRadius: 3, border: '2px solid ' + (labelTplForm.is_default ? C.accent : C.textDim), background: labelTplForm.is_default ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' } },
                  labelTplForm.is_default && React.createElement(LIcon, { name: 'check', size: 10, color: '#fff' })
                ),
                'Set as Default'
              )
            ),

            // Label preview
            React.createElement('div', { style: { marginBottom: 12 } },
              React.createElement('div', { style: { fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 6 } }, 'Preview'),
              React.createElement('div', { style: { display: 'inline-flex', border: '1px dashed ' + C.border, borderRadius: 6, padding: 12, background: '#fff' } },
                React.createElement('div', { style: {
                  width: Math.min(parseFloat(labelTplForm.width_mm) || 50, 120) * 2.5,
                  minHeight: Math.min(parseFloat(labelTplForm.height_mm) || 25, 80) * 2.5,
                  border: '1px solid #ccc', borderRadius: 2, padding: 6, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', background: '#fff'
                } },
                  // 1. Barcode SVG
                  (function() { var pv = labelPreviewCode(labelTplCategory); return React.createElement('div', { style: { textAlign: 'center', marginBottom: 1, maxWidth: '100%', overflow: 'hidden' }, dangerouslySetInnerHTML: { __html: generateCode128SVG(pv, 24, 1) } }); })(),
                  // 2. Barcode text (FNSKU for FBA, UPC/Barcode for FBM/WFS)
                  (function() { var pv = labelPreviewCode(labelTplCategory); var lb = labelTplCategory === 'fba' ? '' : ''; return React.createElement('div', { style: { fontFamily: 'inherit', fontSize: Math.max(8, (labelTplForm.font_size || 12) - 2), fontWeight: 700, letterSpacing: 1, color: '#000', marginBottom: 1, textAlign: 'center' } }, pv); })(),
                  // 3. Title
                  (labelTplForm.fields || []).indexOf('title') >= 0 && React.createElement('div', { style: { fontSize: Math.max(6, (labelTplForm.font_size || 12) - 4), color: '#333', textAlign: 'center', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%', marginBottom: 1 } }, 'Product Title...'),
                  // 4. Bottom row: Condition | SKU | Expiry
                  (function() {
                    var fl = labelTplForm.fields || [];
                    var parts = [];
                    if (fl.indexOf('condition') >= 0) parts.push('New');
                    if (fl.indexOf('sku') >= 0) parts.push((labelTplCategory === 'meli' ? 'SKU: ' : '') + 'SAMPLE-SKU');
                    if (fl.indexOf('expiry') >= 0) parts.push('Exp: 2026-12-31');
                    return parts.length > 0 ? React.createElement('div', { style: { fontSize: Math.max(6, (labelTplForm.font_size || 12) - 4), color: '#333', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' } }, parts.join('  |  ')) : null;
                  })()
                )
              )
            ),

            // Save buttons
            React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } },
              React.createElement(Btn, { size: 'sm', onClick: function() { setEditLabelTpl(null); setLabelTplForm(Object.assign({}, defaultLabelForm, { category: labelTplCategory })); } }, 'Clear'),
              React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: saveLabelTpl, disabled: !labelTplForm.name || !labelTplForm.width_mm || !labelTplForm.height_mm },
                editLabelTpl ? [React.createElement(LIcon, { key: 'icon', name: 'save', size: 14 }), ' Update'] : '+ Create')
            )
          )
        )
      )
    ),

    // ─── CASE LABEL TEMPLATES SECTION ───
    activeTab === 'templates' && React.createElement(Card, { style: { marginTop: 20 } },
      React.createElement('div', { style: { fontSize: 16, fontWeight: 600, color: C.text, marginBottom: 12 } }, 'Case Label Templates'),
      // Category tabs
      React.createElement('div', { style: { display: 'flex', gap: 4, marginBottom: 16 } },
        [{ key: 'po', label: 'PO' }, { key: 'fba', label: 'FBA-LTL' }, { key: 'fba-spd', label: 'FBA-SPD' }, { key: 'wfs', label: 'WFS' }].map(function(cat) {
          var active = caseTplCategory === cat.key;
          return React.createElement('button', { key: cat.key, onClick: function() {
              setCaseTplCategory(cat.key); setEditCaseTpl(null); setCaseTplForm(Object.assign({}, defaultCaseForm, { category: cat.key }));
            },
            style: { padding: '6px 16px', fontSize: 12, fontWeight: active ? 600 : 400, borderRadius: 6, border: '1px solid ' + (active ? C.accent : C.border), background: active ? C.accent + '12' : 'transparent', color: active ? C.accent : C.text, cursor: 'pointer', fontFamily: 'inherit' }
          }, cat.label);
        })
      ),
      React.createElement('div', { style: { display: 'flex', gap: 20, minHeight: 360 } },
        // Left panel: template list (hidden for FBA-LTL — Amazon format is fixed)
        caseTplCategory !== 'fba' && React.createElement('div', { style: { width: 240, flexShrink: 0, borderRight: '1px solid ' + C.border, paddingRight: 16 } },
          React.createElement('button', { onClick: function() { setEditCaseTpl(null); setCaseTplForm(Object.assign({}, defaultCaseForm, { category: caseTplCategory })); },
            style: { width: '100%', padding: '6px 10px', marginBottom: 10, fontSize: 12, borderRadius: 6, border: '1px dashed ' + C.border, background: 'transparent', color: C.accent, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500 }
          }, '+ New Case Label Template'),
          (function() { var catTpls = caseLabelTemplates.filter(function(t) { return (t.category || 'po') === caseTplCategory; }); return catTpls.length === 0
            ? React.createElement('div', { style: { fontSize: 12, color: C.textMuted, fontStyle: 'italic' } }, 'No case label templates yet')
            : catTpls.map(function(tpl) {
              var isActive = editCaseTpl && editCaseTpl.id === tpl.id;
              return React.createElement('div', { key: tpl.id, style: { display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 } },
                React.createElement('button', { onClick: function() {
                  setEditCaseTpl(tpl);
                  setCaseTplForm({ name: tpl.name, label_type: tpl.label_type, width_mm: tpl.width_mm, height_mm: tpl.height_mm, columns: tpl.columns || 1, rows_per_sheet: tpl.rows_per_sheet || 1, page_width_mm: tpl.page_width_mm || '', page_height_mm: tpl.page_height_mm || '', fields: tpl.fields || ['vendor','warehouse'], show_barcode: tpl.show_barcode !== false, font_size: tpl.font_size || 12, is_default: tpl.is_default || false, category: tpl.category || 'po' });
                },
                  style: { flex: 1, textAlign: 'left', padding: '6px 8px', fontSize: 12, borderRadius: 4, border: 'none',
                    background: isActive ? C.accent + '12' : 'transparent', color: isActive ? C.accent : C.text, cursor: 'pointer', fontFamily: 'inherit',
                    fontWeight: isActive ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
                },
                  tpl.name,
                  tpl.is_default ? React.createElement('span', { style: { fontSize: 9, color: C.textMuted, marginLeft: 6 } }, '(default)') : null,
                  React.createElement('div', { style: { fontSize: 10, color: C.textMuted, marginTop: 2 } },
                    tpl.label_type === 'sheet' ? tpl.columns + '\u00d7' + tpl.rows_per_sheet + ' per sheet' : parseFloat(tpl.width_mm).toFixed(1) + ' \u00d7 ' + parseFloat(tpl.height_mm).toFixed(1) + 'mm')
                ),
                React.createElement('button', { onClick: function() { deleteCaseTpl(tpl.id); },
                  style: { padding: '2px 6px', fontSize: 10, borderRadius: 4, border: 'none', background: 'transparent', color: C.danger, cursor: 'pointer', fontFamily: 'inherit', opacity: 0.6, display: 'inline-flex', alignItems: 'center' }
                }, React.createElement(LIcon, { name: 'x', size: 10 }))
              );
            }); })()
        ),
        // ─── FBA: Read-only Amazon-mandated format display ───
        caseTplCategory === 'fba' && React.createElement('div', { style: { flex: 1 } },
          React.createElement('div', { style: { fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', marginBottom: 12 } }, 'FBA-LTL Case Label — Amazon Required Format'),
          React.createElement('div', { style: { padding: '12px 16px', borderRadius: 8, background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', marginBottom: 16 } },
            React.createElement('div', { style: { fontSize: 12, fontWeight: 600, color: '#3B82F6', marginBottom: 6 } },
              React.createElement(LIcon, { name: 'lock', size: 14 }), ' Amazon 2D Barcode Format'),
            React.createElement('div', { style: { fontSize: 11, color: C.text, lineHeight: 1.6 } },
              'FBA case labels use a PDF417 barcode encoding box content data in Amazon\'s required format. This template cannot be edited — all fields and layout are mandated by Amazon.')
          ),
          // Fixed fields display
          React.createElement('div', { style: { marginBottom: 16 } },
            React.createElement('div', { style: { fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 8 } }, 'Required Fields'),
            [
              { icon: 'hash', label: 'Shipment ID', desc: 'Amazon shipment identifier (PO field)' },
              { icon: 'package', label: 'FNSKU', desc: 'Fulfillment Network SKU for each item in the box' },
              { icon: 'layers', label: 'Quantity', desc: 'Unit count per FNSKU in the box' },
              { icon: 'calendar', label: 'Expiration Date', desc: 'If applicable for the product' }
            ].map(function(f) {
              return React.createElement('div', { key: f.label, style: { display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8, padding: '6px 10px', borderRadius: 6, background: C.surface, border: '1px solid ' + C.border } },
                React.createElement(LIcon, { name: f.icon, size: 14, color: C.accent }),
                React.createElement('div', null,
                  React.createElement('div', { style: { fontSize: 12, fontWeight: 600, color: C.text } }, f.label),
                  React.createElement('div', { style: { fontSize: 10, color: C.textMuted } }, f.desc)
                )
              );
            })
          ),
          // Barcode format
          React.createElement('div', { style: { marginBottom: 16 } },
            React.createElement('div', { style: { fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 8 } }, 'Barcode Encoding'),
            React.createElement('div', { style: { padding: '8px 12px', borderRadius: 6, background: C.surface, border: '1px solid ' + C.border, fontFamily: 'inherit', fontSize: 11, color: C.text, lineHeight: 1.6 } },
              'AMZN,PO:{ShipmentID},FNSKU:{fnsku},QTY:{qty}')
          ),
          // Label specs
          React.createElement('div', { style: { marginBottom: 16 } },
            React.createElement('div', { style: { fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 8 } }, 'Label Specifications'),
            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 } },
              [
                { label: 'Size', val: '4" \u00d7 6" (101.6 \u00d7 152.4 mm)' },
                { label: 'Barcode', val: 'PDF417 2D barcode' },
                { label: 'Type', val: 'Thermal label' },
                { label: 'Placement', val: 'One label per box' }
              ].map(function(s) {
                return React.createElement('div', { key: s.label, style: { padding: '6px 10px', borderRadius: 6, background: C.surface, border: '1px solid ' + C.border } },
                  React.createElement('div', { style: { fontSize: 9, color: C.textMuted, fontWeight: 600, textTransform: 'uppercase' } }, s.label),
                  React.createElement('div', { style: { fontSize: 11, color: C.text, fontWeight: 500, marginTop: 2 } }, s.val)
                );
              })
            )
          ),
          // Preview — matches actual FBA case label layout (4x6 proportions)
          React.createElement('div', { style: { marginBottom: 12 } },
            React.createElement('div', { style: { fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 6 } }, 'Preview'),
            React.createElement('div', { style: { display: 'inline-flex', border: '1px dashed ' + C.border, borderRadius: 6, padding: 12, background: '#fff' } },
              React.createElement('div', { style: {
                width: 210, height: 315,
                border: '1px solid #ccc', borderRadius: 2, padding: '4px 6px', display: 'flex', flexDirection: 'column', background: '#fff', boxSizing: 'border-box', fontFamily: 'Arial, Helvetica, sans-serif'
              } },
                // Banner
                React.createElement('div', { style: { textAlign: 'center', fontSize: 5, fontWeight: 700, letterSpacing: 0.3, color: '#000', marginBottom: 1 } }, 'PLEASE LEAVE THIS LABEL UNCOVERED'),
                // FBA header
                React.createElement('div', { style: { fontSize: 14, fontWeight: 900, color: '#000', marginBottom: 1 } }, 'FBA'),
                // Divider
                React.createElement('div', { style: { width: '100%', height: 2, background: '#000', marginBottom: 2 } }),
                // Address row
                React.createElement('div', { style: { display: 'flex', gap: 4, fontSize: 5.5, lineHeight: 1.3, marginBottom: 2, color: '#000' } },
                  React.createElement('div', { style: { flex: 1 } },
                    React.createElement('div', { style: { fontSize: 5, fontWeight: 700, textTransform: 'uppercase' } }, 'SHIP FROM:'),
                    React.createElement('div', null, 'Vendor Name'),
                    React.createElement('div', null, '123 Vendor St'),
                    React.createElement('div', null, 'City, ST 12345'),
                    React.createElement('div', null, 'US')
                  ),
                  React.createElement('div', { style: { flex: 1 } },
                    React.createElement('div', { style: { fontSize: 5, fontWeight: 700, textTransform: 'uppercase' } }, 'SHIP TO:'),
                    React.createElement('div', null, 'Amazon.com'),
                    React.createElement('div', null, 'PHX5'),
                    React.createElement('div', null, '6835 W Buckeye Rd'),
                    React.createElement('div', null, 'Phoenix, AZ 85043'),
                    React.createElement('div', null, 'US')
                  )
                ),
                // Info bar
                React.createElement('div', { style: { background: '#000', color: '#fff', fontSize: 5, padding: '1px 3px', display: 'flex', justifyContent: 'space-between', fontWeight: 600, marginBottom: 2 } },
                  React.createElement('span', null, 'Shipment: FBA3YRGGYB0'),
                  React.createElement('span', null, 'Created: 2026/03/07')
                ),
                // Box ID barcode
                React.createElement('div', { style: { textAlign: 'center', marginBottom: 1, overflow: 'hidden' },
                  dangerouslySetInnerHTML: { __html: generateCode128SVG('FBA18BCXY000001', 24, 1) } }),
                React.createElement('div', { style: { fontFamily: 'inherit', fontSize: 6, textAlign: 'center', letterSpacing: 0.5, color: '#000', marginBottom: 1 } }, 'FBA18BCXY000001'),
                // Divider
                React.createElement('div', { style: { width: '100%', height: 2, background: '#000', marginBottom: 2 } }),
                // Box number
                React.createElement('div', { style: { fontSize: 14, fontWeight: 700, textAlign: 'center', color: '#000', marginBottom: 1 } }, 'BOX 1'),
                // Divider
                React.createElement('div', { style: { width: '100%', height: 2, background: '#000', marginBottom: 2 } }),
                // PDF417 barcode
                (function() {
                  var sampleData = buildAmazonCaseBarcodeData('FBA3YRGGYB0', [
                    { fnsku: 'X0017HHGEB', qty: 12 },
                    { fnsku: 'X000RU7V2V', qty: 6 }
                  ]);
                  return React.createElement('div', { style: { textAlign: 'center', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
                    dangerouslySetInnerHTML: { __html: generatePDF417SVG(sampleData, { scale: 1, columns: 3, eclevel: 5 }) } });
                })(),
                // Summary
                React.createElement('div', { style: { fontSize: 6, textAlign: 'center', color: '#000', marginTop: 2 } },
                  'Dest: ', React.createElement('b', null, 'PHX5'), ' Shipment: ', React.createElement('b', null, 'FBA3YRGGYB0'), ' Units: ', React.createElement('b', null, '18')
                )
              )
            )
          )
        ),
        // ─── FBA-SPD: Shipping label format + case label info ───
        caseTplCategory === 'fba-spd' && React.createElement('div', { style: { flex: 1 } },
          React.createElement('div', { style: { fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', marginBottom: 12 } }, 'FBA-SPD Label Settings'),

          // Shipping Label Format
          React.createElement('div', { style: { padding: '12px 16px', borderRadius: 8, background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', marginBottom: 16 } },
            React.createElement('div', { style: { fontSize: 12, fontWeight: 600, color: '#3B82F6', marginBottom: 8 } },
              React.createElement(LIcon, { name: 'truck', size: 14 }), ' Amazon Shipping Label Format'),
            React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginBottom: 10 } },
              'When downloading UPS/FedEx shipping labels from Amazon for SPD shipments, this format will be used.'),
            React.createElement('label', { style: { display: 'block', fontSize: 11, fontWeight: 600, color: C.textMuted, marginBottom: 4 } }, 'Default Page Type'),
            React.createElement('select', { style: { width: '100%', maxWidth: 350, padding: '8px 10px', borderRadius: 6, border: '1px solid ' + C.border, background: C.bg, color: C.text, fontSize: 12 },
              value: settings.spd_shipping_label_format || 'PackageLabel_Letter_6',
              onChange: function(e) { setSettings(Object.assign({}, settings, { spd_shipping_label_format: e.target.value })); } },
              React.createElement('option', { value: 'PackageLabel_Letter_6' }, 'Letter 6-up (8.5" x 11" — 6 labels per page)'),
              React.createElement('option', { value: 'PackageLabel_Letter_2' }, 'Letter 2-up (8.5" x 11" — 2 labels per page)'),
              React.createElement('option', { value: 'PackageLabel_Thermal' }, 'Thermal (4" x 6" — 1 label per page)'),
              React.createElement('option', { value: 'PackageLabel_Plain_Paper' }, 'Plain Paper (8.5" x 11" — 1 label per page)'),
              React.createElement('option', { value: 'PackageLabel_A4_4' }, 'A4 4-up (4 labels per page)')
            ),
            React.createElement('div', { style: { marginTop: 10, display: 'flex', justifyContent: 'flex-end' } },
              React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: save }, React.createElement(LIcon, { name: 'check', size: 14 }), ' Save'))
          ),

          // Case Label Info
          React.createElement('div', { style: { padding: '12px 16px', borderRadius: 8, background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', marginBottom: 16 } },
            React.createElement('div', { style: { fontSize: 12, fontWeight: 600, color: '#3B82F6', marginBottom: 6 } },
              React.createElement(LIcon, { name: 'lock', size: 14 }), ' SPD Case Labels — Same Amazon Format as LTL'),
            React.createElement('div', { style: { fontSize: 11, color: C.text, lineHeight: 1.6 } },
              'SPD case labels use the same Amazon 4x6 PDF417 barcode format as LTL. Each box gets one label with the shipment-specific 2D barcode, box ID, and destination FC.')
          ),

          // Specs
          React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 } },
            [
              { label: 'Case Label Size', val: '4" \u00d7 6" (thermal)' },
              { label: 'Case Label Barcode', val: 'PDF417 2D barcode' },
              { label: 'Shipping Labels', val: 'Downloaded from Amazon' },
              { label: 'Carrier', val: 'Amazon Partnered (UPS/FedEx)' }
            ].map(function(s) {
              return React.createElement('div', { key: s.label, style: { padding: '6px 10px', borderRadius: 6, background: C.surface, border: '1px solid ' + C.border } },
                React.createElement('div', { style: { fontSize: 9, color: C.textMuted, fontWeight: 600, textTransform: 'uppercase' } }, s.label),
                React.createElement('div', { style: { fontSize: 11, color: C.text, fontWeight: 500, marginTop: 2 } }, s.val)
              );
            })
          )
        ),
        // ─── PO/WFS: Editable right panel ───
        caseTplCategory !== 'fba' && caseTplCategory !== 'fba-spd' && React.createElement('div', { style: { flex: 1 } },
          React.createElement('div', { style: { fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', marginBottom: 8 } },
            editCaseTpl ? 'Edit Case Label Template' : 'New Case Label Template'),

          // Presets
          React.createElement('div', { style: { marginBottom: 12 } },
            React.createElement('div', { style: { fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 6 } }, 'Quick Presets'),
            React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 4 } },
              casePresets.map(function(preset) {
                return React.createElement('button', { key: preset.name, onClick: function() { applyCasePreset(preset); },
                  style: { padding: '3px 8px', fontSize: 10, borderRadius: 4, border: '1px solid ' + C.border, background: C.surface, color: C.text, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500 }
                }, preset.name);
              })
            )
          ),

          // Name + type
          React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, marginBottom: 10 } },
            React.createElement(Inp, { label: 'Template Name', value: caseTplForm.name, onChange: function(e) { setCaseTplForm(Object.assign({}, caseTplForm, { name: e.target.value })); } }),
            React.createElement('div', null,
              React.createElement('label', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted, display: 'block', marginBottom: 4 } }, 'Type'),
              React.createElement('select', { value: caseTplForm.label_type, onChange: function(e) { setCaseTplForm(Object.assign({}, caseTplForm, { label_type: e.target.value })); },
                style: { padding: '8px 12px', borderRadius: 6, border: '1px solid ' + C.border, background: C.bg, color: C.text, fontSize: 12 } },
                React.createElement('option', { value: 'thermal' }, 'Thermal (single)'),
                React.createElement('option', { value: 'sheet' }, 'Sheet (multi-up)')
              )
            )
          ),

          // Label dimensions
          React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 } },
            React.createElement(Inp, { label: 'Label Width (mm)', type: 'number', step: '0.1', value: caseTplForm.width_mm, onChange: function(e) { setCaseTplForm(Object.assign({}, caseTplForm, { width_mm: e.target.value })); } }),
            React.createElement(Inp, { label: 'Label Height (mm)', type: 'number', step: '0.1', value: caseTplForm.height_mm, onChange: function(e) { setCaseTplForm(Object.assign({}, caseTplForm, { height_mm: e.target.value })); } }),
            React.createElement(Inp, { label: 'Font Size (pt)', type: 'number', value: caseTplForm.font_size, onChange: function(e) { setCaseTplForm(Object.assign({}, caseTplForm, { font_size: parseInt(e.target.value) || 12 })); } })
          ),

          // Sheet layout (only for sheet type)
          caseTplForm.label_type === 'sheet' && React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, marginBottom: 10 } },
            React.createElement(Inp, { label: 'Columns', type: 'number', min: 1, value: caseTplForm.columns, onChange: function(e) { setCaseTplForm(Object.assign({}, caseTplForm, { columns: parseInt(e.target.value) || 1 })); } }),
            React.createElement(Inp, { label: 'Rows per Sheet', type: 'number', min: 1, value: caseTplForm.rows_per_sheet, onChange: function(e) { setCaseTplForm(Object.assign({}, caseTplForm, { rows_per_sheet: parseInt(e.target.value) || 1 })); } }),
            React.createElement(Inp, { label: 'Page Width (mm)', type: 'number', step: '0.1', value: caseTplForm.page_width_mm, onChange: function(e) { setCaseTplForm(Object.assign({}, caseTplForm, { page_width_mm: e.target.value })); } }),
            React.createElement(Inp, { label: 'Page Height (mm)', type: 'number', step: '0.1', value: caseTplForm.page_height_mm, onChange: function(e) { setCaseTplForm(Object.assign({}, caseTplForm, { page_height_mm: e.target.value })); } })
          ),

          // ─── PO/WFS: Show Barcode toggle ───
          React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 } },
            React.createElement('button', { onClick: function() { setCaseTplForm(Object.assign({}, caseTplForm, { show_barcode: !caseTplForm.show_barcode })); },
              style: { display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 6,
                border: '1px solid ' + (caseTplForm.show_barcode ? C.accent : C.border), background: caseTplForm.show_barcode ? C.accent + '12' : 'transparent',
                color: caseTplForm.show_barcode ? C.accent : C.text, cursor: 'pointer', fontSize: 11, fontFamily: 'inherit', fontWeight: 500 } },
              React.createElement('div', { style: { width: 16, height: 16, borderRadius: 3, border: '2px solid ' + (caseTplForm.show_barcode ? C.accent : C.textDim), background: caseTplForm.show_barcode ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' } },
                caseTplForm.show_barcode && React.createElement(LIcon, { name: 'check', size: 10, color: '#fff' })
              ),
              'Show Barcode on Label'
            )
          ),

          // ─── PO/WFS: Fields to include ───
          React.createElement('div', { style: { marginBottom: 10 } },
            React.createElement('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 6 } }, 'Fields to Show on Label'),
            React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 6 } },
              (caseFieldOptionsByCategory[caseTplCategory] || []).map(function(opt) {
                var checked = (caseTplForm.fields || []).indexOf(opt.value) >= 0;
                return React.createElement('button', { key: opt.value, onClick: function() { toggleCaseField(opt.value); },
                  style: { display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 6,
                    border: '1px solid ' + (checked ? C.accent : C.border), background: checked ? C.accent + '12' : 'transparent',
                    color: checked ? C.accent : C.text, cursor: 'pointer', fontSize: 11, fontFamily: 'inherit', fontWeight: 500 }
                },
                  React.createElement('div', { style: { width: 16, height: 16, borderRadius: 3, border: '2px solid ' + (checked ? C.accent : C.textDim), background: checked ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' } },
                    checked && React.createElement(LIcon, { name: 'check', size: 10, color: '#fff' })
                  ),
                  opt.label
                );
              })
            )
          ),

          // Default toggle
          React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 } },
            React.createElement('button', { onClick: function() { setCaseTplForm(Object.assign({}, caseTplForm, { is_default: !caseTplForm.is_default })); },
              style: { display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 6,
                border: '1px solid ' + (caseTplForm.is_default ? C.accent : C.border), background: caseTplForm.is_default ? C.accent + '12' : 'transparent',
                color: caseTplForm.is_default ? C.accent : C.text, cursor: 'pointer', fontSize: 11, fontFamily: 'inherit', fontWeight: 500 } },
              React.createElement('div', { style: { width: 16, height: 16, borderRadius: 3, border: '2px solid ' + (caseTplForm.is_default ? C.accent : C.textDim), background: caseTplForm.is_default ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' } },
                caseTplForm.is_default && React.createElement(LIcon, { name: 'check', size: 10, color: '#fff' })
              ),
              'Set as Default'
            )
          ),

          // Preview
          React.createElement('div', { style: { marginBottom: 12 } },
            React.createElement('div', { style: { fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 6 } }, 'Preview'),
            React.createElement('div', { style: { display: 'inline-flex', border: '1px dashed ' + C.border, borderRadius: 6, padding: 12, background: '#fff' } },
              (function() {
                var fl = caseTplForm.fields || [];
                var sc = (caseTplForm.font_size || 12) / 12;
                var sm = Math.round(6 * sc);
                var md = Math.round(7.5 * sc);
                var lg = Math.round(10 * sc);
                var xl = Math.round(11 * sc);
                var lineH = Math.round(9 * sc);
                var divider = { width: '100%', height: 0, borderTop: '1px solid #000', margin: Math.round(4 * sc) + 'px 0' };
                var dividerLight = { width: '100%', height: 0, borderTop: '0.5px solid #999', margin: Math.round(4 * sc) + 'px 0' };
                return React.createElement('div', { style: {
                  width: Math.min(parseFloat(caseTplForm.width_mm) || 100, 180) * 2,
                  minHeight: Math.min(parseFloat(caseTplForm.height_mm) || 100, 180) * 2,
                  border: '1px solid #ccc', borderRadius: 2, padding: 8, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', background: '#fff'
                } },
                  // Company name
                  React.createElement('div', { style: { fontSize: sm, color: '#666', textAlign: 'center', marginBottom: 2 } }, 'Atharva Brands'),
                  // Divider
                  React.createElement('div', { style: divider }),
                  // Barcode
                  caseTplForm.show_barcode && React.createElement('div', { style: { textAlign: 'center', marginBottom: 2, maxWidth: '100%', overflow: 'hidden' }, dangerouslySetInnerHTML: { __html: generateCode128SVG('PO-2026-001', 24, 1) } }),
                  // PO# text
                  React.createElement('div', { style: { fontSize: xl, fontWeight: 700, color: '#000', textAlign: 'center', marginBottom: 2 } }, 'PO# 2026-001'),
                  // Divider
                  React.createElement('div', { style: divider }),
                  // Ship From
                  fl.indexOf('vendor') >= 0 && React.createElement('div', { key: 'vendor' },
                    React.createElement('div', { style: { fontSize: sm, fontWeight: 700, color: '#555' } }, 'SHIP FROM:'),
                    React.createElement('div', { style: { fontSize: md, fontWeight: 700, color: '#000', marginTop: 1 } }, 'Sample Vendor Inc'),
                    React.createElement('div', { style: { fontSize: Math.round(6.5 * sc), color: '#333', lineHeight: 1.3, marginTop: 1 } },
                      React.createElement('div', null, '123 Vendor Street'),
                      React.createElement('div', null, 'Phoenix, AZ, 85001')
                    ),
                    React.createElement('div', { style: dividerLight })
                  ),
                  // Ship To
                  fl.indexOf('warehouse') >= 0 && React.createElement('div', { key: 'warehouse' },
                    React.createElement('div', { style: { fontSize: sm, fontWeight: 700, color: '#555' } }, 'SHIP TO:'),
                    React.createElement('div', { style: { fontSize: md, fontWeight: 700, color: '#000', marginTop: 1 } }, 'Atharva Brands'),
                    React.createElement('div', { style: { fontSize: Math.round(6.5 * sc), color: '#333', lineHeight: 1.3, marginTop: 1 } },
                      React.createElement('div', null, '8235 NW 64 ST UNIT 8'),
                      React.createElement('div', null, 'Miami, FL, 33166'),
                      React.createElement('div', null, 'Phone: 800-405-6670')
                    )
                  )
                );
              })()
            )
          ),

          // Save buttons
          React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } },
            React.createElement(Btn, { size: 'sm', onClick: function() { setEditCaseTpl(null); setCaseTplForm(Object.assign({}, defaultCaseForm, { category: caseTplCategory })); } }, 'Clear'),
            React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: saveCaseTpl, disabled: !caseTplForm.name || !caseTplForm.width_mm || !caseTplForm.height_mm },
              editCaseTpl ? [React.createElement(LIcon, { key: 'icon', name: 'save', size: 14 }), ' Update'] : '+ Create')
          )
        )
      )
    ),

    // ─── TAB: INTEGRATIONS ───
    activeTab === 'integrations' && React.createElement(IntegrationsPanel, { showToast: showToast, onNavigate: onNavigate }),

    // ─── TAB: ANALYSIS ───
    activeTab === 'analysis' && React.createElement('div', { style: { marginTop: 16 } },
      React.createElement(Card, null,
        React.createElement('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 12 } }, 'Default Referral Fee Rates'),
        React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 16, lineHeight: 1.5 } },
          'Set default referral fee percentages used in profitability analysis. For FBA, this is used when Keepa/Amazon data does not return a referral fee. For FBM, this is always used.'),
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, maxWidth: 400 } },
          React.createElement(Inp, { label: 'FBA Default Referral Fee %', type: 'number', step: '0.5', min: '0', max: '100',
            value: settings.fba_default_referral_pct ?? '15', onChange: function(e) { setSettings(Object.assign({}, settings, { fba_default_referral_pct: e.target.value })); } }),
          React.createElement(Inp, { label: 'FBM Default Referral Fee %', type: 'number', step: '0.5', min: '0', max: '100',
            value: settings.fbm_default_referral_pct ?? '15', onChange: function(e) { setSettings(Object.assign({}, settings, { fbm_default_referral_pct: e.target.value })); } })
        ),
        React.createElement('div', { style: { marginTop: 16 } },
          React.createElement('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 12 } }, 'Default Cost Factors'),
          React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 16, lineHeight: 1.5 } },
            'Fallback values used in profitability calculations when a product does not have these fields set.'),
          React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, maxWidth: 400 } },
            React.createElement('div', null,
              React.createElement(Inp, { label: 'Default Inbound Freight %', type: 'number', step: '0.01', min: '0', max: '1',
                value: settings.default_inbound_freight_pct ?? '0', onChange: function(e) { setSettings(Object.assign({}, settings, { default_inbound_freight_pct: e.target.value })); } }),
              React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 4 } }, 'Percentage of unit cost for inbound freight. E.g. 0.1 = 10%.')
            ),
            React.createElement('div', null,
              React.createElement(Inp, { label: 'Default Prep Cost / Unit ($)', type: 'number', step: '0.01', min: '0',
                value: settings.default_prep_cost_unit ?? '0', onChange: function(e) { setSettings(Object.assign({}, settings, { default_prep_cost_unit: e.target.value })); } }),
              React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 4 } }, 'Flat dollar amount per unit for prep/labeling.')
            )
          )
        ),
        React.createElement('div', { style: { marginTop: 16 } },
          React.createElement('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 12 } }, 'Forecasting Flags'),
          React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, maxWidth: 400 } },
            React.createElement('div', null,
              React.createElement(Inp, { label: 'Case Pack Threshold (%)', type: 'number', step: '5', min: '0', max: '100',
                value: settings.case_pack_threshold_pct ?? '50', onChange: function(e) { setSettings(Object.assign({}, settings, { case_pack_threshold_pct: e.target.value })); } }),
              React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 4 } }, 'Flag items where reorder qty is below this % of the case pack. E.g. 50% = flag if reorder qty < half a case.')
            )
          )
        ),
        React.createElement('div', { style: { marginTop: 16 } },
          React.createElement('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 12 } }, 'Keepa Auto-Refresh'),
          React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12, lineHeight: 1.5 } },
            'When on, each night at 3:00 AM ET the system refreshes Keepa pricing/fees for items on ',
            React.createElement('strong', { style: { color: C.text } }, 'draft'),
            ' purchase orders in FBA warehouses (US + CA). Only items whose data is older than 7 days are fetched, and it self-throttles on your Keepa token balance so it never blocks a manual fetch. A large first-time backlog drains over several nights.'),
          React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', minHeight: 34 } },
            React.createElement('input', { type: 'checkbox', checked: settings.keepa_auto_refresh_enabled === true,
              onChange: function(e) { setSettings(Object.assign({}, settings, { keepa_auto_refresh_enabled: e.target.checked })); },
              style: { width: 16, height: 16, accentColor: C.accent, cursor: 'pointer' } }),
            React.createElement('span', { style: { fontSize: 13, color: C.text, fontWeight: 600 } }, 'Enable nightly Keepa refresh for draft FBA POs')
          )
        ),
        React.createElement('div', { style: { marginTop: 16 } },
          React.createElement('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 12 } }, 'Top Revenue Tag'),
          React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12, lineHeight: 1.5 } },
            'Products in the top percent of trailing revenue (all channels, USD-normalized) get a gold TOP chip in the catalog and on purchase orders. Recomputed nightly at 3:00 AM ET; changes here take effect on the next run.'),
          React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, maxWidth: 400 } },
            React.createElement(Inp, { label: 'Revenue Window (days)', type: 'number', step: '1', min: '7', max: '730',
              value: settings.top_revenue_window_days ?? '90', onChange: function(e) { setSettings(Object.assign({}, settings, { top_revenue_window_days: e.target.value })); } }),
            React.createElement(Inp, { label: 'Top Percent (%)', type: 'number', step: '1', min: '1', max: '100',
              value: settings.top_revenue_pct ?? '20', onChange: function(e) { setSettings(Object.assign({}, settings, { top_revenue_pct: e.target.value })); } })
          )
        ),
        React.createElement('div', { style: { marginTop: 16, display: 'flex', justifyContent: 'flex-end' } },
          React.createElement(Btn, { variant: 'primary', onClick: save }, React.createElement(LIcon, { name: 'check', size: 14 }), ' Save Settings')
        )
      )
    ),

    // ─── TAB: FORECASTING ───
    activeTab === 'forecasting' && React.createElement('div', { style: { marginTop: 16 } },
      React.createElement(Card, null,
        React.createElement('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 12 } }, 'Forecasting Algorithm Settings'),
        React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 16, lineHeight: 1.5 } },
          'Configure defaults used by the demand forecasting engine to calculate reorder timing and stock health.'),
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, maxWidth: 700 } },
          React.createElement('div', null,
            React.createElement(Inp, { label: 'Default Lead Time (days)', type: 'number', step: '1', min: '1',
              value: settings.default_lead_time_days ?? '14', onChange: function(e) { setSettings(Object.assign({}, settings, { default_lead_time_days: e.target.value })); } }),
            React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 4 } }, 'Fallback lead time for products without a vendor lead time set.')
          ),
          React.createElement('div', null,
            React.createElement(Inp, { label: 'Reorder Frequency (days)', type: 'number', step: '1', min: '1',
              value: settings.reorder_frequency_days ?? '14', onChange: function(e) { setSettings(Object.assign({}, settings, { reorder_frequency_days: e.target.value })); } }),
            React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 4 } }, 'How often you place orders with vendors. Items within this window appear in "Reorder Now".')
          ),
          React.createElement('div', null,
            React.createElement(Inp, { label: 'Reorder Qty Multiplier (x lead time)', type: 'number', step: '0.25', min: '0.25',
              value: settings.reorder_qty_multiplier ?? '1', onChange: function(e) { setSettings(Object.assign({}, settings, { reorder_qty_multiplier: e.target.value })); } }),
            React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 4 } }, 'Reorder enough stock to cover this many lead times. E.g. 2x with 14-day lead time = order 28 days of stock.')
          ),
          React.createElement('div', null,
            React.createElement('label', { style: { display: 'block', fontSize: 11, fontWeight: 600, color: C.textMuted, marginBottom: 4 } }, 'Safety Stock Service Level'),
            React.createElement('select', { style: { width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid ' + C.border, background: C.bg, color: C.text, fontSize: 13 },
              value: settings.safety_stock_service_level ?? '95', onChange: function(e) { setSettings(Object.assign({}, settings, { safety_stock_service_level: e.target.value })); } },
              React.createElement('option', { value: '85' }, '85% (z=1.04)'),
              React.createElement('option', { value: '90' }, '90% (z=1.28)'),
              React.createElement('option', { value: '95' }, '95% (z=1.65) \u2014 Recommended'),
              React.createElement('option', { value: '97' }, '97% (z=1.88)'),
              React.createElement('option', { value: '99' }, '99% (z=2.33)')
            ),
            React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 4 } }, 'Higher = more safety stock buffer. Safety Stock = z \u00d7 \u03c3(demand) \u00d7 \u221a(lead time).')
          ),
          React.createElement('div', null,
            React.createElement('label', { style: { display: 'block', fontSize: 11, fontWeight: 600, color: C.textMuted, marginBottom: 4 } }, 'Velocity Method'),
            React.createElement('select', { style: { width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid ' + C.border, background: C.bg, color: C.text, fontSize: 13 },
              value: settings.velocity_method ?? 'best_window', onChange: function(e) { setSettings(Object.assign({}, settings, { velocity_method: e.target.value })); } },
              React.createElement('option', { value: 'best_window' }, 'Best Window (shortest with \u22653 sales)'),
              React.createElement('option', { value: 'blended' }, 'Blended (weighted average of all windows)')
            ),
            React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 4 } }, 'Best Window picks one window. Blended averages all with recent data weighted heavier. Products with fewer than 7 days of inventory data automatically use an averaged gross velocity across all windows with sales to prevent inflated estimates. If 30-day units sold exceeds the averaged reorder qty, the system uses the 30-day sell rate instead to avoid under-ordering.')
          ),
          React.createElement('div', null,
            React.createElement(Inp, { label: 'Outlier Cap (\u00d7 median order size)', type: 'number', step: '0.5', min: '0',
              value: settings.outlier_threshold_multiplier ?? '10', onChange: function(e) { setSettings(Object.assign({}, settings, { outlier_threshold_multiplier: e.target.value })); } }),
            React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 4 } }, 'Cap large orders at this multiple of the median. Default 10\u00d7 median lets genuine bulk orders through while trimming B2B outliers. Set to 0 to disable.')
          ),
          React.createElement('div', null,
            React.createElement(Inp, { label: 'Overstock Threshold (days)', type: 'number', step: '1', min: '1',
              value: settings.overstock_threshold_days ?? '60', onChange: function(e) { setSettings(Object.assign({}, settings, { overstock_threshold_days: e.target.value })); } }),
            React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 4 } }, 'Items with days on hand above this are flagged as "Overstock".')
          ),
          React.createElement('div', null,
            React.createElement(Inp, { label: 'Min In-Stock Days for Velocity', type: 'number', step: '1', min: '1',
              value: settings.min_instock_days_velocity ?? '14', onChange: function(e) { setSettings(Object.assign({}, settings, { min_instock_days_velocity: e.target.value })); } }),
            React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 4 } }, 'If a SKU has fewer in-stock days than this in a window, use calendar days instead to avoid inflated velocity.')
          ),
          React.createElement('div', null,
            React.createElement(Inp, { label: 'In-Stock Density Threshold (%)', type: 'number', step: '5', min: '0', max: '100',
              value: settings.instock_density_threshold_pct ?? '50', onChange: function(e) { setSettings(Object.assign({}, settings, { instock_density_threshold_pct: e.target.value })); } }),
            React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 4, lineHeight: 1.6 } },
              React.createElement('div', { style: { marginBottom: 6 } },
                React.createElement('strong', { style: { color: C.text } }, 'What this does:'), ' When a SKU was in stock for fewer than this percentage of days in a velocity window, the forecast falls back to calendar-day velocity (total sold \u00f7 window days) instead of in-stock-adjusted velocity (total sold \u00f7 in-stock days).'
              ),
              React.createElement('div', { style: { marginBottom: 6 } },
                React.createElement('strong', { style: { color: C.text } }, 'Why it matters:'), ' The in-stock adjustment assumes stockout days would have sold at the same rate as in-stock days \u2014 valid for fast-movers, but wrong for sparse-sale SKUs. Example: a SKU with 6 units sold across 5 sale-days in the last 90 days, in stock only 28 of those days. In-stock-adjusted velocity = 6 \u00f7 28 = 0.21/day (inflates forecast to 3 cases). Calendar-day velocity = 6 \u00f7 90 = 0.067/day (forecasts 1 case). The 62 stockout days mostly wouldn\'t have been sale days regardless.'
              ),
              React.createElement('div', { style: { marginBottom: 6 } },
                React.createElement('strong', { style: { color: C.text } }, 'How to tune:'), ' Higher values (60\u201370%) are more conservative \u2014 more SKUs fall back to calendar days. Lower values (30\u201340%) trust the in-stock adjustment more often. Default 50% is a balanced starting point. Set to 0 to disable the guard entirely and always use in-stock-adjusted velocity when the window meets the minimum in-stock days above.'
              ),
              React.createElement('div', null,
                React.createElement('strong', { style: { color: C.text } }, 'Interaction:'), ' This guard applies to all velocity windows (7/14/30/90/180/365d) and is evaluated per-window. It does not affect the Low-Instock cascade, which triggers separately when both 30d AND 90d windows fail the min-in-stock-days threshold.'
              )
            )
          ),
        ),
        // Out-of-stock velocity freeze toggle
        React.createElement('div', { style: { marginTop: 16, maxWidth: 760, paddingTop: 12, borderTop: '1px solid ' + C.border + '44' } },
          React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', minHeight: 34 } },
            React.createElement('input', { type: 'checkbox', checked: settings.velocity_freeze_enabled === true,
              onChange: function(e) { setSettings(Object.assign({}, settings, { velocity_freeze_enabled: e.target.checked })); },
              style: { width: 16, height: 16, accentColor: C.accent, cursor: 'pointer' } }),
            React.createElement('span', { style: { fontSize: 13, color: C.text, fontWeight: 600 } }, 'Freeze velocity on stockout')
          ),
          React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 4, lineHeight: 1.6 } },
            'Off by default. When a SKU hits 0 on-hand, snapshot its current velocity and reorder at that frozen rate instead of the live calc (which decays as the pre-stockout sales age out of the rolling windows). Resumes the live calc once it’s back in stock for ≥ the Min In-Stock Days above; after 720 days out of stock the snapshot is erased and the SKU drops to a one-case-pack (MOQ) re-test. Applies to new stockouts going forward — items already out of stock keep the live calc until they cycle.')
        ),
        React.createElement('div', { style: { marginTop: 16, padding: '12px 16px', borderRadius: 8, background: C.bg, border: '1px solid ' + C.border + '44' } },
          React.createElement('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 8 } }, 'How It Works'),
          React.createElement('div', { style: { fontSize: 12, color: C.textMuted, lineHeight: 1.8 } },
            React.createElement('div', null, React.createElement('strong', { style: { color: C.text } }, 'Velocity'), ' = Units Sold \u00f7 In-Stock Days (stockout days excluded)'),
            React.createElement('div', null, React.createElement('strong', { style: { color: C.text } }, 'Safety Stock'), ' = z-score \u00d7 \u03c3(daily demand) \u00d7 \u221a(Lead Time)'),
            React.createElement('div', null, React.createElement('strong', { style: { color: C.text } }, 'Reorder Qty'), ' = (Velocity \u00d7 Lead Time \u00d7 Multiplier) + Safety Stock \u2212 Total Stock'),
            React.createElement('div', null, React.createElement('strong', { style: { color: C.text } }, 'Days To Order'), ' = Days On Hand \u2212 Lead Time'),
            React.createElement('div', null, React.createElement('strong', { style: { color: C.text } }, 'Reorder Now'), ' = items where Days To Order \u2264 Reorder Frequency, or item is stocked out'),
            React.createElement('div', null, React.createElement('strong', { style: { color: C.text } }, 'Lost Sales Est.'), ' = Velocity \u00d7 Out-of-Stock Days (demand that was missed)'),
            React.createElement('div', null, React.createElement('strong', { style: { color: C.text } }, 'YoY Trend'), ' = compares current window sales to same calendar period last year'),
            React.createElement('div', null, React.createElement('strong', { style: { color: C.text } }, 'Stock Health'), ' = Low Stock \u2264 vendor lead time, Healthy = between lead time and overstock threshold, Overstock > threshold'),
            React.createElement('div', { style: { marginTop: 6, fontSize: 11, fontStyle: 'italic' } }, 'Example: SKU sells 5/day, 14-day lead time, 2x multiplier, 95% service level \u2192 target = 5 \u00d7 14 \u00d7 2 + safety stock = ~155 units. If 40 in stock, reorder ~115.')
          )
        ),
        React.createElement('div', { style: { marginTop: 16, display: 'flex', justifyContent: 'flex-end' } },
          React.createElement(Btn, { variant: 'primary', onClick: save }, React.createElement(LIcon, { name: 'check', size: 14 }), ' Save Settings')
        )
      ),
      // ─── FBM → FBA Transfer Recommendations (separate Card under the Forecasting tab) ───
      React.createElement(Card, { style: { marginTop: 16 } },
        React.createElement('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 12 } }, 'FBM → FBA Transfer Recommendations'),
        React.createElement('div', { style: { fontSize: 12, color: C.textMuted, lineHeight: 1.7, marginBottom: 16 } },
          'When the forecast says an FBA US SKU needs reordering, check if the FBM sibling has surplus beyond the ',
          React.createElement('strong', { style: { color: C.text } }, 'Overstock Threshold'),
          ' above. If so, transfer the surplus from FBM instead of buying new — split across destination FBA warehouses (FBAUS / FBAHAZ / FBAUSKIT / SCNJ / …) resolved from your Zentail mapping. Internal transfers are auto-created as draft ',
          React.createElement('strong', { style: { color: C.text } }, '"Transfer from FBM"'),
          ' POs and skip Bill.com matching.'
        ),
        React.createElement('div', { style: { maxWidth: 720, marginBottom: 16 } },
          React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', minHeight: 34 } },
            React.createElement('input', { type: 'checkbox', checked: settings.transfer_from_fbm_enabled === true,
              onChange: function(e) { setSettings(Object.assign({}, settings, { transfer_from_fbm_enabled: e.target.checked })); },
              style: { width: 16, height: 16, accentColor: C.accent, cursor: 'pointer' } }),
            React.createElement('span', { style: { fontSize: 13, color: C.text, fontWeight: 600 } }, 'Enable FBM → FBA US transfer recommendations')
          ),
          React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 4 } }, 'Off by default. Runs as part of the nightly auto-fill (Phase 0) and on manual Run Now.')
        ),
        React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end' } },
          React.createElement(Btn, { variant: 'primary', onClick: save }, React.createElement(LIcon, { name: 'check', size: 14 }), ' Save Settings')
        )
      ),
      // ─── Auto-Fill Draft POs (separate Card under the Forecasting tab) ───
      React.createElement(Card, { style: { marginTop: 16 } },
        React.createElement('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 12 } }, 'Auto-Fill Draft POs'),
        React.createElement('div', { style: { fontSize: 12, color: C.textMuted, lineHeight: 1.7, marginBottom: 16 } },
          'Each night the system automatically updates draft purchase orders to match the latest forecast. For every vendor + warehouse with items needing reorder, the system finds the existing draft PO (or creates one) and adds any new reorder items, refreshes quantities on existing auto-managed items, and removes items that no longer need reorder. ',
          React.createElement('strong', { style: { color: C.text } }, 'Manually-added and locked items are never touched.'),
          ' Editing a quantity on an auto-managed line automatically locks it. ',
          React.createElement('strong', { style: { color: C.text } }, 'No-Sales SKUs'),
          ' (zero velocity, zero on-hand, zero incoming) are also seeded with one case pack (the MOQ); these lines show ',
          React.createElement('strong', { style: { color: C.text } }, '0%'),
          ' in the % of Case column to indicate no forecast history. To get an email summary after each run, subscribe to ',
          React.createElement('strong', { style: { color: C.text } }, '"Auto-Fill Drafts: Daily Summary"'),
          ' under your user profile (Settings → Users & Roles → your user → Alerts).'
        ),
        // Row 1: form controls — three columns, top-aligned
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 140px 220px', gap: 16, maxWidth: 720, marginBottom: 16 } },
          // Enable toggle
          React.createElement('div', null,
            React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', minHeight: 34 } },
              React.createElement('input', { type: 'checkbox', checked: settings.auto_fill_drafts_enabled === true,
                onChange: function(e) { setSettings(Object.assign({}, settings, { auto_fill_drafts_enabled: e.target.checked })); },
                style: { width: 16, height: 16, accentColor: C.accent, cursor: 'pointer' } }),
              React.createElement('span', { style: { fontSize: 13, color: C.text, fontWeight: 600 } }, 'Enable nightly auto-fill')
            ),
            React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 4 } }, 'Off by default. Toggle on once you\'re happy with how Run Now behaves.')
          ),
          // Hour picker
          React.createElement('div', null,
            React.createElement('label', { style: { display: 'block', fontSize: 11, fontWeight: 600, color: C.textMuted, marginBottom: 4 } }, 'Run Hour'),
            React.createElement('select', { style: { width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid ' + C.border, background: C.bg, color: C.text, fontSize: 13 },
              value: settings.auto_fill_drafts_hour != null ? settings.auto_fill_drafts_hour : 2,
              onChange: function(e) { setSettings(Object.assign({}, settings, { auto_fill_drafts_hour: parseInt(e.target.value) })); } },
              Array.from({ length: 24 }, function(_, h) {
                return React.createElement('option', { key: h, value: h }, String(h).padStart(2, '0') + ':00');
              })
            ),
            React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 4 } }, 'In the timezone at right.')
          ),
          // Timezone picker
          React.createElement('div', null,
            React.createElement('label', { style: { display: 'block', fontSize: 11, fontWeight: 600, color: C.textMuted, marginBottom: 4 } }, 'Timezone'),
            React.createElement('select', { style: { width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid ' + C.border, background: C.bg, color: C.text, fontSize: 13 },
              value: settings.auto_fill_drafts_timezone || 'America/New_York',
              onChange: function(e) { setSettings(Object.assign({}, settings, { auto_fill_drafts_timezone: e.target.value })); } },
              [
                { v: 'America/New_York', l: 'Eastern (New York)' },
                { v: 'America/Chicago', l: 'Central (Chicago)' },
                { v: 'America/Denver', l: 'Mountain (Denver)' },
                { v: 'America/Phoenix', l: 'Arizona (no DST)' },
                { v: 'America/Los_Angeles', l: 'Pacific (Los Angeles)' },
                { v: 'America/Anchorage', l: 'Alaska (Anchorage)' },
                { v: 'Pacific/Honolulu', l: 'Hawaii (Honolulu)' },
                { v: 'UTC', l: 'UTC' },
                { v: 'Europe/London', l: 'London' },
                { v: 'Europe/Paris', l: 'Paris / Berlin' },
                { v: 'Asia/Kolkata', l: 'India (Kolkata)' },
                { v: 'Asia/Shanghai', l: 'China (Shanghai)' },
                { v: 'Asia/Tokyo', l: 'Japan (Tokyo)' },
                { v: 'Australia/Sydney', l: 'Sydney' }
              ].map(function(tz) { return React.createElement('option', { key: tz.v, value: tz.v }, tz.l); })
            ),
            React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 4 } }, 'Auto-adjusts for DST.')
          )
        ),
        // Stale-line trim toggle
        React.createElement('div', { style: { maxWidth: 720, marginBottom: 16, paddingTop: 4, borderTop: '1px solid ' + C.border + '44' } },
          React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', minHeight: 34, marginTop: 8 } },
            React.createElement('input', { type: 'checkbox', checked: settings.auto_fill_trim_stale_enabled === true,
              onChange: function(e) { setSettings(Object.assign({}, settings, { auto_fill_trim_stale_enabled: e.target.checked })); },
              style: { width: 16, height: 16, accentColor: C.accent, cursor: 'pointer' } }),
            React.createElement('span', { style: { fontSize: 13, color: C.text, fontWeight: 600 } }, 'Trim stale auto-managed lines')
          ),
          React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 4 } },
            'Off by default. When on, the run also deletes auto-managed lines whose SKU has more than ~60 days to order even without that line (faded velocity, or overstocked by other incoming), and No-Sales seed lines whose SKU now has stock on hand or inbound elsewhere. Vendor-direct errored carry lines and genuinely stocked-out SKUs are never trimmed.')
        ),
        // Row 2: action buttons, right-aligned (matches main settings save pattern)
        React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 16 } },
          currentUser && currentUser.role === 'admin' && React.createElement(Btn, { variant: 'default', onClick: runAutoFillNow, disabled: autoFillRunning },
            React.createElement(LIcon, { name: autoFillRunning ? 'loader' : 'play', size: 14 }),
            ' ', autoFillRunning ? 'Running...' : 'Run Now'
          ),
          React.createElement(Btn, { variant: 'primary', onClick: save }, React.createElement(LIcon, { name: 'check', size: 14 }), ' Save Settings')
        ),
        // Recent runs (admin only)
        currentUser && currentUser.role === 'admin' && React.createElement('div', { style: { marginTop: 8 } },
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 } },
            React.createElement('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase' } }, 'Recent Runs (last 10)'),
            React.createElement(Btn, { variant: 'ghost', size: 'sm', onClick: loadAutoFillRuns },
              React.createElement(LIcon, { name: 'refresh-cw', size: 12 }), ' Refresh')
          ),
          autoFillRuns.length === 0
            ? React.createElement('div', { style: { fontSize: 12, color: C.textMuted, fontStyle: 'italic', padding: '12px 0' } }, 'No runs yet. Click "Run Now" to trigger one.')
            : React.createElement('div', { style: { overflowX: 'auto' } },
                React.createElement('table', { style: { width: '100%', fontSize: 12, borderCollapse: 'collapse' } },
                  React.createElement('thead', null,
                    React.createElement('tr', { style: { color: C.textMuted, textAlign: 'left' } },
                      ['Started', 'By', 'Trigger', 'Duration', 'Drafts (new/upd)', 'Lines (+/~/−)', 'Status'].map(function(h) {
                        return React.createElement('th', { key: h, style: { padding: '6px 12px', borderBottom: '1px solid ' + C.border, fontWeight: 600 } }, h);
                      })
                    )
                  ),
                  React.createElement('tbody', null,
                    autoFillRuns.map(function(r) {
                      var statusColor = r.status === 'error' ? (C.error || '#EF4444')
                                      : r.status === 'running' ? (C.warning || '#F59E0B')
                                      : (C.success || '#10B981');
                      return React.createElement('tr', { key: r.id, style: { borderBottom: '1px solid ' + (C.border + '44') } },
                        React.createElement('td', { style: { padding: '6px 12px' } }, new Date(r.started_at).toLocaleString()),
                        React.createElement('td', { style: { padding: '6px 12px' } }, r.triggered_by_user_name || 'system'),
                        React.createElement('td', { style: { padding: '6px 12px' } }, r.triggered_by),
                        React.createElement('td', { style: { padding: '6px 12px' } }, r.duration_ms ? (r.duration_ms / 1000).toFixed(1) + 's' : '—'),
                        React.createElement('td', { style: { padding: '6px 12px' } }, (r.drafts_created || 0) + ' / ' + (r.drafts_updated || 0)),
                        React.createElement('td', { style: { padding: '6px 12px' } },
                          '+' + (r.lines_added || 0) + ' / ~' + (r.lines_updated || 0) + ' / −' + (r.lines_removed || 0)),
                        React.createElement('td', { style: { padding: '6px 12px', color: statusColor, fontWeight: 600, textTransform: 'capitalize' } },
                          r.status + (r.error_message ? ' — ' + r.error_message.slice(0, 60) : ''))
                      );
                    })
                  )
                )
              )
        )
      )
    ),

    // ─── TAB: FBA SHIPMENTS ───
    // ─── TAB: MOBILE APP ───
    activeTab === 'mobile' && React.createElement('div', { style: { marginTop: 16 } },
      React.createElement(Card, null,
        React.createElement('div', { style: { display: 'flex', alignItems: 'flex-start', gap: 20 } },
          React.createElement('div', { style: { width: 56, height: 56, borderRadius: 14, background: '#2563EB', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } },
            React.createElement(LIcon, { name: 'smartphone', size: 28, color: '#fff' })
          ),
          React.createElement('div', { style: { flex: 1 } },
            React.createElement('div', { style: { fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 4 } }, 'AtharvaToolz Warehouse'),
            React.createElement('div', { style: { fontSize: 13, color: C.textMuted, lineHeight: 1.5, marginBottom: 16 } }, 'Mobile app for PO check-in, receiving, and barcode scanning. Install on any Android device connected to the internet.'),
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 } },
              React.createElement(MobileVersionChip, null),
              React.createElement('span', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, background: C.bg, border: '1px solid ' + C.border, borderRadius: 5, padding: '3px 8px' } }, 'Android')
            ),
            React.createElement('a', {
              href: '/download.html',
              target: '_blank',
              style: { display: 'inline-flex', alignItems: 'center', gap: 8, background: '#2563EB', color: '#fff', padding: '10px 20px', borderRadius: 8, fontSize: 14, fontWeight: 600, textDecoration: 'none', cursor: 'pointer' }
            },
              React.createElement(LIcon, { name: 'download', size: 16, color: '#fff' }),
              'Download Page'
            )
          )
        )
      ),
      React.createElement(Card, null,
        React.createElement('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 12 } }, 'Installation Instructions'),
        React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 10 } },
          [
            { step: '1', text: 'Open the download page on the Android device or share the link' },
            { step: '2', text: 'Tap "Download APK" and open the downloaded file' },
            { step: '3', text: 'If prompted, allow "Install from unknown sources" in device settings' },
            { step: '4', text: 'Tap Install and open the app' },
            { step: '5', text: 'Log in with the same credentials and 2FA code used on the web app' },
          ].map(function(item) {
            return React.createElement('div', { key: item.step, style: { display: 'flex', alignItems: 'flex-start', gap: 12 } },
              React.createElement('div', { style: { width: 24, height: 24, borderRadius: 12, background: C.accent + '18', color: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 } }, item.step),
              React.createElement('div', { style: { fontSize: 13, color: C.text, lineHeight: 1.5, paddingTop: 2 } }, item.text)
            );
          })
        )
      ),
      React.createElement(Card, null,
        React.createElement('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 12 } }, 'Features'),
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 } },
          [
            { icon: 'clipboard-check', title: 'PO Check-In', desc: 'Scan case labels, assign locations, record pallets & boxes' },
            { icon: 'inbox', title: 'Receive POs', desc: 'Scan items, confirm with product image, assign bins' },
            { icon: 'scan', title: 'Barcode Scanner', desc: 'Built-in camera scanner for barcodes and case labels' },
            { icon: 'zap', title: 'Quick Receive', desc: 'Scan loose items, auto-creates daily work order' },
          ].map(function(f) {
            return React.createElement('div', { key: f.title, style: { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', borderRadius: 8, border: '1px solid ' + C.border } },
              React.createElement(LIcon, { name: f.icon, size: 18, color: C.accent }),
              React.createElement('div', null,
                React.createElement('div', { style: { fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 2 } }, f.title),
                React.createElement('div', { style: { fontSize: 12, color: C.textMuted, lineHeight: 1.4 } }, f.desc)
              )
            );
          })
        )
      )
    ),

    activeTab === 'fba' && React.createElement('div', { style: { marginTop: 16 } },
      // Ship From Addresses
      React.createElement(Card, null,
        React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 } },
          React.createElement('div', null,
            React.createElement('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 4 } }, 'Ship From Addresses'),
            React.createElement('div', { style: { fontSize: 12, color: C.textMuted } }, 'Saved addresses used when creating FBA shipments. Amazon requires a phone number.')
          ),
          React.createElement(Btn, { size: 'sm', variant: 'primary', onClick: function() { setFbaEditAddr(null); setFbaShowForm(true); setFbaAddrForm({ label: '', name: '', address_line1: '', city: '', state: '', postal_code: '', country_code: 'US', phone: '' }); } },
            React.createElement(LIcon, { name: 'plus', size: 13 }), ' Add Address')
        ),
        // Address list
        fbaShipFromAddrs.length === 0
          ? React.createElement('div', { style: { textAlign: 'center', padding: '24px 0', color: C.textMuted, fontSize: 13 } }, 'No ship-from addresses yet. Add one to get started.')
          : React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 8 } },
              fbaShipFromAddrs.map(function(a) {
                return React.createElement('div', { key: a.id, style: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 8, border: '1px solid ' + C.border, background: a.is_default ? 'rgba(79,110,247,0.04)' : 'transparent' } },
                  React.createElement('div', { style: { flex: 1 } },
                    React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 } },
                      React.createElement('span', { style: { fontWeight: 600, fontSize: 13, color: C.text } }, a.label),
                      a.is_default && React.createElement('span', { style: { fontSize: 10, fontWeight: 600, color: C.accent, background: C.accent + '18', padding: '1px 6px', borderRadius: 4 } }, 'DEFAULT')
                    ),
                    React.createElement('div', { style: { fontSize: 12, color: C.textMuted, lineHeight: 1.4 } },
                      a.name && React.createElement('span', null, a.name + ' · '),
                      a.address_line1, ' · ',
                      [a.city, a.state, a.postal_code].filter(Boolean).join(', '),
                      a.phone && React.createElement('span', null, ' · Phone: ' + a.phone)
                    )
                  ),
                  React.createElement('div', { style: { display: 'flex', gap: 4 } },
                    React.createElement('button', { onClick: function() {
                      setFbaEditAddr(a); setFbaShowForm(true);
                      setFbaAddrForm({ label: a.label, name: a.name || '', address_line1: a.address_line1 || '', city: a.city || '', state: a.state || '', postal_code: a.postal_code || '', country_code: a.country_code || 'US', phone: a.phone || '' });
                    }, style: { background: 'none', border: 'none', cursor: 'pointer', color: C.accent, fontSize: 12, padding: 4 } }, React.createElement(LIcon, { name: 'edit-2', size: 14 })),
                    React.createElement('button', { onClick: function() { deleteFbaAddr(a.id); }, style: { background: 'none', border: 'none', cursor: 'pointer', color: C.danger, fontSize: 12, padding: 4 } }, React.createElement(LIcon, { name: 'trash-2', size: 14 }))
                  )
                );
              })
            ),
        // Add/Edit form
        fbaShowForm && React.createElement('div', { style: { marginTop: 16, padding: '14px 16px', borderRadius: 8, border: '1px solid ' + C.border, background: C.surface } },
          React.createElement('div', { style: { fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 10 } }, fbaEditAddr ? 'Edit Address' : 'New Address'),
          React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 } },
            React.createElement(Inp, { label: 'Label *', value: fbaAddrForm.label, onChange: function(e) { setFbaAddrForm(Object.assign({}, fbaAddrForm, { label: e.target.value })); }, placeholder: 'e.g. Main Warehouse' }),
            React.createElement(Inp, { label: 'Name', value: fbaAddrForm.name, onChange: function(e) { setFbaAddrForm(Object.assign({}, fbaAddrForm, { name: e.target.value })); }, placeholder: 'Company / person name' }),
            React.createElement(Inp, { label: 'Address *', value: fbaAddrForm.address_line1, onChange: function(e) { setFbaAddrForm(Object.assign({}, fbaAddrForm, { address_line1: e.target.value })); } }),
            React.createElement(Inp, { label: 'City', value: fbaAddrForm.city, onChange: function(e) { setFbaAddrForm(Object.assign({}, fbaAddrForm, { city: e.target.value })); } }),
            React.createElement(StateSelect, { label: 'State', country: fbaAddrForm.country_code || 'US', value: fbaAddrForm.state, onChange: function(e) { setFbaAddrForm(Object.assign({}, fbaAddrForm, { state: e.target.value })); } }),
            React.createElement(Inp, { label: 'ZIP', value: fbaAddrForm.postal_code, onChange: function(e) { setFbaAddrForm(Object.assign({}, fbaAddrForm, { postal_code: e.target.value })); } }),
            React.createElement(Inp, { label: 'Country', value: fbaAddrForm.country_code, onChange: function(e) { setFbaAddrForm(Object.assign({}, fbaAddrForm, { country_code: e.target.value })); } }),
            React.createElement(Inp, { label: 'Phone', value: fbaAddrForm.phone, onChange: function(e) { setFbaAddrForm(Object.assign({}, fbaAddrForm, { phone: e.target.value })); } })
          ),
          React.createElement('div', { style: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 } },
            React.createElement(Btn, { onClick: function() { setFbaEditAddr(null); setFbaShowForm(false); setFbaAddrForm({ label: '', name: '', address_line1: '', city: '', state: '', postal_code: '', country_code: 'US', phone: '' }); } }, 'Cancel'),
            React.createElement(Btn, { variant: 'primary', onClick: saveFbaAddr }, fbaEditAddr ? 'Update Address' : 'Save Address')
          )
        )
      )
    )
  );
}

// ─── API ACTIVITY LOG COMPONENT ───
// Latest warehouse-app version as the server advertises it (GET /api/auth/mobile-version) — the
// same number the app compares itself against for its update banner.
function MobileVersionChip() {
  var [v, setV] = useState(null);
  useEffect(function() {
    api('/auth/mobile-version').then(function(r) { setV(r && r.latest ? 'v' + r.latest : '?'); }).catch(function() { setV('?'); });
  }, []);
  return React.createElement('span', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, background: C.bg, border: '1px solid ' + C.border, borderRadius: 5, padding: '3px 8px' } }, v || '…');
}

function IntegrationApiLog(props) {
  var channel = props.channel || '';
  var label = props.label || channel || 'all';
  var _s = useState(false), expanded = _s[0], setExpanded = _s[1];
  var _p = useState(1), page = _p[0], setPage = _p[1];
  var _d = useState({ rows: [], total: 0 }), data = _d[0], setData = _d[1];
  var _sd = useState(''), startDate = _sd[0], setStartDate = _sd[1];
  var _ed = useState(''), endDate = _ed[0], setEndDate = _ed[1];
  var _l = useState(false), loading = _l[0], setLoading = _l[1];
  var _tc = useState(0), totalCount = _tc[0], setTotalCount = _tc[1];
  var limit = 50;

  // Fetch count on mount (channel-scoped)
  useEffect(function() {
    var p = '?limit=1';
    if (channel) p += '&channel=' + encodeURIComponent(channel);
    api('/integrations/activity-log' + p).then(function(r) { setTotalCount(r.total); }).catch(function(e) { console.error('[silent-catch]', e); });
  }, [channel]);

  // Fetch data when filters or page change (only when expanded)
  useEffect(function() {
    if (!expanded) return;
    setLoading(true);
    var params = '?page=' + page + '&limit=' + limit;
    if (channel) params += '&channel=' + encodeURIComponent(channel);
    if (startDate) params += '&startDate=' + startDate;
    if (endDate) params += '&endDate=' + endDate;
    api('/integrations/activity-log' + params).then(function(r) {
      setData(r);
      setTotalCount(r.total);
      setLoading(false);
    }).catch(function() { setLoading(false); });
  }, [expanded, page, channel, startDate, endDate]);

  function downloadCsv() {
    var params = '?';
    if (channel) params += 'channel=' + encodeURIComponent(channel) + '&';
    if (startDate) params += 'startDate=' + startDate + '&';
    if (endDate) params += 'endDate=' + endDate + '&';
    fetch('/api/integrations/activity-log/csv' + params, {
      credentials: 'include'
    }).then(function(r) { return r.blob(); }).then(function(blob) {
      var url = URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url;
      a.download = 'api-activity-' + (label || channel || 'all').toLowerCase().replace(/\s+/g, '-') + '-' + new Date().toISOString().slice(0, 10) + '.csv';
      a.click();
      URL.revokeObjectURL(url);
    }).catch(function(e) { console.error('[silent-catch]', e); });
  }

  var channelLabels = { walmart: 'Walmart', shipstation: 'ShipStation', amazon_sp_api: 'Amazon SP-API', keepa: 'Keepa', smtp: 'SMTP', sendgrid: 'SendGrid', anthropic: 'Anthropic', zentail: 'Zentail', ebay: 'eBay', shein: 'SHEIN', scrapfly: 'Scrapfly', fx_rate: 'Exchange Rates', dhl_ecommerce: 'DHL eCommerce', usps: 'USPS', gmail: 'Gmail', dropbox: 'Dropbox', firebase: 'Firebase', usps_portal: 'USPS.com portal' };

  var totalPages = Math.max(1, Math.ceil(data.total / limit));
  var showFrom = data.total === 0 ? 0 : (page - 1) * limit + 1;
  var showTo = Math.min(page * limit, data.total);

  return React.createElement('div', { style: { marginTop: 14, border: '1px solid ' + C.border, borderRadius: 8, overflow: 'hidden' } },
    React.createElement('div', {
      onClick: function() { setExpanded(!expanded); },
      style: { padding: '10px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: C.cardBg, borderBottom: expanded ? '1px solid ' + C.border : 'none' }
    },
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
        React.createElement(LIcon, { name: 'list', size: 14, color: C.textMuted }),
        React.createElement('span', { style: { fontSize: 13, fontWeight: 600, color: C.text } }, 'API Log'),
        React.createElement('span', { style: { fontSize: 10, fontWeight: 600, color: C.accent, background: C.accent + '18', padding: '2px 7px', borderRadius: 8 } },
          totalCount.toLocaleString() + (totalCount === 1 ? ' record' : ' records'))
      ),
      React.createElement('span', { style: { fontSize: 14, color: C.textMuted, transition: 'transform 0.2s',
        transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' } }, '\u25BC')
    ),
    expanded && React.createElement('div', { style: { padding: '12px 14px' } },
      // Filter row (date range only \u2014 channel locked to this integration)
      React.createElement('div', { style: { display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' } },
        React.createElement('label', { style: { fontSize: 11, color: C.textMuted, fontWeight: 600 } }, 'From'),
        React.createElement('input', { type: 'date', value: startDate, onChange: function(e) { setStartDate(e.target.value); setPage(1); },
          style: { padding: '5px 8px', borderRadius: 6, border: '1px solid ' + C.border, background: C.inputBg, color: C.text, fontSize: 12 } }),
        React.createElement('label', { style: { fontSize: 11, color: C.textMuted, fontWeight: 600 } }, 'To'),
        React.createElement('input', { type: 'date', value: endDate, onChange: function(e) { setEndDate(e.target.value); setPage(1); },
          style: { padding: '5px 8px', borderRadius: 6, border: '1px solid ' + C.border, background: C.inputBg, color: C.text, fontSize: 12 } }),
        (startDate || endDate) && React.createElement('button', { onClick: function() { setStartDate(''); setEndDate(''); setPage(1); },
          style: { padding: '5px 10px', borderRadius: 6, border: '1px solid ' + C.border, background: 'transparent', color: C.textMuted, fontSize: 11, cursor: 'pointer' }
        }, 'Clear'),
        React.createElement('button', { onClick: downloadCsv,
          style: { marginLeft: 'auto', padding: '5px 12px', borderRadius: 6, border: '1px solid ' + C.accent, background: 'transparent', color: C.accent, fontSize: 12, fontWeight: 600, cursor: 'pointer' }
        }, 'Download CSV')
      ),
      // Table
      loading
        ? React.createElement('div', { style: { color: C.textMuted, padding: 20, textAlign: 'center', fontSize: 13 } }, 'Loading...')
        : React.createElement('div', { style: { overflowX: 'auto' } },
            React.createElement('table', { style: { width: '100%', borderCollapse: 'collapse', fontSize: 12 } },
              React.createElement('thead', null,
                React.createElement('tr', { style: { borderBottom: '2px solid ' + C.border } },
                  ['Time', 'Channel', 'Method', 'Endpoint', 'Status', 'Response Time', 'Summary'].map(function(h) {
                    return React.createElement('th', { key: h, style: { padding: '8px 6px', textAlign: 'left', color: C.textMuted,
                      fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' } }, h);
                  })
                )
              ),
              React.createElement('tbody', null,
                data.rows.length === 0
                  ? React.createElement('tr', null, React.createElement('td', { colSpan: 7, style: { padding: 20, textAlign: 'center', color: C.textMuted } }, 'No activity log entries found'))
                  : data.rows.map(function(row) {
                      var isError = row.status_code >= 400 || row.error_message;
                      return React.createElement('tr', { key: row.id, style: { borderBottom: '1px solid ' + C.border + '60' } },
                        React.createElement('td', { style: { padding: '6px', whiteSpace: 'nowrap', color: C.textMuted } },
                          new Date(row.created_at).toLocaleString()),
                        React.createElement('td', { style: { padding: '6px' } },
                          React.createElement('span', { style: { fontSize: 11, fontWeight: 600, padding: '2px 6px', borderRadius: 4,
                            background: C.accent + '15', color: C.accent } }, channelLabels[row.channel] || row.channel)),
                        React.createElement('td', { style: { padding: '6px', fontWeight: 600, color: C.text } }, row.method),
                        React.createElement('td', { style: { padding: '6px', color: C.text, maxWidth: 300, overflow: 'hidden',
                          textOverflow: 'ellipsis', whiteSpace: 'nowrap' }, title: row.endpoint }, row.endpoint),
                        React.createElement('td', { style: { padding: '6px' } },
                          row.status_code
                            ? React.createElement('span', { style: { fontWeight: 600, color: isError ? C.danger : C.success } }, row.status_code)
                            : React.createElement('span', { style: { color: row.error_message ? C.danger : C.textMuted } }, row.error_message ? 'ERR' : '—')),
                        React.createElement('td', { style: { padding: '6px', color: C.textMuted, whiteSpace: 'nowrap' } },
                          row.response_time_ms != null ? row.response_time_ms + 'ms' : '—'),
                        React.createElement('td', { style: { padding: '6px', color: isError ? C.danger : C.textMuted, maxWidth: 200,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
                          title: row.error_message || row.request_summary || '' },
                          row.error_message || row.request_summary || '—')
                      );
                    })
              )
            )
          ),
      // Pagination
      data.total > 0 && React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginTop: 12, fontSize: 12, color: C.textMuted } },
        React.createElement('span', null, 'Showing ' + showFrom + '–' + showTo + ' of ' + data.total.toLocaleString()),
        React.createElement('div', { style: { display: 'flex', gap: 6 } },
          React.createElement('button', { disabled: page <= 1, onClick: function() { setPage(page - 1); },
            style: { padding: '4px 12px', borderRadius: 4, border: '1px solid ' + C.border, background: page <= 1 ? C.border : C.inputBg,
              color: page <= 1 ? C.textMuted : C.text, cursor: page <= 1 ? 'default' : 'pointer', fontSize: 12 } }, 'Prev'),
          React.createElement('span', { style: { padding: '4px 8px', fontSize: 12 } }, 'Page ' + page + ' of ' + totalPages),
          React.createElement('button', { disabled: page >= totalPages, onClick: function() { setPage(page + 1); },
            style: { padding: '4px 12px', borderRadius: 4, border: '1px solid ' + C.border, background: page >= totalPages ? C.border : C.inputBg,
              color: page >= totalPages ? C.textMuted : C.text, cursor: page >= totalPages ? 'default' : 'pointer', fontSize: 12 } }, 'Next')
        )
      )
    )
  );
}

// ─── SP-API ACCOUNT CARD (reusable per-account) ───
function SpapiAccountCard({ acct, showToast, onRefresh, expanded, onToggle }) {
  var [form, setForm] = useState({
    client_id: acct.client_id || '',
    client_secret: '',
    refresh_token: '',
    aws_access_key: (acct.config && acct.config.aws_access_key) || '',
    aws_secret_key: '',
    region: (acct.config && acct.config.region) || 'na',
    environment: acct.environment || 'sandbox',
    enabled: acct.enabled || false,
    seller_name: (acct.config && acct.config.seller_name) || '',
    display_name: acct.display_name || '',
  });
  var hasSecret = acct.has_secret;
  var hasRefreshToken = acct.has_refresh_token;
  var hasAwsSecret = acct.has_aws_secret;
  var [testResult, setTestResult] = useState(null);
  var [testing, setTesting] = useState(false);

  var save = async function() {
    try {
      var body = { client_id: form.client_id, environment: form.environment, enabled: form.enabled, display_name: form.display_name, config: { region: form.region, seller_name: form.seller_name } };
      if (form.client_secret) body.client_secret = form.client_secret;
      if (form.refresh_token) body.config.refresh_token = form.refresh_token;
      if (form.aws_access_key) body.config.aws_access_key = form.aws_access_key;
      if (form.aws_secret_key) body.config.aws_secret_key = form.aws_secret_key;
      await api('/integrations/by-id/' + acct.id, { method: 'PUT', body: JSON.stringify(body) });
      setForm(Object.assign({}, form, { client_secret: '', refresh_token: '', aws_secret_key: '' }));
      setTestResult(null);
      onRefresh();
      showToast((acct.display_name || 'Amazon SP-API') + ' credentials saved');
    } catch (e) { showToast(e.message, 'error'); }
  };

  var test = async function() {
    setTesting(true); setTestResult(null);
    try {
      var result = await api('/integrations/by-id/' + acct.id + '/test', { method: 'POST' });
      setTestResult({ success: true, message: result.message });
    } catch (e) { setTestResult({ success: false, message: e.message }); }
    setTesting(false);
  };

  var deleteAccount = async function() {
    if (!(await appConfirm('Delete ' + (acct.display_name || 'this integration') + '? This cannot be undone.', { danger: true, okLabel: 'Delete' }))) return;
    try {
      await api('/integrations/by-id/' + acct.id, { method: 'DELETE' });
      onRefresh();
      showToast((acct.display_name || 'Integration') + ' deleted');
    } catch (e) { showToast(e.message, 'error'); }
  };

  var label = acct.display_name || ('Amazon SP-API' + (acct.marketplace ? ' (' + acct.marketplace + ')' : ''));

  return React.createElement(Card, { style: { padding: 0, overflow: 'hidden' } },
    React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: expanded ? '1px solid ' + C.border : 'none', cursor: 'pointer' }, onClick: onToggle },
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12 } },
        React.createElement('div', { style: { width: 40, height: 40, borderRadius: 8, background: '#FF990015', display: 'flex', alignItems: 'center', justifyContent: 'center' } }, channelIcon('amazon', 28)),
        React.createElement('div', null,
          React.createElement('div', { style: { fontSize: 15, fontWeight: 600, color: C.text } }, label),
          React.createElement('div', { style: { fontSize: 12, color: acct.health_status === 'down' ? C.danger : C.textMuted } }, acct.health_status === 'down' ? 'Down since ' + new Date(acct.health_down_since).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) + (acct.health_last_error ? ' — ' + String(acct.health_last_error).slice(0, 90) : '') : form.enabled && hasSecret ? 'Connected — ' + (form.environment === 'mock' ? 'Mock' : form.environment === 'sandbox' ? 'Sandbox' : 'Production') : 'Not connected')
        )
      ),
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
          React.createElement('div', { style: { width: 8, height: 8, borderRadius: 4, background: form.enabled && hasSecret ? C.success : C.textDim } }),
          React.createElement('span', { style: { fontSize: 12, color: form.enabled && hasSecret ? C.success : C.textMuted, fontWeight: 500 } }, form.enabled && hasSecret ? 'Enabled' : 'Disabled')
        ),
        React.createElement(LIcon, { name: expanded ? 'chevron-up' : 'chevron-down', size: 16, style: { color: C.textMuted } })
      )
    ),
    expanded && React.createElement('div', { style: { padding: '16px 20px' } },
      React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12, lineHeight: 1.5 } },
        'Amazon SP-API' + (acct.marketplace ? ' (' + acct.marketplace + ' marketplace)' : '') + '. Provides FBA inbound shipment management, pricing data, and fee estimates. Manage the app, rotate the LWA client secret, or re-authorize in ',
        React.createElement('a', { href: 'https://sellercentral.amazon.com/sellingpartner/developerconsole', target: '_blank', rel: 'noopener', style: { color: C.accent, fontWeight: 500 } }, 'Amazon Developer Central'),
        acct.marketplace === 'CA' ? '. The Canada app is registered under the atharvaimports@gmail.com login.' : '.'),
      React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 } },
        React.createElement('div', null,
          React.createElement(Inp, { label: 'LWA Client ID', placeholder: 'amzn1.application-oa2-client....',
            value: form.client_id, onChange: function(e) { setForm(Object.assign({}, form, { client_id: e.target.value })); } })),
        React.createElement('div', null,
          React.createElement(Inp, { label: 'LWA Client Secret' + (hasSecret ? ' (saved)' : ''), type: 'password',
            placeholder: hasSecret ? '••••••••••••••••••••' : 'Enter client secret...',
            value: form.client_secret, onChange: function(e) { setForm(Object.assign({}, form, { client_secret: e.target.value })); } }))
      ),
      React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 } },
        React.createElement('div', null,
          React.createElement(Inp, { label: 'Refresh Token' + (hasRefreshToken ? ' (saved)' : ''), type: 'password',
            placeholder: hasRefreshToken ? '••••••••••••••••••••' : 'Atzr|...',
            value: form.refresh_token, onChange: function(e) { setForm(Object.assign({}, form, { refresh_token: e.target.value })); } })),
        React.createElement('div', null,
          React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 4 } }, 'Region'),
          React.createElement('select', { value: form.region, onChange: function(e) { setForm(Object.assign({}, form, { region: e.target.value })); },
            style: { width: '100%', background: C.surface, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit' } },
            React.createElement('option', { value: 'na' }, 'North America (US, CA, MX, BR)'),
            React.createElement('option', { value: 'eu' }, 'Europe (UK, DE, FR, IT, ES, etc.)'),
            React.createElement('option', { value: 'fe' }, 'Far East (JP, AU, SG, IN)')
          ))
      ),
      React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 } },
        React.createElement('div', null,
          React.createElement(Inp, { label: 'AWS Access Key ID', placeholder: 'AKIA...',
            value: form.aws_access_key, onChange: function(e) { setForm(Object.assign({}, form, { aws_access_key: e.target.value })); } })),
        React.createElement('div', null,
          React.createElement(Inp, { label: 'AWS Secret Access Key' + (hasAwsSecret ? ' (saved)' : ''), type: 'password',
            placeholder: hasAwsSecret ? '••••••••••••••••••••' : 'Enter AWS secret key...',
            value: form.aws_secret_key, onChange: function(e) { setForm(Object.assign({}, form, { aws_secret_key: e.target.value })); } }))
      ),
      React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 } },
        React.createElement('div', null,
          React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 4 } }, 'Environment'),
          React.createElement('select', { value: form.environment, onChange: function(e) { setForm(Object.assign({}, form, { environment: e.target.value })); },
            style: { width: '100%', background: C.surface, border: '1px solid ' + C.border, color: form.environment === 'mock' ? '#8b5cf6' : form.environment === 'sandbox' ? C.warning : C.success, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', fontWeight: 600 } },
            React.createElement('option', { value: 'mock', style: { color: '#8b5cf6', fontWeight: 600 } }, 'Mock (Local Testing)'),
            React.createElement('option', { value: 'sandbox', style: { color: '#f59e0b', fontWeight: 600 } }, 'Sandbox'),
            React.createElement('option', { value: 'production', style: { color: '#22c55e', fontWeight: 600 } }, 'Production')
          )),
        React.createElement('div', null,
          React.createElement(Inp, { label: 'Seller Name (for labels)', placeholder: 'e.g. Atharva Brands',
            value: form.seller_name, onChange: function(e) { setForm(Object.assign({}, form, { seller_name: e.target.value })); } }))
      ),
      React.createElement('div', { style: { marginBottom: 12 } },
        React.createElement(Inp, { label: 'Display Name (card title)', placeholder: 'Auto-set by Test Connection — type to override, clear to re-auto',
          value: form.display_name, onChange: function(e) { setForm(Object.assign({}, form, { display_name: e.target.value })); } })
      ),
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 } },
        React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.text, cursor: 'pointer' } },
          React.createElement('input', { type: 'checkbox', checked: form.enabled, onChange: function(e) { setForm(Object.assign({}, form, { enabled: e.target.checked })); } }),
          'Enable')
      ),
      React.createElement('div', { style: { display: 'flex', gap: 8 } },
        React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: save }, React.createElement(LIcon, { name: 'save', size: 14 }), ' Save'),
        hasSecret && React.createElement(Btn, { size: 'sm', onClick: test, disabled: testing }, testing ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Testing...'] : [React.createElement(LIcon, { name: 'flask-conical', size: 14, key: 'ic' }), ' Test Connection']),
        React.createElement(Btn, { size: 'sm', variant: 'ghost', onClick: deleteAccount, style: { color: C.danger, marginLeft: 'auto' } }, React.createElement(LIcon, { name: 'trash-2', size: 14 }), ' Delete')
      ),
      testResult && React.createElement('div', { style: { padding: '10px 14px', borderRadius: 8, marginTop: 12,
        background: testResult.success ? C.success + '15' : C.danger + '15',
        border: '1px solid ' + (testResult.success ? C.success + '40' : C.danger + '40'),
        color: testResult.success ? C.success : C.danger, fontSize: 13 }
      }, testResult.success ? React.createElement(LIcon, { name: 'check', size: 14 }) : React.createElement(LIcon, { name: 'x', size: 14 }), ' ', testResult.message),
      React.createElement(IntegrationApiLog, { channel: 'amazon_sp_api', label: 'Amazon SP-API ' + (acct.marketplace || '') })
    )
  );
}

// ─── SHOPIFY STORE CARD (added stores — multi-store) ───
// The primary store keeps the main Shopify card; every additional store row
// (channel='shopify', marketplace=slug) renders one of these. Creds save/test/delete
// go through the by-id endpoints so each store's row is updated independently.
function ShopifyStoreCard({ store, showToast, onRefresh, expanded, onToggle }) {
  var [form, setForm] = useState({
    shop: (store.config && store.config.shop) || '',
    client_id: store.client_id || '',
    client_secret: '',
    enabled: store.enabled || false,
  });
  var hasSecret = store.has_secret;
  var [testResult, setTestResult] = useState(null);
  var [testing, setTesting] = useState(false);
  var label = store.display_name || 'Shopify store';

  var save = async function() {
    try {
      var body = { client_id: form.client_id, environment: 'production', enabled: form.enabled, config: { shop: form.shop } };
      if (form.client_secret) body.client_secret = form.client_secret;
      await api('/integrations/by-id/' + store.id, { method: 'PUT', body: JSON.stringify(body) });
      setForm(Object.assign({}, form, { client_secret: '' }));
      setTestResult(null);
      onRefresh();
      showToast(label + ' saved');
    } catch (e) { showToast(e.message, 'error'); }
  };

  var test = async function() {
    setTesting(true); setTestResult(null);
    try {
      var result = await api('/integrations/by-id/' + store.id + '/test', { method: 'POST' });
      setTestResult({ success: true, message: result.message });
    } catch (e) { setTestResult({ success: false, message: e.message }); }
    setTesting(false);
  };

  var remove = async function() {
    if (!(await appConfirm('Delete Shopify store "' + label + '"? This cannot be undone.', { danger: true, okLabel: 'Delete' }))) return;
    try {
      await api('/integrations/by-id/' + store.id, { method: 'DELETE' });
      onRefresh();
      showToast(label + ' deleted');
    } catch (e) { showToast(e.message, 'error'); }
  };

  return React.createElement(Card, { style: { padding: 0, overflow: 'hidden', marginTop: 16 } },
    React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: expanded ? '1px solid ' + C.border : 'none', cursor: 'pointer' }, onClick: onToggle },
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12 } },
        React.createElement('div', { style: { width: 40, height: 40, borderRadius: 8, background: '#95BF4715', display: 'flex', alignItems: 'center', justifyContent: 'center' } },
          React.createElement('svg', { width: 26, height: 26, viewBox: '0 0 24 24', style: { display: 'block' } },
            React.createElement('circle', { cx: 12, cy: 12, r: 12, fill: '#95BF47' }),
            React.createElement('text', { x: 12, y: 16.5, textAnchor: 'middle', fill: '#FFFFFF', fontSize: 12, fontWeight: 700, fontFamily: 'Arial' }, 'S')
          )
        ),
        React.createElement('div', null,
          React.createElement('div', { style: { fontSize: 15, fontWeight: 600, color: C.text } }, 'Shopify — ' + label),
          React.createElement('div', { style: { fontSize: 12, color: C.textMuted } }, form.enabled && hasSecret ? 'Connected — ' + (form.shop || 'store') : 'Not connected')
        )
      ),
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
          React.createElement('div', { style: { width: 8, height: 8, borderRadius: 4, background: form.enabled && hasSecret ? C.success : C.textDim } }),
          React.createElement('span', { style: { fontSize: 12, color: form.enabled && hasSecret ? C.success : C.textMuted, fontWeight: 500 } }, form.enabled && hasSecret ? 'Enabled' : 'Disabled')
        ),
        React.createElement(LIcon, { name: expanded ? 'chevron-up' : 'chevron-down', size: 16, style: { color: C.textMuted } })
      )
    ),
    expanded && React.createElement('div', { style: { padding: 20 } },
      React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12, lineHeight: 1.5 } }, 'Additional Shopify store (Dev Dashboard custom app, OAuth client credentials). Use the .myshopify.com domain — not the public store domain.'),
      React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 } },
        React.createElement(Inp, { label: 'Store domain (.myshopify.com)', placeholder: 'your-store.myshopify.com', value: form.shop || '', onChange: function(e) { setForm(Object.assign({}, form, { shop: e.target.value })); } }),
        React.createElement(Inp, { label: 'Client ID', placeholder: 'Dev Dashboard > app > Settings', value: form.client_id || '', onChange: function(e) { setForm(Object.assign({}, form, { client_id: e.target.value })); } })
      ),
      React.createElement('div', { style: { marginBottom: 12 } },
        React.createElement(Inp, { label: 'Client Secret' + (hasSecret ? ' (saved — leave blank to keep)' : ''), type: 'password',
          placeholder: hasSecret ? '••••••••••••••••••••' : 'shpss_...',
          value: form.client_secret || '', onChange: function(e) { setForm(Object.assign({}, form, { client_secret: e.target.value })); } })
      ),
      React.createElement('div', { style: { marginBottom: 12 } },
        React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: C.text } },
          React.createElement('input', { type: 'checkbox', checked: form.enabled, onChange: function(e) { setForm(Object.assign({}, form, { enabled: e.target.checked })); } }),
          'Enable Integration'
        )
      ),
      React.createElement('div', { style: { display: 'flex', gap: 8, marginBottom: 12 } },
        React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: save }, React.createElement(LIcon, { name: 'save', size: 14 }), ' Save'),
        React.createElement(Btn, { size: 'sm', onClick: test, disabled: testing || !hasSecret }, testing ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Testing...'] : [React.createElement(LIcon, { name: 'plug', size: 14, key: 'ic' }), ' Test Connection']),
        React.createElement(Btn, { size: 'sm', variant: 'ghost', onClick: remove, style: { color: C.danger, marginLeft: 'auto' } }, React.createElement(LIcon, { name: 'trash-2', size: 14 }), ' Delete')
      ),
      testResult && React.createElement('div', { style: { padding: '10px 14px', borderRadius: 8, marginBottom: 12,
        background: testResult.success ? C.success + '15' : C.danger + '15',
        border: '1px solid ' + (testResult.success ? C.success + '40' : C.danger + '40'),
        color: testResult.success ? C.success : C.danger, fontSize: 13 }
      }, testResult.success ? React.createElement(LIcon, { name: 'check', size: 14 }) : React.createElement(LIcon, { name: 'x', size: 14 }), ' ', testResult.message)
    )
  );
}

// ─── SHIPPING CARRIERS CARD (DHL eCommerce / USPS direct APIs) ───
// Credential management for the direct-carrier accounts the Shipping app's rate
// shopper and label clients use. Self-contained (fetches /integrations/carriers
// on mount) instead of threading more state through IntegrationsPanel.
// "Save & Test" saves the form first and then runs the live OAuth test, so
// credentials can't sit saved-but-untested by accident.
var CARRIER_CRED_DEFS = {
  dhl_ecommerce: {
    icon: 'truck', title: 'DHL eCommerce',
    desc: 'Direct API for labels, international products, and end-of-day manifests.',
    idLabel: 'Client ID', secretLabel: 'Client secret',
    extras: [
      { key: 'pickup_account', label: 'Pickup account #', hint: '5-digit pickup ID — required for label purchase' },
      { key: 'distribution_center', label: 'Distribution center', hint: 'e.g. USMIA1 (optional)' },
    ],
  },
  usps: {
    icon: 'mail', title: 'USPS',
    desc: 'USPS Ship API for your negotiated rates, labels, and SCAN forms.',
    idLabel: 'Consumer key', secretLabel: 'Consumer secret',
    extras: [
      { key: 'crid', label: 'CRID', hint: 'Customer Registration ID' },
      { key: 'mid', label: 'MID', hint: 'Mailer ID' },
      { key: 'manifest_mid', label: 'Manifest MID', hint: 'optional' },
      { key: 'eps_account', label: 'EPS account #', hint: 'payment account — required for label purchase' },
    ],
    // 2026-09-17: the first big USPS-direct day stopped at $592 — EPS caps card spend per day
    note: 'Labels and SCAN forms bill the EPS account by credit card. In EPS, set "Credit Card Total Anticipated Daily Spend Amount" above your busiest day\'s label spend: once it is reached, USPS refuses every label and SCAN form until the next day.',
  },
  // UPS direct (2026-10-29): Rating + Shipping + Tracking on the account's negotiated rates, domestic
  // first; multi-box orders ship as one UPS shipment with a label per box.
  ups: {
    icon: 'package', title: 'UPS',
    desc: 'UPS Shipping, Rating and Tracking APIs with your negotiated rates — labels, multi-box shipments, voids.',
    idLabel: 'Client ID', secretLabel: 'Client secret',
    extras: [
      { key: 'account_number', label: 'Account number', hint: '6-character shipper number — the account the negotiated rates are on; required for labels' },
    ],
    note: 'Create the app at developer.ups.com → My Apps → Add Apps ("I want to integrate UPS technology into my business"), link this account number and select the Rating, Shipping and Tracking products. The Client ID and Client secret are on the app\'s page. Test (CIE) runs the same app against wwwcie.ups.com.',
  },
};

function CarrierCredRow({ channel, data, refresh, showToast }) {
  var def = CARRIER_CRED_DEFS[channel];
  var mkForm = function() {
    var f = { client_id: '', client_secret: '', environment: (data && data.environment) || 'production' };
    def.extras.forEach(function(x) { f[x.key] = (data && data.extras && data.extras[x.key]) || ''; });
    return f;
  };
  var _o = useState(false); var open = _o[0], setOpen = _o[1];
  var _f = useState(null); var form = _f[0], setForm = _f[1];
  var _b = useState(null); var busy = _b[0], setBusy = _b[1]; // 'save' | 'test'

  var toggleOpen = function() {
    if (!open) setForm(mkForm());
    setOpen(!open);
  };

  var doSave = function() {
    var extras = {};
    def.extras.forEach(function(x) { extras[x.key] = form[x.key]; });
    return api('/integrations/carriers', { method: 'POST', body: JSON.stringify({
      channel: channel, client_id: form.client_id, client_secret: form.client_secret,
      environment: form.environment, extras: extras,
    }) });
  };

  var save = function() {
    setBusy('save');
    doSave()
      .then(function() { showToast('Credentials saved'); refresh(); })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setBusy(null); });
  };

  var saveAndTest = function() {
    setBusy('test');
    doSave()
      .then(function() { return api('/integrations/carriers/test', { method: 'POST', body: JSON.stringify({ channel: channel }) }); })
      .then(function(r) { showToast(r.ok ? 'Connected — ' + r.detail : 'Failed — ' + r.detail, r.ok ? 'success' : 'error'); refresh(); })
      .catch(function(e) { showToast(e.message, 'error'); refresh(); })
      .finally(function() { setBusy(null); });
  };

  var canTest = !!(data && data.configured) || !!(form && form.client_id && form.client_secret);
  var status = !data || !data.configured ? { label: 'awaiting creds', color: C.warning }
    : data.last_test && data.last_test.ok ? { label: 'connected', color: C.success }
    : data.last_test ? { label: 'auth failed', color: C.danger }
    : { label: 'saved — untested', color: C.warning };

  var inpS = { background: C.bg, color: C.text, border: '1px solid ' + C.border, borderRadius: 6, padding: '7px 10px', fontSize: 13, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' };
  var field = function(label, hint, node) {
    return React.createElement('div', null,
      React.createElement('div', { style: { fontSize: 11, color: C.textMuted, fontWeight: 600, marginBottom: 3 } }, label,
        hint && React.createElement('span', { style: { fontWeight: 400, color: C.textDim } }, ' — ' + hint)),
      node);
  };

  return React.createElement('div', { style: { borderTop: '1px solid ' + C.border } },
    React.createElement('div', { onClick: toggleOpen, style: { display: 'flex', gap: 12, padding: '13px 20px', alignItems: 'center', cursor: 'pointer' } },
      React.createElement(LIcon, { name: open ? 'chevron-down' : 'chevron-right', size: 15, style: { color: C.textMuted } }),
      React.createElement(LIcon, { name: def.icon, size: 17, style: { color: C.accent } }),
      React.createElement('div', { style: { flex: 1, minWidth: 0 } },
        React.createElement('div', { style: { fontSize: 13, fontWeight: 600, color: C.text } }, def.title),
        React.createElement('div', { style: { fontSize: 11, color: C.textMuted, lineHeight: 1.4 } },
          data && data.last_test
            ? (data.last_test.ok ? '✓ ' : '✕ ') + data.last_test.detail + ' · ' + new Date(data.last_test.at).toLocaleString()
            : def.desc)
      ),
      React.createElement('span', { style: { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', padding: '3px 8px', borderRadius: 4, background: status.color + '22', color: status.color, whiteSpace: 'nowrap' } }, status.label)
    ),
    open && form && React.createElement('div', { style: { padding: '2px 20px 16px 47px', display: 'flex', flexDirection: 'column', gap: 10 } },
      React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 } },
        field(def.idLabel, data && data.client_id_masked ? 'saved: ' + data.client_id_masked : null,
          React.createElement('input', { value: form.client_id, onChange: function(e) { setForm(Object.assign({}, form, { client_id: e.target.value })); },
            placeholder: data && data.configured ? '(unchanged)' : '', autoComplete: 'off', style: inpS })),
        field(def.secretLabel, null,
          React.createElement('input', { type: 'password', value: form.client_secret, onChange: function(e) { setForm(Object.assign({}, form, { client_secret: e.target.value })); },
            placeholder: data && data.configured ? '(unchanged)' : '', autoComplete: 'new-password', style: inpS }))
      ),
      React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 } },
        def.extras.map(function(x) {
          return React.createElement('div', { key: x.key }, field(x.label, x.hint,
            React.createElement('input', { value: form[x.key], onChange: function(e) { var n = {}; n[x.key] = e.target.value; setForm(Object.assign({}, form, n)); }, autoComplete: 'off', style: inpS })));
        })
      ),
      def.note && React.createElement('div', { style: { fontSize: 11, color: C.textMuted, lineHeight: 1.45, margin: '2px 0 10px' } }, def.note),
      field('Environment', null,
        React.createElement('select', { value: form.environment, onChange: function(e) { setForm(Object.assign({}, form, { environment: e.target.value })); },
          style: Object.assign({}, inpS, { width: 220, cursor: 'pointer' }) },
          React.createElement('option', { value: 'production' }, 'Production'),
          React.createElement('option', { value: 'sandbox' }, channel === 'usps' ? 'Test (TEM)' : channel === 'ups' ? 'Test (CIE)' : 'Sandbox'))),
      React.createElement('div', { style: { display: 'flex', gap: 8, marginTop: 4 } },
        React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: saveAndTest, disabled: !!busy || !canTest,
          title: canTest ? 'Saves the form, then runs a live authentication test' : 'Enter credentials first' },
          busy === 'test' ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Testing…'] : [React.createElement(LIcon, { name: 'plug', size: 14, key: 'ic' }), ' Save & Test']),
        React.createElement(Btn, { size: 'sm', onClick: save, disabled: !!busy }, busy === 'save' ? 'Saving…' : 'Save only')
      )
    )
  );
}

function ShippingCarriersCard({ showToast }) {
  var _c = useState(null); var carriers = _c[0], setCarriers = _c[1]; // {channel: rowData}
  var load = function() {
    api('/integrations/carriers').then(function(r) {
      var m = {};
      (r.carriers || []).forEach(function(c) { m[c.channel] = c; });
      setCarriers(m);
    }).catch(function() { setCarriers({}); });
  };
  useEffect(function() { load(); }, []);
  var channels = Object.keys(CARRIER_CRED_DEFS);
  var connected = carriers ? channels.filter(function(ch) { return carriers[ch] && carriers[ch].last_test && carriers[ch].last_test.ok; }).length : 0;
  return React.createElement(Card, { style: { padding: 0, overflow: 'hidden', marginTop: 16 } },
    React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px' } },
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12 } },
        React.createElement('div', { style: { width: 40, height: 40, borderRadius: 8, background: '#FFCC0022', display: 'flex', alignItems: 'center', justifyContent: 'center' } }, React.createElement(LIcon, { name: 'truck', size: 22 })),
        React.createElement('div', null,
          React.createElement('div', { style: { fontSize: 15, fontWeight: 600, color: C.text } }, 'Shipping Carriers'),
          React.createElement('div', { style: { fontSize: 12, color: C.textMuted } }, 'DHL eCommerce, USPS & UPS direct APIs — used by the Shipping app for rates and labels')
        )
      ),
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
        React.createElement('div', { style: { width: 8, height: 8, borderRadius: 4, background: connected ? C.success : C.textDim } }),
        React.createElement('span', { style: { fontSize: 12, color: connected ? C.success : C.textMuted, fontWeight: 500 } }, connected + ' of ' + channels.length + ' connected')
      )
    ),
    carriers === null
      ? React.createElement('div', { style: { padding: '13px 20px', fontSize: 12, color: C.textMuted, borderTop: '1px solid ' + C.border } }, 'Loading…')
      : channels.map(function(ch) {
          return React.createElement(CarrierCredRow, { key: ch, channel: ch, data: carriers[ch], refresh: load, showToast: showToast });
        })
  );
}

// ─── INTEGRATIONS PANEL ───
function IntegrationsPanel({ showToast, onNavigate }) {
  const [walmart, setWalmart] = useState({ client_id: '', client_secret: '', environment: 'sandbox', enabled: false });
  const [ebayInt, setEbayInt] = useState({ client_id: '', client_secret: '', refresh_token: '', environment: 'production', enabled: false });
  const [hasEbaySecret, setHasEbaySecret] = useState(false);
  const [hasEbayRefresh, setHasEbayRefresh] = useState(false);
  const [ebayVerifToken, setEbayVerifToken] = useState('');
  const [sheinSummary, setSheinSummary] = useState(null);
  const [sheinUploading, setSheinUploading] = useState(false);
  const [sheinResult, setSheinResult] = useState(null);
  const sheinFileRef = useRef(null);
  const [sheinInt, setSheinInt] = useState({ client_id: '', client_secret: '', enabled: false, shopify_integration_id: '' });
  const [hasSheinSecret, setHasSheinSecret] = useState(false);
  const [sheinStore, setSheinStore] = useState({ authorized: false, store_name: '', supplier_id: '', authorized_at: null });
  const [sheinTest, setSheinTest] = useState(null);
  const [sheinTesting, setSheinTesting] = useState(false);
  const [sheinCode, setSheinCode] = useState('');
  const [sheinExchanging, setSheinExchanging] = useState(false);
  const [anthropic, setAnthropic] = useState({ api_key: '', enabled: false });
  const [shopify, setShopify] = useState({ shop: '', client_id: '', client_secret: '', enabled: false });
  const [shopifyStores, setShopifyStores] = useState([]); // ALL shopify rows (primary + added) — cards render the added ones, WM CA store picker uses all
  const [gmailAccounts, setGmailAccounts] = useState([]); // ALL gmail rows (multi-account like shopify)
  const [gmailTests, setGmailTests] = useState({}); // row id → { testing } | { success, message }
  const [dropbox, setDropbox] = useState({ app_key: '', app_secret: '', root_path: '', enabled: false, auth_code: '' });
  const [hasDropboxSecret, setHasDropboxSecret] = useState(false);
  const [hasDropboxToken, setHasDropboxToken] = useState(false);
  const [dropboxTest, setDropboxTest] = useState(null);
  const [dropboxTesting, setDropboxTesting] = useState(false);
  const [dropboxConnecting, setDropboxConnecting] = useState(false);
  const [hasShopifySecret, setHasShopifySecret] = useState(false);
  const [shopifyTest, setShopifyTest] = useState(null);
  const [shopifyTesting, setShopifyTesting] = useState(false);
  const [wmca, setWmca] = useState({ client_id: '', client_secret: '', enabled: false, shopify_integration_id: '' });
  const [hasWmcaSecret, setHasWmcaSecret] = useState(false);
  const [wmcaTest, setWmcaTest] = useState(null);
  const [wmcaTesting, setWmcaTesting] = useState(false);
  const [amzAds, setAmzAds] = useState({ client_id: '', client_secret: '', enabled: false });
  const [hasAmzAdsSecret, setHasAmzAdsSecret] = useState(false);
  const [hasAmzAdsRefresh, setHasAmzAdsRefresh] = useState(false);
  const [amzAdsProfiles, setAmzAdsProfiles] = useState({ profiles: [], profile_id_ca: null, profile_id_us: null });
  const [amzAdsTest, setAmzAdsTest] = useState(null);
  const [amzAdsTesting, setAmzAdsTesting] = useState(false);
  const [amzAdsCode, setAmzAdsCode] = useState('');
  const [amzAdsCodeSlot, setAmzAdsCodeSlot] = useState('us'); // which Amazon login a pasted code belongs to
  const [amzAdsExchanging, setAmzAdsExchanging] = useState(false);
  const [adsSyncEnabled, setAdsSyncEnabled] = useState(null); // null = settings not readable by this user
  const [adsSyncStatus, setAdsSyncStatus] = useState(null);
  const [adsSyncing, setAdsSyncing] = useState(false);
  const [meliInt, setMeliInt] = useState({ client_id: '', client_secret: '', enabled: false });
  const [hasMeliSecret, setHasMeliSecret] = useState(false);
  const [hasMeliRefresh, setHasMeliRefresh] = useState(false);
  const [meliAccount, setMeliAccount] = useState({ user_id: null, nickname: null, site_id: null });
  const [meliTest, setMeliTest] = useState(null);
  const [meliTesting, setMeliTesting] = useState(false);
  const [meliCode, setMeliCode] = useState('');
  const [meliExchanging, setMeliExchanging] = useState(false);
  const [meliLastSync, setMeliLastSync] = useState(null);
  const [meliSyncing, setMeliSyncing] = useState(null); // 'orders' | 'inventory' | null
  const [loaded, setLoaded] = useState(false);
  const [expandedInt, setExpandedInt] = useState({}); // { amazon: true, anthropic: false, ... }
  var toggleInt = function(key) { setExpandedInt(function(prev) { var n = Object.assign({}, prev); n[key] = !n[key]; return n; }); };
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [hasSecret, setHasSecret] = useState(false);
  const [hasAnthropicKey, setHasAnthropicKey] = useState(false);
  const [anthropicTest, setAnthropicTest] = useState(null);
  const [anthropicTesting, setAnthropicTesting] = useState(false);
  const [keepa, setKeepa] = useState({ api_key: '', enabled: false });
  const [hasKeepaKey, setHasKeepaKey] = useState(false);
  const [keepaTest, setKeepaTest] = useState(null);
  const [keepaTesting, setKeepaTesting] = useState(false);
  const [spapiAccounts, setSpapiAccounts] = useState([]);
  const [intHealth, setIntHealth] = useState(null); // integration_health rows (probe + passive watch), null until loaded
  const [existingChannels, setExistingChannels] = useState({});
  const [addNewModal, setAddNewModal] = useState(null); // { channel: '', marketplace: '' }
  const [intTypes, setIntTypes] = useState(null);
  const [shipstation, setShipstation] = useState({ api_key: '', api_secret: '', enabled: false, last_sync_at: null });
  const [hasShipstationSecret, setHasShipstationSecret] = useState(false);
  // USPS.com portal login (Tracy, Phase 2 2026-10-13): username + password, optional mailbox for one-time codes
  const [uspsPortal, setUspsPortal] = useState({ username: '', password: '', otp_user: '', otp_pass: '', enabled: false });
  const [hasUspsPortalSecret, setHasUspsPortalSecret] = useState(false);
  const [hasUspsOtpPass, setHasUspsOtpPass] = useState(false);
  const [uspsPortalTest, setUspsPortalTest] = useState(null);
  const [uspsPortalTesting, setUspsPortalTesting] = useState(false);
  const [shipstationTest, setShipstationTest] = useState(null);
  const [shipstationTesting, setShipstationTesting] = useState(false);
  const [shipstationSyncing, setShipstationSyncing] = useState(false);
  const [shipstationSyncResult, setShipstationSyncResult] = useState(null);
  const [shipstationSyncMode, setShipstationSyncMode] = useState(null);
  const [shipstationSyncStartedAt, setShipstationSyncStartedAt] = useState(null);
  const shipstationPollRef = useRef(null);
  const [smtp, setSmtp] = useState({ host: '', port: '587', username: '', password: '', from_email: '', from_name: '', enabled: false });
  const [hasSmtpPass, setHasSmtpPass] = useState(false);
  const [smtpTest, setSmtpTest] = useState(null);
  const [smtpTesting, setSmtpTesting] = useState(false);
  // Firebase (text-message 2FA codes, 9-05): client_id = project id, client_secret = Web API key
  const [firebase, setFirebase] = useState({ project_id: '', api_key: '', enabled: false });
  const [hasFirebaseKey, setHasFirebaseKey] = useState(false);
  const [firebaseTest, setFirebaseTest] = useState(null);
  const [firebaseTesting, setFirebaseTesting] = useState(false);
  const [sendgrid, setSendgrid] = useState({ api_key: '', from_email: '', from_name: '', enabled: false });
  const [hasSendgridKey, setHasSendgridKey] = useState(false);
  const [sendgridTest, setSendgridTest] = useState(null);
  const [sendgridTesting, setSendgridTesting] = useState(false);
  const [fxRate, setFxRate] = useState({ enabled: false });
  const [fxRates, setFxRates] = useState(null);
  const [fxTest, setFxTest] = useState(null);
  const [fxTesting, setFxTesting] = useState(false);
  const [zentail, setZentail] = useState({ api_token: '', enabled: false });
  const [hasZentailSecret, setHasZentailSecret] = useState(false);
  const [zentailTest, setZentailTest] = useState(null);
  const [zentailTesting, setZentailTesting] = useState(false);
  const [zentailSyncing, setZentailSyncing] = useState(null); // 'orders' | 'products' | null
  const [zentailSyncResult, setZentailSyncResult] = useState(null);
  const [backfillModal, setBackfillModal] = useState(null); // { startDate, endDate }
  const [zentailLastSync, setZentailLastSync] = useState(null);
  const [zentailLiveStatus, setZentailLiveStatus] = useState(null);
  // Scrapfly
  const [scrapfly, setScrapfly] = useState({ api_key: '', enabled: false });
  const [hasScrapflyKey, setHasScrapflyKey] = useState(false);
  // Bill.com
  const [billdc, setBilldc] = useState({ username: '', password: '', dev_key: '', org_id: '', environment: 'production', enabled: false });
  const [hasBilldcPass, setHasBilldcPass] = useState(false);
  const [hasBilldcKey, setHasBilldcKey] = useState(false);
  const [billdcLastSync, setBilldcLastSync] = useState(null);
  const [billdcTest, setBilldcTest] = useState(null);
  const [billdcTesting, setBilldcTesting] = useState(false);
  const [billdcSyncing, setBilldcSyncing] = useState(false);
  const [billdcSyncResult, setBilldcSyncResult] = useState(null);
  var saveBilldcCreds = async function() {
    try {
      var body = {
        client_id: billdc.username, // username goes into client_id
        environment: billdc.environment,
        enabled: billdc.enabled,
        config: { orgId: billdc.org_id },
      };
      if (billdc.password) body.client_secret = billdc.password;
      if (billdc.dev_key) body.config.devKey = billdc.dev_key;
      await api('/integrations/billdotcom', { method: 'PUT', body: JSON.stringify(body) });
      var fresh = await api('/integrations/billdotcom');
      if (fresh) {
        var cfg = fresh.config || {};
        setBilldc({ username: fresh.client_id || '', password: '', dev_key: '', org_id: cfg.orgId || '', environment: fresh.environment || 'production', enabled: fresh.enabled || false });
        setHasBilldcPass(fresh.has_secret || false);
        setHasBilldcKey(!!cfg.devKey || !!cfg.has_devKey);
        setBilldcLastSync(fresh.last_sync_at || null);
      }
      setBilldcTest(null);
      showToast('Bill.com credentials saved');
    } catch (e) { showToast(e.message, 'error'); }
  };
  var testBilldcConnection = async function() {
    setBilldcTesting(true); setBilldcTest(null);
    try {
      var result = await api('/integrations/billdotcom/test', { method: 'POST' });
      setBilldcTest({ success: true, message: result.message });
    } catch (e) { setBilldcTest({ success: false, message: e.message }); }
    setBilldcTesting(false);
  };
  var syncBilldc = async function(full) {
    setBilldcSyncing(true); setBilldcSyncResult(null);
    try {
      var result = await api('/integrations/billdotcom/sync' + (full ? '?full=1' : ''), { method: 'POST' });
      setBilldcSyncResult(result);
      var fresh = await api('/integrations/billdotcom');
      if (fresh) setBilldcLastSync(fresh.last_sync_at);
      showToast('Bill.com sync: ' + result.bills + ' bills, ' + result.invoices + ' invoices, ' + result.pos_matched + ' POs matched');
      loadVendorMappings();
    } catch (e) { showToast(e.message, 'error'); }
    setBilldcSyncing(false);
  };
  // Bill.com vendor mapping state
  const [billdcUnmapped, setBilldcUnmapped] = useState(null);
  const [billdcMappings, setBilldcMappings] = useState(null);
  const [billdcOurVendors, setBilldcOurVendors] = useState(null);
  const [billdcMapPicks, setBilldcMapPicks] = useState({}); // { bdc_vendor_id: vendor_id }
  const [billdcShowMapping, setBilldcShowMapping] = useState(false);
  var loadVendorMappings = function() {
    Promise.all([
      api('/integrations/billdotcom/unmapped-vendors').catch(function() { return []; }),
      api('/integrations/billdotcom/vendor-mappings').catch(function() { return []; }),
      api('/vendors?active=true').catch(function() { return []; }),
    ]).then(function(rs) { setBilldcUnmapped(rs[0]); setBilldcMappings(rs[1]); setBilldcOurVendors(rs[2]); });
  };
  var saveVendorMapping = async function(bdcVendorId, bdcVendorName, vendorId) {
    if (!vendorId) { showToast('Pick a vendor first', 'error'); return; }
    try {
      await api('/integrations/billdotcom/vendor-mappings', { method: 'POST',
        body: JSON.stringify({ bdc_vendor_id: bdcVendorId, bdc_vendor_name: bdcVendorName, vendor_id: parseInt(vendorId) }) });
      showToast('Vendor mapped');
      loadVendorMappings();
    } catch (e) { showToast(e.message, 'error'); }
  };
  var removeVendorMapping = async function(bdcVendorId) {
    if (!(await appConfirm('Remove this vendor mapping? Future bill syncs will auto-match by name again.', { danger: true, okLabel: 'Remove' }))) return;
    try {
      await api('/integrations/billdotcom/vendor-mappings/' + encodeURIComponent(bdcVendorId), { method: 'DELETE' });
      showToast('Mapping removed');
      loadVendorMappings();
    } catch (e) { showToast(e.message, 'error'); }
  };
  var saveScrapfly = async function() {
    try {
      var body = { enabled: scrapfly.enabled };
      if (scrapfly.api_key) body.client_secret = scrapfly.api_key;
      await api('/integrations/scrapfly', { method: 'PUT', body: JSON.stringify(body) });
      setHasScrapflyKey(true);
      showToast('Scrapfly credentials saved');
    } catch (e) { showToast(e.message, 'error'); }
  };
  // WFS Fee Settings
  const [wfsFeeTiers, setWfsFeeTiers] = useState([]);
  const [wfsCategoryFees, setWfsCategoryFees] = useState([]);
  const [wfsSurcharges, setWfsSurcharges] = useState([]);
  const [wfsFeeLoaded, setWfsFeeLoaded] = useState(false);
  const [wfsFeeSaving, setWfsFeeSaving] = useState(false);
  const [wfsPrepFees, setWfsPrepFees] = useState({ wfs_label_fee: '', wfs_bag_fee: '' });
  var loadWfsFeeSettings = function() {
    Promise.all([
      api('/settings/wfs-fee-tiers'), api('/settings/wfs-category-fees'), api('/settings/wfs-surcharges'), api('/settings')
    ]).then(function(r) { setWfsFeeTiers(r[0]); setWfsCategoryFees(r[1]); setWfsSurcharges(r[2]); setWfsPrepFees({ wfs_label_fee: (r[3] && r[3].wfs_label_fee != null ? r[3].wfs_label_fee : ''), wfs_bag_fee: (r[3] && r[3].wfs_bag_fee != null ? r[3].wfs_bag_fee : '') }); setWfsFeeLoaded(true); }).catch(function(e) { console.error('[silent-catch]', e); });
  };
  var saveWfsFeeTiers = async function() {
    setWfsFeeSaving(true);
    try {
      var result = await api('/settings/wfs-fee-tiers', { method: 'PUT', body: JSON.stringify(wfsFeeTiers) });
      setWfsFeeTiers(result);
      showToast('Fee tiers saved');
    } catch (e) { showToast(e.message, 'error'); }
    setWfsFeeSaving(false);
  };
  var saveWfsSurcharges = async function() {
    setWfsFeeSaving(true);
    try {
      var result = await api('/settings/wfs-surcharges', { method: 'PUT', body: JSON.stringify(wfsSurcharges) });
      setWfsSurcharges(result);
      showToast('Surcharges saved');
    } catch (e) { showToast(e.message, 'error'); }
    setWfsFeeSaving(false);
  };
  var saveWfsPrepFees = async function() {
    setWfsFeeSaving(true);
    try {
      await api('/settings', { method: 'PUT', body: JSON.stringify({ wfs_label_fee: wfsPrepFees.wfs_label_fee, wfs_bag_fee: wfsPrepFees.wfs_bag_fee }) });
      showToast('Prep fees saved');
    } catch (e) { showToast(e.message, 'error'); }
    setWfsFeeSaving(false);
  };
  var addWfsCategory = async function() {
    try {
      var result = await api('/settings/wfs-category-fees', { method: 'POST', body: JSON.stringify({ category: 'New Category', referral_fee_pct: 15, price_up_to: null }) });
      setWfsCategoryFees(wfsCategoryFees.concat([result]));
    } catch (e) { showToast(e.message, 'error'); }
  };
  var updateWfsCategory = async function(id, field, value) {
    var cat = wfsCategoryFees.find(function(c) { return c.id === id; });
    if (!cat) return;
    var updated = Object.assign({}, cat, { [field]: field === 'price_up_to' && !value ? null : value });
    try {
      await api('/settings/wfs-category-fees/' + id, { method: 'PUT', body: JSON.stringify(updated) });
      setWfsCategoryFees(wfsCategoryFees.map(function(c) { return c.id === id ? updated : c; }));
    } catch (e) { showToast(e.message, 'error'); }
  };
  var deleteWfsCategory = async function(id) {
    try {
      await api('/settings/wfs-category-fees/' + id, { method: 'DELETE' });
      setWfsCategoryFees(wfsCategoryFees.filter(function(c) { return c.id !== id; }));
    } catch (e) { showToast(e.message, 'error'); }
  };
  function fmtDuration(ms) {
    if (!ms) return '';
    var secs = Math.round(ms / 1000);
    if (secs < 60) return ' (' + secs + 's)';
    var mins = Math.floor(secs / 60);
    var remSecs = secs % 60;
    if (mins < 60) return ' (' + mins + 'm ' + remSecs + 's)';
    var hrs = Math.floor(mins / 60);
    var remMins = mins % 60;
    return ' (' + hrs + 'h ' + remMins + 'm)';
  }
  const [zentailMappingStats, setZentailMappingStats] = useState(null);
  const [zentailWhMappings, setZentailWhMappings] = useState([]);
  const [zentailWhConfig, setZentailWhConfig] = useState({});
  const [allWarehouses, setAllWarehouses] = useState([]);

  useEffect(function() {
    var loaded1 = false, loaded2 = false, loaded3 = false, loaded4 = false, loaded5 = false, loaded6 = false, loaded7 = false, loaded8 = false, loaded9 = false;
    var loaded10 = false, loaded11 = false;
    var checkDone = function() { if (loaded1 && loaded2 && loaded3 && loaded4 && loaded5 && loaded6 && loaded7 && loaded8 && loaded9 && loaded10 && loaded11) setLoaded(true); };
    api('/integrations/ebay').then(function(data) {
      if (data) {
        setEbayInt({ client_id: data.client_id || '', client_secret: '', refresh_token: '', runame: (data.config && data.config.runame) || '', environment: data.environment || 'production', enabled: data.enabled || false, last_sync_at: data.last_sync_at, display_name: data.display_name || '' });
        setHasEbaySecret(!!data.client_id);
        setHasEbayRefresh(!!data.has_refresh_token); // the server strips refresh_token from config and sends this flag (review 2026-09-28, medium #35: the card never showed "connected")
      }
      loaded11 = true; checkDone();
    }).catch(function() { loaded11 = true; checkDone(); });
    // Load the eBay MAD verification token from settings (separate from integrations row)
    api('/settings').then(function(data) {
      if (data && data.ebay_verification_token) setEbayVerifToken(data.ebay_verification_token);
    }).catch(function() {});
    // Load SHEIN returns import summary
    api('/integrations/shein/returns-summary').then(function(data) {
      if (data) setSheinSummary(data);
    }).catch(function() {});
    api('/integrations/walmart').then(function(data) {
      if (data) {
        setWalmart({ client_id: data.client_id || '', client_secret: '', environment: data.environment || 'sandbox', enabled: data.enabled || false, last_sync_at: data.last_sync_at, display_name: data.display_name || '' });
        setHasSecret(!!data.client_id);
      }
      loaded1 = true; checkDone();
    }).catch(function() { loaded1 = true; checkDone(); });
    api('/integrations/anthropic').then(function(data) {
      if (data) {
        setAnthropic({ api_key: '', enabled: data.enabled || false });
        setHasAnthropicKey(data.has_secret || false);
      }
      loaded2 = true; checkDone();
    }).catch(function() { loaded2 = true; checkDone(); });
    api('/integrations/shopify').then(function(data) {
      if (data) {
        setShopify({ shop: (data.config && data.config.shop) || '', client_id: data.client_id || '', client_secret: '', enabled: data.enabled || false });
        setHasShopifySecret(data.has_secret || false);
      }
    }).catch(function(e) { console.error('[silent-catch]', e); });
    api('/integrations/walmart_ca').then(function(data) {
      if (data) {
        setWmca({ client_id: data.client_id || '', client_secret: '', enabled: data.enabled || false, last_sync_at: data.last_sync_at,
          shopify_integration_id: (data.config && data.config.shopify_integration_id) || '', display_name: data.display_name || '' });
        setHasWmcaSecret(data.has_secret || false);
      }
    }).catch(function(e) { console.error('[silent-catch]', e); });
    loadAmzAds();
    loadMeli();
    loadShein();
    api('/integrations/keepa').then(function(data) {
      if (data) {
        setKeepa({ api_key: '', enabled: data.enabled || false });
        setHasKeepaKey(data.has_secret || false);
      }
      loaded3 = true; checkDone();
    }).catch(function() { loaded3 = true; checkDone(); });
    api('/integrations/health').then(function(h) { setIntHealth(h || []); }).catch(function() { setIntHealth([]); });
    api('/integrations').then(function(all) {
      var spAccts = (all || []).filter(function(r) { return r.channel === 'amazon_sp_api'; });
      setSpapiAccounts(spAccts);
      setShopifyStores((all || []).filter(function(r) { return r.channel === 'shopify'; }));
      setGmailAccounts(gmailRowsToState(all));
      // A channel "exists" (its card renders) iff it has a DB row — enabled/disabled is shown by each card's own pill
      var chMap = {};
      (all || []).forEach(function(r) { chMap[r.channel] = true; });
      setExistingChannels(chMap);
      loaded4 = true; checkDone();
    }).catch(function() { loaded4 = true; checkDone(); });
    api('/integrations/dropbox').then(function(d) {
      if (d) {
        var dcfg = d.config || {};
        setDropbox(function(prev) { return Object.assign({}, prev, { app_key: d.client_id || '', enabled: !!d.enabled, root_path: dcfg.root_path || '' }); });
        setHasDropboxSecret(!!d.has_secret);
        setHasDropboxToken(!!d.has_refresh_token);
      }
    }).catch(function(e) { console.error('[silent-catch]', e); });
    api('/integrations/meta/types').then(function(t) { setIntTypes(t || []); }).catch(function(e) { console.error('[silent-catch]', e); });
    api('/integrations/shipstation').then(function(data) {
      if (data) {
        setShipstation({ api_key: data.client_id || '', api_secret: '', enabled: data.enabled || false, last_sync_at: data.last_sync_at || null });
        setHasShipstationSecret(data.has_secret || false);
      }
      loaded6 = true; checkDone();
    }).catch(function() { loaded6 = true; checkDone(); });
    api('/integrations/usps_portal').then(function(data) {
      if (data) {
        var ucfg = data.config || {};
        setUspsPortal({ username: data.client_id || '', password: '', otp_user: ucfg.otp_user || '', otp_pass: '', enabled: !!data.enabled });
        setHasUspsPortalSecret(!!data.has_secret);
        setHasUspsOtpPass(!!ucfg.has_otp_pass);
      }
    }).catch(function(e) { console.error('[silent-catch]', e); });
    api('/integrations/smtp').then(function(data) {
      if (data) {
        var cfg = data.config || {};
        setSmtp({ host: data.client_id || '', port: cfg.port || '587', username: cfg.username || '', password: '', from_email: cfg.from_email || '', from_name: cfg.from_name || '', enabled: data.enabled || false });
        setHasSmtpPass(data.has_secret || false);
      }
      loaded5 = true; checkDone();
    }).catch(function() { loaded5 = true; checkDone(); });
    api('/integrations/firebase').then(function(data) {
      if (data) {
        setFirebase({ project_id: data.client_id || '', api_key: '', enabled: data.enabled || false });
        setHasFirebaseKey(data.has_secret || false);
      }
    }).catch(function(e) { console.error('[silent-catch]', e); });
    api('/integrations/zentail').then(function(data) {
      if (data) {
        setZentail({ api_token: '', enabled: data.enabled || false });
        setHasZentailSecret(data.has_secret || false);
        setZentailLastSync(data.last_sync_at || null);
      }
      loaded7 = true; checkDone();
      // Also load mapping stats and warehouse mappings
      api('/integrations/zentail/mapping-stats').then(function(s) { setZentailMappingStats(s); }).catch(function(e) { console.error('[silent-catch]', e); });
      api('/integrations/zentail/warehouse-mappings').then(function(r) { setZentailWhMappings(r || []); }).catch(function(e) { console.error('[silent-catch]', e); });
      api('/integrations/zentail/warehouse-config').then(function(r) {
        var cfg = {};
        (r || []).forEach(function(c) { cfg[c.zentail_warehouse] = c; });
        setZentailWhConfig(cfg);
      }).catch(function(e) { console.error('[silent-catch]', e); });
      api('/warehouses').then(function(r) { setAllWarehouses(r || []); }).catch(function(e) { console.error('[silent-catch]', e); });
    }).catch(function() { loaded7 = true; checkDone(); });
    api('/integrations/sendgrid').then(function(data) {
      if (data) {
        var cfg = data.config || {};
        setSendgrid({ api_key: '', from_email: cfg.from_email || '', from_name: cfg.from_name || '', enabled: data.enabled || false });
        setHasSendgridKey(data.has_secret || false);
      }
      loaded8 = true; checkDone();
    }).catch(function() { loaded8 = true; checkDone(); });
    api('/integrations/exchange_rate').then(function(data) {
      if (data) {
        setFxRate({ enabled: data.enabled || false });
        var cfg = data.config || {};
        if (cfg.rates) setFxRates({ rates: cfg.rates, date: cfg.rate_date, updatedAt: cfg.last_fetched });
      }
      loaded9 = true; checkDone();
    }).catch(function() { loaded9 = true; checkDone(); });
    api('/integrations/scrapfly').then(function(data) {
      if (data) {
        setScrapfly({ api_key: '', enabled: data.enabled || false });
        setHasScrapflyKey(data.has_secret || false);
      }
      loaded10 = true; checkDone();
    }).catch(function() { loaded10 = true; checkDone(); });
    api('/integrations/billdotcom').then(function(data) {
      if (data) {
        var cfg = data.config || {};
        setBilldc({ username: data.client_id || '', password: '', dev_key: '', org_id: cfg.orgId || '', environment: data.environment || 'production', enabled: data.enabled || false });
        setHasBilldcPass(data.has_secret || false);
        setHasBilldcKey(!!cfg.devKey || !!cfg.has_devKey);
        setBilldcLastSync(data.last_sync_at || null);
      }
    }).catch(function(e) { console.error('[silent-catch]', e); });
  }, []);

  // On mount, check if a Zentail product sync is already running and resume polling
  useEffect(function() {
    var cancelled = false;
    api('/integrations/zentail/sync-products/status').then(async function(st) {
      if (cancelled || !st || !st.running) return;
      setZentailSyncing(st.mode === 'full' ? 'fullsync' : 'products');
      setZentailSyncResult({ success: true, message: st.message || 'Sync in progress...' });
      while (!cancelled) {
        await new Promise(function(r) { setTimeout(r, 3000); });
        if (cancelled) break;
        try {
          var s = await api('/integrations/zentail/sync-products/status');
          if (!s.running) {
            if (s.status === 'done') {
              setZentailSyncResult({ success: true, message: s.synced + ' synced, ' + s.matched + ' matched, ' + (s.message || '') });
              setZentailLastSync(new Date().toISOString());
            } else {
              setZentailSyncResult({ success: false, message: s.message || 'Sync failed' });
            }
            setZentailSyncing(null);
            // Refresh mapping stats and warehouse mappings after sync completes
            api('/integrations/zentail/mapping-stats').then(function(ms) { setZentailMappingStats(ms); }).catch(function(e) { console.error('[silent-catch]', e); });
            api('/integrations/zentail/warehouse-mappings').then(function(r) { setZentailWhMappings(r || []); }).catch(function(e) { console.error('[silent-catch]', e); });
            break;
          }
          setZentailSyncResult({ success: true, message: s.message || 'Working...' });
        } catch (e) { break; }
      }
    }).catch(function(e) { console.error('[silent-catch]', e); });
    return function() { cancelled = true; };
  }, []);

  // Poll Zentail live sync status every 10 seconds
  useEffect(function() {
    var cancelled = false;
    function fetchStatus() {
      if (cancelled) return;
      api('/integrations/zentail/sync-status').then(function(st) {
        if (!cancelled) setZentailLiveStatus(st);
      }).catch(function(e) { console.error('[silent-catch]', e); });
    }
    fetchStatus();
    var timer = setInterval(fetchStatus, 10000);
    return function() { cancelled = true; clearInterval(timer); };
  }, []);

  var saveCredentials = async function() {
    try {
      var body = { client_id: walmart.client_id, environment: walmart.environment, enabled: walmart.enabled, display_name: walmart.display_name || '' };
      if (walmart.client_secret) body.client_secret = walmart.client_secret;
      await api('/integrations/walmart', { method: 'PUT', body: JSON.stringify(body) });
      setHasSecret(true);
      setTestResult(null);
      showToast('Walmart credentials saved');
    } catch (e) { showToast(e.message, 'error'); }
  };

  var testConnection = async function() {
    setTesting(true); setTestResult(null);
    try {
      var result = await api('/integrations/walmart/test', { method: 'POST' });
      setTestResult({ success: true, message: result.message });
    } catch (e) {
      setTestResult({ success: false, message: e.message });
    }
    setTesting(false);
  };

  var syncOrders = async function() {
    setSyncing(true); setSyncResult(null);
    try {
      var result = await api('/integrations/walmart/sync-orders', { method: 'POST', body: JSON.stringify({}) });
      setSyncResult(result);
      showToast('Synced ' + (result.synced || 0) + ' new, ' + (result.updated || 0) + ' updated' + (result.failed ? ', ' + result.failed + ' failed' : ''));
      // Refresh last sync time
      setWalmart(Object.assign({}, walmart, { last_sync_at: new Date().toISOString() }));
    } catch (e) {
      setSyncResult({ error: e.message });
      showToast('Sync failed: ' + e.message, 'error');
    }
    setSyncing(false);
  };

  var WF = function(key) { return { value: walmart[key] || '', onChange: function(e) { setWalmart(Object.assign({}, walmart, { [key]: e.target.value })); } }; };

  var EF = function(key) { return { value: ebayInt[key] || '', onChange: function(e) { setEbayInt(Object.assign({}, ebayInt, { [key]: e.target.value })); } }; };

  var saveEbayCredentials = async function() {
    try {
      var body = { client_id: ebayInt.client_id, environment: ebayInt.environment, enabled: ebayInt.enabled, display_name: ebayInt.display_name || '', config: {} };
      if (ebayInt.client_secret) body.client_secret = ebayInt.client_secret;
      if (ebayInt.refresh_token) body.config.refresh_token = ebayInt.refresh_token;
      if (ebayInt.runame !== undefined) body.config.runame = ebayInt.runame;
      await api('/integrations/ebay', { method: 'PUT', body: JSON.stringify(body) });
      setHasEbaySecret(true);
      if (ebayInt.refresh_token) setHasEbayRefresh(true);
      showToast('eBay credentials saved');
    } catch (e) { showToast(e.message, 'error'); }
  };

  var uploadSheinReturns = async function(file) {
    if (!file) return;
    setSheinUploading(true); setSheinResult(null);
    try {
      var fd = new FormData();
      fd.append('file', file);
      var resp = await fetch('/api/integrations/shein/import-returns', {
        method: 'POST',
        body: fd,
        credentials: 'include',
      });
      var data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Upload failed');
      setSheinResult(data);
      // Refresh summary
      var s = await api('/integrations/shein/returns-summary');
      setSheinSummary(s);
      showToast('Imported ' + data.inserted + ' SHEIN returns (' + data.skipped_rejected + ' rejected filtered out)');
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setSheinUploading(false);
    }
  };

  var connectEbayOAuth = async function() {
    try {
      var resp = await api('/integrations/ebay/oauth-start');
      if (resp && resp.url) {
        window.location.href = resp.url;
      } else {
        showToast('Failed to build OAuth URL', 'error');
      }
    } catch (e) { showToast(e.message, 'error'); }
  };

  var saveEbayVerifToken = async function() {
    try {
      if (!ebayVerifToken || ebayVerifToken.length < 32) {
        showToast('Verification token must be at least 32 characters', 'error');
        return;
      }
      await api('/settings', { method: 'PUT', body: JSON.stringify({ ebay_verification_token: ebayVerifToken }) });
      showToast('Verification token saved — now register the endpoint in the eBay portal');
    } catch (e) { showToast(e.message, 'error'); }
  };

  var testEbay = async function() {
    setTesting(true); setTestResult(null);
    try {
      var result = await api('/integrations/ebay/test', { method: 'POST' });
      setTestResult({ success: true, message: result.message });
    } catch (e) { setTestResult({ success: false, message: e.message }); }
    setTesting(false);
  };

  var syncEbay = async function() {
    setSyncing(true); setSyncResult(null);
    try {
      var result = await api('/integrations/ebay/sync-orders', { method: 'POST', body: JSON.stringify({}) });
      setSyncResult(result);
      showToast('Synced ' + (result.synced || 0) + ' new, ' + (result.updated || 0) + ' updated' + (result.failed ? ', ' + result.failed + ' failed' : ''));
      setEbayInt(Object.assign({}, ebayInt, { last_sync_at: new Date().toISOString() }));
    } catch (e) {
      setSyncResult({ error: e.message });
      showToast('Sync failed: ' + e.message, 'error');
    }
    setSyncing(false);
  };

  var saveAnthropicKey = async function() {
    try {
      var body = { enabled: anthropic.enabled };
      if (anthropic.api_key) body.client_secret = anthropic.api_key;
      await api('/integrations/anthropic', { method: 'PUT', body: JSON.stringify(body) });
      // Refresh state from server
      var fresh = await api('/integrations/anthropic');
      if (fresh) {
        setAnthropic({ api_key: '', enabled: fresh.enabled || false });
        setHasAnthropicKey(fresh.has_secret || false);
      }
      setAnthropicTest(null);
      showToast('Anthropic API key saved');
    } catch (e) { showToast(e.message, 'error'); }
  };

  var testAnthropicKey = async function() {
    setAnthropicTesting(true); setAnthropicTest(null);
    try {
      var result = await api('/integrations/anthropic/test', { method: 'POST' });
      setAnthropicTest({ success: true, message: result.message });
    } catch (e) {
      setAnthropicTest({ success: false, message: e.message });
    }
    setAnthropicTesting(false);
  };

  var saveShopify = async function() {
    try {
      var body = { enabled: shopify.enabled, client_id: shopify.client_id, config: { shop: (shopify.shop || '').trim() } };
      if (shopify.client_secret) body.client_secret = shopify.client_secret;
      await api('/integrations/shopify', { method: 'PUT', body: JSON.stringify(body) });
      var fresh = await api('/integrations/shopify');
      if (fresh) {
        setShopify({ shop: (fresh.config && fresh.config.shop) || '', client_id: fresh.client_id || '', client_secret: '', enabled: fresh.enabled || false });
        setHasShopifySecret(fresh.has_secret || false);
      }
      setShopifyTest(null);
      showToast('Shopify settings saved');
    } catch (e) { showToast(e.message, 'error'); }
  };

  var testShopify = async function() {
    setShopifyTesting(true); setShopifyTest(null);
    try {
      var result = await api('/integrations/shopify/test', { method: 'POST' });
      setShopifyTest({ success: true, message: result.message });
    } catch (e) {
      setShopifyTest({ success: false, message: e.message });
    }
    setShopifyTesting(false);
  };

  var saveWmca = async function() {
    try {
      var body = { enabled: wmca.enabled, client_id: wmca.client_id, environment: 'production', display_name: wmca.display_name || '',
        config: { shopify_integration_id: wmca.shopify_integration_id ? String(wmca.shopify_integration_id) : '' } };
      if (wmca.client_secret) body.client_secret = wmca.client_secret;
      await api('/integrations/walmart_ca', { method: 'PUT', body: JSON.stringify(body) });
      var fresh = await api('/integrations/walmart_ca');
      if (fresh) {
        setWmca({ client_id: fresh.client_id || '', client_secret: '', enabled: fresh.enabled || false, last_sync_at: fresh.last_sync_at,
          shopify_integration_id: (fresh.config && fresh.config.shopify_integration_id) || '', display_name: fresh.display_name || '' });
        setHasWmcaSecret(fresh.has_secret || false);
      }
      setWmcaTest(null);
      showToast('Walmart Canada credentials saved');
    } catch (e) { showToast(e.message, 'error'); }
  };

  var testWmca = async function() {
    setWmcaTesting(true); setWmcaTest(null);
    try {
      var result = await api('/integrations/walmart_ca/test', { method: 'POST' });
      setWmcaTest({ success: true, message: result.message });
    } catch (e) {
      setWmcaTest({ success: false, message: e.message });
    }
    setWmcaTesting(false);
  };

  // ─── Amazon Advertising (channel amazon_ads — LWA creds + one-time consent, docs/14) ───
  var loadAdsSyncInfo = function() {
    api('/settings').then(function(s) {
      if (s) setAdsSyncEnabled(s.ads_sync_enabled === true);
    }).catch(function() { setAdsSyncEnabled(null); });
    api('/advertising/sync/status').then(setAdsSyncStatus).catch(function() { setAdsSyncStatus(null); });
  };

  var loadAmzAds = async function() {
    try {
      var data = await api('/integrations/amazon_ads');
      if (data) {
        setAmzAds({ client_id: data.client_id || '', client_secret: '', enabled: data.enabled || false });
        setHasAmzAdsSecret(data.has_secret || false);
        setHasAmzAdsRefresh(data.has_refresh_token || false);
        var cfg = data.config || {};
        setAmzAdsProfiles({ profiles: cfg.profiles || [], profile_id_ca: cfg.profile_id_ca || null, profile_id_us: cfg.profile_id_us || null });
      }
    } catch (e) { console.error('[silent-catch]', e); }
    loadAdsSyncInfo();
  };

  var saveAmzAds = async function() {
    try {
      var body = { enabled: amzAds.enabled, client_id: amzAds.client_id, environment: 'production' };
      if (amzAds.client_secret) body.client_secret = amzAds.client_secret;
      await api('/integrations/amazon_ads', { method: 'PUT', body: JSON.stringify(body) });
      await loadAmzAds();
      setAmzAdsTest(null);
      showToast('Amazon Advertising credentials saved');
    } catch (e) { showToast(e.message, 'error'); }
  };

  // One consent per Amazon login: the US and CA advertising accounts live under different logins
  var connectAmzAds = async function(slot) {
    try {
      var r = await api('/integrations/amazon-ads/consent-url?marketplace=' + encodeURIComponent(slot || 'us'));
      window.open(r.url, '_blank', 'noopener');
      showToast('Sign in to Amazon with the ' + String(slot || 'us').toUpperCase() + ' advertising login and approve access, then come back and Test Connection');
    } catch (e) { showToast(e.message, 'error'); }
  };

  var exchangeAmzAdsCode = async function() {
    if (!amzAdsCode.trim()) { showToast('Paste the authorization code (or the full redirect URL) first', 'error'); return; }
    setAmzAdsExchanging(true);
    try {
      var r = await api('/integrations/amazon-ads/exchange-code', { method: 'POST', body: JSON.stringify({ code: amzAdsCode, marketplace: amzAdsCodeSlot }) });
      setAmzAdsCode('');
      await loadAmzAds();
      showToast(r.message || 'Amazon Ads authorized');
    } catch (e) { showToast(e.message, 'error'); }
    setAmzAdsExchanging(false);
  };

  var refreshAmzAdsProfiles = async function() {
    try {
      var r = await api('/integrations/amazon-ads/refresh-profiles', { method: 'POST' });
      await loadAmzAds();
      showToast(r.message || 'Profiles refreshed');
    } catch (e) { showToast(e.message, 'error'); }
  };

  var testAmzAds = async function() {
    setAmzAdsTesting(true); setAmzAdsTest(null);
    try {
      var result = await api('/integrations/amazon_ads/test', { method: 'POST' });
      setAmzAdsTest({ success: true, message: result.message });
    } catch (e) {
      setAmzAdsTest({ success: false, message: e.message });
    }
    setAmzAdsTesting(false);
  };

  // ─── Mercado Libre (channel mercadolibre — Global Selling APP_ID/secret + one-time consent) ───
  var loadMeli = async function() {
    try {
      var data = await api('/integrations/mercadolibre');
      if (data) {
        setMeliInt({ client_id: data.client_id || '', client_secret: '', enabled: data.enabled || false });
        setHasMeliSecret(data.has_secret || false);
        setHasMeliRefresh(data.has_refresh_token || false);
        setMeliLastSync(data.last_sync_at || null);
        var cfg = data.config || {};
        setMeliAccount({ user_id: cfg.user_id || null, nickname: cfg.nickname || null, site_id: cfg.site_id || null });
      }
    } catch (e) { console.error('[silent-catch]', e); }
  };

  var saveMeli = async function() {
    try {
      var body = { enabled: meliInt.enabled, client_id: meliInt.client_id, environment: 'production' };
      if (meliInt.client_secret) body.client_secret = meliInt.client_secret;
      await api('/integrations/mercadolibre', { method: 'PUT', body: JSON.stringify(body) });
      await loadMeli();
      setMeliTest(null);
      showToast('Mercado Libre credentials saved');
    } catch (e) { showToast(e.message, 'error'); }
  };

  var connectMeli = async function() {
    try {
      var r = await api('/integrations/meli/consent-url');
      window.open(r.url, '_blank', 'noopener');
      showToast('Approve access in the Mercado Libre tab, then come back and Test Connection');
    } catch (e) { showToast(e.message, 'error'); }
  };

  var exchangeMeliCode = async function() {
    if (!meliCode.trim()) { showToast('Paste the authorization code (or the full redirect URL) first', 'error'); return; }
    setMeliExchanging(true);
    try {
      var r = await api('/integrations/meli/exchange-code', { method: 'POST', body: JSON.stringify({ code: meliCode }) });
      setMeliCode('');
      await loadMeli();
      showToast(r.message || 'Mercado Libre authorized');
    } catch (e) { showToast(e.message, 'error'); }
    setMeliExchanging(false);
  };

  var testMeli = async function() {
    setMeliTesting(true); setMeliTest(null);
    try {
      var result = await api('/integrations/mercadolibre/test', { method: 'POST' });
      setMeliTest({ success: true, message: result.message });
      await loadMeli();
    } catch (e) {
      setMeliTest({ success: false, message: e.message });
    }
    setMeliTesting(false);
  };

  var syncMeli = async function(kind) {
    setMeliSyncing(kind);
    try {
      var r = await api('/integrations/mercadolibre/sync-' + kind, { method: 'POST' });
      showToast('Mercado Libre ' + kind + ' sync ' + (r.status || 'started'));
      setTimeout(loadMeli, 4000); // let last_sync_at update
    } catch (e) { showToast(e.message, 'error'); }
    setMeliSyncing(null);
  };

  // ─── SHEIN (channel shein — application App ID/Secret + one-time store authorization, docs/25) ───
  var loadShein = async function() {
    try {
      var data = await api('/integrations/shein');
      if (data) {
        var cfg = data.config || {};
        setSheinInt({ client_id: data.client_id || '', client_secret: '', enabled: data.enabled || false,
          shopify_integration_id: cfg.shopify_integration_id || '' });
        setHasSheinSecret(data.has_secret || false);
        setSheinStore({ authorized: !!cfg.open_key_id, store_name: cfg.store_name || '', supplier_id: cfg.supplier_id || '', authorized_at: cfg.authorized_at || null });
      }
    } catch (e) { console.error('[silent-catch]', e); }
  };

  var saveShein = async function() {
    try {
      var body = { enabled: sheinInt.enabled, client_id: (sheinInt.client_id || '').trim(), environment: 'production', display_name: 'SHEIN',
        config: { shopify_integration_id: sheinInt.shopify_integration_id ? String(sheinInt.shopify_integration_id) : '' } };
      if (sheinInt.client_secret) body.client_secret = sheinInt.client_secret.trim();
      await api('/integrations/shein', { method: 'PUT', body: JSON.stringify(body) });
      await loadShein();
      setSheinTest(null);
      showToast('SHEIN credentials saved');
    } catch (e) { showToast(e.message, 'error'); }
  };

  var connectShein = async function() {
    try {
      var r = await api('/integrations/shein/consent-url');
      window.open(r.url, '_blank', 'noopener');
      showToast('Authorize in the SHEIN tab with the store\'s main account, then come back and Test Connection');
    } catch (e) { showToast(e.message, 'error'); }
  };

  var exchangeSheinCode = async function() {
    if (!sheinCode.trim()) { showToast('Paste the tempToken (or the full redirect address) first', 'error'); return; }
    setSheinExchanging(true);
    try {
      var r = await api('/integrations/shein/exchange-code', { method: 'POST', body: JSON.stringify({ code: sheinCode }) });
      setSheinCode('');
      await loadShein();
      showToast(r.message || 'SHEIN store authorized');
    } catch (e) { showToast(e.message, 'error'); }
    setSheinExchanging(false);
  };

  var testShein = async function() {
    setSheinTesting(true); setSheinTest(null);
    try {
      var result = await api('/integrations/shein/test', { method: 'POST' });
      setSheinTest({ success: true, message: result.message });
      await loadShein();
    } catch (e) {
      setSheinTest({ success: false, message: e.message });
    }
    setSheinTesting(false);
  };

  var toggleAdsSync = async function(v) {
    try {
      await api('/settings', { method: 'PUT', body: JSON.stringify({ ads_sync_enabled: v }) });
      setAdsSyncEnabled(v);
      showToast('Ads data sync ' + (v ? 'enabled — reports plan at 4 AM ET, entities every 6h' : 'disabled'));
    } catch (e) { showToast(e.message, 'error'); }
  };

  var syncAdsEntitiesNow = async function() {
    setAdsSyncing(true);
    try {
      var r = await api('/advertising/sync/entities', { method: 'POST' });
      var parts = [];
      Object.keys(r.result || {}).forEach(function(mp) {
        var c = r.result[mp];
        parts.push(mp + ': ' + (c.error ? ('ERROR ' + c.error) : (c.campaigns + ' campaigns, ' + c.keywords + ' keywords, ' + c.targets + ' targets')));
      });
      showToast(parts.length ? 'Entity sync — ' + parts.join(' · ') : 'Entity sync ran (no enabled profiles yet)');
      loadAdsSyncInfo();
    } catch (e) { showToast(e.message, 'error'); }
    setAdsSyncing(false);
  };

  var saveKeepaKey = async function() {
    try {
      var body = { enabled: keepa.enabled };
      if (keepa.api_key) body.client_secret = keepa.api_key;
      await api('/integrations/keepa', { method: 'PUT', body: JSON.stringify(body) });
      var fresh = await api('/integrations/keepa');
      if (fresh) {
        setKeepa({ api_key: '', enabled: fresh.enabled || false });
        setHasKeepaKey(fresh.has_secret || false);
      }
      setKeepaTest(null);
      showToast('Keepa API key saved');
    } catch (e) { showToast(e.message, 'error'); }
  };

  var testKeepaKey = async function() {
    setKeepaTesting(true); setKeepaTest(null);
    try {
      var result = await api('/analysis/keepa-status');
      if (result.configured && result.tokens_left !== undefined) {
        setKeepaTest({ success: true, message: 'Connected! Tokens: ' + result.tokens_left + ' | Refill in: ' + Math.round(result.refill_in / 60000) + 'min' });
      } else {
        setKeepaTest({ success: false, message: 'API key not configured' });
      }
    } catch (e) { setKeepaTest({ success: false, message: e.message }); }
    setKeepaTesting(false);
  };

  var saveFxRate = async function() {
    try {
      await api('/integrations/exchange_rate', { method: 'PUT', body: JSON.stringify({ enabled: fxRate.enabled }) });
      var fresh = await api('/integrations/exchange_rate');
      if (fresh) {
        setFxRate({ enabled: fresh.enabled || false });
        var cfg = fresh.config || {};
        if (cfg.rates) setFxRates({ rates: cfg.rates, date: cfg.rate_date, updatedAt: cfg.last_fetched });
      }
      showToast('Exchange rate settings saved');
    } catch (e) { showToast(e.message, 'error'); }
  };
  var testFxRate = async function() {
    setFxTesting(true); setFxTest(null);
    try {
      var result = await api('/integrations/exchange_rate/test', { method: 'POST' });
      setFxTest({ success: true, message: result.message });
      if (result.rates) setFxRates({ rates: result.rates, date: result.date || null, updatedAt: new Date().toISOString() });
    } catch (e) { setFxTest({ success: false, message: e.message }); }
    setFxTesting(false);
  };
  var refreshFxRate = async function() {
    setFxTesting(true); setFxTest(null);
    try {
      var result = await api('/integrations/exchange-rate/refresh', { method: 'POST' });
      setFxRates({ rates: result.rates, date: result.date, updatedAt: new Date().toISOString() });
      setFxTest({ success: true, message: 'Rates refreshed! 1 CAD = ' + (result.rates.CAD ? result.rates.CAD.toFixed(5) : 'N/A') + ' USD' });
    } catch (e) { setFxTest({ success: false, message: e.message }); }
    setFxTesting(false);
  };

  // gmail rows → per-account edit state (config flags surface as checkboxes)
  function gmailRowsToState(all) {
    return (all || []).filter(function(r) { return r.channel === 'gmail'; }).map(function(r) {
      var cfg = r.config || {};
      // GET /integrations strips client_secret and ships a has_secret flag instead
      return { id: r.id, display_name: r.display_name || '', email: r.client_id || '', password: '',
        enabled: !!r.enabled, has_secret: r.has_secret !== undefined ? !!r.has_secret : !!r.client_secret,
        docs_inbox: cfg.docs_inbox === true, pilot: cfg.agent ? cfg.agent.pilot !== false : true };
    });
  }

  var refreshIntegrations = function() {
    api('/integrations/health').then(function(h) { setIntHealth(h || []); }).catch(function() { setIntHealth([]); });
    api('/integrations').then(function(all) {
      var spAccts = (all || []).filter(function(r) { return r.channel === 'amazon_sp_api'; });
      setSpapiAccounts(spAccts);
      setShopifyStores((all || []).filter(function(r) { return r.channel === 'shopify'; }));
      setGmailAccounts(gmailRowsToState(all));
      // A channel "exists" (its card renders) iff it has a DB row — enabled/disabled is shown by each card's own pill.
      // This also keeps cards created via Add New visible (rows are created with enabled=false).
      var chMap = {};
      (all || []).forEach(function(r) { chMap[r.channel] = true; });
      setExistingChannels(chMap);
    }).catch(function(e) { console.error('[silent-catch]', e); });
    api('/integrations/dropbox').then(function(d) {
      if (d) {
        var dcfg = d.config || {};
        setDropbox(function(prev) { return Object.assign({}, prev, { app_key: d.client_id || prev.app_key, enabled: !!d.enabled, root_path: dcfg.root_path || prev.root_path }); });
        setHasDropboxSecret(!!d.has_secret);
        setHasDropboxToken(!!d.has_refresh_token);
      }
    }).catch(function(e) { console.error('[silent-catch]', e); });
    api('/integrations/meta/types').then(function(t) { setIntTypes(t || []); }).catch(function(e) { console.error('[silent-catch]', e); });
  };

  var saveShipstationCreds = async function() {
    try {
      var body = { client_id: shipstation.api_key, enabled: shipstation.enabled };
      if (shipstation.api_secret) body.client_secret = shipstation.api_secret;
      await api('/integrations/shipstation', { method: 'PUT', body: JSON.stringify(body) });
      var fresh = await api('/integrations/shipstation');
      if (fresh) {
        setShipstation(Object.assign({}, shipstation, { api_secret: '', last_sync_at: fresh.last_sync_at }));
        setHasShipstationSecret(fresh.has_secret || false);
      }
      setShipstationTest(null);
      showToast('ShipStation credentials saved');
    } catch (e) { showToast(e.message, 'error'); }
  };

  var saveUspsPortal = async function() {
    try {
      var body = { client_id: (uspsPortal.username || '').trim(), enabled: !!uspsPortal.enabled, config: { otp_user: (uspsPortal.otp_user || '').trim() } };
      if (uspsPortal.password) body.client_secret = uspsPortal.password;
      if (uspsPortal.otp_pass) body.config.otp_pass = uspsPortal.otp_pass;
      await api('/integrations/usps_portal', { method: 'PUT', body: JSON.stringify(body) });
      var fresh = await api('/integrations/usps_portal');
      if (fresh) { setHasUspsPortalSecret(!!fresh.has_secret); setHasUspsOtpPass(!!(fresh.config && fresh.config.has_otp_pass)); }
      setUspsPortal(Object.assign({}, uspsPortal, { password: '', otp_pass: '' }));
      setUspsPortalTest(null);
      showToast('USPS.com portal login saved');
    } catch (e) { showToast(e.message, 'error'); }
  };
  var testUspsPortal = async function() {
    setUspsPortalTesting(true); setUspsPortalTest(null);
    try {
      var r = await api('/integrations/usps_portal/test', { method: 'POST' });
      setUspsPortalTest({ success: true, message: r.message });
    } catch (e) { setUspsPortalTest({ success: false, message: e.message }); }
    finally { setUspsPortalTesting(false); }
  };

  var testShipstationConnection = async function() {
    setShipstationTesting(true); setShipstationTest(null);
    try {
      var result = await api('/integrations/shipstation/test', { method: 'POST' });
      setShipstationTest({ success: true, message: result.message });
    } catch (e) { setShipstationTest({ success: false, message: e.message }); }
    setShipstationTesting(false);
  };

  var pollShipstationStatus = function() {
    if (shipstationPollRef.current) { clearTimeout(shipstationPollRef.current); shipstationPollRef.current = null; }
    api('/integrations/shipstation/sync-status').then(function(s) {
      if (s && s.running) {
        setShipstationSyncing(true);
        setShipstationSyncMode(s.mode || null);
        setShipstationSyncStartedAt(s.startedAt || null);
        shipstationPollRef.current = setTimeout(pollShipstationStatus, 4000);
      } else {
        setShipstationSyncing(false);
        setShipstationSyncMode(null);
        setShipstationSyncStartedAt(null);
        if (s && s.lastResult) {
          setShipstationSyncResult(s.lastResult);
          if (s.lastRun) setShipstation(function(prev) { return Object.assign({}, prev, { last_sync_at: s.lastRun }); });
        }
      }
    }).catch(function(e) {
      console.error('[ShipStation Poll] error:', e && e.message);
      shipstationPollRef.current = setTimeout(pollShipstationStatus, 8000);
    });
  };
  useEffect(function() {
    pollShipstationStatus();
    return function() { if (shipstationPollRef.current) { clearTimeout(shipstationPollRef.current); shipstationPollRef.current = null; } };
  }, []);

  var syncShipstationCosts = async function(days) {
    if (shipstationSyncing) { showToast('Sync already running', 'error'); return; }
    setShipstationSyncResult(null);
    setShipstationSyncing(true);
    try {
      var body = days ? { days: days } : {};
      await api('/integrations/shipstation/sync', { method: 'POST', body: JSON.stringify(body) });
      showToast(days ? 'Sync started (' + days + 'd) — running in background' : 'Sync started — running in background');
      pollShipstationStatus();
    } catch (e) {
      if (e.message && e.message.toLowerCase().indexOf('already running') >= 0) {
        showToast('Sync already running — tracking status');
        pollShipstationStatus();
      } else {
        setShipstationSyncing(false);
        setShipstationSyncResult({ error: e.message });
        showToast('Sync failed: ' + e.message, 'error');
      }
    }
  };

  var updateGmailAccount = function(id, patch) {
    setGmailAccounts(function(prev) { return prev.map(function(a) { return a.id === id ? Object.assign({}, a, patch) : a; }); });
  };
  var saveGmailAccount = async function(acct) {
    try {
      var body = { client_id: (acct.email || '').trim(), display_name: acct.display_name, enabled: acct.enabled,
        config: { docs_inbox: acct.docs_inbox, agent: { pilot: acct.pilot } } };
      if (acct.password) body.client_secret = acct.password;
      await api('/integrations/by-id/' + acct.id, { method: 'PUT', body: JSON.stringify(body) });
      showToast('Gmail account saved');
      refreshIntegrations();
    } catch (e) { showToast(e.message, 'error'); }
  };
  var testGmailAccount = async function(acct) {
    setGmailTests(function(prev) { var n = Object.assign({}, prev); n[acct.id] = { testing: true }; return n; });
    try {
      var r = await api('/integrations/by-id/' + acct.id + '/test', { method: 'POST' });
      setGmailTests(function(prev) { var n = Object.assign({}, prev); n[acct.id] = { success: true, message: r.message }; return n; });
    } catch (e) {
      setGmailTests(function(prev) { var n = Object.assign({}, prev); n[acct.id] = { success: false, message: e.message }; return n; });
    }
  };
  var removeGmailAccount = async function(acct) {
    if (!window.confirm('Remove Gmail account "' + (acct.email || acct.display_name) + '"?')) return;
    try {
      await api('/integrations/by-id/' + acct.id, { method: 'DELETE' });
      showToast('Gmail account removed');
      refreshIntegrations();
    } catch (e) { showToast(e.message, 'error'); }
  };
  var saveDropboxCreds = async function() {
    try {
      var body = { client_id: (dropbox.app_key || '').trim(), enabled: dropbox.enabled, config: { root_path: (dropbox.root_path || '').trim() } };
      if (dropbox.app_secret) body.client_secret = dropbox.app_secret;
      await api('/integrations/dropbox', { method: 'PUT', body: JSON.stringify(body) });
      setDropbox(Object.assign({}, dropbox, { app_secret: '' }));
      setDropboxTest(null);
      showToast('Dropbox settings saved');
      refreshIntegrations();
    } catch (e) { showToast(e.message, 'error'); }
  };
  var connectDropbox = async function() {
    setDropboxConnecting(true); setDropboxTest(null);
    try {
      var r = await api('/integrations/dropbox/exchange-code', { method: 'POST', body: JSON.stringify({ code: dropbox.auth_code }) });
      setDropbox(Object.assign({}, dropbox, { auth_code: '' }));
      setHasDropboxToken(true);
      setDropboxTest({ success: true, message: 'Authorized as ' + r.account + (r.teamSpace ? ' (team space)' : '') });
      showToast('Dropbox authorized');
    } catch (e) { setDropboxTest({ success: false, message: e.message }); }
    setDropboxConnecting(false);
  };
  var testDropboxConnection = async function() {
    setDropboxTesting(true); setDropboxTest(null);
    try {
      var r = await api('/integrations/dropbox/test', { method: 'POST' });
      setDropboxTest({ success: true, message: r.message });
    } catch (e) { setDropboxTest({ success: false, message: e.message }); }
    setDropboxTesting(false);
  };

  var saveSmtpCreds = async function() {
    try {
      var body = { client_id: smtp.host, enabled: smtp.enabled, config: { port: smtp.port, username: smtp.username, from_email: smtp.from_email, from_name: smtp.from_name } };
      if (smtp.password) body.client_secret = smtp.password;
      await api('/integrations/smtp', { method: 'PUT', body: JSON.stringify(body) });
      var fresh = await api('/integrations/smtp');
      if (fresh) {
        var cfg = fresh.config || {};
        setSmtp(Object.assign({}, smtp, { password: '' }));
        setHasSmtpPass(fresh.has_secret || false);
      }
      setSmtpTest(null);
      showToast('SMTP settings saved');
    } catch (e) { showToast(e.message, 'error'); }
  };

  var testSmtpConnection = async function() {
    setSmtpTesting(true); setSmtpTest(null);
    try {
      var result = await api('/integrations/smtp/test', { method: 'POST' });
      setSmtpTest({ success: true, message: result.message });
    } catch (e) { setSmtpTest({ success: false, message: e.message }); }
    setSmtpTesting(false);
  };

  var saveFirebaseCreds = async function() {
    try {
      var body = { client_id: firebase.project_id, enabled: firebase.enabled, config: {} };
      if (firebase.api_key) body.client_secret = firebase.api_key;
      await api('/integrations/firebase', { method: 'PUT', body: JSON.stringify(body) });
      var fresh = await api('/integrations/firebase');
      if (fresh) { setFirebase(Object.assign({}, firebase, { api_key: '' })); setHasFirebaseKey(fresh.has_secret || false); }
      setFirebaseTest(null);
      showToast('Firebase settings saved');
    } catch (e) { showToast(e.message, 'error'); }
  };
  var testFirebaseConnection = async function() {
    setFirebaseTesting(true); setFirebaseTest(null);
    try {
      var result = await api('/integrations/firebase/test', { method: 'POST' });
      setFirebaseTest({ success: true, message: result.message });
    } catch (e) { setFirebaseTest({ success: false, message: e.message }); }
    setFirebaseTesting(false);
  };

  var saveSendgridCreds = async function() {
    try {
      var body = { client_id: sendgrid.from_email, enabled: sendgrid.enabled, config: { from_email: sendgrid.from_email, from_name: sendgrid.from_name } };
      if (sendgrid.api_key) body.client_secret = sendgrid.api_key;
      await api('/integrations/sendgrid', { method: 'PUT', body: JSON.stringify(body) });
      var fresh = await api('/integrations/sendgrid');
      if (fresh) {
        var cfg = fresh.config || {};
        setSendgrid(Object.assign({}, sendgrid, { api_key: '' }));
        setHasSendgridKey(fresh.has_secret || false);
      }
      setSendgridTest(null);
      showToast('SendGrid settings saved');
    } catch (e) { showToast(e.message, 'error'); }
  };

  var testSendgridConnection = async function() {
    setSendgridTesting(true); setSendgridTest(null);
    try {
      var result = await api('/integrations/sendgrid/test', { method: 'POST' });
      setSendgridTest({ success: true, message: result.message });
    } catch (e) { setSendgridTest({ success: false, message: e.message }); }
    setSendgridTesting(false);
  };

  var saveZentailCreds = async function() {
    try {
      var body = { enabled: zentail.enabled };
      if (zentail.api_token) body.client_secret = zentail.api_token;
      await api('/integrations/zentail', { method: 'PUT', body: JSON.stringify(body) });
      var fresh = await api('/integrations/zentail');
      if (fresh) {
        setZentail({ api_token: '', enabled: fresh.enabled || false });
        setHasZentailSecret(fresh.has_secret || false);
      }
      setZentailTest(null);
      showToast('Zentail API token saved');
    } catch (e) { showToast(e.message, 'error'); }
  };

  var testZentailConnection = async function() {
    setZentailTesting(true); setZentailTest(null);
    try {
      var result = await api('/integrations/zentail/test', { method: 'POST' });
      setZentailTest({ success: true, message: result.message });
    } catch (e) { setZentailTest({ success: false, message: e.message }); }
    setZentailTesting(false);
  };

  var SF = function(key) { return { value: smtp[key] || '', onChange: function(e) { setSmtp(Object.assign({}, smtp, { [key]: e.target.value })); } }; };

  if (!loaded) return React.createElement('div', { style: { color: C.textMuted, padding: 40, textAlign: 'center' } }, 'Loading...');

  return React.createElement('div', { style: { marginTop: 16 } },
    // Add New Integration button
    // Health strip: what the 10-minute monitor (services/integration-health.js) last saw.
    // Down rows list the reason; otherwise one quiet line with the last check time.
    (function() {
      if (!intHealth || !intHealth.length) return null;
      var down = intHealth.filter(function(h) { return h.status === 'down'; });
      var fmt = function(d) { return d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''; };
      var lastCheck = intHealth.reduce(function(m, h) { return (h.last_checked_at && (!m || h.last_checked_at > m)) ? h.last_checked_at : m; }, null);
      if (!down.length) {
        return React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: C.success, marginBottom: 12 } },
          React.createElement(LIcon, { name: 'check-circle', size: 14 }), 'All ' + intHealth.length + ' monitored integrations connected',
          lastCheck && React.createElement('span', { style: { color: C.textMuted } }, '· last check ' + fmt(lastCheck)));
      }
      return React.createElement('div', { style: { background: C.dangerSoft || C.bg, border: '1px solid ' + C.danger, borderRadius: 8, padding: '10px 14px', marginBottom: 12 } },
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: C.danger, marginBottom: 4 } },
          React.createElement(LIcon, { name: 'alert-triangle', size: 15 }), down.length + ' integration' + (down.length > 1 ? 's' : '') + ' down'),
        down.map(function(h) {
          return React.createElement('div', { key: h.key, style: { fontSize: 12, color: C.text, marginTop: 2 } },
            React.createElement('span', { style: { fontWeight: 600 } }, h.label || h.channel), ' — since ' + fmt(h.down_since),
            h.last_error && React.createElement('span', { style: { color: C.textMuted } }, ' · ' + String(h.last_error).slice(0, 140)));
        }));
    })(),
    React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', marginBottom: 16 } },
      React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: function() { setAddNewModal({ channel: '', marketplace: '' }); } },
        React.createElement(LIcon, { name: 'plus', size: 14 }), ' Add New Integration')
    ),
    // Integration cards — one { title, el } entry per card. el is falsy when the channel has no
    // DB row (existingChannels gate); the list is filtered to existing channels and sorted
    // alphabetically by the display title shown on each card header.
    (function() {
      // Amazon SP-API Cards (dynamic, one per account)
      var intCards = spapiAccounts.map(function(acct) {
        var key = 'amazon_' + acct.id;
        var title = acct.display_name || ('Amazon SP-API' + (acct.marketplace ? ' (' + acct.marketplace + ')' : ''));
        return { title: title, el: React.createElement('div', { key: key, style: { marginBottom: 16 } },
          React.createElement(SpapiAccountCard, {
            acct: acct, showToast: showToast, onRefresh: refreshIntegrations,
            expanded: !!expandedInt[key],
            onToggle: function() { toggleInt(key); }
          })
        ) };
      });
      intCards.push(
    // Anthropic AI Card
    { title: 'Anthropic (Claude AI)', el: existingChannels.anthropic && React.createElement(Card, { key: 'anthropic-card', style: { padding: 0, overflow: 'hidden', marginTop: 16 } },
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: expandedInt.anthropic ? '1px solid ' + C.border : 'none', cursor: 'pointer' }, onClick: function() { toggleInt('anthropic'); } },
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12 } },
          React.createElement('div', { style: { width: 40, height: 40, borderRadius: 8, background: '#D4A27415', display: 'flex', alignItems: 'center', justifyContent: 'center' } },
            React.createElement('svg', { width: 26, height: 26, viewBox: '0 0 24 24', style: { display: 'block' } },
              React.createElement('circle', { cx: 12, cy: 12, r: 12, fill: '#D4A274' }),
              React.createElement('text', { x: 12, y: 16.5, textAnchor: 'middle', fill: '#FFFFFF', fontSize: 12, fontWeight: 700, fontFamily: 'Arial' }, 'A')
            )
          ),
          React.createElement('div', null,
            React.createElement('div', { style: { fontSize: 15, fontWeight: 600, color: C.text } }, 'Anthropic (Claude AI)'),
            React.createElement('div', { style: { fontSize: 12, color: C.textMuted } }, anthropic.enabled && hasAnthropicKey ? 'Connected — AI suggestions enabled' : 'Not connected')
          )
        ),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
            React.createElement('div', { style: { width: 8, height: 8, borderRadius: 4, background: anthropic.enabled && hasAnthropicKey ? C.success : C.textDim } }),
            React.createElement('span', { style: { fontSize: 12, color: anthropic.enabled && hasAnthropicKey ? C.success : C.textMuted, fontWeight: 500 } }, anthropic.enabled && hasAnthropicKey ? 'Enabled' : 'Disabled')
          ),
          React.createElement(LIcon, { name: expandedInt.anthropic ? 'chevron-up' : 'chevron-down', size: 16, style: { color: C.textMuted } })
        )
      ),
      expandedInt.anthropic && React.createElement('div', { style: { padding: 20 } },
        React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12, lineHeight: 1.5 } }, 'Powers the ✨ AI Suggest feature in Customer Service. Claude analyzes the conversation, order details, and tracking info to generate professional response suggestions.'),
        React.createElement('div', { style: { fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 } }, 'API Key'),
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, marginBottom: 12, alignItems: 'end' } },
          React.createElement(Inp, { label: 'API Key' + (hasAnthropicKey ? ' (saved — leave blank to keep)' : ''), type: 'password',
            placeholder: hasAnthropicKey ? '••••••••••••••••••••' : 'sk-ant-...',
            value: anthropic.api_key || '', onChange: function(e) { setAnthropic(Object.assign({}, anthropic, { api_key: e.target.value })); } }),
          React.createElement('div', { style: { paddingBottom: 4 } },
            React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: C.text } },
              React.createElement('input', { type: 'checkbox', checked: anthropic.enabled, onChange: function(e) { setAnthropic(Object.assign({}, anthropic, { enabled: e.target.checked })); } }),
              'Enable AI'
            )
          )
        ),
        React.createElement('div', { style: { display: 'flex', gap: 8, marginBottom: 12 } },
          React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: saveAnthropicKey }, React.createElement(LIcon, { name: 'save', size: 14 }), ' Save Key'),
          React.createElement(Btn, { size: 'sm', onClick: testAnthropicKey, disabled: anthropicTesting || !hasAnthropicKey }, anthropicTesting ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Testing...'] : [React.createElement(LIcon, { name: 'plug', size: 14, key: 'ic' }), ' Test Connection'])
        ),
        anthropicTest && React.createElement('div', { style: { padding: '10px 14px', borderRadius: 8,
          background: anthropicTest.success ? C.success + '15' : C.danger + '15',
          border: '1px solid ' + (anthropicTest.success ? C.success + '40' : C.danger + '40'),
          color: anthropicTest.success ? C.success : C.danger, fontSize: 13 }
        }, anthropicTest.success ? React.createElement(LIcon, { name: 'check', size: 14 }) : React.createElement(LIcon, { name: 'x', size: 14 }), ' ', anthropicTest.message),
        React.createElement(IntegrationApiLog, { channel: 'anthropic', label: 'Anthropic' })
      )
    ) },
    // Keepa Card
    { title: 'Keepa', el: existingChannels.keepa && React.createElement(Card, { key: 'keepa-card', style: { padding: 0, overflow: 'hidden', marginTop: 16 } },
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: expandedInt.keepa ? '1px solid ' + C.border : 'none', cursor: 'pointer' }, onClick: function() { toggleInt('keepa'); } },
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12 } },
          React.createElement('div', { style: { width: 40, height: 40, borderRadius: 8, background: '#38761D15', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 } }, React.createElement(LIcon, { name: 'bar-chart-3', size: 22 })),
          React.createElement('div', null,
            React.createElement('div', { style: { fontSize: 15, fontWeight: 600, color: C.text } }, 'Keepa'),
            React.createElement('div', { style: { fontSize: 12, color: C.textMuted } }, keepa.enabled && hasKeepaKey ? 'Connected — Amazon data & fee analysis' : 'Not connected')
          )
        ),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
            React.createElement('div', { style: { width: 8, height: 8, borderRadius: 4, background: keepa.enabled && hasKeepaKey ? C.success : C.textDim } }),
            React.createElement('span', { style: { fontSize: 12, color: keepa.enabled && hasKeepaKey ? C.success : C.textMuted, fontWeight: 500 } }, keepa.enabled && hasKeepaKey ? 'Enabled' : 'Disabled')
          ),
          React.createElement(LIcon, { name: expandedInt.keepa ? 'chevron-up' : 'chevron-down', size: 16, style: { color: C.textMuted } })
        )
      ),
      expandedInt.keepa && React.createElement('div', { style: { padding: '16px 20px' } },
        React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12, lineHeight: 1.5 } }, 'Powers the Analysis module. Keepa provides Amazon Buy Box prices, FBA fees, referral fees, sales rank, and monthly sold data per ASIN. Get an API key from keepa.com → Data Access.'),
        React.createElement('div', { style: { display: 'flex', gap: 12, marginBottom: 12, alignItems: 'flex-end' } },
          React.createElement('div', { style: { flex: 1 } },
            React.createElement(Inp, { label: 'API Key' + (hasKeepaKey ? ' (saved — leave blank to keep)' : ''), type: 'password',
              placeholder: hasKeepaKey ? '••••••••••••••••••••' : 'Enter Keepa API key...',
              value: keepa.api_key, onChange: function(e) { setKeepa(Object.assign({}, keepa, { api_key: e.target.value })); } })
          ),
          React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.text, cursor: 'pointer', paddingBottom: 6 } },
            React.createElement('input', { type: 'checkbox', checked: keepa.enabled, onChange: function(e) { setKeepa(Object.assign({}, keepa, { enabled: e.target.checked })); } }),
            'Enable')
        ),
        React.createElement('div', { style: { display: 'flex', gap: 8 } },
          React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: saveKeepaKey, disabled: !keepa.api_key && !hasKeepaKey }, React.createElement(LIcon, { name: 'save', size: 14 }), ' Save'),
          hasKeepaKey && React.createElement(Btn, { size: 'sm', onClick: testKeepaKey, disabled: keepaTesting }, keepaTesting ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Testing...'] : [React.createElement(LIcon, { name: 'flask-conical', size: 14, key: 'ic' }), ' Test Connection'])
        ),
        keepaTest && React.createElement('div', { style: { padding: '10px 14px', borderRadius: 8, marginTop: 12,
          background: keepaTest.success ? C.success + '15' : C.danger + '15',
          border: '1px solid ' + (keepaTest.success ? C.success + '40' : C.danger + '40'),
          color: keepaTest.success ? C.success : C.danger, fontSize: 13 }
        }, keepaTest.success ? React.createElement(LIcon, { name: 'check', size: 14 }) : React.createElement(LIcon, { name: 'x', size: 14 }), ' ', keepaTest.message),
        React.createElement(IntegrationApiLog, { channel: 'keepa', label: 'Keepa' })
      )
    ) },
    // SMTP / Email Card
    { title: 'SMTP / Email', el: existingChannels.smtp && React.createElement(Card, { key: 'smtp-card', style: { padding: 0, overflow: 'hidden', marginTop: 16 } },
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: expandedInt.smtp ? '1px solid ' + C.border : 'none', cursor: 'pointer' }, onClick: function() { toggleInt('smtp'); } },
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12 } },
          React.createElement('div', { style: { width: 40, height: 40, borderRadius: 8, background: '#6366F115', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 } }, React.createElement(LIcon, { name: 'mail', size: 22 })),
          React.createElement('div', null,
            React.createElement('div', { style: { fontSize: 15, fontWeight: 600, color: C.text } }, 'SMTP / Email'),
            React.createElement('div', { style: { fontSize: 12, color: C.textMuted } }, smtp.enabled && hasSmtpPass ? 'Connected — Email POs to vendors' : 'Not connected')
          )
        ),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
            React.createElement('div', { style: { width: 8, height: 8, borderRadius: 4, background: smtp.enabled && hasSmtpPass ? C.success : C.textDim } }),
            React.createElement('span', { style: { fontSize: 12, color: smtp.enabled && hasSmtpPass ? C.success : C.textMuted, fontWeight: 500 } }, smtp.enabled && hasSmtpPass ? 'Enabled' : 'Disabled')
          ),
          React.createElement(LIcon, { name: expandedInt.smtp ? 'chevron-up' : 'chevron-down', size: 16, style: { color: C.textMuted } })
        )
      ),
      expandedInt.smtp && React.createElement('div', { style: { padding: 20 } },
        React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12, lineHeight: 1.5 } }, 'Configure SMTP to email purchase orders directly to vendors. Each email includes a PDF and CSV copy of the PO.'),
        React.createElement('div', { style: { fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 } }, 'SMTP Server'),
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 } },
          React.createElement(Inp, { label: 'SMTP Host', placeholder: 'smtp.gmail.com', ...SF('host') }),
          React.createElement(Inp, { label: 'Port', placeholder: '587', ...SF('port') })
        ),
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 } },
          React.createElement(Inp, { label: 'Username', placeholder: 'user@company.com', ...SF('username') }),
          React.createElement(Inp, { label: 'Password' + (hasSmtpPass ? ' (saved — leave blank to keep)' : ''), type: 'password',
            placeholder: hasSmtpPass ? '••••••••••••••' : 'Enter password',
            value: smtp.password, onChange: function(e) { setSmtp(Object.assign({}, smtp, { password: e.target.value })); } })
        ),
        React.createElement('div', { style: { fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12, marginTop: 8 } }, 'Sender'),
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 } },
          React.createElement(Inp, { label: 'From Email', placeholder: 'purchasing@company.com', ...SF('from_email') }),
          React.createElement(Inp, { label: 'From Name', placeholder: 'Company Purchasing', ...SF('from_name') })
        ),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 } },
          React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.text, cursor: 'pointer' } },
            React.createElement('input', { type: 'checkbox', checked: smtp.enabled, onChange: function(e) { setSmtp(Object.assign({}, smtp, { enabled: e.target.checked })); } }),
            'Enable Email')
        ),
        React.createElement('div', { style: { display: 'flex', gap: 8 } },
          React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: saveSmtpCreds }, React.createElement(LIcon, { name: 'save', size: 14 }), ' Save'),
          hasSmtpPass && React.createElement(Btn, { size: 'sm', onClick: testSmtpConnection, disabled: smtpTesting }, smtpTesting ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Testing...'] : [React.createElement(LIcon, { name: 'flask-conical', size: 14, key: 'ic' }), ' Test Connection'])
        ),
        smtpTest && React.createElement('div', { style: { padding: '10px 14px', borderRadius: 8, marginTop: 12,
          background: smtpTest.success ? C.success + '15' : C.danger + '15',
          border: '1px solid ' + (smtpTest.success ? C.success + '40' : C.danger + '40'),
          color: smtpTest.success ? C.success : C.danger, fontSize: 13 }
        }, smtpTest.success ? React.createElement(LIcon, { name: 'check', size: 14 }) : React.createElement(LIcon, { name: 'x', size: 14 }), ' ', smtpTest.message),
        React.createElement(IntegrationApiLog, { channel: 'smtp', label: 'Gmail SMTP' })
      )
    ) },
    // Gmail Card (multi-account: one row per Google account, like Shopify stores)
    { title: 'Gmail', el: existingChannels.gmail && React.createElement(Card, { key: 'gmail-card', style: { padding: 0, overflow: 'hidden', marginTop: 16 } },
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: expandedInt.gmail ? '1px solid ' + C.border : 'none', cursor: 'pointer' }, onClick: function() { toggleInt('gmail'); } },
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12 } },
          React.createElement('div', { style: { width: 40, height: 40, borderRadius: 8, background: '#EA433515', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 } }, React.createElement(LIcon, { name: 'inbox', size: 22 })),
          React.createElement('div', null,
            React.createElement('div', { style: { fontSize: 15, fontWeight: 600, color: C.text } }, 'Gmail'),
            React.createElement('div', { style: { fontSize: 12, color: C.textMuted } },
              gmailAccounts.length
                ? gmailAccounts.filter(function(a) { return a.enabled && a.has_secret; }).length + ' of ' + gmailAccounts.length + ' account' + (gmailAccounts.length === 1 ? '' : 's') + ' connected'
                : 'No accounts yet')
          )
        ),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
            React.createElement('div', { style: { width: 8, height: 8, borderRadius: 4, background: gmailAccounts.some(function(a) { return a.enabled && a.has_secret; }) ? C.success : C.textDim } }),
            React.createElement('span', { style: { fontSize: 12, color: gmailAccounts.some(function(a) { return a.enabled && a.has_secret; }) ? C.success : C.textMuted, fontWeight: 500 } }, gmailAccounts.some(function(a) { return a.enabled && a.has_secret; }) ? 'Enabled' : 'Disabled')
          ),
          React.createElement(LIcon, { name: expandedInt.gmail ? 'chevron-up' : 'chevron-down', size: 16, style: { color: C.textMuted } })
        )
      ),
      expandedInt.gmail && React.createElement('div', { style: { padding: 20 } },
        React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12, lineHeight: 1.5 } },
          'Google Workspace accounts the system can read over IMAP. Each account uses an app password (Google Account → Security → 2-Step Verification → App passwords). Mark one account as the invoice filing inbox — that’s the mailbox Danny reads.'),
        gmailAccounts.map(function(acct) {
          var t = gmailTests[acct.id];
          return React.createElement('div', { key: acct.id, style: { border: '1px solid ' + C.border, borderRadius: 10, padding: 16, marginBottom: 12 } },
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 } },
              React.createElement('div', { style: { width: 7, height: 7, borderRadius: 4, background: acct.enabled && acct.has_secret ? C.success : C.textDim } }),
              React.createElement('span', { style: { fontSize: 13, fontWeight: 700, color: C.text } }, acct.display_name || acct.email || 'New account'),
              acct.docs_inbox && React.createElement('span', { style: { fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 9, background: 'rgba(234,179,8,0.12)', color: '#EAB308' } }, 'INVOICE FILING INBOX')
            ),
            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 } },
              React.createElement(Inp, { label: 'Label', placeholder: 'e.g. Docs inbox', value: acct.display_name, onChange: function(e) { updateGmailAccount(acct.id, { display_name: e.target.value }); } }),
              React.createElement(Inp, { label: 'Email address', placeholder: 'docs@atharvaimports.com', value: acct.email, onChange: function(e) { updateGmailAccount(acct.id, { email: e.target.value }); } })
            ),
            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr', gap: 12, marginBottom: 12 } },
              React.createElement(Inp, { label: 'App password' + (acct.has_secret ? ' (saved — leave blank to keep)' : ''), type: 'password',
                placeholder: acct.has_secret ? '••••••••••••••' : 'xxxx xxxx xxxx xxxx',
                value: acct.password, onChange: function(e) { updateGmailAccount(acct.id, { password: e.target.value }); } })
            ),
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12, flexWrap: 'wrap' } },
              React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.text, cursor: 'pointer' } },
                React.createElement('input', { type: 'checkbox', checked: acct.enabled, onChange: function(e) { updateGmailAccount(acct.id, { enabled: e.target.checked }); } }),
                'Enabled'),
              React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.text, cursor: 'pointer' } },
                React.createElement('input', { type: 'checkbox', checked: acct.docs_inbox, onChange: function(e) { updateGmailAccount(acct.id, { docs_inbox: e.target.checked }); } }),
                'Invoice filing inbox (Danny reads this)'),
              acct.docs_inbox && React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.text, cursor: 'pointer' } },
                React.createElement('input', { type: 'checkbox', checked: acct.pilot, onChange: function(e) { updateGmailAccount(acct.id, { pilot: e.target.checked }); } }),
                'Pilot mode (propose only, no Dropbox writes)')
            ),
            React.createElement('div', { style: { display: 'flex', gap: 8 } },
              React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: function() { saveGmailAccount(acct); } }, React.createElement(LIcon, { name: 'save', size: 14 }), ' Save'),
              acct.has_secret && React.createElement(Btn, { size: 'sm', disabled: t && t.testing, onClick: function() { testGmailAccount(acct); } },
                t && t.testing ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Testing...'] : [React.createElement(LIcon, { name: 'flask-conical', size: 14, key: 'ic' }), ' Test Connection']),
              React.createElement(Btn, { size: 'sm', onClick: function() { removeGmailAccount(acct); } }, React.createElement(LIcon, { name: 'trash-2', size: 14 }), ' Remove')
            ),
            t && !t.testing && React.createElement('div', { style: { padding: '10px 14px', borderRadius: 8, marginTop: 12,
              background: t.success ? C.success + '15' : C.danger + '15',
              border: '1px solid ' + (t.success ? C.success + '40' : C.danger + '40'),
              color: t.success ? C.success : C.danger, fontSize: 13 }
            }, t.success ? React.createElement(LIcon, { name: 'check', size: 14 }) : React.createElement(LIcon, { name: 'x', size: 14 }), ' ', t.message)
          );
        }),
        React.createElement(Btn, { size: 'sm', onClick: function() { setAddNewModal({ channel: 'gmail' }); } }, React.createElement(LIcon, { name: 'plus', size: 14 }), ' Add Gmail account'),
        React.createElement(IntegrationApiLog, { channel: 'gmail', label: 'Gmail' })
      )
    ) },
    // Dropbox Card
    { title: 'Dropbox', el: existingChannels.dropbox && React.createElement(Card, { key: 'dropbox-card', style: { padding: 0, overflow: 'hidden', marginTop: 16 } },
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: expandedInt.dropbox ? '1px solid ' + C.border : 'none', cursor: 'pointer' }, onClick: function() { toggleInt('dropbox'); } },
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12 } },
          React.createElement('div', { style: { width: 40, height: 40, borderRadius: 8, background: '#0061FF15', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 } }, React.createElement(LIcon, { name: 'folder', size: 22 })),
          React.createElement('div', null,
            React.createElement('div', { style: { fontSize: 15, fontWeight: 600, color: C.text } }, 'Dropbox'),
            React.createElement('div', { style: { fontSize: 12, color: C.textMuted } }, dropbox.enabled && hasDropboxToken ? 'Connected — invoice filing into vendor folders' : hasDropboxToken ? 'Authorized, disabled' : 'Not connected')
          )
        ),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
            React.createElement('div', { style: { width: 8, height: 8, borderRadius: 4, background: dropbox.enabled && hasDropboxToken ? C.success : C.textDim } }),
            React.createElement('span', { style: { fontSize: 12, color: dropbox.enabled && hasDropboxToken ? C.success : C.textMuted, fontWeight: 500 } }, dropbox.enabled && hasDropboxToken ? 'Enabled' : 'Disabled')
          ),
          React.createElement(LIcon, { name: expandedInt.dropbox ? 'chevron-up' : 'chevron-down', size: 16, style: { color: C.textMuted } })
        )
      ),
      expandedInt.dropbox && React.createElement('div', { style: { padding: 20 } },
        React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12, lineHeight: 1.5 } },
          'Lets Danny file renamed invoices into per-vendor Dropbox folders. Create a scoped app with Full Dropbox access at dropbox.com/developers, paste its key and secret here, then authorize once.'),
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 } },
          React.createElement(Inp, { label: 'App key', placeholder: 'From the Dropbox app console', value: dropbox.app_key, onChange: function(e) { setDropbox(Object.assign({}, dropbox, { app_key: e.target.value })); } }),
          React.createElement(Inp, { label: 'App secret' + (hasDropboxSecret ? ' (saved — leave blank to keep)' : ''), type: 'password',
            placeholder: hasDropboxSecret ? '••••••••••••••' : 'Enter app secret',
            value: dropbox.app_secret, onChange: function(e) { setDropbox(Object.assign({}, dropbox, { app_secret: e.target.value })); } })
        ),
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr', gap: 12, marginBottom: 12 } },
          React.createElement(Inp, { label: 'Vendor folders root path', placeholder: '/Atharva Invoices & Vendor Documents',
            value: dropbox.root_path, onChange: function(e) { setDropbox(Object.assign({}, dropbox, { root_path: e.target.value })); } })
        ),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 } },
          React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.text, cursor: 'pointer' } },
            React.createElement('input', { type: 'checkbox', checked: dropbox.enabled, onChange: function(e) { setDropbox(Object.assign({}, dropbox, { enabled: e.target.checked })); } }),
            'Enable Dropbox')
        ),
        React.createElement('div', { style: { display: 'flex', gap: 8, marginBottom: hasDropboxSecret && !hasDropboxToken ? 16 : 0 } },
          React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: saveDropboxCreds }, React.createElement(LIcon, { name: 'save', size: 14 }), ' Save'),
          hasDropboxToken && React.createElement(Btn, { size: 'sm', onClick: testDropboxConnection, disabled: dropboxTesting },
            dropboxTesting ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Testing...'] : [React.createElement(LIcon, { name: 'flask-conical', size: 14, key: 'ic' }), ' Test Connection'])
        ),
        hasDropboxSecret && !hasDropboxToken && React.createElement('div', { style: { border: '1px solid ' + C.border, borderRadius: 10, padding: 14 } },
          React.createElement('div', { style: { fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 6 } }, 'One-time authorization'),
          React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 10, lineHeight: 1.5 } },
            '1. Open the authorization page and click Allow.  2. Copy the code Dropbox shows.  3. Paste it here and connect.'),
          React.createElement('div', { style: { display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' } },
            dropbox.app_key && React.createElement('a', { href: 'https://www.dropbox.com/oauth2/authorize?client_id=' + encodeURIComponent(dropbox.app_key) + '&response_type=code&token_access_type=offline', target: '_blank', rel: 'noopener', style: { textDecoration: 'none' } },
              React.createElement(Btn, { size: 'sm' }, React.createElement(LIcon, { name: 'external-link', size: 14 }), ' Open Dropbox authorization')),
            React.createElement('div', { style: { minWidth: 220, flex: 1 } },
              React.createElement(Inp, { label: 'Authorization code', placeholder: 'Paste the code from Dropbox',
                value: dropbox.auth_code, onChange: function(e) { setDropbox(Object.assign({}, dropbox, { auth_code: e.target.value })); } })),
            React.createElement(Btn, { variant: 'primary', size: 'sm', disabled: !(dropbox.auth_code || '').trim() || dropboxConnecting, onClick: connectDropbox },
              dropboxConnecting ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Connecting...'] : 'Connect')
          )
        ),
        dropboxTest && React.createElement('div', { style: { padding: '10px 14px', borderRadius: 8, marginTop: 12,
          background: dropboxTest.success ? C.success + '15' : C.danger + '15',
          border: '1px solid ' + (dropboxTest.success ? C.success + '40' : C.danger + '40'),
          color: dropboxTest.success ? C.success : C.danger, fontSize: 13 }
        }, dropboxTest.success ? React.createElement(LIcon, { name: 'check', size: 14 }) : React.createElement(LIcon, { name: 'x', size: 14 }), ' ', dropboxTest.message),
        React.createElement(IntegrationApiLog, { channel: 'dropbox', label: 'Dropbox' })
      )
    ) },
    // SendGrid Card
    // Firebase (text-message 2FA codes) card — 9-05
    { title: 'Firebase (SMS codes)', el: existingChannels.firebase && React.createElement(Card, { key: 'firebase-card', style: { padding: 0, overflow: 'hidden', marginTop: 16 } },
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: expandedInt.firebase ? '1px solid ' + C.border : 'none', cursor: 'pointer' }, onClick: function() { toggleInt('firebase'); } },
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12 } },
          React.createElement('div', { style: { width: 40, height: 40, borderRadius: 8, background: '#F59E0B15', display: 'flex', alignItems: 'center', justifyContent: 'center' } }, React.createElement(LIcon, { name: 'smartphone', size: 20, color: '#F59E0B' })),
          React.createElement('div', null,
            React.createElement('div', { style: { fontSize: 15, fontWeight: 600, color: C.text } }, 'Firebase (SMS codes)'),
            React.createElement('div', { style: { fontSize: 12, color: C.textMuted } }, firebase.enabled && hasFirebaseKey ? 'Connected — text-message sign-in codes' : 'Not connected')
          )
        ),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
            React.createElement('div', { style: { width: 8, height: 8, borderRadius: 4, background: firebase.enabled && hasFirebaseKey ? C.success : C.textDim } }),
            React.createElement('span', { style: { fontSize: 12, color: firebase.enabled && hasFirebaseKey ? C.success : C.textMuted, fontWeight: 500 } }, firebase.enabled && hasFirebaseKey ? 'Enabled' : 'Disabled')
          ),
          React.createElement(LIcon, { name: expandedInt.firebase ? 'chevron-up' : 'chevron-down', size: 16, style: { color: C.textMuted } })
        )
      ),
      expandedInt.firebase && React.createElement('div', { style: { padding: 20 } },
        React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12, lineHeight: 1.5 } },
          'Lets users receive their sign-in code by text message. Google sends and checks the code through Firebase Authentication; this app never touches the SMS itself and the phone number never leaves the server. ',
          'Setup: Firebase project on the Blaze plan → Authentication → Sign-in method → Phone enabled → Authorized domains include app.atharvatoolz.com → paste the Web API key from Project settings. Restrict SMS regions in the Firebase console to the countries your team is in.'),
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 } },
          React.createElement(Inp, { label: 'Project ID', placeholder: 'atharva-toolz', value: firebase.project_id, onChange: function(e) { setFirebase(Object.assign({}, firebase, { project_id: e.target.value })); } }),
          React.createElement(Inp, { label: 'Web API key' + (hasFirebaseKey ? ' (saved — leave blank to keep)' : ''), type: 'password',
            placeholder: hasFirebaseKey ? '••••••••••••••' : 'AIza…',
            value: firebase.api_key, onChange: function(e) { setFirebase(Object.assign({}, firebase, { api_key: e.target.value })); } })
        ),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 } },
          React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.text, cursor: 'pointer' } },
            React.createElement('input', { type: 'checkbox', checked: firebase.enabled, onChange: function(e) { setFirebase(Object.assign({}, firebase, { enabled: e.target.checked })); } }),
            'Enable text-message codes')
        ),
        React.createElement('div', { style: { display: 'flex', gap: 8 } },
          React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: saveFirebaseCreds }, React.createElement(LIcon, { name: 'save', size: 14 }), ' Save'),
          hasFirebaseKey && React.createElement(Btn, { size: 'sm', onClick: testFirebaseConnection, disabled: firebaseTesting }, firebaseTesting ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Testing...'] : [React.createElement(LIcon, { name: 'plug-zap', size: 14, key: 'ic' }), ' Test Connection'])
        ),
        firebaseTest && React.createElement('div', { style: { padding: '10px 14px', borderRadius: 8, marginTop: 12,
          background: firebaseTest.success ? C.success + '15' : C.danger + '15',
          border: '1px solid ' + (firebaseTest.success ? C.success + '40' : C.danger + '40'),
          color: firebaseTest.success ? C.success : C.danger, fontSize: 13 }
        }, firebaseTest.success ? React.createElement(LIcon, { name: 'check', size: 14 }) : React.createElement(LIcon, { name: 'x', size: 14 }), ' ', firebaseTest.message),
        React.createElement(IntegrationApiLog, { channel: 'firebase', label: 'Firebase' })
      )
    ) },
    { title: 'SendGrid', el: existingChannels.sendgrid && React.createElement(Card, { key: 'sendgrid-card', style: { padding: 0, overflow: 'hidden', marginTop: 16 } },
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: expandedInt.sendgrid ? '1px solid ' + C.border : 'none', cursor: 'pointer' }, onClick: function() { toggleInt('sendgrid'); } },
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12 } },
          React.createElement('div', { style: { width: 40, height: 40, borderRadius: 8, background: '#1A82E215', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 } }, React.createElement(LIcon, { name: 'send', size: 22 })),
          React.createElement('div', null,
            React.createElement('div', { style: { fontSize: 15, fontWeight: 600, color: C.text } }, 'SendGrid'),
            React.createElement('div', { style: { fontSize: 12, color: C.textMuted } }, sendgrid.enabled && hasSendgridKey ? 'Connected — Email via SendGrid API' : 'Not connected')
          )
        ),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
            React.createElement('div', { style: { width: 8, height: 8, borderRadius: 4, background: sendgrid.enabled && hasSendgridKey ? C.success : C.textDim } }),
            React.createElement('span', { style: { fontSize: 12, color: sendgrid.enabled && hasSendgridKey ? C.success : C.textMuted, fontWeight: 500 } }, sendgrid.enabled && hasSendgridKey ? 'Enabled' : 'Disabled')
          ),
          React.createElement(LIcon, { name: expandedInt.sendgrid ? 'chevron-up' : 'chevron-down', size: 16, style: { color: C.textMuted } })
        )
      ),
      expandedInt.sendgrid && React.createElement('div', { style: { padding: 20 } },
        React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12, lineHeight: 1.5 } }, 'Use SendGrid to send emails via HTTPS API. Works on servers where SMTP ports are blocked (e.g. Digital Ocean). Free tier: 100 emails/day.'),
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr', gap: 12, marginBottom: 12 } },
          React.createElement(Inp, { label: 'API Key' + (hasSendgridKey ? ' (saved — leave blank to keep)' : ''), type: 'password',
            placeholder: hasSendgridKey ? '••••••••••••••' : 'SG.xxxxxxxxxx...',
            value: sendgrid.api_key, onChange: function(e) { setSendgrid(Object.assign({}, sendgrid, { api_key: e.target.value })); } })
        ),
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 } },
          React.createElement(Inp, { label: 'From Email', placeholder: 'noreply@company.com',
            value: sendgrid.from_email, onChange: function(e) { setSendgrid(Object.assign({}, sendgrid, { from_email: e.target.value })); } }),
          React.createElement(Inp, { label: 'From Name', placeholder: 'Company Name',
            value: sendgrid.from_name, onChange: function(e) { setSendgrid(Object.assign({}, sendgrid, { from_name: e.target.value })); } })
        ),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 } },
          React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.text, cursor: 'pointer' } },
            React.createElement('input', { type: 'checkbox', checked: sendgrid.enabled, onChange: function(e) { setSendgrid(Object.assign({}, sendgrid, { enabled: e.target.checked })); } }),
            'Enable SendGrid')
        ),
        React.createElement('div', { style: { display: 'flex', gap: 8 } },
          React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: saveSendgridCreds }, React.createElement(LIcon, { name: 'save', size: 14 }), ' Save'),
          hasSendgridKey && React.createElement(Btn, { size: 'sm', onClick: testSendgridConnection, disabled: sendgridTesting }, sendgridTesting ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Testing...'] : [React.createElement(LIcon, { name: 'flask-conical', size: 14, key: 'ic' }), ' Test Connection'])
        ),
        sendgridTest && React.createElement('div', { style: { padding: '10px 14px', borderRadius: 8, marginTop: 12,
          background: sendgridTest.success ? C.success + '15' : C.danger + '15',
          border: '1px solid ' + (sendgridTest.success ? C.success + '40' : C.danger + '40'),
          color: sendgridTest.success ? C.success : C.danger, fontSize: 13 }
        }, sendgridTest.success ? React.createElement(LIcon, { name: 'check', size: 14 }) : React.createElement(LIcon, { name: 'x', size: 14 }), ' ', sendgridTest.message),
        React.createElement(IntegrationApiLog, { channel: 'sendgrid', label: 'SendGrid' })
      )
    ) },
    // ShipStation Card
    { title: 'ShipStation', el: existingChannels.shipstation && React.createElement(Card, { key: 'shipstation-card', style: { padding: 0, overflow: 'hidden', marginTop: 16 } },
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: expandedInt.shipstation ? '1px solid ' + C.border : 'none', cursor: 'pointer' }, onClick: function() { toggleInt('shipstation'); } },
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12 } },
          React.createElement('div', { style: { width: 40, height: 40, borderRadius: 8, background: '#62BD4F15', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 } }, React.createElement(LIcon, { name: 'package', size: 22 })),
          React.createElement('div', null,
            React.createElement('div', { style: { fontSize: 15, fontWeight: 600, color: C.text } }, 'ShipStation'),
            React.createElement('div', { style: { fontSize: 12, color: C.textMuted } }, shipstation.enabled && hasShipstationSecret ? 'Connected — FBM shipping cost tracking' : 'Not connected')
          )
        ),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
            React.createElement('div', { style: { width: 8, height: 8, borderRadius: 4, background: shipstation.enabled && hasShipstationSecret ? C.success : C.textDim } }),
            React.createElement('span', { style: { fontSize: 12, color: shipstation.enabled && hasShipstationSecret ? C.success : C.textMuted, fontWeight: 500 } }, shipstation.enabled && hasShipstationSecret ? 'Enabled' : 'Disabled')
          ),
          React.createElement(LIcon, { name: expandedInt.shipstation ? 'chevron-up' : 'chevron-down', size: 16, style: { color: C.textMuted } })
        )
      ),
      expandedInt.shipstation && React.createElement('div', { style: { padding: '16px 20px' } },
        React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12, lineHeight: 1.5 } },
          'Import actual shipping costs from ShipStation for FBM (Fulfilled by Merchant) orders. Costs are distributed by weight across SKUs and used in profitability analysis instead of FBA fees.'),
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 } },
          React.createElement(Inp, { label: 'API Key', placeholder: 'Enter ShipStation API Key...',
            value: shipstation.api_key, onChange: function(e) { setShipstation(Object.assign({}, shipstation, { api_key: e.target.value })); } }),
          React.createElement(Inp, { label: 'API Secret' + (hasShipstationSecret ? ' (saved — leave blank to keep)' : ''), type: 'password',
            placeholder: hasShipstationSecret ? '••••••••••••••••••••' : 'Enter API Secret...',
            value: shipstation.api_secret, onChange: function(e) { setShipstation(Object.assign({}, shipstation, { api_secret: e.target.value })); } })
        ),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 } },
          React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.text, cursor: 'pointer' } },
            React.createElement('input', { type: 'checkbox', checked: shipstation.enabled, onChange: function(e) { setShipstation(Object.assign({}, shipstation, { enabled: e.target.checked })); } }),
            'Enable')
        ),
        React.createElement('div', { style: { display: 'flex', gap: 8, marginBottom: 12 } },
          React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: saveShipstationCreds }, React.createElement(LIcon, { name: 'save', size: 14 }), ' Save'),
          hasShipstationSecret && React.createElement(Btn, { size: 'sm', onClick: testShipstationConnection, disabled: shipstationTesting }, shipstationTesting ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Testing...'] : [React.createElement(LIcon, { name: 'flask-conical', size: 14, key: 'ic' }), ' Test Connection']),
          hasShipstationSecret && shipstation.enabled && React.createElement(Btn, { size: 'sm', onClick: function() { syncShipstationCosts(); }, disabled: shipstationSyncing }, shipstationSyncing ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Syncing...'] : [React.createElement(LIcon, { name: 'refresh-cw', size: 14, key: 'ic' }), ' Sync New']),
          hasShipstationSecret && shipstation.enabled && React.createElement(Btn, { size: 'sm', onClick: function() { syncShipstationCosts(90); }, disabled: shipstationSyncing }, React.createElement(LIcon, { name: 'refresh-cw', size: 14 }), ' 90d'),
          hasShipstationSecret && shipstation.enabled && React.createElement(Btn, { size: 'sm', onClick: function() { syncShipstationCosts(365); }, disabled: shipstationSyncing }, React.createElement(LIcon, { name: 'refresh-cw', size: 14 }), ' 1yr'),
          hasShipstationSecret && shipstation.enabled && React.createElement(Btn, { size: 'sm', onClick: function() { syncShipstationCosts(1095); }, disabled: shipstationSyncing }, React.createElement(LIcon, { name: 'refresh-cw', size: 14 }), ' All')
        ),
        shipstationTest && React.createElement('div', { style: { padding: '10px 14px', borderRadius: 8, marginBottom: 12,
          background: shipstationTest.success ? C.success + '15' : C.danger + '15',
          border: '1px solid ' + (shipstationTest.success ? C.success + '40' : C.danger + '40'),
          color: shipstationTest.success ? C.success : C.danger, fontSize: 13 }
        }, shipstationTest.success ? React.createElement(LIcon, { name: 'check', size: 14 }) : React.createElement(LIcon, { name: 'x', size: 14 }), ' ', shipstationTest.message),
        shipstationSyncing && React.createElement('div', { style: { padding: '10px 14px', borderRadius: 8, marginBottom: 12,
          background: C.accent + '15', border: '1px solid ' + C.accent + '40', color: C.accent, fontSize: 13,
          display: 'flex', alignItems: 'center', gap: 8 } },
          React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true }),
          (function() {
            var label = 'Syncing in background';
            if (shipstationSyncMode) label += ' (' + shipstationSyncMode + ')';
            if (shipstationSyncStartedAt) {
              var secs = Math.max(0, Math.round((Date.now() - Date.parse(shipstationSyncStartedAt)) / 1000));
              var elapsed = secs < 60 ? (secs + 's') : (Math.floor(secs/60) + 'm ' + (secs%60) + 's');
              label += ' — ' + elapsed + ' elapsed';
            }
            return label;
          })()
        ),
        shipstationSyncResult && !shipstationSyncing && React.createElement('div', { style: { padding: '10px 14px', borderRadius: 8, marginBottom: 12,
          background: shipstationSyncResult.error ? C.danger + '15' : C.accent + '15',
          border: '1px solid ' + (shipstationSyncResult.error ? C.danger + '40' : C.accent + '40'),
          color: shipstationSyncResult.error ? C.danger : C.accent, fontSize: 13 }
        }, shipstationSyncResult.error ? [React.createElement(LIcon, { name: 'x', size: 14, key: 'ic' }), ' ' + shipstationSyncResult.error] : [React.createElement(LIcon, { name: 'check', size: 14, key: 'ic' }), ' Synced ' + (shipstationSyncResult.synced || 0) + ' records from ' + (shipstationSyncResult.shipments || 0) + ' shipments (' + (shipstationSyncResult.skus_updated || 0) + ' SKUs)' + (shipstationSyncResult.shipping_revenue_updated ? ', ' + shipstationSyncResult.shipping_revenue_updated + ' order totals updated' : '')]),
        shipstation.last_sync_at && React.createElement('div', { style: { fontSize: 11, color: C.textMuted } }, 'Last synced: ' + new Date(shipstation.last_sync_at).toLocaleString()),
        React.createElement(IntegrationApiLog, { channel: 'shipstation', label: 'ShipStation' })
      )
    ) },
    // Shopify Card
    // USPS.com portal (Tracy signs in with it to file claims and unused-label refunds — Phase 2, 2026-10-13)
    { title: 'USPS.com portal', el: existingChannels.usps_portal && React.createElement(Card, { key: 'usps-portal-card', style: { padding: 0, overflow: 'hidden', marginTop: 16 } },
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: expandedInt.usps_portal ? '1px solid ' + C.border : 'none', cursor: 'pointer' }, onClick: function() { toggleInt('usps_portal'); } },
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12 } },
          React.createElement('div', { style: { width: 40, height: 40, borderRadius: 8, background: '#33336615', display: 'flex', alignItems: 'center', justifyContent: 'center' } }, React.createElement(LIcon, { name: 'lock', size: 20, style: { color: '#6b6bd6' } })),
          React.createElement('div', null,
            React.createElement('div', { style: { fontSize: 15, fontWeight: 600, color: C.text } }, 'USPS.com portal'),
            React.createElement('div', { style: { fontSize: 12, color: C.textMuted } }, uspsPortal.enabled && hasUspsPortalSecret ? 'Connected — Tracy files USPS claims and unused-label refunds with this login' : 'Not connected')
          )
        ),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
            React.createElement('div', { style: { width: 8, height: 8, borderRadius: 4, background: uspsPortal.enabled && hasUspsPortalSecret ? C.success : C.textDim } }),
            React.createElement('span', { style: { fontSize: 12, color: uspsPortal.enabled && hasUspsPortalSecret ? C.success : C.textMuted, fontWeight: 500 } }, uspsPortal.enabled && hasUspsPortalSecret ? 'Active' : 'Inactive')
          ),
          React.createElement(LIcon, { name: expandedInt.usps_portal ? 'chevron-up' : 'chevron-down', size: 16, style: { color: C.textMuted } })
        )
      ),
      expandedInt.usps_portal && React.createElement('div', { style: { padding: '16px 20px' } },
        React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12, lineHeight: 1.5 } },
          'The usps.com login Tracy uses on the server to file domestic claims and upload the monthly unused-label refund file (the same login as the Business Customer Gateway). It is stored encrypted. If usps.com asks for a one-time code on the first sign-in from the server, give Tracy the mailbox that receives it (a Gmail app password, not the mailbox password).'),
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 } },
          React.createElement(Inp, { label: 'usps.com username', placeholder: 'Username', value: uspsPortal.username,
            onChange: function(e) { setUspsPortal(Object.assign({}, uspsPortal, { username: e.target.value })); } }),
          React.createElement(Inp, { label: 'Password' + (hasUspsPortalSecret ? ' (saved — leave blank to keep)' : ''), type: 'password',
            placeholder: hasUspsPortalSecret ? '••••••••••••' : 'Password', value: uspsPortal.password,
            onChange: function(e) { setUspsPortal(Object.assign({}, uspsPortal, { password: e.target.value })); } })
        ),
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 } },
          React.createElement(Inp, { label: 'Mailbox for one-time codes (optional)', placeholder: 'anthony@atharvaimports.com', value: uspsPortal.otp_user,
            onChange: function(e) { setUspsPortal(Object.assign({}, uspsPortal, { otp_user: e.target.value })); } }),
          React.createElement(Inp, { label: 'Mailbox app password' + (hasUspsOtpPass ? ' (saved — leave blank to keep)' : ''), type: 'password',
            placeholder: hasUspsOtpPass ? '••••••••••••' : '16-character Gmail app password', value: uspsPortal.otp_pass,
            onChange: function(e) { setUspsPortal(Object.assign({}, uspsPortal, { otp_pass: e.target.value })); } })
        ),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 } },
          React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.text, cursor: 'pointer' } },
            React.createElement('input', { type: 'checkbox', checked: uspsPortal.enabled, onChange: function(e) { setUspsPortal(Object.assign({}, uspsPortal, { enabled: e.target.checked })); } }),
            'Enable')
        ),
        React.createElement('div', { style: { display: 'flex', gap: 8, marginBottom: 12 } },
          React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: saveUspsPortal }, React.createElement(LIcon, { name: 'save', size: 14 }), ' Save'),
          hasUspsPortalSecret && React.createElement(Btn, { size: 'sm', onClick: testUspsPortal, disabled: uspsPortalTesting },
            uspsPortalTesting ? [React.createElement(LIcon, { key: 'i', name: 'loader-2', size: 14, spin: true }), ' Signing in on the server (up to a minute)…'] : [React.createElement(LIcon, { key: 'i', name: 'log-in', size: 14 }), ' Test sign-in'])
        ),
        uspsPortalTest && React.createElement('div', { style: { padding: '10px 14px', borderRadius: 8, marginBottom: 12,
          background: uspsPortalTest.success ? C.success + '15' : C.danger + '15',
          border: '1px solid ' + (uspsPortalTest.success ? C.success + '40' : C.danger + '40'),
          color: uspsPortalTest.success ? C.success : C.danger, fontSize: 13 }
        }, uspsPortalTest.success ? React.createElement(LIcon, { name: 'check', size: 14 }) : React.createElement(LIcon, { name: 'x', size: 14 }), ' ', uspsPortalTest.message)
      )
    ) },
    { title: 'Shopify', el: existingChannels.shopify && React.createElement(Card, { key: 'shopify-card', style: { padding: 0, overflow: 'hidden', marginTop: 16 } },
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: expandedInt.shopify ? '1px solid ' + C.border : 'none', cursor: 'pointer' }, onClick: function() { toggleInt('shopify'); } },
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12 } },
          React.createElement('div', { style: { width: 40, height: 40, borderRadius: 8, background: '#95BF4715', display: 'flex', alignItems: 'center', justifyContent: 'center' } },
            React.createElement('svg', { width: 26, height: 26, viewBox: '0 0 24 24', style: { display: 'block' } },
              React.createElement('circle', { cx: 12, cy: 12, r: 12, fill: '#95BF47' }),
              React.createElement('text', { x: 12, y: 16.5, textAnchor: 'middle', fill: '#FFFFFF', fontSize: 12, fontWeight: 700, fontFamily: 'Arial' }, 'S')
            )
          ),
          React.createElement('div', null,
            React.createElement('div', { style: { fontSize: 15, fontWeight: 600, color: C.text } }, 'Shopify'),
            React.createElement('div', { style: { fontSize: 12, color: C.textMuted } }, shopify.enabled && hasShopifySecret ? 'Connected' : 'Not connected')
          )
        ),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
            React.createElement('div', { style: { width: 8, height: 8, borderRadius: 4, background: shopify.enabled && hasShopifySecret ? C.success : C.textDim } }),
            React.createElement('span', { style: { fontSize: 12, color: shopify.enabled && hasShopifySecret ? C.success : C.textMuted, fontWeight: 500 } }, shopify.enabled && hasShopifySecret ? 'Enabled' : 'Disabled')
          ),
          React.createElement(LIcon, { name: expandedInt.shopify ? 'chevron-up' : 'chevron-down', size: 16, style: { color: C.textMuted } })
        )
      ),
      expandedInt.shopify && React.createElement('div', { style: { padding: 20 } },
        React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12, lineHeight: 1.5 } }, 'Connects via a Dev Dashboard custom app (OAuth client credentials). Use your .myshopify.com domain — not the public store domain. Product FAQs are written by Sage in The Office.'),
        React.createElement('div', { style: { marginBottom: 12 } },
          React.createElement(Inp, { label: 'Store domain (.myshopify.com)', placeholder: 'your-store.myshopify.com', value: shopify.shop || '', onChange: function(e) { setShopify(Object.assign({}, shopify, { shop: e.target.value })); } })
        ),
        React.createElement('div', { style: { marginBottom: 12 } },
          React.createElement(Inp, { label: 'Client ID', placeholder: 'Dev Dashboard > app > Settings', value: shopify.client_id || '', onChange: function(e) { setShopify(Object.assign({}, shopify, { client_id: e.target.value })); } })
        ),
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, marginBottom: 12, alignItems: 'end' } },
          React.createElement(Inp, { label: 'Client Secret' + (hasShopifySecret ? ' (saved — leave blank to keep)' : ''), type: 'password',
            placeholder: hasShopifySecret ? '••••••••••••••••••••' : 'shpss_...',
            value: shopify.client_secret || '', onChange: function(e) { setShopify(Object.assign({}, shopify, { client_secret: e.target.value })); } }),
          React.createElement('div', { style: { paddingBottom: 4 } },
            React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: C.text } },
              React.createElement('input', { type: 'checkbox', checked: shopify.enabled, onChange: function(e) { setShopify(Object.assign({}, shopify, { enabled: e.target.checked })); } }),
              'Enable'
            )
          )
        ),
        React.createElement('div', { style: { display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' } },
          React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: saveShopify }, React.createElement(LIcon, { name: 'save', size: 14 }), ' Save'),
          React.createElement(Btn, { size: 'sm', onClick: testShopify, disabled: shopifyTesting || !hasShopifySecret }, shopifyTesting ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Testing...'] : [React.createElement(LIcon, { name: 'plug', size: 14, key: 'ic' }), ' Test Connection']),
          shopify.enabled && hasShopifySecret && React.createElement('a', { href: '/the-office/customer-support/sage',
            onClick: function(e) { if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return; e.preventDefault(); onNavigate('agent-office', '/product-data/sage'); },
            style: { background: '#95BF47', color: '#fff', textDecoration: 'none', padding: '6px 12px', fontSize: 12, borderRadius: 6, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 'auto', cursor: 'pointer' } },
            React.createElement(LIcon, { name: 'sparkles', size: 14 }), ' Sage\'s desk (product FAQs)')
        ),
        shopifyTest && React.createElement('div', { style: { padding: '10px 14px', borderRadius: 8,
          background: shopifyTest.success ? C.success + '15' : C.danger + '15',
          border: '1px solid ' + (shopifyTest.success ? C.success + '40' : C.danger + '40'),
          color: shopifyTest.success ? C.success : C.danger, fontSize: 13 }
        }, shopifyTest.success ? React.createElement(LIcon, { name: 'check', size: 14 }) : React.createElement(LIcon, { name: 'x', size: 14 }), ' ', shopifyTest.message),
        React.createElement(IntegrationApiLog, { channel: 'shopify', label: 'Shopify' })
      )
    ) },
    // Walmart Card
    { title: 'Walmart Marketplace', el: existingChannels.walmart && React.createElement(Card, { key: 'walmart-card', style: { padding: 0, overflow: 'hidden', marginTop: 16 } },
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: expandedInt.walmart ? '1px solid ' + C.border : 'none', cursor: 'pointer' }, onClick: function() { toggleInt('walmart'); } },
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12 } },
          React.createElement('div', { style: { width: 40, height: 40, borderRadius: 8, background: '#0071DC15', display: 'flex', alignItems: 'center', justifyContent: 'center' } }, channelIcon('walmart', 28)),
          React.createElement('div', null,
            React.createElement('div', { style: { fontSize: 15, fontWeight: 600, color: C.text } }, walmart.display_name || 'Walmart Marketplace'),
            React.createElement('div', { style: { fontSize: 12, color: C.textMuted } }, walmart.enabled ? 'Connected — ' + (walmart.environment || 'sandbox') : 'Not connected')
          )
        ),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
            React.createElement('div', { style: { width: 8, height: 8, borderRadius: 4, background: walmart.enabled ? C.success : C.textDim } }),
            React.createElement('span', { style: { fontSize: 12, color: walmart.enabled ? C.success : C.textMuted, fontWeight: 500 } }, walmart.enabled ? 'Enabled' : 'Disabled')
          ),
          React.createElement(LIcon, { name: expandedInt.walmart ? 'chevron-up' : 'chevron-down', size: 16, style: { color: C.textMuted } })
        )
      ),
      expandedInt.walmart && React.createElement('div', { style: { padding: 20 } },
        React.createElement('div', { style: { fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 } }, 'API Credentials'),
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 } },
          React.createElement(Inp, { label: 'Client ID', placeholder: 'e.g. db7fdea5-013b-41fe-...', ...WF('client_id') }),
          React.createElement(Inp, { label: 'Client Secret' + (hasSecret ? ' (saved — leave blank to keep)' : ''), type: 'password', placeholder: hasSecret ? '••••••••••••••' : 'Enter client secret', ...WF('client_secret') }),
          React.createElement(Inp, { label: 'Display Name (card title)', placeholder: 'e.g. Walmart United States - Atharva Imports', ...WF('display_name') })
        ),
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 } },
          React.createElement(Sel, { label: 'Environment', options: [
            { value: 'sandbox', label: 'Sandbox (Testing)' },
            { value: 'production', label: 'Production (Live)' },
          ], value: walmart.environment || 'sandbox', onChange: function(e) { setWalmart(Object.assign({}, walmart, { environment: e.target.value })); } }),
          React.createElement('div', { style: { display: 'flex', alignItems: 'flex-end', paddingBottom: 4 } },
            React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: C.text } },
              React.createElement('input', { type: 'checkbox', checked: walmart.enabled, onChange: function(e) { setWalmart(Object.assign({}, walmart, { enabled: e.target.checked })); } }),
              'Enable Integration'
            )
          )
        ),
        React.createElement('div', { style: { display: 'flex', gap: 8, marginBottom: 16 } },
          React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: saveCredentials }, React.createElement(LIcon, { name: 'save', size: 14 }), ' Save Credentials'),
          React.createElement(Btn, { size: 'sm', onClick: testConnection, disabled: testing || !hasSecret }, testing ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Testing...'] : [React.createElement(LIcon, { name: 'plug', size: 14, key: 'ic' }), ' Test Connection']),
          React.createElement(Btn, { size: 'sm', onClick: syncOrders, disabled: syncing || !walmart.enabled }, syncing ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Syncing...'] : [React.createElement(LIcon, { name: 'refresh-cw', size: 14, key: 'ic' }), ' Sync Orders Now'])
        ),
        testResult && React.createElement('div', { style: { padding: '10px 14px', borderRadius: 8, marginBottom: 12,
          background: testResult.success ? C.success + '15' : C.danger + '15',
          border: '1px solid ' + (testResult.success ? C.success + '40' : C.danger + '40'),
          color: testResult.success ? C.success : C.danger, fontSize: 13 }
        }, testResult.success ? React.createElement(LIcon, { name: 'check', size: 14 }) : React.createElement(LIcon, { name: 'x', size: 14 }), ' ', testResult.message),
        syncResult && React.createElement('div', { style: { padding: '10px 14px', borderRadius: 8, marginBottom: 12,
          background: syncResult.error ? C.danger + '15' : C.accent + '15',
          border: '1px solid ' + (syncResult.error ? C.danger + '40' : C.accent + '40'),
          color: syncResult.error ? C.danger : C.accent, fontSize: 13 }
        }, syncResult.error ? [React.createElement(LIcon, { name: 'x', size: 14, key: 'ic' }), ' ' + syncResult.error] : [React.createElement(LIcon, { name: 'check', size: 14, key: 'ic' }), ' Synced ' + (syncResult.synced || 0) + ' new, ' + (syncResult.updated || 0) + ' updated' + (syncResult.failed ? ', ' + syncResult.failed + ' failed' : '') + ' (' + (syncResult.total || 0) + ' total orders' + (syncResult.more ? '; more remain, sync again' : '') + ')']),
        walmart.last_sync_at && React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginBottom: 16 } }, 'Last synced: ' + new Date(walmart.last_sync_at).toLocaleString()),
        // ─── WFS Fee Calculator Section ───
        React.createElement('div', { style: { borderTop: '1px solid ' + C.border, paddingTop: 16 } },
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 } },
            React.createElement('div', { style: { fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em' } }, 'WFS Fee Calculator'),
            !wfsFeeLoaded && React.createElement(Btn, { size: 'sm', onClick: loadWfsFeeSettings }, 'Load Settings')
          ),
          wfsFeeLoaded && React.createElement('div', null,
            // Fee Tiers Table
            React.createElement('div', { style: { fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 8 } }, 'Fulfillment Fee Tiers'),
            React.createElement('table', { style: { width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 12 } },
              React.createElement('thead', null,
                React.createElement('tr', { style: { borderBottom: '1px solid ' + C.border } },
                  ['Label', 'Min (lb)', 'Max (lb)', 'Base Fee ($)', '$/lb Over', 'Over Threshold'].map(function(h) {
                    return React.createElement('th', { key: h, style: { padding: '6px 8px', textAlign: 'left', color: C.textMuted, fontWeight: 500 } }, h);
                  })
                )
              ),
              React.createElement('tbody', null,
                wfsFeeTiers.map(function(t, idx) {
                  var updateTier = function(field, val) {
                    var updated = wfsFeeTiers.map(function(tier, i) {
                      return i === idx ? Object.assign({}, tier, { [field]: val }) : tier;
                    });
                    setWfsFeeTiers(updated);
                  };
                  return React.createElement('tr', { key: t.id || idx, style: { borderBottom: '1px solid ' + C.border + '40' } },
                    React.createElement('td', { style: { padding: '4px 8px' } }, React.createElement('input', { value: t.label, onChange: function(e) { updateTier('label', e.target.value); }, style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '4px 6px', borderRadius: 4, width: 100, fontSize: 12, fontFamily: 'inherit' } })),
                    React.createElement('td', { style: { padding: '4px 8px' } }, React.createElement('input', { type: 'number', value: t.min_weight_lb, onChange: function(e) { updateTier('min_weight_lb', e.target.value); }, style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '4px 6px', borderRadius: 4, width: 60, fontSize: 12, fontFamily: 'inherit' } })),
                    React.createElement('td', { style: { padding: '4px 8px' } }, React.createElement('input', { type: 'number', value: t.max_weight_lb || '', placeholder: '∞', onChange: function(e) { updateTier('max_weight_lb', e.target.value || null); }, style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '4px 6px', borderRadius: 4, width: 60, fontSize: 12, fontFamily: 'inherit' } })),
                    React.createElement('td', { style: { padding: '4px 8px' } }, React.createElement('input', { type: 'number', step: '0.01', value: t.base_fee, onChange: function(e) { updateTier('base_fee', e.target.value); }, style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '4px 6px', borderRadius: 4, width: 70, fontSize: 12, fontFamily: 'inherit' } })),
                    React.createElement('td', { style: { padding: '4px 8px' } }, React.createElement('input', { type: 'number', step: '0.01', value: t.per_lb_over, onChange: function(e) { updateTier('per_lb_over', e.target.value); }, style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '4px 6px', borderRadius: 4, width: 60, fontSize: 12, fontFamily: 'inherit' } })),
                    React.createElement('td', { style: { padding: '4px 8px' } }, React.createElement('input', { type: 'number', value: t.per_lb_threshold, onChange: function(e) { updateTier('per_lb_threshold', e.target.value); }, style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '4px 6px', borderRadius: 4, width: 60, fontSize: 12, fontFamily: 'inherit' } }))
                  );
                })
              )
            ),
            React.createElement('div', { style: { display: 'flex', gap: 8, marginBottom: 16 } },
              React.createElement(Btn, { size: 'sm', variant: 'primary', onClick: saveWfsFeeTiers, disabled: wfsFeeSaving }, 'Save Tiers'),
              React.createElement(Btn, { size: 'sm', onClick: function() { setWfsFeeTiers(wfsFeeTiers.concat([{ label: 'New Tier', min_weight_lb: 0, max_weight_lb: null, base_fee: 0, per_lb_over: 0, per_lb_threshold: 0 }])); } }, '+ Add Tier')
            ),
            // Category Referral Fees
            React.createElement('div', { style: { fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 8 } }, 'Category Referral Fees'),
            React.createElement('table', { style: { width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 12 } },
              React.createElement('thead', null,
                React.createElement('tr', { style: { borderBottom: '1px solid ' + C.border } },
                  ['Category', 'Up To ($)', 'Referral %', ''].map(function(h) {
                    return React.createElement('th', { key: h, style: { padding: '6px 8px', textAlign: 'left', color: C.textMuted, fontWeight: 500 } }, h);
                  })
                )
              ),
              React.createElement('tbody', null,
                wfsCategoryFees.map(function(cat) {
                  return React.createElement('tr', { key: cat.id, style: { borderBottom: '1px solid ' + C.border + '40' } },
                    React.createElement('td', { style: { padding: '4px 8px' } }, React.createElement('input', { value: cat.category, onChange: function(e) { updateWfsCategory(cat.id, 'category', e.target.value); }, style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '4px 6px', borderRadius: 4, width: 160, fontSize: 12, fontFamily: 'inherit' } })),
                    React.createElement('td', { style: { padding: '4px 8px' } }, React.createElement('input', { type: 'number', step: '1', value: cat.price_up_to || '', placeholder: '∞', onChange: function(e) { updateWfsCategory(cat.id, 'price_up_to', e.target.value); }, style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '4px 6px', borderRadius: 4, width: 70, fontSize: 12, fontFamily: 'inherit' } })),
                    React.createElement('td', { style: { padding: '4px 8px' } }, React.createElement('input', { type: 'number', step: '0.1', value: cat.referral_fee_pct, onChange: function(e) { updateWfsCategory(cat.id, 'referral_fee_pct', e.target.value); }, style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '4px 6px', borderRadius: 4, width: 60, fontSize: 12, fontFamily: 'inherit', textAlign: 'right' } })),
                    React.createElement('td', { style: { padding: '4px 8px' } }, cat.category !== 'Default' && React.createElement('button', { onClick: function() { deleteWfsCategory(cat.id); }, style: { background: 'none', border: 'none', cursor: 'pointer', color: C.danger, padding: 2, fontSize: 14, lineHeight: 1 } }, '×'))
                  );
                })
              )
            ),
            React.createElement(Btn, { size: 'sm', onClick: addWfsCategory, style: { marginBottom: 16 } }, '+ Add Category'),
            // Surcharges
            React.createElement('div', { style: { fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 8 } }, 'Surcharges'),
            wfsSurcharges.map(function(s, idx) {
              return React.createElement('div', { key: s.id, style: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 } },
                React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: C.text, minWidth: 160 } },
                  React.createElement('input', { type: 'checkbox', checked: s.enabled, onChange: function(e) {
                    var updated = wfsSurcharges.map(function(sur, i) { return i === idx ? Object.assign({}, sur, { enabled: e.target.checked }) : sur; });
                    setWfsSurcharges(updated);
                  } }),
                  s.label
                ),
                React.createElement('span', { style: { fontSize: 12, color: C.textMuted } }, '$'),
                React.createElement('input', { type: 'number', step: '0.01', value: s.amount, onChange: function(e) {
                  var updated = wfsSurcharges.map(function(sur, i) { return i === idx ? Object.assign({}, sur, { amount: e.target.value }) : sur; });
                  setWfsSurcharges(updated);
                }, style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '4px 6px', borderRadius: 4, width: 60, fontSize: 12, fontFamily: 'inherit' } }),
                s.threshold != null && React.createElement(React.Fragment, null,
                  React.createElement('span', { style: { fontSize: 11, color: C.textMuted } }, 'under $'),
                  React.createElement('input', { type: 'number', step: '0.01', value: s.threshold, onChange: function(e) {
                    var updated = wfsSurcharges.map(function(sur, i) { return i === idx ? Object.assign({}, sur, { threshold: e.target.value }) : sur; });
                    setWfsSurcharges(updated);
                  }, style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '4px 6px', borderRadius: 4, width: 60, fontSize: 12, fontFamily: 'inherit' } })
                )
              );
            }),
            React.createElement(Btn, { size: 'sm', variant: 'primary', onClick: saveWfsSurcharges, disabled: wfsFeeSaving, style: { marginTop: 8 } }, 'Save Surcharges'),

            // WFS Prep Service Fees
            React.createElement('div', { style: { fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 8, marginTop: 20 } }, 'Prep Service Fees'),
            React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginBottom: 8 } }, 'Per-unit fees Walmart charges when they handle prep (labeling, poly bagging).'),
            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 } },
              React.createElement('div', null,
                React.createElement('label', { style: { fontSize: 11, color: C.textMuted, display: 'block', marginBottom: 2 } }, 'Label Fee ($/unit)'),
                React.createElement(Inp, { placeholder: '0.45', style: { width: '100%' }, value: wfsPrepFees.wfs_label_fee, onChange: function(e) { setWfsPrepFees(Object.assign({}, wfsPrepFees, { wfs_label_fee: e.target.value })); } })
              ),
              React.createElement('div', null,
                React.createElement('label', { style: { fontSize: 11, color: C.textMuted, display: 'block', marginBottom: 2 } }, 'Poly Bag Fee ($/unit)'),
                React.createElement(Inp, { placeholder: '0.60', style: { width: '100%' }, value: wfsPrepFees.wfs_bag_fee, onChange: function(e) { setWfsPrepFees(Object.assign({}, wfsPrepFees, { wfs_bag_fee: e.target.value })); } })
              )
            ),
            React.createElement(Btn, { size: 'sm', variant: 'primary', onClick: saveWfsPrepFees, disabled: wfsFeeSaving, style: { marginTop: 8 } }, 'Save Prep Fees')
          )
        ),
        React.createElement(IntegrationApiLog, { channel: 'walmart', label: 'Walmart' })
      )
    ) },
    // Walmart Canada Card (Global API, market ca — powers the Shopify ↔ Walmart CA sync)
    { title: 'Walmart Canada', el: existingChannels.walmart_ca && React.createElement(Card, { key: 'wmca-card', style: { padding: 0, overflow: 'hidden', marginTop: 16 } },
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: expandedInt.walmart_ca ? '1px solid ' + C.border : 'none', cursor: 'pointer' }, onClick: function() { toggleInt('walmart_ca'); } },
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12 } },
          React.createElement('div', { style: { width: 40, height: 40, borderRadius: 8, background: '#0071DC15', display: 'flex', alignItems: 'center', justifyContent: 'center' } }, channelIcon('walmart', 28)),
          React.createElement('div', null,
            React.createElement('div', { style: { fontSize: 15, fontWeight: 600, color: C.text } }, wmca.display_name || 'Walmart Canada'),
            React.createElement('div', { style: { fontSize: 12, color: C.textMuted } }, wmca.enabled && hasWmcaSecret ? 'Connected — Shopify sync (orders / inventory / tracking)' : 'Not connected')
          )
        ),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
            React.createElement('div', { style: { width: 8, height: 8, borderRadius: 4, background: wmca.enabled && hasWmcaSecret ? C.success : C.textDim } }),
            React.createElement('span', { style: { fontSize: 12, color: wmca.enabled && hasWmcaSecret ? C.success : C.textMuted, fontWeight: 500 } }, wmca.enabled && hasWmcaSecret ? 'Enabled' : 'Disabled')
          ),
          React.createElement(LIcon, { name: expandedInt.walmart_ca ? 'chevron-up' : 'chevron-down', size: 16, style: { color: C.textMuted } })
        )
      ),
      expandedInt.walmart_ca && React.createElement('div', { style: { padding: 20 } },
        React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12, lineHeight: 1.5 } }, 'Walmart Global Marketplace API keys for the CANADA market (separate from the US keys above — generate under Developer Portal > My Account > API Keys while logged in with your CA Seller Center account). Production only; powers the Shopify ↔ Walmart CA order/inventory/tracking sync.'),
        React.createElement('div', { style: { marginBottom: 12 } },
          React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 4 } }, 'Shopify store for this sync'),
          React.createElement('select', { value: String(wmca.shopify_integration_id || ''), onChange: function(e) { setWmca(Object.assign({}, wmca, { shopify_integration_id: e.target.value })); },
            style: { width: '100%', background: C.surface, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit' } },
            React.createElement('option', { value: '' }, 'Select store…'),
            shopifyStores.map(function(st) {
              var name = (st.display_name || 'Shopify') + ((st.config && st.config.shop) ? ' (' + st.config.shop + ')' : '');
              return React.createElement('option', { key: st.id, value: String(st.id) }, name);
            })
          )
        ),
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 } },
          React.createElement(Inp, { label: 'Client ID', placeholder: 'e.g. 6c2a1f0e-....', value: wmca.client_id || '', onChange: function(e) { setWmca(Object.assign({}, wmca, { client_id: e.target.value })); } }),
          React.createElement(Inp, { label: 'Client Secret' + (hasWmcaSecret ? ' (saved — leave blank to keep)' : ''), type: 'password',
            placeholder: hasWmcaSecret ? '••••••••••••••' : 'Enter client secret',
            value: wmca.client_secret || '', onChange: function(e) { setWmca(Object.assign({}, wmca, { client_secret: e.target.value })); } }),
          React.createElement(Inp, { label: 'Display Name (card title)', placeholder: 'e.g. Walmart Canada - Atharva Imports', value: wmca.display_name || '', onChange: function(e) { setWmca(Object.assign({}, wmca, { display_name: e.target.value })); } })
        ),
        React.createElement('div', { style: { marginBottom: 12 } },
          React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: C.text } },
            React.createElement('input', { type: 'checkbox', checked: wmca.enabled, onChange: function(e) { setWmca(Object.assign({}, wmca, { enabled: e.target.checked })); } }),
            'Enable Integration'
          )
        ),
        React.createElement('div', { style: { display: 'flex', gap: 8, marginBottom: 12 } },
          React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: saveWmca }, React.createElement(LIcon, { name: 'save', size: 14 }), ' Save Credentials'),
          React.createElement(Btn, { size: 'sm', onClick: testWmca, disabled: wmcaTesting || !hasWmcaSecret }, wmcaTesting ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Testing...'] : [React.createElement(LIcon, { name: 'plug', size: 14, key: 'ic' }), ' Test Connection']),
          wmca.enabled && hasWmcaSecret && React.createElement('a', { href: pageToPath('shopify-wmca'),
            onClick: function(e) { if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return; e.preventDefault(); onNavigate('shopify-wmca'); },
            style: { background: '#0071DC', color: '#fff', textDecoration: 'none', padding: '6px 12px', fontSize: 12, borderRadius: 6, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 'auto', cursor: 'pointer' } },
            React.createElement(LIcon, { name: 'layout-dashboard', size: 14 }), ' Open Dashboard')
        ),
        wmcaTest && React.createElement('div', { style: { padding: '10px 14px', borderRadius: 8, marginBottom: 12,
          background: wmcaTest.success ? C.success + '15' : C.danger + '15',
          border: '1px solid ' + (wmcaTest.success ? C.success + '40' : C.danger + '40'),
          color: wmcaTest.success ? C.success : C.danger, fontSize: 13 }
        }, wmcaTest.success ? React.createElement(LIcon, { name: 'check', size: 14 }) : React.createElement(LIcon, { name: 'x', size: 14 }), ' ', wmcaTest.message),
        React.createElement(IntegrationApiLog, { channel: 'walmart_ca', label: 'Walmart Canada' })
      )
    ) },
    // Amazon Advertising Card (LWA creds + one-time OAuth consent — powers the Advertising module, docs/14)
    { title: 'Amazon Advertising', el: existingChannels.amazon_ads && React.createElement(Card, { key: 'amzads-card', style: { padding: 0, overflow: 'hidden', marginTop: 16 } },
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: expandedInt.amazon_ads ? '1px solid ' + C.border : 'none', cursor: 'pointer' }, onClick: function() { toggleInt('amazon_ads'); } },
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12 } },
          React.createElement('div', { style: { width: 40, height: 40, borderRadius: 8, background: '#FF990015', display: 'flex', alignItems: 'center', justifyContent: 'center' } }, channelIcon('amazon', 28)),
          React.createElement('div', null,
            React.createElement('div', { style: { fontSize: 15, fontWeight: 600, color: C.text } }, 'Amazon Advertising'),
            React.createElement('div', { style: { fontSize: 12, color: C.textMuted } },
              amzAds.enabled && hasAmzAdsRefresh ? ('Connected' + (amzAdsProfiles.profile_id_us ? ' — US #' + amzAdsProfiles.profile_id_us : '') + (amzAdsProfiles.profile_id_ca ? (amzAdsProfiles.profile_id_us ? ', ' : ' — ') + 'CA #' + amzAdsProfiles.profile_id_ca : '') +
                (adsSyncStatus && adsSyncStatus.logins ? ' · logins: ' + ['us', 'ca'].map(function(s) { return s.toUpperCase() + (adsSyncStatus.logins[s] && adsSyncStatus.logins[s].connected ? ' ✓' : ' ✗'); }).join(' ') : '')) : 'Not connected')
          )
        ),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
            React.createElement('div', { style: { width: 8, height: 8, borderRadius: 4, background: amzAds.enabled && hasAmzAdsRefresh ? C.success : C.textDim } }),
            React.createElement('span', { style: { fontSize: 12, color: amzAds.enabled && hasAmzAdsRefresh ? C.success : C.textMuted, fontWeight: 500 } }, amzAds.enabled && hasAmzAdsRefresh ? 'Enabled' : 'Disabled')
          ),
          React.createElement(LIcon, { name: expandedInt.amazon_ads ? 'chevron-up' : 'chevron-down', size: 16, style: { color: C.textMuted } })
        )
      ),
      expandedInt.amazon_ads && React.createElement('div', { style: { padding: 20 } },
        React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12, lineHeight: 1.5 } },
          'Login-with-Amazon app credentials approved for the Amazon Ads API (apply at ',
          React.createElement('a', { href: 'https://advertising.amazon.com/about-api', target: '_blank', rel: 'noopener', style: { color: C.accent, fontWeight: 500 } }, 'advertising.amazon.com/about-api'),
          '). After saving, run the one-time consent once per Amazon login — the US and CA advertising accounts live under different logins, so click Connect for each while signed in to Amazon with that login. Add this redirect URL to the LWA app’s Allowed Return URLs first: ',
          React.createElement('code', { style: { fontSize: 11, background: C.bg, padding: '1px 4px', borderRadius: 3 } }, 'https://app.atharvatoolz.com/oauth/amazon-ads/accepted')),
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 } },
          React.createElement(Inp, { label: 'LWA Client ID', placeholder: 'amzn1.application-oa2-client....', value: amzAds.client_id || '', onChange: function(e) { setAmzAds(Object.assign({}, amzAds, { client_id: e.target.value })); } }),
          React.createElement(Inp, { label: 'LWA Client Secret' + (hasAmzAdsSecret ? ' (saved — leave blank to keep)' : ''), type: 'password',
            placeholder: hasAmzAdsSecret ? '••••••••••••••' : 'Enter client secret',
            value: amzAds.client_secret || '', onChange: function(e) { setAmzAds(Object.assign({}, amzAds, { client_secret: e.target.value })); } })
        ),
        React.createElement('div', { style: { marginBottom: 12 } },
          React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: C.text } },
            React.createElement('input', { type: 'checkbox', checked: amzAds.enabled, onChange: function(e) { setAmzAds(Object.assign({}, amzAds, { enabled: e.target.checked })); } }),
            'Enable Integration'
          )
        ),
        React.createElement('div', { style: { display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' } },
          React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: saveAmzAds }, React.createElement(LIcon, { name: 'save', size: 14 }), ' Save Credentials'),
          React.createElement(Btn, { size: 'sm', onClick: function() { connectAmzAds('us'); }, disabled: !hasAmzAdsSecret }, React.createElement(LIcon, { name: 'link', size: 14 }), ' Connect US Login'),
          React.createElement(Btn, { size: 'sm', onClick: function() { connectAmzAds('ca'); }, disabled: !hasAmzAdsSecret }, React.createElement(LIcon, { name: 'link', size: 14 }), ' Connect CA Login'),
          React.createElement(Btn, { size: 'sm', onClick: testAmzAds, disabled: amzAdsTesting || !hasAmzAdsRefresh }, amzAdsTesting ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Testing...'] : [React.createElement(LIcon, { name: 'plug', size: 14, key: 'ic' }), ' Test Connection']),
          hasAmzAdsRefresh && React.createElement(Btn, { size: 'sm', onClick: refreshAmzAdsProfiles }, React.createElement(LIcon, { name: 'refresh-cw', size: 14 }), ' Refresh Profiles')
        ),
        // One line per Amazon login: consent date + the ads account the reports run against
        adsSyncStatus && adsSyncStatus.logins && React.createElement('div', { style: { display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 } },
          ['us', 'ca'].map(function(s) {
            var l = adsSyncStatus.logins[s] || {};
            var when = l.consented_at ? new Date(l.consented_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
            return React.createElement('span', { key: s, style: { fontSize: 11, padding: '3px 8px', borderRadius: 12, background: C.bg, border: '1px solid ' + (l.connected ? C.success + '55' : C.border), color: l.connected ? C.success : C.textMuted } },
              s.toUpperCase() + ' login: ' + (l.connected ? ('connected' + (when ? ' ' + when : '') + (l.ads_account_id ? ' · ads account ' + String(l.ads_account_id).slice(-8) : ' · ads account not found yet')) : 'not connected'));
          })
        ),
        (amzAdsProfiles.profiles || []).length > 0 && React.createElement('div', { style: { display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 } },
          amzAdsProfiles.profiles.map(function(p, i) {
            return React.createElement('span', { key: i, style: { fontSize: 11, padding: '3px 8px', borderRadius: 12, background: C.bg, border: '1px solid ' + C.border, color: C.textMuted } },
              (p.countryCode || '?') + ' #' + p.profileId + (p.accountType ? ' · ' + p.accountType : '') + (p.currencyCode ? ' · ' + p.currencyCode : '') + (p.auth ? ' · via ' + String(p.auth).toUpperCase() + ' login' : ''));
          })
        ),
        // Data sync (Phase 1) — nightly reports + 6h entity sync into the local ads_* tables
        React.createElement('div', { style: { border: '1px solid ' + C.border, borderRadius: 8, padding: '12px 14px', marginBottom: 12 } },
          React.createElement('div', { style: { fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 8 } }, 'Data Sync'),
          adsSyncEnabled !== null && React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: C.text, marginBottom: 10 } },
            React.createElement('input', { type: 'checkbox', checked: adsSyncEnabled === true, onChange: function(e) { toggleAdsSync(e.target.checked); } }),
            'Nightly data sync (report pulls 4 AM ET · entity sync every 6h · first enable backfills ~90 days)'
          ),
          React.createElement('div', { style: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' } },
            React.createElement(Btn, { size: 'sm', onClick: syncAdsEntitiesNow, disabled: adsSyncing || !hasAmzAdsRefresh },
              adsSyncing ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Syncing...'] : [React.createElement(LIcon, { name: 'refresh-cw', size: 14, key: 'ic' }), ' Sync Entities Now']),
            adsSyncStatus && React.createElement('span', { style: { fontSize: 11.5, color: C.textMuted } },
              (adsSyncStatus.entities && adsSyncStatus.entities.campaigns ? adsSyncStatus.entities.campaigns.count : 0) + ' campaigns · ' +
              (adsSyncStatus.entities && adsSyncStatus.entities.keywords ? adsSyncStatus.entities.keywords.count : 0) + ' keywords · ' +
              (adsSyncStatus.daily_metrics ? adsSyncStatus.daily_metrics.count : 0) + ' metric rows' +
              (adsSyncStatus.daily_metrics && adsSyncStatus.daily_metrics.max_date ? (' (through ' + String(adsSyncStatus.daily_metrics.max_date).slice(0, 10) + ')') : '') +
              (adsSyncStatus.report_queue && Object.keys(adsSyncStatus.report_queue).length ? ' · queue: ' + Object.keys(adsSyncStatus.report_queue).map(function(k) { return k + ' ' + adsSyncStatus.report_queue[k]; }).join(', ') : ''))
          ),
          adsSyncStatus && (adsSyncStatus.recent_failures || []).length > 0 && React.createElement('div', { style: { fontSize: 11.5, color: C.danger, marginTop: 8 } },
            'Recent report failures: ' + adsSyncStatus.recent_failures.map(function(f) { return f.report_type + ' ' + String(f.error || '').slice(0, 80); }).join(' | '))
        ),
        hasAmzAdsSecret && React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 8, alignItems: 'end', marginBottom: 12 } },
          React.createElement(Inp, { label: 'Fallback: paste authorization code (or the full redirect URL) if the consent tab errored', placeholder: 'ANxxxxxxxxxxxxxxxxxx or https://app.atharvatoolz.com/oauth/amazon-ads/accepted?code=...', value: amzAdsCode, onChange: function(e) { setAmzAdsCode(e.target.value); } }),
          React.createElement(Sel, { label: 'Login', value: amzAdsCodeSlot, onChange: function(e) { setAmzAdsCodeSlot(e.target.value || 'us'); },
            options: [{ value: 'us', label: 'US login' }, { value: 'ca', label: 'CA login' }] }),
          React.createElement(Btn, { size: 'sm', onClick: exchangeAmzAdsCode, disabled: amzAdsExchanging }, amzAdsExchanging ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Exchanging...'] : 'Exchange Code')
        ),
        amzAdsTest && React.createElement('div', { style: { padding: '10px 14px', borderRadius: 8, marginBottom: 12,
          background: amzAdsTest.success ? C.success + '15' : C.danger + '15',
          border: '1px solid ' + (amzAdsTest.success ? C.success + '40' : C.danger + '40'),
          color: amzAdsTest.success ? C.success : C.danger, fontSize: 13 }
        }, amzAdsTest.success ? React.createElement(LIcon, { name: 'check', size: 14 }) : React.createElement(LIcon, { name: 'x', size: 14 }), ' ', amzAdsTest.message),
        React.createElement(IntegrationApiLog, { channel: 'amazon_ads', label: 'Amazon Advertising' })
      )
    ) },
    // Mercado Libre Card (Global Selling APP_ID/secret + one-time OAuth consent — docs/mercado-libre)
    { title: 'Mercado Libre', el: existingChannels.mercadolibre && React.createElement(Card, { key: 'meli-card', style: { padding: 0, overflow: 'hidden', marginTop: 16 } },
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: expandedInt.mercadolibre ? '1px solid ' + C.border : 'none', cursor: 'pointer' }, onClick: function() { toggleInt('mercadolibre'); } },
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12 } },
          React.createElement('div', { style: { width: 40, height: 40, borderRadius: 8, background: '#FFE60025', display: 'flex', alignItems: 'center', justifyContent: 'center' } }, channelIcon('mercadolibre', 28)),
          React.createElement('div', null,
            React.createElement('div', { style: { fontSize: 15, fontWeight: 600, color: C.text } }, 'Mercado Libre'),
            React.createElement('div', { style: { fontSize: 12, color: C.textMuted } },
              meliInt.enabled && hasMeliRefresh
                ? ('Connected' + (meliAccount.nickname ? ' — ' + meliAccount.nickname : '') + (meliAccount.user_id ? ' #' + meliAccount.user_id : '') + (meliAccount.site_id ? ' (site ' + meliAccount.site_id + ')' : ''))
                : 'Not connected')
          )
        ),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
            React.createElement('div', { style: { width: 8, height: 8, borderRadius: 4, background: meliInt.enabled && hasMeliRefresh ? C.success : C.textDim } }),
            React.createElement('span', { style: { fontSize: 12, color: meliInt.enabled && hasMeliRefresh ? C.success : C.textMuted, fontWeight: 500 } }, meliInt.enabled && hasMeliRefresh ? 'Enabled' : 'Disabled')
          ),
          React.createElement(LIcon, { name: expandedInt.mercadolibre ? 'chevron-up' : 'chevron-down', size: 16, style: { color: C.textMuted } })
        )
      ),
      expandedInt.mercadolibre && React.createElement('div', { style: { padding: 20 } },
        React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12, lineHeight: 1.6 } },
          'Global Selling application credentials (create the app at global-selling.mercadolibre.com/devcenter). Register this Redirect URI on the app: ',
          React.createElement('code', { style: { fontSize: 11, background: C.bg, padding: '1px 4px', borderRadius: 3 } }, 'https://app.atharvatoolz.com/oauth/meli/accepted'),
          ' and this Notifications callback URL: ',
          React.createElement('code', { style: { fontSize: 11, background: C.bg, padding: '1px 4px', borderRadius: 3 } }, 'https://app.atharvatoolz.com/webhooks/meli'),
          '. After saving, run the one-time "Connect Mercado Libre" consent (sign in with the seller account) to mint the tokens.'),
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 } },
          React.createElement(Inp, { label: 'APP_ID (Client ID)', placeholder: 'e.g. 1234567890123456', value: meliInt.client_id || '', onChange: function(e) { setMeliInt(Object.assign({}, meliInt, { client_id: e.target.value })); } }),
          React.createElement(Inp, { label: 'Client Secret' + (hasMeliSecret ? ' (saved — leave blank to keep)' : ''), type: 'password',
            placeholder: hasMeliSecret ? '••••••••••••••' : 'Enter client secret',
            value: meliInt.client_secret || '', onChange: function(e) { setMeliInt(Object.assign({}, meliInt, { client_secret: e.target.value })); } })
        ),
        React.createElement('div', { style: { marginBottom: 12 } },
          React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: C.text } },
            React.createElement('input', { type: 'checkbox', checked: meliInt.enabled, onChange: function(e) { setMeliInt(Object.assign({}, meliInt, { enabled: e.target.checked })); } }),
            'Enable Integration'
          )
        ),
        React.createElement('div', { style: { display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' } },
          React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: saveMeli }, React.createElement(LIcon, { name: 'save', size: 14 }), ' Save Credentials'),
          React.createElement(Btn, { size: 'sm', onClick: connectMeli, disabled: !hasMeliSecret }, React.createElement(LIcon, { name: 'link', size: 14 }), ' Connect Mercado Libre'),
          React.createElement(Btn, { size: 'sm', onClick: testMeli, disabled: meliTesting || !hasMeliRefresh }, meliTesting ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Testing...'] : [React.createElement(LIcon, { name: 'plug', size: 14, key: 'ic' }), ' Test Connection']),
          React.createElement(Btn, { size: 'sm', onClick: function() { syncMeli('orders'); }, disabled: !!meliSyncing || !hasMeliRefresh },
            meliSyncing === 'orders' ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Syncing...'] : [React.createElement(LIcon, { name: 'refresh-cw', size: 14, key: 'ic' }), ' Sync Orders Now']),
          React.createElement(Btn, { size: 'sm', onClick: function() { syncMeli('inventory'); }, disabled: !!meliSyncing || !hasMeliRefresh },
            meliSyncing === 'inventory' ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Syncing...'] : [React.createElement(LIcon, { name: 'package', size: 14, key: 'ic' }), ' Sync Inventory Now'])
        ),
        meliLastSync && React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12 } },
          'Last synced: ' + new Date(meliLastSync).toLocaleString() + ' — orders auto-sync every 15 min, Full inventory every 30 min'),
        !hasMeliRefresh && hasMeliSecret && React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'end', marginBottom: 12 } },
          React.createElement(Inp, { label: 'Fallback: paste authorization code (or the full redirect URL) if the consent tab errored', placeholder: 'TG-xxxxxxxx or https://app.atharvatoolz.com/oauth/meli/accepted?code=...', value: meliCode, onChange: function(e) { setMeliCode(e.target.value); } }),
          React.createElement(Btn, { size: 'sm', onClick: exchangeMeliCode, disabled: meliExchanging }, meliExchanging ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Exchanging...'] : 'Exchange Code')
        ),
        meliTest && React.createElement('div', { style: { padding: '10px 14px', borderRadius: 8, marginBottom: 12,
          background: meliTest.success ? C.success + '15' : C.danger + '15',
          border: '1px solid ' + (meliTest.success ? C.success + '40' : C.danger + '40'),
          color: meliTest.success ? C.success : C.danger, fontSize: 13 }
        }, meliTest.success ? React.createElement(LIcon, { name: 'check', size: 14 }) : React.createElement(LIcon, { name: 'x', size: 14 }), ' ', meliTest.message),
        React.createElement(IntegrationApiLog, { channel: 'mercadolibre', label: 'Mercado Libre' })
      )
    ) },
    // Scrapfly Card
    { title: 'Scrapfly', el: existingChannels.scrapfly && React.createElement(Card, { key: 'scrapfly-card', style: { padding: 0, overflow: 'hidden', marginTop: 16 } },
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: expandedInt.scrapfly ? '1px solid ' + C.border : 'none', cursor: 'pointer' }, onClick: function() { toggleInt('scrapfly'); } },
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12 } },
          React.createElement('div', { style: { width: 40, height: 40, borderRadius: 8, background: '#6366F115', display: 'flex', alignItems: 'center', justifyContent: 'center' } },
            React.createElement(LIcon, { name: 'globe', size: 22 })
          ),
          React.createElement('div', null,
            React.createElement('div', { style: { fontSize: 15, fontWeight: 600, color: C.text } }, 'Scrapfly'),
            React.createElement('div', { style: { fontSize: 12, color: C.textMuted } }, scrapfly.enabled && hasScrapflyKey ? 'Connected — Walmart buy box scraping' : 'Not connected')
          )
        ),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
            React.createElement('div', { style: { width: 8, height: 8, borderRadius: 4, background: scrapfly.enabled && hasScrapflyKey ? C.success : C.textDim } }),
            React.createElement('span', { style: { fontSize: 12, color: scrapfly.enabled && hasScrapflyKey ? C.success : C.textMuted, fontWeight: 500 } }, scrapfly.enabled && hasScrapflyKey ? 'Enabled' : 'Disabled')
          ),
          React.createElement(LIcon, { name: expandedInt.scrapfly ? 'chevron-up' : 'chevron-down', size: 16, style: { color: C.textMuted } })
        )
      ),
      expandedInt.scrapfly && React.createElement('div', { style: { padding: 20 } },
        React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12, lineHeight: 1.5 } },
          'Used to scrape Walmart product pages for live buy box prices. Free plan: 200 scrapes/month (1 credit per product). Get your API key at scrapfly.io.'),
        React.createElement(Inp, { label: 'API Key' + (hasScrapflyKey ? ' (saved — leave blank to keep)' : ''), type: 'password', placeholder: hasScrapflyKey ? '••••••••••••••' : 'scp-live-...', value: scrapfly.api_key, onChange: function(e) { setScrapfly(Object.assign({}, scrapfly, { api_key: e.target.value })); } }),
        React.createElement('div', { style: { display: 'flex', alignItems: 'flex-end', paddingBottom: 4, marginTop: 12, marginBottom: 12 } },
          React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: C.text } },
            React.createElement('input', { type: 'checkbox', checked: scrapfly.enabled, onChange: function(e) { setScrapfly(Object.assign({}, scrapfly, { enabled: e.target.checked })); } }),
            'Enable Integration'
          )
        ),
        React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: saveScrapfly }, React.createElement(LIcon, { name: 'save', size: 14 }), ' Save Credentials'),
        React.createElement(IntegrationApiLog, { channel: 'scrapfly', label: 'Scrapfly' })
      )
    ) },
    // Bill.com Card
    { title: 'Bill.com', el: existingChannels.billdotcom && React.createElement(Card, { key: 'billdotcom-card', style: { padding: 0, overflow: 'hidden', marginTop: 16 } },
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: expandedInt.billdotcom ? '1px solid ' + C.border : 'none', cursor: 'pointer' }, onClick: function() { toggleInt('billdotcom'); } },
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12 } },
          React.createElement('div', { style: { width: 40, height: 40, borderRadius: 8, background: '#FF6B0015', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 4 } },
            React.createElement('img', { src: '/assets/billdotcom-logo.png', alt: 'Bill.com', style: { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' } })
          ),
          React.createElement('div', null,
            React.createElement('div', { style: { fontSize: 15, fontWeight: 600, color: C.text } }, 'Bill.com'),
            React.createElement('div', { style: { fontSize: 12, color: C.textMuted } }, billdc.enabled && hasBilldcPass ? 'Connected — bills + invoices read-only sync' : 'Not connected')
          )
        ),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
            React.createElement('div', { style: { width: 8, height: 8, borderRadius: 4, background: billdc.enabled && hasBilldcPass ? C.success : C.textDim } }),
            React.createElement('span', { style: { fontSize: 12, color: billdc.enabled && hasBilldcPass ? C.success : C.textMuted, fontWeight: 500 } }, billdc.enabled && hasBilldcPass ? 'Enabled' : 'Disabled')
          ),
          React.createElement(LIcon, { name: expandedInt.billdotcom ? 'chevron-up' : 'chevron-down', size: 16, style: { color: C.textMuted } })
        )
      ),
      expandedInt.billdotcom && React.createElement('div', { style: { padding: 20 } },
        React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12, lineHeight: 1.5 } },
          'Read-only sync of Bill.com bills, invoices, and payment status. Bills auto-match to POs by PO# in the invoice description, with fallback to vendor + amount + date heuristic. Auto-sync runs hourly. Get your dev key at ',
          React.createElement('a', { href: 'https://developer.bill.com/docs/home', target: '_blank', style: { color: C.accent } }, 'developer.bill.com'),
          '.'
        ),
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 } },
          React.createElement(Inp, { label: 'Sync Token Name (Username)', placeholder: 'e.g. AtharvaToolz',
            value: billdc.username, onChange: function(e) { setBilldc(Object.assign({}, billdc, { username: e.target.value })); } }),
          React.createElement(Inp, { label: 'Sync Token' + (hasBilldcPass ? ' (saved — leave blank to keep)' : ''), type: 'password',
            placeholder: hasBilldcPass ? '••••••••••••' : 'Generated sync token value',
            value: billdc.password, onChange: function(e) { setBilldc(Object.assign({}, billdc, { password: e.target.value })); } })
        ),
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12, marginBottom: 12 } },
          React.createElement(Inp, { label: 'Dev Key' + (hasBilldcKey ? ' (saved — leave blank to keep)' : ''), type: 'password',
            placeholder: hasBilldcKey ? '••••••••••••••••' : '0123456789abcdef...',
            value: billdc.dev_key, onChange: function(e) { setBilldc(Object.assign({}, billdc, { dev_key: e.target.value })); } }),
          React.createElement(Inp, { label: 'Org ID', placeholder: '008...',
            value: billdc.org_id, onChange: function(e) { setBilldc(Object.assign({}, billdc, { org_id: e.target.value })); } }),
          React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 } },
            React.createElement('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' } }, 'Environment'),
            React.createElement('select', { value: billdc.environment, onChange: function(e) { setBilldc(Object.assign({}, billdc, { environment: e.target.value })); },
              style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', outline: 'none' } },
              React.createElement('option', { value: 'sandbox' }, 'Sandbox'),
              React.createElement('option', { value: 'production' }, 'Production')
            )
          )
        ),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 } },
          React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: C.text } },
            React.createElement('input', { type: 'checkbox', checked: billdc.enabled, onChange: function(e) { setBilldc(Object.assign({}, billdc, { enabled: e.target.checked })); } }),
            'Enable Integration'
          )
        ),
        React.createElement('div', { style: { display: 'flex', gap: 8, flexWrap: 'wrap' } },
          React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: saveBilldcCreds }, React.createElement(LIcon, { name: 'save', size: 14 }), ' Save'),
          hasBilldcPass && hasBilldcKey && React.createElement(Btn, { size: 'sm', onClick: testBilldcConnection, disabled: billdcTesting },
            billdcTesting ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Testing...']
              : [React.createElement(LIcon, { name: 'flask-conical', size: 14, key: 'ic' }), ' Test Connection']),
          hasBilldcPass && hasBilldcKey && billdc.enabled && React.createElement(Btn, { size: 'sm', onClick: function() { syncBilldc(false); }, disabled: billdcSyncing },
            billdcSyncing ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Syncing...']
              : [React.createElement(LIcon, { name: 'refresh-cw', size: 14, key: 'ic' }), ' Sync Now']),
          hasBilldcPass && hasBilldcKey && billdc.enabled && React.createElement('span', { title: 'Full re-sync from the beginning of time (ignores last_sync_at)' },
            React.createElement(Btn, { size: 'sm', onClick: async function() { if (await appConfirm('Full re-sync may take a few minutes for large accounts. Continue?')) syncBilldc(true); }, disabled: billdcSyncing }, React.createElement(LIcon, { name: 'rotate-cw', size: 14 }), ' Full Sync'))
        ),
        billdcLastSync && React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 8 } },
          'Last sync: ' + new Date(billdcLastSync).toLocaleString()
        ),
        hasBilldcPass && hasBilldcKey && React.createElement('div', { style: { marginTop: 10, padding: '10px 12px', background: C.bg, border: '1px solid ' + C.border, borderRadius: 6, fontSize: 12, color: C.textMuted, lineHeight: 1.5 } },
          React.createElement('div', { style: { marginBottom: 4 } }, React.createElement('strong', { style: { color: C.text } }, 'Sync Now'), ' — Incremental pull of bills, invoices, and payments updated in Bill.com since the last sync (uses ', React.createElement('code', { style: { fontSize: 11 } }, 'last_sync_at'), '). Re-runs PO auto-matching and reconciles open cycles. Runs automatically every hour.'),
          React.createElement('div', null, React.createElement('strong', { style: { color: C.text } }, 'Full Sync'), ' — Re-pulls every bill and invoice from Bill.com from the beginning, ignoring ', React.createElement('code', { style: { fontSize: 11 } }, 'last_sync_at'), '. Use after fixing a vendor mapping, recovering from a failed sync, or onboarding a new account. Existing rows are upserted, archived/inactive bills are purged.')
        ),
        billdcTest && React.createElement('div', { style: { padding: '10px 14px', borderRadius: 8, marginTop: 12,
          background: billdcTest.success ? C.success + '15' : C.danger + '15',
          border: '1px solid ' + (billdcTest.success ? C.success + '40' : C.danger + '40'),
          color: billdcTest.success ? C.success : C.danger, fontSize: 13 }
        }, billdcTest.success ? React.createElement(LIcon, { name: 'check', size: 14 }) : React.createElement(LIcon, { name: 'x', size: 14 }), ' ', billdcTest.message),
        billdcSyncResult && React.createElement('div', { style: { padding: '10px 14px', borderRadius: 8, marginTop: 12, background: C.success + '15', border: '1px solid ' + C.success + '40', color: C.success, fontSize: 13 } },
          React.createElement(LIcon, { name: 'check', size: 14 }), ' Synced ', billdcSyncResult.bills, ' bills, ', billdcSyncResult.invoices, ' invoices · ', billdcSyncResult.pos_matched, ' POs auto-matched.'),
        // Vendor mapping section — collapsible. Bill.com vendors that don't auto-match our vendors by name.
        hasBilldcPass && hasBilldcKey && React.createElement('div', { style: { marginTop: 18, paddingTop: 16, borderTop: '1px dashed ' + C.border } },
          React.createElement('div', {
            onClick: function() { var next = !billdcShowMapping; setBilldcShowMapping(next); if (next && billdcUnmapped == null) loadVendorMappings(); },
            style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', padding: '6px 0' }
          },
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
              React.createElement(LIcon, { name: billdcShowMapping ? 'chevron-down' : 'chevron-right', size: 14 }),
              React.createElement('span', { style: { fontSize: 13, fontWeight: 600, color: C.text } }, 'Vendor Mapping'),
              billdcUnmapped != null && React.createElement('span', { style: { fontSize: 11, color: billdcUnmapped.length > 0 ? C.warning : C.textMuted, padding: '2px 8px', background: billdcUnmapped.length > 0 ? C.warningSoft : 'transparent', borderRadius: 4 } },
                billdcUnmapped.length + ' unmapped'),
              billdcMappings != null && React.createElement('span', { style: { fontSize: 11, color: C.textMuted } },
                ' · ' + billdcMappings.length + ' mapped')
            ),
            React.createElement('span', { style: { fontSize: 11, color: C.textDim }, onClick: function(e) { e.stopPropagation(); loadVendorMappings(); }, title: 'Refresh' }, '↻ refresh')
          ),
          billdcShowMapping && React.createElement('div', { style: { marginTop: 12 } },
            React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12, lineHeight: 1.5 } },
              'Bill.com vendors auto-match to our vendors by exact (case-insensitive) name. Vendors here either don\'t match or have a slight name mismatch — pick our vendor for each so PO auto-matching and (future) cycle auto-populate know they\'re the same entity.'),
            billdcUnmapped == null && React.createElement('div', { style: { fontSize: 12, color: C.textMuted, padding: 12 } }, 'Loading…'),
            billdcUnmapped != null && billdcUnmapped.length === 0 && React.createElement('div', { style: { fontSize: 12, color: C.success, padding: '10px 14px', background: C.success + '15', borderRadius: 6, border: '1px solid ' + C.success + '40' } },
              React.createElement(LIcon, { name: 'check', size: 14 }), ' All Bill.com vendors are matched.'),
            billdcUnmapped != null && billdcUnmapped.length > 0 && React.createElement('div', { style: { background: C.bg, border: '1px solid ' + C.border, borderRadius: 8, overflow: 'hidden' } },
              React.createElement('div', { style: { padding: '8px 14px', fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', background: C.surface, borderBottom: '1px solid ' + C.border } }, 'Unmapped (' + billdcUnmapped.length + ')'),
              billdcUnmapped.map(function(u) {
                return React.createElement('div', { key: u.bdc_vendor_id, style: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderTop: '1px solid ' + C.border } },
                  React.createElement('div', { style: { flex: '1 1 200px', minWidth: 0 } },
                    React.createElement('div', { style: { fontSize: 13, color: C.text, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, u.bdc_vendor_name || '(no name)'),
                    React.createElement('div', { style: { fontSize: 11, color: C.textMuted } }, u.bill_count + ' bill' + (u.bill_count === 1 ? '' : 's') + ' · $' + Number(u.total_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 }))
                  ),
                  React.createElement('select', {
                    value: billdcMapPicks[u.bdc_vendor_id] || '',
                    onChange: function(e) { var n = Object.assign({}, billdcMapPicks); n[u.bdc_vendor_id] = e.target.value; setBilldcMapPicks(n); },
                    style: { background: C.surface, border: '1px solid ' + C.border, color: C.text, padding: '6px 10px', borderRadius: 4, fontSize: 12, minWidth: 220, fontFamily: 'inherit' }
                  },
                    React.createElement('option', { value: '' }, '— pick our vendor —'),
                    (billdcOurVendors || []).map(function(v) {
                      return React.createElement('option', { key: v.id, value: v.id }, v.vendor_name);
                    })
                  ),
                  React.createElement(Btn, { size: 'sm', variant: 'primary', disabled: !billdcMapPicks[u.bdc_vendor_id],
                    onClick: function() { saveVendorMapping(u.bdc_vendor_id, u.bdc_vendor_name, billdcMapPicks[u.bdc_vendor_id]); } }, 'Map')
                );
              })
            ),
            billdcMappings != null && billdcMappings.length > 0 && React.createElement('div', { style: { background: C.bg, border: '1px solid ' + C.border, borderRadius: 8, overflow: 'hidden', marginTop: 12 } },
              React.createElement('div', { style: { padding: '8px 14px', fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', background: C.surface, borderBottom: '1px solid ' + C.border } }, 'Mapped (' + billdcMappings.length + ')'),
              billdcMappings.map(function(m) {
                return React.createElement('div', { key: m.bdc_vendor_id, style: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', borderTop: '1px solid ' + C.border } },
                  React.createElement('div', { style: { flex: 1, minWidth: 0, fontSize: 12 } },
                    React.createElement('span', { style: { color: C.text } }, m.bdc_vendor_name || m.bdc_vendor_id),
                    React.createElement('span', { style: { color: C.textDim, margin: '0 8px' } }, '→'),
                    React.createElement('span', { style: { color: C.accent, fontWeight: 500 } }, m.our_vendor_name || '(deleted vendor)')
                  ),
                  React.createElement('button', {
                    onClick: function() { removeVendorMapping(m.bdc_vendor_id); },
                    title: 'Remove mapping',
                    style: { background: 'transparent', border: 'none', color: C.textDim, cursor: 'pointer', padding: 4 }
                  }, React.createElement(LIcon, { name: 'trash-2', size: 14 }))
                );
              })
            )
          )
        ),
        React.createElement(IntegrationApiLog, { channel: 'billdotcom', label: 'Bill.com' })
      )
    ) },
    // eBay Card
    { title: 'eBay', el: existingChannels.ebay && React.createElement(Card, { key: 'ebay-card', style: { padding: 0, overflow: 'hidden', marginTop: 16 } },
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: expandedInt.ebay ? '1px solid ' + C.border : 'none', cursor: 'pointer' }, onClick: function() { toggleInt('ebay'); } },
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12 } },
          React.createElement('div', { style: { width: 40, height: 40, borderRadius: 8, background: '#E5323815', display: 'flex', alignItems: 'center', justifyContent: 'center' } }, channelIcon('ebay', 28)),
          React.createElement('div', null,
            React.createElement('div', { style: { fontSize: 15, fontWeight: 600, color: C.text } }, ebayInt.display_name || 'eBay'),
            React.createElement('div', { style: { fontSize: 12, color: C.textMuted } }, ebayInt.enabled ? 'Connected — ' + (ebayInt.environment || 'production') : 'Not connected')
          )
        ),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
            React.createElement('div', { style: { width: 8, height: 8, borderRadius: 4, background: ebayInt.enabled ? C.success : C.textDim } }),
            React.createElement('span', { style: { fontSize: 12, color: ebayInt.enabled ? C.success : C.textMuted, fontWeight: 500 } }, ebayInt.enabled ? 'Enabled' : 'Disabled')
          ),
          React.createElement(LIcon, { name: expandedInt.ebay ? 'chevron-up' : 'chevron-down', size: 16, style: { color: C.textMuted } })
        )
      ),
      expandedInt.ebay && React.createElement('div', { style: { padding: 20 } },
        React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12, lineHeight: 1.5 } },
          'Read-only order sync for customer service. Orders auto-sync every 10 minutes and create CS tickets.'),
        React.createElement('div', { style: { padding: '12px 14px', borderRadius: 8, background: C.bgAlt, border: '1px solid ' + C.border, marginBottom: 16, fontSize: 12, lineHeight: 1.7, color: C.textMuted } },
          React.createElement('div', { style: { fontWeight: 600, color: C.text, marginBottom: 4 } }, 'Setup Instructions'),
          React.createElement('div', null,
            '1. Go to ',
            React.createElement('a', { href: 'https://developer.ebay.com/my/keys', target: '_blank', style: { color: C.accent, textDecoration: 'underline' } }, 'eBay Developer Portal'),
            ' and create an application (or use existing)'
          ),
          React.createElement('div', null, '2. Copy the ', React.createElement('strong', { style: { color: C.text } }, 'App ID (Client ID)'), ' and ', React.createElement('strong', { style: { color: C.text } }, 'Cert ID (Client Secret)'), ' from your keyset'),
          React.createElement('div', null,
            '3. Go to ',
            React.createElement('a', { href: 'https://developer.ebay.com/my/auth', target: '_blank', style: { color: C.accent, textDecoration: 'underline' } }, 'User Tokens'),
            ' \u2192 select your app \u2192 choose ', React.createElement('strong', { style: { color: C.text } }, 'Production'), ' or Sandbox'
          ),
          React.createElement('div', null, '4. Under "OAuth Scopes", add: ', React.createElement('code', { style: { fontSize: 11, background: C.bg, padding: '1px 4px', borderRadius: 3 } }, 'sell.fulfillment.readonly')),
          React.createElement('div', null, '5. Click "Get a Token from eBay via Your Application" \u2192 sign in \u2192 copy the ', React.createElement('strong', { style: { color: C.text } }, 'Refresh Token'))
        ),
        React.createElement('div', { style: { fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 } }, 'API Credentials'),
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 } },
          React.createElement(Inp, { label: 'Client ID (App ID)', placeholder: 'e.g. YourApp-PRD-abc123...', ...EF('client_id') }),
          React.createElement(Inp, { label: 'Client Secret' + (hasEbaySecret ? ' (saved — leave blank to keep)' : ''), type: 'password', placeholder: hasEbaySecret ? '••••••••••••••' : 'Enter client secret', ...EF('client_secret') }),
          React.createElement(Inp, { label: 'Display Name (card title)', placeholder: 'e.g. eBay - Atharva Brands', ...EF('display_name') })
        ),
        React.createElement('div', { style: { marginBottom: 12 } },
          React.createElement(Inp, { label: 'RuName (from eBay Developer Portal)', placeholder: 'e.g. Atharva_Imports-AtharvaI-AtharvaT-xxxxxxx', ...EF('runame') }),
          React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 4 } }, 'Set up in eBay Portal → Application Keys → "Get a Token from eBay via your Application" → Add eBay Redirect URL. Use accept URL ', React.createElement('code', { style: { fontSize: 11, background: C.bg, padding: '1px 4px', borderRadius: 3 } }, 'https://app.atharvatoolz.com/oauth/ebay/accepted'))
        ),
        React.createElement('div', { style: { padding: 12, borderRadius: 8, background: hasEbayRefresh ? C.success + '15' : C.bgAlt, border: '1px solid ' + (hasEbayRefresh ? C.success + '40' : C.border), marginBottom: 12 } },
          React.createElement('div', { style: { fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 4 } }, hasEbayRefresh ? '✓ eBay is connected' : 'Connect eBay (OAuth)'),
          React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 10, lineHeight: 1.5 } }, hasEbayRefresh ? 'Refresh token is saved. Click Reconnect to replace it — e.g. if you change scopes or the existing token is rejected.' : 'Save Client ID + Cert ID + RuName above first, then click Connect. You\'ll be redirected to eBay to sign in; on return your refresh token is saved automatically.'),
          React.createElement(Btn, { variant: hasEbayRefresh ? 'secondary' : 'primary', size: 'sm', onClick: connectEbayOAuth }, React.createElement(LIcon, { name: hasEbayRefresh ? 'refresh-cw' : 'link', size: 14 }), hasEbayRefresh ? ' Reconnect' : ' Connect eBay via OAuth')
        ),
        React.createElement('details', { style: { marginBottom: 12 } },
          React.createElement('summary', { style: { cursor: 'pointer', fontSize: 12, color: C.textMuted } }, 'Or paste a refresh token manually'),
          React.createElement('div', { style: { marginTop: 8 } },
            React.createElement(Inp, { label: 'Refresh Token' + (hasEbayRefresh ? ' (saved — leave blank to keep)' : ''), type: 'password', placeholder: hasEbayRefresh ? '••••••••••••••' : 'Paste refresh token here', ...EF('refresh_token') })
          )
        ),
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 } },
          React.createElement(Sel, { label: 'Environment', options: [
            { value: 'sandbox', label: 'Sandbox (Testing)' },
            { value: 'production', label: 'Production (Live)' },
          ], value: ebayInt.environment || 'production', onChange: function(e) { setEbayInt(Object.assign({}, ebayInt, { environment: e.target.value })); } }),
          React.createElement('div', { style: { display: 'flex', alignItems: 'flex-end', paddingBottom: 4 } },
            React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: C.text } },
              React.createElement('input', { type: 'checkbox', checked: ebayInt.enabled, onChange: function(e) { setEbayInt(Object.assign({}, ebayInt, { enabled: e.target.checked })); } }),
              'Enable Integration'
            )
          )
        ),
        React.createElement('div', { style: { display: 'flex', gap: 8, marginBottom: 16 } },
          React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: saveEbayCredentials }, React.createElement(LIcon, { name: 'save', size: 14 }), ' Save Credentials'),
          React.createElement(Btn, { size: 'sm', onClick: testEbay, disabled: testing || !hasEbaySecret }, testing ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Testing...'] : [React.createElement(LIcon, { name: 'plug', size: 14, key: 'ic' }), ' Test Connection']),
          React.createElement(Btn, { size: 'sm', onClick: syncEbay, disabled: syncing || !ebayInt.enabled }, syncing ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Syncing...'] : [React.createElement(LIcon, { name: 'refresh-cw', size: 14, key: 'ic' }), ' Sync Orders Now'])
        ),
        testResult && expandedInt.ebay && React.createElement('div', { style: { padding: '10px 14px', borderRadius: 8, marginBottom: 12,
          background: testResult.success ? C.success + '15' : C.danger + '15',
          border: '1px solid ' + (testResult.success ? C.success + '40' : C.danger + '40'),
          color: testResult.success ? C.success : C.danger, fontSize: 13 }
        }, testResult.success ? React.createElement(LIcon, { name: 'check', size: 14 }) : React.createElement(LIcon, { name: 'x', size: 14 }), ' ', testResult.message),
        syncResult && expandedInt.ebay && React.createElement('div', { style: { padding: '10px 14px', borderRadius: 8, marginBottom: 12,
          background: syncResult.error ? C.danger + '15' : C.accent + '15',
          border: '1px solid ' + (syncResult.error ? C.danger + '40' : C.accent + '40'),
          color: syncResult.error ? C.danger : C.accent, fontSize: 13 }
        }, syncResult.error ? [React.createElement(LIcon, { name: 'x', size: 14, key: 'ic' }), ' ' + syncResult.error] : [React.createElement(LIcon, { name: 'check', size: 14, key: 'ic' }), ' Synced ' + (syncResult.synced || 0) + ' new, ' + (syncResult.updated || 0) + ' updated' + (syncResult.failed ? ', ' + syncResult.failed + ' failed' : '') + ' (' + (syncResult.total || 0) + ' total)']),
        ebayInt.last_sync_at && React.createElement('div', { style: { fontSize: 11, color: C.textMuted } }, 'Last synced: ' + new Date(ebayInt.last_sync_at).toLocaleString()),
        // ─── Marketplace Account Deletion (MAD) Compliance ───
        React.createElement('div', { style: { marginTop: 20, paddingTop: 16, borderTop: '1px solid ' + C.border } },
          React.createElement('div', { style: { fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 } }, 'Marketplace Account Deletion Compliance'),
          React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12, lineHeight: 1.5 } },
            'eBay requires a public webhook to receive user-deletion notifications before approving production credentials. Generate a verification token below, then register the endpoint + token in the ',
            React.createElement('a', { href: 'https://developer.ebay.com/my/push_notifications', target: '_blank', style: { color: C.accent, textDecoration: 'underline' } }, 'eBay Developer Portal'),
            ' under Alerts and Notifications → Marketplace Account Deletion.'
          ),
          React.createElement('div', { style: { padding: '10px 14px', borderRadius: 8, background: C.bgAlt, border: '1px solid ' + C.border, marginBottom: 12 } },
            React.createElement('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, marginBottom: 4 } }, 'Endpoint URL (copy into eBay portal)'),
            React.createElement('div', { style: { fontSize: 12, fontFamily: 'inherit', color: C.text, wordBreak: 'break-all' } }, 'https://app.atharvatoolz.com/api/ebay/account-deletion')
          ),
          React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'flex-end', marginBottom: 6 } },
            React.createElement(Inp, { label: 'Verification Token (32-80 chars)',
              value: ebayVerifToken,
              onChange: function(e) { setEbayVerifToken(e.target.value); } }),
            React.createElement(Btn, { size: 'sm', onClick: function() {
              var tok = ''; var chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-';
              for (var i = 0; i < 48; i++) tok += chars.charAt(Math.floor(Math.random() * chars.length));
              setEbayVerifToken(tok);
            } }, React.createElement(LIcon, { name: 'refresh-cw', size: 14 }), ' Generate')
          ),
          React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 6, marginBottom: 12 } }, 'Click Generate, then Save to persist. After saving, register the endpoint + this token in eBay. eBay will hit the URL with a challenge; if the hash matches, eBay marks your app compliant.'),
          React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: saveEbayVerifToken }, React.createElement(LIcon, { name: 'save', size: 14 }), ' Save Verification Token')
        ),
        React.createElement(IntegrationApiLog, { channel: 'ebay', label: 'eBay' })
      )
    ) },
    // SHEIN Card — application keys + one-time store authorization for the Shopify ↔ SHEIN sync
    // (docs/25), and the manual XLSX import of after-sales orders. Always shown: saving the keys
    // creates the integrations row.
    { title: 'SHEIN', el: React.createElement(Card, { key: 'shein-card', style: { padding: 0, overflow: 'hidden', marginTop: 16 } },
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: expandedInt.shein ? '1px solid ' + C.border : 'none', cursor: 'pointer' }, onClick: function() { toggleInt('shein'); } },
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12 } },
          React.createElement('div', { style: { width: 40, height: 40, borderRadius: 8, background: '#00000015', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, color: C.text } }, 'SHEIN'),
          React.createElement('div', null,
            React.createElement('div', { style: { fontSize: 15, fontWeight: 600, color: C.text } }, 'SHEIN'),
            React.createElement('div', { style: { fontSize: 12, color: C.textMuted } },
              (sheinInt.enabled && sheinStore.authorized
                ? 'Connected' + (sheinStore.store_name ? ' — ' + sheinStore.store_name : '') + ' — Shopify sync (orders / inventory / tracking)'
                : (sheinStore.authorized ? 'Store authorized, integration not enabled' : 'Not connected')) +
              ' · ' + (sheinSummary && sheinSummary.total_rows > 0 ? (sheinSummary.total_rows + ' returns imported (' + String(sheinSummary.earliest || '').slice(0, 10) + ' → ' + String(sheinSummary.latest || '').slice(0, 10) + ')') : 'No returns imported yet'))
          )
        ),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
            React.createElement('div', { style: { width: 8, height: 8, borderRadius: 4, background: sheinInt.enabled && sheinStore.authorized ? C.success : C.textDim } }),
            React.createElement('span', { style: { fontSize: 12, color: sheinInt.enabled && sheinStore.authorized ? C.success : C.textMuted, fontWeight: 500 } }, sheinInt.enabled && sheinStore.authorized ? 'Enabled' : 'Disabled')
          ),
          React.createElement(LIcon, { name: expandedInt.shein ? 'chevron-up' : 'chevron-down', size: 16, style: { color: C.textMuted } })
        )
      ),
      expandedInt.shein && React.createElement('div', { style: { padding: 20 } },
        React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12, lineHeight: 1.6 } },
          'SHEIN Open Platform application keys (open.sheincorp.com > Console > Application Management > the application > Basic Information). This server\'s IP address must be on the application\'s IP whitelist. After saving, run the one-time "Authorize SHEIN Store" consent, signed in with the store\'s main account, to mint the store keys. Powers the Shopify ↔ SHEIN order / inventory / tracking sync.'),
        React.createElement('div', { style: { marginBottom: 12 } },
          React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 4 } }, 'Shopify store for this sync'),
          React.createElement('select', { value: String(sheinInt.shopify_integration_id || ''), onChange: function(e) { setSheinInt(Object.assign({}, sheinInt, { shopify_integration_id: e.target.value })); },
            style: { width: '100%', background: C.surface, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', colorScheme: getThemeName() === 'light' ? 'light' : 'dark' } },
            React.createElement('option', { value: '' }, 'Select Store…'),
            shopifyStores.map(function(st) {
              var name = (st.display_name || 'Shopify') + ((st.config && st.config.shop) ? ' (' + st.config.shop + ')' : '');
              return React.createElement('option', { key: st.id, value: String(st.id) }, name);
            })
          )
        ),
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 } },
          React.createElement(Inp, { label: 'App ID', placeholder: 'e.g. 164B4BB8FD00…', value: sheinInt.client_id || '', onChange: function(e) { setSheinInt(Object.assign({}, sheinInt, { client_id: e.target.value })); } }),
          React.createElement(Inp, { label: 'App Secret' + (hasSheinSecret ? ' (saved — leave blank to keep)' : ''), type: 'password',
            placeholder: hasSheinSecret ? '••••••••••••••' : 'Enter app secret',
            value: sheinInt.client_secret || '', onChange: function(e) { setSheinInt(Object.assign({}, sheinInt, { client_secret: e.target.value })); } })
        ),
        React.createElement('div', { style: { marginBottom: 12 } },
          React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: C.text } },
            React.createElement('input', { type: 'checkbox', checked: sheinInt.enabled, onChange: function(e) { setSheinInt(Object.assign({}, sheinInt, { enabled: e.target.checked })); } }),
            'Enable Integration'
          )
        ),
        React.createElement('div', { style: { display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' } },
          React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: saveShein }, React.createElement(LIcon, { name: 'save', size: 14 }), ' Save Credentials'),
          React.createElement(Btn, { size: 'sm', onClick: connectShein, disabled: !hasSheinSecret }, React.createElement(LIcon, { name: 'link', size: 14 }), sheinStore.authorized ? ' Authorize SHEIN Store Again' : ' Authorize SHEIN Store'),
          React.createElement(Btn, { size: 'sm', onClick: testShein, disabled: sheinTesting || !sheinStore.authorized }, sheinTesting ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Testing...'] : [React.createElement(LIcon, { name: 'plug', size: 14, key: 'ic' }), ' Test Connection']),
          sheinStore.authorized && React.createElement('a', { href: pageToPath('shopify-shein'),
            onClick: function(e) { if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return; e.preventDefault(); onNavigate('shopify-shein'); },
            style: { background: C.accent, color: '#fff', textDecoration: 'none', padding: '6px 12px', fontSize: 12, borderRadius: 6, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 'auto', cursor: 'pointer' } },
            React.createElement(LIcon, { name: 'layout-dashboard', size: 14 }), ' Open Dashboard')
        ),
        sheinStore.authorized && React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12 } },
          'Store authorized' + (sheinStore.authorized_at ? ' ' + new Date(sheinStore.authorized_at).toLocaleString() : '') +
          (sheinStore.store_name ? ' — ' + sheinStore.store_name : '') + (sheinStore.supplier_id ? ' (merchant ' + sheinStore.supplier_id + ')' : '')),
        !sheinStore.authorized && hasSheinSecret && React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'end', marginBottom: 12 } },
          React.createElement(Inp, { label: 'Fallback: paste the tempToken (or the full redirect address) if the SHEIN tab errored', placeholder: 'https://app.atharvatoolz.com/oauth/shein/authorized?appid=...&tempToken=...', value: sheinCode, onChange: function(e) { setSheinCode(e.target.value); } }),
          React.createElement(Btn, { size: 'sm', onClick: exchangeSheinCode, disabled: sheinExchanging }, sheinExchanging ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Exchanging...'] : 'Exchange Code')
        ),
        sheinTest && React.createElement('div', { style: { padding: '10px 14px', borderRadius: 8, marginBottom: 12,
          background: sheinTest.success ? C.success + '15' : C.danger + '15',
          border: '1px solid ' + (sheinTest.success ? C.success + '40' : C.danger + '40'),
          color: sheinTest.success ? C.success : C.danger, fontSize: 13 }
        }, sheinTest.success ? React.createElement(LIcon, { name: 'check', size: 14 }) : React.createElement(LIcon, { name: 'x', size: 14 }), ' ', sheinTest.message),
        React.createElement('div', { style: { borderTop: '1px solid ' + C.border, margin: '16px 0 12px', paddingTop: 14, fontSize: 13, fontWeight: 600, color: C.text } }, 'Returns Import'),
        React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12, lineHeight: 1.5 } },
          'Returns are not part of the sync. Export your after-sales orders from the SHEIN seller portal as XLSX and upload below. Only rows with resolution = "Return & Refund" and refund ratio > 0 are counted as actual returns (reject/cancel rows are filtered out).'),
        React.createElement('div', { style: { padding: '12px 14px', borderRadius: 8, background: C.bgAlt, border: '1px solid ' + C.border, marginBottom: 16, fontSize: 12, lineHeight: 1.7, color: C.textMuted } },
          React.createElement('div', { style: { fontWeight: 600, color: C.text, marginBottom: 4 } }, 'How to export from SHEIN'),
          React.createElement('div', null, '1. Open ', React.createElement('strong', { style: { color: C.text } }, 'SHEIN Seller Portal'), ' → Orders → After-sales Orders'),
          React.createElement('div', null, '2. Set the date range (the importer is idempotent — re-uploading the same period skips duplicates by After-sales order number)'),
          React.createElement('div', null, '3. Click ', React.createElement('strong', { style: { color: C.text } }, 'Export'), ' → download the XLSX'),
          React.createElement('div', null, '4. Upload the file below. Run weekly or monthly to keep up to date.')
        ),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 } },
          React.createElement('input', { type: 'file', ref: sheinFileRef, accept: '.xlsx,.xls,.csv', style: { display: 'none' }, onChange: function(e) { var f = e.target.files && e.target.files[0]; if (f) uploadSheinReturns(f); e.target.value = ''; } }),
          React.createElement(Btn, { variant: 'primary', size: 'sm', disabled: sheinUploading, onClick: function() { sheinFileRef.current && sheinFileRef.current.click(); } },
            sheinUploading ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Uploading...'] : [React.createElement(LIcon, { name: 'upload', size: 14, key: 'ic' }), ' Upload SHEIN Export']
          )
        ),
        sheinResult && React.createElement('div', { style: { padding: '10px 14px', borderRadius: 8, background: C.success + '15', border: '1px solid ' + C.success + '40', color: C.success, fontSize: 13, marginBottom: 12 } },
          React.createElement(LIcon, { name: 'check', size: 14 }), ' ' + sheinResult.inserted + ' new inserted, ' + sheinResult.already_existed + ' already existed, ' + sheinResult.skipped_rejected + ' rejected filtered, ' + sheinResult.skipped_invalid + ' invalid (of ' + sheinResult.total_rows + ' rows)'
        ),
        sheinSummary && sheinSummary.total_rows > 0 && React.createElement('div', { style: { fontSize: 11, color: C.textMuted } }, 'Last import: ' + (sheinSummary.last_import ? new Date(sheinSummary.last_import).toLocaleString() : '—') + ' • Covers ' + sheinSummary.distinct_skus + ' distinct SKUs'),
        React.createElement(IntegrationApiLog, { channel: 'shein', label: 'SHEIN' })
      )
    ) },
    // Zentail Card
    { title: 'Zentail', el: existingChannels.zentail && React.createElement(Card, { key: 'zentail-card', style: { padding: 0, overflow: 'hidden', marginTop: 16 } },
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: expandedInt.zentail ? '1px solid ' + C.border : 'none', cursor: 'pointer' }, onClick: function() { toggleInt('zentail'); } },
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12 } },
          React.createElement('div', { style: { width: 40, height: 40, borderRadius: 8, background: '#4A90D915', display: 'flex', alignItems: 'center', justifyContent: 'center' } },
            React.createElement(LIcon, { name: 'layers', size: 22 })
          ),
          React.createElement('div', null,
            React.createElement('div', { style: { fontSize: 15, fontWeight: 600, color: C.text } }, 'Zentail'),
            React.createElement('div', { style: { fontSize: 12, color: C.textMuted } }, zentail.enabled && hasZentailSecret ? 'Connected — Catalog & order sync' : 'Not connected')
          )
        ),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
            React.createElement('div', { style: { width: 8, height: 8, borderRadius: 4, background: zentail.enabled && hasZentailSecret ? C.success : C.textDim } }),
            React.createElement('span', { style: { fontSize: 12, color: zentail.enabled && hasZentailSecret ? C.success : C.textMuted, fontWeight: 500 } }, zentail.enabled && hasZentailSecret ? 'Enabled' : 'Disabled')
          ),
          React.createElement(LIcon, { name: expandedInt.zentail ? 'chevron-up' : 'chevron-down', size: 16, style: { color: C.textMuted } })
        )
      ),
      expandedInt.zentail && React.createElement('div', { style: { padding: '16px 20px' } },
        React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12, lineHeight: 1.5 } },
          'Import catalog data and sales orders from Zentail for product matching and order forecasting.'),
        React.createElement(Inp, { label: 'API Token' + (hasZentailSecret ? ' (saved — leave blank to keep)' : ''), type: 'password',
          placeholder: hasZentailSecret ? '••••••••••••••••••••' : 'Enter Zentail API token...',
          value: zentail.api_token, onChange: function(e) { setZentail(Object.assign({}, zentail, { api_token: e.target.value })); } }),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, marginBottom: 12 } },
          React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.text, cursor: 'pointer' } },
            React.createElement('input', { type: 'checkbox', checked: zentail.enabled, onChange: function(e) { setZentail(Object.assign({}, zentail, { enabled: e.target.checked })); } }),
            'Enable')
        ),
        React.createElement('div', { style: { display: 'flex', gap: 8 } },
          React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: saveZentailCreds }, React.createElement(LIcon, { name: 'save', size: 14 }), ' Save'),
          hasZentailSecret && React.createElement(Btn, { size: 'sm', onClick: testZentailConnection, disabled: zentailTesting }, zentailTesting ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Testing...'] : [React.createElement(LIcon, { name: 'flask-conical', size: 14, key: 'ic' }), ' Test Connection'])
        ),
        zentailTest && React.createElement('div', { style: { padding: '10px 14px', borderRadius: 8, marginTop: 12,
          background: zentailTest.success ? C.success + '15' : C.danger + '15',
          border: '1px solid ' + (zentailTest.success ? C.success + '40' : C.danger + '40'),
          color: zentailTest.success ? C.success : C.danger, fontSize: 13 }
        }, zentailTest.success ? React.createElement(LIcon, { name: 'check', size: 14 }) : React.createElement(LIcon, { name: 'x', size: 14 }), ' ', zentailTest.message),
        // Sync buttons
        hasZentailSecret && zentail.enabled && React.createElement('div', { style: { marginTop: 16, borderTop: '1px solid ' + C.border + '44', paddingTop: 16 } },
          React.createElement('div', { style: { fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 } }, 'Data Sync'),
          // Live sync status panel
          zentailLiveStatus && React.createElement('div', { style: { background: C.bgAlt, borderRadius: 8, padding: '12px 14px', marginBottom: 12, border: '1px solid ' + C.border + '44' } },
            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 } },
              // Orders status
              React.createElement('div', null,
                React.createElement('div', { style: { fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 } }, 'Orders'),
                React.createElement('div', { style: { fontSize: 18, fontWeight: 700, color: C.text } }, (zentailLiveStatus.orders && zentailLiveStatus.orders.count || 0).toLocaleString()),
                zentailLiveStatus.orders && zentailLiveStatus.orders.running
                  ? React.createElement('div', { style: { fontSize: 11, color: C.warning, marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 } },
                      React.createElement(LIcon, { name: 'loader-2', size: 11, spin: true }), 'Syncing',
                      zentailLiveStatus.orders.progress ? ' (' + (zentailLiveStatus.orders.progress.phase || '') + ' · ' + zentailLiveStatus.orders.progress.fetched + ' fetched)' : '')
                  : zentailLiveStatus.orders && zentailLiveStatus.orders.lastResult && zentailLiveStatus.orders.lastResult.error
                    ? React.createElement('div', { style: { fontSize: 11, color: C.danger, marginTop: 2 } }, 'Error')
                    : null,
                React.createElement('div', { style: { marginTop: 6, fontSize: 10, color: C.textDim, lineHeight: 1.8 } },
                  React.createElement('div', null, React.createElement('span', { style: { color: C.textMuted } }, 'Auto Sync: '),
                    zentailLiveStatus.orders && zentailLiveStatus.orders.history && zentailLiveStatus.orders.history.sync
                      ? new Date(zentailLiveStatus.orders.history.sync.completedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) + fmtDuration(zentailLiveStatus.orders.history.sync.durationMs)
                      : 'Never'),
                  React.createElement('div', null, React.createElement('span', { style: { color: C.textMuted } }, 'Backfill: '),
                    zentailLiveStatus.orders && zentailLiveStatus.orders.history && zentailLiveStatus.orders.history.backfill
                      ? new Date(zentailLiveStatus.orders.history.backfill.completedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) + fmtDuration(zentailLiveStatus.orders.history.backfill.durationMs)
                      : 'Never')
                )
              ),
              // Products status
              React.createElement('div', null,
                React.createElement('div', { style: { fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 } }, 'Products'),
                React.createElement('div', { style: { fontSize: 18, fontWeight: 700, color: C.text } }, (zentailLiveStatus.products && zentailLiveStatus.products.count || 0).toLocaleString()),
                zentailLiveStatus.products && zentailLiveStatus.products.running
                  ? React.createElement('div', { style: { fontSize: 11, color: C.warning, marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 } },
                      React.createElement(LIcon, { name: 'loader-2', size: 11, spin: true }), zentailLiveStatus.products.message || 'Syncing...')
                  : null,
                React.createElement('div', { style: { marginTop: 6, fontSize: 10, color: C.textDim, lineHeight: 1.8 } },
                  React.createElement('div', null, React.createElement('span', { style: { color: C.textMuted } }, 'Quick Sync: '),
                    zentailLiveStatus.products && zentailLiveStatus.products.history && zentailLiveStatus.products.history.quick
                      ? new Date(zentailLiveStatus.products.history.quick.completedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) + fmtDuration(zentailLiveStatus.products.history.quick.durationMs)
                      : 'Never'),
                  React.createElement('div', null, React.createElement('span', { style: { color: C.textMuted } }, 'Full Sync: '),
                    zentailLiveStatus.products && zentailLiveStatus.products.history && zentailLiveStatus.products.history.full
                      ? new Date(zentailLiveStatus.products.history.full.completedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) + fmtDuration(zentailLiveStatus.products.history.full.durationMs)
                      : 'Never'),
                  React.createElement('div', null, React.createElement('span', { style: { color: C.textMuted } }, 'Refresh Attrs: '),
                    zentailLiveStatus.products && zentailLiveStatus.products.history && zentailLiveStatus.products.history.refresh
                      ? new Date(zentailLiveStatus.products.history.refresh.completedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) + fmtDuration(zentailLiveStatus.products.history.refresh.durationMs)
                      : 'Never')
                )
              ),
              // Inventory status — last success + duration + counts, like the other syncs;
              // errors show the actual message (full text in the tooltip)
              React.createElement('div', null,
                React.createElement('div', { style: { fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 } }, 'Inventory'),
                (function() {
                  var inv = zentailLiveStatus.inventory || {};
                  var res = inv.lastResult || {};
                  var statusEl = inv.running
                    ? React.createElement('div', { style: { fontSize: 11, color: C.warning, marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 } },
                        React.createElement(LIcon, { name: 'loader-2', size: 11, spin: true }), inv.isFullSync ? 'Full sync...' : 'Syncing...')
                    : res.error
                      ? React.createElement('div', { title: res.error, style: { fontSize: 11, color: C.danger, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 190 } },
                          'Error' + (res.at ? ' ' + new Date(res.at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '') + ': ' + res.error)
                      : inv.lastRun
                        ? React.createElement('div', { style: { fontSize: 14, fontWeight: 700, color: C.success, marginTop: 2 } }, 'Active')
                        : React.createElement('div', { style: { fontSize: 11, color: C.textDim, marginTop: 2 } }, 'Not started');
                  return React.createElement('div', null,
                    statusEl,
                    React.createElement('div', { style: { marginTop: 6, fontSize: 10, color: C.textDim, lineHeight: 1.8 } },
                      React.createElement('div', null, React.createElement('span', { style: { color: C.textMuted } }, 'Last Sync: '),
                        inv.lastRun
                          ? new Date(inv.lastRun).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) + (res.durationMs != null ? fmtDuration(res.durationMs) : '')
                          : 'Never'),
                      React.createElement('div', null, React.createElement('span', { style: { color: C.textMuted } }, 'Cadence: '),
                        'every 60s' + (!res.error && res.skus != null ? '  ·  ' + res.skus + ' SKUs, ' + (res.updated || 0) + ' updated' : ''))
                    )
                  );
                })()
              )
            ),
          ),
          React.createElement('div', { style: { display: 'flex', gap: 8, flexWrap: 'wrap' } },
            React.createElement(Btn, { size: 'sm', onClick: async function() {
              setZentailSyncing('orders'); setZentailSyncResult(null);
              try {
                await api('/integrations/zentail/sync-orders', { method: 'POST' });
                var done = false;
                while (!done) {
                  await new Promise(function(r) { setTimeout(r, 3000); });
                  var st = await api('/integrations/zentail/sync-orders/status');
                  if (st.progress) { setZentailSyncResult({ success: true, message: 'Syncing... ' + st.progress.fetched + ' fetched, ' + st.progress.created + ' new, ' + st.progress.updated + ' updated' }); }
                  if (!st.running) {
                    done = true;
                    if (st.lastResult && st.lastResult.error) { setZentailSyncResult({ success: false, message: st.lastResult.error }); }
                    else if (st.lastResult) { setZentailSyncResult({ success: true, message: st.lastResult.created + ' new, ' + st.lastResult.updated + ' updated (' + st.lastResult.total + ' total orders)' }); }
                    setZentailLastSync(new Date().toISOString());
                  }
                }
              } catch (e) { setZentailSyncResult({ success: false, message: e.message }); }
              setZentailSyncing(null);
            }, disabled: zentailSyncing === 'orders' || zentailSyncing === 'backfill' },
              zentailSyncing === 'orders' ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Syncing...'] : [React.createElement(LIcon, { name: 'refresh-cw', size: 14, key: 'ic' }), ' Sync Orders']),
            React.createElement(Btn, { size: 'sm', onClick: async function() {
              var progress = null;
              try { progress = await api('/integrations/zentail/backfill-progress'); } catch(e) {}
              var defaultStart = progress && progress.earliestDate ? progress.earliestDate : '2016-01-01';
              setBackfillModal({ startDate: defaultStart, endDate: new Date().toISOString().slice(0, 10), progress: progress && progress.completedDays > 0 ? progress : null });
            }, disabled: zentailSyncing === 'orders' || zentailSyncing === 'backfill' },
              zentailSyncing === 'backfill' ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Backfilling...'] : [React.createElement(LIcon, { name: 'history', size: 14, key: 'ic' }), ' Backfill Historical Orders']),
            React.createElement(Btn, { size: 'sm', onClick: function() {
              var inp = document.createElement('input');
              inp.type = 'file'; inp.accept = '.csv';
              inp.onchange = async function() {
                if (!inp.files[0]) return;
                setZentailSyncing('csvimport'); setZentailSyncResult(null);
                try {
                  await apiUpload('/integrations/zentail/import-orders-csv', inp.files[0]);
                  // Poll for progress
                  var done = false;
                  while (!done) {
                    await new Promise(function(r) { setTimeout(r, 2000); });
                    var st = await api('/integrations/zentail/import-orders-csv/status');
                    if (st.progress) {
                      var pct = st.progress.total > 0 ? Math.round((st.progress.processed / st.progress.total) * 100) : 0;
                      setZentailSyncResult({ success: true, message: 'Importing... ' + st.progress.created + ' new, ' + st.progress.skipped + ' skipped (' + st.progress.processed + '/' + st.progress.total + ' orders \u2014 ' + pct + '%)' });
                    }
                    if (!st.running) {
                      done = true;
                      if (st.lastResult) {
                        if (st.lastResult.success) {
                          setZentailSyncResult({ success: true, message: 'Imported ' + st.lastResult.created + ' new orders (' + st.lastResult.lines + ' line items), ' + st.lastResult.skipped + ' already existed' });
                          showToast('CSV import complete: ' + st.lastResult.created + ' new, ' + st.lastResult.skipped + ' skipped');
                        } else {
                          setZentailSyncResult({ success: false, message: st.lastResult.error + ' (partial: ' + st.lastResult.created + ' created before error)' });
                        }
                      }
                    }
                  }
                } catch (e) { setZentailSyncResult({ success: false, message: e.message }); }
                setZentailSyncing(null);
              };
              inp.click();
            }, disabled: !!zentailSyncing },
              zentailSyncing === 'csvimport' ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Importing...'] : [React.createElement(LIcon, { name: 'upload', size: 14, key: 'ic' }), ' Import Sales Order Line Item CSV']),
            React.createElement(Btn, { size: 'sm', onClick: function() {
              var inp = document.createElement('input');
              inp.type = 'file'; inp.accept = '.csv';
              inp.onchange = async function() {
                if (!inp.files[0]) return;
                setZentailSyncing('bundleimport'); setZentailSyncResult(null);
                try {
                  var text = await inp.files[0].text();
                  var lines = text.split('\n').map(function(l) { return l.trim(); }).filter(Boolean);
                  if (lines.length < 2) throw new Error('CSV has no data rows');
                  var headers = lines[0].split(',').map(function(h) { return h.replace(/^"|"$/g, '').trim(); });
                  var rows = [];
                  for (var i = 1; i < lines.length; i++) {
                    var vals = lines[i].split(',').map(function(v) { return v.replace(/^"|"$/g, '').replace(/^="(.*)"$/, '$1').trim(); });
                    var row = {};
                    headers.forEach(function(h, idx) { row[h] = vals[idx] || ''; });
                    rows.push(row);
                  }
                  var result = await api('/products/bundles/import-csv', { method: 'POST', body: JSON.stringify({ rows: rows }) });
                  var msg = result.parents + ' parent SKUs: ' + result.kits.created + ' kits created, ' + result.kits.updated + ' kits updated, ' + result.bundles.created + ' bundle components created, ' + result.bundles.updated + ' updated' + (result.skipped ? ', ' + result.skipped + ' skipped' : '') + (result.errors.length ? ', ' + result.errors.length + ' errors' : '');
                  setZentailSyncResult({ success: true, message: msg });
                  showToast('Import complete: ' + result.kits.created + ' kits, ' + result.bundles.created + ' bundle components');
                } catch (e) { setZentailSyncResult({ success: false, message: e.message }); }
                setZentailSyncing(null);
              };
              inp.click();
            }, disabled: !!zentailSyncing },
              zentailSyncing === 'bundleimport' ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'bi' }), ' Importing...'] : [React.createElement(LIcon, { name: 'package', size: 14, key: 'bi' }), ' Import Kits & Bundles CSV']),
            React.createElement(Btn, { size: 'sm', onClick: async function() {
              setZentailSyncing('products'); setZentailSyncResult(null);
              try {
                await api('/integrations/zentail/sync-products?mode=quick', { method: 'POST' });
                var done = false;
                while (!done) {
                  await new Promise(function(r) { setTimeout(r, 3000); });
                  var st = await api('/integrations/zentail/sync-products/status');
                  if (!st.running) {
                    done = true;
                    if (st.status === 'done') {
                      setZentailSyncResult({ success: true, message: st.synced + ' synced, ' + st.matched + ' matched, ' + (st.message || '') });
                      setZentailLastSync(new Date().toISOString());
                    } else {
                      setZentailSyncResult({ success: false, message: st.message || 'Sync failed' });
                    }
                  } else {
                    setZentailSyncResult({ success: true, message: st.message || 'Working...' });
                  }
                }
              } catch (e) { setZentailSyncResult({ success: false, message: e.message }); }
              setZentailSyncing(null);
            }, disabled: zentailSyncing === 'products' || zentailSyncing === 'fullsync' || zentailSyncing === 'attrs' },
              zentailSyncing === 'products' ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Syncing...'] : [React.createElement(LIcon, { name: 'database', size: 14, key: 'ic' }), ' Quick Sync']),
            React.createElement(Btn, { size: 'sm', onClick: async function() {
              if (!(await appConfirm('Full sync exports ALL ~64,000 products (active + inactive) with all known attributes in batches. This may take 10-30 minutes. Continue?'))) return;
              setZentailSyncing('fullsync'); setZentailSyncResult(null);
              try {
                await api('/integrations/zentail/sync-products?mode=full', { method: 'POST' });
                var done = false;
                while (!done) {
                  await new Promise(function(r) { setTimeout(r, 5000); });
                  var st = await api('/integrations/zentail/sync-products/status');
                  if (!st.running) {
                    done = true;
                    if (st.status === 'done') {
                      setZentailSyncResult({ success: true, message: 'Full: ' + st.synced + ' synced, ' + st.matched + ' matched, ' + (st.message || '') });
                      setZentailLastSync(new Date().toISOString());
                    } else {
                      setZentailSyncResult({ success: false, message: st.message || 'Full sync failed' });
                    }
                  } else {
                    setZentailSyncResult({ success: true, message: st.message || 'Full sync working...' });
                  }
                }
              } catch (e) { setZentailSyncResult({ success: false, message: e.message }); }
              setZentailSyncing(null);
            }, disabled: zentailSyncing === 'products' || zentailSyncing === 'fullsync' || zentailSyncing === 'attrs' },
              zentailSyncing === 'fullsync' ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Full Sync...'] : [React.createElement(LIcon, { name: 'layers', size: 14, key: 'ic' }), ' Full Sync']),
            React.createElement(Btn, { size: 'sm', onClick: async function() {
              setZentailSyncing('attrs'); setZentailSyncResult(null);
              try {
                var r = await api('/integrations/zentail/refresh-attributes', { method: 'POST' });
                setZentailSyncResult({ success: true, message: r.core + ' attributes cataloged (' + r.inserted + ' new)' });
                api('/integrations/zentail/mapping-stats').then(function(ms) { setZentailMappingStats(ms); }).catch(function(e) { console.error('[silent-catch]', e); });
              } catch (e) { setZentailSyncResult({ success: false, message: e.message }); }
              setZentailSyncing(null);
            }, disabled: zentailSyncing === 'products' || zentailSyncing === 'fullsync' || zentailSyncing === 'attrs' },
              zentailSyncing === 'attrs' ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Refreshing...'] : [React.createElement(LIcon, { name: 'list', size: 14, key: 'ic' }), ' Refresh Attributes'])
          ),
          React.createElement('div', { style: { fontSize: 11, color: C.textDim, marginTop: 10, lineHeight: 1.8 } },
            React.createElement('div', { style: { marginBottom: 4 } }, React.createElement('strong', null, 'Sync Orders'), ' — Syncs all Pending and Partially Shipped orders, plus any orders updated in the last 24 hours to catch status changes like newly shipped, cancelled, or refunded orders. Runs automatically every 4 hours.'),
            React.createElement('div', { style: { marginBottom: 4 } }, React.createElement('strong', null, 'Backfill Historical Orders'), ' — One-time import of older orders beyond the 30-day rolling window. Specify how many days of history to pull. Use this when first connecting or to fill in gaps.'),
            React.createElement('div', { style: { marginBottom: 4 } }, React.createElement('strong', null, 'Import Sales Order Line Item CSV'), ' — Upload a Zentail order report CSV to bulk-import historical orders. Supports the standard Zentail report format with ="..." wrapped values. Existing orders are updated, new ones are created.'),
            React.createElement('div', { style: { marginBottom: 4 } }, React.createElement('strong', null, 'Import Kits & Bundles CSV'), ' — Upload a Zentail kit/bundle export CSV (columns: Sku, Component Sku, Component Sku Units). Auto-detects: parent SKUs with 1 component become kits (sets kit_parent + kit_qty on the child product), parent SKUs with 2+ components become bundles (bundle_contents table).'),
            React.createElement('div', { style: { marginBottom: 4 } }, React.createElement('strong', null, 'Quick Sync'), ' — Refreshes product data for attributes already known to have values in Zentail, plus any fields you\'ve explicitly mapped. Skips empty attributes to keep the export small and fast. Runs automatically every hour.'),
            React.createElement('div', { style: { marginBottom: 4 } }, React.createElement('strong', null, 'Full Sync'), ' — Exports all known attributes including unchecked ones to discover new data. When Zentail adds new attributes to your catalog, the full sync detects which ones have values and marks them for future quick syncs. Runs automatically every 4 hours.'),
            React.createElement('div', { style: { marginBottom: 4 } }, React.createElement('strong', null, 'Inventory Sync'), ' — Pulls real-time quantity and bin location data directly from the Zentail Inventory API. Only fetches changes since the last sync to stay lightweight. Runs automatically every 60 seconds.'),
            React.createElement('div', null, React.createElement('strong', null, 'Refresh Attributes'), ' — Queries Zentail for all available attribute names and updates the local catalog without exporting any product data. Use this to discover newly available attributes that can then be mapped to local product fields.')
          ),
          // Mapping stats indicator
          zentailMappingStats && React.createElement('div', { style: { marginTop: 12, display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8,
            background: zentailMappingStats.unmapped > 0 ? '#FF990015' : C.success + '15',
            border: '1px solid ' + (zentailMappingStats.unmapped > 0 ? '#FF990040' : C.success + '40') } },
            React.createElement(LIcon, { name: zentailMappingStats.unmapped > 0 ? 'alert-triangle' : 'check-circle', size: 16 }),
            React.createElement('span', { style: { fontSize: 13, color: zentailMappingStats.unmapped > 0 ? '#FF9900' : C.success, fontWeight: 500, flex: 1 } },
              zentailMappingStats.mapped + ' of ' + zentailMappingStats.total_cataloged + ' attributes mapped'
              + (zentailMappingStats.total_with_data > 0 ? ' (' + zentailMappingStats.total_with_data + ' with data)' : '')),
            React.createElement(Btn, { size: 'sm', variant: 'ghost', onClick: function() {
              if (onNavigate) onNavigate('zentail-mappings');
            } }, React.createElement(LIcon, { name: 'settings', size: 14 }), ' Manage Mappings')
          ),
          // Warehouse mapping status indicator
          (function() {
            // Zentail warehouses from mappings (all discovered)
            var zentailWhNames = [];
            var mappedZentailWhs = [];
            zentailWhMappings.forEach(function(wm) {
              if (zentailWhNames.indexOf(wm.zentail_warehouse) === -1) zentailWhNames.push(wm.zentail_warehouse);
              if (wm.warehouse_id && mappedZentailWhs.indexOf(wm.zentail_warehouse) === -1) mappedZentailWhs.push(wm.zentail_warehouse);
            });
            var unmappedZentail = zentailWhNames.filter(function(n) { return mappedZentailWhs.indexOf(n) === -1; });
            if (zentailWhNames.length === 0) return null;
            var hasIssues = unmappedZentail.length > 0;
            return React.createElement('div', { style: { marginTop: 12, display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px', borderRadius: 8,
              background: hasIssues ? '#FF990015' : C.success + '15',
              border: '1px solid ' + (hasIssues ? '#FF990040' : C.success + '40') } },
              React.createElement(LIcon, { name: hasIssues ? 'alert-triangle' : 'check-circle', size: 16, style: { marginTop: 2 } }),
              React.createElement('div', { style: { flex: 1, fontSize: 13, fontWeight: 500, color: hasIssues ? '#FF9900' : C.success } },
                hasIssues
                  ? [unmappedZentail.length + ' Zentail warehouse' + (unmappedZentail.length !== 1 ? 's' : '') + ' not mapped: ', React.createElement('span', { key: 'names', style: { fontWeight: 400 } }, unmappedZentail.join(', '))]
                  : mappedZentailWhs.length + ' of ' + zentailWhNames.length + ' Zentail warehouses mapped'
              )
            );
          })(),
          // Warehouse Mapping section
          React.createElement('div', { style: { marginTop: 12, padding: '12px 16px', borderRadius: 8, background: C.surface, border: '1px solid ' + C.border + '44' } },
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 } },
              React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
                React.createElement(LIcon, { name: 'warehouse', size: 16, color: C.accent }),
                React.createElement('span', { style: { fontSize: 13, fontWeight: 600, color: C.text } }, 'Warehouse Mapping')
              ),
              React.createElement(Btn, { size: 'sm', variant: 'ghost', onClick: async function() {
                try {
                  var r = await api('/integrations/zentail/discover-warehouses', { method: 'POST' });
                  setZentailWhMappings(r.mappings || []);
                  showToast(r.discovered + ' warehouses found' + (r.inserted > 0 ? ' (' + r.inserted + ' new)' : ''));
                } catch (e) { showToast(e.message, 'error'); }
              } }, React.createElement(LIcon, { name: 'search', size: 14 }), ' Discover')
            ),
            React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginBottom: 10, lineHeight: 1.5 } },
              'All warehouses sync automatically once mapped. New warehouses are discovered during inventory sync. Map each Zentail warehouse to one or more local warehouses to begin syncing inventory.'
            ),
            (function() {
              // Group mappings by zentail_warehouse
              var whGroups = {};
              zentailWhMappings.forEach(function(wm) {
                if (!whGroups[wm.zentail_warehouse]) whGroups[wm.zentail_warehouse] = [];
                if (wm.warehouse_id) whGroups[wm.zentail_warehouse].push(wm);
              });
              var zentailNames = Object.keys(whGroups).sort();
              if (zentailNames.length === 0) return React.createElement('div', { style: { fontSize: 12, color: C.textMuted, padding: '8px 0' } }, 'No Zentail warehouses discovered yet. Run a Full Sync or click Discover.');
              var totalMapped = zentailNames.filter(function(n) { return whGroups[n].length > 0; }).length;
              // Collect ALL mapped warehouse IDs across all Zentail warehouses
              var allMappedWhIds = [];
              zentailNames.forEach(function(n) { whGroups[n].forEach(function(m) { if (m.warehouse_id) allMappedWhIds.push(m.warehouse_id); }); });
              return React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 8 } },
                zentailNames.map(function(zName) {
                  var mappings = whGroups[zName];
                  var availableWh = allWarehouses.filter(function(w) { return !allMappedWhIds.includes(w.id); });
                  var curCfg = zentailWhConfig[zName] || {};
                  var curType = curCfg.warehouse_type || 'fbm';
                  var curBinName = curCfg.bin_display_name || '';
                  return React.createElement('div', { key: zName, style: { padding: '8px 12px', borderRadius: 6, background: C.bg, border: '1px solid ' + C.border + '44' } },
                    React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: mappings.length > 0 ? 6 : 0 } },
                      React.createElement('span', { style: { fontSize: 13, fontWeight: 600, color: C.text, minWidth: 60 } }, zName),
                      React.createElement('select', {
                        value: curType,
                        onChange: async function(e) {
                          var newType = e.target.value;
                          try {
                            await api('/integrations/zentail/warehouse-config/' + encodeURIComponent(zName), { method: 'PUT', body: JSON.stringify({ warehouse_type: newType, bin_display_name: curBinName || null }) });
                            setZentailWhConfig(Object.assign({}, zentailWhConfig, (function() { var o = {}; o[zName] = { zentail_warehouse: zName, warehouse_type: newType, bin_display_name: curBinName || null }; return o; })()));
                            showToast(zName + ' set to ' + newType.toUpperCase());
                          } catch (err) { showToast(err.message, 'error'); }
                        },
                        style: { fontSize: 11, padding: '3px 6px', borderRadius: 4, border: '1px solid ' + C.border, background: C.surface, color: C.text, minWidth: 60 }
                      },
                        React.createElement('option', { value: 'fbm' }, 'FBM'),
                        React.createElement('option', { value: '3pl' }, '3PL')
                      ),
                      curType === '3pl' && React.createElement('input', {
                        placeholder: 'Bin label (e.g. Amazon)',
                        defaultValue: curBinName,
                        onBlur: async function(e) {
                          var val = e.target.value.trim();
                          if (val === curBinName) return;
                          try {
                            await api('/integrations/zentail/warehouse-config/' + encodeURIComponent(zName), { method: 'PUT', body: JSON.stringify({ warehouse_type: '3pl', bin_display_name: val || null }) });
                            setZentailWhConfig(Object.assign({}, zentailWhConfig, (function() { var o = {}; o[zName] = { zentail_warehouse: zName, warehouse_type: '3pl', bin_display_name: val || null }; return o; })()));
                          } catch (err) { showToast(err.message, 'error'); }
                        },
                        style: { fontSize: 11, padding: '3px 6px', borderRadius: 4, border: '1px solid ' + C.border, background: C.surface, color: C.text, width: 100 }
                      }),
                      React.createElement(LIcon, { name: 'arrow-right', size: 14, color: C.textMuted }),
                      React.createElement('select', {
                        value: '',
                        onChange: async function(e) {
                          var val = parseInt(e.target.value);
                          if (!val) return;
                          try {
                            var r = await api('/integrations/zentail/warehouse-mappings', { method: 'POST', body: JSON.stringify({ zentail_warehouse: zName, warehouse_id: val }) });
                            // Refresh all mappings
                            var all = await api('/integrations/zentail/warehouse-mappings');
                            setZentailWhMappings(all || []);
                            showToast('Warehouse added to ' + zName);
                          } catch (err) { showToast(err.message, 'error'); }
                        },
                        style: { flex: 1, fontSize: 12, padding: '4px 8px', borderRadius: 4, border: '1px solid ' + C.border, background: C.surface, color: C.text }
                      },
                        React.createElement('option', { value: '' }, '+ Add warehouse...'),
                        availableWh.map(function(w) {
                          return React.createElement('option', { key: w.id, value: w.id }, w.warehouse_id + ' \u2014 ' + w.name);
                        })
                      ),
                      React.createElement(Badge, { color: mappings.length > 0 ? C.success : '#FF9900' }, mappings.length > 0 ? mappings.length + ' mapped' : 'Unmapped')
                    ),
                    mappings.length > 0 && React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 4 } },
                      mappings.map(function(m) {
                        return React.createElement('span', { key: m.id, style: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 10, background: C.accent + '18', color: C.accent } },
                          m.wh_code || ('ID:' + m.warehouse_id),
                          React.createElement('button', {
                            onClick: async function() {
                              try {
                                await api('/integrations/zentail/warehouse-mappings/by-id/' + m.id, { method: 'DELETE' });
                                setZentailWhMappings(zentailWhMappings.filter(function(x) { return x.id !== m.id; }));
                                showToast('Removed ' + (m.wh_code || '') + ' from ' + zName);
                              } catch (err) { showToast(err.message, 'error'); }
                            },
                            style: { background: 'none', border: 'none', color: C.accent, cursor: 'pointer', padding: 0, fontSize: 13, lineHeight: 1, fontWeight: 700 },
                            onMouseOver: function(e) { e.currentTarget.style.color = C.danger; },
                            onMouseOut: function(e) { e.currentTarget.style.color = C.accent; }
                          }, '\u00D7')
                        );
                      })
                    )
                  );
                }),
                React.createElement('div', { style: { fontSize: 11, color: C.textDim, marginTop: 4 } },
                  totalMapped + ' of ' + zentailNames.length + ' warehouses mapped')
              );
            })()
          ),
          zentailSyncResult && React.createElement('div', { style: { padding: '10px 14px', borderRadius: 8, marginTop: 10,
            background: zentailSyncResult.success ? C.success + '15' : C.danger + '15',
            border: '1px solid ' + (zentailSyncResult.success ? C.success + '40' : C.danger + '40'),
            color: zentailSyncResult.success ? C.success : C.danger, fontSize: 13 }
          }, zentailSyncResult.success ? React.createElement(LIcon, { name: 'check', size: 14 }) : React.createElement(LIcon, { name: 'x', size: 14 }), ' ', zentailSyncResult.message),
        ),
        React.createElement(IntegrationApiLog, { channel: 'zentail', label: 'Zentail' })
      )
    ) },
    // Currency Exchange Rate
    { title: 'Currency Exchange', el: existingChannels.exchange_rate && React.createElement(Card, { key: 'fx-card', style: { padding: 0, overflow: 'hidden', marginTop: 16 } },
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: expandedInt.fx ? '1px solid ' + C.border : 'none', cursor: 'pointer' }, onClick: function() { toggleInt('fx'); } },
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12 } },
          React.createElement('div', { style: { width: 40, height: 40, borderRadius: 8, background: '#4CAF5015', display: 'flex', alignItems: 'center', justifyContent: 'center' } },
            React.createElement(LIcon, { name: 'arrow-left-right', size: 22, style: { color: '#4CAF50' } })
          ),
          React.createElement('div', null,
            React.createElement('div', { style: { fontSize: 15, fontWeight: 600, color: C.text } }, 'Currency Exchange'),
            React.createElement('div', { style: { fontSize: 12, color: C.textMuted } },
              fxRate.enabled && fxRates && fxRates.rates ? 'Active — 1 CAD = ' + (fxRates.rates.CAD || 0).toFixed(4) + ' USD' : 'Not enabled')
          )
        ),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
            React.createElement('div', { style: { width: 8, height: 8, borderRadius: 4, background: fxRate.enabled && fxRates ? C.success : C.textDim } }),
            React.createElement('span', { style: { fontSize: 12, color: fxRate.enabled && fxRates ? C.success : C.textMuted, fontWeight: 500 } }, fxRate.enabled ? 'Enabled' : 'Disabled')
          ),
          React.createElement(LIcon, { name: expandedInt.fx ? 'chevron-up' : 'chevron-down', size: 16, style: { color: C.textMuted } })
        )
      ),
      expandedInt.fx && React.createElement('div', { style: { padding: 20 } },
        React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 16, lineHeight: 1.5 } },
          'Converts non-USD order totals to USD for unified analytics and reporting. Rates are fetched daily from the European Central Bank via frankfurter.app (no API key required).'),
        fxRates && fxRates.rates && React.createElement('div', { style: { marginBottom: 16 } },
          React.createElement('div', { style: { fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 } }, 'Current Rates'),
          React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8 } },
            Object.keys(fxRates.rates).filter(function(c) { return c !== 'USD'; }).map(function(cur) {
              return React.createElement('div', { key: cur, style: { padding: '8px 12px', borderRadius: 6, background: C.bgLight, border: '1px solid ' + C.border } },
                React.createElement('span', { style: { fontSize: 13, fontWeight: 600, color: C.text } }, '1 ' + cur + ' = '),
                React.createElement('span', { style: { fontSize: 13, color: C.primary } }, fxRates.rates[cur].toFixed(5) + ' USD')
              );
            })
          ),
          fxRates.date && React.createElement('div', { style: { fontSize: 11, color: C.textDim, marginTop: 6 } },
            'ECB rate date: ' + fxRates.date + (fxRates.updatedAt ? ' | Last fetched: ' + new Date(fxRates.updatedAt).toLocaleString() : ''))
        ),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 } },
          React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: C.text, cursor: 'pointer' } },
            React.createElement('input', { type: 'checkbox', checked: fxRate.enabled, onChange: function(e) { setFxRate(Object.assign({}, fxRate, { enabled: e.target.checked })); } }),
            'Enable currency conversion')
        ),
        React.createElement('div', { style: { display: 'flex', gap: 8, marginBottom: 12 } },
          React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: saveFxRate }, React.createElement(LIcon, { name: 'save', size: 14 }), ' Save'),
          React.createElement(Btn, { size: 'sm', onClick: refreshFxRate, disabled: fxTesting }, fxTesting ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Fetching...'] : [React.createElement(LIcon, { name: 'refresh-cw', size: 14, key: 'ic' }), ' Refresh Rates'])
        ),
        fxTest && React.createElement('div', { style: { padding: '10px 14px', borderRadius: 8,
          background: fxTest.success ? C.success + '15' : C.danger + '15',
          border: '1px solid ' + (fxTest.success ? C.success + '40' : C.danger + '40'),
          color: fxTest.success ? C.success : C.danger, fontSize: 13 }
        }, fxTest.success ? React.createElement(LIcon, { name: 'check', size: 14 }) : React.createElement(LIcon, { name: 'x', size: 14 }), ' ', fxTest.message),
        React.createElement(IntegrationApiLog, { channel: 'fx_rate', label: 'Exchange Rates' })
      )
    ) },
    // Shipping Carriers Card (DHL eCommerce + USPS direct APIs) — always visible;
    // manages the rows the Shipping app's rate shopper and label clients read.
    { title: 'Shipping Carriers', el: React.createElement(ShippingCarriersCard, { key: 'carriers-card', showToast: showToast }) }
      );
      // Added Shopify stores (multi-store): one card per extra row, titled to sort next to the main Shopify card
      shopifyStores.filter(function(s) { return s.marketplace; }).forEach(function(st) {
        var key = 'shopify_' + st.id;
        intCards.push({ title: 'Shopify — ' + (st.display_name || st.marketplace), el: React.createElement(ShopifyStoreCard, {
          key: key, store: st, showToast: showToast, onRefresh: refreshIntegrations,
          expanded: !!expandedInt[key], onToggle: function() { toggleInt(key); }
        }) });
      });
      // Hide cards for channels with no DB row, then sort alphabetically by display title
      return intCards.filter(function(c) { return !!c.el; }).sort(function(a, b) {
        var ta = (a.title || '').toLowerCase(), tb = (b.title || '').toLowerCase();
        return ta < tb ? -1 : ta > tb ? 1 : 0;
      }).map(function(c) { return c.el; });
    })(),
    // (Per-integration API logs are rendered inside each card now — no global log section.)
    // Backfill date range modal (rendered at top level, outside cards)
    backfillModal && React.createElement(Modal, { open: true, title: 'Backfill Historical Orders', onClose: function() { setBackfillModal(null); }, width: 420 },
      React.createElement('div', { style: { fontSize: 13, color: C.textMuted, marginBottom: 16, lineHeight: 1.5 } },
        'Import orders from Zentail in 1-day batches. Completed days are saved \u2014 if interrupted, just run again to resume where you left off.'),
      backfillModal.progress && React.createElement('div', { style: { marginBottom: 16, padding: 12, borderRadius: 8, background: C.bgAlt, border: '1px solid ' + C.border } },
        React.createElement('div', { style: { fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 } }, 'Previous Progress'),
        React.createElement('div', { style: { fontSize: 12, color: C.textMuted, lineHeight: 1.6 } },
          backfillModal.progress.completedDays + ' days completed',
          backfillModal.progress.earliestDate ? ' (' + backfillModal.progress.earliestDate + ' \u2014 ' + backfillModal.progress.latestDate + ')' : '',
          React.createElement('br'),
          backfillModal.progress.totalCreated + ' orders created, ' + backfillModal.progress.totalUpdated + ' updated',
          backfillModal.progress.gapCount > 0 ? React.createElement('span', { style: { color: C.warning } }, React.createElement('br'), backfillModal.progress.gapCount + ' gap days remaining') : null
        )
      ),
      React.createElement('div', { style: { display: 'flex', gap: 12, marginBottom: 16 } },
        React.createElement('div', { style: { flex: 1 } },
          React.createElement('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, display: 'block', marginBottom: 4 } }, 'Start Date'),
          React.createElement('input', { type: 'date', value: backfillModal.startDate,
            style: { width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid ' + C.border, background: C.bgAlt, color: '#111', fontSize: 13 },
            onChange: function(e) { setBackfillModal(Object.assign({}, backfillModal, { startDate: e.target.value })); } })
        ),
        React.createElement('div', { style: { flex: 1 } },
          React.createElement('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, display: 'block', marginBottom: 4 } }, 'End Date'),
          React.createElement('input', { type: 'date', value: backfillModal.endDate,
            style: { width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid ' + C.border, background: C.bgAlt, color: '#111', fontSize: 13 },
            onChange: function(e) { setBackfillModal(Object.assign({}, backfillModal, { endDate: e.target.value })); } })
        )
      ),
      React.createElement('div', { style: { fontSize: 11, color: C.textDim, marginBottom: 16 } },
        (function() {
          var days = Math.ceil((new Date(backfillModal.endDate) - new Date(backfillModal.startDate)) / (1000 * 60 * 60 * 24));
          if (days <= 0) return 'Invalid range';
          var alreadyDone = backfillModal.progress ? backfillModal.progress.completedDays : 0;
          return days + ' total days' + (alreadyDone > 0 ? ' (' + alreadyDone + ' already done, ~' + Math.max(0, days - alreadyDone) + ' remaining)' : '');
        })()),
      React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } },
        React.createElement(Btn, { size: 'sm', onClick: function() { setBackfillModal(null); } }, 'Cancel'),
        React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: async function() {
          var startStr = backfillModal.startDate;
          var endStr = backfillModal.endDate;
          setBackfillModal(null);
          setZentailSyncing('backfill'); setZentailSyncResult(null);
          try {
            await api('/integrations/zentail/sync-orders', { method: 'POST', body: JSON.stringify({ backfill: true, startDate: startStr, endDate: endStr }) });
            var done = false;
            while (!done) {
              await new Promise(function(r) { setTimeout(r, 3000); });
              var st = await api('/integrations/zentail/sync-orders/status');
              if (st.progress) {
                var msg = 'Backfilling... ' + st.progress.fetched + ' fetched, ' + st.progress.created + ' new, ' + st.progress.updated + ' updated';
                if (st.progress.phase) msg += ' \u2014 ' + st.progress.phase;
                if (st.progress.completedDays != null && st.progress.totalDays) {
                  var pct = Math.round((st.progress.completedDays / st.progress.totalDays) * 100);
                  msg += ' \u2014 ' + pct + '% complete';
                }
                setZentailSyncResult({ success: true, message: msg });
              }
              if (!st.running) {
                done = true;
                if (st.lastResult && st.lastResult.error) { setZentailSyncResult({ success: false, message: st.lastResult.error }); }
                else if (st.lastResult) { setZentailSyncResult({ success: true, message: 'Backfill: ' + st.lastResult.created + ' new, ' + st.lastResult.updated + ' updated (' + st.lastResult.total + ' total orders)' }); }
                setZentailLastSync(new Date().toISOString());
              }
            }
          } catch (e) { setZentailSyncResult({ success: false, message: e.message }); }
          setZentailSyncing(null);
        } }, 'Start Backfill'))
    ),
    // Add New Integration modal
    addNewModal && React.createElement(Modal, { open: true, title: 'Add New Integration', onClose: function() { setAddNewModal(null); }, width: 400 },
      React.createElement('div', { style: { fontSize: 13, color: C.textMuted, marginBottom: 16, lineHeight: 1.5 } },
        'Select an integration type to add. Marketplace integrations (Amazon, Walmart) support multiple accounts for different marketplaces.'),
      React.createElement('div', { style: { marginBottom: 16 } },
        React.createElement('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, display: 'block', marginBottom: 4 } }, 'Integration Type'),
        React.createElement('select', {
          value: addNewModal.channel || '',
          onChange: function(e) { setAddNewModal({ channel: e.target.value, marketplace: '' }); },
          style: { width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid ' + C.border, background: C.surface, color: C.text, fontSize: 13, fontFamily: 'inherit' }
        },
          React.createElement('option', { value: '' }, 'Select integration...'),
          (intTypes || []).filter(function(t) { return t.canAdd; }).sort(function(a, b) { return a.name.localeCompare(b.name); }).map(function(t) {
            return React.createElement('option', { key: t.channel, value: t.channel }, t.name);
          })
        )
      ),
      addNewModal.channel && (function() {
        var typ = (intTypes || []).find(function(t) { return t.channel === addNewModal.channel; });
        if (typ && typ.multiStore) {
          return React.createElement('div', { style: { marginBottom: 16 } },
            React.createElement('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, display: 'block', marginBottom: 4 } }, 'Store Name'),
            React.createElement('input', {
              value: addNewModal.label || '',
              placeholder: 'e.g. Atharva Brands',
              onChange: function(e) { setAddNewModal(Object.assign({}, addNewModal, { label: e.target.value })); },
              style: { width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid ' + C.border, background: C.surface, color: C.text, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }
            })
          );
        }
        if (!typ || !typ.isMarketplace) return null;
        var availMkts = (typ.marketplaces || []).filter(function(m) { return (typ.usedMarketplaces || []).indexOf(m) === -1; });
        return React.createElement('div', { style: { marginBottom: 16 } },
          React.createElement('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, display: 'block', marginBottom: 4 } }, 'Marketplace'),
          React.createElement('select', {
            value: addNewModal.marketplace || '',
            onChange: function(e) { setAddNewModal(Object.assign({}, addNewModal, { marketplace: e.target.value })); },
            style: { width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid ' + C.border, background: C.surface, color: C.text, fontSize: 13, fontFamily: 'inherit' }
          },
            React.createElement('option', { value: '' }, 'Select marketplace...'),
            availMkts.map(function(m) {
              return React.createElement('option', { key: m, value: m }, m);
            })
          )
        );
      })(),
      React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } },
        React.createElement(Btn, { size: 'sm', onClick: function() { setAddNewModal(null); } }, 'Cancel'),
        React.createElement(Btn, { variant: 'primary', size: 'sm', disabled: !addNewModal.channel || ((function() {
          var typ = (intTypes || []).find(function(t) { return t.channel === addNewModal.channel; });
          if (typ && typ.multiStore) return !(addNewModal.label || '').trim();
          return typ && typ.isMarketplace && !addNewModal.marketplace;
        })()), onClick: async function() {
          try {
            var body = { channel: addNewModal.channel };
            var typ = (intTypes || []).find(function(t) { return t.channel === addNewModal.channel; });
            if (typ && typ.isMarketplace) body.marketplace = addNewModal.marketplace;
            if (typ && typ.multiStore) body.label = (addNewModal.label || '').trim();
            await api('/integrations/meta/create', { method: 'POST', body: JSON.stringify(body) });
            var ch = addNewModal.channel;
            setAddNewModal(null);
            // Force-show the new card and auto-expand it
            setExistingChannels(function(prev) { var n = Object.assign({}, prev); n[ch] = true; return n; });
            toggleInt(ch);
            refreshIntegrations();
            showToast('Integration added');
          } catch (e) { showToast(e.message, 'error'); }
        } }, 'Create')
      )
    )
  );
}

// ─── ZENTAIL ATTRIBUTE MAPPINGS PAGE ───
var FIELD_LABELS = {
  title: 'Title', sku: 'SKU', description: 'Description', brand: 'Brand',
  barcode: 'Barcode/UPC', asin: 'ASIN', fnsku: 'FNSKU', fnetid: 'FnetID',
  vendor_title: 'Vendor Title', vendor_sku: 'Vendor SKU', vendor_name: 'Vendor',
  unit_cost: 'Unit Cost', case_qty: 'Case Pack Qty', max_po_qty: 'Max Units / PO',
  length: 'Case Length', width: 'Case Width', height: 'Case Height', weight: 'Case Weight',
  package_length: 'Package Length', package_width: 'Package Width',
  package_height: 'Package Height', package_weight: 'Package Weight',
  purchase_order_lead_time_days: 'PO Lead Time (Days)',
  bullet_point_1: 'Bullet Point 1', bullet_point_2: 'Bullet Point 2',
  bullet_point_3: 'Bullet Point 3', bullet_point_4: 'Bullet Point 4',
  bullet_point_5: 'Bullet Point 5', bullet_point_6: 'Bullet Point 6',
  bullet_point_7: 'Bullet Point 7', bullet_point_8: 'Bullet Point 8',
  alias_of: 'Alias Of',
  kit_parent: 'Kit Parent',
  kit_qty: 'Kit Quantity'
};
function fieldLabel(col) {
  if (FIELD_LABELS[col]) return FIELD_LABELS[col] + ' (' + col + ')';
  return col.replace(/_/g, ' ').replace(/\b\w/g, function(c) { return c.toUpperCase(); }) + ' (' + col + ')';
}
function ZentailMappingsPage({ showToast, onNavigate }) {
  var [mappings, setMappings] = useState([]);
  var [productFields, setProductFields] = useState([]);
  var [catalogAttrs, setCatalogAttrs] = useState([]);
  var [search, setSearch] = useState('');
  var [loading, setLoading] = useState(true);
  var [saving, setSaving] = useState(false);
  var [filter, setFilter] = useState('all'); // 'all', 'mapped', 'unmapped'
  var [page, setPage] = useState(1);
  var [pageSize, setPageSize] = useState(50);
  var [pendingChanges, setPendingChanges] = useState({}); // attrName -> newFieldValue

  useEffect(function() {
    Promise.all([
      api('/integrations/zentail/field-mappings'),
      api('/integrations/zentail/product-fields'),
      api('/integrations/zentail/attributes')
    ]).then(function(results) {
      setMappings(results[0] || []);
      setProductFields(results[1] || []);
      setCatalogAttrs(results[2] || []);
      setLoading(false);
    }).catch(function() { setLoading(false); });
  }, []);

  var mappingMap = {};
  var visibilityMap = {};
  mappings.forEach(function(m) { mappingMap[m.zentail_attr] = m.product_field; visibilityMap[m.zentail_attr] = m.visible_in_editor !== false; });

  async function toggleVisibility(attrName) {
    var newVal = !visibilityMap[attrName];
    try {
      await api('/integrations/zentail/field-mappings/' + encodeURIComponent(attrName) + '/visibility', { method: 'PUT', body: JSON.stringify({ visible: newVal }) });
      setMappings(mappings.map(function(m) { return m.zentail_attr === attrName ? Object.assign({}, m, { visible_in_editor: newVal }) : m; }));
    } catch (e) { showToast(e.message, 'error'); }
  }

  var relevantAttrs = catalogAttrs;

  var filteredAttrs = relevantAttrs
    .filter(function(a) {
      if (filter === 'mapped') return !!mappingMap[a.attr_name];
      if (filter === 'unmapped') return !mappingMap[a.attr_name];
      if (filter === 'unmapped-data') return !mappingMap[a.attr_name] && a.has_data;
      return true;
    })
    .filter(function(a) { return !search || a.attr_name.toLowerCase().includes(search.toLowerCase()); });

  var mappedCount = mappings.length;
  var totalCount = relevantAttrs.length;
  var unmappedWithDataCount = relevantAttrs.filter(function(a) { return !mappingMap[a.attr_name] && a.has_data; }).length;
  var hasChanges = Object.keys(pendingChanges).length > 0;

  async function handleMappingChange(attrName, val) {
    if (val === '__custom__') {
      var custom = await appPrompt('Enter new field name (lowercase, underscores only):');
      if (!custom) return;
      val = custom.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
      if (!val) { showToast('Invalid field name', 'error'); return; }
    }
    var saved = mappingMap[attrName] || '';
    if (val === saved) {
      // Reverted to saved value — remove from pending
      var next = Object.assign({}, pendingChanges);
      delete next[attrName];
      setPendingChanges(next);
    } else {
      setPendingChanges(Object.assign({}, pendingChanges, { [attrName]: val }));
    }
  }

  async function saveAllChanges() {
    setSaving(true);
    try {
      var entries = Object.entries(pendingChanges);
      for (var i = 0; i < entries.length; i++) {
        var attrName = entries[i][0], val = entries[i][1];
        if (!val) {
          await api('/integrations/zentail/field-mappings/' + encodeURIComponent(attrName), { method: 'DELETE' });
        } else {
          await api('/integrations/zentail/field-mappings', { method: 'POST', body: JSON.stringify({ zentail_attr: attrName, product_field: val }) });
        }
      }
      var newMappings = await api('/integrations/zentail/field-mappings');
      setMappings(newMappings || []);
      var newFields = await api('/integrations/zentail/product-fields');
      setProductFields(newFields || []);
      setPendingChanges({});
      showToast(entries.length + ' mapping' + (entries.length === 1 ? '' : 's') + ' saved');
    } catch (e) { showToast(e.message, 'error'); }
    setSaving(false);
  }

  var skipFields = ['id', 'created_at', 'updated_at', 'is_active'];

  return React.createElement('div', { style: { padding: 24, maxWidth: 1100, margin: '0 auto' } },
    // Header
    React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 } },
      React.createElement('button', { onClick: async function() { if (hasChanges && !(await appConfirm('Discard unsaved changes?', { danger: true, okLabel: 'Discard' }))) return; onNavigate('settings-integrations'); },
        style: { background: 'none', border: 'none', color: C.textMuted, cursor: 'pointer', padding: 4 } },
        React.createElement(LIcon, { name: 'arrow-left', size: 20 })),
      React.createElement('div', { style: { flex: 1 } },
        React.createElement('div', { style: { fontSize: 18, fontWeight: 600, color: C.text } }, 'Zentail Attribute Mappings'),
        React.createElement('div', { style: { fontSize: 13, color: C.textMuted } },
          mappedCount + ' mapped, ' + (totalCount - mappedCount) + ' unmapped of ' + totalCount + ' total attributes')
      ),
      hasChanges && React.createElement('div', { style: { display: 'flex', gap: 8, alignItems: 'center' } },
        React.createElement('span', { style: { fontSize: 12, color: '#FF9900', fontWeight: 500 } }, Object.keys(pendingChanges).length + ' unsaved change' + (Object.keys(pendingChanges).length === 1 ? '' : 's')),
        React.createElement(Btn, { size: 'sm', variant: 'ghost', onClick: function() { setPendingChanges({}); } }, 'Cancel'),
        React.createElement(Btn, { size: 'sm', onClick: saveAllChanges, disabled: saving },
          saving ? [React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'ic' }), ' Saving...'] : [React.createElement(LIcon, { name: 'check', size: 14, key: 'ic' }), ' Save'])
      )
    ),
    // Controls
    React.createElement('div', { style: { display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' } },
      React.createElement('div', { style: { flex: 1 } },
        React.createElement(Inp, { placeholder: 'Search attributes...', value: search, onChange: function(e) { setSearch(e.target.value); setPage(1); } })
      ),
      React.createElement('select', {
        value: filter, onChange: function(e) { setFilter(e.target.value); setPage(1); },
        style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 13 }
      },
        React.createElement('option', { value: 'all' }, 'All (' + totalCount + ')'),
        React.createElement('option', { value: 'mapped' }, 'Mapped (' + mappedCount + ')'),
        React.createElement('option', { value: 'unmapped' }, 'Unmapped (' + (totalCount - mappedCount) + ')'),
        React.createElement('option', { value: 'unmapped-data' }, 'Unmapped \u2014 With Data (' + unmappedWithDataCount + ')')
      )
    ),
    // Table
    loading ? React.createElement('div', { style: { textAlign: 'center', padding: 60, color: C.textMuted } }, 'Loading attributes...') :
    React.createElement(Card, { style: { padding: 0, overflow: 'hidden' } },
      // Rows per page bar (DataTable style)
      React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 14px', borderBottom: '1px solid ' + C.border + '44', flexWrap: 'wrap', gap: 8 } },
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 6 } },
          React.createElement('span', { style: { fontSize: 11, color: C.textMuted } }, 'Show'),
          [50, 100, 200, 400, 800].map(function(n) {
            return React.createElement('button', {
              key: n, onClick: function() { setPageSize(n); setPage(1); },
              style: { padding: '2px 8px', fontSize: 11, borderRadius: 4, border: 'none', cursor: 'pointer',
                fontFamily: 'inherit', fontWeight: pageSize === n ? 600 : 400,
                background: pageSize === n ? C.accent : 'transparent',
                color: pageSize === n ? '#fff' : C.textMuted }
            }, String(n));
          }),
          React.createElement('span', { style: { fontSize: 11, color: C.textMuted } }, 'rows')
        )
      ),
      React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr 60px auto', gap: 0, background: C.surface, borderBottom: '1px solid ' + C.border, padding: '10px 20px', fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' } },
        React.createElement('div', null, 'Zentail Attribute'),
        React.createElement('div', null, 'Mapped To Product Field'),
        React.createElement('div', { style: { textAlign: 'center' } }, 'Visible'),
        React.createElement('div', null, 'Status')
      ),
      React.createElement('div', { style: { maxHeight: 'calc(calc(100vh * var(--invzoom, 1)) - 320px)', overflowY: 'auto' } },
        filteredAttrs.length === 0
          ? React.createElement('div', { style: { padding: 40, textAlign: 'center', color: C.textMuted, fontSize: 13 } }, 'No attributes match your search.')
          : filteredAttrs.slice((page - 1) * pageSize, page * pageSize).map(function(attr) {
              var saved = mappingMap[attr.attr_name] || '';
              var isPending = pendingChanges.hasOwnProperty(attr.attr_name);
              var displayVal = isPending ? pendingChanges[attr.attr_name] : saved;
              var effectiveMapped = displayVal ? true : false;
              var isVisible = visibilityMap[attr.attr_name] !== false;
              return React.createElement('div', { key: attr.attr_name, style: { display: 'grid', gridTemplateColumns: '1fr 1fr 60px auto', gap: 0, borderBottom: '1px solid ' + C.border + '44', padding: '8px 20px', alignItems: 'center', fontSize: 13, background: isPending ? C.accent + '08' : 'transparent' } },
                React.createElement('div', { style: { fontFamily: 'inherit', fontSize: 12, color: C.text } }, attr.attr_name),
                React.createElement('select', {
                  value: displayVal,
                  onChange: function(e) { handleMappingChange(attr.attr_name, e.target.value); },
                  style: { background: C.bg, border: '1px solid ' + (isPending ? C.accent : C.border), color: C.text, padding: '6px 10px', borderRadius: 4, fontSize: 12, width: '100%', maxWidth: 280 }
                },
                  React.createElement('option', { value: '' }, '\u2014 Unmapped \u2014'),
                  productFields.filter(function(f) { return !skipFields.includes(f.column_name); }).sort(function(a, b) { return fieldLabel(a.column_name).localeCompare(fieldLabel(b.column_name)); }).map(function(f) {
                    return React.createElement('option', { key: f.column_name, value: f.column_name }, fieldLabel(f.column_name));
                  }),
                  React.createElement('option', { value: '__custom__' }, '+ Create new field...')
                ),
                React.createElement('div', { style: { textAlign: 'center' } },
                  effectiveMapped ? React.createElement('button', {
                    onClick: function() { toggleVisibility(attr.attr_name); },
                    title: isVisible ? 'Visible on Edit Product — click to hide' : 'Hidden from Edit Product — click to show',
                    style: { background: 'none', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 4, opacity: isVisible ? 1 : 0.4 }
                  }, React.createElement(LIcon, { name: isVisible ? 'eye' : 'eye-off', size: 15, color: isVisible ? C.success : C.textMuted }))
                  : null
                ),
                React.createElement('div', { style: { minWidth: 80, textAlign: 'right' } },
                  isPending ? React.createElement('span', { style: { fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 8, background: C.accent + '18', color: C.accent, textTransform: 'uppercase', letterSpacing: '0.03em' } }, 'Changed')
                  : effectiveMapped ? React.createElement('span', { style: { fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 8, background: C.success + '18', color: C.success, textTransform: 'uppercase', letterSpacing: '0.03em' } }, 'Mapped')
                  : React.createElement('span', { style: { fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 8, background: '#FF990018', color: '#FF9900', textTransform: 'uppercase', letterSpacing: '0.03em' } }, 'Unmapped')
                )
              );
            })
      ),
      // Pagination footer (DataTable style)
      (function() {
        var tp = Math.max(1, Math.ceil(filteredAttrs.length / pageSize));
        var atEnd = page >= tp;
        var atStart = page <= 1;
        var btnStyle = function(disabled) { return { padding: '4px 8px', fontSize: 11, borderRadius: 4, border: '1px solid ' + C.border, background: 'transparent', color: disabled ? C.textDim : C.textMuted, cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit' }; };
        return filteredAttrs.length > pageSize && React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderTop: '1px solid ' + C.border + '44', fontSize: 12, color: C.textMuted } },
          React.createElement('span', null, 'Showing ' + ((page - 1) * pageSize + 1) + '\u2013' + Math.min(page * pageSize, filteredAttrs.length) + ' of ' + filteredAttrs.length.toLocaleString()),
          React.createElement('div', { style: { display: 'flex', gap: 4 } },
            React.createElement('button', { onClick: function() { setPage(1); }, disabled: atStart, style: btnStyle(atStart) }, '\u00ab First'),
            React.createElement('button', { onClick: function() { setPage(page - 1); }, disabled: atStart, style: btnStyle(atStart) }, '\u2039 Prev'),
            React.createElement('span', { style: { padding: '4px 10px', fontSize: 11, color: C.text, fontWeight: 500 } }, 'Page ' + page + ' of ' + tp),
            React.createElement('button', { onClick: function() { setPage(page + 1); }, disabled: atEnd, style: btnStyle(atEnd) }, 'Next \u203a'),
            React.createElement('button', { onClick: function() { setPage(tp); }, disabled: atEnd, style: btnStyle(atEnd) }, 'Last \u00bb')
          )
        );
      })()
    )
  );
}

window.DashboardPage = DashboardPage; window.VendorsPage = VendorsPage; window.WarehousesPage = WarehousesPage; window.WarehouseDetailPage = WarehouseDetailPage; window.ProductsPage = ProductsPage; window.SettingsPage = SettingsPage; window.ZentailMappingsPage = ZentailMappingsPage;

;
/* ===== app-fba.js ===== */
// ─── FBA Shipments Module ───
// FbaShipmentsPage (list), FbaDetailPage (wizard), FbaBoxPackingView (scanner)

var { createElement: h, Fragment } = React;

// ─── Confetti burst ───
var fireConfetti = window.fireConfetti = function() {
  var colors = ['#FF6B6B','#4ECDC4','#45B7D1','#FFA07A','#98D8C8','#F7DC6F','#BB8FCE','#85C1E9','#F0B27A','#82E0AA'];
  var count = 120;
  var container = document.createElement('div');
  container.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:99999;overflow:hidden';
  document.body.appendChild(container);
  for (var i = 0; i < count; i++) {
    var p = document.createElement('div');
    var sz = Math.random() * 8 + 4;
    var x = Math.random() * 100;
    var clr = colors[Math.floor(Math.random() * colors.length)];
    var drift = (Math.random() - 0.5) * 200;
    var dur = Math.random() * 1.5 + 1.5;
    var delay = Math.random() * 0.4;
    var rot = Math.random() * 720 - 360;
    p.style.cssText = 'position:absolute;top:-10px;left:' + x + '%;width:' + sz + 'px;height:' + (sz * 0.6) + 'px;background:' + clr + ';border-radius:2px;opacity:1;animation:confettiFall ' + dur + 's ease-out ' + delay + 's forwards';
    p.style.setProperty('--drift', drift + 'px');
    p.style.setProperty('--rot', rot + 'deg');
    container.appendChild(p);
  }
  if (!document.getElementById('confetti-style')) {
    var style = document.createElement('style');
    style.id = 'confetti-style';
    style.textContent = '@keyframes confettiFall{0%{transform:translateY(0) translateX(0) rotate(0);opacity:1}100%{transform:translateY(' + window.innerHeight + 'px) translateX(var(--drift)) rotate(var(--rot));opacity:0}}';
    document.head.appendChild(style);
  }
  setTimeout(function() { container.remove(); }, 3500);
};

// ─── FBA hover styles ───
(function() {
  if (!document.getElementById('fba-hover-styles')) {
    var style = document.createElement('style');
    style.id = 'fba-hover-styles';
    style.textContent =
      '[data-fba-hover="add"]>button:hover,[data-fba-hover="add"]>div:hover{background:rgba(79,110,247,0.28)!important;border-color:rgba(79,110,247,0.5)!important}' +
      '[data-fba-hover="delete"]:hover{background:rgba(248,113,113,0.15)!important;transform:scale(1.2)}' +
      '[data-fba-hover="generate"]>button:hover,[data-fba-hover="generate"]>div:hover{filter:brightness(1.15)!important;box-shadow:0 2px 8px rgba(79,110,247,0.25)}' +
      '[data-fba-hover="cancel"]>button:hover,[data-fba-hover="cancel"]>div:hover{background:rgba(248,113,113,0.25)!important;border-color:rgba(248,113,113,0.5)!important}' +
      '[data-fba-hover] button,[data-fba-hover] div,[data-fba-hover]{transition:all 0.15s ease!important}';
    document.head.appendChild(style);
  }
})();

// ─── Status colors for FBA ───
var fbaStatusColors = {
  draft: { bg: 'rgba(251,191,36,0.12)', text: '#FBBF24' },
  plan_created: { bg: 'rgba(59,130,246,0.12)', text: '#3B82F6' },
  packing_set: { bg: 'rgba(20,184,166,0.12)', text: '#14B8A6' },
  placement_confirmed: { bg: 'rgba(167,139,250,0.12)', text: '#A78BFA' },
  transport_confirmed: { bg: 'rgba(79,110,247,0.12)', text: '#4F6EF7' },
  ready_to_ship: { bg: 'rgba(52,211,153,0.12)', text: '#34D399' },
  shipped: { bg: 'rgba(99,102,241,0.12)', text: '#6366F1' },
  delivered: { bg: 'rgba(52,211,153,0.12)', text: '#34D399' },
  closed: { bg: 'rgba(139,143,163,0.12)', text: '#8B8FA3' },
  cancelled: { bg: 'rgba(248,113,113,0.12)', text: '#F87171' },
  pending: { bg: 'rgba(139,143,163,0.12)', text: '#8B8FA3' },
  packing: { bg: 'rgba(251,191,36,0.12)', text: '#FBBF24' },
  packed: { bg: 'rgba(20,184,166,0.12)', text: '#14B8A6' },
  labels_ready: { bg: 'rgba(52,211,153,0.12)', text: '#34D399' },
  working: { bg: 'rgba(79,110,247,0.12)', text: '#4F6EF7' },
  in_transit: { bg: 'rgba(96,165,250,0.12)', text: '#60A5FA' },
  checked_in: { bg: 'rgba(45,212,191,0.12)', text: '#2DD4BF' },
  receiving: { bg: 'rgba(132,204,22,0.12)', text: '#84CC16' },
};

var fbaStatusLabels = {
  plan_created: 'Plan Created',
  placement_confirmed: 'Placement Confirmed',
  working: 'Working',
  ready_to_ship: 'Ready to Ship',
  shipped: 'Shipped',
  in_transit: 'In Transit',
  delivered: 'Delivered',
  checked_in: 'Checked-In',
  receiving: 'Receiving',
  closed: 'Closed',
};
// Map any raw plan/shipment status to the Amazon-aligned display code (idempotent for display codes).
function fbaDisplayCode(status) {
  if (status === 'pending' || status === 'packing' || status === 'packing_set' || status === 'transport_confirmed') return 'working';
  if (status === 'labels_ready') return 'ready_to_ship';
  return status;
}
function FbaBadge({ status }) {
  var code = fbaDisplayCode(status);
  var sc = fbaStatusColors[code] || { bg: 'rgba(139,143,163,0.12)', text: '#8B8FA3' };
  var label = fbaStatusLabels[code] || (code || '').replace(/_/g, ' ');
  return h('span', { style: { background: sc.bg, color: sc.text, padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' } }, label);
}

// Pasted SKU + Quantity text → [{ sku, qty }] for the create wizard's Paste tab. One pair per
// line; the two are split by a tab (Excel), a comma, or spaces before a trailing number. Extra
// columns are ignored and a leading "SKU / Qty" header line is skipped.
function fbaParsePastedSkuQty(text) {
  var out = [];
  String(text || '').split(/\r?\n/).forEach(function(line) {
    if (!line.trim()) return;
    var parts;
    if (line.indexOf('\t') !== -1) parts = line.split('\t');
    else if (line.indexOf(',') !== -1) parts = line.split(',');
    else { var m = line.trim().match(/^(.*\S)\s+(\d+)$/); parts = m ? [m[1], m[2]] : [line]; }
    var sku = (parts[0] || '').trim().replace(/^"|"$/g, '').trim();
    var qty = (parts[1] || '').trim().replace(/[",]/g, '');
    if (!sku) return;
    if (!out.length && /^(sku|msku|seller sku)$/i.test(sku)) return;
    out.push({ sku: sku, qty: qty });
  });
  return out;
}
window.fbaParsePastedSkuQty = fbaParsePastedSkuQty;

// ─── FBA Shipments List Page ───
// Module-scope cache (mirrors _poListCache) so opening a shipment and coming back —
// in-app arrow or browser back — restores the status tab, page, and scroll position.
// A full page load resets it, so the module still ENTERS on the 'working' default.
var _fbaListCache = { status: null, page: 0, scroll: 0 };
function FbaShipmentsPage({ showToast, onNavigate, currentUser, createWithPO }) {
  var [plans, setPlans] = useState([]);
  var [loading, setLoading] = useState(true);
  var [statusFilter, _setStatusFilter] = useState(_fbaListCache.status != null ? _fbaListCache.status : 'working'); // default 'working' on entry (user request 2026-07-17)
  var setStatusFilter = function(s) { _setStatusFilter(s); _fbaListCache.status = s; };
  var [search, setSearch] = useState('');
  var [createOpen, setCreateOpen] = useState(false);
  var [duplicateState, setDuplicateState] = useState(null); // { planId, copyBoxes, submitting } | null
  var [eligiblePOs, setEligiblePOs] = useState([]);
  var [eligibleLoading, setEligibleLoading] = useState(false);
  var [createPOIds, setCreatePOIds] = useState([]);
  var [createType, setCreateType] = useState(''); // '' | 'SPD' | 'LTL'
  var [createPackingMode, setCreatePackingMode] = useState(''); // '' | 'individual' | 'case_packed'
  var [createStep, setCreateStep] = useState(0); // 0=POs, 1=Type, 2=Mode, 3=Ship From, 4=Review
  var [poWeights, setPoWeights] = useState({}); // { po_id: est_weight_lbs }
  var lastPoClickRef = useRef(null);
  var [createContentSrc, setCreateContentSrc] = useState('BARCODE_2D');
  var [createWhFilter, setCreateWhFilter] = useState('');
  var [createPOSearch, setCreatePOSearch] = useState('');
  var [createWhWarning, setCreateWhWarning] = useState('');
  var [creating, setCreating] = useState(false);
  var [shipFromAddrs, setShipFromAddrs] = useState([]);
  var [selectedShipFrom, setSelectedShipFrom] = useState('');
  var [newAddrForm, setNewAddrForm] = useState({ label: '', name: '', address_line1: '', city: '', state: '', postal_code: '', country_code: 'US', phone: '' });
  // Vendor address state (populated when any selected PO is vendor-direct)
  var [vendorData, setVendorData] = useState(null); // raw vendor row from /api/vendors/:id
  var [vendorEditForm, setVendorEditForm] = useState(null); // editable copy used in step 3
  var [loadingVendor, setLoadingVendor] = useState(false);
  // User-chosen FBA plan name (default auto-computed until the user types)
  var [createPlanName, setCreatePlanName] = useState('');
  var [planNameEdited, setPlanNameEdited] = useState(false);
  // Source mode: 'po' (default), 'manual' (items picked/uploaded, no PO link) or 'paste'
  // (SKU + Qty rows typed or pasted, as on Create PO). 'paste' is a second way to fill
  // manualItems — the shipment it creates is a manual one.
  var [createSourceMode, setCreateSourceMode] = useState('po');
  var [pasteRows, setPasteRows] = useState([{ sku: '', qty: '' }]); // [{sku, qty, error?}] — rows that failed the check stay, with the reason
  var [pasteResolving, setPasteResolving] = useState(false);
  var [manualMarketplace, setManualMarketplace] = useState('ATVPDKIKX0DER'); // US default
  var [manualSearch, setManualSearch] = useState('');
  var [manualSearchResults, setManualSearchResults] = useState([]);
  var [manualSearching, setManualSearching] = useState(false);
  var [manualItems, setManualItems] = useState([]); // [{product_id, sku, fnsku, asin, vendor_title, brand, quantity}]
  var [manualCsvImporting, setManualCsvImporting] = useState(false);
  var [manualCsvReport, setManualCsvReport] = useState(null); // last import {matched, unmatched, fbm_only, zero_qty}
  var manualSearchTimerRef = useRef(null);
  var manualCsvInputRef = useRef(null);
  var createFromPORef = useRef(null); // PO id when the wizard was auto-opened from PO detail — cancel returns there

  // Paged list (9-27 sweep L17): the newest FBA_PLAN_PAGE plans first, older ones on demand. A
  // failed load shows its error with Retry instead of an empty "No FBA Shipments" table.
  var [planTotal, setPlanTotal] = useState(0);
  var [loadError, setLoadError] = useState('');
  var [loadingMore, setLoadingMore] = useState(false);
  var FBA_PLAN_PAGE = 500;
  var loadPlans = useCallback(function() {
    setLoading(true);
    setLoadError('');
    api('/fba-shipments/plans?limit=' + FBA_PLAN_PAGE + '&offset=0').then(function(data) {
      var rows = Array.isArray(data) ? data : ((data && data.plans) || []);
      setPlans(rows);
      setPlanTotal(Array.isArray(data) ? rows.length : ((data && data.total) || rows.length));
    }).catch(function(e) { setLoadError(e.message || 'Could not load FBA shipments'); showToast(e.message, 'error'); }).finally(function() { setLoading(false); });
  }, []);
  var loadMorePlans = function() {
    setLoadingMore(true);
    api('/fba-shipments/plans?limit=' + FBA_PLAN_PAGE + '&offset=' + plans.length).then(function(data) {
      var rows = (data && data.plans) || [];
      setPlans(function(prev) {
        var seen = {}; prev.forEach(function(p) { seen[p.id] = true; });
        return prev.concat(rows.filter(function(p) { return !seen[p.id]; }));
      });
      if (data && data.total != null) setPlanTotal(data.total);
    }).catch(function(e) { showToast(e.message, 'error'); }).finally(function() { setLoadingMore(false); });
  };
  useEffect(function() { loadPlans(); }, [loadPlans]);

  // Auto-open create modal when navigated from PO detail with createWithPO
  useEffect(function() {
    if (!createWithPO) return;
    createFromPORef.current = createWithPO;
    Promise.all([api('/fba-shipments/eligible-pos'), api('/fba-shipments/ship-from-addresses')]).then(function(results) {
      var fbaPos = results[0]; var addrs = results[1];
      setEligiblePOs(fbaPos);
      // Fetch estimated weights for the eligible POs
      if (fbaPos.length) {
        api('/fba-shipments/po-weights', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ po_ids: fbaPos.map(function(p) { return p.id; }) }) })
          .then(function(w) { setPoWeights(w || {}); }).catch(function() { setPoWeights({}); });
      } else setPoWeights({});
      // Pre-select the PO
      var targetPO = fbaPos.find(function(po) { return po.id === createWithPO; });
      setCreatePOIds(targetPO ? [targetPO.id] : []);
      setCreateType('');
      setCreatePackingMode('');
      setCreateStep(targetPO ? 1 : 0); // skip PO-select if we already have the PO
      setShipFromAddrs(addrs);
      var def = addrs.find(function(a) { return a.is_default; }) || addrs[0];
      setSelectedShipFrom(def ? String(def.id) : 'new');
      setNewAddrForm({ label: '', name: '', address_line1: '', city: '', state: '', postal_code: '', country_code: 'US', phone: '' });
      setVendorData(null); setVendorEditForm(null); setCreatePlanName(''); setPlanNameEdited(false);
      setCreateOpen(true);
    }).catch(function(e) { showToast(e.message, 'error'); });
  }, [createWithPO]);

  // Close the create wizard without creating; if it was auto-opened from a PO
  // detail page (createWithPO), return the user to that PO instead of leaving
  // them on the FBA list.
  var closeCreate = function() {
    setCreateOpen(false);
    if (createFromPORef.current) {
      var poId = createFromPORef.current;
      createFromPORef.current = null;
      onNavigate('po-detail', poId);
    }
  };

  var openCreate = function() {
    // Open the modal immediately and reset state — eligible POs / addresses load
    // in the background so the user sees instant feedback. The PO list shows a
    // "Loading..." line until data arrives.
    createFromPORef.current = null;
    setEligiblePOs([]);
    setShipFromAddrs([]);
    setPoWeights({});
    setCreatePOIds([]);
    setCreateType('');
    setCreatePackingMode('');
    setCreateStep(0);
    setSelectedShipFrom('');
    setNewAddrForm({ label: '', name: '', address_line1: '', city: '', state: '', postal_code: '', country_code: 'US', phone: '' });
    setVendorData(null); setVendorEditForm(null); setCreatePlanName(''); setPlanNameEdited(false);
    setCreateOpen(true);
    setEligibleLoading(true);
    Promise.all([api('/fba-shipments/eligible-pos'), api('/fba-shipments/ship-from-addresses')]).then(function(results) {
      var fbaPos = results[0]; var addrs = results[1];
      setEligiblePOs(fbaPos);
      if (fbaPos.length) {
        api('/fba-shipments/po-weights', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ po_ids: fbaPos.map(function(p) { return p.id; }) }) })
          .then(function(w) { setPoWeights(w || {}); }).catch(function() { setPoWeights({}); });
      }
      setShipFromAddrs(addrs);
      var def = addrs.find(function(a) { return a.is_default; }) || addrs[0];
      setSelectedShipFrom(def ? String(def.id) : 'new');
    }).catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setEligibleLoading(false); });
  };

  var addrToSource = function(a) {
    return { name: a.name, addressLine1: a.address_line1, city: a.city, stateOrProvinceCode: a.state, postalCode: a.postal_code, countryCode: a.country_code || 'US', phoneNumber: a.phone || '' };
  };

  // Vendor-direct detection: any selected PO in submitted/confirmed status
  var selectedVendorDirectPOs = eligiblePOs.filter(function(po) {
    return createPOIds.indexOf(po.id) !== -1 && (po.status === 'submitted' || po.status === 'confirmed');
  });
  var isVendorDirectSelection = selectedVendorDirectPOs.length > 0;
  var vendorDirectVendorId = isVendorDirectSelection ? selectedVendorDirectPOs[0].vendor_id : null;

  // Fetch vendor record as soon as the selection is vendor-direct (not just on Step 3,
  // so the address is loaded and preselected before the user ever reaches Ship From).
  useEffect(function() {
    if (!createOpen || !vendorDirectVendorId) return;
    if (vendorData && vendorData.id === vendorDirectVendorId) { setSelectedShipFrom('vendor'); return; }
    setLoadingVendor(true);
    api('/vendors/' + vendorDirectVendorId)
      .then(function(v) {
        setVendorData(v);
        setVendorEditForm({
          address1: v.address1 || '', city: v.city || '', state: v.state || '',
          zip: v.zip || '', country: v.country || 'US', phone: v.phone || '',
        });
        setSelectedShipFrom('vendor');
      })
      .catch(function(e) { showToast('Could not load vendor address: ' + e.message, 'error'); })
      .finally(function() { setLoadingVendor(false); });
  }, [createOpen, vendorDirectVendorId]);

  // If the selection stops being vendor-direct while "vendor" is chosen, fall back to
  // the default saved address — a warehouse plan must never ship from the vendor.
  useEffect(function() {
    if (selectedShipFrom !== 'vendor' || isVendorDirectSelection) return;
    var def = shipFromAddrs.find(function(a) { return a.is_default; }) || shipFromAddrs[0];
    setSelectedShipFrom(def ? String(def.id) : 'new');
  }, [isVendorDirectSelection, selectedShipFrom]);

  // Required fields for a usable Amazon source address
  var VENDOR_REQUIRED = ['address1', 'city', 'state', 'zip', 'country', 'phone'];
  var VENDOR_FIELD_LABEL = { address1: 'Address line 1', city: 'City', state: 'State', zip: 'Postal code', country: 'Country', phone: 'Phone' };
  var vendorMissingFields = vendorEditForm
    ? VENDOR_REQUIRED.filter(function(f) { return !vendorEditForm[f] || !String(vendorEditForm[f]).trim(); })
    : [];

  var vendorToSource = function(v, e) {
    return {
      name: (v && v.vendor_name) || '',
      addressLine1: (e && e.address1) || (v && v.address1) || '',
      city: (e && e.city) || (v && v.city) || '',
      stateOrProvinceCode: (e && e.state) || (v && v.state) || '',
      postalCode: (e && e.zip) || (v && v.zip) || '',
      countryCode: ((e && e.country) || (v && v.country) || 'US').substring(0, 2).toUpperCase(),
      phoneNumber: (e && e.phone) || (v && v.phone) || '',
    };
  };

  // Default plan name:
  //   1 PO  → "<Vendor Name> - PO <po#> - MM-DD-YYYY"
  //   >1 PO → "MM-DD-YYYY <WAREHOUSE>" (warehouse code of first PO; uses "MULTI" if mixed)
  var defaultPlanName = function() {
    var d = new Date();
    var mdy = String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0') + '-' + d.getFullYear();
    if (createSourceMode !== 'po') {
      var mp = manualMarketplace === 'A2EUQ1WTGCTBG2' ? 'CA' : 'US';
      return ['Manual', mp, mdy].join(' - ');
    }
    if (!createPOIds.length) return '';
    var sel = eligiblePOs.filter(function(po) { return createPOIds.indexOf(po.id) !== -1; });
    if (!sel.length) return '';
    if (sel.length === 1) {
      var po = sel[0];
      var vendor = po.vendor_name || '';
      var poNum = po.po_number || po.id;
      return [vendor, 'PO ' + poNum, mdy].filter(Boolean).join(' - ');
    }
    var whCodes = Array.from(new Set(sel.map(function(p) { return (p.wh_code || p.warehouse_code || '').toUpperCase(); }).filter(Boolean)));
    var whPart = whCodes.length === 1 ? whCodes[0] : (whCodes.length > 1 ? 'MULTI' : '');
    return [mdy, whPart].filter(Boolean).join(' ');
  };

  // Keep the default plan name applied (re-computes when selection changes) until the user types.
  useEffect(function() {
    if (!createOpen) return;
    if (planNameEdited) return;
    var def = defaultPlanName();
    if (def) setCreatePlanName(def);
  }, [createOpen, createPOIds, eligiblePOs.length, planNameEdited, createSourceMode, manualMarketplace]);

  // Matched rows from the CSV import or the Paste tab → manualItems; a SKU already listed
  // gets the quantity added to it.
  var mergeManualMatched = function(matched) {
    setManualItems(function(prev) {
      var byKey = {}; prev.forEach(function(it) { byKey[it.sku.toLowerCase()] = Object.assign({}, it); });
      (matched || []).forEach(function(it) {
        var k = (it.sku || '').toLowerCase();
        if (byKey[k]) byKey[k].quantity = (byKey[k].quantity || 0) + it.quantity;
        else byKey[k] = { product_id: it.product_id, sku: it.sku, fnsku: it.fnsku, asin: it.asin, vendor_title: it.vendor_title, brand: it.brand, quantity: it.quantity };
      });
      return Object.keys(byKey).map(function(k) { return byKey[k]; });
    });
  };

  // Paste tab. A multi-cell paste (Excel rows) into any SKU box fills the grid from that row
  // down; rows above it and filled rows below it are kept.
  var updatePasteRow = function(i, key, val) {
    setPasteRows(function(prev) { var n = prev.slice(); n[i] = { sku: n[i].sku, qty: n[i].qty }; n[i][key] = val; return n; });
  };
  var handlePasteRows = function(e, i) {
    var text = e.clipboardData ? e.clipboardData.getData('text') : '';
    if (text.indexOf('\t') === -1 && text.indexOf('\n') === -1 && text.indexOf(',') === -1) return;
    var parsed = fbaParsePastedSkuQty(text);
    if (!parsed.length) return;
    e.preventDefault();
    setPasteRows(function(prev) {
      var filled = function(r) { return (r.sku || '').trim() || String(r.qty || '').trim(); };
      return prev.slice(0, i).concat(parsed, prev.slice(i + 1).filter(filled));
    });
  };
  // Check the rows on the server (the CSV import's validator). Good rows move into the item
  // list; a row that fails stays in the grid with its reason so it can be fixed and re-added.
  var addPastedRows = function() {
    var entries = pasteRows.filter(function(r) { return (r.sku || '').trim(); })
      .map(function(r) { return { sku: r.sku.trim(), qty: String(r.qty == null ? '' : r.qty).trim() }; });
    if (!entries.length) { showToast('Enter at least one SKU + Quantity', 'warning'); return; }
    var mpLabel = manualMarketplace === 'A2EUQ1WTGCTBG2' ? 'Amazon CA' : 'Amazon US';
    setPasteResolving(true);
    api('/fba-shipments/items-resolve', { method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ marketplace: manualMarketplace, rows: entries.map(function(en) { return { sku: en.sku, quantity: parseInt(en.qty, 10) || 0 }; }) }) })
      .then(function(res) {
        var reasonByRow = {}; // row_number = 1-based position in the list sent
        var mark = function(list, reason) { (list || []).forEach(function(r) { reasonByRow[r.row_number] = reason; }); };
        mark(res.unmatched, 'Unknown SKU');
        mark(res.inactive, 'Inactive in the catalog');
        mark(res.fbm_only, 'FBM-only, cannot ship to FBA');
        mark(res.wrong_marketplace, 'Not an ' + mpLabel + ' FBA item');
        mark(res.zero_qty, 'Quantity must be 1 or more');
        var left = [];
        entries.forEach(function(en, pos) { if (reasonByRow[pos + 1]) left.push({ sku: en.sku, qty: en.qty, error: reasonByRow[pos + 1] }); });
        var added = (res.matched || []).length;
        mergeManualMatched(res.matched);
        setPasteRows(left.length ? left : [{ sku: '', qty: '' }]);
        if (!added) showToast('No rows added. The reason is shown under each row.', 'error');
        else showToast(added + ' row' + (added === 1 ? '' : 's') + ' added' + (left.length ? ', ' + left.length + ' need a fix' : ''), left.length ? 'warning' : 'success');
      })
      .catch(function(err) { showToast(err.message || 'Could not check the rows', 'error'); })
      .finally(function() { setPasteResolving(false); });
  };

  var handleCreate = function() {
    var isManualMode = createSourceMode !== 'po';
    if (isManualMode) { if (!manualItems.length) return; }
    else if (!createPOIds.length) return;
    setCreating(true);
    var isVendorDirect = !isManualMode && isVendorDirectSelection;
    var planName = (createPlanName || '').trim();
    var doCreate = function(sourceAddr) {
      var payload = { shipment_type: createType, packing_mode: createPackingMode, content_info_source: createContentSrc, source_address: sourceAddr, name: planName };
      if (isManualMode) {
        payload.source_mode = 'manual';
        payload.marketplace_id = manualMarketplace;
        payload.items = manualItems.map(function(it) { return { product_id: it.product_id, sku: it.sku, quantity: it.quantity }; });
      } else {
        payload.po_ids = createPOIds;
        if (isVendorDirect) payload.ship_mode = 'vendor_direct';
      }
      api('/fba-shipments/plans', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) })
        .then(function(plan) { setCreateOpen(false); setCreating(false); onNavigate('fba-detail', plan.id); })
        .catch(function(e) { showToast(e.message, 'error'); setCreating(false); });
    };
    if (!isManualMode && selectedShipFrom === 'vendor') {
      if (!vendorData || !vendorEditForm) { showToast('Vendor address still loading', 'error'); setCreating(false); return; }
      if (vendorMissingFields.length) { showToast('Fill required vendor fields: ' + vendorMissingFields.map(function(f) { return VENDOR_FIELD_LABEL[f]; }).join(', '), 'error'); setCreating(false); return; }
      // Persist any edits back to the vendor record, then create the plan.
      var changed = VENDOR_REQUIRED.some(function(f) { return (vendorEditForm[f] || '') !== (vendorData[f] || ''); });
      var persist = changed
        ? api('/vendors/' + vendorData.id, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(Object.assign({}, vendorData, vendorEditForm)) })
            .then(function(updated) { setVendorData(updated); return updated; })
        : Promise.resolve(vendorData);
      persist
        .then(function(v) { doCreate(vendorToSource(v, vendorEditForm)); })
        .catch(function(e) { showToast('Could not save vendor address: ' + e.message, 'error'); setCreating(false); });
    } else if (selectedShipFrom === 'new') {
      if (!newAddrForm.label || !newAddrForm.address_line1) { showToast('Label and address are required', 'error'); setCreating(false); return; }
      api('/fba-shipments/ship-from-addresses', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(newAddrForm) })
        .then(function(saved) { doCreate(addrToSource(saved)); })
        .catch(function(e) { showToast(e.message, 'error'); setCreating(false); });
    } else {
      var addr = shipFromAddrs.find(function(a) { return String(a.id) === selectedShipFrom; });
      if (!addr) { showToast('Select a ship-from address', 'error'); setCreating(false); return; }
      doCreate(addrToSource(addr));
    }
  };

  var statuses = ['', 'draft', 'plan_created', 'placement_confirmed', 'working', 'ready_to_ship', 'shipped', 'in_transit', 'delivered', 'checked_in', 'receiving', 'closed', 'cancelled'];
  var statusCounts = useMemo(function() {
    var counts = {};
    plans.forEach(function(p) { var ds = p.display_status || p.status; counts[ds] = (counts[ds] || 0) + 1; });
    counts[''] = plans.filter(function(p) { return (p.display_status || p.status) !== 'cancelled'; }).length;
    return counts;
  }, [plans]);
  var filtered = statusFilter ? plans.filter(function(p) { return (p.display_status || p.status) === statusFilter; }) : plans.filter(function(p) { return (p.display_status || p.status) !== 'cancelled'; });
  if (search) {
    var q = search.toLowerCase();
    filtered = filtered.filter(function(p) {
      return (p.po_numbers || p.po_number || '').toLowerCase().indexOf(q) !== -1 ||
        (p.shipment_ids || '').toLowerCase().indexOf(q) !== -1 ||
        (p.amazon_plan_id || '').toLowerCase().indexOf(q) !== -1 ||
        (p.name || '').toLowerCase().indexOf(q) !== -1 ||
        (p.vendor_name || '').toLowerCase().indexOf(q) !== -1 ||
        (p.destinations || '').toLowerCase().indexOf(q) !== -1 ||
        (p.skus || '').toLowerCase().indexOf(q) !== -1;
    });
  }

  var exportPlanXlsx = function(planId) {
    var a = document.createElement('a');
    a.href = '/api/fba-shipments/plans/' + planId + '/export-xlsx';
    document.body.appendChild(a);
    a.click();
    setTimeout(function() { document.body.removeChild(a); }, 0);
  };

  var duplicatePlan = function(planId) {
    setDuplicateState({ planId: planId, copyBoxes: false, submitting: false });
  };

  var submitDuplicate = function() {
    if (!duplicateState || duplicateState.submitting) return;
    setDuplicateState(function(s) { return Object.assign({}, s, { submitting: true }); });
    api('/fba-shipments/plans/' + duplicateState.planId + '/duplicate', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ copy_boxes: !!duplicateState.copyBoxes, boxed_only: !!duplicateState.copyBoxes && duplicateState.boxedOnly !== false, remaining_only: !!duplicateState.remainingOnly })
    })
      .then(function(res) {
        var extra = res.boxes_copied ? ', ' + res.boxes_copied + ' boxes' : '';
        if (res.units_copied != null) extra += ', ' + res.units_copied + ' units';
        if (res.remaining_only) extra += ', remaining units only';
        if (res.boxes_on_departed) extra += ', ' + res.boxes_on_departed + ' from a shipment Amazon shows as shipped/in transit';
        showToast('Duplicated as ' + (res.new_plan_name || 'new plan') + ' (' + res.items_copied + ' items' + extra + ')', 'success');
        setDuplicateState(null);
        onNavigate('fba-detail', res.new_plan_id);
      })
      .catch(function(e) {
        showToast(e.message || 'Failed to duplicate plan', 'error');
        setDuplicateState(function(s) { return s ? Object.assign({}, s, { submitting: false }) : null; });
      });
  };

  var printPlanBOLs = function(planId) {
    api('/fba-shipments/plans/' + planId + '/bills-of-lading')
      .then(function(res) {
        var bills = (res && res.bills) || [];
        var withUrls = bills.filter(function(b) { return b.downloadUrl; });
        var errored = bills.filter(function(b) { return !b.downloadUrl; });
        if (!withUrls.length) {
          showToast(errored[0] && errored[0].error ? errored[0].error : 'No Bill of Lading available', 'error');
          return;
        }
        withUrls.forEach(function(b) { window.open(b.downloadUrl, '_blank'); });
        if (errored.length) {
          showToast(errored.length + ' shipment(s) still pending Amazon BOL', 'info');
        }
      })
      .catch(function(e) { showToast(e.message || 'Failed to fetch Bill of Lading', 'error'); });
  };

  // Split-group highlighting — link a parent plan with the sibling plans split off from it so
  // they're easy to find together. Group root = split_from_plan_id || id; a root with >1 member
  // (the origin plan + its splits) gets a shared light color.
  var _splitRoot = {};
  (plans || []).forEach(function(p) { _splitRoot[p.id] = p.split_from_plan_id || p.id; });
  var _splitCount = {};
  (plans || []).forEach(function(p) { var r = _splitRoot[p.id]; _splitCount[r] = (_splitCount[r] || 0) + 1; });
  // Group recency = newest created_at in the group, so a group sorts/clusters by its newest member.
  var _splitRecency = {};
  (plans || []).forEach(function(p) { var r = _splitRoot[p.id]; var t = p.created_at || ''; if (!_splitRecency[r] || t > _splitRecency[r]) _splitRecency[r] = t; });
  // Composite sort key: cluster a group together (shared recency prefix), origin first ('~' sorts
  // ahead of timestamps in desc), then siblings by created_at. Non-split plans sort by created_at.
  var splitSortValue = function(r) { var root = _splitRoot[r.id]; return (_splitRecency[root] || r.created_at || '') + '|' + (root === r.id ? '~' : (r.created_at || '')); };
  var SPLIT_COLORS = ['#F59E0B', '#8B5CF6', '#06B6D4', '#EC4899', '#10B981', '#6366F1', '#F472B6'];
  var _splitColor = {};
  var _sci = 0;
  Object.keys(_splitCount).forEach(function(r) { if (_splitCount[r] > 1) { _splitColor[r] = SPLIT_COLORS[_sci % SPLIT_COLORS.length]; _sci++; } });
  var splitColorOf = function(row) { return _splitColor[_splitRoot[row.id]] || null; };

  var columns = [
    { key: 'created_at', label: 'Created', sortValue: splitSortValue, render: function(r) { return r.created_at ? new Date(r.created_at).toLocaleDateString() : ''; } },
    { key: 'name', label: 'Name', render: function(r) {
        var nm = r.name ? h('span', { style: { fontWeight: 500 } }, r.name) : h('span', { style: { color: C.textMuted } }, '—');
        var gc = splitColorOf(r);
        if (!gc) return nm;
        var isOrigin = (r.split_from_plan_id || r.id) === r.id;
        var badge = h('span', { title: isOrigin ? 'This plan was split into separate plan(s)' : 'Split off from plan #' + r.split_from_plan_id, style: { fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 10, background: gc + '22', color: gc, whiteSpace: 'nowrap' } }, isOrigin ? '⑂ Split origin' : '⑂ Split #' + r.split_from_plan_id);
        return h('span', { style: { display: 'inline-flex', alignItems: 'center', gap: 6 } }, nm, badge);
      }
    },
    { key: 'po_number', label: 'PO #', defaultVisible: false, render: function(r) { return h('span', { style: { fontFamily: 'inherit', fontWeight: 600 } }, r.po_numbers || r.po_number || '—'); } },
    { key: 'shipment_ids', label: 'Shipment ID', defaultVisible: false, render: function(r) {
        if (!r.shipment_ids) return h('span', { style: { color: C.textMuted } }, '—');
        // Link each FBA shipment ID to its Seller Central shipment summary (new tab).
        var scHost = r.marketplace_id === 'A2EUQ1WTGCTBG2' ? 'sellercentral.amazon.ca'
                   : r.marketplace_id === 'A1AM78C64UM0Y8' ? 'sellercentral.amazon.com.mx'
                   : 'sellercentral.amazon.com';
        var ids = String(r.shipment_ids).split(',').map(function(s) { return s.trim(); }).filter(Boolean);
        var children = [];
        ids.forEach(function(id, i) {
          children.push(h('a', { key: 'a' + id,
            href: 'https://' + scHost + '/fba/inbound-shipment/summary/' + encodeURIComponent(id),
            target: '_blank', rel: 'noopener noreferrer',
            onClick: function(e) { e.stopPropagation(); },
            title: 'Open ' + id + ' in Seller Central',
            style: { color: C.accent, textDecoration: 'none' } },
            id, h(LIcon, { name: 'external-link', size: 10, style: { marginLeft: 2, verticalAlign: 'middle' } })));
          if (i < ids.length - 1) children.push(h('span', { key: 's' + i, style: { color: C.textMuted } }, ', '));
        });
        return h('span', { style: { fontFamily: 'inherit', display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 } }, children);
      } },
    { key: 'status', label: 'Status', render: function(r) { return h(FbaBadge, { status: r.display_status || r.status }); } },
    { key: 'marketplace_id', label: 'Marketplace', render: function(r) { var mktMap = { 'A2EUQ1WTGCTBG2': 'Amazon CA', 'ATVPDKIKX0DER': 'Amazon US', 'A1AM78C64UM0Y8': 'Amazon MX' }; var label = mktMap[r.marketplace_id] || r.marketplace_id || '—'; return h('span', null, label); } },
    { key: 'shipment_type', label: 'Type', defaultVisible: false, render: function(r) { return h('span', { style: { fontWeight: 500 } }, (r.shipment_type || 'SPD') + (r.ship_mode === 'vendor_direct' ? ' \u00b7 VD' : '')); } },
    { key: 'total_units', label: 'Units', render: function(r) { return r.total_units || 0; } },
    { key: 'packed_progress', label: 'Packed',
      sortValue: function(r) { var t = r.total_units || 0; return t > 0 ? (r.total_packed || 0) / t : 0; },
      render: function(r) {
        var total = r.total_units || 0;
        var packed = r.total_packed || 0;
        var missing = r.total_missing || 0;
        var pct = total > 0 ? Math.round(packed / total * 100) : 0;
        var pctMissing = total > 0 ? Math.min(100 - pct, Math.round(missing / total * 100)) : 0;
        // Packing is "done" when every unit is packed or declared missing — packed
        // segment goes green then, with the amber missing segment keeping the shortage visible.
        var done = total > 0 && packed + missing >= total;
        var color = pct === 0 ? C.textMuted : (done || pct >= 100 ? '#10B981' : C.accent);
        var tip = pct + '% packed' + (missing > 0 ? ' · ' + missing.toLocaleString() + ' missing' : '');
        return h('div', { title: tip, style: { width: 90, cursor: 'help' } },
          h('div', { style: { fontSize: 13, fontWeight: 500, color: C.text, marginBottom: 4, textAlign: 'center' } }, packed + ' / ' + total),
          h('div', { style: { height: 5, background: C.border, borderRadius: 3, overflow: 'hidden', display: 'flex' } },
            h('div', { style: { height: '100%', width: pct + '%', background: color, transition: 'width 0.2s' } }),
            pctMissing > 0 && h('div', { style: { height: '100%', width: pctMissing + '%', background: '#F59E0B', transition: 'width 0.2s' } })
          )
        );
      }
    },
    { key: 'received_progress', label: 'Received', defaultVisible: false,
      sortValue: function(r) { var t = r.total_units || 0; return t > 0 ? (r.total_received || 0) / t : 0; },
      render: function(r) {
        var st = r.display_status || r.status;
        var show = (r.total_received || 0) > 0 || ['checked_in', 'receiving', 'closed'].indexOf(st) >= 0;
        if (!show) return h('span', { style: { color: C.textDim } }, '—');
        var total = r.total_units || 0;
        var received = r.total_received || 0;
        var pct = total > 0 ? Math.round(received / total * 100) : 0;
        var color = pct === 0 ? C.textMuted : (pct >= 100 ? '#10B981' : '#F59E0B');
        return h('div', { title: pct + '% received at Amazon', style: { width: 90, cursor: 'help' } },
          h('div', { style: { fontSize: 13, fontWeight: 500, color: C.text, marginBottom: 4, textAlign: 'center' } }, received + ' / ' + total),
          h('div', { style: { height: 5, background: C.border, borderRadius: 3, overflow: 'hidden' } },
            h('div', { style: { height: '100%', width: pct + '%', background: color, transition: 'width 0.2s' } })
          )
        );
      }
    },
    { key: 'box_count', label: 'Boxes', defaultVisible: false, render: function(r) { return r.box_count || 0; } },
    { key: 'destinations', label: 'FC Destinations', defaultVisible: false, render: function(r) { return h('span', { style: { fontWeight: 500 } }, r.destinations || '—'); } },
    { key: 'vendor_name', label: 'Vendor', defaultVisible: false },
    { key: 'export', label: '', locked: true, stopClick: true, noFilter: true, sortKey: null,
      render: function(r) {
        var canBOL = r.shipment_type === 'LTL' && ['transport_confirmed', 'labels_ready', 'shipped'].indexOf(r.status) >= 0;
        return h('span', { style: { display: 'inline-flex', gap: 2, alignItems: 'center' } },
          h('button', {
            onClick: function(e) { e.stopPropagation(); e.preventDefault(); duplicatePlan(r.id); },
            title: 'Duplicate shipment (copy items into a new draft)',
            onMouseEnter: function(e) { e.currentTarget.style.background = 'rgba(79,110,247,0.15)'; e.currentTarget.style.color = C.accent; },
            onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = C.textMuted; },
            style: { background: 'transparent', border: 'none', padding: 4, cursor: 'pointer', color: C.textMuted, display: 'inline-flex', alignItems: 'center', borderRadius: 4, lineHeight: 0 }
          }, h(LIcon, { name: 'copy', size: 15 })),
          h('button', {
            onClick: function(e) { e.stopPropagation(); e.preventDefault(); exportPlanXlsx(r.id); },
            title: 'Download picklist (Excel)',
            onMouseEnter: function(e) { e.currentTarget.style.background = 'rgba(79,110,247,0.15)'; e.currentTarget.style.color = C.accent; },
            onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = C.textMuted; },
            style: { background: 'transparent', border: 'none', padding: 4, cursor: 'pointer', color: C.textMuted, display: 'inline-flex', alignItems: 'center', borderRadius: 4, lineHeight: 0 }
          }, h(LIcon, { name: 'download', size: 15 })),
          canBOL && h('button', {
            onClick: function(e) { e.stopPropagation(); e.preventDefault(); printPlanBOLs(r.id); },
            title: 'Print Bill of Lading (Amazon partnered LTL)',
            onMouseEnter: function(e) { e.currentTarget.style.background = 'rgba(251,191,36,0.15)'; e.currentTarget.style.color = '#FBBF24'; },
            onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = C.textMuted; },
            style: { background: 'transparent', border: 'none', padding: 4, cursor: 'pointer', color: C.textMuted, display: 'inline-flex', alignItems: 'center', borderRadius: 4, lineHeight: 0 }
          }, h(LIcon, { name: 'file-text', size: 15 }))
        );
      }
    },
  ];

  return h('div', { style: { animation: 'fadeIn 0.3s ease' } },
    // Header
    h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 } },
      h('h2', { style: { fontSize: 20, fontWeight: 700, color: C.text } }, 'FBA Shipments'),
      h(Btn, { variant: 'primary', onClick: openCreate }, h(LIcon, { name: 'plus', size: 14 }), ' New Shipment')
    ),
    // Status filter tabs
    h('div', { style: { display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' } },
      statuses.map(function(s) {
        var cnt = statusCounts[s] || 0;
        var isActive = statusFilter === s;
        var label = s ? (fbaStatusLabels[s] || s.replace(/_/g, ' ')) : 'All';
        return h('button', { key: s, onClick: function() { setStatusFilter(s); },
          className: isActive ? '' : 'filter-pill',
          style: { padding: '6px 14px', borderRadius: 6, border: '1px solid transparent', cursor: 'pointer', fontSize: 12, fontWeight: 500, fontFamily: 'inherit', textTransform: 'lowercase', transition: 'all 0.15s',
            background: isActive ? C.accent : C.surface, color: isActive ? '#fff' : C.textMuted }
        }, label + (cnt ? ' (' + cnt + ')' : ''));
      })
    ),
    // Search bar
    h('div', { style: { marginBottom: 16 } }, h(SearchInput, { type: 'text', value: search, onChange: function(e) { setSearch(e.target.value); },
      placeholder: 'Search Name, PO#, Shipment ID, SKU, Vendor, FC...',
      style: { width: '100%', background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '10px 14px', borderRadius: 8, fontSize: 13, outline: 'none' }
    })),
    // Table (a failed load shows the error, not an empty list)
    loadError && !loading
      ? h('div', { style: { padding: 24, textAlign: 'center', color: C.danger || '#EF4444', fontSize: 13 } },
          h('div', { style: { marginBottom: 10 } }, 'Could not load FBA shipments: ' + loadError),
          h(Btn, { size: 'sm', onClick: loadPlans }, h(LIcon, { name: 'refresh-cw', size: 13 }), ' Retry'))
      : h(DataTable, {
      columns: columns,
      data: filtered,
      onRowClick: function(row) { onNavigate('fba-detail', row.id); },
      rowHref: function(row) { return '/fba/' + row.id; },
      rowStyle: function(row) { var c = splitColorOf(row); return c ? { background: c + '14', boxShadow: 'inset 3px 0 0 ' + c } : null; },
      emptyTitle: loading ? 'Loading FBA Shipments…' : 'No FBA Shipments',
      emptySub: loading ? '' : 'Create your first FBA shipment from a Purchase Order',
      storageKey: 'fba-plans-list',
      defaultSort: 'created_at',
      defaultSortDir: 'desc',
      initialPage: _fbaListCache.page,
      onPageChange: function(p) { _fbaListCache.page = p; },
      initialScroll: _fbaListCache.scroll,
      onScrollChange: function(s) { _fbaListCache.scroll = s; },
    }),
    !loadError && plans.length < planTotal && h('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '12px 0', fontSize: 12, color: C.textMuted } },
      'Showing the newest ' + plans.length + ' of ' + planTotal + ' plans (search and counts cover the loaded plans)',
      h(Btn, { size: 'sm', onClick: loadMorePlans, disabled: loadingMore }, loadingMore ? 'Loading…' : 'Load Older Plans')),
    // Create Modal
    h(Modal, { open: createOpen, onClose: closeCreate, title: 'New FBA Shipment', width: 900, maxHeight: 'calc(95vh * var(--invzoom, 1))' },
      (function() {
        var isPasteMode = createSourceMode === 'paste';
        var isManualMode = createSourceMode !== 'po'; // Manual Items and Paste both build a manual (no PO) shipment
        var stepLabels = [isManualMode ? 'Items' : 'Purchase Orders', 'Shipment Type', 'Packing Mode', 'Ship From', 'Review'];
        var shipFromValid = !!selectedShipFrom && (selectedShipFrom !== 'vendor' || (vendorEditForm && vendorMissingFields.length === 0));
        var step0Valid = isManualMode ? manualItems.length > 0 : createPOIds.length > 0;
        var stepValid = [step0Valid, !!createType, !!createPackingMode, shipFromValid, true];
        var canGoNext = stepValid[createStep];
        return h('div', { style: { display: 'flex', flexDirection: 'column', gap: 14, height: 'calc(72vh * var(--invzoom, 1))' } },
          // ─── Stepper ───
          h('div', { style: { display: 'flex', alignItems: 'center', padding: '2px 0 14px' } },
            stepLabels.map(function(lbl, idx) {
              var isActive = idx === createStep;
              var isComplete = idx < createStep;
              var color = isComplete ? C.success : (isActive ? C.accent : C.textDim);
              var canClick = idx < createStep || (idx === createStep + 1 && stepValid[createStep]);
              return h(React.Fragment, { key: idx },
                idx > 0 && h('div', { style: { flex: 1, height: 2, background: idx <= createStep ? C.success : C.border, minWidth: 12, margin: '0 6px' } }),
                h('div', {
                  onClick: function() { if (canClick) setCreateStep(idx); },
                  style: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, cursor: canClick ? 'pointer' : 'default', minWidth: 60, opacity: idx > createStep + 1 ? 0.4 : 1 }
                },
                  h('div', { style: { width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, border: '2px solid ' + color, background: isComplete ? C.success : (isActive ? C.accent : 'transparent'), color: (isComplete || isActive) ? '#fff' : C.textDim, transition: 'all 0.15s' } },
                    isComplete ? h(LIcon, { name: 'check', size: 13, color: '#fff' }) : String(idx + 1)
                  ),
                  h('span', { style: { fontSize: 10, fontWeight: isActive ? 600 : 400, color: color, whiteSpace: 'nowrap' } }, lbl)
                )
              );
            })
          ),

          // ─── STEP 0: Items (From POs OR Manual) ───
          createStep === 0 && h(React.Fragment, null,
        // Source toggle
        h('div', { style: { display: 'flex', gap: 4, padding: 3, background: C.surface, borderRadius: 6, border: '1px solid ' + C.border, marginBottom: 6 } },
          ['po', 'manual', 'paste'].map(function(mode) {
            var label = mode === 'po' ? 'From Purchase Orders' : (mode === 'manual' ? 'Manual Items' : 'Paste SKU + Qty');
            var active = createSourceMode === mode;
            return h('button', { key: mode, type: 'button',
              onClick: function() {
                if (createSourceMode === mode) return;
                // Manual Items and Paste fill the same item list, so moving between those two
                // keeps it; crossing to or from Purchase Orders starts over.
                var keepItems = createSourceMode !== 'po' && mode !== 'po';
                setCreateSourceMode(mode); setCreatePOIds([]); setManualSearchResults([]); setManualSearch(''); setManualCsvReport(null);
                if (!keepItems) { setManualItems([]); setPasteRows([{ sku: '', qty: '' }]); }
              },
              style: { flex: 1, padding: '8px 14px', borderRadius: 4, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, background: active ? C.accent : 'transparent', color: active ? '#fff' : C.textMuted, transition: 'background 0.12s', fontFamily: 'inherit' }
            }, label);
          })
        ),
        // ─── PO MODE ───
        !isManualMode && h(React.Fragment, null,
        h('label', { style: { fontSize: 12, fontWeight: 500, color: C.textMuted } }, 'Purchase Orders (checked-in, FBA warehouses)'),
        // Warehouse filter
        (function() {
          var whCodes = [];
          eligiblePOs.forEach(function(po) {
            var code = (po.wh_code || po.warehouse_code || '').toUpperCase();
            if (code && whCodes.indexOf(code) === -1) whCodes.push(code);
          });
          whCodes.sort();
          return whCodes.length > 1 && h('select', {
            value: createWhFilter,
            onChange: function(e) { setCreateWhFilter(e.target.value); setCreatePOIds([]); setCreateWhWarning(''); },
            style: { padding: '6px 10px', borderRadius: 6, border: '1px solid ' + C.border, background: C.bg, color: C.text, fontSize: 12, marginBottom: 4 }
          },
            h('option', { value: '' }, 'All Warehouses'),
            whCodes.map(function(wh) {
              var count = eligiblePOs.filter(function(po) { return (po.wh_code || po.warehouse_code || '').toUpperCase() === wh; }).length;
              return h('option', { key: wh, value: wh }, wh + ' (' + count + ')');
            })
          );
        })(),
        h('input', {
          type: 'text', value: createPOSearch,
          onChange: function(e) { setCreatePOSearch(e.target.value); },
          placeholder: 'Search PO#, vendor, location...',
          style: { padding: '6px 10px', borderRadius: 6, border: '1px solid ' + C.border, background: C.bg, color: C.text, fontSize: 12, marginBottom: 4, width: '100%', boxSizing: 'border-box', outline: 'none' }
        }),
        h('div', { style: { flex: '1 1 auto', minHeight: 240, overflowY: 'auto', border: '1px solid ' + C.border, borderRadius: 6, padding: 4 } },
          (function() {
            var filtered = createWhFilter ? eligiblePOs.filter(function(po) { return (po.wh_code || po.warehouse_code || '').toUpperCase() === createWhFilter; }) : eligiblePOs;
            if (createPOSearch.trim()) {
              var q = createPOSearch.trim().toLowerCase();
              filtered = filtered.filter(function(po) {
                return (po.po_number || '').toLowerCase().indexOf(q) !== -1
                  || (po.vendor_name || '').toLowerCase().indexOf(q) !== -1
                  || (po.warehouse_location || '').toLowerCase().indexOf(q) !== -1
                  || (po.wh_code || po.warehouse_code || '').toLowerCase().indexOf(q) !== -1;
              });
            }
            filtered = filtered.slice().sort(function(a, b) {
              var va = (a.vendor_name || '').toLowerCase();
              var vb = (b.vendor_name || '').toLowerCase();
              if (va !== vb) return va < vb ? -1 : 1;
              return (a.po_number || '').localeCompare(b.po_number || '');
            });
            if (filtered.length === 0) {
              if (eligibleLoading) {
                return h('div', { style: { display: 'flex', alignItems: 'center', gap: 6, padding: 8, color: C.textMuted, fontSize: 12 } },
                  h(LIcon, { name: 'loader-2', size: 12, spin: true }), ' Loading eligible POs…');
              }
              var emptyMsg = createPOSearch.trim()
                ? 'No POs match "' + createPOSearch.trim() + '"' + (createWhFilter ? ' in ' + createWhFilter : '')
                : createWhFilter ? 'No POs for ' + createWhFilter : 'No eligible FBA warehouse POs found';
              return h('p', { style: { color: C.danger, fontSize: 12, padding: 8, margin: 0 } }, emptyMsg);
            }

            // Determine the warehouse lock that "Select All" would honor:
            // if user has already selected a PO, mirror that lock; otherwise lock to the
            // first non-VD PO in the filtered list so we don't end up mixing warehouses.
            var selLockedWh = '';
            var selLockedVD = false;
            var selLockedT = null; // Transparency POs ship alone as a group — no mixing with regular POs
            if (createPOIds.length > 0) {
              var firstSel = eligiblePOs.find(function(p) { return p.id === createPOIds[0]; });
              selLockedWh = firstSel ? (firstSel.wh_code || firstSel.warehouse_code || '').toUpperCase() : '';
              selLockedVD = !!(firstSel && (firstSel.status === 'submitted' || firstSel.status === 'confirmed'));
              selLockedT = firstSel ? !!firstSel.has_transparency : null;
            } else {
              var firstNonVD = filtered.find(function(p) { return !(p.status === 'submitted' || p.status === 'confirmed'); });
              if (firstNonVD) {
                selLockedWh = (firstNonVD.wh_code || firstNonVD.warehouse_code || '').toUpperCase();
                selLockedT = !!firstNonVD.has_transparency;
              }
            }
            // Vendor-Direct ships alone, so Select All only acts on non-VD POs in the locked
            // warehouse with the locked Transparency state.
            var selectableIds = selLockedVD ? [] : filtered.filter(function(p) {
              var pWh = (p.wh_code || p.warehouse_code || '').toUpperCase();
              var pVD = p.status === 'submitted' || p.status === 'confirmed';
              if (pVD) return false;
              if (selLockedWh && pWh !== selLockedWh) return false;
              if (selLockedT != null && !!p.has_transparency !== selLockedT) return false;
              return true;
            }).map(function(p) { return p.id; });
            var allSelected = selectableIds.length > 0 && selectableIds.every(function(id) { return createPOIds.indexOf(id) !== -1; });
            var someSelected = !allSelected && selectableIds.some(function(id) { return createPOIds.indexOf(id) !== -1; });

            var selectAllRow = selectableIds.length > 0 && h('div', {
              key: '__select_all',
              onClick: function() {
                setCreateWhWarning('');
                if (allSelected) {
                  setCreatePOIds(function(prev) { return prev.filter(function(id) { return selectableIds.indexOf(id) === -1; }); });
                } else {
                  setCreatePOIds(function(prev) {
                    var next = prev.slice();
                    selectableIds.forEach(function(id) { if (next.indexOf(id) === -1) next.push(id); });
                    return next;
                  });
                }
                lastPoClickRef.current = null;
              },
              style: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 4, cursor: 'pointer', background: allSelected ? 'rgba(79,110,247,0.08)' : 'transparent', borderBottom: '1px solid ' + C.border, marginBottom: 4, userSelect: 'none' }
            },
              h('div', { style: { width: 20, height: 20, minWidth: 20, borderRadius: 4, border: '2px solid ' + (allSelected || someSelected ? C.accent : C.textDim), background: allSelected ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' } },
                allSelected && h(LIcon, { name: 'check', size: 13, color: '#fff' }),
                someSelected && !allSelected && h('span', { style: { color: C.accent, fontSize: 14, fontWeight: 700, lineHeight: 1 } }, '–')
              ),
              h('span', { style: { fontSize: 13, fontWeight: 600, color: C.text } },
                (allSelected ? 'Deselect All' : 'Select All') + (selLockedWh ? ' ' + selLockedWh : '') + (selLockedT ? ' · Transparency' : '') + ' (' + selectableIds.length + ')'
              )
            );

            return [selectAllRow].concat(filtered.map(function(po) {
                  var checked = createPOIds.indexOf(po.id) !== -1;
                  var poWh = (po.wh_code || po.warehouse_code || '').toUpperCase();
                  var isThisVD = po.status === 'submitted' || po.status === 'confirmed';
                  // Determine locked warehouse + VD + Transparency state from the first selected PO
                  var lockedWh = '';
                  var firstSelVD = false;
                  var lockedT = null; // null = nothing selected; true/false = selection's Transparency state
                  if (createPOIds.length > 0) {
                    var firstSelPO = eligiblePOs.find(function(p) { return p.id === createPOIds[0]; });
                    lockedWh = firstSelPO ? (firstSelPO.wh_code || firstSelPO.warehouse_code || '').toUpperCase() : '';
                    firstSelVD = firstSelPO && (firstSelPO.status === 'submitted' || firstSelPO.status === 'confirmed');
                    lockedT = firstSelPO ? !!firstSelPO.has_transparency : null;
                  }
                  var whMismatch = !checked && lockedWh && poWh !== lockedWh;
                  // Vendor Direct rule: a single VD PO ships alone — can't mix with anything
                  var vdBlock = !checked && createPOIds.length > 0 && (isThisVD || firstSelVD);
                  // Transparency rule: Transparency POs ship in their own shipment — no mixing
                  // with regular POs (label scanning + prep printing stay per-shipment clean).
                  var tMismatch = !checked && lockedT != null && !!po.has_transparency !== lockedT;
                  var blocked = whMismatch || vdBlock || tMismatch;
                  var blockReason = whMismatch
                    ? ('Unavailable: this PO is in ' + poWh + ', but selected POs are in ' + lockedWh + '. A single FBA shipment cannot mix warehouses.')
                    : vdBlock
                      ? (isThisVD
                          ? 'Unavailable: Vendor Direct POs ship by themselves. Deselect the other POs first.'
                          : 'Unavailable: a Vendor Direct PO is already selected and must ship alone. Deselect it first.')
                      : tMismatch
                        ? (po.has_transparency
                            ? 'Unavailable: this PO has Transparency items — Transparency POs ship in their own shipment, separate from regular POs.'
                            : 'Unavailable: the selected POs have Transparency items and ship in their own shipment. Deselect them to pick regular POs.')
                        : undefined;
                  return h('div', { key: po.id,
                    title: blockReason,
                    onMouseDown: function(e) { if (e.shiftKey) e.preventDefault(); }, // avoid text selection on shift-click
                    onClick: function(e) {
                      if (blocked) {
                        setCreateWhWarning(blockReason);
                        return;
                      }
                      setCreateWhWarning('');
                      if (e.shiftKey && lastPoClickRef.current != null && !isThisVD && !firstSelVD) {
                        var lastIdx = filtered.findIndex(function(p) { return p.id === lastPoClickRef.current; });
                        var curIdx = filtered.findIndex(function(p) { return p.id === po.id; });
                        if (lastIdx !== -1 && curIdx !== -1) {
                          var lo = Math.min(lastIdx, curIdx);
                          var hi = Math.max(lastIdx, curIdx);
                          var rangeT = lockedT != null ? lockedT : !!po.has_transparency; // clicked PO defines the group
                          var rangeIds = filtered.slice(lo, hi + 1)
                            .filter(function(p) {
                              var pWh = (p.wh_code || p.warehouse_code || '').toUpperCase();
                              var pVD = p.status === 'submitted' || p.status === 'confirmed';
                              return (!lockedWh || pWh === lockedWh) && !pVD && !!p.has_transparency === rangeT;
                            })
                            .map(function(p) { return p.id; });
                          var targetState = !checked; // mirror the clicked PO's new state across the range
                          setCreatePOIds(function(prev) {
                            var next = prev.slice();
                            rangeIds.forEach(function(id) {
                              var idx = next.indexOf(id);
                              if (targetState && idx === -1) next.push(id);
                              else if (!targetState && idx !== -1) next.splice(idx, 1);
                            });
                            return next;
                          });
                        }
                      } else {
                        setCreatePOIds(function(prev) { return checked ? prev.filter(function(id) { return id !== po.id; }) : prev.concat([po.id]); });
                      }
                      lastPoClickRef.current = po.id;
                    }, style: { display: 'flex', flexDirection: 'column', gap: 6, padding: '8px 10px', borderRadius: 4, cursor: blocked ? 'not-allowed' : 'pointer', background: checked ? 'rgba(79,110,247,0.08)' : 'transparent', opacity: blocked ? 0.4 : 1, transition: 'background 0.15s', userSelect: 'none' } },
                    (function() {
                      var pillBase = { fontSize: 11, fontWeight: 600, padding: '0 9px', borderRadius: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4, height: 22, lineHeight: 1, boxSizing: 'border-box', whiteSpace: 'nowrap', fontFamily: 'inherit' };
                      return h(React.Fragment, null,
                        // Row 1: checkbox + PO# / Vendor / Warehouse on the same line
                        h('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
                          h('div', { style: { width: 20, height: 20, minWidth: 20, borderRadius: 4, border: '2px solid ' + (checked ? C.accent : C.textDim), background: checked ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' } },
                            checked && h(LIcon, { name: 'check', size: 13, color: '#fff' })
                          ),
                          h('div', { style: { flex: 1, fontSize: 14, minWidth: 0 } },
                            h('span', { style: { color: C.textMuted, marginRight: 4 } }, 'PO#:'),
                            h('span', { style: { fontWeight: 600, fontFamily: 'inherit' } }, po.po_number),
                            h('span', { style: { color: C.textMuted, marginLeft: 8 } }, (po.vendor_name || '') + ' · ' + (po.wh_code || po.warehouse_code || ''))
                          )
                        ),
                        // Row 2: pills packed left-to-right; conditional pills don't leave gaps.
                        h('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' } },
                          h('span', { style: Object.assign({}, pillBase, { background: C.accent + '18', color: C.accent }) }, (po.sku_count || 0) + ' SKU' + ((po.sku_count || 0) !== 1 ? 's' : '')),
                          h('span', { style: Object.assign({}, pillBase, { background: '#f59e0b18', color: '#f59e0b' }) }, (po.total_units || 0).toLocaleString() + ' units'),
                          po.warehouse_location && h('span', { style: Object.assign({}, pillBase, { background: '#10B98118', color: '#10B981', fontFamily: 'inherit' }) },
                            h(LIcon, { name: 'map-pin', size: 10 }), po.warehouse_location),
                          (po.status === 'submitted' || po.status === 'confirmed') && h('span', { style: Object.assign({}, pillBase, { background: '#8b5cf618', color: '#8b5cf6' }) }, 'Vendor Direct'),
                          po.has_transparency && h('span', {
                            title: 'This PO contains Amazon Transparency SKUs — every unit needs a serialized Transparency label. Transparency POs ship in their own shipment.',
                            style: Object.assign({}, pillBase, { background: 'rgba(167,139,250,0.12)', color: '#A78BFA', cursor: 'help' }) },
                            h('img', { src: '/img/transparency-logo.png', alt: '', style: { width: 12, height: 12, borderRadius: 2, display: 'block' } }), 'Transparency'),
                          po.checked_in_date && (function() {
                            var days = Math.max(0, Math.floor((Date.now() - new Date(po.checked_in_date).getTime()) / 86400000));
                            var color = days <= 3 ? '#10B981' : days <= 7 ? '#f59e0b' : '#F87171';
                            var label = days === 0 ? 'Today' : (days === 1 ? '1 day' : days + ' days');
                            var checkedInStr = new Date(po.checked_in_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                            var tooltip = 'Aging: days since PO was checked in at the warehouse (Checked in ' + checkedInStr + ')';
                            return h('span', { title: tooltip, style: Object.assign({}, pillBase, { background: color + '18', color: color, cursor: 'help' }) },
                              h(LIcon, { name: 'clock', size: 10 }), label);
                          })(),
                          (function() {
                            var w = poWeights[po.id];
                            if (w == null) return null;
                            var label = 'Est. ' + (w >= 10 ? Math.round(w) : w.toFixed(1)) + ' lbs';
                            var tooltip = 'Estimated weight (lbs): ' + w.toFixed(2) + ' · sum of package_weight × qty_ordered for this PO';
                            return h('span', { title: tooltip, style: Object.assign({}, pillBase, { background: '#64748B18', color: '#94a3b8', cursor: 'help' }) }, label);
                          })()
                        )
                      );
                    })()
                  );
                }));
          })()
        ),
        createWhWarning && h('div', { style: { fontSize: 11, color: '#F87171', display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px', background: 'rgba(248,113,113,0.1)', borderRadius: 6 } },
          h(LIcon, { name: 'alert-triangle', size: 12 }), createWhWarning
        ),
        createPOIds.length > 0 && (function() {
          var selPOs = eligiblePOs.filter(function(po) { return createPOIds.indexOf(po.id) !== -1; });
          var totSkus = selPOs.reduce(function(s, po) { return s + (po.sku_count || 0); }, 0);
          var totUnits = selPOs.reduce(function(s, po) { return s + (po.total_units || 0); }, 0);
          var totWeight = selPOs.reduce(function(s, po) { return s + (poWeights[po.id] || 0); }, 0);
          var weightStr = totWeight > 0 ? ', ~' + (totWeight >= 10 ? Math.round(totWeight) : totWeight.toFixed(1)) + ' lbs' : '';
          var hasVendorDirect = selPOs.some(function(po) { return po.status === 'submitted' || po.status === 'confirmed'; });
          return h('div', null,
            h('div', { style: { fontSize: 11, color: C.accent, fontWeight: 500 } },
              createPOIds.length + ' PO' + (createPOIds.length !== 1 ? 's' : '') + ' selected — ' + totSkus + ' SKU' + (totSkus !== 1 ? 's' : '') + ', ' + totUnits.toLocaleString() + ' units' + weightStr + (createPOIds.length > 1 ? ' (items will be combined)' : '')
            ),
            hasVendorDirect && h('div', { style: { fontSize: 11, color: '#8b5cf6', fontWeight: 500, marginTop: 4, padding: '4px 8px', background: '#8b5cf610', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 4 } },
              h(LIcon, { name: 'truck', size: 12 }), ' Vendor Direct — ships from vendor to Amazon. Ship-from address will use the vendor\u2019s address.'
            )
          );
        })()
        ), // end PO mode fragment

        // ─── MANUAL MODE ───
        isManualMode && h(React.Fragment, null,
          h('div', { style: { display: 'flex', gap: 8, alignItems: 'center' } },
            h('label', { style: { fontSize: 12, fontWeight: 500, color: C.textMuted } }, 'Destination Marketplace'),
            h('select', { value: manualMarketplace,
              onChange: function(e) {
                var v = e.target.value;
                if (v === manualMarketplace) return;
                setManualMarketplace(v);
                // Clear any selection / search results since they were scoped to the old marketplace.
                setManualItems([]); setManualSearchResults([]); setManualSearch(''); setManualCsvReport(null);
                // Pasted rows stay, but their reasons were for the old marketplace.
                setPasteRows(function(prev) { return prev.map(function(r) { return { sku: r.sku, qty: r.qty }; }); });
              },
              style: { padding: '6px 10px', borderRadius: 6, border: '1px solid ' + C.border, background: C.bg, color: C.text, fontSize: 12 } },
              h('option', { value: 'ATVPDKIKX0DER' }, 'Amazon US'),
              h('option', { value: 'A2EUQ1WTGCTBG2' }, 'Amazon CA')
            )
          ),
          // ─── PASTE TAB: SKU / Qty rows, as on Create PO ───
          isPasteMode && h('div', { style: { border: '1px solid ' + C.border, borderRadius: 6, padding: 10 } },
            h('div', { style: { fontSize: 11, color: C.textMuted, marginBottom: 8 } }, 'Enter SKU + Qty pairs, or paste rows from Excel into a SKU box. Add Items checks them and moves them into the list below.'),
            h('div', { style: { display: 'grid', gridTemplateColumns: '2fr 1fr 32px', gap: 8, marginBottom: 6 } },
              h('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', padding: '0 4px' } }, 'SKU'),
              h('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', padding: '0 4px' } }, 'Quantity'),
              h('div')
            ),
            h('div', { style: { maxHeight: 200, overflowY: 'auto' } },
              pasteRows.map(function(row, i) {
                var cell = { background: C.bg, border: '1px solid ' + (row.error ? '#F87171' : C.border), color: C.text, padding: '6px 10px', borderRadius: 6, fontSize: 12, fontFamily: 'inherit', outline: 'none', minWidth: 0 };
                return h('div', { key: i, style: { marginBottom: 6 } },
                  h('div', { style: { display: 'grid', gridTemplateColumns: '2fr 1fr 32px', gap: 8 } },
                    h('input', { value: row.sku, placeholder: 'Enter SKU', style: cell,
                      onChange: function(e) { updatePasteRow(i, 'sku', e.target.value); },
                      onPaste: function(e) { handlePasteRows(e, i); } }),
                    h('input', { value: row.qty, placeholder: 'Qty', type: 'number', min: '1', style: cell,
                      onChange: function(e) { updatePasteRow(i, 'qty', e.target.value); } }),
                    h('button', { type: 'button', title: 'Remove row',
                      onClick: function() { setPasteRows(function(prev) { return prev.length > 1 ? prev.filter(function(_, idx) { return idx !== i; }) : [{ sku: '', qty: '' }]; }); },
                      style: { background: 'none', border: 'none', color: C.textDim, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' } }, h(LIcon, { name: 'x', size: 14 }))
                  ),
                  row.error && h('div', { style: { fontSize: 11, color: '#F87171', padding: '2px 4px 0' } }, row.error)
                );
              })
            ),
            h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 } },
              h(Btn, { variant: 'ghost', size: 'sm', onClick: function() { setPasteRows(function(prev) { return prev.concat([{ sku: '', qty: '' }]); }); } }, '+ Add Row'),
              h(Btn, { variant: 'primary', size: 'sm', onClick: addPastedRows, disabled: pasteResolving },
                pasteResolving ? 'Checking…' : h(React.Fragment, null, h(LIcon, { name: 'plus', size: 14 }), ' Add Items'))
            )
          ),
          !isPasteMode && h('div', { style: { display: 'flex', gap: 8, alignItems: 'center' } },
            h('input', { type: 'text', value: manualSearch,
              onChange: function(e) {
                var v = e.target.value; setManualSearch(v);
                if (manualSearchTimerRef.current) clearTimeout(manualSearchTimerRef.current);
                if (v.trim().length < 2) { setManualSearchResults([]); return; }
                manualSearchTimerRef.current = setTimeout(function() {
                  setManualSearching(true);
                  api('/fba-shipments/items-search?q=' + encodeURIComponent(v.trim()) + '&marketplace=' + encodeURIComponent(manualMarketplace))
                    .then(function(res) { setManualSearchResults(res.items || []); })
                    .catch(function() { setManualSearchResults([]); })
                    .finally(function() { setManualSearching(false); });
                }, 250);
              },
              placeholder: 'Search catalog by SKU, ASIN, or title…',
              style: { flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid ' + C.border, background: C.bg, color: C.text, fontSize: 12, outline: 'none' } }),
            h('a', { href: '/api/fba-shipments/items-csv-template', download: 'fba_manual_items_template.csv',
              style: { fontSize: 11, padding: '6px 10px', borderRadius: 6, border: '1px solid ' + C.border, color: C.accent, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 } },
              h(LIcon, { name: 'download', size: 12 }), ' Template'),
            h('input', { type: 'file', accept: '.csv,text/csv', ref: manualCsvInputRef, style: { display: 'none' },
              onChange: function(e) {
                var file = e.target.files && e.target.files[0]; if (!file) return;
                var form = new FormData(); form.append('file', file); form.append('marketplace', manualMarketplace);
                setManualCsvImporting(true); setManualCsvReport(null);
                fetch('/api/fba-shipments/items-csv-import', { method: 'POST', credentials: 'include', body: form })
                  .then(function(r) { return r.json().then(function(j) { return { ok: r.ok, body: j }; }); })
                  .then(function(res) {
                    if (!res.ok) { showToast(res.body.error || 'CSV import failed', 'error'); return; }
                    setManualCsvReport(res.body);
                    mergeManualMatched(res.body.matched);
                    var msg = (res.body.matched || []).length + ' matched';
                    var skipped = (res.body.unmatched || []).length + (res.body.inactive || []).length + (res.body.fbm_only || []).length + (res.body.wrong_marketplace || []).length + (res.body.zero_qty || []).length;
                    if (skipped) msg += ', ' + skipped + ' skipped';
                    showToast(msg, 'success');
                  })
                  .catch(function(err) { showToast(err.message || 'CSV import failed', 'error'); })
                  .finally(function() { setManualCsvImporting(false); e.target.value = ''; });
              } }),
            h('button', { type: 'button', onClick: function() { if (manualCsvInputRef.current) manualCsvInputRef.current.click(); }, disabled: manualCsvImporting,
              style: { fontSize: 11, padding: '6px 10px', borderRadius: 6, border: '1px solid ' + C.border, background: 'transparent', color: C.accent, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: 'inherit' } },
              h(LIcon, { name: manualCsvImporting ? 'loader-2' : 'upload', size: 12, spin: manualCsvImporting }), ' Upload CSV')
          ),
          !isPasteMode && manualCsvReport && (function() {
            var sections = [];
            if (manualCsvReport.unmatched && manualCsvReport.unmatched.length) sections.push({ color: '#F87171', label: 'Unknown SKU', rows: manualCsvReport.unmatched });
            if (manualCsvReport.inactive && manualCsvReport.inactive.length) sections.push({ color: '#F87171', label: 'Inactive (skipped)', rows: manualCsvReport.inactive });
            if (manualCsvReport.fbm_only && manualCsvReport.fbm_only.length) sections.push({ color: '#F59E0B', label: 'FBM-only (skipped)', rows: manualCsvReport.fbm_only });
            if (manualCsvReport.wrong_marketplace && manualCsvReport.wrong_marketplace.length) {
              var mpLabel = manualMarketplace === 'A2EUQ1WTGCTBG2' ? 'Amazon CA' : 'Amazon US';
              sections.push({ color: '#F59E0B', label: 'Wrong Marketplace for ' + mpLabel, rows: manualCsvReport.wrong_marketplace });
            }
            if (manualCsvReport.zero_qty && manualCsvReport.zero_qty.length) sections.push({ color: '#94a3b8', label: 'Zero Quantity', rows: manualCsvReport.zero_qty });
            if (!sections.length) return null;
            return h('div', { style: { fontSize: 11, padding: '8px 10px', borderRadius: 6, background: C.surface, border: '1px solid ' + C.border } },
              sections.map(function(s) {
                return h('div', { key: s.label, style: { marginBottom: 4, color: s.color } },
                  h('span', { style: { fontWeight: 600 } }, s.label + ' (' + s.rows.length + '):'),
                  ' ' + s.rows.slice(0, 8).map(function(r) { return r.sku; }).join(', ') + (s.rows.length > 8 ? ', …' : '')
                );
              })
            );
          })(),
          !isPasteMode && manualSearch.trim().length >= 2 && h('div', { style: { maxHeight: 200, overflowY: 'auto', border: '1px solid ' + C.border, borderRadius: 6 } },
            manualSearching && h('div', { style: { padding: 8, fontSize: 11, color: C.textMuted, display: 'flex', alignItems: 'center', gap: 6 } },
              h(LIcon, { name: 'loader-2', size: 12, spin: true }), ' Searching…'),
            !manualSearching && manualSearchResults.length === 0 && h('div', { style: { padding: 8, fontSize: 11, color: C.textMuted } }, 'No matches'),
            !manualSearching && manualSearchResults.map(function(p) {
              var alreadyAdded = manualItems.some(function(it) { return it.product_id === p.id; });
              return h('div', { key: p.id,
                onClick: function() {
                  if (alreadyAdded) return;
                  setManualItems(function(prev) { return prev.concat([{ product_id: p.id, sku: p.sku, fnsku: p.fnsku, asin: p.asin, vendor_title: p.vendor_title, brand: p.brand, quantity: 1 }]); });
                },
                style: { padding: '6px 10px', borderTop: '1px solid ' + C.border, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: alreadyAdded ? 'default' : 'pointer', opacity: alreadyAdded ? 0.45 : 1, fontSize: 12 } },
                h('div', null,
                  h('div', { style: { fontWeight: 500, color: C.text, fontFamily: 'inherit', fontSize: 11 } }, p.sku),
                  h('div', { style: { color: C.textMuted, fontSize: 10 } }, (p.vendor_title || '').slice(0, 80) + ((p.vendor_title || '').length > 80 ? '…' : ''))
                ),
                h('span', { style: { fontSize: 10, color: alreadyAdded ? C.success : C.accent, fontWeight: 600 } }, alreadyAdded ? '✓ Added' : '+ Add')
              );
            })
          ),
          h('div', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted } },
            manualItems.length + ' item' + (manualItems.length === 1 ? '' : 's') + ' selected' +
              (manualItems.length ? ' — ' + manualItems.reduce(function(s, it) { return s + (parseInt(it.quantity) || 0); }, 0).toLocaleString() + ' total units' : '')
          ),
          manualItems.length > 0 && h('div', { style: { maxHeight: 300, overflowY: 'auto', border: '1px solid ' + C.border, borderRadius: 6 } },
            h('table', { style: { width: '100%', fontSize: 11, borderCollapse: 'collapse' } },
              h('thead', null, h('tr', { style: { background: C.surface, color: C.textMuted, textAlign: 'left' } },
                h('th', { style: { padding: '6px 8px' } }, 'SKU'),
                h('th', { style: { padding: '6px 8px' } }, 'FNSKU'),
                h('th', { style: { padding: '6px 8px' } }, 'Title'),
                h('th', { style: { padding: '6px 8px', width: 80, textAlign: 'right' } }, 'Qty'),
                h('th', { style: { padding: '6px 8px', width: 44 } }, '')
              )),
              h('tbody', null, manualItems.map(function(it, idx) {
                return h('tr', { key: it.product_id, style: { borderTop: '1px solid ' + C.border } },
                  h('td', { style: { padding: '4px 8px', fontFamily: 'inherit', color: C.text, whiteSpace: 'nowrap' } }, it.sku),
                  h('td', { style: { padding: '4px 8px', fontFamily: 'inherit', color: it.fnsku ? C.text : C.textDim, whiteSpace: 'nowrap' } }, it.fnsku || '—'),
                  h('td', { style: { padding: '4px 8px', color: C.textMuted } }, (it.vendor_title || '').slice(0, 60) + ((it.vendor_title || '').length > 60 ? '…' : '')),
                  h('td', { style: { padding: '4px 8px', textAlign: 'right' } },
                    h('input', { type: 'number', min: '1', value: it.quantity,
                      onChange: function(e) {
                        var v = parseInt(e.target.value) || 0;
                        setManualItems(function(prev) { var n = prev.slice(); n[idx] = Object.assign({}, n[idx], { quantity: v }); return n; });
                      },
                      style: { width: 70, padding: '3px 6px', fontSize: 11, border: '1px solid ' + C.border, borderRadius: 4, background: C.bg, color: C.text, textAlign: 'right' } })
                  ),
                  h('td', { style: { padding: '4px 8px', textAlign: 'center' } },
                    h('button', { type: 'button', title: 'Remove', 'aria-label': 'Remove ' + it.sku, 'data-fba-hover': 'delete',
                      onClick: function() { setManualItems(function(prev) { return prev.filter(function(_, i) { return i !== idx; }); }); },
                      style: { width: 28, height: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', borderRadius: 6, color: '#F87171', cursor: 'pointer', padding: 0, fontFamily: 'inherit' } },
                      h(LIcon, { name: 'x', size: 18, color: '#F87171' }))
                  )
                );
              }))
            )
          )
        )
          ), // end createStep === 0 fragment

          // ─── STEP 1: Shipment Type ───
          createStep === 1 && (function() {
            var totW = createPOIds.reduce(function(s, id) { return s + (poWeights[id] || 0); }, 0);
            // "Recommended" pill always shows on the suggested option, but the
            // green border/background highlight only appears before the user picks
            // (so their chosen option stays visually dominant afterwards).
            var recommended = totW > 150 ? 'LTL' : (totW > 0 ? 'SPD' : null);
            return h(React.Fragment, null,
              h('label', { style: { fontSize: 13, fontWeight: 500, color: C.textMuted } }, 'How will this shipment go to Amazon?'),
              h('div', { style: { display: 'flex', gap: 10 } },
                [{ key: 'SPD', label: 'Small Parcel (SPD)', desc: 'Boxes ship via UPS/FedEx/USPS directly to Amazon FCs' },
                 { key: 'LTL', label: 'LTL / Pallet', desc: 'Palletized freight shipped via an LTL carrier' }].map(function(t) {
                  var sel = createType === t.key;
                  var isRec = recommended === t.key;
                  return h('button', { key: t.key, type: 'button', onClick: function() { setCreateType(t.key); },
                    onMouseEnter: function(e) { if (!sel) { e.currentTarget.style.borderColor = C.accent; e.currentTarget.style.background = C.accent + '08'; } },
                    onMouseLeave: function(e) { if (!sel) { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = 'transparent'; } },
                    style: { flex: 1, padding: '14px 16px', borderRadius: 8, border: '2px solid ' + (sel ? C.accent : C.border), background: sel ? C.accent + '12' : 'transparent', color: sel ? C.accent : C.text, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', textAlign: 'left', position: 'relative', transition: 'border-color 0.15s, background 0.15s' } },
                    h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 } },
                      h('span', { style: { fontWeight: 600, fontSize: 14 } }, t.label),
                      isRec && h('span', { style: { fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: '#10B98118', color: '#10B981', letterSpacing: '0.03em' } }, '✓ Recommended')
                    ),
                    h('div', { style: { fontSize: 11, color: C.textMuted } }, t.desc)
                  );
                })
              ),
              totW > 0 && h('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 4 } },
                'Total selected weight: ~' + (totW >= 10 ? Math.round(totW) : totW.toFixed(1)) + ' lbs' + (totW > 150 ? ' — over 150 lbs typically ships cheaper LTL.' : '')
              )
            );
          })(),

          // ─── STEP 2: Packing Mode ───
          createStep === 2 && h(React.Fragment, null,
            h('label', { style: { fontSize: 13, fontWeight: 500, color: C.textMuted } }, 'How are boxes packed?'),
            h('div', { style: { display: 'flex', gap: 10 } },
              [{ key: 'case_packed', label: 'Case Packed', desc: 'Each box contains ONE SKU at a fixed case qty' },
               { key: 'individual', label: 'Individual Units', desc: 'Mixed SKUs per box, packed individually' }].map(function(m) {
                var sel = createPackingMode === m.key;
                return h('button', { key: m.key, type: 'button', onClick: function() { setCreatePackingMode(m.key); },
                  onMouseEnter: function(e) { if (!sel) { e.currentTarget.style.borderColor = C.accent; e.currentTarget.style.background = C.accent + '08'; } },
                  onMouseLeave: function(e) { if (!sel) { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = 'transparent'; } },
                  style: { flex: 1, padding: '14px 16px', borderRadius: 8, border: '2px solid ' + (sel ? C.accent : C.border), background: sel ? C.accent + '12' : 'transparent', color: sel ? C.accent : C.text, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', textAlign: 'left', transition: 'border-color 0.15s, background 0.15s' } },
                  h('div', { style: { fontWeight: 600, fontSize: 14, marginBottom: 4 } }, m.label),
                  h('div', { style: { fontSize: 11, color: C.textMuted } }, m.desc)
                );
              })
            )
          ),

          // ─── STEP 3: Ship From Address ───
          createStep === 3 && h(React.Fragment, null,
          h('label', { style: { fontSize: 13, fontWeight: 500, color: C.textMuted, display: 'block' } }, 'Ship From Address'),
          h('select', { value: selectedShipFrom, onChange: function(e) { setSelectedShipFrom(e.target.value); },
            style: { width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid ' + C.border, background: C.bg, color: C.text, fontSize: 13, marginBottom: 8 }
          },
            !isManualMode && isVendorDirectSelection && vendorData && h('option', { value: 'vendor' }, 'Vendor: ' + (vendorData.vendor_name || '') + ' (Vendor Direct)'),
            shipFromAddrs.map(function(a) {
              return h('option', { key: a.id, value: String(a.id) }, a.label + (a.city ? ' — ' + a.city + ', ' + a.state : ''));
            }),
            h('option', { value: 'new' }, '+ Create New Address')
          ),
          // Vendor-direct address card with inline editor for missing fields
          selectedShipFrom === 'vendor' && (function() {
            if (loadingVendor) return h('div', { style: { fontSize: 12, color: C.textMuted, padding: '8px 10px' } }, 'Loading vendor address…');
            if (!vendorData || !vendorEditForm) return null;
            var setField = function(k, v) { setVendorEditForm(Object.assign({}, vendorEditForm, { ['' + k]: v })); };
            return h('div', { style: { padding: '10px 12px', background: C.surface, borderRadius: 6, lineHeight: 1.5 } },
              h('div', { style: { fontWeight: 600, color: C.text, marginBottom: 6 } }, vendorData.vendor_name),
              vendorMissingFields.length > 0 && h('div', { style: { fontSize: 11, color: '#F59E0B', padding: '6px 8px', background: 'rgba(245,158,11,0.1)', borderRadius: 4, marginBottom: 8, display: 'flex', alignItems: 'flex-start', gap: 6 } },
                h(LIcon, { name: 'alert-triangle', size: 12 }),
                h('span', null, 'Missing required: ' + vendorMissingFields.map(function(f) { return VENDOR_FIELD_LABEL[f]; }).join(', ') + '. Saving will update the vendor record.')
              ),
              h('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 } },
                h(Inp, { label: 'Address Line 1 *', value: vendorEditForm.address1, onChange: function(e) { setField('address1', e.target.value); } }),
                h(Inp, { label: 'City *', value: vendorEditForm.city, onChange: function(e) { setField('city', e.target.value); } }),
                h(StateSelect, { label: 'State *', country: vendorEditForm.country || 'US', value: vendorEditForm.state, onChange: function(e) { setField('state', e.target.value); } }),
                h(Inp, { label: 'Postal Code *', value: vendorEditForm.zip, onChange: function(e) { setField('zip', e.target.value); } }),
                h(Inp, { label: 'Country *', value: vendorEditForm.country, onChange: function(e) { setField('country', e.target.value); } }),
                h(Inp, { label: 'Phone *', value: vendorEditForm.phone, onChange: function(e) { setField('phone', e.target.value); } })
              )
            );
          })(),
          selectedShipFrom !== 'new' && selectedShipFrom !== 'vendor' && (function() {
            var addr = shipFromAddrs.find(function(a) { return String(a.id) === selectedShipFrom; });
            if (!addr) return null;
            return h('div', { style: { fontSize: 12, color: C.textMuted, padding: '8px 10px', background: C.surface, borderRadius: 6, lineHeight: 1.5 } },
              addr.name && h('div', { style: { fontWeight: 500, color: C.text } }, addr.name),
              h('div', null, addr.address_line1),
              h('div', null, [addr.city, addr.state, addr.postal_code].filter(Boolean).join(', ') + (addr.country_code ? ' ' + addr.country_code : '')),
              addr.phone && h('div', null, 'Phone: ' + addr.phone)
            );
          })(),
          selectedShipFrom === 'new' && h('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 } },
            h(Inp, { label: 'Label *', value: newAddrForm.label, onChange: function(e) { setNewAddrForm(Object.assign({}, newAddrForm, { label: e.target.value })); }, placeholder: 'e.g. Main Warehouse' }),
            h(Inp, { label: 'Name', value: newAddrForm.name, onChange: function(e) { setNewAddrForm(Object.assign({}, newAddrForm, { name: e.target.value })); }, placeholder: 'Company / person name' }),
            h(Inp, { label: 'Address *', value: newAddrForm.address_line1, onChange: function(e) { setNewAddrForm(Object.assign({}, newAddrForm, { address_line1: e.target.value })); } }),
            h(Inp, { label: 'City', value: newAddrForm.city, onChange: function(e) { setNewAddrForm(Object.assign({}, newAddrForm, { city: e.target.value })); } }),
            h(StateSelect, { label: 'State', country: newAddrForm.country_code || 'US', value: newAddrForm.state, onChange: function(e) { setNewAddrForm(Object.assign({}, newAddrForm, { state: e.target.value })); } }),
            h(Inp, { label: 'ZIP', value: newAddrForm.postal_code, onChange: function(e) { setNewAddrForm(Object.assign({}, newAddrForm, { postal_code: e.target.value })); } }),
            h(Inp, { label: 'Country', value: newAddrForm.country_code, onChange: function(e) { setNewAddrForm(Object.assign({}, newAddrForm, { country_code: e.target.value })); } }),
            h(Inp, { label: 'Phone', value: newAddrForm.phone, onChange: function(e) { setNewAddrForm(Object.assign({}, newAddrForm, { phone: e.target.value })); } })
          )
          ),

          // ─── STEP 4: Review ───
          createStep === 4 && (function() {
            var selPOs = eligiblePOs.filter(function(po) { return createPOIds.indexOf(po.id) !== -1; });
            var totSkus = isManualMode ? manualItems.length : selPOs.reduce(function(s, po) { return s + (po.sku_count || 0); }, 0);
            var totUnits = isManualMode
              ? manualItems.reduce(function(s, it) { return s + (parseInt(it.quantity) || 0); }, 0)
              : selPOs.reduce(function(s, po) { return s + (po.total_units || 0); }, 0);
            var addr = shipFromAddrs.find(function(a) { return String(a.id) === selectedShipFrom; });
            var row = function(lbl, val) { return h('div', { style: { display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid ' + C.border + '44', fontSize: 13 } },
              h('span', { style: { color: C.textMuted } }, lbl),
              h('span', { style: { color: C.text, fontWeight: 500, textAlign: 'right', maxWidth: '60%' } }, val)
            ); };
            var shipFromLabel;
            if (!isManualMode && selectedShipFrom === 'vendor' && vendorData) {
              shipFromLabel = 'Vendor: ' + vendorData.vendor_name + ' · ' + [vendorEditForm && vendorEditForm.city, vendorEditForm && vendorEditForm.state, vendorEditForm && vendorEditForm.zip].filter(Boolean).join(', ');
            } else if (addr) {
              shipFromLabel = addr.label + ' · ' + [addr.city, addr.state, addr.postal_code].filter(Boolean).join(', ');
            } else if (selectedShipFrom === 'new') {
              shipFromLabel = newAddrForm.label || 'New address';
            } else {
              shipFromLabel = '—';
            }
            return h('div', null,
              h('div', { style: { fontSize: 13, color: C.textMuted, marginBottom: 10 } }, 'Review and confirm. This creates the plan on Amazon.'),
              h('div', { style: { marginBottom: 12 } },
                h(Inp, {
                  label: 'Plan Name',
                  value: createPlanName,
                  onChange: function(e) { setPlanNameEdited(true); setCreatePlanName(e.target.value); },
                  placeholder: defaultPlanName(),
                }),
                h('div', { style: { fontSize: 10, color: C.textDim, marginTop: 2 } }, 'Shown in Amazon Seller Central and copied to each shipment name. Leave blank to use the default.')
              ),
              isManualMode
                ? row('Source', 'Manual (no PO) · ' + (manualMarketplace === 'A2EUQ1WTGCTBG2' ? 'Amazon CA' : 'Amazon US'))
                : row('POs', selPOs.map(function(p) { return p.po_number; }).join(', ') + ' (' + selPOs.length + ')'),
              row('SKUs / Units', totSkus + ' SKU' + (totSkus !== 1 ? 's' : '') + ' · ' + totUnits.toLocaleString() + ' units'),
              row('Shipment Type', createType === 'SPD' ? 'Small Parcel (SPD)' : 'LTL / Pallet'),
              row('Packing Mode', createPackingMode === 'case_packed' ? 'Case Packed' : 'Individual Units'),
              row('Ship From', shipFromLabel)
            );
          })(),

          // ─── Nav ───
          h('div', { style: { display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto', borderTop: '1px solid ' + C.border, paddingTop: 14 } },
            h(Btn, { onClick: function() { if (createStep === 0) { closeCreate(); } else { setCreateStep(createStep - 1); } } },
              createStep === 0 ? 'Cancel' : h(React.Fragment, null, h(LIcon, { name: 'arrow-left', size: 14 }), ' Back')
            ),
            createStep < 4
              ? h(Btn, { key: 'next-' + createStep + '-' + (stepValid[createStep] ? '1' : '0'), variant: 'primary', onClick: function() { setCreateStep(createStep + 1); }, disabled: !stepValid[createStep] },
                  'Next ', h(LIcon, { name: 'arrow-right', size: 14 }))
              : h(Btn, { variant: 'primary', onClick: handleCreate, disabled: creating || !stepValid.every(function(v) { return v; }) },
                  creating ? 'Creating...' : h(React.Fragment, null, h(LIcon, { name: 'check', size: 14 }), ' Create Shipment'))
          )
        );
      })()
    ),
    // Duplicate Shipment Modal
    h(Modal, { open: !!duplicateState, onClose: function() { if (!(duplicateState && duplicateState.submitting)) setDuplicateState(null); }, title: 'Duplicate FBA Shipment', width: 480 },
      duplicateState && (function() {
        var src = plans.find(function(p) { return p.id === duplicateState.planId; });
        var srcLabel = src ? ((src.name || ('Plan ' + src.id)) + (src.po_numbers ? ' — PO ' + src.po_numbers : '')) : ('Plan #' + duplicateState.planId);
        return h('div', null,
          h('div', { style: { fontSize: 13, color: C.text, marginBottom: 12, lineHeight: 1.5 } },
            'Create a new draft FBA shipment from ',
            h('span', { style: { fontWeight: 600 } }, srcLabel),
            '. The wizard restarts from the beginning on the duplicate.'
          ),
          h('label', { style: { display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', background: C.surface, borderRadius: 6, border: '1px solid ' + C.border, cursor: 'pointer', marginBottom: 8 } },
            h('input', { type: 'checkbox', checked: !!duplicateState.remainingOnly, disabled: duplicateState.submitting,
              onChange: function(e) { var v = e.target.checked; setDuplicateState(function(s) { return s ? Object.assign({}, s, { remainingOnly: v, copyBoxes: v ? false : s.copyBoxes }) : null; }); },
              style: { marginTop: 2, width: 15, height: 15, accentColor: C.accent, cursor: 'pointer', flexShrink: 0 } }),
            h('div', null,
              h('div', { style: { fontWeight: 600, color: C.text, fontSize: 13 } }, 'Remaining units only'),
              h('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 3, lineHeight: 1.4 } }, 'Only the units not yet packed and not marked missing, with no boxes. Use this after the packed boxes were copied to their own plan, so the rest of the job continues on a fresh plan. This copy keeps the PO link, so packing it still receives against the PO.'))
          ),
          h('label', { style: { display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', background: C.surface, borderRadius: 6, border: '1px solid ' + C.border, cursor: duplicateState.remainingOnly ? 'default' : 'pointer', marginBottom: 16, opacity: duplicateState.remainingOnly ? 0.5 : 1 } },
            h('input', { type: 'checkbox', checked: !!duplicateState.copyBoxes, disabled: duplicateState.submitting || !!duplicateState.remainingOnly,
              onChange: function(e) { var v = e.target.checked; setDuplicateState(function(s) { return s ? Object.assign({}, s, { copyBoxes: v }) : null; }); },
              style: { marginTop: 2, width: 15, height: 15, accentColor: C.accent, cursor: 'pointer', flexShrink: 0 } }),
            h('div', null,
              h('div', { style: { fontWeight: 600, color: C.text, fontSize: 13 } }, 'Also copy box contents'),
              duplicateState.copyBoxes && h('label', { style: { display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 12, color: C.text, cursor: 'pointer' }, onClick: function(e) { e.stopPropagation(); } },
                h('input', { type: 'checkbox', checked: duplicateState.boxedOnly !== false, disabled: duplicateState.submitting,
                  onChange: function(e) { var v = e.target.checked; setDuplicateState(function(s) { return s ? Object.assign({}, s, { boxedOnly: v }) : null; }); },
                  style: { width: 14, height: 14, accentColor: C.accent, cursor: 'pointer' } }),
                'Items = box contents only (units not yet boxed are left out)'),
              duplicateState.copyBoxes && h('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 4, lineHeight: 1.4 } }, 'Every box on every shipment of the plan is copied, including shipments Amazon already shows as shipped or in transit.'),
              h('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 3, lineHeight: 1.4 } }, 'Copies boxes (dimensions, weight) and the item-to-box assignments. Box-level state like 2D barcodes, sealed flag, phantom flag is reset.')
            )
          ),
          h('div', { style: { display: 'flex', gap: 8, justifyContent: 'flex-end' } },
            h(Btn, { onClick: function() { setDuplicateState(null); }, disabled: duplicateState.submitting }, 'Cancel'),
            h(Btn, { variant: 'primary', onClick: submitDuplicate, disabled: duplicateState.submitting },
              duplicateState.submitting ? [h(LIcon, { key: 'l', name: 'loader-2', size: 14, spin: true }), ' Duplicating...'] : [h(LIcon, { key: 'c', name: 'copy', size: 14 }), ' Duplicate'])
          )
        );
      })()
    )
  );
}

// ─── Work Orders download dropdown (per-PO + all, Excel or PDF) ───
// Self-contained: reads plan.pos (per-PO list from the plan endpoint) and downloads each
// PO's work order from /api/receiving/:poId/export-wo (?format=pdf for the PDF variant).
function WorkOrdersMenu({ shipment, plan, showToast, align, fileId }) {
  var [open, setOpen] = useState(false);
  var wrapRef = useRef(null);
  useEffect(function() {
    if (!open) return;
    var onDoc = function(e) { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return function() { document.removeEventListener('mousedown', onDoc); };
  }, [open]);

  var pos = (plan && plan.pos && plan.pos.length) ? plan.pos
    : (plan && plan.po_ids && plan.po_ids.length) ? plan.po_ids.map(function(id) { return { id: id, po_number: '', vendor_name: '' }; })
    : (plan && plan.po_id) ? [{ id: plan.po_id, po_number: plan.po_number || '', vendor_name: plan.vendor_name || '' }]
    : [];
  // fileId: the ZIP name for a non-FBA caller (the MELI Pack Boxes header passes its plan + ML shipment number)
  var fbaId = (fileId || ((shipment && (shipment.amazon_shipment_id || shipment.shipment_confirmation_id)) || ('Draft' + (shipment && shipment.id))) || 'Shipment').replace(/[^A-Za-z0-9_-]/g, '');

  var triggerBlob = function(blob, filename) {
    var u = URL.createObjectURL(blob);
    var a = document.createElement('a'); a.href = u; a.download = filename; document.body.appendChild(a); a.click();
    setTimeout(function() { URL.revokeObjectURL(u); document.body.removeChild(a); }, 0);
  };
  var fnFromCd = function(resp, fallback) {
    var cd = resp.headers.get('content-disposition');
    return (cd && cd.indexOf('filename=') >= 0) ? cd.split('filename=')[1].replace(/"/g, '').trim() : fallback;
  };
  var woUrl = function(poId, format) { return '/api/receiving/' + poId + '/export-wo' + (format === 'pdf' ? '?format=pdf' : ''); };

  var dlSingle = function(poId, format) {
    setOpen(false);
    fetch(woUrl(poId, format), { credentials: 'include' }).then(function(resp) {
      if (!resp.ok) throw new Error('Failed (' + resp.status + ')');
      return resp.blob().then(function(blob) { triggerBlob(blob, fnFromCd(resp, 'WorkOrder.' + (format === 'pdf' ? 'pdf' : 'xlsx'))); });
    }).catch(function(e) { showToast('Work Order download failed: ' + e.message, 'error'); });
  };
  var dlAll = function(format) {
    setOpen(false);
    var ids = pos.map(function(p) { return p.id; });
    if (!ids.length) { showToast('No POs tied to this shipment', 'error'); return; }
    if (ids.length === 1) { dlSingle(ids[0], format); return; }
    if (!window.JSZip) { showToast('ZIP library not loaded', 'error'); return; }
    showToast('Bundling ' + ids.length + ' Work Orders…');
    var zip = new window.JSZip();
    var fetched = 0;
    Promise.all(ids.map(function(id) {
      return fetch(woUrl(id, format), { credentials: 'include' }).then(function(r) {
        if (!r.ok) return;
        return r.blob().then(function(b) { zip.file(fnFromCd(r, 'WO_' + id + '.' + (format === 'pdf' ? 'pdf' : 'xlsx')), b); fetched++; });
      });
    })).then(function() {
      if (!fetched) { showToast('No Work Orders could be downloaded', 'error'); return; }
      return zip.generateAsync({ type: 'blob' }).then(function(content) {
        triggerBlob(content, fbaId + '_WorkOrders_' + (format === 'pdf' ? 'PDF' : 'XLSX') + '.zip');
        showToast(fetched + ' Work Orders bundled as ZIP', 'success');
      });
    }).catch(function(e) { showToast('Work Orders bundle failed: ' + e.message, 'error'); });
  };

  // Small inline-SVG file logos (lucide's file-spreadsheet/file-text aren't in this build).
  var fmtIcon = function(isPdf, sz) {
    if (isPdf) return h('svg', { width: sz, height: sz, viewBox: '0 0 24 24', style: { display: 'block' } },
      h('rect', { x: 3, y: 2, width: 18, height: 20, rx: 3, fill: '#D93025' }),
      h('text', { x: 12, y: 15.6, fontSize: 8, fontWeight: 'bold', fill: '#fff', textAnchor: 'middle', fontFamily: 'Arial, sans-serif' }, 'PDF'));
    return h('img', { src: '/icons/csv-icon.svg', width: sz, height: sz, alt: 'Excel', style: { display: 'block' } });
  };
  var fmtBtn = function(format, poId, isAll) {
    var isPdf = format === 'pdf';
    return h('button', {
      onClick: function(e) { e.stopPropagation(); if (isAll) dlAll(format); else dlSingle(poId, format); },
      title: (isAll ? 'Download all as ' : 'Download ') + (isPdf ? 'PDF' : 'Excel'),
      onMouseEnter: function(e) { e.currentTarget.style.background = C.bg; e.currentTarget.style.borderColor = C.border; },
      onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'transparent'; },
      style: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, padding: 4, borderRadius: 6, border: '1px solid transparent', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit' }
    }, fmtIcon(isPdf, 18));
  };
  var rowStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '7px 8px', borderRadius: 6 };

  return h('div', { ref: wrapRef, style: { position: 'relative' } },
    h('button', {
      onClick: function() { setOpen(!open); },
      title: 'Download Work Orders',
      onMouseEnter: function(e) { e.currentTarget.style.background = 'rgba(79,110,247,0.15)'; },
      onMouseLeave: function(e) { e.currentTarget.style.background = 'rgba(79,110,247,0.08)'; },
      style: { padding: '5px 10px', background: 'rgba(79,110,247,0.08)', color: C.accent, border: '1px solid rgba(79,110,247,0.3)', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: 'inherit' }
    }, h(LIcon, { name: 'download', size: 13 }), ' Work Orders', h(LIcon, { name: 'chevron-down', size: 12 })),
    open && h('div', {
      style: { position: 'absolute', top: '100%', marginTop: 4, zIndex: 1000, background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.4)', minWidth: 300, maxHeight: 380, overflowY: 'auto', padding: 6,
        left: align === 'right' ? 'auto' : 0, right: align === 'right' ? 0 : 'auto' }
    },
      pos.length > 1 && h('div', { style: Object.assign({}, rowStyle, { background: C.accent + '10', marginBottom: 4 }) },
        h('div', { style: { fontSize: 12, fontWeight: 700, color: C.text } }, 'All Work Orders (' + pos.length + ')'),
        h('div', { style: { display: 'flex', gap: 6 } }, fmtBtn('xlsx', null, true), fmtBtn('pdf', null, true))
      ),
      pos.length === 0 && h('div', { style: { padding: '10px 8px', fontSize: 12, color: C.textMuted } }, 'No POs tied to this shipment'),
      pos.map(function(p) {
        return h('div', { key: p.id, style: rowStyle,
          onMouseEnter: function(e) { e.currentTarget.style.background = C.bg; },
          onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; } },
          h('div', { style: { minWidth: 0, flex: 1 } },
            h('div', { style: { fontSize: 12, fontWeight: 600, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }, p.vendor_name || 'Vendor'),
            h('div', { style: { fontSize: 11, color: C.textMuted, fontFamily: 'inherit' } }, p.po_number ? ('PO# ' + p.po_number) : ('PO id ' + p.id))
          ),
          h('div', { style: { display: 'flex', gap: 6, flexShrink: 0 } }, fmtBtn('xlsx', p.id, false), fmtBtn('pdf', p.id, false))
        );
      })
    )
  );
}
window.WorkOrdersMenu = WorkOrdersMenu;

// ─── FBA Detail Page (Wizard) ───
// Mirror of the server's fbaReadyToShipStart clamp: Amazon only accepts future UTC
// day-boundary starts, so a picked date whose 00:00Z has already passed becomes the
// NEXT UTC midnight — after ~8 PM ET that lands 2 calendar days past the local date.
function fbaEffectiveReadyDate(dateStr) {
  if (!dateStr) return null;
  var t = Date.parse(dateStr + 'T00:00:00Z');
  if (t > Date.now() + 60000) return dateStr;
  return new Date((Math.floor(Date.now() / 86400000) + 1) * 86400000).toISOString().split('T')[0];
}
// One transport option per carrier per shipment out of Amazon's menu. Partnered LTL comes back as
// one option per business day the carrier can do, starting the day after the request, whatever the
// readyToShipWindow said (plan 692, 2026-10-20: ready 10/22 sent, options from 10/21 came back) —
// so the ready date is ours to enforce. The earliest appointment on or after readyCut (YYYY-MM-DD,
// the date the last Generate sent) wins and price breaks ties; a carrier with nothing on or after
// the cut falls back to its earliest and is flagged early. Options without an appointment (small
// parcel, own carrier) have no date to judge. With no cut, earliest wins — the 8-12 rule: several
// dates share a price, so cheapest-first used to confirm whichever date Amazon listed first.
function fbaBuildCarrierMap(allShipOpts, shipments, readyCut) {
  var map = {};
  Object.keys(allShipOpts).forEach(function(sid) {
    var shipId = parseInt(sid);
    var fc = ((shipments || []).find(function(x) { return x.id === shipId; }) || {}).destination_fc || '';
    (allShipOpts[sid] || []).forEach(function(opt) {
      var code = (opt.carrier && opt.carrier.alphaCode) || (opt.carrier && opt.carrier.name) || 'Other';
      if (!map[code]) map[code] = { name: opt.carrier && opt.carrier.name || code, solution: opt.shippingSolution, shipments: [], totalCost: 0 };
      var cost = opt.quote && opt.quote.cost ? parseFloat(opt.quote.cost.amount) : 0;
      // carrierAppointment.startTime is Amazon's pickup day for this option (a 00:00Z..23:59Z range)
      var appt = (opt.carrierAppointment && opt.carrierAppointment.startTime) || null;
      var day = appt ? String(appt).slice(0, 10) : null;
      var cand = { shipId: shipId, optId: opt.transportationOptionId || opt.id, cost: cost, appt: appt, fc: fc, early: !!(readyCut && day && day < readyCut) };
      var existing = map[code].shipments.find(function(s) { return s.shipId === shipId; });
      if (!existing) { map[code].shipments.push(cand); map[code].totalCost += cost; return; }
      if (fbaBetterPickup(cand, existing)) {
        map[code].totalCost += cost - existing.cost;
        existing.optId = cand.optId; existing.cost = cost; existing.appt = appt; existing.early = cand.early;
      }
    });
  });
  return map;
}
// cand replaces existing when it is on time and existing is early, or, in the same class, when its
// pickup is earlier, or the same day and cheaper.
function fbaBetterPickup(cand, existing) {
  if (!!cand.early !== !!existing.early) return !cand.early;
  var c = cand.appt ? new Date(cand.appt).getTime() : Infinity;
  var e = existing.appt ? new Date(existing.appt).getTime() : Infinity;
  if (c !== e) return c < e;
  return cand.cost > 0 && (existing.cost === 0 || cand.cost < existing.cost);
}
function fbaFmtReadyDay(dateStr) {
  var d = new Date(dateStr + 'T00:00:00Z');
  return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getUTCDay()] + ' ' + (d.getUTCMonth() + 1) + '/' + d.getUTCDate();
}
// Weights beside the LTL pallet rows (Anthony 2026-09-30). Expected = the boxes actually packed
// (phantom boxes are ≤39 lb placeholders for missing units that never ship, so they are left
// out). Catalog = unit weight × planned units for every item, missing ones included; the unit
// weight follows the server's rule: package_weight, else case weight / case qty.
function fbaShipmentWeights(s) {
  var out = { expected: 0, boxes: 0, catalog: 0, noWeightSkus: 0 };
  ((s && s.boxes) || []).forEach(function(b) {
    if (b.is_phantom) return;
    out.boxes += 1;
    out.expected += parseFloat(b.weight) || 0;
  });
  ((s && s.items) || []).forEach(function(i) {
    var pkg = parseFloat(i.package_weight), cw = parseFloat(i.case_weight), cq = parseFloat(i.case_qty);
    var unit = pkg > 0 ? pkg : (cw > 0 && cq > 0 ? cw / cq : 0);
    if (!unit) out.noWeightSkus += 1;
    out.catalog += unit * (parseInt(i.quantity) || 0);
  });
  return out;
}
function fbaFmtLbs(n) {
  return (Math.round((parseFloat(n) || 0) * 100) / 100).toLocaleString() + ' lbs';
}
// Mirrors server/services/fba-phantom-boxes.js for the phantom modal preview (Anthony
// 2026-10-31): missing units are split into 25×25×25 phantom boxes of at most 39 lb by
// catalog unit weight — package_weight, else case weight / case qty, else 0.5 lb assumed.
// Heaviest units first; a box closes when the next unit would push it past the cap; a unit
// heavier than the cap goes alone and is declared at the cap.
function fbaPhantomUnitLb(it) {
  var pkg = parseFloat(it && it.package_weight), cw = parseFloat(it && it.case_weight), cq = parseFloat(it && it.case_qty);
  if (pkg > 0) return { unit: pkg, known: true };
  if (cw > 0 && cq > 0) return { unit: cw / cq, known: true };
  return { unit: 0.5, known: false };
}
function fbaPhantomSplit(items) {
  var CAP = 39, EPS = 1e-9;
  var units = (items || []).map(function(it) {
    return { qty: parseInt(it.quantity_missing, 10) || 0, unit: fbaPhantomUnitLb(it).unit };
  }).filter(function(u) { return u.qty > 0; }).sort(function(a, b) { return b.unit - a.unit; });
  var boxes = [], cur = null;
  var open = function() { cur = { catalog_weight: 0, units: 0 }; boxes.push(cur); };
  units.forEach(function(u) {
    var left = u.qty;
    while (left > 0) {
      if (!cur) open();
      var fit;
      if (u.unit > CAP + EPS) { if (cur.units) open(); fit = 1; }
      else {
        fit = Math.floor((CAP - cur.catalog_weight + EPS) / u.unit);
        if (fit <= 0) { open(); fit = Math.floor((CAP + EPS) / u.unit); }
      }
      fit = Math.min(fit, left);
      cur.catalog_weight += fit * u.unit; cur.units += fit; left -= fit;
      if (cur.catalog_weight >= CAP - EPS) cur = null;
    }
  });
  return boxes.map(function(b) {
    return { units: b.units, catalog_weight: Math.round(b.catalog_weight * 100) / 100, weight: Math.min(CAP, Math.max(1, Math.ceil(b.catalog_weight * 100 - EPS) / 100)) };
  });
}

function FbaDetailPage({ planId, showToast, onNavigate, currentUser }) {
  var [plan, setPlan] = useState(null);
  var [loading, setLoading] = useState(true);
  var [actionLoading, setActionLoading] = useState('');
  // Live override for the errored/hazmat Continue gate, reported by the packing view as
  // items are received — plan.hazmat_pending_items only refreshes on loadPlan, which left
  // Continue locked until a page refresh. Cleared whenever the plan reloads.
  var [erroredCountOverride, setErroredCountOverride] = useState(null);
  useEffect(function() { setErroredCountOverride(null); }, [plan]);

  // Packing options state
  var [packingOptions, setPackingOptions] = useState([]);
  var [selectedPackingOpt, setSelectedPackingOpt] = useState('');

  // Placement options state
  var [placementOptions, setPlacementOptions] = useState([]);
  var [amazonRates, setAmazonRates] = useState(null); // { options: [...], generated_at, took_ms }
  var [loadingAmazonRates, setLoadingAmazonRates] = useState(false);
  // Elapsed-seconds ticker while Amazon partnered rates load (30-60s per option)
  var [ratesElapsed, setRatesElapsed] = useState(0);
  useEffect(function() {
    if (!loadingAmazonRates) { setRatesElapsed(0); return; }
    var t = setInterval(function() { setRatesElapsed(function(n) { return n + 1; }); }, 1000);
    return function() { clearInterval(t); };
  }, [loadingAmazonRates]);
  var [amazonRatesError, setAmazonRatesError] = useState('');
  var [selectedPlacementOpt, setSelectedPlacementOpt] = useState('');
  var [splitSel, setSplitSel] = useState({}); // { shipmentId: true } — shipments checked to split off
  var [splitModal, setSplitModal] = useState(null); // { ships } — open the SPD/LTL + packing chooser for a split
  var [splitType, setSplitType] = useState('SPD');
  var [splitPacking, setSplitPacking] = useState('individual');
  // SPD: auto-select the cheapest placement option once Amazon partnered rates load —
  // it was only highlighted green before, the user still had to click it. Mirrors the
  // bestOptionId math in the placement render (rates + placement fees, all rates known).
  useEffect(function() {
    if (!plan || plan.shipment_type !== 'SPD') return;
    if (!amazonRates || !Array.isArray(amazonRates.options) || !placementOptions.length) return;
    var amzOptMap = {};
    amazonRates.options.forEach(function(o) { amzOptMap[o.placement_option_id] = o; });
    var best = null, bestTotal = Infinity;
    placementOptions.forEach(function(opt) {
      var pid = opt.placementOptionId || opt.id;
      var amz = amzOptMap[pid];
      if (!amz || amz.unavailable_count > 0 || !(amz.partnered_total > 0)) return;
      var fees = (opt.fees || []).reduce(function(s, f) { var raw = f.value && f.value.amount != null ? f.value.amount : f.amount; return s + (parseFloat(raw) || 0); }, 0);
      var t = amz.partnered_total + fees;
      if (t > 0 && t < bestTotal) { bestTotal = t; best = pid; }
    });
    if (best) setSelectedPlacementOpt(best);
  }, [amazonRates, placementOptions]);

  // Transport options state (per shipment)
  var [transportOptions, setTransportOptions] = useState({});
  var [selectedTransportOpt, setSelectedTransportOpt] = useState({});

  // Delivery windows state (per shipment)
  var [deliveryOptions, setDeliveryOptions] = useState({});
  var [selectedDeliveryOpt, setSelectedDeliveryOpt] = useState({});

  // Pallet data state (per shipment)
  var [palletData, setPalletData] = useState({});
  // Freight details state (per shipment: { shipId: { freight_class, declared_value } })
  var [freightData, setFreightData] = useState({});

  // Ready-to-ship dates (per shipment). The picker defaults to TODAY, local (Anthony 2026-10-20;
  // it used to default to two days out). The server clamps a date whose UTC midnight has passed to
  // the next UTC midnight (fbaReadyToShipStart), so "today" reaches Amazon as tomorrow 00:00Z.
  var defaultReadyDate = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().split('T')[0];
  var [readyDates, setReadyDates] = useState({});
  // The ready date the last Generate sent (after the clamp), plan-wide: Amazon's menu ignores it as
  // a lower bound, so the carrier list enforces it (fbaBuildCarrierMap).
  var [sentReadyDate, setSentReadyDate] = useState(null);

  // Per-shipment packing state
  var [activeShipIdx, setActiveShipIdx] = useState(0);
  var [shipPackingStatus, setShipPackingStatus] = useState({});

  // ─── Amazon packing groups (SPD-individual, pre-placement) ───
  // Owned here (not in FbaBoxPackingView) so the group picker renders above the
  // shipment header, in the exact slot and style of the multi-shipment overview.
  var [packGroups, setPackGroups] = useState(null);
  var [activeGroupIdx, setActiveGroupIdx] = useState(0);
  useEffect(function() {
    var isSpdIndividual = plan && plan.shipment_type === 'SPD' && (plan.packing_mode || 'individual') === 'individual';
    if (!isSpdIndividual || plan.placement_option_id || !plan.amazon_plan_id) { setPackGroups(null); return; }
    api('/fba-shipments/plans/' + planId + '/packing-groups')
      .then(function(r) {
        var gs = r.groups && r.groups.length > 1 ? r.groups : null;
        setPackGroups(gs);
        setActiveGroupIdx(function(idx) { return gs && idx < gs.length ? idx : 0; });
      })
      .catch(function() { setPackGroups(null); });
  }, [planId, plan && plan.shipment_type, plan && plan.packing_mode, plan && plan.placement_option_id, plan && plan.amazon_plan_id]);

  // Ship-from address editing
  var [editAddress, setEditAddress] = useState(false);
  // Expiration-date editing. Opening the editor used to null the SAVED date on the local
  // plan copy so the <input> would render; that bumped the "items missing a date" count the
  // auto-default effect keys on, which immediately re-saved today+2y and closed the input —
  // a saved date could never be corrected. Now an item is "editing" via this map and the
  // saved date is left alone. expAutoAppliedRef remembers which items this session already
  // defaulted so the effect never fires twice for the same item. (Review finding, 2026-09-02.)
  var [editingExp, setEditingExp] = useState({});
  var expAutoAppliedRef = useRef({});

  // review M7 #88: LTL declared-value auto-fill used to run as a fetch + PUT INSIDE
  // renderTransportation, so every keystroke in a pallet field re-issued it and clearing the
  // field to retype was overwritten by the response. Now an effect, once per shipment per mount.
  var declaredValueRequestedRef = useRef({});
  useEffect(function() {
    if (!plan || plan.shipment_type !== 'LTL') return;
    (plan.shipments || []).forEach(function(s) {
      if (s.status === 'transport_confirmed' || s.status === 'labels_ready' || s.status === 'shipped') return;
      var fd = freightData[s.id] || {};
      var cur = fd.declared_value !== undefined ? fd.declared_value : (s.declared_value || '');
      if (cur || declaredValueRequestedRef.current[s.id]) return;
      declaredValueRequestedRef.current[s.id] = true;
      api('/fba-shipments/shipments/' + s.id + '/calc-declared-value').then(function(res) {
        if (!res || !res.value) return;
        var v = Math.min(res.value, 100000);
        setFreightData(function(prev) { var n = Object.assign({}, prev); n[s.id] = Object.assign({}, prev[s.id] || {}, { declared_value: v }); return n; });
        return api('/fba-shipments/shipments/' + s.id, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ declared_value: v }) });
      }).catch(function(e) { console.error('[silent-catch]', e); });
    });
  }, [plan]);

  // review M7 #90: the auto-transport poll lives in a ref, is cleared on unmount, capped at
  // 10 minutes, and resumes on mount when a run is in progress (status is persisted server-side
  // since M3). It used to be a bare setInterval in the click handler — unreachable after
  // navigating away, polling forever if the job died without writing done.
  var autoTransportPollRef = useRef(null);
  var stopAutoTransportPoll = function() { if (autoTransportPollRef.current) { clearInterval(autoTransportPollRef.current); autoTransportPollRef.current = null; } };
  var startAutoTransportPoll = function() {
    stopAutoTransportPoll();
    var attempts = 0;
    var iv = setInterval(function() {
      if (++attempts > 200) { stopAutoTransportPoll(); setActionLoading(''); showToast('Auto-transport is still running after 10 minutes — reload the plan to check its status', 'warning'); return; }
      api('/fba-shipments/plans/' + planId + '/auto-transport-status')
        .then(function(st) {
          if (autoTransportPollRef.current !== iv) return; // cleared meanwhile (unmount / restart)
          if (st.status === 'done') {
            stopAutoTransportPoll(); setActionLoading('');
            showToast(st.message || 'Transport confirmed', 'success');
            if (st.errors && st.errors.length) st.errors.forEach(function(e) { showToast(e, 'error'); });
            loadPlan();
          } else if (st.status === 'error') {
            stopAutoTransportPoll(); setActionLoading('');
            showToast(st.message || 'Auto-transport failed', 'error');
            loadPlan();
          }
        })
        .catch(function(e) { console.error('[silent-catch]', e); });
    }, 3000);
    autoTransportPollRef.current = iv;
  };
  useEffect(function() {
    // Resume a run started before a reload or on the other cluster worker
    api('/fba-shipments/plans/' + planId + '/auto-transport-status').then(function(st) {
      if (st && st.status === 'running') { setActionLoading('auto-transport'); startAutoTransportPoll(); }
    }).catch(function() {});
    return function() { stopAutoTransportPoll(); };
  }, [planId]);
  var [detailShipFromAddrs, setDetailShipFromAddrs] = useState([]);
  var [detailSelectedAddr, setDetailSelectedAddr] = useState('current');
  var [detailNewAddrForm, setDetailNewAddrForm] = useState({ label: '', name: '', address_line1: '', city: '', state: '', postal_code: '', country_code: 'US', phone: '' });
  var [expirationDates, setExpirationDates] = useState({});
  var [expirationItems, setExpirationItems] = useState([]); // items that need expiration dates

  var loadPlan = useCallback(function() {
    if (!planId) return;
    setLoading(true);
    api('/fba-shipments/plans/' + planId).then(function(data) { setPlan(data); }).catch(function(e) { showToast(e.message, 'error'); }).finally(function() { setLoading(false); });
  }, [planId]);
  useEffect(function() { loadPlan(); }, [loadPlan]);

  // Trigger XLSX picklist download for a shipment — auth via same-origin cookie.
  var exportShipmentXlsx = function(s) {
    var fbaId = (s.amazon_shipment_id || s.shipment_confirmation_id || ('Draft' + s.id)).replace(/[^A-Za-z0-9_-]/g, '');
    var a = document.createElement('a');
    a.href = '/api/fba-shipments/shipments/' + s.id + '/export-xlsx';
    a.download = fbaId + '_Picklist.xlsx';
    document.body.appendChild(a);
    a.click();
    setTimeout(function() { document.body.removeChild(a); }, 0);
  };

  // Download Work Order XLSX for every PO tied to this shipment's plan. Single PO
  // streams as XLSX; multiple POs are zipped client-side (JSZip mirrors the
  // pattern used in app-main's bulk WO export).
  var exportShipmentWorkOrders = async function(s, planArg) {
    var p = planArg || plan;
    var poIds = (p && p.po_ids && p.po_ids.length) ? p.po_ids.slice() : (p && p.po_id ? [p.po_id] : []);
    if (!poIds.length) { showToast('No POs tied to this shipment', 'error'); return; }
    var fbaId = (s.amazon_shipment_id || s.shipment_confirmation_id || ('Draft' + s.id)).replace(/[^A-Za-z0-9_-]/g, '');
    if (poIds.length === 1) {
      try {
        var resp = await fetch('/api/receiving/' + poIds[0] + '/export-wo', { credentials: 'include' });
        if (!resp.ok) throw new Error('Failed (' + resp.status + ')');
        var blob = await resp.blob();
        var cd = resp.headers.get('content-disposition');
        var fn = cd ? cd.split('filename=')[1].replace(/"/g, '').trim() : (fbaId + '_WorkOrder.xlsx');
        var url = URL.createObjectURL(blob);
        var a = document.createElement('a'); a.href = url; a.download = fn; document.body.appendChild(a); a.click();
        setTimeout(function() { URL.revokeObjectURL(url); document.body.removeChild(a); }, 0);
      } catch (e) { showToast('Work Order download failed: ' + e.message, 'error'); }
      return;
    }
    if (!window.JSZip) { showToast('ZIP library not loaded', 'error'); return; }
    showToast('Bundling ' + poIds.length + ' Work Orders…');
    try {
      var zip = new window.JSZip();
      var fetched = 0;
      await Promise.all(poIds.map(async function(id) {
        var r = await fetch('/api/receiving/' + id + '/export-wo', { credentials: 'include' });
        if (!r.ok) return;
        var b = await r.blob();
        var cd2 = r.headers.get('content-disposition');
        var fn2 = cd2 ? cd2.split('filename=')[1].replace(/"/g, '').trim() : ('WO_' + id + '.xlsx');
        zip.file(fn2, b);
        fetched++;
      }));
      if (!fetched) { showToast('No Work Orders could be downloaded', 'error'); return; }
      var content = await zip.generateAsync({ type: 'blob' });
      var zurl = URL.createObjectURL(content);
      var za = document.createElement('a'); za.href = zurl; za.download = fbaId + '_WorkOrders.zip'; document.body.appendChild(za); za.click();
      setTimeout(function() { URL.revokeObjectURL(zurl); document.body.removeChild(za); }, 0);
      showToast(fetched + ' Work Orders bundled as ZIP', 'success');
    } catch (e) { showToast('Work Orders bundle failed: ' + e.message, 'error'); }
  };

  // Resubmit to Amazon: cancel + recreate plan when "Mark Missing" leaves the
  // Amazon plan expecting units that aren't in any box. LTL case-packed only — SPD folds
  // its short units in submit-packing (no recreate). See server: POST /plans/:planId/resubmit-amazon.
  var [resubmitOpen, setResubmitOpen] = useState(false);
  var [resubmitting, setResubmitting] = useState(false);
  var handleResubmit = function() {
    setResubmitting(true);
    api('/fba-shipments/plans/' + planId + '/resubmit-amazon', { method: 'POST' })
      .then(function(r) {
        var excluded = r.items_excluded || 0;
        showToast('Resubmitted to Amazon' + (excluded ? ' (excluded ' + excluded + ' missing SKU' + (excluded > 1 ? 's' : '') + ' from the plan)' : ''), 'success');
        setResubmitOpen(false);
        setManualStep(null);
        loadPlan();
      })
      .catch(function(e) { showToast('Resubmit failed: ' + e.message, 'error'); })
      .finally(function() { setResubmitting(false); });
  };

  // Phantom box modal — LTL Individual flow. Triggered when the packer clicks
  // Continue at Pack Boxes with quantity_missing > 0. Server rebuilds the phantom
  // boxes per shipment — 25x25x25, at most 39 lb each, split by catalog unit weight
  // (the old single 49 lb box tripped Amazon's 40 lb jewelry cap, 2026-10-31) —
  // and submit-packing then sends them as normal boxes.
  var [phantomOpen, setPhantomOpen] = useState(false);
  var [phantomCreating, setPhantomCreating] = useState(false);
  var handlePhantomConfirm = function() {
    setPhantomCreating(true);
    var declared = null;
    api('/fba-shipments/plans/' + planId + '/create-phantom-boxes', { method: 'POST' })
      .then(function(r) {
        declared = r;
        return api('/fba-shipments/plans/' + planId + '/submit-packing', { method: 'POST' });
      })
      .then(function() {
        var n = declared && declared.phantom_boxes_created;
        showToast((n ? n + ' phantom box' + (n === 1 ? '' : 'es') + ' (' + fbaFmtLbs(declared.total_phantom_weight) + ') declared' : 'Phantom boxes declared') + ' and packing submitted to Amazon', 'success');
        setPhantomOpen(false);
        setManualStep(null);
        loadPlan();
      })
      .catch(function(err) {
        setPhantomOpen(false);
        handleOperationError(err, 'Submit packing with phantom boxes');
      })
      .finally(function() { setPhantomCreating(false); });
  };

  // Initialize shipPackingStatus from plan data. This is purely per-shipment packing
  // completeness (every non-excluded item packed or marked missing). The plan-level
  // errored/hazmat gate (binning Amazon-rejected SKUs) is enforced separately on the
  // Continue button — it must NOT suppress a fully-packed shipment's done indicator,
  // or a small split that's 100% packed shows orange while a sibling errored item lingers.
  useEffect(function() {
    if (!plan || !plan.shipments) return;
    var status = {};
    plan.shipments.forEach(function(s) {
      var sItems = s.items || [];
      if (sItems.length === 0) { status[s.id] = true; return; }
      var allResolved = sItems.every(function(i) {
        // Excluded items are intentionally not shipped (Resubmit dropped them
        // from the Amazon plan) — treat as resolved so the user can continue.
        if (i.excluded_from_amazon) return true;
        return (i.quantity_packed || 0) + (i.quantity_missing || 0) >= (i.quantity || 0);
      });
      status[s.id] = allResolved;
    });
    setShipPackingStatus(status);
  }, [plan && plan.id, plan && plan.shipments && plan.shipments.length]);

  // Load pallet data for LTL shipments
  useEffect(function() {
    if (plan && plan.shipment_type === 'LTL' && plan.shipments) {
      plan.shipments.forEach(function(s) {
        if (!palletData[s.id]) {
          api('/fba-shipments/shipments/' + s.id + '/pallets').then(function(rows) {
            if (rows.length) {
              setPalletData(function(prev) {
                var n = Object.assign({}, prev);
                n[s.id] = rows.map(function(p) { return { pallet_count: p.pallet_count || 1, length: Math.round(p.length) || 48, width: Math.round(p.width) || 40, height: Math.round(p.height) || 60, weight: Math.round(p.weight) || 0, stackability: p.stackability || 'NON_STACKABLE' }; });
                return n;
              });
            }
          }).catch(function(e) { console.error('[silent-catch]', e); });
        }
      });
    }
  }, [plan && plan.id, plan && plan.shipments && plan.shipments.length]);

  // Determine current step from status
  // Determine step order based on shipment type + packing mode
  var isLtlIndividual = plan && plan.shipment_type === 'LTL' && (plan.packing_mode || 'individual') === 'individual';

  // Step keys define the order; render functions map to keys
  var stepKeys, steps, statusStepMap;
  if (isLtlIndividual) {
    // LTL Individual (Flow 2): skip packing options, placement first, then pack boxes + setPackingInformation
    stepKeys = ['review', 'create', 'placement', 'pack_boxes', 'transport', 'labels'];
    steps = ['Review Items', 'Create Plan', 'Placement', 'Pack Boxes', 'Transportation', 'Labels & Ship'];
    statusStepMap = { draft: 0, plan_created: 1, placement_confirmed: 2, packing: 3, packing_set: 4, transport_confirmed: 5, ready_to_ship: 5, shipped: 5, closed: 5, cancelled: 5 };
  } else {
    // SPD & LTL Case Packed (Flow 1): pack boxes + setPackingInformation, then packing options, then placement
    stepKeys = ['review', 'create', 'pack_boxes', 'packing', 'placement', 'transport', 'labels'];
    steps = ['Review Items', 'Create Plan', 'Pack Boxes', 'Packing', 'Placement', 'Transportation', 'Labels & Ship'];
    // packing_set with placement confirmed means we're at transport (step 5), without placement means we're at placement (step 4)
    var packingSetStep = (plan && plan.placement_option_id) ? 5 : 4;
    statusStepMap = { draft: 0, plan_created: 1, packing: 2, packing_set: packingSetStep, placement_confirmed: 5, transport_confirmed: 6, ready_to_ship: 6, shipped: 6, closed: 6, cancelled: 6 };
  }

  var currentStep = plan ? (statusStepMap[plan.status] != null ? statusStepMap[plan.status] : 0) : 0;
  // If plan is created on Amazon but status hasn't advanced yet, jump to the next
  // required step: Placement for LTL Individual (which runs placement before packing),
  // or Pack Boxes for SPD / LTL Case Packed (which packs before placement).
  if (plan && plan.amazon_plan_id && plan.status === 'plan_created') {
    currentStep = stepKeys.indexOf(isLtlIndividual ? 'placement' : 'pack_boxes');
  }
  var [manualStep, setManualStep] = useState(null);
  var [hazmatIssue, setHazmatIssue] = useState(null); // { skus: [], fullError: '' }
  var [hazmatBins, setHazmatBins] = useState({}); // { sku: bin }
  var [cancellingHazmat, setCancellingHazmat] = useState(false);
  var [packingReloadKey, setPackingReloadKey] = useState(0);
  // "Items Amazon rejected" panel — per-item create-plan errors surfaced for the user to resolve.
  var [cancelState, setCancelState] = useState(null); // { cancelSiblings, keepReceiving, packed, submitting } | null
  var [createErrors, setCreateErrors] = useState(null); // { items: [{ msku, title, reason, reasons[], message, amazonMessages[], po_numbers[], po_ids[] }] }
  var [activeErrorTab, setActiveErrorTab] = useState('all'); // active error-type tab in the "Items Amazon rejected" panel
  var [prepChoice, setPrepChoice] = useState('label'); // chosen prep for "Prep required" items
  // Prep options sent to Amazon via setPrepDetails. prepCategory/prepType per SP-API FBA Inbound
  // v2024-03-20. "Labeling only" / "No prep" use the NONE category (verified). Owners: AMAZON is
  // discontinued, so prep/label owner is SELLER (or NONE when nothing to do).
  var PREP_OPTIONS = [
    { value: 'label', label: 'Labeling Only (FNSKU)', prepCategory: 'NONE', prepTypes: ['ITEM_LABELING'], prepOwner: 'NONE', labelOwner: 'SELLER' },
    { value: 'noprep', label: 'No Prep Needed', prepCategory: 'NONE', prepTypes: ['ITEM_NO_PREP'], prepOwner: 'NONE', labelOwner: 'NONE' },
    { value: 'polybag', label: 'Poly Bagging + Label', prepCategory: 'BABY', prepTypes: ['ITEM_POLYBAGGING', 'ITEM_LABELING'], prepOwner: 'SELLER', labelOwner: 'SELLER' },
    { value: 'bubble', label: 'Bubble Wrap + Label', prepCategory: 'FRAGILE', prepTypes: ['ITEM_BUBBLEWRAP', 'ITEM_LABELING'], prepOwner: 'SELLER', labelOwner: 'SELLER' },
    { value: 'taping', label: 'Taping + label', prepCategory: 'FRAGILE', prepTypes: ['ITEM_TAPING', 'ITEM_LABELING'], prepOwner: 'SELLER', labelOwner: 'SELLER' },
    { value: 'boxing', label: 'Boxing + label', prepCategory: 'FRAGILE', prepTypes: ['ITEM_BOXING', 'ITEM_LABELING'], prepOwner: 'SELLER', labelOwner: 'SELLER' },
    { value: 'capseal', label: 'Cap Sealing (Liquids) + Label', prepCategory: 'LIQUID', prepTypes: ['ITEM_CAP_SEALING', 'ITEM_LABELING'], prepOwner: 'SELLER', labelOwner: 'SELLER' }
  ];

  // Detect Amazon "Hazmat review" / "Approval is required" errors and parse out
  // the affected SKUs from messages like: "(There's an input error with the resource 'ACDD-STEP3N181'.)"
  function handleOperationError(err, fallbackContext) {
    var msg = (err && err.message) || '';
    var isHazmat = /Hazmat review|Approval is required before this item/i.test(msg);
    if (isHazmat) {
      var skus = [];
      var re = /resource '([^']+)'/g;
      var m;
      while ((m = re.exec(msg)) !== null) { if (skus.indexOf(m[1]) === -1) skus.push(m[1]); }
      if (skus.length) {
        setHazmatIssue({ skus: skus, fullError: msg });
        setHazmatBins({});
        return;
      }
    }
    showToast((fallbackContext ? fallbackContext + ': ' : '') + msg, 'error');
  }
  var step = manualStep != null ? manualStep : currentStep;
  var activeKey = stepKeys[step] || 'review';
  // Clear stuck actionLoading when switching steps
  useEffect(function() { setActionLoading(''); }, [activeKey]);

  // Helper to find step index by key
  var stepIndex = function(key) { return stepKeys.indexOf(key); };

  // ─── Action handlers ───

  var handleCreatePlan = function(stripList) {
    setActionLoading('create-plan');
    api('/fba-shipments/plans/' + planId + '/create-plan', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify((stripList && stripList.length) ? { stripItems: stripList } : {}) })
      .then(function(res) {
        if (res && res.status === 'PROCESSING') {
          showToast(res.message || 'Plan submitted — Amazon is still finalizing it. Refresh in a moment to see the final status.', 'warning');
          setManualStep(stepIndex('create') + 1);
          loadPlan();
          return;
        }
        showToast('Inbound plan created: ' + (res.inboundPlanId || ''), 'success');
        if (res.hazmat_stripped_count && res.hazmat_stripped_count > 0) {
          var byReason = res.stripped_by_reason || {};
          var reasonLabels = { hazmat: 'Hazmat', catalog_lag: 'Catalog lag', invalid_msku: 'Invalid MSKU', no_fc_capacity: 'No FC capacity', brand_registry: 'Brand registry', other: 'Errored' };
          var parts = Object.keys(byReason).map(function(k) { return (byReason[k] || []).length + ' ' + (reasonLabels[k] || k); });
          var hzSkus = (res.hazmat_stripped_skus || []).slice(0, 3).join(', ');
          var suffix = res.hazmat_stripped_count > 3 ? ' +' + (res.hazmat_stripped_count - 3) + ' more' : '';
          setTimeout(function() {
            showToast(
              res.hazmat_stripped_count + ' SKU(s) auto-removed from the plan' +
              (parts.length ? ' (' + parts.join(', ') + ')' : '') +
              (hzSkus ? ' [' + hzSkus + suffix + ']' : '') +
              ' — receive them on the Errored tab in the packing step.',
              'warning'
            );
          }, 600);
        }
        setManualStep(stepIndex('create') + 1);
        loadPlan();
      })
      .catch(function(e) {
        var msg = e.message || '';
        var body = e.apiBody || {};
        // DG segregation — Amazon flagged items that can't ship with hazmat (or vice versa).
        // Offer to split them off into a new sibling plan that stays in draft.
        if (body.action === 'split_dg_segregation' && Array.isArray(body.split_items) && body.split_items.length) {
          var items = body.split_items;
          var skuLine = items.map(function(i) { return i.msku; }).join(', ');
          var confirmMsg = 'Amazon won\'t ship these items with the rest of this plan (hazmat/non-hazmat segregation):\n\n' +
            items.map(function(i) { return '  • ' + i.msku + (i.title ? ' — ' + i.title : ''); }).join('\n') +
            '\n\nSplit them into a new sibling plan (draft) and retry this one without them?';
          if (window.confirm(confirmMsg)) {
            api('/fba-shipments/plans/' + planId + '/split-into-sibling', {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ mskus: items.map(function(i) { return i.msku; }) })
            }).then(function(splitRes) {
              showToast(
                'Moved ' + splitRes.moved_items + ' SKU(s) (' + skuLine + ') to new plan #' +
                splitRes.new_plan_id + ' (draft). Retrying this plan…',
                'success'
              );
              loadPlan();
              setTimeout(function() { handleCreatePlan(); }, 500);
            }).catch(function(splitErr) {
              showToast('Split failed: ' + (splitErr.message || 'unknown error'), 'error');
            });
          }
          return;
        }
        // Items Amazon rejected (missing dimensions, brand-registry, hazmat, catalog lag, etc.) —
        // surface them in a panel so the user can remove them (→ Errored tab → bin) or fix in
        // Seller Central, instead of silently stripping or 500ing.
        if (Array.isArray(body.create_item_errors) && body.create_item_errors.length) {
          setCreateErrors({ items: body.create_item_errors });
          return;
        }
        // nginx gives up on the request after 10 minutes but the server keeps working and stores
        // the outcome on the plan (last_create_result). Say so and re-read the plan a few times
        // so the stored outcome (or the created plan) shows up without a manual refresh.
        if (e.apiStatus === 504 || e.apiStatus === 502) {
          showToast('The server is still working on this plan — large plans can take several minutes. This page will re-check on its own; the outcome appears in the Create step.', 'warning');
          [30000, 90000, 180000, 300000, 480000].forEach(function(ms) { setTimeout(function() { loadPlan(); }, ms); });
          return;
        }
        if (msg.toLowerCase().includes('expiration')) {
          showToast('Expiration dates required — set them below then retry', 'warning');
          loadPlan(); // reload to pick up any products that got flagged
        } else {
          showToast(msg, 'error');
        }
      })
      .finally(function() { setActionLoading(''); });
  };

  // Drop msku(s) from the open "Items Amazon rejected" panel once an action resolves them.
  var dropFromCreateErrors = function(mskus) {
    var rm = {}; (mskus || []).forEach(function(m) { rm[m] = true; });
    setCreateErrors(function(prev) {
      if (!prev) return prev;
      var rest = prev.items.filter(function(x) { return !rm[x.msku]; });
      return rest.length ? { items: rest } : null;
    });
  };

  // Move a single rejected item to the Errored tab (binned), without creating the plan yet.
  var handleMoveToErrored = function(item) {
    api('/fba-shipments/plans/' + planId + '/strip-items', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ msku: item.msku, reason: item.reason || 'rejected' }] })
    }).then(function() {
      showToast(item.msku + ' moved to Errored', 'success');
      dropFromCreateErrors([item.msku]);
    }).catch(function(e) { showToast(e.message, 'error'); });
  };

  // Move every rejected item currently shown (in the active tab) to the Errored tab.
  var handleMoveAllToErrored = function(list) {
    if (!list || !list.length) return;
    api('/fba-shipments/plans/' + planId + '/strip-items', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: list.map(function(it) { return { msku: it.msku, reason: it.reason || 'rejected' }; }) })
    }).then(function() {
      showToast('Moved ' + list.length + ' item(s) to Errored', 'success');
      dropFromCreateErrors(list.map(function(it) { return it.msku; }));
    }).catch(function(e) { showToast(e.message, 'error'); });
  };

  // Remove the item's entire PO from the shipment — items revert to checked-in (like a cancel).
  var handleRemovePO = function(item) {
    var poIds = item.po_ids || [];
    if (!poIds.length) { showToast('No PO linked to ' + item.msku, 'error'); return; }
    Promise.all(poIds.map(function(poId) {
      return api('/fba-shipments/plans/' + planId + '/remove-po', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ po_id: poId })
      });
    })).then(function(results) {
      var removed = results.reduce(function(acc, r) { return acc.concat((r && r.removed_mskus) || []); }, []);
      showToast('Removed PO ' + (item.po_numbers || []).join(', ') + ' (' + removed.length + ' item(s) → checked-in)', 'success');
      dropFromCreateErrors(removed);
    }).catch(function(e) { showToast(e.message, 'error'); });
  };

  // Apply the chosen prep classification (setPrepDetails) to all "Prep required" items, then re-create.
  var handleApplyPrep = function(prepMskus) {
    if (!prepMskus || !prepMskus.length) return;
    var opt = PREP_OPTIONS.filter(function(o) { return o.value === prepChoice; })[0] || PREP_OPTIONS[0];
    setActionLoading('set-prep');
    api('/fba-shipments/plans/' + planId + '/set-prep', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mskus: prepMskus, prepCategory: opt.prepCategory, prepTypes: opt.prepTypes, prepOwner: opt.prepOwner, labelOwner: opt.labelOwner })
    }).then(function() {
      showToast('Prep set (' + opt.label + ') for ' + prepMskus.length + ' item(s) — re-creating plan…', 'success');
      setCreateErrors(null);
      handleCreatePlan();
    }).catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setActionLoading(''); });
  };


  var twoYearsFromNow = new Date();
  twoYearsFromNow.setFullYear(twoYearsFromNow.getFullYear() + 2);
  var minExpDate = twoYearsFromNow.toISOString().substring(0, 10);

  // Pre-fill expiration dates with 2-year default and auto-save
  useEffect(function() {
    if (!plan || !plan.items || !plan.items.length) return;
    var twoYr = new Date();
    twoYr.setFullYear(twoYr.getFullYear() + 2);
    var defaultDate = twoYr.toISOString().substring(0, 10);
    var needsDefault = plan.items.filter(function(i) { return i.requires_expiration && !i.expiration_date && !expAutoAppliedRef.current[i.id]; });
    if (!needsDefault.length) return;
    needsDefault.forEach(function(i) { expAutoAppliedRef.current[i.id] = true; });
    // Set in state
    var dates = {};
    needsDefault.forEach(function(i) { dates[i.id] = defaultDate; });
    setExpirationDates(function(prev) {
      var merged = Object.assign({}, prev);
      var changed = false;
      needsDefault.forEach(function(i) { if (!merged[i.id]) { merged[i.id] = defaultDate; changed = true; } });
      return changed ? merged : prev;
    });
    // Auto-save to server in ONE bulk request — a per-item PATCH flood trips the rate
    // limiter ("Too many requests") and leaves some dates unsaved.
    api('/fba-shipments/plans/' + planId + '/expirations', {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dates: needsDefault.map(function(i) { return { item_id: i.id, expiration_date: defaultDate }; }) })
    }).then(function() { loadPlan(); }).catch(function(e) { console.error('[exp-autosave]', e); });
  }, [plan && plan.items && plan.items.filter(function(i) { return i.requires_expiration && !i.expiration_date; }).length]);

  var handleBulkExpiration = function(date) {
    var dates = Object.assign({}, expirationDates);
    var expItems = (items || []).filter(function(i) { return i.requires_expiration; });
    expItems.forEach(function(item) { dates[item.id] = date; });
    setExpirationDates(dates);
    // Open every expiration input for editing without touching the saved dates
    var editing = Object.assign({}, editingExp);
    expItems.forEach(function(item) { editing[item.id] = true; });
    setEditingExp(editing);
  };

  var handleSaveAllExpirations = function() {
    var itemsToSave = (items || []).filter(function(i) { return i.requires_expiration && expirationDates[i.id]; });
    if (!itemsToSave.length) { showToast('No dates to save', 'error'); return; }
    // Validate all dates are at least 2 years out
    var tooSoon = itemsToSave.filter(function(item) { return expirationDates[item.id] < minExpDate; });
    if (tooSoon.length) {
      showToast('Expiration dates must be at least 2 years from today (' + minExpDate + '). Fix: ' + tooSoon.map(function(i) { return i.msku; }).join(', '), 'warning');
      return;
    }
    setActionLoading('save-expirations');
    api('/fba-shipments/plans/' + planId + '/expirations', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dates: itemsToSave.map(function(item) { return { item_id: item.id, expiration_date: expirationDates[item.id] }; }) })
    })
      .then(function() { showToast('All expiration dates saved', 'success'); setEditingExp({}); loadPlan(); })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setActionLoading(''); });
  };

  var handleGenPackingOpts = function() {
    setActionLoading('packing-options');
    api('/fba-shipments/plans/' + planId + '/packing-options', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
      .then(function(res) { setPackingOptions(res.options || []); if (res.options && res.options.length) setSelectedPackingOpt(res.options[0].packingOptionId || res.options[0].id || ''); })
      .catch(function(e) { handleOperationError(e, 'Generate packing options'); })
      .finally(function() { setActionLoading(''); });
  };

  var handleConfirmPacking = function() {
    if (!selectedPackingOpt) return;
    setActionLoading('confirm-packing');
    api('/fba-shipments/plans/' + planId + '/confirm-packing', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ packing_option_id: selectedPackingOpt }) })
      .then(function() { showToast('Packing confirmed', 'success'); setManualStep(null); loadPlan(); })
      .catch(function(e) { handleOperationError(e, 'Confirm packing'); })
      .finally(function() { setActionLoading(''); });
  };

  var handleGenPlacementOpts = function() {
    setActionLoading('placement-options');
    api('/fba-shipments/plans/' + planId + '/placement-options', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
      .then(function(res) { setPlacementOptions(res.options || []); if (res.options && res.options.length) setSelectedPlacementOpt(res.options[0].placementOptionId || res.options[0].id || ''); })
      .catch(function(e) { handleOperationError(e, 'Generate placement options'); })
      .finally(function() { setActionLoading(''); });
  };

  // Open the split chooser modal (pick SPD/LTL + packing, like New Shipment) for the selected
  // shipments. The actual split runs in handleSplitConfirm.
  var openSplitModal = function(ships) {
    if (!ships || !ships.length) { showToast('Select shipment(s) to move first', 'error'); return; }
    if (!ships.some(function(s) { return (s.items || []).length; })) { showToast('No items in the selected shipment(s) to move', 'error'); return; }
    setSplitType('SPD'); setSplitPacking('individual');
    setSplitModal({ ships: ships });
  };

  // Move the chosen shipments' items into a new separate (sibling) plan with the chosen
  // SPD/LTL + packing. Cancels the current Amazon plan and resets THIS plan to draft.
  var handleSplitConfirm = function() {
    var ships = (splitModal && splitModal.ships) || [];
    var mskuSet = {};
    ships.forEach(function(s) { (s.items || []).forEach(function(it) { if (it.msku) mskuSet[it.msku] = true; }); });
    var mskus = Object.keys(mskuSet);
    if (!mskus.length) { showToast('No items to move', 'error'); return; }
    setActionLoading('split-shipment');
    api('/fba-shipments/plans/' + planId + '/split-into-sibling', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mskus: mskus, shipment_type: splitType, packing_mode: splitPacking })
    }).then(function(res) {
      showToast('Moved ' + (res.moved_items || mskus.length) + ' item(s) to "' + (res.new_plan_name || 'new plan') + '" (' + splitType + ' · ' + (splitPacking === 'case_packed' ? 'Case-packed' : 'Individual') + ') — this plan reset to draft.', 'success');
      setSplitModal(null);
      setPlacementOptions([]);
      setSelectedPlacementOpt('');
      setSplitSel({});
      setManualStep(stepIndex('create'));
      loadPlan();
    }).catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setActionLoading(''); });
  };

  // LTL-individual plans can't get partnered-carrier rate previews on v2024-03-20:
  // Amazon requires the placement option to be confirmed before generateTransportationOptions
  // returns anything (FBA_INB_0344 "No placement option was confirmed"). Skip the preview
  // for that combo to save API quota and avoid a confusing "no rates" state — rates appear
  // on the transport step after confirmation. See project_fba_inbound_api_caps.md.
  var isLtlIndividual = plan && plan.shipment_type === 'LTL' && (plan.packing_mode || 'individual') === 'individual';
  var loadAmazonRates = useCallback(function(force) {
    if (!planId || !placementOptions.length) return;
    if (isLtlIndividual) { setAmazonRates(null); setAmazonRatesError(''); return; }
    setLoadingAmazonRates(true);
    setAmazonRatesError('');
    var url = '/fba-shipments/plans/' + planId + '/amazon-partnered-rates' + (force ? '?refresh=1' : '');
    api(url)
      .then(function(res) { setAmazonRates(res); })
      .catch(function(e) { console.error('[Amazon Rates]', e.message); setAmazonRates(null); setAmazonRatesError(e.message || 'Failed to load Amazon rates'); })
      .finally(function() { setLoadingAmazonRates(false); });
  }, [planId, placementOptions.length, isLtlIndividual]);
  useEffect(function() { loadAmazonRates(false); }, [loadAmazonRates]);

  // Rehydrate placement options when the Placement step opens with an empty list —
  // they only live in page state after Generate, so leaving the detail page loses
  // them while Amazon still has them. List-only GET (never regenerates, spares
  // inbound-API quota); empty result just leaves the Generate button showing.
  useEffect(function() {
    if (activeKey !== 'placement' || placementOptions.length) return;
    if (!plan || !plan.amazon_plan_id || plan.placement_option_id) return;
    api('/fba-shipments/plans/' + planId + '/placement-options')
      .then(function(res) {
        var opts = (res && res.options) || [];
        if (!opts.length) return;
        setPlacementOptions(opts);
        setSelectedPlacementOpt(opts[0].placementOptionId || opts[0].id || '');
      })
      .catch(function(e) { console.error('[silent-catch]', e); });
  }, [activeKey, placementOptions.length, plan && plan.amazon_plan_id, plan && plan.placement_option_id]);

  var handleConfirmPlacement = function() {
    if (!selectedPlacementOpt) return;
    setActionLoading('confirm-placement');
    // Find selected option's fees
    var selOpt = placementOptions.find(function(o) { return (o.placementOptionId || o.id) === selectedPlacementOpt; });
    var placementFees = 0;
    if (selOpt) {
      (selOpt.fees || []).forEach(function(f) { var v = f.value || f.amount; placementFees += typeof v === 'object' ? parseFloat(v.amount || 0) : parseFloat(v || 0); });
      (selOpt.discounts || []).forEach(function(d) { var v = d.value || d.amount; placementFees -= typeof v === 'object' ? parseFloat(v.amount || 0) : parseFloat(v || 0); });
    }
    api('/fba-shipments/plans/' + planId + '/confirm-placement', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ placement_option_id: selectedPlacementOpt, placement_fees: placementFees }) })
      .then(function(res) { showToast('Placement confirmed — ' + (res.shipment_count || 0) + ' shipment(s)', 'success'); setManualStep(null); loadPlan(); })
      .catch(function(e) { handleOperationError(e, 'Confirm placement'); })
      .finally(function() { setActionLoading(''); });
  };

  var handleGenTransportOpts = function(shipId) {
    setActionLoading('transport-' + shipId);
    var rd = readyDates[shipId] || defaultReadyDate;
    var sent = fbaEffectiveReadyDate(rd); // what Amazon receives (the server's clamp, mirrored)
    api('/fba-shipments/shipments/' + shipId + '/transport-options', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ready_date: rd }) })
      .then(function(res) {
        setSentReadyDate(sent);
        // Store options for all shipments if returned
        if (res.allShipmentOptions) {
          setTransportOptions(function(prev) {
            var n = Object.assign({}, prev);
            Object.keys(res.allShipmentOptions).forEach(function(sid) { n[sid] = res.allShipmentOptions[sid]; });
            return n;
          });
        } else {
          setTransportOptions(function(prev) { var n = Object.assign({}, prev); n[shipId] = res.options || []; return n; });
        }
      })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setActionLoading(''); });
  };

  var handleConfirmTransport = function(shipId) {
    var optId = selectedTransportOpt[shipId];
    if (!optId) return;
    setActionLoading('confirm-transport-' + shipId);
    api('/fba-shipments/shipments/' + shipId + '/confirm-transport', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ transport_option_id: optId }) })
      .then(function() { showToast('Transport confirmed', 'success'); loadPlan(); })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setActionLoading(''); });
  };

  // Amazon confirms the shipments of a placement option in ONE call (9-18, plan 734: three
  // single-shipment confirms were each rejected as not matching the placement option).
  var handleConfirmTransportAll = function(selections) {
    if (!selections || !selections.length) return;
    setActionLoading('confirm-transport-all');
    api('/fba-shipments/plans/' + planId + '/confirm-transport', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ selections: selections }) })
      .then(function(r) { showToast('Transport confirmed for ' + ((r && r.confirmed) || selections.length) + ' shipment(s)', 'success'); loadPlan(); })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setActionLoading(''); });
  };

  var handleGenDeliveryWindows = function(shipId) {
    setActionLoading('delivery-' + shipId);
    api('/fba-shipments/shipments/' + shipId + '/delivery-windows', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
      .then(function(res) { setDeliveryOptions(function(prev) { var n = Object.assign({}, prev); n[shipId] = res.options || []; return n; }); })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setActionLoading(''); });
  };

  var handleConfirmDeliveryWindow = function(shipId) {
    var opt = selectedDeliveryOpt[shipId];
    if (!opt) return;
    setActionLoading('confirm-delivery-' + shipId);
    api('/fba-shipments/shipments/' + shipId + '/confirm-delivery', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ delivery_window_option_id: opt.deliveryWindowOptionId || opt.id, start: opt.startDate || opt.start, end: opt.endDate || opt.end })
    })
      .then(function() {
        showToast('Delivery window confirmed', 'success');
        // Clear cached transport options so user re-generates with delivery windows confirmed
        setTransportOptions({});
        setSentReadyDate(null);
        loadPlan();
      })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setActionLoading(''); });
  };


  var handleGetLabels = function(shipId) {
    setActionLoading('labels-' + shipId);
    api('/fba-shipments/shipments/' + shipId + '/labels', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
      .then(function(res) { showToast('Labels retrieved', 'success'); loadPlan(); var url = res.downloadURL || res.DownloadURL; if (url) window.open(url, '_blank'); })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setActionLoading(''); });
  };

  var handleMarkShipped = function(shipId, carrier, trackingId, proNumber, bolNumber, boxTracking) {
    setActionLoading('ship-' + shipId);
    api('/fba-shipments/shipments/' + shipId + '/tracking', { method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ carrier: carrier, tracking_id: trackingId, pro_number: proNumber, bol_number: bolNumber, box_tracking: boxTracking || undefined }) })
      .then(function(r) { showToast(r.warning ? r.warning : 'Tracking updated', r.warning ? 'warning' : 'success'); loadPlan(); })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setActionLoading(''); });
  };

  var handleCancelPlan = async function() {
    var related = (plan && Array.isArray(plan.related_plans)) ? plan.related_plans : [];
    var cancelSiblings = false;
    if (related.length) {
      var lines = related.map(function(r) { return '  • #' + r.id + ' — ' + (r.name || ('Plan ' + r.id)) + ' (' + r.status + ')'; }).join('\n');
      var both = await appConfirm('This plan is part of a split group. Related plan(s):\n\n' + lines +
        '\n\nChoose "Cancel all" to cancel this plan AND the related plan(s) above.\n' +
        'Choose "Only this plan" to cancel only this plan (you can still abort at the next step).', { danger: true, okLabel: 'Cancel all', cancelLabel: 'Only this plan' });
      if (both) {
        cancelSiblings = true;
      } else {
        if (!(await appConfirm('Cancel ONLY this plan? Sibling plans will remain active and may end up with duplicate or orphaned items.', { danger: true, okLabel: 'Cancel this plan' }))) return;
        cancelSiblings = false;
      }
    }
    // The dialog carries the one decision the plain confirm could not: whether the PO
    // receiving these packed units produced stays (units moved to another plan) or is
    // reversed back into staging (Anthony 2026-09-16, plan 718 → 751).
    var packed = (items || []).reduce(function(a, i) { return a + (parseInt(i.quantity_packed, 10) || 0); }, 0);
    // A PO that is already received keeps its receiving by default; reversing is the opt-in there.
    setCancelState({ cancelSiblings: cancelSiblings, keepReceiving: !!plan.po_all_received, poReceived: !!plan.po_all_received, packed: packed, submitting: false });
  };
  var submitCancelPlan = function() {
    if (!cancelState || cancelState.submitting) return;
    setCancelState(function(s) { return Object.assign({}, s, { submitting: true }); });
    var qs = [];
    if (cancelState.cancelSiblings) qs.push('cancel_siblings=true');
    qs.push('keep_receiving=' + (cancelState.keepReceiving ? 'true' : 'false'));
    var url = '/fba-shipments/plans/' + planId + (qs.length ? '?' + qs.join('&') : '');
    api(url, { method: 'DELETE' })
      .then(function(res) {
        var extra = (res && res.cancelled_siblings && res.cancelled_siblings.length)
          ? ' (also cancelled ' + res.cancelled_siblings.length + ' related plan' + (res.cancelled_siblings.length > 1 ? 's' : '') + ')'
          : '';
        if (res && res.kept_receiving) extra += ' — PO receiving kept (' + (res.packed_kept || 0) + ' units)';
        setCancelState(null);
        showToast('Shipment cancelled' + extra, 'success');
        // Vendor-direct single-PO plans return to their PO (same convention as the create wizard's
        // cancel — that wizard only auto-opens from a vendor-direct PO). Warehouse, manual and multi-PO
        // plans fall back to the FBA list (Anthony 2026-10-30: cancelling a warehouse plan landed on its PO).
        var poIds = (plan && plan.po_ids && plan.po_ids.length) ? plan.po_ids : (plan && plan.po_id ? [plan.po_id] : []);
        if (poIds.length === 1 && plan && plan.ship_mode === 'vendor_direct') onNavigate('po-detail', poIds[0]);
        else onNavigate('fba-shipments');
      })
      .catch(function(e) { showToast(e.message, 'error'); setCancelState(function(s) { return s ? Object.assign({}, s, { submitting: false }) : null; }); });
  };

  // ─── Print all case labels for all boxes across all shipments ───
  var printCaseLabelBatch = function(allData, totalBoxes) {
      var pw = window.open('', '_blank', 'width=420,height=630');
      if (!pw) { showToast('Pop-up blocked', 'error'); return; }

      var html = '<!DOCTYPE html><html><head><title>FBA Case Labels</title>' +
        '<style>' +
        '@page { size: 4.25in 6in; margin: 0; }' +
        'body { margin: 0; padding: 0; font-family: Helvetica, Arial, sans-serif; }' +
        '.label { width: 4.25in; height: 6in; padding: 0.1in 0.2in; box-sizing: border-box; overflow: hidden; }' +
        '.label + .label { page-break-before: always; }' +
        '.banner { text-align: center; font-size: 8pt; margin-bottom: 2px; }' +
        '.fba-hdr { font-size: 15pt; margin-bottom: 0; }' +
        '.hr { border: none; border-top: 1.5px solid #000; margin: 1px 0; }' +
        '.addr-row { display: flex; font-size: 8pt; line-height: 1.05; margin: 0; }' +
        '.addr-col { flex: 1; }' +
        '.addr-lbl { font-size: 8pt; margin-bottom: 0; }' +
        '.info-bar { background: #000 !important; color: #fff !important; font-size: 6pt; padding: 1px 3px; display: flex; justify-content: space-between; align-items: center; margin: 0 0 2px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }' +
        '.barcode-area { margin: 4px 0 0; }' +
        '.code128-col { width: 100%; text-align: center; }' +
        '.code128-col svg { display: inline-block; }' +
        '.pdf417-col { width: 100%; text-align: center; margin: 0.4in 0 0; }' +
        '.pdf417-col img { display: block; width: 3.5in; height: auto; image-rendering: pixelated; margin: 0 auto; }' +
        '.box-id-text { font-size: 8pt; text-align: center; margin: 2px 0 0; }' +
        '.hr2 { border: none; border-top: 1px solid #000; margin: 3px 0; }' +
        '.item-section { margin: 6px 0 0; font-size: 8pt; line-height: 1.3; text-align: center; }' +
        '.item-section .sku-label { font-size: 9pt; font-weight: bold; }' +
        '.bottom-box-id { position: absolute; bottom: 0.1in; left: 0; right: 0; text-align: center; font-size: 14pt; }' +
        '</style></head><body>';

      allData.forEach(function(d) {
        var barcodeItems = (d.items || []).map(function(it) { return { fnsku: it.fnsku || '', qty: it.quantity || 0 }; });
        var amznData = buildAmazonCaseBarcodeData(d.shipment.amazonShipmentId, barcodeItems);
        var _c128canvas = document.createElement('canvas');
        window.bwipjs.toCanvas(_c128canvas, { bcid: 'code128', text: d.boxId, scale: 3, height: 18, paddingleft: 10, paddingright: 10, includetext: false });
        var boxIdBarcode = '<img src="' + _c128canvas.toDataURL('image/png') + '" style="display:block;width:3.5in;height:auto;image-rendering:pixelated;margin:0 auto;" />';
        var pdf417Html = generateCasePDF417SVG(amznData, 'display:block;width:3.5in;height:auto;image-rendering:pixelated;margin:0 auto;');

        var cDate = new Date(d.shipment.createdAt);
        var createdStr = 'Created: ' + cDate.getFullYear() + '/' + String(cDate.getMonth() + 1).padStart(2, '0') + '/' + String(cDate.getDate()).padStart(2, '0') + ' ' + String(cDate.getHours()).padStart(2, '0') + ':' + String(cDate.getMinutes()).padStart(2, '0');

        var isSingleSku = d.items.length === 1;
        var countryFrom = (d.shipFrom.country || 'US') === 'US' ? 'United States' : d.shipFrom.country;
        var countryTo = (d.shipTo.country || 'US') === 'US' ? 'United States' : d.shipTo.country;

        var shipToLines = '<div>' + escHtml(d.shipTo.name) + '</div>'; // text is escaped: names, addresses and titles come from Amazon / the catalog / the user (review 2026-09-28, medium #40)
        if (d.shipTo.fc) shipToLines += '<div>' + escHtml(d.shipTo.fc) + '</div>';
        if (d.shipTo.address1) shipToLines += '<div>' + escHtml(d.shipTo.address1) + '</div>';
        if (d.shipTo.address2) shipToLines += '<div>' + escHtml(d.shipTo.address2) + '</div>';
        if (d.shipTo.city || d.shipTo.state || d.shipTo.zip) shipToLines += '<div>' + escHtml([d.shipTo.city, d.shipTo.state].filter(Boolean).join(', ') + ' ' + d.shipTo.zip) + '</div>';
        shipToLines += '<div>' + escHtml(countryTo) + '</div>';

        // SKU section (LTL only) — single-SKU boxes show the SKU + Vendor SKU,
        // multi-SKU boxes show "Mixed SKUs". SPD labels are Amazon-provided so
        // this branch is skipped.
        var skuLine = '';
        if (d.plan && d.plan.shipmentType === 'LTL') {
          var esc = function(s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); };
          if (isSingleSku) {
            var it = d.items[0] || {};
            var sku = it.product_sku || it.msku || '';
            var vsku = it.vendor_sku || '';
            var fnsku = it.fnsku || '';
            if (sku) skuLine += '<div>SKU: <strong>' + esc(sku) + '</strong></div>';
            if (vsku) skuLine += '<div>Vendor SKU: <strong>' + esc(vsku) + '</strong></div>';
            if (fnsku) skuLine += '<div>FNSKU: <strong>' + esc(fnsku) + '</strong></div>';
          } else if (d.items.length > 1) {
            skuLine = '<div><strong>Mixed SKUs</strong></div>';
          }
        }

        html += '<div class="label" style="position:relative;">' +
          '<div class="banner">PLEASE LEAVE THIS LABEL UNCOVERED</div>' +
          '<div class="fba-hdr">FBA</div>' +
          '<hr class="hr">' +
          '<div class="addr-row">' +
            '<div class="addr-col"><div class="addr-lbl">SHIP FROM:</div><div>' + escHtml(d.shipFrom.name) + '</div><div>' + escHtml(d.shipFrom.address1) + '</div><div>' + escHtml([d.shipFrom.city, d.shipFrom.state].filter(Boolean).join(', ') + ' ' + d.shipFrom.zip) + '</div><div>' + escHtml(countryFrom) + '</div></div>' +
            '<div class="addr-col"><div class="addr-lbl">SHIP TO:</div>' + shipToLines + '</div>' +
          '</div>' +
          '<div class="info-bar"><span>' + escHtml(d.shipment.displayName) + '</span><span>' + escHtml(createdStr) + '</span></div>' +
          '<div class="barcode-area">' +
            '<div class="code128-col">' + boxIdBarcode + '</div>' +
            '<div class="box-id-text">' + escHtml(d.boxId) + '</div>' +
            '<hr class="hr2">' +
            '<div class="pdf417-col">' + pdf417Html + '</div>' +
            '<div class="item-section">' +
              '<div>Dest: <strong>' + escHtml(d.shipment.destinationFc || '') + '</strong></div><div>Shipment: <strong>' + escHtml(d.shipment.amazonShipmentId || '') + '</strong></div><div>Units: <strong>' + (d.totalUnits || 0) + '</strong></div>' +
              skuLine +
            '</div>' +
          '</div>' +
          '<div class="bottom-box-id">Box ' + d.boxNumber + '</div>' +
        '</div>';
      });

      html += '</body></html>';
      pw.document.write(html);
      pw.document.close();
      // Drive printing from the opener (same as printAllCaseLabelsLocal) — an in-document
      // window.onload handler did not reliably fire for document.write popups, leaving labels
      // stuck in preview. 500ms lets the data-URI barcodes paint before the dialog opens.
      setTimeout(function() { try { pw.focus(); pw.print(); } catch (e) { /* popup closed */ } }, 500);
      // Mark all as printed
      allData.forEach(function(d) {
        if (d.box && d.box.id) api('/fba-shipments/boxes/' + d.box.id + '/mark-printed', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }).catch(function(e) { console.error('[silent-catch]', e); });
      });
  };

  var printAllCaseLabels = function() {
    var allBoxIds = [];
    (plan.shipments || []).forEach(function(s) {
      (s.boxes || []).forEach(function(b) { allBoxIds.push(b.id); });
    });
    if (!allBoxIds.length) { showToast('No boxes to print labels for', 'error'); return; }
    showToast('Generating ' + allBoxIds.length + ' case label(s)...', 'info');
    Promise.all(allBoxIds.map(function(boxId) { return api('/fba-shipments/boxes/' + boxId + '/case-label-data'); }))
      .then(function(allData) { printCaseLabelBatch(allData, allBoxIds.length); })
      .catch(function(e) { showToast('Failed to generate case labels: ' + e.message, 'error'); });
  };

  // Case labels for ONE shipment (transport-stage reprint). Phantom boxes hold
  // declared-missing units that never ship — no label for those.
  var printShipmentCaseLabels = function(s) {
    var shipBoxIds = (s.boxes || []).filter(function(b) { return !b.is_phantom; }).map(function(b) { return b.id; });
    if (!shipBoxIds.length) { showToast('No boxes for this shipment', 'error'); return; }
    showToast('Generating ' + shipBoxIds.length + ' case label(s)...', 'info');
    Promise.all(shipBoxIds.map(function(boxId) { return api('/fba-shipments/boxes/' + boxId + '/case-label-data'); }))
      .then(function(allData) { printCaseLabelBatch(allData, shipBoxIds.length); })
      .catch(function(e) { showToast('Failed to generate case labels: ' + e.message, 'error'); });
  };

  if (loading || !plan) return h('div', { style: { padding: 40, textAlign: 'center', color: C.textMuted } }, 'Loading...');

  var shipments = plan.shipments || [];
  var hasShipmentIds = shipments.some(function(s) { return !!s.amazon_shipment_id; });
  var allShipmentsPacked = shipments.length > 0 && shipments.every(function(s) { return shipPackingStatus[s.id]; });
  // Plan-level errored/hazmat gate: Amazon-rejected SKUs (binned via the Errored tab) must
  // be resolved before submitting packing. Kept separate from per-shipment done-state so a
  // fully-packed shipment still shows its green check while errored items remain unbinned.
  var unreceivedErroredCount = erroredCountOverride != null
    ? erroredCountOverride
    : ((plan && Array.isArray(plan.hazmat_pending_items)) ? plan.hazmat_pending_items.filter(function(h) { return h && !h.received && !h.removed_from_po && !h.marked_missing; }).length : 0);
  var items = plan.items || [];
  // Shipment type / packing can still change on a CREATED plan while nothing Amazon-side depends
  // on the track: no packing option, no placement, no boxes or packed units, no Amazon shipment
  // IDs (the mode only reaches Amazon at the transport step). Mirrors the server guard in
  // services/fba-shipping-type.js — the server has the final say.
  var canChangeShipping = !!(plan.amazon_plan_id && ['plan_created', 'packing'].indexOf(plan.status) >= 0
    && !plan.packing_option_id && !plan.placement_option_id
    && !shipments.some(function(s) { return (s.boxes || []).length > 0 || s.amazon_shipment_id || s.transportation_option_id; })
    && !items.some(function(i) { return (i.quantity_packed || 0) > 0; }));
  // totalUnits drives the Placement step's $/unit and %-of-cost columns, so it
  // must reflect what Amazon is actually placing — excluded SKUs (Resubmit drops
  // them from the recreated plan) shouldn't dilute the denominator.
  var totalUnits = items.reduce(function(s, i) { return s + (i.excluded_from_amazon ? 0 : (i.quantity || 0)); }, 0);
  var totalPacked = items.reduce(function(s, i) { return s + (i.quantity_packed || 0); }, 0);

  // Print all case labels across all shipments, sorted by SKU
  var printAllCaseLabelsSorted = function() {
    var allBoxIds = [];
    shipments.forEach(function(s) {
      (s.boxes || []).forEach(function(b) { allBoxIds.push(b.id); });
    });
    if (!allBoxIds.length) { showToast('No boxes to print labels for', 'error'); return; }
    showToast('Generating ' + allBoxIds.length + ' case label(s)...', 'info');
    Promise.all(allBoxIds.map(function(boxId) { return api('/fba-shipments/boxes/' + boxId + '/case-label-data'); }))
      .then(function(allData) {
        allData.sort(function(a, b) {
          var skuA = (a.items && a.items[0] && a.items[0].sku) || '';
          var skuB = (b.items && b.items[0] && b.items[0].sku) || '';
          return skuA < skuB ? -1 : skuA > skuB ? 1 : 0;
        });
        printCaseLabelBatch(allData, allBoxIds.length);
      })
      .catch(function(e) { showToast('Failed: ' + e.message, 'error'); });
  };

  // Print all box labels for SPD Case Packed
  var printAllBoxLabels = function() {
    showToast('Fetching all shipping labels...', 'info');
    api('/fba-shipments/plans/' + planId + '/all-labels', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
      .then(function(labelData) {
        if (labelData.base64) {
          var blob = new Blob([Uint8Array.from(atob(labelData.base64), function(c) { return c.charCodeAt(0); })], { type: 'application/pdf' });
          window.open(URL.createObjectURL(blob), '_blank');
        }
        loadPlan();
        showToast('Shipping labels — ' + (labelData.pages || 0) + ' pages from ' + (labelData.shipments || 0) + ' shipments', 'success');
      })
      .catch(function(e) { showToast('Failed: ' + e.message, 'error'); });
  };

  return h('div', { style: { animation: 'fadeIn 0.3s ease' } },
    // Back button + header
    h('div', { style: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 } },
      h('button', { onClick: function() { onNavigate('fba-shipments'); }, style: { background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, padding: 4 } }, h(LIcon, { name: 'arrow-left', size: 18 })),
      h('div', null,
        h('h2', { style: { fontSize: 18, fontWeight: 700, color: C.text, margin: 0 } }, plan.name || ('FBA Shipment — PO ' + (plan.po_numbers || plan.po_number || 'Draft'))),
        h('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 2 } },
          (plan.wh_code || '') + ' | ' + (plan.shipment_type || 'SPD'),
          canChangeShipping && activeKey !== 'create' && h('button', { type: 'button', title: 'Switch between SPD and LTL — the Amazon plan stays', onClick: function() { setManualStep(stepIndex('create')); },
            style: { background: 'none', border: 'none', cursor: 'pointer', color: C.accent, fontSize: 11, fontWeight: 600, padding: '0 4px', fontFamily: 'inherit' } }, 'Change'),
          ' | ',
          h(FbaBadge, { status: plan.display_status || plan.status })
        )
      ),
      h('div', { style: { marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 } },
        plan.amazon_plan_id && h('span', { style: { fontFamily: 'inherit', fontSize: 11, color: C.textMuted } }, 'Plan: ' + plan.amazon_plan_id),
        plan.status === 'draft' && activeKey === 'review' && h(Btn, { size: 'sm', variant: 'primary', onClick: function() { setManualStep(1); } }, 'Continue to Create Plan ', h(LIcon, { name: 'arrow-right', size: 14 })),
        plan.status === 'draft' && activeKey === 'create' && !plan.amazon_plan_id && h(Btn, { size: 'sm', variant: 'primary', disabled: !!actionLoading, onClick: function() {
          // Save any pending expiration dates before creating plan
          var unsaved = items.filter(function(i) { return i.requires_expiration && (!i.expiration_date || editingExp[i.id]) && expirationDates[i.id]; });
          if (unsaved.length) {
            setActionLoading('create-plan');
            Promise.all(unsaved.map(function(i) {
              return api('/fba-shipments/items/' + i.id + '/expiration', { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ expiration_date: expirationDates[i.id] }) }).catch(function(e) { console.error('[silent-catch]', e); });
            })).then(function() { handleCreatePlan(); });
          } else { handleCreatePlan(); }
        } }, actionLoading === 'create-plan' ? 'Creating Plan...' : ['Create Inbound Plan ', h(LIcon, { key: 'ic', name: 'arrow-right', size: 14 })]),
        plan.status === 'draft' && activeKey === 'create' && plan.amazon_plan_id && h(Btn, { size: 'sm', variant: 'primary', onClick: function() { setManualStep(stepIndex('create') + 1); } }, 'Continue ', h(LIcon, { name: 'arrow-right', size: 14 })),
        activeKey === 'pack_boxes' && h(Btn, { size: 'sm', variant: 'primary', disabled: !allShipmentsPacked || unreceivedErroredCount > 0 || actionLoading === 'submit-packing', onClick: function() {
          // Missing-units interception. Amazon's setPackingInformation rejects with
          // "Package group did not contain expected items and/or quantities" whenever
          // packed totals don't match the original plan items. Per-flow recovery:
          //   - LTL Individual: declare a phantom box for the missing units (modal).
          //   - SPD: no prompt — submit-packing folds the short units into the last box
          //     automatically (declared to Amazon, flagged missing + Errored on the PO).
          //   - LTL Case Packed: cancel + recreate the plan (resubmit modal confirm).
          var hasMissingUnits = (plan && plan.shipments || []).some(function(s) {
            return (s.items || []).some(function(it) { return (it.quantity_missing || 0) > 0; });
          });
          if (hasMissingUnits) {
            if (isLtlIndividual) { setPhantomOpen(true); return; }
            var isSPD = plan && plan.shipment_type === 'SPD';
            if (!isSPD && plan && plan.amazon_plan_id) { setResubmitOpen(true); return; }
          }
          setActionLoading('submit-packing');
          api('/fba-shipments/plans/' + planId + '/submit-packing', { method: 'POST' })
            .then(function() { showToast(hasMissingUnits ? 'Packing submitted — short units declared to Amazon and flagged Errored on the PO' : 'Packing info submitted to Amazon', 'success'); setManualStep(null); loadPlan(); })
            .catch(function(err) { handleOperationError(err, 'Submit packing'); })
            .finally(function() { setActionLoading(null); });
        } }, actionLoading === 'submit-packing' ? 'Submitting...' : ['Continue ', h(LIcon, { key: 'ic', name: 'arrow-right', size: 14 })]),
        ['closed', 'cancelled'].indexOf(plan.status) === -1 && h('span', { 'data-fba-hover': 'cancel' }, h(Btn, { size: 'sm', onClick: handleCancelPlan, style: { background: 'rgba(248,113,113,0.1)', color: '#F87171', border: '1px solid rgba(248,113,113,0.3)' } }, h(LIcon, { name: 'x', size: 13 }), ' Cancel'))
      )
    ),

    // Stepper — per-step completion is computed from plan data, not just status
    // order: it drives both the green check (a fully-packed Pack Boxes shows done
    // before packing is submitted) and the forward-click frontier (the NEXT step
    // becomes clickable only once the current one is actually complete — the old
    // unconditional +1 peek bypassed the pack/confirm gates). 'review' always
    // allows forward nav: creating the plan happens on the next screen.
    (function() {
      var stepDoneByKey = {
        review: false,
        create: !!plan.amazon_plan_id,
        pack_boxes: allShipmentsPacked && unreceivedErroredCount === 0,
        packing: !!plan.packing_option_id,
        placement: !!plan.placement_option_id,
        transport: false,
        labels: false
      };
      var curKey = stepKeys[currentStep];
      var canAdvance = curKey === 'review' ? true : !!stepDoneByKey[curKey];
      var maxNav = Math.max(currentStep + (canAdvance ? 1 : 0), step);
      return h('div', { style: { display: 'flex', alignItems: 'center', margin: '0 0 24px 0', overflowX: 'auto', padding: '0 4px' } },
      steps.map(function(label, idx) {
        var isComplete = idx < currentStep || !!stepDoneByKey[stepKeys[idx]];
        var isActive = idx === step;
        var color = isComplete ? C.success : isActive ? C.accent : C.textDim;
        return h(Fragment, { key: idx },
          idx > 0 && h('div', { style: { flex: 1, height: 2, background: isComplete ? C.success : C.border, minWidth: 16 } }),
          h('div', { style: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, cursor: idx <= maxNav ? 'pointer' : 'default', minWidth: 70, opacity: idx > maxNav ? 0.4 : 1 },
            onClick: function() { if (idx <= maxNav) setManualStep(idx); } },
            h('div', { style: { width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600,
              border: '2px solid ' + color, background: isComplete ? C.success : isActive ? C.accent : 'transparent', color: (isComplete || isActive) ? '#fff' : C.textDim, transition: 'all 0.2s' } },
              isComplete ? h(LIcon, { name: 'check', size: 13, color: '#fff' }) : String(idx + 1)),
            h('span', { style: { fontSize: 9, fontWeight: isActive ? 600 : 400, color: color, textAlign: 'center', whiteSpace: 'nowrap' } }, label)
          )
        );
      })
      );
    })(),

    // Step content
    activeKey === 'review' && renderReviewItems(),
    activeKey === 'create' && renderCreatePlan(),
    activeKey === 'packing' && renderPacking(),
    activeKey === 'pack_boxes' && renderPackBoxes(),
    activeKey === 'placement' && renderPlacement(),
    activeKey === 'transport' && renderTransportation(),
    activeKey === 'labels' && plan.shipment_type === 'SPD' && plan.packing_mode === 'case_packed' && h('div', { style: { display: 'flex', gap: 8, alignItems: 'center', padding: '12px 16px', flexWrap: 'wrap', background: C.surface, borderRadius: 8, border: '1px solid ' + C.border, marginBottom: 12 } },
      h('button', { onClick: function() { try { printAllBoxLabels(); } catch(e) { appAlert('Error: ' + e.message); } }, style: { padding: '8px 16px', background: '#4F6EF7', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 } },
        h(LIcon, { name: 'printer', size: 14 }), ' Print All Box Labels'),
      h('button', { onClick: function() { try { printAllCaseLabelsSorted(); } catch(e) { appAlert('Error: ' + e.message); } }, style: { padding: '8px 16px', background: 'rgba(79,110,247,0.1)', color: C.accent, border: '1px solid rgba(79,110,247,0.3)', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 } },
        h(LIcon, { name: 'printer', size: 14 }), ' Print All Case Labels'),
      h('span', { style: { fontSize: 11, color: C.textMuted } }, 'Labels sorted by SKU')
    ),
    activeKey === 'labels' && renderLabelsShip(),

    // ─── Resubmit to Amazon confirmation modal ───
    h(Modal, { open: resubmitOpen, onClose: function() { if (!resubmitting) setResubmitOpen(false); }, title: 'Resubmit Plan to Amazon', width: 520 },
      h('div', null,
        h('div', { style: { padding: 12, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 6, marginBottom: 12, fontSize: 13, color: C.text, lineHeight: 1.5 } },
          h('div', { style: { fontWeight: 600, marginBottom: 6 } }, 'This will:'),
          h('ul', { style: { margin: 0, paddingLeft: 20, color: C.text } },
            h('li', null, 'Cancel the current Amazon plan ', plan && plan.amazon_plan_id && h('span', { style: { fontFamily: 'inherit', fontSize: 11, color: C.textMuted } }, '(' + plan.amazon_plan_id + ')')),
            h('li', null, 'Drop any SKUs that are fully marked missing'),
            h('li', null, 'Shrink partially-missing SKUs to their packed quantity'),
            h('li', null, 'Recreate the plan on Amazon and resubmit packing info'),
            h('li', null, 'Keep your local boxes intact — no re-packing needed')
          )
        ),
        h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12 } }, 'Takes ~20-40 seconds. You\'ll land at the Placement step when it finishes.'),
        h('div', { style: { display: 'flex', gap: 8, justifyContent: 'flex-end' } },
          h(Btn, { onClick: function() { setResubmitOpen(false); }, disabled: resubmitting }, 'Cancel'),
          h(Btn, { variant: 'primary', onClick: handleResubmit, disabled: resubmitting },
            resubmitting ? [h(LIcon, { key: 'l', name: 'loader-2', size: 14, spin: true }), ' Resubmitting...'] : 'Resubmit')
        )
      )
    ),

    // ─── Phantom box modal (LTL Individual) ───
    h(Modal, { open: phantomOpen, onClose: function() { if (!phantomCreating) setPhantomOpen(false); }, title: 'Declare Phantom Boxes for Missing Units', width: 560 },
      (function() {
        var missingByShipment = [];
        (plan && plan.shipments || []).forEach(function(s) {
          var ms = (s.items || []).filter(function(it) { return (it.quantity_missing || 0) > 0; });
          if (ms.length) missingByShipment.push({ shipmentName: s.amazon_shipment_id || ('Shipment ' + s.id), items: ms, boxes: fbaPhantomSplit(ms) });
        });
        var totalUnits = missingByShipment.reduce(function(sum, sh) {
          return sum + sh.items.reduce(function(s2, it) { return s2 + (it.quantity_missing || 0); }, 0);
        }, 0);
        var totalBoxes = missingByShipment.reduce(function(sum, sh) { return sum + sh.boxes.length; }, 0);
        var totalCatalog = missingByShipment.reduce(function(sum, sh) {
          return sum + sh.boxes.reduce(function(s2, b) { return s2 + b.catalog_weight; }, 0);
        }, 0);
        var unknownSkus = missingByShipment.reduce(function(sum, sh) {
          return sum + sh.items.filter(function(it) { return !fbaPhantomUnitLb(it).known; }).length;
        }, 0);
        return h('div', null,
          h('div', { style: { padding: 12, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 6, marginBottom: 12, fontSize: 13, color: C.text, lineHeight: 1.5 } },
            h('div', { style: { fontWeight: 600, marginBottom: 6 } }, totalUnits + ' unit' + (totalUnits === 1 ? '' : 's') + ' marked missing across ' + missingByShipment.length + ' shipment' + (missingByShipment.length === 1 ? '' : 's') + ' → ' + totalBoxes + ' phantom box' + (totalBoxes === 1 ? '' : 'es') + ' (' + fbaFmtLbs(totalCatalog) + ' catalog weight)'),
            h('div', { style: { color: C.textMuted, fontSize: 12 } }, 'LTL Individual locks shipment quantities with Amazon at placement. To pass the package-group check we\'ll declare phantom boxes (25×25×25, at most 39 lb each, split by catalog unit weight) containing the missing units in each affected shipment. Amazon caps jewelry and watch boxes at 40 lb and every box at 50 lb, so no phantom box can trip either.'),
            unknownSkus > 0 && h('div', { style: { color: C.textMuted, fontSize: 12, marginTop: 4 } }, unknownSkus + ' SKU' + (unknownSkus === 1 ? ' has' : 's have') + ' no catalog weight — 0.5 lb per unit assumed.')
          ),
          h('div', { style: { marginBottom: 12, maxHeight: 220, overflowY: 'auto', border: '1px solid ' + C.border, borderRadius: 6 } },
            missingByShipment.map(function(sh, si) {
              return h('div', { key: si, style: { padding: '8px 12px', borderBottom: si < missingByShipment.length - 1 ? '1px solid ' + C.border : 'none' } },
                h('div', { style: { fontFamily: 'inherit', fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 4 } },
                  sh.shipmentName + ' — ' + sh.boxes.length + ' phantom box' + (sh.boxes.length === 1 ? '' : 'es') + ': ' + sh.boxes.map(function(b) { return b.weight; }).join(', ') + ' lb'),
                sh.items.map(function(it, ii) {
                  var u = fbaPhantomUnitLb(it);
                  return h('div', { key: ii, style: { fontSize: 11, color: C.textMuted, paddingLeft: 8 } },
                    '• ' + (it.msku || '?') + ' — ' + (it.quantity_missing || 0) + ' units · ' + (u.known ? (Math.round(u.unit * 100) / 100) + ' lb/unit' : 'no catalog weight'));
                })
              );
            })
          ),
          h('div', { style: { fontSize: 11, color: C.textMuted, marginBottom: 12 } }, 'After confirming, packing info is submitted to Amazon and you move to Transportation. Phantom boxes get a Phantom badge in the box list and are excluded from real label printing. Any phantom boxes declared earlier are replaced.'),
          h('div', { style: { display: 'flex', gap: 8, justifyContent: 'flex-end' } },
            h(Btn, { onClick: function() { setPhantomOpen(false); }, disabled: phantomCreating }, 'Cancel'),
            h(Btn, { variant: 'primary', onClick: handlePhantomConfirm, disabled: phantomCreating },
              phantomCreating ? [h(LIcon, { key: 'l', name: 'loader-2', size: 14, spin: true }), ' Submitting...'] : 'Create phantom boxes & submit')
          )
        );
      })()
    ),

    // ─── Hazmat recovery modal (scan-to-bin during packing) ───
    h(Modal, { open: !!hazmatIssue, onClose: function() { setHazmatIssue(null); }, title: 'Hazmat Review Required — Receive to Bin', width: 600 },
      hazmatIssue && (function() {
        var fmtBin = window.formatBin || function(v) { return (v || '').toUpperCase(); };
        var allBinned = hazmatIssue.skus.every(function(s) { return (hazmatBins[s] || '').trim().length > 0; });
        var isLtlIndividual = plan && plan.shipment_type === 'LTL' && plan.packing_mode === 'individual';
        return h('div', null,
          h('div', { style: { padding: 12, background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 6, marginBottom: 12, fontSize: 13, color: '#F87171' } },
            h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontWeight: 600 } },
              h(LIcon, { name: 'alert-triangle', size: 16, color: '#F87171' }), 'Amazon flagged ', hazmatIssue.skus.length, ' SKU(s) for Hazmat review'
            ),
            h('div', { style: { fontSize: 12, color: C.textMuted } },
              isLtlIndividual
                ? 'LTL Individual: these items will be removed from the Amazon plan and added to the PO as unexpected items with the bins you enter. They\'ll appear in the "Received Unexpected Items" section below so you can continue packing the rest.'
                : 'They cannot ship FBA right now. Scan/type a bin location for each one — they\'ll be removed from the FBA plan and recorded as received into the warehouse so you can continue packing.')
          ),
          h('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 6 } }, 'Bin destinations'),
          h('div', { style: { display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14, maxHeight: 280, overflowY: 'auto' } },
            hazmatIssue.skus.map(function(s) {
              return h('div', { key: s, style: { display: 'grid', gridTemplateColumns: '1fr 200px', gap: 10, alignItems: 'center', padding: '8px 10px', background: C.surface, borderRadius: 6, border: '1px solid ' + C.border } },
                h('div', { style: { display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 } },
                  h(LIcon, { name: 'package', size: 14, color: C.textMuted }),
                  h('span', { style: { fontSize: 12, fontFamily: 'inherit', color: C.text, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, s)
                ),
                h('input', {
                  value: hazmatBins[s] || '',
                  placeholder: 'A-100',
                  onChange: function(e) {
                    var v = (e.target.value || '').toUpperCase();
                    setHazmatBins(function(prev) { var n = Object.assign({}, prev); n[s] = v; return n; });
                  },
                  onBlur: function() {
                    setHazmatBins(function(prev) {
                      var n = Object.assign({}, prev);
                      var v = n[s] || '';
                      n[s] = fmtBin(v) || v;
                      return n;
                    });
                  },
                  style: { width: '100%', padding: '7px 10px', fontSize: 13, border: '1px solid ' + C.border, borderRadius: 6, background: C.bg, color: C.text, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }
                })
              );
            })
          ),
          h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 } },
            h('span', { style: { fontSize: 11, color: C.textMuted } }, allBinned ? 'Ready — all SKUs have a bin.' : 'Enter a bin for every SKU to continue.'),
            h('div', { style: { display: 'flex', gap: 8 } },
              h(Btn, { onClick: function() { setHazmatIssue(null); }, disabled: cancellingHazmat }, 'Close'),
              h(Btn, { variant: 'primary', disabled: cancellingHazmat || !allBinned,
                onClick: function() {
                  setCancellingHazmat(true);
                  var items = hazmatIssue.skus.map(function(s) { return { sku: s, bin: (hazmatBins[s] || '').trim() }; });
                  api('/fba-shipments/plans/' + planId + '/receive-hazmat-skus-to-bin', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: items }) })
                    .then(function(res) {
                      var n = (res.received || []).length;
                      var msg = res.treated_as_unexpected
                        ? 'Removed ' + n + ' Hazmat SKU(s) from FBA plan — added to PO as unexpected items. Continue packing.'
                        : 'Received ' + n + ' Hazmat SKU(s) to bin. Continue packing.';
                      showToast(msg, 'success');
                      setHazmatIssue(null);
                      setPackingReloadKey(function(k) { return k + 1; });
                      loadPlan();
                    })
                    .catch(function(e) { showToast('Receive failed: ' + e.message, 'error'); })
                    .finally(function() { setCancellingHazmat(false); });
                }
              }, cancellingHazmat ? 'Receiving…' : h(React.Fragment, null, h(LIcon, { name: 'check', size: 14 }), ' Receive to bins & continue'))
            )
          )
        );
      })()
    ),
    // ── Split chooser — pick SPD/LTL + packing for the new plan (like New Shipment) ──
    h(Modal, { open: !!splitModal, onClose: function() { setSplitModal(null); }, title: 'Split Into a New Plan', width: 640 },
      splitModal && (function() {
        var ships = splitModal.ships || [];
        var mskuSet = {}; ships.forEach(function(s) { (s.items || []).forEach(function(it) { if (it.msku) mskuSet[it.msku] = true; }); });
        var skuCount = Object.keys(mskuSet).length;
        var unitCount = ships.reduce(function(a, s) { return a + (s.items || []).reduce(function(b, it) { return b + (it.quantity || 0); }, 0); }, 0);
        var typeOpts = [{ key: 'SPD', label: 'Small Parcel (SPD)', desc: 'Boxes ship via UPS/FedEx/USPS to Amazon FCs' }, { key: 'LTL', label: 'LTL / Pallet', desc: 'Palletized freight via an LTL carrier' }];
        var packOpts = [{ key: 'individual', label: 'Individual Units', desc: 'Mixed SKUs per box, packed individually' }, { key: 'case_packed', label: 'Case Packed', desc: 'Each box contains ONE SKU at a fixed case qty' }];
        return h('div', null,
          h('div', { style: { fontSize: 13, color: C.text, marginBottom: 14, padding: '10px 12px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 6 } },
            'Moving ' + ships.length + ' shipment(s) — ' + skuCount + ' SKU(s), ' + unitCount + ' unit(s) — into a new plan. This cancels the current Amazon plan and resets THIS plan to draft (re-run Create → Packing → Placement without these items).'),
          h('label', { style: { fontSize: 13, fontWeight: 500, color: C.textMuted } }, 'New plan — shipment type'),
          h('div', { style: { display: 'flex', gap: 10, marginTop: 6, marginBottom: 14 } },
            typeOpts.map(function(t) {
              var sel = splitType === t.key;
              return h('button', { key: t.key, type: 'button', onClick: function() { setSplitType(t.key); }, style: { flex: 1, padding: '12px 14px', borderRadius: 8, border: '2px solid ' + (sel ? C.accent : C.border), background: sel ? C.accent + '12' : 'transparent', color: sel ? C.accent : C.text, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', textAlign: 'left' } },
                h('div', { style: { fontWeight: 600, fontSize: 14, marginBottom: 4 } }, t.label),
                h('div', { style: { fontSize: 11, color: C.textMuted } }, t.desc));
            })
          ),
          h('label', { style: { fontSize: 13, fontWeight: 500, color: C.textMuted } }, 'Packing'),
          h('div', { style: { display: 'flex', gap: 10, marginTop: 6 } },
            packOpts.map(function(m) {
              var sel = splitPacking === m.key;
              return h('button', { key: m.key, type: 'button', onClick: function() { setSplitPacking(m.key); }, style: { flex: 1, padding: '12px 14px', borderRadius: 8, border: '2px solid ' + (sel ? C.accent : C.border), background: sel ? C.accent + '12' : 'transparent', color: sel ? C.accent : C.text, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', textAlign: 'left' } },
                h('div', { style: { fontWeight: 600, fontSize: 14, marginBottom: 4 } }, m.label),
                h('div', { style: { fontSize: 11, color: C.textMuted } }, m.desc));
            })
          ),
          h('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 } },
            h(Btn, { onClick: function() { setSplitModal(null); } }, 'Cancel'),
            h(Btn, { variant: 'primary', disabled: actionLoading === 'split-shipment', onClick: handleSplitConfirm }, h(LIcon, { name: 'split', size: 14 }), actionLoading === 'split-shipment' ? ' Creating…' : ' Create split plan')
          )
        );
      })()
    ),
    // ── "Items Amazon rejected" panel — per-item create errors surfaced for the user to resolve ──
    // ── Cancel plan dialog: the receiving decision (keep vs reverse) ──
    h(Modal, { open: !!cancelState, onClose: function() { if (!(cancelState && cancelState.submitting)) setCancelState(null); }, title: 'Cancel FBA Shipment', width: 500 },
      cancelState && h('div', null,
        h('div', { style: { fontSize: 13, color: C.text, marginBottom: 12, lineHeight: 1.5 } },
          'Cancel ', h('span', { style: { fontWeight: 600 } }, plan.name || ('Plan ' + planId)),
          cancelState.cancelSiblings ? ' and its related plan(s)' : '', ' in Amazon and here. This cannot be undone.'),
        cancelState.packed > 0 && h('label', { style: { display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', background: C.surface, borderRadius: 6, border: '1px solid ' + C.border, cursor: 'pointer', marginBottom: 8 } },
          h('input', { type: 'checkbox', checked: !!cancelState.keepReceiving, disabled: cancelState.submitting,
            onChange: function(e) { var v = e.target.checked; setCancelState(function(s) { return s ? Object.assign({}, s, { keepReceiving: v }) : null; }); },
            style: { marginTop: 2, width: 15, height: 15, accentColor: C.accent, cursor: 'pointer', flexShrink: 0 } }),
          h('div', null,
            h('div', { style: { fontWeight: 600, color: C.text, fontSize: 13 } }, 'Keep the PO receiving as is'),
            h('div', { style: { fontSize: 11.5, color: C.textMuted, marginTop: 3, lineHeight: 1.45 } },
              'The ' + cancelState.packed + ' packed units stay received on the PO and are not returned to staging. Use this when the boxes ship on another plan (a copy of this one). Unticked, the receiving is reversed and the units go back to the staging bins.'),
            cancelState.poReceived && h('div', { style: { fontSize: 11.5, color: C.success || C.accent, marginTop: 4, fontWeight: 600 } },
              'The PO is already received, so this is on by default: cancelling changes nothing on the PO.'))),
        cancelState.packed > 0 && !cancelState.keepReceiving && h('div', { style: { fontSize: 11.5, color: C.warning, marginBottom: 12, lineHeight: 1.45 } },
          'Reversing: ' + cancelState.packed + ' units come off the PO and return to staging.'),
        h('div', { style: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 } },
          h(Btn, { onClick: function() { setCancelState(null); }, disabled: cancelState.submitting }, 'Keep Shipment'),
          h(Btn, { variant: 'primary', onClick: submitCancelPlan, disabled: cancelState.submitting, style: { background: C.danger, borderColor: C.danger } },
            cancelState.submitting ? [h(LIcon, { key: 'l', name: 'loader-2', size: 14, spin: true }), ' Cancelling...'] : [h(LIcon, { key: 'x', name: 'x-circle', size: 14 }), ' Cancel shipment']))
      )),
    h(Modal, { open: !!createErrors, onClose: function() { setCreateErrors(null); loadPlan(); }, title: 'Items Amazon rejected', width: 920 },
      createErrors && (function() {
        var its = createErrors.items || [];
        var REASON_LABEL = { brand_registry: 'Brand Registry', not_in_catalog: 'Not in Catalog', invalid_msku: 'Invalid SKU', compliance: 'Safety Compliance', prep_required: 'Prep Required', missing_title: 'Missing Title', missing_category: 'Missing Category', missing_dimensions: 'Missing Dimensions', missing_weight: 'Missing Weight', missing_info: 'Missing Info', approval_required: 'Approval Required', hazmat: 'Hazmat', no_fc_capacity: 'No FC Capacity', catalog_lag: 'Catalog Lag', other: 'Other' };
        var REASON_COLOR = { brand_registry: '#F87171', not_in_catalog: '#F87171', invalid_msku: '#F87171', compliance: '#F87171', prep_required: '#FBBF24', missing_title: '#FBBF24', missing_category: '#FBBF24', missing_dimensions: '#FBBF24', missing_weight: '#FBBF24', missing_info: '#FBBF24', approval_required: '#FBBF24', hazmat: '#F87171', no_fc_capacity: '#FBBF24', catalog_lag: '#FBBF24', other: '#9CA3AF' };
        var reasonsOf = function(it) { return (it.reasons && it.reasons.length) ? it.reasons : (it.reason ? [it.reason] : ['other']); };
        var byReason = {}; its.forEach(function(it) { reasonsOf(it).forEach(function(r) { byReason[r] = (byReason[r] || 0) + 1; }); });
        var reasonSummary = Object.keys(byReason).sort(function(a, b) { return byReason[b] - byReason[a]; });
        var effTab = (activeErrorTab === 'all' || byReason[activeErrorTab]) ? activeErrorTab : 'all';
        var filtered = effTab === 'all' ? its : its.filter(function(it) { return reasonsOf(it).indexOf(effTab) !== -1; });
        var tabs = [{ key: 'all', label: 'All', count: its.length }].concat(reasonSummary.map(function(r) { return { key: r, label: REASON_LABEL[r] || r, count: byReason[r] }; }));
        var downloadReport = function() {
          if (!window.XLSX) { showToast('Excel library not loaded', 'error'); return; }
          var rows = its.map(function(it) {
            return {
              'SKU#': it.msku,
              'PO#': (it.po_numbers || []).join(', '),
              'Item Name': it.title || '',
              'Error Type': reasonsOf(it).map(function(r) { return REASON_LABEL[r] || r; }).join(', '),
              'Amazon Error Detail': (it.amazonMessages && it.amazonMessages.length) ? it.amazonMessages.join(' | ') : (it.message || '')
            };
          });
          var ws = XLSX.utils.json_to_sheet(rows, { header: ['SKU#', 'PO#', 'Item Name', 'Error Type', 'Amazon Error Detail'] });
          ws['!cols'] = [{ wch: 20 }, { wch: 14 }, { wch: 42 }, { wch: 30 }, { wch: 60 }];
          var wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, 'Rejected Items');
          XLSX.writeFile(wb, 'rejected-items-plan-' + planId + '.xlsx');
        };
        return h('div', { style: { fontSize: 14 } },
          h('div', { style: { padding: 14, background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 8, marginBottom: 12, fontSize: 14, color: C.text } },
            h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5, fontWeight: 600, fontSize: 15 } },
              h(LIcon, { name: 'alert-triangle', size: 18, color: '#F87171' }), 'Amazon rejected ', its.length, ' item(s) on this plan'
            ),
            h('div', { style: { fontSize: 13, color: C.textMuted } }, 'Resolve each item: fix it in Seller Central, move it to Errored (binned), or remove its entire PO from the shipment (reverts to checked-in). Then create the plan with the rest.')
          ),
          // Tabs by error type
          h('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 7, marginBottom: 12 } },
            tabs.map(function(t) {
              var active = effTab === t.key;
              var col = t.key === 'all' ? C.accent : (REASON_COLOR[t.key] || C.accent);
              return h('button', { key: t.key, type: 'button', onClick: function() { setActiveErrorTab(t.key); },
                style: { cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: '6px 14px', borderRadius: 16, fontFamily: 'inherit', border: '1px solid ' + (active ? col : C.border), background: active ? (col + '22') : 'transparent', color: active ? col : C.textMuted } },
                t.label + ' (' + t.count + ')');
            })
          ),
          effTab === 'prep_required' && (function() {
            var prepMskus = its.filter(function(it) { return reasonsOf(it).indexOf('prep_required') !== -1; }).map(function(it) { return it.msku; });
            return h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: 10, marginBottom: 10, background: 'rgba(96,165,250,0.08)', border: '1px solid rgba(96,165,250,0.35)', borderRadius: 6 } },
              h('span', { style: { fontSize: 13, color: C.text, fontWeight: 600 } }, 'Set prep for these ' + prepMskus.length + ' item(s):'),
              h('select', { value: prepChoice, onChange: function(e) { setPrepChoice(e.target.value); }, style: { fontSize: 13, padding: '6px 10px', borderRadius: 6, background: C.surface, color: C.text, border: '1px solid ' + C.border } },
                PREP_OPTIONS.map(function(o) { return h('option', { key: o.value, value: o.value }, o.label); })),
              h(Btn, { size: 'md', variant: 'primary', disabled: actionLoading === 'set-prep', onClick: function() { handleApplyPrep(prepMskus); } }, h(LIcon, { name: 'wand-2', size: 14 }), actionLoading === 'set-prep' ? ' Applying…' : ' Apply prep & re-create'),
              h('span', { style: { fontSize: 11, color: C.textMuted, flexBasis: '100%' } }, 'Sends the prep to Amazon (setPrepDetails) and re-creates the plan. If Amazon rejects a prep type, the error shows here — pick another.')
            );
          })(),
          h('div', { style: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginBottom: 8 } },
            h(Btn, { size: 'md', onClick: downloadReport }, h(LIcon, { name: 'download', size: 14 }), ' Download Report (Excel)'),
            filtered.length > 1 && h(Btn, { size: 'md', onClick: function() { handleMoveAllToErrored(filtered); } }, h(LIcon, { name: 'archive', size: 14 }), ' Move all ' + filtered.length + ' → Errored')
          ),
          h('div', { style: { display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 460, overflowY: 'auto' } },
            filtered.map(function(it) {
              var rs = reasonsOf(it);
              var detail = (it.amazonMessages && it.amazonMessages.length) ? it.amazonMessages.join(' • ') : it.message;
              var pos = it.po_numbers || [];
              return h('div', { key: it.msku, style: { padding: '12px 14px', background: C.surface, borderRadius: 8, border: '1px solid ' + C.border } },
                h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' } },
                  h(CopyCell, { value: it.msku, style: { fontSize: 14, fontFamily: 'inherit', color: C.text, fontWeight: 600 } }),
                  rs.map(function(r) {
                    return h('span', { key: r, style: { fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 4, background: 'rgba(255,255,255,0.06)', color: REASON_COLOR[r] || C.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em' } }, REASON_LABEL[r] || r);
                  }),
                  pos.length > 0 && h('span', { style: { display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: C.textMuted } },
                    pos.map(function(po) { return h(CopyCell, { key: po, value: po, style: { fontFamily: 'inherit', color: C.accent, fontWeight: 600 } }, 'PO ' + po); })
                  ),
                  it.title && h('span', { style: { fontSize: 12, color: C.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260 } }, it.title)
                ),
                detail && h('div', { style: { fontSize: 13, color: C.textMuted, marginTop: 6 } }, detail),
                h('div', { style: { display: 'flex', gap: 8, marginTop: 10 } },
                  h(Btn, { size: 'md', onClick: function() { handleMoveToErrored(it); } }, h(LIcon, { name: 'archive', size: 14 }), ' Move to Errored'),
                  pos.length > 0 && h(Btn, { size: 'md', onClick: function() { handleRemovePO(it); }, style: { color: '#F87171' } }, h(LIcon, { name: 'package-x', size: 14 }), ' Remove PO')
                )
              );
            })
          ),
          h('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 } },
            h(Btn, { size: 'md', onClick: function() { setCreateErrors(null); loadPlan(); } }, 'Close'),
            h(Btn, { size: 'md', variant: 'primary', onClick: function() { setCreateErrors(null); handleCreatePlan(); } }, h(LIcon, { name: 'refresh-cw', size: 16 }), ' Create Plan with remaining')
          )
        );
      })()
    )
  );

  // NOTE: function declarations (hoisted) — like handleSetPlanShipping below, these sit textually
  // after this component's return, so as var-assigned expressions they were still undefined when
  // the Review step rendered: the ship-from "Change" button and the modal's Save were no-ops.
  // (Review finding, 2026-09-02.)
  function detailAddrToSource(a) {
    return { name: a.name, addressLine1: a.address_line1, city: a.city, stateOrProvinceCode: a.state, postalCode: a.postal_code, countryCode: a.country_code || 'US', phoneNumber: a.phone || '' };
  }

  function handleSaveAddress() {
    var doSave = function(sourceAddr) {
      api('/fba-shipments/plans/' + planId, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ source_address: sourceAddr }) })
        .then(function() { showToast('Ship-from address updated', 'success'); setEditAddress(false); loadPlan(); })
        .catch(function(e) { showToast(e.message, 'error'); });
    };
    if (detailSelectedAddr === 'new') {
      if (!detailNewAddrForm.label || !detailNewAddrForm.address_line1) { showToast('Label and address are required', 'error'); return; }
      api('/fba-shipments/ship-from-addresses', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(detailNewAddrForm) })
        .then(function(saved) { doSave(detailAddrToSource(saved)); })
        .catch(function(e) { showToast(e.message, 'error'); });
    } else {
      var addr = detailShipFromAddrs.find(function(a) { return String(a.id) === detailSelectedAddr; });
      if (!addr) { showToast('Select an address', 'error'); return; }
      doSave(detailAddrToSource(addr));
    }
  }

  function openAddressEdit() {
    api('/fba-shipments/ship-from-addresses').then(function(addrs) {
      setDetailShipFromAddrs(addrs);
      setDetailSelectedAddr(addrs.length ? String(addrs[0].id) : 'new');
      setDetailNewAddrForm({ label: '', name: '', address_line1: '', city: '', state: '', postal_code: '', country_code: 'US', phone: '' });
      setEditAddress(true);
    }).catch(function(e) { showToast(e.message, 'error'); });
  }

  function renderReviewItems() {
    var addr = plan.source_address || {};
    var isDraftUncreated = plan.status === 'draft' && !plan.amazon_plan_id;
    return h('div', null,
      // Shipment type / packing chooser (draft, not yet created in Amazon) — shown on the first
      // step so the user can pick the type when they open the plan (e.g. a split-off plan).
      isDraftUncreated && h(Card, { style: { marginBottom: 12 } },
        h('h3', { style: { fontSize: 14, fontWeight: 600, color: C.text, margin: '0 0 10px 0' } }, 'Shipment Type'),
        h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' } },
          h('span', { style: { fontSize: 12, color: C.text, fontWeight: 600, minWidth: 70 } }, 'Type:'),
          ['SPD', 'LTL'].map(function(t) {
            var active = (plan.shipment_type || 'SPD') === t;
            return h('button', { key: t, type: 'button', disabled: actionLoading === 'set-type', onClick: function() { handleSetPlanShipping(t, null); },
              style: { cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 6, fontFamily: 'inherit', border: '1px solid ' + (active ? C.accent : C.border), background: active ? C.accent : 'transparent', color: active ? '#fff' : C.textMuted } },
              t === 'SPD' ? 'SPD (Small Parcel)' : 'LTL (Freight)');
          })
        ),
        h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' } },
          h('span', { style: { fontSize: 12, color: C.text, fontWeight: 600, minWidth: 70 } }, 'Packing:'),
          [{ k: 'individual', label: 'Individual Units' }, { k: 'case_packed', label: 'Case-packed' }].map(function(pm) {
            var active = (plan.packing_mode || 'individual') === pm.k;
            return h('button', { key: pm.k, type: 'button', disabled: actionLoading === 'set-type', onClick: function() { handleSetPlanShipping(null, pm.k); },
              style: { cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 6, fontFamily: 'inherit', border: '1px solid ' + (active ? C.accent : C.border), background: active ? C.accent : 'transparent', color: active ? '#fff' : C.textMuted } },
              pm.label);
          })
        )
      ),
      // Ship From Address card
      h(Card, { style: { marginBottom: 12 } },
        h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 } },
          h('h3', { style: { fontSize: 14, fontWeight: 600, color: C.text, margin: 0 } }, 'Ship From Address'),
          h('button', { onClick: openAddressEdit, style: { background: 'none', border: 'none', cursor: 'pointer', color: C.accent, fontSize: 12, fontWeight: 500 } }, h(LIcon, { name: 'edit-2', size: 13 }), ' Change')
        ),
        h('div', { style: { fontSize: 13, color: C.text, lineHeight: 1.6 } },
          addr.name && h('div', { style: { fontWeight: 600 } }, addr.name),
          addr.addressLine1 && h('div', null, addr.addressLine1),
          h('div', null, [addr.city, addr.stateOrProvinceCode, addr.postalCode].filter(Boolean).join(', ')),
          addr.countryCode && h('div', { style: { color: C.textMuted } }, addr.countryCode),
          addr.phoneNumber && h('div', { style: { color: C.textMuted } }, addr.phoneNumber)
        )
      ),
      h(Card, null,
        h('h3', { style: { fontSize: 14, fontWeight: 600, marginBottom: 12, color: C.text } }, 'Items to Ship'),
        h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12 } }, items.length + ' SKUs, ' + totalUnits + ' total units'),
        h('table', { style: { width: '100%', fontSize: 12 } },
          h('thead', null, h('tr', null,
            ['SKU', 'FNSKU', 'ASIN', 'Product', 'PO #', 'Location', 'Units', 'Exp.'].map(function(col) { return h('th', { key: col, style: { textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid ' + C.border, color: C.textMuted, fontWeight: 500, fontSize: 11 } }, col); })
          )),
          h('tbody', null, items.map(function(item) {
            var asinUrl = item.asin ? (plan.marketplace_id === 'A2EUQ1WTGCTBG2' ? 'https://www.amazon.ca/dp/' : 'https://www.amazon.com/dp/') + item.asin : null;
            return h('tr', { key: item.id },
              h('td', { style: { padding: '6px 8px', fontFamily: 'inherit', fontWeight: 600 } }, item.msku),
              h('td', { style: { padding: '6px 8px', fontFamily: 'inherit', fontSize: 11 } }, item.fnsku || '\u2014'),
              h('td', { style: { padding: '6px 8px', fontFamily: 'inherit', fontSize: 11 } }, asinUrl
                ? h('a', { href: asinUrl, target: '_blank', rel: 'noopener noreferrer', style: { color: C.accent, textDecoration: 'none' } }, item.asin, h(LIcon, { name: 'external-link', size: 10, style: { marginLeft: 4, verticalAlign: 'middle' } }))
                : '\u2014'),
              h('td', { style: { padding: '6px 8px' } }, item.vendor_title || ''),
              h('td', { style: { padding: '6px 8px', fontFamily: 'inherit', fontSize: 11 } }, item.po_number || '\u2014'),
              h('td', { style: { padding: '6px 8px', fontSize: 11 } }, item.warehouse_location || '\u2014'),
              h('td', { style: { padding: '6px 8px', fontWeight: 600 } }, item.quantity),
              h('td', { style: { padding: '6px 8px', fontSize: 11 } },
                item.requires_expiration
                  ? (item.expiration_date && !editingExp[item.id]
                    ? h('div', { style: { display: 'flex', alignItems: 'center', gap: 6 } },
                        h(LIcon, { name: 'check-circle', size: 16, color: C.success }),
                        h('span', { style: { color: C.success, fontSize: 12 } }, item.expiration_date.substring(0, 10)),
                        h('button', { onClick: function() {
                          var dates = Object.assign({}, expirationDates);
                          dates[item.id] = item.expiration_date.substring(0, 10);
                          setExpirationDates(dates);
                          var editing = Object.assign({}, editingExp); editing[item.id] = true;
                          setEditingExp(editing);
                        }, style: { background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, fontSize: 11 } }, 'edit')
                      )
                    : h('input', {
                        type: 'date',
                        value: expirationDates[item.id] || '',
                        min: minExpDate,
                        onChange: function(e) {
                          var dates = Object.assign({}, expirationDates);
                          dates[item.id] = e.target.value;
                          setExpirationDates(dates);
                        },
                        style: { padding: '4px 8px', borderRadius: 4, border: '1px solid ' + C.border, background: '#fff', color: '#111', fontSize: 11, colorScheme: 'light', width: 130 }
                      }))
                  : '\u2014'
              )
            );
          }))
        ),
        h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 } },
          h('div', { style: { fontSize: 12, color: C.textMuted } },
            'Marketplace: ' + (plan.marketplace_id === 'A2EUQ1WTGCTBG2' ? 'Canada' : 'US')
          ),
          Object.keys(expirationDates).length > 0 && h(Btn, { size: 'sm', variant: 'primary', onClick: handleSaveAllExpirations, disabled: actionLoading === 'save-expirations' },
            actionLoading === 'save-expirations' ? 'Saving...' : 'Save Expiration Dates')
        )
      ),
      // Address edit modal
      h(Modal, { open: editAddress, onClose: function() { setEditAddress(false); }, title: 'Change Ship From Address', width: 480 },
        h('div', { style: { display: 'flex', flexDirection: 'column', gap: 12 } },
          h('select', { value: detailSelectedAddr, onChange: function(e) { setDetailSelectedAddr(e.target.value); },
            style: { width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid ' + C.border, background: C.bg, color: C.text, fontSize: 13 }
          },
            detailShipFromAddrs.map(function(a) {
              return h('option', { key: a.id, value: String(a.id) }, a.label + (a.city ? ' — ' + a.city + ', ' + a.state : ''));
            }),
            h('option', { value: 'new' }, '+ Create New Address')
          ),
          detailSelectedAddr !== 'new' && (function() {
            var sa = detailShipFromAddrs.find(function(a) { return String(a.id) === detailSelectedAddr; });
            if (!sa) return null;
            return h('div', { style: { fontSize: 12, color: C.textMuted, padding: '8px 10px', background: C.surface, borderRadius: 6, lineHeight: 1.5 } },
              sa.name && h('div', { style: { fontWeight: 500, color: C.text } }, sa.name),
              h('div', null, sa.address_line1),
              h('div', null, [sa.city, sa.state, sa.postal_code].filter(Boolean).join(', ') + (sa.country_code ? ' ' + sa.country_code : '')),
              sa.phone && h('div', null, 'Phone: ' + sa.phone)
            );
          })(),
          detailSelectedAddr === 'new' && h('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 } },
            h(Inp, { label: 'Label *', value: detailNewAddrForm.label, onChange: function(e) { setDetailNewAddrForm(Object.assign({}, detailNewAddrForm, { label: e.target.value })); }, placeholder: 'e.g. Main Warehouse' }),
            h(Inp, { label: 'Name', value: detailNewAddrForm.name, onChange: function(e) { setDetailNewAddrForm(Object.assign({}, detailNewAddrForm, { name: e.target.value })); }, placeholder: 'Company / person name' }),
            h(Inp, { label: 'Address *', value: detailNewAddrForm.address_line1, onChange: function(e) { setDetailNewAddrForm(Object.assign({}, detailNewAddrForm, { address_line1: e.target.value })); } }),
            h(Inp, { label: 'City', value: detailNewAddrForm.city, onChange: function(e) { setDetailNewAddrForm(Object.assign({}, detailNewAddrForm, { city: e.target.value })); } }),
            h(StateSelect, { label: 'State', country: detailNewAddrForm.country_code || 'US', value: detailNewAddrForm.state, onChange: function(e) { setDetailNewAddrForm(Object.assign({}, detailNewAddrForm, { state: e.target.value })); } }),
            h(Inp, { label: 'ZIP', value: detailNewAddrForm.postal_code, onChange: function(e) { setDetailNewAddrForm(Object.assign({}, detailNewAddrForm, { postal_code: e.target.value })); } }),
            h(Inp, { label: 'Country', value: detailNewAddrForm.country_code, onChange: function(e) { setDetailNewAddrForm(Object.assign({}, detailNewAddrForm, { country_code: e.target.value })); } }),
            h(Inp, { label: 'Phone', value: detailNewAddrForm.phone, onChange: function(e) { setDetailNewAddrForm(Object.assign({}, detailNewAddrForm, { phone: e.target.value })); } })
          ),
          h('div', { style: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 } },
            h(Btn, { onClick: function() { setEditAddress(false); } }, 'Cancel'),
            h(Btn, { variant: 'primary', onClick: handleSaveAddress }, 'Save')
          )
        )
      )
    );
  }

  // Set shipment type (SPD/LTL) and/or packing (individual/case_packed) on a draft plan — lets
  // the user choose when they start working on a split-off (or any draft) plan, before creating
  // it in Amazon. Pass null to keep the current value for the other field.
  // Also used on a CREATED plan while canChangeShipping holds (2026-10-30): the server swaps the
  // status to the new track's post-create step, so the manual step is cleared and the wizard
  // re-derives it (Pack Boxes for SPD / LTL case-packed, Placement for LTL Individual).
  // NOTE: function declaration (hoisted) — it's defined textually after this component's return,
  // so a `var` assignment here would never run and the toggle onClick would throw "not a function".
  function handleSetPlanShipping(nextType, nextMode) {
    var t = nextType || plan.shipment_type || 'SPD';
    var m = nextMode || plan.packing_mode || 'individual';
    if ((plan.shipment_type || 'SPD') === t && (plan.packing_mode || 'individual') === m) return;
    var wasCreated = !!plan.amazon_plan_id;
    setActionLoading('set-type');
    api('/fba-shipments/plans/' + planId, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ shipment_type: t, packing_mode: m }) })
      .then(function() {
        showToast('Set to ' + t + ' · ' + (m === 'case_packed' ? 'Case-packed' : 'Individual') + (wasCreated ? ' — Amazon plan kept' : ''), 'success');
        if (wasCreated) setManualStep(null);
        loadPlan();
      })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setActionLoading(''); });
  }

  function renderCreatePlan() {
    var alreadyCreated = !!plan.amazon_plan_id;
    var expItems = items.filter(function(i) { return i.requires_expiration; });
    var missingExp = expItems.filter(function(i) { return !i.expiration_date && !expirationDates[i.id]; });
    var unsavedExp = expItems.filter(function(i) { return !i.expiration_date && expirationDates[i.id]; });

    return h('div', null,
      // Expiration dates section (if any items require them)
      expItems.length > 0 && h(Card, { style: { marginBottom: 12 } },
        h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 } },
          h(LIcon, { name: 'alert-triangle', size: 16, color: '#f59e0b' }),
          h('h3', { style: { fontSize: 14, fontWeight: 600, color: C.text, margin: 0 } }, 'Expiration Dates Required')
        ),
        h('p', { style: { fontSize: 12, color: C.textMuted, marginBottom: 8 } }, 'Amazon requires expiration dates for the following items. Dates must be at least 2 years out.'),
        h('div', { style: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, padding: '8px 12px', background: 'rgba(79,110,247,0.06)', borderRadius: 6, border: '1px solid ' + C.border } },
          h('span', { style: { fontSize: 12, color: C.text, fontWeight: 500 } }, 'Set all to:'),
          h('input', {
            type: 'date',
            min: minExpDate,
            onChange: function(e) { if (e.target.value) handleBulkExpiration(e.target.value); },
            style: { padding: '4px 8px', borderRadius: 4, border: '1px solid ' + C.border, background: '#fff', color: '#111', fontSize: 12, colorScheme: 'light' }
          })
        ),
        h('table', { style: { width: '100%', fontSize: 12, marginBottom: 12 } },
          h('thead', null, h('tr', null,
            ['SKU', 'Product', 'Qty', 'Expiration Date'].map(function(col) {
              return h('th', { key: col, style: { textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid ' + C.border, color: C.textMuted, fontWeight: 500, fontSize: 11 } }, col);
            })
          )),
          h('tbody', null, expItems.map(function(item) {
            var currentDate = expirationDates[item.id] || (item.expiration_date ? item.expiration_date.substring(0, 10) : '');
            return h('tr', { key: item.id },
              h('td', { style: { padding: '6px 8px', fontFamily: 'inherit', fontWeight: 600 } }, item.msku),
              h('td', { style: { padding: '6px 8px' } }, item.vendor_title || ''),
              h('td', { style: { padding: '6px 8px', fontWeight: 600 } }, item.quantity),
              h('td', { style: { padding: '6px 8px' } },
                item.expiration_date && !editingExp[item.id]
                  ? h('div', { style: { display: 'flex', alignItems: 'center', gap: 6 } },
                      h(LIcon, { name: 'check-circle', size: 16, color: C.success }),
                      h('span', { style: { color: C.success, fontSize: 12 } }, item.expiration_date.substring(0, 10)),
                      h('button', { onClick: function() {
                        var dates = Object.assign({}, expirationDates);
                        dates[item.id] = item.expiration_date.substring(0, 10);
                        setExpirationDates(dates);
                        // Open the input for editing; the saved date stays on the item
                        var editing = Object.assign({}, editingExp); editing[item.id] = true;
                        setEditingExp(editing);
                      }, style: { background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, fontSize: 11 } }, 'edit')
                    )
                  : h('input', {
                      type: 'date',
                      value: currentDate,
                      min: minExpDate,
                      onChange: function(e) {
                        var dates = Object.assign({}, expirationDates);
                        dates[item.id] = e.target.value;
                        setExpirationDates(dates);
                      },
                      style: { padding: '4px 8px', borderRadius: 4, border: '1px solid ' + C.border, background: '#fff', color: '#111', fontSize: 12, colorScheme: 'light' }
                    })
              )
            );
          }))
        ),
        Object.keys(expirationDates).length > 0 && h('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } },
          h(Btn, { size: 'sm', variant: 'primary', onClick: handleSaveAllExpirations, disabled: actionLoading === 'save-expirations' },
            actionLoading === 'save-expirations' ? 'Saving...' : 'Save Expiration Dates')
        )
      ),

      // Outcome of the last create attempt, stored server-side (last_create_result) so it survives
      // a browser timeout. Item errors already handled (SKU no longer on the plan) are dropped.
      !alreadyCreated && plan.last_create_result && !plan.last_create_result.ok && (function() {
        var lr = plan.last_create_result;
        var hadItemErrors = Array.isArray(lr.create_item_errors) && lr.create_item_errors.length > 0;
        var errs = hadItemErrors ? lr.create_item_errors.filter(function(e) { return items.some(function(i) { return i.msku === e.msku; }); }) : [];
        if (hadItemErrors && !errs.length) return null;
        var when = lr.at ? new Date(lr.at).toLocaleString() : '';
        return h(Card, { style: { marginBottom: 12, borderColor: '#f59e0b' } },
          h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 } },
            h(LIcon, { name: 'alert-triangle', size: 16, color: '#f59e0b' }),
            h('h3', { style: { fontSize: 14, fontWeight: 600, color: C.text, margin: 0 } }, 'Last create attempt did not complete'),
            when && h('span', { style: { fontSize: 11, color: C.textMuted, marginLeft: 'auto' } }, when)
          ),
          h('p', { style: { fontSize: 12, color: C.textMuted, marginBottom: errs.length ? 10 : 0 } }, lr.error || 'Amazon rejected the plan.'),
          errs.length > 0 && h(Btn, { size: 'sm', onClick: function() { setCreateErrors({ items: errs }); } }, 'Review ' + errs.length + ' Rejected Item' + (errs.length === 1 ? '' : 's'))
        );
      })(),

      h(Card, null,
        h('h3', { style: { fontSize: 14, fontWeight: 600, marginBottom: 12, color: C.text } }, 'Create Inbound Plan in Amazon'),
        alreadyCreated
          ? h('div', null,
              h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', background: 'rgba(52,211,153,0.1)', borderRadius: 8, marginBottom: 12 } },
                h(LIcon, { name: 'check-circle', size: 18, color: C.success }),
                h('div', null,
                  h('div', { style: { fontWeight: 600, color: C.success, fontSize: 13 } }, 'Plan Created'),
                  h('div', { style: { fontFamily: 'inherit', fontSize: 11, color: C.textMuted } }, plan.amazon_plan_id)
                )
              ),
              // Shipment type / packing can still be switched on a created plan until a box is packed or
              // a packing option / placement is confirmed — Amazon only learns the mode at transport.
              canChangeShipping && h('div', { style: { padding: '12px 16px', background: C.surface, borderRadius: 8, border: '1px solid ' + C.border, marginBottom: 12 } },
                h('div', { style: { fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 4 } }, 'Shipment Type'),
                h('p', { style: { fontSize: 12, color: C.textMuted, margin: '0 0 10px 0' } },
                  'Amazon only learns the shipping mode at the Transportation step, so this can still be changed until a box is packed or a packing option / placement is confirmed. The Amazon plan stays; the wizard moves to the matching step.'),
                h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' } },
                  h('span', { style: { fontSize: 12, color: C.text, fontWeight: 600, minWidth: 92 } }, 'Shipment type:'),
                  ['SPD', 'LTL'].map(function(t) {
                    var active = (plan.shipment_type || 'SPD') === t;
                    return h('button', { key: t, type: 'button', disabled: actionLoading === 'set-type', onClick: function() { handleSetPlanShipping(t, null); },
                      style: { cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 6, fontFamily: 'inherit', border: '1px solid ' + (active ? C.accent : C.border), background: active ? C.accent : 'transparent', color: active ? '#fff' : C.textMuted } },
                      t === 'SPD' ? 'SPD (Small Parcel)' : 'LTL (Freight)');
                  })
                ),
                h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' } },
                  h('span', { style: { fontSize: 12, color: C.text, fontWeight: 600, minWidth: 92 } }, 'Packing:'),
                  [{ k: 'individual', label: 'Individual Units' }, { k: 'case_packed', label: 'Case-packed' }].map(function(pm) {
                    var active = (plan.packing_mode || 'individual') === pm.k;
                    return h('button', { key: pm.k, type: 'button', disabled: actionLoading === 'set-type', onClick: function() { handleSetPlanShipping(null, pm.k); },
                      style: { cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 6, fontFamily: 'inherit', border: '1px solid ' + (active ? C.accent : C.border), background: active ? C.accent : 'transparent', color: active ? '#fff' : C.textMuted } },
                      pm.label);
                  })
                )
              )
            )
          : h('div', null,
              h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' } },
                h('span', { style: { fontSize: 12, color: C.text, fontWeight: 600, minWidth: 92 } }, 'Shipment type:'),
                ['SPD', 'LTL'].map(function(t) {
                  var active = (plan.shipment_type || 'SPD') === t;
                  return h('button', { key: t, type: 'button', disabled: actionLoading === 'set-type', onClick: function() { handleSetPlanShipping(t, null); },
                    style: { cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 6, fontFamily: 'inherit', border: '1px solid ' + (active ? C.accent : C.border), background: active ? C.accent : 'transparent', color: active ? '#fff' : C.textMuted } },
                    t === 'SPD' ? 'SPD (Small Parcel)' : 'LTL (Freight)');
                })
              ),
              h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' } },
                h('span', { style: { fontSize: 12, color: C.text, fontWeight: 600, minWidth: 92 } }, 'Packing:'),
                [{ k: 'individual', label: 'Individual Units' }, { k: 'case_packed', label: 'Case-packed' }].map(function(pm) {
                  var active = (plan.packing_mode || 'individual') === pm.k;
                  return h('button', { key: pm.k, type: 'button', disabled: actionLoading === 'set-type', onClick: function() { handleSetPlanShipping(null, pm.k); },
                    style: { cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 6, fontFamily: 'inherit', border: '1px solid ' + (active ? C.accent : C.border), background: active ? C.accent : 'transparent', color: active ? '#fff' : C.textMuted } },
                    pm.label);
                })
              ),
              h('p', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12 } }, 'This will call Amazon SP-API to create an inbound shipment plan with ' + items.length + ' SKUs and ' + totalUnits + ' units.'),
              missingExp.length > 0 && h('div', { style: { fontSize: 12, color: '#f59e0b', display: 'flex', alignItems: 'center', gap: 6 } },
                h(LIcon, { name: 'alert-triangle', size: 14 }),
                unsavedExp.length > 0
                  ? unsavedExp.length + ' item(s) have expiration dates entered — click "Save Expiration Dates" above first'
                  : missingExp.length + ' item(s) still need expiration dates set above'
              )
            )
      )
    );
  }

  function renderPacking() {
    return h(Card, null,
      h('h3', { style: { fontSize: 14, fontWeight: 600, marginBottom: 12, color: C.text } }, 'Packing Options'),
      plan.packing_option_id
        ? h('div', null,
            h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', background: 'rgba(52,211,153,0.1)', borderRadius: 8, marginBottom: 12 } },
              h(LIcon, { name: 'check-circle', size: 18, color: C.success }),
              h('span', { style: { fontWeight: 600, color: C.success, fontSize: 13 } }, 'Packing confirmed')
            ),
            h(Btn, { variant: 'primary', onClick: function() { setManualStep(stepIndex('packing') + 1); } }, 'Continue ', h(LIcon, { name: 'arrow-right', size: 14 }))
          )
        : h('div', null,
            packingOptions.length === 0
              ? h(Btn, { variant: 'primary', onClick: handleGenPackingOpts, disabled: !!actionLoading }, actionLoading === 'packing-options' ? 'Generating...' : 'Generate Packing Options')
              : h('div', null,
                  h('div', { style: { marginBottom: 12 } },
                    packingOptions.map(function(opt, idx) {
                      var optId = opt.packingOptionId || opt.id || idx;
                      return h('label', { key: optId, style: { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 6, border: '1px solid ' + (selectedPackingOpt === optId ? C.accent : C.border), cursor: 'pointer', marginBottom: 6, background: selectedPackingOpt === optId ? 'rgba(79,110,247,0.06)' : 'transparent' } },
                        h('input', { type: 'radio', checked: selectedPackingOpt === optId, onChange: function() { setSelectedPackingOpt(optId); } }),
                        h('div', null,
                          h('div', { style: { fontSize: 13, fontWeight: 500 } }, 'Packing Option ' + (idx + 1)),
                          h('div', { style: { fontSize: 11, color: C.textMuted } },
                            (opt.packingGroups ? opt.packingGroups.length + ' packing group(s)' : '') +
                            (opt.fees && opt.fees.length ? ' \u00b7 ' + opt.fees.map(function(f) { return (f.description || f.type || 'Fee') + (f.amount ? ': $' + f.amount : ''); }).join(', ') : '') +
                            (opt.discounts && opt.discounts.length ? ' \u00b7 ' + opt.discounts.map(function(d) { return (d.description || d.type || 'Discount') + (d.amount ? ': $' + d.amount : ''); }).join(', ') : '')
                          )
                        )
                      );
                    })
                  ),
                  h(Btn, { variant: 'primary', onClick: handleConfirmPacking, disabled: !selectedPackingOpt || !!actionLoading }, actionLoading === 'confirm-packing' ? 'Confirming...' : 'Confirm Packing')
                )
          )
    );
  }

  function renderPackBoxes() {
    if (!shipments.length) return h(Card, null, h('p', { style: { color: C.textMuted } }, 'No shipment available. Complete placement step first.'));
    var safeIdx = Math.min(activeShipIdx, shipments.length - 1);
    var activeShip = shipments[safeIdx];
    var completedCount = shipments.filter(function(s) { return shipPackingStatus[s.id]; }).length;
    var allShipmentsPacked = shipments.length > 0 && completedCount === shipments.length;

    // Packing-group split (pre-placement SPD-individual only, so never alongside a
    // real multi-shipment split). Stats come from the placeholder shipment's items —
    // same freshness as the shipment overview cards (refreshed on each box commit).
    var pgMskuIdx = {};
    if (packGroups) packGroups.forEach(function(g, gi) { (g.mskus || []).forEach(function(m) { pgMskuIdx[m] = gi + 1; }); });
    var pgStats = packGroups ? packGroups.map(function(g, gi) {
      var gItems = (activeShip.items || []).filter(function(i) { return !i.excluded_from_amazon && pgMskuIdx[i.msku] === gi + 1; });
      var gUnits = 0, gResolved = 0, gRemaining = 0;
      gItems.forEach(function(i) {
        var done = (i.quantity_packed || 0) + (i.quantity_missing || 0);
        gUnits += i.quantity || 0;
        gResolved += Math.min(done, i.quantity || 0);
        if (done < i.quantity) gRemaining++;
      });
      return { skus: gItems.length, units: gUnits, pct: gUnits > 0 ? Math.round(gResolved / gUnits * 100) : 0, done: gItems.length > 0 && gRemaining === 0 };
    }) : null;
    var pgNextIdx = -1;
    if (pgStats) {
      for (var pgi = 0; pgi < pgStats.length; pgi++) {
        if (pgi !== activeGroupIdx && pgStats[pgi].skus > 0 && !pgStats[pgi].done) { pgNextIdx = pgi; break; }
      }
    }

    // Parse destination for active shipment
    var destAddr = activeShip.destination_address ? (typeof activeShip.destination_address === 'string' ? JSON.parse(activeShip.destination_address) : activeShip.destination_address) : {};
    var da = destAddr.address || {};
    var destLocation = [da.city, da.stateOrProvinceCode].filter(Boolean).join(', ');
    var destFullAddr = [da.addressLine1, da.addressLine2].filter(Boolean).join(', ');
    var destCityStateZip = [destLocation, da.postalCode].filter(Boolean).join(' ');

    // Find next unpacked shipment
    var nextUnpackedIdx = -1;
    for (var ni = 1; ni <= shipments.length; ni++) {
      var ci = (safeIdx + ni) % shipments.length;
      if (!shipPackingStatus[shipments[ci].id]) { nextUnpackedIdx = ci; break; }
    }

    // ─── Missing items banner ───
    // If any unit has been marked missing, Amazon's plan still expects the original
    // quantities and would reject setPackingInformation. SPD declares the short units in
    // the last box (no recreate) + flags the PO Errored; LTL case-packed cancels +
    // recreates with packed-only quantities. Both route through the branched resubmit modal.
    var missingItems = [];
    shipments.forEach(function(s) {
      (s.items || []).forEach(function(it) {
        if ((it.quantity_missing || 0) > 0) missingItems.push(it);
      });
    });
    var totalMissingUnits = missingItems.reduce(function(sum, it) { return sum + (it.quantity_missing || 0); }, 0);
    var canResubmit = !isLtlIndividual && missingItems.length > 0 && plan && plan.amazon_plan_id;

    return h('div', null,
      // ─── "Pack everything to enable Continue" hint, near the Continue button ───
      // Same text that used to sit at the bottom of this section — moved up so the
      // user sees the requirement next to the disabled Continue button in the top bar.
      (!allShipmentsPacked || unreceivedErroredCount > 0) && h('div', {
        style: { marginBottom: 12, padding: '8px 14px', borderRadius: 6, background: C.warning + '10', border: '1px solid ' + C.warning + '44', fontSize: 12, color: C.text, display: 'flex', alignItems: 'center', gap: 8 }
      },
        h(LIcon, { name: 'info', size: 14, color: C.warning }),
        h('span', null,
          !allShipmentsPacked
            ? (shipments.length > 1
                ? 'Pack all ' + shipments.length + ' shipments to enable Continue in the top bar.'
                : 'Pack all items to enable Continue in the top bar.')
            : 'Resolve ' + unreceivedErroredCount + ' errored item' + (unreceivedErroredCount > 1 ? 's' : '') + ' on the Errored tab to enable Continue in the top bar.'
        )
      ),
      // ─── Resubmit banner (only when there are missing items) ───
      canResubmit && (function() {
        var isSPD = plan && plan.shipment_type === 'SPD';
        return h('div', { style: { padding: '12px 16px', background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.4)', borderRadius: 8, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 } },
          h(LIcon, { name: 'alert-triangle', size: 18, color: '#F59E0B' }),
          h('div', { style: { flex: 1, fontSize: 12, color: C.text } },
            h('div', { style: { fontWeight: 600, marginBottom: 2 } }, missingItems.length + ' SKU' + (missingItems.length > 1 ? 's' : '') + ' marked missing (' + totalMissingUnits + ' unit' + (totalMissingUnits > 1 ? 's' : '') + ')'),
            h('div', { style: { color: C.textMuted } }, isSPD
              ? 'These short units will be declared to Amazon in the last box (no plan recreate) and flagged missing + Errored on the PO when you click Continue. Your boxes stay as packed.'
              : 'Amazon still expects the original quantities. Resubmit to cancel this plan and recreate it with packed-only quantities — your boxes stay intact.')
          ),
          // SPD folds short units automatically on Continue (no button); LTL case-packed
          // still needs the explicit cancel+recreate confirm.
          !isSPD && h(Btn, { size: 'sm', variant: 'primary', disabled: resubmitting, onClick: function() { setResubmitOpen(true); } },
            h(LIcon, { name: 'refresh-cw', size: 13 }), ' Resubmit to Amazon')
        );
      })(),

      // ─── Shipment Overview Panel ───
      shipments.length > 1 && h(Card, { style: { marginBottom: 12 } },
        h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 } },
          h('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
            h(LIcon, { name: 'git-branch', size: 16, color: C.accent }),
            h('span', { style: { fontSize: 14, fontWeight: 600, color: C.text } }, 'Shipment Overview')
          ),
          h('span', { style: { fontSize: 12, color: allShipmentsPacked ? C.success : C.textMuted, fontWeight: 600 } },
            completedCount + '/' + shipments.length + ' packed'
          )
        ),
        h('div', { style: { display: 'flex', gap: 8, flexWrap: 'wrap' } },
          shipments.map(function(s, idx) {
            var isActive = idx === safeIdx;
            var isDone = !!shipPackingStatus[s.id];
            var sItems = s.items || [];
            var sUnits = sItems.reduce(function(sum, i) { return sum + (i.quantity || 0); }, 0);
            var sPacked = sItems.reduce(function(sum, i) { return sum + (i.quantity_packed || 0) + (i.quantity_missing || 0); }, 0);
            var pct = sUnits > 0 ? Math.round(sPacked / sUnits * 100) : 0;
            var sAddr = s.destination_address ? (typeof s.destination_address === 'string' ? JSON.parse(s.destination_address) : s.destination_address) : {};
            var sLoc = sAddr.address ? [sAddr.address.city, sAddr.address.stateOrProvinceCode].filter(Boolean).join(', ') : '';
            return h('div', {
              key: s.id,
              onClick: function() { setActiveShipIdx(idx); },
              style: {
                flex: '1 1 140px', maxWidth: 180, minWidth: 130, padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                // Done-ness is shown only by the checkmark + green progress bar — the card
                // itself is not flooded green (active = blue highlight, otherwise neutral).
                border: '2px solid ' + (isActive ? C.accent : C.border),
                background: isActive ? 'rgba(79,110,247,0.08)' : 'transparent',
                transition: 'all 0.15s'
              }
            },
              h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 } },
                h('span', { style: { fontSize: 15, fontWeight: 700, fontFamily: 'inherit', color: isActive ? C.accent : C.text } }, s.destination_fc || 'FC?'),
                h('div', { style: { display: 'flex', alignItems: 'center', gap: 4 } },
                  isDone && h(LIcon, { name: 'check-circle', size: 16, color: C.success }),
                  h('button', {
                    onClick: function(e) { e.stopPropagation(); exportShipmentXlsx(s); },
                    title: 'Export picklist (Excel)',
                    onMouseEnter: function(e) { e.currentTarget.style.color = C.accent; },
                    onMouseLeave: function(e) { e.currentTarget.style.color = C.textMuted; },
                    style: { background: 'transparent', border: 'none', padding: 2, cursor: 'pointer', color: C.textMuted, display: 'inline-flex', alignItems: 'center', lineHeight: 0 }
                  }, h(LIcon, { name: 'download', size: 14 }))
                )
              ),
              h('div', { style: { fontSize: 10, fontFamily: 'inherit', color: C.textDim, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } },
                s.amazon_shipment_id || 'Pending'
              ),
              sLoc && h('div', { style: { fontSize: 10, color: C.textMuted, marginBottom: 6 } }, sLoc),
              h('div', { style: { fontSize: 11, color: C.textMuted, marginBottom: 6 } },
                sItems.length + ' SKU' + (sItems.length !== 1 ? 's' : '') + ' \u00B7 ' + sUnits + ' units'
              ),
              h('div', { style: { height: 5, borderRadius: 3, background: C.border, overflow: 'hidden' } },
                h('div', { style: { height: '100%', width: pct + '%', borderRadius: 3, background: isDone ? C.success : pct > 0 ? '#f59e0b' : 'transparent', transition: 'width 0.3s' } })
              ),
              h('div', { style: { fontSize: 10, color: isDone ? C.success : C.textMuted, marginTop: 3, textAlign: 'right', fontWeight: 500 } }, pct + '%')
            );
          })
        )
      ),

      // ─── Packing Group Overview ─── (pre-placement SPD-individual; same slot and
      // card anatomy as the Shipment Overview above — title row, id line, counts,
      // progress bar — with groups instead of shipments)
      packGroups && pgStats && h(Card, { style: { marginBottom: 12 } },
        h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 } },
          h('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
            h(LIcon, { name: 'git-branch', size: 16, color: C.accent }),
            h('span', { style: { fontSize: 14, fontWeight: 600, color: C.text } }, 'Packing Group Overview')
          ),
          h('span', { style: { fontSize: 12, color: pgStats.every(function(g) { return g.done; }) ? C.success : C.textMuted, fontWeight: 600 } },
            pgStats.filter(function(g) { return g.done; }).length + '/' + pgStats.length + ' packed'
          )
        ),
        h('div', { style: { display: 'flex', gap: 8, flexWrap: 'wrap' } },
          pgStats.map(function(gs, gi) {
            var isActive = gi === activeGroupIdx;
            return h('div', {
              key: gi,
              onClick: function() { setActiveGroupIdx(gi); },
              style: {
                flex: '1 1 140px', maxWidth: 180, minWidth: 130, padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                border: '2px solid ' + (isActive ? C.accent : C.border),
                background: isActive ? 'rgba(79,110,247,0.08)' : 'transparent',
                transition: 'all 0.15s'
              }
            },
              h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 } },
                h('span', { style: { fontSize: 15, fontWeight: 700, fontFamily: 'inherit', color: isActive ? C.accent : C.text } }, 'Group ' + (gi + 1)),
                h('div', { style: { display: 'flex', alignItems: 'center', gap: 4 } },
                  gs.done && h(LIcon, { name: 'check-circle', size: 16, color: C.success })
                )
              ),
              h('div', { style: { fontSize: 10, fontFamily: 'inherit', color: C.textDim, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } },
                (packGroups[gi] && packGroups[gi].packingGroupId) || 'Pending'
              ),
              h('div', { style: { fontSize: 11, color: C.textMuted, marginBottom: 6 } },
                gs.skus + ' SKU' + (gs.skus !== 1 ? 's' : '') + ' · ' + gs.units + ' units'
              ),
              h('div', { style: { height: 5, borderRadius: 3, background: C.border, overflow: 'hidden' } },
                h('div', { style: { height: '100%', width: gs.pct + '%', borderRadius: 3, background: gs.done ? C.success : gs.pct > 0 ? '#f59e0b' : 'transparent', transition: 'width 0.3s' } })
              ),
              h('div', { style: { fontSize: 10, color: gs.done ? C.success : C.textMuted, marginTop: 3, textAlign: 'right', fontWeight: 500 } }, gs.pct + '%')
            );
          })
        )
      ),

      // ─── Active Shipment Header ───
      h(Card, { style: { marginBottom: 12 } },
        h('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' } },
          h('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
            h(LIcon, { name: 'truck', size: 18, color: C.accent }),
            h('div', null,
              h('div', { style: { fontSize: 11, color: C.textMuted } },
                (shipments.length > 1 ? 'Shipment ' + (safeIdx + 1) + ' of ' + shipments.length + ' — ' : '') + 'Shipment ID: ',
                h('span', { style: { fontWeight: 600, fontSize: 13, color: C.text } }, activeShip.amazon_shipment_id || 'Pending'),
                activeShip.amazon_reference_id && h('span', null, ' · Amazon PO ID: ',
                  h('span', { style: { fontWeight: 600, fontSize: 13, color: C.text } }, activeShip.amazon_reference_id))
              ),
              (activeShip.destination_fc || destLocation) && h('div', { style: { marginTop: 6 } },
                activeShip.destination_fc && h('div', { style: { fontSize: 15, fontWeight: 700, fontFamily: 'inherit', color: C.text } }, activeShip.destination_fc),
                destFullAddr && h('div', { style: { fontSize: 12, color: C.textMuted } }, destFullAddr),
                destCityStateZip && h('div', { style: { fontSize: 12, color: C.textMuted } }, destCityStateZip),
                da.countryCode && da.countryCode !== 'US' && h('div', { style: { fontSize: 12, color: C.textMuted } }, da.countryCode)
              )
            )
          ),
          h('div', { style: { display: 'flex', alignItems: 'flex-start', gap: 16 } },
            h('div', { style: { display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' } },
              h('button', {
                onClick: function() { exportShipmentXlsx(activeShip); },
                title: 'Export picklist (Excel)',
                onMouseEnter: function(e) { e.currentTarget.style.background = 'rgba(79,110,247,0.15)'; e.currentTarget.style.borderColor = C.accent; e.currentTarget.style.color = C.accent; },
                onMouseLeave: function(e) { e.currentTarget.style.background = 'rgba(79,110,247,0.08)'; e.currentTarget.style.borderColor = 'rgba(79,110,247,0.3)'; e.currentTarget.style.color = C.accent; },
                style: { padding: '6px 10px', background: 'rgba(79,110,247,0.08)', color: C.accent, border: '1px solid rgba(79,110,247,0.3)', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: 'inherit' }
              }, h(LIcon, { name: 'download', size: 13 }), ' Picklist'),
              isLtlIndividual && h('button', {
                onClick: function() {
                  var rows = palletRowsOrPrompt(palletData[activeShip.id]);
                  if (rows) printFbaPalletLabels(activeShip, plan, rows, showToast);
                },
                title: 'Print 4x6 pallet labels (4 per pallet)',
                onMouseEnter: function(e) { e.currentTarget.style.background = 'rgba(167,139,250,0.18)'; },
                onMouseLeave: function(e) { e.currentTarget.style.background = 'rgba(167,139,250,0.1)'; },
                style: { padding: '6px 10px', background: 'rgba(167,139,250,0.1)', color: '#8B5CF6', border: '1px solid rgba(167,139,250,0.3)', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: 'inherit' }
              }, h(LIcon, { name: 'printer', size: 13 }), ' Pallet Labels'),
              h(WorkOrdersMenu, { shipment: activeShip, plan: plan, showToast: showToast, align: 'right' })
            )
          )
        )
      ),

      // ─── Box Packing View for active shipment ───
      h(FbaBoxPackingView, {
        key: activeShip.id + '-' + packingReloadKey,
        shipmentId: activeShip.id,
        planId: planId,
        showToast: showToast,
        plan: plan,
        currentUser: currentUser,
        packGroups: packGroups,
        activeGroupIdx: activeGroupIdx,
        onSwitchGroup: setActiveGroupIdx,
        onNavigate: onNavigate,
        onRefresh: loadPlan,
        onErroredCountChange: setErroredCountOverride,
        onPackingStatusChange: function(complete) {
          setShipPackingStatus(function(prev) {
            var n = Object.assign({}, prev);
            n[activeShip.id] = complete;
            return n;
          });
        }
      }),

      // ─── Next Shipment / Next Group ─── (the "pack all to continue" hint now lives at the top of this section)
      h('div', { style: { marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' } },
        shipPackingStatus[activeShip.id] && nextUnpackedIdx >= 0 && h(Btn, {
          onClick: function() { setActiveShipIdx(nextUnpackedIdx); },
          style: { background: 'rgba(79,110,247,0.1)', color: C.accent, border: '1px solid rgba(79,110,247,0.3)' }
        }, 'Next: ', h('span', { style: { fontWeight: 700, fontFamily: 'inherit' } }, shipments[nextUnpackedIdx].destination_fc || 'Shipment ' + (nextUnpackedIdx + 1)), ' ', h(LIcon, { name: 'arrow-right', size: 14 })),
        pgStats && pgStats[activeGroupIdx] && pgStats[activeGroupIdx].done && pgNextIdx >= 0 && h(Btn, {
          onClick: function() { setActiveGroupIdx(pgNextIdx); },
          style: { background: 'rgba(79,110,247,0.1)', color: C.accent, border: '1px solid rgba(79,110,247,0.3)' }
        }, 'Next: ', h('span', { style: { fontWeight: 700, fontFamily: 'inherit' } }, 'Group ' + (pgNextIdx + 1)), ' ', h(LIcon, { name: 'arrow-right', size: 14 }))
      )
    );
  }

  function renderPlacement() {
    // Same reasoning as totalUnits — exclude SKUs that Resubmit dropped from the
    // recreated Amazon plan so the %-of-cost column reflects the placed cost basis.
    var totalCost = items.reduce(function(s, i) { return s + (i.excluded_from_amazon ? 0 : ((parseFloat(i.unit_cost) || 0) * (i.quantity || 0))); }, 0);
    var ps = function(bg, fg) { return { fontSize: 12, fontWeight: 600, padding: '3px 9px', borderRadius: 4, background: bg, color: fg, letterSpacing: '0.03em', whiteSpace: 'nowrap', display: 'inline-block' }; };
    var copyBadge = function(display, value, bg, fg) {
      return h('span', {
        onClick: function(e) {
          e.stopPropagation();
          var v = String(value);
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(v).then(function() { showToast('Copied ' + v, 'success'); }).catch(function() { showToast('Copy failed', 'error'); });
          }
        },
        onMouseEnter: function(e) { e.currentTarget.style.filter = 'brightness(1.25)'; },
        onMouseLeave: function(e) { e.currentTarget.style.filter = 'brightness(1)'; },
        title: 'Click to copy',
        style: Object.assign({}, ps(bg, fg), { cursor: 'pointer', transition: 'filter 0.15s' })
      }, display);
    };
    var skel = function(w) { return h('span', { style: { display: 'inline-block', width: w || 44, height: 10, borderRadius: 4, background: C.border, animation: 'fbaRatePulse 1.2s ease-in-out infinite', verticalAlign: 'middle' } }); };
    return h(Card, null,
      h('style', null, '@keyframes fbaRatePulse{0%,100%{opacity:.35}50%{opacity:.85}}'),
      h('h3', { style: { fontSize: 14, fontWeight: 600, marginBottom: 12, color: C.text } }, 'Placement Options'),
      plan.placement_option_id
        ? h('div', null,
            h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', background: 'rgba(52,211,153,0.1)', borderRadius: 8, marginBottom: 12 } },
              h(LIcon, { name: 'check-circle', size: 18, color: C.success }),
              h('span', { style: { fontWeight: 600, color: C.success, fontSize: 13 } }, 'Placement confirmed — ' + shipments.length + ' shipment(s)')
            ),
            shipments.map(function(s) {
              var sda = s.destination_address ? (typeof s.destination_address === 'string' ? JSON.parse(s.destination_address) : s.destination_address) : {};
              var sdAddr = sda.address || {};
              var sdLine = [sdAddr.addressLine1, sdAddr.addressLine2].filter(Boolean).join(', ');
              var sdCity = [sdAddr.city, sdAddr.stateOrProvinceCode].filter(Boolean).join(', ');
              var sdCityZip = [sdCity, sdAddr.postalCode].filter(Boolean).join(' ');
              var shipUnits = (s.items || []).reduce(function(a, it) { return a + (it.quantity || 0); }, 0);
              var boxCount = (s.boxes || []).length;
              var statParts = [];
              if (shipUnits > 0) statParts.push(shipUnits + ' units');
              if (boxCount > 0) statParts.push(boxCount + (boxCount === 1 ? ' box' : ' boxes'));
              return h('div', { key: s.id, style: { padding: '8px 12px', borderRadius: 6, border: '1px solid ' + C.border, marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' } },
                h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' } },
                  s.amazon_shipment_id
                    ? copyBadge(s.amazon_shipment_id, s.amazon_shipment_id, C.accent + '14', C.accent)
                    : h('span', { style: { fontWeight: 600, fontSize: 12, fontFamily: 'inherit' } }, 'Shipment'),
                  s.destination_fc && copyBadge('→ ' + s.destination_fc, s.destination_fc, 'rgba(16,185,129,0.12)', '#10B981'),
                  statParts.length > 0 && h('span', { style: { fontSize: 11, color: C.textMuted } }, statParts.join(' · ')),
                  s.distance_miles != null && h('span', { style: { fontSize: 11, color: C.textDim }, title: 'Straight-line distance from your ship-from ZIP — road miles typically run 15-25% longer' }, '~' + s.distance_miles + ' mi')
                ),
                (sdLine || sdCityZip) && h('div', { style: { textAlign: 'right', fontSize: 11, color: C.textMuted, lineHeight: '1.4' } },
                  sdLine && h('div', null, sdLine),
                  sdCityZip && h('div', null, sdCityZip)
                )
              );
            }),
            h('div', { style: { marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' } },
              !isLtlIndividual && h(Btn, { onClick: printAllCaseLabels, style: { background: 'rgba(79,110,247,0.1)', color: C.accent, border: '1px solid rgba(79,110,247,0.3)' } }, h(LIcon, { name: 'printer', size: 13 }), ' Print All Case Labels'),
              h(Btn, { variant: 'primary', onClick: function() { setManualStep(stepIndex('placement') + 1); } }, 'Continue ', h(LIcon, { name: 'arrow-right', size: 14 }))
            )
          )
        : h('div', null,
            placementOptions.length === 0
              ? h(Btn, { variant: 'primary', onClick: handleGenPlacementOpts, disabled: !!actionLoading }, actionLoading === 'placement-options' ? 'Generating...' : 'Generate Placement Options')
              : (function() {
                  // Amazon partnered-carrier rate lookups
                  var amzOptMap = {}; var amzShipMap = {};
                  if (amazonRates && amazonRates.options) {
                    amazonRates.options.forEach(function(o) {
                      amzOptMap[o.placement_option_id] = o;
                      (o.shipment_rates || []).forEach(function(r) {
                        amzShipMap[o.placement_option_id + '|' + r.shipment_id] = r;
                      });
                    });
                  }
                  // Sum placement fees per option from opt.fees (placement option intrinsic data)
                  var feesForOpt = function(opt) {
                    return (opt.fees || []).reduce(function(s, f) {
                      var raw = f.value && f.value.amount != null ? f.value.amount : f.amount;
                      return s + (parseFloat(raw) || 0);
                    }, 0);
                  };
                  // Best option = lowest (Amazon partnered + placement fees) where every shipment has a rate
                  var bestOptionId = null; var bestOptionTotal = Infinity;
                  placementOptions.forEach(function(opt) {
                    var pid = opt.placementOptionId || opt.id;
                    var amz = amzOptMap[pid];
                    if (!amz || amz.unavailable_count > 0 || !(amz.partnered_total > 0)) return;
                    var optTotal = amz.partnered_total + feesForOpt(opt);
                    if (optTotal > 0 && optTotal < bestOptionTotal) { bestOptionTotal = optTotal; bestOptionId = pid; }
                  });
                  // Render order: fully-rated options cheapest-first, then unrated ones
                  // by placement fees. Option numbers stay tied to Amazon's original
                  // order so labels don't shuffle when rates arrive.
                  var rankOf = function(opt) {
                    var pid = opt.placementOptionId || opt.id;
                    var amz = amzOptMap[pid];
                    if (amz && !amz.error && !(amz.unavailable_count > 0) && amz.partnered_total > 0) return { tier: 0, val: amz.partnered_total + feesForOpt(opt) };
                    return { tier: 1, val: feesForOpt(opt) };
                  };
                  var displayOpts = placementOptions.map(function(o, i) { return { opt: o, origIdx: i }; });
                  displayOpts.sort(function(a, b) {
                    var ra = rankOf(a.opt), rb = rankOf(b.opt);
                    return ra.tier !== rb.tier ? ra.tier - rb.tier : ((ra.val - rb.val) || (a.origIdx - b.origIdx));
                  });
                  return h('div', null,
                    isLtlIndividual
                      ? h('div', { style: { marginBottom: 8, padding: '8px 12px', borderRadius: 4, border: '1px solid ' + C.warning + '55', background: C.warning + '10', fontSize: 11, color: C.text, display: 'flex', alignItems: 'flex-start', gap: 8 } },
                          h(LIcon, { name: 'info', size: 14, color: C.warning, style: { flexShrink: 0, marginTop: 1 } }),
                          h('span', null,
                            h('strong', { style: { color: C.warning } }, 'Rates appear after you confirm a placement. '),
                            'Amazon\'s v2024 API doesn\'t preview partnered-carrier rates for LTL-Individual plans before placement confirmation (FBA_INB_0344). Pick a placement based on destination FCs + placement fees, then confirm — rates load on the transport step.'
                          )
                        )
                      : h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, fontSize: 11, color: C.textMuted, gap: 8, flexWrap: 'wrap' } },
                        h('span', null, loadingAmazonRates ? 'Fetching Amazon partnered-carrier rates… ' + ratesElapsed + 's elapsed (30-60s per option)'
                          : amazonRatesError ? h('span', { style: { color: '#F87171' } }, 'Amazon rates: ' + amazonRatesError)
                          : amazonRates ? 'Amazon partnered-carrier rates · generated ' + new Date(amazonRates.generated_at).toLocaleTimeString() + (amazonRates.cached ? ' (cached 10 min)' : '')
                          : 'Amazon partnered-carrier rates'),
                        h('button', {
                          onClick: function() { loadAmazonRates(true); },
                          disabled: loadingAmazonRates,
                          title: 'Refresh Amazon partnered-carrier rates',
                          onMouseEnter: function(e) { if (!loadingAmazonRates) { e.currentTarget.style.background = '#10B98114'; e.currentTarget.style.color = '#10B981'; e.currentTarget.style.borderColor = '#10B981'; } },
                          onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = C.textMuted; e.currentTarget.style.borderColor = C.border; },
                          style: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 4, border: '1px solid ' + C.border, background: 'transparent', color: C.textMuted, cursor: loadingAmazonRates ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 600, fontFamily: 'inherit', transition: 'background 0.15s, color 0.15s', opacity: loadingAmazonRates ? 0.5 : 1 }
                        },
                          h(LIcon, { name: 'refresh-cw', size: 11 }), 'Refresh rates'
                        )
                      ),
                  displayOpts.map(function(entry) {
                    var opt = entry.opt;
                    var idx = entry.origIdx;
                    var optId = opt.placementOptionId || opt.id || idx;
                    var optAmz = amzOptMap[optId];
                    var isCheapest = bestOptionId && optId === bestOptionId;
                    var shipList = opt.shipments || [];
                    var allCharges = [].concat(
                      (opt.fees || []).map(function(f) { return Object.assign({}, f, { _kind: 'fee' }); }),
                      (opt.discounts || []).map(function(d) { return Object.assign({}, d, { _kind: 'discount' }); })
                    );
                    var formatAmt = function(a) {
                      if (!a) return '$0.00';
                      var val = typeof a === 'object' ? parseFloat(a.amount || 0) : parseFloat(a || 0);
                      var cur = (typeof a === 'object' && a.code) ? a.code : '';
                      return '$' + val.toFixed(2) + (cur && cur !== 'USD' ? ' ' + cur : '');
                    };
                    var isSelected = selectedPlacementOpt === optId;
                    return h('label', { key: optId, style: { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px', borderRadius: 8, border: '1px solid ' + (isSelected ? C.accent : (isCheapest ? '#10B981' : C.border)), boxShadow: isSelected ? '0 0 0 1px ' + C.accent : 'none', cursor: 'pointer', marginBottom: 8, background: isSelected ? 'rgba(79,110,247,0.06)' : (isCheapest ? 'rgba(16,185,129,0.04)' : 'transparent'), transition: 'border-color 0.15s, box-shadow 0.15s' } },
                      h('input', { type: 'radio', checked: selectedPlacementOpt === optId, onChange: function() { setSelectedPlacementOpt(optId); }, style: { marginTop: 3 } }),
                      h('div', { style: { flex: 1 } },
                        (function() {
                          var optUnits = shipList.reduce(function(a, s) { return a + (s.items || []).reduce(function(b, it) { return b + (it.quantity || 0); }, 0); }, 0);
                          var amzTot = optAmz && !(optAmz.unavailable_count > 0) && optAmz.partnered_total > 0 ? optAmz.partnered_total + feesForOpt(opt) : null;
                          var delta = (!isCheapest && amzTot != null && bestOptionId) ? amzTot - bestOptionTotal : null;
                          return h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' } },
                            h('span', { style: { fontSize: 13, fontWeight: 600 } }, 'Option ' + (idx + 1) + ' \u2014 ' + (shipList.length || (opt.shipmentIds || []).length) + ' shipment(s)' + (optUnits > 0 ? ' \u00b7 ' + optUnits + ' units' : '')),
                            isCheapest && h('span', { style: { fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: 'rgba(16,185,129,0.15)', color: '#10B981', letterSpacing: '0.04em', textTransform: 'uppercase' } }, 'Cheapest'),
                            delta != null && delta > 0.005 && h('span', { style: { fontSize: 11, fontWeight: 600, color: '#F59E0B' } }, '+$' + delta.toFixed(2) + ' vs cheapest')
                          );
                        })(),
                        shipList.length > 0 && h('div', { style: { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 } },
                          shipList.map(function(s) {
                            var label = s.warehouseId || s.shipmentId;
                            var location = [s.city, s.state].filter(Boolean).join(', ');
                            var amzRate = amzShipMap[optId + '|' + s.shipmentId];
                            var amzCost = amzRate && typeof amzRate.partnered_cost === 'number' ? amzRate.partnered_cost : null;
                            var shipItems = s.items || [];
                            var shipUnits = shipItems.reduce(function(a, it) { return a + (it.quantity || 0); }, 0);
                            return h('div', { key: s.shipmentId, style: { background: splitSel[s.shipmentId] ? 'rgba(245,158,11,0.10)' : 'rgba(255,255,255,0.04)', borderRadius: 4, border: '1px solid ' + (splitSel[s.shipmentId] ? 'rgba(245,158,11,0.4)' : 'transparent') } },
                              h('div', { style: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '3px 8px' } },
                                (selectedPlacementOpt === optId) && (function() {
                                  var checked = !!splitSel[s.shipmentId];
                                  return h('div', {
                                    onClick: function(e) { e.stopPropagation(); setSplitSel(function(prev) { var n = Object.assign({}, prev); if (checked) { delete n[s.shipmentId]; } else { n[s.shipmentId] = true; } return n; }); },
                                    title: 'Select to move this shipment to a separate plan',
                                    style: { width: 16, height: 16, minWidth: 16, borderRadius: 4, border: '2px solid ' + (checked ? '#F59E0B' : C.textDim), background: checked ? '#F59E0B' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.15s', flexShrink: 0 }
                                  }, checked && h(LIcon, { name: 'check', size: 11, color: '#fff' }));
                                })(),
                                h(LIcon, { name: 'package', size: 13, color: C.textMuted }),
                                h('span', { style: { fontWeight: 600, fontFamily: 'inherit' } }, label),
                                location && h('span', { style: { color: C.textMuted } }, location),
                                s.distance_miles != null && h('span', { style: { color: C.textDim, fontSize: 11 }, title: 'Straight-line distance from your ship-from ZIP' + (s.postalCode ? ' to ' + s.postalCode : '') + ' — road miles typically run 15-25% longer' }, '· ~' + s.distance_miles + ' mi'),
                                h('span', { style: { marginLeft: 'auto', fontSize: 11, fontFamily: 'inherit' } },
                                  amzCost != null
                                    ? h('span', { style: { color: C.text, fontWeight: 600 }, title: 'Amazon partnered carrier — ' + (amzRate.carrier || 'UPS') }, '$' + amzCost.toFixed(2))
                                    : amzRate && amzRate.reason === 'no_partnered_carrier'
                                      ? h('span', { style: { color: C.textDim, fontStyle: 'italic' }, title: 'Amazon partnered carrier not available for this destination' }, 'Not available')
                                      : amzRate && amzRate.reason === 'api_error'
                                        ? h('span', { style: { color: '#F87171', fontStyle: 'italic' }, title: 'API error fetching Amazon rate' }, 'Error')
                                        : loadingAmazonRates
                                          ? skel(40)
                                          : h('span', { style: { color: C.textDim } }, '—')
                                )
                              ),
                              shipItems.length > 0 && h('details', { style: { padding: '0 8px 6px 30px' } },
                                h('summary', { onClick: function(e) { e.stopPropagation(); }, style: { cursor: 'pointer', fontSize: 11, color: C.textMuted, userSelect: 'none' } }, shipItems.length + ' SKU(s) · ' + shipUnits + ' unit(s)'),
                                h('div', { style: { marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 180, overflowY: 'auto' } },
                                  shipItems.map(function(it, ii) {
                                    return h('div', { key: (it.msku || ii), style: { display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 11, fontFamily: 'inherit' } },
                                      h('span', { style: { color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, it.msku),
                                      h('span', { style: { color: C.textMuted } }, '×' + (it.quantity || 0))
                                    );
                                  })
                                )
                              )
                            );
                          })
                        ),
                        (selectedPlacementOpt === optId) && (function() {
                          var selShips = shipList.filter(function(s) { return splitSel[s.shipmentId]; });
                          if (!selShips.length) {
                            return shipList.length > 1 ? h('div', { style: { fontSize: 11, color: C.textMuted, marginBottom: 8 } }, 'Tip: check shipments above to move them into their own separate plan.') : null;
                          }
                          var selUnits = selShips.reduce(function(a, s) { return a + (s.items || []).reduce(function(b, it) { return b + (it.quantity || 0); }, 0); }, 0);
                          return h('div', { style: { display: 'flex', justifyContent: 'flex-end', marginBottom: 8 } },
                            h(Btn, { size: 'sm', onClick: function(e) { e.preventDefault(); e.stopPropagation(); openSplitModal(selShips); }, disabled: actionLoading === 'split-shipment', style: { color: '#F59E0B', borderColor: 'rgba(245,158,11,0.4)' } }, h(LIcon, { name: 'split', size: 12 }), ' Move ' + selShips.length + ' selected shipment(s) · ' + selUnits + ' units → separate plan')
                          );
                        })(),
                        allCharges.length > 0
                          ? h('div', { style: { display: 'flex', flexDirection: 'column', gap: 3, borderTop: '1px solid ' + C.border, paddingTop: 6 } },
                              allCharges.map(function(c, ci) {
                                var rawVal = c.value || c.amount;
                                var amt = formatAmt(rawVal);
                                var numVal = typeof rawVal === 'object' ? parseFloat(rawVal.amount || 0) : parseFloat(rawVal || 0);
                                var perUnit = totalUnits > 0 ? numVal / totalUnits : 0;
                                var isDiscount = c._kind === 'discount';
                                var isZero = amt.indexOf('$0.00') === 0;
                                var label = c.target && c.target !== 'PER_UNIT' && c.target !== 'PER_SHIPMENT'
                                  ? c.target
                                  : (isDiscount ? 'Discount' : 'Fee');
                                return h('div', { key: ci, style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, gap: 12 } },
                                  h('span', { style: { color: C.text }, title: c.description || '' }, label),
                                  h('div', { style: { textAlign: 'right' } },
                                    h('span', { style: { fontWeight: 600, color: isDiscount ? C.success : isZero ? C.textMuted : '#f59e0b' } },
                                      (isDiscount && !isZero ? '-' : '') + amt
                                    ),
                                    !isZero && totalUnits > 0 && h('div', { style: { fontSize: 10, color: C.textMuted, marginTop: 1 } },
                                      (isDiscount ? '-' : '') + '$' + perUnit.toFixed(4) + '/unit' + (totalCost > 0 ? ' \u00b7 ' + (numVal / totalCost * 100).toFixed(2) + '% of cost' : '')
                                    )
                                  )
                                );
                              })
                            )
                          : h('div', { style: { fontSize: 11, color: C.textMuted, borderTop: '1px solid ' + C.border, paddingTop: 6 } }, 'No additional fees'),
                        (optAmz || loadingAmazonRates) && h('div', { style: { borderTop: '1px solid ' + C.border, marginTop: 6, paddingTop: 6 } },
                          (function() {
                            var fees = feesForOpt(opt);
                            return h('div', { style: { display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 2 } },
                              h('span', { style: { color: C.textMuted } }, 'Placement fees'),
                              h('span', null, '$' + fees.toFixed(2))
                            );
                          })(),
                          h('div', { style: { display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 } },
                            h('span', { style: { color: C.textMuted } }, 'Shipping (Amazon Partnered)'),
                            (function() {
                              if (loadingAmazonRates && !optAmz) return skel(56);
                              if (!optAmz) return h('span', null, '—');
                              if (optAmz.error) return h('span', { style: { color: '#F87171', fontStyle: 'italic' }, title: optAmz.error }, 'Error');
                              if (optAmz.unavailable_count > 0 && optAmz.partnered_total > 0) {
                                return h('span', { style: { color: '#F59E0B' }, title: optAmz.unavailable_count + ' shipment(s) without partnered carrier' },
                                  '$' + optAmz.partnered_total.toFixed(2) + ' (' + optAmz.unavailable_count + ' unavail)');
                              }
                              if (optAmz.unavailable_count > 0) {
                                return h('span', { style: { color: C.textDim, fontStyle: 'italic' } }, 'Not available');
                              }
                              return h('span', { style: { color: C.text, fontWeight: 600 } }, '$' + optAmz.partnered_total.toFixed(2));
                            })()
                          ),
                          (function() {
                            if (!optAmz || optAmz.unavailable_count > 0 || !(optAmz.partnered_total > 0)) return null;
                            var total = optAmz.partnered_total + feesForOpt(opt);
                            var perUnitTotal = totalUnits > 0 ? total / totalUnits : 0;
                            return h('div', { style: { display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 600 } },
                              h('span', null, 'Total estimate' + (isCheapest ? ' ✓' : '')),
                              h('div', { style: { textAlign: 'right' } },
                                h('span', { style: { color: isCheapest ? '#10B981' : C.text } }, '$' + total.toFixed(2)),
                                // Combined landed % — placement fees + partnered shipping over shipment
                                // cost (the per-fee rows above show fees-only percentages).
                                totalUnits > 0 && h('div', { style: { fontSize: 10, fontWeight: 400, color: C.textMuted, marginTop: 1 } },
                                  '$' + perUnitTotal.toFixed(4) + '/unit' + (totalCost > 0 ? ' · ' + (total / totalCost * 100).toFixed(2) + '% of cost' : ''))
                              )
                            );
                          })()
                        )
                      )
                    );
                  }),
                  (function() {
                    var selEntry = null;
                    displayOpts.forEach(function(en) { if ((en.opt.placementOptionId || en.opt.id) === selectedPlacementOpt) selEntry = en; });
                    var selTxt = 'Confirm Placement';
                    if (selEntry) {
                      var selPid = selEntry.opt.placementOptionId || selEntry.opt.id;
                      var selAmz = amzOptMap[selPid];
                      var selTot = selAmz && !(selAmz.unavailable_count > 0) && selAmz.partnered_total > 0 ? selAmz.partnered_total + feesForOpt(selEntry.opt) : null;
                      selTxt = 'Confirm Option ' + (selEntry.origIdx + 1) + (selTot != null ? ' — $' + selTot.toFixed(2) + ' est.' : '');
                    }
                    return h('div', { style: { marginTop: 10, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' } },
                      h(Btn, { variant: 'primary', onClick: handleConfirmPlacement, disabled: !selectedPlacementOpt || !!actionLoading }, actionLoading === 'confirm-placement' ? 'Confirming...' : selTxt),
                      !selectedPlacementOpt && h('span', { style: { fontSize: 11, color: C.textMuted } }, 'Select a placement option to continue')
                    );
                  })()
                );
              })()
          )
    );
  }

  function renderTransportation() {
    if (!shipments.length) return h(Card, null, h('p', { style: { color: C.textMuted } }, 'Complete placement first.'));
    var isLtl = plan && plan.shipment_type === 'LTL';

    return h('div', { style: { display: 'flex', flexDirection: 'column', gap: 12 } },
      (function() {
        var unconfirmedTransport = shipments.filter(function(s) { return s.status !== 'transport_confirmed' && s.status !== 'shipped'; });
        return unconfirmedTransport.length > 0 && h(Card, null,
          h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' } },
            h('div', null,
              h('div', { style: { fontSize: 13, fontWeight: 600 } }, unconfirmedTransport.length + ' shipment(s) need transport setup'),
              h('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 2 } },
                isLtl ? 'Auto-confirm delivery windows and generate transport options' : 'Auto-select cheapest Amazon partnered carrier (UPS/FedEx) for all shipments')
            ),
            h('span', { 'data-fba-hover': 'generate', title: 'Automatically confirms delivery windows, generates transport options, and selects the cheapest Amazon partnered carrier for all shipments' },
              h(Btn, { variant: 'primary', onClick: function() {
                setActionLoading('auto-transport');
                api('/fba-shipments/plans/' + planId + '/auto-transport', { method: 'POST' })
                  .then(function() { startAutoTransportPoll(); }) // poll lives in a ref — see review M7 #90
                  .catch(function(e) { showToast(e.message, 'error'); setActionLoading(''); loadPlan(); });
              }, disabled: !!actionLoading },
                actionLoading === 'auto-transport'
                  ? [h(LIcon, { key: 'l', name: 'loader-2', size: 14, spin: true }), ' Processing...']
                  : [h(LIcon, { key: 'l', name: 'zap', size: 14 }), ' Auto-Confirm Transport'])
            )
          )
        );
      })(),
      shipments.map(function(s) {
        var opts = transportOptions[s.id] || [];
        var delOpts = deliveryOptions[s.id] || [];
        var isConfirmed = s.status === 'transport_confirmed' || s.status === 'labels_ready' || s.status === 'shipped';
        var hasDeliveryWindow = !!s.delivery_window_option_id;
        // Does THIS shipment's selected transport option require a delivery window? Partnered LTL
        // options come back with empty preconditions (no window needed); only when the chosen option
        // lists CONFIRMED_DELIVERY_WINDOW do we surface/require the delivery-window step.
        var selOptObj = opts.find(function(o) { return (o.transportationOptionId || o.id) === selectedTransportOpt[s.id]; });
        var selectedNeedsDW = isLtl && !!(selOptObj && Array.isArray(selOptObj.preconditions) && selOptObj.preconditions.indexOf('CONFIRMED_DELIVERY_WINDOW') >= 0);
        var tda = s.destination_address ? (typeof s.destination_address === 'string' ? JSON.parse(s.destination_address) : s.destination_address) : {};
        var tdAddr = tda.address || {};
        var tdLine = [tdAddr.addressLine1, tdAddr.addressLine2].filter(Boolean).join(', ');
        var tdCity = [[tdAddr.city, tdAddr.stateOrProvinceCode].filter(Boolean).join(', '), tdAddr.postalCode].filter(Boolean).join(' ');

        return h(Card, { key: s.id },
          // Header
          h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 } },
            h('div', null,
              h('div', { style: { fontSize: 11, color: C.textMuted } }, 'Shipment ID: ', h('span', { style: { fontWeight: 600, fontSize: 13, color: C.text } }, s.amazon_shipment_id || 'Pending')),
              s.amazon_reference_id && h('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 2 } }, 'Amazon PO ID: ', h('span', { style: { fontWeight: 600, fontSize: 13, color: C.text } }, s.amazon_reference_id))
            ),
            h('div', null,
              h('div', { style: { fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 2 } }, 'Ship To:'),
              s.destination_fc && h('div', { style: { fontSize: 13, fontWeight: 700, fontFamily: 'inherit', color: C.text } }, s.destination_fc),
              (tdLine || tdCity) && h('div', { style: { fontSize: 11, color: C.textMuted, lineHeight: '1.3' } },
                tdLine && h('div', null, tdLine),
                tdCity && h('div', null, tdCity)
              )
            )
          ),

          // Case + pallet labels stay printable during transport (LTL only — SPD gets its
          // complete label set from Amazon at the Labels step; case labels are an LTL concept)
          isLtl && h('div', { style: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 } },
            h(Btn, { size: 'sm', onClick: function() { printShipmentCaseLabels(s); }, style: { background: 'rgba(79,110,247,0.1)', color: C.accent, border: '1px solid rgba(79,110,247,0.3)' } },
              h(LIcon, { name: 'printer', size: 13 }), ' Case Labels'),
            h(Btn, { size: 'sm', onClick: function() {
                var rows = palletRowsOrPrompt(palletData[s.id]);
                if (rows) printFbaPalletLabels(s, plan, rows, showToast);
              }, style: { background: 'rgba(167,139,250,0.1)', color: '#8B5CF6', border: '1px solid rgba(167,139,250,0.3)' } },
              h(LIcon, { name: 'printer', size: 13 }), ' Pallet Labels')
          ),

          isConfirmed
            ? h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, color: C.success, fontSize: 12 } }, h(LIcon, { name: 'check-circle', size: 14 }), 'Transport confirmed')
            : h('div', { style: { display: 'flex', flexDirection: 'column', gap: 16 } },

              // ── Phase 1: Pallet & Freight Details (LTL only — no dependencies) ──
              isLtl && (function() {
                var shipId = s.id;
                var pds = Array.isArray(palletData[shipId]) ? palletData[shipId] : [];
                var saveCurrent = function(arr) {
                  api('/fba-shipments/shipments/' + shipId + '/pallets', {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ pallets: arr || pds })
                  }).catch(function(e) { console.error('[silent-catch]', e); });
                };
                var updatePal = function(idx, field, val) {
                  setPalletData(function(prev) {
                    var n = Object.assign({}, prev);
                    var arr = (n[shipId] || []).map(function(p) { return Object.assign({}, p); });
                    arr[idx] = Object.assign({}, arr[idx]);
                    arr[idx][field] = val;
                    n[shipId] = arr;
                    return n;
                  });
                };
                var updateAndSave = function(idx, field, val) {
                  var arr = pds.map(function(p) { return Object.assign({}, p); });
                  arr[idx] = Object.assign({}, arr[idx]); arr[idx][field] = val;
                  setPalletData(function(prev) { var n = Object.assign({}, prev); n[shipId] = arr; return n; });
                  saveCurrent(arr);
                };
                var addPallet = function() {
                  var arr = pds.concat([{ pallet_count: 1, length: 48, width: 40, height: '', weight: 0, stackability: 'NON_STACKABLE' }]);
                  setPalletData(function(prev) { var n = Object.assign({}, prev); n[shipId] = arr; return n; });
                  saveCurrent(arr);
                };
                var removePallet = function(idx) {
                  var arr = pds.filter(function(_, i) { return i !== idx; });
                  setPalletData(function(prev) { var n = Object.assign({}, prev); n[shipId] = arr; return n; });
                  saveCurrent(arr);
                };
                var saveOnBlur = function() { saveCurrent(palletData[shipId] || pds); };
                var inpStyle = { width: 64, padding: '4px 6px', fontSize: 12, border: '1px solid ' + C.border, borderRadius: 4, background: C.bg, color: C.text, textAlign: 'center' };

                // Freight class estimator: density = total weight / total cubic feet
                var freightClasses = [
                  { code: 'FC_500', label: '500', maxDensity: 1 },
                  { code: 'FC_400', label: '400', maxDensity: 2 },
                  { code: 'FC_300', label: '300', maxDensity: 3 },
                  { code: 'FC_250', label: '250', maxDensity: 4 },
                  { code: 'FC_200', label: '200', maxDensity: 5 },
                  { code: 'FC_175', label: '175', maxDensity: 6 },
                  { code: 'FC_150', label: '150', maxDensity: 7 },
                  { code: 'FC_125', label: '125', maxDensity: 8 },
                  { code: 'FC_110', label: '110', maxDensity: 9 },
                  { code: 'FC_100', label: '100', maxDensity: 10.5 },
                  { code: 'FC_92_5', label: '92.5', maxDensity: 12 },
                  { code: 'FC_85', label: '85', maxDensity: 13.5 },
                  { code: 'FC_77_5', label: '77.5', maxDensity: 15 },
                  { code: 'FC_70', label: '70', maxDensity: 22.5 },
                  { code: 'FC_65', label: '65', maxDensity: 30 },
                  { code: 'FC_60', label: '60', maxDensity: 35 },
                  { code: 'FC_55', label: '55', maxDensity: 50 },
                  { code: 'FC_50', label: '50', maxDensity: 999 },
                ];
                var totalWeight = 0; var totalCuFt = 0;
                pds.forEach(function(pd) {
                  var qty = parseInt(pd.pallet_count) || 1;
                  totalWeight += qty * (parseFloat(pd.weight) || 0);
                  totalCuFt += qty * ((parseFloat(pd.length) || 48) * (parseFloat(pd.width) || 40) * (parseFloat(pd.height) || 60)) / 1728;
                });
                var density = totalCuFt > 0 ? totalWeight / totalCuFt : 0;
                var estimatedClass = 'FC_50';
                for (var fci = 0; fci < freightClasses.length; fci++) {
                  if (density <= freightClasses[fci].maxDensity) { estimatedClass = freightClasses[fci].code; break; }
                }
                var estimatedLabel = freightClasses.find(function(fc) { return fc.code === estimatedClass; });
                // Entered (pallet rows, live) beside what was packed and what the catalog says the plan weighs
                var wts = fbaShipmentWeights(s);
                var wtBox = function(label, value, title) {
                  return h('div', { title: title, style: { fontSize: 11, color: C.textMuted, padding: '6px 10px', background: C.surface, borderRadius: 6, border: '1px solid ' + C.border, lineHeight: 1.5 } },
                    h('div', null, label),
                    h('div', { style: { fontWeight: 600, color: C.text } }, value));
                };

                // Freight state for this shipment
                var fd = freightData[shipId] || {};
                var curFreightClass = fd.freight_class !== undefined ? fd.freight_class : (s.freight_class || '');
                var curDeclaredValue = fd.declared_value !== undefined ? fd.declared_value : (s.declared_value || '');

                var saveFreightField = function(field, val) {
                  setFreightData(function(prev) { var n = Object.assign({}, prev); n[shipId] = Object.assign({}, prev[shipId] || {}, {}); n[shipId][field] = val; return n; });
                  var body = {};
                  body[field] = val;
                  api('/fba-shipments/shipments/' + shipId, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).catch(function(e) { console.error('[silent-catch]', e); });
                };

                // (declared-value auto-fill moved to an effect on FbaDetailPage — review M7 #88)

                // Freight class is left unset on the shipment; backend computes
                // from saved pallet data at the moment of generateTransportationOptions.
                // (Auto-saving here would lock FC_500 the moment the user types the first
                // digit of weight, before the rest of the pallet data exists.)

                return h('div', { style: { borderBottom: '1px solid ' + C.border, paddingBottom: 12 } },
                  h('div', { style: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 } },
                    h('div', { style: { fontSize: 12, fontWeight: 600, color: C.text } }, '1. Pallet & Freight Details'),
                    h('span', { 'data-fba-hover': 'add' }, h(Btn, { size: 'sm', onClick: addPallet, style: { fontSize: 12, padding: '4px 12px', background: 'rgba(79,110,247,0.15)', color: C.accent, border: '1px solid rgba(79,110,247,0.3)', fontWeight: 600 } }, '+ Add Pallet'))
                  ),
                  pds.length === 0
                  ? h('div', { style: { fontSize: 12, color: C.textMuted, fontStyle: 'italic', padding: '8px 0' } }, 'No pallets added. Click "+ Add Pallet" to enter dimensions, weight, and get a freight class estimate.')
                  : h('div', null,
                    h('div', { style: { display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4, fontSize: 10, color: C.textMuted, fontWeight: 500 } },
                      h('span', { style: { width: 40 } }, 'Qty'),
                      h('span', { style: { width: 64 } }, 'L (in)'),
                      h('span', { style: { width: 64 } }, 'W (in)'),
                      h('span', { style: { width: 64 } }, 'H (in)'),
                      h('span', { style: { width: 72 } }, 'Wt (lbs)'),
                      h('span', { style: { width: 100 } }, 'Stackable')
                    ),
                    pds.map(function(pd, idx) {
                      var missingBorder = '1px solid #F87171';
                      var normalBorder = '1px solid ' + C.border;
                      var qtyVal = parseFloat(pd.pallet_count) || 0;
                      var lenVal = parseFloat(pd.length) || 0;
                      var widVal = parseFloat(pd.width) || 0;
                      var htVal = parseFloat(pd.height) || 0;
                      var wtVal = parseFloat(pd.weight) || 0;
                      var htOver = htVal > 72;
                      var wtOver = wtVal > 1500;
                      var errors = [];
                      if (htOver) errors.push('Height exceeds Amazon max of 72 inches');
                      if (wtOver) errors.push('Weight exceeds Amazon max of 1,500 lbs');
                      return h('div', { key: idx },
                        h('div', { style: { display: 'flex', gap: 6, alignItems: 'center', marginBottom: errors.length ? 2 : 4 } },
                          h('input', { type: 'number', min: '1', value: pd.pallet_count, onChange: function(e) { updatePal(idx, 'pallet_count', e.target.value); }, onBlur: saveOnBlur, style: Object.assign({}, inpStyle, { width: 40, border: qtyVal <= 0 ? missingBorder : normalBorder }) }),
                          h('input', { type: 'number', value: pd.length, onChange: function(e) { updatePal(idx, 'length', e.target.value); }, onBlur: saveOnBlur, style: Object.assign({}, inpStyle, { border: lenVal <= 0 ? missingBorder : normalBorder }) }),
                          h('input', { type: 'number', value: pd.width, onChange: function(e) { updatePal(idx, 'width', e.target.value); }, onBlur: saveOnBlur, style: Object.assign({}, inpStyle, { border: widVal <= 0 ? missingBorder : normalBorder }) }),
                          h('input', { type: 'number', value: pd.height, onChange: function(e) { updatePal(idx, 'height', e.target.value); }, onBlur: saveOnBlur, style: Object.assign({}, inpStyle, { border: htOver || htVal <= 0 ? missingBorder : normalBorder }) }),
                          h('input', { type: 'number', value: pd.weight, onChange: function(e) { updatePal(idx, 'weight', e.target.value); }, onBlur: saveOnBlur, style: Object.assign({}, inpStyle, { width: 72, border: wtOver || wtVal <= 0 ? missingBorder : normalBorder }) }),
                          h('select', { value: pd.stackability, onChange: function(e) { updateAndSave(idx, 'stackability', e.target.value); },
                            style: { width: 100, padding: '4px 4px', fontSize: 11, border: normalBorder, borderRadius: 4, background: C.bg, color: C.text } },
                            h('option', { value: 'STACKABLE' }, 'Stackable'),
                            h('option', { value: 'NON_STACKABLE' }, 'Non-Stack')
                          ),
                          pds.length > 1 && h('button', { 'data-fba-hover': 'delete', onClick: function() { removePallet(idx); }, title: 'Remove',
                            style: { background: 'none', border: 'none', cursor: 'pointer', color: '#F87171', fontSize: 20, padding: '0 6px', lineHeight: 1, borderRadius: 4 } }, '\u00D7')
                        ),
                        errors.length > 0 && h('div', { style: { fontSize: 11, color: '#F87171', marginBottom: 4, paddingLeft: 2 } },
                          errors.map(function(err, ei) { return h('div', { key: ei, style: { display: 'flex', alignItems: 'center', gap: 4 } }, h(LIcon, { name: 'alert-triangle', size: 12 }), err); })
                        )
                      );
                    })
                  ),
                  // ── Declared Value & Freight Estimate (only show when pallets exist) ──
                  pds.length > 0 &&
                  h('div', { style: { display: 'flex', gap: 16, marginTop: 10, flexWrap: 'wrap', alignItems: 'flex-end' } },
                    h('div', null,
                      h('div', { style: { fontSize: 10, color: C.textMuted, marginBottom: 2, fontWeight: 500 } }, 'Declared Value ($) max $100k'),
                      h('input', {
                        type: 'number', min: '1', max: '100000', step: '0.01',
                        value: curDeclaredValue,
                        onChange: function(e) {
                          setFreightData(function(prev) { var n = Object.assign({}, prev); n[shipId] = Object.assign({}, prev[shipId] || {}, { declared_value: e.target.value }); return n; });
                        },
                        onBlur: function(e) {
                          var val = Math.min(parseFloat(e.target.value) || 0, 100000);
                          if (val > 0) saveFreightField('declared_value', val);
                        },
                        style: { padding: '5px 8px', borderRadius: 4, border: '1px solid ' + C.border, background: C.bg, color: C.text, fontSize: 12, width: 100 }
                      })
                    ),
                    h('div', { style: { fontSize: 11, color: C.textMuted, padding: '6px 10px', background: C.surface, borderRadius: 6, border: '1px solid ' + C.border, lineHeight: 1.5 } },
                      density > 0
                        ? [
                            h('div', { key: 'd' }, 'Density: ', h('span', { style: { fontWeight: 600, color: C.text } }, density.toFixed(1) + ' lbs/ft\u00B3')),
                            h('div', { key: 'f' }, 'Freight Class: ', h('span', { style: { fontWeight: 600, color: C.accent } }, 'Class ' + (estimatedLabel ? estimatedLabel.label : '?')))
                          ]
                        : h('div', { style: { fontStyle: 'italic' } }, 'Enter pallet weight to estimate freight class')
                    ),
                    wtBox('Entered Weight', fbaFmtLbs(totalWeight), 'Pallet quantity × weight for the pallets entered above'),
                    wtBox('Expected Weight', wts.boxes ? fbaFmtLbs(wts.expected) : '—',
                      wts.boxes ? 'Sum of the ' + wts.boxes + ' box' + (wts.boxes === 1 ? '' : 'es') + ' packed (a phantom box is not counted)' : 'No boxes packed on this shipment yet'),
                    wtBox('Catalog Weight', wts.catalog > 0 ? fbaFmtLbs(wts.catalog) : '—',
                      'Catalog unit weight × planned units, missing items included' + (wts.noWeightSkus ? ' · ' + wts.noWeightSkus + ' SKU' + (wts.noWeightSkus === 1 ? ' has' : 's have') + ' no catalog weight and count' + (wts.noWeightSkus === 1 ? 's' : '') + ' as 0' : ''))
                  )
                );
              })(),

              // ── Delivery Window — LTL only surfaces this when the chosen carrier requires it ──
              (!isLtl || selectedNeedsDW) && h('div', { style: { borderBottom: '1px solid ' + C.border, paddingBottom: 12 } },
                h('div', { style: { fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 8 } }, isLtl ? 'Delivery Window — required by this carrier' : 'Delivery Window'),
                hasDeliveryWindow
                  ? h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, color: C.success, fontSize: 12 } },
                      h(LIcon, { name: 'check-circle', size: 14 }),
                      'Delivery window confirmed',
                      s.delivery_window_start && h('span', { style: { color: C.textMuted, marginLeft: 4 } },
                        '(' + new Date(s.delivery_window_start).toLocaleDateString() + (s.delivery_window_end ? ' – ' + new Date(s.delivery_window_end).toLocaleDateString() : '') + ')')
                    )
                  : h('div', null,
                      delOpts.length === 0
                        ? h(Btn, { size: 'sm', onClick: function() { handleGenDeliveryWindows(s.id); }, disabled: !!actionLoading },
                            actionLoading === 'delivery-' + s.id ? 'Loading...' : 'Get Delivery Windows')
                        : h('div', { style: { display: 'flex', gap: 8, alignItems: 'center', maxWidth: 420 } },
                            h('select', { style: { flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid ' + C.border, background: C.bg, color: C.text, fontSize: 12 },
                              value: selectedDeliveryOpt[s.id] ? (selectedDeliveryOpt[s.id].deliveryWindowOptionId || selectedDeliveryOpt[s.id].id || '') : '',
                              onChange: function(e) {
                                var val = e.target.value;
                                var match = delOpts.find(function(o) { return (o.deliveryWindowOptionId || o.id || '') === val; });
                                setSelectedDeliveryOpt(function(prev) { var n = Object.assign({}, prev); n[s.id] = match || null; return n; });
                              }
                            },
                              h('option', { value: '' }, '— Select delivery window —'),
                              delOpts.slice().sort(function(a, b) {
                                var aStart = new Date(a.startDate || a.start || 0).getTime();
                                var bStart = new Date(b.startDate || b.start || 0).getTime();
                                return aStart - bStart;
                              }).map(function(opt, idx) {
                                var optId = opt.deliveryWindowOptionId || opt.id || idx;
                                var startD = opt.startDate || opt.start || '';
                                var endD = opt.endDate || opt.end || '';
                                var availType = opt.availabilityType ? ' (' + opt.availabilityType.replace(/_/g, ' ') + ')' : '';
                                var label = (startD ? new Date(startD).toLocaleDateString() : '?') + ' – ' + (endD ? new Date(endD).toLocaleDateString() : '?') + availType;
                                return h('option', { key: optId, value: optId }, label);
                              })
                            ),
                            h(Btn, { size: 'sm', variant: 'primary', onClick: function() { handleConfirmDeliveryWindow(s.id); }, disabled: !selectedDeliveryOpt[s.id] || !!actionLoading },
                              actionLoading === 'confirm-delivery-' + s.id ? 'Confirming...' : 'Confirm')
                          )
                    )
              ),

              // ── Phase 3: Transport Options ──
              (function() {
                var allDWConfirmed = isLtl ? shipments.every(function(sh) { return !!sh.delivery_window_option_id; }) : hasDeliveryWindow;
                var dwRemaining = isLtl ? shipments.filter(function(sh) { return !sh.delivery_window_option_id; }).length : 0;
                // For LTL: gather options across ALL shipments for unified carrier selection
                var allShipOpts = {};
                if (isLtl) { shipments.forEach(function(sh) { allShipOpts[sh.id] = transportOptions[sh.id] || transportOptions[String(sh.id)] || []; }); }
                var anyOptsLoaded = isLtl ? Object.keys(allShipOpts).some(function(k) { return allShipOpts[k].length > 0; }) : opts.length > 0;
                // Build unified carrier list (by alphaCode) with per-shipment optionIds and costs —
                // one option per carrier per shipment, the earliest pickup on or after the ready
                // date the last Generate sent (fbaBuildCarrierMap).
                // { alphaCode: { name, solution, shipments: [{ shipId, optId, cost, appt, fc, early }], totalCost } }
                var carrierMap = (isLtl && anyOptsLoaded) ? fbaBuildCarrierMap(allShipOpts, shipments, sentReadyDate) : {};
                // Pickup-date helpers. carrierAppointment.startTime is a 00:00Z..23:59Z range, so
                // render in UTC to avoid showing the previous day in timezones behind UTC.
                var fmtPickup = function(iso) {
                  if (!iso) return '';
                  var d = new Date(iso);
                  if (isNaN(d.getTime())) return '';
                  return d.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' });
                };
                var carrierAppts = function(code) {
                  return (carrierMap[code] ? carrierMap[code].shipments : []).map(function(s) { return s.appt; }).filter(Boolean);
                };
                // Rank a carrier by its LATEST pickup across shipments (so the default carrier is the
                // one that gets every shipment picked up soonest). Carriers with no appointment rank last.
                var carrierPickupSort = function(code) {
                  var ts = carrierAppts(code).map(function(x) { return new Date(x).getTime(); }).filter(function(t) { return !isNaN(t); });
                  return ts.length ? Math.max.apply(null, ts) : Infinity;
                };
                // A carrier is "early" when a shipment had nothing on or after the ready date and fell
                // back to an earlier pickup — the label says so and the carrier ranks after the others.
                var carrierEarly = function(code) {
                  return (carrierMap[code] ? carrierMap[code].shipments : []).some(function(s) { return s.early; });
                };
                var carrierPickupLabel = function(code) {
                  var a = carrierAppts(code).slice().sort();
                  if (!a.length) return '';
                  var first = fmtPickup(a[0]), last = fmtPickup(a[a.length - 1]);
                  return (first === last ? first : first + '–' + last) + (carrierEarly(code) ? ' (before ready date)' : '');
                };
                // Only show carriers available for ALL shipments
                var validCarriers = Object.keys(carrierMap).filter(function(code) { return carrierMap[code].shipments.length === shipments.length; });
                // Sort: on time before early, then earliest pickup (no appointment ranks last), then
                // partnered, then cheapest
                validCarriers.sort(function(a, b) {
                  var aE = carrierEarly(a) ? 1 : 0, bE = carrierEarly(b) ? 1 : 0;
                  if (aE !== bE) return aE - bE;
                  var aP = carrierPickupSort(a), bP = carrierPickupSort(b);
                  if (aP !== bP) return aP - bP;
                  var aSol = carrierMap[a].solution === 'AMAZON_PARTNERED_CARRIER' ? 0 : 1;
                  var bSol = carrierMap[b].solution === 'AMAZON_PARTNERED_CARRIER' ? 0 : 1;
                  if (aSol !== bSol) return aSol - bSol;
                  return carrierMap[a].totalCost - carrierMap[b].totalCost;
                });
                return h('div', null,
                h('div', { style: { fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 8 } }, 'Transport Options'),
                (!isLtl && !allDWConfirmed)
                  ? h('div', { style: { fontSize: 12, color: C.textMuted, fontStyle: 'italic' } }, 'Please confirm a delivery window first')
                  : !anyOptsLoaded
                    ? h('div', null,
                        h('div', { style: { display: 'flex', gap: 8, alignItems: 'flex-end' } },
                          h('div', null,
                            h('div', { style: { fontSize: 10, color: C.textMuted, marginBottom: 2 } }, 'Ready to Ship'),
                            h('input', { type: 'date', value: readyDates[s.id] || defaultReadyDate,
                              // min = LOCAL today (toISOString is UTC — in the evening it lands on
                              // tomorrow and blocks picking today, which Seller Central allows)
                              min: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().split('T')[0],
                              onChange: function(e) { setReadyDates(function(prev) { var n = Object.assign({}, prev); n[s.id] = e.target.value; return n; }); },
                              style: { padding: '4px 6px', fontSize: 12, border: '1px solid ' + C.border, borderRadius: 4, background: '#fff', color: '#111', colorScheme: 'light' } })
                          ),
                          h('span', { 'data-fba-hover': 'generate' }, h(Btn, { size: 'sm', onClick: function() { handleGenTransportOpts(s.id); }, disabled: !!actionLoading },
                            actionLoading === 'transport-' + s.id ? 'Loading...' : 'Generate Transport Options'))
                        ),
                        (function() {
                          var picked = readyDates[s.id] || defaultReadyDate;
                          var eff = fbaEffectiveReadyDate(picked);
                          if (!eff) return null;
                          var shifted = eff !== picked;
                          var isToday = picked === defaultReadyDate;
                          // Today is the default and always shifts (its UTC midnight has passed) — said
                          // quietly; a later date that still shifts (after ~8 PM ET) gets the warning.
                          var msg = !shifted
                            ? 'Amazon will see freight ready ' + fbaFmtReadyDay(eff)
                            : isToday
                              ? 'Freight ready today — Amazon will see it ready ' + fbaFmtReadyDay(eff) + ' (its day boundary is UTC)'
                              : 'Amazon can no longer accept ' + fbaFmtReadyDay(picked) + ' (its UTC day boundary passed) — it will see freight ready ' + fbaFmtReadyDay(eff);
                          // Amazon offers a pickup for every business day its carriers can do, from the
                          // day after the request on, whatever the ready date (plan 692, 2026-10-20) —
                          // the carrier list keeps the earliest pickup on or after the ready date.
                          if (isLtl) msg += ' · the carrier list will pick the earliest pickup on or after ' + fbaFmtReadyDay(eff);
                          return h('div', { style: { fontSize: 10, color: shifted && !isToday ? C.warning : C.textMuted, marginTop: 4 } }, msg);
                        })()
                      )
                    : isLtl
                    ? (function() {
                        // ── LTL Unified Carrier Dropdown ──
                        var selectedCarrier = null;
                        validCarriers.forEach(function(code) {
                          var cm = carrierMap[code];
                          var allMatch = cm.shipments.every(function(cs) { return selectedTransportOpt[cs.shipId] === cs.optId; });
                          if (allMatch && cm.shipments.length) selectedCarrier = code;
                        });
                        // Auto-select the earliest-pickup carrier if nothing selected
                        // (validCarriers is sorted earliest-pickup-first)
                        if (!selectedCarrier && validCarriers.length > 0) {
                          var autoCode = validCarriers[0];
                          var autoCm = carrierMap[autoCode];
                          // Set it
                          var autoSelections = {};
                          autoCm.shipments.forEach(function(cs) { autoSelections[cs.shipId] = cs.optId; });
                          setSelectedTransportOpt(function(prev) { return Object.assign({}, prev, autoSelections); });
                          selectedCarrier = autoCode;
                        }
                        var handleSelectCarrier = function(code) {
                          if (!code) return;
                          var cm = carrierMap[code];
                          setSelectedTransportOpt(function(prev) {
                            var n = Object.assign({}, prev);
                            cm.shipments.forEach(function(cs) { n[cs.shipId] = cs.optId; });
                            return n;
                          });
                        };
                        var selCm = selectedCarrier ? carrierMap[selectedCarrier] : null;
                        return h('div', null,
                          shipments.length > 1 && h('div', { style: { fontSize: 11, color: C.textMuted, marginBottom: 8 } }, 'Same carrier will be used across all ' + shipments.length + ' shipments.'),
                          h('select', {
                            value: selectedCarrier || '',
                            onChange: function(e) { handleSelectCarrier(e.target.value); },
                            style: { width: '100%', maxWidth: 420, padding: '8px 12px', borderRadius: 6, border: '1px solid ' + C.border, background: C.bg, color: C.text, fontSize: 13, fontFamily: 'inherit' }
                          },
                            h('option', { value: '' }, '— Select carrier —'),
                            validCarriers.map(function(code) {
                              var cm = carrierMap[code];
                              var isPartnered = cm.solution === 'AMAZON_PARTNERED_CARRIER';
                              var label = (isPartnered ? '\u2605 ' : '') + cm.name + (code !== cm.name ? ' (' + code + ')' : '');
                              if (cm.totalCost > 0) label += ' — $' + cm.totalCost.toFixed(2);
                              var pk = carrierPickupLabel(code);
                              if (pk) label += ' — pickup ' + pk;
                              if (isPartnered) label += ' [Partnered]';
                              return h('option', { key: code, value: code }, label);
                            })
                          ),
                          // Show per-shipment breakdown when a carrier is selected
                          selCm && h('div', { style: { marginTop: 10, padding: '10px 12px', background: C.surface, borderRadius: 6, border: '1px solid ' + C.border } },
                            h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: selCm.shipments.length > 1 ? 8 : 0 } },
                              h('div', null,
                                h('span', { style: { fontWeight: 600, fontSize: 13, color: C.text } }, selCm.name),
                                selCm.solution === 'AMAZON_PARTNERED_CARRIER' && h('span', { style: { marginLeft: 8, fontSize: 10, padding: '2px 6px', borderRadius: 3, background: 'rgba(52,211,153,0.15)', color: C.success, fontWeight: 600 } }, 'PARTNERED'),
                                carrierPickupLabel(selectedCarrier) && h('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 3 } }, 'Est. pickup ' + carrierPickupLabel(selectedCarrier))
                              ),
                              selCm.totalCost > 0 && h('span', { style: { fontSize: 15, fontWeight: 700, color: selCm.solution === 'AMAZON_PARTNERED_CARRIER' ? C.success : C.text } }, '$' + selCm.totalCost.toFixed(2))
                            ),
                            selCm.shipments.length > 1 && h('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 6 } },
                              selCm.shipments.map(function(cs) {
                                return h('div', { key: cs.shipId, style: { fontSize: 11, color: C.textMuted, padding: '3px 8px', background: C.bg, borderRadius: 4 } },
                                  h('span', { style: { fontWeight: 600, fontFamily: 'inherit' } }, cs.fc || 'Ship ' + cs.shipId),
                                  cs.cost > 0 ? h('span', { style: { marginLeft: 6, color: C.text } }, '$' + cs.cost.toFixed(2)) : null,
                                  cs.appt ? h('span', { style: { marginLeft: 6, color: cs.early ? C.warning : undefined } }, '· ' + fmtPickup(cs.appt) + (cs.early ? ' (before ready date)' : '')) : null
                                );
                              })
                            )
                          ),
                          selectedNeedsDW && !allDWConfirmed && h('div', { style: { fontSize: 11, color: C.textMuted, fontStyle: 'italic', marginTop: 8 } }, 'This carrier requires a delivery window — confirm one above before confirming transport.'),
                          selectedCarrier && h(Btn, { size: 'sm', variant: 'primary', disabled: !!actionLoading || (selectedNeedsDW && !allDWConfirmed), style: { marginTop: 10 },
                            onClick: function() {
                              var cm = carrierMap[selectedCarrier];
                              handleConfirmTransportAll(cm.shipments.map(function(cs) { return { shipment_id: cs.shipId, transport_option_id: cs.optId }; }));
                            }
                          }, actionLoading === 'confirm-transport-all' ? 'Confirming...' : 'Confirm Transport — All ' + shipments.length + ' Shipments')
                        );
                      })()
                    : (function() {
                        // ── SPD / Non-LTL: Original per-shipment options ──
                        var solutionLabels = { AMAZON_PARTNERED_CARRIER: 'Amazon Partnered Carrier', USE_YOUR_OWN_CARRIER: 'Your Own Carrier' };
                        var modeLabels = { FREIGHT_LTL: 'LTL Freight', FREIGHT_FTL: 'FTL Freight', GROUND_SMALL_PARCEL: 'Ground / Small Parcel' };
                        var groups = {};
                        opts.forEach(function(opt) {
                          var sol = opt.shippingSolution || 'OTHER';
                          if (!groups[sol]) groups[sol] = [];
                          groups[sol].push(opt);
                        });
                        Object.keys(groups).forEach(function(sol) {
                          groups[sol].sort(function(a, b) {
                            var ca = (a.carrier && (a.carrier.alphaCode || a.carrier.name) || '').toLowerCase();
                            var cb = (b.carrier && (b.carrier.alphaCode || b.carrier.name) || '').toLowerCase();
                            return ca < cb ? -1 : ca > cb ? 1 : 0;
                          });
                        });
                        return h('div', null,
                          Object.keys(groups).map(function(sol) {
                            var groupOpts = groups[sol];
                            var isOwn = sol === 'USE_YOUR_OWN_CARRIER';
                            return h('div', { key: sol, style: { marginBottom: 10 } },
                              h('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.03em' } }, solutionLabels[sol] || sol.replace(/_/g, ' ')),
                              isOwn
                                ? h('div', null,
                                    h('div', { style: { fontSize: 11, color: C.textMuted, marginBottom: 6 } }, 'Select your freight carrier:'),
                                    h('select', { style: { padding: '6px 10px', borderRadius: 4, border: '1px solid ' + C.border, background: C.bg, color: C.text, fontSize: 12, maxWidth: 320 },
                                      value: (function() { var sel = selectedTransportOpt[s.id]; return groupOpts.some(function(o) { return (o.transportationOptionId || o.id) === sel; }) ? sel : ''; })(),
                                      onChange: function(e) { setSelectedTransportOpt(function(prev) { var n = Object.assign({}, prev); n[s.id] = e.target.value; return n; }); }
                                    },
                                      h('option', { value: '' }, '— Select carrier —'),
                                      groupOpts.map(function(opt) {
                                        var optId = opt.transportationOptionId || opt.id;
                                        var scac = (opt.carrier && opt.carrier.alphaCode) || '';
                                        var carrierName = (opt.carrier && opt.carrier.name) || '';
                                        // Name first, SCAC as a trailing hint; if Amazon ever sends no name the code still shows.
                                        var code = carrierName || scac;
                                        if (scac && code !== scac) code += ' (' + scac + ')';
                                        var mode = modeLabels[opt.shippingMode] || opt.shippingMode || '';
                                        return h('option', { key: optId, value: optId }, code + (mode ? ' — ' + mode : ''));
                                      })
                                    )
                                  )
                                : groupOpts.map(function(opt, idx) {
                                    var optId = opt.transportationOptionId || opt.id || idx;
                                    var carrier = opt.carrier && opt.carrier.name ? opt.carrier.name : '';
                                    var mode = modeLabels[opt.shippingMode] || opt.shippingMode || '';
                                    var quoteAmt = opt.quote && opt.quote.cost ? opt.quote.cost.amount : (opt.quote ? opt.quote.amount : '');
                                    return h('label', { key: optId, style: { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 6, border: '1px solid ' + (selectedTransportOpt[s.id] === optId ? C.accent : C.border), background: selectedTransportOpt[s.id] === optId ? 'rgba(79,110,247,0.06)' : 'transparent', cursor: 'pointer', marginBottom: 4, fontSize: 12 } },
                                      h('input', { type: 'radio', checked: selectedTransportOpt[s.id] === optId, onChange: function() { setSelectedTransportOpt(function(prev) { var n = Object.assign({}, prev); n[s.id] = optId; return n; }); } }),
                                      h('span', { style: { fontWeight: 500 } }, (carrier || mode || 'Option') + (quoteAmt ? ' — $' + quoteAmt : ''))
                                    );
                                  })
                            );
                          }),
                          (function() {
                            // A multi-shipment plan is confirmed in ONE call once every card has a carrier (9-18).
                            var openShips = shipments.filter(function(sh) { return sh.status !== 'cancelled' && sh.status !== 'transport_confirmed' && sh.status !== 'labels_ready' && sh.status !== 'shipped'; });
                            if (openShips.length <= 1) return h(Btn, { size: 'sm', variant: 'primary', onClick: function() { handleConfirmTransport(s.id); }, disabled: !selectedTransportOpt[s.id] || !!actionLoading, style: { marginTop: 6 } }, 'Confirm Transport');
                            var missing = openShips.filter(function(sh) { return !selectedTransportOpt[sh.id]; });
                            return h('div', { style: { marginTop: 6 } },
                              h(Btn, { size: 'sm', variant: 'primary', disabled: missing.length > 0 || !!actionLoading,
                                onClick: function() { handleConfirmTransportAll(openShips.map(function(sh) { return { shipment_id: sh.id, transport_option_id: selectedTransportOpt[sh.id] }; })); } },
                                actionLoading === 'confirm-transport-all' ? 'Confirming...' : 'Confirm Transport — All ' + openShips.length + ' Shipments'),
                              missing.length > 0 && h('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 4 } },
                                'Amazon confirms all ' + openShips.length + ' shipments together. Select a carrier for ' + missing.map(function(sh) { return sh.destination_fc || ('shipment ' + sh.id); }).join(', ') + ' first.')
                            );
                          })()
                        );
                      })()
              );
              })()
            )
        );
      }),
      shipments.every(function(s) { return s.status === 'transport_confirmed' || s.status === 'labels_ready' || s.status === 'shipped'; }) &&
        h('div', { style: { display: 'flex', justifyContent: 'flex-end' } },
          h(Btn, { variant: 'primary', onClick: function() { setManualStep(stepIndex('labels')); } }, 'Continue to Labels ', h(LIcon, { name: 'arrow-right', size: 14 }))
        )
    );
  }

  function renderLabelsShip() {
    var isLtl = plan && plan.shipment_type === 'LTL';
    var isSPD = !isLtl;
    var hasOwnCarrier = shipments.some(function(s) { return s.transport_solution === 'USE_YOUR_OWN_CARRIER'; });
    var allOwnCarrier = shipments.every(function(s) { return s.transport_solution === 'USE_YOUR_OWN_CARRIER'; });
    // Calculate totals across all shipments
    var totalShippingCost = shipments.reduce(function(sum, s) { return sum + (parseFloat(s.transport_cost) || 0); }, 0);
    var totalPlacementFees = parseFloat(plan.placement_fees) || 0;

    return h('div', { style: { display: 'flex', flexDirection: 'column', gap: 12 } },
      // Cost summary
      (totalShippingCost > 0 || totalPlacementFees > 0) && h(Card, { style: { display: 'flex', gap: 24, alignItems: 'center', padding: '12px 16px' } },
        totalShippingCost > 0 && h('div', null,
          h('div', { style: { fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 2 } }, 'Total Shipping Cost'),
          h('div', { style: { fontSize: 20, fontWeight: 700, color: C.success } }, '$' + totalShippingCost.toFixed(2))
        ),
        totalPlacementFees > 0 && h('div', null,
          h('div', { style: { fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 2 } }, 'Total Placement Fees'),
          h('div', { style: { fontSize: 20, fontWeight: 700, color: '#f59e0b' } }, '$' + totalPlacementFees.toFixed(2))
        ),
        (totalShippingCost > 0 && totalPlacementFees > 0) && h('div', null,
          h('div', { style: { fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 2 } }, 'Total'),
          h('div', { style: { fontSize: 20, fontWeight: 700, color: C.text } }, '$' + (totalShippingCost + totalPlacementFees).toFixed(2))
        )
      ),
      // SPD aggregate-print card — fetches shipping + case labels for every shipment
      // and merges them into a single PDF, grouped per-shipment. Saves the warehouse
      // user from clicking through each ShipmentLabelsCard's two buttons individually.
      isSPD && shipments.some(function(s) { return !!s.amazon_shipment_id; }) && h(Card, { style: { padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 } },
        h('div', null,
          h('div', { style: { fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 2 } }, 'Print All Shipping Labels'),
          h('div', { style: { fontSize: 11, color: C.textMuted } }, 'Aggregates every shipment\'s shipping labels (box label + partnered-carrier label) into a single PDF, grouped per shipment.')
        ),
        h(Btn, { variant: 'primary', disabled: actionLoading === 'all-ship-case-labels',
          onClick: function() {
            setActionLoading('all-ship-case-labels');
            showToast('Aggregating shipping labels for all shipments…', 'info');
            api('/fba-shipments/plans/' + plan.id + '/all-shipping-and-case-labels', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
              .then(function(res) {
                if (res.base64) {
                  var blob = new Blob([Uint8Array.from(atob(res.base64), function(c) { return c.charCodeAt(0); })], { type: 'application/pdf' });
                  window.open(URL.createObjectURL(blob), '_blank');
                }
                loadPlan();
                showToast((res.shipments || 0) + ' shipment(s) — ' + (res.pages || 0) + ' shipping label page(s)', 'success');
              })
              .catch(function(e) { showToast('Failed: ' + e.message, 'error'); })
              .finally(function() { setActionLoading(''); });
          } },
          actionLoading === 'all-ship-case-labels' ? 'Aggregating…' : [h(LIcon, { key: 'ic', name: 'printer', size: 14 }), ' Print All Shipping Labels'])
      ),
      shipments.map(function(s) {
        return h(ShipmentLabelsCard, { key: s.id, shipment: s, plan: plan, palletData: palletData[s.id] || [], showToast: showToast, actionLoading: actionLoading,
          onGetLabels: function() { handleGetLabels(s.id); },
          onExportPicklist: function() { exportShipmentXlsx(s); },
          onExportWorkOrders: function() { exportShipmentWorkOrders(s, plan); },
          onMarkShipped: function(carrier, tid, pro, bol, boxTracking) { handleMarkShipped(s.id, carrier, tid, pro, bol, boxTracking); },
          onPrintCaseLabels: function() {
            var isSPD = plan && plan.shipment_type === 'SPD';
            if (isSPD && s.amazon_shipment_id) {
              // SPD: Fetch Amazon's official case labels
              showToast('Downloading Amazon case labels...', 'info');
              api('/fba-shipments/shipments/' + s.id + '/case-labels', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
                .then(function(res) { var url = res.downloadURL || res.DownloadURL; if (url) window.open(url, '_blank'); else showToast('No download URL returned', 'error'); })
                .catch(function(e) { showToast(e.message, 'error'); });
            } else {
              // Fallback to system-generated case labels (no shipment ID yet)
              var shipBoxIds = (s.boxes || []).map(function(b) { return b.id; });
              if (!shipBoxIds.length) { showToast('No boxes for this shipment', 'error'); return; }
              showToast('Generating ' + shipBoxIds.length + ' case label(s)...', 'info');
              Promise.all(shipBoxIds.map(function(boxId) { return api('/fba-shipments/boxes/' + boxId + '/case-label-data'); }))
                .then(function(allData) { printCaseLabelBatch(allData, shipBoxIds.length); })
                .catch(function(e) { showToast('Error: ' + e.message, 'error'); });
            }
          },
          onRefresh: loadPlan });
      })
    );
  }
}

// ─── Auto-pack eligibility (shared) ───
// Used by the case-packed wizard and, since 2026-10-22, the individual flow's Select Items step
// (Anthony: "the auto-pack for items that have case pack details stored ... is not working" — plan
// 728, an individual-packing LTL plan whose four SKUs all carry saved case packs). Saved case data
// + remaining evenly divisible by case_qty. Transparency-flagged SKUs are NEVER eligible — auto-pack
// seals boxes in one click, which would ship units without a label scan.
function fbaAutoPackInfo(availableItems) {
  return (availableItems || []).map(function(item) {
    var remaining = item.quantity - (item.quantity_packed || 0) - (item.quantity_missing || 0);
    var hasSaved = item.case_qty > 0 && item.case_length > 0;
    if (remaining <= 0) return { item: item, eligible: false, reason: !hasSaved ? 'no_saved' : 'no_remaining', remaining: remaining };
    if (item.requires_transparency && item.product_id) return { item: item, eligible: false, reason: 'transparency', remaining: remaining };
    if (!hasSaved) return { item: item, eligible: false, reason: 'no_saved', remaining: remaining };
    if (remaining % item.case_qty !== 0) return { item: item, eligible: false, reason: 'partial', remaining: remaining };
    return { item: item, eligible: true, remaining: remaining, cases: remaining / item.case_qty };
  });
}

// ─── Pallet labels (shared) ───
// Locally-generated 4x6 pallet labels, 4 per pallet (one per side). pds = fba_pallets-style
// rows [{ pallet_count }]. Used from the packing header, transport cards, and ship/labels card.
function printFbaPalletLabels(s, plan, pds, showToast) {
  pds = pds || [];
  var totalPallets = pds.reduce(function(sum, p) { return sum + (parseInt(p.pallet_count) || 0); }, 0);
  if (!totalPallets) { showToast('No pallet data', 'error'); return; }
  var win = window.open('', '_blank', 'width=420,height=630');
  if (!win) { showToast('Pop-up blocked', 'error'); return; }
  var shipId = s.amazon_shipment_id || 'DRAFT';
  var fc = s.destination_fc || '';
  var shipName = s.display_name || (shipId + '-' + fc);
  // Ship From
  var sf = plan.source_address || {};
  var sfName = sf.name || '';
  var sfLine1 = sf.addressLine1 || sf.address_line1 || '';
  var sfLine2 = sf.addressLine2 || '';
  var sfCity = sf.city || '';
  var sfState = sf.stateOrProvinceCode || sf.state || '';
  var sfZip = sf.postalCode || sf.postal_code || '';
  var sfCountry = sf.countryCode || sf.country_code || 'US';
  // Ship To
  var st = s.destination_address ? (typeof s.destination_address === 'string' ? JSON.parse(s.destination_address) : s.destination_address) : {};
  var stAddr = st.address || st;
  var stName = stAddr.name || fc;
  var stLine1 = stAddr.addressLine1 || '';
  var stCity = stAddr.city || '';
  var stState = stAddr.stateOrProvinceCode || '';
  var stZip = stAddr.postalCode || '';
  var stCountry = stAddr.countryCode || 'CA';
  var now = new Date();
  var created = now.getFullYear() + '/' + String(now.getMonth()+1).padStart(2,'0') + '/' + String(now.getDate()).padStart(2,'0') + ' ' + String(now.getHours()).padStart(2,'0') + ':' + String(now.getMinutes()).padStart(2,'0');

  var labelsPerPallet = 4;
  var palletNum = 0;
  // Ship-to seller line = the Amazon seller name (plan.seller_name from GET /plans/:id), never the
  // ship-from name: a vendor-direct plan ships from the vendor's warehouse and these labels read
  // "FBA: Spunk Lube" (Anthony 2026-10-22, FBA19M4SW7JG).
  var sellerLabel = 'FBA: ' + ((plan && plan.seller_name) || 'Seller');
  var html = '<!DOCTYPE html><html><head><title>Pallet Labels</title>' +
    '<style>' +
    '@page { size: 4in 6in; margin: 0; }' +
    'body { margin: 0; padding: 0; font-family: Arial, Helvetica, sans-serif; }' +
    '.label { width: 4in; height: 6in; padding: 3mm 4mm; box-sizing: border-box; display: flex; flex-direction: column; overflow: hidden; }' +
    '.label + .label { page-break-before: always; }' +
    '.banner { text-align: center; font-size: 7pt; font-weight: 700; letter-spacing: 0.5px; margin-bottom: 2px; }' +
    '.fba-hdr { font-size: 18pt; font-weight: 900; margin-bottom: 2px; }' +
    '.hr { border: none; border-top: 2.5px solid #000; margin: 2px 0; }' +
    '.addr-row { display: flex; gap: 8px; font-size: 8pt; line-height: 1.35; margin-bottom: 2px; }' +
    '.addr-col { flex: 1; }' +
    '.addr-col b { font-size: 7pt; text-transform: uppercase; }' +
    '.info-bar { background: #000 !important; color: #fff !important; font-size: 7pt; padding: 2px 4px; display: flex; justify-content: space-between; align-items: center; margin: 2px 0; font-weight: 600; -webkit-print-color-adjust: exact; print-color-adjust: exact; }' +
    '.barcode-center { text-align: center; }' +
    '.barcode-center svg { width: 70%; height: auto; }' +
    '.ship-id-text { font-family: monospace; font-size: 10pt; text-align: center; margin-top: 2px; letter-spacing: 1px; }' +
    '.pallet-num { text-align: center; font-size: 14pt; font-weight: 900; letter-spacing: 1px; margin-top: 6px; }' +
    '</style></head><body>';

  pds.forEach(function(pd) {
    var count = parseInt(pd.pallet_count) || 1;
    for (var i = 0; i < count; i++) {
      palletNum++;
      for (var side = 0; side < labelsPerPallet; side++) {
        var shipIdBarcode = generateCode128SVG(shipId, 60, 2);
        html += '<div class="label">' +
          '<div class="banner">PLEASE LEAVE THIS LABEL UNCOVERED</div>' +
          '<div class="fba-hdr">FBA</div>' +
          '<hr class="hr">' +
          '<div class="addr-row">' +
            '<div class="addr-col"><b>SHIP FROM:</b><div>' + escHtml(sfName) + '</div>' + (sfLine1 ? '<div>' + escHtml(sfLine1) + '</div>' : '') + (sfLine2 ? '<div>' + escHtml(sfLine2) + '</div>' : '') + '<div>' + escHtml([sfCity, sfState].filter(Boolean).join(', ') + ' ' + sfZip) + '</div><div>' + escHtml(sfCountry) + '</div></div>' + // escaped (review 2026-09-28, medium #40)
            '<div class="addr-col"><b>SHIP TO:</b><div>' + escHtml(sellerLabel) + '</div><div>' + escHtml(stName) + '</div>' + (stLine1 ? '<div>' + escHtml(stLine1) + '</div>' : '') + '<div>' + escHtml([stCity, stState].filter(Boolean).join(', ') + ' ' + stZip) + '</div><div>' + escHtml(stCountry) + '</div></div>' +
          '</div>' +
          '<div class="info-bar"><span>' + escHtml(shipName) + '</span><span>Created: ' + escHtml(created) + '</span></div>' +
          '<div class="barcode-center">' + shipIdBarcode + '</div>' +
          '<div class="ship-id-text">' + escHtml(shipId) + '</div>' +
          (s.amazon_reference_id ? '<div style="font-size:8pt;text-align:center;margin-top:2px;">Amazon PO: ' + escHtml(s.amazon_reference_id) + '</div>' : '') +
          '<hr class="hr">' +
          '<div class="pallet-num">PALLET ' + palletNum + ' OF ' + totalPallets + '</div>' +
          '</div>';
      }
    }
  });
  html += '<script>setTimeout(function(){window.print()},800)<\/script></body></html>';
  win.document.write(html);
  win.document.close();
}

// Saved freight rows for a shipment, else prompt for a pallet count — packing happens
// before Pallet & Freight Details are entered, so a count may not exist yet.
function palletRowsOrPrompt(pds) {
  var total = (pds || []).reduce(function(sum, p) { return sum + (parseInt(p.pallet_count) || 0); }, 0);
  if (total > 0) return pds;
  var n = parseInt(window.prompt('Number of pallets for this shipment?', '1'), 10);
  return (n && n > 0) ? [{ pallet_count: n }] : null;
}

// ─── Shipment Labels & Ship Card ───
function ShipmentLabelsCard({ shipment, plan, palletData, showToast, actionLoading, onGetLabels, onMarkShipped, onPrintCaseLabels, onRefresh, onExportPicklist, onExportWorkOrders }) {
  var s = shipment;
  var [carrier, setCarrier] = useState(s.carrier || '');
  var [trackingId, setTrackingId] = useState(s.tracking_id || '');
  var [proNumber, setProNumber] = useState(s.pro_number || '');
  var [bolNumber, setBolNumber] = useState(s.bol_number || '');
  var [bolLoading, setBolLoading] = useState(false);
  var isLtl = plan && plan.shipment_type === 'LTL';
  var boxes = s.boxes || [];
  var caseCount = boxes.length;
  // Own-carrier small parcel: one tracking number per box (9-27 sweep ML12 — a single field used to
  // be stamped onto every box). Keyed by box number, prefilled from what each box already carries.
  var parcelBoxes = boxes.filter(function(b) { return !b.is_phantom; });
  var perBoxTracking = !isLtl && s.transport_solution !== 'AMAZON_PARTNERED_CARRIER' && parcelBoxes.length > 1;
  var [boxTrackInputs, setBoxTrackInputs] = useState(function() {
    var m = {};
    parcelBoxes.forEach(function(b) { m[b.box_number] = b.tracking_id || ''; });
    return m;
  });
  var submitTracking = function() {
    if (!perBoxTracking) { onMarkShipped(carrier, trackingId, proNumber, bolNumber); return; }
    var list = parcelBoxes.map(function(b) { return { box_number: b.box_number, tracking_id: String(boxTrackInputs[b.box_number] || '').trim() }; });
    var missing = list.filter(function(x) { return !x.tracking_id; }).map(function(x) { return x.box_number; });
    if (missing.length) { showToast('Enter a tracking number for every box — missing Box ' + missing.join(', Box '), 'error'); return; }
    onMarkShipped(carrier, list[0].tracking_id, proNumber, bolNumber, list);
  };
  // Amazon-assigned per-box UPS tracking on partnered SPD shipments (Anthony 2026-09-23): what the plan
  // already carries (fba_boxes.tracking_id, stored by the status sync) shows at once; the card asks
  // Amazon for the latest when it opens and on Refresh.
  var partneredSpd = !isLtl && s.transport_solution === 'AMAZON_PARTNERED_CARRIER';
  var [boxTracking, setBoxTracking] = useState(null); // { boxes: [{ box_number, tracking_id }], warning }
  var [btLoading, setBtLoading] = useState(false);
  var loadBoxTracking = function() {
    if (!partneredSpd || !s.amazon_shipment_id) return;
    setBtLoading(true);
    api('/fba-shipments/shipments/' + s.id + '/box-tracking').then(function(r) { setBoxTracking(r); }).catch(function() {}).then(function() { setBtLoading(false); });
  };
  useEffect(function() { loadBoxTracking(); }, [s.id, s.status]);
  var trackedBoxes = (boxTracking && boxTracking.boxes) || boxes.map(function(b) { return { box_number: b.box_number, tracking_id: b.tracking_id || null }; });
  var renderBoxTracking = function() {
    var assigned = trackedBoxes.filter(function(b) { return b.tracking_id; }).length;
    return h('div', { style: { marginTop: 10 } },
      h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 } },
        h('div', { style: { fontSize: 12, fontWeight: 500, color: C.textMuted, flex: 1 } }, 'UPS Tracking by Box' + (trackedBoxes.length ? ' (' + assigned + ' of ' + trackedBoxes.length + ' assigned)' : '')),
        h('button', { onClick: loadBoxTracking, disabled: btLoading, title: 'Ask Amazon for the latest tracking', style: { background: 'none', border: 'none', color: C.accent, cursor: btLoading ? 'default' : 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, padding: 0, fontFamily: 'inherit' } },
          h(LIcon, { name: 'refresh-cw', size: 12, spin: btLoading }), btLoading ? 'Checking…' : 'Refresh')),
      boxTracking && boxTracking.warning && h('div', { style: { fontSize: 11, color: C.warning, marginBottom: 6 } }, boxTracking.warning),
      !trackedBoxes.length
        ? h('div', { style: { fontSize: 12, color: C.textDim } }, 'No boxes on this shipment yet')
        : h('div', { style: { display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 14px', fontSize: 12.5, alignItems: 'center' } },
            trackedBoxes.map(function(b) {
              return h(React.Fragment, { key: b.box_number },
                h('span', { style: { color: C.textMuted } }, 'Box ' + b.box_number + ' of ' + trackedBoxes.length),
                b.tracking_id
                  ? h('span', { style: { display: 'flex', alignItems: 'center', gap: 6 } },
                      h(window.CopyCell, { value: b.tracking_id }, h('span', { style: { color: C.text } }, b.tracking_id)),
                      h('a', { href: 'https://www.ups.com/track?tracknum=' + encodeURIComponent(b.tracking_id), target: '_blank', rel: 'noopener', title: 'Track on UPS', style: { color: C.accent, display: 'flex' } }, h(LIcon, { name: 'arrow-up-right', size: 12 })))
                  : h('span', { style: { color: C.textDim } }, 'Pending — Amazon has not assigned a number yet'));
            })));
  };
  var totalWeight = boxes.reduce(function(sum, b) { return sum + (parseFloat(b.weight) || 0); }, 0);
  var pds = palletData || [];
  var totalPallets = pds.reduce(function(sum, p) { return sum + (parseInt(p.pallet_count) || 0); }, 0);
  var shipItems = s.items || [];
  var skuCount = shipItems.length;
  var totalUnits = shipItems.reduce(function(sum, i) { return sum + (parseInt(i.quantity) || 0); }, 0);

  // Ship From address
  var srcAddr = plan.source_address || {};
  var srcLine = [srcAddr.addressLine1, srcAddr.addressLine2 || srcAddr.address_line1].filter(Boolean).join(', ');
  var srcCity = [[srcAddr.city, srcAddr.stateOrProvinceCode || srcAddr.state].filter(Boolean).join(', '), srcAddr.postalCode || srcAddr.postal_code].filter(Boolean).join(' ');
  var srcName = srcAddr.name || '';

  // Ship To address
  var lda = s.destination_address ? (typeof s.destination_address === 'string' ? JSON.parse(s.destination_address) : s.destination_address) : {};
  var ldAddr = lda.address || lda || {};
  var ldName = ldAddr.name || '';
  var ldLine = [ldAddr.addressLine1, ldAddr.addressLine2].filter(Boolean).join(', ');
  var ldCity = [[ldAddr.city, ldAddr.stateOrProvinceCode].filter(Boolean).join(', '), ldAddr.postalCode].filter(Boolean).join(' ');
  var ldCountry = ldAddr.countryCode || '';

  var printPalletLabels = function() {
    // Pre-confirmation there may be no saved pallet specs yet — prompt for a count,
    // same as the packing-header and transport-card printers.
    var rows = palletRowsOrPrompt(pds);
    if (rows) printFbaPalletLabels(s, plan, rows, showToast);
  };

  var getBillOfLading = function() {
    setBolLoading(true);
    api('/fba-shipments/shipments/' + s.id + '/bill-of-lading')
      .then(function(res) {
        if (res && res.downloadUrl) { window.open(res.downloadUrl, '_blank'); }
        else { showToast('No Bill of Lading URL returned', 'error'); }
      })
      .catch(function(e) { showToast(e.message || 'Failed to fetch Bill of Lading', 'error'); })
      .finally(function() { setBolLoading(false); });
  };

  var statStyle = { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '8px 12px', borderRadius: 6, background: C.surface, minWidth: 70 };
  var statNum = { fontSize: 18, fontWeight: 700, color: C.text };
  var statLabel = { fontSize: 10, color: C.textMuted, marginTop: 2 };
  var addrBlockStyle = { flex: 1, fontSize: 11, lineHeight: '1.4' };
  var addrTitleStyle = { fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 2 };

  return h(Card, null,
    // Header
    h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 } },
      h('div', null,
        h('span', { style: { fontWeight: 600, fontSize: 14 } }, s.amazon_shipment_id || 'Shipment'),
        s.destination_fc && h('span', { style: { marginLeft: 8, fontSize: 12, fontWeight: 700, fontFamily: 'inherit' } }, ' → ' + s.destination_fc)
      ),
      h('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
        (onExportPicklist || onExportWorkOrders) && h('div', { style: { display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' } },
          onExportPicklist && h('button', {
            onClick: onExportPicklist,
            title: 'Export picklist (Excel)',
            onMouseEnter: function(e) { e.currentTarget.style.background = 'rgba(79,110,247,0.15)'; },
            onMouseLeave: function(e) { e.currentTarget.style.background = 'rgba(79,110,247,0.08)'; },
            style: { padding: '5px 10px', background: 'rgba(79,110,247,0.08)', color: C.accent, border: '1px solid rgba(79,110,247,0.3)', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: 'inherit' }
          }, h(LIcon, { name: 'download', size: 13 }), ' Picklist'),
          h(WorkOrdersMenu, { shipment: shipment, plan: plan, showToast: showToast, align: 'right' })
        )
      )
    ),

    // IDs: FBA Shipment ID + Amazon Reference ID stacked
    h('div', { style: { marginBottom: 10, fontSize: 12 } },
      s.amazon_shipment_id && h('div', { style: { marginBottom: 2 } },
        h('span', { style: { color: C.textMuted } }, 'FBA Shipment ID: '),
        h('span', { style: { fontWeight: 600, color: C.text } }, s.amazon_shipment_id)
      ),
      s.amazon_reference_id && h('div', { style: { marginBottom: 2 } },
        h('span', { style: { color: C.textMuted } }, 'Amazon PO ID: '),
        h('span', { style: { fontWeight: 600, color: C.text } }, s.amazon_reference_id)
      ),
      s.transport_carrier && h('div', null,
        h('span', { style: { color: C.textMuted } }, 'Carrier: '),
        h('span', { style: { fontWeight: 600, color: C.text } }, s.transport_carrier),
        s.transport_cost && h('span', { style: { marginLeft: 8, fontWeight: 600, color: C.success } }, '$' + parseFloat(s.transport_cost).toFixed(2)),
        s.transport_solution === 'AMAZON_PARTNERED_CARRIER' && h('span', { style: { marginLeft: 8, fontSize: 10, padding: '2px 6px', borderRadius: 3, background: 'rgba(52,211,153,0.15)', color: C.success, fontWeight: 600 } }, 'PARTNERED')
      )
    ),

    // Stats row
    h('div', { style: { display: 'flex', gap: 10, marginBottom: 12 } },
      h('div', { style: statStyle },
        h('div', { style: statNum }, skuCount),
        h('div', { style: statLabel }, 'SKUs')
      ),
      h('div', { style: statStyle },
        h('div', { style: statNum }, totalUnits.toLocaleString()),
        h('div', { style: statLabel }, 'Units')
      ),
      h('div', { style: statStyle },
        h('div', { style: statNum }, caseCount),
        h('div', { style: statLabel }, 'Cases')
      ),
      h('div', { style: statStyle },
        h('div', { style: statNum }, totalWeight.toLocaleString() + ' lbs'),
        h('div', { style: statLabel }, 'Expected Total Weight')
      ),
      isLtl && totalPallets > 0 && h('div', { style: statStyle },
        h('div', { style: statNum }, totalPallets),
        h('div', { style: statLabel }, totalPallets === 1 ? 'Pallet' : 'Pallets')
      ),
      isLtl && pds.length > 0 && h('div', { style: Object.assign({}, statStyle, { alignItems: 'flex-start', flex: 1 }) },
        h('div', { style: { fontSize: 10, fontWeight: 600, color: C.textMuted, marginBottom: 2 } }, 'Pallet Specs'),
        pds.map(function(pd, i) {
          return h('div', { key: i, style: { fontSize: 11, color: C.text } },
            (pd.pallet_count || 1) + 'x ' + (pd.length || 48) + 'x' + (pd.width || 40) + 'x' + (pd.height || 60) + ' inches, ' + (pd.weight || 0) + ' lbs ' + (pd.stackability === 'STACKABLE' ? '(Stackable)' : '(Non-Stackable)')
          );
        })
      )
    ),

    // Addresses: Ship From → Ship To
    h('div', { style: { display: 'flex', gap: 16, marginBottom: 12, padding: '10px 12px', borderRadius: 6, background: C.surface } },
      h('div', { style: addrBlockStyle },
        h('div', { style: addrTitleStyle }, 'Ship From'),
        srcName && h('div', { style: { fontWeight: 500, color: C.text } }, srcName),
        srcLine && h('div', { style: { color: C.text } }, srcLine),
        srcCity && h('div', { style: { color: C.text } }, srcCity)
      ),
      h('div', { style: { display: 'flex', alignItems: 'center', color: C.textMuted } }, '→'),
      h('div', { style: addrBlockStyle },
        h('div', { style: addrTitleStyle }, 'Ship To'),
        h('div', { style: { fontWeight: 500, color: C.text } }, 'FBA: ' + ((plan && plan.seller_name) || '')), // the seller, never the ship-from name (2026-10-22)
        s.destination_fc && h('div', { style: { color: C.text } }, s.destination_fc),
        ldLine && h('div', { style: { color: C.text } }, ldLine),
        ldCity && h('div', { style: { color: C.text } }, ldCity),
        ldCountry && h('div', { style: { color: C.text } }, ldCountry)
      )
    ),

    // Label & Print buttons
    h('div', { style: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 } },
      // SPD + partnered carrier: Amazon's shipping labels already include the box label
      // AND the partnered-carrier (UPS) label — that is the complete SPD set. SPD does not
      // use 2D-barcode case labels (an LTL concept); fetching them just re-returned the
      // shipping labels and printed every page twice. Fetch shipping labels only.
      !isLtl && s.transport_solution !== 'USE_YOUR_OWN_CARRIER' && h(Btn, { size: 'sm', variant: 'primary',
        onClick: function() { onGetLabels(); },
        disabled: actionLoading === 'labels-' + s.id },
        actionLoading === 'labels-' + s.id
          ? [h(LIcon, { key: 'l', name: 'loader-2', size: 13, spin: true }), ' Downloading...']
          : [h(LIcon, { key: 'l', name: 'download', size: 13 }), ' Shipping Labels']),
      // SPD + own carrier: no shipping label fetch; case labels button still needed.
      !isLtl && s.transport_solution === 'USE_YOUR_OWN_CARRIER' && h('div', { style: { fontSize: 11, color: C.textMuted, padding: '6px 10px', background: C.surface, borderRadius: 6, border: '1px solid ' + C.border, display: 'flex', alignItems: 'center', gap: 6 } },
        h(LIcon, { name: 'info', size: 12 }), 'Own carrier — print shipping labels from your carrier account'),
      (isLtl || (!isLtl && s.transport_solution === 'USE_YOUR_OWN_CARRIER')) && h(Btn, { size: 'sm', onClick: onPrintCaseLabels, style: { background: 'rgba(79,110,247,0.1)', color: C.accent, border: '1px solid rgba(79,110,247,0.3)' } }, h(LIcon, { name: 'printer', size: 13 }), ' Case Labels'),
      isLtl && h(Btn, { size: 'sm', onClick: printPalletLabels, style: { background: 'rgba(167,139,250,0.1)', color: '#8B5CF6', border: '1px solid rgba(167,139,250,0.3)' } }, h(LIcon, { name: 'printer', size: 13 }), ' Pallet Labels'),
      isLtl && s.transport_solution === 'AMAZON_PARTNERED_CARRIER' && ['transport_confirmed', 'labels_ready', 'shipped'].indexOf(s.status) >= 0 && h(Btn, { size: 'sm', onClick: getBillOfLading, disabled: bolLoading, style: { background: 'rgba(251,191,36,0.1)', color: '#FBBF24', border: '1px solid rgba(251,191,36,0.3)' } },
        bolLoading ? [h(LIcon, { key: 'l', name: 'loader-2', size: 13, spin: true }), ' Loading...'] : [h(LIcon, { key: 'l', name: 'file-text', size: 13 }), ' Bill of Lading'])
    ),

    // Tracking info — show carrier/BOL/PRO only for non-partnered; partnered is managed by Amazon
    h('div', { style: { borderTop: '1px solid ' + C.border, paddingTop: 12 } },
      h('div', { style: { fontSize: 12, fontWeight: 500, color: C.textMuted, marginBottom: 8 } }, 'Shipping Details'),
      (s.transport_solution === 'AMAZON_PARTNERED_CARRIER' || (isLtl && s.status === 'transport_confirmed' && !s.carrier))
        ? h('div', null,
            h('div', { style: { fontSize: 12, color: C.textMuted, padding: '8px 12px', background: C.surface, borderRadius: 6, display: 'flex', alignItems: 'center', gap: 8 } },
              h(LIcon, { name: 'check-circle', size: 16, color: C.success }),
              'Amazon Partnered Carrier — carrier, BOL, and tracking managed by Amazon'),
            // the per-box UPS numbers Amazon assigned, grouped by shipment because each card is one shipment
            partneredSpd && renderBoxTracking())
        : h(React.Fragment, null,
            isLtl
              ? h('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 } },
                  h(Inp, { label: 'Carrier', value: carrier, onChange: function(e) { setCarrier(e.target.value); }, placeholder: 'Carrier name' }),
                  h(Inp, { label: 'BOL #', value: bolNumber, onChange: function(e) { setBolNumber(e.target.value); }, placeholder: 'Bill of Lading' }),
                  h(Inp, { label: 'PRO #', value: proNumber, onChange: function(e) { setProNumber(e.target.value); }, placeholder: 'PRO number' })
                )
              : perBoxTracking
                ? h('div', null,
                    h('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 } },
                      h(Inp, { label: 'Carrier', value: carrier, onChange: function(e) { setCarrier(e.target.value); }, placeholder: 'UPS, FedEx...' })),
                    h('div', { style: { fontSize: 12, fontWeight: 500, color: C.textMuted, margin: '8px 0 4px' } }, 'Tracking by Box'),
                    h('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 } },
                      parcelBoxes.map(function(b) {
                        return h(Inp, { key: b.box_number, label: 'Box ' + b.box_number + ' of ' + parcelBoxes.length, value: boxTrackInputs[b.box_number] || '',
                          onChange: function(e) { var v = e.target.value; setBoxTrackInputs(function(prev) { var n = Object.assign({}, prev); n[b.box_number] = v; return n; }); } });
                      })))
                : h('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 } },
                  h(Inp, { label: 'Carrier', value: carrier, onChange: function(e) { setCarrier(e.target.value); }, placeholder: 'UPS, FedEx...' }),
                  h(Inp, { label: 'Tracking ID', value: trackingId, onChange: function(e) { setTrackingId(e.target.value); } })
                ),
            h('div', { style: { marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' } },
              h(Btn, { variant: s.status === 'shipped' ? 'primary' : 'success', onClick: submitTracking, disabled: !!actionLoading },
                h(LIcon, { name: 'truck', size: 14 }), s.status === 'shipped' ? ' Update Tracking' : ' Mark Shipped'),
              s.status === 'shipped' && h(Badge, { color: '#10B981' }, 'Shipped')
            )
          )
    )
  );
}

// ─── Box Packing View ───
function FbaBoxPackingView({ shipmentId, planId, showToast, plan, currentUser, packGroups, activeGroupIdx, onSwitchGroup, onNavigate, onRefresh, onPackingStatusChange, onErroredCountChange }) {
  var [shipment, setShipment] = useState(null);
  var [boxes, setBoxes] = useState([]);
  var [activeBoxId, setActiveBoxId] = useState(null);
  var [dimForm, setDimForm] = useState({ length: '', width: '', height: '', weight: '' });
  var [oversizeConfirmed, setOversizeConfirmed] = useState(false);
  var [labelTemplates, setLabelTemplates] = useState([]);
  var [printModal, setPrintModal] = useState(null);
  var [bulkBoxModal, setBulkBoxModal] = useState(null); // { file, report } — Excel box-contents preview/confirm
  var [bulkBoxBusy, setBulkBoxBusy] = useState(false);
  var bulkBoxFileRef = useRef(null);
  var [lightbox, setLightbox] = useState(null); // { images: [], index: 0 }
  var [expandedBoxId, setExpandedBoxId] = useState(null); // used for box contents modal
  var viewBox = expandedBoxId ? boxes.find(function(b) { return b.id === expandedBoxId; }) : null;
  var [editBox, setEditBox] = useState(null); // { id, box_number, items, dims, removed, added }
  var [editBoxSaving, setEditBoxSaving] = useState(false);
  // Transparency serial audit for the box being edited (which serials went into it).
  var [boxTScans, setBoxTScans] = useState(null);
  useEffect(function() {
    if (!editBox || !editBox.id) { setBoxTScans(null); return; }
    api('/fba-shipments/boxes/' + editBox.id + '/transparency-scans')
      .then(function(rows) { setBoxTScans(rows || []); })
      .catch(function() { setBoxTScans(null); });
  }, [editBox && editBox.id]);
  var [editBoxAddOpen, setEditBoxAddOpen] = useState(false);
  var [editBoxSearch, setEditBoxSearch] = useState(''); // search within the Edit Box "Add Item" panel
  var [editBoxItemSearch, setEditBoxItemSearch] = useState(''); // search within the current Items-in-Box list
  var [deleteBoxConfirm, setDeleteBoxConfirm] = useState(null); // { boxId, boxNum, unitCount, skuCount }
  var [duplicateBoxModal, setDuplicateBoxModal] = useState(null); // { boxId, boxNum, unitCount, skuCount, maxCopies }
  var [duplicateCount, setDuplicateCount] = useState(1);
  var [duplicateSaving, setDuplicateSaving] = useState(false);
  var [selectedLabelTpl, setSelectedLabelTpl] = useState(null);
  var [printQtyOverride, setPrintQtyOverride] = useState({});
  var [missingConfirm, setMissingConfirm] = useState(null); // item pending "mark missing" confirmation
  var [missingNote, setMissingNote] = useState('');
  var [unexpectedOpen, setUnexpectedOpen] = useState(false);
  var [packTab, setPackTab] = useState('shipment'); // 'shipment' | 'errored' | 'unexpected' | 'activity'
  var [activityLog, setActivityLog] = useState([]);
  var [activityLoading, setActivityLoading] = useState(false);
  var [activitySearch, setActivitySearch] = useState('');
  var loadActivity = useCallback(function() {
    setActivityLoading(true);
    return api('/fba-shipments/shipments/' + shipmentId + '/activity')
      .then(function(rows) { setActivityLog(rows || []); })
      .catch(function() { setActivityLog([]); })
      .finally(function() { setActivityLoading(false); });
  }, [shipmentId]);
  useEffect(function() { if (packTab === 'activity') loadActivity(); }, [packTab, loadActivity]);

  // Guided workflow state — box is created only on Finish (no pre-reservation),
  // so cancel/refresh never strands a box_number.
  var [numCopies, setNumCopies] = useState(1);
  var cancelWizard = function() {
    setSelectedItems({});
    setPackQtys({});
    setNumCopies(1);
    setWizStep(0);
  };
  // Shift-click selects the whole range between the last-clicked row and this one
  // (mirrors the create-modal PO list). Plain row click still only selects; deselect
  // stays a deliberate checkbox click.
  var lastPackClickRef = useRef(null);
  var packRowClick = function(e, item, list) {
    if (e.shiftKey && lastPackClickRef.current != null) {
      var lastIdx = -1, curIdx = -1;
      for (var i = 0; i < list.length; i++) {
        if (list[i].id === lastPackClickRef.current) lastIdx = i;
        if (list[i].id === item.id) curIdx = i;
      }
      if (lastIdx !== -1 && curIdx !== -1) {
        var lo = Math.min(lastIdx, curIdx), hi = Math.max(lastIdx, curIdx);
        for (var j = lo; j <= hi; j++) { if (!selectedItems[list[j].id]) toggleItem(list[j].id); }
        lastPackClickRef.current = item.id;
        return;
      }
    }
    if (!selectedItems[item.id]) toggleItem(item.id);
    lastPackClickRef.current = item.id;
  };
  var [wizStep, setWizStep] = useState(0); // 0=overview, 1=select items, 2=verify & qty, 3=dims & finish
  var [selectedItems, setSelectedItems] = useState({}); // { shipmentItemId: true }
  var [packQtys, setPackQtys] = useState({}); // { shipmentItemId: qty }
  var [saving, setSaving] = useState(false);
  var [itemSearch, setItemSearch] = useState('');
  // ─── Transparency scan station (step 2) ───
  // Worker applies a Transparency label, scans it, server rejects duplicates (codes are
  // single-use globally) and wrong-product codes. Counts are per box session.
  var [tScanCounts, setTScanCounts] = useState({});   // product_id → scans this box
  var [tScanIds, setTScanIds] = useState({});         // product_id → [scan ids] (undo-last)
  var [tScanActive, setTScanActive] = useState(null); // product_id currently being scanned
  var [tScanMsg, setTScanMsg] = useState(null);       // { type: 'ok'|'error', text }
  var [tScanBusy, setTScanBusy] = useState(false);
  var [tScanVal, setTScanVal] = useState('');
  var tScanInputRef = useRef(null);
  var [tSeedInfo, setTSeedInfo] = useState(0); // units seeded from earlier sessions (scanned, not yet boxed)
  var [tOthersInfo, setTOthersInfo] = useState(0); // other packers' in-flight (unboxed) scans on this plan
  var [prepPdfFallback, setPrepPdfFallback] = useState(null); // {url} — last served labels PDF, manual open path
  useEffect(function() {
    if (wizStep === 0) { setTScanCounts({}); setTScanIds({}); setTScanActive(null); setTScanMsg(null); setTScanVal(''); setTSeedInfo(0); setTOthersInfo(0); }
  }, [wizStep]);
  // Entering the scan step seeds the station from persisted scans not yet attributed to
  // a box — scanned units survive cancelled boxes/sessions/deleted boxes and flow into
  // the next box. OWN scans only: two packers work one plan concurrently, and seeding
  // another packer's in-flight scans would pull their units into this box (and let this
  // station undo their scans). Individual path also snaps flagged SKUs' box quantities
  // to their scan counts (scan-driven packing) and auto-selects them.
  useEffect(function() {
    if (wizStep !== (isCasePacked ? 3 : 2)) return;
    api('/fba-shipments/plans/' + planId + '/transparency-scans').then(function(rows) {
      var counts = {}, ids = {}, others = 0;
      var myId = currentUser && currentUser.id;
      (rows || []).filter(function(r) { return !r.box_id; })
        .sort(function(a, b) { return a.id - b.id; })
        .forEach(function(r) {
          if (r.scanned_by !== myId) { others++; return; }
          counts[r.product_id] = (counts[r.product_id] || 0) + 1;
          (ids[r.product_id] = ids[r.product_id] || []).push(r.id);
        });
      setTScanCounts(counts);
      setTScanIds(ids);
      setTOthersInfo(others);
      setTSeedInfo(Object.keys(counts).reduce(function(s, k) { return s + counts[k]; }, 0));
      if (!isCasePacked) {
        setPackQtys(function(prev) {
          var n = Object.assign({}, prev);
          availableItems.forEach(function(it) {
            if (!(it.requires_transparency && it.product_id)) return;
            var c = counts[it.product_id] || 0;
            var maxQ = Math.max(0, it.quantity - (it.quantity_packed || 0) - (it.quantity_missing || 0));
            if (c > 0) n[it.id] = String(Math.min(c, maxQ)); else delete n[it.id];
          });
          return n;
        });
        setSelectedItems(function(prev) {
          var n = Object.assign({}, prev);
          availableItems.forEach(function(it) {
            if (it.requires_transparency && it.product_id && counts[it.product_id]) n[it.id] = true;
          });
          return n;
        });
      }
    }).catch(function() { /* offline seed failure — session scanning still works */ });
  }, [wizStep]); // deps intentionally narrow — seed once per step entry
  // Arriving at the scan step drops the cursor straight into the scan input, so the
  // first trigger-pull lands in the box with no clicking (input refocuses after each
  // scan already).
  useEffect(function() {
    if (wizStep !== (isCasePacked ? 3 : 2)) return;
    var t = setTimeout(function() { if (tScanInputRef.current) tScanInputRef.current.focus(); }, 200);
    return function() { clearTimeout(t); };
  }, [wizStep]);
  // Scan-anywhere: if the worker clicked somewhere and focus left the field, printable
  // keystrokes falling on the page are redirected into the scan input (focusing during
  // keydown makes the character land there). Typing in real inputs (qty, search) is
  // untouched.
  useEffect(function() {
    if (wizStep !== (isCasePacked ? 3 : 2)) return;
    var onKey = function(e) {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      var t = e.target;
      var tag = t && t.tagName ? t.tagName.toUpperCase() : '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (t && t.isContentEditable)) return;
      if (e.key && e.key.length === 1 && tScanInputRef.current) tScanInputRef.current.focus();
    };
    document.addEventListener('keydown', onKey);
    return function() { document.removeEventListener('keydown', onKey); };
  }, [wizStep]);
  var submitTScan = function() {
    var code = tScanVal.trim();
    if (!code || tScanBusy) return;
    // FNSKU-first flow: scanning a unit's FNSKU (or barcode) switches the active SKU —
    // the whole station runs off the scanner, and each T-code lands on the right product.
    // Non-Transparency items pack directly: one FNSKU/barcode scan = one unit in the box.
    var codeUp = code.toUpperCase();
    var fnskuHit = availableItems.find(function(i) { return (i.fnsku || '').toUpperCase() === codeUp || (i.barcode || '') === code.replace(/^0+/, '') || (i.barcode || '') === code; });
    if (fnskuHit) {
      // Case-packed runs are locked to the selected SKU — an FNSKU from another product
      // is a wrong-item scan, not a switch request.
      if (isCasePacked && cpSelectedItem && fnskuHit.id !== cpSelectedItem) {
        var cpLockIt = availableItems.find(function(i) { return i.id === cpSelectedItem; });
        setTScanMsg({ type: 'error', text: 'WRONG SKU — this case run packs ' + (cpLockIt ? (cpLockIt.product_sku || cpLockIt.msku) : 'the selected SKU') + ' only. That FNSKU belongs to ' + (fnskuHit.product_sku || fnskuHit.msku) + '.' });
        setTScanVal('');
        return;
      }
      if (fnskuHit.requires_transparency && fnskuHit.product_id) {
        setTScanActive(fnskuHit.product_id);
        // Scan-driven packing: the FNSKU scan also selects the item into the box.
        if (!isCasePacked && !selectedItems[fnskuHit.id]) toggleItem(fnskuHit.id);
        setTScanMsg({ type: 'ok', text: 'Now scanning: ' + (fnskuHit.product_sku || fnskuHit.msku) + ' — apply and scan Transparency labels' });
      } else if (!isCasePacked) {
        // Plain item — no serialized label to verify, so the FNSKU/barcode scan itself
        // packs one unit into the box (edit the qty field to correct mistakes).
        var pq = parseInt(packQtys[fnskuHit.id]) || 0;
        var pMax = Math.max(0, fnskuHit.quantity - (fnskuHit.quantity_packed || 0) - (fnskuHit.quantity_missing || 0));
        if (!selectedItems[fnskuHit.id]) toggleItem(fnskuHit.id);
        if (pq + 1 > pMax) {
          setTScanMsg({ type: 'error', text: (fnskuHit.product_sku || fnskuHit.msku) + ' is already at ' + pMax + ' — all remaining units are in this box.' });
        } else {
          setPackQtys(function(prev) { var n = Object.assign({}, prev); n[fnskuHit.id] = String(pq + 1); return n; });
          setTScanMsg({ type: 'ok', text: (fnskuHit.product_sku || fnskuHit.msku) + ' +1 — ' + (pq + 1) + ' in box' });
        }
      }
      setTScanVal('');
      setTimeout(function() { if (tScanInputRef.current) tScanInputRef.current.focus(); }, 30);
      return;
    }
    if (!tScanActive) {
      setTScanMsg({ type: 'error', text: 'Code not recognized — scan a unit\'s FNSKU or barcode' + (availableItems.some(function(i) { return i.requires_transparency && i.product_id; }) ? ' (Transparency codes need their FNSKU scanned first so they land on the right product)' : '') });
      setTScanVal(''); return;
    }
    setTScanBusy(true); setTScanVal('');
    api('/fba-shipments/plans/' + planId + '/transparency-scans', { method: 'POST', body: JSON.stringify({ code: code, product_id: tScanActive }) })
      .then(function(r) {
        if (r.ok) {
          setTScanCounts(function(p) { var n = Object.assign({}, p); n[r.scan.product_id] = (n[r.scan.product_id] || 0) + 1; return n; });
          setTScanIds(function(p) { var n = Object.assign({}, p); n[r.scan.product_id] = (n[r.scan.product_id] || []).concat([r.scan.id]); return n; });
          // Scan-driven packing (individual path): each accepted scan selects the item
          // into the box and raises its quantity to the scan count.
          if (!isCasePacked) {
            var scannedIt = availableItems.find(function(x) { return x.product_id === r.scan.product_id; });
            if (scannedIt) {
              var newCount = (tScanCounts[r.scan.product_id] || 0) + 1;
              var maxQ = Math.max(0, scannedIt.quantity - (scannedIt.quantity_packed || 0) - (scannedIt.quantity_missing || 0));
              if (!selectedItems[scannedIt.id]) toggleItem(scannedIt.id);
              setPackQtys(function(prev) { var n = Object.assign({}, prev); n[scannedIt.id] = String(Math.min(newCount, maxQ)); return n; });
              if (newCount > maxQ) setTScanMsg({ type: 'error', text: 'Scan recorded, but ' + (r.scan.sku || '') + ' now has more scans (' + newCount + ') than units remaining to pack (' + maxQ + ') — check quantities.' });
              else setTScanMsg({ type: 'ok', text: 'Scan OK — ' + (r.scan.sku || '') + (r.reclaimed ? ' (reclaimed from a cancelled plan)' : '') + ' · ' + Math.min(newCount, maxQ) + ' in box' });
            } else {
              setTScanMsg({ type: 'ok', text: 'Scan OK — ' + (r.scan.sku || '') });
            }
          } else {
            setTScanMsg({ type: 'ok', text: 'Scan OK — ' + (r.scan.sku || '') + (r.reclaimed ? ' (reclaimed from a cancelled plan)' : '') });
          }
        } else if (r.reason === 'duplicate') {
          var d = r.duplicate || {};
          setTScanMsg({ type: 'error', text: 'DUPLICATE — this code was already scanned' + (d.sku ? ' for ' + d.sku : '') + (d.scanned_at ? ' on ' + new Date(d.scanned_at).toLocaleString() : '') + (d.scanned_by_name ? ' by ' + d.scanned_by_name : '') + '. This label may already be applied to another unit.' });
        } else if (r.reason === 'mismatch') {
          var mm = r.mismatch || {};
          setTScanMsg({ type: 'error', text: 'WRONG PRODUCT — code belongs to barcode ' + mm.scanned_gtin + ', but ' + mm.sku + ' expects ' + (mm.product_barcode || '?') + '. Wrong label sheet?' });
        } else {
          setTScanMsg({ type: 'error', text: 'Scan failed' });
        }
      })
      .catch(function(e) { setTScanMsg({ type: 'error', text: 'Scan failed: ' + (e.message || 'unknown error') }); })
      .finally(function() { setTScanBusy(false); setTimeout(function() { if (tScanInputRef.current) tScanInputRef.current.focus(); }, 30); });
  };
  // Interleaved FNSKU + Transparency label PDF (server-built; consumes label-file pages).
  // Printed via hidden iframe — window.open after an async fetch trips popup blockers.
  var [prepPrintBusy, setPrepPrintBusy] = useState(false);
  // Auto-print a served labels PDF via hidden iframe. The blob URL always parks in
  // the fallback pill: a print dialog that silently swallows the PDF (PDF viewer not
  // ready at print() time, dialog dismissed) leaves a visible manual path instead of
  // burning serials — the old window.open fallback was popup-blocked after the fetch.
  var printServedPrepPdf = function(r) {
    var bytes = Uint8Array.from(atob(r.pdf_base64), function(c) { return c.charCodeAt(0); });
    var url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
    setPrepPdfFallback(function(prev) {
      if (prev && prev.url) { try { URL.revokeObjectURL(prev.url); } catch (e) { /* already gone */ } }
      return { url: url };
    });
    var frame = document.createElement('iframe');
    frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    frame.src = url;
    frame.onload = function() { setTimeout(function() { try { frame.contentWindow.focus(); frame.contentWindow.print(); } catch (e) { /* fallback pill covers it */ } }, 600); };
    document.body.appendChild(frame);
    showToast((r.allocations || []).map(function(a) { return a.sku + ': labels ' + (a.start + 1) + '–' + a.end + (r.reprint ? '' : ' (' + a.remaining + ' left)'); }).join(' · ') + (r.reprint ? ' — REPRINT, same serials as last print' : ''), 'success');
  };
  var printPrepLabelsCore = function(reqItems, widthMm, heightMm) {
    if (!reqItems.length) { showToast('Enter quantities for the Transparency items first', 'error'); return; }
    var fbaTpls = labelTemplates.filter(function(t) { return (t.category || 'fba') === 'fba'; });
    var def = fbaTpls.find(function(t) { return t.is_default; }) || fbaTpls[0];
    setPrepPrintBusy(true);
    api('/fba-shipments/plans/' + planId + '/prep-labels', { method: 'POST', body: JSON.stringify({
      items: reqItems,
      width_mm: widthMm || (def ? parseFloat(def.width_mm) : 57.15),
      height_mm: heightMm || (def ? parseFloat(def.height_mm) : 31.75)
    }) })
      .then(printServedPrepPdf)
      .catch(function(e) { showToast(e.message || 'Print failed', 'error'); })
      .finally(function() { setPrepPrintBusy(false); });
  };
  // Re-serves the exact page window of the caller's last print — for when the dialog
  // swallowed it. Serials are NOT consumed again.
  var reprintLastPrepLabels = function() {
    setPrepPrintBusy(true);
    api('/fba-shipments/plans/' + planId + '/prep-labels/reprint-last', { method: 'POST', body: JSON.stringify({}) })
      .then(printServedPrepPdf)
      .catch(function(e) { showToast(e.message || 'Reprint failed', 'error'); })
      .finally(function() { setPrepPrintBusy(false); });
  };
  var undoTScan = function(productId) {
    var ids = tScanIds[productId] || [];
    if (!ids.length) return;
    api('/fba-shipments/plans/' + planId + '/transparency-scans/' + ids[ids.length - 1], { method: 'DELETE' })
      .then(function() {
        setTScanCounts(function(p) { var n = Object.assign({}, p); n[productId] = Math.max(0, (n[productId] || 1) - 1); return n; });
        setTScanIds(function(p) { var n = Object.assign({}, p); n[productId] = (n[productId] || []).slice(0, -1); return n; });
        // Scan-driven packing: undo lowers the box quantity with the count.
        if (!isCasePacked) {
          var undoneIt = availableItems.find(function(x) { return x.product_id === productId; });
          if (undoneIt) {
            var c = Math.max(0, (tScanCounts[productId] || 1) - 1);
            setPackQtys(function(prev) {
              var n = Object.assign({}, prev);
              if (c > 0) n[undoneIt.id] = String(c); else delete n[undoneIt.id];
              return n;
            });
          }
        }
        setTScanMsg({ type: 'ok', text: 'Last scan removed' });
      })
      .catch(function(e) { setTScanMsg({ type: 'error', text: 'Undo failed: ' + (e.message || '') }); });
  };
  // ─── Amazon packing groups (SPD-individual, pre-placement) ───
  // Amazon partitions the plan's SKUs into packing groups (hazmat split etc.) and a box
  // must never mix groups. The group picker lives in the parent (above the shipment
  // header, exactly like the multi-shipment overview) and passes packGroups +
  // activeGroupIdx down; this view scopes the to-pack list, the Pack-a-Box wizard and
  // the boxes list to the active group — cross-group boxes are impossible by
  // construction. Null packGroups (0-1 groups / N-A / fetch failed) = flow unchanged.
  var mskuGroupIdx = {}; // msku → 1-based group number
  if (packGroups) packGroups.forEach(function(g, gi) { (g.mskus || []).forEach(function(m) { mskuGroupIdx[m] = gi + 1; }); });
  var groupOfItem = function(it) { return (packGroups && it && mskuGroupIdx[it.msku]) || null; };
  // An item belongs to the active group's scope; unknown SKUs (not in any group —
  // anomalies Amazon doesn't know about) stay visible in every group rather than vanish.
  var inActiveGroup = function(it) {
    if (!packGroups) return true;
    var g = groupOfItem(it);
    return !g || g === activeGroupIdx + 1;
  };
  // Housekeeping when the parent switches the active group: reset the search, drop a
  // box highlight that belongs to another group, and — if the wizard is mid-box with
  // items now out of scope — bail back to step 0 (the box draft persists server-side).
  // A draft-resume snap arrives with in-scope selections and is left untouched.
  useEffect(function() {
    if (!packGroups) return;
    setItemSearch('');
    var ab = boxes.find(function(b) { return b.id === activeBoxId; });
    if (ab && (ab.items || []).length && !(ab.items || []).some(function(bi) { var g = mskuGroupIdx[bi.msku]; return !g || g === activeGroupIdx + 1; })) setActiveBoxId(null);
    var hasOutOfScope = Object.keys(selectedItems).some(function(k) {
      if (!selectedItems[k]) return false;
      var it = items.find(function(i) { return String(i.id) === String(k); });
      return it && !inActiveGroup(it);
    });
    if (wizStep > 0 && hasOutOfScope) { setWizStep(0); setSelectedItems({}); setPackQtys({}); }
  }, [activeGroupIdx]);
  // ─── Pack-a-Box draft persistence (resume an in-progress box after leaving, cross-device) ───
  // rawDraft: undefined = not loaded yet, null = none saved, { draft, updated_at } = saved.
  var [rawDraft, setRawDraft] = useState(undefined);
  var [draftPrompt, setDraftPrompt] = useState(null); // { data, updated_at } while asking Resume / Start-fresh
  var [draftDiscardConfirm, setDraftDiscardConfirm] = useState(false); // Start-fresh needs a second, explicit click
  var draftSaveTimer = useRef(null);
  // Reconcile a saved draft against currently-packable items: drop items that are gone /
  // fully-packed / excluded, clamp each qty to what's still packable. An item belongs to the
  // draft if it was checked OR had a qty entered (selection alone counts — qty is entered later
  // in the wizard). Returns null if nothing usable remains.
  var reconcileDraft = function(draft, avail) {
    if (!draft) return null;
    var sel0 = draft.selectedItems || {};
    var q0 = draft.packQtys || {};
    var byId = {};
    (avail || []).forEach(function(it) { byId[String(it.id)] = it; });
    var ids = {};
    Object.keys(sel0).forEach(function(k) { if (sel0[k]) ids[k] = true; });
    Object.keys(q0).forEach(function(k) { if ((parseInt(q0[k], 10) || 0) > 0) ids[k] = true; });
    var sel = {}, qtys = {}, lines = [];
    Object.keys(ids).forEach(function(sid) {
      var it = byId[String(sid)];
      if (!it) return;
      var remaining = (it.quantity || 0) - (it.quantity_packed || 0) - (it.quantity_missing || 0);
      if (remaining <= 0) return;
      sel[sid] = true;
      var savedQ = parseInt(q0[sid], 10);
      if (!isNaN(savedQ) && savedQ > 0) qtys[sid] = Math.min(savedQ, remaining);
      lines.push({ label: it.msku || it.product_sku || ('#' + sid), qty: (qtys[sid] != null ? qtys[sid] : null) });
    });
    if (!lines.length) return null;
    return { selectedItems: sel, packQtys: qtys, numCopies: draft.numCopies || 1,
      dimForm: draft.dimForm || { length: '', width: '', height: '', weight: '' }, lines: lines };
  };
  var [erroredSearch, setErroredSearch] = useState('');
  var [missingSearch, setMissingSearch] = useState('');
  var [unexpectedSearch, setUnexpectedSearch] = useState('');
  // Search bar matching the Shipment Items one — reused for the Errored / Unexpected tabs.
  var packSearchBar = function(value, setValue, placeholder) {
    return h('div', { style: { marginBottom: 10, position: 'relative' } },
      h('input', { value: value, onChange: function(e) { setValue(e.target.value); }, placeholder: placeholder,
        style: { width: '100%', padding: '8px 34px 8px 12px', fontSize: 13, border: '1px solid ' + C.border, borderRadius: 6, background: C.bg, color: C.text, outline: 'none', boxSizing: 'border-box' } }),
      value && h('button', { onClick: function() { setValue(''); }, title: 'Clear search',
        style: { position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', width: 22, height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', border: 'none', background: C.border, color: C.text, cursor: 'pointer', padding: 0, fontFamily: 'inherit' } },
        h(LIcon, { name: 'x', size: 12 }))
    );
  };
  var [bulkMissingSelected, setBulkMissingSelected] = useState({}); // { itemId: true }
  var [bulkMissingConfirm, setBulkMissingConfirm] = useState(null); // { items: [...], note: '' }
  var [bulkMissingSaving, setBulkMissingSaving] = useState(false);

  // Case-packed mode state
  var isCasePacked = plan && plan.packing_mode === 'case_packed';
  var [cpSelectedItem, setCpSelectedItem] = useState(null); // shipment item id
  var [cpUseSaved, setCpUseSaved] = useState(true);
  var [cpUnitsPerCase, setCpUnitsPerCase] = useState('');
  var [cpDims, setCpDims] = useState({ length: '', width: '', height: '', weight: '' });
  var [cpNumCases, setCpNumCases] = useState('');
  var [cpSaveDefault, setCpSaveDefault] = useState(true);
  var [cpGenerating, setCpGenerating] = useState(false);
  var [cpProgress, setCpProgress] = useState({ current: 0, total: 0 });
  // Case-packed runs are single-SKU: arm the scan station on the selected item so the
  // worker can scan immediately — no FNSKU-first scan needed to pick a product.
  useEffect(function() {
    if (!isCasePacked || !cpSelectedItem) return;
    var it = availableItems.find(function(i) { return i.id === cpSelectedItem; });
    if (it && it.requires_transparency && it.product_id) setTScanActive(it.product_id);
  }, [isCasePacked, cpSelectedItem, wizStep]);
  var [boxPage, setBoxPage] = useState(0);
  var [boxFilter, setBoxFilter] = useState('');
  var [toPackSelId, setToPackSelId] = useState(null); // to-pack list: clicked SKU stays highlighted while its box filter is active
  var BOXES_PER_PAGE = 50;
  var [rightTab, setRightTab] = useState('items');

  // Internal notes (from PO)
  var [notes, setNotes] = useState([]);
  var [newNote, setNewNote] = useState('');
  var [notesOpen, setNotesOpen] = useState(false);
  var notesEndRef = useRef(null);
  var poId = plan && (plan.po_id || (plan.po_ids && plan.po_ids[0]));

  var loadNotes = useCallback(function() {
    if (!poId) return;
    api('/purchase-orders/' + poId + '/notes').then(setNotes).catch(function(e) { console.error('[silent-catch]', e); });
  }, [poId]);

  useEffect(function() { loadNotes(); }, [loadNotes]);

  var sendNote = function() {
    if (!newNote.trim() || !poId) return;
    api('/purchase-orders/' + poId + '/notes', { method: 'POST', body: JSON.stringify({ message: newNote.trim(), source: 'fba', entity_id: plan && plan.id }) })
      .then(function() { setNewNote(''); loadNotes(); })
      .catch(function(e) { showToast(e.message, 'error'); });
  };

  var deleteNote = function(noteId) {
    if (!poId) return;
    api('/purchase-orders/' + poId + '/notes/' + noteId, { method: 'DELETE' })
      .then(function() { loadNotes(); })
      .catch(function(e) { showToast(e.message, 'error'); });
  };

  useEffect(function() {
    if (notesOpen && notesEndRef.current) notesEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [notes.length, notesOpen]);

  useEffect(function() {
    api('/settings/label-templates').then(function(tpls) {
      setLabelTemplates(tpls);
      var def = tpls.find(function(t) { return t.is_default; });
      if (def) setSelectedLabelTpl(def.id);
      else if (tpls.length) setSelectedLabelTpl(tpls[0].id);
    }).catch(function(e) { console.error('[silent-catch]', e); });
  }, []);

  var loadShipment = useCallback(function() {
    api('/fba-shipments/shipments/' + shipmentId).then(function(data) {
      setShipment(data);
      setBoxes(data.boxes || []);
      if (!activeBoxId && data.boxes && data.boxes.length) setActiveBoxId(data.boxes[data.boxes.length - 1].id);
    }).catch(function(e) { if (e.message && !e.message.includes('not found') && !e.message.includes('Not found')) showToast(e.message, 'error'); });
  }, [shipmentId]);
  useEffect(function() { loadShipment(); }, [loadShipment]);

  // Load this user's saved in-progress box draft for the shipment (enables cross-device resume).
  useEffect(function() {
    var cancelled = false;
    setRawDraft(undefined);
    api('/fba-shipments/shipments/' + shipmentId + '/box-draft')
      .then(function(d) { if (!cancelled) setRawDraft(d || null); })
      .catch(function() { if (!cancelled) setRawDraft(null); });
    return function() { cancelled = true; };
  }, [shipmentId]);

  // Auto-save the in-progress box (debounced) so it survives leaving the page — even on another
  // device. Only while actively packing (wizStep > 0) and after the initial draft load, so the
  // empty mount state never clobbers a saved draft. An empty selection clears the draft instead.
  useEffect(function() {
    if (rawDraft === undefined) return;
    if (wizStep === 0) return;
    if (draftSaveTimer.current) clearTimeout(draftSaveTimer.current);
    draftSaveTimer.current = setTimeout(function() {
      var hasContent = Object.keys(selectedItems).some(function(k) { return selectedItems[k]; });
      if (!hasContent) {
        api('/fba-shipments/shipments/' + shipmentId + '/box-draft', { method: 'DELETE' }).catch(function() {});
        return;
      }
      api('/fba-shipments/shipments/' + shipmentId + '/box-draft', {
        method: 'PUT',
        body: JSON.stringify({ draft: { selectedItems: selectedItems, packQtys: packQtys, numCopies: numCopies, dimForm: dimForm, wizStep: wizStep } })
      }).catch(function(e) { console.error('[draft-save]', e && e.message); });
    }, 600);
    return function() { if (draftSaveTimer.current) clearTimeout(draftSaveTimer.current); };
  }, [shipmentId, selectedItems, packQtys, numCopies, dimForm, wizStep, rawDraft]);

  // Lightweight refresh — refetches boxes only and recomputes quantity_packed on shipment.items
  // from box contents. Avoids the full-plan repaint caused by onRefresh() / loadShipment().
  var loadBoxes = useCallback(function() {
    return api('/fba-shipments/shipments/' + shipmentId + '/boxes').then(function(boxesData) {
      var arr = boxesData || [];
      setBoxes(arr);
      setShipment(function(prev) {
        if (!prev) return prev;
        var packedBySi = {};
        arr.forEach(function(b) {
          (b.items || []).forEach(function(bi) {
            packedBySi[bi.shipment_item_id] = (packedBySi[bi.shipment_item_id] || 0) + (bi.quantity || 0);
          });
        });
        var newItems = (prev.items || []).map(function(it) {
          return Object.assign({}, it, { quantity_packed: packedBySi[it.id] || 0 });
        });
        return Object.assign({}, prev, { items: newItems, boxes: arr });
      });
    }).catch(function(e) { if (e.message && !e.message.includes('not found') && !e.message.includes('Not found')) showToast(e.message, 'error'); });
  }, [shipmentId]);

  // ─── Bulk box contents via Excel (Amazon Send-to-Amazon-style template) ───
  // Download pre-fills the shipment's SKUs + existing boxes; upload goes
  // preview → confirm modal → apply (REPLACES existing boxes in scope).
  var activePackGroupId = (packGroups && packGroups[activeGroupIdx]) ? packGroups[activeGroupIdx].packingGroupId : null;
  var boxTemplateHref = '/api/fba-shipments/shipments/' + shipmentId + '/box-content-template' + (activePackGroupId ? '?group=' + encodeURIComponent(activePackGroupId) : '');
  var uploadBoxContents = function(file, mode) {
    var form = new FormData();
    form.append('file', file);
    form.append('mode', mode);
    if (activePackGroupId) form.append('group', activePackGroupId);
    setBulkBoxBusy(true);
    return fetch('/api/fba-shipments/shipments/' + shipmentId + '/box-content-upload', { method: 'POST', credentials: 'include', body: form })
      .then(function(r) { return r.json().then(function(j) { return { ok: r.ok, body: j }; }); })
      .then(function(res) {
        if (mode === 'preview') {
          if (!res.ok) { showToast(res.body.error || 'Could not read the file', 'error'); return; }
          setBulkBoxModal({ file: file, report: res.body });
          return;
        }
        if (!res.ok) {
          // Shipment state may have moved since the preview — surface fresh errors in the modal
          if (res.body.errors && res.body.errors.length) {
            setBulkBoxModal(function(prev) {
              return prev ? Object.assign({}, prev, { report: Object.assign({}, prev.report, { ok: false, errors: res.body.errors, warnings: res.body.warnings || [] }) }) : prev;
            });
          }
          showToast(res.body.error || 'Apply failed', 'error');
          return;
        }
        setBulkBoxModal(null);
        showToast(res.body.boxes_created + ' box' + (res.body.boxes_created === 1 ? '' : 'es') + ' created' + (res.body.boxes_replaced ? ' (' + res.body.boxes_replaced + ' replaced)' : '') + ' — ' + res.body.units + ' units packed', 'success');
        loadBoxes();
      })
      .catch(function(err) { showToast(err.message || 'Upload failed', 'error'); })
      .finally(function() { setBulkBoxBusy(false); });
  };

  var [unexpectedReceived, setUnexpectedReceived] = useState([]);
  var [unexpLightbox, setUnexpLightbox] = useState(null); // { images, index }
  var loadUnexpectedReceived = useCallback(function() {
    if (!planId) return;
    api('/fba-shipments/plans/' + planId + '/unexpected-received-items')
      .then(setUnexpectedReceived).catch(function() { setUnexpectedReceived([]); });
  }, [planId]);
  useEffect(function() { loadUnexpectedReceived(); }, [loadUnexpectedReceived]);
  var undoUnexpected = function(logId) {
    api('/fba-shipments/plans/' + planId + '/unexpected-received-items/' + logId + '/undo', { method: 'POST' })
      .then(function(r) { showToast('Undid unexpected ' + r.sku + ' x' + r.qty, 'info'); loadUnexpectedReceived(); if (onRefresh) onRefresh(); })
      .catch(function(e) { showToast(e.message, 'error'); });
  };

  // Hazmat pending — SKUs Amazon rejected at plan creation, auto-stripped and
  // queued here for the packer to bin into the warehouse (as unexpected items
  // on the PO). Loaded from fba_inbound_plans.hazmat_pending_items JSONB.
  var [hazmatPending, setHazmatPending] = useState([]);
  var [hazmatBinInputs, setHazmatBinInputs] = useState({}); // { msku: binStr }
  var [hazmatReceiving, setHazmatReceiving] = useState({}); // { msku: true }
  // Vendor-direct errored resolution (remove from PO → carry to vendor draft)
  var [vdRemoveConfirm, setVdRemoveConfirm] = useState({}); // { msku: true } two-click confirm
  var [vdRemoving, setVdRemoving] = useState({});           // { msku: true }
  // Errored-item Mark Missing resolution (units never arrived)
  var [hzMissingConfirm, setHzMissingConfirm] = useState(null); // errored entry pending confirm
  var [hzMissingNote, setHzMissingNote] = useState('');
  var [hzMissingSaving, setHzMissingSaving] = useState(false);
  var hazmatLoadedRef = useRef(false);
  var loadHazmatPending = useCallback(function() {
    if (!planId) return;
    api('/fba-shipments/plans/' + planId + '/hazmat-pending-items')
      .then(function(list) { hazmatLoadedRef.current = true; setHazmatPending(list); })
      .catch(function() { setHazmatPending([]); });
  }, [planId]);
  useEffect(function() { loadHazmatPending(); }, [loadHazmatPending]);
  // Report the live unreceived-errored count to the parent so the header Continue
  // unlocks the moment the last errored item is received (the parent's gate otherwise
  // reads plan.hazmat_pending_items, stale until a full reload). Only after the first
  // successful fetch — reporting the initial empty state would briefly unlock the gate.
  useEffect(function() {
    if (!hazmatLoadedRef.current || !onErroredCountChange) return;
    // removed_from_po = resolved for vendor-direct plans (line left the PO entirely)
    onErroredCountChange(hazmatPending.filter(function(h) { return h && !h.received && !h.removed_from_po && !h.marked_missing; }).length);
  }, [hazmatPending]);

  // Vendor-direct plans: an errored item's units live at the VENDOR, so instead of a bin
  // receive, remove the line from the PO (user re-sends the revised PO) and carry it to
  // the vendor's draft PO for re-ordering once the listing is fixed.
  var removeErroredFromPo = function(msku) {
    setVdRemoving(function(prev) { var n = Object.assign({}, prev); n[msku] = true; return n; });
    api('/fba-shipments/plans/' + planId + '/hazmat-pending-items/remove-from-po', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ msku: msku })
    }).then(function(r) {
      if (r && r.carried === false) showToast(r.message || (msku + ' is already received — left on PO ' + r.po_number), 'warning');
      else showToast('Removed ' + msku + ' from PO ' + r.po_number + ' — carried to draft PO #' + r.draft_po_number + ' (re-send the revised PO to the vendor)', 'success');
      loadHazmatPending();
    }).catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() {
        setVdRemoving(function(prev) { var n = Object.assign({}, prev); delete n[msku]; return n; });
        setVdRemoveConfirm(function(prev) { var n = Object.assign({}, prev); delete n[msku]; return n; });
      });
  };
  // Optimistic local update for hazmat undo — avoids calling onRefresh (which
  // reloads the full plan and visibly repaints the wizard).
  var undoHazmatPending = function(msku) {
    api('/fba-shipments/plans/' + planId + '/hazmat-pending-items/undo-receive', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ msku: msku })
    }).then(function() {
      showToast('Undid Hazmat receive for ' + msku, 'info');
      setHazmatPending(function(prev) {
        return prev.map(function(it) {
          if (it.msku !== msku) return it;
          var cleared = Object.assign({}, it);
          cleared.received = false;
          delete cleared.bin; delete cleared.received_qty;
          delete cleared.received_at; delete cleared.received_by;
          delete cleared.received_po_id;
          return cleared;
        });
      });
      loadUnexpectedReceived();
    }).catch(function(e) { showToast(e.message, 'error'); });
  };
  var receiveHazmatPending = function(msku) {
    var bin = (hazmatBinInputs[msku] || '').trim();
    if (!bin) { showToast('Enter a bin for ' + msku, 'error'); return; }
    setHazmatReceiving(function(prev) { var n = Object.assign({}, prev); n[msku] = true; return n; });
    api('/fba-shipments/plans/' + planId + '/hazmat-pending-items/receive', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ msku: msku, bin: bin })
    }).then(function(r) {
      var finalBin = (r && r.received && r.received.bin_location) || bin;
      var finalQty = (r && r.received && r.received.qty_received) || null;
      showToast('Received ' + msku + ' → Bin ' + finalBin + ' (Hazmat Review)', 'success');
      setHazmatBinInputs(function(prev) { var n = Object.assign({}, prev); delete n[msku]; return n; });
      // Optimistically mark the single row as Complete in place — no full reload.
      setHazmatPending(function(prev) {
        return prev.map(function(it) {
          if (it.msku !== msku) return it;
          return Object.assign({}, it, {
            received: true,
            bin: finalBin,
            received_qty: finalQty || it.quantity,
            received_at: new Date().toISOString(),
            received_by: (currentUser && (currentUser.display_name || currentUser.username)) || 'You',
            received_po_id: (r && r.received && r.received.po_id) || (it.matched_po && it.matched_po.id) || null,
          });
        });
      });
      loadUnexpectedReceived();
    }).catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() {
        setHazmatReceiving(function(prev) { var n = Object.assign({}, prev); delete n[msku]; return n; });
      });
  };

  // Errored-item Mark Missing — third resolution: the units never arrived. The server
  // also records the shortage on the PO line (is_missing receiving logs), same as
  // regular shipment-item mark-missing, so the PO can flip received when accounted.
  var markErroredMissing = function(msku, note) {
    setHzMissingSaving(true);
    api('/fba-shipments/plans/' + planId + '/hazmat-pending-items/mark-missing', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ msku: msku, note: note })
    }).then(function() {
      showToast('Marked ' + msku + ' as missing', 'info');
      setHazmatPending(function(prev) {
        return prev.map(function(it) {
          if (it.msku !== msku) return it;
          return Object.assign({}, it, {
            marked_missing: true,
            missing_at: new Date().toISOString(),
            missing_by: (currentUser && (currentUser.display_name || currentUser.username)) || 'You',
            missing_note: (note || '').trim() || null,
          });
        });
      });
      setHzMissingConfirm(null); setHzMissingNote('');
    }).catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setHzMissingSaving(false); });
  };
  var undoErroredMissing = function(msku) {
    api('/fba-shipments/plans/' + planId + '/hazmat-pending-items/undo-mark-missing', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ msku: msku })
    }).then(function() {
      showToast('Unmarked missing for ' + msku + ' — returned to Errored list', 'info');
      setHazmatPending(function(prev) {
        return prev.map(function(it) {
          if (it.msku !== msku) return it;
          var cleared = Object.assign({}, it);
          cleared.marked_missing = false;
          delete cleared.missing_at; delete cleared.missing_by; delete cleared.missing_note;
          return cleared;
        });
      });
    }).catch(function(e) { showToast(e.message, 'error'); });
  };

  var items = shipment ? (shipment.items || []) : [];
  var totalPacked = items.reduce(function(s, i) { return s + (i.quantity_packed || 0); }, 0);
  var totalQty = items.reduce(function(s, i) { return s + (i.quantity || 0); }, 0);
  var pctPacked = totalQty > 0 ? Math.round((totalPacked / totalQty) * 100) : 0;

  // Whether Amazon shipment ID is available (needed for case labels)
  var hasShipmentIds = shipment && !!shipment.amazon_shipment_id;
  // Completed boxes (exclude reserved empty boxes)
  var completedBoxes = boxes.filter(function(b) { return b.box_status !== 'reserved' || (b.items && b.items.length > 0); });
  // The right-hand Boxes list scopes to the active packing group (a box's group comes
  // from its items; item-less boxes show in every group). Case-label printing and
  // submit remain shipment-level over ALL boxes.
  var scopedBoxes = packGroups ? completedBoxes.filter(function(b) {
    var bIts = b.items || [];
    return !bIts.length || bIts.some(function(bi) { var g = mskuGroupIdx[bi.msku]; return !g || g === activeGroupIdx + 1; });
  }) : completedBoxes;

  // Items available (not fully packed, not marked missing, not excluded from Amazon).
  // Excluded items are kept on the local record (so they show on the picklist) but
  // they aren't part of the recreated Amazon plan, so they shouldn't block the
  // packer from advancing or appear in the "to pack" queue.
  // Units still packable = expected − packed − marked missing (review M7 #87). Add All, the step-3
  // inputs and the over-pack warning used expected − packed, so after marking units missing a box
  // could be filled past what physically exists and the PO over-received.
  var remainingFor = function(it) { return Math.max(0, (it.quantity || 0) - (it.quantity_packed || 0) - (it.quantity_missing || 0)); };
  var availableItems = items.filter(function(i) {
    if (i.excluded_from_amazon) return false;
    return (i.quantity_packed || 0) + (i.quantity_missing || 0) < i.quantity;
  }).sort(function(a, b) { return (a.msku || '').localeCompare(b.msku || ''); });
  // Auto-pack runner (hoisted from the case-packed wizard 2026-10-22 so the individual flow can use
  // it): one atomic /finish-batch call per eligible SKU — create, fill, measure, seal `cases` boxes.
  function runAutoPack(eligibleAutoPack, totalEligibleCases) {
            if (cpGenerating || !eligibleAutoPack.length) return;
            setCpGenerating(true);
            setCpProgress({ current: 0, total: totalEligibleCases });
            var done = 0;
            var chain = Promise.resolve();
            eligibleAutoPack.forEach(function(entry) {
              var item = entry.item;
              var nc = entry.cases;
              var upc = item.case_qty;
              var dl = parseFloat(item.case_length) || 0;
              var dw = parseFloat(item.case_width) || 0;
              var dh = parseFloat(item.case_height) || 0;
              var dwt = parseFloat(item.case_weight) || null;
              // One atomic /finish-batch call per SKU (review 2026-09-28, medium #39): the per-case
              // create → bulk-pack → dims → seal chain could stop half-way with open boxes.
              chain = chain.then(function() {
                return api('/fba-shipments/shipments/' + shipmentId + '/boxes/finish-batch', { method: 'POST', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ items: [{ barcode: item.msku || item.product_sku, quantity: upc }], dims: { length: dl, width: dw, height: dh, weight: dwt }, count: nc }) })
                  .then(function() { done += nc; setCpProgress({ current: done, total: totalEligibleCases }); });
              });
            });
            chain.then(function() {
              showToast('Auto-packed ' + totalEligibleCases + ' case(s) across ' + eligibleAutoPack.length + ' SKU(s)', 'success');
              fireConfetti();
              setCpGenerating(false);
              setCpProgress({ current: 0, total: 0 });
              loadShipment();
              if (onRefresh) onRefresh();
            }).catch(function(e) {
              showToast(e.message || 'Auto-pack failed mid-batch', 'error');
              setCpGenerating(false);
              setCpProgress({ current: 0, total: 0 });
              loadShipment();
            });
  }
  // The pack UI (to-pack list, wizard, select-all, boxes list) works on the active
  // packing group's slice; shipment-level logic (allResolved, submit, labels) stays
  // on the full set. Identical to availableItems when the plan has 0-1 groups.
  var packScopeItems = packGroups ? availableItems.filter(inActiveGroup) : availableItems;
  // Per-shipment packing completeness — every non-excluded item packed or marked missing.
  // The plan-level errored/hazmat gate (binning rejected SKUs) is enforced on the Continue
  // button, so an unbinned errored SKU no longer suppresses a packed shipment's done badge.
  var unreceivedHazmat = hazmatPending.filter(function(h) { return !h.received && !h.removed_from_po && !h.marked_missing; });
  var allResolved = items.length > 0 && availableItems.length === 0;

  useEffect(function() {
    // Don't report status until this shipment's data has loaded. On (re)mount the
    // shipment is briefly null → items empty → allResolved false; reporting that
    // transient false flickers the parent overview chip (check/green bar → off → on)
    // every time the user clicks a chip. Once loaded, report the real value.
    if (!shipment) return;
    if (onPackingStatusChange) onPackingStatusChange(allResolved);
  }, [allResolved, shipment]);

  // Mark missing handler
  var markMissing = function(item, note) {
    var remaining = item.quantity - (item.quantity_packed || 0) - (item.quantity_missing || 0);
    var qm = (item.quantity_missing || 0) + remaining; // mark all remaining as missing
    var trimmed = (note || '').trim();
    var body = { quantity_missing: qm };
    if (trimmed) body.notes = trimmed;
    api('/fba-shipments/items/' + item.id + '/missing', { method: 'PUT', body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } }).then(function(resp) {
      var msg = resp && resp.po_only
        ? 'Marked ' + remaining + ' unit(s) as missing (Amazon qty unchanged)'
        : 'Marked ' + remaining + ' unit(s) as missing';
      showToast(msg, 'info');
      loadShipment();
    }).catch(function(e) { showToast(e.message, 'error'); });
  };

  // Bulk-mark every selected item's remaining (unpacked, unmarked) units as missing.
  var executeBulkMarkMissing = function(targetItems, note) {
    if (!targetItems || !targetItems.length) return;
    setBulkMissingSaving(true);
    var trimmed = (note || '').trim();
    var total = targetItems.reduce(function(s, it) {
      var remaining = it.quantity - (it.quantity_packed || 0) - (it.quantity_missing || 0);
      return s + Math.max(0, remaining);
    }, 0);
    var requests = targetItems.map(function(it) {
      var remaining = it.quantity - (it.quantity_packed || 0) - (it.quantity_missing || 0);
      if (remaining <= 0) return Promise.resolve();
      var body = { quantity_missing: (it.quantity_missing || 0) + remaining };
      if (trimmed) body.notes = trimmed;
      return api('/fba-shipments/items/' + it.id + '/missing', { method: 'PUT', body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } });
    });
    Promise.allSettled(requests).then(function(results) {
      var failed = results.filter(function(r) { return r.status === 'rejected'; }).length;
      if (failed > 0) {
        showToast('Marked ' + (targetItems.length - failed) + ' / ' + targetItems.length + ' items — ' + failed + ' failed', 'error');
      } else {
        showToast('Marked ' + total + ' unit' + (total === 1 ? '' : 's') + ' across ' + targetItems.length + ' SKU' + (targetItems.length === 1 ? '' : 's') + ' as missing', 'success');
      }
      setBulkMissingConfirm(null);
      setBulkMissingSelected({});
      loadShipment();
    }).finally(function() { setBulkMissingSaving(false); });
  };

  var unmarkMissing = function(item) {
    api('/fba-shipments/items/' + item.id + '/missing', { method: 'PUT', body: JSON.stringify({ quantity_missing: 0 }), headers: { 'Content-Type': 'application/json' } }).then(function() {
      showToast('Cleared missing status', 'info');
      loadShipment();
    }).catch(function(e) { showToast(e.message, 'error'); });
  };

  // Selected items for current box wizard
  var selectedItemsList = availableItems.filter(function(i) { return selectedItems[i.id]; });
  var selectedCount = Object.keys(selectedItems).filter(function(k) { return selectedItems[k]; }).length;

  // Check if all qtys entered in step 2
  var allQtysEntered = selectedItemsList.length > 0 && selectedItemsList.every(function(i) {
    var q = parseInt(packQtys[i.id]);
    var remaining = remainingFor(i);
    return q > 0 && q <= remaining;
  });

  // ─── FNSKU label printing ───
  var printFnskuLabels = function(itemsToPrint) {
    // Print in SKU order regardless of plan insertion order (matches case labels).
    itemsToPrint = (itemsToPrint || []).slice().sort(function(a, b) { return (a.msku || '').localeCompare(b.msku || ''); });
    // Quantities start EMPTY only on shipments carrying Transparency SKUs — typing
    // what you need keeps anyone from accidentally consuming a full run of serials.
    // Plain shipments prefill (box qty in the wizard, else full unit count); labels
    // there are just paper.
    if (items.some(function(i) { return i.requires_transparency && i.product_id; })) {
      setPrintQtyOverride({});
    } else {
      var qtyMap = {};
      itemsToPrint.forEach(function(it) { qtyMap[it.id] = parseInt(packQtys[it.id]) || it.quantity || 1; });
      setPrintQtyOverride(qtyMap);
    }
    var fbaTpls = labelTemplates.filter(function(t) { return (t.category || 'fba') === 'fba'; });
    var def = fbaTpls.find(function(t) { return t.is_default; });
    setSelectedLabelTpl(def ? def.id : (fbaTpls.length > 0 ? fbaTpls[0].id : null));
    setPrintModal({ items: itemsToPrint });
  };

  var executePrint = function() {
    if (!printModal) return;
    var tpl = labelTemplates.find(function(t) { return t.id === selectedLabelTpl; });
    var itemsToPrint = printModal.items;
    var getFieldVal = function(item, field) {
      switch (field) {
        case 'fnsku': return item.fnsku || '';
        case 'sku': return item.msku || item.product_sku || '';
        case 'title': return item.vendor_title || '';
        case 'barcode': return item.barcode || '';
        case 'asin': return item.asin || '';
        case 'condition': return 'New';
        case 'expiry': return '';
        default: return '';
      }
    };
    var widthMm = tpl ? parseFloat(tpl.width_mm) : 57.15;
    var heightMm = tpl ? parseFloat(tpl.height_mm) : 31.75;
    var fontSize = tpl ? (tpl.font_size || 12) : 12;
    var fields = tpl ? (tpl.fields || ['fnsku', 'sku', 'title']) : ['fnsku', 'sku', 'title'];
    var showBarcode = tpl ? tpl.show_barcode !== false : true;
    var isSheet = tpl ? tpl.label_type === 'sheet' : false;
    var cols = tpl ? (tpl.columns || 1) : 1;
    var rowsPerSheet = tpl ? (tpl.rows_per_sheet || 1) : 1;
    var pageW = tpl && tpl.page_width_mm ? parseFloat(tpl.page_width_mm) : 215.9;
    var pageH = tpl && tpl.page_height_mm ? parseFloat(tpl.page_height_mm) : 279.4;
    // Transparency SKUs print as interleaved FNSKU + Transparency pairs from the server
    // (consumes label-file serials — every printed pair gets applied to one unit);
    // only plain SKUs continue through the HTML label path below.
    var tReq = itemsToPrint.filter(function(i) { return i.requires_transparency && i.product_id; })
      .map(function(i) { return { shipment_item_id: i.id, product_id: i.product_id, qty: printQtyOverride[i.id] || 0 }; })
      .filter(function(x) { return x.qty > 0; });
    if (tReq.length) printPrepLabelsCore(tReq, widthMm, heightMm);
    itemsToPrint = itemsToPrint.filter(function(i) { return !(i.requires_transparency && i.product_id); });
    // how many title lines this template has room for (two where they fit, Anthony 2026-09-29)
    var titleFit = productLabelTitleFit(heightMm, fontSize, showBarcode ? Math.max(4, heightMm * 0.28) : 0);
    var allLabels = [];
    itemsToPrint.forEach(function(item) {
      var count = printQtyOverride[item.id] || 0;
      for (var i = 0; i < count; i++) {
        var lbl = '<div style="width:' + widthMm + 'mm;height:' + heightMm + 'mm;box-sizing:border-box;padding:2mm;display:flex;flex-direction:column;justify-content:center;align-items:center;overflow:hidden;page-break-inside:avoid;">';
        // 1. Barcode (always on top)
        if (showBarcode) {
          var bv = item.fnsku || item.msku || '';
          var barcodeWidthMm = widthMm - 4;
          var barcodeHeightMm = Math.max(4, heightMm * 0.28);
          var barcodeImg = bv ? generateBarcodeImg(bv, barcodeWidthMm, barcodeHeightMm) : '';
          lbl += '<div style="text-align:center;margin-bottom:0.3mm;">' + barcodeImg + '</div>';
        }
        // 2. FNSKU text (always under barcode)
        var fnskuVal = item.fnsku || item.msku || '';
        if (fnskuVal) {
          lbl += '<div style="font-family:monospace;font-size:' + Math.max(5, fontSize - 4) + 'pt;font-weight:bold;letter-spacing:0.5px;margin-bottom:0.3mm;text-align:center;">' + escHtml(fnskuVal) + '</div>'; // escaped (review 2026-09-28, medium #40)
        }
        // 3. Title
        var titleVal = getFieldVal(item, 'title');
        if (titleVal && fields.indexOf('title') >= 0) {
          // two lines, never cut on one (Anthony 2026-09-29, shared with the MELI printer)
          lbl += '<div style="' + productLabelTitleCss(titleFit.pt, titleFit.lines) + '">' + escHtml(titleVal) + '</div>';
        }
        // 4. Bottom row: Condition | SKU | Expiration Date
        var condVal = fields.indexOf('condition') >= 0 ? getFieldVal(item, 'condition') : '';
        var skuVal = fields.indexOf('sku') >= 0 ? getFieldVal(item, 'sku') : '';
        var expVal = fields.indexOf('expiry') >= 0 ? getFieldVal(item, 'expiry') : '';
        var bottomParts = [];
        if (condVal) bottomParts.push(condVal);
        if (skuVal) bottomParts.push(skuVal);
        if (expVal) bottomParts.push('Exp: ' + expVal);
        if (bottomParts.length > 0) {
          lbl += '<div style="font-size:' + Math.max(4, fontSize - 6) + 'pt;color:#333;text-align:center;margin-top:0.3mm;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100%;">' + escHtml(bottomParts.join(' | ')) + '</div>';
        }
        lbl += '</div>';
        allLabels.push(lbl);
      }
    });
    if (!allLabels.length) {
      // Nothing on the HTML path: fine if the transparency PDF is printing, an error if
      // the user never entered any quantity at all.
      if (!tReq.length) { showToast('Enter a label quantity for at least one item first', 'error'); return; }
      setPrintModal(null); return;
    }
    var pw = window.open('', '_blank', 'width=600,height=800');
    if (!pw) return;
    var body, html;
    if (isSheet) {
      var mH = (pageW - (cols * widthMm)) / (cols + 1);
      var mV = (pageH - (rowsPerSheet * heightMm)) / (rowsPerSheet + 1);
      var pgs = []; var pp = cols * rowsPerSheet;
      for (var p = 0; p < allLabels.length; p += pp) {
        var sl = allLabels.slice(p, p + pp);
        var ph = '<div style="width:' + pageW + 'mm;height:' + pageH + 'mm;box-sizing:border-box;padding:' + mV + 'mm ' + mH + 'mm;display:flex;flex-wrap:wrap;align-content:flex-start;gap:0;page-break-after:always;"><div style="display:grid;grid-template-columns:repeat(' + cols + ',' + widthMm + 'mm);grid-template-rows:repeat(' + rowsPerSheet + ',' + heightMm + 'mm);gap:0;width:100%;">';
        sl.forEach(function(l) { ph += l; }); ph += '</div></div>'; pgs.push(ph);
      }
      html = pgs.join('');
    } else {
      html = '<div style="display:flex;flex-wrap:wrap;gap:0;justify-content:center;">'; allLabels.forEach(function(l) { html += l; }); html += '</div>';
    }
    pw.document.write('<!DOCTYPE html><html><head><title>FNSKU Labels</title><style>@media print{body{margin:0;}@page{size:' + (isSheet ? pageW + 'mm ' + pageH + 'mm' : widthMm + 'mm ' + heightMm + 'mm') + ';margin:0;}div{page-break-inside:avoid;}}</style></head><body style="font-family:Arial,sans-serif;margin:0;padding:0;">' + html + '</body></html>');
    pw.document.close();
    // Drive printing from the opener (same as printAllCaseLabelsLocal) — an in-document
    // window.onload handler did not reliably fire for document.write popups (it waits on every
    // barcode image to load), leaving labels stuck in preview. 500ms lets the data-URI barcodes
    // paint before the dialog opens.
    setTimeout(function() { try { pw.focus(); pw.print(); } catch (e) { /* popup closed */ } }, 500);
    setPrintModal(null);
  };

  var exportFnskuLabelsZip = function() {
    if (!printModal) return;
    if (!window.JSZip) { showToast('ZIP library not loaded', 'error'); return; }
    var jspdfNs = window.jspdf || window.jsPDF;
    var JsPDFCtor = jspdfNs && (jspdfNs.jsPDF || jspdfNs);
    if (!JsPDFCtor) { showToast('PDF library not loaded', 'error'); return; }
    var tpl = labelTemplates.find(function(t) { return t.id === selectedLabelTpl; });
    if (!tpl) { showToast('Select a template first', 'error'); return; }
    var itemsToPrint = printModal.items;
    var getFieldVal = function(item, field) {
      switch (field) {
        case 'fnsku': return item.fnsku || '';
        case 'sku': return item.msku || item.product_sku || '';
        case 'title': return item.vendor_title || '';
        case 'barcode': return item.barcode || '';
        case 'asin': return item.asin || '';
        case 'condition': return 'New';
        case 'expiry': return '';
        default: return '';
      }
    };
    var widthMm = parseFloat(tpl.width_mm);
    var heightMm = parseFloat(tpl.height_mm);
    var fontSize = tpl.font_size || 12;
    var fields = tpl.fields || ['fnsku', 'sku', 'title'];
    var showBarcode = tpl.show_barcode !== false;
    var isSheet = tpl.label_type === 'sheet';
    var cols = tpl.columns || 1;
    var rowsPerSheet = tpl.rows_per_sheet || 1;
    var pageW = tpl.page_width_mm ? parseFloat(tpl.page_width_mm) : 215.9;
    var pageH = tpl.page_height_mm ? parseFloat(tpl.page_height_mm) : 279.4;
    var pad = 2;
    var barcodeHeightMm = Math.max(4, heightMm * 0.28);
    var barcodeWidthMm = widthMm - pad * 2;
    var ptToMm = function(pt) { return pt * 0.3528; };
    var clip = function(str, maxMm, sizePt) {
      if (!str) return '';
      var charWidthMm = ptToMm(sizePt) * 0.5;
      var maxChars = Math.floor(maxMm / charWidthMm);
      return str.length > maxChars ? str.slice(0, Math.max(0, maxChars - 1)) + '…' : str;
    };
    var renderLabelAt = function(doc, item, x, y) {
      var cursorY = y + pad;
      var fnskuVal = item.fnsku || item.msku || '';
      if (showBarcode && fnskuVal) {
        var canvas = document.createElement('canvas');
        window.bwipjs.toCanvas(canvas, { bcid: 'code128', text: fnskuVal, scale: 3, height: barcodeHeightMm, includetext: false, paddingleft: 4, paddingright: 4 });
        doc.addImage(canvas.toDataURL('image/png'), 'PNG', x + pad, cursorY, barcodeWidthMm, barcodeHeightMm);
        cursorY += barcodeHeightMm + 0.5;
      }
      if (fnskuVal) {
        var fnskuPt = Math.max(5, fontSize - 4);
        doc.setFont('courier', 'bold'); doc.setFontSize(fnskuPt);
        cursorY += ptToMm(fnskuPt) * 0.75;
        doc.text(fnskuVal, x + widthMm / 2, cursorY, { align: 'center' });
        cursorY += 0.4;
      }
      var titleVal = getFieldVal(item, 'title');
      if (titleVal && fields.indexOf('title') >= 0) {
        // up to two lines measured with the real font, the type shrinks before anything is cut
        // (Anthony 2026-09-29, shared with the MELI printer)
        doc.setFont('helvetica', 'normal');
        var titleFit = productLabelTitleFit(heightMm, fontSize, showBarcode ? barcodeHeightMm : 0);
        var titleLines = productLabelTitleLines(doc, titleVal, widthMm - pad * 2, titleFit.pt, titleFit.lines);
        doc.setFontSize(titleLines.pt);
        titleLines.lines.forEach(function(line, idx) {
          cursorY += ptToMm(titleLines.pt) * (idx === 0 ? 0.75 : 1.15);
          doc.text(line, x + widthMm / 2, cursorY, { align: 'center' });
        });
        cursorY += 0.4;
      }
      var condVal = fields.indexOf('condition') >= 0 ? getFieldVal(item, 'condition') : '';
      var skuVal = fields.indexOf('sku') >= 0 ? getFieldVal(item, 'sku') : '';
      var expVal = fields.indexOf('expiry') >= 0 ? getFieldVal(item, 'expiry') : '';
      var bottomParts = [];
      if (condVal) bottomParts.push(condVal);
      if (skuVal) bottomParts.push(skuVal);
      if (expVal) bottomParts.push('Exp: ' + expVal);
      if (bottomParts.length > 0) {
        var botPt = Math.max(4, fontSize - 6);
        doc.setFont('helvetica', 'normal'); doc.setFontSize(botPt);
        var botY = y + heightMm - pad;
        doc.text(clip(bottomParts.join(' | '), widthMm - pad * 2, botPt), x + widthMm / 2, botY, { align: 'center' });
      }
    };
    var triggerDownload = function(blob, successMsg) {
      var url = URL.createObjectURL(blob);
      var a = document.createElement('a');
      var ts = new Date();
      var stamp = ts.getFullYear() + String(ts.getMonth() + 1).padStart(2, '0') + String(ts.getDate()).padStart(2, '0');
      a.href = url;
      a.download = 'fnsku-labels-' + stamp + '.zip';
      document.body.appendChild(a);
      a.click();
      setTimeout(function() { document.body.removeChild(a); URL.revokeObjectURL(url); }, 200);
      showToast(successMsg, 'success');
      setPrintModal(null);
    };
    var zip = new window.JSZip();
    if (isSheet) {
      var perPage = cols * rowsPerSheet;
      var mH = (pageW - cols * widthMm) / (cols + 1);
      var mV = (pageH - rowsPerSheet * heightMm) / (rowsPerSheet + 1);
      var sheetCount = 0;
      var labelCount = 0;
      var usedNames = {};
      try {
        itemsToPrint.forEach(function(item) {
          var count = printQtyOverride[item.id] || 0;
          if (count <= 0) return;
          var doc = new JsPDFCtor({ orientation: pageW >= pageH ? 'landscape' : 'portrait', unit: 'mm', format: [pageW, pageH] });
          for (var i = 0; i < count; i++) {
            var slot = i % perPage;
            if (i > 0 && slot === 0) doc.addPage([pageW, pageH], pageW >= pageH ? 'landscape' : 'portrait');
            var col = slot % cols;
            var row = Math.floor(slot / cols);
            renderLabelAt(doc, item, mH + col * (widthMm + mH), mV + row * (heightMm + mV));
          }
          var baseName = [(item.msku || item.product_sku || ''), (item.fnsku || '')].filter(Boolean).join('_').replace(/[^A-Za-z0-9_-]/g, '_') || 'label';
          var fname = baseName + '.pdf';
          if (usedNames[fname]) { fname = baseName + '_' + (++usedNames[baseName]) + '.pdf'; }
          usedNames[fname] = 1; usedNames[baseName] = usedNames[baseName] || 1;
          zip.file(fname, doc.output('blob'));
          sheetCount++;
          labelCount += count;
        });
      } catch (e) { showToast('PDF build failed: ' + e.message, 'error'); return; }
      showToast('Building ZIP...', 'info');
      zip.generateAsync({ type: 'blob' }).then(function(b) { triggerDownload(b, 'Downloaded ' + sheetCount + ' PDF(s), ' + labelCount + ' labels'); })
        .catch(function(e) { showToast('ZIP failed: ' + e.message, 'error'); });
    } else {
      var skuCount = 0;
      var labelCount2 = 0;
      var usedNames2 = {};
      try {
        itemsToPrint.forEach(function(item) {
          var count = printQtyOverride[item.id] || 0;
          if (count <= 0) return;
          var doc = new JsPDFCtor({ orientation: widthMm >= heightMm ? 'landscape' : 'portrait', unit: 'mm', format: [widthMm, heightMm] });
          for (var i = 0; i < count; i++) {
            if (i > 0) doc.addPage([widthMm, heightMm], widthMm >= heightMm ? 'landscape' : 'portrait');
            renderLabelAt(doc, item, 0, 0);
          }
          var baseName = [(item.msku || item.product_sku || ''), (item.fnsku || '')].filter(Boolean).join('_').replace(/[^A-Za-z0-9_-]/g, '_') || 'label';
          var fname = baseName + '.pdf';
          if (usedNames2[fname]) { fname = baseName + '_' + (++usedNames2[baseName]) + '.pdf'; }
          usedNames2[fname] = 1; usedNames2[baseName] = usedNames2[baseName] || 1;
          zip.file(fname, doc.output('blob'));
          skuCount++;
          labelCount2 += count;
        });
      } catch (e) { showToast('PDF build failed: ' + e.message, 'error'); return; }
      showToast('Building ZIP...', 'info');
      zip.generateAsync({ type: 'blob' }).then(function(b) { triggerDownload(b, 'Downloaded ' + skuCount + ' PDF(s), ' + labelCount2 + ' labels'); })
        .catch(function(e) { showToast('ZIP failed: ' + e.message, 'error'); });
    }
  };

  // ─── Print case label ───
  var printAllCaseLabelsLocal = function(opts) {
    // opts.boxIds — print only the listed boxes (used by finishBox after a batch finish).
    // Default: print labels for every box in the current shipment.
    opts = opts || {};
    var ids = opts.boxIds && opts.boxIds.length ? opts.boxIds : boxes.map(function(b) { return b.id; });
    if (!ids.length) { showToast('No boxes to print labels for', 'error'); return; }
    showToast('Generating ' + ids.length + ' case label(s)...', 'info');
    Promise.all(ids.map(function(id) { return api('/fba-shipments/boxes/' + id + '/case-label-data'); }))
      .then(function(allData) {
        var pw = window.open('', '_blank', 'width=420,height=630');
        if (!pw) { showToast('Pop-up blocked', 'error'); return; }
        // Identical format to the single-box printCaseLabel below (Amazon FBA case label).
        var html = '<!DOCTYPE html><html><head><title>FBA Case Labels</title>' +
          '<style>' +
          '@page { size: 4.25in 6in; margin: 0; }' +
          'body { margin: 0; padding: 0; font-family: Helvetica, Arial, sans-serif; }' +
          '.label { width: 4.25in; height: 6in; padding: 0.1in 0.2in; box-sizing: border-box; overflow: hidden; }' +
          '.label + .label { page-break-before: always; }' +
          '.banner { text-align: center; font-size: 8pt; margin-bottom: 2px; }' +
          '.fba-hdr { font-size: 15pt; margin-bottom: 0; }' +
          '.hr { border: none; border-top: 1.5px solid #000; margin: 1px 0; }' +
          '.addr-row { display: flex; font-size: 8pt; line-height: 1.05; margin: 0; }' +
          '.addr-col { flex: 1; }' +
          '.addr-lbl { font-size: 8pt; margin-bottom: 0; }' +
          '.info-bar { background: #000 !important; color: #fff !important; font-size: 6pt; padding: 1px 3px; display: flex; justify-content: space-between; align-items: center; margin: 0 0 2px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }' +
          '.barcode-area { margin: 4px 0 0; }' +
          '.code128-col { width: 100%; text-align: center; }' +
          '.code128-col svg { display: inline-block; }' +
          '.pdf417-col { width: 100%; text-align: center; margin: 0.4in 0 0; }' +
          '.pdf417-col img { display: block; width: 3.5in; height: auto; image-rendering: pixelated; margin: 0 auto; }' +
          '.box-id-text { font-size: 8pt; text-align: center; margin: 2px 0 0; }' +
          '.hr2 { border: none; border-top: 1px solid #000; margin: 3px 0; }' +
          '.item-section { margin: 6px 0 0; font-size: 8pt; line-height: 1.3; text-align: center; }' +
          '.item-section .sku-label { font-size: 9pt; font-weight: bold; }' +
        '.bottom-box-id { position: absolute; bottom: 0.1in; left: 0; right: 0; text-align: center; font-size: 14pt; }' +
          '</style></head><body>';
        allData.forEach(function(d) {
          var barcodeItems = (d.items || []).map(function(it) { return { fnsku: it.fnsku || '', qty: it.quantity || 0 }; });
          var amznData = buildAmazonCaseBarcodeData(d.shipment.amazonShipmentId, barcodeItems);
          var _c128canvas = document.createElement('canvas');
        window.bwipjs.toCanvas(_c128canvas, { bcid: 'code128', text: d.boxId, scale: 3, height: 18, paddingleft: 10, paddingright: 10, includetext: false });
        var boxIdBarcode = '<img src="' + _c128canvas.toDataURL('image/png') + '" style="display:block;width:3.5in;height:auto;image-rendering:pixelated;margin:0 auto;" />';
          var pdf417Html = generateCasePDF417SVG(amznData, 'display:block;width:3.5in;height:auto;image-rendering:pixelated;margin:0 auto;');
          var cDate = new Date(d.shipment.createdAt);
          var createdStr = 'Created: ' + cDate.getFullYear() + '/' + String(cDate.getMonth() + 1).padStart(2, '0') + '/' + String(cDate.getDate()).padStart(2, '0') + ' ' + String(cDate.getHours()).padStart(2, '0') + ':' + String(cDate.getMinutes()).padStart(2, '0');
          var isSingleSku = d.items.length === 1;
          var countryFrom = (d.shipFrom.country || 'US') === 'US' ? 'United States' : d.shipFrom.country;
          var countryTo = (d.shipTo.country || 'US') === 'US' ? 'United States' : d.shipTo.country;
          var shipToLines = '<div>' + escHtml(d.shipTo.name) + '</div>'; // text is escaped: names, addresses and titles come from Amazon / the catalog / the user (review 2026-09-28, medium #40)
          if (d.shipTo.fc) shipToLines += '<div>' + escHtml(d.shipTo.fc) + '</div>';
          if (d.shipTo.address1) shipToLines += '<div>' + escHtml(d.shipTo.address1) + '</div>';
          if (d.shipTo.address2) shipToLines += '<div>' + escHtml(d.shipTo.address2) + '</div>';
          if (d.shipTo.city || d.shipTo.state || d.shipTo.zip) shipToLines += '<div>' + escHtml([d.shipTo.city, d.shipTo.state].filter(Boolean).join(', ') + ' ' + d.shipTo.zip) + '</div>';
          shipToLines += '<div>' + escHtml(countryTo) + '</div>';
          // SKU section (LTL only) — single-SKU boxes show the SKU + Vendor SKU;
          // multi-SKU boxes show "Mixed SKUs".
          var skuLine = '';
          if (d.plan && d.plan.shipmentType === 'LTL') {
            var escSku2 = function(s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); };
            if (isSingleSku) {
              var sIt2 = d.items[0] || {};
              var sSku2 = sIt2.product_sku || sIt2.msku || '';
              var sVsku2 = sIt2.vendor_sku || '';
              var sFnsku2 = sIt2.fnsku || '';
              if (sSku2) skuLine += '<div>SKU: <strong>' + escSku2(sSku2) + '</strong></div>';
              if (sVsku2) skuLine += '<div>Vendor SKU: <strong>' + escSku2(sVsku2) + '</strong></div>';
              if (sFnsku2) skuLine += '<div>FNSKU: <strong>' + escSku2(sFnsku2) + '</strong></div>';
            } else if (d.items.length > 1) {
              skuLine = '<div><strong>Mixed SKUs</strong></div>';
            }
          }
          html += '<div class="label" style="position:relative;">' +
            '<div class="banner">PLEASE LEAVE THIS LABEL UNCOVERED</div>' +
            '<div class="fba-hdr">FBA</div>' +
            '<hr class="hr">' +
            '<div class="addr-row">' +
              '<div class="addr-col"><div class="addr-lbl">SHIP FROM:</div><div>' + escHtml(d.shipFrom.name) + '</div><div>' + escHtml(d.shipFrom.address1) + '</div><div>' + escHtml([d.shipFrom.city, d.shipFrom.state].filter(Boolean).join(', ') + ' ' + d.shipFrom.zip) + '</div><div>' + escHtml(countryFrom) + '</div></div>' +
              '<div class="addr-col"><div class="addr-lbl">SHIP TO:</div>' + shipToLines + '</div>' +
            '</div>' +
            '<div class="info-bar"><span>' + escHtml(d.shipment.displayName) + '</span><span>' + escHtml(createdStr) + '</span></div>' +
            '<div class="barcode-area">' +
              '<div class="code128-col">' + boxIdBarcode + '</div>' +
              '<div class="box-id-text">' + escHtml(d.boxId) + '</div>' +
              '<hr class="hr2">' +
              '<div class="pdf417-col">' + pdf417Html + '</div>' +
              '<div class="item-section">' +
                '<div>Dest: <strong>' + escHtml(d.shipment.destinationFc || '') + '</strong></div><div>Shipment: <strong>' + escHtml(d.shipment.amazonShipmentId || '') + '</strong></div><div>Units: <strong>' + (d.totalUnits || 0) + '</strong></div>' +
                skuLine +
              '</div>' +
            '</div>' +
            '<div class="bottom-box-id">Box ' + d.boxNumber + '</div>' +
          '</div>';
        });
        html += '</body></html>';
        pw.document.write(html); pw.document.close();
        setTimeout(function() { pw.print(); }, 500);
      }).catch(function(e) { showToast('Error: ' + e.message, 'error'); });
  };

  var printCaseLabel = function(barcodeData, boxNumber, dims, boxIdOverride) {
    // Fetch full case label data from API
    var boxObj = boxes.find(function(b) { return b.box_number === boxNumber; });
    if (!boxObj && boxIdOverride) boxObj = { id: boxIdOverride };
    if (!boxObj) { showToast('Box not found', 'error'); return; }

    api('/fba-shipments/boxes/' + boxObj.id + '/case-label-data').then(function(d) {
      // Build Amazon 2D barcode data string
      var barcodeItems = (d.items || []).map(function(it) {
        return { fnsku: it.fnsku || '', qty: it.quantity || 0 };
      });
      var amznData = buildAmazonCaseBarcodeData(d.shipment.amazonShipmentId, barcodeItems);

      // Generate barcodes
      var _c128canvas = document.createElement('canvas');
      window.bwipjs.toCanvas(_c128canvas, { bcid: 'code128', text: d.boxId, scale: 3, height: 18, paddingleft: 10, paddingright: 10, includetext: false });
      var boxIdBarcode = '<img src="' + _c128canvas.toDataURL('image/png') + '" style="display:block;width:3.5in;height:auto;image-rendering:pixelated;margin:0 auto;" />';
      var pdf417Html = generateCasePDF417SVG(amznData, 'display:block;width:3.5in;height:auto;image-rendering:pixelated;margin:0 auto;');

      // Format timestamp
      var cDate = new Date(d.shipment.createdAt);
      var createdStr = 'Created: ' + cDate.getFullYear() + '/' + String(cDate.getMonth() + 1).padStart(2, '0') + '/' + String(cDate.getDate()).padStart(2, '0') + ' ' + String(cDate.getHours()).padStart(2, '0') + ':' + String(cDate.getMinutes()).padStart(2, '0');

      var isSingleSku = d.items.length === 1;

      // SKU section (LTL only) — single-SKU boxes show the SKU + Vendor SKU,
      // multi-SKU boxes show "Mixed SKUs". SPD labels are Amazon-provided so
      // this branch is skipped.
      var skuLine = '';
      if (d.plan && d.plan.shipmentType === 'LTL') {
        var escSku = function(s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); };
        if (isSingleSku) {
          var sIt = d.items[0] || {};
          var sSku = sIt.product_sku || sIt.msku || '';
          var sVsku = sIt.vendor_sku || '';
          var sFnsku = sIt.fnsku || '';
          if (sSku) skuLine += '<div>SKU: <strong>' + escSku(sSku) + '</strong></div>';
          if (sVsku) skuLine += '<div>Vendor SKU: <strong>' + escSku(sVsku) + '</strong></div>';
          if (sFnsku) skuLine += '<div>FNSKU: <strong>' + escSku(sFnsku) + '</strong></div>';
        } else if (d.items.length > 1) {
          skuLine = '<div><strong>Mixed SKUs</strong></div>';
        }
      }

      // Ship-to lines
      var shipToLines = '<div>' + escHtml(d.shipTo.name) + '</div>'; // text is escaped: names, addresses and titles come from Amazon / the catalog / the user (review 2026-09-28, medium #40)
      if (d.shipTo.fc) shipToLines += '<div>' + escHtml(d.shipTo.fc) + '</div>';
      if (d.shipTo.address1) shipToLines += '<div>' + escHtml(d.shipTo.address1) + '</div>';
      if (d.shipTo.address2) shipToLines += '<div>' + escHtml(d.shipTo.address2) + '</div>';
      if (d.shipTo.city || d.shipTo.state || d.shipTo.zip) shipToLines += '<div>' + escHtml([d.shipTo.city, d.shipTo.state].filter(Boolean).join(', ') + ' ' + d.shipTo.zip) + '</div>';
      // review M7 #91: interpolate the display country while BUILDING the block (as the batch
      // printer does). The old `.replace(country, ...)` on the finished HTML rewrote the first
      // "US" anywhere in it (AUS2, "US HWY", PLUS) and left the real country line untouched.
      var countryFrom = (d.shipFrom.country || 'US') === 'US' ? 'United States' : d.shipFrom.country;
      var countryTo = (d.shipTo.country || 'US') === 'US' ? 'United States' : d.shipTo.country;
      shipToLines += '<div>' + escHtml(countryTo) + '</div>';

      var html = '<!DOCTYPE html><html><head><title>FBA Case Label - Box ' + d.boxNumber + '</title>' +
        '<style>' +
        '@page { size: 4.25in 6in; margin: 0; }' +
        'body { margin: 0; padding: 0; font-family: Helvetica, Arial, sans-serif; width: 4.25in; height: 6in; box-sizing: border-box; }' +
        '.label { width: 4.25in; height: 6in; padding: 0.1in 0.2in; box-sizing: border-box; overflow: hidden; }' +
        '.banner { text-align: center; font-size: 8pt; margin-bottom: 2px; }' +
        '.fba-hdr { font-size: 15pt; margin-bottom: 0; }' +
        '.hr { border: none; border-top: 1.5px solid #000; margin: 1px 0; }' +
        '.addr-row { display: flex; font-size: 8pt; line-height: 1.05; margin: 0; }' +
        '.addr-col { flex: 1; }' +
        '.addr-lbl { font-size: 8pt; margin-bottom: 0; }' +
        '.info-bar { background: #000 !important; color: #fff !important; font-size: 6pt; padding: 1px 3px; display: flex; justify-content: space-between; align-items: center; margin: 0 0 2px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }' +
        '.barcode-area { margin: 4px 0 0; }' +
        '.code128-col { width: 100%; text-align: center; }' +
        '.code128-col svg { display: inline-block; }' +
        '.pdf417-col { width: 100%; text-align: center; margin: 0.4in 0 0; }' +
        '.pdf417-col img { display: block; width: 3.5in; height: auto; image-rendering: pixelated; margin: 0 auto; }' +
        '.box-id-text { font-size: 8pt; text-align: center; margin: 2px 0 0; }' +
        '.hr2 { border: none; border-top: 1px solid #000; margin: 3px 0; }' +
        '.item-section { margin: 6px 0 0; font-size: 8pt; line-height: 1.3; text-align: center; }' +
        '.item-section .sku-label { font-size: 9pt; font-weight: bold; }' +
        '.bottom-box-id { position: absolute; bottom: 0.1in; left: 0; right: 0; text-align: center; font-size: 14pt; }' +
        '</style></head><body>' +
        '<div class="label" style="position:relative;">' +
        '<div class="banner">PLEASE LEAVE THIS LABEL UNCOVERED</div>' +
        '<div class="fba-hdr">FBA</div>' +
        '<hr class="hr">' +
        '<div class="addr-row">' +
          '<div class="addr-col"><div class="addr-lbl">SHIP FROM:</div><div>' + escHtml(d.shipFrom.name) + '</div><div>' + escHtml(d.shipFrom.address1) + '</div><div>' + escHtml([d.shipFrom.city, d.shipFrom.state].filter(Boolean).join(', ') + ' ' + d.shipFrom.zip) + '</div><div>' + escHtml(countryFrom) + '</div></div>' +
          '<div class="addr-col"><div class="addr-lbl">SHIP TO:</div>' + shipToLines + '</div>' +
        '</div>' +
        '<div class="info-bar"><span>' + escHtml(d.shipment.displayName) + '</span><span>' + escHtml(createdStr) + '</span></div>' +
        '<div class="barcode-area">' +
          '<div class="code128-col">' + boxIdBarcode + '</div>' +
          '<div class="box-id-text">' + escHtml(d.boxId) + '</div>' +
          '<hr class="hr2">' +
          '<div class="pdf417-col">' + pdf417Html + '</div>' +
          '<div class="item-section">' +
            '<div>Dest: <strong>' + escHtml(d.shipment.destinationFc || '') + '</strong></div><div>Shipment: <strong>' + escHtml(d.shipment.amazonShipmentId || '') + '</strong></div><div>Units: <strong>' + (d.totalUnits || 0) + '</strong></div>' +
            skuLine +
          '</div>' +
        '</div>' +
        '<div class="bottom-box-id">Box ' + d.boxNumber + '</div>' +
        '</div>' +
        '</body></html>';

      var pw = window.open('', '_blank', 'width=420,height=630');
      if (!pw) { showToast('Pop-up blocked', 'error'); return; }
      pw.document.write(html);
      pw.document.close();
      // Drive printing from the opener (same as printAllCaseLabelsLocal) — an in-document
      // window.onload handler did not reliably fire for document.write popups, leaving the label
      // stuck in preview. 500ms lets the data-URI barcodes paint before the dialog opens.
      setTimeout(function() { try { pw.focus(); pw.print(); } catch (e) { /* popup closed */ } }, 500);
      // Mark box as printed
      api('/fba-shipments/boxes/' + boxObj.id + '/mark-printed', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
        .then(function() { loadShipment(); }).catch(function(e) { console.error('[silent-catch]', e); });
    }).catch(function(e) { showToast('Failed to load case label data: ' + e.message, 'error'); });
  };

  // ─── Step 4: Finish box (create N identical boxes + pack items + seal + print all) ───
  // Single atomic /finish-batch call — no pre-reservation, no partial state on failure.
  // numCopies > 1 ships the same items+dims into N boxes and prints all N labels in one
  // popup, replacing the prior "Duplicate manually, then Print All and narrow it down"
  // workflow.
  var finishBox = function() {
    if (saving) return;
    var l = parseFloat(dimForm.length); var w = parseFloat(dimForm.width);
    var ht = parseFloat(dimForm.height); var wt = parseFloat(dimForm.weight);
    if (!l || !w || !ht) { showToast('Box dimensions (L × W × H) are required', 'error'); return; }
    var copies = Math.max(1, Math.min(999, parseInt(numCopies, 10) || 1));
    // Transparency SKUs force a single box — unique serials can't be duplicated.
    if (selectedItemsList.some(function(i) { return i.requires_transparency && i.product_id; })) copies = 1;
    setSaving(true);

    var payloadItems = [];
    selectedItemsList.forEach(function(item) {
      var qty = parseInt(packQtys[item.id]) || 0;
      if (qty > 0) {
        payloadItems.push({ barcode: item.msku || item.product_sku, quantity: qty });
      }
    });
    if (!payloadItems.length) { showToast('No items selected', 'error'); setSaving(false); return; }

    // Kill any pending draft auto-save NOW — a debounced PUT that fires while the finish
    // request is in flight can land after the post-finish draft DELETE and resurrect the
    // draft as a bogus "Resume in-progress box?" prompt.
    if (draftSaveTimer.current) { clearTimeout(draftSaveTimer.current); draftSaveTimer.current = null; }

    api('/fba-shipments/shipments/' + shipmentId + '/boxes/finish-batch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: payloadItems, dims: { length: l, width: w, height: ht, weight: wt || null }, count: copies })
    })
      .then(function(res) {
        var created = (res && res.boxes) || [];
        var nums = created.map(function(b) { return b.box_number; });
        // Attribute this session's Transparency scans to the created box(es) — audit
        // trail only (submit gate uses plan totals). Chunked in scan order: qty per
        // box, copies in creation order; over-scans stay unattributed.
        try {
          var assignments = [];
          selectedItemsList.forEach(function(item) {
            if (!(item.requires_transparency && item.product_id)) return;
            var q = parseInt(packQtys[item.id]) || 0;
            if (!q) return;
            var ids = tScanIds[item.product_id] || [];
            created.forEach(function(bx, k) {
              ids.slice(k * q, (k + 1) * q).forEach(function(sid) { assignments.push({ scan_id: sid, box_id: bx.id }); });
            });
          });
          if (assignments.length) {
            api('/fba-shipments/plans/' + planId + '/transparency-scans/assign-box', { method: 'POST', body: JSON.stringify({ assignments: assignments }) }).catch(function() {});
          }
        } catch (e2) { /* audit only — never block the pack */ }
        showToast(copies === 1 ? ('Box ' + nums[0] + ' packed successfully') : (copies + ' boxes packed (#' + nums.join(', #') + ')'), 'success');
        fireConfetti();
        // Print case labels (only when shipment IDs exist — LTL Individual)
        if (hasShipmentIds && created.length) {
          if (created.length === 1) {
            printCaseLabel(created[0].barcode_2d_data, created[0].box_number, { length: l, width: w, height: ht, weight: wt }, created[0].id);
          } else {
            printAllCaseLabelsLocal({ boxIds: created.map(function(b) { return b.id; }) });
          }
        }
        // Reset for next box
        setSelectedItems({});
        setPackQtys({});
        setNumCopies(1);
        setDimForm({ length: '', width: '', height: '', weight: '' });
        setWizStep(0);
        setSaving(false);
        // This box is committed — drop the saved draft so it doesn't resurface.
        api('/fba-shipments/shipments/' + shipmentId + '/box-draft', { method: 'DELETE' }).catch(function() {});
        setRawDraft(null);
        loadShipment();
        if (onRefresh) onRefresh();
      })
      .catch(function(e) { showToast(e.message || 'Failed to pack box', 'error'); setSaving(false); });
  };

  // ─── Toggle item selection ───
  var toggleItem = function(id) {
    setSelectedItems(function(prev) {
      var n = Object.assign({}, prev);
      n[id] = !n[id];
      if (!n[id]) {
        delete n[id];
        // Unchecking also clears any pre-entered quantity for that item.
        setPackQtys(function(q) { var nq = Object.assign({}, q); delete nq[id]; return nq; });
      }
      return n;
    });
  };
  var toggleAll = function() {
    // Scoped to the active packing group's items (= all items when no groups).
    var pool = packScopeItems;
    var allPoolSelected = pool.length > 0 && pool.every(function(i) { return selectedItems[i.id]; });
    if (allPoolSelected) {
      setSelectedItems({});
    } else {
      var n = {};
      pool.forEach(function(i) { n[i.id] = true; });
      setSelectedItems(n);
    }
  };

  // ─── Delete box with confirmation ───
  var deleteBox = function(boxId) {
    var box = boxes.find(function(b) { return b.id === boxId; });
    if (!box) return;
    var bItems = box.items || [];
    var unitCount = bItems.reduce(function(s, i) { return s + i.quantity; }, 0);
    setDeleteBoxConfirm({ boxId: boxId, boxNum: box.box_number, unitCount: unitCount, skuCount: bItems.length });
  };

  var confirmDeleteBox = function() {
    if (!deleteBoxConfirm) return;
    var boxId = deleteBoxConfirm.boxId;
    var boxNum = deleteBoxConfirm.boxNum;
    setDeleteBoxConfirm(null);
    api('/fba-shipments/shipments/' + shipmentId + '/boxes/' + boxId, { method: 'DELETE' })
      .then(function() {
        showToast('Box ' + boxNum + ' deleted', 'success');
        if (activeBoxId === boxId) setActiveBoxId(null);
        loadBoxes();
      })
      .catch(function(e) { showToast(e.message, 'error'); });
  };

  // ─── Duplicate box with confirmation + count ───
  var computeMaxCopies = function(box) {
    var bItems = (box && box.items) || [];
    if (!bItems.length) return 0;
    var maxes = bItems.map(function(bi) {
      if (!bi.quantity || bi.quantity <= 0) return 0;
      var si = items.find(function(it) { return it.id === bi.shipment_item_id; });
      if (!si) return 0;
      var remaining = (si.quantity || 0) - (si.quantity_packed || 0) - (si.quantity_missing || 0);
      if (remaining <= 0) return 0;
      return Math.floor(remaining / bi.quantity);
    });
    return Math.min.apply(null, maxes);
  };

  var openDuplicateBox = function(box) {
    var bItems = box.items || [];
    var unitCount = bItems.reduce(function(s, i) { return s + i.quantity; }, 0);
    var maxCopies = computeMaxCopies(box);
    setDuplicateBoxModal({ boxId: box.id, boxNum: box.box_number, unitCount: unitCount, skuCount: bItems.length, maxCopies: maxCopies });
    setDuplicateCount(Math.max(1, Math.min(1, maxCopies)));
  };

  var confirmDuplicateBox = function() {
    if (!duplicateBoxModal) return;
    var max = duplicateBoxModal.maxCopies;
    var n = parseInt(duplicateCount, 10) || 0;
    if (n < 1 || n > max) return;
    var boxNum = duplicateBoxModal.boxNum;
    var boxId = duplicateBoxModal.boxId;
    setDuplicateSaving(true);
    api('/fba-shipments/shipments/' + shipmentId + '/boxes/' + boxId + '/duplicate', { method: 'POST', body: JSON.stringify({ count: n }) })
      .then(function() {
        showToast('Box ' + boxNum + (n === 1 ? ' duplicated' : ' duplicated (' + n + ' copies)'), 'success');
        setDuplicateBoxModal(null);
        loadBoxes();
      })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setDuplicateSaving(false); });
  };

  // ─── Edit box modal helpers ───
  var enrichBoxItem = function(bi) {
    // Cross-reference with shipment items for richer data
    var si = items.find(function(i) { return i.product_id === bi.product_id; }) || {};
    var thumbUrl = (si.images && si.images.length) ? si.images[0] : si.image_1_url || null;
    return {
      id: bi.id, fnsku: bi.fnsku || bi.msku || bi.product_sku || '', msku: bi.msku || si.msku || si.product_sku || '',
      vendor_title: bi.vendor_title || si.vendor_title || '', vendor_sku: si.vendor_sku || '',
      barcode: si.barcode || '', asin: si.asin || '', vendor_name: si.vendor_name || '',
      quantity: bi.quantity, shipment_item_id: bi.shipment_item_id, product_id: bi.product_id,
      _original_qty: bi.quantity, thumbUrl: thumbUrl, _isNew: false
    };
  };

  var openEditBox = function(box) {
    // Do NOT unseal on open — opening to view must never mutate the box. A sealed box is
    // unsealed lazily in saveEditBox, and ONLY when there are real changes, then re-sealed.
    // (Previously open→unseal + cancel/save→seal+mark-printed spammed the activity log with
    // phantom unsealed/sealed/printed entries even when nothing was edited.)
    var origDims = { length: box.length || '', width: box.width || '', height: box.height || '', weight: box.weight || '' };
    setEditBox({
      id: box.id, box_number: box.box_number,
      items: (box.items || []).map(enrichBoxItem),
      dims: Object.assign({}, origDims), _origDims: origDims,
      wasSealed: !!box.is_sealed, wasStatus: box.box_status || 'created', removed: []
    });
    setEditBoxAddOpen(false);
    setEditBoxSearch('');
    setEditBoxItemSearch('');
  };

  var editBoxRemoveItem = function(idx) {
    setEditBox(function(prev) {
      var item = prev.items[idx];
      var removed = prev.removed.slice();
      if (item.id && !item._isNew) removed.push({ id: item.id, item: item, idx: idx });
      return Object.assign({}, prev, { items: prev.items.filter(function(_, i) { return i !== idx; }), removed: removed });
    });
  };

  var editBoxUndoRemove = function(rIdx) {
    setEditBox(function(prev) {
      var entry = prev.removed[rIdx];
      var newRemoved = prev.removed.filter(function(_, i) { return i !== rIdx; });
      var newItems = prev.items.slice();
      // Insert back at original position or end
      var insertAt = Math.min(entry.idx, newItems.length);
      newItems.splice(insertAt, 0, entry.item);
      return Object.assign({}, prev, { items: newItems, removed: newRemoved });
    });
  };

  var editBoxAddItem = function(planItem) {
    var si = planItem;
    var thumbUrl = (si.images && si.images.length) ? si.images[0] : si.image_1_url || null;
    setEditBox(function(prev) {
      // Check if already in box
      if (prev.items.some(function(i) { return i.product_id === si.product_id; })) return prev;
      var newItem = {
        id: null, fnsku: si.fnsku || '', msku: si.msku || si.product_sku || '',
        vendor_title: si.vendor_title || '', vendor_sku: si.vendor_sku || '',
        barcode: si.barcode || '', asin: si.asin || '', vendor_name: si.vendor_name || '',
        quantity: 1, shipment_item_id: si.id, product_id: si.product_id,
        _original_qty: 0, thumbUrl: thumbUrl, _isNew: true
      };
      return Object.assign({}, prev, { items: prev.items.concat([newItem]) });
    });
    setEditBoxAddOpen(false);
  };

  var saveEditBox = function() {
    if (!editBox) return;
    var boxId = editBox.id;

    // Only hit the server when something actually changed — otherwise a sealed box would be
    // unsealed+resealed+reprinted for nothing (the phantom activity-log entries).
    var dimsChanged = ['length', 'width', 'height', 'weight'].some(function(k) {
      return (parseFloat(editBox.dims[k]) || null) !== (parseFloat(editBox._origDims[k]) || null);
    });
    var itemsChanged = editBox.removed.length > 0 || editBox.items.some(function(it) {
      return (it._isNew && it.quantity > 0) || (it.id && !it._isNew && it.quantity !== it._original_qty);
    });
    if (!dimsChanged && !itemsChanged) { setEditBox(null); return; }

    setEditBoxSaving(true);
    var chain = Promise.resolve();

    // 0. A sealed box must be unsealed before its items/dims can be modified.
    if (editBox.wasSealed) {
      chain = chain.then(function() {
        return api('/fba-shipments/boxes/' + boxId + '/unseal', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      });
    }

    // 1. Delete removed items
    editBox.removed.forEach(function(entry) {
      chain = chain.then(function() {
        return api('/fba-shipments/boxes/' + boxId + '/items/' + entry.id, { method: 'DELETE' });
      });
    });

    // 2. Add new items via scan
    editBox.items.forEach(function(item) {
      if (item._isNew && item.quantity > 0) {
        var sku = item.msku || item.fnsku || item.barcode;
        var want = parseInt(item.quantity, 10) || 0;
        // review M7 #89: one bulk-pack per new item (was one /scan round-trip per unit — qty 200 =
        // 200 sequential requests, a mid-chain failure left the box part-filled). bulk-pack clamps
        // to what is still packable, so surface a shortfall instead of silently accepting it.
        chain = chain.then(function() {
          return api('/fba-shipments/boxes/' + boxId + '/bulk-pack', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ barcode: sku, quantity: want }) })
            .then(function(res) {
              // _isNew = not yet in this box, so boxItem.quantity is exactly what this call packed
              if (res && res.boxItem && res.boxItem.quantity != null && res.boxItem.quantity < want) showToast(sku + ': only ' + res.boxItem.quantity + ' of ' + want + ' could be packed (' + (res.remaining || 0) + ' remaining)', 'warning');
              return res;
            });
        });
      }
    });

    // 3. Update changed quantities on existing items
    editBox.items.forEach(function(item) {
      if (item.id && !item._isNew && item.quantity !== item._original_qty) {
        chain = chain.then(function() {
          return api('/fba-shipments/boxes/' + boxId + '/items/' + item.id, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ quantity: item.quantity }) });
        });
      }
    });

    // 4. Update dimensions (only when they changed)
    if (dimsChanged) {
      chain = chain.then(function() {
        var d = editBox.dims;
        return api('/fba-shipments/shipments/' + shipmentId + '/boxes/' + boxId, { method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ length: parseFloat(d.length) || null, width: parseFloat(d.width) || null, height: parseFloat(d.height) || null, weight: parseFloat(d.weight) || null }) });
      });
    }

    // 5. Re-seal if it was sealed before, and restore original status
    if (editBox.wasSealed) {
      chain = chain.then(function() {
        return api('/fba-shipments/boxes/' + boxId + '/seal', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      });
      if (editBox.wasStatus === 'printed') {
        chain = chain.then(function() {
          return api('/fba-shipments/boxes/' + boxId + '/mark-printed', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
        });
      }
    }

    chain.then(function() {
      showToast('Box ' + editBox.box_number + ' updated', 'success');
      setEditBox(null); setEditBoxSaving(false); loadShipment(); if (onRefresh) onRefresh();
    }).catch(function(e) { showToast(e.message, 'error'); setEditBoxSaving(false); });
  };

  var cancelEditBox = function() {
    // No API calls — open no longer unseals, so there's nothing to restore on cancel.
    setEditBox(null);
  };

  if (!shipment) return h('div', { style: { color: C.textMuted, padding: 20 } }, 'Loading...');

  // ─── Step indicator ───
  var stepNames = isCasePacked ? ['Overview', 'Select SKU', 'Case Config', 'Review & Generate'] : ['Overview', 'Select Items', 'Verify & Qty', 'Dimensions & Finish'];
  var stepBar = h('div', { style: { display: 'flex', gap: 0, marginBottom: 20 } },
    stepNames.map(function(name, idx) {
      var isActive = wizStep === idx;
      var isDone = wizStep > idx;
      var color = isActive ? C.accent : isDone ? C.success : C.textDim;
      var canClick = idx <= wizStep && !isActive;
      return h('div', { key: idx, style: { flex: 1, textAlign: 'center', cursor: canClick ? 'pointer' : 'default' }, onClick: canClick ? function() { if (idx === 0 && wizStep > 0) { cancelWizard(); } else { setWizStep(idx); } } : undefined },
        h('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0 } },
          idx > 0 && h('div', { style: { flex: 1, height: 2, background: isDone || isActive ? C.accent : C.border } }),
          h('div', { style: { width: 28, height: 28, borderRadius: '50%', background: isActive ? C.accent : isDone ? C.success : C.surface, border: '2px solid ' + color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: isActive || isDone ? '#fff' : C.textDim, flexShrink: 0 } },
            isDone ? h(LIcon, { name: 'check', size: 14, color: '#fff' }) : String(idx + 1)),
          idx < stepNames.length - 1 && h('div', { style: { flex: 1, height: 2, background: isDone ? C.accent : C.border } })
        ),
        h('div', { style: { fontSize: 10, fontWeight: isActive ? 600 : 400, color: color, marginTop: 4 } }, name)
      );
    })
  );

  // ─── Progress bar ───
  var progressBar = h('div', { style: { marginBottom: 16 } },
    h('div', { style: { display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 } },
      h('span', { style: { color: C.text } }, 'Packing Progress'),
      h('span', { style: { fontWeight: 600, color: pctPacked === 100 ? C.success : C.text } }, totalPacked + ' / ' + totalQty + ' units (' + pctPacked + '%)')
    ),
    h('div', { style: { height: 6, background: C.border, borderRadius: 3 } },
      h('div', { style: { height: '100%', width: pctPacked + '%', background: pctPacked === 100 ? C.success : C.accent, borderRadius: 3, transition: 'width 0.3s' } })
    )
  );

  // Shared "Receive unexpected item" button — surfaced on the overview AND mid-pack (the
  // "Step 2: Select Items" and "Step 3: Verify Items" screens) so a packer who finds an
  // extra/unknown unit can bin it without leaving their in-progress box.
  var receiveUnexpBtn = function(size) {
    return h(Btn, { size: size || 'sm', onClick: function() { setUnexpectedOpen(true); },
      style: { background: 'rgba(245,158,11,0.12)', color: '#F59E0B', border: '1px solid rgba(245,158,11,0.35)' } },
      h(LIcon, { name: 'alert-triangle', size: 14 }), ' Receive unexpected item');
  };

  // ═══════ RENDER ═══════
  return h('div', null,
    progressBar,
    stepBar,

    // ─── ALL STEPS: Left/Right layout ───
    h('div', { style: { display: 'flex', gap: 16, alignItems: 'flex-start' } },
      // LEFT PANEL — wizard steps
      h('div', { style: { flex: 1, minWidth: 0 } },

        // ─── STEP 0: Tab bar (Shipment Items / Errored Items / Unexpected Items) ───
        wizStep === 0 && (function() {
          var erroredCount = unreceivedHazmat.length;
          var missingCount = items.filter(function(i) { return (i.quantity_missing || 0) > 0; }).length;
          var unexpectedCount = unexpectedReceived.length;
          var tabs = [
            { id: 'shipment', label: 'Shipment Items', count: packScopeItems.length, color: C.accent },
            { id: 'errored', label: 'Errored Items', count: erroredCount, color: '#EF4444' },
            { id: 'missing', label: 'Missing Items', count: missingCount, color: '#F87171' },
            { id: 'unexpected', label: 'Unexpected Items', count: unexpectedCount, color: '#F59E0B' },
            { id: 'incomplete', label: 'Incomplete Items', count: packScopeItems.length, color: '#EC4899' },
            { id: 'activity', label: 'Activity Log', count: 0, color: '#8B5CF6' }
          ];
          return h('div', { style: { display: 'flex', gap: 2, marginBottom: 12, borderBottom: '1px solid ' + C.border } },
            tabs.map(function(t) {
              var active = packTab === t.id;
              return h('button', {
                key: t.id,
                onClick: function() { setPackTab(t.id); },
                style: {
                  padding: '8px 14px',
                  fontSize: 14,
                  fontWeight: active ? 700 : 500,
                  color: active ? t.color : C.textMuted,
                  background: 'transparent',
                  border: 'none',
                  borderBottom: active ? '2px solid ' + t.color : '2px solid transparent',
                  marginBottom: -1,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  transition: 'color 0.15s, border-color 0.15s'
                }
              },
                t.label,
                t.count > 0 && h('span', {
                  style: {
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 20,
                    height: 20,
                    padding: '0 6px',
                    borderRadius: 10,
                    background: active ? t.color : t.color + '22',
                    color: active ? '#fff' : t.color,
                    fontSize: 11,
                    fontWeight: 700
                  }
                }, t.count)
              );
            })
          );
        })(),

        // ─── STEP 0: Overview — Items list ───
        wizStep === 0 && packTab === 'shipment' && h(Card, null,
          h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 } },
            h('div', { style: { fontSize: 15, fontWeight: 600 } }, 'Items Remaining to Pack'),
            h('div', { style: { display: 'flex', gap: 8, flexWrap: 'wrap' } },
              toPackSelId && h(Btn, { size: 'sm', onClick: function() { setToPackSelId(null); setBoxFilter(''); setBoxPage(0); },
                style: { background: C.accent + '15', color: C.accent, border: '1px solid ' + C.accent + '55' } },
                h(LIcon, { name: 'x', size: 13 }), ' Clear Selection'),
              receiveUnexpBtn('sm'),
              // No bulk print on Transparency shipments — a Print All would consume
              // serials across every SKU's label pool in one click; per-item only.
              items.length > 0 && !items.some(function(i) { return i.requires_transparency && i.product_id; }) && h(Btn, { size: 'sm', onClick: function() { printFnskuLabels(items); },
                style: { background: 'rgba(167,139,250,0.1)', color: '#8B5CF6', border: '1px solid rgba(167,139,250,0.3)' } },
                h(LIcon, { name: 'printer', size: 14 }), ' Print All FNSKU Labels'),
              packScopeItems.length > 0 && h(Btn, { variant: 'primary', size: 'sm', onClick: function() {
                if (isCasePacked) {
                  // Case-packed: wizard creates boxes on Finish
                  setWizStep(1);
                  setCpSelectedItem(null); setCpUseSaved(true); setCpUnitsPerCase(''); setCpDims({ length: '', width: '', height: '', weight: '' }); setCpNumCases(''); setCpSaveDefault(true);
                  setItemSearch('');
                } else {
                  // Individual packing: defer box creation to Finish — no pre-reservation, so
                  // cancel/refresh can never strand a box_number. If a saved draft exists for this
                  // user+shipment, offer Resume / Start-fresh instead of silently wiping it.
                  // Reconcile against the FULL available set — the draft may belong to another
                  // packing group; the resume handler snaps the active group to match it.
                  var rd = rawDraft && rawDraft.draft ? reconcileDraft(rawDraft.draft, availableItems) : null;
                  if (rd) {
                    setDraftPrompt({ data: rd, updated_at: rawDraft.updated_at });
                  } else {
                    setSelectedItems({}); setPackQtys({});
                    setNumCopies(1);
                    setItemSearch('');
                    setWizStep(1);
                  }
                }
              } },
                h(LIcon, { name: isCasePacked ? 'box' : 'plus', size: 14, style: { marginRight: 2 } }), isCasePacked ? ' Configure Cases' : ' Pack a Box'))
          ),
          packScopeItems.length === 0
            ? h('div', { style: { textAlign: 'center', padding: '20px 0' } },
                h(LIcon, { name: 'check-circle', size: 32, color: C.success }),
                h('div', { style: { fontSize: 14, fontWeight: 600, color: C.success, marginTop: 8 } },
                  packGroups
                    ? 'Group ' + (activeGroupIdx + 1) + (items.some(function(i) { return inActiveGroup(i) && (i.quantity_missing || 0) > 0; }) ? ' resolved!' : ' packed!')
                    : (items.some(function(i) { return (i.quantity_missing || 0) > 0; }) ? 'All items resolved!' : 'All items packed!')))
            : h('div', null,
                // Search bar
                h('div', { style: { marginBottom: 10, position: 'relative' } },
                  h('input', { value: itemSearch, onChange: function(e) { setItemSearch(e.target.value); }, placeholder: 'Search by SKU, FNSKU, barcode, product name, vendor, PO#...',
                    style: { width: '100%', padding: '8px 34px 8px 12px', fontSize: 13, border: '1px solid ' + C.border, borderRadius: 6, background: C.bg, color: C.text, outline: 'none', boxSizing: 'border-box' } }),
                  itemSearch && h('button', { onClick: function() { setItemSearch(''); }, title: 'Clear search',
                    style: { position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', width: 22, height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', border: 'none', background: C.border, color: C.text, cursor: 'pointer', padding: 0, fontFamily: 'inherit' } },
                    h(LIcon, { name: 'x', size: 12 }))
                ),
                // List capped in its own scroll box so the header (title + action buttons) and the
                // search bar stay visible while scrolling through many items.
                h('div', { style: { maxHeight: 'calc(calc(100vh * var(--invzoom, 1)) - 360px)', minHeight: 220, overflowY: 'auto', overflowX: 'hidden', paddingRight: 16 } },
                (function() {
                  var q = itemSearch.toLowerCase().trim();
                  // Users see PO chips rendered as "PO# 12345"; let them search that way.
                  // For the PO# match, strip an optional "po#"/"po #"/"po " prefix.
                  var qPo = q.replace(/^po\s*#?\s*/, '');
                  var filtered = q ? packScopeItems.filter(function(item) {
                    return (item.msku || '').toLowerCase().indexOf(q) >= 0 ||
                      (item.fnsku || '').toLowerCase().indexOf(q) >= 0 ||
                      (item.barcode || '').toLowerCase().indexOf(q) >= 0 ||
                      (item.vendor_title || '').toLowerCase().indexOf(q) >= 0 ||
                      (item.vendor_name || '').toLowerCase().indexOf(q) >= 0 ||
                      (item.vendor_sku || '').toLowerCase().indexOf(q) >= 0 ||
                      (item.product_sku || '').toLowerCase().indexOf(q) >= 0 ||
                      (item.po_numbers || '').toLowerCase().indexOf(qPo) >= 0 ||
                      (item.asin || '').toLowerCase().indexOf(q) >= 0;
                  }) : packScopeItems;
                  var ps = function(bg, fg) { return { fontSize: 12, fontWeight: 600, padding: '3px 9px', borderRadius: 4, background: bg, color: fg, letterSpacing: '0.03em', whiteSpace: 'nowrap', display: 'inline-block' }; };
                  var copyBadge = function(display, value, bg, fg) {
                    return h('span', {
                      onClick: function(e) {
                        e.stopPropagation();
                        var v = String(value);
                        if (navigator.clipboard && navigator.clipboard.writeText) {
                          navigator.clipboard.writeText(v).then(function() { showToast('Copied ' + v, 'success'); }).catch(function() { showToast('Copy failed', 'error'); });
                        }
                      },
                      onMouseEnter: function(e) { e.currentTarget.style.filter = 'brightness(1.25)'; },
                      onMouseLeave: function(e) { e.currentTarget.style.filter = 'brightness(1)'; },
                      title: 'Click to copy',
                      style: Object.assign({}, ps(bg, fg), { cursor: 'pointer', transition: 'filter 0.15s' })
                    }, display);
                  };
                  // When Shipment Items search comes up empty, check if the query
                  // matches a hazmat/errored item and redirect the user there.
                  var hazmatMatches = q ? hazmatPending.filter(function(hz) {
                    return (hz.msku || '').toLowerCase().indexOf(q) >= 0 ||
                      (hz.fnsku || '').toLowerCase().indexOf(q) >= 0 ||
                      (hz.barcode || '').toLowerCase().indexOf(q) >= 0 ||
                      (hz.vendor_title || '').toLowerCase().indexOf(q) >= 0 ||
                      (hz.vendor_sku || '').toLowerCase().indexOf(q) >= 0 ||
                      (hz.asin || '').toLowerCase().indexOf(q) >= 0;
                  }) : [];
                  if (filtered.length === 0 && hazmatMatches.length > 0) {
                    return h('div', { style: { padding: '16px', textAlign: 'center', background: '#F59E0B10', border: '1px solid #F59E0B44', borderRadius: 8, marginTop: 8 } },
                      h(LIcon, { name: 'alert-triangle', size: 22, color: '#F59E0B' }),
                      h('div', { style: { fontSize: 13, fontWeight: 600, color: '#F59E0B', marginTop: 6 } },
                        hazmatMatches.length === 1
                          ? 'This SKU is in Errored Items (Hazmat Review)'
                          : hazmatMatches.length + ' matches in Errored Items (Hazmat Review)'),
                      h('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 4 } },
                        hazmatMatches.map(function(h2) { return h2.msku; }).slice(0, 4).join(', ') +
                        (hazmatMatches.length > 4 ? ' +' + (hazmatMatches.length - 4) + ' more' : '')),
                      h('button', { onClick: function() { setPackTab('errored'); },
                        onMouseEnter: function(e) { e.currentTarget.style.background = '#F59E0B'; e.currentTarget.style.color = '#fff'; },
                        onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#F59E0B'; },
                        style: { marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 14px', borderRadius: 6, border: '1px solid #F59E0B', background: 'transparent', color: '#F59E0B', cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', transition: 'background 0.15s, color 0.15s' } },
                        'Go to Errored Items ', h(LIcon, { name: 'arrow-right', size: 12 }))
                    );
                  }
                  return filtered.length === 0
                    ? h('div', { style: { textAlign: 'center', padding: '16px 0', color: C.textMuted, fontSize: 13 } }, 'No items match "' + itemSearch + '"')
                    : filtered.map(function(item) {
                      var packed = item.quantity_packed || 0;
                      var remaining = item.quantity - packed;
                      var thumbUrl0 = (item.images && item.images.length) ? item.images[0] : item.image_1_url;
                      var sel = toPackSelId === item.id;
                      // Click selects the SKU (sticky highlight) and filters the boxes
                      // list by SKU — so boxes packed after the click show up too.
                      // Clicking the selected row again clears both.
                      var filterByItem = function(e) {
                        if (e.target.closest('button, a, img')) return;
                        if (sel) { setToPackSelId(null); setBoxFilter(''); setBoxPage(0); return; }
                        var hasBox = completedBoxes.some(function(b) { return (b.items || []).some(function(bi) { return bi.shipment_item_id === item.id; }); });
                        if (!hasBox) showToast('No boxes contain this item yet', 'info');
                        setToPackSelId(item.id);
                        setBoxFilter(item.product_sku || item.msku || '');
                        setBoxPage(0);
                        setRightTab('items');
                      };
                      return h('div', { key: item.id, onClick: filterByItem, title: sel ? 'Click to clear selection' : 'Click to highlight and show this SKU\'s boxes',
                        style: { display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 8px', borderBottom: '1px solid ' + C.border + '33', cursor: 'pointer', background: sel ? C.accent + '0F' : 'transparent', borderLeft: sel ? '3px solid ' + C.accent : '3px solid transparent', borderRadius: sel ? 8 : 0, transition: 'background 0.15s' } },
                        thumbUrl0
                          ? h('img', { src: thumbUrl0, onClick: function(e) { e.stopPropagation(); setLightbox({ images: item.images || [item.image_1_url], index: 0 }); }, style: { width: 80, height: 80, borderRadius: 6, objectFit: 'cover', border: '1px solid ' + C.border, flexShrink: 0, cursor: 'pointer' } })
                          : h('div', { style: { width: 80, height: 80, borderRadius: 6, background: C.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } }, h(LIcon, { name: 'package', size: 32, color: C.textDim })),
                        h('div', { style: { flex: 1, minWidth: 0 } },
                          h('div', { style: { fontSize: 16, fontWeight: 600, marginBottom: 4 } }, item.vendor_title || item.msku),
                          h('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 4 } },
                            (item.product_sku || item.msku) && copyBadge('SKU: ' + (item.product_sku || item.msku), item.product_sku || item.msku, C.accent + '14', C.accent),
                            item.vendor_sku && copyBadge('VSKU: ' + item.vendor_sku, item.vendor_sku, C.accent + '14', C.accent),
                            item.asin && h('a', { href: (plan && plan.marketplace_id === 'A2EUQ1WTGCTBG2' ? 'https://www.amazon.ca/dp/' : 'https://www.amazon.com/dp/') + item.asin, target: '_blank', rel: 'noopener noreferrer', onClick: function(e) { e.stopPropagation(); }, style: Object.assign({}, ps(C.accent + '14', C.accent), { textDecoration: 'none' }) }, 'ASIN: ' + item.asin, h(LIcon, { name: 'external-link', size: 9, style: { marginLeft: 3, verticalAlign: 'middle' } })),
                            item.barcode && copyBadge('BARCODE: ' + item.barcode, item.barcode, C.accent + '14', C.accent),
                            item.fnsku && copyBadge('FNSKU: ' + item.fnsku, item.fnsku, C.accent + '14', C.accent),
                            item.requires_transparency && h(window.TransparencyPill)
                          ),
                          item.vendor_name && h('div', { style: { display: 'flex', gap: 4, marginBottom: 4 } },
                            copyBadge(item.vendor_name, item.vendor_name, '#F59E0B' + '18', '#B45309')
                          ),
                          item.po_numbers && h('div', { style: { display: 'flex', gap: 4, marginBottom: 3 } },
                            copyBadge('PO# ' + item.po_numbers, item.po_numbers, '#10B981' + '18', '#047857')
                          ),
                          item.prep_instructions
                            ? h('div', { style: { fontSize: 11, padding: '4px 8px', borderRadius: 4, background: '#FBBF24' + '15', border: '1px solid #FBBF24' + '40', color: '#B45309', marginTop: 3, display: 'inline-block' } },
                                h(LIcon, { name: 'alert-triangle', size: 12, style: { marginRight: 4, verticalAlign: 'middle' } }), 'Prep: ' + item.prep_instructions)
                            : h('div', { style: { fontSize: 11, color: C.textDim, marginTop: 3, fontStyle: 'italic' } }, 'No prep instructions')
                        ),
                        h('div', { style: { textAlign: 'right', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5 } },
                          h('div', { style: { fontSize: 16, fontWeight: 700, color: C.accent } }, remaining + ' remaining'),
                          packed > 0 && h('div', { style: { fontSize: 13, color: C.textMuted } }, packed + ' of ' + item.quantity + ' packed'),
                          h('button', { onClick: function(e) { e.stopPropagation(); setMissingConfirm(item); },
                            onMouseEnter: function(e) { e.currentTarget.style.background = 'rgba(248,113,113,0.22)'; e.currentTarget.style.borderColor = '#F87171'; },
                            onMouseLeave: function(e) { e.currentTarget.style.background = 'rgba(248,113,113,0.08)'; e.currentTarget.style.borderColor = '#F8717144'; },
                            style: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 9px', borderRadius: 4, border: '1px solid #F8717144', background: 'rgba(248,113,113,0.08)', color: '#F87171', cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', transition: 'background 0.15s, border-color 0.15s' } },
                            h(LIcon, { name: 'alert-triangle', size: 13 }), 'Mark Missing'),
                          h('button', { onClick: function(e) { e.stopPropagation(); printFnskuLabels([item]); },
                            onMouseEnter: function(e) { e.currentTarget.style.background = 'rgba(167,139,250,0.18)'; },
                            onMouseLeave: function(e) { e.currentTarget.style.background = 'rgba(167,139,250,0.1)'; },
                            style: { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 11px', borderRadius: 6, border: '1px solid rgba(167,139,250,0.3)', background: 'rgba(167,139,250,0.1)', color: '#8B5CF6', cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', letterSpacing: '0.02em', transition: 'background 0.15s' } },
                            h(LIcon, { name: 'printer', size: 14 }), (item.requires_transparency && item.product_id) ? 'Print FNSKU + Transparency' : 'Print FNSKU Label')
                        )
                      );
                    });
                })())
              )
        ),

        // ─── Errored tab search ───
        wizStep === 0 && packTab === 'errored' && hazmatPending.length > 0 &&
          packSearchBar(erroredSearch, setErroredSearch, 'Search errored items by SKU, FNSKU, barcode, product name, vendor, PO#, reason...'),

        // ─── Missing tab search ───
        wizStep === 0 && packTab === 'missing' && items.some(function(i) { return (i.quantity_missing || 0) > 0; }) &&
          packSearchBar(missingSearch, setMissingSearch, 'Search missing items by SKU, FNSKU, barcode, product name, vendor, PO#...'),

        // ─── Missing Items section (Missing tab) ───
        wizStep === 0 && packTab === 'missing' && (function() {
          var eq = missingSearch.toLowerCase().trim();
          var eqPo = eq.replace(/^po\s*#?\s*/, ''); // let users search "PO# 12345" the way the chip reads
          var missingItems = items.filter(function(i) { return (i.quantity_missing || 0) > 0; })
            .filter(function(i) { return !eq || (i.msku || '').toLowerCase().indexOf(eq) >= 0 || (i.fnsku || '').toLowerCase().indexOf(eq) >= 0 || (i.barcode || '').toLowerCase().indexOf(eq) >= 0 || (i.vendor_title || '').toLowerCase().indexOf(eq) >= 0 || (i.vendor_name || '').toLowerCase().indexOf(eq) >= 0 || (i.vendor_sku || '').toLowerCase().indexOf(eq) >= 0 || (i.product_sku || '').toLowerCase().indexOf(eq) >= 0 || (i.po_numbers || '').toLowerCase().indexOf(eqPo) >= 0 || (i.asin || '').toLowerCase().indexOf(eq) >= 0; })
            .sort(function(a, b) { return (a.msku || '').localeCompare(b.msku || ''); });
          if (!missingItems.length) return null;
          var ps = function(bg, fg) { return { fontSize: 12, fontWeight: 600, padding: '3px 9px', borderRadius: 4, background: bg, color: fg, letterSpacing: '0.03em', whiteSpace: 'nowrap', display: 'inline-block' }; };
          var copyBadge = function(display, value, bg, fg) {
            return h('span', {
              onClick: function(e) { e.stopPropagation(); var v = String(value); if (navigator.clipboard && navigator.clipboard.writeText) { navigator.clipboard.writeText(v).then(function() { showToast('Copied ' + v, 'success'); }).catch(function() { showToast('Copy failed', 'error'); }); } },
              onMouseEnter: function(e) { e.currentTarget.style.filter = 'brightness(1.25)'; },
              onMouseLeave: function(e) { e.currentTarget.style.filter = 'brightness(1)'; },
              title: 'Click to copy',
              style: Object.assign({}, ps(bg, fg), { cursor: 'pointer', transition: 'filter 0.15s' })
            }, display);
          };
          return h(Card, { style: { marginTop: 12 } },
            h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 } },
              h('div', { style: { fontSize: 14, fontWeight: 600, color: '#F87171' } },
                h(LIcon, { name: 'alert-triangle', size: 15, color: '#F87171' }), ' Missing Items (' + missingItems.length + ')')
            ),
            missingItems.map(function(item) {
              var mImgs = (item.images && item.images.length) ? item.images : (item.image_1_url ? [item.image_1_url] : []);
              return h('div', { key: 'missing-' + item.id, style: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid ' + C.border + '33' } },
                mImgs.length > 0
                  ? h('img', { src: mImgs[0],
                      onClick: function() { setLightbox({ images: mImgs, index: 0 }); },
                      title: mImgs.length > 1 ? 'Click to view ' + mImgs.length + ' images' : 'Click to enlarge',
                      style: { width: 64, height: 64, borderRadius: 4, objectFit: 'cover', flexShrink: 0, border: '1px solid ' + C.border, cursor: 'pointer' } })
                  : h('div', { style: { width: 64, height: 64, borderRadius: 4, background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } }, h(LIcon, { name: 'package', size: 26, color: C.textDim })),
                h('div', { style: { flex: 1, minWidth: 0 } },
                  h('div', { style: { fontSize: 16, fontWeight: 600, marginBottom: 4 } }, item.vendor_title || item.msku),
                  h('div', { style: { display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 4 } },
                    (item.product_sku || item.msku) && copyBadge('SKU: ' + (item.product_sku || item.msku), item.product_sku || item.msku, C.accent + '14', C.accent),
                    item.vendor_sku && copyBadge('VSKU: ' + item.vendor_sku, item.vendor_sku, C.accent + '14', C.accent),
                    item.barcode && copyBadge('UPC: ' + item.barcode, item.barcode, C.accent + '14', C.accent),
                    item.fnsku && copyBadge('FNSKU: ' + item.fnsku, item.fnsku, C.accent + '14', C.accent)
                  ),
                  h('div', { style: { fontSize: 13, color: C.textMuted } },
                    (item.quantity_missing || 0) + ' missing' +
                    ((item.quantity_packed || 0) > 0 ? ', ' + (item.quantity_packed || 0) + ' packed' : '') +
                    ' of ' + item.quantity + ' total')
                ),
                h('button', { onClick: function() { unmarkMissing(item); },
                  onMouseEnter: function(e) { e.currentTarget.style.background = C.accent + '14'; e.currentTarget.style.borderColor = C.accent; e.currentTarget.style.color = C.accent; },
                  onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.textMuted; },
                  style: { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 11px', borderRadius: 5, border: '1px solid ' + C.border, background: 'transparent', color: C.textMuted, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', transition: 'background 0.15s, border-color 0.15s, color 0.15s' } },
                  h(LIcon, { name: 'rotate-ccw', size: 13 }), 'Undo')
              );
            })
          );
        })(),

        // ─── Hazmat — Receive to Bin section (Errored tab) ───
        // Surfaces SKUs that Amazon rejected at plan creation (auto-stripped).
        // User enters a bin per SKU → POST to receive, which creates an
        // is_unexpected=true line on the matched PO with "Hazmat Review" note.
        wizStep === 0 && packTab === 'errored' && hazmatPending.length > 0 && (function() {
          var eq = erroredSearch.toLowerCase().trim();
          var eqPo = eq.replace(/^po\s*#?\s*/, ''); // let users search "PO# 12345" the way the chip reads
          var hzFiltered = hazmatPending.filter(function(it) { return !eq || (it.msku || '').toLowerCase().indexOf(eq) >= 0 || (it.fnsku || '').toLowerCase().indexOf(eq) >= 0 || (it.barcode || '').toLowerCase().indexOf(eq) >= 0 || (it.vendor_title || '').toLowerCase().indexOf(eq) >= 0 || (it.vendor_sku || '').toLowerCase().indexOf(eq) >= 0 || (it.asin || '').toLowerCase().indexOf(eq) >= 0 || ((it.matched_po && it.matched_po.vendor_name) || '').toLowerCase().indexOf(eq) >= 0 || ((it.matched_po && it.matched_po.po_number) || '').toLowerCase().indexOf(eqPo) >= 0 || (it.reason || '').toLowerCase().indexOf(eq) >= 0; });
          if (!hzFiltered.length) return null;
          var ps = function(bg, fg) { return { fontSize: 12, fontWeight: 600, padding: '3px 9px', borderRadius: 4, background: bg, color: fg, letterSpacing: '0.03em', whiteSpace: 'nowrap', display: 'inline-block' }; };
          var copyBadge = function(display, value, bg, fg) {
            return h('span', {
              onClick: function(e) { e.stopPropagation(); var v = String(value); if (navigator.clipboard && navigator.clipboard.writeText) { navigator.clipboard.writeText(v).then(function() { showToast('Copied ' + v, 'success'); }).catch(function() { showToast('Copy failed', 'error'); }); } },
              onMouseEnter: function(e) { e.currentTarget.style.filter = 'brightness(1.25)'; },
              onMouseLeave: function(e) { e.currentTarget.style.filter = 'brightness(1)'; },
              title: 'Click to copy',
              style: Object.assign({}, ps(bg, fg), { cursor: 'pointer', transition: 'filter 0.15s' })
            }, display);
          };
          var REASON_LABELS = {
            hazmat:         { label: 'Hazmat',                       color: '#F59E0B' },
            catalog_lag:    { label: 'Catalog Lag — Retry Later',    color: '#3B82F6' },
            invalid_msku:   { label: 'Invalid MSKU — Relist',        color: '#EF4444' },
            no_fc_capacity: { label: 'No FC Capacity',               color: '#A855F7' },
            brand_registry: { label: 'Brand Registry — Re-List Req.',color: '#EC4899' },
            other:          { label: 'Errored',                      color: '#6B7280' },
          };
          var reasonOf = function(r) { return REASON_LABELS[r] || REASON_LABELS.other; };
          var isVendorDirectPlan = plan && plan.ship_mode === 'vendor_direct';
          // Third resolution: units never arrived — mark missing (PO shortage recorded server-side).
          var hzMissBtn = function(it) {
            return h('button', { key: 'hz-miss',
              onClick: function() { setHzMissingNote(''); setHzMissingConfirm(it); },
              title: 'Units never arrived — mark them missing (records the shortage on the PO)',
              onMouseEnter: function(e) { e.currentTarget.style.background = 'rgba(248,113,113,0.22)'; e.currentTarget.style.borderColor = '#F87171'; },
              onMouseLeave: function(e) { e.currentTarget.style.background = 'rgba(248,113,113,0.08)'; e.currentTarget.style.borderColor = '#F8717144'; },
              style: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 4, border: '1px solid #F8717144', background: 'rgba(248,113,113,0.08)', color: '#F87171', cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', flexShrink: 0, transition: 'background 0.15s, border-color 0.15s' } },
              h(LIcon, { name: 'alert-triangle', size: 13 }), 'Mark Missing');
          };
          return h(Card, { style: { marginTop: 12, borderColor: '#F59E0B55', background: '#F59E0B08' } },
            h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 } },
              h('div', { style: { fontSize: 14, fontWeight: 600, color: '#F59E0B' } },
                h(LIcon, { name: 'alert-triangle', size: 15, color: '#F59E0B' }),
                ' Errored Items — ' + (isVendorDirectPlan ? 'Vendor Direct' : 'Receive to FBM Bin') + ' (' + hzFiltered.length + ')')
            ),
            h('div', { style: { fontSize: 11, color: C.textMuted, marginBottom: 10 } },
              isVendorDirectPlan
                ? 'Amazon rejected these SKUs during plan creation. The units are at the vendor — remove each from the PO (then re-send the revised PO) and it carries to the vendor’s draft PO for re-order once the listing is fixed.'
                : ['Amazon rejected these SKUs during plan creation. They were removed from the FBA plan. ',
                   'Enter a bin to receive each one to the warehouse — it will appear on the PO as an unexpected item.']),
            hzFiltered.slice().sort(function(a, b) { return (a.msku || '').localeCompare(b.msku || ''); }).map(function(it) {
              var receiving = !!hazmatReceiving[it.msku];
              var binVal = hazmatBinInputs[it.msku] || '';
              var isDone = !!it.received;
              return h('div', { key: 'hz-' + it.msku,
                style: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid ' + C.border + '33', opacity: (isDone || it.marked_missing) ? 0.85 : 1 } },
                it.image_url
                  ? h('img', { src: it.image_url,
                      onClick: function() { setLightbox({ images: [it.image_url], index: 0 }); },
                      title: 'Click to enlarge',
                      style: { width: 64, height: 64, borderRadius: 4, objectFit: 'cover', flexShrink: 0, border: '1px solid ' + C.border, cursor: 'pointer' } })
                  : h('div', { style: { width: 64, height: 64, borderRadius: 4, background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } }, h(LIcon, { name: 'package', size: 26, color: C.textDim })),
                h('div', { style: { flex: 1, minWidth: 0 } },
                  h('div', { style: { fontSize: 16, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 4 } }, it.vendor_title || it.msku),
                  h('div', { style: { display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 4 } },
                    copyBadge('SKU: ' + it.msku, it.msku, C.accent + '14', C.accent),
                    it.vendor_sku && copyBadge('VSKU: ' + it.vendor_sku, it.vendor_sku, C.accent + '14', C.accent),
                    it.barcode && copyBadge('UPC: ' + it.barcode, it.barcode, C.accent + '14', C.accent),
                    it.fnsku && copyBadge('FNSKU: ' + it.fnsku, it.fnsku, C.accent + '14', C.accent)
                  ),
                  // Reason badge on its own row so it always sits on the 2nd line (consistent
                  // placement) rather than wrapping inconsistently after the identifier tags.
                  h('div', { style: { display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 4 } },
                    (function() { var r = reasonOf(it.reason || 'hazmat'); return h('span', { style: ps(r.color + '22', r.color) }, r.label); })()
                  ),
                  h('div', { style: { fontSize: 13, color: C.textMuted } },
                    'Qty: ', h('span', { style: { fontWeight: 600, color: C.text } }, it.quantity),
                    it.matched_po && [' · PO ', h('span', { key: 'po', style: { fontFamily: 'inherit' } }, it.matched_po.po_number)],
                    it.matched_po && it.matched_po.vendor_name && [' · ', it.matched_po.vendor_name]
                  )
                ),
                isDone
                  ? h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 } },
                      h('span', {
                        style: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 4, background: C.success + '22', color: C.success, fontSize: 12, fontWeight: 700, letterSpacing: '0.03em' }
                      }, h(LIcon, { name: 'check', size: 12 }), 'Complete'),
                      h('div', { style: { textAlign: 'right', fontSize: 12, color: C.textMuted } },
                        'Bin ', h('span', { style: { fontFamily: 'inherit', fontWeight: 700, color: C.accent, fontSize: 13 } }, it.bin || '—'),
                        it.received_qty && h('div', { style: { fontSize: 11 } }, 'x' + it.received_qty + (it.received_by ? ' · ' + it.received_by : ''))
                      ),
                      h('button', { onClick: function() { undoHazmatPending(it.msku); },
                        title: 'Undo this receive — item returns to Errored list',
                        onMouseEnter: function(e) { e.currentTarget.style.background = C.danger + '14'; e.currentTarget.style.borderColor = C.danger; e.currentTarget.style.color = C.danger; },
                        onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.textMuted; },
                        style: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 4, border: '1px solid ' + C.border, background: 'transparent', color: C.textMuted, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', transition: 'background 0.15s, border-color 0.15s, color 0.15s' } },
                        h(LIcon, { name: 'rotate-ccw', size: 12 }), 'Undo')
                    )
                  : it.removed_from_po
                    ? h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 } },
                        h('span', { style: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 4, background: '#8B5CF622', color: '#8B5CF6', fontSize: 12, fontWeight: 700, letterSpacing: '0.03em' } },
                          h(LIcon, { name: 'corner-up-right', size: 12 }), 'Removed from PO'),
                        it.carried_to_po && h('span', { style: { fontSize: 12, color: C.textMuted } }, '→ draft #' + it.carried_to_po)
                      )
                  : it.marked_missing
                    ? h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 } },
                        h('span', { style: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 4, background: '#F8717122', color: '#F87171', fontSize: 12, fontWeight: 700, letterSpacing: '0.03em' } },
                          h(LIcon, { name: 'alert-triangle', size: 12 }), 'Marked Missing'),
                        h('div', { style: { textAlign: 'right', fontSize: 12, color: C.textMuted } },
                          'x' + (it.quantity || 0) + (it.missing_by ? ' · ' + it.missing_by : ''),
                          it.missing_note && h('div', { style: { fontSize: 11, fontStyle: 'italic' } }, it.missing_note)
                        ),
                        h('button', { onClick: function() { undoErroredMissing(it.msku); },
                          title: 'Undo — item returns to the Errored list and the PO shortage is reverted',
                          onMouseEnter: function(e) { e.currentTarget.style.background = C.danger + '14'; e.currentTarget.style.borderColor = C.danger; e.currentTarget.style.color = C.danger; },
                          onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.textMuted; },
                          style: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 4, border: '1px solid ' + C.border, background: 'transparent', color: C.textMuted, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', transition: 'background 0.15s, border-color 0.15s, color 0.15s' } },
                          h(LIcon, { name: 'rotate-ccw', size: 12 }), 'Undo')
                      )
                  : isVendorDirectPlan
                    ? (function() {
                        var confirming = !!vdRemoveConfirm[it.msku];
                        var removing = !!vdRemoving[it.msku];
                        return [h('button', { key: 'vd-rm',
                          onClick: function() {
                            if (removing) return;
                            if (!confirming) { setVdRemoveConfirm(function(prev) { var n = Object.assign({}, prev); n[it.msku] = true; return n; }); return; }
                            removeErroredFromPo(it.msku);
                          },
                          onMouseLeave: function() { if (confirming && !removing) setVdRemoveConfirm(function(prev) { var n = Object.assign({}, prev); delete n[it.msku]; return n; }); },
                          title: 'Vendor-direct: remove this errored line from the PO (re-send the revised PO to the vendor) and carry it to the vendor’s draft PO',
                          style: { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 4, border: '1px solid ' + (confirming ? '#DC2626' : '#F8717155'), background: confirming ? '#DC2626' : 'rgba(248,113,113,0.10)', color: confirming ? '#fff' : '#F87171', cursor: removing ? 'wait' : 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', flexShrink: 0, transition: 'background 0.15s, border-color 0.15s, color 0.15s' }
                        }, removing ? 'Removing…' : (confirming ? 'Confirm: remove from PO?' : [h(LIcon, { key: 'i', name: 'corner-up-right', size: 12 }), 'Remove from PO → draft'])),
                        hzMissBtn(it)];
                      })()
                  : (function() {
                      // Valid bins are one or more letters followed by one or more digits,
                      // optionally with a dash. Support multi-bin entries split by "/".
                      var binStr = (binVal || '').trim();
                      var hasInput = binStr.length > 0;
                      var binValid = hasInput && binStr.split('/').every(function(p) {
                        return /^[A-Z]+-?\d+$/.test(p.trim());
                      });
                      var invalidReason = !hasInput
                        ? 'Enter a bin location before receiving this item'
                        : !binValid
                          ? 'Invalid bin — must be letter(s) followed by number(s), e.g. A-100'
                          : null;
                      return [
                        h('input', { key: 'bin-in',
                          type: 'text', value: binVal, disabled: receiving,
                          placeholder: 'Bin (e.g. A-100)',
                          title: invalidReason || '',
                          onChange: function(e) {
                            var v = e.target.value.toUpperCase();
                            setHazmatBinInputs(function(prev) { var n = Object.assign({}, prev); n[it.msku] = v; return n; });
                          },
                          onBlur: function(e) {
                            var formatted = window.formatBin ? window.formatBin(e.target.value) : e.target.value;
                            if (formatted) {
                              setHazmatBinInputs(function(prev) { var n = Object.assign({}, prev); n[it.msku] = formatted; return n; });
                            }
                          },
                          onKeyDown: function(e) { if (e.key === 'Enter' && binValid) receiveHazmatPending(it.msku); },
                          style: { width: 140, padding: '6px 8px', fontSize: 13, fontWeight: 600,
                            border: '1px solid ' + (hasInput && !binValid ? C.danger : C.border),
                            borderRadius: 4, background: C.bg, color: C.text, outline: 'none', fontFamily: 'inherit', textAlign: 'center', flexShrink: 0 }
                        }),
                        h('button', { key: 'rcv-btn',
                          onClick: function() { if (binValid) receiveHazmatPending(it.msku); },
                          disabled: receiving || !binValid,
                          title: invalidReason || (receiving ? 'Receiving…' : 'Receive this item to the bin'),
                          onMouseEnter: function(e) { if (!e.currentTarget.disabled) { e.currentTarget.style.background = '#0F8A4B'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(16,185,129,0.35)'; e.currentTarget.style.transform = 'translateY(-1px)'; } },
                          onMouseLeave: function(e) { if (!e.currentTarget.disabled) { e.currentTarget.style.background = C.success; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; } },
                          style: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 4, border: 'none', background: C.success, color: '#fff', cursor: receiving || !binValid ? 'not-allowed' : 'pointer', opacity: receiving || !binValid ? 0.45 : 1, fontSize: 12, fontWeight: 600, fontFamily: 'inherit', transition: 'background 0.15s, box-shadow 0.15s, transform 0.15s, opacity 0.15s', flexShrink: 0 }
                        }, receiving ? 'Receiving…' : [h(LIcon, { key: 'ic', name: 'check', size: 12 }), 'Receive']),
                        hzMissBtn(it)
                      ];
                    })()
              );
            })
          );
        })(),

        // ─── Unexpected tab search ───
        wizStep === 0 && packTab === 'unexpected' && unexpectedReceived.length > 0 &&
          packSearchBar(unexpectedSearch, setUnexpectedSearch, 'Search unexpected items by SKU, barcode, product name, vendor, PO#, bin...'),

        // ─── Received Unexpected Items section (Unexpected tab) ───
        wizStep === 0 && packTab === 'unexpected' && unexpectedReceived.length > 0 && (function() {
          var uq = unexpectedSearch.toLowerCase().trim();
          var uqPo = uq.replace(/^po\s*#?\s*/, ''); // let users search "PO# 12345" the way the chip reads
          var unexpFiltered = unexpectedReceived.filter(function(u) { return !uq || (u.sku || '').toLowerCase().indexOf(uq) >= 0 || (u.vendor_sku || '').toLowerCase().indexOf(uq) >= 0 || (u.barcode || '').toLowerCase().indexOf(uq) >= 0 || (u.vendor_title || '').toLowerCase().indexOf(uq) >= 0 || (u.vendor_name || '').toLowerCase().indexOf(uq) >= 0 || (u.po_number || '').toLowerCase().indexOf(uqPo) >= 0 || (u.bin_location || '').toLowerCase().indexOf(uq) >= 0 || (u.received_by || '').toLowerCase().indexOf(uq) >= 0; });
          if (!unexpFiltered.length) return null;
          var ps = function(bg, fg) { return { fontSize: 12, fontWeight: 600, padding: '3px 9px', borderRadius: 4, background: bg, color: fg, letterSpacing: '0.03em', whiteSpace: 'nowrap', display: 'inline-block' }; };
          var copyBadge = function(display, value, bg, fg) {
            return h('span', {
              onClick: function(e) { e.stopPropagation(); var v = String(value); if (navigator.clipboard && navigator.clipboard.writeText) { navigator.clipboard.writeText(v).then(function() { showToast('Copied ' + v, 'success'); }).catch(function() { showToast('Copy failed', 'error'); }); } },
              onMouseEnter: function(e) { e.currentTarget.style.filter = 'brightness(1.25)'; },
              onMouseLeave: function(e) { e.currentTarget.style.filter = 'brightness(1)'; },
              title: 'Click to copy',
              style: Object.assign({}, ps(bg, fg), { cursor: 'pointer', transition: 'filter 0.15s' })
            }, display);
          };
          return h(Card, { style: { marginTop: 12 } },
            h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 } },
              h('div', { style: { fontSize: 14, fontWeight: 600, color: '#F59E0B' } },
                h(LIcon, { name: 'alert-triangle', size: 15, color: '#F59E0B' }), ' Received Unexpected Items (' + unexpFiltered.length + ')')
            ),
            unexpFiltered.slice().sort(function(a, b) { return (a.sku || '').localeCompare(b.sku || ''); }).map(function(u) {
              var t = new Date(u.received_at);
              var timeStr = t.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' + t.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
              var uImgs = (u.images && u.images.length) ? u.images : (u.image_1_url ? [u.image_1_url] : []);
              return h('div', { key: 'unexp-' + u.log_id, style: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid ' + C.border + '33' } },
                uImgs.length > 0
                  ? h('img', { src: uImgs[0],
                      onClick: function() { setUnexpLightbox({ images: uImgs, index: 0 }); },
                      title: uImgs.length > 1 ? 'Click to view ' + uImgs.length + ' images' : 'Click to enlarge',
                      style: { width: 64, height: 64, borderRadius: 4, objectFit: 'cover', flexShrink: 0, border: '1px solid ' + C.border, cursor: 'pointer' } })
                  : h('div', { style: { width: 64, height: 64, borderRadius: 4, background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } }, h(LIcon, { name: 'package', size: 26, color: C.textDim })),
                h('div', { style: { flex: 1, minWidth: 0 } },
                  h('div', { style: { fontSize: 16, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 4 } }, u.vendor_title || u.sku),
                  h('div', { style: { display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 4 } },
                    u.sku && copyBadge('SKU: ' + u.sku, u.sku, C.accent + '14', C.accent),
                    u.vendor_sku && copyBadge('VSKU: ' + u.vendor_sku, u.vendor_sku, C.accent + '14', C.accent),
                    u.barcode && copyBadge('UPC: ' + u.barcode, u.barcode, C.accent + '14', C.accent)
                  ),
                  h('div', { style: { fontSize: 13, color: C.textMuted } },
                    '+' + u.qty_received + ' → Bin ',
                    h('span', { style: { fontFamily: 'inherit', fontWeight: 600, color: C.accent } }, u.bin_location || '—'),
                    ' · PO ', h('span', { style: { fontFamily: 'inherit' } }, u.po_number),
                    ' · ', u.received_by, ' · ', timeStr
                  ),
                  u.notes && h('div', { style: { fontSize: 13, color: C.textMuted, fontStyle: 'italic', marginTop: 2 } }, 'Notes: ' + u.notes)
                ),
                h('button', { onClick: function() { undoUnexpected(u.log_id); },
                  onMouseEnter: function(e) { e.currentTarget.style.background = C.danger + '14'; e.currentTarget.style.borderColor = C.danger; e.currentTarget.style.color = C.danger; },
                  onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.textMuted; },
                  style: { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 11px', borderRadius: 5, border: '1px solid ' + C.border, background: 'transparent', color: C.textMuted, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', transition: 'background 0.15s, border-color 0.15s, color 0.15s', flexShrink: 0 } },
                  h(LIcon, { name: 'rotate-ccw', size: 13 }), 'Undo')
              );
            })
          );
        })(),

        // ─── Empty states for Errored / Missing / Unexpected tabs ───
        wizStep === 0 && packTab === 'errored' && hazmatPending.length === 0 &&
          h(Card, null,
            h('div', { style: { textAlign: 'center', padding: '30px 0', color: C.textMuted } },
              h(LIcon, { name: 'check-circle', size: 32, color: C.success }),
              h('div', { style: { fontSize: 15, fontWeight: 600, color: C.success, marginTop: 8 } }, 'No errored items'),
              h('div', { style: { fontSize: 13, marginTop: 4 } }, 'SKUs Amazon flagged during plan creation (hazmat, catalog lag, invalid MSKU, brand registry, etc.) will appear here.')
            )
          ),
        wizStep === 0 && packTab === 'missing' && !items.some(function(i) { return (i.quantity_missing || 0) > 0; }) &&
          h(Card, null,
            h('div', { style: { textAlign: 'center', padding: '30px 0', color: C.textMuted } },
              h(LIcon, { name: 'check-circle', size: 32, color: C.success }),
              h('div', { style: { fontSize: 15, fontWeight: 600, color: C.success, marginTop: 8 } }, 'No missing items'),
              h('div', { style: { fontSize: 13, marginTop: 4 } }, 'Items marked missing (from the Shipment or Incomplete tabs) will appear here.')
            )
          ),
        wizStep === 0 && packTab === 'unexpected' && unexpectedReceived.length === 0 &&
          h(Card, null,
            h('div', { style: { textAlign: 'center', padding: '30px 0', color: C.textMuted } },
              h(LIcon, { name: 'package', size: 32, color: C.textDim }),
              h('div', { style: { fontSize: 15, fontWeight: 600, color: C.text, marginTop: 8 } }, 'No unexpected items'),
              h('div', { style: { fontSize: 13, marginTop: 4 } }, 'Items received via "Receive unexpected item" will appear here.')
            )
          ),

        wizStep === 0 && packTab === 'incomplete' && h(Card, null, (function() {
          var incomplete = packScopeItems;
          if (!incomplete.length) {
            return h('div', { style: { textAlign: 'center', padding: '30px 0', color: C.textMuted } },
              h(LIcon, { name: 'check-circle', size: 32, color: C.success }),
              h('div', { style: { fontSize: 14, fontWeight: 600, color: C.success, marginTop: 8 } }, 'No incomplete items'),
              h('div', { style: { fontSize: 12, marginTop: 4 } }, 'Every item in this shipment is either fully packed or already marked missing.'));
          }
          var selectedIds = Object.keys(bulkMissingSelected).filter(function(k) { return bulkMissingSelected[k]; });
          var allChecked = selectedIds.length === incomplete.length;
          var anyChecked = selectedIds.length > 0;
          var unitsAtStake = incomplete.reduce(function(s, it) {
            if (!bulkMissingSelected[it.id]) return s;
            return s + Math.max(0, it.quantity - (it.quantity_packed || 0) - (it.quantity_missing || 0));
          }, 0);
          return h('div', null,
            h('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 } },
              h('div', null,
                h('div', { style: { fontSize: 16, fontWeight: 600, color: C.text } }, 'Incomplete Items'),
                h('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 2 } }, incomplete.length + ' SKU' + (incomplete.length === 1 ? '' : 's') + ' with unpacked units remaining')
              ),
              h(Btn, { variant: 'primary', disabled: !anyChecked,
                onClick: function() {
                  var sel = incomplete.filter(function(it) { return bulkMissingSelected[it.id]; });
                  setBulkMissingConfirm({ items: sel, note: '' });
                },
                style: !anyChecked ? null : { background: '#DC2626', borderColor: '#DC2626' } },
                h(LIcon, { name: 'alert-triangle', size: 14 }), ' Mark Selected as Missing' + (anyChecked ? ' (' + selectedIds.length + ')' : ''))
            ),
            h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid ' + C.border } },
              h('button', { onClick: function() {
                  if (allChecked) { setBulkMissingSelected({}); }
                  else { var n = {}; incomplete.forEach(function(it) { n[it.id] = true; }); setBulkMissingSelected(n); }
                },
                style: { display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit' } },
                h('div', { style: { width: 20, height: 20, borderRadius: 4, border: '2px solid ' + (allChecked ? C.accent : C.textDim), background: allChecked ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' } },
                  allChecked && h(LIcon, { name: 'check', size: 13, color: '#fff' })),
                h('span', { style: { fontSize: 12, fontWeight: 600, color: C.text } }, 'Select All (' + incomplete.length + ')')
              ),
              anyChecked && h('span', { style: { fontSize: 12, color: C.textMuted } }, unitsAtStake + ' unit' + (unitsAtStake === 1 ? '' : 's') + ' will be marked missing')
            ),
            incomplete.map(function(it) {
              var checked = !!bulkMissingSelected[it.id];
              var remaining = Math.max(0, it.quantity - (it.quantity_packed || 0) - (it.quantity_missing || 0));
              var packed = it.quantity_packed || 0;
              var missed = it.quantity_missing || 0;
              var thumb = (it.images && it.images.length) ? it.images[0] : it.image_1_url;
              return h('div', { key: it.id, onClick: function() { setBulkMissingSelected(function(prev) { var n = Object.assign({}, prev); if (n[it.id]) delete n[it.id]; else n[it.id] = true; return n; }); },
                style: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 8px', marginBottom: 4, borderRadius: 6, cursor: 'pointer', background: checked ? '#F87171' + '10' : 'transparent', border: '1px solid ' + (checked ? '#F87171' + '50' : 'transparent'), transition: 'background 0.15s, border-color 0.15s' } },
                h('div', { style: { width: 20, height: 20, minWidth: 20, borderRadius: 4, border: '2px solid ' + (checked ? '#F87171' : C.textDim), background: checked ? '#F87171' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' } },
                  checked && h(LIcon, { name: 'check', size: 13, color: '#fff' })),
                thumb
                  ? h('img', { src: thumb, onClick: function(e) { e.stopPropagation(); setLightbox({ images: it.images || [it.image_1_url], index: 0 }); }, style: { width: 64, height: 64, borderRadius: 6, objectFit: 'cover', border: '1px solid ' + C.border, flexShrink: 0, cursor: 'pointer' } })
                  : h('div', { style: { width: 64, height: 64, borderRadius: 6, background: C.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } }, h(LIcon, { name: 'package', size: 26, color: C.textDim })),
                h('div', { style: { flex: 1, minWidth: 0 } },
                  h('div', { style: { fontSize: 16, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, it.vendor_title || it.msku || '—'),
                  h('div', { style: { fontSize: 13, color: C.textMuted, marginTop: 2 } },
                    (it.product_sku || it.msku) && h('span', null, 'SKU ' + (it.product_sku || it.msku)),
                    it.po_numbers && h('span', null, '  ·  PO# ' + it.po_numbers)
                  )
                ),
                h('div', { style: { textAlign: 'right', flexShrink: 0 } },
                  h('div', { style: { fontSize: 16, fontWeight: 700, color: '#F87171' } }, remaining + ' to receive'),
                  h('div', { style: { fontSize: 13, color: C.textMuted, marginTop: 2 } }, packed + ' of ' + it.quantity + ' packed' + (missed > 0 ? ' · ' + missed + ' missing' : ''))
                )
              );
            })
          );
        })()),

        wizStep === 0 && packTab === 'activity' && h(Card, null,
          h('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 } },
            h('div', { style: { fontSize: 16, fontWeight: 600, color: C.text } }, 'Shipment Activity Log'),
            h('div', { style: { display: 'flex', alignItems: 'center', gap: 6 } },
              h('button', { onClick: loadActivity, disabled: activityLoading,
                style: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 5, border: '1px solid ' + C.border, background: 'transparent', color: C.text, cursor: activityLoading ? 'default' : 'pointer', fontSize: 12, fontFamily: 'inherit' } },
                h(LIcon, { name: 'refresh-cw', size: 12, spin: activityLoading }), 'Refresh'),
              h('a', { href: '/api/fba-shipments/shipments/' + shipmentId + '/activity.xlsx', download: '',
                title: 'Download activity log as Excel',
                style: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 5, border: '1px solid ' + C.border, background: 'transparent', color: '#10B981', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit', textDecoration: 'none' } },
                h(LIcon, { name: 'file-spreadsheet', size: 14 }), 'Excel')
            )
          ),
          activityLog.length > 0 && packSearchBar(activitySearch, setActivitySearch, 'Search activity by action, details, user...'),
          activityLoading && activityLog.length === 0
            ? h('div', { style: { textAlign: 'center', padding: '30px 0', color: C.textMuted, fontSize: 13 } }, 'Loading…')
            : activityLog.length === 0
              ? h('div', { style: { textAlign: 'center', padding: '30px 0', color: C.textMuted } },
                  h(LIcon, { name: 'activity', size: 32, color: C.textDim }),
                  h('div', { style: { fontSize: 14, fontWeight: 600, color: C.text, marginTop: 8 } }, 'No activity yet'),
                  h('div', { style: { fontSize: 12, marginTop: 4 } }, 'Box, item, and shipment events for this shipment will appear here.'))
              : (function() {
                  var aq = activitySearch.toLowerCase().trim();
                  var shown = !aq ? activityLog : activityLog.filter(function(a) {
                    var act = (a.action || '').toLowerCase();
                    // Match the raw action ("fba_item_packed") and the humanized badge text ("item packed").
                    return act.indexOf(aq) !== -1 ||
                      act.replace(/^fba_/, '').replace(/_/g, ' ').indexOf(aq) !== -1 ||
                      (a.details || '').toLowerCase().indexOf(aq) !== -1 ||
                      (a.username || '').toLowerCase().indexOf(aq) !== -1;
                  });
                  if (shown.length === 0) return h('div', { style: { textAlign: 'center', padding: '30px 0', color: C.textMuted, fontSize: 13 } }, 'No activity matches "' + activitySearch + '"');
                  var byDate = {};
                  shown.forEach(function(a) {
                    var d = new Date(a.created_at);
                    var key = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
                    (byDate[key] = byDate[key] || []).push(a);
                  });
                  var keys = Object.keys(byDate).sort().reverse();
                  return h('div', null, keys.map(function(k) {
                    var parts = k.split('-');
                    var headerDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
                    return h('div', { key: k, style: { marginBottom: 16 } },
                      h('div', { style: { fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8, paddingBottom: 4, borderBottom: '1px solid ' + C.border } },
                        headerDate.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })),
                      byDate[k].map(function(a) {
                        var act = (a.action || '');
                        var color = act.indexOf('delete') >= 0 ? '#F87171'
                          : act.indexOf('missing') >= 0 ? '#F59E0B'
                          : act.indexOf('duplicat') >= 0 ? '#06B6D4'
                          : (act.indexOf('packed') >= 0 || act.indexOf('received') >= 0) ? C.success
                          : '#8B5CF6';
                        var d = new Date(a.created_at);
                        var hhmm = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
                        return h('div', { key: a.id, style: { display: 'flex', gap: 10, padding: '10px 0', borderBottom: '1px solid ' + C.border + '33' } },
                          h('div', { style: { width: 8, height: 8, borderRadius: '50%', background: color, marginTop: 6, flexShrink: 0 } }),
                          h('div', { style: { flex: 1, minWidth: 0 } },
                            h('div', { style: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 } },
                              h('span', { style: { fontSize: 10, fontWeight: 700, color: color, padding: '2px 7px', borderRadius: 4, background: color + '14', letterSpacing: '0.04em', textTransform: 'uppercase' } }, act.replace(/^fba_/, '').replace(/_/g, ' ')),
                              h('span', { style: { fontSize: 11, color: C.textMuted, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }, title: d.toLocaleString() }, hhmm)
                            ),
                            h('div', { style: { fontSize: 13, color: C.text, marginTop: 4 } }, a.details || '—'),
                            a.username && h('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 2 } }, 'by ' + a.username)
                          )
                        );
                      })
                    );
                  }));
                })()
        ),

        // ─── STEP 1 (Case Packed): Select single SKU ───
        wizStep === 1 && isCasePacked && (function() {
          // Compute auto-pack eligibility: saved case data + remaining evenly divisible by
          // case_qty. Transparency-flagged SKUs are NEVER eligible — auto-pack seals boxes
          // in one click, which would ship units without a label scan.
          var autoPackInfo = fbaAutoPackInfo(availableItems); // shared with the individual flow (2026-10-22)
          var eligibleAutoPack = autoPackInfo.filter(function(a) { return a.eligible; });
          var totalEligibleCases = eligibleAutoPack.reduce(function(s, a) { return s + a.cases; }, 0);
          var totalEligibleUnits = eligibleAutoPack.reduce(function(s, a) { return s + a.remaining; }, 0);

          var autoPackAllSaved = function() { runAutoPack(eligibleAutoPack, totalEligibleCases); };

          var partialCount = autoPackInfo.filter(function(a) { return a.reason === 'partial'; }).length;
          var noSavedSkus = autoPackInfo.filter(function(a) { return a.reason === 'no_saved'; }).map(function(a) { return a.item.msku || a.item.product_sku; });
          var noSavedCount = noSavedSkus.length;
          var transparencySkus = autoPackInfo.filter(function(a) { return a.reason === 'transparency'; }).map(function(a) { return a.item.msku || a.item.product_sku; });
          var bannerTooltip = 'Eligible: ' + eligibleAutoPack.length + ' SKU(s), ' + totalEligibleCases + ' case(s), ' + totalEligibleUnits + ' unit(s) using saved case dims/qty.'
            + (partialCount ? ' Skipped ' + partialCount + ' SKU(s) (remaining not evenly divisible by case qty).' : '')
            + (noSavedCount ? ' Skipped ' + noSavedCount + ' SKU(s) without saved case packs.' : '')
            + (transparencySkus.length ? ' Skipped ' + transparencySkus.length + ' Transparency SKU(s) — label scanning required.' : '');

          return h(Card, null,
          eligibleAutoPack.length > 0 && h('div', { style: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, border: '1px solid ' + C.success + '40', background: C.success + '10', marginBottom: 12 } },
            h(LIcon, { name: 'zap', size: 16, color: C.success }),
            h('div', { style: { flex: 1, fontSize: 12 } },
              h('div', { style: { fontWeight: 600, color: C.text } }, 'Saved case packs detected'),
              h('div', { style: { color: C.textMuted, marginTop: 2 } }, bannerTooltip)
            ),
            h(Btn, { variant: 'primary', disabled: cpGenerating, onClick: autoPackAllSaved },
              cpGenerating
                ? 'Auto-packing… (' + cpProgress.current + '/' + cpProgress.total + ')'
                : h(React.Fragment, null, h(LIcon, { name: 'zap', size: 13 }), ' Auto-Pack ' + eligibleAutoPack.length + ' SKU' + (eligibleAutoPack.length !== 1 ? 's' : ''))
            )
          ),
          // Items with no saved case pack — shown even when nothing is auto-pack eligible
          // (the green banner above hides entirely in that case).
          noSavedSkus.length > 0 && h('div', { style: { display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 12px', borderRadius: 8, border: '1px solid ' + C.warning + '40', background: C.warning + '10', marginBottom: 12, fontSize: 12 } },
            h(LIcon, { name: 'alert-triangle', size: 14, color: C.warning, style: { flexShrink: 0, marginTop: 1 } }),
            h('div', null,
              h('span', { style: { fontWeight: 600, color: C.warning } }, 'No saved case pack: '),
              h('span', { style: { color: C.textMuted } }, noSavedSkus.join(', '))
            )
          ),
          // Transparency SKUs are excluded from Auto-Pack — every unit must pass the scan
          // station, so they go through the manual per-SKU flow below.
          transparencySkus.length > 0 && h('div', { style: { display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 12px', borderRadius: 8, border: '1px solid rgba(167,139,250,0.4)', background: 'rgba(167,139,250,0.08)', marginBottom: 12, fontSize: 12 } },
            h(LIcon, { name: 'scan-line', size: 14, color: '#A78BFA', style: { flexShrink: 0, marginTop: 1 } }),
            h('div', null,
              h('span', { style: { fontWeight: 600, color: '#A78BFA' } }, 'Transparency scanning required: '),
              h('span', { style: { color: C.textMuted } }, transparencySkus.join(', ') + ' — excluded from Auto-Pack. Select the SKU below; labels are printed and scanned on the Review step.')
            )
          ),
          h('div', { style: { fontSize: 14, fontWeight: 600, marginBottom: 4 } }, 'Step 2: Select SKU'),
          h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 10 } }, 'Choose one SKU to configure cases for. Each SKU is packed independently.'),
          h('div', { style: { display: 'flex', justifyContent: 'space-between', marginBottom: 12 } },
            h('div', { style: { display: 'flex', gap: 8 } },
              h(Btn, { onClick: cancelWizard }, h(LIcon, { name: 'arrow-left', size: 14 }), ' Back'),
              h(Btn, { onClick: cancelWizard, style: { background: 'rgba(248,113,113,0.1)', color: '#F87171', border: '1px solid rgba(248,113,113,0.3)' } }, h(LIcon, { name: 'x', size: 14 }), ' Cancel'),
              h(Btn, { variant: 'primary', disabled: !cpSelectedItem, onClick: function() {
                var item = availableItems.find(function(i) { return i.id === cpSelectedItem; });
                if (item && item.case_qty > 0 && item.case_length > 0) {
                  setCpUseSaved(true);
                  setCpUnitsPerCase(String(item.case_qty));
                  setCpDims({ length: String(item.case_length || ''), width: String(item.case_width || ''), height: String(item.case_height || ''), weight: String(item.case_weight || '') });
                } else {
                  setCpUseSaved(false);
                  setCpUnitsPerCase('');
                  setCpDims({ length: '', width: '', height: '', weight: '' });
                }
                setCpNumCases('');
                setCpSaveDefault(true);
                setItemSearch('');
                setWizStep(2);
              } }, 'Next: Case Config ', h(LIcon, { name: 'arrow-right', size: 14 }))
            )
          ),
          h('div', { style: { marginBottom: 10, position: 'relative' } },
            h('input', { value: itemSearch, onChange: function(e) { setItemSearch(e.target.value); }, placeholder: 'Search by SKU, FNSKU, barcode, product name...',
              style: { width: '100%', padding: '8px 34px 8px 12px', fontSize: 12, border: '1px solid ' + C.border, borderRadius: 6, background: C.bg, color: C.text, outline: 'none', boxSizing: 'border-box' } }),
            itemSearch && h('button', { onClick: function() { setItemSearch(''); }, title: 'Clear search',
              style: { position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', width: 22, height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', border: 'none', background: C.border, color: C.text, cursor: 'pointer', padding: 0, fontFamily: 'inherit' } },
              h(LIcon, { name: 'x', size: 12 }))
          ),
          (function() {
            var q = itemSearch.toLowerCase().trim();
            var filtered = q ? availableItems.filter(function(item) {
              return (item.msku || '').toLowerCase().indexOf(q) >= 0 ||
                (item.fnsku || '').toLowerCase().indexOf(q) >= 0 ||
                (item.barcode || '').toLowerCase().indexOf(q) >= 0 ||
                (item.vendor_title || '').toLowerCase().indexOf(q) >= 0 ||
                (item.vendor_sku || '').toLowerCase().indexOf(q) >= 0 ||
                (item.product_sku || '').toLowerCase().indexOf(q) >= 0;
            }) : availableItems;
            var ps = function(bg, fg) { return { fontSize: 12, fontWeight: 600, padding: '3px 9px', borderRadius: 4, background: bg, color: fg, letterSpacing: '0.03em', whiteSpace: 'nowrap', display: 'inline-block' }; };
            return filtered.length === 0
              ? h('div', { style: { textAlign: 'center', padding: '16px 0', color: C.textMuted, fontSize: 12 } }, 'No items match "' + itemSearch + '"')
              : filtered.map(function(item) {
                  var selected = cpSelectedItem === item.id;
                  var remaining = item.quantity - (item.quantity_packed || 0) - (item.quantity_missing || 0);
                  var hasSaved = item.case_qty > 0 && item.case_length > 0;
                  return h('div', { key: item.id, onClick: function() { setCpSelectedItem(item.id); },
                    style: { display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 8px', marginBottom: 4, borderRadius: 8, cursor: 'pointer', background: selected ? C.accent + '08' : 'transparent', border: '1px solid ' + (selected ? C.accent + '40' : 'transparent'), transition: 'all 0.15s' } },
                    h('div', { style: { width: 20, height: 20, minWidth: 20, borderRadius: '50%', border: '2px solid ' + (selected ? C.accent : C.textDim), background: selected ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s', marginTop: 2 } },
                      selected && h('div', { style: { width: 8, height: 8, borderRadius: '50%', background: '#fff' } })),
                    (function() { var tu1 = (item.images && item.images.length) ? item.images[0] : item.image_1_url; return tu1
                      ? h('img', { src: tu1, style: { width: 48, height: 48, borderRadius: 6, objectFit: 'cover', border: '1px solid ' + C.border, flexShrink: 0 } })
                      : h('div', { style: { width: 48, height: 48, borderRadius: 6, background: C.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } }, h(LIcon, { name: 'package', size: 20, color: C.textDim })); })(),
                    h('div', { style: { flex: 1, minWidth: 0 } },
                      h('div', { style: { fontSize: 15, fontWeight: 600, marginBottom: 4 } }, item.vendor_title || item.msku, h(window.TransparencyChip, item)),
                      h('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 3 } },
                        (item.product_sku || item.msku) && h('span', { style: ps(C.accent + '14', C.accent) }, 'SKU: ' + (item.product_sku || item.msku)),
                        item.fnsku && h('span', { style: ps(C.accent + '14', C.accent) }, 'FNSKU: ' + item.fnsku),
                        item.barcode && h('span', { style: ps(C.accent + '14', C.accent) }, 'UPC: ' + item.barcode)
                      ),
                      hasSaved && h('div', { style: { fontSize: 10, color: C.success, fontWeight: 500 } },
                        h(LIcon, { name: 'check-circle', size: 11, style: { marginRight: 3, verticalAlign: 'middle' } }),
                        'Saved: ' + item.case_qty + ' units/case, ' + item.case_length + '" x ' + item.case_width + '" x ' + item.case_height + '"' + (item.case_weight ? ', ' + item.case_weight + ' lbs' : ''))
                    ),
                    h('div', { style: { textAlign: 'right', fontSize: 15, fontWeight: 700, color: C.accent, flexShrink: 0 } }, remaining + ' units')
                  );
                });
          })()
        );
        })(),

        // ─── STEP 1 (Individual): Select Items for this box ───
        wizStep === 1 && !isCasePacked && h(Card, null,
          // Auto-pack on an individual plan (2026-10-22): saved case packs are still one click per
          // SKU — the server's finish-batch has no packing-mode rule, and a box holding one full
          // case is valid individual-packing content. Transparency SKUs and partial cases stay manual.
          (function() {
            var ap = fbaAutoPackInfo(availableItems);
            var el = ap.filter(function(x) { return x.eligible; });
            if (!el.length) return null;
            var cases = el.reduce(function(s, x) { return s + x.cases; }, 0);
            var units = el.reduce(function(s, x) { return s + x.remaining; }, 0);
            var skipped = ap.length - el.length;
            return h('div', { style: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, border: '1px solid ' + C.success + '40', background: C.success + '10', marginBottom: 12 } },
              h(LIcon, { name: 'zap', size: 16, color: C.success }),
              h('div', { style: { flex: 1, fontSize: 12 } },
                h('div', { style: { fontWeight: 600, color: C.text } }, 'Saved case packs detected'),
                h('div', { style: { color: C.textMuted, marginTop: 2 } }, el.length + ' SKU(s), ' + cases + ' case(s), ' + units + ' unit(s) using saved case dims/qty.' + (skipped ? ' ' + skipped + ' SKU(s) stay manual (no saved case pack, a partial case, or Transparency).' : ''))
              ),
              h(Btn, { variant: 'primary', disabled: cpGenerating, onClick: function() { runAutoPack(el, cases); } },
                cpGenerating
                  ? 'Auto-packing… (' + cpProgress.current + '/' + cpProgress.total + ')'
                  : h(React.Fragment, null, h(LIcon, { name: 'zap', size: 13 }), ' Auto-Pack ' + el.length + ' SKU' + (el.length !== 1 ? 's' : ''))
              )
            );
          })(),
          h('div', { style: { fontSize: 14, fontWeight: 600, marginBottom: 4 } }, 'Step 2: Select Items for This Box'),
          h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 10 } }, 'Choose which items to pack in this box. You can select all or pick specific items.'),

          // Navigation
          h('div', { style: { display: 'flex', justifyContent: 'space-between', marginBottom: 12 } },
            h('div', { style: { display: 'flex', gap: 8 } },
              h(Btn, { onClick: cancelWizard }, h(LIcon, { name: 'arrow-left', size: 14 }), ' Back'),
              h(Btn, { onClick: cancelWizard, style: { background: 'rgba(248,113,113,0.1)', color: '#F87171', border: '1px solid rgba(248,113,113,0.3)' } }, h(LIcon, { name: 'x', size: 14 }), ' Cancel'),
              h(Btn, { variant: 'primary', disabled: selectedCount === 0, onClick: function() {
                // Carry any pre-entered quantities from Step 1 into Step 2.
                setItemSearch('');
                setWizStep(2);
              } }, 'Next: Verify Items ', h(LIcon, { name: 'arrow-right', size: 14 }))
            ),
            receiveUnexpBtn('sm')
          ),

          // Search bar
          h('div', { style: { marginBottom: 10, position: 'relative' } },
            h('input', { value: itemSearch, onChange: function(e) { setItemSearch(e.target.value); }, placeholder: 'Search by SKU, FNSKU, barcode, product name, vendor, PO#...',
              style: { width: '100%', padding: '8px 34px 8px 12px', fontSize: 12, border: '1px solid ' + C.border, borderRadius: 6, background: C.bg, color: C.text, outline: 'none', boxSizing: 'border-box' } }),
            itemSearch && h('button', { onClick: function() { setItemSearch(''); }, title: 'Clear search',
              style: { position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', width: 22, height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', border: 'none', background: C.border, color: C.text, cursor: 'pointer', padding: 0, fontFamily: 'inherit' } },
              h(LIcon, { name: 'x', size: 12 }))
          ),

          // Select all toggle + Add All (pre-fills qtys to max remaining)
          // Add All wrapper width (110) + right padding (8) match the per-item qty column
          // layout below so the button centers visually over the qty inputs.
          h('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 12, paddingBottom: 8, paddingRight: 8, borderBottom: '1px solid ' + C.border } },
            h('button', { onClick: toggleAll, style: { display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit' } },
              h('div', { style: { width: 20, height: 20, borderRadius: 4, border: '2px solid ' + (selectedCount === packScopeItems.length ? C.accent : C.textDim), background: selectedCount === packScopeItems.length ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' } },
                selectedCount === packScopeItems.length && h(LIcon, { name: 'check', size: 13, color: '#fff' })),
              h('span', { style: { fontSize: 12, fontWeight: 600, color: C.text } }, 'Select All (' + packScopeItems.length + ' items)')
            ),
            // Add All — one-click "everything goes in this box": selects every available
            // item and pre-fills packQtys with each item's max remaining so Step 3 lands
            // ready to confirm. Hidden when there's nothing to pull in.
            packScopeItems.length > 0 && h('div', { style: { width: 110, display: 'flex', justifyContent: 'center', flexShrink: 0 } },
              h('button', {
                onClick: function() {
                  var sel = {}; var qtys = {};
                  packScopeItems.forEach(function(it) {
                    sel[it.id] = true;
                    var remaining = remainingFor(it);
                    if (remaining > 0) qtys[it.id] = remaining;
                  });
                  setSelectedItems(sel);
                  setPackQtys(qtys);
                },
                title: 'Select every available item and fill its quantity to the max remaining',
                onMouseEnter: function(e) { e.currentTarget.style.background = C.accent + '22'; e.currentTarget.style.borderColor = C.accent; },
                onMouseLeave: function(e) { e.currentTarget.style.background = C.accent + '14'; e.currentTarget.style.borderColor = C.accent + '55'; },
                style: { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 6, border: '1px solid ' + C.accent + '55', background: C.accent + '14', color: C.accent, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', letterSpacing: '0.02em', transition: 'background 0.15s, border-color 0.15s' } },
                h(LIcon, { name: 'check-circle', size: 13 }), 'Add All')
            )
          ),

          // Item list — capped in its own scroll box so the controls above (Back/Cancel/Next,
          // search, Select All / Add All) stay visible while scrolling through many items.
          h('div', { style: { maxHeight: 'calc(calc(100vh * var(--invzoom, 1)) - 380px)', minHeight: 220, overflowY: 'auto', overflowX: 'hidden', paddingRight: 16 } },
          (function() {
            var q = itemSearch.toLowerCase().trim();
            // Strip "PO#" prefix to match the chip display in the UI.
            var qPo = q.replace(/^po\s*#?\s*/, '');
            var filtered = q ? packScopeItems.filter(function(item) {
              return (item.msku || '').toLowerCase().indexOf(q) >= 0 ||
                (item.fnsku || '').toLowerCase().indexOf(q) >= 0 ||
                (item.barcode || '').toLowerCase().indexOf(q) >= 0 ||
                (item.vendor_title || '').toLowerCase().indexOf(q) >= 0 ||
                (item.vendor_name || '').toLowerCase().indexOf(q) >= 0 ||
                (item.vendor_sku || '').toLowerCase().indexOf(q) >= 0 ||
                (item.product_sku || '').toLowerCase().indexOf(q) >= 0 ||
                (item.po_numbers || '').toLowerCase().indexOf(qPo) >= 0 ||
                (item.asin || '').toLowerCase().indexOf(q) >= 0;
            }) : packScopeItems;
            var ps = function(bg, fg) { return { fontSize: 12, fontWeight: 600, padding: '3px 9px', borderRadius: 4, background: bg, color: fg, letterSpacing: '0.03em', whiteSpace: 'nowrap', display: 'inline-block' }; };
            var copyBadgeSel = function(display, value, bg, fg) {
              return h('span', {
                onClick: function(e) {
                  e.stopPropagation();
                  var v = String(value);
                  if (navigator.clipboard && navigator.clipboard.writeText) {
                    navigator.clipboard.writeText(v).then(function() { showToast('Copied ' + v, 'success'); }).catch(function() { showToast('Copy failed', 'error'); });
                  }
                },
                onMouseEnter: function(e) { e.currentTarget.style.filter = 'brightness(1.25)'; },
                onMouseLeave: function(e) { e.currentTarget.style.filter = 'brightness(1)'; },
                title: 'Click to copy',
                style: Object.assign({}, ps(bg, fg), { cursor: 'pointer', transition: 'filter 0.15s' })
              }, display);
            };
            return filtered.length === 0
              ? h('div', { style: { textAlign: 'center', padding: '16px 0', color: C.textMuted, fontSize: 12 } }, 'No items match "' + itemSearch + '"')
              : filtered.map(function(item) {
                var checked = !!selectedItems[item.id];
                var remaining = remainingFor(item);
                var maxQty = Math.max(0, item.quantity - (item.quantity_packed || 0) - (item.quantity_missing || 0));
                var qtyVal = packQtys[item.id] != null ? packQtys[item.id] : '';
                // Row body only *selects* (never deselects), so a stray click can't uncheck an
                // item and wipe the quantity the user already entered. Deselect is a deliberate
                // click on the checkbox (the only both-way toggle).
                return h('div', { key: item.id,
                  onMouseDown: function(e) { if (e.shiftKey) e.preventDefault(); },
                  onClick: function(e) { packRowClick(e, item, filtered); },
                  style: { display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 8px', marginBottom: 4, borderRadius: 8, cursor: 'pointer', background: checked ? C.accent + '08' : 'transparent', border: '1px solid ' + (checked ? C.accent + '40' : 'transparent'), transition: 'all 0.15s' } },
                  h('div', { onClick: function(e) { e.stopPropagation(); if (e.shiftKey) { packRowClick(e, item, filtered); } else { toggleItem(item.id); lastPackClickRef.current = item.id; } }, title: checked ? 'Uncheck to remove from this box' : 'Check to add to this box',
                    style: { width: 20, height: 20, minWidth: 20, borderRadius: 4, border: '2px solid ' + (checked ? C.accent : C.textDim), background: checked ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s', marginTop: 2, cursor: 'pointer' } },
                    checked && h(LIcon, { name: 'check', size: 13, color: '#fff' })),
                  (function() { var tu1 = (item.images && item.images.length) ? item.images[0] : item.image_1_url; return tu1
                    ? h('img', { src: tu1, onClick: function(e) { e.stopPropagation(); setLightbox({ images: item.images || [item.image_1_url], index: 0 }); }, style: { width: 48, height: 48, borderRadius: 6, objectFit: 'cover', border: '1px solid ' + C.border, flexShrink: 0, cursor: 'pointer' } })
                    : h('div', { style: { width: 48, height: 48, borderRadius: 6, background: C.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } }, h(LIcon, { name: 'package', size: 20, color: C.textDim })); })(),
                  h('div', { style: { flex: 1, minWidth: 0 } },
                    h('div', { style: { fontSize: 15, fontWeight: 600, marginBottom: 4 } }, item.vendor_title || item.msku),
                    h('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 3 } },
                      (item.product_sku || item.msku) && copyBadgeSel('SKU: ' + (item.product_sku || item.msku), item.product_sku || item.msku, C.accent + '14', C.accent),
                      item.vendor_sku && copyBadgeSel('VSKU: ' + item.vendor_sku, item.vendor_sku, C.accent + '14', C.accent),
                      item.asin && h('a', { href: (plan && plan.marketplace_id === 'A2EUQ1WTGCTBG2' ? 'https://www.amazon.ca/dp/' : 'https://www.amazon.com/dp/') + item.asin, target: '_blank', rel: 'noopener noreferrer', onClick: function(e) { e.stopPropagation(); }, style: Object.assign({}, ps(C.accent + '14', C.accent), { textDecoration: 'none' }) }, 'ASIN: ' + item.asin, h(LIcon, { name: 'external-link', size: 8, style: { marginLeft: 3, verticalAlign: 'middle' } })),
                      item.barcode && copyBadgeSel('BARCODE: ' + item.barcode, item.barcode, C.accent + '14', C.accent),
                      item.fnsku && copyBadgeSel('FNSKU: ' + item.fnsku, item.fnsku, C.accent + '14', C.accent),
                      item.requires_transparency && h(window.TransparencyPill)
                    ),
                    item.vendor_name && h('div', { style: { display: 'flex', gap: 4, marginBottom: 3 } },
                      copyBadgeSel(item.vendor_name, item.vendor_name, '#F59E0B' + '18', '#B45309')
                    ),
                    item.po_numbers && h('div', { style: { display: 'flex', gap: 4 } },
                      copyBadgeSel('PO# ' + item.po_numbers, item.po_numbers, '#10B981' + '18', '#047857')
                    ),
                    item.prep_instructions
                      ? h('div', { style: { fontSize: 11, padding: '4px 8px', borderRadius: 4, background: '#FBBF24' + '15', border: '1px solid #FBBF24' + '40', color: '#B45309', marginTop: 3, display: 'inline-block' } },
                          h(LIcon, { name: 'alert-triangle', size: 12, style: { marginRight: 4, verticalAlign: 'middle' } }), 'Prep: ' + item.prep_instructions)
                      : h('div', { style: { fontSize: 11, color: C.textDim, marginTop: 3, fontStyle: 'italic' } }, 'No prep instructions')
                  ),
                  (function() {
                    var qtyParsed = parseInt(qtyVal, 10);
                    var qtyValid = qtyVal !== '' && qtyParsed > 0 && qtyParsed <= maxQty;
                    var remainingAvail = Math.max(0, maxQty - (isNaN(qtyParsed) ? 0 : qtyParsed));
                    return h('div', { onClick: function(e) { e.stopPropagation(); }, style: { width: 110, textAlign: 'center', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 6 } },
                      h('div', { style: { fontSize: 14, fontWeight: 700, color: C.accent, marginBottom: 4 } }, remainingAvail + ' remaining'),
                      h('input', {
                        type: 'number', min: 0, max: maxQty, value: qtyVal,
                        placeholder: '0',
                        onClick: function(e) { e.stopPropagation(); },
                        onChange: function(e) {
                          var raw = e.target.value;
                          if (raw === '') {
                            setPackQtys(function(q) { var nq = Object.assign({}, q); delete nq[item.id]; return nq; });
                            return;
                          }
                          var n = parseInt(raw, 10);
                          if (isNaN(n) || n < 0) n = 0;
                          if (n > maxQty) n = maxQty;
                          setPackQtys(function(q) { var nq = Object.assign({}, q); nq[item.id] = n; return nq; });
                          if (n > 0 && !selectedItems[item.id]) {
                            setSelectedItems(function(prev) { var ns = Object.assign({}, prev); ns[item.id] = true; return ns; });
                          }
                        },
                        style: { width: '100%', padding: '10px 6px', fontSize: 18, fontWeight: 700, textAlign: 'center', border: '2px solid ' + (qtyValid ? C.success : C.border), borderRadius: 8, background: C.bg, color: C.text, outline: 'none', boxSizing: 'border-box' }
                      }),
                      h('div', { style: { fontSize: 9, color: C.textMuted, marginTop: 2 } }, 'units')
                    );
                  })()
                );
              });
          })()),

        ),

        // ─── STEP 2 (Case Packed): Case Configuration ───
        wizStep === 2 && isCasePacked && (function() {
          var cpItem = availableItems.find(function(i) { return i.id === cpSelectedItem; });
          if (!cpItem) return null;
          var cpRemaining = cpItem.quantity - (cpItem.quantity_packed || 0) - (cpItem.quantity_missing || 0);
          var hasSaved = cpItem.case_qty > 0 && cpItem.case_length > 0;
          var upc = parseInt(cpUnitsPerCase) || 0;
          var nc = parseInt(cpNumCases) || 0;
          var totalUnits = upc * nc;
          var maxCases = upc > 0 ? Math.floor(cpRemaining / upc) : 0;
          var isSPD = plan && plan.shipment_type === 'SPD';
          var dimL = parseFloat(cpDims.length) || 0, dimW = parseFloat(cpDims.width) || 0, dimH = parseFloat(cpDims.height) || 0, dimWt = parseFloat(cpDims.weight) || 0;
          var dimsValid = dimL > 0 && dimW > 0 && dimH > 0;
          var spdDimsOk = !isSPD || (dimL <= 25 && dimW <= 25 && dimH <= 25 && (!dimWt || dimWt <= 50));
          var configValid = upc > 0 && nc > 0 && nc <= maxCases && dimsValid && spdDimsOk;
          return h(Card, null,
            h('div', { style: { fontSize: 14, fontWeight: 600, marginBottom: 4 } }, 'Step 3: Case Configuration'),
            h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 10 } },
              'Configure case packing for: ', h('strong', null, cpItem.vendor_title || cpItem.msku), h(window.TransparencyChip, cpItem)),
            cpItem.requires_transparency && h('div', { style: { display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 6, border: '1px solid rgba(167,139,250,0.4)', background: 'rgba(167,139,250,0.08)', marginBottom: 12, fontSize: 11, color: '#A78BFA', fontWeight: 600 } },
              h(LIcon, { name: 'scan-line', size: 13 }),
              'Every unit needs a Transparency label — print and scan them on the Review step before generating cases.'),
            h('div', { style: { display: 'flex', justifyContent: 'space-between', marginBottom: 14 } },
              h('div', { style: { display: 'flex', gap: 8 } },
                h(Btn, { onClick: function() { setWizStep(1); } }, h(LIcon, { name: 'arrow-left', size: 14 }), ' Back'),
                h(Btn, { onClick: cancelWizard, style: { background: 'rgba(248,113,113,0.1)', color: '#F87171', border: '1px solid rgba(248,113,113,0.3)' } }, h(LIcon, { name: 'x', size: 14 }), ' Cancel'),
                h(Btn, { variant: 'primary', disabled: !configValid, onClick: function() { setWizStep(3); } }, 'Next: Review ', h(LIcon, { name: 'arrow-right', size: 14 }))
              )
            ),
            // Toggle saved vs custom
            hasSaved && h('div', { style: { display: 'flex', gap: 8, marginBottom: 16 } },
              h('button', { onClick: function() {
                  setCpUseSaved(true);
                  setCpUnitsPerCase(String(cpItem.case_qty));
                  setCpDims({ length: String(cpItem.case_length || ''), width: String(cpItem.case_width || ''), height: String(cpItem.case_height || ''), weight: String(cpItem.case_weight || '') });
                },
                style: { flex: 1, padding: '10px 14px', borderRadius: 8, border: '2px solid ' + (cpUseSaved ? C.accent : C.border), background: cpUseSaved ? C.accent + '10' : 'transparent', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' } },
                h('div', { style: { fontSize: 12, fontWeight: 600, color: cpUseSaved ? C.accent : C.text, marginBottom: 4 } },
                  h(LIcon, { name: 'database', size: 13, style: { marginRight: 4, verticalAlign: 'middle' } }), 'Use Saved Case Pack'),
                h('div', { style: { fontSize: 11, color: C.textMuted } },
                  cpItem.case_qty + ' units/case, ' + cpItem.case_length + '" x ' + cpItem.case_width + '" x ' + cpItem.case_height + '"' + (cpItem.case_weight ? ', ' + cpItem.case_weight + ' lbs' : ''))
              ),
              h('button', { onClick: function() {
                  setCpUseSaved(false);
                  setCpUnitsPerCase('');
                  setCpDims({ length: '', width: '', height: '', weight: '' });
                },
                style: { flex: 1, padding: '10px 14px', borderRadius: 8, border: '2px solid ' + (!cpUseSaved ? C.accent : C.border), background: !cpUseSaved ? C.accent + '10' : 'transparent', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' } },
                h('div', { style: { fontSize: 12, fontWeight: 600, color: !cpUseSaved ? C.accent : C.text } },
                  h(LIcon, { name: 'edit-2', size: 13, style: { marginRight: 4, verticalAlign: 'middle' } }), 'Custom Entry')
              )
            ),
            // Units per case
            h('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 } },
              h('div', null,
                h('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, display: 'block', marginBottom: 4, textTransform: 'uppercase' } }, 'Units per Case'),
                h('input', { type: 'number', min: 1, value: cpUnitsPerCase, disabled: cpUseSaved && hasSaved,
                  onChange: function(e) { setCpUnitsPerCase(e.target.value); },
                  style: { width: '100%', padding: '10px 12px', fontSize: 16, fontWeight: 700, border: '2px solid ' + (upc > 0 ? C.success : C.danger), borderRadius: 8, background: cpUseSaved && hasSaved ? C.surface : C.bg, color: C.text, outline: 'none', boxSizing: 'border-box' } })
              ),
              h('div', null,
                h('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, display: 'block', marginBottom: 4, textTransform: 'uppercase' } }, 'Number of Cases'),
                h('input', { type: 'number', min: 1, max: maxCases || undefined, value: cpNumCases,
                  onChange: function(e) { setCpNumCases(e.target.value); },
                  style: { width: '100%', padding: '10px 12px', fontSize: 16, fontWeight: 700, border: '2px solid ' + (nc > 0 && nc <= maxCases ? C.success : C.danger), borderRadius: 8, background: C.bg, color: C.text, outline: 'none', boxSizing: 'border-box' } }),
                upc > 0 && h('div', { style: { fontSize: 10, color: C.textMuted, marginTop: 4 } }, 'Max: ' + maxCases + ' cases (' + cpRemaining + ' units remaining)')
              )
            ),
            // Calculated display
            upc > 0 && nc > 0 && h('div', { style: { padding: '10px 14px', borderRadius: 8, marginBottom: 16, background: totalUnits <= cpRemaining ? C.accent + '10' : C.danger + '15', border: '1px solid ' + (totalUnits <= cpRemaining ? C.accent + '30' : C.danger + '40') } },
              h('div', { style: { fontSize: 16, fontWeight: 700, color: totalUnits <= cpRemaining ? C.accent : C.danger, textAlign: 'center' } },
                nc + ' cases x ' + upc + ' units = ' + totalUnits + ' units'),
              totalUnits > cpRemaining && h('div', { style: { fontSize: 11, color: C.danger, textAlign: 'center', marginTop: 4 } },
                'Exceeds remaining quantity (' + cpRemaining + ' units)')
            ),
            // Case dimensions
            h('div', { style: { marginBottom: 16 } },
              h('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, display: 'block', marginBottom: 6, textTransform: 'uppercase' } }, 'Case Dimensions'),
              h('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 } },
                ['length', 'width', 'height', 'weight'].map(function(dk) {
                  var val = parseFloat(cpDims[dk]) || 0;
                  var isRequired = dk !== 'weight';
                  var isOk = isRequired ? val > 0 : true;
                  var spdOver = isSPD && dk !== 'weight' && val > 25 || isSPD && dk === 'weight' && val > 50;
                  var borderColor = spdOver ? C.danger : (isRequired ? (isOk ? C.success : C.danger) : (val > 0 ? C.success : C.border));
                  var label = dk === 'length' ? 'Length (in)' : dk === 'width' ? 'Width (in)' : dk === 'height' ? 'Height (in)' : 'Weight (lbs)';
                  return h('div', { key: dk },
                    h('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, display: 'block', marginBottom: 4 } }, label),
                    h('input', { type: 'number', step: '0.1', value: cpDims[dk], disabled: cpUseSaved && hasSaved,
                      onChange: function(e) { setCpDims(function(d) { var n = {}; n[dk] = e.target.value; return Object.assign({}, d, n); }); },
                      style: { width: '100%', padding: '8px 10px', fontSize: 13, border: '2px solid ' + borderColor, borderRadius: 6, background: cpUseSaved && hasSaved ? C.surface : C.bg, color: C.text, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' } })
                  );
                })
              ),
              isSPD && !spdDimsOk && dimsValid && h('div', { style: { fontSize: 11, color: C.danger, marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 } },
                h(LIcon, { name: 'alert-triangle', size: 12 }), ' SPD boxes must be under 25" per side and under 50 lbs')
            ),
            // Save as default checkbox
            !cpUseSaved && h('label', { style: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: C.text, cursor: 'pointer', marginBottom: 8 } },
              h('input', { type: 'checkbox', checked: cpSaveDefault, onChange: function(e) { setCpSaveDefault(e.target.checked); }, style: { width: 14, height: 14, accentColor: C.accent, cursor: 'pointer' } }),
              'Save as default case pack for this product')
          );
        })(),

        // ─── STEP 2 (Individual): Verify & enter quantities ───
        wizStep === 2 && !isCasePacked && h(Card, null,
          h('div', { style: { fontSize: 14, fontWeight: 600, marginBottom: 4 } }, 'Step 3: Verify Items & Enter Quantities'),
          h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 10 } }, 'Confirm the quantity for each item going into this box. Print FNSKU labels if needed.'),

          // Over-packed warning
          (function() {
            var overItems = selectedItemsList.filter(function(i) { var q = parseInt(packQtys[i.id]); var rem = remainingFor(i); return q > rem; });
            return overItems.length > 0 && h('div', { style: { padding: '10px 14px', borderRadius: 8, marginBottom: 12, background: C.dangerSoft, border: '1px solid ' + C.danger + '40', color: C.danger, fontSize: 12, display: 'flex', alignItems: 'flex-start', gap: 8 } },
              h(LIcon, { name: 'alert-triangle', size: 16, style: { flexShrink: 0, marginTop: 2 } }),
              h('div', null,
                h('div', null, overItems.length === 1
                  ? (overItems[0].product_sku || overItems[0].msku) + ' exceeds available quantity (' + remainingFor(overItems[0]) + ' available)'
                  : overItems.length + ' items exceed available quantities — reduce before proceeding'),
                h('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 4, fontStyle: 'italic' } }, 'If there are additional units, receive them as unexpected items.')
              )
            );
          })(),

          // ─── Transparency scan station: every unit of a flagged SKU gets its label
          // scanned right after application; duplicates + wrong-product codes rejected.
          (function() {
            var tItems = selectedItemsList.filter(function(i) { return i.requires_transparency && i.product_id; });
            // Station covers every item now: Transparency SKUs verify serials; plain
            // SKUs pack one unit per FNSKU/barcode scan.
            var anyFlagged = availableItems.some(function(i) { return i.requires_transparency && i.product_id; });
            var activeItem = null;
            tItems.forEach(function(i) { if (i.product_id === tScanActive) activeItem = i; });
            return h('div', { style: { padding: '16px 18px', borderRadius: 10, marginBottom: 12, background: 'rgba(167,139,250,0.08)', border: '1px solid rgba(167,139,250,0.35)' } },
              h('div', { style: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, rowGap: 6, marginBottom: 4 } },
                h(LIcon, { name: 'scan-line', size: 18, color: '#A78BFA' }),
                // Printing lives on the item rows below ("Print FNSKU + Transparency"
                // per item) — the station is scan-only.
                h('span', { style: { flex: 1, minWidth: 160, fontSize: 15, fontWeight: 700, color: '#A78BFA', letterSpacing: '0.03em' } }, anyFlagged ? 'SCAN TO PACK — TRANSPARENCY' : 'SCAN TO PACK')
              ),
              h('div', { style: { fontSize: 12.5, color: C.textMuted, marginBottom: 12, lineHeight: 1.5 } }, anyFlagged
                ? 'Transparency items — for each unit: apply the FNSKU label, then the Transparency label. Scan the FNSKU, then scan the Transparency label; each accepted scan adds that unit to the box (duplicate and wrong-product codes are rejected). Non-Transparency items scan-to-pack too: one FNSKU/barcode scan adds one unit.'
                : 'Scan each unit\'s FNSKU or barcode to add it to the box — one scan per unit. Fix mistakes by editing the quantity field.'),
              tSeedInfo > 0 && h('div', { style: { fontSize: 12.5, color: '#F59E0B', fontWeight: 600, marginBottom: 12, lineHeight: 1.5 } },
                h(LIcon, { name: 'info', size: 12, style: { marginRight: 4, verticalAlign: 'middle' } }),
                tSeedInfo + ' unit' + (tSeedInfo !== 1 ? 's were' : ' was') + ' scanned earlier and never boxed (e.g. a cancelled box) — included in the counts; they will go into this box. If one of those labels was discarded, use the undo button to remove its scan.'),
              tOthersInfo > 0 && h('div', { style: { fontSize: 12.5, color: C.textMuted, marginBottom: 12, lineHeight: 1.5 } },
                h(LIcon, { name: 'users', size: 12, style: { marginRight: 4, verticalAlign: 'middle' } }),
                tOthersInfo + ' unit' + (tOthersInfo !== 1 ? 's are' : ' is') + ' mid-scan by another packer on this plan — not counted here; they belong to that packer\'s box.'),
              tItems.map(function(item) {
                var qty = parseInt(packQtys[item.id]) || 0;
                var done = tScanCounts[item.product_id] || 0;
                var isActive = tScanActive === item.product_id;
                var complete = qty > 0 && done >= qty;
                return h('div', { key: item.id,
                  onClick: function() { setTScanActive(item.product_id); setTScanMsg(null); setTimeout(function() { if (tScanInputRef.current) tScanInputRef.current.focus(); }, 30); },
                  style: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 6, cursor: 'pointer', marginBottom: 4,
                    background: isActive ? 'rgba(167,139,250,0.16)' : 'transparent', border: '1px solid ' + (isActive ? 'rgba(167,139,250,0.5)' : 'transparent') } },
                  h('span', { style: { fontFamily: 'inherit', fontSize: 14, fontWeight: 600, color: C.accent, whiteSpace: 'nowrap' } }, item.product_sku || item.msku),
                  h('span', { style: { flex: 1, fontSize: 12.5, color: C.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, item.vendor_title || ''),
                  h('span', { style: { fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap', color: complete ? C.success : (qty && done > qty ? C.danger : C.text) } }, done + ' / ' + (qty || '—') + ' scanned'),
                  complete && h(LIcon, { name: 'check-circle', size: 16, color: C.success }),
                  (tScanIds[item.product_id] || []).length > 0 && h('button', {
                    onClick: function(e) { e.stopPropagation(); undoTScan(item.product_id); }, title: 'Undo last scan',
                    style: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 6, border: '1px solid rgba(248,113,113,0.35)', background: 'rgba(248,113,113,0.12)', color: '#F87171', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit' } },
                    h(LIcon, { name: 'undo-2', size: 14 }), 'Undo')
                );
              }),
              h('div', { style: { display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' } },
                h('input', { ref: tScanInputRef, value: tScanVal, disabled: tScanBusy,
                  onChange: function(e) { setTScanVal(e.target.value); },
                  onKeyDown: function(e) { if (e.key === 'Enter') { e.preventDefault(); submitTScan(); } },
                  placeholder: tScanActive
                    ? 'Scan Transparency code… (or an FNSKU to switch SKU / pack a plain item)'
                    : (anyFlagged ? 'Scan a unit\'s FNSKU — Transparency items then scan their code; plain items pack per scan' : 'Scan a unit\'s FNSKU or barcode to add it to the box'),
                  style: { flex: 1, padding: '12px 14px', fontSize: 15, fontWeight: 500, border: '1px solid rgba(167,139,250,0.5)', borderRadius: 8, background: C.bg, color: C.text, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' } }),
                activeItem && h('span', { style: { fontSize: 11, color: C.textMuted, whiteSpace: 'nowrap' } }, 'Scanning: ' + (activeItem.product_sku || activeItem.msku))
              ),
              tScanMsg && h('div', { style: { marginTop: 10, padding: '10px 14px', borderRadius: 6, fontSize: 13.5, fontWeight: 700,
                background: (tScanMsg.type === 'ok' ? C.success : C.danger) + '22',
                color: tScanMsg.type === 'ok' ? C.success : C.danger,
                border: '1px solid ' + (tScanMsg.type === 'ok' ? C.success : C.danger) + '55' } }, tScanMsg.text)
            );
          })(),

          // Navigation
          h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 } },
            h('div', { style: { display: 'flex', gap: 8 } },
              h(Btn, { onClick: function() { setWizStep(1); } }, h(LIcon, { name: 'arrow-left', size: 14 }), ' Back'),
              h(Btn, { onClick: cancelWizard, style: { background: 'rgba(248,113,113,0.1)', color: '#F87171', border: '1px solid rgba(248,113,113,0.3)' } }, h(LIcon, { name: 'x', size: 14 }), ' Cancel'),
              h(Btn, { variant: 'primary', disabled: !allQtysEntered, onClick: function() { setItemSearch(''); setWizStep(3); } }, 'Next: Box Dimensions ', h(LIcon, { name: 'arrow-right', size: 14 }))
            ),
            h('div', { style: { display: 'flex', gap: 8, alignItems: 'center' } },
              receiveUnexpBtn('sm'),
              selectedItemsList.some(function(i) { return i.requires_transparency && i.product_id; }) && h(Btn, { size: 'sm', onClick: reprintLastPrepLabels, disabled: prepPrintBusy,
                title: 'Print didn\'t come out? Re-serves your last Transparency print — same pages, serials are not consumed again',
                style: { background: 'transparent', color: '#8B5CF6', border: '1px dashed rgba(167,139,250,0.45)' } },
                h(LIcon, { name: 'rotate-ccw', size: 13 }), ' Reprint Last'),
              selectedItemsList.length > 0 && !items.some(function(i) { return i.requires_transparency && i.product_id; }) && h('button', {
                onClick: function() { if (allQtysEntered) printFnskuLabels(selectedItemsList); },
                disabled: !allQtysEntered,
                title: allQtysEntered ? 'Print FNSKU labels for all items in this box' : 'Enter all quantities first',
                onMouseEnter: function(e) { if (allQtysEntered) { e.currentTarget.style.background = 'rgba(167,139,250,0.18)'; } },
                onMouseLeave: function(e) { if (allQtysEntered) { e.currentTarget.style.background = 'rgba(167,139,250,0.1)'; } },
                style: { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 12px', borderRadius: 6, border: allQtysEntered ? '1px solid rgba(167,139,250,0.3)' : '1px solid transparent', background: allQtysEntered ? 'rgba(167,139,250,0.1)' : C.border, color: allQtysEntered ? '#8B5CF6' : C.textDim, cursor: allQtysEntered ? 'pointer' : 'not-allowed', opacity: allQtysEntered ? 1 : 0.6, fontSize: 12, fontWeight: 600, fontFamily: 'inherit', letterSpacing: '0.02em', transition: 'background 0.15s, opacity 0.15s' } },
                h(LIcon, { name: 'printer', size: 14 }), 'Print All FNSKU Labels')
            )
          ),

          // Search bar — filter the selected items list (uses the same itemSearch state as Step 1)
          h('div', { style: { marginBottom: 10, position: 'relative' } },
            h('input', { value: itemSearch, onChange: function(e) { setItemSearch(e.target.value); }, placeholder: 'Search by SKU, FNSKU, barcode, product name, vendor, PO#...',
              style: { width: '100%', padding: '8px 34px 8px 12px', fontSize: 13, border: '1px solid ' + C.border, borderRadius: 6, background: C.bg, color: C.text, outline: 'none', boxSizing: 'border-box' } }),
            itemSearch && h('button', { onClick: function() { setItemSearch(''); }, title: 'Clear search',
              style: { position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', width: 22, height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', border: 'none', background: C.border, color: C.text, cursor: 'pointer', padding: 0, fontFamily: 'inherit' } },
              h(LIcon, { name: 'x', size: 12 }))
          ),

          // List capped in its own scroll box so the controls above (Back/Cancel/Next, search,
          // Print All FNSKU Labels) stay visible while scrolling through many items.
          h('div', { style: { maxHeight: 'calc(calc(100vh * var(--invzoom, 1)) - 380px)', minHeight: 220, overflowY: 'auto', overflowX: 'hidden', paddingRight: 16 } },
          (function() {
            var sq = itemSearch.toLowerCase().trim();
            var sqPo = sq.replace(/^po\s*#?\s*/, '');
            var s2filtered = sq ? selectedItemsList.filter(function(item) {
              return (item.msku || '').toLowerCase().indexOf(sq) >= 0 ||
                (item.fnsku || '').toLowerCase().indexOf(sq) >= 0 ||
                (item.barcode || '').toLowerCase().indexOf(sq) >= 0 ||
                (item.vendor_title || '').toLowerCase().indexOf(sq) >= 0 ||
                (item.vendor_name || '').toLowerCase().indexOf(sq) >= 0 ||
                (item.vendor_sku || '').toLowerCase().indexOf(sq) >= 0 ||
                (item.product_sku || '').toLowerCase().indexOf(sq) >= 0 ||
                (item.po_numbers || '').toLowerCase().indexOf(sqPo) >= 0 ||
                (item.asin || '').toLowerCase().indexOf(sq) >= 0;
            }) : selectedItemsList;
            if (s2filtered.length === 0) return h('div', { style: { textAlign: 'center', padding: '16px 0', color: C.textMuted, fontSize: 13 } }, 'No items match "' + itemSearch + '"');
            return s2filtered.map(function(item) {
            var remaining = remainingFor(item);
            var q = packQtys[item.id] || '';
            var qValid = parseInt(q) > 0 && parseInt(q) <= remaining;
            return h('div', { key: item.id, style: { display: 'flex', gap: 14, padding: '14px 0', borderBottom: '1px solid ' + C.border + '44' } },
              // Image
              (function() { var tu2 = (item.images && item.images.length) ? item.images[0] : item.image_1_url; return tu2
                ? h('img', { src: tu2, onClick: function(e) { e.stopPropagation(); setLightbox({ images: item.images || [item.image_1_url], index: 0 }); }, style: { width: 64, height: 64, borderRadius: 8, objectFit: 'cover', border: '1px solid ' + C.border, flexShrink: 0, cursor: 'pointer' } })
                : h('div', { style: { width: 64, height: 64, borderRadius: 8, background: C.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } }, h(LIcon, { name: 'package', size: 28, color: C.textDim })); })(),
              // Details
              (function() {
                var ps2 = function(bg, fg) { return { fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: bg, color: fg, letterSpacing: '0.03em', whiteSpace: 'nowrap', display: 'inline-block' }; };
                var copyBadge2 = function(display, value, bg, fg) {
                  return h('span', {
                    onClick: function(e) {
                      e.stopPropagation();
                      var v = String(value);
                      if (navigator.clipboard && navigator.clipboard.writeText) {
                        navigator.clipboard.writeText(v).then(function() { showToast('Copied ' + v, 'success'); }).catch(function() { showToast('Copy failed', 'error'); });
                      }
                    },
                    onMouseEnter: function(e) { e.currentTarget.style.filter = 'brightness(1.25)'; },
                    onMouseLeave: function(e) { e.currentTarget.style.filter = 'brightness(1)'; },
                    title: 'Click to copy',
                    style: Object.assign({}, ps2(bg, fg), { cursor: 'pointer', transition: 'filter 0.15s' })
                  }, display);
                };
                return h('div', { style: { flex: 1, minWidth: 0 } },
                h('div', { style: { fontSize: 15, fontWeight: 600, marginBottom: 4 } }, item.vendor_title || '—'),
                h('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 3 } },
                  (item.product_sku || item.msku) && copyBadge2('SKU: ' + (item.product_sku || item.msku), item.product_sku || item.msku, C.accent + '14', C.accent),
                  item.vendor_sku && copyBadge2('VSKU: ' + item.vendor_sku, item.vendor_sku, C.accent + '14', C.accent),
                  item.asin && h('a', { href: (plan && plan.marketplace_id === 'A2EUQ1WTGCTBG2' ? 'https://www.amazon.ca/dp/' : 'https://www.amazon.com/dp/') + item.asin, target: '_blank', rel: 'noopener noreferrer', onClick: function(e) { e.stopPropagation(); }, style: Object.assign({}, ps2(C.accent + '14', C.accent), { textDecoration: 'none' }) }, 'ASIN: ' + item.asin, h(LIcon, { name: 'external-link', size: 8, style: { marginLeft: 3, verticalAlign: 'middle' } })),
                  item.barcode && copyBadge2('BARCODE: ' + item.barcode, item.barcode, C.accent + '14', C.accent),
                  item.fnsku && copyBadge2('FNSKU: ' + item.fnsku, item.fnsku, C.accent + '14', C.accent),
                  item.requires_transparency && h(window.TransparencyPill)
                ),
                item.vendor_name && h('div', { style: { display: 'flex', gap: 4, marginBottom: 3 } },
                  copyBadge2(item.vendor_name, item.vendor_name, '#F59E0B' + '18', '#B45309')
                ),
                item.po_numbers && h('div', { style: { display: 'flex', gap: 4, marginBottom: 4 } },
                  copyBadge2('PO# ' + item.po_numbers, item.po_numbers, '#10B981' + '18', '#047857')
                ),
                // Prep instructions
                item.prep_instructions
                  ? h('div', { style: { fontSize: 11, padding: '4px 8px', borderRadius: 4, background: '#FBBF24' + '15', border: '1px solid #FBBF24' + '40', color: '#B45309', marginBottom: 4 } },
                      h(LIcon, { name: 'alert-triangle', size: 12, style: { marginRight: 4, verticalAlign: 'middle' } }), 'Prep: ' + item.prep_instructions)
                  : h('div', { style: { fontSize: 11, color: C.textDim, marginBottom: 4, fontStyle: 'italic' } }, 'No prep instructions')
              ); })(),
              // Quantity input + Print FNSKU button
              h('div', { style: { width: 200, textAlign: 'center', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 6 } },
                h('div', null,
                  (function() {
                    var qInt = parseInt(q, 10);
                    var avail = Math.max(0, remaining - (isNaN(qInt) ? 0 : qInt));
                    return h('div', { style: { fontSize: 14, fontWeight: 700, color: C.accent, marginBottom: 4 } }, avail + ' remaining');
                  })(),
                  h('input', { type: 'number', min: 1, max: remaining, value: q,
                    title: (item.requires_transparency && item.product_id)
                      ? 'Scan-driven: the count follows Transparency scans. You can lower it (leave scanned units for a later box) but not raise it above the scanned count.'
                      : undefined,
                    onChange: function(e) {
                      var v = e.target.value;
                      // Flagged SKUs are scan-driven: quantity may be lowered but never
                      // exceed the number of scanned serials this session.
                      if (item.requires_transparency && item.product_id) {
                        var cap = tScanCounts[item.product_id] || 0;
                        if ((parseInt(v, 10) || 0) > cap) v = cap > 0 ? String(cap) : '';
                      }
                      setPackQtys(function(prev) { var n = Object.assign({}, prev); if (v === '') delete n[item.id]; else n[item.id] = v; return n; });
                    },
                    style: { width: '100%', padding: '10px 6px', fontSize: 18, fontWeight: 700, textAlign: 'center', border: '2px solid ' + ((item.requires_transparency && item.product_id) ? 'rgba(167,139,250,0.6)' : (qValid ? C.success : q ? C.danger : C.border)), borderRadius: 8, background: C.bg, color: C.text, outline: 'none', boxSizing: 'border-box' } }),
                  h('div', { style: { fontSize: 9, color: C.textMuted, marginTop: 2 } }, 'units')
                ),
                h('button', { onClick: function(e) { e.stopPropagation(); printFnskuLabels([item]); },
                  onMouseEnter: function(e) { e.currentTarget.style.background = 'rgba(167,139,250,0.18)'; },
                  onMouseLeave: function(e) { e.currentTarget.style.background = 'rgba(167,139,250,0.1)'; },
                  style: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '5px 10px', borderRadius: 6, border: '1px solid rgba(167,139,250,0.3)', background: 'rgba(167,139,250,0.1)', color: '#8B5CF6', cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', letterSpacing: '0.02em', transition: 'background 0.15s', width: '100%', whiteSpace: 'nowrap' } },
                  h(LIcon, { name: 'printer', size: 14 }), (item.requires_transparency && item.product_id) ? 'Print FNSKU + Transparency' : 'Print FNSKU Label'),
                // Remove from the in-progress box (nothing is committed until Finish).
                // Scanned Transparency units stay "scanned, not boxed" and carry over.
                h('button', { onClick: function(e) {
                    e.stopPropagation();
                    var scans = (item.requires_transparency && item.product_id) ? (tScanCounts[item.product_id] || 0) : 0;
                    toggleItem(item.id);
                    showToast((item.product_sku || item.msku) + ' removed from this box' + (scans > 0 ? ' — ' + scans + ' scanned unit(s) stay unboxed and will carry to the next box (undo their scans if the labels were discarded)' : ''), scans > 0 ? 'info' : 'success');
                  },
                  onMouseEnter: function(e) { e.currentTarget.style.background = 'transparent'; },
                  onMouseLeave: function(e) { e.currentTarget.style.background = 'rgba(248,113,113,0.16)'; },
                  title: 'Remove this item from the box being packed (the box is not finished yet, so nothing is committed)',
                  style: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(248,113,113,0.35)', background: 'rgba(248,113,113,0.16)', color: '#F87171', cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', letterSpacing: '0.02em', transition: 'background 0.15s', width: '100%', whiteSpace: 'nowrap' } },
                  h(LIcon, { name: 'x', size: 14 }), 'Remove from Box')
              )
            );
            });
          })())

        ),

        // ─── STEP 3 (Case Packed): Review & Generate ───
        wizStep === 3 && isCasePacked && (function() {
          var cpItem = availableItems.find(function(i) { return i.id === cpSelectedItem; });
          if (!cpItem) return null;
          var upc = parseInt(cpUnitsPerCase) || 0;
          var nc = parseInt(cpNumCases) || 0;
          var totalUnits = upc * nc;
          var dl = parseFloat(cpDims.length); var dw = parseFloat(cpDims.width); var dh = parseFloat(cpDims.height); var dwt = parseFloat(cpDims.weight) || null;
          // Transparency: every unit in this run must be scanned before Generate unlocks.
          // Counters are session-scoped (reset when the wizard closes), same as the
          // individual-path station.
          var needsTScan = !!(cpItem.requires_transparency && cpItem.product_id);
          var tDone = needsTScan ? (tScanCounts[cpItem.product_id] || 0) : 0;
          var tScanComplete = !needsTScan || (totalUnits > 0 && tDone >= totalUnits);

          var generateCases = function() {
            if (cpGenerating) return;
            setCpGenerating(true);
            setCpProgress({ current: 0, total: nc });

            var createdBoxIds = []; // for serial→box attribution, in creation order
            // One atomic /finish-batch call for all nc cases (review 2026-09-28, medium #39): the
            // create → bulk-pack → dims → seal chain per case could stop half-way with open boxes.
            var chain = api('/fba-shipments/shipments/' + shipmentId + '/boxes/finish-batch', { method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: [{ barcode: cpItem.msku || cpItem.product_sku, quantity: upc }], dims: { length: dl, width: dw, height: dh, weight: dwt }, count: nc }) })
              .then(function(res) { createdBoxIds = ((res && res.boxes) || []).map(function(b) { return b.id; }); setCpProgress({ current: nc, total: nc }); });

            chain.then(function() {
              // Save as default case pack if requested
              if (cpSaveDefault && !cpUseSaved) {
                return api('/fba-shipments/products/' + cpItem.product_id + '/case-pack', {
                  method: 'PUT', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ case_qty: upc, length: dl, width: dw, height: dh, weight: dwt })
                });
              }
            }).then(function() {
              // Attribute the run's Transparency scans to the generated cases (audit
              // only) — upc serials per box, in scan + creation order.
              if (needsTScan) {
                try {
                  var ids = tScanIds[cpItem.product_id] || [];
                  var assignments = [];
                  createdBoxIds.forEach(function(bid, k) {
                    ids.slice(k * upc, (k + 1) * upc).forEach(function(sid) { assignments.push({ scan_id: sid, box_id: bid }); });
                  });
                  if (assignments.length) {
                    api('/fba-shipments/plans/' + planId + '/transparency-scans/assign-box', { method: 'POST', body: JSON.stringify({ assignments: assignments }) }).catch(function() {});
                  }
                } catch (e2) { /* audit only */ }
              }
              showToast(nc + ' cases generated for ' + (cpItem.vendor_title || cpItem.msku), 'success');
              fireConfetti();
              setCpGenerating(false);
              setCpProgress({ current: 0, total: 0 });
              setCpSelectedItem(null);
              setCpNumCases('');
              setCpUnitsPerCase('');
              setCpDims({ length: '', width: '', height: '', weight: '' });
              setWizStep(0);
              loadShipment();
              if (onRefresh) onRefresh();
            }).catch(function(e) {
              showToast(e.message || 'Failed to generate cases', 'error');
              setCpGenerating(false);
              loadShipment();
            });
          };

          return h(Card, null,
            h('div', { style: { fontSize: 14, fontWeight: 600, marginBottom: 4 } }, 'Step 4: Review & Generate'),
            h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 14 } }, 'Review case configuration and generate boxes.'),
            h('div', { style: { display: 'flex', justifyContent: 'space-between', marginBottom: 14 } },
              h('div', { style: { display: 'flex', gap: 8 } },
                h(Btn, { onClick: function() { setWizStep(2); }, disabled: cpGenerating }, h(LIcon, { name: 'arrow-left', size: 14 }), ' Back'),
                h(Btn, { onClick: cancelWizard, disabled: cpGenerating, style: { background: 'rgba(248,113,113,0.1)', color: '#F87171', border: '1px solid rgba(248,113,113,0.3)' } }, h(LIcon, { name: 'x', size: 14 }), ' Cancel'),
                h(Btn, { variant: 'primary', onClick: generateCases, disabled: cpGenerating || !tScanComplete,
                  title: !tScanComplete ? 'Scan all ' + totalUnits + ' Transparency labels first (' + tDone + ' scanned)' : undefined },
                  cpGenerating ? 'Generating...' : [h(LIcon, { key: 'ic', name: 'zap', size: 14 }), ' Generate ' + nc + ' Cases'])
              )
            ),
            // ─── Transparency scan station (case-packed): the run is single-SKU — print
            // the interleaved FNSKU + Transparency PDF, apply both labels to each unit,
            // and scan every Transparency code. Generate stays locked until all units in
            // this run are scanned.
            needsTScan && h('div', { style: { padding: '16px 18px', borderRadius: 10, marginBottom: 16, background: 'rgba(167,139,250,0.08)', border: '1px solid rgba(167,139,250,0.35)' } },
              h('div', { style: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, rowGap: 6, marginBottom: 4 } },
                h(LIcon, { name: 'scan-line', size: 18, color: '#A78BFA' }),
                h('span', { style: { flex: 1, minWidth: 160, fontSize: 15, fontWeight: 700, color: '#A78BFA', letterSpacing: '0.03em' } }, 'SCAN TO PACK — TRANSPARENCY'),
                h('button', { onClick: function() { printPrepLabelsCore([{ shipment_item_id: cpItem.id, product_id: cpItem.product_id, qty: totalUnits }]); }, disabled: prepPrintBusy,
                  title: 'One merged PDF, per unit: FNSKU label then that unit\'s Transparency label — peel order matches prep. Consumes pages from the PO\'s uploaded label files.',
                  onMouseEnter: function(e) { e.currentTarget.style.background = 'rgba(167,139,250,0.18)'; },
                  onMouseLeave: function(e) { e.currentTarget.style.background = 'rgba(167,139,250,0.1)'; },
                  style: { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 11px', borderRadius: 6, border: '1px solid rgba(167,139,250,0.3)', background: 'rgba(167,139,250,0.1)', color: '#8B5CF6', cursor: prepPrintBusy ? 'wait' : 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', letterSpacing: '0.02em', transition: 'background 0.15s', whiteSpace: 'nowrap', flexShrink: 0 } },
                  h(LIcon, { name: 'printer', size: 14 }), prepPrintBusy ? 'Building…' : 'Print FNSKU + Transparency'),
                h('button', { onClick: reprintLastPrepLabels, disabled: prepPrintBusy,
                  title: 'Print didn\'t come out? Re-serves your last Transparency print — same pages, serials are not consumed again',
                  style: { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 11px', borderRadius: 6, border: '1px dashed rgba(167,139,250,0.45)', background: 'transparent', color: '#8B5CF6', cursor: prepPrintBusy ? 'wait' : 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', letterSpacing: '0.02em', whiteSpace: 'nowrap', flexShrink: 0 } },
                  h(LIcon, { name: 'rotate-ccw', size: 13 }), 'Reprint Last')
              ),
              h('div', { style: { fontSize: 12.5, color: C.textMuted, marginBottom: 12, lineHeight: 1.5 } }, 'For each unit: apply the FNSKU label, then apply the Transparency label, then scan the Transparency label — work through the run unit by unit. Duplicate codes and wrong-product codes are rejected.'),
              tSeedInfo > 0 && h('div', { style: { fontSize: 12.5, color: '#F59E0B', fontWeight: 600, marginBottom: 12, lineHeight: 1.5 } },
                h(LIcon, { name: 'info', size: 12, style: { marginRight: 4, verticalAlign: 'middle' } }),
                tSeedInfo + ' unit' + (tSeedInfo !== 1 ? 's were' : ' was') + ' scanned earlier and never boxed — included in the counts. If one of those labels was discarded, use the undo button to remove its scan.'),
              tOthersInfo > 0 && h('div', { style: { fontSize: 12.5, color: C.textMuted, marginBottom: 12, lineHeight: 1.5 } },
                h(LIcon, { name: 'users', size: 12, style: { marginRight: 4, verticalAlign: 'middle' } }),
                tOthersInfo + ' unit' + (tOthersInfo !== 1 ? 's are' : ' is') + ' mid-scan by another packer on this plan — not counted here; they belong to that packer\'s box.'),
              h('div', { style: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 6, marginBottom: 4, background: 'rgba(167,139,250,0.16)', border: '1px solid rgba(167,139,250,0.5)' } },
                h('span', { style: { fontFamily: 'inherit', fontSize: 14, fontWeight: 600, color: C.accent, whiteSpace: 'nowrap' } }, cpItem.product_sku || cpItem.msku),
                h('span', { style: { flex: 1, fontSize: 12.5, color: C.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, cpItem.vendor_title || ''),
                h('span', { style: { fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap', color: tScanComplete ? C.success : C.text } }, tDone + ' / ' + totalUnits + ' scanned'),
                tScanComplete && h(LIcon, { name: 'check-circle', size: 16, color: C.success }),
                (tScanIds[cpItem.product_id] || []).length > 0 && h('button', {
                  onClick: function(e) { e.stopPropagation(); undoTScan(cpItem.product_id); }, title: 'Undo last scan',
                  style: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 6, border: '1px solid rgba(248,113,113,0.35)', background: 'rgba(248,113,113,0.12)', color: '#F87171', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit' } },
                  h(LIcon, { name: 'undo-2', size: 14 }), 'Undo')
              ),
              h('div', { style: { display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' } },
                h('input', { ref: tScanInputRef, value: tScanVal, disabled: tScanBusy,
                  onChange: function(e) { setTScanVal(e.target.value); },
                  onKeyDown: function(e) { if (e.key === 'Enter') { e.preventDefault(); submitTScan(); } },
                  placeholder: 'Scan Transparency code…',
                  style: { flex: 1, padding: '12px 14px', fontSize: 15, fontWeight: 500, border: '1px solid rgba(167,139,250,0.5)', borderRadius: 8, background: C.bg, color: C.text, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' } })
              ),
              tScanMsg && h('div', { style: { marginTop: 10, padding: '10px 14px', borderRadius: 6, fontSize: 13.5, fontWeight: 700,
                background: (tScanMsg.type === 'ok' ? C.success : C.danger) + '22',
                color: tScanMsg.type === 'ok' ? C.success : C.danger,
                border: '1px solid ' + (tScanMsg.type === 'ok' ? C.success : C.danger) + '55' } }, tScanMsg.text),
              !tScanComplete && h('div', { style: { marginTop: 10, fontSize: 12.5, fontWeight: 600, color: '#A78BFA' } },
                h(LIcon, { name: 'lock', size: 12, style: { marginRight: 4, verticalAlign: 'middle' } }),
                'Generate is locked until all ' + totalUnits + ' units are scanned (' + tDone + ' so far).')
            ),
            // Summary card
            h('div', { style: { padding: '14px 16px', borderRadius: 8, background: C.surface, border: '1px solid ' + C.border, marginBottom: 16 } },
              h('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 10 } }, 'Case Pack Summary'),
              h('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 } },
                h('div', null,
                  h('div', { style: { fontSize: 11, color: C.textMuted } }, 'Product'),
                  h('div', { style: { fontSize: 13, fontWeight: 600 } }, cpItem.vendor_title || cpItem.msku)),
                h('div', null,
                  h('div', { style: { fontSize: 11, color: C.textMuted } }, 'SKU'),
                  h('div', { style: { fontSize: 13, fontWeight: 600, fontFamily: 'inherit' } }, cpItem.product_sku || cpItem.msku)),
                h('div', null,
                  h('div', { style: { fontSize: 11, color: C.textMuted } }, 'Units per Case'),
                  h('div', { style: { fontSize: 13, fontWeight: 600 } }, upc)),
                h('div', null,
                  h('div', { style: { fontSize: 11, color: C.textMuted } }, 'Number of Cases'),
                  h('div', { style: { fontSize: 13, fontWeight: 600 } }, nc)),
                h('div', null,
                  h('div', { style: { fontSize: 11, color: C.textMuted } }, 'Case Dimensions'),
                  h('div', { style: { fontSize: 13, fontWeight: 600 } }, dl + '" x ' + dw + '" x ' + dh + '"' + (dwt ? ' | ' + dwt + ' lbs' : ''))),
                h('div', null,
                  h('div', { style: { fontSize: 11, color: C.textMuted } }, 'Total Units'),
                  h('div', { style: { fontSize: 16, fontWeight: 700, color: C.accent } }, totalUnits))
              )
            ),
            plan && plan.shipment_type === 'SPD' && h('div', { style: { fontSize: 11, color: C.accent, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 4 } },
              h(LIcon, { name: 'info', size: 12 }), ' SPD shipment — each case must be under 25" per side and under 50 lbs'),
            // Save default note
            cpSaveDefault && !cpUseSaved && h('div', { style: { fontSize: 11, color: C.success, marginBottom: 12 } },
              h(LIcon, { name: 'check-circle', size: 12, style: { marginRight: 4, verticalAlign: 'middle' } }),
              'Case pack dimensions will be saved as default for this product'),
            // Progress bar during generation
            cpGenerating && h('div', { style: { marginBottom: 16 } },
              h('div', { style: { display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 } },
                h('span', { style: { color: C.textMuted } }, 'Creating cases...'),
                h('span', { style: { fontWeight: 600, color: C.accent } }, 'Case ' + cpProgress.current + ' of ' + cpProgress.total)
              ),
              h('div', { style: { height: 8, background: C.border, borderRadius: 4 } },
                h('div', { style: { height: '100%', width: (cpProgress.total > 0 ? Math.round((cpProgress.current / cpProgress.total) * 100) : 0) + '%', background: C.accent, borderRadius: 4, transition: 'width 0.3s' } })
              )
            )
          );
        })(),

        // ─── STEP 3 (Individual): Dimensions & Finish ───
        wizStep === 3 && !isCasePacked && h(Card, null,
          h('div', { style: { fontSize: 14, fontWeight: 600, marginBottom: 4 } }, 'Step 4: Box Dimensions & Weight'),
          h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 10 } }, 'Enter the outer box dimensions. When you click Finish, the box (and any copies) will be created and case labels will print.'),

          // Navigation
          (function() {
            var copiesNum = Math.max(1, Math.min(999, parseInt(numCopies, 10) || 1));
            if (selectedItemsList.some(function(i) { return i.requires_transparency && i.product_id; })) copiesNum = 1;
            var finishLabel = copiesNum === 1 ? ' Finish & Print Case Label' : ' Finish & Print ' + copiesNum + ' Case Labels';
            // Transparency hard gate: every unit going into this box (× copies) must
            // have a scanned serial before Finish — mirrors the case-pack Generate gate.
            var tShorts = [];
            selectedItemsList.forEach(function(i) {
              if (!(i.requires_transparency && i.product_id)) return;
              var need = (parseInt(packQtys[i.id]) || 0) * copiesNum;
              var have = tScanCounts[i.product_id] || 0;
              if (need > 0 && have < need) tShorts.push((i.product_sku || i.msku) + ' ' + have + '/' + need);
            });
            // Oversize (> 25 in or > 50 lb) blocks Finish until "Confirm Oversize" below is clicked —
            // it used to block for good, so an LTL oversize box could not be packed here (review
            // 2026-09-28, medium #37).
            var boxOversize = parseFloat(dimForm.length) > 25 || parseFloat(dimForm.width) > 25 || parseFloat(dimForm.height) > 25 || parseFloat(dimForm.weight) > 50;
            return h(React.Fragment, null,
              h('div', { style: { display: 'flex', justifyContent: 'space-between', marginBottom: 14 } },
                h('div', { style: { display: 'flex', gap: 8 } },
                  h(Btn, { onClick: function() { setWizStep(2); } }, h(LIcon, { name: 'arrow-left', size: 14 }), ' Back'),
                  h(Btn, { onClick: cancelWizard, style: { background: 'rgba(248,113,113,0.1)', color: '#F87171', border: '1px solid rgba(248,113,113,0.3)' } }, h(LIcon, { name: 'x', size: 14 }), ' Cancel'),
                  h(Btn, { variant: 'primary', onClick: finishBox,
                    title: tShorts.length ? 'Scan all Transparency labels first (on the previous step): ' + tShorts.join(', ') : undefined,
                    disabled: saving || tShorts.length > 0 || !dimForm.length || !dimForm.width || !dimForm.height || (boxOversize && !oversizeConfirmed) },
                    saving ? 'Packing...' : [h(LIcon, { key: 'ic', name: 'check', size: 14 }), finishLabel])
                )
              ),
              tShorts.length > 0 && h('div', { style: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px', borderRadius: 6, border: '1px solid rgba(167,139,250,0.4)', background: 'rgba(167,139,250,0.08)', marginBottom: 12, fontSize: 11, color: '#A78BFA', fontWeight: 600 } },
                h(LIcon, { name: 'lock', size: 13 }),
                'Finish is locked until every Transparency unit is scanned (scanned/needed' + (copiesNum > 1 ? ', × ' + copiesNum + ' copies' : '') + '): ' + tShorts.join(' · ') + ' — go Back to scan.')
            );
          })(),

          // Copies — one wizard finish creates N identical boxes (same items + dims) and
          // prints all N case labels at once. Set to 1 for a single box. Transparency
          // SKUs lock Copies to 1: every unit's serial is unique + individually scanned,
          // so identical duplicate boxes are impossible by definition.
          (function() {
            var hasTransparencySel = selectedItemsList.some(function(i) { return i.requires_transparency && i.product_id; });
            return h('div', { style: { marginBottom: 12, padding: '10px 12px', borderRadius: 8, background: C.surface, border: '1px solid ' + C.border, display: 'flex', alignItems: 'center', gap: 12 } },
              h('div', { style: { fontSize: 12, fontWeight: 600, color: C.text } }, 'Copies'),
              h('input', {
                type: 'number', min: 1, max: 999, step: 1, value: hasTransparencySel ? 1 : numCopies,
                disabled: hasTransparencySel,
                title: hasTransparencySel ? 'Locked to 1 — Transparency units carry unique scanned serials, so identical copies are not possible.' : undefined,
                onChange: function(e) {
                  var v = e.target.value;
                  if (v === '') { setNumCopies(''); return; }
                  var n = parseInt(v, 10);
                  if (isNaN(n)) return;
                  setNumCopies(Math.max(1, Math.min(999, n)));
                },
                onBlur: function() { if (!numCopies || parseInt(numCopies, 10) < 1) setNumCopies(1); },
                style: { width: 80, padding: '6px 8px', fontSize: 13, border: '1px solid ' + C.border, borderRadius: 6, background: hasTransparencySel ? C.surface : C.bg, color: C.text, outline: 'none', fontFamily: 'inherit', opacity: hasTransparencySel ? 0.6 : 1 }
              }),
              h('div', { style: { fontSize: 11, color: C.textMuted } },
                hasTransparencySel
                  ? 'Locked to 1 — this box has Transparency SKUs (unique serials per unit).'
                  : (Math.max(1, Math.min(999, parseInt(numCopies, 10) || 1))) === 1
                    ? 'Pack a single box with the items + dims below.'
                    : 'Pack ' + Math.max(1, Math.min(999, parseInt(numCopies, 10) || 1)) + ' identical boxes — same items, same dimensions. All labels print together.')
            );
          })(),

          // Dimensions form
          h('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginBottom: (parseFloat(dimForm.length) > 25 || parseFloat(dimForm.width) > 25 || parseFloat(dimForm.height) > 25 || parseFloat(dimForm.weight) > 50) ? 4 : 16 } },
            h(Inp, { label: 'Length (in)', type: 'number', step: '0.1', value: dimForm.length, onChange: function(e) { setOversizeConfirmed(false); setDimForm(function(f) { return Object.assign({}, f, { length: e.target.value }); }); }, style: parseFloat(dimForm.length) > 25 ? { border: '1px solid #F87171' } : undefined }),
            h(Inp, { label: 'Width (in)', type: 'number', step: '0.1', value: dimForm.width, onChange: function(e) { setOversizeConfirmed(false); setDimForm(function(f) { return Object.assign({}, f, { width: e.target.value }); }); }, style: parseFloat(dimForm.width) > 25 ? { border: '1px solid #F87171' } : undefined }),
            h(Inp, { label: 'Height (in)', type: 'number', step: '0.1', value: dimForm.height, onChange: function(e) { setOversizeConfirmed(false); setDimForm(function(f) { return Object.assign({}, f, { height: e.target.value }); }); }, style: parseFloat(dimForm.height) > 25 ? { border: '1px solid #F87171' } : undefined }),
            h(Inp, { label: 'Weight (lbs)', type: 'number', step: '0.1', value: dimForm.weight, onChange: function(e) { setOversizeConfirmed(false); setDimForm(function(f) { return Object.assign({}, f, { weight: e.target.value }); }); }, style: parseFloat(dimForm.weight) > 50 ? { border: '1px solid #F87171' } : undefined })
          ),
          (function() {
            var zeroWarnings = [];
            var oversizeWarnings = [];
            if (!parseFloat(dimForm.length)) zeroWarnings.push('Length is 0 — enter a valid dimension');
            else if (parseFloat(dimForm.length) > 25) oversizeWarnings.push('Length exceeds Amazon max of 25 inches');
            if (!parseFloat(dimForm.width)) zeroWarnings.push('Width is 0 — enter a valid dimension');
            else if (parseFloat(dimForm.width) > 25) oversizeWarnings.push('Width exceeds Amazon max of 25 inches');
            if (!parseFloat(dimForm.height)) zeroWarnings.push('Height is 0 — enter a valid dimension');
            else if (parseFloat(dimForm.height) > 25) oversizeWarnings.push('Height exceeds Amazon max of 25 inches');
            if (!parseFloat(dimForm.weight)) zeroWarnings.push('Weight is 0 — enter a valid weight');
            else if (parseFloat(dimForm.weight) > 50) oversizeWarnings.push('Weight exceeds Amazon max of 50 lbs');
            var allWarnings = zeroWarnings.concat(oversizeConfirmed ? [] : oversizeWarnings);
            return h(Fragment, null,
              allWarnings.length > 0 && h('div', { style: { marginBottom: 12, padding: '6px 10px', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 6 } },
                allWarnings.map(function(w, i) { return h('div', { key: i, style: { fontSize: 11, color: '#F87171', display: 'flex', alignItems: 'center', gap: 4 } }, h(LIcon, { name: 'alert-triangle', size: 12 }), w); }),
                oversizeWarnings.length > 0 && !oversizeConfirmed && h('button', { onClick: function() { setOversizeConfirmed(true); },
                  style: { marginTop: 6, background: 'none', border: '1px solid #F87171', color: '#F87171', cursor: 'pointer', fontSize: 11, fontWeight: 600, padding: '4px 12px', borderRadius: 4, fontFamily: 'inherit' } },
                  h(LIcon, { name: 'check', size: 12 }), ' Confirm Oversize')
              ),
              oversizeConfirmed && oversizeWarnings.length > 0 && h('div', { style: { marginBottom: 12, padding: '6px 10px', background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.3)', borderRadius: 6, fontSize: 11, color: '#FBBF24', display: 'flex', alignItems: 'center', gap: 4 } },
                h(LIcon, { name: 'check-circle', size: 12 }), ' Oversize confirmed — proceeding with oversized dimensions')
            );
          })(),

          // Summary of what's going in
          h('div', { style: { padding: '10px 12px', borderRadius: 8, background: C.surface, border: '1px solid ' + C.border, marginBottom: 16 } },
            h('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 6 } }, 'Box Contents Summary'),
            selectedItemsList.map(function(item) {
              var q = parseInt(packQtys[item.id]) || 0;
              return h('div', { key: item.id, style: { display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0' } },
                h('span', null, item.msku || item.vendor_title),
                h('span', { style: { fontWeight: 600 } }, q + ' units')
              );
            }),
            h('div', { style: { display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 700, marginTop: 6, paddingTop: 6, borderTop: '1px solid ' + C.border } },
              h('span', null, 'Total'),
              h('span', null, selectedItemsList.reduce(function(s, i) { return s + (parseInt(packQtys[i.id]) || 0); }, 0) + ' units')
            )
          )
        )
      ),

      // RIGHT PANEL — Tabs: Items | Notes. Top-margin matches the left-side
      // "Shipment Items / Errored / Unexpected" tab bar that only shows at wizStep 0,
      // so both card tops line up at that step.
      h('div', { style: { width: 460, flexShrink: 0, marginTop: wizStep === 0 ? 47 : 0 } },
        h(Card, { style: { position: 'sticky', top: 16, display: 'flex', flexDirection: 'column', maxHeight: 'calc(calc(100vh * var(--invzoom, 1)) - 245px)' } },
          // Box Template download + filled-template upload live in the box-list header,
          // above the Boxes / Internal Notes tabs (moved from the items header).
          h('div', { style: { display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 12 } },
            h('a', { href: boxTemplateHref, title: 'Download Excel box packing template' + (activePackGroupId ? ' (active pack group only)' : ''),
              style: { fontSize: 12, padding: '6px 10px', borderRadius: 6, border: '1px solid ' + C.border, color: C.accent, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 5, fontWeight: 600 } },
              h(LIcon, { name: 'download', size: 13 }), ' Box Template'),
            h('input', { type: 'file', accept: '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', ref: bulkBoxFileRef, style: { display: 'none' },
              onChange: function(e) {
                var file = e.target.files && e.target.files[0];
                e.target.value = '';
                if (file) uploadBoxContents(file, 'preview');
              } }),
            h('button', { onClick: function() { if (bulkBoxFileRef.current) bulkBoxFileRef.current.click(); }, disabled: bulkBoxBusy,
              title: 'Upload a filled box packing template' + (activePackGroupId ? ' (active pack group only)' : ''),
              style: { fontSize: 12, padding: '6px 10px', borderRadius: 6, border: '1px solid ' + C.border, background: 'transparent', color: C.accent, cursor: bulkBoxBusy ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5, fontWeight: 600, fontFamily: 'inherit' } },
              h(LIcon, { name: bulkBoxBusy ? 'loader-2' : 'upload', size: 13, spin: bulkBoxBusy }), ' Upload Boxes'),
            completedBoxes.length > 0 && shipment && shipment.amazon_shipment_id && h('button', { onClick: printAllCaseLabelsLocal, title: 'Print All Case Labels',
              style: { fontSize: 12, padding: '6px 10px', borderRadius: 6, border: '1px solid ' + C.border, background: 'transparent', color: C.accent, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5, fontWeight: 600, fontFamily: 'inherit' } },
              h(LIcon, { name: 'printer', size: 13 }), ' Print All Case Labels')),
          // Tab bar
          h('div', { style: { display: 'flex', borderBottom: '1px solid ' + C.border, marginBottom: 12 } },
            ['items', 'notes'].map(function(tab) {
              var isActive = rightTab === tab;
              var label = tab === 'items' ? 'Boxes (' + scopedBoxes.length + ')' : 'Internal Notes' + (notes.length > 0 ? ' (' + notes.length + ')' : '');
              var icon = tab === 'items' ? 'package' : 'message-square';
              return h('button', { key: tab, onClick: function() { setRightTab(tab); },
                style: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px 0', background: 'none', border: 'none', borderBottom: '2px solid ' + (isActive ? C.accent : 'transparent'), color: isActive ? C.accent : C.textMuted, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'color 0.15s, border-color 0.15s' } },
                h(LIcon, { name: icon, size: 14 }), label);
            })
          ),

          // ─── ITEMS TAB ───
          rightTab === 'items' && h('div', { style: { flex: 1, overflow: 'auto', paddingRight: 12 } },
            h('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 } },
              h('div', { style: { fontSize: 15, fontWeight: 600, color: C.text } }, 'Completed Boxes')
            ),
            scopedBoxes.length > 0 && h('div', { style: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 } },
              h('div', { style: { position: 'relative', flex: 1 } },
                h(LIcon, { name: 'search', size: 14, style: { position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: C.textDim, pointerEvents: 'none' } }),
                h('input', {
                  type: 'text', value: boxFilter, placeholder: 'Find box / SKU / vendor / PO# / barcode...',
                  onChange: function(e) { setBoxFilter(e.target.value); setBoxPage(0); setToPackSelId(null); },
                  style: { width: '100%', boxSizing: 'border-box', background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 30px 8px 32px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', outline: 'none' }
                }),
                boxFilter && h('button', { onClick: function() { setBoxFilter(''); setBoxPage(0); setToPackSelId(null); }, title: 'Clear search',
                  style: { position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', width: 22, height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', border: 'none', background: C.border, color: C.text, cursor: 'pointer', padding: 0, fontFamily: 'inherit' } },
                  h(LIcon, { name: 'x', size: 12 }))
              ),
              boxFilter && h('button', { onClick: function() { setBoxFilter(''); setBoxPage(0); setToPackSelId(null); }, title: 'Show all boxes',
                style: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '7px 12px', borderRadius: 6, border: 'none', background: C.accent + '15', color: C.accent, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', whiteSpace: 'nowrap' } },
                'View All')
            ),
            scopedBoxes.length === 0
              ? h('div', { style: { fontSize: 12, color: C.textMuted, textAlign: 'center', padding: '20px 0' } },
                  packGroups ? 'No boxes packed yet for Group ' + (activeGroupIdx + 1) : 'No boxes packed yet')
              : (function() {
                  var q = boxFilter.trim();
                  // Split on commas only so free-text phrases stay intact; commas let a
                  // user OR several terms (the click-a-product flow sets a single SKU).
                  var tokens = q ? q.split(/,+/).map(function(s) { return s.trim(); }).filter(Boolean) : [];
                  var siById = {};
                  items.forEach(function(it) { siById[it.id] = it; });
                  var filtered = tokens.length ? scopedBoxes.filter(function(b) {
                    var bn = String(b.box_number || '');
                    return tokens.some(function(t) {
                      if (bn.indexOf(t) === 0) return true;
                      var tl = t.toLowerCase();
                      var poQ = tl.replace(/^po\s*#?\s*/, '');
                      return (b.items || []).some(function(bi) {
                        if (
                          (bi.product_sku || '').toLowerCase().indexOf(tl) >= 0 ||
                          (bi.msku || '').toLowerCase().indexOf(tl) >= 0 ||
                          (bi.barcode || '').toLowerCase().indexOf(tl) >= 0 ||
                          (bi.vendor_title || '').toLowerCase().indexOf(tl) >= 0 ||
                          (bi.vendor_sku || '').toLowerCase().indexOf(tl) >= 0 ||
                          (bi.fnsku || '').toLowerCase().indexOf(tl) >= 0 ||
                          (bi.asin || '').toLowerCase().indexOf(tl) >= 0
                        ) return true;
                        var si = siById[bi.shipment_item_id];
                        if (si && (
                          (si.vendor_name || '').toLowerCase().indexOf(tl) >= 0 ||
                          (si.po_numbers || '').toLowerCase().indexOf(poQ) >= 0
                        )) return true;
                        return false;
                      });
                    });
                  }) : scopedBoxes;
                  if (filtered.length === 0) return h('div', { style: { fontSize: 12, color: C.textMuted, textAlign: 'center', padding: '20px 0' } }, 'No boxes match "' + q + '"');
                  var sorted = filtered.slice().sort(function(a, b) { return (b.box_number || 0) - (a.box_number || 0); });
                  // All boxes (no pagination). The items-tab container above already scrolls,
                  // so render a plain list here — a second scroll box would double the scrollbar.
                  return h('div', null,
                    sorted.map(function(box) {
                      var bItems = (box.items || []);
                      var unitCount = bItems.reduce(function(s, i) { return s + i.quantity; }, 0);
                      var singleSkuImg = bItems.length === 1 && bItems[0].image_1_url ? bItems[0].image_1_url : null;
                      return h('div', { key: box.id, style: { padding: '10px 0', borderBottom: '1px solid ' + C.border + '33' } },
                        h('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' },
                          onClick: function() { setExpandedBoxId(box.id); } },
                          h('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
                            singleSkuImg
                              ? h('img', { src: singleSkuImg, style: { width: 72, height: 72, borderRadius: 6, objectFit: 'cover', border: '1px solid ' + C.border, flexShrink: 0 } })
                              : h('div', { style: { width: 72, height: 72, borderRadius: 6, background: C.success + '15', display: 'flex', alignItems: 'center', justifyContent: 'center' } },
                                  h(LIcon, { name: 'package', size: 32, color: C.success })),
                            (function() {
                              var dimStr = (box.length && box.width && box.height) ? parseFloat(box.length) + 'x' + parseFloat(box.width) + 'x' + parseFloat(box.height) + ' inches' : '';
                              var weightStr = box.weight ? parseFloat(box.weight) + ' lbs' : '';
                              var dimWeight = [dimStr, weightStr].filter(Boolean).join(' | ');
                              return h('div', null,
                                h('div', { style: { fontWeight: 600, fontSize: 18 } }, 'Box ' + box.box_number,
                                  box.is_phantom && h('span', { title: 'Phantom box — declared to Amazon but not physically shipped', style: { marginLeft: 6, fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: '#F59E0B22', color: '#F59E0B' } }, 'PHANTOM')),
                                h('div', { style: { fontSize: 14, color: C.textMuted } }, unitCount + ' units / ' + bItems.length + ' SKUs'),
                                dimWeight && h('div', { style: { fontSize: 14, color: C.textMuted } }, dimWeight)
                              );
                            })()
                          ),
                          h('div', { style: { display: 'flex', gap: 2, alignItems: 'center' } },
                            shipment && shipment.amazon_shipment_id && h('button', { onClick: function(e) { e.stopPropagation(); printCaseLabel(null, box.box_number, box, box.id); }, title: 'Print Case Label',
                              onMouseEnter: function(e) { e.currentTarget.style.background = C.accent + '18'; },
                              onMouseLeave: function(e) { e.currentTarget.style.background = 'none'; },
                              style: { background: 'none', border: 'none', cursor: 'pointer', color: C.accent, padding: 6, borderRadius: 6, transition: 'background 0.15s' } }, h(LIcon, { name: 'printer', size: 18 })),
                            h('button', { onClick: function(e) { e.stopPropagation(); openDuplicateBox(box); }, title: 'Duplicate Box',
                              onMouseEnter: function(e) { e.currentTarget.style.background = C.accent + '18'; },
                              onMouseLeave: function(e) { e.currentTarget.style.background = 'none'; },
                              style: { background: 'none', border: 'none', cursor: 'pointer', color: C.accent, padding: 6, borderRadius: 6, transition: 'background 0.15s' } }, h(LIcon, { name: 'copy', size: 18 })),
                            h('button', { onClick: function(e) { e.stopPropagation(); openEditBox(box); }, title: 'Edit Box',
                              onMouseEnter: function(e) { e.currentTarget.style.background = C.accent + '18'; },
                              onMouseLeave: function(e) { e.currentTarget.style.background = 'none'; },
                              style: { background: 'none', border: 'none', cursor: 'pointer', color: C.accent, padding: 6, borderRadius: 6, transition: 'background 0.15s' } }, h(LIcon, { name: 'edit-2', size: 18 })),
                            h('button', { onClick: function(e) { e.stopPropagation(); deleteBox(box.id); }, title: 'Delete',
                              onMouseEnter: function(e) { e.currentTarget.style.background = '#F8717118'; },
                              onMouseLeave: function(e) { e.currentTarget.style.background = 'none'; },
                              style: { background: 'none', border: 'none', cursor: 'pointer', color: C.danger, padding: 6, borderRadius: 6, transition: 'background 0.15s' } }, h(LIcon, { name: 'trash-2', size: 18 }))
                          )
                        )
                      );
                    })
                  );
                })()
          ),

          // ─── NOTES TAB (matches PO Internal Notes style) ───
          rightTab === 'notes' && h('div', { style: { flex: 1, display: 'flex', flexDirection: 'column' } },
            // Notes list
            h('div', { style: { flex: 1, overflow: 'auto', padding: '4px 0', display: 'flex', flexDirection: 'column', gap: 8, minHeight: 200 } },
              notes.length === 0 && h('div', { style: { textAlign: 'center', color: C.textDim, fontSize: 13, padding: 40 } }, 'No notes yet. Start the conversation!'),
              notes.map(function(note) {
                var isMe = currentUser && currentUser.id === note.user_id;
                var time = new Date(note.created_at);
                var timeStr = time.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' + time.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                return h('div', { key: note.id, style: { display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start', padding: '0 4px' } },
                  h('div', { style: { fontSize: 10, color: C.textDim, marginBottom: 2, display: 'flex', alignItems: 'center', gap: 6 } },
                    h('span', { style: { fontWeight: 600 } }, note.display_name || note.username),
                    h('span', null, timeStr),
                    isMe && h('button', { onClick: function() { deleteNote(note.id); },
                      style: { background: 'none', border: 'none', color: C.textDim, cursor: 'pointer', fontSize: 10, padding: 0, display: 'inline-flex', alignItems: 'center' } }, h(LIcon, { name: 'x', size: 10 }))
                  ),
                  h('div', { style: { background: isMe ? C.accent : C.surface, color: isMe ? '#fff' : C.text,
                    padding: '8px 14px', borderRadius: 12, borderTopRightRadius: isMe ? 4 : 12, borderTopLeftRadius: isMe ? 12 : 4,
                    maxWidth: '85%', fontSize: 13, lineHeight: 1.4, wordBreak: 'break-word',
                    border: isMe ? 'none' : '1px solid ' + C.border } }, renderNoteBody(note.message), renderNoteAttachments(note))
                );
              }),
              h('div', { ref: notesEndRef })
            ),
            // Input area
            h('div', { style: { borderTop: '1px solid ' + C.border, padding: '12px 0 0', display: 'flex', gap: 8, alignItems: 'center' } },
              h('div', { style: { flex: 1 } },
                h(MentionInput, {
                  value: newNote, onChange: setNewNote,
                  onKeyDown: function(e) { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendNote(); } },
                  placeholder: 'Type a note... use @ to tag a teammate',
                  rows: 2,
                  style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '10px 14px', borderRadius: 8, fontSize: 13, outline: 'none' }
                })
              ),
              h(Btn, { variant: 'primary', size: 'sm', onClick: sendNote, disabled: !newNote.trim() }, 'Send')
            )
          )
        ) // close Card
      ) // close right panel div
    ),

    // Box Contents modal
    h(Modal, { open: !!viewBox, onClose: function() { setExpandedBoxId(null); }, title: viewBox ? 'Box ' + viewBox.box_number + ' Contents' : '', width: 750 },
      viewBox && (function() {
        var vbItems = viewBox.items || [];
        var vbUnits = vbItems.reduce(function(s, i) { return s + i.quantity; }, 0);
        var ps3 = function(bg, fg) { return { fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: bg, color: fg, letterSpacing: '0.03em', whiteSpace: 'nowrap', display: 'inline-block' }; };
        var copyBadge3 = function(display, value, bg, fg) {
          return h('span', {
            onClick: function(e) {
              e.stopPropagation();
              var v = String(value);
              if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(v).then(function() { showToast('Copied ' + v, 'success'); }).catch(function() { showToast('Copy failed', 'error'); });
              }
            },
            onMouseEnter: function(e) { e.currentTarget.style.filter = 'brightness(1.25)'; },
            onMouseLeave: function(e) { e.currentTarget.style.filter = 'brightness(1)'; },
            title: 'Click to copy',
            style: Object.assign({}, ps3(bg, fg), { cursor: 'pointer', transition: 'filter 0.15s' })
          }, display);
        };
        return h('div', null,
          // Action buttons
          h('div', { style: { display: 'flex', gap: 8, marginBottom: 14, justifyContent: 'flex-end' } },
            shipment && shipment.amazon_shipment_id && h(Btn, { size: 'sm', onClick: function() { printCaseLabel(null, viewBox.box_number, viewBox, viewBox.id); } }, h(LIcon, { name: 'printer', size: 12 }), ' Print Case Label'),
            h(Btn, { size: 'sm', onClick: function() { setExpandedBoxId(null); openEditBox(viewBox); } }, h(LIcon, { name: 'edit-2', size: 12 }), ' Edit Box')
          ),
          // Header info
          h('div', { style: { display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' } },
            h('span', { style: ps3(viewBox.box_status === 'printed' ? C.success + '18' : C.accent + '18', viewBox.box_status === 'printed' ? C.success : C.accent) }, viewBox.box_status === 'printed' ? 'PRINTED' : 'CREATED'),
            viewBox.is_phantom && h('span', { title: 'Phantom box — declared to Amazon but not physically shipped', style: ps3('#F59E0B22', '#F59E0B') }, 'PHANTOM'),
            h('span', { style: { fontSize: 13, color: C.textMuted } }, vbUnits + ' units / ' + vbItems.length + ' SKUs'),
            (viewBox.length && viewBox.width && viewBox.height) && h('span', { style: { fontSize: 13, color: C.textMuted } }, parseFloat(viewBox.length) + '" x ' + parseFloat(viewBox.width) + '" x ' + parseFloat(viewBox.height) + '"' + (viewBox.weight ? ' | ' + parseFloat(viewBox.weight) + ' lbs' : ''))
          ),
          // Items list
          vbItems.length === 0
            ? h('div', { style: { fontSize: 13, color: C.textMuted, textAlign: 'center', padding: '20px 0', fontStyle: 'italic' } }, 'No items in this box')
            : vbItems.map(function(bi) {
                return h('div', { key: bi.id, style: { display: 'flex', alignItems: 'center', gap: 14, padding: '14px 0', borderBottom: '1px solid ' + C.border + '33' } },
                  bi.image_1_url
                    ? h('img', { src: bi.image_1_url, onClick: function() { setLightbox({ images: [bi.image_1_url], index: 0 }); }, style: { width: 72, height: 72, borderRadius: 8, objectFit: 'cover', border: '1px solid ' + C.border, flexShrink: 0, cursor: 'pointer' } })
                    : h('div', { style: { width: 72, height: 72, borderRadius: 8, background: C.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } }, h(LIcon, { name: 'package', size: 28, color: C.textDim })),
                  h('div', { style: { flex: 1, minWidth: 0 } },
                    h('div', { style: { fontSize: 14, fontWeight: 600, marginBottom: 5 } }, bi.vendor_title || bi.msku || '—'),
                    h('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 5 } },
                      (bi.product_sku || bi.msku) && copyBadge3('SKU: ' + (bi.product_sku || bi.msku), bi.product_sku || bi.msku, C.accent + '14', C.accent),
                      bi.fnsku && copyBadge3('FNSKU: ' + bi.fnsku, bi.fnsku, C.accent + '14', C.accent),
                      bi.asin && h('a', { href: (plan && plan.marketplace_id === 'A2EUQ1WTGCTBG2' ? 'https://www.amazon.ca/dp/' : 'https://www.amazon.com/dp/') + bi.asin, target: '_blank', rel: 'noopener noreferrer', onClick: function(e) { e.stopPropagation(); }, style: Object.assign({}, ps3(C.accent + '14', C.accent), { textDecoration: 'none' }) }, 'ASIN: ' + bi.asin, h(LIcon, { name: 'external-link', size: 8, style: { marginLeft: 3, verticalAlign: 'middle' } })),
                      bi.barcode && copyBadge3('BARCODE: ' + bi.barcode, bi.barcode, C.accent + '14', C.accent),
                      bi.vendor_sku && copyBadge3('VSKU: ' + bi.vendor_sku, bi.vendor_sku, C.accent + '14', C.accent)
                    )
                  ),
                  h('div', { style: { fontSize: 20, fontWeight: 700, color: C.accent, flexShrink: 0, minWidth: 55, textAlign: 'right' } }, 'x' + bi.quantity)
                );
              }),
        );
      })()
    ),

    // Bulk box contents (Excel) preview / confirm modal
    h(Modal, { open: !!bulkBoxModal, onClose: function() { if (!bulkBoxBusy) setBulkBoxModal(null); }, title: 'Upload Box Contents', width: 580 },
      bulkBoxModal && (function() {
        var rep = bulkBoxModal.report || {};
        var sum = rep.summary || {};
        var errs = rep.errors || [];
        var warns = rep.warnings || [];
        var chip = function(txt) {
          return h('span', { style: { padding: '3px 10px', borderRadius: 999, background: C.accentSoft, color: C.accent, fontSize: 12, fontWeight: 600 } }, txt);
        };
        return h('div', null,
          h('div', { style: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 } },
            chip((sum.boxes || 0) + ' boxes'), chip((sum.units || 0) + ' units'), chip((sum.skus || 0) + ' SKUs')),
          (sum.replace_count || 0) > 0 && h('div', { style: { padding: '8px 10px', borderRadius: 6, background: C.warningSoft, color: C.warning, fontSize: 12, fontWeight: 600, marginBottom: 10 } },
            'Applying will replace ' + sum.replace_count + ' existing box' + (sum.replace_count === 1 ? '' : 'es') + (activePackGroupId ? ' in this pack group' : ' on this shipment') + '.'),
          errs.length > 0 && h('div', { style: { maxHeight: 150, overflowY: 'auto', padding: '8px 10px', borderRadius: 6, background: C.dangerSoft, marginBottom: 10 } },
            h('div', { style: { fontSize: 11, fontWeight: 700, color: C.danger, textTransform: 'uppercase', marginBottom: 4 } }, errs.length + ' error' + (errs.length === 1 ? '' : 's') + ' — fix the file and re-upload'),
            errs.map(function(m, i) { return h('div', { key: i, style: { fontSize: 12, color: C.danger, marginBottom: 2 } }, '• ' + m); })),
          warns.length > 0 && h('div', { style: { maxHeight: 110, overflowY: 'auto', padding: '8px 10px', borderRadius: 6, background: C.warningSoft, marginBottom: 10 } },
            warns.map(function(m, i) { return h('div', { key: i, style: { fontSize: 12, color: C.warning, marginBottom: 2 } }, '• ' + m); })),
          (rep.per_sku && rep.per_sku.length > 0) && h('div', { style: { border: '1px solid ' + C.border, borderRadius: 6, maxHeight: 240, overflowY: 'auto', marginBottom: 4 } },
            h('div', { style: { display: 'flex', padding: '6px 10px', borderBottom: '1px solid ' + C.border, fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, position: 'sticky', top: 0, background: C.surface } },
              h('div', { style: { flex: 1 } }, 'SKU'),
              h('div', { style: { width: 80, textAlign: 'right' } }, 'Uploaded'),
              h('div', { style: { width: 80, textAlign: 'right' } }, 'Expected')),
            rep.per_sku.map(function(row) {
              var color = row.uploaded === 0 ? C.textDim : (row.uploaded === row.expected ? C.success : (row.uploaded > row.expected ? C.danger : C.warning));
              return h('div', { key: row.sku, style: { display: 'flex', padding: '5px 10px', borderBottom: '1px solid ' + C.border, fontSize: 12, alignItems: 'center' } },
                h('div', { style: { flex: 1, minWidth: 0 } },
                  h('div', { style: { fontWeight: 600, color: C.text } }, row.sku),
                  row.title && h('div', { style: { fontSize: 10, color: C.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, row.title)),
                h('div', { style: { width: 80, textAlign: 'right', fontWeight: 700, color: color } }, row.uploaded),
                h('div', { style: { width: 80, textAlign: 'right', color: C.textMuted } }, row.expected));
            })),
          h('div', { style: { fontSize: 11, color: C.textMuted, marginBottom: 12 } }, 'Partial packs are allowed — SKUs left short can be packed later or marked missing.'),
          h('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } },
            h(Btn, { size: 'sm', onClick: function() { if (!bulkBoxBusy) setBulkBoxModal(null); } }, 'Cancel'),
            h(Btn, { variant: 'primary', size: 'sm', disabled: errs.length > 0 || bulkBoxBusy || !(sum.boxes > 0),
              onClick: function() { if (!errs.length && !bulkBoxBusy) uploadBoxContents(bulkBoxModal.file, 'apply'); } },
              bulkBoxBusy ? 'Applying…' : ((sum.replace_count || 0) > 0 ? 'Replace & Apply' : 'Create Boxes'))));
      })()
    ),

    // Print FNSKU labels modal
    h(Modal, { open: !!printModal, onClose: function() { setPrintModal(null); }, title: 'Print FNSKU Labels', width: 720 },
      printModal && h('div', null,
        h('div', { style: { marginBottom: 16 } },
          h('label', { style: { fontSize: 12.5, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', display: 'block', marginBottom: 8 } }, 'Label Template'),
          (function() { var fbaTpls = labelTemplates.filter(function(t) { return (t.category || 'fba') === 'fba'; }); return fbaTpls.length === 0
            ? h('div', { style: { fontSize: 13.5, color: C.textMuted, padding: '10px 0' } }, 'No FBA label templates configured. Create templates in Settings > Templates.')
            : h('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 8 } },
                fbaTpls.map(function(tpl) {
                  var sel = selectedLabelTpl === tpl.id;
                  return h('button', { key: tpl.id, onClick: function() { setSelectedLabelTpl(tpl.id); },
                    style: { padding: '10px 16px', borderRadius: 8, border: '2px solid ' + (sel ? C.accent : C.border), background: sel ? C.accent + '15' : 'transparent', color: sel ? C.accent : C.text, cursor: 'pointer', fontSize: 13.5, fontFamily: 'inherit', fontWeight: sel ? 600 : 400, textAlign: 'left' } },
                    h('div', null, tpl.name),
                    h('div', { style: { fontSize: 11.5, color: C.textMuted, marginTop: 3 } }, tpl.label_type === 'sheet' ? tpl.columns + 'x' + tpl.rows_per_sheet + ' per sheet' : parseFloat(tpl.width_mm).toFixed(1) + 'x' + parseFloat(tpl.height_mm).toFixed(1) + 'mm'),
                    tpl.is_default && h('span', { style: { fontSize: 10, color: C.accent, fontWeight: 600 } }, ' DEFAULT')
                  );
                })
              ); })()
        ),
        (function() {
          var tCount = (printModal.items || []).filter(function(i) { return i.requires_transparency && i.product_id; }).length;
          if (!tCount) return null;
          return h('div', { style: { padding: '10px 14px', borderRadius: 8, border: '1px solid rgba(167,139,250,0.4)', background: 'rgba(167,139,250,0.08)', marginBottom: 14 } },
            h('div', { style: { display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12.5, color: '#A78BFA', fontWeight: 600, lineHeight: 1.5 } },
              h('img', { src: '/img/transparency-logo.png', alt: '', style: { width: 16, height: 16, borderRadius: 2, flexShrink: 0, marginTop: 2 } }),
              tCount + ' Transparency SKU' + (tCount !== 1 ? 's' : '') + ' — Print produces FNSKU + Transparency pairs for them (one merged PDF; consumes serials from the PO\'s uploaded label files, so print only what you will apply).'));
        })(),
        h('div', { style: { marginBottom: 16 } },
          h('label', { style: { fontSize: 12.5, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', display: 'block', marginBottom: 8 } }, 'Items & Label Count'),
          (printModal.items || []).map(function(item) {
            var thumbUrl = (item.images && item.images.length) ? item.images[0] : item.image_1_url;
            return h('div', { key: item.id, style: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid ' + C.border + '44' } },
              thumbUrl
                ? h('img', { src: thumbUrl, style: { width: 60, height: 60, borderRadius: 8, objectFit: 'cover', border: '1px solid ' + C.border, flexShrink: 0 } })
                : h('div', { style: { width: 60, height: 60, borderRadius: 8, background: C.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } }, h(LIcon, { name: 'package', size: 26, color: C.textDim })),
              (function() {
                var mp = function(bg, fg) { return { fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: bg, color: fg, letterSpacing: '0.02em', whiteSpace: 'nowrap', display: 'inline-block' }; };
                return h('div', { style: { flex: 1, minWidth: 0 } },
                  h('div', { style: { fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 3 } }, item.vendor_title || item.msku || ''),
                  h('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 4 } },
                    (item.product_sku || item.msku) && h('span', { style: mp(C.accent + '14', C.accent) }, 'SKU: ' + (item.product_sku || item.msku)),
                    item.vendor_sku && h('span', { style: mp(C.accent + '14', C.accent) }, 'VSKU: ' + item.vendor_sku),
                    item.fnsku && h('span', { style: mp(C.accent + '14', C.accent) }, 'FNSKU: ' + item.fnsku),
                    item.requires_transparency && h(window.TransparencyPill, { style: { fontSize: 11, padding: '2px 7px' } }),
                    item.vendor_name && h('span', { style: mp('#F59E0B' + '18', '#B45309') }, item.vendor_name),
                    item.po_numbers && h('span', { style: mp('#10B981' + '18', '#047857') }, 'PO# ' + item.po_numbers)
                  ));
              })(),
              (function() {
                var rem = Math.max(0, (item.quantity || 0) - (item.quantity_packed || 0) - (item.quantity_missing || 0));
                return h('div', { style: { textAlign: 'right', flexShrink: 0, marginRight: 6 } },
                  h('div', { style: { fontSize: 15, fontWeight: 700, color: C.accent } }, rem),
                  h('div', { style: { fontSize: 10.5, color: C.textMuted } }, 'left to pack'));
              })(),
              h('input', { type: 'number', min: 0, value: printQtyOverride[item.id] || '', placeholder: '0',
                onChange: function(e) { var raw = e.target.value; setPrintQtyOverride(function(prev) { var n = Object.assign({}, prev); if (raw === '') delete n[item.id]; else n[item.id] = Math.max(0, parseInt(raw, 10) || 0); return n; }); },
                style: { width: 76, padding: '8px 8px', fontSize: 15, fontWeight: 600, textAlign: 'center', border: '1px solid ' + C.border, borderRadius: 6, background: C.bg, color: C.text, flexShrink: 0 } })
            );
          }),
          h('div', { style: { fontSize: 12.5, color: C.textMuted, marginTop: 8, textAlign: 'right', fontWeight: 600 } },
            'Total: ' + (printModal.items || []).reduce(function(s, item) { return s + (printQtyOverride[item.id] || 0); }, 0) + ' labels · ' +
            (printModal.items || []).reduce(function(s, item) { return s + Math.max(0, (item.quantity || 0) - (item.quantity_packed || 0) - (item.quantity_missing || 0)); }, 0) + ' units left to pack')
        ),
        h('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } },
          h(Btn, { onClick: function() { setPrintModal(null); } }, 'Cancel'),
          // Hidden when any Transparency SKU is in the modal — the ZIP export is
          // FNSKU-only and would ship flagged units without their serial labels.
          !(printModal.items || []).some(function(i) { return i.requires_transparency && i.product_id; }) &&
            h(Btn, { onClick: exportFnskuLabelsZip }, h(LIcon, { name: 'download', size: 14 }), ' Print As Individual Files'),
          h(Btn, { variant: 'primary', onClick: executePrint }, h(LIcon, { name: 'printer', size: 14 }), ' Print')
        )
      )
    ),

    // ─── Swallowed-print fallback pill ───
    // The last served labels PDF stays one click away: a click is a user gesture, so
    // opening it can't be popup-blocked the way the old auto-fallback was.
    prepPdfFallback && h('div', { style: { position: 'fixed', right: 18, bottom: 18, zIndex: 300, display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 10, background: C.surface, border: '1px solid ' + C.accent + '66', boxShadow: '0 4px 18px rgba(0,0,0,0.28)' } },
      h(LIcon, { name: 'file-text', size: 15, color: C.accent }),
      h('span', { style: { fontSize: 12.5, color: C.textMuted, fontWeight: 600 } }, 'Labels didn\'t print?'),
      h('button', { onClick: function() { window.open(prepPdfFallback.url, '_blank'); },
        style: { padding: '6px 12px', borderRadius: 6, border: '1px solid ' + C.accent, background: C.accent + '15', color: C.accent, cursor: 'pointer', fontSize: 12.5, fontWeight: 700, fontFamily: 'inherit' } }, 'Open PDF'),
      h('button', { onClick: function() { setPrepPdfFallback(function(prev) { if (prev && prev.url) { try { URL.revokeObjectURL(prev.url); } catch (e) { /* already gone */ } } return null; }); },
        style: { padding: '4px 8px', borderRadius: 6, border: 'none', background: 'transparent', color: C.textDim, cursor: 'pointer', fontSize: 15, fontWeight: 700, fontFamily: 'inherit', lineHeight: 1 } }, '×')
    ),

    // ─── Edit Box Modal ───
    h(Modal, { open: !!editBox, onClose: cancelEditBox, title: editBox ? 'Edit Box ' + editBox.box_number : '', width: 900, maxHeight: 'calc(90vh * var(--invzoom, 1))' },
      editBox && h('div', null,
        // Transparency serial audit — which scanned serials were attributed to this box.
        boxTScans && boxTScans.length > 0 && h('div', { style: { marginBottom: 16 } },
          h('div', { style: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 } },
            h('img', { src: '/img/transparency-logo.png', alt: '', style: { width: 14, height: 14, borderRadius: 2 } }),
            h('span', { style: { fontSize: 13, fontWeight: 600, color: '#A78BFA' } }, 'Transparency serials in this box (' + boxTScans.length + ')')),
          h('div', { style: { maxHeight: 150, overflowY: 'auto', border: '1px solid rgba(167,139,250,0.35)', borderRadius: 6, padding: '6px 10px', fontSize: 11, fontFamily: 'inherit' } },
            boxTScans.map(function(s) {
              return h('div', { key: s.id, style: { display: 'flex', gap: 10, padding: '1px 0' } },
                h('span', { style: { color: C.accent, minWidth: 100, flexShrink: 0 } }, s.sku || ''),
                h('span', { style: { color: C.textMuted } }, s.code),
                s.scanned_by_name && h('span', { style: { color: C.textDim, marginLeft: 'auto', flexShrink: 0 } }, s.scanned_by_name));
            }))
        ),
        // Dimensions (under the title)
        h('div', { style: { marginBottom: 16 } },
          h('label', { style: { fontSize: 13, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', display: 'block', marginBottom: 6 } }, 'Dimensions'),
          h('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8 } },
            h(Inp, { label: 'L (in)', type: 'number', value: editBox.dims.length, onChange: function(e) { setEditBox(function(prev) { return Object.assign({}, prev, { dims: Object.assign({}, prev.dims, { length: e.target.value }) }); }); } }),
            h(Inp, { label: 'W (in)', type: 'number', value: editBox.dims.width, onChange: function(e) { setEditBox(function(prev) { return Object.assign({}, prev, { dims: Object.assign({}, prev.dims, { width: e.target.value }) }); }); } }),
            h(Inp, { label: 'H (in)', type: 'number', value: editBox.dims.height, onChange: function(e) { setEditBox(function(prev) { return Object.assign({}, prev, { dims: Object.assign({}, prev.dims, { height: e.target.value }) }); }); } }),
            h(Inp, { label: 'Wt (lbs)', type: 'number', value: editBox.dims.weight, onChange: function(e) { setEditBox(function(prev) { return Object.assign({}, prev, { dims: Object.assign({}, prev.dims, { weight: e.target.value }) }); }); } })
          )
        ),
        // Items header + add button
        h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 } },
          h('label', { style: { fontSize: 13, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase' } }, 'Items in Box (' + editBox.items.reduce(function(s, i) { return s + i.quantity; }, 0) + ' units)'),
          h('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
            editBoxAddOpen && h(Btn, { size: 'md', onClick: function() { setEditBoxAddOpen(false); } }, 'Cancel'),
            h(Btn, { variant: 'primary', size: 'md', onClick: function() { setEditBoxAddOpen(!editBoxAddOpen); } }, h(LIcon, { name: 'plus', size: 14, style: { marginRight: 2 } }), ' Add Item')
          )
        ),

        // Add item dropdown
        editBoxAddOpen && h('div', { style: { marginBottom: 10, padding: '8px 10px', background: C.surface, borderRadius: 6, border: '1px solid ' + C.border } },
          // Search header — same mechanism as the Step 1 packing search
          packSearchBar(editBoxSearch, setEditBoxSearch, 'Search plan items by SKU, FNSKU, barcode, product name, vendor, PO#...'),
          h('div', { style: { maxHeight: 360, overflowY: 'auto' } },
          (function() {
            var ps = function(bg, fg) { return { fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: bg, color: fg, whiteSpace: 'nowrap', display: 'inline-block' }; };
            var boxProductIds = editBox.items.map(function(i) { return i.product_id; });
            var addable = items.filter(function(i) { return boxProductIds.indexOf(i.product_id) === -1; });
            var q = editBoxSearch.toLowerCase().trim();
            var qPo = q.replace(/^po\s*#?\s*/, '');
            if (q) addable = addable.filter(function(item) {
              return (item.msku || '').toLowerCase().indexOf(q) >= 0 ||
                (item.fnsku || '').toLowerCase().indexOf(q) >= 0 ||
                (item.barcode || '').toLowerCase().indexOf(q) >= 0 ||
                (item.vendor_title || '').toLowerCase().indexOf(q) >= 0 ||
                (item.vendor_name || '').toLowerCase().indexOf(q) >= 0 ||
                (item.vendor_sku || '').toLowerCase().indexOf(q) >= 0 ||
                (item.product_sku || '').toLowerCase().indexOf(q) >= 0 ||
                (item.po_numbers || '').toLowerCase().indexOf(qPo) >= 0 ||
                (item.asin || '').toLowerCase().indexOf(q) >= 0;
            });
            addable.sort(function(a, b) { return String(a.product_sku || a.msku || '').localeCompare(String(b.product_sku || b.msku || ''), undefined, { numeric: true, sensitivity: 'base' }); });
            return addable.length === 0
              ? h('div', { style: { fontSize: 13, color: C.textMuted, textAlign: 'center', padding: 12 } }, q ? 'No items match "' + editBoxSearch + '"' : 'All plan items are already in this box')
              : addable.map(function(si) {
                  var thumbUrl = (si.images && si.images.length) ? si.images[0] : si.image_1_url;
                  return h('div', { key: si.id, onClick: function() { editBoxAddItem(si); },
                    style: { display: 'flex', alignItems: 'center', gap: 12, padding: '11px 0', borderBottom: '1px solid ' + C.border + '22', cursor: 'pointer' } },
                    thumbUrl
                      ? h('img', { src: thumbUrl, style: { width: 60, height: 60, borderRadius: 8, objectFit: 'cover', border: '1px solid ' + C.border, flexShrink: 0 } })
                      : h('div', { style: { width: 60, height: 60, borderRadius: 8, background: C.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } }, h(LIcon, { name: 'package', size: 24, color: C.textDim })),
                    h('div', { style: { flex: 1, minWidth: 0 } },
                      h('div', { style: { fontSize: 14, fontWeight: 600, marginBottom: 4 } }, si.vendor_title || si.msku),
                      h('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 2 } },
                        (si.product_sku || si.msku) && h('span', { style: ps(C.accent + '14', C.accent) }, 'SKU: ' + (si.product_sku || si.msku)),
                        si.vendor_sku && h('span', { style: ps(C.accent + '14', C.accent) }, 'VSKU: ' + si.vendor_sku),
                        si.barcode && h('span', { style: ps(C.accent + '14', C.accent) }, 'BARCODE: ' + si.barcode),
                        si.fnsku && h('span', { style: ps(C.accent + '14', C.accent) }, 'FNSKU: ' + si.fnsku),
                        si.requires_transparency && h(window.TransparencyPill)
                      ),
                      si.vendor_name && h('div', { style: { display: 'flex', gap: 3 } },
                        h('span', { style: ps('#F59E0B' + '18', '#B45309') }, si.vendor_name)
                      )
                    ),
                    h(LIcon, { name: 'plus-circle', size: 24, color: C.success, style: { flexShrink: 0 } })
                  );
                });
          })()
          )
        ),

        // Current items list (rich display) — searchable when the box has several items
        editBox.items.length > 3 && packSearchBar(editBoxItemSearch, setEditBoxItemSearch, 'Search items in this box by SKU, FNSKU, barcode, vendor...'),
        h('div', { style: { marginBottom: 10 } },
          editBox.items.length === 0
            ? h('div', { style: { fontSize: 13, color: C.textMuted, padding: '16px 0', textAlign: 'center' } }, 'No items in this box')
            : (function() {
                var ps = function(bg, fg) { return { fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: bg, color: fg, whiteSpace: 'nowrap', display: 'inline-block' }; };
                var iq = editBoxItemSearch.toLowerCase().trim();
                var rows = editBox.items.map(function(item, idx) { return { item: item, idx: idx }; }).filter(function(row) {
                  if (!iq) return true;
                  var it = row.item;
                  return (it.msku || '').toLowerCase().indexOf(iq) >= 0 ||
                    (it.fnsku || '').toLowerCase().indexOf(iq) >= 0 ||
                    (it.barcode || '').toLowerCase().indexOf(iq) >= 0 ||
                    (it.vendor_title || '').toLowerCase().indexOf(iq) >= 0 ||
                    (it.vendor_sku || '').toLowerCase().indexOf(iq) >= 0 ||
                    (it.vendor_name || '').toLowerCase().indexOf(iq) >= 0;
                });
                rows.sort(function(a, b) { return String(a.item.msku || '').localeCompare(String(b.item.msku || ''), undefined, { numeric: true, sensitivity: 'base' }); });
                if (rows.length === 0) return h('div', { style: { fontSize: 13, color: C.textMuted, padding: '12px 0', textAlign: 'center' } }, 'No items match "' + editBoxItemSearch + '"');
                return rows.map(function(row) {
                  var item = row.item; var idx = row.idx;
                  return h('div', { key: item.id || ('new-' + idx), style: { display: 'flex', alignItems: 'flex-start', gap: 12, padding: '11px 0', borderBottom: '1px solid ' + C.border + '33' } },
                    item.thumbUrl
                      ? h('img', { src: item.thumbUrl, style: { width: 60, height: 60, borderRadius: 8, objectFit: 'cover', border: '1px solid ' + C.border, flexShrink: 0 } })
                      : h('div', { style: { width: 60, height: 60, borderRadius: 8, background: C.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } }, h(LIcon, { name: 'package', size: 24, color: C.textDim })),
                    h('div', { style: { flex: 1, minWidth: 0 } },
                      h('div', { style: { fontSize: 14, fontWeight: 600, marginBottom: 4 } }, item.vendor_title || item.msku || item.fnsku),
                      h('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 2 } },
                        (item.msku) && h('span', { style: ps(C.accent + '14', C.accent) }, 'SKU: ' + item.msku),
                        item.vendor_sku && h('span', { style: ps(C.accent + '14', C.accent) }, 'VSKU: ' + item.vendor_sku),
                        item.barcode && h('span', { style: ps(C.accent + '14', C.accent) }, 'BARCODE: ' + item.barcode),
                        item.fnsku && h('span', { style: ps(C.accent + '14', C.accent) }, 'FNSKU: ' + item.fnsku),
                        item.requires_transparency && h(window.TransparencyPill)
                      ),
                      item.vendor_name && h('div', { style: { display: 'flex', gap: 3 } },
                        h('span', { style: ps('#F59E0B' + '18', '#B45309') }, item.vendor_name)
                      ),
                      item._isNew && h('span', { style: ps('#10B981' + '18', '#047857') }, 'NEW')
                    ),
                    h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 } },
                      h('input', { type: 'number', min: 1, value: item.quantity,
                        onChange: function(e) {
                          var val = Math.max(1, parseInt(e.target.value) || 1);
                          setEditBox(function(prev) {
                            var newItems = prev.items.map(function(it, i) { return i === idx ? Object.assign({}, it, { quantity: val }) : it; });
                            return Object.assign({}, prev, { items: newItems });
                          });
                        },
                        style: { width: 66, padding: '7px 8px', fontSize: 14, textAlign: 'center', border: '1px solid ' + C.border, borderRadius: 6, background: C.bg, color: C.text } }),
                      h('button', { onClick: function() { editBoxRemoveItem(idx); }, title: 'Remove',
                        style: { background: 'none', border: 'none', cursor: 'pointer', color: C.danger, padding: 4 } }, h(LIcon, { name: 'trash-2', size: 18 }))
                    )
                  );
                });
              })()
        ),

        // Removed items (undo)
        editBox.removed.length > 0 && h('div', { style: { marginBottom: 10, padding: '6px 10px', background: 'rgba(248,113,113,0.06)', borderRadius: 6, border: '1px solid rgba(248,113,113,0.15)' } },
          h('div', { style: { fontSize: 10, fontWeight: 600, color: '#F87171', marginBottom: 4, textTransform: 'uppercase' } }, 'Removed Items'),
          editBox.removed.map(function(entry, rIdx) {
            return h('div', { key: entry.id, style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0', fontSize: 11, color: C.textMuted, textDecoration: 'line-through' } },
              h('span', null, (entry.item.fnsku || entry.item.msku) + ' — ' + (entry.item.vendor_title || '')),
              h('button', { onClick: function() { editBoxUndoRemove(rIdx); },
                style: { background: 'none', border: 'none', cursor: 'pointer', color: C.accent, fontSize: 11, fontWeight: 600, fontFamily: 'inherit', padding: '2px 6px' } }, 'Undo')
            );
          })
        ),

        // Actions
        h('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } },
          h(Btn, { onClick: cancelEditBox }, 'Cancel'),
          h(Btn, { variant: 'primary', onClick: saveEditBox, disabled: editBoxSaving }, editBoxSaving ? 'Saving...' : 'Save Changes')
        )
      )
    ),

    // ─── Receive Unexpected Item Modal ───
    h(window.UnexpectedItemModal, {
      open: unexpectedOpen,
      onClose: function() { setUnexpectedOpen(false); },
      lookupUrl: '/fba-shipments/plans/' + planId + '/product-lookup',
      submitUrl: '/fba-shipments/plans/' + planId + '/unexpected-item',
      onOpenPlan: onNavigate ? function(sibId) { setUnexpectedOpen(false); onNavigate('fba-detail', sibId); } : null,
      onSuccess: function(resp) {
        showToast('Received ' + resp.qty_received + ' unit(s) of ' + resp.sku + ' on PO ' + resp.po_number, 'success');
        loadUnexpectedReceived();
        // Mid-pack (wizStep > 0): skip the full-plan onRefresh. It reloads the parent plan
        // (toggling its loading state), which remounts this packing view and wipes the
        // in-progress box. The unexpected item lands on the PO, not the shipment's packable
        // items, so no plan reload is needed here — the Unexpected tab was refreshed above.
        if (wizStep === 0 && onRefresh) onRefresh();
      },
    }),

    // ─── Unexpected Items Image Lightbox ───
    unexpLightbox && h(window.ImageLightbox, {
      images: unexpLightbox.images,
      index: unexpLightbox.index,
      onIndexChange: function(i) { setUnexpLightbox(function(prev) { return prev ? { images: prev.images, index: i } : null; }); },
      onClose: function() { setUnexpLightbox(null); },
    }),

    // ─── Mark Missing Confirmation Modal ───
    h(Modal, { open: !!missingConfirm, onClose: function() { setMissingConfirm(null); setMissingNote(''); }, title: 'Mark Units Missing', width: 440 },
      missingConfirm && (function() {
        var mc = missingConfirm;
        var remaining = mc.quantity - (mc.quantity_packed || 0) - (mc.quantity_missing || 0);
        var thumbUrl = (mc.images && mc.images.length) ? mc.images[0] : mc.image_1_url;
        var ps = function(bg, fg) { return { fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: bg, color: fg, letterSpacing: '0.03em', whiteSpace: 'nowrap', display: 'inline-block' }; };
        return h('div', null,
          h('div', { style: { display: 'flex', alignItems: 'center', gap: 10, padding: 12, background: C.surface, borderRadius: 6, marginBottom: 14 } },
            thumbUrl
              ? h('img', { src: thumbUrl, style: { width: 48, height: 48, borderRadius: 6, objectFit: 'cover', flexShrink: 0 } })
              : h('div', { style: { width: 48, height: 48, borderRadius: 6, background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } }, h(LIcon, { name: 'package', size: 22, color: C.textDim })),
            h('div', { style: { flex: 1, minWidth: 0 } },
              h('div', { style: { fontSize: 13, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 6 } }, mc.vendor_title || mc.msku || ''),
              h('div', { style: { display: 'flex', gap: 4, flexWrap: 'wrap' } },
                (mc.product_sku || mc.msku) && h('span', { style: ps(C.accent + '14', C.accent) }, 'SKU: ' + (mc.product_sku || mc.msku)),
                mc.fnsku && h('span', { style: ps(C.accent + '14', C.accent) }, 'FNSKU: ' + mc.fnsku)
              )
            )
          ),
          h('div', { style: { fontSize: 13, color: C.text, marginBottom: 6 } },
            'Mark ', h('strong', { style: { color: '#F87171' } }, remaining + ' unit' + (remaining === 1 ? '' : 's')), ' as missing?'
          ),
          h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 14 } },
            (plan && plan.shipment_type === 'LTL' && plan.packing_mode === 'individual')
              ? 'LTL Individual: Amazon’s expected shipment quantity stays locked and will not change. These units are tracked as missing internally and on the PO.'
              : 'These units will be excluded from this shipment and marked missing on the PO. You can unmark them later if recovered.'
          ),
          h('div', { style: { marginBottom: 16 } },
            h('label', { style: { display: 'block', fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 } }, 'Note (optional)'),
            h('textarea', {
              value: missingNote,
              onChange: function(e) { setMissingNote(e.target.value); },
              placeholder: 'Reason or context for the log',
              rows: 3,
              style: { width: '100%', background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }
            })
          ),
          h('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } },
            h(Btn, { onClick: function() { setMissingConfirm(null); setMissingNote(''); } }, 'Cancel'),
            h(Btn, { variant: 'primary', onClick: function() { markMissing(mc, missingNote); setMissingConfirm(null); setMissingNote(''); },
              style: { background: '#DC2626', borderColor: '#DC2626' } },
              h(LIcon, { name: 'alert-triangle', size: 14 }), ' Mark Missing')
          )
        );
      })()
    ),

    // ─── Errored-item Mark Missing Confirmation Modal ───
    h(Modal, { open: !!hzMissingConfirm, onClose: function() { if (!hzMissingSaving) { setHzMissingConfirm(null); setHzMissingNote(''); } }, title: 'Mark Errored Item Missing', width: 440 },
      hzMissingConfirm && (function() {
        var mc = hzMissingConfirm;
        var ps = function(bg, fg) { return { fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: bg, color: fg, letterSpacing: '0.03em', whiteSpace: 'nowrap', display: 'inline-block' }; };
        return h('div', null,
          h('div', { style: { display: 'flex', alignItems: 'center', gap: 10, padding: 12, background: C.surface, borderRadius: 6, marginBottom: 14 } },
            mc.image_url
              ? h('img', { src: mc.image_url, style: { width: 48, height: 48, borderRadius: 6, objectFit: 'cover', flexShrink: 0 } })
              : h('div', { style: { width: 48, height: 48, borderRadius: 6, background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } }, h(LIcon, { name: 'package', size: 22, color: C.textDim })),
            h('div', { style: { flex: 1, minWidth: 0 } },
              h('div', { style: { fontSize: 13, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 6 } }, mc.vendor_title || mc.msku || ''),
              h('div', { style: { display: 'flex', gap: 4, flexWrap: 'wrap' } },
                mc.msku && h('span', { style: ps(C.accent + '14', C.accent) }, 'SKU: ' + mc.msku),
                mc.fnsku && h('span', { style: ps(C.accent + '14', C.accent) }, 'FNSKU: ' + mc.fnsku)
              )
            )
          ),
          h('div', { style: { fontSize: 13, color: C.text, marginBottom: 6 } },
            'Mark ', h('strong', { style: { color: '#F87171' } }, (mc.quantity || 0) + ' unit' + ((mc.quantity || 0) === 1 ? '' : 's')), ' as missing?'
          ),
          h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 14 } },
            'This errored item was already removed from the Amazon plan. Marking it missing records the shortage on the PO line (like regular missing items) and resolves it here. You can undo later if the units turn up.'
          ),
          h('div', { style: { marginBottom: 16 } },
            h('label', { style: { display: 'block', fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 } }, 'Note (optional)'),
            h('textarea', {
              value: hzMissingNote,
              onChange: function(e) { setHzMissingNote(e.target.value); },
              placeholder: 'Reason or context for the log',
              rows: 3,
              style: { width: '100%', background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }
            })
          ),
          h('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } },
            h(Btn, { onClick: function() { setHzMissingConfirm(null); setHzMissingNote(''); }, disabled: hzMissingSaving }, 'Cancel'),
            h(Btn, { variant: 'primary', disabled: hzMissingSaving, onClick: function() { markErroredMissing(mc.msku, hzMissingNote); },
              style: { background: '#DC2626', borderColor: '#DC2626' } },
              h(LIcon, { name: 'alert-triangle', size: 14 }), ' ', hzMissingSaving ? 'Marking…' : 'Mark Missing')
          )
        );
      })()
    ),

    // ─── Bulk Mark Missing Confirmation Modal ───
    h(Modal, { open: !!bulkMissingConfirm, onClose: function() { if (!bulkMissingSaving) setBulkMissingConfirm(null); }, title: 'Mark Items as Missing', width: 480 },
      bulkMissingConfirm && (function() {
        var bm = bulkMissingConfirm;
        var totalUnits = bm.items.reduce(function(s, it) { return s + Math.max(0, it.quantity - (it.quantity_packed || 0) - (it.quantity_missing || 0)); }, 0);
        return h('div', null,
          h('div', { style: { fontSize: 13, color: C.text, marginBottom: 6 } },
            'Mark ', h('strong', { style: { color: '#F87171' } }, totalUnits + ' unit' + (totalUnits === 1 ? '' : 's')),
            ' across ', h('strong', null, bm.items.length + ' SKU' + (bm.items.length === 1 ? '' : 's')),
            ' as missing?'),
          h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12 } },
            (plan && plan.shipment_type === 'LTL' && plan.packing_mode === 'individual')
              ? 'LTL Individual: Amazon’s expected shipment quantity stays locked. These units are tracked as missing internally and on the PO.'
              : 'These units will be excluded from this shipment and marked missing on the PO. You can unmark them later if recovered.'),
          h('div', { style: { maxHeight: 200, overflow: 'auto', padding: '6px 10px', background: C.surface, borderRadius: 6, marginBottom: 14, fontSize: 11, color: C.textMuted } },
            bm.items.map(function(it) {
              var rem = Math.max(0, it.quantity - (it.quantity_packed || 0) - (it.quantity_missing || 0));
              return h('div', { key: it.id, style: { padding: '3px 0' } }, (it.product_sku || it.msku || '—') + ' — ' + rem + ' unit' + (rem === 1 ? '' : 's'));
            })
          ),
          h('div', { style: { marginBottom: 16 } },
            h('label', { style: { display: 'block', fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 } }, 'Shared note (optional)'),
            h('textarea', {
              value: bm.note,
              onChange: function(e) { var v = e.target.value; setBulkMissingConfirm(function(prev) { return prev ? Object.assign({}, prev, { note: v }) : prev; }); },
              placeholder: 'Reason or context (applied to every selected item)',
              rows: 2,
              style: { width: '100%', background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }
            })
          ),
          h('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } },
            h(Btn, { onClick: function() { setBulkMissingConfirm(null); }, disabled: bulkMissingSaving }, 'Cancel'),
            h(Btn, { variant: 'primary', disabled: bulkMissingSaving,
              onClick: function() { executeBulkMarkMissing(bm.items, bm.note); },
              style: { background: '#DC2626', borderColor: '#DC2626' } },
              h(LIcon, { name: 'alert-triangle', size: 14 }), ' ', bulkMissingSaving ? 'Marking…' : 'Mark Missing')
          )
        );
      })()
    ),

    // ─── Delete Box Confirmation Modal ───
    h(Modal, { open: !!deleteBoxConfirm, onClose: function() { setDeleteBoxConfirm(null); }, title: 'Confirm Box Deletion', width: 400 },
      deleteBoxConfirm && h('div', null,
        h('div', { style: { display: 'flex', alignItems: 'center', gap: 10, padding: 12, background: C.surface, borderRadius: 6, marginBottom: 14 } },
          h('div', { style: { width: 40, height: 40, borderRadius: 6, background: '#F8717118', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } },
            h(LIcon, { name: 'trash-2', size: 20, color: '#F87171' })),
          h('div', { style: { flex: 1, minWidth: 0 } },
            h('div', { style: { fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 2 } }, 'Box ' + deleteBoxConfirm.boxNum),
            h('div', { style: { fontSize: 12, color: C.textMuted } }, deleteBoxConfirm.unitCount + ' unit' + (deleteBoxConfirm.unitCount === 1 ? '' : 's') + ' / ' + deleteBoxConfirm.skuCount + ' SKU' + (deleteBoxConfirm.skuCount === 1 ? '' : 's'))
          )
        ),
        h('div', { style: { fontSize: 13, color: C.text, marginBottom: 16 } },
          'Delete this box? Its contents will be returned to the shipment for repacking.'
        ),
        h('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } },
          h(Btn, { onClick: function() { setDeleteBoxConfirm(null); } }, 'Cancel'),
          h(Btn, { variant: 'primary', onClick: confirmDeleteBox,
            style: { background: '#DC2626', borderColor: '#DC2626' } },
            h(LIcon, { name: 'trash-2', size: 14 }), ' Delete Box')
        )
      )
    ),

    // ─── Resume in-progress box draft prompt ───
    h(Modal, { open: !!draftPrompt, onClose: function() { setDraftPrompt(null); setDraftDiscardConfirm(false); }, title: 'Resume in-progress box?', width: 460 },
      draftPrompt && (function() {
        var ago = '';
        if (draftPrompt.updated_at) {
          var ms = Date.now() - new Date(draftPrompt.updated_at).getTime();
          if (!isNaN(ms) && ms >= 0) {
            var mins = Math.floor(ms / 60000);
            ago = mins < 1 ? 'just now' : mins < 60 ? (mins + ' min ago')
              : mins < 1440 ? (Math.floor(mins / 60) + ' hr ago') : (Math.floor(mins / 1440) + ' d ago');
          }
        }
        return h('div', null,
          h('div', { style: { fontSize: 13, color: C.text, marginBottom: 12 } },
            'You have an unfinished box for this shipment' + (ago ? ' (saved ' + ago + ')' : '') + '. Resume it, or start a new empty box?'),
          h('div', { style: { background: C.bg, border: '1px solid ' + C.border, borderRadius: 6, padding: 10, marginBottom: 16, maxHeight: 220, overflowY: 'auto' } },
            draftPrompt.data.lines.map(function(ln, i) {
              return h('div', { key: i, style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, padding: '4px 0', borderTop: i ? '1px solid ' + C.border : 'none' } },
                h('span', { style: { fontFamily: 'inherit', color: C.text } }, ln.label),
                h('span', { style: { fontWeight: ln.qty != null ? 700 : 500, color: ln.qty != null ? C.text : C.textMuted, fontStyle: ln.qty != null ? 'normal' : 'italic' } },
                  ln.qty != null ? ('× ' + ln.qty) : 'qty not set'));
            })),
          // Discarding a draft is the one destructive action here — it needs a second,
          // explicit click. (Cancel elsewhere is safe: the draft + scans persist.)
          draftDiscardConfirm && h('div', { style: { display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', borderRadius: 6, background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.35)', marginBottom: 12, fontSize: 12.5, color: '#F87171', fontWeight: 600, lineHeight: 1.5 } },
            h(LIcon, { name: 'alert-triangle', size: 15, style: { flexShrink: 0, marginTop: 1 } }),
            'This permanently deletes the saved draft (selections, quantities, dimensions). Scanned Transparency serials are NOT deleted — they stay "scanned, not boxed" and will count toward the next box.'),
          draftDiscardConfirm
            ? h('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } },
                h(Btn, { onClick: function() { setDraftDiscardConfirm(false); } }, 'Back'),
                h(Btn, { onClick: function() {
                  setDraftDiscardConfirm(false);
                  api('/fba-shipments/shipments/' + shipmentId + '/box-draft', { method: 'DELETE' }).catch(function() {});
                  setRawDraft(null);
                  setSelectedItems({}); setPackQtys({}); setNumCopies(1);
                  setDimForm({ length: '', width: '', height: '', weight: '' });
                  setItemSearch(''); setDraftPrompt(null); setWizStep(1);
                }, style: { background: 'rgba(248,113,113,0.15)', color: '#F87171', border: '1px solid rgba(248,113,113,0.4)' } },
                  h(LIcon, { name: 'trash-2', size: 14 }), ' Discard draft'))
            : h('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } },
            h(Btn, { onClick: function() { setDraftDiscardConfirm(true); } }, 'Start Fresh'),
            h(Btn, { variant: 'primary', onClick: function() {
              var d = draftPrompt.data;
              // A draft can belong to another packing group — snap the active group to
              // the draft's items so the resumed selection is visible, not orphaned.
              if (packGroups && onSwitchGroup) {
                for (var dk in d.selectedItems) {
                  if (!d.selectedItems[dk]) continue;
                  var dit = items.find(function(i) { return String(i.id) === String(dk); });
                  var dg = groupOfItem(dit);
                  if (dg) { onSwitchGroup(dg - 1); break; }
                }
              }
              setSelectedItems(d.selectedItems); setPackQtys(d.packQtys); setNumCopies(d.numCopies);
              setDimForm(d.dimForm || { length: '', width: '', height: '', weight: '' });
              // Resume lands on Verify & Qty (items are already selected; Back returns
              // to selection) — entering that step also re-seeds the Transparency station.
              setItemSearch(''); setDraftPrompt(null); setWizStep(2);
            } }, h(LIcon, { name: 'rotate-ccw', size: 14 }), ' Resume')
          )
        );
      })()
    ),

    // ─── Duplicate Box Confirmation Modal ───
    h(Modal, { open: !!duplicateBoxModal, onClose: function() { if (!duplicateSaving) setDuplicateBoxModal(null); }, title: 'Duplicate Box', width: 400 },
      duplicateBoxModal && (function() {
        var max = duplicateBoxModal.maxCopies;
        var n = parseInt(duplicateCount, 10);
        var valid = max >= 1 && n >= 1 && n <= max;
        return h('div', null,
          h('div', { style: { display: 'flex', alignItems: 'center', gap: 10, padding: 12, background: C.surface, borderRadius: 6, marginBottom: 14 } },
            h('div', { style: { width: 40, height: 40, borderRadius: 6, background: C.accent + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } },
              h(LIcon, { name: 'copy', size: 20, color: C.accent })),
            h('div', { style: { flex: 1, minWidth: 0 } },
              h('div', { style: { fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 2 } }, 'Box ' + duplicateBoxModal.boxNum),
              h('div', { style: { fontSize: 12, color: C.textMuted } }, duplicateBoxModal.unitCount + ' unit' + (duplicateBoxModal.unitCount === 1 ? '' : 's') + ' / ' + duplicateBoxModal.skuCount + ' SKU' + (duplicateBoxModal.skuCount === 1 ? '' : 's'))
            )
          ),
          max < 1
            ? h('div', { style: { fontSize: 12, color: '#F87171', padding: '10px 12px', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.2)', borderRadius: 6, marginBottom: 16 } },
                'No remaining capacity — all units for SKUs in this box are already packed or marked missing.')
            : h('div', { style: { marginBottom: 16 } },
                h('label', { style: { display: 'block', fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 } }, 'Number of copies'),
                h('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
                  h('input', {
                    type: 'number', min: 1, max: max, value: duplicateCount,
                    onChange: function(e) {
                      var v = parseInt(e.target.value, 10);
                      if (isNaN(v)) { setDuplicateCount(''); return; }
                      if (v < 1) v = 1;
                      if (v > max) v = max;
                      setDuplicateCount(v);
                    },
                    style: { width: 90, background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }
                  }),
                  h('span', { style: { fontSize: 12, color: C.textMuted } }, 'Max: ', h('strong', { style: { color: C.text } }, max))
                )
              ),
          h('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } },
            h(Btn, { onClick: function() { setDuplicateBoxModal(null); }, disabled: duplicateSaving }, 'Cancel'),
            h(Btn, { variant: 'primary', onClick: confirmDuplicateBox, disabled: !valid || duplicateSaving },
              h(LIcon, { name: 'copy', size: 14 }), ' ', duplicateSaving ? 'Duplicating…' : 'Duplicate')
          )
        );
      })()
    ),

    // ─── Image Lightbox ───
    lightbox && h('div', { onClick: function() { setLightbox(null); },
      style: { position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' } },
      // Close button
      h('button', { onClick: function() { setLightbox(null); },
        style: { position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 28, lineHeight: 1, padding: 8, zIndex: 10001 } }, '\u00D7'),
      // Counter
      lightbox.images.length > 1 && h('div', { style: { position: 'absolute', top: 20, left: '50%', transform: 'translateX(-50%)', color: '#fff', fontSize: 13, fontWeight: 600, zIndex: 10001 } },
        (lightbox.index + 1) + ' / ' + lightbox.images.length),
      // Prev arrow
      lightbox.images.length > 1 && h('button', { onClick: function(e) { e.stopPropagation(); setLightbox(function(prev) { return { images: prev.images, index: (prev.index - 1 + prev.images.length) % prev.images.length }; }); },
        style: { position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 24, fontWeight: 700, width: 44, height: 44, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001 } }, '\u2039'),
      // Next arrow
      lightbox.images.length > 1 && h('button', { onClick: function(e) { e.stopPropagation(); setLightbox(function(prev) { return { images: prev.images, index: (prev.index + 1) % prev.images.length }; }); },
        style: { position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 24, fontWeight: 700, width: 44, height: 44, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001 } }, '\u203A'),
      // Main image
      h('img', { src: lightbox.images[lightbox.index], onClick: function(e) { e.stopPropagation(); },
        style: { maxWidth: 'calc(85vw * var(--invzoom, 1))', maxHeight: 'calc(85vh * var(--invzoom, 1))', objectFit: 'contain', borderRadius: 8, cursor: 'default' } }),
      // Thumbnail strip
      lightbox.images.length > 1 && h('div', { onClick: function(e) { e.stopPropagation(); },
        style: { position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 8, padding: '8px 12px', background: 'rgba(0,0,0,0.6)', borderRadius: 8 } },
        lightbox.images.map(function(url, i) {
          return h('img', { key: i, src: url, onClick: function() { setLightbox(function(prev) { return { images: prev.images, index: i }; }); },
            style: { width: 52, height: 52, objectFit: 'cover', borderRadius: 6, border: '2px solid ' + (i === lightbox.index ? '#fff' : 'transparent'), cursor: 'pointer', opacity: i === lightbox.index ? 1 : 0.6, transition: 'all 0.15s' } });
        })
      )
    )
  );
}

// ─── Expose to window ───
window.FbaShipmentsPage = FbaShipmentsPage;
window.FbaDetailPage = FbaDetailPage;
window.FbaBoxPackingView = FbaBoxPackingView;
window.FbaBadge = FbaBadge;

;
/* ===== app-recon.js ===== */
// ─── FBA Reconciliation Module (docs/16-fba-reconciliation.md) ───
// FbaReconPage — shipped-vs-received queue, per-shipment ladder modal with approvals
// (Units not Shipped / Unexpected overage sign-offs) and claim-case tracking.
// Filing itself happens in Seller Central (no API); cases record + track the outcome.

var { createElement: h, Fragment } = React;

var reconStateMeta = {
  action_needed:  { label: 'Action Needed', bg: 'rgba(239,68,68,0.12)',   text: '#EF4444' },
  pending_review: { label: 'Pending Review', bg: 'rgba(245,158,11,0.12)',  text: '#F59E0B' },
  watching:       { label: 'Watching',       bg: 'rgba(59,130,246,0.12)',  text: '#3B82F6' },
  expired:        { label: 'Expired',        bg: 'rgba(148,163,184,0.12)', text: '#94A3B8' },
  in_case:        { label: 'In Case',       bg: 'rgba(99,102,241,0.12)',  text: '#6366F1' },
  settled_ok:     { label: 'Settled',        bg: 'rgba(16,185,129,0.12)',  text: '#10B981' },
  resolved:       { label: 'Resolved',       bg: 'rgba(16,185,129,0.12)',  text: '#10B981' },
};
var reconCaseMeta = {
  draft:      { label: 'Draft',      bg: 'rgba(148,163,184,0.12)', text: '#94A3B8' },
  filed:      { label: 'Filed',      bg: 'rgba(59,130,246,0.12)',  text: '#3B82F6' },
  partial:    { label: 'Partial',    bg: 'rgba(245,158,11,0.12)',  text: '#F59E0B' },
  reimbursed: { label: 'Reimbursed', bg: 'rgba(16,185,129,0.12)',  text: '#10B981' },
  denied:     { label: 'Denied',     bg: 'rgba(239,68,68,0.12)',   text: '#EF4444' },
  closed:     { label: 'Closed',     bg: 'rgba(148,163,184,0.12)', text: '#94A3B8' },
};

function ReconStateBadge({ state, map }) {
  var m = (map || reconStateMeta)[state] || { label: state || '—', bg: 'rgba(148,163,184,0.12)', text: '#94A3B8' };
  return h('span', { style: { display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: m.bg, color: m.text, whiteSpace: 'nowrap' } }, m.label);
}

function reconFmtMoney(n) {
  var v = Number(n || 0);
  return '$' + v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function reconFmtDate(d) {
  if (!d) return '—';
  var t = new Date(d);
  return isNaN(t.getTime()) ? '—' : t.toLocaleDateString();
}
var RECON_MKT = { ATVPDKIKX0DER: 'US', A2EUQ1WTGCTBG2: 'CA' };

// Days until the claim deadline, color-ramped. Null delivery date → unknown deadline.
function ReconDeadline({ row }) {
  if (!row.claim_deadline_at) {
    return h('span', { title: 'No delivery date known — verify eligibility in Seller Central', style: { color: C.textDim, cursor: 'help' } }, '—');
  }
  var days = Math.floor((new Date(row.claim_deadline_at).getTime() - Date.now()) / 86400000);
  var color = days < 0 ? C.textDim : days <= 30 ? '#EF4444' : days <= 60 ? '#F59E0B' : C.textMuted;
  var label = days < 0 ? 'passed' : days + 'd left';
  return h('span', { title: 'Claim by ' + reconFmtDate(row.claim_deadline_at) + ' (delivery + 9 months)', style: { color: color, fontWeight: days >= 0 && days <= 30 ? 700 : 500, cursor: 'help' } }, label);
}

function ReconStatCard({ icon, label, value, sub, color }) {
  return h('div', { style: { flex: '1 1 160px', minWidth: 160, background: C.surface, border: '1px solid ' + C.border, borderRadius: 10, padding: '14px 16px' } },
    h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 } },
      h(LIcon, { name: icon, size: 15, color: color || C.accent }),
      h('span', { style: { fontSize: 12, color: C.textMuted, fontWeight: 500 } }, label)
    ),
    h('div', { style: { fontSize: 20, fontWeight: 700, color: color || C.text } }, value),
    sub && h('div', { style: { fontSize: 11, color: C.textDim, marginTop: 2 } }, sub)
  );
}

function FbaReconPage({ showToast, onNavigate, currentUser }) {
  var [rows, setRows] = useState([]);
  var [summary, setSummary] = useState(null);
  var [loading, setLoading] = useState(true);
  var [stateFilter, setStateFilter] = useState('open'); // open | action_needed | pending_review | watching | expired | settled | ''
  var [mktFilter, setMktFilter] = useState('');
  var [minExposure, setMinExposure] = useState(''); // noise floor — user-chosen filter, not a config gate
  var [search, setSearch] = useState('');
  var [detail, setDetail] = useState(null); // { row, data: { items, cases } | null }
  var [fileForm, setFileForm] = useState(null); // { case_id, units, amount, notes } | null
  var [caseAmt, setCaseAmt] = useState(null); // { id, val, status } — inline recovered-$ input
  var [backfill, setBackfill] = useState(null);
  var [backfillConfirm, setBackfillConfirm] = useState(false);
  var [busy, setBusy] = useState(false);
  var isAdmin = currentUser && currentUser.role === 'admin';
  var canWrite = hasPermission(currentUser, 'fba_reconciliation', 'write');

  var load = useCallback(function() {
    setLoading(true);
    Promise.all([
      api('/fba-reconciliation'),
      api('/fba-reconciliation/summary'),
      api('/fba-reconciliation/backfill/status'),
    ]).then(function(res) {
      setRows(res[0] || []); setSummary(res[1] || null); setBackfill(res[2] || null);
    }).catch(function(e) { showToast(e.message, 'error'); }).finally(function() { setLoading(false); });
  }, []);
  useEffect(function() { load(); }, [load]);

  // Poll while a backfill runs; reload the queue when it finishes.
  useEffect(function() {
    if (!backfill || !backfill.running) return;
    var t = setInterval(function() {
      api('/fba-reconciliation/backfill/status').then(function(st) {
        setBackfill(st);
        if (st && !st.running) {
          clearInterval(t);
          showToast(st.phase === 'done' ? 'Backfill complete' : 'Backfill ' + (st.phase || 'stopped'), st.phase === 'done' ? 'success' : 'error');
          load();
        }
      }).catch(function() {});
    }, 5000);
    return function() { clearInterval(t); };
  }, [backfill && backfill.running]);

  var startBackfill = function() {
    setBusy(true);
    api('/fba-reconciliation/backfill', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ months_back: 18 }) })
      .then(function() {
        setBackfillConfirm(false);
        setBackfill({ running: true, phase: 'starting' });
        showToast('Backfill started — this runs in the background (roughly 30–60 min)');
      })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setBusy(false); });
  };

  var recompute = function() {
    setBusy(true);
    api('/fba-reconciliation/recompute', { method: 'POST' })
      .then(function() { showToast('Recomputed'); load(); })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setBusy(false); });
  };

  var refreshDetail = function(row) {
    api('/fba-reconciliation/shipments/' + encodeURIComponent(row.amazon_shipment_id))
      .then(function(data) { setDetail(function(d) { return d && d.row.amazon_shipment_id === row.amazon_shipment_id ? { row: row, data: data } : d; }); })
      .catch(function(e) { showToast(e.message, 'error'); setDetail(null); });
  };
  var openDetail = function(row) {
    setFileForm(null); setCaseAmt(null);
    setDetail({ row: row, data: null });
    refreshDetail(row);
  };
  var postAction = function(row, path, body, okMsg) {
    setBusy(true);
    api(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) })
      .then(function() { if (okMsg) showToast(okMsg); refreshDetail(row); load(); })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setBusy(false); });
  };
  var putCase = function(row, caseId, body, okMsg) {
    setBusy(true);
    api('/fba-reconciliation/cases/' + caseId, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      .then(function() { showToast(okMsg || 'Case updated'); setCaseAmt(null); refreshDetail(row); load(); })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setBusy(false); });
  };

  // ─── Filters (client-side, list stays in the hundreds) ───
  var stateCounts = {};
  rows.forEach(function(r) { stateCounts[r.state] = (stateCounts[r.state] || 0) + 1; });
  var openStates = { action_needed: 1, pending_review: 1, watching: 1, in_case: 1 };
  var statePills = [
    { id: 'open', label: 'Open', count: rows.filter(function(r) { return openStates[r.state]; }).length },
    { id: 'action_needed', label: 'Action Needed', count: stateCounts.action_needed || 0 },
    { id: 'pending_review', label: 'Pending Review', count: stateCounts.pending_review || 0 },
    { id: 'in_case', label: 'In Case', count: stateCounts.in_case || 0 },
    { id: 'watching', label: 'Watching', count: stateCounts.watching || 0 },
    { id: 'expired', label: 'Expired', count: stateCounts.expired || 0 },
    { id: 'settled', label: 'Settled', count: (stateCounts.settled_ok || 0) + (stateCounts.resolved || 0) },
    { id: '', label: 'All', count: rows.length },
  ];

  var q = search.trim().toLowerCase();
  var minExp = parseFloat(minExposure) || 0;
  var filtered = rows.filter(function(r) {
    if (stateFilter === 'open' && !openStates[r.state]) return false;
    if (stateFilter === 'settled' && r.state !== 'settled_ok' && r.state !== 'resolved') return false;
    if (stateFilter && stateFilter !== 'open' && stateFilter !== 'settled' && r.state !== stateFilter) return false;
    if (mktFilter && RECON_MKT[r.marketplace_id] !== mktFilter) return false;
    if (minExp > 0 && Number(r.exposure || 0) < minExp) return false;
    if (q) {
      var hay = ((r.amazon_shipment_id || '') + ' ' + (r.shipment_name || '') + ' ' + (r.destination_fc || '')).toLowerCase();
      if (hay.indexOf(q) === -1) return false;
    }
    return true;
  });

  var columns = [
    { key: 'shipment', label: 'Shipment', locked: true,
      sortValue: function(r) { return (r.shipment_name || r.amazon_shipment_id || '').toLowerCase(); },
      render: function(r) {
        return h('div', { style: { minWidth: 180 } },
          h('div', { style: { fontWeight: 600, color: C.text, display: 'flex', alignItems: 'center', gap: 6 } },
            h('span', null, r.shipment_name || r.amazon_shipment_id),
            r.data_source === 'backfill' && h('span', { title: 'Created outside the app (found by backfill) — no pack data; claimable = declared − received', style: { fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 8, background: 'rgba(148,163,184,0.15)', color: C.textMuted, cursor: 'help' } }, 'external')
          ),
          h('div', { style: { fontSize: 11, color: C.textDim, marginTop: 2 } }, r.amazon_shipment_id)
        );
      }
    },
    { key: 'marketplace', label: 'Mkt', render: function(r) { return RECON_MKT[r.marketplace_id] || '—'; } },
    { key: 'destination_fc', label: 'FC', render: function(r) { return r.destination_fc || '—'; } },
    { key: 'shipment_status', label: 'Amazon Status', render: function(r) { return h('span', { style: { textTransform: 'lowercase', color: C.textMuted } }, String(r.shipment_status || '—').replace(/_/g, ' ')); } },
    { key: 'delivered_at', label: 'Delivered', sortValue: function(r) { return r.delivered_at ? new Date(r.delivered_at).getTime() : 0; }, render: function(r) { return reconFmtDate(r.delivered_at); } },
    { key: 'closed_at', label: 'Closed', defaultVisible: false, sortValue: function(r) { return r.closed_at ? new Date(r.closed_at).getTime() : 0; }, render: function(r) { return reconFmtDate(r.closed_at); } },
    { key: 'deadline', label: 'Claim By', sortValue: function(r) { return r.claim_deadline_at ? new Date(r.claim_deadline_at).getTime() : Infinity; }, render: function(r) { return h(ReconDeadline, { row: r }); } },
    { key: 'sku_count', label: 'SKUs', defaultVisible: false, sortValue: function(r) { return r.sku_count || 0; }, render: function(r) { return r.sku_count || 0; } },
    { key: 'short_expected', label: 'Not Shipped', sortValue: function(r) { return r.short_expected || 0; },
      render: function(r) { return r.short_expected > 0 ? h('span', { title: 'Declared to Amazon but never shipped (known shorts) — never claimable', style: { color: '#F59E0B', fontWeight: 600, cursor: 'help' } }, r.short_expected) : h('span', { style: { color: C.textDim } }, '—'); } },
    { key: 'short_claimable', label: 'Claimable Short', sortValue: function(r) { return (r.short_claimable || 0) - (r.short_offset || 0); },
      render: function(r) {
        var net = (r.short_claimable || 0) - (r.short_offset || 0);
        if (net > 0) return h('span', { title: 'Shipped but not received by Amazon' + (r.short_offset > 0 ? ' — +' + r.short_offset + ' more offset by sibling-shipment overages' : ''), style: { color: '#EF4444', fontWeight: 700, cursor: 'help' } }, net);
        if (r.short_offset > 0) return h('span', { title: r.short_offset + ' unit(s) short here but fully covered by sibling-shipment overages (plan-level net zero) — not claimable', style: { color: C.textDim, cursor: 'help' } }, '0 ⇄' + r.short_offset);
        return h('span', { style: { color: C.textDim } }, '—');
      } },
    { key: 'overage', label: 'Overage', sortValue: function(r) { return r.overage || 0; },
      render: function(r) { return r.overage > 0 ? h('span', { title: 'Amazon received more than declared', style: { color: '#6366F1', fontWeight: 600, cursor: 'help' } }, '+' + r.overage) : h('span', { style: { color: C.textDim } }, '—'); } },
    { key: 'exposure', label: 'Exposure', sortValue: function(r) { return Number(r.exposure || 0); },
      render: function(r) {
        var v = Number(r.exposure || 0);
        if (v <= 0 && !(r.uncosted_skus > 0)) return h('span', { style: { color: C.textDim } }, '—');
        var approx = r.uncosted_skus > 0;
        return h('span', { title: approx ? r.uncosted_skus + ' SKU(s) with claimable units have no known cost — exposure is understated' : 'Claimable units × PO unit cost', style: { fontWeight: 700, color: v > 0 ? '#EF4444' : C.textMuted, cursor: 'help' } }, (approx ? '≈ ' : '') + reconFmtMoney(v));
      } },
    { key: 'approvals_pending', label: 'Review', sortValue: function(r) { return r.approvals_pending || 0; },
      render: function(r) { return r.approvals_pending > 0 ? h('span', { title: 'Classification sign-offs awaiting approval', style: { fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: 'rgba(245,158,11,0.15)', color: '#F59E0B', cursor: 'help' } }, r.approvals_pending) : h('span', { style: { color: C.textDim } }, '—'); } },
    { key: 'state', label: 'State', sortValue: function(r) { return r.state_rank || 0; }, render: function(r) { return h(ReconStateBadge, { state: r.state }); } },
  ];

  var runningPhase = backfill && backfill.running ? (backfill.phase || 'running') : null;

  // ─── Detail modal internals ───
  var detailBody = null;
  if (detail) {
    var items = detail.data ? detail.data.items : null;
    var cases = detail.data ? detail.data.cases : [];
    var claimableNow = (items || []).filter(function(it) {
      return it.short_claimable > 0 && !it.sibling_offset && ['watching', 'pending_review', 'action_needed', 'expired'].indexOf(it.state) >= 0;
    });
    var sumClaimUnits = claimableNow.reduce(function(a, it) { return a + (it.short_claimable || 0); }, 0);
    var sumClaimAmt = claimableNow.reduce(function(a, it) { return a + (it.short_claimable || 0) * Number(it.unit_cost || 0); }, 0);
    var scHost = detail.row.marketplace_id === 'A2EUQ1WTGCTBG2' ? 'https://sellercentral.amazon.ca' : 'https://sellercentral.amazon.com';
    var scUrl = scHost + '/gp/fba/inbound-shipment-workflow/index.html#' + encodeURIComponent(detail.row.amazon_shipment_id) + '/summary/reconcile';
    var thCell = function(hd, i) {
      return h('th', { key: hd, style: { textAlign: i === 0 ? 'left' : 'right', padding: '8px 10px', borderBottom: '1px solid ' + C.border, color: C.textMuted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', whiteSpace: 'nowrap' } }, hd);
    };
    var heads = ['SKU', 'Declared', 'Packed', 'Not Shipped', 'Received', 'Claimable', 'Overage', 'Unit Cost', 'Exposure', 'State'];
    if (canWrite) heads.push('Actions');

    detailBody = h('div', { style: { display: 'flex', flexDirection: 'column', gap: 12 } },
      h('div', { style: { display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', fontSize: 12, color: C.textMuted } },
        h('span', null, 'Shipment ID: ', h(CopyCell, { value: detail.row.amazon_shipment_id })),
        h('span', null, 'FC: ' + (detail.row.destination_fc || '—')),
        h('span', null, 'Delivered: ' + reconFmtDate(detail.row.delivered_at)),
        h('span', null, 'Closed: ' + reconFmtDate(detail.row.closed_at)),
        h('span', null, 'Claim by: ' + reconFmtDate(detail.row.claim_deadline_at)),
        h('div', { style: { flex: 1 } }),
        h('a', { href: scUrl, target: '_blank', rel: 'noopener', style: { textDecoration: 'none' } },
          h(Btn, { size: 'sm' }, h(LIcon, { name: 'external-link', size: 13 }), ' Reconcile in Seller Central')),
        detail.row.plan_id && h(Btn, { size: 'sm', onClick: function() { setDetail(null); onNavigate('fba-detail', detail.row.plan_id); } }, h(LIcon, { name: 'package-check', size: 13 }), ' Open Plan')
      ),
      detail.row.data_source === 'backfill' && h('div', { style: { fontSize: 12, color: '#F59E0B', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 8, padding: '8px 12px' } },
        'This shipment was created outside the app, so there is no box-level pack data. Claimable = declared − received (Amazon’s own Reconcile view); unit costs fall back to current product cost.'),
      !items ? h('div', { style: { color: C.textMuted, padding: 20, textAlign: 'center' } }, 'Loading…') :
      h('div', { style: { overflowX: 'auto' } },
        h('table', { style: { width: '100%', borderCollapse: 'collapse', fontSize: 13 } },
          h('thead', null, h('tr', null, heads.map(thCell))),
          h('tbody', null, items.map(function(it) {
            var exp = (it.sibling_offset ? 0 : Number(it.short_claimable || 0)) * Number(it.unit_cost || 0);
            var right = { textAlign: 'right', padding: '7px 10px', borderBottom: '1px solid ' + C.border, whiteSpace: 'nowrap' };
            var actions = [];
            if (canWrite) {
              if (it.not_shipped_status === 'proposed') {
                actions.push(h(Btn, { key: 'ns-ok', size: 'sm', variant: 'success', disabled: busy,
                  onClick: function() { postAction(detail.row, '/fba-reconciliation/items/' + it.id + '/not-shipped', { action: 'approve' }, 'Confirmed: units not shipped'); } },
                  'Confirm not shipped'));
                actions.push(h(Btn, { key: 'ns-no', size: 'sm', disabled: busy,
                  onClick: function() { postAction(detail.row, '/fba-reconciliation/items/' + it.id + '/not-shipped', { action: 'reclassify_shipped' }, 'Reclassified — units moved to claimable'); } },
                  'We shipped these'));
              } else if (it.not_shipped_status === 'approved') {
                actions.push(h('span', { key: 'ns-done', style: { fontSize: 11, color: '#10B981', whiteSpace: 'nowrap' } }, '✓ not-shipped'));
              } else if (it.not_shipped_status === 'reclassified_shipped') {
                actions.push(h('span', { key: 'ns-re', style: { fontSize: 11, color: C.textMuted, whiteSpace: 'nowrap' } }, 'reclassified → claimable'));
              }
              if (it.overage > 0 && (it.overage_status === 'proposed_unexpected' || it.overage_status === 'none')) {
                actions.push(h(Btn, { key: 'ov', size: 'sm', disabled: busy,
                  onClick: function() { postAction(detail.row, '/fba-reconciliation/items/' + it.id + '/overage', {}, 'Overage marked unexpected'); } },
                  'Mark unexpected'));
              } else if (it.overage > 0) {
                actions.push(h('span', { key: 'ov-done', style: { fontSize: 11, color: '#10B981', whiteSpace: 'nowrap' } }, '✓ unexpected'));
              }
              if (['action_needed', 'expired'].indexOf(it.state) >= 0 && it.short_claimable > 0) {
                actions.push(h(Btn, { key: 'wo', size: 'sm', variant: 'ghost', disabled: busy,
                  onClick: function() { if (window.confirm('Write off ' + it.short_claimable + ' unit(s) of ' + it.msku + '? No claim will be filed for them.')) postAction(detail.row, '/fba-reconciliation/items/' + it.id + '/resolve', { reason: 'written_off' }, 'Written off'); } },
                  'Write off'));
              }
            }
            return h('tr', { key: it.id },
              h('td', { style: { padding: '7px 10px', borderBottom: '1px solid ' + C.border } },
                h('div', { style: { fontWeight: 600, color: C.text } }, it.msku),
                h('div', { style: { fontSize: 11, color: C.textDim } }, (it.product_name || '').slice(0, 60) || (it.fnsku || ''))),
              h('td', { style: right }, it.qty_declared),
              h('td', { style: right }, it.pack_authoritative ? it.qty_packed : h('span', { title: 'No authoritative pack data', style: { color: C.textDim, cursor: 'help' } }, '—')),
              h('td', { style: Object.assign({}, right, { color: it.short_expected > 0 ? '#F59E0B' : C.textDim, fontWeight: it.short_expected > 0 ? 600 : 400 }) }, it.short_expected || '—'),
              h('td', { style: right }, it.qty_received),
              h('td', { style: Object.assign({}, right, { color: it.short_claimable > 0 && !it.sibling_offset ? '#EF4444' : C.textDim, fontWeight: it.short_claimable > 0 && !it.sibling_offset ? 700 : 400 }) },
                it.short_claimable ? (it.sibling_offset ? h('span', { title: 'Offset by sibling-shipment overage (plan-level net zero) — not claimable', style: { cursor: 'help' } }, it.short_claimable + ' ⇄') : it.short_claimable) : '—'),
              h('td', { style: Object.assign({}, right, { color: it.overage > 0 && !it.sibling_offset ? '#6366F1' : C.textDim }) },
                it.overage > 0 ? (it.sibling_offset ? h('span', { title: 'Offsets sibling-shipment shorts (plan-level net zero)', style: { cursor: 'help' } }, '+' + it.overage + ' ⇄') : '+' + it.overage) : '—'),
              h('td', { style: Object.assign({}, right, { color: C.textMuted }) },
                it.unit_cost != null ? h('span', { title: 'Cost source: ' + (it.cost_source || '—'), style: { cursor: 'help' } }, reconFmtMoney(it.unit_cost)) : '—'),
              h('td', { style: Object.assign({}, right, { fontWeight: exp > 0 ? 700 : 400, color: exp > 0 ? '#EF4444' : C.textDim }) }, exp > 0 ? reconFmtMoney(exp) : '—'),
              h('td', { style: Object.assign({}, right, { textAlign: 'left' }) }, h(ReconStateBadge, { state: it.state })),
              canWrite && h('td', { style: Object.assign({}, right, { textAlign: 'left' }) },
                h('div', { style: { display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' } }, actions))
            );
          }))
        )
      ),

      // ─── Cases ───
      items && h('div', { style: { borderTop: '1px solid ' + C.border, paddingTop: 12 } },
        h('div', { style: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 } },
          h('span', { style: { fontSize: 13, fontWeight: 700, color: C.text } }, 'Claims (' + cases.length + ')'),
          h('div', { style: { flex: 1 } }),
          canWrite && sumClaimUnits > 0 && !fileForm && h(Btn, { size: 'sm', variant: 'primary', disabled: busy,
            onClick: function() { setFileForm({ case_id: '', units: String(sumClaimUnits), amount: (Math.round(sumClaimAmt * 100) / 100).toFixed(2), notes: '' }); } },
            h(LIcon, { name: 'file-plus-2', size: 13 }), ' Record Filed Claim')
        ),
        fileForm && h('div', { style: { background: C.bg, border: '1px solid ' + C.border, borderRadius: 8, padding: 12, marginBottom: 10, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' } },
          h(Inp, { label: 'Amazon Case ID', value: fileForm.case_id, onChange: function(e) { setFileForm(Object.assign({}, fileForm, { case_id: e.target.value })); }, placeholder: 'e.g. 1234567890', style: { width: 160 } }),
          h(Inp, { label: 'Units', type: 'number', value: fileForm.units, onChange: function(e) { setFileForm(Object.assign({}, fileForm, { units: e.target.value })); }, style: { width: 90 } }),
          h(Inp, { label: 'Requested $', type: 'number', value: fileForm.amount, onChange: function(e) { setFileForm(Object.assign({}, fileForm, { amount: e.target.value })); }, style: { width: 110 } }),
          h(Inp, { label: 'Notes', value: fileForm.notes, onChange: function(e) { setFileForm(Object.assign({}, fileForm, { notes: e.target.value })); }, style: { width: 220 } }),
          h(Btn, { variant: 'primary', size: 'sm', disabled: busy || !fileForm.case_id.trim(),
            onClick: function() {
              postAction(detail.row, '/fba-reconciliation/shipments/' + encodeURIComponent(detail.row.amazon_shipment_id) + '/cases',
                { amazon_case_id: fileForm.case_id.trim(), requested_units: fileForm.units, requested_amount: fileForm.amount, notes: fileForm.notes },
                'Claim recorded — items moved to In case');
              setFileForm(null);
            } }, 'Save'),
          h(Btn, { size: 'sm', onClick: function() { setFileForm(null); } }, 'Cancel')
        ),
        !cases.length && !fileForm && h('div', { style: { fontSize: 12, color: C.textDim } },
          sumClaimUnits > 0
            ? 'No claim recorded yet. File in Seller Central (Reconcile tab), then record the case ID here — the claimable items move to "In case" and their numbers freeze.'
            : 'No claims for this shipment.'),
        cases.map(function(cs) {
          var canAct = canWrite && ['filed', 'partial'].indexOf(cs.status) >= 0;
          var asking = caseAmt && caseAmt.id === cs.id;
          return h('div', { key: cs.id, style: { display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', padding: '8px 10px', border: '1px solid ' + C.border, borderRadius: 8, marginBottom: 6, fontSize: 12 } },
            h(ReconStateBadge, { state: cs.status, map: reconCaseMeta }),
            h('span', { style: { color: C.textMuted } }, 'Case ', h(CopyCell, { value: cs.amazon_case_id || '—' })),
            h('span', { style: { color: C.textMuted } }, (cs.requested_units || 0) + ' units · ' + reconFmtMoney(cs.requested_amount)),
            cs.recovered_amount != null && h('span', { style: { color: '#10B981', fontWeight: 600 } }, 'recovered ' + reconFmtMoney(cs.recovered_amount)),
            h('span', { style: { color: C.textDim } }, 'filed ' + reconFmtDate(cs.filed_at) + (cs.filed_by_name ? ' by ' + cs.filed_by_name : '')),
            cs.notes && h('span', { style: { color: C.textDim, fontStyle: 'italic' } }, cs.notes),
            h('div', { style: { flex: 1 } }),
            canAct && !asking && h(Fragment, null,
              h(Btn, { size: 'sm', variant: 'success', disabled: busy, onClick: function() { setCaseAmt({ id: cs.id, val: String(cs.requested_amount || ''), status: 'reimbursed' }); } }, 'Reimbursed'),
              h(Btn, { size: 'sm', disabled: busy, onClick: function() { setCaseAmt({ id: cs.id, val: String(cs.recovered_amount || ''), status: 'partial' }); } }, 'Partial'),
              h(Btn, { size: 'sm', variant: 'danger', disabled: busy, onClick: function() { if (window.confirm('Mark this claim denied? Linked items resolve as denied.')) putCase(detail.row, cs.id, { status: 'denied' }, 'Claim marked denied'); } }, 'Denied'),
              h(Btn, { size: 'sm', variant: 'ghost', disabled: busy, onClick: function() { if (window.confirm('Close this claim? Remaining linked items resolve as written off.')) putCase(detail.row, cs.id, { status: 'closed' }, 'Claim closed'); } }, 'Close')
            ),
            asking && h(Fragment, null,
              h(Inp, { type: 'number', value: caseAmt.val, onChange: function(e) { setCaseAmt(Object.assign({}, caseAmt, { val: e.target.value })); }, placeholder: 'Recovered $', style: { width: 110 } }),
              h(Btn, { size: 'sm', variant: 'primary', disabled: busy || caseAmt.val === '',
                onClick: function() { putCase(detail.row, cs.id, { status: caseAmt.status, recovered_amount: caseAmt.val }, caseAmt.status === 'reimbursed' ? 'Claim marked reimbursed' : 'Partial recovery recorded'); } }, 'Save'),
              h(Btn, { size: 'sm', onClick: function() { setCaseAmt(null); } }, 'Cancel')
            )
          );
        })
      ),

      items && h('div', { style: { fontSize: 11, color: C.textDim } },
        '"Not Shipped" units were declared to Amazon but never left the warehouse (known shorts) — they are excluded from claimable math and must never be claimed. File claims in Seller Central: Shipments → shipment → Reconcile tab.')
    );
  }

  return h('div', { style: { animation: 'fadeIn 0.3s ease' } },
    // Header
    h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 } },
      h('h2', { style: { fontSize: 20, fontWeight: 700, color: C.text } }, 'FBA Reconciliation'),
      isAdmin && h('div', { style: { display: 'flex', gap: 8, alignItems: 'center' } },
        runningPhase && h('span', { style: { fontSize: 12, color: '#F59E0B', display: 'inline-flex', alignItems: 'center', gap: 6 } },
          h(LIcon, { name: 'loader-2', size: 13, spin: true }), 'Backfill: ' + runningPhase +
          (backfill.shipments_seen ? ' · ' + backfill.shipments_seen + ' shipments' : '')),
        backfill && (backfill.errors || []).length > 0 && h('span', {
          title: (backfill.errors || []).slice(0, 12).join('\n'),
          style: { fontSize: 11, color: '#F59E0B', cursor: 'help' }
        }, (backfill.errors || []).length + ' backfill error' + ((backfill.errors || []).length > 1 ? 's' : '')),
        h(Btn, { disabled: busy, onClick: recompute }, h(LIcon, { name: 'refresh-cw', size: 13 }), ' Recompute'),
        h(Btn, { disabled: busy || !!runningPhase, onClick: function() { setBackfillConfirm(true); } }, h(LIcon, { name: 'history', size: 13 }), ' Backfill 18 mo')
      )
    ),
    // Stat cards
    summary && h('div', { style: { display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' } },
      h(ReconStatCard, { icon: 'circle-dollar-sign', label: 'Open Exposure', value: reconFmtMoney(summary.open_exposure), sub: 'actionable + in-case claimable', color: Number(summary.open_exposure) > 0 ? '#EF4444' : '#10B981' }),
      h(ReconStatCard, { icon: 'truck', label: 'In-Flight', value: reconFmtMoney(summary.in_flight_exposure), sub: 'still receiving — not yet actionable', color: '#3B82F6' }),
      h(ReconStatCard, { icon: 'alert-triangle', label: 'Action Needed', value: String(summary.action_shipments || 0), sub: (summary.action_units || 0) + ' units short', color: (summary.action_shipments || 0) > 0 ? '#F59E0B' : '#10B981' }),
      h(ReconStatCard, { icon: 'clock', label: 'Deadline ≤ 30d', value: String(summary.expiring_30d || 0), sub: 'shipments to claim soon', color: (summary.expiring_30d || 0) > 0 ? '#EF4444' : C.text }),
      h(ReconStatCard, { icon: 'clipboard-check', label: 'Pending Approvals', value: String(summary.approvals_pending || 0), sub: 'not-shipped / overage sign-offs' }),
      h(ReconStatCard, { icon: 'hourglass', label: 'Expired (missed)', value: reconFmtMoney(summary.expired_value), sub: 'past the claim window', color: C.textMuted })
    ),
    // State pills + filters
    h('div', { style: { display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' } },
      statePills.map(function(s) {
        var isActive = stateFilter === s.id;
        return h('button', { key: s.id || 'all', onClick: function() { setStateFilter(s.id); },
          className: isActive ? '' : 'filter-pill',
          style: { padding: '6px 14px', borderRadius: 6, border: '1px solid transparent', cursor: 'pointer', fontSize: 12, fontWeight: 500, fontFamily: 'inherit', transition: 'all 0.15s',
            background: isActive ? C.accent : C.surface, color: isActive ? '#fff' : C.textMuted }
        }, s.label + (s.count ? ' (' + s.count + ')' : ''));
      }),
      h('div', { style: { flex: 1 } }),
      h('select', {
        value: mktFilter, onChange: function(e) { setMktFilter(e.target.value); },
        style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', outline: 'none', width: 120 }
      },
        h('option', { value: '' }, 'All markets'),
        h('option', { value: 'US' }, 'US'),
        h('option', { value: 'CA' }, 'CA')
      ),
      h(Inp, { type: 'number', value: minExposure, onChange: function(e) { setMinExposure(e.target.value); },
        placeholder: 'Min $', title: 'Noise floor: hide shipments with exposure below this amount', style: { width: 90 } })
    ),
    h('div', { style: { marginBottom: 16 } },
      h(SearchInput, { type: 'text', value: search, onChange: function(e) { setSearch(e.target.value); },
        placeholder: 'Search Shipment ID, Name, FC...',
        style: { width: '100%', background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '10px 14px', borderRadius: 8, fontSize: 13, outline: 'none' } })
    ),
    // Queue
    h(DataTable, {
      columns: columns,
      data: filtered,
      onRowClick: openDetail,
      rowHref: function(row) { return row.plan_id ? '/fba/' + row.plan_id : null; },
      emptyTitle: loading ? 'Loading…' : 'No reconciliation rows',
      emptySub: loading ? '' : 'Rows appear once shipments reach checked-in at Amazon' + (isAdmin ? ' — run the 18-month backfill to pull history' : ''),
      storageKey: 'fba-recon-queue',
      defaultSort: 'deadline',
      defaultSortDir: 'asc',
    }),

    // ─── Detail modal: per-SKU ladder + approvals + cases ───
    detail && h(Modal, { open: true, onClose: function() { setDetail(null); }, title: 'Reconciliation — ' + detail.row.amazon_shipment_id, width: 1080 }, detailBody),

    // ─── Backfill confirm ───
    h(Modal, { open: backfillConfirm, onClose: function() { setBackfillConfirm(false); }, title: 'Run 18-month backfill?', width: 520 },
      h('div', { style: { display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13, color: C.textMuted } },
        h('div', null, 'Sweeps every FBA shipment updated in the last 18 months (US + CA), pulls Amazon’s received counts per SKU, and reads the inventory ledger for delivery dates (claim deadline = delivery + 9 months).'),
        h('div', null, 'Runs in the background for roughly 30–60 minutes (SP-API rate limits). Safe to re-run — rows are upserted.'),
        h('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 6 } },
          h(Btn, { onClick: function() { setBackfillConfirm(false); } }, 'Cancel'),
          h(Btn, { variant: 'primary', disabled: busy, onClick: startBackfill }, busy ? 'Starting…' : 'Start Backfill')
        )
      )
    )
  );
}

window.FbaReconPage = FbaReconPage;

;
/* ===== app-wfs.js ===== */
// ─── WFS Shipments Module ───
// WfsShipmentsPage (list), WfsDetailPage (wizard), WfsBoxPackingView (scanner)

var { createElement: h, Fragment } = React;

// ─── Status colors for WFS ───
var wfsStatusColors = {
  draft: { bg: 'rgba(251,191,36,0.12)', text: '#FBBF24' },
  submitted: { bg: 'rgba(59,130,246,0.12)', text: '#3B82F6' },
  packing: { bg: 'rgba(251,191,36,0.12)', text: '#FBBF24' },
  packed: { bg: 'rgba(20,184,166,0.12)', text: '#14B8A6' },
  labels_ready: { bg: 'rgba(52,211,153,0.12)', text: '#34D399' },
  shipped: { bg: 'rgba(99,102,241,0.12)', text: '#6366F1' },
  receiving: { bg: 'rgba(251,146,60,0.12)', text: '#F59E0B' },
  delivered: { bg: 'rgba(52,211,153,0.12)', text: '#34D399' },
  closed: { bg: 'rgba(139,143,163,0.12)', text: '#8B8FA3' },
  cancelled: { bg: 'rgba(248,113,113,0.12)', text: '#F87171' },
};

function WfsBadge({ status }) {
  var sc = wfsStatusColors[status] || { bg: 'rgba(139,143,163,0.12)', text: '#8B8FA3' };
  return h('span', { style: { background: sc.bg, color: sc.text, padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' } }, (status || '').replace(/_/g, ' '));
}

// ─── WFS Shipments List Page ───
function WfsShipmentsPage({ showToast, onNavigate, currentUser, createWithPO }) {
  var [plans, setPlans] = useState([]);
  var [loading, setLoading] = useState(true);
  var [statusFilter, setStatusFilter] = useState('');
  var [search, setSearch] = useState('');
  var [createOpen, setCreateOpen] = useState(false);
  var [eligibleLoading, setEligibleLoading] = useState(false);
  var [eligiblePOs, setEligiblePOs] = useState([]);
  var [createPOIds, setCreatePOIds] = useState([]);
  var [createType, setCreateType] = useState('SPD');
  var [createPackingMode, setCreatePackingMode] = useState('individual');
  var [creating, setCreating] = useState(false);
  var [shipFromAddrs, setShipFromAddrs] = useState([]);
  var [selectedShipFrom, setSelectedShipFrom] = useState('');
  var [newAddrForm, setNewAddrForm] = useState({ label: '', name: '', address_line1: '', city: '', state: '', postal_code: '', country_code: 'US', phone: '' });
  // Vendor address state (populated when the selected PO is vendor-direct — mirrors FBA)
  var [vendorData, setVendorData] = useState(null); // raw vendor row from /api/vendors/:id
  var [vendorEditForm, setVendorEditForm] = useState(null); // editable copy shown under Ship From
  var [loadingVendor, setLoadingVendor] = useState(false);
  var createFromPORef = useRef(null); // PO id when the wizard was auto-opened from PO detail — cancel returns there

  // Paged list (9-27 sweep L17): newest WFS_PLAN_PAGE first, older on demand; a failed load shows
  // its error with Retry instead of an empty "No WFS Shipments" table.
  var [planTotal, setPlanTotal] = useState(0);
  var [loadError, setLoadError] = useState('');
  var [loadingMore, setLoadingMore] = useState(false);
  var WFS_PLAN_PAGE = 500;
  var loadPlans = useCallback(function() {
    setLoading(true);
    setLoadError('');
    api('/wfs-shipments/plans?limit=' + WFS_PLAN_PAGE + '&offset=0').then(function(data) {
      var rows = Array.isArray(data) ? data : ((data && data.plans) || []);
      setPlans(rows);
      setPlanTotal(Array.isArray(data) ? rows.length : ((data && data.total) || rows.length));
    }).catch(function(e) { setLoadError(e.message || 'Could not load WFS shipments'); showToast(e.message, 'error'); }).finally(function() { setLoading(false); });
  }, []);
  var loadMorePlans = function() {
    setLoadingMore(true);
    api('/wfs-shipments/plans?limit=' + WFS_PLAN_PAGE + '&offset=' + plans.length).then(function(data) {
      var rows = (data && data.plans) || [];
      setPlans(function(prev) {
        var seen = {}; prev.forEach(function(p) { seen[p.id] = true; });
        return prev.concat(rows.filter(function(p) { return !seen[p.id]; }));
      });
      if (data && data.total != null) setPlanTotal(data.total);
    }).catch(function(e) { showToast(e.message, 'error'); }).finally(function() { setLoadingMore(false); });
  };
  useEffect(function() { loadPlans(); }, [loadPlans]);

  // Auto-open create modal when navigated from PO detail with createWithPO
  useEffect(function() {
    if (!createWithPO) return;
    createFromPORef.current = createWithPO;
    // Server-filtered eligible list (same endpoint as openCreate below): the old flow
    // downloaded the ENTIRE PO table + all plans and filtered client-side — slow, and it
    // required purchase_orders read; /eligible-pos runs on wfs_shipments perms and applies
    // the identical WFS%/status/active-plan filter server-side.
    Promise.all([api('/wfs-shipments/eligible-pos'), api('/fba-shipments/ship-from-addresses')]).then(function(results) {
      var wfsPOs = results[0]; var addrs = results[1];
      setEligiblePOs(wfsPOs);
      var targetPO = wfsPOs.find(function(po) { return po.id === createWithPO; });
      setCreatePOIds(targetPO ? [targetPO.id] : []);
      setShipFromAddrs(addrs);
      var def = addrs.find(function(a) { return a.is_default; }) || addrs[0];
      setSelectedShipFrom(def ? String(def.id) : 'new');
      setNewAddrForm({ label: '', name: '', address_line1: '', city: '', state: '', postal_code: '', country_code: 'US', phone: '' });
      setCreateOpen(true);
    }).catch(function(e) { showToast(e.message, 'error'); });
  }, [createWithPO]);

  // Close the create wizard without creating; if it was auto-opened from a PO
  // detail page (createWithPO), return the user to that PO instead of leaving
  // them on the WFS list. Mirrors the FBA create modal.
  var closeCreate = function() {
    setCreateOpen(false);
    if (createFromPORef.current) {
      var poId = createFromPORef.current;
      createFromPORef.current = null;
      onNavigate('po-detail', poId);
    }
  };

  var openCreate = function() {
    // Open the modal IMMEDIATELY with a loading state — awaiting the three fetches first
    // (incl. the full PO list) made the button feel dead for seconds (mirrors FBA's fix).
    createFromPORef.current = null;
    setEligiblePOs([]);
    setCreatePOIds([]);
    setVendorData(null); setVendorEditForm(null);
    setEligibleLoading(true);
    setCreateOpen(true);
    // Server-filtered eligible list (mirrors FBA's /eligible-pos): the old flow downloaded
    // the ENTIRE PO table + all plans and filtered client-side — a multi-second load.
    Promise.all([api('/wfs-shipments/eligible-pos'), api('/fba-shipments/ship-from-addresses')]).then(function(results) {
      var wfsPOs = results[0]; var addrs = results[1];
      setEligiblePOs(wfsPOs);
      setShipFromAddrs(addrs);
      var def = addrs.find(function(a) { return a.is_default; }) || addrs[0];
      setSelectedShipFrom(def ? String(def.id) : 'new');
      setNewAddrForm({ label: '', name: '', address_line1: '', city: '', state: '', postal_code: '', country_code: 'US', phone: '' });
    }).catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setEligibleLoading(false); });
  };

  var addrToSource = function(a) {
    return { name: a.name, addressLine1: a.address_line1, city: a.city, state: a.state, postalCode: a.postal_code, countryCode: a.country_code || 'US', phone: a.phone || '' };
  };

  // Vendor-direct detection: any selected PO in submitted/confirmed status (only WFS-direct
  // POs appear in those statuses in the eligible list). Mirrors the FBA create modal.
  var selectedVendorDirectPOs = eligiblePOs.filter(function(po) {
    return createPOIds.indexOf(po.id) !== -1 && (po.status === 'submitted' || po.status === 'confirmed');
  });
  var isVendorDirectSelection = selectedVendorDirectPOs.length > 0;
  var vendorDirectVendorId = isVendorDirectSelection ? selectedVendorDirectPOs[0].vendor_id : null;

  // Fetch the vendor record as soon as the selection is vendor-direct so the ship-from
  // address is loaded and preselected before Create.
  useEffect(function() {
    if (!createOpen || !vendorDirectVendorId) return;
    if (vendorData && vendorData.id === vendorDirectVendorId) { setSelectedShipFrom('vendor'); return; }
    setLoadingVendor(true);
    api('/vendors/' + vendorDirectVendorId)
      .then(function(v) {
        setVendorData(v);
        setVendorEditForm({
          address1: v.address1 || '', city: v.city || '', state: v.state || '',
          zip: v.zip || '', country: v.country || 'US', phone: v.phone || '',
        });
        setSelectedShipFrom('vendor');
      })
      .catch(function(e) { showToast('Could not load vendor address: ' + e.message, 'error'); })
      .finally(function() { setLoadingVendor(false); });
  }, [createOpen, vendorDirectVendorId]);

  // If the selection stops being vendor-direct while "vendor" is chosen, fall back to the
  // default saved address — a warehouse plan must never ship from the vendor.
  useEffect(function() {
    if (selectedShipFrom !== 'vendor' || isVendorDirectSelection) return;
    var def = shipFromAddrs.find(function(a) { return a.is_default; }) || shipFromAddrs[0];
    setSelectedShipFrom(def ? String(def.id) : 'new');
  }, [isVendorDirectSelection, selectedShipFrom]);

  // Required fields for a usable Walmart source address
  var VENDOR_REQUIRED = ['address1', 'city', 'state', 'zip', 'country', 'phone'];
  var VENDOR_FIELD_LABEL = { address1: 'Address line 1', city: 'City', state: 'State', zip: 'Postal code', country: 'Country', phone: 'Phone' };
  var vendorMissingFields = vendorEditForm
    ? VENDOR_REQUIRED.filter(function(f) { return !vendorEditForm[f] || !String(vendorEditForm[f]).trim(); })
    : [];

  var vendorToSource = function(v, e) {
    return {
      name: (v && v.vendor_name) || '',
      addressLine1: (e && e.address1) || (v && v.address1) || '',
      city: (e && e.city) || (v && v.city) || '',
      state: (e && e.state) || (v && v.state) || '',
      postalCode: (e && e.zip) || (v && v.zip) || '',
      countryCode: ((e && e.country) || (v && v.country) || 'US').substring(0, 2).toUpperCase(),
      phone: (e && e.phone) || (v && v.phone) || '',
    };
  };

  var handleCreate = function() {
    if (!createPOIds.length) return;
    setCreating(true);
    var doCreate = function(sourceAddr) {
      api('/wfs-shipments/plans', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ po_ids: createPOIds, shipment_type: createType, packing_mode: createPackingMode, ship_from_address: sourceAddr }) })
        .then(function(plan) { setCreateOpen(false); setCreating(false); onNavigate('wfs-detail', plan.id); })
        .catch(function(e) { showToast(e.message, 'error'); setCreating(false); });
    };
    if (selectedShipFrom === 'vendor') {
      if (!vendorData || !vendorEditForm) { showToast('Vendor address still loading', 'error'); setCreating(false); return; }
      if (vendorMissingFields.length) { showToast('Fill required vendor fields: ' + vendorMissingFields.map(function(f) { return VENDOR_FIELD_LABEL[f]; }).join(', '), 'error'); setCreating(false); return; }
      // Persist any edits back to the vendor record, then create the plan (mirrors FBA).
      var changed = VENDOR_REQUIRED.some(function(f) { return (vendorEditForm[f] || '') !== (vendorData[f] || ''); });
      var persist = changed
        ? api('/vendors/' + vendorData.id, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(Object.assign({}, vendorData, vendorEditForm)) })
            .then(function(updated) { setVendorData(updated); return updated; })
        : Promise.resolve(vendorData);
      persist
        .then(function(v) { doCreate(vendorToSource(v, vendorEditForm)); })
        .catch(function(e) { showToast('Could not save vendor address: ' + e.message, 'error'); setCreating(false); });
    } else if (selectedShipFrom === 'new') {
      if (!newAddrForm.label || !newAddrForm.address_line1) { showToast('Label and address are required', 'error'); setCreating(false); return; }
      api('/fba-shipments/ship-from-addresses', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(newAddrForm) })
        .then(function(saved) { doCreate(addrToSource(saved)); })
        .catch(function(e) { showToast(e.message, 'error'); setCreating(false); });
    } else {
      var addr = shipFromAddrs.find(function(a) { return String(a.id) === selectedShipFrom; });
      if (!addr) { showToast('Select a ship-from address', 'error'); setCreating(false); return; }
      doCreate(addrToSource(addr));
    }
  };

  var statuses = ['', 'draft', 'submitted', 'packing', 'packed', 'labels_ready', 'shipped', 'delivered', 'receiving', 'closed', 'cancelled'];
  var statusCounts = useMemo(function() {
    var counts = {};
    plans.forEach(function(p) { counts[p.status] = (counts[p.status] || 0) + 1; });
    counts[''] = plans.filter(function(p) { return p.status !== 'cancelled' && p.status !== 'closed'; }).length;
    return counts;
  }, [plans]);
  var filtered = statusFilter ? plans.filter(function(p) { return p.status === statusFilter; }) : plans.filter(function(p) { return p.status !== 'cancelled' && p.status !== 'closed'; });
  if (search) {
    var q = search.toLowerCase();
    filtered = filtered.filter(function(p) {
      return (p.po_numbers || p.po_number || '').toLowerCase().indexOf(q) !== -1 ||
        (p.walmart_shipment_id || '').toLowerCase().indexOf(q) !== -1 ||
        (p.vendor_name || '').toLowerCase().indexOf(q) !== -1 ||
        (p.destination_fc || '').toLowerCase().indexOf(q) !== -1 ||
        (p.fc_display_name || '').toLowerCase().indexOf(q) !== -1 ||
        (p.skus || '').toLowerCase().indexOf(q) !== -1;
    });
  }

  var columns = [
    { key: 'created_at', label: 'Created', render: function(r) { return r.created_at ? new Date(r.created_at).toLocaleDateString() : ''; } },
    { key: 'po_number', label: 'PO #', render: function(r) { var v = r.po_numbers || r.po_number; return v ? h(CopyCell, { value: v, style: { fontFamily: 'inherit', fontWeight: 600 } }) : h('span', { style: { color: C.textMuted } }, '--'); } },
    { key: 'walmart_shipment_id', label: 'WFS Shipment ID', render: function(r) { if ((r.shipment_count || 0) > 1) return h('span', { style: { fontSize: 11, fontWeight: 600, color: C.accent } }, r.shipment_count + ' shipments'); return r.walmart_shipment_id ? h(CopyCell, { value: r.walmart_shipment_id, style: { fontFamily: 'inherit', fontSize: 11 } }) : h('span', { style: { color: C.textMuted, fontSize: 11 } }, '--'); } },
    { key: 'status', label: 'Status', render: function(r) { return h(WfsBadge, { status: r.status }); } },
    { key: 'shipment_type', label: 'Type', render: function(r) { return h('span', { style: { fontSize: 12, fontWeight: 500 } }, r.shipment_type || 'SPD'); } },
    { key: 'total_units', label: 'Units', render: function(r) { return r.total_units || 0; } },
    { key: 'packed_progress', label: 'Packed',
      sortValue: function(r) { var t = r.total_units || 0; return t > 0 ? (r.total_packed || 0) / t : 0; },
      render: function(r) {
        var total = r.total_units || 0;
        var packed = r.total_packed || 0;
        var missing = r.total_missing || 0;
        var pct = total > 0 ? Math.round(packed / total * 100) : 0;
        var pctMissing = total > 0 ? Math.min(100 - pct, Math.round(missing / total * 100)) : 0;
        // Packing is "done" when every unit is packed or declared missing — packed
        // segment goes green then, with the amber missing segment keeping the shortage visible.
        var done = total > 0 && packed + missing >= total;
        var color = pct === 0 ? C.textMuted : (done || pct >= 100 ? '#10B981' : C.accent);
        var tip = pct + '% packed' + (missing > 0 ? ' · ' + missing.toLocaleString() + ' missing' : '');
        return h('div', { title: tip, style: { width: 90, cursor: 'help' } },
          h('div', { style: { fontSize: 13, fontWeight: 500, color: C.text, marginBottom: 4, textAlign: 'center' } }, packed + ' / ' + total),
          h('div', { style: { height: 5, background: C.border, borderRadius: 3, overflow: 'hidden', display: 'flex' } },
            h('div', { style: { height: '100%', width: pct + '%', background: color, transition: 'width 0.2s' } }),
            pctMissing > 0 && h('div', { style: { height: '100%', width: pctMissing + '%', background: '#F59E0B', transition: 'width 0.2s' } })
          )
        );
      }
    },
    { key: 'box_count', label: 'Boxes', render: function(r) { return r.box_count || 0; } },
    { key: 'destination_fc', label: 'FC', render: function(r) { if ((r.shipment_count || 0) > 1) return h('span', { style: { fontSize: 12, color: C.textMuted } }, r.shipment_count + ' FCs'); return h('span', { style: { fontWeight: 500 } }, r.fc_display_name || r.destination_fc || '--'); } },
    { key: 'vendor_name', label: 'Vendor' },
  ];

  return h('div', { style: { animation: 'fadeIn 0.3s ease' } },
    h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 } },
      h('h2', { style: { fontSize: 20, fontWeight: 700, color: C.text } }, 'WFS Shipments'),
      h(Btn, { variant: 'primary', onClick: openCreate }, h(LIcon, { name: 'plus', size: 14 }), ' New WFS Shipment')
    ),
    // Status filter tabs
    h('div', { style: { display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' } },
      statuses.map(function(s) {
        var cnt = statusCounts[s] || 0;
        var isActive = statusFilter === s;
        var label = s ? s.replace(/_/g, ' ') : 'All';
        return h('button', { key: s, onClick: function() { setStatusFilter(s); },
          className: isActive ? '' : 'filter-pill',
          style: { padding: '6px 14px', borderRadius: 6, border: '1px solid transparent', cursor: 'pointer', fontSize: 12, fontWeight: 500, fontFamily: 'inherit', transition: 'all 0.15s',
            background: isActive ? C.accent : C.surface, color: isActive ? '#fff' : C.textMuted }
        }, label + (cnt ? ' (' + cnt + ')' : ''));
      })
    ),
    // Search
    h('div', { style: { marginBottom: 16 } }, h(SearchInput, { type: 'text', value: search, onChange: function(e) { setSearch(e.target.value); },
      placeholder: 'Search PO#, WFS Shipment ID, SKU, Vendor, FC...',
      style: { width: '100%', background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '10px 14px', borderRadius: 8, fontSize: 13, outline: 'none' }
    })),
    // Table (a failed load shows the error, not an empty list)
    loadError && !loading
      ? h('div', { style: { padding: 24, textAlign: 'center', color: C.danger, fontSize: 13 } },
          h('div', { style: { marginBottom: 10 } }, 'Could not load WFS shipments: ' + loadError),
          h(Btn, { size: 'sm', onClick: loadPlans }, h(LIcon, { name: 'refresh-cw', size: 13 }), ' Retry'))
      : h(DataTable, {
      columns: columns,
      data: filtered,
      onRowClick: function(row) { onNavigate('wfs-detail', row.id); },
      rowHref: function(row) { return '/wfs/' + row.id; },
      emptyTitle: loading ? 'Loading WFS Shipments…' : 'No WFS Shipments',
      emptySub: loading ? '' : 'Create your first WFS shipment from a Purchase Order',
      storageKey: 'wfs-plans-list',
      defaultSort: 'created_at',
      defaultSortDir: 'desc',
    }),
    !loadError && plans.length < planTotal && h('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '12px 0', fontSize: 12, color: C.textMuted } },
      'Showing the newest ' + plans.length + ' of ' + planTotal + ' plans (search and counts cover the loaded plans)',
      h(Btn, { size: 'sm', onClick: loadMorePlans, disabled: loadingMore }, loadingMore ? 'Loading…' : 'Load Older Plans')),
    // Create Modal
    h(Modal, { open: createOpen, onClose: closeCreate, title: 'New WFS Shipment', width: 900, maxHeight: 'calc(95vh * var(--invzoom, 1))' },
      h('div', { style: { display: 'flex', flexDirection: 'column', gap: 14, height: 'calc(72vh * var(--invzoom, 1))' } },
        h('label', { style: { fontSize: 12, fontWeight: 500, color: C.textMuted } }, 'Purchase Orders (checked-in or Vendor Direct, WFS warehouses)'),
        h('div', { style: { flex: '1 1 auto', minHeight: 180, overflowY: 'auto', border: '1px solid ' + C.border, borderRadius: 6, padding: 4 } },
          eligibleLoading
            ? h('p', { style: { color: C.textMuted, fontSize: 12, padding: 8, margin: 0 } }, 'Loading eligible POs…')
            : eligiblePOs.length === 0
            ? h('p', { style: { color: C.danger, fontSize: 12, padding: 8, margin: 0 } }, 'No checked-in WFS warehouse POs found')
            : eligiblePOs.map(function(po) {
                var checked = createPOIds.indexOf(po.id) !== -1;
                var isThisVD = po.status === 'submitted' || po.status === 'confirmed';
                var firstSelPO = createPOIds.length > 0 ? eligiblePOs.find(function(p) { return p.id === createPOIds[0]; }) : null;
                var firstSelVD = !!(firstSelPO && (firstSelPO.status === 'submitted' || firstSelPO.status === 'confirmed'));
                // Vendor Direct rule (mirrors FBA): a VD PO ships alone from the vendor — no mixing
                var blocked = !checked && createPOIds.length > 0 && (isThisVD || firstSelVD);
                var blockReason = blocked
                  ? (isThisVD
                      ? 'Unavailable: Vendor Direct POs ship by themselves. Deselect the other POs first.'
                      : 'Unavailable: a Vendor Direct PO is already selected and must ship alone. Deselect it first.')
                  : undefined;
                return h('div', { key: po.id, title: blockReason, onClick: function() {
                    if (blocked) { showToast(blockReason, 'error'); return; }
                    setCreatePOIds(function(prev) { return checked ? prev.filter(function(id) { return id !== po.id; }) : prev.concat([po.id]); });
                  }, style: { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 4, cursor: blocked ? 'not-allowed' : 'pointer', opacity: blocked ? 0.4 : 1, background: checked ? 'rgba(79,110,247,0.08)' : 'transparent', transition: 'background 0.15s', userSelect: 'none' } },
                  h('div', { style: { width: 20, height: 20, minWidth: 20, borderRadius: 4, border: '2px solid ' + (checked ? C.accent : C.textDim), background: checked ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' } },
                    checked && h(LIcon, { name: 'check', size: 13, color: '#fff' })
                  ),
                  h('div', { style: { flex: 1, fontSize: 14, minWidth: 0 } },
                    h('div', null,
                      h('span', { style: { fontWeight: 600, fontFamily: 'inherit' } }, po.po_number),
                      h('span', { style: { color: C.textMuted, marginLeft: 8 } }, (po.vendor_name || '') + ' -- ' + (po.wh_code || po.warehouse_code || ''))
                    ),
                    (function() {
                      var pillBase = { fontSize: 11, fontWeight: 600, padding: '0 9px', borderRadius: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4, height: 22, lineHeight: 1, boxSizing: 'border-box', whiteSpace: 'nowrap', fontFamily: 'inherit' };
                      return h('div', { style: { display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' } },
                        h('span', { style: Object.assign({}, pillBase, { background: C.accent + '18', color: C.accent }) }, (po.sku_count || 0) + ' SKU' + ((po.sku_count || 0) !== 1 ? 's' : '')),
                        h('span', { style: Object.assign({}, pillBase, { background: '#f59e0b18', color: '#f59e0b' }) }, (po.total_units || 0).toLocaleString() + ' units'),
                        isThisVD && h('span', { style: Object.assign({}, pillBase, { background: '#8b5cf618', color: '#8b5cf6' }) }, 'Vendor Direct')
                      );
                    })()
                  )
                );
              })
        ),
        createPOIds.length > 0 && (function() {
          var selPOs = eligiblePOs.filter(function(po) { return createPOIds.indexOf(po.id) !== -1; });
          var totSkus = selPOs.reduce(function(s, po) { return s + (po.sku_count || 0); }, 0);
          var totUnits = selPOs.reduce(function(s, po) { return s + (po.total_units || 0); }, 0);
          return h('div', null,
            h('div', { style: { fontSize: 11, color: C.accent, fontWeight: 500 } },
              createPOIds.length + ' PO' + (createPOIds.length !== 1 ? 's' : '') + ' selected -- ' + totSkus + ' SKU' + (totSkus !== 1 ? 's' : '') + ', ' + totUnits.toLocaleString() + ' units'
            ),
            isVendorDirectSelection && h('div', { style: { fontSize: 11, color: '#8b5cf6', fontWeight: 500, marginTop: 4, padding: '4px 8px', background: '#8b5cf610', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 4 } },
              h(LIcon, { name: 'truck', size: 12 }), ' Vendor Direct — ships from vendor to Walmart. Ship-from address will use the vendor’s address.'
            )
          );
        })(),

        h('label', { style: { fontSize: 12, fontWeight: 500, color: C.textMuted } }, 'Shipment Type'),
        h('div', { style: { display: 'flex', gap: 8 } },
          ['SPD', 'LTL'].map(function(t) {
            return h('button', { key: t, onClick: function() { setCreateType(t); },
              style: { padding: '6px 16px', borderRadius: 6, border: '1px solid ' + (createType === t ? C.accent : C.border), background: createType === t ? C.accent : 'transparent', color: createType === t ? '#fff' : C.text, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit' }
            }, t === 'SPD' ? 'Small Parcel (SPD)' : 'LTL / Pallet');
          })
        ),

        h('div', { style: { marginTop: 4 } },
          h('label', { style: { fontSize: 12, fontWeight: 500, color: C.textMuted, display: 'block', marginBottom: 6 } }, 'Packing Mode'),
          h('div', { style: { display: 'flex', gap: 8 } },
            [{ key: 'case_packed', label: 'Case Packed (Supplier Pack)', desc: 'One SKU per carton at a fixed qty. You set units per carton and carton size next — Walmart receives it as supplier packs.' }, { key: 'individual', label: 'Individual Units', desc: 'Mixed SKUs per box, packed individually' }].map(function(m) {
              var sel = createPackingMode === m.key;
              return h('button', { key: m.key, onClick: function() { setCreatePackingMode(m.key); },
                style: { flex: 1, padding: '8px 12px', borderRadius: 6, border: '1px solid ' + (sel ? C.accent : C.border), background: sel ? C.accent + '10' : 'transparent', color: sel ? C.accent : C.text, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit', textAlign: 'left' } },
                h('div', { style: { fontWeight: 600 } }, m.label),
                h('div', { style: { fontSize: 10, color: C.textMuted, marginTop: 2 } }, m.desc)
              );
            })
          )
        ),

        // Ship From Address
        h('div', { style: { borderTop: '1px solid ' + C.border, paddingTop: 12, marginTop: 2 } },
          h('label', { style: { fontSize: 12, fontWeight: 500, color: C.textMuted, marginBottom: 6, display: 'block' } }, 'Ship From Address'),
          h('select', { value: selectedShipFrom, onChange: function(e) { setSelectedShipFrom(e.target.value); },
            style: { width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid ' + C.border, background: C.bg, color: C.text, fontSize: 13, marginBottom: 8, fontFamily: 'inherit' }
          },
            isVendorDirectSelection && vendorData && h('option', { value: 'vendor' }, 'Vendor: ' + (vendorData.vendor_name || '') + ' (Vendor Direct)'),
            shipFromAddrs.map(function(a) {
              return h('option', { key: a.id, value: String(a.id) }, a.label + (a.city ? ' -- ' + a.city + ', ' + a.state : ''));
            }),
            h('option', { value: 'new' }, '+ Create New Address')
          ),
          // Vendor-direct address card with inline editor for missing fields (mirrors FBA)
          selectedShipFrom === 'vendor' && (function() {
            if (loadingVendor) return h('div', { style: { fontSize: 12, color: C.textMuted, padding: '8px 10px' } }, 'Loading vendor address…');
            if (!vendorData || !vendorEditForm) return null;
            var setField = function(k, v) { setVendorEditForm(Object.assign({}, vendorEditForm, { ['' + k]: v })); };
            return h('div', { style: { padding: '10px 12px', background: C.surface, borderRadius: 6, lineHeight: 1.5 } },
              h('div', { style: { fontWeight: 600, color: C.text, marginBottom: 6 } }, vendorData.vendor_name),
              vendorMissingFields.length > 0 && h('div', { style: { fontSize: 11, color: '#F59E0B', padding: '6px 8px', background: 'rgba(245,158,11,0.1)', borderRadius: 4, marginBottom: 8, display: 'flex', alignItems: 'flex-start', gap: 6 } },
                h(LIcon, { name: 'alert-triangle', size: 12 }),
                h('span', null, 'Missing required: ' + vendorMissingFields.map(function(f) { return VENDOR_FIELD_LABEL[f]; }).join(', ') + '. Saving will update the vendor record.')
              ),
              h('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 } },
                h(Inp, { label: 'Address Line 1 *', value: vendorEditForm.address1, onChange: function(e) { setField('address1', e.target.value); } }),
                h(Inp, { label: 'City *', value: vendorEditForm.city, onChange: function(e) { setField('city', e.target.value); } }),
                h(StateSelect, { label: 'State *', country: vendorEditForm.country || 'US', value: vendorEditForm.state, onChange: function(e) { setField('state', e.target.value); } }),
                h(Inp, { label: 'Postal Code *', value: vendorEditForm.zip, onChange: function(e) { setField('zip', e.target.value); } }),
                h(Inp, { label: 'Country *', value: vendorEditForm.country, onChange: function(e) { setField('country', e.target.value); } }),
                h(Inp, { label: 'Phone *', value: vendorEditForm.phone, onChange: function(e) { setField('phone', e.target.value); } })
              )
            );
          })(),
          selectedShipFrom !== 'new' && selectedShipFrom !== 'vendor' && (function() {
            var addr = shipFromAddrs.find(function(a) { return String(a.id) === selectedShipFrom; });
            if (!addr) return null;
            return h('div', { style: { fontSize: 12, color: C.textMuted, padding: '8px 10px', background: C.surface, borderRadius: 6, lineHeight: 1.5 } },
              addr.name && h('div', { style: { fontWeight: 500, color: C.text } }, addr.name),
              h('div', null, addr.address_line1),
              h('div', null, [addr.city, addr.state, addr.postal_code].filter(Boolean).join(', '))
            );
          })(),
          selectedShipFrom === 'new' && h('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 } },
            h(Inp, { label: 'Label *', value: newAddrForm.label, onChange: function(e) { setNewAddrForm(Object.assign({}, newAddrForm, { label: e.target.value })); }, placeholder: 'e.g. Main Warehouse' }),
            h(Inp, { label: 'Name', value: newAddrForm.name, onChange: function(e) { setNewAddrForm(Object.assign({}, newAddrForm, { name: e.target.value })); } }),
            h(Inp, { label: 'Address *', value: newAddrForm.address_line1, onChange: function(e) { setNewAddrForm(Object.assign({}, newAddrForm, { address_line1: e.target.value })); } }),
            h(Inp, { label: 'City', value: newAddrForm.city, onChange: function(e) { setNewAddrForm(Object.assign({}, newAddrForm, { city: e.target.value })); } }),
            h(StateSelect, { label: 'State', country: newAddrForm.country_code || 'US', value: newAddrForm.state, onChange: function(e) { setNewAddrForm(Object.assign({}, newAddrForm, { state: e.target.value })); } }),
            h(Inp, { label: 'ZIP', value: newAddrForm.postal_code, onChange: function(e) { setNewAddrForm(Object.assign({}, newAddrForm, { postal_code: e.target.value })); } }),
            h(Inp, { label: 'Phone', value: newAddrForm.phone, onChange: function(e) { setNewAddrForm(Object.assign({}, newAddrForm, { phone: e.target.value })); } })
          )
        ),

        h('div', { style: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 } },
          h(Btn, { onClick: closeCreate }, 'Cancel'),
          h(Btn, { variant: 'primary', onClick: handleCreate, disabled: creating || !createPOIds.length }, creating ? 'Creating...' : 'Create WFS Shipment')
        )
      )
    )
  );
}

// ─── WFS Detail Page (5-Step Wizard) ───
// A Walmart quote record counts as booked when its status says so. The status sits on the record
// for list shapes and under rateQuote for the single-object payload GET carrier-rate-quotes returns
// (2026-10-22, 3576818WFB: payload.rateQuote.status = "ACTIVE").
// ACTIVE is NOT a booking: Walmart's quote record reads ACTIVE as soon as a rate is quoted, so a
// reload between Get Rate Quote and Book Carrier would otherwise show "Carrier Booked". Our own
// record (plan.carrier / tracking) is the primary signal; this only recognises an explicit
// CONFIRMED / BOOKED from Walmart.
function wfsQuoteBooked(q) {
  if (!q) return false;
  var st = q.status || (q.rateQuote && q.rateQuote.status) || '';
  return st === 'CONFIRMED' || st === 'BOOKED';
}

// Ship-step cost summary (Anthony 2026-10-22: "the final step isn't summarizing the full shipping
// fees, placement fees and prep fees like the FBA Shipment workflow"). Prep = the prep step's own
// math (label / bag units × the Settings rates); the same numbers the prep step shows.
function wfsPrepFeeTotals(items, itemPrep, prepFees) {
  var labelUnits = 0, bagUnits = 0;
  (items || []).forEach(function(it) {
    var pr = (itemPrep || {})[it.id] || {};
    if (pr.label) labelUnits += (it.quantity || 0);
    if (pr.bag) bagUnits += (it.quantity || 0);
  });
  var rates = prepFees || { label: 0, bag: 0 };
  var total = labelUnits * (rates.label || 0) + bagUnits * (rates.bag || 0);
  return { labelUnits: labelUnits, bagUnits: bagUnits, total: Math.round(total * 100) / 100 };
}
function wfsCostBlock(label, amount, color, sub) {
  return h('div', null,
    h('div', { style: { fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 2 } }, label),
    h('div', { style: { fontSize: 20, fontWeight: 700, color: color } }, '$' + (Number(amount) || 0).toFixed(2)),
    sub ? h('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 2 } }, sub) : null
  );
}

function WfsDetailPage({ planId, showToast, onNavigate, currentUser }) {
  var [plan, setPlan] = useState(null);
  var [loading, setLoading] = useState(true);
  var [actionLoading, setActionLoading] = useState('');

  // Ship-from address editing (mirrors FBA)
  var [editAddress, setEditAddress] = useState(false);
  var [detailShipFromAddrs, setDetailShipFromAddrs] = useState([]);
  var [detailSelectedAddr, setDetailSelectedAddr] = useState('current');
  var [detailNewAddrForm, setDetailNewAddrForm] = useState({ label: '', name: '', address_line1: '', city: '', state: '', postal_code: '', country_code: 'US', phone: '' });

  var loadPlan = useCallback(function() {
    if (!planId) return;
    setLoading(true);
    api('/wfs-shipments/plans/' + planId).then(function(data) { setPlan(data); }).catch(function(e) { showToast(e.message, 'error'); }).finally(function() { setLoading(false); });
  }, [planId]);
  useEffect(function() { loadPlan(); }, [loadPlan]);

  // Step definitions: Review → Prep → Submit → Pack → Labels → Ship
  var steps = ['Review Items', 'Prep Options', 'Submit to Walmart', 'Pack Boxes', 'Get Labels', 'Ship'];
  var statusStepMap = { draft: 0, preview_ready: 2, submit_failed: 2, submitted: 3, packing: 3, packed: 4, labels_ready: 5, shipped: 5, receiving: 5, delivered: 5, closed: 5, cancelled: 5 };
  var currentStep = plan ? (statusStepMap[plan.status] != null ? statusStepMap[plan.status] : 0) : 0;
  var [manualStep, setManualStep] = useState(null);
  var step = manualStep != null ? manualStep : currentStep;

  // Packing completion state (from WfsBoxPackingView)
  var [packingComplete, setPackingComplete] = useState(false);

  // ─── Supplier-pack (case) configuration — case-packed plans (2026-10-30) ───
  // One row per item: units per carton + carton L/W/H/weight. Pre-filled from the item's saved
  // config (the plan GET folds the product defaults in as case_*), autosaved to
  // PUT /plans/:id/case-config, and validated here exactly like the server (whole cartons, size +
  // weight present) so Continue / Fetch Placement / Submit can be gated instantly. Walmart receives
  // vendorPackQty = cartons, innerPackQty = units per carton + the carton block — a supplier pack.
  var isCasePackedPlan = !!(plan && plan.packing_mode === 'case_packed');
  var [caseCfg, setCaseCfg] = useState({});            // { itemId: { upc, length, width, height, weight, save_default, _touched } }
  var caseCfgRef = useRef({});
  var caseCfgTimer = useRef(null);
  var [caseCfgDirty, setCaseCfgDirty] = useState(false);
  var [caseCfgSaving, setCaseCfgSaving] = useState(false);
  useEffect(function() { return function() { if (caseCfgTimer.current) clearTimeout(caseCfgTimer.current); }; }, []);
  useEffect(function() {
    if (!plan || plan.packing_mode !== 'case_packed') return;
    var next = {};
    (plan.items || []).forEach(function(it) {
      var cur = caseCfgRef.current[it.id];
      if (cur && cur._touched) { next[it.id] = cur; return; } // keep edits that have not saved yet
      var hasProductDefault = parseInt(it.case_qty) > 0 && parseFloat(it.case_length) > 0;
      next[it.id] = {
        upc: parseInt(it.inner_pack_qty) > 0 ? String(it.inner_pack_qty) : (parseInt(it.case_qty) > 0 ? String(it.case_qty) : ''),
        length: parseFloat(it.case_length) > 0 ? String(it.case_length) : '',
        width: parseFloat(it.case_width) > 0 ? String(it.case_width) : '',
        height: parseFloat(it.case_height) > 0 ? String(it.case_height) : '',
        weight: parseFloat(it.case_weight) > 0 ? String(it.case_weight) : '',
        save_default: !hasProductDefault,
        _touched: false,
      };
    });
    caseCfgRef.current = next;
    setCaseCfg(next);
  }, [plan]);
  var caseRowProblems = function(it, cfg) {
    var qty = (it.quantity || 0) - (it.quantity_missing || 0);
    if (qty <= 0) return [];
    var c = cfg || {};
    var upc = parseInt(c.upc) || 0;
    var probs = [];
    if (!upc) probs.push('units per carton not set');
    else if (qty % upc !== 0) probs.push(qty + ' units is not a whole number of cartons of ' + upc + ' (' + Math.floor(qty / upc) * upc + ' or ' + Math.ceil(qty / upc) * upc + ' units would be)');
    if (!(parseFloat(c.length) > 0 && parseFloat(c.width) > 0 && parseFloat(c.height) > 0)) probs.push('carton size missing');
    if (!(parseFloat(c.weight) > 0)) probs.push('carton weight missing');
    return probs;
  };
  var saveCaseCfg = function(snapshot) {
    var rows = (plan ? (plan.items || []) : []).map(function(it) {
      var c = snapshot[it.id] || {};
      return { item_id: it.id, units_per_case: parseInt(c.upc) || null, length: c.length, width: c.width, height: c.height, weight: c.weight, save_default: !!c.save_default };
    });
    if (!rows.length) return Promise.resolve();
    setCaseCfgSaving(true);
    return api('/wfs-shipments/plans/' + planId + '/case-config', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: rows }) })
      .then(function() {
        var next = {};
        Object.keys(caseCfgRef.current).forEach(function(k) { next[k] = Object.assign({}, caseCfgRef.current[k], { _touched: false }); });
        caseCfgRef.current = next; setCaseCfg(next); setCaseCfgDirty(false);
      })
      .catch(function(e) { showToast('Case configuration not saved: ' + e.message, 'error'); })
      .finally(function() { setCaseCfgSaving(false); });
  };
  var editCaseCfg = function(itemId, field, value) {
    var row = Object.assign({}, caseCfgRef.current[itemId] || {}, { _touched: true });
    row[field] = value;
    var next = Object.assign({}, caseCfgRef.current); next[itemId] = row;
    caseCfgRef.current = next; setCaseCfg(next); setCaseCfgDirty(true);
    if (caseCfgTimer.current) clearTimeout(caseCfgTimer.current);
    caseCfgTimer.current = setTimeout(function() { caseCfgTimer.current = null; saveCaseCfg(caseCfgRef.current); }, 600);
  };
  // Flush a pending autosave before anything that reads the rows server-side (preview / submit).
  var flushCaseCfg = function() {
    if (!isCasePackedPlan || !caseCfgTimer.current) return Promise.resolve();
    clearTimeout(caseCfgTimer.current); caseCfgTimer.current = null;
    return saveCaseCfg(caseCfgRef.current);
  };

  // Placement (ITS) preview + choice — Phase 4
  var [placementChoice, setPlacementChoice] = useState('N');
  var [previews, setPreviews] = useState(null);
  var [previewLoading, setPreviewLoading] = useState(false);
  var [submitPolling, setSubmitPolling] = useState(false);
  // review M7 #106: the Walmart allocation poll is held in a ref and cleared on unmount — it used
  // to keep polling (and toasting on whatever page the user was on) for up to 40 × 5 s.
  var submitPollRef = useRef(null);
  useEffect(function() { return function() { if (submitPollRef.current) { clearInterval(submitPollRef.current); submitPollRef.current = null; } }; }, []);
  // Items Walmart rejected on preview/submit (not WFS-active in its catalog) — [{item_id, sku, reasons}].
  // Fresh 422s land in state; plan.last_item_errors (persisted server-side) covers page refreshes.
  var [rejectedItems, setRejectedItems] = useState(null);
  var [rejectedDismissed, setRejectedDismissed] = useState(false);
  var [removingRejected, setRemovingRejected] = useState(false);
  var effectiveRejected = rejectedDismissed ? null : (rejectedItems || (plan && plan.last_item_errors) || null);
  useEffect(function() { if (plan) setPlacementChoice(plan.placement_choice === 'single' ? 'Y' : 'N'); }, [plan && plan.id]);

  // Multi-shipment pack: which shipment is being packed + per-shipment completion (Phase 5)
  var [activeShipIdx, setActiveShipIdx] = useState(0);
  var [shipPackDone, setShipPackDone] = useState({});

  // Prep state
  var [prepMode, setPrepMode] = useState('seller');
  var [itemPrep, setItemPrep] = useState({});
  var [prepFees, setPrepFees] = useState({ label: 0.45, bag: 0.60 });
  useEffect(function() {
    if (plan) {
      setPrepMode(plan.prep_mode || 'seller');
      var ps = {};
      (plan.items || []).forEach(function(it) {
        var svc = it.prep_services || [];
        ps[it.id] = { label: svc.some(function(s) { return s.serviceType === 'LABEL'; }), bag: svc.some(function(s) { return s.serviceType === 'BAG'; }) };
      });
      setItemPrep(ps);
    }
    api('/settings').then(function(s) {
      if (s) setPrepFees({ label: parseFloat(s.wfs_label_fee) || 0.45, bag: parseFloat(s.wfs_bag_fee) || 0.60 });
    }).catch(function(e) { console.error('[silent-catch]', e); });
  }, [plan]);

  // Items
  var items = plan ? (plan.items || []) : [];
  var totalUnits = items.reduce(function(s, i) { return s + (i.quantity || 0); }, 0);
  var totalPacked = items.reduce(function(s, i) { return s + (i.quantity_packed || 0); }, 0);
  var totalMissing = items.reduce(function(s, i) { return s + (i.quantity_missing || 0); }, 0);
  var allPacked = totalPacked >= (totalUnits - totalMissing) && totalUnits > 0;
  // Supplier-pack validation (case-packed plans): what still blocks the plan, mirrored server-side.
  var caseProblemRows = isCasePackedPlan ? items.map(function(it) { return { item: it, probs: caseRowProblems(it, caseCfg[it.id]) }; }).filter(function(r) { return r.probs.length > 0; }) : [];
  var caseCfgValid = !isCasePackedPlan || (items.length > 0 && caseProblemRows.length === 0);
  var caseCfgTotalCartons = isCasePackedPlan ? items.reduce(function(s, it) {
    var c = caseCfg[it.id] || {}; var upc = parseInt(c.upc) || 0; var qty = (it.quantity || 0) - (it.quantity_missing || 0);
    return s + (upc > 0 && qty > 0 ? Math.ceil(qty / upc) : 0);
  }, 0) : 0;

  // ─── Action handlers ───

  // NOTE: these must be assigned BEFORE the component's return statement — `var fn =`
  // placed in the render-functions zone below the return hoists the name but never runs
  // the assignment, leaving onClick handlers silently undefined.
  var fetchPreview = function() {
    setPreviewLoading(true);
    flushCaseCfg().then(function() { return api('/wfs-shipments/plans/' + planId + '/preview', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }); })
      .then(function(res) { setPreviews(res.options || []); setRejectedItems(null); setRejectedDismissed(true); })
      .catch(function(e) {
        if (e.apiBody && e.apiBody.walmart_item_errors) { setRejectedItems(e.apiBody.walmart_item_errors); setRejectedDismissed(false); }
        if (e.apiBody && e.apiBody.case_config_problems) setManualStep(0); // the table on Review Items shows what to fix
        showToast(e.message, 'error');
      })
      .finally(function() { setPreviewLoading(false); });
  };

  // Remove the Walmart-rejected items from the plan, then re-fetch placement options.
  var removeRejectedAndRefetch = function() {
    if (!effectiveRejected || !effectiveRejected.length) return;
    setRemovingRejected(true);
    api('/wfs-shipments/plans/' + planId + '/remove-items', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ item_ids: effectiveRejected.map(function(r) { return r.item_id; }) }) })
      .then(function(r) {
        var msg = 'Removed ' + r.removed + ' item' + (r.removed === 1 ? '' : 's') + ' from the shipment';
        var vdc = r.vendor_direct_carry;
        if (vdc && vdc.moved > 0) msg += ' — also removed ' + vdc.moved + ' line' + (vdc.moved === 1 ? '' : 's') + ' from PO ' + vdc.po_number + ' and carried to draft PO #' + vdc.draft_po_number + ' (re-send the revised PO to the vendor)';
        if (vdc && vdc.skipped > 0) msg += ' — ' + vdc.skipped + ' line' + (vdc.skipped === 1 ? '' : 's') + ' already received — left on PO ' + vdc.po_number;
        showToast(msg, 'success');
        setRejectedItems(null);
        setRejectedDismissed(true); // suppress the stale plan copy until loadPlan returns the cleared column
        loadPlan();
        fetchPreview();
      })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setRemovingRejected(false); });
  };

  var handleSubmit = function() {
    setActionLoading('submit');
    // The case configuration lives on the plan's item rows (autosaved from Review Items); the
    // server builds the Walmart payload from them, so the body carries nothing but a flush first.
    flushCaseCfg().then(function() { return api('/wfs-shipments/plans/' + planId + '/submit', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }); })
      .then(function(res) {
        if (res.mode === 'async') {
          showToast('Submitted — Walmart is allocating fulfillment center(s). This can take a few minutes.', 'warning');
          setSubmitPolling(true);
          pollSubmitStatus();
        } else {
          var n = res.shipmentCount || 1;
          showToast('Submitted to Walmart — ' + n + ' shipment' + (n === 1 ? '' : 's'), 'success');
          if (res.warning) showToast(res.warning, 'warning');
          // The placement preview is a projection; Walmart's allocator makes the real
          // call at create-time (seen: same SKU 5-way split one day, single CROSSDOCK
          // the next). Say so explicitly when the outcome differs from the choice.
          if (n === 1 && plan && plan.placement_choice === 'split') {
            showToast('Walmart consolidated this order into a single shipment — the preview split is projected; the final allocation is Walmart\'s.', 'warning');
          }
          setManualStep(3);
          loadPlan();
        }
      })
      .catch(function(e) {
        if (e.apiBody && e.apiBody.walmart_item_errors) { setRejectedItems(e.apiBody.walmart_item_errors); setRejectedDismissed(false); }
        if (e.apiBody && e.apiBody.case_config_problems) setManualStep(0); // the table on Review Items shows what to fix
        showToast(e.message, 'error');
        loadPlan(); // submit failure flips plan status to submit_failed — reflect it
      })
      .finally(function() { setActionLoading(''); });
  };

  // Poll for async create resolution (Walmart allocating FCs after submit).
  var pollSubmitStatus = function() {
    var attempts = 0;
    if (submitPollRef.current) clearInterval(submitPollRef.current);
    var iv = setInterval(function() {
      attempts++;
      api('/wfs-shipments/plans/' + planId).then(function(data) {
        if (submitPollRef.current !== iv) return; // cleared (unmount or a newer submit)
        var hasShip = (data.shipments || []).some(function(s) { return s.walmart_shipment_id; });
        if (hasShip || data.status === 'submit_failed' || attempts > 40) {
          clearInterval(iv); submitPollRef.current = null; setSubmitPolling(false); setPlan(data);
          if (data.status === 'submit_failed') showToast('Walmart did not return shipments in time — check Walmart or retry submit.', 'error');
          else if (hasShip) { showToast('Shipment(s) allocated by Walmart.', 'success'); setManualStep(3); }
        }
      }).catch(function() {});
    }, 5000);
    submitPollRef.current = iv;
  };

  // Trigger XLSX picklist download for a shipment — auth via same-origin cookie.
  var exportWfsShipmentXlsx = function(s) {
    var wid = (s.walmart_shipment_id || ('Draft' + s.id)).replace(/[^A-Za-z0-9_-]/g, '');
    var a = document.createElement('a');
    a.href = '/api/wfs-shipments/shipments/' + s.id + '/export-xlsx';
    a.download = wid + '_Picklist.xlsx';
    document.body.appendChild(a);
    a.click();
    setTimeout(function() { document.body.removeChild(a); }, 0);
  };

  // Manual header Refresh removed (FBA parity): syncWfsShipmentStatuses polls Walmart
  // for all active plans every 4h, which covers status/receiving/tracking updates.
  // Tracking state
  var [trackCarrier, setTrackCarrier] = useState('');
  var [trackNumber, setTrackNumber] = useState('');
  var [trackPro, setTrackPro] = useState('');
  var [trackBol, setTrackBol] = useState('');

  useEffect(function() {
    if (plan) {
      setTrackCarrier(plan.carrier || '');
      setTrackNumber(plan.tracking_number || '');
      setTrackPro(plan.pro_number || '');
      setTrackBol(plan.bol_number || '');
    }
  }, [plan && plan.id]);

  var handleShip = function() {
    setActionLoading('ship');
    api('/wfs-shipments/plans/' + planId + '/tracking', { method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ carrier: trackCarrier, tracking_number: trackNumber, pro_number: trackPro, bol_number: trackBol }) })
      .then(function(r) { showToast(r.warning ? r.warning : 'Tracking updated — marked as shipped', r.warning ? 'warning' : 'success'); loadPlan(); })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setActionLoading(''); });
  };

  var handleCancel = async function() {
    if (!(await appConfirm('Cancel this WFS shipment? This cannot be undone.', { danger: true, okLabel: 'Cancel shipment' }))) return;
    api('/wfs-shipments/plans/' + planId, { method: 'DELETE' })
      .then(function() {
        showToast('WFS shipment cancelled', 'success');
        // Vendor-direct single-PO plans return to their PO (the create wizard's cancel convention — that
        // wizard only auto-opens from a vendor-direct PO, and the cancel route puts such a PO back to
        // "confirmed", where Create WFS Shipment is offered again). Warehouse and multi-PO plans go back
        // to the WFS list (Anthony 2026-10-30: cancelling a warehouse plan landed on its PO). Mirrors FBA.
        var poIds = (plan && plan.po_ids && plan.po_ids.length) ? plan.po_ids : (plan && plan.po_id ? [plan.po_id] : []);
        if (poIds.length === 1 && plan && plan.po_wfs_direct) onNavigate('po-detail', poIds[0]);
        else onNavigate('wfs-shipments');
      })
      .catch(function(e) { showToast(e.message, 'error'); });
  };

  // Step-advance handlers — hoisted so the top action bar can drive each step (mirrors FBA's header).
  var savePrepAndContinue = function() {
    var payload = {
      prep_mode: prepMode,
      items: items.map(function(it) {
        var p = itemPrep[it.id] || {};
        var services = [];
        if (prepMode === 'walmart') { if (p.label) services.push({ serviceType: 'LABEL' }); if (p.bag) services.push({ serviceType: 'BAG' }); }
        return { item_id: it.id, prep_services: services };
      })
    };
    api('/wfs-shipments/plans/' + planId + '/prep', { method: 'PUT', body: JSON.stringify(payload) })
      .then(function() { setManualStep(2); loadPlan(); })
      .catch(function(e) { showToast(e.message, 'error'); });
  };
  var handlePackedContinue = function() {
    api('/wfs-shipments/plans/' + planId + '/status', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: 'packed' }) })
      .then(function() { return api('/wfs-shipments/plans/' + planId); })
      .then(function(data) { setPlan(data); setManualStep(4); })
      .catch(function() { setManualStep(4); });
  };
  var handleLabelsContinue = function() {
    api('/wfs-shipments/plans/' + planId + '/status', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: 'labels_ready' }) })
      .then(function() { return api('/wfs-shipments/plans/' + planId); })
      .then(function(data) { setPlan(data); setManualStep(5); })
      .catch(function() { setManualStep(5); });
  };

  // ─── Address editing (mirrors FBA) ───
  var detailAddrToSource = function(a) {
    return { name: a.name, addressLine1: a.address_line1, city: a.city, state: a.state, postalCode: a.postal_code, countryCode: a.country_code || 'US', phone: a.phone || '' };
  };

  var openAddressEdit = function() {
    api('/fba-shipments/ship-from-addresses').then(function(addrs) {
      setDetailShipFromAddrs(addrs);
      setDetailSelectedAddr(addrs.length ? String(addrs[0].id) : 'new');
      setDetailNewAddrForm({ label: '', name: '', address_line1: '', city: '', state: '', postal_code: '', country_code: 'US', phone: '' });
      setEditAddress(true);
    }).catch(function(e) { showToast(e.message, 'error'); });
  };

  var handleSaveAddress = function() {
    var doSave = function(sourceAddr) {
      api('/wfs-shipments/plans/' + planId, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ship_from_address: sourceAddr }) })
        .then(function() { showToast('Ship-from address updated', 'success'); setEditAddress(false); loadPlan(); })
        .catch(function(e) { showToast(e.message, 'error'); });
    };
    if (detailSelectedAddr === 'new') {
      if (!detailNewAddrForm.label || !detailNewAddrForm.address_line1) { showToast('Label and address are required', 'error'); return; }
      api('/fba-shipments/ship-from-addresses', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(detailNewAddrForm) })
        .then(function(saved) { doSave(detailAddrToSource(saved)); })
        .catch(function(e) { showToast(e.message, 'error'); });
    } else {
      var addr = detailShipFromAddrs.find(function(a) { return String(a.id) === detailSelectedAddr; });
      if (!addr) { showToast('Select an address', 'error'); return; }
      doSave(detailAddrToSource(addr));
    }
  };

  var [palletCount, setPalletCount] = useState(1);
  var [carrierQuotes, setCarrierQuotes] = useState(null);
  var [carrierBooked, setCarrierBooked] = useState(false);
  var [carrierLoading, setCarrierLoading] = useState('');
  var [carrierLabelReady, setCarrierLabelReady] = useState(false);
  var [carrierMode, setCarrierMode] = useState('preferred');

  // Ship-step cost summary: the ITS transfer charge lives in the placement previews, which only the
  // placement step fetches — load the stored rows once per plan, in their own state.
  var [shipCostPreviews, setShipCostPreviews] = useState(null);
  useEffect(function() {
    if (!plan || !plan.id) return;
    api('/wfs-shipments/plans/' + plan.id + '/previews').then(function(rows) { setShipCostPreviews(Array.isArray(rows) ? rows : []); }).catch(function() { setShipCostPreviews([]); });
  }, [plan && plan.id]);

  // Detect if carrier was already booked. Our own record first (2026-10-22): the server writes
  // carrier / tracking on ANY 2xx confirm and its double-booking guard reads the same columns —
  // the Walmart check below is the supplement for bookings made outside the app. Walmart nests the
  // quote status under payload.rateQuote; reading q.status on the payload showed the booked plan
  // 143 as "Get Rate Quote" after a reload.
  useEffect(function() {
    if (!plan || !plan.walmart_shipment_id) return;
    if (plan.carrier || plan.tracking_number) setCarrierBooked(true);
    api('/wfs-shipments/plans/' + plan.id + '/carrier-quote').then(function(data) {
      var quotes = data.rateQuotes || data.quotes || data.payload || [];
      if (!Array.isArray(quotes)) quotes = [quotes];
      var active = quotes.find(function(q) { return wfsQuoteBooked(q); });
      if (active) {
        setCarrierQuotes(data);
        setCarrierBooked(true);
      }
    }).catch(function(e) { console.error('[silent-catch]', e); });
  }, [plan && plan.id]);

  if (loading || !plan) return h('div', { style: { padding: 40, textAlign: 'center', color: C.textMuted } }, 'Loading...');

  var isCancelled = plan.status === 'cancelled';
  var isClosed = plan.status === 'closed';
  var isShipped = plan.status === 'shipped' || plan.status === 'delivered';
  // Top action-bar gates (mirror FBA): which primary button shows + whether it's enabled.
  var alreadySubmitted = (plan.status !== 'submit_failed') && (!!plan.walmart_shipment_id || (plan.shipments && plan.shipments.some(function(s) { return s.walmart_shipment_id; })));
  var isMultiShip = !!(plan.shipments && plan.shipments.length > 1);
  // A shipment counts as packed from the DATA (packed+missing >= units), with the live
  // per-view report (shipPackDone) as an overlay. Relying on shipPackDone alone greyed
  // Continue after a refresh: only the actively-viewed shipment ever reported its status.
  var shipmentPackedFromData = function(s) {
    if (shipPackDone[s.id]) return true;
    var su = (s.items || []).reduce(function(a, it) { return a + (it.quantity || 0); }, 0);
    var sp = (s.items || []).reduce(function(a, it) { return a + (it.quantity_packed || 0) + (it.quantity_missing || 0); }, 0);
    return su > 0 && sp >= su;
  };
  var packComplete = isMultiShip ? plan.shipments.every(shipmentPackedFromData) : packingComplete;

  // Per-shipment model (Phase 3): N=1 plans have a single shipment row. Fall back to the
  // plan itself when no shipment row exists (transitional / cancelled plans).
  var shipment = (plan.shipments && plan.shipments.length) ? plan.shipments[0] : null;
  var shipmentId = shipment ? shipment.id : null;
  // Friendly FC label for single-shipment (plan-scalar) display sites — the plan scalar
  // only holds the numeric node; the shipment row carries Walmart's warehouse name.
  var planFcLabel = (shipment && (shipment.display_name || shipment.destination_fc)) || plan.destination_fc;

  // ─── Render ───
  return h('div', { style: { animation: 'fadeIn 0.3s ease' } },
    // Header (matches FBA)
    h('div', { style: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 } },
      h('button', { onClick: function() { onNavigate('wfs-shipments'); }, style: { background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, padding: 4 } }, h(LIcon, { name: 'arrow-left', size: 18 })),
      h('div', null,
        h('h2', { style: { fontSize: 18, fontWeight: 700, color: C.text, margin: 0 } }, 'WFS Shipment \u2014 PO ' + (plan.po_numbers || plan.po_number || 'Draft')),
        h('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 2 } },
          (plan.vendor_name || '') + ' \u2192 ' + (plan.wh_code || 'WFS') + ' | ' + (plan.shipment_type || 'SPD') + ' | ',
          h(WfsBadge, { status: plan.status })
        )
      ),
      h('div', { style: { marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 } },
        plan.walmart_shipment_id && h('span', { style: { fontFamily: 'inherit', fontSize: 11, color: C.textMuted } }, 'WFS: ' + plan.walmart_shipment_id),
        step > 0 && ['closed', 'cancelled'].indexOf(plan.status) === -1 && h(Btn, { size: 'sm', onClick: function() { setManualStep(step - 1); } }, h(LIcon, { name: 'arrow-left', size: 14 }), ' Back'),
        ['draft', 'preview_ready', 'submit_failed'].indexOf(plan.status) >= 0 && step === 0 && h(Btn, { size: 'sm', variant: 'primary', disabled: !caseCfgValid, onClick: function() { flushCaseCfg(); setManualStep(1); } }, 'Continue to Prep ', h(LIcon, { name: 'arrow-right', size: 14 })),
        step === 1 && h(Btn, { size: 'sm', variant: 'primary', onClick: savePrepAndContinue }, 'Continue ', h(LIcon, { name: 'arrow-right', size: 14 })),
        step === 2 && !submitPolling && !alreadySubmitted && h(Btn, { size: 'sm', variant: 'primary', disabled: actionLoading === 'submit' || !caseCfgValid, onClick: handleSubmit }, actionLoading === 'submit' ? 'Submitting...' : 'Continue'),
        step === 2 && !submitPolling && alreadySubmitted && h(Btn, { size: 'sm', variant: 'primary', onClick: function() { setManualStep(3); } }, 'Next: Pack Boxes ', h(LIcon, { name: 'arrow-right', size: 14 })),
        step === 3 && h(Btn, { size: 'sm', variant: 'primary', disabled: !packComplete, onClick: handlePackedContinue }, 'Continue ', h(LIcon, { name: 'arrow-right', size: 14 })),
        step === 4 && h(Btn, { size: 'sm', variant: 'primary', onClick: handleLabelsContinue }, 'Next: Ship ', h(LIcon, { name: 'arrow-right', size: 14 })),
        step === 5 && !isMultiShip && !isShipped && !isClosed && h(Btn, { size: 'sm', variant: 'primary', disabled: actionLoading === 'ship' || !trackCarrier, onClick: handleShip }, actionLoading === 'ship' ? 'Saving...' : 'Mark as Shipped'),
        ['closed', 'cancelled'].indexOf(plan.status) === -1 && h(Btn, { size: 'sm', onClick: handleCancel, style: { background: 'rgba(248,113,113,0.1)', color: '#F87171', border: '1px solid rgba(248,113,113,0.3)' } }, h(LIcon, { name: 'x', size: 13 }), ' Cancel')
      )
    ),

    // Delivered-but-never-receipted claim warnings — surfaced above the stepper so they
    // are visible regardless of which step/branch renders below.
    (plan.shipments || []).map(function(ws) { return h(WfsDeliveredNotReceivedWarning, { key: 'dnr' + ws.id, s: ws }); }),

    // Stepper (circle style matching FBA)
    h('div', { style: { display: 'flex', alignItems: 'center', margin: '0 0 24px 0', overflowX: 'auto', padding: '0 4px' } },
      steps.map(function(label, idx) {
        // "reach" = the furthest step the user has progressed to, whether by plan status
        // (currentStep) or manual navigation (step). Basing completion only on status made
        // earlier steps drop back to dim gray when navigating forward on a draft plan.
        var reach = Math.max(step, currentStep);
        var isComplete = idx < reach;
        var isActive = idx === step;
        var color = isComplete ? C.success : isActive ? C.accent : C.textDim;
        return h(Fragment, { key: idx },
          idx > 0 && h('div', { style: { flex: 1, height: 2, background: idx <= reach ? C.success : C.border, minWidth: 16 } }),
          h('div', { style: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, cursor: idx <= reach + 1 ? 'pointer' : 'default', minWidth: 70, opacity: idx > reach + 1 ? 0.55 : 1 },
            onClick: function() { if (idx <= reach + 1) setManualStep(idx); } },
            h('div', { style: { width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600,
              border: '2px solid ' + color, background: isComplete ? C.success : isActive ? C.accent : 'transparent', color: (isComplete || isActive) ? '#fff' : C.textDim, transition: 'all 0.2s' } },
              isComplete ? h(LIcon, { name: 'check', size: 13, color: '#fff' }) : String(idx + 1)),
            h('span', { style: { fontSize: 10, fontWeight: isActive ? 600 : 400, color: color, textAlign: 'center', whiteSpace: 'nowrap' } }, label)
          )
        );
      })
    ),

    // Step content
    step === 0 && renderReviewStep(),
    step === 1 && renderPrepStep(),
    step === 2 && renderSubmitStep(),
    step === 3 && renderPackStep(),
    step === 4 && renderLabelsStep(),
    step === 5 && renderShipStep()
  );

  // ─── Step renderers ───

  function renderReviewStep() {
    var addr = plan.ship_from_address || {};
    return h('div', null,
      // Ship From Address card (matches FBA — with Change button)
      h(Card, { style: { marginBottom: 12 } },
        h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 } },
          h('h3', { style: { fontSize: 14, fontWeight: 600, color: C.text, margin: 0 } }, 'Ship From Address'),
          h('button', { onClick: openAddressEdit, style: { background: 'none', border: 'none', cursor: 'pointer', color: C.accent, fontSize: 12, fontWeight: 500 } }, h(LIcon, { name: 'edit-2', size: 13 }), ' Change')
        ),
        h('div', { style: { fontSize: 13, color: C.text, lineHeight: 1.6 } },
          addr.name && h('div', { style: { fontWeight: 600 } }, addr.name),
          (addr.addressLine1 || addr.address_line1) && h('div', null, addr.addressLine1 || addr.address_line1),
          h('div', null, [addr.city, addr.stateOrProvinceCode || addr.state, addr.postalCode || addr.postal_code].filter(Boolean).join(', ')),
          (addr.countryCode || addr.country_code) && h('div', { style: { color: C.textMuted } }, addr.countryCode || addr.country_code),
          (addr.phoneNumber || addr.phone) && h('div', { style: { color: C.textMuted } }, addr.phoneNumber || addr.phone)
        )
      ),
      // Supplier-pack configuration — case-packed plans (2026-10-30). Walmart receives
      // vendorPackQty = cartons / innerPackQty = units per carton + the carton size/weight block, so
      // every line needs a whole number of cartons, a size and a weight before Continue / Submit.
      isCasePackedPlan && (function() {
        var locked = alreadySubmitted;
        var inp = function(it, field, width, stepAttr) {
          var c = caseCfg[it.id] || {};
          return h('input', { type: 'number', min: field === 'upc' ? 1 : 0.1, step: stepAttr || 'any', value: c[field] == null ? '' : c[field], disabled: locked,
            onChange: function(e) { editCaseCfg(it.id, field, e.target.value); },
            style: { width: width, padding: '4px 6px', borderRadius: 4, border: '1px solid ' + C.border, background: locked ? C.surface : C.bg, color: C.text, textAlign: 'center', fontSize: 12 } });
        };
        var th = function(label, extra) { return h('th', { style: Object.assign({ textAlign: 'center', padding: '6px 4px', borderBottom: '1px solid ' + C.border, color: C.textMuted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', whiteSpace: 'nowrap' }, extra || {}) }, label); };
        return h(Card, { style: { marginBottom: 12, border: '1px solid ' + (caseCfgValid ? C.border : 'rgba(248,113,113,0.55)') } },
          h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 } },
            h('h3', { style: { fontSize: 14, fontWeight: 600, color: C.text, margin: 0 } }, 'Supplier Pack Configuration'),
            h('div', { style: { fontSize: 11, color: caseCfgDirty ? '#F59E0B' : C.textMuted } }, locked ? 'Submitted to Walmart' : (caseCfgSaving ? 'Saving…' : (caseCfgDirty ? 'Unsaved changes' : 'Saved')))
          ),
          h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 10, lineHeight: 1.5 } },
            'Walmart receives this plan as supplier packs: one SKU per carton at a fixed quantity. Set the units per carton and the carton size and weight for every SKU — each quantity must be a whole number of cartons.'),
          h('table', { style: { width: '100%', fontSize: 12, borderCollapse: 'collapse' } },
            h('thead', null, h('tr', null,
              th('SKU', { textAlign: 'left' }), th('Qty'), th('Units / Carton'), th('Cartons'), th('L (in)'), th('W (in)'), th('H (in)'), th('Weight (lb)'), th('Save as default')
            )),
            h('tbody', null, items.map(function(it) {
              var c = caseCfg[it.id] || {};
              var qty = (it.quantity || 0) - (it.quantity_missing || 0);
              var upc = parseInt(c.upc) || 0;
              var probs = caseRowProblems(it, c);
              var cartons = (upc > 0 && qty > 0 && qty % upc === 0) ? qty / upc : null;
              return h('tr', { key: it.id, style: { borderBottom: '1px solid ' + C.border + '44', background: probs.length ? 'rgba(248,113,113,0.06)' : 'transparent' } },
                h('td', { style: { padding: '6px 4px' } },
                  h('div', { style: { fontWeight: 600 } }, it.sku),
                  h('div', { style: { fontSize: 11, color: C.textMuted } }, it.vendor_title || ''),
                  probs.length > 0 && h('div', { style: { fontSize: 11, color: '#F87171', marginTop: 2 } }, probs.join(' · '))
                ),
                h('td', { style: { padding: '6px 4px', textAlign: 'center', fontWeight: 600 } }, qty),
                h('td', { style: { padding: '6px 4px', textAlign: 'center' } }, inp(it, 'upc', 64, '1')),
                h('td', { style: { padding: '6px 4px', textAlign: 'center', fontWeight: 600, color: cartons != null ? C.accent : '#F87171' } }, cartons != null ? cartons : '—'),
                h('td', { style: { padding: '6px 4px', textAlign: 'center' } }, inp(it, 'length', 56)),
                h('td', { style: { padding: '6px 4px', textAlign: 'center' } }, inp(it, 'width', 56)),
                h('td', { style: { padding: '6px 4px', textAlign: 'center' } }, inp(it, 'height', 56)),
                h('td', { style: { padding: '6px 4px', textAlign: 'center' } }, inp(it, 'weight', 64)),
                h('td', { style: { padding: '6px 4px', textAlign: 'center' } },
                  h('input', { type: 'checkbox', checked: !!c.save_default, disabled: locked, onChange: function(e) { editCaseCfg(it.id, 'save_default', e.target.checked); }, style: { width: 14, height: 14, accentColor: C.accent, cursor: locked ? 'default' : 'pointer' } }))
              );
            }))
          ),
          h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, fontSize: 12 } },
            h('div', { style: { color: caseCfgValid ? C.success : '#F87171' } },
              h(LIcon, { name: caseCfgValid ? 'check-circle' : 'alert-triangle', size: 13, style: { marginRight: 4, verticalAlign: 'middle' } }),
              caseCfgValid ? 'Ready — Walmart will receive the carton counts and sizes.' : (caseProblemRows.length + ' SKU' + (caseProblemRows.length === 1 ? ' needs' : 's need') + ' attention before this plan can continue.')),
            h('div', { style: { color: C.textMuted } }, 'Total cartons: ', h('strong', { style: { color: C.text } }, caseCfgTotalCartons))
          )
        );
      })(),
      // Items card (matches FBA layout)
      h(Card, null,
        h('h3', { style: { fontSize: 14, fontWeight: 600, marginBottom: 12, color: C.text } }, 'Items to Ship'),
        h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12 } }, items.length + ' SKUs, ' + totalUnits + ' total units'),
        h('table', { style: { width: '100%', fontSize: 12 } },
          h('thead', null, h('tr', null,
            ['SKU', 'GTIN', 'Item ID', 'Product', 'PO #', 'Location', 'Units'].map(function(col) { return h('th', { key: col, style: { textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid ' + C.border, color: C.textMuted, fontWeight: 500, fontSize: 11 } }, col); })
          )),
          h('tbody', null, items.map(function(item) {
            return h('tr', { key: item.id },
              h('td', { style: { padding: '6px 8px', fontFamily: 'inherit', fontWeight: 600 } }, item.sku),
              h('td', { style: { padding: '6px 8px', fontFamily: 'inherit', fontSize: 11 } }, item.gtin || item.barcode || '\u2014'),
              h('td', { style: { padding: '6px 8px', fontFamily: 'inherit', fontSize: 11 } }, item.walmart_item_id
                ? h('a', { href: 'https://www.walmart.com/ip/' + item.walmart_item_id, target: '_blank', rel: 'noopener noreferrer', style: { color: C.accent, textDecoration: 'none' } }, item.walmart_item_id, h(LIcon, { name: 'external-link', size: 10, style: { marginLeft: 4, verticalAlign: 'middle' } }))
                : '\u2014'),
              h('td', { style: { padding: '6px 8px' } }, item.vendor_title || ''),
              h('td', { style: { padding: '6px 8px', fontFamily: 'inherit', fontSize: 11 } }, plan.po_numbers || plan.po_number || '\u2014'),
              h('td', { style: { padding: '6px 8px', fontSize: 11 } }, item.warehouse_location || '\u2014'),
              h('td', { style: { padding: '6px 8px', fontWeight: 600 } }, item.quantity)
            );
          }))
        ),
        h('div', { style: { marginTop: 12, fontSize: 12, color: C.textMuted } },
          'Marketplace: Walmart US'
        )
      ),
      // Address edit modal (matches FBA)
      h(Modal, { open: editAddress, onClose: function() { setEditAddress(false); }, title: 'Change Ship From Address', width: 480 },
        h('div', { style: { display: 'flex', flexDirection: 'column', gap: 12 } },
          h('select', { value: detailSelectedAddr, onChange: function(e) { setDetailSelectedAddr(e.target.value); },
            style: { width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid ' + C.border, background: C.bg, color: C.text, fontSize: 13, fontFamily: 'inherit' }
          },
            detailShipFromAddrs.map(function(a) {
              return h('option', { key: a.id, value: String(a.id) }, a.label + (a.city ? ' \u2014 ' + a.city + ', ' + a.state : ''));
            }),
            h('option', { value: 'new' }, '+ Create New Address')
          ),
          detailSelectedAddr !== 'new' && (function() {
            var sa = detailShipFromAddrs.find(function(a) { return String(a.id) === detailSelectedAddr; });
            if (!sa) return null;
            return h('div', { style: { fontSize: 12, color: C.textMuted, padding: '8px 10px', background: C.surface, borderRadius: 6, lineHeight: 1.5 } },
              sa.name && h('div', { style: { fontWeight: 500, color: C.text } }, sa.name),
              h('div', null, sa.address_line1),
              h('div', null, [sa.city, sa.state, sa.postal_code].filter(Boolean).join(', ') + (sa.country_code ? ' ' + sa.country_code : '')),
              sa.phone && h('div', null, 'Phone: ' + sa.phone)
            );
          })(),
          detailSelectedAddr === 'new' && h('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 } },
            h(Inp, { label: 'Label *', value: detailNewAddrForm.label, onChange: function(e) { setDetailNewAddrForm(Object.assign({}, detailNewAddrForm, { label: e.target.value })); }, placeholder: 'e.g. Main Warehouse' }),
            h(Inp, { label: 'Name', value: detailNewAddrForm.name, onChange: function(e) { setDetailNewAddrForm(Object.assign({}, detailNewAddrForm, { name: e.target.value })); }, placeholder: 'Company / person name' }),
            h(Inp, { label: 'Address *', value: detailNewAddrForm.address_line1, onChange: function(e) { setDetailNewAddrForm(Object.assign({}, detailNewAddrForm, { address_line1: e.target.value })); } }),
            h(Inp, { label: 'City', value: detailNewAddrForm.city, onChange: function(e) { setDetailNewAddrForm(Object.assign({}, detailNewAddrForm, { city: e.target.value })); } }),
            h(StateSelect, { label: 'State', country: detailNewAddrForm.country_code || 'US', value: detailNewAddrForm.state, onChange: function(e) { setDetailNewAddrForm(Object.assign({}, detailNewAddrForm, { state: e.target.value })); } }),
            h(Inp, { label: 'ZIP', value: detailNewAddrForm.postal_code, onChange: function(e) { setDetailNewAddrForm(Object.assign({}, detailNewAddrForm, { postal_code: e.target.value })); } }),
            h(Inp, { label: 'Country', value: detailNewAddrForm.country_code, onChange: function(e) { setDetailNewAddrForm(Object.assign({}, detailNewAddrForm, { country_code: e.target.value })); } }),
            h(Inp, { label: 'Phone', value: detailNewAddrForm.phone, onChange: function(e) { setDetailNewAddrForm(Object.assign({}, detailNewAddrForm, { phone: e.target.value })); } })
          ),
          h('div', { style: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 } },
            h(Btn, { onClick: function() { setEditAddress(false); } }, 'Cancel'),
            h(Btn, { variant: 'primary', onClick: handleSaveAddress }, 'Save')
          )
        )
      )
    );
  }

  function renderPrepStep() {
    var isDraft = plan.status === 'draft';
    var allLabel = items.every(function(it) { return (itemPrep[it.id] || {}).label; });
    var allBag = items.every(function(it) { return (itemPrep[it.id] || {}).bag; });

    var toggleAll = function(type) {
      var allOn = items.every(function(it) { return (itemPrep[it.id] || {})[type]; });
      var next = {};
      items.forEach(function(it) {
        var cur = itemPrep[it.id] || {};
        next[it.id] = Object.assign({}, cur);
        next[it.id][type] = !allOn;
      });
      setItemPrep(next);
    };

    var toggleItem = function(itemId, type) {
      var cur = itemPrep[itemId] || {};
      var next = Object.assign({}, itemPrep);
      next[itemId] = Object.assign({}, cur);
      next[itemId][type] = !cur[type];
      setItemPrep(next);
    };

    // Fee totals
    var labelUnits = 0, bagUnits = 0;
    items.forEach(function(it) {
      var p = itemPrep[it.id] || {};
      if (p.label) labelUnits += (it.quantity || 0);
      if (p.bag) bagUnits += (it.quantity || 0);
    });
    var totalFee = labelUnits * prepFees.label + bagUnits * prepFees.bag;

    var cardStyle = function(mode) {
      return {
        flex: 1, padding: 16, borderRadius: 8, cursor: isDraft ? 'pointer' : 'default',
        border: '2px solid ' + (prepMode === mode ? C.accent : C.border),
        background: prepMode === mode ? 'rgba(59,130,246,0.06)' : C.surface,
        transition: 'all 0.15s'
      };
    };

    return h('div', null,
      h(Card, null,
        h('h3', { style: { fontSize: 15, fontWeight: 600, marginBottom: 12 } }, 'Prep Options'),
        h('div', { style: { fontSize: 13, color: C.textMuted, marginBottom: 16 } }, 'Choose who prepares items (labeling, poly bagging) before they reach the Walmart fulfillment center.'),

        // Toggle cards
        h('div', { style: { display: 'flex', gap: 12, marginBottom: 20 } },
          h('div', { style: cardStyle('seller'), onClick: function() { if (isDraft) setPrepMode('seller'); } },
            h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 } },
              h('div', { style: { width: 16, height: 16, borderRadius: '50%', border: '2px solid ' + (prepMode === 'seller' ? C.accent : C.textDim), display: 'flex', alignItems: 'center', justifyContent: 'center' } },
                prepMode === 'seller' && h('div', { style: { width: 8, height: 8, borderRadius: '50%', background: C.accent } })
              ),
              h('span', { style: { fontWeight: 600, fontSize: 14, color: C.text } }, 'Seller Prep')
            ),
            h('div', { style: { fontSize: 12, color: C.textMuted } }, 'You label and bag items before sending to Walmart. No additional fees.')
          ),
          h('div', { style: cardStyle('walmart'), onClick: function() { if (isDraft) setPrepMode('walmart'); } },
            h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 } },
              h('div', { style: { width: 16, height: 16, borderRadius: '50%', border: '2px solid ' + (prepMode === 'walmart' ? C.accent : C.textDim), display: 'flex', alignItems: 'center', justifyContent: 'center' } },
                prepMode === 'walmart' && h('div', { style: { width: 8, height: 8, borderRadius: '50%', background: C.accent } })
              ),
              h('span', { style: { fontWeight: 600, fontSize: 14, color: C.text } }, 'Walmart Prep')
            ),
            h('div', { style: { fontSize: 12, color: C.textMuted } }, 'Walmart handles labeling and/or poly bagging at the FC. Per-unit fees apply.')
          )
        ),

        // Walmart Prep item table
        prepMode === 'walmart' && h('div', null,
          h('div', { style: { fontSize: 13, fontWeight: 600, marginBottom: 8, color: C.text } }, 'Select services per item'),
          h('table', { style: { width: '100%', borderCollapse: 'collapse', fontSize: 12 } },
            h('thead', null,
              h('tr', { style: { borderBottom: '1px solid ' + C.border } },
                h('th', { style: { textAlign: 'left', padding: '8px 6px', color: C.textMuted, fontWeight: 600 } }, 'SKU'),
                h('th', { style: { textAlign: 'left', padding: '8px 6px', color: C.textMuted, fontWeight: 600 } }, 'Product'),
                h('th', { style: { textAlign: 'right', padding: '8px 6px', color: C.textMuted, fontWeight: 600 } }, 'Qty'),
                h('th', { style: { textAlign: 'center', padding: '8px 6px', cursor: 'pointer', color: C.textMuted, fontWeight: 600 }, onClick: function() { toggleAll('label'); } },
                  h('div', { style: { display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center', cursor: 'pointer' } },
                    h('div', { style: { width: 18, height: 18, borderRadius: 4, border: '2px solid ' + (allLabel ? C.accent : C.textDim), background: allLabel ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s', flexShrink: 0 } },
                      allLabel && h(LIcon, { name: 'check', size: 12, color: '#fff' })
                    ),
                    'Label ($' + prepFees.label.toFixed(2) + ')'
                  )
                ),
                h('th', { style: { textAlign: 'center', padding: '8px 6px', cursor: 'pointer', color: C.textMuted, fontWeight: 600 }, onClick: function() { toggleAll('bag'); } },
                  h('div', { style: { display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center', cursor: 'pointer' } },
                    h('div', { style: { width: 18, height: 18, borderRadius: 4, border: '2px solid ' + (allBag ? C.accent : C.textDim), background: allBag ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s', flexShrink: 0 } },
                      allBag && h(LIcon, { name: 'check', size: 12, color: '#fff' })
                    ),
                    'Bag ($' + prepFees.bag.toFixed(2) + ')'
                  )
                ),
                h('th', { style: { textAlign: 'right', padding: '8px 6px', color: C.textMuted, fontWeight: 600 } }, 'Fee')
              )
            ),
            h('tbody', null,
              items.map(function(it) {
                var p = itemPrep[it.id] || {};
                var fee = ((p.label ? prepFees.label : 0) + (p.bag ? prepFees.bag : 0)) * (it.quantity || 0);
                return h('tr', { key: it.id, style: { borderBottom: '1px solid ' + C.border + '40' } },
                  h('td', { style: { padding: '6px', fontFamily: 'inherit', fontSize: 11 } }, it.sku),
                  h('td', { style: { padding: '6px', color: C.textMuted, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, it.vendor_title || it.title || ''),
                  h('td', { style: { padding: '6px', textAlign: 'right' } }, it.quantity),
                  h('td', { style: { padding: '6px', textAlign: 'center' } },
                    h('div', { onClick: function() { toggleItem(it.id, 'label'); }, style: { width: 18, height: 18, borderRadius: 4, border: '2px solid ' + (p.label ? C.accent : C.textDim), background: p.label ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.15s', margin: '0 auto' } },
                      p.label && h(LIcon, { name: 'check', size: 12, color: '#fff' })
                    )
                  ),
                  h('td', { style: { padding: '6px', textAlign: 'center' } },
                    h('div', { onClick: function() { toggleItem(it.id, 'bag'); }, style: { width: 18, height: 18, borderRadius: 4, border: '2px solid ' + (p.bag ? C.accent : C.textDim), background: p.bag ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.15s', margin: '0 auto' } },
                      p.bag && h(LIcon, { name: 'check', size: 12, color: '#fff' })
                    )
                  ),
                  h('td', { style: { padding: '6px', textAlign: 'right', fontWeight: fee > 0 ? 600 : 400, color: fee > 0 ? C.text : C.textDim } }, fee > 0 ? '$' + fee.toFixed(2) : '-')
                );
              })
            )
          ),

          // Fee summary
          h('div', { style: { marginTop: 12, padding: 12, background: C.surface, borderRadius: 8, display: 'flex', gap: 24, justifyContent: 'flex-end', alignItems: 'center' } },
            labelUnits > 0 && h('span', { style: { fontSize: 12, color: C.textMuted } }, 'Labeling: ' + labelUnits + ' units \u00d7 $' + prepFees.label.toFixed(2) + ' = $' + (labelUnits * prepFees.label).toFixed(2)),
            bagUnits > 0 && h('span', { style: { fontSize: 12, color: C.textMuted } }, 'Bagging: ' + bagUnits + ' units \u00d7 $' + prepFees.bag.toFixed(2) + ' = $' + (bagUnits * prepFees.bag).toFixed(2)),
            h('span', { style: { fontSize: 14, fontWeight: 700, color: totalFee > 0 ? C.warning : C.text } }, 'Total: $' + totalFee.toFixed(2))
          )
        ),

        // Seller prep info
        prepMode === 'seller' && h('div', { style: { padding: 16, background: C.surface, borderRadius: 8, fontSize: 13, color: C.textMuted } },
          'You will prepare all items yourself before shipping. Ensure items are properly labeled with GTIN barcodes and bagged if required. Unprepared items may incur unplanned prep fees ($0.65/label, $0.80/bag).'
        ),

        // Buttons
        h('div', { style: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 } },
          h(Btn, { variant: 'primary', onClick: savePrepAndContinue }, 'Continue ', h(LIcon, { name: 'arrow-right', size: 14 }))
        )
      )
    );
  }

  function renderPlacementCard() {
    var choose = function(its) {
      setPlacementChoice(its);
      api('/wfs-shipments/plans/' + planId + '/placement', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ its_value: its }) })
        .then(function() { showToast(its === 'Y' ? 'Will ship to one location (ITS)' : 'Will split across FCs (self-distribution)', 'success'); })
        .catch(function(e) { showToast(e.message, 'error'); });
    };
    var pv = {};
    (previews || []).forEach(function(o) { pv[o.its_value] = o; });
    var optCard = function(its, title, subtitle) {
      var o = pv[its] || {};
      var selected = placementChoice === its;
      return h('div', { onClick: function() { choose(its); }, style: { flex: 1, padding: 14, borderRadius: 8, cursor: 'pointer', border: '2px solid ' + (selected ? C.accent : C.border), background: selected ? 'rgba(59,130,246,0.06)' : C.surface } },
        h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 } },
          h('div', { style: { width: 16, height: 16, borderRadius: '50%', border: '2px solid ' + (selected ? C.accent : C.textDim), display: 'flex', alignItems: 'center', justifyContent: 'center' } }, selected && h('div', { style: { width: 8, height: 8, borderRadius: '50%', background: C.accent } })),
          h('span', { style: { fontWeight: 600, fontSize: 14, color: C.text } }, title)
        ),
        h('div', { style: { fontSize: 12, color: C.textMuted } }, subtitle),
        (previews && o.its_value) ? h('div', { style: { marginTop: 8, fontSize: 12 } },
          o.shipment_count != null && h('div', null, h('strong', null, o.shipment_count), ' projected shipment' + (o.shipment_count === 1 ? '' : 's')),
          o.total_transfer_charge != null && h('div', { style: { color: C.warning } }, 'Est. fee: $' + Number(o.total_transfer_charge).toFixed(2) + (o.currency_unit && o.currency_unit !== 'USD' ? ' ' + o.currency_unit : ''))
        ) : null
      );
    };
    return h(Card, { style: { marginBottom: 16 } },
      h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 } },
        h('h3', { style: { fontSize: 15, fontWeight: 600, margin: 0 } }, 'Placement'),
        h(Btn, { size: 'sm', disabled: previewLoading || !caseCfgValid, onClick: fetchPreview }, previewLoading ? 'Fetching…' : 'Fetch Placement Fees')
      ),
      h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12 } }, 'Choose how this order reaches Walmart. Estimates are projected — Walmart decides the final split + fees at creation.'),
      h('div', { style: { display: 'flex', gap: 12 } },
        optCard('Y', 'Ship to one location', 'Inventory Transfer Service — Walmart redistributes for a per-unit fee. Usually one shipment.'),
        optCard('N', 'Split across FCs', 'Self-distribution — you ship to each assigned FC. No placement fee.')
      )
    );
  }

  function renderSubmitStep() {
    var alreadySubmitted = (plan.status !== 'submit_failed') && (!!plan.walmart_shipment_id || (plan.shipments && plan.shipments.some(function(s) { return s.walmart_shipment_id; })));
    if (submitPolling) return h(Card, null,
      h('div', { style: { display: 'flex', alignItems: 'center', gap: 10, padding: 8 } },
        h(LIcon, { name: 'loader-2', size: 18, color: C.accent }),
        h('div', null,
          h('div', { style: { fontWeight: 600, color: C.text } }, 'Walmart is allocating fulfillment center(s)…'),
          h('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 2 } }, 'This can take a few minutes. You can leave and come back — it keeps processing.')
        )
      ),
      h('div', { style: { marginTop: 12, display: 'flex', justifyContent: 'flex-end' } }, h(Btn, { size: 'sm', onClick: loadPlan }, 'Check Status'))
    );
    return h('div', null,
      h(Card, null,
        h('h3', { style: { fontSize: 15, fontWeight: 600, marginBottom: 12 } }, 'Submit to Walmart'),
        alreadySubmitted ?
          h('div', null,
            h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, padding: 12, background: 'rgba(52,211,153,0.08)', borderRadius: 8, marginBottom: 12 } },
              h(LIcon, { name: 'check-circle', size: 18, color: '#34D399' }),
              h('div', null,
                h('div', { style: { fontWeight: 600, color: '#34D399' } }, 'Submitted to Walmart'),
                h('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 2 } }, 'Shipment ID: ' + plan.walmart_shipment_id),
                plan.destination_fc && h('div', { style: { fontSize: 12, color: C.textMuted } }, 'Destination FC: ' + planFcLabel)
              )
            )
          )
        :
          h('div', null,
            h('div', { style: { padding: 16, background: C.surface, borderRadius: 8, marginBottom: 16, fontSize: 13, color: C.textMuted, lineHeight: 1.6 } },
              h('div', null, 'This will submit ' + totalUnits + ' units (' + items.length + ' SKUs) to Walmart WFS.'),
              h('div', null, 'Walmart will assign a fulfillment center destination.'),
              totalMissing > 0 && h('div', { style: { color: C.warning, marginTop: 4 } }, totalMissing + ' units marked as missing will be excluded.'),
              prepMode === 'walmart' && h('div', { style: { marginTop: 8, padding: 8, background: 'rgba(59,130,246,0.08)', borderRadius: 6, color: C.text } },
                h('div', { style: { fontWeight: 600, fontSize: 12, marginBottom: 4 } }, 'Walmart Prep Selected'),
                (function() {
                  var labelCount = 0, bagCount = 0, labelUnits = 0, bagUnits = 0;
                  items.forEach(function(it) { var p = itemPrep[it.id] || {}; if (p.label) { labelCount++; labelUnits += it.quantity || 0; } if (p.bag) { bagCount++; bagUnits += it.quantity || 0; } });
                  return h('div', { style: { fontSize: 12, color: C.textMuted } },
                    labelCount > 0 && h('div', null, 'Labeling: ' + labelCount + ' SKUs (' + labelUnits + ' units) — $' + (labelUnits * prepFees.label).toFixed(2)),
                    bagCount > 0 && h('div', null, 'Poly Bagging: ' + bagCount + ' SKUs (' + bagUnits + ' units) — $' + (bagUnits * prepFees.bag).toFixed(2))
                  );
                })()
              )
            ),
            // Walmart-rejected items triage (mirrors FBA's rejected-items handling)
            effectiveRejected && effectiveRejected.length > 0 && h('div', { style: { marginBottom: 16, padding: 14, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.35)', borderRadius: 8 } },
              h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 } },
                h(LIcon, { name: 'alert-triangle', size: 16, color: '#F59E0B' }),
                h('span', { style: { fontWeight: 600, color: '#F59E0B', fontSize: 14 } }, 'Walmart rejected ' + effectiveRejected.length + ' item' + (effectiveRejected.length === 1 ? '' : 's'))
              ),
              h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 10, lineHeight: 1.5 } },
                'These SKUs aren’t WFS-active in Walmart’s catalog (not converted or not “SI active”). Convert them in Seller Center and re-fetch, or remove them from this shipment and continue without them. Vendor-direct POs: the lines are also removed from the PO (re-send the revised PO to the vendor) and carried to the vendor’s draft PO. Warehouse POs keep their lines for disposition.'),
              h('div', { style: { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 } },
                effectiveRejected.map(function(ri) {
                  return h('div', { key: ri.item_id, style: { fontSize: 12, display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' } },
                    h('span', { style: { fontFamily: 'inherit', fontWeight: 600, color: C.text } }, ri.sku),
                    h('span', { style: { color: C.textMuted } }, (ri.reasons || []).join(' · ')));
                })
              ),
              h('div', { style: { display: 'flex', gap: 8 } },
                h(Btn, { size: 'sm', variant: 'primary', disabled: removingRejected, onClick: removeRejectedAndRefetch },
                  removingRejected ? 'Removing…' : ('Remove ' + effectiveRejected.length + ' item' + (effectiveRejected.length === 1 ? '' : 's') + ' & re-fetch')),
                h(Btn, { size: 'sm', onClick: function() { setRejectedItems(null); setRejectedDismissed(true); } }, 'Dismiss')
              )
            ),
            // Placement choice (ITS vs split) — projected preview
            renderPlacementCard(),
            // Supplier packs — the configuration lives on Review Items (2026-10-30); this is the recap.
            isCasePackedPlan && h('div', { style: { marginBottom: 16, padding: '12px 14px', background: C.surface, borderRadius: 8, fontSize: 12, border: '1px solid ' + (caseCfgValid ? C.border : 'rgba(248,113,113,0.55)') } },
              h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 } },
                h('div', { style: { fontWeight: 600, color: C.text, fontSize: 13 } }, 'Supplier packs'),
                h('button', { onClick: function() { setManualStep(0); }, style: { background: 'none', border: 'none', cursor: 'pointer', color: C.accent, fontSize: 12, fontWeight: 500 } }, h(LIcon, { name: 'edit-2', size: 12 }), ' Edit on Review Items')
              ),
              h('div', { style: { color: C.textMuted } }, caseCfgTotalCartons + ' carton' + (caseCfgTotalCartons === 1 ? '' : 's') + ' across ' + items.length + ' SKU' + (items.length === 1 ? '' : 's') + ' — Walmart receives the carton counts, units per carton and carton size/weight.'),
              caseProblemRows.length > 0 && h('div', { style: { color: '#F87171', marginTop: 6 } },
                h(LIcon, { name: 'alert-triangle', size: 13, style: { marginRight: 4, verticalAlign: 'middle' } }),
                'Fix first: ' + caseProblemRows.map(function(r) { return r.item.sku + ' (' + r.probs.join('; ') + ')'; }).join(', '))
            )
          )
      )
    );
  }

  function renderPackStep() {
    var ships = plan.shipments || [];
    var multi = ships.length > 1;
    var safeIdx = Math.min(activeShipIdx, Math.max(0, ships.length - 1));
    var activeShip = ships.length ? ships[safeIdx] : null;
    var activeShipId = activeShip ? activeShip.id : shipmentId;
    var infoShip = multi ? activeShip : shipment;
    var fc = multi
      ? (activeShip && (activeShip.display_name || activeShip.destination_fc))
      : ((shipment && (shipment.display_name || shipment.destination_fc)) || plan.destination_fc);
    var da = multi ? (activeShip && activeShip.destination_address) : plan.destination_address;
    var packComplete = multi ? (ships.length > 0 && ships.every(shipmentPackedFromData)) : packingComplete;
    return h('div', null,
      // Multi-shipment: Shipment Overview panel (mirrors FBA's — FC, ID, location, progress)
      multi && (function() {
        var doneOf = function(s) {
          var su = (s.items || []).reduce(function(a, it) { return a + (it.quantity || 0); }, 0);
          var sp = (s.items || []).reduce(function(a, it) { return a + (it.quantity_packed || 0) + (it.quantity_missing || 0); }, 0);
          return !!shipPackDone[s.id] || (su > 0 && sp >= su);
        };
        var completedCount = ships.filter(doneOf).length;
        return h(Card, { style: { marginBottom: 12 } },
          h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 } },
            h('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
              h(LIcon, { name: 'git-branch', size: 16, color: C.accent }),
              h('span', { style: { fontSize: 14, fontWeight: 600, color: C.text } }, 'Shipment Overview')
            ),
            h('span', { style: { fontSize: 12, color: completedCount >= ships.length ? C.success : C.textMuted, fontWeight: 600 } },
              completedCount + '/' + ships.length + ' packed'
            )
          ),
          h('div', { style: { display: 'flex', gap: 8, flexWrap: 'wrap' } },
            ships.map(function(s, idx) {
              var isActive = idx === safeIdx;
              var sItems = s.items || [];
              var sUnits = sItems.reduce(function(sum, i) { return sum + (i.quantity || 0); }, 0);
              var sPacked = sItems.reduce(function(sum, i) { return sum + (i.quantity_packed || 0) + (i.quantity_missing || 0); }, 0);
              var pct = sUnits > 0 ? Math.round(sPacked / sUnits * 100) : 0;
              var isDone = doneOf(s);
              var sAddr = s.destination_address ? (typeof s.destination_address === 'string' ? JSON.parse(s.destination_address) : s.destination_address) : {};
              var sLoc = [sAddr.city, sAddr.stateCode || sAddr.state].filter(Boolean).join(', ');
              return h('div', {
                key: s.id,
                onClick: function() { setActiveShipIdx(idx); },
                style: {
                  flex: '1 1 140px', maxWidth: 180, minWidth: 130, padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                  // Done-ness is shown only by the checkmark + green progress bar — the card
                  // itself is not flooded green (active = blue highlight, otherwise neutral).
                  border: '2px solid ' + (isActive ? C.accent : C.border),
                  background: isActive ? 'rgba(79,110,247,0.08)' : 'transparent',
                  transition: 'all 0.15s'
                }
              },
                h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 } },
                  h('span', { style: { fontSize: 15, fontWeight: 700, fontFamily: 'inherit', color: isActive ? C.accent : C.text } }, s.display_name || s.destination_fc || 'FC?'),
                  h('div', { style: { display: 'flex', alignItems: 'center', gap: 4 } },
                    isDone && h(LIcon, { name: 'check-circle', size: 16, color: C.success }),
                    h('button', {
                      onClick: function(e) { e.stopPropagation(); exportWfsShipmentXlsx(s); },
                      title: 'Export picklist (Excel)',
                      onMouseEnter: function(e) { e.currentTarget.style.color = C.accent; },
                      onMouseLeave: function(e) { e.currentTarget.style.color = C.textMuted; },
                      style: { background: 'transparent', border: 'none', padding: 2, cursor: 'pointer', color: C.textMuted, display: 'inline-flex', alignItems: 'center', lineHeight: 0 }
                    }, h(LIcon, { name: 'download', size: 14 }))
                  )
                ),
                h('div', { style: { fontSize: 10, fontFamily: 'inherit', color: C.textDim, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } },
                  s.walmart_shipment_id || 'Pending'
                ),
                sLoc && h('div', { style: { fontSize: 10, color: C.textMuted, marginBottom: 6 } }, sLoc),
                h('div', { style: { fontSize: 11, color: C.textMuted, marginBottom: 6 } },
                  sItems.length + ' SKU' + (sItems.length !== 1 ? 's' : '') + ' · ' + sUnits + ' units'
                ),
                h('div', { style: { height: 5, borderRadius: 3, background: C.border, overflow: 'hidden' } },
                  h('div', { style: { height: '100%', width: pct + '%', borderRadius: 3, background: isDone ? C.success : pct > 0 ? '#f59e0b' : 'transparent', transition: 'width 0.3s' } })
                ),
                h('div', { style: { fontSize: 10, color: isDone ? C.success : C.textMuted, marginTop: 3, textAlign: 'right', fontWeight: 500 } }, pct + '%')
              );
            })
          )
        );
      })(),
      // Shipment info card (WFS Shipment ID + destination FC)
      (multi ? activeShip : (plan.walmart_shipment_id || shipment)) && h(Card, { style: { marginBottom: 12 } },
        h('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' } },
          h('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
            h(LIcon, { name: 'truck', size: 18, color: C.accent }),
            h('div', null,
              h('div', { style: { fontSize: 13, fontWeight: 600, color: C.text, display: 'flex', alignItems: 'center', gap: 8 } }, multi ? ('Shipment ' + (safeIdx + 1) + ' of ' + ships.length) : 'WFS Shipment ID', infoShip && h(WfsBadge, { status: infoShip.status })),
              h('div', { style: { fontFamily: 'inherit', fontSize: 12, color: C.accent } }, (infoShip && infoShip.walmart_shipment_id) || plan.walmart_shipment_id || '—')
            )
          ),
          fc && h('div', null,
            h('div', { style: { fontSize: 13, fontWeight: 600, color: C.text } }, 'Ship To'),
            h('div', { style: { fontSize: 13, fontWeight: 700, fontFamily: 'inherit', color: C.text } }, fc),
            da && da.addressLine1 && h('div', { style: { fontSize: 12, color: C.textMuted } }, da.addressLine1),
            da && (da.city || da.stateCode) && h('div', { style: { fontSize: 12, color: C.textMuted } }, [da.city, da.stateCode || da.state, da.postalCode].filter(Boolean).join(', '))
          )
        )
      ),
      h(WfsBoxPackingView, { key: activeShipId || 'plan', planId: planId, shipmentId: activeShipId, showToast: showToast, plan: plan, currentUser: currentUser, onRefresh: loadPlan,
        onPackingStatusChange: multi ? function(complete) { setShipPackDone(function(prev) { var n = Object.assign({}, prev); if (activeShip) n[activeShip.id] = complete; return n; }); } : setPackingComplete }),
      null
    );
  }

  // Case Labels = Walmart's official receiving label (Anthony 2026-10-22: "walmart's official label").
  // POST get-labels asks Walmart for the shipment's label PDF; labels-pdf streams it — one page per
  // box and pre-filled once the shipment is booked through the new Preferred Carrier APIs, stamped
  // by the server only on the old blank layout. The app's own HTML sheet lives on as "Box Contents".
  function openWalmartCaseLabels() {
    if (!plan.walmart_shipment_id) { showToast('Submit to Walmart first', 'error'); return; }
    api('/wfs-shipments/plans/' + planId + '/get-labels', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
      .then(function() { window.open('/api/wfs-shipments/plans/' + planId + '/labels-pdf', '_blank'); })
      .catch(function(e) { showToast(e.message, 'error'); });
  }

  // The app's per-box contents sheet (FROM / SHIP TO / shipment barcode / GTIN + qty table). Not a
  // Walmart label — Walmart's is what the FC scans; this one is for the packer.
  function printWfsBoxContents() {
    try {
    var boxes = plan.boxes || [];
    if (!boxes.length) { showToast('No boxes to print labels for', 'error'); return; }

    var shipFrom = plan.ship_from_address || {};
    var shipTo = plan.destination_address || {};
    var shipmentId = plan.walmart_shipment_id || '';
    var totalBoxes = boxes.length;

    // Generate barcode first
    var barcodeImg = '';
    try {
      var canvas = document.createElement('canvas');
      bwipjs.toCanvas(canvas, { bcid: 'code128', text: shipmentId, scale: 3, height: 15, includetext: false });
      barcodeImg = '<img src="' + canvas.toDataURL('image/png') + '" style="max-width:100%;height:auto;">';
    } catch (e) { barcodeImg = '<div style="font-family:monospace;font-size:18px;letter-spacing:3px;padding:8px;border:1px dashed #999;">' + escHtml(shipmentId) + '</div>'; }

    // Build all labels
    var labelsHtml = '';
    boxes.forEach(function(box, idx) {
      var boxItems = box.items || [];
      var boxUnits = boxItems.reduce(function(s, i) { return s + (i.quantity || 0); }, 0);
      var boxNum = box.box_number || (idx + 1);

      var itemRows = boxItems.map(function(bi) {
        var gtin = escHtml((bi.gtin || bi.barcode || '').padStart(14, '0')); // text is escaped: titles and addresses come from the catalog / Walmart (review 2026-09-28, medium #40)
        return '<tr><td style="padding:3px 6px;font-family:monospace;font-size:11px;border-bottom:1px solid #ddd;">' + gtin + '</td>' +
          '<td style="padding:3px 6px;font-size:10px;border-bottom:1px solid #ddd;">' + escHtml(bi.vendor_title || bi.product_sku || bi.sku || '') + '</td>' +
          '<td style="padding:3px 6px;font-weight:bold;text-align:right;border-bottom:1px solid #ddd;">' + (bi.quantity || 0) + '</td></tr>';
      }).join('');

      labelsHtml += '<div style="width:4in;height:6in;box-sizing:border-box;border:1px solid #000;padding:12px;page-break-after:always;font-family:Arial,sans-serif;display:flex;flex-direction:column;">' +
        '<div style="font-size:9px;color:#666;margin-bottom:6px;">' +
          '<div style="font-weight:bold;font-size:10px;color:#000;">FROM:</div>' +
          escHtml(shipFrom.name || '') + '<br>' +
          escHtml(shipFrom.addressLine1 || shipFrom.address_line1 || '') + '<br>' +
          escHtml([shipFrom.city, shipFrom.stateOrProvinceCode || shipFrom.state, shipFrom.postalCode || shipFrom.postal_code].filter(Boolean).join(', ')) +
        '</div>' +
        '<div style="font-size:10px;margin-bottom:8px;padding:6px;border:1px solid #000;background:#f5f5f5;">' +
          '<div style="font-weight:bold;">SHIP TO: FC ' + escHtml(plan.destination_fc || '') + '</div>' +
          escHtml(shipTo.addressLine1 || '') + '<br>' +
          escHtml([shipTo.city, shipTo.stateCode || shipTo.state, shipTo.postalCode].filter(Boolean).join(', ') + ' ' + (shipTo.countryCode || 'US')) +
        '</div>' +
        '<div style="text-align:center;margin-bottom:6px;">' +
          '<div style="font-size:9px;color:#666;margin-bottom:2px;">SHIPMENT ID</div>' +
          barcodeImg +
          '<div style="font-family:monospace;font-size:14px;font-weight:bold;letter-spacing:2px;margin-top:2px;">' + escHtml(shipmentId) + '</div>' +
        '</div>' +
        '<div style="text-align:center;font-size:12px;font-weight:bold;margin-bottom:6px;padding:4px;background:#eee;border:1px solid #ccc;">' +
          'BOX ' + boxNum + ' of ' + totalBoxes + ' - ' + boxUnits + ' unit(s)' +
        '</div>' +
        '<div style="flex:1;overflow:hidden;">' +
          '<table style="width:100%;border-collapse:collapse;font-size:10px;">' +
            '<tr style="background:#eee;"><th style="padding:3px 6px;text-align:left;">GTIN</th><th style="padding:3px 6px;text-align:left;">Item</th><th style="padding:3px 6px;text-align:right;">Qty</th></tr>' +
            itemRows +
          '</table>' +
        '</div>' +
      '</div>';
    });

    // Use blob URL approach — most reliable across browsers
    var fullHtml = '<!DOCTYPE html><html><head><title>WFS Box Contents - ' + escHtml(shipmentId) + '</title>' +
      '<style>@media print{@page{size:4in 6in;margin:0;}body{margin:0;}div{page-break-inside:avoid;}}</style>' +
      '</head><body style="margin:0;padding:0;">' + labelsHtml + '</body></html>';

    var blob = new Blob([fullHtml], { type: 'text/html' });
    var url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    } catch (err) {
      showToast('Label error: ' + err.message, 'error');
      console.error('printWfsBoxContents error:', err);
    }
  }

  function renderLabelsStep() {
    var lblShips = plan.shipments || [];
    if (lblShips.length > 1) {
      var totalPlacementFees = lblShips.reduce(function(sum, s) { return sum + (parseFloat(s.total_net_transfer_charge) || 0); }, 0);
      return h('div', null,
        h(Card, { style: { marginBottom: 12 } },
          h('h3', { style: { fontSize: 15, fontWeight: 600, marginBottom: 8 } }, 'Receiving Labels — ' + lblShips.length + ' shipments'),
          h('div', { style: { fontSize: 12, color: C.textMuted } }, 'Print receiving labels per shipment — each ships to a different fulfillment center.')
        ),
        // Cost summary — placement (ITS) fees aggregated across shipments
        totalPlacementFees > 0 && h(Card, { style: { display: 'flex', gap: 24, alignItems: 'center', padding: '12px 16px', marginBottom: 12 } },
          h('div', null,
            h('div', { style: { fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 2 } }, 'Placement (ITS) Fee'),
            h('div', { style: { fontSize: 20, fontWeight: 700, color: '#f59e0b' } }, '$' + totalPlacementFees.toFixed(2))
          ),
          h('div', null,
            h('div', { style: { fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 2 } }, 'Total'),
            h('div', { style: { fontSize: 20, fontWeight: 700, color: C.text } }, '$' + totalPlacementFees.toFixed(2))
          )
        ),
        // Aggregate print — merges every shipment's receiving labels into a single PDF, grouped per shipment.
        h(Card, { style: { padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 12 } },
          h('div', null,
            h('div', { style: { fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 2 } }, 'Print All Receiving Labels'),
            h('div', { style: { fontSize: 11, color: C.textMuted } }, 'Aggregates every shipment\'s receiving labels into a single PDF, grouped per shipment.')
          ),
          h(Btn, { variant: 'primary', disabled: actionLoading === 'all-wfs-labels',
            onClick: function() {
              setActionLoading('all-wfs-labels');
              showToast('Aggregating receiving labels for all shipments…', 'info');
              api('/wfs-shipments/plans/' + planId + '/all-labels', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
                .then(function(res) {
                  if (res.base64) {
                    var blob = new Blob([Uint8Array.from(atob(res.base64), function(c) { return c.charCodeAt(0); })], { type: 'application/pdf' });
                    window.open(URL.createObjectURL(blob), '_blank');
                  }
                  loadPlan();
                  showToast((res.shipments || 0) + ' shipment(s) — ' + (res.pages || 0) + ' label page(s)', 'success');
                })
                .catch(function(e) { showToast('Failed: ' + e.message, 'error'); })
                .finally(function() { setActionLoading(''); });
            } },
            actionLoading === 'all-wfs-labels' ? 'Aggregating…' : [h(LIcon, { key: 'ic', name: 'printer', size: 14 }), ' Print All Receiving Labels'])
        ),
        lblShips.map(function(s) {
          return h(Card, { key: s.id, style: { marginBottom: 8 } },
            h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' } },
              h('div', null,
                h('div', { style: { fontSize: 13, fontWeight: 600, color: C.text, display: 'flex', gap: 8, alignItems: 'center' } }, (s.display_name || s.destination_fc || 'Shipment'), h(WfsBadge, { status: s.status })),
                h('div', { style: { fontFamily: 'inherit', fontSize: 11, color: C.textMuted } }, (s.walmart_shipment_id || '—') + ' · ' + ((s.boxes && s.boxes.length) || 0) + ' boxes')
              ),
              h('div', { style: { display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' } },
                h(Btn, { size: 'sm', onClick: function() { exportWfsShipmentXlsx(s); } }, h(LIcon, { name: 'file-text', size: 13 }), ' Picklist'),
                h(WorkOrdersMenu, { shipment: s, plan: plan, showToast: showToast }),
                h(Btn, { size: 'sm', onClick: function() { api('/wfs-shipments/shipments/' + s.id + '/get-labels', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }).then(function() { window.open('/api/wfs-shipments/shipments/' + s.id + '/labels-pdf', '_blank'); }).catch(function(e) { showToast(e.message, 'error'); }); } }, h(LIcon, { name: 'printer', size: 13 }), ' Labels')
              )
            )
          );
        })
      );
    }
    var boxCount = (plan.boxes || []).length;
    return h('div', null,
      h(Card, null,
        h('h3', { style: { fontSize: 15, fontWeight: 600, marginBottom: 12 } }, 'Receiving Labels'),
        h('div', { style: { padding: 16, background: C.surface, borderRadius: 8, marginBottom: 16, fontSize: 13, color: C.textMuted, lineHeight: 1.6 } },
          h('div', null, 'Prints the official Walmart receiving label \u2014 one page per box with BOX # and SKU mix pre-filled.'),
          h('div', { style: { marginTop: 8 } },
            h('span', { style: { fontWeight: 600, color: C.text } }, boxCount + ' box(es)'),
            ' ready for labels',
            plan.walmart_shipment_id && h('span', { style: { marginLeft: 8 } }, '\u2014 Shipment ID: ', h('strong', { style: { fontFamily: 'inherit' } }, plan.walmart_shipment_id)),
            plan.destination_fc && h('span', { style: { marginLeft: 8 } }, '\u2014 FC: ', h('strong', null, planFcLabel))
          ),
          plan.destination_address && plan.destination_address.addressLine1 && h('div', { style: { marginTop: 4, fontSize: 12 } },
            'Ship to: ' + plan.destination_address.addressLine1 + ', ' +
            [plan.destination_address.city, plan.destination_address.stateCode || plan.destination_address.state, plan.destination_address.postalCode].filter(Boolean).join(', ')
          )
        ),
        h('div', { style: { display: 'flex', gap: 8, justifyContent: 'flex-end' } },
          h('button', { onClick: function() {
              if (!shipmentId) { showToast('No shipment on this plan yet', 'error'); return; }
              api('/wfs-shipments/shipments/' + shipmentId + '/get-labels', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
                .then(function() { window.open('/api/wfs-shipments/shipments/' + shipmentId + '/labels-pdf', '_blank'); })
                .catch(function(e) { showToast(e.message, 'error'); });
            }, style: { padding: '8px 16px', background: C.accent, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' } }, 'Print ' + boxCount + ' Label(s)')
        )
      )
    );
  }

  // (state moved before return)

  function printPalletLabels() {
    var shipmentId = plan.walmart_shipment_id || '';
    if (!shipmentId) { showToast('No shipment ID', 'error'); return; }
    var shipFrom = plan.ship_from_address || {};
    var shipTo = plan.destination_address || {};
    var fc = planFcLabel || '';
    var labelsPerPallet = 4;
    var count = parseInt(palletCount) || 1;

    // Generate barcode
    var barcodeImg = '';
    try {
      var canvas = document.createElement('canvas');
      bwipjs.toCanvas(canvas, { bcid: 'code128', text: shipmentId, scale: 3, height: 18, includetext: false });
      barcodeImg = '<img src="' + canvas.toDataURL('image/png') + '" style="max-width:100%;height:auto;">';
    } catch (e) { barcodeImg = '<div style="font-family:monospace;font-size:16px;letter-spacing:2px;padding:6px;border:1px dashed #999;">' + escHtml(shipmentId) + '</div>'; }

    var now = new Date();
    var created = now.getFullYear() + '/' + String(now.getMonth()+1).padStart(2,'0') + '/' + String(now.getDate()).padStart(2,'0');

    var html = '';
    for (var p = 0; p < count; p++) {
      for (var side = 0; side < labelsPerPallet; side++) {
        html += '<div style="width:4in;height:6in;padding:3mm 4mm;box-sizing:border-box;display:flex;flex-direction:column;overflow:hidden;font-family:Arial,sans-serif;page-break-after:always;">' +
          '<div style="text-align:center;font-size:7pt;font-weight:700;letter-spacing:0.5px;margin-bottom:2px;">PLEASE LEAVE THIS LABEL UNCOVERED</div>' +
          '<div style="text-align:center;font-size:18pt;font-weight:900;margin-bottom:2px;">WFS</div>' +
          '<hr style="border:none;border-top:2.5px solid #000;margin:2px 0;">' +
          '<div style="display:flex;gap:8px;font-size:8pt;line-height:1.35;margin-bottom:2px;">' +
            '<div style="flex:1;"><b style="font-size:7pt;text-transform:uppercase;">SHIP FROM:</b>' +
              '<div>' + escHtml(shipFrom.name || '') + '</div>' +
              '<div>' + escHtml(shipFrom.addressLine1 || shipFrom.address_line1 || '') + '</div>' +
              '<div>' + escHtml([shipFrom.city, shipFrom.stateOrProvinceCode || shipFrom.state].filter(Boolean).join(', ') + ' ' + (shipFrom.postalCode || shipFrom.postal_code || '')) + '</div>' +
            '</div>' +
            '<div style="flex:1;"><b style="font-size:7pt;text-transform:uppercase;">SHIP TO:</b>' +
              '<div>WFS FC ' + escHtml(fc) + '</div>' +
              '<div>' + escHtml(shipTo.addressLine1 || '') + '</div>' +
              '<div>' + escHtml([shipTo.city, shipTo.stateCode || shipTo.state].filter(Boolean).join(', ') + ' ' + (shipTo.postalCode || '')) + '</div>' +
            '</div>' +
          '</div>' +
          '<div style="background:#000;color:#fff;font-size:7pt;padding:2px 4px;display:flex;justify-content:space-between;margin:2px 0;font-weight:600;-webkit-print-color-adjust:exact;print-color-adjust:exact;">' +
            '<span>PALLET ' + (p + 1) + ' of ' + count + '</span><span>Created: ' + created + '</span>' +
          '</div>' +
          '<div style="text-align:center;margin:6px 0;">' + barcodeImg + '</div>' +
          '<div style="font-family:monospace;font-size:12pt;text-align:center;letter-spacing:2px;font-weight:bold;">' + escHtml(shipmentId) + '</div>' +
          '<hr style="border:none;border-top:2.5px solid #000;margin:4px 0;">' +
        '</div>';
      }
    }

    var fullHtml = '<!DOCTYPE html><html><head><title>WFS Pallet Labels - ' + shipmentId + '</title>' +
      '<style>@media print{@page{size:4in 6in;margin:0;}body{margin:0;}}</style>' +
      '</head><body style="margin:0;padding:0;">' + html + '</body></html>';

    var blob = new Blob([fullHtml], { type: 'text/html' });
    window.open(URL.createObjectURL(blob), '_blank');
  }

  function renderShipStep() {
    var shpShips = plan.shipments || [];
    if (shpShips.length > 1) {
      return h('div', null,
        h(Card, { style: { marginBottom: 12 } },
          h('h3', { style: { fontSize: 15, fontWeight: 600, margin: 0 } }, 'Ship — ' + shpShips.length + ' shipments'),
          h('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 4 } }, 'Book a carrier (or enter tracking) for each shipment independently.')
        ),
        shpShips.map(function(s) { return h(WfsShipmentCard, { key: s.id, shipment: s, plan: plan, showToast: showToast, onRefresh: loadPlan }); })
      );
    }
    var isLTL = plan.shipment_type === 'LTL';
    // ─── Cost summary (mirrors FBA's Ship-step card) ───
    var costShipping = parseFloat(plan.carrier_quote_net_charge) || 0;
    if (!(costShipping > 0)) costShipping = shpShips.reduce(function(sum, s) { return sum + (parseFloat(s.total_net_transfer_charge) || 0); }, 0);
    var costIts = (plan.placement_choice && plan.inventory_transfer_service === 'Y') ? 'Y' : 'N';
    var costPreview = (shipCostPreviews || []).filter(function(o) { return o.its_value === costIts; })[0] || null;
    var costPlacement = (costPreview && costPreview.total_transfer_charge != null) ? (parseFloat(costPreview.total_transfer_charge) || 0) : 0;
    var costPrep = wfsPrepFeeTotals(plan.items || [], itemPrep, prepFees);
    var costTotal = Math.round((costShipping + costPlacement + costPrep.total) * 100) / 100;
    var costBooked = !!(plan.carrier || plan.tracking_number);
    return h('div', null,
      (costShipping > 0 || costPlacement > 0 || costPrep.total > 0) && h(Card, { style: { display: 'flex', gap: 24, alignItems: 'center', padding: '12px 16px', marginBottom: 12, flexWrap: 'wrap' } },
        costShipping > 0 && wfsCostBlock(costBooked ? 'Shipping (' + (plan.carrier_quote_carrier || plan.carrier || 'carrier') + ')' : 'Shipping (quoted)', costShipping, C.success),
        costPlacement > 0 && wfsCostBlock(costIts === 'Y' ? 'Placement fee (ITS)' : 'Placement fee', costPlacement, '#f59e0b'),
        costPrep.total > 0 && wfsCostBlock('Prep fees', costPrep.total, C.text,
          [costPrep.labelUnits > 0 ? costPrep.labelUnits + ' labels \u00d7 $' + prepFees.label.toFixed(2) : null, costPrep.bagUnits > 0 ? costPrep.bagUnits + ' bags \u00d7 $' + prepFees.bag.toFixed(2) : null].filter(Boolean).join(' + ')),
        wfsCostBlock('Total', costTotal, C.text, costBooked ? null : 'Estimate — the shipping line is the quoted rate until the carrier is booked')
      ),
      // Pallet labels section (LTL only)
      isLTL && h(Card, { style: { marginBottom: 12 } },
        h('h3', { style: { fontSize: 15, fontWeight: 600, marginBottom: 12 } }, 'Pallet Labels'),
        h('div', { style: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 } },
          h('div', null,
            h('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, display: 'block', marginBottom: 4 } }, 'Number of Pallets'),
            h('input', { type: 'number', min: 1, value: palletCount, onChange: function(e) { setPalletCount(parseInt(e.target.value) || 1); },
              style: { width: 80, padding: '8px 10px', fontSize: 14, fontWeight: 700, borderRadius: 6, border: '1px solid ' + C.border, background: C.bg, color: C.text, textAlign: 'center', fontFamily: 'inherit' } })
          ),
          h('div', { style: { fontSize: 12, color: C.textMuted } },
            (palletCount * 4) + ' labels total (4 per pallet)',
            h('br'),
            'Shipment ID: ', h('strong', { style: { fontFamily: 'inherit' } }, plan.walmart_shipment_id || '--')
          )
        ),
        h(Btn, { variant: 'primary', onClick: printPalletLabels, disabled: !plan.walmart_shipment_id },
          h(LIcon, { name: 'printer', size: 14 }), ' Print Pallet Labels')
      ),

      // ─── Reprint after shipping (Anthony 2026-10-22) ───
      // Shipped only locks the ship action; a lost or damaged label still needs a reprint. Same
      // buttons as the booked carrier card below, without quote / book / cancel.
      plan.walmart_shipment_id && (isShipped || isClosed) && h(Card, { style: { marginBottom: 12 } },
        h('h3', { style: { fontSize: 15, fontWeight: 600, marginBottom: 4 } }, 'Reprint Labels'),
        h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12 } }, 'The shipment is ' + (isClosed ? 'closed' : 'marked as shipped') + '. Labels can still be printed again.'),
        h('div', { style: { display: 'flex', gap: 8, flexWrap: 'wrap' } },
          (carrierBooked || !!plan.carrier || !!plan.tracking_number) && h(Btn, { variant: 'primary', onClick: function() {
            window.open('/api/wfs-shipments/plans/' + planId + '/carrier-label-pdf', '_blank');
          } }, h(LIcon, { name: 'download', size: 14 }), ' Shipping Label'),
          h(Btn, { onClick: openWalmartCaseLabels }, h(LIcon, { name: 'package', size: 14 }), ' Case Labels'), h(Btn, { size: 'sm', onClick: printWfsBoxContents }, h(LIcon, { name: 'file-text', size: 13 }), ' Box Contents'),
          isLTL && h(Btn, { onClick: function() {
            window.open('/api/wfs-shipments/plans/' + planId + '/bol-pdf', '_blank');
          } }, h(LIcon, { name: 'file-text', size: 14 }), ' BOL')
        )
      ),

      // ─── Carrier Section ───
      plan.walmart_shipment_id && !isShipped && !isClosed && h(Card, { style: { marginBottom: 12 } },
        h('h3', { style: { fontSize: 15, fontWeight: 600, marginBottom: 12 } }, 'Shipping Method'),
        h('div', { style: { display: 'flex', gap: 8, marginBottom: 16 } },
          h('button', { type: 'button', onClick: function() { setCarrierMode('preferred'); },
            style: { flex: 1, padding: '12px 14px', borderRadius: 8, border: '2px solid ' + (carrierMode === 'preferred' ? C.accent : C.border), background: carrierMode === 'preferred' ? C.accent + '10' : 'transparent', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' } },
            h('div', { style: { fontSize: 13, fontWeight: 600, color: carrierMode === 'preferred' ? C.accent : C.text } }, h(LIcon, { name: 'truck', size: 14, style: { marginRight: 6, verticalAlign: 'middle' } }), 'Walmart Preferred Carrier'),
            h('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 4 } }, 'Walmart-negotiated FedEx rates')
          ),
          h('button', { type: 'button', onClick: function() { setCarrierMode('own'); },
            style: { flex: 1, padding: '12px 14px', borderRadius: 8, border: '2px solid ' + (carrierMode === 'own' ? C.accent : C.border), background: carrierMode === 'own' ? C.accent + '10' : 'transparent', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' } },
            h('div', { style: { fontSize: 13, fontWeight: 600, color: carrierMode === 'own' ? C.accent : C.text } }, h(LIcon, { name: 'edit-2', size: 14, style: { marginRight: 6, verticalAlign: 'middle' } }), 'Own Carrier'),
            h('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 4 } }, 'Enter tracking details manually')
          )
        ),

        // ─── Preferred Carrier Flow ───
        carrierMode === 'preferred' && h('div', null,
          // Step 1: Get Quote
          !carrierQuotes && !carrierBooked && h('div', null,
            h('div', { style: { padding: 12, background: C.surface, borderRadius: 8, marginBottom: 12, fontSize: 13, color: C.textMuted } },
              'Get a shipping rate quote using Walmart-negotiated carrier rates. Box dimensions from packing will be used.'),
            h(Btn, { variant: 'primary', disabled: carrierLoading === 'quote', onClick: function() {
              setCarrierLoading('quote');
              api('/wfs-shipments/plans/' + planId + '/carrier-quote', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
                .then(function(data) { setCarrierQuotes(data); showToast('Rate quote received', 'success'); })
                .catch(function(e) {
                  // A quote already exists server-side. Load it, but only treat it as BOOKED if its
                  // status actually says so — a bare rate quote is not a booking.
                  if (e.message && e.message.indexOf('already exists') >= 0) {
                    return api('/wfs-shipments/plans/' + planId + '/carrier-quote')
                      .then(function(data) {
                        var qs = data.rateQuotes || data.quotes || data.payload || [];
                        if (!Array.isArray(qs)) qs = [qs];
                        var booked = qs.some(function(q) { return wfsQuoteBooked(q); }) || !!(plan.carrier || plan.tracking_number);
                        setCarrierQuotes(data);
                        setCarrierBooked(booked);
                        showToast(booked ? 'Carrier already booked' : 'Existing rate quote loaded', 'success');
                      });
                  }
                  showToast(e.message, 'error');
                })
                .finally(function() { setCarrierLoading(''); });
            } }, carrierLoading === 'quote' ? 'Getting Quote...' : 'Get Rate Quote')
          ),

          // Step 2: Show Quote & Book
          carrierQuotes && !carrierBooked && h('div', null,
            h('div', { style: { marginBottom: 12 } },
              h('div', { style: { fontSize: 12, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 8 } }, 'Rate Quote'),
              (function() {
                var quotes = carrierQuotes.rateQuotes || carrierQuotes.quotes || carrierQuotes.payload || (Array.isArray(carrierQuotes) ? carrierQuotes : [carrierQuotes]);
                if (!Array.isArray(quotes)) quotes = [quotes];
                return quotes.map(function(q, idx) {
                  var carrierObj = q.carrier || {};
                  var carrier = carrierObj.carrierName || q.carrierName || q.carrierCode || 'FedEx';
                  var cost = q.netCharge || q.totalCharge || q.totalCost || q.amount || null;
                  var transit = q.transitDetails && q.transitDetails.transitTime && q.transitDetails.transitTime[0]
                    ? q.transitDetails.transitTime[0].measurementValue + ' ' + (q.transitDetails.transitTime[0].unitOfMeasure || '').toLowerCase() + 's'
                    : (q.transitDays ? q.transitDays + ' days' : '');
                  var delivery = q.estimatedDeliveryDateTime ? new Date(q.estimatedDeliveryDateTime).toLocaleDateString() : '';
                  return h('div', { key: idx, style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: C.surface, borderRadius: 8, border: '1px solid ' + C.border, marginBottom: 6 } },
                    h('div', null,
                      h('div', { style: { fontSize: 14, fontWeight: 600 } }, carrier),
                      transit && h('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 2 } }, 'Transit: ' + transit),
                      delivery && h('div', { style: { fontSize: 11, color: C.textMuted } }, 'Est. delivery: ' + delivery)
                    ),
                    h('div', { style: { fontSize: 20, fontWeight: 700, color: C.accent } },
                      cost != null ? '$' + Number(cost).toFixed(2) : '--')
                  );
                });
              })()
            ),
            h('div', { style: { display: 'flex', gap: 8 } },
              h(Btn, { onClick: function() { setCarrierQuotes(null); } }, 'Cancel'),
              h(Btn, { variant: 'primary', disabled: carrierLoading === 'book', onClick: function() {
                setCarrierLoading('book');
                api('/wfs-shipments/plans/' + planId + '/carrier-book', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
                  .then(function(data) {
                    setCarrierBooked(true);
                    showToast('Carrier booked!', 'success');
                    if (data.carrierName) setTrackCarrier(data.carrierName);
                    if (data.trackingNumber || data.trackingNo) setTrackNumber(data.trackingNumber || data.trackingNo);
                    loadPlan();
                  })
                  .catch(function(e) { showToast(e.message, 'error'); })
                  .finally(function() { setCarrierLoading(''); });
              } }, carrierLoading === 'book' ? 'Booking...' : [h(LIcon, { key: 'ic', name: 'check', size: 14 }), ' Book Carrier'])
            )
          ),

          // Step 3: Booked — Generate & Download Labels
          carrierBooked && h('div', null,
            h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, padding: 12, background: 'rgba(52,211,153,0.08)', borderRadius: 8, marginBottom: 12 } },
              h(LIcon, { name: 'check-circle', size: 18, color: '#34D399' }),
              h('div', null,
                h('div', { style: { fontWeight: 600, color: '#34D399' } }, 'Carrier Booked'),
                trackCarrier && h('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 2 } }, trackCarrier + (trackNumber ? ' — ' + trackNumber : ''))
              )
            ),
            h('div', { style: { display: 'flex', gap: 8, flexWrap: 'wrap' } },
              h(Btn, { variant: 'primary', onClick: function() {
                window.open('/api/wfs-shipments/plans/' + planId + '/carrier-label-pdf', '_blank');
              } }, h(LIcon, { name: 'download', size: 14 }), ' Shipping Label'),
              h(Btn, { onClick: openWalmartCaseLabels }, h(LIcon, { name: 'package', size: 14 }), ' Case Labels'), h(Btn, { size: 'sm', onClick: printWfsBoxContents }, h(LIcon, { name: 'file-text', size: 13 }), ' Box Contents'),
              isLTL && h(Btn, { onClick: function() {
                window.open('/api/wfs-shipments/plans/' + planId + '/bol-pdf', '_blank');
              } }, h(LIcon, { name: 'file-text', size: 14 }), ' BOL'),
              h(Btn, { style: { background: 'rgba(248,113,113,0.08)', color: '#F87171', border: '1px solid rgba(248,113,113,0.3)' }, disabled: carrierLoading === 'cancel', onClick: async function() {
                if (!(await appConfirm('Cancel this carrier booking? Must be within 24hrs (parcel) or 1hr (LTL).', { danger: true, okLabel: 'Cancel booking' }))) return;
                setCarrierLoading('cancel');
                api('/wfs-shipments/plans/' + planId + '/carrier-book', { method: 'DELETE' })
                  .then(function() { setCarrierBooked(false); setCarrierQuotes(null); setCarrierLabelReady(false); showToast('Booking cancelled', 'success'); loadPlan(); })
                  .catch(function(e) { showToast(e.message, 'error'); })
                  .finally(function() { setCarrierLoading(''); });
              } }, carrierLoading === 'cancel' ? 'Cancelling...' : 'Cancel Booking')
            )
          )
        ),

        // ─── Own Carrier: Manual Entry ───
        carrierMode === 'own' && h('div', null,
          h('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 } },
            h(Inp, { label: 'Carrier', value: trackCarrier, onChange: function(e) { setTrackCarrier(e.target.value); }, placeholder: 'e.g. UPS, FedEx' }),
            h(Inp, { label: 'Tracking Number', value: trackNumber, onChange: function(e) { setTrackNumber(e.target.value); } }),
            isLTL && h(Inp, { label: 'PRO Number', value: trackPro, onChange: function(e) { setTrackPro(e.target.value); } }),
            isLTL && h(Inp, { label: 'BOL Number', value: trackBol, onChange: function(e) { setTrackBol(e.target.value); } })
          )
        )
      ),

      // ─── Destination & Ship Action ───
      h(Card, null,
        plan.destination_fc && h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12 } }, 'Destination FC: ' + planFcLabel),
        plan.destination_address && plan.destination_address.addressLine1 && h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12, padding: 8, background: C.surface, borderRadius: 6 } },
          h('div', { style: { fontWeight: 500, color: C.text } }, 'Ship To:'),
          h('div', null, plan.destination_address.addressLine1),
          h('div', null, [plan.destination_address.city, plan.destination_address.stateCode || plan.destination_address.state, plan.destination_address.postalCode].filter(Boolean).join(', '))
        ),
        // Show carrier/tracking summary if filled (from either mode)
        (trackCarrier || trackNumber) && h('div', { style: { padding: 8, background: C.surface, borderRadius: 6, marginBottom: 12, fontSize: 12 } },
          trackCarrier && h('div', null, h('span', { style: { color: C.textMuted } }, 'Carrier: '), h('strong', null, trackCarrier)),
          trackNumber && h('div', null, h('span', { style: { color: C.textMuted } }, 'Tracking: '), h('strong', { style: { fontFamily: 'inherit' } }, trackNumber)),
          trackPro && h('div', null, h('span', { style: { color: C.textMuted } }, 'PRO: '), h('strong', { style: { fontFamily: 'inherit' } }, trackPro)),
          trackBol && h('div', null, h('span', { style: { color: C.textMuted } }, 'BOL: '), h('strong', { style: { fontFamily: 'inherit' } }, trackBol))
        ),
        isShipped && h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, padding: 12, background: 'rgba(99,102,241,0.08)', borderRadius: 8, marginBottom: 12 } },
          h(LIcon, { name: 'check-circle', size: 18, color: '#6366F1' }),
          h('span', { style: { fontWeight: 600, color: '#6366F1' } }, 'Shipment marked as shipped')
        )
      )
    );
  }
}

// ─── Per-shipment Labels & Ship card (N>1) — mirrors FBA's ShipmentLabelsCard ───
// Owns its own carrier/tracking state and scopes every action to /shipments/:shipId/...
// Amber claim-warning: Walmart confirmed physical delivery 7+ days ago but the FC has
// receipted nothing — WFS inbound-discrepancy-claim territory (the 7702612WFA case:
// delivered 05-04, still 0/36 received in July while its 3 split-siblings closed 72/72).
function WfsDeliveredNotReceivedWarning({ s }) {
  if (!s || !s.actual_delivery_date) return null;
  if ((parseInt(s.received_units) || 0) > 0) return null;
  if (['closed', 'cancelled'].indexOf(s.status) >= 0) return null;
  var days = Math.floor((Date.now() - new Date(s.actual_delivery_date).getTime()) / 86400000);
  if (days < 7) return null;
  return h('div', { style: { display: 'flex', alignItems: 'flex-start', gap: 8, padding: 10, background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.35)', borderRadius: 8, marginBottom: 10 } },
    h(LIcon, { name: 'alert-triangle', size: 15, color: '#F59E0B' }),
    h('div', { style: { fontSize: 12, color: '#FCD34D', lineHeight: 1.5 } },
      h('strong', null, (s.walmart_shipment_id || 'Shipment') + ': delivered ' + days + ' days ago — nothing received. '),
      'Walmart shows delivery on ' + new Date(s.actual_delivery_date).toLocaleDateString() + (s.shipment_units ? ' (' + s.shipment_units + ' units)' : '') + ' but 0 units receipted at ' + (s.destination_fc || 'the FC') + '. Consider filing a WFS inbound discrepancy claim.'
    )
  );
}

function WfsShipmentCard({ shipment, plan, showToast, onRefresh }) {
  var s = shipment;
  var isLTL = (s.shipment_type || plan.shipment_type) === 'LTL';
  var base = '/wfs-shipments/shipments/' + s.id;
  var [carrierMode, setCarrierMode] = useState('preferred');
  var [carrierQuotes, setCarrierQuotes] = useState(null);
  var [carrierBooked, setCarrierBooked] = useState(!!(s.carrier || s.tracking_number));
  var [loading, setLoading] = useState('');
  var [carrier, setCarrier] = useState(s.carrier || '');
  var [trackNumber, setTrackNumber] = useState(s.tracking_number || '');
  var [proNumber, setProNumber] = useState(s.pro_number || '');
  var [bolNumber, setBolNumber] = useState(s.bol_number || '');
  var shipped = ['shipped', 'delivered', 'closed', 'receiving'].indexOf(s.status) >= 0;

  var getLabels = function() {
    setLoading('labels');
    api(base + '/get-labels', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
      .then(function() { window.open('/api' + base + '/labels-pdf', '_blank'); if (onRefresh) onRefresh(); })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setLoading(''); });
  };
  var getQuote = function() {
    setLoading('quote');
    api(base + '/carrier-quote', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
      .then(function(d) { setCarrierQuotes(d); showToast('Rate quote received', 'success'); })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setLoading(''); });
  };
  var book = function() {
    setLoading('book');
    api(base + '/carrier-book', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
      .then(function(d) { setCarrierBooked(true); if (d.carrierName) setCarrier(d.carrierName); if (d.trackingNumber || d.trackingNo) setTrackNumber(d.trackingNumber || d.trackingNo); showToast('Carrier booked!', 'success'); if (onRefresh) onRefresh(); })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setLoading(''); });
  };
  var cancelBooking = async function() {
    if (!(await appConfirm('Cancel this carrier booking?', { danger: true, okLabel: 'Cancel booking' }))) return;
    setLoading('cancel');
    api(base + '/carrier-book', { method: 'DELETE' })
      .then(function() { setCarrierBooked(false); setCarrierQuotes(null); showToast('Booking cancelled', 'success'); if (onRefresh) onRefresh(); })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setLoading(''); });
  };
  var markShipped = function() {
    setLoading('ship');
    api(base + '/tracking', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ carrier: carrier, tracking_number: trackNumber, pro_number: proNumber, bol_number: bolNumber }) })
      .then(function(r) { showToast(r.warning || 'Marked shipped', r.warning ? 'warning' : 'success'); if (onRefresh) onRefresh(); })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setLoading(''); });
  };

  return h(Card, { style: { marginBottom: 12 } },
    h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 } },
      h('div', null,
        h('div', { style: { fontSize: 14, fontWeight: 600, color: C.text, display: 'flex', alignItems: 'center', gap: 8 } }, (s.display_name || s.destination_fc || 'Shipment'), h(WfsBadge, { status: s.status })),
        h('div', { style: { fontFamily: 'inherit', fontSize: 11, color: C.textMuted } }, s.walmart_shipment_id || '—')
      ),
      h('div', { style: { display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' } },
        h(Btn, { size: 'sm', onClick: function() {
          var wid = (s.walmart_shipment_id || ('Draft' + s.id)).replace(/[^A-Za-z0-9_-]/g, '');
          var a = document.createElement('a'); a.href = '/api' + base + '/export-xlsx'; a.download = wid + '_Picklist.xlsx';
          document.body.appendChild(a); a.click(); setTimeout(function() { document.body.removeChild(a); }, 0);
        } }, h(LIcon, { name: 'file-text', size: 13 }), ' Picklist'),
        h(WorkOrdersMenu, { shipment: s, plan: plan, showToast: showToast }),
        h(Btn, { size: 'sm', disabled: loading === 'labels', onClick: getLabels }, h(LIcon, { name: 'printer', size: 13 }), ' Receiving Labels'),
        carrierBooked && h(Btn, { size: 'sm', onClick: function() { window.open('/api' + base + '/carrier-label-pdf', '_blank'); } }, h(LIcon, { name: 'download', size: 13 }), ' Carrier Label'),
        isLTL && h(Btn, { size: 'sm', onClick: function() { window.open('/api' + base + '/bol-pdf', '_blank'); } }, h(LIcon, { name: 'file-text', size: 13 }), ' BOL')
      )
    ),
    h(WfsDeliveredNotReceivedWarning, { s: s }),
    shipped ? h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, padding: 10, background: 'rgba(99,102,241,0.08)', borderRadius: 8 } },
      h(LIcon, { name: 'check-circle', size: 16, color: '#6366F1' }),
      h('span', { style: { fontWeight: 600, color: '#6366F1' } }, 'Shipped' + (carrier ? ' — ' + carrier : '') + (trackNumber ? ' (' + trackNumber + ')' : ''))
    ) : h('div', null,
      h('div', { style: { display: 'flex', gap: 8, marginBottom: 12 } },
        h('button', { type: 'button', onClick: function() { setCarrierMode('preferred'); }, style: { flex: 1, padding: '8px 10px', borderRadius: 6, border: '2px solid ' + (carrierMode === 'preferred' ? C.accent : C.border), background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 600, color: carrierMode === 'preferred' ? C.accent : C.text } }, 'Preferred Carrier'),
        h('button', { type: 'button', onClick: function() { setCarrierMode('own'); }, style: { flex: 1, padding: '8px 10px', borderRadius: 6, border: '2px solid ' + (carrierMode === 'own' ? C.accent : C.border), background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 600, color: carrierMode === 'own' ? C.accent : C.text } }, 'Own Carrier')
      ),
      carrierMode === 'preferred' && h('div', null,
        !carrierQuotes && !carrierBooked && h(Btn, { variant: 'primary', size: 'sm', disabled: loading === 'quote', onClick: getQuote }, loading === 'quote' ? 'Getting Quote…' : 'Get Rate Quote'),
        carrierQuotes && !carrierBooked && h('div', null,
          h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 8 } }, 'Quote ready.'),
          h('div', { style: { display: 'flex', gap: 8 } },
            h(Btn, { size: 'sm', onClick: function() { setCarrierQuotes(null); } }, 'Cancel'),
            h(Btn, { variant: 'primary', size: 'sm', disabled: loading === 'book', onClick: book }, loading === 'book' ? 'Booking…' : 'Book Carrier')
          )
        ),
        carrierBooked && h('div', { style: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' } },
          h('span', { style: { fontSize: 12, color: C.success, fontWeight: 600 } }, 'Carrier booked' + (carrier ? (' — ' + carrier) : '')),
          h(Btn, { size: 'sm', style: { background: 'rgba(248,113,113,0.08)', color: '#F87171', border: '1px solid rgba(248,113,113,0.3)' }, disabled: loading === 'cancel', onClick: cancelBooking }, 'Cancel Booking')
        )
      ),
      carrierMode === 'own' && h('div', null,
        h('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 } },
          h(Inp, { label: 'Carrier', value: carrier, onChange: function(e) { setCarrier(e.target.value); }, placeholder: 'UPS, FedEx' }),
          h(Inp, { label: 'Tracking Number', value: trackNumber, onChange: function(e) { setTrackNumber(e.target.value); } }),
          isLTL && h(Inp, { label: 'PRO Number', value: proNumber, onChange: function(e) { setProNumber(e.target.value); } }),
          isLTL && h(Inp, { label: 'BOL Number', value: bolNumber, onChange: function(e) { setBolNumber(e.target.value); } })
        ),
        h('div', { style: { display: 'flex', justifyContent: 'flex-end' } },
          h(Btn, { variant: 'primary', size: 'sm', disabled: loading === 'ship' || !carrier, onClick: markShipped }, loading === 'ship' ? 'Saving…' : 'Mark Shipped')
        )
      )
    )
  );
}

// ─── WFS Box Packing View (matches FBA pattern) ───
function WfsBoxPackingView({ planId, shipmentId, showToast, plan, currentUser, onRefresh, onPackingStatusChange }) {
  var [items, setItems] = useState([]);
  var [boxes, setBoxes] = useState([]);
  var [loading, setLoading] = useState(true);
  var [dimForm, setDimForm] = useState({ length: '', width: '', height: '', weight: '' });
  var [oversizeConfirmed, setOversizeConfirmed] = useState(false);
  var [expandedBoxId, setExpandedBoxId] = useState(null);
  var viewBox = expandedBoxId ? boxes.find(function(b) { return b.id === expandedBoxId; }) : null;
  var [editBox, setEditBox] = useState(null);
  var [editBoxSaving, setEditBoxSaving] = useState(false);
  var [editBoxAddOpen, setEditBoxAddOpen] = useState(false);
  var [editBoxSearch, setEditBoxSearch] = useState(''); // search within the Edit Box "Add Item" panel
  var [editBoxItemSearch, setEditBoxItemSearch] = useState(''); // search within the current Items-in-Box list
  var [deleteBoxConfirm, setDeleteBoxConfirm] = useState(null);
  var [duplicateBoxModal, setDuplicateBoxModal] = useState(null); // { boxId, boxNum, unitCount, skuCount, maxCopies }
  var [duplicateCount, setDuplicateCount] = useState(1);
  var [duplicateSaving, setDuplicateSaving] = useState(false);
  var [lightbox, setLightbox] = useState(null);

  // Guided workflow state
  var [wizStep, setWizStep] = useState(0); // 0=overview, 1=select items, 2=verify & qty, 3=dims & finish
  var [selectedItems, setSelectedItems] = useState({});
  var [packQtys, setPackQtys] = useState({});
  // Abandon the in-progress box (mirrors FBA's cancelWizard). The autosave effect skips
  // wizStep 0, so the saved draft must be deleted explicitly or Resume would offer the
  // abandoned box next time.
  var cancelWizard = function() {
    setSelectedItems({});
    setPackQtys({});
    setNumCopies(1);
    setWizStep(0);
    if (shipmentId) api('/wfs-shipments/shipments/' + shipmentId + '/box-draft', { method: 'DELETE' }).catch(function() {});
    setRawDraft(null);
  };
  // Shift-click selects the whole range between the last-clicked row and this one
  // (mirrors the FBA create-modal PO list). Plain row click still only selects;
  // deselect stays a deliberate checkbox click.
  var lastPackClickRef = useRef(null);
  var packRowClick = function(e, item, list) {
    if (e.shiftKey && lastPackClickRef.current != null) {
      var lastIdx = -1, curIdx = -1;
      for (var i = 0; i < list.length; i++) {
        if (list[i].id === lastPackClickRef.current) lastIdx = i;
        if (list[i].id === item.id) curIdx = i;
      }
      if (lastIdx !== -1 && curIdx !== -1) {
        var lo = Math.min(lastIdx, curIdx), hi = Math.max(lastIdx, curIdx);
        for (var j = lo; j <= hi; j++) { if (!selectedItems[list[j].id]) toggleItem(list[j].id); }
        lastPackClickRef.current = item.id;
        return;
      }
    }
    if (!selectedItems[item.id]) toggleItem(item.id);
    lastPackClickRef.current = item.id;
  };
  var [saving, setSaving] = useState(false);
  var [itemSearch, setItemSearch] = useState('');
  var [unexpectedOpen, setUnexpectedOpen] = useState(false);

  // ─── Pack-a-Box draft persistence (resume an in-progress box after leaving, cross-device) ───
  // rawDraft: undefined = not loaded yet, null = none saved, { draft, updated_at } = saved.
  // Only active when shipmentId is set (draft routes are shipment-scoped, like the box routes).
  var [rawDraft, setRawDraft] = useState(undefined);
  var [draftPrompt, setDraftPrompt] = useState(null); // { data, updated_at } while asking Resume / Start-fresh
  var draftSaveTimer = useRef(null);
  // Reconcile a saved draft against currently-packable items: drop items that are gone /
  // fully-packed / excluded, clamp each qty to what's still packable. An item belongs to the
  // draft if it was checked OR had a qty entered. Returns null if nothing usable remains.
  // WFS box-items key on plan_item_id, but the shipment-items array (avail) is keyed by its own
  // `id` (== the box item's plan_item_id), so the draft's selection map is keyed by item.id.
  var reconcileDraft = function(draft, avail) {
    if (!draft) return null;
    var sel0 = draft.selectedItems || {};
    var q0 = draft.packQtys || {};
    var byId = {};
    (avail || []).forEach(function(it) { byId[String(it.id)] = it; });
    var ids = {};
    Object.keys(sel0).forEach(function(k) { if (sel0[k]) ids[k] = true; });
    Object.keys(q0).forEach(function(k) { if ((parseInt(q0[k], 10) || 0) > 0) ids[k] = true; });
    var sel = {}, qtys = {}, lines = [];
    Object.keys(ids).forEach(function(sid) {
      var it = byId[String(sid)];
      if (!it) return;
      var remaining = (it.quantity || 0) - (it.quantity_packed || 0) - (it.quantity_missing || 0);
      if (remaining <= 0) return;
      sel[sid] = true;
      var savedQ = parseInt(q0[sid], 10);
      if (!isNaN(savedQ) && savedQ > 0) qtys[sid] = Math.min(savedQ, remaining);
      lines.push({ label: it.sku || it.product_sku || ('#' + sid), qty: (qtys[sid] != null ? qtys[sid] : null) });
    });
    if (!lines.length) return null;
    return { selectedItems: sel, packQtys: qtys, numCopies: draft.numCopies || 1,
      dimForm: draft.dimForm || { length: '', width: '', height: '', weight: '' }, lines: lines };
  };

  // ─── Print GTIN labels modal + label templates (mirrors FBA print modal) ───
  var [labelTemplates, setLabelTemplates] = useState([]);
  var [printModal, setPrintModal] = useState(null);          // null | { items: [...] }
  var [selectedLabelTpl, setSelectedLabelTpl] = useState(null);
  var [printQtyOverride, setPrintQtyOverride] = useState({}); // { itemId: qty }
  // Copies (Step 3) — one finish creates N identical boxes
  var [numCopies, setNumCopies] = useState(1);
  // Completed-boxes search (right panel)
  var [boxFilter, setBoxFilter] = useState('');
  // Bulk mark-missing (Step 0 Incomplete Items)
  var [bulkMissingSelected, setBulkMissingSelected] = useState({}); // { itemId: true }
  var [bulkMissingConfirm, setBulkMissingConfirm] = useState(null); // { items: [...], note: '' }
  var [bulkMissingSaving, setBulkMissingSaving] = useState(false);

  // Case-packed mode state
  var isCasePacked = plan && plan.packing_mode === 'case_packed';
  // Walmart-prep labeling: when the plan chose Walmart Prep, Walmart applies the GTIN
  // labels — don't push seller printing. Items with an explicit per-item service list
  // obey it (LABEL present = Walmart labels); items with NO services stored default to
  // Walmart-labeled, since the plan-level mode declares the intent.
  var walmartLabeled = function(it) {
    if (!plan || plan.prep_mode !== 'walmart') return false;
    var svc = it && it.prep_services;
    if (typeof svc === 'string') { try { svc = JSON.parse(svc); } catch (e) { svc = []; } }
    if (Array.isArray(svc) && svc.length) return svc.some(function(s) { return s && s.serviceType === 'LABEL'; });
    return true;
  };
  var [cpSelectedItem, setCpSelectedItem] = useState(null);
  var [cpUseSaved, setCpUseSaved] = useState(true);
  var [cpUnitsPerCase, setCpUnitsPerCase] = useState('');
  var [cpDims, setCpDims] = useState({ length: '', width: '', height: '', weight: '' });
  var [cpNumCases, setCpNumCases] = useState('');
  var [cpSaveDefault, setCpSaveDefault] = useState(true);
  var [cpGenerating, setCpGenerating] = useState(false);
  var [cpProgress, setCpProgress] = useState({ current: 0, total: 0 });
  var [cpGenAllOpen, setCpGenAllOpen] = useState(false);   // confirm dialog for "Generate All Cases"
  var [rightTab, setRightTab] = useState('items');

  // Internal notes (from PO)
  var [notes, setNotes] = useState([]);
  var [newNote, setNewNote] = useState('');
  var [notesOpen, setNotesOpen] = useState(false);
  var notesEndRef = useRef(null);
  var wfsPoId = plan && (plan.po_id || (plan.po_ids && plan.po_ids[0]));

  var loadNotes = useCallback(function() {
    if (!wfsPoId) return;
    api('/purchase-orders/' + wfsPoId + '/notes').then(setNotes).catch(function(e) { console.error('[silent-catch]', e); });
  }, [wfsPoId]);

  useEffect(function() { loadNotes(); }, [loadNotes]);

  var sendNote = function() {
    if (!newNote.trim() || !wfsPoId) return;
    api('/purchase-orders/' + wfsPoId + '/notes', { method: 'POST', body: JSON.stringify({ message: newNote.trim(), source: 'wfs', entity_id: plan && plan.id }) })
      .then(function() { setNewNote(''); loadNotes(); })
      .catch(function(e) { showToast(e.message, 'error'); });
  };

  var deleteNote = function(noteId) {
    if (!wfsPoId) return;
    api('/purchase-orders/' + wfsPoId + '/notes/' + noteId, { method: 'DELETE' })
      .then(function() { loadNotes(); })
      .catch(function(e) { showToast(e.message, 'error'); });
  };

  useEffect(function() {
    if (notesOpen && notesEndRef.current) notesEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [notes.length, notesOpen]);

  // Box routes scope to the shipment when available (Phase 3), else fall back to the plan.
  var boxBase = shipmentId ? ('/wfs-shipments/shipments/' + shipmentId) : ('/wfs-shipments/plans/' + planId);

  var loadData = useCallback(function() {
    if (!planId) return;
    setLoading(true);
    var bb = shipmentId ? ('/wfs-shipments/shipments/' + shipmentId) : ('/wfs-shipments/plans/' + planId);
    Promise.all([
      api('/wfs-shipments/plans/' + planId),
      api(bb + '/boxes')
    ]).then(function(results) {
      var planData = results[0];
      var boxData = results[1];
      // Scope items to the active shipment when the order is split (N>1); else use the plan's items.
      var scopedItems = null;
      if (shipmentId && planData.shipments && planData.shipments.length > 1) {
        var sh = planData.shipments.find(function(s) { return s.id === shipmentId; });
        if (sh) scopedItems = sh.items || [];
      }
      setItems(scopedItems || planData.items || []);
      setBoxes(boxData || []);
      setLoading(false);
    }).catch(function(e) { showToast(e.message, 'error'); setLoading(false); });
  }, [planId, shipmentId]);
  useEffect(function() { loadData(); }, [loadData]);

  // Load this user's saved in-progress box draft for the shipment (cross-device resume).
  // Draft persistence is shipment-scoped — when there's no shipmentId, treat it as "no draft".
  useEffect(function() {
    if (!shipmentId) { setRawDraft(null); return; }
    var cancelled = false;
    setRawDraft(undefined);
    api('/wfs-shipments/shipments/' + shipmentId + '/box-draft')
      .then(function(d) { if (!cancelled) setRawDraft(d || null); })
      .catch(function() { if (!cancelled) setRawDraft(null); });
    return function() { cancelled = true; };
  }, [shipmentId]);

  // Auto-save the in-progress box (debounced) so it survives leaving the page — even on another
  // device. Only while actively packing (wizStep > 0) and after the initial draft load, so the
  // empty mount state never clobbers a saved draft. An empty selection clears the draft instead.
  useEffect(function() {
    if (!shipmentId) return;
    if (rawDraft === undefined) return;
    if (wizStep === 0) return;
    if (draftSaveTimer.current) clearTimeout(draftSaveTimer.current);
    draftSaveTimer.current = setTimeout(function() {
      var hasContent = Object.keys(selectedItems).some(function(k) { return selectedItems[k]; });
      if (!hasContent) {
        api('/wfs-shipments/shipments/' + shipmentId + '/box-draft', { method: 'DELETE' }).catch(function() {});
        return;
      }
      api('/wfs-shipments/shipments/' + shipmentId + '/box-draft', {
        method: 'PUT',
        body: JSON.stringify({ draft: { selectedItems: selectedItems, packQtys: packQtys, numCopies: numCopies, dimForm: dimForm, wizStep: wizStep } })
      }).catch(function(e) { console.error('[draft-save]', e && e.message); });
    }, 600);
    return function() { if (draftSaveTimer.current) clearTimeout(draftSaveTimer.current); };
  }, [shipmentId, selectedItems, packQtys, numCopies, dimForm, wizStep, rawDraft]);

  // Load label templates once (kept whole; filtered to category 'wfs' where shown).
  useEffect(function() {
    api('/settings/label-templates').then(function(tpls) {
      setLabelTemplates(tpls || []);
      var wfsTpls = (tpls || []).filter(function(t) { return (t.category || '') === 'wfs'; });
      var def = wfsTpls.find(function(t) { return t.is_default; });
      if (def) setSelectedLabelTpl(def.id);
      else if (wfsTpls.length) setSelectedLabelTpl(wfsTpls[0].id);
    }).catch(function(e) { console.error('[silent-catch]', e); });
  }, []);

  var [unexpectedReceived, setUnexpectedReceived] = useState([]);
  var [unexpLightbox, setUnexpLightbox] = useState(null); // { images, index }
  var [missingPending, setMissingPending] = useState(null); // { item } pending mark-missing confirmation
  var [missingNote, setMissingNote] = useState('');
  // Pack-view tabs (parity with FBA's pack step; no Errored tab — that's Amazon-specific)
  var [packTab, setPackTab] = useState('shipment'); // 'shipment' | 'errored' | 'unexpected' | 'incomplete' | 'activity'
  // Errored items (FBA parity): removed from the plan as Walmart-rejected; loaded eagerly
  // so the tab badge shows without clicking.
  var [erroredItems, setErroredItems] = useState([]);
  useEffect(function() {
    if (!planId) return;
    api('/wfs-shipments/plans/' + planId + '/errored-items').then(function(r) { setErroredItems(Array.isArray(r) ? r : []); }).catch(function() {});
  }, [planId]);
  var [activityLog, setActivityLog] = useState([]);
  var [activityLoading, setActivityLoading] = useState(false);
  var [activitySearch, setActivitySearch] = useState('');
  var loadActivity = useCallback(function() {
    if (!planId) return;
    setActivityLoading(true);
    return api('/wfs-shipments/plans/' + planId + '/activity')
      .then(function(rows) { setActivityLog(rows || []); })
      .catch(function() { setActivityLog([]); })
      .finally(function() { setActivityLoading(false); });
  }, [planId]);
  useEffect(function() { if (packTab === 'activity') loadActivity(); }, [packTab, loadActivity]);
  var loadUnexpectedReceived = useCallback(function() {
    if (!planId) return;
    api('/wfs-shipments/plans/' + planId + '/unexpected-received-items')
      .then(setUnexpectedReceived).catch(function() { setUnexpectedReceived([]); });
  }, [planId]);
  useEffect(function() { loadUnexpectedReceived(); }, [loadUnexpectedReceived]);
  var undoUnexpected = function(logId) {
    api('/wfs-shipments/plans/' + planId + '/unexpected-received-items/' + logId + '/undo', { method: 'POST' })
      .then(function(r) { showToast('Undid unexpected ' + r.sku + ' x' + r.qty, 'info'); loadUnexpectedReceived(); if (onRefresh) onRefresh(); })
      .catch(function(e) { showToast(e.message, 'error'); });
  };

  var totalPacked = items.reduce(function(s, i) { return s + (i.quantity_packed || 0); }, 0);
  var totalQty = items.reduce(function(s, i) { return s + (i.quantity || 0); }, 0);
  var totalMissing = items.reduce(function(s, i) { return s + (i.quantity_missing || 0); }, 0);
  var effectiveTotal = totalQty - totalMissing;
  var pctPacked = effectiveTotal > 0 ? Math.round((totalPacked / effectiveTotal) * 100) : 0;

  // Items available (not fully packed or marked missing)
  var availableItems = items.filter(function(i) { return (i.quantity_packed || 0) + (i.quantity_missing || 0) < i.quantity; });
  var allResolved = items.length > 0 && availableItems.length === 0;

  // "Generate All Cases" (2026-10-30): every open line of a case-packed plan already carries its
  // supplier-pack config (units per carton + carton dims, stamped at submit), so one click creates
  // every sealed carton through /boxes/finish-batch, SKU by SKU — the same write Configure Cases
  // does per SKU. Lines whose remainder is not whole cartons are left for the per-SKU wizard.
  var cpPlanRows = isCasePacked ? availableItems.map(function(it) {
    var upc = parseInt(it.inner_pack_qty) || 0;
    var rem = it.quantity - (it.quantity_packed || 0) - (it.quantity_missing || 0);
    var dimsOk = parseFloat(it.case_length) > 0 && parseFloat(it.case_width) > 0 && parseFloat(it.case_height) > 0;
    return { it: it, upc: upc, count: upc > 0 ? Math.floor(rem / upc) : 0, whole: upc > 0 && rem % upc === 0, dimsOk: dimsOk };
  }).filter(function(x) { return x.count > 0 && x.whole && x.dimsOk; }) : [];
  var cpPlanTotal = cpPlanRows.reduce(function(s, x) { return s + x.count; }, 0);
  var generateAllCases = function() {
    if (cpGenerating || !cpPlanRows.length) return;
    setCpGenAllOpen(false);
    setCpGenerating(true);
    setCpProgress({ current: 0, total: cpPlanTotal });
    var done = 0;
    cpPlanRows.reduce(function(chain, x) {
      return chain.then(function() {
        return api(boxBase + '/boxes/finish-batch', { method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ barcode: x.it.sku || x.it.product_sku, quantity: x.upc }], dims: { length: parseFloat(x.it.case_length), width: parseFloat(x.it.case_width), height: parseFloat(x.it.case_height), weight: parseFloat(x.it.case_weight) || null }, count: x.count }) })
          .then(function() { done += x.count; setCpProgress({ current: done, total: cpPlanTotal }); });
      });
    }, Promise.resolve())
      .then(function() {
        showToast(cpPlanTotal + ' cases generated for ' + cpPlanRows.length + ' SKU' + (cpPlanRows.length === 1 ? '' : 's'), 'success');
        if (typeof fireConfetti === 'function') fireConfetti();
      })
      .catch(function(e) { showToast(e.message || 'Failed to generate cases', 'error'); })
      .finally(function() { setCpGenerating(false); setCpProgress({ current: 0, total: 0 }); loadData(); if (onRefresh) onRefresh(); });
  };

  useEffect(function() {
    if (onPackingStatusChange) onPackingStatusChange(allResolved);
  }, [allResolved]);

  // Mark missing handler
  var markMissing = function(item, note) {
    var remaining = item.quantity - (item.quantity_packed || 0) - (item.quantity_missing || 0);
    var qm = (item.quantity_missing || 0) + remaining;
    var trimmed = (note || '').trim();
    var body = { quantity_missing: qm };
    if (trimmed) body.notes = trimmed;
    api('/wfs-shipments/items/' + item.id + '/missing', { method: 'PUT', body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } }).then(function() {
      showToast('Marked ' + remaining + ' unit(s) as missing', 'info');
      loadData();
      if (onRefresh) onRefresh();
    }).catch(function(e) { showToast(e.message, 'error'); });
  };

  var unmarkMissing = function(item) {
    api('/wfs-shipments/items/' + item.id + '/missing', { method: 'PUT', body: JSON.stringify({ quantity_missing: 0 }), headers: { 'Content-Type': 'application/json' } }).then(function() {
      showToast('Cleared missing status', 'info');
      loadData();
      if (onRefresh) onRefresh();
    }).catch(function(e) { showToast(e.message, 'error'); });
  };

  // Bulk-mark every selected item's remaining (unpacked, unmarked) units as missing.
  var executeBulkMarkMissing = function(targetItems, note) {
    if (!targetItems || !targetItems.length) return;
    setBulkMissingSaving(true);
    var trimmed = (note || '').trim();
    var total = targetItems.reduce(function(s, it) {
      return s + Math.max(0, it.quantity - (it.quantity_packed || 0) - (it.quantity_missing || 0));
    }, 0);
    var requests = targetItems.map(function(it) {
      var remaining = it.quantity - (it.quantity_packed || 0) - (it.quantity_missing || 0);
      if (remaining <= 0) return Promise.resolve();
      var body = { quantity_missing: (it.quantity_missing || 0) + remaining };
      if (trimmed) body.notes = trimmed;
      return api('/wfs-shipments/items/' + it.id + '/missing', { method: 'PUT', body: JSON.stringify(body), headers: { 'Content-Type': 'application/json' } });
    });
    Promise.allSettled(requests).then(function(results) {
      var failed = results.filter(function(r) { return r.status === 'rejected'; }).length;
      if (failed > 0) {
        showToast('Marked ' + (targetItems.length - failed) + ' / ' + targetItems.length + ' items — ' + failed + ' failed', 'error');
      } else {
        showToast('Marked ' + total + ' unit' + (total === 1 ? '' : 's') + ' across ' + targetItems.length + ' SKU' + (targetItems.length === 1 ? '' : 's') + ' as missing', 'success');
      }
      setBulkMissingConfirm(null);
      setBulkMissingSelected({});
      loadData();
      if (onRefresh) onRefresh();
    }).finally(function() { setBulkMissingSaving(false); });
  };

  // Selected items for current box wizard
  var selectedItemsList = availableItems.filter(function(i) { return selectedItems[i.id]; });
  var selectedCount = Object.keys(selectedItems).filter(function(k) { return selectedItems[k]; }).length;

  // Check if all qtys entered in step 2
  var allQtysEntered = selectedItemsList.length > 0 && selectedItemsList.every(function(i) {
    var q = parseInt(packQtys[i.id]);
    return q > 0;
  });

  // ─── Step 3: Finish box (pack items + set dims + seal) ───
  // numCopies > 1 creates N identical boxes (same items + dims), sealed sequentially.
  var finishBox = function() {
    if (saving) return;
    var l = parseFloat(dimForm.length); var w = parseFloat(dimForm.width);
    var ht = parseFloat(dimForm.height); var wt = parseFloat(dimForm.weight);
    if (!l || !w || !ht) { showToast('Box dimensions (L x W x H) are required', 'error'); return; }
    var copies = Math.max(1, Math.min(999, parseInt(numCopies, 10) || 1));

    // Build the per-box item payload once (same contents for every copy).
    var payloadItems = [];
    selectedItemsList.forEach(function(item) {
      var qty = parseInt(packQtys[item.id]) || 0;
      if (qty > 0) payloadItems.push({ barcode: item.sku || item.product_sku, quantity: qty });
    });
    if (!payloadItems.length) { showToast('No items selected', 'error'); return; }
    setSaving(true);

    // One atomic /finish-batch call (review 2026-09-28, medium #39): the old create → bulk-pack →
    // dims → seal chain per copy left a half-filled open box already received against the PO when
    // a later call failed, and a retry double-packed. The server validates qty × copies against
    // the packable balance before it writes anything; this check shows the shortfall first (#38).
    var over = [];
    selectedItemsList.forEach(function(item) {
      var qty = parseInt(packQtys[item.id]) || 0;
      var rem = item.quantity - (item.quantity_packed || 0) - (item.quantity_missing || 0);
      if (qty * copies > rem) over.push((item.sku || item.product_sku) + ': ' + rem + ' packable, asked ' + qty + ' × ' + copies);
    });
    if (over.length) { showToast('Too many units for the packable balance — ' + over.join('; '), 'error'); setSaving(false); return; }
    api(boxBase + '/boxes/finish-batch', { method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: payloadItems, dims: { length: l, width: w, height: ht, weight: wt || null }, count: copies }) })
    .then(function(res) {
      var packedNums = ((res && res.boxes) || []).map(function(b) { return b.box_number; });
      showToast(copies === 1 ? ('Box ' + packedNums[0] + ' packed successfully') : (copies + ' boxes packed (#' + packedNums.join(', #') + ')'), 'success');
      if (typeof fireConfetti === 'function') fireConfetti();
      // Reset for next box
      setSelectedItems({});
      setPackQtys({});
      setNumCopies(1);
      setDimForm({ length: '', width: '', height: '', weight: '' });
      setWizStep(0);
      setSaving(false);
      // This box is committed — drop the saved draft so it doesn't resurface.
      if (shipmentId) api('/wfs-shipments/shipments/' + shipmentId + '/box-draft', { method: 'DELETE' }).catch(function() {});
      setRawDraft(null);
      loadData();
      if (onRefresh) onRefresh();
    }).catch(function(e) { showToast(e.message || 'Failed to pack box', 'error'); setSaving(false); loadData(); });
  };

  // ─── Toggle item selection ───
  var toggleItem = function(id) {
    setSelectedItems(function(prev) {
      var n = Object.assign({}, prev);
      n[id] = !n[id];
      if (!n[id]) {
        delete n[id];
        // Deselecting also clears any inline quantity entered for that item.
        setPackQtys(function(q) { var nq = Object.assign({}, q); delete nq[id]; return nq; });
      }
      return n;
    });
  };
  var toggleAll = function() {
    if (selectedCount === availableItems.length) {
      setSelectedItems({});
    } else {
      var n = {};
      availableItems.forEach(function(i) { n[i.id] = true; });
      setSelectedItems(n);
    }
  };

  // ─── Delete box (FBA-style: confirm modal, then hard delete) ───
  var deleteBox = function(boxId) {
    var box = boxes.find(function(b) { return b.id === boxId; });
    if (!box) return;
    var bItems = box.items || [];
    var unitCount = bItems.reduce(function(s, i) { return s + (i.quantity || 0); }, 0);
    setDeleteBoxConfirm({ boxId: boxId, boxNum: box.box_number, unitCount: unitCount, skuCount: bItems.length });
  };

  var confirmDeleteBox = function() {
    if (!deleteBoxConfirm) return;
    var boxId = deleteBoxConfirm.boxId;
    var boxNum = deleteBoxConfirm.boxNum;
    setDeleteBoxConfirm(null);
    api(boxBase + '/boxes/' + boxId, { method: 'DELETE' })
      .then(function() { showToast('Box ' + boxNum + ' deleted', 'success'); loadData(); if (onRefresh) onRefresh(); })
      .catch(function(e) { showToast(e.message, 'error'); });
  };

  // ─── Duplicate box with confirmation + count ───
  // WFS box-items key on plan_item_id; the shipment-items array (`items`) is keyed by its own id.
  var computeMaxCopies = function(box) {
    var bItems = (box && box.items) || [];
    if (!bItems.length) return 0;
    var maxes = bItems.map(function(bi) {
      if (!bi.quantity || bi.quantity <= 0) return 0;
      var si = items.find(function(it) { return it.id === bi.plan_item_id; });
      if (!si) return 0;
      var remaining = (si.quantity || 0) - (si.quantity_packed || 0) - (si.quantity_missing || 0);
      if (remaining <= 0) return 0;
      return Math.floor(remaining / bi.quantity);
    });
    return Math.min.apply(null, maxes);
  };

  var openDuplicateBox = function(box) {
    var bItems = box.items || [];
    var unitCount = bItems.reduce(function(s, i) { return s + i.quantity; }, 0);
    var maxCopies = computeMaxCopies(box);
    setDuplicateBoxModal({ boxId: box.id, boxNum: box.box_number, unitCount: unitCount, skuCount: bItems.length, maxCopies: maxCopies });
    setDuplicateCount(Math.max(1, Math.min(1, maxCopies)));
  };

  var confirmDuplicateBox = function() {
    if (!duplicateBoxModal) return;
    var max = duplicateBoxModal.maxCopies;
    var n = parseInt(duplicateCount, 10) || 0;
    if (n < 1 || n > max) return;
    var boxNum = duplicateBoxModal.boxNum;
    var boxId = duplicateBoxModal.boxId;
    setDuplicateSaving(true);
    api(boxBase + '/boxes/' + boxId + '/duplicate', { method: 'POST', body: JSON.stringify({ count: n }) })
      .then(function() {
        showToast('Box ' + boxNum + (n === 1 ? ' duplicated' : ' duplicated (' + n + ' copies)'), 'success');
        setDuplicateBoxModal(null);
        loadData();
        if (onRefresh) onRefresh();
      })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setDuplicateSaving(false); });
  };

  // ─── Edit box modal helpers ───
  var enrichBoxItem = function(bi) {
    var si = items.find(function(i) { return i.product_id === bi.product_id; }) || {};
    var thumbUrl = (si.images && si.images.length) ? si.images[0] : si.image_1_url || null;
    return {
      id: bi.id, sku: bi.product_sku || bi.sku || si.sku || '',
      gtin: bi.gtin || si.gtin || '', barcode: bi.barcode || si.barcode || '',
      vendor_title: bi.vendor_title || si.vendor_title || '', vendor_sku: si.vendor_sku || '',
      vendor_name: si.vendor_name || '',
      quantity: bi.quantity, plan_item_id: bi.plan_item_id, product_id: bi.product_id,
      _original_qty: bi.quantity, thumbUrl: thumbUrl, _isNew: false
    };
  };

  var openEditBox = function(box) {
    // Do NOT unseal on open — opening to view must never mutate the box. A sealed box is
    // unsealed lazily in saveEditBox, and ONLY when there are real changes, then re-sealed.
    // (Previously open→unseal + cancel/save→seal spammed the activity log with phantom
    // unsealed/sealed entries even when nothing was edited.)
    var origDims = { length: box.length || '', width: box.width || '', height: box.height || '', weight: box.weight || '' };
    setEditBox({
      id: box.id, box_number: box.box_number,
      items: (box.items || []).map(enrichBoxItem),
      dims: Object.assign({}, origDims), _origDims: origDims,
      wasSealed: !!box.is_sealed, removed: []
    });
    setEditBoxAddOpen(false);
    setEditBoxSearch('');
    setEditBoxItemSearch('');
  };

  var editBoxRemoveItem = function(idx) {
    setEditBox(function(prev) {
      var item = prev.items[idx];
      var removed = prev.removed.slice();
      if (item.id && !item._isNew) removed.push({ id: item.id, item: item, idx: idx });
      return Object.assign({}, prev, { items: prev.items.filter(function(_, i) { return i !== idx; }), removed: removed });
    });
  };

  var editBoxUndoRemove = function(rIdx) {
    setEditBox(function(prev) {
      var entry = prev.removed[rIdx];
      var newRemoved = prev.removed.filter(function(_, i) { return i !== rIdx; });
      var newItems = prev.items.slice();
      var insertAt = Math.min(entry.idx, newItems.length);
      newItems.splice(insertAt, 0, entry.item);
      return Object.assign({}, prev, { items: newItems, removed: newRemoved });
    });
  };

  var editBoxAddItem = function(planItem) {
    var si = planItem;
    var thumbUrl = (si.images && si.images.length) ? si.images[0] : si.image_1_url || null;
    setEditBox(function(prev) {
      if (prev.items.some(function(i) { return i.product_id === si.product_id; })) return prev;
      var newItem = {
        id: null, sku: si.sku || si.product_sku || '',
        gtin: si.gtin || '', barcode: si.barcode || '',
        vendor_title: si.vendor_title || '', vendor_sku: si.vendor_sku || '',
        vendor_name: si.vendor_name || '',
        quantity: 1, plan_item_id: si.id, product_id: si.product_id,
        _original_qty: 0, thumbUrl: thumbUrl, _isNew: true
      };
      return Object.assign({}, prev, { items: prev.items.concat([newItem]) });
    });
    setEditBoxAddOpen(false);
  };

  var saveEditBox = function() {
    if (!editBox) return;
    var boxId = editBox.id;

    // Only hit the server when something actually changed — otherwise a sealed box would be
    // unsealed+resealed for nothing (the phantom activity-log entries).
    var dimsChanged = ['length', 'width', 'height', 'weight'].some(function(k) {
      return (parseFloat(editBox.dims[k]) || null) !== (parseFloat(editBox._origDims[k]) || null);
    });
    var itemsChanged = editBox.removed.length > 0 || editBox.items.some(function(it) {
      return (it._isNew && it.quantity > 0) || (it.id && !it._isNew && it.quantity !== it._original_qty);
    });
    if (!dimsChanged && !itemsChanged) { setEditBox(null); return; }

    setEditBoxSaving(true);
    var chain = Promise.resolve();

    // 0. A sealed box must be unsealed before its items/dims can be modified.
    if (editBox.wasSealed) {
      chain = chain.then(function() {
        return api('/wfs-shipments/boxes/' + boxId + '/unseal', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      });
    }

    // 1. Delete removed items
    editBox.removed.forEach(function(entry) {
      chain = chain.then(function() {
        return api('/wfs-shipments/boxes/' + boxId + '/items/' + entry.id, { method: 'DELETE' });
      });
    });

    // 2. Add new items via bulk-pack
    editBox.items.forEach(function(item) {
      if (item._isNew && item.quantity > 0) {
        chain = chain.then(function() {
          return api('/wfs-shipments/boxes/' + boxId + '/bulk-pack', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ barcode: item.sku || item.barcode, quantity: item.quantity }) });
        });
      }
    });

    // 3. Update changed quantities on existing items
    editBox.items.forEach(function(item) {
      if (item.id && !item._isNew && item.quantity !== item._original_qty) {
        chain = chain.then(function() {
          return api('/wfs-shipments/boxes/' + boxId + '/items/' + item.id, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ quantity: item.quantity }) });
        });
      }
    });

    // 4. Update dimensions (only when they changed)
    if (dimsChanged) {
      chain = chain.then(function() {
        var d = editBox.dims;
        return api(boxBase + '/boxes/' + boxId, { method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ length: parseFloat(d.length) || null, width: parseFloat(d.width) || null, height: parseFloat(d.height) || null, weight: parseFloat(d.weight) || null }) });
      });
    }

    // 5. Re-seal if it was sealed before
    if (editBox.wasSealed) {
      chain = chain.then(function() {
        return api('/wfs-shipments/boxes/' + boxId + '/seal', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      });
    }

    chain.then(function() {
      showToast('Box ' + editBox.box_number + ' updated', 'success');
      setEditBox(null); setEditBoxSaving(false); loadData(); if (onRefresh) onRefresh();
    }).catch(function(e) { showToast(e.message, 'error'); setEditBoxSaving(false); });
  };

  var cancelEditBox = function() {
    // No API calls — open no longer unseals, so there's nothing to restore on cancel.
    setEditBox(null);
  };

  if (loading) return h('div', { style: { color: C.textMuted, padding: 20 } }, 'Loading...');

  // ─── Step indicator ───
  var stepNames = isCasePacked ? ['Overview', 'Select SKU', 'Case Config', 'Review & Generate'] : ['Overview', 'Select Items', 'Verify & Qty', 'Dimensions & Finish'];
  var stepBar = h('div', { style: { display: 'flex', gap: 0, marginBottom: 20 } },
    stepNames.map(function(name, idx) {
      var isActive = wizStep === idx;
      var isDone = wizStep > idx;
      var color = isActive ? C.accent : isDone ? C.success : C.textDim;
      var canClick = idx <= wizStep && !isActive;
      return h('div', { key: idx, style: { flex: 1, textAlign: 'center', cursor: canClick ? 'pointer' : 'default' }, onClick: canClick ? function() { setWizStep(idx); } : undefined },
        h('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0 } },
          idx > 0 && h('div', { style: { flex: 1, height: 2, background: isDone || isActive ? C.accent : C.border } }),
          h('div', { style: { width: 28, height: 28, borderRadius: '50%', background: isActive ? C.accent : isDone ? C.success : C.surface, border: '2px solid ' + color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: isActive || isDone ? '#fff' : C.textDim, flexShrink: 0 } },
            isDone ? h(LIcon, { name: 'check', size: 14, color: '#fff' }) : String(idx + 1)),
          idx < stepNames.length - 1 && h('div', { style: { flex: 1, height: 2, background: isDone ? C.accent : C.border } })
        ),
        h('div', { style: { fontSize: 10, fontWeight: isActive ? 600 : 400, color: color, marginTop: 4 } }, name)
      );
    })
  );

  // ─── Print GTIN item labels (WFS equivalent of FBA's Print FNSKU Labels) ───
  // One code128 GTIN barcode label per unit. Size/format come from the WFS label template
  // configured in Settings > Templates (category 'wfs', default = 2" x 1"), mirroring FBA's
  // template-driven executePrint. Falls back to 2" x 1" thermal if no template exists.
  // Opens the Print GTIN Labels modal (no longer prints directly). Seeds the per-item
  // label count to each item's remaining (quantity − missing) and selects the default
  // WFS template, then lets the user adjust counts / pick a template before printing.
  var printGtinLabels = function(labelItems) {
    var list = (labelItems && labelItems.length) ? labelItems : items;
    list = list.filter(function(it) { return (it.gtin || it.barcode); });
    if (!list.length) { showToast('No GTIN/barcode on these items', 'error'); return; }
    var qtyMap = {};
    list.forEach(function(item) {
      qtyMap[item.id] = Math.max(1, (item.quantity || 0) - (item.quantity_missing || 0));
    });
    setPrintQtyOverride(qtyMap);
    var wfsTpls = labelTemplates.filter(function(t) { return (t.category || '') === 'wfs'; });
    var def = wfsTpls.find(function(t) { return t.is_default; });
    setSelectedLabelTpl(def ? def.id : (wfsTpls.length > 0 ? wfsTpls[0].id : null));
    setPrintModal({ items: list });
  };

  // Print executor — honors printQtyOverride per item via the optional qtyMap arg.
  var executeGtinPrint = function() {
    if (!printModal) return;
    var tpl = labelTemplates.find(function(t) { return t.id === selectedLabelTpl; }) || null;
    renderGtinLabels(printModal.items, tpl, printQtyOverride);
    setPrintModal(null);
  };

  // qtyMap (optional): { itemId: count }. When absent, falls back to remaining units.
  var renderGtinLabels = function(list, tpl, qtyMap) {
    var widthMm = tpl ? parseFloat(tpl.width_mm) : 50.8;   // default 2" x 1"
    var heightMm = tpl ? parseFloat(tpl.height_mm) : 25.4;
    var fontSize = tpl ? (tpl.font_size || 11) : 11;
    var showBarcode = tpl ? tpl.show_barcode !== false : true;
    var isSheet = tpl ? tpl.label_type === 'sheet' : false;
    var cols = tpl ? (tpl.columns || 1) : 1;
    var rowsPerSheet = tpl ? (tpl.rows_per_sheet || 1) : 1;
    var pageW = tpl && tpl.page_width_mm ? parseFloat(tpl.page_width_mm) : 215.9;
    var pageH = tpl && tpl.page_height_mm ? parseFloat(tpl.page_height_mm) : 279.4;
    var esc = function(s) { return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;'); };
    // how many title lines this template has room for (two where they fit, Anthony 2026-09-29)
    var titleFit = productLabelTitleFit(heightMm, fontSize, showBarcode ? Math.max(5, Math.round(heightMm * 0.32)) : 0);
    var allLabels = [];
    list.forEach(function(it) {
      var gtin = (it.gtin || it.barcode || '').toString();
      var qty = (qtyMap && qtyMap[it.id] != null) ? qtyMap[it.id] : ((it.quantity || 0) - (it.quantity_missing || 0));
      if (qty < 1) qty = 1;
      var barcodeImg = '';
      if (showBarcode && gtin) {
        try {
          var canvas = document.createElement('canvas');
          bwipjs.toCanvas(canvas, { bcid: 'code128', text: gtin, scale: 3, height: Math.max(5, Math.round(heightMm * 0.32)), includetext: false });
          barcodeImg = '<img src="' + canvas.toDataURL('image/png') + '" style="max-width:92%;height:auto;">';
        } catch (e) { barcodeImg = ''; }
      }
      var title = esc(it.vendor_title || it.product_sku || it.sku);
      var sku = esc(it.product_sku || it.sku);
      for (var i = 0; i < qty; i++) {
        var lbl = '<div style="width:' + widthMm + 'mm;height:' + heightMm + 'mm;box-sizing:border-box;padding:1.5mm;display:flex;flex-direction:column;justify-content:center;align-items:center;overflow:hidden;page-break-inside:avoid;font-family:Arial,sans-serif;">';
        if (barcodeImg) lbl += '<div style="text-align:center;margin-bottom:0.3mm;">' + barcodeImg + '</div>';
        lbl += '<div style="font-family:monospace;font-size:' + Math.max(5, fontSize - 3) + 'pt;font-weight:bold;letter-spacing:0.5px;">' + esc(gtin) + '</div>';
        // two lines, never cut on one (Anthony 2026-09-29, shared with the MELI printer)
        lbl += '<div style="' + productLabelTitleCss(titleFit.pt, titleFit.lines) + '">' + title + '</div>';
        if (sku) lbl += '<div style="font-size:' + Math.max(4, fontSize - 5) + 'pt;color:#666;">' + sku + '</div>';
        lbl += '</div>';
        allLabels.push(lbl);
      }
    });
    var pw = window.open('', '_blank', 'width=600,height=800');
    if (!pw) { showToast('Popup blocked — allow popups to print labels', 'error'); return; }
    var html;
    if (isSheet) {
      var mH = (pageW - (cols * widthMm)) / (cols + 1);
      var mV = (pageH - (rowsPerSheet * heightMm)) / (rowsPerSheet + 1);
      var pgs = []; var pp = cols * rowsPerSheet;
      for (var p = 0; p < allLabels.length; p += pp) {
        var ph = '<div style="width:' + pageW + 'mm;height:' + pageH + 'mm;box-sizing:border-box;padding:' + mV + 'mm ' + mH + 'mm;page-break-after:always;"><div style="display:grid;grid-template-columns:repeat(' + cols + ',' + widthMm + 'mm);grid-template-rows:repeat(' + rowsPerSheet + ',' + heightMm + 'mm);gap:0;width:100%;">';
        allLabels.slice(p, p + pp).forEach(function(l) { ph += l; });
        ph += '</div></div>'; pgs.push(ph);
      }
      html = pgs.join('');
    } else {
      html = '<div style="display:flex;flex-wrap:wrap;gap:0;">'; allLabels.forEach(function(l) { html += l; }); html += '</div>';
    }
    pw.document.write('<!DOCTYPE html><html><head><title>GTIN Labels</title><style>@media print{body{margin:0;}@page{size:' + (isSheet ? (pageW + 'mm ' + pageH + 'mm') : (widthMm + 'mm ' + heightMm + 'mm')) + ';margin:0;}div{page-break-inside:avoid;}}</style></head><body style="margin:0;padding:0;">' + html + '</body></html>');
    pw.document.close();
    setTimeout(function() { try { pw.focus(); pw.print(); } catch (e) { /* popup closed */ } }, 500);
  };

  // ─── Export GTIN labels as individual PDF files in a ZIP (one PDF per SKU) ───
  // Faithful port of FBA's exportFnskuLabelsZip with GTIN semantics (barcode value =
  // gtin||barcode, text line = GTIN). Honors printQtyOverride and the selected template.
  var exportGtinLabelsZip = function() {
    if (!printModal) return;
    if (!window.JSZip) { showToast('ZIP library not loaded', 'error'); return; }
    var jspdfNs = window.jspdf || window.jsPDF;
    var JsPDFCtor = jspdfNs && (jspdfNs.jsPDF || jspdfNs);
    if (!JsPDFCtor) { showToast('PDF library not loaded', 'error'); return; }
    var tpl = labelTemplates.find(function(t) { return t.id === selectedLabelTpl; });
    if (!tpl) { showToast('Select a template first', 'error'); return; }
    var itemsToPrint = printModal.items;
    var getFieldVal = function(item, field) {
      switch (field) {
        case 'gtin': return (item.gtin || item.barcode || '');
        case 'fnsku': return (item.gtin || item.barcode || '');
        case 'sku': return item.product_sku || item.sku || '';
        case 'title': return item.vendor_title || '';
        case 'barcode': return item.barcode || '';
        case 'condition': return 'New';
        case 'expiry': return '';
        default: return '';
      }
    };
    var widthMm = parseFloat(tpl.width_mm);
    var heightMm = parseFloat(tpl.height_mm);
    var fontSize = tpl.font_size || 11;
    var fields = tpl.fields || ['gtin', 'sku', 'title'];
    var showBarcode = tpl.show_barcode !== false;
    var isSheet = tpl.label_type === 'sheet';
    var cols = tpl.columns || 1;
    var rowsPerSheet = tpl.rows_per_sheet || 1;
    var pageW = tpl.page_width_mm ? parseFloat(tpl.page_width_mm) : 215.9;
    var pageH = tpl.page_height_mm ? parseFloat(tpl.page_height_mm) : 279.4;
    var pad = 2;
    var barcodeHeightMm = Math.max(4, heightMm * 0.28);
    var barcodeWidthMm = widthMm - pad * 2;
    var ptToMm = function(pt) { return pt * 0.3528; };
    var clip = function(str, maxMm, sizePt) {
      if (!str) return '';
      var charWidthMm = ptToMm(sizePt) * 0.5;
      var maxChars = Math.floor(maxMm / charWidthMm);
      return str.length > maxChars ? str.slice(0, Math.max(0, maxChars - 1)) + '…' : str;
    };
    var renderLabelAt = function(doc, item, x, y) {
      var cursorY = y + pad;
      var gtinVal = (item.gtin || item.barcode || '').toString();
      if (showBarcode && gtinVal) {
        var canvas = document.createElement('canvas');
        window.bwipjs.toCanvas(canvas, { bcid: 'code128', text: gtinVal, scale: 3, height: barcodeHeightMm, includetext: false, paddingleft: 4, paddingright: 4 });
        doc.addImage(canvas.toDataURL('image/png'), 'PNG', x + pad, cursorY, barcodeWidthMm, barcodeHeightMm);
        cursorY += barcodeHeightMm + 0.5;
      }
      if (gtinVal) {
        var gtinPt = Math.max(5, fontSize - 4);
        doc.setFont('courier', 'bold'); doc.setFontSize(gtinPt);
        cursorY += ptToMm(gtinPt) * 0.75;
        doc.text(gtinVal, x + widthMm / 2, cursorY, { align: 'center' });
        cursorY += 0.4;
      }
      var titleVal = getFieldVal(item, 'title');
      if (titleVal && fields.indexOf('title') >= 0) {
        // up to two lines measured with the real font, the type shrinks before anything is cut
        // (Anthony 2026-09-29, shared with the MELI printer)
        doc.setFont('helvetica', 'normal');
        var titleFit = productLabelTitleFit(heightMm, fontSize, showBarcode ? barcodeHeightMm : 0);
        var titleLines = productLabelTitleLines(doc, titleVal, widthMm - pad * 2, titleFit.pt, titleFit.lines);
        doc.setFontSize(titleLines.pt);
        titleLines.lines.forEach(function(line, idx) {
          cursorY += ptToMm(titleLines.pt) * (idx === 0 ? 0.75 : 1.15);
          doc.text(line, x + widthMm / 2, cursorY, { align: 'center' });
        });
        cursorY += 0.4;
      }
      var condVal = fields.indexOf('condition') >= 0 ? getFieldVal(item, 'condition') : '';
      var skuVal = fields.indexOf('sku') >= 0 ? getFieldVal(item, 'sku') : '';
      var expVal = fields.indexOf('expiry') >= 0 ? getFieldVal(item, 'expiry') : '';
      var bottomParts = [];
      if (condVal) bottomParts.push(condVal);
      if (skuVal) bottomParts.push(skuVal);
      if (expVal) bottomParts.push('Exp: ' + expVal);
      if (bottomParts.length > 0) {
        var botPt = Math.max(4, fontSize - 6);
        doc.setFont('helvetica', 'normal'); doc.setFontSize(botPt);
        var botY = y + heightMm - pad;
        doc.text(clip(bottomParts.join(' | '), widthMm - pad * 2, botPt), x + widthMm / 2, botY, { align: 'center' });
      }
    };
    var triggerDownload = function(blob, successMsg) {
      var url = URL.createObjectURL(blob);
      var a = document.createElement('a');
      var ts = new Date();
      var stamp = ts.getFullYear() + String(ts.getMonth() + 1).padStart(2, '0') + String(ts.getDate()).padStart(2, '0');
      a.href = url;
      a.download = 'gtin-labels-' + stamp + '.zip';
      document.body.appendChild(a);
      a.click();
      setTimeout(function() { document.body.removeChild(a); URL.revokeObjectURL(url); }, 200);
      showToast(successMsg, 'success');
      setPrintModal(null);
    };
    var zip = new window.JSZip();
    if (isSheet) {
      var perPage = cols * rowsPerSheet;
      var mH = (pageW - cols * widthMm) / (cols + 1);
      var mV = (pageH - rowsPerSheet * heightMm) / (rowsPerSheet + 1);
      var sheetCount = 0;
      var labelCount = 0;
      var usedNames = {};
      try {
        itemsToPrint.forEach(function(item) {
          var count = printQtyOverride[item.id] || 1;
          if (count <= 0) return;
          var doc = new JsPDFCtor({ orientation: pageW >= pageH ? 'landscape' : 'portrait', unit: 'mm', format: [pageW, pageH] });
          for (var i = 0; i < count; i++) {
            var slot = i % perPage;
            if (i > 0 && slot === 0) doc.addPage([pageW, pageH], pageW >= pageH ? 'landscape' : 'portrait');
            var col = slot % cols;
            var row = Math.floor(slot / cols);
            renderLabelAt(doc, item, mH + col * (widthMm + mH), mV + row * (heightMm + mV));
          }
          var baseName = [(item.product_sku || item.sku || ''), (item.gtin || '')].filter(Boolean).join('_').replace(/[^A-Za-z0-9_-]/g, '_') || 'label';
          var fname = baseName + '.pdf';
          if (usedNames[fname]) { fname = baseName + '_' + (++usedNames[baseName]) + '.pdf'; }
          usedNames[fname] = 1; usedNames[baseName] = usedNames[baseName] || 1;
          zip.file(fname, doc.output('blob'));
          sheetCount++;
          labelCount += count;
        });
      } catch (e) { showToast('PDF build failed: ' + e.message, 'error'); return; }
      showToast('Building ZIP...', 'info');
      zip.generateAsync({ type: 'blob' }).then(function(b) { triggerDownload(b, 'Downloaded ' + sheetCount + ' PDF(s), ' + labelCount + ' labels'); })
        .catch(function(e) { showToast('ZIP failed: ' + e.message, 'error'); });
    } else {
      var skuCount = 0;
      var labelCount2 = 0;
      var usedNames2 = {};
      try {
        itemsToPrint.forEach(function(item) {
          var count = printQtyOverride[item.id] || 1;
          if (count <= 0) return;
          var doc = new JsPDFCtor({ orientation: widthMm >= heightMm ? 'landscape' : 'portrait', unit: 'mm', format: [widthMm, heightMm] });
          for (var i = 0; i < count; i++) {
            if (i > 0) doc.addPage([widthMm, heightMm], widthMm >= heightMm ? 'landscape' : 'portrait');
            renderLabelAt(doc, item, 0, 0);
          }
          var baseName = [(item.product_sku || item.sku || ''), (item.gtin || '')].filter(Boolean).join('_').replace(/[^A-Za-z0-9_-]/g, '_') || 'label';
          var fname = baseName + '.pdf';
          if (usedNames2[fname]) { fname = baseName + '_' + (++usedNames2[baseName]) + '.pdf'; }
          usedNames2[fname] = 1; usedNames2[baseName] = usedNames2[baseName] || 1;
          zip.file(fname, doc.output('blob'));
          skuCount++;
          labelCount2 += count;
        });
      } catch (e) { showToast('PDF build failed: ' + e.message, 'error'); return; }
      showToast('Building ZIP...', 'info');
      zip.generateAsync({ type: 'blob' }).then(function(b) { triggerDownload(b, 'Downloaded ' + skuCount + ' PDF(s), ' + labelCount2 + ' labels'); })
        .catch(function(e) { showToast('ZIP failed: ' + e.message, 'error'); });
    }
  };

  // ─── Progress bar ───
  var progressBar = h('div', { style: { marginBottom: 16 } },
    h('div', { style: { display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 } },
      h('span', { style: { color: C.textMuted } }, 'Packing Progress'),
      h('span', { style: { fontWeight: 600, color: pctPacked === 100 ? C.success : C.text } }, totalPacked + ' / ' + effectiveTotal + ' units (' + pctPacked + '%)')
    ),
    h('div', { style: { height: 6, background: C.border, borderRadius: 3 } },
      h('div', { style: { height: '100%', width: pctPacked + '%', background: pctPacked === 100 ? C.success : C.accent, borderRadius: 3, transition: 'width 0.3s' } })
    )
  );

  // Badge style helper
  var ps = function(bg, fg) { return { fontSize: 12, fontWeight: 600, padding: '3px 9px', borderRadius: 4, background: bg, color: fg, letterSpacing: '0.03em', whiteSpace: 'nowrap', display: 'inline-block' }; };

  // Item search filter helper
  var filterItems = function(list) {
    var q = itemSearch.toLowerCase().trim();
    if (!q) return list;
    return list.filter(function(item) {
      return (item.sku || '').toLowerCase().indexOf(q) >= 0 ||
        (item.gtin || '').toLowerCase().indexOf(q) >= 0 ||
        (item.barcode || '').toLowerCase().indexOf(q) >= 0 ||
        (item.vendor_title || '').toLowerCase().indexOf(q) >= 0 ||
        (item.vendor_name || '').toLowerCase().indexOf(q) >= 0 ||
        (item.vendor_sku || '').toLowerCase().indexOf(q) >= 0 ||
        (item.product_sku || '').toLowerCase().indexOf(q) >= 0;
    });
  };

  // Click-to-copy pill (mirrors FBA's copyBadge: whole pill clickable, toast on copy)
  var copyBadge = function(display, value, bg, fg) {
    return h('span', {
      onClick: function(e) {
        e.stopPropagation();
        var v = String(value);
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(v).then(function() { showToast('Copied ' + v, 'success'); }).catch(function() { showToast('Copy failed', 'error'); });
        }
      },
      onMouseEnter: function(e) { e.currentTarget.style.filter = 'brightness(1.25)'; },
      onMouseLeave: function(e) { e.currentTarget.style.filter = 'brightness(1)'; },
      title: 'Click to copy',
      style: Object.assign({}, ps(bg, fg), { cursor: 'pointer', transition: 'filter 0.15s' })
    }, display);
  };

  // Render item badges (GTIN/barcode instead of FNSKU/ASIN) — every pill copies on click
  var renderItemBadges = function(item) {
    return h('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 3 } },
      (item.product_sku || item.sku) && copyBadge('SKU: ' + (item.product_sku || item.sku), item.product_sku || item.sku, C.accent + '14', C.accent),
      item.vendor_sku && copyBadge('VSKU: ' + item.vendor_sku, item.vendor_sku, C.accent + '14', C.accent),
      item.gtin && copyBadge('GTIN: ' + item.gtin, item.gtin, C.accent + '14', C.accent),
      item.barcode && copyBadge('BARCODE: ' + item.barcode, item.barcode, C.accent + '14', C.accent)
    );
  };

  // ═══════ RENDER ═══════
  return h('div', null,
    progressBar,
    stepBar,

    // ─── ALL STEPS: Left/Right layout ───
    h('div', { style: { display: 'flex', gap: 16, alignItems: 'flex-start' } },
      // LEFT PANEL — wizard steps
      h('div', { style: { flex: 1, minWidth: 0 } },

        // ─── STEP 0: Tab bar (mirrors FBA's pack view, incl. Errored = Walmart-rejected) ───
        wizStep === 0 && (function() {
          var missingCount = items.filter(function(i) { return (i.quantity_missing || 0) > 0; }).length;
          var tabs = [
            { id: 'shipment', label: 'Shipment Items', count: availableItems.length, color: C.accent },
            { id: 'errored', label: 'Errored Items', count: erroredItems.length, color: '#EF4444' },
            { id: 'missing', label: 'Missing Items', count: missingCount, color: '#F87171' },
            { id: 'unexpected', label: 'Unexpected Items', count: unexpectedReceived.length, color: '#F59E0B' },
            { id: 'incomplete', label: 'Incomplete Items', count: availableItems.length, color: '#EC4899' },
            { id: 'activity', label: 'Activity Log', count: 0, color: '#8B5CF6' }
          ];
          return h('div', { style: { display: 'flex', gap: 2, marginBottom: 12, borderBottom: '1px solid ' + C.border } },
            tabs.map(function(t) {
              var active = packTab === t.id;
              return h('button', {
                key: t.id,
                onClick: function() { setPackTab(t.id); },
                style: {
                  padding: '8px 14px', fontSize: 14, fontWeight: active ? 700 : 500,
                  color: active ? t.color : C.textMuted, background: 'transparent', border: 'none',
                  borderBottom: active ? '2px solid ' + t.color : '2px solid transparent', marginBottom: -1,
                  cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6,
                  transition: 'color 0.15s, border-color 0.15s'
                }
              },
                t.label,
                t.count > 0 && h('span', { style: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 20, height: 20, padding: '0 6px', borderRadius: 10, background: active ? t.color : t.color + '22', color: active ? '#fff' : t.color, fontSize: 11, fontWeight: 700 } }, t.count)
              );
            })
          );
        })(),

        // ─── STEP 0: Overview — Items list ───
        wizStep === 0 && packTab === 'shipment' && h(Card, null,
          h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 } },
            h('div', { style: { fontSize: 15, fontWeight: 600 } }, 'Items Remaining to Pack'),
            h('div', { style: { display: 'flex', gap: 8 } },
              h(Btn, { size: 'sm', onClick: function() { setUnexpectedOpen(true); },
                style: { background: 'rgba(245,158,11,0.12)', color: '#F59E0B', border: '1px solid rgba(245,158,11,0.35)' } },
                h(LIcon, { name: 'alert-triangle', size: 14 }), ' Receive unexpected item'),
              items.filter(function(it) { return !walmartLabeled(it); }).length > 0 && h(Btn, { size: 'sm', onClick: function() { printGtinLabels(items.filter(function(it) { return !walmartLabeled(it); })); },
                style: { background: '#8B5CF6', color: '#fff', border: 'none' } },
                h(LIcon, { name: 'printer', size: 14 }), ' Print All GTIN Labels'),
              isCasePacked && cpPlanRows.length > 0 && h(Btn, { size: 'sm', disabled: cpGenerating, onClick: function() { setCpGenAllOpen(true); },
                style: { background: 'rgba(52,211,153,0.12)', color: '#34D399', border: '1px solid rgba(52,211,153,0.35)' } },
                h(LIcon, { name: 'zap', size: 14 }), cpGenerating ? ' Generating ' + cpProgress.current + ' / ' + cpProgress.total : ' Generate All Cases (' + cpPlanTotal + ')'),
              availableItems.length > 0 && h(Btn, { variant: 'primary', size: 'sm', onClick: function() {
                if (isCasePacked) {
                  setWizStep(1);
                  setCpSelectedItem(null); setCpUseSaved(true); setCpUnitsPerCase(''); setCpDims({ length: '', width: '', height: '', weight: '' }); setCpNumCases(''); setCpSaveDefault(true);
                  setItemSearch('');
                } else {
                  // If a saved draft exists for this user+shipment, offer Resume / Start-fresh
                  // instead of silently wiping it.
                  var rd = rawDraft && rawDraft.draft ? reconcileDraft(rawDraft.draft, availableItems) : null;
                  if (rd) {
                    setDraftPrompt({ data: rd, updated_at: rawDraft.updated_at });
                  } else {
                    setSelectedItems({}); setPackQtys({});
                    setNumCopies(1);
                    setItemSearch('');
                    setWizStep(1);
                  }
                }
              } },
                h(LIcon, { name: isCasePacked ? 'box' : 'plus', size: 14 }), isCasePacked ? ' Configure Cases' : ' Pack a Box')
            )
          ),
          // Confirm "Generate All Cases" — an in-app dialog (never window.confirm)
          isCasePacked && h(Modal, { open: cpGenAllOpen, onClose: function() { setCpGenAllOpen(false); }, title: 'Generate All Cases', width: 480 },
            h('div', { style: { fontSize: 13, color: C.text, lineHeight: 1.6 } },
              h('div', null, 'Create ' + cpPlanTotal + ' sealed carton' + (cpPlanTotal === 1 ? '' : 's') + ' for ' + cpPlanRows.length + ' SKU' + (cpPlanRows.length === 1 ? '' : 's') + ' from the saved supplier-pack configuration:'),
              h('ul', { style: { margin: '8px 0 0 18px', padding: 0, fontSize: 12, color: C.textMuted } },
                cpPlanRows.map(function(x) {
                  return h('li', { key: x.it.id }, x.it.sku + ' — ' + x.count + ' × ' + x.upc + ' units, ' + x.it.case_length + '" × ' + x.it.case_width + '" × ' + x.it.case_height + '"' + (parseFloat(x.it.case_weight) > 0 ? ', ' + x.it.case_weight + ' lb' : ''));
                })),
              h('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 8 } }, 'Each carton becomes a sealed box and receives against the PO, exactly like Configure Cases does per SKU.')
            ),
            h('div', { style: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 } },
              h(Btn, { onClick: function() { setCpGenAllOpen(false); } }, 'Cancel'),
              h(Btn, { variant: 'primary', onClick: generateAllCases }, h(LIcon, { name: 'zap', size: 14 }), ' Generate ' + cpPlanTotal + ' Cases')
            )
          ),
          availableItems.length === 0
            ? h('div', { style: { textAlign: 'center', padding: '20px 0' } },
                h(LIcon, { name: 'check-circle', size: 32, color: C.success }),
                h('div', { style: { fontSize: 14, fontWeight: 600, color: C.success, marginTop: 8 } },
                  items.some(function(i) { return (i.quantity_missing || 0) > 0; }) ? 'All items resolved!' : 'All items packed!'))
            : h('div', null,
                // Search bar
                h('div', { style: { marginBottom: 10 } },
                  h('input', { value: itemSearch, onChange: function(e) { setItemSearch(e.target.value); }, placeholder: 'Search by SKU, GTIN, barcode, product name, vendor...',
                    style: { width: '100%', padding: '8px 12px', fontSize: 12, border: '1px solid ' + C.border, borderRadius: 6, background: C.bg, color: C.text, outline: 'none', boxSizing: 'border-box' } })
                ),
                (function() {
                  var filtered = filterItems(availableItems);
                  return filtered.length === 0
                    ? h('div', { style: { textAlign: 'center', padding: '16px 0', color: C.textMuted, fontSize: 13 } }, 'No items match "' + itemSearch + '"')
                    : filtered.map(function(item) {
                      var packed = item.quantity_packed || 0;
                      var remaining = item.quantity - packed - (item.quantity_missing || 0);
                      var thumbUrl0 = (item.images && item.images.length) ? item.images[0] : item.image_1_url;
                      return h('div', { key: item.id, style: { display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 0', borderBottom: '1px solid ' + C.border + '33' } },
                        thumbUrl0
                          ? h('img', { src: thumbUrl0, onClick: function(e) { e.stopPropagation(); setLightbox({ images: item.images || [item.image_1_url], index: 0 }); }, style: { width: 80, height: 80, borderRadius: 6, objectFit: 'cover', border: '1px solid ' + C.border, flexShrink: 0, cursor: 'pointer' } })
                          : h('div', { style: { width: 80, height: 80, borderRadius: 6, background: C.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } }, h(LIcon, { name: 'package', size: 32, color: C.textDim })),
                        h('div', { style: { flex: 1, minWidth: 0 } },
                          h('div', { style: { fontSize: 16, fontWeight: 600, marginBottom: 4 } }, item.vendor_title || item.sku),
                          renderItemBadges(item),
                          item.vendor_name && h('div', { style: { display: 'flex', gap: 4, marginBottom: 3 } },
                            copyBadge(item.vendor_name, item.vendor_name, '#F59E0B' + '18', '#B45309')
                          )
                        ),
                        h('div', { style: { textAlign: 'right', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5 } },
                          h('div', { style: { fontSize: 16, fontWeight: 700, color: C.accent } }, remaining + ' remaining'),
                          packed > 0 && h('div', { style: { fontSize: 13, color: C.textMuted } }, packed + ' of ' + item.quantity + ' packed'),
                          h('button', { onClick: function(e) { e.stopPropagation(); setMissingNote(''); setMissingPending({ item: item }); },
                            style: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 9px', borderRadius: 4, border: '1px solid #F8717144', background: 'rgba(248,113,113,0.08)', color: '#F87171', cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit' } },
                            h(LIcon, { name: 'alert-triangle', size: 13 }), 'Mark Missing'),
                          (item.gtin || item.barcode) && !walmartLabeled(item) && h('button', { onClick: function(e) { e.stopPropagation(); printGtinLabels([item]); },
                            onMouseEnter: function(e) { e.currentTarget.style.background = '#7C3AED'; }, onMouseLeave: function(e) { e.currentTarget.style.background = '#8B5CF6'; },
                            style: { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 11px', borderRadius: 6, border: 'none', background: '#8B5CF6', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', transition: 'background 0.15s' } },
                            h(LIcon, { name: 'printer', size: 14 }), 'Print GTIN Label'),
                          walmartLabeled(item) && h('span', { title: 'Walmart Prep: labeling service selected — Walmart applies the GTIN labels for this SKU', style: { fontSize: 12, fontWeight: 600, color: '#8B5CF6', display: 'inline-flex', alignItems: 'center', gap: 4 } },
                            h(LIcon, { name: 'tag', size: 12 }), 'Labeled by Walmart')
                        )
                      );
                    });
                })()
              )
        ),

        // ─── Incomplete Items (bulk mark-missing) — Incomplete tab ───
        // ─── Errored Items tab — Walmart-rejected SKUs removed from this plan (FBA parity) ───
        wizStep === 0 && packTab === 'errored' && h(Card, null,
          h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 } },
            h(LIcon, { name: 'alert-octagon', size: 16, color: '#F87171' }),
            h('h3', { style: { fontSize: 14, fontWeight: 600, color: C.text, margin: 0 } }, 'Errored Items'),
            h('span', { style: { fontSize: 12, color: C.textMuted } }, 'Removed from this shipment (Walmart rejected them as not WFS-active). Vendor-direct: also removed from the PO and carried to the vendor’s draft PO. Warehouse: PO lines remain for disposition.')
          ),
          erroredItems.length === 0
            ? h('div', { style: { textAlign: 'center', padding: '28px 0', color: C.textDim, fontSize: 13 } }, 'No errored items on this plan')
            : h('div', { style: { display: 'flex', flexDirection: 'column', gap: 6 } },
                erroredItems.map(function(ei) {
                  return h('div', { key: ei.id, style: { display: 'flex', gap: 10, alignItems: 'center', padding: '10px 10px', borderRadius: 6, background: 'rgba(248,113,113,0.06)', border: '1px solid rgba(248,113,113,0.25)' } },
                    ei.image_url
                      ? h('img', { src: ei.image_url, onClick: function() { setLightbox({ images: [ei.image_url], index: 0 }); }, title: 'Click to enlarge',
                          style: { width: 64, height: 64, borderRadius: 4, objectFit: 'cover', flexShrink: 0, border: '1px solid ' + C.border, cursor: 'pointer' } })
                      : h('div', { style: { width: 64, height: 64, borderRadius: 4, background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } }, h(LIcon, { name: 'package', size: 26, color: C.textDim })),
                    h('div', { style: { flex: 1, minWidth: 0 } },
                      h('div', { style: { fontSize: 16, fontWeight: 600, color: C.text, marginBottom: 4 } }, ei.vendor_title || ei.sku),
                      h('div', { style: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' } },
                        copyBadge('SKU: ' + ei.sku, ei.sku, C.accent + '14', C.accent),
                        ei.barcode && copyBadge('UPC: ' + ei.barcode, ei.barcode, C.accent + '14', C.accent)
                      ),
                      (ei.reasons && ei.reasons.length > 0) && h('div', { style: { fontSize: 13, color: '#F87171', marginTop: 4 } }, ei.reasons.join(' · ')),
                      h('div', { style: { fontSize: 12, color: C.textDim, marginTop: 4 } }, 'Removed ' + new Date(ei.created_at).toLocaleString() + (ei.removed_by ? ' by ' + ei.removed_by : '') + (ei.source === 'walmart_rejection' ? ' — Walmart rejection' : ''))
                    ),
                    h('div', { style: { fontSize: 14, fontWeight: 700, color: '#F87171', whiteSpace: 'nowrap' } }, (ei.quantity || 0) + ' units')
                  );
                })
              )
        ),

        wizStep === 0 && packTab === 'incomplete' && availableItems.length > 0 && h(Card, null, (function() {
          var incomplete = availableItems;
          var selectedIds = Object.keys(bulkMissingSelected).filter(function(k) { return bulkMissingSelected[k]; });
          var allChecked = selectedIds.length === incomplete.length;
          var anyChecked = selectedIds.length > 0;
          var unitsAtStake = incomplete.reduce(function(s, it) {
            if (!bulkMissingSelected[it.id]) return s;
            return s + Math.max(0, it.quantity - (it.quantity_packed || 0) - (it.quantity_missing || 0));
          }, 0);
          return h('div', null,
            h('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 } },
              h('div', null,
                h('div', { style: { fontSize: 14, fontWeight: 600, color: C.text } }, 'Incomplete Items'),
                h('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 2 } }, incomplete.length + ' SKU' + (incomplete.length === 1 ? '' : 's') + ' with unpacked units remaining')
              ),
              h(Btn, { variant: 'primary', disabled: !anyChecked,
                onClick: function() {
                  var sel = incomplete.filter(function(it) { return bulkMissingSelected[it.id]; });
                  setBulkMissingConfirm({ items: sel, note: '' });
                },
                style: !anyChecked ? null : { background: '#DC2626', borderColor: '#DC2626' } },
                h(LIcon, { name: 'alert-triangle', size: 14 }), ' Mark Selected as Missing' + (anyChecked ? ' (' + selectedIds.length + ')' : ''))
            ),
            h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid ' + C.border } },
              h('button', { onClick: function() {
                  if (allChecked) { setBulkMissingSelected({}); }
                  else { var n = {}; incomplete.forEach(function(it) { n[it.id] = true; }); setBulkMissingSelected(n); }
                },
                style: { display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit' } },
                h('div', { style: { width: 20, height: 20, borderRadius: 4, border: '2px solid ' + (allChecked ? C.accent : C.textDim), background: allChecked ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' } },
                  allChecked && h(LIcon, { name: 'check', size: 13, color: '#fff' })),
                h('span', { style: { fontSize: 12, fontWeight: 600, color: C.text } }, 'Select All (' + incomplete.length + ')')
              ),
              anyChecked && h('span', { style: { fontSize: 12, color: C.textMuted } }, unitsAtStake + ' unit' + (unitsAtStake === 1 ? '' : 's') + ' will be marked missing')
            ),
            incomplete.map(function(it) {
              var checked = !!bulkMissingSelected[it.id];
              var remaining = Math.max(0, it.quantity - (it.quantity_packed || 0) - (it.quantity_missing || 0));
              var packed = it.quantity_packed || 0;
              var missed = it.quantity_missing || 0;
              var thumb = (it.images && it.images.length) ? it.images[0] : it.image_1_url;
              return h('div', { key: it.id, onClick: function() { setBulkMissingSelected(function(prev) { var n = Object.assign({}, prev); if (n[it.id]) delete n[it.id]; else n[it.id] = true; return n; }); },
                style: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 8px', marginBottom: 4, borderRadius: 6, cursor: 'pointer', background: checked ? '#F87171' + '10' : 'transparent', border: '1px solid ' + (checked ? '#F87171' + '50' : 'transparent'), transition: 'background 0.15s, border-color 0.15s' } },
                h('div', { style: { width: 20, height: 20, minWidth: 20, borderRadius: 4, border: '2px solid ' + (checked ? '#F87171' : C.textDim), background: checked ? '#F87171' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' } },
                  checked && h(LIcon, { name: 'check', size: 13, color: '#fff' })),
                thumb
                  ? h('img', { src: thumb, onClick: function(e) { e.stopPropagation(); setLightbox({ images: it.images || [it.image_1_url], index: 0 }); }, style: { width: 64, height: 64, borderRadius: 6, objectFit: 'cover', border: '1px solid ' + C.border, flexShrink: 0, cursor: 'pointer' } })
                  : h('div', { style: { width: 64, height: 64, borderRadius: 6, background: C.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } }, h(LIcon, { name: 'package', size: 26, color: C.textDim })),
                h('div', { style: { flex: 1, minWidth: 0 } },
                  h('div', { style: { fontSize: 16, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 4 } }, it.vendor_title || it.sku || '—'),
                  h('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' } },
                    (it.product_sku || it.sku) && copyBadge('SKU: ' + (it.product_sku || it.sku), it.product_sku || it.sku, C.accent + '14', C.accent),
                    it.gtin && copyBadge('GTIN: ' + it.gtin, it.gtin, C.accent + '14', C.accent),
                    (it.po_numbers || it.po_number) && copyBadge('PO# ' + (it.po_numbers || it.po_number), it.po_numbers || it.po_number, '#10B981' + '18', '#047857')
                  )
                ),
                h('div', { style: { textAlign: 'right', flexShrink: 0 } },
                  h('div', { style: { fontSize: 16, fontWeight: 700, color: '#F87171' } }, remaining + ' to pack'),
                  h('div', { style: { fontSize: 13, color: C.textMuted, marginTop: 2 } }, packed + ' of ' + it.quantity + ' packed' + (missed > 0 ? ' · ' + missed + ' missing' : ''))
                )
              );
            })
          );
        })()),

        // ─── Incomplete tab: empty state ───
        wizStep === 0 && packTab === 'incomplete' && availableItems.length === 0 &&
          h(Card, null,
            h('div', { style: { textAlign: 'center', padding: '30px 0', color: C.textMuted } },
              h(LIcon, { name: 'check-circle', size: 32, color: C.success }),
              h('div', { style: { fontSize: 14, fontWeight: 600, color: C.success, marginTop: 8 } }, 'No incomplete items'),
              h('div', { style: { fontSize: 12, marginTop: 4 } }, 'Every item in this shipment is either fully packed or already marked missing.')
            )
          ),

        // ─── Missing Items section (Missing tab) ───
        wizStep === 0 && packTab === 'missing' && (function() {
          var missingItems = items.filter(function(i) { return (i.quantity_missing || 0) > 0; });
          if (!missingItems.length) return null;
          return h(Card, null,
            h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 } },
              h('div', { style: { fontSize: 14, fontWeight: 600, color: '#F87171' } },
                h(LIcon, { name: 'alert-triangle', size: 15, color: '#F87171' }), ' Missing Items (' + missingItems.length + ')')
            ),
            missingItems.map(function(item) {
              var mThumb = (item.images && item.images.length) ? item.images[0] : item.image_1_url;
              return h('div', { key: 'missing-' + item.id, style: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid ' + C.border + '33' } },
                mThumb
                  ? h('img', { src: mThumb, onClick: function() { setLightbox({ images: item.images || [item.image_1_url], index: 0 }); }, title: 'Click to enlarge',
                      style: { width: 64, height: 64, borderRadius: 4, objectFit: 'cover', flexShrink: 0, border: '1px solid ' + C.border, cursor: 'pointer' } })
                  : h('div', { style: { width: 64, height: 64, borderRadius: 4, background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } }, h(LIcon, { name: 'package', size: 26, color: C.textDim })),
                h('div', { style: { flex: 1, minWidth: 0 } },
                  h('div', { style: { fontSize: 16, fontWeight: 600, marginBottom: 4 } }, item.vendor_title || item.sku),
                  renderItemBadges(item),
                  h('div', { style: { fontSize: 13, color: C.textMuted } },
                    (item.quantity_missing || 0) + ' missing' +
                    ((item.quantity_packed || 0) > 0 ? ', ' + (item.quantity_packed || 0) + ' packed' : '') +
                    ' of ' + item.quantity + ' total')
                ),
                h('button', { onClick: function() { unmarkMissing(item); },
                  onMouseEnter: function(e) { e.currentTarget.style.background = C.accent + '14'; e.currentTarget.style.borderColor = C.accent; e.currentTarget.style.color = C.accent; },
                  onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.textMuted; },
                  style: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 11px', borderRadius: 4, border: '1px solid ' + C.border, background: 'transparent', color: C.textMuted, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', transition: 'background 0.15s, border-color 0.15s, color 0.15s' } },
                  h(LIcon, { name: 'rotate-ccw', size: 12 }), 'Undo')
              );
            })
          );
        })(),

        // ─── Missing tab: empty state ───
        wizStep === 0 && packTab === 'missing' && !items.some(function(i) { return (i.quantity_missing || 0) > 0; }) &&
          h(Card, null,
            h('div', { style: { textAlign: 'center', padding: '30px 0', color: C.textMuted } },
              h(LIcon, { name: 'check-circle', size: 32, color: C.success }),
              h('div', { style: { fontSize: 15, fontWeight: 600, color: C.success, marginTop: 8 } }, 'No missing items'),
              h('div', { style: { fontSize: 13, marginTop: 4 } }, 'Items marked missing (from the Shipment or Incomplete tabs) will appear here.')
            )
          ),

        // ─── Received Unexpected Items section (Unexpected tab) ───
        wizStep === 0 && packTab === 'unexpected' && unexpectedReceived.length > 0 && (function() {
          var ps = function(bg, fg) { return { fontSize: 12, fontWeight: 600, padding: '3px 9px', borderRadius: 4, background: bg, color: fg, letterSpacing: '0.03em', whiteSpace: 'nowrap', display: 'inline-block' }; };
          return h(Card, null,
            h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 } },
              h('div', { style: { fontSize: 14, fontWeight: 600, color: '#F59E0B' } },
                h(LIcon, { name: 'alert-triangle', size: 15, color: '#F59E0B' }), ' Received Unexpected Items (' + unexpectedReceived.length + ')')
            ),
            unexpectedReceived.map(function(u) {
              var t = new Date(u.received_at);
              var timeStr = t.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' + t.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
              var uImgs = (u.images && u.images.length) ? u.images : (u.image_1_url ? [u.image_1_url] : []);
              return h('div', { key: 'unexp-' + u.log_id, style: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid ' + C.border + '33' } },
                uImgs.length > 0
                  ? h('img', { src: uImgs[0],
                      onClick: function() { setUnexpLightbox({ images: uImgs, index: 0 }); },
                      title: uImgs.length > 1 ? 'Click to view ' + uImgs.length + ' images' : 'Click to enlarge',
                      style: { width: 64, height: 64, borderRadius: 4, objectFit: 'cover', flexShrink: 0, border: '1px solid ' + C.border, cursor: 'pointer' } })
                  : h('div', { style: { width: 64, height: 64, borderRadius: 4, background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } }, h(LIcon, { name: 'package', size: 26, color: C.textDim })),
                h('div', { style: { flex: 1, minWidth: 0 } },
                  h('div', { style: { fontSize: 16, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 4 } }, u.vendor_title || u.sku),
                  h('div', { style: { display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 4 } },
                    u.sku && copyBadge('SKU: ' + u.sku, u.sku, C.accent + '14', C.accent),
                    u.vendor_sku && copyBadge('VSKU: ' + u.vendor_sku, u.vendor_sku, C.accent + '14', C.accent),
                    u.barcode && copyBadge('UPC: ' + u.barcode, u.barcode, C.accent + '14', C.accent)
                  ),
                  h('div', { style: { fontSize: 13, color: C.textMuted } },
                    '+' + u.qty_received + ' → Bin ',
                    h('span', { style: { fontFamily: 'inherit', fontWeight: 600, color: C.accent } }, u.bin_location || '—'),
                    ' · PO ', h('span', { style: { fontFamily: 'inherit' } }, u.po_number),
                    ' · ', u.received_by, ' · ', timeStr
                  ),
                  u.notes && h('div', { style: { fontSize: 13, color: C.textMuted, fontStyle: 'italic', marginTop: 2 } }, 'Notes: ' + u.notes)
                ),
                h('button', { onClick: function() { undoUnexpected(u.log_id); },
                  onMouseEnter: function(e) { e.currentTarget.style.background = C.danger + '14'; e.currentTarget.style.borderColor = C.danger; e.currentTarget.style.color = C.danger; },
                  onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.textMuted; },
                  style: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 11px', borderRadius: 4, border: '1px solid ' + C.border, background: 'transparent', color: C.textMuted, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', transition: 'background 0.15s, border-color 0.15s, color 0.15s', flexShrink: 0 } },
                  h(LIcon, { name: 'rotate-ccw', size: 12 }), 'Undo')
              );
            })
          );
        })(),

        // ─── Unexpected tab: empty state ───
        wizStep === 0 && packTab === 'unexpected' && unexpectedReceived.length === 0 &&
          h(Card, null,
            h('div', { style: { textAlign: 'center', padding: '30px 0', color: C.textMuted } },
              h(LIcon, { name: 'package', size: 32, color: C.textDim }),
              h('div', { style: { fontSize: 14, fontWeight: 600, color: C.text, marginTop: 8 } }, 'No unexpected items'),
              h('div', { style: { fontSize: 12, marginTop: 4 } }, 'Items received via "Receive unexpected item" will appear here.')
            )
          ),

        // ─── Activity Log tab (mirrors FBA) ───
        wizStep === 0 && packTab === 'activity' && h(Card, null,
          h('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 } },
            h('div', { style: { fontSize: 16, fontWeight: 600, color: C.text } }, 'Shipment Activity Log'),
            h('button', { onClick: loadActivity, disabled: activityLoading,
              style: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 5, border: '1px solid ' + C.border, background: 'transparent', color: C.text, cursor: activityLoading ? 'default' : 'pointer', fontSize: 12, fontFamily: 'inherit' } },
              h(LIcon, { name: 'refresh-cw', size: 12, spin: activityLoading }), 'Refresh')
          ),
          activityLog.length > 0 && h('div', { style: { marginBottom: 10, position: 'relative' } },
            h('input', { value: activitySearch, onChange: function(e) { setActivitySearch(e.target.value); }, placeholder: 'Search activity by action, details, user...',
              style: { width: '100%', padding: '8px 34px 8px 12px', fontSize: 13, border: '1px solid ' + C.border, borderRadius: 6, background: C.bg, color: C.text, outline: 'none', boxSizing: 'border-box' } }),
            activitySearch && h('button', { onClick: function() { setActivitySearch(''); }, title: 'Clear search',
              style: { position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', width: 22, height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', border: 'none', background: C.border, color: C.text, cursor: 'pointer', padding: 0, fontFamily: 'inherit' } },
              h(LIcon, { name: 'x', size: 12 }))
          ),
          activityLoading && activityLog.length === 0
            ? h('div', { style: { textAlign: 'center', padding: '30px 0', color: C.textMuted, fontSize: 13 } }, 'Loading…')
            : activityLog.length === 0
              ? h('div', { style: { textAlign: 'center', padding: '30px 0', color: C.textMuted } },
                  h(LIcon, { name: 'activity', size: 32, color: C.textDim }),
                  h('div', { style: { fontSize: 14, fontWeight: 600, color: C.text, marginTop: 8 } }, 'No activity yet'),
                  h('div', { style: { fontSize: 12, marginTop: 4 } }, 'Box, item, and shipment events for this plan will appear here.'))
              : (function() {
                  var aq = activitySearch.toLowerCase().trim();
                  var shown = !aq ? activityLog : activityLog.filter(function(a) {
                    var act = (a.action || '').toLowerCase();
                    // Match the raw action ("wfs_item_packed") and the humanized badge text ("item packed").
                    return act.indexOf(aq) !== -1 ||
                      act.replace(/^wfs_/, '').replace(/_/g, ' ').indexOf(aq) !== -1 ||
                      (a.details || '').toLowerCase().indexOf(aq) !== -1 ||
                      (a.username || '').toLowerCase().indexOf(aq) !== -1;
                  });
                  if (shown.length === 0) return h('div', { style: { textAlign: 'center', padding: '30px 0', color: C.textMuted, fontSize: 13 } }, 'No activity matches "' + activitySearch + '"');
                  var byDate = {};
                  shown.forEach(function(a) {
                    var d = new Date(a.created_at);
                    var key = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
                    (byDate[key] = byDate[key] || []).push(a);
                  });
                  var keys = Object.keys(byDate).sort().reverse();
                  return h('div', null, keys.map(function(k) {
                    var parts = k.split('-');
                    var headerDate = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
                    return h('div', { key: k, style: { marginBottom: 16 } },
                      h('div', { style: { fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8, paddingBottom: 4, borderBottom: '1px solid ' + C.border } },
                        headerDate.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })),
                      byDate[k].map(function(a) {
                        var act = (a.action || '');
                        var color = act.indexOf('delete') >= 0 ? '#F87171'
                          : act.indexOf('missing') >= 0 ? '#F59E0B'
                          : act.indexOf('duplicat') >= 0 ? '#06B6D4'
                          : (act.indexOf('packed') >= 0 || act.indexOf('received') >= 0 || act.indexOf('box') >= 0) ? C.success
                          : '#8B5CF6';
                        var d = new Date(a.created_at);
                        var hhmm = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
                        return h('div', { key: a.id, style: { display: 'flex', gap: 10, padding: '10px 0', borderBottom: '1px solid ' + C.border + '33' } },
                          h('div', { style: { width: 8, height: 8, borderRadius: '50%', background: color, marginTop: 6, flexShrink: 0 } }),
                          h('div', { style: { flex: 1, minWidth: 0 } },
                            h('div', { style: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 } },
                              h('span', { style: { fontSize: 10, fontWeight: 700, color: color, padding: '2px 7px', borderRadius: 4, background: color + '14', letterSpacing: '0.04em', textTransform: 'uppercase' } }, act.replace(/^wfs_/, '').replace(/_/g, ' ')),
                              h('span', { style: { fontSize: 11, color: C.textMuted, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }, title: d.toLocaleString() }, hhmm)
                            ),
                            h('div', { style: { fontSize: 13, color: C.text, marginTop: 4 } }, a.details || '—'),
                            a.username && h('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 2 } }, 'by ' + a.username)
                          )
                        );
                      })
                    );
                  }));
                })()
        ),

        // ─── STEP 1 (Case Packed): Select single SKU ───
        wizStep === 1 && isCasePacked && h(Card, null,
          h('div', { style: { fontSize: 14, fontWeight: 600, marginBottom: 4 } }, 'Step 1: Select SKU'),
          h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 10 } }, 'Choose one SKU to configure cases for. Each SKU is packed independently.'),
          h('div', { style: { display: 'flex', justifyContent: 'space-between', marginBottom: 12 } },
            h(Btn, { onClick: function() { setWizStep(0); } }, h(LIcon, { name: 'arrow-left', size: 14 }), ' Back'),
            h(Btn, { variant: 'primary', disabled: !cpSelectedItem, onClick: function() {
              var item = availableItems.find(function(i) { return i.id === cpSelectedItem; });
              if (item && item.case_qty > 0 && item.case_length > 0) {
                setCpUseSaved(true);
                setCpUnitsPerCase(String(item.case_qty));
                setCpDims({ length: String(item.case_length || ''), width: String(item.case_width || ''), height: String(item.case_height || ''), weight: String(item.case_weight || '') });
              } else {
                setCpUseSaved(false);
                setCpUnitsPerCase('');
                setCpDims({ length: '', width: '', height: '', weight: '' });
              }
              setCpNumCases('');
              setCpSaveDefault(true);
              setWizStep(2);
            } }, 'Next: Case Config ', h(LIcon, { name: 'arrow-right', size: 14 }))
          ),
          h('div', { style: { marginBottom: 10 } },
            h('input', { value: itemSearch, onChange: function(e) { setItemSearch(e.target.value); }, placeholder: 'Search by SKU, GTIN, barcode, product name...',
              style: { width: '100%', padding: '8px 12px', fontSize: 12, border: '1px solid ' + C.border, borderRadius: 6, background: C.bg, color: C.text, outline: 'none', boxSizing: 'border-box' } })
          ),
          (function() {
            var filtered = filterItems(availableItems);
            return filtered.length === 0
              ? h('div', { style: { textAlign: 'center', padding: '16px 0', color: C.textMuted, fontSize: 12 } }, 'No items match "' + itemSearch + '"')
              : filtered.map(function(item) {
                  var selected = cpSelectedItem === item.id;
                  var remaining = item.quantity - (item.quantity_packed || 0) - (item.quantity_missing || 0);
                  var hasSaved = item.case_qty > 0 && item.case_length > 0;
                  var thumbUrl = (item.images && item.images.length) ? item.images[0] : item.image_1_url;
                  return h('div', { key: item.id, onClick: function() { setCpSelectedItem(item.id); },
                    style: { display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 8px', marginBottom: 4, borderRadius: 8, cursor: 'pointer', background: selected ? C.accent + '08' : 'transparent', border: '1px solid ' + (selected ? C.accent + '40' : 'transparent'), transition: 'all 0.15s' } },
                    h('div', { style: { width: 20, height: 20, minWidth: 20, borderRadius: '50%', border: '2px solid ' + (selected ? C.accent : C.textDim), background: selected ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s', marginTop: 2 } },
                      selected && h('div', { style: { width: 8, height: 8, borderRadius: '50%', background: '#fff' } })),
                    thumbUrl
                      ? h('img', { src: thumbUrl, style: { width: 48, height: 48, borderRadius: 6, objectFit: 'cover', border: '1px solid ' + C.border, flexShrink: 0 } })
                      : h('div', { style: { width: 48, height: 48, borderRadius: 6, background: C.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } }, h(LIcon, { name: 'package', size: 20, color: C.textDim })),
                    h('div', { style: { flex: 1, minWidth: 0 } },
                      h('div', { style: { fontSize: 13, fontWeight: 600, marginBottom: 4 } }, item.vendor_title || item.sku),
                      h('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 3 } },
                        (item.product_sku || item.sku) && h('span', { style: ps(C.accent + '14', C.accent) }, 'SKU: ' + (item.product_sku || item.sku)),
                        item.gtin && h('span', { style: ps(C.accent + '14', C.accent) }, 'GTIN: ' + item.gtin),
                        item.barcode && h('span', { style: ps(C.accent + '14', C.accent) }, 'BARCODE: ' + item.barcode)
                      ),
                      hasSaved && h('div', { style: { fontSize: 10, color: C.success, fontWeight: 500 } },
                        h(LIcon, { name: 'check-circle', size: 11, style: { marginRight: 3, verticalAlign: 'middle' } }),
                        'Saved: ' + item.case_qty + ' units/case, ' + item.case_length + '" x ' + item.case_width + '" x ' + item.case_height + '"' + (item.case_weight ? ', ' + item.case_weight + ' lbs' : ''))
                    ),
                    h('div', { style: { textAlign: 'right', fontSize: 13, fontWeight: 700, color: C.accent, flexShrink: 0 } }, remaining + ' units')
                  );
                });
          })()
        ),

        // ─── STEP 1 (Individual): Select Items for this box ───
        wizStep === 1 && !isCasePacked && h(Card, null,
          h('div', { style: { fontSize: 14, fontWeight: 600, marginBottom: 4 } }, 'Step 1: Select Items for This Box'),
          h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 10 } }, 'Choose which items to pack in this box. You can select all or pick specific items.'),

          h('div', { style: { display: 'flex', gap: 8, marginBottom: 12 } },
            h(Btn, { onClick: function() { setWizStep(0); } }, h(LIcon, { name: 'arrow-left', size: 14 }), ' Back'),
            h(Btn, { onClick: cancelWizard, style: { background: 'rgba(248,113,113,0.1)', color: '#F87171', border: '1px solid rgba(248,113,113,0.3)' } }, h(LIcon, { name: 'x', size: 14 }), ' Cancel'),
            h(Btn, { variant: 'primary', disabled: selectedCount === 0, onClick: function() {
              // Preserve only quantities the user actually entered (or Add All pre-filled) —
              // defaulting the rest to max remaining silently packed full quantities.
              var q = {};
              selectedItemsList.forEach(function(i) {
                var rem = i.quantity - (i.quantity_packed || 0) - (i.quantity_missing || 0);
                var entered = parseInt(packQtys[i.id], 10);
                if (entered > 0) q[i.id] = Math.min(entered, rem);
              });
              setPackQtys(q);
              setWizStep(2);
            } }, 'Next: Verify Items ', h(LIcon, { name: 'arrow-right', size: 14 }))
          ),

          h('div', { style: { marginBottom: 10 } },
            h('input', { value: itemSearch, onChange: function(e) { setItemSearch(e.target.value); }, placeholder: 'Search by SKU, GTIN, barcode, product name, vendor...',
              style: { width: '100%', padding: '8px 12px', fontSize: 12, border: '1px solid ' + C.border, borderRadius: 6, background: C.bg, color: C.text, outline: 'none', boxSizing: 'border-box' } })
          ),

          // Select all toggle + Add All (pre-fills qtys to max remaining)
          h('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 12, paddingBottom: 8, paddingRight: 8, borderBottom: '1px solid ' + C.border } },
            h('button', { onClick: toggleAll, style: { display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit' } },
              h('div', { style: { width: 20, height: 20, borderRadius: 4, border: '2px solid ' + (selectedCount === availableItems.length ? C.accent : C.textDim), background: selectedCount === availableItems.length ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' } },
                selectedCount === availableItems.length && h(LIcon, { name: 'check', size: 13, color: '#fff' })),
              h('span', { style: { fontSize: 12, fontWeight: 600, color: C.text } }, 'Select All (' + availableItems.length + ' items)')
            ),
            // Add All — selects every available item and pre-fills each qty to its max remaining.
            availableItems.length > 0 && h('div', { style: { width: 110, display: 'flex', justifyContent: 'center', flexShrink: 0 } },
              h('button', {
                onClick: function() {
                  var sel = {}; var qtys = {};
                  availableItems.forEach(function(it) {
                    sel[it.id] = true;
                    var remaining = it.quantity - (it.quantity_packed || 0) - (it.quantity_missing || 0);
                    if (remaining > 0) qtys[it.id] = remaining;
                  });
                  setSelectedItems(sel);
                  setPackQtys(qtys);
                },
                title: 'Select every available item and fill its quantity to the max remaining',
                onMouseEnter: function(e) { e.currentTarget.style.background = C.accent + '22'; e.currentTarget.style.borderColor = C.accent; },
                onMouseLeave: function(e) { e.currentTarget.style.background = C.accent + '14'; e.currentTarget.style.borderColor = C.accent + '55'; },
                style: { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 6, border: '1px solid ' + C.accent + '55', background: C.accent + '14', color: C.accent, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', letterSpacing: '0.02em', transition: 'background 0.15s, border-color 0.15s' } },
                h(LIcon, { name: 'check-circle', size: 13 }), 'Add All')
            )
          ),

          // Item list (filtered)
          (function() {
            var filtered = filterItems(availableItems);
            return filtered.length === 0
              ? h('div', { style: { textAlign: 'center', padding: '16px 0', color: C.textMuted, fontSize: 12 } }, 'No items match "' + itemSearch + '"')
              : filtered.map(function(item) {
                var checked = !!selectedItems[item.id];
                var remaining = item.quantity - (item.quantity_packed || 0) - (item.quantity_missing || 0);
                var thumbUrl = (item.images && item.images.length) ? item.images[0] : item.image_1_url;
                return h('div', { key: item.id,
                  onMouseDown: function(e) { if (e.shiftKey) e.preventDefault(); },
                  onClick: function(e) { packRowClick(e, item, filtered); },
                  style: { display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 8px', marginBottom: 4, borderRadius: 8, cursor: 'pointer', background: checked ? C.accent + '08' : 'transparent', border: '1px solid ' + (checked ? C.accent + '40' : 'transparent'), transition: 'all 0.15s' } },
                  h('div', { onClick: function(e) { e.stopPropagation(); if (e.shiftKey) { packRowClick(e, item, filtered); } else { toggleItem(item.id); lastPackClickRef.current = item.id; } }, title: checked ? 'Uncheck to remove from this box' : 'Check to add to this box',
                    style: { width: 20, height: 20, minWidth: 20, borderRadius: 4, border: '2px solid ' + (checked ? C.accent : C.textDim), background: checked ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s', marginTop: 2, cursor: 'pointer' } },
                    checked && h(LIcon, { name: 'check', size: 13, color: '#fff' })),
                  thumbUrl
                    ? h('img', { src: thumbUrl, onClick: function(e) { e.stopPropagation(); setLightbox({ images: item.images || [item.image_1_url], index: 0 }); }, style: { width: 48, height: 48, borderRadius: 6, objectFit: 'cover', border: '1px solid ' + C.border, flexShrink: 0, cursor: 'pointer' } })
                    : h('div', { style: { width: 48, height: 48, borderRadius: 6, background: C.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } }, h(LIcon, { name: 'package', size: 20, color: C.textDim })),
                  h('div', { style: { flex: 1, minWidth: 0 } },
                    h('div', { style: { fontSize: 13, fontWeight: 600, marginBottom: 4 } }, item.vendor_title || item.sku),
                    renderItemBadges(item),
                    item.vendor_name && h('div', { style: { display: 'flex', gap: 4, marginBottom: 3 } },
                      copyBadge(item.vendor_name, item.vendor_name, '#F59E0B' + '18', '#B45309')
                    )
                  ),
                  (function() {
                    var maxQty = remaining;
                    var qtyVal = packQtys[item.id] != null ? packQtys[item.id] : '';
                    var qtyParsed = parseInt(qtyVal, 10);
                    var qtyValid = qtyVal !== '' && qtyParsed > 0 && qtyParsed <= maxQty;
                    var remainingAvail = Math.max(0, maxQty - (isNaN(qtyParsed) ? 0 : qtyParsed));
                    return h('div', { onClick: function(e) { e.stopPropagation(); }, style: { width: 104, textAlign: 'center', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 4 } },
                      h('div', { style: { fontSize: 13, fontWeight: 700, color: C.accent } }, remainingAvail + ' remaining'),
                      h('input', {
                        type: 'number', min: 0, max: maxQty, value: qtyVal, placeholder: '0',
                        onClick: function(e) { e.stopPropagation(); },
                        onChange: function(e) {
                          var raw = e.target.value;
                          if (raw === '') { setPackQtys(function(q) { var nq = Object.assign({}, q); delete nq[item.id]; return nq; }); return; }
                          var n = parseInt(raw, 10);
                          if (isNaN(n) || n < 0) n = 0;
                          if (n > maxQty) n = maxQty;
                          setPackQtys(function(q) { var nq = Object.assign({}, q); nq[item.id] = n; return nq; });
                          if (n > 0 && !selectedItems[item.id]) { setSelectedItems(function(prev) { var ns = Object.assign({}, prev); ns[item.id] = true; return ns; }); }
                        },
                        style: { width: '100%', padding: '8px 6px', fontSize: 16, fontWeight: 700, textAlign: 'center', border: '2px solid ' + (qtyValid ? C.success : C.border), borderRadius: 8, background: C.bg, color: C.text, outline: 'none', boxSizing: 'border-box' }
                      }),
                      h('div', { style: { fontSize: 9, color: C.textMuted } }, 'units')
                    );
                  })()
                );
              });
          })()
        ),

        // ─── STEP 2 (Case Packed): Case Configuration ───
        wizStep === 2 && isCasePacked && (function() {
          var cpItem = availableItems.find(function(i) { return i.id === cpSelectedItem; });
          if (!cpItem) return null;
          var cpRemaining = cpItem.quantity - (cpItem.quantity_packed || 0) - (cpItem.quantity_missing || 0);
          var hasSaved = cpItem.case_qty > 0 && cpItem.case_length > 0;
          var upc = parseInt(cpUnitsPerCase) || 0;
          var nc = parseInt(cpNumCases) || 0;
          var totalUnitsCalc = upc * nc;
          var maxCases = upc > 0 ? Math.floor(cpRemaining / upc) : 0;
          var dimsValid = parseFloat(cpDims.length) > 0 && parseFloat(cpDims.width) > 0 && parseFloat(cpDims.height) > 0;
          var configValid = upc > 0 && nc > 0 && nc <= maxCases && dimsValid;
          return h(Card, null,
            h('div', { style: { fontSize: 14, fontWeight: 600, marginBottom: 4 } }, 'Step 2: Case Configuration'),
            h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 10 } },
              'Configure case packing for: ', h('strong', null, cpItem.vendor_title || cpItem.sku)),
            h('div', { style: { display: 'flex', justifyContent: 'space-between', marginBottom: 14 } },
              h(Btn, { onClick: function() { setWizStep(1); } }, h(LIcon, { name: 'arrow-left', size: 14 }), ' Back'),
              h(Btn, { variant: 'primary', disabled: !configValid, onClick: function() { setWizStep(3); } }, 'Next: Review ', h(LIcon, { name: 'arrow-right', size: 14 }))
            ),
            // Toggle saved vs custom
            hasSaved && h('div', { style: { display: 'flex', gap: 8, marginBottom: 16 } },
              h('button', { onClick: function() {
                  setCpUseSaved(true);
                  setCpUnitsPerCase(String(cpItem.case_qty));
                  setCpDims({ length: String(cpItem.case_length || ''), width: String(cpItem.case_width || ''), height: String(cpItem.case_height || ''), weight: String(cpItem.case_weight || '') });
                },
                style: { flex: 1, padding: '10px 14px', borderRadius: 8, border: '2px solid ' + (cpUseSaved ? C.accent : C.border), background: cpUseSaved ? C.accent + '10' : 'transparent', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' } },
                h('div', { style: { fontSize: 12, fontWeight: 600, color: cpUseSaved ? C.accent : C.text, marginBottom: 4 } },
                  h(LIcon, { name: 'database', size: 13, style: { marginRight: 4, verticalAlign: 'middle' } }), 'Use Saved Case Pack'),
                h('div', { style: { fontSize: 11, color: C.textMuted } },
                  cpItem.case_qty + ' units/case, ' + cpItem.case_length + '" x ' + cpItem.case_width + '" x ' + cpItem.case_height + '"' + (cpItem.case_weight ? ', ' + cpItem.case_weight + ' lbs' : ''))
              ),
              h('button', { onClick: function() {
                  setCpUseSaved(false);
                  setCpUnitsPerCase('');
                  setCpDims({ length: '', width: '', height: '', weight: '' });
                },
                style: { flex: 1, padding: '10px 14px', borderRadius: 8, border: '2px solid ' + (!cpUseSaved ? C.accent : C.border), background: !cpUseSaved ? C.accent + '10' : 'transparent', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' } },
                h('div', { style: { fontSize: 12, fontWeight: 600, color: !cpUseSaved ? C.accent : C.text } },
                  h(LIcon, { name: 'edit-2', size: 13, style: { marginRight: 4, verticalAlign: 'middle' } }), 'Custom Entry')
              )
            ),
            // Units per case + number of cases
            h('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 } },
              h('div', null,
                h('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, display: 'block', marginBottom: 4, textTransform: 'uppercase' } }, 'Units per Case'),
                h('input', { type: 'number', min: 1, value: cpUnitsPerCase, disabled: cpUseSaved && hasSaved,
                  onChange: function(e) { setCpUnitsPerCase(e.target.value); },
                  style: { width: '100%', padding: '10px 12px', fontSize: 16, fontWeight: 700, border: '2px solid ' + (upc > 0 ? C.success : C.border), borderRadius: 8, background: cpUseSaved && hasSaved ? C.surface : C.bg, color: C.text, outline: 'none', boxSizing: 'border-box' } })
              ),
              h('div', null,
                h('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, display: 'block', marginBottom: 4, textTransform: 'uppercase' } }, 'Number of Cases'),
                h('input', { type: 'number', min: 1, max: maxCases || undefined, value: cpNumCases,
                  onChange: function(e) { setCpNumCases(e.target.value); },
                  style: { width: '100%', padding: '10px 12px', fontSize: 16, fontWeight: 700, border: '2px solid ' + (nc > 0 && nc <= maxCases ? C.success : nc > maxCases ? C.danger : C.border), borderRadius: 8, background: C.bg, color: C.text, outline: 'none', boxSizing: 'border-box' } }),
                upc > 0 && h('div', { style: { fontSize: 10, color: C.textMuted, marginTop: 4 } }, 'Max: ' + maxCases + ' cases (' + cpRemaining + ' units remaining)')
              )
            ),
            // Calculated display
            upc > 0 && nc > 0 && h('div', { style: { padding: '10px 14px', borderRadius: 8, marginBottom: 16, background: totalUnitsCalc <= cpRemaining ? C.accent + '10' : C.danger + '15', border: '1px solid ' + (totalUnitsCalc <= cpRemaining ? C.accent + '30' : C.danger + '40') } },
              h('div', { style: { fontSize: 16, fontWeight: 700, color: totalUnitsCalc <= cpRemaining ? C.accent : C.danger, textAlign: 'center' } },
                nc + ' cases x ' + upc + ' units = ' + totalUnitsCalc + ' units'),
              totalUnitsCalc > cpRemaining && h('div', { style: { fontSize: 11, color: C.danger, textAlign: 'center', marginTop: 4 } },
                'Exceeds remaining quantity (' + cpRemaining + ' units)')
            ),
            // Walmart mismatch warning
            cpItem.inner_pack_qty && upc > 0 && upc !== cpItem.inner_pack_qty && h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', marginBottom: 16, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 8, fontSize: 12, color: '#F59E0B' } },
              h(LIcon, { name: 'alert-triangle', size: 16 }),
              h('div', null,
                h('div', { style: { fontWeight: 600 } }, 'Walmart case mismatch'),
                h('div', { style: { fontSize: 11, marginTop: 2 } }, 'Submitted to Walmart as ' + cpItem.inner_pack_qty + ' units/case (' + cpItem.vendor_pack_qty + ' cases) — you are packing ' + upc + ' units/case'))
            ),
            cpItem.inner_pack_qty && nc > 0 && nc !== cpItem.vendor_pack_qty && upc === cpItem.inner_pack_qty && h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', marginBottom: 16, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 8, fontSize: 12, color: '#F59E0B' } },
              h(LIcon, { name: 'alert-triangle', size: 16 }),
              h('div', null,
                h('div', { style: { fontWeight: 600 } }, 'Walmart case count mismatch'),
                h('div', { style: { fontSize: 11, marginTop: 2 } }, 'Submitted to Walmart as ' + cpItem.vendor_pack_qty + ' cases — you are packing ' + nc + ' cases'))
            ),
            // Case dimensions
            h('div', { style: { marginBottom: 16 } },
              h('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, display: 'block', marginBottom: 6, textTransform: 'uppercase' } }, 'Case Dimensions'),
              h('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 } },
                h(Inp, { label: 'Length (in)', type: 'number', step: '0.1', value: cpDims.length, disabled: cpUseSaved && hasSaved,
                  onChange: function(e) { setCpDims(function(d) { return Object.assign({}, d, { length: e.target.value }); }); } }),
                h(Inp, { label: 'Width (in)', type: 'number', step: '0.1', value: cpDims.width, disabled: cpUseSaved && hasSaved,
                  onChange: function(e) { setCpDims(function(d) { return Object.assign({}, d, { width: e.target.value }); }); } }),
                h(Inp, { label: 'Height (in)', type: 'number', step: '0.1', value: cpDims.height, disabled: cpUseSaved && hasSaved,
                  onChange: function(e) { setCpDims(function(d) { return Object.assign({}, d, { height: e.target.value }); }); } }),
                h(Inp, { label: 'Weight (lbs)', type: 'number', step: '0.1', value: cpDims.weight, disabled: cpUseSaved && hasSaved,
                  onChange: function(e) { setCpDims(function(d) { return Object.assign({}, d, { weight: e.target.value }); }); } })
              )
            ),
            // Save as default checkbox
            !cpUseSaved && h('label', { style: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: C.text, cursor: 'pointer', marginBottom: 8 } },
              h('input', { type: 'checkbox', checked: cpSaveDefault, onChange: function(e) { setCpSaveDefault(e.target.checked); }, style: { width: 14, height: 14, accentColor: C.accent, cursor: 'pointer' } }),
              'Save as default case pack for this product')
          );
        })(),

        // ─── STEP 2 (Individual): Verify & enter quantities ───
        wizStep === 2 && !isCasePacked && h(Card, null,
          h('div', { style: { fontSize: 14, fontWeight: 600, marginBottom: 4 } }, 'Step 2: Verify Items & Enter Quantities'),
          h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 10 } }, 'Confirm the quantity for each item going into this box.'),

          h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 } },
            h('div', { style: { display: 'flex', gap: 8 } },
              h(Btn, { onClick: function() { setWizStep(1); } }, h(LIcon, { name: 'arrow-left', size: 14 }), ' Back'),
              h(Btn, { onClick: cancelWizard, style: { background: 'rgba(248,113,113,0.1)', color: '#F87171', border: '1px solid rgba(248,113,113,0.3)' } }, h(LIcon, { name: 'x', size: 14 }), ' Cancel'),
              h(Btn, { variant: 'primary', disabled: !allQtysEntered, onClick: function() {
                // Prefill dims from product case pack if single SKU
                if (selectedItemsList.length === 1) {
                  var si = selectedItemsList[0];
                  if (si.case_length && si.case_width && si.case_height) {
                    setDimForm({ length: String(si.case_length), width: String(si.case_width), height: String(si.case_height), weight: si.case_weight ? String(si.case_weight) : '' });
                  }
                }
                setWizStep(3);
              } }, 'Next: Box Dimensions ', h(LIcon, { name: 'arrow-right', size: 14 }))
            ),
            selectedItemsList.filter(function(it) { return !walmartLabeled(it); }).length > 0 && h('button', {
              onClick: function() { printGtinLabels(selectedItemsList.filter(function(it) { return !walmartLabeled(it); })); },
              title: 'Print GTIN labels for all items in this box',
              onMouseEnter: function(e) { e.currentTarget.style.background = '#7C3AED'; },
              onMouseLeave: function(e) { e.currentTarget.style.background = '#8B5CF6'; },
              style: { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 12px', borderRadius: 6, border: 'none', background: '#8B5CF6', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', letterSpacing: '0.02em', transition: 'background 0.15s' } },
              h(LIcon, { name: 'printer', size: 14 }), 'Print All GTIN Labels')
          ),

          selectedItemsList.map(function(item) {
            var remaining = item.quantity - (item.quantity_packed || 0) - (item.quantity_missing || 0);
            var q = packQtys[item.id] || '';
            var qInt = parseInt(q) || 0;
            var qValid = qInt > 0 && qInt <= remaining;
            // Case mismatch only matters on case-packed plans (since 2026-10-30 Walmart really holds
            // inner_pack_qty units per case for them); individual plans stamp inner_pack_qty=1 — which
            // made this warn "Walmart expects 1 units/case" on every multi-unit box.
            var walmartInner = isCasePacked ? item.inner_pack_qty : null;
            var walmartMismatch = walmartInner && qInt > 0 && qInt !== walmartInner;
            var thumbUrl = (item.images && item.images.length) ? item.images[0] : item.image_1_url;
            return h('div', { key: item.id, style: { display: 'flex', gap: 14, padding: '14px 0', borderBottom: '1px solid ' + C.border + '44' } },
              // Image
              thumbUrl
                ? h('img', { src: thumbUrl, onClick: function(e) { e.stopPropagation(); setLightbox({ images: item.images || [item.image_1_url], index: 0 }); }, style: { width: 64, height: 64, borderRadius: 8, objectFit: 'cover', border: '1px solid ' + C.border, flexShrink: 0, cursor: 'pointer' } })
                : h('div', { style: { width: 64, height: 64, borderRadius: 8, background: C.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } }, h(LIcon, { name: 'package', size: 28, color: C.textDim })),
              // Details
              h('div', { style: { flex: 1, minWidth: 0 } },
                h('div', { style: { fontSize: 13, fontWeight: 600, marginBottom: 4 } }, item.vendor_title || item.sku || '--'),
                renderItemBadges(item),
                item.vendor_name && h('div', { style: { display: 'flex', gap: 4, marginBottom: 3 } },
                  copyBadge(item.vendor_name, item.vendor_name, '#F59E0B' + '18', '#B45309')
                ),
                walmartMismatch && h('div', { style: { display: 'flex', alignItems: 'center', gap: 4, marginTop: 4, padding: '4px 8px', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 6, fontSize: 11, color: '#F59E0B' } },
                  h(LIcon, { name: 'alert-triangle', size: 12 }),
                  ' Walmart expects ' + walmartInner + ' units/case — you are packing ' + qInt)
              ),
              // Quantity input + Print GTIN button
              h('div', { style: { width: 150, textAlign: 'center', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 6 } },
                h('div', null,
                  h('div', { style: { fontSize: 10, color: C.textMuted, marginBottom: 4, fontWeight: 500 } }, remaining + ' available'),
                  h('input', { type: 'number', min: 1, max: remaining, value: q,
                    onChange: function(e) { var v = e.target.value; setPackQtys(function(prev) { var n = Object.assign({}, prev); n[item.id] = v; return n; }); },
                    style: { width: '100%', padding: '10px 6px', fontSize: 18, fontWeight: 700, textAlign: 'center', border: '2px solid ' + (walmartMismatch ? '#F59E0B' : qValid ? C.success : q ? C.danger : C.border), borderRadius: 8, background: C.bg, color: C.text, outline: 'none', boxSizing: 'border-box' } }),
                  h('div', { style: { fontSize: 9, color: C.textMuted, marginTop: 2 } }, 'units'),
                  walmartInner && h('div', { style: { fontSize: 9, color: C.textMuted, marginTop: 2 } }, 'Walmart: ' + walmartInner + '/case')
                ),
                (item.gtin || item.barcode) && !walmartLabeled(item) && h('button', { onClick: function(e) { e.stopPropagation(); printGtinLabels([item]); },
                  onMouseEnter: function(e) { e.currentTarget.style.background = '#7C3AED'; }, onMouseLeave: function(e) { e.currentTarget.style.background = '#8B5CF6'; },
                  style: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '5px 10px', borderRadius: 6, border: 'none', background: '#8B5CF6', color: '#fff', cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: 'inherit', transition: 'background 0.15s', width: '100%', whiteSpace: 'nowrap' } },
                  h(LIcon, { name: 'printer', size: 13 }), 'Print GTIN Label'),
                walmartLabeled(item) && h('span', { title: 'Walmart Prep: labeling service selected — Walmart applies the GTIN labels for this SKU', style: { fontSize: 11, fontWeight: 600, color: '#8B5CF6', display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: 'center', width: '100%' } },
                  h(LIcon, { name: 'tag', size: 12 }), 'Labeled by Walmart')
              )
            );
          })
        ),

        // ─── STEP 3 (Case Packed): Review & Generate ───
        wizStep === 3 && isCasePacked && (function() {
          var cpItem = availableItems.find(function(i) { return i.id === cpSelectedItem; });
          if (!cpItem) return null;
          var upc = parseInt(cpUnitsPerCase) || 0;
          var nc = parseInt(cpNumCases) || 0;
          var totalUnitsCalc = upc * nc;
          var dl = parseFloat(cpDims.length); var dw = parseFloat(cpDims.width); var dh = parseFloat(cpDims.height); var dwt = parseFloat(cpDims.weight) || null;

          var generateCases = function() {
            if (cpGenerating) return;
            setCpGenerating(true);
            setCpProgress({ current: 0, total: nc });

            // One atomic /finish-batch call for all nc cases (review 2026-09-28, medium #39): the
            // create → bulk-pack → dims → seal chain per case could stop half-way with open boxes.
            var chain = api(boxBase + '/boxes/finish-batch', { method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: [{ barcode: cpItem.sku || cpItem.product_sku, quantity: upc }], dims: { length: dl, width: dw, height: dh, weight: dwt }, count: nc }) })
              .then(function() { setCpProgress({ current: nc, total: nc }); });

            chain.then(function() {
              // Save as default case pack if requested
              if (cpSaveDefault && !cpUseSaved && cpItem.product_id) {
                return api('/fba-shipments/products/' + cpItem.product_id + '/case-pack', {
                  method: 'PUT', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ case_qty: upc, length: dl, width: dw, height: dh, weight: dwt })
                });
              }
            }).then(function() {
              showToast(nc + ' cases generated for ' + (cpItem.vendor_title || cpItem.sku), 'success');
              if (typeof fireConfetti === 'function') fireConfetti();
              setCpGenerating(false);
              setCpProgress({ current: 0, total: 0 });
              setCpSelectedItem(null);
              setCpNumCases('');
              setCpUnitsPerCase('');
              setCpDims({ length: '', width: '', height: '', weight: '' });
              setWizStep(0);
              loadData();
              if (onRefresh) onRefresh();
            }).catch(function(e) {
              showToast(e.message || 'Failed to generate cases', 'error');
              setCpGenerating(false);
              loadData();
            });
          };

          return h(Card, null,
            h('div', { style: { fontSize: 14, fontWeight: 600, marginBottom: 4 } }, 'Step 3: Review & Generate'),
            h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 14 } }, 'Review case configuration and generate boxes.'),
            h('div', { style: { display: 'flex', justifyContent: 'space-between', marginBottom: 14 } },
              h(Btn, { onClick: function() { setWizStep(2); }, disabled: cpGenerating }, h(LIcon, { name: 'arrow-left', size: 14 }), ' Back'),
              h(Btn, { variant: 'primary', onClick: generateCases, disabled: cpGenerating },
                cpGenerating ? 'Generating...' : [h(LIcon, { key: 'ic', name: 'zap', size: 14 }), ' Generate ' + nc + ' Cases'])
            ),
            // Summary card
            h('div', { style: { padding: '14px 16px', borderRadius: 8, background: C.surface, border: '1px solid ' + C.border, marginBottom: 16 } },
              h('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 10 } }, 'Case Pack Summary'),
              h('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 } },
                h('div', null,
                  h('div', { style: { fontSize: 11, color: C.textMuted } }, 'Product'),
                  h('div', { style: { fontSize: 13, fontWeight: 600 } }, cpItem.vendor_title || cpItem.sku)),
                h('div', null,
                  h('div', { style: { fontSize: 11, color: C.textMuted } }, 'SKU'),
                  h('div', { style: { fontSize: 13, fontWeight: 600, fontFamily: 'inherit' } }, cpItem.product_sku || cpItem.sku)),
                h('div', null,
                  h('div', { style: { fontSize: 11, color: C.textMuted } }, 'Units per Case'),
                  h('div', { style: { fontSize: 13, fontWeight: 600 } }, upc)),
                h('div', null,
                  h('div', { style: { fontSize: 11, color: C.textMuted } }, 'Number of Cases'),
                  h('div', { style: { fontSize: 13, fontWeight: 600 } }, nc)),
                h('div', null,
                  h('div', { style: { fontSize: 11, color: C.textMuted } }, 'Case Dimensions'),
                  h('div', { style: { fontSize: 13, fontWeight: 600 } }, dl + '" x ' + dw + '" x ' + dh + '"' + (dwt ? ' | ' + dwt + ' lbs' : ''))),
                h('div', null,
                  h('div', { style: { fontSize: 11, color: C.textMuted } }, 'Total Units'),
                  h('div', { style: { fontSize: 16, fontWeight: 700, color: C.accent } }, totalUnitsCalc))
              )
            ),
            // Save default note
            cpSaveDefault && !cpUseSaved && h('div', { style: { fontSize: 11, color: C.success, marginBottom: 12 } },
              h(LIcon, { name: 'check-circle', size: 12, style: { marginRight: 4, verticalAlign: 'middle' } }),
              'Case pack dimensions will be saved as default for this product'),
            // Progress bar during generation
            cpGenerating && h('div', { style: { marginBottom: 16 } },
              h('div', { style: { display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 } },
                h('span', { style: { color: C.textMuted } }, 'Creating cases...'),
                h('span', { style: { fontWeight: 600, color: C.accent } }, 'Case ' + cpProgress.current + ' of ' + cpProgress.total)
              ),
              h('div', { style: { height: 8, background: C.border, borderRadius: 4 } },
                h('div', { style: { height: '100%', width: (cpProgress.total > 0 ? Math.round((cpProgress.current / cpProgress.total) * 100) : 0) + '%', background: C.accent, borderRadius: 4, transition: 'width 0.3s' } })
              )
            )
          );
        })(),

        // ─── STEP 3 (Individual): Dimensions & Finish ───
        wizStep === 3 && !isCasePacked && h(Card, null,
          h('div', { style: { fontSize: 14, fontWeight: 600, marginBottom: 4 } }, 'Step 3: Box Dimensions & Weight'),
          h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 10 } }, 'Enter the outer box dimensions. When you click Finish, the box will be created and sealed.'),

          (function() {
            var copiesNum = Math.max(1, Math.min(999, parseInt(numCopies, 10) || 1));
            var finishLabel = copiesNum === 1 ? ' Finish & Seal Box' : ' Finish & Seal ' + copiesNum + ' Boxes';
            return h('div', { style: { display: 'flex', gap: 8, marginBottom: 14 } },
              h(Btn, { onClick: function() { setWizStep(2); } }, h(LIcon, { name: 'arrow-left', size: 14 }), ' Back'),
              h(Btn, { onClick: cancelWizard, style: { background: 'rgba(248,113,113,0.1)', color: '#F87171', border: '1px solid rgba(248,113,113,0.3)' } }, h(LIcon, { name: 'x', size: 14 }), ' Cancel'),
              h(Btn, { variant: 'primary', onClick: finishBox, disabled: saving || !dimForm.length || !dimForm.width || !dimForm.height },
                saving ? 'Packing...' : [h(LIcon, { key: 'ic', name: 'check', size: 14 }), finishLabel])
            );
          })(),

          // Copies — one finish creates N identical boxes (same items + dims). Set to 1 for a single box.
          h('div', { style: { marginBottom: 12, padding: '10px 12px', borderRadius: 8, background: C.surface, border: '1px solid ' + C.border, display: 'flex', alignItems: 'center', gap: 12 } },
            h('div', { style: { fontSize: 12, fontWeight: 600, color: C.text } }, 'Copies'),
            h('input', {
              type: 'number', min: 1, max: 999, step: 1, value: numCopies,
              onChange: function(e) {
                var v = e.target.value;
                if (v === '') { setNumCopies(''); return; }
                var n = parseInt(v, 10);
                if (isNaN(n)) return;
                setNumCopies(Math.max(1, Math.min(999, n)));
              },
              onBlur: function() { if (!numCopies || parseInt(numCopies, 10) < 1) setNumCopies(1); },
              style: { width: 80, padding: '6px 8px', fontSize: 13, border: '1px solid ' + C.border, borderRadius: 6, background: C.bg, color: C.text, outline: 'none', fontFamily: 'inherit' }
            }),
            h('div', { style: { fontSize: 11, color: C.textMuted } },
              (Math.max(1, Math.min(999, parseInt(numCopies, 10) || 1))) === 1
                ? 'Pack a single box with the items + dims below.'
                : 'Pack ' + Math.max(1, Math.min(999, parseInt(numCopies, 10) || 1)) + ' identical boxes — same items, same dimensions.')
          ),

          // Dimensions form
          h('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 16 } },
            h(Inp, { label: 'Length (in)', type: 'number', step: '0.1', value: dimForm.length, onChange: function(e) { setOversizeConfirmed(false); setDimForm(function(f) { return Object.assign({}, f, { length: e.target.value }); }); } }),
            h(Inp, { label: 'Width (in)', type: 'number', step: '0.1', value: dimForm.width, onChange: function(e) { setOversizeConfirmed(false); setDimForm(function(f) { return Object.assign({}, f, { width: e.target.value }); }); } }),
            h(Inp, { label: 'Height (in)', type: 'number', step: '0.1', value: dimForm.height, onChange: function(e) { setOversizeConfirmed(false); setDimForm(function(f) { return Object.assign({}, f, { height: e.target.value }); }); } }),
            h(Inp, { label: 'Weight (lbs)', type: 'number', step: '0.1', value: dimForm.weight, onChange: function(e) { setOversizeConfirmed(false); setDimForm(function(f) { return Object.assign({}, f, { weight: e.target.value }); }); } })
          ),

          // Dimension/weight warnings
          (function() {
            var zeroWarnings = [];
            var oversizeWarnings = [];
            if (!parseFloat(dimForm.length)) zeroWarnings.push('Length is 0 — enter a valid dimension');
            else if (parseFloat(dimForm.length) > 25) oversizeWarnings.push('Length exceeds max of 25 inches');
            if (!parseFloat(dimForm.width)) zeroWarnings.push('Width is 0 — enter a valid dimension');
            else if (parseFloat(dimForm.width) > 25) oversizeWarnings.push('Width exceeds max of 25 inches');
            if (!parseFloat(dimForm.height)) zeroWarnings.push('Height is 0 — enter a valid dimension');
            else if (parseFloat(dimForm.height) > 25) oversizeWarnings.push('Height exceeds max of 25 inches');
            if (!parseFloat(dimForm.weight)) zeroWarnings.push('Weight is 0 — enter a valid weight');
            else if (parseFloat(dimForm.weight) > 50) oversizeWarnings.push('Weight exceeds max of 50 lbs');
            var allWarnings = zeroWarnings.concat(oversizeConfirmed ? [] : oversizeWarnings);
            return h(Fragment, null,
              allWarnings.length > 0 && h('div', { style: { marginBottom: 12, padding: '6px 10px', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 6 } },
                allWarnings.map(function(w, i) { return h('div', { key: i, style: { fontSize: 11, color: '#F87171', display: 'flex', alignItems: 'center', gap: 4 } }, h(LIcon, { name: 'alert-triangle', size: 12 }), w); }),
                oversizeWarnings.length > 0 && !oversizeConfirmed && h('button', { onClick: function() { setOversizeConfirmed(true); },
                  style: { marginTop: 6, background: 'none', border: '1px solid #F87171', color: '#F87171', cursor: 'pointer', fontSize: 11, fontWeight: 600, padding: '4px 12px', borderRadius: 4, fontFamily: 'inherit' } },
                  h(LIcon, { name: 'check', size: 12 }), ' Confirm Oversize')
              ),
              oversizeConfirmed && oversizeWarnings.length > 0 && h('div', { style: { marginBottom: 12, padding: '6px 10px', background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.3)', borderRadius: 6, fontSize: 11, color: '#FBBF24', display: 'flex', alignItems: 'center', gap: 4 } },
                h(LIcon, { name: 'check-circle', size: 12 }), ' Oversize confirmed — proceeding with oversized dimensions')
            );
          })(),

          // Summary of what's going in
          h('div', { style: { padding: '10px 12px', borderRadius: 8, background: C.surface, border: '1px solid ' + C.border, marginBottom: 16 } },
            h('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 6 } }, 'Box Contents Summary'),
            selectedItemsList.map(function(item) {
              var q = parseInt(packQtys[item.id]) || 0;
              return h('div', { key: item.id, style: { display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0' } },
                h('span', null, item.sku || item.vendor_title),
                h('span', { style: { fontWeight: 600 } }, q + ' units')
              );
            }),
            h('div', { style: { display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 700, marginTop: 6, paddingTop: 6, borderTop: '1px solid ' + C.border } },
              h('span', null, 'Total'),
              h('span', null, selectedItemsList.reduce(function(s, i) { return s + (parseInt(packQtys[i.id]) || 0); }, 0) + ' units')
            )
          )
        )
      ),

      // RIGHT PANEL — Tabs: Items | Notes. On step 0 the left column has the pack tab bar
      // above its first card, so offset by its height to keep the card tops aligned.
      h('div', { style: { width: 460, flexShrink: 0, marginTop: wizStep === 0 ? 44 : 0 } },
        h(Card, { style: { position: 'sticky', top: 16, display: 'flex', flexDirection: 'column', maxHeight: 'calc(calc(100vh * var(--invzoom, 1)) - 200px)' } },
          // Tab bar
          h('div', { style: { display: 'flex', borderBottom: '1px solid ' + C.border, marginBottom: 12 } },
            ['items', 'notes'].map(function(tab) {
              var isActive = rightTab === tab;
              var label = tab === 'items' ? 'Boxes (' + boxes.length + ')' : 'Internal Notes' + (notes.length > 0 ? ' (' + notes.length + ')' : '');
              var icon = tab === 'items' ? 'package' : 'message-square';
              return h('button', { key: tab, onClick: function() { setRightTab(tab); },
                style: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px 0', background: 'none', border: 'none', borderBottom: '2px solid ' + (isActive ? C.accent : 'transparent'), color: isActive ? C.accent : C.textMuted, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'color 0.15s, border-color 0.15s' } },
                h(LIcon, { name: icon, size: 14 }), label);
            })
          ),

          // ─── ITEMS TAB ───
          rightTab === 'items' && h('div', { style: { flex: 1, overflow: 'auto' } },
            boxes.length > 0 && h('div', { style: { fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 } }, 'Completed Boxes'),
            boxes.length > 0 && h('div', { style: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 } },
              h('div', { style: { position: 'relative', flex: 1 } },
                h(LIcon, { name: 'search', size: 14, style: { position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: C.textDim, pointerEvents: 'none' } }),
                h('input', { type: 'text', value: boxFilter, placeholder: 'Find box / SKU / GTIN / vendor / PO# / barcode...',
                  onChange: function(e) { setBoxFilter(e.target.value); },
                  style: { width: '100%', boxSizing: 'border-box', background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 10px 8px 32px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', outline: 'none' } })
              ),
              boxFilter && h('button', { onClick: function() { setBoxFilter(''); }, title: 'Show all boxes',
                style: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '7px 12px', borderRadius: 6, border: 'none', background: C.accent + '15', color: C.accent, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', whiteSpace: 'nowrap' } }, 'View All')
            ),
            boxes.length === 0
              ? h('div', { style: { fontSize: 12, color: C.textMuted, textAlign: 'center', padding: '20px 0' } }, 'No boxes packed yet')
              : (function() {
                  var bq = boxFilter.trim();
                  // Split on commas only so free-text phrases stay intact (e.g. "2,5,12").
                  var tokens = bq ? bq.split(/,+/).map(function(s) { return s.trim(); }).filter(Boolean) : [];
                  var siById = {};
                  items.forEach(function(it) { siById[it.product_id] = it; });
                  var sortedBoxes = boxes.slice().sort(function(a, b) { return (b.box_number || 0) - (a.box_number || 0); });
                  var filteredBoxes = tokens.length ? sortedBoxes.filter(function(b) {
                    var bn = String(b.box_number || '');
                    return tokens.some(function(t) {
                      if (bn.indexOf(t) === 0) return true;
                      var tl = t.toLowerCase();
                      var poQ = tl.replace(/^po\s*#?\s*/, '');
                      return (b.items || []).some(function(bi) {
                        if (
                          (bi.product_sku || '').toLowerCase().indexOf(tl) >= 0 ||
                          (bi.sku || '').toLowerCase().indexOf(tl) >= 0 ||
                          (bi.gtin || '').toLowerCase().indexOf(tl) >= 0 ||
                          (bi.barcode || '').toLowerCase().indexOf(tl) >= 0 ||
                          (bi.vendor_title || '').toLowerCase().indexOf(tl) >= 0 ||
                          (bi.vendor_sku || '').toLowerCase().indexOf(tl) >= 0
                        ) return true;
                        var si = siById[bi.product_id];
                        if (si && (
                          (si.vendor_name || '').toLowerCase().indexOf(tl) >= 0 ||
                          (si.po_numbers || si.po_number || '').toLowerCase().indexOf(poQ) >= 0
                        )) return true;
                        return false;
                      });
                    });
                  }) : sortedBoxes;
                  if (filteredBoxes.length === 0) return h('div', { style: { fontSize: 12, color: C.textMuted, textAlign: 'center', padding: '20px 0' } }, 'No boxes match "' + bq + '"');
                  return filteredBoxes.map(function(box) {
                  var bItems = (box.items || []);
                  var unitCount = bItems.reduce(function(s, i) { return s + i.quantity; }, 0);
                  var singleSkuImg = bItems.length === 1 && bItems[0].image_1_url ? bItems[0].image_1_url : null;
                  return h('div', { key: box.id, style: { padding: '10px 0', borderBottom: '1px solid ' + C.border + '33' } },
                    h('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' },
                      onClick: function() { setExpandedBoxId(box.id); } },
                      h('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
                        singleSkuImg
                          ? h('img', { src: singleSkuImg, style: { width: 72, height: 72, borderRadius: 6, objectFit: 'cover', border: '1px solid ' + C.border, flexShrink: 0 } })
                          : h('div', { style: { width: 72, height: 72, borderRadius: 6, background: C.success + '15', display: 'flex', alignItems: 'center', justifyContent: 'center' } },
                              h(LIcon, { name: 'package', size: 28, color: C.success })),
                        h('div', null,
                          h('div', { style: { fontWeight: 600, fontSize: 18 } }, 'Box ' + box.box_number),
                          h('div', { style: { fontSize: 14, color: C.textMuted } }, unitCount + ' units / ' + bItems.length + ' SKUs'),
                          (box.length && box.width && box.height) && h('div', { style: { fontSize: 14, color: C.textMuted } },
                            box.length + '" x ' + box.width + '" x ' + box.height + '"',
                            box.weight ? ' | ' + box.weight + ' lbs' : '')
                        )
                      ),
                      h('div', { style: { display: 'flex', gap: 4, alignItems: 'center' } },
                        h('button', { onClick: function(e) { e.stopPropagation(); openDuplicateBox(box); }, title: 'Duplicate Box',
                          onMouseEnter: function(e) { e.currentTarget.style.background = C.accent + '18'; }, onMouseLeave: function(e) { e.currentTarget.style.background = 'none'; },
                          style: { background: 'none', border: 'none', cursor: 'pointer', color: C.accent, padding: 6, borderRadius: 6, transition: 'background 0.15s' } }, h(LIcon, { name: 'copy', size: 18 })),
                        h('button', { onClick: function(e) { e.stopPropagation(); openEditBox(box); }, title: 'Edit Box',
                          onMouseEnter: function(e) { e.currentTarget.style.background = C.accent + '18'; }, onMouseLeave: function(e) { e.currentTarget.style.background = 'none'; },
                          style: { background: 'none', border: 'none', cursor: 'pointer', color: C.accent, padding: 6, borderRadius: 6, transition: 'background 0.15s' } }, h(LIcon, { name: 'edit-2', size: 18 })),
                        h('button', { onClick: function(e) { e.stopPropagation(); deleteBox(box.id); }, title: 'Delete',
                          onMouseEnter: function(e) { e.currentTarget.style.background = '#F8717118'; }, onMouseLeave: function(e) { e.currentTarget.style.background = 'none'; },
                          style: { background: 'none', border: 'none', cursor: 'pointer', color: C.danger, padding: 6, borderRadius: 6, transition: 'background 0.15s' } }, h(LIcon, { name: 'trash-2', size: 18 }))
                      )
                    )
                  );
                })
                })()
          ),

          // ─── NOTES TAB (matches PO Internal Notes style) ───
          rightTab === 'notes' && h('div', { style: { flex: 1, display: 'flex', flexDirection: 'column' } },
            h('div', { style: { flex: 1, overflow: 'auto', padding: '4px 0', display: 'flex', flexDirection: 'column', gap: 8, minHeight: 200 } },
              notes.length === 0 && h('div', { style: { textAlign: 'center', color: C.textDim, fontSize: 13, padding: 40 } }, 'No notes yet. Start the conversation!'),
              notes.map(function(note) {
                var isMe = currentUser && currentUser.id === note.user_id;
                var time = new Date(note.created_at);
                var timeStr = time.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' + time.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                return h('div', { key: note.id, style: { display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start', padding: '0 4px' } },
                  h('div', { style: { fontSize: 10, color: C.textDim, marginBottom: 2, display: 'flex', alignItems: 'center', gap: 6 } },
                    h('span', { style: { fontWeight: 600 } }, note.display_name || note.username),
                    h('span', null, timeStr),
                    isMe && h('button', { onClick: function() { deleteNote(note.id); },
                      style: { background: 'none', border: 'none', color: C.textDim, cursor: 'pointer', fontSize: 10, padding: 0, display: 'inline-flex', alignItems: 'center' } }, h(LIcon, { name: 'x', size: 10 }))
                  ),
                  h('div', { style: { background: isMe ? C.accent : C.surface, color: isMe ? '#fff' : C.text,
                    padding: '8px 14px', borderRadius: 12, borderTopRightRadius: isMe ? 4 : 12, borderTopLeftRadius: isMe ? 12 : 4,
                    maxWidth: '85%', fontSize: 13, lineHeight: 1.4, wordBreak: 'break-word',
                    border: isMe ? 'none' : '1px solid ' + C.border } }, renderNoteBody(note.message), renderNoteAttachments(note))
                );
              }),
              h('div', { ref: notesEndRef })
            ),
            h('div', { style: { borderTop: '1px solid ' + C.border, padding: '12px 0 0', display: 'flex', gap: 8, alignItems: 'flex-end' } },
              h('div', { style: { flex: 1 } },
                h(MentionInput, {
                  value: newNote, onChange: setNewNote,
                  onKeyDown: function(e) { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendNote(); } },
                  placeholder: 'Type a note... use @ to tag a teammate',
                  rows: 2,
                  style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '10px 14px', borderRadius: 8, fontSize: 13, outline: 'none' }
                })
              ),
              h(Btn, { variant: 'primary', size: 'sm', onClick: sendNote, disabled: !newNote.trim() }, 'Send')
            )
          )
        ) // close Card
      ) // close right panel div
    ),

    // Box Contents modal
    h(Modal, { open: !!viewBox, onClose: function() { setExpandedBoxId(null); }, title: viewBox ? 'Box ' + viewBox.box_number + ' Contents' : '', width: 750 },
      viewBox && (function() {
        var vbItems = viewBox.items || [];
        var vbUnits = vbItems.reduce(function(s, i) { return s + i.quantity; }, 0);
        return h('div', null,
          // Action buttons
          h('div', { style: { display: 'flex', gap: 8, marginBottom: 14, justifyContent: 'flex-end' } },
            h(Btn, { size: 'sm', onClick: function() { setExpandedBoxId(null); openEditBox(viewBox); } }, h(LIcon, { name: 'edit-2', size: 12 }), ' Edit Box')
          ),
          // Header info
          h('div', { style: { display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' } },
            h('span', { style: { fontSize: 13, color: C.textMuted } }, vbUnits + ' units / ' + vbItems.length + ' SKUs'),
            (viewBox.length && viewBox.width && viewBox.height) && h('span', { style: { fontSize: 13, color: C.textMuted } }, viewBox.length + '" x ' + viewBox.width + '" x ' + viewBox.height + '"' + (viewBox.weight ? ' | ' + viewBox.weight + ' lbs' : ''))
          ),
          // Items list
          vbItems.length === 0
            ? h('div', { style: { fontSize: 13, color: C.textMuted, textAlign: 'center', padding: '20px 0', fontStyle: 'italic' } }, 'No items in this box')
            : vbItems.map(function(bi) {
                return h('div', { key: bi.id, style: { display: 'flex', alignItems: 'center', gap: 14, padding: '14px 0', borderBottom: '1px solid ' + C.border + '33' } },
                  bi.image_1_url
                    ? h('img', { src: bi.image_1_url, onClick: function() { setLightbox({ images: [bi.image_1_url], index: 0 }); }, style: { width: 72, height: 72, borderRadius: 8, objectFit: 'cover', border: '1px solid ' + C.border, flexShrink: 0, cursor: 'pointer' } })
                    : h('div', { style: { width: 72, height: 72, borderRadius: 8, background: C.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } }, h(LIcon, { name: 'package', size: 28, color: C.textDim })),
                  h('div', { style: { flex: 1, minWidth: 0 } },
                    h('div', { style: { fontSize: 14, fontWeight: 600, marginBottom: 5 } }, bi.vendor_title || bi.product_sku || bi.sku || '--'),
                    h('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 5 } },
                      (bi.product_sku || bi.sku) && h(CopyCell, { value: bi.product_sku || bi.sku }, h('span', { style: ps(C.accent + '14', C.accent) }, 'SKU: ' + (bi.product_sku || bi.sku))),
                      bi.gtin && h(CopyCell, { value: bi.gtin }, h('span', { style: ps(C.accent + '14', C.accent) }, 'GTIN: ' + bi.gtin)),
                      bi.barcode && h(CopyCell, { value: bi.barcode }, h('span', { style: ps(C.accent + '14', C.accent) }, 'BARCODE: ' + bi.barcode)),
                      bi.vendor_sku && h(CopyCell, { value: bi.vendor_sku }, h('span', { style: ps(C.accent + '14', C.accent) }, 'VSKU: ' + bi.vendor_sku))
                    )
                  ),
                  h('div', { style: { fontSize: 20, fontWeight: 700, color: C.accent, flexShrink: 0, minWidth: 55, textAlign: 'right' } }, 'x' + bi.quantity)
                );
              })
        );
      })()
    ),

    // ─── Delete Box Confirm Modal (FBA-style) ───
    h(Modal, { open: !!deleteBoxConfirm, onClose: function() { setDeleteBoxConfirm(null); }, title: 'Confirm Box Deletion', width: 400 },
      deleteBoxConfirm && h('div', null,
        h('div', { style: { display: 'flex', alignItems: 'center', gap: 10, padding: 10, background: 'rgba(248,113,113,0.08)', borderRadius: 8, marginBottom: 12 } },
          h(LIcon, { name: 'trash-2', size: 18, color: C.danger }),
          h('div', null,
            h('div', { style: { fontWeight: 600, color: C.text } }, 'Box ' + deleteBoxConfirm.boxNum),
            h('div', { style: { fontSize: 12, color: C.textMuted } }, deleteBoxConfirm.unitCount + ' units / ' + deleteBoxConfirm.skuCount + ' SKUs')
          )
        ),
        h('div', { style: { fontSize: 13, color: C.text, marginBottom: 16 } }, 'Delete this box? Its contents will be returned to the shipment for repacking.'),
        h('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } },
          h(Btn, { onClick: function() { setDeleteBoxConfirm(null); } }, 'Cancel'),
          h(Btn, { variant: 'primary', onClick: confirmDeleteBox, style: { background: '#DC2626', borderColor: '#DC2626' } }, h(LIcon, { name: 'trash-2', size: 14 }), ' Delete Box')
        )
      )
    ),

    // ─── Resume in-progress box draft prompt ───
    h(Modal, { open: !!draftPrompt, onClose: function() { setDraftPrompt(null); }, title: 'Resume In-Progress Box?', width: 460 },
      draftPrompt && (function() {
        var ago = '';
        if (draftPrompt.updated_at) {
          var ms = Date.now() - new Date(draftPrompt.updated_at).getTime();
          if (!isNaN(ms) && ms >= 0) {
            var mins = Math.floor(ms / 60000);
            ago = mins < 1 ? 'just now' : mins < 60 ? (mins + ' min ago')
              : mins < 1440 ? (Math.floor(mins / 60) + ' hr ago') : (Math.floor(mins / 1440) + ' d ago');
          }
        }
        return h('div', null,
          h('div', { style: { fontSize: 13, color: C.text, marginBottom: 12 } },
            'You have an unfinished box for this shipment' + (ago ? ' (saved ' + ago + ')' : '') + '. Resume it, or start a new empty box?'),
          h('div', { style: { background: C.bg, border: '1px solid ' + C.border, borderRadius: 6, padding: 10, marginBottom: 16, maxHeight: 220, overflowY: 'auto' } },
            draftPrompt.data.lines.map(function(ln, i) {
              return h('div', { key: i, style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, padding: '4px 0', borderTop: i ? '1px solid ' + C.border : 'none' } },
                h('span', { style: { fontFamily: 'inherit', color: C.text } }, ln.label),
                h('span', { style: { fontWeight: ln.qty != null ? 700 : 500, color: ln.qty != null ? C.text : C.textMuted, fontStyle: ln.qty != null ? 'normal' : 'italic' } },
                  ln.qty != null ? ('× ' + ln.qty) : 'qty not set'));
            })),
          h('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } },
            h(Btn, { onClick: function() {
              if (shipmentId) api('/wfs-shipments/shipments/' + shipmentId + '/box-draft', { method: 'DELETE' }).catch(function() {});
              setRawDraft(null);
              setSelectedItems({}); setPackQtys({}); setNumCopies(1);
              setDimForm({ length: '', width: '', height: '', weight: '' });
              setItemSearch(''); setDraftPrompt(null); setWizStep(1);
            } }, 'Start fresh'),
            h(Btn, { variant: 'primary', onClick: function() {
              var d = draftPrompt.data;
              setSelectedItems(d.selectedItems); setPackQtys(d.packQtys); setNumCopies(d.numCopies);
              setDimForm(d.dimForm || { length: '', width: '', height: '', weight: '' });
              setItemSearch(''); setDraftPrompt(null); setWizStep(1);
            } }, h(LIcon, { name: 'rotate-ccw', size: 14 }), ' Resume')
          )
        );
      })()
    ),

    // ─── Duplicate Box Confirmation Modal (count) ───
    h(Modal, { open: !!duplicateBoxModal, onClose: function() { if (!duplicateSaving) setDuplicateBoxModal(null); }, title: 'Duplicate Box', width: 400 },
      duplicateBoxModal && (function() {
        var max = duplicateBoxModal.maxCopies;
        var n = parseInt(duplicateCount, 10);
        var valid = max >= 1 && n >= 1 && n <= max;
        return h('div', null,
          h('div', { style: { display: 'flex', alignItems: 'center', gap: 10, padding: 12, background: C.surface, borderRadius: 6, marginBottom: 14 } },
            h('div', { style: { width: 40, height: 40, borderRadius: 6, background: C.accent + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } },
              h(LIcon, { name: 'copy', size: 20, color: C.accent })),
            h('div', { style: { flex: 1, minWidth: 0 } },
              h('div', { style: { fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 2 } }, 'Box ' + duplicateBoxModal.boxNum),
              h('div', { style: { fontSize: 12, color: C.textMuted } }, duplicateBoxModal.unitCount + ' unit' + (duplicateBoxModal.unitCount === 1 ? '' : 's') + ' / ' + duplicateBoxModal.skuCount + ' SKU' + (duplicateBoxModal.skuCount === 1 ? '' : 's'))
            )
          ),
          max < 1
            ? h('div', { style: { fontSize: 12, color: '#F87171', padding: '10px 12px', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.2)', borderRadius: 6, marginBottom: 16 } },
                'No remaining capacity — all units for SKUs in this box are already packed or marked missing.')
            : h('div', { style: { marginBottom: 16 } },
                h('label', { style: { display: 'block', fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 } }, 'Number of copies'),
                h('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
                  h('input', {
                    type: 'number', min: 1, max: max, value: duplicateCount,
                    onChange: function(e) {
                      var v = parseInt(e.target.value, 10);
                      if (isNaN(v)) { setDuplicateCount(''); return; }
                      if (v < 1) v = 1;
                      if (v > max) v = max;
                      setDuplicateCount(v);
                    },
                    style: { width: 90, background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }
                  }),
                  h('span', { style: { fontSize: 12, color: C.textMuted } }, 'Max: ', h('strong', { style: { color: C.text } }, max))
                )
              ),
          h('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } },
            h(Btn, { onClick: function() { setDuplicateBoxModal(null); }, disabled: duplicateSaving }, 'Cancel'),
            h(Btn, { variant: 'primary', onClick: confirmDuplicateBox, disabled: !valid || duplicateSaving },
              h(LIcon, { name: 'copy', size: 14 }), ' ', duplicateSaving ? 'Duplicating…' : 'Duplicate')
          )
        );
      })()
    ),

    // ─── Edit Box Modal ───
    h(Modal, { open: !!editBox, onClose: cancelEditBox, title: editBox ? 'Edit Box ' + editBox.box_number : '', width: 900, maxHeight: 'calc(90vh * var(--invzoom, 1))' },
      editBox && h('div', null,
        // Dimensions (under the title)
        h('div', { style: { marginBottom: 16 } },
          h('label', { style: { fontSize: 13, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', display: 'block', marginBottom: 6 } }, 'Dimensions'),
          h('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8 } },
            h(Inp, { label: 'L (in)', type: 'number', value: editBox.dims.length, onChange: function(e) { setEditBox(function(prev) { return Object.assign({}, prev, { dims: Object.assign({}, prev.dims, { length: e.target.value }) }); }); } }),
            h(Inp, { label: 'W (in)', type: 'number', value: editBox.dims.width, onChange: function(e) { setEditBox(function(prev) { return Object.assign({}, prev, { dims: Object.assign({}, prev.dims, { width: e.target.value }) }); }); } }),
            h(Inp, { label: 'H (in)', type: 'number', value: editBox.dims.height, onChange: function(e) { setEditBox(function(prev) { return Object.assign({}, prev, { dims: Object.assign({}, prev.dims, { height: e.target.value }) }); }); } }),
            h(Inp, { label: 'Wt (lbs)', type: 'number', value: editBox.dims.weight, onChange: function(e) { setEditBox(function(prev) { return Object.assign({}, prev, { dims: Object.assign({}, prev.dims, { weight: e.target.value }) }); }); } })
          )
        ),
        // Items header + add button
        h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 } },
          h('label', { style: { fontSize: 13, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase' } }, 'Items in Box (' + editBox.items.reduce(function(s, i) { return s + i.quantity; }, 0) + ' units)'),
          h('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
            editBoxAddOpen && h(Btn, { size: 'md', onClick: function() { setEditBoxAddOpen(false); } }, 'Cancel'),
            h(Btn, { variant: 'primary', size: 'md', onClick: function() { setEditBoxAddOpen(!editBoxAddOpen); } }, h(LIcon, { name: 'plus', size: 14, style: { marginRight: 2 } }), ' Add Item')
          )
        ),

        // Add item dropdown
        editBoxAddOpen && h('div', { style: { marginBottom: 10, padding: '8px 10px', background: C.surface, borderRadius: 6, border: '1px solid ' + C.border } },
          // Search header — same mechanism as the Step 1 packing search
          h('div', { style: { marginBottom: 10, position: 'relative' } },
            h('input', { value: editBoxSearch, onChange: function(e) { setEditBoxSearch(e.target.value); }, placeholder: 'Search plan items by SKU, GTIN, barcode, product name, vendor, PO#...',
              style: { width: '100%', padding: '8px 34px 8px 12px', fontSize: 13, border: '1px solid ' + C.border, borderRadius: 6, background: C.bg, color: C.text, outline: 'none', boxSizing: 'border-box' } }),
            editBoxSearch && h('button', { onClick: function() { setEditBoxSearch(''); }, title: 'Clear search',
              style: { position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', width: 22, height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', border: 'none', background: C.border, color: C.text, cursor: 'pointer', padding: 0, fontFamily: 'inherit' } },
              h(LIcon, { name: 'x', size: 12 }))
          ),
          h('div', { style: { maxHeight: 360, overflowY: 'auto' } },
          (function() {
            var boxProductIds = editBox.items.map(function(i) { return i.product_id; });
            var addable = items.filter(function(i) { return boxProductIds.indexOf(i.product_id) === -1; });
            var q = editBoxSearch.toLowerCase().trim();
            var qPo = q.replace(/^po\s*#?\s*/, '');
            if (q) addable = addable.filter(function(item) {
              return (item.sku || '').toLowerCase().indexOf(q) >= 0 ||
                (item.product_sku || '').toLowerCase().indexOf(q) >= 0 ||
                (item.gtin || '').toLowerCase().indexOf(q) >= 0 ||
                (item.barcode || '').toLowerCase().indexOf(q) >= 0 ||
                (item.vendor_title || '').toLowerCase().indexOf(q) >= 0 ||
                (item.vendor_name || '').toLowerCase().indexOf(q) >= 0 ||
                (item.vendor_sku || '').toLowerCase().indexOf(q) >= 0 ||
                (item.po_numbers || '').toLowerCase().indexOf(qPo) >= 0;
            });
            addable.sort(function(a, b) { return String(a.product_sku || a.sku || '').localeCompare(String(b.product_sku || b.sku || ''), undefined, { numeric: true, sensitivity: 'base' }); });
            return addable.length === 0
              ? h('div', { style: { fontSize: 13, color: C.textMuted, textAlign: 'center', padding: 12 } }, q ? 'No items match "' + editBoxSearch + '"' : 'All plan items are already in this box')
              : addable.map(function(si) {
                  var thumbUrl = (si.images && si.images.length) ? si.images[0] : si.image_1_url;
                  return h('div', { key: si.id, onClick: function() { editBoxAddItem(si); },
                    style: { display: 'flex', alignItems: 'center', gap: 12, padding: '11px 0', borderBottom: '1px solid ' + C.border + '22', cursor: 'pointer' } },
                    thumbUrl
                      ? h('img', { src: thumbUrl, style: { width: 60, height: 60, borderRadius: 8, objectFit: 'cover', border: '1px solid ' + C.border, flexShrink: 0 } })
                      : h('div', { style: { width: 60, height: 60, borderRadius: 8, background: C.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } }, h(LIcon, { name: 'package', size: 24, color: C.textDim })),
                    h('div', { style: { flex: 1, minWidth: 0 } },
                      h('div', { style: { fontSize: 14, fontWeight: 600, marginBottom: 4 } }, si.vendor_title || si.sku),
                      h('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 2 } },
                        (si.product_sku || si.sku) && h('span', { style: { fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: C.accent + '14', color: C.accent, whiteSpace: 'nowrap', display: 'inline-block' } }, 'SKU: ' + (si.product_sku || si.sku)),
                        si.vendor_sku && h('span', { style: { fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: C.accent + '14', color: C.accent, whiteSpace: 'nowrap', display: 'inline-block' } }, 'VSKU: ' + si.vendor_sku),
                        si.barcode && h('span', { style: { fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: C.accent + '14', color: C.accent, whiteSpace: 'nowrap', display: 'inline-block' } }, 'BARCODE: ' + si.barcode),
                        si.gtin && h('span', { style: { fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: '#8B5CF6' + '18', color: '#8B5CF6', whiteSpace: 'nowrap', display: 'inline-block' } }, 'GTIN: ' + si.gtin)
                      ),
                      si.vendor_name && h('div', { style: { display: 'flex', gap: 3 } },
                        h('span', { style: { fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: '#F59E0B' + '18', color: '#B45309', whiteSpace: 'nowrap', display: 'inline-block' } }, si.vendor_name)
                      )
                    ),
                    h(LIcon, { name: 'plus-circle', size: 24, color: C.success, style: { flexShrink: 0 } })
                  );
                });
          })()
          )
        ),

        // Current items list — searchable when the box has several items
        editBox.items.length > 3 && h('div', { style: { marginBottom: 10, position: 'relative' } },
          h('input', { value: editBoxItemSearch, onChange: function(e) { setEditBoxItemSearch(e.target.value); }, placeholder: 'Search items in this box by SKU, GTIN, barcode, vendor...',
            style: { width: '100%', padding: '8px 34px 8px 12px', fontSize: 13, border: '1px solid ' + C.border, borderRadius: 6, background: C.bg, color: C.text, outline: 'none', boxSizing: 'border-box' } }),
          editBoxItemSearch && h('button', { onClick: function() { setEditBoxItemSearch(''); }, title: 'Clear search',
            style: { position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', width: 22, height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', border: 'none', background: C.border, color: C.text, cursor: 'pointer', padding: 0, fontFamily: 'inherit' } },
            h(LIcon, { name: 'x', size: 12 }))
        ),
        h('div', { style: { marginBottom: 10 } },
          editBox.items.length === 0
            ? h('div', { style: { fontSize: 13, color: C.textMuted, padding: '16px 0', textAlign: 'center' } }, 'No items in this box')
            : (function() {
                var iq = editBoxItemSearch.toLowerCase().trim();
                var rows = editBox.items.map(function(item, idx) { return { item: item, idx: idx }; }).filter(function(row) {
                  if (!iq) return true;
                  var it = row.item;
                  return (it.sku || '').toLowerCase().indexOf(iq) >= 0 ||
                    (it.gtin || '').toLowerCase().indexOf(iq) >= 0 ||
                    (it.barcode || '').toLowerCase().indexOf(iq) >= 0 ||
                    (it.vendor_title || '').toLowerCase().indexOf(iq) >= 0 ||
                    (it.vendor_sku || '').toLowerCase().indexOf(iq) >= 0 ||
                    (it.vendor_name || '').toLowerCase().indexOf(iq) >= 0;
                });
                rows.sort(function(a, b) { return String(a.item.sku || '').localeCompare(String(b.item.sku || ''), undefined, { numeric: true, sensitivity: 'base' }); });
                if (rows.length === 0) return h('div', { style: { fontSize: 13, color: C.textMuted, padding: '12px 0', textAlign: 'center' } }, 'No items match "' + editBoxItemSearch + '"');
                return rows.map(function(row) {
                  var item = row.item; var idx = row.idx;
                  return h('div', { key: item.id || ('new-' + idx), style: { display: 'flex', alignItems: 'flex-start', gap: 12, padding: '11px 0', borderBottom: '1px solid ' + C.border + '33' } },
                  item.thumbUrl
                    ? h('img', { src: item.thumbUrl, style: { width: 60, height: 60, borderRadius: 8, objectFit: 'cover', border: '1px solid ' + C.border, flexShrink: 0 } })
                    : h('div', { style: { width: 60, height: 60, borderRadius: 8, background: C.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } }, h(LIcon, { name: 'package', size: 24, color: C.textDim })),
                  h('div', { style: { flex: 1, minWidth: 0 } },
                    h('div', { style: { fontSize: 14, fontWeight: 600, marginBottom: 4 } }, item.vendor_title || item.sku || item.gtin),
                    h('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 2 } },
                      item.sku && h('span', { style: { fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: C.accent + '14', color: C.accent, whiteSpace: 'nowrap', display: 'inline-block' } }, 'SKU: ' + item.sku),
                      item.barcode && h('span', { style: { fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: C.accent + '14', color: C.accent, whiteSpace: 'nowrap', display: 'inline-block' } }, 'BARCODE: ' + item.barcode),
                      item.gtin && h('span', { style: { fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: '#8B5CF6' + '18', color: '#8B5CF6', whiteSpace: 'nowrap', display: 'inline-block' } }, 'GTIN: ' + item.gtin)
                    ),
                    item.vendor_name && h('div', { style: { display: 'flex', gap: 3 } },
                      h('span', { style: { fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: '#F59E0B' + '18', color: '#B45309', whiteSpace: 'nowrap', display: 'inline-block' } }, item.vendor_name)
                    ),
                    item._isNew && h('span', { style: { fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: '#10B981' + '18', color: '#047857', whiteSpace: 'nowrap', display: 'inline-block' } }, 'NEW')
                  ),
                  h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 } },
                    h('input', { type: 'number', min: 1, value: item.quantity,
                      onChange: function(e) {
                        var val = Math.max(1, parseInt(e.target.value) || 1);
                        setEditBox(function(prev) {
                          var newItems = prev.items.map(function(it, i) { return i === idx ? Object.assign({}, it, { quantity: val }) : it; });
                          return Object.assign({}, prev, { items: newItems });
                        });
                      },
                      style: { width: 66, padding: '7px 8px', fontSize: 14, textAlign: 'center', border: '1px solid ' + C.border, borderRadius: 6, background: C.bg, color: C.text } }),
                    h('button', { onClick: function() { editBoxRemoveItem(idx); }, title: 'Remove',
                      style: { background: 'none', border: 'none', cursor: 'pointer', color: C.danger, padding: 4 } }, h(LIcon, { name: 'trash-2', size: 18 }))
                  )
                );
              })
              })()
        ),

        // Removed items (undo)
        editBox.removed.length > 0 && h('div', { style: { marginBottom: 10, padding: '6px 10px', background: 'rgba(248,113,113,0.06)', borderRadius: 6, border: '1px solid rgba(248,113,113,0.15)' } },
          h('div', { style: { fontSize: 10, fontWeight: 600, color: '#F87171', marginBottom: 4, textTransform: 'uppercase' } }, 'Removed Items'),
          editBox.removed.map(function(entry, rIdx) {
            return h('div', { key: entry.id, style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0', fontSize: 11, color: C.textMuted, textDecoration: 'line-through' } },
              h('span', null, (entry.item.sku || entry.item.gtin) + ' -- ' + (entry.item.vendor_title || '')),
              h('button', { onClick: function() { editBoxUndoRemove(rIdx); },
                style: { background: 'none', border: 'none', cursor: 'pointer', color: C.accent, fontSize: 11, fontWeight: 600, fontFamily: 'inherit', padding: '2px 6px' } }, 'Undo')
            );
          })
        ),

        // Actions
        h('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } },
          h(Btn, { onClick: cancelEditBox }, 'Cancel'),
          h(Btn, { variant: 'primary', onClick: saveEditBox, disabled: editBoxSaving }, editBoxSaving ? 'Saving...' : 'Save Changes')
        )
      )
    ),

    // ─── Print GTIN Labels Modal ───
    h(Modal, { open: !!printModal, onClose: function() { setPrintModal(null); }, title: 'Print GTIN Labels', width: 520 },
      printModal && h('div', null,
        h('div', { style: { marginBottom: 14 } },
          h('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', display: 'block', marginBottom: 6 } }, 'Label Template'),
          (function() { var wfsTpls = labelTemplates.filter(function(t) { return (t.category || '') === 'wfs'; }); return wfsTpls.length === 0
            ? h('div', { style: { fontSize: 12, color: C.textMuted, padding: '10px 0' } }, 'No WFS label templates configured. Create templates in Settings > Templates.')
            : h('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 6 } },
                wfsTpls.map(function(tpl) {
                  var sel = selectedLabelTpl === tpl.id;
                  return h('button', { key: tpl.id, onClick: function() { setSelectedLabelTpl(tpl.id); },
                    style: { padding: '6px 12px', borderRadius: 6, border: '2px solid ' + (sel ? C.accent : C.border), background: sel ? C.accent + '15' : 'transparent', color: sel ? C.accent : C.text, cursor: 'pointer', fontSize: 11, fontFamily: 'inherit', fontWeight: sel ? 600 : 400, textAlign: 'left' } },
                    h('div', null, tpl.name),
                    h('div', { style: { fontSize: 9, color: C.textMuted, marginTop: 2 } }, tpl.label_type === 'sheet' ? tpl.columns + 'x' + tpl.rows_per_sheet + ' per sheet' : parseFloat(tpl.width_mm).toFixed(1) + 'x' + parseFloat(tpl.height_mm).toFixed(1) + 'mm'),
                    tpl.is_default && h('span', { style: { fontSize: 8, color: C.accent, fontWeight: 600 } }, ' DEFAULT')
                  );
                })
              ); })()
        ),
        h('div', { style: { marginBottom: 14 } },
          h('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', display: 'block', marginBottom: 6 } }, 'Items & Label Count'),
          (printModal.items || []).map(function(item) {
            var thumbUrl = (item.images && item.images.length) ? item.images[0] : item.image_1_url;
            return h('div', { key: item.id, style: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid ' + C.border + '44' } },
              thumbUrl
                ? h('img', { src: thumbUrl, style: { width: 44, height: 44, borderRadius: 6, objectFit: 'cover', border: '1px solid ' + C.border, flexShrink: 0 } })
                : h('div', { style: { width: 44, height: 44, borderRadius: 6, background: C.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } }, h(LIcon, { name: 'package', size: 20, color: C.textDim })),
              h('div', { style: { flex: 1, minWidth: 0 } },
                h('div', { style: { fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, item.vendor_title || item.product_sku || item.sku || ''),
                h('div', { style: { fontSize: 11, fontFamily: 'inherit', color: C.textMuted } }, item.gtin || item.barcode || item.product_sku || item.sku)
              ),
              h('input', { type: 'number', min: 1, value: printQtyOverride[item.id] || 1,
                onChange: function(e) { var v = Math.max(1, parseInt(e.target.value) || 1); setPrintQtyOverride(function(prev) { var n = Object.assign({}, prev); n[item.id] = v; return n; }); },
                style: { width: 60, padding: '4px 6px', fontSize: 12, textAlign: 'center', border: '1px solid ' + C.border, borderRadius: 4, background: C.bg, color: C.text, flexShrink: 0 } })
            );
          }),
          h('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 6, textAlign: 'right' } },
            'Total: ' + (printModal.items || []).reduce(function(s, item) { return s + (printQtyOverride[item.id] || 1); }, 0) + ' labels')
        ),
        h('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } },
          h(Btn, { onClick: function() { setPrintModal(null); } }, 'Cancel'),
          h(Btn, { onClick: exportGtinLabelsZip }, h(LIcon, { name: 'download', size: 14 }), ' Print As Individual Files'),
          h(Btn, { variant: 'primary', onClick: executeGtinPrint }, h(LIcon, { name: 'printer', size: 14 }), ' Print')
        )
      )
    ),

    // ─── Receive Unexpected Item Modal ───
    h(window.UnexpectedItemModal, {
      open: unexpectedOpen,
      onClose: function() { setUnexpectedOpen(false); },
      lookupUrl: '/wfs-shipments/plans/' + planId + '/product-lookup',
      submitUrl: '/wfs-shipments/plans/' + planId + '/unexpected-item',
      onSuccess: function(resp) {
        showToast('Received ' + resp.qty_received + ' unit(s) of ' + resp.sku + ' on PO ' + resp.po_number, 'success');
        loadUnexpectedReceived();
        if (onRefresh) onRefresh();
      },
    }),

    // ─── Unexpected Items Image Lightbox ───
    unexpLightbox && h(window.ImageLightbox, {
      images: unexpLightbox.images,
      index: unexpLightbox.index,
      onIndexChange: function(i) { setUnexpLightbox(function(prev) { return prev ? { images: prev.images, index: i } : null; }); },
      onClose: function() { setUnexpLightbox(null); },
    }),

    // ─── Mark Missing Confirmation Modal ───
    h(Modal, { open: !!missingPending, onClose: function() { setMissingPending(null); setMissingNote(''); }, title: 'Mark Units Missing', width: 440 },
      missingPending && (function() {
        var mp = missingPending.item;
        var remaining = mp.quantity - (mp.quantity_packed || 0) - (mp.quantity_missing || 0);
        var thumbUrl = (mp.images && mp.images.length) ? mp.images[0] : mp.image_1_url;
        var ps = function(bg, fg) { return { fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: bg, color: fg, letterSpacing: '0.03em', whiteSpace: 'nowrap', display: 'inline-block' }; };
        return h('div', null,
          h('div', { style: { display: 'flex', alignItems: 'center', gap: 10, padding: 12, background: C.surface, borderRadius: 6, marginBottom: 14 } },
            thumbUrl
              ? h('img', { src: thumbUrl, style: { width: 48, height: 48, borderRadius: 6, objectFit: 'cover', flexShrink: 0 } })
              : h('div', { style: { width: 48, height: 48, borderRadius: 6, background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } }, h(LIcon, { name: 'package', size: 22, color: C.textDim })),
            h('div', { style: { flex: 1, minWidth: 0 } },
              h('div', { style: { fontSize: 13, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 6 } }, mp.vendor_title || mp.sku || ''),
              mp.sku && h('div', { style: { display: 'flex', gap: 4, flexWrap: 'wrap' } },
                h('span', { style: ps(C.accent + '14', C.accent) }, 'SKU: ' + mp.sku))
            )
          ),
          h('div', { style: { fontSize: 13, color: C.text, marginBottom: 6 } },
            'Mark ', h('strong', { style: { color: '#F87171' } }, remaining + ' unit' + (remaining === 1 ? '' : 's')), ' as missing?'
          ),
          h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 14 } },
            'These units will be excluded from this WFS shipment. You can unmark them later if recovered.'
          ),
          h('div', { style: { marginBottom: 16 } },
            h('label', { style: { display: 'block', fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 } }, 'Note (optional)'),
            h('textarea', {
              value: missingNote,
              onChange: function(e) { setMissingNote(e.target.value); },
              placeholder: 'Reason or context for the log',
              rows: 3,
              style: { width: '100%', background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }
            })
          ),
          h('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } },
            h(Btn, { onClick: function() { setMissingPending(null); setMissingNote(''); } }, 'Cancel'),
            h(Btn, { variant: 'primary', onClick: function() { markMissing(mp, missingNote); setMissingPending(null); setMissingNote(''); },
              style: { background: '#DC2626', borderColor: '#DC2626' } },
              h(LIcon, { name: 'alert-triangle', size: 14 }), ' Mark Missing')
          )
        );
      })()
    ),

    // ─── Bulk Mark Missing Confirmation Modal ───
    h(Modal, { open: !!bulkMissingConfirm, onClose: function() { if (!bulkMissingSaving) setBulkMissingConfirm(null); }, title: 'Mark Items as Missing', width: 480 },
      bulkMissingConfirm && (function() {
        var bm = bulkMissingConfirm;
        var totalUnits = bm.items.reduce(function(s, it) { return s + Math.max(0, it.quantity - (it.quantity_packed || 0) - (it.quantity_missing || 0)); }, 0);
        return h('div', null,
          h('div', { style: { fontSize: 13, color: C.text, marginBottom: 6 } },
            'Mark ', h('strong', { style: { color: '#F87171' } }, totalUnits + ' unit' + (totalUnits === 1 ? '' : 's')),
            ' across ', h('strong', null, bm.items.length + ' SKU' + (bm.items.length === 1 ? '' : 's')),
            ' as missing?'),
          h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12 } },
            'These units will be excluded from this WFS shipment and marked missing on the PO. You can unmark them later if recovered.'),
          h('div', { style: { maxHeight: 200, overflow: 'auto', padding: '6px 10px', background: C.surface, borderRadius: 6, marginBottom: 14, fontSize: 11, color: C.textMuted } },
            bm.items.map(function(it) {
              var rem = Math.max(0, it.quantity - (it.quantity_packed || 0) - (it.quantity_missing || 0));
              return h('div', { key: it.id, style: { padding: '3px 0' } }, (it.product_sku || it.sku || '—') + ' — ' + rem + ' unit' + (rem === 1 ? '' : 's'));
            })
          ),
          h('div', { style: { marginBottom: 16 } },
            h('label', { style: { display: 'block', fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 } }, 'Shared note (optional)'),
            h('textarea', {
              value: bm.note,
              onChange: function(e) { var v = e.target.value; setBulkMissingConfirm(function(prev) { return prev ? Object.assign({}, prev, { note: v }) : prev; }); },
              placeholder: 'Reason or context (applied to every selected item)',
              rows: 2,
              style: { width: '100%', background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }
            })
          ),
          h('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } },
            h(Btn, { onClick: function() { setBulkMissingConfirm(null); }, disabled: bulkMissingSaving }, 'Cancel'),
            h(Btn, { variant: 'primary', disabled: bulkMissingSaving,
              onClick: function() { executeBulkMarkMissing(bm.items, bm.note); },
              style: { background: '#DC2626', borderColor: '#DC2626' } },
              h(LIcon, { name: 'alert-triangle', size: 14 }), ' ', bulkMissingSaving ? 'Marking…' : 'Mark Missing')
          )
        );
      })()
    ),

    // ─── Image Lightbox ───
    lightbox && h('div', { onClick: function() { setLightbox(null); },
      style: { position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' } },
      h('button', { onClick: function() { setLightbox(null); },
        style: { position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 28, lineHeight: 1, padding: 8, zIndex: 10001 } }, '\u00D7'),
      lightbox.images.length > 1 && h('div', { style: { position: 'absolute', top: 20, left: '50%', transform: 'translateX(-50%)', color: '#fff', fontSize: 13, fontWeight: 600, zIndex: 10001 } },
        (lightbox.index + 1) + ' / ' + lightbox.images.length),
      lightbox.images.length > 1 && h('button', { onClick: function(e) { e.stopPropagation(); setLightbox(function(prev) { return { images: prev.images, index: (prev.index - 1 + prev.images.length) % prev.images.length }; }); },
        style: { position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 24, fontWeight: 700, width: 44, height: 44, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001 } }, '\u2039'),
      lightbox.images.length > 1 && h('button', { onClick: function(e) { e.stopPropagation(); setLightbox(function(prev) { return { images: prev.images, index: (prev.index + 1) % prev.images.length }; }); },
        style: { position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 24, fontWeight: 700, width: 44, height: 44, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001 } }, '\u203A'),
      h('img', { src: lightbox.images[lightbox.index], onClick: function(e) { e.stopPropagation(); },
        style: { maxWidth: 'calc(85vw * var(--invzoom, 1))', maxHeight: 'calc(85vh * var(--invzoom, 1))', objectFit: 'contain', borderRadius: 8, cursor: 'default' } }),
      lightbox.images.length > 1 && h('div', { onClick: function(e) { e.stopPropagation(); },
        style: { position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 8, padding: '8px 12px', background: 'rgba(0,0,0,0.6)', borderRadius: 8 } },
        lightbox.images.map(function(url, i) {
          return h('img', { key: i, src: url, onClick: function() { setLightbox(function(prev) { return { images: prev.images, index: i }; }); },
            style: { width: 52, height: 52, objectFit: 'cover', borderRadius: 6, border: '2px solid ' + (i === lightbox.index ? '#fff' : 'transparent'), cursor: 'pointer', opacity: i === lightbox.index ? 1 : 0.6, transition: 'all 0.15s' } });
        })
      )
    )
  );
}

// Expose on window for cross-file access
window.WfsShipmentsPage = WfsShipmentsPage;
window.WfsDetailPage = WfsDetailPage;
window.WfsBoxPackingView = WfsBoxPackingView;

;
/* ===== app-meli.js ===== */
// MELI Shipments — Mercado Libre Full inbound module (P0: list, create, detail, filing).
// No marketplace create-API exists — filing happens in the ML Seller Center; this module
// plans/packs/tracks locally (docs/15-meli-shipments.md). List mirrors app-wfs.js
// (pills/search/columns); the detail wizard mirrors app-fba.js FbaDetailPage
// (status-driven stepper + Continue-in-header).
/* global React, useState, useEffect, api, showToastGlobal, C, DataTable, Modal, Card, Btn, Inp, Badge, LIcon, CopyCell, SearchInput */

var MELI_STATUS_COLORS = {
  draft: '#94A3B8', packing: '#F59E0B', packed: '#F59E0B', filed: '#3483FA',
  shipped: '#8B5CF6', receiving: '#22C55E', closed: '#16A34A', cancelled: '#EF4444',
};

// Forward-only ladder (mirrors server MELI_STATUS_ORDER) — used for "reached" checks.
// Filing precedes packing: the real flow files in Seller Center BEFORE packing boxes.
var MELI_STATUS_ORDER = ['draft', 'filed', 'packing', 'packed', 'shipped', 'receiving', 'closed'];

function MeliBadge(props) {
  var st = props.status || 'draft';
  var color = MELI_STATUS_COLORS[st] || C.textMuted;
  return React.createElement('span', { style: { background: color + '18', color: color, padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' } }, st);
}

// The plan items Mercado Libre could not tell apart on the filing sheet: no ML Code (2026-09-29)
function meliItemsWithoutCode(items) {
  return (items || []).filter(function(i) { return !i || !String(i.ml_code || '').trim(); });
}
// ... and no listing number ("# de Publicación", the Mexican item behind the original listing).
// A product with an alias listing needs it on the sheet, or Mercado Libre answers "not linked to
// any listing" (plan 6, 2026-09-29). The 30-minute sync stamps it.
function meliItemsWithoutListing(items) {
  return (items || []).filter(function(i) { return !i || !String(i.ml_item_id || '').trim(); });
}
// The number as the sheet and Mercado Libre's pages show it: digits, without the MLM prefix.
function meliListingNumber(mlItemId) {
  var d = String(mlItemId || '').replace(/^[A-Z]+/, '');
  return /^\d+$/.test(d) ? d : '';
}

function MeliShipmentsPage(props) {
  var showToast = props.showToast, onNavigate = props.onNavigate, createWithPO = props.createWithPO;
  var createFromPORef = React.useRef(null); // set when the page was opened from a PO's Create MELI Shipment button
  var _p = useState([]), plans = _p[0], setPlans = _p[1];
  var _l = useState(true), loading = _l[0], setLoading = _l[1];
  var _f = useState(''), statusFilter = _f[0], setStatusFilter = _f[1];
  var _q = useState(''), search = _q[0], setSearch = _q[1];
  var _m = useState(false), createOpen = _m[0], setCreateOpen = _m[1];
  var _e = useState([]), eligible = _e[0], setEligible = _e[1];
  var _el = useState(false), eligibleLoading = _el[0], setEligibleLoading = _el[1];
  var _s = useState({}), selectedPos = _s[0], setSelectedPos = _s[1];
  var _c = useState(false), creating = _c[0], setCreating = _c[1];

  var load = function() {
    setLoading(true);
    // One dataset, filtered client-side by the status pills (mirrors WFS). The bare list
    // endpoint excludes cancelled server-side, so fetch cancelled separately to keep that
    // pill's count/tab working — both calls are the documented GET /plans(?status=) shape.
    Promise.all([api('/meli-shipments/plans'), api('/meli-shipments/plans?status=cancelled')])
      .then(function(results) { setPlans((results[0] || []).concat(results[1] || [])); setLoading(false); })
      .catch(function(e) { showToast(e.message, 'error'); setLoading(false); });
  };
  useEffect(load, []);

  // Arriving from a PO (MELI Direct, Anthony 2026-09-29; mirrors the FBA and WFS pages): the create
  // window opens with that PO already selected.
  useEffect(function() {
    if (!createWithPO) return;
    createFromPORef.current = createWithPO;
    setEligible([]); setSelectedPos({});
    setEligibleLoading(true);
    setCreateOpen(true);
    api('/meli-shipments/eligible-pos').then(function(rows) {
      rows = rows || [];
      setEligible(rows);
      var target = rows.find(function(po) { return Number(po.id) === Number(createWithPO); });
      if (target) { var sel = {}; sel[target.id] = true; setSelectedPos(sel); }
      else showToast('That PO is not available for a MELI shipment (already on one, or not submitted yet)', 'error');
    }).catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setEligibleLoading(false); });
  }, [createWithPO]);

  // Closing the window without creating goes back to the PO it was opened from
  var closeCreate = function() {
    setCreateOpen(false);
    if (createFromPORef.current) {
      var poId = createFromPORef.current;
      createFromPORef.current = null;
      onNavigate('po-detail', poId);
    }
  };

  var openCreate = function() {
    // Open the modal IMMEDIATELY with a loading state (mirrors FBA/WFS) — awaiting the
    // fetch first makes the button feel dead while the eligible list loads.
    createFromPORef.current = null;
    setEligible([]); setSelectedPos({});
    setEligibleLoading(true);
    setCreateOpen(true);
    api('/meli-shipments/eligible-pos').then(function(rows) { setEligible(rows || []); })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setEligibleLoading(false); });
  };

  var createPlan = function() {
    var ids = Object.keys(selectedPos).filter(function(k) { return selectedPos[k]; }).map(Number);
    if (!ids.length) { showToast('Select at least one PO', 'error'); return; }
    setCreating(true);
    api('/meli-shipments/plans', { method: 'POST', body: JSON.stringify({ po_ids: ids }) }).then(function(r) {
      setCreating(false); setCreateOpen(false); createFromPORef.current = null;
      showToast('MELI plan #' + r.id + ' created (' + r.item_count + ' SKUs)');
      onNavigate('meli-detail', r.id);
    }).catch(function(e) { setCreating(false); showToast(e.message, 'error'); });
  };

  // Selection summary + Vendor Direct rule (mirrors WFS/FBA): a meli_direct PO ships
  // from the vendor and must be the only PO on its plan — no mixing.
  var selPOs = eligible.filter(function(po) { return !!selectedPos[po.id]; });
  var selSkus = selPOs.reduce(function(s, po) { return s + (po.sku_count || 0); }, 0);
  var selUnits = selPOs.reduce(function(s, po) { return s + (po.total_units || 0); }, 0);
  var hasDirectSelected = selPOs.some(function(po) { return !!po.meli_direct; });

  // Status pills + client-side filtering (mirrors WFS list: All = active, excl cancelled/closed)
  var statuses = ['', 'draft', 'packing', 'packed', 'filed', 'shipped', 'receiving', 'closed', 'cancelled'];
  var statusCounts = {};
  plans.forEach(function(p) { statusCounts[p.status] = (statusCounts[p.status] || 0) + 1; });
  statusCounts[''] = plans.filter(function(p) { return p.status !== 'cancelled' && p.status !== 'closed'; }).length;
  var filtered = statusFilter
    ? plans.filter(function(p) { return p.status === statusFilter; })
    : plans.filter(function(p) { return p.status !== 'cancelled' && p.status !== 'closed'; });
  if (search) {
    var q = search.toLowerCase();
    filtered = filtered.filter(function(p) {
      return (p.po_numbers || '').toLowerCase().indexOf(q) !== -1 ||
        (p.ml_inbound_ref || '').toLowerCase().indexOf(q) !== -1 ||
        (p.carrier || '').toLowerCase().indexOf(q) !== -1 ||
        (p.tracking_number || '').toLowerCase().indexOf(q) !== -1;
    });
  }

  // Columns mirror the WFS list: Created | PO # | marketplace ref | Status | SKUs | Units |
  // packed progress bar | Tracking (MELI has no boxes/FC/vendor rollups in P0).
  var columns = [
    { key: 'created_at', label: 'Created', render: function(r) { return r.created_at ? new Date(r.created_at).toLocaleDateString() : ''; } },
    { key: 'po_numbers', label: 'PO #', render: function(r) {
        return r.po_numbers
          ? React.createElement('span', { style: { display: 'inline-flex', alignItems: 'center', gap: 6 } },
              React.createElement(CopyCell, { value: r.po_numbers }, React.createElement('span', { style: { fontFamily: 'inherit', fontWeight: 600 } }, r.po_numbers)),
              // ships from the vendor straight to Mercado Libre (MELI Direct, 2026-09-29)
              r.ship_mode === 'vendor_direct' && React.createElement('span', { title: 'Ships from the vendor straight to Mercado Libre', style: { fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 8, background: '#8b5cf618', color: '#8b5cf6', whiteSpace: 'nowrap' } }, 'Vendor Direct'))
          : React.createElement('span', { style: { color: C.textMuted } }, '--');
      } },
    { key: 'ml_inbound_ref', label: 'ML Ref', render: function(r) {
        return r.ml_inbound_ref
          ? React.createElement(CopyCell, { value: r.ml_inbound_ref }, React.createElement('span', { style: { fontFamily: 'inherit', fontSize: 11 } }, r.ml_inbound_ref))
          : React.createElement('span', { style: { color: C.textMuted, fontSize: 11 } }, '--');
      } },
    { key: 'status', label: 'Status', render: function(r) { return React.createElement(MeliBadge, { status: r.status }); } },
    { key: 'sku_count', label: 'SKUs', render: function(r) { return parseInt(r.sku_count) || 0; } },
    { key: 'total_units', label: 'Units', render: function(r) { return parseInt(r.total_units) || 0; } },
    { key: 'packed_progress', label: 'Packed',
      sortValue: function(r) { var t = parseInt(r.total_units) || 0; return t > 0 ? (parseInt(r.packed_units) || 0) / t : 0; },
      render: function(r) {
        var total = parseInt(r.total_units) || 0;
        var packed = parseInt(r.packed_units) || 0;
        var pct = total > 0 ? Math.round(packed / total * 100) : 0;
        var color = pct === 0 ? C.textMuted : (pct >= 100 ? '#10B981' : C.accent);
        return React.createElement('div', { title: pct + '% packed', style: { width: 90, cursor: 'help' } },
          React.createElement('div', { style: { fontSize: 13, fontWeight: 500, color: C.text, marginBottom: 4, textAlign: 'center' } }, packed + ' / ' + total),
          React.createElement('div', { style: { height: 5, background: C.border, borderRadius: 3, overflow: 'hidden' } },
            React.createElement('div', { style: { height: '100%', width: pct + '%', background: color, transition: 'width 0.2s' } })
          )
        );
      }
    },
    { key: 'tracking_number', label: 'Tracking', render: function(r) {
        return r.tracking_number
          ? React.createElement('span', { style: { fontFamily: 'inherit', fontSize: 11 } }, (r.carrier ? r.carrier + ' ' : '') + r.tracking_number)
          : React.createElement('span', { style: { color: C.textMuted, fontSize: 11 } }, '--');
      } },
  ];

  return React.createElement('div', { style: { animation: 'fadeIn 0.3s ease' } },
    React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 } },
      React.createElement('h2', { style: { fontSize: 20, fontWeight: 700, color: C.text } }, 'MELI Shipments'),
      React.createElement(Btn, { variant: 'primary', onClick: openCreate }, React.createElement(LIcon, { name: 'plus', size: 14 }), ' New MELI Shipment')
    ),
    // Status filter tabs (mirrors WFS)
    React.createElement('div', { style: { display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' } },
      statuses.map(function(s) {
        var cnt = statusCounts[s] || 0;
        var isActive = statusFilter === s;
        var label = s ? s.replace(/_/g, ' ') : 'All';
        return React.createElement('button', { key: s, onClick: function() { setStatusFilter(s); },
          className: isActive ? '' : 'filter-pill',
          style: { padding: '6px 14px', borderRadius: 6, border: '1px solid transparent', cursor: 'pointer', fontSize: 12, fontWeight: 500, fontFamily: 'inherit', transition: 'all 0.15s',
            background: isActive ? C.accent : C.surface, color: isActive ? '#fff' : C.textMuted }
        }, label + (cnt ? ' (' + cnt + ')' : ''));
      })
    ),
    // Search (mirrors WFS)
    React.createElement('div', { style: { marginBottom: 16 } }, React.createElement(SearchInput, { type: 'text', value: search, onChange: function(e) { setSearch(e.target.value); },
      placeholder: 'Search PO#, ML Ref, Carrier, Tracking...',
      style: { width: '100%', background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '10px 14px', borderRadius: 8, fontSize: 13, outline: 'none' }
    })),
    // Table
    React.createElement(DataTable, {
      columns: columns,
      data: filtered, storageKey: 'meli-shipments', loading: loading,
      defaultSort: 'created_at', defaultSortDir: 'desc',
      rowHref: function(p) { return '/meli/' + p.id; },
      onRowClick: function(p) { onNavigate('meli-detail', p.id); },
      emptyTitle: 'No MELI Shipments', emptySub: 'Create your first MELI shipment from a checked-in MELI Purchase Order'
    }),
    // Create Modal — mirrors the WFS/FBA "New Shipment" modal (app-wfs.js): wide modal,
    // scrollable eligible-PO list with custom checkboxes + SKU/unit pills, Vendor Direct
    // badge + ships-alone rule, selected-count summary, disabled-until-selection Create.
    React.createElement(Modal, { open: createOpen, onClose: closeCreate, title: 'New MELI Shipment', width: 900, maxHeight: 'calc(95vh * var(--invzoom, 1))' },
      React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 14, height: 'calc(72vh * var(--invzoom, 1))' } },
        React.createElement('div', { style: { fontSize: 12, color: C.textMuted } },
          'Items are snapshotted from the PO lines; you file the inbound in the ML Seller Center after packing.'),
        React.createElement('label', { style: { fontSize: 12, fontWeight: 500, color: C.textMuted } }, 'Purchase Orders (checked-in or Vendor Direct, MELI warehouses)'),
        React.createElement('div', { style: { flex: '1 1 auto', minHeight: 180, overflowY: 'auto', border: '1px solid ' + C.border, borderRadius: 6, padding: 4 } },
          eligibleLoading
            ? React.createElement('p', { style: { color: C.textMuted, fontSize: 12, padding: 8, margin: 0 } }, 'Loading eligible POs…')
            : eligible.length === 0
            ? React.createElement('p', { style: { color: C.danger, fontSize: 12, padding: 8, margin: 0 } }, 'No checked-in or Vendor Direct MELI warehouse POs found')
            : eligible.map(function(po) {
                var checked = !!selectedPos[po.id];
                var isThisVD = !!po.meli_direct;
                // Vendor Direct rule (mirrors FBA/WFS): a VD PO ships alone — no mixing
                var blocked = !checked && selPOs.length > 0 && (isThisVD || hasDirectSelected);
                var blockReason = blocked
                  ? (isThisVD
                      ? 'Unavailable: Vendor Direct POs ship by themselves. Deselect the other POs first.'
                      : 'Unavailable: a Vendor Direct PO is already selected and must ship alone. Deselect it first.')
                  : undefined;
                return React.createElement('div', { key: po.id, title: blockReason, onClick: function() {
                    if (blocked) { showToast(blockReason, 'error'); return; }
                    var n = Object.assign({}, selectedPos); n[po.id] = !checked; setSelectedPos(n);
                  }, style: { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 4, cursor: blocked ? 'not-allowed' : 'pointer', opacity: blocked ? 0.4 : 1, background: checked ? 'rgba(79,110,247,0.08)' : 'transparent', transition: 'background 0.15s', userSelect: 'none' } },
                  React.createElement('div', { style: { width: 20, height: 20, minWidth: 20, borderRadius: 4, border: '2px solid ' + (checked ? C.accent : C.textDim), background: checked ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' } },
                    checked && React.createElement(LIcon, { name: 'check', size: 13, color: '#fff' })
                  ),
                  React.createElement('div', { style: { flex: 1, fontSize: 14, minWidth: 0 } },
                    React.createElement('div', null,
                      React.createElement('span', { style: { fontWeight: 600, fontFamily: 'inherit' } }, po.po_number),
                      React.createElement('span', { style: { color: C.textMuted, marginLeft: 8 } }, (po.vendor_name || '') + ' -- ' + (po.wh_code || ''))
                    ),
                    (function() {
                      var pillBase = { fontSize: 11, fontWeight: 600, padding: '0 9px', borderRadius: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4, height: 22, lineHeight: 1, boxSizing: 'border-box', whiteSpace: 'nowrap', fontFamily: 'inherit' };
                      return React.createElement('div', { style: { display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' } },
                        React.createElement('span', { style: Object.assign({}, pillBase, { background: C.accent + '18', color: C.accent }) }, (po.sku_count || 0) + ' SKU' + ((po.sku_count || 0) !== 1 ? 's' : '')),
                        React.createElement('span', { style: Object.assign({}, pillBase, { background: '#f59e0b18', color: '#f59e0b' }) }, (po.total_units || 0).toLocaleString() + ' units'),
                        po.checked_in_date && React.createElement('span', { style: Object.assign({}, pillBase, { background: C.textMuted + '18', color: C.textMuted, fontWeight: 500 }) }, 'Checked in ' + new Date(po.checked_in_date).toLocaleDateString()),
                        isThisVD && React.createElement('span', { style: Object.assign({}, pillBase, { background: '#8b5cf618', color: '#8b5cf6' }) }, 'Vendor Direct')
                      );
                    })()
                  )
                );
              })
        ),
        hasDirectSelected && React.createElement('div', { style: { fontSize: 11, color: '#8b5cf6', display: 'flex', alignItems: 'center', gap: 4 } },
          React.createElement(LIcon, { name: 'truck', size: 12 }), ' Vendor Direct: ships from the vendor straight to Mercado Libre. The PO moves to Receiving when the shipment is created.'),
        selPOs.length > 0 && React.createElement('div', null,
          React.createElement('div', { style: { fontSize: 11, color: C.accent, fontWeight: 500 } },
            selPOs.length + ' PO' + (selPOs.length !== 1 ? 's' : '') + ' selected -- ' + selSkus + ' SKU' + (selSkus !== 1 ? 's' : '') + ', ' + selUnits.toLocaleString() + ' units'
          ),
          hasDirectSelected && React.createElement('div', { style: { fontSize: 11, color: '#8b5cf6', fontWeight: 500, marginTop: 4, padding: '4px 8px', background: '#8b5cf610', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 4 } },
            React.createElement(LIcon, { name: 'truck', size: 12 }), ' Vendor Direct — ships from the vendor to Mercado Libre Full.'
          )
        ),
        React.createElement('div', { style: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 } },
          React.createElement(Btn, { onClick: closeCreate }, 'Cancel'),
          React.createElement(Btn, { variant: 'primary', onClick: createPlan, disabled: creating || !selPOs.length }, creating ? 'Creating...' : 'Create MELI Shipment')
        )
      )
    )
  );
}

// ─── Mercado Libre product labels (Anthony 2026-09-29, approved) ───
// Every unit sent to Full carries a label Mercado Libre reads by the ML Code: Code 128 of the
// code, the code in bold, our vendor title and "SKU: ...", the shape of the label file the
// Seller Center hands out after filing. Templates live under Settings > Templates > MELI.
function meliLabelTemplates(templates) {
  return (templates || []).filter(function(t) { return (t.category || '') === 'meli'; });
}
// Only a product with an ML Code can be labelled. The others are named and refused, never
// printed with the GTIN or the SKU in place of the code: Mercado Libre could not read that label.
function meliPrintableItems(items) {
  return (items || []).filter(function(i) { return i && String(i.ml_code || '').trim(); });
}
function meliLabelFor(item) {
  return { code: String(item.ml_code || '').trim(), title: item.title || '', sku: item.sku || '' };
}
// One label per unit, products in SKU order (the order the pick list and the filing sheet use).
function meliExpandLabels(items, counts) {
  var out = [];
  meliPrintableItems(items).slice().sort(function(a, b) { return String(a.sku || '').localeCompare(String(b.sku || '')); })
    .forEach(function(item) {
      var n = parseInt(counts && counts[item.id]) || 0;
      for (var k = 0; k < n; k++) out.push(meliLabelFor(item));
    });
  return out;
}

// ─── MELI Detail Page (4-step wizard, mirrors FbaDetailPage's stepper + Continue-in-header;
// side cards keep the shared Card idiom) ───
function MeliDetailPage(props) {
  var planId = props.planId, showToast = props.showToast, onNavigate = props.onNavigate;
  var _p = useState(null), plan = _p[0], setPlan = _p[1];
  var _m = useState({}), meta = _m[0], setMeta = _m[1];
  var _b = useState(false), busy = _b[0], setBusy = _b[1];
  var _st = useState(null), manualStep = _st[0], setManualStep = _st[1];
  var _lt = useState([]), labelTemplates = _lt[0], setLabelTemplates = _lt[1];
  var _pm = useState(null), printModal = _pm[0], setPrintModal = _pm[1];
  var _sl = useState(null), selectedLabelTpl = _sl[0], setSelectedLabelTpl = _sl[1];
  var _pq = useState({}), printQty = _pq[0], setPrintQty = _pq[1];

  // Label templates once per visit (the FBA page does the same).
  useEffect(function() {
    api('/settings/label-templates').then(function(t) { setLabelTemplates(t || []); })
      .catch(function(e) { console.error('[silent-catch]', e); });
  }, []);

  // Opens the label window for the given items (the whole plan when none are given); the count
  // per product starts at its plan units, a product without an ML Code starts at nothing.
  var openLabelPrint = function(itemsToPrint) {
    var list = (itemsToPrint || (plan && plan.items) || []).slice()
      .sort(function(a, b) { return String(a.sku || '').localeCompare(String(b.sku || '')); });
    var counts = {};
    meliPrintableItems(list).forEach(function(it) { counts[it.id] = parseInt(it.quantity) || 0; });
    setPrintQty(counts);
    var tpls = meliLabelTemplates(labelTemplates);
    var def = tpls.find(function(t) { return t.is_default; }) || tpls[0] || null;
    setSelectedLabelTpl(def ? def.id : null);
    setPrintModal({ items: list });
  };
  var labelsToPrint = function() {
    if (!printModal) return [];
    return meliExpandLabels(printModal.items, printQty);
  };
  var executeLabelPrint = function() {
    var tpl = labelTemplates.find(function(t) { return t.id === selectedLabelTpl; }) || null;
    var labels = labelsToPrint();
    if (!labels.length) { showToast('Enter a label count for at least one product with an ML Code', 'error'); return; }
    var html = productLabelPagesHtml(labels.map(function(l) { return productLabelHtml(l, tpl, { skuPrefix: true }); }), tpl);
    if (!openLabelPrintWindow(html, 'ML Labels, Plan #' + planId, tpl)) { showToast('Pop-up blocked. Allow pop-ups for this site.', 'error'); return; }
    setPrintModal(null);
  };
  // One PDF with every label, for a vendor direct plan: the vendor applies the labels, so the
  // file travels by e-mail. Works for a dock plan too.
  var downloadLabelPdf = function() {
    var tpl = labelTemplates.find(function(t) { return t.id === selectedLabelTpl; }) || null;
    var labels = labelsToPrint();
    if (!labels.length) { showToast('Enter a label count for at least one product with an ML Code', 'error'); return; }
    var doc;
    try { doc = productLabelsPdf(labels, tpl, { skuPrefix: true }); }
    catch (e) { showToast('PDF build failed: ' + e.message, 'error'); return; }
    if (!doc) { showToast('PDF library not loaded', 'error'); return; }
    var poPart = (plan && plan.pos || []).map(function(p) { return p.po_number; }).filter(Boolean).join('-');
    var name = 'ML-Labels-Plan-' + planId + (poPart ? '-PO-' + poPart : '') + '.pdf';
    var url = URL.createObjectURL(doc.output('blob'));
    var a = document.createElement('a');
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click();
    setTimeout(function() { document.body.removeChild(a); URL.revokeObjectURL(url); }, 200);
    showToast('Downloaded ' + labels.length + ' label' + (labels.length === 1 ? '' : 's'));
    setPrintModal(null);
  };

  var load = function() {
    // No plan number, nothing to load (Anthony 2026-09-29, error_log 706: the page asked the server
    // for plan "null" in the instant before its number arrived). FBA and WFS wait the same way.
    if (!planId) return;
    api('/meli-shipments/plans/' + planId).then(function(p) {
      setPlan(p);
      setMeta({ ml_inbound_ref: p.ml_inbound_ref || '', appointment_date: p.appointment_date ? p.appointment_date.slice(0, 10) : new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
        carrier: (p.shipments[0] && p.shipments[0].carrier) || '', tracking_number: (p.shipments[0] && p.shipments[0].tracking_number) || '',
        notes: p.notes || '', box_count: p.box_count || '' });
    }).catch(function(e) { showToast(e.message, 'error'); });
  };
  useEffect(load, [planId]);

  var saveMeta = function() {
    setBusy(true);
    api('/meli-shipments/plans/' + planId + '/meta', { method: 'PUT', body: JSON.stringify(meta) }).then(function() {
      setBusy(false); showToast('Saved'); load();
    }).catch(function(e) { setBusy(false); showToast(e.message, 'error'); });
  };

  var setStatus = function(st) {
    setBusy(true);
    // Mark as Shipped sends the carrier / tracking typed on the page (9-27 sweep M10: they were
    // lost unless Save was clicked first).
    var body = { status: st };
    if (st === 'shipped') { body.carrier = meta.carrier || ''; body.tracking_number = meta.tracking_number || ''; }
    api('/meli-shipments/plans/' + planId + '/status', { method: 'POST', body: JSON.stringify(body) }).then(function(r) {
      // FBA post-action pattern: drop manual navigation so the wizard lands on the
      // status-derived step after the status advances.
      setBusy(false);
      var closedPos = (r && r.closed_pos) || [];
      showToast(st === 'closed' ? 'Shipment closed' + (closedPos.length ? ' — ' + closedPos.length + ' PO(s) closed' : '') : 'Plan → ' + st);
      setManualStep(null); load();
    }).catch(function(e) { setBusy(false); showToast(e.message, 'error'); });
  };
  // Close = received by Mercado Libre. Forward-only, so it asks first; a vendor-direct plan takes its
  // PO(s) to closed with it (the server does that).
  var closePlan = async function() {
    var direct = plan.ship_mode === 'vendor_direct';
    var ok = await appConfirm('Close this shipment as received by Mercado Libre?' + (direct ? ' Its PO(s) will be closed too.' : '') + ' This cannot be undone.', { okLabel: 'Close Shipment' });
    if (ok) setStatus('closed');
  };

  // Cancel (P1b, 2026-10-20): allowed from shipped too, and the server gives back what the plan took —
  // packed units off the PO ledger (into staging), missing marks cleared, PO(s) a step back.
  var cancelPlan = async function() {
    var ok = await appConfirm(cancelMessage(), { okLabel: 'Cancel Plan', danger: true });
    if (!ok) return;
    api('/meli-shipments/plans/' + planId, { method: 'DELETE' }).then(function(r) {
      var rs = (r && r.restored) || {};
      var errs = (r && r.restore_errors) || [];
      var back = (rs.packed || rs.missing) ? ' — ' + (rs.packed || 0) + ' packed / ' + (rs.missing || 0) + ' missing unit(s) back on the PO' : '';
      showToast('Plan cancelled' + back + (errs.length ? '; ' + errs.length + ' reverse(s) failed — see the error log' : ''), errs.length ? 'error' : undefined);
      onNavigate('meli-shipments');
    }).catch(function(e) { showToast(e.message, 'error'); });
  };
  var cancelMessage = function() {
    var its = (plan && plan.items) || [];
    var packed = its.reduce(function(a, i) { return a + (parseInt(i.quantity_packed) || 0); }, 0);
    var missing = its.reduce(function(a, i) { return a + (parseInt(i.quantity_missing) || 0); }, 0);
    var pos = ((plan && plan.pos) || []).map(function(po) { return po.po_number; }).join(', ');
    var direct = plan && plan.ship_mode === 'vendor_direct';
    var msg = 'Cancel this plan?';
    if (plan && plan.status === 'shipped') msg += ' It is marked shipped — cancelling takes it back as if it never left (Mercado Libre is not notified).';
    if (packed || missing) msg += ' ' + (packed ? packed + ' packed unit(s) go back on PO ' + pos + (missing ? ' and ' + missing + ' missing mark(s) are cleared' : '') : missing + ' missing mark(s) on PO ' + pos + ' are cleared') + '.';
    msg += ' The PO(s) return to ' + (direct ? 'Confirmed' : 'Checked-in') + ' and become eligible for a new plan.';
    return msg;
  };
  // Un-ship (P1b): the one step back on the ladder — shipped → where the plan was (the server decides:
  // packed / packing / filed), its dock PO(s) → Checked-in. Only while nothing says ML is receiving it.
  var unshipPlan = async function() {
    var direct = plan.ship_mode === 'vendor_direct';
    var ok = await appConfirm('Undo Mark as Shipped? The plan goes back to ' + (direct ? 'Filed' : 'packing, where it was before shipping, and its PO(s) to Checked-in') + '. Mercado Libre is not notified — amend the filing in Seller Center if it changed.', { okLabel: 'Un-ship' });
    if (!ok) return;
    setBusy(true);
    api('/meli-shipments/plans/' + planId + '/unship', { method: 'POST' }).then(function(r) {
      setBusy(false);
      var n = (r && r.reverted_pos || []).length;
      showToast('Plan → ' + ((r && r.status) || 'packing') + (n ? ' · ' + n + ' PO(s) back to Checked-in' : ''));
      setManualStep(null); load();
    }).catch(function(e) { setBusy(false); showToast(e.message, 'error'); });
  };

  var openFilingSheet = function() { window.open('/api/meli-shipments/plans/' + planId + '/filing-sheet', '_blank'); };

  // Mercado Libre's own per-box labels (Anthony 2026-10-22, PO 30697 / plan #10: "it's not showing me the
  // case labels"). Seller Center issues one per box declared when filing; the server reproduces them from
  // the shipment number, our box numbers and the connected account (GET /plans/:id/ml-box-labels, cookie
  // auth like the Filing Sheet). The declared count lives on the plan (Seller Center Filing card, File
  // step) and is asked here EVERY time, pre-filled with the saved one — Anthony 2026-10-24: "what about
  // if I have to edit the number of boxes? It doesn't let me after the first time" (the Filing card is
  // off-screen on Pack Boxes). Enter prints as saved; a new number is saved first. Not e-mailed to the
  // vendor: the count is known only once the vendor has packed.
  var openMlBoxLabels = async function() {
    if (!plan.ml_inbound_ref) { showToast('File the shipment in Seller Center first — the labels carry Mercado Libre\'s shipment number', 'error'); return; }
    var saved = parseInt(plan.box_count) > 0 ? String(parseInt(plan.box_count)) : '';
    var typed = await appPrompt('How many boxes did you declare in Seller Center for shipment ' + plan.ml_inbound_ref + '? Mercado Libre issues one box label per declared box.', saved, { title: 'ML Box Labels', okLabel: 'Print Labels' });
    if (typed == null) return;
    var count = parseInt(String(typed).trim(), 10);
    if (!(count >= 1) || String(count) !== String(typed).trim()) { showToast('Enter the number of boxes (a whole number, 1 or more)', 'error'); return; }
    if (String(count) !== saved) {
      try { await api('/meli-shipments/plans/' + planId + '/meta', { method: 'PUT', body: JSON.stringify({ box_count: count }) }); }
      catch (e) { showToast(e.message, 'error'); return; }
      load();
    }
    window.open('/api/meli-shipments/plans/' + planId + '/ml-box-labels', '_blank');
  };

  // Step definitions (wizard mirrors FBA): Review → File → Pack → Ship. Filing comes
  // BEFORE packing — Pack Boxes stays locked until the plan is filed (status 'filed'+),
  // enforced by the stepper frontier math (draft's frontier = step 1).
  // Pack screens land in P1 — that step renders a placeholder card for now.
  var steps = ['Review Items', 'File in Seller Center', 'Pack Boxes', 'Ship'];
  var statusStepMap = { draft: 0, filed: 2, packing: 2, packed: 2, shipped: 3, receiving: 3, closed: 3, cancelled: 3 };
  var currentStep = plan ? (statusStepMap[plan.status] != null ? statusStepMap[plan.status] : 0) : 0;
  var step = manualStep != null ? manualStep : currentStep;

  if (!plan) return React.createElement('div', { style: { padding: 40, textAlign: 'center', color: C.textMuted } }, 'Loading...');

  var items = plan.items || [];
  var totalUnits = items.reduce(function(s, i) { return s + (parseInt(i.quantity) || 0); }, 0);
  var totalPacked = items.reduce(function(s, i) { return s + (parseInt(i.quantity_packed) || 0); }, 0);
  var poNumbers = (plan.pos || []).map(function(po) { return po.po_number; }).join(', ');
  var statusIdx = MELI_STATUS_ORDER.indexOf(plan.status);
  var isFiled = statusIdx >= MELI_STATUS_ORDER.indexOf('filed');
  var isShipped = statusIdx >= MELI_STATUS_ORDER.indexOf('shipped');
  var notEnded = ['closed', 'cancelled'].indexOf(plan.status) === -1;
  var canCancel = !['receiving', 'closed', 'cancelled'].includes(plan.status);
  // Forward-only actions (reordered ladder: filing precedes packing): draft → filed;
  // filed/packing/packed → shipped.
  // shipped → receiving → closed are recorded by hand (Anthony 2026-10-20: PO 30297 / plan #6 sat at
  // "shipped" with no way on — nothing watches Mercado Libre's receipts).
  var nextActions = { draft: ['filed'], filed: ['shipped'], packing: ['shipped'], packed: ['shipped'], shipped: ['receiving', 'closed'], receiving: ['closed'] };
  var nexts = nextActions[plan.status] || [];
  var canFile = nexts.indexOf('filed') !== -1;
  var canShip = nexts.indexOf('shipped') !== -1;
  var canReceive = nexts.indexOf('receiving') !== -1;
  // Mark as Packed (docs/15 P1): a dock plan in packing — the server checks the balance and the seals
  var canMarkPacked = plan.status === 'packing' && plan.ship_mode !== 'vendor_direct';
  var canClose = nexts.indexOf('closed') !== -1;

  // Side cards per stage (Anthony 2026-10-20: "remove the Seller Center Filing, Shipping and Purchase
  // orders cards from the Pack Boxes stage ... Keep Seller Center Filing in the File in Seller Center
  // stage. The Shipping should go into the Ship stage."): Review and File keep the POs card, File adds
  // the filing card, Ship pairs the POs with the carrier / tracking card, and Pack Boxes takes the full
  // width for the packing screens. The meta stays editable through PUT /meta at every stage it shows.
  var sideCards = step === 1 ? [renderFilingCard(), renderPosCard()]
    : step === 3 ? [renderShippingCard(), renderPosCard()]
    : step === 0 ? [renderPosCard()]
    : [];

  return React.createElement('div', { style: { animation: 'fadeIn 0.3s ease' } },
    // Header (matches FBA: back arrow, title + subtitle w/ badge; right side = monospace
    // ref + ONE contextual primary Continue/action button that advances the wizard + Cancel.
    // Backward navigation happens by clicking the stepper circles, like FBA — no Back button.)
    React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 } },
      React.createElement('button', { onClick: function() { onNavigate('meli-shipments'); }, style: { background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, padding: 4 } }, React.createElement(LIcon, { name: 'arrow-left', size: 18 })),
      React.createElement('div', null,
        React.createElement('h2', { style: { fontSize: 18, fontWeight: 700, color: C.text, margin: 0 } }, 'MELI Shipment — PO ' + (poNumbers || '#' + plan.id)),
        React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 2 } },
          'Plan #' + plan.id + ' → Mercado Libre Full (USTX01) | ',
          React.createElement(MeliBadge, { status: plan.status })
        )
      ),
      React.createElement('div', { style: { marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 } },
        plan.ml_inbound_ref && React.createElement('span', { style: { fontFamily: 'inherit', fontSize: 11, color: C.textMuted } }, 'ML: ' + plan.ml_inbound_ref),
        step === 0 && notEnded && React.createElement(Btn, { size: 'sm', variant: 'primary', onClick: function() { setManualStep(1); } }, 'Continue to File in Seller Center ', React.createElement(LIcon, { name: 'arrow-right', size: 14 })),
        step === 1 && canFile && React.createElement(Btn, { size: 'sm', variant: 'primary', disabled: busy || !plan.ml_inbound_ref, title: !plan.ml_inbound_ref ? 'Save the ML inbound reference in the Seller Center Filing card first' : undefined, onClick: function() { setStatus('filed'); } }, busy ? 'Saving...' : ['Mark Filed ', React.createElement(LIcon, { key: 'ic', name: 'arrow-right', size: 14 })]),
        step === 1 && isFiled && React.createElement(Btn, { size: 'sm', variant: 'primary', onClick: function() { setManualStep(2); } }, 'Continue ', React.createElement(LIcon, { name: 'arrow-right', size: 14 })),
        step === 2 && canMarkPacked && React.createElement(Btn, { size: 'sm', variant: 'primary', disabled: busy, title: 'Every unit packed or declared missing, every box sealed', onClick: function() { setStatus('packed'); } }, busy ? 'Saving...' : ['Mark as Packed ', React.createElement(LIcon, { key: 'ic', name: 'package-check', size: 14 })]),
        step === 2 && notEnded && React.createElement(Btn, { size: 'sm', variant: canMarkPacked ? undefined : 'primary', onClick: function() { setManualStep(3); } }, 'Continue ', React.createElement(LIcon, { name: 'arrow-right', size: 14 })),
        step === 3 && canShip && React.createElement(Btn, { size: 'sm', variant: 'primary', disabled: busy, onClick: function() { setStatus('shipped'); } }, busy ? 'Saving...' : 'Mark as Shipped'),
        canCancel && React.createElement(Btn, { size: 'sm', onClick: cancelPlan, style: { background: 'rgba(248,113,113,0.1)', color: '#F87171', border: '1px solid rgba(248,113,113,0.3)' } }, React.createElement(LIcon, { name: 'x', size: 13 }), ' Cancel')
      )
    ),

    // Stepper (matches the FBA wizard): completion green is STATUS-driven only — manually
    // peeking ahead never re-colors earlier steps (they stay dim gray, like FBA); future
    // steps beyond the navigable frontier render at 0.4 opacity and are not clickable.
    // The frontier IS the filing lock: a draft plan's frontier is step 1 (File), so
    // Pack Boxes (2) and Ship (3) stay locked until the plan reaches 'filed' (map: filed→2).
    // maxNav keeps the manually-reached/active step clickable+undimmed if a map change
    // ever lets manual navigation outrun `currentStep + 1` (FBA's raw gate).
    React.createElement('div', { style: { display: 'flex', alignItems: 'center', margin: '0 0 24px 0', overflowX: 'auto', padding: '0 4px' } },
      steps.map(function(label, idx) {
        var maxNav = Math.max(currentStep + 1, step);
        var isComplete = idx < currentStep;
        var isActive = idx === step;
        var color = isComplete ? C.success : isActive ? C.accent : C.textDim;
        return React.createElement(React.Fragment, { key: idx },
          idx > 0 && React.createElement('div', { style: { flex: 1, height: 2, background: isComplete ? C.success : C.border, minWidth: 16 } }),
          React.createElement('div', { style: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, cursor: idx <= maxNav ? 'pointer' : 'default', minWidth: 70, opacity: idx > maxNav ? 0.4 : 1 },
            onClick: function() { if (idx <= maxNav) setManualStep(idx); } },
            React.createElement('div', { style: { width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600,
              border: '2px solid ' + color, background: isComplete ? C.success : isActive ? C.accent : 'transparent', color: (isComplete || isActive) ? '#fff' : C.textDim, transition: 'all 0.2s' } },
              isComplete ? React.createElement(LIcon, { name: 'check', size: 13, color: '#fff' }) : String(idx + 1)),
            React.createElement('span', { style: { fontSize: 9, fontWeight: isActive ? 600 : 400, color: color, textAlign: 'center', whiteSpace: 'nowrap' } }, label)
          )
        );
      })
    ),

    // Step content on the left; the stage's side cards on the right (none on Pack Boxes — the
    // packing screens get the whole width; shared Card idioms).
    React.createElement('div', { style: { display: 'grid', gridTemplateColumns: sideCards.length ? '2fr 1fr' : '1fr', gap: 16, alignItems: 'start' } },
      React.createElement('div', null,
        step === 0 && renderReviewStep(),
        step === 1 && renderFileStep(),
        step === 2 && renderPackStep(),
        step === 3 && renderShipStep()
      ),
      sideCards.length > 0 && React.createElement.apply(React, ['div', null].concat(sideCards))
    ),
    renderLabelModal()
  );

  // ─── Print ML Labels window (mirrors the FBA FNSKU window: template, count per product) ───
  function renderLabelModal() {
    var tpls = meliLabelTemplates(labelTemplates);
    var modalItems = printModal ? (printModal.items || []) : [];
    var missing = modalItems.filter(function(i) { return !String(i.ml_code || '').trim(); });
    var total = labelsToPrint().length;
    return React.createElement(Modal, { open: !!printModal, onClose: function() { setPrintModal(null); }, title: 'Print ML Labels', width: 720 },
      printModal && React.createElement('div', null,
        React.createElement('div', { style: { marginBottom: 16 } },
          React.createElement('label', { style: { fontSize: 12.5, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', display: 'block', marginBottom: 8 } }, 'Label Template'),
          tpls.length === 0
            ? React.createElement('div', { style: { fontSize: 13.5, color: C.textMuted, padding: '10px 0' } }, 'No MELI label templates configured. Create them in Settings > Templates > MELI.')
            : React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 8 } },
                tpls.map(function(tpl) {
                  var sel = selectedLabelTpl === tpl.id;
                  return React.createElement('button', { key: tpl.id, onClick: function() { setSelectedLabelTpl(tpl.id); },
                    style: { padding: '10px 16px', borderRadius: 8, border: '2px solid ' + (sel ? C.accent : C.border), background: sel ? C.accent + '15' : 'transparent', color: sel ? C.accent : C.text, cursor: 'pointer', fontSize: 13.5, fontFamily: 'inherit', fontWeight: sel ? 600 : 400, textAlign: 'left' } },
                    React.createElement('div', null, tpl.name),
                    React.createElement('div', { style: { fontSize: 11.5, color: C.textMuted, marginTop: 3 } }, tpl.label_type === 'sheet' ? tpl.columns + 'x' + tpl.rows_per_sheet + ' per sheet' : parseFloat(tpl.width_mm).toFixed(1) + 'x' + parseFloat(tpl.height_mm).toFixed(1) + 'mm'),
                    tpl.is_default && React.createElement('span', { style: { fontSize: 10, color: C.accent, fontWeight: 600 } }, ' DEFAULT')
                  );
                }))
        ),
        React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12, lineHeight: 1.5 } },
          'One label per unit: barcode of the ML Code, the code, the product title and the SKU. Mercado Libre reads the unit by this code at Full.'),
        missing.length > 0 && React.createElement('div', { style: { padding: '10px 12px', marginBottom: 12, borderRadius: 8, border: '1px solid ' + C.warning, background: C.warning + '14', color: C.text, fontSize: 12, lineHeight: 1.5 } },
          React.createElement('strong', null, missing.length + (missing.length === 1 ? ' product has' : ' products have') + ' no ML Code and will not print: '),
          missing.map(function(i) { return i.sku; }).join(', ') + '. A product gets its ML Code once it is listed on Mercado Libre; the code is read every 30 minutes.'),
        React.createElement('div', { style: { marginBottom: 16 } },
          React.createElement('label', { style: { fontSize: 12.5, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', display: 'block', marginBottom: 8 } }, 'Products & Label Count'),
          modalItems.map(function(item) {
            var hasCode = !!String(item.ml_code || '').trim();
            var pill = function(bg, fg) { return { fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: bg, color: fg, letterSpacing: '0.02em', whiteSpace: 'nowrap', display: 'inline-block' }; };
            return React.createElement('div', { key: item.id, style: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid ' + C.border + '44', opacity: hasCode ? 1 : 0.6 } },
              item.image_url
                ? React.createElement('img', { src: item.image_url, style: { width: 52, height: 52, borderRadius: 8, objectFit: 'cover', border: '1px solid ' + C.border, flexShrink: 0 } })
                : React.createElement('div', { style: { width: 52, height: 52, borderRadius: 8, background: C.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } }, React.createElement(LIcon, { name: 'package', size: 22, color: C.textDim })),
              React.createElement('div', { style: { flex: 1, minWidth: 0 } },
                React.createElement('div', { style: { fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 3 } }, item.title || item.sku || ''),
                React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 4 } },
                  item.sku && React.createElement('span', { style: pill(C.accent + '14', C.accent) }, 'SKU: ' + item.sku),
                  hasCode
                    ? React.createElement('span', { style: pill(C.accent + '14', C.accent) }, 'ML Code: ' + item.ml_code)
                    : React.createElement('span', { style: pill(C.warning + '22', C.warning) }, 'No ML Code'))),
              React.createElement('div', { style: { textAlign: 'right', flexShrink: 0, marginRight: 6 } },
                React.createElement('div', { style: { fontSize: 15, fontWeight: 700, color: C.accent } }, parseInt(item.quantity) || 0),
                React.createElement('div', { style: { fontSize: 10.5, color: C.textMuted } }, 'units on plan')),
              React.createElement('input', { type: 'number', min: 0, value: hasCode ? (printQty[item.id] || '') : '', placeholder: '0', disabled: !hasCode,
                onChange: function(e) { var raw = e.target.value; setPrintQty(function(prev) { var n = Object.assign({}, prev); if (raw === '') delete n[item.id]; else n[item.id] = Math.max(0, parseInt(raw, 10) || 0); return n; }); },
                style: { width: 76, padding: '8px 8px', fontSize: 15, fontWeight: 600, textAlign: 'center', border: '1px solid ' + C.border, borderRadius: 6, background: C.bg, color: C.text, flexShrink: 0 } })
            );
          }),
          React.createElement('div', { style: { fontSize: 12.5, color: C.textMuted, marginTop: 8, textAlign: 'right', fontWeight: 600 } }, 'Total: ' + total + ' label' + (total === 1 ? '' : 's'))
        ),
        React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } },
          React.createElement(Btn, { onClick: function() { setPrintModal(null); } }, 'Cancel'),
          React.createElement(Btn, { onClick: downloadLabelPdf, disabled: !tpls.length }, React.createElement(LIcon, { name: 'download', size: 14 }), ' Download PDF'),
          React.createElement(Btn, { variant: 'primary', onClick: executeLabelPrint, disabled: !tpls.length }, React.createElement(LIcon, { name: 'printer', size: 14 }), ' Print')
        )
      )
    );
  }

  // ─── Items table (WFS review-table styling; thumbs use the WFS packing-view idiom
  // since the MELI items API returns image_url) ───
  function renderItemsTable(showProgress) {
    var cols = ['', 'SKU', 'Product', 'GTIN', 'ML Code', 'Listing #', 'Units'];
    if (showProgress) cols = cols.concat(['Packed', 'Received']);
    cols = cols.concat(['Labels']);
    var th = function(col, idx) { return React.createElement('th', { key: col + idx, style: { textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid ' + C.border, color: C.textMuted, fontWeight: 500, fontSize: 11 } }, col); };
    return React.createElement('table', { style: { width: '100%', fontSize: 12 } },
      React.createElement('thead', null, React.createElement('tr', null, cols.map(th))),
      React.createElement('tbody', null, items.map(function(item) {
        return React.createElement('tr', { key: item.id },
          React.createElement('td', { style: { padding: '6px 8px', width: 48 } },
            item.image_url
              ? React.createElement('img', { src: item.image_url, style: { width: 40, height: 40, borderRadius: 6, objectFit: 'cover', border: '1px solid ' + C.border, display: 'block' } })
              : React.createElement('div', { style: { width: 40, height: 40, borderRadius: 6, border: '1px solid ' + C.border, background: C.surface, display: 'flex', alignItems: 'center', justifyContent: 'center' } },
                  React.createElement(LIcon, { name: 'package', size: 16, color: C.textDim }))
          ),
          React.createElement('td', { style: { padding: '6px 8px', fontFamily: 'inherit', fontWeight: 600 } }, item.sku),
          React.createElement('td', { style: { padding: '6px 8px' } }, item.title || ''),
          React.createElement('td', { style: { padding: '6px 8px', fontFamily: 'inherit', fontSize: 11 } }, item.barcode || '—'),
          // the code Mercado Libre recognises the product by on the filing sheet (2026-09-29)
          React.createElement('td', { style: { padding: '6px 8px', fontFamily: 'inherit', fontSize: 11 } }, item.ml_code
            ? item.ml_code
            : React.createElement('span', { style: { color: C.warning, fontStyle: 'italic' } }, 'No ML Code')),
          // the listing number the filing sheet carries in "# de Publicación" (2026-09-29)
          React.createElement('td', { style: { padding: '6px 8px', fontFamily: 'inherit', fontSize: 11 } }, meliListingNumber(item.ml_item_id)
            ? meliListingNumber(item.ml_item_id)
            : React.createElement('span', { style: { color: C.warning, fontStyle: 'italic' } }, 'No listing #')),
          React.createElement('td', { style: { padding: '6px 8px', fontWeight: 600 } }, parseInt(item.quantity) || 0),
          showProgress && React.createElement('td', { style: { padding: '6px 8px' } }, parseInt(item.quantity_packed) || 0),
          showProgress && React.createElement('td', { style: { padding: '6px 8px' } }, parseInt(item.quantity_received) || 0),
          // this product's ML labels alone (a product without a code has nothing to print)
          React.createElement('td', { style: { padding: '4px 8px', width: 36 } },
            item.ml_code && React.createElement('button', { onClick: function() { openLabelPrint([item]); }, title: 'Print ML labels for ' + item.sku,
              style: { background: 'none', border: '1px solid ' + C.border, borderRadius: 6, padding: '4px 6px', cursor: 'pointer', color: C.textMuted, display: 'inline-flex', alignItems: 'center' } },
              React.createElement(LIcon, { name: 'printer', size: 14 })))
        );
      }))
    );
  }

  // Print ML Labels for the whole plan; greyed out until a product has an ML Code.
  function renderPrintLabelsButton() {
    var printable = meliPrintableItems(items).length;
    return React.createElement('span', { title: printable ? 'One label per unit for every product with an ML Code' : 'No product on this plan has an ML Code yet' },
      React.createElement(Btn, { size: 'sm', onClick: function() { openLabelPrint(null); }, disabled: !printable },
        React.createElement(LIcon, { name: 'printer', size: 14 }), ' Print ML Labels'));
  }

  function renderReviewStep() {
    return React.createElement(Card, null,
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 12 } },
        React.createElement('h3', { style: { fontSize: 14, fontWeight: 600, margin: 0, color: C.text } }, 'Items to Ship'),
        renderPrintLabelsButton()),
      React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12 } }, items.length + ' SKUs, ' + totalUnits + ' total units'),
      renderItemsTable(false),
      React.createElement('div', { style: { marginTop: 12, fontSize: 12, color: C.textMuted } }, 'Marketplace: Mercado Libre Full (USTX01)')
    );
  }

  // The stage header, like FBA's Pack Boxes (Anthony 2026-10-21: "The MELI packing stage should look
  // like the Amazon FBA one. That includes the ship to address, MELI shipment ID#, work order drop
  // down, etc."): a hint while units remain, then the shipment card — the MELI shipment number
  // (Mercado Libre's, pasted back after filing; "Pending" until then), our plan + POs, the ship-to
  // (Mercado Libre Full USTX01 — the POs' warehouse address from Settings > Warehouses), the
  // appointment, and on the right the pick list + the Work Orders menu (per PO / all, Excel or PDF:
  // app-fba.js WorkOrdersMenu, which reads plan.pos). A vendor-direct plan has no dock work, so it
  // shows the card without the warehouse buttons. Both modes get ML Box Labels (2026-10-22): Mercado
  // Libre's own per-box labels, one per box declared in Seller Center — the dock crew's box labels, and
  // the vendor's case labels on a vendor-direct plan.
  function renderPackHeader() {
    var isDock = plan.ship_mode !== 'vendor_direct';
    var showHint = isDock && ['filed', 'packing'].indexOf(plan.status) !== -1 && !meliAllUnitsResolved(items);
    var shipTo = meliShipToLines(plan.destination);
    var fileId = 'MELI' + plan.id + (plan.ml_inbound_ref ? '-' + plan.ml_inbound_ref : '');
    return React.createElement(React.Fragment, null,
      showHint && React.createElement('div', { style: { marginBottom: 12, padding: '8px 14px', borderRadius: 6, background: C.warning + '10', border: '1px solid ' + C.warning + '44', fontSize: 12, color: C.text, display: 'flex', alignItems: 'center', gap: 8 } },
        React.createElement(LIcon, { name: 'info', size: 14, color: C.warning }),
        React.createElement('span', null, 'Pack every unit (or declare it missing) and seal every box to enable Mark as Packed in the top bar.')),
      React.createElement(Card, { style: { marginBottom: 12 } },
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 } },
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
            React.createElement(LIcon, { name: 'truck', size: 18, color: C.accent }),
            React.createElement('div', null,
              React.createElement('div', { style: { fontSize: 11, color: C.textMuted } },
                'MELI Shipment ID: ',
                React.createElement('span', { style: { fontWeight: 600, fontSize: 13, color: C.text } }, plan.ml_inbound_ref || 'Pending'),
                ' · Plan #' + plan.id + (poNumbers ? ' · PO ' + poNumbers : '')),
              React.createElement('div', { style: { marginTop: 6 } },
                React.createElement('div', { style: { fontSize: 15, fontWeight: 700, color: C.text } }, 'Mercado Libre Full — ' + (plan.destination_node || 'USTX01')),
                shipTo.map(function(line, i) { return React.createElement('div', { key: i, style: { fontSize: 12, color: C.textMuted } }, line); }),
                plan.appointment_date && React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 2 } }, 'Appointment: ' + fmtDateOnly(plan.appointment_date))))),
          React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start', flexShrink: 0 } },
            React.createElement('span', { title: !plan.ml_inbound_ref ? 'File the shipment in Seller Center first — the labels carry Mercado Libre\'s shipment number'
                : 'Mercado Libre\'s box labels for shipment ' + plan.ml_inbound_ref + (parseInt(plan.box_count) > 0 ? ' — ' + plan.box_count + ' box' + (parseInt(plan.box_count) === 1 ? '' : 'es') + ' declared in Seller Center (change the number in the dialog)' : ' — asks for the box count declared in Seller Center') },
              React.createElement(Btn, { size: 'sm', onClick: openMlBoxLabels, disabled: !plan.ml_inbound_ref },
                React.createElement(LIcon, { name: 'printer', size: 13 }), ' ML Box Labels' + (parseInt(plan.box_count) > 0 ? ' (' + plan.box_count + ')' : ''))),
            // P1b: the pick list by staging bin, XLSX (cookie auth, like the Filing Sheet)
            isDock && React.createElement(Btn, { size: 'sm', onClick: function() { window.open('/api/meli-shipments/plans/' + planId + '/pick-list', '_blank'); }, title: 'Pick list by staging bin (XLSX)' },
              React.createElement(LIcon, { name: 'file-text', size: 13 }), ' Pick List'),
            isDock && React.createElement(WorkOrdersMenu, { plan: plan, showToast: showToast, align: 'right', fileId: fileId })))));
  }

  function renderPackStep() {
    // docs/15 P1 (Anthony 2026-10-20): the box-packing screens. A vendor-direct plan is packed by the
    // vendor, so it keeps the items table + ML labels (the vendor applies them from the PDF).
    var shipment = (plan.shipments && plan.shipments[0]) || null;
    if (plan.ship_mode === 'vendor_direct' || !shipment) {
      return React.createElement('div', null,
        renderPackHeader(),
        React.createElement(Card, { style: { marginBottom: 12 } },
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
            React.createElement(LIcon, { name: 'truck', size: 18, color: C.accent }),
            React.createElement('div', null,
              React.createElement('div', { style: { fontWeight: 600, color: C.text } }, plan.ship_mode === 'vendor_direct' ? 'Packed by the vendor' : 'No shipment on this plan'),
              React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 2 } }, plan.ship_mode === 'vendor_direct' ? 'This plan ships from the vendor straight to Mercado Libre. Send the vendor the ML product labels (one per unit) and, once its box count is filed in Seller Center, the ML Box Labels above (one per box). Continue to Ship once it leaves.' : 'Create the plan again to get a shipment to pack.')))),
        React.createElement(Card, null,
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 12 } },
            React.createElement('h3', { style: { fontSize: 14, fontWeight: 600, margin: 0, color: C.text } }, 'Items to Ship'),
            renderPrintLabelsButton()),
          React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12 } }, items.length + ' SKUs, ' + totalUnits + ' total units'),
          renderItemsTable(false)),
        // the plan's activity (2026-10-20: the Pack Boxes stage shows the shipment history, as FBA does)
        React.createElement(MeliActivityLog, { planId: plan.id, style: { marginTop: 12 } }));
    }
    if (!isFiled) {
      return React.createElement('div', null,
        renderPackHeader(),
        React.createElement(Card, { style: { marginBottom: 12 } },
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
            React.createElement(LIcon, { name: 'lock', size: 18, color: C.textMuted }),
            React.createElement('div', null,
              React.createElement('div', { style: { fontWeight: 600, color: C.text } }, 'File the shipment first'),
              React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 2 } }, 'Packing opens once the shipment is filed in Seller Center (Mark Filed on the previous step). Picking can start now — the pick list (above) is by staging bin.')))),
        React.createElement(MeliActivityLog, { planId: plan.id }));
    }
    return React.createElement('div', null,
      renderPackHeader(),
      React.createElement(MeliBoxPackingView, { planId: plan.id, shipmentId: shipment.id, plan: plan, items: items, showToast: showToast, onRefresh: load, currentUser: props.currentUser, onPrintLabels: openLabelPrint }));
  }

  function renderFileStep() {
    return React.createElement(Card, null,
      React.createElement('h3', { style: { fontSize: 14, fontWeight: 600, marginBottom: 12, color: C.text } }, 'File in Seller Center'),
      isFiled
        ? React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, padding: 12, background: 'rgba(52,211,153,0.08)', borderRadius: 8, marginBottom: 12 } },
            React.createElement(LIcon, { name: 'check-circle', size: 18, color: '#34D399' }),
            React.createElement('div', null,
              React.createElement('div', { style: { fontWeight: 600, color: '#34D399' } }, 'Filed in Seller Center'),
              React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 2 } }, 'ML Ref: ' + (plan.ml_inbound_ref || '—')),
              plan.appointment_date && React.createElement('div', { style: { fontSize: 12, color: C.textMuted } }, 'Appointment: ' + fmtDateOnly(plan.appointment_date)) // a date at UTC midnight: the calendar day, not the viewer's local day (review 2026-09-28, medium #36)
            )
          )
        : React.createElement('div', { style: { padding: 16, background: C.surface, borderRadius: 8, marginBottom: 16, fontSize: 13, color: C.textMuted, lineHeight: 1.6 } },
            React.createElement('div', null, 'ML has no create-inbound API — file this shipment manually in the Seller Center ("Send to Full"):'),
            React.createElement('div', null, '1. Download the Filing Sheet (ML’s official Stock-Shipment-Full template, pre-filled with ' + items.length + ' SKUs / ' + totalUnits + ' units).'),
            React.createElement('div', null, '2. Upload it in Seller Center and book the appointment.'),
            React.createElement('div', null, '3. Paste the ML inbound reference (and appointment date) into the Seller Center Filing card and Save.'),
            React.createElement('div', null, '4. Mark Filed.')
          ),
      meliItemsWithoutCode(items).length > 0 && React.createElement('div', { style: { padding: '10px 12px', marginBottom: 12, borderRadius: 8, border: '1px solid ' + C.warning, background: C.warning + '14', color: C.text, fontSize: 12, lineHeight: 1.5 } },
        React.createElement('strong', null, meliItemsWithoutCode(items).length + (meliItemsWithoutCode(items).length === 1 ? ' product has' : ' products have') + ' no ML Code: '),
        meliItemsWithoutCode(items).map(function(i) { return i.sku; }).join(', ') + '. ',
        'Mercado Libre may not recognise them on the Filing Sheet. A product gets its ML Code once it is listed on Mercado Libre; the code is read every 30 minutes.'),
      meliItemsWithoutListing(items).length > 0 && React.createElement('div', { style: { padding: '10px 12px', marginBottom: 12, borderRadius: 8, border: '1px solid ' + C.warning, background: C.warning + '14', color: C.text, fontSize: 12, lineHeight: 1.5 } },
        React.createElement('strong', null, meliItemsWithoutListing(items).length + (meliItemsWithoutListing(items).length === 1 ? ' product has' : ' products have') + ' no listing number: '),
        meliItemsWithoutListing(items).map(function(i) { return i.sku; }).join(', ') + '. ',
        'A product that also has an alias listing on Mercado Libre needs its listing number on the Filing Sheet, or the upload answers "not linked to any listing". The number is read every 30 minutes from the product\'s original listing.'),
      React.createElement('div', { style: { display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' } },
        canFile && !plan.ml_inbound_ref && React.createElement('span', { style: { fontSize: 11, color: C.textMuted } }, 'Save the ML inbound reference first →'),
        React.createElement(Btn, { onClick: openFilingSheet }, React.createElement(LIcon, { name: 'file-spreadsheet', size: 14 }), ' Download Filing Sheet'),
        canFile && React.createElement(Btn, { variant: 'primary', disabled: busy || !plan.ml_inbound_ref, onClick: function() { setStatus('filed'); } }, busy ? 'Saving...' : 'Mark Filed')
      )
    );
  }

  function renderShipStep() {
    var shipment = (plan.shipments && plan.shipments[0]) || {};
    return React.createElement(Card, null,
      React.createElement('h3', { style: { fontSize: 14, fontWeight: 600, marginBottom: 12, color: C.text } }, 'Ship'),
      React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12, padding: 8, background: C.surface, borderRadius: 6 } },
        React.createElement('div', { style: { fontWeight: 500, color: C.text } }, 'Ship To:'),
        React.createElement('div', null, 'Mercado Libre Full — USTX01'),
        plan.appointment_date && React.createElement('div', null, 'Appointment: ' + fmtDateOnly(plan.appointment_date)) // calendar day (review 2026-09-28, medium #36)
      ),
      isShipped
        ? React.createElement('div', null,
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, padding: 12, background: 'rgba(99,102,241,0.08)', borderRadius: 8, marginBottom: 12 } },
              React.createElement(LIcon, { name: 'check-circle', size: 18, color: '#6366F1' }),
              React.createElement('span', { style: { fontWeight: 600, color: '#6366F1' } }, 'Shipment marked as shipped' + (shipment.shipped_at ? ' — ' + new Date(shipment.shipped_at).toLocaleDateString() : ''))
            ),
            (shipment.carrier || shipment.tracking_number) && React.createElement('div', { style: { padding: 8, background: C.surface, borderRadius: 6, fontSize: 12 } },
              shipment.carrier && React.createElement('div', null, React.createElement('span', { style: { color: C.textMuted } }, 'Carrier: '), React.createElement('strong', null, shipment.carrier)),
              shipment.tracking_number && React.createElement('div', null, React.createElement('span', { style: { color: C.textMuted } }, 'Tracking: '), React.createElement('strong', { style: { fontFamily: 'inherit' } }, shipment.tracking_number))
            ),
            // Onward from shipped (2026-10-20): Mercado Libre's receipt is recorded here by hand.
            plan.status === 'receiving' && React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, padding: 12, background: 'rgba(34,197,94,0.08)', borderRadius: 8, marginTop: 12 } },
              React.createElement(LIcon, { name: 'package-open', size: 18, color: '#22C55E' }),
              React.createElement('span', { style: { fontWeight: 600, color: '#16A34A' } }, 'Mercado Libre is receiving this shipment')),
            plan.status === 'closed' && React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, padding: 12, background: 'rgba(34,197,94,0.1)', borderRadius: 8, marginTop: 12 } },
              React.createElement(LIcon, { name: 'check-circle', size: 18, color: '#22C55E' }),
              React.createElement('span', { style: { fontWeight: 600, color: '#16A34A' } }, 'Received by Mercado Libre — closed' + (plan.updated_at ? ' ' + new Date(plan.updated_at).toLocaleDateString() : ''))),
            (canReceive || canClose || plan.status === 'shipped') && React.createElement('div', { style: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 } },
              // Un-ship (P1b): only while shipped — once ML is receiving there is nothing to take back
              plan.status === 'shipped' && React.createElement(Btn, { disabled: busy, onClick: unshipPlan, title: 'Undo Mark as Shipped', style: { marginRight: 'auto' } }, React.createElement(LIcon, { name: 'undo-2', size: 13 }), ' Un-ship'),
              canReceive && React.createElement(Btn, { disabled: busy, onClick: function() { setStatus('receiving'); } }, busy ? 'Saving...' : 'Mark as Receiving'),
              canClose && React.createElement(Btn, { variant: 'primary', disabled: busy, onClick: closePlan }, busy ? 'Saving...' : 'Close Shipment'))
          )
        : canShip
        ? React.createElement('div', null,
            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 } },
              React.createElement(Inp, { label: 'Carrier', value: meta.carrier || '', onChange: function(e) { setMeta(Object.assign({}, meta, { carrier: e.target.value })); }, placeholder: 'e.g. UPS, FedEx' }),
              React.createElement(Inp, { label: 'Tracking / PRO #', value: meta.tracking_number || '', onChange: function(e) { setMeta(Object.assign({}, meta, { tracking_number: e.target.value })); } })
            ),
            React.createElement('div', { style: { display: 'flex', gap: 8, justifyContent: 'flex-end' } },
              React.createElement(Btn, { onClick: saveMeta, disabled: busy }, 'Save'),
              React.createElement(Btn, { variant: 'primary', disabled: busy, onClick: function() { setStatus('shipped'); } }, busy ? 'Saving...' : 'Mark as Shipped')
            )
          )
        // Peeking ahead on a pre-filed plan — ML doesn't accept carrier/tracking until
        // the shipment is filed, so no inputs here yet.
        : React.createElement('div', { style: { padding: 16, background: C.surface, borderRadius: 8, fontSize: 13, color: C.textMuted, lineHeight: 1.6 } },
            'File the shipment in Seller Center first — carrier and tracking are entered after filing.')
    );
  }

  // ─── Side cards (existing Filing + POs cards, restyled to WFS card idioms) ───
  function renderFilingCard() {
    var linkStyle = { display: 'inline-flex', alignItems: 'center', gap: 4, color: C.accent, textDecoration: 'none', fontSize: 12, fontWeight: 500 };
    return React.createElement(Card, { style: { marginBottom: 16 } },
      React.createElement('h3', { style: { fontSize: 14, fontWeight: 600, color: C.text, margin: '0 0 10px' } }, 'Seller Center Filing'),
      React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 10, lineHeight: 1.5 } },
        'File this inbound in the ML Seller Center (Send to Full) using the Filing Sheet, then paste the reference here.'),
      // Download the filled template first, then the Seller Center links it uploads to.
      React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 } },
        React.createElement('a', { href: '/api/meli-shipments/plans/' + planId + '/filing-sheet', target: '_blank', style: linkStyle },
          React.createElement(LIcon, { name: 'file-spreadsheet', size: 12 }), ' Download Filing Sheet'),
        React.createElement('a', { href: 'https://global-selling.mercadolibre.com/listings/list/shipment_planning/plans', target: '_blank', rel: 'noopener noreferrer', style: linkStyle },
          React.createElement(LIcon, { name: 'external-link', size: 12 }), ' Open Shipment Planning'),
        React.createElement('a', { href: 'https://global-selling.mercadolibre.com/shipping/import/excel/upload', target: '_blank', rel: 'noopener noreferrer', style: linkStyle },
          React.createElement(LIcon, { name: 'external-link', size: 12 }), ' Upload Filing Sheet')
      ),
      React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 8 } },
        React.createElement(Inp, { label: 'ML Inbound Reference', value: meta.ml_inbound_ref || '', onChange: function(e) { setMeta(Object.assign({}, meta, { ml_inbound_ref: e.target.value })); } }),
        React.createElement(Inp, { label: 'Appointment Date', type: 'date', value: meta.appointment_date || '', onChange: function(e) { setMeta(Object.assign({}, meta, { appointment_date: e.target.value })); } }),
        // the box count declared in Seller Center (2026-10-22): Mercado Libre issues one box label per declared box
        React.createElement(Inp, { label: 'Boxes declared in Seller Center', type: 'number', min: 1, step: 1, placeholder: 'For the ML Box Labels', value: meta.box_count || '', onChange: function(e) { setMeta(Object.assign({}, meta, { box_count: e.target.value })); } }),
        React.createElement(Inp, { label: 'Notes', value: meta.notes || '', onChange: function(e) { setMeta(Object.assign({}, meta, { notes: e.target.value })); } })
      ),
      React.createElement('div', { style: { marginTop: 10, display: 'flex', justifyContent: 'flex-end' } },
        React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: saveMeta, disabled: busy }, busy ? 'Saving...' : 'Save'))
    );
  }

  // Carrier/tracking live in their own card, shown only from 'filed' onward — ML doesn't
  // accept logistics details until the end of the process. Same PUT /meta save path.
  function renderShippingCard() {
    if (!isFiled) return null;
    return React.createElement(Card, { style: { marginBottom: 16 } },
      React.createElement('h3', { style: { fontSize: 14, fontWeight: 600, color: C.text, margin: '0 0 10px' } }, 'Shipping'),
      React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 8 } },
        React.createElement(Inp, { label: 'Carrier', value: meta.carrier || '', onChange: function(e) { setMeta(Object.assign({}, meta, { carrier: e.target.value })); }, placeholder: 'e.g. UPS, FedEx' }),
        React.createElement(Inp, { label: 'Tracking / PRO #', value: meta.tracking_number || '', onChange: function(e) { setMeta(Object.assign({}, meta, { tracking_number: e.target.value })); } })
      ),
      React.createElement('div', { style: { marginTop: 10, display: 'flex', justifyContent: 'flex-end' } },
        React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: saveMeta, disabled: busy }, busy ? 'Saving...' : 'Save'))
    );
  }

  function renderPosCard() {
    var pos = plan.pos || [];
    return React.createElement(Card, null,
      React.createElement('h3', { style: { fontSize: 14, fontWeight: 600, color: C.text, margin: '0 0 10px' } }, 'Purchase Orders'),
      pos.map(function(po, idx) {
        return React.createElement('div', { key: po.id, style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', fontSize: 13, borderBottom: idx < pos.length - 1 ? '1px solid ' + C.border : 'none' } },
          React.createElement('a', { href: '/po/' + po.id, style: { color: C.accent, textDecoration: 'none', fontWeight: 600, fontFamily: 'inherit' }, onClick: function(e) { e.preventDefault(); onNavigate('po-detail', po.id); } }, po.po_number),
          React.createElement('span', { style: { color: C.textMuted, fontSize: 12 } }, po.status + ' · ' + (po.total_units || 0) + ' units'));
      })
    );
  }
}

window.MeliShipmentsPage = MeliShipmentsPage;
window.MeliDetailPage = MeliDetailPage;
window.MeliBadge = MeliBadge;

;
/* ===== app-meli-pack.js ===== */
// ─── MELI box packing (docs/15 P1, Anthony 2026-10-20: "can we work on P1? Box packing arrives in P1") ───
// The Pack step of a MELI inbound plan, after the shipment is filed in Seller Center: the items
// remaining to pack, a scanner into an open box (Mercado Libre's own product label carries the ML
// Code, so scanning it works), the Pack-a-Box wizard (select items → verify quantities → dims &
// copies → finish, which creates, fills and seals N identical boxes in one call and resumes from a
// per-user draft), the boxes column (contents, edit, duplicate, seal, delete, box content label, ML
// labels for one box), declared-missing and errored units, and the packing summary Seller Center
// asks for at the appointment (box count, dims groups, weights). Server: routes/meli-packing.js.
// Mirrors app-wfs.js WfsBoxPackingView's shape without the Walmart-only parts (case-pack generator,
// carrier labels, notes). Shared components come from app-utils.js (window.*).
(function() {
var h = React.createElement;
var MELI_PACK_BASE = '/meli-shipments';

// Units of a shipment item still to pack: assigned − packed − declared missing (never negative).
function meliPackRemaining(it) {
  var q = parseInt(it && it.quantity, 10) || 0, p = parseInt(it && it.quantity_packed, 10) || 0, m = parseInt(it && it.quantity_missing, 10) || 0;
  return Math.max(0, q - p - m);
}
// A saved Pack-a-Box draft against the items that can still be packed: items that are gone or
// fully packed drop out, quantities are capped at the remaining balance, an item counts if it was
// checked or had a quantity. null when nothing usable remains.
function meliReconcileDraft(draft, avail) {
  if (!draft || typeof draft !== 'object') return null;
  var sel0 = draft.selectedItems || {}, q0 = draft.packQtys || {};
  var sel = {}, qtys = {}, lines = 0;
  (avail || []).forEach(function(it) {
    var rem = meliPackRemaining(it);
    var wanted = parseInt(q0[it.id], 10);
    if (!sel0[it.id] && !(wanted > 0)) return;
    if (rem <= 0) return;
    sel[it.id] = true;
    if (wanted > 0) qtys[it.id] = Math.min(wanted, rem);
    lines++;
  });
  if (!lines) return null;
  var dims = draft.dimForm && typeof draft.dimForm === 'object' ? draft.dimForm : {};
  return { selectedItems: sel, packQtys: qtys, numCopies: Math.max(1, parseInt(draft.numCopies, 10) || 1),
    dimForm: { length: dims.length || '', width: dims.width || '', height: dims.height || '', weight: dims.weight || '' }, lines: lines };
}
// The text of a box content label (our own, not Mercado Libre's per-box label, which Seller Center
// hands out): plan, ML inbound reference, box n of N, dims, a scannable box code, the contents.
function meliBoxLabelLines(plan, box, totalBoxes, maxRows) {
  var rows = (box.items || []).slice().sort(function(a, b) { return String(a.sku || '').localeCompare(String(b.sku || '')); })
    .map(function(l) { return { sku: l.sku || '', ml_code: l.ml_code || '', qty: parseInt(l.quantity, 10) || 0 }; });
  var cap = maxRows || 12;
  var units = rows.reduce(function(s, r) { return s + r.qty; }, 0);
  var dims = (box.length && box.width && box.height) ? box.length + ' x ' + box.width + ' x ' + box.height + ' in' + (box.weight ? ' · ' + box.weight + ' lb' : '') : '';
  return {
    title: 'Mercado Libre Full — ' + (plan.destination_node || 'USTX01'),
    subtitle: 'Plan #' + plan.id + (plan.ml_inbound_ref ? ' · Envío ' + plan.ml_inbound_ref : '') + ((plan.pos || []).length ? ' · PO ' + plan.pos.map(function(p) { return p.po_number; }).join(', ') : ''),
    boxLine: 'Box ' + box.box_number + ' of ' + (totalBoxes || box.box_number),
    dims: dims,
    code: 'MELI' + plan.id + '-B' + box.box_number,
    units: units,
    rows: rows.slice(0, cap),
    more: Math.max(0, rows.length - cap),
  };
}
// One 4 x 6 in page per box (jsPDF + bwip-js, the libraries the ML product labels use).
function meliBoxLabelsPdf(plan, boxes) {
  var ns = window.jspdf || window.jsPDF;
  var Ctor = ns && (ns.jsPDF || ns);
  if (!Ctor || !window.bwipjs) return null;
  var doc = new Ctor({ unit: 'in', format: [4, 6], orientation: 'portrait' });
  boxes.forEach(function(box, idx) {
    if (idx > 0) doc.addPage([4, 6], 'portrait');
    var L = meliBoxLabelLines(plan, box, boxes.length ? Math.max.apply(null, boxes.map(function(b) { return b.box_number; })) : box.box_number, 14);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.text(L.title, 0.25, 0.4);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.text(doc.splitTextToSize(L.subtitle, 3.5), 0.25, 0.62);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(26); doc.text(L.boxLine, 0.25, 1.25);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
    if (L.dims) doc.text(L.dims, 0.25, 1.5);
    doc.text(L.units + ' units · ' + (box.items || []).length + ' SKU' + ((box.items || []).length === 1 ? '' : 's'), 0.25, 1.68);
    try {
      var canvas = document.createElement('canvas');
      window.bwipjs.toCanvas(canvas, { bcid: 'code128', text: L.code, scale: 3, height: 10, includetext: false, paddingleft: 4, paddingright: 4 });
      doc.addImage(canvas.toDataURL('image/png'), 'PNG', 0.25, 1.85, 2.6, 0.55);
    } catch (e) { /* no barcode, the code prints as text below */ }
    doc.setFontSize(9); doc.text(L.code, 0.25, 2.55);
    doc.setDrawColor(120); doc.line(0.25, 2.7, 3.75, 2.7);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
    doc.text('SKU', 0.25, 2.9); doc.text('ML Code', 2.0, 2.9); doc.text('Qty', 3.45, 2.9);
    doc.setFont('helvetica', 'normal');
    var y = 3.08;
    L.rows.forEach(function(r) {
      doc.text(String(r.sku).slice(0, 26), 0.25, y); doc.text(String(r.ml_code).slice(0, 14), 2.0, y); doc.text(String(r.qty), 3.45, y);
      y += 0.2;
    });
    if (L.more) doc.text('+ ' + L.more + ' more SKU' + (L.more === 1 ? '' : 's'), 0.25, y);
  });
  return doc;
}
window.meliPackRemaining = meliPackRemaining;
window.meliReconcileDraft = meliReconcileDraft;
window.meliBoxLabelLines = meliBoxLabelLines;
// The ship-to block of the Pack Boxes header (like FBA's destination FC): the plan's warehouse row
// from Settings > Warehouses — street, then city/state/zip, then the country when it is not the US.
function meliShipToLines(dest) {
  var d = dest || {};
  var street = [d.address, d.address2].filter(Boolean).join(', ');
  var cityLine = [[d.city, d.state].filter(Boolean).join(', '), d.zip].filter(Boolean).join(' ');
  var country = d.country && String(d.country).toUpperCase() !== 'US' ? String(d.country) : '';
  return [street, cityLine, country].filter(Boolean);
}
// Every unit packed or declared missing (the hint above the stage header goes once this holds).
function meliAllUnitsResolved(items) {
  var list = items || [];
  return list.length > 0 && list.every(function(it) { return meliPackRemaining(it) === 0; });
}
window.meliBoxLabelsPdf = meliBoxLabelsPdf;
window.meliShipToLines = meliShipToLines;
window.meliAllUnitsResolved = meliAllUnitsResolved;

function pillStyle(bg, color) {
  return { display: 'inline-flex', alignItems: 'center', padding: '2px 7px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: bg, color: color, whiteSpace: 'nowrap' };
}
function meliItemPills(it) {
  return h('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' } },
    it.sku && h(CopyCell, { value: it.sku }, h('span', { style: pillStyle(C.accent + '14', C.accent) }, 'SKU: ' + it.sku)),
    it.ml_code && h(CopyCell, { value: it.ml_code }, h('span', { style: pillStyle('#3483FA22', '#3483FA') }, 'ML: ' + it.ml_code)),
    it.barcode && h(CopyCell, { value: it.barcode }, h('span', { style: pillStyle(C.accent + '14', C.accent) }, 'GTIN: ' + it.barcode))
  );
}
function meliThumb(it, size) {
  var url = it.image_url || it.image_1_url;
  return url
    ? h('img', { src: url, style: { width: size, height: size, borderRadius: 6, objectFit: 'cover', border: '1px solid ' + C.border, flexShrink: 0 } })
    : h('div', { style: { width: size, height: size, borderRadius: 6, background: C.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } }, h(LIcon, { name: 'package', size: Math.round(size * 0.4), color: C.textDim }));
}
function numInputStyle(valid) {
  return { width: '100%', padding: '8px 6px', fontSize: 16, fontWeight: 700, textAlign: 'center', border: '2px solid ' + (valid ? C.success : C.border), borderRadius: 8, background: C.bg, color: C.text, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' };
}
function checkBox(on, color) {
  return h('div', { style: { width: 20, height: 20, minWidth: 20, borderRadius: 4, border: '2px solid ' + (on ? color : C.textDim), background: on ? color : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' } },
    on && h(LIcon, { name: 'check', size: 13, color: '#fff' }));
}
function iconBtn(name, title, color, onClick) {
  return h('button', { onClick: function(e) { e.stopPropagation(); onClick(); }, title: title,
    onMouseEnter: function(e) { e.currentTarget.style.background = color + '18'; }, onMouseLeave: function(e) { e.currentTarget.style.background = 'none'; },
    style: { background: 'none', border: 'none', cursor: 'pointer', color: color, padding: 5, borderRadius: 6, display: 'inline-flex', transition: 'background 0.15s' } }, h(LIcon, { name: name, size: 16 }));
}

// ─── Activity log (Anthony 2026-10-20: "The Pack Boxes stage is also missing the shipment activity/history.
// Similar to the FBA Shipments one") ───
// The FBA Activity Log tab's shape: newest first, grouped by day, a colored dot + action badge per row,
// search over action / details / user, Refresh and an Excel download. Reads GET /plans/:id/activity
// (the plan's, its shipment's and its boxes' events). Used as a tab in the packing view and as a card on
// the Pack step before filing and on vendor-direct plans.
function MeliActivityLog(props) {
  var planId = props.planId;
  var _l = useState([]), log = _l[0], setLog = _l[1];
  var _b = useState(true), loading = _b[0], setLoading = _b[1];
  var _q = useState(''), q = _q[0], setQ = _q[1];
  var load = useCallback(function() {
    setLoading(true);
    return api(MELI_PACK_BASE + '/plans/' + planId + '/activity')
      .then(function(rows) { setLog(Array.isArray(rows) ? rows : []); })
      .catch(function() { setLog([]); })
      .finally(function() { setLoading(false); });
  }, [planId]);
  useEffect(function() { load(); }, [load, props.refreshKey]);
  var aq = q.toLowerCase().trim();
  var shown = !aq ? log : log.filter(function(a) {
    var act = (a.action || '').toLowerCase();
    // the raw action ("meli_item_packed") and the badge text ("item packed")
    return act.indexOf(aq) !== -1 || act.replace(/^meli_/, '').replace(/_/g, ' ').indexOf(aq) !== -1 ||
      (a.details || '').toLowerCase().indexOf(aq) !== -1 || (a.username || '').toLowerCase().indexOf(aq) !== -1;
  });
  var byDate = {};
  shown.forEach(function(a) {
    var d = new Date(a.created_at);
    var key = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
    (byDate[key] = byDate[key] || []).push(a);
  });
  var keys = Object.keys(byDate).sort().reverse();
  var colorOf = function(act) {
    return (act.indexOf('delete') >= 0 || act.indexOf('cancel') >= 0) ? '#F87171'
      : (act.indexOf('missing') >= 0 || act.indexOf('errored') >= 0) ? '#F59E0B'
      : act.indexOf('duplicat') >= 0 ? '#06B6D4'
      : (act.indexOf('packed') >= 0 || act.indexOf('finished') >= 0) ? C.success
      : '#8B5CF6';
  };
  var btnStyle = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 5, border: '1px solid ' + C.border, background: 'transparent', color: C.text, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit', textDecoration: 'none' };
  return h(Card, { style: props.style },
    h('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 14 } },
      h('div', { style: { fontSize: 16, fontWeight: 600, color: C.text } }, props.title || 'Shipment Activity Log'),
      h('div', { style: { display: 'flex', alignItems: 'center', gap: 6 } },
        h('button', { onClick: load, disabled: loading, style: Object.assign({}, btnStyle, { cursor: loading ? 'default' : 'pointer' }) }, h(LIcon, { name: 'refresh-cw', size: 12, spin: loading }), 'Refresh'),
        h('a', { href: '/api' + MELI_PACK_BASE + '/plans/' + planId + '/activity.xlsx', download: '', title: 'Download the activity log as Excel', style: Object.assign({}, btnStyle, { color: '#10B981' }) }, h(LIcon, { name: 'file-spreadsheet', size: 14 }), 'Excel'))),
    log.length > 0 && h('div', { style: { marginBottom: 10, position: 'relative' } },
      h('input', { value: q, onChange: function(e) { setQ(e.target.value); }, placeholder: 'Search activity by action, details, user...',
        style: { width: '100%', padding: '8px 34px 8px 12px', fontSize: 13, border: '1px solid ' + C.border, borderRadius: 6, background: C.bg, color: C.text, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' } }),
      q && h('button', { onClick: function() { setQ(''); }, title: 'Clear search',
        style: { position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', width: 22, height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', border: 'none', background: C.border, color: C.text, cursor: 'pointer', padding: 0, fontFamily: 'inherit' } },
        h(LIcon, { name: 'x', size: 12 }))),
    loading && log.length === 0
      ? h('div', { style: { textAlign: 'center', padding: '30px 0', color: C.textMuted, fontSize: 13 } }, 'Loading…')
      : log.length === 0
        ? h('div', { style: { textAlign: 'center', padding: '30px 0', color: C.textMuted } },
            h(LIcon, { name: 'activity', size: 32, color: C.textDim }),
            h('div', { style: { fontSize: 14, fontWeight: 600, color: C.text, marginTop: 8 } }, 'No activity yet'),
            h('div', { style: { fontSize: 12, marginTop: 4 } }, 'Plan, box and item events for this shipment will appear here.'))
        : shown.length === 0
          ? h('div', { style: { textAlign: 'center', padding: '30px 0', color: C.textMuted, fontSize: 13 } }, 'No activity matches "' + q + '"')
          : h('div', null, keys.map(function(k) {
              var parts = k.split('-');
              var headerDate = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
              return h('div', { key: k, style: { marginBottom: 16 } },
                h('div', { style: { fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8, paddingBottom: 4, borderBottom: '1px solid ' + C.border } },
                  headerDate.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })),
                byDate[k].map(function(a) {
                  var act = a.action || '';
                  var color = colorOf(act);
                  var d = new Date(a.created_at);
                  return h('div', { key: a.id, style: { display: 'flex', gap: 10, padding: '10px 0', borderBottom: '1px solid ' + C.border + '33' } },
                    h('div', { style: { width: 8, height: 8, borderRadius: '50%', background: color, marginTop: 6, flexShrink: 0 } }),
                    h('div', { style: { flex: 1, minWidth: 0 } },
                      h('div', { style: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 } },
                        h('span', { style: { fontSize: 10, fontWeight: 700, color: color, padding: '2px 7px', borderRadius: 4, background: color + '14', letterSpacing: '0.04em', textTransform: 'uppercase' } }, act.replace(/^meli_/, '').replace(/_/g, ' ')),
                        h('span', { style: { fontSize: 11, color: C.textMuted, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }, title: d.toLocaleString() }, d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }))),
                      h('div', { style: { fontSize: 13, color: C.text, marginTop: 4 } }, a.details || '—'),
                      a.username && h('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 2 } }, 'by ' + a.username)));
                }));
            })));
}

function MeliBoxPackingView(props) {
  var planId = props.planId, shipmentId = props.shipmentId, plan = props.plan, showToast = props.showToast, onRefresh = props.onRefresh, onPrintLabels = props.onPrintLabels, currentUser = props.currentUser;
  var items = props.items || [];
  var shipBase = MELI_PACK_BASE + '/shipments/' + shipmentId;
  var packOpen = ['filed', 'packing', 'packed'].indexOf(plan.status) !== -1;

  var _bx = useState([]), boxes = _bx[0], setBoxes = _bx[1];
  var _bl = useState(true), boxesLoading = _bl[0], setBoxesLoading = _bl[1];
  var _er = useState([]), errored = _er[0], setErrored = _er[1];
  var _sm = useState(null), summary = _sm[0], setSummary = _sm[1];
  var _ws = useState(0), wizStep = _ws[0], setWizStep = _ws[1];
  var _pt = useState('items'), packTab = _pt[0], setPackTab = _pt[1];
  var _si = useState({}), selectedItems = _si[0], setSelectedItems = _si[1];
  var _pq = useState({}), packQtys = _pq[0], setPackQtys = _pq[1];
  var _nc = useState(1), numCopies = _nc[0], setNumCopies = _nc[1];
  var _df = useState({ length: '', width: '', height: '', weight: '' }), dimForm = _df[0], setDimForm = _df[1];
  var _is = useState(''), itemSearch = _is[0], setItemSearch = _is[1];
  var _sv = useState(false), saving = _sv[0], setSaving = _sv[1];
  var _rd = useState(undefined), rawDraft = _rd[0], setRawDraft = _rd[1];
  var _dp = useState(null), draftPrompt = _dp[0], setDraftPrompt = _dp[1];
  var draftSaveTimer = useRef(null);
  var _vb = useState(null), viewBoxId = _vb[0], setViewBoxId = _vb[1];
  var _eb = useState(null), editBox = _eb[0], setEditBox = _eb[1];
  var _dc = useState(null), deleteBoxConfirm = _dc[0], setDeleteBoxConfirm = _dc[1];
  var _dm = useState(null), duplicateModal = _dm[0], setDuplicateModal = _dm[1];
  var _mm = useState(null), missingModal = _mm[0], setMissingModal = _mm[1];
  var _em = useState(null), erroredModal = _em[0], setErroredModal = _em[1];
  var _sb = useState(null), scanBoxId = _sb[0], setScanBoxId = _sb[1];
  var _sc = useState(''), scanCode = _sc[0], setScanCode = _sc[1];
  var _ls = useState(null), lastScan = _ls[0], setLastScan = _ls[1];
  var _bf = useState(''), boxFilter = _bf[0], setBoxFilter = _bf[1];
  var scanInputRef = useRef(null);
  // Boxes | Internal Notes (Anthony 2026-10-21: the stage looks like FBA's): the first PO's notes thread,
  // as FBA's boxes column shows it — read, sent and deleted through /purchase-orders/:id/notes, source
  // 'meli' so a mention e-mail deep-links to this plan.
  var _rt = useState('items'), rightTab = _rt[0], setRightTab = _rt[1];
  var _nt = useState([]), notes = _nt[0], setNotes = _nt[1];
  var _nn = useState(''), newNote = _nn[0], setNewNote = _nn[1];
  var notesEndRef = useRef(null);
  var notesPoId = plan.po_ids && plan.po_ids.length ? plan.po_ids[0] : plan.po_id;
  var loadNotes = useCallback(function() {
    if (!notesPoId) return;
    api('/purchase-orders/' + notesPoId + '/notes').then(function(rows) { setNotes(Array.isArray(rows) ? rows : []); }).catch(function(e) { console.error('[silent-catch]', e); });
  }, [notesPoId]);
  useEffect(function() { loadNotes(); }, [loadNotes]);
  useEffect(function() { if (rightTab === 'notes' && notesEndRef.current && notesEndRef.current.scrollIntoView) notesEndRef.current.scrollIntoView({ block: 'end' }); }, [rightTab, notes.length]);
  var sendNote = function() {
    if (!newNote.trim() || !notesPoId) return;
    api('/purchase-orders/' + notesPoId + '/notes', { method: 'POST', body: JSON.stringify({ message: newNote.trim(), source: 'meli', entity_id: plan.id }) })
      .then(function() { setNewNote(''); loadNotes(); })
      .catch(function(e) { showToast(e.message, 'error'); });
  };
  var deleteNote = function(noteId) {
    if (!notesPoId) return;
    api('/purchase-orders/' + notesPoId + '/notes/' + noteId, { method: 'DELETE' }).then(loadNotes).catch(function(e) { showToast(e.message, 'error'); });
  };

  var availableItems = items.filter(function(it) { return meliPackRemaining(it) > 0; });
  var selectedItemsList = availableItems.filter(function(it) { return selectedItems[it.id]; });
  var selectedCount = selectedItemsList.length;
  var allQtysEntered = selectedCount > 0 && selectedItemsList.every(function(it) { var q = parseInt(packQtys[it.id], 10); return q > 0 && q <= meliPackRemaining(it); });
  var missingItems = items.filter(function(it) { return (parseInt(it.quantity_missing, 10) || 0) > 0; });
  var totalUnits = items.reduce(function(s, it) { return s + (parseInt(it.quantity, 10) || 0); }, 0);
  var totalPacked = items.reduce(function(s, it) { return s + (parseInt(it.quantity_packed, 10) || 0); }, 0);
  var totalMissing = items.reduce(function(s, it) { return s + (parseInt(it.quantity_missing, 10) || 0); }, 0);
  var openBoxes = boxes.filter(function(b) { return !b.is_sealed; });
  var viewBox = viewBoxId ? boxes.find(function(b) { return b.id === viewBoxId; }) : null;
  var maxBoxNumber = boxes.reduce(function(m, b) { return Math.max(m, b.box_number || 0); }, 0);

  var filterItems = function(list) {
    var q = itemSearch.trim().toLowerCase();
    if (!q) return list;
    return list.filter(function(it) { return [it.sku, it.title, it.barcode, it.ml_code].some(function(v) { return v && String(v).toLowerCase().indexOf(q) >= 0; }); });
  };

  // ─── Loads ───
  var loadBoxes = useCallback(function() {
    return api(shipBase + '/boxes').then(function(b) { setBoxes(Array.isArray(b) ? b : []); setBoxesLoading(false); })
      .catch(function(e) { setBoxesLoading(false); showToast('Boxes: ' + e.message, 'error'); });
  }, [shipBase]);
  var loadSide = useCallback(function() {
    api(MELI_PACK_BASE + '/plans/' + planId + '/errored-items').then(function(r) { setErrored(Array.isArray(r) ? r : []); }).catch(function() {});
    api(shipBase + '/packing-summary').then(setSummary).catch(function() {});
  }, [planId, shipBase]);
  useEffect(function() { loadBoxes(); loadSide(); }, [loadBoxes, loadSide]);
  var reload = function() { loadBoxes(); loadSide(); if (onRefresh) onRefresh(); };

  // The per-user draft (resume an in-progress box, even from another computer)
  useEffect(function() {
    var cancelled = false;
    setRawDraft(undefined);
    api(shipBase + '/box-draft').then(function(d) { if (!cancelled) setRawDraft(d || null); }).catch(function() { if (!cancelled) setRawDraft(null); });
    return function() { cancelled = true; };
  }, [shipBase]);
  useEffect(function() {
    if (rawDraft === undefined || wizStep === 0) return;
    if (draftSaveTimer.current) clearTimeout(draftSaveTimer.current);
    draftSaveTimer.current = setTimeout(function() {
      var hasContent = Object.keys(selectedItems).some(function(k) { return selectedItems[k]; });
      if (!hasContent) { api(shipBase + '/box-draft', { method: 'DELETE' }).catch(function() {}); return; }
      api(shipBase + '/box-draft', { method: 'PUT', body: JSON.stringify({ draft: { selectedItems: selectedItems, packQtys: packQtys, numCopies: numCopies, dimForm: dimForm, wizStep: wizStep } }) })
        .catch(function(e) { console.error('[meli draft-save]', e && e.message); });
    }, 600);
    return function() { if (draftSaveTimer.current) clearTimeout(draftSaveTimer.current); };
  }, [shipBase, selectedItems, packQtys, numCopies, dimForm, wizStep, rawDraft]);
  useEffect(function() { if (scanBoxId && scanInputRef.current) scanInputRef.current.focus(); }, [scanBoxId, lastScan]);

  // ─── Wizard ───
  var startWizard = function() {
    var rd = rawDraft && rawDraft.draft ? meliReconcileDraft(rawDraft.draft, availableItems) : null;
    if (rd) { setDraftPrompt({ data: rd, updated_at: rawDraft.updated_at }); return; }
    setSelectedItems({}); setPackQtys({}); setNumCopies(1); setItemSearch(''); setWizStep(1);
  };
  var resumeDraft = function() {
    var d = draftPrompt.data;
    setSelectedItems(d.selectedItems); setPackQtys(d.packQtys); setNumCopies(d.numCopies); setDimForm(d.dimForm);
    setDraftPrompt(null); setItemSearch(''); setWizStep(1);
  };
  var startFresh = function() {
    api(shipBase + '/box-draft', { method: 'DELETE' }).catch(function() {});
    setRawDraft(null); setDraftPrompt(null);
    setSelectedItems({}); setPackQtys({}); setNumCopies(1); setItemSearch(''); setWizStep(1);
  };
  var cancelWizard = function() {
    setSelectedItems({}); setPackQtys({}); setNumCopies(1); setDimForm({ length: '', width: '', height: '', weight: '' }); setWizStep(0);
    api(shipBase + '/box-draft', { method: 'DELETE' }).catch(function() {});
    setRawDraft(null);
  };
  var toggleItem = function(id) {
    setSelectedItems(function(prev) {
      var n = Object.assign({}, prev);
      if (n[id]) { delete n[id]; setPackQtys(function(q) { var nq = Object.assign({}, q); delete nq[id]; return nq; }); }
      else n[id] = true;
      return n;
    });
  };
  var addAll = function() {
    var sel = {}, qtys = {};
    availableItems.forEach(function(it) { sel[it.id] = true; qtys[it.id] = meliPackRemaining(it); });
    setSelectedItems(sel); setPackQtys(qtys);
  };
  var finishBox = function() {
    if (saving) return;
    var l = parseFloat(dimForm.length), w = parseFloat(dimForm.width), ht = parseFloat(dimForm.height), wt = parseFloat(dimForm.weight);
    if (!l || !w || !ht) { showToast('Box dimensions (L x W x H) are required', 'error'); return; }
    var copies = Math.max(1, Math.min(999, parseInt(numCopies, 10) || 1));
    var payload = [], over = [];
    selectedItemsList.forEach(function(it) {
      var qty = parseInt(packQtys[it.id], 10) || 0;
      if (qty > 0) payload.push({ item_id: it.id, quantity: qty });
      if (qty * copies > meliPackRemaining(it)) over.push(it.sku + ': ' + meliPackRemaining(it) + ' packable, asked ' + qty + ' × ' + copies);
    });
    if (!payload.length) { showToast('No items selected', 'error'); return; }
    if (over.length) { showToast('Too many units for the packable balance — ' + over.join('; '), 'error'); return; }
    setSaving(true);
    api(shipBase + '/boxes/finish-batch', { method: 'POST', body: JSON.stringify({ items: payload, dims: { length: l, width: w, height: ht, weight: wt || null }, count: copies }) })
      .then(function(r) {
        var nums = ((r && r.boxes) || []).map(function(b) { return b.box_number; });
        showToast(copies === 1 ? 'Box ' + nums[0] + ' packed and sealed' : copies + ' boxes packed (#' + nums.join(', #') + ')', 'success');
        if (typeof fireConfetti === 'function') fireConfetti();
        setSelectedItems({}); setPackQtys({}); setNumCopies(1); setDimForm({ length: '', width: '', height: '', weight: '' }); setWizStep(0); setSaving(false);
        api(shipBase + '/box-draft', { method: 'DELETE' }).catch(function() {});
        setRawDraft(null);
        reload();
      })
      .catch(function(e) { showToast(e.message || 'Failed to pack box', 'error'); setSaving(false); reload(); });
  };

  // ─── Scanner into an open box ───
  var newOpenBox = function() {
    api(shipBase + '/boxes', { method: 'POST', body: JSON.stringify({}) })
      .then(function(b) { showToast('Box ' + b.box_number + ' opened'); setScanBoxId(b.id); setLastScan(null); reload(); })
      .catch(function(e) { showToast(e.message, 'error'); });
  };
  var submitScan = function() {
    var code = scanCode.trim();
    if (!code || !scanBoxId) return;
    setScanCode('');
    api(MELI_PACK_BASE + '/boxes/' + scanBoxId + '/scan', { method: 'POST', body: JSON.stringify({ barcode: code }) })
      .then(function(r) { setLastScan({ ok: true, text: r.product.sku + ' +1 — ' + r.packed + ' packed, ' + r.remaining + ' remaining' }); reload(); })
      .catch(function(e) { setLastScan({ ok: false, text: e.message }); });
  };
  var sealBox = function(box, after) {
    api(MELI_PACK_BASE + '/boxes/' + box.id + '/seal', { method: 'POST', body: '{}' })
      .then(function() { showToast('Box ' + box.box_number + ' sealed'); if (after) after(); reload(); })
      .catch(function(e) { showToast(e.message, 'error'); });
  };
  var unsealBox = function(box) {
    api(MELI_PACK_BASE + '/boxes/' + box.id + '/unseal', { method: 'POST', body: '{}' })
      .then(function() { showToast('Box ' + box.box_number + ' unsealed'); reload(); })
      .catch(function(e) { showToast(e.message, 'error'); });
  };

  // ─── Box actions ───
  var confirmDeleteBox = function() {
    var box = deleteBoxConfirm; setDeleteBoxConfirm(null);
    api(shipBase + '/boxes/' + box.id, { method: 'DELETE' })
      .then(function() { showToast('Box ' + box.box_number + ' deleted — its units went back to the shipment'); if (scanBoxId === box.id) setScanBoxId(null); reload(); })
      .catch(function(e) { showToast(e.message, 'error'); });
  };
  var runDuplicate = function() {
    var m = duplicateModal; if (!m || m.saving) return;
    var count = Math.max(1, Math.min(999, parseInt(m.count, 10) || 1));
    setDuplicateModal(Object.assign({}, m, { saving: true }));
    api(shipBase + '/boxes/' + m.box.id + '/duplicate', { method: 'POST', body: JSON.stringify({ count: count }) })
      .then(function(r) { showToast(r.count + ' cop' + (r.count === 1 ? 'y' : 'ies') + ' of box ' + m.box.box_number + ' added'); setDuplicateModal(null); reload(); })
      .catch(function(e) { showToast(e.message, 'error'); setDuplicateModal(Object.assign({}, m, { saving: false })); });
  };
  var openEditBox = function(box) {
    setEditBox({ box: box, dims: { length: box.length || '', width: box.width || '', height: box.height || '', weight: box.weight || '' }, addItemId: '', addQty: '', saving: false });
  };
  var editBoxLive = editBox ? boxes.find(function(b) { return b.id === editBox.box.id; }) || editBox.box : null;
  var saveEditDims = function() {
    var e = editBox; if (!e) return;
    setEditBox(Object.assign({}, e, { saving: true }));
    api(shipBase + '/boxes/' + e.box.id, { method: 'PUT', body: JSON.stringify(e.dims) })
      .then(function() { showToast('Box ' + e.box.box_number + ' updated'); setEditBox(Object.assign({}, e, { saving: false })); reload(); })
      .catch(function(err) { showToast(err.message, 'error'); setEditBox(Object.assign({}, e, { saving: false })); });
  };
  var setLineQty = function(box, line, qty) {
    api(MELI_PACK_BASE + '/boxes/' + box.id + '/items/' + line.id, { method: 'PUT', body: JSON.stringify({ quantity: qty }) })
      .then(function() { reload(); }).catch(function(e) { showToast(e.message, 'error'); });
  };
  var removeLine = function(box, line) {
    api(MELI_PACK_BASE + '/boxes/' + box.id + '/items/' + line.id, { method: 'DELETE' })
      .then(function() { showToast(line.sku + ' removed from box ' + box.box_number); reload(); }).catch(function(e) { showToast(e.message, 'error'); });
  };
  var addLine = function() {
    var e = editBox; if (!e || !e.addItemId) return;
    var qty = parseInt(e.addQty, 10);
    if (!qty || qty < 1) { showToast('Enter a quantity', 'error'); return; }
    api(MELI_PACK_BASE + '/boxes/' + e.box.id + '/bulk-pack', { method: 'POST', body: JSON.stringify({ item_id: e.addItemId, quantity: qty }) })
      .then(function(r) { showToast(r.product.sku + ' x' + qty + ' added to box ' + e.box.box_number); setEditBox(Object.assign({}, e, { addItemId: '', addQty: '' })); reload(); })
      .catch(function(err) { showToast(err.message, 'error'); });
  };
  var printBoxLabels = function(list) {
    var doc = meliBoxLabelsPdf(plan, list);
    if (!doc) { showToast('PDF library not loaded', 'error'); return; }
    var url = URL.createObjectURL(doc.output('blob'));
    var w = window.open(url, '_blank');
    if (!w) showToast('Pop-up blocked. Allow pop-ups for this site.', 'error');
    setTimeout(function() { URL.revokeObjectURL(url); }, 60000);
  };
  // ML product labels for one box: one per unit in the box (the items' plan units are replaced)
  var printBoxMlLabels = function(box) {
    if (!onPrintLabels) return;
    var byItem = {};
    items.forEach(function(it) { byItem[it.id] = it; });
    var list = (box.items || []).map(function(l) { var it = byItem[l.shipment_item_id] || {}; return Object.assign({}, it, { id: l.shipment_item_id, sku: l.sku, title: l.title || it.title, ml_code: l.ml_code || it.ml_code, quantity: l.quantity }); });
    onPrintLabels(list);
  };

  // ─── Missing / errored ───
  var saveMissing = function() {
    var m = missingModal; if (!m || m.saving) return;
    var qty = parseInt(m.qty, 10);
    if (!(qty >= 0)) { showToast('Enter the missing count', 'error'); return; }
    setMissingModal(Object.assign({}, m, { saving: true }));
    api(MELI_PACK_BASE + '/items/' + m.item.id + '/missing', { method: 'PUT', body: JSON.stringify({ quantity_missing: qty, notes: m.note }) })
      .then(function() { showToast(qty ? qty + ' unit' + (qty === 1 ? '' : 's') + ' of ' + m.item.sku + ' marked missing' : m.item.sku + ' missing mark cleared'); setMissingModal(null); reload(); })
      .catch(function(e) { showToast(e.message, 'error'); setMissingModal(Object.assign({}, m, { saving: false })); });
  };
  var saveErrored = function() {
    var m = erroredModal; if (!m || m.saving) return;
    var qty = parseInt(m.qty, 10);
    if (!(qty > 0)) { showToast('Enter how many units cannot ship', 'error'); return; }
    setErroredModal(Object.assign({}, m, { saving: true }));
    api(MELI_PACK_BASE + '/plans/' + planId + '/errored-items', { method: 'POST', body: JSON.stringify({ items: [{ item_id: m.item.id, quantity: qty, reason: m.reason }] }) })
      .then(function(r) {
        var c = r && r.vendor_direct_carry;
        showToast(qty + ' unit' + (qty === 1 ? '' : 's') + ' of ' + m.item.sku + ' errored' + (c && c.draft_po_number ? ' — carried to draft PO#' + c.draft_po_number : ''));
        setErroredModal(null); reload();
      })
      .catch(function(e) { showToast(e.message, 'error'); setErroredModal(Object.assign({}, m, { saving: false })); });
  };

  // ─── Renders ───
  var btnDanger = { background: 'rgba(248,113,113,0.1)', color: '#F87171', border: '1px solid rgba(248,113,113,0.3)' };
  var renderProgress = function() {
    var pct = totalUnits ? Math.round(((totalPacked + totalMissing) / totalUnits) * 100) : 0;
    return h('div', { style: { marginBottom: 12 } },
      h('div', { style: { display: 'flex', justifyContent: 'space-between', fontSize: 12, color: C.textMuted, marginBottom: 4 } },
        h('span', null, totalPacked + ' of ' + totalUnits + ' units packed' + (totalMissing ? ' · ' + totalMissing + ' missing' : '')),
        h('span', null, boxes.length + ' box' + (boxes.length === 1 ? '' : 'es') + (openBoxes.length ? ' · ' + openBoxes.length + ' open' : ''))),
      h('div', { style: { height: 6, borderRadius: 3, background: C.surface, overflow: 'hidden' } },
        h('div', { style: { width: pct + '%', height: '100%', background: pct >= 100 ? C.success : C.accent, transition: 'width 0.3s' } })));
  };
  var renderTabs = function() {
    var tabs = [
      { id: 'items', label: 'Items to Pack', count: availableItems.length, color: C.accent },
      { id: 'missing', label: 'Missing', count: missingItems.length, color: '#F87171' },
      { id: 'errored', label: 'Errored', count: errored.length, color: '#EF4444' },
      { id: 'activity', label: 'Activity Log', count: 0, color: '#8B5CF6' },
    ];
    return h('div', { style: { display: 'flex', gap: 2, marginBottom: 12, borderBottom: '1px solid ' + C.border } },
      tabs.map(function(t) {
        var active = packTab === t.id;
        return h('button', { key: t.id, onClick: function() { setPackTab(t.id); },
          style: { padding: '8px 14px', fontSize: 13, fontWeight: active ? 700 : 500, color: active ? t.color : C.textMuted, background: 'transparent', border: 'none', borderBottom: '2px solid ' + (active ? t.color : 'transparent'), marginBottom: -1, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6 } },
          t.label,
          t.count > 0 && h('span', { style: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 20, height: 20, padding: '0 6px', borderRadius: 10, background: active ? t.color : t.color + '22', color: active ? '#fff' : t.color, fontSize: 11, fontWeight: 700 } }, t.count));
      }));
  };
  var renderScanPanel = function() {
    var box = boxes.find(function(b) { return b.id === scanBoxId; });
    if (!box) return null;
    var units = (box.items || []).reduce(function(s, l) { return s + l.quantity; }, 0);
    return h(Card, { style: { marginBottom: 12, border: '1px solid ' + C.accent + '66' } },
      h('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 10 } },
        h('div', null,
          h('div', { style: { fontSize: 14, fontWeight: 600, color: C.text } }, 'Scanning into Box ' + box.box_number),
          h('div', { style: { fontSize: 12, color: C.textMuted } }, units + ' unit' + (units === 1 ? '' : 's') + ' in the box · scan the ML Code on the label, the GTIN, or type the SKU')),
        h('div', { style: { display: 'flex', gap: 6 } },
          h(Btn, { size: 'sm', variant: 'primary', disabled: !units, onClick: function() { sealBox(box, function() { setScanBoxId(null); }); } }, h(LIcon, { name: 'lock', size: 13 }), ' Seal Box'),
          h(Btn, { size: 'sm', onClick: function() { setScanBoxId(null); setLastScan(null); } }, 'Done'))),
      h('input', { ref: scanInputRef, value: scanCode, autoFocus: true, placeholder: 'Scan or type, then Enter',
        onChange: function(e) { setScanCode(e.target.value); },
        onKeyDown: function(e) { if (e.key === 'Enter') { e.preventDefault(); submitScan(); } },
        style: { width: '100%', padding: '12px 14px', fontSize: 16, fontWeight: 600, border: '2px solid ' + C.accent, borderRadius: 8, background: C.bg, color: C.text, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' } }),
      lastScan && h('div', { style: { marginTop: 8, padding: '8px 10px', borderRadius: 6, fontSize: 13, fontWeight: 600, background: lastScan.ok ? 'rgba(34,197,94,0.1)' : 'rgba(248,113,113,0.1)', color: lastScan.ok ? '#16A34A' : '#F87171', display: 'flex', alignItems: 'center', gap: 6 } },
        h(LIcon, { name: lastScan.ok ? 'check-circle' : 'alert-triangle', size: 14 }), lastScan.text));
  };
  var renderItemRow = function(it, big) {
    var remaining = meliPackRemaining(it), packed = parseInt(it.quantity_packed, 10) || 0, missing = parseInt(it.quantity_missing, 10) || 0;
    return h('div', { key: it.id, style: { display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 0', borderBottom: '1px solid ' + C.border + '33' } },
      meliThumb(it, big ? 72 : 48),
      h('div', { style: { flex: 1, minWidth: 0 } },
        h('div', { style: { fontSize: big ? 15 : 13, fontWeight: 600, marginBottom: 4, color: C.text } }, it.title || it.sku),
        meliItemPills(it)),
      h('div', { style: { textAlign: 'right', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5 } },
        h('div', { style: { fontSize: 16, fontWeight: 700, color: C.accent } }, remaining + ' remaining'),
        (packed > 0 || missing > 0) && h('div', { style: { fontSize: 12, color: C.textMuted } }, packed + ' of ' + it.quantity + ' packed' + (missing ? ' · ' + missing + ' missing' : '')),
        h('div', { style: { display: 'flex', gap: 4 } },
          packOpen && h('button', { onClick: function() { setMissingModal({ item: it, qty: String(missing || remaining), note: '' }); }, title: 'Declare units missing (the PO ledger follows)',
            style: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: 4, border: '1px solid #F8717144', background: 'rgba(248,113,113,0.08)', color: '#F87171', cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: 'inherit' } },
            h(LIcon, { name: 'alert-triangle', size: 12 }), 'Missing'),
          packOpen && h('button', { onClick: function() { setErroredModal({ item: it, qty: String(remaining), reason: '' }); }, title: 'Units that cannot go to Full — they come off the plan',
            style: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: 4, border: '1px solid #EF444444', background: 'rgba(239,68,68,0.08)', color: '#EF4444', cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: 'inherit' } },
            h(LIcon, { name: 'ban', size: 12 }), 'Errored'),
          it.ml_code && onPrintLabels && h('button', { onClick: function() { onPrintLabels([Object.assign({}, it, { quantity: remaining || it.quantity })]); }, title: 'Print ML labels for ' + it.sku,
            style: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: 4, border: 'none', background: '#3483FA', color: '#fff', cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: 'inherit' } },
            h(LIcon, { name: 'printer', size: 12 }), 'ML Labels'))));
  };
  var renderItemsTab = function() {
    var filtered = filterItems(availableItems);
    return h(Card, null,
      h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' } },
        h('div', { style: { fontSize: 15, fontWeight: 600, color: C.text } }, 'Items Remaining to Pack'),
        h('div', { style: { display: 'flex', gap: 8 } },
          // (the pick list sits in the stage header above, next to Work Orders — like FBA)
          packOpen && availableItems.length > 0 && !scanBoxId && h(Btn, { size: 'sm', onClick: function() {
            if (openBoxes.length) { setScanBoxId(openBoxes[openBoxes.length - 1].id); setLastScan(null); } else newOpenBox();
          } }, h(LIcon, { name: 'scan-line', size: 14 }), openBoxes.length ? ' Scan into Box ' + openBoxes[openBoxes.length - 1].box_number : ' New Box + Scan'),
          packOpen && availableItems.length > 0 && h(Btn, { variant: 'primary', size: 'sm', onClick: startWizard }, h(LIcon, { name: 'plus', size: 14 }), ' Pack a Box'))),
      availableItems.length === 0
        ? h('div', { style: { textAlign: 'center', padding: '20px 0' } },
            h(LIcon, { name: 'check-circle', size: 32, color: C.success }),
            h('div', { style: { fontSize: 14, fontWeight: 600, color: C.success, marginTop: 8 } }, totalMissing ? 'All units packed or declared missing' : 'All units packed!'),
            openBoxes.length > 0 && h('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 4 } }, openBoxes.length + ' box' + (openBoxes.length === 1 ? ' is' : 'es are') + ' still open — seal before Mark as Packed'))
        : h('div', null,
            h('input', { value: itemSearch, onChange: function(e) { setItemSearch(e.target.value); }, placeholder: 'Search by SKU, ML Code, GTIN or title...',
              style: { width: '100%', padding: '8px 12px', fontSize: 12, border: '1px solid ' + C.border, borderRadius: 6, background: C.bg, color: C.text, outline: 'none', boxSizing: 'border-box', marginBottom: 6, fontFamily: 'inherit' } }),
            filtered.length === 0 ? h('div', { style: { textAlign: 'center', padding: '16px 0', color: C.textMuted, fontSize: 13 } }, 'No items match "' + itemSearch + '"')
              : filtered.map(function(it) { return renderItemRow(it, true); })));
  };
  var renderMissingTab = function() {
    return h(Card, null,
      h('div', { style: { fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 4 } }, 'Missing Units'),
      h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 10 } }, 'Declared missing here and on the PO ledger, so the PO can still close short. Clear a mark by setting it to 0.'),
      missingItems.length === 0 ? h('div', { style: { textAlign: 'center', padding: '16px 0', color: C.textMuted, fontSize: 13 } }, 'Nothing declared missing')
        : missingItems.map(function(it) {
          return h('div', { key: it.id, style: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid ' + C.border + '33' } },
            meliThumb(it, 48),
            h('div', { style: { flex: 1, minWidth: 0 } }, h('div', { style: { fontSize: 13, fontWeight: 600, marginBottom: 4 } }, it.title || it.sku), meliItemPills(it)),
            h('div', { style: { textAlign: 'right' } },
              h('div', { style: { fontSize: 16, fontWeight: 700, color: '#F87171' } }, it.quantity_missing + ' missing'),
              packOpen && h('button', { onClick: function() { setMissingModal({ item: it, qty: String(it.quantity_missing), note: '' }); },
                style: { marginTop: 4, background: 'none', border: '1px solid ' + C.border, color: C.textMuted, cursor: 'pointer', fontSize: 11, padding: '3px 8px', borderRadius: 4, fontFamily: 'inherit' } }, 'Change')));
        }));
  };
  var renderErroredTab = function() {
    return h(Card, null,
      h('div', { style: { fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 4 } }, 'Errored Units'),
      h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 10 } }, 'Units that cannot go to Full. They came off this plan; on a vendor-direct PO the lines carried to the vendor\'s draft PO.'),
      errored.length === 0 ? h('div', { style: { textAlign: 'center', padding: '16px 0', color: C.textMuted, fontSize: 13 } }, 'No errored units')
        : errored.map(function(e) {
          var reasons = Array.isArray(e.reasons) ? e.reasons : [];
          return h('div', { key: e.id, style: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid ' + C.border + '33' } },
            h('div', { style: { flex: 1, minWidth: 0 } },
              h('div', { style: { fontSize: 13, fontWeight: 600 } }, e.vendor_title || e.sku),
              h('div', { style: { fontSize: 12, color: C.textMuted } }, e.sku + (e.ml_code ? ' · ML ' + e.ml_code : '') + (reasons.length ? ' · ' + reasons.join('; ') : '') + (e.removed_by ? ' · ' + e.removed_by : '') + (e.created_at ? ' · ' + new Date(e.created_at).toLocaleDateString() : ''))),
            h('div', { style: { fontSize: 16, fontWeight: 700, color: '#EF4444' } }, 'x' + e.quantity));
        }));
  };
  var renderStep1 = function() {
    var filtered = filterItems(availableItems);
    var allOn = selectedCount === availableItems.length && availableItems.length > 0;
    return h(Card, null,
      h('div', { style: { fontSize: 14, fontWeight: 600, marginBottom: 4 } }, 'Step 1: Select Items for This Box'),
      h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 10 } }, 'Pick the items going into this box. A quantity typed here carries to the next step.'),
      h('div', { style: { display: 'flex', gap: 8, marginBottom: 12 } },
        h(Btn, { onClick: function() { setWizStep(0); } }, h(LIcon, { name: 'arrow-left', size: 14 }), ' Back'),
        h(Btn, { onClick: cancelWizard, style: btnDanger }, h(LIcon, { name: 'x', size: 14 }), ' Cancel'),
        h(Btn, { variant: 'primary', disabled: selectedCount === 0, onClick: function() {
          var q = {};
          selectedItemsList.forEach(function(it) { var n = parseInt(packQtys[it.id], 10); if (n > 0) q[it.id] = Math.min(n, meliPackRemaining(it)); });
          setPackQtys(q); setWizStep(2);
        } }, 'Next: Verify Items ', h(LIcon, { name: 'arrow-right', size: 14 }))),
      h('input', { value: itemSearch, onChange: function(e) { setItemSearch(e.target.value); }, placeholder: 'Search by SKU, ML Code, GTIN or title...',
        style: { width: '100%', padding: '8px 12px', fontSize: 12, border: '1px solid ' + C.border, borderRadius: 6, background: C.bg, color: C.text, outline: 'none', boxSizing: 'border-box', marginBottom: 10, fontFamily: 'inherit' } }),
      h('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid ' + C.border } },
        h('button', { onClick: function() { if (allOn) setSelectedItems({}); else { var n = {}; availableItems.forEach(function(it) { n[it.id] = true; }); setSelectedItems(n); } },
          style: { display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit' } },
          checkBox(allOn, C.accent), h('span', { style: { fontSize: 12, fontWeight: 600, color: C.text } }, 'Select All (' + availableItems.length + ' items)')),
        h('button', { onClick: addAll, title: 'Select every item and fill its quantity to the max remaining',
          style: { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 6, border: '1px solid ' + C.accent + '55', background: C.accent + '14', color: C.accent, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit' } },
          h(LIcon, { name: 'check-circle', size: 13 }), 'Add All')),
      filtered.length === 0 ? h('div', { style: { textAlign: 'center', padding: '16px 0', color: C.textMuted, fontSize: 12 } }, 'No items match "' + itemSearch + '"')
        : filtered.map(function(it) {
          var checked = !!selectedItems[it.id], maxQty = meliPackRemaining(it);
          var qtyVal = packQtys[it.id] != null ? packQtys[it.id] : '';
          var qtyParsed = parseInt(qtyVal, 10);
          return h('div', { key: it.id, onClick: function() { toggleItem(it.id); },
            style: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 8px', marginBottom: 4, borderRadius: 8, cursor: 'pointer', background: checked ? C.accent + '08' : 'transparent', border: '1px solid ' + (checked ? C.accent + '44' : 'transparent') } },
            checkBox(checked, C.accent),
            meliThumb(it, 48),
            h('div', { style: { flex: 1, minWidth: 0 } }, h('div', { style: { fontSize: 13, fontWeight: 600, marginBottom: 4 } }, it.title || it.sku), meliItemPills(it)),
            h('div', { onClick: function(e) { e.stopPropagation(); }, style: { width: 104, textAlign: 'center', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 4 } },
              h('div', { style: { fontSize: 12, fontWeight: 700, color: C.accent } }, Math.max(0, maxQty - (isNaN(qtyParsed) ? 0 : qtyParsed)) + ' remaining'),
              h('input', { type: 'number', min: 0, max: maxQty, value: qtyVal, placeholder: '0',
                onChange: function(e) {
                  var raw = e.target.value;
                  if (raw === '') { setPackQtys(function(q) { var nq = Object.assign({}, q); delete nq[it.id]; return nq; }); return; }
                  var n = parseInt(raw, 10); if (isNaN(n) || n < 0) n = 0; if (n > maxQty) n = maxQty;
                  setPackQtys(function(q) { var nq = Object.assign({}, q); nq[it.id] = n; return nq; });
                  if (n > 0 && !selectedItems[it.id]) setSelectedItems(function(prev) { var ns = Object.assign({}, prev); ns[it.id] = true; return ns; });
                },
                style: numInputStyle(qtyVal !== '' && qtyParsed > 0 && qtyParsed <= maxQty) }),
              h('div', { style: { fontSize: 9, color: C.textMuted } }, 'units')));
        }));
  };
  var renderStep2 = function() {
    return h(Card, null,
      h('div', { style: { fontSize: 14, fontWeight: 600, marginBottom: 4 } }, 'Step 2: Verify Items & Enter Quantities'),
      h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 10 } }, 'Confirm the quantity of each item going into this box.'),
      h('div', { style: { display: 'flex', gap: 8, marginBottom: 14 } },
        h(Btn, { onClick: function() { setWizStep(1); } }, h(LIcon, { name: 'arrow-left', size: 14 }), ' Back'),
        h(Btn, { onClick: cancelWizard, style: btnDanger }, h(LIcon, { name: 'x', size: 14 }), ' Cancel'),
        h(Btn, { variant: 'primary', disabled: !allQtysEntered, onClick: function() { setWizStep(3); } }, 'Next: Box Dimensions ', h(LIcon, { name: 'arrow-right', size: 14 }))),
      selectedItemsList.map(function(it) {
        var remaining = meliPackRemaining(it), q = packQtys[it.id] != null ? packQtys[it.id] : '', qInt = parseInt(q, 10) || 0;
        return h('div', { key: it.id, style: { display: 'flex', gap: 14, padding: '12px 0', borderBottom: '1px solid ' + C.border + '44', alignItems: 'center' } },
          meliThumb(it, 56),
          h('div', { style: { flex: 1, minWidth: 0 } }, h('div', { style: { fontSize: 13, fontWeight: 600, marginBottom: 4 } }, it.title || it.sku), meliItemPills(it)),
          h('div', { style: { width: 130, textAlign: 'center', flexShrink: 0 } },
            h('div', { style: { fontSize: 10, color: C.textMuted, marginBottom: 4 } }, remaining + ' available'),
            h('input', { type: 'number', min: 1, max: remaining, value: q,
              onChange: function(e) { var v = e.target.value; setPackQtys(function(prev) { var n = Object.assign({}, prev); n[it.id] = v; return n; }); },
              style: numInputStyle(qInt > 0 && qInt <= remaining) }),
            h('div', { style: { fontSize: 9, color: C.textMuted, marginTop: 2 } }, 'units')));
      }));
  };
  var renderStep3 = function() {
    var copies = Math.max(1, Math.min(999, parseInt(numCopies, 10) || 1));
    var total = selectedItemsList.reduce(function(s, it) { return s + (parseInt(packQtys[it.id], 10) || 0); }, 0);
    var dimField = function(key, label) {
      return h(Inp, { label: label, type: 'number', step: '0.1', value: dimForm[key], onChange: function(e) { var v = e.target.value; setDimForm(function(f) { var n = Object.assign({}, f); n[key] = v; return n; }); } });
    };
    return h(Card, null,
      h('div', { style: { fontSize: 14, fontWeight: 600, marginBottom: 4 } }, 'Step 3: Box Dimensions & Weight'),
      h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 10 } }, 'Outer dimensions in inches and the weight in pounds — Seller Center asks for them at the appointment. Finish creates and seals the box.'),
      h('div', { style: { display: 'flex', gap: 8, marginBottom: 14 } },
        h(Btn, { onClick: function() { setWizStep(2); } }, h(LIcon, { name: 'arrow-left', size: 14 }), ' Back'),
        h(Btn, { onClick: cancelWizard, style: btnDanger }, h(LIcon, { name: 'x', size: 14 }), ' Cancel'),
        h(Btn, { variant: 'primary', onClick: finishBox, disabled: saving || !dimForm.length || !dimForm.width || !dimForm.height },
          saving ? 'Packing...' : [h(LIcon, { key: 'ic', name: 'check', size: 14 }), copies === 1 ? ' Finish & Seal Box' : ' Finish & Seal ' + copies + ' Boxes'])),
      h('div', { style: { marginBottom: 12, padding: '10px 12px', borderRadius: 8, background: C.surface, border: '1px solid ' + C.border, display: 'flex', alignItems: 'center', gap: 12 } },
        h('div', { style: { fontSize: 12, fontWeight: 600, color: C.text } }, 'Copies'),
        h('input', { type: 'number', min: 1, max: 999, value: numCopies,
          onChange: function(e) { var v = e.target.value; if (v === '') { setNumCopies(''); return; } var n = parseInt(v, 10); if (!isNaN(n)) setNumCopies(Math.max(1, Math.min(999, n))); },
          onBlur: function() { if (!numCopies || parseInt(numCopies, 10) < 1) setNumCopies(1); },
          style: { width: 80, padding: '6px 8px', fontSize: 13, border: '1px solid ' + C.border, borderRadius: 6, background: C.bg, color: C.text, outline: 'none', fontFamily: 'inherit' } }),
        h('div', { style: { fontSize: 11, color: C.textMuted } }, copies === 1 ? 'One box with the items and dims below.' : copies + ' identical boxes — same items, same dimensions.')),
      h('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 16 } },
        dimField('length', 'Length (in)'), dimField('width', 'Width (in)'), dimField('height', 'Height (in)'), dimField('weight', 'Weight (lb)')),
      h('div', { style: { padding: '10px 12px', borderRadius: 8, background: C.surface, border: '1px solid ' + C.border } },
        h('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 6 } }, 'Box Contents'),
        selectedItemsList.map(function(it) {
          return h('div', { key: it.id, style: { display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '3px 0' } }, h('span', null, it.sku), h('span', { style: { fontWeight: 600 } }, (parseInt(packQtys[it.id], 10) || 0) + ' units'));
        }),
        h('div', { style: { display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 700, marginTop: 6, paddingTop: 6, borderTop: '1px solid ' + C.border } }, h('span', null, 'Total'), h('span', null, total + ' units' + (copies > 1 ? ' × ' + copies : '')))));
  };
  var renderBoxRow = function(box) {
    var lines = box.items || [], units = lines.reduce(function(s, l) { return s + l.quantity; }, 0);
    var single = lines.length === 1 && lines[0].image_1_url ? lines[0].image_1_url : null;
    return h('div', { key: box.id, style: { padding: '10px 0', borderBottom: '1px solid ' + C.border + '33' } },
      h('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', gap: 6 }, onClick: function() { setViewBoxId(box.id); } },
        h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 } },
          single ? h('img', { src: single, style: { width: 56, height: 56, borderRadius: 6, objectFit: 'cover', border: '1px solid ' + C.border, flexShrink: 0 } })
            : h('div', { style: { width: 56, height: 56, borderRadius: 6, background: (box.is_sealed ? C.success : C.accent) + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } }, h(LIcon, { name: box.is_sealed ? 'package-check' : 'package-open', size: 24, color: box.is_sealed ? C.success : C.accent })),
          h('div', { style: { minWidth: 0 } },
            h('div', { style: { display: 'flex', alignItems: 'center', gap: 6 } },
              h('div', { style: { fontWeight: 600, fontSize: 16 } }, 'Box ' + box.box_number),
              h('span', { style: pillStyle(box.is_sealed ? C.success + '22' : '#F59E0B22', box.is_sealed ? C.success : '#B45309') }, box.is_sealed ? 'Sealed' : 'Open')),
            h('div', { style: { fontSize: 12, color: C.textMuted } }, units + ' units / ' + lines.length + ' SKU' + (lines.length === 1 ? '' : 's')),
            (box.length && box.width && box.height) && h('div', { style: { fontSize: 12, color: C.textMuted } }, box.length + '" x ' + box.width + '" x ' + box.height + '"' + (box.weight ? ' | ' + box.weight + ' lb' : '')))),
        h('div', { style: { display: 'flex', gap: 0, alignItems: 'center', flexShrink: 0 } },
          iconBtn('tag', 'Box content label (PDF)', C.textMuted, function() { printBoxLabels([box]); }),
          onPrintLabels && lines.some(function(l) { return l.ml_code; }) && iconBtn('printer', 'ML labels for this box', '#3483FA', function() { printBoxMlLabels(box); }),
          packOpen && iconBtn(box.is_sealed ? 'lock-open' : 'lock', box.is_sealed ? 'Unseal' : 'Seal', box.is_sealed ? C.textMuted : C.success, function() { if (box.is_sealed) unsealBox(box); else sealBox(box); }),
          packOpen && iconBtn('copy', 'Duplicate Box', C.accent, function() { setDuplicateModal({ box: box, count: 1, saving: false }); }),
          packOpen && iconBtn('edit-2', 'Edit Box', C.accent, function() { openEditBox(box); }),
          packOpen && iconBtn('trash-2', 'Delete Box', C.danger, function() { setDeleteBoxConfirm(box); }))));
  };
  var renderBoxesColumn = function() {
    var q = boxFilter.trim().toLowerCase();
    var sorted = boxes.slice().sort(function(a, b) { return (b.box_number || 0) - (a.box_number || 0); });
    var shown = q ? sorted.filter(function(b) {
      if (String(b.box_number).indexOf(q) === 0) return true;
      return (b.items || []).some(function(l) { return [l.sku, l.title, l.barcode, l.ml_code, l.vendor_title].some(function(v) { return v && String(v).toLowerCase().indexOf(q) >= 0; }); });
    }) : sorted;
    return h(Card, { style: { position: 'sticky', top: 16, display: 'flex', flexDirection: 'column', maxHeight: 'calc(calc(100vh * var(--invzoom, 1)) - 200px)' } },
      // Boxes | Internal Notes, as FBA's boxes column (Anthony 2026-10-21)
      h('div', { style: { display: 'flex', borderBottom: '1px solid ' + C.border, marginBottom: 10 } },
        ['items', 'notes'].map(function(tab) {
          var isActive = rightTab === tab;
          var label = tab === 'items' ? 'Boxes (' + boxes.length + ')' : 'Internal Notes' + (notes.length > 0 ? ' (' + notes.length + ')' : '');
          return h('button', { key: tab, onClick: function() { setRightTab(tab); },
            style: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px 0', background: 'none', border: 'none', borderBottom: '2px solid ' + (isActive ? C.accent : 'transparent'), color: isActive ? C.accent : C.textMuted, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'color 0.15s, border-color 0.15s' } },
            h(LIcon, { name: tab === 'items' ? 'package' : 'message-square', size: 14 }), label);
        })),
      rightTab === 'items' && h(React.Fragment, null,
        h('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 } },
          h('div', { style: { fontSize: 14, fontWeight: 600, color: C.text } }, 'Packed Boxes'),
          boxes.length > 0 && h(Btn, { size: 'sm', onClick: function() { printBoxLabels(sorted.slice().reverse()); }, title: 'One 4 x 6 content label per box' }, h(LIcon, { name: 'tag', size: 13 }), ' All Box Labels')),
        boxes.length > 1 && h('input', { value: boxFilter, placeholder: 'Find box / SKU / ML Code / GTIN...', onChange: function(e) { setBoxFilter(e.target.value); },
          style: { width: '100%', boxSizing: 'border-box', background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '7px 10px', borderRadius: 6, fontSize: 12, fontFamily: 'inherit', outline: 'none', marginBottom: 8 } }),
        h('div', { style: { flex: 1, overflow: 'auto' } },
          boxesLoading ? h('div', { style: { fontSize: 12, color: C.textMuted, textAlign: 'center', padding: '20px 0' } }, 'Loading...')
            : boxes.length === 0 ? h('div', { style: { fontSize: 12, color: C.textMuted, textAlign: 'center', padding: '20px 0' } }, 'No boxes packed yet')
            : shown.length === 0 ? h('div', { style: { fontSize: 12, color: C.textMuted, textAlign: 'center', padding: '20px 0' } }, 'No boxes match "' + boxFilter + '"')
            : shown.map(renderBoxRow)),
        renderSummary()),
      rightTab === 'notes' && renderNotesTab());
  };
  // The PO's internal notes thread, in FBA's boxes-column shape (own notes on the right, deletable).
  var renderNotesTab = function() {
    return h('div', { style: { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 } },
      h('div', { style: { flex: 1, overflow: 'auto', padding: '4px 0', display: 'flex', flexDirection: 'column', gap: 8, minHeight: 200 } },
        !notesPoId && h('div', { style: { textAlign: 'center', color: C.textDim, fontSize: 13, padding: 40 } }, 'No PO on this plan'),
        notesPoId && notes.length === 0 && h('div', { style: { textAlign: 'center', color: C.textDim, fontSize: 13, padding: 40 } }, 'No notes yet. Start the conversation!'),
        notes.map(function(note) {
          var isMe = currentUser && currentUser.id === note.user_id;
          var time = new Date(note.created_at);
          var timeStr = time.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' + time.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
          return h('div', { key: note.id, style: { display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start', padding: '0 4px' } },
            h('div', { style: { fontSize: 10, color: C.textDim, marginBottom: 2, display: 'flex', alignItems: 'center', gap: 6 } },
              h('span', { style: { fontWeight: 600 } }, note.display_name || note.username),
              h('span', null, timeStr),
              isMe && h('button', { onClick: function() { deleteNote(note.id); }, title: 'Delete note',
                style: { background: 'none', border: 'none', color: C.textDim, cursor: 'pointer', fontSize: 10, padding: 0, display: 'inline-flex', alignItems: 'center' } }, h(LIcon, { name: 'x', size: 10 }))),
            h('div', { style: { background: isMe ? C.accent : C.surface, color: isMe ? '#fff' : C.text, padding: '8px 14px', borderRadius: 12, borderTopRightRadius: isMe ? 4 : 12, borderTopLeftRadius: isMe ? 12 : 4, maxWidth: '85%', fontSize: 13, lineHeight: 1.4, wordBreak: 'break-word', border: isMe ? 'none' : '1px solid ' + C.border } },
              renderNoteBody(note.message), renderNoteAttachments(note)));
        }),
        h('div', { ref: notesEndRef })),
      notesPoId && h('div', { style: { borderTop: '1px solid ' + C.border, padding: '12px 0 0', display: 'flex', gap: 8, alignItems: 'center' } },
        h('div', { style: { flex: 1 } },
          h(MentionInput, { value: newNote, onChange: setNewNote,
            onKeyDown: function(e) { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendNote(); } },
            placeholder: 'Type a note... use @ to tag a teammate', rows: 2,
            style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '10px 14px', borderRadius: 8, fontSize: 13, outline: 'none' } })),
        h(Btn, { variant: 'primary', size: 'sm', onClick: sendNote, disabled: !newNote.trim() }, 'Send')));
  };
  var summaryText = function() {
    if (!summary) return '';
    var lines = ['Plan #' + plan.id + (plan.ml_inbound_ref ? ' · Envío ' + plan.ml_inbound_ref : ''), summary.boxes + ' boxes · ' + summary.units + ' units · ' + summary.weight_lb + ' lb'];
    (summary.groups || []).forEach(function(g) { lines.push(g.boxes + ' x ' + g.dims + ' in' + (g.weight_lb ? ' · ' + g.weight_lb + ' lb' : '') + ' · ' + g.units + ' units'); });
    return lines.join('\n');
  };
  var renderSummary = function() {
    if (!summary || !summary.boxes) return null;
    return h('div', { style: { marginTop: 10, paddingTop: 10, borderTop: '1px solid ' + C.border } },
      h('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 } },
        h('div', { style: { fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' } }, 'Packing Summary (for Seller Center)'),
        h('button', { onClick: function() { try { navigator.clipboard.writeText(summaryText()); showToast('Summary copied'); } catch (e) { showToast('Copy failed', 'error'); } }, title: 'Copy the summary text',
          style: { background: 'none', border: 'none', color: C.accent, cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 4 } }, h(LIcon, { name: 'copy', size: 12 }), 'Copy')),
      h('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginBottom: 6 } },
        [['Boxes', summary.boxes], ['Sealed', summary.sealed], ['Units', summary.units], ['Weight', summary.weight_lb + ' lb']].map(function(p) {
          return h('div', { key: p[0], style: { padding: '6px 8px', borderRadius: 6, background: C.surface, textAlign: 'center' } },
            h('div', { style: { fontSize: 15, fontWeight: 700, color: C.text } }, p[1]), h('div', { style: { fontSize: 10, color: C.textMuted } }, p[0]));
        })),
      (summary.groups || []).map(function(g) {
        return h('div', { key: g.dims, style: { display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '2px 0', color: C.text } },
          h('span', null, g.boxes + ' × ' + g.dims + ' in'), h('span', { style: { color: C.textMuted } }, g.units + ' units' + (g.weight_lb ? ' · ' + g.weight_lb + ' lb' : '')));
      }),
      summary.unweighed > 0 && h('div', { style: { fontSize: 11, color: '#B45309', marginTop: 4 } }, summary.unweighed + ' box' + (summary.unweighed === 1 ? ' has' : 'es have') + ' no weight yet'));
  };

  // ─── Modals ───
  var renderModals = function() {
    var ago = draftPrompt && draftPrompt.updated_at ? (function() { var m = Math.round((Date.now() - new Date(draftPrompt.updated_at).getTime()) / 60000); return m < 1 ? 'just now' : m < 60 ? m + ' min ago' : Math.round(m / 60) + ' h ago'; })() : '';
    var editLines = editBoxLive ? (editBoxLive.items || []) : [];
    var addable = editBoxLive ? availableItems.filter(function(it) { return meliPackRemaining(it) > 0; }) : [];
    return h(React.Fragment, null,
      h(Modal, { open: !!draftPrompt, onClose: function() { setDraftPrompt(null); }, title: 'Resume In-Progress Box?', width: 460 },
        draftPrompt && h('div', null,
          h('div', { style: { fontSize: 13, color: C.text, marginBottom: 14 } }, 'You have an unfinished box for this shipment' + (ago ? ' (saved ' + ago + ')' : '') + ' with ' + draftPrompt.data.lines + ' item' + (draftPrompt.data.lines === 1 ? '' : 's') + '. Resume it, or start a new empty box?'),
          h('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } },
            h(Btn, { onClick: startFresh }, 'Start fresh'),
            h(Btn, { variant: 'primary', onClick: resumeDraft }, h(LIcon, { name: 'rotate-ccw', size: 14 }), ' Resume')))),
      h(Modal, { open: !!deleteBoxConfirm, onClose: function() { setDeleteBoxConfirm(null); }, title: 'Delete Box', width: 400 },
        deleteBoxConfirm && h('div', null,
          h('div', { style: { fontSize: 13, color: C.text, marginBottom: 16 } }, 'Delete box ' + deleteBoxConfirm.box_number + '? Its ' + (deleteBoxConfirm.items || []).reduce(function(s, l) { return s + l.quantity; }, 0) + ' units go back to the shipment for repacking, and the PO receipts reverse.'),
          h('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } },
            h(Btn, { onClick: function() { setDeleteBoxConfirm(null); } }, 'Cancel'),
            h(Btn, { variant: 'primary', onClick: confirmDeleteBox, style: { background: '#DC2626', borderColor: '#DC2626' } }, h(LIcon, { name: 'trash-2', size: 14 }), ' Delete Box')))),
      h(Modal, { open: !!duplicateModal, onClose: function() { if (!(duplicateModal && duplicateModal.saving)) setDuplicateModal(null); }, title: 'Duplicate Box', width: 420 },
        duplicateModal && h('div', null,
          h('div', { style: { fontSize: 13, color: C.text, marginBottom: 12 } }, 'Copies of box ' + duplicateModal.box.box_number + ' with the same items and dimensions. The server refuses more than the remaining units allow.'),
          h(Inp, { label: 'Copies', type: 'number', min: 1, max: 999, value: duplicateModal.count, onChange: function(e) { setDuplicateModal(Object.assign({}, duplicateModal, { count: e.target.value })); } }),
          h('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 } },
            h(Btn, { onClick: function() { setDuplicateModal(null); }, disabled: duplicateModal.saving }, 'Cancel'),
            h(Btn, { variant: 'primary', onClick: runDuplicate, disabled: duplicateModal.saving }, duplicateModal.saving ? 'Copying...' : 'Duplicate')))),
      h(Modal, { open: !!missingModal, onClose: function() { if (!(missingModal && missingModal.saving)) setMissingModal(null); }, title: 'Missing Units', width: 440 },
        missingModal && h('div', null,
          h('div', { style: { fontSize: 13, color: C.text, marginBottom: 4, fontWeight: 600 } }, missingModal.item.title || missingModal.item.sku),
          h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12 } }, 'Total missing for ' + missingModal.item.sku + ' (at most the unpacked units). The PO ledger records the same units, so the PO can close short. 0 clears the mark.'),
          h('div', { style: { display: 'grid', gridTemplateColumns: '120px 1fr', gap: 12 } },
            h(Inp, { label: 'Missing', type: 'number', min: 0, value: missingModal.qty, onChange: function(e) { setMissingModal(Object.assign({}, missingModal, { qty: e.target.value })); } }),
            h(Inp, { label: 'Note (optional)', value: missingModal.note, onChange: function(e) { setMissingModal(Object.assign({}, missingModal, { note: e.target.value })); } })),
          h('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 } },
            h(Btn, { onClick: function() { setMissingModal(null); }, disabled: missingModal.saving }, 'Cancel'),
            h(Btn, { variant: 'primary', onClick: saveMissing, disabled: missingModal.saving, style: { background: '#DC2626', borderColor: '#DC2626' } }, missingModal.saving ? 'Saving...' : 'Save')))),
      h(Modal, { open: !!erroredModal, onClose: function() { if (!(erroredModal && erroredModal.saving)) setErroredModal(null); }, title: 'Errored Units', width: 440 },
        erroredModal && h('div', null,
          h('div', { style: { fontSize: 13, color: C.text, marginBottom: 4, fontWeight: 600 } }, erroredModal.item.title || erroredModal.item.sku),
          h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12 } }, 'Units of ' + erroredModal.item.sku + ' that cannot go to Full (damaged, no ML Code, refused at filing). They come off this plan' + (plan.ship_mode === 'vendor_direct' ? ' and the PO line carries to the vendor\'s draft PO.' : '; the PO line stays for disposition.')),
          h('div', { style: { display: 'grid', gridTemplateColumns: '120px 1fr', gap: 12 } },
            h(Inp, { label: 'Units', type: 'number', min: 1, value: erroredModal.qty, onChange: function(e) { setErroredModal(Object.assign({}, erroredModal, { qty: e.target.value })); } }),
            h(Inp, { label: 'Reason', value: erroredModal.reason, onChange: function(e) { setErroredModal(Object.assign({}, erroredModal, { reason: e.target.value })); } })),
          h('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 } },
            h(Btn, { onClick: function() { setErroredModal(null); }, disabled: erroredModal.saving }, 'Cancel'),
            h(Btn, { variant: 'primary', onClick: saveErrored, disabled: erroredModal.saving, style: { background: '#DC2626', borderColor: '#DC2626' } }, erroredModal.saving ? 'Saving...' : 'Mark Errored')))),
      h(Modal, { open: !!viewBox, onClose: function() { setViewBoxId(null); }, title: viewBox ? 'Box ' + viewBox.box_number + ' Contents' : '', width: 640 },
        viewBox && h('div', null,
          h('div', { style: { display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center', fontSize: 13, color: C.textMuted } },
            h('span', null, (viewBox.items || []).reduce(function(s, l) { return s + l.quantity; }, 0) + ' units / ' + (viewBox.items || []).length + ' SKUs'),
            (viewBox.length && viewBox.width && viewBox.height) && h('span', null, viewBox.length + '" x ' + viewBox.width + '" x ' + viewBox.height + '"' + (viewBox.weight ? ' | ' + viewBox.weight + ' lb' : '')),
            h('span', { style: pillStyle(viewBox.is_sealed ? C.success + '22' : '#F59E0B22', viewBox.is_sealed ? C.success : '#B45309') }, viewBox.is_sealed ? 'Sealed' : 'Open'),
            packOpen && h('span', { style: { marginLeft: 'auto' } }, h(Btn, { size: 'sm', onClick: function() { setViewBoxId(null); openEditBox(viewBox); } }, h(LIcon, { name: 'edit-2', size: 12 }), ' Edit Box'))),
          (viewBox.items || []).length === 0 ? h('div', { style: { fontSize: 13, color: C.textMuted, textAlign: 'center', padding: '20px 0' } }, 'Empty box')
            : (viewBox.items || []).map(function(l) {
              return h('div', { key: l.id, style: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid ' + C.border + '33' } },
                meliThumb(l, 56),
                h('div', { style: { flex: 1, minWidth: 0 } }, h('div', { style: { fontSize: 13, fontWeight: 600, marginBottom: 4 } }, l.vendor_title || l.title || l.sku), meliItemPills(l)),
                h('div', { style: { fontSize: 18, fontWeight: 700, color: C.accent, minWidth: 50, textAlign: 'right' } }, 'x' + l.quantity));
            }))),
      h(Modal, { open: !!editBox, onClose: function() { if (!(editBox && editBox.saving)) setEditBox(null); }, title: editBox ? 'Edit Box ' + editBox.box.box_number : '', width: 640 },
        editBox && editBoxLive && h('div', null,
          editBoxLive.is_sealed && h('div', { style: { fontSize: 12, color: '#B45309', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 } }, h(LIcon, { name: 'lock', size: 13 }), 'Sealed — unseal to change the contents; dimensions can still be saved.'),
          h('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr auto', gap: 10, alignItems: 'end', marginBottom: 14 } },
            ['length', 'width', 'height', 'weight'].map(function(k) {
              return h(Inp, { key: k, label: k === 'weight' ? 'Weight (lb)' : k.charAt(0).toUpperCase() + k.slice(1) + ' (in)', type: 'number', step: '0.1', value: editBox.dims[k], onChange: function(e) { var v = e.target.value; setEditBox(function(prev) { var d = Object.assign({}, prev.dims); d[k] = v; return Object.assign({}, prev, { dims: d }); }); } });
            }),
            h(Btn, { size: 'sm', onClick: saveEditDims, disabled: editBox.saving }, editBox.saving ? 'Saving...' : 'Save Dims')),
          h('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 6 } }, 'Items in Box (' + editLines.length + ')'),
          editLines.length === 0 && h('div', { style: { fontSize: 12, color: C.textMuted, padding: '8px 0' } }, 'Empty box'),
          editLines.map(function(l) {
            var it = items.find(function(x) { return x.id === l.shipment_item_id; });
            var maxQty = l.quantity + (it ? meliPackRemaining(it) : 0);
            return h('div', { key: l.id, style: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid ' + C.border + '33' } },
              meliThumb(l, 40),
              h('div', { style: { flex: 1, minWidth: 0 } }, h('div', { style: { fontSize: 13, fontWeight: 600 } }, l.vendor_title || l.title || l.sku), h('div', { style: { fontSize: 11, color: C.textMuted } }, l.sku + (l.ml_code ? ' · ML ' + l.ml_code : ''))),
              h('input', { type: 'number', min: 0, max: maxQty, defaultValue: l.quantity, disabled: editBoxLive.is_sealed, key: 'q' + l.id + '-' + l.quantity,
                onBlur: function(e) { var n = parseInt(e.target.value, 10); if (!isNaN(n) && n !== l.quantity) setLineQty(editBoxLive, l, Math.max(0, Math.min(maxQty, n))); },
                onKeyDown: function(e) { if (e.key === 'Enter') e.currentTarget.blur(); },
                style: { width: 70, padding: '6px', fontSize: 14, fontWeight: 700, textAlign: 'center', border: '1px solid ' + C.border, borderRadius: 6, background: C.bg, color: C.text, fontFamily: 'inherit' } }),
              !editBoxLive.is_sealed && iconBtn('trash-2', 'Remove from box', C.danger, function() { removeLine(editBoxLive, l); }));
          }),
          !editBoxLive.is_sealed && addable.length > 0 && h('div', { style: { display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 12 } },
            h('div', { style: { flex: 1 } },
              h('label', { style: { fontSize: 11, color: C.textMuted, display: 'block', marginBottom: 4 } }, 'Add item'),
              h('select', { value: editBox.addItemId, onChange: function(e) { var v = e.target.value; setEditBox(function(prev) { return Object.assign({}, prev, { addItemId: v }); }); },
                style: { width: '100%', padding: '8px', fontSize: 12, border: '1px solid ' + C.border, borderRadius: 6, background: C.bg, color: C.text, fontFamily: 'inherit' } },
                h('option', { value: '' }, 'Choose...'),
                addable.map(function(it) { return h('option', { key: it.id, value: it.id }, it.sku + ' — ' + meliPackRemaining(it) + ' remaining'); }))),
            h('div', { style: { width: 90 } }, h(Inp, { label: 'Units', type: 'number', min: 1, value: editBox.addQty, onChange: function(e) { var v = e.target.value; setEditBox(function(prev) { return Object.assign({}, prev, { addQty: v }); }); } })),
            h(Btn, { variant: 'primary', size: 'sm', onClick: addLine, disabled: !editBox.addItemId }, 'Add')))));
  };

  return h('div', null,
    renderProgress(),
    h('div', { style: { display: 'flex', gap: 16, alignItems: 'flex-start' } },
      h('div', { style: { flex: 1, minWidth: 0 } },
        wizStep === 0 && renderTabs(),
        wizStep === 0 && scanBoxId && renderScanPanel(),
        wizStep === 0 && packTab === 'items' && renderItemsTab(),
        wizStep === 0 && packTab === 'missing' && renderMissingTab(),
        wizStep === 0 && packTab === 'errored' && renderErroredTab(),
        wizStep === 0 && packTab === 'activity' && h(MeliActivityLog, { planId: planId }),
        wizStep === 1 && renderStep1(),
        wizStep === 2 && renderStep2(),
        wizStep === 3 && renderStep3()),
      h('div', { style: { width: 400, flexShrink: 0, marginTop: wizStep === 0 ? 44 : 0 } }, renderBoxesColumn())),
    renderModals());
}
window.MeliBoxPackingView = MeliBoxPackingView;
window.MeliActivityLog = MeliActivityLog;
})();

;
/* ===== app-meli-research.js ===== */
// ─── MELI Product Research (docs/17 P2 — dashboard redesign 2026-07-26) ───
// Analyzer.tools-style dashboard in the app's own theme: monthly processed/found
// charts + recent scans. A SCAN is one processed batch — "Process Spreadsheet"
// (CSV upload with column mapping, per-row costs, carried-through reference columns)
// or "Quick Lookup" (pasted codes). Clicking a scan opens the spreadsheet-like
// results view. Mexico only. Economics: ML price auto-converted to the chosen base
// currency (live FX — the user picks the currency, never types a rate), minus
// commission %, seller shipping at ≥ $299 MXN, prep, and inbound freight (% of cost:
// vendor → our warehouse → fulfillment center).

var { createElement: h, Fragment } = React;

var FREE_SHIP_MXN = 299; // ML MX mandates free shipping at/above this item price — seller pays the leg
var MAX_SCAN_ROWS = 10000; // server cap per scan — the modal warns instead of silently truncating

// Poll pacing for running scans (review 2026-09-27 ML18): 4 s while progress moves, stretching
// ×1.5 per unchanged tick up to 30 s. MR_ROWS_REFRESH_MS caps how often the full row set is
// re-fetched while a scan runs (the counts come from the light /progress endpoint).
var MR_POLL_BASE_MS = 4000, MR_POLL_MAX_MS = 30000, MR_ROWS_REFRESH_MS = 20000;
function meliPollDelay(idleTicks) {
  var n = Math.max(0, Math.min(20, Number(idleTicks) || 0));
  return Math.min(MR_POLL_MAX_MS, Math.round(MR_POLL_BASE_MS * Math.pow(1.5, n)));
}

// ── Tiny CSV parser (quoted fields, CRLF, escaped quotes) ──
function mrParseCsv(text) {
  var rows = [], row = [], cell = '', inQ = false;
  for (var i = 0; i < text.length; i++) {
    var ch = text[i];
    if (inQ) {
      if (ch === '"') {
        if (text[i + 1] === '"') { cell += '"'; i++; }
        else inQ = false;
      } else cell += ch;
    } else if (ch === '"') inQ = true;
    else if (ch === ',') { row.push(cell); cell = ''; }
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(cell); cell = '';
      if (row.length > 1 || (row.length === 1 && row[0].trim() !== '')) rows.push(row);
      row = [];
    } else cell += ch;
  }
  if (cell !== '' || row.length) { row.push(cell); if (row.length > 1 || row[0].trim() !== '') rows.push(row); }
  return rows;
}

// ── SVG charts (self-contained; instant React hover tooltip + highlight) ──
function MrChartTooltip({ xPct, yPct, label, value, color }) {
  return h('div', {
    style: { position: 'absolute', left: xPct + '%', top: yPct + '%', transform: 'translate(-50%, -130%)',
      background: C.bg, border: '1px solid ' + color, borderRadius: 8, padding: '5px 10px',
      pointerEvents: 'none', whiteSpace: 'nowrap', zIndex: 5, boxShadow: '0 4px 14px rgba(0,0,0,0.4)' }
  },
    h('div', { style: { fontSize: 13, color: color } }, value.toLocaleString()));
}

function MrAreaChart({ data, color, label }) {
  var [hover, setHover] = useState(null);
  var W = 600, H = 230, padL = 46, padR = 16, padT = 26, padB = 30;
  var max = Math.max.apply(null, [1].concat(data.map(function(d) { return d.value; })));
  var iw = W - padL - padR, ih = H - padT - padB;
  var px = function(i) { return padL + (data.length > 1 ? (i / (data.length - 1)) * iw : iw / 2); };
  var py = function(v) { return padT + ih - (v / max) * ih; };
  var line = data.map(function(d, i) { return px(i) + ',' + py(d.value); }).join(' ');
  var area = px(0) + ',' + (padT + ih) + ' ' + line + ' ' + px(Math.max(0, data.length - 1)) + ',' + (padT + ih);
  var ticks = [0, 0.5, 1].map(function(f) { return Math.round(max * f); });
  var colW = data.length > 0 ? iw / data.length : iw;
  // Fills the parent card's height (cards are fixed-height so all dashboard tiles match);
  // aspectRatio keeps svg coords aligned with the wrapper so tooltip % math stays exact.
  return h('div', { style: { height: '100%', display: 'flex', flexDirection: 'column' } },
    h('div', { style: { display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center', marginBottom: 4 } },
      h('span', { style: { width: 10, height: 10, borderRadius: 5, background: color, display: 'inline-block' } }),
      h('span', { style: { fontSize: 12, color: C.textMuted } }, label)),
    h('div', { style: { flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' } },
    h('div', { style: { position: 'relative', width: '100%', maxHeight: '100%', aspectRatio: W + ' / ' + H } },
      hover != null && data[hover] && h(MrChartTooltip, {
        xPct: px(hover) / W * 100, yPct: py(data[hover].value) / H * 100,
        label: data[hover].label, value: data[hover].value, color: color }),
      h('svg', { viewBox: '0 0 ' + W + ' ' + H, style: { width: '100%', height: '100%', display: 'block' },
        onMouseLeave: function() { setHover(null); } },
        h('defs', null, h('linearGradient', { id: 'mrGrad-' + color.replace('#', ''), x1: 0, y1: 0, x2: 0, y2: 1 },
          h('stop', { offset: '0%', stopColor: color, stopOpacity: 0.55 }),
          h('stop', { offset: '100%', stopColor: color, stopOpacity: 0.03 }))),
        ticks.map(function(t, i) {
          var y = py(t);
          return h(Fragment, { key: i },
            h('line', { x1: padL, x2: W - padR, y1: y, y2: y, stroke: C.border, strokeWidth: 0.6, opacity: 0.5 }),
            h('text', { x: padL - 8, y: y + 4, textAnchor: 'end', fontSize: 11, fill: C.textDim }, t.toLocaleString()));
        }),
        data.length > 0 && h('polygon', { points: area, fill: 'url(#mrGrad-' + color.replace('#', '') + ')' }),
        data.length > 0 && h('polyline', { points: line, fill: 'none', stroke: color, strokeWidth: 2.4, strokeLinejoin: 'round', strokeLinecap: 'round' }),
        hover != null && data[hover] && h('line', { x1: px(hover), x2: px(hover), y1: padT, y2: padT + ih, stroke: color, strokeWidth: 1, strokeDasharray: '3,3', opacity: 0.7 }),
        data.map(function(d, i) {
          var active = hover === i;
          return h(Fragment, { key: 'p' + i },
            h('circle', { cx: px(i), cy: py(d.value), r: active ? 5.5 : 3.2, fill: active ? C.bg : color,
              stroke: color, strokeWidth: active ? 2.5 : 0, style: { transition: 'r 0.1s' } }),
            h('rect', { x: px(i) - colW / 2, y: padT, width: colW, height: ih, fill: 'transparent',
              onMouseEnter: function() { setHover(i); }, style: { cursor: 'crosshair' } }),
            h('text', { x: px(i), y: H - 8, textAnchor: 'middle', fontSize: 11,
              fill: active ? color : C.textDim, fontWeight: active ? 700 : 400 }, d.label));
        })
      )))
  );
}

function MrBarChart({ data, color, label }) {
  var [hover, setHover] = useState(null);
  var W = 600, H = 230, padL = 46, padR = 16, padT = 26, padB = 30;
  var max = Math.max.apply(null, [1].concat(data.map(function(d) { return d.value; })));
  var iw = W - padL - padR, ih = H - padT - padB;
  var bw = Math.min(70, iw / Math.max(1, data.length) * 0.55);
  var ticks = [0, 0.5, 1].map(function(f) { return Math.round(max * f); });
  var cellW = iw / Math.max(1, data.length);
  return h('div', { style: { height: '100%', display: 'flex', flexDirection: 'column' } },
    h('div', { style: { display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center', marginBottom: 4 } },
      h('span', { style: { width: 10, height: 10, borderRadius: 2, background: color, display: 'inline-block' } }),
      h('span', { style: { fontSize: 12, color: C.textMuted } }, label)),
    h('div', { style: { flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' } },
    h('div', { style: { position: 'relative', width: '100%', maxHeight: '100%', aspectRatio: W + ' / ' + H } },
      hover != null && data[hover] && h(MrChartTooltip, {
        xPct: (padL + (hover + 0.5) * cellW) / W * 100,
        yPct: (padT + ih - (max ? (data[hover].value / max) * ih : 0)) / H * 100,
        label: data[hover].label, value: data[hover].value, color: color }),
      h('svg', { viewBox: '0 0 ' + W + ' ' + H, style: { width: '100%', height: '100%', display: 'block' },
        onMouseLeave: function() { setHover(null); } },
        ticks.map(function(t, i) {
          var y = padT + ih - (max ? (t / max) * ih : 0);
          return h(Fragment, { key: i },
            h('line', { x1: padL, x2: W - padR, y1: y, y2: y, stroke: C.border, strokeWidth: 0.6, opacity: 0.5 }),
            h('text', { x: padL - 8, y: y + 4, textAnchor: 'end', fontSize: 11, fill: C.textDim }, t.toLocaleString()));
        }),
        data.map(function(d, i) {
          var cx = padL + (i + 0.5) * cellW;
          var bh = max ? (d.value / max) * ih : 0;
          var active = hover === i;
          return h(Fragment, { key: i },
            h('rect', { x: cx - bw / 2, y: padT + ih - bh, width: bw, height: Math.max(bh, d.value > 0 ? 2 : 0), rx: 3,
              fill: color, opacity: active ? 1 : 0.75, stroke: active ? color : 'none', strokeWidth: active ? 2 : 0,
              style: { transition: 'opacity 0.1s' } }),
            h('rect', { x: cx - cellW / 2, y: padT, width: cellW, height: ih, fill: 'transparent',
              onMouseEnter: function() { setHover(i); }, style: { cursor: 'crosshair' } }),
            h('text', { x: cx, y: H - 8, textAnchor: 'middle', fontSize: 11,
              fill: active ? color : C.textDim, fontWeight: active ? 700 : 400 }, d.label));
        })
      )))
  );
}

var mrScanStatusMeta = {
  processing: { label: 'Processing', bg: 'rgba(245,158,11,0.12)', text: '#F59E0B' },
  done:       { label: 'Done',       bg: 'rgba(16,185,129,0.12)', text: '#10B981' },
  error:      { label: 'Error',      bg: 'rgba(239,68,68,0.12)',  text: '#EF4444' },
  cancelled:  { label: 'Cancelled',  bg: 'rgba(148,163,184,0.14)', text: '#94A3B8' },
};

function MeliResearchPage({ showToast, currentUser, scanArg, onNavigate }) {
  var [scans, setScans] = useState([]);
  var [monthly, setMonthly] = useState([]);
  var [loading, setLoading] = useState(true);
  var [openScan, setOpenScan] = useState(null);     // { scan, rows } | null → dashboard
  var [menuOpen, setMenuOpen] = useState(false);
  var [sheetModal, setSheetModal] = useState(false);
  var [quickModal, setQuickModal] = useState(false);
  var [busy, setBusy] = useState(false);
  var [search, setSearch] = useState('');
  var [statusFilter, setStatusFilter] = useState('');
  var [renaming, setRenaming] = useState(false);
  var [renameVal, setRenameVal] = useState('');
  var [renameW, setRenameW] = useState(0); // measured title width — the edit box keeps the title's size
  // Row selection (results table): checkboxes + shift-click range over the visible sort order
  var [selected, setSelected] = useState({});
  var lastCheckedRef = useRef(null);
  var sortedRowsRef = useRef(null);
  var scanReqRef = useRef(0); // review M7 #105: scan A's response must not render under scan B's URL
  // Host element the DataTable views dropdown portals into (rendered above the status pills)
  var [viewsHost, setViewsHost] = useState(null);
  var [assumpModal, setAssumpModal] = useState(false);
  var [assumpDraft, setAssumpDraft] = useState(null);
  // Assumptions. FX is automatic (live rate from /config) — the user only picks the
  // base currency; everything else is editable, seeded from /config + our actuals.
  var [fxMxnUsd, setFxMxnUsd] = useState(0.055);
  var [baseCur, setBaseCur] = useState('USD');
  var [commPct, setCommPct] = useState('17');
  var [shipUsd, setShipUsd] = useState('6.00');
  var [prepUsd, setPrepUsd] = useState('0.20');
  var [inboundPct, setInboundPct] = useState('5');
  var canWrite = hasPermission(currentUser, 'meli_research', 'write');

  // Spreadsheet modal state
  var [csvName, setCsvName] = useState('');
  var [fileLabel, setFileLabel] = useState('');
  var [dragOver, setDragOver] = useState(false);
  var fileInputRef = useRef(null);
  var [csvHeaders, setCsvHeaders] = useState([]);
  var [csvRows, setCsvRows] = useState([]);
  var [colBarcode, setColBarcode] = useState('');
  var [colCost, setColCost] = useState('');
  var [refCols, setRefCols] = useState({});
  var [quickInput, setQuickInput] = useState('');
  var [notifyOn, setNotifyOn] = useState(false);
  var [notifyEmail, setNotifyEmail] = useState((currentUser && (currentUser.email || currentUser.username)) || '');

  var loadDash = useCallback(function() {
    setLoading(true);
    Promise.all([api('/meli-research/scans?limit=30'), api('/meli-research/stats/monthly')])
      .then(function(res) { setScans(res[0] || []); setMonthly(res[1] || []); })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setLoading(false); });
  }, []);
  // (dashboard data loads via the scanArg effect below — it runs on mount too)
  useEffect(function() {
    api('/meli-research/config').then(function(c) {
      if (c.mxn_usd) setFxMxnUsd(Number(c.mxn_usd));
      if (c.default_commission_pct != null) setCommPct(String(c.default_commission_pct));
      if (c.default_ship_usd != null) setShipUsd(Number(c.default_ship_usd).toFixed(2));
      if (c.default_prep_usd != null) setPrepUsd(Number(c.default_prep_usd).toFixed(2));
      if (c.default_inbound_pct != null) setInboundPct(String(c.default_inbound_pct));
      if (c.free_ship_threshold_mxn) FREE_SHIP_MXN = c.free_ship_threshold_mxn;
    }).catch(function() {});
  }, []);

  var commitRename = function() {
    var name = renameVal.trim();
    setRenaming(false);
    if (!openScan || !name || name === openScan.scan.name) return;
    api('/meli-research/scans/' + openScan.scan.id, { method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: name }) })
      .then(function() {
        setOpenScan(Object.assign({}, openScan, { scan: Object.assign({}, openScan.scan, { name: name }) }));
        setScans(scans.map(function(s) { return s.id === openScan.scan.id ? Object.assign({}, s, { name: name }) : s; }));
      })
      .catch(function(e) { showToast(e.message, 'error'); });
  };

  // Poll an open processing scan (review 2026-09-27 ML18). The tick reads the light
  // /progress header; the full row set (up to 10k rows) is re-fetched only when progress moved
  // and at most every MR_ROWS_REFRESH_MS, plus once when the run ends. Unchanged ticks back off
  // (meliPollDelay), a hidden tab skips the request, and leaving the scan/page stops the loop.
  useEffect(function() {
    if (!openScan || openScan.scan.status !== 'processing') return;
    var scanId = openScan.scan.id;
    var stopped = false, timer = null, idle = 0;
    var lastProcessed = openScan.scan.processed, lastRowsAt = Date.now();
    var schedule = function() { if (!stopped) timer = setTimeout(tick, meliPollDelay(idle)); };
    var tick = function() {
      if (stopped) return;
      if (typeof document !== 'undefined' && document.hidden) { schedule(); return; }
      api('/meli-research/scans/' + scanId + '/progress').then(function(p) {
        if (stopped || !p) return;
        var finished = p.status !== 'processing';
        var moved = p.processed !== lastProcessed;
        if (moved) { idle = 0; lastProcessed = p.processed; } else idle++;
        if (finished || (moved && Date.now() - lastRowsAt >= MR_ROWS_REFRESH_MS)) {
          lastRowsAt = Date.now();
          return api('/meli-research/scans/' + scanId).then(function(d) {
            if (stopped || !d || !d.scan || d.scan.id !== scanId) return;
            setOpenScan(d);
            if (finished) loadDash();
          });
        }
        setOpenScan(function(cur) {
          if (!cur || !cur.scan || cur.scan.id !== scanId) return cur;
          return Object.assign({}, cur, { scan: Object.assign({}, cur.scan, p) });
        });
      }).catch(function() { idle++; }).then(schedule);
    };
    schedule();
    return function() { stopped = true; if (timer) clearTimeout(timer); };
  }, [openScan && openScan.scan.id, openScan && openScan.scan.status]);

  // Keep the dashboard list fresh while any scan is processing (drives the Status % column);
  // same back-off / hidden-tab rules as the open-scan poll.
  var dashProcessing = !openScan && scans.some(function(s) { return s.status === 'processing'; });
  useEffect(function() {
    if (!dashProcessing) return;
    var stopped = false, timer = null, idle = 0, lastSig = null;
    var schedule = function() { if (!stopped) timer = setTimeout(tick, meliPollDelay(idle)); };
    var tick = function() {
      if (stopped) return;
      if (typeof document !== 'undefined' && document.hidden) { schedule(); return; }
      api('/meli-research/scans?limit=30').then(function(r) {
        if (stopped) return;
        var sig = (r || []).map(function(s) { return s.id + ':' + s.status + ':' + s.processed; }).join('|');
        if (sig === lastSig) idle++; else { idle = 0; lastSig = sig; }
        setScans(r || []);
      }).catch(function() { idle++; }).then(schedule);
    };
    schedule();
    return function() { stopped = true; if (timer) clearTimeout(timer); };
  }, [dashProcessing]);

  // The URL owns which scan is open (/meli-research/:id) — refresh/deep-link safe.
  var gotoScan = function(id) {
    if (onNavigate) onNavigate('meli-research', id);
    else viewScan(id);
  };
  var gotoDash = function() {
    setSearch(''); setStatusFilter('');
    if (onNavigate) onNavigate('meli-research');
    else { setOpenScan(null); loadDash(); }
  };
  useEffect(function() {
    if (scanArg) viewScan(scanArg);
    else { setOpenScan(null); loadDash(); }
  }, [scanArg]);

  var viewScan = function(id) {
    var seq = ++scanReqRef.current;
    api('/meli-research/scans/' + id).then(function(d) {
      if (seq !== scanReqRef.current) return; // a newer scan was opened meanwhile
      var s = (d.scan && d.scan.settings) || {};
      if (s.base_currency) setBaseCur(s.base_currency);
      if (s.commission_pct != null) setCommPct(String(s.commission_pct));
      if (s.ship_usd != null) setShipUsd(String(s.ship_usd));
      if (s.prep_usd != null) setPrepUsd(String(s.prep_usd));
      if (s.inbound_pct != null) setInboundPct(String(s.inbound_pct));
      setSelected({});
      lastCheckedRef.current = null;
      setOpenScan(d);
    }).catch(function(e) { if (seq === scanReqRef.current) showToast(e.message, 'error'); });
  };

  var toggleRow = function(id, shiftKey) {
    if (shiftKey) window.getSelection().removeAllRanges();
    var next = Object.assign({}, selected);
    if (shiftKey && lastCheckedRef.current !== null) {
      var vis = sortedRowsRef.current || [];
      var lastIdx = vis.findIndex(function(o) { return o.id === lastCheckedRef.current; });
      var curIdx = vis.findIndex(function(o) { return o.id === id; });
      if (lastIdx !== -1 && curIdx !== -1) {
        var start = Math.min(lastIdx, curIdx), end = Math.max(lastIdx, curIdx);
        var newVal = !selected[id];
        for (var i = start; i <= end; i++) {
          if (newVal) next[vis[i].id] = true; else delete next[vis[i].id];
        }
      }
    } else {
      if (next[id]) delete next[id]; else next[id] = true;
    }
    lastCheckedRef.current = id;
    setSelected(next);
  };

  var fxRate = function() { return baseCur === 'MXN' ? 1 : (fxMxnUsd || 0); };
  var settings = function() {
    return { base_currency: baseCur, fx_mxn_rate: fxRate(), commission_pct: parseFloat(commPct) || 0,
      ship_usd: parseFloat(shipUsd) || 0, prep_usd: parseFloat(prepUsd) || 0, inbound_pct: parseFloat(inboundPct) || 0 };
  };

  // Per-row economics in the base currency: ML price (MXN) auto-converted, minus
  // commission %, seller shipping (only at ≥ threshold — buyer pays below), prep,
  // then cost + inbound freight (% of cost) for profit / margin / ROI. The CSV
  // row's cost wins over the matched catalog cost.
  var econ = function(r) {
    var priceMxn = r.buy_box_price != null ? Number(r.buy_box_price) : (r.best_price != null ? Number(r.best_price) : null);
    if (priceMxn == null || !(priceMxn > 0)) return null;
    var st = settings();
    var rate = fxRate();
    var sellerPaysShip = priceMxn >= FREE_SHIP_MXN;
    var price = priceMxn * rate;
    var commission = price * st.commission_pct / 100;
    var ship = sellerPaysShip ? st.ship_usd : 0;
    var net = price - commission - ship - st.prep_usd;
    var cost = r.cost != null ? Number(r.cost) : (r.matched_cost != null ? Number(r.matched_cost) : null);
    var inbound = cost != null ? cost * st.inbound_pct / 100 : null;
    var landed = cost != null ? cost + inbound : null;
    return { priceMxn: priceMxn, price: price, commission: commission, ship: ship, prep: st.prep_usd,
      sellerPaysShip: sellerPaysShip, net: net, cost: cost, inbound: inbound, landed: landed,
      profit: landed != null ? net - landed : null,
      margin: landed != null && price > 0 ? (net - landed) / price * 100 : null,
      roi: landed != null && landed > 0 ? (net - landed) / landed * 100 : null };
  };
  var csym = function() { return baseCur === 'MXN' ? 'MX$' : '$'; };

  // ── CSV intake ──
  var handleFile = function(file) {
    setFileLabel(file.name);
    var reader = new FileReader();
    reader.onload = function() {
      var parsed = mrParseCsv(String(reader.result || ''));
      if (parsed.length < 2) { showToast('CSV needs a header row and at least one data row', 'error'); return; }
      var headers = parsed[0].map(function(x, i) { return String(x || '').trim() || ('Column ' + (i + 1)); });
      var body = parsed.slice(1);
      var bIdx = headers.findIndex(function(hd) { return /upc|ean|barcode|gtin|c(o|ó)digo/i.test(hd); });
      if (bIdx === -1) {
        bIdx = headers.findIndex(function(_, ci) {
          var hits = 0, seen = 0;
          for (var ri = 0; ri < Math.min(body.length, 25); ri++) {
            var v = String(body[ri][ci] || '').replace(/[^0-9]/g, '');
            if (String(body[ri][ci] || '').trim()) { seen++; if (v.length >= 8 && v.length <= 14) hits++; }
          }
          return seen > 0 && hits / seen > 0.5;
        });
      }
      var cIdx = headers.findIndex(function(hd) { return /cost|costo|wholesale/i.test(hd); });
      if (cIdx === -1) cIdx = headers.findIndex(function(hd) { return /price|precio/i.test(hd); });
      setCsvName(file.name.replace(/\.[^.]+$/, ''));
      setCsvHeaders(headers);
      setCsvRows(body);
      setColBarcode(bIdx >= 0 ? String(bIdx) : '');
      setColCost(cIdx >= 0 ? String(cIdx) : '');
      setRefCols({});
    };
    reader.readAsText(file);
  };

  var downloadTemplate = function() {
    var lines = ['UPC,Cost,Description,Vendor',
      '6291108738528,3.50,Example product name,Example Vendor',
      '844828015972,12.00,Another example,Example Vendor'];
    var blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    var a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'MELI Research Template.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  var submitSheet = function() {
    var bi = parseInt(colBarcode);
    if (!(bi >= 0)) { showToast('Pick the barcode column', 'error'); return; }
    var ci = colCost === '' ? -1 : parseInt(colCost);
    var keep = csvHeaders.map(function(hd, i) { return refCols[i] ? { i: i, name: hd } : null; }).filter(Boolean);
    var rows = csvRows.map(function(r) {
      var ref = null;
      if (keep.length) { ref = {}; keep.forEach(function(k) { ref[k.name] = String(r[k.i] != null ? r[k.i] : ''); }); }
      return { barcode: String(r[bi] || ''), cost: ci >= 0 ? String(r[ci] || '').replace(/[^0-9.]/g, '') : null, ref: ref };
    }).filter(function(r) {
      var s = String(r.barcode).replace(/[^0-9]/g, '');
      return s.length >= 8 && s.length <= 14 && !/^(\d)\1+$/.test(s);
    });
    if (!rows.length) { showToast('No rows with a valid barcode in that column', 'error'); return; }
    setBusy(true);
    api('/meli-research/scans', { method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: csvName, kind: 'spreadsheet', settings: settings(),
        ref_columns: keep.map(function(k) { return k.name; }), rows: rows,
        notify_email: notifyOn && notifyEmail.trim() ? notifyEmail.trim() : null }) })
      .then(function(r) {
        setSheetModal(false); setCsvHeaders([]); setCsvRows([]); setCsvName(''); setFileLabel('');
        // Stay on the dashboard — Recent List's Status column tracks the run live
        showToast('Scan started — ' + r.total + ' codes. Watch its progress in Recent List.');
        loadDash();
      })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setBusy(false); });
  };

  var submitQuick = function() {
    var codes = Array.from(new Set(quickInput.split(/[\s,;]+/).map(function(s) { return s.replace(/[^0-9]/g, ''); })
      .filter(function(s) { return s.length >= 8 && s.length <= 14 && !/^(\d)\1+$/.test(s); })));
    if (!codes.length) { showToast('Paste at least one UPC/EAN (8–14 digits)', 'error'); return; }
    setBusy(true);
    api('/meli-research/scans', { method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kind: 'quick', settings: settings(), rows: codes.map(function(c) { return { barcode: c }; }) }) })
      .then(function(r) {
        setQuickModal(false); setQuickInput('');
        showToast('Lookup started — ' + r.total + ' code' + (r.total === 1 ? '' : 's') + '. Watch its progress in Recent List.');
        loadDash();
      })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setBusy(false); });
  };

  // Inline assumptions editor — used by the New Scan modals (their values snapshot into the
  // scan's settings at creation; after that each scan owns its own via the Assumptions modal)
  var assumptionsStrip = function() {
    var inp = function(val, set, w) {
      return h('input', { value: val, onChange: function(e) { set(e.target.value); },
        style: { width: w, background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '5px 8px', borderRadius: 6, fontSize: 12, fontFamily: 'inherit', outline: 'none' } });
    };
    return h('div', { style: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', fontSize: 12, color: C.textMuted } },
      h('span', { style: { fontWeight: 700, textTransform: 'uppercase', fontSize: 10, letterSpacing: '0.06em' } }, 'Assumptions'),
      h('span', null, 'Currency'),
      h('select', { value: baseCur, onChange: function(e) { setBaseCur(e.target.value); },
        style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '5px 8px', borderRadius: 6, fontSize: 12, outline: 'none' } },
        h('option', { value: 'USD' }, 'USD'), h('option', { value: 'MXN' }, 'MXN')),
      baseCur === 'USD' && h('span', { title: 'Live rate — applied automatically', style: { color: C.textDim, cursor: 'help' } }, '1 MXN = $' + (fxMxnUsd || 0).toFixed(4)),
      h('span', null, 'Commission %'), inp(commPct, setCommPct, 52),
      h('span', null, 'Ship (' + baseCur + ')'), inp(shipUsd, setShipUsd, 62),
      h('span', null, 'Prep (' + baseCur + ')'), inp(prepUsd, setPrepUsd, 62),
      h('span', { title: 'Inbound freight as % of unit cost — vendor → our warehouse → fulfillment center. Added to the cost side of profit/ROI.', style: { cursor: 'help' } }, 'Inbound % of cost'), inp(inboundPct, setInboundPct, 46),
      h('span', { title: 'ML MX mandates free shipping at/above $' + FREE_SHIP_MXN + ' MXN item price — the seller is charged; below it the buyer pays and shipping drops out.', style: { color: C.textDim, cursor: 'help' } }, 'ship ≥ $' + FREE_SHIP_MXN + ' MXN')
    );
  };

  // Assumptions modal: edits a draft, Save applies + persists to THIS scan only
  var openAssumptions = function() {
    setAssumpDraft({ baseCur: baseCur, commPct: commPct, shipUsd: shipUsd, prepUsd: prepUsd, inboundPct: inboundPct });
    setAssumpModal(true);
  };
  var saveAssumptions = function() {
    var d = assumpDraft;
    if (!d) return;
    setBaseCur(d.baseCur); setCommPct(d.commPct); setShipUsd(d.shipUsd); setPrepUsd(d.prepUsd); setInboundPct(d.inboundPct);
    setAssumpModal(false);
    if (openScan && canWrite) {
      var s = { base_currency: d.baseCur, fx_mxn_rate: d.baseCur === 'MXN' ? 1 : (fxMxnUsd || 0),
        commission_pct: parseFloat(d.commPct) || 0, ship_usd: parseFloat(d.shipUsd) || 0,
        prep_usd: parseFloat(d.prepUsd) || 0, inbound_pct: parseFloat(d.inboundPct) || 0 };
      api('/meli-research/scans/' + openScan.scan.id, { method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ settings: s }) })
        .then(function() {
          setOpenScan(Object.assign({}, openScan, { scan: Object.assign({}, openScan.scan, { settings: s }) }));
        })
        .catch(function(e) { showToast(e.message, 'error'); });
    }
  };

  // ── Scan detail (spreadsheet view) ──
  if (openScan) {
    var sc = openScan.scan;
    var rows = openScan.rows || [];
    var refColumns = (sc.ref_columns || []).map(function(name) {
      return { key: 'ref:' + name, label: name, sortValue: function(r) { return (r.ref_data && r.ref_data[name]) || ''; },
        render: function(r) { return h('span', { style: { color: C.textMuted, fontSize: 12 } }, (r.ref_data && r.ref_data[name]) || '—'); } };
    });
    // Catalog membership is BARCODE-ONLY (matched_sku). matched_ml_sku just marks that the
    // matched product already has an ML listing. Found pill shows NEW finds only.
    var isOurs = function(r) { return !!r.matched_sku; };
    var q2 = search.trim().toLowerCase();
    var visRows = rows.filter(function(r) {
      if (r.hidden) return false;
      if (statusFilter === 'found') { if (r.status !== 'found' || isOurs(r)) return false; }
      else if (statusFilter === 'in_catalog_no_ml') { if (!(r.status === 'found' && r.matched_sku && !r.matched_ml_sku)) return false; }
      else if (statusFilter === 'in_catalog_ml') { if (!(r.status === 'found' && r.matched_ml_sku)) return false; }
      else if (statusFilter && r.status !== statusFilter) return false;
      if (q2) {
        var hay = ((r.barcode || '') + ' ' + (r.name || '') + ' ' + (r.matched_sku || '') + ' ' + (r.matched_ml_sku || '')).toLowerCase()
          + ' ' + Object.values(r.ref_data || {}).join(' ').toLowerCase();
        if (hay.indexOf(q2) === -1) return false;
      }
      return true;
    });
    var counts = { in_catalog_no_ml: 0, in_catalog_ml: 0 };
    rows.forEach(function(r) {
      if (r.status === 'found' && r.matched_ml_sku) { counts.in_catalog_ml++; return; }
      if (r.status === 'found' && r.matched_sku) { counts.in_catalog_no_ml++; return; }
      counts[r.status] = (counts[r.status] || 0) + 1;
    });
    var pills2 = [
      { id: '', label: 'All', count: rows.length },
      { id: 'found', label: 'Found', count: counts.found || 0 },
      { id: 'in_catalog_ml', label: 'Already in Our Catalog - ML Listed', count: counts.in_catalog_ml || 0,
        hint: 'In our catalog (barcode match) and that product already has an ML listing' },
      { id: 'in_catalog_no_ml', label: 'Already in Our Catalog - Not ML Listed', count: counts.in_catalog_no_ml || 0,
        hint: 'In our catalog (barcode match) but no ML listing yet — candidates to list' },
      { id: 'not_found', label: 'Not Found on ML', count: counts.not_found || 0 },
      { id: 'pending', label: 'Pending', count: counts.pending || 0 },
      { id: 'error', label: 'Errors', count: counts.error || 0 },
    ];
    // ── Selection + bulk actions ──
    var selIds = Object.keys(selected).filter(function(k) { return selected[k]; }).map(Number);
    var hiddenCount = rows.reduce(function(n, r) { return n + (r.hidden ? 1 : 0); }, 0);
    var patchRows = function(ids, patch) {
      var idSet = {};
      ids.forEach(function(i) { idSet[i] = true; });
      return rows.map(function(r) { return idSet[r.id] ? Object.assign({}, r, patch) : r; });
    };
    var hideSelected = function() {
      api('/meli-research/scans/' + sc.id + '/hide', { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ row_ids: selIds, hidden: true }) })
        .then(function() {
          setOpenScan({ scan: sc, rows: patchRows(selIds, { hidden: true }) });
          setSelected({});
          showToast(selIds.length + ' row' + (selIds.length === 1 ? '' : 's') + ' hidden');
        })
        .catch(function(e) { showToast(e.message, 'error'); });
    };
    var unhideAll = function() {
      api('/meli-research/scans/' + sc.id + '/hide', { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ unhide_all: true }) })
        .then(function() {
          setOpenScan({ scan: sc, rows: rows.map(function(r) { return r.hidden ? Object.assign({}, r, { hidden: false }) : r; }) });
          showToast(hiddenCount + ' row' + (hiddenCount === 1 ? '' : 's') + ' unhidden');
        })
        .catch(function(e) { showToast(e.message, 'error'); });
    };
    var refreshRows = function(body, ids, label) {
      api('/meli-research/scans/' + sc.id + '/refresh', { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body) })
        .then(function() {
          // Poll effect takes over once the scan shows processing again
          setOpenScan({ scan: Object.assign({}, sc, { status: 'processing' }), rows: patchRows(ids, { status: 'pending' }) });
          setSelected({});
          showToast('Refreshing ' + label + ' from Mercado Libre…');
        })
        .catch(function(e) { showToast(e.message, 'error'); });
    };
    var refreshSelected = function() {
      refreshRows({ row_ids: selIds }, selIds, selIds.length + ' row' + (selIds.length === 1 ? '' : 's'));
    };
    var refreshAll = function() {
      var ids = rows.map(function(r) { return r.id; });
      if (!ids.length) return;
      if (ids.length > 25 && !window.confirm('Re-check all ' + ids.length + ' codes live on Mercado Libre? This ignores the 7-day cache and can take a while.')) return;
      refreshRows({ all: true }, ids, 'all ' + ids.length + ' row' + (ids.length === 1 ? '' : 's'));
    };
    var cancelScan = function() {
      if (!window.confirm('Cancel this run? Codes already processed keep their results; the rest stay pending (Refresh restarts them).')) return;
      api('/meli-research/scans/' + sc.id + '/cancel', { method: 'POST' })
        .then(function() {
          setOpenScan({ scan: Object.assign({}, sc, { status: 'cancelled' }), rows: rows });
          showToast('Scan cancelled');
        })
        .catch(function(e) { showToast(e.message, 'error'); });
    };
    var exportScanCsv = function(exportRows, suffix) {
      var list = exportRows || visRows;
      var cur = baseCur;
      var head = ['Barcode', 'Status', 'Our Matching SKU', 'Cost (' + cur + ')', 'Inbound (' + cur + ')', 'Product',
        'Price (' + cur + ')', 'Buy Box (MXN)', 'Best Offer (MXN)', 'Offers', 'Sold',
        'Referral Fee (' + cur + ')', 'Est Net (' + cur + ')', 'Est Profit (' + cur + ')', 'Margin %', 'ROI %',
        'ML Product Code', 'ML Brand', 'ML Category', 'ML Star Rating', 'ML Review Count', 'ML Model', 'ML Condition', 'ML Listing Type',
        'ML Full Offers', 'ML Free Ship Offers', 'ML Original Price (MXN)', 'ML Status', 'ML Created', 'ML Image', 'Link'].concat(sc.ref_columns || []);
      // review M7 #108: every cell (and the user-named ref_columns header) goes through escapeCSV,
      // which doubles embedded quotes — only the name column used to, so a 12" vase shifted columns.
      var lines = [head.map(escapeCSV).join(',')].concat(list.map(function(r) {
        var e = econ(r);
        var m = r.ml_data || {};
        var url = r.permalink || (r.product_id ? 'https://www.mercadolibre.com.mx/p/' + r.product_id : '');
        var cells = [r.barcode, r.status, r.matched_sku || '',
          e && e.cost != null ? e.cost.toFixed(2) : '',
          e && e.inbound != null ? e.inbound.toFixed(2) : '',
          r.name || '',
          e ? e.price.toFixed(2) : '',
          r.buy_box_price != null ? r.buy_box_price : '',
          r.best_price != null ? r.best_price : '',
          r.offers_count != null ? r.offers_count : '', r.sold_band || '',
          e ? e.commission.toFixed(2) : '',
          e ? e.net.toFixed(2) : '', e && e.profit != null ? e.profit.toFixed(2) : '',
          e && e.margin != null ? e.margin.toFixed(1) : '', e && e.roi != null ? e.roi.toFixed(1) : '',
          r.product_id || '', m.brand || '', m.category || '',
          m.rating_avg != null ? m.rating_avg : '', m.reviews_count != null ? m.reviews_count : '',
          m.model || '', m.bb_condition || '', m.bb_listing_type || '',
          m.full_offers != null ? m.full_offers : '', m.freeship_offers != null ? m.freeship_offers : '',
          m.bb_original_price != null ? m.bb_original_price : '', m.status || '',
          m.created ? String(m.created).slice(0, 10) : '', m.thumb || '', url]
          .concat((sc.ref_columns || []).map(function(name) { return String((r.ref_data && r.ref_data[name]) || ''); }));
        return cells.map(escapeCSV).join(',');
      }));
      var blob = new Blob([lines.join('\n')], { type: 'text/csv' });
      var a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = sc.name.replace(/[\\/:*?"<>|]/g, '') + ' - MELI Research' + (suffix || '') + '.csv';
      a.click();
      URL.revokeObjectURL(a.href);
    };
    var bulkItems = [];
    if (canWrite) bulkItems.push({ label: 'Hide Selected' + (selIds.length ? ' (' + selIds.length + ')' : ''), icon: 'eye-off', disabled: !selIds.length, onClick: hideSelected });
    bulkItems.push({ label: 'Export Selected to Excel', icon: 'download', disabled: !selIds.length,
      onClick: function() { exportScanCsv(visRows.filter(function(r) { return selected[r.id]; }), ' - Selected'); } });
    if (canWrite) bulkItems.push({ label: 'Refresh Selected from ML', icon: 'refresh-cw', disabled: !selIds.length || sc.status === 'processing',
      title: sc.status === 'processing' ? 'Wait for the current pass to finish' : 'Re-check these codes live on Mercado Libre (ignores the 7-day cache)',
      onClick: refreshSelected });
    if (canWrite && hiddenCount > 0) {
      bulkItems.push({ divider: true });
      bulkItems.push({ label: 'Unhide All (' + hiddenCount + ')', icon: 'eye', onClick: unhideAll });
    }
    var dimCell = function(v) { return h('span', { style: { color: C.textDim } }, v || '—'); };
    var md = function(r) { return r.ml_data || {}; };
    // App-standard checkbox (matches the products/forecasting list selection boxes)
    var checkbox = function(checked, indeterminate) {
      return h('div', { style: { width: 20, height: 20, borderRadius: 4, border: '2px solid ' + (checked || indeterminate ? C.accent : C.textDim),
        background: checked ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' } },
        checked && h(LIcon, { name: 'check', size: 13, color: '#fff' }),
        indeterminate && !checked && h('span', { style: { color: C.accent, fontSize: 13, fontWeight: 700, lineHeight: 1 } }, '–'));
    };
    var columns = [
      { key: '_sel', label: '', locked: true, noFilter: true, width: 34,
        headerRender: function() {
          // Operates on what the table actually shows (column filters + sort applied)
          var pool = sortedRowsRef.current || visRows;
          var all = pool.length > 0 && pool.every(function(r) { return selected[r.id]; });
          var some = !all && pool.some(function(r) { return selected[r.id]; });
          return h('div', {
            onClick: function(e) {
              e.stopPropagation();
              var p = sortedRowsRef.current || visRows;
              if (p.length > 0 && p.every(function(r) { return selected[r.id]; })) { setSelected({}); }
              else { var next = {}; p.forEach(function(r) { next[r.id] = true; }); setSelected(next); }
            },
            style: { width: 32, height: 28, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', userSelect: 'none' }
          }, checkbox(all, some));
        },
        render: function(r) {
          return h('div', {
            onMouseDown: function(e) { if (e.shiftKey) e.preventDefault(); },
            onClick: function(e) { e.stopPropagation(); toggleRow(r.id, e.shiftKey); },
            title: 'Select — shift-click selects the range',
            style: { width: 32, height: 28, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', userSelect: 'none' }
          }, checkbox(!!selected[r.id], false));
        } },
      { key: 'thumb', label: 'Img', noFilter: true,
        render: function(r) {
          var t = md(r).thumb;
          return t
            ? h('img', { src: t, loading: 'lazy', alt: '',
                style: { width: 34, height: 34, objectFit: 'contain', borderRadius: 6, background: '#fff', display: 'block' } })
            : dimCell();
        } },
      { key: 'barcode', label: 'Barcode', locked: true, sortValue: function(r) { return r.barcode; },
        render: function(r) { return h(CopyCell, { value: r.barcode }, h('span', { style: { fontFamily: 'inherit', fontSize: 12, color: C.text, fontWeight: 600 } }, r.barcode)); } },
      { key: 'product_id', label: 'ML Product Code', sortValue: function(r) { return r.product_id || ''; },
        render: function(r) {
          return r.product_id
            ? h(CopyCell, { value: r.product_id }, h('span', { style: { fontFamily: 'inherit', fontSize: 11, color: C.textMuted } }, r.product_id))
            : dimCell();
        } },
      { key: 'matched_sku', label: 'Our Matching SKU', sortValue: function(r) { return (r.matched_sku || '').toLowerCase(); },
        filterValue: function(r) { return r.matched_sku || null; },
        render: function(r) {
          if (!r.matched_sku) return h('span', { title: 'Not in our catalog — new candidate', style: { color: C.textDim, cursor: 'help' } }, 'new');
          var mlNote = r.matched_ml_sku ? ' — already listed on ML as ' + r.matched_ml_sku : ' — no ML listing yet';
          return h('span', { title: 'Already in our catalog as ' + r.matched_sku + ' (barcode match)' + mlNote, style: { fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 8, background: 'rgba(16,185,129,0.12)', color: '#10B981', fontFamily: 'inherit', cursor: 'help' } }, r.matched_sku);
        } },
      { key: 'name', label: 'Product', sortValue: function(r) { return (r.name || '').toLowerCase(); },
        render: function(r) {
          if (r.status === 'pending') return dimCell('…');
          if (r.status === 'not_found') return h('span', { style: { color: C.textDim, fontStyle: 'italic' } }, 'No catalog match');
          if (r.status === 'error') return h('span', { title: r.name || '', style: { color: '#EF4444', fontStyle: 'italic', cursor: 'help' } }, 'Lookup error');
          return h('span', { title: r.name || '', style: { display: 'block', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: C.text } }, r.name || '—');
        } },
      { key: 'ml_brand', label: 'ML Brand', sortValue: function(r) { return (md(r).brand || '').toLowerCase(); },
        filterValue: function(r) { return md(r).brand || null; },
        render: function(r) { return md(r).brand ? h('span', { style: { color: C.textMuted } }, md(r).brand) : dimCell(); } },
      { key: 'ml_category', label: 'ML Category', sortValue: function(r) { return (md(r).category || '').toLowerCase(); },
        filterValue: function(r) { return md(r).category || null; },
        render: function(r) {
          var c = md(r).category;
          return c ? h('span', { style: { color: C.textMuted, textTransform: 'capitalize' } }, String(c).toLowerCase()) : dimCell();
        } },
      { key: 'ml_star_rating', label: 'ML Star Rating', sortValue: function(r) { var m = md(r); return m.rating_avg != null && m.reviews_count > 0 ? Number(m.rating_avg) : -1; },
        filterValue: function(r) { var m = md(r); return m.rating_avg != null && m.reviews_count > 0 ? Number(m.rating_avg) : null; },
        render: function(r) {
          var m = md(r);
          if (m.rating_avg == null || !(m.reviews_count > 0)) return dimCell();
          return h('span', { style: { color: '#F59E0B', fontWeight: 600 } }, Number(m.rating_avg).toFixed(1) + ' ★');
        } },
      { key: 'ml_review_count', label: 'ML Review Count', sortValue: function(r) { var m = md(r); return m.reviews_count != null ? m.reviews_count : -1; },
        filterValue: function(r) { var m = md(r); return m.reviews_count != null ? m.reviews_count : null; },
        render: function(r) {
          var m = md(r);
          if (m.reviews_count == null) return dimCell();
          var src = m.reviews_network ? 'From ML’s global network (another country site) — what the MX page shows'
            : (m.reviews_family ? 'Family-wide count across variations — what the ML page shows' : null);
          return h('span', {
            title: src || undefined,
            style: { color: m.reviews_count > 0 ? C.text : C.textDim, fontWeight: m.reviews_count > 0 ? 600 : 400, cursor: src ? 'help' : 'default' }
          }, m.reviews_count.toLocaleString() + (src ? '*' : ''));
        } },
      { key: 'price', label: 'Price', sortValue: function(r) { return Number(r.buy_box_price != null ? r.buy_box_price : r.best_price) || 0; },
        // Filter in the DISPLAYED currency, not raw MXN
        filterValue: function(r) {
          var mxn = r.buy_box_price != null ? Number(r.buy_box_price) : (r.best_price != null ? Number(r.best_price) : null);
          return mxn != null && mxn > 0 ? mxn * fxRate() : null;
        },
        render: function(r) {
          var mxn = r.buy_box_price != null ? Number(r.buy_box_price) : (r.best_price != null ? Number(r.best_price) : null);
          if (mxn == null) return dimCell();
          var parts = [r.buy_box_price != null ? 'Buy-box (featured) price' : 'Lowest competing offer — no buy box on this product',
            mxn.toFixed(2) + ' MXN'];
          if (r.buy_box_price != null && r.best_price != null && Number(r.best_price) !== mxn) {
            parts.push('lowest offer ' + csym() + (Number(r.best_price) * fxRate()).toFixed(2));
          }
          return h('span', { title: parts.join(' · '), style: { fontWeight: 600, color: C.text, cursor: 'help' } },
            csym() + (mxn * fxRate()).toFixed(2));
        } },
      { key: 'offers_count', label: 'Offers', sortValue: function(r) { return r.offers_count || 0; },
        render: function(r) { return r.offers_count != null ? r.offers_count : '—'; } },
      { key: 'sold_band', label: 'Sold', defaultVisible: false, sortValue: function(r) { return parseInt(String(r.sold_band || '').replace(/[^0-9]/g, '')) || 0; },
        render: function(r) {
          if (r.sold_band) return h('span', { title: 'ML bands sold counts — approximate by design', style: { color: '#F59E0B', fontWeight: 600, cursor: 'help' } }, r.sold_band);
          if (r.status === 'found') return h('span', { title: 'ML does not expose sold counts via API for other sellers’ listings — the count on the ML page is page-only.', style: { color: C.textDim, cursor: 'help', fontStyle: 'italic', fontSize: 11 } }, 'page only');
          return dimCell();
        } },
      { key: 'cost', label: 'Cost', sortValue: function(r) { var e = econ(r); return e && e.cost != null ? e.cost : -1; },
        filterValue: function(r) { var e = econ(r); return e && e.cost != null ? e.cost : null; },
        render: function(r) {
          var e = econ(r);
          if (!e || e.cost == null) return dimCell();
          return h('span', { title: r.cost != null ? 'From the uploaded sheet' : 'From our catalog (' + (r.matched_sku || '') + ')', style: { color: C.textMuted, cursor: 'help' } }, csym() + e.cost.toFixed(2));
        } },
      { key: 'inbound', label: 'Inbound $', sortValue: function(r) { var e = econ(r); return e && e.inbound != null ? e.inbound : -1; },
        filterValue: function(r) { var e = econ(r); return e && e.inbound != null ? e.inbound : null; },
        render: function(r) {
          var e = econ(r);
          if (!e || e.inbound == null) return dimCell();
          return h('span', { title: (parseFloat(inboundPct) || 0) + '% of cost — vendor → warehouse → fulfillment center', style: { color: C.textMuted, cursor: 'help' } }, csym() + e.inbound.toFixed(2));
        } },
      { key: 'referral', label: 'Referral Fee $', sortValue: function(r) { var e = econ(r); return e ? e.commission : -1; },
        filterValue: function(r) { var e = econ(r); return e ? e.commission : null; },
        render: function(r) {
          var e = econ(r);
          if (!e) return dimCell();
          return h('span', { title: (parseFloat(commPct) || 0) + '% of price', style: { color: C.textMuted, cursor: 'help' } }, csym() + e.commission.toFixed(2));
        } },
      { key: 'est_net', label: 'Est. Net', sortValue: function(r) { var e = econ(r); return e ? e.net : -Infinity; },
        filterValue: function(r) { var e = econ(r); return e ? e.net : null; },
        render: function(r) {
          var e = econ(r);
          if (!e) return dimCell();
          return h('span', {
            title: csym() + e.price.toFixed(2) + ' price (' + e.priceMxn.toFixed(0) + ' MXN)'
              + '\n− ' + csym() + e.commission.toFixed(2) + ' commission @ ' + (parseFloat(commPct) || 0) + '%'
              + '\n− ' + csym() + e.ship.toFixed(2) + ' shipping ' + (e.sellerPaysShip ? '(seller pays, ≥ ' + FREE_SHIP_MXN + ' MXN)' : '(buyer pays under ' + FREE_SHIP_MXN + ' MXN)')
              + '\n− ' + csym() + e.prep.toFixed(2) + ' prep',
            style: { fontWeight: 600, color: e.net > 0 ? C.text : '#EF4444', cursor: 'help' }
          }, csym() + e.net.toFixed(2));
        } },
      { key: 'est_profit', label: 'Est. Profit', sortValue: function(r) { var e = econ(r); return e && e.profit != null ? e.profit : -Infinity; },
        filterValue: function(r) { var e = econ(r); return e && e.profit != null ? e.profit : null; },
        render: function(r) {
          var e = econ(r);
          if (!e) return dimCell();
          if (e.profit == null) return h('span', { title: 'No cost — map a cost column or match a catalog product', style: { color: C.textDim, fontStyle: 'italic', cursor: 'help' } }, 'no cost');
          return h('span', {
            title: 'Net ' + csym() + e.net.toFixed(2) + ' − cost ' + csym() + e.cost.toFixed(2) + ' − inbound ' + csym() + e.inbound.toFixed(2) + ' = ' + csym() + e.profit.toFixed(2),
            style: { fontWeight: 700, color: e.profit >= 0 ? '#10B981' : '#EF4444', cursor: 'help' }
          }, csym() + e.profit.toFixed(2));
        } },
      { key: 'margin', label: 'Margin %', sortValue: function(r) { var e = econ(r); return e && e.margin != null ? e.margin : -Infinity; },
        filterValue: function(r) { var e = econ(r); return e && e.margin != null ? e.margin : null; },
        render: function(r) {
          var e = econ(r);
          if (!e || e.margin == null) return dimCell();
          return h('span', { style: { fontWeight: 600, color: e.margin >= 0 ? C.text : '#EF4444' } }, e.margin.toFixed(1) + '%');
        } },
      { key: 'roi', label: 'ROI %', sortValue: function(r) { var e = econ(r); return e && e.roi != null ? e.roi : -Infinity; },
        filterValue: function(r) { var e = econ(r); return e && e.roi != null ? e.roi : null; },
        render: function(r) {
          var e = econ(r);
          if (!e || e.roi == null) return dimCell();
          return h('span', { title: 'Profit ÷ (cost + inbound)', style: { fontWeight: 600, color: e.roi >= 0 ? '#10B981' : '#EF4444', cursor: 'help' } }, e.roi.toFixed(1) + '%');
        } },
      // Hidden by default — exists so saved views can capture a status filter via the column menu
      { key: 'status', label: 'Status', defaultVisible: false, filterOptions: ['found', 'not_found', 'pending', 'error'],
        sortValue: function(r) { return r.status; },
        render: function(r) {
          var colr = { found: '#10B981', not_found: C.textDim, pending: '#F59E0B', error: '#EF4444' };
          var lbl = { found: 'Found', not_found: 'Not Found on ML', pending: 'Pending', error: 'Error' };
          return h('span', { style: { color: colr[r.status] || C.textMuted, fontWeight: 600, fontSize: 12 } }, lbl[r.status] || r.status);
        } },
      // ML catalog extras — hidden by default; enable via the Columns menu (and save as a view)
      { key: 'ml_model', label: 'ML Model', defaultVisible: false, sortValue: function(r) { return (md(r).model || '').toLowerCase(); },
        filterValue: function(r) { return md(r).model || null; },
        render: function(r) { return md(r).model ? h('span', { style: { color: C.textMuted } }, md(r).model) : dimCell(); } },
      { key: 'ml_condition', label: 'ML Condition', defaultVisible: false, filterOptions: ['new', 'used'],
        sortValue: function(r) { return md(r).bb_condition || ''; },
        render: function(r) { return md(r).bb_condition ? h('span', { style: { color: C.textMuted, textTransform: 'capitalize' } }, md(r).bb_condition) : dimCell(); } },
      { key: 'ml_listing_type', label: 'ML Listing Type', defaultVisible: false,
        sortValue: function(r) { return md(r).bb_listing_type || ''; },
        render: function(r) {
          var m = { gold_special: 'Classic', gold_pro: 'Premium' };
          var v = md(r).bb_listing_type;
          return v ? h('span', { title: v, style: { color: C.textMuted } }, m[v] || v) : dimCell();
        } },
      { key: 'ml_full_offers', label: 'ML Full Offers', defaultVisible: false,
        sortValue: function(r) { return md(r).full_offers != null ? md(r).full_offers : -1; },
        filterValue: function(r) { return md(r).full_offers != null ? md(r).full_offers : null; },
        render: function(r) {
          var v = md(r).full_offers;
          return v != null ? h('span', { title: 'Competing offers shipped from ML’s Full warehouse', style: { color: v > 0 ? '#F59E0B' : C.textMuted, fontWeight: v > 0 ? 600 : 400, cursor: 'help' } }, v) : dimCell();
        } },
      { key: 'ml_freeship_offers', label: 'ML Free Ship Offers', defaultVisible: false,
        sortValue: function(r) { return md(r).freeship_offers != null ? md(r).freeship_offers : -1; },
        filterValue: function(r) { return md(r).freeship_offers != null ? md(r).freeship_offers : null; },
        render: function(r) { var v = md(r).freeship_offers; return v != null ? String(v) : dimCell(); } },
      { key: 'ml_bb_fulfillment', label: 'ML BB Fulfillment', defaultVisible: false, filterOptions: ['FULL', 'No'],
        sortValue: function(r) { return md(r).bb_fulfillment === true ? 1 : 0; },
        filterValue: function(r) { var v = md(r).bb_fulfillment; return v == null ? null : (v ? 'FULL' : 'No'); },
        render: function(r) {
          var v = md(r).bb_fulfillment;
          if (v == null) return dimCell();
          return v ? h('span', { title: 'The buy-box winner ships from ML’s Full warehouse', style: { color: '#F59E0B', fontWeight: 600, cursor: 'help' } }, 'FULL') : h('span', { style: { color: C.textDim } }, 'No');
        } },
      { key: 'ml_bb_free_ship', label: 'ML BB Free Ship', defaultVisible: false, filterOptions: ['Yes', 'No'],
        sortValue: function(r) { return md(r).bb_free_ship === true ? 1 : 0; },
        filterValue: function(r) { var v = md(r).bb_free_ship; return v == null ? null : (v ? 'Yes' : 'No'); },
        render: function(r) { var v = md(r).bb_free_ship; return v == null ? dimCell() : (v ? 'Yes' : 'No'); } },
      { key: 'ml_original_price', label: 'ML Original Price', defaultVisible: false,
        sortValue: function(r) { return md(r).bb_original_price != null ? md(r).bb_original_price : -1; },
        filterValue: function(r) { var v = md(r).bb_original_price; return v != null ? v * fxRate() : null; },
        render: function(r) {
          var v = md(r).bb_original_price;
          if (v == null) return dimCell();
          return h('span', { title: 'Buy-box pre-promo price: ' + v.toFixed(2) + ' MXN', style: { color: C.textMuted, textDecoration: 'line-through', cursor: 'help' } }, csym() + (v * fxRate()).toFixed(2));
        } },
      { key: 'ml_status', label: 'ML Status', defaultVisible: false, sortValue: function(r) { return md(r).status || ''; },
        filterValue: function(r) { return md(r).status || null; },
        render: function(r) { return md(r).status ? h('span', { style: { color: C.textMuted } }, md(r).status) : dimCell(); } },
      { key: 'ml_created', label: 'ML Created', defaultVisible: false,
        sortValue: function(r) { return md(r).created ? new Date(md(r).created).getTime() : 0; },
        filterValue: function(r) { return md(r).created ? new Date(md(r).created).toLocaleDateString() : null; },
        render: function(r) { return md(r).created ? new Date(md(r).created).toLocaleDateString() : dimCell(); } },
      { key: 'link', label: 'Link', noFilter: true,
        render: function(r) {
          var url = r.permalink || (r.product_id ? 'https://www.mercadolibre.com.mx/p/' + r.product_id : null);
          return url
            ? h('a', { href: url, target: '_blank', rel: 'noopener', onClick: function(e) { e.stopPropagation(); }, title: 'Open on Mercado Libre México', style: { color: C.accent, display: 'inline-flex' } }, h(LIcon, { name: 'external-link', size: 14 }))
            : dimCell();
        } },
    ].concat(refColumns);

    var stM = mrScanStatusMeta[sc.status] || mrScanStatusMeta.done;
    return h('div', { style: { animation: 'fadeIn 0.3s ease' } },
      h('div', { style: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' } },
        h(Btn, { onClick: gotoDash }, h(LIcon, { name: 'arrow-left', size: 14 }), ' Back'),
        renaming
          ? h('input', { autoFocus: true, value: renameVal,
              onChange: function(e) { setRenameVal(e.target.value); },
              onKeyDown: function(e) { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setRenaming(false); },
              onBlur: commitRename,
              style: { fontSize: 18, fontWeight: 700, color: C.text, background: C.bg, border: '1px solid ' + C.accent, borderRadius: 6, padding: '2px 8px', margin: '-3px 0', fontFamily: 'inherit', outline: 'none', width: Math.max(renameW, 120), boxSizing: 'content-box' } })
          : h('h2', {
              title: canWrite ? 'Click to rename' : undefined,
              onClick: canWrite ? function(e) { setRenameW(e.currentTarget.offsetWidth); setRenameVal(sc.name); setRenaming(true); } : undefined,
              style: { fontSize: 18, fontWeight: 700, color: C.text, margin: 0, cursor: canWrite ? 'text' : 'default', display: 'inline-flex', alignItems: 'center', gap: 7 }
            }, sc.name, canWrite && h(LIcon, { name: 'pencil', size: 13, color: C.textDim })),
        h('span', { style: { display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: stM.bg, color: stM.text } }, stM.label),
        sc.status === 'processing' && h('span', { style: { fontSize: 12, color: '#F59E0B', display: 'inline-flex', alignItems: 'center', gap: 6 } },
          h(LIcon, { name: 'loader-2', size: 13, spin: true }), (sc.processed || 0) + ' / ' + (sc.total_codes || 0)),
        sc.status === 'processing' && canWrite && h(Btn, {
          onClick: cancelScan,
          title: 'Stop this run — processed codes keep their results, the rest stay pending'
        }, h(LIcon, { name: 'square', size: 11 }), ' Cancel'),
        h('div', { style: { flex: 1 } }),
        hiddenCount > 0 && h('span', {
          title: 'Hidden rows are excluded from the table and exports — use Actions → Unhide All to bring them back',
          style: { fontSize: 12, color: C.textDim, display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'help' }
        }, h(LIcon, { name: 'eye-off', size: 13 }), hiddenCount + ' hidden'),
        h(Btn, { onClick: function() { exportScanCsv(); }, disabled: !visRows.length }, h(LIcon, { name: 'download', size: 13 }), ' Export CSV'),
        canWrite && h(Btn, {
          onClick: refreshAll, disabled: sc.status === 'processing' || !rows.length,
          title: 'Re-check every code live on Mercado Libre (ignores the 7-day cache)'
        }, h(LIcon, { name: 'refresh-cw', size: 13 }), ' Refresh')
      ),
      // View dropdown (portaled out of the table) + Assumptions button + current-assumptions glance
      h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' } },
        h('div', { ref: function(el) { if (el) setViewsHost(el); }, style: { display: 'inline-flex' } }),
        h('button', {
          onClick: openAssumptions, className: 'dt-btn',
          title: 'Pricing assumptions behind the fee/profit columns — saved to this result set only',
          style: { background: 'none', border: '1px solid ' + C.border, color: C.textMuted, cursor: 'pointer', padding: '4px 10px', borderRadius: 4, fontSize: 11, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 4 }
        }, h(LIcon, { name: 'calculator', size: 12 }), ' Assumptions'),
        h('span', { style: { fontSize: 11, color: C.textDim } },
          (parseFloat(commPct) || 0) + '% comm · ' + csym() + (parseFloat(shipUsd) || 0).toFixed(2) + ' ship (≥' + FREE_SHIP_MXN + ' MXN) · '
          + csym() + (parseFloat(prepUsd) || 0).toFixed(2) + ' prep · ' + (parseFloat(inboundPct) || 0) + '% inbound · ' + baseCur)),
      h('div', { style: { display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' } },
        pills2.map(function(s) {
          var isActive = statusFilter === s.id;
          return h('button', { key: s.id || 'all', onClick: function() { setStatusFilter(s.id); },
            title: s.hint,
            className: isActive ? '' : 'filter-pill',
            style: { padding: '6px 14px', borderRadius: 6, border: '1px solid transparent', cursor: 'pointer', fontSize: 12, fontWeight: 500, fontFamily: 'inherit', transition: 'all 0.15s', background: isActive ? C.accent : C.surface, color: isActive ? '#fff' : C.textMuted }
          }, s.label + (s.count ? ' (' + s.count + ')' : ''));
        }),
        h('div', { style: { flex: 1 } }),
        h(SearchInput, { type: 'text', value: search, onChange: function(e) { setSearch(e.target.value); },
          placeholder: 'Search barcode, product, SKU, reference…',
          style: { width: 300, background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 8, fontSize: 13, outline: 'none' } })
      ),
      h(DataTable, { columns: columns, data: visRows, storageKey: 'meli-research-scan',
        scrollMode: true, scrollHeight: 620, compact: true,
        enableViews: true, viewsPortalEl: viewsHost,
        onSortedData: function(d) { sortedRowsRef.current = d; },
        bulkActions: { count: selIds.length, alwaysShow: hiddenCount > 0, items: bulkItems,
          onClear: function() { setSelected({}); lastCheckedRef.current = null; } },
        emptyTitle: 'No rows', emptySub: '' }),

      h(Modal, { open: assumpModal, onClose: function() { setAssumpModal(false); }, title: 'Assumptions', width: 440 },
        assumpDraft && (function() {
          var aSet = function(k, v) { var n = Object.assign({}, assumpDraft); n[k] = v; setAssumpDraft(n); };
          var aIn = function(k, w) {
            return h('input', { value: assumpDraft[k], onChange: function(e) { aSet(k, e.target.value); },
              style: { width: w || 100, background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '6px 9px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', outline: 'none' } });
          };
          var aRow = function(label, ctrl, hint) {
            return h('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 } },
              h('span', { title: hint, style: { fontSize: 13, color: C.textMuted, cursor: hint ? 'help' : 'default' } }, label), ctrl);
          };
          var cur = assumpDraft.baseCur;
          return h('div', { style: { display: 'flex', flexDirection: 'column', gap: 12 } },
            h('div', { style: { fontSize: 12, color: C.textDim } }, 'Used for the fee and profit columns. Saved to this result set only — other scans keep their own.'),
            aRow('Base currency', h('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
              cur === 'USD' && h('span', { title: 'Live rate — applied automatically', style: { fontSize: 11, color: C.textDim, cursor: 'help' } }, '1 MXN = $' + (fxMxnUsd || 0).toFixed(4)),
              h('select', { value: cur, onChange: function(e) { aSet('baseCur', e.target.value); },
                style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '6px 9px', borderRadius: 6, fontSize: 13, outline: 'none' } },
                h('option', { value: 'USD' }, 'USD'), h('option', { value: 'MXN' }, 'MXN')))),
            aRow('Commission %', aIn('commPct', 80)),
            aRow('Shipping (' + cur + ')', aIn('shipUsd', 80),
              'Seller-paid label — only charged at/above $' + FREE_SHIP_MXN + ' MXN item price (below it the buyer pays)'),
            aRow('Prep (' + cur + ')', aIn('prepUsd', 80)),
            aRow('Inbound % of cost', aIn('inboundPct', 80),
              'Inbound freight as % of unit cost — vendor → our warehouse → fulfillment center. Added to the cost side of profit/ROI.'),
            h('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 } },
              h(Btn, { onClick: function() { setAssumpModal(false); } }, 'Cancel'),
              h(Btn, { variant: 'primary', onClick: saveAssumptions }, 'Save')));
        })())
    );
  }

  // ── Dashboard ──
  var months = monthly.map(function(m) {
    var d = new Date(m.month + '-02');
    return { label: d.toLocaleString(undefined, { month: 'short' }) + ' ' + String(d.getFullYear()).slice(2),
      processed: m.processed || 0, found: m.found || 0 };
  });
  var scanCols = [
    { key: 'name', label: 'File Name', locked: true, sortValue: function(r) { return (r.name || '').toLowerCase(); },
      render: function(r) {
        return h('div', { title: r.name, style: { maxWidth: 320 } },
          h('span', { style: { display: 'block', fontWeight: 600, color: C.accent, fontSize: 11.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, r.name));
      } },
    { key: 'total_codes', label: 'Uploaded', sortValue: function(r) { return r.total_codes || 0; },
      render: function(r) { return h('span', { style: { fontWeight: 600 } }, (r.total_codes || 0).toLocaleString()); } },
    { key: 'found_count', label: 'Found', sortValue: function(r) { return r.found_count || 0; },
      render: function(r) { return h('span', { style: { color: '#10B981', fontWeight: 600 } }, (r.found_count || 0).toLocaleString()); } },
    { key: 'in_catalog_ml_count', label: 'In Cat (ML Listed)', sortValue: function(r) { return r.in_catalog_ml_count || 0; },
      render: function(r) {
        var n = r.in_catalog_ml_count || 0;
        return h('span', { title: 'Found on ML, in our catalog (barcode match), and already listed on ML', style: { color: n > 0 ? '#10B981' : C.textDim, fontWeight: n > 0 ? 600 : 400, cursor: 'help' } }, n.toLocaleString());
      } },
    { key: 'in_catalog_no_ml_count', label: 'In Cat (Not Listed)', sortValue: function(r) { return r.in_catalog_no_ml_count || 0; },
      render: function(r) {
        var n = r.in_catalog_no_ml_count || 0;
        return h('span', { title: 'Found on ML, in our catalog (barcode match), but no ML listing yet — candidates to list', style: { color: n > 0 ? '#F59E0B' : C.textDim, fontWeight: n > 0 ? 600 : 400, cursor: 'help' } }, n.toLocaleString());
      } },
    { key: 'created_at', label: 'Date', sortValue: function(r) { return new Date(r.created_at).getTime(); },
      render: function(r) { return new Date(r.created_at).toLocaleDateString(); } },
    { key: 'status', label: 'Status', sortValue: function(r) { return r.status; },
      render: function(r) {
        var m = mrScanStatusMeta[r.status] || mrScanStatusMeta.done;
        var label = m.label;
        if (r.status === 'processing') {
          var pct = r.total_codes > 0 ? Math.round((r.processed || 0) / r.total_codes * 100) : 0;
          label = pct + '%';
        }
        return h('div', { style: { display: 'inline-flex', alignItems: 'center', gap: 6 } },
          h('span', {
            title: r.status === 'processing' ? (r.processed || 0) + ' of ' + (r.total_codes || 0) + ' codes processed' : undefined,
            style: { display: 'inline-block', padding: '2px 9px', borderRadius: 12, fontSize: 10, fontWeight: 600, background: m.bg, color: m.text }
          }, label),
          r.status === 'processing' && canWrite && h('span', {
            title: 'Cancel this run',
            onClick: function(e) {
              e.preventDefault();
              e.stopPropagation();
              if (!window.confirm('Cancel "' + r.name + '"? Processed codes keep their results; the rest stay pending.')) return;
              api('/meli-research/scans/' + r.id + '/cancel', { method: 'POST' })
                .then(function() { loadDash(); })
                .catch(function(err) { showToast(err.message, 'error'); });
            },
            style: { display: 'inline-flex', cursor: 'pointer', color: '#EF4444', padding: 2 }
          }, h(LIcon, { name: 'square', size: 12 })));
      } },
  ];

  return h('div', { style: { animation: 'fadeIn 0.3s ease' } },
    h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 } },
      h('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
        h('h2', { style: { fontSize: 20, fontWeight: 700, color: C.text, margin: 0 } }, 'Product Research'),
        h('span', { style: { fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 10, background: '#FFE60022', color: '#B59B00' } }, 'Mercado Libre México')),
      canWrite && h('div', { style: { position: 'relative' } },
        h(Btn, { variant: 'primary', onClick: function() { setMenuOpen(!menuOpen); } },
          h(LIcon, { name: 'scan-line', size: 14 }), ' New Scan ', h(LIcon, { name: 'chevron-down', size: 13 })),
        menuOpen && h(Fragment, null,
          // Click-away layer: any click outside the menu closes it
          h('div', { onClick: function() { setMenuOpen(false); }, style: { position: 'fixed', inset: 0, zIndex: 29 } }),
          h('div', { style: { position: 'absolute', right: 0, top: '110%', zIndex: 30, background: C.surface, border: '1px solid ' + C.border, borderRadius: 10, overflow: 'hidden', minWidth: 200, boxShadow: '0 8px 24px rgba(0,0,0,0.35)' } },
            [{ label: 'Process Spreadsheet', icon: 'file-spreadsheet', act: function() { setSheetModal(true); } },
             { label: 'Quick Lookup', icon: 'zap', act: function() { setQuickModal(true); } }].map(function(it) {
              return h('div', { key: it.label,
                onClick: function() { setMenuOpen(false); it.act(); },
                style: { padding: '10px 14px', fontSize: 13, color: C.text, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 },
                onMouseEnter: function(e) { e.currentTarget.style.background = C.bg; },
                onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; }
              }, h(LIcon, { name: it.icon, size: 14, color: C.accent }), it.label);
            }))))
    ),
    // Charts row (original positions), then the Recent List table full-width underneath
    h('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 14, marginBottom: 18 } },
      h('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 10, padding: 16, height: 340 } },
        h(MrAreaChart, { data: months.map(function(m) { return { label: m.label, value: m.processed }; }), color: C.accent, label: 'Records Processed per Month' })),
      h('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 10, padding: 16, height: 340 } },
        h(MrBarChart, { data: months.map(function(m) { return { label: m.label, value: m.found }; }), color: '#10B981', label: 'Products Found on ML per Month' }))
    ),
    h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 } },
      h('span', { style: { fontSize: 15, fontWeight: 700, color: C.text } }, 'Recent List'),
      h('span', { style: { fontSize: 12, color: C.textDim } }, '— click a scan to open its results')),
    h(DataTable, { columns: scanCols, data: scans, compact: true,
      scrollMode: true, scrollHeight: 300,
      onRowClick: function(r) { gotoScan(r.id); },
      rowHref: function(r) { return '/meli-research/' + r.id; },
      emptyTitle: loading ? 'Loading…' : 'No scans yet',
      emptySub: loading ? '' : 'Use New Scan to research products',
      storageKey: 'meli-research-scans', defaultSort: 'created_at', defaultSortDir: 'desc' }),

    // ── Process Spreadsheet modal ──
    h(Modal, { open: sheetModal, onClose: function() { setSheetModal(false); }, title: 'Process Spreadsheet', width: 720 },
      h('div', { style: { display: 'flex', flexDirection: 'column', gap: 14 } },
        h('div', { style: { fontSize: 12, color: C.textMuted } },
          'Upload a CSV (e.g. a vendor price list). Pick which column holds the barcodes, optionally a cost column for profit math, and any columns to carry through into the results.'),
        h('input', { ref: fileInputRef, type: 'file', accept: '.csv,text/csv', style: { display: 'none' },
          onChange: function(e) { if (e.target.files && e.target.files[0]) handleFile(e.target.files[0]); e.target.value = ''; } }),
        h('div', { style: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' } },
          h(Btn, { onClick: function() { if (fileInputRef.current) fileInputRef.current.click(); } },
            h(LIcon, { name: 'upload', size: 13 }), ' Upload File'),
          h('span', { style: { fontSize: 12, color: fileLabel ? C.text : C.textDim, fontStyle: fileLabel ? 'normal' : 'italic' } },
            fileLabel || 'No file chosen'),
          h('div', { style: { flex: 1 } }),
          h(Btn, { size: 'sm', onClick: downloadTemplate }, h(LIcon, { name: 'download', size: 13 }), ' Download Template')),
        h('div', {
          onClick: function() { if (fileInputRef.current) fileInputRef.current.click(); },
          onDragOver: function(e) { e.preventDefault(); setDragOver(true); },
          onDragLeave: function() { setDragOver(false); },
          onDrop: function(e) {
            e.preventDefault(); setDragOver(false);
            var f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
            if (f) handleFile(f);
          },
          style: { border: '2px dashed ' + (dragOver ? C.accent : C.border), borderRadius: 10,
            background: dragOver ? C.accent + '11' : 'transparent', padding: '26px 16px',
            textAlign: 'center', cursor: 'pointer', transition: 'all 0.15s' }
        },
          h(LIcon, { name: 'file-up', size: 22, color: dragOver ? C.accent : C.textDim }),
          h('div', { style: { marginTop: 6, fontSize: 13, color: dragOver ? C.accent : C.textMuted, fontWeight: dragOver ? 600 : 400 } },
            dragOver ? 'Drop it!' : 'Drag & drop a CSV here — or click to browse')),
        csvHeaders.length > 0 && h(Fragment, null,
          h('div', { style: { fontSize: 12, color: C.textMuted } },
            csvRows.length.toLocaleString() + ' data rows — mapping:',
            csvRows.length > MAX_SCAN_ROWS && h('span', { style: { color: '#F59E0B', fontWeight: 600 } },
              ' ⚠ over the ' + MAX_SCAN_ROWS.toLocaleString() + '-row limit — only the first ' + MAX_SCAN_ROWS.toLocaleString() + ' will be processed (split the sheet for the rest)')),
          h('div', { style: { display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' } },
            h('label', { style: { fontSize: 12, color: C.textMuted } }, 'Barcode Column '),
            h('select', { value: colBarcode, onChange: function(e) { setColBarcode(e.target.value); },
              style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '7px 10px', borderRadius: 6, fontSize: 13, outline: 'none' } },
              [h('option', { key: '-', value: '' }, '— pick —')].concat(csvHeaders.map(function(hd, i) { return h('option', { key: i, value: String(i) }, hd); }))),
            h('label', { style: { fontSize: 12, color: C.textMuted } }, 'Cost Column '),
            h('select', { value: colCost, onChange: function(e) { setColCost(e.target.value); },
              style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '7px 10px', borderRadius: 6, fontSize: 13, outline: 'none' } },
              [h('option', { key: '-', value: '' }, 'None (use catalog cost)')].concat(csvHeaders.map(function(hd, i) { return h('option', { key: i, value: String(i) }, hd); })))
          ),
          h('div', null,
            h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 6 } }, 'Reference Columns to Keep (appended to the results):'),
            h('div', { style: { display: 'flex', gap: 8, flexWrap: 'wrap' } },
              csvHeaders.map(function(hd, i) {
                if (String(i) === colBarcode) return null;
                var on = !!refCols[i];
                return h('label', { key: i, style: { display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: on ? C.text : C.textMuted, background: on ? C.accent + '22' : C.bg, border: '1px solid ' + (on ? C.accent : C.border), borderRadius: 6, padding: '4px 9px', cursor: 'pointer' } },
                  h('input', { type: 'checkbox', checked: on, onChange: function() { var next = Object.assign({}, refCols); next[i] = !on; setRefCols(next); }, style: { display: 'none' } }),
                  hd);
              }))
          ),
          h('div', { style: { background: C.bg, border: '1px solid ' + C.border + '66', borderRadius: 8, padding: '10px 12px' } }, assumptionsStrip()),
          h('div', { style: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' } },
            h('label', { style: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.textMuted, cursor: 'pointer' } },
              h('input', { type: 'checkbox', checked: notifyOn, onChange: function(e) { setNotifyOn(e.target.checked); } }),
              'Email me when the results are ready (with the CSV attached)'),
            notifyOn && h('input', { value: notifyEmail, onChange: function(e) { setNotifyEmail(e.target.value); }, placeholder: 'you@company.com',
              style: { width: 230, background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '6px 10px', borderRadius: 6, fontSize: 12, outline: 'none' } })),
          h('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } },
            h(Btn, { onClick: function() { setSheetModal(false); } }, 'Cancel'),
            h(Btn, { variant: 'primary', disabled: busy || colBarcode === '' || (notifyOn && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(notifyEmail.trim())), onClick: submitSheet }, busy ? 'Starting…' : 'Start Scan'))
        )
      )
    ),

    // ── Quick Lookup modal ──
    h(Modal, { open: quickModal, onClose: function() { setQuickModal(false); }, title: 'Quick Lookup', width: 560 },
      h('div', { style: { display: 'flex', flexDirection: 'column', gap: 12 } },
        h('div', { style: { fontSize: 12, color: C.textMuted } }, 'Paste UPC/EAN codes — one per line, or comma/space separated.'),
        h('textarea', { value: quickInput, onChange: function(e) { setQuickInput(e.target.value); }, rows: 5,
          placeholder: '6291108738528\n844828015972\n…',
          style: { width: '100%', background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '10px 12px', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none', resize: 'vertical' } }),
        h('div', { style: { background: C.bg, border: '1px solid ' + C.border + '66', borderRadius: 8, padding: '10px 12px' } }, assumptionsStrip()),
        h('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } },
          h(Btn, { onClick: function() { setQuickModal(false); } }, 'Cancel'),
          h(Btn, { variant: 'primary', disabled: busy || !quickInput.trim(), onClick: submitQuick }, busy ? 'Starting…' : 'Look Up'))
      )
    )
  );
}

window.MeliResearchPage = MeliResearchPage;

;
/* ===== app-research.js ===== */
// ─── Research → Data + Amazon US (docs/26) ───
// Data = vendor price-list STORAGE (Anthony 2026-10-03): one master sheet per vendor, edited like
// Smartsheet (Tabulator grid: type into a cell, paste a block from Excel, fill down, clear, undo /
// redo, add / delete rows, custom columns, row history). Uploading a list (xlsx / xls / csv, read in
// the browser with SheetJS) merges it into the vendor's sheet. Amazon US shows each row's match to
// our catalog today; the Amazon recommendations come later (docs/26 Phase 3).

var { createElement: h } = React;

var PL_FIELDS = [
  { key: 'barcode', label: 'Barcode (UPC / EAN)', re: /\b(upc|ean|barcode|bar code|gtin)\b/i },
  { key: 'cost', label: 'Cost', re: /^(unit )?cost\b|wholesale|net price|your price|\bfob\b|price usd|usd price|^cost/i, fallback: /price|\baed\b|amount|\brate\b/i },
  { key: 'description', label: 'Description', re: /desc|vendor title|product name|item name|^name$/i, fallback: /\btitle\b|^product$/i },
  { key: 'vendor_sku', label: 'Vendor Item #', re: /item ?#|item ?(no|num)|item code|^sku|vendor sku|style|model|\bref\b|^item$|code$/i, fallback: /^product( ?(#|id|code))?$/i },
  { key: 'brand', label: 'Brand', re: /brand|manufacturer|\bhouse\b/i },
  { key: 'case_pack', label: 'Case Pack', re: /case|carton|\binner\b|master|per box|pcs/i, fallback: /pack/i },
  { key: 'asin', label: 'ASIN', re: /\basin\b/i },
  { key: 'moq', label: 'MOQ', re: /\bmoq\b|min(imum)? ?(order|qty)|^min$/i },
  { key: 'qty', label: 'Qty Available', re: /avail|on hand|in stock|\bstock\b|^qty$|^quantity$|total qty/i },
  { key: 'msrp', label: 'MSRP', re: /msrp|retail|\bsrp\b|\bmap\b/i },
];
var PL_STATUS = {
  in_catalog: { label: 'In Catalog', color: '#22c55e', hint: 'We buy it from this vendor on an active US FBA SKU' },
  other_vendor: { label: 'Other Vendor', color: '#2dd4bf', hint: 'We sell it on an active US FBA SKU bought from another vendor' },
  no_fba: { label: 'No FBA SKU', color: '#f59e0b', hint: 'Active, but not on a US FBA SKU (FBM / WFS / MELI / Canada…)' },
  archived: { label: 'Archived', color: '#a78bfa', hint: 'Only archived SKUs match: a reactivation candidate' },
  'new': { label: 'New', color: '#38bdf8', hint: 'Not in our catalog' },
  check: { label: 'Check Match', color: '#f87171', hint: 'Only a barcode links it to our SKU, and the names do not match' },
};
var PL_CURRENCIES = ['USD', 'AED', 'EUR', 'GBP', 'CAD', 'MXN', 'CNY', 'INR'];
var PL_PEGGED = { AED: 0.2723 }; // 1 AED = 0.2723 USD (pegged at 3.6725)
var PL_COL_TYPES = [{ value: 'text', label: 'Text' }, { value: 'number', label: 'Number' }, { value: 'money', label: 'Money ($)' },
  { value: 'date', label: 'Date' }, { value: 'checkbox', label: 'Checkbox' }, { value: 'select', label: 'Dropdown' }];
var PL_TABULATOR_JS = 'https://cdnjs.cloudflare.com/ajax/libs/tabulator-tables/6.6.0/js/tabulator.min.js';
var PL_TABULATOR_CSS = '/vendor/tabulator-6.6.0.min.css';

function plMoney(v, d) { if (v == null || v === '') return ''; var n = Number(v); return isFinite(n) ? '$' + n.toFixed(d == null ? 2 : d) : ''; }
function plPct(v) { if (v == null || v === '') return ''; var n = Number(v); return isFinite(n) ? (n > 0 ? '+' : '') + (n * 100).toFixed(1) + '%' : ''; }
function plToday() { var d = new Date(); return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'); }
function plDate(v) { return v ? String(v).slice(0, 10) : ''; }
function plCell(v) {
  if (v instanceof Date) return isNaN(v) ? '' : v.toISOString().slice(0, 10);
  if (typeof v === 'number') return Number.isInteger(v) ? String(v) : v; // keep long barcodes as digits
  return v == null ? '' : v;
}

// SheetJS: the workbook from a picked file. CSV is read as text cells so barcodes keep their digits.
function plReadWorkbook(file) {
  return new Promise(function(resolve, reject) {
    if (typeof XLSX === 'undefined') { reject(new Error('The spreadsheet reader did not load - reload the page')); return; }
    var reader = new FileReader();
    reader.onload = function() {
      try { resolve(XLSX.read(new Uint8Array(reader.result), { type: 'array', raw: /\.csv$/i.test(file.name), cellDates: true })); }
      catch (e) { reject(new Error('Could not read this file: ' + e.message)); }
    };
    reader.onerror = function() { reject(new Error('Could not read this file')); };
    reader.readAsArrayBuffer(file);
  });
}
function plSheetRows(wb, name) {
  var ws = wb.Sheets[name]; if (!ws) return [];
  return XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: '', blankrows: false })
    .map(function(r) { return r.map(plCell); });
}
// The header row: within the first 25 rows, the one naming the most known columns.
function plGuessHeaderRow(aoa) {
  var best = 0, bestScore = -1;
  for (var i = 0; i < Math.min(aoa.length, 25); i++) {
    var cells = aoa[i].filter(function(c) { return String(c).trim() !== ''; });
    if (cells.length < 2) continue;
    var text = cells.filter(function(c) { return typeof c === 'string' && /[A-Za-z]/.test(c); }).length;
    var hits = cells.filter(function(c) { return PL_FIELDS.some(function(f) { return f.re.test(String(c)) || (f.fallback && f.fallback.test(String(c))); }); }).length;
    var score = hits * 10 + text;
    if (score > bestScore) { bestScore = score; best = i; }
  }
  return best;
}
function plGuessMap(headers, body) {
  var map = {}, used = {};
  var take = function(key, idx) { if (idx >= 0 && !used[idx]) { map[key] = String(idx); used[idx] = true; } };
  var sample = body.slice(0, 30);
  var looksLikeBarcode = function(ci) {
    var hits = 0, seen = 0;
    sample.forEach(function(r) { var s = String(r[ci] == null ? '' : r[ci]).trim(); if (!s) return; seen++; var d = s.replace(/[^0-9]/g, ''); if (d.length >= 8 && d.length <= 14 && d.length >= s.length - 2) hits++; });
    return seen > 0 && hits / seen > 0.6;
  };
  PL_FIELDS.forEach(function(f) { take(f.key, headers.findIndex(function(hd, i) { return !used[i] && f.re.test(hd); })); });
  PL_FIELDS.forEach(function(f) { if (map[f.key] == null && f.fallback) take(f.key, headers.findIndex(function(hd, i) { return !used[i] && f.fallback.test(hd); })); });
  if (map.barcode == null) take('barcode', headers.findIndex(function(_, i) { return !used[i] && looksLikeBarcode(i); }));
  // AED + USD cost columns on one sheet (the Maison Alhambra list): prefer the USD one for cost
  var usd = headers.findIndex(function(hd) { return /^cost$|usd/i.test(hd); });
  if (usd >= 0 && map.cost != null && String(usd) !== map.cost && /\baed\b/i.test(headers[+map.cost])) { delete used[+map.cost]; map.cost = null; take('cost', usd); }
  return map;
}

// ── Terms (per uploaded list): upload form + Edit Terms ──
function plTermsFrom(list, vendorId) {
  var pct = function(v) { return v == null || v === '' ? '' : String(Math.round(Number(v) * 10000) / 100); };
  return {
    vendor_id: vendorId ? String(vendorId) : (list && list.vendor_id ? String(list.vendor_id) : ''),
    name: (list && list.name) || '', received_at: list && list.received_at ? plDate(list.received_at) : plToday(),
    currency: (list && list.currency) || 'USD', fx_rate: list && list.fx_rate != null ? String(Number(list.fx_rate)) : '1',
    freight_pct: pct(list && list.freight_pct), tariff_pct: pct(list ? list.tariff_pct : 0),
    prep_cost: list ? (list.prep_cost != null ? String(Number(list.prep_cost)) : '') : '0.70',
    case_pack: list && list.case_pack ? String(list.case_pack) : '', lead_days: list && list.lead_days ? String(list.lead_days) : '',
    notes: (list && list.notes) || '',
  };
}
function plTermsPayload(t) {
  var frac = function(v) { return v === '' || v == null ? null : Number(v) / 100; };
  return { vendor_id: t.vendor_id ? Number(t.vendor_id) : null, name: t.name.trim(), received_at: t.received_at || null, currency: t.currency,
    fx_rate: Number(t.fx_rate) || 1, freight_pct: frac(t.freight_pct), tariff_pct: frac(t.tariff_pct) || 0, prep_cost: t.prep_cost === '' ? null : Number(t.prep_cost),
    case_pack: t.case_pack === '' ? null : Number(t.case_pack), lead_days: t.lead_days === '' ? null : Number(t.lead_days), notes: t.notes };
}
function PlTermsForm({ terms, setTerms, vendors, lockVendor }) {
  var set = function(k, v) { var n = Object.assign({}, terms); n[k] = v; if (k === 'currency' && PL_PEGGED[v] && (n.fx_rate === '1' || n.fx_rate === '')) n.fx_rate = String(PL_PEGGED[v]); if (k === 'currency' && v === 'USD') n.fx_rate = '1'; setTerms(n); };
  var lab = function(t) { return h('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' } }, t); };
  var field = function(label, el, hint) { return h('div', { style: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }, title: hint || '' }, lab(label), el); };
  var inp = function(k, extra) { return h('input', Object.assign({ value: terms[k], onChange: function(e) { set(k, e.target.value); },
    style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 10px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', outline: 'none', minWidth: 0, width: '100%', boxSizing: 'border-box' } }, extra || {})); };
  // minmax(0, 1fr): a plain 1fr column will not shrink below its input's natural width, which pushed
  // Lead Days past the Edit Terms window (Anthony 10-03)
  var grid = function(cols, kids) { return h('div', { style: { display: 'grid', gridTemplateColumns: cols.replace(/1fr/g, 'minmax(0, 1fr)'), gap: 12 } }, kids); };
  var vendorOpts = (vendors || []).map(function(v) { return { value: String(v.id), label: v.vendor_name + (v.is_active === false ? ' (inactive)' : '') }; });
  var vName = (vendors || []).find(function(v) { return String(v.id) === String(terms.vendor_id); });
  var cur = terms.currency;
  return h('div', { style: { display: 'flex', flexDirection: 'column', gap: 12 } },
    grid('1fr 1fr', [
      h('div', { key: 'v' }, field('Vendor', lockVendor
        ? h('div', { style: { padding: '8px 0', fontSize: 14, color: C.text, fontWeight: 600 } }, vName ? vName.vendor_name : '')
        : h(SearchSelect, { options: vendorOpts, value: terms.vendor_id, placeholder: 'Pick the vendor…', onChange: function(v) { set('vendor_id', v || ''); } }),
        'A vendor that is not in the system yet is created in Settings > Vendors first')),
      h('div', { key: 'n' }, field('List Name', inp('name', { placeholder: 'e.g. Price List 2025' }))),
    ]),
    grid('1fr 1fr 1fr', [
      h('div', { key: 'r' }, field('Received', inp('received_at', { type: 'date' }), 'A list older than one already loaded is kept as price history only')),
      h('div', { key: 'c' }, field('Currency', h('select', { value: cur, onChange: function(e) { set('currency', e.target.value); },
        style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 10px', borderRadius: 6, fontSize: 13, outline: 'none', width: '100%', minWidth: 0, boxSizing: 'border-box' } },
        PL_CURRENCIES.map(function(c) { return h('option', { key: c, value: c }, c); })))),
      h('div', { key: 'f' }, field(cur === 'USD' ? 'Exchange Rate' : '1 ' + cur + ' = USD', inp('fx_rate', { disabled: cur === 'USD', inputMode: 'decimal' }),
        'Multiplies every list cost into USD. AED is pegged: 0.2723.')),
    ]),
    grid('repeat(5, 1fr)', [
      h('div', { key: 'fr' }, field('Inbound %', inp('freight_pct', { inputMode: 'decimal', placeholder: 'e.g. 15' }), 'Inbound freight as % of cost (direct imports: 15)')),
      h('div', { key: 'ta' }, field('Tariff %', inp('tariff_pct', { inputMode: 'decimal' }), 'Duty as % of cost (UAE direct imports: 12.5; domestic, duty-paid: 0)')),
      h('div', { key: 'pr' }, field('Prep $', inp('prep_cost', { inputMode: 'decimal' }))),
      h('div', { key: 'cp' }, field('Case Pack', inp('case_pack', { inputMode: 'numeric', placeholder: 'per row' }), 'Used where the list has no case-pack column')),
      h('div', { key: 'ld' }, field('Lead Days', inp('lead_days', { inputMode: 'numeric', placeholder: 'e.g. 90' }))),
    ]),
    field('Notes', inp('notes', { placeholder: 'Terms, minimums, contact…' })));
}

// ── Upload: read the file, map columns, set terms, merge into the vendor's sheet ──
function PlUploadModal({ open, onClose, onDone, showToast, vendors, vendorId }) {
  var _f = useState(null), file = _f[0], setFile = _f[1];
  var _wb = useState(null), wb = _wb[0], setWb = _wb[1];
  var _sh = useState(''), sheet = _sh[0], setSheet = _sh[1];
  var _a = useState([]), aoa = _a[0], setAoa = _a[1];
  var _hr = useState(1), headerRow = _hr[0], setHeaderRow = _hr[1];
  var _m = useState({}), map = _m[0], setMap = _m[1];
  var _t = useState(plTermsFrom(null, vendorId)), terms = _t[0], setTerms = _t[1];
  var _b = useState(false), busy = _b[0], setBusy = _b[1];
  useEffect(function() { if (open) setTerms(function(t) { return Object.assign({}, t, { vendor_id: vendorId ? String(vendorId) : t.vendor_id }); }); }, [open, vendorId]);

  // The app mounts with ReactDOM.render, so state set in a promise callback renders after EACH call:
  // clear the workbook first, so the form is never drawn with a workbook but no file (error_log 742).
  var reset = function() { setWb(null); setFile(null); setSheet(''); setAoa([]); setHeaderRow(1); setMap({}); setTerms(plTermsFrom(null, vendorId)); };
  var close = function() { if (!busy) { reset(); onClose(); } };
  var hdrs = function(rows, hr) { return (rows[hr - 1] || []).map(function(x, i) { return String(x == null ? '' : x).trim() || ('Column ' + (i + 1)); }); };
  var headers = aoa.length ? hdrs(aoa, headerRow) : [];
  var body = aoa.slice(headerRow);
  var loadSheet = function(w, name) {
    var rows = plSheetRows(w, name), hr = plGuessHeaderRow(rows) + 1;
    setSheet(name); setAoa(rows); setHeaderRow(hr); setMap(plGuessMap(hdrs(rows, hr), rows.slice(hr)));
  };
  var pick = function(f) {
    if (!f) return;
    setFile(f);
    plReadWorkbook(f).then(function(w) {
      setWb(w);
      var names = w.SheetNames.slice().sort(function(a, b) { return plSheetRows(w, b).length - plSheetRows(w, a).length; }); // the biggest sheet is almost always the list
      loadSheet(w, names[0]);
      setTerms(function(t) { return Object.assign({}, t, { name: t.name || f.name.replace(/\.[^.]+$/, '') }); });
    }).catch(function(e) { showToast(e.message, 'error'); setFile(null); });
  };
  var changeHeaderRow = function(n) {
    var hr = Math.max(1, Math.min(aoa.length || 1, n || 1));
    setHeaderRow(hr); setMap(plGuessMap(hdrs(aoa, hr), aoa.slice(hr)));
  };
  var col = function(r, k) { var i = map[k]; return i == null || i === '' ? '' : r[+i]; };
  var usable = body.filter(function(r) { return ['cost', 'barcode', 'vendor_sku', 'asin'].some(function(k) { return String(col(r, k) == null ? '' : col(r, k)).trim() !== ''; }); }).length;
  var fx = Number(terms.fx_rate) || 1;
  var nonUsd = terms.currency !== 'USD';

  var submit = function() {
    if (map.cost == null || map.cost === '') { showToast('Map the cost column', 'error'); return; }
    if ([map.barcode, map.vendor_sku, map.asin].every(function(v) { return v == null || v === ''; })) { showToast('Map a barcode, vendor item # or ASIN column', 'error'); return; }
    if (!terms.vendor_id) { showToast('Pick the vendor', 'error'); return; }
    if (!terms.name.trim()) { showToast('Give the list a name', 'error'); return; }
    var cm = {}; Object.keys(map).forEach(function(k) { if (map[k] !== '' && map[k] != null) cm[k] = +map[k]; });
    setBusy(true);
    api('/research/data/lists', { method: 'POST', body: JSON.stringify(Object.assign(plTermsPayload(terms), {
      file_name: file ? file.name : null, sheet_name: sheet, header_row: headerRow, headers: headers, rows: body, column_map: cm })) })
      .then(function(r) {
        var c = r.counts;
        showToast(c.latest
          ? 'Merged ' + c.rows.toLocaleString() + ' rows: ' + c.added + ' new, ' + c.updated + ' changed (' + c.cost_changed + ' cost), ' + c.not_on_list + ' not on this list'
          : 'Older than the newest list: kept as price history only');
        onDone(r.vendor_id, c); reset();
      })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setBusy(false); });
  };
  var sel = function(k) {
    return h('select', { value: map[k] == null ? '' : map[k], onChange: function(e) { var n = Object.assign({}, map); n[k] = e.target.value; setMap(n); },
      style: { width: '100%', background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '6px 8px', borderRadius: 6, fontSize: 13, outline: 'none' } },
      h('option', { value: '' }, '— Not On This List —'),
      headers.map(function(hd, i) { return h('option', { key: i, value: String(i) }, hd); }));
  };
  var th = { textAlign: 'left', padding: '6px 8px', fontSize: 11, color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', borderBottom: '1px solid ' + C.border, whiteSpace: 'nowrap' };
  var td = { padding: '5px 8px', fontSize: 12, color: C.text, borderBottom: '1px solid ' + C.border, whiteSpace: 'nowrap', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' };
  var small = { fontSize: 11, color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 };
  var previewCols = ['Barcode', 'Vendor Item #', 'Description', 'Cost' + (nonUsd ? ' (' + terms.currency + ')' : '')].concat(nonUsd ? ['Cost (USD)'] : [], ['Case Pack']);

  return h(Modal, { open: open, onClose: close, title: 'Upload Price List', width: 920 },
    !wb
      ? h('div', { style: { display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center', padding: '36px 12px' } },
          h(LIcon, { name: 'file-spreadsheet', size: 30, color: C.textDim }),
          h('div', { style: { fontSize: 13, color: C.textMuted, textAlign: 'center' } }, 'Excel (.xlsx, .xls) or CSV. The file is read in your browser and merged into the vendor\'s sheet: matching rows are updated, new items added, and the list is kept as price history.'),
          h('label', { style: { cursor: 'pointer' } },
            h('input', { type: 'file', accept: '.xlsx,.xls,.xlsm,.csv', style: { display: 'none' }, onChange: function(e) { pick(e.target.files && e.target.files[0]); e.target.value = ''; } }),
            h('span', { style: { display: 'inline-flex', alignItems: 'center', gap: 6, background: C.accent, color: '#fff', padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 500 } },
              h(LIcon, { name: 'upload', size: 14 }), file ? 'Reading ' + file.name + '…' : 'Choose File')))
      : h('div', { style: { display: 'flex', flexDirection: 'column', gap: 16 } },
          h('div', { style: { display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' } },
            h('div', { style: { fontSize: 13, color: C.text, fontWeight: 600, flex: 1, minWidth: 200 } }, file ? file.name : '',
              h('div', { style: { fontSize: 12, color: C.textMuted, fontWeight: 400 } }, body.length.toLocaleString() + ' rows below the header · ' + usable.toLocaleString() + ' with a cost or an identifier')),
            wb.SheetNames.length > 1 && h('div', null, h('div', { style: small }, 'Sheet'),
              h('select', { value: sheet, onChange: function(e) { loadSheet(wb, e.target.value); },
                style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '6px 8px', borderRadius: 6, fontSize: 13, outline: 'none' } },
                wb.SheetNames.map(function(n) { return h('option', { key: n, value: n }, n); }))),
            h('div', null, h('div', { style: small }, 'Header Row'),
              h('input', { type: 'number', min: 1, value: headerRow, onChange: function(e) { changeHeaderRow(parseInt(e.target.value, 10)); },
                style: { width: 70, background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '6px 8px', borderRadius: 6, fontSize: 13, outline: 'none' } })),
            h(Btn, { size: 'sm', variant: 'ghost', onClick: reset }, 'Choose Another File')),
          h('div', null,
            h('div', { style: { fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 4 } }, 'Columns'),
            h('div', { style: { fontSize: 12, color: C.textDim, marginBottom: 8 } }, 'Columns not mapped here are kept in the sheet as they are.'),
            h('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 } },
              PL_FIELDS.map(function(f) {
                var first = body.find(function(r) { return String(col(r, f.key) == null ? '' : col(r, f.key)).trim() !== ''; });
                return h('div', { key: f.key, style: { minWidth: 0 } },
                  h('div', { style: Object.assign({}, small, { color: f.key === 'cost' ? C.text : C.textMuted }) }, f.label + (f.key === 'cost' ? ' *' : '')),
                  sel(f.key),
                  h('div', { style: { fontSize: 11, color: C.textDim, marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }, first ? 'e.g. ' + String(col(first, f.key)) : ' '));
              }))),
          h('div', null,
            h('div', { style: { fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 8 } }, 'Preview'),
            h('div', { style: { overflowX: 'auto', border: '1px solid ' + C.border, borderRadius: 8 } },
              h('table', { style: { borderCollapse: 'collapse', width: '100%' } },
                h('thead', null, h('tr', null, previewCols.map(function(x) { return h('th', { key: x, style: th }, x); }))),
                h('tbody', null, body.slice(0, 6).map(function(r, i) {
                  var c = col(r, 'cost'), n = Number(String(c).replace(/[^0-9.\-]/g, ''));
                  var cells = [col(r, 'barcode'), col(r, 'vendor_sku'), col(r, 'description'), c].concat(nonUsd ? [isFinite(n) && String(c) !== '' ? plMoney(n * fx) : ''] : [], [col(r, 'case_pack') || terms.case_pack]);
                  return h('tr', { key: i }, cells.map(function(v, j) { return h('td', { key: j, style: td }, String(v == null ? '' : v)); }));
                }))))),
          h('div', null,
            h('div', { style: { fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 8 } }, 'Vendor And Terms'),
            h(PlTermsForm, { terms: terms, setTerms: setTerms, vendors: vendors, lockVendor: !!vendorId })),
          h('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } },
            h(Btn, { onClick: close, disabled: busy }, 'Cancel'),
            h(Btn, { variant: 'primary', onClick: submit, disabled: busy || !usable }, busy ? 'Merging…' : 'Merge ' + usable.toLocaleString() + ' Rows'))));
}

function plStatusPill(s) {
  var d = PL_STATUS[s]; if (!d) return null;
  return h('span', { title: d.hint, style: { display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, color: d.color, background: d.color + '1f', whiteSpace: 'nowrap' } }, d.label);
}
function plCount(n, color) { return h('span', { style: { color: n ? (color || C.text) : C.textDim, fontWeight: n ? 600 : 400 } }, (n || 0).toLocaleString()); }
function plChip(on, label, onClick, color, title) {
  return h('button', { key: label, onClick: onClick, title: title || '',
    style: { padding: '6px 12px', borderRadius: 6, border: '1px solid ' + (on ? 'transparent' : C.border), cursor: 'pointer', fontSize: 12, fontWeight: 500, fontFamily: 'inherit',
      background: on ? (color || C.accent) : C.surface, color: on ? '#fff' : (color || C.textMuted) } }, label);
}
// One-line cell capped at maxWidth; the full text shows in the app tooltip on mouse-over.
function plClip(text, maxWidth, color, fontSize) {
  if (text == null || text === '') return '';
  return h(Tooltip, { text: String(text) },
    h('span', { style: { display: 'block', maxWidth: maxWidth, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: color, fontSize: fontSize } }, String(text)));
}
function plOnLatest(r) { return r.source === 'manual' ? 'Added by hand' : r.on_latest === false ? 'Not on latest list' : 'On latest list'; }

// ── Tabulator, loaded on first use ──
var _plTabPromise = null;
function plLoadTabulator() {
  if (window.Tabulator) return Promise.resolve(window.Tabulator);
  if (_plTabPromise) return _plTabPromise;
  _plTabPromise = new Promise(function(resolve, reject) {
    if (!document.querySelector('link[data-tabulator]')) {
      var l = document.createElement('link'); l.rel = 'stylesheet'; l.href = PL_TABULATOR_CSS; l.setAttribute('data-tabulator', '1'); document.head.appendChild(l);
    }
    var s = document.createElement('script'); s.src = PL_TABULATOR_JS; s.crossOrigin = 'anonymous';
    s.onload = function() { window.Tabulator ? resolve(window.Tabulator) : reject(new Error('The sheet editor did not load')); };
    s.onerror = function() { _plTabPromise = null; reject(new Error('The sheet editor did not load - check the connection and reload')); };
    document.head.appendChild(s);
  });
  return _plTabPromise;
}
// Dark / light theme for the grid, from the app's colour tokens
function plGridCss() {
  return '.pl-grid.tabulator{background:' + C.surface + ';border:1px solid ' + C.border + ';border-radius:8px;font-size:13px;font-family:inherit;color:' + C.text + '}' +
    '.pl-grid .tabulator-header{background:' + C.bg + ';color:' + C.textMuted + ';border-bottom:1px solid ' + C.border + '}' +
    '.pl-grid .tabulator-header .tabulator-col{background:' + C.bg + ';border-right:1px solid ' + C.border + '80}' +
    '.pl-grid .tabulator-header .tabulator-col .tabulator-col-content{padding:7px 8px 6px}' +
    // titles: normal case, wrap instead of "…" (Anthony 10-04: "still looks ugly")
    '.pl-grid .tabulator-col-title{display:flex;align-items:flex-start;gap:4px;font-size:12px;font-weight:600;color:' + C.textMuted + ';text-transform:none;letter-spacing:0;white-space:normal;line-height:1.25;padding-right:12px}' +
    '.pl-grid .tabulator-col-title .tabulator-header-popup-button{order:2;margin-left:auto;padding:1px 0 0 2px}' +
    // sort arrows: small, shown on hover or when the column is sorted
    '.pl-grid .tabulator-header .tabulator-col .tabulator-col-content .tabulator-col-sorter{right:6px;opacity:0;transition:opacity .12s}' +
    '.pl-grid .tabulator-header .tabulator-col:hover .tabulator-col-content .tabulator-col-sorter,.pl-grid .tabulator-header .tabulator-col[aria-sort=ascending] .tabulator-col-content .tabulator-col-sorter,.pl-grid .tabulator-header .tabulator-col[aria-sort=descending] .tabulator-col-content .tabulator-col-sorter{opacity:1}' +
    '.pl-grid .tabulator-header .tabulator-col .tabulator-col-content .tabulator-col-sorter .tabulator-arrow{border-left-width:4px;border-right-width:4px;border-bottom-width:5px;border-top-width:0}' +
    '.pl-grid .tabulator-header .tabulator-col[aria-sort=descending] .tabulator-col-content .tabulator-col-sorter .tabulator-arrow{border-top-width:5px;border-bottom-width:0}' +
    '.pl-grid .tabulator-header .tabulator-col[aria-sort=ascending] .tabulator-col-content .tabulator-col-sorter .tabulator-arrow{border-bottom-color:' + C.accent + '}' +
    '.pl-grid .tabulator-header .tabulator-col[aria-sort=descending] .tabulator-col-content .tabulator-col-sorter .tabulator-arrow{border-top-color:' + C.accent + '}' +
    // Analyzer-style filter box: operator icon + value in one box
    // filter boxes sit on one line at the bottom of the header, whatever the title height
    '.pl-grid .tabulator-header .tabulator-col .tabulator-col-content{display:flex;flex-direction:column;flex:1 1 auto;box-sizing:border-box}' +
    '.pl-grid.tabulator .tabulator-header .tabulator-col .tabulator-header-filter{margin-top:auto;padding-top:6px}' + // must outrank Tabulator's margin-top:2px
    '.pl-grid .pl-hf{display:flex;align-items:center;height:24px;background:' + C.surface + ';border:1px solid transparent;border-radius:5px;overflow:hidden}' +
    '.pl-grid .pl-hf:hover{border-color:' + C.border + '}' +
    '.pl-grid .pl-hf:focus-within{border-color:' + C.accent + ';box-shadow:0 0 0 2px ' + C.accent + '22}' +
    '.pl-grid .pl-hf-btn{flex:0 0 22px;height:22px;display:inline-flex;align-items:center;justify-content:center;border:none;background:none;color:' + C.textDim + ';cursor:pointer;font-size:12px;font-family:inherit;padding:0}' +
    '.pl-grid .pl-hf-btn:hover{color:' + C.text + '}' +
    '.pl-grid .pl-hf-btn.on{color:' + C.accent + '}' +
    '.pl-grid .tabulator-header-filter .pl-hf input{flex:1 1 auto;min-width:0;width:100%;height:100% !important;align-self:stretch;cursor:text;border:none !important;background:transparent !important;padding:2px 4px 2px 0 !important;color:' + C.text + ';font-size:12px;outline:none;box-shadow:none}' +
    '.pl-grid .tabulator-header-filter .pl-hf input:disabled{color:' + C.textDim + '}' +
    '.pl-hf-menu{position:fixed;z-index:10000;min-width:210px;padding:4px;background:' + C.surface + ';border:1px solid ' + C.border + ';border-radius:8px;box-shadow:0 8px 24px rgba(0,0,0,.35);font-size:13px;color:' + C.text + '}' +
    '.pl-hf-item{display:flex;align-items:center;gap:8px;padding:6px 10px;border-radius:5px;cursor:pointer}' +
    '.pl-hf-item:hover{background:' + C.accent + '22}' +
    '.pl-hf-item.on{color:' + C.accent + ';font-weight:600}' +
    '.pl-hf-ic{width:18px;display:inline-flex;justify-content:center;color:' + C.textMuted + '}' +
    '.pl-hf-item.on .pl-hf-ic{color:' + C.accent + '}' +
    '.pl-hf-reset{border-top:1px solid ' + C.border + ';margin-top:4px;padding-top:7px}' +
    // funnel on list columns + its tick-list popup
    '.pl-grid .tabulator-header .tabulator-col .tabulator-header-popup-button{color:' + C.textDim + ';opacity:1;padding:0 4px}' +
    '.pl-grid .tabulator-header .tabulator-col .tabulator-header-popup-button:hover{color:' + C.text + ';opacity:1}' +
    '.pl-grid .tabulator-header .tabulator-col.pl-funnel-on .tabulator-header-popup-button{color:' + C.accent + '}' +
    '.tabulator-popup-container{background:' + C.surface + ';border:1px solid ' + C.border + ';border-radius:8px;box-shadow:0 8px 24px rgba(0,0,0,.35);color:' + C.text + '}' +
    '.pl-funnel{padding:8px;width:250px;font-size:12px}' +
    '.pl-funnel input[type=text],.pl-funnel > input{width:100%;box-sizing:border-box;background:' + C.bg + ';color:' + C.text + ';border:1px solid ' + C.border + ';border-radius:5px;padding:5px 8px;font-size:12px;outline:none}' +
    '.pl-funnel-bar{display:flex;gap:6px;margin:6px 0}' +
    '.pl-funnel-bar button{flex:1;background:' + C.bg + ';color:' + C.textMuted + ';border:1px solid ' + C.border + ';border-radius:5px;padding:4px;font-size:11px;cursor:pointer;font-family:inherit}' +
    '.pl-funnel-bar button:hover{color:' + C.text + '}' +
    '.pl-funnel-list{max-height:260px;overflow:auto}' +
    '.pl-funnel-list label{display:flex;align-items:center;gap:6px;padding:3px 2px;cursor:pointer;border-radius:4px}' +
    '.pl-funnel-list label:hover{background:' + C.accent + '18}' +
    '.pl-funnel-list label span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}' +
    '.pl-funnel-list label em{margin-left:auto;color:' + C.textDim + ';font-style:normal}' +
    '.pl-grid .tabulator-header-filter input{background:' + C.surface + ';color:' + C.text + ';border:1px solid transparent;border-radius:5px;padding:3px 6px;font-size:12px;outline:none}' +
    '.pl-grid .tabulator-tableholder{background:' + C.surface + '}' +
    '.pl-grid .tabulator-row{background:' + C.surface + ';color:' + C.text + ';border-bottom:1px solid ' + C.border + ';min-height:30px}' +
    '.pl-grid .tabulator-row.tabulator-row-even{background:' + C.surface + '}' +
    '.pl-grid .tabulator-row .tabulator-cell{border-right:1px solid ' + C.border + ';padding:5px 8px}' +
    '.pl-grid .tabulator-row .tabulator-cell.pl-ro{color:' + C.textMuted + '}' +
    // mouseover: faint tint on the row, stronger tint + thin outline on the cell (Anthony 10-03);
    // listed before the selection / editing rules so those keep priority
    // solid layers: Tabulator paints .tabulator-table white, so a see-through tint washed the row out
    '.pl-grid .tabulator-tableholder .tabulator-table{background:' + C.surface + '}' +
    '.pl-grid .tabulator-row:hover,.pl-grid .tabulator-row:hover .tabulator-frozen{background:linear-gradient(' + C.accent + '14,' + C.accent + '14),' + C.surface + '}' +
    '.pl-grid .tabulator-row .tabulator-cell:hover{background:' + C.accent + '1c}' + // hover = light fill only, never reads as a selection
    '.pl-grid .tabulator-row .tabulator-cell.tabulator-editing{border:2px solid ' + C.accent + ';background:' + C.bg + '}' +
    '.pl-grid .tabulator-row .tabulator-cell.tabulator-editing input,.pl-grid .tabulator-row .tabulator-cell.tabulator-editing select{background:' + C.bg + ';color:' + C.text + '}' +
    '.pl-grid .tabulator-row .tabulator-cell.tabulator-range-selected:not(.tabulator-range-only-cell-selected){background:' + C.accent + '26}' +
    '.pl-grid .tabulator-row .tabulator-cell.tabulator-range-only-cell-selected{background:' + C.accent + '4d;box-shadow:inset 0 0 0 2px ' + C.accent + '}' + // selected = strongest
    '.pl-grid .tabulator-row .tabulator-frozen,.pl-grid .tabulator-header .tabulator-frozen{background:' + C.bg + '}' +
    '.pl-grid .tabulator-row-header,.pl-grid .tabulator-row .tabulator-row-header{color:' + C.textDim + ';font-size:11px}' +
    // header hover: the app's faint tint, not Tabulator's #cdcdcd grey; sort arrows in theme colours (Anthony 10-03)
    '.pl-grid.tabulator .tabulator-header .tabulator-col.tabulator-sortable.tabulator-col-sorter-element:hover,' +
    '.pl-grid.tabulator .tabulator-header .tabulator-col:hover{background:linear-gradient(' + C.accent + '14,' + C.accent + '14),' + C.bg + ';color:' + C.text + '}' +
    '.pl-grid.tabulator .tabulator-header .tabulator-col.tabulator-sortable[aria-sort=none] .tabulator-col-content .tabulator-col-sorter .tabulator-arrow{border-bottom-color:' + C.textDim + '}' +
    '.pl-grid.tabulator .tabulator-header .tabulator-col.tabulator-sortable[aria-sort] .tabulator-col-content .tabulator-col-sorter.tabulator-col-sorter-element .tabulator-arrow:hover{border-bottom-color:' + C.text + ';border-top-color:' + C.text + '}' +
    // the selected cell's column header and row number take the app's highlight, not Tabulator's
    // grey #d6d6d6 / blue #3876ca (Anthony 10-03); a whole selected column / row is a stronger tint
    '.pl-grid .tabulator-header .tabulator-col.tabulator-range-highlight,' +
    '.pl-grid .tabulator-row.tabulator-range-highlight .tabulator-cell.tabulator-range-row-header{background:linear-gradient(' + C.accent + '24,' + C.accent + '24),' + C.bg + ';color:' + C.text + '}' +
    '.pl-grid .tabulator-header .tabulator-col.tabulator-range-selected,' +
    '.pl-grid .tabulator-row.tabulator-range-selected .tabulator-cell.tabulator-range-row-header{background:linear-gradient(' + C.accent + '40,' + C.accent + '40),' + C.bg + ';color:' + C.text + '}' +
    // Tabulator's own range / active-cell boxes sit 2px lower than the cell (a double line under the
    // selected cell, Anthony 10-03): hidden, the cell's inset outline is the only border
    '.pl-grid .tabulator-tableholder .tabulator-range-overlay .tabulator-range,' +
    '.pl-grid .tabulator-tableholder .tabulator-range-overlay .tabulator-range-cell-active{border:none}' +
    '.pl-grid .tabulator-range-fill-handle{background-color:' + C.accent + '}' +
    '.pl-grid .tabulator-footer{background:' + C.bg + ';color:' + C.textMuted + ';border-top:1px solid ' + C.border + '}' +
    // page footer (Anthony 10-03: show the chosen 25-800 rows, page through the rest)
    '.pl-grid .tabulator-footer .tabulator-paginator{color:' + C.textMuted + ';font-size:12px}' +
    '.pl-grid .tabulator-footer .tabulator-page{background:' + C.surface + ';color:' + C.textMuted + ';border:1px solid ' + C.border + ';border-radius:5px;margin:0 2px;padding:3px 9px;font-size:12px}' +
    '.pl-grid .tabulator-footer .tabulator-page:not(:disabled):hover{background:' + C.accent + '24;color:' + C.text + '}' +
    '.pl-grid .tabulator-footer .tabulator-page.active{background:' + C.accent + ';border-color:' + C.accent + ';color:#fff}' +
    '.pl-grid .tabulator-footer .tabulator-page:disabled{opacity:.45}' +
    '.pl-grid .tabulator-footer .tabulator-page-size{background:' + C.bg + ';color:' + C.text + ';border:1px solid ' + C.border + ';border-radius:5px;padding:2px 6px;margin:0 6px}' +
    '.pl-grid .tabulator-footer .tabulator-page-counter{color:' + C.textMuted + ';font-size:12px}' +
    '.tabulator-tooltip{background:' + C.surface + ';color:' + C.text + ';border:1px solid ' + C.border + ';border-radius:6px;padding:6px 9px;font-size:12px;max-width:520px;box-shadow:0 4px 14px rgba(0,0,0,.35)}' +
    '.tabulator-menu{background:' + C.surface + ';color:' + C.text + ';border:1px solid ' + C.border + ';border-radius:6px;font-size:13px}' +
    '.tabulator-menu .tabulator-menu-item:not(.tabulator-menu-item-disabled):hover{background:' + C.accent + '33}' +
    '.tabulator-edit-list{background:' + C.surface + ';color:' + C.text + ';border:1px solid ' + C.border + '}' +
    '.tabulator-edit-list .tabulator-edit-list-item.active,.tabulator-edit-list .tabulator-edit-list-item:hover{background:' + C.accent + ';color:#fff}';
}

// Every column gets a fixed starting width (a width saved by dragging wins). Leaving it to the grid's
// fit-to-content gave every unmeasured column the full table width on the ACD sheet (10-03).
function plDefaultWidth(c) {
  if (c.key === 'description') return 300;
  if (c.key === 'vendor_sku' || c.key === 'barcode' || c.key === 'asin') return 140;
  if (c.type === 'money' || c.type === 'number' || c.type === 'int') return 100;
  if (c.type === 'checkbox') return 90;
  if (c.type === 'date') return 110;
  return 160;
}

// ── Column filters, Analyzer-style (Anthony 10-04: "analyzer has the filters inside of the actual box") ──
// Each filter-row box has an operator icon at its left; clicking it opens a menu (Contains, Does not contain,
// Starts with, Ends with, Equals, … / Less than, Greater than, Between …). The value is typed in the same box
// (numbers also take ">20", "<=5", "10-12"). List columns get a funnel on the title with a tick-list of values.
// A header value is "op<US>value".
var PL_HF_SEP = '\u001f';
var PL_HF_MAG = '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>';
var PL_HF_FUNNEL = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linejoin="round"><path d="M3 4h18l-7 8.5V19l-4 2v-8.5z"/></svg>';
var PL_HF_OPS = {
  text: [['contains', PL_HF_MAG, 'Contains'], ['not', '∌', 'Does not contain'], ['starts', 'a…', 'Starts with'], ['ends', '…a', 'Ends with'],
    ['equals', '=', 'Equals'], ['ne', '≠', 'Does not equal'], ['empty', '∅', 'Is blank'], ['notempty', '✱', 'Is not blank']],
  number: [['eq', '=', 'Equals'], ['ne', '≠', 'Does not equal'], ['lt', '<', 'Less than'], ['gt', '>', 'Greater than'],
    ['lte', '≤', 'Less than or equal to'], ['gte', '≥', 'Greater than or equal to'], ['between', '↔', 'Between'], ['empty', '∅', 'Is blank'], ['notempty', '✱', 'Is not blank']],
  date: [['on', '=', 'Equals'], ['before', '<', 'Before'], ['after', '>', 'After'], ['between', '↔', 'Between'], ['empty', '∅', 'Is blank'], ['notempty', '✱', 'Is not blank']],
};
var PL_HF_LABEL = {}; Object.keys(PL_HF_OPS).forEach(function(k) { PL_HF_OPS[k].forEach(function(o) { PL_HF_LABEL[k + ':' + o[0]] = o[2]; }); });

// one open menu at a time, closed by a click outside or Esc
var _plHfMenu = null;
function plHfCloseMenu() { if (_plHfMenu) { _plHfMenu.remove(); _plHfMenu = null; document.removeEventListener('mousedown', _plHfOutside, true); document.removeEventListener('keydown', _plHfEsc, true); } }
function _plHfOutside(e) { if (_plHfMenu && !_plHfMenu.contains(e.target)) plHfCloseMenu(); }
function _plHfEsc(e) { if (e.key === 'Escape') { e.stopPropagation(); plHfCloseMenu(); } }
function plHfOpenMenu(anchor, items, current, onPick) {
  plHfCloseMenu();
  var z = window.APP_ZOOM || 1, r = anchor.getBoundingClientRect();
  var m = document.createElement('div'); m.className = 'pl-hf-menu';
  m.style.left = (r.left / z) + 'px'; m.style.top = ((r.bottom + 4) / z) + 'px';
  items.forEach(function(o) {
    var b = document.createElement('div'); b.className = 'pl-hf-item' + (o[0] === current ? ' on' : '');
    b.innerHTML = '<span class="pl-hf-ic">' + o[1] + '</span><span>' + o[2] + '</span>';
    b.addEventListener('mousedown', function(e) { e.preventDefault(); e.stopPropagation(); plHfCloseMenu(); onPick(o[0]); });
    m.appendChild(b);
  });
  var reset = document.createElement('div'); reset.className = 'pl-hf-item pl-hf-reset';
  reset.innerHTML = '<span class="pl-hf-ic">⟲</span><span>Reset</span>';
  reset.addEventListener('mousedown', function(e) { e.preventDefault(); e.stopPropagation(); plHfCloseMenu(); onPick(null); });
  m.appendChild(reset);
  document.body.appendChild(m); _plHfMenu = m;
  setTimeout(function() { document.addEventListener('mousedown', _plHfOutside, true); document.addEventListener('keydown', _plHfEsc, true); }, 0);
}

function plHfEditor(kind) {
  var ops = PL_HF_OPS[kind], def = ops[0][0];
  return function(cell, onRendered, success) {
    var op = def, timer = null;
    var wrap = document.createElement('div'); wrap.className = 'pl-hf';
    var btn = document.createElement('button'); btn.type = 'button'; btn.className = 'pl-hf-btn';
    var inp = document.createElement('input'); inp.type = 'text';
    var paint = function() {
      var o = ops.find(function(x) { return x[0] === op; }) || ops[0];
      var active = op !== def || inp.value.trim() !== '';
      btn.innerHTML = op === def ? PL_HF_MAG : o[1]; btn.title = o[2] + ' (click to change)';
      btn.classList.toggle('on', active);
      var noVal = op === 'empty' || op === 'notempty';
      inp.disabled = noVal; inp.placeholder = noVal ? o[2] : op === 'between' ? (kind === 'date' ? 'YYYY-MM-DD - YYYY-MM-DD' : 'from - to') : kind === 'date' ? 'YYYY-MM-DD' : '';
      if (noVal) inp.value = '';
    };
    var fire = function() {
      var v = inp.value.trim(), noVal = op === 'empty' || op === 'notempty';
      // a typed ">10", "<=5", "<>3" or "6-8" becomes the box's rule, so the icon and the filter bar say what it does
      if (kind === 'number' && v && !noVal) {
        var tm = v.match(/^(>=|<=|≥|≤|<>|!=|≠|>|<|=)\s*(.*)$/);
        if (tm) {
          op = { '>=': 'gte', '≥': 'gte', '<=': 'lte', '≤': 'lte', '>': 'gt', '<': 'lt', '=': 'eq', '<>': 'ne', '!=': 'ne', '≠': 'ne' }[tm[1]];
          v = tm[2].trim(); inp.value = v;
          if (v === '') { paint(); return; } // only the operator so far: wait for the number
        } else if (/^\s*-?[\d.,$]+\s*(?:-|to|\.\.)\s*-?[\d.,$]+\s*%?$/.test(v) && !/^-?[\d.,$]+$/.test(v)) op = 'between';
      }
      paint();
      success(noVal ? op + PL_HF_SEP : (v === '' ? '' : op + PL_HF_SEP + v));
    };
    btn.addEventListener('mousedown', function(e) { e.preventDefault(); e.stopPropagation(); });
    btn.addEventListener('click', function(e) {
      e.stopPropagation();
      plHfOpenMenu(btn, ops, op, function(picked) {
        if (picked == null) { op = def; inp.value = ''; } else op = picked;
        fire(); if (!inp.disabled) inp.focus();
      });
    });
    inp.addEventListener('input', function() { clearTimeout(timer); timer = setTimeout(fire, 250); });
    inp.addEventListener('keydown', function(e) { e.stopPropagation(); if (e.key === 'Enter') { clearTimeout(timer); fire(); } });
    ['mousedown', 'click'].forEach(function(ev) { inp.addEventListener(ev, function(e) { e.stopPropagation(); }); });
    // A click anywhere on the box (not just the text) puts the cursor in it; otherwise Tabulator took the click as a
    // column-header click, kept the focus, and a paste never reached the box (Anthony 10-04: "when I paste it resets")
    wrap.addEventListener('mousedown', function(e) { e.stopPropagation(); if (e.target !== inp && e.target !== btn && !btn.contains(e.target)) { e.preventDefault(); if (!inp.disabled) inp.focus(); } });
    ['click', 'paste', 'copy', 'cut'].forEach(function(ev) { wrap.addEventListener(ev, function(e) { e.stopPropagation(); }); });
    // Clear (filter bar) sets the header value back to '' -> show the default operator again
    onRendered(function() {
      // the strip around the box belongs to it too
      var holder = wrap.parentElement;
      if (holder && !holder._plHf) { holder._plHf = true; holder.addEventListener('mousedown', function(e) { if (e.target === holder) { e.preventDefault(); e.stopPropagation(); if (!inp.disabled) inp.focus(); } }); }
      var v = cell.getValue(); if (v) { var p = String(v).split(PL_HF_SEP); op = p[0] || def; inp.value = p[1] || ''; } paint(); });
    wrap.appendChild(btn); wrap.appendChild(inp);
    paint();
    return wrap;
  };
}
function plHfNum(v) { if (v == null || v === '') return NaN; var s = String(v).replace(/[^0-9.\-]/g, ''); return s === '' || s === '-' || s === '.' ? NaN : Number(s); }
function plHfMatch(kind, opts) {
  var pct = opts && opts.pct;
  return function(hv, rv) {
    var parts = String(hv).split(PL_HF_SEP), op = parts[0], v = (parts[1] || '').trim();
    if (Array.isArray(rv)) rv = rv.join(' · ');
    var s = rv == null ? '' : String(rv);
    if (op === 'empty') return s.trim() === '';
    if (op === 'notempty') return s.trim() !== '';
    if (kind === 'text') {
      var a = s.toLowerCase(), b = v.toLowerCase();
      if (op === 'equals') return a === b;
      if (op === 'ne') return a !== b;
      if (op === 'starts') return a.indexOf(b) === 0;
      if (op === 'ends') return b === '' || a.slice(-b.length) === b;
      if (op === 'not') return a.indexOf(b) === -1;
      return a.indexOf(b) !== -1;
    }
    if (kind === 'date') {
      var d = s.slice(0, 10); if (!d) return false;
      var rg = v.match(/^\s*(\d{4}-\d{2}-\d{2})\s*(?:-|to|\.\.)\s*(\d{4}-\d{2}-\d{2})\s*$/);
      if (op === 'between' || rg) return !!rg && d >= rg[1] && d <= rg[2];
      return op === 'before' ? d < v : op === 'after' ? d > v : d === v;
    }
    // number: a typed operator or range wins over the menu
    var n = plHfNum(rv); if (isNaN(n)) return false;
    if (pct) n = Math.round(n * 10000) / 100; // stored as a fraction, shown and typed as %
    var m = v.match(/^(>=|<=|≥|≤|<>|!=|≠|>|<|=)\s*(.+)$/), r = v.match(/^\s*(-?[\d.,$]+)\s*(?:-|to|\.\.)\s*(-?[\d.,$]+)\s*%?$/);
    if (r) { var lo = plHfNum(r[1]), hi = plHfNum(r[2]); return !isNaN(lo) && !isNaN(hi) && n >= Math.min(lo, hi) && n <= Math.max(lo, hi); }
    if (op === 'between') return false; // between needs "from - to"
    if (m) { op = { '>=': 'gte', '≥': 'gte', '<=': 'lte', '≤': 'lte', '>': 'gt', '<': 'lt', '=': 'eq', '<>': 'ne', '!=': 'ne', '≠': 'ne' }[m[1]]; v = m[2]; }
    var x = plHfNum(v); if (isNaN(x)) return false;
    return op === 'gt' ? n > x : op === 'gte' ? n >= x : op === 'lt' ? n < x : op === 'lte' ? n <= x : op === 'ne' ? n !== x : n === x;
  };
}
// Column-definition fragment for one filter kind ('list' = a contains box + the funnel tick-list on the title)
function plHf(kind, opts) {
  if (kind === 'checkbox') return { headerFilter: 'tickCross', headerFilterParams: { tristate: true }, headerFilterFunc: '=' };
  if (kind === 'none') return { headerFilter: false };
  var k = kind === 'list' ? 'text' : kind;
  // headerFilterLiveFilter false: Tabulator's own keyup listener read the box's (empty) value and cleared the filter
  // when the user let go of Ctrl after Ctrl+V (Anthony 10-04: pasting showed every row); our editor sets the value itself
  var out = { headerFilter: plHfEditor(k), headerFilterFunc: plHfMatch(k, opts), headerFilterLiveFilter: false, plHfKind: k };
  if (kind === 'list' && opts && opts.funnel) Object.assign(out, opts.funnel);
  return out;
}

// ── Funnel tick-lists. Selections live in funnels[field] = [values]; the grid's own filter checks them. ──
function plFunnelPass(row, funnels) {
  var ks = Object.keys(funnels || {});
  for (var i = 0; i < ks.length; i++) {
    var sel = funnels[ks[i]]; if (!sel || !sel.length) continue;
    var v = row[ks[i]]; v = v == null || v === '' ? '(Blanks)' : String(v);
    if (sel.indexOf(v) === -1) return false;
  }
  return true;
}
// getTable(): the Tabulator; funnelsRef: { current: {} }; onChange(): refresh filter + summary
function plFunnel(getTable, funnelsRef, onChange) {
  return {
    headerPopupIcon: PL_HF_FUNNEL,
    headerPopup: function(e, column) {
      var t = getTable(), field = column.getField();
      var counts = {};
      (t ? t.getData() : []).forEach(function(r) { var v = r[field]; v = v == null || v === '' ? '(Blanks)' : String(v); counts[v] = (counts[v] || 0) + 1; });
      var values = Object.keys(counts).sort(function(a, b) { return a === '(Blanks)' ? 1 : b === '(Blanks)' ? -1 : a.localeCompare(b); });
      var cur = (funnelsRef.current[field] || []).slice();
      var box = document.createElement('div'); box.className = 'pl-funnel';
      var search = document.createElement('input'); search.placeholder = 'Search ' + values.length + ' values…';
      var list = document.createElement('div'); list.className = 'pl-funnel-list';
      var apply = function() { if (cur.length) funnelsRef.current[field] = cur.slice(); else delete funnelsRef.current[field]; onChange(); };
      var draw = function() {
        var q = search.value.trim().toLowerCase(); list.innerHTML = '';
        values.filter(function(v) { return !q || v.toLowerCase().indexOf(q) !== -1; }).slice(0, 500).forEach(function(v) {
          var lab = document.createElement('label'); var cb = document.createElement('input'); cb.type = 'checkbox'; cb.checked = cur.indexOf(v) !== -1;
          cb.addEventListener('change', function() { if (cb.checked) cur.push(v); else cur = cur.filter(function(x) { return x !== v; }); apply(); });
          var tx = document.createElement('span'); tx.textContent = v; var ct = document.createElement('em'); ct.textContent = counts[v];
          lab.appendChild(cb); lab.appendChild(tx); lab.appendChild(ct); list.appendChild(lab);
        });
      };
      var bar = document.createElement('div'); bar.className = 'pl-funnel-bar';
      var all = document.createElement('button'); all.type = 'button'; all.textContent = 'Select All Shown';
      all.addEventListener('click', function() { var q = search.value.trim().toLowerCase(); values.forEach(function(v) { if ((!q || v.toLowerCase().indexOf(q) !== -1) && cur.indexOf(v) === -1) cur.push(v); }); apply(); draw(); });
      var none = document.createElement('button'); none.type = 'button'; none.textContent = 'Clear';
      none.addEventListener('click', function() { cur = []; apply(); draw(); });
      bar.appendChild(all); bar.appendChild(none);
      search.addEventListener('input', draw); search.addEventListener('keydown', function(ev) { ev.stopPropagation(); });
      box.addEventListener('mousedown', function(ev) { ev.stopPropagation(); });
      box.appendChild(search); box.appendChild(bar); box.appendChild(list);
      draw();
      return box;
    },
  };
}
// Mark funnel icons of filtered columns (blue)
function plFunnelPaint(t, funnels) {
  if (!t) return;
  t.getColumns().forEach(function(c) { var el = c.getElement(), f = c.getField(); if (el && f) el.classList.toggle('pl-funnel-on', !!(funnels[f] && funnels[f].length)); });
}
// "[Cost] Is greater than '20' and [Brand] in (A, B)" for the filter bar
function plFilterSummary(t, funnels) {
  if (!t) return '';
  var parts = [];
  t.getHeaderFilters().forEach(function(hf) {
    var col = t.getColumn(hf.field); if (!col) return;
    var d = col.getDefinition(), title = d.title, kind = d.plHfKind;
    if (typeof hf.value === 'boolean') { parts.push('[' + title + '] is ' + (hf.value ? 'checked' : 'not checked')); return; }
    var p = String(hf.value).split(PL_HF_SEP), lab = PL_HF_LABEL[(kind || 'text') + ':' + p[0]] || p[0];
    parts.push('[' + title + '] ' + lab + (p[1] ? " '" + p[1] + "'" : ''));
  });
  Object.keys(funnels || {}).forEach(function(f) {
    var sel = funnels[f]; if (!sel || !sel.length) return;
    var col = t.getColumn(f), title = col ? col.getDefinition().title : f;
    parts.push('[' + title + '] in (' + sel.slice(0, 3).join(', ') + (sel.length > 3 ? ', +' + (sel.length - 3) : '') + ')');
  });
  return parts.join(' and ');
}
function PlFilterBar({ summary, onClear }) {
  return h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, minHeight: 28, padding: '4px 10px', border: '1px solid ' + C.border, borderTop: 'none', borderRadius: '0 0 8px 8px', background: C.bg, fontSize: 12 } },
    h('span', { style: { color: summary ? C.accent : C.textDim, display: 'inline-flex' }, dangerouslySetInnerHTML: { __html: PL_HF_FUNNEL } }),
    h('span', { style: { flex: 1, color: summary ? C.text : C.textDim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }, title: summary || '' }, summary || 'No filters'),
    summary && h('button', { onClick: onClear, style: { background: 'none', border: 'none', color: C.accent, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit', fontWeight: 600 } }, 'Clear'));
}

// One normalized shape for comparing a cell before / after (null, '', false and undefined are all "empty").
function plNorm(v) { return v == null || v === '' || v === false ? '' : String(v); }

// ── One vendor's sheet ──
function PlSheet({ vendorId, showToast, onNavigate, canWrite, canDelete, vendors }) {
  var _d = useState(null), data = _d[0], setData = _d[1];
  var _err = useState(null), loadErr = _err[0], setLoadErr = _err[1];
  var _tab = useState('sheet'), tab = _tab[0], setTab = _tab[1];
  var _f = useState('all'), filter = _f[0], setFilter = _f[1];
  var _q = useState(''), search = _q[0], setSearch = _q[1];
  var _up = useState(false), upOpen = _up[0], setUpOpen = _up[1];
  var _hist = useState(null), hist = _hist[0], setHist = _hist[1];
  var _addCol = useState(null), addCol = _addCol[0], setAddCol = _addCol[1];
  var _cols = useState(false), colsOpen = _cols[0], setColsOpen = _cols[1];
  var _terms = useState(null), editTerms = _terms[0], setEditTerms = _terms[1];
  var _saving = useState(0), saving = _saving[0], setSaving = _saving[1];
  var _sel = useState(0), selRows = _sel[0], setSelRows = _sel[1];
  var _gv = useState(0), gridVer = _gv[0], setGridVer = _gv[1];
  var _fs = useState(''), filterSummary = _fs[0], setFilterSummary = _fs[1];
  var gridEl = useRef(null), tableRef = useRef(null), baseRef = useRef(new Map()), colsRef = useRef([]), funnelsRef = useRef({});
  var undoRef = useRef([]), redoRef = useRef([]), syncQ = useRef(Promise.resolve()), pendingSrc = useRef(null), syncTimer = useRef(null);

  var load = function() {
    return api('/research/data/vendors/' + vendorId).then(function(d) { setData(d); setLoadErr(null); return d; })
      .catch(function(e) { setLoadErr(e.message); });
  };
  useEffect(function() { load(); }, [vendorId]);

  var editableKeys = function() { return colsRef.current.map(function(c) { return c.key; }); };
  var pick = function(row) { var o = {}; editableKeys().forEach(function(k) { o[k] = row[k]; }); return o; };

  // Send every cell that differs from the last saved copy. Covers typing, paste, fill, clear, undo, redo.
  var syncNow = function(source) {
    var t = tableRef.current; if (!t) return;
    var edits = [], keys = editableKeys(), base = baseRef.current;
    t.getData().forEach(function(r) {
      var b = base.get(r.id); if (!b) return;
      keys.forEach(function(k) { if (plNorm(r[k]) !== plNorm(b[k])) edits.push({ id: r.id, key: k, value: r[k] == null ? null : r[k], old: b[k] == null ? null : b[k] }); });
    });
    if (!edits.length) return;
    // mark as in flight so a second change while saving only sends the new cells
    edits.forEach(function(e) { var b = base.get(e.id); b[e.key] = e.value; });
    setSaving(function(n) { return n + 1; });
    syncQ.current = syncQ.current.then(function() {
      return api('/research/data/vendors/' + vendorId + '/edits', { method: 'POST', body: JSON.stringify({ source: source, edits: edits.map(function(e) { return { id: e.id, key: e.key, value: e.value }; }) }) })
        .then(function(r) {
          var bad = {}; (r.errors || []).forEach(function(x) { bad[x.id + '|' + x.key] = x.error; });
          var revert = {};
          edits.forEach(function(e) { if (bad[e.id + '|' + e.key] !== undefined) { (revert[e.id] = revert[e.id] || { id: e.id })[e.key] = e.old; base.get(e.id)[e.key] = e.old; } });
          (r.rows || []).forEach(function(row) { base.set(row.id, pick(row)); });
          if (r.rows && r.rows.length) t.updateData(r.rows);
          if (Object.keys(revert).length) t.updateData(Object.values(revert));
          var good = edits.filter(function(e) { return bad[e.id + '|' + e.key] === undefined; });
          if (good.length) {
            var batch = good.map(function(e) { return { id: e.id, key: e.key, old: e.old, value: e.value }; });
            if (source === 'undo') redoRef.current.push(batch);
            else if (source === 'redo') undoRef.current.push(batch);
            else { undoRef.current.push(batch); redoRef.current = []; if (undoRef.current.length > 100) undoRef.current.shift(); }
          }
          if (r.errors && r.errors.length) showToast(r.errors.length + ' cell' + (r.errors.length === 1 ? '' : 's') + ' not saved: ' + r.errors[0].error + (r.errors.length > 1 ? ' …' : ''), 'error');
        })
        .catch(function(e) {
          var revert = {};
          edits.forEach(function(x) { (revert[x.id] = revert[x.id] || { id: x.id })[x.key] = x.old; base.get(x.id)[x.key] = x.old; });
          t.updateData(Object.values(revert));
          showToast('Not saved: ' + e.message, 'error');
        })
        .finally(function() { setSaving(function(n) { return n - 1; }); });
    });
  };
  var scheduleSync = function(source) {
    if (!pendingSrc.current || source !== 'edit') pendingSrc.current = source; // paste / fill / clear name the change, not the per-cell edit events
    clearTimeout(syncTimer.current);
    syncTimer.current = setTimeout(function() { var s = pendingSrc.current || 'edit'; pendingSrc.current = null; syncNow(s); }, 30);
  };
  var applyBatch = function(batch, useOld, source) {
    var t = tableRef.current; if (!t || !batch) return;
    var rows = {};
    batch.forEach(function(e) { if (!baseRef.current.has(e.id)) return; (rows[e.id] = rows[e.id] || { id: e.id })[e.key] = useOld ? e.old : e.value; });
    if (!Object.keys(rows).length) return;
    t.updateData(Object.values(rows)).then(function() { syncNow(source); });
  };
  // Each saved change is { old, value }. Undo puts back old; the undo's own save is pushed on the redo
  // stack as { old: what was undone, value: restored }, so redo also puts back old.
  var undo = function() { applyBatch(undoRef.current.pop(), true, 'undo'); };
  var redo = function() { applyBatch(redoRef.current.pop(), true, 'redo'); };
  var fillDown = function() {
    var t = tableRef.current; if (!t) return;
    var ranges = t.getRanges ? t.getRanges() : [];
    ranges.forEach(function(rg) {
      var data = rg.getData(); if (data.length < 2) return;
      var top = data[0];
      rg.getRows().slice(1).forEach(function(row) {
        var upd = { id: row.getData().id };
        rg.getColumns().forEach(function(c) { var f = c.getField(); if (f && editableKeys().indexOf(f) !== -1) upd[f] = top[f]; });
        t.updateData([upd]);
      });
    });
    scheduleSync('fill');
  };
  var selectedRowIds = function() {
    var t = tableRef.current; if (!t || !t.getRanges) return [];
    var ids = {};
    t.getRanges().forEach(function(rg) { rg.getRows().forEach(function(r) { ids[r.getData().id] = true; }); });
    return Object.keys(ids).map(Number);
  };

  // Column definitions from the sheet's registry + read-only system columns
  var onFunnel = function() { var t = tableRef.current; if (!t) return; t.refreshFilter(); plFunnelPaint(t, funnelsRef.current); setFilterSummary(plFilterSummary(t, funnelsRef.current)); };
  var clearFilters = function() { var t = tableRef.current; if (!t) return; funnelsRef.current = {}; t.clearHeaderFilter(); t.refreshFilter(); plFunnelPaint(t, {}); setFilterSummary(''); };
  var buildColumns = function(cols) {
    var funnel = plFunnel(function() { return tableRef.current; }, funnelsRef, onFunnel);
    var lists = (data && data.lists) || [];
    var anyForeign = (data && data.items || []).some(function(r) { return r.currency && r.currency !== 'USD'; });
    var editorFor = function(c) {
      if (!canWrite) return false;
      if (c.type === 'checkbox') return 'tickCross';
      if (c.type === 'select') return 'list';
      return 'input';
    };
    // Only the row-number header is frozen: Tabulator's range selection misbehaves with other frozen columns
    var out = [{ title: '', field: '_hist', width: 34, headerSort: false, download: false, resizable: false, hozAlign: 'center', cssClass: 'pl-ro',
      formatter: function() { return '<span title="Row history" style="cursor:pointer;opacity:.6">&#9776;</span>'; },
      cellClick: function(e, cell) { openHistory(cell.getRow().getData()); } }];
    cols.forEach(function(c) {
      if (c.hidden) return;
      var def = { title: c.label, field: c.key, width: c.width || plDefaultWidth(c), minWidth: 60,
        editor: editorFor(c), headerSortClickElement: 'icon', headerFilterPlaceholder: '',
        headerMenu: canWrite ? function() { return columnMenu(c); } : undefined };
      Object.assign(def, plHf(c.type === 'checkbox' ? 'checkbox' : c.type === 'select' || c.key === 'brand' ? 'list'
        : c.type === 'money' || c.type === 'number' || c.type === 'int' ? 'number' : c.type === 'date' ? 'date' : 'text', { funnel: funnel }));
      if (c.type === 'money') def.formatter = function(cell) { var v = cell.getValue(); var n = Number(v); return v == null || v === '' ? '' : isFinite(n) ? '$' + n.toFixed(2) : String(v); };
      if (c.type === 'money' || c.type === 'number' || c.type === 'int') def.hozAlign = 'right';
      if (c.type === 'checkbox') { def.formatter = 'tickCross'; def.formatterParams = { allowEmpty: true, crossElement: false }; def.hozAlign = 'center'; }
      if (c.type === 'select') def.editorParams = { values: c.options || [], autocomplete: true, freetext: true, listOnEmpty: true };
      if (c.key === 'barcode') def.formatter = function(cell) { var r = cell.getRow().getData(); var v = cell.getValue(); return v && !r.barcode_valid ? '<span style="color:' + C.warning + '" title="Not an 8-14 digit barcode">' + v + '</span>' : (v || ''); };
      if (c.kind === 'custom') def.cssClass = 'pl-custom';
      out.push(def);
    });
    var ro = function(title, field, fmt, width, kind) { return Object.assign({ title: title, field: field, width: width, editor: false, headerSortClickElement: 'icon', cssClass: 'pl-ro', formatter: fmt }, plHf(kind || 'text', { funnel: funnel })); };
    if (anyForeign) out.push(ro('List Cost', 'cost_raw', function(cell) { var r = cell.getRow().getData(); return r.cost_raw == null ? '' : Number(r.cost_raw).toFixed(2) + ' ' + (r.currency || ''); }, 120, 'number'));
    out.push(ro('Previous Cost', 'prev_cost', function(cell) {
      var r = cell.getRow().getData(); if (r.prev_cost == null) return '';
      var d = r.cost != null && r.prev_cost > 0 ? r.cost / r.prev_cost - 1 : null;
      return '$' + Number(r.prev_cost).toFixed(2) + (d != null && Math.abs(d) > 0.005 ? ' <span style="color:' + (d > 0 ? C.danger : C.success) + '">' + plPct(d) + '</span>' : '');
    }, 140, 'number'));
    out.push(Object.assign(ro('Status', '_latest', function(cell) { return plOnLatest(cell.getRow().getData()); }, 150, 'list'), { mutator: function(v, r) { return plOnLatest(r); } }));
    out.push(ro('Last Seen', 'last_seen_at', function(cell) { return plDate(cell.getValue()); }, 110, 'date'));
    out.push(Object.assign(ro('Flags', 'flags', function(cell) { var f = cell.getValue() || []; return f.length ? '<span style="color:' + C.warning + '">' + f.join(' · ') + '</span>' : ''; }, 260, 'text'), { headerSort: false }));
    return out;
  };
  var columnMenu = function(c) {
    var items = [{ label: 'Hide Column', action: function() { patchColumn(c.key, { hidden: true }); } }];
    if (c.kind !== 'standard') items.push({ label: 'Rename…', action: function() {
      appPrompt('New name for "' + c.label + '"', c.label, { title: 'Rename Column' }).then(function(v) { if (v && v.trim()) patchColumn(c.key, { label: v.trim() }); });
    } });
    if (c.kind === 'custom') {
      items.push({ label: 'Change Type…', action: function() { setAddCol({ edit: c, label: c.label, type: c.type, options: (c.options || []).join(', ') }); } });
      if (canDelete) items.push({ label: 'Delete Column', action: function() {
        appConfirm('Delete the column "' + c.label + '" and everything typed in it?', { title: 'Delete Column', okLabel: 'Delete', danger: true }).then(function(ok) {
          if (!ok) return;
          api('/research/data/vendors/' + vendorId + '/columns/' + encodeURIComponent(c.key), { method: 'DELETE' })
            .then(function(r) { setData(function(d) { return Object.assign({}, d, { columns: r.columns }); }); showToast('Column deleted'); })
            .catch(function(e) { showToast(e.message, 'error'); });
        });
      } });
    }
    return items;
  };
  var patchColumn = function(key, body) {
    return api('/research/data/vendors/' + vendorId + '/columns/' + encodeURIComponent(key), { method: 'PATCH', body: JSON.stringify(body) })
      .then(function(r) { setData(function(d) { return Object.assign({}, d, { columns: r.columns }); }); })
      .catch(function(e) { showToast(e.message, 'error'); });
  };

  // Build the grid once the data is in; rebuild columns when the registry changes
  useEffect(function() {
    if (!data || tab !== 'sheet' || !gridEl.current) return;
    var cancelled = false;
    colsRef.current = data.columns;
    plLoadTabulator().then(function(Tabulator) {
      if (cancelled) return;
      if (!document.getElementById('pl-grid-css')) { var st = document.createElement('style'); st.id = 'pl-grid-css'; document.head.appendChild(st); }
      document.getElementById('pl-grid-css').textContent = plGridCss();
      if (tableRef.current) { tableRef.current.setColumns(buildColumns(data.columns)); return; }
      var base = new Map(); data.items.forEach(function(r) { base.set(r.id, pick(r)); }); baseRef.current = base;
      var t = new Tabulator(gridEl.current, {
        data: data.items, index: 'id', columns: buildColumns(data.columns), height: 'calc(100vh - 322px)', layout: 'fitData',
        placeholder: 'No rows yet. Upload a price list or add rows.',
        rowHeader: { formatter: 'rownum', headerSort: false, hozAlign: 'center', resizable: false, frozen: true, width: 46 },
        debugInvalidOptions: false, selectableRange: true, selectableRangeColumns: true, selectableRangeRows: true, selectableRangeClearCells: canWrite,
        editTriggerEvent: 'dblclick', clipboard: true, clipboardCopyStyled: false, clipboardCopyConfig: { rowHeaders: false, columnHeaders: false },
        clipboardCopyRowRange: 'range', clipboardPasteParser: 'range', clipboardPasteAction: canWrite ? 'range' : function() { return []; },
        columnDefaults: { headerSort: true, headerSortClickElement: 'icon', resizable: 'header', headerWordWrap: true }, movableColumns: canWrite, history: false,
        // pages of 25-800 rows (Anthony 10-03); the page size is remembered in this browser
        pagination: 'local', paginationSize: 100, paginationSizeSelector: [25, 50, 100, 200, 400, 800], paginationCounter: 'rows', paginationButtonCount: 5,
        persistence: { page: { size: true, page: false } }, persistenceID: 'research-data-sheet',
      });
      tableRef.current = t;
      t.on('tableBuilt', function() { setGridVer(function(n) { return n + 1; }); });
      t.on('dataFiltered', function() { setFilterSummary(plFilterSummary(t, funnelsRef.current)); });
      t.on('cellEdited', function() { scheduleSync('edit'); });
      t.on('clipboardPasted', function() { scheduleSync('paste'); });
      t.on('rangeEdited', function() { scheduleSync('clear'); });
      t.on('rangeChanged', function() { setSelRows(selectedRowIds().length); });
      t.on('columnResized', function(col) { var f = col.getField(); if (canWrite && f && editableKeys().indexOf(f) !== -1) patchColumn(f, { width: Math.round(col.getWidth()) }); });
      t.on('columnMoved', function() {
        if (!canWrite) return;
        var order = t.getColumns().map(function(c) { return c.getField(); }).filter(function(f) { return editableKeys().indexOf(f) !== -1; });
        var hidden = colsRef.current.filter(function(c) { return c.hidden; }).map(function(c) { return c.key; });
        api('/research/data/vendors/' + vendorId + '/layout', { method: 'PUT', body: JSON.stringify({ columns: order.concat(hidden).map(function(k) { return { key: k }; }) }) })
          .then(function(r) { colsRef.current = r.columns; }).catch(function(e) { showToast(e.message, 'error'); });
      });
    }).catch(function(e) { showToast(e.message, 'error'); });
    return function() { cancelled = true; };
  }, [data && data.columns, tab]);
  useEffect(function() { return function() { if (tableRef.current) { tableRef.current.destroy(); tableRef.current = null; } }; }, [vendorId]);
  useEffect(function() { if (tab !== 'sheet' && tableRef.current) { tableRef.current.destroy(); tableRef.current = null; } }, [tab]);

  // The grid's colours come from the theme tokens: rewrite them on every render so switching
  // light / dark while the sheet is open restyles it at once (the toggle re-renders the page).
  useEffect(function() { var st = document.getElementById('pl-grid-css'); if (st) { var css = plGridCss(); if (st.textContent !== css) st.textContent = css; } });

  // Spreadsheet keys: start typing to replace a cell; Ctrl+Z / Ctrl+Y undo / redo; Ctrl+D fill down
  useEffect(function() {
    var el = gridEl.current; if (!el) return;
    // Typing into a selected cell opens its editor; keys typed before the editor has focus are held
    // and written in once it is ready (Tabulator resets the input while it renders the editor).
    var pending = null;
    var flush = function(tries) {
      if (!pending) return;
      var inp = pending.cell.getElement().querySelector('input');
      if (inp && document.activeElement === inp) { if (inp.value !== pending.buf) inp.value = pending.buf; inp.setSelectionRange(inp.value.length, inp.value.length); pending = null; return; }
      if (tries > 40) { pending = null; return; }
      requestAnimationFrame(function() { flush(tries + 1); });
    };
    var gridActive = false;
    var onDown = function(e) { gridActive = el.contains(e.target); };
    var onKey = function(e) {
      var t = tableRef.current; if (!t) return;
      var ae = document.activeElement;
      if (!(el.contains(ae) || (gridActive && (!ae || ae === document.body)))) return;
      if (ae && ae.closest && ae.closest('.tabulator-header')) return; // typing in a column filter
      if (pending) {
        if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) { pending.buf += e.key; e.preventDefault(); e.stopPropagation(); return; }
        var inp0 = pending.cell.getElement().querySelector('input'); if (inp0) inp0.value = pending.buf; pending = null;
      }
      var editing = t.modules && t.modules.edit && t.modules.edit.currentCell;
      var mod = e.ctrlKey || e.metaKey;
      if (!editing && mod && !e.shiftKey && (e.key === 'z' || e.key === 'Z')) { e.preventDefault(); e.stopPropagation(); if (canWrite) undo(); return; }
      if (!editing && mod && (e.key === 'y' || e.key === 'Y' || (e.shiftKey && (e.key === 'z' || e.key === 'Z')))) { e.preventDefault(); e.stopPropagation(); if (canWrite) redo(); return; }
      if (!editing && mod && (e.key === 'd' || e.key === 'D')) { e.preventDefault(); if (canWrite) fillDown(); return; }
      // Delete / Backspace on a selection: Tabulator clears cell by cell (plain cell edits) - name the change "cleared"
      if (!editing && !mod && (e.key === 'Delete' || e.key === 'Backspace') && canWrite) { scheduleSync('clear'); return; }
      if (editing || mod || e.altKey || e.key.length !== 1 || !canWrite) return;
      var sr = t.modules && t.modules.selectRange; var cell = sr && sr.getActiveCell && sr.getActiveCell();
      if (!cell || !cell.column || !cell.column.definition.editor || cell.column.definition.editor === 'tickCross') return;
      e.preventDefault(); e.stopPropagation();
      pending = { cell: cell, buf: e.key };
      t.modules.edit.editCell(cell, false, { initialValue: e.key }); // the input editor starts from this value
      requestAnimationFrame(function() { flush(0); });
    };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey, true);
    return function() { document.removeEventListener('mousedown', onDown, true); document.removeEventListener('keydown', onKey, true); };
  }, [data, tab]);

  // Filters: chips + search
  useEffect(function() {
    var t = tableRef.current; if (!t) return;
    var q = search.trim().toLowerCase();
    var keys = editableKeys();
    t.setFilter(function(r) {
      if (!plFunnelPass(r, funnelsRef.current)) return false;
      if (filter === 'latest' && !(r.on_latest !== false && r.source !== 'manual')) return false;
      if (filter === 'gone' && r.on_latest !== false) return false;
      if (filter === 'manual' && r.source !== 'manual') return false;
      if (filter === 'flagged' && !(r.flags && r.flags.length)) return false;
      if (filter === 'cost_up' && !(r.prev_cost > 0 && r.cost > r.prev_cost * 1.005)) return false;
      if (!q) return true;
      return keys.some(function(k) { var v = r[k]; return v != null && String(v).toLowerCase().indexOf(q) !== -1; });
    });
  }, [filter, search, data, gridVer]);

  var openHistory = function(row) {
    setHist({ row: row, loading: true });
    api('/research/data/items/' + row.id + '/history').then(function(r) { setHist({ row: row, prices: r.prices, edits: r.edits }); })
      .catch(function(e) { setHist(null); showToast(e.message, 'error'); });
  };
  var addRows = function() {
    appPrompt('How many rows to add?', '1', { title: 'Add Rows' }).then(function(v) {
      var n = parseInt(v, 10); if (!(n > 0)) return;
      api('/research/data/vendors/' + vendorId + '/rows', { method: 'POST', body: JSON.stringify({ count: Math.min(n, 500) }) }).then(function(r) {
        var t = tableRef.current; if (!t) return;
        r.rows.forEach(function(row) { baseRef.current.set(row.id, pick(row)); });
        t.addData(r.rows, true).then(function() { return t.setPage(1); }).then(function() { t.scrollToRow(r.rows[0].id, 'top', false); }).catch(function() {});
        showToast(r.rows.length + ' row' + (r.rows.length === 1 ? '' : 's') + ' added at the top');
      }).catch(function(e) { showToast(e.message, 'error'); });
    });
  };
  var deleteRows = function() {
    var ids = selectedRowIds(); if (!ids.length) return;
    appConfirm('Delete ' + ids.length + ' row' + (ids.length === 1 ? '' : 's') + ' from this sheet? Their price history stays with the uploaded lists, and the row history keeps a copy.',
      { title: 'Delete Rows', okLabel: 'Delete', danger: true }).then(function(ok) {
      if (!ok) return;
      api('/research/data/vendors/' + vendorId + '/rows', { method: 'DELETE', body: JSON.stringify({ ids: ids }) }).then(function(r) {
        ids.forEach(function(id) { baseRef.current.delete(id); });
        tableRef.current.deleteRow(ids);
        setSelRows(0);
        showToast(r.deleted + ' row' + (r.deleted === 1 ? '' : 's') + ' deleted');
      }).catch(function(e) { showToast(e.message, 'error'); });
    });
  };
  var saveColumn = function() {
    var a = addCol, opts = a.type === 'select' ? a.options.split(',').map(function(s) { return s.trim(); }).filter(Boolean) : null;
    if (!a.label.trim()) { showToast('Name the column', 'error'); return; }
    var req = a.edit
      ? api('/research/data/vendors/' + vendorId + '/columns/' + encodeURIComponent(a.edit.key), { method: 'PATCH', body: JSON.stringify({ label: a.label.trim(), type: a.type, options: opts }) })
      : api('/research/data/vendors/' + vendorId + '/columns', { method: 'POST', body: JSON.stringify({ label: a.label.trim(), type: a.type, options: opts }) });
    req.then(function(r) { setAddCol(null); setData(function(d) { return Object.assign({}, d, { columns: r.columns }); }); showToast(a.edit ? 'Column updated' : 'Column added at the end'); })
      .catch(function(e) { showToast(e.message, 'error'); });
  };
  var exportXlsx = function() {
    var t = tableRef.current; if (!t) return;
    plExport(t, (data.vendor.vendor_name || 'Vendor') + ' - price list sheet.xlsx', 'Sheet');
  };
  var deleteList = function(l) {
    appConfirm('Delete the upload "' + l.name + '"? Its price history goes, and rows that came only from it are removed. Values it set on other rows stay.',
      { title: 'Delete Upload', okLabel: 'Delete', danger: true }).then(function(ok) {
      if (!ok) return;
      api('/research/data/lists/' + l.id, { method: 'DELETE' }).then(function(r) { showToast('Upload deleted' + (r.rows_removed ? '; ' + r.rows_removed + ' rows removed' : '')); if (tableRef.current) { tableRef.current.destroy(); tableRef.current = null; } load(); })
        .catch(function(e) { showToast(e.message, 'error'); });
    });
  };
  var saveTerms = function() {
    var p = plTermsPayload(editTerms); delete p.vendor_id;
    api('/research/data/lists/' + editTerms.id, { method: 'PATCH', body: JSON.stringify(p) })
      .then(function() { setEditTerms(null); showToast('Saved'); load(); })
      .catch(function(e) { showToast(e.message, 'error'); });
  };

  if (loadErr) return h('div', { style: { padding: 40, color: C.textMuted } }, loadErr, ' ', h(Btn, { size: 'sm', onClick: function() { onNavigate('research-data'); } }, 'All Vendors'));
  if (!data) return h('div', { style: { padding: 40, color: C.textMuted } }, 'Loading…');
  var latest = data.lists[0];
  var items = data.items;
  var cnt = { all: items.length, latest: items.filter(function(r) { return r.on_latest !== false && r.source !== 'manual'; }).length,
    gone: items.filter(function(r) { return r.on_latest === false; }).length, manual: items.filter(function(r) { return r.source === 'manual'; }).length,
    flagged: items.filter(function(r) { return r.flags && r.flags.length; }).length, cost_up: items.filter(function(r) { return r.prev_cost > 0 && r.cost > r.prev_cost * 1.005; }).length };
  var pct = function(v) { return v == null ? '—' : (Math.round(Number(v) * 10000) / 100) + '%'; };
  var term = function(label, val) { return h('div', null, h('div', { style: { fontSize: 11, color: C.textMuted, textTransform: 'uppercase', fontWeight: 600 } }, label), h('div', { style: { fontSize: 14, color: C.text, marginTop: 2 } }, val)); };
  var hidden = data.columns.filter(function(c) { return c.hidden; });
  var listCols = [
    { key: 'received_at', label: 'Received', render: function(l) { return plDate(l.received_at); } },
    { key: 'name', label: 'List', render: function(l) { return h('span', { style: { fontWeight: 600 } }, l.name); } },
    { key: 'file_name', label: 'File' },
    { key: 'rows', label: 'Rows', render: function(l) { return plCount((l.counts || {}).rows); } },
    { key: 'added', label: 'New', render: function(l) { return plCount((l.counts || {}).added, PL_STATUS['new'].color); } },
    { key: 'updated', label: 'Changed', render: function(l) { return plCount((l.counts || {}).updated); } },
    { key: 'cost_changed', label: 'Cost Changed', render: function(l) { return plCount((l.counts || {}).cost_changed, C.warning); } },
    { key: 'not_on_list', label: 'Not On List', render: function(l) { return plCount((l.counts || {}).not_on_list); } },
    { key: 'currency', label: 'Currency', render: function(l) { return l.currency + (l.currency !== 'USD' ? ' · ' + Number(l.fx_rate) : ''); } },
    { key: 'status', label: 'Status', render: function(l) { return l.status === 'current' ? 'Current' : (l.counts && l.counts.latest === false ? 'History only' : 'Superseded'); } },
    { key: 'created_by_name', label: 'Uploaded By' },
    { key: '_act', label: '', noFilter: true, stopClick: true, render: function(l) {
      return h('div', { style: { display: 'flex', gap: 6 } },
        canWrite && h(Btn, { size: 'sm', onClick: function() { setEditTerms(Object.assign(plTermsFrom(l, vendorId), { id: l.id })); } }, 'Edit'),
        canDelete && h(Btn, { size: 'sm', variant: 'danger', onClick: function() { deleteList(l); } }, h(LIcon, { name: 'trash-2', size: 13 })));
    } },
  ];

  return h('div', { style: { display: 'flex', flexDirection: 'column', gap: 12 } },
    h('div', { style: { display: 'flex', alignItems: 'flex-start', gap: 10 } },
      h(Btn, { size: 'sm', variant: 'ghost', onClick: function() { onNavigate('research-data'); } }, h(LIcon, { name: 'arrow-left', size: 14 }), 'Data'),
      h('div', { style: { flex: 1 } },
        h('div', { style: { fontSize: 20, fontWeight: 700, color: C.text } }, data.vendor.vendor_name),
        h('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 2 } }, items.length.toLocaleString() + ' items · ' + data.lists.length + ' upload' + (data.lists.length === 1 ? '' : 's') +
          (latest ? ' · latest: ' + latest.name + ' (' + plDate(latest.received_at) + ')' : ''))),
      plChip(tab === 'sheet', 'Sheet', function() { setTab('sheet'); }),
      plChip(tab === 'uploads', 'Uploads (' + data.lists.length + ')', function() { setTab('uploads'); }),
      canWrite && h(Btn, { variant: 'primary', size: 'sm', onClick: function() { setUpOpen(true); } }, h(LIcon, { name: 'upload', size: 13 }), 'Upload Price List')),
    latest && h(Card, { style: { display: 'flex', gap: 28, flexWrap: 'wrap', padding: '12px 18px', alignItems: 'center' } },
      term('Currency', latest.currency + (latest.currency === 'USD' ? '' : ' · 1 = $' + Number(latest.fx_rate))),
      term('Inbound', pct(latest.freight_pct)), term('Tariff', pct(latest.tariff_pct)),
      term('Prep', latest.prep_cost != null ? plMoney(latest.prep_cost) : '—'),
      term('Case Pack', latest.case_pack || 'per row'), term('Lead Time', latest.lead_days ? latest.lead_days + ' days' : '—'),
      latest.notes && term('Notes', latest.notes),
      h('div', { style: { flex: 1 } }),
      canWrite && h(Btn, { size: 'sm', onClick: function() { setEditTerms(Object.assign(plTermsFrom(latest, vendorId), { id: latest.id })); } }, h(LIcon, { name: 'pencil', size: 13 }), 'Edit Terms')),
    tab === 'uploads'
      ? h(DataTable, { columns: listCols, data: data.lists, storageKey: 'research-data-uploads', emptyTitle: 'No uploads yet', emptySub: '' })
      : h(React.Fragment, null,
          h('div', { style: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' } },
            plChip(filter === 'all', 'All (' + cnt.all.toLocaleString() + ')', function() { setFilter('all'); }),
            plChip(filter === 'latest', 'On Latest List (' + cnt.latest.toLocaleString() + ')', function() { setFilter('latest'); }),
            plChip(filter === 'gone', 'Not On Latest List (' + cnt.gone.toLocaleString() + ')', function() { setFilter('gone'); }),
            cnt.manual > 0 && plChip(filter === 'manual', 'Added By Hand (' + cnt.manual + ')', function() { setFilter('manual'); }),
            plChip(filter === 'cost_up', 'Cost Up (' + cnt.cost_up + ')', function() { setFilter('cost_up'); }),
            plChip(filter === 'flagged', 'Flagged (' + cnt.flagged + ')', function() { setFilter('flagged'); }, C.warning),
            h('div', { style: { flex: 1 } }),
            saving > 0 && h('span', { style: { fontSize: 12, color: C.textMuted } }, 'Saving…'),
            h(SearchInput, { type: 'text', value: search, onChange: function(e) { setSearch(e.target.value); }, placeholder: 'Search the sheet…',
              style: { width: 240, background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '7px 12px', borderRadius: 8, fontSize: 13, outline: 'none' } })),
          h('div', { style: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' } },
            canWrite && h(Btn, { size: 'sm', onClick: addRows }, h(LIcon, { name: 'plus', size: 13 }), 'Add Rows'),
            canDelete && h(Btn, { size: 'sm', variant: selRows ? 'danger' : 'default', disabled: !selRows, onClick: deleteRows }, h(LIcon, { name: 'trash-2', size: 13 }), selRows ? 'Delete ' + selRows + ' Row' + (selRows === 1 ? '' : 's') : 'Delete Rows'),
            canWrite && h(Btn, { size: 'sm', onClick: function() { setAddCol({ label: '', type: 'text', options: '' }); } }, h(LIcon, { name: 'columns-3', size: 13 }), 'Add Column'),
            h(Btn, { size: 'sm', onClick: function() { setColsOpen(true); } }, 'Columns' + (hidden.length ? ' (' + hidden.length + ' hidden)' : '')),
            canWrite && h(Btn, { size: 'sm', onClick: undo, title: 'Ctrl+Z' }, h(LIcon, { name: 'undo-2', size: 13 }), 'Undo'),
            canWrite && h(Btn, { size: 'sm', onClick: redo, title: 'Ctrl+Y' }, h(LIcon, { name: 'redo-2', size: 13 }), 'Redo'),
            h(Btn, { size: 'sm', onClick: exportXlsx }, h(LIcon, { name: 'download', size: 13 }), 'Export'),
            h('div', { style: { flex: 1 } }),
            h('span', { style: { fontSize: 11, color: C.textDim } }, 'Click a cell and type · double-click to edit · paste from Excel · Ctrl+D fill down · Delete clears · Ctrl+Z undo')),
          h('div', { ref: gridEl, className: 'pl-grid' }),
          h(PlFilterBar, { summary: filterSummary, onClear: clearFilters })),
    h(PlUploadModal, { open: upOpen, onClose: function() { setUpOpen(false); }, vendors: vendors, vendorId: vendorId, showToast: showToast,
      onDone: function() { setUpOpen(false); if (tableRef.current) { tableRef.current.destroy(); tableRef.current = null; } load(); } }),
    h(Modal, { open: !!hist, onClose: function() { setHist(null); }, title: 'Row History', width: 720 },
      hist && h('div', { style: { display: 'flex', flexDirection: 'column', gap: 14 } },
        h('div', { style: { fontSize: 14, fontWeight: 600, color: C.text } }, (hist.row.vendor_sku ? hist.row.vendor_sku + ' · ' : '') + (hist.row.description || '(no description)')),
        hist.loading ? h('div', { style: { color: C.textMuted } }, 'Loading…') : h(React.Fragment, null,
          h('div', { style: { fontSize: 12, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase' } }, 'Price On Each List'),
          hist.prices.length ? h('table', { style: { borderCollapse: 'collapse', width: '100%', fontSize: 13 } },
            h('tbody', null, hist.prices.map(function(p) {
              return h('tr', { key: p.list_id, style: { borderBottom: '1px solid ' + C.border } },
                h('td', { style: { padding: '5px 6px', color: C.textMuted } }, plDate(p.received_at)), h('td', { style: { padding: '5px 6px' } }, p.name),
                h('td', { style: { padding: '5px 6px', textAlign: 'right' } }, plMoney(p.cost)),
                h('td', { style: { padding: '5px 6px', textAlign: 'right', color: C.textMuted } }, p.currency !== 'USD' && p.cost_raw != null ? Number(p.cost_raw).toFixed(2) + ' ' + p.currency : ''));
            }))) : h('div', { style: { color: C.textDim, fontSize: 13 } }, 'Not on any uploaded list (added by hand).'),
          h('div', { style: { fontSize: 12, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase' } }, 'Changes'),
          h('div', { style: { maxHeight: 340, overflowY: 'auto' } },
            hist.edits.map(function(e) {
              var what = e.source === 'row_added' ? 'Row added' + (e.list_id ? ' from a list' : '') : e.source === 'row_deleted' ? 'Row deleted'
                : (e.label || e.column_key) + ': ' + (e.old_value == null || e.old_value === '' ? '(empty)' : String(e.old_value)) + ' → ' + (e.new_value == null || e.new_value === '' ? '(empty)' : String(e.new_value));
              var how = { edit: 'typed', paste: 'pasted', fill: 'filled', clear: 'cleared', undo: 'undo', redo: 'redo', 'import': 'list upload', row_added: '', row_deleted: '' }[e.source];
              return h('div', { key: e.id, style: { padding: '6px 0', borderBottom: '1px solid ' + C.border, fontSize: 13 } },
                h('div', { style: { color: C.text } }, what),
                h('div', { style: { fontSize: 11, color: C.textDim } }, new Date(e.created_at).toLocaleString() + ' · ' + (e.username || '') + (how ? ' · ' + how : '')));
            }))))),
    h(Modal, { open: !!addCol, onClose: function() { setAddCol(null); }, title: addCol && addCol.edit ? 'Edit Column' : 'Add Column', width: 440 },
      addCol && h('div', { style: { display: 'flex', flexDirection: 'column', gap: 12 } },
        h(Inp, { label: 'Name', value: addCol.label, autoFocus: true, onChange: function(e) { setAddCol(Object.assign({}, addCol, { label: e.target.value })); } }),
        h(Sel, { label: 'Type', options: PL_COL_TYPES, value: addCol.type, onChange: function(e) { setAddCol(Object.assign({}, addCol, { type: e.target.value || 'text' })); } }),
        addCol.type === 'select' && h(Inp, { label: 'Choices (comma-separated)', value: addCol.options, placeholder: 'e.g. Asked, Quoted, Ordered', onChange: function(e) { setAddCol(Object.assign({}, addCol, { options: e.target.value })); } }),
        h('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } },
          h(Btn, { onClick: function() { setAddCol(null); } }, 'Cancel'),
          h(Btn, { variant: 'primary', onClick: saveColumn }, addCol.edit ? 'Save' : 'Add Column')))),
    h(Modal, { open: colsOpen, onClose: function() { setColsOpen(false); }, title: 'Columns', width: 420 },
      h('div', { style: { display: 'flex', flexDirection: 'column', gap: 6 } },
        h('div', { style: { fontSize: 12, color: C.textDim, marginBottom: 4 } }, 'Shown or hidden for everyone who opens this sheet. Drag headers in the sheet to reorder.'),
        data.columns.map(function(c) {
          return h('label', { key: c.key, style: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: C.text, cursor: canWrite ? 'pointer' : 'default' } },
            h('input', { type: 'checkbox', checked: !c.hidden, disabled: !canWrite, onChange: function(e) { patchColumn(c.key, { hidden: !e.target.checked }); } }),
            c.label, h('span', { style: { fontSize: 11, color: C.textDim } }, c.kind === 'standard' ? 'system' : c.kind === 'extra' ? 'from the list' : 'added here'));
        }))),
    h(Modal, { open: !!editTerms, onClose: function() { setEditTerms(null); }, title: 'Edit Terms', width: 760 },
      editTerms && h('div', { style: { display: 'flex', flexDirection: 'column', gap: 14 } },
        h(PlTermsForm, { terms: editTerms, setTerms: function(t) { setEditTerms(Object.assign({}, t, { id: editTerms.id })); }, vendors: vendors, lockVendor: true }),
        h('div', { style: { fontSize: 12, color: C.textDim } }, 'Terms belong to the uploaded list; the newest list\'s terms are the vendor\'s current terms.'),
        h('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } },
          h(Btn, { onClick: function() { setEditTerms(null); } }, 'Cancel'),
          h(Btn, { variant: 'primary', onClick: saveTerms }, 'Save')))));
}

// ── Data: vendors with a sheet ──
function PlDataIndex({ showToast, onNavigate, canWrite, vendors }) {
  var _l = useState(null), rows = _l[0], setRows = _l[1];
  var _u = useState(false), upOpen = _u[0], setUpOpen = _u[1];
  useEffect(function() { api('/research/data/vendors').then(setRows).catch(function(e) { showToast(e.message, 'error'); setRows([]); }); }, []);
  var columns = [
    { key: 'vendor_name', label: 'Vendor', render: function(r) { return h('span', { style: { fontWeight: 600 } }, r.vendor_name); } },
    { key: 'items', label: 'Items', render: function(r) { return plCount(r.items); } },
    { key: 'on_latest', label: 'On Latest List', render: function(r) { return plCount(r.on_latest); } },
    { key: 'lists', label: 'Uploads', render: function(r) { return plCount(r.lists); } },
    { key: 'latest', label: 'Latest List', render: function(r) { return r.latest_list ? r.latest_list.name + ' · ' + plDate(r.latest_list.received_at) : ''; } },
    { key: 'latest_by', label: 'Uploaded By', render: function(r) { return r.latest_list ? r.latest_list.by || '' : ''; } },
    { key: 'updated_at', label: 'Last Change', render: function(r) { return r.updated_at ? new Date(r.updated_at).toLocaleDateString() : ''; } },
  ];
  return h('div', { style: { display: 'flex', flexDirection: 'column', gap: 14 } },
    h('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
      h('div', { style: { flex: 1 } },
        h('div', { style: { fontSize: 20, fontWeight: 700, color: C.text } }, 'Data'),
        h('div', { style: { fontSize: 13, color: C.textMuted, marginTop: 2 } }, 'Vendor price lists: one sheet per vendor. Every list you upload merges into its vendor\'s sheet and is kept as price history.')),
      canWrite && h(Btn, { variant: 'primary', onClick: function() { setUpOpen(true); } }, h(LIcon, { name: 'upload', size: 14 }), 'Upload Price List')),
    h(DataTable, { columns: columns, data: rows || [], storageKey: 'research-data-vendors',
      onRowClick: function(r) { onNavigate('research-data', r.vendor_id); }, rowHref: function(r) { return '/research/data/' + r.vendor_id; },
      emptyTitle: rows == null ? 'Loading…' : 'No vendor sheets yet', emptySub: canWrite ? 'Upload a vendor price list to start.' : '' }),
    h(PlUploadModal, { open: upOpen, onClose: function() { setUpOpen(false); }, vendors: vendors, showToast: showToast,
      onDone: function(vid) { setUpOpen(false); onNavigate('research-data', vid); } }));
}

function ResearchDataPage({ showToast, currentUser, vendorArg, onNavigate }) {
  var _v = useState([]), vendors = _v[0], setVendors = _v[1];
  var canWrite = hasPermission(currentUser, 'product_research', 'write');
  var canDelete = hasPermission(currentUser, 'product_research', 'delete');
  useEffect(function() { api('/research/meta/vendors').then(setVendors).catch(function() {}); }, []);
  return vendorArg
    ? h(PlSheet, { key: 'sheet-' + vendorArg, vendorId: Number(vendorArg), showToast: showToast, onNavigate: onNavigate, canWrite: canWrite, canDelete: canDelete, vendors: vendors })
    : h(PlDataIndex, { showToast: showToast, onNavigate: onNavigate, canWrite: canWrite, vendors: vendors });
}
window.ResearchDataPage = ResearchDataPage;

// ── Amazon US: each vendor item against our catalog (recommendations come in Phase 3) ──
function plEsc(v) { return String(v == null ? '' : v).replace(/[&<>"]/g, function(c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); }
// Export what the user is looking at: every row their chips, search and header filters leave (all pages), in the
// current sort, visible columns only (Anthony 10-03).
function plExport(t, fileName, sheetName) {
  if (!t) return;
  t.download('xlsx', fileName.replace(/[\\/:*?"<>|]+/g, ' '), { sheetName: sheetName || 'Sheet' }, 'active');
}

// ── Amazon side of each row (docs/26 Phase 2, Keepa-only 2026-10-04) ──
var AMZ_STATUS = {
  found: { label: 'Listed', color: '#10B981', hint: 'Amazon listing found' },
  check_name: { label: 'Check Listing', color: '#F59E0B', hint: 'A listing carries this barcode but its title shares no word with the description' },
  no_listing: { label: 'No Listing', color: '#94A3B8', hint: 'Keepa has no Amazon US listing for this barcode' },
  no_identifier: { label: 'No Barcode', color: '#94A3B8', hint: 'No usable barcode or ASIN on the sheet' },
  pending: { label: 'Waiting', color: '#6366F1', hint: 'Not pulled yet: the automatic pull gets to it' },
};
// sell = the LOWER of Buy Box now and the 90-day average (Anthony 9-30); landed = cost × (1 + inbound + tariff) + prep (10-01)
function plAmzCalc(r, a, terms) {
  var o = { amz_status: a ? a.amz_status : (r.barcode || r.asin ? 'pending' : 'no_identifier') };
  if (!a) return o;
  var num = function(v) { return v == null || v === '' ? null : Number(v); };
  ['bb_now', 'bb_avg90', 'amazon_price', 'fba_fee', 'referral_pct', 'closing_fee', 'amazon_bb_share', 'top_seller_share', 'amazon_oos_pct90'].forEach(function(k) { o[k] = num(a[k]); });
  ['rank', 'package_qty', 'new_offers', 'bb_sellers', 'monthly_sold', 'drops30', 'drops90', 'drops180'].forEach(function(k) { o[k] = num(a[k]); });
  o.amz_asin = a.asin; o.amz_title = a.title; o.amz_brand = a.brand; o.amz_category = a.category; o.amz_image = a.image;
  o.bb_is_amazon = a.bb_is_amazon; o.bb_fba = a.bb_is_fba == null ? '' : a.bb_is_fba ? 'FBA' : 'FBM';
  o.candidates = a.candidates || []; o.listings = o.candidates.length || null;
  o.pulled = a.pulled_at ? String(a.pulled_at).slice(0, 10) : null;
  var sell = o.bb_now != null && o.bb_avg90 != null ? Math.min(o.bb_now, o.bb_avg90) : (o.bb_now != null ? o.bb_now : o.bb_avg90);
  o.sell = sell; o.sell_basis = o.bb_now != null && o.bb_avg90 != null ? (o.bb_now <= o.bb_avg90 ? 'Buy Box now' : '90-day average') : o.bb_now != null ? 'Buy Box now (no 90-day average)' : o.bb_avg90 != null ? '90-day average (no Buy Box now)' : null;
  var t = terms || {}, cost = num(r.cost);
  if (cost != null) o.landed = Math.round((cost * (1 + (num(t.freight_pct) || 0) + (num(t.tariff_pct) || 0)) + (num(t.prep_cost) || 0)) * 100) / 100;
  if (sell != null && o.landed != null && o.landed > 0) {
    var fees = sell * (o.referral_pct != null ? o.referral_pct : 15) / 100 + (o.fba_fee || 0) + (o.closing_fee || 0);
    o.profit = Math.round((sell - fees - o.landed) * 100) / 100;
    o.roi = o.profit / o.landed;
  }
  return o;
}
function plAmzLabel(o) { o.amz_label = (AMZ_STATUS[o.amz_status] || {}).label || ''; return o; }
function plAgo(d) {
  if (!d) return '';
  var m = Math.round((Date.now() - new Date(d).getTime()) / 60000);
  return m < 1 ? 'just now' : m < 60 ? m + ' min ago' : m < 1440 ? Math.round(m / 60) + ' h ago' : Math.round(m / 1440) + ' d ago';
}
function PlAmzStatus({ status, canWrite, vid, onChanged, showToast }) {
  var busy = useState(false), b = busy[0], setB = busy[1];
  if (!status) return null;
  var s = status, j = s.job || {};
  var waiting = s.paused ? 'Paused' : j.waiting === 'nothing due' ? 'Up to date' : j.waiting ? 'Waiting: ' + j.waiting : j.last_tick ? 'Pulling' : 'Starting';
  var post = function(path, body, msg) {
    setB(true);
    api(path, { method: 'POST', body: JSON.stringify(body || {}) }).then(function(r) { if (msg) showToast(msg(r)); onChanged(); })
      .catch(function(e) { showToast(e.message, 'error'); }).finally(function() { setB(false); });
  };
  return h('div', { style: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 12, color: C.textMuted, padding: '7px 12px', border: '1px solid ' + C.border, borderRadius: 8, background: C.surface } },
    h(LIcon, { name: 'database', size: 13 }),
    h('span', { style: { fontWeight: 600, color: C.text } }, 'Amazon Data (Keepa)'),
    h('span', null, (s.pulled || 0).toLocaleString() + ' of ' + (s.eligible || 0).toLocaleString() + ' rows pulled'),
    h('span', null, '· ' + (s.due || 0).toLocaleString() + ' due'),
    s.oldest && h('span', null, '· oldest ' + String(s.oldest).slice(0, 10)),
    h('span', null, '· refreshed every ' + s.refresh_days + ' days'),
    h('span', { style: { color: s.paused ? C.warning : C.textMuted } }, '· ' + waiting),
    j.tokens_left != null && h('span', { title: 'Keeps ' + s.reserve + ' tokens for your own Analysis fetches; quiet 2-5 AM ET for the nightly PO refresh' }, '· ' + j.tokens_left + ' Keepa tokens'),
    s.last_24h && h('span', null, '· last 24 h: ' + s.last_24h.rows.toLocaleString() + ' rows, ' + s.last_24h.tokens.toLocaleString() + ' tokens'),
    j.last_error && h('span', { style: { color: C.danger }, title: j.last_error }, '· last error ' + String(j.last_error).slice(0, 60)),
    h('div', { style: { flex: 1 } }),
    canWrite && vid && h(Btn, { size: 'sm', disabled: b, onClick: function() { post('/research/amazon/vendors/' + vid + '/refresh', null, function(r) { return r.queued.toLocaleString() + ' rows moved to the front of the queue'; }); } },
      h(LIcon, { name: 'refresh-cw', size: 13 }), 'Refresh Now'),
    canWrite && h(Btn, { size: 'sm', disabled: b, onClick: function() { post('/research/amazon/pause', { paused: !s.paused }, function() { return s.paused ? 'Automatic pull resumed' : 'Automatic pull paused'; }); } },
      s.paused ? 'Resume' : 'Pause'));
}
function PlCandidates({ row, onClose }) {
  if (!row) return null;
  var cands = row.candidates || [];
  return h(Modal, { open: true, onClose: onClose, title: 'Amazon Listings: ' + (row.description || row.vendor_sku || ''), width: 760 },
    h('div', { style: { display: 'flex', flexDirection: 'column', gap: 8 } },
      h('div', { style: { fontSize: 12, color: C.textMuted } }, 'Barcode ' + (row.barcode || '-') + (row.asin ? ' · sheet ASIN ' + row.asin : '') + ' · pulled ' + (row.pulled || '-')),
      !cands.length && h('div', { style: { padding: 16, color: C.textMuted } }, 'Keepa has no Amazon US listing for this barcode.'),
      cands.map(function(c) {
        var chosen = c.asin === row.amz_asin;
        return h('div', { key: c.asin, style: { display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 10px', borderRadius: 8, border: '1px solid ' + (chosen ? C.accent : C.border) } },
          h('div', { style: { flex: 1, minWidth: 0 } },
            h('div', { style: { fontSize: 13, color: C.text, fontWeight: 600 } }, c.title || '(no title)'),
            h('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 3, display: 'flex', gap: 10, flexWrap: 'wrap' } },
              h('span', null, c.asin), c.brand && h('span', null, c.brand),
              c.monthly_sold != null && h('span', null, c.monthly_sold + ' sold / mo'), c.rank != null && h('span', null, 'rank ' + c.rank.toLocaleString()),
              c.package_qty > 1 && h('span', { style: { color: C.warning } }, 'pack of ' + c.package_qty),
              h('span', { style: { color: c.name_ok ? C.success : C.warning } }, c.name_ok ? 'name matches' : 'name does not match'),
              c.ours && h('span', { style: { color: C.accent } }, 'our SKU sells here'),
              chosen && h('span', { style: { color: C.accent, fontWeight: 600 } }, 'used for this row'))),
          h('a', { href: 'https://www.amazon.com/dp/' + c.asin, target: '_blank', rel: 'noopener', style: { color: C.accent, fontSize: 12 } }, 'Amazon'),
          h('a', { href: 'https://keepa.com/#!product/1-' + c.asin, target: '_blank', rel: 'noopener', style: { color: C.accent, fontSize: 12 } }, 'Keepa'));
      })));
}
function PlKeepaLookup({ open, onClose, showToast, canWrite }) {
  var _t = useState(''), text = _t[0], setText = _t[1];
  var _e = useState(null), est = _e[0], setEst = _e[1];
  var _r = useState(null), res = _r[0], setRes = _r[1];
  var _b = useState(false), busy = _b[0], setBusy = _b[1];
  var run = function(dry) {
    setBusy(true);
    api('/research/amazon/lookup', { method: 'POST', body: JSON.stringify({ ids: text, dry: dry }) })
      .then(function(r) { if (dry) setEst(r); else { setRes(r); setEst(null); } })
      .catch(function(e) { showToast(e.message, 'error'); }).finally(function() { setBusy(false); });
  };
  var money = function(v) { return v == null ? '' : '$' + Number(v).toFixed(2); };
  var cols = [
    { key: 'query', label: 'You Pasted' },
    { key: 'asin', label: 'ASIN', render: function(r) { return r.missing && !r.title ? h('span', { style: { color: C.textDim } }, r.asin || 'no listing') : h('a', { href: 'https://keepa.com/#!product/1-' + r.asin, target: '_blank', rel: 'noopener', style: { color: C.accent } }, r.asin); } },
    { key: 'title', label: 'Title', render: function(r) { return h('span', { title: r.title || '', style: { display: 'inline-block', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, r.title || ''); } },
    { key: 'bb_now', label: 'Buy Box Now', render: function(r) { return money(r.bb_now); } },
    { key: 'bb_avg90', label: '90-Day Buy Box', render: function(r) { return money(r.bb_avg90); } },
    { key: 'amazon_bb_share', label: 'Amazon BB %', render: function(r) { return r.amazon_bb_share == null ? '' : Math.round(r.amazon_bb_share) + '%'; } },
    { key: 'monthly_sold', label: 'Sold / Mo' }, { key: 'drops90', label: 'Rank Drops 90d' }, { key: 'rank', label: 'Rank' },
    { key: 'new_offers', label: 'Offers' }, { key: 'fba_fee', label: 'FBA Fee', render: function(r) { return money(r.fba_fee); } },
    { key: 'referral_pct', label: 'Referral %', render: function(r) { return r.referral_pct == null ? '' : Number(r.referral_pct).toFixed(1) + '%'; } },
  ];
  return h(Modal, { open: open, onClose: onClose, title: 'Keepa Lookup', width: 1150 },
    h('div', { style: { display: 'flex', flexDirection: 'column', gap: 10 } },
      h('div', { style: { fontSize: 12, color: C.textMuted } }, 'Paste ASINs, UPCs or EANs (up to 100). Answers from the last day are free; the rest cost about 3 Keepa tokens per listing.'),
      h('textarea', { value: text, onChange: function(e) { setText(e.target.value); setEst(null); }, rows: 4, placeholder: 'B07JJYYC74\n5706569106010',
        style: { width: '100%', background: C.bg, color: C.text, border: '1px solid ' + C.border, borderRadius: 8, padding: 10, fontSize: 13, fontFamily: 'inherit', resize: 'vertical' } }),
      h('div', { style: { display: 'flex', gap: 8, alignItems: 'center' } },
        h(Btn, { size: 'sm', disabled: busy || !text.trim(), onClick: function() { run(true); } }, 'Estimate Tokens'),
        canWrite && h(Btn, { size: 'sm', variant: 'primary', disabled: busy || !text.trim(), onClick: function() { run(false); } }, busy ? 'Looking Up…' : 'Look Up'),
        est && h('span', { style: { fontSize: 12, color: C.textMuted } }, '~' + est.estimate + ' tokens · ' + est.cached + ' from cache · ' + est.need + ' to fetch' + (est.bad.length ? ' · not an ASIN or barcode: ' + est.bad.join(', ') : '')),
        res && h('span', { style: { fontSize: 12, color: C.textMuted } }, res.tokens_used + ' tokens used' + (res.tokens_left != null ? ' · ' + res.tokens_left + ' left' : ''))),
      res && h(DataTable, { columns: cols, data: res.rows.map(function(r, i) { return Object.assign({ _k: i }, r); }), rowKey: '_k', storageKey: 'research-keepa-lookup', emptyTitle: 'Nothing found' })));
}

function AmazonUsPage({ showToast, currentUser, vendorArg, onNavigate, initialView }) {
  var _vs = useState(null), vendorRows = _vs[0], setVendorRows = _vs[1];
  var _d = useState(null), data = _d[0], setData = _d[1];
  // initialView: the filter / search / latest-only a product link carried out and back (Anthony 10-03)
  var iv = initialView || {};
  var _f = useState(iv.filter || 'all'), filter = _f[0], setFilter = _f[1];
  var _lt = useState(iv.latestOnly !== false), latestOnly = _lt[0], setLatestOnly = _lt[1];
  var _q = useState(iv.search || ''), search = _q[0], setSearch = _q[1];
  var _b = useState(false), busy = _b[0], setBusy = _b[1];
  var _co = useState(false), colsOpen = _co[0], setColsOpen = _co[1];
  var _gv = useState(0), gridVer = _gv[0], setGridVer = _gv[1];
  var _fs = useState(''), filterSummary = _fs[0], setFilterSummary = _fs[1];
  var _am = useState(null), amz = _am[0], setAmz = _am[1]; // { map: { itemId: amazonRow }, terms }
  var _as = useState(null), amzStatus = _as[0], setAmzStatus = _as[1];
  var _cr = useState(null), candRow = _cr[0], setCandRow = _cr[1];
  var _kl = useState(false), lookupOpen = _kl[0], setLookupOpen = _kl[1];
  var gridEl = useRef(null), tableRef = useRef(null), viewRef = useRef({}), funnelsRef = useRef({}), amzRef = useRef(null), amzSig = useRef('');
  var onFunnel = function() { var t = tableRef.current; if (!t) return; t.refreshFilter(); plFunnelPaint(t, funnelsRef.current); setFilterSummary(plFilterSummary(t, funnelsRef.current)); };
  var clearFilters = function() { var t = tableRef.current; if (!t) return; funnelsRef.current = {}; t.clearHeaderFilter(); t.refreshFilter(); plFunnelPaint(t, {}); setFilterSummary(''); };
  var canWrite = hasPermission(currentUser, 'product_research', 'write');
  var vid = vendorArg ? Number(vendorArg) : null;
  viewRef.current = { vid: vid, filter: filter, search: search, latestOnly: latestOnly };
  useEffect(function() { api('/research/data/vendors').then(setVendorRows).catch(function(e) { showToast(e.message, 'error'); setVendorRows([]); }); }, []);
  var load = function() { if (!vid) { setData(null); return; } setData(null); api('/research/data/vendors/' + vid).then(setData).catch(function(e) { showToast(e.message, 'error'); }); };
  useEffect(load, [vid]);
  // Amazon (Keepa) data: loaded with the vendor, then the status is polled each minute and the rows reloaded
  // when the automatic pull has written more of them
  var loadAmz = function() {
    if (!vid) { setAmz(null); amzRef.current = null; return api('/research/amazon/status').then(setAmzStatus).catch(function() {}); }
    return api('/research/amazon/vendors/' + vid).then(function(r) {
      var map = {}; r.rows.forEach(function(x) { map[x.id] = x; });
      amzRef.current = { map: map, terms: r.terms }; amzSig.current = r.status.pulled + '|' + r.status.due + '|' + r.status.oldest;
      setAmz(amzRef.current); setAmzStatus(r.status);
    }).catch(function(e) { showToast(e.message, 'error'); });
  };
  useEffect(function() { amzRef.current = null; setAmz(null); loadAmz(); }, [vid]);
  useEffect(function() {
    var iv = setInterval(function() {
      api('/research/amazon/status' + (vid ? '?vid=' + vid : '')).then(function(st) {
        setAmzStatus(st);
        if (vid && st.pulled + '|' + st.due + '|' + st.oldest !== amzSig.current) loadAmz();
      }).catch(function() {});
    }, 60000);
    return function() { clearInterval(iv); };
  }, [vid]);

  // The rows the chips, search and "latest list only" leave
  var visibleRows = function() {
    if (!data) return [];
    var v = viewRef.current, q = v.search.trim().toLowerCase();
    return data.items.filter(function(r) {
      if (v.latestOnly && r.on_latest === false) return false;
      var f = v.filter;
      if (f === 'flagged' ? !(r.flags && r.flags.length) : f === 'cost_up' ? !(r.cost_delta_pct > 0.005) : f === 'cost_down' ? !(r.cost_delta_pct < -0.005) : f !== 'all' && r.catalog_status !== f) return false;
      if (!q) return true;
      return [r.description, r.vendor_sku, r.barcode, r.brand, r.asin, (r.match_skus || []).join(' ')].some(function(x) { return x && String(x).toLowerCase().indexOf(q) !== -1; });
    }).map(function(r) {
      return plAmzLabel(Object.assign({}, r, { match_skus_txt: (r.match_skus || []).join(', '), flags_txt: (r.flags || []).join(' · '),
        match_by: (r.match_method || '').replace('vendor_sku', 'item #').replace(/\+/g, ' + '),
        catalog_label: (PL_STATUS[r.catalog_status] || {}).label || '' },
        plAmzCalc(r, amzRef.current && amzRef.current.map[r.id], amzRef.current && amzRef.current.terms)));
    });
  };

  // Same grid as the Data sheet (Anthony 10-03), read-only ("lock the cells"), with pages of 25-800 rows
  var ausColumns = function() {
    var money = function(cell) { var v = cell.getValue(); return v == null || v === '' ? '' : '$' + Number(v).toFixed(2); };
    var num = { hozAlign: 'right', sorter: 'number' };
    var L = plHf('list', { funnel: plFunnel(function() { return tableRef.current; }, funnelsRef, onFunnel) });
    return [
      Object.assign({ title: 'Item #', field: 'vendor_sku', width: 140 }, plHf('text')),
      Object.assign({ title: 'Description', field: 'description', width: 320, tooltip: true }, plHf('text')),
      Object.assign({ title: 'Brand', field: 'brand', width: 140 }, L),
      Object.assign({ title: 'Barcode', field: 'barcode', width: 140 }, plHf('text')),
      Object.assign({ title: 'Cost', field: 'cost', width: 100, formatter: money }, num, plHf('number')),
      Object.assign({ title: 'Previous Cost', field: 'prev_cost', width: 120, formatter: money }, num, plHf('number')),
      Object.assign({ title: 'Case Pack', field: 'case_pack', width: 100 }, num, plHf('number')),
      Object.assign({ title: 'Catalog', field: 'catalog_label', width: 150 }, L, { formatter: function(cell) {
        var d = PL_STATUS[cell.getRow().getData().catalog_status]; if (!d) return '';
        return '<span title="' + plEsc(d.hint) + '" style="display:inline-block;padding:1px 8px;border-radius:10px;font-size:11px;font-weight:600;color:' + d.color + ';background:' + d.color + '1f">' + d.label + '</span>';
      } }),
      Object.assign({ title: 'Our SKU', field: 'ref_sku', width: 170 }, plHf('text'), { formatter: function(cell) {
        var r = cell.getRow().getData(); if (!r.ref_product_id) return '';
        return '<a href="/catalog/' + r.ref_product_id + '" style="color:' + C.accent + ';font-weight:600;text-decoration:none">' + plEsc(r.ref_sku) + '</a>';
      }, cellClick: function(e, cell) {
        var r = cell.getRow().getData(); if (!r.ref_product_id || !(e.target && e.target.closest && e.target.closest('a'))) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return; // new tab: the link's own href
        e.preventDefault();
        var v = viewRef.current; // closing the product comes back here: same vendor, same view
        onNavigate('products', r.ref_product_id, { returnTo: 'amazon-research', returnOpts: { returnArg: v.vid, amazonView: { filter: v.filter, search: v.search, latestOnly: v.latestOnly } } });
      } }),
      Object.assign({ title: 'Our Vendor', field: 'ref_vendor', width: 170 }, L),
      Object.assign({ title: 'Our Cost', field: 'ref_cost', width: 100, formatter: money }, num, plHf('number')),
      Object.assign({ title: 'vs Our Cost', field: 'cost_delta_pct', width: 110, formatter: function(cell) {
        var v = cell.getValue(); if (v == null || v === '') return '';
        var d = Number(v); return '<span style="font-weight:600;color:' + (d > 0.005 ? C.danger : d < -0.005 ? C.success : C.textMuted) + '">' + plPct(d) + '</span>';
      } }, num, plHf('number', { pct: true })),
      Object.assign({ title: 'Cheapest Other Source', field: 'other_cost', width: 110, formatter: money }, num, plHf('number')),
      Object.assign({ title: 'Other Vendor', field: 'other_vendor', width: 170 }, L),
      Object.assign({ title: 'All Matching SKUs', field: 'match_skus_txt', width: 220, visible: false, tooltip: true }, plHf('text')),
      Object.assign({ title: 'Matched By', field: 'match_by', width: 130, visible: false }, L),
      Object.assign({ title: 'Sheet ASIN', field: 'asin', width: 120, visible: false }, plHf('text')),
      // Amazon (Keepa) - Phase 2
      Object.assign({ title: 'Amazon', field: 'amz_label', width: 130 }, L, { formatter: function(cell) {
        var d = AMZ_STATUS[cell.getRow().getData().amz_status]; if (!d) return '';
        return '<span title="' + plEsc(d.hint) + '" style="display:inline-block;padding:1px 8px;border-radius:10px;font-size:11px;font-weight:600;color:' + d.color + ';background:' + d.color + '1f">' + d.label + '</span>';
      } }),
      Object.assign({ title: 'ASIN', field: 'amz_asin', width: 120 }, plHf('text'), { formatter: function(cell) {
        var v = cell.getValue(); return v ? '<a href="https://www.amazon.com/dp/' + plEsc(v) + '" target="_blank" rel="noopener" style="color:' + C.accent + ';text-decoration:none">' + plEsc(v) + '</a>' : '';
      } }),
      Object.assign({ title: 'Listings', field: 'listings', width: 90, tooltip: 'Click to see every listing on this barcode' }, num, plHf('number'), { formatter: function(cell) {
        var v = cell.getValue(); return v ? '<span style="color:' + C.accent + ';cursor:pointer;text-decoration:underline dotted">' + v + '</span>' : '';
      }, cellClick: function(e, cell) { var r = cell.getRow().getData(); if (r.listings) setCandRow(r); } }),
      Object.assign({ title: 'Amazon Title', field: 'amz_title', width: 280, tooltip: true }, plHf('text')),
      Object.assign({ title: 'Buy Box Now', field: 'bb_now', width: 110, formatter: money }, num, plHf('number')),
      Object.assign({ title: '90-Day Buy Box', field: 'bb_avg90', width: 110, formatter: money }, num, plHf('number')),
      Object.assign({ title: 'Sell Price', field: 'sell', width: 100, formatter: money, tooltip: function(e, cell) { return cell.getRow().getData().sell_basis || ''; } }, num, plHf('number')),
      Object.assign({ title: 'Landed Cost', field: 'landed', width: 110, formatter: money, visible: false }, num, plHf('number')),
      Object.assign({ title: 'Profit', field: 'profit', width: 100, formatter: function(cell) {
        var v = cell.getValue(); if (v == null) return ''; return '<span style="color:' + (v > 0 ? C.success : C.danger) + '">' + (v < 0 ? '-$' : '$') + Math.abs(v).toFixed(2) + '</span>';
      } }, num, plHf('number')),
      Object.assign({ title: 'ROI', field: 'roi', width: 90, formatter: function(cell) {
        var v = cell.getValue(); if (v == null) return ''; return '<span style="font-weight:600;color:' + (v >= 0.3 ? C.success : v >= 0.1 ? C.warning : C.danger) + '">' + (v * 100).toFixed(0) + '%</span>';
      } }, num, plHf('number', { pct: true })),
      Object.assign({ title: 'Amazon Buy Box % (90d)', field: 'amazon_bb_share', width: 120, formatter: function(cell) { var v = cell.getValue(); return v == null ? '' : '<span style="color:' + (v >= 50 ? C.danger : C.text) + '">' + Math.round(v) + '%</span>'; } }, num, plHf('number')),
      Object.assign({ title: 'Buy Box', field: 'bb_fba', width: 90 }, L),
      Object.assign({ title: 'Sold / Month', field: 'monthly_sold', width: 100 }, num, plHf('number')),
      Object.assign({ title: 'Rank Drops 90d', field: 'drops90', width: 110 }, num, plHf('number')),
      Object.assign({ title: 'Rank Drops 30d', field: 'drops30', width: 110, visible: false }, num, plHf('number')),
      Object.assign({ title: 'Rank Drops 180d', field: 'drops180', width: 110, visible: false }, num, plHf('number')),
      Object.assign({ title: 'Sales Rank', field: 'rank', width: 100, visible: false }, num, plHf('number')),
      Object.assign({ title: 'Offers', field: 'new_offers', width: 80 }, num, plHf('number')),
      Object.assign({ title: 'Buy Box Sellers (90d)', field: 'bb_sellers', width: 110, visible: false }, num, plHf('number')),
      Object.assign({ title: 'Top Seller % (90d)', field: 'top_seller_share', width: 110, visible: false, formatter: function(cell) { var v = cell.getValue(); return v == null ? '' : Math.round(v) + '%'; } }, num, plHf('number')),
      Object.assign({ title: 'Amazon Out of Stock % (90d)', field: 'amazon_oos_pct90', width: 120, visible: false, formatter: function(cell) { var v = cell.getValue(); return v == null ? '' : Math.round(v) + '%'; } }, num, plHf('number')),
      Object.assign({ title: 'FBA Fee', field: 'fba_fee', width: 90, formatter: money, visible: false }, num, plHf('number')),
      Object.assign({ title: 'Referral %', field: 'referral_pct', width: 90, visible: false, formatter: function(cell) { var v = cell.getValue(); return v == null ? '' : Number(v).toFixed(1) + '%'; } }, num, plHf('number')),
      Object.assign({ title: 'Pack Qty', field: 'package_qty', width: 80, visible: false }, num, plHf('number')),
      Object.assign({ title: 'Amazon Brand', field: 'amz_brand', width: 140, visible: false }, L),
      Object.assign({ title: 'Amazon Category', field: 'amz_category', width: 220, visible: false, tooltip: true }, plHf('text')),
      Object.assign({ title: 'Pulled', field: 'pulled', width: 100, visible: false }, plHf('date')),
      Object.assign({ title: 'Flags', field: 'flags_txt', width: 280, tooltip: true }, plHf('text'), { formatter: function(cell) { var v = cell.getValue(); return v ? '<span style="color:' + C.warning + '">' + plEsc(v) + '</span>' : ''; } }),
    ];
  };

  // build the grid once the vendor's rows are in
  useEffect(function() {
    if (!data || !gridEl.current) return;
    var cancelled = false;
    plLoadTabulator().then(function(Tabulator) {
      if (cancelled || !gridEl.current) return;
      if (!document.getElementById('pl-grid-css')) { var st = document.createElement('style'); st.id = 'pl-grid-css'; document.head.appendChild(st); }
      document.getElementById('pl-grid-css').textContent = plGridCss();
      var t = new Tabulator(gridEl.current, {
        data: visibleRows(), index: 'id', columns: ausColumns(), height: 'calc(100vh - 252px)', layout: 'fitData', placeholder: 'No items', debugInvalidOptions: false,
        rowHeader: { formatter: 'rownum', headerSort: false, hozAlign: 'center', resizable: false, frozen: true, width: 46 },
        selectableRange: true, selectableRangeColumns: true, selectableRangeRows: true, selectableRangeClearCells: false,
        clipboard: 'copy', clipboardCopyStyled: false, clipboardCopyConfig: { rowHeaders: false, columnHeaders: false }, clipboardCopyRowRange: 'range',
        columnDefaults: { headerSort: true, headerSortClickElement: 'icon', resizable: 'header', headerWordWrap: true, editor: false, headerFilterPlaceholder: '' },
        movableColumns: true, history: false,
        pagination: 'local', paginationSize: 100, paginationSizeSelector: [25, 50, 100, 200, 400, 800], paginationCounter: 'rows', paginationButtonCount: 5,
        // only width / visibility (+ order) are remembered: 'columns: true' stored whole definitions and put the old plain
        // filter box back over the new filters (10-04); new ID drops layouts saved that way
        persistence: { columns: ['width', 'visible'], page: { size: true, page: false } }, persistenceID: 'research-amazon-us-v3',
      });
      tableRef.current = t;
      t.on('tableBuilt', function() { setGridVer(function(n) { return n + 1; }); });
      t.on('dataFiltered', function() { setFilterSummary(plFilterSummary(t, funnelsRef.current)); });
    }).catch(function(e) { showToast(e.message, 'error'); });
    return function() { cancelled = true; if (tableRef.current) { tableRef.current.destroy(); tableRef.current = null; } };
  }, [data]);
  // chips / search / latest-only: new row set, back to page 1
  useEffect(function() {
    var t = tableRef.current; if (!t || !gridVer) return;
    // the funnel filter is set here, not in tableBuilt (setting it there crashed Tabulator's first row render)
    if (!t._plFunnelSet) { t.setFilter(function(r) { return plFunnelPass(r, funnelsRef.current); }); t._plFunnelSet = true; }
    t.setData(visibleRows());
  }, [filter, search, latestOnly, gridVer]);
  useEffect(function() { var t = tableRef.current; if (!t || !gridVer || !amz) return; t.updateData(visibleRows()); }, [amz, gridVer]);
  // theme switch while the page is open
  useEffect(function() { var st = document.getElementById('pl-grid-css'); if (st) { var css = plGridCss(); if (st.textContent !== css) st.textContent = css; } });

  var vendorPicker = h('div', { style: { width: 320 } }, h(SearchSelect, {
    options: (vendorRows || []).map(function(v) { return { value: String(v.vendor_id), label: v.vendor_name, sub: v.items + ' items' }; }),
    value: vid ? String(vid) : '', placeholder: 'Pick a vendor…', onChange: function(v) { if (v) onNavigate('amazon-research', v); } }));
  var head = h('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
    h('div', { style: { flex: 1 } },
      h('div', { style: { fontSize: 20, fontWeight: 700, color: C.text } }, 'Amazon US'),
      h('div', { style: { fontSize: 13, color: C.textMuted, marginTop: 2 } }, 'Vendor items against our catalog and Amazon (Keepa). Verdicts and first orders come next.')),
    vid && data && h(SearchInput, { type: 'text', value: search, onChange: function(e) { setSearch(e.target.value); }, placeholder: 'Search description, item #, barcode, SKU…',
      style: { width: 260, background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 8, fontSize: 13, outline: 'none' } }),
    vendorPicker,
    vid && canWrite && h(Btn, { size: 'sm', disabled: busy, onClick: function() {
      setBusy(true);
      api('/research/data/vendors/' + vid + '/rematch', { method: 'POST' }).then(function(r) { showToast('Re-matched ' + r.total.toLocaleString() + ' items: ' + r.changed + ' changed'); load(); })
        .catch(function(e) { showToast(e.message, 'error'); }).finally(function() { setBusy(false); });
    } }, h(LIcon, { name: 'refresh-cw', size: 13 }), busy ? 'Matching…' : 'Re-Match'),
    h(Btn, { size: 'sm', onClick: function() { setLookupOpen(true); } }, h(LIcon, { name: 'search', size: 13 }), 'Keepa Lookup'),
    vid && h(Btn, { size: 'sm', onClick: function() { onNavigate('research-data', vid); } }, 'Open Sheet'));
  var statusLine = h(PlAmzStatus, { status: amzStatus, canWrite: canWrite, vid: vid, showToast: showToast,
    onChanged: function() { if (vid) loadAmz(); else api('/research/amazon/status').then(setAmzStatus).catch(function() {}); } });
  var lookup = h(PlKeepaLookup, { open: lookupOpen, onClose: function() { setLookupOpen(false); }, showToast: showToast, canWrite: canWrite });
  if (!vid) {
    return h('div', { style: { display: 'flex', flexDirection: 'column', gap: 14 } }, head, statusLine, lookup,
      h(DataTable, { columns: [
        { key: 'vendor_name', label: 'Vendor', render: function(r) { return h('span', { style: { fontWeight: 600 } }, r.vendor_name); } },
        { key: 'on_latest', label: 'On Latest List', render: function(r) { return plCount(r.on_latest); } },
        { key: 'in_catalog', label: 'In Catalog', render: function(r) { return plCount(r.in_catalog, PL_STATUS.in_catalog.color); } },
        { key: 'other_vendor', label: 'Other Vendor', render: function(r) { return plCount(r.other_vendor, PL_STATUS.other_vendor.color); } },
        { key: 'no_fba', label: 'No FBA SKU', render: function(r) { return plCount(r.no_fba, PL_STATUS.no_fba.color); } },
        { key: 'archived', label: 'Archived', render: function(r) { return plCount(r.archived, PL_STATUS.archived.color); } },
        { key: 'new_items', label: 'New', render: function(r) { return plCount(r.new_items, PL_STATUS['new'].color); } },
        { key: 'check_match', label: 'Check Match', render: function(r) { return plCount(r.check_match, PL_STATUS.check.color); } },
      ], data: vendorRows || [], storageKey: 'research-amazon-us-vendors',
        onRowClick: function(r) { onNavigate('amazon-research', r.vendor_id); }, rowHref: function(r) { return '/research/amazon-us/' + r.vendor_id; },
        emptyTitle: vendorRows == null ? 'Loading…' : 'No vendor sheets yet', emptySub: 'Upload price lists in Research → Data.' }));
  }
  if (!data) return h('div', { style: { display: 'flex', flexDirection: 'column', gap: 14 } }, head, h('div', { style: { padding: 30, color: C.textMuted } }, 'Loading…'));
  var base = data.items.filter(function(r) { return !latestOnly || (r.on_latest !== false); });
  var count = function(k) { return base.filter(function(r) { return r.catalog_status === k; }).length; };
  var vendorName = ((vendorRows || []).find(function(v) { return v.vendor_id === vid; }) || {}).vendor_name || 'Vendor';
  var gridCols = tableRef.current ? tableRef.current.getColumns().filter(function(c) { return c.getField(); }) : [];
  return h('div', { style: { display: 'flex', flexDirection: 'column', gap: 12 } }, head, statusLine, lookup,
    h(PlCandidates, { row: candRow, onClose: function() { setCandRow(null); } }),
    h('div', { style: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' } },
      plChip(filter === 'all', 'All (' + base.length.toLocaleString() + ')', function() { setFilter('all'); }),
      Object.keys(PL_STATUS).map(function(k) { return plChip(filter === k, PL_STATUS[k].label + ' (' + count(k).toLocaleString() + ')', function() { setFilter(k); }, PL_STATUS[k].color, PL_STATUS[k].hint); }),
      plChip(filter === 'cost_up', 'Dearer Than Ours', function() { setFilter('cost_up'); }),
      plChip(filter === 'cost_down', 'Cheaper Than Ours', function() { setFilter('cost_down'); }),
      plChip(filter === 'flagged', 'Flagged', function() { setFilter('flagged'); }, C.warning),
      h('label', { style: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.textMuted, marginLeft: 8, cursor: 'pointer' } },
        h('input', { type: 'checkbox', checked: latestOnly, onChange: function(e) { setLatestOnly(e.target.checked); } }), 'On the latest list only'),
      h('div', { style: { flex: 1 } }),
      h(Btn, { size: 'sm', onClick: function() { setColsOpen(true); } }, 'Columns'),
      h(Btn, { size: 'sm', onClick: function() { plExport(tableRef.current, vendorName + ' - Amazon US.xlsx', 'Amazon US'); } }, h(LIcon, { name: 'download', size: 13 }), 'Export')),
    h('div', { ref: gridEl, className: 'pl-grid' }),
    h(PlFilterBar, { summary: filterSummary, onClear: clearFilters }),
    h(Modal, { open: colsOpen, onClose: function() { setColsOpen(false); }, title: 'Columns', width: 380 },
      h('div', { style: { display: 'flex', flexDirection: 'column', gap: 6 } },
        h('div', { style: { fontSize: 12, color: C.textDim, marginBottom: 4 } }, 'Remembered in this browser. Drag headers to reorder.'),
        gridCols.map(function(c) {
          return h('label', { key: c.getField(), style: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: C.text, cursor: 'pointer' } },
            h('input', { type: 'checkbox', defaultChecked: c.isVisible(), onChange: function(e) { if (e.target.checked) c.show(); else c.hide(); } }),
            c.getDefinition().title);
        }))));
}
window.AmazonUsPage = AmazonUsPage;

;
/* ===== app-reports.js ===== */
// ─── Reports Module (Library edition, 2026-08) ───
// The Reports page is a LIBRARY of curated, plain-English reports served by
// GET /api/reports/catalog. There is no free-form builder anymore: every report
// opens pre-configured, runs immediately, and exposes only a date range plus
// dropdown parameters whose choices come from live data (GET /api/reports/values),
// so it is impossible to filter on a value that doesn't exist.
// Saved reports from the old builder still run via a read-only legacy view.
// Exports: window.ReportsPage

var { createElement: h, Fragment } = React;

// ─── Module-level state cache ─────────────────────────────────────────────
// Preserves page + open-report state across drill-down navigation (PO/product
// links) within the SPA session. Wiped on hard refresh.
var _reportsPageCache = {
  tab: null,        // 'library' | 'saved' | 'schedules'
  view: null,       // { mode: 'library' } | { mode: 'run', entryId, savedMeta } | { mode: 'legacy', saved }
  runState: null,   // { entryId, savedId, params, datePreset, dateRange, result }
  legacyResult: null, // { savedId, result }
};

// ─── Date range presets ───
// Anchored to TODAY IN NEW YORK (business dates are ET; the server runs reports in ET and
// the scheduler's resolvePreset uses the same anchor). Day math runs on Date.UTC over the
// ET calendar day, so neither the viewer's zone nor DST can shift a date.
function presetRange(key, nowOverride) {
  var p = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', year: 'numeric', month: 'numeric', day: 'numeric' })
    .formatToParts(nowOverride || new Date()).reduce(function(a, x) { a[x.type] = x.value; return a; }, {});
  var Y = +p.year, M = (+p.month) - 1, D = +p.day;
  var fmt = function(y, m0, d) { return new Date(Date.UTC(y, m0, d)).toISOString().slice(0, 10); };
  var today = fmt(Y, M, D);
  switch (key) {
    case 'today':       return { start: today, end: today };
    case 'yesterday':   return { start: fmt(Y, M, D - 1), end: fmt(Y, M, D - 1) };
    case '7d':          return { start: fmt(Y, M, D - 6), end: today };
    case '30d':         return { start: fmt(Y, M, D - 29), end: today };
    case '90d':         return { start: fmt(Y, M, D - 89), end: today };
    case '365d':        return { start: fmt(Y, M, D - 364), end: today };
    case 'mtd':         return { start: fmt(Y, M, 1), end: today };
    case 'qtd':         { var q = Math.floor(M / 3) * 3; return { start: fmt(Y, q, 1), end: today }; }
    case 'ytd':         return { start: fmt(Y, 0, 1), end: today };
    case 'last_month':  return { start: fmt(Y, M - 1, 1), end: fmt(Y, M, 0) };
    case 'last_year':   return { start: fmt(Y - 1, 0, 1), end: fmt(Y - 1, 11, 31) };
    default:            return { start: '', end: '' };
  }
}

var DATE_PRESETS = [
  { key: '7d',         label: '7 Days' },
  { key: '30d',        label: '30 Days' },
  { key: '90d',        label: '90 Days' },
  { key: '365d',       label: '365 Days' },
  { key: 'mtd',        label: 'Month to Date' },
  { key: 'ytd',        label: 'Year to Date' },
  { key: 'last_month', label: 'Last Month' },
  { key: 'custom',     label: 'Custom…' },
];
function datePresetLabel(key) {
  if (key === 'today') return 'Today';
  if (key === 'yesterday') return 'Yesterday';
  var p = DATE_PRESETS.find(function(x) { return x.key === key; });
  return p ? p.label : key;
}

// ─── Number/value formatters ───
function formatValue(v, type) {
  if (v == null || v === '') return '';
  if (type === 'currency') {
    var n = Number(v);
    if (isNaN(n)) return v;
    return '$' + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  if (type === 'integer') {
    var n = Number(v);
    if (isNaN(n)) return v;
    return Math.round(n).toLocaleString();
  }
  if (type === 'number') {
    var n = Number(v);
    if (isNaN(n)) return v;
    return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  if (type === 'percentage') {
    var n = Number(v);
    if (isNaN(n)) return v;
    return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';
  }
  if (type === 'date') {
    if (v instanceof Date) return v.toISOString().slice(0, 10);
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
    return v;
  }
  return String(v);
}

// ─── Chart palette (dark-theme friendly) ───
var CHART_PALETTE = [
  '#4F6EF7', '#10B981', '#F59E0B', '#EF4444', '#A855F7',
  '#06B6D4', '#EC4899', '#84CC16', '#F97316', '#6366F1',
  '#14B8A6', '#FB923C', '#E879F9', '#22C55E', '#3B82F6',
];

// Determine which chart types are valid for the current result shape
function chartTypesFor(result) {
  if (!result || !result.columns) return [];
  var dims = result.columns.filter(function(c) { return c.kind === 'dimension'; });
  var meas = result.columns.filter(function(c) { return c.kind === 'measure'; });
  if (dims.length === 0 || meas.length === 0) return [];
  var types = ['bar', 'line'];
  if (dims.length === 1 && meas.length === 1) types.push('pie');
  if (dims.length >= 2) types.push('stacked');
  return types;
}

// ─── ReportChart — renders a Chart.js canvas from result ───
function ReportChart({ result, chartType }) {
  var canvasRef = useRef(null);
  var chartInstance = useRef(null);

  useEffect(function() {
    if (!result || !result.columns || result.rowCount === 0 || typeof Chart === 'undefined') return;
    if (!canvasRef.current) return;

    var dims = result.columns.filter(function(c) { return c.kind === 'dimension'; });
    var meas = result.columns.filter(function(c) { return c.kind === 'measure'; });
    if (dims.length === 0 || meas.length === 0) return;

    if (chartInstance.current) { chartInstance.current.destroy(); chartInstance.current = null; }

    var firstDim = dims[0];
    var firstMeas = meas[0];
    var labels, datasets;

    var fmtAxis = function(v) {
      if (firstMeas.type === 'currency') return '$' + (Math.abs(v) >= 1000000 ? (v / 1000000).toFixed(1) + 'M' : Math.abs(v) >= 1000 ? (v / 1000).toFixed(0) + 'k' : v);
      if (firstMeas.type === 'percentage') return Number(v).toFixed(1) + '%';
      if (Math.abs(v) >= 1000000) return (v / 1000000).toFixed(1) + 'M';
      if (Math.abs(v) >= 1000) return (v / 1000).toFixed(0) + 'k';
      return v;
    };
    var fmtTooltip = function(v, type) {
      if (v == null) return '';
      if (type === 'currency') return '$' + Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      if (type === 'integer') return Math.round(Number(v)).toLocaleString();
      if (type === 'percentage') return Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';
      return Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 });
    };

    if (chartType === 'stacked' && dims.length >= 2) {
      // First dim = x-axis, second dim = stacked series, first measure = value
      var secondDim = dims[1];
      var xKeys = [];
      var seriesKeys = [];
      var seen = {};
      var seriesSeen = {};
      result.rows.forEach(function(r) {
        var x = r[firstDim.key];
        var s = r[secondDim.key];
        if (!seen[x]) { seen[x] = true; xKeys.push(x); }
        if (!seriesSeen[s]) { seriesSeen[s] = true; seriesKeys.push(s); }
      });
      // Build matrix: { xKey: { seriesKey: value } }
      var matrix = {};
      result.rows.forEach(function(r) {
        var x = r[firstDim.key];
        var s = r[secondDim.key];
        if (!matrix[x]) matrix[x] = {};
        matrix[x][s] = Number(r[firstMeas.key]) || 0;
      });
      labels = xKeys.map(function(x) { return formatValue(x, firstDim.type); });
      datasets = seriesKeys.map(function(s, i) {
        return {
          label: s == null ? '(none)' : String(s),
          data: xKeys.map(function(x) { return matrix[x][s] || 0; }),
          backgroundColor: CHART_PALETTE[i % CHART_PALETTE.length],
          stack: 'stack1',
          borderRadius: 2,
        };
      });
    } else {
      // Bar / line / pie: x = first dim, datasets = each measure
      labels = result.rows.map(function(r) { return formatValue(r[firstDim.key], firstDim.type); });
      datasets = meas.map(function(m, i) {
        var color = CHART_PALETTE[i % CHART_PALETTE.length];
        var d = {
          label: m.label,
          data: result.rows.map(function(r) { return Number(r[m.key]) || 0; }),
          backgroundColor: chartType === 'pie'
            ? result.rows.map(function(_, idx) { return CHART_PALETTE[idx % CHART_PALETTE.length]; })
            : (chartType === 'line' ? color + '20' : color),
          borderColor: color,
          borderWidth: chartType === 'line' ? 2 : 1,
          tension: chartType === 'line' ? 0.3 : 0,
          fill: chartType === 'line',
          pointRadius: chartType === 'line' ? 3 : 0,
          borderRadius: chartType === 'bar' ? 2 : 0,
        };
        return d;
      });
    }

    var stacked = chartType === 'stacked';
    var renderType = (chartType === 'stacked') ? 'bar' : chartType;

    var options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: chartType === 'pie' ? 'right' : 'top',
          labels: { color: C.textMuted, font: { size: 11, family: 'DM Sans' }, boxWidth: 12, padding: 16 }
        },
        tooltip: {
          mode: chartType === 'pie' ? 'nearest' : 'index',
          intersect: false,
          callbacks: {
            label: function(ctx) {
              var v = ctx.parsed && (ctx.parsed.y != null ? ctx.parsed.y : ctx.parsed);
              var measType = (chartType === 'stacked') ? firstMeas.type : (meas[ctx.datasetIndex] && meas[ctx.datasetIndex].type);
              return ctx.dataset.label + ': ' + fmtTooltip(v, measType);
            }
          }
        }
      },
      scales: chartType === 'pie' ? {} : {
        x: { stacked: stacked, grid: { color: C.border + '40' }, ticks: { color: C.textMuted, font: { size: 10 }, maxRotation: 45 } },
        y: { stacked: stacked, grid: { color: C.border + '40' }, ticks: { color: C.textMuted, font: { size: 10 }, callback: fmtAxis } }
      },
      interaction: chartType === 'pie' ? { mode: 'nearest' } : { mode: 'index', intersect: false },
    };

    chartInstance.current = new Chart(canvasRef.current, {
      type: renderType,
      data: { labels: labels, datasets: datasets },
      options: options,
    });

    return function() { if (chartInstance.current) { chartInstance.current.destroy(); chartInstance.current = null; } };
  }, [result, chartType]);

  return h('div', { style: { position: 'relative', height: 380 } },
    h('canvas', { ref: canvasRef })
  );
}

// ─── Result Table (with viz toggle) ───
function ReportResultTable({ result, onNavigate, initialViz }) {
  var availableCharts = useMemo(function() { return chartTypesFor(result); }, [result]);
  var [viz, setViz] = useState(function() {
    if (initialViz && initialViz !== 'table' && availableCharts.indexOf(initialViz) !== -1) return initialViz;
    return 'table';
  });

  // Reset to table when result changes shape
  useEffect(function() {
    if (viz !== 'table' && availableCharts.indexOf(viz) === -1) setViz('table');
  }, [availableCharts.join(','), viz]);

  if (!result) return null;
  if (result.rowCount === 0) {
    return h('div', { style: { padding: 40, textAlign: 'center', color: C.textMuted, fontSize: 13 } }, 'No data — try a wider date range or fewer filters.');
  }

  var cols = result.columns.map(function(col) {
    return {
      key: col.key,
      label: col.label,
      sortKey: col.key,
      defaultVisible: true,
      render: function(row) {
        var v = row[col.key];
        var formatted = formatValue(v, col.type);
        if (col.kind === 'dimension' && col.drillTo && v != null) {
          return h('a', {
            href: '#',
            onClick: function(e) {
              e.preventDefault();
              // Pass returnTo/from so the destination knows where to come back to.
              // Open-report state is auto-synced to _reportsPageCache, so when the
              // user closes the destination, ReportsPage remounts and restores.
              if (col.drillTo === 'po' && row[col.drillKey || 'po_id']) {
                onNavigate('po-detail', String(row[col.drillKey || 'po_id']), { from: 'reports' });
              } else if (col.drillTo === 'product') {
                onNavigate('products', String(v), { returnTo: 'reports' });
              } else if (col.drillTo === 'ticket' && row[col.drillKey || 'ticket_id']) {
                onNavigate('cs', String(row[col.drillKey || 'ticket_id'])); // /cs/:id (review M6 #78)
              }
            },
            style: { color: C.accent, textDecoration: 'none', fontWeight: 500 }
          }, formatted);
        }
        if (col.kind === 'measure') {
          return h('span', { style: { fontWeight: 600, color: C.text } }, formatted);
        }
        return formatted;
      }
    };
  });

  var vizOptions = [{ key: 'table', label: 'Table', icon: 'table' }].concat(availableCharts.map(function(c) {
    var labels = { bar: 'Bar', line: 'Line', pie: 'Pie', stacked: 'Stacked Bar' };
    var icons  = { bar: 'bar-chart-3', line: 'line-chart', pie: 'pie-chart', stacked: 'bar-chart-4' };
    return { key: c, label: labels[c], icon: icons[c] };
  }));

  return h('div', null,
    h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, fontSize: 12, color: C.textMuted, gap: 12, flexWrap: 'wrap' } },
      h('span', null, result.rowCount.toLocaleString(), ' row', result.rowCount === 1 ? '' : 's', ' • ', result.durationMs, ' ms', result.truncated ? ' • truncated' : ''),
      // Viz toggle
      vizOptions.length > 1 && h('div', { style: { display: 'inline-flex', background: C.bg, border: '1px solid ' + C.border, borderRadius: 6, padding: 2 } },
        vizOptions.map(function(opt) {
          var active = viz === opt.key;
          return h('button', {
            key: opt.key,
            onClick: function() { setViz(opt.key); },
            title: opt.label,
            style: {
              background: active ? C.accent : 'transparent',
              color: active ? '#fff' : C.textMuted,
              border: 'none', cursor: 'pointer', padding: '5px 10px', borderRadius: 4,
              fontSize: 11, fontWeight: 500, fontFamily: 'inherit',
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }
          }, h(LIcon, { name: opt.icon, size: 12 }), opt.label);
        })
      )
    ),
    viz === 'table'
      ? h(DataTable, {
          // Force remount when result columns change so visibility/sort/filter state resets
          // (DataTable initializes `visible` once via useState; without a fresh key, columns
          // from a new report end up undefined → filtered out as hidden)
          key: cols.map(function(c) { return c.key; }).join('|'),
          columns: cols,
          data: result.rows,
          emptyTitle: 'No Results',
          emptySub: 'Adjust filters or date range',
        })
      : h(ReportChart, { result: result, chartType: viz })
  );
}

// ─── Save Report Modal ───
function SaveReportModal({ open, onClose, onSave, initialName, initialDescription, isUpdate }) {
  var [name, setName] = useState(initialName || '');
  var [description, setDescription] = useState(initialDescription || '');
  useEffect(function() { if (open) { setName(initialName || ''); setDescription(initialDescription || ''); } }, [open]);

  var save = function() {
    if (!name.trim()) return;
    onSave({ name: name.trim(), description: description.trim() });
  };

  return h(Modal, { open: open, onClose: onClose, title: isUpdate ? 'Update Saved Report' : 'Save Report', width: 480 },
    h('div', { style: { display: 'flex', flexDirection: 'column', gap: 14 } },
      h('div', { style: { fontSize: 12, color: C.textMuted, lineHeight: 1.5 } },
        'Saves this report with its current marketplace/vendor filters and date window. Saved reports can be emailed on a schedule from the Schedules tab.'),
      h(Inp, { label: 'Name', value: name, onChange: function(e) { setName(e.target.value); }, placeholder: 'COGS — Mercado Libre — monthly' }),
      h('div', { style: { display: 'flex', flexDirection: 'column', gap: 4 } },
        h('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' } }, 'Description'),
        h('textarea', {
          value: description,
          onChange: function(e) { setDescription(e.target.value); },
          rows: 3,
          placeholder: 'Optional notes about this report',
          style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', outline: 'none', resize: 'vertical' }
        })
      ),
      h('div', { style: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 } },
        h(Btn, { onClick: onClose }, 'Cancel'),
        h(Btn, { variant: 'primary', onClick: save, disabled: !name.trim() }, isUpdate ? 'Update' : 'Save')
      )
    )
  );
}

// ─── Field-values fetcher (param dropdown options; full lists cached 5 min,
// `q` searches for high-cardinality fields run live and are never cached) ──
var _reportsValuesCache = {}; // 'source|field' -> { at, promise }
function fetchReportFieldValues(source, field, q) {
  var base = '/reports/values?source=' + encodeURIComponent(source) + '&field=' + encodeURIComponent(field);
  if (q) {
    return api(base + '&q=' + encodeURIComponent(q)).then(function(res) { return res.values || []; });
  }
  var key = source + '|' + field;
  var hit = _reportsValuesCache[key];
  if (hit && (Date.now() - hit.at) < 5 * 60 * 1000) return hit.promise;
  var promise = api(base)
    .then(function(res) { return res.values || []; })
    .catch(function(e) { delete _reportsValuesCache[key]; throw e; });
  _reportsValuesCache[key] = { at: Date.now(), promise: promise };
  return promise;
}

// ─── ReportParamSelect moved to app-utils.js (shared across bundles) ──────

// ─── ReportDatePills — preset chips + custom range inputs ─────────────────
function ReportDatePills({ datePreset, dateRange, onChange }) {
  var pill = function(active) {
    return {
      background: active ? C.accent : C.bg,
      color: active ? '#fff' : C.textMuted,
      border: '1px solid ' + (active ? C.accent : C.border),
      padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 500,
      cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
    };
  };
  return h('div', { style: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' } },
    h(LIcon, { name: 'calendar', size: 14, color: C.textMuted }),
    DATE_PRESETS.map(function(p) {
      return h('button', {
        key: p.key,
        onClick: function() {
          if (p.key === 'custom') onChange('custom', dateRange);
          else onChange(p.key, presetRange(p.key));
        },
        style: pill(datePreset === p.key),
      }, p.label);
    }),
    datePreset === 'custom' && h('div', { style: { display: 'flex', gap: 6, alignItems: 'center' } },
      h('input', { type: 'date', value: dateRange.start || '', onChange: function(e) { onChange('custom', { start: e.target.value, end: dateRange.end }); }, style: { background: C.bg, color: C.text, border: '1px solid ' + C.border, padding: '5px 8px', borderRadius: 6, fontSize: 12 } }),
      h('span', { style: { color: C.textMuted, fontSize: 12 } }, 'to'),
      h('input', { type: 'date', value: dateRange.end || '', onChange: function(e) { onChange('custom', { start: dateRange.start, end: e.target.value }); }, style: { background: C.bg, color: C.text, border: '1px solid ' + C.border, padding: '5px 8px', borderRadius: 6, fontSize: 12 } })
    )
  );
}

// ─── Compile a catalog entry + user choices into an engine config ─────────
function compileReportConfig(entry, params, datePreset, dateRange) {
  var cfg = {
    source: entry.source,
    dimensions: (entry.config.dimensions || []).slice(),
    measures: (entry.config.measures || []).slice(),
    filters: (entry.config.filters || []).slice(),
    limit: entry.config.limit || 1000,
  };
  if (entry.config.sort) cfg.sort = entry.config.sort;
  Object.keys(params || {}).forEach(function(field) {
    var vals = params[field];
    if (vals && vals.length) cfg.filters.push({ field: field, op: 'in', value: vals });
  });
  if (entry.dateless) {
    // Snapshot-style report: the catalog decides the window (if any); no user date UI.
    if (entry.config.dateRangePreset) {
      cfg.dateRangePreset = entry.config.dateRangePreset;
      cfg.dateRange = presetRange(entry.config.dateRangePreset);
    }
  } else if (datePreset && datePreset !== 'custom') {
    cfg.dateRangePreset = datePreset; // scheduler re-resolves this into a rolling window
    cfg.dateRange = presetRange(datePreset);
  } else if (dateRange && (dateRange.start || dateRange.end)) {
    cfg.dateRange = dateRange;
  }
  return cfg;
}

function reportParamSummary(entry, params) {
  var parts = [];
  (entry.params || []).forEach(function(p) {
    var vals = (params || {})[p.key];
    if (vals && vals.length) parts.push(p.label + ': ' + vals.join(', '));
  });
  return parts;
}

// ─── Empty-state that explains itself ─────────────────────────────────────
function ReportEmptyState({ entry, params, datePreset, dateRange }) {
  var activeParams = reportParamSummary(entry, params);
  var windowText = entry.dateless ? null
    : datePreset === 'custom'
      ? ((dateRange.start || '…') + ' to ' + (dateRange.end || 'today'))
      : datePresetLabel(datePreset);
  return h('div', { style: { padding: '40px 24px', textAlign: 'center' } },
    h(LIcon, { name: 'search', size: 28, color: C.textDim }),
    h('div', { style: { fontSize: 15, fontWeight: 600, color: C.text, marginTop: 12 } }, 'Nothing matched this view'),
    h('div', { style: { fontSize: 13, color: C.textMuted, marginTop: 10, lineHeight: 1.7, maxWidth: 460, margin: '10px auto 0' } },
      windowText && h('div', null, 'Date window: ', h('strong', { style: { color: C.text } }, windowText)),
      activeParams.map(function(p, i) { return h('div', { key: i }, 'Filter — ', h('strong', { style: { color: C.text } }, p)); }),
      h('div', { style: { marginTop: 12 } },
        activeParams.length
          ? 'The filter values exist in your data, but not in this date window. Try a wider window or reset a filter to "All".'
          : 'There are simply no records in this date window. Try widening it.'
      )
    )
  );
}

// ─── ReportRunView — an open library report: params + dates + live result ──
function ReportRunView({ entry, savedMeta: initialSavedMeta, initialParams, initialDatePreset, initialDateRange, onBack, showToast, onNavigate, onSavedRefresh, onSchedule }) {
  var cached = _reportsPageCache.runState;
  var hasCache = cached && cached.entryId === entry.id &&
    String(cached.savedId || '') === String((initialSavedMeta && initialSavedMeta.id) || '');

  var [savedMeta, setSavedMeta] = useState(initialSavedMeta || null);
  var [params, setParams] = useState(hasCache ? cached.params : (initialParams || {}));
  var [datePreset, setDatePreset] = useState(
    hasCache ? cached.datePreset : (initialDatePreset || entry.config.dateRangePreset || '30d')
  );
  var [dateRange, setDateRange] = useState(
    hasCache ? cached.dateRange
      : (initialDateRange || presetRange(initialDatePreset || entry.config.dateRangePreset || '30d'))
  );
  var [result, setResult] = useState(hasCache ? cached.result : null);
  var [running, setRunning] = useState(false);
  var [saveOpen, setSaveOpen] = useState(false);
  var [paramOptions, setParamOptions] = useState({}); // field -> { values, loading }

  var runSeq = useRef(0);
  var skipFirstRun = useRef(hasCache && !!cached.result);

  // Sync state to module cache so drill-down navigation can restore this view
  useEffect(function() {
    _reportsPageCache.runState = {
      entryId: entry.id, savedId: savedMeta ? savedMeta.id : null,
      params: params, datePreset: datePreset, dateRange: dateRange, result: result,
    };
  }, [entry.id, savedMeta, params, datePreset, dateRange, result]);

  // Load dropdown values for each param (live data only). Search-mode params
  // (high-cardinality, e.g. SKU) fetch on demand as the user types instead.
  useEffect(function() {
    var alive = true;
    var next = {};
    (entry.params || []).forEach(function(p) {
      next[p.key] = p.search ? { values: [], loading: false } : { values: null, loading: true };
    });
    setParamOptions(next);
    (entry.params || []).filter(function(p) { return !p.search; }).forEach(function(p) {
      fetchReportFieldValues(entry.source, p.key).then(function(values) {
        if (!alive) return;
        setParamOptions(function(prev) {
          var upd = Object.assign({}, prev);
          upd[p.key] = { values: values, loading: false };
          return upd;
        });
      }).catch(function() {
        if (!alive) return;
        setParamOptions(function(prev) {
          var upd = Object.assign({}, prev);
          upd[p.key] = { values: [], loading: false };
          return upd;
        });
      });
    });
    return function() { alive = false; };
  }, [entry.id]);

  // Auto-run: immediately on open, and (debounced) whenever params/dates change
  useEffect(function() {
    if (skipFirstRun.current) { skipFirstRun.current = false; return; }
    var seq = ++runSeq.current;
    setRunning(true);
    var t = setTimeout(function() {
      var cfg = compileReportConfig(entry, params, datePreset, dateRange);
      api('/reports/run', { method: 'POST', body: JSON.stringify(cfg) }).then(function(res) {
        if (seq !== runSeq.current) return; // stale response
        setResult(res);
      }).catch(function(e) {
        if (seq !== runSeq.current) return;
        showToast(e.message, 'error');
      }).finally(function() {
        if (seq === runSeq.current) setRunning(false);
      });
    }, 250);
    return function() { clearTimeout(t); };
  }, [entry.id, JSON.stringify(params), datePreset, dateRange.start, dateRange.end]);

  var exportXlsx = function() {
    var cfg = compileReportConfig(entry, params, datePreset, dateRange);
    fetch('/api/reports/export/xlsx', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(cfg),
    }).then(function(res) {
      if (!res.ok) return res.json().then(function(j) { throw new Error(j.error || 'Export failed'); });
      var disp = res.headers.get('content-disposition') || '';
      var m = disp.match(/filename="([^"]+)"/);
      var filename = m ? m[1] : 'report.xlsx';
      return res.blob().then(function(blob) { return { blob: blob, filename: filename }; });
    }).then(function(d) {
      var url = URL.createObjectURL(d.blob);
      var a = document.createElement('a');
      a.href = url; a.download = d.filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }).catch(function(e) { showToast(e.message, 'error'); });
  };

  var save = function(meta) {
    var cfg = compileReportConfig(entry, params, datePreset, dateRange);
    cfg._library = {
      reportId: entry.id,
      params: params,
      datePreset: entry.dateless ? null : datePreset,
      dateRange: (!entry.dateless && datePreset === 'custom') ? dateRange : undefined,
    };
    var body = { name: meta.name, description: meta.description, data_source: entry.source, config: cfg };
    var isUpdate = savedMeta && savedMeta.id;
    var path = isUpdate ? '/reports/saved/' + savedMeta.id : '/reports/saved';
    api(path, { method: isUpdate ? 'PUT' : 'POST', body: JSON.stringify(body) }).then(function(saved) {
      setSavedMeta({ id: saved.id, name: saved.name, description: saved.description || '' });
      setSaveOpen(false);
      showToast(isUpdate ? 'Report updated' : 'Report saved — you can now schedule it', 'success');
      if (onSavedRefresh) onSavedRefresh();
    }).catch(function(e) { showToast(e.message, 'error'); });
  };

  var defaultSaveName = function() {
    if (savedMeta && savedMeta.name) return savedMeta.name;
    var parts = reportParamSummary(entry, params);
    return entry.name + (parts.length ? ' — ' + parts.map(function(p) { return p.split(': ')[1]; }).join(', ') : '');
  };

  return h('div', { style: { display: 'flex', flexDirection: 'column', gap: 14 } },
    // Title row
    h('div', { style: { display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' } },
      h('button', {
        onClick: onBack,
        style: { background: 'transparent', border: '1px solid ' + C.border, color: C.textMuted, borderRadius: 6, padding: '6px 10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontFamily: 'inherit', marginTop: 2 }
      }, h(LIcon, { name: 'arrow-left', size: 13 }), ' Library'),
      h('div', { style: { flex: 1, minWidth: 220 } },
        h('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
          h(LIcon, { name: entry.icon || 'bar-chart-3', size: 18, color: C.accent }),
          h('div', { style: { fontSize: 18, fontWeight: 700, color: C.text } }, entry.name),
          savedMeta && h('span', { style: { fontSize: 11, background: C.successSoft, color: C.success, padding: '2px 8px', borderRadius: 4, fontWeight: 600 } }, 'Saved: ' + savedMeta.name)
        ),
        entry.question && h('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 3 } }, entry.question)
      ),
      h('div', { style: { display: 'flex', gap: 6, flexWrap: 'wrap' } },
        h(Btn, { onClick: function() { setSaveOpen(true); } },
          h(LIcon, { name: 'save', size: 14 }), savedMeta ? ' Update' : ' Save'),
        savedMeta && h(Btn, { onClick: function() { onSchedule && onSchedule({ id: savedMeta.id, name: savedMeta.name }); } },
          h(LIcon, { name: 'clock', size: 14 }), ' Schedule'),
        h(Btn, { onClick: exportXlsx, disabled: !result || result.rowCount === 0 },
          h(LIcon, { name: 'download', size: 14 }), ' Export')
      )
    ),

    // Controls: date window + params
    h(Card, { style: { padding: 14, display: 'flex', flexDirection: 'column', gap: 10 } },
      !entry.dateless && h(ReportDatePills, {
        datePreset: datePreset, dateRange: dateRange,
        onChange: function(preset, range) { setDatePreset(preset); setDateRange(range); },
      }),
      (entry.params || []).length > 0 && h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' } },
        h(LIcon, { name: 'filter', size: 14, color: C.textMuted }),
        (entry.params || []).map(function(p) {
          var opts = paramOptions[p.key] || { values: null, loading: true };
          return h(ReportParamSelect, {
            key: p.key,
            label: p.label,
            values: opts.values,
            loading: opts.loading,
            search: !!p.search,
            source: entry.source,
            fieldKey: p.key,
            selected: params[p.key] || [],
            onChange: function(vals) {
              setParams(function(prev) {
                var next = Object.assign({}, prev);
                if (vals.length) next[p.key] = vals; else delete next[p.key];
                return next;
              });
            },
          });
        })
      ),
      entry.dateless && entry.config.dateRangePreset === 'yesterday' &&
        h('div', { style: { fontSize: 11, color: C.textDim } }, 'Shows the latest daily snapshot.')
    ),

    // Result
    h(Card, { style: { padding: 16, position: 'relative', minHeight: 120 } },
      running && h('div', { style: { position: 'absolute', inset: 0, background: C.surface + 'C0', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5, borderRadius: 8 } },
        h('div', { style: { display: 'inline-flex', alignItems: 'center', gap: 8, color: C.textMuted, fontSize: 13 } },
          h(LIcon, { name: 'loader-2', size: 16, spin: true }), 'Running…')
      ),
      !result && !running && h('div', { style: { padding: 30, textAlign: 'center', color: C.textMuted, fontSize: 13 } }, 'Loading…'),
      result && result.rowCount === 0
        ? h(ReportEmptyState, { entry: entry, params: params, datePreset: datePreset, dateRange: dateRange })
        : result && h(ReportResultTable, { key: entry.id, result: result, onNavigate: onNavigate, initialViz: entry.defaultViz })
    ),

    h(SaveReportModal, {
      open: saveOpen,
      onClose: function() { setSaveOpen(false); },
      onSave: save,
      initialName: defaultSaveName(),
      initialDescription: savedMeta ? (savedMeta.description || '') : (entry.question || ''),
      isUpdate: !!savedMeta,
    })
  );
}

// ─── LegacySavedReportView — saved reports from the old builder (read-only) ──
function LegacySavedReportView({ saved, schema, onBack, showToast, onNavigate, onSchedule }) {
  var cachedRes = _reportsPageCache.legacyResult;
  var [result, setResult] = useState(cachedRes && cachedRes.savedId === saved.id ? cachedRes.result : null);
  var [running, setRunning] = useState(false);

  useEffect(function() {
    _reportsPageCache.legacyResult = { savedId: saved.id, result: result };
  }, [saved.id, result]);

  useEffect(function() {
    if (result) return; // restored from cache
    var cfg = Object.assign({}, saved.config, { source: saved.config.source || saved.data_source });
    if (cfg.dateRangePreset && cfg.dateRangePreset !== 'custom') cfg.dateRange = presetRange(cfg.dateRangePreset);
    setRunning(true);
    api('/reports/run', { method: 'POST', body: JSON.stringify(cfg) }).then(function(res) {
      setResult(res);
    }).catch(function(e) { showToast(e.message, 'error'); }).finally(function() { setRunning(false); });
  }, [saved.id]);

  var exportXlsx = function() {
    fetch('/api/reports/export/xlsx', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ saved_id: saved.id }),
    }).then(function(res) {
      if (!res.ok) return res.json().then(function(j) { throw new Error(j.error || 'Export failed'); });
      var disp = res.headers.get('content-disposition') || '';
      var m = disp.match(/filename="([^"]+)"/);
      var filename = m ? m[1] : 'report.xlsx';
      return res.blob().then(function(blob) { return { blob: blob, filename: filename }; });
    }).then(function(d) {
      var url = URL.createObjectURL(d.blob);
      var a = document.createElement('a');
      a.href = url; a.download = d.filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }).catch(function(e) { showToast(e.message, 'error'); });
  };

  var srcLabel = (schema && schema.sources[saved.data_source] && schema.sources[saved.data_source].label) || saved.data_source;

  return h('div', { style: { display: 'flex', flexDirection: 'column', gap: 14 } },
    h('div', { style: { display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' } },
      h('button', {
        onClick: onBack,
        style: { background: 'transparent', border: '1px solid ' + C.border, color: C.textMuted, borderRadius: 6, padding: '6px 10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontFamily: 'inherit', marginTop: 2 }
      }, h(LIcon, { name: 'arrow-left', size: 13 }), ' Back'),
      h('div', { style: { flex: 1, minWidth: 220 } },
        h('div', { style: { fontSize: 18, fontWeight: 700, color: C.text } }, saved.name),
        h('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 3 } }, srcLabel, saved.description ? ' — ' + saved.description : '')
      ),
      h('div', { style: { display: 'flex', gap: 6 } },
        h(Btn, { onClick: function() { onSchedule && onSchedule({ id: saved.id, name: saved.name }); } },
          h(LIcon, { name: 'clock', size: 14 }), ' Schedule'),
        h(Btn, { onClick: exportXlsx, disabled: !result },
          h(LIcon, { name: 'download', size: 14 }), ' Export')
      )
    ),
    h('div', { style: { background: C.warningSoft || C.accentSoft, border: '1px solid ' + C.border, borderRadius: 8, padding: '10px 14px', fontSize: 12, color: C.textMuted } },
      'This report was saved from the old report builder. It still runs and can be scheduled or exported, but its filters can\'t be edited — open a Library report and save a new version to replace it.'),
    h(Card, { style: { padding: 16, minHeight: 120 } },
      running
        ? h('div', { style: { padding: 30, textAlign: 'center', color: C.textMuted, fontSize: 13 } },
            h(LIcon, { name: 'loader-2', size: 16, spin: true }), ' Running…')
        : result
          ? h(ReportResultTable, { result: result, onNavigate: onNavigate })
          : h('div', { style: { padding: 30, textAlign: 'center', color: C.textMuted, fontSize: 13 } }, 'Loading…')
    )
  );
}

// ─── ReportLibraryView — searchable grouped tiles; the landing page ───────
function ReportLibraryView({ catalog, onOpen }) {
  var [search, setSearch] = useState('');

  var q = search.trim().toLowerCase();
  var matches = function(r) {
    if (!q) return true;
    return (r.name + ' ' + (r.question || '') + ' ' + r.group).toLowerCase().indexOf(q) !== -1;
  };

  return h('div', null,
    h('div', { style: { position: 'relative', maxWidth: 420, marginBottom: 20 } },
      h(LIcon, { name: 'search', size: 14, color: C.textMuted, style: { position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' } }),
      h('input', {
        value: search,
        onChange: function(e) { setSearch(e.target.value); },
        placeholder: 'Search reports… (e.g. "COGS", "returns", "lead time")',
        style: {
          width: '100%', boxSizing: 'border-box', background: C.bg, color: C.text,
          border: '1px solid ' + C.border, borderRadius: 8, padding: '10px 12px 10px 34px',
          fontSize: 13, outline: 'none', fontFamily: 'inherit',
        }
      })
    ),
    catalog.groups.map(function(g) {
      var reports = catalog.reports.filter(function(r) { return r.group === g.key && matches(r); });
      if (!reports.length) return null;
      return h('div', { key: g.key, style: { marginBottom: 26 } },
        h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 } },
          h(LIcon, { name: g.icon, size: 14, color: C.accent }),
          h('div', { style: { fontSize: 12, fontWeight: 700, color: C.text, textTransform: 'uppercase', letterSpacing: '0.06em' } }, g.label)
        ),
        h('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 12 } },
          reports.map(function(r) {
            return h('a', {
              key: r.id,
              href: '/reports?report=' + encodeURIComponent(r.id),
              onClick: function(e) {
                if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return; // new-tab convention
                e.preventDefault();
                onOpen(r);
              },
              onMouseOver: function(e) { e.currentTarget.style.borderColor = C.accent; e.currentTarget.style.background = C.accentSoft; },
              onMouseOut: function(e) { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = C.surface; },
              style: {
                display: 'flex', flexDirection: 'column', gap: 6, textDecoration: 'none',
                background: C.surface, border: '1px solid ' + C.border, borderRadius: 10,
                padding: '14px 16px', transition: 'all 0.15s', cursor: 'pointer',
              }
            },
              h('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
                h(LIcon, { name: r.icon || 'bar-chart-3', size: 15, color: C.accent }),
                h('div', { style: { fontSize: 13, fontWeight: 600, color: C.text, lineHeight: 1.3 } }, r.name)
              ),
              r.question && h('div', { style: { fontSize: 11, color: C.textMuted, lineHeight: 1.45 } }, r.question),
              (r.params || []).length > 0 && h('div', { style: { fontSize: 10, color: C.textDim, marginTop: 'auto' } },
                'Filter by ' + r.params.map(function(p) { return p.label.toLowerCase(); }).join(', '))
            );
          })
        )
      );
    }),
    q && catalog.reports.filter(matches).length === 0 &&
      h('div', { style: { padding: 40, textAlign: 'center', color: C.textMuted, fontSize: 13 } },
        'No reports match "', search, '". Ask for a new report to be added to the library.')
  );
}

// ─── Schedule helpers ───
var DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' }, { value: 1, label: 'Monday' }, { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' }, { value: 4, label: 'Thursday' }, { value: 5, label: 'Friday' }, { value: 6, label: 'Saturday' },
];
function hourLabel(h) {
  if (h === 0) return '12 AM';
  if (h < 12) return h + ' AM';
  if (h === 12) return '12 PM';
  return (h - 12) + ' PM';
}
function describeSchedule(s) {
  if (!s) return '';
  var hr = hourLabel(s.hour_of_day);
  if (s.frequency === 'daily') return 'Daily at ' + hr;
  if (s.frequency === 'weekly') {
    var d = DAYS_OF_WEEK.find(function(x) { return x.value === s.day_of_week; });
    return 'Weekly on ' + (d ? d.label : 'Mon') + ' at ' + hr;
  }
  if (s.frequency === 'monthly') return 'Monthly on day ' + (s.day_of_month || 1) + ' at ' + hr;
  return s.frequency;
}
function formatRelative(d) {
  if (!d) return '—';
  var dt = new Date(d);
  var diff = dt.getTime() - Date.now();
  var abs = Math.abs(diff);
  var mins = Math.round(abs / 60000);
  var hrs  = Math.round(abs / 3600000);
  var days = Math.round(abs / 86400000);
  var s;
  if (mins < 60)      s = mins + 'm';
  else if (hrs < 48)  s = hrs + 'h';
  else                s = days + 'd';
  return diff < 0 ? s + ' ago' : 'in ' + s;
}

// ─── ScheduleModal — create or edit a schedule for a saved report ───
function ScheduleModal({ open, onClose, onSave, onDelete, savedReports, initial, lockedReportId }) {
  var [reportId, setReportId]    = useState(initial && initial.report_id ? String(initial.report_id) : (lockedReportId ? String(lockedReportId) : ''));
  var [frequency, setFrequency]  = useState((initial && initial.frequency) || 'daily');
  var [dayOfWeek, setDayOfWeek]  = useState(initial && initial.day_of_week != null ? initial.day_of_week : 1);
  var [dayOfMonth, setDayOfMonth]= useState(initial && initial.day_of_month != null ? initial.day_of_month : 1);
  var [hourOfDay, setHourOfDay]  = useState(initial && initial.hour_of_day != null ? initial.hour_of_day : 7);
  var [recipients, setRecipients]= useState(initial && initial.recipients ? initial.recipients.join(', ') : '');
  var [format, setFormat]        = useState((initial && initial.format) || 'xlsx');
  var [enabled, setEnabled]      = useState(initial ? initial.enabled !== false : true);
  var [saving, setSaving]        = useState(false);

  useEffect(function() {
    if (!open) return;
    setReportId(initial && initial.report_id ? String(initial.report_id) : (lockedReportId ? String(lockedReportId) : ''));
    setFrequency((initial && initial.frequency) || 'daily');
    setDayOfWeek(initial && initial.day_of_week != null ? initial.day_of_week : 1);
    setDayOfMonth(initial && initial.day_of_month != null ? initial.day_of_month : 1);
    setHourOfDay(initial && initial.hour_of_day != null ? initial.hour_of_day : 7);
    setRecipients(initial && initial.recipients ? initial.recipients.join(', ') : '');
    setFormat((initial && initial.format) || 'xlsx');
    setEnabled(initial ? initial.enabled !== false : true);
  }, [open, initial && initial.id, lockedReportId]);

  var save = function() {
    var emails = recipients.split(/[,;\s]+/).map(function(s) { return s.trim(); }).filter(Boolean);
    if (!reportId) return;
    if (emails.length === 0) return;
    setSaving(true);
    var body = {
      report_id: parseInt(reportId, 10),
      frequency: frequency,
      day_of_week: frequency === 'weekly' ? dayOfWeek : null,
      day_of_month: frequency === 'monthly' ? dayOfMonth : null,
      hour_of_day: parseInt(hourOfDay, 10),
      recipients: emails,
      format: format,
      enabled: enabled,
    };
    var path = initial && initial.id ? '/reports/schedules/' + initial.id : '/reports/schedules';
    var method = initial && initial.id ? 'PUT' : 'POST';
    api(path, { method: method, body: JSON.stringify(body) }).then(function(saved) {
      onSave(saved);
      onClose();
    }).catch(function(e) {
      appAlert(e.message);
    }).finally(function() { setSaving(false); });
  };

  var del = async function() {
    if (!initial || !initial.id) return;
    if (!(await appConfirm('Delete this schedule? Existing execution history will remain.', { danger: true, okLabel: 'Delete' }))) return;
    api('/reports/schedules/' + initial.id, { method: 'DELETE' }).then(function() {
      onDelete && onDelete(initial.id);
      onClose();
    }).catch(function(e) { appAlert(e.message); });
  };

  var hourOptions = [];
  for (var i = 0; i < 24; i++) hourOptions.push({ value: i, label: hourLabel(i) });

  var labelStyle = { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' };
  var fieldRow = { display: 'flex', flexDirection: 'column', gap: 4 };
  var inputStyle = { background: C.bg, color: C.text, border: '1px solid ' + C.border, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', outline: 'none' };

  return h(Modal, { open: open, onClose: onClose, title: initial && initial.id ? 'Edit Schedule' : 'New Schedule', width: 540 },
    h('div', { style: { display: 'flex', flexDirection: 'column', gap: 14 } },
      // Report picker (locked when scheduling from a specific saved report)
      h('div', { style: fieldRow },
        h('label', { style: labelStyle }, 'Report'),
        lockedReportId
          ? h('div', { style: Object.assign({}, inputStyle, { color: C.textMuted, background: C.surface }) },
              ((savedReports && savedReports.find(function(r) { return r.id === parseInt(reportId, 10); })) || {}).name || '(loading)')
          : h('select', { value: reportId, onChange: function(e) { setReportId(e.target.value); }, style: inputStyle },
              h('option', { value: '' }, '— Select a saved report —'),
              (savedReports || []).map(function(r) { return h('option', { key: r.id, value: r.id }, r.name); })
            )
      ),
      // Frequency
      h('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 } },
        h('div', { style: fieldRow },
          h('label', { style: labelStyle }, 'Frequency'),
          h('select', { value: frequency, onChange: function(e) { setFrequency(e.target.value); }, style: inputStyle },
            h('option', { value: 'daily' }, 'Daily'),
            h('option', { value: 'weekly' }, 'Weekly'),
            h('option', { value: 'monthly' }, 'Monthly')
          )
        ),
        h('div', { style: fieldRow },
          h('label', { style: labelStyle }, 'Hour of Day (ET)'),
          h('select', { value: hourOfDay, onChange: function(e) { setHourOfDay(parseInt(e.target.value, 10)); }, style: inputStyle },
            hourOptions.map(function(o) { return h('option', { key: o.value, value: o.value }, o.label); })
          )
        )
      ),
      // Conditional day picker
      frequency === 'weekly' && h('div', { style: fieldRow },
        h('label', { style: labelStyle }, 'Day of Week'),
        h('select', { value: dayOfWeek, onChange: function(e) { setDayOfWeek(parseInt(e.target.value, 10)); }, style: inputStyle },
          DAYS_OF_WEEK.map(function(d) { return h('option', { key: d.value, value: d.value }, d.label); })
        )
      ),
      frequency === 'monthly' && h('div', { style: fieldRow },
        h('label', { style: labelStyle }, 'Day of Month (1-28)'),
        h('input', { type: 'number', min: 1, max: 28, value: dayOfMonth, onChange: function(e) { setDayOfMonth(parseInt(e.target.value, 10) || 1); }, style: inputStyle })
      ),
      // Recipients
      h('div', { style: fieldRow },
        h('label', { style: labelStyle }, 'Recipients (comma or newline separated)'),
        h('textarea', {
          value: recipients,
          onChange: function(e) { setRecipients(e.target.value); },
          rows: 3,
          placeholder: 'alice@example.com, bob@example.com',
          style: Object.assign({}, inputStyle, { resize: 'vertical', fontFamily: 'inherit' })
        })
      ),
      // Format + Enabled
      h('div', { style: { display: 'flex', gap: 16, alignItems: 'center' } },
        h('div', { style: { display: 'flex', gap: 4, background: C.bg, border: '1px solid ' + C.border, borderRadius: 6, padding: 2 } },
          ['xlsx', 'csv'].map(function(f) {
            var active = format === f;
            return h('button', {
              key: f,
              onClick: function() { setFormat(f); },
              style: {
                background: active ? C.accent : 'transparent', color: active ? '#fff' : C.textMuted,
                border: 'none', cursor: 'pointer', padding: '6px 14px', borderRadius: 4,
                fontSize: 12, fontWeight: 500, fontFamily: 'inherit', textTransform: 'uppercase'
              }
            }, f);
          })
        ),
        h('label', { style: { display: 'flex', alignItems: 'center', gap: 6, color: C.text, fontSize: 13, cursor: 'pointer' } },
          h('input', { type: 'checkbox', checked: enabled, onChange: function(e) { setEnabled(e.target.checked); }, style: { cursor: 'pointer' } }),
          'Enabled'
        )
      ),
      // Action bar
      h('div', { style: { display: 'flex', gap: 8, justifyContent: 'space-between', marginTop: 8 } },
        initial && initial.id
          ? h(Btn, { variant: 'danger', onClick: del }, h(LIcon, { name: 'trash-2', size: 14 }), ' Delete')
          : h('div'),
        h('div', { style: { display: 'flex', gap: 8 } },
          h(Btn, { onClick: onClose }, 'Cancel'),
          h(Btn, { variant: 'primary', onClick: save, disabled: saving || !reportId || !recipients.trim() },
            saving ? 'Saving…' : (initial && initial.id ? 'Update' : 'Create Schedule')
          )
        )
      )
    )
  );
}

// ─── ExecutionHistory — recent runs of all reports (manual + scheduled) ───
function ExecutionHistory({ refreshKey }) {
  var [items, setItems] = useState([]);
  var [loading, setLoading] = useState(true);
  var [filterReportId, setFilterReportId] = useState('');
  var [loadError, setLoadError] = useState(null);

  var load = useCallback(function() {
    setLoading(true);
    setLoadError(null);
    var path = '/reports/executions?limit=50' + (filterReportId ? '&report_id=' + filterReportId : '');
    api(path).then(function(rows) { setItems(rows); })
      .catch(function(e) { setLoadError((e && e.message) || 'Failed to load execution history'); })
      .finally(function() { setLoading(false); });
  }, [filterReportId]);
  useEffect(function() { load(); }, [load, refreshKey]);

  var fmtMs = function(ms) {
    if (ms == null) return '—';
    if (ms < 1000) return ms + ' ms';
    return (ms / 1000).toFixed(2) + ' s';
  };
  var fmtDate = function(d) {
    if (!d) return '—';
    var dt = new Date(d);
    return dt.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };
  var triggerLabel = { manual: 'Manual', scheduled: 'Scheduled', export: 'Export' };
  var triggerColor = { manual: C.accent, scheduled: C.success, export: C.purple };

  return h('div', { style: { marginTop: 28 } },
    h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 } },
      h('div', null,
        h('div', { style: { fontSize: 13, fontWeight: 600, color: C.text } }, 'Execution History'),
        h('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 2 } }, 'Last 50 report runs (manual + scheduled)')
      ),
      filterReportId && h(Btn, { size: 'sm', onClick: function() { setFilterReportId(''); } },
        h(LIcon, { name: 'x', size: 12 }), ' Clear Filter'
      )
    ),
    loading
      ? h('div', { style: { padding: 20, textAlign: 'center', color: C.textMuted, fontSize: 12 } }, 'Loading…')
      : loadError
      ? h('div', { style: { padding: 20, textAlign: 'center', color: C.danger, fontSize: 12 } }, 'Failed to load execution history: ' + loadError)
      : items.length === 0
        ? h('div', { style: { padding: 20, textAlign: 'center', color: C.textMuted, fontSize: 12 } }, 'No executions yet.')
        : h('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, overflow: 'hidden' } },
            items.map(function(e, i) {
              var statusColor = e.status === 'success' ? C.success : C.danger;
              var bg = i % 2 === 0 ? 'transparent' : C.bg;
              return h('div', {
                key: e.id,
                onClick: function() {
                  if (filterReportId !== String(e.report_id)) setFilterReportId(String(e.report_id));
                },
                title: 'Click to filter to this report',
                style: {
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '8px 14px', background: bg, fontSize: 12,
                  borderBottom: i < items.length - 1 ? '1px solid ' + C.border : 'none',
                  cursor: 'pointer',
                }
              },
                h('div', { style: { width: 8, height: 8, borderRadius: 4, background: statusColor, flexShrink: 0 } }),
                h('div', { style: { flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: C.text, fontWeight: 500 } },
                  e.report_name || ('Report #' + e.report_id)
                ),
                h('span', {
                  style: {
                    fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                    background: (triggerColor[e.trigger] || C.textMuted) + '20',
                    color: triggerColor[e.trigger] || C.textMuted,
                    textTransform: 'uppercase',
                  }
                }, triggerLabel[e.trigger] || e.trigger),
                h('div', { style: { color: C.textMuted, minWidth: 60, textAlign: 'right' } }, e.row_count != null ? e.row_count.toLocaleString() + ' rows' : '—'),
                h('div', { style: { color: C.textMuted, minWidth: 70, textAlign: 'right' } }, fmtMs(e.duration_ms)),
                h('div', { style: { color: C.textMuted, minWidth: 110, textAlign: 'right' } }, fmtDate(e.created_at)),
                e.error && h('span', { title: e.error, style: { color: C.danger, fontSize: 11 } }, h(LIcon, { name: 'alert-circle', size: 13 }))
              );
            })
          )
  );
}

// ─── SchedulesList — list of all schedules with actions ───
function SchedulesList({ savedReports, refreshKey, onRefresh, showToast }) {
  var [items, setItems] = useState([]);
  var [loading, setLoading] = useState(true);
  var [editing, setEditing] = useState(null);
  var [running, setRunning] = useState({});
  var [loadError, setLoadError] = useState(null);

  var load = useCallback(function() {
    setLoading(true);
    setLoadError(null);
    api('/reports/schedules').then(function(rows) { setItems(rows); })
      .catch(function(e) { setLoadError((e && e.message) || 'Failed to load schedules'); })
      .finally(function() { setLoading(false); });
  }, []);
  useEffect(function() { load(); }, [load, refreshKey]);

  var runNow = function(id) {
    setRunning(Object.assign({}, running, { [id]: true }));
    api('/reports/schedules/' + id + '/run-now', { method: 'POST', body: '{}' }).then(function(res) {
      showToast('Report sent (' + res.rowCount + ' rows, ' + res.durationMs + ' ms)', 'success');
      load();
    }).catch(function(e) {
      showToast(e.message, 'error');
      load(); // the failed run is in the history + the schedule's Last Error
    }).finally(function() {
      setRunning(function(prev) { var next = Object.assign({}, prev); delete next[id]; return next; });
    });
  };

  var toggle = function(s) {
    api('/reports/schedules/' + s.id, { method: 'PUT', body: JSON.stringify({ enabled: !s.enabled }) })
      .then(function() { load(); })
      .catch(function(e) { showToast(e.message, 'error'); });
  };

  if (loading) return h('div', { style: { padding: 40, textAlign: 'center', color: C.textMuted } }, 'Loading…');
  if (loadError) return h('div', { style: { padding: 40, textAlign: 'center', color: C.danger } },
    'Failed to load schedules: ' + loadError, ' ',
    h(Btn, { size: 'sm', onClick: load }, 'Retry'));

  return h('div', null,
    h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 } },
      h('div', { style: { fontSize: 12, color: C.textMuted } }, items.length, ' schedule', items.length === 1 ? '' : 's'),
      h(Btn, { variant: 'primary', size: 'sm', onClick: function() { setEditing({}); } },
        h(LIcon, { name: 'plus', size: 12 }), ' New Schedule'
      )
    ),
    items.length === 0
      ? h('div', { style: { padding: 40, textAlign: 'center', color: C.textMuted } }, 'No schedules yet. Click "New Schedule" or add one from a saved report.')
      : h('div', { style: { display: 'flex', flexDirection: 'column', gap: 10 } },
          items.map(function(s) {
            var statusColor = s.enabled ? C.success : C.textDim;
            var lastErr = s.last_error;
            return h(Card, { key: s.id, style: { padding: 14, opacity: s.enabled ? 1 : 0.6 } },
              h('div', { style: { display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' } },
                // Status dot
                h('div', { style: { width: 8, height: 8, borderRadius: 4, background: statusColor, flexShrink: 0 } }),
                // Name + frequency + recipients
                h('div', { style: { flex: 1, minWidth: 200 } },
                  h('div', { style: { fontSize: 14, fontWeight: 600, color: C.text } }, s.report_name || ('Report #' + s.report_id)),
                  h('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 2 } },
                    describeSchedule(s),
                    ' • ', s.format.toUpperCase(),
                    ' • ', (s.recipients || []).length, ' recipient', (s.recipients || []).length === 1 ? '' : 's',
                    s.created_by_name ? ' • by ' + s.created_by_name : ''
                  ),
                  lastErr && h('div', { style: { fontSize: 11, color: C.danger, marginTop: 4 } }, '⚠ Last error: ', lastErr)
                ),
                // Last/next run
                h('div', { style: { fontSize: 11, color: C.textMuted, minWidth: 140, textAlign: 'right' } },
                  h('div', null, 'Next: ', h('span', { style: { color: C.text } }, formatRelative(s.next_run_at))),
                  h('div', { style: { marginTop: 2 } }, 'Last: ', h('span', { style: { color: C.text } }, formatRelative(s.last_run_at)))
                ),
                // Actions — only the schedule's creator or an admin (server enforces; can_edit from GET)
                s.can_edit !== false && h('div', { style: { display: 'flex', gap: 6 } },
                  h(Btn, { size: 'sm', onClick: function() { runNow(s.id); }, disabled: !!running[s.id] },
                    h(LIcon, { name: running[s.id] ? 'loader-2' : 'send', size: 12, spin: !!running[s.id] }),
                    ' Run Now'
                  ),
                  h(Btn, { size: 'sm', onClick: function() { toggle(s); } }, s.enabled ? 'Disable' : 'Enable'),
                  h(Btn, { size: 'sm', onClick: function() { setEditing(s); } },
                    h(LIcon, { name: 'pencil', size: 12 }), ' Edit'
                  )
                )
              )
            );
          })
        ),

    h(ScheduleModal, {
      open: !!editing,
      onClose: function() { setEditing(null); },
      onSave: function() { onRefresh && onRefresh(); load(); },
      onDelete: function() { onRefresh && onRefresh(); load(); },
      savedReports: savedReports,
      initial: editing,
      lockedReportId: null,
    }),

    h(ExecutionHistory, { refreshKey: refreshKey })
  );
}

// ─── Saved Reports List ───
function SavedReportsList({ schema, catalogById, onLoad, onDelete, onSchedule, refreshKey }) {
  var [items, setItems] = useState([]);
  var [loading, setLoading] = useState(true);
  var [loadError, setLoadError] = useState(null);
  var [actionError, setActionError] = useState(null);

  var load = useCallback(function() {
    setLoading(true);
    setLoadError(null);
    api('/reports/saved').then(function(rows) { setItems(rows); })
      .catch(function(e) { setLoadError((e && e.message) || 'Failed to load saved reports'); })
      .finally(function() { setLoading(false); });
  }, []);
  useEffect(function() { load(); }, [load, refreshKey]);

  var togglePin = function(r) {
    setActionError(null);
    api('/reports/saved/' + r.id + '/pin', { method: 'PUT', body: JSON.stringify({ is_pinned: !r.is_pinned }) })
      .then(function() { load(); })
      .catch(function(e) { setActionError((e && e.message) || 'Pin failed'); });
  };

  if (loading) return h('div', { style: { padding: 40, textAlign: 'center', color: C.textMuted } }, 'Loading…');
  if (loadError) return h('div', { style: { padding: 40, textAlign: 'center', color: C.danger } },
    'Failed to load saved reports: ' + loadError, ' ',
    h(Btn, { size: 'sm', onClick: load }, 'Retry'));
  if (items.length === 0) return h('div', { style: { padding: 40, textAlign: 'center', color: C.textMuted } }, 'No saved reports yet. Open any report in the Library, set its filters, and click Save.');

  var renderCard = function(r) {
    var lib = r.config && r.config._library;
    var entry = lib && catalogById ? catalogById[lib.reportId] : null;
    var srcLabel = entry
      ? entry.name
      : (((schema.sources[r.data_source] && schema.sources[r.data_source].label) || r.data_source) + ' • legacy');
    return h(Card, { key: r.id, style: { padding: 16, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 8, borderColor: r.is_pinned ? C.warning : C.border } },
      h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 } },
        h('div', { style: { fontSize: 14, fontWeight: 600, color: C.text, lineHeight: 1.3, flex: 1 } }, r.name),
        h('div', { style: { display: 'flex', gap: 2 } },
          r.can_edit !== false && h('button', {
            onClick: function(e) { e.stopPropagation(); togglePin(r); },
            style: { background: 'transparent', border: 'none', color: r.is_pinned ? C.warning : C.textMuted, cursor: 'pointer', padding: 2 },
            title: r.is_pinned ? 'Unpin' : 'Pin to top',
            onMouseOver: function(e) { if (!r.is_pinned) e.currentTarget.style.color = C.warning; },
            onMouseOut: function(e) { if (!r.is_pinned) e.currentTarget.style.color = C.textMuted; }
          }, h(LIcon, { name: 'star', size: 14 })),
          // Delete only for the report's creator or an admin (server enforces; can_edit from GET /saved)
          r.can_edit !== false && h('button', {
            onClick: async function(e) {
              e.stopPropagation();
              if (!(await appConfirm('Delete report "' + r.name + '"?', { danger: true, okLabel: 'Delete' }))) return;
              setActionError(null);
              api('/reports/saved/' + r.id, { method: 'DELETE' }).then(function() { onDelete && onDelete(r); load(); })
                .catch(function(err) { setActionError((err && err.message) || 'Delete failed'); });
            },
            style: { background: 'transparent', border: 'none', color: C.textMuted, cursor: 'pointer', padding: 2 },
            title: 'Delete',
            onMouseOver: function(e) { e.currentTarget.style.color = C.danger; },
            onMouseOut: function(e) { e.currentTarget.style.color = C.textMuted; }
          }, h(LIcon, { name: 'trash-2', size: 14 }))
        )
      ),
      r.description && h('div', { style: { fontSize: 12, color: C.textMuted, lineHeight: 1.4 } }, r.description),
      h('div', { style: { fontSize: 11, color: C.textDim, marginTop: 'auto' } },
        srcLabel, ' • ', r.created_by_name || 'unknown',
        ' • ', new Date(r.updated_at).toLocaleDateString()
      ),
      h('div', { style: { display: 'flex', gap: 6 } },
        h(Btn, { variant: 'primary', size: 'sm', onClick: function() { onLoad(r); } },
          h(LIcon, { name: 'play', size: 12 }), ' Open'
        ),
        h(Btn, { size: 'sm', onClick: function() { onSchedule && onSchedule(r); } },
          h(LIcon, { name: 'clock', size: 12 }), ' Schedule'
        )
      )
    );
  };

  var pinned = items.filter(function(r) { return r.is_pinned; });
  var rest = items.filter(function(r) { return !r.is_pinned; });

  return h('div', null,
    actionError && h('div', { style: { marginBottom: 12, fontSize: 12, color: C.danger } }, actionError),
    pinned.length > 0 && h('div', { style: { marginBottom: 24 } },
      h('div', { style: { fontSize: 11, fontWeight: 600, color: C.warning, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 } },
        h(LIcon, { name: 'star', size: 12 }), 'Pinned'
      ),
      h('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 } },
        pinned.map(renderCard)
      )
    ),
    rest.length > 0 && h('div', null,
      pinned.length > 0 && h('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 } }, 'All Reports'),
      h('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 } },
        rest.map(renderCard)
      )
    )
  );
}

// ─── Top-level Reports Page ───
function ReportsPage({ showToast, onNavigate, currentUser }) {
  var [tab, setTab] = useState(_reportsPageCache.tab || 'library');
  var [view, setView] = useState(_reportsPageCache.view || { mode: 'library' });
  var [catalog, setCatalog] = useState(null);
  var [schema, setSchema] = useState(null);
  var [loadError, setLoadError] = useState(null);
  var [loading, setLoading] = useState(true);
  var [savedRefreshKey, setSavedRefreshKey] = useState(0);
  var [scheduleRefreshKey, setScheduleRefreshKey] = useState(0);
  var [savedReports, setSavedReports] = useState([]);
  var [scheduleModalReport, setScheduleModalReport] = useState(null); // { id, name } — opens ScheduleModal locked
  var deepLinkApplied = useRef(false);

  // Persist page state so drill-down navigation can restore it
  useEffect(function() {
    _reportsPageCache.tab = tab;
    _reportsPageCache.view = view;
  }, [tab, view]);

  useEffect(function() {
    Promise.all([
      api('/reports/catalog'),
      api('/reports/schema'),
    ]).then(function(results) {
      setCatalog(results[0]);
      setSchema(results[1]);
    }).catch(function(e) {
      setLoadError(e.message);
    }).finally(function() { setLoading(false); });
  }, []);

  // Keep a fresh list of saved reports (schedule modal names + saved tab)
  useEffect(function() {
    api('/reports/saved').then(setSavedReports).catch(function(e) { console.error('[silent-catch]', e); });
  }, [savedRefreshKey]);

  var catalogById = useMemo(function() {
    var map = {};
    if (catalog) catalog.reports.forEach(function(r) { map[r.id] = r; });
    return map;
  }, [catalog]);

  var openEntry = function(entry, savedMeta, lib) {
    _reportsPageCache.runState = null; // fresh open starts clean
    setView({
      mode: 'run',
      entryId: entry.id,
      savedMeta: savedMeta || null,
      initialParams: (lib && lib.params) || {},
      initialDatePreset: (lib && lib.datePreset) || null,
      initialDateRange: (lib && lib.dateRange) || null,
    });
    setTab('library');
  };

  var loadSaved = function(rep) {
    var lib = rep.config && rep.config._library;
    var entry = lib && catalogById[lib.reportId];
    if (entry) {
      openEntry(entry, { id: rep.id, name: rep.name, description: rep.description || '' }, lib);
    } else {
      _reportsPageCache.legacyResult = null;
      setView({ mode: 'legacy', saved: rep });
      setTab('library');
    }
  };

  var backToLibrary = function() {
    _reportsPageCache.runState = null;
    _reportsPageCache.legacyResult = null;
    setView({ mode: 'library' });
  };

  // Deep links: /reports?report=<catalog id>, ?saved=<saved id>, ?tab=<tab>
  useEffect(function() {
    if (!catalog || deepLinkApplied.current) return;
    deepLinkApplied.current = true;
    try {
      var qs = new URLSearchParams(window.location.search);
      var qTab = qs.get('tab');
      var qReport = qs.get('report');
      var qSaved = qs.get('saved');
      if (qReport && catalogById[qReport]) {
        openEntry(catalogById[qReport], null, null);
      } else if (qSaved) {
        api('/reports/saved/' + qSaved).then(function(rep) { loadSaved(rep); }).catch(function() {});
      } else if (qTab) {
        setTab(qTab);
      }
    } catch (e) {}
  }, [catalog]);

  if (loading) return h('div', { style: { padding: 40, color: C.textMuted } }, 'Loading reports…');
  if (loadError || !catalog) return h('div', { style: { padding: 40, color: C.danger } }, 'Failed to load the report library', loadError ? ': ' + loadError : '.');

  var openRun = view.mode === 'run' && catalogById[view.entryId] ? view : null;

  return h('div', null,
    // Header
    h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 } },
      h('div', null,
        h('h2', { style: { margin: 0, fontSize: 22, fontWeight: 700, color: C.text } }, 'Reports'),
        h('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 4 } }, 'Pick a question, get an answer. Every report runs instantly and can be saved, exported, or emailed on a schedule.')
      )
    ),

    // Tabs
    h('div', { style: { display: 'flex', gap: 4, borderBottom: '1px solid ' + C.border, marginBottom: 18 } },
      [
        { key: 'library', label: 'Library' },
        { key: 'saved', label: 'Saved Reports' },
        { key: 'schedules', label: 'Schedules' },
      ].map(function(t) {
        var active = tab === t.key;
        return h('a', {
          key: t.key,
          href: '/reports' + (t.key !== 'library' ? '?tab=' + t.key : ''),
          onClick: function(e) {
            if (e.ctrlKey || e.metaKey || e.shiftKey) return;
            e.preventDefault();
            setTab(t.key);
            if (t.key === 'library' && view.mode !== 'library') backToLibrary();
          },
          style: {
            background: 'transparent', border: 'none', cursor: 'pointer', textDecoration: 'none',
            padding: '10px 16px', fontSize: 13, fontWeight: 500, fontFamily: 'inherit',
            color: active ? C.accent : C.textMuted,
            borderBottom: active ? '2px solid ' + C.accent : '2px solid transparent',
            marginBottom: -1,
          }
        }, t.label);
      })
    ),

    tab === 'library' && (
      openRun
        ? h(ReportRunView, {
            key: openRun.entryId + '|' + ((openRun.savedMeta && openRun.savedMeta.id) || ''),
            entry: catalogById[openRun.entryId],
            savedMeta: openRun.savedMeta,
            initialParams: openRun.initialParams,
            initialDatePreset: openRun.initialDatePreset,
            initialDateRange: openRun.initialDateRange,
            onBack: backToLibrary,
            showToast: showToast,
            onNavigate: onNavigate,
            onSavedRefresh: function() { setSavedRefreshKey(savedRefreshKey + 1); },
            onSchedule: function(rep) { setScheduleModalReport(rep); },
          })
        : view.mode === 'legacy' && view.saved
          ? h(LegacySavedReportView, {
              saved: view.saved,
              schema: schema,
              onBack: backToLibrary,
              showToast: showToast,
              onNavigate: onNavigate,
              onSchedule: function(rep) { setScheduleModalReport(rep); },
            })
          : h(ReportLibraryView, { catalog: catalog, onOpen: function(entry) { openEntry(entry, null, null); } })
    ),

    tab === 'saved' && h(SavedReportsList, {
      schema: schema,
      catalogById: catalogById,
      onLoad: loadSaved,
      onDelete: function(rep) {
        setSavedRefreshKey(savedRefreshKey + 1);
        if (view.mode === 'run' && view.savedMeta && view.savedMeta.id === rep.id) backToLibrary();
        if (view.mode === 'legacy' && view.saved && view.saved.id === rep.id) backToLibrary();
      },
      onSchedule: function(rep) { setScheduleModalReport({ id: rep.id, name: rep.name }); },
      refreshKey: savedRefreshKey,
    }),
    tab === 'schedules' && h(SchedulesList, {
      savedReports: savedReports,
      refreshKey: scheduleRefreshKey,
      onRefresh: function() { setScheduleRefreshKey(scheduleRefreshKey + 1); },
      showToast: showToast,
    }),

    // Schedule modal launched from a run view or saved card (locks the report dropdown)
    h(ScheduleModal, {
      open: !!scheduleModalReport,
      onClose: function() { setScheduleModalReport(null); },
      onSave: function() { setScheduleRefreshKey(scheduleRefreshKey + 1); showToast('Schedule saved', 'success'); },
      onDelete: function() { setScheduleRefreshKey(scheduleRefreshKey + 1); },
      savedReports: scheduleModalReport ? [{ id: scheduleModalReport.id, name: scheduleModalReport.name }] : [],
      initial: scheduleModalReport ? { report_id: scheduleModalReport.id } : null,
      lockedReportId: scheduleModalReport ? scheduleModalReport.id : null,
    })
  );
}

window.ReportsPage = ReportsPage;

;
/* ===== app-import-export.js ===== */
// ─── Import / Export module (docs/18) ───
// Custom product import/export (modeled on the reference screenshots). One shared template registry
// (SKU + any attributes, ordered) powers both directions:
//   Export: pick template → filters → download filled XLSX/CSV
//   Import: upload file → dry-run preview (diffs, errors, unmatched) → Apply
// Exports: window.ImportExportPage

var { createElement: h } = React;

var _iePageCache = { tab: null };

// ─── Shared fetch helpers ─────────────────────────────────────────────────
function ieDownload(path, body) {
  return fetch('/api/import-export' + path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body || {}),
  }).then(function(res) {
    if (!res.ok) return res.json().then(function(j) { throw new Error(j.error || 'Download failed'); });
    var disp = res.headers.get('content-disposition') || '';
    var m = disp.match(/filename="([^"]+)"/);
    return res.blob().then(function(blob) { return { blob: blob, filename: m ? m[1] : 'export.xlsx' }; });
  }).then(function(d) {
    var url = URL.createObjectURL(d.blob);
    var a = document.createElement('a');
    a.href = url; a.download = d.filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  });
}

// Headers-only file for a template (shared by the Import and Templates tabs — the Templates
// copy used to swallow a failed download silently).
function ieDownloadBlank(t, showToast) {
  return fetch('/api/import-export/templates/' + t.id + '/blank?format=csv', { credentials: 'include' })
    .then(function(res) {
      if (!res.ok) return res.json().catch(function() { return {}; }).then(function(j) { throw new Error(j.error || 'Download failed'); });
      var disp = res.headers.get('content-disposition') || '';
      var m = disp.match(/filename="([^"]+)"/);
      return res.blob().then(function(b) { return { blob: b, filename: m ? m[1] : 'template.csv' }; });
    })
    .then(function(d) {
      var url = URL.createObjectURL(d.blob);
      var a = document.createElement('a');
      a.href = url; a.download = d.filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    })
    .catch(function(e) { showToast && showToast(e.message, 'error'); });
}

var ieLabelStyle = { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' };

// ─── Attribute picker (grouped, searchable dropdown) ──────────────────────
function IEAttributePicker({ catalog, exclude, onPick, label }) {
  var [open, setOpen] = useState(false);
  var [search, setSearch] = useState('');
  var ref = useRef(null);

  useEffect(function() {
    if (!open) return;
    var onClick = function(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return function() { document.removeEventListener('mousedown', onClick); };
  }, [open]);

  var q = search.trim().toLowerCase();
  var excluded = exclude || new Set();

  return h('div', { ref: ref, style: { position: 'relative', display: 'inline-block' } },
    h(Btn, { variant: 'primary', size: 'sm', onClick: function() { setOpen(!open); setSearch(''); } },
      h(LIcon, { name: 'plus', size: 13 }), ' ', label || 'Add Column'),
    open && h('div', {
      style: {
        position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 100,
        background: C.surface, border: '1px solid ' + C.border, borderRadius: 8,
        boxShadow: '0 8px 24px rgba(0,0,0,0.3)', width: 320, maxHeight: 380, overflowY: 'auto',
      }
    },
      h('input', {
        autoFocus: true, value: search,
        onChange: function(e) { setSearch(e.target.value); },
        placeholder: 'Search attributes…',
        style: { width: '100%', background: C.bg, border: 'none', borderBottom: '1px solid ' + C.border, color: C.text, padding: '8px 12px', fontSize: 13, outline: 'none', boxSizing: 'border-box' }
      }),
      (catalog.groups || []).map(function(g) {
        var fields = g.fields.filter(function(f) {
          if (excluded.has(f.key)) return false;
          if (!q) return true;
          return (f.label + ' ' + f.key).toLowerCase().indexOf(q) !== -1;
        });
        if (!fields.length) return null;
        return h('div', { key: g.key },
          h('div', { style: { padding: '6px 12px', fontSize: 10, fontWeight: 700, color: C.textDim, textTransform: 'uppercase', letterSpacing: '0.06em', background: C.bg } }, g.label),
          fields.map(function(f) {
            return h('div', {
              key: f.key,
              onClick: function() { onPick(f); setOpen(false); },
              onMouseOver: function(e) { e.currentTarget.style.background = C.surfaceHover; },
              onMouseOut: function(e) { e.currentTarget.style.background = 'transparent'; },
              style: { padding: '7px 12px', cursor: 'pointer', fontSize: 13, color: C.text, display: 'flex', justifyContent: 'space-between', gap: 8 }
            },
              h('span', null, f.label),
              h('span', { style: { fontSize: 10, color: C.textDim } }, f.type + (f.readOnly ? ' • read-only' : ''))
            );
          })
        );
      })
    )
  );
}

// ─── Template editor (lettered, ordered columns) ──────────────────────────
function IETemplateEditor({ catalog, template, onClose, onSaved, showToast }) {
  var isEdit = template && template.id;
  var [name, setName] = useState(template ? template.name : '');
  var [cols, setCols] = useState(function() {
    if (template && template.columns && template.columns.length) return template.columns.slice();
    return [{ key: 'sku', header: 'SKU' }];
  });
  var [saving, setSaving] = useState(false);

  var move = function(i, dir) {
    var j = i + dir;
    if (j < 1 || j >= cols.length || i === 0) return; // SKU stays at A
    var next = cols.slice();
    var t = next[i]; next[i] = next[j]; next[j] = t;
    setCols(next);
  };
  var remove = function(i) {
    if (i === 0) return;
    setCols(cols.filter(function(_, x) { return x !== i; }));
  };
  var add = function(f) {
    if (cols.find(function(c) { return c.key === f.key; })) return;
    setCols(cols.concat([{ key: f.key, header: f.label }]));
  };

  var save = function() {
    if (!name.trim()) { showToast('Give the template a name', 'error'); return; }
    if (cols.length < 2) { showToast('Add at least one attribute next to SKU', 'error'); return; }
    setSaving(true);
    var body = { name: name.trim(), columns: cols };
    var path = isEdit ? '/import-export/templates/' + template.id : '/import-export/templates';
    api(path, { method: isEdit ? 'PUT' : 'POST', body: JSON.stringify(body) }).then(function() {
      showToast(isEdit ? 'Template updated' : 'Template created', 'success');
      onSaved();
      onClose();
    }).catch(function(e) { showToast(e.message, 'error'); }).finally(function() { setSaving(false); });
  };

  var letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  var letterFor = function(i) { return i < 26 ? letters[i] : letters[Math.floor(i / 26) - 1] + letters[i % 26]; };

  // Page-wide editor (not a modal) — per user feedback, mirrors the reference
  // platform's full-page Create Custom Template so the Add Column dropdown has room.
  return h('div', null,
    h('div', { style: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 } },
      h('button', {
        onClick: onClose,
        style: { background: 'transparent', border: '1px solid ' + C.border, color: C.textMuted, borderRadius: 6, padding: '6px 10px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontFamily: 'inherit' }
      }, h(LIcon, { name: 'arrow-left', size: 13 }), ' Templates'),
      h('div', { style: { fontSize: 17, fontWeight: 700, color: C.text } }, isEdit ? 'Edit Template' : 'Create New Template')
    ),
    h(Card, { style: { padding: 18, display: 'flex', flexDirection: 'column', gap: 14 } },
      h('div', { style: { fontSize: 12, color: C.textMuted } },
        'Columns become the file headers, in order. SKU is always column A — it\'s how import rows match products.'),
      h('div', { style: { maxWidth: 480 } },
        h(Inp, { label: 'Template Name', value: name, onChange: function(e) { setName(e.target.value); }, placeholder: 'e.g. Costs & Dimensions' })
      ),
      h('div', { style: { display: 'grid', gridTemplateColumns: 'minmax(320px, 560px) 1fr', gap: 20, alignItems: 'start' } },
        h('div', null,
          h('div', { style: Object.assign({}, ieLabelStyle, { marginBottom: 8 }) }, 'Columns'),
          h('div', { style: { display: 'flex', flexDirection: 'column', gap: 6 } },
          cols.map(function(c, i) {
            var f = catalog.byKey ? catalog.byKey[c.key] : null;
            var isSku = i === 0;
            return h('div', {
              key: c.key,
              style: {
                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                background: isSku ? C.accentSoft : C.bg, border: '1px solid ' + C.border, borderRadius: 8,
              }
            },
              h('div', {
                style: {
                  width: 26, height: 26, borderRadius: 13, background: isSku ? C.accent : C.surface,
                  color: isSku ? '#fff' : C.textMuted, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 700, flexShrink: 0, border: isSku ? 'none' : '1px solid ' + C.border,
                }
              }, letterFor(i)),
              h('div', { style: { flex: 1, minWidth: 0 } },
                h('div', { style: { fontSize: 13, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, c.header),
                h('div', { style: { fontSize: 10, color: C.textDim } }, (f ? f.type : 'text') + (isSku ? ' • match key' : ''))
              ),
              !isSku && h('div', { style: { display: 'flex', gap: 2 } },
                h('button', { onClick: function() { move(i, -1); }, disabled: i <= 1, style: { background: 'transparent', border: 'none', color: i <= 1 ? C.textDim : C.textMuted, cursor: i <= 1 ? 'default' : 'pointer', padding: 4 } }, h(LIcon, { name: 'arrow-up', size: 14 })),
                h('button', { onClick: function() { move(i, 1); }, disabled: i >= cols.length - 1, style: { background: 'transparent', border: 'none', color: i >= cols.length - 1 ? C.textDim : C.textMuted, cursor: i >= cols.length - 1 ? 'default' : 'pointer', padding: 4 } }, h(LIcon, { name: 'arrow-down', size: 14 })),
                h('button', {
                  onClick: function() { remove(i); },
                  style: { background: 'transparent', border: 'none', color: C.textMuted, cursor: 'pointer', padding: 4 },
                  onMouseOver: function(e) { e.currentTarget.style.color = C.danger; },
                  onMouseOut: function(e) { e.currentTarget.style.color = C.textMuted; }
                }, h(LIcon, { name: 'trash-2', size: 14 }))
              )
            );
          })
        ),
          h('div', { style: { marginTop: 10 } },
            h(IEAttributePicker, {
              catalog: catalog,
              exclude: new Set(cols.map(function(c) { return c.key; })),
              onPick: add,
            })
          )
        ),
        // Right: live preview of the file headers this template produces
        h('div', null,
          h('div', { style: Object.assign({}, ieLabelStyle, { marginBottom: 8 }) }, 'File Header Preview'),
          h('div', { style: { border: '1px solid ' + C.border, borderRadius: 8, overflowX: 'auto', background: C.bg } },
            h('table', { style: { borderCollapse: 'collapse', fontSize: 11 } },
              h('thead', null, h('tr', null,
                cols.map(function(c, i) {
                  return h('th', { key: c.key, style: { padding: '6px 10px', borderRight: '1px solid ' + C.border, borderBottom: '1px solid ' + C.border, color: C.textMuted, fontWeight: 600, textAlign: 'left', whiteSpace: 'nowrap' } }, letterFor(i) + ' · ' + c.header);
                })
              )),
              h('tbody', null, h('tr', null,
                cols.map(function(c) {
                  var f = catalog.byKey ? catalog.byKey[c.key] : null;
                  return h('td', { key: c.key, style: { padding: '6px 10px', borderRight: '1px solid ' + C.border, color: C.textDim, fontStyle: 'italic', whiteSpace: 'nowrap' } }, f ? f.type : 'text');
                })
              ))
            )
          ),
          h('div', { style: { fontSize: 11, color: C.textDim, marginTop: 8, lineHeight: 1.5 } },
            'Exactly how the exported and blank files will come out.')
        )
      ),
      h('div', { style: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 } },
        h(Btn, { onClick: onClose }, 'Cancel'),
        h(Btn, { variant: 'primary', onClick: save, disabled: saving }, saving ? 'Saving…' : (isEdit ? 'Update Template' : 'Create Template'))
      )
    )
  );
}

// ─── Template list (shared by Import + Export + Templates tabs) ───────────
function IETemplateList({ templates, selectedId, onSelect, onEdit, onDuplicate, onDelete, onBlank, showActions, pinnedTop }) {
  return h('div', { style: { display: 'flex', flexDirection: 'column', gap: 8 } },
    pinnedTop,
    templates.map(function(t) {
      var active = selectedId === t.id;
      var summary = (t.columns || []).map(function(c) { return c.header; }).join(', ');
      return h('div', {
        key: t.id,
        onClick: function() { onSelect && onSelect(t); },
        style: {
          padding: '10px 14px', borderRadius: 8, cursor: onSelect ? 'pointer' : 'default',
          background: active ? C.accentSoft : C.surface,
          border: '1px solid ' + (active ? C.accent : C.border),
        }
      },
        h('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
          h('div', { style: { flex: 1, minWidth: 0 } },
            h('div', { style: { fontSize: 13, fontWeight: 600, color: active ? C.accent : C.text } }, t.name),
            h('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, summary)
          ),
          showActions && h('div', { style: { display: 'flex', gap: 2, flexShrink: 0 } },
            onEdit && h('button', { title: 'Edit', onClick: function(e) { e.stopPropagation(); onEdit(t); }, style: { background: 'transparent', border: 'none', color: C.textMuted, cursor: 'pointer', padding: 4 } }, h(LIcon, { name: 'pencil', size: 14 })),
            onDuplicate && h('button', { title: 'Duplicate', onClick: function(e) { e.stopPropagation(); onDuplicate(t); }, style: { background: 'transparent', border: 'none', color: C.textMuted, cursor: 'pointer', padding: 4 } }, h(LIcon, { name: 'copy', size: 14 })),
            onDelete && h('button', {
              title: 'Delete',
              onClick: function(e) { e.stopPropagation(); onDelete(t); },
              style: { background: 'transparent', border: 'none', color: C.textMuted, cursor: 'pointer', padding: 4 },
              onMouseOver: function(e) { e.currentTarget.style.color = C.danger; },
              onMouseOut: function(e) { e.currentTarget.style.color = C.textMuted; }
            }, h(LIcon, { name: 'trash-2', size: 14 })),
            onBlank && h('button', { title: 'Download blank file with these headers', onClick: function(e) { e.stopPropagation(); onBlank(t); }, style: { background: 'transparent', border: 'none', color: C.textMuted, cursor: 'pointer', padding: 4 } }, h(LIcon, { name: 'download', size: 14 }))
          )
        )
      );
    }),
    templates.length === 0 && h('div', { style: { padding: 20, textAlign: 'center', color: C.textMuted, fontSize: 12 } },
      'No templates yet — create one in the Templates tab.')
  );
}

// ─── Filter multi-select for export (vendor / brand / warehouse) ──────────
function IEFilterSelect({ label, field, selected, onChange }) {
  var [values, setValues] = useState(null);
  var [loaded, setLoaded] = useState(false);
  var loadOnce = function() {
    if (loaded) return;
    setLoaded(true);
    api('/import-export/filter-values?field=' + field).then(function(r) { setValues(r.values || []); }).catch(function() { setValues([]); });
  };
  return h('div', { onMouseDown: loadOnce },
    h(ReportParamSelect, { label: label, values: values, loading: values === null, selected: selected, onChange: onChange })
  );
}

// ─── Export tab ───────────────────────────────────────────────────────────
function IEExportTab({ templates, catalog, showToast }) {
  var [selected, setSelected] = useState(null);
  var [format, setFormat] = useState('xlsx');
  var [activeOnly, setActiveOnly] = useState(true);
  var [vendors, setVendors] = useState([]);
  var [brands, setBrands] = useState([]);
  var [warehouses, setWarehouses] = useState([]);
  var [skuText, setSkuText] = useState('');
  var [downloading, setDownloading] = useState(false);

  var download = function() {
    if (!selected) return;
    setDownloading(true);
    var skus = skuText.split(/[\s,;]+/).map(function(s) { return s.trim(); }).filter(Boolean);
    ieDownload('/export', {
      template_id: selected.id,
      format: format,
      filters: {
        active_only: activeOnly,
        vendors: vendors, brands: brands, warehouses: warehouses,
        skus: skus,
      },
    }).catch(function(e) { showToast(e.message, 'error'); }).finally(function() { setDownloading(false); });
  };

  return h('div', { style: { display: 'grid', gridTemplateColumns: 'minmax(280px, 380px) 1fr', gap: 16, alignItems: 'start' } },
    h(Card, { style: { padding: 14 } },
      h('div', { style: Object.assign({}, ieLabelStyle, { marginBottom: 10 }) }, '1. Pick a Template'),
      h(IETemplateList, { templates: templates, selectedId: selected && selected.id, onSelect: setSelected })
    ),
    h(Card, { style: { padding: 16 } },
      h('div', { style: Object.assign({}, ieLabelStyle, { marginBottom: 12 }) }, '2. Options & Download'),
      !selected
        ? h('div', { style: { padding: 30, color: C.textMuted, fontSize: 13 } }, 'Pick a template on the left. The export contains every product matching the filters, one row per SKU, with the template\'s columns.')
        : h('div', { style: { display: 'flex', flexDirection: 'column', gap: 14 } },
            h('div', { style: { display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' } },
              h('div', { style: { display: 'flex', gap: 4, background: C.bg, border: '1px solid ' + C.border, borderRadius: 6, padding: 2 } },
                ['xlsx', 'csv'].map(function(f) {
                  var active = format === f;
                  return h('button', {
                    key: f, onClick: function() { setFormat(f); },
                    style: { background: active ? C.accent : 'transparent', color: active ? '#fff' : C.textMuted, border: 'none', cursor: 'pointer', padding: '6px 14px', borderRadius: 4, fontSize: 12, fontWeight: 500, fontFamily: 'inherit', textTransform: 'uppercase' }
                  }, f);
                })
              ),
              h('label', { style: { display: 'flex', alignItems: 'center', gap: 6, color: C.text, fontSize: 13, cursor: 'pointer' } },
                h('input', { type: 'checkbox', checked: activeOnly, onChange: function(e) { setActiveOnly(e.target.checked); }, style: { cursor: 'pointer', accentColor: C.accent, width: 14, height: 14 } }),
                'Active Products Only'
              )
            ),
            h('div', { style: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' } },
              h(LIcon, { name: 'filter', size: 14, color: C.textMuted }),
              h(IEFilterSelect, { label: 'Vendor', field: 'vendor_name', selected: vendors, onChange: setVendors }),
              h(IEFilterSelect, { label: 'Brand', field: 'brand', selected: brands, onChange: setBrands }),
              h(IEFilterSelect, { label: 'Warehouse', field: 'default_warehouse', selected: warehouses, onChange: setWarehouses })
            ),
            h('div', null,
              h('div', { style: Object.assign({}, ieLabelStyle, { marginBottom: 4 }) }, 'Only These SKUs (Optional — Paste a List)'),
              h('textarea', {
                value: skuText, onChange: function(e) { setSkuText(e.target.value); }, rows: 3,
                placeholder: 'SKU-001, SKU-002 — commas, spaces, or one per line. Leave empty for all.',
                style: { width: '100%', boxSizing: 'border-box', background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 12, fontFamily: 'inherit', outline: 'none', resize: 'vertical' }
              })
            ),
            h('div', null,
              h(Btn, { variant: 'primary', onClick: download, disabled: downloading },
                h(LIcon, { name: downloading ? 'loader-2' : 'download', size: 14, spin: downloading }),
                downloading ? ' Preparing…' : ' Download'
              )
            )
          )
    )
  );
}

// ─── Import tab ───────────────────────────────────────────────────────────
function IEImportTab({ templates, showToast, onHistoryRefresh }) {
  var [selected, setSelected] = useState('templateless'); // 'templateless' | template object
  var [preview, setPreview] = useState(null);
  var [uploading, setUploading] = useState(false);
  var [applying, setApplying] = useState(false);
  var [applied, setApplied] = useState(null);
  var fileRef = useRef(null);

  var reset = function() { setPreview(null); setApplied(null); if (fileRef.current) fileRef.current.value = ''; };

  var uploadFile = function(file) {
    if (!file) return;
    setUploading(true);
    setPreview(null); setApplied(null);
    var fd = new FormData();
    fd.append('file', file);
    if (selected !== 'templateless' && selected && selected.id) fd.append('template_id', selected.id);
    fetch('/api/import-export/import/preview', { method: 'POST', credentials: 'include', body: fd })
      .then(function(res) { return res.json().then(function(j) { if (!res.ok) throw new Error(j.error || 'Preview failed'); return j; }); })
      .then(function(j) { setPreview(j); })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setUploading(false); if (fileRef.current) fileRef.current.value = ''; });
  };

  var apply = async function() {
    if (!preview) return;
    if (!(await appConfirm('Apply ' + preview.summary.changed_cells + ' changes to ' + preview.summary.products_changing + ' products? This cannot be undone from here (each change is logged on the product).'))) return;
    setApplying(true);
    api('/import-export/import/' + preview.job_id + '/apply', { method: 'POST', body: '{}' }).then(function(r) {
      setApplied(r);
      setPreview(null);
      if (r.failed_count > 0) showToast('Import applied — ' + r.products_updated + ' products updated, ' + r.failed_count + ' not written (see below)', 'error');
      else showToast('Import applied — ' + r.products_updated + ' products updated', 'success');
      onHistoryRefresh && onHistoryRefresh();
    }).catch(function(e) { showToast(e.message, 'error'); }).finally(function() { setApplying(false); });
  };

  var blank = function(t) { ieDownloadBlank(t, showToast); };

  var s = preview && preview.summary;
  var tile = function(label, value, color) {
    return h('div', { style: { background: C.bg, border: '1px solid ' + C.border, borderRadius: 8, padding: '10px 14px', minWidth: 110 } },
      h('div', { style: { fontSize: 20, fontWeight: 700, color: color || C.text } }, value),
      h('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 2 } }, label)
    );
  };

  return h('div', { style: { display: 'grid', gridTemplateColumns: 'minmax(280px, 380px) 1fr', gap: 16, alignItems: 'start' } },
    h(Card, { style: { padding: 14 } },
      h('div', { style: Object.assign({}, ieLabelStyle, { marginBottom: 10 }) }, '1. Pick a Template'),
      h(IETemplateList, {
        templates: templates,
        selectedId: selected !== 'templateless' && selected ? selected.id : null,
        onSelect: function(t) { setSelected(t); reset(); },
        showActions: true,
        onBlank: blank,
        pinnedTop: h('div', {
          onClick: function() { setSelected('templateless'); reset(); },
          style: {
            padding: '10px 14px', borderRadius: 8, cursor: 'pointer',
            background: selected === 'templateless' ? C.accentSoft : C.surface,
            border: '1px solid ' + (selected === 'templateless' ? C.accent : C.border),
          }
        },
          h('div', { style: { fontSize: 13, fontWeight: 600, color: selected === 'templateless' ? C.accent : C.text } }, 'Templateless Import'),
          h('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 2 } }, 'Any file with a SKU column — headers are matched to attributes by name.')
        ),
      })
    ),
    h(Card, { style: { padding: 16 } },
      h('div', { style: Object.assign({}, ieLabelStyle, { marginBottom: 12 }) }, '2. Upload → Preview → Apply'),
      h('div', { style: { display: 'flex', flexDirection: 'column', gap: 14 } },
        h('div', { style: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' } },
          h('input', {
            ref: fileRef, type: 'file', accept: '.csv,.xlsx,.xls',
            onChange: function(e) { uploadFile(e.target.files && e.target.files[0]); },
            style: { fontSize: 12, color: C.textMuted }
          }),
          uploading && h('span', { style: { display: 'inline-flex', alignItems: 'center', gap: 6, color: C.textMuted, fontSize: 12 } },
            h(LIcon, { name: 'loader-2', size: 14, spin: true }), 'Analyzing file…'),
          h('span', { style: { fontSize: 11, color: C.textDim } },
            'CSV or Excel. Blank cells are skipped; type [CLEAR] to empty a field. Nothing is written until you Apply.')
        ),

        applied && h('div', { style: { background: C.successSoft, border: '1px solid ' + C.success, borderRadius: 8, padding: '12px 16px', fontSize: 13, color: C.success, fontWeight: 600 } },
          '✓ Import applied — ' + applied.products_updated + ' products / ' + applied.cells_updated + ' fields updated'
          + (applied.po_lines_renamed > 0 ? ', ' + applied.po_lines_renamed + ' open PO line(s) took the new vendor title' : '')
          + '. Details are in History and on each product\'s activity log.'),
        applied && applied.failed_count > 0 && h('div', { style: { background: C.bg, border: '1px solid ' + C.danger, borderRadius: 8, padding: '10px 14px', fontSize: 12, color: C.textMuted, maxHeight: 160, overflowY: 'auto' } },
          h('strong', { style: { color: C.danger } }, applied.failed_count + ' Product(s) Not Written (Everything Else Was): '),
          (applied.failed || []).map(function(f, i) { return h('div', { key: i }, f.sku + ' — ' + f.error); })),

        preview && h('div', { style: { display: 'flex', flexDirection: 'column', gap: 14 } },
          h('div', { style: { display: 'flex', gap: 10, flexWrap: 'wrap' } },
            tile('Rows in File', s.total_rows),
            tile('Matched SKUs', s.matched_rows, C.success),
            tile('Unmatched', s.unmatched_rows, s.unmatched_rows ? C.danger : C.text),
            tile('Products Changing', s.products_changing, C.accent),
            tile('Field Changes', s.changed_cells, C.accent),
            s.cost_changes > 0 && tile('⚠ Cost Changes', s.cost_changes, C.warning),
            s.error_rows > 0 && tile('Rows with Errors', s.error_rows, C.danger)
          ),
          preview.unknown_columns && preview.unknown_columns.length > 0 && h('div', { style: { background: C.bg, border: '1px solid ' + C.warning, borderRadius: 8, padding: '10px 14px', fontSize: 12, color: C.textMuted } },
            h('strong', { style: { color: C.warning } }, 'Ignored Columns: '),
            preview.unknown_columns.join(', '),
            ' — these headers didn\'t match any attribute.'),
          preview.unmatched_skus && preview.unmatched_skus.length > 0 && h('div', { style: { background: C.bg, border: '1px solid ' + C.border, borderRadius: 8, padding: '10px 14px', fontSize: 12, color: C.textMuted, maxHeight: 90, overflowY: 'auto' } },
            h('strong', { style: { color: C.danger } }, 'Unmatched SKUs (Skipped): '),
            preview.unmatched_skus.join(', ')),
          preview.row_errors && preview.row_errors.length > 0 && h('div', { style: { background: C.bg, border: '1px solid ' + C.danger, borderRadius: 8, padding: '10px 14px', fontSize: 12, color: C.textMuted, maxHeight: 130, overflowY: 'auto' } },
            h('strong', { style: { color: C.danger } }, 'Cell Errors (Those Cells Are Skipped): '),
            preview.row_errors.map(function(re, i) { return h('div', { key: i }, re.sku + ' — ' + re.errors.join('; ')); })),
          s.products_changing > 0
            ? h('div', null,
                h('div', { style: Object.assign({}, ieLabelStyle, { marginBottom: 6 }) }, 'Preview of Changes' + (preview.sample_changes.length < s.products_changing ? ' (first ' + preview.sample_changes.length + ' of ' + s.products_changing + ')' : '')),
                h('div', { style: { border: '1px solid ' + C.border, borderRadius: 8, overflow: 'hidden', maxHeight: 300, overflowY: 'auto' } },
                  preview.sample_changes.map(function(c, i) {
                    return h('div', { key: c.sku, style: { padding: '8px 12px', background: i % 2 ? C.bg : 'transparent', fontSize: 12, borderBottom: '1px solid ' + C.border } },
                      h('span', { style: { fontWeight: 700, color: C.text } }, c.sku),
                      h('span', { style: { color: C.textMuted } }, ' — ',
                        c.log.map(function(l) { return l[0] + ': "' + l[1] + '" → "' + l[2] + '"'; }).join('; '))
                    );
                  })
                )
              )
            : h('div', { style: { padding: 14, color: C.textMuted, fontSize: 13 } }, 'Nothing would change — every value in the file already matches the catalog.'),
          h('div', { style: { display: 'flex', gap: 8 } },
            h(Btn, { variant: 'primary', onClick: apply, disabled: applying || s.products_changing === 0 },
              h(LIcon, { name: applying ? 'loader-2' : 'check', size: 14, spin: applying }),
              applying ? ' Applying…' : ' Apply ' + s.changed_cells + ' Changes'),
            h(Btn, { onClick: reset }, 'Discard')
          )
        )
      )
    )
  );
}

// ─── Templates tab ────────────────────────────────────────────────────────
function IETemplatesTab({ templates, catalog, onRefresh, showToast }) {
  var [editing, setEditing] = useState(null); // null | {} (new) | template

  var duplicate = function(t) {
    api('/import-export/templates/' + t.id + '/duplicate', { method: 'POST', body: '{}' })
      .then(function() { onRefresh(); showToast('Template duplicated', 'success'); })
      .catch(function(e) { showToast(e.message, 'error'); });
  };
  var del = async function(t) {
    if (!(await appConfirm('Delete template "' + t.name + '"?', { danger: true, okLabel: 'Delete' }))) return;
    api('/import-export/templates/' + t.id, { method: 'DELETE' })
      .then(function() { onRefresh(); })
      .catch(function(e) { showToast(e.message, 'error'); });
  };
  var blank = function(t) { ieDownloadBlank(t, showToast); };

  // The editor takes over the whole tab (page-wide, not a modal)
  if (editing) {
    return h(IETemplateEditor, {
      catalog: catalog,
      template: editing.id ? editing : null,
      onClose: function() { setEditing(null); },
      onSaved: onRefresh,
      showToast: showToast,
    });
  }

  return h('div', null,
    h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 } },
      h('div', { style: { fontSize: 12, color: C.textMuted } }, templates.length, ' template', templates.length === 1 ? '' : 's', ' — shared by Import and Export'),
      h(Btn, { variant: 'primary', size: 'sm', onClick: function() { setEditing({}); } },
        h(LIcon, { name: 'plus', size: 12 }), ' New Template')
    ),
    h('div', { style: { maxWidth: 640 } },
      h(IETemplateList, {
        templates: templates,
        showActions: true,
        onEdit: function(t) { setEditing(t); },
        onDuplicate: duplicate,
        onDelete: del,
        onBlank: blank,
      })
    )
  );
}

// ─── History tab (imports + exports) ──────────────────────────────────────
function IEHistoryTab({ refreshKey }) {
  var [items, setItems] = useState([]);
  var [exports_, setExports] = useState([]);
  var [loading, setLoading] = useState(true);
  var [loadError, setLoadError] = useState(null);
  useEffect(function() {
    var alive = true;
    setLoading(true); setLoadError(null);
    // A failed load is shown as an error — never as "No imports or exports yet".
    Promise.all([api('/import-export/history'), api('/import-export/export-history')])
      .then(function(r) { if (alive) { setItems(r[0] || []); setExports(r[1] || []); } })
      .catch(function(e) { if (alive) setLoadError(e.message || 'Failed to load history'); })
      .finally(function() { if (alive) setLoading(false); });
    return function() { alive = false; };
  }, [refreshKey]);

  if (loading) return h('div', { style: { padding: 30, textAlign: 'center', color: C.textMuted } }, 'Loading…');
  if (loadError) return h('div', { style: { padding: 30, textAlign: 'center', color: C.danger } }, 'Could not load history: ' + loadError);
  if (!items.length && !exports_.length) return h('div', { style: { padding: 30, textAlign: 'center', color: C.textMuted } }, 'No imports or exports yet.');

  var statusColor = { applied: C.success, previewed: C.warning, applying: C.warning, failed: C.danger };
  var importsList = h('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, overflow: 'hidden' } },
    items.map(function(j, i) {
      return h('div', { key: j.id, style: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', fontSize: 12, background: i % 2 ? C.bg : 'transparent', borderBottom: i < items.length - 1 ? '1px solid ' + C.border : 'none', flexWrap: 'wrap' } },
        h('div', { style: { width: 8, height: 8, borderRadius: 4, background: statusColor[j.status] || C.textDim, flexShrink: 0 } }),
        h('div', { style: { flex: 1, minWidth: 160 } },
          h('div', { style: { fontWeight: 600, color: C.text } }, j.filename || '(file)'),
          h('div', { style: { fontSize: 11, color: C.textMuted } }, (j.template_name || 'Templateless') + ' • ' + (j.created_by_name || '?') + ' • ' + new Date(j.created_at).toLocaleString())
        ),
        h('div', { style: { color: C.textMuted, minWidth: 200, textAlign: 'right' } },
          j.matched_rows + '/' + j.total_rows + ' matched • ' + j.products_changing + ' products / ' + j.changed_cells + ' fields'
          + (j.cost_changes > 0 ? ' • ⚠ ' + j.cost_changes + ' cost' : '')),
        h('span', { style: { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', padding: '2px 8px', borderRadius: 4, background: (statusColor[j.status] || C.textDim) + '20', color: statusColor[j.status] || C.textDim } }, j.status),
        j.error && h('span', { title: j.error, style: { color: C.danger } }, h(LIcon, { name: 'alert-circle', size: 13 }))
      );
    })
  );

  var exportsList = h('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, overflow: 'hidden' } },
    exports_.map(function(e, i) {
      return h('div', { key: e.id, style: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', fontSize: 12, background: i % 2 ? C.bg : 'transparent', borderBottom: i < exports_.length - 1 ? '1px solid ' + C.border : 'none', flexWrap: 'wrap' } },
        h(LIcon, { name: 'download', size: 13, color: C.textMuted }),
        h('div', { style: { flex: 1, minWidth: 160 } },
          h('div', { style: { fontWeight: 600, color: C.text } }, e.template_name || '(ad hoc)'),
          h('div', { style: { fontSize: 11, color: C.textMuted } }, (e.created_by_name || '?') + ' • ' + new Date(e.created_at).toLocaleString())
        ),
        h('div', { style: { color: C.textMuted, minWidth: 160, textAlign: 'right' } },
          e.row_count.toLocaleString() + ' rows × ' + e.column_count + ' columns'),
        h('span', { style: { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', padding: '2px 8px', borderRadius: 4, background: C.accentSoft, color: C.accent } }, e.format)
      );
    })
  );

  return h('div', { style: { display: 'flex', flexDirection: 'column', gap: 22 } },
    h('div', null,
      h('div', { style: Object.assign({}, ieLabelStyle, { marginBottom: 8 }) }, 'Exports (' + exports_.length + ')'),
      exports_.length ? exportsList : h('div', { style: { padding: 16, color: C.textMuted, fontSize: 12 } }, 'No exports yet.')
    ),
    h('div', null,
      h('div', { style: Object.assign({}, ieLabelStyle, { marginBottom: 8 }) }, 'Imports (' + items.length + ')'),
      items.length ? importsList : h('div', { style: { padding: 16, color: C.textMuted, fontSize: 12 } }, 'No imports yet.')
    )
  );
}

// ─── Top-level page ───────────────────────────────────────────────────────
function ImportExportPage({ showToast }) {
  var [tab, setTab] = useState(_iePageCache.tab || 'import');
  var [catalog, setCatalog] = useState(null);
  var [templates, setTemplates] = useState([]);
  var [loading, setLoading] = useState(true);
  var [historyKey, setHistoryKey] = useState(0);

  useEffect(function() { _iePageCache.tab = tab; }, [tab]);

  var loadTemplates = useCallback(function() {
    api('/import-export/templates').then(setTemplates).catch(function(e) { showToast('Could not reload templates: ' + e.message, 'error'); });
  }, []);

  useEffect(function() {
    Promise.all([api('/import-export/attributes'), api('/import-export/templates')])
      .then(function(r) {
        var cat = r[0];
        cat.byKey = {};
        (cat.groups || []).forEach(function(g) { g.fields.forEach(function(f) { cat.byKey[f.key] = f; }); });
        setCatalog(cat);
        setTemplates(r[1]);
      })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setLoading(false); });
  }, []);

  if (loading) return h('div', { style: { padding: 40, color: C.textMuted } }, 'Loading…');
  if (!catalog) return h('div', { style: { padding: 40, color: C.danger } }, 'Failed to load the attribute catalog.');

  return h('div', null,
    h('div', { style: { marginBottom: 20 } },
      h('h2', { style: { margin: 0, fontSize: 22, fontWeight: 700, color: C.text } }, 'Import / Export'),
      h('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 4 } },
        'Custom product exports and bulk attribute imports — build a template of SKU + any attributes, export it filled, edit in Excel, import it back.')
    ),
    h('div', { style: { display: 'flex', gap: 4, borderBottom: '1px solid ' + C.border, marginBottom: 18 } },
      [
        { key: 'import', label: 'Import' },
        { key: 'export', label: 'Export' },
        { key: 'templates', label: 'Templates' },
        { key: 'history', label: 'History' },
      ].map(function(t) {
        var active = tab === t.key;
        return h('button', {
          key: t.key,
          onClick: function() { setTab(t.key); },
          style: {
            background: 'transparent', border: 'none', cursor: 'pointer',
            padding: '10px 16px', fontSize: 13, fontWeight: 500, fontFamily: 'inherit',
            color: active ? C.accent : C.textMuted,
            borderBottom: active ? '2px solid ' + C.accent : '2px solid transparent',
            marginBottom: -1,
          }
        }, t.label);
      })
    ),
    tab === 'import' && h(IEImportTab, { templates: templates, showToast: showToast, onHistoryRefresh: function() { setHistoryKey(function(k) { return k + 1; }); } }),
    tab === 'export' && h(IEExportTab, { templates: templates, catalog: catalog, showToast: showToast }),
    tab === 'templates' && h(IETemplatesTab, { templates: templates, catalog: catalog, onRefresh: loadTemplates, showToast: showToast }),
    tab === 'history' && h(IEHistoryTab, { refreshKey: historyKey })
  );
}

window.ImportExportPage = ImportExportPage;

;
/* ===== app-accounting.js ===== */
// Accounting module — admin-only bi-weekly cashflow forecasting.
// Exposes: AccountingCyclePage, AccountingHistoryPage, AccountingSettingsPage on window.

(function() {
  var fmt = function(n) {
    var v = Number(n || 0);
    var s = Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return (v < 0 ? '-$' : '$') + s;
  };
  // Money cells accept arithmetic (Anthony 2026-09-23, "allow math to take place within cash cycle
  // money rows"): "1200+350", "3*49.99", "(100+50)*2", "$1,200 + 350". A tiny recursive-descent
  // parser over + - * / ( ) and unary minus — no eval. Returns { ok, value } with the value rounded
  // to cents; a plain number passes through unchanged. A function declaration so tests can lift it.
  function evalMoneyExpr(text) {
    var s = String(text == null ? '' : text).replace(/[$,\s]/g, '').replace(/^=/, '');
    if (!s || !/^[0-9+\-*/().]+$/.test(s)) return { ok: false, value: null };
    var i = 0, bad = false;
    function peek() { return s.charAt(i); }
    function number() {
      var m = /^(\d+\.?\d*|\.\d+)/.exec(s.slice(i));
      if (!m) { bad = true; return 0; }
      i += m[0].length;
      return parseFloat(m[0]);
    }
    function factor() {
      var c = peek();
      if (c === '-') { i++; return -factor(); }
      if (c === '+') { i++; return factor(); }
      if (c === '(') { i++; var v = expr(); if (peek() !== ')') { bad = true; return 0; } i++; return v; }
      return number();
    }
    function term() {
      var v = factor();
      while (!bad) {
        var c = peek();
        if (c === '*') { i++; v = v * factor(); }
        else if (c === '/') { i++; var d = factor(); if (d === 0) { bad = true; return 0; } v = v / d; }
        else break;
      }
      return v;
    }
    function expr() {
      var v = term();
      while (!bad) {
        var c = peek();
        if (c === '+') { i++; v = v + term(); }
        else if (c === '-') { i++; v = v - term(); }
        else break;
      }
      return v;
    }
    var out = expr();
    if (bad || i !== s.length || !isFinite(out)) return { ok: false, value: null };
    return { ok: true, value: Math.round(out * 100) / 100 };
  }
  var fmtDate = function(d) {
    if (!d) return '';
    try {
      // PG DATE columns come through as "YYYY-MM-DD". Parsing that with new Date()
      // treats it as UTC midnight, which renders as the prior day in negative-UTC zones.
      // Split it manually so the date stays local.
      var s = String(d);
      var m = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/);
      var dt = m ? new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3])) : new Date(d);
      return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (e) { return String(d); }
  };
  // Business dates are New York days (9-27 sweep LM13), wherever the browser is.
  var todayStr = function() {
    return new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
  };
  // A cycle date from the API ('2026-07-16' or '2026-07-16T04:00:00.000Z') as 'YYYY-MM-DD'.
  var ymdOf = function(v) { return v ? String(v).slice(0, 10) : null; };
  // "Current" cycle = the cycle the server resolves as active — the EARLIEST OPEN
  // cycle. Advancing is a user decision, not a calendar event: it stays current
  // (even past its end_date) until explicitly closed, which promotes the next open
  // cycle. Prefer the server-stamped flag; the today-in-range fallback only covers
  // payloads that somehow lack the stamp (every cycle endpoint sets it).
  var isCurrentCycle = function(c) {
    if (!c || c.status === 'closed') return false;
    if (c.is_current === true) return true;
    if (c.is_current === false) return false;
    var t = todayStr();
    var s = String(c.start_date).slice(0, 10);
    var e = c.end_date ? String(c.end_date).slice(0, 10) : null;
    return s <= t && (!e || e >= t);
  };
  var statusLabel = function(c) {
    if (c.status === 'closed') return 'closed';
    if (isCurrentCycle(c)) return 'current';
    return c.status; // 'draft' for future forecast cycles
  };
  var statusColors = function(label) {
    if (label === 'closed') return { color: C.success, bg: C.successSoft };
    if (label === 'current') return { color: C.accent, bg: C.accentSoft };
    return { color: C.warning, bg: C.warningSoft }; // draft / forecast
  };

  var SECTION_DEFS = [
    { key: 'ar',          label: 'AR · Income Channels',     allowInflow: true,  allowOutflow: true,  allowAdhoc: false, hidePaymentMethod: true },
    { key: 'cash',        label: 'Cash Accounts',            allowInflow: false, allowOutflow: false, allowAdhoc: true,  balanceMode: true },
    { key: 'vendor',      label: 'AP · Vendors',             allowInflow: true,  allowOutflow: true,  allowAdhoc: true  },
    { key: 'loan',        label: 'Loans',                    allowInflow: true,  allowOutflow: true,  allowAdhoc: true  },
    { key: 'cc',          label: 'Credit Cards',             allowInflow: true,  allowOutflow: true,  allowAdhoc: true  },
    { key: 'wages_loan',  label: 'Shareholder Loans',        allowInflow: true,  allowOutflow: true,  allowAdhoc: true, hidePaymentMethod: true, groupByLender: true },
    { key: 'payroll',     label: 'Payroll',                  allowInflow: true,  allowOutflow: true,  allowAdhoc: true  },
    { key: 'expense',     label: 'Recurring Expenses',       allowInflow: true,  allowOutflow: true,  allowAdhoc: true  },
    { key: 'adhoc',       label: 'Adhoc / One-off',          allowInflow: true,  allowOutflow: true,  allowAdhoc: true  },
  ];

  // Editable cell — controlled input, debounced save on blur.
  function EditCell(props) {
    var value = props.value;
    var onSave = props.onSave;
    var type = props.type || 'number';
    var disabled = props.disabled;
    var width = props.width || 120;
    var formatInitial = function(v) {
      if (v == null) return '';
      if (type === 'number') return Number(v).toFixed(2);
      if (type === 'date') {
        // Accept "YYYY-MM-DD" or full timestamps; strip to date for <input type="date">.
        var s = String(v);
        var m = s.match(/^(\d{4}-\d{2}-\d{2})/);
        return m ? m[1] : s;
      }
      return String(v);
    };
    var initial = formatInitial(value);
    var ref = React.useRef(null);
    var origRef = React.useRef(initial);
    var [val, setVal] = React.useState(initial);
    React.useEffect(function() {
      var next = formatInitial(value);
      setVal(next); origRef.current = next;
    }, [value]);
    var commit = function() {
      var v = val;
      if (v === origRef.current) return;
      if (type === 'number') {
        if (v === '') { origRef.current = ''; onSave(0); return; }
        // arithmetic allowed (evalMoneyExpr): the cell shows the result, the number is what saves;
        // an invalid entry reverts and is reported through onInvalid (a toast on every page)
        var r = evalMoneyExpr(v);
        if (!r.ok) { setVal(origRef.current); if (props.onInvalid) props.onInvalid(v); return; }
        var disp = r.value.toFixed(2);
        var same = disp === origRef.current;
        setVal(disp); origRef.current = disp;
        if (!same) onSave(r.value);
        return;
      }
      origRef.current = v;
      if (type === 'date') onSave(v === '' ? null : v);
      else onSave(v);
    };
    // number cells are TEXT inputs (inputMode decimal): a number input drops "1200+350" before the page can read it
    var htmlType = type === 'date' ? 'date' : 'text';
    var ta = props.textAlign || (type === 'number' ? 'right' : 'left');
    return React.createElement('input', {
      ref: ref, type: htmlType, inputMode: type === 'number' ? 'decimal' : undefined,
      disabled: disabled, value: val,
      onChange: function(e) { setVal(e.target.value); },
      onFocus: function(e) { if (type === 'number') e.target.select(); },
      onBlur: commit,
      onKeyDown: function(e) { if (e.key === 'Enter') { e.target.blur(); } if (e.key === 'Escape') { setVal(origRef.current); setTimeout(function(){ ref.current && ref.current.blur(); }, 0); } },
      style: {
        width: width, background: disabled ? 'transparent' : C.bg, border: '1px solid ' + (disabled ? 'transparent' : C.border),
        color: C.text, padding: '7px 10px', borderRadius: 4, fontSize: 14, fontFamily: type === 'number' ? 'inherit' : 'inherit',
        textAlign: ta, outline: 'none', colorScheme: 'dark'
      }
    });
  }

  // Download a binary (xlsx) response via authed fetch → blob → anchor click.
  // (Mirrors the Reports module's export; cookie auth means a plain GET works.)
  var downloadXlsx = function(path, fallbackName, showToast) {
    fetch('/api' + path, { credentials: 'include' }).then(function(res) {
      if (!res.ok) {
        return res.text().then(function(t) {
          var msg = 'Export failed';
          try { msg = JSON.parse(t).error || msg; } catch (e) {}
          throw new Error(msg);
        });
      }
      var disp = res.headers.get('content-disposition') || '';
      var m = disp.match(/filename="([^"]+)"/);
      var filename = m ? m[1] : fallbackName;
      return res.blob().then(function(blob) { return { blob: blob, filename: filename }; });
    }).then(function(d) {
      var url = URL.createObjectURL(d.blob);
      var a = document.createElement('a');
      a.href = url; a.download = d.filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }).catch(function(e) { if (showToast) showToast(e.message, 'error'); });
  };

  var sectionLabelOf = function(key) {
    var d = SECTION_DEFS.filter(function(x) { return x.key === key; })[0];
    return d ? d.label : key;
  };

  // Preview + confirm modal for cycle Excel import. Shows the server's dry-run diff
  // (updates / new rows / removals / errors) and only writes when the user clicks Apply.
  function ImportCycleModal(props) {
    var p = props.preview;
    // Server-side apply guard (9-27 sweep L14): foreign Line IDs block the apply; removing a large
    // share of the cycle needs this explicit confirmation (sent as ?confirm_removals=1).
    var [confirmed, setConfirmed] = React.useState(false);
    React.useEffect(function() { setConfirmed(false); }, [p]);
    if (!p) return null;
    var c = p.counts || {};
    var removes = p.removes || [];
    var hasTombstone = removes.some(function(r) { return r.master_linked || r.bill_linked; });
    var nothing = !c.updates && !c.adds && !c.removes;
    var guard = p.guard || null;
    var blocked = !!(guard && guard.code === 'unknown_line_ids');
    var needsConfirm = !!(guard && guard.code === 'confirm_removals');

    var changeStr = function(ch) {
      var parts = [];
      if (ch.inflow) parts.push('inflow ' + fmt(ch.inflow.from) + ' → ' + fmt(ch.inflow.to));
      if (ch.outflow) parts.push('outflow ' + fmt(ch.outflow.from) + ' → ' + fmt(ch.outflow.to));
      if (ch.balance) parts.push('balance ' + (ch.balance.from == null ? '—' : fmt(ch.balance.from)) + ' → ' + (ch.balance.to == null ? '—' : fmt(ch.balance.to)));
      if (ch.payment_method) parts.push('method → ' + (ch.payment_method.to || '—'));
      if (ch.name) parts.push('renamed → "' + ch.name.to + '"');
      if (ch.notes) parts.push('notes changed');
      return parts.join(' · ');
    };
    var listBox = function(title, color, items, render) {
      if (!items || !items.length) return null;
      return React.createElement('div', { style: { marginTop: 12 } },
        React.createElement('div', { style: { fontSize: 12, fontWeight: 700, color: color, marginBottom: 6 } }, title + ' (' + items.length + ')'),
        React.createElement('div', { style: { maxHeight: 150, overflowY: 'auto', border: '1px solid ' + C.border, borderRadius: 6, background: C.bg } },
          items.map(function(it, i) {
            return React.createElement('div', { key: i, style: { padding: '6px 10px', borderBottom: i < items.length - 1 ? '1px solid ' + C.border : 'none', fontSize: 12, color: C.text } }, render(it));
          })
        )
      );
    };

    return React.createElement(window.Modal, { open: props.open, onClose: props.onCancel, title: 'Review Import', width: 640 },
      React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4 } },
        React.createElement('div', { style: { fontSize: 13, color: C.textMuted } },
          'From sheet "', React.createElement('strong', { style: { color: C.text } }, p.sheet), '". ',
          nothing ? 'No changes detected — the file matches the current cycle.' : 'Review before saving:'),
        React.createElement('div', { style: { display: 'flex', gap: 16, marginTop: 8, fontSize: 13, flexWrap: 'wrap' } },
          React.createElement('span', null, React.createElement('strong', { style: { color: C.accent } }, c.updates || 0), ' updated'),
          React.createElement('span', null, React.createElement('strong', { style: { color: C.success } }, c.adds || 0), ' new'),
          React.createElement('span', null, React.createElement('strong', { style: { color: C.danger } }, c.removes || 0), ' removed'),
          React.createElement('span', { style: { color: C.textMuted } }, (c.unchanged || 0) + ' unchanged'),
          c.errors ? React.createElement('span', null, React.createElement('strong', { style: { color: C.warning } }, c.errors), ' errors') : null
        ),

        listBox('Updates', C.accent, p.updates, function(u) {
          return React.createElement('div', null,
            React.createElement('span', { style: { color: C.textMuted } }, '[' + sectionLabelOf(u.section) + '] '),
            React.createElement('strong', null, u.name), ' — ', changeStr(u.changes));
        }),
        listBox('New rows', C.success, p.adds, function(a) {
          var amt = a.section === 'cash' ? ('balance ' + (a.balance_snapshot == null ? '—' : fmt(a.balance_snapshot)))
            : ('+' + fmt(a.inflow) + ' / -' + fmt(a.outflow));
          return React.createElement('div', null,
            React.createElement('span', { style: { color: C.textMuted } }, '[' + sectionLabelOf(a.section) + '] '),
            React.createElement('strong', null, a.name), ' — ', amt);
        }),
        listBox('Removed', C.danger, removes, function(r) {
          return React.createElement('div', null,
            React.createElement('span', { style: { color: C.textMuted } }, '[' + sectionLabelOf(r.section) + '] '),
            React.createElement('strong', null, r.name),
            (r.master_linked || r.bill_linked) ? React.createElement('span', { style: { marginLeft: 8, fontSize: 10, padding: '1px 6px', borderRadius: 3, background: C.warningSoft, color: C.warning, fontWeight: 700 } }, r.bill_linked ? 'BILL.COM' : 'FROM SETTINGS') : null);
        }),
        listBox('Skipped (errors)', C.warning, p.errors, function(e) {
          return React.createElement('div', null, 'Row ' + e.row + ': ' + e.message);
        }),

        hasTombstone ? React.createElement('div', { style: { marginTop: 12, fontSize: 12, color: C.warning, background: C.warningSoft, border: '1px solid ' + C.warning + '55', borderRadius: 6, padding: '8px 12px' } },
          'Some removed rows are linked to Settings/Bill.com. They’ll be tombstoned so Refresh/Sync won’t auto-add them back. Re-add from the UI if that wasn’t intended.') : null,

        blocked ? React.createElement('div', { style: { marginTop: 12, fontSize: 12, color: C.danger, background: C.dangerSoft, border: '1px solid ' + C.danger + '55', borderRadius: 6, padding: '8px 12px' } },
          React.createElement('strong', null, 'Cannot Import: '), guard.error) : null,
        needsConfirm ? React.createElement('label', { style: { marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: C.danger, background: C.dangerSoft, border: '1px solid ' + C.danger + '55', borderRadius: 6, padding: '8px 12px', cursor: 'pointer' } },
          React.createElement('input', { type: 'checkbox', checked: confirmed, onChange: function(e) { setConfirmed(e.target.checked); }, style: { accentColor: C.danger } }),
          'Yes, remove ' + (guard.removes || c.removes || 0) + ' of the cycle’s ' + (guard.existing || '') + ' lines') : null,

        React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 } },
          React.createElement(window.Btn, { variant: 'ghost', onClick: props.onCancel, disabled: props.busy }, nothing ? 'Close' : 'Cancel'),
          !nothing && React.createElement(window.Btn, { variant: c.removes ? 'danger' : 'primary', onClick: function() { props.onApply(needsConfirm && confirmed); }, disabled: props.busy || blocked || (needsConfirm && !confirmed) },
            props.busy ? 'Applying…' : 'Apply ' + ((c.updates || 0) + (c.adds || 0) + (c.removes || 0)) + ' change' + (((c.updates || 0) + (c.adds || 0) + (c.removes || 0)) === 1 ? '' : 's'))
        )
      )
    );
  }

  // ---------- AccountingCyclePage ----------
  function AccountingCyclePage(props) {
    var showToast = props.showToast;
    var badMoney = function(t) { showToast('"' + t + '" is not a number or a sum', 'error'); }; // money-cell arithmetic that did not parse
    var onNavigate = props.onNavigate;
    var [cycle, setCycle] = React.useState(null);
    var [lines, setLines] = React.useState([]);
    var [summary, setSummary] = React.useState(null);
    var [loading, setLoading] = React.useState(true);
    var [cycleId, setCycleId] = React.useState(props.cycleId || null);
    var [generateOpen, setGenerateOpen] = React.useState(false);
    var [closeOpen, setCloseOpen] = React.useState(false);
    var [closeBusy, setCloseBusy] = React.useState(false);
    var [reopenBusy, setReopenBusy] = React.useState(false);
    var [activityOpen, setActivityOpen] = React.useState(false);
    var [collapsed, setCollapsed] = React.useState(function() {
      // Default all sections collapsed; user clicks chevron to expand the ones they care about.
      var c = {};
      SECTION_DEFS.forEach(function(d) { c[d.key] = true; });
      return c;
    });
    var [vendorPickerOpen, setVendorPickerOpen] = React.useState(null); // section name
    var [importPreview, setImportPreview] = React.useState(null);
    var [importFile, setImportFile] = React.useState(null);
    var [importing, setImporting] = React.useState(false);
    var fileInputRef = React.useRef(null);
    var [neighbors, setNeighbors] = React.useState({ prev: null, next: null });

    var load = async function(id) {
      setLoading(true);
      try {
        var targetId = id || cycleId;
        if (!targetId) {
          var a = await window.api('/accounting/cycles/active');
          targetId = a.id;
          if (targetId) setCycleId(targetId);
        }
        if (!targetId) { setCycle(null); setLines([]); setSummary(null); setLoading(false); return; }
        var data = await window.api('/accounting/cycles/' + targetId);
        setCycle(data.cycle); setLines(data.lines || []); setSummary(data.summary);
        // Previous / Next come with the cycle (Anthony 9-10) — no second request, live on first paint
        setNeighbors((data.neighbors && typeof data.neighbors === 'object') ? { prev: data.neighbors.prev || null, next: data.neighbors.next || null } : { prev: null, next: null });
      } catch (e) { showToast(e.message, 'error'); }
      finally { setLoading(false); }
    };

    React.useEffect(function() { load(props.cycleId); }, [props.cycleId]);

    var updateLine = async function(line, patch) {
      try {
        var r = await window.api('/accounting/cycles/' + cycle.id + '/lines/' + line.id, { method: 'PUT', body: JSON.stringify(patch) });
        setLines(function(prev) { return prev.map(function(l) { return l.id === line.id ? Object.assign({}, l, r.line) : l; }); });
        if (r.summary) setSummary(r.summary);
        showToast('Saved');
      } catch (e) { showToast(e.message, 'error'); }
    };

    var addLine = async function(section, payload) {
      try {
        var r = await window.api('/accounting/cycles/' + cycle.id + '/lines', { method: 'POST', body: JSON.stringify(Object.assign({ section: section }, payload)) });
        setLines(function(prev) { return prev.concat([r.line]); });
        if (r.summary) setSummary(r.summary);
        showToast('Line added');
      } catch (e) { showToast(e.message, 'error'); }
    };

    var deleteLine = async function(line) {
      if (!(await appConfirm('Remove "' + line.name_snapshot + '" from this cycle?', { danger: true, okLabel: 'Remove' }))) return;
      try {
        var r = await window.api('/accounting/cycles/' + cycle.id + '/lines/' + line.id, { method: 'DELETE' });
        setLines(function(prev) { return prev.filter(function(l) { return l.id !== line.id; }); });
        if (r.summary) setSummary(r.summary);
        showToast('Line removed');
      } catch (e) { showToast(e.message, 'error'); }
    };

    var updateHeader = async function(patch) {
      try {
        var r = await window.api('/accounting/cycles/' + cycle.id, { method: 'PUT', body: JSON.stringify(patch) });
        setCycle(r);
        // Re-fetch summary
        var data = await window.api('/accounting/cycles/' + cycle.id);
        setSummary(data.summary);
        showToast('Saved');
      } catch (e) { showToast(e.message, 'error'); }
    };

    var closeCycle = async function() {
      if (closeBusy) return;
      setCloseBusy(true);
      try {
        var r = await window.api('/accounting/cycles/' + cycle.id + '/close', { method: 'POST' });
        setCloseOpen(false);
        if (r.next_cycle && r.next_cycle.id) {
          showToast(r.next_cycle.already_existed ? 'Cycle closed — next cycle already exists' : 'Cycle closed — next cycle created', 'success');
          // Navigate so URL updates to /accounting/{nextId}
          onNavigate('accounting', r.next_cycle.id);
        } else {
          showToast('Cycle closed', 'success');
          load(cycle.id);
        }
      } catch (e) { showToast(e.message, 'error'); }
      finally { setCloseBusy(false); }
    };

    var reopenCycle = async function() {
      if (reopenBusy) return; // double click while the first reopen is in flight (sweep M5)
      if (!(await appConfirm('Reopen this cycle for editing? Subsequent edits will replace the snapshot.', { danger: true, okLabel: 'Reopen' }))) return;
      setReopenBusy(true);
      try {
        await window.api('/accounting/cycles/' + cycle.id + '/reopen', { method: 'POST' });
        showToast('Cycle reopened', 'success');
        load(cycle.id);
      } catch (e) { showToast(e.message, 'error'); }
      finally { setReopenBusy(false); }
    };

    var syncFromSettings = async function() {
      try {
        var r = await window.api('/accounting/cycles/' + cycle.id + '/sync', { method: 'POST' });
        var parts = [];
        if (r.added > 0) parts.push('added ' + r.added + ' row' + (r.added === 1 ? '' : 's'));
        if (r.notes_backfilled > 0) parts.push('filled in ' + r.notes_backfilled + ' note' + (r.notes_backfilled === 1 ? '' : 's'));
        if (parts.length === 0) showToast('Cycle already in sync — nothing to update', 'success');
        else showToast('Sync: ' + parts.join(', '), 'success');
        load(cycle.id);
      } catch (e) { showToast(e.message, 'error'); }
    };

    var onPickImportFile = function(e) {
      var file = e.target.files && e.target.files[0];
      e.target.value = ''; // reset so re-picking the same file fires onChange again
      if (!file) return;
      setImporting(true);
      window.apiUpload('/accounting/cycles/' + cycle.id + '/import?mode=preview', file).then(function(report) {
        setImportFile(file);
        setImportPreview(report);
      }).catch(function(err) { showToast(err.message, 'error'); }).finally(function() { setImporting(false); });
    };
    var applyImport = function(confirmRemovals) {
      if (!importFile || importing) return;
      setImporting(true);
      window.apiUpload('/accounting/cycles/' + cycle.id + '/import?mode=apply' + (confirmRemovals ? '&confirm_removals=1' : ''), importFile).then(function(report) {
        var cc = report.counts || {};
        showToast('Imported: ' + (cc.updates || 0) + ' updated, ' + (cc.adds || 0) + ' added, ' + (cc.removes || 0) + ' removed', 'success');
        setImportPreview(null); setImportFile(null);
        load(cycle.id);
      }).catch(function(err) { showToast(err.message, 'error'); }).finally(function() { setImporting(false); });
    };

    var isClosed = cycle && cycle.status === 'closed';

    // Empty state
    if (loading) return React.createElement('div', { style: { padding: 40, color: C.textMuted } }, 'Loading…');
    if (!cycle) {
      return React.createElement('div', { style: { padding: 40 } },
        React.createElement(window.Empty, { title: 'No cycles yet', sub: 'Generate your first bi-weekly cycles to start tracking inflows and outflows. Use 1 cycle for now, or 51 to forecast a year ahead.' }),
        React.createElement('div', { style: { textAlign: 'center', marginTop: 16 } },
          React.createElement(window.Btn, { variant: 'primary', onClick: function() { setGenerateOpen(true); } }, 'Generate Cycles')
        ),
        React.createElement(GenerateBatchModal, { open: generateOpen, onClose: function() { setGenerateOpen(false); }, onCreated: function(r) { setGenerateOpen(false); showToast('Generated ' + r.created + ' cycles', 'success'); props.onNavigate('accounting-history'); }, showToast: showToast })
      );
    }

    var byKey = {};
    SECTION_DEFS.forEach(function(d) { byKey[d.key] = []; });
    lines.forEach(function(l) {
      var section = l.section;
      if (!byKey[section]) byKey[section] = [];
      byKey[section].push(l);
    });

    var neighborLabel = function(c) {
      if (!c) return '';
      return 'Cash Cycle · ' + fmtDate(c.start_date) + (c.end_date ? ' → ' + fmtDate(c.end_date) : '');
    };

    return React.createElement('div', { style: { padding: '20px 28px 60px', maxWidth: 1700, margin: '0 auto' } },
      // Header: the heading on its own line; under it Previous / Next on the left and the cycle
      // actions on the right, on ONE line (Anthony 9-10); notes beneath.
      React.createElement('div', { style: { marginBottom: 14 } },
        React.createElement('h2', { style: { margin: 0, fontSize: 18, fontWeight: 600, color: C.text } },
          'Cash Cycle · ', fmtDate(cycle.start_date),
          cycle.end_date ? ' → ' + fmtDate(cycle.end_date) : '',
          (function() {
            var lbl = statusLabel(cycle);
            var sc = statusColors(lbl);
            return React.createElement('span', { style: { marginLeft: 12, fontSize: 11, padding: '3px 8px', borderRadius: 4, background: sc.bg, color: sc.color, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 } }, lbl);
          })()
        ),
        React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginTop: 8 } },
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
            React.createElement('span', { title: neighbors.prev ? neighborLabel(neighbors.prev) : 'No earlier cycle' },
              React.createElement(window.Btn, { variant: 'default', size: 'sm', disabled: !neighbors.prev, style: { minWidth: 108, justifyContent: 'center' }, onClick: function() { if (neighbors.prev) onNavigate('accounting', neighbors.prev.id); } },
                React.createElement(window.LIcon, { name: 'chevron-left', size: 14 }), ' Previous')
            ),
            React.createElement('span', { title: neighbors.next ? neighborLabel(neighbors.next) : 'No later cycle' },
              React.createElement(window.Btn, { variant: 'default', size: 'sm', disabled: !neighbors.next, style: { minWidth: 108, justifyContent: 'center' }, onClick: function() { if (neighbors.next) onNavigate('accounting', neighbors.next.id); } },
                'Next ', React.createElement(window.LIcon, { name: 'chevron-right', size: 14 }))
            )
          ),
          React.createElement('div', { style: { display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' } },
          React.createElement('span', { title: 'View all cycles past and future, with summary stats per cycle' },
            React.createElement(window.Btn, { variant: 'primary', size: 'sm', onClick: function() { onNavigate('accounting-history'); } }, 'All Cycles')
          ),
          React.createElement(window.Btn, { variant: 'default', size: 'sm', onClick: function() { onNavigate('settings-accounting'); } }, 'Settings'),
          React.createElement('span', { title: 'Download this cycle as an Excel workbook (Cycle Lines + Summary sheets)' },
            React.createElement(window.Btn, { variant: 'default', size: 'sm', onClick: function() { downloadXlsx('/accounting/cycles/' + cycle.id + '/export', 'cycle_' + cycle.id + '.xlsx', showToast); } },
              React.createElement(window.LIcon, { name: 'download', size: 14 }), ' Export Excel')
          ),
          !isClosed && React.createElement('span', { title: 'Upload an edited copy of this cycle. You’ll see a preview of every change before anything is saved.' },
            React.createElement(window.Btn, { variant: 'default', size: 'sm', onClick: function() { if (fileInputRef.current) fileInputRef.current.click(); }, disabled: importing },
              React.createElement(window.LIcon, { name: 'upload', size: 14 }), importing && !importPreview ? ' Reading…' : ' Import Excel')
          ),
          React.createElement('input', { ref: fileInputRef, type: 'file', accept: '.xlsx,.xls', style: { display: 'none' }, onChange: onPickImportFile }),
          !isClosed && React.createElement('span', { title: 'Pull in any entities from Settings (AR channels, vendors, loans, etc.) that aren’t in this cycle yet, and backfill any empty notes from master defaults' },
            React.createElement(window.Btn, { variant: 'default', size: 'sm', onClick: syncFromSettings }, 'Sync from Settings')
          ),
          React.createElement('span', { title: 'Bulk-create future cycles (e.g. 51 bi-weekly cycles for a full year of forecast). For a single new cycle, set count=1.' },
            React.createElement(window.Btn, { variant: 'default', size: 'sm', onClick: function() { setGenerateOpen(true); } }, 'Generate Cycles')
          ),
          React.createElement('span', { title: 'View this cycle\'s structural change history' },
            React.createElement(window.Btn, { variant: 'default', size: 'sm', onClick: function() { setActivityOpen(true); } }, 'Activity Log')
          ),
          isClosed
            ? React.createElement(window.Btn, { variant: 'default', size: 'sm', onClick: reopenCycle, disabled: reopenBusy }, reopenBusy ? 'Reopening…' : 'Reopen')
            : React.createElement(window.Btn, { variant: 'danger', size: 'sm', onClick: function() { setCloseOpen(true); } }, 'Close Cycle')
          )
        ),
        React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 6 } }, cycle.notes || ' ')
      ),

      // Summary card (sticky)
      React.createElement(SummaryCard, { cycle: cycle, summary: summary, onUpdateHeader: updateHeader, disabled: isClosed, byKey: byKey, onInvalid: badMoney }),

      // Sections
      SECTION_DEFS.map(function(def) {
        var rows = byKey[def.key] || [];
        var isCollapsed = !!collapsed[def.key];
        var totalIn = rows.reduce(function(s, r) { return s + Number(r.inflow || 0); }, 0);
        var totalOut = rows.reduce(function(s, r) { return s + Number(r.outflow || 0); }, 0);
        var balanceTotal = def.balanceMode ? rows.reduce(function(s, r) { return s + (r.balance_snapshot != null ? Number(r.balance_snapshot) : 0); }, 0) : 0;
        return React.createElement('div', { key: def.key, style: { marginTop: 18, background: C.surface, border: '1px solid ' + C.border, borderRadius: 10, overflow: 'hidden' } },
          // Section header
          React.createElement('div', {
            onClick: function() { setCollapsed(function(c) { var n = Object.assign({}, c); n[def.key] = !n[def.key]; return n; }); },
            style: { padding: '12px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', borderBottom: isCollapsed ? 'none' : '1px solid ' + C.border, userSelect: 'none' }
          },
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
              React.createElement(window.LIcon, { name: isCollapsed ? 'chevron-right' : 'chevron-down', size: 18 }),
              React.createElement('span', { style: { fontSize: 15, fontWeight: 600, color: C.text } }, def.label),
              React.createElement('span', { style: { fontSize: 13, color: C.textMuted } },
                def.groupByLender
                  ? (function() { var c = Object.keys(rows.reduce(function(a, r) { a[(r.sh_lender && String(r.sh_lender).trim()) || '(No Lender)'] = 1; return a; }, {})).length; return '(' + c + ' shareholder' + (c === 1 ? '' : 's') + ' · ' + rows.length + ' loan' + (rows.length === 1 ? '' : 's') + ')'; })()
                  : '(' + rows.length + ')')
            ),
            def.balanceMode
              ? React.createElement('div', { style: { display: 'flex', gap: 18, alignItems: 'center', fontSize: 14, fontFamily: 'inherit' } },
                  React.createElement('span', { style: { color: C.text, fontWeight: 600 } }, 'Total ' + fmt(balanceTotal)),
                  React.createElement('span', { style: { color: C.textMuted, fontSize: 12, fontFamily: 'inherit' } }, '→ Starting Cash')
                )
              : React.createElement('div', { style: { display: 'flex', gap: 18, alignItems: 'center', fontSize: 14, fontFamily: 'inherit' } },
                  def.allowInflow && React.createElement('span', { style: { color: C.success } }, '+ ' + fmt(totalIn)),
                  def.allowOutflow && React.createElement('span', { style: { color: C.danger } }, '- ' + fmt(totalOut)),
                  React.createElement('span', { style: { color: totalIn - totalOut >= 0 ? C.success : C.danger, fontWeight: 600 } }, 'Net ' + fmt(totalIn - totalOut))
                )
          ),
          // Section body
          !isCollapsed && React.createElement(SectionTable, {
            def: def, rows: rows, disabled: isClosed,
            onUpdate: updateLine, onAdd: addLine, onDelete: deleteLine, onInvalid: badMoney,
            onPickVendor: def.key === 'vendor' ? function() { setVendorPickerOpen('vendor'); } : null
          })
        );
      }),

      // Modals
      React.createElement(GenerateBatchModal, { open: generateOpen, onClose: function() { setGenerateOpen(false); }, onCreated: function(r) { setGenerateOpen(false); showToast('Generated ' + r.created + ' cycle' + (r.created === 1 ? '' : 's'), 'success'); props.onNavigate('accounting-history'); }, showToast: showToast }),
      React.createElement(CloseCycleModal, { open: closeOpen, onClose: function() { setCloseOpen(false); }, onConfirm: closeCycle, confirmBusy: closeBusy, summary: summary }),
      React.createElement(CycleActivityModal, { open: activityOpen, onClose: function() { setActivityOpen(false); }, cycleId: cycle.id, cycleStart: cycle.start_date, showToast: showToast }),
      React.createElement(VendorPickerModal, {
        open: !!vendorPickerOpen, onClose: function() { setVendorPickerOpen(null); },
        existingIds: (byKey.vendor || []).filter(function(l){ return l.entity_id; }).map(function(l){ return l.entity_id; }),
        onPick: function(vendor) {
          setVendorPickerOpen(null);
          addLine('vendor', { entity_type: 'vendor', entity_id: vendor.id, name_snapshot: vendor.vendor_name, payment_method: vendor.payment_method || null });
        },
        showToast: showToast
      }),
      React.createElement(ImportCycleModal, {
        open: !!importPreview, preview: importPreview, busy: importing,
        onCancel: function() { setImportPreview(null); setImportFile(null); },
        onApply: applyImport
      })
    );
  }

  // Summary card — sticky-ish top block with Starting/Ending/Net + per-section net totals.
  function SummaryCard(props) {
    var cycle = props.cycle;
    var s = props.summary || {};
    var disabled = props.disabled;
    var byKey = props.byKey || {};
    var cashLines = byKey.cash || [];
    var hasBalances = cashLines.some(function(l) { return l.balance_snapshot != null; });

    // Per-section net contribution (inflow − outflow). Sign drives color.
    var sectionNet = function(key) {
      var rows = byKey[key] || [];
      return rows.reduce(function(acc, l) {
        return acc + Number(l.inflow || 0) - Number(l.outflow || 0);
      }, 0);
    };

    var stat = function(label, value, color, sub) {
      return React.createElement('div', { style: { padding: '12px 16px', minWidth: 150 } },
        React.createElement('div', { style: { fontSize: 11, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 } }, label),
        React.createElement('div', { style: { fontSize: 17, color: color || C.text, fontWeight: 600, fontFamily: 'inherit', marginTop: 6 } }, value),
        sub && React.createElement('div', { style: { fontSize: 10, color: C.textDim, marginTop: 2 } }, sub)
      );
    };
    var signed = function(v) { return (v >= 0 ? '+' : '') + fmt(v); };
    var colorOf = function(v) { return v > 0 ? C.success : v < 0 ? C.danger : C.textMuted; };

    var ending = s.ending_cash != null ? Number(s.ending_cash) : 0;
    var net = s.net_cashflow != null ? Number(s.net_cashflow) : 0;
    var startCash = s.starting_cash != null ? Number(s.starting_cash) : Number(cycle.starting_cash || 0);

    var arNet = sectionNet('ar');
    var apNet = sectionNet('vendor');
    var loanNet = sectionNet('loan');
    var ccNet = sectionNet('cc');
    var wagesLoanNet = sectionNet('wages_loan');
    var payrollNet = sectionNet('payroll');
    var expenseNet = sectionNet('expense');
    var adhocNet = sectionNet('adhoc');

    return React.createElement('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 10, padding: 4 } },
      // Top row: cash totals
      React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', alignItems: 'center' } },
        hasBalances
          ? stat('Starting Cash', fmt(startCash), C.text, '= sum of cash balances')
          : React.createElement('div', { style: { padding: '12px 16px', minWidth: 170, display: 'flex', flexDirection: 'column', gap: 4 } },
              React.createElement('div', { style: { fontSize: 11, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 } }, 'Starting Cash'),
              React.createElement(EditCell, { onInvalid: props.onInvalid, value: cycle.starting_cash, onSave: function(v) { props.onUpdateHeader({ starting_cash: v }); }, disabled: disabled, width: 140 }),
              React.createElement('div', { style: { fontSize: 10, color: C.textDim, marginTop: 2 } }, 'enter cash balances to auto-sum')
            ),
        stat('Ending Cash', fmt(ending), ending >= 0 ? C.text : C.danger),
        stat('Net Cashflow', signed(net), colorOf(net))
      ),
      // Divider
      React.createElement('div', { style: { borderTop: '1px solid ' + C.border, margin: '4px 12px' } }),
      // Bottom row: per-section totals
      React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', alignItems: 'center' } },
        stat('AR Total', signed(arNet), colorOf(arNet)),
        stat('AP Total', signed(apNet), colorOf(apNet)),
        stat('Loans', signed(loanNet), colorOf(loanNet)),
        stat('Credit Cards', signed(ccNet), colorOf(ccNet)),
        stat('Shareholder Loans', signed(wagesLoanNet), colorOf(wagesLoanNet)),
        stat('Payroll', signed(payrollNet), colorOf(payrollNet)),
        stat('Recurring Expenses', signed(expenseNet), colorOf(expenseNet)),
        stat('Adhoc/One-off', signed(adhocNet), colorOf(adhocNet))
      )
    );
  }

  // Section table — list of rows for one section.
  function SectionTable(props) {
    var def = props.def;
    var rows = props.rows || [];
    var disabled = props.disabled;
    var adhocAllowed = def.allowAdhoc && !disabled;
    var [showAdd, setShowAdd] = React.useState(false);
    var [adhocName, setAdhocName] = React.useState('');
    var [adhocIn, setAdhocIn] = React.useState('');
    var [adhocOut, setAdhocOut] = React.useState('');
    var [adhocBalance, setAdhocBalance] = React.useState('');
    var [adhocMethod, setAdhocMethod] = React.useState('');
    var [adhocNotes, setAdhocNotes] = React.useState('');
    var [adhocBusy, setAdhocBusy] = React.useState(false);
    var [collapsedLenders, setCollapsedLenders] = React.useState({});
    // Parse a leading date from a loan name ("4/1/2023 36MO", "06/2023 …") for chronological sort.
    var leadDate = function(name) {
      var s = String(name || '').trim(), m;
      m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
      if (m) { var y = parseInt(m[3]); if (y < 100) y += 2000; return new Date(y, parseInt(m[1]) - 1, parseInt(m[2])).getTime(); }
      m = s.match(/^(\d{1,2})\/(\d{4})\b/);
      if (m) return new Date(parseInt(m[2]), parseInt(m[1]) - 1, 1).getTime();
      return Infinity;
    };

    var submitAdhoc = async function() {
      if (!adhocName.trim() || adhocBusy) return;
      // the same arithmetic the cells take (evalMoneyExpr); a bad entry stops the add and says which
      var money = function(s) { if (s === '' || s == null) return 0; var r = evalMoneyExpr(s); return r.ok ? r.value : NaN; };
      var inV = money(adhocIn), outV = money(adhocOut), balV = (def.balanceMode && adhocBalance !== '') ? money(adhocBalance) : null;
      if (isNaN(inV) || isNaN(outV) || (balV !== null && isNaN(balV))) {
        var badOne = [adhocBalance, adhocIn, adhocOut].filter(function(x) { return x !== '' && isNaN(money(x)); })[0];
        if (props.onInvalid) props.onInvalid(badOne);
        return;
      }
      setAdhocBusy(true);
      var body = {
        entity_type: null, entity_id: null,
        name_snapshot: adhocName.trim(),
        inflow: inV, outflow: outV,
        payment_method: adhocMethod || null, notes: adhocNotes || null
      };
      if (balV !== null) body.balance_snapshot = balV;
      try {
        await props.onAdd(def.key, body);
        setAdhocName(''); setAdhocIn(''); setAdhocOut(''); setAdhocBalance(''); setAdhocMethod(''); setAdhocNotes('');
        setShowAdd(false);
      } finally { setAdhocBusy(false); }
    };

    if (!rows.length && !adhocAllowed) {
      return React.createElement('div', { style: { padding: 18, color: C.textMuted, fontSize: 12 } }, 'No rows for this section. Add entities in Settings > Accounting.');
    }

    // One data row. opts.name overrides the displayed name; opts.indent left-pads it (grouped mode).
    function renderRow(l, opts) {
      var isLinked = !!l.entity_id;
      var isBill = !!l.bdc_bill_id;
      var billPaid = isBill && l.bdc_payment_status === 'paid';
      // is_paid stores the manual "Complete" checkbox state — visual-only,
      // doesn't affect any totals or summary math. Column name stays for
      // back-compat; the UI just calls it "Complete".
      var isComplete = !!l.is_paid || billPaid;
      var rowStyle = { borderTop: '1px solid ' + C.border };
      if (isComplete) { rowStyle.opacity = 0.6; rowStyle.textDecoration = 'line-through'; }
      var displayName = (opts && opts.name != null) ? opts.name : l.name_snapshot;
      var namePad = (opts && opts.indent) ? '8px 14px 8px 38px' : '8px 14px';
      var toggleComplete = function(e) {
        e.stopPropagation();
        if (disabled) return;
        // Manual toggle does NOT touch Bill.com-driven paid state; for non-Bill rows it just flips is_paid.
        props.onUpdate(l, { is_paid: !l.is_paid });
      };
      var completeInteractive = !(disabled || billPaid);
      var onCompleteEnter = function(e) {
        if (!completeInteractive) return;
        // Only the border shifts on hover — keep the inner box bg untouched per UX.
        e.currentTarget.style.borderColor = '#10B981';
      };
      var onCompleteLeave = function(e) {
        if (!completeInteractive) return;
        e.currentTarget.style.borderColor = isComplete ? '#10B981' : C.textMuted;
      };
      var completeBtn = React.createElement('button', {
        onClick: toggleComplete,
        onMouseEnter: onCompleteEnter,
        onMouseLeave: onCompleteLeave,
        title: isComplete ? (billPaid ? 'Paid via Bill.com' : 'Marked complete — click to undo') : 'Mark complete',
        disabled: disabled || billPaid,
        style: {
          padding: 0, width: 22, height: 22, minWidth: 22, borderRadius: 4,
          border: '2px solid ' + (isComplete ? '#10B981' : C.textMuted),
          background: isComplete ? '#10B981' : C.bg,
          boxShadow: isComplete ? 'none' : 'inset 0 0 0 1px ' + C.bg,
          cursor: completeInteractive ? 'pointer' : 'default',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          verticalAlign: 'middle', textDecoration: 'none', opacity: billPaid ? 0.7 : 1,
          transition: 'border-color 0.12s'
        }
      }, isComplete ? React.createElement(window.LIcon, { name: 'check', size: 14, color: '#fff' }) : null);
      return React.createElement('tr', { key: l.id, style: rowStyle },
        React.createElement('td', { style: { padding: namePad, color: C.text, fontFamily: 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } },
          isLinked || isBill
            ? React.createElement('span', null,
                displayName,
                isBill && React.createElement(window.BdcBillHover, {
                  tip: 'From Bill.com' + (billPaid ? ' · paid' : ''),
                  detail: 'Invoice ' + (l.bdc_invoice_number || '—')
                    + (l.bdc_amount != null ? ' · $' + Number(l.bdc_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '')
                    + (l.bdc_due_date ? ' · Due ' + fmtDate(l.bdc_due_date) : ''),
                  bdcBillId: l.bdc_bill_id
                },
                  React.createElement('a', {
                    href: window.bdcBillUrl(l.bdc_bill_id), target: '_blank', rel: 'noopener noreferrer',
                    onClick: function(e) { e.stopPropagation(); },
                    style: { marginLeft: 8, padding: '2px 8px', fontSize: 10, borderRadius: 3, background: '#007BFF22', color: '#5BA8FF', fontWeight: 600, letterSpacing: '0.04em', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }
                  }, 'BILL.COM', React.createElement(window.LIcon, { name: 'external-link', size: 9 }))
                )
              )
            : React.createElement(EditCell, { type: 'text', value: l.name_snapshot, onSave: function(v) { props.onUpdate(l, { name_snapshot: v }); }, disabled: disabled, width: '100%' })
        ),
        def.balanceMode && React.createElement('td', { style: { padding: '6px 8px', textAlign: 'right' } },
          React.createElement(EditCell, { onInvalid: props.onInvalid, value: l.balance_snapshot, onSave: function(v) { props.onUpdate(l, { balance_snapshot: v }); }, disabled: disabled, width: '100%' })
        ),
        !def.balanceMode && def.allowInflow && React.createElement('td', { style: { padding: '6px 8px', textAlign: 'right' } },
          React.createElement(EditCell, { onInvalid: props.onInvalid, value: l.inflow, onSave: function(v) { props.onUpdate(l, { inflow: v }); }, disabled: disabled, width: '100%' })
        ),
        !def.balanceMode && def.allowOutflow && React.createElement('td', { style: { padding: '6px 8px', textAlign: 'right' } },
          React.createElement(EditCell, { onInvalid: props.onInvalid, value: l.outflow, onSave: function(v) { props.onUpdate(l, { outflow: v }); }, disabled: disabled, width: '100%' })
        ),
        !def.balanceMode && !def.hidePaymentMethod && React.createElement('td', { style: { padding: '6px 8px' } },
          React.createElement(EditCell, { type: 'text', value: l.payment_method || '', onSave: function(v) { props.onUpdate(l, { payment_method: v }); }, disabled: disabled, width: '100%' })
        ),
        React.createElement('td', { style: { padding: '6px 8px' } },
          React.createElement(EditCell, { type: 'text', value: l.notes || '', onSave: function(v) { props.onUpdate(l, { notes: v }); }, disabled: disabled, width: '100%' })
        ),
        React.createElement('td', { style: { padding: '4px 6px', textAlign: 'right', whiteSpace: 'nowrap' } },
          React.createElement('div', { style: { display: 'inline-flex', alignItems: 'center', gap: 6 } },
            completeBtn,
            !disabled && React.createElement('button', {
              onClick: function() { props.onDelete(l); },
              onMouseEnter: function(e) { e.currentTarget.style.color = '#F87171'; },
              onMouseLeave: function(e) { e.currentTarget.style.color = C.textDim; },
              title: 'Remove from cycle',
              style: { background: 'transparent', border: 'none', color: C.textDim, cursor: 'pointer', padding: 4, display: 'inline-flex', alignItems: 'center', transition: 'color 0.12s' }
            }, React.createElement(window.LIcon, { name: 'trash-2', size: 18 }))
          )
        )
      );
    }

    // Body: grouped-by-lender (collapsible, default collapsed, showing total payment) for the
    // Shareholder Loans section; a flat row list everywhere else.
    var tbodyChildren;
    if (def.groupByLender) {
      var colCount = 1 + (def.allowInflow ? 1 : 0) + (def.allowOutflow ? 1 : 0) + (def.hidePaymentMethod ? 0 : 1) + 1 + 1;
      var byLender = {};
      rows.forEach(function(l) {
        var key = (l.sh_lender && String(l.sh_lender).trim()) || '(No Lender)';
        if (!byLender[key]) byLender[key] = { rows: [], totalOut: 0, totalIn: 0 };
        byLender[key].rows.push(l);
        byLender[key].totalOut += Number(l.outflow || 0);
        byLender[key].totalIn += Number(l.inflow || 0);
      });
      var lenderNames = Object.keys(byLender).sort(function(a, b) { return a.localeCompare(b); });
      tbodyChildren = [];
      lenderNames.forEach(function(name) {
        var g = byLender[name];
        var isCol = collapsedLenders[name] !== false; // default collapsed; explicit false = expanded
        tbodyChildren.push(React.createElement('tr', {
          key: 'lh-' + name,
          onClick: (function(c) { return function() { setCollapsedLenders(function(prev) { var n = Object.assign({}, prev); n[name] = !c; return n; }); }; })(isCol),
          style: { background: C.bg, borderTop: '2px solid ' + C.border, cursor: 'pointer', userSelect: 'none' }
        },
          React.createElement('td', { colSpan: colCount, style: { padding: '10px 14px' } },
            React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' } },
              React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
                React.createElement(window.LIcon, { name: isCol ? 'chevron-right' : 'chevron-down', size: 16 }),
                React.createElement('span', { style: { fontSize: 14, fontWeight: 700, color: C.text } }, name),
                React.createElement('span', { style: { fontSize: 12, color: C.textMuted } }, g.rows.length + ' loan' + (g.rows.length === 1 ? '' : 's'))
              ),
              React.createElement('div', { style: { display: 'flex', gap: 16, alignItems: 'center', fontSize: 13, fontFamily: 'inherit' } },
                g.totalIn > 0 && React.createElement('span', { style: { color: C.success } }, '+ ' + fmt(g.totalIn)),
                React.createElement('span', { style: { color: C.text, fontWeight: 600 } }, 'Payment ' + fmt(g.totalOut))
              )
            )
          )
        ));
        if (!isCol) {
          g.rows.slice().sort(function(a, b) {
            var da = leadDate(a.name_snapshot), db2 = leadDate(b.name_snapshot);
            if (da !== db2) return da - db2;
            return (a.name_snapshot || '').toLowerCase().localeCompare((b.name_snapshot || '').toLowerCase());
          }).forEach(function(l) {
            var nm = l.name_snapshot;
            if (name !== '(No Lender)' && nm) { var suff = ' (' + name + ')'; if (nm.slice(-suff.length) === suff) nm = nm.slice(0, -suff.length); }
            tbodyChildren.push(renderRow(l, { name: nm, indent: true }));
          });
        }
      });
    } else {
      tbodyChildren = rows.map(function(l) { return renderRow(l); });
    }

    return React.createElement('div', { style: { padding: '4px 0' } },
      React.createElement('table', { style: { width: '100%', borderCollapse: 'collapse', fontSize: 14, tableLayout: 'fixed' } },
        React.createElement('colgroup', null,
          React.createElement('col', null), // Name — flex
          def.balanceMode && React.createElement('col', { style: { width: 150 } }),
          !def.balanceMode && def.allowInflow && React.createElement('col', { style: { width: 130 } }),
          !def.balanceMode && def.allowOutflow && React.createElement('col', { style: { width: 130 } }),
          !def.balanceMode && !def.hidePaymentMethod && React.createElement('col', { style: { width: 130 } }),
          React.createElement('col', null), // Notes — flex
          React.createElement('col', { style: { width: 70 } }) // Complete + delete buttons
        ),
        React.createElement('thead', null,
          React.createElement('tr', { style: { color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 12, fontWeight: 700, fontFamily: 'inherit' } },
            React.createElement('th', { style: { textAlign: 'left', padding: '10px 14px' } }, 'Name'),
            def.balanceMode && React.createElement('th', { style: { textAlign: 'right', padding: '10px 8px' } }, 'Balance'),
            !def.balanceMode && def.allowInflow && React.createElement('th', { style: { textAlign: 'right', padding: '10px 8px' } }, 'Inflow'),
            !def.balanceMode && def.allowOutflow && React.createElement('th', { style: { textAlign: 'right', padding: '10px 8px' } }, 'Outflow'),
            !def.balanceMode && !def.hidePaymentMethod && React.createElement('th', { style: { textAlign: 'left', padding: '10px 8px' } }, 'Method'),
            React.createElement('th', { style: { textAlign: 'left', padding: '10px 8px' } }, 'Notes'),
            React.createElement('th', null)
          )
        ),
        React.createElement('tbody', null, tbodyChildren)
      ),
      adhocAllowed && React.createElement('div', { style: { padding: '8px 14px', borderTop: '1px solid ' + C.border, display: 'flex', gap: 8, alignItems: 'center' } },
        props.onPickVendor && React.createElement(window.Btn, { size: 'sm', variant: 'default', onClick: props.onPickVendor }, '+ Add Vendor from List'),
        !showAdd && React.createElement(window.Btn, { size: 'sm', variant: 'ghost', onClick: function() { setShowAdd(true); } }, '+ Adhoc Row'),
        showAdd && React.createElement('div', { style: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' } },
          React.createElement('input', { placeholder: 'Name', value: adhocName, onChange: function(e) { setAdhocName(e.target.value); }, style: { width: 220, background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '6px 10px', borderRadius: 4, fontSize: 14, fontFamily: 'inherit' } }),
          def.balanceMode && React.createElement('input', { placeholder: 'Balance', type: 'text', inputMode: 'decimal', value: adhocBalance, onChange: function(e) { setAdhocBalance(e.target.value); }, style: { width: 130, background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '6px 10px', borderRadius: 4, fontSize: 14, fontFamily: 'inherit', textAlign: 'right' } }),
          !def.balanceMode && def.allowInflow && React.createElement('input', { placeholder: 'Inflow', type: 'text', inputMode: 'decimal', value: adhocIn, onChange: function(e) { setAdhocIn(e.target.value); }, style: { width: 110, background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '6px 10px', borderRadius: 4, fontSize: 14, fontFamily: 'inherit', textAlign: 'right' } }),
          !def.balanceMode && def.allowOutflow && React.createElement('input', { placeholder: 'Outflow', type: 'text', inputMode: 'decimal', value: adhocOut, onChange: function(e) { setAdhocOut(e.target.value); }, style: { width: 110, background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '6px 10px', borderRadius: 4, fontSize: 14, fontFamily: 'inherit', textAlign: 'right' } }),
          !def.balanceMode && !def.hidePaymentMethod && React.createElement('input', { placeholder: 'Method', value: adhocMethod, onChange: function(e) { setAdhocMethod(e.target.value); }, style: { width: 120, background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '6px 10px', borderRadius: 4, fontSize: 14, fontFamily: 'inherit' } }),
          React.createElement('input', { placeholder: 'Notes', value: adhocNotes, onChange: function(e) { setAdhocNotes(e.target.value); }, style: { flex: '1 1 200px', minWidth: 200, background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '6px 10px', borderRadius: 4, fontSize: 14, fontFamily: 'inherit' } }),
          React.createElement(window.Btn, { size: 'sm', variant: 'primary', disabled: adhocBusy, onClick: submitAdhoc }, adhocBusy ? 'Adding…' : 'Add'),
          React.createElement(window.Btn, { size: 'sm', variant: 'ghost', onClick: function() { setShowAdd(false); } }, 'Cancel')
        )
      )
    );
  }

  // New cycle modal — start date + clone-or-seed mode + initial starting cash.
  function NewCycleModal(props) {
    var [startDate, setStartDate] = React.useState(todayStr());
    var [endDate, setEndDate] = React.useState('');
    var [mode, setMode] = React.useState('clone_or_templates');
    var [startingCash, setStartingCash] = React.useState('');
    var [notes, setNotes] = React.useState('');
    var [busy, setBusy] = React.useState(false);

    React.useEffect(function() {
      if (props.open) {
        setStartDate(todayStr()); setEndDate(''); setMode('clone_or_templates'); setStartingCash(''); setNotes(''); setBusy(false);
      }
    }, [props.open]);

    var submit = async function() {
      setBusy(true);
      try {
        var body = { start_date: startDate, end_date: endDate || null, seed_mode: mode, notes: notes };
        if (startingCash !== '') body.starting_cash = parseFloat(startingCash);
        var r = await window.api('/accounting/cycles', { method: 'POST', body: JSON.stringify(body) });
        props.onCreated(r);
      } catch (e) { props.showToast(e.message, 'error'); setBusy(false); }
    };

    return React.createElement(window.Modal, { open: props.open, onClose: props.onClose, title: 'New Cash Cycle', width: 520 },
      React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 14 } },
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 } },
          React.createElement(window.Inp, { label: 'Start Date', type: 'date', value: startDate, onChange: function(e) { setStartDate(e.target.value); } }),
          React.createElement(window.Inp, { label: 'End Date (opt)', type: 'date', value: endDate, onChange: function(e) { setEndDate(e.target.value); } })
        ),
        React.createElement(window.Inp, { label: 'Starting Cash (opt — defaults to last closed ending cash)', type: 'number', step: '0.01', placeholder: 'auto', value: startingCash, onChange: function(e) { setStartingCash(e.target.value); } }),
        React.createElement('div', null,
          React.createElement('div', { style: { fontSize: 11, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, marginBottom: 6 } }, 'Seed Mode'),
          ['clone_or_templates', 'templates', 'blank'].map(function(m) {
            var labels = { clone_or_templates: 'Clone from latest + templates (recommended)', templates: 'Seed from master templates only', blank: 'Blank — add rows manually' };
            return React.createElement('label', { key: m, style: { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', cursor: 'pointer', fontSize: 13, color: C.text } },
              React.createElement('input', { type: 'radio', checked: mode === m, onChange: function() { setMode(m); } }),
              React.createElement('span', null, labels[m])
            );
          })
        ),
        React.createElement(window.Inp, { label: 'Notes (opt)', value: notes, onChange: function(e) { setNotes(e.target.value); } }),
        React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 } },
          React.createElement(window.Btn, { variant: 'ghost', onClick: props.onClose, disabled: busy }, 'Cancel'),
          React.createElement(window.Btn, { variant: 'primary', onClick: submit, disabled: busy || !startDate }, busy ? 'Creating…' : 'Create Cycle')
        )
      )
    );
  }

  // Bulk-generate future cycles. Useful for forecasting (e.g. stamp out 51 bi-weekly
  // cycles so the Trends chart shows a full year of projected cashflow).
  function GenerateBatchModal(props) {
    var [startDate, setStartDate] = React.useState('');
    var [count, setCount] = React.useState(51);
    var [duration, setDuration] = React.useState(14);
    var [mode, setMode] = React.useState('clone_or_templates');
    var [busy, setBusy] = React.useState(false);
    var [latestEnd, setLatestEnd] = React.useState(null);

    React.useEffect(function() {
      if (!props.open) return;
      setBusy(false); setCount(51); setDuration(14); setMode('clone_or_templates');
      // Default start = day after latest cycle's end_date; fallback = today
      window.api('/accounting/cycles').then(function(rows) {
        var latest = null;
        rows.forEach(function(r) { if (!latest || String(r.end_date || r.start_date) > String(latest.end_date || latest.start_date)) latest = r; });
        if (latest && latest.end_date) {
          // end_date arrives as a full ISO timestamp; appending 'T12:00:00Z' to it made an Invalid
          // Date, toISOString threw, and the catch below defaulted to today every time (sweep LM12).
          var nextDay = new Date(ymdOf(latest.end_date) + 'T12:00:00Z'); nextDay.setUTCDate(nextDay.getUTCDate() + 1);
          setStartDate(nextDay.toISOString().slice(0, 10));
          setLatestEnd(ymdOf(latest.end_date));
        } else {
          setStartDate(todayStr());
          setLatestEnd(null);
        }
      }).catch(function() { setStartDate(todayStr()); });
    }, [props.open]);

    // Overlap guard: start_date must be strictly AFTER the latest cycle's end_date.
    var latestEndStr = latestEnd ? String(latestEnd).slice(0, 10) : null;
    var overlap = !!(latestEndStr && startDate && startDate <= latestEndStr);
    var minStartStr = latestEndStr
      ? new Date(new Date(latestEndStr + 'T12:00:00Z').getTime() + 86400000).toISOString().slice(0, 10)
      : null;

    var submit = async function() {
      if (overlap) {
        props.showToast('Start date must be on or after ' + fmtDate(minStartStr), 'error');
        return;
      }
      setBusy(true);
      try {
        var r = await window.api('/accounting/cycles/generate-batch', { method: 'POST',
          body: JSON.stringify({ start_date: startDate, count: parseInt(count) || 26, duration_days: parseInt(duration) || 14, seed_mode: mode }) });
        props.onCreated(r);
      } catch (e) { props.showToast(e.message, 'error'); setBusy(false); }
    };

    return React.createElement(window.Modal, { open: props.open, onClose: props.onClose, title: 'Generate Cycles', width: 520 },
      React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 14 } },
        latestEnd && !overlap && React.createElement('div', { style: { fontSize: 12, color: C.textMuted, padding: '8px 12px', background: C.bg, border: '1px solid ' + C.border, borderRadius: 6 } },
          'Latest cycle ends ', fmtDate(latestEnd), ' — new cycles must start on or after ', fmtDate(minStartStr), '.'
        ),
        overlap && React.createElement('div', { style: { fontSize: 12, color: C.danger, padding: '10px 14px', background: C.dangerSoft, border: '1px solid ' + C.danger + '55', borderRadius: 6 } },
          React.createElement('strong', null, 'Date conflict: '),
          'start date is on or before the latest cycle\'s end (', fmtDate(latestEndStr), '). Pick ', fmtDate(minStartStr), ' or later to avoid overlap.'
        ),
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 } },
          React.createElement(window.Inp, { label: 'Start Date', type: 'date', value: startDate, onChange: function(e) { setStartDate(e.target.value); } }),
          React.createElement(window.Inp, { label: '# Cycles', type: 'number', value: count, onChange: function(e) { setCount(e.target.value); } }),
          React.createElement(window.Inp, { label: 'Days per Cycle', type: 'number', value: duration, onChange: function(e) { setDuration(e.target.value); } })
        ),
        React.createElement('div', { style: { fontSize: 11, color: C.textDim } },
          'Cap: 60 cycles per batch. Bi-weekly = 14 days. Trends chart shows up to 52 cycles.'
        ),
        React.createElement('div', null,
          React.createElement('div', { style: { fontSize: 11, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, marginBottom: 6 } }, 'Seed Mode'),
          ['clone_or_templates', 'templates', 'blank'].map(function(m) {
            var labels = { clone_or_templates: 'Clone from latest cycle (recommended)', templates: 'Seed from master templates only', blank: 'Blank cycles' };
            return React.createElement('label', { key: m, style: { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', cursor: 'pointer', fontSize: 13, color: C.text } },
              React.createElement('input', { type: 'radio', checked: mode === m, onChange: function() { setMode(m); } }),
              React.createElement('span', null, labels[m])
            );
          })
        ),
        React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 } },
          React.createElement(window.Btn, { variant: 'ghost', onClick: props.onClose, disabled: busy }, 'Cancel'),
          React.createElement(window.Btn, { variant: 'primary', onClick: submit, disabled: busy || !startDate || overlap }, busy ? 'Generating…' : ('Generate ' + (parseInt(count) || 26) + ' cycles'))
        )
      )
    );
  }

  // Cycle-level activity log modal — structural changes only (create/close/line add/remove/sync).
  function CycleActivityModal(props) {
    var [entries, setEntries] = React.useState(null);
    React.useEffect(function() {
      if (!props.open || !props.cycleId) { setEntries(null); return; }
      window.api('/accounting/cycles/' + props.cycleId + '/activity')
        .then(setEntries).catch(function(e) { props.showToast && props.showToast(e.message, 'error'); setEntries([]); });
    }, [props.open, props.cycleId]);
    if (!props.open) return null;

    var actionLabel = function(a) {
      return ({ created: 'Created', closed: 'Closed', reopened: 'Reopened', deleted: 'Deleted',
        line_added: 'Line Added', line_removed: 'Line Removed', line_updated: 'Line Edited', synced: 'Synced from Settings',
        bills_carried_forward: 'Bills Carried Forward', bills_received_from_close: 'Bills Received from Closed Cycle',
        bill_added: 'Bill Added · Bill.com', bill_removed: 'Bill Removed · Bill.com', bill_moved: 'Bill Moved · Bill.com',
        bill_balance_changed: 'Bill Balance Changed · Bill.com' })[a] || a;
    };
    var actionColor = function(a) {
      if (a === 'closed' || a === 'line_removed' || a === 'bill_removed') return C.danger;
      if (a === 'created' || a === 'line_added' || a === 'reopened' || a === 'bills_received_from_close' || a === 'bill_added') return C.success;
      if (a === 'synced' || a === 'bills_carried_forward' || a === 'bill_moved' || a === 'bill_balance_changed' || a === 'line_updated') return C.accent;
      return C.textMuted;
    };
    var detailStr = function(e) {
      var d = e.details || {};
      if (e.action === 'closed') return d.ending_cash != null ? 'ending cash $' + Number(d.ending_cash).toFixed(2) : '';
      if (e.action === 'created') return 'start ' + (d.start_date || '') + (d.seed_mode ? ' · seed: ' + d.seed_mode : '');
      if (e.action === 'line_added') return '[' + d.section + '] ' + (d.name || '') + (d.outflow ? ' · −$' + Number(d.outflow).toFixed(2) : '') + (d.inflow ? ' · +$' + Number(d.inflow).toFixed(2) : '') + (d.adhoc ? ' · adhoc' : '');
      if (e.action === 'line_removed') return '[' + d.section + '] ' + (d.name || '') + (d.master_linked ? ' · tombstoned' : '');
      if (e.action === 'synced') return (d.rows_added || 0) + ' rows added' + (d.notes_backfilled > 0 ? ', ' + d.notes_backfilled + ' notes filled' : '');
      if (e.action === 'bills_carried_forward') return d.count + ' unpaid bill' + (d.count === 1 ? '' : 's') + ' → cycle #' + d.target_cycle_id;
      if (e.action === 'bills_received_from_close') return d.count + ' unpaid bill' + (d.count === 1 ? '' : 's') + ' from closed cycle #' + d.source_cycle_id;
      if (e.action === 'bill_added') return (d.vendor || '(unknown)') + (d.invoice ? ' · ' + d.invoice : '') + (d.amount != null ? ' · $' + Number(d.amount).toFixed(2) : '');
      if (e.action === 'bill_removed') return (d.vendor || '(unknown)') + (d.invoice ? ' · ' + d.invoice : '') + (d.reason ? ' · ' + d.reason : '');
      if (e.action === 'bill_moved') return (d.vendor || '(unknown)') + (d.invoice ? ' · ' + d.invoice : '') + (d.direction === 'out' ? ' → cycle #' + d.to_cycle_id : ' ← cycle #' + d.from_cycle_id);
      if (e.action === 'bill_balance_changed') return (d.vendor || '(unknown)') + (d.invoice ? ' · ' + d.invoice : '') + ' · owed $' + Number(d.from).toFixed(2) + ' → $' + Number(d.to).toFixed(2) + (d.paid ? ' (paid)' : (Number(d.from) === 0 ? ' (reopened)' : ''));
      if (e.action === 'line_updated') {
        var ch = d.changes || {}; var parts = [];
        if (ch.outflow) parts.push('outflow $' + Number(ch.outflow.from).toFixed(2) + ' → $' + Number(ch.outflow.to).toFixed(2));
        if (ch.inflow) parts.push('inflow $' + Number(ch.inflow.from).toFixed(2) + ' → $' + Number(ch.inflow.to).toFixed(2));
        if (ch.balance) parts.push('balance ' + (ch.balance.from == null ? '—' : '$' + Number(ch.balance.from).toFixed(2)) + ' → ' + (ch.balance.to == null ? '—' : '$' + Number(ch.balance.to).toFixed(2)));
        if (ch.name) parts.push('renamed');
        if (ch.payment_method !== undefined) parts.push('method');
        if (ch.notes) parts.push('notes');
        return '[' + (d.section || '') + '] ' + (d.name || '') + (d.bill ? ' · Bill.com' : '') + (parts.length ? ' · ' + parts.join(', ') : '');
      }
      return '';
    };

    var downloadCsv = function() {
      if (!entries || !entries.length) return;
      var rows = [['Date', 'User', 'Action', 'Details']];
      entries.forEach(function(e) { rows.push([new Date(e.created_at).toISOString(), e.username || '', actionLabel(e.action), detailStr(e)]); });
      var csv = rows.map(function(r) { return r.map(escapeCSV).join(','); }).join('\r\n'); // shared cell rule, formula-safe (review 2026-09-28, medium #31)
      var blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
      var url = URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url; a.download = 'cycle-activity_' + (props.cycleStart || props.cycleId) + '.csv';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    };

    return React.createElement(window.Modal, { open: true, onClose: props.onClose, title: 'Cycle Activity Log', width: 760, maxHeight: 'calc(85vh * var(--invzoom, 1))' },
      React.createElement('div', null,
        entries && entries.length > 0 && React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 } },
          React.createElement('div', { style: { fontSize: 12, color: C.textMuted } }, entries.length + ' entr' + (entries.length === 1 ? 'y' : 'ies')),
          React.createElement(window.Btn, { size: 'sm', variant: 'default', onClick: downloadCsv }, React.createElement(window.LIcon, { name: 'download', size: 14 }), ' Download CSV')
        ),
        !entries && React.createElement('div', { style: { color: C.textMuted, padding: 20 } }, 'Loading…'),
        entries && entries.length === 0 && React.createElement(window.Empty, { title: 'No activity yet', sub: 'Changes to this cycle (line edits, Bill.com bill add/remove/move/balance, close, sync, etc.) will appear here.' }),
        entries && entries.length > 0 && React.createElement('div', { style: { background: C.bg, border: '1px solid ' + C.border, borderRadius: 8, maxHeight: 'calc(65vh * var(--invzoom, 1))', overflowY: 'auto' } },
          entries.map(function(e) {
            return React.createElement('div', { key: e.id, style: { display: 'flex', justifyContent: 'space-between', gap: 12, padding: '10px 14px', borderTop: '1px solid ' + C.border } },
              React.createElement('div', { style: { flex: 1, minWidth: 0 } },
                React.createElement('div', { style: { fontSize: 13, color: C.text } },
                  React.createElement('span', { style: { fontWeight: 600, color: actionColor(e.action) } }, actionLabel(e.action))
                ),
                React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 4, fontFamily: 'inherit' } }, detailStr(e))
              ),
              React.createElement('div', { style: { fontSize: 11, color: C.textDim, whiteSpace: 'nowrap', textAlign: 'right' } },
                new Date(e.created_at).toLocaleString(),
                e.username && React.createElement('div', { style: { marginTop: 2 } }, 'by ' + e.username)
              )
            );
          })
        )
      )
    );
  }

  function CloseCycleModal(props) {
    var s = props.summary || {};
    return React.createElement(window.Modal, { open: props.open, onClose: props.onClose, title: 'Close this cycle?', width: 460 },
      React.createElement('div', { style: { color: C.text, fontSize: 13, lineHeight: 1.5 } },
        'Closing freezes this cycle and snapshots its summary. ',
        React.createElement('strong', null, 'The next cycle (cloned from this one) will be auto-created '),
        'unless one already exists for that date. You can reopen later if needed.',
        React.createElement('div', { style: { background: C.bg, border: '1px solid ' + C.border, borderRadius: 8, padding: 12, marginTop: 14, fontFamily: 'inherit', fontSize: 12 } },
          React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between' } }, React.createElement('span', null, 'Ending Cash'), React.createElement('span', null, fmt(s.ending_cash))),
          React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between' } }, React.createElement('span', null, 'Net Cashflow'), React.createElement('span', { style: { color: Number(s.net_cashflow || 0) >= 0 ? C.success : C.danger } }, fmt(s.net_cashflow)))
        ),
        React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 } },
          React.createElement(window.Btn, { variant: 'ghost', onClick: props.onClose }, 'Cancel'),
          React.createElement(window.Btn, { variant: 'danger', disabled: props.confirmBusy, onClick: props.onConfirm }, props.confirmBusy ? 'Closing…' : 'Close Cycle')
        )
      )
    );
  }

  function VendorPickerModal(props) {
    var [vendors, setVendors] = React.useState(null);
    var [search, setSearch] = React.useState('');
    var existingIds = props.existingIds || [];
    React.useEffect(function() {
      if (!props.open) return;
      window.api('/accounting/vendors').then(setVendors).catch(function(e) { props.showToast(e.message, 'error'); });
    }, [props.open]);
    var toggle = async function(v) {
      var willEnable = !v.accounting_active;
      try {
        var r = await window.api('/accounting/vendors/' + v.id + '/accounting', { method: 'PUT', body: JSON.stringify({ accounting_active: willEnable }) });
        setVendors(function(prev) { return (prev || []).map(function(x) { return x.id === v.id ? Object.assign({}, x, r) : x; }); });
        props.showToast('Saved');
      } catch (e) { props.showToast(e.message, 'error'); }
    };
    var q = search.trim().toLowerCase();
    var filtered = (vendors || []).filter(function(v) { return !q || (v.vendor_name || '').toLowerCase().indexOf(q) >= 0; });
    return React.createElement(window.Modal, { open: props.open, onClose: props.onClose, title: 'Add Vendor to Cycle', width: 640, maxHeight: 'calc(80vh * var(--invzoom, 1))' },
      React.createElement('div', null,
        React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 10 } }, 'Click a vendor to add it as a cycle line. Toggle the chip on the right to keep this vendor in your AP list for every new cycle.'),
        React.createElement('input', { autoFocus: true, value: search, onChange: function(e) { setSearch(e.target.value); }, placeholder: 'Search vendors…',
          style: { width: '100%', boxSizing: 'border-box', background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 13, marginBottom: 12 } }),
        !vendors && React.createElement('div', { style: { color: C.textMuted } }, 'Loading…'),
        vendors && filtered.length === 0 && React.createElement('div', { style: { color: C.textMuted, fontSize: 13, padding: 12 } }, 'No vendors match.'),
        vendors && React.createElement('div', { style: { maxHeight: 'calc(52vh * var(--invzoom, 1))', overflowY: 'auto', border: '1px solid ' + C.border, borderRadius: 8 } },
          filtered.map(function(v) {
            var inCycle = existingIds.indexOf(v.id) >= 0;
            return React.createElement('div', { key: v.id, style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderTop: '1px solid ' + C.border, gap: 12 } },
              React.createElement('div', { style: { flex: 1, minWidth: 0 } },
                React.createElement('div', { style: { fontSize: 13, color: C.text, fontWeight: 500 } }, v.vendor_name),
                React.createElement('div', { style: { fontSize: 11, color: C.textMuted } }, v.payment_method || 'no payment method set')
              ),
              React.createElement('button', {
                onClick: function() { toggle(v); },
                title: v.accounting_active ? 'Pinned to AP list' : 'Add to AP list',
                style: { background: v.accounting_active ? C.accentSoft : 'transparent', color: v.accounting_active ? C.accent : C.textDim, border: '1px solid ' + (v.accounting_active ? C.accent + '55' : C.border), borderRadius: 4, padding: '4px 8px', fontSize: 11, cursor: 'pointer' }
              }, v.accounting_active ? '★ pinned' : '☆ pin'),
              inCycle
                ? React.createElement('span', { style: { fontSize: 11, color: C.textMuted } }, 'already in cycle')
                : React.createElement(window.Btn, { size: 'sm', variant: 'primary', onClick: function() { props.onPick(v); } }, 'Add')
            );
          })
        )
      )
    );
  }

  // ---------- AccountingHistoryPage ----------
  function AccountingHistoryPage(props) {
    var [cycles, setCycles] = React.useState(null);
    var [refreshing, setRefreshing] = React.useState(false);
    var [generateOpen, setGenerateOpen] = React.useState(false);
    var loadCycles = function() {
      window.api('/accounting/cycles').then(function(rows) {
        var sortedAsc = rows.slice().sort(function(a, b) { return String(a.start_date).localeCompare(String(b.start_date)); });
        var deltaMap = {};
        for (var i = 0; i < sortedAsc.length; i++) {
          var cur = sortedAsc[i];
          if (i === 0 || cur.ending_cash == null || sortedAsc[i-1].ending_cash == null) { deltaMap[cur.id] = null; continue; }
          deltaMap[cur.id] = Number(cur.ending_cash) - Number(sortedAsc[i-1].ending_cash);
        }
        setCycles(rows.map(function(r) { return Object.assign({}, r, { prev_change: deltaMap[r.id] }); }));
      }).catch(function(e) { props.showToast(e.message, 'error'); });
    };
    var doRefresh = async function() {
      setRefreshing(true);
      try {
        var r = await window.api('/accounting/cycles/refresh-chain', { method: 'POST' });
        var parts = ['re-chained ' + r.cycles_refreshed + ' cycles'];
        if (r.rows_added > 0) parts.push('added ' + r.rows_added + ' missing row' + (r.rows_added === 1 ? '' : 's'));
        if (r.expired_loans_pruned > 0) parts.push('removed ' + r.expired_loans_pruned + ' expired loan row' + (r.expired_loans_pruned === 1 ? '' : 's'));
        if (r.cash_balances_cleared > 0) parts.push('cleared ' + r.cash_balances_cleared + ' stale cash balances');
        props.showToast('Refresh: ' + parts.join(', '), 'success');
        loadCycles();
      } catch (e) { props.showToast(e.message, 'error'); }
      finally { setRefreshing(false); }
    };
    React.useEffect(function() {
      window.api('/accounting/cycles').then(function(rows) {
        // /cycles returns DESC by start_date. Compute "prev_change" = this.ending_cash − prior(older).ending_cash.
        var sortedAsc = rows.slice().sort(function(a, b) { return String(a.start_date).localeCompare(String(b.start_date)); });
        var deltaMap = {};
        for (var i = 0; i < sortedAsc.length; i++) {
          var cur = sortedAsc[i];
          if (i === 0 || cur.ending_cash == null || sortedAsc[i-1].ending_cash == null) { deltaMap[cur.id] = null; continue; }
          deltaMap[cur.id] = Number(cur.ending_cash) - Number(sortedAsc[i-1].ending_cash);
        }
        var enriched = rows.map(function(r) { return Object.assign({}, r, { prev_change: deltaMap[r.id] }); });
        setCycles(enriched);
      }).catch(function(e) { props.showToast(e.message, 'error'); });
    }, []);
    if (!cycles) return React.createElement('div', { style: { padding: 40, color: C.textMuted } }, 'Loading…');

    var cols = [
      { key: 'start_date', label: 'Start', render: function(r) { return fmtDate(r.start_date); }, sortValue: function(r) { return r.start_date; } },
      { key: 'end_date', label: 'End', render: function(r) { return r.end_date ? fmtDate(r.end_date) : '—'; } },
      { key: 'status', label: 'Status', sortValue: function(r) { return statusLabel(r); }, render: function(r) {
        var lbl = statusLabel(r);
        var sc = statusColors(lbl);
        return React.createElement('span', { style: { fontSize: 10, padding: '2px 8px', borderRadius: 4, background: sc.bg, color: sc.color, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 } }, lbl);
      }},
      { key: 'starting_cash', label: 'Start Cash', sortValue: function(r) { return Number(r.starting_cash || 0); }, render: function(r) { return React.createElement('span', { style: { fontFamily: 'inherit' } }, fmt(r.starting_cash)); }},
      { key: 'ending_cash', label: 'End Cash', sortValue: function(r) { return Number(r.ending_cash || 0); }, render: function(r) { return React.createElement('span', { style: { fontFamily: 'inherit' } }, fmt(r.ending_cash)); }},
      { key: 'prev_change', label: 'Prev +/-', sortValue: function(r) { return r.prev_change == null ? -Infinity : Number(r.prev_change); }, render: function(r) {
        if (r.prev_change == null) return React.createElement('span', { style: { color: C.textDim } }, '—');
        var v = r.prev_change;
        return React.createElement('span', { style: { fontFamily: 'inherit', color: v >= 0 ? C.success : C.danger, fontWeight: 600 } }, (v >= 0 ? '+' : '') + fmt(v));
      }},
      { key: 'net_cashflow', label: 'Net', sortValue: function(r) { return Number(r.net_cashflow || 0); }, render: function(r) {
        var v = Number(r.net_cashflow || 0);
        return React.createElement('span', { style: { fontFamily: 'inherit', color: v >= 0 ? C.success : C.danger, fontWeight: 600 } }, (v >= 0 ? '+' : '') + fmt(v));
      }},
      { key: 'ar_total', label: 'AR', render: function(r) { return React.createElement('span', { style: { fontFamily: 'inherit', color: C.success } }, fmt(r.ar_total)); }},
      { key: 'ap_total', label: 'AP', render: function(r) { return React.createElement('span', { style: { fontFamily: 'inherit', color: C.danger } }, fmt(r.ap_total)); }},
      { key: 'payroll_total', label: 'Payroll', render: function(r) { return React.createElement('span', { style: { fontFamily: 'inherit', color: C.danger } }, fmt(r.payroll_total)); }},
      { key: 'loan_payments_total', label: 'Loans', render: function(r) { return React.createElement('span', { style: { fontFamily: 'inherit', color: C.danger } }, fmt(r.loan_payments_total)); }},
      { key: 'notes', label: 'Notes', defaultVisible: false, render: function(r) { return r.notes || ''; } },
    ];

    return React.createElement('div', { style: { padding: '20px 28px 60px', maxWidth: 1700, margin: '0 auto' } },
      React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 } },
        React.createElement('h2', { style: { margin: 0, fontSize: 18, fontWeight: 600, color: C.text } }, 'All Cycles'),
        React.createElement('div', { style: { display: 'flex', gap: 8 } },
          React.createElement(window.Btn, { variant: 'default', size: 'sm', onClick: function() { props.onNavigate('settings-accounting'); } }, 'Settings'),
          React.createElement('span', { title: 'Re-chain starting cash through all draft cycles (each cycle starts where the previous one ended) and recompute summaries' },
            React.createElement(window.Btn, { variant: 'default', size: 'sm', onClick: doRefresh, disabled: refreshing }, refreshing ? 'Refreshing…' : 'Refresh')
          ),
          React.createElement('span', { title: 'Bulk-create future cycles (e.g. 51 bi-weekly cycles for a full year of forecast). For a single new cycle, set count=1.' },
            React.createElement(window.Btn, { variant: 'default', size: 'sm', onClick: function() { setGenerateOpen(true); } }, 'Generate Cycles')
          ),
          React.createElement('span', { title: 'Download all cycles + summary stats as an Excel workbook (review only)' },
            React.createElement(window.Btn, { variant: 'default', size: 'sm', onClick: function() { downloadXlsx('/accounting/cycles/export', 'all_cycles.xlsx', props.showToast); } },
              React.createElement(window.LIcon, { name: 'download', size: 14 }), ' Export Excel')
          ),
          React.createElement(window.Btn, { variant: 'default', size: 'sm', onClick: function() { props.onNavigate('accounting-trends'); } }, 'Trends'),
          React.createElement(window.Btn, { variant: 'primary', size: 'sm', onClick: function() { props.onNavigate('accounting'); } }, 'Current Cycle')
        )
      ),
      React.createElement(GenerateBatchModal, { open: generateOpen, onClose: function() { setGenerateOpen(false); },
        onCreated: function(r) { setGenerateOpen(false); props.showToast('Generated ' + r.created + ' cycle' + (r.created === 1 ? '' : 's'), 'success'); loadCycles(); },
        showToast: props.showToast }),
      React.createElement(window.DataTable, {
        columns: cols, data: cycles,
        onRowClick: function(r) { props.onNavigate('accounting', r.id); },
        rowHref: function(r) { return '/accounting/' + r.id; }, // enables Ctrl/Cmd/middle-click → new tab
        emptyTitle: 'No cycles yet', emptySub: 'Create one from Current Cycle',
        defaultSort: 'start_date', defaultSortDir: 'asc', storageKey: 'accounting-history'
      })
    );
  }

  // ---------- AccountingTrendsPage ----------
  // Chart wrapper — creates/destroys Chart.js instance on config change.
  function ChartCanvas(props) {
    var ref = React.useRef(null);
    var chartRef = React.useRef(null);
    var configKey = JSON.stringify(props.config);
    React.useEffect(function() {
      if (!ref.current || !window.Chart) return;
      if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }
      chartRef.current = new window.Chart(ref.current.getContext('2d'), props.config);
      return function() { if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; } };
    }, [configKey]);
    return React.createElement('div', { style: { position: 'relative', height: props.height || 280 } },
      React.createElement('canvas', { ref: ref })
    );
  }

  function AccountingTrendsPage(props) {
    var [data, setData] = React.useState(null);
    var [statusFilter, setStatusFilter] = React.useState('all'); // 'all' | 'closed'
    var [windowN, setWindowN] = React.useState(999); // default: all cycles (handles forecast use case)
    var [customRange, setCustomRange] = React.useState({ from: '', to: '' });
    var [customOpen, setCustomOpen] = React.useState(false);

    React.useEffect(function() {
      var qs = statusFilter === 'closed' ? '?status=closed' : '';
      window.api('/accounting/trends' + qs).then(setData).catch(function(e) { props.showToast(e.message, 'error'); });
    }, [statusFilter]);

    if (!data) return React.createElement('div', { style: { padding: 40, color: C.textMuted } }, 'Loading…');
    if (!window.Chart) {
      return React.createElement('div', { style: { padding: 40, color: C.textMuted } }, 'Chart.js failed to load. Refresh the page.');
    }
    if (!data.length) {
      return React.createElement('div', { style: { padding: 40 } },
        React.createElement(window.Empty, { title: 'No data yet', sub: 'Close at least one cycle to see trends.' })
      );
    }

    // Find the CURRENT cycle index. Prefer the server's is_current stamp
    // (= the active cycle resolver — see /cycles/active). Fall back to the
    // legacy today-in-range scan that skips closed cycles for older payloads.
    var todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' }); // New York day
    var currentIdx = -1;
    for (var ci0 = 0; ci0 < data.length; ci0++) { if (data[ci0].is_current) { currentIdx = ci0; break; } }
    if (currentIdx === -1) {
      for (var ci = 0; ci < data.length; ci++) {
        var cd = data[ci];
        if (cd.status === 'closed' || cd.status === 'cancelled') continue;
        var cs = String(cd.start_date).slice(0, 10);
        var ce = cd.end_date ? String(cd.end_date).slice(0, 10) : '9999-12-31';
        if (cs <= todayStr && ce >= todayStr) { currentIdx = ci; break; }
      }
    }
    if (currentIdx === -1) {
      for (var cj = 0; cj < data.length; cj++) {
        if (data[cj].status !== 'closed' && data[cj].status !== 'cancelled') { currentIdx = cj; break; }
      }
    }
    if (currentIdx === -1) currentIdx = 0;

    var trimmed;
    if (customRange.from || customRange.to) {
      // Custom date range mode — filter by start_date inclusive.
      trimmed = data.filter(function(d) {
        var s = String(d.start_date).slice(0, 10);
        if (customRange.from && s < customRange.from) return false;
        if (customRange.to && s > customRange.to) return false;
        return true;
      });
    } else if (windowN >= data.length) {
      trimmed = data;
    } else {
      // Take N cycles starting from current (forecast-friendly: shows current + next N-1)
      trimmed = data.slice(currentIdx, currentIdx + windowN);
    }
    var labels = trimmed.map(function(r) { return fmtDate(r.start_date); });
    var endingCash = trimmed.map(function(r) { return r.ending_cash != null ? Number(r.ending_cash) : null; });
    var startingCash = trimmed.map(function(r) { return r.starting_cash != null ? Number(r.starting_cash) : null; });
    var net = trimmed.map(function(r) { return r.net_cashflow != null ? Number(r.net_cashflow) : 0; });
    var ar = trimmed.map(function(r) { return Number(r.ar_total || 0); });
    var ap = trimmed.map(function(r) { return Number(r.ap_total || 0); });
    var payroll = trimmed.map(function(r) { return Number(r.payroll_total || 0); });
    var loans = trimmed.map(function(r) { return Number(r.loan_payments_total || 0); });
    var cc = trimmed.map(function(r) { return Number(r.cc_outflow || 0); });
    var wagesLoan = trimmed.map(function(r) { return Number(r.wages_loan_outflow || 0); });
    var expense = trimmed.map(function(r) { return Number(r.expense_outflow || 0); });
    var adhoc = trimmed.map(function(r) { return Number(r.adhoc_outflow || 0); });

    // "Current cycle" = cycle whose date range contains today. Falls back to most-recent closed,
    // then most-recent of any status. This is more useful than the chronologically-last cycle
    // when the user has bulk-generated future cycles for forecasting.
    var todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' }); // New York day
    // The KPIs ("Current Cycle", "Change from Prior", etc.) should track the
    // actual active cycle and its immediate predecessor — independent of the
    // chart window. Pull them from the full data set so swapping the 6/13/26
    // filter doesn't shuffle the KPI row.
    var latest = null;
    for (var li0 = 0; li0 < data.length; li0++) { if (data[li0].is_current) { latest = data[li0]; break; } }
    if (!latest) {
      for (var li = 0; li < data.length; li++) {
        var c = data[li];
        if (c.status === 'closed' || c.status === 'cancelled') continue;
        if (c.start_date <= todayStr && (!c.end_date || c.end_date >= todayStr)) { latest = c; break; }
      }
    }
    if (!latest) {
      for (var lj = 0; lj < data.length; lj++) {
        if (data[lj].status !== 'closed' && data[lj].status !== 'cancelled') { latest = data[lj]; break; }
      }
    }
    if (!latest) latest = data[data.length - 1];
    var latestIdx = data.indexOf(latest);
    var prior = latestIdx > 0 ? data[latestIdx - 1] : null;
    var delta = latest && prior && latest.ending_cash != null && prior.ending_cash != null
      ? Number(latest.ending_cash) - Number(prior.ending_cash) : null;

    // Common chart axis options
    var axisColor = C.textMuted;
    var gridColor = C.border;
    var baseScales = {
      x: { ticks: { color: axisColor, font: { size: 10 } }, grid: { color: gridColor, drawBorder: false } },
      y: { ticks: { color: axisColor, font: { size: 10 }, callback: function(v) { return '$' + Number(v).toLocaleString(); } }, grid: { color: gridColor, drawBorder: false } }
    };
    var legendOpts = { labels: { color: C.text, font: { size: 11 }, usePointStyle: true, padding: 14 } };
    var tooltipOpts = {
      backgroundColor: C.surface, borderColor: C.border, borderWidth: 1, titleColor: C.text, bodyColor: C.text, padding: 10,
      callbacks: { label: function(ctx) { return ctx.dataset.label + ': ' + fmt(ctx.parsed.y); } }
    };

    // Chart 1: Cash trend (Starting + Ending line)
    var cashTrend = {
      type: 'line',
      data: {
        labels: labels,
        datasets: [
          { label: 'Ending Cash', data: endingCash, borderColor: C.accent, backgroundColor: C.accent + '22', tension: 0.25, pointRadius: 3, fill: true }
        ]
      },
      options: { responsive: true, maintainAspectRatio: false, plugins: { legend: legendOpts, tooltip: tooltipOpts }, scales: baseScales, interaction: { mode: 'index', intersect: false } }
    };

    // Chart 2: Net cashflow per cycle (bar, colored by sign)
    var netChart = {
      type: 'bar',
      data: {
        labels: labels,
        datasets: [{
          label: 'Net Cashflow',
          data: net,
          backgroundColor: net.map(function(v) { return v >= 0 ? C.success + 'CC' : C.danger + 'CC'; }),
          borderColor: net.map(function(v) { return v >= 0 ? C.success : C.danger; }),
          borderWidth: 1
        }]
      },
      options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: tooltipOpts }, scales: baseScales }
    };

    // Chart 3: Payments by bucket (stacked bar) — matches the per-section breakdown on the cycle summary card
    var paymentsChart = {
      type: 'bar',
      data: {
        labels: labels,
        datasets: [
          { label: 'AP Vendors', data: ap, backgroundColor: '#F87171CC' },
          { label: 'Loans', data: loans, backgroundColor: '#A78BFACC' },
          { label: 'Credit Cards', data: cc, backgroundColor: '#34D399CC' },
          { label: 'Shareholder Loans', data: wagesLoan, backgroundColor: '#60A5FACC' },
          { label: 'Payroll', data: payroll, backgroundColor: '#FBBF24CC' },
          { label: 'Recurring Expenses', data: expense, backgroundColor: '#F472B6CC' },
          { label: 'Adhoc', data: adhoc, backgroundColor: '#C084FCCC' },
        ]
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: legendOpts, tooltip: Object.assign({}, tooltipOpts, { mode: 'index', intersect: false }) },
        scales: {
          x: Object.assign({}, baseScales.x, { stacked: true }),
          y: Object.assign({}, baseScales.y, { stacked: true })
        },
        interaction: { mode: 'index', intersect: false }
      }
    };

    // Chart 4: Inflow vs Outflow (grouped bar) — outflow = AP + payroll + loans + brex + amex + shipping
    var totalOutflow = trimmed.map(function(r, i) { return ap[i] + payroll[i] + loans[i] + cc[i] + wagesLoan[i] + expense[i] + adhoc[i]; });
    var inflowOutflow = {
      type: 'bar',
      data: {
        labels: labels,
        datasets: [
          { label: 'Inflows (AR)', data: ar, backgroundColor: C.success + 'CC', borderColor: C.success, borderWidth: 1 },
          { label: 'Outflows', data: totalOutflow, backgroundColor: C.danger + 'CC', borderColor: C.danger, borderWidth: 1 }
        ]
      },
      options: { responsive: true, maintainAspectRatio: false, plugins: { legend: legendOpts, tooltip: tooltipOpts }, scales: baseScales, interaction: { mode: 'index', intersect: false } }
    };

    // KPI tile component
    var kpi = function(label, value, sub, color) {
      return React.createElement('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 10, padding: '14px 18px', flex: '1 1 200px', minWidth: 180 } },
        React.createElement('div', { style: { fontSize: 10, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 } }, label),
        React.createElement('div', { style: { fontSize: 20, fontFamily: 'inherit', fontWeight: 600, color: color || C.text, marginTop: 6 } }, value),
        sub && React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 4 } }, sub)
      );
    };

    var card = function(title, child) {
      return React.createElement('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 10, padding: 18, marginTop: 18 } },
        React.createElement('div', { style: { fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 12 } }, title),
        child
      );
    };

    return React.createElement('div', { style: { padding: '20px 28px 60px', maxWidth: 1700, margin: '0 auto' } },
      // Header
      React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, gap: 12, flexWrap: 'wrap' } },
        React.createElement('h2', { style: { margin: 0, fontSize: 18, fontWeight: 600, color: C.text } }, 'Cash Cycle Trends'),
        React.createElement('div', { style: { display: 'flex', gap: 8, alignItems: 'center' } },
          React.createElement('label', { style: { fontSize: 11, color: C.textMuted } }, 'Window:'),
          [6, 13, 26, 52, 999].map(function(n) {
            var label = n === 999 ? 'All' : (n + ' cycles');
            var active = windowN === n && !customRange.from && !customRange.to;
            return React.createElement('button', { key: n, onClick: function() { setCustomRange({ from: '', to: '' }); setWindowN(n); },
              style: { padding: '4px 10px', fontSize: 11, borderRadius: 4, cursor: 'pointer', background: active ? C.accentSoft : C.surface, color: active ? C.accent : C.textMuted, border: '1px solid ' + (active ? C.accent + '55' : C.border) } }, label);
          }),
          // Custom date range — a button that opens a date chooser; overrides window N when set
          (function() {
            var hasRange = !!(customRange.from || customRange.to);
            var btnLabel = hasRange
              ? (customRange.from ? fmtDate(customRange.from) : '…') + ' → ' + (customRange.to ? fmtDate(customRange.to) : '…')
              : 'Custom';
            var dateInp = function(key, label) {
              return React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4 } },
                React.createElement('span', { style: { fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' } }, label),
                React.createElement('input', { type: 'date', value: customRange[key],
                  onChange: function(e) { var next = { from: customRange.from, to: customRange.to }; next[key] = e.target.value; setCustomRange(next); },
                  style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '6px 9px', borderRadius: 6, fontSize: 12, colorScheme: 'dark', fontFamily: 'inherit' } }));
            };
            return React.createElement('div', { style: { position: 'relative', marginLeft: 6 } },
              React.createElement('button', { onClick: function() { setCustomOpen(!customOpen); },
                style: { padding: '4px 10px', fontSize: 11, borderRadius: 4, cursor: 'pointer', background: (hasRange || customOpen) ? C.accentSoft : C.surface, color: (hasRange || customOpen) ? C.accent : C.textMuted, border: '1px solid ' + ((hasRange || customOpen) ? C.accent + '55' : C.border) } }, btnLabel),
              customOpen && React.createElement(React.Fragment, null,
                React.createElement('div', { onClick: function() { setCustomOpen(false); }, style: { position: 'fixed', inset: 0, zIndex: 49 } }),
                React.createElement('div', { style: { position: 'absolute', top: '110%', right: 0, zIndex: 50, background: C.surface, border: '1px solid ' + C.border, borderRadius: 10, padding: 14, minWidth: 230, boxShadow: '0 8px 24px rgba(0,0,0,0.35)', display: 'flex', flexDirection: 'column', gap: 10 } },
                  React.createElement('div', { style: { fontSize: 11, fontWeight: 700, color: C.text } }, 'Custom date range'),
                  React.createElement('div', { style: { fontSize: 10, color: C.textDim } }, 'Cycles whose start date falls in this range'),
                  dateInp('from', 'From'),
                  dateInp('to', 'To'),
                  React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 2 } },
                    React.createElement(window.Btn, { size: 'sm', onClick: function() { setCustomRange({ from: '', to: '' }); setCustomOpen(false); } }, 'Clear'),
                    React.createElement(window.Btn, { size: 'sm', variant: 'primary', onClick: function() { setCustomOpen(false); } }, 'Apply')))));
          })(),
          React.createElement('label', { style: { fontSize: 11, color: C.textMuted, marginLeft: 10 } }, 'Status:'),
          [['all', 'All'], ['closed', 'Closed only']].map(function(pair) {
            return React.createElement('button', { key: pair[0], onClick: function() { setStatusFilter(pair[0]); },
              style: { padding: '4px 10px', fontSize: 11, borderRadius: 4, cursor: 'pointer', background: statusFilter === pair[0] ? C.accentSoft : C.surface, color: statusFilter === pair[0] ? C.accent : C.textMuted, border: '1px solid ' + (statusFilter === pair[0] ? C.accent + '55' : C.border) } }, pair[1]);
          }),
          React.createElement(window.Btn, { variant: 'primary', size: 'sm', onClick: function() { props.onNavigate('accounting-history'); } }, 'All Cycles'),
          React.createElement(window.Btn, { variant: 'primary', size: 'sm', onClick: function() { props.onNavigate('accounting'); } }, 'Current Cycle')
        )
      ),
      // KPIs
      React.createElement('div', { style: { display: 'flex', gap: 12, flexWrap: 'wrap' } },
        kpi('Current Cycle', latest ? fmtDate(latest.start_date) : '—',
          latest ? (latest.status === 'closed' ? 'closed' : (latest.is_current ? 'current' : 'draft')) : null),
        kpi('Ending Cash', fmt(latest && latest.ending_cash), null, Number(latest && latest.ending_cash || 0) >= 0 ? C.text : C.danger),
        kpi('Change from Prior', delta == null ? '—' : (delta >= 0 ? '+' : '') + fmt(delta),
          prior ? 'vs ' + fmtDate(prior.start_date) : null,
          delta == null ? C.textMuted : (delta >= 0 ? C.success : C.danger)),
        kpi('Net Cashflow', (latest && Number(latest.net_cashflow || 0) >= 0 ? '+' : '') + fmt(latest && latest.net_cashflow),
          null, latest && Number(latest.net_cashflow || 0) >= 0 ? C.success : C.danger),
        kpi('Cycles Tracked', String(data.length), data[0] ? 'since ' + fmtDate(data[0].start_date) : null)
      ),
      // Charts
      card('Cash Trend', React.createElement(ChartCanvas, { config: cashTrend, height: 280 })),
      card('Net Cashflow per Cycle', React.createElement(ChartCanvas, { config: netChart, height: 240 })),
      card('Payments by Bucket (stacked)', React.createElement(ChartCanvas, { config: paymentsChart, height: 300 })),
      card('Inflows vs Outflows', React.createElement(ChartCanvas, { config: inflowOutflow, height: 260 }))
    );
  }

  // ---------- AccountingSettingsPage ----------
  var TABS = [
    { key: 'accounts',          label: 'Cash Accounts',      kind: 'accounts'          },
    { key: 'ar-channels',       label: 'AR Channels',        kind: 'ar-channels'       },
    { key: 'loans',             label: 'Loans',              kind: 'loans'             },
    { key: 'shareholder-loans', label: 'Shareholder Loans',  kind: 'shareholder-loans' },
    { key: 'credit-cards',      label: 'Credit Cards',       kind: 'credit-cards'      },
    { key: 'payroll',           label: 'Payroll',            kind: 'payroll'           },
    { key: 'expenses',          label: 'Recurring Expenses', kind: 'expenses'          },
    { key: 'vendors',           label: 'AP Vendor Toggle',   kind: 'vendors'           },
  ];

  function AccountingSettingsPage(props) {
    var [tab, setTab] = React.useState('accounts');
    React.useEffect(function() { try { var qt = new URLSearchParams(window.location.search).get('tab'); if (qt) setTab(qt); } catch (e) {} }, []);
    return React.createElement('div', { style: { padding: '20px 28px 60px', maxWidth: 1700, margin: '0 auto' } },
      React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 } },
        React.createElement('h2', { style: { margin: 0, fontSize: 18, fontWeight: 600, color: C.text } }, 'Accounting Settings'),
        React.createElement(window.Btn, { variant: 'default', size: 'sm', onClick: function() { props.onNavigate('accounting'); } }, 'Back to Cycle')
      ),
      React.createElement('div', { style: { display: 'flex', gap: 4, borderBottom: '1px solid ' + C.border, marginBottom: 16 } },
        TABS.map(function(t) {
          var isActive = tab === t.key;
          return React.createElement('a', {
            key: t.key, href: '/settings/accounting' + (t.key !== 'accounts' ? '?tab=' + t.key : ''),
            onClick: function(e) { if (e.ctrlKey || e.metaKey || e.shiftKey) return; e.preventDefault(); setTab(t.key); },
            style: { background: 'transparent', border: 'none', padding: '10px 14px', cursor: 'pointer', textDecoration: 'none',
              color: isActive ? C.accent : C.textMuted, fontSize: 13, fontWeight: 500, borderBottom: isActive ? '2px solid ' + C.accent : '2px solid transparent', marginBottom: -1 }
          }, t.label);
        })
      ),
      tab === 'vendors'
        ? React.createElement(VendorTogglePanel, { showToast: props.showToast })
        : React.createElement(MasterDataPanel, { kind: tab, key: tab, showToast: props.showToast })
    );
  }

  // Schema metadata per master kind (drives the inline-add row + columns)
  var MASTER_SCHEMA = {
    'accounts': {
      addLabel: 'Add Account',
      // Balance is intentionally NOT here — balances are managed on the current cycle
      // (cycle 1 holds the balance snapshot; cycles 2+ chain via starting_cash). The
      // round-trip from cycle line edits keeps cash_accounts.current_balance in sync, so
      // new accounts added here will pick up a balance the moment the user enters it on
      // cycle 1.
      fields: [
        { key: 'name', label: 'Name', type: 'text', width: 240 },
        { key: 'type', label: 'Type', type: 'select', options: [{value:'bank',label:'Bank'},{value:'credit_card',label:'Credit Card'},{value:'brokerage',label:'Brokerage'},{value:'other',label:'Other'}], width: 130 },
        { key: 'external_ref', label: 'External Ref', type: 'text', width: 120 },
        { key: 'credit_limit', label: 'Credit Limit', type: 'number', width: 110 },
        { key: 'notes', label: 'Notes', type: 'text', width: '100%' },
      ],
    },
    'ar-channels': {
      addLabel: 'Add AR Channel',
      fields: [
        { key: 'name', label: 'Name', type: 'text', width: 260 },
        { key: 'default_biweekly', label: 'Default Bi-weekly Inflow', type: 'number', width: 160 },
        { key: 'notes', label: 'Notes', type: 'text', width: '100%' },
      ],
    },
    'loans': {
      addLabel: 'Add Loan / Tranche',
      fields: [
        { key: 'name', label: 'Name', type: 'text', width: 260 },
        { key: 'lender', label: 'Lender', type: 'text', width: 130 },
        { key: 'tranche_label', label: 'Tranche', type: 'text', width: 130 },
        { key: 'balance', label: 'Balance', type: 'number', width: 110 },
        { key: 'biweekly_payment', label: 'Bi-weekly Pmt', type: 'number', width: 120 },
        { key: 'monthly_payment', label: 'Monthly Pmt', type: 'number', width: 120 },
        { key: 'payment_method', label: 'Method', type: 'text', width: 110 },
        { key: 'source_account', label: 'Source Account', type: 'text', width: 130 },
        { key: 'start_date', label: 'Start Date', type: 'date', width: 140 },
        { key: 'end_date', label: 'End Date', type: 'date', width: 140 },
        { key: 'notes', label: 'Notes', type: 'text', width: '100%' },
      ],
    },
    'shareholder-loans': {
      addLabel: 'Add Shareholder Loan',
      fields: [
        { key: 'name', label: 'Name', type: 'text', width: 240, required: true },
        { key: 'lender', label: 'Lender', type: 'text', width: 150, required: true },
        { key: 'balance', label: 'Balance', type: 'number', width: 130, required: true },
        { key: 'biweekly_payment', label: 'Bi-weekly Pmt', type: 'number', width: 130, required: true },
        { key: 'start_date', label: 'Start Date', type: 'date', width: 140 },
        { key: 'end_date', label: 'End Date', type: 'date', width: 140, required: true },
        { key: 'notes', label: 'Notes', type: 'text', width: '100%' },
      ],
    },
    'credit-cards': {
      addLabel: 'Add Credit Card',
      fields: [
        { key: 'name', label: 'Name', type: 'text', width: 260 },
        { key: 'biweekly_payment', label: 'Bi-weekly Payment', type: 'number', width: 150 },
        { key: 'payment_method', label: 'Method', type: 'text', width: 130 },
        { key: 'notes', label: 'Notes', type: 'text', width: '100%' },
      ],
    },
    'payroll': {
      addLabel: 'Add Employee',
      fields: [
        { key: 'name', label: 'Name', type: 'text', width: 220 },
        { key: 'biweekly_amount', label: 'Bi-weekly Amount', type: 'number', width: 140 },
        { key: 'payment_method', label: 'Method', type: 'select', options: [{value:'ACH',label:'ACH'},{value:'ZELLE',label:'Zelle'},{value:'WISE',label:'Wise'},{value:'CHECK',label:'Check'}], width: 110 },
        { key: 'is_auto', label: 'Auto', type: 'bool', width: 60 },
        { key: 'notes', label: 'Notes', type: 'text', width: '100%' },
      ],
    },
    'expenses': {
      addLabel: 'Add Recurring Expense',
      fields: [
        { key: 'name', label: 'Name', type: 'text', width: 240 },
        { key: 'biweekly_amount', label: 'Bi-weekly Amount', type: 'number', width: 140 },
        { key: 'source_account', label: 'Source Account', type: 'text', width: 130 },
        { key: 'schedule', label: 'Schedule', type: 'select', options: [{value:'biweekly',label:'Bi-weekly'},{value:'first_half',label:'First Half Only'},{value:'second_half',label:'Second Half Only'}], width: 140 },
        { key: 'notes', label: 'Notes', type: 'text', width: '100%' },
      ],
    },
  };

  function MasterDataPanel(props) {
    var schema = MASTER_SCHEMA[props.kind];
    var [rows, setRows] = React.useState(null);
    var [showInactive, setShowInactive] = React.useState(false);
    var [addForm, setAddForm] = React.useState({});
    var [showAdd, setShowAdd] = React.useState(false);
    var [activityLender, setActivityLender] = React.useState(null);
    var [collapsedLenders, setCollapsedLenders] = React.useState({});
    var [confirmDel, setConfirmDel] = React.useState(null); // row pending disable-confirmation
    var [delBusy, setDelBusy] = React.useState(false);
    var [addBusy, setAddBusy] = React.useState(false);

    var load = function() {
      window.api('/accounting/master/' + props.kind).then(setRows).catch(function(e) { props.showToast(e.message, 'error'); });
    };
    React.useEffect(load, [props.kind]);

    var updateRow = async function(row, patch) {
      try {
        var r = await window.api('/accounting/master/' + props.kind + '/' + row.id, { method: 'PUT', body: JSON.stringify(patch) });
        setRows(function(prev) { return (prev || []).map(function(x) { return x.id === row.id ? Object.assign({}, x, r) : x; }); });
        props.showToast('Saved');
      } catch (e) { props.showToast(e.message, 'error'); }
    };

    var disableRow = function(row) { setConfirmDel(row); };
    var doDisable = async function() {
      if (!confirmDel) return;
      setDelBusy(true);
      try {
        var res = await window.api('/accounting/master/' + props.kind + '/' + confirmDel.id, { method: 'DELETE' });
        var pruned = (res && res.pruned_lines) || 0;
        props.showToast('Disabled "' + confirmDel.name + '"' + (pruned > 0 ? ' — removed from ' + pruned + ' forecast row' + (pruned === 1 ? '' : 's') : ''), 'success');
        setConfirmDel(null);
        load();
      } catch (e) { props.showToast(e.message, 'error'); }
      finally { setDelBusy(false); }
    };

    var reactivate = async function(row) {
      await updateRow(row, { is_active: true });
    };

    var addNew = async function() {
      if (addBusy) return;
      // Validate: fields marked required:true (plus Name as a fallback) must be non-empty.
      // Numbers can be 0 (valid) but not null/undefined; text must be non-blank; dates must be set.
      var required = schema.fields.filter(function(f) { return f.required; });
      if (!required.length) required = schema.fields.filter(function(f) { return f.key === 'name'; });
      var missing = [];
      for (var i = 0; i < required.length; i++) {
        var f = required[i];
        var v = addForm[f.key];
        var empty = v == null || v === '' || (f.type === 'text' && !String(v).trim());
        if (empty) missing.push(f.label);
      }
      if (missing.length) {
        props.showToast('Required: ' + missing.join(', '), 'error');
        return;
      }
      setAddBusy(true);
      try {
        await window.api('/accounting/master/' + props.kind, { method: 'POST', body: JSON.stringify(addForm) });
        setAddForm({}); setShowAdd(false); load();
      } catch (e) { props.showToast(e.message, 'error'); }
      finally { setAddBusy(false); }
    };

    if (!rows) return React.createElement('div', { style: { color: C.textMuted, padding: 20 } }, 'Loading…');

    var visible = rows.filter(function(r) { return showInactive || r.is_active; });

    return React.createElement('div', null,
      React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 } },
        React.createElement('label', { style: { fontSize: 12, color: C.textMuted, display: 'flex', alignItems: 'center', gap: 6 } },
          React.createElement('input', { type: 'checkbox', checked: showInactive, onChange: function(e) { setShowInactive(e.target.checked); } }),
          'Show inactive'
        ),
        showAdd
          ? React.createElement(window.Btn, { size: 'sm', variant: 'ghost', onClick: function() { setShowAdd(false); setAddForm({}); } }, 'Cancel')
          : React.createElement(window.Btn, { size: 'sm', variant: 'primary', onClick: function() { setShowAdd(true); setAddForm({}); } }, '+ ' + schema.addLabel)
      ),
      showAdd && React.createElement('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: 12, marginBottom: 14, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-end' } },
        schema.fields.map(function(f) {
          return React.createElement('div', { key: f.key, style: { display: 'flex', flexDirection: 'column', gap: 4, flex: f.width === '100%' ? '1 1 100%' : '0 0 auto' } },
            React.createElement('label', { style: { fontSize: 10, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 } },
              f.label,
              f.required && React.createElement('span', { style: { color: C.danger, marginLeft: 3 }, title: 'Required' }, '*')
            ),
            f.type === 'select' ? React.createElement('select', {
              value: addForm[f.key] || '', onChange: function(e) { setAddForm(Object.assign({}, addForm, (function(){ var o={}; o[f.key] = e.target.value; return o; })())); },
              style: { width: f.width === '100%' ? '100%' : f.width, background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '6px 8px', borderRadius: 4, fontSize: 12 }
            }, React.createElement('option', { value: '' }, '—'), f.options.map(function(o) { return React.createElement('option', { key: o.value, value: o.value }, o.label); }))
            : f.type === 'bool' ? React.createElement('input', { type: 'checkbox', checked: !!addForm[f.key], onChange: function(e) { setAddForm(Object.assign({}, addForm, (function(){ var o={}; o[f.key] = e.target.checked; return o; })())); }, style: { width: 18, height: 18 } })
            : React.createElement('input', { type: f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text', step: f.type === 'number' ? '0.01' : undefined,
              value: addForm[f.key] != null ? addForm[f.key] : '', onChange: function(e) { setAddForm(Object.assign({}, addForm, (function(){ var o={}; o[f.key] = f.type === 'number' ? (e.target.value === '' ? '' : parseFloat(e.target.value)) : e.target.value; return o; })())); },
              style: { width: f.width === '100%' ? '100%' : f.width, background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '7px 10px', borderRadius: 4, fontSize: 13, fontFamily: f.type === 'number' ? 'inherit' : 'inherit', textAlign: 'left', colorScheme: 'dark' } })
          );
        }),
        React.createElement(window.Btn, { size: 'sm', variant: 'primary', disabled: addBusy, onClick: addNew }, addBusy ? 'Saving…' : 'Save')
      ),
      (function() {
        if (visible.length === 0) {
          return React.createElement(window.Empty, { title: 'No entries', sub: 'Add some via "' + schema.addLabel + '" above.' });
        }
        var renderRow = function(row) {
          return React.createElement('tr', { key: row.id, style: { borderTop: '1px solid ' + C.border, opacity: row.is_active ? 1 : 0.5 } },
            schema.fields.map(function(f) {
              return React.createElement('td', { key: f.key, style: { padding: '7px 12px', textAlign: 'left' } },
                f.type === 'select' ? React.createElement('select', {
                  value: row[f.key] || '', onChange: function(e) { var p = {}; p[f.key] = e.target.value; updateRow(row, p); },
                  style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '4px 8px', borderRadius: 4, fontSize: 12 }
                }, React.createElement('option', { value: '' }, '—'), f.options.map(function(o) { return React.createElement('option', { key: o.value, value: o.value }, o.label); }))
                : f.type === 'bool' ? React.createElement('input', { type: 'checkbox', checked: !!row[f.key], onChange: function(e) { var p = {}; p[f.key] = e.target.checked; updateRow(row, p); } })
                : React.createElement(EditCell, { onInvalid: function(t) { props.showToast('"' + t + '" is not a number or a sum', 'error'); }, type: f.type, value: row[f.key], onSave: function(v) { var p = {}; p[f.key] = v; updateRow(row, p); }, width: f.width === '100%' ? '100%' : f.width, textAlign: 'left' })
              );
            }),
            React.createElement('td', { style: { padding: '6px 12px', textAlign: 'center' } },
              React.createElement('input', { type: 'checkbox', checked: !!row.is_active, onChange: function() { row.is_active ? disableRow(row) : reactivate(row); } })
            ),
            React.createElement('td', { style: { textAlign: 'right', padding: '4px 8px' } },
              React.createElement('button', { onClick: function() { row.is_active ? disableRow(row) : null; }, disabled: !row.is_active, title: 'Disable',
                style: { background: 'transparent', border: 'none', color: row.is_active ? C.textDim : 'transparent', cursor: row.is_active ? 'pointer' : 'default', padding: 4 } },
                React.createElement(window.LIcon, { name: 'trash-2', size: 14 }))
            )
          );
        };
        // Group rows by lender for shareholder-loans (with Total Balance + Activity Log per group).
        var isShLoan = props.kind === 'shareholder-loans';
        var bodyRows;
        var totalCols = schema.fields.length + 2;
        if (isShLoan) {
          var byLender = {};
          visible.forEach(function(row) {
            var l = (row.lender && String(row.lender).trim()) || '(No Lender)';
            if (!byLender[l]) byLender[l] = { lender: l, rows: [], totalBalance: 0 };
            byLender[l].rows.push(row);
            byLender[l].totalBalance += Number(row.balance || 0);
          });
          var lenderNames = Object.keys(byLender).sort(function(a, b) { return a.localeCompare(b); });
          // Sort loans within each lender chronologically — names typically lead with a date
          // like "4/1/2023 36MO LOAN", "01/05/2024 36 MO", "06/2023 36MO 30%". Parse the leading
          // date and sort by it. Names without a parseable date fall through to alphabetical.
          var parseLeadingDate = function(name) {
            var s = String(name || '').trim();
            var m;
            // M/D/YYYY or MM/DD/YYYY (or 2-digit year)
            m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
            if (m) { var y = parseInt(m[3]); if (y < 100) y += 2000; return new Date(y, parseInt(m[1]) - 1, parseInt(m[2])).getTime(); }
            // M/YYYY or MM/YYYY (no day) — first of month
            m = s.match(/^(\d{1,2})\/(\d{4})\b/);
            if (m) return new Date(parseInt(m[2]), parseInt(m[1]) - 1, 1).getTime();
            return Infinity;
          };
          lenderNames.forEach(function(name) {
            byLender[name].rows.sort(function(a, b) {
              var da = parseLeadingDate(a.name);
              var db = parseLeadingDate(b.name);
              if (da !== db) return da - db;
              return (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase());
            });
          });
          bodyRows = [];
          lenderNames.forEach(function(name) {
            var g = byLender[name];
            // Default to collapsed; explicit false in state means user expanded this group.
            var isCollapsed = collapsedLenders[name] !== false;
            bodyRows.push(React.createElement('tr', { key: 'h-' + name,
              onClick: (function(_isCollapsed) { return function() {
                setCollapsedLenders(function(prev) { var n = Object.assign({}, prev); n[name] = !_isCollapsed; return n; });
              }; })(isCollapsed),
              style: { background: C.bg, borderTop: '3px solid ' + C.border, cursor: 'pointer', userSelect: 'none' }
            },
              React.createElement('td', { colSpan: totalCols, style: { padding: '12px 14px' } },
                React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' } },
                  React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' } },
                    React.createElement(window.LIcon, { name: isCollapsed ? 'chevron-right' : 'chevron-down', size: 16 }),
                    React.createElement('span', { style: { fontSize: 14, fontWeight: 700, color: C.text } }, name),
                    React.createElement('span', { style: { fontSize: 12, color: C.textMuted } }, g.rows.length + ' loan' + (g.rows.length === 1 ? '' : 's')),
                    React.createElement('span', { style: { fontSize: 13, color: C.text } },
                      'Total Balance: ',
                      React.createElement('span', { style: { fontFamily: 'inherit', fontWeight: 600 } }, '$' + g.totalBalance.toLocaleString('en-US', { minimumFractionDigits: 2 }))
                    )
                  ),
                  name !== '(No Lender)' && React.createElement(window.Btn, { size: 'sm', variant: 'default',
                    onClick: function(e) { e.stopPropagation(); setActivityLender(name); }
                  }, 'Activity Log')
                )
              )
            ));
            if (!isCollapsed) {
              g.rows.forEach(function(row) { bodyRows.push(renderRow(row)); });
            }
          });
        } else {
          bodyRows = visible.map(renderRow);
        }
        return React.createElement('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, overflow: 'auto' } },
          React.createElement('table', { style: { width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 800 } },
            React.createElement('thead', null,
              React.createElement('tr', { style: { color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 11, fontWeight: 700 } },
                schema.fields.map(function(f) { return React.createElement('th', { key: f.key, style: { textAlign: 'left', padding: '10px 12px' } }, f.label); }),
                React.createElement('th', { style: { padding: '10px 12px', textAlign: 'left', width: 80 } }, 'Active'),
                React.createElement('th', { style: { width: 40 } })
              )
            ),
            React.createElement('tbody', null, bodyRows)
          )
        );
      })(),
      React.createElement(ShareholderActivityModal, { lender: activityLender, onClose: function() { setActivityLender(null); }, showToast: props.showToast }),
      confirmDel && React.createElement(window.Modal, { open: true, onClose: function() { if (!delBusy) setConfirmDel(null); }, title: 'Disable "' + confirmDel.name + '"?', width: 520 },
        React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 14 } },
          React.createElement('div', { style: { fontSize: 13, color: C.text, lineHeight: 1.6 } },
            'Here’s what happens:',
            React.createElement('ul', { style: { margin: '8px 0 0', paddingLeft: 20, color: C.textMuted } },
              React.createElement('li', { style: { marginBottom: 6 } }, 'It stops appearing in ', React.createElement('strong', { style: { color: C.text } }, 'new'), ' cycles going forward.'),
              React.createElement('li', { style: { marginBottom: 6 } }, 'It’s removed from your ', React.createElement('strong', { style: { color: C.text } }, 'future Draft (forecast) cycles'), ' right away.'),
              React.createElement('li', null, 'Your ', React.createElement('strong', { style: { color: C.text } }, 'current cycle'), ' and any ', React.createElement('strong', { style: { color: C.text } }, 'closed cycles'), ' keep it — history is preserved.')
            )
          ),
          React.createElement('div', { style: { fontSize: 12, color: C.textDim } }, 'You can re-enable it anytime with the “Show inactive” toggle.'),
          React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 } },
            React.createElement(window.Btn, { variant: 'ghost', onClick: function() { setConfirmDel(null); }, disabled: delBusy }, 'Cancel'),
            React.createElement(window.Btn, { variant: 'danger', onClick: doDisable, disabled: delBusy }, delBusy ? 'Disabling…' : 'Disable')
          )
        )
      )
    );
  }

  // Activity log modal for shareholder loans (per lender).
  function ShareholderActivityModal(props) {
    var [entries, setEntries] = React.useState(null);
    React.useEffect(function() {
      if (!props.lender) { setEntries(null); return; }
      window.api('/accounting/master/shareholder-loans/activity?lender=' + encodeURIComponent(props.lender))
        .then(setEntries).catch(function(e) { if (props.showToast) props.showToast(e.message, 'error'); setEntries([]); });
    }, [props.lender]);
    if (!props.lender) return null;

    var actionLabel = function(a) {
      var map = { cycle_payment: 'Cycle Payment', manual_balance_change: 'Balance Edit', end_date_change: 'End Date Change', start_date_change: 'Start Date Change', created: 'Created', disabled: 'Disabled', reactivated: 'Reactivated' };
      return map[a] || a;
    };
    var actionColor = function(a) {
      if (a === 'cycle_payment') return C.danger;
      if (a === 'created' || a === 'reactivated') return C.success;
      if (a === 'disabled') return C.textMuted;
      return C.accent;
    };
    var detailStr = function(e) {
      var d = e.details || {};
      if (e.action === 'cycle_payment') return '−$' + Number(d.payment || 0).toFixed(2) + ' (cycle ' + (d.cycle_start || '') + ') → balance $' + Number(d.new_balance || 0).toFixed(2);
      if (e.action === 'manual_balance_change') return '$' + Number(d.from || 0).toFixed(2) + ' → $' + Number(d.to || 0).toFixed(2);
      if (e.action === 'end_date_change' || e.action === 'start_date_change') return (d.from || 'none') + ' → ' + (d.to || 'none');
      if (e.action === 'created') return 'Balance: $' + Number(d.initial_balance || 0).toFixed(2) + (d.biweekly_payment ? ' · biweekly $' + Number(d.biweekly_payment).toFixed(2) : '') + (d.end_date ? ' · ends ' + d.end_date : '');
      return '';
    };
    var downloadCsv = function() {
      if (!entries || !entries.length) return;
      var rows = [['Date', 'User', 'Action', 'Loan', 'Details']];
      entries.forEach(function(e) {
        rows.push([
          new Date(e.created_at).toISOString(),
          e.username || '',
          actionLabel(e.action),
          e.loan_name || '',
          detailStr(e)
        ]);
      });
      var csv = rows.map(function(r) {
        return r.map(escapeCSV).join(','); // shared cell rule, formula-safe (review 2026-09-28, medium #31)
      }).join('\r\n');
      var blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
      var url = URL.createObjectURL(blob);
      var a = document.createElement('a');
      var stamp = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
      a.href = url;
      a.download = 'shareholder-loan-activity_' + props.lender.replace(/[^a-z0-9-]+/gi, '_') + '_' + stamp + '.csv';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    };
    return React.createElement(window.Modal, { open: true, onClose: props.onClose, title: 'Activity Log — ' + props.lender, width: 720, maxHeight: 'calc(85vh * var(--invzoom, 1))' },
      React.createElement('div', null,
        entries && entries.length > 0 && React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 } },
          React.createElement('div', { style: { fontSize: 12, color: C.textMuted } }, entries.length + ' entr' + (entries.length === 1 ? 'y' : 'ies')),
          React.createElement(window.Btn, { size: 'sm', variant: 'default', onClick: downloadCsv }, React.createElement(window.LIcon, { name: 'download', size: 14 }), ' Download CSV')
        ),
        !entries && React.createElement('div', { style: { color: C.textMuted, padding: 20 } }, 'Loading…'),
        entries && entries.length === 0 && React.createElement(window.Empty, { title: 'No activity yet', sub: 'Cycle payments, balance edits, end-date changes, and create/disable events will appear here.' }),
        entries && entries.length > 0 && React.createElement('div', { style: { background: C.bg, border: '1px solid ' + C.border, borderRadius: 8, maxHeight: 'calc(65vh * var(--invzoom, 1))', overflowY: 'auto' } },
          entries.map(function(e) {
            return React.createElement('div', { key: e.id, style: { display: 'flex', justifyContent: 'space-between', gap: 12, padding: '12px 14px', borderTop: '1px solid ' + C.border } },
              React.createElement('div', { style: { flex: 1, minWidth: 0 } },
                React.createElement('div', { style: { fontSize: 13, color: C.text } },
                  React.createElement('span', { style: { fontWeight: 600, color: actionColor(e.action) } }, actionLabel(e.action)),
                  e.loan_name && React.createElement('span', { style: { marginLeft: 8, color: C.textMuted } }, '· ' + e.loan_name)
                ),
                React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 4, fontFamily: 'inherit' } }, detailStr(e))
              ),
              React.createElement('div', { style: { fontSize: 11, color: C.textDim, whiteSpace: 'nowrap', textAlign: 'right' } },
                new Date(e.created_at).toLocaleString(),
                e.username && React.createElement('div', { style: { marginTop: 2 } }, 'by ' + e.username)
              )
            );
          })
        )
      )
    );
  }

  function VendorTogglePanel(props) {
    var [vendors, setVendors] = React.useState(null);
    var [search, setSearch] = React.useState('');
    var [filter, setFilter] = React.useState('all');
    React.useEffect(function() {
      window.api('/accounting/vendors').then(setVendors).catch(function(e) { props.showToast(e.message, 'error'); });
    }, []);
    var toggle = async function(v) {
      try {
        var r = await window.api('/accounting/vendors/' + v.id + '/accounting', { method: 'PUT', body: JSON.stringify({ accounting_active: !v.accounting_active }) });
        setVendors(function(prev) { return (prev || []).map(function(x) { return x.id === v.id ? Object.assign({}, x, r) : x; }); });
        props.showToast('Saved');
      } catch (e) { props.showToast(e.message, 'error'); }
    };
    if (!vendors) return React.createElement('div', { style: { color: C.textMuted, padding: 20 } }, 'Loading…');
    var q = search.trim().toLowerCase();
    var filtered = vendors.filter(function(v) {
      if (filter === 'active' && !v.accounting_active) return false;
      if (filter === 'inactive' && v.accounting_active) return false;
      if (q && (v.vendor_name || '').toLowerCase().indexOf(q) < 0) return false;
      return true;
    });
    var activeCount = vendors.filter(function(v){ return v.accounting_active; }).length;
    return React.createElement('div', null,
      React.createElement('div', { style: { color: C.textMuted, fontSize: 12, marginBottom: 12 } },
        'Pin vendors to your AP list. Pinned vendors auto-populate in every new cycle. ',
        React.createElement('strong', { style: { color: C.text } }, activeCount + ' of ' + vendors.length + ' pinned.')
      ),
      React.createElement('div', { style: { display: 'flex', gap: 8, marginBottom: 12 } },
        React.createElement('input', { value: search, onChange: function(e) { setSearch(e.target.value); }, placeholder: 'Search vendors…',
          style: { flex: 1, background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 13 } }),
        ['all', 'active', 'inactive'].map(function(f) {
          return React.createElement('button', { key: f, onClick: function() { setFilter(f); },
            style: { padding: '6px 12px', background: filter === f ? C.accentSoft : C.surface, color: filter === f ? C.accent : C.textMuted, border: '1px solid ' + (filter === f ? C.accent + '55' : C.border), borderRadius: 6, fontSize: 12, cursor: 'pointer', textTransform: 'capitalize' } }, f);
        })
      ),
      React.createElement('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, maxHeight: 'calc(65vh * var(--invzoom, 1))', overflowY: 'auto' } },
        filtered.length === 0 && React.createElement('div', { style: { padding: 20, color: C.textMuted, fontSize: 13 } }, 'No vendors match.'),
        filtered.map(function(v) {
          return React.createElement('div', { key: v.id, style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderTop: '1px solid ' + C.border } },
            React.createElement('div', null,
              React.createElement('div', { style: { fontSize: 13, color: C.text, fontWeight: 500 } }, v.vendor_name),
              React.createElement('div', { style: { fontSize: 11, color: C.textMuted } }, v.payment_method || 'no payment method')
            ),
            React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' } },
              React.createElement('input', { type: 'checkbox', checked: !!v.accounting_active, onChange: function() { toggle(v); } }),
              React.createElement('span', { style: { fontSize: 12, color: v.accounting_active ? C.accent : C.textMuted } }, v.accounting_active ? 'Pinned' : 'Not pinned')
            )
          );
        })
      )
    );
  }

  // ---------- AccountingBillsPage ----------
  // Read-only viewer of all Bill.com bills in our DB, with cycle / vendor / PO linkage shown
  // for reconciliation: is this bill in a cycle? matched to our vendor? linked to a PO?
  function AccountingBillsPage(props) {
    var [bills, setBills] = React.useState(null);
    var [filter, setFilter] = React.useState('all'); // all | unmapped | not_in_cycle | paid | open
    React.useEffect(function() {
      window.api('/accounting/bills').then(setBills).catch(function(e) { props.showToast(e.message, 'error'); setBills([]); });
    }, []);
    if (!bills) return React.createElement('div', { style: { padding: 40, color: C.textMuted } }, 'Loading…');

    var ourVendor = function(b) { return b.mapped_vendor_name || b.name_match_vendor_name || null; };

    var filtered = bills.filter(function(b) {
      if (filter === 'unmapped') return !ourVendor(b);
      if (filter === 'not_in_cycle') return !b.cycle_id;
      if (filter === 'paid') return b.payment_status === 'paid';
      if (filter === 'open') return b.payment_status !== 'paid';
      return true;
    });

    var counts = {
      all: bills.length,
      unmapped: bills.filter(function(b) { return !ourVendor(b); }).length,
      not_in_cycle: bills.filter(function(b) { return !b.cycle_id; }).length,
      paid: bills.filter(function(b) { return b.payment_status === 'paid'; }).length,
      open: bills.filter(function(b) { return b.payment_status !== 'paid'; }).length,
    };
    var totalOpenAmount = bills.filter(function(b) { return b.payment_status !== 'paid'; }).reduce(function(s, b) { return s + Math.max(0, Number(b.amount || 0) - Number(b.amount_paid || 0)); }, 0);

    var statusColor = function(s) {
      if (s === 'paid') return C.success;
      if (s === 'scheduled') return C.accent;
      if (s === 'partial') return C.warning;
      return C.textMuted;
    };

    var cols = [
      { key: 'vendor_name', label: 'Bill.com Vendor', render: function(b) {
        return React.createElement('span', { style: { color: C.text, fontWeight: 500 } }, b.vendor_name || '—');
      }},
      { key: 'our_vendor', label: 'Our Vendor', sortValue: function(b) { return (ourVendor(b) || '').toLowerCase(); }, render: function(b) {
        var v = ourVendor(b);
        if (!v) return React.createElement('span', { style: { fontSize: 11, padding: '2px 8px', borderRadius: 4, background: C.warningSoft, color: C.warning, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }, title: 'Bill.com vendor not matched to any of our vendors. Add a mapping in Settings > Integrations > Bill.com.' }, 'Unmapped');
        var source = b.mapped_vendor_id ? '(manual mapping)' : '(name match)';
        return React.createElement('span', { title: source }, v);
      }},
      { key: 'invoice_number', label: 'Invoice #', render: function(b) { return React.createElement('span', { style: { fontFamily: 'inherit', fontSize: 12 } }, b.invoice_number || '—'); }},
      { key: 'invoice_date', label: 'Invoice Date', sortValue: function(b) { return b.invoice_date || ''; }, render: function(b) { return b.invoice_date ? fmtDate(b.invoice_date) : '—'; }},
      { key: 'due_date', label: 'Due Date', sortValue: function(b) { return b.due_date || ''; }, render: function(b) { return b.due_date ? fmtDate(b.due_date) : '—'; }},
      { key: 'amount', label: 'Amount', sortValue: function(b) { return Number(b.amount || 0); }, render: function(b) {
        return React.createElement('span', { style: { fontFamily: 'inherit' } }, '$' + Number(b.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 }));
      }},
      { key: 'remaining', label: 'Remaining', sortValue: function(b) { return Math.max(0, Number(b.amount || 0) - Number(b.amount_paid || 0)); }, render: function(b) {
        var rem = Math.max(0, Number(b.amount || 0) - Number(b.amount_paid || 0));
        var color = rem > 0 ? C.danger : C.success;
        return React.createElement('span', { style: { fontFamily: 'inherit', color: color, fontWeight: 600 } }, '$' + rem.toLocaleString('en-US', { minimumFractionDigits: 2 }));
      }},
      { key: 'payment_status', label: 'Status', render: function(b) {
        var s = b.payment_status || 'open';
        var c = statusColor(s);
        return React.createElement('span', { style: { fontSize: 10, padding: '2px 8px', borderRadius: 4, background: c + '22', color: c, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' } }, s);
      }},
      { key: 'cycle_id', label: 'Cycle', sortValue: function(b) { return b.cycle_start || ''; }, render: function(b) {
        if (!b.cycle_id) return React.createElement('span', { style: { color: C.textDim, fontStyle: 'italic' } }, 'not in cycle');
        var href = '/accounting/' + b.cycle_id;
        return React.createElement('a', { href: href,
          onClick: function(e) { if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return; e.preventDefault(); props.onNavigate('accounting', b.cycle_id); },
          style: { color: C.accent, textDecoration: 'none', fontWeight: 500 } },
          fmtDate(b.cycle_start) + ' → ' + fmtDate(b.cycle_end)
        );
      }},
      { key: 'po_number', label: 'Linked PO', sortValue: function(b) { return b.po_number || ''; }, render: function(b) {
        // One bill can cover several POs (comma/slash-separated Bill.com PO field) — link each.
        var ids = b.po_ids || (b.po_id ? [b.po_id] : []);
        var nums = b.po_numbers || (b.po_number ? [b.po_number] : []);
        if (!ids.length) return React.createElement('span', { style: { color: C.textDim } }, '—');
        var parts = [];
        ids.forEach(function(poId, i) {
          if (i > 0) parts.push(', ');
          parts.push(React.createElement('a', { key: poId, href: '/po/' + poId,
            onClick: function(e) { if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return; e.preventDefault(); props.onNavigate('po-detail', poId); },
            style: { color: C.accent, textDecoration: 'none', fontFamily: 'inherit', fontWeight: 600 } },
            'PO #' + (nums[i] || poId)
          ));
        });
        return React.createElement('span', null, parts);
      }},
      { key: 'fetched_at', label: 'Last Synced', defaultVisible: false, render: function(b) { return b.fetched_at ? new Date(b.fetched_at).toLocaleString() : '—'; }},
    ];

    var pill = function(key, label) {
      var active = filter === key;
      return React.createElement('button', { key: key, onClick: function() { setFilter(key); },
        style: { padding: '6px 12px', fontSize: 12, borderRadius: 4, cursor: 'pointer', background: active ? C.accentSoft : C.surface, color: active ? C.accent : C.textMuted, border: '1px solid ' + (active ? C.accent + '55' : C.border), fontWeight: 500 } },
        label, ' ', React.createElement('span', { style: { fontSize: 11, opacity: 0.7 } }, '(' + counts[key] + ')'));
    };

    return React.createElement('div', { style: { padding: '20px 28px 60px', maxWidth: 1700, margin: '0 auto' } },
      React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, gap: 12, flexWrap: 'wrap' } },
        React.createElement('div', null,
          React.createElement('h2', { style: { margin: 0, fontSize: 18, fontWeight: 600, color: C.text } }, 'Bills'),
          React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 4 } },
            bills.length + ' bills synced from Bill.com · ',
            React.createElement('span', { style: { color: C.danger, fontWeight: 600, fontFamily: 'inherit' } }, '$' + totalOpenAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })),
            ' open balance'
          )
        ),
        React.createElement(window.Btn, { variant: 'primary', size: 'sm', onClick: function() { props.onNavigate('accounting'); } }, 'Back to Current Cycle')
      ),
      React.createElement('div', { style: { display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' } },
        pill('all', 'All'),
        pill('open', 'Open'),
        pill('paid', 'Paid'),
        pill('unmapped', 'Unmapped Vendor'),
        pill('not_in_cycle', 'Not in any cycle')
      ),
      bills.length === 0
        ? React.createElement(window.Empty, { title: 'No bills synced yet', sub: 'Go to Settings > Integrations > Bill.com and click Sync Now.' })
        : React.createElement(window.DataTable, {
            columns: cols, data: filtered,
            emptyTitle: 'No bills match this filter', emptySub: 'Try a different filter above.',
            defaultSort: 'due_date', defaultSortDir: 'desc', storageKey: 'accounting-bills'
          })
    );
  }

  // Expose
  window.AccountingCyclePage = AccountingCyclePage;
  window.AccountingHistoryPage = AccountingHistoryPage;
  window.AccountingTrendsPage = AccountingTrendsPage;
  window.AccountingBillsPage = AccountingBillsPage;
  window.AccountingSettingsPage = AccountingSettingsPage;
})();

;
/* ===== app-shopify.js ===== */
// ─── Shopify AI FAQ Module ───
// Visual dashboard for the Shopify FAQ integration, backed by /api/shopify-faq.
// Reached from the Shopify card in Settings > Integrations (not a top-level nav item).
// Exports: window.ShopifyFaqPage

var h = React.createElement;

function _sfFmtDate(d) { return d ? new Date(d).toLocaleString() : '—'; }

function _sfPill(col, text) {
  return h('span', { style: { display: 'inline-block', background: col + '22', color: col, padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' } }, text);
}
function _sfStatusBadge(s) {
  var map = { generated: C.success, failed: C.danger, pending: C.textMuted, skipped: C.accent };
  return _sfPill(map[s] || C.textMuted, s || 'pending');
}

function ShopifyFaqStatCard(props) {
  return h('div', { style: { flex: 1, minWidth: 150, background: C.bgCard || C.card || '#1A1D27', border: '1px solid ' + C.border, borderRadius: 10, padding: '14px 16px' } },
    h('div', { style: { fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 } }, props.label),
    h('div', { style: { fontSize: 22, fontWeight: 700, color: props.color || C.text } }, props.value),
    props.sub ? h('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 2 } }, props.sub) : null
  );
}

function ShopifyFaqPage({ showToast, onNavigate, currentUser, embedded }) {
  // Generate (paid AI calls pushed live to Shopify) and Settings are admin-only on the server (9-27);
  // unknown user (embedded without one) leaves them enabled and the server decides
  var sfAdmin = currentUser ? currentUser.role === 'admin' : true;
  var s = useState(null); var status = s[0], setStatus = s[1];
  var t = useState('products'); var tab = t[0], setTab = t[1];
  var p = useState([]); var products = p[0], setProducts = p[1];
  var pt = useState(0); var pTotal = pt[0], setPTotal = pt[1];
  var pf = useState('all'); var pFilter = pf[0], setPFilter = pf[1];
  var sc = useState(''); var search = sc[0], setSearch = sc[1];
  var rr = useState([]); var runs = rr[0], setRuns = rr[1];
  var ed = useState(null); var editing = ed[0], setEditing = ed[1];
  var bz = useState(false); var busy = bz[0], setBusy = bz[1];
  var st = useState({ model: '', prompt: '', schedule_enabled: false, in_stock_only: true }); var settings = st[0], setSettings = st[1];
  var settingsLoaded = useRef(false); // populate the settings form from the server only once, so 5s polls don't clobber edits

  function loadStatus() {
    api('/shopify-faq/status').then(function(d) {
      setStatus(d);
      // Only seed the settings form once — polls during a run must not overwrite the user's edits.
      if (d.settings && !settingsLoaded.current) {
        setSettings({ model: d.settings.model || '', prompt: d.settings.prompt || '', schedule_enabled: !!d.settings.scheduleEnabled, in_stock_only: d.settings.inStockOnly !== false });
        settingsLoaded.current = true;
      }
    }).catch(function(e) { showToast(e.message, 'error'); });
  }
  function loadProducts() {
    api('/shopify-faq/products?status=' + pFilter + '&search=' + encodeURIComponent(search) + '&limit=150')
      .then(function(r) { setProducts(r.products || []); setPTotal(r.total || 0); })
      .catch(function(e) { console.error('[silent-catch]', e); });
  }
  function loadRuns() { api('/shopify-faq/runs').then(function(r) { setRuns(r || []); }).catch(function(e) { console.error('[silent-catch]', e); }); }

  useEffect(function() { loadStatus(); loadProducts(); loadRuns(); }, []);
  useEffect(function() { loadProducts(); }, [pFilter]);
  // Poll while a run is in progress so coverage + run counts update live. The tick reads the
  // CURRENT loaders/tab through refs (review M7 #107): the old closure kept the filter and search
  // from when the run started and overwrote the user's newer results every 5 s.
  var pollRef = useRef({});
  pollRef.current = { loadStatus: loadStatus, loadRuns: loadRuns, loadProducts: loadProducts, tab: tab };
  useEffect(function() {
    if (!status || !status.running) return;
    var iv = setInterval(function() { var p = pollRef.current; p.loadStatus(); p.loadRuns(); if (p.tab === 'products') p.loadProducts(); }, 5000);
    return function() { clearInterval(iv); };
  }, [status && status.running]);

  function generate(mode) {
    if (mode === 'all' && !window.confirm('Generate FAQs for ALL products that don’t have one yet?\n\nThis calls the AI for every missing product (~$0.0035 each — roughly $35 for 10,000 products) and can take a while. Products that already have a FAQ are skipped. Continue?')) return;
    setBusy(true);
    api('/shopify-faq/generate', { method: 'POST', body: JSON.stringify({ mode: mode }) })
      .then(function(r) { showToast('Generation started (run #' + r.runId + ')', 'success'); setBusy(false); loadStatus(); loadRuns(); })
      .catch(function(e) { setBusy(false); showToast(e.message, 'error'); });
  }

  function openEditor(row) {
    setEditing({ product_gid: row.product_gid, title: row.title, faqs: (row.faqs || []).map(function(f) { return { question: f.question, answer: f.answer }; }) });
  }
  function saveEditor() {
    setBusy(true);
    api('/shopify-faq/product', { method: 'PUT', body: JSON.stringify({ productGid: editing.product_gid, faqs: editing.faqs }) })
      .then(function() { showToast('FAQ saved and pushed to Shopify', 'success'); setBusy(false); setEditing(null); loadProducts(); loadStatus(); })
      .catch(function(e) { setBusy(false); showToast(e.message, 'error'); });
  }
  function regenEditor() {
    setBusy(true);
    api('/shopify-faq/product/regenerate', { method: 'POST', body: JSON.stringify({ productGid: editing.product_gid }) })
      .then(function(r) { setBusy(false); setEditing(Object.assign({}, editing, { faqs: r.faqs || [] })); showToast('Regenerated — review and Save to push', 'success'); })
      .catch(function(e) { setBusy(false); showToast(e.message, 'error'); });
  }
  function saveSettings() {
    api('/shopify-faq/settings', { method: 'PUT', body: JSON.stringify(settings) })
      .then(function() { showToast('Settings saved', 'success'); loadStatus(); })
      .catch(function(e) { showToast(e.message, 'error'); });
  }

  var cov = status ? status.coverage : null;
  var sp = status && status.storeProductCount;
  var running = status && status.running;
  var lastRun = status && status.lastRun;

  var taStyle = { width: '100%', background: C.bg, color: C.text, border: '1px solid ' + C.border, borderRadius: 8, padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' };

  return h('div', { style: { padding: embedded ? 0 : 24, maxWidth: 1200, margin: '0 auto' } },
    // Header (hidden when embedded on Sage's desk in The Office — the desk carries the identity)
    embedded ? null : h('div', { style: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 } },
      h('button', { onClick: function() { onNavigate('settings-integrations'); }, style: { background: 'none', border: 'none', color: C.textMuted, cursor: 'pointer', display: 'flex', alignItems: 'center' } }, h(LIcon, { name: 'arrow-left', size: 20 })),
      h('div', { style: { width: 32, height: 32, borderRadius: 7, background: '#95BF47', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700 } }, 'S'),
      h('div', { style: { flex: 1 } },
        h('div', { style: { fontSize: 20, fontWeight: 700, color: C.text } }, 'Shopify AI FAQ'),
        h('div', { style: { fontSize: 12, color: C.textMuted } }, 'Auto-generated product FAQs written to the custom.faq metafield')
      ),
      h(Btn, { size: 'sm', onClick: function() { loadStatus(); loadProducts(); loadRuns(); } }, h(LIcon, { name: 'refresh-cw', size: 14 }), ' Refresh')
    ),

    // Stat cards (the desk's stats row shows these when embedded)
    embedded ? null : h('div', { style: { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 } },
      h(ShopifyFaqStatCard, { label: 'Coverage', value: status ? (status.coveragePct + '%') : '—', color: C.success, sub: cov ? (cov.generated + ' of ' + cov.total + ' tracked') : 'Run a scan to populate' }),
      h(ShopifyFaqStatCard, { label: 'Generated', value: cov ? cov.generated : '—' }),
      h(ShopifyFaqStatCard, { label: 'Failed', value: cov ? cov.failed : '—', color: cov && cov.failed ? C.danger : C.text }),
      h(ShopifyFaqStatCard, { label: 'Store products', value: sp ? (sp.count + (sp.precision === 'AT_LEAST' ? '+' : '')) : '—' }),
      h(ShopifyFaqStatCard, { label: 'Daily schedule', value: (status && status.settings && status.settings.scheduleEnabled) ? 'On' : 'Off', color: (status && status.settings && status.settings.scheduleEnabled) ? C.success : C.textMuted, sub: '07:00 UTC' })
    ),

    // Running banner
    running ? h('div', { style: { padding: '12px 16px', borderRadius: 8, background: C.accent + '15', border: '1px solid ' + C.accent + '40', color: C.accent, fontSize: 13, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 } },
      h(LIcon, { name: 'loader-2', size: 16, spin: true }),
      lastRun ? ('Run #' + lastRun.id + ' (' + lastRun.run_type + ') in progress — scanned ' + lastRun.products_scanned + ', generated ' + lastRun.generated + ', skipped ' + lastRun.skipped + ', failed ' + lastRun.failed) : 'Generation in progress…'
    ) : null,

    // Action bar
    h('div', { style: { display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' } },
      h('span', { style: { display: 'inline-flex' }, title: "Scans the whole catalog: generates a FAQ for every product missing one, and regenerates when a product's title, description, or bullets have changed. Failed products retry on later runs (up to 5 attempts). Manual FAQ edits are kept until the product content next changes. Same logic as the daily schedule." + (settings.in_stock_only ? ' Limited to in-stock products (inventory > 0) — out-of-stock items are picked up automatically on the first run after they restock.' : ' Includes all products.') },
        h(Btn, { variant: 'primary', size: 'sm', disabled: busy || running || !sfAdmin, onClick: function() { generate('new'); }, style: { background: '#95BF47' } }, h(LIcon, { name: 'sparkles', size: 14 }), sfAdmin ? ' Generate Missing FAQs' : ' Generate Missing FAQs (Admins Only)')
      ),
      status && !status.integrationEnabled ? h('span', { style: { fontSize: 12, color: C.danger, alignSelf: 'center' } }, 'Shopify integration is disabled — enable it in Integrations first') : null
    ),

    // Tabs
    h('div', { style: { display: 'flex', gap: 4, borderBottom: '1px solid ' + C.border, marginBottom: 16 } },
      (embedded ? ['products', 'runs'] : ['products', 'runs', 'settings']).map(function(id) {
        var labels = { products: 'Products', runs: 'Run History', settings: 'Settings' };
        return h('button', { key: id, onClick: function() { setTab(id); if (id === 'runs') loadRuns(); }, style: {
          background: 'none', border: 'none', borderBottom: '2px solid ' + (tab === id ? C.accent : 'transparent'),
          color: tab === id ? C.text : C.textMuted, padding: '8px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600 } }, labels[id]);
      })
    ),

    // Products tab
    tab === 'products' ? h('div', null,
      h('div', { style: { display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' } },
        ['all', 'generated', 'failed', 'pending'].map(function(f) {
          return h('button', { key: f, onClick: function() { setPFilter(f); }, style: {
            background: pFilter === f ? C.accent : 'transparent', color: pFilter === f ? '#fff' : C.textMuted,
            border: '1px solid ' + (pFilter === f ? C.accent : C.border), borderRadius: 6, padding: '5px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600, textTransform: 'capitalize' } }, f);
        }),
        h('div', { style: { flex: 1 } }),
        h('input', { placeholder: 'Search title or SKU…', value: search, onChange: function(e) { setSearch(e.target.value); }, onKeyDown: function(e) { if (e.key === 'Enter') loadProducts(); },
          style: { background: C.bg, color: C.text, border: '1px solid ' + C.border, borderRadius: 6, padding: '6px 10px', fontSize: 13, width: 220 } }),
        h(Btn, { size: 'sm', onClick: loadProducts }, 'Search')
      ),
      h(DataTable, {
        columns: [
          { key: 'sku', label: 'SKU', render: function(o) { return o.sku || '—'; }, style: { width: 150, fontFamily: 'inherit', fontSize: 12, textAlign: 'left' } },
          { key: 'title', label: 'Product', render: function(o) { var t = o.title || o.product_gid; return (t && t.length > 80) ? h('span', { style: { display: 'inline-block', whiteSpace: 'normal', overflowWrap: 'anywhere', maxWidth: 540 } }, t) : t; }, style: { fontWeight: 600, textAlign: 'left' } },
          { key: 'status', label: 'Status', render: function(o) { var pill = _sfStatusBadge(o.status); return (o.status === 'failed' && o.error) ? h('span', { title: o.error }, pill) : pill; }, style: { width: 110, textAlign: 'left' } },
          { key: 'faqs', label: 'FAQs', render: function(o) { return (o.faqs || []).length; }, style: { width: 70, textAlign: 'left' } },
          { key: 'generated_at', label: 'Generated', render: function(o) { return _sfFmtDate(o.generated_at); }, style: { width: 180, textAlign: 'left' } },
          { key: 'view', label: 'View', render: function(o) { var url = o.online_store_url || (o.handle ? 'https://www.atharvaimports.com/products/' + o.handle : null); return url ? h('a', { href: url, target: '_blank', rel: 'noopener', title: 'View on storefront', onClick: function(e) { e.stopPropagation(); }, style: { color: C.textMuted, display: 'inline-flex' } }, h(LIcon, { name: 'eye', size: 16 })) : ''; }, style: { width: 60, textAlign: 'left' } },
        ],
        data: products,
        storageKey: 'shopify-faq-products',
        pageSize: 50,
        onRowClick: function(o) { openEditor(o); },
      }),
      h('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 8 } }, pTotal + ' products tracked' + (search ? ' (filtered)' : '') + '. Click a row to view or edit its FAQ.')
    ) : null,

    // Runs tab
    tab === 'runs' ? h(DataTable, {
      columns: [
        { key: 'id', label: 'Run', render: function(o) { return '#' + o.id; }, style: { width: 60, textAlign: 'left' } },
        { key: 'run_type', label: 'Type', style: { width: 90, textAlign: 'left' } },
        { key: 'status', label: 'Status', render: function(o) { return _sfPill(o.status === 'completed' ? C.success : (o.status === 'failed' ? C.danger : C.accent), o.status); }, style: { width: 110, textAlign: 'left' } },
        { key: 'generated', label: 'Generated', style: { width: 90, textAlign: 'left' } },
        { key: 'skipped', label: 'Skipped', style: { width: 80, textAlign: 'left' } },
        { key: 'failed', label: 'Failed', style: { width: 70, textAlign: 'left' } },
        { key: 'est_cost_usd', label: 'Est. cost', render: function(o) { return '$' + Number(o.est_cost_usd || 0).toFixed(2); }, style: { width: 90, textAlign: 'left' } },
        { key: 'triggered_by', label: 'By', style: { width: 110, textAlign: 'left' } },
        { key: 'started_at', label: 'Started', render: function(o) { return _sfFmtDate(o.started_at); }, style: { textAlign: 'left' } },
      ],
      data: runs,
      storageKey: 'shopify-faq-runs',
      pageSize: 20,
    }) : null,

    // Settings tab
    tab === 'settings' ? h('div', { style: { maxWidth: 760 } },
      h('div', { style: { marginBottom: 16 } },
        h('label', { style: { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: C.text } },
          h('input', { type: 'checkbox', checked: settings.schedule_enabled, onChange: function(e) { setSettings(Object.assign({}, settings, { schedule_enabled: e.target.checked })); } }),
          'Run daily to generate FAQs for any product missing one (starts 07:00 UTC; catches up later in the day after restarts)'
        )
      ),
      h('div', { style: { marginBottom: 16 } },
        h('label', { style: { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: C.text } },
          h('input', { type: 'checkbox', checked: settings.in_stock_only, onChange: function(e) { setSettings(Object.assign({}, settings, { in_stock_only: e.target.checked })); } }),
          'Only generate FAQs for in-stock products (inventory > 0)'
        )
      ),
      h('div', { style: { marginBottom: 16 } },
        h('div', { style: { fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 } }, 'Model'),
        h('input', { value: settings.model, onChange: function(e) { setSettings(Object.assign({}, settings, { model: e.target.value })); }, style: { width: 360, background: C.bg, color: C.text, border: '1px solid ' + C.border, borderRadius: 8, padding: '8px 10px', fontSize: 13 } }),
        h('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 4 } }, 'Default: claude-haiku-4-5-20251001 (cheap, good for FAQs)')
      ),
      h('div', { style: { marginBottom: 16 } },
        h('div', { style: { fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 } }, 'Generation prompt'),
        h('textarea', { value: settings.prompt, rows: 10, onChange: function(e) { setSettings(Object.assign({}, settings, { prompt: e.target.value })); }, style: taStyle })
      ),
      h(Btn, { variant: 'primary', size: 'sm', disabled: !sfAdmin, onClick: saveSettings }, h(LIcon, { name: 'save', size: 14 }), sfAdmin ? ' Save Settings' : ' Save Settings (Admins Only)')
    ) : null,

    // Edit modal
    editing ? h(Modal, { open: true, onClose: function() { setEditing(null); }, title: 'FAQ — ' + (editing.title || '') },
      h('div', { style: { maxHeight: 'calc(60vh * var(--invzoom, 1))', overflowY: 'auto', paddingRight: 4 } },
        (editing.faqs || []).length === 0 ? h('div', { style: { fontSize: 13, color: C.textMuted, marginBottom: 12 } }, 'No FAQs yet. Regenerate or add one below.') : null,
        (editing.faqs || []).map(function(f, i) {
          return h('div', { key: i, style: { border: '1px solid ' + C.border, borderRadius: 8, padding: 12, marginBottom: 10 } },
            h('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 } },
              h('span', { style: { fontSize: 11, fontWeight: 700, color: C.textMuted } }, 'Q' + (i + 1)),
              h('button', { onClick: function() { var n = editing.faqs.slice(); n.splice(i, 1); setEditing(Object.assign({}, editing, { faqs: n })); }, style: { background: 'none', border: 'none', color: C.danger, cursor: 'pointer', fontSize: 12 } }, 'Remove')
            ),
            h('input', { value: f.question, placeholder: 'Question', onChange: function(e) { var n = editing.faqs.slice(); n[i] = Object.assign({}, n[i], { question: e.target.value }); setEditing(Object.assign({}, editing, { faqs: n })); }, style: { width: '100%', background: C.bg, color: C.text, border: '1px solid ' + C.border, borderRadius: 6, padding: '7px 9px', fontSize: 13, marginBottom: 6, boxSizing: 'border-box' } }),
            h('textarea', { value: f.answer, placeholder: 'Answer', rows: 3, onChange: function(e) { var n = editing.faqs.slice(); n[i] = Object.assign({}, n[i], { answer: e.target.value }); setEditing(Object.assign({}, editing, { faqs: n })); }, style: taStyle })
          );
        })
      ),
      h('div', { style: { display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' } },
        h(Btn, { size: 'sm', onClick: function() { setEditing(Object.assign({}, editing, { faqs: (editing.faqs || []).concat([{ question: '', answer: '' }]) })); } }, h(LIcon, { name: 'plus', size: 14 }), ' Add Q&A'),
        h(Btn, { size: 'sm', onClick: regenEditor, disabled: busy }, h(LIcon, { name: 'sparkles', size: 14 }), ' Regenerate with AI'),
        h('div', { style: { flex: 1 } }),
        h(Btn, { onClick: function() { setEditing(null); }, size: 'sm' }, 'Cancel'),
        h(Btn, { variant: 'primary', size: 'sm', onClick: saveEditor, disabled: busy, style: { background: '#95BF47' } }, h(LIcon, { name: 'save', size: 14 }), ' Save & Push')
      )
    ) : null
  );
}

window.ShopifyFaqPage = ShopifyFaqPage;

;
/* ===== app-wmca.js ===== */
// ─── Walmart Canada Dashboard (docs/13-shopify-walmart-ca.md §7) ───
// Order import pipeline UI, backed by /api/walmart-ca. Reached from the Walmart Canada
// card in Settings > Integrations (FAQ-app pattern — not a top-level nav item).
// Exports: window.ShopifyWmcaPage

var _wmH = React.createElement;

function _wmFmtDate(d) { return d ? new Date(d).toLocaleString() : '—'; }
function _wmFmtDay(d) { return d ? new Date(d).toLocaleDateString() : '—'; }

function _wmPill(col, text) {
  return _wmH('span', { style: { display: 'inline-block', background: col + '22', color: col, padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' } }, text);
}
function _wmImportBadge(s) {
  var map = { imported: C.success, preview: C.accent, pending: C.textMuted, failed: C.danger, skipped: C.warning };
  return _wmPill(map[s] || C.textMuted, s || 'pending');
}

function WmcaStatCard(props) {
  return _wmH('div', { style: { flex: 1, minWidth: 130, background: C.bgCard || C.card || '#1A1D27', border: '1px solid ' + C.border, borderRadius: 10, padding: '14px 16px' } },
    _wmH('div', { style: { fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 } }, props.label),
    _wmH('div', { style: { fontSize: 22, fontWeight: 700, color: props.color || C.text } }, props.value),
    props.sub ? _wmH('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 2 } }, props.sub) : null
  );
}

function ShopifyWmcaPage({ showToast, onNavigate, currentUser }) {
  var h = _wmH;
  var st = useState(null); var status = st[0], setStatus = st[1];
  var tb = useState('orders'); var tab = tb[0], setTab = tb[1];
  var od = useState([]); var orders = od[0], setOrders = od[1];
  var fl = useState('all'); var filter = fl[0], setFilter = fl[1];
  var ex = useState(null); var expanded = ex[0], setExpanded = ex[1];
  var bz = useState(false); var busy = bz[0], setBusy = bz[1];
  var fm = useState({ wmca_orders_enabled: false, wmca_orders_dry_run: true, wmca_tracking_enabled: false, wmca_channel_label: 'Walmart Canada',
    wmca_inventory_enabled: false, wmca_inventory_dry_run: true, wmca_inventory_buffer: 0, wmca_race_window_hours: 6, wmca_shopify_location_id: '' });
  var form = fm[0], setForm = fm[1];
  var formLoaded = useRef(false);
  var cm = useState([]); var carrierMap = cm[0], setCarrierMap = cm[1];
  var im = useState([]); var invMap = im[0], setInvMap = im[1];
  var il = useState([]); var invLog = il[0], setInvLog = il[1];
  var nc = useState({ shopify_company: '', wm_carrier_name: 'Canada Post', is_other: false });
  var newCarrier = nc[0], setNewCarrier = nc[1];

  var WM_CARRIERS = ['Canada Post', 'Purolator Inc.', 'UPS', 'FedEx', 'DHL', 'USPS', 'Canpar', 'Loomis',
    'Tforce', 'Sameday', 'Swyft', '17TRACK', 'CEVA', 'ASL', 'Globeistics Inc.',
    'SWW Ground via Purolator', 'SWW Express via FedEx'];

  function loadStatus() {
    api('/walmart-ca/status').then(function(d) {
      setStatus(d);
      if (d.settings && !formLoaded.current) {
        setForm({
          wmca_orders_enabled: !!d.settings.wmca_orders_enabled,
          wmca_orders_dry_run: d.settings.wmca_orders_dry_run !== false,
          wmca_tracking_enabled: !!d.settings.wmca_tracking_enabled,
          wmca_channel_label: d.settings.wmca_channel_label || 'Walmart Canada',
          wmca_inventory_enabled: !!d.settings.wmca_inventory_enabled,
          wmca_inventory_dry_run: d.settings.wmca_inventory_dry_run !== false,
          wmca_inventory_buffer: d.settings.wmca_inventory_buffer || 0,
          wmca_race_window_hours: d.settings.wmca_race_window_hours || 6,
          wmca_shopify_location_id: d.settings.wmca_shopify_location_id || '',
        });
        formLoaded.current = true;
      }
    }).catch(function(e) { showToast(e.message, 'error'); });
  }
  function loadCarrierMap() {
    api('/walmart-ca/carrier-map').then(function(r) { setCarrierMap(r || []); }).catch(function(e) { console.error('[silent-catch]', e); });
  }
  function loadInv() {
    api('/walmart-ca/inventory/map').then(function(r) { setInvMap(r || []); }).catch(function(e) { console.error('[silent-catch]', e); });
    api('/walmart-ca/inventory/log?limit=60').then(function(r) { setInvLog(r || []); }).catch(function(e) { console.error('[silent-catch]', e); });
  }
  function loadOrders() {
    api('/walmart-ca/orders?status=' + filter + '&limit=200')
      .then(function(r) { setOrders(r || []); })
      .catch(function(e) { console.error('[silent-catch]', e); });
  }

  useEffect(function() { loadStatus(); loadOrders(); loadCarrierMap(); loadInv(); }, []);
  useEffect(function() { if (tab === 'inventory') loadInv(); }, [tab]);
  useEffect(function() { loadOrders(); }, [filter]);
  useEffect(function() {
    var iv = setInterval(function() { loadStatus(); loadOrders(); }, 15000);
    return function() { clearInterval(iv); };
  }, [filter]);

  var syncNow = function() {
    setBusy(true);
    api('/walmart-ca/sync-now', { method: 'POST' }).then(function(r) {
      setBusy(false);
      if (r.error) return showToast(r.error, 'error');
      if (r.skipped) return showToast('Sync skipped: ' + r.skipped, 'error');
      showToast('Sync done — ' + (r.fetched || 0) + ' fetched, ' + (r.previewed || 0) + ' previewed, ' + (r.imported || 0) + ' imported, ' + (r.failed || 0) + ' failed');
      loadStatus(); loadOrders();
    }).catch(function(e) { setBusy(false); showToast(e.message, 'error'); });
  };

  var retryOrder = function(id) {
    api('/walmart-ca/orders/' + id + '/retry', { method: 'POST' }).then(function() {
      showToast('Queued for retry on the next sync cycle');
      loadOrders(); loadStatus();
    }).catch(function(e) { showToast(e.message, 'error'); });
  };

  var retryLinePush = function(lineId) {
    api('/walmart-ca/lines/' + lineId + '/retry-push', { method: 'POST' }).then(function() {
      showToast('Line queued — tracking push retries on the next cycle');
      loadOrders();
    }).catch(function(e) { showToast(e.message, 'error'); });
  };

  var pushTrackingNow = function() {
    setBusy(true);
    api('/walmart-ca/tracking-now', { method: 'POST' }).then(function(r) {
      setBusy(false);
      if (r.error) return showToast(r.error, 'error');
      if (r.skipped) return showToast('Tracking push skipped: ' + r.skipped, 'error');
      showToast('Tracking — ' + (r.checked || 0) + ' orders checked, ' + (r.pushed || 0) + ' pushed, ' + (r.corrected || 0) + ' corrected, ' + (r.unmapped || 0) + ' unmapped, ' + (r.failed || 0) + ' failed');
      loadStatus(); loadOrders();
    }).catch(function(e) { setBusy(false); showToast(e.message, 'error'); });
  };

  var addCarrierMapping = function() {
    if (!newCarrier.shopify_company.trim()) return showToast('Enter the Shopify carrier name', 'error');
    api('/walmart-ca/carrier-map', { method: 'POST', body: JSON.stringify(newCarrier) }).then(function() {
      showToast('Carrier mapping saved');
      setNewCarrier({ shopify_company: '', wm_carrier_name: 'Canada Post', is_other: false });
      loadCarrierMap();
    }).catch(function(e) { showToast(e.message, 'error'); });
  };

  var deleteCarrierMapping = async function(id) {
    if (!(await appConfirm('Delete this carrier mapping?', { danger: true, okLabel: 'Delete' }))) return;
    api('/walmart-ca/carrier-map/' + id, { method: 'DELETE' }).then(function() { loadCarrierMap(); })
      .catch(function(e) { showToast(e.message, 'error'); });
  };

  var seedInvMap = function() {
    setBusy(true);
    api('/walmart-ca/inventory/seed', { method: 'POST' }).then(function(r) {
      setBusy(false);
      showToast('Seeded — ' + (r.wmItems || 0) + ' WM items, ' + (r.added || 0) + ' new (' + (r.matched || 0) + ' matched, ' + (r.unlisted || 0) + ' need mapping)');
      loadInv();
    }).catch(function(e) { setBusy(false); showToast(e.message, 'error'); });
  };

  var invPushNow = function() {
    setBusy(true);
    api('/walmart-ca/inventory/push-now', { method: 'POST' }).then(function(r) {
      setBusy(false);
      if (r.error) return showToast(r.error, 'error');
      if (r.skipped) return showToast('Skipped: ' + r.skipped, 'error');
      showToast('Sweep — ' + (r.checked || 0) + ' checked, ' + (r.pushed || 0) + ' pushed, ' + (r.wouldPush || 0) + ' would push, ' + (r.failed || 0) + ' failed');
      loadInv(); loadStatus();
    }).catch(function(e) { setBusy(false); showToast(e.message, 'error'); });
  };

  var setInvRow = function(id, patch) {
    api('/walmart-ca/inventory/map/' + id, { method: 'PUT', body: JSON.stringify(patch) }).then(function() { loadInv(); })
      .catch(function(e) { showToast(e.message, 'error'); });
  };

  var mapInvRow = async function(row) {
    var sku = await appPrompt('Shopify SKU to map "' + row.wm_sku + '" to:', row.shopify_sku || row.wm_sku);
    if (!sku) return;
    setInvRow(row.id, { shopify_sku: sku.trim() });
  };

  var saveSettings = async function() {
    var st = (status && status.settings) || {};
    var goingLive = !form.wmca_orders_dry_run && st.wmca_orders_dry_run !== false;
    if (goingLive && !(await appConfirm('Turn OFF order dry-run? Preview orders will be CREATED in Shopify and ACKed on Walmart on the next sync cycle.', { danger: true, okLabel: 'Turn off' }))) return;
    var invGoingLive = !form.wmca_inventory_dry_run && st.wmca_inventory_dry_run !== false;
    if (invGoingLive && !(await appConfirm('Turn OFF inventory dry-run? Quantities will be PUSHED to Walmart Canada on the next cycle.', { danger: true, okLabel: 'Turn off' }))) return;
    api('/walmart-ca/settings', { method: 'PUT', body: JSON.stringify(form) }).then(function() {
      showToast('Settings saved');
      formLoaded.current = false;
      loadStatus();
    }).catch(function(e) { showToast(e.message, 'error'); });
  };

  var counts = (status && status.counts) || {};
  var lastRun = (status && status.lastRun) || {};
  var lastTrack = (status && status.lastTrackingRun) || {};
  var lastInv = (status && status.lastInvRun) || {};
  var settings = (status && status.settings) || {};
  var mode = !settings.wmca_orders_enabled ? { col: C.textDim, label: 'Disabled' }
    : settings.wmca_orders_dry_run !== false ? { col: C.accent, label: 'Dry-run (preview only)' }
    : { col: C.success, label: 'LIVE' };

  var attention = orders.filter(function(o) {
    if (o.import_status === 'failed' || o.import_status === 'skipped') return true;
    if (o.import_status === 'imported' && !o.ack_at) return true;
    return (o.lines || []).some(function(l) { return l.ship_push_status === 'unmapped_carrier' || l.ship_push_status === 'failed'; });
  });

  function ordersTable(rows, showRetry) {
    if (!rows.length) return h('div', { style: { color: C.textMuted, fontSize: 13, padding: 24, textAlign: 'center' } }, 'No orders here.');
    return h('div', { style: { overflowX: 'auto' } },
      h('table', { style: { width: '100%', borderCollapse: 'collapse', fontSize: 12.5 } },
        h('thead', null, h('tr', null,
          ['WM PO#', 'Customer Order#', 'Date', 'Ship By', 'Customer', 'CAD', 'USD', 'WM Status', 'Import', 'Shopify', ''].map(function(hd, i) {
            return h('th', { key: i, style: { textAlign: 'left', padding: '8px 10px', color: C.textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid ' + C.border } }, hd);
          })
        )),
        h('tbody', null, rows.map(function(o) {
          var isOpen = expanded === o.id;
          var rowEls = [h('tr', { key: o.id, onClick: function() { setExpanded(isOpen ? null : o.id); }, style: { cursor: 'pointer', background: isOpen ? (C.bgAlt || 'transparent') : 'transparent' } },
            h('td', { style: { padding: '8px 10px', borderBottom: '1px solid ' + C.border, fontFamily: 'inherit', fontSize: 12 } }, o.purchase_order_id),
            h('td', { style: { padding: '8px 10px', borderBottom: '1px solid ' + C.border, fontFamily: 'inherit', fontSize: 12 } }, o.customer_order_id || '—'),
            h('td', { style: { padding: '8px 10px', borderBottom: '1px solid ' + C.border } }, _wmFmtDay(o.order_date)),
            h('td', { style: { padding: '8px 10px', borderBottom: '1px solid ' + C.border } }, _wmFmtDay(o.ship_by)),
            h('td', { style: { padding: '8px 10px', borderBottom: '1px solid ' + C.border } }, o.customer_name || '—'),
            h('td', { style: { padding: '8px 10px', borderBottom: '1px solid ' + C.border } }, o.cad_total != null ? ('$' + Number(o.cad_total).toFixed(2)) : '—'),
            h('td', { style: { padding: '8px 10px', borderBottom: '1px solid ' + C.border } }, o.usd_total != null ? ('$' + Number(o.usd_total).toFixed(2)) : '—'),
            h('td', { style: { padding: '8px 10px', borderBottom: '1px solid ' + C.border } }, o.wm_status || '—'),
            h('td', { style: { padding: '8px 10px', borderBottom: '1px solid ' + C.border } }, _wmImportBadge(o.import_status)),
            h('td', { style: { padding: '8px 10px', borderBottom: '1px solid ' + C.border } },
              o.shopify_order_name ? h('span', { style: { color: C.success, fontWeight: 600 } }, o.shopify_order_name) : '—',
              o.import_status === 'imported' ? h('span', { style: { color: o.ack_at ? C.textMuted : C.warning, fontSize: 11, marginLeft: 6 } }, o.ack_at ? 'ACKed' : 'ACK pending') : null),
            h('td', { style: { padding: '8px 10px', borderBottom: '1px solid ' + C.border } },
              showRetry && (o.import_status === 'failed' || o.import_status === 'skipped')
                ? h(Btn, { size: 'sm', onClick: function(e) { e.stopPropagation(); retryOrder(o.id); } }, 'Retry') : null)
          )];
          if (isOpen) {
            rowEls.push(h('tr', { key: o.id + '-detail' },
              h('td', { colSpan: 11, style: { padding: '10px 16px', borderBottom: '1px solid ' + C.border, background: C.bgAlt || 'transparent' } },
                o.error ? h('div', { style: { color: C.danger, fontSize: 12, marginBottom: 8 } }, 'Error: ' + o.error) : null,
                h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 6 } },
                  'Ship to: ' + [((o.ship_to || {}).address1), ((o.ship_to || {}).city), ((o.ship_to || {}).province), ((o.ship_to || {}).postalCode)].filter(Boolean).join(', ')
                  + (o.customer_email ? ' · ' + o.customer_email : '')
                  + (o.fx_rate ? ' · FX ' + Number(o.fx_rate).toFixed(5) : '')),
                (o.lines || []).map(function(l) {
                  return h('div', { key: l.id, style: { fontSize: 12.5, padding: '4px 0', borderTop: '1px dotted ' + C.border, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' } },
                    h('span', null,
                      h('span', { style: { fontFamily: 'inherit' } }, l.wm_sku), ' × ' + l.qty + ' — ' + (l.product_name || ''),
                      h('span', { style: { color: C.textMuted } },
                        '  CAD $' + Number(l.cad_item_price || 0).toFixed(2) + ' + ship $' + Number(l.cad_shipping || 0).toFixed(2)
                        + (l.usd_item_price != null ? ('  →  USD $' + Number(l.usd_item_price).toFixed(2) + ' + ship $' + Number(l.usd_shipping || 0).toFixed(2)) : '')
                        + '  [' + (l.line_status || '?') + ']')
                    ),
                    l.ship_push_status === 'pushed' ? h('span', { style: { color: C.success, fontSize: 11, fontWeight: 600 } },
                      '✓ ' + (l.carrier_used || '') + ' ' + (l.tracking_number || '') + ' pushed to WM') : null,
                    l.ship_push_status === 'unmapped_carrier' ? _wmPill(C.warning, 'unmapped carrier') : null,
                    l.ship_push_status === 'failed' ? _wmPill(C.danger, 'push failed') : null,
                    (l.ship_push_status === 'unmapped_carrier' || l.ship_push_status === 'failed')
                      ? h(Btn, { size: 'sm', onClick: function(e) { e.stopPropagation(); retryLinePush(l.id); } }, 'Retry push') : null,
                    l.error && l.ship_push_status ? h('span', { style: { color: C.danger, fontSize: 11 } }, l.error) : null
                  );
                })
              )));
          }
          return rowEls;
        }))
      ));
  }

  return h('div', { style: { maxWidth: 1200, margin: '0 auto', padding: 24 } },
    // Header
    h('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 10 } },
      h('div', null,
        h('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
          h('h1', { style: { fontSize: 22, fontWeight: 700, color: C.text, margin: 0 } }, 'Walmart Canada'),
          _wmPill(mode.col, mode.label)
        ),
        h('div', { style: { fontSize: 12.5, color: C.textMuted, marginTop: 4 } }, 'Order import → Shopify (Atharva Brands). Inventory + tracking phases arrive next.')
      ),
      h('div', { style: { display: 'flex', gap: 8 } },
        h(Btn, { size: 'sm', onClick: function() { onNavigate('settings-integrations'); } }, h(LIcon, { name: 'arrow-left', size: 14 }), ' Integrations'),
        h(Btn, { size: 'sm', onClick: pushTrackingNow, disabled: busy }, h(LIcon, { name: 'truck', size: 14 }), ' Push Tracking'),
        h(Btn, { variant: 'primary', size: 'sm', onClick: syncNow, disabled: busy }, busy ? [h(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'i' }), ' Syncing...'] : [h(LIcon, { name: 'refresh-cw', size: 14, key: 'i' }), ' Sync Now'])
      )
    ),
    // Stats
    h('div', { style: { display: 'flex', gap: 12, marginBottom: 18, flexWrap: 'wrap' } },
      _wmH(WmcaStatCard, { label: 'Preview', value: counts.preview || 0, color: C.accent }),
      _wmH(WmcaStatCard, { label: 'Pending', value: counts.pending || 0 }),
      _wmH(WmcaStatCard, { label: 'Imported', value: counts.imported || 0, color: C.success }),
      _wmH(WmcaStatCard, { label: 'Failed', value: counts.failed || 0, color: counts.failed ? C.danger : undefined }),
      _wmH(WmcaStatCard, { label: 'Last Sync', value: lastRun.at ? new Date(lastRun.at).toLocaleTimeString() : '—', sub: lastRun.ok === false ? ('failed: ' + (lastRun.error || '')) : (lastRun.at ? 'ok' : 'not yet run'), color: lastRun.ok === false ? C.danger : undefined }),
      _wmH(WmcaStatCard, { label: 'Tracking', value: settings.wmca_tracking_enabled ? ((lastTrack.pushed || 0) + (lastTrack.corrected || 0)) : 'OFF',
        sub: !settings.wmca_tracking_enabled ? 'enable in Settings' : (lastTrack.at ? ('last run ' + new Date(lastTrack.at).toLocaleTimeString() + (lastTrack.ok === false ? ' — failed' : '')) : 'not yet run'),
        color: lastTrack.ok === false ? C.danger : (settings.wmca_tracking_enabled ? C.success : undefined) }),
      _wmH(WmcaStatCard, { label: 'Inventory', value: !settings.wmca_inventory_enabled ? 'OFF' : (settings.wmca_inventory_dry_run !== false ? 'DRY-RUN' : (lastInv.pushed || 0)),
        sub: !settings.wmca_inventory_enabled ? 'enable in Settings' : (lastInv.at ? ((lastInv.mode || '') + ' ' + new Date(lastInv.at).toLocaleTimeString() + (lastInv.ok === false ? ' — failed' : '')) : 'not yet run'),
        color: lastInv.ok === false ? C.danger : (settings.wmca_inventory_enabled && settings.wmca_inventory_dry_run === false ? C.success : (settings.wmca_inventory_enabled ? C.accent : undefined)) })
    ),
    // Tabs
    h('div', { style: { display: 'flex', gap: 4, borderBottom: '1px solid ' + C.border, marginBottom: 16 } },
      [['orders', 'Orders'], ['attention', 'Attention' + (attention.length ? ' (' + attention.length + ')' : '')], ['inventory', 'Inventory'], ['settings', 'Settings']].map(function(t) {
        var active = tab === t[0];
        return h('button', { key: t[0], onClick: function() { setTab(t[0]); },
          style: { background: 'none', border: 'none', borderBottom: active ? '2px solid ' + C.accent : '2px solid transparent', color: active ? C.text : C.textMuted, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' } }, t[1]);
      })
    ),
    // Orders tab
    tab === 'orders' && h('div', null,
      h('div', { style: { display: 'flex', gap: 6, marginBottom: 12 } },
        ['all', 'preview', 'pending', 'imported', 'failed', 'skipped'].map(function(f) {
          var active = filter === f;
          return h('button', { key: f, onClick: function() { setFilter(f); },
            style: { background: active ? C.accent + '22' : 'transparent', border: '1px solid ' + (active ? C.accent : C.border), color: active ? C.accent : C.textMuted, padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' } }, f);
        })
      ),
      settings.wmca_orders_dry_run !== false && settings.wmca_orders_enabled ? h('div', { style: { padding: '10px 14px', borderRadius: 8, marginBottom: 12, background: C.accent + '15', border: '1px solid ' + C.accent + '40', color: C.accent, fontSize: 13 } },
        'Dry-run mode: rows below marked "preview" show exactly what would be created in Shopify. Review prices, address, and SKUs — then flip live in Settings.') : null,
      ordersTable(orders, false)
    ),
    // Attention tab
    tab === 'attention' && h('div', null,
      attention.length ? null : h('div', { style: { color: C.textMuted, fontSize: 13, padding: 24, textAlign: 'center' } }, 'Nothing needs attention.'),
      attention.length ? ordersTable(attention, true) : null
    ),
    // Inventory tab
    tab === 'inventory' && h('div', null,
      h('div', { style: { display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' } },
        h(Btn, { size: 'sm', onClick: seedInvMap, disabled: busy }, h(LIcon, { name: 'download', size: 14 }), ' Seed from Walmart items'),
        h(Btn, { size: 'sm', onClick: invPushNow, disabled: busy }, h(LIcon, { name: 'upload', size: 14 }), settings.wmca_inventory_dry_run !== false ? ' Preview All (dry-run)' : ' Push All Now'),
        settings.wmca_inventory_enabled && settings.wmca_inventory_dry_run !== false
          ? h('span', { style: { fontSize: 12, color: C.accent, fontWeight: 600 } }, 'DRY-RUN — quantities are logged below, nothing is sent to Walmart') : null,
        !settings.wmca_inventory_enabled ? h('span', { style: { fontSize: 12, color: C.textMuted } }, 'Inventory sync is OFF — enable it in Settings') : null
      ),
      h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 10, lineHeight: 1.5 } },
        'Only SKUs that already exist on Walmart Canada are ever pushed (the map is seeded from Walmart\\u2019s own item list — listings are never created). ',
        'Pushed qty = Shopify available \\u2212 units from orders imported in the last ' + (settings.wmca_race_window_hours || 6) + 'h \\u2212 buffer (' + (settings.wmca_inventory_buffer || 0) + ').'),
      // SKU map
      h('table', { style: { width: '100%', borderCollapse: 'collapse', fontSize: 12.5, marginBottom: 18 } },
        h('thead', null, h('tr', null, ['WM CA SKU', 'Shopify SKU', 'Status', 'Source', 'Last pushed', 'At', ''].map(function(hd, i) {
          return h('th', { key: i, style: { textAlign: 'left', padding: '7px 9px', color: C.textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', borderBottom: '1px solid ' + C.border } }, hd);
        }))),
        h('tbody', null, invMap.map(function(m) {
          var stCol = m.status === 'active' ? C.success : m.status === 'excluded' ? C.textDim : C.warning;
          return h('tr', { key: m.id },
            h('td', { style: { padding: '6px 9px', borderBottom: '1px solid ' + C.border, fontFamily: 'inherit', fontSize: 12 } }, m.wm_sku),
            h('td', { style: { padding: '6px 9px', borderBottom: '1px solid ' + C.border, fontFamily: 'inherit', fontSize: 12 } }, m.shopify_sku || h('span', { style: { color: C.warning } }, '— not matched —')),
            h('td', { style: { padding: '6px 9px', borderBottom: '1px solid ' + C.border } }, _wmPill(stCol, m.status)),
            h('td', { style: { padding: '6px 9px', borderBottom: '1px solid ' + C.border, color: C.textMuted } }, m.source || ''),
            h('td', { style: { padding: '6px 9px', borderBottom: '1px solid ' + C.border } }, m.last_pushed_qty != null ? m.last_pushed_qty : '—'),
            h('td', { style: { padding: '6px 9px', borderBottom: '1px solid ' + C.border, color: C.textMuted, fontSize: 11.5 } }, m.last_pushed_at ? new Date(m.last_pushed_at).toLocaleString() : '—'),
            h('td', { style: { padding: '6px 9px', borderBottom: '1px solid ' + C.border, whiteSpace: 'nowrap' } },
              h('button', { onClick: function() { mapInvRow(m); }, style: { background: 'none', border: 'none', color: C.accent, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit', marginRight: 8 } }, 'map'),
              m.status !== 'excluded'
                ? h('button', { onClick: function() { setInvRow(m.id, { status: 'excluded' }); }, style: { background: 'none', border: 'none', color: C.textMuted, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' } }, 'exclude')
                : h('button', { onClick: function() { setInvRow(m.id, { status: m.shopify_sku ? 'active' : 'unlisted' }); }, style: { background: 'none', border: 'none', color: C.success, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' } }, 'include'))
          );
        }))
      ),
      // Activity log
      h('div', { style: { fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 8 } }, 'Recent activity'),
      h('div', { style: { overflowX: 'auto' } },
        h('table', { style: { width: '100%', borderCollapse: 'collapse', fontSize: 12 } },
          h('thead', null, h('tr', null, ['Time', 'WM SKU', 'Available', 'Guard', 'Buffer', 'Qty', 'Result'].map(function(hd, i) {
            return h('th', { key: i, style: { textAlign: 'left', padding: '6px 9px', color: C.textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', borderBottom: '1px solid ' + C.border } }, hd);
          }))),
          h('tbody', null, invLog.map(function(l) {
            var col = l.status === 'pushed' ? C.success : l.status === 'would_push' ? C.accent : C.danger;
            return h('tr', { key: l.id },
              h('td', { style: { padding: '5px 9px', borderBottom: '1px solid ' + C.border, color: C.textMuted, fontSize: 11.5, whiteSpace: 'nowrap' } }, new Date(l.created_at).toLocaleTimeString()),
              h('td', { style: { padding: '5px 9px', borderBottom: '1px solid ' + C.border, fontFamily: 'inherit', fontSize: 11.5 } }, l.wm_sku),
              h('td', { style: { padding: '5px 9px', borderBottom: '1px solid ' + C.border } }, l.available != null ? l.available : '—'),
              h('td', { style: { padding: '5px 9px', borderBottom: '1px solid ' + C.border } }, l.guard_units || 0),
              h('td', { style: { padding: '5px 9px', borderBottom: '1px solid ' + C.border } }, l.buffer_units || 0),
              h('td', { style: { padding: '5px 9px', borderBottom: '1px solid ' + C.border, fontWeight: 700 } }, l.pushed_qty != null ? l.pushed_qty : '—'),
              h('td', { style: { padding: '5px 9px', borderBottom: '1px solid ' + C.border } }, _wmPill(col, l.status), l.error ? h('span', { style: { color: C.danger, fontSize: 11, marginLeft: 6 } }, l.error) : null)
            );
          }))
        ))
    ),
    // Settings tab
    tab === 'settings' && h('div', { style: { maxWidth: 560 } },
      h('div', { style: { fontSize: 12.5, color: C.textMuted, marginBottom: 16, lineHeight: 1.6 } },
        'The order sync runs every 60 seconds when enabled. In dry-run it stages previews only — nothing touches Shopify or Walmart. ',
        'Going live creates the Shopify order (no stock claimed, no customer emails) and then acknowledges it on Walmart.'),
      h('label', { style: { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: C.text, marginBottom: 10 } },
        h('input', { type: 'checkbox', checked: form.wmca_orders_enabled, onChange: function(e) { setForm(Object.assign({}, form, { wmca_orders_enabled: e.target.checked })); } }),
        'Enable order sync'),
      h('label', { style: { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: C.text, marginBottom: 10 } },
        h('input', { type: 'checkbox', checked: form.wmca_orders_dry_run, onChange: function(e) { setForm(Object.assign({}, form, { wmca_orders_dry_run: e.target.checked })); } }),
        'Dry-run (preview only — uncheck to go LIVE)'),
      h('label', { style: { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: C.text, marginBottom: 14 } },
        h('input', { type: 'checkbox', checked: form.wmca_tracking_enabled, onChange: function(e) { setForm(Object.assign({}, form, { wmca_tracking_enabled: e.target.checked })); } }),
        'Tracking export (5-min poll: Shopify fulfillments → Walmart ship confirmation)'),
      h('div', { style: { marginBottom: 14, maxWidth: 320 } },
        _wmH(Inp, { label: 'Channel label (order notes2)', value: form.wmca_channel_label, onChange: function(e) { setForm(Object.assign({}, form, { wmca_channel_label: e.target.value })); } })),
      // Inventory export settings
      h('div', { style: { borderTop: '1px solid ' + C.border, paddingTop: 14, marginBottom: 14 } },
        h('div', { style: { fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 8 } }, 'Inventory export'),
        h('label', { style: { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: C.text, marginBottom: 10 } },
          h('input', { type: 'checkbox', checked: form.wmca_inventory_enabled, onChange: function(e) { setForm(Object.assign({}, form, { wmca_inventory_enabled: e.target.checked })); } }),
          'Enable inventory sync (60s delta + nightly reconcile)'),
        h('label', { style: { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: C.text, marginBottom: 12 } },
          h('input', { type: 'checkbox', checked: form.wmca_inventory_dry_run, onChange: function(e) { setForm(Object.assign({}, form, { wmca_inventory_dry_run: e.target.checked })); } }),
          'Dry-run (log quantities only — uncheck to push to Walmart)'),
        h('div', { style: { display: 'flex', gap: 12, marginBottom: 4, maxWidth: 480 } },
          _wmH(Inp, { label: 'Safety buffer (units)', type: 'number', value: String(form.wmca_inventory_buffer), style: { width: 120 }, onChange: function(e) { setForm(Object.assign({}, form, { wmca_inventory_buffer: e.target.value })); } }),
          _wmH(Inp, { label: 'Race window (hours)', type: 'number', value: String(form.wmca_race_window_hours), style: { width: 120 }, onChange: function(e) { setForm(Object.assign({}, form, { wmca_race_window_hours: e.target.value })); } }),
          _wmH(Inp, { label: 'Shopify location ID', value: form.wmca_shopify_location_id, style: { width: 280 }, onChange: function(e) { setForm(Object.assign({}, form, { wmca_shopify_location_id: e.target.value })); } })
        )
      ),
      h(Btn, { variant: 'primary', size: 'sm', onClick: saveSettings }, h(LIcon, { name: 'save', size: 14 }), ' Save Settings'),

      // ─── Carrier map editor ───
      h('div', { style: { marginTop: 28, borderTop: '1px solid ' + C.border, paddingTop: 18 } },
        h('div', { style: { fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 6 } }, 'Carrier map'),
        h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12, lineHeight: 1.5 } },
          'Shopify fulfillment carrier → Walmart CA carrier name. Matching ignores case and punctuation. Unmapped carriers park the shipment in Attention (and email you) instead of guessing. "Other" carriers send the tracking URL instead of a carrier code.'),
        h('table', { style: { width: '100%', maxWidth: 640, borderCollapse: 'collapse', fontSize: 12.5, marginBottom: 12 } },
          h('thead', null, h('tr', null, ['Shopify carrier (normalized)', 'Walmart CA carrier', 'Other?', ''].map(function(hd, i) {
            return h('th', { key: i, style: { textAlign: 'left', padding: '6px 8px', color: C.textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', borderBottom: '1px solid ' + C.border } }, hd);
          }))),
          h('tbody', null, carrierMap.map(function(m) {
            return h('tr', { key: m.id },
              h('td', { style: { padding: '5px 8px', borderBottom: '1px solid ' + C.border, fontFamily: 'inherit', fontSize: 12 } }, m.shopify_company_norm),
              h('td', { style: { padding: '5px 8px', borderBottom: '1px solid ' + C.border } }, m.wm_carrier_name),
              h('td', { style: { padding: '5px 8px', borderBottom: '1px solid ' + C.border } }, m.is_other ? 'yes' : ''),
              h('td', { style: { padding: '5px 8px', borderBottom: '1px solid ' + C.border } },
                h('button', { onClick: function() { deleteCarrierMapping(m.id); }, style: { background: 'none', border: 'none', color: C.danger, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' } }, 'delete'))
            );
          }))
        ),
        h('div', { style: { display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' } },
          h('div', { style: { width: 200 } }, _wmH(Inp, { label: 'Shopify carrier name', placeholder: 'e.g. Stallion Express', value: newCarrier.shopify_company, onChange: function(e) { setNewCarrier(Object.assign({}, newCarrier, { shopify_company: e.target.value })); } })),
          h('div', null,
            h('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 } }, 'WM CA carrier'),
            newCarrier.is_other
              ? _wmH(Inp, { placeholder: 'Carrier display name', value: newCarrier.wm_carrier_name === 'Canada Post' ? '' : newCarrier.wm_carrier_name, onChange: function(e) { setNewCarrier(Object.assign({}, newCarrier, { wm_carrier_name: e.target.value })); } })
              : h('select', { value: newCarrier.wm_carrier_name, onChange: function(e) { setNewCarrier(Object.assign({}, newCarrier, { wm_carrier_name: e.target.value })); },
                  style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit' } },
                  WM_CARRIERS.map(function(c) { return h('option', { key: c, value: c }, c); }))),
          h('label', { style: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.text, cursor: 'pointer', paddingBottom: 8 } },
            h('input', { type: 'checkbox', checked: newCarrier.is_other, onChange: function(e) { setNewCarrier(Object.assign({}, newCarrier, { is_other: e.target.checked })); } }), 'Other (tracking URL)'),
          h(Btn, { size: 'sm', onClick: addCarrierMapping }, h(LIcon, { name: 'plus', size: 14 }), ' Add mapping')
        )
      )
    )
  );
}

window.ShopifyWmcaPage = ShopifyWmcaPage;

;
/* ===== app-shein.js ===== */
// ─── SHEIN Dashboard (docs/25-shopify-shein.md §7) ───
// Order pipeline UI for the Shopify ↔ SHEIN sync, backed by /api/shein. Reached from the SHEIN
// card in Settings > Integrations. Every tab has its own URL: /shopify-shein, /shopify-shein/attention,
// /shopify-shein/listings, /shopify-shein/sku-map, /shopify-shein/inventory, /shopify-shein/settings;
// the Orders filter rides in ?status=, the Listings, SKU Map and Inventory views in ?show=.
// Exports: window.ShopifySheinPage

var _shH = React.createElement;

var SHEIN_STATUS_NAMES = { 1: 'Pending', 2: 'To Be Shipped', 3: 'Pending SHEIN Shipment', 4: 'Shipped', 5: 'Delivered',
  6: 'Refunded', 7: 'To Be Picked Up', 8: 'Reported Damaged', 9: 'Rejected' };
var SHEIN_TABS = [['orders', 'Orders'], ['attention', 'Attention'], ['listings', 'Listings'], ['sku-map', 'SKU Map'], ['inventory', 'Inventory'], ['settings', 'Settings']];
// Create Listings: the status chips (in_review = queued, publishing or waiting for SHEIN's review)
var SHEIN_LISTING_FILTERS = [['ready', 'Ready'], ['blocked', 'Blocked'], ['in_review', 'Sent To SHEIN'], ['approved', 'Approved'],
  ['rejected', 'Not Approved'], ['failed', 'Failed'], ['skipped', 'Skipped'], ['new', 'Checking'], ['all', 'All']];
var SHEIN_LISTING_STATUS = { new: ['Checking', 'textMuted'], ready: ['Ready', 'accent'], blocked: ['Blocked', 'warning'], queued: ['Queued', 'accent'],
  publishing: ['Publishing', 'accent'], submitted: ['In SHEIN Review', 'accent'], approved: ['Approved', 'success'], rejected: ['Not Approved', 'danger'],
  failed: ['Failed', 'danger'], skipped: ['Skipped', 'textMuted'] };
var SHEIN_SKU_FILTERS = [['unmatched', 'Not Matched'], ['manual', 'Matched By Hand'], ['all', 'All']];
// the inventory log: the newest row of each SKU, or every row
var SHEIN_INV_VIEWS = [['latest', 'Latest Per SKU'], ['differs', 'Differs From SHEIN'], ['failed', 'Failed'], ['all', 'Everything']];
var SHEIN_FILTERS = [['all', 'All'], ['preview', 'Would Import'], ['pending', 'Waiting'], ['external', 'Released Elsewhere'],
  ['imported', 'Imported'], ['failed', 'Failed'], ['skipped', 'Skipped'],
  // the dry-run check against the Shopify order the current connector created
  ['differs', 'Differs from CedCommerce'], ['missing', 'Not in Shopify'],
  // orders the tracking job staged or sent
  ['tracking', 'Tracking']];
var SHEIN_COMPARE = { match: 'Match', differs: 'Differs', missing: 'Not in Shopify' };
var SHEIN_DECISIONS = { preview: 'Would Import', pending: 'Waiting', external: 'Released Elsewhere', importing: 'Importing',
  imported: 'Imported', adopted: 'Adopted', failed: 'Failed', skipped: 'Skipped' };

function _shFmtDateTime(d) { return d ? new Date(d).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'; }
function _shMoney(v) { return v == null ? '—' : '$' + Number(v).toFixed(2); }

function _shPill(col, text) {
  return _shH('span', { style: { display: 'inline-block', background: col + '22', color: col, padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' } }, text);
}
function _shDecisionBadge(s) {
  var map = { preview: C.accent, pending: C.textMuted, external: C.textMuted, importing: C.accent, imported: C.success, adopted: C.success, failed: C.danger, skipped: C.warning };
  return _shPill(map[s] || C.textMuted, SHEIN_DECISIONS[s] || s || 'Waiting');
}

function _shCompareBadge(s) {
  if (!s) return _shH('span', { style: { color: C.textDim } }, '—');
  var map = { match: C.success, differs: C.danger, missing: C.warning };
  return _shPill(map[s] || C.textMuted, SHEIN_COMPARE[s] || s);
}

// The deadline that matters now: release while the order is still Pending on SHEIN, carrier scan
// after. SHEIN does not always send a release deadline; the ship-by date stands in for it.
// cancel_by is when SHEIN cancels the order by itself if it has not shipped by then.
function _shDeadline(o) {
  if ([4, 5, 6, 8, 9].indexOf(Number(o.shein_status)) !== -1) return null;
  var release = Number(o.shein_status) === 1 && o.release_by;
  var at = release ? o.release_by : o.ship_by;
  if (!at && !o.cancel_by) return null;
  var hours = at ? (new Date(at).getTime() - Date.now()) / 36e5 : null;
  var left = o.cancel_by ? (new Date(o.cancel_by).getTime() - Date.now()) / 36e5 : null;
  return {
    label: at ? (release ? 'Release by ' : 'Ship by ') + _shFmtDateTime(at) : null,
    color: hours == null ? C.textMuted : hours < 0 ? C.danger : hours < 12 ? C.warning : C.textMuted,
    cancel: o.cancel_by ? 'SHEIN cancels ' + _shFmtDateTime(o.cancel_by) : null,
    cancelColor: left == null ? C.textMuted : left < 24 ? C.danger : left < 72 ? C.warning : C.textMuted,
  };
}

// Units of one order → one line per SKU
function _shLines(units) {
  var bySku = {}, order = [];
  (units || []).forEach(function(u) {
    var key = (u.seller_sku || u.sku_code || '?') + '|' + Number(u.price || 0).toFixed(2) + '|' + Number(u.discount || 0).toFixed(2) + '|' + u.unit_status + '|' + u.exchange_tag;
    if (!bySku[key]) { bySku[key] = { key: key, sku: u.seller_sku || null, sheinSku: u.sku_code || null, title: u.title || '', qty: 0, price: Number(u.price || 0), discount: Number(u.discount || 0), status: u.unit_status, exchangeTag: u.exchange_tag, units: [] }; order.push(key); }
    bySku[key].qty++;
    bySku[key].units.push(u);
  });
  return order.map(function(k) { return bySku[k]; });
}

// The tracking of one line (its units share a fulfillment as a rule): what this connector sent
// or would send, what SHEIN holds for the unit, and the units that wait on a carrier mapping.
function _shTracking(units, onRetry) {
  var h = _shH;
  var out = [];
  var seen = {};
  (units || []).forEach(function(u) {
    var key = (u.ship_push_status || '') + '|' + (u.tracking_number || '') + '|' + (u.shein_waybill || '');
    if (!u.ship_push_status && !u.shein_waybill) return;
    if (seen[key]) return;
    seen[key] = true;
    var n = units.filter(function(x) { return ((x.ship_push_status || '') + '|' + (x.tracking_number || '') + '|' + (x.shein_waybill || '')) === key; }).length;
    var many = n > 1 ? ' (' + n + ' units)' : '';
    if (u.ship_push_status === 'pushed') {
      out.push(h('span', { key: key, style: { color: C.success, fontSize: 11, fontWeight: 600 } }, '✓ ' + (u.carrier_used || '') + ' ' + (u.tracking_number || '') + ' sent to SHEIN' + many));
    } else if (u.ship_push_status === 'preview') {
      var same = u.shein_waybill && u.shein_waybill === u.tracking_number && String(u.shein_carrier || '').toLowerCase() === String(u.carrier_used || '').toLowerCase();
      out.push(h('span', { key: key, style: { color: C.accent, fontSize: 11, fontWeight: 600 } }, 'Would send ' + (u.carrier_used || '') + ' ' + (u.tracking_number || '') + many));
      out.push(h('span', { key: key + '-shein', style: { color: !u.shein_waybill ? C.textMuted : same ? C.success : C.danger, fontSize: 11 } },
        !u.shein_waybill ? 'SHEIN has no tracking yet' : same ? 'same as SHEIN has' : 'SHEIN has ' + (u.shein_carrier || '') + ' ' + u.shein_waybill));
    } else if (u.ship_push_status === 'unmapped_carrier') {
      out.push(_shPill(C.warning, 'Unmapped Carrier: ' + (u.shopify_carrier || '(blank)')));
      if (onRetry) out.push(h('button', { key: key + '-retry', onClick: function(e) { e.stopPropagation(); onRetry(u); }, style: { background: 'none', border: '1px solid ' + C.border, borderRadius: 5, color: C.accent, cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: 'inherit', padding: '1px 8px' } }, 'Retry'));
    } else if (u.ship_push_status === 'failed') {
      out.push(_shPill(C.danger, 'Tracking Failed'));
      out.push(h('span', { key: key + '-why', style: { color: C.danger, fontSize: 11 } }, u.error || ''));
      if (onRetry) out.push(h('button', { key: key + '-retry', onClick: function(e) { e.stopPropagation(); onRetry(u); }, style: { background: 'none', border: '1px solid ' + C.border, borderRadius: 5, color: C.accent, cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: 'inherit', padding: '1px 8px' } }, 'Retry'));
    } else if (u.shein_waybill) {
      out.push(h('span', { key: key, style: { color: C.textMuted, fontSize: 11 } }, 'SHEIN has ' + (u.shein_carrier || '') + ' ' + u.shein_waybill + many));
    }
  });
  return out.length ? h('span', { style: { display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' } }, out) : null;
}

function SheinStatCard(props) {
  return _shH('div', { style: { flex: 1, minWidth: 130, background: C.bgCard || C.card || C.surface, border: '1px solid ' + C.border, borderRadius: 10, padding: '14px 16px' } },
    _shH('div', { style: { fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 } }, props.label),
    _shH('div', { style: { fontSize: 22, fontWeight: 700, color: props.color || C.text } }, props.value),
    props.sub ? _shH('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 2 } }, props.sub) : null
  );
}

// ─── Listings tab: create products on SHEIN from a listing workbook (Anthony 2026-10-13) ───
// Import → every SKU is checked (nothing sent) → a person ticks Ready rows and publishes them →
// the job publishes 3 a minute and follows SHEIN's review. Only our SKU identifies a listing on
// SHEIN; no barcode is sent.
function SheinListingsTab(props) {
  var h = _shH;
  var showToast = props.showToast, isAdmin = props.isAdmin;
  var dt = useState(null); var data = dt[0], setData = dt[1];
  var fs = useState(function() {
    var q = new URLSearchParams(window.location.search).get('show');
    return SHEIN_LISTING_FILTERS.some(function(f) { return f[0] === q; }) ? q : 'ready';
  });
  var filter = fs[0], setFilterState = fs[1];
  var qs = useState(''); var q = qs[0], setQ = qs[1];
  var rs = useState(''); var reason = rs[0], setReason = rs[1];
  var os = useState(0); var offset = os[0], setOffset = os[1];
  var ss = useState({}); var selected = ss[0], setSelected = ss[1];
  var qt = useState(null); var quota = qt[0], setQuota = qt[1];
  var bz = useState(false); var busy = bz[0], setBusy = bz[1];
  var pv = useState(null); var preview = pv[0], setPreview = pv[1];
  var fileRef = useRef(null);
  var PAGE = 200;

  function setFilter(f) {
    setFilterState(f); setOffset(0); setReason(''); setSelected({});
    try { history.replaceState(history.state, '', pageToPath('shopify-shein', 'listings') + (f === 'ready' ? '' : '?show=' + encodeURIComponent(f))); } catch (e) { /* ignore */ }
  }
  function load() {
    var url = '/shein/listings?status=' + encodeURIComponent(filter) + '&limit=' + PAGE + '&offset=' + offset +
      (q.trim() ? '&q=' + encodeURIComponent(q.trim()) : '') + (reason ? '&reason=' + encodeURIComponent(reason) : '');
    api(url).then(function(r) { setData(r); }).catch(function(e) { showToast(e.message, 'error'); });
  }
  function loadQuota(fresh) {
    api('/shein/listings/quota' + (fresh ? '?fresh=1' : '')).then(function(r) { setQuota(r); }).catch(function(e) { console.error('[silent-catch]', e); });
  }
  useEffect(function() { load(); }, [filter, offset, reason]);
  useEffect(function() { var t = setTimeout(load, 350); return function() { clearTimeout(t); }; }, [q]);
  useEffect(function() { loadQuota(false); }, []);
  useEffect(function() {
    var tick = setInterval(function() { load(); }, 15000);
    return function() { clearInterval(tick); };
  }, [filter, offset, reason, q]);

  var summary = (data && data.summary) || {};
  var byStatus = summary.byStatus || {};
  var rows = (data && data.rows) || null;
  var total = (data && data.total) || 0;
  var lastRun = summary.lastRun || null;
  var picked = Object.keys(selected).filter(function(k) { return selected[k]; }).map(Number);
  var readyOnPage = (rows || []).filter(function(r) { return r.status === 'ready'; });

  function countOf(f) {
    if (f === 'all') return Object.keys(byStatus).reduce(function(a, k) { return a + byStatus[k]; }, 0);
    if (f === 'in_review') return (byStatus.queued || 0) + (byStatus.publishing || 0) + (byStatus.submitted || 0);
    return byStatus[f] || 0;
  }

  var upload = function(e) {
    var file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    apiUpload('/shein/listings/import', file).then(function(r) {
      setBusy(false);
      if (r.error) return showToast(r.error, 'error');
      showToast(file.name + ': ' + r.rows + ' SKUs read, ' + r.added + ' new, ' + r.refreshed + ' to check again, ' + r.kept + ' already sent to SHEIN (kept)' + (r.warnings && r.warnings.length ? '. ' + r.warnings.length + ' warning(s)' : '') + '. Checking now.');
      setFilter('new'); load();
    }).catch(function(err) { setBusy(false); showToast(err.message, 'error'); });
  };
  var checkAgain = function() {
    api('/shein/listings/check', { method: 'POST', body: JSON.stringify({}) }).then(function(r) {
      showToast(r.reset + ' listings will be checked again (those already sent to SHEIN are kept)');
      load();
    }).catch(function(e) { showToast(e.message, 'error'); });
  };
  var runNow = function() {
    api('/shein/listings/run', { method: 'POST' }).then(function() { showToast('Running: checks, publishing and SHEIN review'); setTimeout(load, 4000); })
      .catch(function(e) { showToast(e.message, 'error'); });
  };
  var publish = async function() {
    if (!picked.length) return;
    // counted over every ticked row, whichever page or search it was ticked on (each tick keeps its row's
    // quota flag; the old count read only the rows on screen and said 2/3 for a real 1/4 on 2026-10-14)
    var exempt = picked.filter(function(id) { return selected[id] && selected[id].exempt; }).length;
    var shelfLeft = quota && quota.shelf && quota.shelf.need ? quota.shelf.remain_count : null;
    var msg = 'Create ' + picked.length + ' listing' + (picked.length === 1 ? '' : 's') + ' on SHEIN?\n\n' +
      'Each one is sent with its pictures, price and the Shopify quantity at that moment, then waits for SHEIN’s review. ' +
      (picked.length - exempt) + ' use the on-shelf quota' + (shelfLeft != null ? ' (' + shelfLeft + ' left)' : '') + ', ' + exempt + ' are from brands that do not. ' +
      'They go 3 a minute.';
    if (!(await appConfirm(msg, { title: 'Publish On SHEIN', okLabel: 'Publish ' + picked.length, danger: true }))) return;
    api('/shein/listings/publish', { method: 'POST', body: JSON.stringify({ ids: picked }) }).then(function(r) {
      showToast(r.queued + ' queued for SHEIN');
      setSelected({}); load();
    }).catch(function(e) { showToast(e.message, 'error'); });
  };
  var skip = function(r, undo) {
    api('/shein/listings/' + r.id + '/skip', { method: 'POST', body: JSON.stringify({ undo: !!undo }) }).then(function() { load(); })
      .catch(function(e) { showToast(e.message, 'error'); });
  };
  var retry = function(r) {
    api('/shein/listings/' + r.id + '/retry', { method: 'POST' }).then(function(x) {
      showToast(x.alreadyOnShein ? r.sku + ' is already on SHEIN (an earlier attempt created it)' : r.sku + ' will be checked again');
      load();
    }).catch(function(e) { showToast(e.message, 'error'); });
  };
  var openPreview = function(r) {
    api('/shein/listings/' + r.id + '/payload').then(function(p) { setPreview({ row: r, data: p }); }).catch(function(e) { showToast(e.message, 'error'); });
  };

  var tdStyle = { padding: '8px 10px', borderBottom: '1px solid ' + C.border, verticalAlign: 'top', fontSize: 12.5 };
  var linkStyle = { background: 'none', border: 'none', color: C.accent, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', padding: 0, marginRight: 10 };
  var chip = function(active, label, onClick, key) {
    return h('button', { key: key, onClick: onClick,
      style: { background: active ? C.accent + '22' : 'transparent', border: '1px solid ' + (active ? C.accent : C.border), color: active ? C.accent : C.textMuted, padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' } }, label);
  };
  function statusCell(r) {
    var st = SHEIN_LISTING_STATUS[r.status] || [r.status, 'textMuted'];
    var lines = [];
    (r.reasons || []).forEach(function(x, i) { if (r.status === 'blocked') lines.push(h('div', { key: 'r' + i, style: { color: C.warning, fontSize: 11.5 } }, x)); });
    if (r.error && (r.status === 'failed' || r.status === 'publishing')) lines.push(h('div', { key: 'e', style: { color: C.danger, fontSize: 11.5 } }, r.error));
    if (r.review_note && ['submitted', 'approved', 'rejected'].indexOf(r.status) !== -1) lines.push(h('div', { key: 'n', style: { color: r.status === 'rejected' ? C.danger : C.textMuted, fontSize: 11.5 } }, r.review_note));
    if (r.spu_name) lines.push(h('div', { key: 's', style: { color: C.textMuted, fontSize: 11 } }, 'SPU ', h(CopyCell, { value: r.spu_name }, r.spu_name)));
    (r.warnings || []).forEach(function(x, i) { if (['ready', 'blocked'].indexOf(r.status) !== -1) lines.push(h('div', { key: 'w' + i, style: { color: C.textMuted, fontSize: 11 } }, '• ' + x)); });
    return h('td', { style: Object.assign({}, tdStyle, { minWidth: 220 }) }, _shPill(C[st[1]] || C.textMuted, st[0]), lines);
  }

  function table() {
    if (rows == null) return h('div', { style: { color: C.textMuted, fontSize: 13, padding: 24, textAlign: 'center' } }, 'Loading…');
    if (!rows.length) {
      return h('div', { style: { color: C.textMuted, fontSize: 13, padding: 24, textAlign: 'center' } },
        countOf('all') ? 'No listings here.' : 'No workbook imported yet. Use "Import Workbook" to load one.');
    }
    var allPicked = readyOnPage.length && readyOnPage.every(function(r) { return selected[r.id]; });
    var heads = [
      isAdmin && filter === 'ready' ? h('input', { type: 'checkbox', checked: !!allPicked, onChange: function(e) {
        var next = Object.assign({}, selected); readyOnPage.forEach(function(r) { next[r.id] = e.target.checked ? { exempt: !!r.quota_exempt } : false; }); setSelected(next);
      } }) : '',
      'SKU', 'Product', 'SHEIN Category', 'Scent / Gender / Size', 'Price', 'Stock', 'Pictures', 'Status', ''];
    return h('div', { style: { overflowX: 'auto' } },
      h('table', { style: { width: '100%', borderCollapse: 'collapse' } },
        h('thead', null, h('tr', null, heads.map(function(hd, i) {
          return h('th', { key: i, style: { textAlign: 'left', padding: '8px 10px', color: C.textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid ' + C.border, whiteSpace: 'nowrap' } }, hd);
        }))),
        h('tbody', null, rows.map(function(r) {
          var priceOff = r.file_price != null && r.shopify_price != null && Number(r.shopify_price) > 0 && Math.abs(Number(r.file_price) - Number(r.shopify_price)) / Number(r.shopify_price) > 0.25;
          return h('tr', { key: r.id },
            h('td', { style: tdStyle }, isAdmin && r.status === 'ready' ? h('input', { type: 'checkbox', checked: !!selected[r.id], onChange: function(e) { var n = Object.assign({}, selected); n[r.id] = e.target.checked ? { exempt: !!r.quota_exempt } : false; setSelected(n); } }) : null),
            h('td', { style: Object.assign({}, tdStyle, { whiteSpace: 'nowrap' }) }, h(CopyCell, { value: r.sku }, h('span', { style: { fontWeight: 600, color: C.text } }, r.sku))),
            h('td', { style: Object.assign({}, tdStyle, { maxWidth: 360 }) },
              h('div', { style: { color: C.text } }, r.file_title || '—'),
              h('div', { style: { fontSize: 11.5, color: C.textMuted, marginTop: 2 } }, (r.brand || 'no brand') + (r.quota_exempt ? ' · does not use the on-shelf quota' : ''))),
            h('td', { style: Object.assign({}, tdStyle, { color: C.textMuted, fontSize: 11.5, maxWidth: 200 }) }, r.category_path ? r.category_path.split(' > ').slice(-1)[0] : '—'),
            h('td', { style: Object.assign({}, tdStyle, { color: C.textMuted, fontSize: 11.5, whiteSpace: 'nowrap' }) },
              [r.scent_type, r.gender, r.volume_ml ? r.volume_ml + ' ml' : null, r.pack > 1 ? 'pack of ' + r.pack : null].filter(Boolean).join(' · ') || '—'),
            h('td', { style: Object.assign({}, tdStyle, { whiteSpace: 'nowrap' }) },
              h('div', { style: { color: C.text, fontWeight: 600 } }, _shMoney(r.file_price)),
              r.shopify_price != null ? h('div', { style: { fontSize: 11, color: priceOff ? C.warning : C.textMuted } }, 'Shopify ' + _shMoney(r.shopify_price)) : null),
            h('td', { style: tdStyle }, r.shopify_available == null ? '—' : r.shopify_available),
            h('td', { style: tdStyle }, r.image_count),
            statusCell(r),
            h('td', { style: Object.assign({}, tdStyle, { whiteSpace: 'nowrap' }) },
              h('button', { onClick: function() { openPreview(r); }, style: linkStyle }, r.status === 'submitted' || r.status === 'approved' || r.status === 'rejected' ? 'What Was Sent' : 'Preview'),
              isAdmin && ['ready', 'blocked', 'new'].indexOf(r.status) !== -1 ? h('button', { onClick: function() { skip(r); }, style: Object.assign({}, linkStyle, { color: C.textMuted }) }, 'Skip') : null,
              isAdmin && r.status === 'skipped' ? h('button', { onClick: function() { skip(r, true); }, style: linkStyle }, 'Bring Back') : null,
              isAdmin && r.status === 'failed' ? h('button', { onClick: function() { retry(r); }, style: linkStyle }, 'Retry') : null)
          );
        }))
      ));
  }

  var pub = (quota && quota.publish) || {};
  var shelf = (quota && quota.shelf) || {};
  var readyQuota = summary.readyQuota || {};
  return h('div', null,
    h('div', { style: { fontSize: 12.5, color: C.textMuted, marginBottom: 12, lineHeight: 1.6 } },
      'Import a listing workbook (Master tab + Image 2…8 tabs + Sheet2 descriptions). Every SKU is checked without sending anything: already on SHEIN, brand authorized on the store, in the Shopify store with stock, SHEIN category and required attributes, pictures, price and package. ',
      'Tick Ready rows and publish them: each goes with its pictures (put on a white square), the file price and the Shopify quantity, identified by our SKU only (no barcode), then waits for SHEIN’s review. Approved listings reach the SKU Map by themselves and the inventory sync takes over.'),
    h('div', { style: { display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' } },
      isAdmin ? h('input', { type: 'file', accept: '.xlsx,.xlsm,.xls', ref: fileRef, style: { display: 'none' }, onChange: upload }) : null,
      isAdmin ? h(Btn, { size: 'sm', onClick: function() { if (fileRef.current) fileRef.current.click(); }, disabled: busy }, busy ? [h(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'i' }), ' Importing...'] : [h(LIcon, { name: 'upload', size: 14, key: 'i' }), ' Import Workbook']) : null,
      isAdmin ? h(Btn, { size: 'sm', onClick: checkAgain }, h(LIcon, { name: 'refresh-cw', size: 14 }), ' Check Again') : null,
      isAdmin ? h(Btn, { size: 'sm', onClick: runNow }, h(LIcon, { name: 'play', size: 14 }), ' Run Now') : null,
      isAdmin && filter === 'ready' ? h(Btn, { variant: 'primary', size: 'sm', onClick: publish, disabled: !picked.length }, h(LIcon, { name: 'send', size: 14 }), ' Publish Selected (' + picked.length + ')') : null,
      h('span', { style: { fontSize: 12, color: C.textMuted } },
        quota ? ('On-shelf quota: ' + (shelf.need ? (shelf.remain_count + ' left of ' + shelf.total_quota_count) : 'not limited') +
          ' · publishing this month: ' + (pub.isControlled ? (pub.availableQuota + ' left of ' + pub.totalQuota) : 'not limited') + ' · ') : '',
        'Ready: ' + (readyQuota.counted || 0) + ' use the on-shelf quota, ' + (readyQuota.exempt || 0) + ' do not',
        isAdmin ? h('button', { onClick: function() { loadQuota(true); }, style: Object.assign({}, linkStyle, { marginLeft: 10 }) }, 'Refresh Quota') : null)),
    lastRun ? h('div', { style: { fontSize: 11.5, color: (lastRun.checksError || lastRun.publishError || lastRun.reviewError) ? C.danger : C.textMuted, marginBottom: 10 } },
      'Last run ' + _shFmtDateTime(lastRun.at) +
      (lastRun.checks ? ' · checked ' + lastRun.checks.checked + ' (' + lastRun.checks.ready + ' ready, ' + lastRun.checks.blocked + ' blocked)' : '') +
      (lastRun.publish ? ' · sent ' + lastRun.publish.submitted + ', failed ' + lastRun.publish.failed + (lastRun.publish.waitingForQuota ? ', ' + lastRun.publish.waitingForQuota + ' waiting for quota' : '') : '') +
      (lastRun.review ? ' · review: ' + lastRun.review.approved + ' approved, ' + lastRun.review.rejected + ' not approved, ' + lastRun.review.waiting + ' waiting' : '') +
      (lastRun.checksError ? ' · check failed: ' + lastRun.checksError : '') + (lastRun.publishError ? ' · publish failed: ' + lastRun.publishError : '') + (lastRun.reviewError ? ' · review failed: ' + lastRun.reviewError : '')) : null,
    h('div', { style: { display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' } },
      SHEIN_LISTING_FILTERS.map(function(f) { return chip(filter === f[0], f[1] + ' (' + countOf(f[0]) + ')', function() { setFilter(f[0]); }, f[0]); })),
    filter === 'blocked' && (summary.reasons || []).length ? h('div', { style: { display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' } },
      h('span', { style: { fontSize: 11, color: C.textMuted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' } }, 'Why'),
      chip(!reason, 'All Reasons', function() { setReason(''); setOffset(0); }, '_all'),
      summary.reasons.map(function(x) { return chip(reason === x.reason, x.reason + ' (' + x.n + ')', function() { setReason(x.reason); setOffset(0); }, x.reason); })) : null,
    h('div', { style: { display: 'flex', gap: 10, marginBottom: 10, alignItems: 'center', flexWrap: 'wrap' } },
      h('input', { value: q, placeholder: 'Search SKU, title or brand', onChange: function(e) { setQ(e.target.value); setOffset(0); },
        style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '7px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', minWidth: 280 } }),
      h('span', { style: { fontSize: 12, color: C.textMuted } }, total ? ((offset + 1) + '–' + Math.min(offset + PAGE, total) + ' of ' + total) : ''),
      offset > 0 ? h('button', { onClick: function() { setOffset(Math.max(0, offset - PAGE)); }, style: linkStyle }, 'Previous') : null,
      offset + PAGE < total ? h('button', { onClick: function() { setOffset(offset + PAGE); }, style: linkStyle }, 'Next') : null),
    table(),
    preview ? h(Modal, { open: true, onClose: function() { setPreview(null); }, title: (preview.data.sent ? 'Sent To SHEIN: ' : 'Would Send: ') + preview.row.sku, width: 860 },
      h('div', { style: { fontSize: 12.5, color: C.textMuted, marginBottom: 10, lineHeight: 1.6 } },
        preview.data.sent ? 'The body of the publish call as it was sent (pictures are SHEIN addresses).'
          : 'The body of the publish call this listing would send. Pictures show the file’s addresses here; at publish time each is put on a white square and uploaded to SHEIN first. The quantity is read from Shopify at that moment.'),
      preview.data.error ? h('div', { style: { color: C.danger, fontSize: 12.5, marginBottom: 8 } }, preview.data.error) : null,
      (preview.data.reasons || []).length ? h('div', { style: { color: C.warning, fontSize: 12.5, marginBottom: 8 } }, 'Blocked: ' + preview.data.reasons.join(' · ')) : null,
      (preview.data.warnings || []).length ? h('div', { style: { color: C.textMuted, fontSize: 12.5, marginBottom: 8 } }, 'Notes: ' + preview.data.warnings.join(' · ')) : null,
      preview.data.plan && preview.data.plan.product_attributes ? h('div', { style: { fontSize: 12.5, color: C.text, marginBottom: 8, lineHeight: 1.6 } },
        'Attributes: ' + preview.data.plan.product_attributes.map(function(a) { return a.name + ' = ' + a.value; }).concat(
          (preview.data.plan.size_attributes || []).map(function(a) { return a.name + ' = ' + a.attribute_extra_value; }),
          preview.data.plan.sale_attribute ? [preview.data.plan.sale_attribute.name + ' = ' + preview.data.plan.sale_attribute.value + ' (main option)'] : []).join(' · ')) : null,
      h('pre', { style: { background: C.bg, border: '1px solid ' + C.border, borderRadius: 6, padding: 12, fontSize: 11.5, color: C.text, maxHeight: 460, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' } },
        preview.data.body ? JSON.stringify(preview.data.body, null, 2) : '—')) : null
  );
}

function ShopifySheinPage({ showToast, onNavigate, currentUser, routeArg }) {
  var h = _shH;
  var tab = SHEIN_TABS.some(function(t) { return t[0] === routeArg; }) ? routeArg : 'orders';
  var isAdmin = currentUser ? currentUser.role === 'admin' : true;

  var st = useState(null); var status = st[0], setStatus = st[1];
  var od = useState(null); var orders = od[0], setOrders = od[1];
  var at = useState([]); var attention = at[0], setAttention = at[1];
  var fl = useState(function() {
    var q = new URLSearchParams(window.location.search).get('status');
    return SHEIN_FILTERS.some(function(f) { return f[0] === q; }) ? q : 'all';
  });
  var filter = fl[0], setFilterState = fl[1];
  var ex = useState(null); var expanded = ex[0], setExpanded = ex[1];
  var bz = useState(false); var busy = bz[0], setBusy = bz[1];
  var fm = useState({ shein_orders_enabled: false, shein_tracking_enabled: false, shein_inventory_enabled: false, shein_inventory_buffer: 0, shein_race_window_hours: 6, shein_shopify_location_id: '', shein_warehouse_code: '' });
  var form = fm[0], setForm = fm[1];
  var iv = useState(null); var invLog = iv[0], setInvLog = iv[1];
  var ivv = useState(function() {
    var q = new URLSearchParams(window.location.search).get('show');
    return SHEIN_INV_VIEWS.some(function(f) { return f[0] === q; }) ? q : 'latest';
  });
  var invView = ivv[0], setInvViewState = ivv[1];
  var wh = useState({ warehouses: [], at: null }); var warehouses = wh[0], setWarehouses = wh[1];
  var lc = useState([]); var locations = lc[0], setLocations = lc[1];
  var formLoaded = useRef(false);
  var cm = useState([]); var carrierMap = cm[0], setCarrierMap = cm[1];
  var cl = useState({ carriers: { channels: [], platform: [] }, at: null }); var carriers = cl[0], setCarriers = cl[1];
  var nc = useState({ shopify_company: '', shein_carrier_name: '' }); var newCarrier = nc[0], setNewCarrier = nc[1];
  var sk = useState(null); var skuData = sk[0], setSkuData = sk[1];
  var sf = useState(function() {
    var q = new URLSearchParams(window.location.search).get('show');
    return SHEIN_SKU_FILTERS.some(function(f) { return f[0] === q; }) ? q : 'unmatched';
  });
  var skuFilter = sf[0], setSkuFilterState = sf[1];

  function setFilter(f) {
    setFilterState(f);
    // the filter is part of the address, so a refresh or a shared link lands on the same list
    try { history.replaceState(history.state, '', pageToPath('shopify-shein') + (f === 'all' ? '' : '?status=' + encodeURIComponent(f))); } catch (e) { /* ignore */ }
  }
  function setSkuFilter(f) {
    setSkuFilterState(f);
    try { history.replaceState(history.state, '', pageToPath('shopify-shein', 'sku-map') + (f === 'unmatched' ? '' : '?show=' + encodeURIComponent(f))); } catch (e) { /* ignore */ }
  }
  function setInvView(f) {
    setInvViewState(f);
    try { history.replaceState(history.state, '', pageToPath('shopify-shein', 'inventory') + (f === 'latest' ? '' : '?show=' + encodeURIComponent(f))); } catch (e) { /* ignore */ }
  }

  function loadStatus() {
    api('/shein/status').then(function(d) {
      setStatus(d);
      if (d.settings && !formLoaded.current) {
        setForm({ shein_orders_enabled: !!d.settings.shein_orders_enabled, shein_tracking_enabled: !!d.settings.shein_tracking_enabled,
          shein_orders_dry_run: d.settings.shein_orders_dry_run !== false, shein_inventory_dry_run: d.settings.shein_inventory_dry_run !== false,
          shein_inventory_enabled: !!d.settings.shein_inventory_enabled, shein_inventory_buffer: d.settings.shein_inventory_buffer || 0,
          shein_race_window_hours: d.settings.shein_race_window_hours == null ? 6 : d.settings.shein_race_window_hours,
          shein_shopify_location_id: d.settings.shein_shopify_location_id || '', shein_warehouse_code: d.settings.shein_warehouse_code || '' });
        formLoaded.current = true;
      }
    }).catch(function(e) { showToast(e.message, 'error'); });
  }
  function loadCarriers() {
    api('/shein/carrier-map').then(function(r) { setCarrierMap(r || []); }).catch(function(e) { console.error('[silent-catch]', e); });
    api('/shein/carriers').then(function(r) { setCarriers(r || { carriers: { channels: [], platform: [] }, at: null }); }).catch(function(e) { console.error('[silent-catch]', e); });
  }
  function loadInventorySettings() {
    api('/shein/warehouses').then(function(r) { setWarehouses(r || { warehouses: [], at: null }); }).catch(function(e) { console.error('[silent-catch]', e); });
    api('/shein/locations').then(function(r) { setLocations(r || []); }).catch(function(e) { console.error('[silent-catch]', e); });
  }
  function loadInventory() {
    api('/shein/inventory/log?view=' + encodeURIComponent(invView) + '&limit=300')
      .then(function(r) { setInvLog(r || []); })
      .catch(function(e) { console.error('[silent-catch]', e); });
  }
  function loadOrders() {
    api('/shein/orders?status=' + encodeURIComponent(filter) + '&limit=200')
      .then(function(r) { setOrders(r || []); })
      .catch(function(e) { console.error('[silent-catch]', e); });
  }
  function loadAttention() {
    api('/shein/orders?status=attention&limit=200')
      .then(function(r) { setAttention(r || []); })
      .catch(function(e) { console.error('[silent-catch]', e); });
  }

  function loadSkuMap() {
    api('/shein/sku-map?filter=' + encodeURIComponent(skuFilter))
      .then(function(r) { setSkuData(r || { rows: [], summary: {} }); })
      .catch(function(e) { console.error('[silent-catch]', e); });
  }

  useEffect(function() { loadStatus(); loadAttention(); }, []);
  useEffect(function() { loadOrders(); }, [filter]);
  useEffect(function() { if (tab === 'sku-map') loadSkuMap(); }, [tab, skuFilter]);
  useEffect(function() { if (tab === 'inventory') loadInventory(); }, [tab, invView]);
  useEffect(function() { if (tab === 'settings') { loadCarriers(); loadInventorySettings(); } }, [tab]);

  var inventoryNow = function() {
    setBusy(true);
    api('/shein/inventory-now', { method: 'POST' }).then(function(r) {
      setBusy(false);
      if (r.error) return showToast(r.error, 'error');
      if (r.skipped) return showToast('Inventory skipped: ' + (r.skipped === 'disabled' ? 'inventory sync is off, or the store is not authorized' : r.skipped), 'error');
      showToast('Inventory (' + (r.mode || 'delta') + '): ' + (r.checked || 0) + ' SKUs looked at, ' + (r.wouldPush || 0) + ' would send, ' + (r.pushed || 0) + ' sent, ' + (r.agree || 0) + ' same as SHEIN, ' + (r.differ || 0) + ' differ, ' + (r.failed || 0) + ' failed');
      loadStatus(); loadInventory();
    }).catch(function(e) { setBusy(false); showToast(e.message, 'error'); });
  };
  var sweepNow = async function() {
    var live = settings.shein_inventory_dry_run === false && status && !status.liveImportLocked;
    if (live && !(await appConfirm('Send the quantity of every matched listing to SHEIN now?', { title: 'Push All Now', okLabel: 'Push All', danger: true }))) return;
    setBusy(true);
    api('/shein/inventory/sweep-now', { method: 'POST' }).then(function(r) {
      setBusy(false);
      if (r.error) return showToast(r.error, 'error');
      if (r.skipped) return showToast('Sweep skipped: ' + (r.skipped === 'disabled' ? 'inventory sync is off, or the store is not authorized' : r.skipped), 'error');
      showToast('Sweep: ' + (r.checked || 0) + ' SKUs looked at, ' + (r.wouldPush || 0) + ' would send, ' + (r.pushed || 0) + ' sent, ' + (r.agree || 0) + ' same as SHEIN, ' + (r.differ || 0) + ' differ, ' + (r.failed || 0) + ' failed' + (r.cutOff ? '; ' + r.cutOff + ' left for the next run' : ''));
      loadStatus(); loadInventory(); loadSkuMap();
    }).catch(function(e) { setBusy(false); showToast(e.message, 'error'); });
  };
  var catalogNow = function() {
    setBusy(true);
    api('/shein/catalog/refresh', { method: 'POST' }).then(function(r) {
      setBusy(false);
      if (r.error) return showToast(r.error, 'error');
      showToast('Catalog read: ' + (r.listings || 0) + ' listings, ' + (r.added || 0) + ' new, ' + (r.named || 0) + ' seller SKUs filled in' + (r.spus ? ' (' + r.spus + ' products read; the rest follows a run at a time)' : ''));
      loadStatus(); loadSkuMap();
    }).catch(function(e) { setBusy(false); showToast(e.message, 'error'); });
  };
  var refreshWarehouses = function() {
    api('/shein/warehouses/refresh', { method: 'POST' }).then(function(r) {
      showToast('SHEIN lists ' + ((r.warehouses || []).length) + ' warehouse(s) for the store');
      loadInventorySettings();
    }).catch(function(e) { showToast(e.message, 'error'); });
  };

  var addCarrierMapping = function() {
    api('/shein/carrier-map', { method: 'POST', body: JSON.stringify(newCarrier) }).then(function(r) {
      showToast('Carrier mapping saved' + (r.requeued ? '; ' + r.requeued + ' units go back to the tracking job' : ''));
      setNewCarrier({ shopify_company: '', shein_carrier_name: '' });
      loadCarriers(); loadAttention();
    }).catch(function(e) { showToast(e.message, 'error'); });
  };
  var deleteCarrierMapping = async function(m) {
    if (!(await appConfirm('Remove the mapping of "' + m.shopify_company_norm + '" to "' + m.shein_carrier_name + '"?', { title: 'Remove Mapping', danger: true, okLabel: 'Remove' }))) return;
    api('/shein/carrier-map/' + m.id, { method: 'DELETE' }).then(function() { loadCarriers(); }).catch(function(e) { showToast(e.message, 'error'); });
  };
  var refreshCarriers = function() {
    api('/shein/carriers/refresh', { method: 'POST' }).then(function(r) {
      showToast('SHEIN lists ' + ((r.carriers && r.carriers.channels) || []).length + ' carriers for the store');
      loadCarriers();
    }).catch(function(e) { showToast(e.message, 'error'); });
  };
  var trackingNow = function() {
    setBusy(true);
    api('/shein/tracking-now', { method: 'POST' }).then(function(r) {
      setBusy(false);
      if (r.error) return showToast(r.error, 'error');
      if (r.skipped) return showToast('Tracking skipped: ' + (r.skipped === 'disabled' ? 'tracking is off, or the store is not authorized' : r.skipped), 'error');
      showToast('Tracking (' + (r.mode || 'dry-run') + '): ' + (r.checked || 0) + ' orders looked at, ' + (r.staged || 0) + ' units staged, ' + (r.pushed || 0) + ' sent, ' + (r.unmapped || 0) + ' with no carrier mapping');
      loadStatus(); loadOrders(); loadAttention();
    }).catch(function(e) { setBusy(false); showToast(e.message, 'error'); });
  };
  var retryPush = function(u) {
    api('/shein/units/' + u.id + '/retry-push', { method: 'POST' }).then(function() { showToast('Back in the tracking job'); loadOrders(); loadAttention(); })
      .catch(function(e) { showToast(e.message, 'error'); });
  };
  useEffect(function() {
    var tick = setInterval(function() { loadStatus(); loadOrders(); loadAttention(); if (tab === 'sku-map') loadSkuMap(); if (tab === 'inventory') loadInventory(); }, 20000);
    return function() { clearInterval(tick); };
  }, [filter, tab, skuFilter, invView]);

  // A match is made by a person: the Shopify SKU is typed (the hint is offered as a start) and
  // the server refuses one that no Shopify product carries.
  var matchSku = async function(row) {
    var start = row.shopify_sku || (row.suggestion && row.suggestion.sku) || '';
    var sku = await appPrompt('Shopify SKU that the SHEIN SKU "' + (row.seller_sku || row.shein_sku_code) + '" stands for:', start, { title: 'Match SKU', okLabel: 'Match' });
    if (!sku || !String(sku).trim()) return;
    api('/shein/sku-map/' + row.id, { method: 'PUT', body: JSON.stringify({ shopify_sku: String(sku).trim() }) }).then(function(r) {
      showToast((row.seller_sku || row.shein_sku_code) + ' matched to ' + r.shopifySku + (r.recompare ? '. ' + r.recompare + ' orders will be compared again.' : ''));
      loadSkuMap(); loadStatus();
    }).catch(function(e) { showToast(e.message, 'error'); });
  };
  var retryOrder = function(o) {
    api('/shein/orders/' + o.id + '/retry', { method: 'POST' }).then(function() {
      showToast(o.order_no + ' is back in the import queue');
      loadStatus(); loadOrders(); loadAttention();
    }).catch(function(e) { showToast(e.message, 'error'); });
  };
  var unmatchSku = async function(row) {
    if (!(await appConfirm('Remove the match of "' + (row.seller_sku || row.shein_sku_code) + '" with "' + row.shopify_sku + '"?', { title: 'Remove Match', danger: true, okLabel: 'Remove' }))) return;
    api('/shein/sku-map/' + row.id, { method: 'PUT', body: JSON.stringify({ shopify_sku: '' }) }).then(function() {
      showToast('Match removed');
      loadSkuMap(); loadStatus();
    }).catch(function(e) { showToast(e.message, 'error'); });
  };

  var syncNow = function() {
    setBusy(true);
    api('/shein/sync-now', { method: 'POST' }).then(function(r) {
      setBusy(false);
      if (r.error) return showToast(r.error, 'error');
      if (r.skipped) return showToast('Sync skipped: ' + (r.skipped === 'disabled' ? 'the order sync is off, or the store is not authorized' : r.skipped), 'error');
      showToast((r.caughtUp === false ? 'Catching up, more on the next run: ' : 'Sync done: ') + (r.listed || 0) + ' listed, ' + (r.fetched || 0) + ' read, ' + (r.previewed || 0) + ' would import, ' + (r.waiting || 0) + ' waiting, ' + (r.external || 0) + ' released elsewhere');
      loadStatus(); loadOrders(); loadAttention();
    }).catch(function(e) { setBusy(false); showToast(e.message, 'error'); });
  };

  var saveSettings = function() {
    api('/shein/settings', { method: 'PUT', body: JSON.stringify({ shein_orders_enabled: form.shein_orders_enabled, shein_tracking_enabled: form.shein_tracking_enabled,
      shein_orders_dry_run: form.shein_orders_dry_run !== false, shein_inventory_dry_run: form.shein_inventory_dry_run !== false,
      shein_inventory_enabled: form.shein_inventory_enabled, shein_inventory_buffer: form.shein_inventory_buffer, shein_race_window_hours: form.shein_race_window_hours,
      shein_shopify_location_id: form.shein_shopify_location_id, shein_warehouse_code: form.shein_warehouse_code }) }).then(function() {
      showToast('Settings saved');
      formLoaded.current = false;
      loadStatus();
    }).catch(function(e) { showToast(e.message, 'error'); });
  };

  var counts = (status && status.counts) || {};
  var lastRun = (status && status.lastRun) || {};
  var settings = (status && status.settings) || {};
  var conn = (status && status.connection) || {};
  var compared = (status && status.compared) || {};
  var trackingCounts = (status && status.tracking) || {};
  var invCounts = (status && status.inventory) || {};
  var lastInv = (status && status.lastInventoryRun) || {};
  var readThrough = settings.shein_orders_last_delta || null;
  var behindMinutes = readThrough ? (Date.now() - new Date(readThrough).getTime()) / 60000 : 0;
  var mode = !status ? { col: C.textDim, label: 'Loading' }
    : !conn.authorized ? { col: C.warning, label: 'Store Not Authorized' }
    : !conn.enabled ? { col: C.textDim, label: 'Integration Disabled' }
    : !settings.shein_orders_enabled ? { col: C.textDim, label: 'Order Sync Off' }
    : settings.shein_orders_dry_run !== false ? { col: C.accent, label: 'Dry-Run (Preview Only)' }
    : { col: C.success, label: 'Live' };

  var tdStyle = { padding: '8px 10px', borderBottom: '1px solid ' + C.border, verticalAlign: 'top' };

  function ordersTable(rows) {
    if (rows == null) return h('div', { style: { color: C.textMuted, fontSize: 13, padding: 24, textAlign: 'center' } }, 'Loading…');
    if (!rows.length) return h('div', { style: { color: C.textMuted, fontSize: 13, padding: 24, textAlign: 'center' } }, 'No orders here.');
    var heads = ['SHEIN Order', 'Placed', 'Deadline', 'Ship To', 'Units', 'Total', 'SHEIN Status', 'Decision', 'CedCommerce', 'Shopify'];
    return h('div', { style: { overflowX: 'auto' } },
      h('table', { style: { width: '100%', borderCollapse: 'collapse', fontSize: 12.5 } },
        h('thead', null, h('tr', null, heads.map(function(hd, i) {
          return h('th', { key: i, style: { textAlign: 'left', padding: '8px 10px', color: C.textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid ' + C.border, whiteSpace: 'nowrap' } }, hd);
        }))),
        h('tbody', null, rows.map(function(o) {
          var isOpen = expanded === o.id;
          var area = o.ship_to || {};
          var toShip = (o.units || []).filter(function(u) { return Number(u.unit_status) !== 6 && [2, 4].indexOf(Number(u.exchange_tag)) === -1; }).length;
          var deadline = _shDeadline(o);
          var rowEls = [h('tr', { key: o.id, onClick: function() { setExpanded(isOpen ? null : o.id); }, style: { cursor: 'pointer', background: isOpen ? (C.bgAlt || 'transparent') : 'transparent' } },
            h('td', { style: tdStyle }, h(CopyCell, { value: o.order_no }, h('span', { style: { fontWeight: 600, color: C.text } }, o.order_no)),
              Number(o.order_type) === 2 ? h('div', { style: { fontSize: 11, color: C.warning } }, 'Exchange of ' + (o.bill_no || '')) : null),
            h('td', { style: Object.assign({}, tdStyle, { whiteSpace: 'nowrap' }) }, _shFmtDateTime(o.order_time)),
            h('td', { style: Object.assign({}, tdStyle, { whiteSpace: 'nowrap', color: deadline ? deadline.color : C.textMuted }) },
              deadline ? [deadline.label ? h('div', { key: 'by' }, deadline.label) : null,
                deadline.cancel ? h('div', { key: 'cancel', style: { fontSize: 11.5, color: deadline.cancelColor } }, deadline.cancel) : null] : '—'),
            h('td', { style: tdStyle }, [o.customer_name, area.city, area.province, area.postalCode].filter(Boolean).join(', ') || '—'),
            h('td', { style: tdStyle }, toShip + (toShip !== (o.units || []).length ? ' of ' + (o.units || []).length : '')),
            h('td', { style: tdStyle }, _shMoney(o.order_total)),
            h('td', { style: Object.assign({}, tdStyle, { whiteSpace: 'nowrap' }) }, SHEIN_STATUS_NAMES[o.shein_status] || (o.shein_status != null ? 'Status ' + o.shein_status : '—')),
            h('td', { style: tdStyle }, _shDecisionBadge(o.import_status)),
            h('td', { style: tdStyle }, _shCompareBadge(o.import_status === 'external' ? o.compare_status : null)),
            h('td', { style: tdStyle }, o.shopify_order_name ? h('span', { style: { color: o.shopify_cancelled_at ? C.textMuted : C.success, fontWeight: 600 } }, o.shopify_order_name + (o.shopify_cancelled_at ? ' (cancelled)' : '')) : '—')
          )];
          // Attention tab: why the order is here, always in view
          if (o.attention && o.attention.length) {
            rowEls.push(h('tr', { key: o.id + '-why' },
              h('td', { colSpan: heads.length, style: { padding: '0 10px 8px 10px', borderBottom: '1px solid ' + C.border, color: C.warning, fontSize: 12 } },
                o.attention.join(' · '),
                isAdmin && o.import_status === 'failed' && !o.shopify_order_id
                  ? h('button', { onClick: function(e) { e.stopPropagation(); retryOrder(o); }, style: { marginLeft: 12, background: 'none', border: '1px solid ' + C.border, borderRadius: 5, color: C.accent, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', padding: '2px 10px' } }, 'Retry')
                  : null)));
          }
          if (isOpen) {
            rowEls.push(h('tr', { key: o.id + '-detail' },
              h('td', { colSpan: heads.length, style: { padding: '10px 16px', borderBottom: '1px solid ' + C.border, background: C.bgAlt || 'transparent' } },
                o.error ? h('div', { style: { color: C.danger, fontSize: 12, marginBottom: 8 } }, 'Error: ' + o.error) : null,
                o.status_note ? h('div', { style: { color: C.text, fontSize: 12, marginBottom: 8 } }, o.status_note) : null,
                o.import_status === 'external' && o.compare_status ? h('div', { style: { fontSize: 12, marginBottom: 8, color: o.compare_status === 'match' ? C.success : o.compare_status === 'differs' ? C.danger : C.warning } },
                  o.compare_status === 'match' ? ('Matches the Shopify order CedCommerce created: same units per SKU, same amount (' + _shMoney(o.compare_total) + ').' + (o.compare_note ? ' ' + o.compare_note + '.' : ''))
                    : o.compare_status === 'differs' ? ('Differs from the Shopify order CedCommerce created. ' + (o.compare_note || ''))
                    : ((o.compare_note || 'No Shopify order carries this number') + '. Looked up ' + _shFmtDateTime(o.compared_at) + '; an order under 3 days old is looked up again every 15 minutes.')) : null,
                h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 6 } },
                  'Products ' + _shMoney(o.product_total) + ' − discounts ' + _shMoney(o.discount_total) + ' + shipping ' + _shMoney(o.shipping_total) + ' = ' + _shMoney(o.order_total) +
                  ' · sales tax collected by SHEIN ' + _shMoney(o.tax_total) +
                  (o.sales_site ? ' · ' + o.sales_site : '') +
                  (o.release_by ? ' · release by ' + _shFmtDateTime(o.release_by) : '') +
                  (o.ship_by ? ' · ship by ' + _shFmtDateTime(o.ship_by) : '') +
                  (o.cancel_by ? ' · SHEIN cancels it ' + _shFmtDateTime(o.cancel_by) + ' unless it has shipped' : '')),
                _shLines(o.units).map(function(l) {
                  var gone = Number(l.status) === 6 || [2, 4].indexOf(Number(l.exchangeTag)) !== -1;
                  return h('div', { key: l.key, style: { fontSize: 12.5, padding: '4px 0', borderTop: '1px dotted ' + C.border, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', opacity: gone ? 0.55 : 1 } },
                    h('span', null,
                      h('span', { style: { fontWeight: 600, color: C.text } }, l.sku || '(no seller SKU)'), ' × ' + l.qty + (l.title ? ' — ' + l.title : ''),
                      h('span', { style: { color: C.textMuted } },
                        '  ' + _shMoney(l.price) + ' each' + (l.discount ? ', discount ' + _shMoney(l.discount) : '') +
                        '  [' + (SHEIN_STATUS_NAMES[l.status] || '?') + (Number(l.exchangeTag) === 3 ? ', exchanged in' : Number(l.exchangeTag) === 2 ? ', exchanged out' : '') + ']')),
                    _shTracking(l.units, isAdmin ? retryPush : null),
                    l.units.some(function(u) { return u.shopify_refund_status === 'removed'; }) ? _shPill(C.warning, 'Removed From Shopify') : null
                  );
                })
              )));
          }
          return rowEls;
        }))
      ));
  }

  var skuSummary = (skuData && skuData.summary) || (status && status.skuMap) || {};

  // The inventory log: one row per SKU (its newest) or every row; dry-run rows carry SHEIN's total beside ours.
  function inventoryTable(rows) {
    if (rows == null) return h('div', { style: { color: C.textMuted, fontSize: 13, padding: 24, textAlign: 'center' } }, 'Loading…');
    if (!rows.length) {
      return h('div', { style: { color: C.textMuted, fontSize: 13, padding: 24, textAlign: 'center' } },
        invView === 'differs' ? 'Every quantity this connector would send is the one SHEIN holds.' : invView === 'failed' ? 'Nothing failed.' : 'Nothing logged yet.');
    }
    var dry = settings.shein_inventory_dry_run !== false;
    var heads = ['SHEIN SKU', 'SKU Code', 'Shopify SKU', 'Shopify Available', 'Held Back', dry ? 'Would Send' : 'Sent', 'SHEIN Total', 'SHEIN Usable', 'Check', 'When'];
    return h('div', { style: { overflowX: 'auto' } },
      h('table', { style: { width: '100%', borderCollapse: 'collapse', fontSize: 12.5 } },
        h('thead', null, h('tr', null, heads.map(function(hd, i) {
          return h('th', { key: i, style: { textAlign: 'left', padding: '8px 10px', color: C.textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid ' + C.border, whiteSpace: 'nowrap' } }, hd);
        }))),
        h('tbody', null, rows.map(function(l) {
          var held = (Number(l.guard_units) || 0) + (Number(l.buffer_units) || 0);
          var check = l.status === 'failed' ? _shPill(C.danger, 'Failed')
            : l.status === 'pushed' ? _shPill(C.success, 'Sent')
            : l.agree === true ? _shPill(C.success, 'Same As SHEIN')
            : l.agree === false ? _shPill(C.danger, 'Differs')
            : _shPill(C.textMuted, 'SHEIN Not Read');
          return h('tr', { key: l.id },
            h('td', { style: tdStyle }, l.seller_sku ? h(CopyCell, { value: l.seller_sku }, h('span', { style: { fontWeight: 600, color: C.text } }, l.seller_sku)) : h('span', { style: { color: C.textDim } }, '—')),
            h('td', { style: Object.assign({}, tdStyle, { color: C.textMuted, whiteSpace: 'nowrap' }) }, h(CopyCell, { value: l.shein_sku_code }, l.shein_sku_code)),
            h('td', { style: tdStyle }, l.shopify_sku ? h(CopyCell, { value: l.shopify_sku }, l.shopify_sku) : '—'),
            h('td', { style: tdStyle }, l.available == null ? '—' : l.available),
            h('td', { style: Object.assign({}, tdStyle, { color: C.textMuted }) }, l.status === 'failed' && l.available == null ? '—' : (held ? '−' + held + (l.guard_units ? ' (' + l.guard_units + ' just ordered' + (l.buffer_units ? ', ' + l.buffer_units + ' buffer)' : ')') : '') : '0')),
            h('td', { style: Object.assign({}, tdStyle, { fontWeight: 600, color: C.text }) }, l.pushed_qty == null ? '—' : l.pushed_qty),
            h('td', { style: tdStyle }, l.shein_total == null ? '—' : l.shein_total),
            h('td', { style: Object.assign({}, tdStyle, { color: C.textMuted }) }, l.shein_usable == null ? '—' : l.shein_usable),
            h('td', { style: tdStyle }, check, l.error ? h('div', { style: { color: C.danger, fontSize: 11, marginTop: 3 } }, l.error) : null),
            h('td', { style: Object.assign({}, tdStyle, { whiteSpace: 'nowrap', color: C.textMuted }) }, _shFmtDateTime(l.created_at))
          );
        }))
      ));
  }

  function skuMapTable(rows) {
    if (rows == null) return h('div', { style: { color: C.textMuted, fontSize: 13, padding: 24, textAlign: 'center' } }, 'Loading…');
    if (!rows.length) {
      return h('div', { style: { color: C.textMuted, fontSize: 13, padding: 24, textAlign: 'center' } },
        skuFilter === 'unmatched' ? 'Every SHEIN SKU seen on an order has its Shopify SKU.' : 'No SKUs here.');
    }
    // SPU = the product and SKC = its colour, the two the seller hub searches by; the SKU code is
    // the sellable variant, the one SHEIN puts on orders and takes for inventory.
    var heads = ['SHEIN SKU', 'SPU', 'SKC', 'SKU Code', 'Product', 'Units, 30 Days', 'Shopify SKU', 'Matched', 'CedCommerce Used', ''];
    var linkStyle = { background: 'none', border: 'none', color: C.accent, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', padding: 0, marginRight: 12 };
    return h('div', { style: { overflowX: 'auto' } },
      h('table', { style: { width: '100%', borderCollapse: 'collapse', fontSize: 12.5 } },
        h('thead', null, h('tr', null, heads.map(function(hd, i) {
          return h('th', { key: i, style: { textAlign: 'left', padding: '8px 10px', color: C.textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid ' + C.border, whiteSpace: 'nowrap' } }, hd);
        }))),
        h('tbody', null, rows.map(function(m) {
          var byHand = m.source === 'manual';
          return h('tr', { key: m.id },
            h('td', { style: tdStyle }, m.seller_sku ? h(CopyCell, { value: m.seller_sku }, h('span', { style: { fontWeight: 600, color: C.text } }, m.seller_sku)) : h('span', { style: { color: C.textDim } }, '(no seller SKU)')),
            h('td', { style: Object.assign({}, tdStyle, { color: C.textMuted, whiteSpace: 'nowrap' }) }, m.spu_name ? h(CopyCell, { value: m.spu_name }, m.spu_name) : '—'),
            h('td', { style: Object.assign({}, tdStyle, { color: C.textMuted, whiteSpace: 'nowrap' }) }, m.skc_name ? h(CopyCell, { value: m.skc_name }, m.skc_name) : '—'),
            h('td', { style: Object.assign({}, tdStyle, { color: C.textMuted, whiteSpace: 'nowrap' }) }, h(CopyCell, { value: m.shein_sku_code }, m.shein_sku_code)),
            h('td', { style: Object.assign({}, tdStyle, { maxWidth: 300 }) }, m.title || '—'),
            h('td', { style: tdStyle }, m.units || 0),
            h('td', { style: tdStyle }, m.shopify_sku ? h(CopyCell, { value: m.shopify_sku }, h('span', { style: { fontWeight: 600, color: C.text } }, m.shopify_sku))
              : _shPill(C.warning, m.checked_at ? 'Not Matched' : 'Not Yet Looked Up')),
            h('td', { style: Object.assign({}, tdStyle, { whiteSpace: 'nowrap', color: C.textMuted }) }, !m.shopify_sku ? '—' : byHand ? 'By Hand' : 'Same Name'),
            h('td', { style: tdStyle }, m.suggestion
              ? h('span', null, h('span', { style: { color: C.text, fontWeight: 600 } }, m.suggestion.sku),
                  h('span', { style: { color: C.textMuted } }, '  on ' + m.suggestion.orders + (m.suggestion.of > m.suggestion.orders ? ' of ' + m.suggestion.of : '') + (m.suggestion.orders === 1 ? ' order' : ' orders')))
              : '—'),
            h('td', { style: Object.assign({}, tdStyle, { whiteSpace: 'nowrap' }) },
              isAdmin ? h('button', { onClick: function() { matchSku(m); }, style: linkStyle }, m.shopify_sku ? 'Change' : 'Match') : null,
              isAdmin && byHand ? h('button', { onClick: function() { unmatchSku(m); }, style: Object.assign({}, linkStyle, { color: C.textMuted }) }, 'Remove') : null)
          );
        }))
      ));
  }

  function tabLink(t) {
    var active = tab === t[0];
    var open = (skuSummary.unmatched || 0) + (skuSummary.unchecked || 0);
    var label = t[1] + (t[0] === 'attention' && attention.length ? ' (' + attention.length + ')' : '') +
      (t[0] === 'sku-map' && open ? ' (' + open + ')' : '');
    var arg = t[0] === 'orders' ? null : t[0];
    return h('a', { key: t[0], href: pageToPath('shopify-shein', arg),
      onClick: function(e) { if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return; e.preventDefault(); onNavigate('shopify-shein', arg); },
      style: { textDecoration: 'none', borderBottom: active ? '2px solid ' + C.accent : '2px solid transparent', color: active ? C.text : C.textMuted, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' } }, label);
  }

  return h('div', { style: { maxWidth: 1280, margin: '0 auto', padding: 24 } },
    // Header
    h('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 10 } },
      h('div', null,
        h('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
          h('h1', { style: { fontSize: 22, fontWeight: 700, color: C.text, margin: 0 } }, 'SHEIN'),
          _shPill(mode.col, mode.label)
        ),
        h('div', { style: { fontSize: 12.5, color: C.textMuted, marginTop: 4 } },
          'Orders in from SHEIN, tracking and inventory out to it, new listings from a workbook' + (conn.storeName ? ' · store ' + conn.storeName : '') + '.')
      ),
      h('div', { style: { display: 'flex', gap: 8 } },
        h('a', { href: pageToPath('settings-integrations'),
          onClick: function(e) { if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return; e.preventDefault(); onNavigate('settings-integrations'); },
          style: { textDecoration: 'none', background: C.surface, color: C.text, border: '1px solid ' + C.border, padding: '6px 12px', fontSize: 12, borderRadius: 6, fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' } },
          h(LIcon, { name: 'arrow-left', size: 14 }), ' Integrations'),
        h(Btn, { variant: 'primary', size: 'sm', onClick: syncNow, disabled: busy }, busy ? [h(LIcon, { name: 'loader-2', size: 14, spin: true, key: 'i' }), ' Syncing...'] : [h(LIcon, { name: 'refresh-cw', size: 14, key: 'i' }), ' Sync Now'])
      )
    ),
    // Stats
    h('div', { style: { display: 'flex', gap: 12, marginBottom: 18, flexWrap: 'wrap' } },
      h(SheinStatCard, { label: 'Would Import', value: counts.preview || 0, color: C.accent }),
      h(SheinStatCard, { label: 'Waiting', value: counts.pending || 0 }),
      h(SheinStatCard, { label: 'Released Elsewhere', value: counts.external || 0 }),
      h(SheinStatCard, { label: 'Imported', value: (counts.imported || 0) + (counts.adopted || 0), color: C.success }),
      h(SheinStatCard, { label: 'Failed', value: counts.failed || 0, color: counts.failed ? C.danger : undefined }),
      h(SheinStatCard, { label: 'Match CedCommerce', value: compared.match || 0,
        sub: (compared.differs || 0) + ' differ · ' + (compared.missing || 0) + ' not in Shopify · ' + (compared.unchecked || 0) + ' to check',
        color: compared.differs ? C.warning : (compared.match ? C.success : undefined) }),
      // dry-run: units whose tracking this connector would send; live: units sent
      h(SheinStatCard, { label: settings.shein_orders_dry_run !== false ? 'Tracking, Would Send' : 'Tracking Sent',
        value: !settings.shein_tracking_enabled ? 'Off' : (settings.shein_orders_dry_run !== false ? (trackingCounts.preview || 0) : (trackingCounts.pushed || 0)),
        sub: !settings.shein_tracking_enabled ? 'switch it on in Settings'
          : (trackingCounts.match || 0) + ' same as SHEIN · ' + (trackingCounts.differs || 0) + ' differ · ' + (trackingCounts.unmapped_carrier || 0) + ' no carrier mapping' + (trackingCounts.failed ? ' · ' + trackingCounts.failed + ' refused' : ''),
        color: (trackingCounts.unmapped_carrier || trackingCounts.failed || trackingCounts.differs) ? C.warning : (trackingCounts.match ? C.success : undefined) }),
      // dry-run: SKUs whose quantity this connector would send, checked against SHEIN's total; live: SKUs sent
      h(SheinStatCard, { label: settings.shein_inventory_dry_run !== false ? 'Inventory, Would Send' : 'Inventory Sent',
        value: !settings.shein_inventory_enabled ? 'Off' : (settings.shein_inventory_dry_run !== false ? (invCounts.would_push || 0) : (invCounts.pushed || 0)),
        sub: !settings.shein_inventory_enabled ? 'switch it on in Settings'
          : lastInv.ok === false ? ('last run failed: ' + (lastInv.error || ''))
          : (invCounts.agree || 0) + ' same as SHEIN · ' + (invCounts.differ || 0) + ' differ' + (invCounts.failed ? ' · ' + invCounts.failed + ' failed' : ''),
        color: lastInv.ok === false || invCounts.failed || invCounts.differ ? C.warning : (invCounts.agree ? C.success : undefined) }),
      // The cursor lives in the database, so this reads the same on either server worker.
      h(SheinStatCard, { label: 'Read Through', value: readThrough ? _shFmtDateTime(readThrough) : '—',
        sub: lastRun.ok === false ? ('last run failed: ' + (lastRun.error || ''))
          : !readThrough ? 'not yet run'
          : !settings.shein_orders_enabled ? 'order sync is off'
          : behindMinutes > 15 ? 'catching up' : 'up to date',
        color: lastRun.ok === false ? C.danger : (readThrough && settings.shein_orders_enabled && behindMinutes > 15 ? C.warning : undefined) })
    ),
    // Tabs
    h('div', { style: { display: 'flex', gap: 4, borderBottom: '1px solid ' + C.border, marginBottom: 16 } }, SHEIN_TABS.map(tabLink)),
    // Orders tab
    tab === 'orders' && h('div', null,
      h('div', { style: { display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' } },
        SHEIN_FILTERS.map(function(f) {
          var active = filter === f[0];
          return h('button', { key: f[0], onClick: function() { setFilter(f[0]); },
            style: { background: active ? C.accent + '22' : 'transparent', border: '1px solid ' + (active ? C.accent : C.border), color: active ? C.accent : C.textMuted, padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' } }, f[1]);
        })
      ),
      settings.shein_orders_enabled && settings.shein_orders_dry_run !== false ? h('div', { style: { padding: '10px 14px', borderRadius: 8, marginBottom: 12, background: C.accent + '15', border: '1px solid ' + C.accent + '40', color: C.accent, fontSize: 13 } },
        'Dry-run: nothing is sent to SHEIN or Shopify. "Would Import" is an order this connector would take now; "Released Elsewhere" is one the current connector already took.') : null,
      ordersTable(orders)
    ),
    // Attention tab
    tab === 'attention' && h('div', null,
      attention.length ? ordersTable(attention) : h('div', { style: { color: C.textMuted, fontSize: 13, padding: 24, textAlign: 'center' } }, 'Nothing needs attention.')
    ),
    // Listings tab
    tab === 'listings' && h(SheinListingsTab, { showToast: showToast, isAdmin: isAdmin }),
    // SKU Map tab
    tab === 'sku-map' && h('div', null,
      h('div', { style: { fontSize: 12.5, color: C.textMuted, marginBottom: 12, lineHeight: 1.6 } },
        (skuSummary.total || 0) + ' SHEIN SKUs' + (skuSummary.listed ? ' (' + skuSummary.listed + ' from SHEIN’s catalog, the rest seen on orders only)' : ' seen on orders') + ': ' +
        (skuSummary.exact || 0) + ' have a Shopify SKU of the same name, ' +
        (skuSummary.manual || 0) + ' matched by hand, ' + (skuSummary.unmatched || 0) + ' not matched' +
        (skuSummary.unchecked ? ', ' + skuSummary.unchecked + ' not yet looked up' : '') +
        (skuSummary.unnamed ? ', ' + skuSummary.unnamed + ' with no seller SKU on SHEIN yet' : '') + '. ',
        'A new listing on SHEIN appears here within minutes of its approval, once the inventory sync is on. A SHEIN SKU that no Shopify product carries is matched here by hand. "CedCommerce Used" is the SKU on the Shopify orders CedCommerce created for it: a hint, never applied by itself.'),
      h('div', { style: { display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' } },
        SHEIN_SKU_FILTERS.map(function(f) {
          var active = skuFilter === f[0];
          return h('button', { key: f[0], onClick: function() { setSkuFilter(f[0]); },
            style: { background: active ? C.accent + '22' : 'transparent', border: '1px solid ' + (active ? C.accent : C.border), color: active ? C.accent : C.textMuted, padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' } }, f[1]);
        })
      ),
      skuMapTable(skuData ? skuData.rows : null)
    ),
    // Inventory tab
    tab === 'inventory' && h('div', null,
      h('div', { style: { fontSize: 12.5, color: C.textMuted, marginBottom: 12, lineHeight: 1.6 } },
        'Every 60 seconds the Shopify stock changed at the location set in Settings is read and, for each listing matched on the SKU Map, the quantity to set on SHEIN is worked out: ' +
        'available − units of orders this connector imported in the last ' + (settings.shein_race_window_hours == null ? 6 : settings.shein_race_window_hours) + ' hours − buffer (' + (settings.shein_inventory_buffer || 0) + '). Nightly, every matched listing. ',
        settings.shein_inventory_dry_run !== false
          ? 'Dry-run: nothing is sent. "Would Send" stands beside "SHEIN Total", what SHEIN holds today (set by the current connector); the two should agree.'
          : 'Live: the quantity is sent to SHEIN as the listing’s total.'),
      !settings.shein_inventory_enabled ? h('div', { style: { padding: '10px 14px', borderRadius: 8, marginBottom: 12, background: C.warning + '15', border: '1px solid ' + C.warning + '40', color: C.warning, fontSize: 13 } },
        'Inventory sync is off: switch it on in Settings and pick the Shopify location that feeds SHEIN.') : null,
      lastInv.ok === false ? h('div', { style: { padding: '10px 14px', borderRadius: 8, marginBottom: 12, background: C.danger + '15', border: '1px solid ' + C.danger + '40', color: C.danger, fontSize: 13 } },
        'The last run failed: ' + (lastInv.error || '')) : null,
      h('div', { style: { display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' } },
        h(Btn, { size: 'sm', onClick: inventoryNow, disabled: busy || !settings.shein_inventory_enabled }, h(LIcon, { name: 'refresh-cw', size: 14 }), ' Run Now'),
        isAdmin ? h(Btn, { size: 'sm', onClick: sweepNow, disabled: busy || !settings.shein_inventory_enabled }, h(LIcon, { name: 'upload', size: 14 }), settings.shein_inventory_dry_run !== false ? ' Preview All Now' : ' Push All Now') : null,
        isAdmin ? h(Btn, { size: 'sm', onClick: catalogNow, disabled: busy }, h(LIcon, { name: 'book-open', size: 14 }), ' Read SHEIN Catalog') : null,
        h('span', { style: { fontSize: 12, color: C.textMuted } },
          (lastInv.at ? 'Last run ' + _shFmtDateTime(lastInv.at) + ' (' + (lastInv.mode || '') + '): ' + (lastInv.checked || 0) + ' SKUs, ' + (lastInv.wouldPush || 0) + ' would send, ' + (lastInv.pushed || 0) + ' sent, ' + (lastInv.failed || 0) + ' failed' : 'Not yet run') +
          (settings.shein_catalog_read_at ? ' · catalog read ' + _shFmtDateTime(settings.shein_catalog_read_at) : ''))),
      h('div', { style: { display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' } },
        SHEIN_INV_VIEWS.map(function(f) {
          var active = invView === f[0];
          return h('button', { key: f[0], onClick: function() { setInvView(f[0]); },
            style: { background: active ? C.accent + '22' : 'transparent', border: '1px solid ' + (active ? C.accent : C.border), color: active ? C.accent : C.textMuted, padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' } }, f[1]);
        })
      ),
      inventoryTable(invLog)
    ),
    // Settings tab
    tab === 'settings' && h('div', { style: { maxWidth: 620 } },
      h('div', { style: { fontSize: 12.5, color: C.textMuted, marginBottom: 16, lineHeight: 1.6 } },
        'The order sync reads SHEIN every 60 seconds when enabled. It runs in dry-run: orders are read and staged as previews, and nothing is sent to SHEIN or Shopify. ',
        'The live import is switched on at the cutover, after the previews have been compared with the orders the current connector creates.'),
      h('label', { style: { display: 'flex', alignItems: 'center', gap: 8, cursor: isAdmin ? 'pointer' : 'not-allowed', fontSize: 13, color: C.text, marginBottom: 10 } },
        h('input', { type: 'checkbox', checked: form.shein_orders_enabled, disabled: !isAdmin, onChange: function(e) { setForm(Object.assign({}, form, { shein_orders_enabled: e.target.checked })); } }),
        'Enable Order Sync'),
      h('label', { style: { display: 'flex', alignItems: 'center', gap: 8, cursor: isAdmin ? 'pointer' : 'not-allowed', fontSize: 13, color: C.text, marginBottom: 10 } },
        h('input', { type: 'checkbox', checked: form.shein_tracking_enabled, disabled: !isAdmin, onChange: function(e) { setForm(Object.assign({}, form, { shein_tracking_enabled: e.target.checked })); } }),
        'Enable Tracking'),
      h('div', { style: { fontSize: 12, color: C.textMuted, margin: '-4px 0 10px 24px', lineHeight: 1.5 } },
        'Every 5 minutes the orders shipped in Shopify are read and each unit gets its tracking number and carrier. In dry-run nothing is sent to SHEIN: the number this connector would send is shown next to the one SHEIN holds (uploaded by the current connector), so the carrier names are proven before the cutover.'),
      // The Dry-Run boxes were locked on until the cutover release of 2026-10-02; now they are the live switches.
      status && status.liveImportLocked
        ? h('label', { style: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: C.textMuted, marginBottom: 14, cursor: 'not-allowed' } },
          h('input', { type: 'checkbox', checked: true, disabled: true, readOnly: true }),
          'Dry-Run (locked on until the cutover)')
        : h('label', { style: { display: 'flex', alignItems: 'center', gap: 8, cursor: isAdmin ? 'pointer' : 'not-allowed', fontSize: 13, color: C.text, marginBottom: 14 } },
          h('input', { type: 'checkbox', checked: form.shein_orders_dry_run !== false, disabled: !isAdmin, onChange: function(e) { setForm(Object.assign({}, form, { shein_orders_dry_run: e.target.checked })); } }),
          'Dry-Run for orders (ticked = preview only; unticked = orders are released on SHEIN, created in Shopify, and tracking is uploaded)'),
      status && status.liveImportLocked ? h('div', { style: { fontSize: 12.5, color: C.textMuted, marginBottom: 14, lineHeight: 1.6 } },
        'The live import is locked in this release: no order is released on SHEIN and no order is created in Shopify, whatever is set here. The lock comes off with the cutover release, after CedCommerce’s order import has been switched off.') : null,
      !conn.authorized ? h('div', { style: { padding: '10px 14px', borderRadius: 8, marginBottom: 12, background: C.warning + '15', border: '1px solid ' + C.warning + '40', color: C.warning, fontSize: 13 } },
        'The SHEIN store is not authorized yet. Save the application keys and authorize the store on the SHEIN card in Settings > Integrations.') : null,
      conn.authorized && !conn.enabled ? h('div', { style: { padding: '10px 14px', borderRadius: 8, marginBottom: 12, background: C.warning + '15', border: '1px solid ' + C.warning + '40', color: C.warning, fontSize: 13 } },
        'The SHEIN integration is not enabled. Tick "Enable Integration" on the SHEIN card in Settings > Integrations.') : null,
      // ─── Inventory ───
      h('div', { style: { marginTop: 8, borderTop: '1px solid ' + C.border, paddingTop: 14 } },
        h('div', { style: { fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 6 } }, 'Inventory'),
        h('label', { style: { display: 'flex', alignItems: 'center', gap: 8, cursor: isAdmin ? 'pointer' : 'not-allowed', fontSize: 13, color: C.text, marginBottom: 10 } },
          h('input', { type: 'checkbox', checked: form.shein_inventory_enabled, disabled: !isAdmin, onChange: function(e) { setForm(Object.assign({}, form, { shein_inventory_enabled: e.target.checked })); } }),
          'Enable Inventory Sync'),
        h('div', { style: { fontSize: 12, color: C.textMuted, margin: '-4px 0 10px 24px', lineHeight: 1.5 } },
          'Every 60 seconds the Shopify stock changed at the location below is read; nightly, every matched listing. SHEIN’s catalog is read the same way, so a new listing appears on the SKU Map by itself. In dry-run nothing is sent: the quantity this connector would send is logged beside the total SHEIN holds.'),
        status && status.liveImportLocked
          ? h('label', { style: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: C.textMuted, marginBottom: 12, cursor: 'not-allowed' } },
            h('input', { type: 'checkbox', checked: true, disabled: true, readOnly: true }),
            'Inventory Dry-Run (locked on until the cutover)')
          : h('label', { style: { display: 'flex', alignItems: 'center', gap: 8, cursor: isAdmin ? 'pointer' : 'not-allowed', fontSize: 13, color: C.text, marginBottom: 12 } },
            h('input', { type: 'checkbox', checked: form.shein_inventory_dry_run !== false, disabled: !isAdmin, onChange: function(e) { setForm(Object.assign({}, form, { shein_inventory_dry_run: e.target.checked })); } }),
            'Inventory Dry-Run (ticked = preview only; unticked = quantities are sent to SHEIN)'),
        h('div', { style: { display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 12 } },
          h('div', null,
            h('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 } }, 'Shopify Location'),
            h('select', { value: form.shein_shopify_location_id, disabled: !isAdmin, onChange: function(e) { setForm(Object.assign({}, form, { shein_shopify_location_id: e.target.value })); },
                style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', minWidth: 220 } },
              [h('option', { key: '', value: '' }, 'Select Location…')].concat(
                (form.shein_shopify_location_id && !locations.some(function(l) { return l.id === form.shein_shopify_location_id; }) ? [h('option', { key: 'cur', value: form.shein_shopify_location_id }, form.shein_shopify_location_id)] : []),
                locations.map(function(l) { return h('option', { key: l.id, value: l.id }, l.name + (l.active ? '' : ' (inactive)')); })))),
          h('div', null,
            h('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 } }, 'SHEIN Warehouse'),
            h('select', { value: form.shein_warehouse_code, disabled: !isAdmin, onChange: function(e) { setForm(Object.assign({}, form, { shein_warehouse_code: e.target.value })); },
                style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', minWidth: 200 } },
              [h('option', { key: '', value: '' }, warehouses.warehouses.length === 1 ? 'The only warehouse' : 'Select Warehouse…')].concat(
                warehouses.warehouses.map(function(w) { return h('option', { key: w.code, value: w.code }, (w.name || w.code) + (w.type === 2 ? ' (certified, no stock updates)' : '')); })))),
          h('div', { style: { width: 130 } }, h(Inp, { label: 'Safety Buffer (Units)', type: 'number', value: String(form.shein_inventory_buffer), disabled: !isAdmin, onChange: function(e) { setForm(Object.assign({}, form, { shein_inventory_buffer: e.target.value })); } })),
          h('div', { style: { width: 150 } }, h(Inp, { label: 'Race Window (Hours)', type: 'number', value: String(form.shein_race_window_hours), disabled: !isAdmin, onChange: function(e) { setForm(Object.assign({}, form, { shein_race_window_hours: e.target.value })); } }))),
        h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 14, lineHeight: 1.6 } },
          warehouses.warehouses.length
            ? 'SHEIN’s warehouses for this store' + (warehouses.at ? ' (read ' + _shFmtDateTime(warehouses.at) + ')' : '') + ': ' + warehouses.warehouses.map(function(w) { return (w.name || w.code) + ' [' + w.code + ']'; }).join(', ') + '. A store with one merchant warehouse needs no choice. '
            : 'SHEIN’s warehouse list has not been read yet (it is read when the inventory sync first runs). ',
          isAdmin ? h('button', { onClick: refreshWarehouses, style: { background: 'none', border: 'none', color: C.accent, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', padding: 0 } }, 'Read It Again') : null)),
      h('div', { style: { display: 'flex', gap: 8, flexWrap: 'wrap' } },
        h(Btn, { variant: 'primary', size: 'sm', onClick: saveSettings, disabled: !isAdmin }, h(LIcon, { name: 'save', size: 14 }), ' Save Settings'),
        h(Btn, { size: 'sm', onClick: trackingNow, disabled: busy || !settings.shein_tracking_enabled }, h(LIcon, { name: 'truck', size: 14 }), ' Run Tracking Now'),
        h(Btn, { size: 'sm', onClick: inventoryNow, disabled: busy || !settings.shein_inventory_enabled }, h(LIcon, { name: 'package', size: 14 }), ' Run Inventory Now')),

      // ─── Carrier map ───
      h('div', { style: { marginTop: 28, borderTop: '1px solid ' + C.border, paddingTop: 18 } },
        h('div', { style: { fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 6 } }, 'Carrier Map'),
        h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 12, lineHeight: 1.5 } },
          'Shopify fulfillment carrier → the carrier name SHEIN takes for this store. Matching ignores case and punctuation. A carrier with no mapping parks the unit on the Attention tab; nothing is guessed.'),
        h('table', { style: { width: '100%', maxWidth: 640, borderCollapse: 'collapse', fontSize: 12.5, marginBottom: 12 } },
          h('thead', null, h('tr', null, ['Shopify Carrier (Normalized)', 'SHEIN Carrier', ''].map(function(hd, i) {
            return h('th', { key: i, style: { textAlign: 'left', padding: '6px 8px', color: C.textMuted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', borderBottom: '1px solid ' + C.border } }, hd);
          }))),
          h('tbody', null, carrierMap.map(function(m) {
            var listed = !carriers.carriers.channels.length || carriers.carriers.channels.some(function(c) { return String(c.name).toLowerCase() === String(m.shein_carrier_name).toLowerCase(); });
            return h('tr', { key: m.id },
              h('td', { style: { padding: '5px 8px', borderBottom: '1px solid ' + C.border, fontSize: 12 } }, m.shopify_company_norm),
              h('td', { style: { padding: '5px 8px', borderBottom: '1px solid ' + C.border, color: listed ? C.text : C.danger } }, m.shein_carrier_name + (listed ? '' : ' (not in SHEIN’s list)')),
              h('td', { style: { padding: '5px 8px', borderBottom: '1px solid ' + C.border } },
                isAdmin ? h('button', { onClick: function() { deleteCarrierMapping(m); }, style: { background: 'none', border: 'none', color: C.danger, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' } }, 'Remove') : null)
            );
          }))
        ),
        isAdmin ? h('div', { style: { display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 14 } },
          h('div', { style: { width: 220 } }, h(Inp, { label: 'Shopify Carrier Name', placeholder: 'e.g. DHL eCommerce', value: newCarrier.shopify_company, onChange: function(e) { setNewCarrier(Object.assign({}, newCarrier, { shopify_company: e.target.value })); } })),
          h('div', null,
            h('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 } }, 'SHEIN Carrier'),
            carriers.carriers.channels.length
              ? h('select', { value: newCarrier.shein_carrier_name, onChange: function(e) { setNewCarrier(Object.assign({}, newCarrier, { shein_carrier_name: e.target.value })); },
                  style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit' } },
                  [h('option', { key: '', value: '' }, 'Select Carrier…')].concat(carriers.carriers.channels.map(function(c) { return h('option', { key: c.name, value: c.name }, c.name); })))
              : h(Inp, { placeholder: 'SHEIN carrier name', value: newCarrier.shein_carrier_name, onChange: function(e) { setNewCarrier(Object.assign({}, newCarrier, { shein_carrier_name: e.target.value })); } })),
          h(Btn, { size: 'sm', onClick: addCarrierMapping, disabled: !newCarrier.shopify_company.trim() || !newCarrier.shein_carrier_name.trim() }, h(LIcon, { name: 'plus', size: 14 }), ' Add Mapping')) : null,
        h('div', { style: { fontSize: 12, color: C.textMuted, lineHeight: 1.6 } },
          carriers.carriers.channels.length
            ? ['SHEIN’s carriers for this store' + (carriers.at ? ' (read ' + _shFmtDateTime(carriers.at) + ')' : '') + ': ' + carriers.carriers.channels.map(function(c) { return c.name; }).join(', ') + '. ']
            : ['SHEIN’s carrier list has not been read yet. '],
          isAdmin ? h('button', { onClick: refreshCarriers, style: { background: 'none', border: 'none', color: C.accent, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', padding: 0 } }, 'Read It Again') : null)
      )
    )
  );
}

window.ShopifySheinPage = ShopifySheinPage;

;
/* ===== app-chat.js ===== */
// AI Chat module — full page (ChatPage) + floating quick-ask widget (ChatWidget).
// Admin-only (area 'chat'). Talks to /api/chat/* (routes/chat.js); assistant
// replies stream over SSE. Exports: window.ChatPage, window.ChatWidget.
(function () {
  var h = React.createElement;

  // ─── Markdown-lite renderer (bold, inline code, fences, tables, lists, headers) ───
  function mdInline(text, keyBase) {
    var parts = String(text).split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
    return parts.map(function (p, i) {
      if (/^\*\*[^*]+\*\*$/.test(p)) return h('strong', { key: keyBase + ':' + i }, p.slice(2, -2));
      if (/^`[^`]+`$/.test(p)) return h('code', { key: keyBase + ':' + i, style: { background: C.surfaceHover, border: '1px solid ' + C.border, borderRadius: 4, padding: '0 4px', fontSize: '0.92em' } }, p.slice(1, -1));
      return p;
    });
  }

  function mdRender(text) {
    var lines = String(text || '').split('\n');
    var out = [], i = 0, key = 0;
    function tableAt(idx) { // returns rows count if a md table starts at idx
      if (!/^\s*\|.*\|\s*$/.test(lines[idx] || '')) return 0;
      if (!/^\s*\|[\s:|-]+\|\s*$/.test(lines[idx + 1] || '')) return 0;
      var n = 2;
      while (/^\s*\|.*\|\s*$/.test(lines[idx + n] || '')) n++;
      return n;
    }
    while (i < lines.length) {
      var line = lines[i];
      if (/^```/.test(line)) {
        var code = []; i++;
        while (i < lines.length && !/^```/.test(lines[i])) { code.push(lines[i]); i++; }
        i++;
        out.push(h('pre', { key: key++, style: { background: C.bg, border: '1px solid ' + C.border, borderRadius: 8, padding: 10, fontSize: 11.5, overflowX: 'auto', margin: '6px 0' } }, code.join('\n')));
        continue;
      }
      var tn = tableAt(i);
      if (tn) {
        var cells = function (l) { return l.replace(/^\s*\||\|\s*$/g, '').split('|').map(function (c) { return c.trim(); }); };
        var head = cells(lines[i]);
        var rows = [];
        for (var r = 2; r < tn; r++) rows.push(cells(lines[i + r]));
        out.push(h('div', { key: key++, style: { overflowX: 'auto', margin: '6px 0' } },
          h('table', { style: { borderCollapse: 'collapse', fontSize: 12, minWidth: 240 } },
            h('thead', null, h('tr', null, head.map(function (c, ci) {
              return h('th', { key: ci, style: { textAlign: 'left', padding: '4px 10px', borderBottom: '1px solid ' + C.border, color: C.textMuted, fontWeight: 600, whiteSpace: 'nowrap' } }, mdInline(c, 'th' + ci));
            }))),
            h('tbody', null, rows.map(function (row, ri) {
              return h('tr', { key: ri }, row.map(function (c, ci) {
                return h('td', { key: ci, style: { padding: '3px 10px', borderBottom: '1px solid ' + C.border, whiteSpace: 'nowrap' } }, mdInline(c, 'td' + ri + ':' + ci));
              }));
            }))
          )));
        i += tn;
        continue;
      }
      var mHead = /^(#{1,4})\s+(.*)$/.exec(line);
      if (mHead) {
        out.push(h('div', { key: key++, style: { fontWeight: 700, fontSize: mHead[1].length <= 2 ? 14 : 12.5, margin: '10px 0 4px' } }, mdInline(mHead[2], 'h' + key)));
        i++; continue;
      }
      if (/^\s*([-*]|\d+\.)\s+/.test(line)) {
        var items = [];
        while (i < lines.length && /^\s*([-*]|\d+\.)\s+/.test(lines[i])) {
          items.push(lines[i].replace(/^\s*([-*]|\d+\.)\s+/, ''));
          i++;
        }
        out.push(h('ul', { key: key++, style: { margin: '4px 0', paddingLeft: 20 } }, items.map(function (it, ii) {
          return h('li', { key: ii, style: { margin: '2px 0' } }, mdInline(it, 'li' + ii));
        })));
        continue;
      }
      if (line.trim() === '') { out.push(h('div', { key: key++, style: { height: 6 } })); i++; continue; }
      out.push(h('div', { key: key++, style: { margin: '2px 0' } }, mdInline(line, 'p' + key)));
      i++;
    }
    return out;
  }

  // ─── Shared bits ───────────────────────────────────────────────────────────
  function toolChip(t, i) {
    return h('span', { key: i, style: { display: 'inline-flex', alignItems: 'center', gap: 5, background: C.accentSoft, border: '1px solid ' + C.border, color: C.textMuted, borderRadius: 12, padding: '2px 9px', fontSize: 10.5, marginRight: 6, marginBottom: 4 } },
      h(LIcon, { name: t.running ? 'loader-2' : 'wrench', size: 11, spin: !!t.running, color: C.accent }),
      t.label || t.name,
      t.summary ? h('span', { style: { color: C.textDim } }, '· ' + t.summary) : null);
  }

  function extractStored(m) {
    // Map a stored chat_messages row to renderable {kind, text, tools}
    var blocks = Array.isArray(m.content) ? m.content : [];
    if (m.role === 'user') {
      if (blocks.some(function (b) { return b.type === 'tool_result'; })) return null; // internal
      var text = blocks.filter(function (b) { return b.type === 'text' && !/^\[context\]/.test(b.text || ''); })
        .map(function (b) { return b.text; }).join('\n');
      return text ? { kind: 'user', text: text } : null;
    }
    var tools = blocks.filter(function (b) { return b.type === 'tool_use'; })
      .map(function (b) { return { name: b.name, label: window._chatToolLabel(b) }; });
    var atext = blocks.filter(function (b) { return b.type === 'text'; }).map(function (b) { return b.text; }).join('');
    if (!tools.length && !atext) return null;
    return { kind: 'assistant', text: atext, tools: tools, usage: m.usage, cost: m.cost };
  }

  window._chatToolLabel = function (b) {
    var inp = b.input || {};
    switch (b.name) {
      case 'run_report': return 'Report: ' + (inp.report_id || '');
      case 'days_of_stock': return 'Days of stock';
      case 'inventory_valuation': return 'Inventory valuation';
      case 'product_lookup': return 'Lookup: ' + (inp.query || '');
      case 'list_tables': return 'List tables';
      case 'describe_table': return 'Describe: ' + (inp.table || '');
      case 'sql_query': return inp.purpose ? 'SQL: ' + inp.purpose : 'SQL query';
      default: return b.name;
    }
  };

  // ─── ChatThread — message list + composer + streaming, for one conversation ─
  function ChatThread(props) {
    var convId = props.conversationId;
    var compact = props.compact;
    var _m = useState(null); var conv = _m[0], setConv = _m[1];
    var _s = useState(null); var live = _s[0], setLive = _s[1]; // {text, tools, status}
    var _i = useState(''); var input = _i[0], setInput = _i[1];
    var _b = useState(false); var busy = _b[0], setBusy = _b[1];
    var _e = useState(null); var err = _e[0], setErr = _e[1];
    var scrollRef = useRef(null);
    var abortRef = useRef(null);
    var liveRef = useRef(null);
    var loadSeqRef = useRef(0); // review M7 #105: switching conversations mid-load must not paint the old one

    function load(id) {
      var target = id || convId;
      if (!target) { setConv(null); return; }
      var seq = ++loadSeqRef.current;
      api('/chat/conversations/' + target)
        .then(function (c) { if (seq === loadSeqRef.current) setConv(c); })
        .catch(function (e) { if (seq === loadSeqRef.current) setErr(e.message); });
    }
    useEffect(function () { load(); }, [convId]);
    useEffect(function () {
      var el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });

    function updateLive(fn) {
      liveRef.current = fn(liveRef.current || { text: '', tools: [], status: null });
      setLive(Object.assign({}, liveRef.current));
    }

    async function sendMessage() {
      var text = input.trim();
      if (!text || busy) return;
      var cid = convId;
      if (!cid) {
        // Conversations are created on first send — never empty shells.
        if (!props.ensureConversation) return;
        try { cid = await props.ensureConversation(); } catch (e) { setErr(e.message); return; }
        if (!cid) return;
      }
      setInput(''); setErr(null); setBusy(true);
      liveRef.current = { text: '', tools: [], status: null, userText: text };
      setLive(Object.assign({}, liveRef.current));
      var ctrl = new AbortController();
      abortRef.current = ctrl;
      try {
        var resp = await fetch(API_BASE + '/chat/conversations/' + cid + '/message', {
          method: 'POST', credentials: 'include', signal: ctrl.signal,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: text, model: props.model }),
        });
        if (!resp.ok) {
          var eb = await resp.json().catch(function () { return {}; });
          throw new Error(eb.error || 'Request failed (' + resp.status + ')');
        }
        var reader = resp.body.getReader();
        var dec = new TextDecoder();
        var buf = '';
        while (true) {
          var chunk = await reader.read();
          if (chunk.done) break;
          buf += dec.decode(chunk.value, { stream: true });
          var idx;
          while ((idx = buf.indexOf('\n\n')) !== -1) {
            var frame = buf.slice(0, idx); buf = buf.slice(idx + 2);
            frame.split('\n').forEach(function (line) {
              if (!line.startsWith('data: ')) return;
              var ev; try { ev = JSON.parse(line.slice(6)); } catch (_) { return; }
              if (ev.type === 'text') updateLive(function (l) { l.text += ev.delta; l.status = null; return l; });
              else if (ev.type === 'status') updateLive(function (l) { l.status = ev.status; return l; });
              else if (ev.type === 'tool') updateLive(function (l) { l.tools.push({ name: ev.name, label: ev.label, running: true }); l.status = null; return l; });
              else if (ev.type === 'tool_done') updateLive(function (l) {
                for (var k = l.tools.length - 1; k >= 0; k--) if (l.tools[k].name === ev.name && l.tools[k].running) { l.tools[k].running = false; l.tools[k].summary = ev.summary; break; }
                return l;
              });
              else if (ev.type === 'usage') updateLive(function (l) { l.usage = ev; return l; });
              else if (ev.type === 'error') { setErr(ev.message); }
            });
          }
        }
      } catch (e) {
        if (e.name !== 'AbortError') setErr(e.message);
      } finally {
        abortRef.current = null;
        setBusy(false);
        liveRef.current = null;
        setLive(null);
        load(cid);
        if (props.onActivity) props.onActivity();
      }
    }

    function stop() { if (abortRef.current) abortRef.current.abort(); }

    var msgs = ((conv && conv.messages) || []).map(extractStored).filter(Boolean);
    var fs = 14; // 9-05: was 12 (widget) / 13 (page) — too small to read comfortably

    function bubble(m, i) {
      if (m.kind === 'user') {
        return h('div', { key: i, style: { display: 'flex', justifyContent: 'flex-end', margin: '8px 0' } },
          h('div', { style: { background: C.accentSoft, border: '1px solid ' + C.border, color: C.text, borderRadius: '12px 12px 2px 12px', padding: '9px 13px', maxWidth: '82%', fontSize: fs, whiteSpace: 'pre-wrap' } }, m.text));
      }
      return h('div', { key: i, style: { margin: '8px 0' } },
        m.tools.length ? h('div', { style: { marginBottom: 4 } }, m.tools.map(toolChip)) : null,
        m.text ? h('div', { style: { background: C.surface, border: '1px solid ' + C.border, color: C.text, borderRadius: '12px 12px 12px 2px', padding: '10px 13px', maxWidth: '95%', fontSize: fs, lineHeight: 1.55 } }, mdRender(m.text)) : null);
    }

    return h('div', { style: { display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 } },
      h('div', { ref: scrollRef, style: { flex: 1, overflowY: 'auto', padding: compact ? '10px 12px' : '4px 4px 12px', minHeight: 0 } },
        convId && !conv ? h('div', { style: { color: C.textDim, fontSize: 14, textAlign: 'center', marginTop: 40 } }, 'Loading…') :
        msgs.length === 0 && !live ? h('div', { style: { color: C.textDim, fontSize: 14, textAlign: 'center', marginTop: 40 } },
          'Ask about sales, inventory, purchasing, shipments…', h('br'), h('span', { style: { fontSize: 12.5 } }, 'e.g. "top 10 overstocked SKUs by tied-up value"')) : null,
        msgs.map(bubble),
        live ? [
          live.userText ? bubble({ kind: 'user', text: live.userText }, 'lu') : null,
          h('div', { key: 'la', style: { margin: '8px 0' } },
            live.tools.length ? h('div', { style: { marginBottom: 4 } }, live.tools.map(toolChip)) : null,
            (live.text || live.status) ? h('div', { style: { background: C.surface, border: '1px solid ' + C.border, color: C.text, borderRadius: '12px 12px 12px 2px', padding: '10px 13px', maxWidth: '95%', fontSize: fs, lineHeight: 1.55 } },
              live.text ? mdRender(live.text) : null,
              !live.text && live.status === 'thinking' ? h('span', { style: { color: C.textDim, fontStyle: 'italic', fontSize: 13 } }, 'Thinking…') : null,
              busy ? h('span', { style: { display: 'inline-block', width: 7, height: 13, background: C.accent, marginLeft: 3, verticalAlign: 'text-bottom', opacity: 0.8 } }) : null) : null,
            live.usage ? h('div', { style: { color: C.textDim, fontSize: 11.5, marginTop: 4 } },
              '$' + Number(live.usage.cost).toFixed(3) + ' · ' + Math.round((live.usage.input_tokens + live.usage.cache_read_input_tokens + live.usage.cache_creation_input_tokens) / 1000) + 'k in / ' + Math.round(live.usage.output_tokens / 1000 * 10) / 10 + 'k out' + (live.usage.tool_calls ? ' · ' + live.usage.tool_calls + ' tools' : '')) : null)
        ] : null,
        err ? h('div', { style: { color: C.danger, fontSize: 13, margin: '8px 0' } }, err) : null),
      h('div', { style: { borderTop: '1px solid ' + C.border, padding: compact ? '8px 10px' : '10px 4px 0', display: 'flex', gap: 8, alignItems: 'flex-end' } },
        h('textarea', {
          value: input, rows: compact ? 3 : 2,
          placeholder: busy ? 'Answering…' : 'Ask a question… (Enter to send)',
          onChange: function (ev) { setInput(ev.target.value); },
          onKeyDown: function (ev) { if (ev.key === 'Enter' && !ev.shiftKey) { ev.preventDefault(); sendMessage(); } },
          style: { flex: 1, resize: 'none', background: C.surface, color: C.text, border: '1px solid ' + C.border, borderRadius: 10, padding: '10px 12px', fontSize: fs, fontFamily: 'inherit', outline: 'none' },
        }),
        busy
          ? h('button', { onClick: stop, style: { background: C.dangerSoft, color: C.danger, border: '1px solid ' + C.border, borderRadius: 10, padding: '10px 14px', cursor: 'pointer', fontSize: 13.5, fontFamily: 'inherit' } }, 'Stop')
          : (function () {
              var canSend = !!input.trim() && (!!convId || !!props.ensureConversation);
              return h('button', { onClick: sendMessage, disabled: !canSend, style: { background: canSend ? C.accent : C.surfaceHover, color: canSend ? '#fff' : C.textDim, border: 'none', borderRadius: 10, padding: '10px 14px', cursor: canSend ? 'pointer' : 'default', fontSize: 12, display: 'flex', alignItems: 'center', gap: 5 } },
                h(LIcon, { name: 'send', size: 16, color: canSend ? '#fff' : C.textDim }), compact ? null : 'Send');
            })()));
  }

  // ─── Model picker (Opus / Haiku) ───────────────────────────────────────────
  function ModelPicker(props) {
    return h('select', {
      value: props.value,
      onChange: function (ev) { props.onChange(ev.target.value); },
      style: { background: C.surface, color: C.textMuted, border: '1px solid ' + C.border, borderRadius: 8, fontSize: 12.5, padding: '5px 8px', outline: 'none', fontFamily: 'inherit' },
    },
      h('option', { value: 'fable' }, 'Fable 5.1 (most capable)'),
      h('option', { value: 'opus' }, 'Opus 5 (smart)'),
      h('option', { value: 'haiku' }, 'Haiku (fast/cheap)'));
  }
  function modelLabel(m) { return m === 'haiku' ? 'Haiku' : m === 'fable' ? 'Fable 5.1' : 'Opus'; }

  // ─── Full page ─────────────────────────────────────────────────────────────
  function ChatPage(props) {
    var _c = useState([]); var convs = _c[0], setConvs = _c[1];
    var _sel = useState(null); var selId = _sel[0], setSelId = _sel[1];
    var _mdl = useState('opus'); var model = _mdl[0], setModel = _mdl[1];

    function loadConvs(selectFirst) {
      api('/chat/conversations').then(function (rows) {
        setConvs(rows);
        if (selectFirst && rows.length && !selId) setSelId(rows[0].id);
      }).catch(function () {});
    }
    useEffect(function () { loadConvs(true); }, []);
    useEffect(function () {
      var cur = convs.find(function (c) { return c.id === selId; });
      if (cur) setModel(cur.model || 'opus');
    }, [selId]);

    // Conversations are created on first send (never empty shells) — "+ New"
    // just clears the selection so the composer targets a fresh chat.
    function newChat() { setSelId(null); }
    async function ensureConversation() {
      var c = await api('/chat/conversations', { method: 'POST', body: JSON.stringify({ model: model }) });
      setConvs(function (prev) { return [c].concat(prev); });
      setSelId(c.id);
      return c.id;
    }
    async function delChat(id, ev) {
      ev.stopPropagation();
      if (!window.confirm('Delete this conversation?')) return;
      await api('/chat/conversations/' + id, { method: 'DELETE' });
      if (selId === id) setSelId(null);
      loadConvs(false);
    }
    function changeModel(m) {
      setModel(m);
      if (selId) api('/chat/conversations/' + selId, { method: 'PATCH', body: JSON.stringify({ model: m }) }).catch(function () {});
    }

    var totalCost = convs.reduce(function (s, c) { return s + (parseFloat(c.total_cost) || 0); }, 0);

    return h('div', { style: { display: 'flex', gap: 16, height: 'calc(100vh - 110px)', minHeight: 480 } },
      // Conversation list
      h('div', { style: { width: 250, flexShrink: 0, display: 'flex', flexDirection: 'column', background: C.surface, border: '1px solid ' + C.border, borderRadius: 12, overflow: 'hidden' } },
        h('div', { style: { padding: '10px 12px', borderBottom: '1px solid ' + C.border, display: 'flex', alignItems: 'center', justifyContent: 'space-between' } },
          h('div', { style: { fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 } }, h(LIcon, { name: 'sparkles', size: 15, color: C.accent }), 'Ask AI'),
          h('button', { onClick: newChat, style: { background: C.accent, color: '#fff', border: 'none', borderRadius: 8, padding: '4px 9px', fontSize: 11.5, cursor: 'pointer' } }, '+ New')),
        h('div', { style: { flex: 1, overflowY: 'auto' } },
          convs.map(function (c) {
            var sel = c.id === selId;
            return h('div', {
              key: c.id, onClick: function () { setSelId(c.id); },
              style: { padding: '8px 12px', cursor: 'pointer', background: sel ? C.accentSoft : 'transparent', borderLeft: '3px solid ' + (sel ? C.accent : 'transparent'), position: 'relative' },
            },
              h('div', { style: { fontSize: 12, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 18 } }, c.title || 'New chat'),
              h('div', { style: { fontSize: 10, color: C.textDim, marginTop: 2 } },
                modelLabel(c.model) + ' · ' + c.message_count + ' msgs' + (parseFloat(c.total_cost) ? ' · $' + parseFloat(c.total_cost).toFixed(2) : '')),
              h('span', { onClick: function (ev) { delChat(c.id, ev); }, style: { position: 'absolute', right: 8, top: 9, opacity: 0.55, cursor: 'pointer' } }, h(LIcon, { name: 'x', size: 12, color: C.textMuted })));
          })),
        totalCost > 0 ? h('div', { style: { padding: '7px 12px', borderTop: '1px solid ' + C.border, fontSize: 10.5, color: C.textDim } }, 'Total spend: $' + totalCost.toFixed(2)) : null),
      // Thread
      h('div', { style: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, background: C.surface, border: '1px solid ' + C.border, borderRadius: 12, padding: '12px 16px' } },
        h('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: 8, borderBottom: '1px solid ' + C.border } },
          h('div', { style: { fontSize: 12.5, color: C.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } },
            (convs.find(function (c) { return c.id === selId; }) || {}).title || 'Ask questions over live app data'),
          h(ModelPicker, { value: model, onChange: changeModel })),
        h(ChatThread, { conversationId: selId, model: model, ensureConversation: ensureConversation, onActivity: function () { loadConvs(false); } })));
  }

  // ─── Floating widget ───────────────────────────────────────────────────────
  function ChatWidget(props) {
    var _o = useState(false); var open = _o[0], setOpen = _o[1];
    var _c = useState(null); var convId = _c[0], setConvId = _c[1];
    var _mdl = useState('opus'); var model = _mdl[0], setModel = _mdl[1];
    var _v = useState('thread'); var view = _v[0], setView = _v[1]; // 'thread' | 'list'
    var _h = useState([]); var hist = _h[0], setHist = _h[1];
    var restoredRef = useRef(false);

    function toggle() {
      var next = !open;
      setOpen(next);
      if (next && !restoredRef.current) {
        restoredRef.current = true;
        var saved = parseInt(localStorage.getItem('chat_widget_conv') || '', 10);
        if (saved) api('/chat/conversations/' + saved)
          .then(function (c) { setConvId(c.id); setModel(c.model || 'opus'); })
          .catch(function () { localStorage.removeItem('chat_widget_conv'); });
      }
    }
    async function ensureConversation() {
      var nc = await api('/chat/conversations', { method: 'POST', body: JSON.stringify({ model: model }) });
      localStorage.setItem('chat_widget_conv', String(nc.id));
      setConvId(nc.id);
      return nc.id;
    }
    function fresh() { setConvId(null); localStorage.removeItem('chat_widget_conv'); setView('thread'); }
    function openHistory() {
      setView('list');
      api('/chat/conversations').then(setHist).catch(function () { setHist([]); });
    }
    function pick(c) {
      setConvId(c.id); setModel(c.model || 'opus');
      localStorage.setItem('chat_widget_conv', String(c.id));
      setView('thread');
    }
    function changeModel(m) {
      setModel(m);
      if (convId) api('/chat/conversations/' + convId, { method: 'PATCH', body: JSON.stringify({ model: m }) }).catch(function () {});
    }

    var header = view === 'list'
      ? h('div', { style: { padding: '11px 14px', borderBottom: '1px solid ' + C.border, display: 'flex', alignItems: 'center', gap: 10 } },
          h('span', { onClick: function () { setView('thread'); }, title: 'Back', style: { cursor: 'pointer', display: 'flex' } }, h(LIcon, { name: 'chevron-left', size: 18, color: C.textMuted })),
          h('div', { style: { fontWeight: 700, fontSize: 14.5, flex: 1 } }, 'Conversations'),
          h('span', { onClick: fresh, title: 'New conversation', style: { cursor: 'pointer', opacity: 0.7, display: 'flex' } }, h(LIcon, { name: 'plus', size: 18, color: C.textMuted })),
          h('span', { onClick: toggle, style: { cursor: 'pointer', opacity: 0.7, display: 'flex' } }, h(LIcon, { name: 'x', size: 18, color: C.textMuted })))
      : h('div', { style: { padding: '11px 14px', borderBottom: '1px solid ' + C.border, display: 'flex', alignItems: 'center', gap: 10 } },
          h('span', { onClick: openHistory, title: 'All conversations', style: { cursor: 'pointer', display: 'flex' } }, h(LIcon, { name: 'history', size: 18, color: C.textMuted })),
          h(LIcon, { name: 'sparkles', size: 18, color: C.accent }),
          h('div', { style: { fontWeight: 700, fontSize: 14.5, flex: 1 } }, 'Ask AI'),
          h(ModelPicker, { value: model, onChange: changeModel }),
          h('span', { onClick: fresh, title: 'New conversation', style: { cursor: 'pointer', opacity: 0.7, display: 'flex' } }, h(LIcon, { name: 'plus', size: 18, color: C.textMuted })),
          props.onOpenFull ? h('span', { onClick: function () { setOpen(false); props.onOpenFull(); }, title: 'Open full page', style: { cursor: 'pointer', opacity: 0.7, display: 'flex' } }, h(LIcon, { name: 'maximize-2', size: 14, color: C.textMuted })) : null,
          h('span', { onClick: toggle, style: { cursor: 'pointer', opacity: 0.7, display: 'flex' } }, h(LIcon, { name: 'x', size: 18, color: C.textMuted })));

    var listBody = h('div', { style: { flex: 1, overflowY: 'auto', minHeight: 0 } },
      hist.length === 0 ? h('div', { style: { color: C.textDim, fontSize: 13.5, textAlign: 'center', marginTop: 30 } }, 'No conversations yet') : null,
      hist.map(function (c) {
        return h('div', { key: c.id, onClick: function () { pick(c); }, style: { padding: '11px 14px', cursor: 'pointer', borderBottom: '1px solid ' + C.border, background: c.id === convId ? C.accentSoft : 'transparent' } },
          h('div', { style: { fontSize: 13.5, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, c.title || 'New chat'),
          h('div', { style: { fontSize: 11.5, color: C.textDim, marginTop: 3 } },
            modelLabel(c.model) + ' · ' + c.message_count + ' msgs · ' + new Date(c.updated_at).toLocaleDateString() + (parseFloat(c.total_cost) ? ' · $' + parseFloat(c.total_cost).toFixed(2) : '')));
      }));

    return h(React.Fragment, null,
      open ? h('div', { style: { position: 'fixed', bottom: 92, right: 20, width: 470, height: 'min(720px, 82vh)', background: C.surface, border: '1px solid ' + C.border, borderRadius: 14, boxShadow: '0 12px 40px rgba(0,0,0,0.45)', zIndex: 940, display: 'flex', flexDirection: 'column', overflow: 'hidden' } },
        header,
        view === 'list' ? listBody :
        h('div', { style: { flex: 1, minHeight: 0 } },
          h(ChatThread, { conversationId: convId, model: model, compact: true, ensureConversation: ensureConversation }))) : null,
      h('button', {
        onClick: toggle, title: 'Ask AI',
        style: { position: 'fixed', bottom: 20, right: 20, width: 54, height: 54, borderRadius: 27, background: C.accent, border: 'none', cursor: 'pointer', zIndex: 940, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 6px 20px rgba(79,110,247,0.45)' } },
        h(LIcon, { name: open ? 'chevron-down' : 'sparkles', size: 24, color: '#fff' })));
  }

  window.ChatPage = ChatPage;
  window.ChatWidget = ChatWidget;
})();

;
/* ===== app-office.js ===== */
// ─── The Office (AI Agents) ───
// AgentOfficePage — a full office floor (dollhouse cutaway): cubicle row with
// partitions, break area (water cooler, sofa), kitchen (counter, coffee
// machine, fridge), ceiling lamps, night windows, a roomba roaming the floor.
// Every AI agent we run gets a cubicle; agents on break physically walk to the
// kitchen. Clicking a cubicle zooms into that agent — close-up desk scene,
// profile stats, full task history, shift log. Status comes from
// GET /agent-office/status (read-only over the agent's own tables).
// Art: flat vector SVG drawn inline (no libs), animated with eased CSS
// keyframes — typing, blinking, chat bubbles on monitors, steam, walk cycles.

var { createElement: h, Fragment } = React;

// ── Shared palette ──
var OFC_SKIN = '#E8B98A', OFC_HAIR = '#4A3B2F';
// Long hair as ONE smooth mass: swept fringe over the crown, volume behind the
// head, tapering sheet flowing down past the shoulder. Drawn over the head
// circle (face stays open). Sized to the close-up head (c 88,62 r17); the
// walker/mini variants are the same shape scaled to their head sizes.
var OFC_HAIR_LONG = 'M 101,49 Q 94,40.5 86,40.8 Q 72,42 66,53 Q 63,60 62.5,70 Q 60,84 60.5,94 Q 61,103 67,106 Q 72,108 77,105 Q 75.5,95 75.5,86 Q 75.5,76 73.5,68 Q 74,58 80,52 Q 88,46.5 95,48 Q 99,48.6 101,49 Z';
var OFC_HAIR_LONG_WALKER = 'M 8.8,-76.8 Q 4.1,-82.5 -1.4,-82.3 Q -10.8,-81.5 -14.9,-74.1 Q -16.9,-69.4 -17.2,-62.6 Q -18.9,-53.1 -18.6,-46.4 Q -18.3,-40.3 -14.2,-38.3 Q -10.8,-36.9 -7.4,-38.9 Q -8.4,-45.7 -8.4,-51.8 Q -8.4,-58.5 -9.8,-63.9 Q -9.5,-70.7 -5.4,-74.8 Q 0,-78.5 4.7,-77.5 Q 7.4,-77.1 8.8,-76.8 Z';
var OFC_HAIR_LONG_MINI = 'M 4.6,-41.6 Q 2.1,-44.6 -0.7,-44.5 Q -5.6,-44.1 -7.8,-40.2 Q -8.8,-37.7 -9,-34.2 Q -9.9,-29.2 -9.7,-25.7 Q -9.5,-22.5 -7.4,-21.5 Q -5.6,-20.8 -3.9,-21.8 Q -4.4,-25.3 -4.4,-28.5 Q -4.4,-32.1 -5.1,-34.9 Q -4.9,-38.4 -2.8,-40.5 Q 0,-42.5 2.5,-41.9 Q 3.9,-41.7 4.6,-41.6 Z';
// Short hair: solid cap with a real hairline, crown volume, tapered nape.
var OFC_HAIR_SHORT = 'M 102,52 Q 96,42.5 87,42 Q 74,42.5 68.5,52 Q 66,58 66,66 Q 65.8,72 69,75.5 Q 72.5,73 72.5,68 Q 72,58 79,52.5 Q 87,47.5 95,49 Q 100,50 102,52 Z';
var OFC_HAIR_SHORT_WALKER = 'M 9.5,-74.8 Q 5.4,-81.2 -0.7,-81.5 Q -9.5,-81.2 -13.2,-74.8 Q -14.9,-70.7 -14.9,-65.3 Q -15,-61.2 -12.9,-58.9 Q -10.5,-60.6 -10.5,-63.9 Q -10.8,-70.7 -6.1,-74.4 Q -0.7,-77.8 4.7,-76.8 Q 8.1,-76.1 9.5,-74.8 Z';
// ── Per-agent look (Anthony 2026-09-06: "different hair styles, jewelry, make up") ──
// Derived from the agent id (same unsigned-shift hashing as officeDecor) so it never changes and is
// identical at every zoom; a server-side agent.look object pins any field. The agent's long/short
// hair flag still decides the presentation, so names keep matching their figures. No makeup
// (Anthony 2026-09-06: lipstick and blush looked creepy at the close-up).
var OFC_HAIR_COLORS = ['#4A3B2F', '#1F1A17', '#6B4226', '#8C3B22', '#C99A4B', '#B8BCC4'];
var OFC_SKINS = ['#F3D2B3', '#E8B98A', '#C98E62', '#9C6644', '#6B4530'];
var OFC_STYLES_F = ['long', 'bob', 'bun', 'ponytail', 'curly'];
var OFC_STYLES_M = ['short', 'crop', 'curly', 'sidepart'];
function officeLook(a) {
  var id = (a && a.id) || 'x';
  var hsh = 0;
  for (var i = 0; i < id.length; i++) hsh = (hsh * 31 + id.charCodeAt(i) + 7) >>> 0;
  var f = (a && a.hair) === 'long';
  var styles = f ? OFC_STYLES_F : OFC_STYLES_M;
  var look = {
    presentation: f ? 'f' : 'm',
    hairStyle: styles[hsh % styles.length],
    hairColor: OFC_HAIR_COLORS[(hsh >>> 4) % OFC_HAIR_COLORS.length],
    skin: OFC_SKINS[(hsh >>> 7) % OFC_SKINS.length],
    glasses: ((hsh >>> 10) % 5) < 2 ? (((hsh >>> 12) & 1) ? 'round' : 'rect') : null,
    earrings: f && ((hsh >>> 13) % 3) !== 0 ? (((hsh >>> 15) & 1) ? 'hoop' : 'stud') : null,
    necklace: f && ((hsh >>> 16) % 2) === 0,
    beard: !f && ((hsh >>> 19) % 3) === 0,
    // the headset stays a customer-facing thing: Customer Support desks and the front desk
    headset: !!(a && (a.dept === 'Customer Support' || a.dept === 'Reception')),
  };
  if (a && a.look) Object.assign(look, a.look);
  return look;
}
// untagged scenery figures (ambient walkers) keep one stable look per presentation
function officeLookFromHair(hair) { return officeLook({ id: hair === 'long' ? 'ambient-f' : 'ambient-m', hair: hair }); }
// Head geometry per scale: the hand-drawn base caps stay; everything else is anchored to (cx, cy, r)
var OFC_HEAD_CLOSE = { cx: 88, cy: 62, r: 17, capLong: OFC_HAIR_LONG, capShort: OFC_HAIR_SHORT };
var OFC_HEAD_WALKER = { cx: 0, cy: -68, r: 11.5, capLong: OFC_HAIR_LONG_WALKER, capShort: OFC_HAIR_SHORT_WALKER };
// Filled crescent between two radii over an angle span (degrees, 0 = front/right, 270 = top)
function officeCrescent(cx, cy, ro, ri, a0, a1) {
  var P = function(rad, a) { var t = a * Math.PI / 180; return (cx + Math.cos(t) * rad).toFixed(2) + ',' + (cy + Math.sin(t) * rad).toFixed(2); };
  var large = Math.abs(a1 - a0) > 180 ? 1 : 0;
  return 'M ' + P(ro, a0) + ' A ' + ro + ',' + ro + ' 0 ' + large + ' 1 ' + P(ro, a1) + ' L ' + P(ri, a1) + ' A ' + ri + ',' + ri + ' 0 ' + large + ' 0 ' + P(ri, a0) + ' Z';
}
function officeArc(cx, cy, r, a0, a1) { // degrees, SVG clockwise on screen
  var p = function(a) { var t = a * Math.PI / 180; return (cx + Math.cos(t) * r).toFixed(2) + ',' + (cy + Math.sin(t) * r).toFixed(2); };
  return 'M ' + p(a0) + ' A ' + r + ',' + r + ' 0 ' + (Math.abs(a1 - a0) > 180 ? 1 : 0) + ' 1 ' + p(a1);
}
// The head with hair, face and accessories at one scale. level: 'close' | 'walker' | 'mini'.
// Order: hair behind the head, necklace, head, makeup, eye, beard, hair cap, glasses, earrings, headset.
function officeHeadGroup(lk, g, level) {
  var r = g.r, cx = g.cx, cy = g.cy, hc = lk.hairColor, f = lk.presentation === 'f', st = lk.hairStyle;
  var k = [];
  if (st === 'bob') k.push(h('rect', { key: 'hb', x: cx - 1.1 * r, y: cy - 0.62 * r, width: 1.45 * r, height: 1.58 * r, rx: 0.42 * r, fill: hc })); // hugs the back of the neck, ends at the jaw
  if (st === 'bun') k.push(h('circle', { key: 'hb', cx: cx - 0.85 * r, cy: cy - 0.6 * r, r: 0.42 * r, fill: hc }));
  if (st === 'ponytail') k.push(h('ellipse', { key: 'hb', cx: cx - 1.02 * r, cy: cy + 0.35 * r, rx: 0.3 * r, ry: 0.9 * r, fill: hc, transform: 'rotate(10 ' + (cx - 1.02 * r) + ' ' + (cy + 0.35 * r) + ')' }));
  if (st === 'curly') { // a halo of curls behind the head; the long-hair set gets a second, fuller ring down the back
    var curls = [];
    var ring = function(rad, cr, a0, a1, step) { for (var a = a0; a <= a1; a += step) { var t = a * Math.PI / 180; curls.push(h('circle', { key: 'c' + rad.toFixed(1) + '-' + a, cx: cx + Math.cos(t) * rad, cy: cy + Math.sin(t) * rad, r: cr, fill: hc })); } };
    ring(0.92 * r, 0.34 * r, f ? 100 : 175, f ? 345 : 350, f ? 15 : 17.5);
    if (f) ring(1.22 * r, 0.36 * r, 120, 275, 16);
    k.push(h('g', { key: 'hb' }, curls));
  }
  if (level === 'close' && lk.necklace) {
    k.push(h('path', { key: 'nk', d: 'M ' + (cx - 0.4 * r) + ',' + (cy + 1.0 * r) + ' Q ' + cx + ',' + (cy + 1.42 * r) + ' ' + (cx + 0.4 * r) + ',' + (cy + 1.0 * r), stroke: '#F2CF6B', strokeWidth: 0.09 * r, fill: 'none' }));
    k.push(h('circle', { key: 'nk2', cx: cx, cy: cy + 1.38 * r, r: 0.09 * r, fill: '#F2CF6B' }));
  }
  k.push(h('circle', { key: 'hd', cx: cx, cy: cy, r: r, fill: lk.skin }));
  if (level !== 'mini') k.push(h('g', { key: 'ey', className: level === 'close' ? 'ofc-eye' : undefined }, h('circle', { cx: cx + 0.5 * r, cy: cy - 0.05 * r, r: 0.14 * r, fill: '#1F2937' })));
  if (lk.beard && level !== 'mini') k.push(h('path', { key: 'bd', d: 'M ' + (cx - 0.3 * r) + ',' + (cy + 0.55 * r) + ' A ' + r + ',' + r + ' 0 0 0 ' + (cx + 0.9 * r) + ',' + (cy + 0.35 * r), stroke: hc, strokeWidth: 0.3 * r, fill: 'none', strokeLinecap: 'round' }));
  if (st === 'crop') k.push(h('path', { key: 'cap', d: officeCrescent(cx, cy, 1.03 * r, 0.82 * r, 188, 352), fill: hc })); // close-cropped: a thin filled cap
  else k.push(h('path', { key: 'cap', d: st === 'long' ? g.capLong : g.capShort, fill: hc }));
  if (st === 'sidepart') k.push(h('path', { key: 'sp', d: officeCrescent(cx, cy, 1.04 * r, 0.68 * r, 285, 352), fill: hc })); // fringe swept forward over the temple
  if (level !== 'mini' && lk.glasses) {
    var gx = cx + 0.52 * r, gy = cy - 0.05 * r, gr = 0.3 * r;
    k.push(lk.glasses === 'round'
      ? h('circle', { key: 'gl', cx: gx, cy: gy, r: gr, fill: 'rgba(255,255,255,0.10)', stroke: '#2B3444', strokeWidth: 0.09 * r })
      : h('rect', { key: 'gl', x: gx - gr, y: gy - gr * 0.8, width: gr * 2, height: gr * 1.6, rx: 0.08 * r, fill: 'rgba(255,255,255,0.10)', stroke: '#2B3444', strokeWidth: 0.09 * r }));
    k.push(h('path', { key: 'gl2', d: 'M ' + (gx - gr) + ',' + gy + ' L ' + (cx - 0.55 * r) + ',' + (cy - 0.1 * r), stroke: '#2B3444', strokeWidth: 0.08 * r, fill: 'none' }));
  }
  if (level !== 'mini' && lk.earrings) {
    var ex = cx - 0.48 * r, ey = cy + 0.58 * r;
    k.push(lk.earrings === 'hoop'
      ? h('circle', { key: 'er', cx: ex, cy: ey + 0.1 * r, r: 0.16 * r, fill: 'none', stroke: '#F2CF6B', strokeWidth: 0.07 * r })
      : h('circle', { key: 'er', cx: ex, cy: ey, r: 0.09 * r, fill: '#F2CF6B' }));
  }
  if (lk.headset && level !== 'mini') {
    k.push(h('path', { key: 'hs1', d: 'M ' + (cx - 1.0 * r) + ',' + (cy - 0.35 * r) + ' A ' + (1.03 * r) + ',' + (1.03 * r) + ' 0 0 1 ' + (cx + 1.0 * r) + ',' + (cy - 0.35 * r), stroke: '#222B3A', strokeWidth: 0.2 * r, fill: 'none', strokeLinecap: 'round' }));
    k.push(h('circle', { key: 'hs2', cx: cx - 0.88 * r, cy: cy + 0.06 * r, r: 0.29 * r, fill: '#222B3A' }));
    k.push(h('path', { key: 'hs3', d: 'M ' + (cx - 0.88 * r) + ',' + (cy + 0.35 * r) + ' Q ' + (cx - 0.7 * r) + ',' + (cy + 0.82 * r) + ' ' + (cx - 0.12 * r) + ',' + (cy + 0.88 * r), stroke: '#222B3A', strokeWidth: 0.15 * r, fill: 'none', strokeLinecap: 'round' }));
    k.push(h('circle', { key: 'hs4', cx: cx - 0.06 * r, cy: cy + 0.88 * r, r: 0.13 * r, fill: '#222B3A' }));
  }
  return h('g', { key: 'head' }, k);
}

var OFC_HAIR_SHORT_MINI = 'M 4.9,-40.5 Q 2.8,-43.9 -0.4,-44.1 Q -4.9,-43.9 -6.9,-40.5 Q -7.8,-38.4 -7.8,-35.6 Q -7.8,-33.5 -6.7,-32.2 Q -5.5,-33.1 -5.5,-34.9 Q -5.6,-38.4 -3.2,-40.4 Q -0.4,-42.1 2.5,-41.6 Q 4.2,-41.2 4.9,-40.5 Z';
var OFC_DARK = '#39445A', OFC_DARKER = '#2A3346';
var OFC_WOOD = '#B08968', OFC_WOOD_DARK = '#8F6B4E';

function officeEnsureStyle() {
  if (document.getElementById('office-style')) return;
  var st = document.createElement('style');
  st.id = 'office-style';
  st.textContent =
    '.ofc-bob { animation: ofcBob 3.4s ease-in-out infinite; }' +
    '@keyframes ofcBob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-2.5px); } }' +
    '.ofc-arm { transform-box: fill-box; transform-origin: 10% 50%; }' +
    '.ofc-arm.typing { animation: ofcType 0.55s ease-in-out infinite; }' +
    '@keyframes ofcType { 0%,100% { transform: rotate(0deg); } 50% { transform: rotate(3.5deg); } }' +
    '.ofc-eye { animation: ofcBlink 4.6s infinite; transform-box: fill-box; transform-origin: center; }' +
    '@keyframes ofcBlink { 0%, 91%, 100% { transform: scaleY(1); } 93.5%, 95.5% { transform: scaleY(0.12); } }' +
    '.ofc-msg { animation: ofcMsg 4.2s ease-in-out infinite; opacity: 0; transform-box: fill-box; }' +
    '@keyframes ofcMsg { 0% { opacity: 0; transform: translateY(5px); } 8% { opacity: 1; transform: translateY(0); } 78% { opacity: 1; } 92%, 100% { opacity: 0; } }' +
    '.ofc-steam { animation: ofcSteam 2.8s ease-in-out infinite; opacity: 0; }' +
    '@keyframes ofcSteam { 0% { transform: translateY(0); opacity: 0; } 30% { opacity: 0.65; } 100% { transform: translateY(-16px); opacity: 0; } }' +
    '.ofc-pulse { animation: ofcPulse 2.2s ease-in-out infinite; }' +
    '@keyframes ofcPulse { 0%,100% { opacity: 0.3; } 50% { opacity: 1; } }' +
    '.ofc-ring { animation: ofcRing 1.7s ease-out infinite; transform-box: fill-box; transform-origin: center; }' +
    '@keyframes ofcRing { 0% { transform: scale(0.4); opacity: 0.9; } 100% { transform: scale(1.9); opacity: 0; } }' +
    '.ofc-float { animation: ofcFloat 3.2s ease-in-out infinite; }' +
    '@keyframes ofcFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }' +
    // break-time stroll: out to the kitchen (pause for coffee), then back
    '.ofc-stroll { animation: ofcStroll 30s ease-in-out infinite; }' +
    '@keyframes ofcStroll { 0%, 3% { transform: translateX(0); } 38%, 60% { transform: translateX(var(--dx, 600px)); } 95%, 100% { transform: translateX(0); } }' +
    '.ofc-strollFlip { animation: ofcStrollFlip 30s infinite; transform-box: fill-box; transform-origin: center; }' +
    '@keyframes ofcStrollFlip { 0%, 59% { transform: scaleX(1); } 60%, 94% { transform: scaleX(-1); } 95%, 100% { transform: scaleX(1); } }' +
    // restroom trips ride the same 30 s clock as the stroll: the walker is inside (hidden) while the slider reads occupied
    '.ofc-wcHide { animation: ofcWcHide 30s infinite; }' +
    '@keyframes ofcWcHide { 0%, 37% { opacity: 1; } 38.5%, 59.5% { opacity: 0; } 61%, 100% { opacity: 1; } }' +
    '.ofc-wcBusy { animation: ofcWcBusy 30s infinite; }' +
    '@keyframes ofcWcBusy { 0%, 37% { fill: #34D399; transform: translateX(0); } 38.5%, 59.5% { fill: #F87171; transform: translateX(18px); } 61%, 100% { fill: #34D399; transform: translateX(0); } }' +
    '.ofc-legA { animation: ofcLeg 0.62s ease-in-out infinite; transform-box: fill-box; transform-origin: 50% 6%; }' +
    '.ofc-legB { animation: ofcLeg 0.62s ease-in-out infinite; animation-delay: -0.31s; transform-box: fill-box; transform-origin: 50% 6%; }' +
    '@keyframes ofcLeg { 0%,100% { transform: rotate(14deg); } 50% { transform: rotate(-14deg); } }' +
    '.ofc-walkbob { animation: ofcWalkBob 0.31s ease-in-out infinite alternate; }' +
    '@keyframes ofcWalkBob { from { transform: translateY(0); } to { transform: translateY(-1.6px); } }' +
    '.ofc-roomba { animation: ofcRoomba 46s ease-in-out infinite; }' +
    '@keyframes ofcRoomba { 0%, 2% { transform: translateX(0); } 46%, 52% { transform: translateX(var(--rdx, 900px)); } 98%, 100% { transform: translateX(0); } }' +
    // building view: pacing staff, elevator, drifting clouds, passing car
    '.ofc-pace { animation: ofcPace 16s ease-in-out infinite; }' +
    '@keyframes ofcPace { 0%, 6% { transform: translateX(0); } 44%, 56% { transform: translateX(var(--pdx, 120px)); } 94%, 100% { transform: translateX(0); } }' +
    '.ofc-paceFlip { animation: ofcPaceFlip 16s infinite; transform-box: fill-box; transform-origin: center; }' +
    '@keyframes ofcPaceFlip { 0%, 55% { transform: scaleX(1); } 56%, 93% { transform: scaleX(-1); } 94%, 100% { transform: scaleX(1); } }' +
    '.ofc-elev { animation: ofcElev 18s ease-in-out infinite; }' +
    '@keyframes ofcElev { 0%, 12% { transform: translateY(0); } 42%, 62% { transform: translateY(var(--ey, -240px)); } 90%, 100% { transform: translateY(0); } }' +
    '.ofc-cloud { animation: ofcCloud 44s ease-in-out infinite; }' +
    '@keyframes ofcCloud { 0%, 100% { transform: translateX(0); } 50% { transform: translateX(70px); } }' +
    '.ofc-car { animation: ofcCar 26s linear infinite; }' +
    '@keyframes ofcCar { 0%, 55% { transform: translateX(0); } 96%, 100% { transform: translateX(var(--vdx, 1300px)); } }' +
    '.ofc-croc { animation: ofcCroc 36s linear infinite; }' +
    '@keyframes ofcCroc { from { transform: translateX(0); } to { transform: translateX(var(--cdx, 1200px)); } }' +
    // office pets: amble across the floor with a pause, tails always going
    '.ofc-pet { animation: ofcPet 44s ease-in-out infinite; }' +
    '@keyframes ofcPet { 0%, 6% { transform: translateX(0); } 36%, 62% { transform: translateX(var(--petdx, 500px)); } 96%, 100% { transform: translateX(0); } }' +
    '.ofc-petFlip { animation: ofcPetFlip 44s infinite; transform-box: fill-box; transform-origin: center; }' +
    '@keyframes ofcPetFlip { 0%, 61% { transform: scaleX(1); } 62%, 95% { transform: scaleX(-1); } 96%, 100% { transform: scaleX(1); }  }' +
    '.ofc-tailSway { animation: ofcTailSway 3.4s ease-in-out infinite; transform-box: fill-box; transform-origin: 100% 100%; }' +
    '@keyframes ofcTailSway { 0%,100% { transform: rotate(0deg); } 50% { transform: rotate(14deg); } }' +
    '.ofc-tailWag { animation: ofcTailSway 0.55s ease-in-out infinite; transform-box: fill-box; transform-origin: 100% 100%; }' +
    '.ofc-petLegA { animation: ofcLeg 0.42s ease-in-out infinite; transform-box: fill-box; transform-origin: 50% 8%; }' +
    '.ofc-petLegB { animation: ofcLeg 0.42s ease-in-out infinite; animation-delay: -0.21s; transform-box: fill-box; transform-origin: 50% 8%; }' +
    '.ofc-earFlick { animation: ofcEarFlick 6s ease-in-out infinite; transform-box: fill-box; transform-origin: 50% 100%; }' +
    '@keyframes ofcEarFlick { 0%, 90%, 100% { transform: rotate(0deg); } 93%, 96% { transform: rotate(-18deg); } }' +
    '.officedesk-zone { cursor: pointer; border-radius: 12px; transition: background 0.2s ease, box-shadow 0.2s ease; }' +
    '.officedesk-zone:hover { background: rgba(56,189,248,0.07); box-shadow: inset 0 0 0 1px rgba(56,189,248,0.25); }' +
    '@keyframes officezoomin { from { transform: scale(0.72) translateY(14px); opacity: 0.2; } to { transform: scale(1) translateY(0); opacity: 1; } }' +
    '.officezoom { animation: officezoomin 0.42s cubic-bezier(0.16, 1, 0.3, 1); transform-origin: 40% 85%; }' +
    '.officesign { position: absolute; z-index: 5; border-radius: 999px; font-size: 10.5px; font-weight: 700; letter-spacing: 0.8px; padding: 4px 11px; box-shadow: 0 4px 14px rgba(0,0,0,0.35); }';
  document.head.appendChild(st);
}

// ── Close-up desk scene (detail view; also embedded in each cubicle) ──
// viewBox 300×190, ground line at y=172.
var OFC_W = 300, OFC_H = 190;

// Every agent's cubicle is personalized the same way at every zoom level:
// a deterministic decor pack hashed from the agent id (art print, pinboard
// item, plant pot, rug). Same id → same cubicle in building, floor, close-up.
function officeDecor(id) {
  if (!id) return null;
  var hsh = 0;
  for (var i = 0; i < id.length; i++) hsh = (hsh * 31 + id.charCodeAt(i)) >>> 0;
  // >>> keeps every derived index non-negative (>> goes signed past 2^31)
  return {
    art: hsh % 8, pin: (hsh >>> 3) % 6, pot: (hsh >>> 6) % 3, rug: (hsh >>> 8) % 3,
    desk: (hsh >>> 10) % 3, chair: (hsh >>> 12) % 3, side: (hsh >>> 14) % 4,
    plant: (hsh >>> 16) % 5,
  };
}
var OFC_POTS = [['#C2703D', '#A85C30'], ['#56688C', '#46536B'], ['#8A94A2', '#6B7280']];
var OFC_RUGS = [null, 'rgba(86,104,140,0.30)', 'rgba(143,107,78,0.25)'];
var OFC_DESKS = [[OFC_WOOD, OFC_WOOD_DARK], ['#9A7B5F', '#7A5F49'], ['#C29A72', '#9C7854']];
var OFC_CHAIRS = [[OFC_DARK, OFC_DARKER], ['#454055', '#332F40'], ['#3E4A44', '#2E3833']];

// Each department's kitchen has its own look, hashed from the dept name so the
// floor view and the building's mini kitchen strip agree.
// ── Break-area variety (Anthony 2026-09-06: "change some of the couch designs so they're not all the
// same, add a bathroom and different plants. Also allow the agents to sit on the couches when they're
// drinking their coffee on break") ──
// Sofa design = the floor's position in the building (so every floor differs); plant species follow.
var OFC_SOFAS = [
  { name: 'classic',      back: '#4A5A78', seat: '#56688C', arm: '#4A5A78', leg: '#2A3346', w: 160, seats: [40, 108], seatY: 232 },
  { name: 'loveseat',     back: '#2F6F6B', seat: '#3B8783', arm: '#2F6F6B', leg: '#1F4A47', w: 160, seats: [42, 108], seatY: 234 },
  { name: 'midcentury',   back: '#B5832A', seat: '#C9932E', arm: '#8F6B4E', leg: '#8F6B4E', w: 160, seats: [40, 108], seatY: 236 },
  { name: 'chesterfield', back: '#5E4033', seat: '#75503F', arm: '#5E4033', leg: '#2A1F1A', w: 160, seats: [44, 106], seatY: 234 },
  { name: 'sectional',    back: '#4E5D7A', seat: '#5A6B8A', arm: '#4E5D7A', leg: '#2A3346', w: 230, seats: [40, 108, 190], seatY: 232 },
  { name: 'modern',       back: '#C95F47', seat: '#DA7059', arm: '#C95F47', leg: '#8A94A2', w: 160, seats: [40, 106], seatY: 232 },
];
var OFC_PLANT_SPECIES = ['fronds', 'monstera', 'snake', 'rubber', 'cactus'];
function officeSofaVariantFromFloors(dept, floors) {
  var i = -1;
  (floors || []).forEach(function(fl, idx) { if (i < 0 && fl.dept === dept) i = idx; });
  if (i >= 0) return i % OFC_SOFAS.length;
  var str = String(dept || 'hq'), hsh = 0;
  for (var j = 0; j < str.length; j++) hsh = (hsh * 31 + str.charCodeAt(j) + 3) >>> 0;
  return (hsh >>> 5) % OFC_SOFAS.length;
}
function officeSofaVariant(dept, allAgents) {
  return officeSofaVariantFromFloors(dept, officeBuildingFloors((allAgents || []).filter(function(a) { return (a.dept || '') !== 'Reception'; })));
}
// Seat positions (hip points) for a sofa whose left edge is at x
function officeSofaSeats(x, v) {
  var S = OFC_SOFAS[v] || OFC_SOFAS[0];
  return S.seats.map(function(o) { return { x: x + o, y: S.seatY }; });
}
// Sofa with its left edge at x, feet on the floor line
function officeSofa(x, v, key) {
  var S = OFC_SOFAS[v] || OFC_SOFAS[0], G = FLOOR_GROUND, k = [];
  k.push(h('ellipse', { key: 'sh', cx: x + S.w / 2, cy: G + 2, rx: S.w / 2 - 5, ry: 5, fill: 'rgba(0,0,0,0.28)' }));
  if (S.name === 'classic' || S.name === 'modern' || S.name === 'sectional') {
    var mw = S.name === 'sectional' ? 160 : S.w;
    k.push(h('rect', { key: 'bk', x: x + 12, y: 206, width: mw - 24, height: 34, rx: 12, fill: S.back }));
    k.push(h('rect', { key: 'st', x: x + 6, y: 232, width: mw - 12, height: 30, rx: 10, fill: S.seat }));
    k.push(h('rect', { key: 'a1', x: x, y: 214, width: 18, height: 48, rx: 8, fill: S.arm }));
    k.push(h('rect', { key: 'a2', x: x + mw - 18, y: 214, width: 18, height: 48, rx: 8, fill: S.arm }));
    k.push(h('line', { key: 'sp', x1: x + mw / 2, y1: 236, x2: x + mw / 2, y2: 258, stroke: S.back, strokeWidth: 3 }));
    if (S.name === 'sectional') { // chaise continues to the right without a backrest, one cushion on it
      k.push(h('rect', { key: 'ch', x: x + mw - 8, y: 232, width: S.w - mw + 8, height: 30, rx: 10, fill: S.seat }));
      k.push(h('rect', { key: 'ch2', x: x + S.w - 16, y: 214, width: 16, height: 48, rx: 8, fill: S.arm }));
      k.push(h('rect', { key: 'pl', x: x + mw + 14, y: 218, width: 26, height: 15, rx: 4, fill: '#E0B15A', transform: 'rotate(-8 ' + (x + mw + 27) + ' 225)' }));
    }
    if (S.name === 'modern') { // folded blanket over the right arm
      k.push(h('rect', { key: 'bl', x: x + S.w - 27, y: 207, width: 32, height: 22, rx: 4, fill: '#F1E4CF' }));
      k.push(h('rect', { key: 'bl2', x: x + S.w - 27, y: 213, width: 32, height: 3, fill: S.back, opacity: 0.55 }));
      k.push(h('rect', { key: 'bl3', x: x + S.w - 27, y: 221, width: 32, height: 3, fill: S.back, opacity: 0.55 }));
    }
    k.push(h('rect', { key: 'l1', x: x + 12, y: 262, width: 8, height: 8, rx: 2, fill: S.leg }));
    k.push(h('rect', { key: 'l2', x: x + S.w - 20, y: 262, width: 8, height: 8, rx: 2, fill: S.leg }));
  } else if (S.name === 'loveseat') {
    k.push(h('rect', { key: 'bk', x: x + 8, y: 200, width: S.w - 16, height: 44, rx: 20, fill: S.back }));
    k.push(h('rect', { key: 'st', x: x + 4, y: 234, width: S.w - 8, height: 28, rx: 14, fill: S.seat }));
    k.push(h('circle', { key: 'a1', cx: x + 10, cy: 240, r: 12, fill: S.arm }));
    k.push(h('circle', { key: 'a2', cx: x + S.w - 10, cy: 240, r: 12, fill: S.arm }));
    k.push(h('rect', { key: 'p1', x: x + 22, y: 214, width: 20, height: 20, rx: 4, fill: '#E0B15A', transform: 'rotate(-10 ' + (x + 32) + ' 224)' }));
    k.push(h('rect', { key: 'p2', x: x + S.w - 44, y: 214, width: 20, height: 20, rx: 4, fill: '#F1E4CF', transform: 'rotate(8 ' + (x + S.w - 34) + ' 224)' }));
    k.push(h('rect', { key: 'l1', x: x + 16, y: 262, width: 8, height: 8, rx: 3, fill: S.leg }));
    k.push(h('rect', { key: 'l2', x: x + S.w - 24, y: 262, width: 8, height: 8, rx: 3, fill: S.leg }));
  } else if (S.name === 'midcentury') {
    k.push(h('rect', { key: 'bk', x: x + 8, y: 212, width: S.w - 16, height: 28, rx: 6, fill: S.back }));
    k.push(h('rect', { key: 'st', x: x + 4, y: 236, width: S.w - 8, height: 20, rx: 6, fill: S.seat }));
    k.push(h('rect', { key: 'fr', x: x + 2, y: 254, width: S.w - 4, height: 5, rx: 2, fill: OFC_WOOD }));
    k.push(h('rect', { key: 'a1', x: x, y: 226, width: 6, height: 30, rx: 3, fill: OFC_WOOD }));
    k.push(h('rect', { key: 'a2', x: x + S.w - 6, y: 226, width: 6, height: 30, rx: 3, fill: OFC_WOOD }));
    k.push(h('line', { key: 'sp', x1: x + S.w / 2, y1: 214, x2: x + S.w / 2, y2: 238, stroke: S.seat, strokeWidth: 2 }));
    [x + 14, x + S.w - 14].forEach(function(lx, i) { k.push(h('rect', { key: 'l' + i, x: lx - 3, y: 258, width: 6, height: 12, rx: 2, fill: OFC_WOOD_DARK, transform: 'rotate(' + (i ? 12 : -12) + ' ' + lx + ' 258)' })); });
  } else if (S.name === 'chesterfield') {
    k.push(h('rect', { key: 'bk', x: x + 10, y: 202, width: S.w - 20, height: 40, rx: 10, fill: S.back }));
    for (var r = 0; r < 2; r++) for (var c = 0; c < 6; c++) k.push(h('circle', { key: 'b' + r + c, cx: x + 24 + c * (S.w - 48) / 5, cy: 214 + r * 14, r: 1.8, fill: '#3F2A21' }));
    k.push(h('rect', { key: 'st', x: x + 6, y: 234, width: S.w - 12, height: 28, rx: 8, fill: S.seat }));
    k.push(h('rect', { key: 'a1b', x: x, y: 230, width: 20, height: 32, rx: 6, fill: S.arm }));
    k.push(h('rect', { key: 'a2b', x: x + S.w - 20, y: 230, width: 20, height: 32, rx: 6, fill: S.arm }));
    k.push(h('circle', { key: 'a1', cx: x + 12, cy: 230, r: 13, fill: S.arm }));
    k.push(h('circle', { key: 'a2', cx: x + S.w - 12, cy: 230, r: 13, fill: S.arm }));
    k.push(h('rect', { key: 'l1', x: x + 8, y: 262, width: 10, height: 8, rx: 3, fill: S.leg }));
    k.push(h('rect', { key: 'l2', x: x + S.w - 18, y: 262, width: 10, height: 8, rx: 3, fill: S.leg }));
  }
  return h('g', { key: key || 'sofa' }, k);
}
// Potted plant: pot bottom-centre at (x, ground); s scales it (1 = the desk plant, ~60 tall). The plant
// group's origin is the soil line; the pot hangs 22 units below it.
function officePlant(species, x, ground, s, pot, key) {
  pot = pot || OFC_POTS[0]; s = s || 1;
  var sp = OFC_PLANT_SPECIES[(species || 0) % OFC_PLANT_SPECIES.length], k = [];
  if (sp === 'fronds') {
    k.push(h('path', { key: 'a', d: 'M -4,0 Q -12,-20 -6,-36', stroke: '#10B981', strokeWidth: 4, fill: 'none', strokeLinecap: 'round' }));
    k.push(h('path', { key: 'b', d: 'M 0,0 Q 0,-24 8,-34', stroke: '#34D399', strokeWidth: 4, fill: 'none', strokeLinecap: 'round' }));
    k.push(h('path', { key: 'c', d: 'M 4,0 Q 10,-14 6,-24', stroke: '#059669', strokeWidth: 4, fill: 'none', strokeLinecap: 'round' }));
  } else if (sp === 'monstera') {
    [[-11, -24, -35], [10, -28, 30], [0, -40, 0]].forEach(function(L, i) {
      k.push(h('path', { key: 's' + i, d: 'M 0,0 Q ' + (L[0] / 2) + ',' + (L[1] / 2) + ' ' + L[0] + ',' + L[1], stroke: '#166534', strokeWidth: 2.2, fill: 'none' }));
      k.push(h('g', { key: 'l' + i, transform: 'translate(' + L[0] + ',' + L[1] + ') rotate(' + L[2] + ')' },
        h('path', { d: 'M 0,7 C -13,5 -13,-11 0,-13 C 13,-11 13,5 0,7 Z', fill: '#15803D' }),
        h('path', { d: 'M -8,-2 L -3,-2 M -7,3 L -3,3 M 8,-2 L 3,-2 M 7,3 L 3,3', stroke: '#0B4A2B', strokeWidth: 1.6, strokeLinecap: 'round' })));
    });
  } else if (sp === 'snake') {
    [[-8, 28], [-4, 36], [0, 42], [4, 34], [8, 26]].forEach(function(B, i) {
      k.push(h('path', { key: 'b' + i, d: 'M ' + (B[0] - 3) + ',0 L ' + (B[0] - 2) + ',' + (-B[1] + 8) + ' L ' + B[0] + ',' + (-B[1]) + ' L ' + (B[0] + 2) + ',' + (-B[1] + 8) + ' L ' + (B[0] + 3) + ',0 Z', fill: '#166534' }));
      k.push(h('path', { key: 'e' + i, d: 'M ' + B[0] + ',-3 L ' + B[0] + ',' + (-B[1] + 8), stroke: '#A3E635', strokeWidth: 1.2, opacity: 0.6 }));
    });
  } else if (sp === 'rubber') {
    k.push(h('path', { key: 'st', d: 'M 0,0 L 0,-42', stroke: '#3F6212', strokeWidth: 2.4, strokeLinecap: 'round' }));
    [[-7, -12, -35, '#14532D'], [7, -20, 35, '#166534'], [-7, -28, -30, '#14532D'], [6, -36, 30, '#166534'], [0, -44, 0, '#15803D']].forEach(function(L, i) {
      k.push(h('g', { key: 'l' + i, transform: 'translate(' + L[0] + ',' + L[1] + ') rotate(' + L[2] + ')' },
        h('ellipse', { cx: 0, cy: 0, rx: 5.5, ry: 9, fill: L[3] }),
        h('line', { x1: 0, y1: -7, x2: 0, y2: 7, stroke: '#7C2D12', strokeWidth: 1, opacity: 0.7 })));
    });
  } else if (sp === 'cactus') {
    k.push(h('rect', { key: 'bd', x: -6, y: -42, width: 12, height: 44, rx: 6, fill: '#4D7C0F' }));
    k.push(h('rect', { key: 'a1', x: -17, y: -30, width: 6, height: 14, rx: 3, fill: '#4D7C0F' }));
    k.push(h('rect', { key: 'a1b', x: -17, y: -20, width: 13, height: 5, rx: 2.5, fill: '#4D7C0F' }));
    k.push(h('rect', { key: 'a2', x: 11, y: -24, width: 6, height: 12, rx: 3, fill: '#4D7C0F' }));
    k.push(h('rect', { key: 'a2b', x: 4, y: -16, width: 13, height: 5, rx: 2.5, fill: '#4D7C0F' }));
    [-34, -26, -18, -10].forEach(function(y, i) { k.push(h('line', { key: 'sp' + i, x1: -3, y1: y, x2: 3, y2: y, stroke: '#D9F99D', strokeWidth: 1, opacity: 0.7 })); });
    k.push(h('circle', { key: 'fl', cx: 0, cy: -43, r: 2.5, fill: '#F472B6' }));
  }
  k.push(h('path', { key: 'pot', d: 'M -12,0 L 12,0 L 9,22 L -9,22 Z', fill: pot[0] }));
  k.push(h('rect', { key: 'rim', x: -14, y: -2, width: 28, height: 5, rx: 2.5, fill: pot[1] }));
  return h('g', { key: key || 'plant', transform: 'translate(' + x + ',' + (ground - 22 * s) + ') scale(' + s + ')' }, k);
}
// Restroom door with a WC sign and a vacant/occupied slider; busy = someone is inside on the 30 s walk cycle
function officeRestroom(x, key, busy) { // 92 wide, 162 tall: about 1.3× a floor figure
  return h('g', { key: key || 'wc' },
    h('rect', { x: x, y: 108, width: 92, height: 162, rx: 7, fill: '#2E3950' }),
    h('rect', { x: x + 7, y: 115, width: 78, height: 155, rx: 5, fill: '#3E4B63' }),
    h('circle', { cx: x + 46, cy: 150, r: 12, fill: 'rgba(125,211,252,0.35)', stroke: '#2E3950', strokeWidth: 2.5 }),
    h('rect', { x: x + 66, y: 196, width: 10, height: 4, rx: 2, fill: '#CBD5E1' }),
    h('rect', { x: x + 30, y: 176, width: 32, height: 10, rx: 5, fill: '#222B3A' }),
    h('circle', { className: busy ? 'ofc-wcBusy' : undefined, cx: x + 37, cy: 181, r: 3.6, fill: '#34D399' }),
    h('rect', { x: x + 24, y: 80, width: 44, height: 20, rx: 5, fill: '#222B3A' }),
    h('text', { x: x + 46, y: 95, textAnchor: 'middle', fontSize: 13, fontWeight: 700, fill: '#CBD5E1', fontFamily: 'inherit' }, 'WC')
  );
}
// The restroom door at building scale (26 × 56; mini figures are ~44 tall), left edge at x
function officeMiniRestroom(x, base, key) {
  return h('g', { key: key || 'mwc' },
    h('rect', { x: x, y: base - 56, width: 26, height: 56, rx: 2.5, fill: '#2E3950' }),
    h('rect', { x: x + 2, y: base - 53, width: 22, height: 53, rx: 2, fill: '#3E4B63' }),
    h('circle', { cx: x + 13, cy: base - 42, r: 3.8, fill: 'rgba(125,211,252,0.35)', stroke: '#2E3950', strokeWidth: 0.9 }),
    h('rect', { x: x + 18, y: base - 27, width: 3.5, height: 1.4, rx: 0.7, fill: '#CBD5E1' }),
    h('rect', { x: x + 7, y: base - 31, width: 12, height: 3.4, rx: 1.7, fill: '#222B3A' }),
    h('circle', { cx: x + 9.3, cy: base - 29.3, r: 1.4, fill: '#34D399' }),
    h('rect', { x: x + 4, y: base - 66, width: 18, height: 8, rx: 2, fill: '#222B3A' }),
    h('text', { x: x + 13, y: base - 60, textAnchor: 'middle', fontSize: 6, fontWeight: 700, fill: '#CBD5E1', fontFamily: 'inherit' }, 'WC')
  );
}
// Agent sitting with a coffee (Anthony 2026-09-06). Origin = hip point on the seat cushion, faces right;
// drop = seat-to-floor distance in figure units so the feet land on the floor.
function officeSitPerson(shirt, look, drop) {
  var lk = look || officeLookFromHair('short');
  var shin = Math.max(14, drop || 24);
  return h('g', null,
    h('rect', { x: -4, y: -4, width: 22, height: 8, rx: 4, fill: '#2A4879' }),
    h('rect', { x: 12, y: 0, width: 7, height: shin, rx: 3, fill: '#2A4879' }),
    h('rect', { x: -8, y: -1, width: 24, height: 8, rx: 4, fill: '#31548F' }),
    h('rect', { x: 8, y: 3, width: 7, height: shin - 3, rx: 3, fill: '#31548F' }),
    h('g', { className: 'ofc-bob' },
      h('rect', { x: -13, y: -38, width: 26, height: 40, rx: 10, fill: shirt }),
      h('rect', { x: 0, y: -30, width: 20, height: 8, rx: 4, fill: shirt, transform: 'rotate(-20 2 -26)' }),
      h('circle', { cx: 19, cy: -34, r: 4.5, fill: lk.skin }),
      h('rect', { x: 22, y: -42, width: 9, height: 10, rx: 2, fill: '#E5E7EB' }),
      h('path', { className: 'ofc-steam', d: 'M 26,-45 Q 24,-49 26,-53', stroke: '#CBD5E1', strokeWidth: 1.8, fill: 'none', strokeLinecap: 'round' }),
      h('g', { transform: 'translate(0,22)' }, officeHeadGroup(lk, OFC_HEAD_WALKER, 'walker'))
    )
  );
}
function officeSitter(shirt, look, seatX, seatY, key) {
  return h('g', { key: key || 'sitter', transform: 'translate(' + seatX + ',' + seatY + ') scale(1.5)' },
    officeSitPerson(shirt, look, (FLOOR_GROUND - seatY) / 1.5));
}

function officeKitchenDecor(dept) {
  var s = dept || '', hsh = 0;
  for (var i = 0; i < s.length; i++) hsh = (hsh * 31 + s.charCodeAt(i)) >>> 0;
  return { fridge: hsh % 3, cab: (hsh >>> 2) % 3, item: (hsh >>> 4) % 3, bowl: (hsh >>> 6) % 3 };
}
var OFC_FRIDGES = [['#4E5A72', '#57647E'], ['#5E4B58', '#6E5A68'], ['#3E5A55', '#4A6B65']];
var OFC_CABS = [OFC_DARK, '#5C4A38', '#3A5560'];
var OFC_BOWLS = [['#F59E0B', '#EF4444', '#84CC16'], ['#EF4444', '#EF4444', '#FDE68A'], ['#84CC16', '#F97316', '#FDE68A']];

var OFC_HEAD_MINI = { cx: 0, cy: -37, r: 6, capLong: OFC_HAIR_LONG_MINI, capShort: OFC_HAIR_SHORT_MINI };

function officeDeskKids(variant, shirt, bang, hair, standee, deco, look) {
  var lk = look || officeLookFromHair(hair);
  var seated = variant === 'work' || variant === 'idle';
  var screenOn = variant !== 'dark' && variant !== 'boxes';
  var shirtC = shirt || '#14B8A6';

  var kids = [];
  // cubicle back wall behind the desk zone (same palette as the floor
  // partitions) — wall art and pinned things hang here at every zoom level
  kids.push(h('g', { key: 'wall' },
    h('rect', { x: 118, y: 62, width: 174, height: 110, rx: 6, fill: '#323E52' }),
    h('rect', { x: 118, y: 62, width: 174, height: 6, rx: 3, fill: '#4C5A70' })
  ));
  if (deco) {
    // framed print, left of the monitor
    var artKids = [h('rect', { key: 'fr', x: 132, y: 72, width: 34, height: 26, rx: 3, fill: '#2E3950', stroke: '#4C5A70', strokeWidth: 1.5 })];
    if (deco.art === 0) artKids.push(h('path', { key: 'a', d: 'M 137,94 L 145,80 L 150,87 L 156,78 L 161,94 Z', fill: '#38BDF8', opacity: 0.75 }));
    if (deco.art === 1) artKids.push(
      h('circle', { key: 'a', cx: 149, cy: 84, r: 5.5, fill: '#F59E0B', opacity: 0.85 }),
      h('rect', { key: 'b', x: 136, y: 89, width: 26, height: 3, rx: 1.5, fill: '#EC4899', opacity: 0.55 }));
    if (deco.art === 2) artKids.push(
      h('path', { key: 'a', d: 'M 143,93 Q 141,80 149,76 Q 157,80 155,93', fill: '#34D399', opacity: 0.8 }),
      h('line', { key: 'b', x1: 149, y1: 79, x2: 149, y2: 93, stroke: '#1E4536', strokeWidth: 1.4 }));
    if (deco.art === 3) artKids.push(
      h('circle', { key: 'a', cx: 142, cy: 82, r: 3.4, fill: '#EC4899', opacity: 0.8 }),
      h('circle', { key: 'b', cx: 152, cy: 89, r: 3, fill: '#38BDF8', opacity: 0.8 }),
      h('circle', { key: 'c', cx: 157, cy: 79, r: 2.6, fill: '#FDE68A', opacity: 0.85 }));
    if (deco.art === 4) artKids.push(
      h('circle', { key: 'a', cx: 149, cy: 82, r: 4.5, fill: '#8FA4C8' }),
      h('path', { key: 'b', d: 'M 141,95 Q 149,85 157,95 Z', fill: '#8FA4C8' }));
    if (deco.art === 5) artKids.push(
      h('line', { key: 'a', x1: 136, y1: 92, x2: 148, y2: 76, stroke: '#F59E0B', strokeWidth: 3, strokeLinecap: 'round' }),
      h('line', { key: 'b', x1: 143, y1: 95, x2: 157, y2: 77, stroke: '#38BDF8', strokeWidth: 3, strokeLinecap: 'round' }),
      h('line', { key: 'c', x1: 151, y1: 94, x2: 161, y2: 81, stroke: '#EC4899', strokeWidth: 3, strokeLinecap: 'round' }));
    if (deco.art === 6) artKids.push(
      h('rect', { key: 'a', x: 135, y: 75, width: 28, height: 20, rx: 2, fill: '#1B2740' }),
      h('circle', { key: 'b', cx: 156, cy: 81, r: 3.6, fill: '#E9E4D0' }),
      h('circle', { key: 'c', cx: 141, cy: 79, r: 1.2, fill: '#8FA4C8' }),
      h('circle', { key: 'd', cx: 147, cy: 89, r: 1.2, fill: '#8FA4C8' }));
    if (deco.art === 7) artKids.push(
      h('rect', { key: 'a', x: 137, y: 77, width: 24, height: 3.5, rx: 1.75, fill: '#94A3B8' }),
      h('rect', { key: 'b', x: 140, y: 84, width: 18, height: 3.5, rx: 1.75, fill: '#F59E0B' }),
      h('rect', { key: 'c', x: 138, y: 91, width: 22, height: 3.5, rx: 1.75, fill: '#94A3B8' }));
    kids.push(h('g', { key: 'art' }, artKids));
    // pinned item, between the art and the monitor
    if (deco.pin === 0) kids.push(h('g', { key: 'pin' },
      h('rect', { x: 172, y: 73, width: 7.5, height: 7.5, rx: 1, fill: '#FDE68A', transform: 'rotate(-6 175 76)' }),
      h('rect', { x: 182, y: 78, width: 7.5, height: 7.5, rx: 1, fill: '#8FE3C0', transform: 'rotate(5 185 81)' }),
      h('rect', { x: 174, y: 86, width: 7.5, height: 7.5, rx: 1, fill: '#7DD3FC', transform: 'rotate(-4 177 89)' })));
    if (deco.pin === 1) kids.push(h('g', { key: 'pin' },
      h('rect', { x: 174, y: 72, width: 16, height: 20, rx: 2, fill: '#E5E7EB' }),
      h('rect', { x: 174, y: 72, width: 16, height: 5.5, rx: 2, fill: '#EF4444' }),
      h('line', { x1: 177, y1: 82, x2: 187, y2: 82, stroke: '#9CA3AF', strokeWidth: 1.4 }),
      h('line', { x1: 177, y1: 86, x2: 184, y2: 86, stroke: '#9CA3AF', strokeWidth: 1.4 })));
    if (deco.pin === 2) kids.push(h('g', { key: 'pin' },
      h('circle', { cx: 182, cy: 81, r: 7.5, fill: '#E5E7EB', stroke: '#39445A', strokeWidth: 2 }),
      h('line', { x1: 182, y1: 81, x2: 182, y2: 76, stroke: '#39445A', strokeWidth: 1.6, strokeLinecap: 'round' }),
      h('line', { x1: 182, y1: 81, x2: 186, y2: 83, stroke: '#39445A', strokeWidth: 1.6, strokeLinecap: 'round' })));
    if (deco.pin === 3) kids.push(h('path', { key: 'pin', d: 'M 173,72 L 191,76 L 173,84 Z', fill: shirtC, opacity: 0.85 }));
    if (deco.pin === 4) kids.push(h('g', { key: 'pin' },
      h('rect', { x: 176, y: 70, width: 11, height: 26, rx: 1.5, fill: '#E5E7EB' }),
      h('rect', { x: 178, y: 72, width: 7, height: 6, rx: 1, fill: '#56688C' }),
      h('rect', { x: 178, y: 80, width: 7, height: 6, rx: 1, fill: '#8FA4C8' }),
      h('rect', { x: 178, y: 88, width: 7, height: 6, rx: 1, fill: '#56688C' })));
    if (deco.pin === 5) kids.push(h('g', { key: 'pin' },
      h('rect', { x: 170, y: 88, width: 24, height: 3.5, rx: 1.75, fill: '#4C5A70' }),
      h('path', { d: 'M 178,78 Q 178,84 182,85 L 182,86 L 180,88 L 184,88 L 182,86 L 182,85 Q 186,84 186,78 Z', fill: '#FDE68A' })));
  }
  var rugC = deco && OFC_RUGS[deco.rug];
  if (rugC) kids.push(h('ellipse', { key: 'rug', cx: 84, cy: 171, rx: 38, ry: 5, fill: rugC }));
  kids.push(h('ellipse', { key: 'sh1', cx: 190, cy: 172, rx: 105, ry: 8, fill: 'rgba(0,0,0,0.28)' }));
  kids.push(h('ellipse', { key: 'sh2', cx: 52, cy: 172, rx: 40, ry: 6, fill: 'rgba(0,0,0,0.24)' }));

  // floor plant, left of the chair (pot color is part of the decor pack)
  var pot = (deco && OFC_POTS[deco.pot]) || OFC_POTS[0];
  kids.push(officePlant(deco ? deco.plant : 0, 16, 170, 1, pot, 'plant'));

  // chair — seat directly under the seated figure's hips (torso x68-108),
  // backrest tucked behind the torso so the agent actually sits IN it
  var ch = (deco && OFC_CHAIRS[deco.chair]) || OFC_CHAIRS[0];
  kids.push(h('g', { key: 'chair' },
    h('rect', { x: 62, y: 66, width: 15, height: 68, rx: 7, fill: ch[1] }),
    h('rect', { x: 58, y: 126, width: 52, height: 13, rx: 6.5, fill: ch[0] }),
    h('rect', { x: 80, y: 138, width: 7, height: 22, rx: 3, fill: ch[1] }),
    h('rect', { x: 62, y: 158, width: 44, height: 6, rx: 3, fill: ch[1] }),
    h('circle', { cx: 67, cy: 167, r: 4, fill: ch[0] }),
    h('circle', { cx: 101, cy: 167, r: 4, fill: ch[0] })
  ));

  // "on break": the agent stands by their empty desk, coffee in hand
  if (standee) {
    kids.push(h('g', { key: 'standee', transform: 'translate(88,172) scale(1.5)' },
      h('ellipse', { cx: 0, cy: 1, rx: 14, ry: 3, fill: 'rgba(0,0,0,0.28)' }),
      officeStrollPerson(shirt || '#14B8A6', hair, false, lk)));
  }

  if (seated) {
    var typing = variant === 'work';
    kids.push(h('g', { key: 'legs' },
      h('rect', { x: 82, y: 126, width: 52, height: 13, rx: 6.5, fill: '#31548F' }),
      h('rect', { x: 122, y: 130, width: 13, height: 38, rx: 6, fill: '#2A4879' }),
      h('rect', { x: 118, y: 162, width: 28, height: 9, rx: 4.5, fill: OFC_DARKER })
    ));
    kids.push(h('g', { key: 'body', className: 'ofc-bob' },
      // torso (tucks under the head so the bob never opens a neck gap)
      h('rect', { x: 68, y: 77, width: 40, height: 56, rx: 13, fill: shirtC }),
      officeHeadGroup(lk, OFC_HEAD_CLOSE, 'close'),
      typing
        ? h('g', { className: 'ofc-arm typing' },
            h('rect', { x: 96, y: 96, width: 54, height: 10, rx: 5, fill: shirtC, transform: 'rotate(14 98 101)' }),
            h('circle', { cx: 149, cy: 111, r: 5.5, fill: lk.skin }))
        : h('g', { className: 'ofc-arm' },
            h('rect', { x: 94, y: 96, width: 40, height: 10, rx: 5, fill: shirtC, transform: 'rotate(38 96 101)' }),
            h('circle', { cx: 126, cy: 124, r: 5.5, fill: lk.skin }))
    ));
  }

  // desk (wood tone is part of the decor pack)
  var dt = (deco && OFC_DESKS[deco.desk]) || OFC_DESKS[0];
  kids.push(h('g', { key: 'desk' },
    h('rect', { x: 118, y: 118, width: 174, height: 11, rx: 5.5, fill: dt[0] }),
    h('rect', { x: 126, y: 129, width: 9, height: 42, rx: 4, fill: dt[1] }),
    h('rect', { x: 275, y: 129, width: 9, height: 42, rx: 4, fill: dt[1] })
  ));
  // side item: CPU tower or storage totes under the desk, or a pencil cup on it
  if (deco && deco.side === 1) kids.push(h('g', { key: 'side' },
    h('rect', { x: 252, y: 136, width: 20, height: 35, rx: 3, fill: '#39445A' }),
    h('rect', { x: 256, y: 141, width: 12, height: 2.5, rx: 1.25, fill: '#222B3A' }),
    h('rect', { x: 256, y: 146, width: 12, height: 2.5, rx: 1.25, fill: '#222B3A' }),
    h('circle', { className: 'ofc-pulse', cx: 258, cy: 164, r: 1.8, fill: '#34D399' })));
  if (deco && deco.side === 2) kids.push(h('g', { key: 'side' },
    h('rect', { x: 246, y: 162, width: 30, height: 9, rx: 2, fill: '#46536B' }),
    h('rect', { x: 250, y: 153, width: 22, height: 9, rx: 2, fill: '#56688C' })));
  if (deco && deco.side === 3) kids.push(h('g', { key: 'side' },
    h('line', { x1: 126, y1: 100, x2: 126, y2: 108, stroke: '#F59E0B', strokeWidth: 2 }),
    h('line', { x1: 130, y1: 99, x2: 130, y2: 108, stroke: '#38BDF8', strokeWidth: 2 }),
    h('line', { x1: 134, y1: 101, x2: 134, y2: 108, stroke: '#EC4899', strokeWidth: 2 }),
    h('rect', { x: 121, y: 106, width: 17, height: 13, rx: 2, fill: '#56688C' })));

  // keyboard + mug
  kids.push(h('rect', { key: 'kb', x: 138, y: 112, width: 46, height: 6.5, rx: 3, fill: OFC_DARK }));
  kids.push(h('g', { key: 'mug' },
    h('rect', { x: 190, y: 105, width: 12, height: 13.5, rx: 2.5, fill: '#E5E7EB' }),
    h('path', { d: 'M 202,108 Q 208,111 202,115', stroke: '#E5E7EB', strokeWidth: 2.5, fill: 'none' }),
    variant === 'idle' && h('g', null,
      h('path', { className: 'ofc-steam', d: 'M 194,102 Q 192,98 194,94', stroke: '#CBD5E1', strokeWidth: 2, fill: 'none', strokeLinecap: 'round' }),
      h('path', { className: 'ofc-steam', style: { animationDelay: '-1.4s' }, d: 'M 199,102 Q 201,98 199,94', stroke: '#CBD5E1', strokeWidth: 2, fill: 'none', strokeLinecap: 'round' })
    )
  ));

  // monitor
  var glow = screenOn ? { filter: 'drop-shadow(0 0 10px rgba(56,189,248,0.30))' } : null;
  kids.push(h('g', { key: 'mon', style: glow },
    h('rect', { x: 224, y: 106, width: 7, height: 14, rx: 3, fill: OFC_DARKER }),
    h('rect', { x: 210, y: 116, width: 36, height: 5, rx: 2.5, fill: OFC_DARKER }),
    h('rect', { x: 196, y: 50, width: 92, height: 60, rx: 8, fill: '#222B3A' }),
    h('rect', { x: 201, y: 55, width: 82, height: 50, rx: 4, fill: screenOn ? '#0D1626' : '#161D2A' }),
    screenOn && variant !== 'empty' && h('g', null,
      h('rect', { className: 'ofc-msg', x: 206, y: 61, width: 40, height: 7, rx: 3.5, fill: '#334860' }),
      h('rect', { className: 'ofc-msg', style: { animationDelay: '0.7s' }, x: 236, y: 72, width: 42, height: 7, rx: 3.5, fill: '#38BDF8' }),
      h('rect', { className: 'ofc-msg', style: { animationDelay: '1.4s' }, x: 206, y: 83, width: 32, height: 7, rx: 3.5, fill: '#334860' }),
      h('rect', { className: 'ofc-msg', style: { animationDelay: '2.1s' }, x: 236, y: 94, width: 36, height: 7, rx: 3.5, fill: '#38BDF8' })
    ),
    screenOn && variant === 'empty' && h('circle', { className: 'ofc-pulse', cx: 242, cy: 80, r: 7, fill: '#F59E0B' }),
    !screenOn && h('circle', { cx: 280, cy: 106, r: 1.8, fill: '#F59E0B', opacity: 0.9 }),
    bang && h('g', null,
      h('circle', { className: 'ofc-ring', cx: 288, cy: 52, r: 9, fill: 'none', stroke: '#EF4444', strokeWidth: 2 }),
      h('circle', { cx: 288, cy: 52, r: 8, fill: '#EF4444' }),
      h('text', { x: 288, y: 56, textAnchor: 'middle', fontSize: 11, fontWeight: 800, fill: '#fff' }, '!')
    )
  ));

  // "onboarding": nobody's been hired yet — moving boxes wait on and beside
  // the desk (same cardboard as the FOR LEASE floor upstairs)
  if (variant === 'boxes') {
    kids.push(h('g', { key: 'boxes' },
      h('rect', { x: 34, y: 146, width: 38, height: 26, rx: 2, fill: '#6B5540' }),
      h('rect', { x: 34, y: 154, width: 38, height: 3.5, fill: '#8F6B4E' }),
      h('rect', { x: 40, y: 126, width: 28, height: 20, rx: 2, fill: '#5A4632' }),
      h('rect', { x: 40, y: 132, width: 28, height: 3, fill: '#8F6B4E' }),
      h('rect', { x: 146, y: 90, width: 36, height: 28, rx: 2, fill: '#6B5540' }),
      h('rect', { x: 146, y: 99, width: 36, height: 3.5, fill: '#8F6B4E' }),
      h('rect', { x: 152, y: 70, width: 26, height: 20, rx: 2, fill: '#5A4632' }),
      h('rect', { x: 152, y: 76, width: 26, height: 3, fill: '#8F6B4E' })
    ));
  }

  return kids;
}

// ── Reception scene (close-up + embedded in the Reception floor) ──
// The lobby IS the Reception dept, so its floor/close-up show the same
// front-desk counter the building's lobby shows in miniature: wall with the
// lobby's mountain print + clock, counter with bell and name plate, the
// receptionist seated behind it (or moving boxes while onboarding).
function officeReceptionKids(variant, shirt, hair, standee, bang, look) {
  var lk = look || officeLookFromHair(hair);
  var seated = variant === 'work' || variant === 'idle';
  var screenOn = variant !== 'dark' && variant !== 'boxes';
  var shirtC = shirt || '#7C6FA8';
  var kids = [];
  kids.push(h('g', { key: 'wall' },
    h('rect', { x: 40, y: 56, width: 252, height: 116, rx: 6, fill: '#323E52' }),
    h('rect', { x: 40, y: 56, width: 252, height: 6, rx: 3, fill: '#4C5A70' }),
    h('rect', { x: 60, y: 74, width: 40, height: 27, rx: 3, fill: '#2E3950', stroke: '#4C5A70', strokeWidth: 1.5 }),
    h('path', { d: 'M 66,97 L 74,84 L 80,91 L 86,82 L 92,97 Z', fill: '#38BDF8', opacity: 0.75 }),
    h('circle', { cx: 274, cy: 72, r: 8, fill: '#E5E7EB', stroke: '#39445A', strokeWidth: 2.2 }),
    h('line', { x1: 274, y1: 72, x2: 274, y2: 66.5, stroke: '#39445A', strokeWidth: 1.7, strokeLinecap: 'round' }),
    h('line', { x1: 274, y1: 72, x2: 278.5, y2: 74, stroke: '#39445A', strokeWidth: 1.7, strokeLinecap: 'round' })
  ));
  kids.push(h('ellipse', { key: 'sh1', cx: 172, cy: 172, rx: 100, ry: 8, fill: 'rgba(0,0,0,0.28)' }));
  kids.push(officePlant(1, 16, 170, 1, OFC_POTS[0], 'plant')); // a monstera at the front desk
  if (standee) {
    kids.push(h('g', { key: 'standee', transform: 'translate(52,172) scale(1.5)' },
      h('ellipse', { cx: 0, cy: 1, rx: 14, ry: 3, fill: 'rgba(0,0,0,0.28)' }),
      officeStrollPerson(shirtC, hair, false, lk)));
  }
  if (seated) {
    kids.push(h('g', { key: 'body', className: 'ofc-bob', transform: 'translate(78,10)' },
      h('rect', { x: 68, y: 77, width: 40, height: 52, rx: 13, fill: shirtC }),
      officeHeadGroup(lk, OFC_HEAD_CLOSE, 'close'),
      h('circle', { cx: 87, cy: 77, r: 2.2, fill: '#222B3A' })
    ));
  }
  // counter, drawn over the seated figure's lower half
  kids.push(h('g', { key: 'counter' },
    h('rect', { x: 96, y: 130, width: 160, height: 42, rx: 5, fill: OFC_DARK }),
    h('rect', { x: 104, y: 141, width: 144, height: 4, rx: 2, fill: '#4C5A70', opacity: 0.6 }),
    h('rect', { x: 90, y: 122, width: 172, height: 10, rx: 5, fill: '#4C5A70' }),
    h('rect', { x: 112, y: 113, width: 30, height: 7, rx: 2, fill: '#8A94A2' }),
    h('path', { d: 'M 152,122 A 8,8 0 0 1 168,122 Z', fill: '#FDE68A' }),
    h('circle', { cx: 160, cy: 113.5, r: 1.6, fill: '#B8933F' })
  ));
  var glow = screenOn ? { filter: 'drop-shadow(0 0 8px rgba(56,189,248,0.28))' } : null;
  kids.push(h('g', { key: 'mon', style: glow },
    h('rect', { x: 226, y: 108, width: 6, height: 14, rx: 3, fill: OFC_DARKER }),
    h('rect', { x: 204, y: 78, width: 52, height: 34, rx: 5, fill: '#222B3A' }),
    h('rect', { x: 208, y: 82, width: 44, height: 26, rx: 3, fill: screenOn ? '#0D1626' : '#161D2A' }),
    screenOn && variant !== 'empty' && h('g', null,
      h('rect', { className: 'ofc-msg', x: 211, y: 86, width: 22, height: 5, rx: 2.5, fill: '#334860' }),
      h('rect', { className: 'ofc-msg', style: { animationDelay: '1.1s' }, x: 227, y: 94, width: 22, height: 5, rx: 2.5, fill: '#38BDF8' })),
    screenOn && variant === 'empty' && h('circle', { className: 'ofc-pulse', cx: 230, cy: 95, r: 5, fill: '#F59E0B' }),
    !screenOn && h('circle', { cx: 250, cy: 108, r: 1.6, fill: '#F59E0B', opacity: 0.9 }),
    bang && h('g', null,
      h('circle', { className: 'ofc-ring', cx: 268, cy: 62, r: 9, fill: 'none', stroke: '#EF4444', strokeWidth: 2 }),
      h('circle', { cx: 268, cy: 62, r: 8, fill: '#EF4444' }),
      h('text', { x: 268, y: 66, textAnchor: 'middle', fontSize: 11, fontWeight: 800, fill: '#fff' }, '!'))
  ));
  if (variant === 'boxes') {
    kids.push(h('g', { key: 'boxes' },
      h('rect', { x: 34, y: 146, width: 38, height: 26, rx: 2, fill: '#6B5540' }),
      h('rect', { x: 34, y: 154, width: 38, height: 3.5, fill: '#8F6B4E' }),
      h('rect', { x: 40, y: 126, width: 28, height: 20, rx: 2, fill: '#5A4632' }),
      h('rect', { x: 40, y: 132, width: 28, height: 3, fill: '#8F6B4E' }),
      h('rect', { x: 168, y: 94, width: 34, height: 28, rx: 2, fill: '#6B5540' }),
      h('rect', { x: 168, y: 103, width: 34, height: 3.5, fill: '#8F6B4E' }),
      h('rect', { x: 174, y: 74, width: 26, height: 20, rx: 2, fill: '#5A4632' }),
      h('rect', { x: 174, y: 80, width: 26, height: 3, fill: '#8F6B4E' })
    ));
  }
  return kids;
}

function OfficeReceptionArt({ variant, shirt, scale, hair, standee, bang, look }) {
  var s = scale || 1;
  return h('svg', {
    width: OFC_W * s, height: OFC_H * s, viewBox: '0 0 ' + OFC_W + ' ' + OFC_H,
    style: { display: 'block', overflow: 'visible' },
  }, officeReceptionKids(variant, shirt, hair, standee, bang, look));
}

// Status → desk-scene variant. ONE mapping shared by every zoom level (close-up,
// floor cubicle, building mini) so all three tell the same story.
function officeVariant(status) {
  return status === 'working' ? 'work'
    : status === 'error' ? 'idle'
    : status === 'idle' || status === 'away' ? 'empty'
    : status === 'onboarding' ? 'boxes' : 'dark';
}

function OfficeDeskArt({ variant, shirt, scale, bang, hair, standee, deco, pet, look }) {
  var s = scale || 1;
  var kids = officeDeskKids(variant, shirt, bang, hair, standee, deco, look).slice();
  if (pet) {
    // the dept pet dropping by, sat at the edge of the cubicle facing the desk
    kids.push(h('g', { key: 'pet', transform: 'translate(268,172) scale(1.1)' },
      h('ellipse', { cx: 0, cy: 1.5, rx: 14, ry: 3, fill: 'rgba(0,0,0,0.28)' }),
      h('g', { transform: 'scale(-1,1)' }, pet.species === 'cat' ? officeCatSit(pet.coat) : officeDogSit(pet.breed))
    ));
  }
  return h('svg', {
    width: OFC_W * s, height: OFC_H * s, viewBox: '0 0 ' + OFC_W + ' ' + OFC_H,
    style: { display: 'block', overflow: 'visible' },
  }, kids);
}

// ── Floor scene ──
// One wide SVG (viewBox height 300, ground at y=270). Layout: partitioned
// cubicle row (one per agent + ≥1 hiring slot), then break area (water cooler
// + sofa), then kitchen (cabinets, coffee machine, fridge). Windows + pendant
// lamps repeat across the wall; a roomba patrols the open floor.
var FLOOR_H = 300, FLOOR_GROUND = 270, CUB_W = 360, FLOOR_K = 0.80;
// Floors shrink to fit the panel only this far (Anthony 2026-09-06); wider floors scroll sideways.
// Below this the DOM overlays (name cards, signs) stop matching the scaled art and pile up.
var FLOOR_K_MIN = 0.55;

// Floors and the lobby fit their panel (Anthony 2026-09-04): the scene scales
// down to the panel width instead of panning sideways — down to FLOOR_K_MIN
// (2026-09-06), past which the floor keeps that scale and scrolls sideways.
// Measured on mount and on resize; FLOOR_K stays the ceiling so wide screens
// never blow the art up.
function useOfficeFitScale(sceneW) {
  var ref = useRef(null);
  var [panelW, setPanelW] = useState(0);
  useEffect(function() {
    var el = ref.current;
    if (!el) return;
    var measure = function() { setPanelW(el.clientWidth || 0); };
    measure();
    var ro = (typeof ResizeObserver !== 'undefined') ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el); else window.addEventListener('resize', measure);
    return function() { if (ro) ro.disconnect(); else window.removeEventListener('resize', measure); };
  }, []);
  var k = panelW > 0 ? Math.min(FLOOR_K, panelW / sceneW) : FLOOR_K;
  return { ref: ref, k: Math.max(FLOOR_K_MIN, k) };
}

function officeFloorLayout(agentCount, dept, sofaV) {
  var slots = Math.max(2, agentCount + 1);
  var rowX = 40;
  // Filing keeps its records on the floor (Anthony 2026-09-04, "just for
  // fun"): a bank of file cabinets between the last cubicle and the break
  // sofa. Other floors have no records zone (recW 0 collapses it).
  var recW = dept === 'Filing' ? 250 : 0;
  var recX = rowX + slots * CUB_W + 34;
  var breakX = recX + recW;
  // break area = cooler, sofa (a wider sectional on some floors), floor plant; restroom door past the fridge
  var sofa = OFC_SOFAS[sofaV || 0] || OFC_SOFAS[0];
  var breakW = 330 + Math.max(0, sofa.w - 160);
  var kitchX = breakX + breakW;
  var wcX = kitchX + 396;
  var sceneW = wcX + 100;
  return { slots: slots, rowX: rowX, recX: recX, recW: recW, breakX: breakX, breakW: breakW, kitchX: kitchX, wcX: wcX, sceneW: sceneW, sofaV: sofaV || 0 };
}

// Four-drawer file cabinet, feet at (x, ground). `open` pulls the second
// drawer out with folder tabs showing; `topper` = 'box' | 'plant' | null.
function officeFileCabinet(key, x, ground, open, topper) {
  var W = 58, H = 152, top = ground - H;
  var kids = [
    h('ellipse', { cx: x + W / 2, cy: ground + 2, rx: 34, ry: 4.5, fill: 'rgba(0,0,0,0.28)' }),
    h('rect', { x: x, y: top, width: W, height: H, rx: 5, fill: '#3F4A5E' }),
    h('rect', { x: x, y: top, width: W, height: 6, rx: 3, fill: '#56617A' }),
  ];
  for (var d = 0; d < 4; d++) {
    var dy = top + 10 + d * 35;
    if (open && d === 1) {
      // pulled-out drawer: dark cavity, drawer front forward, folders inside
      kids.push(h('rect', { x: x + 4, y: dy, width: W - 8, height: 31, rx: 3, fill: '#2A3346' }));
      kids.push(h('rect', { x: x + 8, y: dy + 6, width: 9, height: 22, rx: 1.5, fill: '#E8D8A6' }));
      kids.push(h('rect', { x: x + 19, y: dy + 4, width: 9, height: 24, rx: 1.5, fill: '#D9B36C' }));
      kids.push(h('rect', { x: x + 30, y: dy + 7, width: 9, height: 21, rx: 1.5, fill: '#A7C4E2' }));
      kids.push(h('rect', { x: x + 41, y: dy + 5, width: 9, height: 23, rx: 1.5, fill: '#E8D8A6' }));
      kids.push(h('rect', { x: x - 6, y: dy + 20, width: W + 12, height: 13, rx: 3, fill: '#5F6B85' }));
      kids.push(h('rect', { x: x + W / 2 - 7, y: dy + 25, width: 14, height: 3.5, rx: 1.75, fill: '#8A94A2' }));
    } else {
      kids.push(h('rect', { x: x + 4, y: dy, width: W - 8, height: 31, rx: 3, fill: '#56617A' }));
      kids.push(h('rect', { x: x + W / 2 - 7, y: dy + 14, width: 14, height: 3.5, rx: 1.75, fill: '#8A94A2' }));
      kids.push(h('rect', { x: x + 9, y: dy + 5, width: 12, height: 5, rx: 1, fill: '#E5E7EB', opacity: 0.55 })); // label holder
    }
  }
  if (topper === 'box') {
    kids.push(h('rect', { x: x + 8, y: top - 24, width: 42, height: 24, rx: 3, fill: OFC_WOOD }));
    kids.push(h('rect', { x: x + 6, y: top - 28, width: 46, height: 7, rx: 2, fill: OFC_WOOD_DARK }));
    kids.push(h('rect', { x: x + 20, y: top - 17, width: 18, height: 7, rx: 1.5, fill: '#F1F5F9', opacity: 0.85 }));
  } else if (topper === 'plant') {
    kids.push(h('rect', { x: x + 21, y: top - 14, width: 16, height: 14, rx: 3, fill: '#C2703D' }));
    kids.push(h('path', { d: 'M ' + (x + 29) + ',' + (top - 14) + ' Q ' + (x + 20) + ',' + (top - 30) + ' ' + (x + 26) + ',' + (top - 40), stroke: '#10B981', strokeWidth: 3, fill: 'none', strokeLinecap: 'round' }));
    kids.push(h('path', { d: 'M ' + (x + 29) + ',' + (top - 14) + ' Q ' + (x + 38) + ',' + (top - 28) + ' ' + (x + 34) + ',' + (top - 38), stroke: '#34D399', strokeWidth: 3, fill: 'none', strokeLinecap: 'round' }));
  }
  return h('g', { key: key }, kids);
}

// Building-view miniature of the same cabinets (two, one drawer open).
function officeMiniCabinets(key, x, base) {
  var one = function(cx, open) {
    var kids = [h('rect', { x: cx, y: base - 40, width: 17, height: 40, rx: 2, fill: '#3F4A5E' })];
    for (var d = 0; d < 4; d++) {
      var dy = base - 37 + d * 9;
      kids.push(h('rect', { x: cx + 1.5, y: dy, width: 14, height: 7.5, rx: 1, fill: open && d === 1 ? '#2A3346' : '#56617A' }));
      if (open && d === 1) kids.push(h('rect', { x: cx - 1.5, y: dy + 4.5, width: 20, height: 3.5, rx: 1, fill: '#5F6B85' }));
      else kids.push(h('rect', { x: cx + 6.5, y: dy + 3, width: 4, height: 1.4, rx: 0.7, fill: '#8A94A2' }));
    }
    return kids;
  };
  return h('g', { key: key }, one(x, false).concat(one(x + 21, true),
    [h('rect', { x: x + 23, y: base - 47, width: 13, height: 7, rx: 1, fill: OFC_WOOD })]));
}

// People shouldn't pace nonstop: every walker flips between standing around
// and walking on a slow time bucket (the page re-renders on its 30s tick, so
// poses change without a reload). Seed staggers so the office isn't in
// lockstep; walking roughly a third of the time.
function officeAmbientBucket(seed) { return Math.floor(Date.now() / 40000) + (seed || 0); }

// Standing/walking figure with a coffee, ~80 units tall, feet at origin.
// Shared by the floor stroller and the detail-view "on break" standee.
function officeStrollPerson(shirt, hair, walking, look) {
  var lk = look || officeLookFromHair(hair);
  return h('g', null,
    h('g', { className: walking ? 'ofc-legA' : undefined }, h('rect', { x: -9.5, y: -28, width: 8, height: 28, rx: 4, fill: '#2A4879' })),
    h('g', { className: walking ? 'ofc-legB' : undefined }, h('rect', { x: 1.5, y: -28, width: 8, height: 28, rx: 4, fill: '#31548F' })),
    h('g', { className: walking ? 'ofc-walkbob' : undefined },
      h('rect', { x: -13, y: -58, width: 26, height: 36, rx: 10, fill: shirt }),
      h('g', { className: 'ofc-arm' },
        h('rect', { x: 2, y: -52, width: 22, height: 8, rx: 4, fill: shirt, transform: 'rotate(18 4 -48)' }),
        h('circle', { cx: 23, cy: -43, r: 4.5, fill: lk.skin }),
        h('rect', { x: 27, y: -51, width: 9, height: 10, rx: 2, fill: '#E5E7EB' }),
        h('path', { className: 'ofc-steam', d: 'M 31,-54 Q 29,-58 31,-62', stroke: '#CBD5E1', strokeWidth: 1.8, fill: 'none', strokeLinecap: 'round' })
      ),
      officeHeadGroup(lk, OFC_HEAD_WALKER, 'walker')
    )
  );
}

// Break-time figure on the floor. Walks to the kitchen some of the time;
// otherwise stands there sipping coffee.
// opts: key (several walkers share a scene), walking (override the ambient clock), hide (restroom trip:
// the figure fades while "inside", on the same 30 s clock as the stroll)
function officeWalker(shirt, startX, dx, hair, seed, look, opts) {
  opts = opts || {};
  var bucket = officeAmbientBucket(seed);
  var walking = opts.walking != null ? opts.walking : bucket % 3 === 0;
  var person = officeStrollPerson(shirt, hair, walking, look);
  var shadow = h('ellipse', { cx: 0, cy: 2, rx: 20, ry: 4.5, fill: 'rgba(0,0,0,0.28)' });
  var key = opts.key || 'walker';
  if (!walking) {
    var standX = startX + dx + (bucket % 2 ? -34 : 4); // lingering by the coffee machine
    return h('g', { key: key, transform: 'translate(' + standX + ',' + FLOOR_GROUND + ') scale(1.5)' }, shadow, person);
  }
  return h('g', { key: key, className: opts.hide ? 'ofc-wcHide' : undefined, transform: 'translate(' + startX + ',' + FLOOR_GROUND + ') scale(1.5)' },
    h('g', { className: 'ofc-stroll', style: { '--dx': (dx / 1.5) + 'px' } },
      shadow,
      h('g', { className: 'ofc-strollFlip' }, person)
    )
  );
}

// ── Office pets ──
// Every dept floor keeps a resident cat or dog (species + coat hashed off the
// dept name, same unsigned-shift discipline as officeDecor). Figures face
// right, feet at origin, ~34 units tall.
var OFC_CAT_COATS = ['#D08048', '#8A94A2', '#4A4544'];
var OFC_DOG_BREEDS = [
  { kind: 'retriever', body: '#C99B5F', trim: '#A87E48' }, // golden
  { kind: 'shihtzu',   body: '#E3DACC', trim: '#B49A78' },
  { kind: 'dachshund', body: '#7E5138', trim: '#5E3C28' },
  { kind: 'retriever', body: '#5E6B7A', trim: '#49545F' }, // gray lab
];

function officePetFor(dept) {
  var s = String(dept || 'hq'), hsh = 0;
  for (var i = 0; i < s.length; i++) hsh = (hsh * 31 + s.charCodeAt(i)) | 0;
  var cat = ((hsh >>> 4) % 5) < 3;
  return {
    species: cat ? 'cat' : 'dog',
    coat: OFC_CAT_COATS[(hsh >>> 7) % OFC_CAT_COATS.length],
    breed: OFC_DOG_BREEDS[(hsh >>> 7) % OFC_DOG_BREEDS.length],
    seed: (hsh >>> 10) % 13,
  };
}

// The pet wanders, so in the zoomed-in view it's at a given agent's desk only
// some of the time — ambient bucket keyed to the agent, so visits rotate.
function officePetVisit(agent) {
  var s = String((agent && agent.id) || ''), n = 0;
  for (var i = 0; i < s.length; i++) n += s.charCodeAt(i);
  return officeAmbientBucket(n) % 3 === 0 ? officePetFor(agent && agent.dept) : null;
}

function officeCatFig(coat, walking) {
  return h('g', null,
    h('g', { className: 'ofc-tailSway' },
      h('path', { d: 'M -13,-9 Q -21,-12 -19,-25', stroke: coat, strokeWidth: 4.5, fill: 'none', strokeLinecap: 'round' })),
    h('g', { className: walking ? 'ofc-petLegA' : undefined }, h('rect', { x: -10, y: -9, width: 4.5, height: 9.5, rx: 2.2, fill: coat })),
    h('g', { className: walking ? 'ofc-petLegB' : undefined }, h('rect', { x: 4, y: -9, width: 4.5, height: 9.5, rx: 2.2, fill: coat })),
    h('ellipse', { cx: -1, cy: -14, rx: 13, ry: 7.5, fill: coat }),
    h('circle', { cx: 12, cy: -22, r: 7, fill: coat }),
    h('g', { className: 'ofc-earFlick' }, h('path', { d: 'M 6.5,-27 L 8,-33 L 11.5,-27.5 Z', fill: coat })),
    h('path', { d: 'M 12.5,-28 L 15.5,-33.5 L 17,-27 Z', fill: coat }),
    h('circle', { className: 'ofc-eye', cx: 14.5, cy: -22.5, r: 1.1, fill: '#1F2937' }),
    h('path', { d: 'M 18.5,-20.5 L 23,-21.5 M 18.5,-19 L 23,-18.5', stroke: 'rgba(230,235,242,0.5)', strokeWidth: 0.9 })
  );
}
function officeCatSit(coat) {
  return h('g', null,
    h('g', { className: 'ofc-tailSway' },
      h('path', { d: 'M -6,-3 Q -16,-3 -14,-13', stroke: coat, strokeWidth: 4.5, fill: 'none', strokeLinecap: 'round' })),
    h('path', { d: 'M -8,0 Q -12,-16 0,-21 Q 9,-24 10,-13 L 10,0 Z', fill: coat }),
    h('rect', { x: 4.5, y: -12, width: 4.5, height: 12, rx: 2.2, fill: coat }),
    h('circle', { cx: 8, cy: -27, r: 7, fill: coat }),
    h('g', { className: 'ofc-earFlick' }, h('path', { d: 'M 2.5,-32 L 4,-38 L 7.5,-32.5 Z', fill: coat })),
    h('path', { d: 'M 8.5,-33 L 11.5,-38.5 L 13,-32 Z', fill: coat }),
    h('circle', { className: 'ofc-eye', cx: 10.5, cy: -27.5, r: 1.1, fill: '#1F2937' }),
    h('path', { d: 'M 14.5,-25.5 L 19,-26.5 M 14.5,-24 L 19,-23.5', stroke: 'rgba(230,235,242,0.5)', strokeWidth: 0.9 })
  );
}
// Dogs come in breeds; each figure pair is walking + sitting, dispatched below.
function officeRetrieverFig(b, walking) {
  return h('g', null,
    h('g', { className: 'ofc-tailWag' },
      h('path', { d: 'M -15,-14 Q -21,-18 -21,-26', stroke: b.body, strokeWidth: 5, fill: 'none', strokeLinecap: 'round' })),
    h('g', { className: walking ? 'ofc-petLegA' : undefined }, h('rect', { x: -12, y: -11, width: 5, height: 11.5, rx: 2.4, fill: b.body })),
    h('g', { className: walking ? 'ofc-petLegB' : undefined }, h('rect', { x: 5, y: -11, width: 5, height: 11.5, rx: 2.4, fill: b.body })),
    h('ellipse', { cx: -1, cy: -17, rx: 15, ry: 8.5, fill: b.body }),
    h('circle', { cx: 14, cy: -26, r: 8, fill: b.body }),
    h('ellipse', { cx: 20.5, cy: -23.5, rx: 4.5, ry: 3.4, fill: b.body }),
    h('circle', { cx: 24, cy: -24.5, r: 1.9, fill: '#1F2937' }),
    h('circle', { className: 'ofc-eye', cx: 15, cy: -27.5, r: 1.2, fill: '#1F2937' }),
    h('path', { d: 'M 7,-32 Q 3,-27 7,-21 Q 10.5,-24 10,-30 Z', fill: b.trim }),
    h('rect', { x: 7, y: -21, width: 11, height: 3, rx: 1.5, fill: '#EF4444', transform: 'rotate(-14 12 -19.5)' })
  );
}
function officeRetrieverSit(b) {
  return h('g', null,
    h('g', { className: 'ofc-tailWag' },
      h('path', { d: 'M -8,-3 Q -16,-5 -16,-13', stroke: b.body, strokeWidth: 5, fill: 'none', strokeLinecap: 'round' })),
    h('path', { d: 'M -10,0 Q -14,-18 0,-24 Q 10,-27 11,-14 L 11,0 Z', fill: b.body }),
    h('rect', { x: 5, y: -14, width: 5, height: 14, rx: 2.4, fill: b.body }),
    h('circle', { cx: 9, cy: -31, r: 8, fill: b.body }),
    h('ellipse', { cx: 15.5, cy: -28.5, rx: 4.5, ry: 3.4, fill: b.body }),
    h('circle', { cx: 19, cy: -29.5, r: 1.9, fill: '#1F2937' }),
    h('circle', { className: 'ofc-eye', cx: 10, cy: -32.5, r: 1.2, fill: '#1F2937' }),
    h('path', { d: 'M 2,-37 Q -2,-32 2,-26 Q 5.5,-29 5,-35 Z', fill: b.trim }),
    h('rect', { x: 2, y: -26, width: 11, height: 3, rx: 1.5, fill: '#EF4444', transform: 'rotate(-14 7 -24.5)' })
  );
}
// Shih tzu: small, long coat that hides the legs, fur-drape ears, topknot bow.
function officeShihTzuFig(b, walking) {
  return h('g', null,
    h('g', { className: 'ofc-tailWag' },
      h('path', { d: 'M -11,-15 Q -16,-23 -8,-23', stroke: b.body, strokeWidth: 5.5, fill: 'none', strokeLinecap: 'round' })),
    h('g', { className: walking ? 'ofc-petLegA' : undefined }, h('rect', { x: -8, y: -6, width: 4, height: 6.5, rx: 2, fill: b.trim })),
    h('g', { className: walking ? 'ofc-petLegB' : undefined }, h('rect', { x: 3, y: -6, width: 4, height: 6.5, rx: 2, fill: b.trim })),
    h('rect', { x: -13, y: -19, width: 24, height: 15, rx: 7, fill: b.body }),
    h('circle', { cx: 11, cy: -20, r: 6.5, fill: b.body }),
    h('path', { d: 'M 5.5,-24 Q 3,-16 6,-12 Q 9,-14 8.5,-22 Z', fill: b.trim }),
    h('path', { d: 'M 8.5,-26.5 Q 8,-30.5 12,-30 Q 14.5,-29 13.5,-26 Z', fill: b.body }),
    h('circle', { cx: 11.5, cy: -29.5, r: 1.5, fill: '#F472B6' }),
    h('circle', { className: 'ofc-eye', cx: 13.5, cy: -21, r: 1.1, fill: '#1F2937' }),
    h('circle', { cx: 17, cy: -19.5, r: 1.7, fill: '#1F2937' })
  );
}
function officeShihTzuSit(b) {
  return h('g', null,
    h('g', { className: 'ofc-tailWag' },
      h('path', { d: 'M -7,-13 Q -12,-21 -4,-20', stroke: b.body, strokeWidth: 5.5, fill: 'none', strokeLinecap: 'round' })),
    h('path', { d: 'M -9,0 Q -12,-14 -1,-18 Q 7,-20 8,-10 L 8,0 Z', fill: b.body }),
    h('rect', { x: 2.5, y: -5, width: 4, height: 5.5, rx: 2, fill: b.trim }),
    h('circle', { cx: 6, cy: -22, r: 6.5, fill: b.body }),
    h('path', { d: 'M 0.5,-26 Q -2,-18 1,-14 Q 4,-16 3.5,-24 Z', fill: b.trim }),
    h('path', { d: 'M 3.5,-28.5 Q 3,-32.5 7,-32 Q 9.5,-31 8.5,-28 Z', fill: b.body }),
    h('circle', { cx: 6.5, cy: -31.5, r: 1.5, fill: '#F472B6' }),
    h('circle', { className: 'ofc-eye', cx: 8.5, cy: -23, r: 1.1, fill: '#1F2937' }),
    h('circle', { cx: 12, cy: -21.5, r: 1.7, fill: '#1F2937' })
  );
}
// Dachshund: long and low, long muzzle, big floppy ear.
function officeDachshundFig(b, walking) {
  return h('g', null,
    h('g', { className: 'ofc-tailWag' },
      h('path', { d: 'M -18,-10 Q -23,-13 -24,-18', stroke: b.body, strokeWidth: 4, fill: 'none', strokeLinecap: 'round' })),
    h('g', { className: walking ? 'ofc-petLegA' : undefined }, h('rect', { x: -15, y: -7, width: 4, height: 7.5, rx: 2, fill: b.body })),
    h('g', { className: walking ? 'ofc-petLegB' : undefined }, h('rect', { x: 9, y: -7, width: 4, height: 7.5, rx: 2, fill: b.body })),
    h('ellipse', { cx: -1, cy: -11, rx: 17, ry: 6, fill: b.body }),
    h('circle', { cx: 16, cy: -16, r: 6, fill: b.body }),
    h('ellipse', { cx: 22.5, cy: -14.5, rx: 4.5, ry: 2.8, fill: b.body }),
    h('circle', { cx: 26, cy: -15, r: 1.6, fill: '#1F2937' }),
    h('circle', { className: 'ofc-eye', cx: 17, cy: -17.5, r: 1.1, fill: '#1F2937' }),
    h('path', { d: 'M 11,-21 Q 8,-15 12,-11 Q 14.5,-14 14,-20 Z', fill: b.trim }),
    h('rect', { x: 10, y: -12.5, width: 9, height: 2.6, rx: 1.3, fill: '#0EA5E9', transform: 'rotate(-16 14 -11)' })
  );
}
function officeDachshundSit(b) {
  return h('g', null,
    h('g', { className: 'ofc-tailWag' },
      h('path', { d: 'M -9,-3 Q -15,-5 -16,-11', stroke: b.body, strokeWidth: 4, fill: 'none', strokeLinecap: 'round' })),
    h('path', { d: 'M -10,0 Q -13,-13 -3,-16 Q 5,-18 6,-9 L 6,0 Z', fill: b.body }),
    h('rect', { x: 1, y: -8, width: 4, height: 8, rx: 2, fill: b.body }),
    h('circle', { cx: 5, cy: -21, r: 6, fill: b.body }),
    h('ellipse', { cx: 11.5, cy: -19.5, rx: 4.5, ry: 2.8, fill: b.body }),
    h('circle', { cx: 15, cy: -20, r: 1.6, fill: '#1F2937' }),
    h('circle', { className: 'ofc-eye', cx: 6, cy: -22.5, r: 1.1, fill: '#1F2937' }),
    h('path', { d: 'M 0,-26 Q -3,-20 1,-16 Q 3.5,-19 3,-25 Z', fill: b.trim }),
    h('rect', { x: -1, y: -17.5, width: 9, height: 2.6, rx: 1.3, fill: '#0EA5E9', transform: 'rotate(-16 3 -16)' })
  );
}
function officeDogFig(breed, walking) {
  if (breed.kind === 'shihtzu') return officeShihTzuFig(breed, walking);
  if (breed.kind === 'dachshund') return officeDachshundFig(breed, walking);
  return officeRetrieverFig(breed, walking);
}
function officeDogSit(breed) {
  if (breed.kind === 'shihtzu') return officeShihTzuSit(breed);
  if (breed.kind === 'dachshund') return officeDachshundSit(breed);
  return officeRetrieverSit(breed);
}

// Floor-level pet: ambles between its start and dx some of the time, otherwise
// sits somewhere along the way (same ambient bucket rhythm as the walkers).
function officePetWalker(pet, startX, dx, seed) {
  var bucket = officeAmbientBucket(seed);
  var walking = bucket % 3 !== 1; // pets are restless — walking two-thirds of the time
  var shadow = h('ellipse', { cx: 0, cy: 1.5, rx: 16, ry: 3.5, fill: 'rgba(0,0,0,0.28)' });
  if (!walking) {
    var fig = pet.species === 'cat' ? officeCatSit(pet.coat) : officeDogSit(pet.breed);
    var sx = startX + (bucket % 2 ? Math.round(dx * 0.7) : 60);
    return h('g', { key: 'pet', transform: 'translate(' + sx + ',' + FLOOR_GROUND + ') scale(1.6)' }, shadow, fig);
  }
  var walkFig = pet.species === 'cat' ? officeCatFig(pet.coat, true) : officeDogFig(pet.breed, true);
  return h('g', { key: 'pet', transform: 'translate(' + startX + ',' + FLOOR_GROUND + ') scale(1.6)' },
    h('g', { className: 'ofc-pet', style: { '--petdx': (dx / 1.6) + 'px' } },
      shadow,
      h('g', { className: 'ofc-petFlip' }, walkFig)
    )
  );
}

function OfficeFloorArt({ agents, layout, dept, k }) {
  var L = layout;
  var phase = officePhase(), PH = OFFICE_PHASES[phase];
  var K = officeKitchenDecor(dept), KF = OFC_FRIDGES[K.fridge], KC = OFC_CABS[K.cab], KB = OFC_BOWLS[K.bowl];
  var kids = [];

  // defs: window sky (time-of-day) + lamp glow
  kids.push(h('defs', { key: 'defs' },
    h('linearGradient', { id: 'ofcWin', x1: 0, y1: 0, x2: 0, y2: 1 },
      h('stop', { offset: '0%', stopColor: PH.winTop }),
      h('stop', { offset: '100%', stopColor: PH.winBot })),
    h('radialGradient', { id: 'ofcGlow', cx: '50%', cy: '30%', r: '65%' },
      h('stop', { offset: '0%', stopColor: 'rgba(255,214,130,0.14)' }),
      h('stop', { offset: '100%', stopColor: 'rgba(255,214,130,0)' }))
  ));

  // wall + floor
  kids.push(h('rect', { key: 'wall', x: 0, y: 0, width: L.sceneW, height: FLOOR_GROUND, fill: PH.wall }));
  kids.push(h('rect', { key: 'base', x: 0, y: FLOOR_GROUND - 5, width: L.sceneW, height: 5, fill: '#171E2B' }));
  kids.push(h('rect', { key: 'floor', x: 0, y: FLOOR_GROUND, width: L.sceneW, height: FLOOR_H - FLOOR_GROUND, fill: '#161C28' }));
  kids.push(h('rect', { key: 'floorline', x: 0, y: FLOOR_GROUND, width: L.sceneW, height: 2, fill: '#2C3648' }));

  // windows along the wall (skip where the fridge stands)
  var moonDone = false;
  for (var wx = 90; wx + 260 < L.sceneW - 140; wx += CUB_W) {
    kids.push(h('g', { key: 'win' + wx },
      h('rect', { x: wx, y: 34, width: 260, height: 106, rx: 10, fill: 'url(#ofcWin)', stroke: '#2C3648', strokeWidth: 4 }),
      h('line', { x1: wx + 130, y1: 36, x2: wx + 130, y2: 138, stroke: '#2C3648', strokeWidth: 4 }),
      phase === 'night' && !moonDone && h('circle', { cx: wx + 200, cy: 64, r: 13, fill: '#E9E4D0', opacity: 0.9 }),
      phase === 'night' && !moonDone && h('circle', { cx: wx + 194, cy: 60, r: 11, fill: PH.winTop }),
      phase === 'day' && !moonDone && h('circle', { cx: wx + 200, cy: 62, r: 14, fill: '#FDE68A', opacity: 0.95 }),
      phase === 'night' && h('circle', { cx: wx + 40, cy: 58, r: 1.6, fill: '#8FA4C8', opacity: 0.8 }),
      phase === 'night' && h('circle', { cx: wx + 84, cy: 88, r: 1.3, fill: '#8FA4C8', opacity: 0.6 }),
      phase === 'night' && h('circle', { cx: wx + 168, cy: 108, r: 1.4, fill: '#8FA4C8', opacity: 0.7 })
    ));
    moonDone = true;
  }

  // pendant lamps: one over each cubicle, break area, kitchen
  var lampXs = [];
  for (var i = 0; i < L.slots; i++) lampXs.push(L.rowX + i * CUB_W + CUB_W / 2);
  if (L.recW) lampXs.push(L.recX + L.recW / 2);
  lampXs.push(L.breakX + 130, L.kitchX + 220);
  lampXs.forEach(function(lx, li) {
    kids.push(h('g', { key: 'lamp' + li },
      h('line', { x1: lx, y1: 0, x2: lx, y2: 22, stroke: '#4A5568', strokeWidth: 3 }),
      h('path', { d: 'M ' + (lx - 22) + ',40 L ' + (lx - 12) + ',22 L ' + (lx + 12) + ',22 L ' + (lx + 22) + ',40 Z', fill: '#2E3950' }),
      PH.lampOn && h('ellipse', { cx: lx, cy: 44, rx: 20, ry: 5, fill: '#FFD68A', opacity: 0.55 }),
      PH.lampOn && h('ellipse', { cx: lx, cy: 62, rx: 64, ry: 34, fill: 'url(#ofcGlow)' })
    ));
  });

  // cubicles: partitions + embedded close-up desk scene per slot
  for (var s = 0; s <= L.slots; s++) {
    var px = L.rowX + s * CUB_W;
    kids.push(h('g', { key: 'part' + s },
      h('rect', { x: px, y: 118, width: 14, height: FLOOR_GROUND - 118, rx: 6, fill: '#3A4658' }),
      h('rect', { x: px, y: 118, width: 14, height: 7, rx: 3.5, fill: '#4C5A70' })
    ));
  }
  agents.forEach(function(a, i) {
    var status = a.status || 'offline';
    var variant = officeVariant(status);
    kids.push(h('svg', {
      key: 'cub' + a.id, x: L.rowX + i * CUB_W + 26, y: FLOOR_GROUND - 183.5, width: 320, height: 202.7,
      viewBox: '0 0 ' + OFC_W + ' ' + OFC_H, style: { overflow: 'visible' },
    }, officeDeskKids(variant, a.shirt, status === 'error', a.hair, false, officeDecor(a.id), officeLook(a))));
  });
  for (var e = agents.length; e < L.slots; e++) {
    kids.push(h('svg', {
      key: 'empty' + e, x: L.rowX + e * CUB_W + 26, y: FLOOR_GROUND - 183.5, width: 320, height: 202.7,
      viewBox: '0 0 ' + OFC_W + ' ' + OFC_H, style: { overflow: 'visible' }, opacity: 0.85,
    }, officeDeskKids('dark', null, false)));
  }

  // records zone (Filing floor only): three file cabinets on a rug
  if (L.recW) {
    var rx0 = L.recX + 18;
    kids.push(h('g', { key: 'records' },
      h('rect', { x: L.recX + 6, y: 252, width: L.recW - 12, height: 16, rx: 8, fill: '#222B3B' }),
      officeFileCabinet('cab1', rx0, FLOOR_GROUND, false, 'box'),
      officeFileCabinet('cab2', rx0 + 74, FLOOR_GROUND, true, null),
      officeFileCabinet('cab3', rx0 + 148, FLOOR_GROUND, false, 'plant')
    ));
  }

  // break area: water cooler + sofa (design per floor) + floor plant
  var bx = L.breakX;
  kids.push(h('g', { key: 'break' },
    h('rect', { x: bx, y: 252, width: L.breakW - 10, height: 16, rx: 8, fill: '#222B3B' }), // rug
    // water cooler
    h('ellipse', { cx: bx + 32, cy: FLOOR_GROUND + 2, rx: 22, ry: 4.5, fill: 'rgba(0,0,0,0.28)' }),
    h('rect', { x: bx + 18, y: 196, width: 28, height: 74, rx: 6, fill: OFC_DARK }),
    h('rect', { x: bx + 21, y: 170, width: 22, height: 30, rx: 7, fill: 'rgba(125,211,252,0.55)' }),
    h('rect', { x: bx + 25, y: 174, width: 6, height: 16, rx: 3, fill: 'rgba(255,255,255,0.35)' }),
    h('rect', { x: bx + 24, y: 214, width: 7, height: 5, rx: 2, fill: '#7DD3FC' }),
    h('rect', { x: bx + 34, y: 214, width: 7, height: 5, rx: 2, fill: '#94A3B8' })
  ));
  kids.push(officeSofa(bx + 80, L.sofaV, 'sofa'));
  kids.push(officePlant((L.sofaV + 2) % 5, bx + 80 + OFC_SOFAS[L.sofaV].w + 44, FLOOR_GROUND, 1.9, OFC_POTS[(L.sofaV + 1) % 3], 'fplant'));

  // kitchen: wall cabinets, counter + base cabinets, coffee machine, fridge
  var kx = L.kitchX;
  kids.push(h('g', { key: 'kitchen' },
    h('rect', { x: kx, y: 250, width: 420, height: 20, rx: 4, fill: '#1D2534' }), // tile strip
    h('rect', { x: kx + 10, y: 84, width: 200, height: 44, rx: 8, fill: KC }), // wall cabinet
    h('line', { x1: kx + 110, y1: 88, x2: kx + 110, y2: 124, stroke: '#2A3346', strokeWidth: 3 }),
    h('rect', { x: kx + 56, y: 104, width: 10, height: 4, rx: 2, fill: '#8A94A2' }),
    h('rect', { x: kx + 154, y: 104, width: 10, height: 4, rx: 2, fill: '#8A94A2' }),
    // counter + base cabinets
    h('rect', { x: kx, y: 204, width: 230, height: 12, rx: 5, fill: OFC_WOOD }),
    h('rect', { x: kx + 6, y: 216, width: 218, height: 54, rx: 6, fill: KC }),
    h('line', { x1: kx + 115, y1: 220, x2: kx + 115, y2: 266, stroke: '#2A3346', strokeWidth: 3 }),
    h('rect', { x: kx + 58, y: 238, width: 10, height: 4, rx: 2, fill: '#8A94A2' }),
    h('rect', { x: kx + 162, y: 238, width: 10, height: 4, rx: 2, fill: '#8A94A2' }),
    // coffee machine (the break-time destination)
    h('rect', { x: kx + 28, y: 162, width: 44, height: 42, rx: 6, fill: '#2E3950' }),
    h('rect', { x: kx + 34, y: 168, width: 32, height: 10, rx: 3, fill: '#222B3A' }),
    h('circle', { className: 'ofc-pulse', cx: kx + 64, cy: 196, r: 2.2, fill: '#F87171' }),
    h('rect', { x: kx + 42, y: 190, width: 12, height: 11, rx: 2, fill: '#E5E7EB' }),
    h('path', { className: 'ofc-steam', d: 'M 0,0 Q -2,-5 0,-9', transform: 'translate(' + (kx + 48) + ',186)', stroke: '#CBD5E1', strokeWidth: 2, fill: 'none', strokeLinecap: 'round' }),
    // fruit bowl
    h('path', { d: 'M ' + (kx + 150) + ',198 Q ' + (kx + 165) + ',210 ' + (kx + 180) + ',198 Z', fill: '#4A5568' }),
    h('circle', { cx: kx + 158, cy: 196, r: 5, fill: KB[0] }),
    h('circle', { cx: kx + 168, cy: 195, r: 5, fill: KB[1] }),
    h('circle', { cx: kx + 176, cy: 197, r: 5, fill: KB[2] }),
    // dept-specific counter appliance: microwave or kettle (or clear counter)
    K.item === 1 && h('g', null,
      h('rect', { x: kx + 186, y: 182, width: 36, height: 22, rx: 3, fill: '#2E3950' }),
      h('rect', { x: kx + 190, y: 186, width: 20, height: 14, rx: 2, fill: '#222B3A' }),
      h('circle', { cx: kx + 216, cy: 189, r: 1.6, fill: '#8A94A2' }),
      h('circle', { cx: kx + 216, cy: 195, r: 1.6, fill: '#8A94A2' })),
    K.item === 2 && h('g', null,
      h('rect', { x: kx + 190, y: 186, width: 22, height: 18, rx: 7, fill: '#8A94A2' }),
      h('path', { d: 'M ' + (kx + 212) + ',192 Q ' + (kx + 219) + ',194 ' + (kx + 212) + ',198', stroke: '#8A94A2', strokeWidth: 3, fill: 'none' }),
      h('rect', { x: kx + 196, y: 182, width: 10, height: 5, rx: 2.5, fill: '#6B7280' })),
    // fridge
    h('ellipse', { cx: kx + 330, cy: FLOOR_GROUND + 2, rx: 45, ry: 5, fill: 'rgba(0,0,0,0.28)' }),
    h('rect', { x: kx + 288, y: 96, width: 84, height: 174, rx: 10, fill: KF[0] }),
    h('rect', { x: kx + 288, y: 96, width: 84, height: 58, rx: 10, fill: KF[1] }),
    h('rect', { x: kx + 296, y: 132, width: 5, height: 16, rx: 2.5, fill: '#8A94A2' }),
    h('rect', { x: kx + 296, y: 166, width: 5, height: 30, rx: 2.5, fill: '#8A94A2' }),
    h('rect', { x: kx + 306, y: 108, width: 22, height: 14, rx: 3, fill: '#8FE3C0', opacity: 0.5 }) // magnet note
  ));

  // restroom past the fridge; the slider reads occupied while a break walker is inside
  var wcBusy = agents.some(function(a, i) {
    if ((a.status || '') !== 'idle') return false;
    var b = officeAmbientBucket(i * 3 + 1);
    return b % 3 === 0 && ((b >>> 2) % 3) === 0;
  });
  kids.push(officeRestroom(L.wcX, 'wc', wcBusy));

  // roomba patrolling the open floor
  kids.push(h('g', { key: 'roomba', transform: 'translate(' + (L.rowX + 60) + ',' + (FLOOR_GROUND - 6) + ') scale(1.7)' },
    h('g', { className: 'ofc-roomba', style: { '--rdx': ((L.breakX + 140 - L.rowX - 60) / 1.7) + 'px' } },
      h('ellipse', { cx: 0, cy: 8, rx: 20, ry: 4, fill: 'rgba(0,0,0,0.3)' }),
      h('ellipse', { cx: 0, cy: 0, rx: 18, ry: 7.5, fill: '#46536B' }),
      h('ellipse', { cx: 0, cy: -3, rx: 12, ry: 5, fill: '#596885' }),
      h('circle', { className: 'ofc-pulse', cx: 0, cy: -3.5, r: 2, fill: '#34D399' })
    )
  ));

  // agents on break: some stroll to the coffee machine or the restroom, the rest sit on the sofa with
  // their coffee (Anthony 2026-09-06); once the sofa is full they stand by the machine as before
  var seats = officeSofaSeats(L.breakX + 80, L.sofaV), seatIdx = 0;
  agents.forEach(function(a, i) {
    if ((a.status || '') !== 'idle') return;
    var seed = i * 3 + 1, b = officeAmbientBucket(seed), walking = b % 3 === 0, toWc = walking && ((b >>> 2) % 3) === 0;
    var startX = L.rowX + i * CUB_W + 88, shirt = a.shirt || '#14B8A6', look = officeLook(a);
    if (walking) kids.push(officeWalker(shirt, startX, (toWc ? L.wcX + 40 : L.kitchX + 105) - startX, a.hair, seed, look, { key: 'walk' + a.id, walking: true, hide: toWc }));
    else if (seatIdx < seats.length) { var st = seats[seatIdx++]; kids.push(officeSitter(shirt, look, st.x, st.y, 'sit' + a.id)); }
    else kids.push(officeWalker(shirt, startX, L.kitchX + 105 - startX, a.hair, seed, look, { key: 'walk' + a.id, walking: false }));
  });

  // the department pet ambles between the cubicles and the break sofa;
  // its food + water bowls live at the kitchen end
  var pet = officePetFor(dept);
  kids.push(officePetWalker(pet, L.rowX + 140, L.breakX + 40 - (L.rowX + 140), pet.seed));
  kids.push(h('g', { key: 'petbowls' },
    h('rect', { x: L.kitchX + 238, y: FLOOR_GROUND - 4, width: 46, height: 6, rx: 3, fill: '#222B3B' }),
    h('path', { d: 'M ' + (L.kitchX + 242) + ',' + (FLOOR_GROUND - 10) + ' L ' + (L.kitchX + 258) + ',' + (FLOOR_GROUND - 10) + ' L ' + (L.kitchX + 255) + ',' + (FLOOR_GROUND - 3) + ' L ' + (L.kitchX + 245) + ',' + (FLOOR_GROUND - 3) + ' Z', fill: '#C2703D' }),
    h('ellipse', { cx: L.kitchX + 250, cy: FLOOR_GROUND - 10, rx: 8, ry: 2, fill: '#8F5A2E' }),
    h('path', { d: 'M ' + (L.kitchX + 264) + ',' + (FLOOR_GROUND - 10) + ' L ' + (L.kitchX + 280) + ',' + (FLOOR_GROUND - 10) + ' L ' + (L.kitchX + 277) + ',' + (FLOOR_GROUND - 3) + ' L ' + (L.kitchX + 267) + ',' + (FLOOR_GROUND - 3) + ' Z', fill: '#56688C' }),
    h('ellipse', { cx: L.kitchX + 272, cy: FLOOR_GROUND - 10, rx: 8, ry: 2, fill: '#7DD3FC', opacity: 0.75 })
  ));

  var KS = k || FLOOR_K;
  return h('svg', {
    width: Math.round(L.sceneW * KS), height: Math.round(FLOOR_H * KS),
    viewBox: '0 0 ' + L.sceneW + ' ' + FLOOR_H, style: { display: 'block' },
  }, kids);
}

// ── Status metadata ──
var OFFICE_STATUS_META = {
  working:    { label: 'Working', color: '#10B981' },
  idle:       { label: 'On break', color: '#3B82F6' },
  away:       { label: 'Away from desk', color: '#F59E0B' },
  error:      { label: 'Needs attention', color: '#EF4444' },
  offline:    { label: 'Off duty', color: '#94A3B8' },
  onboarding: { label: 'Onboarding', color: '#A78BFA' },
};
var OFFICE_STATE_META = {
  auto_sent:  { label: 'Auto',   bg: 'rgba(16,185,129,0.12)', text: '#10B981' },
  held:       { label: 'Held',   bg: 'rgba(245,158,11,0.12)', text: '#F59E0B' },
  noise_done: { label: 'Noise',  bg: 'rgba(148,163,184,0.12)', text: '#94A3B8' },
  forwarded:  { label: 'Fwd',    bg: 'rgba(99,102,241,0.12)', text: '#6366F1' },
  error:      { label: 'Error',  bg: 'rgba(239,68,68,0.12)',  text: '#EF4444' },
  // invoice-filing agent states
  filed:      { label: 'Filed',  bg: 'rgba(16,185,129,0.12)', text: '#10B981' },
  proposed:   { label: 'Pilot',  bg: 'rgba(245,158,11,0.12)', text: '#F59E0B' },
  skipped:    { label: 'Skip',   bg: 'rgba(148,163,184,0.12)', text: '#94A3B8' },
  duplicate:  { label: 'Duplicate', bg: 'rgba(148,163,184,0.12)', text: '#94A3B8' },
  // Anthony 2026-09-01: exceptions are a caution (needs a human decision),
  // yellow — red stays reserved for real errors.
  exception:  { label: 'Review', bg: 'rgba(234,179,8,0.16)',  text: '#CA8A04' },
  // Anthony 9-10: queued file-by-hand — Danny is fetching + uploading in the background
  filing:     { label: 'Filing…', bg: 'rgba(59,130,246,0.14)', text: '#3B82F6' },
  split:      { label: 'Split',  bg: 'rgba(99,102,241,0.12)', text: '#6366F1' },
  // eBay feedback agent (Ethan): the buyer's rating, or Answered once a public reply exists
  positive:   { label: 'Positive', bg: 'rgba(16,185,129,0.12)', text: '#10B981' },
  neutral:    { label: 'Neutral',  bg: 'rgba(245,158,11,0.12)', text: '#F59E0B' },
  negative:   { label: 'Negative', bg: 'rgba(239,68,68,0.12)',  text: '#EF4444' },
  answered:   { label: 'Answered', bg: 'rgba(99,102,241,0.12)', text: '#6366F1' },
  drafted:    { label: 'Draft',    bg: 'rgba(245,158,11,0.12)', text: '#F59E0B' },
  posted:     { label: 'Posted',   bg: 'rgba(16,185,129,0.12)', text: '#10B981' },
  failed:     { label: 'Failed',   bg: 'rgba(239,68,68,0.12)',  text: '#EF4444' },
  // Iris (listing monitor): severity of a catalog change
  high:       { label: 'High',     bg: 'rgba(239,68,68,0.12)',  text: '#EF4444' },
  material:   { label: 'Changed',  bg: 'rgba(245,158,11,0.12)', text: '#F59E0B' },
  cosmetic:   { label: 'Cosmetic', bg: 'rgba(148,163,184,0.12)', text: '#94A3B8' },
  // Shopify FAQ agent (Sage)
  generated:  { label: 'FAQ',      bg: 'rgba(16,185,129,0.12)', text: '#10B981' },
  pending:    { label: 'Pending',  bg: 'rgba(148,163,184,0.12)', text: '#94A3B8' },
};
// Time of day — the scenes follow the viewer's local clock (page re-renders on
// the 30s tick, so sunrise flips the scene without a reload).
// window.OFFICE_FORCE_PHASE = 'day'|'dusk'|'night' forces it (testing/demo).
function officePhase() {
  if (window.OFFICE_FORCE_PHASE) return window.OFFICE_FORCE_PHASE;
  var hr = new Date().getHours();
  if (hr >= 8 && hr < 18) return 'day';
  if ((hr >= 6 && hr < 8) || (hr >= 18 && hr < 21)) return 'dusk';
  return 'night';
}
var OFFICE_PHASES = {
  day: {
    backdrop: 'linear-gradient(180deg, #8FB8DE 0%, #A9CBE8 55%, #7FA6CC 100%)',
    label: '#33465E', winTop: '#A5CBEA', winBot: '#DCEEFB', wall: '#2E3B50',
    lampOn: false, cloudFill: '#FFFFFF', cloudOp: 0.5,
  },
  dusk: {
    backdrop: 'linear-gradient(180deg, #3A4569 0%, #55486B 60%, #7A5A62 100%)',
    label: '#9BA3B6', winTop: '#54608F', winBot: '#8A6472', wall: '#252E40',
    lampOn: true, cloudFill: '#D9B8A6', cloudOp: 0.2,
  },
  night: {
    backdrop: 'radial-gradient(900px 340px at 45% -10%, #273043 0%, #1D2433 52%, #171D2A 100%)',
    label: '#4B5768', winTop: '#1E3050', winBot: '#101A2E', wall: '#202836',
    lampOn: true, cloudFill: '#8FA4C8', cloudOp: 0.1,
  },
};

function officeWhen(d) {
  return new Date(d).toLocaleString([], { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}
function officeAgo(d) {
  if (!d) return 'never';
  var s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return Math.floor(s / 60) + ' min ago';
  if (s < 86400) return Math.floor(s / 3600) + ' h ago';
  return Math.floor(s / 86400) + ' d ago';
}
function officeMoney(n) { return '$' + Number(n || 0).toFixed(2); }

function officeSign(status) {
  if (status === 'away') return { text: 'BE RIGHT BACK', bg: '#F59E0B', color: '#3A2A05' };
  if (status === 'offline') return { text: 'OFF DUTY', bg: '#475569', color: '#E2E8F0' };
  if (status === 'onboarding') return { text: 'STARTING SOON', bg: '#A78BFA', color: '#241A4D' };
  return null;
}

// Name card under a desk (Anthony 2026-09-06: two lines, smaller type): status dot + name + state
// on the first line, the agent's responsibility on the second. Width is bounded by the cubicle.
function officeNameCard(a, meta, maxW) {
  return h('div', { style: { display: 'flex', flexDirection: 'column', gap: 1, maxWidth: maxW, background: 'rgba(10,14,22,0.66)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10, padding: '4px 10px 5px', backdropFilter: 'blur(6px)', textAlign: 'left' } },
    h('div', { style: { display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 } },
      h('span', { style: { width: 7, height: 7, borderRadius: '50%', background: meta.color, boxShadow: '0 0 7px ' + meta.color, flexShrink: 0 } }),
      h('span', { style: { fontSize: 11.5, fontWeight: 700, color: '#F1F5F9', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }, a.name),
      h('span', { style: { fontSize: 9.5, fontWeight: 600, color: meta.color, marginLeft: 'auto', paddingLeft: 10, whiteSpace: 'nowrap' } }, meta.label)),
    h('div', { style: { fontSize: 10, fontWeight: 500, color: '#9CA3AF', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', paddingLeft: 13 } }, a.role || ''));
}

// The floor panel: scene SVG + DOM overlays (bubbles, signs, name pills,
// clickable cubicle zones). Scrolls horizontally when the floor outgrows the
// container.
function OfficeFloor({ agents, data, loading, onOpen, dept }) {
  var L = officeFloorLayout(agents.length, dept, officeSofaVariant(dept, data && data.agents));
  var fit = useOfficeFitScale(L.sceneW);
  var K = fit.k;
  var w = Math.round(L.sceneW * K), hgt = Math.round(FLOOR_H * K);
  var overlays = [];
  agents.forEach(function(a, i) {
    var cubX = (L.rowX + i * CUB_W) * K;
    var status = a.status || 'offline';
    var meta = OFFICE_STATUS_META[status] || OFFICE_STATUS_META.offline;
    var sign = officeSign(status);
    overlays.push(h(Fragment, { key: 'ov' + a.id },
      sign && h('div', { className: 'officesign ofc-float', style: { left: cubX + 205 * K, top: 128 * K, background: sign.bg, color: sign.color } }, sign.text),
      h('div', {
        className: 'officedesk-zone', title: 'Zoom in on ' + a.name,
        onClick: function() { onOpen(a.id); },
        style: { position: 'absolute', left: cubX + 10, top: 50, width: CUB_W * K - 16, height: hgt - 58, zIndex: 7 },
      }),
      h('div', { style: { position: 'absolute', left: cubX, bottom: 4, width: CUB_W * K, display: 'flex', justifyContent: 'center', zIndex: 8, pointerEvents: 'none' } },
        officeNameCard(a, meta, CUB_W * K - 28))
    ));
  });
  for (var e = agents.length; e < L.slots; e++) {
    var ex = (L.rowX + e * CUB_W) * K;
    overlays.push(h(Fragment, { key: 'hire' + e },
      h('div', { className: 'officesign ofc-float', style: { left: ex + 205 * K, top: 128 * K, background: '#38BDF8', color: '#082032' } }, 'NOW HIRING'),
      h('div', { style: { position: 'absolute', left: ex, bottom: 4, width: CUB_W * K, textAlign: 'center', zIndex: 8, pointerEvents: 'none' } },
        h('div', { style: { display: 'inline-flex', background: 'rgba(10,14,22,0.62)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 999, padding: '5px 13px', backdropFilter: 'blur(6px)' } },
          h('span', { style: { fontSize: 11, fontWeight: 500, color: '#9CA3AF' } }, 'Open desk, next agent we build sits here')
        )
      )
    ));
  }
  var PH = OFFICE_PHASES[officePhase()];
  return h('div', { ref: fit.ref, className: 'officezoom', style: { position: 'relative', borderRadius: 12, border: '1px solid ' + C.border, overflow: 'hidden', background: PH.backdrop } },
    h('div', { style: { position: 'absolute', top: 13, left: 20, fontSize: 11, fontWeight: 700, letterSpacing: 2.5, color: PH.label, zIndex: 9 } }, 'ATHARVA BRANDS · ' + ((dept || 'AGENT') + ' FLOOR').toUpperCase()),
    h('div', { style: { position: 'absolute', top: 12, right: 20, fontSize: 11, fontWeight: 600, color: PH.label, zIndex: 9 } },
      data ? 'updated ' + officeAgo(data.generated_at) : 'loading...'),
    loading && !data
      ? h('div', { style: { color: '#9CA3AF', fontSize: 13, fontWeight: 600, padding: '110px 40px' } }, 'Opening the office...')
      : h('div', { style: { overflowX: 'auto', overflowY: 'hidden' } },
          h('div', { style: { position: 'relative', width: w, height: hgt, margin: '0 auto' } },
            h(OfficeFloorArt, { agents: agents, layout: L, dept: dept, k: K }),
            overlays
          )
        )
  );
}

// ── Reception floor (the walked-into lobby) ──
// Mirrors the building's lobby at floor scale: glass entry doors, the same
// counter scene as the close-up (embedded like cubicles are), waiting area.
var RECEP_W = 1500;

function OfficeReceptionFloorArt({ agent, k }) {
  var phase = officePhase(), PH = OFFICE_PHASES[phase];
  var status = (agent && agent.status) || 'offline';
  var variant = officeVariant(status);
  var kids = [];

  kids.push(h('defs', { key: 'defs' },
    h('linearGradient', { id: 'ofcWin', x1: 0, y1: 0, x2: 0, y2: 1 },
      h('stop', { offset: '0%', stopColor: PH.winTop }),
      h('stop', { offset: '100%', stopColor: PH.winBot })),
    h('radialGradient', { id: 'ofcGlow', cx: '50%', cy: '30%', r: '65%' },
      h('stop', { offset: '0%', stopColor: 'rgba(255,214,130,0.14)' }),
      h('stop', { offset: '100%', stopColor: 'rgba(255,214,130,0)' }))
  ));
  kids.push(h('rect', { key: 'wall', x: 0, y: 0, width: RECEP_W, height: FLOOR_GROUND, fill: PH.wall }));
  kids.push(h('rect', { key: 'base', x: 0, y: FLOOR_GROUND - 5, width: RECEP_W, height: 5, fill: '#171E2B' }));
  kids.push(h('rect', { key: 'floor', x: 0, y: FLOOR_GROUND, width: RECEP_W, height: FLOOR_H - FLOOR_GROUND, fill: '#161C28' }));
  kids.push(h('rect', { key: 'floorline', x: 0, y: FLOOR_GROUND, width: RECEP_W, height: 2, fill: '#2C3648' }));

  // one window over the waiting area
  kids.push(h('g', { key: 'win' },
    h('rect', { x: 1140, y: 34, width: 260, height: 106, rx: 10, fill: 'url(#ofcWin)', stroke: '#2C3648', strokeWidth: 4 }),
    h('line', { x1: 1270, y1: 36, x2: 1270, y2: 138, stroke: '#2C3648', strokeWidth: 4 }),
    phase === 'day' && h('circle', { cx: 1340, cy: 62, r: 14, fill: '#FDE68A', opacity: 0.95 }),
    phase === 'night' && h('circle', { cx: 1340, cy: 64, r: 13, fill: '#E9E4D0', opacity: 0.9 }),
    phase === 'night' && h('circle', { cx: 1334, cy: 60, r: 11, fill: PH.winTop })
  ));

  // pendant lamps
  [300, 700, 1100].forEach(function(lx, li) {
    kids.push(h('g', { key: 'lamp' + li },
      h('line', { x1: lx, y1: 0, x2: lx, y2: 22, stroke: '#4A5568', strokeWidth: 3 }),
      h('path', { d: 'M ' + (lx - 22) + ',40 L ' + (lx - 12) + ',22 L ' + (lx + 12) + ',22 L ' + (lx + 22) + ',40 Z', fill: '#2E3950' }),
      PH.lampOn && h('ellipse', { cx: lx, cy: 44, rx: 20, ry: 5, fill: '#FFD68A', opacity: 0.55 }),
      PH.lampOn && h('ellipse', { cx: lx, cy: 62, rx: 64, ry: 34, fill: 'url(#ofcGlow)' })
    ));
  });

  // glass entry doors + welcome mat (left)
  var dx0 = 120;
  kids.push(h('g', { key: 'doors' },
    h('rect', { x: dx0 - 12, y: 96, width: 186, height: 14, rx: 5, fill: '#39445A' }),
    h('rect', { x: dx0, y: 110, width: 76, height: 160, rx: 4, fill: 'rgba(125,211,252,0.14)', stroke: '#4C5A70', strokeWidth: 5 }),
    h('rect', { x: dx0 + 86, y: 110, width: 76, height: 160, rx: 4, fill: 'rgba(125,211,252,0.14)', stroke: '#4C5A70', strokeWidth: 5 }),
    h('circle', { cx: dx0 + 66, cy: 192, r: 4, fill: '#8A94A2' }),
    h('circle', { cx: dx0 + 96, cy: 192, r: 4, fill: '#8A94A2' }),
    h('rect', { x: dx0 + 14, y: FLOOR_GROUND + 6, width: 134, height: 10, rx: 4, fill: '#22303F' })
  ));

  // the front desk: SAME art as the zoomed-in reception view
  kids.push(h('svg', {
    key: 'recep', x: 430, y: FLOOR_GROUND - 183.5, width: 320, height: 202.7,
    viewBox: '0 0 ' + OFC_W + ' ' + OFC_H, style: { overflow: 'visible' },
  }, officeReceptionKids(variant, agent && agent.shirt, agent && agent.hair, false, status === 'error', agent ? officeLook(agent) : null)));

  // water cooler between the desk and the waiting area
  kids.push(h('g', { key: 'cooler' },
    h('ellipse', { cx: 892, cy: FLOOR_GROUND + 2, rx: 22, ry: 4.5, fill: 'rgba(0,0,0,0.28)' }),
    h('rect', { x: 878, y: 196, width: 28, height: 74, rx: 6, fill: OFC_DARK }),
    h('rect', { x: 881, y: 170, width: 22, height: 30, rx: 7, fill: 'rgba(125,211,252,0.55)' }),
    h('rect', { x: 885, y: 174, width: 6, height: 16, rx: 3, fill: 'rgba(255,255,255,0.35)' }),
    h('rect', { x: 884, y: 214, width: 7, height: 5, rx: 2, fill: '#7DD3FC' }),
    h('rect', { x: 894, y: 214, width: 7, height: 5, rx: 2, fill: '#94A3B8' })
  ));

  // waiting area: rug, sofa, low table with magazines, big plant
  var bx = 970;
  kids.push(h('g', { key: 'wait' },
    h('rect', { x: bx, y: 252, width: 330, height: 16, rx: 8, fill: '#222B3B' }),
    officeSofa(bx + 20, 3, 'lobbysofa'), // the lobby keeps a chesterfield
    // low table + magazines
    h('rect', { x: bx + 200, y: 238, width: 80, height: 8, rx: 4, fill: OFC_WOOD }),
    h('rect', { x: bx + 208, y: 246, width: 7, height: 24, rx: 3, fill: OFC_WOOD_DARK }),
    h('rect', { x: bx + 264, y: 246, width: 7, height: 24, rx: 3, fill: OFC_WOOD_DARK }),
    h('rect', { x: bx + 214, y: 232, width: 24, height: 5, rx: 2, fill: '#38BDF8', opacity: 0.7 }),
    h('rect', { x: bx + 240, y: 230, width: 24, height: 5, rx: 2, fill: '#EC4899', opacity: 0.6 }),
    // big plant
    officePlant(1, bx + 322, FLOOR_GROUND, 2.1, OFC_POTS[0], 'bigplant')
  ));

  // roomba on patrol
  kids.push(h('g', { key: 'roomba', transform: 'translate(360,' + (FLOOR_GROUND - 6) + ') scale(1.7)' },
    h('g', { className: 'ofc-roomba', style: { '--rdx': (420 / 1.7) + 'px' } },
      h('ellipse', { cx: 0, cy: 8, rx: 20, ry: 4, fill: 'rgba(0,0,0,0.3)' }),
      h('ellipse', { cx: 0, cy: 0, rx: 18, ry: 7.5, fill: '#46536B' }),
      h('ellipse', { cx: 0, cy: -3, rx: 12, ry: 5, fill: '#596885' }),
      h('circle', { className: 'ofc-pulse', cx: 0, cy: -3.5, r: 2, fill: '#34D399' })
    )
  ));

  // a visitor wandering in from the doors; the receptionist strolls on break
  kids.push(officeWalker('#856F5A', 330, 500, 'short', 11));
  if (status === 'idle' && agent) {
    // on break the receptionist strolls the lobby or sits on the sofa next to the cat
    var rb = officeAmbientBucket(5);
    kids.push(rb % 3 === 0
      ? officeWalker(agent.shirt || '#7C6FA8', 600, 320, agent.hair, 5, officeLook(agent), { key: 'recwalk', walking: true })
      : officeSitter(agent.shirt || '#7C6FA8', officeLook(agent), bx + 142, 234, 'recsit'));
  }

  // lobby pets: the office cat asleep on the sofa, the office dog greeting
  // whoever comes through the doors
  kids.push(h('g', { key: 'lobbycat', transform: 'translate(' + (bx + 88) + ',232) scale(1.5)' },
    h('g', { className: 'ofc-tailSway' },
      h('path', { d: 'M -10,-3 Q -17,-2 -15,-9', stroke: '#D08048', strokeWidth: 4, fill: 'none', strokeLinecap: 'round' })),
    h('ellipse', { cx: 0, cy: -4, rx: 12, ry: 6, fill: '#D08048' }),
    h('circle', { cx: 9, cy: -8, r: 5.5, fill: '#D08048' }),
    h('path', { d: 'M 5,-12 L 6,-16.5 L 9,-12.5 Z', fill: '#D08048' }),
    h('path', { d: 'M 9.5,-13 L 12,-17 L 13,-12 Z', fill: '#D08048' }),
    h('path', { d: 'M 10.5,-8 Q 11.5,-7 12.5,-8', stroke: '#7C4A22', strokeWidth: 0.9, fill: 'none', strokeLinecap: 'round' })
  ));
  kids.push(officePetWalker({ species: 'dog', breed: OFC_DOG_BREEDS[1] }, 260, 560, 7)); // the lobby shih tzu

  var KS = k || FLOOR_K;
  return h('svg', {
    width: Math.round(RECEP_W * KS), height: Math.round(FLOOR_H * KS),
    viewBox: '0 0 ' + RECEP_W + ' ' + FLOOR_H, style: { display: 'block' },
  }, kids);
}

function OfficeReceptionFloor({ agent, data, loading, onOpen }) {
  var fit = useOfficeFitScale(RECEP_W);
  var K = fit.k;
  var w = Math.round(RECEP_W * K), hgt = Math.round(FLOOR_H * K);
  var overlays = [];
  if (agent) {
    var status = agent.status || 'offline';
    var meta = OFFICE_STATUS_META[status] || OFFICE_STATUS_META.offline;
    var sign = officeSign(status);
    overlays.push(h(Fragment, { key: 'ov' + agent.id },
      sign && h('div', { className: 'officesign ofc-float', style: { left: 600 * K, top: 118 * K, background: sign.bg, color: sign.color } }, sign.text),
      h('div', {
        className: 'officedesk-zone', title: 'Zoom in on ' + agent.name,
        onClick: function() { onOpen(agent.id); },
        style: { position: 'absolute', left: 424 * K, top: 56, width: 336 * K, height: hgt - 64, zIndex: 7 },
      }),
      h('div', { style: { position: 'absolute', left: 380 * K, bottom: 4, width: 420 * K, display: 'flex', justifyContent: 'center', zIndex: 8, pointerEvents: 'none' } },
        officeNameCard(agent, meta, 420 * K - 28))
    ));
  }
  var PH = OFFICE_PHASES[officePhase()];
  return h('div', { ref: fit.ref, className: 'officezoom', style: { position: 'relative', borderRadius: 12, border: '1px solid ' + C.border, overflow: 'hidden', background: PH.backdrop } },
    h('div', { style: { position: 'absolute', top: 13, left: 20, fontSize: 11, fontWeight: 700, letterSpacing: 2.5, color: PH.label, zIndex: 9 } }, 'ATHARVA BRANDS · RECEPTION'),
    h('div', { style: { position: 'absolute', top: 12, right: 20, fontSize: 11, fontWeight: 600, color: PH.label, zIndex: 9 } },
      data ? 'updated ' + officeAgo(data.generated_at) : 'loading...'),
    loading && !data
      ? h('div', { style: { color: '#9CA3AF', fontSize: 13, fontWeight: 600, padding: '110px 40px' } }, 'Opening the lobby...')
      : h('div', { style: { overflowX: 'auto', overflowY: 'hidden' } },
          h('div', { style: { position: 'relative', width: w, height: hgt, margin: '0 auto' } },
            h(OfficeReceptionFloorArt, { agent: agent, k: K }),
            overlays
          )
        )
  );
}

// ── Building view (most zoomed out) ──
// Cutaway of the whole HQ at dusk: rooftop (glowing sign, water tank, antenna,
// cat), one cutaway floor per agent department with mini agents at desks or
// pacing with coffee, a FOR LEASE floor for future agents, a lobby with a
// receptionist and ambient staff, a working elevator, and street life below.
// Ambient walkers are deliberately untagged — only real agents get names.
var BLDG_FH = 112, BLDG_LOBBY_H = 128, BLDG_SKY_H = 170, BLDG_GROUND_H = 66;

function officeBuildingFloors(agents) {
  var by = {}, order = [];
  agents.forEach(function(a) {
    var d = a.dept || 'Operations';
    if (!by[d]) { by[d] = []; order.push(d); }
    by[d].push(a);
  });
  return order.map(function(d) { return { dept: d, agents: by[d] }; });
}

function officeBuildingLayout(floorCount) {
  var rows = floorCount + 1; // +1 FOR LEASE floor on top
  // scene runs well past the panel width so the beach + road reach the edges
  // (the building panel centers its initial scroll)
  var sceneW = 1300, BW = 640;
  var BX = Math.round((sceneW - BW) / 2);
  var topY = BLDG_SKY_H;
  var lobbyY = topY + rows * BLDG_FH;
  var groundY = lobbyY + BLDG_LOBBY_H;
  return { rows: rows, sceneW: sceneW, BW: BW, BX: BX, topY: topY, lobbyY: lobbyY, groundY: groundY, totalH: groundY + BLDG_GROUND_H };
}

// Palm tree, base at origin, ~92 units tall at scale 1.
function officePalmTree(x, gy, s) {
  return h('g', { transform: 'translate(' + x + ',' + gy + ') scale(' + (s || 1) + ')' },
    h('path', { d: 'M 0,0 Q 8,-34 3,-66', stroke: '#8F6B4E', strokeWidth: 7, fill: 'none', strokeLinecap: 'round' }),
    h('path', { d: 'M -3,-18 L 10,-20', stroke: '#74543C', strokeWidth: 2 }),
    h('path', { d: 'M -2,-34 L 11,-36', stroke: '#74543C', strokeWidth: 2 }),
    h('path', { d: 'M 3,-66 Q -22,-74 -38,-58', stroke: '#2E7D4F', strokeWidth: 5, fill: 'none', strokeLinecap: 'round' }),
    h('path', { d: 'M 3,-66 Q 28,-74 44,-58', stroke: '#2E7D4F', strokeWidth: 5, fill: 'none', strokeLinecap: 'round' }),
    h('path', { d: 'M 3,-66 Q -14,-84 -30,-80', stroke: '#35915C', strokeWidth: 5, fill: 'none', strokeLinecap: 'round' }),
    h('path', { d: 'M 3,-66 Q 20,-84 36,-80', stroke: '#35915C', strokeWidth: 5, fill: 'none', strokeLinecap: 'round' }),
    h('path', { d: 'M 3,-66 Q 3,-88 12,-92', stroke: '#245240', strokeWidth: 5, fill: 'none', strokeLinecap: 'round' }),
    h('circle', { cx: -3, cy: -62, r: 3.4, fill: '#74543C' }),
    h('circle', { cx: 9, cy: -61, r: 3.4, fill: '#5E3C28' })
  );
}

// A tiny building-scale person, ~44 units tall. Origin = feet center.
function officeMiniPerson(opts) {
  var shirt = opts.shirt || '#5A6A85';
  var lk = opts.look || officeLookFromHair(opts.hair);
  if (opts.hairColor) lk = Object.assign({}, lk, { hairColor: opts.hairColor });
  return [
    h('ellipse', { key: 'sh', cx: 0, cy: 1, rx: 9, ry: 2, fill: 'rgba(0,0,0,0.3)' }),
    h('g', { key: 'la', className: opts.walking ? 'ofc-legA' : undefined }, h('rect', { x: -5, y: -14, width: 4.5, height: 14, rx: 2, fill: '#2A4879' })),
    h('g', { key: 'lb', className: opts.walking ? 'ofc-legB' : undefined }, h('rect', { x: 0.5, y: -14, width: 4.5, height: 14, rx: 2, fill: '#31548F' })),
    h('g', { key: 'bd', className: opts.walking ? 'ofc-walkbob' : undefined },
      h('rect', { x: -7, y: -31, width: 14, height: 18, rx: 5.5, fill: shirt }),
      opts.mug && h('g', null,
        h('rect', { x: 6, y: -27, width: 5, height: 5.5, rx: 1, fill: '#E5E7EB' }),
        h('path', { className: 'ofc-steam', d: 'M 8.5,-29 Q 7.5,-31 8.5,-33', stroke: '#CBD5E1', strokeWidth: 1.2, fill: 'none', strokeLinecap: 'round' })),
      officeHeadGroup(lk, OFC_HEAD_MINI, 'mini')
    ),
  ];
}

// Person on a span: paces it only some of the time (see officeAmbientBucket),
// otherwise stands somewhere along it.
function officePacer(key, x, y, span, dur, opts, seed) {
  var bucket = officeAmbientBucket(seed);
  if (bucket % 3 !== 0) {
    var frac = ((bucket * 7 + (seed || 0)) % 4) / 3;
    return h('g', { key: key, transform: 'translate(' + Math.round(x + span * frac) + ',' + y + ')' },
      officeMiniPerson(Object.assign({}, opts, { walking: false })));
  }
  var delay = -((seed || 0) * 1.7) + 's';
  var anim = { '--pdx': span + 'px', animationDuration: dur + 's', animationDelay: delay };
  return h('g', { key: key, transform: 'translate(' + x + ',' + y + ')' },
    h('g', { className: 'ofc-pace', style: anim },
      h('g', { className: 'ofc-paceFlip', style: { animationDuration: dur + 's', animationDelay: delay } },
        officeMiniPerson(opts))));
}

// ── Beach crowd (Anthony 2026-09-06) ──
// Figures in swimwear on the sand strip behind the HQ, drawn at building scale (mini-person units,
// ~0.8×). Day = the full crowd; dusk = one sunset watcher on a towel + a couple on a blanket with a
// lantern; night = empty beach. Looks (skin, hair) come from the same system as the agents.
var OFC_SWIM = ['#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#EC4899', '#8B5CF6', '#14B8A6', '#F97316'];
function officeBeachLook(n) { return officeLook({ id: 'beach-' + n, hair: n % 2 ? 'long' : 'short' }); }
// Hats sit on the mini head (centre (0,-37), r 6)
function officeBeachHat(kind, color) {
  if (kind === 'sun') return h('g', null,
    h('rect', { x: -5.5, y: -47, width: 11, height: 7, rx: 3, fill: color || '#E9D8A6' }),
    h('ellipse', { cx: 0, cy: -40.5, rx: 10.5, ry: 2.4, fill: color || '#E9D8A6' }),
    h('rect', { x: -5.5, y: -42.2, width: 11, height: 1.6, fill: '#C2703D' }));
  if (kind === 'cap') return h('g', null,
    h('path', { d: 'M -6.2,-40.5 A 6.2,6.2 0 0 1 6.2,-40.5 Z', fill: color || '#3B82F6' }),
    h('rect', { x: 1, y: -41.3, width: 8.5, height: 1.8, rx: 0.9, fill: color || '#3B82F6' }));
  if (kind === 'visor') return h('g', null,
    h('rect', { x: -6.2, y: -42.2, width: 12.4, height: 2.4, rx: 1.2, fill: '#F1EDE2' }),
    h('rect', { x: 2, y: -41.5, width: 8, height: 1.8, rx: 0.9, fill: '#F1EDE2' }));
  return null;
}
// Standing figure in swimwear, feet at origin, facing right. o: {lk, suit, color, hat, hatColor, walking, ball, bag}
function officeBeachStander(o) {
  var skin = o.lk.skin;
  return h('g', null,
    h('ellipse', { cx: 0, cy: 1, rx: 8, ry: 1.8, fill: 'rgba(0,0,0,0.22)' }),
    h('g', { className: o.walking ? 'ofc-legA' : undefined }, h('rect', { x: -5, y: -14, width: 4.5, height: 14, rx: 2, fill: skin })),
    h('g', { className: o.walking ? 'ofc-legB' : undefined }, h('rect', { x: 0.5, y: -14, width: 4.5, height: 14, rx: 2, fill: skin })),
    h('g', { className: o.walking ? 'ofc-walkbob' : undefined },
      h('rect', { x: -7, y: -31, width: 14, height: 18, rx: 5.5, fill: skin }),
      o.suit === 'onepiece' && h('rect', { x: -7, y: -29, width: 14, height: 16, rx: 5, fill: o.color }),
      o.suit === 'bikini' && h('rect', { x: -7, y: -27, width: 14, height: 4, rx: 2, fill: o.color }),
      o.suit === 'bikini' && h('rect', { x: -7, y: -16, width: 14, height: 4.5, rx: 2, fill: o.color }),
      o.suit === 'trunks' && h('rect', { x: -7, y: -17.5, width: 14, height: 7, rx: 2, fill: o.color }),
      o.bag && h('g', null,
        h('rect', { x: -14, y: -20, width: 8, height: 9, rx: 1.5, fill: '#F1EDE2' }),
        h('rect', { x: -14, y: -16.5, width: 8, height: 1.6, fill: '#EC4899' }),
        h('path', { d: 'M -12,-20 Q -10,-26 -8,-20', stroke: '#B08968', strokeWidth: 1, fill: 'none' })),
      h('rect', { x: 4, y: -30, width: 4, height: 13, rx: 2, fill: skin, transform: o.ball ? 'rotate(-55 6 -29)' : 'rotate(-14 6 -29)' }),
      officeHeadGroup(o.lk, OFC_HEAD_MINI, 'mini'),
      o.hat && officeBeachHat(o.hat, o.hatColor),
      o.ball && h('g', { transform: 'translate(13,-36)' }, h('g', { className: 'ofc-float' },
        h('circle', { cx: 0, cy: 0, r: 5, fill: '#F1EDE2' }),
        h('path', { d: 'M 0,0 L 0,-5 A 5,5 0 0 1 4.33,-2.5 Z', fill: '#EF4444' }),
        h('path', { d: 'M 0,0 L 4.33,2.5 A 5,5 0 0 1 0,5 Z', fill: '#3B82F6' }),
        h('path', { d: 'M 0,0 L -4.33,2.5 A 5,5 0 0 1 -4.33,-2.5 Z', fill: '#F59E0B' })))
    ));
}
// Seated on the ground, hips at origin, legs out to the right. o: {lk, color, hat, lean}
function officeBeachSitter(o) {
  var skin = o.lk.skin;
  return h('g', null,
    h('rect', { x: -2, y: -4.5, width: 16, height: 4.5, rx: 2, fill: skin }),
    h('rect', { x: -6.5, y: -21, width: 13, height: 18, rx: 5, fill: skin }),
    h('rect', { x: -6.5, y: -19, width: 13, height: 15, rx: 4.5, fill: o.color }),
    h('rect', { x: -9, y: -18, width: 4, height: 15, rx: 2, fill: skin, transform: 'rotate(25 -7 -17)' }),
    h('g', { transform: 'translate(0,12)' + (o.lean ? ' rotate(' + o.lean + ' 0 -31)' : '') }, officeHeadGroup(o.lk, OFC_HEAD_MINI, 'mini'), o.hat && officeBeachHat(o.hat))
  );
}
// Sunbather on a striped towel, head to the right, sun hat over the face; towel centre at origin
function officeSunbather(o) {
  var skin = o.lk.skin;
  return h('g', null,
    h('rect', { x: -19, y: -3, width: 38, height: 9, rx: 2, fill: o.towel[0] }),
    h('rect', { x: -19, y: -1, width: 38, height: 1.6, fill: o.towel[1] }),
    h('rect', { x: -19, y: 2.5, width: 38, height: 1.6, fill: o.towel[1] }),
    h('rect', { x: -17, y: -6, width: 16, height: 4.5, rx: 2, fill: skin }),
    h('rect', { x: -4, y: -7, width: 12, height: 6, rx: 3, fill: skin }),
    h('rect', { x: -4, y: -7, width: 5, height: 6, rx: 2, fill: o.color }),
    o.suit !== 'trunks' && h('rect', { x: 3.5, y: -7, width: 3.5, height: 6, rx: 1.5, fill: o.color }),
    h('circle', { cx: 12, cy: -5, r: 4, fill: skin }),
    h('rect', { x: 8.5, y: -10.5, width: 7, height: 4.5, rx: 2, fill: '#E9D8A6' }),
    h('ellipse', { cx: 12, cy: -6.2, rx: 6.5, ry: 2, fill: '#E9D8A6' }),
    h('rect', { x: 8.5, y: -7.4, width: 7, height: 1.2, fill: '#C2703D' }),
    h('circle', { cx: 22.5, cy: -1.5, r: 1.4, fill: '#1F2937' }), h('circle', { cx: 25.8, cy: -1.5, r: 1.4, fill: '#1F2937' }),
    h('rect', { x: -27, y: -3, width: 6, height: 8, rx: 1, fill: '#3B82F6' })
  );
}
// Low beach chair under an upright umbrella with someone in a one-piece and a straw hat, drink in hand
function officeBeachChair(o) {
  var skin = o.lk.skin;
  return h('g', null,
    h('line', { x1: -12, y1: 0, x2: -12, y2: -48, stroke: '#8A94A2', strokeWidth: 1.8 }),
    h('path', { d: 'M -36,-40 Q -12,-60 12,-40 Z', fill: o.umb[0] }),
    h('path', { d: 'M -22,-41.6 Q -12,-56 -2,-41.6 Z', fill: o.umb[1] }),
    h('path', { d: 'M -36,-40 Q -30,-43 -24,-40 M -12,-40 Q -6,-43 0,-40', stroke: o.umb[1], strokeWidth: 1.4, fill: 'none' }),
    h('rect', { x: -12, y: -22, width: 4, height: 22, rx: 1.5, fill: '#CBD5E1', transform: 'rotate(20 -10 -22)' }),
    h('rect', { x: -9, y: -21, width: 12, height: 14, rx: 2, fill: o.chair, transform: 'rotate(20 -10 -22)' }),
    h('rect', { x: -14, y: -9, width: 22, height: 3, rx: 1.5, fill: '#CBD5E1' }),
    h('rect', { x: -12, y: -6, width: 3, height: 6, rx: 1, fill: '#CBD5E1' }), h('rect', { x: 5, y: -6, width: 3, height: 6, rx: 1, fill: '#CBD5E1' }),
    h('rect', { x: -3, y: -11.5, width: 15, height: 4.5, rx: 2, fill: skin }),
    h('rect', { x: 9, y: -11, width: 4, height: 11, rx: 2, fill: skin }),
    h('g', { transform: 'rotate(-20 -2 -9)' },
      h('rect', { x: -8, y: -27, width: 13, height: 19, rx: 5, fill: skin }),
      h('rect', { x: -8, y: -25, width: 13, height: 15, rx: 4.5, fill: o.color }),
      h('g', { transform: 'translate(-1.5,6)' }, officeHeadGroup(o.lk, OFC_HEAD_MINI, 'mini'), officeBeachHat('sun'))),
    h('rect', { x: 8, y: -23, width: 4.5, height: 6, rx: 1, fill: 'rgba(255,255,255,0.75)' }),
    h('rect', { x: 8, y: -20, width: 4.5, height: 3, rx: 1, fill: '#F59E0B' }),
    h('line', { x1: 10.5, y1: -23, x2: 12.5, y2: -27.5, stroke: '#EF4444', strokeWidth: 0.9 })
  );
}
// A kid kneeling at a sandcastle with a bucket and spade; castle base at origin
function officeSandcastle(o) {
  var skin = o.lk.skin;
  return h('g', null,
    h('path', { d: 'M -14,0 L 14,0 L 11,-9 L -11,-9 Z', fill: '#C9A96E' }),
    h('rect', { x: -7, y: -17, width: 14, height: 9, rx: 1, fill: '#C9A96E' }),
    h('rect', { x: -7, y: -19.5, width: 3, height: 3, fill: '#C9A96E' }), h('rect', { x: -1.5, y: -19.5, width: 3, height: 3, fill: '#C9A96E' }), h('rect', { x: 4, y: -19.5, width: 3, height: 3, fill: '#C9A96E' }),
    h('rect', { x: -1.5, y: -13, width: 3, height: 4, rx: 1, fill: '#8F6B4E' }),
    h('line', { x1: 0, y1: -19.5, x2: 0, y2: -28, stroke: '#39445A', strokeWidth: 0.9 }),
    h('path', { d: 'M 0,-28 L 6,-26 L 0,-24 Z', fill: '#EF4444' }),
    h('path', { d: 'M 18,0 L 26,0 L 25,-8 L 19,-8 Z', fill: '#F59E0B' }),
    h('line', { x1: 29, y1: 0, x2: 33, y2: -12, stroke: '#3B82F6', strokeWidth: 1.6 }),
    h('g', { transform: 'translate(-24,0) scale(-1,1)' },
      h('rect', { x: -6, y: -6, width: 13, height: 6, rx: 2.5, fill: skin }),
      h('rect', { x: -5, y: -19, width: 10, height: 13, rx: 4, fill: skin }),
      h('rect', { x: -5, y: -11, width: 10, height: 5, rx: 2, fill: o.color }),
      h('rect', { x: 3, y: -17, width: 3.5, height: 11, rx: 1.7, fill: skin, transform: 'rotate(-40 4 -16)' }),
      h('g', { transform: 'translate(0,14)' }, officeHeadGroup(o.lk, OFC_HEAD_MINI, 'mini'), officeBeachHat('cap', '#EF4444')))
  );
}
// Everything on the sand for the current phase; positions are scene coordinates (gy = street level)
function officeBeachCrowd(phase, L) {
  var gy = L.groundY, seaY = gy - 64, k = [];
  if (phase === 'night') return k;
  if (phase === 'day') {
    k.push(h('g', { key: 'sunbather', transform: 'translate(120,' + (gy - 21) + ') scale(0.75)' },
      officeSunbather({ lk: officeBeachLook(1), suit: 'bikini', color: OFC_SWIM[4], towel: ['#F1EDE2', '#3B82F6'] })));
    k.push(h('g', { key: 'castle', transform: 'translate(98,' + (gy - 6) + ') scale(0.8)' },
      officeSandcastle({ lk: officeBeachLook(2), color: OFC_SWIM[2] })));
    k.push(h('g', { key: 'ball', transform: 'translate(206,' + (gy - 5) + ') scale(0.8)' },
      officeBeachStander({ lk: officeBeachLook(3), suit: 'trunks', color: OFC_SWIM[3], hat: 'cap', hatColor: '#F1EDE2', ball: true })));
    k.push(h('g', { key: 'chair', transform: 'translate(1180,' + (gy - 12) + ') scale(0.8)' },
      officeBeachChair({ lk: officeBeachLook(5), color: OFC_SWIM[0], umb: ['#F1EDE2', '#EF4444'], chair: '#3B82F6' })));
    k.push(h('g', { key: 'cooler', transform: 'translate(1146,' + (gy - 5) + ') scale(0.8)' },
      h('rect', { x: -7, y: -10, width: 14, height: 10, rx: 1.5, fill: '#3B82F6' }),
      h('rect', { x: -7, y: -10, width: 14, height: 3, rx: 1.5, fill: '#F1EDE2' }),
      h('ellipse', { cx: 12, cy: -1, rx: 3.2, ry: 1.6, fill: '#EC4899' }), h('ellipse', { cx: 18, cy: -1.5, rx: 3.2, ry: 1.6, fill: '#EC4899' })));
    // couple strolling along the water's edge, turning around at each end like the floor walkers
    k.push(h('g', { key: 'stroll', transform: 'translate(1090,' + (gy - 24) + ') scale(0.66)' },
      h('g', { className: 'ofc-pace', style: { '--pdx': Math.round(140 / 0.66) + 'px', animationDuration: '30s' } },
        h('g', { className: 'ofc-paceFlip', style: { animationDuration: '30s' } },
          h('g', { transform: 'translate(-8,0)' }, officeBeachStander({ lk: officeBeachLook(7), suit: 'bikini', color: OFC_SWIM[1], hat: 'sun', walking: true, bag: true })),
          h('g', { transform: 'translate(9,0)' }, officeBeachStander({ lk: officeBeachLook(6), suit: 'trunks', color: OFC_SWIM[6], hat: 'visor', walking: true }))))));
    k.push(h('g', { key: 'crab', transform: 'translate(1228,' + (gy - 3) + ') scale(0.8)' },
      h('g', { className: 'ofc-pace', style: { '--pdx': '26px', animationDuration: '9s' } },
        h('ellipse', { cx: 0, cy: -2.6, rx: 4.5, ry: 2.8, fill: '#EF4444' }),
        h('circle', { cx: -5.6, cy: -4.2, r: 1.9, fill: '#EF4444' }), h('circle', { cx: 5.6, cy: -4.2, r: 1.9, fill: '#EF4444' }),
        h('path', { d: 'M -3,-1 L -6.5,1.5 M -1,-0.5 L -3,2 M 1,-0.5 L 3,2 M 3,-1 L 6.5,1.5', stroke: '#B91C1C', strokeWidth: 0.9, strokeLinecap: 'round' }),
        h('circle', { cx: -1.6, cy: -4.8, r: 0.7, fill: '#1F2937' }), h('circle', { cx: 1.6, cy: -4.8, r: 0.7, fill: '#1F2937' }))));
    [[150, seaY - 42, '40s', '0s'], [1130, seaY - 58, '52s', '-18s']].forEach(function(g, i) {
      k.push(h('g', { key: 'gull' + i, transform: 'translate(' + g[0] + ',' + g[1] + ')' },
        h('g', { className: 'ofc-cloud', style: { animationDuration: g[2], animationDelay: g[3] } },
          h('path', { d: 'M -6,0 Q -3,-3.5 0,-0.5 Q 3,-3.5 6,0', stroke: '#F1EDE2', strokeWidth: 1.4, fill: 'none', strokeLinecap: 'round' }))));
    });
    return k;
  }
  // dusk: one sunset watcher on the towel, a couple on a blanket with a lantern and a basket
  k.push(h('g', { key: 'watcher', transform: 'translate(120,' + (gy - 21) + ') scale(0.75)' },
    h('rect', { x: -19, y: -3, width: 38, height: 9, rx: 2, fill: '#F1EDE2' }),
    h('rect', { x: -19, y: -1, width: 38, height: 1.6, fill: '#3B82F6' }),
    h('rect', { x: -19, y: 2.5, width: 38, height: 1.6, fill: '#3B82F6' }),
    h('g', { transform: 'translate(-6,-2)' }, officeBeachSitter({ lk: officeBeachLook(1), color: OFC_SWIM[4] }))));
  k.push(h('g', { key: 'couple', transform: 'translate(1172,' + (gy - 10) + ') scale(0.8)' },
    h('rect', { x: -22, y: -3, width: 44, height: 10, rx: 2, fill: '#9A3412' }),
    h('rect', { x: -22, y: 0, width: 44, height: 1.4, fill: '#F59E0B', opacity: 0.8 }),
    h('rect', { x: -22, y: 4, width: 44, height: 1.4, fill: '#F59E0B', opacity: 0.8 }),
    h('rect', { x: -12, y: -3, width: 1.4, height: 10, fill: '#F59E0B', opacity: 0.6 }), h('rect', { x: 8, y: -3, width: 1.4, height: 10, fill: '#F59E0B', opacity: 0.6 }),
    h('rect', { x: -33, y: -7, width: 9, height: 7, rx: 1.5, fill: '#B08968' }),
    h('path', { d: 'M -31,-7 Q -28.5,-12 -26,-7', stroke: '#8F6B4E', strokeWidth: 1.2, fill: 'none' }),
    h('g', { transform: 'translate(-10,-2)' }, officeBeachSitter({ lk: officeBeachLook(9), color: '#F1EDE2', lean: 16 })),
    h('g', { transform: 'translate(4,-2)' }, officeBeachSitter({ lk: officeBeachLook(8), color: '#8B5CF6' })),
    h('ellipse', { cx: 24, cy: -4, rx: 10, ry: 6.5, fill: 'rgba(255,214,130,0.22)' }),
    h('rect', { x: 22, y: -8, width: 4, height: 7, rx: 1, fill: '#39445A' }),
    h('circle', { className: 'ofc-pulse', cx: 24, cy: -4.6, r: 1.6, fill: '#FFD68A' })));
  return k;
}

function OfficeBuildingArt({ floors, layout, reception }) {
  var L = layout, kids = [];
  var phase = officePhase(), PH = OFFICE_PHASES[phase];
  var BX = L.BX, BW = L.BW, cx = BX + BW / 2;
  var shaftW = 54, shaftX = BX + BW - shaftW - 14;

  kids.push(h('defs', { key: 'defs' },
    h('radialGradient', { id: 'ofcBGlow', cx: '50%', cy: '30%', r: '65%' },
      h('stop', { offset: '0%', stopColor: 'rgba(255,214,130,0.16)' }),
      h('stop', { offset: '100%', stopColor: 'rgba(255,214,130,0)' }))
  ));

  // sky: stars + moon at night, sun by day, drifting clouds always
  if (phase === 'night') {
    [[60, 40, 1.6], [220, 92, 1.3], [305, 30, 1.7], [430, 72, 1.2], [520, 118, 1.4], [645, 38, 1.6], [758, 92, 1.2], [845, 52, 1.7], [152, 132, 1.2], [565, 62, 1.3], [905, 122, 1.4], [40, 112, 1.3]].forEach(function(s, i) {
      kids.push(h('circle', { key: 'st' + i, cx: s[0], cy: s[1], r: s[2], fill: '#8FA4C8', opacity: 0.55 + (i % 3) * 0.15 }));
    });
    kids.push(h('g', { key: 'moon' },
      h('circle', { cx: L.sceneW - 130, cy: 66, r: 21, fill: '#E9E4D0', opacity: 0.92 }),
      h('circle', { cx: L.sceneW - 138, cy: 60, r: 4, fill: '#C9C4B2', opacity: 0.5 }),
      h('circle', { cx: L.sceneW - 124, cy: 72, r: 2.6, fill: '#C9C4B2', opacity: 0.5 })
    ));
  }
  if (phase === 'day') {
    kids.push(h('g', { key: 'sun' },
      h('circle', { cx: L.sceneW - 130, cy: 66, r: 34, fill: 'rgba(253,230,138,0.30)' }),
      h('circle', { cx: L.sceneW - 130, cy: 66, r: 22, fill: '#FDE68A' })
    ));
  }
  kids.push(h('g', { key: 'cl1', className: 'ofc-cloud', opacity: PH.cloudOp },
    h('ellipse', { cx: 130, cy: 64, rx: 46, ry: 12, fill: PH.cloudFill }),
    h('ellipse', { cx: 168, cy: 56, rx: 30, ry: 10, fill: PH.cloudFill })));
  kids.push(h('g', { key: 'cl2', className: 'ofc-cloud', style: { animationDelay: '-20s', animationDuration: '56s' }, opacity: PH.cloudOp * 0.8 },
    h('ellipse', { cx: L.sceneW - 330, cy: 108, rx: 52, ry: 13, fill: PH.cloudFill }),
    h('ellipse', { cx: L.sceneW - 285, cy: 99, rx: 32, ry: 10, fill: PH.cloudFill })));

  // the beach behind the office — sea to the horizon and sand down to the
  // street, running the full width of the scene (the building stands in front
  // of it, and the palm tree below stands on this sand)
  var seaY = L.groundY - 64;
  var seaC = phase === 'day' ? '#5E9DC4' : phase === 'dusk' ? '#46648E' : '#20334C';
  var sandC = phase === 'day' ? '#D9C49A' : phase === 'dusk' ? '#9C8869' : '#4C483E';
  kids.push(h('g', { key: 'beach' },
    h('rect', { x: 0, y: seaY, width: L.sceneW, height: 30, fill: seaC }),
    h('rect', { x: 0, y: seaY, width: L.sceneW, height: 2.5, fill: 'rgba(255,255,255,0.25)' }),
    h('rect', { x: 28, y: seaY + 13, width: 34, height: 2.2, rx: 1.1, fill: 'rgba(255,255,255,0.18)' }),
    h('rect', { x: 104, y: seaY + 21, width: 26, height: 2.2, rx: 1.1, fill: 'rgba(255,255,255,0.14)' }),
    h('rect', { x: BX + BW + 44, y: seaY + 12, width: 30, height: 2.2, rx: 1.1, fill: 'rgba(255,255,255,0.18)' }),
    h('rect', { x: BX + BW + 92, y: seaY + 21, width: 24, height: 2.2, rx: 1.1, fill: 'rgba(255,255,255,0.14)' }),
    h('rect', { x: BX + BW + 128, y: seaY + 15, width: 26, height: 2.2, rx: 1.1, fill: 'rgba(255,255,255,0.16)' }),
    // sailboat out on the water, left of the building (float class must live on
    // an inner g — a CSS transform overrides the SVG transform attribute)
    h('g', { transform: 'translate(84,' + (seaY + 8) + ')' },
      h('g', { className: 'ofc-float' },
        h('path', { d: 'M -11,0 L 11,0 L 6,5 L -7,5 Z', fill: '#39445A' }),
        h('path', { d: 'M 0,-1 L 0,-16 L 9,-2 Z', fill: phase === 'day' ? '#F1EDE2' : '#9BA3B6' }),
        h('line', { x1: 0, y1: 0, x2: 0, y2: -16, stroke: '#39445A', strokeWidth: 1.4 }))),
    // sand
    h('rect', { x: 0, y: seaY + 30, width: L.sceneW, height: L.groundY - seaY - 30, fill: sandC }),
    h('rect', { x: 0, y: seaY + 30, width: L.sceneW, height: 3.5, fill: 'rgba(255,255,255,0.12)' }),
    // distant palms bookending the strip
    officePalmTree(38, L.groundY - 2, 0.7),
    officePalmTree(L.sceneW - 52, L.groundY - 2, 0.75),
    h('rect', { x: 190, y: seaY + 17, width: 28, height: 2.2, rx: 1.1, fill: 'rgba(255,255,255,0.15)' }),
    h('rect', { x: L.sceneW - 150, y: seaY + 19, width: 30, height: 2.2, rx: 1.1, fill: 'rgba(255,255,255,0.15)' }),
    // beach umbrella leaning by the shore, right of the building
    h('g', { transform: 'translate(' + (BX + BW + 144) + ',' + (L.groundY - 12) + ') rotate(-8)' },
      h('line', { x1: 0, y1: 0, x2: 0, y2: -24, stroke: '#8A94A2', strokeWidth: 2.2 }),
      h('path', { d: 'M -16,-22 Q 0,-36 16,-22 Z', fill: '#EF4444' }),
      h('path', { d: 'M -5.5,-27.8 L 5.5,-27.8 L 4,-23.4 L -4,-23.4 Z', fill: '#F1EDE2' }))
  ));
  // beach crowd on the sand (day), sunset watchers (dusk), nobody at night
  kids.push(h('g', { key: 'beachfolk' }, officeBeachCrowd(phase, L)));

  // ground: sidewalk + road
  kids.push(h('g', { key: 'ground' },
    h('rect', { x: 0, y: L.groundY, width: L.sceneW, height: 22, fill: '#1A222E' }),
    h('rect', { x: 0, y: L.groundY, width: L.sceneW, height: 2.5, fill: '#242E3C' }),
    h('rect', { x: 0, y: L.groundY + 22, width: L.sceneW, height: L.totalH - L.groundY - 22, fill: '#0D1119' })
  ));
  for (var dx = 20; dx < L.sceneW; dx += 95) {
    kids.push(h('rect', { key: 'dash' + dx, x: dx, y: L.groundY + 40, width: 34, height: 3, rx: 1.5, fill: '#26303F' }));
  }

  // streetlamp (left) + tree (right)
  kids.push(h('g', { key: 'lamppost' },
    h('rect', { x: BX - 64, y: L.groundY - 92, width: 5, height: 92, rx: 2, fill: '#39445A' }),
    h('rect', { x: BX - 64, y: L.groundY - 92, width: 28, height: 4, rx: 2, fill: '#39445A' }),
    h('rect', { x: BX - 44, y: L.groundY - 94, width: 14, height: 7, rx: 3, fill: '#2E3950' }),
    PH.lampOn && h('ellipse', { cx: BX - 37, cy: L.groundY - 86, rx: 8, ry: 3, fill: '#FFD68A', opacity: 0.6 }),
    PH.lampOn && h('ellipse', { cx: BX - 37, cy: L.groundY - 58, rx: 40, ry: 34, fill: 'url(#ofcBGlow)' })
  ));
  // palm tree by the entrance (Anthony's pick over the old round tree)
  kids.push(h('g', { key: 'tree' }, officePalmTree(BX + BW + 62, L.groundY, 1)));

  // building interiors (row 0 = FOR LEASE, then one row per department)
  for (var r = 0; r < L.rows; r++) {
    var fy = L.topY + r * BLDG_FH;
    var base = fy + BLDG_FH - 8;
    var lease = r === 0;
    kids.push(h('rect', { key: 'int' + r, x: BX, y: fy, width: BW, height: BLDG_FH - 8, fill: lease ? '#1B2331' : '#232C3D' }));
    kids.push(h('rect', { key: 'slab' + r, x: BX, y: fy + BLDG_FH - 8, width: BW, height: 8, fill: '#1A2231' }));
    if (lease) {
      kids.push(h('g', { key: 'boxes' },
        h('rect', { x: BX + 60, y: base - 26, width: 34, height: 26, rx: 2, fill: '#5A4632' }),
        h('rect', { x: BX + 98, y: base - 20, width: 26, height: 20, rx: 2, fill: '#6B5540' }),
        h('rect', { x: BX + 74, y: base - 46, width: 26, height: 20, rx: 2, fill: '#6B5540' }),
        h('rect', { x: BX + 60, y: base - 16, width: 34, height: 3, fill: '#8F6B4E' }),
        h('rect', { x: BX + 74, y: base - 38, width: 26, height: 3, fill: '#8F6B4E' })
      ));
    } else {
      var f = floors[r - 1];
      // mini cubicles: the SAME close-up desk scene as the zoomed-in floor,
      // embedded at half scale and framed by partitions, so the building's
      // floors read as miniatures of the floor view. Visual cap of 3; the
      // floor pill lists everyone.
      // scale so the seated agent matches the mini walkers' height (~44u) —
      // the whole floor shares one human scale
      var CW2 = 118, CUB0 = BX + 14, CUBK = 102 / OFC_W;
      var shown = f.agents.slice(0, 3);
      for (var pp = 0; pp <= shown.length; pp++) {
        kids.push(h('g', { key: 'cpart' + r + '-' + pp },
          h('rect', { x: CUB0 - 6 + pp * CW2, y: base - 52, width: 7, height: 52, rx: 3, fill: '#3A4658' }),
          h('rect', { x: CUB0 - 6 + pp * CW2, y: base - 52, width: 7, height: 3.5, rx: 1.75, fill: '#4C5A70' })));
      }
      shown.forEach(function(a, ai) {
        var st = a.status || 'offline';
        var variant = officeVariant(st);
        kids.push(h('svg', {
          key: 'cub' + a.id, x: CUB0 + ai * CW2, y: base - Math.round(172 * CUBK), width: 102, height: Math.round(OFC_H * CUBK),
          viewBox: '0 0 ' + OFC_W + ' ' + OFC_H, style: { overflow: 'visible' },
        }, officeDeskKids(variant, a.shirt, st === 'error', a.hair, false, officeDecor(a.id), officeLook(a))));
      });
      // mini break corner + kitchen + restroom, mirroring the floor view's right end
      // (plant, sofa, cooler, counter + coffee machine, wall cabinet, fridge, WC door);
      // skipped when a crowded floor leaves no room
      var kx0 = CUB0 + shown.length * CW2 + 12;
      if (shaftX - kx0 >= 262) {
        var kR = shaftX - 40; // 32 px kept free at the right for the restroom door
        var K2 = officeKitchenDecor(f.dept), KF2 = OFC_FRIDGES[K2.fridge], KC2 = OFC_CABS[K2.cab], MS = OFC_SOFAS[officeSofaVariantFromFloors(f.dept, floors)];
        // Filing floor keeps its mini cabinets left of the break corner when there is room
        if (f.dept === 'Filing' && (kR - 222) - kx0 >= 46) kids.push(officeMiniCabinets('mcab' + r, kx0 + 2, base));
        kids.push(h('g', { key: 'amen' + r },
          h('path', { d: 'M ' + (kR - 212) + ',' + (base - 22) + ' Q ' + (kR - 216) + ',' + (base - 34) + ' ' + (kR - 211) + ',' + (base - 42), stroke: '#10B981', strokeWidth: 2.5, fill: 'none', strokeLinecap: 'round' }),
          h('path', { d: 'M ' + (kR - 208) + ',' + (base - 22) + ' Q ' + (kR - 204) + ',' + (base - 32) + ' ' + (kR - 207) + ',' + (base - 40), stroke: '#34D399', strokeWidth: 2.5, fill: 'none', strokeLinecap: 'round' }),
          h('path', { d: 'M ' + (kR - 217) + ',' + (base - 22) + ' L ' + (kR - 202) + ',' + (base - 22) + ' L ' + (kR - 204) + ',' + (base - 8) + ' L ' + (kR - 215) + ',' + (base - 8) + ' Z', fill: '#C2703D' }),
          h('rect', { x: kR - 196, y: base - 23, width: 52, height: 13, rx: 5, fill: MS.back }),
          h('rect', { x: kR - 199, y: base - 13, width: 58, height: 10, rx: 4, fill: MS.seat }),
          h('rect', { x: kR - 201, y: base - 19, width: 7, height: 16, rx: 3, fill: MS.arm }),
          h('rect', { x: kR - 146, y: base - 19, width: 7, height: 16, rx: 3, fill: MS.arm }),
          h('rect', { x: kR - 130, y: base - 26, width: 11, height: 26, rx: 3, fill: OFC_DARK }),
          h('rect', { x: kR - 128.7, y: base - 36, width: 8.5, height: 12, rx: 3.2, fill: 'rgba(125,211,252,0.55)' }),
          h('rect', { x: kR - 105, y: base - 52, width: 50, height: 13, rx: 3, fill: KC2 }),
          h('rect', { x: kR - 112, y: base - 23, width: 70, height: 4.5, rx: 2.2, fill: OFC_WOOD }),
          h('rect', { x: kR - 110, y: base - 18.5, width: 66, height: 18.5, rx: 3, fill: KC2 }),
          h('line', { x1: kR - 77, y1: base - 16, x2: kR - 77, y2: base - 2, stroke: '#2A3346', strokeWidth: 2 }),
          h('rect', { x: kR - 99, y: base - 36, width: 12, height: 13, rx: 2, fill: '#2E3950' }),
          h('rect', { x: kR - 95.5, y: base - 27, width: 4.5, height: 4, rx: 1, fill: '#E5E7EB' }),
          h('circle', { className: 'ofc-pulse', cx: kR - 89, cy: base - 33, r: 1.2, fill: '#F87171' }),
          h('rect', { x: kR - 29, y: base - 58, width: 24, height: 58, rx: 4, fill: KF2[0] }),
          h('rect', { x: kR - 29, y: base - 58, width: 24, height: 20, rx: 4, fill: KF2[1] }),
          h('rect', { x: kR - 26.7, y: base - 45, width: 2.4, height: 7, rx: 1.2, fill: '#8A94A2' }),
          h('rect', { x: kR - 26.7, y: base - 33, width: 2.4, height: 10, rx: 1.2, fill: '#8A94A2' }),
          // restroom door by the elevator shaft (the floor view's WC at building scale)
          officeMiniRestroom(kR + 4, base)
        ));
      } else if (shaftX - kx0 >= 180) {
        // crowded floor (three cubicles, Anthony 2026-09-06): a compact corner still gets the
        // sofa, a short coffee counter, the fridge and the restroom door
        var kRc = shaftX - 40;
        var K3 = officeKitchenDecor(f.dept), KF3 = OFC_FRIDGES[K3.fridge], KC3 = OFC_CABS[K3.cab], MS3 = OFC_SOFAS[officeSofaVariantFromFloors(f.dept, floors)];
        kids.push(h('g', { key: 'amen' + r },
          h('rect', { x: kRc - 130, y: base - 22, width: 46, height: 12, rx: 4.5, fill: MS3.back }),
          h('rect', { x: kRc - 133, y: base - 13, width: 52, height: 10, rx: 4, fill: MS3.seat }),
          h('rect', { x: kRc - 135, y: base - 18, width: 6.5, height: 15, rx: 3, fill: MS3.arm }),
          h('rect', { x: kRc - 85.5, y: base - 18, width: 6.5, height: 15, rx: 3, fill: MS3.arm }),
          h('rect', { x: kRc - 72, y: base - 23, width: 38, height: 4.5, rx: 2.2, fill: OFC_WOOD }),
          h('rect', { x: kRc - 70, y: base - 18.5, width: 34, height: 18.5, rx: 3, fill: KC3 }),
          h('rect', { x: kRc - 64, y: base - 36, width: 12, height: 13, rx: 2, fill: '#2E3950' }),
          h('rect', { x: kRc - 60.5, y: base - 27, width: 4.5, height: 4, rx: 1, fill: '#E5E7EB' }),
          h('circle', { className: 'ofc-pulse', cx: kRc - 54, cy: base - 33, r: 1.2, fill: '#F87171' }),
          h('rect', { x: kRc - 29, y: base - 58, width: 24, height: 58, rx: 4, fill: KF3[0] }),
          h('rect', { x: kRc - 29, y: base - 58, width: 24, height: 20, rx: 4, fill: KF3[1] }),
          h('rect', { x: kRc - 26.7, y: base - 45, width: 2.4, height: 7, rx: 1.2, fill: '#8A94A2' }),
          h('rect', { x: kRc - 26.7, y: base - 33, width: 2.4, height: 10, rx: 1.2, fill: '#8A94A2' }),
          officeMiniRestroom(kRc + 4, base)
        ));
      }
      // one ambient staffer, plus agents on break (they stand around as often
      // as they walk)
      kids.push(officePacer('amb' + r, BX + 44, base, shaftX - BX - 150, 19, { shirt: '#5A6A85', walking: true }, r * 2 + 1));
      f.agents.forEach(function(a, ai) {
        if ((a.status || '') !== 'idle') return;
        kids.push(officePacer('brk' + a.id, BX + 90 + ai * 40, base, shaftX - BX - 190, 13, { shirt: a.shirt || '#14B8A6', walking: true, mug: true, hair: a.hair, look: officeLook(a) }, ai * 4 + r + 3));
      });
      // the dept pet, same one the floor view shows, pacing its mini floor
      var bpet = officePetFor(f.dept);
      kids.push(h('g', { key: 'bpet' + r, transform: 'translate(' + (BX + 66) + ',' + base + ') scale(0.62)' },
        h('g', { className: 'ofc-pace', style: { '--pdx': ((shaftX - BX - 160) / 0.62) + 'px', animationDelay: (-(r * 5)) + 's' } },
          h('g', { className: 'ofc-paceFlip', style: { animationDelay: (-(r * 5)) + 's' } },
            bpet.species === 'cat' ? officeCatFig(bpet.coat, true) : officeDogFig(bpet.breed, true)))));
    }
  }

  // lobby
  var lbase = L.groundY - 8;
  kids.push(h('g', { key: 'lobby' },
    h('rect', { x: BX, y: L.lobbyY, width: BW, height: BLDG_LOBBY_H - 8, fill: '#26303F' }),
    h('rect', { x: BX, y: L.groundY - 8, width: BW, height: 8, fill: '#1A2231' }),
    // glass entry doors
    h('rect', { x: cx - 34, y: lbase - 64, width: 30, height: 64, rx: 3, fill: 'rgba(125,211,252,0.14)', stroke: '#4C5A70', strokeWidth: 2.5 }),
    h('rect', { x: cx + 4, y: lbase - 64, width: 30, height: 64, rx: 3, fill: 'rgba(125,211,252,0.14)', stroke: '#4C5A70', strokeWidth: 2.5 }),
    h('rect', { x: cx - 38, y: lbase - 72, width: 76, height: 8, rx: 3, fill: '#39445A' }),
    h('circle', { cx: cx - 10, cy: lbase - 34, r: 1.8, fill: '#8A94A2' }),
    h('circle', { cx: cx + 10, cy: lbase - 34, r: 1.8, fill: '#8A94A2' }),
    // wall art + plant
    h('rect', { x: BX + 190, y: L.lobbyY + 24, width: 34, height: 22, rx: 3, fill: '#2E3950', stroke: '#4C5A70', strokeWidth: 1.5 }),
    h('path', { d: 'M ' + (BX + 196) + ',' + (L.lobbyY + 40) + ' L ' + (BX + 204) + ',' + (L.lobbyY + 30) + ' L ' + (BX + 210) + ',' + (L.lobbyY + 37) + ' L ' + (BX + 216) + ',' + (L.lobbyY + 29) + ' L ' + (BX + 219) + ',' + (L.lobbyY + 40) + ' Z', fill: '#38BDF8', opacity: 0.7 })
  ));
  // receptionist SEATED behind the desk: sunk low so only head + shoulders
  // clear the counter (person first, desk front covers the rest). The lobby IS
  // the Reception dept now: the figure takes the reception agent's look, and
  // while that agent is onboarding the desk waits with moving boxes instead.
  if (reception && reception.status === 'onboarding') {
    kids.push(h('g', { key: 'recep' },
      h('rect', { x: BX + 152, y: lbase - 22, width: 24, height: 22, rx: 2, fill: '#6B5540' }),
      h('rect', { x: BX + 152, y: lbase - 15, width: 24, height: 3, fill: '#8F6B4E' }),
      h('rect', { x: BX + 157, y: lbase - 38, width: 17, height: 16, rx: 2, fill: '#5A4632' }),
      h('rect', { x: BX + 157, y: lbase - 33, width: 17, height: 2.5, fill: '#8F6B4E' })));
  } else {
    kids.push(h('g', { key: 'recep', transform: 'translate(' + (BX + 92) + ',' + (lbase - 6) + ')' },
      officeMiniPerson({ shirt: (reception && reception.shirt) || '#7C6FA8', hair: (reception && reception.hair) || 'long' })));
  }
  kids.push(h('g', { key: 'recepdesk' },
    h('rect', { x: BX + 48, y: lbase - 30, width: 92, height: 30, rx: 4, fill: OFC_DARK }),
    h('rect', { x: BX + 44, y: lbase - 34, width: 100, height: 6, rx: 3, fill: '#4C5A70' }),
    h('rect', { x: BX + 118, y: lbase - 42, width: 12, height: 9, rx: 1.5, fill: '#222B3A' }),
    h('rect', { x: BX + 119.5, y: lbase - 40.5, width: 9, height: 6, rx: 1, fill: '#0D1626' })
  ));
  // ambient lobby walkers (stand around as often as they walk)
  kids.push(officePacer('lw1', BX + 180, lbase, cx - BX - 230, 17, { shirt: '#5A6A85', walking: true }, 5));
  kids.push(officePacer('lw2', cx + 50, lbase, shaftX - cx - 110, 23, { shirt: '#856F5A', walking: true, mug: true }, 9));

  // elevator shaft + car
  var travel = BLDG_LOBBY_H + (L.rows - 1) * BLDG_FH;
  kids.push(h('g', { key: 'shaft' },
    h('rect', { x: shaftX, y: L.topY, width: shaftW, height: L.groundY - L.topY - 8, fill: '#151C29' }),
    h('line', { x1: shaftX + 7, y1: L.topY, x2: shaftX + 7, y2: L.groundY - 8, stroke: '#2A3448', strokeWidth: 2 }),
    h('line', { x1: shaftX + shaftW - 7, y1: L.topY, x2: shaftX + shaftW - 7, y2: L.groundY - 8, stroke: '#2A3448', strokeWidth: 2 }),
    h('g', { transform: 'translate(' + (shaftX + 7) + ',' + (L.groundY - 8 - 46) + ')' },
      h('g', { className: 'ofc-elev', style: { '--ey': (-travel) + 'px' } },
        h('rect', { x: 0, y: 0, width: shaftW - 14, height: 46, rx: 4, fill: '#4C5A70' }),
        h('line', { x1: (shaftW - 14) / 2, y1: 4, x2: (shaftW - 14) / 2, y2: 42, stroke: '#39445A', strokeWidth: 2.5 }),
        h('circle', { className: 'ofc-pulse', cx: (shaftW - 14) / 2, cy: -4, r: 2, fill: '#34D399' })
      ))
  ));

  // building shell: side walls + parapet
  kids.push(h('g', { key: 'shell' },
    h('rect', { x: BX - 12, y: L.topY - 6, width: 12, height: L.groundY - L.topY + 6, fill: '#2C3648' }),
    h('rect', { x: BX + BW, y: L.topY - 6, width: 12, height: L.groundY - L.topY + 6, fill: '#2C3648' }),
    h('rect', { x: BX - 18, y: L.topY - 12, width: BW + 36, height: 12, rx: 3, fill: '#2C3648' })
  ));

  // rooftop: water tank, AC, antenna, cat (sign removed 2026-08-30 by request)
  kids.push(h('g', { key: 'roof' },
    h('rect', { x: BX + 64, y: L.topY - 24, width: 5, height: 12, fill: '#2E3950' }),
    h('rect', { x: BX + 98, y: L.topY - 24, width: 5, height: 12, fill: '#2E3950' }),
    h('rect', { x: BX + 58, y: L.topY - 58, width: 50, height: 36, rx: 5, fill: '#39445A' }),
    h('ellipse', { cx: BX + 83, cy: L.topY - 58, rx: 25, ry: 5, fill: '#46536B' }),
    h('rect', { x: BX + 140, y: L.topY - 34, width: 46, height: 22, rx: 3, fill: '#2E3950' }),
    h('circle', { cx: BX + 163, cy: L.topY - 23, r: 7, fill: 'none', stroke: '#4C5A70', strokeWidth: 2 }),
    h('line', { x1: BX + BW - 60, y1: L.topY - 12, x2: BX + BW - 60, y2: L.topY - 86, stroke: '#4A5568', strokeWidth: 3 }),
    h('line', { x1: BX + BW - 72, y1: L.topY - 68, x2: BX + BW - 48, y2: L.topY - 68, stroke: '#4A5568', strokeWidth: 2 }),
    h('circle', { className: 'ofc-pulse', cx: BX + BW - 60, cy: L.topY - 90, r: 3, fill: '#EF4444' }),
    // the rooftop cat, perched between the sign and the antenna
    h('g', { key: 'cat', fill: '#0E131C', transform: 'translate(' + (BX + 500) + ',' + (L.topY - 12) + ')' },
      h('path', { d: 'M -8,0 Q -14,-3 -13,-10', stroke: '#0E131C', strokeWidth: 2.4, fill: 'none', strokeLinecap: 'round' }),
      h('ellipse', { cx: 0, cy: -5, rx: 8.5, ry: 5 }),
      h('circle', { cx: 7, cy: -11, r: 4.2 }),
      h('path', { d: 'M 4.2,-13.6 L 5.2,-17.6 L 7.2,-14.4 Z' }),
      h('path', { d: 'M 8,-14.6 L 10,-17.8 L 10.8,-13.8 Z' })
    )
  ));

  // passing car (foreground) — sometimes it's somebody's red sports car
  var sporty = officeAmbientBucket(41) % 3 === 0;
  var sportPaint = '#D63B3B';
  kids.push(h('g', { key: 'car', transform: 'translate(-180,' + (L.groundY + 28) + ')' },
    h('g', { className: 'ofc-car', style: { '--vdx': (L.sceneW + 360) + 'px', animationDuration: sporty ? '17s' : undefined } },
      sporty ? h('g', null,
        // ferrari-ish: kamm tail with a ducktail lip, cabin set back, long low
        // nose, side scoop ahead of the rear wheel
        h('rect', { x: -1, y: 0.5, width: 5, height: 2.5, rx: 1, fill: '#8E1F1F' }),
        h('path', { d: 'M 0,10 L 0,4 Q 0,1 6,0 L 15,-1.5 Q 19,-6.5 27,-7 L 35,-7 Q 41,-6.5 47,-4 L 61,-1 Q 70,0.5 70,4.5 L 70,10 Q 70,13 64,13 L 6,13 Q 0,13 0,10 Z', fill: sportPaint }),
        h('path', { d: 'M 19,-5.5 L 34,-5.8 L 43,-3 L 22,-2.8 Z', fill: 'rgba(17,24,39,0.75)' }),
        h('path', { d: 'M 25,4.5 L 33,4.5 L 29.5,8.5 L 25,8.5 Z', fill: '#8E1F1F' }),
        h('rect', { x: 4, y: 7, width: 62, height: 1.8, rx: 0.9, fill: 'rgba(0,0,0,0.25)' }),
        h('circle', { cx: 15, cy: 13, r: 6.5, fill: '#0B0F16' }),
        h('circle', { cx: 56, cy: 13, r: 6.5, fill: '#0B0F16' }),
        h('circle', { cx: 15, cy: 13, r: 2.2, fill: '#B7BDC9' }),
        h('circle', { cx: 56, cy: 13, r: 2.2, fill: '#B7BDC9' }),
        h('circle', { cx: 61.5, cy: 3.2, r: 1.3, fill: '#F6C445' }),
        h('rect', { x: 65.5, y: 1.2, width: 4.5, height: 1.8, rx: 0.9, fill: '#FFD68A' }),
        PH.lampOn && h('path', { d: 'M 71,0 L 106,-3 L 106,8 L 71,6 Z', fill: 'rgba(255,214,130,0.10)' })
      ) : h('g', null,
        h('rect', { x: 12, y: -9, width: 30, height: 12, rx: 5, fill: '#56688C' }),
        h('rect', { x: 15, y: -7, width: 11, height: 8, rx: 2, fill: 'rgba(125,211,252,0.35)' }),
        h('rect', { x: 28, y: -7, width: 11, height: 8, rx: 2, fill: 'rgba(125,211,252,0.35)' }),
        h('rect', { x: 0, y: 0, width: 64, height: 15, rx: 7, fill: '#4A5A78' }),
        h('circle', { cx: 14, cy: 15, r: 6, fill: '#0B0F16' }),
        h('circle', { cx: 50, cy: 15, r: 6, fill: '#0B0F16' }),
        h('circle', { cx: 14, cy: 15, r: 2, fill: '#39445A' }),
        h('circle', { cx: 50, cy: 15, r: 2, fill: '#39445A' }),
        h('rect', { x: 61, y: 3, width: 4, height: 4, rx: 1, fill: '#FFD68A' }),
        PH.lampOn && h('path', { d: 'M 65,2 L 100,-1 L 100,10 L 65,8 Z', fill: 'rgba(255,214,130,0.10)' })
      )
    )
  ));

  // the lobby cat, sat by the wall art (same orange cat as the reception floor)
  kids.push(h('g', { key: 'lobbycat', transform: 'translate(' + (BX + 246) + ',' + lbase + ') scale(0.6)' },
    officeCatSit('#D08048')));

  // …and every once in a while a crocodile crosses the street (Anthony's ask)
  if (officeAmbientBucket(29) % 5 === 0) {
    kids.push(h('g', { key: 'croc', transform: 'translate(-70,' + (L.groundY + 34) + ')' },
      h('g', { className: 'ofc-croc', style: { '--cdx': (L.sceneW + 140) + 'px' } },
        h('ellipse', { cx: 2, cy: 8, rx: 34, ry: 3, fill: 'rgba(0,0,0,0.3)' }),
        h('path', { d: 'M -28,-3 Q -44,-2 -54,2 Q -43,5 -28,4 Z', fill: '#3E7A52' }),
        // four stubby waddling legs — back pair behind the body, front pair over it
        h('g', { className: 'ofc-petLegA' }, h('rect', { x: -25, y: -1, width: 5.5, height: 8.5, rx: 2.6, fill: '#4E9464' })),
        h('g', { className: 'ofc-petLegB' }, h('rect', { x: 9, y: -1, width: 5.5, height: 8.5, rx: 2.6, fill: '#4E9464' })),
        h('rect', { x: -30, y: -8, width: 52, height: 11, rx: 5.5, fill: '#3E7A52' }),
        h('g', { className: 'ofc-petLegB' }, h('rect', { x: -15, y: 0, width: 5.5, height: 8.5, rx: 2.6, fill: '#356B47' })),
        h('g', { className: 'ofc-petLegA' }, h('rect', { x: 17, y: 0, width: 5.5, height: 8.5, rx: 2.6, fill: '#356B47' })),
        h('path', { d: 'M -24,-8 L -20,-12 L -16,-8 Z', fill: '#356B47' }),
        h('path', { d: 'M -13,-8 L -9,-12 L -5,-8 Z', fill: '#356B47' }),
        h('path', { d: 'M -2,-8 L 2,-12 L 6,-8 Z', fill: '#356B47' }),
        h('rect', { x: 20, y: -6.5, width: 22, height: 8, rx: 4, fill: '#3E7A52' }),
        h('rect', { x: 27, y: -2.5, width: 15, height: 4, rx: 2, fill: '#2E5A3D' }),
        h('circle', { cx: 23, cy: -7.5, r: 2.6, fill: '#3E7A52' }),
        h('circle', { cx: 23.6, cy: -7.8, r: 1, fill: '#1F2937' }),
        h('circle', { cx: 39.5, cy: -4.5, r: 0.9, fill: '#1F2937' })
      )
    ));
  }

  return h('svg', {
    width: L.sceneW, height: L.totalH, viewBox: '0 0 ' + L.sceneW + ' ' + L.totalH,
    style: { display: 'block' },
  }, kids);
}

// Building panel: scene + DOM overlays (clickable floor zones + floor pills).
function OfficeBuilding({ agents, data, loading, onEnterFloor }) {
  // the Reception dept lives in the lobby, not on a cutaway floor
  var reception = agents.find(function(a) { return (a.dept || '') === 'Reception'; });
  var floors = officeBuildingFloors(agents.filter(function(a) { return (a.dept || '') !== 'Reception'; }));
  var L = officeBuildingLayout(Math.max(1, floors.length));
  var overlays = [];
  floors.forEach(function(f, fi) {
    var y = L.topY + (fi + 1) * BLDG_FH;
    overlays.push(h(Fragment, { key: 'fo' + f.dept },
      h('div', {
        className: 'officedesk-zone', title: 'Walk into ' + f.dept,
        onClick: function() { onEnterFloor(f); },
        style: { position: 'absolute', left: L.BX - 4, top: y - 2, width: L.BW + 8, height: BLDG_FH - 4, zIndex: 7 },
      }),
      h('div', { style: { position: 'absolute', left: L.BX + 14, top: y + 8, zIndex: 8, pointerEvents: 'none' } },
        h('div', { style: { display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(10,14,22,0.62)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 999, padding: '4px 12px', backdropFilter: 'blur(6px)' } },
          h('span', { style: { fontSize: 11.5, fontWeight: 700, color: '#F1F5F9' } }, f.dept),
          f.agents.map(function(a) {
            var meta = OFFICE_STATUS_META[a.status] || OFFICE_STATUS_META.offline;
            return h('span', { key: a.id, style: { display: 'inline-flex', alignItems: 'center', gap: 4 } },
              h('span', { style: { width: 7, height: 7, borderRadius: '50%', background: meta.color, boxShadow: '0 0 6px ' + meta.color } }),
              h('span', { style: { fontSize: 10.5, fontWeight: 600, color: '#9CA3AF' } }, a.name));
          })
        ))
    ));
  });
  if (reception) {
    var rMeta = OFFICE_STATUS_META[reception.status] || OFFICE_STATUS_META.offline;
    overlays.push(h(Fragment, { key: 'recep' },
      h('div', {
        className: 'officedesk-zone', title: 'Walk into Reception',
        onClick: function() { onEnterFloor({ dept: 'Reception' }); },
        style: { position: 'absolute', left: L.BX - 4, top: L.lobbyY - 2, width: L.BW + 8, height: BLDG_LOBBY_H - 4, zIndex: 7 },
      }),
      h('div', { style: { position: 'absolute', left: L.BX + 14, top: L.lobbyY + 8, zIndex: 8, pointerEvents: 'none' } },
        h('div', { style: { display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(10,14,22,0.62)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 999, padding: '4px 12px', backdropFilter: 'blur(6px)' } },
          h('span', { style: { fontSize: 11.5, fontWeight: 700, color: '#F1F5F9' } }, 'Reception'),
          h('span', { style: { display: 'inline-flex', alignItems: 'center', gap: 4 } },
            h('span', { style: { width: 7, height: 7, borderRadius: '50%', background: rMeta.color, boxShadow: '0 0 6px ' + rMeta.color } }),
            h('span', { style: { fontSize: 10.5, fontWeight: 600, color: '#9CA3AF' } }, reception.name))
        ))
    ));
  }
  overlays.push(h('div', { key: 'lease', className: 'officesign', style: { left: L.BX + L.BW / 2 - 44, top: L.topY + 40, background: '#475569', color: '#CBD5E1', letterSpacing: 1.5 } }, 'FOR LEASE'));
  var PH = OFFICE_PHASES[officePhase()];
  return h('div', { className: 'officezoom', style: { position: 'relative', borderRadius: 12, border: '1px solid ' + C.border, overflow: 'hidden', background: PH.backdrop } },
    h('div', { style: { position: 'absolute', top: 13, left: 20, fontSize: 11, fontWeight: 700, letterSpacing: 2.5, color: PH.label, zIndex: 9 } }, 'ATHARVA BRANDS · HEADQUARTERS'),
    h('div', { style: { position: 'absolute', top: 12, right: 20, fontSize: 11, fontWeight: 600, color: PH.label, zIndex: 9 } },
      data ? 'updated ' + officeAgo(data.generated_at) : 'loading...'),
    loading && !data
      ? h('div', { style: { color: '#9CA3AF', fontSize: 13, fontWeight: 600, padding: '110px 40px' } }, 'Unlocking the building...')
      // The scene is wider than the panel on purpose (beach + road run past the
      // building). It used to scroll sideways; now it sits centered and the
      // scenery simply crops at the panel edges (Anthony 2026-09-04).
      : h('div', { style: { overflow: 'hidden', display: 'flex', justifyContent: 'center' } },
          h('div', { style: { position: 'relative', width: L.sceneW, height: L.totalH, flexShrink: 0 } },
            h(OfficeBuildingArt, { floors: floors, layout: L, reception: reception }),
            overlays
          )
        )
  );
}

function OfficeStat({ label, value, color }) {
  return h('div', { style: { minWidth: 92 } },
    h('div', { style: { fontSize: 11, color: C.textMuted, marginBottom: 2 } }, label),
    h('div', { style: { fontSize: 17, fontWeight: 700, color: color || C.text } }, value)
  );
}

function officeModeMeta(mode) {
  return mode === 'pilot' ? { label: 'PILOT', bg: 'rgba(245,158,11,0.12)', text: '#F59E0B' }
    : mode === 'off' ? { label: 'OFF', bg: 'rgba(148,163,184,0.12)', text: '#94A3B8' }
    : mode === 'planned' ? { label: 'PLANNED', bg: 'rgba(167,139,250,0.12)', text: '#A78BFA' }
    : { label: (mode || 'LIVE').toUpperCase(), bg: 'rgba(16,185,129,0.12)', text: '#10B981' };
}

// History rows expand when the server ships a `decision` payload — the durable
// record of how the agent reached each response (reasoning, investigation
// steps, the drafted/sent reply) now that the Reamaze notes are going away.
function OfficeHistoryRow({ r, first, onChanged, onActed, onGone, autoOpen, folders: sharedFolders, foldersLoading: sharedLoading, onReviewClick }) {
  var [open, setOpen] = useState(false);
  var rowRef = useRef(null);
  var [actBusy, setActBusy] = useState(null);
  var [actDone, setActDone] = useState(null);
  var [actErr, setActErr] = useState(null);
  // Actions that carry a `form` (Danny's "File to folder…" / "Ignore", Anthony 9-09) open an
  // inline form first and POST its values. A `folder` field gets a datalist of the real Dropbox
  // vendor folders, fetched once per row that asks for it.
  var [formFor, setFormFor] = useState(null);
  var [formVals, setFormVals] = useState({});
  // the desk hands down the folder list (fetched once); a row fetches on its own only otherwise
  var [ownFolders, setOwnFolders] = useState(null);
  var [ownLoading, setOwnLoading] = useState(false);
  var folders = sharedFolders !== undefined ? sharedFolders : ownFolders;
  var foldersLoading = sharedFolders !== undefined ? sharedLoading : ownLoading;
  var runAction = function(a, body) {
    if (actBusy || actDone) return;
    setActBusy(a.key); setActErr(null);
    api(a.url, { method: a.method || 'POST', body: JSON.stringify(body || a.body || {}) })
      .then(function() {
        setFormFor(null);
        // File it / Ignore leave the list at once (Anthony 9-10): the desk hides the row,
        // toasts, and opens the next one while Danny works the queue in the background
        if (onGone && a.form) { onGone(r.slug, a); }
        else { setActDone(a.done || 'Done.'); if (onActed) onActed(r.slug); }
        if (onChanged) setTimeout(onChanged, 600);
      })
      .catch(function(e) { setActErr(e.message || 'Failed'); })
      .finally(function() { setActBusy(null); });
  };
  var openForm = function(a) {
    var vals = {};
    (a.form.fields || []).forEach(function(f) { vals[f.name] = f.value == null ? '' : String(f.value); });
    setFormVals(vals); setActErr(null); setFormFor(a.key);
    if (sharedFolders === undefined && ownFolders === null && (a.form.fields || []).some(function(f) { return f.type === 'folder'; })) {
      setOwnLoading(true);
      api('/agent-office/agents/invoice-filing/folders')
        .then(function(d) { setOwnFolders((d && d.folders) || []); })
        .catch(function() { setOwnFolders([]); })
        .finally(function() { setOwnLoading(false); });
    }
  };
  // Review queue auto-advance (Anthony 9-10): the row the desk points at opens with its
  // primary form ready and scrolls into view.
  useEffect(function() {
    var acts = (r.decision && r.decision.actions) || [];
    if (!autoOpen || !acts.length || actDone) return;
    var a = acts.find(function(x) { return x.form && x.style === 'primary'; }) || acts.find(function(x) { return x.form; });
    setOpen(true);
    if (a) openForm(a);
    if (rowRef.current && rowRef.current.scrollIntoView) { try { rowRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch (e) {} }
  }, [autoOpen]);
  var submitForm = function(a) {
    var missing = (a.form.fields || []).filter(function(f) { return f.required && !String(formVals[f.name] || '').trim(); });
    if (missing.length) { setActErr(missing[0].label + ' is required'); return; }
    // With the folder list loaded, the value must be one of the real folders (the dropdown
    // guarantees it; this guards a stale suggestion that is not in the list).
    var badFolder = (a.form.fields || []).find(function(f) { return f.type === 'folder' && folders && folders.length > 0 && folders.indexOf(String(formVals[f.name] || '')) < 0; });
    if (badFolder) { setActErr('Choose a vendor folder from the list'); return; }
    runAction(a, formVals);
  };
  var sm = OFFICE_STATE_META[r.agent_state] || { label: r.agent_state, bg: 'rgba(148,163,184,0.12)', text: '#94A3B8' };
  var d = r.decision;
  var secTitle = { fontSize: 10.5, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 };
  // the yellow Review pill (and a red Error one) doubles as the filter switch
  var reviewable = !!onReviewClick && (r.agent_state === 'exception' || r.agent_state === 'error');
  return h('div', { ref: rowRef, style: { borderTop: first ? 'none' : '1px solid ' + C.border } },
    h('div', {
      onClick: d ? function() { setOpen(!open); } : undefined,
      style: { display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', fontSize: 12.5, cursor: d ? 'pointer' : 'default' },
    },
      h('span', { style: { color: C.textDim, width: 74, flexShrink: 0 } }, officeAgo(r.updated_at)),
      h('span', { title: reviewable ? 'Show only the items that still need review' : undefined, onClick: reviewable ? function(e) { e.stopPropagation(); onReviewClick(); } : undefined,
        style: { padding: '1px 8px', borderRadius: 9, fontSize: 10.5, fontWeight: 700, background: sm.bg, color: sm.text, flexShrink: 0, width: 74, textAlign: 'center', cursor: reviewable ? 'pointer' : undefined } }, sm.label),
      h('span', { style: { color: C.text, fontWeight: 600, flexShrink: 0, width: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, r.customer_name || '—'),
      h('span', { style: { color: C.textMuted, flexShrink: 0, width: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, (r.category || '').replace(/_/g, ' ')),
      r.url
        ? h('a', { href: r.url, target: '_blank', rel: 'noopener', onClick: function(e) { e.stopPropagation(); }, style: { color: C.accent, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, r.subject || r.slug)
        : h('span', { style: { color: C.textDim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, r.subject || r.slug),
      r.error && h('span', { style: { color: '#EF4444', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260 } }, r.error),
      d && h('span', { style: { marginLeft: 'auto', color: C.textDim, fontSize: 10, flexShrink: 0, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' } }, '▶')
    ),
    open && d && h('div', { style: { margin: '2px 0 12px 84px', padding: '12px 16px', background: C.bg, border: '1px solid ' + C.border, borderRadius: 10, fontSize: 12.5 } },
      d.summary && h('div', { style: { marginBottom: 12 } },
        h('div', { style: secTitle }, 'Decision'),
        h('div', { style: { color: C.text, lineHeight: 1.55 } }, d.summary)),
      d.investigation && d.investigation.length > 0 && h('div', { style: { marginBottom: 12 } },
        h('div', { style: secTitle }, 'How it got there'),
        d.investigation.map(function(step, si) {
          if (step.kind === 'tool') {
            return h('div', { key: si, style: { padding: '3px 0 3px 10px', borderLeft: '2px solid ' + C.border, marginBottom: 6 } },
              h('div', null,
                h('span', { style: { color: C.accent, fontWeight: 600 } }, step.tool),
                step.input && h('span', { style: { color: C.textDim, fontSize: 11.5 } }, '  ' + step.input)),
              step.result && h('div', { style: { color: C.textDim, fontSize: 11.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, step.result));
          }
          return h('div', { key: si, style: { color: C.textMuted, lineHeight: 1.55, marginBottom: 8 } }, step.text);
        })),
      d.draft && h('div', { style: { marginBottom: 10 } },
        h('div', { style: secTitle }, r.agent_state === 'auto_sent' ? 'Reply sent' : 'Reply draft'),
        h('div', { style: { whiteSpace: 'pre-wrap', color: C.text, lineHeight: 1.55, background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: '10px 12px' } }, d.draft)),
      (d.panel_actions || []).length > 0 && h('div', { style: { color: C.textMuted, marginBottom: 4 } }, 'Panel actions: ' + d.panel_actions.join('; ')),
      (d.actions || []).length > 0 && h('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, marginBottom: 4, flexWrap: 'wrap' } },
        actDone
          ? h('span', { style: { color: '#10B981', fontWeight: 600 } }, actDone)
          : d.actions.map(function(a) {
              var primary = a.style === 'primary';
              var active = formFor === a.key;
              return h('button', { key: a.key, className: 'phov', disabled: !!actBusy, onClick: function(e) { e.stopPropagation(); if (a.form) { if (active) { setFormFor(null); } else { openForm(a); } } else { runAction(a); } },
                style: { background: primary ? C.accent : 'transparent', color: primary ? '#fff' : C.text, border: '1px solid ' + (primary ? C.accent : (active ? C.accent : C.border)), borderRadius: 7, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: actBusy ? 'default' : 'pointer', fontFamily: 'inherit', opacity: actBusy && actBusy !== a.key ? 0.6 : 1 } },
                actBusy === a.key ? 'Working…' : a.label);
            }),
        !formFor && actErr && h('span', { style: { color: '#EF4444', fontSize: 12 } }, actErr)),
      (function() {
        var fa = formFor && !actDone ? (d.actions || []).find(function(a) { return a.key === formFor && a.form; }) : null;
        if (!fa) return null;
        var inp = { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '6px 10px', borderRadius: 6, fontSize: 12.5, fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box', marginTop: 3 };
        // Vendor folder = a dropdown of the real Dropbox folders, A to Z (Anthony 9-10); Danny's
        // suggestion is pre-selected when it exists. The text box only returns when the list
        // could not be loaded, so filing is never blocked.
        var sortedFolders = (folders || []).slice().sort(function(a, b) { return String(a).localeCompare(String(b), undefined, { sensitivity: 'base', numeric: true }); });
        return h('div', { onClick: function(e) { e.stopPropagation(); }, style: { marginTop: 8, padding: '10px 12px', background: C.surface, border: '1px solid ' + C.border, borderRadius: 8 } },
          h('div', { style: { fontSize: 12.5, fontWeight: 700, color: C.text, marginBottom: 8 } }, fa.form.title || fa.label),
          h('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 } },
            (fa.form.fields || []).map(function(f) {
              var isFolder = f.type === 'folder';
              var setVal = function(v) { setFormVals(function(p) { var n = Object.assign({}, p); n[f.name] = v; return n; }); };
              var useSelect = isFolder && (foldersLoading || sortedFolders.length > 0);
              var cur = formVals[f.name] || '';
              return h('label', { key: f.name, style: { display: 'block', fontSize: 11.5, color: C.textMuted, gridColumn: isFolder ? '1 / -1' : 'auto' } },
                f.label + (f.required ? ' *' : ''),
                useSelect
                  ? h('select', { value: sortedFolders.indexOf(cur) >= 0 ? cur : '', disabled: foldersLoading, onChange: function(e) { setVal(e.target.value); },
                      style: Object.assign({}, inp, { cursor: foldersLoading ? 'default' : 'pointer', appearance: 'auto' }) },
                      h('option', { value: '' }, foldersLoading ? 'Loading the vendor folders…' : 'Choose a vendor folder…'),
                      sortedFolders.map(function(name) { return h('option', { key: name, value: name }, name); }))
                  : h('input', { type: f.type === 'date' ? 'date' : 'text', value: cur, placeholder: f.placeholder || '', autoComplete: 'off',
                      onChange: function(e) { setVal(e.target.value); },
                      onKeyDown: function(e) { if (e.key === 'Enter') { e.preventDefault(); submitForm(fa); } },
                      style: inp }),
                isFolder && !foldersLoading && sortedFolders.length > 0 && h('div', { style: { fontSize: 11, color: C.textDim, marginTop: 2 } }, sortedFolders.length + ' vendor folders, A to Z'),
                isFolder && !foldersLoading && folders && folders.length === 0 && h('div', { style: { fontSize: 11, color: C.textDim, marginTop: 2 } }, 'The folder list could not be loaded. Type the folder name exactly as it appears in Dropbox.'));
            })),
          fa.form.note && h('div', { style: { fontSize: 11.5, color: C.textDim, marginTop: 8, lineHeight: 1.5 } }, fa.form.note),
          h('div', { style: { display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' } },
            h('button', { className: 'phov', disabled: !!actBusy, onClick: function() { submitForm(fa); },
              style: { background: C.accent, color: '#fff', border: '1px solid ' + C.accent, borderRadius: 7, padding: '5px 14px', fontSize: 12, fontWeight: 600, cursor: actBusy ? 'default' : 'pointer', fontFamily: 'inherit' } },
              actBusy === fa.key ? 'Working…' : (fa.form.submit || fa.label)),
            h('button', { className: 'phov', disabled: !!actBusy, onClick: function() { setFormFor(null); setActErr(null); },
              style: { background: 'transparent', color: C.text, border: '1px solid ' + C.border, borderRadius: 7, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' } }, 'Cancel'),
            actErr && h('span', { style: { color: '#EF4444', fontSize: 12 } }, actErr)));
      })(),
      d.refund && h('div', { style: { color: '#F59E0B', marginBottom: 4 } },
        'Refund flagged: ' + (d.refund.order_number || '?') + ' ' + (d.refund.amount || '') + (d.refund.reason ? ' (' + d.refund.reason + ')' : '')),
      d.confident === false && h('div', { style: { color: '#F59E0B' } }, 'Model marked itself not confident')
    )
  );
}

// Zoomed-in view: one agent, close-up desk scene, stats, task history + shift
// log — plus a Settings tab for agents that register settings server-side.
// Chip-style input for the agents' email lists (Anthony 2026-09-03: "the email ends up in its
// own little box"). Each address is a pill with an ×; comma / semicolon / Enter / blur commit the
// typed text; Backspace on an empty box removes the last pill. Value in/out stays the
// "a@x.com, b@y.com" string the server validates (routes/agent-office.js parseEmailList) and the
// mailer splits. A pill that does not look like an address turns red so the save error is no surprise.
function EmailChipsInput({ value, onChange, placeholder }) {
  var _d = React.useState(''); var draft = _d[0]; var setDraft = _d[1];
  var chips = String(value || '').split(/[,;]/).map(function(x) { return x.trim(); }).filter(Boolean);
  var emit = function(list) { onChange(list.join(', ')); };
  var commit = function() { var t = draft.trim(); if (t) emit(chips.concat([t])); setDraft(''); };
  var remove = function(i) { emit(chips.filter(function(_, j) { return j !== i; })); };
  var looksValid = function(e) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e); };
  return h('div', {
    onClick: function(e) { var inp = e.currentTarget.querySelector('input'); if (inp) inp.focus(); },
    style: { display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', minHeight: 36, padding: '5px 8px', borderRadius: 6, border: '1px solid ' + C.border, background: C.bg, cursor: 'text' },
  },
    chips.map(function(c, i) {
      var ok = looksValid(c);
      return h('span', { key: c + '-' + i, title: ok ? c : 'Not a valid email address', style: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 4px 3px 9px', borderRadius: 999, fontSize: 12.5, lineHeight: 1.4, background: ok ? C.accent + '22' : '#F8717122', color: ok ? C.text : '#F87171', border: '1px solid ' + (ok ? C.accent + '55' : '#F8717166') } },
        c,
        h('button', { type: 'button', 'aria-label': 'Remove ' + c, title: 'Remove', onClick: function(e) { e.stopPropagation(); remove(i); },
          style: { border: 'none', background: 'transparent', color: 'inherit', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: '0 4px', fontFamily: 'inherit' } }, '×'));
    }),
    h('input', {
      type: 'text', value: draft, placeholder: chips.length ? 'add another' : (placeholder || ''),
      onChange: function(e) {
        var v = e.target.value;
        if (/[,;]/.test(v)) {
          var parts = v.split(/[,;]/);
          var add = parts.slice(0, -1).map(function(x) { return x.trim(); }).filter(Boolean);
          if (add.length) emit(chips.concat(add));
          setDraft(parts[parts.length - 1]);
        } else setDraft(v);
      },
      onKeyDown: function(e) {
        if (e.key === 'Enter') { e.preventDefault(); commit(); }
        else if (e.key === 'Backspace' && !draft && chips.length) { e.preventDefault(); remove(chips.length - 1); }
      },
      onBlur: commit,
      style: { flex: 1, minWidth: 140, border: 'none', outline: 'none', background: 'transparent', color: C.text, fontSize: 13, fontFamily: 'inherit', padding: '3px 2px' },
    }));
}

// Chat tab (Anthony 2026-09-06): admins ask an agent about its own task history. Read-only and
// explanatory on the server side; one rolling thread per admin per agent; Sonnet by default.
var OFFICE_CHAT_MODELS = [
  { key: 'sonnet', label: 'Sonnet 5' }, { key: 'haiku', label: 'Haiku 4.5' }, { key: 'opus', label: 'Opus 5' }, { key: 'fable', label: 'Fable 5.1' },
];
function OfficeAgentChat({ agent, showToast }) {
  var [msgs, setMsgs] = useState(null);
  var [model, setModel] = useState('sonnet');
  var [text, setText] = useState('');
  var [busy, setBusy] = useState(false);
  var [totalCost, setTotalCost] = useState(0);
  var endRef = useRef(null);
  var base = '/agent-office/agents/' + encodeURIComponent(agent.id) + '/chat';
  useEffect(function() {
    setMsgs(null);
    api(base).then(function(t) { setMsgs(t.messages || []); setModel(t.model || t.default_model || 'sonnet'); setTotalCost(Number(t.total_cost || 0)); })
      .catch(function(e) { setMsgs([]); if (showToast) showToast(e.message, 'error'); });
  }, [agent.id]);
  useEffect(function() { if (endRef.current && endRef.current.scrollIntoView) endRef.current.scrollIntoView({ block: 'end' }); }, [msgs, busy]);
  var send = function(q) {
    var t = String(q != null ? q : text).trim();
    if (!t || busy) return;
    setBusy(true); setText('');
    setMsgs(function(m) { return (m || []).concat([{ role: 'user', content: t }]); });
    api(base, { method: 'POST', body: JSON.stringify({ text: t, model: model }) })
      .then(function(r) { setMsgs(function(m) { return (m || []).concat([{ role: 'assistant', content: r.reply, model: r.model, cost: r.cost }]); }); setTotalCost(Number(r.total_cost || 0)); })
      .catch(function(e) { setMsgs(function(m) { return (m || []).concat([{ role: 'assistant', content: 'Sorry, that failed: ' + (e.message || 'error'), error: true }]); }); })
      .finally(function() { setBusy(false); });
  };
  var clear = function() {
    if (busy) return;
    api(base, { method: 'DELETE' }).then(function() { setMsgs([]); setTotalCost(0); }).catch(function(e) { if (showToast) showToast(e.message, 'error'); });
  };
  var suggestions = ['What did you do today?', 'What is waiting on me right now?', 'Walk me through your last error.', 'Which entries did you hold this week, and why?'];
  var bubble = function(m, i) {
    var me = m.role === 'user';
    return h('div', { key: i, style: { display: 'flex', justifyContent: me ? 'flex-end' : 'flex-start', marginBottom: 10 } },
      h('div', { style: { maxWidth: '78%', padding: '9px 13px', borderRadius: 12, fontSize: 13.5, lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        background: me ? C.accent : (m.error ? 'rgba(239,68,68,0.10)' : C.bg), color: me ? '#fff' : (m.error ? '#EF4444' : C.text), border: me ? 'none' : '1px solid ' + C.border } },
        m.content,
        !me && m.cost != null ? h('div', { style: { fontSize: 10.5, color: C.textDim, marginTop: 6 } }, (OFFICE_CHAT_MODELS.find(function(x) { return x.key === m.model; }) || { label: m.model || '' }).label + (Number(m.cost) ? ' · $' + Number(m.cost).toFixed(3) : '')) : null));
  };
  return h('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 10, padding: '14px 18px', marginBottom: 14, display: 'flex', flexDirection: 'column', height: 560 } },
    h('div', { style: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' } },
      h('div', { style: { fontSize: 13, fontWeight: 600, color: C.text } }, 'Ask ' + agent.name + ' about the work on this desk'),
      h('span', { style: { fontSize: 11.5, color: C.textMuted } }, 'Read-only. Answers come from the task history and settings; nothing here changes anything.'),
      h('span', { style: { flex: 1 } }),
      h('select', { value: model, onChange: function(e) { setModel(e.target.value); }, style: { background: C.bg, color: C.text, border: '1px solid ' + C.border, borderRadius: 6, padding: '4px 8px', fontSize: 12, fontFamily: 'inherit' } },
        OFFICE_CHAT_MODELS.map(function(o) { return h('option', { key: o.key, value: o.key }, o.label); })),
      totalCost > 0 && h('span', { style: { fontSize: 11.5, color: C.textMuted } }, 'thread $' + totalCost.toFixed(2)),
      h('button', { className: 'phov', onClick: clear, disabled: busy || !(msgs && msgs.length), title: 'Start over',
        style: { background: 'transparent', border: '1px solid ' + C.border, borderRadius: 6, color: C.textMuted, fontSize: 12, padding: '4px 10px', cursor: 'pointer', fontFamily: 'inherit' } }, 'Clear')),
    h('div', { style: { flex: 1, minHeight: 0, overflowY: 'auto', padding: '6px 4px' } },
      msgs === null && h('div', { style: { color: C.textMuted, fontSize: 13 } }, 'Loading…'),
      msgs && msgs.length === 0 && !busy && h('div', { style: { color: C.textMuted, fontSize: 13 } },
        h('div', { style: { marginBottom: 10 } }, 'Nothing asked yet. Try one of these:'),
        h('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 8 } },
          suggestions.map(function(sg) { return h('button', { key: sg, className: 'phov', onClick: function() { send(sg); },
            style: { background: C.bg, border: '1px solid ' + C.border, borderRadius: 999, color: C.text, fontSize: 12.5, padding: '6px 12px', cursor: 'pointer', fontFamily: 'inherit' } }, sg); }))),
      (msgs || []).map(bubble),
      busy && h('div', { style: { color: C.textMuted, fontSize: 12.5, fontStyle: 'italic' } }, agent.name + ' is looking through the history…'),
      h('div', { ref: endRef })),
    h('div', { style: { display: 'flex', gap: 8, marginTop: 10 } },
      h('input', { type: 'text', value: text, placeholder: 'Ask about an order, a customer, a held item, a decision…', disabled: busy,
        onChange: function(e) { setText(e.target.value); }, onKeyDown: function(e) { if (e.key === 'Enter') { e.preventDefault(); send(); } },
        style: { flex: 1, background: C.bg, color: C.text, border: '1px solid ' + C.border, borderRadius: 8, padding: '9px 12px', fontSize: 13.5, fontFamily: 'inherit' } }),
      h('button', { className: 'phov', onClick: function() { send(); }, disabled: busy || !text.trim(),
        style: { background: C.accent, color: '#fff', border: '1px solid ' + C.accent, borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: busy ? 'default' : 'pointer', fontFamily: 'inherit', opacity: busy || !text.trim() ? 0.6 : 1 } }, 'Send')));
}

// Old page routes that moved onto a desk (e.g. /shopify-faq → Sage's FAQ tab)
function OfficeAgentRedirect({ onNavigate, sub }) {
  useEffect(function() { if (onNavigate) onNavigate('agent-office', sub || null); }, []);
  return null;
}
window.OfficeAgentRedirect = OfficeAgentRedirect;

// ── Performance tab (Anthony 2026-09-07): spend and volume per New York day for a date range ──
// URL carries the range: /the-office/<dept>/<agent>/performance/<token>, token = today | 7d |
// 30d | month | last-month | YYYY-MM-DD..YYYY-MM-DD (default 30d). Days are New York days.
var OFFICE_RANGE_PRESETS = [['today', 'Today'], ['7d', '7 days'], ['30d', '30 days'], ['month', 'This month'], ['last-month', 'Last month']];
function officeNyToday() { return new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' }); }
function officeShiftDay(ymd, n) { var d = new Date(ymd + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); }
function officeRangeFromToken(tok) {
  var today = officeNyToday();
  var m = /^(\d{4}-\d{2}-\d{2})\.\.(\d{4}-\d{2}-\d{2})$/.exec(tok || '');
  if (m && m[1] <= m[2]) return { from: m[1], to: m[2], token: tok };
  if (tok === 'today') return { from: today, to: today, token: tok };
  if (tok === '7d') return { from: officeShiftDay(today, -6), to: today, token: tok };
  if (tok === 'month') return { from: today.slice(0, 8) + '01', to: today, token: tok };
  if (tok === 'last-month') { var lastEnd = officeShiftDay(today.slice(0, 8) + '01', -1); return { from: lastEnd.slice(0, 8) + '01', to: lastEnd, token: tok }; }
  return { from: officeShiftDay(today, -29), to: today, token: '30d' };
}
function officeIsDark() { var m = /^#([0-9a-f]{6})$/i.exec(String(C.bg || '')); if (!m) return true; var n = parseInt(m[1], 16); return ((n >> 16) * 0.299 + ((n >> 8) & 255) * 0.587 + (n & 255) * 0.114) < 128; }
function officeRangeBtn(key, label, active, onClick) {
  return h('button', { key: key, onClick: onClick, style: { padding: '5px 12px', borderRadius: 8, border: '1px solid ' + (active ? C.accent : C.border), background: active ? 'rgba(79,110,247,0.10)' : C.surface, color: active ? C.accent : C.textMuted, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' } }, label);
}
function officeCard(children, style) {
  return h('div', { style: Object.assign({ background: C.surface, border: '1px solid ' + C.border, borderRadius: 10, padding: '14px 18px', marginBottom: 14 }, style || {}) }, children);
}

// One measure per chart (spend, then volume), never a dual axis. Thin bars with a 2px gap,
// rounded ends, a recessive baseline, only the peak labelled, and a hover tooltip on every day.
// Hover: the column under the cursor highlights and a tooltip names the day and the value
// (a real HTML layer, not the browser's slow native SVG tooltip — Anthony 9-07).
function OfficeBars({ title, days, valueKey, fmt }) {
  var [hover, setHover] = useState(null);
  var svgRef = useRef(null);
  var vals = days.map(function(d) { return Number(d[valueKey]) || 0; });
  var max = Math.max.apply(null, vals.concat([0]));
  var W = 600, H = 120, padB = 18, padT = 14;
  var n = Math.max(1, days.length), gap = n > 90 ? 1 : 2, bw = Math.max(1, (W - gap * (n - 1)) / n);
  var peak = vals.indexOf(max);
  var ticks = n <= 8 ? days.map(function(_, i) { return i; }) : [0, Math.floor((n - 1) / 2), n - 1];
  var onMove = function(e) {
    var el = svgRef.current; if (!el) return;
    var r = el.getBoundingClientRect(); if (!r.width) return;
    var x = (e.clientX - r.left) / r.width * W;
    var i = Math.floor(x / (bw + gap));
    setHover(i >= 0 && i < n ? i : null);
  };
  var hd = hover != null ? days[hover] : null;
  var tipLeft = hover != null ? ((hover * (bw + gap) + bw / 2) / W * 100) : 0;
  return h('div', { style: { flex: 1, minWidth: 260, position: 'relative' } },
    h('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 4 } }, title),
    h('svg', { ref: svgRef, viewBox: '0 0 ' + W + ' ' + H, onMouseMove: onMove, onMouseLeave: function() { setHover(null); },
      style: { width: '100%', height: 'auto', display: 'block', cursor: 'crosshair' }, role: 'img', 'aria-label': title },
      h('line', { x1: 0, x2: W, y1: H - padB + 0.5, y2: H - padB + 0.5, stroke: C.border, strokeWidth: 1 }),
      hover != null ? h('rect', { x: hover * (bw + gap) - gap / 2, y: 0, width: bw + gap, height: H - padB, fill: C.text, fillOpacity: 0.06, style: { pointerEvents: 'none' } }) : null,
      days.map(function(d, i) {
        var v = vals[i]; var bh = max > 0 ? Math.round((H - padB - padT) * v / max) : 0;
        var x = i * (bw + gap); var y = H - padB - bh;
        return h('g', { key: d.day, style: { pointerEvents: 'none' } },
          v > 0 ? h('rect', { x: x, y: y, width: bw, height: Math.max(bh, 1), rx: Math.min(4, bw / 2), fill: C.accent, fillOpacity: hover == null || hover === i ? 1 : 0.55 }) : null,
          v > 0 && i === peak && hover == null ? h('text', { x: Math.min(W - 24, Math.max(24, x + bw / 2)), y: Math.max(10, y - 4), textAnchor: 'middle', fontSize: 10, fill: C.textMuted }, fmt(v)) : null);
      }),
      ticks.map(function(i) { var d = days[i]; if (!d) return null; return h('text', { key: 't' + i, x: i * (bw + gap) + bw / 2, y: H - 4, textAnchor: i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle', fontSize: 10, fill: C.textDim, style: { pointerEvents: 'none' } }, d.day.slice(5)); })),
    hd ? h('div', { style: { position: 'absolute', left: tipLeft + '%', top: 18, transform: 'translate(' + (tipLeft > 80 ? '-100%' : tipLeft < 20 ? '0' : '-50%') + ', 0)', pointerEvents: 'none', background: C.surface, border: '1px solid ' + C.border, borderRadius: 6, padding: '4px 8px', fontSize: 11.5, color: C.text, boxShadow: '0 4px 12px rgba(0,0,0,0.25)', whiteSpace: 'nowrap', zIndex: 2 } },
      h('span', { style: { color: C.textMuted } }, hd.day + ' · '), h('b', null, fmt(vals[hover]))) : null);
}

function OfficeAgentPerformance({ agent, range, onRange }) {
  var r = officeRangeFromToken(range);
  var [data, setData] = useState(null); var [err, setErr] = useState(null); var [loading, setLoading] = useState(true);
  var [from, setFrom] = useState(r.from); var [to, setTo] = useState(r.to);
  useEffect(function() { setFrom(r.from); setTo(r.to); }, [r.from, r.to]);
  useEffect(function() {
    var alive = true; setLoading(true);
    api('/agent-office/agents/' + encodeURIComponent(agent.id) + '/performance?from=' + r.from + '&to=' + r.to)
      .then(function(d) { if (alive) { setData(d); setErr(null); } })
      .catch(function(e) { if (alive) setErr(e.message); })
      .finally(function() { if (alive) setLoading(false); });
    return function() { alive = false; };
  }, [agent.id, r.from, r.to]);
  var apply = function() { if (from && to && from <= to) onRange(from + '..' + to); };
  var dateInput = function(val, set) {
    return h('input', { type: 'date', value: val, max: officeNyToday(), onChange: function(e) { set(e.target.value); },
      style: { padding: '4px 8px', borderRadius: 8, border: '1px solid ' + C.border, background: C.bg, color: C.text, fontSize: 12, fontFamily: 'inherit', colorScheme: officeIsDark() ? 'dark' : 'light' } });
  };
  var t = data && data.totals;
  var money = officeMoney;
  var num = function(v) { return Number(v || 0).toLocaleString(); };
  var tiles = t ? [
    { label: 'Spend' + (data.cost_note ? ' (est.)' : ''), value: money(t.cost) },
    { label: 'Chat spend', value: money(t.chat_cost) },
    { label: data.items_label, value: num(t.items) },
    { label: 'Cost per item', value: t.cost_per_item != null ? '$' + Number(t.cost_per_item).toFixed(t.cost_per_item < 0.1 ? 3 : 2) : '—' },
    { label: 'Polls', value: num(t.runs) },
  ].concat(data.columns.filter(function(c) { return c.key !== data.items_key; }).map(function(c) {
    return { label: c.label, value: num(t[c.key]), color: c.key === 'errors' && t[c.key] > 0 ? '#EF4444' : (c.key === 'held' || c.key === 'exceptions') && t[c.key] > 0 ? '#F59E0B' : undefined };
  })) : [];
  var exportCsv = function() {
    if (!data) return;
    var cols = [['day', 'Day'], ['runs', 'Polls'], ['cost', 'Spend'], ['chat_cost', 'Chat spend']].concat(data.columns.map(function(c) { return [c.key, c.label]; }));
    var esc = escapeCSV; // shared cell rule, formula-safe (review 2026-09-28, medium #31)
    var lines = [cols.map(function(c) { return esc(c[1]); }).join(',')].concat(data.days.map(function(d) { return cols.map(function(c) { return esc(d[c[0]]); }).join(','); }));
    var blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    var a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = String(agent.name || agent.id).toLowerCase() + '-performance-' + data.from + '-to-' + data.to + '.csv';
    document.body.appendChild(a); a.click();
    setTimeout(function() { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
  };
  var th = function(label, right) { return h('th', { key: label, style: { textAlign: right ? 'right' : 'left', padding: '6px 8px', fontSize: 11, fontWeight: 600, color: C.textMuted, borderBottom: '1px solid ' + C.border, whiteSpace: 'nowrap', position: 'sticky', top: 0, background: C.surface } }, label); };
  var td = function(key, v, right, muted, strong) { return h('td', { key: key, style: { textAlign: right ? 'right' : 'left', padding: '5px 8px', fontSize: 12.5, color: muted ? C.textDim : C.text, fontWeight: strong ? 700 : 400, borderBottom: '1px solid ' + C.border, whiteSpace: 'nowrap' } }, v); };
  var dayRows = data ? data.days.slice().reverse() : [];
  return h(Fragment, null,
    officeCard([
      h('div', { key: 'r', style: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' } },
        OFFICE_RANGE_PRESETS.map(function(p) { return officeRangeBtn(p[0], p[1], r.token === p[0], function() { onRange(p[0]); }); }),
        h('span', { key: 'sep', style: { width: 1, height: 22, background: C.border, margin: '0 4px' } }),
        dateInput(from, setFrom), h('span', { key: 'to', style: { color: C.textMuted, fontSize: 12 } }, 'to'), dateInput(to, setTo),
        officeRangeBtn('apply', 'Apply', /\.\./.test(r.token), apply),
        h('span', { key: 'sp', style: { flex: 1 } }),
        data && officeRangeBtn('csv', 'Export CSV', false, exportCsv)),
      h('div', { key: 'n', style: { fontSize: 11.5, color: C.textDim, marginTop: 8 } },
        'Days are New York days. ' + (data && data.cost_note ? agent.name + "'s spend is " + data.cost_note + '. ' : '') + 'Chat spend is what the Chat tab cost. Polls are the agent\'s work cycles.'),
    ]),
    err && officeCard(h('div', { style: { color: '#EF4444', fontSize: 12.5 } }, err)),
    loading && !data && officeCard(h('div', { style: { color: C.textMuted, fontSize: 13 } }, 'Loading...')),
    data && officeCard(h('div', { style: { display: 'flex', gap: 22, flexWrap: 'wrap', opacity: loading ? 0.6 : 1 } },
      tiles.map(function(s, i) { return h(OfficeStat, { key: i, label: s.label, value: s.value, color: s.color }); }))),
    data && officeCard(h('div', { style: { display: 'flex', gap: 24, flexWrap: 'wrap', opacity: loading ? 0.6 : 1 } },
      h(OfficeBars, { key: 'spend', title: 'Spend per day' + (data.cost_note ? ' (est.)' : ''), days: data.days, valueKey: 'cost', fmt: money }),
      h(OfficeBars, { key: 'items', title: data.items_label + ' per day', days: data.days, valueKey: data.items_key, fmt: num }))),
    data && officeCard(h('div', { style: { overflow: 'auto', maxHeight: 460 } },
      h('table', { style: { borderCollapse: 'collapse', width: '100%' } },
        h('thead', null, h('tr', null, [th('Day'), th('Polls', true), th('Spend', true), th('Chat', true)].concat(data.columns.map(function(c) { return th(c.label, true); })))),
        h('tbody', null,
          h('tr', { key: 'total' }, [td('d', 'Total ' + data.from + ' to ' + data.to, false, false, true), td('r', num(t.runs), true, false, true), td('c', money(t.cost), true, false, true), td('ch', money(t.chat_cost), true, false, true)]
            .concat(data.columns.map(function(c) { return td(c.key, num(t[c.key]), true, false, true); }))),
          dayRows.map(function(d) {
            var quiet = !d.runs && !d.chat_cost;
            return h('tr', { key: d.day }, [td('d', d.day, false, quiet), td('r', num(d.runs), true, quiet), td('c', money(d.cost), true, quiet), td('ch', d.chat_cost ? money(d.chat_cost) : '—', true, quiet)]
              .concat(data.columns.map(function(c) { return td(c.key, num(d[c.key]), true, quiet || !d[c.key]); })));
          })))), { padding: '4px 6px' }));
}

function OfficeDetailView({ agent, hist, histLoading, onBack, onLoadMore, showToast, onReload, onHistoryChanged, tab: tabProp, onTab, currentUser, range: rangeProp, onRange, filter, onFilter }) {
  var tab = tabProp || 'activity'; // URL-controlled (/the-office/<dept>/<agent>/settings, .../performance/<range>)
  var [cfg, setCfg] = useState(null);      // { fields, values } from the server
  // Review queue (Anthony 9-10): after File it / Ignore the next row that still needs a human
  // opens by itself; the Dropbox folder list is fetched once per desk so that form is instant.
  var [autoOpenSlug, setAutoOpenSlug] = useState(null);
  var [folders, setFolders] = useState(undefined);
  var [foldersLoading, setFoldersLoading] = useState(false);
  useEffect(function() {
    setFolders(undefined); setAutoOpenSlug(null);
    if (agent.id !== 'invoice-filing') return;
    setFoldersLoading(true);
    api('/agent-office/agents/invoice-filing/folders')
      .then(function(d) { setFolders((d && d.folders) || []); })
      .catch(function() { setFolders([]); })
      .finally(function() { setFoldersLoading(false); });
  }, [agent.id]);
  var [cfgVals, setCfgVals] = useState({});
  var [cfgSaving, setCfgSaving] = useState(false);

  useEffect(function() {
    setCfg(null);
    if (!agent.has_settings) return;
    api('/agent-office/agents/' + encodeURIComponent(agent.id) + '/settings')
      .then(function(d) { setCfg(d); setCfgVals(d.values || {}); })
      .catch(function(e) { console.error('[silent-catch]', e); });
  }, [agent.id]);

  var saveSettings = function() {
    setCfgSaving(true);
    api('/agent-office/agents/' + encodeURIComponent(agent.id) + '/settings', { method: 'PUT', body: JSON.stringify(cfgVals) })
      .then(function() {
        setCfgSaving(false);
        if (showToast) showToast('Settings saved');
        if (onReload) onReload();
      })
      .catch(function(e) { setCfgSaving(false); if (showToast) showToast(e.message, 'error'); });
  };

  var setVal = function(key, val) {
    setCfgVals(function(prev) { var n = Object.assign({}, prev); n[key] = val; return n; });
  };

  var status = agent.status || 'offline';
  var meta = OFFICE_STATUS_META[status] || OFFICE_STATUS_META.offline;
  var modeMeta = officeModeMeta(agent.mode);
  var t = agent.today || {};
  var life = agent.lifetime || {};
  var hired = life.hired_at ? new Date(life.hired_at).toLocaleDateString() : null;
  var allRows = (hist && hist.rows) || [];
  var runs = (hist && hist.runs) || [];
  // Rows leave the list the moment File it / Ignore is accepted (Anthony 9-10); the hidden set
  // clears on every history refresh, by which time the server already shows the new state.
  var [hidden, setHidden] = useState({});
  useEffect(function() { setHidden({}); }, [hist]);
  var rows = allRows.filter(function(x) { return !hidden[x.slug]; });
  // after File it / Ignore on a row, the next row that still has actions opens itself
  var onRowActed = function(slug) {
    var idx = rows.findIndex(function(x) { return x.slug === slug; });
    var next = rows.slice(idx + 1).find(function(x) { return x.decision && x.decision.actions && x.decision.actions.length; });
    setAutoOpenSlug(next ? next.slug : null);
  };
  var onRowGone = function(slug, a) {
    var row = rows.find(function(x) { return x.slug === slug; });
    var name = row ? (row.subject || slug) : slug;
    if (showToast) showToast(a && a.key === 'ignore' ? 'Ignored "' + name + '"' : 'Filing "' + name + '" in the background');
    onRowActed(slug);
    setHidden(function(hd) { var n = Object.assign({}, hd); n[slug] = true; return n; });
  };
  // While Danny is filing in the background, refresh every 10 s so rows turn Filed on their
  // own; a row that comes back as Review / Error after filing gets a toast with the reason.
  var anyFiling = allRows.some(function(x) { return x.agent_state === 'filing'; });
  useEffect(function() {
    if (!anyFiling) return;
    var t = setInterval(function() { if (onHistoryChanged) onHistoryChanged(); }, 10000);
    return function() { clearInterval(t); };
  }, [anyFiling]);
  var filingRef = useRef({});
  useEffect(function() {
    var now = {};
    allRows.forEach(function(x) { if (x.agent_state === 'filing') now[x.slug] = true; });
    Object.keys(filingRef.current).forEach(function(slug) {
      var x = allRows.find(function(y) { return y.slug === slug; });
      if (x && (x.agent_state === 'exception' || x.agent_state === 'error') && showToast) showToast('Filing failed: ' + (x.subject || slug) + (x.error ? ' — ' + x.error : ''), 'error');
    });
    filingRef.current = now;
  }, [hist]);
  // "On break" shows the desk empty with the agent standing beside it — the
  // same story every zoom level tells.
  var variant = officeVariant(status);
  var sign = officeSign(status);
  return h('div', null,
    h('div', { style: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 } },
      h('button', { onClick: onBack, style: { padding: '6px 14px', borderRadius: 8, border: '1px solid ' + C.border, background: C.surface, color: C.text, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' } }, '← Office floor'),
      h('span', { style: { fontSize: 18, fontWeight: 700, color: C.text } }, agent.name),
      h('span', { style: { fontSize: 13, color: C.textMuted } }, agent.role + (agent.dept && agent.dept !== agent.role ? ' · ' + agent.dept : '')),
      h('span', { style: { padding: '2px 9px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: modeMeta.bg, color: modeMeta.text } }, modeMeta.label),
      agent.model && h('span', { style: { fontSize: 11, color: C.textDim } }, agent.model),
      h('span', { style: { marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 7 } },
        h('span', { style: { width: 9, height: 9, borderRadius: '50%', background: meta.color, boxShadow: '0 0 6px ' + meta.color } }),
        h('span', { style: { fontSize: 13, fontWeight: 600, color: meta.color } }, meta.label),
        !agent.placeholder && h('span', { style: { fontSize: 12, color: C.textMuted } }, '· last check ' + officeAgo(agent.last_poll_at))
      )
    ),
    h('div', { style: { display: 'flex', justifyContent: 'center', borderRadius: 12, border: '1px solid ' + C.border, background: OFFICE_PHASES[officePhase()].backdrop, padding: '22px 28px 12px', marginBottom: 16, overflowX: 'auto' } },
      h('div', { className: 'officezoom', style: { position: 'relative', width: OFC_W * 1.45, height: OFC_H * 1.45 + 30 } },
        h('div', { style: { position: 'absolute', bottom: 0, left: 0 } },
          (agent.dept || '') === 'Reception'
            ? h(OfficeReceptionArt, { variant: variant, shirt: agent.shirt, scale: 1.45, hair: agent.hair, standee: status === 'idle', bang: status === 'error', look: officeLook(agent) })
            : h(OfficeDeskArt, { variant: variant, shirt: agent.shirt, scale: 1.45, bang: status === 'error', hair: agent.hair, standee: status === 'idle', deco: officeDecor(agent.id), pet: officePetVisit(agent), look: officeLook(agent) })),
        sign && h('div', { className: 'officesign ofc-float', style: { left: 285, top: 44, background: sign.bg, color: sign.color } }, sign.text)
      )
    ),
    agent.error && h('div', { style: { fontSize: 12.5, color: '#EF4444', marginBottom: 12 } }, 'Status error: ' + agent.error),
    (agent.has_settings || (!agent.placeholder && currentUser && currentUser.role === 'admin')) && h('div', { style: { display: 'flex', gap: 8, marginBottom: 14 } },
      ['activity'].concat(agent.has_performance ? ['performance'] : []).concat(agent.has_settings ? ['settings'] : []).concat((agent.extra_tabs || []).map(function(x) { return x.key; })).concat(!agent.placeholder && currentUser && currentUser.role === 'admin' ? ['chat'] : []).map(function(t) {
        var active = tab === t;
        return h('button', {
          key: t, onClick: function() { if (onTab) onTab(t); },
          style: { padding: '5px 16px', borderRadius: 8, border: '1px solid ' + (active ? C.accent : C.border),
            background: active ? 'rgba(79,110,247,0.10)' : C.surface, color: active ? C.accent : C.textMuted,
            fontSize: 12.5, fontWeight: 600, cursor: 'pointer' },
        }, t === 'activity' ? 'Activity' : t === 'performance' ? 'Performance' : t === 'settings' ? 'Settings' : t === 'chat' ? 'Chat' : (((agent.extra_tabs || []).find(function(x) { return x.key === t; }) || {}).label || t));
      })),
    tab === 'performance' && agent.has_performance && h(OfficeAgentPerformance, { agent: agent, range: rangeProp, onRange: onRange }),
    tab === 'chat' && currentUser && currentUser.role === 'admin' && h(OfficeAgentChat, { agent: agent, showToast: showToast }),
    // Agent-specific tabs (e.g. Sage's FAQ catalog): the server names a window component, rendered embedded
    (agent.extra_tabs || []).map(function(x) { return tab === x.key && window[x.component] ? h(window[x.component], { key: x.key, showToast: showToast, embedded: true }) : null; }),
    tab === 'settings' && h('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 10, padding: '16px 18px', marginBottom: 14, maxWidth: 560 } },
      !cfg && h('div', { style: { color: C.textMuted, fontSize: 13 } }, 'Loading settings...'),
      cfg && h(Fragment, null,
        cfg.fields.map(function(f) {
          if (f.type === 'checkbox') {
            return h('label', { key: f.key, style: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: C.text, marginBottom: 12, cursor: 'pointer' } },
              h('input', { type: 'checkbox', checked: !!cfgVals[f.key], onChange: function(e) { setVal(f.key, e.target.checked); } }),
              f.label);
          }
          if (f.type === 'emails') {
            return h('div', { key: f.key, style: { marginBottom: 12 } },
              h('div', { style: { fontSize: 11.5, fontWeight: 600, color: C.textMuted, marginBottom: 4 } }, f.label),
              h(EmailChipsInput, { value: cfgVals[f.key] != null ? cfgVals[f.key] : '', placeholder: f.placeholder, onChange: function(v) { setVal(f.key, v); } }));
          }
          if (f.type === 'textarea') {
            return h('div', { key: f.key, style: { marginBottom: 12 } },
              h('div', { style: { fontSize: 11.5, fontWeight: 600, color: C.textMuted, marginBottom: 4 } }, f.label),
              h('textarea', {
                value: cfgVals[f.key] != null ? cfgVals[f.key] : '', rows: f.rows || 8,
                onChange: function(e) { setVal(f.key, e.target.value); },
                style: { width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 6, border: '1px solid ' + C.border, background: C.bg, color: C.text, fontSize: 12.5, lineHeight: 1.5, fontFamily: 'inherit', resize: 'vertical' },
              }));
          }
          if (f.type === 'select') {
            return h('div', { key: f.key, style: { marginBottom: 12 } },
              h('div', { style: { fontSize: 11.5, fontWeight: 600, color: C.textMuted, marginBottom: 4 } }, f.label),
              h('select', {
                value: cfgVals[f.key] != null ? cfgVals[f.key] : '',
                onChange: function(e) { setVal(f.key, e.target.value); },
                style: { width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid ' + C.border, background: C.bg, color: C.text, fontSize: 13, fontFamily: 'inherit' },
              }, (f.options || []).map(function(o) { return h('option', { key: o.value, value: o.value }, o.label); })));
          }
          return h('div', { key: f.key, style: { marginBottom: 12 } },
            h('div', { style: { fontSize: 11.5, fontWeight: 600, color: C.textMuted, marginBottom: 4 } }, f.label),
            h('input', {
              type: f.type === 'number' ? 'number' : 'text',
              value: cfgVals[f.key] != null ? cfgVals[f.key] : '', min: f.min, max: f.max, placeholder: f.placeholder,
              onChange: function(e) { setVal(f.key, e.target.value); },
              style: { width: '100%', boxSizing: 'border-box', padding: '7px 10px', borderRadius: 6, border: '1px solid ' + C.border, background: C.bg, color: C.text, fontSize: 13, fontFamily: 'inherit' },
            }));
        }),
        h('button', {
          disabled: cfgSaving, onClick: saveSettings,
          style: { padding: '7px 18px', borderRadius: 8, border: 'none', background: C.accent, color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: cfgSaving ? 'default' : 'pointer', opacity: cfgSaving ? 0.6 : 1 },
        }, cfgSaving ? 'Saving...' : 'Save settings')
      )
    ),
    // planned agent: the desk is reserved — show the job description instead of
    // stats and history that don't exist yet
    agent.placeholder && h('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 10, padding: '16px 18px', marginBottom: 14 } },
      h('div', { style: { fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 6 } }, 'Job description'),
      h('div', { style: { fontSize: 13, color: C.textMuted, lineHeight: 1.55 } }, agent.description || 'Planned agent — not built yet.'),
      h('div', { style: { fontSize: 12, color: C.textDim, marginTop: 10 } }, 'Desk reserved. The boxes unpack themselves the day this agent ships.')
    ),
    // stats card: agents that ship a generic `stats` array (Danny) render it
    // directly; Alexa keeps the original reamaze-shaped grid below
    tab === 'activity' && !agent.placeholder && agent.stats && h('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 10, padding: '14px 18px', marginBottom: 14 } },
      h('div', { style: { display: 'flex', gap: 22, flexWrap: 'wrap' } },
        agent.stats.map(function(s, i) { return h(OfficeStat, { key: i, label: s.label, value: s.value, color: s.color }); })
      )
    ),
    tab === 'activity' && !agent.placeholder && !agent.stats && h('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 10, padding: '14px 18px', marginBottom: 14 } },
      h('div', { style: { display: 'flex', gap: 22, flexWrap: 'wrap' } },
        h(OfficeStat, { label: 'Handled today', value: Number(t.processed || 0) }),
        h(OfficeStat, { label: 'Auto', value: Number(t.auto_sent || 0), color: '#10B981' }),
        h(OfficeStat, { label: 'Held', value: Number(t.held || 0), color: '#F59E0B' }),
        h(OfficeStat, { label: 'Forwarded', value: Number(t.forwarded || 0) }),
        h(OfficeStat, { label: 'Noise', value: Number(t.noise_done || 0) }),
        h(OfficeStat, { label: 'Errors today', value: Number(t.errors || 0), color: Number(t.errors || 0) > 0 ? '#EF4444' : C.text }),
        h(OfficeStat, { label: 'Spend today', value: officeMoney(t.cost) }),
        h(OfficeStat, { label: 'Lifetime handled', value: Number(life.processed || 0) }),
        h(OfficeStat, { label: 'Lifetime spend', value: officeMoney(life.cost) }),
        hired && h(OfficeStat, { label: 'Hired', value: hired })
      )
    ),
    tab === 'activity' && !agent.placeholder && h('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 10, padding: '14px 18px', marginBottom: 14 } },
      h('div', { style: { fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' } },
        h('span', null, 'Task history', hist && hist.total ? h('span', { style: { fontWeight: 500, color: C.textMuted } }, filter === 'review' ? ' · showing ' + hist.total + ' to review' : ' · ' + hist.total + ' total') : null),
        // Review chip (Anthony 9-10): how many items still need a human; click filters to them
        filter !== 'review' && hist && Number(hist.review_total) > 0 && onFilter && h('button', {
          onClick: function() { onFilter('review'); }, title: 'Show only the items that still need review',
          style: { padding: '1px 9px', borderRadius: 9, fontSize: 10.5, fontWeight: 700, background: OFFICE_STATE_META.exception.bg, color: OFFICE_STATE_META.exception.text, border: '1px solid transparent', cursor: 'pointer', fontFamily: 'inherit' } },
          'Review · ' + hist.review_total),
        filter === 'review' && onFilter && h('button', {
          onClick: function() { onFilter(null); }, title: 'Back to the full task history',
          style: { padding: '1px 9px', borderRadius: 9, fontSize: 11, fontWeight: 600, background: 'transparent', color: C.accent, border: '1px solid ' + C.border, cursor: 'pointer', fontFamily: 'inherit' } },
          'Show all')),
      !hist && histLoading && h('div', { style: { color: C.textMuted, fontSize: 13, padding: '10px 0' } }, 'Loading history...'),
      // stable keys (slug) so a background refresh never remounts the row someone is typing in
      (function() {
        var seen = {};
        return rows.map(function(r, i) {
          var k = r.slug; if (seen[k]) k = k + '-' + i; seen[k] = true;
          return h(OfficeHistoryRow, { key: k, r: r, first: i === 0, onChanged: onHistoryChanged, onActed: onRowActed, onGone: onRowGone, autoOpen: r.slug === autoOpenSlug, folders: folders, foldersLoading: foldersLoading, onReviewClick: onFilter ? function() { onFilter('review'); } : null });
        });
      })(),
      hist && rows.length === 0 && h('div', { style: { color: C.textMuted, fontSize: 13, padding: '10px 0' } }, filter === 'review' ? 'Nothing left to review.' : 'Nothing handled yet.'),
      hist && rows.length < hist.total && h('div', { style: { paddingTop: 10, textAlign: 'center' } },
        h('button', { disabled: histLoading, onClick: onLoadMore, style: { padding: '6px 16px', borderRadius: 8, border: '1px solid ' + C.border, background: 'transparent', color: C.accent, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' } },
          histLoading ? 'Loading...' : 'Load more'))
    ),
    tab === 'activity' && runs.length > 0 && h('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 10, padding: '14px 18px', marginBottom: 14 } },
      h('div', { style: { fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 6 } }, 'Shift log', h('span', { style: { fontWeight: 500, color: C.textMuted } }, ' · recent polls with activity')),
      runs.map(function(r, i) {
        // generic shift-log rows (r.parts = [{label, color, strong}]) — used by
        // agents whose runs aren't reamaze-shaped
        // every counter keeps a fixed-width slot (blank when zero) so the
        // shift log reads as columns instead of drifting per row
        if (r.parts) {
          return h('div', { key: i, style: { display: 'flex', gap: 14, alignItems: 'center', padding: '6px 0', borderTop: i ? '1px solid ' + C.border : 'none', fontSize: 12.5, color: C.textMuted } },
            h('span', { style: { color: C.textDim, width: 96, flexShrink: 0, whiteSpace: 'nowrap' } }, officeWhen(r.started_at)),
            r.parts.map(function(p, pi) { return h('span', { key: pi, style: { width: p.w, flexShrink: p.w ? 0 : undefined, whiteSpace: 'nowrap', color: p.color || (p.strong ? C.text : undefined), fontWeight: p.strong || p.color ? 600 : 400 } }, p.label); }),
            h('span', { style: { marginLeft: 'auto' } }, officeMoney(r.cost))
          );
        }
        var slot = function(n, word, w, color, strong) {
          n = Number(n) || 0;
          return h('span', { style: { width: w, flexShrink: 0, whiteSpace: 'nowrap', color: n > 0 ? color : undefined, fontWeight: strong || (color && n > 0) ? 600 : 400 } }, n > 0 ? n + ' ' + word : '');
        };
        return h('div', { key: i, style: { display: 'flex', gap: 14, alignItems: 'center', padding: '6px 0', borderTop: i ? '1px solid ' + C.border : 'none', fontSize: 12.5, color: C.textMuted } },
          h('span', { style: { color: C.textDim, width: 96, flexShrink: 0, whiteSpace: 'nowrap' } }, officeWhen(r.started_at)),
          h('span', { style: { width: 60, flexShrink: 0 } }, r.seen + ' seen'),
          h('span', { style: { width: 80, flexShrink: 0, color: C.text, fontWeight: 600 } }, r.processed + ' handled'),
          slot(r.auto_sent, 'auto', 56, '#10B981'),
          slot(r.held, 'held', 56, '#F59E0B'),
          slot(r.forwarded, 'fwd', 50, '#6366F1'),
          slot(r.noise_done, 'noise', 60),
          slot(r.errors, Number(r.errors) === 1 ? 'error' : 'errors', 64, '#EF4444'),
          h('span', { style: { marginLeft: 'auto' } }, officeMoney(r.cost))
        );
      })
    )
  );
}

var OFFICE_HIST_PAGE = 50;
// Pure helpers for the history list's request guard (tested in office-history-seq-2026-09-26).
// g = { seq: last request id, refresh: id of the last offset-0 (refresh) request }.
// A refresh reply counts only if it is the latest refresh; a load-more reply counts only
// if no refresh started after it.
function officeHistIsCurrent(mySeq, offset, g) {
  if (!offset) return mySeq === g.refresh;
  return mySeq > g.refresh;
}
function officeHistMerge(prev, d, offset) {
  if (offset && prev) {
    // A load-more for an offset we no longer sit at (duplicate click) would duplicate rows.
    if (prev.rows.length !== offset) return prev;
    return { total: d.total, runs: prev.runs, rows: prev.rows.concat(d.rows) };
  }
  return d;
}
window.officeHistIsCurrent = officeHistIsCurrent; window.officeHistMerge = officeHistMerge;

// Everything has a URL (Anthony 2026-08-30): /the-office = building,
// /the-office/<dept> = floor, /the-office/<dept>/<agent> = detail,
// + /settings for the settings tab. View state derives ENTIRELY from routeArg
// (the path after /the-office) so deep links and browser back/forward work.
function officeSlug(s) {
  return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

// Contact sheet of every style at the three scales: /the-office/look-sheet
function OfficeLookSheet({ onBack }) {
  var samples = [];
  OFC_STYLES_F.forEach(function(st, i) { samples.push({ id: 'sheet-f-' + i, hair: 'long', dept: i === 0 ? 'Customer Support' : 'Purchasing',
    look: { hairStyle: st, hairColor: OFC_HAIR_COLORS[i % OFC_HAIR_COLORS.length], skin: OFC_SKINS[i % OFC_SKINS.length], glasses: i % 2 ? 'round' : null, earrings: i % 3 ? 'hoop' : 'stud', necklace: i % 2 === 0 } }); });
  OFC_STYLES_M.forEach(function(st, i) { samples.push({ id: 'sheet-m-' + i, hair: 'short', dept: i === 0 ? 'Customer Support' : 'Logistics',
    look: { hairStyle: st, hairColor: OFC_HAIR_COLORS[(i + 3) % OFC_HAIR_COLORS.length], skin: OFC_SKINS[(i + 2) % OFC_SKINS.length], glasses: i % 2 ? 'rect' : null, beard: i % 2 === 1 } }); });
  return h('div', null,
    h('div', { style: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 } },
      h('button', { onClick: onBack, style: { padding: '6px 14px', borderRadius: 8, border: '1px solid ' + C.border, background: C.surface, color: C.text, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' } }, '← Building'),
      h('h2', { style: { fontSize: 20, fontWeight: 700, color: C.text } }, 'Figure variety pack')),
    h('div', { style: { fontSize: 12.5, color: C.textMuted, marginBottom: 16 } }, 'Each style at the close-up, floor and building scales. Colours, skin tones and accessories rotate across the samples.'),
    h('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 } },
      samples.map(function(sm) {
        var lk = officeLook(sm);
        var label = lk.hairStyle + (lk.beard ? ' + beard' : '') + (lk.glasses ? ', ' + lk.glasses + ' glasses' : '') + (lk.earrings ? ', ' + lk.earrings + 's' : '') + (lk.headset ? ', headset' : '');
        return h('div', { key: sm.id, style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 10, padding: 10 } },
          h('div', { style: { fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 } }, label),
          h('div', { style: { display: 'flex', alignItems: 'flex-end', gap: 10, background: OFFICE_PHASES.day.backdrop, borderRadius: 8, padding: 8 } },
            h(OfficeDeskArt, { variant: 'work', shirt: '#4F6EF7', scale: 0.5, hair: sm.hair, look: lk, deco: officeDecor(sm.id) }),
            h('svg', { width: 64, height: 132, viewBox: '-32 -92 64 96', style: { overflow: 'visible' } }, h('g', { transform: 'scale(1.05)' }, officeStrollPerson('#4F6EF7', sm.hair, false, lk))),
            h('svg', { width: 44, height: 64, viewBox: '-22 -50 44 54', style: { overflow: 'visible' } }, officeMiniPerson({ shirt: '#4F6EF7', hair: sm.hair, look: lk, mug: true }))));
      })),
    h('h3', { style: { fontSize: 15, fontWeight: 700, color: C.text, margin: '22px 0 8px' } }, 'Break areas'),
    h('div', { style: { fontSize: 12.5, color: C.textMuted, marginBottom: 12 } }, 'One sofa design per floor, a plant species to match, and the restroom door with its occupied slider.'),
    h('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 } },
      OFC_SOFAS.map(function(S, v) {
        var seats = officeSofaSeats(30, v);
        return h('div', { key: S.name, style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 10, padding: 10 } },
          h('div', { style: { fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 } }, S.name + ' · ' + OFC_PLANT_SPECIES[(v + 2) % 5]),
          h('svg', { width: '100%', viewBox: '0 130 ' + (S.w + 150) + ' 150', style: { display: 'block', background: OFFICE_PHASES.day.backdrop, borderRadius: 8 } },
            officeSofa(30, v, 'sofa'),
            officePlant((v + 2) % 5, 30 + S.w + 44, FLOOR_GROUND, 1.9, OFC_POTS[(v + 1) % 3], 'plant'),
            seats.map(function(st, i) { return officeSitter(['#4F6EF7', '#14B8A6', '#F59E0B'][i % 3], officeLook({ id: 'sit' + v + i, hair: i % 2 ? 'long' : 'short' }), st.x, st.y, 'sit' + i); })));
      }).concat([
        h('div', { key: 'wc', style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 10, padding: 10 } },
          h('div', { style: { fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 } }, 'restroom · occupied cycle'),
          h('svg', { width: '100%', viewBox: '0 60 240 220', style: { display: 'block', background: OFFICE_PHASES.day.backdrop, borderRadius: 8 } },
            h('g', { transform: 'translate(20,' + FLOOR_GROUND + ') scale(1.5)' }, officeStrollPerson('#4F6EF7', 'short', false, officeLook({ id: 'wc-demo', hair: 'short' }))),
            officeRestroom(100, 'wc', true))),
      ])),
    h('h3', { style: { fontSize: 15, fontWeight: 700, color: C.text, margin: '22px 0 8px' } }, 'Beach'),
    h('div', { style: { fontSize: 12.5, color: C.textMuted, marginBottom: 12 } }, 'The sand strip behind the HQ, left and right of the building, by time of day.'),
    h('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 } },
      [['day', 0], ['day', 1], ['dusk', 0], ['dusk', 1]].map(function(p) {
        var fakeL = { groundY: 100, sceneW: 1300, BX: 330, BW: 640 }, seaY = 36, left = p[1] === 0;
        var seaC = p[0] === 'day' ? '#5E9DC4' : '#46648E', sandC = p[0] === 'day' ? '#D9C49A' : '#9C8869';
        return h('div', { key: p[0] + p[1], style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 10, padding: 10 } },
          h('div', { style: { fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 6 } }, p[0] + ' · ' + (left ? 'left of the building' : 'right of the building')),
          h('svg', { width: '100%', viewBox: (left ? 0 : 970) + ' 0 330 122', style: { display: 'block', background: OFFICE_PHASES[p[0]].backdrop, borderRadius: 8 } },
            h('rect', { x: 0, y: seaY, width: 1300, height: 30, fill: seaC }),
            h('rect', { x: 0, y: seaY + 30, width: 1300, height: 34, fill: sandC }),
            h('rect', { x: 0, y: 100, width: 1300, height: 22, fill: '#1A222E' }),
            officePalmTree(38, 98, 0.7), officePalmTree(1248, 98, 0.75),
            officeBeachCrowd(p[0], fakeL)));
      })));
}

function AgentOfficePage({ showToast, onNavigate, currentUser, routeArg }) {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [hist, setHist] = useState(null);           // { rows, runs, total }
  var [histLoading, setHistLoading] = useState(false);
  var [, setClockTick] = useState(0); // re-render for the "x min ago" labels

  useEffect(function() { officeEnsureStyle(); }, []);

  var load = useCallback(function() {
    api('/agent-office/status')
      .then(function(d) { setData(d); setLoading(false); })
      .catch(function(e) { setLoading(false); if (showToast) showToast(e.message, 'error'); });
  }, []);

  useEffect(function() {
    load();
    var poll = setInterval(load, 30000);
    var clk = setInterval(function() { setClockTick(function(i) { return i + 1; }); }, 30000);
    return function() { clearInterval(poll); clearInterval(clk); };
  }, [load]);

  // Request sequence guard (9-26): switching agent/filter or a refresh can overtake an
  // older request; a stale reply must not overwrite the newer list, and a load-more that
  // started before a refresh must not append onto the refreshed rows.
  var histSeqRef = useRef({ seq: 0, refresh: 0 });
  var loadHist = useCallback(function(agentId, offset, filter) {
    var g = histSeqRef.current;
    var mySeq = ++g.seq;
    if (!offset) g.refresh = mySeq;
    setHistLoading(true);
    api('/agent-office/agents/' + encodeURIComponent(agentId) + '/history?limit=' + OFFICE_HIST_PAGE + '&offset=' + offset + (filter ? '&state=' + encodeURIComponent(filter) : ''))
      .then(function(d) {
        if (!officeHistIsCurrent(mySeq, offset, g)) return;
        setHist(function(prev) { return officeHistMerge(prev, d, offset); });
        if (mySeq === g.seq) setHistLoading(false);
      })
      .catch(function(e) {
        if (!officeHistIsCurrent(mySeq, offset, g)) return;
        if (mySeq === g.seq) setHistLoading(false);
        if (showToast) showToast(e.message, 'error');
      });
  }, []);

  // route → view derivation
  var go = useCallback(function(sub) { if (onNavigate) onNavigate('agent-office', sub || null); }, [onNavigate]);
  var agents = (data && data.agents) || [];
  var parts = String(routeArg || '').split('/').filter(Boolean);
  var deptSlug = parts[0] || null, agentSlug = parts[1] || null;
  var detailTab = parts[2] ? String(parts[2]) : 'activity'; // settings, chat, performance, or an agent-specific tab such as faq
  var detailRange = parts[3] ? String(parts[3]) : null;    // performance/<range token>

  var deptNames = [];
  agents.forEach(function(a) { var d = a.dept || 'Operations'; if (deptNames.indexOf(d) === -1) deptNames.push(d); });
  var deptFocus = deptSlug ? (deptNames.filter(function(d) { return officeSlug(d) === deptSlug; })[0] || null) : null;
  var focused = agentSlug
    ? agents.find(function(a) { return officeSlug(a.name) === agentSlug || a.id === agentSlug; })
    : null;
  var view = deptSlug === 'look-sheet' ? 'sheet' : focused ? 'detail' : (deptFocus ? 'floor' : 'building'); // look-sheet = figure contact sheet

  var agentPath = function(a) { return '/' + officeSlug(a.dept || 'Operations') + '/' + officeSlug(a.name); };
  var openAgent = function(id) {
    var a = agents.find(function(x) { return x.id === id; });
    if (a) go(agentPath(a));
  };

  // Placeholders have no history endpoint — never ask for one. Boolean dep so
  // the 30s poll's fresh agents array doesn't refire the fetch.
  var focusId = focused ? focused.id : null;
  var focusedPlaceholder = !!(focused && focused.placeholder);

  // Review filter (Anthony 9-10): /the-office/<dept>/<agent>/activity/review lists only the
  // items a human still has to handle. It stays on until the server reports none left.
  var histFilter = detailTab === 'activity' && detailRange === 'review' ? 'review' : null;
  useEffect(function() {
    if (!focusId || focusedPlaceholder) { setHist(null); return; }
    loadHist(focusId, 0, histFilter);
  }, [focusId, focusedPlaceholder, loadHist, histFilter]);
  useEffect(function() {
    if (histFilter === 'review' && focused && hist && hist.filter === 'review' && Number(hist.review_total) === 0) {
      if (showToast) showToast('All review items cleared');
      go(agentPath(focused));
    }
  }, [hist, histFilter]);

  // dept floors are real floors now: walking in shows only that dept's desks
  var floorAgents = deptFocus
    ? agents.filter(function(a) { return (a.dept || 'Operations') === deptFocus; })
    : agents;

  return h('div', { style: { padding: '24px 28px', maxWidth: 1280, margin: '0 auto' } },
    view === 'sheet' && h(OfficeLookSheet, { onBack: function() { go(null); } }),
    view !== 'detail' && view !== 'sheet' && h(Fragment, null,
      h('div', { style: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 } },
        view === 'floor' && h('button', { onClick: function() { go(null); }, style: { padding: '6px 14px', borderRadius: 8, border: '1px solid ' + C.border, background: C.surface, color: C.text, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' } }, '← Building'),
        h('h2', { style: { fontSize: 20, fontWeight: 700, color: C.text } }, 'The Office'),
        h('span', { style: { fontSize: 13, color: C.textMuted } },
          view === 'floor' ? (deptFocus ? deptFocus : 'Our agents') + ' floor. Click a cubicle to zoom in.' : 'Atharva HQ. Click a floor to walk in.'),
        h('button', { onClick: load, style: { marginLeft: 'auto', padding: '6px 14px', borderRadius: 8, border: '1px solid ' + C.border, background: C.surface, color: C.text, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' } }, 'Refresh')
      ),
      view === 'floor'
        ? (deptFocus === 'Reception'
            ? h(OfficeReceptionFloor, { agent: floorAgents[0], data: data, loading: loading, onOpen: openAgent })
            : h(OfficeFloor, { agents: floorAgents, data: data, loading: loading, onOpen: openAgent, dept: deptFocus }))
        : h(OfficeBuilding, { agents: agents, data: data, loading: loading, onEnterFloor: function(f) { go('/' + officeSlug(f.dept)); } }),
      !loading && !agents.length && h('div', { style: { textAlign: 'center', color: C.textMuted, padding: 40 } }, 'No agents reporting yet.')
    ),
    view === 'detail' && focused && h(OfficeDetailView, {
      agent: focused, hist: hist, histLoading: histLoading,
      onBack: function() { go('/' + officeSlug(focused.dept || 'Operations')); },
      onLoadMore: function() { if (hist) loadHist(focusId, hist.rows.length, histFilter); },
      showToast: showToast, onReload: load,
      // a row action (Danny's file-by-hand / ignore) changed a document: refresh status + history
      onHistoryChanged: function() { load(); if (focusId) loadHist(focusId, 0, histFilter); },
      filter: histFilter,
      onFilter: function(f) { go(agentPath(focused) + (f ? '/activity/' + f : '')); },
      tab: detailTab,
      onTab: function(t) { go(agentPath(focused) + (t === 'activity' ? '' : '/' + t)); },
      range: detailRange,
      onRange: function(tok) { go(agentPath(focused) + '/performance' + (tok ? '/' + tok : '')); },
      currentUser: currentUser,
    })
  );
}

window.AgentOfficePage = AgentOfficePage;

;
/* ===== app-main.js ===== */
// Captured at script load — the router normalizes unknown paths (like /login)
// with replaceState before auth resolves, which wiped ?returnTo= mid-boot and
// stranded subdomain users on the dashboard (8-12).
var _bootReturnTo = null;
try { _bootReturnTo = new URLSearchParams(window.location.search).get('returnTo'); } catch (e) {}

// Toast for POST /analysis/wfs-fee-fetch (PO Analysis tab + Analysis page). "Own" = our own
// last-90-day Walmart actuals from the reconciliation reports (avg sale price, commission paid,
// WFS fee paid); "Walmart" = the Items API + buy box scrape for SKUs with no own sales.
function wfsFetchSummary(result, total) {
  var parts = [];
  if (result.own) parts.push('Own 90-day sales for ' + result.own + ' of ' + total + ' SKU' + (total === 1 ? '' : 's') + '.');
  var wt = result.walmart_total != null ? result.walmart_total : total;
  if (wt > 0) {
    var w = 'Walmart: fetched ' + (result.fetched || 0) + '/' + wt + '.';
    if (result.skipped) w += ' ' + result.skipped + ' skipped (data < 7 days old).';
    if (result.notFound) w += ' ' + result.notFound + ' not on Walmart.';
    if (result.errors) w += ' ' + result.errors + ' errors.';
    parts.push(w);
  }
  if (result.own_error) parts.push('Recon reports: ' + result.own_error + '.');
  else if (result.own_sync && result.own_sync.errors) parts.push('Recon reports: ' + result.own_sync.errors + ' file' + (result.own_sync.errors === 1 ? '' : 's') + ' failed to load (Settings → Integrations → Walmart recon status).');
  return parts.join(' ') || 'Nothing to fetch.';
}

// ─── CUSTOMER SERVICE (moved) ───
// The unified ticket desk (CustomerServicePage + RefundPanel, ~1,100 lines) retired
// 2026-09-06: the inbox is its own app at cs.atharvatoolz.com (docs/22). Legacy /cs
// links land here so a bookmark never dead-ends.
function CustomerServiceMovedPage() {
  return React.createElement('div', { style: { padding: 24, maxWidth: 560 } },
    React.createElement(Card, { style: { padding: 28, textAlign: 'center' } },
      React.createElement(LIcon, { name: 'headphones', size: 30, style: { color: C.accent, marginBottom: 12 } }),
      React.createElement('div', { style: { fontSize: 17, fontWeight: 500, color: C.text, marginBottom: 6 } }, 'Customer Service has moved'),
      React.createElement('div', { style: { fontSize: 13, color: C.textMuted, lineHeight: 1.6, marginBottom: 16 } },
        'Conversations from every marketplace now live in the Customer Service app. It opens in its own tab, like Shipping.'),
      React.createElement('a', { href: 'https://cs.atharvatoolz.com', target: '_blank', rel: 'noopener',
        style: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, background: C.accent, color: '#fff', fontSize: 13, fontWeight: 500, textDecoration: 'none' } },
        React.createElement(LIcon, { name: 'external-link', size: 14, color: '#fff' }), 'Open Customer Service')));
}

// ─── NOT FOUND ───
// Unknown app paths (e.g. /products, /shipping) used to render the Dashboard with
// the URL kept, which looked like a broken link. Now a small card with the real homes.
function NotFoundPage(props) {
  var linkStyle = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500, textDecoration: 'none', border: '1px solid ' + C.border, color: C.text, background: C.surface }; // theme surface (a hard #fff hid C.text in dark mode)
  return React.createElement('div', { style: { padding: 24, maxWidth: 560 } },
    React.createElement(Card, { style: { padding: 28, textAlign: 'center' } },
      React.createElement(LIcon, { name: 'map-pin-off', size: 30, style: { color: C.textDim, marginBottom: 12 } }),
      React.createElement('div', { style: { fontSize: 17, fontWeight: 500, color: C.text, marginBottom: 6 } }, 'Page Not Found'),
      React.createElement('div', { style: { fontSize: 13, color: C.textMuted, lineHeight: 1.6, marginBottom: 16 } },
        'There is no page at ', React.createElement('b', null, props.path || window.location.pathname), '. Shipping and Customer Service live in their own apps.'),
      React.createElement('div', { style: { display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' } },
        React.createElement('a', { href: '/', onClick: function(e) { if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return; e.preventDefault(); props.onNavigate && props.onNavigate('dashboard'); },
          style: Object.assign({}, linkStyle, { background: C.accent, color: '#fff', border: 'none' }) }, 'Go to Dashboard'),
        React.createElement('a', { href: 'https://ship.atharvatoolz.com', target: '_blank', rel: 'noopener', style: linkStyle }, 'Open Shipping'),
        React.createElement('a', { href: 'https://cs.atharvatoolz.com', target: '_blank', rel: 'noopener', style: linkStyle }, 'Open Customer Service'))));
}

// ─── FORECASTING PAGE ───
var _forecastCache = { data: null, ts: 0, filter: 'all', search: '', vendorFilter: '', warehouseFilter: '', paymentTermsFilter: '', page: 0 };
var FORECAST_TTL = 60 * 60 * 1000; // 1 hour

function ForecastingPage({ showToast, onNavigate }) {
  var [data, setData] = useState(_forecastCache.data);
  var [loading, setLoading] = useState(!_forecastCache.data);
  var [filter, setFilter] = useState(_forecastCache.filter);
  var [search, setSearch] = useState(_forecastCache.search);
  var [vendorFilter, setVendorFilter] = useState(_forecastCache.vendorFilter);
  var [warehouseFilter, setWarehouseFilter] = useState(_forecastCache.warehouseFilter);
  var [paymentTermsFilter, setPaymentTermsFilter] = useState(_forecastCache.paymentTermsFilter);
  var [syncing, setSyncing] = useState(false);
  var [selected, setSelected] = useState({});
  var lastCheckedRef = useRef(null);
  var sortedDataRef = useRef([]);
  var [addingToPO, setAddingToPO] = useState(false);
  var [addToPOResult, setAddToPOResult] = useState(null);
  var [loadError, setLoadError] = useState(null);
  var loadSeqRef = useRef(0);

  var loadData = useCallback(function(force, silent) {
    if (!force && _forecastCache.data && (Date.now() - _forecastCache.ts) < FORECAST_TTL) {
      setData(_forecastCache.data); setLoading(false); setLoadError(null); return;
    }
    if (!silent) setLoading(true);
    // Only the newest request lands (a slow earlier load must not overwrite a Refresh)
    var seq = ++loadSeqRef.current;
    // When force=true (user clicked Refresh), bust the server-side cache too.
    var url = force ? '/analysis/demand-forecast?fresh=1' : '/analysis/demand-forecast';
    api(url).then(function(d) {
      if (seq !== loadSeqRef.current) return;
      setData(d); setLoadError(null); _forecastCache.data = d; _forecastCache.ts = Date.now();
    }).catch(function(e) {
      if (seq !== loadSeqRef.current) return;
      setLoadError(e.message || 'Could not load the forecast'); showToast(e.message, 'error');
    }).finally(function() { if (seq === loadSeqRef.current) setLoading(false); });
  }, []);
  useEffect(function() { loadData(); }, [loadData]);

  // Persist filter state to cache on change
  useEffect(function() { _forecastCache.filter = filter; }, [filter]);
  useEffect(function() { _forecastCache.search = search; }, [search]);
  useEffect(function() { _forecastCache.vendorFilter = vendorFilter; }, [vendorFilter]);
  useEffect(function() { _forecastCache.warehouseFilter = warehouseFilter; }, [warehouseFilter]);
  useEffect(function() { _forecastCache.paymentTermsFilter = paymentTermsFilter; }, [paymentTermsFilter]);

  var syncOrders = function() {
    setSyncing(true);
    api('/integrations/zentail/sync-orders', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) })
      .then(function(r) { showToast('Synced ' + (r.synced + r.updated) + ' orders from Zentail'); loadData(true); })
      .catch(function(e) { showToast(e.message, 'error'); })
      .finally(function() { setSyncing(false); });
  };

  var selectedIds = Object.keys(selected).filter(function(k) { return selected[k]; }).map(Number);
  var selCount = selectedIds.length;

  var toggleOne = function(id, shiftKey) {
    if (shiftKey) window.getSelection().removeAllRanges();
    var next = Object.assign({}, selected);
    if (shiftKey && lastCheckedRef.current !== null) {
      var visibleData = sortedDataRef.current || filtered;
      var lastIdx = visibleData.findIndex(function(o) { return o.product_id === lastCheckedRef.current; });
      var curIdx = visibleData.findIndex(function(o) { return o.product_id === id; });
      if (lastIdx !== -1 && curIdx !== -1) {
        var start = Math.min(lastIdx, curIdx);
        var end = Math.max(lastIdx, curIdx);
        var newVal = !selected[id];
        for (var i = start; i <= end; i++) { next[visibleData[i].product_id] = newVal; }
      }
    } else {
      next[id] = !next[id];
    }
    if (!next[id]) delete next[id];
    lastCheckedRef.current = id;
    setSelected(next);
  };

  var toggleAll = function() {
    if (selCount === filtered.length && filtered.length > 0) { setSelected({}); }
    else { var next = {}; filtered.forEach(function(f) { next[f.product_id] = true; }); setSelected(next); }
  };

  var addToPO = async function(products) {
    if (!products.length) return;
    setAddingToPO(true);
    try {
      var items = products.filter(function(p) { return p.reorder_qty > 0; }).map(function(p) { return { sku: p.sku, qty: p.reorder_qty, raw_reorder_qty: p.raw_reorder_qty || null, pct_of_case: p.pct_of_case || null }; });
      if (!items.length) { showToast('No items with reorder qty > 0', 'warning'); setAddingToPO(false); return; }
      var result = await api('/analysis/forecast/add-to-po', { method: 'POST', body: JSON.stringify({ items: items }) });
      setAddToPOResult(result);
      setSelected({});
      loadData(true, true);
    } catch (e) { showToast(e.message, 'error'); }
    setAddingToPO(false);
  };

  if (loading) return React.createElement('div', { style: { padding: 40, textAlign: 'center', color: C.textMuted } }, React.createElement(LIcon, { name: 'loader-2', size: 24, spin: true }), ' Loading forecast data...');
  if (!data && loadError) return React.createElement('div', { style: { padding: 40, textAlign: 'center' } },
    React.createElement('div', { style: { color: C.danger, marginBottom: 12 } }, 'Could Not Load the Forecast: ' + loadError),
    React.createElement(Btn, { size: 'sm', onClick: function() { loadData(true); } }, 'Retry'));
  if (!data) return React.createElement('div', { style: { padding: 40, color: C.textMuted } }, 'No Data');

  var summary = data.summary || {};
  var forecast = data.forecast || [];

  // Get unique vendors and warehouses for filters
  var reorderFreq = summary.reorder_frequency_days || 14;
  var overDays = summary.overstock_days || 60;

  // First pass: filter by status tab only
  var statusFiltered = forecast.filter(function(f) {
    if (filter === 'reorder') return f.daily_velocity > 0 && f.reorder_qty > 0 && (f.days_to_order !== null && f.days_to_order <= reorderFreq || (f.on_hand === 0 && f.total_incoming === 0));
    if (filter === 'ordered') return !!f.on_active_po;
    if (filter === 'stockout') return f.daily_velocity > 0 && f.on_hand === 0 && f.total_incoming === 0;
    if (filter === 'low') return f.daily_velocity > 0 && f.days_to_order !== null && f.days_to_order <= 0 && !(f.on_hand === 0 && f.total_incoming === 0);
    if (filter === 'healthy') return f.daily_velocity > 0 && f.days_to_order !== null && f.days_to_order > 0 && (f.days_on_hand - f.lead_time_days) <= overDays;
    if (filter === 'overstock') return f.daily_velocity > 0 && (f.days_on_hand - f.lead_time_days) > overDays;
    if (filter === 'no-sales') return f.daily_velocity === 0 && !f.on_active_po && f.on_hand === 0;
    if (filter === 'below-case') return f.below_case_threshold;
    return true;
  });

  // Derive vendor/warehouse/payment-terms options from status-filtered data
  var vendors = [];
  var warehouses = [];
  var paymentTermsList = [];
  var vendorSet = {};
  var whSet = {};
  var ptSet = {};
  statusFiltered.forEach(function(f) {
    if (f.vendor_name && !vendorSet[f.vendor_name]) { vendorSet[f.vendor_name] = true; vendors.push(f.vendor_name); }
    if (f.warehouse && !whSet[f.warehouse]) { whSet[f.warehouse] = true; warehouses.push(f.warehouse); }
    if (f.vendor_payment_terms && !ptSet[f.vendor_payment_terms]) { ptSet[f.vendor_payment_terms] = true; paymentTermsList.push(f.vendor_payment_terms); }
  });
  vendors.sort();
  warehouses.sort();
  paymentTermsList.sort();

  // Reset dropdowns if current selection not in filtered options
  if (vendorFilter && !vendorSet[vendorFilter]) setVendorFilter('');
  if (warehouseFilter && !whSet[warehouseFilter]) setWarehouseFilter('');
  if (paymentTermsFilter && !ptSet[paymentTermsFilter]) setPaymentTermsFilter('');

  // Second pass: apply search + vendor + warehouse + payment terms
  var filtered = statusFiltered.filter(function(f) {
    if (search) {
      var s = search.toLowerCase();
      if (!((f.sku || '').toLowerCase().includes(s) || (f.vendor_title || '').toLowerCase().includes(s) || (f.asin || '').toLowerCase().includes(s) || (f.vendor_name || '').toLowerCase().includes(s))) return false;
    }
    if (vendorFilter && f.vendor_name !== vendorFilter) return false;
    if (warehouseFilter && f.warehouse !== warehouseFilter) return false;
    if (paymentTermsFilter && f.vendor_payment_terms !== paymentTermsFilter) return false;
    return true;
  });

  // Compute sort helper fields so computed/object columns are sortable
  filtered = filtered.map(function(f) {
    var tlc = (f.reorder_qty && f.unit_cost) ? (Number(f.reorder_qty) * Number(f.unit_cost)) : 0;
    var yoySort = 0;
    if (f.yoy) {
      var ly = Number(f.yoy.sold_30d_ly) || 0;
      var cur = Number(f.sold_30d) || 0;
      if (ly > 0) yoySort = ((cur - ly) / ly) * 100;
      else if (cur > 0) yoySort = 99999; // "NEW" — sort highest
      else yoySort = -99999; // no data either direction — sort lowest
    } else {
      yoySort = -99999;
    }
    return Object.assign({}, f, { total_line_cost: tlc, yoy_sort: yoySort });
  });

  // Status badge helper
  var stockBadge = function(f) {
    if (f.daily_velocity === 0) return React.createElement('span', { style: { background: 'rgba(139,143,163,0.12)', color: '#8B8FA3', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600 } }, 'NO SALES');
    if (f.on_hand === 0 && f.total_incoming === 0) return React.createElement('span', { style: { background: 'rgba(248,113,113,0.15)', color: '#F87171', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600 } }, 'STOCKOUT');
    if (f.days_to_order !== null && f.days_to_order <= 0) return React.createElement('span', { style: { background: 'rgba(251,191,36,0.15)', color: '#FBBF24', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600 } }, 'LOW');
    var dohMinusLead = f.days_on_hand - f.lead_time_days;
    if (dohMinusLead > overDays) return React.createElement('span', { style: { background: 'rgba(99,102,241,0.12)', color: '#6366F1', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600 } }, 'OVERSTOCK');
    return React.createElement('span', { style: { background: 'rgba(52,211,153,0.15)', color: '#34D399', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600 } }, 'HEALTHY');
  };

  var summaryCards = [
    { label: 'SKUs w/ Sales', value: summary.skus_with_sales || 0, icon: 'trending-up', color: '#3B82F6' },
    { label: 'Stockouts', value: summary.stockouts || 0, icon: 'alert-triangle', color: '#F87171' },
    { label: 'Low Stock', value: summary.low_stock || 0, icon: 'alert-circle', color: '#FBBF24' },
    { label: 'Healthy', value: summary.healthy_stock || 0, icon: 'check-circle', color: '#34D399' },
    { label: 'Overstock', value: summary.overstock || 0, icon: 'archive', color: '#6366F1' },
  ];

  var columns = [
    { key: '_select', label: '', locked: true, stopClick: true, noFilter: true, style: { width: 44 },
      headerRender: function() {
        var allChecked = filtered.length > 0 && filtered.every(function(f) { return selected[f.product_id]; });
        var someChecked = !allChecked && filtered.some(function(f) { return selected[f.product_id]; });
        return React.createElement('div', {
          onClick: function(e) { e.stopPropagation(); toggleAll(); },
          style: { width: 32, height: 28, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', userSelect: 'none' }
        },
          React.createElement('div', {
            style: { width: 20, height: 20, borderRadius: 4, border: '2px solid ' + (allChecked || someChecked ? C.accent : C.textDim),
              background: allChecked ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }
          },
            allChecked && React.createElement(LIcon, { name: 'check', size: 13, color: '#fff' }),
            someChecked && !allChecked && React.createElement('span', { style: { color: C.accent, fontSize: 13, fontWeight: 700, lineHeight: 1 } }, '\u2013')
          )
        );
      },
      render: function(r) {
        var checked = !!selected[r.product_id];
        return React.createElement('div', {
          onMouseDown: function(e) { if (e.shiftKey) e.preventDefault(); },
          onClick: function(e) { e.stopPropagation(); toggleOne(r.product_id, e.shiftKey); },
          style: { width: 32, height: 28, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', userSelect: 'none' }
        },
          React.createElement('div', {
            style: { width: 20, height: 20, borderRadius: 4, border: '2px solid ' + (checked ? C.accent : C.textDim),
              background: checked ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }
          },
            checked && React.createElement(LIcon, { name: 'check', size: 13, color: '#fff' })
          )
        );
      }
    },
    { key: 'sku', label: 'SKU', copyable: true, render: function(r) {
      return React.createElement('div', null,
        React.createElement('span', { onClick: function(e) { e.stopPropagation(); if (r.product_id) onNavigate('products', r.sku || r.product_id, { returnTo: 'forecasting' }); }, style: { fontFamily: 'inherit', fontWeight: 600, fontSize: 11, color: C.accent, cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: '3px' } }, r.sku),
        r.on_active_po && React.createElement('div', { style: { marginTop: 2 } },
          r.on_active_po.map(function(po) {
            return React.createElement(Tooltip, { key: po.po_number, text: po.qty + ' units on PO #' + po.po_number + ' (' + po.status + ')', style: { padding: '1px 5px', borderRadius: 99, fontSize: 9, fontWeight: 600, background: '#3B82F620', color: '#3B82F6', marginRight: 3, whiteSpace: 'nowrap' } }, 'PO#' + po.po_number);
          })
        )
      );
    } },
    { key: 'vendor_title', label: 'Title', width: 180, wrap: true, render: function(r) { return React.createElement(Tooltip, { text: r.vendor_title || '', style: { fontSize: 11, lineHeight: '1.3' } }, r.vendor_title || ''); } },
    { key: 'vendor_name', label: 'Vendor', copyable: true, render: function(r) { return React.createElement('span', { style: { fontSize: 11 } }, r.vendor_name || ''); } },
    { key: 'warehouse', label: 'WH', render: function(r) { return React.createElement('span', { style: { fontSize: 10, fontFamily: 'inherit' } }, r.warehouse || ''); } },
    { key: 'status', label: 'Status', sortKey: 'status_rank', render: function(r) { return stockBadge(r); } },
    { key: 'daily_velocity', label: 'Units/Day', render: function(r) {
      var v = r.daily_velocity || 0;
      return React.createElement('span', { style: { fontWeight: 600, fontSize: 12, color: v > 0 ? C.text : C.textMuted } }, v > 0 ? v.toFixed(1) : '—');
    }},
    { key: 'sold_30d', label: 'Sold (30D)', render: function(r) { var s = r.sold_30d || 0; return React.createElement('span', { style: { fontSize: 12, color: s > 0 ? C.text : C.textMuted } }, s > 0 ? s : '—'); } },
    { key: 'sold_90d', label: 'Sold (90D)', render: function(r) { var s = r.sold_90d || 0; return React.createElement('span', { style: { fontSize: 12, color: s > 0 ? C.text : C.textMuted } }, s > 0 ? s : '—'); } },
    { key: 'sold_180d', label: 'Sold (180D)', render: function(r) { var s = r.sold_180d || 0; return React.createElement('span', { style: { fontSize: 12, color: s > 0 ? C.text : C.textMuted } }, s > 0 ? s : '—'); } },
    { key: 'sold_365d', label: 'Sold (365D)', render: function(r) { var s = r.sold_365d || 0; return React.createElement('span', { style: { fontSize: 12, color: s > 0 ? C.text : C.textMuted } }, s > 0 ? s : '—'); } },
    { key: 'instock_days', label: 'In-Stock Days (30D)', headerRender: function() { return React.createElement(Tooltip, { text: 'Number of days the item was in stock within the last 30 days. Velocity is only calculated using in-stock days, so stockouts don\'t dilute the sell rate.' }, 'In-Stock (30D)'); }, render: function(r) {
      var isd = r.instock_30d || 0;
      var tracked = r.tracked_30d || 30;
      var oos = Math.max(0, tracked - isd);
      var pct = tracked > 0 ? Math.round((isd / tracked) * 100) : 0;
      var color = tracked < 3 ? C.textMuted : pct >= 90 ? '#34D399' : pct >= 50 ? '#FBBF24' : '#F87171';
      var tip = isd + ' of ' + tracked + ' tracked days in stock (' + pct + '%)';
      if (oos > 0) tip += '\n' + oos + ' days out of stock';
      return React.createElement(Tooltip, { text: tip, style: { fontSize: 12, color: color, fontWeight: 600 } }, isd + '/' + tracked);
    }},
    { key: 'on_hand', label: 'On Hand', render: function(r) {
      return React.createElement('span', { style: { fontWeight: 600, fontSize: 12, color: r.on_hand === 0 && r.daily_velocity > 0 ? '#F87171' : C.text } }, r.on_hand);
    }},
    { key: 'po_incoming', label: 'PO Incoming', render: function(r) {
      if (!r.po_incoming) return React.createElement('span', { style: { color: C.textMuted, fontSize: 12 } }, '—');
      var statusParts = [];
      Object.keys(r.po_by_status || {}).forEach(function(st) {
        if (r.po_by_status[st] > 0) statusParts.push(st + ': ' + r.po_by_status[st]);
      });
      return React.createElement(Tooltip, { text: statusParts.join(', '), style: { fontSize: 12, color: '#3B82F6' } }, '+' + r.po_incoming);
    }},
    { key: 'fba_inbound', label: 'FBA Inbound', render: function(r) {
      var total = (r.fba_inbound || 0) + (r.amz_inbound || 0);
      if (!total) return React.createElement('span', { style: { color: C.textMuted, fontSize: 12 } }, '—');
      var parts = [];
      if (r.fba_inbound) parts.push(r.fba_inbound + ' packing');
      if (r.amz_inbound) parts.push(r.amz_inbound + ' inbound');
      return React.createElement(Tooltip, { text: parts.join(', '), style: { fontSize: 12, color: '#A78BFA' } }, '+' + total);
    }},
    { key: 'wfs_inbound', label: 'WFS Inbound', render: function(r) {
      var total = r.wfs_inbound || 0;
      if (!total) return React.createElement('span', { style: { color: C.textMuted, fontSize: 12 } }, '—');
      var parts = [];
      if (r.wfs_by_status) Object.keys(r.wfs_by_status).forEach(function(s) { parts.push(r.wfs_by_status[s] + ' ' + s); });
      return React.createElement(Tooltip, { text: parts.join(', '), style: { fontSize: 12, color: '#F59E0B' } }, '+' + total);
    }},
    // MELI inbound (server 4e): units packed for Mercado Libre Full that Full has not received yet
    { key: 'meli_inbound', label: 'MELI Inbound', render: function(r) {
      var total = r.meli_inbound || 0;
      if (!total) return React.createElement('span', { style: { color: C.textMuted, fontSize: 12 } }, '—');
      var parts = [];
      if (r.meli_by_status) Object.keys(r.meli_by_status).forEach(function(s) { parts.push(r.meli_by_status[s] + ' ' + s); });
      return React.createElement(Tooltip, { text: parts.join(', '), style: { fontSize: 12, color: '#FDE047' } }, '+' + total);
    }},
    { key: 'days_on_hand', label: 'Days On Hand', headerRender: function() { return React.createElement(Tooltip, { text: 'How many days current on-hand inventory will last at the current sell rate' }, 'Days On Hand'); }, render: function(r) {
      if (r.daily_velocity === 0) return React.createElement('span', { style: { color: C.textMuted, fontSize: 12 } }, '—');
      var d = r.days_on_hand;
      var color = d === 0 ? '#F87171' : d <= r.lead_time_days ? '#FBBF24' : d <= overDays ? '#34D399' : '#6366F1';
      var tip = r.on_hand + ' on hand ÷ ' + r.daily_velocity.toFixed(2) + ' units/day = ' + d + ' days';
      return React.createElement(Tooltip, { text: tip, style: { fontWeight: 700, fontSize: 13, color: color } }, d >= 999 ? '999+' : d);
    }},
    { key: 'days_of_stock', label: 'Days Total', headerRender: function() { return React.createElement(Tooltip, { text: 'How many days total supply (on-hand + PO incoming + FBA inbound + WFS inbound + MELI inbound) will last at the current sell rate' }, 'Days Total'); }, render: function(r) {
      if (r.daily_velocity === 0) return React.createElement('span', { style: { color: C.textMuted, fontSize: 12 } }, '—');
      var d = r.days_of_stock;
      var fbaTotal = (r.fba_inbound || 0) + (r.amz_inbound || 0);
      var wfsTotal = r.wfs_inbound || 0;
      var meliTotal = r.meli_inbound || 0;
      var supply = r.on_hand + ' on hand + ' + (r.po_incoming || 0) + ' PO + ' + fbaTotal + ' FBA' + (wfsTotal ? ' + ' + wfsTotal + ' WFS' : '') + (meliTotal ? ' + ' + meliTotal + ' MELI' : '') + ' = ' + r.total_stock + ' total';
      var tip = supply + '\n' + r.total_stock + ' ÷ ' + r.daily_velocity.toFixed(2) + ' units/day = ' + d + ' days';
      return React.createElement(Tooltip, { text: tip, style: { fontSize: 12, color: C.text } }, d >= 999 ? '999+' : d);
    }},
    { key: 'safety_stock', label: 'Safety Stock', headerRender: function() { return React.createElement(Tooltip, { text: 'Buffer stock to prevent stockouts during demand variability. Formula: z-score \u00d7 \u03c3(daily demand) \u00d7 \u221a(lead time)' }, 'Safety Stock'); }, render: function(r) {
      if (!r.safety_stock) return React.createElement('span', { style: { color: C.textMuted, fontSize: 12 } }, '\u2014');
      var tip = 'Safety stock: ' + r.safety_stock + ' units\n\u03c3(demand) = ' + (r.demand_stddev || 0).toFixed(2) + ' units/day\nLead time = ' + r.lead_time_days + ' days';
      return React.createElement(Tooltip, { text: tip, style: { fontSize: 12, color: '#A78BFA' } }, r.safety_stock);
    }},
    { key: 'reorder_qty', label: 'Reorder Qty', headerRender: function() { return React.createElement(Tooltip, { text: 'Reorder Qty = max(0, Velocity \u00d7 (Lead Time + Reorder Frequency) + Safety Stock \u2212 Incoming).\nIncoming includes active POs (draft \u2192 received) + FBA/WFS/MELI inbound.' }, 'Reorder Qty'); }, render: function(r) {
      var reorderFreqDays = summary.reorder_frequency_days || 14;
      var velocity = r.daily_velocity || 0;
      var incoming = r.total_incoming || 0;
      var rqty = r.reorder_qty || 0;
      var methodLabel = r.reorder_method === '30d_sold' ? '30-Day Sell Rate (low in-stock)' : r.reorder_method === 'avg_gross' ? 'Averaged Gross Velocity (low in-stock)' : r.reorder_method === '90d_sold' ? '90-Day Sell Rate (low in-stock)' : r.reorder_method === '180d_sold' ? '180-Day Sell Rate (low in-stock)' : r.reorder_method === '365d_sold' ? '365-Day Sell Rate (low in-stock)' : r.reorder_method === 'all_time' ? 'All-Time Velocity' : r.reorder_method === 'blended' ? 'Blended' : 'Best Window';
      var target = velocity > 0 ? Math.ceil(velocity * (r.lead_time_days + reorderFreqDays) + (r.safety_stock || 0)) : 0;
      var tip = 'Method: ' + methodLabel + '\n';
      tip += velocity.toFixed(2) + ' units/day \u00d7 (' + r.lead_time_days + 'd lead + ' + reorderFreqDays + 'd reorder freq)';
      if (r.safety_stock > 0) tip += ' + ' + r.safety_stock + ' safety stock';
      tip += ' = ' + target + ' target';
      var onHand = r.on_hand || 0;
      var gapForRounding = incoming > 0 ? Math.max(0, target - incoming) : target;
      var roundedCases = (r.case_qty > 1) ? Math.round(gapForRounding / r.case_qty) : 0;
      var wasForcedToOneCase = (r.case_qty > 1) && (roundedCases === 0) && (rqty > 0); // forced because on_hand + incoming === 0
      if (incoming > 0) {
        tip += '\n\u2212 ' + incoming + ' incoming\n= ' + gapForRounding;
        if (rqty === 0) {
          if (gapForRounding === 0) tip += ' (fully covered by inbound)';
          else if (r.case_qty > 1) tip += '\n\u2192 0 cases (gap < \u00bd of case pack ' + r.case_qty + ')';
        } else if (wasForcedToOneCase) {
          tip += '\n\u2192 1 case = ' + rqty + ' (forced \u2014 no stock or incoming)';
        } else if (r.case_qty > 1) {
          tip += '\n\u2192 rounded to ' + rqty + ' (nearest case pack of ' + r.case_qty + ')';
        }
      } else {
        tip += '\n= ' + rqty;
        if (rqty === 0 && velocity === 0) tip += ' (no sales)';
        else if (rqty === 0 && r.case_qty > 1) tip += ' (gap < \u00bd of case pack ' + r.case_qty + ')';
        else if (wasForcedToOneCase) tip += '\n\u2192 1 case = ' + rqty + ' (forced \u2014 no stock or incoming)';
        else if (rqty > 0 && r.case_qty > 1) tip += '\n\u2192 rounded to ' + rqty + ' (nearest case pack of ' + r.case_qty + ')';
      }
      // Per-SKU max units per PO (products.max_po_qty): not a clamp. Auto-fill / Add to PO
      // split the qty across several draft POs (services/po-distribute.js).
      if (r.max_po_cap && rqty > r.max_po_cap) {
        tip += '\n\u2192 max ' + r.max_po_qty + ' units per PO' + (r.max_po_cap !== r.max_po_qty ? ' (' + r.max_po_cap + ' in whole cases)' : '') + ': splits into ' + Math.ceil(rqty / r.max_po_cap) + ' draft POs';
      } else if (r.max_po_qty && rqty > 0) {
        tip += '\nMax per PO: ' + r.max_po_qty;
      }
      var color = rqty > 0 ? '#F59E0B' : C.textMuted;
      var weight = rqty > 0 ? 600 : 400;
      return React.createElement(Tooltip, { text: tip, style: { fontWeight: weight, fontSize: 12, color: color, cursor: 'default' } }, rqty);
    }},
    { key: 'total_line_cost', label: 'Total Line Cost', render: function(r) {
      if (!r.reorder_qty || !r.unit_cost) return React.createElement('span', { style: { color: C.textMuted, fontSize: 12 } }, '\u2014');
      var total = r.reorder_qty * Number(r.unit_cost);
      return React.createElement(Tooltip, { text: r.reorder_qty + ' units \u00d7 $' + Number(r.unit_cost).toFixed(2) + ' = $' + total.toFixed(2), style: { fontWeight: 600, fontSize: 12, color: C.text } }, '$' + total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    }},
    { key: 'pct_of_case', label: '% of Case', render: function(r) {
      if (r.pct_of_case === null || r.pct_of_case === undefined) return React.createElement('span', { style: { color: C.textMuted, fontSize: 12 } }, '\u2014');
      var pct = r.pct_of_case;
      if (pct === 0) return React.createElement(Tooltip, { text: 'No forecast history \u2014 seeded as No-Sales SKU (case pack / MOQ)', style: { fontSize: 12, color: C.textMuted, fontStyle: 'italic' } }, '0%');
      var color = r.below_case_threshold ? '#F472B6' : pct >= 100 ? '#34D399' : '#FBBF24';
      var tip = 'Velocity-based target ' + (r.target_qty || 0) + ' units \u00f7 Case Pack ' + r.case_qty + ' = ' + pct + '%';
      if (r.below_case_threshold) tip += '\n\u26a0 Below ' + (summary.case_pack_threshold_pct || 50) + '% threshold (ordering 1 case would over-order)';
      return React.createElement(Tooltip, { text: tip, style: { fontSize: 12, fontWeight: r.below_case_threshold ? 700 : 400, color: color } }, pct + '%');
    }},
    { key: 'days_to_order', label: 'Days To Order', render: function(r) {
      if (r.days_to_order === null || r.days_to_order === undefined) return React.createElement('span', { style: { color: C.textMuted, fontSize: 12 } }, '\u2014');
      var d = r.days_to_order;
      var color = d <= 0 ? '#F87171' : d <= 7 ? '#FBBF24' : d <= 21 ? '#34D399' : '#6366F1';
      return React.createElement('span', { style: { fontWeight: 700, fontSize: 13, color: color } }, d);
    }},
    { key: 'actual_lead_time', label: 'Actual Lead Time', defaultVisible: false, headerRender: function() { return React.createElement(Tooltip, { text: 'Average days from Submitted to Checked-In across vendor PO history' }, 'Actual Lead Time'); }, render: function(r) {
      if (r.actual_lead_time == null) return React.createElement('span', { style: { color: C.textMuted, fontSize: 12 } }, '\u2014');
      var tip = 'Avg ' + r.actual_lead_time + ' days (based on ' + r.actual_lead_time_po_count + ' PO' + (r.actual_lead_time_po_count !== 1 ? 's' : '') + ')';
      var color = C.text;
      if (r.lead_time_days && r.actual_lead_time > r.lead_time_days) color = '#FBBF24';
      return React.createElement(Tooltip, { text: tip, style: { fontWeight: 600, fontSize: 12, color: color } }, r.actual_lead_time + 'd');
    }},
    { key: 'yoy', label: 'YoY', sortKey: 'yoy_sort', headerRender: function() { return React.createElement(Tooltip, { text: 'Year-over-year comparison: last 30 days sales vs same calendar period last year' }, 'YoY'); }, render: function(r) {
      if (!r.yoy) return React.createElement('span', { style: { color: C.textMuted, fontSize: 12 } }, '\u2014');
      var ly = r.yoy.sold_30d_ly || 0;
      var current = r.sold_30d || 0;
      if (ly === 0 && current === 0) return React.createElement('span', { style: { color: C.textMuted, fontSize: 12 } }, '\u2014');
      if (ly === 0) return React.createElement(Tooltip, { text: 'No sales last year in same period. This year: ' + current, style: { fontSize: 11, color: '#34D399', fontWeight: 600 } }, '\u2191 NEW');
      var pct = Math.round(((current - ly) / ly) * 100);
      var color = pct > 0 ? '#34D399' : pct < 0 ? '#F87171' : C.textMuted;
      var arrow = pct > 0 ? '\u2191' : pct < 0 ? '\u2193' : '';
      var tip = 'This period (30D): ' + current + ' sold\nLast year same period: ' + ly + ' sold\nChange: ' + (pct >= 0 ? '+' : '') + pct + '%';
      return React.createElement(Tooltip, { text: tip, style: { fontSize: 11, color: color, fontWeight: 600 } }, arrow + ' ' + (pct >= 0 ? '+' : '') + pct + '%');
    }},
  ];

  var getStatusText = function(f) {
    if (f.on_active_po) return 'Ordered';
    if (f.daily_velocity === 0) return 'No Sales';
    if (f.on_hand === 0) return 'Stockout';
    if (f.days_to_order !== null && f.days_to_order <= 0) return 'Low Stock';
    if ((f.days_on_hand - f.lead_time_days) > overDays) return 'Overstock';
    return 'Healthy';
  };

  var filterLabels = { all: 'All', reorder: 'Reorder_Now', ordered: 'Ordered', low: 'Low_Stock', stockout: 'Stockout', healthy: 'Healthy', overstock: 'Overstock', 'no-sales': 'No_Sales' };

  var exportForecasting = function() {
    if (!filtered.length) return;
    var rows = filtered.map(function(r) {
      var v = r.daily_velocity || 0;
      var ly = r.yoy ? (r.yoy.sold_30d_ly || 0) : 0;
      var current = r.sold_30d || 0;
      var yoyPct = ly > 0 ? Math.round(((current - ly) / ly) * 100) : (current > 0 ? 'NEW' : '');
      var row = {};
      row['SKU'] = r.sku || '';
      row['Title'] = r.vendor_title || '';
      row['Vendor'] = r.vendor_name || '';
      row['Warehouse'] = r.warehouse || '';
      row['Status'] = getStatusText(r);
      row['On PO'] = r.on_active_po ? r.on_active_po.map(function(po) { return 'PO#' + po.po_number + ' (' + po.qty + ')'; }).join(', ') : '';
      row['Units/Day'] = v > 0 ? Math.round(v * 100) / 100 : 0;
      row['Sold (30D)'] = r.sold_30d || 0;
      row['Sold (90D)'] = r.sold_90d || 0;
      row['Sold (180D)'] = r.sold_180d || 0;
      row['Sold (365D)'] = r.sold_365d || 0;
      row['In-Stock (30D)'] = (r.instock_30d || 0) + '/' + (r.tracked_30d || 30);
      row['On Hand'] = r.on_hand || 0;
      row['PO Incoming'] = r.po_incoming || 0;
      row['FBA Inbound'] = (r.fba_inbound || 0) + (r.amz_inbound || 0);
      row['WFS Inbound'] = r.wfs_inbound || 0;
      row['MELI Inbound'] = r.meli_inbound || 0;
      row['Days On Hand'] =r.daily_velocity > 0 ? (r.days_on_hand >= 999 ? '999+' : r.days_on_hand) : '';
      row['Days Total'] = r.daily_velocity > 0 ? (r.days_of_stock >= 999 ? '999+' : r.days_of_stock) : '';
      row['Safety Stock'] = r.safety_stock || 0;
      row['Reorder Qty'] = r.reorder_qty || 0;
      row['Total Line Cost'] = r.reorder_qty && r.unit_cost ? (r.reorder_qty * Number(r.unit_cost)).toFixed(2) : '';
      row['Days To Order'] = r.days_to_order != null ? r.days_to_order : '';
      row['YoY %'] = yoyPct !== '' ? (typeof yoyPct === 'number' ? (yoyPct >= 0 ? '+' : '') + yoyPct + '%' : yoyPct) : '';
      row['Returns 365d'] = r.returned_365d || 0;
      row['Return Rate %'] = r.return_rate_pct != null ? r.return_rate_pct : '';
      return row;
    });
    var ws = XLSX.utils.json_to_sheet(rows);
    // Auto-width columns
    var colWidths = Object.keys(rows[0]).map(function(k) {
      var max = k.length;
      rows.forEach(function(r) { var len = String(r[k]).length; if (len > max) max = len; });
      return { wch: Math.min(max + 2, 40) };
    });
    ws['!cols'] = colWidths;
    var wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Forecast');
    XLSX.writeFile(wb, 'Forecast_' + (filterLabels[filter] || 'All') + '_' + new Date().toISOString().slice(0, 10) + '.xlsx');
  };

  var orderedCount = forecast.filter(function(f) { return !!f.on_active_po; }).length;
  var reorderCount = forecast.filter(function(f) { return f.daily_velocity > 0 && f.reorder_qty > 0 && (f.days_to_order !== null && f.days_to_order <= reorderFreq || (f.on_hand === 0 && f.total_incoming === 0)); }).length;
  var filterTabs = [
    { key: 'all', label: 'All (' + forecast.length + ')' },
    { key: 'reorder', label: 'Reorder Now (' + reorderCount + ')', color: '#EF4444' },
    { key: 'ordered', label: 'Ordered (' + orderedCount + ')', color: '#3B82F6' },
    { key: 'stockout', label: 'Stockout (' + (summary.stockouts || 0) + ')', color: '#F87171' },
    { key: 'low', label: 'Low Stock (' + (summary.low_stock || 0) + ')', color: '#FBBF24' },
    { key: 'healthy', label: 'Healthy (' + (summary.healthy_stock || 0) + ')', color: '#34D399' },
    { key: 'overstock', label: 'Overstock (' + (summary.overstock || 0) + ')', color: '#6366F1' },
    { key: 'no-sales', label: 'No Sales (' + forecast.filter(function(f) { return f.daily_velocity === 0 && !f.on_active_po && f.on_hand === 0; }).length + ')', color: '#8B8FA3' },
    { key: 'below-case', label: 'Below Case Pack (' + (summary.below_case_pack || 0) + ')', color: '#F472B6' },
  ];

  return React.createElement('div', { style: { animation: 'fadeIn 0.3s ease' } },
    // Header
    React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 } },
      React.createElement('div', null,
        React.createElement('h2', { style: { margin: 0, fontSize: 20, fontWeight: 700, color: C.text } }, 'Demand Forecasting'),
        React.createElement('p', { style: { margin: '4px 0 0', fontSize: 12, color: C.textMuted } }, 'Sales velocity, inventory levels & reorder suggestions based on Zentail orders')
      ),
      React.createElement('div', { style: { display: 'flex', gap: 8, flexWrap: 'wrap' } },
        selCount > 0 && React.createElement(Btn, { size: 'sm', variant: 'primary', onClick: function() { var prods = filtered.filter(function(f) { return selected[f.product_id]; }); addToPO(prods); }, disabled: addingToPO }, addingToPO ? [React.createElement(LIcon, { key: 'i', name: 'loader-2', size: 14, spin: true }), ' Adding...'] : [React.createElement(LIcon, { key: 'i', name: 'plus', size: 14 }), ' Add Selected (' + selCount + ') to PO']),
        React.createElement(Btn, { size: 'sm', onClick: async function() {
          // Adds every filtered SKU to draft POs in one click — confirm first (review L19)
          var n = filtered.filter(function(p) { return p.reorder_qty > 0; }).length;
          if (!n) { showToast('No items with reorder qty > 0', 'warning'); return; }
          if (!(await appConfirm('Add all ' + n + ' SKU(s) with a reorder quantity to draft POs?'))) return;
          addToPO(filtered);
        }, disabled: addingToPO }, addingToPO ? [React.createElement(LIcon, { key: 'i', name: 'loader-2', size: 14, spin: true }), ' Adding...'] : [React.createElement(LIcon, { key: 'i', name: 'plus-circle', size: 14 }), ' Add All (' + filtered.length + ') to PO']),
        React.createElement(Btn, { size: 'sm', onClick: function() { loadData(true); }, disabled: loading }, React.createElement(LIcon, { name: 'refresh-cw', size: 14 }), ' Refresh')
      )
    ),
    // Missing settings warning
    data.missing_settings && data.missing_settings.length > 0 && React.createElement('div', { style: { padding: '10px 16px', background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.3)', borderRadius: 8, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10 } },
      React.createElement(LIcon, { name: 'alert-triangle', size: 16, style: { color: '#FBBF24', flexShrink: 0 } }),
      React.createElement('span', { style: { fontSize: 12, color: '#FBBF24', fontWeight: 500 } }, 'Missing forecast settings: ', React.createElement('strong', null, data.missing_settings.join(', ')), '. Configure in Settings \u2192 Forecasting to get accurate results.')
    ),
    // Summary cards (rg-2x2: two per row on narrow widths)
    React.createElement('div', { className: 'rg-2x2', style: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 16 } },
      summaryCards.map(function(c) {
        return React.createElement(Card, { key: c.label, style: { padding: '14px 16px', textAlign: 'center' } },
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 4 } },
            React.createElement(LIcon, { name: c.icon, size: 16, style: { color: c.color } }),
            React.createElement('span', { style: { fontSize: 22, fontWeight: 700, color: c.color } }, c.value)
          ),
          React.createElement('div', { style: { fontSize: 10, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 } }, c.label)
        );
      })
    ),
    // Filter tabs
    React.createElement('div', { style: { display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' } },
      filterTabs.map(function(t) {
        var isActive = filter === t.key;
        return React.createElement('button', { key: t.key, onClick: function() { setFilter(t.key); },
          className: isActive ? '' : 'filter-pill',
          style: { padding: '5px 12px', borderRadius: 6, border: '1px solid transparent', cursor: 'pointer', fontSize: 11, fontWeight: 600,
            background: isActive ? (t.color || C.accent) : C.surface, color: isActive ? '#fff' : C.textMuted, transition: 'all 0.2s' }
        }, t.label);
      })
    ),
    // Search + filters row
    React.createElement('div', { style: { display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' } },
      React.createElement(SearchInput, { value: search, onChange: function(e) { setSearch(e.target.value); }, placeholder: 'Search SKU, title, ASIN, vendor...',
        style: { width: 260, fontSize: 12, background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '6px 10px', borderRadius: 6 } }),
      React.createElement('select', { value: vendorFilter, onChange: function(e) { setVendorFilter(e.target.value); },
        style: { width: 180, fontSize: 12, background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '6px 10px', borderRadius: 6 } },
        React.createElement('option', { value: '' }, 'All Vendors'),
        vendors.map(function(v) { return React.createElement('option', { key: v, value: v }, v); })
      ),
      React.createElement('select', { value: warehouseFilter, onChange: function(e) { setWarehouseFilter(e.target.value); },
        style: { width: 140, fontSize: 12, background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '6px 10px', borderRadius: 6 } },
        React.createElement('option', { value: '' }, 'All Warehouses'),
        warehouses.map(function(w) { return React.createElement('option', { key: w, value: w }, w); })
      ),
      React.createElement('select', { value: paymentTermsFilter, onChange: function(e) { setPaymentTermsFilter(e.target.value); },
        style: { width: 160, fontSize: 12, background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '6px 10px', borderRadius: 6 } },
        React.createElement('option', { value: '' }, 'All Payment Terms'),
        paymentTermsList.map(function(pt) { return React.createElement('option', { key: pt, value: pt }, pt); })
      ),
    ),
    // Data table
    React.createElement(DataTable, {
      columns: columns,
      data: filtered,
      storageKey: 'forecasting',
      defaultSort: 'days_on_hand',
      defaultSortDir: 'asc',
      pageSize: 50,
      initialPage: _forecastCache.page,
      onPageChange: function(p) { _forecastCache.page = p; },
      onSortedData: function(d) { sortedDataRef.current = d; },
      bulkActions: {
        alwaysShow: true,
        count: selCount,
        items: [
          { section: 'Actions' },
          { label: 'Add Selected to PO (' + selCount + ')', icon: 'plus', disabled: selCount === 0 || addingToPO, onClick: function() { var prods = filtered.filter(function(f) { return selected[f.product_id]; }); addToPO(prods); } },
          { section: 'Export' },
          { label: 'Export Report (' + (filterLabels[filter] || 'All').replace('_', ' ') + ')', icon: 'download', onClick: exportForecasting },
        ]
      }
    }),
    React.createElement(Modal, { open: !!addToPOResult, onClose: function() { setAddToPOResult(null); }, title: 'Purchase Orders Created', width: 560 },
      addToPOResult && React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 16 } },
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12 } },
          React.createElement('div', { style: { width: 44, height: 44, borderRadius: 10, background: C.successSoft, display: 'flex', alignItems: 'center', justifyContent: 'center' } }, React.createElement(LIcon, { name: 'circle-check', size: 24, color: C.success })),
          React.createElement('div', null,
            React.createElement('div', { style: { fontSize: 16, fontWeight: 600, color: C.text } }, (addToPOResult.pos || []).length + ' PO(s) ' + ((addToPOResult.pos || []).some(function(p) { return p.created; }) ? 'Created' : 'Updated')),
            React.createElement('div', { style: { fontSize: 12, color: C.textMuted } }, addToPOResult.added + ' items added from forecast')
          )
        ),
        addToPOResult.skipped && addToPOResult.skipped.length > 0 && React.createElement('div', { style: { fontSize: 12, color: C.warning, padding: '6px 10px', background: C.warningSoft, borderRadius: 6 } },
          addToPOResult.skipped.length + ' skipped (no vendor/warehouse): ' + addToPOResult.skipped.join(', ')
        ),
        // Per-SKU max units per PO: SKUs that were split across several draft POs
        addToPOResult.splits && addToPOResult.splits.length > 0 && React.createElement('div', { style: { fontSize: 12, color: C.warning, padding: '6px 10px', background: C.warningSoft, borderRadius: 6 } },
          'Split by max units per PO: ' + addToPOResult.splits.map(function(s) {
            return s.sku + ' → ' + s.pos + ' POs (' + s.units + ' units, max ' + s.cap + ' each)';
          }).join(', ')
        ),
        React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 8 } },
          (addToPOResult.pos || []).map(function(po) {
            return React.createElement('div', { key: po.po_id || po.po_number, onClick: function() { setAddToPOResult(null); onNavigate('po-detail', po.po_id); },
              style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: C.bg, borderRadius: 8, border: '1px solid ' + C.border, cursor: 'pointer', transition: 'border-color 0.15s' },
              onMouseEnter: function(e) { e.currentTarget.style.borderColor = C.accent; },
              onMouseLeave: function(e) { e.currentTarget.style.borderColor = C.border; }
            },
              React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
                React.createElement('span', { style: { fontWeight: 600, fontFamily: 'inherit', color: C.accent } }, 'PO #' + po.po_number),
                React.createElement(Badge, { status: po.status }),
                React.createElement('span', { style: { fontSize: 12, color: C.textMuted } }, po.vendor_name),
                React.createElement('span', { style: { fontSize: 11, color: C.textDim } }, '\u2192 ' + po.wh_code)
              ),
              React.createElement('div', { style: { textAlign: 'right', fontSize: 12 } },
                React.createElement('div', { style: { color: C.textMuted } }, po.sku_count + ' SKUs \u00b7 ' + (po.total_units || 0).toLocaleString() + ' units'),
                React.createElement('div', { style: { fontWeight: 600, color: C.text, fontSize: 14 } }, '$' + Number(po.total_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2 }))
              )
            );
          })
        ),
        React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 } },
          React.createElement(Btn, { onClick: function() { setAddToPOResult(null); } }, 'Close'),
          (addToPOResult.pos || []).length === 1 && React.createElement(Btn, { variant: 'primary', onClick: function() { setAddToPOResult(null); onNavigate('po-detail', addToPOResult.pos[0].po_id); } }, 'View PO')
        )
      )
    )
  );
}

// ─── Refunds on an order (Anthony 2026-09-23, to-do 12): full or partial by amount; issued on the
//     marketplace where an API exists (eBay) and always recorded on the order. Amazon has no refund
//     API: the panel links to Seller Central and records. Lives in the Order Details modal. ───
var ORDER_REFUND_REASONS = [['buyer_cancel', 'Buyer cancelled'], ['item_not_received', 'Item not received'], ['not_as_described', 'Item not as described'], ['damaged', 'Damaged'], ['other', 'Other']];
// Pure refund math (9-26): "Full" = order total minus what was already refunded, and no
// amount may exceed what remains. total <= 0 (unknown total) leaves the amount unchecked.
function orderRefundPlan(total, refunded, kind, amountStr) {
  var t = Number(total) || 0, done = Number(refunded) || 0;
  var remaining = Math.max(0, Math.round((t - done) * 100) / 100);
  var hasTotal = t > 0;
  var amt = kind === 'full' ? remaining : parseFloat(String(amountStr == null ? '' : amountStr).replace(/[$,\s]/g, ''));
  if (kind === 'full' && hasTotal && remaining <= 0) return { remaining: remaining, amount: 0, error: 'Nothing left to refund: the order total is already refunded.' };
  if (!(amt > 0)) return { remaining: remaining, amount: 0, error: 'Enter the amount to refund' };
  if (hasTotal && amt > remaining + 0.005) return { remaining: remaining, amount: amt, error: 'The amount is more than the remaining refundable ' + remaining.toFixed(2) + ' (order total ' + t.toFixed(2) + ' minus ' + done.toFixed(2) + ' already refunded).' };
  return { remaining: remaining, amount: Math.round(amt * 100) / 100, error: null };
}
window.orderRefundPlan = orderRefundPlan;
// The message for a refund POST that failed (9-26): 400 carries `refundable` (the balance left),
// 409 = a refund already in flight, status 'unknown' = eBay did not answer (it may have gone through).
function orderRefundErrorText(e, unknownMsg) {
  var b = (e && e.apiBody) || {};
  if (b.status === 'unknown' || (b.refund && b.refund.status === 'unknown')) return unknownMsg + (b.warning ? ': ' + b.warning : '');
  var msg = String((e && e.message) || e || 'Refund failed');
  if (e && e.apiStatus === 400 && b.refundable != null && !isNaN(Number(b.refundable))) msg += ' Refundable: $' + Number(b.refundable).toFixed(2) + '.';
  if (e && e.apiStatus === 409) msg += ' (refresh the order once it finishes)';
  return msg;
}
window.orderRefundErrorText = orderRefundErrorText;
// A refund row's status line (9-26): pending (in flight) and unknown (check eBay) are called out
function orderRefundStatusText(r) {
  var mp = r.marketplace === 'ebay' ? 'eBay' : (r.marketplace || 'the marketplace');
  if (r.status === 'issued') return 'Issued on ' + mp + (r.marketplace_refund_id ? ' · ' + r.marketplace_refund_id : '');
  if (r.status === 'failed') return 'Failed: ' + (r.error || '');
  if (r.status === 'pending') return 'Pending: sent to ' + mp + ', waiting for the answer';
  if (r.status === 'unknown') return 'Unknown: check ' + mp + ' before retrying' + (r.error ? ' · ' + r.error : '');
  return 'Recorded';
}
window.orderRefundStatusText = orderRefundStatusText;
function newIdempotencyKey() {
  try { if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID(); } catch (e) {}
  return 'rf-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
}
function OrderRefundsPanel(props) {
  var channel = props.channel, orderPk = props.orderPk, detail = props.detail || {}, raw = props.raw || {}, showToast = props.showToast;
  var [data, setData] = React.useState(null); // { refunds, capability, order }
  var [open, setOpen] = React.useState(false);
  var [busy, setBusy] = React.useState(false);
  var blank = { kind: 'full', amount: '', reason: 'buyer_cancel', note: '', issue: true };
  var [form, setForm] = React.useState(blank);
  var set = function(k, v) { setForm(function(p) { var n = Object.assign({}, p); n[k] = v; return n; }); };
  // A failed load (403 for a role without the right, 500, network) is shown as an error with a
  // Retry — never as "No refund on this order yet" (sweep 9-27 M10).
  var [loadError, setLoadError] = React.useState(null);
  var load = function() {
    return api('/integrations/' + channel + '/orders/' + orderPk + '/refunds')
      .then(function(d) { setLoadError(null); setData(d); })
      .catch(function(e) { setLoadError(String((e && e.message) || e || 'Request failed')); });
  };
  React.useEffect(function() { load(); }, [channel, orderPk]);
  var order = (data && data.order) || {};
  var total = parseFloat(order.total != null ? order.total : detail.order_total) || 0;
  var currency = order.currency || detail.currency || 'USD';
  var fmt = function(v, cur) { return (cur && cur !== 'USD' ? cur + ' ' : '$') + Number(v || 0).toFixed(2); };
  var refunds = (data && data.refunds) || [];
  var refunded = refunds.filter(function(r) { return r.status !== 'failed'; }).reduce(function(a, r) { return a + (parseFloat(r.amount) || 0); }, 0);
  var cap = data && data.capability;
  var sellerUrl = marketplaceOrderUrl(props.channelName || raw.channelName || channel, raw.channelOrderId || order.channel_order_id || order.order_id);
  var reasonLabel = function(k) { var r = ORDER_REFUND_REASONS.find(function(x) { return x[0] === k; }); return r ? r[1] : k; };
  var plan = orderRefundPlan(total, refunded, form.kind, form.amount);
  var remaining = plan.remaining;
  // One Idempotency-Key per opened form: a retry of the same form (double click, network
  // error) reuses it so the server can refuse a second marketplace refund.
  var idemKeyRef = React.useRef(null);
  var openForm = function() { idemKeyRef.current = newIdempotencyKey(); setOpen(true); };
  var closeForm = function() { idemKeyRef.current = null; setOpen(false); setForm(blank); };
  var unknownMsg = 'Result unknown — check eBay before retrying';
  var isUnknown = function(o) { return !!(o && (o.status === 'unknown' || (o.refund && o.refund.status === 'unknown'))); };
  // The last unknown outcome stays on the panel (a toast is easy to miss): the server's warning text
  var [unknownWarn, setUnknownWarn] = React.useState(null);
  var submit = async function() {
    if (busy) return;
    if (plan.error) { showToast(plan.error, 'error'); return; }
    var amt = plan.amount;
    var issue = !!(cap && cap.api && form.issue);
    if (!(await appConfirm((issue ? 'Issue a ' : 'Record a ') + (form.kind === 'full' ? 'full' : 'partial') + ' refund of ' + fmt(amt, currency) + (issue ? ' on ' + cap.label + ' now?' : ' on this order?' + (cap && cap.api ? '' : ' (nothing is sent to the marketplace)'))))) return;
    if (!idemKeyRef.current) idemKeyRef.current = newIdempotencyKey();
    setBusy(true);
    api('/integrations/' + channel + '/orders/' + orderPk + '/refund', { method: 'POST', headers: { 'Idempotency-Key': idemKeyRef.current }, body: JSON.stringify({ amount: amt, currency: currency, kind: form.kind, reason: form.reason, note: form.note, issue: issue }) })
      .then(function(r) {
        var st = r.refund && r.refund.status;
        if (isUnknown(r)) { setUnknownWarn(r.warning || unknownMsg); showToast(unknownMsg + (r.warning ? ': ' + r.warning : ''), 'error'); closeForm(); load(); return; }
        setUnknownWarn(null);
        if (st === 'failed') showToast(cap.label + ' refused the refund: ' + (r.refund.error || 'unknown error') + '. It was recorded as failed; nothing was refunded.', 'error');
        else showToast(st === 'issued' ? 'Refund of ' + fmt(r.refund.amount, r.refund.currency) + ' issued on ' + cap.label : 'Refund of ' + fmt(r.refund.amount, r.refund.currency) + ' recorded', 'success');
        closeForm(); load();
      })
      .catch(function(e) { showToast(orderRefundErrorText(e, unknownMsg), 'error'); if (isUnknown(e && e.apiBody)) setUnknownWarn((e.apiBody && e.apiBody.warning) || unknownMsg); if (e && (isUnknown(e.apiBody) || e.apiStatus === 400 || e.apiStatus === 409)) load(); })
      .then(function() { setBusy(false); });
  };
  var inp = function(p) { return React.createElement('input', Object.assign({ style: { background: C.bg, color: C.text, border: '1px solid ' + C.border, borderRadius: 6, padding: '7px 10px', fontSize: 13, fontFamily: 'inherit' } }, p)); };
  var lbl = function(t) { return React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginBottom: 4 } }, t); };
  return React.createElement('div', { style: { background: C.bg, borderRadius: 8, padding: '12px 16px', border: '1px solid ' + C.border + '44' } },
    React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
      React.createElement('div', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', flex: 1 } },
        'Refunds' + (refunded > 0 ? ' · ' + fmt(refunded, currency) + ' refunded' : '')),
      data && !open && React.createElement(Btn, { size: 'sm', variant: 'primary', onClick: openForm }, cap && cap.api ? 'Refund' : 'Record Refund')),
    unknownWarn && React.createElement('div', { style: { marginTop: 8, padding: '8px 10px', borderRadius: 6, background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.4)', color: '#FBBF24', fontSize: 12.5 } },
      unknownMsg + '. ' + unknownWarn),
    loadError ? React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, color: '#F87171', marginTop: 6 } },
        'Couldn\'t Load Refunds: ' + loadError,
        React.createElement(Btn, { size: 'sm', variant: 'ghost', onClick: function() { setLoadError(null); load(); } }, 'Retry'))
    : data === null ? React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 6 } }, 'Loading…')
      : !refunds.length && !open ? React.createElement('div', { style: { fontSize: 12, color: C.textDim, marginTop: 6 } }, 'No refund on this order yet')
      : refunds.map(function(r) {
          return React.createElement('div', { key: r.id, style: { display: 'flex', gap: 12, alignItems: 'center', fontSize: 12.5, padding: '5px 0', borderTop: '1px solid ' + C.border + '44', marginTop: 4 } },
            React.createElement('span', { style: { color: C.textMuted, minWidth: 130 } }, new Date(r.created_at).toLocaleString()),
            React.createElement('span', { style: { fontWeight: 600, color: r.status === 'failed' ? '#F87171' : C.text, minWidth: 90 } }, fmt(r.amount, r.currency)),
            React.createElement('span', { style: { color: C.text, minWidth: 60 } }, r.kind === 'full' ? 'Full' : 'Partial'),
            React.createElement('span', { style: { color: C.textMuted, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, reasonLabel(r.reason) + (r.note ? ' · ' + r.note : '')),
            (r.status === 'unknown' || r.status === 'pending') && React.createElement('span', { style: { fontSize: 10.5, fontWeight: 700, padding: '1px 7px', borderRadius: 999, whiteSpace: 'nowrap',
              background: r.status === 'unknown' ? 'rgba(251,191,36,0.15)' : C.accentSoft, color: r.status === 'unknown' ? '#FBBF24' : C.accent } }, r.status === 'unknown' ? 'Unknown' : 'Pending'),
            React.createElement('span', { title: orderRefundStatusText(r), style: { color: r.status === 'failed' ? '#F87171' : r.status === 'issued' ? C.success : r.status === 'unknown' ? '#FBBF24' : C.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 360 } },
              orderRefundStatusText(r)),
            React.createElement('span', { style: { color: C.textDim, whiteSpace: 'nowrap' } }, r.created_by_name || ''));
        }),
    open && React.createElement('div', { style: { marginTop: 10, paddingTop: 10, borderTop: '1px solid ' + C.border + '44' } },
      cap && !cap.api && React.createElement('div', { style: { fontSize: 12, color: '#FBBF24', marginBottom: 8 } }, cap.why, ' ',
        sellerUrl && React.createElement('a', { href: sellerUrl, target: '_blank', rel: 'noopener', style: { color: C.accent } }, 'Open the order on ' + cap.label)),
      React.createElement('div', { style: { display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' } },
        React.createElement('div', null, lbl('Refund'),
          React.createElement('div', { style: { display: 'flex', gap: 6 } },
            ['full', 'partial'].map(function(k) {
              var on = form.kind === k;
              var dis = k === 'full' && total > 0 && remaining <= 0;
              return React.createElement('button', { key: k, disabled: dis, title: dis ? 'Nothing left to refund' : undefined, onClick: function() { if (!dis) set('kind', k); }, style: { padding: '7px 12px', borderRadius: 6, border: '1px solid ' + (on ? C.accent : C.border), background: on ? C.accentSoft : 'transparent', color: on ? C.accent : C.text, fontSize: 13, cursor: dis ? 'not-allowed' : 'pointer', opacity: dis ? 0.45 : 1, fontFamily: 'inherit' } }, k === 'full' ? 'Full (' + fmt(remaining, currency) + ')' : 'Partial');
            })),
          total > 0 && React.createElement('div', { style: { fontSize: 11.5, color: C.textMuted, marginTop: 4 } }, 'Remaining Refundable: ' + fmt(remaining, currency))),
        form.kind === 'partial' && React.createElement('div', null, lbl('Amount (' + currency + ')'), inp({ value: form.amount, onChange: function(e) { set('amount', e.target.value); }, placeholder: '0.00', inputMode: 'decimal', style: { width: 110 } })),
        React.createElement('div', null, lbl('Reason'),
          React.createElement('select', { value: form.reason, onChange: function(e) { set('reason', e.target.value); }, style: { background: C.bg, color: C.text, border: '1px solid ' + C.border, borderRadius: 6, padding: '7px 10px', fontSize: 13, fontFamily: 'inherit', colorScheme: getThemeName() === 'light' ? 'light' : 'dark' } },
            ORDER_REFUND_REASONS.map(function(r) { return React.createElement('option', { key: r[0], value: r[0] }, r[1]); }))),
        React.createElement('div', { style: { flex: '1 1 220px' } }, lbl('Note (optional)'), inp({ value: form.note, onChange: function(e) { set('note', e.target.value); }, placeholder: cap && cap.api ? 'Goes to the buyer as the refund comment' : 'For our records', style: { width: '100%', boxSizing: 'border-box' } }))),
      React.createElement('div', { style: { display: 'flex', gap: 10, alignItems: 'center', marginTop: 10 } },
        cap && cap.api && React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: C.text, cursor: 'pointer' } },
          React.createElement('input', { type: 'checkbox', checked: form.issue, onChange: function(e) { set('issue', e.target.checked); } }), 'Issue on ' + cap.label + ' now'),
        React.createElement('span', { style: { flex: 1 } }),
        form.kind === 'partial' && form.amount !== '' && plan.error && React.createElement('span', { style: { fontSize: 12, color: '#F87171' } }, plan.error),
        React.createElement(Btn, { size: 'sm', variant: 'ghost', onClick: closeForm }, 'Cancel'),
        React.createElement(Btn, { size: 'sm', variant: 'primary', disabled: busy || !!plan.error, onClick: submit }, busy ? 'Working…' : (cap && cap.api && form.issue) ? 'Issue Refund' : 'Record Refund'))));
}
window.OrderRefundsPanel = OrderRefundsPanel;

// ─── ORDERS MODULE PAGE ───
// The marketplace orders list (Zentail relays, Amazon FBA, Mercado Libre) with the Order Details
// modal. The syncs run on their own schedules; this page only reads.
// Orders filters live in the page link (Anthony 2026-10-13): a refresh, a bookmark or a shared link
// brings the same list back. ?order=&channel= stays the open-one-order deep link.
function orderFilterFromUrl(key) {
  try { return new URLSearchParams(window.location.search).get(key) || ''; } catch (e) { return ''; }
}
// Date shortcuts (Anthony 2026-10-13), in Eastern time like the server's date filter
function orderDatePresets() {
  var parts = {};
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' })
      .formatToParts(new Date()).forEach(function(x) { parts[x.type] = x.value; });
  } catch (e) { var n = new Date(); parts = { year: n.getFullYear(), month: n.getMonth() + 1, day: n.getDate() }; }
  var y = Number(parts.year), m = Number(parts.month), dd = Number(parts.day);
  var day = function(yy, mm, d) { return new Date(Date.UTC(yy, mm - 1, d)).toISOString().slice(0, 10); }; // Date.UTC rolls days/months over
  var today = day(y, m, dd);
  return [
    { key: 'today', label: 'Today', from: today, to: today },
    { key: 'yesterday', label: 'Yesterday', from: day(y, m, dd - 1), to: day(y, m, dd - 1) },
    { key: 'last7', label: 'Last 7 days', from: day(y, m, dd - 6), to: today },
    { key: 'last30', label: 'Last 30 days', from: day(y, m, dd - 29), to: today },
    { key: 'thisMonth', label: 'This month', from: day(y, m, 1), to: today },
    { key: 'lastMonth', label: 'Last month', from: day(y, m - 1, 1), to: day(y, m, 0) },
  ];
}
// Channel = where the order was sold (server: services/order-search.js FACET_MARKETPLACES)
var ORDER_MARKETPLACE_OPTIONS = [['amazon', 'Amazon'], ['ebay', 'eBay'], ['walmart', 'Walmart'], ['shein', 'SHEIN'], ['temu', 'Temu'], ['faire', 'Faire'],
  ['jcpenney', 'JCPenney'], ['target', 'Target Plus'], ['shopify', 'Websites'], ['mercadolibre', 'Mercado Libre'], ['other', 'Other']];
var ORDER_FULFILLMENT_OPTIONS = ['FBA', 'FBM', 'WFS', 'Dropship', 'ML Full'];

function OrdersModulePage({ showToast }) {
  var [orders, setOrders] = useState([]);
  var [totalOrders, setTotalOrders] = useState(0);
  var [loading, setLoading] = useState(true);
  var [search, setSearch] = useState(function() { return orderFilterFromUrl('q'); });
  var [debouncedSearch, setDebouncedSearch] = useState(function() { return orderFilterFromUrl('q'); });
  var searchTimerRef = useRef(null);
  var [channelFilter, setChannelFilter] = useState(function() { return orderFilterFromUrl('integration'); });
  var [marketplaceFilter, setMarketplaceFilter] = useState(function() { return orderFilterFromUrl('mp'); });
  var [fulfillmentFilter, setFulfillmentFilter] = useState(function() { return orderFilterFromUrl('ful'); });
  var [statusFilter, setStatusFilter] = useState(function() { return orderFilterFromUrl('status'); });
  var [dateFrom, setDateFrom] = useState(function() { return orderFilterFromUrl('from'); });
  var [dateTo, setDateTo] = useState(function() { return orderFilterFromUrl('to'); });
  var [selectedOrder, setSelectedOrder] = useState(null);
  var [orderDetail, setOrderDetail] = useState(null);
  var [detailLoading, setDetailLoading] = useState(false);
  var lastFetchParamsRef = useRef(null);
  var fetchGenRef = useRef(0);

  var handleServerFetch = function(params) {
    lastFetchParamsRef.current = params;
    fetchGenRef.current++;
    var myGen = fetchGenRef.current;
    var qs = 'page=' + params.page + '&limit=' + params.pageSize;
    if (params.sortCol) qs += '&sort=' + params.sortCol + '&sort_dir=' + params.sortDir;
    if (debouncedSearch) qs += '&search=' + encodeURIComponent(debouncedSearch);
    if (channelFilter) qs += '&channel=' + channelFilter;
    if (marketplaceFilter) qs += '&marketplace=' + encodeURIComponent(marketplaceFilter);
    if (fulfillmentFilter) qs += '&fulfillment=' + encodeURIComponent(fulfillmentFilter);
    if (statusFilter) qs += '&status=' + statusFilter;
    if (dateFrom) qs += '&date_from=' + dateFrom;
    if (dateTo) qs += '&date_to=' + dateTo;
    api('/integrations/orders?' + qs).then(function(r) {
      if (myGen === fetchGenRef.current) { setOrders(r.rows || []); setTotalOrders(r.total || 0); setLoading(false); }
    }).catch(function(e) { showToast(e.message, 'error'); setLoading(false); });
  };

  var reloadOrders = function() {
    if (lastFetchParamsRef.current) handleServerFetch(lastFetchParamsRef.current);
  };

  var handleSearch = function(val) {
    setSearch(val);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(function() { setDebouncedSearch(val); }, 500);
  };

  // Reload when filters change
  useEffect(function() { reloadOrders(); }, [channelFilter, marketplaceFilter, fulfillmentFilter, statusFilter, dateFrom, dateTo]);

  // Every filter mirrored into the page link (replaceState: no history entry per keystroke)
  useEffect(function() {
    try {
      var sp = new URLSearchParams(window.location.search);
      var vals = { q: debouncedSearch, integration: channelFilter, mp: marketplaceFilter, ful: fulfillmentFilter, status: statusFilter, from: dateFrom, to: dateTo };
      Object.keys(vals).forEach(function(k) { if (vals[k]) sp.set(k, vals[k]); else sp.delete(k); });
      var rest = sp.toString();
      var next = window.location.pathname + (rest ? '?' + rest : '');
      if (next !== window.location.pathname + window.location.search) history.replaceState(history.state, '', next);
    } catch (e) {}
  }, [debouncedSearch, channelFilter, marketplaceFilter, fulfillmentFilter, statusFilter, dateFrom, dateTo]);

  var filtersActive = !!(search || channelFilter || marketplaceFilter || fulfillmentFilter || statusFilter || dateFrom || dateTo);
  var clearFilters = function() {
    clearTimeout(searchTimerRef.current);
    setSearch(''); setDebouncedSearch(''); setChannelFilter(''); setMarketplaceFilter(''); setFulfillmentFilter('');
    setStatusFilter(''); setDateFrom(''); setDateTo('');
  };
  var datePresets = orderDatePresets();
  var activeDatePreset = datePresets.find(function(p) { return p.from === dateFrom && p.to === dateTo; });
  var filterSelectStyle = { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 8, fontSize: 13, outline: 'none', cursor: 'pointer', transition: 'border-color 0.15s, background 0.15s' };

  // Stale-response guard (sweep 9-27 L15): clicking order A then B quickly could land A's detail
  // after B's and show A under B's header (and B's refunds panel). Only the latest request wins;
  // closing the modal also invalidates an in-flight one.
  var detailGenRef = useRef(0);
  var openOrderDetail = async function(order) {
    var myGen = ++detailGenRef.current;
    setSelectedOrder(order);
    setOrderDetail(null);
    setDetailLoading(true);
    try {
      var detail = await api('/integrations/' + order.channel + '/orders/' + order.id);
      if (myGen !== detailGenRef.current) return;
      setOrderDetail(detail);
    } catch (e) {
      if (myGen !== detailGenRef.current) return;
      showToast(e.message, 'error'); setOrderDetail(null);
    }
    setDetailLoading(false);
  };

  // Deep link: /orders?order=<id>&channel=<channel> opens that order's detail (new-tab target).
  useEffect(function() {
    try {
      var sp = new URLSearchParams(window.location.search);
      var qid = sp.get('order'); var qch = sp.get('channel');
      if (qid && qch) {
        openOrderDetail({ id: qid, channel: qch });
        // drop only the deep-link params: filters in the same link stay
        sp.delete('order'); sp.delete('channel');
        var rest = sp.toString();
        history.replaceState(history.state, '', '/orders' + (rest ? '?' + rest : ''));
      }
    } catch (e) {}
  }, []);

  var channelBadge = function(ch) {
    var colors = { walmart: '#0071CE', zentail: C.accent, amazon_ca: '#FF9900', amazon_us: '#FF9900', amazon_au: '#FF9900', amazon_mx: '#FF9900', mercadolibre: '#3483FA' };
    var labels = { walmart: 'Walmart', zentail: 'Zentail', amazon_ca: 'Amazon CA', amazon_us: 'Amazon US', amazon_au: 'Amazon AU', amazon_mx: 'Amazon MX', mercadolibre: 'Mercado Libre' };
    var color = colors[ch] || C.textMuted;
    var label = labels[ch] || (ch ? ch.charAt(0).toUpperCase() + ch.slice(1) : 'Unknown');
    return React.createElement('span', { style: { background: color + '18', color: color, padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' } }, label);
  };

  // Who fulfils the order (Anthony 2026-10-13): FBA / FBM / WFS, Dropship (a vendor ships), ML Full.
  // The server decides (services/order-fulfillment.js); an order shipped from two places shows both,
  // an unknown one shows nothing.
  var fulfillmentBadges = function(tags) {
    if (!tags || !tags.length) return null;
    var colors = { FBA: '#FF9900', FBM: '#14B8A6', WFS: '#0071CE', Dropship: '#A78BFA', 'ML Full': '#3483FA' };
    return React.createElement('span', { style: { display: 'inline-flex', gap: 4, flexWrap: 'wrap' } }, tags.map(function(t) {
      var color = colors[t] || C.textMuted;
      return React.createElement('span', { key: t, style: { background: color + '18', color: color, padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', whiteSpace: 'nowrap' } }, t);
    }));
  };

  // Status codes arrive raw from three syncs (Zentail RETURN_REQUESTED, Amazon Canceled, Mercado
  // Libre released): one wording and one color per meaning (polish 2026-10-11). Returns and refunds
  // used to be grey like an unknown value.
  var statusBadge = function(st) {
    if (!st) return '\u2014';
    var s = String(st).toLowerCase();
    var isReturn = s.includes('return') || s.includes('refund') || s === 'not_delivered';
    var isCancel = s.includes('cancel') || s === 'unfulfillable';
    var isDone = s.includes('deliver') || s.includes('complet');
    var isShipped = /ship/.test(s) && !/unship|partial/.test(s);
    var color = isReturn ? '#A78BFA' : isCancel ? C.danger : isDone ? C.success : isShipped ? C.accent : '#F59E0B';
    var bg = color + '18';
    var label = s.includes('cancel') ? 'Cancelled' : s === 'released' ? 'Paid' : String(st).replace(/_/g, ' ');
    return React.createElement('span', { style: { background: bg, color: color, padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' } }, label);
  };

  return React.createElement('div', null,
    // Header
    React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 } },
      React.createElement('div', null,
        React.createElement('h2', { style: { margin: 0, fontSize: 20, fontWeight: 600, color: C.text } }, 'Orders'),
        React.createElement('p', { style: { margin: '4px 0 0', fontSize: 13, color: C.textMuted } }, totalOrders.toLocaleString() + ' order' + (totalOrders !== 1 ? 's' : ''))
      ),
      React.createElement('div', { style: { display: 'flex', gap: 8 } },
        React.createElement(Btn, { size: 'sm', onClick: reloadOrders, disabled: loading },
          React.createElement(LIcon, { name: 'refresh-cw', size: 14 }), ' Refresh')
      )
    ),
    // Filters
    React.createElement('div', { style: { display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' } },
      // The index behind the search covers more than the old placeholder admitted (polish 2026-10-11)
      React.createElement(SearchInput, { placeholder: 'Search order, customer, SKU, ASIN, tracking or marketplace order #', value: search, onChange: function(e) { handleSearch(e.target.value); },
        className: 'dt-input', flex: 1,
        style: { minWidth: 200, background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 8, fontSize: 13, outline: 'none', transition: 'border-color 0.15s, box-shadow 0.15s' } }),
      React.createElement('select', { value: channelFilter, onChange: function(e) { setChannelFilter(e.target.value); },
        className: 'dt-select',
        style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 8, fontSize: 13, outline: 'none', cursor: 'pointer', transition: 'border-color 0.15s, background 0.15s' } },
        React.createElement('option', { value: '' }, 'All Integrations'),
        React.createElement('option', { value: 'zentail' }, 'Zentail'),
        React.createElement('option', { value: 'amazon_ca' }, 'Amazon Canada'),
        React.createElement('option', { value: 'amazon_us' }, 'Amazon US'),
        React.createElement('option', { value: 'mercadolibre' }, 'Mercado Libre')
      ),
      // Channel + Fulfillment across all orders (Anthony 2026-10-13; server reads order_facets)
      React.createElement('select', { value: marketplaceFilter, onChange: function(e) { setMarketplaceFilter(e.target.value); },
        className: 'dt-select', title: 'Where the order was sold', style: filterSelectStyle },
        React.createElement('option', { value: '' }, 'All Channels'),
        ORDER_MARKETPLACE_OPTIONS.map(function(o) { return React.createElement('option', { key: o[0], value: o[0] }, o[1]); })
      ),
      React.createElement('select', { value: fulfillmentFilter, onChange: function(e) { setFulfillmentFilter(e.target.value); },
        className: 'dt-select', title: 'Who ships the order', style: filterSelectStyle },
        React.createElement('option', { value: '' }, 'All Fulfillment'),
        ORDER_FULFILLMENT_OPTIONS.map(function(t) { return React.createElement('option', { key: t, value: t }, t); })
      ),
      // Status groups (server: services/orders-list-filters.js STATUS_GROUPS). The old picker sent one
      // raw status: "Pending" missed Pending Payment, "Unshipped" and "Delivered" matched nothing.
      React.createElement('select', { value: statusFilter, onChange: function(e) { setStatusFilter(e.target.value); },
        className: 'dt-select',
        style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 8, fontSize: 13, outline: 'none', cursor: 'pointer', transition: 'border-color 0.15s, background 0.15s' } },
        React.createElement('option', { value: '' }, 'All Statuses'),
        React.createElement('option', { value: 'pending' }, 'Pending'),
        React.createElement('option', { value: 'shipped' }, 'Shipped'),
        React.createElement('option', { value: 'cancelled' }, 'Cancelled'),
        React.createElement('option', { value: 'returns' }, 'Returns & Refunds')
      ),
      // Date shortcuts fill From / To; picking a date by hand shows "Custom dates"
      React.createElement('select', { value: activeDatePreset ? activeDatePreset.key : (dateFrom || dateTo ? 'custom' : ''),
        onChange: function(e) {
          var p = datePresets.find(function(x) { return x.key === e.target.value; });
          if (p) { setDateFrom(p.from); setDateTo(p.to); } else if (!e.target.value) { setDateFrom(''); setDateTo(''); }
        },
        className: 'dt-select', title: 'Date shortcuts (Eastern time)', style: filterSelectStyle },
        React.createElement('option', { value: '' }, 'Any Date'),
        (dateFrom || dateTo) && !activeDatePreset && React.createElement('option', { value: 'custom', disabled: true }, 'Custom Dates'),
        datePresets.map(function(p) { return React.createElement('option', { key: p.key, value: p.key }, p.label); })
      ),
      React.createElement('input', { type: 'date', value: dateFrom, onChange: function(e) { setDateFrom(e.target.value); },
        className: 'dt-input', title: 'From date',
        style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 8, fontSize: 13, outline: 'none', colorScheme: 'dark', transition: 'border-color 0.15s, box-shadow 0.15s' } }),
      React.createElement('span', { style: { color: C.textMuted, fontSize: 13, alignSelf: 'center' } }, '\u2013'),
      React.createElement('input', { type: 'date', value: dateTo, onChange: function(e) { setDateTo(e.target.value); },
        className: 'dt-input', title: 'To date',
        style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 8, fontSize: 13, outline: 'none', colorScheme: 'dark', transition: 'border-color 0.15s, box-shadow 0.15s' } }),
      (dateFrom || dateTo) && React.createElement(Btn, { size: 'sm', onClick: function() { setDateFrom(''); setDateTo(''); }, title: 'Clear dates' },
        React.createElement(LIcon, { name: 'x', size: 14 })),
      filtersActive && React.createElement(Btn, { size: 'sm', onClick: clearFilters, title: 'Reset the search and every filter' },
        React.createElement(LIcon, { name: 'filter-x', size: 14 }), ' Clear Filters')
    ),
    // Orders table. Sorting (polish 2026-10-11): the server sorts the full list by date only \u2014 any
    // other column meant the whole 2M-row union sorted on every click (3.8 s) \u2014 and by any listed
    // column inside a search, where the set is already small; the headers follow the search box
    // (noSort). Items / Units / SKUs / Tracking are per-page aggregates and never sort.
    React.createElement(DataTable, {
      columns: [
        { key: 'channel', label: 'Integration', noSort: !debouncedSearch, render: function(o) { return channelBadge(o.channel); }, style: { width: 90 } },
        { key: 'fulfillment', label: 'Fulfillment', noSort: true, render: function(o) { return fulfillmentBadges(o.fulfillment) || '—'; },
          filterValue: function(o) { return (o.fulfillment || []).join(' + '); }, style: { width: 90 } },
        { key: 'order_id', label: 'Order ID', noSort: !debouncedSearch, copyable: true, style: { fontWeight: 600, fontFamily: 'inherit', fontSize: 12, color: C.accent } },
        { key: 'channel_name', label: 'Channel Name', noSort: !debouncedSearch, render: function(o) { return o.channel_name || '\u2014'; }, style: { fontSize: 12 } },
        { key: 'channel_order_id', label: 'Channel Order #', noSort: true, copyable: true, render: function(o) { return o.channel_order_id || '\u2014'; }, style: { fontFamily: 'inherit', fontSize: 12 } },
        // The name, else the ship-to name (server fallback); an Amazon FBA row \u2014 Amazon shares no
        // buyer name \u2014 shows the ship-to city muted in its place instead of nothing
        { key: 'customer_name', label: 'Customer', noSort: !debouncedSearch, render: function(o) {
          if (o.customer_name) return o.customer_name;
          if (o.customer_place) return React.createElement('span', { style: { color: C.textMuted }, title: 'Ship-to city: this marketplace does not share the buyer name' }, o.customer_place);
          return '\u2014';
        } },
        { key: 'order_date', label: 'Date', render: function(o) { return o.order_date ? new Date(o.order_date).toLocaleDateString() : '\u2014'; } },
        { key: 'status', label: 'Status', noSort: !debouncedSearch, render: function(o) { return statusBadge(o.status); } },
        // `total` is USD (converted server-side at the order-date rate); relay orders placed in
        // MXN/CAD/\u2026 also show the amount the customer actually paid (currency Phase D, 2026-09-03)
        { key: 'total', label: 'Total (USD)', noSort: !debouncedSearch, render: function(o) {
          if (!o.total || Number(o.total) === 0) return '\u2014';
          var usd = Number(o.total).toFixed(2);
          if (o.original_currency && o.original_currency !== 'USD' && o.original_total != null) {
            return React.createElement('span', { title: 'Placed in ' + o.original_currency + '; converted at the rate on the order date' }, usd,
              React.createElement('span', { style: { color: C.textDim, fontSize: 11, marginLeft: 4 } }, Number(o.original_total).toFixed(2) + ' ' + o.original_currency));
          }
          return usd;
        }, style: { textAlign: 'left' } },
        { key: 'line_count', label: 'Items', noSort: true, style: { textAlign: 'left' } },
        { key: 'total_units', label: 'Units', noSort: true, style: { textAlign: 'left' } },
        // The SKUs had been shown under "Tracking" on every Amazon and Mercado Libre row
        { key: 'skus', label: 'SKUs', noSort: true, render: function(o) { return o.skus ? React.createElement('span', { title: o.skus }, o.skus) : '\u2014'; }, style: { fontSize: 11, color: C.textMuted, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } },
        { key: 'tracking', label: 'Tracking', noSort: true, render: function(o) { return o.tracking ? React.createElement('span', { title: o.tracking }, o.tracking) : '\u2014'; }, style: { fontSize: 11, color: C.textMuted, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } },
      ],
      data: orders, storageKey: 'orders-module',
      // Column filters off (9-05): this table pages on the server through a 4-table UNION that does not
      // receive per-column filters, so header filters could only ever narrow the loaded page. Use the toolbar.
      noColumnFilters: true,
      defaultSort: 'order_date', defaultSortDir: 'desc',
      loading: loading,
      rowHref: function(o) { return '/orders?order=' + o.id + '&channel=' + o.channel; },
      onRowClick: function(o) { openOrderDetail(o); },
      onServerFetch: handleServerFetch, serverTotal: totalOrders, serverSearch: debouncedSearch,
      emptyTitle: 'No orders',
      emptySub: filtersActive ? 'Nothing matches these filters' : 'Orders arrive through the Zentail, Amazon and Mercado Libre syncs'
    }),
    // Order detail modal
    (function() {
      var raw = orderDetail && orderDetail.raw_json ? (typeof orderDetail.raw_json === 'string' ? JSON.parse(orderDetail.raw_json) : orderDetail.raw_json) : {};
      var acct = raw.accounting || {};
      var isAmazonOrder = selectedOrder && selectedOrder.channel && selectedOrder.channel.startsWith('amazon');
      if (isAmazonOrder && orderDetail) {
        var aLines = orderDetail.lines || [];
        var sumF = function(f) { var s = 0; aLines.forEach(function(l) { s += Number(l[f] || 0); }); return s; };
        var aCost = 0; aLines.forEach(function(l) { if (l.unit_cost != null) aCost += Number(l.unit_cost) * (l.qty_ordered || 1); });
        // unit_price is PER UNIT on amazon_order_lines (the sync and the report import both divide
        // ItemPrice by the quantity) — the order's item price is unit × qty (sweep 9-27 M8)
        var aItemPrice = 0; aLines.forEach(function(l) { aItemPrice += Number(l.unit_price || 0) * Number(l.qty_ordered || 1); });
        var aShippingPrice = sumF('shipping_price') || 0;
        var aDiscount = sumF('promo_discount') || 0;
        var aShippingCost = sumF('fba_fee') || 0;
        // Commission = the referral and other fees Amazon charged, less what a refund credited back;
        // the refund itself (money returned to the buyer) is its own line (accounting review 2026-10-11).
        // Fees come from the finance-event sync, daily since 10-11 — until it reaches an order the
        // fees are unknown, not zero.
        var aFeesKnown = aLines.some(function(l) { return l.financials_synced; });
        var aCommission = (sumF('referral_fee') + sumF('other_fees') - sumF('refund_fee_credit')) || 0;
        var aRefund = sumF('refund_amount') || 0;
        acct = {
          itemPrice: aItemPrice,
          shippingPrice: aShippingPrice || null,
          channelTax: (sumF('item_tax') + sumF('shipping_tax')) || null,
          totalDiscount: aDiscount || null,
          itemCost: aCost || null,
          shippingCost: aFeesKnown ? (aShippingCost || 0) : null,
          resellerCommission: aFeesKnown ? (aCommission || 0) : null,
          refund: aRefund || null,
          feesKnown: aFeesKnown,
        };
      }
      var isMeliOrder = selectedOrder && selectedOrder.channel === 'mercadolibre';
      if (isMeliOrder && orderDetail) {
        var mLines = orderDetail.lines || [];
        var mItemPrice = 0, mFees = 0, mCost = 0;
        mLines.forEach(function(l) {
          var q = Number(l.qty_ordered || 1);
          mItemPrice += Number(l.unit_price || 0) * q;
          mFees += Number(l.sale_fee || 0) * q; // sale_fee is per unit (USD, CBT commission)
          if (l.unit_cost != null) mCost += Number(l.unit_cost) * q;
        });
        // Seller eats the leg's list_cost when the buyer got free shipping (est. until
        // verified against ML billing); buyer-paid shipping is not seller revenue on CBT.
        var mShip = (orderDetail.ship_cost_type === 'free' && orderDetail.ship_list_cost != null) ? Number(orderDetail.ship_list_cost) : 0;
        // 0, not null, for a known-zero cost: null now means "not known" (accounting review 2026-10-11)
        acct = {
          itemPrice: mItemPrice,
          shippingPrice: null,
          channelTax: null,
          totalDiscount: null,
          itemCost: mCost || null,
          shippingCost: mShip,
          resellerCommission: mFees,
        };
      }
      // Relay orders placed in MXN/CAD/… (Zentail → Amazon MX/CA) carry native amounts: convert the
      // order-side money to USD at the order-date rate so profit/margin compare with USD costs.
      // Costs are ours and already USD (Zentail's accounting.itemCost/shippingCost = our product
      // cost + our label cost). The lines table below keeps the native price with its code.
      // Zentail relay: the figures the server settled from our labels, Amazon's finance events, the
      // refunds recorded here and the configured rates (services/order-accounting.js relayFinancials,
      // accounting review 2026-10-11). Commission and refund are in the order's currency and convert
      // below with the rest; the label cost is ours and already USD. null = not known (shown as a dash).
      var fin = (!isAmazonOrder && !isMeliOrder && orderDetail && orderDetail.financials) || null;
      if (fin) {
        acct = Object.assign({}, acct);
        acct.shippingCost = fin.shippingCost;
        acct.resellerCommission = fin.commission;
        acct.refund = fin.refund;
      }
      var odCur = orderDetail && orderDetail.currency && orderDetail.currency !== 'USD' ? orderDetail.currency : null;
      var odRate = odCur ? (Number(orderDetail.fx_rate_to_usd) || 0) : 1;
      if (odCur && odRate > 0) {
        // Copy first: raw.accounting lives in React state, and mutating it would convert again on every re-render
        acct = Object.assign({}, acct);
        // An FBA order's fees are in its marketplace's currency too (fba_fee on a CA order is CAD)
        ['itemPrice', 'shippingPrice', 'channelTax', 'totalDiscount', 'resellerCommission', 'refund'].concat(isAmazonOrder ? ['shippingCost'] : []).forEach(function(k) {
          if (acct[k] != null && acct[k] !== '') acct[k] = Number(acct[k]) * odRate;
        });
      }
      // Unified profit: Item Price + Shipping Price - Discount - Refund - Item Cost - Shipping Cost - Commission,
      // margin on the revenue kept after the refund. A fee not known yet counts as 0 here and shows as a dash.
      var pItemPrice = Number(acct.itemPrice || 0);
      var pShippingPrice = Number(acct.shippingPrice || 0);
      var pDiscount = Number(acct.totalDiscount || 0);
      var pRefund = Number(acct.refund || 0);
      var pItemCost = Number(acct.itemCost || 0);
      var pShippingCost = Number(acct.shippingCost || 0);
      var pCommission = Number(acct.resellerCommission || 0);
      acct.revenue = pItemPrice + pShippingPrice - pDiscount;
      var pKept = acct.revenue - pRefund;
      acct.profit = pItemCost ? (pKept - pItemCost - pShippingCost - pCommission) : null;
      acct.margin = (pItemCost && pKept > 0) ? acct.profit / pKept : null;
      acct.roi = pItemCost > 0 ? acct.profit / pItemCost : null;
      // Where the commission figure comes from, on the row's label
      var mpLabel = { amazon: 'Amazon', ebay: 'eBay', walmart: 'Walmart', target: 'Target Plus', shein: 'SHEIN', temu: 'Temu', faire: 'Faire', jcpenney: 'JCPenney', shopify: 'Shopify', mercadolibre: 'Mercado Libre' };
      var fmtRate = function(r) { return (Number(r) * 100).toFixed(1).replace(/\.0$/, '') + '%'; };
      // The channel the integration carried the order for (Anthony 2026-10-13: "this order came in
      // through Zentail but the channel is Easternperfume.com") — the table's Channel Name. A Zentail
      // channel that carries several marketplaces ("Atharva" = Amazon US FBM, eBay and Walmart; "SHEIN
      // - Amz AU" = SHEIN, Temu and Faire) adds the marketplace read off the order; websites need none.
      var odChannelName = (selectedOrder && selectedOrder.channel_name)
        || (selectedOrder && selectedOrder.channel === 'zentail' && orderDetail ? (orderDetail.channel || raw.channelName) : null)
        || (isAmazonOrder ? 'Amazon FBA ' + ((orderDetail && orderDetail.marketplace) || String(selectedOrder.channel).replace(/^amazon_/, '').toUpperCase()) : null)
        || (isMeliOrder ? 'Mercado Libre' + (orderDetail && orderDetail.site_id ? ' ' + orderDetail.site_id : '') : null)
        || (selectedOrder && selectedOrder.channel === 'walmart' ? 'Walmart' : null);
      var odMpLabel = fin && fin.marketplace && fin.marketplace !== 'shopify' ? mpLabel[fin.marketplace] : null;
      var odLetters = function(s) { return String(s).toLowerCase().replace(/[^a-z]/g, ''); };
      if (odChannelName && odMpLabel && odLetters(odChannelName) !== odLetters(odMpLabel)) odChannelName += ' (' + odMpLabel + ')';
      // a configured rate of 0 (Temu charges none) reads as "none", not "est. 0%"
      var commissionLabel = 'Commission' + (fin
        ? (fin.commissionSource === 'estimate' ? (Number(fin.commissionRate) > 0 ? ' (est. ' + fmtRate(fin.commissionRate) + ')' : ' (' + (mpLabel[fin.marketplace] || 'channel') + ': none)')
          : fin.commissionSource === 'amazon_finances' ? ' (Amazon)'
          : fin.commissionSource === 'zentail' ? ' (' + (mpLabel[fin.marketplace] || 'channel') + ')' : '')
        : (isAmazonOrder && acct.feesKnown ? ' (Amazon)' : ''));
      var sectionLabel = function(text) { return React.createElement('div', { style: { fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 } }, text); };
      var infoRow = function(label, value, copyable) { return React.createElement('div', { style: { display: 'flex', gap: 12, fontSize: 12, padding: '3px 0' } }, React.createElement('span', { style: { color: C.textMuted, minWidth: 90 } }, label), copyable && value != null && value !== '' ? React.createElement(CopyCell, { value: value }, React.createElement('span', { style: { color: C.text, fontWeight: 500 } }, String(value))) : React.createElement('span', { style: { color: C.text, fontWeight: 500 } }, value != null && value !== '' ? String(value) : '\u2014')); };
      var money = function(v) { return v != null && v !== '' ? '$' + Number(v).toFixed(2) : '\u2014'; };
      // Line prices are what the marketplace charged, in the order's currency \u2014 never a "$" on MXN
      var lineMoney = function(v) { return v == null || v === '' ? '\u2014' : (odCur ? Number(v).toFixed(2) + ' ' + odCur : money(v)); };
      var rawLines = raw.products || [];

      return React.createElement(Modal, { open: !!selectedOrder, onClose: function() { detailGenRef.current++; setSelectedOrder(null); setOrderDetail(null); setDetailLoading(false); }, title: 'Order Details', width: 1300 },
        detailLoading ? React.createElement('div', { style: { textAlign: 'center', padding: 40, color: C.textMuted } }, 'Loading...') :
        orderDetail ? React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 16 } },
          // Order header
          React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' } },
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
              channelBadge(selectedOrder.channel),
              React.createElement(CopyCell, { value: orderDetail.purchase_order_id || orderDetail.zentail_order_id || orderDetail.amazon_order_id || selectedOrder.order_id },
                React.createElement('span', { style: { fontSize: 16, fontWeight: 600, color: C.text, fontFamily: 'inherit' } },
                  orderDetail.purchase_order_id || orderDetail.zentail_order_id || orderDetail.amazon_order_id || selectedOrder.order_id)),
              odChannelName && React.createElement('span', { style: { fontSize: 12, color: C.textMuted } }, 'Channel: ',
                React.createElement('span', { style: { color: C.text, fontWeight: 500 } }, odChannelName)),
              orderDetail.fulfillment && orderDetail.fulfillment.length > 0 && React.createElement('span', { style: { fontSize: 12, color: C.textMuted, display: 'inline-flex', alignItems: 'center', gap: 4 } }, 'Fulfillment: ',
                fulfillmentBadges(orderDetail.fulfillment)),
              // the channel order id opens the order on the marketplace (Anthony 2026-09-23, to-do 15)
              raw.channelOrderId && React.createElement(CopyCell, { value: raw.channelOrderId },
                React.createElement('span', { style: { fontSize: 12, color: C.textMuted } }, 'Channel Order #: ', marketplaceOrderLink(selectedOrder.channel_name || raw.channelName || selectedOrder.channel, raw.channelOrderId, { fontSize: 12 })))
            ),
            statusBadge(orderDetail.order_status)
          ),
          // 3-column info cards
          React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 } },
            // Customer
            React.createElement('div', { style: { background: C.bg, borderRadius: 8, padding: '12px 16px', border: '1px solid ' + C.border + '44' } },
              sectionLabel('Customer'),
              React.createElement('div', { style: { fontSize: 13, color: C.text, fontWeight: 500 } },
                orderDetail.customer_name ? React.createElement(CopyCell, { value: orderDetail.customer_name }, orderDetail.customer_name) : '\u2014'),
              orderDetail.customer_email && React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 2 } }, orderDetail.customer_email),
              infoRow('Order Date', orderDetail.order_date ? new Date(orderDetail.order_date).toLocaleString() : null),
              infoRow('Last Updated', (raw.lastUpdatedTs || raw.LastUpdateDate) ? new Date(raw.lastUpdatedTs || raw.LastUpdateDate).toLocaleString() : null),
              infoRow('Service Level', raw.requestedServiceLevel || raw.standardServiceLevel || raw.ShipServiceLevel || raw.ShipmentServiceLevelCategory || null),
              infoRow('Prime', (raw.prime || raw.IsPrime || raw.FulfillmentChannel === 'AFN') ? 'Yes' : 'No'),
              infoRow('Business Order', (raw.businessOrder || raw.IsBusinessOrder) ? 'Yes' : 'No')
              // Fulfillment moved to the header tag (Anthony 2026-10-13), every integration now has one
            ),
            // Shipping
            React.createElement('div', { style: { background: C.bg, borderRadius: 8, padding: '12px 16px', border: '1px solid ' + C.border + '44' } },
              sectionLabel('Shipping Address'),
              React.createElement('div', { style: { fontSize: 13, color: C.text, lineHeight: 1.5, marginBottom: 6 } },
                [orderDetail.ship_to_name, orderDetail.ship_to_address1, orderDetail.ship_to_address2,
                 [orderDetail.ship_to_city, orderDetail.ship_to_state, orderDetail.ship_to_zip].filter(Boolean).join(', '),
                 orderDetail.ship_to_country].filter(Boolean).map(function(line, i) {
                  return React.createElement('div', { key: i }, line);
                })
              ),
              isMeliOrder && orderDetail.shipment_status && infoRow('Shipment', orderDetail.shipment_status.replace(/_/g, ' ') + (orderDetail.shipment_substatus ? ' · ' + orderDetail.shipment_substatus.replace(/_/g, ' ') : '')),
              isMeliOrder && orderDetail.logistic_type && infoRow('Logistic', orderDetail.logistic_type === 'fulfillment' ? 'Full (ML fulfillment)' : orderDetail.logistic_type),
              isMeliOrder && orderDetail.ship_list_cost != null && infoRow('Ship Cost' + (orderDetail.ship_cost_type === 'free' ? ' (seller est.)' : ''), money(orderDetail.ship_list_cost)),
              isMeliOrder && orderDetail.stock_node && infoRow('Ship From', orderDetail.stock_node),
              (raw.shippingAddress && raw.shippingAddress.phone) && infoRow('Phone', raw.shippingAddress.phone),
              (raw.ShippingAddress && raw.ShippingAddress.Phone) && infoRow('Phone', raw.ShippingAddress.Phone),
              (raw.shipBy || raw.LatestShipDate) && infoRow('Ship By', new Date(raw.shipBy || raw.LatestShipDate).toLocaleDateString()),
              raw.customer_notes && React.createElement('div', { style: { marginTop: 6, fontSize: 11, color: '#FBBF24', fontStyle: 'italic' } }, 'Note: ' + raw.customer_notes)
            ),
            // Financials
            React.createElement('div', { style: { background: C.bg, borderRadius: 8, padding: '12px 16px', border: '1px solid ' + C.border + '44' } },
              sectionLabel('Financials'),
              infoRow('Item Price', money(acct.itemPrice)),
              infoRow('Shipping Price', money(acct.shippingPrice)),
              infoRow('Channel Tax', money(acct.channelTax)),
              infoRow('Discount', acct.totalDiscount ? '-' + money(acct.totalDiscount) : money(0)),
              React.createElement('div', { style: { borderTop: '1px solid ' + C.border + '44', marginTop: 4, paddingTop: 4 } },
                infoRow('Revenue', money(acct.revenue) + (odCur ? ' (from ' + odCur + ' at ' + odRate.toFixed(4) + ')' : '')),
                acct.refund > 0 && infoRow('Refund' + (fin && fin.refundSource === 'amazon_finances' ? ' (Amazon)' : fin && fin.refundSource === 'recorded' ? ' (recorded)' : ''), '-' + money(acct.refund)),
                infoRow('Item Cost', money(acct.itemCost)),
                // Sources (accounting review 2026-10-11): the shipping cost is our label where we bought
                // one; the commission is Amazon's own fee, what the channel reported through Zentail, or
                // an estimate at the configured rate. A dash means not known (yet), not zero.
                infoRow('Shipping Cost' + (fin && fin.shippingCostSource === 'label' ? ' (label)' : ''), acct.shippingCost != null ? money(acct.shippingCost) : null),
                infoRow(commissionLabel, acct.resellerCommission != null ? money(acct.resellerCommission) : null)
              ),
              React.createElement('div', { style: { borderTop: '1px solid ' + C.border + '44', marginTop: 4, paddingTop: 4 } },
                React.createElement('div', { style: { display: 'flex', gap: 12, fontSize: 13, padding: '3px 0' } },
                  // A fee not known yet counts as $0 in the profit and shows as a dash on its own row above.
                  // Anthony 2026-10-13: the label stays plain — its "pending" suffix showed on website orders
                  // that carry no commission at all (order 4287154).
                  React.createElement('span', { style: { color: C.text, fontWeight: 600, minWidth: 90 } }, 'Profit'),
                  React.createElement('span', { style: { fontWeight: 700, color: (acct.profit || 0) >= 0 ? C.success : '#F87171' } }, money(acct.profit))
                ),
                infoRow('Margin', acct.margin != null ? (Number(acct.margin) * 100).toFixed(1) + '%' : null),
                // ROI = profit ÷ item cost, the app's ROI everywhere else (PO analysis, research) — shipping
                // and commission are already taken out of the profit (Anthony 2026-10-13)
                React.createElement('div', { style: { display: 'flex', gap: 12, fontSize: 12, padding: '3px 0' } },
                  React.createElement('span', { style: { color: C.textMuted, minWidth: 90 } }, 'ROI'),
                  React.createElement('span', { style: { fontWeight: 500, color: acct.roi == null ? C.text : acct.roi >= 0 ? C.success : '#F87171' } },
                    acct.roi != null ? (Number(acct.roi) * 100).toFixed(1) + '%' : '—'))
              )
            )
          ),
          // Refunds (Anthony 2026-09-23): issue on the marketplace where an API exists, always record here
          React.createElement(OrderRefundsPanel, { key: 'refunds:' + selectedOrder.channel + ':' + selectedOrder.id, channel: selectedOrder.channel, orderPk: selectedOrder.id, detail: orderDetail, raw: raw, channelName: selectedOrder.channel_name || raw.channelName || null, showToast: showToast }),
          // Line items table
          React.createElement('div', null,
            sectionLabel('Line Items (' + (orderDetail.lines ? orderDetail.lines.length : 0) + ')'),
            React.createElement('div', { style: { border: '1px solid ' + C.border, borderRadius: 8, overflow: 'auto' } },
              React.createElement('table', { style: { width: '100%', borderCollapse: 'collapse', fontSize: 12 } },
                React.createElement('thead', null,
                  React.createElement('tr', { style: { background: C.surface, textAlign: 'left' } },
                    (isMeliOrder
                      ? ['Image', 'SKU', 'Product', 'Qty', 'Unit Price', 'Sale Fee', 'Total', 'Cost', 'Status', 'Tracking']
                      : ['Image', 'SKU', 'Product', 'Qty', 'Unit Price', 'Total', 'Cost', 'Status', 'Tracking']).map(function(h) {
                      return React.createElement('th', { key: h, style: { padding: '8px 10px', fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid ' + C.border, whiteSpace: 'nowrap' } }, h);
                    })
                  )
                ),
                React.createElement('tbody', null,
                  (orderDetail.lines || []).map(function(line, i) {
                    var rl = rawLines[i] || {};
                    var lineCost = line.unit_cost != null ? line.unit_cost : rl.cost;
                    return React.createElement('tr', { key: i, style: { borderBottom: i < orderDetail.lines.length - 1 ? '1px solid ' + C.border + '66' : 'none' } },
                      React.createElement('td', { style: { padding: '6px 10px' } },
                        (line.primary_image || rl.primary_image) ? React.createElement('img', { src: line.primary_image || rl.primary_image, style: { width: 36, height: 36, objectFit: 'cover', borderRadius: 4 } }) :
                        React.createElement('div', { style: { width: 36, height: 36, borderRadius: 4, background: C.surface, display: 'flex', alignItems: 'center', justifyContent: 'center' } }, React.createElement(LIcon, { name: 'package', size: 14, color: C.textDim }))
                      ),
                      React.createElement('td', { style: { padding: '6px 10px', fontFamily: 'inherit', fontSize: 11, color: C.accent } }, line.sku || '\u2014'),
                      React.createElement('td', { style: { padding: '6px 10px', color: C.text, maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, line.vendor_title || line.title || rl.title || '\u2014'),
                      React.createElement('td', { style: { padding: '6px 10px', fontWeight: 500 } }, line.qty_ordered || rl.quantity || 1),
                      React.createElement('td', { style: { padding: '6px 10px' } }, lineMoney(line.unit_price || rl.unitPrice)),
                      isMeliOrder && React.createElement('td', { style: { padding: '6px 10px', color: '#F59E0B' } },
                        line.sale_fee != null ? money(line.sale_fee) + (Number(line.qty_ordered || 1) > 1 ? ' ×' + line.qty_ordered : '') : '—'),
                      React.createElement('td', { style: { padding: '6px 10px', fontWeight: 600 } }, lineMoney((line.qty_ordered || rl.quantity || 1) * (line.unit_price || rl.unitPrice || 0))),
                      React.createElement('td', { style: { padding: '6px 10px', color: C.textMuted } }, lineCost != null ? money(lineCost) : '\u2014'),
                      React.createElement('td', { style: { padding: '6px 10px' } }, statusBadge(line.line_status || rl.status ||
                        (isMeliOrder ? (orderDetail.shipment_status || orderDetail.order_status || null)
                                     : (line.qty_shipped > 0 ? 'Shipped' : line.qty_ordered > 0 ? 'Unshipped' : null)))),
                      React.createElement('td', { style: { padding: '6px 10px', fontSize: 10, color: C.textMuted, fontFamily: 'inherit', whiteSpace: 'nowrap' } },
                        (function() {
                          var tn = line.tracking_number || (isMeliOrder && orderDetail.tracking_number);
                          if (!tn) return '\u2014';
                          return React.createElement(CopyCell, { value: tn },
                            React.createElement('a', {
                              href: shipTrackingUrl(line.carrier, tn), target: '_blank', rel: 'noopener',
                              onClick: function(e) { e.stopPropagation(); },
                              title: 'Track on carrier site' + (line.carrier ? ' (' + shipCarrierLabel(line.carrier) + ')' : ''),
                              style: { fontFamily: 'inherit', color: C.accent, textDecoration: 'none' } }, tn));
                        })())
                    );
                  })
                )
              )
            )
          ),
          // Packages
          raw.packages && raw.packages.length > 0 && React.createElement('div', null,
            sectionLabel('Packages (' + raw.packages.length + ')'),
            raw.packages.map(function(pkg, i) {
              var pkgTracking = pkg.tracking || pkg.trackingNumber || null;
              return React.createElement('div', { key: i, style: { background: C.bg, borderRadius: 8, padding: '8px 14px', border: '1px solid ' + C.border + '44', marginBottom: 6, fontSize: 12, color: C.text } },
                infoRow('Carrier', pkg.carrier ? shipCarrierLabel(pkg.carrier) : '\u2014'),
                // Tracking links to the carrier's tracking page (copy icon still copies).
                React.createElement('div', { style: { display: 'flex', gap: 12, fontSize: 12, padding: '3px 0' } },
                  React.createElement('span', { style: { color: C.textMuted, minWidth: 90 } }, 'Tracking'),
                  pkgTracking
                    ? React.createElement(CopyCell, { value: pkgTracking },
                        React.createElement('a', {
                          href: shipTrackingUrl(pkg.carrier, pkgTracking), target: '_blank', rel: 'noopener',
                          title: 'Track on carrier site',
                          style: { fontFamily: 'inherit', color: C.accent, fontWeight: 500, textDecoration: 'none' } }, pkgTracking))
                    : React.createElement('span', { style: { color: C.text, fontWeight: 500 } }, '\u2014')
                ),
                infoRow('Ship Date', (pkg.shippedTs || pkg.shipDate) ? new Date(pkg.shippedTs || pkg.shipDate).toLocaleDateString() : '\u2014')
              );
            })
          )
        ) : null
      );
    })()
  );
}

// ─── ANALYSIS PAGE (placeholder) ───
// Module-level cache so Analysis data survives navigation
var _analysisCache = { pos: null, products: null };

// Inline editable money cell for the Analysis tables. Module-scoped on purpose (review M6 #77):
// a component type created inside AnalysisPage's render is a NEW type every render, so React
// unmounted every cell (losing an in-progress edit) whenever a toast showed or dismissed.
function EditableMoneyCell(props) {
  var value = props.value;
  var onSave = props.onSave;
  var fmtM = props.fmtM || function(v) { return '$' + Number(v).toFixed(2); };
  var _s = useState(false), editing = _s[0], setEditing = _s[1];
  var _v = useState(''), editVal = _v[0], setEditVal = _v[1];
  var inputRef = useRef(null);
  useEffect(function() { if (editing && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); } }, [editing]);
  var commit = function() {
    setEditing(false);
    var num = parseFloat(editVal);
    if (editVal === '' || editVal === null) { onSave(null); return; }
    if (!isNaN(num) && num !== value) onSave(num);
  };
  if (editing) {
    return React.createElement('input', {
      ref: inputRef,
      type: 'number', step: '0.01', value: editVal,
      onChange: function(e) { setEditVal(e.target.value); },
      onBlur: commit,
      onKeyDown: function(e) { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); },
      onClick: function(e) { e.stopPropagation(); },
      style: { width: 80, padding: '2px 6px', fontSize: 12, fontFamily: 'inherit', background: C.bg, color: C.text, border: '1px solid ' + C.accent, borderRadius: 4, outline: 'none' }
    });
  }
  return React.createElement('span', {
    onClick: function(e) { e.stopPropagation(); setEditVal(value > 0 ? value.toFixed(2) : ''); setEditing(true); },
    title: props.title || undefined,
    style: { fontFamily: 'inherit', cursor: 'pointer', borderBottom: '1px dashed ' + C.textDim, paddingBottom: 1 }
  }, value > 0 ? fmtM(value) : '—');
}

// The Analysis tab's CSV (PO detail › Analysis › Export). Editable on re-import: qty_ordered,
// unit_cost, prep_cost, sell_price and ship_fee — the preview and import routes read those by name
// and ignore every other column. pct_of_case sits beside the quantity for reading only (Anthony
// 2026-09-29: "it has to remain not-changeable upon importing data. It's just so the csv is easily
// visualized"); it is written as a percentage ("50%") and left blank when the line has none.
function buildAnalysisCsv(ad) {
  var isFBM = ad.po && (ad.po.marketplace === 'US-FBM' || (ad.lines.length > 0 && ad.lines[0].is_fbm));
  // Mercado Libre POs carry the ML Code, the number the tab shows in place of the ASIN (Anthony
  // 2026-09-29). Like pct_of_case it is there to be read: the import takes its own columns by name.
  var isMELI = !!(ad.po && (ad.po.is_meli || String(ad.po.wh_code || '').toUpperCase().indexOf('MELI') >= 0));
  var cols = ['line_item_id','sku','vendor_sku','vendor_title','asin','qty_ordered','pct_of_case','unit_cost','prep_cost','sell_price','ship_fee','referral_fee_pct','profit_per_unit','line_cost','line_profit','roi','margin','internal_notes'];
  if (isMELI) cols.splice(cols.indexOf('asin') + 1, 0, 'ml_code');
  var escCsv = escapeCSV; // shared cell rule, formula-safe (review 2026-09-28, medium #31)
  var rows = [cols.join(',')];
  ad.lines.forEach(function(l) {
    var cells = [
      l.id,
      escCsv(l.sku),
      escCsv(l.vendor_sku),
      escCsv(l.vendor_title),
      escCsv(l.asin),
      l.qty_ordered || 0,
      l.pct_of_case === null || l.pct_of_case === undefined || l.pct_of_case === '' ? '' : l.pct_of_case + '%',
      l.unit_cost || 0,
      l.prep_cost || 0,
      l.buy_box_price || 0,
      isFBM ? (l.shipping_fee || 0) : (l.fba_fee || 0),
      l.referral_fee_pct || 0,
      l.profit_per_unit || 0,
      l.line_cost || 0,
      l.line_profit || 0,
      l.roi || 0,
      l.margin || 0,
      escCsv(l.internal_notes || '')
    ];
    if (isMELI) cells.splice(5, 0, escCsv(l.ml_code || ''));
    rows.push(cells.join(','));
  });
  return rows.join('\n');
}

// The case-pack question for a quantity that is not a whole number of cases (Line Items and Analysis
// tabs). Anthony 2026-09-29: all the buttons on one line, Cancel in red, and never offer "round down"
// when that would be zero. Resolves to the quantity to save, or null when cancelled (nothing saved).
async function askCasePackQty(qty, upc) {
  var down = Math.floor(qty / upc) * upc, up = Math.ceil(qty / upc) * upc;
  var cases = function(n) { var c = n / upc; return c + (c === 1 ? ' case' : ' cases'); };
  var options = [];
  if (down > 0) options.push({ label: 'Round down to ' + down + ' (' + cases(down) + ')', qty: down });
  options.push({ label: 'Round up to ' + up + ' (' + cases(up) + ')', qty: up });
  options.push({ label: 'Keep ' + qty, qty: qty });
  var pick = await appChoice(qty + ' units is not a full case (' + upc + ' per case).',
    options.map(function(o) { return o.label; }), { title: 'Not a full case', cancelDanger: true });
  return pick >= 0 && options[pick] ? options[pick].qty : null;
}

// PO Analysis: recompute one line's derived columns after an optimistic edit — the same formula as
// the server's /analysis/po line build (routes/analysis.js: fees by channel, freight = cost × pct,
// profit = buy box + ship revenue − cost − fees − freight − prep), so the row shows the right profit
// before the reload confirms it (Anthony 2026-09-29).
function recalcAnalysisLine(l) {
  var n = function(v) { var x = parseFloat(v); return isNaN(x) ? 0 : x; };
  var r2 = function(x) { return Math.round(x * 100) / 100; };
  var buyBox = n(l.buy_box_price), cost = n(l.unit_cost), qty = parseInt(l.qty_ordered, 10) || 0;
  var refPct = l.referral_fee_pct != null && l.referral_fee_pct !== '' ? n(l.referral_fee_pct) : 15;
  var referral = buyBox * (refPct / 100);
  var fees = (l.is_wfs || l.is_meli) ? n(l.is_wfs ? l.wfs_fee : l.fba_fee) + referral
           : l.is_fbm ? referral + n(l.closing_fee) + n(l.shipping_fee)
           : n(l.fba_fee) + referral + n(l.closing_fee); // storage fee is out of the math (Anthony 2026-10-28)
  var freight = cost * n(l.inbound_freight_pct);
  var profit = buyBox + n(l.shipping_revenue) - cost - fees - freight - n(l.prep_cost);
  return Object.assign({}, l, {
    referral_fee: r2(referral), total_fees: r2(fees), inbound_freight_cost: r2(freight),
    profit_per_unit: r2(profit), roi: r2(cost > 0 ? (profit / cost) * 100 : 0), margin: r2(buyBox > 0 ? (profit / buyBox) * 100 : 0),
    line_cost: r2(cost * qty), line_profit: r2(profit * qty), line_revenue: r2((buyBox + n(l.shipping_revenue)) * qty),
  });
}

// PO detail › Analysis tab summary cards (Anthony 2026-10-28: "separate the fees box into separate
// boxes for each fee"): the single Fees card becomes one card per fee the PO type pays — the
// fulfillment fee (FBA / Ship / WFS / ML Ship) and Referral + Closing together — then Freight and
// Prep, which Est. Profit always subtracted but no card showed. Storage fee is out of the math.
// `s` is the /analysis/po/:id summary; `flags` = { fbm, wfs, meli }.
function poAnalysisSummaryCards(s, flags, fmtM, fmtP, C) {
  var fb = s.fee_breakdown || {};
  var n = function(v) { return Number(v) || 0; };
  var fulfillLabel = flags.wfs ? 'WFS Fee' : flags.meli ? 'ML Ship (est.)' : flags.fbm ? 'Ship Fee' : 'FBA Fee';
  var refLabel = (flags.wfs || flags.meli) ? 'Referral' : 'Referral + Closing';
  return [
    { label: 'Est. Revenue', value: fmtM(s.total_revenue), color: C.text },
    { label: 'Total Cost', value: fmtM(s.total_cost), color: C.text },
    { label: fulfillLabel, value: fmtM(n(fb.fulfillment)), color: C.warning },
    { label: refLabel, value: fmtM(n(fb.referral) + n(fb.closing)), color: C.warning },
    { label: 'Freight', value: fmtM(n(s.total_freight)), color: C.warning },
    { label: 'Prep', value: fmtM(n(s.total_prep)), color: C.warning },
    { label: 'Est. Profit', value: fmtM(s.total_profit), color: s.total_profit >= 0 ? C.success : C.danger },
    { label: 'ROI', value: fmtP(s.roi), color: s.roi >= 30 ? C.success : s.roi >= 0 ? C.warning : C.danger },
    { label: 'Margin', value: fmtP(s.margin), color: s.margin >= 0 ? C.success : C.danger }
  ];
}

// By Product: recompute one row after its price / fee override is typed or cleared — the same
// formula the server's /analysis/products build uses (review M12: the old local recalc dropped
// freight + prep, used the FBA formula for WFS, and a cleared override fell back to $0 instead of
// the source value). value null = override cleared -> the row's source_* value (server-provided).
function recalcAnalysisProductRow(p, field, value) {
  var u = Object.assign({}, p);
  var cleared = value === '' || value === null || value === undefined;
  var v = cleared ? null : parseFloat(value);
  if (field === 'override_buy_box_price') {
    u.override_buy_box_price = v;
    u.buy_box_price = v != null ? v : (Number(u.source_buy_box_price) || 0);
    u.price_source = v != null ? 'Manual' : (u.source_price_source || null);
  } else if (field === 'override_fba_fee') {
    u.override_fba_fee = v;
    var fee = v != null ? v : (Number(u.source_fee) || 0);
    if (u.is_fbm) u.shipping_fee = fee; else u.fba_fee = fee;
  }
  var bb = Number(u.buy_box_price) || 0;
  var cost = Number(u.unit_cost) || 0;
  var refPct = u.referral_fee_pct != null ? Number(u.referral_fee_pct) : 15;
  var refFee = bb * (refPct / 100);
  u.referral_fee = refFee;
  var closing = Number(u.closing_fee) || 0;
  var ship = Number(u.shipping_fee) || 0;
  var fba = Number(u.fba_fee) || 0;
  // storage fee is out of the math (Anthony 2026-10-28)
  u.total_fees = u.is_wfs ? (fba + refFee) : (u.is_fbm ? (refFee + closing + ship) : (fba + refFee + closing));
  var extra = (Number(u.inbound_freight_cost) || 0) + (Number(u.prep_cost) || 0);
  u.profit = bb > 0 ? bb + (Number(u.shipping_revenue) || 0) - cost - u.total_fees - extra : 0;
  u.roi = cost > 0 && bb > 0 ? (u.profit / cost) * 100 : 0;
  u.margin = bb > 0 ? (u.profit / bb) * 100 : 0;
  u.has_data = u.is_wfs ? (bb > 0 && fba > 0) : (bb > 0 && (u.is_fbm ? ship > 0 : fba > 0));
  u.draft_line_profit = u.has_data ? Math.round(u.profit * (Number(u.draft_units) || 0) * 100) / 100 : 0;
  return u;
}
window.recalcAnalysisProductRow = recalcAnalysisProductRow;

function AnalysisPage({ showToast, onNavigate, initialOpts }) {
  const [analysisTab, setAnalysisTabRaw] = useState((initialOpts && initialOpts.analysisTab) || (initialOpts && initialOpts.tab === 'product' ? 'product' : 'po'));
  // Tab changes are written back to the address bar (review M6 #78), mirroring PO detail.
  var setAnalysisTab = function(t) {
    setAnalysisTabRaw(t);
    try { if (window.location.pathname === '/analysis') window.history.replaceState(window.history.state, '', '/analysis' + (t && t !== 'po' ? '?tab=' + t : '')); } catch (e) {}
  };
  const [pos, setPos] = useState(_analysisCache.pos || []);
  const [analysisProducts, setAnalysisProducts] = useState(_analysisCache.products || []);
  const [productsLoaded, setProductsLoaded] = useState(!!_analysisCache.products);
  // Both tabs load in parallel on mount; the full-page "Loading analysis data..." stays up
  // until BOTH are done so the user never lands on a half-ready view.
  const [posLoading, setPosLoading] = useState(!_analysisCache.pos);
  const [productsLoading, setProductsLoading] = useState(!_analysisCache.products);
  var loading = posLoading || productsLoading;
  const [search, setSearch] = useState('');
  const [incompleteOnly, setIncompleteOnly] = useState(!!(initialOpts && initialOpts.incompleteOnly));
  const [mktFilter, setMktFilter] = useState((initialOpts && initialOpts.mktFilter) || '');
  const [fetching, setFetching] = useState(false);
  const [fetchResult, setFetchResult] = useState(null);
  const [selected, setSelectedRaw] = useState({});
  var setSelected = function(val) { setSelectedRaw(val); };
  var lastCheckedRef = useRef(null);
  var sortedDataRef = useRef([]);
  // WFS buy-box scrape poll: stopped when the page unmounts (review L19 — it kept polling and
  // reloading after the user left Analysis).
  var wfsPollRef = useRef(null);
  useEffect(function() { return function() { if (wfsPollRef.current) { clearInterval(wfsPollRef.current); wfsPollRef.current = null; } }; }, []);

  // Load failures are shown (with Retry) instead of an empty list; only the newest request of
  // each kind lands (a slow first load must not overwrite the refresh after a fetch).
  const [loadErr, setLoadErr] = useState(null);
  var posSeqRef = useRef(0), prodSeqRef = useRef(0);
  var loadPos = function(force) {
    if (force || !_analysisCache.pos) setPosLoading(true);
    var seq = ++posSeqRef.current;
    api('/analysis/pos').then(function(data) {
      if (seq !== posSeqRef.current) return;
      setPos(data); _analysisCache.pos = data; setPosLoading(false); setLoadErr(null);
    }).catch(function(e) {
      if (seq !== posSeqRef.current) return;
      setPosLoading(false); setLoadErr('POs: ' + ((e && e.message) || 'request failed'));
    });
  };
  var loadProducts = function(force) {
    if (productsLoaded && !force) return;
    if (force || !_analysisCache.products) setProductsLoading(true);
    var pseq = ++prodSeqRef.current;
    // Fetch products + demand-forecast in parallel. Overlay forecast's raw_reorder_qty/pct_of_case
    // by SKU so this view matches what the Forecast page shows (full velocity cascade, full incoming,
    // safety stock, etc.) — the /products endpoint's simplified formula returns null for any SKU
    // with no 30d sales, even when the SKU has historical sales.
    Promise.all([
      api('/analysis/products' + (force ? '?fresh=1' : '')),
      api('/analysis/demand-forecast').catch(function() { return null; })
    ]).then(function(results) {
      var prods = results[0] || [];
      var fc = results[1];
      var fcList = (fc && Array.isArray(fc.forecast)) ? fc.forecast : [];
      var fcMap = {};
      fcList.forEach(function(f) { if (f && f.sku) fcMap[f.sku] = f; });
      var merged = prods.map(function(p) {
        var ff = fcMap[p.sku];
        if (!ff) return p;
        return Object.assign({}, p, {
          raw_reorder_qty: ff.raw_reorder_qty != null ? ff.raw_reorder_qty : p.raw_reorder_qty,
          pct_of_case: ff.pct_of_case != null ? ff.pct_of_case : p.pct_of_case
        });
      });
      if (pseq !== prodSeqRef.current) return;
      setAnalysisProducts(merged);
      _analysisCache.products = merged;
      setProductsLoaded(true);
      setProductsLoading(false);
      setLoadErr(null);
    }).catch(function(e) {
      if (pseq !== prodSeqRef.current) return;
      setProductsLoading(false); setLoadErr('By Product: ' + ((e && e.message) || 'request failed'));
    });
  };
  // Kick off both fetches on mount so switching tabs is instant once the background load lands.
  useEffect(function() { loadPos(); loadProducts(); }, []);

  // Filter by search + incomplete
  var filtered = pos.filter(function(p) {
    if (incompleteOnly && p.asins_total > 0 && p.asins_with_data >= p.asins_total) return false;
    if (!search) return true;
    var q = search.toLowerCase();
    return (p.po_number || '').toLowerCase().indexOf(q) >= 0 ||
      (p.vendor_name || '').toLowerCase().indexOf(q) >= 0 ||
      (p.wh_code || '').toLowerCase().indexOf(q) >= 0 ||
      (p.marketplace || '').toLowerCase().indexOf(q) >= 0;
  });

  var filteredProducts = analysisProducts.filter(function(p) {
    if (incompleteOnly && p.has_data) return false;
    var wh = (p.wh_code || '').toUpperCase();
    var isWFS = wh.indexOf('WFS') >= 0;
    if (mktFilter === 'FBM' && !p.is_fbm) return false;
    if (mktFilter === 'WFS' && !isWFS) return false;
    if (mktFilter && mktFilter !== 'FBM' && mktFilter !== 'WFS' && ((p.marketplace || 'US') !== mktFilter || p.is_fbm || isWFS)) return false;
    if (!search) return true;
    var q = search.toLowerCase();
    return (p.sku || '').toLowerCase().indexOf(q) >= 0 ||
      (p.vendor_title || '').toLowerCase().indexOf(q) >= 0 ||
      (p.asin || '').toLowerCase().indexOf(q) >= 0 ||
      (p.vendor_name || '').toLowerCase().indexOf(q) >= 0;
  });

  var selectedIds = Object.keys(selected).filter(function(k) { return selected[k]; }).map(Number);
  var selCount = selectedIds.length;

  var activeList = analysisTab === 'po' ? filtered : filteredProducts;

  var toggleOne = function(id, shiftKey) {
    if (shiftKey) window.getSelection().removeAllRanges();
    var next = Object.assign({}, selected);
    if (shiftKey && lastCheckedRef.current !== null) {
      var visibleData = sortedDataRef.current || activeList;
      var lastIdx = visibleData.findIndex(function(o) { return o.id === lastCheckedRef.current; });
      var curIdx = visibleData.findIndex(function(o) { return o.id === id; });
      if (lastIdx !== -1 && curIdx !== -1) {
        var start = Math.min(lastIdx, curIdx);
        var end = Math.max(lastIdx, curIdx);
        var newVal = !selected[id];
        for (var i = start; i <= end; i++) { next[visibleData[i].id] = newVal; }
      }
    } else {
      next[id] = !next[id];
    }
    if (!next[id]) delete next[id];
    lastCheckedRef.current = id;
    setSelected(next);
  };

  var toggleAll = function() {
    if (selCount === activeList.length && activeList.length > 0) { setSelected({}); }
    else { var next = {}; activeList.forEach(function(p) { next[p.id] = true; }); setSelected(next); }
  };

  // Collect ASINs from selected POs (shared helper)
  var collectAsins = async function(list) {
    var usAsins = []; var caAsins = [];
    for (var i = 0; i < list.length; i++) {
      var poData = await api('/analysis/po/' + list[i].id);
      if (poData && poData.lines) {
        poData.lines.forEach(function(l) {
          if (!l.asin) return;
          if (poData.po.domain === 1 && usAsins.indexOf(l.asin) === -1) usAsins.push(l.asin);
          if (poData.po.domain === 6 && caAsins.indexOf(l.asin) === -1) caAsins.push(l.asin);
        });
      }
    }
    return { usAsins: usAsins, caAsins: caAsins };
  };

  // Collect ASINs from selected products (product tab)
  var collectProductAsins = function() {
    var list = selCount > 0 ? analysisProducts.filter(function(p) { return selected[p.id]; }) : filteredProducts;
    var usAsins = []; var caAsins = [];
    list.forEach(function(p) {
      if (!p.asin) return;
      if (p.marketplace === 'CA') { if (caAsins.indexOf(p.asin) === -1) caAsins.push(p.asin); }
      else { if (usAsins.indexOf(p.asin) === -1) usAsins.push(p.asin); }
    });
    return { usAsins: usAsins, caAsins: caAsins };
  };

  // Fetch Keepa for selected POs or products
  var fetchKeepa = async function() {
    setFetching('keepa'); setFetchResult(null);
    try {
      var asins;
      if (analysisTab === 'product') {
        asins = collectProductAsins();
      } else {
        var list = selCount > 0 ? pos.filter(function(p) { return selected[p.id]; }) : filtered;
        if (!list.length) { showToast('No POs to fetch', 'error'); setFetching(false); return; }
        asins = await collectAsins(list);
      }
      var total = asins.usAsins.length + asins.caAsins.length;
      if (!total) { setFetchResult({ success: false, message: 'No ASINs found' }); setFetching(false); return; }
      var fetched = 0; var kNotFound = 0; var kNoPrice = 0; var kSkipped = 0; var kPartial = false; var kTotal = total;
      if (asins.usAsins.length) { var r = await api('/analysis/keepa-fetch', { method: 'POST', body: JSON.stringify({ asins: asins.usAsins, domain: 1 }) }); fetched += r.fetched; kNotFound += (r.not_found || 0); kNoPrice += (r.no_price || 0); kSkipped += (r.skipped_fresh || 0); if (r.partial) kPartial = true; }
      if (asins.caAsins.length) { var r2 = await api('/analysis/keepa-fetch', { method: 'POST', body: JSON.stringify({ asins: asins.caAsins, domain: 6 }) }); fetched += r2.fetched; kNotFound += (r2.not_found || 0); kNoPrice += (r2.no_price || 0); kSkipped += (r2.skipped_fresh || 0); if (r2.partial) kPartial = true; }
      var kMsg = 'Fetched ' + fetched + '/' + kTotal + ' ASINs from Keepa.';
      if (kSkipped) kMsg += ' ' + kSkipped + ' skipped (data < 7 days old).';
      if (kNotFound) kMsg += ' ' + kNotFound + ' not found.';
      if (kNoPrice) kMsg += ' ' + kNoPrice + ' no price.';
      if (kPartial) kMsg += ' (Stopped early — tokens depleted.)';
      setFetchResult({ success: !kPartial, message: kMsg });
      if (analysisTab === 'product') { loadProducts(true); } else { loadPos(); }
    } catch (e) { setFetchResult({ success: false, message: e.message }); }
    setFetching(false);
  };

  // Fetch SP-API for selected POs or products
  var fetchSpApi = async function() {
    setFetching('spapi'); setFetchResult(null);
    try {
      var asins;
      if (analysisTab === 'product') {
        asins = collectProductAsins();
      } else {
        var list = selCount > 0 ? pos.filter(function(p) { return selected[p.id]; }) : filtered;
        if (!list.length) { showToast('No POs to fetch', 'error'); setFetching(false); return; }
        asins = await collectAsins(list);
      }
      var total = asins.usAsins.length + asins.caAsins.length;
      if (!total) { setFetchResult({ success: false, message: 'No ASINs found' }); setFetching(false); return; }
      var fetched = 0; var sNoPrice = 0;
      for (var j = 0; j < asins.usAsins.length; j += 20) {
        var batch = asins.usAsins.slice(j, j + 20);
        var r = await api('/analysis/sp-api-fetch', { method: 'POST', body: JSON.stringify({ asins: batch, domain: 1 }) });
        fetched += r.fetched; sNoPrice += (r.no_price || 0);
      }
      for (var k = 0; k < asins.caAsins.length; k += 20) {
        var batch2 = asins.caAsins.slice(k, k + 20);
        var r2 = await api('/analysis/sp-api-fetch', { method: 'POST', body: JSON.stringify({ asins: batch2, domain: 6 }) });
        fetched += r2.fetched; sNoPrice += (r2.no_price || 0);
      }
      var sMsg = 'Fetched ' + fetched + ' ASINs from SP-API.';
      if (sNoPrice) sMsg += ' ' + sNoPrice + ' no price.';
      setFetchResult({ success: true, message: sMsg });
      if (analysisTab === 'product') { loadProducts(true); } else { loadPos(); }
    } catch (e) { setFetchResult({ success: false, message: e.message }); }
    setFetching(false);
  };

  // Fetch ShipStation data for FBM draft POs
  var fetchShipStation = async function() {
    setFetching('shipstation'); setFetchResult(null);
    try {
      var body = {};
      if (selCount > 0 && analysisTab === 'po') {
        body.po_ids = selectedIds;
      } else if (selCount > 0 && analysisTab === 'product') {
        body.product_ids = selectedIds;
      }
      var result = await api('/analysis/apply-shipstation-bulk', { method: 'POST', body: JSON.stringify(body) });
      var msg = result.matched + '/' + result.total + ' SKUs matched from ShipStation data across ' + result.po_count + ' FBM PO(s).';
      if (result.not_found) msg += ' ' + result.not_found + ' not found.';
      setFetchResult({ success: true, message: msg });
      if (analysisTab === 'product') { loadProducts(true); } else { loadPos(); }
    } catch (e) { setFetchResult({ success: false, message: e.message }); }
    setFetching(false);
  };

  // Fetch Walmart Items API data for WFS products
  var fetchWalmartData = async function() {
    setFetching('walmart'); setFetchResult(null);
    try {
      // Collect WFS SKUs from filtered products or POs
      var skus = [];
      if (analysisTab === 'product') {
        var list = selCount > 0 ? analysisProducts.filter(function(p) { return selected[p.id]; }) : filteredProducts;
        list.forEach(function(p) {
          var wh = (p.wh_code || '').toUpperCase();
          if (wh.indexOf('WFS') >= 0 && p.sku && skus.indexOf(p.sku) < 0) skus.push(p.sku);
        });
      } else {
        var poList = selCount > 0 ? pos.filter(function(p) { return selected[p.id]; }) : filtered;
        var wfsPOs = poList.filter(function(p) { return (p.wh_code || '').toUpperCase().indexOf('WFS') >= 0; });
        for (var i = 0; i < wfsPOs.length; i++) {
          var poData = await api('/analysis/po/' + wfsPOs[i].id);
          if (poData && poData.lines) {
            poData.lines.forEach(function(l) {
              var sku = l.product_sku || l.sku;
              if (sku && skus.indexOf(sku) < 0) skus.push(sku);
            });
          }
        }
      }
      if (!skus.length) { setFetchResult({ success: false, message: 'No WFS SKUs found' }); setFetching(false); return; }
      var result = await api('/analysis/wfs-fee-fetch', { method: 'POST', body: JSON.stringify({ skus: skus }) });
      var msg = wfsFetchSummary(result, skus.length);
      var pollSkus = result.scrape_skus && result.scrape_skus.length ? result.scrape_skus : skus; // Own SKUs never get a buy box
      if (result.scraping) {
        msg += ' Scraping buy box prices...';
        setFetchResult({ success: true, message: msg });
        setFetching('walmart-bb');
        // Poll until buy box prices are populated
        var pollCount = 0;
        var pollMax = 60; // 60 polls x 2 s = 2 min
        if (wfsPollRef.current) clearInterval(wfsPollRef.current);
        var pollInterval = setInterval(async function() {
          pollCount++;
          try {
            var check = await api('/analysis/wfs-bb-status?skus=' + encodeURIComponent(pollSkus.join(',')));
            if (wfsPollRef.current !== pollInterval) return; // page left (unmount cleared it) or a newer poll took over
            if (check.done || pollCount >= pollMax) {
              clearInterval(pollInterval); wfsPollRef.current = null;
              setFetchResult({ success: true, message: wfsFetchSummary(result, skus.length) + ' Buy box prices: ' + check.scraped + '/' + check.total + ' complete.' });
              setFetching(false);
              if (analysisTab === 'product') { loadProducts(true); } else { loadPos(); }
            }
          } catch (e) {
            if (pollCount >= pollMax && wfsPollRef.current === pollInterval) { clearInterval(pollInterval); wfsPollRef.current = null; setFetching(false); }
          }
        }, 2000);
        wfsPollRef.current = pollInterval;
      } else {
        setFetchResult({ success: result.errors === 0, message: msg });
        if (analysisTab === 'product') { loadProducts(true); } else { loadPos(); }
        setFetching(false);
      }
    } catch (e) { setFetchResult({ success: false, message: e.message }); setFetching(false); }
  };

  // Export ASINs as CSV for Keepa Product Viewer
  var exportAsins = function(domain) {
    if (analysisTab === 'product') {
      // Export from filtered client-side data
      var list = selCount > 0 ? analysisProducts.filter(function(p) { return selected[p.id]; }) : filteredProducts;
      var seen = {};
      var csv = 'ASIN,Marketplace\n';
      list.forEach(function(p) {
        if (!p.asin) return;
        var mkt = p.marketplace || 'US';
        if (seen[p.asin]) return;
        seen[p.asin] = true;
        csv += p.asin + ',' + (mkt === 'CA' ? 'Amazon.ca' : 'Amazon.com') + '\n';
      });
      var label = mktFilter || 'ALL';
      var blob = new Blob([csv], { type: 'text/csv' });
      var url = URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url;
      a.download = 'asins-' + label + '-' + new Date().toISOString().slice(0, 10) + '.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } else {
      var url = '/api/analysis/export-asins?domain=' + domain;
      var a = document.createElement('a');
      a.href = url;
      a.download = 'asins-' + (domain === 1 ? 'US' : domain === 6 ? 'CA' : 'ALL') + '.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  // Import Keepa Product Viewer CSV
  var importFileRef = useRef(null);
  var handleKeepaImport = async function(e) {
    var file = e.target.files && e.target.files[0];
    if (!file) return;
    setFetching('import'); setFetchResult(null);
    try {
      var domainParam = mktFilter === 'CA' ? '?domain=6' : mktFilter === 'US' ? '?domain=1' : '';
      var result = await apiUpload('/analysis/keepa-import' + domainParam, file);
      setFetchResult({ success: true, message: 'Imported ' + result.imported + ' ASINs from Keepa CSV (' + result.skipped + ' skipped).' });
      if (analysisTab === 'product') { loadProducts(true); } else { loadPos(); }
    } catch (err) { setFetchResult({ success: false, message: err.message }); }
    setFetching(false);
    if (importFileRef.current) importFileRef.current.value = '';
  };

  // Approve selected POs (change status from draft → approved)
  var approveSelected = async function() {
    var list = selCount > 0 ? pos.filter(function(p) { return selected[p.id]; }) : [];
    if (!list.length) { showToast('Select POs to approve', 'error'); return; }
    if (!(await appConfirm('Approve ' + list.length + ' PO(s)? They will be removed from the Analysis module.'))) return;
    try {
      for (var i = 0; i < list.length; i++) {
        await api('/purchase-orders/' + list[i].id, { method: 'PUT', body: JSON.stringify({ status: 'approved' }) });
      }
      showToast(list.length + ' PO(s) approved');
      setSelected({});
      loadPos();
    } catch (err) { showToast(err.message, 'error'); }
  };

  // Move selected products to "Next" PO
  var moveToNext = async function() {
    var ids = selectedIds;
    if (!ids.length) { showToast('Select products to move', 'error'); return; }
    if (!(await appConfirm('Move ' + ids.length + ' product(s) to Next PO?\n\nItems will be removed from their current draft POs and placed into a Next PO for each vendor/warehouse combination.'))) return;
    setFetching('move'); setFetchResult(null);
    try {
      var result = await api('/analysis/products/move-to-next', { method: 'POST', body: JSON.stringify({ product_ids: ids }) });
      var msg = 'Moved ' + result.moved + ' item(s) to ' + result.next_pos.length + ' Next PO(s): ';
      msg += result.next_pos.map(function(p) { return p.po_number + ' (' + p.sku_count + ' SKUs)'; }).join(', ');
      var skippedMv = (result.skipped || []).map(function(s) { return s.sku + (s.po_number ? ' on ' + s.po_number : ''); });
      if (skippedMv.length) msg += '. Not moved (has receipts): ' + skippedMv.join(', ');
      setFetchResult({ success: !skippedMv.length, message: msg });
      setSelected({});
      loadProducts(true);
    } catch (e) { setFetchResult({ success: false, message: e.message }); }
    setFetching(false);
  };

  var fmtM = function(v) { return v != null ? '$' + Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'; };
  var fmtP = function(v) { return v != null ? Number(v).toFixed(1) + '%' : '—'; };
  var fmtN = function(v) { return v != null ? Number(v).toLocaleString() : '—'; };

  var statusClr = function(s) { return { draft: '#F59E0B', submitted: '#3B82F6', approved: '#22C55E', confirmed: '#14B8A6', next: '#06B6D4', shipped: C.accent, 'checked-in': C.purple || '#8B5CF6', receiving: C.warning, received: C.success, closed: C.textDim, cancelled: C.danger }[s] || C.textMuted; };

  var analysisCols = [
    { key: 'select', label: '', locked: true, noFilter: true, style: { width: 44 },
      headerRender: function() {
        var allChecked = filtered.length > 0 && filtered.every(function(r) { return selected[r.id]; });
        var someChecked = !allChecked && filtered.some(function(r) { return selected[r.id]; });
        return React.createElement('div', {
          onClick: function(e) { e.stopPropagation(); toggleAll(); },
          style: { width: 32, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', userSelect: 'none' }
        },
          React.createElement('div', { style: { width: 18, height: 18, borderRadius: 4, border: '2px solid ' + (allChecked || someChecked ? C.accent : C.textDim), background: allChecked ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' } },
            allChecked && React.createElement(LIcon, { name: 'check', size: 12, color: '#fff' }),
            someChecked && !allChecked && React.createElement('span', { style: { color: C.accent, fontSize: 12, fontWeight: 700, lineHeight: 1 } }, '–')
          )
        );
      },
      render: function(r) {
        var checked = !!selected[r.id];
        return React.createElement('div', {
          onMouseDown: function(e) { if (e.shiftKey) e.preventDefault(); },
          onClick: function(e) { e.stopPropagation(); toggleOne(r.id, e.shiftKey); },
          style: { width: 32, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', userSelect: 'none' }
        }, React.createElement('div', { style: { width: 18, height: 18, borderRadius: 4, border: '2px solid ' + (checked ? C.accent : C.textDim), background: checked ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' } },
          checked && React.createElement(LIcon, { name: 'check', size: 12, color: '#fff' })
        ));
      } },
    { key: 'po_number', label: 'PO #', locked: true, copyable: true, render: function(r) { return React.createElement('a', { href: '/po/' + r.id, onClick: function(e) { e.preventDefault(); }, style: { fontWeight: 600, color: C.accent, textDecoration: 'none' } }, r.po_number); } },
    { key: 'po_date', label: 'PO Date', render: function(r) { return fmtDateOnly(r.po_date) || '—'; }, sortKey: 'po_date' },
    { key: 'vendor_name', label: 'Vendor', copyable: true, render: function(r) { return r.vendor_name || '—'; } },
    { key: 'wh_code', label: 'Warehouse', copyable: true, render: function(r) { return React.createElement('span', { style: { fontFamily: 'inherit', fontSize: 11 } }, r.wh_code || '—'); } },
    { key: 'payment_method', label: 'Payment', copyable: true, render: function(r) { return r.payment_method || '—'; } },
    { key: 'marketplace', label: 'Mkt', defaultVisible: false, render: function(r) {
      return React.createElement('span', { style: { padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600,
        background: r.marketplace === 'US' ? '#3B82F620' : '#EF444420', color: r.marketplace === 'US' ? '#3B82F6' : '#EF4444' } },
        r.marketplace || 'US');
    } },
    { key: 'status', label: 'Status', defaultVisible: false, render: function(r) {
      var sc = statusClr(r.status);
      return React.createElement('span', { style: { padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 600, background: sc + '18', color: sc } }, r.status);
    } },
    { key: 'sku_count', label: 'SKUs', defaultVisible: false, render: function(r) { return React.createElement('span', { style: { fontFamily: 'inherit' } }, fmtN(r.sku_count)); } },
    { key: 'total_units', label: 'Units', defaultVisible: false, render: function(r) { return React.createElement('span', { style: { fontFamily: 'inherit' } }, fmtN(r.total_units)); } },
    { key: 'total_cost', label: 'Total Cost', render: function(r) { return React.createElement('span', { style: { fontFamily: 'inherit' } }, fmtM(r.total_cost)); } },
    { key: 'total_revenue', label: 'Est. Revenue', defaultVisible: false, render: function(r) { return React.createElement('span', { style: { fontFamily: 'inherit' } }, r.asins_with_data > 0 ? fmtM(r.total_revenue) : '—'); } },
    { key: 'total_fees', label: 'Total Fees', defaultVisible: false, render: function(r) { return React.createElement('span', { style: { fontFamily: 'inherit' } }, r.asins_with_data > 0 ? fmtM(r.total_fees) : '—'); } },
    { key: 'total_referral_fees', label: 'Referral', render: function(r) { return React.createElement('span', { style: { fontFamily: 'inherit' } }, r.asins_with_data > 0 ? fmtM(r.total_referral_fees) : '—'); } },
    { key: 'total_ship_fees', label: 'Ship/FBA Fee', render: function(r) { return React.createElement('span', { style: { fontFamily: 'inherit' } }, r.asins_with_data > 0 ? fmtM(r.total_ship_fees) : '—'); } },
    { key: 'total_profit', label: 'Total Profit', render: function(r) {
      if (!r.asins_with_data) return '—';
      var color = r.total_profit > 0 ? C.success : r.total_profit < 0 ? C.danger : C.textMuted;
      return React.createElement('span', { style: { fontFamily: 'inherit', fontWeight: 600, color: color } }, fmtM(r.total_profit));
    } },
    { key: 'roi', label: 'ROI %', render: function(r) {
      if (!r.asins_with_data) return '—';
      var color = r.roi >= 30 ? C.success : r.roi >= 0 ? C.warning : C.danger;
      return React.createElement('span', { style: { fontFamily: 'inherit', fontWeight: 600, color: color } }, fmtP(r.roi));
    } },
    { key: 'margin', label: 'Margin %', render: function(r) {
      if (!r.asins_with_data) return '—';
      var color = r.margin >= 0 ? C.success : C.danger;
      return React.createElement('span', { style: { fontFamily: 'inherit', fontWeight: 600, color: color } }, fmtP(r.margin));
    } },
    { key: 'asins_with_data', label: 'Products with Data', defaultVisible: false, render: function(r) {
      var dataText = r.asins_with_data + '/' + r.asins_total;
      return React.createElement('span', { style: { fontSize: 11, color: r.asins_with_data === r.asins_total ? C.success : C.warning } }, dataText);
    } },
  ];

  // Save product override to all draft PO line items
  var saveProductOverride = function(productId, field, value) {
    var body = {};
    body[field] = value === '' || value === null ? null : parseFloat(value);
    api('/analysis/products/' + productId + '/overrides', { method: 'PUT', body: JSON.stringify(body) }).then(function(r) {
      // Update local state so the UI reflects the change immediately
      // Same formula as the server build (recalcAnalysisProductRow, module scope above)
      setAnalysisProducts(function(prev) { var result = prev.map(function(p) {
        if (p.id !== productId) return p;
        return recalcAnalysisProductRow(p, field, body[field]);
      }); _analysisCache.products = result; return result; });
      showToast('Updated ' + r.updated + ' line item(s) across ' + r.pos_affected + ' PO(s)');
    }).catch(function(e) { showToast(e.message, 'error'); });
  };

  // (EditableMoneyCell is module-scoped — see above AnalysisPage. Declaring it inside the page
  //  gave every parent render a new component type, remounting every cell mid-edit. Review M6 #77.)

  // ─── Export By Product to Excel ───
  var exportByPoExcel = function() {
    var list = selCount > 0 ? filtered.filter(function(r) { return selected[r.id]; }) : (sortedDataRef.current || filtered);
    if (!list.length) return;
    var rows = list.map(function(r) {
      return {
        'PO #': r.po_number || '',
        'PO Date': fmtDateOnly(r.po_date),
        'Vendor': r.vendor_name || '',
        'Warehouse': r.wh_code || '',
        'Payment': r.payment_method || '',
        'Marketplace': r.marketplace || '',
        'SKUs': r.sku_count || 0,
        'Units': r.total_units || 0,
        'Total Cost': r.total_cost || 0,
        'Est. Revenue': r.asins_with_data > 0 ? r.total_revenue : '',
        'Total Fees': r.asins_with_data > 0 ? r.total_fees : '',
        'Referral Fees': r.asins_with_data > 0 ? r.total_referral_fees : '',
        'Ship/FBA Fee': r.asins_with_data > 0 ? r.total_ship_fees : '',
        'Total Profit': r.asins_with_data > 0 ? r.total_profit : '',
        'ROI %': r.asins_with_data > 0 ? r.roi : '',
        'Margin %': r.asins_with_data > 0 ? r.margin : '',
        'Products w/ Data': (r.asins_with_data || 0) + '/' + (r.asins_total || 0)
      };
    });
    var ws = XLSX.utils.json_to_sheet(rows);
    var wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'By PO');
    XLSX.writeFile(wb, 'Analysis_By_PO_' + new Date().toISOString().slice(0, 10) + '.xlsx');
  };

  var exportByProductExcel = function() {
    var data = sortedDataRef.current || filteredProducts;
    if (!data.length) return;
    var rows = data.map(function(r) {
      return {
        'PO #': (r.draft_pos || []).map(function(p) { return p.po_number; }).join(', '),
        'SKU': r.sku,
        'Title': r.vendor_title || '',
        'ASIN': r.asin || '',
        'Vendor': r.vendor_name || '',
        'Warehouse': r.wh_code || '',
        'Marketplace': r.marketplace || '',
        'Cost': r.unit_cost || 0,
        'Sell Price': r.buy_box_price || 0,
        'FBA Fee': r.is_fbm ? (r.shipping_fee || 0) : (r.fba_fee || 0),
        'Referral Fee': r.referral_fee || 0,
        'Total Fees': r.total_fees || 0,
        'Profit/Unit': r.has_data ? r.profit : '',
        'ROI %': r.has_data ? Math.round(r.roi * 100) / 100 : '',
        'Margin %': r.has_data ? Math.round(r.margin * 100) / 100 : '',
        'Total Units': r.draft_units || 0,
        'Total Line Cost': r.draft_line_cost || 0,
        'Total Line Profit': r.has_data ? r.draft_line_profit : '',
        '% of Case': r.pct_of_case != null ? r.pct_of_case : '',
        'On Hand': r.on_hand || 0,
        '30d Units': r.units_30d || 0,
        'Velocity': r.velocity ? Math.round(r.velocity * 100) / 100 : 0,
        'Returns 365d': r.returned_365d || 0,
        'Sold 365d': r.sold_365d || 0,
        'Return Rate %': r.return_rate_pct != null ? r.return_rate_pct : ''
      };
    });
    var ws = XLSX.utils.json_to_sheet(rows);
    var wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'By Product');
    XLSX.writeFile(wb, 'Analysis_By_Product_' + new Date().toISOString().slice(0, 10) + '.xlsx');
  };

  // ─── Product columns ───
  var productCols = [
    { key: 'select', label: '', locked: true, noFilter: true, style: { width: 44 },
      headerRender: function() {
        var allChecked = filteredProducts.length > 0 && filteredProducts.every(function(r) { return selected[r.id]; });
        var someChecked = !allChecked && filteredProducts.some(function(r) { return selected[r.id]; });
        return React.createElement('div', {
          onClick: function(e) { e.stopPropagation(); toggleAll(); },
          style: { width: 32, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', userSelect: 'none' }
        },
          React.createElement('div', { style: { width: 18, height: 18, borderRadius: 4, border: '2px solid ' + (allChecked || someChecked ? C.accent : C.textDim), background: allChecked ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' } },
            allChecked && React.createElement(LIcon, { name: 'check', size: 12, color: '#fff' }),
            someChecked && !allChecked && React.createElement('span', { style: { color: C.accent, fontSize: 12, fontWeight: 700, lineHeight: 1 } }, '–')
          )
        );
      },
      render: function(r) {
        var checked = !!selected[r.id];
        return React.createElement('div', {
          onMouseDown: function(e) { if (e.shiftKey) e.preventDefault(); },
          onClick: function(e) { e.stopPropagation(); toggleOne(r.id, e.shiftKey); },
          style: { width: 32, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', userSelect: 'none' }
        }, React.createElement('div', { style: { width: 18, height: 18, borderRadius: 4, border: '2px solid ' + (checked ? C.accent : C.textDim), background: checked ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' } },
          checked && React.createElement(LIcon, { name: 'check', size: 12, color: '#fff' })
        ));
      } },
    { key: 'draft_pos', label: 'PO #', locked: true, copyable: true,
      copyValue: function(r) { return (r.draft_pos || []).map(function(po) { return po.po_number; }).join(', '); },
      render: function(r) {
      var pos = r.draft_pos || [];
      if (!pos.length) return '—';
      return React.createElement('span', null, pos.map(function(po, i) {
        return [i > 0 ? ', ' : null, React.createElement('a', { key: po.id, href: '#', onClick: function(e) { e.preventDefault(); e.stopPropagation(); onNavigate('po-detail', po.id, { tab: 'analysis', from: 'analysis', analysisTab: analysisTab, mktFilter: mktFilter, incompleteOnly: incompleteOnly }); }, style: { fontWeight: 600, color: C.accent, cursor: 'pointer', textDecoration: 'none' } }, po.po_number)];
      }));
    } },
    { key: 'sku', label: 'SKU', locked: true, copyable: true, stopClick: true, render: function(r) { return React.createElement('span', { onClick: function(e) { e.stopPropagation(); onNavigate('products', r.sku || r.id, { returnTo: 'analysis', returnOpts: { analysisTab: analysisTab, mktFilter: mktFilter, incompleteOnly: incompleteOnly } }); }, style: { fontWeight: 600, color: C.accent, fontFamily: 'inherit', fontSize: 11, cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: '3px' } }, r.sku); } },
    { key: 'vendor_title', label: 'Title', render: function(r) { return React.createElement('span', { style: { fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' } }, r.vendor_title || '—'); } },
    { key: 'asin', label: mktFilter === 'WFS' ? 'Walmart Item#' : 'ASIN', copyable: true, render: function(r) {
      if (mktFilter === 'WFS' && r.walmart_item_id) return React.createElement('a', { href: 'https://www.walmart.com/ip/' + r.walmart_item_id, target: '_blank', onClick: function(e) { e.stopPropagation(); }, style: { color: C.accent, fontFamily: 'inherit', fontSize: 11, textDecoration: 'none' } }, r.walmart_item_id);
      return r.asin ? React.createElement('a', { href: 'https://www.amazon' + (r.domain === 6 ? '.ca' : '.com') + '/dp/' + r.asin, target: '_blank', onClick: function(e) { e.stopPropagation(); }, style: { color: C.accent, fontFamily: 'inherit', fontSize: 11 } }, r.asin) : '—';
    } },
    { key: 'vendor_name', label: 'Vendor', copyable: true, render: function(r) { return r.vendor_name || '—'; } },
    { key: 'internal_notes', label: 'Internal Notes', defaultVisible: false, render: function(r) { return r.internal_notes ? React.createElement('span', { style: { fontSize: 11, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }, title: r.internal_notes }, r.internal_notes) : ''; } },
    { key: 'wh_code', label: 'Warehouse', defaultVisible: false, copyable: true, render: function(r) { return React.createElement('span', { style: { fontFamily: 'inherit', fontSize: 11 } }, r.wh_code || '—'); } },
    { key: 'marketplace', label: 'Mkt', defaultVisible: false, render: function(r) {
      return React.createElement('span', { style: { padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600,
        background: r.marketplace === 'US' ? '#3B82F620' : '#EF444420', color: r.marketplace === 'US' ? '#3B82F6' : '#EF4444' } }, r.marketplace || 'US');
    } },
    { key: 'unit_cost', label: 'Cost', render: function(r) { return React.createElement('span', { style: { fontFamily: 'inherit' } }, fmtM(r.unit_cost)); } },
    { key: 'inbound_freight_cost', label: 'Freight', defaultVisible: false, render: function(r) {
      if (!r.inbound_freight_cost && !r.inbound_freight_pct) return React.createElement('span', { style: { color: C.textMuted, fontSize: 12 } }, '\u2014');
      return React.createElement('span', { style: { fontFamily: 'inherit', fontSize: 12 }, title: (r.inbound_freight_pct * 100).toFixed(1) + '% of unit cost' }, fmtM(r.inbound_freight_cost || 0));
    } },
    { key: 'prep_cost', label: 'Prep', defaultVisible: false, render: function(r) {
      return React.createElement('span', { style: { fontFamily: 'inherit', fontSize: 12 } }, r.prep_cost > 0 ? fmtM(r.prep_cost) : '\u2014');
    } },
    { key: 'buy_box_price', label: 'Sell Price', stopClick: true, render: function(r) { return React.createElement(EditableMoneyCell, { fmtM: fmtM, value: r.buy_box_price || 0, title: r.price_source ? 'Source: ' + r.price_source : undefined, onSave: function(v) { saveProductOverride(r.id, 'override_buy_box_price', v); } }); } },
    { key: 'referral_fee', label: 'Referral', render: function(r) { return React.createElement('span', { style: { fontFamily: 'inherit' } }, r.has_data ? fmtM(r.referral_fee) : '—'); } },
    { key: 'ship_fba_fee', label: 'Ship/FBA Fee', stopClick: true, render: function(r) { return React.createElement(EditableMoneyCell, { fmtM: fmtM, value: r.is_fbm ? (r.shipping_fee || 0) : (r.fba_fee || 0), onSave: function(v) { saveProductOverride(r.id, 'override_fba_fee', v); } }); } },
    { key: 'total_fees', label: 'Total Fees', defaultVisible: false, render: function(r) { return React.createElement('span', { style: { fontFamily: 'inherit' } }, r.has_data ? fmtM(r.total_fees) : '—'); } },
    { key: 'profit', label: 'Profit/Unit', render: function(r) {
      if (!r.has_data) return '—';
      var color = r.profit > 0 ? C.success : r.profit < 0 ? C.danger : C.textMuted;
      return React.createElement('span', { style: { fontFamily: 'inherit', fontWeight: 600, color: color } }, fmtM(r.profit));
    } },
    { key: 'roi', label: 'ROI %', render: function(r) {
      if (!r.has_data) return '—';
      var color = r.roi >= 30 ? C.success : r.roi >= 0 ? C.warning : C.danger;
      return React.createElement('span', { style: { fontFamily: 'inherit', fontWeight: 600, color: color } }, fmtP(r.roi));
    } },
    { key: 'margin', label: 'Margin %', render: function(r) {
      if (!r.has_data) return '—';
      var color = r.margin >= 0 ? C.success : C.danger;
      return React.createElement('span', { style: { fontFamily: 'inherit', fontWeight: 600, color: color } }, fmtP(r.margin));
    } },
    { key: 'return_rate_pct', label: 'Return Rate', headerRender: function() { return React.createElement('span', { title: 'Customer returns \u00f7 units sold (last 365 days), aggregated across all channels (Amazon FBA+FBM, Walmart, eBay, SHEIN). Alias SKUs roll up to canonical.' }, 'Return Rate'); }, render: function(r) {
      if (r.return_rate_pct == null) return React.createElement('span', { style: { color: C.textMuted, fontSize: 12 } }, '\u2014');
      var pct = r.return_rate_pct;
      var color = pct >= 10 ? '#F87171' : pct >= 5 ? '#FBBF24' : '#34D399';
      var soldCanonical = r.sold_365d_canonical != null ? r.sold_365d_canonical : r.sold_365d;
      var tip = (r.returned_365d || 0) + ' returned \u00f7 ' + (soldCanonical || 0) + ' sold (365d, canonical SKU)';
      return React.createElement('span', { style: { fontFamily: 'inherit', fontWeight: 600, color: color }, title: tip }, pct + '%');
    } },
    { key: 'returned_365d', label: 'Returns 365d', defaultVisible: false, render: function(r) {
      var returned = r.returned_365d || 0;
      var soldCanonical = r.sold_365d_canonical != null ? r.sold_365d_canonical : (r.sold_365d || 0);
      if (returned === 0 && soldCanonical === 0) return React.createElement('span', { style: { color: C.textMuted, fontSize: 12 } }, '\u2014');
      var pct = r.return_rate_pct;
      var color = pct == null ? C.text : (pct >= 10 ? '#F87171' : pct >= 5 ? '#FBBF24' : '#34D399');
      return React.createElement('span', { style: { fontFamily: 'inherit', fontWeight: 600, color: color }, title: returned + ' returned \u00f7 ' + soldCanonical + ' sold over 365d (canonical)' + (pct != null ? ' = ' + pct + '%' : '') }, returned + '/' + soldCanonical);
    } },


    { key: 'pct_of_case', label: '% of Case', render: function(r) {
      if (r.pct_of_case === null || r.pct_of_case === undefined) return React.createElement('span', { style: { color: C.textMuted, fontSize: 12 } }, '\u2014');
      var pct = r.pct_of_case;
      if (pct === 0) return React.createElement('span', { style: { fontSize: 12, color: C.textMuted, fontStyle: 'italic' }, title: 'No forecast history \u2014 seeded as No-Sales SKU (case pack / MOQ)' }, '0%');
      var color = pct < 50 ? '#F472B6' : pct < 100 ? '#FBBF24' : '#34D399';
      var upc2 = r.case_qty || 0;
      var derivedTarget2 = upc2 > 0 ? Math.round(pct * upc2 / 100) : 0;
      return React.createElement('span', { style: { fontSize: 12, fontWeight: pct < 50 ? 700 : 400, color: color }, title: 'Velocity-based target ' + derivedTarget2 + ' units \u00f7 Case Pack ' + upc2 + ' = ' + pct + '%' }, pct + '%');
    } },
    { key: 'draft_units', label: 'Total Units', render: function(r) { return React.createElement('span', { style: { fontFamily: 'inherit' } }, r.draft_units || 0); } },
    { key: 'draft_line_cost', label: 'Total Line Cost', render: function(r) { return React.createElement('span', { style: { fontFamily: 'inherit' } }, fmtM(r.draft_line_cost || 0)); } },
    { key: 'draft_line_profit', label: 'Total Line Profit', render: function(r) {
      if (!r.has_data) return '—';
      var color = r.draft_line_profit > 0 ? C.success : r.draft_line_profit < 0 ? C.danger : C.textMuted;
      return React.createElement('span', { style: { fontFamily: 'inherit', fontWeight: 600, color: color } }, fmtM(r.draft_line_profit));
    } },
    { key: 'keepa_status', label: 'Data', defaultVisible: false, render: function(r) {
      if (r.has_data) return React.createElement('span', { style: { color: C.success, fontSize: 11, fontWeight: 600 } }, 'active');
      if (!r.keepa_status) return React.createElement('span', { style: { color: C.textDim, fontSize: 11 } }, 'No data');
      var color = r.keepa_status === 'active' ? C.success : r.keepa_status === 'not_found' ? C.danger : C.warning;
      return React.createElement('span', { style: { color: color, fontSize: 11, fontWeight: 600 } }, r.keepa_status);
    } },
  ];

  // Deep link: /analysis?tab=po|product selects the tab on load (new-tab target).
  useEffect(function() { try { var qt = new URLSearchParams(window.location.search).get('tab'); if (qt === 'po' || qt === 'product') setAnalysisTab(qt); } catch (e) {} }, []);
  var tabBtn = function(key, label, icon) {
    var active = analysisTab === key;
    return React.createElement('a', {
      href: '/analysis' + (key !== 'po' ? '?tab=' + key : ''),
      onClick: function(e) { if (e.ctrlKey || e.metaKey || e.shiftKey) return; e.preventDefault(); setAnalysisTab(key); setSearch(''); setSelected({}); },
      style: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', textDecoration: 'none',
        border: '1px solid ' + (active ? C.accent : C.border), borderRadius: 6, cursor: 'pointer',
        fontSize: 13, fontWeight: 600, fontFamily: 'DM Sans',
        background: active ? C.accent + '22' : C.surface, color: active ? C.accent : C.textMuted }
    }, React.createElement(LIcon, { name: icon, size: 14 }), label);
  };

  if (loading) return React.createElement('div', { style: { padding: 40, textAlign: 'center', color: C.textMuted } }, React.createElement(LIcon, { name: 'loader-2', size: 24, spin: true }), ' Loading analysis data...');

  return React.createElement('div', null,
    loadErr && React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', marginBottom: 12, borderRadius: 8, background: C.dangerSoft || (C.danger + '15'), color: C.danger, fontSize: 13 } },
      React.createElement(LIcon, { name: 'alert-triangle', size: 14 }),
      React.createElement('span', { style: { flex: 1 } }, 'Could Not Load Analysis Data (' + loadErr + ')'),
      React.createElement(Btn, { size: 'sm', onClick: function() { setLoadErr(null); loadPos(true); loadProducts(true); } }, 'Retry')),
    React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 } },
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 16 } },
        React.createElement('div', null,
          React.createElement('h2', { style: { margin: 0, fontSize: 20, fontWeight: 600, color: C.text } }, 'Analysis'),
          React.createElement('p', { style: { margin: '4px 0 0', fontSize: 13, color: C.textMuted } },
            analysisTab === 'po' ? filtered.length + ' purchase orders' : filteredProducts.length + ' products')
        ),
        React.createElement('div', { style: { display: 'flex', gap: 6 } },
          tabBtn('po', 'By PO', 'file-text'),
          tabBtn('product', 'By Product', 'package')
        )
      ),
      React.createElement('input', { ref: importFileRef, type: 'file', accept: '.csv,.txt', style: { display: 'none' }, onChange: handleKeepaImport }),
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
        fetching && React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.accent } },
          React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true }), fetching === 'keepa' ? 'Fetching Keepa...' : fetching === 'spapi' ? 'Fetching SP-API...' : fetching === 'shipstation' ? 'Fetching ShipStation...' : fetching === 'walmart' ? 'Fetching Walmart...' : fetching === 'walmart-bb' ? 'Scraping buy box prices...' : fetching === 'import' ? 'Importing...' : 'Working...'
        ),
        React.createElement(Btn, { size: 'sm', onClick: function() {
          loadPos(true); loadProducts(true);
        }, disabled: loading }, React.createElement(LIcon, { name: 'refresh-cw', size: 14 }), ' Refresh')
      )
    ),
    // Search bar + incomplete filter
    React.createElement('div', { style: { display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 } },
      React.createElement(SearchInput, { placeholder: analysisTab === 'po' ? 'Search PO #, vendor, warehouse...' : 'Search SKU, title, ASIN, vendor...', value: search, onChange: function(e) { setSearch(e.target.value); }, flex: 1,
        style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '10px 14px', borderRadius: 8, fontSize: 13, outline: 'none', fontFamily: 'inherit' } }),
      React.createElement('button', { onClick: function() { setIncompleteOnly(!incompleteOnly); },
        style: { padding: '8px 14px', fontSize: 12, borderRadius: 6, cursor: 'pointer', fontWeight: 500, fontFamily: 'inherit', whiteSpace: 'nowrap',
          background: incompleteOnly ? C.warning + '20' : C.surface, color: incompleteOnly ? C.warning : C.textMuted,
          border: '1px solid ' + (incompleteOnly ? C.warning + '40' : C.border) } }, 'Incomplete Only'),
      analysisTab === 'product' && React.createElement('select', { value: mktFilter, onChange: function(e) { setMktFilter(e.target.value); },
        style: { padding: '8px 14px', fontSize: 12, borderRadius: 6, cursor: 'pointer', fontWeight: 500, fontFamily: 'inherit',
          background: mktFilter ? C.accent + '20' : C.surface, color: mktFilter ? C.accent : C.textMuted,
          border: '1px solid ' + (mktFilter ? C.accent + '40' : C.border), outline: 'none' } },
        React.createElement('option', { value: '' }, 'All Markets'),
        React.createElement('option', { value: 'US' }, 'US-FBA'),
        React.createElement('option', { value: 'CA' }, 'CA-FBA'),
        React.createElement('option', { value: 'FBM' }, 'FBM'),
        React.createElement('option', { value: 'WFS' }, 'WFS')
      )
    ),
    // Fetch result banner
    fetchResult && React.createElement('div', { style: { padding: '8px 14px', borderRadius: 6, marginBottom: 12,
      background: fetchResult.success ? C.success + '15' : C.danger + '15',
      border: '1px solid ' + (fetchResult.success ? C.success + '40' : C.danger + '40'),
      color: fetchResult.success ? C.success : C.danger, fontSize: 12 } },
      fetchResult.success ? React.createElement(LIcon, { name: 'check', size: 14 }) : React.createElement(LIcon, { name: 'x', size: 14 }), ' ', fetchResult.message),
    // Both DataTables stay mounted; toggle visibility with display:none so tab switches
    // are instant (no remount of the 5k-row Products table). The full-page Loading guard
    // above this gates the first paint until both data sets are loaded.
    React.createElement('div', { style: { display: analysisTab === 'po' ? 'block' : 'none' } },
      React.createElement(DataTable, { key: 'analysis-pos',
        columns: analysisCols, data: filtered, storageKey: 'analysis-pos',
        defaultSort: 'po_number', defaultSortDir: 'asc',
        onRowClick: function(p) { onNavigate('po-detail', p.id, { tab: 'analysis', from: 'analysis', analysisTab: analysisTab, mktFilter: mktFilter, incompleteOnly: incompleteOnly }); },
        rowHref: function(p) { return '/po/' + p.id; },
        onSortedData: function(sorted) { if (analysisTab === 'po') { sortedDataRef.current = sorted; poNavSave(sorted, 'PO analysis', { tab: 'analysis', from: 'analysis', analysisTab: analysisTab, mktFilter: mktFilter, incompleteOnly: incompleteOnly }); } },
        emptyTitle: 'No purchase orders found', emptySub: 'POs in Amazon FBA warehouses will appear here',
        bulkActions: {
          alwaysShow: true,
          count: selCount,
          onClear: selCount > 0 ? function() { setSelected({}); } : null,
          items: [].concat(
            [{ section: 'Fetch Data' }],
            [
              { label: 'Fetch Keepa', icon: 'bar-chart-3', onClick: fetchKeepa },
              { label: 'Fetch SP-API', icon: 'link', onClick: fetchSpApi },
              { label: 'Fetch ShipStation', icon: 'truck', onClick: fetchShipStation },
              { label: 'Fetch Walmart', icon: 'shopping-cart', onClick: fetchWalmartData },
            ],
            [{ divider: true }, { section: 'Export' }],
            [
              { label: 'Export Report (XLSX)', icon: 'file-spreadsheet', onClick: exportByPoExcel },
            ],
            selCount > 0 ? [
              { divider: true },
              { section: selCount + ' selected' },
              { label: 'Approve Selected', icon: 'check', onClick: approveSelected },
            ] : []
          )
        }
      })
    ),
    React.createElement('div', { style: { display: analysisTab === 'product' ? 'block' : 'none' } },
      React.createElement(DataTable, { key: 'analysis-products',
        columns: productCols, data: filteredProducts, storageKey: 'analysis-products',
        defaultSort: 'sku', defaultSortDir: 'asc',
        onSortedData: function(sorted) { if (analysisTab === 'product') sortedDataRef.current = sorted; },
        emptyTitle: 'No products found', emptySub: 'Products on draft POs will appear here',
        bulkActions: {
          alwaysShow: true,
          count: selCount,
          onClear: selCount > 0 ? function() { setSelected({}); } : null,
          items: [].concat(
            [{ section: 'Fetch Data' }],
            [
              { label: 'Fetch Keepa', icon: 'bar-chart-3', onClick: fetchKeepa },
              { label: 'Fetch SP-API', icon: 'link', onClick: fetchSpApi },
              { label: 'Fetch ShipStation', icon: 'truck', onClick: fetchShipStation },
              { label: 'Fetch Walmart', icon: 'shopping-cart', onClick: fetchWalmartData },
            ],
            [{ divider: true }, { section: 'Export / Import' }],
            [
              { label: 'Export ASINs', icon: 'download', onClick: function() { exportAsins(); } },
              { label: 'Import Keepa CSV', icon: 'upload', onClick: function() { importFileRef.current && importFileRef.current.click(); } },
              { label: 'Export Report (XLSX)', icon: 'file-spreadsheet', onClick: exportByProductExcel },
            ],
            selCount > 0 ? [
              { divider: true },
              { section: selCount + ' selected' },
              { label: 'Move to Next PO', icon: 'arrow-right', onClick: moveToNext },
            ] : []
          )
        }
      })
    )
  );
}

// ─── CREATE PO PAGE ───
function CreatePOPage({ showToast, onNavigate }) {
  const [mode, setMode] = useState(null); // null=choice, 'sku-qty', 'traditional'
  const [rows, setRows] = useState([{ sku: '', qty: '' }]);
  const [generating, setGenerating] = useState(false);
  const [results, setResults] = useState(null);
  // Traditional mode state
  const [vendors, setVendors] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [vendorId, setVendorId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [creating, setCreating] = useState(false);
  const [productsLoaded, setProductsLoaded] = useState(false);

  useEffect(function() {
    if (mode === 'traditional') {
      api('/vendors').then(function(v) { setVendors(v.filter(function(x) { return x.is_active; })); });
      api('/warehouses').then(function(w) { setWarehouses(w); });
      api('/products').then(function(p) { setAllProducts(p); setProductsLoaded(true); });
    }
  }, [mode]);

  // Warehouses offered for the selected vendor: the ones its products ship to. A brand-new
  // vendor has no products yet — fall back to ALL warehouses (flagged so the UI says so)
  // instead of a dead-end empty list.
  var vendorWarehouses = useMemo(function() {
    if (!vendorId || !warehouses.length) return { list: [], fallback: false };
    var vid = parseInt(vendorId);
    // "Transfer from FBM" vendor can use any warehouse
    var selectedVendor = vendors.find(function(v) { return v.id === vid; });
    if (selectedVendor && selectedVendor.vendor_name === 'Transfer from FBM') return { list: warehouses, fallback: false };
    if (!productsLoaded) return { list: [], fallback: false };
    var whIds = {};
    allProducts.forEach(function(p) {
      if (p.vendor_id === vid && p.warehouse_id) whIds[p.warehouse_id] = true;
    });
    var mapped = warehouses.filter(function(w) { return whIds[w.id]; });
    if (!mapped.length) return { list: warehouses, fallback: true };
    return { list: mapped, fallback: false };
  }, [vendorId, warehouses, allProducts, vendors, productsLoaded]);

  const updateRow = (i, key, val) => { const nr = [...rows]; nr[i][key] = val; setRows(nr); };
  const addRow = () => setRows([...rows, { sku: '', qty: '' }]);
  const removeRow = (i) => rows.length > 1 && setRows(rows.filter((_, idx) => idx !== i));

  const handlePaste = (e) => {
    const text = e.clipboardData.getData('text');
    if (!text.includes('\t') && !text.includes('\n')) return;
    e.preventDefault();
    const lines = text.trim().split('\n').map(l => l.split('\t')).filter(p => p[0]?.trim());
    if (lines.length) setRows(lines.map(p => ({ sku: p[0]?.trim() || '', qty: p[1]?.trim() || '' })));
  };

  const generate = async () => {
    const items = rows.filter(r => r.sku && r.qty);
    if (!items.length) return showToast('Enter at least one SKU + Qty', 'warning');
    setGenerating(true);
    try {
      const result = await api('/purchase-orders/generate', { method: 'POST', body: JSON.stringify({ items }) });
      setResults(result);
      var newCount = result.purchase_orders ? result.purchase_orders.filter(function(p) { return !p.was_existing; }).length : result.created;
      var existingCount = result.purchase_orders ? result.purchase_orders.filter(function(p) { return p.was_existing; }).length : 0;
      var msg = newCount > 0 ? 'Created ' + newCount + ' PO(s)' : '';
      if (existingCount > 0) msg += (msg ? ', ' : '') + 'Updated ' + existingCount + ' existing draft PO(s)';
      showToast(msg);
    } catch (e) { showToast(e.message, 'error'); }
    setGenerating(false);
  };

  var createTraditional = async function() {
    if (!vendorId) return showToast('Select a vendor', 'warning');
    if (!warehouseId) return showToast('Select a warehouse', 'warning');
    setCreating(true);
    try {
      var po = await api('/purchase-orders', { method: 'POST', body: JSON.stringify({ vendor_id: parseInt(vendorId), warehouse_id: parseInt(warehouseId) }) });
      showToast('Draft PO #' + po.po_number + ' created');
      onNavigate('po-detail', po.id);
    } catch (e) { showToast(e.message, 'error'); }
    setCreating(false);
  };

  var resultsModal = React.createElement(Modal, { open: !!results, onClose: function() { setResults(null); setRows([{ sku: '', qty: '' }]); setMode(null); }, title: 'Purchase Orders Created', width: 560 },
    results && React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 16 } },
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12 } },
        React.createElement('div', { style: { width: 44, height: 44, borderRadius: 10, background: C.successSoft, display: 'flex', alignItems: 'center', justifyContent: 'center' } }, React.createElement(LIcon, { name: 'circle-check', size: 24, color: C.success })),
        React.createElement('div', null,
          React.createElement('div', { style: { fontSize: 16, fontWeight: 600, color: C.text } }, results.created + ' PO(s) Created'),
          React.createElement('div', { style: { fontSize: 12, color: C.textMuted } }, 'SKUs grouped by vendor + warehouse')
        )
      ),
      results.skipped && results.skipped.length > 0 && React.createElement('div', { style: { fontSize: 12, color: C.warning, padding: '6px 10px', background: C.warningSoft, borderRadius: 6 } },
        'Skipped: ' + results.skipped.join(', ')
      ),
      results.resolved && results.resolved.length > 0 && React.createElement('div', { style: { fontSize: 12, color: C.accent, padding: '6px 10px', background: C.accentSoft, borderRadius: 6 } },
        'Resolved: ' + results.resolved.map(function(r) { return r.from + ' \u2192 ' + r.to + (r.type === 'kit' ? ' (\u00D7' + r.kit_qty + ')' : ' (alias)'); }).join(', ')
      ),
      // Per-SKU max units per PO: SKUs that were split across several draft POs
      results.splits && results.splits.length > 0 && React.createElement('div', { style: { fontSize: 12, color: C.warning, padding: '6px 10px', background: C.warningSoft, borderRadius: 6 } },
        'Split by max units per PO: ' + results.splits.map(function(s) { return s.sku + ' \u2192 ' + s.pos + ' POs (' + s.units + ' units, max ' + s.cap + ' each)'; }).join(', ')
      ),
      React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 8 } },
        (results.purchase_orders || []).map(function(po) {
          return React.createElement('div', { key: po.id, onClick: function() { setResults(null); onNavigate('po-detail', po.id); },
            style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: C.bg, borderRadius: 8, border: '1px solid ' + C.border, cursor: 'pointer', transition: 'border-color 0.15s' },
            onMouseEnter: function(e) { e.currentTarget.style.borderColor = C.accent; },
            onMouseLeave: function(e) { e.currentTarget.style.borderColor = C.border; }
          },
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
              React.createElement('span', { style: { fontWeight: 600, fontFamily: 'inherit', color: C.accent } }, 'PO #' + po.po_number),
              React.createElement(Badge, { status: po.status }),
              React.createElement('span', { style: { fontSize: 12, color: C.textMuted } }, po.vendor_name),
              React.createElement('span', { style: { fontSize: 11, color: C.textDim } }, '\u2192 ' + po.wh_code)
            ),
            React.createElement('div', { style: { textAlign: 'right', fontSize: 12 } },
              React.createElement('div', { style: { color: C.textMuted } }, po.sku_count + ' SKUs \u00b7 ' + po.total_units + ' units'),
              React.createElement('div', { style: { fontWeight: 600, color: C.text, fontSize: 14 } }, '$' + Number(po.total_cost).toLocaleString(undefined, { minimumFractionDigits: 2 }))
            )
          );
        })
      ),
      React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 } },
        React.createElement(Btn, { onClick: function() { setResults(null); setRows([{ sku: '', qty: '' }]); setMode(null); } }, 'Create More'),
        React.createElement(Btn, { variant: 'primary', onClick: function() { setResults(null); onNavigate('orders'); } }, 'View All POs')
      )
    )
  );

  // Method selection screen
  if (!mode) {
    var cardStyle = { cursor: 'pointer', padding: 28, border: '2px solid ' + C.border, borderRadius: 12, background: C.surface, transition: 'all 0.15s' };
    return React.createElement('div', null,
      React.createElement('div', { style: { marginBottom: 24 } },
        React.createElement('h2', { style: { margin: 0, fontSize: 20, fontWeight: 600, color: C.text } }, 'Create Purchase Order'),
        React.createElement('p', { style: { margin: '4px 0 0', fontSize: 13, color: C.textMuted } }, 'Choose a method to create your purchase order')
      ),
      React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, maxWidth: 700 } },
        React.createElement('div', { style: cardStyle,
          onMouseEnter: function(e) { e.currentTarget.style.borderColor = C.accent; e.currentTarget.style.background = C.accentSoft; },
          onMouseLeave: function(e) { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = C.surface; },
          onClick: function() { setMode('sku-qty'); } },
          React.createElement('div', { style: { fontSize: 28, marginBottom: 12, display: 'flex' } }, React.createElement(LIcon, { name: 'zap', size: 28 })),
          React.createElement('div', { style: { fontSize: 16, fontWeight: 600, color: C.text, marginBottom: 6 } }, 'SKU + Quantity'),
          React.createElement('div', { style: { fontSize: 13, color: C.textMuted, lineHeight: 1.5 } }, 'Enter SKU and quantity pairs. POs are auto-grouped by vendor and warehouse. Paste from Excel supported.')
        ),
        React.createElement('div', { style: cardStyle,
          onMouseEnter: function(e) { e.currentTarget.style.borderColor = C.accent; e.currentTarget.style.background = C.accentSoft; },
          onMouseLeave: function(e) { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = C.surface; },
          onClick: function() { setMode('traditional'); } },
          React.createElement('div', { style: { fontSize: 28, marginBottom: 12, display: 'flex' } }, React.createElement(LIcon, { name: 'clipboard-list', size: 28 })),
          React.createElement('div', { style: { fontSize: 16, fontWeight: 600, color: C.text, marginBottom: 6 } }, 'Traditional'),
          React.createElement('div', { style: { fontSize: 13, color: C.textMuted, lineHeight: 1.5 } }, 'Select a vendor and warehouse first, then manually add SKUs and quantities to the draft PO.')
        )
      )
    );
  }

  // Traditional mode
  if (mode === 'traditional') {
    return React.createElement('div', null,
      React.createElement('div', { style: { marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 } },
        React.createElement('button', { onClick: function() { setMode(null); }, style: { background: 'none', border: 'none', color: C.textMuted, cursor: 'pointer', fontSize: 18, padding: 0, display: 'flex', alignItems: 'center' } }, React.createElement(LIcon, { name: 'arrow-left', size: 20 })),
        React.createElement('div', null,
          React.createElement('h2', { style: { margin: 0, fontSize: 20, fontWeight: 600, color: C.text } }, 'Create Draft PO'),
          React.createElement('p', { style: { margin: '4px 0 0', fontSize: 13, color: C.textMuted } }, 'Select a vendor and ship-to warehouse. You can add SKUs after creating the draft.')
        )
      ),
      React.createElement(Card, { style: { maxWidth: 500 } },
        React.createElement('div', { style: { marginBottom: 16 } },
          React.createElement('label', { style: { display: 'block', fontSize: 12, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 6 } }, 'Vendor'),
          React.createElement(SearchSelect, {
            value: vendorId,
            onChange: function(val) { setVendorId(String(val)); setWarehouseId(''); },
            options: vendors.map(function(v) { return { value: v.id, label: v.vendor_name }; }),
            placeholder: '-- Select Vendor --'
          })
        ),
        React.createElement('div', { style: { marginBottom: 20 } },
          React.createElement('label', { style: { display: 'block', fontSize: 12, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 6 } }, 'Ship-To Warehouse'),
          React.createElement(SearchSelect, {
            value: warehouseId,
            onChange: function(val) { setWarehouseId(String(val)); },
            options: vendorWarehouses.list.map(function(w) { return { value: w.id, label: w.warehouse_id + ' \u2014 ' + w.name }; }),
            placeholder: !vendorId ? '-- Select a vendor first --' : (!productsLoaded ? 'Loading\u2026' : '-- Select Warehouse --'),
            disabled: !vendorId || !productsLoaded
          }),
          vendorWarehouses.fallback && React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 6 } }, 'No products are linked to this vendor yet \u2014 showing all warehouses.')
        ),
        React.createElement(Btn, { variant: 'primary', onClick: createTraditional, disabled: creating || !vendorId || !warehouseId }, creating ? 'Creating...' : 'Create Draft PO')
      )
    );
  }

  // SKU + Qty mode
  return React.createElement('div', null,
    React.createElement('div', { style: { marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 } },
      React.createElement('button', { onClick: function() { setMode(null); }, style: { background: 'none', border: 'none', color: C.textMuted, cursor: 'pointer', fontSize: 18, padding: 0, display: 'flex', alignItems: 'center' } }, React.createElement(LIcon, { name: 'arrow-left', size: 20 })),
      React.createElement('div', null,
        React.createElement('h2', { style: { margin: 0, fontSize: 20, fontWeight: 600, color: C.text } }, 'Create Purchase Orders'),
        React.createElement('p', { style: { margin: '4px 0 0', fontSize: 13, color: C.textMuted } }, 'Enter SKU + Qty pairs. Auto-grouped by vendor & warehouse. Paste from Excel supported.')
      )
    ),
    React.createElement(Card, null,
      React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '2fr 1fr 32px', gap: 8, marginBottom: 8 } },
        React.createElement('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', padding: '0 4px' } }, 'SKU'),
        React.createElement('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', padding: '0 4px' } }, 'QUANTITY'),
        React.createElement('div')
      ),
      rows.map((row, i) =>
        React.createElement('div', { key: i, style: { display: 'grid', gridTemplateColumns: '2fr 1fr 32px', gap: 8, marginBottom: 6 } },
          React.createElement('input', { value: row.sku, onChange: e => updateRow(i, 'sku', e.target.value), onPaste: i === 0 ? handlePaste : undefined, placeholder: 'Enter SKU',
            style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', outline: 'none' } }),
          React.createElement('input', { value: row.qty, onChange: e => updateRow(i, 'qty', e.target.value), placeholder: 'Qty', type: 'number',
            style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 13, outline: 'none' } }),
          React.createElement('button', { onClick: () => removeRow(i), style: { background: 'none', border: 'none', color: C.textDim, cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center' } }, React.createElement(LIcon, { name: 'x', size: 14 }))
        )
      ),
      React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', marginTop: 12 } },
        React.createElement(Btn, { variant: 'ghost', size: 'sm', onClick: addRow }, '+ Add Row'),
        React.createElement(Btn, { variant: 'primary', onClick: generate, disabled: generating }, generating ? 'Generating...' : [React.createElement(LIcon, { key: 'icon', name: 'zap', size: 14 }), ' Generate POs'])
      )
    ),
    resultsModal
  );
}

// ─── ORDERS LIST PAGE ───
// Module-scope cache so re-entering the page paints from memory while a
// background refetch updates the data.
var _poListCache = { page: 0, scroll: 0, orders: null, urgencyMap: null };
// PO list search box: case-insensitive substring over the row's fields plus the lazily merged
// search index (o.skus = line SKUs + vendor SKUs + ASINs, o.titles). Trimmed, so a pasted
// " b0abc12345 " still finds the PO by ASIN (Anthony 2026-09-27).
function poMatchesSearch(o, search) {
  var s = String(search == null ? '' : search).trim().toLowerCase();
  if (!s) return true;
  var fields = [o.po_number, o.vendor_name, o.wh_code, o.wh_name, o.shipping_method, o.payment_method, o.skus, o.titles, o.notes_text];
  for (var i = 0; i < fields.length; i++) {
    if (fields[i] != null && String(fields[i]).toLowerCase().indexOf(s) !== -1) return true;
  }
  return false;
}
function OrdersPage({ showToast, onNavigate, initialStatus, initialWarehouse }) {
  const [orders, setOrders] = useState(_poListCache.orders || []);
  const [urgencyMap, setUrgencyMap] = useState(_poListCache.urgencyMap || {}); // po_id -> { is_urgent, stockout_count, stockout_pct, avg_days_on_hand, threshold_days, line_count }
  const [filter, setFilterRaw] = useState(function() {
    if (initialStatus !== undefined) return initialStatus;
    try { return sessionStorage.getItem('po_status_filter') || ''; } catch(e) { return ''; }
  });
  var setFilter = function(v) {
    setFilterRaw(v);
    try { sessionStorage.setItem('po_status_filter', v); } catch(e) {}
    var params = [];
    if (v) params.push('status=' + encodeURIComponent(v));
    if (warehouseFilter) params.push('warehouse=' + encodeURIComponent(warehouseFilter));
    var url = '/purchase-orders' + (params.length ? '?' + params.join('&') : '');
    history.replaceState(Object.assign({}, history.state, { opts: { status: v || undefined, warehouse: warehouseFilter || undefined } }), '', url);
  };
  const [warehouseFilter, setWarehouseFilterRaw] = useState(function() {
    if (initialWarehouse !== undefined) return initialWarehouse;
    try { return sessionStorage.getItem('po_wh_filter') || ''; } catch(e) { return ''; }
  });
  var setWarehouseFilter = function(v) {
    setWarehouseFilterRaw(v);
    try { sessionStorage.setItem('po_wh_filter', v); } catch(e) {}
    var params = [];
    if (filter) params.push('status=' + encodeURIComponent(filter));
    if (v) params.push('warehouse=' + encodeURIComponent(v));
    var url = '/purchase-orders' + (params.length ? '?' + params.join('&') : '');
    history.replaceState(Object.assign({}, history.state, { opts: { status: filter || undefined, warehouse: v || undefined } }), '', url);
  };
  const [search, setSearchRaw] = useState(function() {
    try { return sessionStorage.getItem('po_search') || ''; } catch(e) { return ''; }
  });
  var setSearch = function(v) {
    setSearchRaw(v);
    try { sessionStorage.setItem('po_search', v); } catch(e) {}
  };
  const [topOnly, setTopOnlyRaw] = useState(function() {
    try { return sessionStorage.getItem('po_top_filter') === '1'; } catch(e) { return false; }
  });
  var setTopOnly = function(v) {
    setTopOnlyRaw(v);
    try { sessionStorage.setItem('po_top_filter', v ? '1' : ''); } catch(e) {}
  };
  const [selected, setSelected] = useState({});
  var [locModalOpen, setLocModalOpen] = useState(false);
  var [locModalIds, setLocModalIds] = useState([]);
  // Draft urgency + the SKU/ASIN search index are re-fetched on a real load and at most every
  // 5 minutes by the background refresh (review L18, 2026-09-27: the 60 s refresh re-requested
  // both every minute — 5,300 draft-urgency calls a day). The last index is kept here and merged
  // into every refreshed list, so SKU/ASIN search keeps matching between index fetches.
  var poAuxRef = useRef({ at: 0, skus: null, titles: null });
  const load = useCallback(async (opts) => {
    var pos = await api('/purchase-orders');
    var aux = poAuxRef.current;
    if (aux.skus && Array.isArray(pos)) pos = pos.map(function(o) { return Object.assign({}, o, { skus: aux.skus[o.id] || '', titles: aux.titles[o.id] || '' }); });
    _poListCache.orders = pos;
    setOrders(pos);
    if (opts && opts.background && aux.at && (Date.now() - aux.at) < 5 * 60 * 1000) return;
    aux.at = Date.now();
    // Parallel-fetch draft urgency map (cheap once forecast cache is warm).
    // Fail silently — urgent badges/chips simply don't render if it fails.
    api('/purchase-orders/draft-urgency').then(function(m) {
      if (!m || typeof m !== 'object') return; // never poison state with a non-map (error_log 295)
      _poListCache.urgencyMap = m; setUrgencyMap(m);
    }).catch(function(e) { console.error('[silent-catch]', e); });
    // Lazy-fetch the SKU search index in the background and merge into rows.
    // Search by PO#/vendor/etc works immediately; SKU matches hydrate ~200ms later.
    api('/purchase-orders/search-index').then(function(idx) {
      var skusById = {}, titlesById = {};
      for (var i = 0; i < idx.length; i++) { skusById[idx[i].id] = idx[i].skus; titlesById[idx[i].id] = idx[i].titles; }
      aux.skus = skusById; aux.titles = titlesById;
      setOrders(function(prev) {
        var merged = prev.map(function(o) { return Object.assign({}, o, { skus: skusById[o.id] || '', titles: titlesById[o.id] || '' }); });
        _poListCache.orders = merged;
        return merged;
      });
    }).catch(function(e) { console.error('[silent-catch]', e); });
  }, []);
  useEffect(() => { load().catch(function(e) { showToast('Could not load purchase orders: ' + e.message, 'error'); }); }, [load]);

  // Auto-refresh PO list every 60 seconds — only while the tab is visible
  useEffect(function() {
    var timer = setInterval(function() {
      if (document.visibilityState === 'hidden') return;
      load({ background: true }).catch(function(e) { console.error('[po-list refresh]', e); });
    }, 60000);
    return function() { clearInterval(timer); };
  }, [load]);

  // Live Billed/Unbilled badge: surgically merge bill_status into the existing rows
  // (no full-list reload — preserves scroll/selection/expanded state) on a 30s timer
  // AND whenever the tab regains focus. The focus refresh is the key one: linking an
  // invoice in Bill.com then switching back reflects the new "Billed" tag immediately.
  // Bill.com auto-sync sets bill_status server-side; this just polls the lightweight
  // /bill-statuses endpoint and re-renders only the rows whose status actually changed.
  useEffect(function() {
    var alive = true;
    var refreshBillStatuses = function() {
      api('/purchase-orders/bill-statuses').then(function(rows) {
        if (!alive || !Array.isArray(rows)) return;
        var byId = {};
        for (var i = 0; i < rows.length; i++) byId[rows[i].id] = rows[i].bill_status;
        setOrders(function(prev) {
          var changed = false;
          var next = prev.map(function(o) {
            if (Object.prototype.hasOwnProperty.call(byId, o.id) && byId[o.id] !== o.bill_status) {
              changed = true;
              return Object.assign({}, o, { bill_status: byId[o.id] });
            }
            return o;
          });
          if (!changed) return prev;
          _poListCache.orders = next;
          return next;
        });
      }).catch(function(e) { console.error('[silent-catch]', e); });
    };
    var timer = setInterval(refreshBillStatuses, 30000);
    var onFocus = function() { if (document.visibilityState !== 'hidden') refreshBillStatuses(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return function() {
      alive = false;
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, []);

  // Analysis data is now stored on purchase_orders — use directly
  var ordersWithAnalysis = orders;

  var uniqueWarehouses = useMemo(function() {
    var base = filter ? orders.filter(function(o) { return o.status === filter; }) : orders;
    var map = {};
    base.forEach(function(o) { if (o.wh_code) map[o.wh_code] = o.wh_name || o.wh_code; });
    return Object.keys(map).sort().map(function(code) { return { code: code, name: map[code] }; });
  }, [orders, filter]);

  // Clear warehouse filter if it's no longer valid for the current status
  useEffect(function() {
    if (warehouseFilter && uniqueWarehouses.length > 0 && !uniqueWarehouses.some(function(w) { return w.code === warehouseFilter; })) {
      setWarehouseFilter('');
    }
  }, [uniqueWarehouses]);

  // Counts + totals for status pills (filtered by current warehouse selection)
  var statusCounts = useMemo(function() {
    var base = warehouseFilter ? orders.filter(function(o) { return o.wh_code === warehouseFilter; }) : orders;
    var counts = {};
    var totals = {};
    base.forEach(function(o) {
      counts[o.status] = (counts[o.status] || 0) + 1;
      totals[o.status] = (totals[o.status] || 0) + Number(o.total_cost || 0);
    });
    var allOpen = base.filter(function(o) { return o.status !== 'cancelled' && o.status !== 'closed'; });
    counts[''] = allOpen.length;
    totals[''] = allOpen.reduce(function(s, o) { return s + Number(o.total_cost || 0); }, 0);
    return { counts: counts, totals: totals };
  }, [orders, warehouseFilter]);

  // Counts for warehouse pills (filtered by current status selection)
  var warehouseCounts = useMemo(function() {
    var base = filter ? orders.filter(function(o) { return o.status === filter; }) : orders.filter(function(o) { return o.status !== 'cancelled' && o.status !== 'closed'; });
    var counts = {};
    base.forEach(function(o) { if (o.wh_code) counts[o.wh_code] = (counts[o.wh_code] || 0) + 1; });
    counts[''] = base.length;
    return counts;
  }, [orders, filter]);

  var filtered = ordersWithAnalysis.filter(function(o) {
    if (!filter && (o.status === 'cancelled' || o.status === 'closed')) return false;
    if (filter && o.status !== filter) return false;
    if (warehouseFilter && o.wh_code !== warehouseFilter) return false;
    if (topOnly && !(o.top_revenue_count > 0)) return false;
    return poMatchesSearch(o, search);
  });

  var selectedIds = Object.keys(selected).filter(function(k) { return selected[k]; }).map(Number);
  var selCount = selectedIds.length;
  var lastCheckedRef = useRef(null);
  var sortedDataRef = useRef(filtered);

  var toggleOne = function(id, shiftKey, e) {
    if (shiftKey) {
      window.getSelection().removeAllRanges();
    }
    var next = Object.assign({}, selected);
    if (shiftKey && lastCheckedRef.current !== null) {
      // Use the sorted data from DataTable for correct visual order
      var visibleData = sortedDataRef.current || filtered;
      var lastIdx = visibleData.findIndex(function(o) { return o.id === lastCheckedRef.current; });
      var curIdx = visibleData.findIndex(function(o) { return o.id === id; });
      if (lastIdx !== -1 && curIdx !== -1) {
        var start = Math.min(lastIdx, curIdx);
        var end = Math.max(lastIdx, curIdx);
        var newVal = !selected[id];
        for (var i = start; i <= end; i++) {
          next[visibleData[i].id] = newVal;
        }
      }
    } else {
      next[id] = !next[id];
    }
    lastCheckedRef.current = id;
    setSelected(next);
  };

  var toggleAll = function() {
    if (selCount === filtered.length && filtered.length > 0) {
      setSelected({});
    } else {
      var next = {};
      filtered.forEach(function(o) { next[o.id] = true; });
      setSelected(next);
    }
  };

  var bulkStatus = async function(status) {
    if (status === 'checked-in') {
      setLocModalIds(selectedIds.slice());
      setLocModalOpen(true);
      return;
    }
    try {
      await api('/purchase-orders/bulk/status', { method: 'PUT', body: JSON.stringify({ ids: selectedIds, status: status }) });
      showToast(selCount + ' PO(s) marked as ' + status);
      setSelected({});
      var idSet = {};
      selectedIds.forEach(function(id) { idSet[id] = true; });
      setOrders(function(prev) { return prev.map(function(o) { return idSet[o.id] ? Object.assign({}, o, { status: status }) : o; }); });
    } catch (e) { showToast(e.message, 'error'); }
  };

  var handleBulkCheckIn = async function(loc, pallets, boxes) {
    setLocModalOpen(false);
    var ids = locModalIds.slice();
    try {
      await api('/purchase-orders/bulk/status', { method: 'PUT', body: JSON.stringify({ ids: ids, status: 'checked-in' }) });
      // The location call records the bin AND stages the PO's units (server: services/po-checkin.js)
      // — a failure is shown, not swallowed (2026-09-27 sweep H3).
      var locFailed = [];
      for (var i = 0; i < ids.length; i++) {
        try { await api('/receiving/' + ids[i] + '/location', { method: 'PUT', body: JSON.stringify({ warehouse_location: loc, pallets: pallets, boxes: boxes, append: true }) }); }
        catch (locErr) { locFailed.push(locErr.message); }
      }
      if (locFailed.length) showToast(locFailed.length + ' of ' + ids.length + ' PO(s) checked in but the location / staging failed: ' + locFailed[0], 'error');
      else showToast(ids.length + ' PO(s) checked in at ' + loc);
      setSelected({});
      var idSet = {};
      ids.forEach(function(id) { idSet[id] = true; });
      setOrders(function(prev) { return prev.map(function(o) { return idSet[o.id] ? Object.assign({}, o, { status: 'checked-in' }) : o; }); });
    } catch (e) { showToast(e.message, 'error'); }
    finally { setLocModalIds([]); }
  };

  var bulkCancel = async function() {
    var checkedIn = orders.filter(function(o) { return selected[o.id] && ['checked-in', 'receiving', 'received'].includes(o.status); });
    if (checkedIn.length) { showToast('Cannot cancel POs that have been checked in: ' + checkedIn.map(function(o) { return 'PO ' + o.po_number; }).join(', '), 'error'); return; }
    if (!(await appConfirm('Cancel ' + selCount + ' purchase order(s)?', { danger: true, okLabel: 'Cancel POs' }))) return;
    try {
      await api('/purchase-orders/bulk', { method: 'DELETE', body: JSON.stringify({ ids: selectedIds }) });
      showToast(selCount + ' PO(s) cancelled');
      setSelected({});
      load();
    } catch (e) { showToast(e.message, 'error'); }
  };

  var bulkEmailVendor = async function() {
    var selectedPOs = orders.filter(function(o) { return selected[o.id]; });
    if (!selectedPOs.length) return;
    if (!(await appConfirm('Email ' + selectedPOs.length + ' PO(s) to their vendors using the default email template?'))) return;
    showToast('Sending ' + selectedPOs.length + ' PO email(s)...');
    try {
      var result = await api('/purchase-orders/bulk/email-vendor', { method: 'POST', body: JSON.stringify({ ids: selectedPOs.map(function(o) { return o.id; }) }) });
      var msgs = [];
      if (result.sent) msgs.push(result.sent + ' sent');
      if (result.failed) msgs.push(result.failed + ' failed');
      showToast('Bulk email: ' + msgs.join(', '), result.failed ? 'error' : 'success');
      setSelected({});
      load();
    } catch (e) { showToast('Bulk email failed: ' + e.message, 'error'); }
  };

  var bulkExportXLSX = async function() {
    var selectedPOs = orders.filter(function(o) { return selected[o.id]; });
    if (!selectedPOs.length) return;

    // Single PO — just download directly
    if (selectedPOs.length === 1) {
      var token = getAuthToken();
      try {
        var resp = await fetch('/api/purchase-orders/' + selectedPOs[0].id + '/export-xlsx', { credentials: 'include' });
        if (!resp.ok) { var err = await resp.json().catch(function() { return {}; }); throw new Error(err.error || 'Export failed'); }
        var blob = await resp.blob();
        var filename = resp.headers.get('content-disposition');
        filename = filename ? filename.split('filename=')[1].replace(/"/g, '') : 'PO-Excel.xlsx';
        var url = URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
        showToast('PO exported');
      } catch (e) { showToast('Export failed', 'error'); }
      return;
    }

    // Multiple POs — zip them
    showToast('Exporting ' + selectedPOs.length + ' POs...');
    try {
      var token = getAuthToken();
      var zip = new JSZip();
      for (var i = 0; i < selectedPOs.length; i++) {
        var resp = await fetch('/api/purchase-orders/' + selectedPOs[i].id + '/export-xlsx', { credentials: 'include' });
        if (!resp.ok) continue;
        var blob = await resp.blob();
        var filename = resp.headers.get('content-disposition');
        filename = filename ? filename.split('filename=')[1].replace(/"/g, '') : 'PO_' + selectedPOs[i].po_number + '.xlsx';
        zip.file(filename, blob);
      }
      var content = await zip.generateAsync({ type: 'blob' });
      var url = URL.createObjectURL(content);
      var a = document.createElement('a');
      a.href = url; a.download = 'PO_Export_' + selectedPOs.length + '_POs.zip'; a.click();
      URL.revokeObjectURL(url);
      showToast(selectedPOs.length + ' POs exported as ZIP');
    } catch (e) { showToast('Export failed: ' + e.message, 'error'); }
  };

  var bulkExportLineItemsCSV = async function() {
    var selectedPOs = orders.filter(function(o) { return selected[o.id]; });
    if (!selectedPOs.length) return;
    showToast('Fetching line items for ' + selectedPOs.length + ' PO(s)...');
    try {
      var headers = ['po_number', 'sku', 'vendor_sku', 'vendor_title', 'qty_ordered', 'unit_cost'];
      var ids = selectedPOs.map(function(o) { return o.id; }).join(',');
      var items = await api('/purchase-orders/bulk/line-items?ids=' + ids);
      var allRows = (items || []).map(function(li) {
        var row = {};
        headers.forEach(function(h) { row[h] = li[h] != null ? li[h] : ''; });
        return row;
      });
      if (!allRows.length) { showToast('No line items found', 'error'); return; }
      downloadCSV('PO_Line_Items_' + selectedPOs.length + '_POs.csv', arrayToCSV(headers, allRows));
      showToast(allRows.length + ' line items exported');
    } catch (e) { showToast('Export failed: ' + e.message, 'error'); }
  };

  var _renderAging = function(days, isLive) {
    if (days == null) return React.createElement('span', { style: { color: C.textDim, fontSize: 12 } }, '\u2014');
    var color = days <= 5 ? C.success : days <= 14 ? C.warning : C.danger;
    var label = days === 0 ? 'Today' : days === 1 ? '1 day' : days + ' days';
    if (isLive) label += ' \u25B8';
    return React.createElement('span', { style: { fontWeight: 500, fontSize: 12, color: color } }, label);
  };

  var poCols = [
    { key: '_select', label: '', locked: true, stopClick: true, noFilter: true, style: { width: 44, paddingRight: 12 },
      headerRender: function() {
        var allChecked = filtered.length > 0 && filtered.every(function(o) { return selected[o.id]; });
        var someChecked = !allChecked && filtered.some(function(o) { return selected[o.id]; });
        return React.createElement('div', {
          onClick: function(e) { e.stopPropagation(); toggleAll(); },
          style: { width: 32, height: 28, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', userSelect: 'none' }
        },
          React.createElement('div', {
            style: { width: 20, height: 20, borderRadius: 4, border: '2px solid ' + (allChecked || someChecked ? C.accent : C.textDim),
              background: allChecked ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }
          },
            allChecked && React.createElement(LIcon, { name: 'check', size: 13, color: '#fff' }),
            someChecked && !allChecked && React.createElement('span', { style: { color: C.accent, fontSize: 13, fontWeight: 700, lineHeight: 1 } }, '–')
          )
        );
      },
      render: function(o) {
      var checked = !!selected[o.id];
      return React.createElement('div', {
        onMouseDown: function(e) { if (e.shiftKey) e.preventDefault(); },
        onClick: function(e) { e.stopPropagation(); toggleOne(o.id, e.shiftKey, e); },
        style: { width: 32, height: 28, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', userSelect: 'none' }
      },
        React.createElement('div', {
          style: { width: 20, height: 20, borderRadius: 4, border: '2px solid ' + (checked ? C.accent : C.textDim),
            background: checked ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }
        },
          checked && React.createElement(LIcon, { name: 'check', size: 13, color: '#fff' })
        )
      );
    }},
    { key: 'po_number', label: 'PO #', copyable: true, style: { fontWeight: 600, fontFamily: 'inherit' }, render: function(o) {
      return React.createElement('a', { href: '/po/' + o.id, onClick: function(e) { e.preventDefault(); }, style: { color: C.accent, textDecoration: 'none' } }, o.po_number);
    } },
    { key: 'po_date', label: 'Creation Date', render: function(o) { return fmtDateOnly(o.po_date) || '—'; } },
    { key: 'status', label: 'Status', filterOptions: ['draft','submitted','approved','confirmed','next','shipped','checked-in','receiving','received','closed','cancelled'], render: function(o) {
      var colors = { draft: '#F59E0B', submitted: '#3B82F6', approved: '#22C55E', confirmed: '#14B8A6', next: '#06B6D4', shipped: C.accent, 'checked-in': C.purple || '#8B5CF6', receiving: C.warning, received: C.success, closed: C.textDim, cancelled: C.danger };
      var sc = colors[o.status] || C.textMuted;
      return React.createElement('span', { style: { display: 'inline-flex', gap: 4, alignItems: 'center' } },
        React.createElement('span', { style: { padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 600, background: sc + '18', color: sc, textTransform: 'uppercase', letterSpacing: '0.05em' } }, o.status),
        o.has_errored_fba_items && React.createElement('span', {
          title: 'This PO has un-binned errored FBA items — review on the Errored tab before closing',
          style: { padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 700, background: C.danger + '22', color: C.danger, textTransform: 'uppercase', letterSpacing: '0.05em' }
        }, 'Errored'),
        o.top_revenue_count > 0 && React.createElement('span', {
          title: o.top_revenue_count + ' top-revenue SKU' + (o.top_revenue_count > 1 ? 's' : '') + ' on this PO',
          style: { padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 700, background: '#EAB30822', color: '#EAB308', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }
        }, 'Top' + (o.top_revenue_count > 1 ? ' ×' + o.top_revenue_count : ''))
      );
    } },
    { key: 'bill_status', label: 'Billed', filterOptions: ['billed','unbilled'], defaultVisible: false,
      sortValue: function(o) { return (o.bill_status === 'billed' || o.vendor_name === 'Transfer from FBM') ? 1 : 0; },
      render: function(o) {
        return React.createElement(window.BilledBadge, { billed: o.bill_status === 'billed', isTransfer: o.vendor_name === 'Transfer from FBM', bdcBillId: o.bdc_bill_id, invoiceNumber: o.bill_invoice_number, amount: o.bill_amount, bills: o.linked_bills });
      } },
    // Stockouts column — sortable by percentage. Filtered out when filter !== 'draft'.
    { key: 'stockout_pct', label: 'Stockouts',
      sortValue: function(o) {
        var u = urgencyMap[o.id];
        return u ? (u.stockout_pct || 0) : -1; // -1 keeps "no data" rows at the bottom when sorted desc
      },
      render: function(o) {
        var u = urgencyMap[o.id];
        if (!u) return React.createElement('span', { style: { color: C.textDim } }, '—');
        if (u.stockout_count === 0) return React.createElement('span', {
          title: '0 of ' + u.line_count + ' line items out of stock',
          style: { padding: '3px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: '#05966933', color: '#6EE7B7' }
        }, '0%');
        // 3-tier color scale: amber (1-33), orange (34-66), red (67-100)
        var pct = u.stockout_pct;
        var bg, fg;
        if (pct <= 33) { bg = '#F59E0B33'; fg = '#FCD34D'; }
        else if (pct <= 66) { bg = '#EA580C33'; fg = '#FDBA74'; }
        else { bg = '#7C2D1233'; fg = '#FCA5A5'; }
        return React.createElement('span', {
          title: u.stockout_count + ' of ' + u.line_count + ' line items physically out of stock with no other coverage incoming',
          style: { padding: '3px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: bg, color: fg }
        }, pct + '%');
      }
    },
    { key: 'vendor_name', label: 'Vendor', copyable: true, render: function(o) { return o.vendor_name || '—'; } },
    { key: 'wh_code', label: 'Warehouse', filterOptions: 'auto', render: function(o) { return React.createElement('span', { style: { fontFamily: 'inherit', fontSize: 11 } }, o.wh_code || '—'); } },
    { key: 'sku_count', label: 'SKUs' },
    { key: 'total_units', label: 'Units', render: function(o) { return o.total_units != null ? o.total_units.toLocaleString() : ''; } },
    { key: 'total_cases', label: 'Cases', defaultVisible: false, render: function(o) { return o.total_cases != null ? o.total_cases.toLocaleString() : ''; } },
    { key: 'total_cost', label: 'Total Cost', render: function(o) { return React.createElement('span', { style: { fontFamily: 'inherit', fontWeight: 500 } }, '$' + Number(o.total_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })); } },
    { key: 'aging_days', label: 'Aging',
      sortValue: function(o) {
        if (o.status === 'approved') return o.aging_approved;
        if (o.status === 'submitted') return o.aging_submitted;
        if (o.status === 'confirmed') return o.aging_confirmed;
        if (o.status === 'shipped') return o.aging_shipped;
        if (o.status === 'checked-in') return o.aging_checked_in;
        return o.aging_days;
      },
      render: function(o) {
      // Show time in current status
      var days = null;
      var isLive = false;
      if (o.status === 'approved') { days = o.aging_approved; isLive = true; }
      else if (o.status === 'submitted') { days = o.aging_submitted; isLive = true; }
      else if (o.status === 'confirmed') { days = o.aging_confirmed; isLive = true; }
      else if (o.status === 'shipped') { days = o.aging_shipped; isLive = true; }
      else if (o.status === 'checked-in') { days = o.aging_checked_in; isLive = true; }
      else { days = o.aging_days; isLive = false; }
      return _renderAging(days, isLive);
    }},
    { key: 'aging_total', label: 'Submitted \u2192 Received', defaultVisible: false, render: function(o) { return _renderAging(o.aging_total, !o.received_date && o.submitted_date); } },
    { key: 'total_profit', label: 'Profit', defaultVisible: false, render: function(o) {
      if (o.total_profit == null) return React.createElement('span', { style: { color: C.textDim } }, '—');
      var v = Number(o.total_profit);
      var color = v > 0 ? C.success : v < 0 ? C.danger : C.textMuted;
      return React.createElement('span', { style: { fontWeight: 500, color: color } }, '$' + v.toLocaleString(undefined, { minimumFractionDigits: 2 }));
    }},
    { key: 'roi', label: 'ROI', defaultVisible: false, render: function(o) {
      if (o.roi == null) return React.createElement('span', { style: { color: C.textDim } }, '—');
      var v = Number(o.roi);
      var color = v >= 30 ? C.success : v >= 0 ? C.warning : C.danger;
      return React.createElement('span', { style: { fontWeight: 500, color: color } }, v.toFixed(1) + '%');
    }},
    { key: 'margin', label: 'Margin', defaultVisible: false, render: function(o) {
      if (o.margin == null) return React.createElement('span', { style: { color: C.textDim } }, '—');
      var v = Number(o.margin);
      var color = v >= 20 ? C.success : v >= 0 ? C.warning : C.danger;
      return React.createElement('span', { style: { fontWeight: 500, color: color } }, v.toFixed(1) + '%');
    }},
    { key: 'shipping_method', label: 'Shipping', defaultVisible: false, filterOptions: 'auto' },
    { key: 'payment_method', label: 'Payment', defaultVisible: false, filterOptions: 'auto' },
  ];

  var exportPOcsv = async function() {
    var dataToExport = filtered;
    var statusLabel = filter || 'all';
    var exportLabel = statusLabel + (warehouseFilter ? '_' + warehouseFilter : '');

    // Sheet 1: PO Summary
    var summaryHeaders = ['PO Date', 'PO#', 'Status', 'Vendor Name', 'SKU Count', 'Unit Count', 'Case Count', 'Total Cost', 'Vendor PO Minimum', 'Vendor Terms', 'Warehouse'];
    var summaryRows = dataToExport.map(function(o) {
      return [
        fmtDateOnly(o.po_date),
        o.po_number, o.status, o.vendor_name, o.sku_count, o.total_units, o.total_cases,
        o.total_cost, o.vendor_po_minimum || '', o.vendor_terms || '', o.wh_code || ''
      ];
    });

    // Sheet 2: Line Items — fetch from API
    var poIds = dataToExport.map(function(o) { return o.id; });
    var lineItems = [];
    try { lineItems = await api('/purchase-orders/bulk/line-items?ids=' + poIds.join(',')); } catch(e) {}

    var itemHeaders = ['PO#', 'PO Date', 'Vendor', 'Warehouse', 'SKU', 'Vendor Title', 'Qty Ordered', 'Case Qty', 'Unit Cost', 'Line Total'];
    var itemRows = lineItems.map(function(li) {
      return [
        li.po_number, fmtDateOnly(li.po_date), li.vendor_name, li.wh_code || '',
        li.sku, li.vendor_title, li.qty_ordered, li.case_qty,
        li.unit_cost, li.line_total
      ];
    });

    var buildCSV = function(headers, rows) {
      var escape = escapeCSV; // shared cell rule, formula-safe (review 2026-09-28, medium #31)
      return headers.map(escape).join(',') + '\n' + rows.map(function(r) { return r.map(escape).join(','); }).join('\n');
    };

    var summaryCsv = buildCSV(summaryHeaders, summaryRows);
    var itemsCsv = buildCSV(itemHeaders, itemRows);

    // If no line items, just download summary CSV
    if (itemRows.length === 0) {
      var blob = new Blob([summaryCsv], { type: 'text/csv' });
      var url = URL.createObjectURL(blob);
      var a = document.createElement('a'); a.href = url; a.download = 'purchase_orders_' + exportLabel + '.csv'; a.click();
      URL.revokeObjectURL(url);
      return;
    }

    // ZIP both CSVs
    var zip = new JSZip();
    zip.file('PO_Summary_' + exportLabel + '.csv', summaryCsv);
    zip.file('PO_Line_Items_' + exportLabel + '.csv', itemsCsv);
    var content = await zip.generateAsync({ type: 'blob' });
    var url = URL.createObjectURL(content);
    var a = document.createElement('a'); a.href = url; a.download = 'PO_Export_' + exportLabel + '.zip'; a.click();
    URL.revokeObjectURL(url);
    showToast('Exported ' + dataToExport.length + ' POs with ' + itemRows.length + ' line items');
  };

  return React.createElement('div', null,
    React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 } },
      React.createElement('div', null,
        React.createElement('h2', { style: { margin: 0, fontSize: 20, fontWeight: 600, color: C.text } }, 'Purchasing'),
        React.createElement('p', { style: { margin: '4px 0 0', fontSize: 13, color: C.textMuted } }, filtered.length + ' purchase orders')
      ),
      React.createElement('div', { style: { display: 'flex', gap: 8 } },
        React.createElement('span', { title: 'Exports all filtered POs and their line items to CSV' },
          React.createElement(Btn, { size: 'sm', onClick: exportPOcsv, disabled: filtered.length === 0 }, React.createElement(LIcon, { name: 'download', size: 14 }), ' Export CSV')
        ),
        React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: () => onNavigate('create-po') }, '+ New PO')
      )
    ),
    React.createElement('div', { style: { display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' } },
      ['', 'draft', 'approved', 'submitted', 'confirmed', 'shipped', 'checked-in', 'receiving', 'received', 'closed', 'next', 'cancelled'].map(function(s) {
        var cnt = statusCounts.counts[s] || 0;
        var total = statusCounts.totals[s] || 0;
        var active = filter === s;
        var fmtTotal = total >= 1000 ? '$' + (total / 1000).toFixed(1) + 'k' : '$' + total.toFixed(0);
        return React.createElement('button', { key: s, onClick: function() { setFilter(s); setSelected({}); },
          className: active ? '' : 'filter-pill',
          style: { padding: '6px 14px', fontSize: 12, borderRadius: 6, border: '1px solid transparent', cursor: 'pointer', fontWeight: 500, fontFamily: 'inherit', transition: 'all 0.15s',
            background: active ? C.accent : C.surface, color: active ? '#fff' : C.textMuted } },
          (s || 'All') + (cnt ? ' (' + cnt + ')' : ''));
      }),
      // Gold toggle: only POs containing top-revenue SKUs (composes with status/warehouse/search)
      (function() {
        var base = filter ? orders.filter(function(o) { return o.status === filter; }) : orders.filter(function(o) { return o.status !== 'cancelled' && o.status !== 'closed'; });
        if (warehouseFilter) base = base.filter(function(o) { return o.wh_code === warehouseFilter; });
        var n = base.filter(function(o) { return o.top_revenue_count > 0; }).length;
        return React.createElement('button', { onClick: function() { setTopOnly(!topOnly); },
          className: topOnly ? '' : 'filter-pill',
          title: 'Show only POs containing top-revenue SKUs',
          style: { padding: '6px 14px', fontSize: 12, borderRadius: 6, border: '1px solid ' + (topOnly ? '#EAB308' : 'transparent'), cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit', transition: 'all 0.15s', marginLeft: 10,
            background: topOnly ? '#EAB30822' : C.surface, color: topOnly ? '#EAB308' : C.textMuted } },
          '★ Top SKUs' + (n ? ' (' + n + ')' : ''));
      })()
    ),
    false && uniqueWarehouses.length > 1 && React.createElement('div', { style: { display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' } },
      React.createElement('span', { style: { fontSize: 12, color: C.textDim, marginRight: 4 } }, 'Warehouse:'),
      [{ code: '', label: 'All' }].concat(uniqueWarehouses.map(function(w) { return { code: w.code, label: w.code }; })).map(function(w) {
        var cnt = warehouseCounts[w.code] || 0;
        var active = warehouseFilter === w.code;
        return React.createElement('button', { key: w.code || '_all', onClick: function() { setWarehouseFilter(w.code); },
          style: { padding: '6px 14px', fontSize: 12, borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 500, fontFamily: 'inherit',
            background: active ? C.accent : C.surface, color: active ? '#fff' : C.textMuted } },
          w.label + (cnt ? ' (' + cnt + ')' : ''));
      })
    ),
    React.createElement('div', { style: { marginBottom: 16 } }, React.createElement(SearchInput, { placeholder: 'Search PO #, vendor, warehouse, SKU, ASIN, product title, notes...', value: search, onChange: function(e) { setSearch(e.target.value); },
      style: { width: '100%', background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '10px 14px', borderRadius: 8, fontSize: 13, outline: 'none' } })),
    React.createElement(DataTable, {
      columns: poCols.filter(function(c) {
        if ((filter === 'draft' || filter === '') && (c.key === 'aging_days' || c.key === 'aging_total')) return false;
        if (c.key === 'aging_total' && filter !== 'received' && filter !== 'closed') return false;
        if (c.key === 'stockout_pct' && filter !== 'draft') return false;
        // Billed/Unbilled only relevant once a PO has been submitted to a vendor — hide for Draft.
        if (c.key === 'bill_status' && filter === 'draft') return false;
        return true;
      }),
      data: filtered, storageKey: 'purchase-orders' + (filter ? '-' + filter : ''),
      defaultSort: 'vendor_name', defaultSortDir: 'asc',
      initialPage: _poListCache.page,
      onPageChange: function(p) { _poListCache.page = p; },
      initialScroll: _poListCache.scroll,
      onScrollChange: function(s) { _poListCache.scroll = s; },
      onRowClick: function(o) { onNavigate('po-detail', o.id); },
      rowHref: function(o) { return '/po/' + o.id; },
      onSortedData: function(sorted) {
        sortedDataRef.current = sorted;
        poNavSave(sorted, 'Purchase orders' + (filter ? ' · ' + filter.charAt(0).toUpperCase() + filter.slice(1).replace(/[-_]/g, ' ') : '') + (warehouseFilter ? ' · ' + warehouseFilter : ''), {});
      },
      emptyTitle: 'No purchase orders', emptySub: 'Create your first PO',
      bulkActions: selCount > 0 ? {
        count: selCount,
        onClear: function() { setSelected({}); },
        items: [
          { label: 'Export Selected POs', onClick: bulkExportXLSX },
          { label: 'Export Selected PO Line Items', onClick: bulkExportLineItemsCSV },
          { divider: true },
          { label: 'Email to Vendors', onClick: bulkEmailVendor },
          { divider: true },
          { label: 'Mark as Submitted', onClick: function() { bulkStatus('submitted'); } },
          { label: 'Mark as Approved', onClick: function() { bulkStatus('approved'); } },
          { label: 'Mark as Confirmed', onClick: function() { bulkStatus('confirmed'); } },
          { label: 'Mark as Next', onClick: function() { bulkStatus('next'); } },
          { label: 'Mark as Shipped', onClick: function() { bulkStatus('shipped'); } },
          { label: 'Mark as Checked-In', onClick: function() { bulkStatus('checked-in'); } },
          { label: 'Mark as Receiving', onClick: function() { bulkStatus('receiving'); } },
          { label: 'Mark as Received', onClick: function() { bulkStatus('received'); } },
          { label: 'Mark as Closed', onClick: function() { bulkStatus('closed'); } },
        ].concat(filter ? [
          { divider: true },
          { label: 'Cancel POs', onClick: bulkCancel, danger: true },
        ] : [])
      } : null
    }),
    locModalOpen && React.createElement(LocationPickerModal, {
      title: 'Check In — ' + locModalIds.length + ' PO(s)',
      onConfirm: handleBulkCheckIn,
      onCancel: function() { setLocModalOpen(false); setLocModalIds([]); }
    })
  );
}

// ─── PO DETAIL PAGE ───
// ─── PO Previous / Next (Anthony 2026-09-07) ───
// The list a PO is opened from writes its current order of ids (sorted + filtered, every page)
// to the tab's session storage on every re-order; the detail page walks it. Middle / ctrl-click
// tabs carry it too (Chrome copies sessionStorage into a tab opened from this one). A status
// change on the current PO leaves the sequence alone, so cycling through drafts and submitting
// one still moves on to the next draft; the sequence refreshes when the user is back on a list.
var PO_NAV_KEY = 'po-nav';
function poNavSave(rows, label, opts) {
  try {
    var ids = (rows || []).map(function(r) { return r && r.id; }).filter(function(x) { return x != null; });
    if (!ids.length) return;
    sessionStorage.setItem(PO_NAV_KEY, JSON.stringify({ ids: ids, label: label || 'Purchase orders', opts: opts || {}, at: Date.now() }));
  } catch (e) { /* no storage: the buttons simply stay hidden */ }
}
function poNavRead(poId) {
  try {
    var raw = sessionStorage.getItem(PO_NAV_KEY); if (!raw) return null;
    var nav = JSON.parse(raw); var ids = Array.isArray(nav.ids) ? nav.ids : [];
    var i = ids.findIndex(function(x) { return String(x) === String(poId); });
    if (i < 0) return null;
    return { position: i + 1, total: ids.length, prev_id: i > 0 ? ids[i - 1] : null, next_id: i < ids.length - 1 ? ids[i + 1] : null, label: nav.label || 'Purchase orders', opts: nav.opts || {} };
  } catch (e) { return null; }
}
function PONavButtons({ poId, onNavigate }) {
  var nav = poNavRead(poId);
  if (!nav) return null;
  var go = function(id) { if (id != null) onNavigate('po-detail', id, nav.opts || {}); };
  var btn = function(side, id) {
    var label = side === 'previous' ? 'Previous' : 'Next';
    return React.createElement('button', {
      onClick: function() { go(id); }, disabled: id == null,
      title: id == null ? 'No ' + side + ' PO in this list' : label + ' PO in ' + nav.label,
      style: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 7, border: '1px solid ' + C.border, background: C.surface, color: C.text, fontSize: 12.5, fontWeight: 500, fontFamily: 'inherit', cursor: id == null ? 'default' : 'pointer', opacity: id == null ? 0.45 : 1 }
    }, side === 'previous' ? React.createElement(LIcon, { name: 'chevron-left', size: 14 }) : null, label, side === 'next' ? React.createElement(LIcon, { name: 'chevron-right', size: 14 }) : null);
  };
  // Count sits after the buttons, matching the CS thread header (Anthony 9-09)
  return React.createElement('div', { style: { display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8 } },
    btn('previous', nav.prev_id), btn('next', nav.next_id),
    React.createElement('span', { title: nav.label, style: { fontSize: 11.5, color: C.textMuted, whiteSpace: 'nowrap', marginLeft: 2 } }, nav.position + ' of ' + nav.total));
}

function PODetailPage({ poId, showToast, onNavigate, currentUser, initialTab, backPage, backOpts }) {
  const [po, setPo] = useState(null);
  // Any address part makes the PO's ship-to an override - the same six columns as the server's
  // hasShipOverride(); country alone is a default. The card used to key off ship_to_name only,
  // so an override without a name printed on the PDF but showed here as the plain warehouse
  // block with no way to clear it from the page.
  var hasShipToOverride = !!(po && (po.ship_to_name || po.ship_to_address || po.ship_to_address2 || po.ship_to_city || po.ship_to_state || po.ship_to_zip));
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  // Archived SKUs are orderable on Transfer-from-FBM POs only (TRANSFER_VENDOR in
  // routes/purchase-orders.js). Fetched the first time the picker opens on such a PO, so
  // every other PO page keeps the smaller active-only payload.
  const [archivedProducts, setArchivedProducts] = useState([]);
  const [archivedLoaded, setArchivedLoaded] = useState(false);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [editingField, setEditingField] = useState(null);
  const [fieldVal, setFieldVal] = useState('');
  const [shipToForm, setShipToForm] = useState({});
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState({});
  const editingRef = useRef({ id: null, form: null });
  var liSortedRef = useRef([]);
  var aSortedRef = useRef([]);
  var liLastClickRef = useRef(null);
  var aLastClickRef = useRef(null);
  const [activeTab, setActiveTabRaw] = useState(initialTab || 'items');
  var setActiveTab = function(t) {
    setActiveTabRaw(t);
    var path = window.location.pathname;
    if (path.startsWith('/po/')) {
      history.replaceState(history.state, '', path + (t && t !== 'items' ? '?tab=' + t : ''));
    }
  };
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [noteFiles, setNoteFiles] = useState([]); // pending composer attachments (File objects)
  const [sendingNote, setSendingNote] = useState(false);
  const noteFileInputRef = useRef(null);
  const [activity, setActivity] = useState([]);
  const notesEndRef = useRef(null);
  const [lastDeleted, setLastDeleted] = useState(null);
  const undoTimerRef = useRef(null);

  var [locationModalOpen, setLocationModalOpen] = useState(false);

  // Email template modal state
  var [emailModalOpen, setEmailModalOpen] = useState(false);
  var [poEmailTemplates, setPoEmailTemplates] = useState([]);
  var [selectedTemplateId, setSelectedTemplateId] = useState(null);
  var [emailPreview, setEmailPreview] = useState(null);
  var [emailTo, setEmailTo] = useState('');
  var [emailSubject, setEmailSubject] = useState('');
  var [emailSending, setEmailSending] = useState(false);
  var emailBodyRef = useRef(null);
  var [emailFiles, setEmailFiles] = useState([]); // user attachments, File objects, sent with this e-mail only
  var emailFileInputRef = useRef(null);
  var [emailDragOver, setEmailDragOver] = useState(false);
  var [emailTplKey, setEmailTplKey] = useState(0);
  var [scheduleMode, setScheduleMode] = useState(false);
  var [scheduleDateTime, setScheduleDateTime] = useState('');
  var [scheduledEmails, setScheduledEmails] = useState([]);
  var [showScheduledEmails, setShowScheduledEmails] = useState(false);

  // Vendor confirmation import state
  var confirmFileRef = useRef(null);
  var [confirmPreview, setConfirmPreview] = useState(null);
  var [confirmLoading, setConfirmLoading] = useState(false);
  var [confirmApplying, setConfirmApplying] = useState(false);
  // Track which items user wants to include (default all checked)
  var [confirmChecked, setConfirmChecked] = useState({});
  var confirmLastClickRef = useRef(null);

  // ─── Transparency label files (vendor-supplied per-SKU PDF label sheets) ───
  var labelFileRef = useRef(null);
  var [labelFiles, setLabelFiles] = useState(null); // { files: [], missing: [] }
  var [labelBusy, setLabelBusy] = useState(false);
  var [labelModalOpen, setLabelModalOpen] = useState(false);
  var [labelPending, setLabelPending] = useState(null);   // File objects held for the confirm step
  var [labelPreview, setLabelPreview] = useState(null);   // preview payload; files[].line_item_id editable in review
  var loadLabelFiles = useCallback(function() {
    if (!isValidPoId) return;
    api('/purchase-orders/' + poId + '/label-files').then(setLabelFiles).catch(function(e) { console.error('[silent-catch]', e); });
  }, [poId]);
  useEffect(function() { loadLabelFiles(); }, [loadLabelFiles]);
  // Picking files runs a PREVIEW (nothing persisted server-side) and opens the review
  // step — mirrors the vendor-confirmation import flow. Save re-sends the same files
  // with the reviewed assignments; Cancel just drops them.
  var uploadLabelFiles = function(e) {
    var files = Array.prototype.slice.call(e.target.files || []);
    if (!files.length) return;
    e.target.value = '';
    setLabelBusy(true);
    var form = new FormData();
    files.forEach(function(f) { form.append('files', f); });
    fetch('/api/purchase-orders/' + poId + '/label-files/preview', { method: 'POST', body: form, credentials: 'include' })
      .then(function(r) { if (r.status === 401) { setAuthToken(null); window.location.reload(); return; } return r.json().then(function(d) { if (!r.ok) throw new Error(d.error || 'Preview failed'); return d; }); })
      .then(function(d) {
        if (!d) return;
        setLabelPending(files);
        setLabelPreview(d);
        setLabelModalOpen(true);
      })
      .catch(function(err) { showToast(err.message, 'error'); })
      .then(function() { setLabelBusy(false); });
  };
  var cancelLabelPreview = function() { setLabelPreview(null); setLabelPending(null); };
  // SKUs that assigning these files will newly mark as Transparency: the line has a product
  // that is not flagged yet, and the file is not a duplicate (duplicates are skipped at save).
  // Mirrors the server's per-saved-file flag flip so the review step and the save toast can
  // show it (Anthony 2026-10-29: "make the auto-flag visible"). One entry per product.
  var labelFlagTargets = function(files) {
    if (!po) return [];
    var liById = {};
    (po.line_items || []).forEach(function(li) { liById[li.id] = li; });
    var seen = {}, skus = [];
    (files || []).forEach(function(f) {
      if (!f.line_item_id || f.duplicate_of || f.duplicate_in_batch) return;
      var li = liById[f.line_item_id];
      if (!li || !li.product_id || li.requires_transparency || seen[li.product_id]) return;
      seen[li.product_id] = true;
      skus.push(li.sku);
    });
    return skus;
  };
  var saveLabelFiles = function() {
    if (!labelPending || !labelPreview) return;
    setLabelBusy(true);
    var form = new FormData();
    labelPending.forEach(function(f) { form.append('files', f); });
    form.append('assignments', JSON.stringify(labelPreview.files.map(function(x) { return { line_item_id: x.line_item_id || null }; })));
    fetch('/api/purchase-orders/' + poId + '/label-files', { method: 'POST', body: form, credentials: 'include' })
      .then(function(r) { if (r.status === 401) { setAuthToken(null); window.location.reload(); return; } return r.json().then(function(d) { if (!r.ok) throw new Error(d.error || 'Save failed'); return d; }); })
      .then(function(d) {
        if (!d) return;
        var skippedNote = (d.skipped && d.skipped.length) ? ', ' + d.skipped.length + ' duplicate' + (d.skipped.length === 1 ? '' : 's') + ' skipped' : '';
        var marked = labelFlagTargets(d.files); // saved rows carry line_item_id; po state is still pre-save here
        var markedNote = marked.length ? ', ' + marked.length + ' SKU' + (marked.length === 1 ? '' : 's') + ' marked Transparency' : '';
        showToast('Labels saved: ' + d.uploaded + ' file' + (d.uploaded === 1 ? '' : 's') + ', ' + d.matched + ' assigned' + markedNote + skippedNote, (d.skipped && d.skipped.length) ? 'error' : undefined);
        setLabelPreview(null); setLabelPending(null);
        loadLabelFiles(); load(); // refresh line chips (requires_transparency may have been set)
      })
      .catch(function(err) { showToast(err.message, 'error'); })
      .then(function() { setLabelBusy(false); });
  };
  var reassignLabelFile = function(fileId, lineId) {
    var marked = lineId ? labelFlagTargets([{ line_item_id: lineId }]) : [];
    api('/purchase-orders/' + poId + '/label-files/' + fileId, { method: 'PATCH', body: JSON.stringify({ line_item_id: lineId || null }) })
      .then(function() { if (marked.length) showToast(marked[0] + ' marked as Transparency'); loadLabelFiles(); load(); })
      .catch(function(err) { showToast(err.message, 'error'); });
  };
  var deleteLabelFile = function(fileId) {
    api('/purchase-orders/' + poId + '/label-files/' + fileId, { method: 'DELETE' })
      .then(function() { loadLabelFiles(); })
      .catch(function(err) { showToast(err.message, 'error'); });
  };

  var uploadVendorConfirmation = function(e) {
    var file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    setConfirmLoading(true);
    var formData = new FormData();
    formData.append('file', file);
    fetch('/api/purchase-orders/' + poId + '/import-confirmation', { method: 'POST', body: formData, credentials: 'include' })
      .then(function(r) { if (r.status === 401) { setAuthToken(null); window.location.reload(); return; } return r.json().then(function(d) { if (!r.ok) throw new Error(d.error || 'Upload failed'); return d; }); })
      .then(function(data) {
        setConfirmPreview(data);
        // Default matched items to checked. Move-to-next rows are pre-checked ONLY
        // when the import looks healthy — on a suspect import (warning set: nothing
        // matched, every line would move) they start unchecked so a reflexive Apply
        // can't empty the PO.
        var checked = {};
        (data.matched || []).forEach(function(m) { if (m.changes.length > 0) checked['update_' + m.po_line_id] = true; });
        if (!data.warning) {
          (data.not_on_confirmation || []).forEach(function(m) { checked['move_' + m.po_line_id] = true; });
        }
        setConfirmChecked(checked);
        setConfirmLoading(false);
      })
      .catch(function(err) { showToast(err.message, 'error'); setConfirmLoading(false); });
  };

  var applyVendorConfirmation = function() {
    if (!confirmPreview) return;
    setConfirmApplying(true);
    var updates = (confirmPreview.matched || []).filter(function(m) { return m.changes.length > 0 && confirmChecked['update_' + m.po_line_id]; }).map(function(m) {
      return { po_line_id: m.po_line_id, new_qty: m.new_qty, new_cost: m.new_cost };
    });
    var moveToNext = (confirmPreview.not_on_confirmation || []).filter(function(m) { return confirmChecked['move_' + m.po_line_id]; }).map(function(m) {
      return { po_line_id: m.po_line_id };
    });
    api('/purchase-orders/' + poId + '/apply-confirmation', { method: 'POST', body: JSON.stringify({ updates: updates, move_to_next: moveToNext }) })
      .then(function(result) {
        var msg = result.updated + ' item(s) updated';
        if (result.moved > 0) msg += ', ' + result.moved + ' moved to PO #' + result.next_po_number;
        // Lines with receipts never move (sweep H2) — say which stayed
        if (result.skipped_received && result.skipped_received.length) msg += '; not moved (already received): ' + result.skipped_received.join(', ');
        showToast(msg, result.skipped_received && result.skipped_received.length ? 'error' : undefined);
        setConfirmPreview(null);
        setConfirmApplying(false);
        load();
      })
      .catch(function(err) { showToast(err.message, 'error'); setConfirmApplying(false); });
  };

  // Keep ref in sync for the click-away handler
  useEffect(function() {
    editingRef.current = { id: editingItem, form: itemForm };
  }, [editingItem, itemForm]);

  // Click-away auto-save
  useEffect(function() {
    if (!editingItem) return;
    var handler = function(e) {
      var target = e.target;
      var isInside = false;
      while (target) {
        // an in-app dialog (the case-pack question) is not "away": its buttons must not start a second save
        if (target.dataset && (target.dataset.editRow === 'true' || target.dataset.appDialog !== undefined)) { isInside = true; break; }
        target = target.parentElement;
      }
      if (!isInside && editingRef.current.id) {
        var savedId = editingRef.current.id;
        var savedForm = editingRef.current.form;
        saveLineItem(savedId, savedForm);
      }
    };
    var timer = setTimeout(function() {
      document.addEventListener('mousedown', handler);
    }, 50);
    return function() { clearTimeout(timer); document.removeEventListener('mousedown', handler); };
  }, [editingItem]);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addSearch, setAddSearch] = useState('');
  const [addSelections, setAddSelections] = useState({}); // { productId: qty_string }
  const [addPage, setAddPage] = useState(0);
  const addLastClickRef = useRef(-1);
  const [prodModalOpen, setProdModalOpen] = useState(false);
  const [prodForm, setProdForm] = useState({});
  const [prodEditing, setProdEditing] = useState(null); // stable identity for the editor; prodForm changes per keystroke
  const [prodTab, setProdTab] = useState('details');

  const [urgency, setUrgency] = useState(null); // { is_urgent, stockout_count, avg_days_of_stock, threshold_days } for drafts only
  // Guard: if poId is missing/null/"null", bail. A bad caller (navigate to
  // po-detail with a falsy arg) used to fire 5 server 500s on mount.
  var isValidPoId = poId && poId !== 'null' && poId !== 'undefined';
  var loadSeq = useRef(0); // review M6 #73: Tab-through line edits fire overlapping reloads — only the newest may paint
  // 2026-09-27 sweep M16: a failed load (404, network, 500) left the page on "Loading..."
  // forever. The newest load's error is kept and shown with a Retry when there is no PO to show.
  const [poLoadError, setPoLoadError] = useState(null);
  const load = useCallback(async () => {
    if (!isValidPoId) { setPoLoadError('Invalid PO link'); return; }
    var seq = ++loadSeq.current;
    try {
      var data = await api('/purchase-orders/' + poId);
      if (seq !== loadSeq.current) return;
      setPoLoadError(null);
      setPo(data);
    } catch (e) {
      if (seq !== loadSeq.current) return;
      setPoLoadError(e && e.message || 'Failed to load');
    }
  }, [poId]);
  // Load urgency once po is known and status is draft
  useEffect(function() {
    if (!po || po.status !== 'draft') { setUrgency(null); return; }
    api('/purchase-orders/draft-urgency').then(function(map) {
      setUrgency(map[po.id] || null);
    }).catch(function(e) { console.error('[silent-catch]', e); });
  }, [po && po.id, po && po.status]);
  const loadNotes = useCallback(async () => {
    if (!isValidPoId) return;
    try { setNotes(await api('/purchase-orders/' + poId + '/notes')); } catch(e) {}
  }, [poId]);
  const loadActivity = useCallback(async () => {
    if (!isValidPoId) return;
    try { setActivity(await api('/purchase-orders/' + poId + '/activity')); } catch(e) {}
  }, [poId]);
  const [receivingHistory, setReceivingHistory] = useState([]);
  const [analysisData, setAnalysisData] = useState(null);
  const [analysisFetching, setAnalysisFetching] = useState(false);
  const [analysisFetchResult, setAnalysisFetchResult] = useState(null);
  const analysisFetchTimer = useRef(null);
  var setAnalysisFetchResultAuto = function(val) { clearTimeout(analysisFetchTimer.current); setAnalysisFetchResult(val); if (val) { analysisFetchTimer.current = setTimeout(function() { setAnalysisFetchResult(null); }, 10000); } };
  const [analysisSelected, setAnalysisSelected] = useState({});
  const [aIncompleteOnly, setAIncompleteOnly] = useState(false);
  const [aEditItem, setAEditItem] = useState(null);
  const [aEditForm, setAEditForm] = useState({});
  const aEditRef = useRef({ id: null, form: null });
  const aImportRef = useRef(null);
  const [liSelected, setLiSelected] = useState({});
  var liSelCount = Object.keys(liSelected).filter(function(k) { return liSelected[k]; }).length;
  var toggleLiSel = function(id, e) {
    var n = Object.assign({}, liSelected);
    if (e && e.shiftKey && liLastClickRef.current != null) {
      var sorted = liSortedRef.current || [];
      var lastIdx = sorted.findIndex(function(r) { return r.id === liLastClickRef.current; });
      var curIdx = sorted.findIndex(function(r) { return r.id === id; });
      if (lastIdx >= 0 && curIdx >= 0) {
        var lo = Math.min(lastIdx, curIdx), hi = Math.max(lastIdx, curIdx);
        for (var i = lo; i <= hi; i++) n[sorted[i].id] = true;
      } else { n[id] = !n[id]; if (!n[id]) delete n[id]; }
    } else { n[id] = !n[id]; if (!n[id]) delete n[id]; }
    liLastClickRef.current = id;
    setLiSelected(n);
  };
  var toggleLiAll = function() {
    var items = po ? (po.line_items || []) : [];
    if (liSelCount === items.length && items.length > 0) setLiSelected({});
    else { var n = {}; items.forEach(function(li) { n[li.id] = true; }); setLiSelected(n); }
  };
  const loadReceivingHistory = useCallback(async () => {
    if (!isValidPoId) return;
    try { setReceivingHistory(await api('/receiving/' + poId + '/history')); } catch(e) {}
  }, [poId]);
  var [forecastMap, setForecastMap] = useState(function() {
    var m = {};
    if (_forecastCache.data && _forecastCache.data.forecast) _forecastCache.data.forecast.forEach(function(f) { m[f.sku] = f; });
    return m;
  });
  const loadAnalysis = useCallback(async () => {
    if (!isValidPoId) return;
    try {
      // Kick off forecast fetch in parallel with analysis fetch (forecast is used for days_on_hand column)
      var loadForecast = function(d) { _forecastCache.data = d; _forecastCache.ts = Date.now(); var m = {}; d.forecast.forEach(function(f) { m[f.sku] = f; }); setForecastMap(m); };
      if (_forecastCache.data && _forecastCache.data.forecast) { var m = {}; _forecastCache.data.forecast.forEach(function(f) { m[f.sku] = f; }); setForecastMap(m); }
      else { api('/analysis/demand-forecast').then(loadForecast).catch(function(e) { console.error('[silent-catch]', e); }); }
      setAnalysisData(await api('/analysis/po/' + poId));
    } catch(e) { console.error('loadAnalysis error:', e); }
  }, [poId]);

  // Analysis inline edit: keep ref in sync
  useEffect(function() { aEditRef.current = { id: aEditItem, form: aEditForm, save: aEditRef.current.save }; }, [aEditItem, aEditForm]);

  var saveAnalysisField = async function(lineId, formOverride) {
    var form = formOverride || aEditForm;
    var ad = analysisData;
    if (!ad) return;
    var line = (ad.lines || []).find(function(l) { return l.id === lineId; });
    if (!line) { setAEditItem(function(cur) { return cur === lineId ? null : cur; }); return; }
    // Fully optimistic (Anthony 2026-09-29: "why can't the database work in the background while
    // showing the user the immediate changes?"): work out what changed, show it in the row at once
    // (derived columns recomputed with the server's formula), close the editor, then save in the
    // background. The analysis reload confirms; a failed save puts the old values back with a toast.
    var flags = analysisFlags();
    var writes = []; // line-item PUTs, in order
    var patch = {};
    var keepa = null;
    // qty_ordered (with the case-pack rounding choice — same UX as the Line Items tab)
    if (form.qty_ordered !== undefined && form.qty_ordered !== '') {
      var newQty = parseInt(form.qty_ordered);
      if (!isNaN(newQty) && newQty >= 0 && newQty !== Number(line.qty_ordered)) {
        var upc = parseInt(line.units_per_case) || parseInt(line.case_qty) || 1;
        // already answered when the Units field was left (leaveAnalysisQty)? then it is not asked again
        if (upc > 1 && newQty % upc !== 0 && Number(form._caseOk) !== newQty) {
          var asked = await askCasePackQty(newQty, upc);
          if (asked == null) { setAEditItem(function(cur) { return cur === lineId ? null : cur; }); return; } // Cancel: nothing is saved and the cell shows the previous number again
          newQty = asked;
        }
        if (newQty !== Number(line.qty_ordered)) { patch.qty_ordered = newQty; writes.push({ qty_ordered: newQty }); }
      }
    }
    if (form.unit_cost !== undefined) {
      var newCost = parseFloat(form.unit_cost);
      if (!isNaN(newCost) && newCost !== Number(line.unit_cost)) { patch.unit_cost = newCost; writes.push({ unit_cost: newCost }); }
    }
    // analysis fields via the keepa cache — only the fields the user actually changed
    var changed = form._changed || {};
    if (changed.buy_box_price || changed.fba_fee || changed.prep_cost) {
      keepa = { domain: ad.po.domain, po_id: poId, line_item_id: lineId };
      if (changed.buy_box_price) { keepa.buy_box_price = parseFloat(form.buy_box_price) || 0; patch.buy_box_price = keepa.buy_box_price; }
      if (changed.fba_fee) { keepa.fba_fee = parseFloat(form.fba_fee) || 0; patch[flags.fbm ? 'shipping_fee' : flags.wfs ? 'wfs_fee' : 'fba_fee'] = keepa.fba_fee; }
      if (changed.prep_cost) { keepa.prep_cost = parseFloat(form.prep_cost) || 0; patch.prep_cost = keepa.prep_cost; }
    }
    setAEditItem(function(cur) { return cur === lineId ? null : cur; });
    if (!writes.length && !keepa) return;
    var before = Object.assign({}, line);
    patchAnalysisLine(lineId, patch);
    try {
      for (var wi = 0; wi < writes.length; wi++) await api('/purchase-orders/' + poId + '/items/' + lineId, { method: 'PUT', body: JSON.stringify(writes[wi]) });
      if (keepa) await api('/analysis/keepa-cache/' + (line.asin || 'no-asin'), { method: 'PATCH', body: JSON.stringify(keepa) });
      if (writes.length) load();
      loadAnalysis();
    } catch (e) {
      setAnalysisData(function(prev) {
        if (!prev || !Array.isArray(prev.lines)) return prev;
        return Object.assign({}, prev, { lines: prev.lines.map(function(l) { return l.id === lineId ? before : l; }) });
      });
      showToast(e.message, 'error');
    }
  };
  // Optimistic refresh (Anthony 2026-09-29: "if I edit a field and then switch it takes a while to
  // update on the screen"): the saved value shows at once; the analysis reload (2.6 MB) and the PO
  // reload follow and settle the derived columns.
  var patchAnalysisLine = function(lineId, patch) {
    setAnalysisData(function(prev) {
      if (!prev || !Array.isArray(prev.lines)) return prev;
      return Object.assign({}, prev, { lines: prev.lines.map(function(l) { return l.id === lineId ? recalcAnalysisLine(Object.assign({}, l, patch)) : l; }) });
    });
  };
  var analysisFlags = function() {
    var ad = analysisData;
    return {
      fbm: !!(ad && ad.po && (ad.po.marketplace === 'US-FBM' || (ad.lines && ad.lines.length > 0 && ad.lines[0].is_fbm))),
      wfs: !!(ad && ad.po && (ad.po.wh_code || '').toUpperCase().indexOf('WFS') >= 0),
    };
  };
  // The editor form for a row with every editable field pre-filled (the cell click handlers build
  // the same shape inline), so Tab-through never lands on an empty cell.
  var analysisFormFor = function(l, focus) {
    var money = function(v) { return v != null && v !== '' ? Number(v).toFixed(2) : ''; };
    return {
      qty_ordered: l.qty_ordered,
      unit_cost: money(l.unit_cost),
      prep_cost: money(l.prep_cost),
      buy_box_price: money(l.buy_box_price),
      fba_fee: money(analysisFlags().fbm ? l.shipping_fee : l.fba_fee),
      referral_fee_pct: l.referral_fee_pct || 15,
      _focus: focus,
    };
  };
  // Leaving the Units field: the case-pack question comes up at once, its answer goes into the form
  // (so the save does not ask again) and Cancel puts the previous number back (Anthony 2026-09-29).
  var leaveAnalysisQty = async function(l, form, then) {
    var qty = parseInt(form.qty_ordered, 10);
    var upc = parseInt(l.units_per_case) || parseInt(l.case_qty) || 1;
    if (!isNaN(qty) && qty > 0 && qty !== Number(l.qty_ordered) && upc > 1 && qty % upc !== 0 && Number(form._caseOk) !== qty) {
      var asked = await askCasePackQty(qty, upc);
      if (asked == null) { setAEditForm(function(prev) { return Object.assign({}, prev, { qty_ordered: l.qty_ordered, _caseOk: undefined }); }); return; }
      setAEditForm(function(prev) { return Object.assign({}, prev, { qty_ordered: asked, _caseOk: asked }); });
    }
    then();
  };
  // Tab out of the last editable cell: save this row and open the next row's Units, like the Line
  // Items tab (Anthony 2026-09-29: "#26 isn't working right for the analysis tab" — Tab from the fee
  // cell used to set a focus no cell renders, so the editor simply vanished).
  var analysisTabToNextRow = function(l) {
    var sorted = aSortedRef.current || [];
    var idx = sorted.findIndex(function(r) { return r.id === l.id; });
    var next = idx >= 0 ? sorted[idx + 1] : null;
    aEditRef.current.id = null; // the blur that follows must not save this row a second time
    saveAnalysisField(l.id);
    if (!next) return;
    setAEditItem(next.id);
    setAEditForm(analysisFormFor(next, 'qty'));
  };
  // Always keep latest save function in the ref so blur/mousedown handlers aren't stale
  aEditRef.current.save = saveAnalysisField;
  // The display cells sit inside data-aedit-row, so clicking straight from one row's editor into
  // another row's cell never reached the click-away handler and the first row's unsaved edits were
  // dropped — save that row before switching (review 2026-09-28, medium #27).
  var flushAnalysisEdit = function(nextId) {
    var cur = aEditRef.current;
    if (cur.id && cur.id !== nextId && cur.save) cur.save(cur.id, cur.form);
  };

  // Blur handler for editable analysis inputs — saves when focus leaves the edit row
  var handleAnalysisBlur = function() {
    setTimeout(function() {
      if (!aEditRef.current.id) return;
      var active = document.activeElement;
      if (active && active.closest && (active.closest('[data-aedit-row]') || active.closest('[data-app-dialog]'))) return; // focus inside an in-app dialog is not leaving the row
      if (aEditRef.current.save) aEditRef.current.save(aEditRef.current.id, aEditRef.current.form);
    }, 0);
  };

  var exportAsinsForPo = function() {
    var ad = analysisData;
    if (!ad || !ad.po) return;
    var domain = ad.po.domain || 1;
    var a = document.createElement('a');
    a.href = '/api/analysis/export-asins?domain=' + domain + '&po_id=' + ad.po.id;
    a.download = 'asins-' + (domain === 1 ? 'US' : 'CA') + '.csv';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  var handleAImport = async function(e) {
    var file = e.target.files && e.target.files[0];
    if (!file) return;
    setAnalysisFetching('import'); setAnalysisFetchResult(null);
    try {
      var result = await apiUpload('/analysis/keepa-import', file);
      setAnalysisFetchResultAuto({ success: true, message: 'Imported ' + result.imported + ' ASINs from Keepa CSV (' + result.skipped + ' skipped).' });
      loadAnalysis();
    } catch (err) { setAnalysisFetchResultAuto({ success: false, message: err.message }); }
    setAnalysisFetching(false);
    if (aImportRef.current) aImportRef.current.value = '';
  };

  var aCsvImportRef = useRef(null);
  var [aCsvPreviewOpen, setACsvPreviewOpen] = useState(false);
  var [aCsvPreviewData, setACsvPreviewData] = useState(null);
  var [aCsvPendingFile, setACsvPendingFile] = useState(null);

  var exportAnalysisCsv = function() {
    var ad = analysisData;
    if (!ad || !ad.lines || !ad.lines.length) return;
    var csv = buildAnalysisCsv(ad);
    var blob = new Blob([csv], { type: 'text/csv' });
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url;
    a.download = 'analysis-' + (ad.po.po_number || ad.po.id) + '.csv';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  var handleAnalysisCsvImport = async function(e) {
    var file = e.target.files && e.target.files[0];
    if (!file) return;
    setAnalysisFetching('csv-import'); setAnalysisFetchResult(null);
    try {
      var result = await apiUpload('/analysis/po/' + poId + '/preview-csv', file);
      var hasInvalids = result.invalids && result.invalids.length > 0;
      if (!result.changes.length && !result.removals.length && !hasInvalids) {
        setAnalysisFetchResultAuto({ success: true, message: 'No changes detected in CSV.' });
      } else {
        setACsvPreviewData(result);
        setACsvPendingFile(file);
        setACsvPreviewOpen(true);
      }
    } catch (err) { setAnalysisFetchResultAuto({ success: false, message: err.message }); }
    setAnalysisFetching(false);
    if (aCsvImportRef.current) aCsvImportRef.current.value = '';
  };

  var handleAnalysisCsvConfirm = async function() {
    if (!aCsvPendingFile) return;
    setAnalysisFetching('csv-import');
    try {
      var form = new FormData();
      form.append('file', aCsvPendingFile);
      var removeIds = (aCsvPreviewData && aCsvPreviewData.removals || []).map(function(r) { return r.line_item_id; });
      if (removeIds.length) form.append('remove_ids', JSON.stringify(removeIds));
      var res = await fetch('/api/analysis/po/' + poId + '/import-csv', { method: 'POST', body: form, credentials: 'include' });
      if (!res.ok) { var err = await res.json().catch(function() { return {}; }); throw new Error(err.error || 'Import failed'); }
      var result = await res.json();
      var msg = 'CSV import: ' + result.updated + ' updated';
      if (result.removed) msg += ', ' + result.removed + ' moved to draft PO#' + result.new_po_number;
      msg += ', ' + result.skipped + ' skipped.';
      if (result.errors && result.errors.length) msg += ' Errors: ' + result.errors.join('; ');
      setAnalysisFetchResultAuto({ success: true, message: msg });
      load(); loadAnalysis();
    } catch (err) { setAnalysisFetchResultAuto({ success: false, message: err.message }); }
    setAnalysisFetching(false);
    setACsvPreviewOpen(false);
    setACsvPreviewData(null);
    setACsvPendingFile(null);
  };

  // Analysis inline edit: click-away auto-save
  useEffect(function() {
    if (!aEditItem) return;
    var handler = function(e) {
      var target = e.target;
      var isInside = false;
      while (target) {
        if (target.dataset && (target.dataset.aeditRow === 'true' || target.dataset.appDialog !== undefined)) { isInside = true; break; } // an in-app dialog is not "away"
        target = target.parentElement;
      }
      if (!isInside && aEditRef.current.id) {
        if (aEditRef.current.save) aEditRef.current.save(aEditRef.current.id, aEditRef.current.form);
      }
    };
    var timer = setTimeout(function() { document.addEventListener('mousedown', handler); }, 50);
    return function() { clearTimeout(timer); document.removeEventListener('mousedown', handler); };
  }, [aEditItem]);

  useEffect(() => { load(); loadNotes(); loadActivity(); loadReceivingHistory(); loadAnalysis(); }, [load]);
  useEffect(function() {
    api('/warehouses').then(setWarehouses).catch(function(e) { console.error('[silent-catch]', e); });
    api('/products').then(setProducts).catch(function(e) { console.error('[silent-catch]', e); });
    api('/vendors?active=true').then(setVendors).catch(function(e) { console.error('[silent-catch]', e); });
  }, []);

  // Live Billed/Unbilled badge (mirrors the PO list poll): hit the lightweight
  // /bill-statuses endpoint every 30s AND on tab focus, patching only bill_status
  // into state — no full PO reload, so in-progress edits on the page survive.
  useEffect(function() {
    if (!isValidPoId) return;
    var alive = true;
    var refreshBillStatus = function() {
      // Just this PO's badge (review L18) — the list endpoint returns every PO
      api('/purchase-orders/bill-statuses?id=' + encodeURIComponent(poId)).then(function(rows) {
        if (!alive || !Array.isArray(rows)) return;
        for (var i = 0; i < rows.length; i++) {
          if (Number(rows[i].id) === Number(poId)) {
            var bs = rows[i].bill_status;
            setPo(function(prev) {
              if (!prev || prev.bill_status === bs) return prev;
              return Object.assign({}, prev, { bill_status: bs });
            });
            break;
          }
        }
      }).catch(function(e) { console.error('[silent-catch]', e); });
    };
    var timer = setInterval(refreshBillStatus, 30000);
    var onFocus = function() { if (document.visibilityState !== 'hidden') refreshBillStatus(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return function() {
      alive = false;
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [poId]);

  // Auto-scroll notes to bottom
  useEffect(function() {
    if (notesEndRef.current) notesEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [notes]);

  var addNoteFiles = function(fileList) {
    var incoming = Array.prototype.slice.call(fileList || []);
    if (!incoming.length) return;
    var next = noteFiles.slice();
    for (var i = 0; i < incoming.length; i++) {
      if (next.length >= 5) { showToast('Max 5 attachments per note', 'error'); break; }
      if (incoming[i].size > 15 * 1024 * 1024) { showToast(incoming[i].name + ' is over the 15 MB limit', 'error'); continue; }
      next.push(incoming[i]);
    }
    setNoteFiles(next);
  };

  var sendNote = async function() {
    if ((!newNote.trim() && !noteFiles.length) || sendingNote) return;
    try {
      setSendingNote(true);
      if (noteFiles.length) {
        var fd = new FormData();
        fd.append('message', newNote.trim());
        fd.append('source', 'po');
        noteFiles.forEach(function(f) { fd.append('files', f, f.name); });
        var res = await fetch('/api/purchase-orders/' + poId + '/notes', { method: 'POST', body: fd, credentials: 'include' });
        if (!res.ok) {
          var errBody = await res.json().catch(function() { return {}; });
          throw new Error(errBody.error || 'Upload failed');
        }
      } else {
        await api('/purchase-orders/' + poId + '/notes', { method: 'POST', body: JSON.stringify({ message: newNote.trim(), source: 'po' }) });
      }
      setNewNote('');
      setNoteFiles([]);
      loadNotes();
      loadActivity();
    } catch (e) { showToast(e.message, 'error'); }
    finally { setSendingNote(false); }
  };

  var deleteNote = async function(noteId) {
    try {
      await api('/purchase-orders/' + poId + '/notes/' + noteId, { method: 'DELETE' });
      loadNotes();
      loadActivity();
    } catch (e) { showToast(e.message, 'error'); }
  };

  const updateStatus = async (status) => {
    if (status === 'checked-in') {
      setLocationModalOpen(true);
      return;
    }
    try {
      await api('/purchase-orders/' + poId, { method: 'PUT', body: JSON.stringify({ status }) });
      showToast('PO marked as ' + status); load(); loadActivity();
    }
    catch (e) { showToast(e.message, 'error'); }
  };

  var handleCheckInConfirm = async function(loc, pallets, boxes) {
    setLocationModalOpen(false);
    try {
      await api('/purchase-orders/' + poId, { method: 'PUT', body: JSON.stringify({ status: 'checked-in' }) });
      await api('/receiving/' + poId + '/location', { method: 'PUT', body: JSON.stringify({ warehouse_location: loc, pallets: pallets, boxes: boxes, append: true }) });
      showToast('PO checked in at ' + loc); load(); loadActivity();
    } catch (e) { showToast(e.message, 'error'); }
  };

  var saveField = async function(field, value) {
    try {
      var body = {};
      body[field] = value;
      await api('/purchase-orders/' + poId, { method: 'PUT', body: JSON.stringify(body) });
      setEditingField(null);
      load();
    } catch (e) { showToast(e.message, 'error'); }
  };

  var startEditField = function(field, currentVal) {
    setEditingField(field);
    setFieldVal(currentVal || '');
  };

  // Leaving the quantity field with a number that is not a whole number of cases asks AT ONCE
  // (Anthony 2026-09-29: the question used to wait for the save, one field later, and came up behind a
  // re-render of the whole table — "takes like 3 seconds"). The answer goes into the form, so the
  // save does not ask again; Cancel puts the previous number back in the field.
  var leaveLineQty = async function(li, form, then) {
    var qty = parseInt(form.qty_ordered, 10);
    var prod = products.find(function(p) { return p.id === li.product_id; });
    var upc = (prod && parseInt(prod.case_qty)) || parseInt(li.units_per_case) || 1;
    if (!isNaN(qty) && qty > 0 && qty !== Number(li.qty_ordered) && upc > 1 && qty % upc !== 0 && Number(form._caseOk) !== qty) {
      var asked = await askCasePackQty(qty, upc);
      if (asked == null) { setItemForm(function(prev) { return Object.assign({}, prev, { qty_ordered: li.qty_ordered, _caseOk: undefined }); }); return; }
      setItemForm(function(prev) { return Object.assign({}, prev, { qty_ordered: asked, _caseOk: asked }); });
    }
    then();
  };

  // Close the row editor only if it is still on the row that was saved: the save is async and
  // Tab / click-away had already moved the editor to the next row, so the late setEditingItem(null)
  // closed that row and dropped what was typed there (review 2026-09-28, medium #26).
  var closeLineEditor = function(itemId) {
    setEditingItem(function(cur) { return cur === itemId ? null : cur; });
  };
  var saveLineItem = async function(itemId, formOverride) {
    try {
      var form = formOverride || itemForm;
      var li = (po.line_items || []).find(function(l) { return l.id === itemId; });
      if (!li) { closeLineEditor(itemId); return; }
      var body = Object.assign({}, form);
      delete body._costFocus;
      delete body._focus;
      var caseOk = body._caseOk; delete body._caseOk; // the case-pack answer given when the quantity field was left

      // Drop fields the user didn't actually change so blurring an unchanged
      // cell doesn't trigger a no-op PUT. Compare numerically — server returns
      // money fields as strings (numeric column) so === would be a type mismatch.
      if (body.qty_ordered != null && body.qty_ordered !== '' && Number(body.qty_ordered) === Number(li.qty_ordered)) delete body.qty_ordered;
      if (body.unit_cost != null && body.unit_cost !== '' && Number(body.unit_cost) === Number(li.unit_cost)) delete body.unit_cost;
      if (Object.keys(body).length === 0) { closeLineEditor(itemId); return; }

      // Check case divisibility when qty changed
      if (li && body.qty_ordered != null) {
        // Get units per case from the products list (source of truth)
        var prod = products.find(function(p) { return p.id === li.product_id; });
        var upc = (prod && parseInt(prod.case_qty)) || parseInt(li.units_per_case) || 1;
        // already answered when the quantity field was left (leaveLineQty)? then it is not asked again
        if (upc > 1 && body.qty_ordered % upc !== 0 && Number(caseOk) !== Number(body.qty_ordered)) {
          var asked = await askCasePackQty(body.qty_ordered, upc);
          if (asked == null) { closeLineEditor(itemId); return; } // Cancel: nothing is saved and the cell shows the previous number again
          body.qty_ordered = asked;
        }
        // Per-SKU max units per PO: warn, never block — a typed number is deliberate.
        var maxPoRaw = (prod && prod.max_po_qty != null) ? prod.max_po_qty : li.max_po_qty;
        var poCapEdit = effectivePoCap(maxPoRaw, upc);
        if (poCapEdit && body.qty_ordered > poCapEdit) {
          var capMsg = body.qty_ordered + ' units exceeds this SKU\'s max of ' + poCapEdit + ' units per PO';
          if (poCapEdit !== parseInt(maxPoRaw)) capMsg += ' (max ' + maxPoRaw + ' rounded down to whole cases)';
          if (!(await appConfirm(capMsg + '.\n\nSave anyway?'))) return; // keep the editor open so the number can be fixed
        }
        body.case_qty = Math.ceil(body.qty_ordered / upc);
      }
      await api('/purchase-orders/' + poId + '/items/' + itemId, { method: 'PUT', body: JSON.stringify(body) });
      closeLineEditor(itemId);
      showToast('Line item updated');
      load();
    } catch (e) { showToast(e.message, 'error'); }
  };

  var removeLineItem = async function(itemId) {
    if (!(await appConfirm('Remove this item from the PO?', { danger: true, okLabel: 'Remove' }))) return;
    // Save item data before deleting for undo
    var item = (po.line_items || []).find(function(li) { return li.id === itemId; });
    try {
      await api('/purchase-orders/' + poId + '/items/' + itemId, { method: 'DELETE' });
      if (item) {
        setLastDeleted({ product_id: item.product_id, sku: item.sku, qty_ordered: item.qty_ordered, unit_cost: item.unit_cost });
        if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
        undoTimerRef.current = setTimeout(function() { setLastDeleted(null); }, 15000);
      }
      load(); loadActivity();
    } catch (e) { showToast(e.message, 'error'); }
  };

  var undoDelete = async function() {
    if (!lastDeleted) return;
    try {
      await api('/purchase-orders/' + poId + '/items', { method: 'POST', body: JSON.stringify({ product_id: lastDeleted.product_id, qty_ordered: lastDeleted.qty_ordered, unit_cost: lastDeleted.unit_cost, keep_qty: true }) }); // as it was: its cost and exact qty (review 2026-09-28, medium #29)
      showToast('Restored ' + lastDeleted.sku);
      setLastDeleted(null);
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
      load(); loadActivity();
    } catch (e) { showToast(e.message, 'error'); }
  };

  var liSelectedIds = Object.keys(liSelected).filter(function(k) { return liSelected[k]; }).map(Number);

  var bulkRemoveLineItems = async function() {
    if (!liSelCount) return;
    if (!(await appConfirm('Remove ' + liSelCount + ' item(s) from this PO?', { danger: true, okLabel: 'Remove' }))) return;
    try {
      var r = await api('/purchase-orders/' + poId + '/items/bulk', { method: 'DELETE', body: JSON.stringify({ ids: liSelectedIds }) });
      showToast((r.removed || liSelCount) + ' item(s) removed');
      setLiSelected({});
      load(); loadActivity();
    } catch (e) { showToast(e.message, 'error'); }
  };

  var bulkMoveToNext = async function() {
    if (!liSelCount) return;
    var skuNames = (po.line_items || []).filter(function(li) { return liSelected[li.id]; }).map(function(li) { return li.sku; });
    if (!(await appConfirm('Move ' + liSelCount + ' item(s) to a "Next" PO?\n\n' + skuNames.join(', ')))) return;
    try {
      // Check for duplicate SKUs in target
      var preview = await api('/analysis/move-to-next/preview', { method: 'POST', body: JSON.stringify({ source_po_id: poId, line_item_ids: liSelectedIds }) });
      if (preview.duplicates && preview.duplicates.length > 0) {
        var dupMsg = preview.duplicates.map(function(d) { return d.sku + ' is already on ' + preview.target_po + ' (x' + d.target_qty + '); moving x' + d.source_qty + ' keeps the larger: x' + Math.max(Number(d.target_qty) || 0, Number(d.source_qty) || 0); }).join('\n');
        if (!(await appConfirm('Duplicate SKUs found:\n\n' + dupMsg + '\n\nQuantities are not added together: each line keeps the larger quantity. Continue?'))) return;
      }
      var result = await api('/analysis/move-to-next', { method: 'POST', body: JSON.stringify({ source_po_id: poId, line_item_ids: liSelectedIds }) });
      var skippedLi = (result.skipped || []).map(function(s) { return s.sku; });
      showToast('Moved ' + result.moved + ' item(s) to ' + result.next_po_number + (skippedLi.length ? '. Not moved (has receipts): ' + skippedLi.join(', ') : ''), skippedLi.length ? 'warning' : undefined);
      setLiSelected({});
      load(); loadActivity();
    } catch (e) { showToast(e.message, 'error'); }
  };

  // One add at a time (sweep L20: a double click posted every selection twice; the server now
  // refuses the duplicate too, under a per-PO lock).
  var addSavingRef = useRef(false);
  const [addSaving, setAddSaving] = useState(false);
  var addProduct = async function() {
    if (addSavingRef.current) return;
    var entries = Object.keys(addSelections).filter(function(pid) { return parseInt(addSelections[pid]) > 0; });
    if (!entries.length) { showToast('Select products and enter quantities', 'error'); return; }
    addSavingRef.current = true; setAddSaving(true);
    var added = 0, errors = [];
    try {
      for (var i = 0; i < entries.length; i++) {
        var pid = parseInt(entries[i]);
        var qty = parseInt(addSelections[entries[i]]);
        try {
          await api('/purchase-orders/' + poId + '/items', { method: 'POST', body: JSON.stringify({ product_id: pid, qty_ordered: qty }) });
          added++;
        } catch (e) { errors.push(e.message); }
      }
    } finally { addSavingRef.current = false; setAddSaving(false); }
    if (added > 0) showToast(added + ' product' + (added > 1 ? 's' : '') + ' added');
    if (errors.length) showToast(errors.join('; '), 'error');
    setAddModalOpen(false); setAddSearch(''); setAddSelections({});
    load();
  };

  var openProductEdit = function(productId) {
    api('/products/' + productId).then(function(full) {
      setProdForm(Object.assign({}, full)); setProdEditing(full); setProdTab('details'); setProdModalOpen(true);
    }).catch(function(e) { showToast(e.message, 'error'); });
  };

  var saveProduct = async function() {
    try {
      await api('/products/' + prodForm.id, { method: 'PUT', body: JSON.stringify(prodForm) });
      // Sync product changes to all open PO line items
      var syncResult = await api('/purchase-orders/sync-product/' + prodForm.id, { method: 'PUT' });
      var msg = 'Product updated';
      // Costs follow the catalog on DRAFT POs only; ordered POs keep their price (sweep H1)
      if (syncResult.updated > 0) msg += ' — ' + syncResult.updated + ' PO line(s) synced' + (syncResult.cost_updated > 0 ? ', cost updated on ' + syncResult.cost_updated + ' draft line(s)' : '');
      showToast(msg);
      api('/products').then(setProducts).catch(function(e) { console.error('[silent-catch]', e); });
      load();
      loadAnalysis();
    } catch (e) { showToast(e.message, 'error'); }
  };

  var PF = function(key) { return { value: prodForm[key] != null ? prodForm[key] : '', onChange: function(e) { var next = Object.assign({}, prodForm); next[key] = e.target.value; setProdForm(next); } }; };

  var isTransferFromFBM = po && po.vendor_name === 'Transfer from FBM';
  // Zentail archives a listing when it stops selling it, but the FBM units are still in the
  // building and still have to be moved to FBA — so a transfer PO can pick from them too.
  var addCandidates = (isTransferFromFBM && archivedProducts.length) ? products.concat(archivedProducts) : products;
  var filteredProducts = addCandidates.filter(function(p) {
    // "Transfer from FBM" vendor can add any product
    if (!isTransferFromFBM) {
      if (po && po.vendor_id && p.vendor_id !== po.vendor_id) return false;
      if (po && po.warehouse_id && p.warehouse_id && p.warehouse_id !== po.warehouse_id) return false;
    }
    // Exclude aliases and kit children (kit parents are orderable)
    if (p.alias_of) return false;
    if (p.kit_parent) return false;
    // Exclude products already on this PO
    var existingSkus = (po && po.line_items || []).map(function(li) { return li.product_id; });
    if (existingSkus.indexOf(p.id) !== -1) return false;
    if (addSearch) {
      var s = addSearch.toLowerCase();
      return (p.sku && p.sku.toLowerCase().includes(s)) || (p.vendor_title && p.vendor_title.toLowerCase().includes(s)) || (p.vendor_sku && p.vendor_sku.toLowerCase().includes(s)) || (p.barcode && p.barcode.toLowerCase().includes(s));
    }
    return true;
  });

  var loadArchivedForTransfer = function() {
    if (!isTransferFromFBM || archivedLoaded) return;
    setArchivedLoaded(true);
    setArchivedLoading(true);
    api('/products?active=false')
      .then(setArchivedProducts)
      .catch(function(e) { console.error('[silent-catch]', e); })
      .finally(function() { setArchivedLoading(false); });
  };

  const csvRef = useRef(null);
  const [csvPreviewOpen, setCsvPreviewOpen] = useState(false);
  const [csvPreviewData, setCsvPreviewData] = useState(null);

  var downloadPOcsv = function() {
    var headers = ['sku', 'vendor_sku', 'vendor_title', 'qty_ordered', 'unit_cost'];
    // Transfer-from-FBM: "bin" is the editable pick-bin override (blank = auto FBM bins);
    // "current_bins" shows the auto-computed pick bins for reference (ignored on import).
    if (isTransferFromFBM) headers = headers.concat(['bin', 'current_bins']);
    var rows = (po.line_items || []).map(function(li) {
      var row = {};
      headers.forEach(function(h) { row[h] = li[h] != null ? li[h] : ''; });
      if (isTransferFromFBM) {
        row.bin = li.pick_bin_override || '';
        row.current_bins = li.bin_location || '';
      }
      return row;
    });
    var vendor = (po.vendor_name || 'Unknown').replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, '_');
    var wh = (po.wh_code || 'WH').replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, '_');
    downloadCSV(vendor + '_' + wh + '_PO_' + po.po_number + '.csv', arrayToCSV(headers, rows));
  };

  var downloadXLSX = async function() {
    try {
      var token = getAuthToken();
      var resp = await fetch('/api/purchase-orders/' + poId + '/export-xlsx', { credentials: 'include' });
      if (!resp.ok) { var err = await resp.json().catch(function() { return {}; }); throw new Error(err.error || 'Export failed'); }
      var blob = await resp.blob();
      var filename = resp.headers.get('content-disposition');
      filename = filename ? filename.split('filename=')[1].replace(/"/g, '') : 'PO-Excel.xlsx';
      var url = URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { showToast(e.message || 'Export failed', 'error'); }
  };

  var emailVendor = async function() {
    try {
      var tpls = await api('/purchase-orders/meta/templates');
      setPoEmailTemplates(tpls);
      var whTpl = po.wh_template_id ? tpls.find(function(t) { return t.id === po.wh_template_id; }) : null;
      var def = tpls.find(function(t) { return t.is_default; });
      var tid = whTpl ? whTpl.id : (def ? def.id : (tpls.length ? tpls[0].id : null));
      setSelectedTemplateId(tid);
      setEmailPreview(null);
      setEmailTo(po.vendor_email || '');
      setEmailSubject('');
      setEmailModalOpen(true);
      if (tid) {
        try {
          var preview = await api('/purchase-orders/' + poId + '/preview-email', { method: 'POST', body: JSON.stringify({ template_id: tid }) });
          setEmailPreview(preview);
          setEmailSubject(preview.subject || '');
          setEmailTplKey(function(k) { return k + 1; });
        } catch (e) { /* preview failed, still allow send */ }
      }
    } catch (e) { showToast(e.message || 'Failed to load templates', 'error'); }
  };

  var loadEmailPreview = async function(tid) {
    setSelectedTemplateId(tid);
    if (!tid) { setEmailPreview(null); setEmailSubject(''); return; }
    try {
      var preview = await api('/purchase-orders/' + poId + '/preview-email', { method: 'POST', body: JSON.stringify({ template_id: Number(tid) }) });
      setEmailPreview(preview);
      setEmailSubject(preview.subject || '');
      setEmailTplKey(function(k) { return k + 1; });
    } catch (e) { setEmailPreview(null); }
  };

  // Mirrors the PO Notes composer (addNoteFiles): 5 files, 15 MB each, matching the multer
  // limits on the server so the user learns about a rejection before the upload starts.
  var postEmail = async function(path, payload) {
    if (!emailFiles.length) return api(path, { method: 'POST', body: JSON.stringify(payload) });
    var fd = new FormData();
    Object.keys(payload).forEach(function(k) { fd.append(k, payload[k] == null ? '' : String(payload[k])); });
    emailFiles.forEach(function(f) { fd.append('files', f, f.name); });
    var res = await fetch('/api' + path, { method: 'POST', body: fd, credentials: 'include' });
    if (!res.ok) {
      var errBody = await res.json().catch(function() { return {}; });
      throw new Error(errBody.error || 'Upload failed');
    }
    return res.json();
  };

  var addEmailFiles = function(fileList) {
    var incoming = Array.prototype.slice.call(fileList || []);
    if (!incoming.length) return;
    var next = emailFiles.slice();
    for (var i = 0; i < incoming.length; i++) {
      if (next.length >= 5) { showToast('Max 5 attachments per e-mail', 'error'); break; }
      if (incoming[i].size > 15 * 1024 * 1024) { showToast(incoming[i].name + ' is over the 15 MB limit', 'error'); continue; }
      next.push(incoming[i]);
    }
    setEmailFiles(next);
  };

  var sendEmailWithTemplate = async function() {
    setEmailSending(true);
    try {
      var finalHtml = emailBodyRef.current ? emailBodyRef.current.innerHTML : (emailPreview ? emailPreview.html : '');
      if (scheduleMode && scheduleDateTime) {
        var payload = { po_id: poId, scheduled_at: new Date(scheduleDateTime).toISOString(), recipient: emailTo, subject: emailSubject, html: finalHtml };
        await postEmail('/purchase-orders/meta/schedule-email', payload);
        showToast('Email scheduled for ' + new Date(scheduleDateTime).toLocaleString());
        setEmailModalOpen(false);
        setScheduleMode(false);
        setScheduleDateTime('');
        setEmailFiles([]); setEmailDragOver(false);
        load();
      } else {
        var payload = { to: emailTo, subject: emailSubject, html: finalHtml };
        var result = await postEmail('/purchase-orders/' + poId + '/email-vendor', payload);
        showToast(result.message || 'Email sent');
        setEmailModalOpen(false);
        setEmailFiles([]); setEmailDragOver(false);
        load();
      }
    } catch (e) { showToast(e.message || 'Failed to send email', 'error'); }
    setEmailSending(false);
  };

  var loadScheduledEmails = async function() {
    try {
      var data = await api('/purchase-orders/meta/scheduled-emails');
      setScheduledEmails(data);
      setShowScheduledEmails(true);
    } catch (e) { showToast(e.message || 'Failed to load', 'error'); }
  };

  var cancelScheduledEmail = async function(id) {
    if (!(await appConfirm('Cancel this scheduled email?', { danger: true, okLabel: 'Cancel email' }))) return;
    try {
      await api('/purchase-orders/meta/scheduled-emails/' + id, { method: 'DELETE' });
      showToast('Scheduled email cancelled');
      loadScheduledEmails();
    } catch (e) { showToast(e.message || 'Failed to cancel', 'error'); }
  };

  var uploadPOcsv = function(e) {
    var file = e.target.files[0];
    if (!file) return;
    var reader = new FileReader();
    reader.onload = async function(ev) {
      var text = ev.target.result;
      // RFC 4180 parser: tracks quote state across newlines so multi-line quoted fields parse correctly
      var parseCSV = function(input) {
        var rows = []; var row = []; var field = ''; var inQuotes = false;
        for (var i = 0; i < input.length; i++) {
          var ch = input[i];
          if (inQuotes) {
            if (ch === '"' && input[i + 1] === '"') { field += '"'; i++; }
            else if (ch === '"') { inQuotes = false; }
            else { field += ch; }
          } else {
            if (ch === '"') { inQuotes = true; }
            else if (ch === ',') { row.push(field.trim()); field = ''; }
            else if (ch === '\n' || ch === '\r') {
              if (ch === '\r' && input[i + 1] === '\n') i++;
              row.push(field.trim());
              if (row.some(function(f) { return f.length > 0; })) rows.push(row);
              row = []; field = '';
            }
            else { field += ch; }
          }
        }
        if (field.length > 0 || row.length > 0) {
          row.push(field.trim());
          if (row.some(function(f) { return f.length > 0; })) rows.push(row);
        }
        return rows;
      };
      var rows = parseCSV(text);
      if (rows.length < 2) { showToast('CSV is empty', 'error'); return; }
      var headers = rows[0].map(function(h) { return h.toLowerCase(); });
      var skuIdx = headers.indexOf('sku');
      var qtyIdx = headers.indexOf('qty_ordered');
      var costIdx = headers.indexOf('unit_cost');
      var caseIdx = headers.indexOf('case_qty');
      // "bin" column = pick-bin override, honored on Transfer-from-FBM POs only
      var binIdx = isTransferFromFBM ? headers.indexOf('bin') : -1;
      if (skuIdx === -1) { showToast('CSV must have a "sku" column', 'error'); return; }

      var items = [];
      for (var i = 1; i < rows.length; i++) {
        var vals = rows[i];
        var sku = vals[skuIdx];
        if (!sku) continue;
        var item = { sku: sku };
        if (qtyIdx !== -1 && vals[qtyIdx]) item.qty_ordered = vals[qtyIdx];
        if (costIdx !== -1 && vals[costIdx]) item.unit_cost = vals[costIdx].replace('$', '');
        if (caseIdx !== -1 && vals[caseIdx]) item.case_qty = vals[caseIdx];
        // Column present → carry the cell even when blank (a blank cell clears the override)
        if (binIdx !== -1) item.bin = vals[binIdx] || '';
        items.push(item);
      }

      if (!items.length) { showToast('No valid rows in CSV', 'error'); return; }

      // Resolve alias and kit SKUs to parent products
      var kitResolutions = [];
      var aliasResolutions = [];
      items = items.map(function(it) {
        var prod = products.find(function(p) { return p.sku === it.sku; });
        if (prod && prod.alias_of) {
          aliasResolutions.push({ original_sku: it.sku, resolved_sku: prod.alias_of });
          return Object.assign({}, it, { sku: prod.alias_of });
        }
        if (prod && prod.kit_parent) {
          var kitQty = parseInt(prod.kit_qty) || 1;
          var origSku = it.sku;
          var origQty = parseInt(it.qty_ordered) || 0;
          var resolvedQty = origQty > 0 ? origQty * kitQty : 0;
          kitResolutions.push({ original_sku: origSku, resolved_sku: prod.kit_parent, kit_qty: kitQty, original_qty: origQty, resolved_qty: resolvedQty });
          return Object.assign({}, it, { sku: prod.kit_parent, qty_ordered: resolvedQty > 0 ? String(resolvedQty) : it.qty_ordered });
        }
        return it;
      });

      // Merge duplicate SKUs after kit resolution (e.g. two kit children resolving to same parent)
      var merged = {};
      items.forEach(function(it) {
        if (merged[it.sku]) {
          var existQty = parseInt(merged[it.sku].qty_ordered) || 0;
          var newQty = parseInt(it.qty_ordered) || 0;
          if (existQty > 0 || newQty > 0) merged[it.sku].qty_ordered = String(existQty + newQty);
        } else {
          merged[it.sku] = Object.assign({}, it);
        }
      });
      items = Object.keys(merged).map(function(k) { return merged[k]; });

      // Build client-side diff
      var currentItems = po.line_items || [];
      var currentBySku = {};
      currentItems.forEach(function(li) { if (li.sku) currentBySku[li.sku] = li; });
      var csvSkus = {};
      items.forEach(function(it) { csvSkus[it.sku] = it; });

      var added = [];
      var updated = [];
      var unchanged = 0;
      var normBin = function(v) { return formatBin(v || '') || ''; };
      items.forEach(function(it) {
        var existing = currentBySku[it.sku];
        if (!existing) {
          added.push({ sku: it.sku, qty: it.qty_ordered || '—' });
        } else {
          var newQty = it.qty_ordered != null ? Number(it.qty_ordered) : null;
          var oldQty = existing.qty_ordered != null ? Number(existing.qty_ordered) : null;
          var newCost = it.unit_cost != null ? Number(it.unit_cost) : null;
          var oldCost = existing.unit_cost != null ? Number(existing.unit_cost) : null;
          var qtyChanged = newQty !== null && newQty !== oldQty;
          var costChanged = newCost !== null && newCost !== oldCost;
          var binChanged = it.bin !== undefined && normBin(it.bin) !== normBin(existing.pick_bin_override);
          if (qtyChanged || costChanged || binChanged) {
            updated.push({ sku: it.sku, oldQty: oldQty, newQty: qtyChanged ? newQty : null, oldCost: oldCost, newCost: costChanged ? newCost : null,
              oldBin: binChanged ? (existing.pick_bin_override || null) : undefined,
              newBin: binChanged ? (normBin(it.bin) || null) : undefined });
          } else {
            unchanged++;
          }
        }
      });
      var removed = [];
      currentItems.forEach(function(li) {
        if (li.sku && !csvSkus[li.sku]) {
          removed.push({ sku: li.sku, qty: li.qty_ordered });
        }
      });

      // Check case qty divisibility warnings
      var warnings = [];
      items.forEach(function(it) {
        var qty = it.qty_ordered != null ? Number(it.qty_ordered) : null;
        if (!qty || qty <= 0) return;
        var existing = currentBySku[it.sku];
        var prod = existing ? products.find(function(p) { return p.id === existing.product_id; }) : null;
        var upc = (prod && parseInt(prod.case_qty)) || (existing && parseInt(existing.units_per_case)) || 0;
        if (upc > 1 && qty % upc !== 0) {
          var roundDown = Math.floor(qty / upc) * upc;
          var roundUp = Math.ceil(qty / upc) * upc;
          warnings.push({ sku: it.sku, qty: qty, upc: upc, roundDown: roundDown, roundUp: roundUp });
        }
      });

      setCsvPreviewData({ items: items, added: added, updated: updated, removed: removed, unchanged: unchanged, warnings: warnings, kitResolutions: kitResolutions, aliasResolutions: aliasResolutions });
      setCsvPreviewOpen(true);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  var confirmCsvUpload = async function() {
    if (!csvPreviewData) return;
    try {
      var result = await api('/purchase-orders/' + poId + '/items-bulk', { method: 'PUT', body: JSON.stringify({ items: csvPreviewData.items }) });
      var msg = [];
      if (result.updated) msg.push(result.updated + ' updated');
      if (result.added) msg.push(result.added + ' added');
      if (result.removed) msg.push(result.removed + ' removed');
      if (result.bins_updated) msg.push(result.bins_updated + ' pick bin' + (result.bins_updated > 1 ? 's' : ''));
      showToast(msg.join(', ') || 'No changes');
      if (result.skipped_received && result.skipped_received.length) {
        showToast('Kept ' + result.skipped_received.length + ' line' + (result.skipped_received.length > 1 ? 's' : '') + ' with received units that the CSV omitted: ' + result.skipped_received.join(', '), 'warning');
      }
      if (result.errors && result.errors.length) showToast(result.errors.join(', '), 'warning');
      setCsvPreviewOpen(false);
      setCsvPreviewData(null);
      load();
    } catch (err) { showToast(err.message, 'error'); }
  };

  if (!po && poLoadError) return React.createElement('div', { style: { padding: 40, textAlign: 'center', color: C.textMuted } },
    React.createElement('div', { style: { color: C.danger, marginBottom: 12 } }, 'Could Not Load This PO: ' + poLoadError),
    React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: function() { setPoLoadError(null); load(); } }, 'Retry'));
  if (!po) return React.createElement('div', { style: { padding: 40, textAlign: 'center', color: C.textMuted } }, 'Loading...');

  var isEditable = !['closed', 'cancelled'].includes(po.status);

  // `style` overrides the display row (the vendor and ship-to names are headings at 15px / 600,
  // whatever the PO's status — a draft used to show them at this row's 13px, Anthony 9-07).
  var editableField = function(field, label, value, style) {
    if (editingField === field) {
      if (field === 'warehouse_id' || field === 'vendor_id') {
        var selectOptions = field === 'warehouse_id'
          ? warehouses.map(function(w) { return React.createElement('option', { key: w.id, value: w.id }, w.warehouse_id + ' — ' + w.name); })
          : vendors.map(function(v) { return React.createElement('option', { key: v.id, value: v.id }, v.vendor_name); });
        return React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 } },
          React.createElement('select', {
            value: fieldVal, onChange: function(e) { setFieldVal(e.target.value); },
            style: { background: C.bg, border: '1px solid ' + C.borderFocus, color: C.text, padding: '4px 8px', borderRadius: 4, fontSize: 12, fontFamily: 'inherit' }
          },
            React.createElement('option', { value: '' }, '— Select —'),
            selectOptions
          ),
          React.createElement('button', { onClick: function() { saveField(field, fieldVal); }, style: { background: 'none', border: 'none', color: C.success, cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center' } }, React.createElement(LIcon, { name: 'check', size: 14 })),
          React.createElement('button', { onClick: function() { setEditingField(null); }, style: { background: 'none', border: 'none', color: C.danger, cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center' } }, React.createElement(LIcon, { name: 'x', size: 14 }))
        );
      }
      return React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 } },
        React.createElement('input', {
          value: fieldVal, onChange: function(e) { setFieldVal(e.target.value); },
          onKeyDown: function(e) { if (e.key === 'Enter') saveField(field, fieldVal); if (e.key === 'Escape') setEditingField(null); },
          autoFocus: true,
          style: { background: C.bg, border: '1px solid ' + C.borderFocus, color: C.text, padding: '4px 8px', borderRadius: 4, fontSize: 12, fontFamily: 'inherit', width: 140 }
        }),
        React.createElement('button', { onClick: function() { saveField(field, fieldVal); }, style: { background: 'none', border: 'none', color: C.success, cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center' } }, React.createElement(LIcon, { name: 'check', size: 14 })),
        React.createElement('button', { onClick: function() { setEditingField(null); }, style: { background: 'none', border: 'none', color: C.danger, cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center' } }, React.createElement(LIcon, { name: 'x', size: 14 }))
      );
    }
    return React.createElement('div', {
      onClick: isEditable ? function() { startEditField(field, field === 'vendor_id' ? po.vendor_id : value); } : undefined,
      onMouseOver: isEditable ? function(e) { var icon = e.currentTarget.querySelector('.edit-pencil'); if (icon) { icon.style.opacity = '1'; icon.style.background = C.accent + '20'; } } : undefined,
      onMouseOut: isEditable ? function(e) { var icon = e.currentTarget.querySelector('.edit-pencil'); if (icon) { icon.style.opacity = '0.4'; icon.style.background = 'transparent'; } } : undefined,
      style: Object.assign({ fontSize: 13, color: C.text, cursor: isEditable ? 'pointer' : 'default', display: 'flex', alignItems: 'center', gap: 4 }, style || {})
    },
      value || '—',
      isEditable && React.createElement('span', { className: 'edit-pencil', style: { opacity: 0.4, padding: '2px 4px', borderRadius: 4, display: 'inline-flex', transition: 'all 0.15s' } }, React.createElement(LIcon, { name: 'pencil', size: 10 }))
    );
  };

  var lineItemCols = [
    { key: '_select', label: '', locked: true, sticky: true, stopClick: true, noFilter: true, style: { width: 44 },
      headerRender: function() {
        var items = po ? (po.line_items || []) : [];
        var allChecked = items.length > 0 && items.every(function(li) { return liSelected[li.id]; });
        var someChecked = !allChecked && items.some(function(li) { return liSelected[li.id]; });
        return React.createElement('div', {
          onClick: function(e) { e.stopPropagation(); toggleLiAll(); },
          style: { width: 32, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', userSelect: 'none' }
        },
          React.createElement('div', {
            style: { width: 18, height: 18, borderRadius: 4, border: '2px solid ' + (allChecked || someChecked ? C.accent : C.textDim),
              background: allChecked ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }
          },
            allChecked && React.createElement(LIcon, { name: 'check', size: 12, color: '#fff' }),
            someChecked && !allChecked && React.createElement('span', { style: { color: C.accent, fontSize: 12, fontWeight: 700, lineHeight: 1 } }, '–')
          )
        );
      },
      render: function(li) {
        var checked = !!liSelected[li.id];
        return React.createElement('div', {
          onClick: function(e) { e.stopPropagation(); toggleLiSel(li.id, e); },
          style: { width: 32, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', userSelect: 'none' }
        }, React.createElement('div', { style: { width: 18, height: 18, borderRadius: 4, border: '2px solid ' + (checked ? C.accent : C.textDim), background: checked ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' } },
          checked && React.createElement(LIcon, { name: 'check', size: 12, color: '#fff' })
        ));
      }
    },
    { key: 'sku', label: 'SKU', copyable: true, render: function(li) {
      return React.createElement('span', { style: { display: 'flex', alignItems: 'center' } },
        React.createElement('a', {
          href: li.product_id ? '/catalog/' + li.product_id : '#',
          onClick: function(e) {
            if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) return;
            e.preventDefault(); e.stopPropagation(); openProductEdit(li.product_id);
          },
          title: li.sku || '',
          style: { fontWeight: 600, color: C.accent, fontFamily: 'inherit', fontSize: 11, cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: '3px', display: 'block', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
        }, li.sku),
        TopRevChip(li),
        TransparencyChip(li)
      );
    }},
    { key: 'vendor_sku', label: 'Vendor SKU', copyable: true },
    { key: 'vendor_title', label: 'Vendor Title', copyable: true, style: { color: C.text }, render: function(li) {
      return React.createElement('span', { title: li.vendor_title || '', style: { display: 'block', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, li.vendor_title || '');
    } },
    { key: 'asin', label: (po.wh_code || '').toUpperCase().indexOf('WFS') >= 0 ? 'Walmart Item#' : (po.wh_code || '').toUpperCase().indexOf('MELI') >= 0 ? 'ML Code' : 'ASIN', defaultVisible: false, copyable: true, render: function(li) {
      var whCode = (po.wh_code || '').toUpperCase();
      var isWFS = whCode.indexOf('WFS') >= 0;
      var isMELI = whCode.indexOf('MELI') >= 0;
      if (isMELI) {
        return li.ml_code ? React.createElement('span', { style: { fontFamily: 'inherit', fontSize: 11 } }, li.ml_code) : '—';
      }
      if (isWFS) {
        if (!li.walmart_item_id) return '—';
        return React.createElement('a', { href: 'https://www.walmart.com/ip/' + li.walmart_item_id, target: '_blank', onClick: function(e) { e.stopPropagation(); },
          style: { color: C.accent, textDecoration: 'none', fontFamily: 'inherit', fontSize: 11 } }, li.walmart_item_id);
      }
      if (!li.asin) return '—';
      var amzSuffix = ['FBACAN','FBACANDI','FBACANHAZ'].indexOf(whCode) >= 0 ? '.ca' : '.com';
      return React.createElement('a', { href: 'https://www.amazon' + amzSuffix + '/dp/' + li.asin, target: '_blank', onClick: function(e) { e.stopPropagation(); },
        style: { color: C.accent, textDecoration: 'none', fontFamily: 'inherit', fontSize: 11 } }, li.asin);
    } },
    { key: 'qty_ordered', label: 'Units', sortKey: 'qty_ordered', render: function(li) {
      if (editingItem === li.id && itemForm._focus === 'qty') {
        var unitsPerCase = li.units_per_case || 1;
        var currentQty = itemForm.qty_ordered != null && itemForm.qty_ordered !== '' ? itemForm.qty_ordered : 0;
        var notDivisible = unitsPerCase > 1 && currentQty > 0 && (currentQty % unitsPerCase !== 0);
        return React.createElement('div', { 'data-edit-row': 'true', onClick: function(e) { e.stopPropagation(); } },
          React.createElement('input', { type: 'number', autoFocus: true, onFocus: function(e) { e.target.select(); },
            value: itemForm.qty_ordered != null ? itemForm.qty_ordered : '',
            onChange: function(e) { setItemForm(Object.assign({}, itemForm, { qty_ordered: e.target.value === '' ? '' : parseInt(e.target.value) })); },
            onKeyDown: function(e) { if (e.key === 'Enter') saveLineItem(li.id); if (e.key === 'Escape') setEditingItem(null); if (e.key === 'Tab') { e.preventDefault(); leaveLineQty(li, itemForm, function() { setItemForm(function(prev) { return Object.assign({}, prev, { _focus: 'cost' }); }); }); } },
            style: { width: 60, background: C.bg, border: '1px solid ' + (notDivisible ? C.warning : C.borderFocus), color: C.text, padding: '2px 6px', borderRadius: 4, fontSize: 12, fontFamily: 'inherit' }
          }),
          notDivisible && React.createElement('div', { style: { fontSize: 10, color: C.warning, marginTop: 2, whiteSpace: 'nowrap' } }, 'Not divisible by ' + unitsPerCase)
        );
      }
      // Per-SKU max units per PO: warn (never block) when a line sits above the cap.
      var poCapLi = effectivePoCap(li.max_po_qty, li.units_per_case || 1);
      var overCapChip = (poCapLi && Number(li.qty_ordered) > poCapLi) ? React.createElement('span', {
        title: 'Exceeds this SKU’s max of ' + poCapLi + ' units per PO' + (poCapLi !== parseInt(li.max_po_qty) ? ' (max ' + li.max_po_qty + ' rounded down to whole cases)' : ''),
        style: { fontSize: 10, fontWeight: 600, color: C.warning, border: '1px solid ' + C.warning, borderRadius: 999, padding: '0 6px', marginLeft: 6, whiteSpace: 'nowrap', cursor: 'default' }
      }, 'over max ' + poCapLi) : null;
      if (!isEditable) return overCapChip ? React.createElement('span', null, String(li.qty_ordered), overCapChip) : String(li.qty_ordered);
      return React.createElement('span', null,
        React.createElement('span', {
          onClick: function(e) { e.stopPropagation(); setEditingItem(li.id); setItemForm({ qty_ordered: li.qty_ordered, unit_cost: li.unit_cost != null && li.unit_cost !== '' ? Number(li.unit_cost).toFixed(2) : '', _focus: 'qty' }); },
          style: { cursor: 'pointer', color: C.text, borderBottom: '1px dashed ' + C.textDim }
        }, String(li.qty_ordered)),
        overCapChip
      );
    }},
    { key: 'case_qty', label: 'Cases', render: function(li) {
      var unitsPerCase = li.units_per_case || 1;
      if (editingItem === li.id && itemForm._focus === 'qty') {
        var currentQty = itemForm.qty_ordered != null && itemForm.qty_ordered !== '' ? itemForm.qty_ordered : li.qty_ordered;
        var newCases = unitsPerCase > 0 ? Math.ceil(currentQty / unitsPerCase) : li.case_qty;
        return React.createElement('span', { style: { color: C.accent, fontWeight: 500 } }, String(newCases));
      }
      var cases = unitsPerCase > 1 ? Math.ceil(li.qty_ordered / unitsPerCase) : li.case_qty;
      return String(cases);
    }},
    { key: 'unit_cost', label: 'Unit Cost', sortKey: 'unit_cost', render: function(li) {
      if (editingItem === li.id && itemForm._focus === 'cost') {
        return React.createElement('div', { 'data-edit-row': 'true', onClick: function(e) { e.stopPropagation(); } },
          React.createElement('input', { type: 'number', step: '0.01', autoFocus: true, onFocus: function(e) { e.target.select(); },
            value: itemForm.unit_cost != null ? itemForm.unit_cost : '',
            onChange: function(e) { setItemForm(Object.assign({}, itemForm, { unit_cost: e.target.value })); },
            onKeyDown: function(e) {
              if (e.key === 'Enter') saveLineItem(li.id);
              if (e.key === 'Escape') setEditingItem(null);
              if (e.key === 'Tab') {
                e.preventDefault();
                var sorted = liSortedRef.current;
                var idx = sorted.findIndex(function(r) { return r.id === li.id; });
                var next = sorted[idx + 1];
                if (next) {
                  saveLineItem(li.id);
                  setEditingItem(next.id);
                  setItemForm({ qty_ordered: next.qty_ordered, unit_cost: next.unit_cost != null && next.unit_cost !== '' ? Number(next.unit_cost).toFixed(2) : '', _focus: 'qty' });
                } else {
                  saveLineItem(li.id);
                }
              }
            },
            style: { width: 70, background: C.bg, border: '1px solid ' + C.borderFocus, color: C.text, padding: '2px 6px', borderRadius: 4, fontSize: 12, fontFamily: 'inherit' }
          })
        );
      }
      if (!isEditable) return '$' + Number(li.unit_cost || 0).toFixed(2);
      return React.createElement('span', {
        onClick: function(e) { e.stopPropagation(); setEditingItem(li.id); setItemForm({ qty_ordered: li.qty_ordered, unit_cost: li.unit_cost != null && li.unit_cost !== '' ? Number(li.unit_cost).toFixed(2) : '', _focus: 'cost' }); },
        style: { cursor: 'pointer', color: C.textMuted, borderBottom: '1px dashed ' + C.textDim }
      }, '$' + Number(li.unit_cost || 0).toFixed(2));
    }},
    { key: 'line_total', label: 'Line Cost', render: function(li) { return '$' + Number(li.line_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 }); }, style: { fontWeight: 600, color: C.text } },
  ];


  return React.createElement('div', null,
    // Header (rg-*: narrow widths wrap the action buttons under the title — app-utils, 9-27)
    React.createElement('div', { className: 'rg-wrap', style: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 } },
      React.createElement('button', { onClick: () => onNavigate(backPage || 'orders', null, backOpts || {}), style: { background: 'none', border: 'none', color: C.textMuted, cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center' } }, React.createElement(LIcon, { name: 'arrow-left', size: 20 })),
      React.createElement('div', { style: { flex: 1 } },
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' } },
          React.createElement('h2', { style: { margin: 0, fontSize: 22, fontWeight: 700, color: C.text, fontFamily: 'inherit' } }, 'PO #' + po.po_number),
          React.createElement(Badge, { status: po.status }),
          po.has_errored_fba_items && React.createElement('span', {
            title: 'Un-binned errored FBA items on this PO — review on the Errored tab of the related FBA plan',
            style: { padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 700, background: C.danger + '22', color: C.danger, textTransform: 'uppercase', letterSpacing: '0.05em' }
          }, 'Errored'),
          React.createElement(window.BilledBadge, { billed: po.bill_status === 'billed', isTransfer: po.vendor_name === 'Transfer from FBM', source: po.bill_link_source || 'auto', bdcBillId: po.bdc_bill_id, invoiceNumber: po.bill_invoice_number, amount: po.bill_amount, bills: po.linked_bills, big: true }),
          urgency && urgency.stockout_count > 0 && React.createElement('span', {
            title: urgency.stockout_count + ' of ' + urgency.line_count + ' line items physically out of stock with no other coverage incoming',
            style: { padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: '#7C2D12' + '33', color: '#FCA5A5' }
          }, urgency.stockout_pct + '% Stockouts')
        ),
        React.createElement('p', { style: { margin: '2px 0 0', fontSize: 13, color: C.textMuted } }, fmtDateOnly(po.po_date, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }, 'en-US')),
        // Previous / Next through the list this PO was opened from, under the date (Anthony 9-07)
        React.createElement(PONavButtons, { poId: poId, onNavigate: onNavigate })
      ),
      React.createElement('div', { className: 'rg-wrap', style: { display: 'flex', gap: 6 } },
        po.status === 'draft' && React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: () => updateStatus('approved'), style: { background: '#22C55E' } }, 'Mark Approved'),
        po.status === 'draft' && React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: () => updateStatus('next'), style: { background: '#06B6D4' } }, 'Mark Next'),
        po.status === 'approved' && React.createElement(Btn, { size: 'sm', onClick: emailVendor }, React.createElement(LIcon, { name: 'send', size: 14 }), ' Submit to Vendor'),
        po.status === 'approved' && React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: () => updateStatus('submitted'), style: { background: '#3B82F6' } }, 'Mark Submitted'),
        po.status === 'approved' && React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: () => updateStatus('next'), style: { background: '#06B6D4' } }, 'Mark Next'),
        po.status === 'submitted' && React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: () => updateStatus('confirmed') }, 'Mark Confirmed'),
        po.status === 'submitted' && React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: () => updateStatus('next'), style: { background: '#06B6D4' } }, 'Mark Next'),
        ['submitted', 'confirmed'].includes(po.status) && po.fba_direct && React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: function() { onNavigate('fba-shipments', null, { createWithPO: po.id }); }, style: { background: '#8B5CF6' } }, React.createElement(LIcon, { name: 'package', size: 14 }), ' Create FBA Shipment'),
        ['submitted', 'confirmed'].includes(po.status) && po.wfs_direct && React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: function() { onNavigate('wfs-shipments', null, { createWithPO: po.id }); }, style: { background: '#F59E0B' } }, React.createElement(LIcon, { name: 'package', size: 14 }), ' Create WFS Shipment'),
        // MELI Direct (Anthony 2026-09-29): the same step for a PO the vendor ships straight to Mercado Libre
        ['submitted', 'confirmed'].includes(po.status) && po.meli_direct && React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: function() { onNavigate('meli-shipments', null, { createWithPO: po.id }); }, style: { background: '#FACC15', color: '#1F2937' } }, React.createElement(LIcon, { name: 'package', size: 14 }), ' Create MELI Shipment'),
        po.status === 'confirmed' && React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: () => updateStatus('shipped') }, 'Mark Shipped'),
        po.status === 'confirmed' && React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: () => updateStatus('next'), style: { background: '#06B6D4' } }, 'Mark Next'),
        po.status === 'shipped' && React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: () => updateStatus('checked-in'), style: { background: C.purple || '#8B5CF6' } }, 'Mark Checked-In'),
        po.status === 'shipped' && React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: () => updateStatus('next'), style: { background: '#06B6D4' } }, 'Mark Next'),
        po.status === 'checked-in' && React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: () => updateStatus('receiving'), style: { background: C.warning } }, 'Mark Receiving'),
        po.status === 'checked-in' && React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: () => updateStatus('next'), style: { background: '#06B6D4' } }, 'Mark Next'),
        po.status === 'receiving' && React.createElement(Btn, { variant: 'success', size: 'sm', onClick: () => updateStatus('received') }, 'Mark Received'),
        po.status === 'receiving' && React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: () => updateStatus('next'), style: { background: '#06B6D4' } }, 'Mark Next'),
        po.status === 'received' && React.createElement(Btn, { size: 'sm', onClick: async () => {
          if (po.has_errored_fba_items) {
            if (!(await appConfirm('This PO still has un-binned errored FBA items.\n\nReview the Errored tab on the related FBA plan before closing.\n\nClose anyway?'))) return;
          }
          updateStatus('closed');
        } }, 'Mark Closed'),
        po.status === 'received' && React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: () => updateStatus('next'), style: { background: '#06B6D4' } }, 'Mark Next'),
        !['closed', 'cancelled', 'checked-in', 'receiving', 'received'].includes(po.status) && React.createElement(Btn, { variant: 'danger', size: 'sm', onClick: async () => {
          // Review 2026-09-28, medium #28: this used to cancel on the click with no confirmation.
          if (!(await appConfirm('Cancel PO ' + po.po_number + '?\n\nStaged inventory is released and scheduled vendor e-mails are dropped. There is no un-cancel on this page.', { danger: true, okLabel: 'Cancel PO' }))) return;
          updateStatus('cancelled');
        } }, 'Cancel')
      )
    ),
    // ERRORED banner — shown when a 'received' PO still has un-binned FBA errored items.
    // Soft signal: doesn't block the user, just steers them to the related FBA plan.
    po.status === 'received' && po.has_errored_fba_items && React.createElement('div', {
      style: { padding: '10px 14px', marginBottom: 16, borderRadius: 4, border: '1px solid ' + C.danger + '55', background: C.danger + '0F', display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: C.text }
    },
      React.createElement(LIcon, { name: 'alert-triangle', size: 16, color: C.danger }),
      React.createElement('span', null,
        React.createElement('strong', { style: { color: C.danger } }, 'This PO has un-binned errored FBA items. '),
        'Review the Errored tab on the related FBA plan and bin them before closing the PO.'
      )
    ),
    // Vendor + Ship To cards. minmax(0,1fr): a bare 1fr track can't shrink below its
    // content's min width, so one long unbreakable token (vendor email) makes the
    // vendor card wider than Ship To.
    // rg-stack: one card per row on narrow widths
    React.createElement('div', { className: 'rg-stack', style: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 16, marginBottom: 20 } },
      React.createElement(Card, null,
        React.createElement('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 10 } }, 'VENDOR'),
        po.status === 'draft' ? React.createElement('div', { style: { fontWeight: 600, fontSize: 15 } }, editableField('vendor_id', 'Vendor', po.vendor_name, { fontSize: 15, fontWeight: 600 })) :React.createElement('div', { style: { fontWeight: 600, fontSize: 15, color: C.text } }, po.vendor_name),
        React.createElement('div', { style: { fontSize: 13, color: C.textMuted, marginTop: 4 } }, po.vendor_contact),
        po.vendor_address1 && React.createElement('div', { style: { fontSize: 12, color: C.textDim } }, po.vendor_address1),
        po.vendor_address2 && React.createElement('div', { style: { fontSize: 12, color: C.textDim } }, po.vendor_address2),
        React.createElement('div', { style: { fontSize: 12, color: C.textDim } }, [po.vendor_city, po.vendor_state, po.vendor_zip].filter(Boolean).join(', ')),
        po.vendor_country && po.vendor_country !== 'US' && po.vendor_country !== 'USA' && React.createElement('div', { style: { fontSize: 12, color: C.textDim } }, countryName(po.vendor_country)),
        React.createElement('div', { style: { fontSize: 12, color: C.textDim, overflowWrap: 'anywhere' } }, po.vendor_email),
        po.vendor_notes && React.createElement('div', { style: { fontSize: 12, color: C.warning, marginTop: 8, padding: '6px 10px', background: C.warningSoft, borderRadius: 4 } }, po.vendor_notes)
      ),
      React.createElement(Card, null,
        React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 } },
          React.createElement('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase' } }, 'SHIP TO'),
          hasShipToOverride && isEditable ? React.createElement('button', { onClick: async function() {
            if (!(await appConfirm('Clear custom ship-to address and revert to warehouse address?', { danger: true, okLabel: 'Clear' }))) return;
            try {
              await api('/purchase-orders/' + poId, { method: 'PUT', body: JSON.stringify({ ship_to_name: '', ship_to_address: '', ship_to_address2: '', ship_to_city: '', ship_to_state: '', ship_to_zip: '', ship_to_country: '', ship_to_phone: '', ship_to_contact: '' }) });
              load();
            } catch(e) { showToast(e.message, 'error'); }
          }, style: { background: 'none', border: 'none', color: C.danger, cursor: 'pointer', fontSize: 10, fontFamily: 'inherit' } }, 'Clear Override') : null
        ),
        editableField('warehouse_id', 'Warehouse', po.wh_code + ' — ' + po.wh_name, { fontSize: 15, fontWeight: 600 }),
        editingField === 'ship_to' ? React.createElement('div', { style: { marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 } },
          [{ key: 'ship_to_name', ph: 'Name' }, { key: 'ship_to_address', ph: 'Address' }, { key: 'ship_to_address2', ph: 'Address 2 (Suite / Unit / Floor)' }, { key: 'ship_to_city', ph: 'City' }, { key: 'ship_to_state', ph: 'State' }, { key: 'ship_to_zip', ph: 'Zip' }, { key: 'ship_to_phone', ph: 'Phone' }, { key: 'ship_to_contact', ph: 'Attn / Contact' }].map(function(f) {
            if (f.key === 'ship_to_state') return React.createElement(StateSelect, { key: f.key, compact: true, placeholder: 'State', country: (shipToForm || {}).ship_to_country || 'US',
              value: (shipToForm || {}).ship_to_state || '', onChange: function(e) { setShipToForm(function(prev) { var n = Object.assign({}, prev); n.ship_to_state = e.target.value; return n; }); } });
            return React.createElement('input', { key: f.key, placeholder: f.ph, value: (shipToForm || {})[f.key] || '',
              onChange: function(e) { setShipToForm(function(prev) { var n = Object.assign({}, prev); n[f.key] = e.target.value; return n; }); },
              style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '4px 8px', borderRadius: 4, fontSize: 11, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' } });
          }),
          React.createElement(SearchSelect, {
            value: (shipToForm || {}).ship_to_country || '',
            onChange: function(val) { setShipToForm(function(prev) { var n = Object.assign({}, prev); n.ship_to_country = val; return n; }); },
            // The blank entry has to be in the list: a native select had an empty <option>
            // to fall back to the warehouse, and SearchSelect has no other way to clear.
            options: [{ value: '', label: 'Country (warehouse default)' }].concat(countryOptionList((shipToForm || {}).ship_to_country || '')),
            placeholder: 'Country (warehouse default)',
            style: { padding: '4px 8px', borderRadius: 4, fontSize: 11 }
          }),
          React.createElement('div', { style: { display: 'flex', gap: 6, marginTop: 2 } },
            React.createElement('button', { onClick: async function() {
              try {
                await api('/purchase-orders/' + poId, { method: 'PUT', body: JSON.stringify(shipToForm) });
                setEditingField(null); load();
              } catch(e) { showToast(e.message, 'error'); }
            }, style: { background: 'none', border: 'none', color: C.success, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center' } }, React.createElement(LIcon, { name: 'check', size: 14 }), ' Save'),
            React.createElement('button', { onClick: function() { setEditingField(null); }, style: { background: 'none', border: 'none', color: C.danger, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center' } }, React.createElement(LIcon, { name: 'x', size: 14 }), ' Cancel')
          )
        ) : React.createElement('div', null,
          hasShipToOverride ? React.createElement('div', { style: { marginTop: 6, padding: '6px 10px', background: C.accentSoft, borderRadius: 4, border: '1px solid ' + C.accent + '33' } },
            React.createElement('div', { style: { fontSize: 11, fontWeight: 600, color: C.accent, marginBottom: 2 } }, 'Custom Address'),
            React.createElement('div', { style: { fontSize: 12, color: C.text } }, po.ship_to_name),
            po.ship_to_address && React.createElement('div', { style: { fontSize: 12, color: C.textDim } }, po.ship_to_address),
            po.ship_to_address2 && React.createElement('div', { style: { fontSize: 12, color: C.textDim } }, po.ship_to_address2),
            React.createElement('div', { style: { fontSize: 12, color: C.textDim } }, [po.ship_to_city, po.ship_to_state, po.ship_to_zip].filter(Boolean).join(', ')),
            po.ship_to_country && po.ship_to_country !== 'US' && React.createElement('div', { style: { fontSize: 12, color: C.textDim } }, countryName(po.ship_to_country)),
            po.ship_to_phone && React.createElement('div', { style: { fontSize: 12, color: C.textDim } }, po.ship_to_phone),
            po.ship_to_contact && React.createElement('div', { style: { fontSize: 12, color: C.textDim } }, 'Attn: ' + po.ship_to_contact)
          ) : React.createElement('div', null,
            React.createElement('div', { style: { fontSize: 12, color: C.textDim, marginTop: 4 } }, po.wh_address),
            po.wh_address2 && React.createElement('div', { style: { fontSize: 12, color: C.textDim } }, po.wh_address2),
            React.createElement('div', { style: { fontSize: 12, color: C.textDim } }, [po.wh_city, po.wh_state, po.wh_zip].filter(Boolean).join(', ')),
            po.wh_country && po.wh_country !== 'US' && React.createElement('div', { style: { fontSize: 12, color: C.textDim } }, countryName(po.wh_country))
          ),
          isEditable && React.createElement('button', { onClick: function() {
            setEditingField('ship_to');
            setShipToForm({ ship_to_name: po.ship_to_name || '', ship_to_address: po.ship_to_address || '', ship_to_address2: po.ship_to_address2 || '', ship_to_city: po.ship_to_city || '', ship_to_state: po.ship_to_state || '', ship_to_zip: po.ship_to_zip || '', ship_to_country: po.ship_to_country || '', ship_to_phone: po.ship_to_phone || '', ship_to_contact: po.ship_to_contact || '' });
          },
            onMouseOver: function(e) { e.currentTarget.style.background = C.accent + '20'; e.currentTarget.style.color = C.accent; },
            onMouseOut: function(e) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = C.accent; },
            style: { background: 'transparent', border: 'none', color: C.accent, cursor: 'pointer', fontSize: 10, fontFamily: 'inherit', marginTop: 6, padding: '3px 6px', borderRadius: 4, transition: 'all 0.15s' } },
            React.createElement(LIcon, { name: 'pencil', size: 10 }), po.ship_to_name ? ' Edit Custom Address' : ' Set Custom Address')
        ),
        React.createElement('div', { style: { display: 'flex', gap: 16, marginTop: 12 } },
          React.createElement('div', null,
            React.createElement('span', { style: { fontSize: 11, color: C.textDim } }, 'Shipping'),
            editableField('shipping_method', 'Shipping', po.shipping_method)
          ),
          React.createElement('div', null,
            React.createElement('span', { style: { fontSize: 11, color: C.textDim } }, 'Payment'),
            editableField('payment_method', 'Payment', po.payment_method)
          )
        )
      )
    ),
    // Stats (rg-2x2: a 2x2 grid on narrow widths)
    React.createElement('div', { className: 'rg-2x2', style: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 } },
      [
        { label: 'SKUs', val: po.sku_count, clr: C.accent },
        { label: 'Total Units', val: po.total_units?.toLocaleString(), clr: C.purple },
        { label: 'Total Cases', val: po.total_cases?.toLocaleString(), clr: C.warning },
        { label: 'Total Cost', val: '$' + Number(po.total_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2 }), clr: C.success },
      ].map(s => React.createElement(Card, { key: s.label, style: { textAlign: 'center' } },
        React.createElement('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 4 } }, s.label),
        React.createElement('div', { style: { fontSize: 22, fontWeight: 700, color: s.clr } }, s.val)
      ))
    ),
    // ─── TABS ─── (rg-scrollx: the tab strip scrolls sideways on narrow widths)
    React.createElement('div', { className: 'rg-scrollx', style: { display: 'flex', gap: 0, marginBottom: 0, borderBottom: '2px solid ' + C.border } },
      [
        { key: 'items', icon: 'package', label: 'Line Items' },
        { key: 'analysis', icon: 'bar-chart-3', label: 'Analysis' },
        { key: 'receiving', icon: 'inbox', label: 'Receiving' },
        { key: 'notes', icon: 'message-square', label: 'Internal Notes' },
        { key: 'activity', icon: 'clipboard-list', label: 'Activity Log' },
      ].map(function(tab) {
        var isActive = activeTab === tab.key;
        return React.createElement('a', {
          key: tab.key,
          href: '/po/' + poId + (tab.key !== 'items' ? '?tab=' + tab.key : ''),
          onClick: function(e) { if (e.ctrlKey || e.metaKey || e.shiftKey) return; e.preventDefault(); setActiveTab(tab.key); if (tab.key === 'notes') loadNotes(); if (tab.key === 'activity') loadActivity(); if (tab.key === 'receiving') loadReceivingHistory(); if (tab.key === 'analysis') loadAnalysis(); },
          className: isActive ? 'po-tab po-tab-active' : 'po-tab',
          style: { background: 'none', border: 'none', borderBottom: isActive ? '2px solid ' + C.accent : '2px solid transparent',
            color: isActive ? C.accent : C.textMuted, padding: '10px 20px', fontSize: 13, fontWeight: isActive ? 600 : 400,
            cursor: 'pointer', fontFamily: 'inherit', marginBottom: -2, transition: 'all 0.15s', display: 'inline-flex', alignItems: 'center', gap: 6, borderRadius: '6px 6px 0 0', textDecoration: 'none' }
        }, React.createElement(LIcon, { name: tab.icon, size: 14 }), tab.label);
      })
    ),

    // ─── TAB: LINE ITEMS ───
    activeTab === 'items' && React.createElement('div', { style: { marginTop: 16 } },
      // Undo delete banner
      lastDeleted && React.createElement('div', {
        style: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', marginBottom: 12, background: C.warningSoft, borderRadius: 8, border: '1px solid ' + C.warning + '33' }
      },
        React.createElement('span', { style: { fontSize: 13, color: C.text, flex: 1 } },
          'Removed ',
          React.createElement('strong', null, lastDeleted.sku),
          ' (' + lastDeleted.qty_ordered + ' units)'
        ),
        React.createElement(Btn, { size: 'sm', onClick: undoDelete }, React.createElement(LIcon, { name: 'undo-2', size: 14 }), ' Undo'),
        React.createElement('button', { onClick: function() { setLastDeleted(null); },
          style: { background: 'none', border: 'none', color: C.textDim, cursor: 'pointer', fontSize: 14, padding: 0, display: 'flex', alignItems: 'center' } }, React.createElement(LIcon, { name: 'x', size: 14 }))
      ),
      React.createElement('input', { type: 'file', accept: '.csv', ref: csvRef, onChange: uploadPOcsv, style: { display: 'none' } }),
      React.createElement('input', { type: 'file', accept: '.pdf,.csv,.xlsx,.xls', ref: confirmFileRef, onChange: uploadVendorConfirmation, style: { display: 'none' } }),
      React.createElement('input', { type: 'file', accept: '.pdf', multiple: true, ref: labelFileRef, onChange: uploadLabelFiles, style: { display: 'none' } }),
      // Missing Transparency labels — slim banner only when coverage is short; full
      // management lives in the Actions → Transparency Labels modal.
      (function() {
        var missing = (labelFiles && labelFiles.missing) || [];
        if (!missing.length) return null;
        return React.createElement('div', { style: { background: C.dangerSoft, border: '1px solid ' + C.danger + '33', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12, color: C.danger, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' } },
          React.createElement(LIcon, { name: 'shield-alert', size: 14 }),
          React.createElement('span', { style: { fontWeight: 700 } }, 'Transparency labels missing for ' + missing.length + ' SKU' + (missing.length === 1 ? '' : 's') + ':'),
          React.createElement('span', null, missing.map(function(m) { return m.sku + ' (' + m.qty_ordered + ')'; }).join(', ')),
          React.createElement('a', { onClick: function() { setLabelModalOpen(true); }, style: { color: C.danger, textDecoration: 'underline', cursor: 'pointer', fontWeight: 600 } }, 'Manage')
        );
      })(),
      React.createElement(DataTable, {
        columns: lineItemCols,
        data: po.line_items || [],
        storageKey: 'po-line-items',
        defaultSort: 'sku', defaultSortDir: 'asc',
        onSortedData: function(d) { liSortedRef.current = d; },
        emptyTitle: 'No line items', emptySub: 'Add products to this PO',
        bulkActions: {
          alwaysShow: true,
          count: liSelCount,
          onClear: liSelCount > 0 ? function() { setLiSelected({}); } : null,
          items: [].concat(
            // Add Products sits first, above the Print section (Anthony 9-09)
            isEditable ? [{ label: 'Add Products', icon: 'plus', onClick: function() { loadArchivedForTransfer(); setAddModalOpen(true); setAddSelections({}); setAddSearch(''); setAddPage(0); } }, { divider: true }] : [],
            [{ section: 'Print' }],
            [
              { label: 'Print PO (Excel)', icon: 'file-text', onClick: downloadXLSX },
              // Downloads as po-<PO#>-pdf.pdf (name from the server's Content-Disposition); used to open in a new tab (Anthony 9-09)
              { label: 'Print PO (PDF)', icon: 'file', onClick: function() { fetch('/api/purchase-orders/' + po.id + '/export-pdf', { credentials: 'include' }).then(function(r) { if (!r.ok) throw new Error('Failed'); var cd = r.headers.get('content-disposition'); var name = cd && cd.indexOf('filename=') >= 0 ? cd.split('filename=')[1].replace(/"/g, '') : ('PO-' + po.po_number + '-PDF.pdf'); return r.blob().then(function(blob) { return { blob: blob, name: name }; }); }).then(function(f) { var url = URL.createObjectURL(f.blob); var a = document.createElement('a'); a.href = url; a.download = f.name; document.body.appendChild(a); a.click(); a.remove(); setTimeout(function() { URL.revokeObjectURL(url); }, 1000); }).catch(function(e) { showToast('Failed to generate PDF: ' + e.message, 'error'); }); } },
              { label: 'Print WO (Excel)', icon: 'clipboard', onClick: function() { var token = getAuthToken(); var woFilename = 'WO_' + (po.po_number || po.id) + '.xlsx'; fetch('/api/receiving/' + po.id + '/export-wo', { credentials: 'include' }).then(function(r) { if (!r.ok) throw new Error('Failed'); var cd = r.headers.get('content-disposition'); if (cd) woFilename = cd.split('filename=')[1].replace(/"/g, ''); return r.blob(); }).then(function(blob) { var url = URL.createObjectURL(blob); var a = document.createElement('a'); a.href = url; a.download = woFilename; a.click(); URL.revokeObjectURL(url); }).catch(function(e) { showToast('Failed to export Work Order: ' + e.message, 'error'); }); } },
            // Downloads as po-<PO#>-case-labels.pdf (name from the server's Content-Disposition); used to open a tab + print dialog (Anthony 9-09)
            ].concat(po.label_required ? [{ label: 'Print Case Labels (PDF)', icon: 'printer', onClick: function() { fetch('/api/purchase-orders/' + po.id + '/case-label', { credentials: 'include' }).then(function(r) { if (!r.ok) throw new Error('Failed'); var cd = r.headers.get('content-disposition'); var name = cd && cd.indexOf('filename=') >= 0 ? cd.split('filename=')[1].replace(/"/g, '') : ('PO-' + po.po_number + '-Case-Labels.pdf'); return r.blob().then(function(blob) { return { blob: blob, name: name }; }); }).then(function(f) { var url = URL.createObjectURL(f.blob); var a = document.createElement('a'); a.href = url; a.download = f.name; document.body.appendChild(a); a.click(); a.remove(); setTimeout(function() { URL.revokeObjectURL(url); }, 1000); }).catch(function(e) { showToast('Failed to load case label: ' + e.message, 'error'); }); } }] : []),
            [{ divider: true }, { section: 'Email' }],
            po.status !== 'cancelled' ? [{ label: 'Email Vendor', icon: 'mail', onClick: emailVendor }, { label: 'Scheduled Emails', icon: 'clock', onClick: loadScheduledEmails }] : [],
            isEditable ? [
              { divider: true },
              { section: 'Manage' },
              // Order per Anthony 9-09: Import Vendor Confirmation, Import Line Items, Export Line Items, Transparency Labels
              { label: 'Import Vendor Confirmation', icon: 'scan-search', onClick: function() { confirmFileRef.current.click(); } },
              { label: 'Import Line Items', icon: 'upload', onClick: function() { csvRef.current.click(); } },
              { label: 'Export Line Items', icon: 'download', onClick: downloadPOcsv },
              { label: 'Transparency Labels', icon: 'shield-check', onClick: function() { setLabelModalOpen(true); } },
            ] : [
              { divider: true },
              { label: 'Export Line Items', icon: 'download', onClick: downloadPOcsv },
              { label: 'Transparency Labels', icon: 'shield-check', onClick: function() { setLabelModalOpen(true); } },
            ],
            liSelCount > 0 ? [
              { divider: true },
              { section: liSelCount + ' item' + (liSelCount > 1 ? 's' : '') + ' selected' },
              { label: 'Move to Next PO', icon: 'arrow-right', onClick: bulkMoveToNext },
              { label: 'Remove from PO', icon: 'trash-2', onClick: bulkRemoveLineItems, danger: true }
            ] : []
          )
        }
      })
    ),

    // ─── TAB: INTERNAL NOTES ───
    activeTab === 'notes' && React.createElement('div', { style: { marginTop: 16 } },
      React.createElement(Card, { style: { display: 'flex', flexDirection: 'column', height: 420 } },
        // Notes list
        React.createElement('div', { style: { flex: 1, overflow: 'auto', padding: '12px 0', display: 'flex', flexDirection: 'column', gap: 8 } },
          notes.length === 0 && React.createElement('div', { style: { textAlign: 'center', color: C.textDim, fontSize: 13, padding: 40 } }, 'No notes yet. Start the conversation!'),
          notes.map(function(note) {
            var isMe = currentUser && currentUser.id === note.user_id;
            var time = new Date(note.created_at);
            var timeStr = time.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' + time.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
            return React.createElement('div', { key: note.id, style: { display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start', padding: '0 8px' } },
              React.createElement('div', { style: { fontSize: 10, color: C.textDim, marginBottom: 2, display: 'flex', alignItems: 'center', gap: 6 } },
                React.createElement('span', { style: { fontWeight: 600 } }, note.display_name || note.username),
                React.createElement('span', null, timeStr),
                isMe && React.createElement('button', { onClick: function() { deleteNote(note.id); },
                  style: { background: 'none', border: 'none', color: C.textDim, cursor: 'pointer', fontSize: 10, padding: 0, display: 'inline-flex', alignItems: 'center' } }, React.createElement(LIcon, { name: 'x', size: 10 }))
              ),
              React.createElement('div', { style: { background: isMe ? C.accent : C.surface, color: isMe ? '#fff' : C.text,
                padding: '8px 14px', borderRadius: 12, borderTopRightRadius: isMe ? 4 : 12, borderTopLeftRadius: isMe ? 12 : 4,
                maxWidth: '70%', fontSize: 13, lineHeight: 1.4, wordBreak: 'break-word',
                border: isMe ? 'none' : '1px solid ' + C.border } }, renderNoteBody(note.message), renderNoteAttachments(note))
            );
          }),
          React.createElement('div', { ref: notesEndRef })
        ),
        // Pending attachment chips
        noteFiles.length > 0 && React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 6, padding: '10px 0 0' } },
          noteFiles.map(function(f, idx) {
            return React.createElement('span', { key: idx, style: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 8, background: C.surface, border: '1px solid ' + C.border, fontSize: 12, color: C.text, maxWidth: 220 } },
              React.createElement(LIcon, { name: /^image\//.test(f.type) ? 'image' : 'paperclip', size: 12 }),
              React.createElement('span', { style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, f.name),
              React.createElement('button', { onClick: function() { setNoteFiles(noteFiles.filter(function(_, i) { return i !== idx; })); },
                style: { background: 'none', border: 'none', color: C.textDim, cursor: 'pointer', padding: 0, display: 'inline-flex', alignItems: 'center' } }, React.createElement(LIcon, { name: 'x', size: 12 }))
            );
          })
        ),
        // Input area
        React.createElement('div', { style: { borderTop: '1px solid ' + C.border, padding: '12px 0 0', display: 'flex', gap: 8, alignItems: 'flex-end' } },
          React.createElement('input', { ref: noteFileInputRef, type: 'file', multiple: true, style: { display: 'none' },
            accept: 'image/*,.pdf,.csv,.txt,.xls,.xlsx,.doc,.docx',
            onChange: function(e) { addNoteFiles(e.target.files); e.target.value = ''; } }),
          React.createElement('div', { style: { flex: 1 } },
            React.createElement(MentionInput, {
              value: newNote, onChange: setNewNote,
              onKeyDown: function(e) { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendNote(); } },
              onPaste: function(e) {
                var pasted = e.clipboardData && e.clipboardData.files;
                if (pasted && pasted.length) { e.preventDefault(); addNoteFiles(pasted); }
              },
              placeholder: 'Type a note... use @ to tag a teammate',
              rows: 2,
              style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '10px 14px', borderRadius: 8, fontSize: 13, outline: 'none' }
            })
          ),
          React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'stretch' } },
            React.createElement(Btn, { size: 'sm', style: { justifyContent: 'center' },
              onClick: function() { if (noteFileInputRef.current) noteFileInputRef.current.click(); } },
              React.createElement(LIcon, { name: 'paperclip', size: 14 })),
            React.createElement(Btn, { variant: 'primary', size: 'sm', style: { justifyContent: 'center' }, onClick: sendNote, disabled: sendingNote || (!newNote.trim() && !noteFiles.length) }, sendingNote ? 'Sending…' : 'Send')
          )
        )
      )
    ),

    // ─── TAB: ACTIVITY LOG ───
    activeTab === 'activity' && React.createElement('div', { style: { marginTop: 16 } },
      React.createElement(Card, { style: { maxHeight: 500, overflow: 'auto' } },
        activity.length === 0 && React.createElement('div', { style: { textAlign: 'center', color: C.textDim, fontSize: 13, padding: 40 } }, 'No activity recorded yet.'),
        activity.map(function(log, _i, _all) {
          // Only the newest upload entry that still has its snapshot is undoable
          // (the server enforces the same ordering; metadata is nulled after undo).
          var lastUndoable = _all.find(function(l) { return (l.action === 'csv_upload' || l.action === 'confirmation_import') && l.metadata && l.metadata.previous_items; });
          var time = new Date(log.created_at);
          var timeStr = time.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ' ' + time.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit' });
          var actionIcons = { 'po_updated': 'pencil', 'status_changed': 'refresh-cw', 'item_added': 'plus', 'item_updated': 'file-pen', 'item_removed': 'trash-2', 'csv_upload': 'file-text', 'csv_undo': 'rotate-ccw', 'confirmation_import': 'file-check', 'confirmation_undo': 'undo-2', 'items_moved_in': 'inbox', 'note_added': 'message-square', 'note_deleted': 'trash-2', 'items_received': 'inbox', 'receive_undone': 'undo-2', 'analysis_updated': 'bar-chart-3', 'keepa_fetch': 'bar-chart-3', 'sp_api_fetch': 'link', 'keepa_import': 'file-text' };
          return React.createElement('div', { key: log.id, style: { display: 'flex', gap: 12, padding: '10px 0', borderBottom: '1px solid ' + C.border + '44' } },
            React.createElement('div', { style: { fontSize: 16, width: 24, textAlign: 'center', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' } }, actionIcons[log.action] ? React.createElement(LIcon, { name: actionIcons[log.action], size: 16 }) : '•'),
            React.createElement('div', { style: { flex: 1 } },
              React.createElement('div', { style: { fontSize: 13, color: C.text } }, log.details),
              React.createElement('div', { style: { fontSize: 11, color: C.textDim, marginTop: 2, display: 'flex', alignItems: 'center', gap: 8 } },
                React.createElement('span', null,
                  React.createElement('span', { style: { fontWeight: 600 } }, log.username),
                  ' — ', timeStr
                ),
                lastUndoable && log.id === lastUndoable.id && React.createElement('button', {
                  onClick: async function() {
                    var isConf = log.action === 'confirmation_import';
                    var msg = isConf
                      ? 'Undo this vendor confirmation? Line items will be restored to their pre-import state' + (log.metadata.next_po ? ', and the items it moved to PO#' + log.metadata.next_po + ' will be pulled back out of that PO' : '') + '.'
                      : 'Undo this CSV upload? This will restore line items to their previous state.';
                    if (!(await appConfirm(msg))) return;
                    try {
                      await api('/purchase-orders/' + poId + '/activity/' + log.id + '/undo', { method: 'POST' });
                      showToast(isConf ? 'Vendor confirmation undone' : 'CSV upload undone');
                      load(); loadActivity();
                    } catch(e) { showToast('Undo failed: ' + (e.message || 'Unknown error'), 'error'); }
                  },
                  style: { background: 'none', border: '1px solid ' + C.border, color: C.warning, cursor: 'pointer', fontSize: 11, padding: '1px 8px', borderRadius: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }
                }, React.createElement(LIcon, { name: 'rotate-ccw', size: 11 }), 'Undo')
              )
            )
          );
        })
      )
    ),

    // ─── TAB: RECEIVING ───
    activeTab === 'receiving' && React.createElement('div', { style: { marginTop: 16 } },
      // Receiving summary for this PO
      po && React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, marginBottom: 16 } },
        [
          { label: 'Total Ordered', val: po.total_units || 0, clr: C.text },
          { label: 'Total Received', val: (po.line_items || []).reduce(function(s, li) { return s + (li.qty_received || 0); }, 0), clr: C.success },
          { label: 'Remaining', val: (po.total_units || 0) - (po.line_items || []).reduce(function(s, li) { return s + (li.qty_received || 0); }, 0), clr: C.warning },
        ].map(function(s) {
          return React.createElement(Card, { key: s.label, style: { padding: 12 } },
            React.createElement('div', { style: { fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' } }, s.label),
            React.createElement('div', { style: { fontSize: 20, fontWeight: 700, color: s.clr, marginTop: 2 } }, s.val)
          );
        })
      ),
      // Line items with receiving status
      React.createElement(Card, { style: { padding: 0, overflow: 'hidden', marginBottom: 16 } },
        React.createElement('table', { style: { width: '100%', fontSize: 13, tableLayout: 'auto' } },
          React.createElement('thead', null,
            React.createElement('tr', { style: { borderBottom: '1px solid ' + C.border } },
              ['SKU', 'Vendor Title', 'Ordered', 'Received', 'Remaining', 'Progress'].map(function(h) {
                return React.createElement('th', { key: h, style: { padding: '8px 12px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' } }, h);
              })
            )
          ),
          React.createElement('tbody', null,
            (po.line_items || []).map(function(li) {
              var recv = li.qty_received || 0;
              var rem = li.qty_ordered - recv;
              var pct = li.qty_ordered > 0 ? Math.round((recv / li.qty_ordered) * 100) : 0;
              var color = pct >= 100 ? C.success : pct > 0 ? C.warning : C.textDim;
              return React.createElement('tr', { key: li.id, style: { borderBottom: '1px solid ' + C.border + '22' } },
                React.createElement('td', { style: { padding: '8px 12px', fontWeight: 600, fontFamily: 'inherit', color: C.accent, wordBreak: 'break-word' } }, li.sku),
                React.createElement('td', { style: { padding: '8px 12px', color: C.text, wordBreak: 'break-word' } }, li.vendor_title),
                React.createElement('td', { style: { padding: '8px 14px', fontWeight: 500 } }, li.qty_ordered),
                React.createElement('td', { style: { padding: '8px 14px', fontWeight: 600, color: color } }, recv),
                React.createElement('td', { style: { padding: '8px 14px', fontWeight: 500, color: rem > 0 ? C.warning : C.success } }, rem > 0 ? rem : React.createElement(LIcon, { name: 'check', size: 14, color: C.success })),
                React.createElement('td', { style: { padding: '8px 14px' } },
                  React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
                    React.createElement('div', { style: { flex: 1, maxWidth: 80, height: 6, background: C.border, borderRadius: 3, overflow: 'hidden' } },
                      React.createElement('div', { style: { width: pct + '%', height: '100%', background: color, borderRadius: 3 } })
                    ),
                    React.createElement('span', { style: { fontSize: 11, color: C.textDim, fontWeight: 500 } }, pct + '%')
                  )
                )
              );
            })
          )
        )
      ),
      // Receiving history
      React.createElement('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 8 } }, 'Receiving History'),
      React.createElement(Card, { style: { maxHeight: 350, overflow: 'auto' } },
        receivingHistory.length === 0 && React.createElement('div', { style: { textAlign: 'center', color: C.textDim, fontSize: 13, padding: 40 } }, 'No receiving entries yet.'),
        receivingHistory.map(function(log) {
          return React.createElement(ReceivingHistoryEntry, { key: log.id, log: log, currentUser: currentUser });
        })
      )
    ),

    // ─── TAB: ANALYSIS ───
    activeTab === 'analysis' && React.createElement('div', { style: { marginTop: 16 } },
      (function() {
        var ad = analysisData;
        if (!ad) return React.createElement('div', { style: { padding: 40, textAlign: 'center', color: C.textMuted } }, 'Loading analysis...');

        var fmtM = function(v) { return v != null ? '$' + Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'; };
        var fmtP = function(v) { return v != null ? Number(v).toFixed(1) + '%' : '—'; };
        var fmtN = function(v) { return v != null && v !== 0 ? Number(v).toLocaleString() : '—'; };

        var isDraft = ad.po && ad.po.status === 'draft';
        var canFetchAnalysis = ad.po && ['draft','approved','submitted','confirmed','shipped'].indexOf(ad.po.status) !== -1;
        var aSelCount = Object.keys(analysisSelected).filter(function(k) { return analysisSelected[k]; }).length;
        var toggleASel = function(id, e) {
          var n = Object.assign({}, analysisSelected);
          if (e && e.shiftKey && aLastClickRef.current != null) {
            var sorted = aSortedRef.current || [];
            var lastIdx = sorted.findIndex(function(r) { return r.id === aLastClickRef.current; });
            var curIdx = sorted.findIndex(function(r) { return r.id === id; });
            if (lastIdx >= 0 && curIdx >= 0) {
              var lo = Math.min(lastIdx, curIdx), hi = Math.max(lastIdx, curIdx);
              for (var i = lo; i <= hi; i++) n[sorted[i].id] = true;
            } else { n[id] = !n[id]; if (!n[id]) delete n[id]; }
          } else { n[id] = !n[id]; if (!n[id]) delete n[id]; }
          aLastClickRef.current = id;
          setAnalysisSelected(n);
        };
        var toggleAAll = function() {
          var visibleLines = aIncompleteOnly ? (ad.lines || []).filter(function(l) { return !l.has_keepa || !l.unit_cost || parseFloat(l.unit_cost) === 0; }) : (ad.lines || []);
          if (aSelCount === visibleLines.length && visibleLines.length > 0) setAnalysisSelected({});
          else { var n = {}; visibleLines.forEach(function(l) { n[l.id] = true; }); setAnalysisSelected(n); }
        };

        var getUniqueAsins = function() {
          var lines = aSelCount > 0
            ? (ad.lines || []).filter(function(l) { return analysisSelected[l.id]; })
            : (ad.lines || []);
          var asins = lines.filter(function(l) { return l.asin; }).map(function(l) { return l.asin; });
          return asins.filter(function(a, i) { return asins.indexOf(a) === i; });
        };

        var fetchKeepaForPo = async function() {
          var unique = getUniqueAsins();
          if (!unique.length) { showToast('No ASINs in this PO', 'error'); return; }
          if (!(await appConfirm('Fetch data for ' + unique.length + ' ASIN(s) from Keepa?'))) return;
          setAnalysisFetching('keepa'); setAnalysisFetchResult(null);
          try {
            var result = await api('/analysis/keepa-fetch', { method: 'POST', body: JSON.stringify({ asins: unique, domain: ad.po.domain, poId: poId }) });
            var msg = 'Fetched ' + result.fetched + '/' + (result.total_requested || unique.length) + ' ASINs from Keepa.';
            if (result.skipped_fresh) msg += ' ' + result.skipped_fresh + ' skipped (data < 7 days old).';
            if (result.not_found) msg += ' ' + result.not_found + ' not found.';
            if (result.no_price) msg += ' ' + result.no_price + ' no price.';
            if (result.partial) msg += ' (Stopped early — tokens depleted.)';
            msg += ' Tokens: ' + (result.tokens_left != null ? result.tokens_left : '?');
            setAnalysisFetchResultAuto({ success: !result.partial, message: msg });
            await loadAnalysis();
          } catch (e) { setAnalysisFetchResultAuto({ success: false, message: e.message }); }
          setAnalysisFetching(false);
        };

        var fetchSpApiForPo = async function() {
          var unique = getUniqueAsins();
          if (!unique.length) { showToast('No ASINs in this PO', 'error'); return; }
          if (!(await appConfirm('Fetch data for ' + unique.length + ' ASIN(s) from Amazon SP-API?'))) return;
          setAnalysisFetching('spapi'); setAnalysisFetchResult(null);
          try {
            var fetched = 0; var spNoPrice = 0;
            for (var j = 0; j < unique.length; j += 20) {
              var batch = unique.slice(j, j + 20);
              var result = await api('/analysis/sp-api-fetch', { method: 'POST', body: JSON.stringify({ asins: batch, domain: ad.po.domain, poId: poId }) });
              fetched += result.fetched;
              spNoPrice += (result.no_price || 0);
            }
            var spMsg = 'Fetched ' + fetched + ' ASINs from SP-API.';
            if (spNoPrice) spMsg += ' ' + spNoPrice + ' no price.';
            setAnalysisFetchResultAuto({ success: true, message: spMsg });
            await loadAnalysis();
          } catch (e) { setAnalysisFetchResultAuto({ success: false, message: e.message }); }
          setAnalysisFetching(false);
        };

        var fetchShipStationForPo = async function() {
          if (!(await appConfirm('Apply stored ShipStation price & shipping data to this PO?'))) return;
          setAnalysisFetching('shipstation'); setAnalysisFetchResult(null);
          try {
            var result = await api('/analysis/po/' + poId + '/apply-shipstation', { method: 'POST' });
            var msg = result.matched + '/' + result.total + ' SKUs matched from ShipStation data.';
            if (result.not_found) msg += ' ' + result.not_found + ' not found.';
            if (result.details && result.details.length) {
              var oldest = Math.max.apply(null, result.details.map(function(d) { return d.age_days; }));
              var newest = Math.min.apply(null, result.details.map(function(d) { return d.age_days; }));
              msg += ' Data age: ' + (newest === oldest ? newest + 'd' : newest + 'd – ' + oldest + 'd') + '.';
              var wins = {}; result.details.forEach(function(d) { if (d.window_days) wins[d.window_days] = (wins[d.window_days] || 0) + 1; });
              var winTxt = Object.keys(wins).map(function(w) { return wins[w] + ' × ' + (w === 'all' ? 'all history' : w + 'd'); }).join(', ');
              if (winTxt) msg += ' Averaged over: ' + winTxt + '.';
            }
            setAnalysisFetchResultAuto({ success: true, message: msg });
            await loadAnalysis();
          } catch (e) { setAnalysisFetchResultAuto({ success: false, message: e.message }); }
          setAnalysisFetching(false);
        };

        var fetchWalmartForPo = async function() {
          var aSelectedIds = Object.keys(analysisSelected).filter(function(k) { return analysisSelected[k]; });
          var lines = aSelectedIds.length > 0 ? (ad.lines || []).filter(function(l) { return analysisSelected[l.id]; }) : (ad.lines || []);
          var skus = lines.map(function(l) { return l.product_sku || l.sku; }).filter(function(s, i, a) { return s && a.indexOf(s) === i; });
          if (!skus.length) { showToast('No SKUs in this PO', 'error'); return; }
          setAnalysisFetching('walmart'); setAnalysisFetchResult(null);
          try {
            var result = await api('/analysis/wfs-fee-fetch', { method: 'POST', body: JSON.stringify({ skus: skus, poId: poId }) });
            var msg = wfsFetchSummary(result, skus.length) + ' ';
            var pollSkus = result.scrape_skus && result.scrape_skus.length ? result.scrape_skus : skus; // Own SKUs never get a buy box
            if (result.scraping) {
              msg += 'Scraping buy box prices...';
              setAnalysisFetchResult({ success: true, message: msg });
              // Poll until buy box prices are populated
              var pollCount = 0;
              var pollPath = window.location.pathname;
              var pollInterval = setInterval(async function() {
                pollCount++;
                // Stop once the user has left this PO (review L19: the poll kept running and
                // reloading after navigation)
                if (window.location.pathname !== pollPath) { clearInterval(pollInterval); return; }
                try {
                  var check = await api('/analysis/wfs-bb-status?skus=' + encodeURIComponent(pollSkus.join(',')));
                  if (window.location.pathname !== pollPath) { clearInterval(pollInterval); return; }
                  if (check.done || pollCount >= 60) {
                    clearInterval(pollInterval);
                    setAnalysisFetchResultAuto({ success: true, message: wfsFetchSummary(result, skus.length) + ' Buy box prices: ' + check.scraped + '/' + check.total + ' complete.' });
                    setAnalysisFetching(false);
                    await loadAnalysis();
                  }
                } catch (e) {
                  if (pollCount >= 60) { clearInterval(pollInterval); setAnalysisFetching(false); }
                }
              }, 2000);
            } else {
              setAnalysisFetchResultAuto({ success: true, message: msg });
              setAnalysisFetching(false);
              await loadAnalysis();
            }
          } catch (e) { setAnalysisFetchResultAuto({ success: false, message: e.message }); setAnalysisFetching(false); }
        };

        var aSelectedIds = Object.keys(analysisSelected).filter(function(k) { return analysisSelected[k]; }).map(Number);

        var moveToNext = async function() {
          if (!aSelectedIds.length) { showToast('Select items to move', 'error'); return; }
          var skuNames = (ad.lines || []).filter(function(l) { return analysisSelected[l.id]; }).map(function(l) { return l.sku; });
          if (!(await appConfirm('Move ' + aSelectedIds.length + ' item(s) to a "Next" PO?\n\n' + skuNames.join(', ')))) return;
          try {
            // Check for duplicate SKUs in target
            var preview = await api('/analysis/move-to-next/preview', { method: 'POST', body: JSON.stringify({ source_po_id: poId, line_item_ids: aSelectedIds }) });
            if (preview.duplicates && preview.duplicates.length > 0) {
              var dupMsg = preview.duplicates.map(function(d) { return d.sku + ' is already on ' + preview.target_po + ' (x' + d.target_qty + '); moving x' + d.source_qty + ' keeps the larger: x' + Math.max(Number(d.target_qty) || 0, Number(d.source_qty) || 0); }).join('\n');
              if (!(await appConfirm('Duplicate SKUs found:\n\n' + dupMsg + '\n\nQuantities are not added together: each line keeps the larger quantity. Continue?'))) return;
            }
            var result = await api('/analysis/move-to-next', { method: 'POST', body: JSON.stringify({ source_po_id: poId, line_item_ids: aSelectedIds }) });
            var skippedMv = (result.skipped || []).map(function(s) { return s.sku; });
            showToast('Moved ' + result.moved + ' item(s) to ' + result.next_po_number + (skippedMv.length ? '. Not moved (has receipts): ' + skippedMv.join(', ') : ''), skippedMv.length ? 'warning' : undefined);
            setAnalysisSelected({});
            load(); await loadAnalysis();
          } catch (e) { showToast(e.message, 'error'); }
        };

        var aRemoveFromPo = async function() {
          if (!aSelectedIds.length) return;
          if (!(await appConfirm('Remove ' + aSelectedIds.length + ' item(s) from this PO?', { danger: true, okLabel: 'Remove' }))) return;
          try {
            var r = await api('/purchase-orders/' + poId + '/items/bulk', { method: 'DELETE', body: JSON.stringify({ ids: aSelectedIds }) });
            showToast((r.removed || aSelectedIds.length) + ' item(s) removed');
            setAnalysisSelected({});
            load(); await loadAnalysis(); loadActivity();
          } catch (e) { showToast(e.message, 'error'); }
        };

        var suffix = ad.po && ad.po.marketplace === 'CA' ? '.ca' : '.com';
        var poIsFBM = ad.po && (ad.po.marketplace === 'US-FBM' || (ad.lines && ad.lines.length > 0 && ad.lines[0].is_fbm));
        var poIsWFS = ad.po && (ad.po.wh_code || '').toUpperCase().indexOf('WFS') >= 0;
        var poIsMELI = ad.po && (ad.po.wh_code || '').toUpperCase().indexOf('MELI') >= 0;
        // One card per fee (module-scope helper so the card set is unit-tested).
        var summaryItems = ad.summary ? poAnalysisSummaryCards(ad.summary, { fbm: !!poIsFBM, wfs: !!poIsWFS, meli: !!poIsMELI }, fmtM, fmtP, C) : [];

        return React.createElement('div', null,
          // Toolbar
          React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 } },
            React.createElement('div', { style: { display: 'flex', gap: 10, alignItems: 'center' } },
              React.createElement('span', { style: { fontSize: 12, color: C.textMuted } },
                ad.summary ? ad.summary.asins_with_data + '/' + ad.summary.asins_total + ' Products with data'
                  + (ad.summary.asins_not_found ? ' · ' + ad.summary.asins_not_found + ' not found' : '')
                  + (ad.summary.asins_no_price ? ' · ' + ad.summary.asins_no_price + ' incomplete' : '') : ''),
              (function() {
                var lines = ad.lines || [];
                var dates = lines.filter(function(l) { return l.keepa_fetched_at; }).map(function(l) { return new Date(l.keepa_fetched_at).getTime(); });
                if (!dates.length) return null;
                var oldest = Math.min.apply(null, dates);
                var newest = Math.max.apply(null, dates);
                var now = Date.now();
                var ageOldest = Math.floor((now - oldest) / 86400000);
                var ageNewest = Math.floor((now - newest) / 86400000);
                var ageLabel = ageOldest === ageNewest
                  ? (ageOldest === 0 ? 'today' : ageOldest + 'd ago')
                  : (ageNewest === 0 ? 'today' : ageNewest + 'd') + ' – ' + ageOldest + 'd ago';
                var ageColor = ageOldest > 14 ? C.danger : ageOldest > 7 ? C.warning : C.success;
                return React.createElement('span', {
                  style: { fontSize: 11, color: ageColor, padding: '2px 8px', borderRadius: 99, background: ageColor + '15', border: '1px solid ' + ageColor + '30', whiteSpace: 'nowrap' },
                  title: 'Oldest: ' + new Date(oldest).toLocaleDateString() + ' · Newest: ' + new Date(newest).toLocaleDateString()
                }, React.createElement(LIcon, { name: 'clock', size: 11 }), ' Data: ' + ageLabel);
              })(),
            ),
            React.createElement('div', { style: { display: 'flex', gap: 8, alignItems: 'center' } },
              canFetchAnalysis && React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: fetchKeepaForPo, disabled: !!analysisFetching }, analysisFetching === 'keepa' ? [React.createElement(LIcon, { key: 'icon', name: 'loader-2', size: 14, spin: true }), ' Fetching...'] : [React.createElement(LIcon, { key: 'icon', name: 'bar-chart-3', size: 14 }), ' Fetch Keepa' + (aSelCount > 0 ? ' (' + aSelCount + ')' : ' API')]),
              canFetchAnalysis && React.createElement(Btn, { size: 'sm', onClick: fetchSpApiForPo, disabled: !!analysisFetching }, analysisFetching === 'spapi' ? [React.createElement(LIcon, { key: 'icon', name: 'loader-2', size: 14, spin: true }), ' Fetching...'] : [React.createElement(LIcon, { key: 'icon', name: 'link', size: 14 }), ' Fetch SP-API' + (aSelCount > 0 ? ' (' + aSelCount + ')' : '')]),
              canFetchAnalysis && poIsFBM && React.createElement(Btn, { size: 'sm', onClick: fetchShipStationForPo, disabled: !!analysisFetching }, analysisFetching === 'shipstation' ? [React.createElement(LIcon, { key: 'icon', name: 'loader-2', size: 14, spin: true }), ' Fetching...'] : [React.createElement(LIcon, { key: 'icon', name: 'truck', size: 14 }), ' Fetch ShipStation Data']),
              canFetchAnalysis && poIsWFS && React.createElement(Btn, { size: 'sm', onClick: fetchWalmartForPo, disabled: !!analysisFetching }, analysisFetching === 'walmart' ? [React.createElement(LIcon, { key: 'icon', name: 'loader-2', size: 14, spin: true }), ' Fetching...'] : [React.createElement(LIcon, { key: 'icon', name: 'shopping-cart', size: 14 }), ' Fetch Walmart' + (aSelCount > 0 ? ' (' + aSelCount + ')' : '')]),
              canFetchAnalysis && React.createElement('div', { style: { width: 1, height: 20, background: C.border, margin: '0 2px' } }),
              React.createElement('input', { ref: aImportRef, type: 'file', accept: '.csv,.txt', style: { display: 'none' }, onChange: handleAImport }),
              React.createElement('input', { ref: aCsvImportRef, type: 'file', accept: '.csv', style: { display: 'none' }, onChange: handleAnalysisCsvImport }),
              isDraft && React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: async function() { if ((await appConfirm('Approve this PO? It will be removed from the Analysis module.'))) { updateStatus('approved'); } }, style: { background: '#22C55E' } }, React.createElement(LIcon, { name: 'check', size: 14 }), ' Approve PO')
            )
          ),
          // Fetch result
          analysisFetchResult && React.createElement('div', { style: { padding: '6px 14px', borderRadius: 6, marginBottom: 12,
            background: analysisFetchResult.success ? C.success + '15' : C.danger + '15',
            border: '1px solid ' + (analysisFetchResult.success ? C.success + '40' : C.danger + '40'),
            color: analysisFetchResult.success ? C.success : C.danger, fontSize: 12 } },
            analysisFetchResult.success ? React.createElement(LIcon, { name: 'check', size: 14 }) : React.createElement(LIcon, { name: 'x', size: 14 }), ' ', analysisFetchResult.message),
          // Summary cards
          ad.summary && React.createElement('div', { className: 'rg-2x2', style: { display: 'grid', gridTemplateColumns: 'repeat(' + summaryItems.length + ', minmax(0, 1fr))', gap: 10, marginBottom: 16 } },
            summaryItems.map(function(c) {
              return React.createElement(Card, { key: c.label, style: { padding: 12, minWidth: 0 } },
                React.createElement('div', { style: { fontSize: 9, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' } }, c.label),
                React.createElement('div', { style: { fontSize: 16, fontWeight: 700, color: c.color, marginTop: 2, whiteSpace: 'nowrap' } }, c.value)
              );
            })
          ),
          // Line items table
          (function() {
            var aLineCols = [
              { key: '_select', label: '', locked: true, sticky: true, stopClick: true, noFilter: true, style: { width: 44 },
                headerRender: function() {
                  var items = aIncompleteOnly ? (ad.lines || []).filter(function(l) { return !l.has_keepa || !l.unit_cost || parseFloat(l.unit_cost) === 0; }) : (ad.lines || []);
                  var allChecked = items.length > 0 && aSelCount === items.length;
                  var someChecked = !allChecked && aSelCount > 0;
                  return React.createElement('div', {
                    onClick: function(e) { e.stopPropagation(); toggleAAll(); },
                    style: { width: 32, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', userSelect: 'none' }
                  },
                    React.createElement('div', {
                      style: { width: 18, height: 18, borderRadius: 4, border: '2px solid ' + (allChecked || someChecked ? C.accent : C.textDim),
                        background: allChecked ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }
                    },
                      allChecked && React.createElement(LIcon, { name: 'check', size: 12, color: '#fff' }),
                      someChecked && !allChecked && React.createElement('span', { style: { color: C.accent, fontSize: 12, fontWeight: 700, lineHeight: 1 } }, '–')
                    )
                  );
                },
                render: function(l) {
                  var checked = !!analysisSelected[l.id];
                  return React.createElement('div', {
                    onClick: function(e) { e.stopPropagation(); toggleASel(l.id, e); },
                    style: { width: 32, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', userSelect: 'none' }
                  }, React.createElement('div', { style: { width: 18, height: 18, borderRadius: 4, border: '2px solid ' + (checked ? C.accent : C.textDim), background: checked ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' } },
                    checked && React.createElement(LIcon, { name: 'check', size: 12, color: '#fff' })
                  ));
                }
              },
              { key: 'sku', label: 'SKU', copyable: true, render: function(l) {
                return React.createElement('span', { style: { display: 'flex', alignItems: 'center' } },
                  React.createElement('span', {
                    onClick: function(e) { e.stopPropagation(); if (l.product_id) openProductEdit(l.product_id); },
                    title: l.sku || '',
                    style: { fontWeight: 600, color: C.accent, fontFamily: 'inherit', fontSize: 11, cursor: l.product_id ? 'pointer' : 'default', textDecoration: l.product_id ? 'underline' : 'none', textDecorationStyle: 'dotted', textUnderlineOffset: '3px', display: 'block', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
                  }, l.sku),
                  TopRevChip(l),
                  TransparencyChip(l)
                );
              }},
              { key: 'vendor_sku', label: 'Vendor SKU', copyable: true },
              { key: 'vendor_title', label: 'Vendor Title', copyable: true, style: { color: C.text }, render: function(l) {
                return React.createElement('span', { title: l.vendor_title || '', style: { display: 'block', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, l.vendor_title || '');
              } },
              { key: 'asin', label: poIsWFS ? 'Walmart Item#' : poIsMELI ? 'ML Code' : 'ASIN', copyable: true, render: function(l) {
                if (poIsMELI) {
                  return l.ml_code ? React.createElement('span', { style: { fontFamily: 'inherit', fontSize: 11 } }, l.ml_code)
                    : React.createElement('span', { style: { color: C.warning, fontSize: 10, fontStyle: 'italic' } }, 'No ML Code');
                }
                var identifier = poIsWFS ? l.walmart_item_id : l.asin;
                var identifierLink = poIsWFS && l.walmart_item_id
                  ? React.createElement('a', { href: 'https://www.walmart.com/ip/' + l.walmart_item_id, target: '_blank',
                      style: { color: C.accent, textDecoration: 'none', fontFamily: 'inherit', fontSize: 11 } }, l.walmart_item_id)
                  : l.asin ? React.createElement('a', { href: 'https://www.amazon' + suffix + '/dp/' + l.asin, target: '_blank',
                      style: { color: C.accent, textDecoration: 'none', fontFamily: 'inherit', fontSize: 11 } }, l.asin)
                  : React.createElement('span', { style: { color: C.warning, fontSize: 10, fontStyle: 'italic' } }, poIsWFS ? 'No Item#' : 'No ASIN');
                return React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 2 } },
                  React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 6 } },
                    identifierLink,
                    l.keepa_status === 'not_found' && React.createElement('span', { style: { padding: '1px 6px', borderRadius: 99, fontSize: 9, fontWeight: 700, background: '#EF444420', color: '#EF4444', whiteSpace: 'nowrap' } }, 'NOT FOUND'),
                    !l.buy_box_price && (l.asin || poIsWFS) && React.createElement('span', { style: { padding: '1px 6px', borderRadius: 99, fontSize: 9, fontWeight: 700, background: '#F59E0B20', color: '#F59E0B', whiteSpace: 'nowrap' } }, 'NO PRICE')
                  ),
                  l.keepa_fetched_at && l.keepa_status !== 'not_found' && (function() {
                    var days = Math.floor((Date.now() - new Date(l.keepa_fetched_at).getTime()) / 86400000);
                    var label = days === 0 ? 'today' : days === 1 ? '1d' : days + 'd';
                    var color = days > 14 ? '#EF4444' : days > 7 ? '#F59E0B' : C.textDim;
                    // The pill names the source the price actually came from (server: services/fbm-price.js
                    // for FBM — Manual > ShipStation > Keepa), not just its age.
                    return React.createElement('span', {
                      style: { fontSize: 9, color: color, whiteSpace: 'nowrap' },
                      title: (l.data_source ? 'Price Source: ' + l.data_source + ' · ' : '') + 'As of ' + new Date(l.keepa_fetched_at).toLocaleDateString()
                    }, label + (l.data_source ? ' · ' + l.data_source : ''));
                  })()
                );
              }},
              { key: 'qty_ordered', label: 'Units', sortKey: 'qty_ordered', stopClick: true, style: { color: C.text }, render: function(l) {
                if (aEditItem === l.id && aEditForm._focus === 'qty') {
                  var upc = l.units_per_case || l.case_qty || 1;
                  var curQty = aEditForm.qty_ordered != null && aEditForm.qty_ordered !== '' ? aEditForm.qty_ordered : 0;
                  var notDivisible = upc > 1 && curQty > 0 && (curQty % upc !== 0);
                  return React.createElement('div', { 'data-aedit-row': 'true', onClick: function(e) { e.stopPropagation(); } },
                    React.createElement('input', { type: 'number', autoFocus: true, onFocus: function(e) { e.target.select(); },
                      value: aEditForm.qty_ordered != null ? aEditForm.qty_ordered : '',
                      onChange: function(e) { var v = e.target.value; setAEditForm(function(prev) { return Object.assign({}, prev, { qty_ordered: v === '' ? '' : parseInt(v) }); }); aEditRef.current.form = Object.assign({}, aEditRef.current.form, { qty_ordered: v === '' ? '' : parseInt(v) }); },
                      onBlur: handleAnalysisBlur,
                      onKeyDown: function(e) {
                        if (e.key === 'Enter') { e.target.blur(); }
                        else if (e.key === 'Escape') { setAEditItem(null); setAEditForm({}); }
                        // Tab-through like the Line Items tab (2026-09-29): on to the cost cell, the rest of the row pre-filled
                        else if (e.key === 'Tab') { e.preventDefault(); leaveAnalysisQty(l, aEditForm, function() { setAEditForm(function(prev) { return Object.assign({}, analysisFormFor(l, 'cost'), prev, { _focus: 'cost' }); }); }); }
                      },
                      style: { width: 70, background: C.bg, border: '1px solid ' + (notDivisible ? C.warning : C.borderFocus), color: C.text, padding: '2px 6px', borderRadius: 4, fontSize: 12, fontFamily: 'inherit' }
                    }),
                    notDivisible && React.createElement('div', { style: { fontSize: 10, color: C.warning, marginTop: 2, whiteSpace: 'nowrap' } }, 'Not divisible by ' + upc)
                  );
                }
                if (!isEditable) return String(l.qty_ordered || 0);
                return React.createElement('span', {
                  onClick: function(e) { e.stopPropagation(); if (aEditItem === l.id) { setAEditForm(function(prev) { return Object.assign({}, prev, { _focus: 'qty', qty_ordered: prev.qty_ordered != null ? prev.qty_ordered : l.qty_ordered }); }); } else { flushAnalysisEdit(l.id); setAEditItem(l.id); setAEditForm({ qty_ordered: l.qty_ordered, _focus: 'qty' }); } },
                  style: { cursor: 'pointer', color: C.text, borderBottom: '1px dashed ' + C.textDim }
                }, String(l.qty_ordered || 0));
              }},
              { key: 'pct_of_case', label: '% of Case', render: function(l) {
                if (l.pct_of_case === null || l.pct_of_case === undefined) return React.createElement('span', { style: { color: C.textMuted, fontSize: 12 } }, '\u2014');
                var pct = l.pct_of_case;
                if (pct === 0) return React.createElement(Tooltip, { text: 'No forecast history \u2014 seeded as No-Sales SKU (case pack / MOQ)', style: { fontSize: 12, color: C.textMuted, fontStyle: 'italic' } }, '0%');
                var upc = l.units_per_case || l.case_qty || 0;
                var derivedTarget = upc > 0 ? Math.round(pct * upc / 100) : 0;
                var color = pct < 50 ? '#F472B6' : pct < 100 ? '#FBBF24' : '#34D399';
                return React.createElement(Tooltip, { text: 'Velocity-based target ' + derivedTarget + ' units \u00f7 Case Pack ' + upc + ' = ' + pct + '%', style: { fontSize: 12, fontWeight: pct < 50 ? 700 : 400, color: color } }, pct + '%');
              }},
              { key: 'unit_cost', label: 'Unit Cost', sortKey: 'unit_cost', stopClick: true, render: function(l) {
                if (aEditItem === l.id && aEditForm._focus === 'cost') return React.createElement('div', { 'data-aedit-row': 'true', onClick: function(e) { e.stopPropagation(); } },
                  React.createElement('input', { type: 'number', step: '0.01', autoFocus: true, onFocus: function(e) { e.target.select(); },
                    value: aEditForm.unit_cost != null ? aEditForm.unit_cost : '', onChange: function(e) { var v = e.target.value; setAEditForm(function(prev) { return Object.assign({}, prev, { unit_cost: v }); }); aEditRef.current.form = Object.assign({}, aEditRef.current.form, { unit_cost: v }); },
                    onBlur: handleAnalysisBlur,
                    onKeyDown: function(e) {
                      if (e.key === 'Enter') {
                        aEditRef.current.id = null;
                        var newCost = parseFloat(aEditForm.unit_cost);
                        if (!isNaN(newCost)) {
                          api('/purchase-orders/' + poId + '/items/' + l.id, { method: 'PUT', body: JSON.stringify({ unit_cost: newCost }) })
                            .then(function() { setAEditItem(null); load(); loadAnalysis(); })
                            .catch(function(err) { showToast(err.message, 'error'); });
                        } else { setAEditItem(null); }
                      }
                      if (e.key === 'Escape') { aEditRef.current.id = null; setAEditItem(null); }
                      if (e.key === 'Tab') { e.preventDefault(); setAEditForm(function(prev) { return Object.assign({}, prev, { _focus: 'prep' }); }); }
                    },
                    style: { width: 70, background: C.bg, border: '1px solid ' + C.borderFocus, color: C.text, padding: '2px 6px', borderRadius: 4, fontSize: 11, fontFamily: 'inherit', textAlign: 'right' } })
                );
                var missingCost = !l.unit_cost || parseFloat(l.unit_cost) === 0;
                return React.createElement('span', {
                  'data-aedit-row': 'true',
                  onClick: function(e) { e.stopPropagation(); if (aEditItem === l.id) { setAEditForm(function(prev) { return Object.assign({}, prev, { _focus: 'cost' }); }); } else { flushAnalysisEdit(l.id); setAEditItem(l.id); setAEditForm({
  unit_cost: l.unit_cost != null && l.unit_cost !== '' ? Number(l.unit_cost).toFixed(2) : '',
  prep_cost: l.prep_cost != null && l.prep_cost !== '' ? Number(l.prep_cost).toFixed(2) : '',
  buy_box_price: l.buy_box_price != null && l.buy_box_price !== '' ? Number(l.buy_box_price).toFixed(2) : '',
  fba_fee: (function() { var v = poIsFBM ? l.shipping_fee : l.fba_fee; return v != null && v !== '' ? Number(v).toFixed(2) : ''; })(),
  referral_fee_pct: l.referral_fee_pct || 15,
  _focus: 'cost'
}); } },
                  style: { fontFamily: 'inherit', cursor: 'pointer', borderBottom: missingCost ? 'none' : '1px dashed ' + C.textDim }
                }, missingCost ? React.createElement('span', { style: { background: C.dangerSoft, color: C.danger, padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' } }, 'MISSING') : fmtM(l.unit_cost));
              }},
              { key: 'inbound_freight_cost', label: 'Freight', defaultVisible: false, render: function(l) {
                if (!l.inbound_freight_cost && !l.inbound_freight_pct) return React.createElement('span', { style: { color: C.textMuted, fontSize: 12 } }, '\u2014');
                return React.createElement('span', { style: { fontFamily: 'inherit', fontSize: 12 }, title: (l.inbound_freight_pct * 100).toFixed(1) + '% of unit cost' }, fmtM(l.inbound_freight_cost || 0));
              }},
              { key: 'prep_cost', label: 'Prep', defaultVisible: false, stopClick: true, render: function(l) {
                if (aEditItem === l.id && aEditForm._focus === 'prep') return React.createElement('div', { 'data-aedit-row': 'true', onClick: function(e) { e.stopPropagation(); } },
                  React.createElement('input', { type: 'number', step: '0.01', autoFocus: true, onFocus: function(e) { e.target.select(); },
                    value: aEditForm.prep_cost != null ? aEditForm.prep_cost : '', onChange: function(e) { var v = e.target.value; setAEditForm(function(prev) { var d = Object.assign({}, prev._changed || {}, { prep_cost: true }); return Object.assign({}, prev, { prep_cost: v, _changed: d }); }); aEditRef.current.form = Object.assign({}, aEditRef.current.form, { prep_cost: v, _changed: Object.assign({}, (aEditRef.current.form || {})._changed, { prep_cost: true }) }); },
                    onBlur: handleAnalysisBlur,
                    onKeyDown: function(e) { if (e.key === 'Enter') { aEditRef.current.id = null; saveAnalysisField(l.id); } if (e.key === 'Escape') { aEditRef.current.id = null; setAEditItem(null); } if (e.key === 'Tab') { e.preventDefault(); setAEditForm(function(prev) { return Object.assign({}, prev, { _focus: 'bb' }); }); } },
                    style: { width: 70, background: C.bg, border: '1px solid ' + C.borderFocus, color: C.text, padding: '2px 6px', borderRadius: 4, fontSize: 11, fontFamily: 'inherit', textAlign: 'right' } })
                );
                return React.createElement('span', {
                  'data-aedit-row': 'true',
                  onClick: function(e) { e.stopPropagation(); if (aEditItem === l.id) { setAEditForm(function(prev) { return Object.assign({}, prev, { _focus: 'prep' }); }); } else { flushAnalysisEdit(l.id); setAEditItem(l.id); setAEditForm({
  prep_cost: l.prep_cost != null && l.prep_cost !== '' ? Number(l.prep_cost).toFixed(2) : '',
  buy_box_price: l.buy_box_price != null && l.buy_box_price !== '' ? Number(l.buy_box_price).toFixed(2) : '',
  fba_fee: (function() { var v = poIsFBM ? l.shipping_fee : l.fba_fee; return v != null && v !== '' ? Number(v).toFixed(2) : ''; })(),
  referral_fee_pct: l.referral_fee_pct || 15,
  _focus: 'prep'
}); } },
                  style: { fontFamily: 'inherit', fontSize: 12, cursor: 'pointer', borderBottom: '1px dashed ' + C.textDim }
                }, l.prep_cost > 0 ? fmtM(l.prep_cost) : '\u2014');
              }},
              { key: 'buy_box_price', label: 'Sell Price', sortKey: 'buy_box_price', stopClick: true, render: function(l) {
                var isEditing = aEditItem === l.id;
                var displayBB = isEditing && aEditForm.buy_box_price !== undefined ? (parseFloat(aEditForm.buy_box_price) || 0) : l.buy_box_price;
                var hasBB = displayBB > 0;
                if (isEditing && aEditForm._focus === 'bb') return React.createElement('div', { 'data-aedit-row': 'true', onClick: function(e) { e.stopPropagation(); } },
                  React.createElement('input', { type: 'number', step: '0.01', autoFocus: true, onFocus: function(e) { e.target.select(); },
                    value: aEditForm.buy_box_price != null ? aEditForm.buy_box_price : '', onChange: function(e) { var v = e.target.value; setAEditForm(function(prev) { var d = Object.assign({}, prev._changed || {}, { buy_box_price: true }); return Object.assign({}, prev, { buy_box_price: v, _changed: d }); }); aEditRef.current.form = Object.assign({}, aEditRef.current.form, { buy_box_price: v, _changed: Object.assign({}, (aEditRef.current.form || {})._changed, { buy_box_price: true }) }); },
                    onBlur: handleAnalysisBlur,
                    onKeyDown: function(e) { if (e.key === 'Enter') { aEditRef.current.id = null; saveAnalysisField(l.id); } if (e.key === 'Escape') { aEditRef.current.id = null; setAEditItem(null); } if (e.key === 'Tab') { e.preventDefault(); setAEditForm(function(prev) { return Object.assign({}, prev, { _focus: 'fba' }); }); } },
                    style: { width: 70, background: C.bg, border: '1px solid ' + C.borderFocus, color: C.text, padding: '2px 6px', borderRadius: 4, fontSize: 11, fontFamily: 'inherit', textAlign: 'right' } })
                );
                var missingBB = !hasBB && (l.asin || poIsWFS) && !l.has_keepa;
                return React.createElement('span', {
                  'data-aedit-row': 'true',
                  onClick: function(e) { e.stopPropagation(); if (aEditItem === l.id) { setAEditForm(function(prev) { return Object.assign({}, prev, { _focus: 'bb' }); }); } else { flushAnalysisEdit(l.id); setAEditItem(l.id); setAEditForm({
  prep_cost: l.prep_cost != null && l.prep_cost !== '' ? Number(l.prep_cost).toFixed(2) : '',
  buy_box_price: l.buy_box_price != null && l.buy_box_price !== '' ? Number(l.buy_box_price).toFixed(2) : '',
  fba_fee: (function() { var v = poIsFBM ? l.shipping_fee : l.fba_fee; return v != null && v !== '' ? Number(v).toFixed(2) : ''; })(),
  referral_fee_pct: l.referral_fee_pct || 15,
  _focus: 'bb'
}); } },
                  style: { fontFamily: 'inherit', fontWeight: 600, color: hasBB ? C.text : C.textMuted, cursor: 'pointer', borderBottom: missingBB ? 'none' : '1px dashed ' + C.textDim }
                }, hasBB ? fmtM(displayBB) : (missingBB ? React.createElement('span', { style: { background: C.dangerSoft, color: C.danger, padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' } }, 'MISSING') : '\u2014'));
              }},
              { key: 'fba_fee', label: poIsWFS ? 'WFS Fee' : poIsMELI ? 'ML Ship (est.)' : poIsFBM ? 'Ship Fee' : 'FBA Fee', sortKey: poIsWFS ? 'wfs_fee' : poIsFBM ? 'shipping_fee' : 'fba_fee', stopClick: true, render: function(l) {
                var isEditing = aEditItem === l.id;
                var displayFee = isEditing && aEditForm.fba_fee !== undefined ? (parseFloat(aEditForm.fba_fee) || 0) : (poIsWFS ? (l.wfs_fee || 0) : poIsFBM ? (l.shipping_fee || 0) : l.fba_fee);
                var hasBB = l.buy_box_price > 0;
                if (isEditing && aEditForm._focus === 'fba') return React.createElement('div', { 'data-aedit-row': 'true', onClick: function(e) { e.stopPropagation(); } },
                  React.createElement('input', { type: 'number', step: '0.01', autoFocus: true, onFocus: function(e) { e.target.select(); },
                    value: aEditForm.fba_fee != null ? aEditForm.fba_fee : '', onChange: function(e) { var v = e.target.value; setAEditForm(function(prev) { var d = Object.assign({}, prev._changed || {}, { fba_fee: true }); return Object.assign({}, prev, { fba_fee: v, _changed: d }); }); aEditRef.current.form = Object.assign({}, aEditRef.current.form, { fba_fee: v, _changed: Object.assign({}, (aEditRef.current.form || {})._changed, { fba_fee: true }) }); },
                    onBlur: handleAnalysisBlur,
                    onKeyDown: function(e) { if (e.key === 'Enter') { aEditRef.current.id = null; saveAnalysisField(l.id); } if (e.key === 'Escape') { aEditRef.current.id = null; setAEditItem(null); } if (e.key === 'Tab') { e.preventDefault(); analysisTabToNextRow(l); } },
                    style: { width: 70, background: C.bg, border: '1px solid ' + C.borderFocus, color: C.text, padding: '2px 6px', borderRadius: 4, fontSize: 11, fontFamily: 'inherit', textAlign: 'right' } })
                );
                var missingFee = (l.asin || poIsWFS) && !l.has_keepa && displayFee <= 0;
                return React.createElement('span', {
                  'data-aedit-row': 'true',
                  onClick: function(e) { e.stopPropagation(); if (aEditItem === l.id) { setAEditForm(function(prev) { return Object.assign({}, prev, { _focus: 'fba' }); }); } else { flushAnalysisEdit(l.id); setAEditItem(l.id); setAEditForm({
  prep_cost: l.prep_cost != null && l.prep_cost !== '' ? Number(l.prep_cost).toFixed(2) : '',
  buy_box_price: l.buy_box_price != null && l.buy_box_price !== '' ? Number(l.buy_box_price).toFixed(2) : '',
  fba_fee: (function() { var v = poIsFBM ? l.shipping_fee : l.fba_fee; return v != null && v !== '' ? Number(v).toFixed(2) : ''; })(),
  referral_fee_pct: l.referral_fee_pct || 15,
  _focus: 'fba'
}); } },
                  style: { fontFamily: 'inherit', cursor: 'pointer', borderBottom: missingFee ? 'none' : '1px dashed ' + C.textDim }
                }, missingFee ? React.createElement('span', { style: { background: C.dangerSoft, color: C.danger, padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' } }, 'MISSING') : (displayFee > 0 ? fmtM(displayFee) : (hasBB ? fmtM(0) : '\u2014')));
              }},
              { key: 'referral_fee_pct', label: 'Referral', sortKey: 'referral_fee', render: function(l) {
                var refFee = l.buy_box_price > 0 ? l.buy_box_price * ((l.referral_fee_pct || 15) / 100) : 0;
                return React.createElement('span', {
                  style: { fontFamily: 'inherit', color: l.buy_box_price > 0 ? C.text : C.textMuted },
                  title: (l.referral_fee_pct || 15) + '%'
                }, l.buy_box_price > 0 ? fmtM(Math.round(refFee * 100) / 100) : '\u2014');
              }},
              { key: 'profit_per_unit', label: 'Profit/u', sortKey: 'profit_per_unit', render: function(l) {
                var hasBB = l.buy_box_price > 0;
                var pColor = hasBB ? (l.profit_per_unit > 0 ? C.success : l.profit_per_unit < 0 ? C.danger : C.textMuted) : C.textMuted;
                return React.createElement('span', { style: { fontFamily: 'inherit', fontWeight: 600, color: pColor } }, hasBB ? fmtM(l.profit_per_unit) : '—');
              }},
              { key: 'line_cost', label: 'Line Cost', sortKey: 'line_cost', render: function(l) {
                return React.createElement('span', { style: { fontFamily: 'inherit' } }, fmtM(l.line_cost));
              }},
              { key: 'line_profit', label: 'Line Profit', sortKey: 'line_profit', render: function(l) {
                var hasBB = l.buy_box_price > 0;
                var pColor = hasBB ? (l.profit_per_unit > 0 ? C.success : l.profit_per_unit < 0 ? C.danger : C.textMuted) : C.textMuted;
                return React.createElement('span', { style: { fontFamily: 'inherit', fontWeight: 600, color: pColor } }, hasBB ? fmtM(l.line_profit) : '—');
              }},
              { key: 'roi', label: 'ROI', sortKey: 'roi', render: function(l) {
                var hasBB = l.buy_box_price > 0;
                var rColor = hasBB ? (l.roi >= 30 ? C.success : l.roi >= 0 ? C.warning : C.danger) : C.textMuted;
                return React.createElement('span', { style: { fontFamily: 'inherit', fontWeight: 600, color: rColor } }, hasBB ? fmtP(l.roi) : '—');
              }},
              { key: 'margin', label: 'Margin', sortKey: 'margin', render: function(l) {
                var hasBB = l.buy_box_price > 0;
                var pColor = hasBB ? (l.profit_per_unit > 0 ? C.success : l.profit_per_unit < 0 ? C.danger : C.textMuted) : C.textMuted;
                return React.createElement('span', { style: { fontFamily: 'inherit', color: pColor } }, hasBB ? fmtP(l.margin) : '—');
              }},
              { key: 'return_rate_pct', label: 'Return Rate', sortKey: 'return_rate_pct', headerRender: function() { return React.createElement('span', { title: 'Customer returns \u00f7 units sold (last 365 days), aggregated across all channels (Amazon FBA+FBM, Walmart, eBay, SHEIN). Alias SKUs roll up to canonical.' }, 'Return Rate'); }, render: function(l) {
                if (l.return_rate_pct == null) return React.createElement('span', { style: { color: C.textMuted, fontSize: 12 } }, '\u2014');
                var pct = l.return_rate_pct;
                var color = pct >= 10 ? '#F87171' : pct >= 5 ? '#FBBF24' : '#34D399';
                var tip = (l.returned_365d || 0) + ' returned \u00f7 ' + (l.sold_365d_canonical || 0) + ' sold (365d, canonical SKU)';
                return React.createElement('span', { style: { fontFamily: 'inherit', fontWeight: 600, color: color }, title: tip }, pct + '%');
              }},
              { key: 'returned_365d', label: 'Returns 365d', sortKey: 'returned_365d', defaultVisible: false, render: function(l) {
                var returned = l.returned_365d || 0;
                var sold = l.sold_365d_canonical || 0;
                if (returned === 0 && sold === 0) return React.createElement('span', { style: { color: C.textMuted, fontSize: 12 } }, '\u2014');
                var pct = l.return_rate_pct;
                var color = pct == null ? C.text : (pct >= 10 ? '#F87171' : pct >= 5 ? '#FBBF24' : '#34D399');
                return React.createElement('span', { style: { fontFamily: 'inherit', fontWeight: 600, color: color }, title: returned + ' returned \u00f7 ' + sold + ' sold over 365d (canonical)' + (pct != null ? ' = ' + pct + '%' : '') }, returned + '/' + sold);
              }},
              { key: 'days_of_stock', label: 'Days of Stock', defaultVisible: false, render: function(l) {
                // Matches Catalog > Product > Analytics \u2014 the server computes it via the shared days-of-stock helper.
                var d = l.days_of_stock;
                if (d === null || d === undefined) return React.createElement('span', { style: { color: C.textMuted } }, '\u2014');
                var color = d === 0 ? C.danger : d <= 14 ? C.warning : d >= 999 ? C.textMuted : C.success;
                return React.createElement('span', { style: { fontFamily: 'inherit', fontWeight: 600, color: color } }, d >= 999 ? '\u221E' : String(d));
              }},
              { key: 'days_to_order', label: 'Days to Order', defaultVisible: false, render: function(l) {
                // Forecast's days_to_order = days of stock (on-hand + PO/FBA/WFS inbound) minus lead time.
                // <= 0 means order now to avoid a stockout once lead time is accounted for.
                var sku = l.product_sku || l.sku;
                var match = sku ? forecastMap[sku] : null;
                var d = match ? match.days_to_order : null;
                if (d === null || d === undefined) return React.createElement('span', { style: { color: C.textMuted } }, '\u2014');
                var color = d <= 0 ? C.danger : d <= 14 ? C.warning : C.success;
                return React.createElement('span', { style: { fontFamily: 'inherit', fontWeight: 600, color: color },
                  title: 'Days of stock (incl. inbound) \u2212 lead time. \u2264 0 = order now.' }, String(d));
              }},
              { key: 'internal_notes', label: 'Internal Notes', defaultVisible: false,
                sortValue: function(l) { return (l.internal_notes || '').toLowerCase().trim(); },
                render: function(l) {
                  return l.internal_notes ? React.createElement('span', { style: { fontSize: 11, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }, title: l.internal_notes }, l.internal_notes) : '';
                }}
            ];
            var aFilteredLines = aIncompleteOnly ? (ad.lines || []).filter(function(l) { return !l.has_keepa || !l.unit_cost || parseFloat(l.unit_cost) === 0; }) : (ad.lines || []);
            // Inject forecast-derived fields (days_on_hand, returns) so columns are sortable
            aFilteredLines = aFilteredLines.map(function(l) {
              var sku = l.product_sku || l.sku;
              var m = sku ? forecastMap[sku] : null;
              return Object.assign({}, l, {
                days_on_hand: m && m.days_on_hand != null ? m.days_on_hand : null,
                days_to_order: m && m.days_to_order != null ? m.days_to_order : null,
                returned_365d: m ? (m.returned_365d || 0) : 0,
                sold_365d_canonical: m && m.sold_365d_canonical != null ? m.sold_365d_canonical : (m ? (m.sold_365d || 0) : 0),
                return_rate_pct: m && m.return_rate_pct != null ? m.return_rate_pct : null
              });
            });
            return React.createElement(DataTable, {
              columns: aLineCols,
              data: aFilteredLines,
              storageKey: 'po-analysis-lines',
              defaultSort: 'sku', defaultSortDir: 'asc',
              onSortedData: function(d) { aSortedRef.current = d; },
              emptyTitle: aIncompleteOnly ? 'All items have complete data' : 'No analysis data',
              emptySub: aIncompleteOnly ? 'No incomplete items to show' : 'Fetch Keepa or SP-API data to populate',
              extraToolbar: React.createElement('button', { onClick: function() { setAIncompleteOnly(!aIncompleteOnly); },
                style: { padding: '4px 10px', fontSize: 11, borderRadius: 6, cursor: 'pointer', fontWeight: 500, fontFamily: 'inherit', whiteSpace: 'nowrap',
                  background: aIncompleteOnly ? C.warning + '20' : C.surface, color: aIncompleteOnly ? C.warning : C.textMuted,
                  border: '1px solid ' + (aIncompleteOnly ? C.warning + '40' : C.border) } }, 'Incomplete Only'),
              bulkActions: {
                count: aSelCount,
                alwaysShow: true,
                onClear: function() { setAnalysisSelected({}); },
                items: [
                  { label: 'Export ASINs', onClick: exportAsinsForPo },
                  { label: 'Import Keepa CSV', onClick: function() { aImportRef.current && aImportRef.current.click(); } },
                  { divider: true },
                  { label: 'Export CSV', onClick: exportAnalysisCsv, disabled: analysisFetching === 'walmart', title: analysisFetching === 'walmart' ? 'Scraping buy box prices — wait for it to finish so the CSV reflects current values' : undefined },
                  { label: 'Import CSV', onClick: function() { aCsvImportRef.current && aCsvImportRef.current.click(); } },
                  aSelCount > 0 && { header: 'Selected (' + aSelCount + ')' },
                  aSelCount > 0 && { label: 'Move to Next PO', onClick: moveToNext },
                  aSelCount > 0 && { divider: true },
                  aSelCount > 0 && { label: 'Remove from PO', onClick: aRemoveFromPo, danger: true }
                ].filter(Boolean)
              }
            });
          })()
        );
      })()
    ),

    // Add Product Modal
    (function() {
      var selCount = Object.keys(addSelections).filter(function(k) { return parseInt(addSelections[k]) > 0; }).length;
      var totalSelCount = Object.keys(addSelections).length;
      var allChecked = filteredProducts.length > 0 && filteredProducts.every(function(p) { return addSelections.hasOwnProperty(String(p.id)); });
      var toggleAll = function() {
        if (allChecked) {
          setAddSelections({});
        } else {
          var next = Object.assign({}, addSelections);
          filteredProducts.forEach(function(p) { if (!next.hasOwnProperty(String(p.id))) next[String(p.id)] = String(p.case_qty || 1); });
          setAddSelections(next);
        }
      };
      return React.createElement(Modal, { open: addModalOpen, onClose: function() { setAddModalOpen(false); setAddSearch(''); setAddSelections({}); }, title: 'Add Products to PO', width: 780 },
        React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 12 } },
          React.createElement('input', { placeholder: 'Search by SKU, vendor SKU, barcode, or vendor title...', value: addSearch, onChange: function(e) { setAddSearch(e.target.value); setAddPage(0); },
            style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '10px 14px', borderRadius: 8, fontSize: 13, outline: 'none', width: '100%' } }),
          archivedLoading ? React.createElement('div', { style: { fontSize: 12, color: C.textDim, marginTop: -4 } },
            'Loading archived SKUs…') : null,
          React.createElement('div', { style: { border: '1px solid ' + C.border, borderRadius: 8, overflow: 'hidden' } },
            // Table header
            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '40px 1fr 1fr 100px 80px 90px', gap: 0, background: C.bg, borderBottom: '1px solid ' + C.border, padding: '8px 0' } },
              React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 8px', cursor: 'pointer' }, onClick: toggleAll },
                React.createElement('div', { style: { width: 18, height: 18, borderRadius: 4, border: '2px solid ' + (allChecked ? C.accent : C.border),
                  background: allChecked ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 700 } },
                  allChecked ? '\u2713' : '')
              ),
              React.createElement('div', { style: { fontSize: 10, fontWeight: 700, color: C.textDim, textTransform: 'uppercase', padding: '0 10px' } }, 'SKU'),
              React.createElement('div', { style: { fontSize: 10, fontWeight: 700, color: C.textDim, textTransform: 'uppercase', padding: '0 10px' } }, 'Vendor Title'),
              React.createElement('div', { style: { fontSize: 10, fontWeight: 700, color: C.textDim, textTransform: 'uppercase', padding: '0 10px' } }, 'Vendor SKU'),
              React.createElement('div', { style: { fontSize: 10, fontWeight: 700, color: C.textDim, textTransform: 'uppercase', padding: '0 10px', textAlign: 'right' } }, 'Unit Cost'),
              React.createElement('div', { style: { fontSize: 10, fontWeight: 700, color: C.textDim, textTransform: 'uppercase', padding: '0 10px', textAlign: 'center' } }, 'Qty')
            ),
            // Table body (paginated)
            React.createElement('div', { style: { maxHeight: 420, overflow: 'auto' } },
              filteredProducts.length === 0
                ? React.createElement('div', { style: { padding: 24, color: C.textDim, fontSize: 13, textAlign: 'center' } }, 'No matching products for this vendor/warehouse')
                : filteredProducts.slice(addPage * 25, addPage * 25 + 25).map(function(p, pageIdx) {
                    var idx = addPage * 25 + pageIdx;
                    var selected = addSelections.hasOwnProperty(String(p.id));
                    return React.createElement('div', {
                      key: p.id,
                      onClick: function(e) {
                        var next = Object.assign({}, addSelections);
                        if (e.shiftKey && addLastClickRef.current >= 0 && addLastClickRef.current !== idx) {
                          var from = Math.min(addLastClickRef.current, idx);
                          var to = Math.max(addLastClickRef.current, idx);
                          for (var si = from; si <= to; si++) {
                            var sp = filteredProducts[si];
                            if (sp && !next.hasOwnProperty(String(sp.id))) { next[String(sp.id)] = String(sp.case_qty || 1); }
                          }
                        } else {
                          if (selected) { delete next[String(p.id)]; } else { next[String(p.id)] = String(p.case_qty || 1); }
                        }
                        addLastClickRef.current = idx;
                        setAddSelections(next);
                      },
                      style: { display: 'grid', gridTemplateColumns: '40px 1fr 1fr 100px 80px 90px', gap: 0, padding: '8px 0', cursor: 'pointer',
                        borderBottom: '1px solid ' + C.border + '44', background: selected ? C.accentSoft : 'transparent', transition: 'background 0.1s' }
                    },
                      React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'center' } },
                        React.createElement('div', { style: { width: 18, height: 18, borderRadius: 4, border: '2px solid ' + (selected ? C.accent : C.border),
                          background: selected ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 700 } },
                          selected ? '\u2713' : '')
                      ),
                      React.createElement('div', { style: { padding: '0 10px', fontSize: 12, fontWeight: 600, color: C.accent, fontFamily: 'inherit', overflow: 'hidden', whiteSpace: 'nowrap', alignSelf: 'center', display: 'flex', alignItems: 'center', gap: 6 } },
                        React.createElement('span', { style: { overflow: 'hidden', textOverflow: 'ellipsis' } }, p.sku),
                        p.is_active === false ? React.createElement('span', {
                          title: 'Archived in the catalog — orderable here because this is a Transfer from FBM PO',
                          style: { flexShrink: 0, background: C.warningSoft, color: C.warning, padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }
                        }, 'Archived') : null
                      ),
                      React.createElement('div', { style: { padding: '0 10px', fontSize: 12, color: C.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', alignSelf: 'center' } }, p.vendor_title || '\u2014'),
                      React.createElement('div', { style: { padding: '0 10px', fontSize: 12, color: C.textDim, fontFamily: 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', alignSelf: 'center' } }, p.vendor_sku || '\u2014'),
                      React.createElement('div', { style: { padding: '0 10px', fontSize: 12, color: C.text, textAlign: 'right', alignSelf: 'center' } }, '$' + Number(p.unit_cost || 0).toFixed(2)),
                      React.createElement('div', { style: { padding: '0 10px', alignSelf: 'center' }, onClick: function(e) { e.stopPropagation(); } },
                        selected ? React.createElement('input', { type: 'number', value: addSelections[String(p.id)] || '',
                          onChange: function(e) { var next = Object.assign({}, addSelections); next[String(p.id)] = e.target.value; setAddSelections(next); },
                          placeholder: 'Qty', autoFocus: totalSelCount === 1,
                          style: { width: '100%', background: C.bg, border: '1px solid ' + C.accent, color: C.text, padding: '5px 8px', borderRadius: 5, fontSize: 12, outline: 'none', textAlign: 'center' } })
                        : React.createElement('div', { style: { height: 28 } })
                      )
                    );
                  })
            )
          ),
          (function() {
            var totalPages = Math.ceil(filteredProducts.length / 25);
            return React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 } },
              totalPages > 1 && React.createElement('div', { style: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6 } },
                React.createElement('button', { onClick: function() { setAddPage(Math.max(0, addPage - 1)); }, disabled: addPage === 0,
                  style: { background: 'none', border: '1px solid ' + C.border, borderRadius: 4, padding: '4px 10px', cursor: addPage === 0 ? 'default' : 'pointer', color: addPage === 0 ? C.textDim : C.text, fontSize: 12, fontFamily: 'inherit' } }, '\u2039 Prev'),
                React.createElement('span', { style: { fontSize: 12, color: C.textMuted } }, 'Page ' + (addPage + 1) + ' of ' + totalPages),
                React.createElement('button', { onClick: function() { setAddPage(Math.min(totalPages - 1, addPage + 1)); }, disabled: addPage >= totalPages - 1,
                  style: { background: 'none', border: '1px solid ' + C.border, borderRadius: 4, padding: '4px 10px', cursor: addPage >= totalPages - 1 ? 'default' : 'pointer', color: addPage >= totalPages - 1 ? C.textDim : C.text, fontSize: 12, fontFamily: 'inherit' } }, 'Next \u203A')
              ),
              React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' } },
                React.createElement('span', { style: { fontSize: 11, color: C.textDim } },
                  filteredProducts.length + ' product' + (filteredProducts.length !== 1 ? 's' : '')
                  + (totalSelCount > 0 ? '  \u00B7  ' + totalSelCount + ' selected' : '')),
                React.createElement('div', { style: { display: 'flex', gap: 8 } },
                  React.createElement(Btn, { onClick: function() { setAddModalOpen(false); setAddSearch(''); setAddSelections({}); } }, 'Cancel'),
                  React.createElement(Btn, { variant: 'primary', onClick: addProduct, disabled: selCount === 0 || addSaving }, addSaving ? 'Adding...' : '+ Add ' + (selCount > 0 ? selCount + ' ' : '') + 'to PO')
                )
              )
            );
          })()
        )
      );
    })(),
    // Product Edit Full-Screen
    prodModalOpen && React.createElement(window.ProductEditorFullScreen, {
      product: prodForm, editing: prodEditing, form: prodForm,
      setForm: function(f) { setProdForm(f); },
      vendors: vendors, warehouses: warehouses, products: products, onSave: saveProduct,
      onStatusChange: function(p) { setProdEditing(function(prev) { return prev && prev.id === p.id ? Object.assign({}, prev, { is_active: p.is_active }) : prev; }); api('/products').then(setProducts).catch(function(e) { console.error('[silent-catch]', e); }); load(); },
      onClose: function() { setProdModalOpen(false); }, showToast: showToast
    }),
    // ─── Email Vendor Template Modal ───
    React.createElement(Modal, { open: emailModalOpen, onClose: function() { setEmailModalOpen(false); }, title: 'Email Vendor — PO #' + (po ? po.po_number : ''), width: 900, maxHeight: 'calc(90vh * var(--invzoom, 1))' },
      React.createElement('div', null,
        // Recipient (editable)
        React.createElement('div', { style: { marginBottom: 10 } },
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 } },
            React.createElement('label', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted } }, 'To'),
            React.createElement('span', { style: { fontSize: 10, color: C.textMuted } },
              'Attachments: PDF + Excel' + (po && po.label_required ? ' + Case Label' : '')
                + (emailFiles.length ? ' + ' + emailFiles.length + ' file' + (emailFiles.length === 1 ? '' : 's') : ''))
          ),
          React.createElement('input', {
            type: 'text', value: emailTo,
            onChange: function(e) { setEmailTo(e.target.value); },
            placeholder: 'recipient@example.com',
            style: { width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid ' + C.border, background: C.surface, color: C.text, fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box' }
          })
        ),
        // Template selector
        React.createElement('div', { style: { marginBottom: 10 } },
          React.createElement('label', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted, marginBottom: 4, display: 'block' } }, 'Template'),
          React.createElement('select', {
            value: selectedTemplateId || '',
            onChange: function(e) { loadEmailPreview(e.target.value ? Number(e.target.value) : null); },
            style: { width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid ' + C.border, background: C.surface, color: C.text, fontSize: 12, fontFamily: 'inherit' }
          },
            poEmailTemplates.map(function(tpl) {
              return React.createElement('option', { key: tpl.id, value: tpl.id }, tpl.name + (tpl.is_default ? ' (default)' : ''));
            })
          )
        ),
        // Editable subject
        emailPreview && React.createElement('div', { style: { marginBottom: 10 } },
          React.createElement('label', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted, marginBottom: 4, display: 'block' } }, 'Subject'),
          React.createElement('input', {
            type: 'text', value: emailSubject,
            onChange: function(e) { setEmailSubject(e.target.value); },
            style: { width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid ' + C.border, background: C.surface, color: C.text, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }
          })
        ),
        // Body label + formatting toolbar
        emailPreview && React.createElement('label', { style: { fontSize: 11, fontWeight: 500, color: C.textMuted, display: 'block', marginTop: 6, marginBottom: 4 } }, 'Body'),
        emailPreview && React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 2, padding: '5px 8px', background: C.bg, border: '1px solid ' + C.border, borderBottom: 'none', borderRadius: '8px 8px 0 0' } },
          React.createElement(FontSizeSelect, { targetRef: emailBodyRef, size: 'sm' }),
          React.createElement('div', { style: { width: 1, height: 18, background: C.border, margin: '0 3px', alignSelf: 'center' } }),
          [
            { cmd: 'bold', icon: 'bold', tip: 'Bold' },
            { cmd: 'italic', icon: 'italic', tip: 'Italic' },
            { cmd: 'underline', icon: 'underline', tip: 'Underline' },
            { divider: true },
            { cmd: 'hiliteColor', icon: 'highlighter', tip: 'Highlight', arg: '#FFEB3B' },
            { cmd: 'foreColor', icon: 'palette', tip: 'Red Text', arg: '#EF4444' },
            { cmd: 'removeFormat', icon: 'eraser', tip: 'Clear Formatting' },
            { divider: true },
            { cmd: 'formatBlock', icon: 'heading', tip: 'Heading', arg: 'h2' },
            { cmd: 'formatBlock', icon: 'pilcrow', tip: 'Paragraph', arg: 'p' },
            { divider: true },
            { cmd: 'insertUnorderedList', icon: 'list', tip: 'Bullet List' },
            { cmd: 'insertOrderedList', icon: 'list-ordered', tip: 'Numbered List' },
            { divider: true },
            { cmd: 'createLink', icon: 'link', tip: 'Insert Link' },
          ].map(function(btn, idx) {
            if (btn.divider) return React.createElement('div', { key: 'div-' + idx, style: { width: 1, height: 18, background: C.border, margin: '0 3px', alignSelf: 'center' } });
            return React.createElement('button', { key: btn.cmd + '-' + idx, title: btn.tip,
              onMouseDown: function(e) { e.preventDefault(); },
              onClick: async function() {
                if (emailBodyRef.current) emailBodyRef.current.focus();
                if (btn.cmd === 'createLink') {
                  // the in-app prompt takes focus (a browser prompt did not), so keep the editor's
                  // selection across it and put it back before the command (2026-09-29)
                  var sel = window.getSelection(), range = sel && sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
                  var url = (await appPrompt('Enter URL:'));
                  if (emailBodyRef.current) emailBodyRef.current.focus();
                  if (range && sel) { sel.removeAllRanges(); sel.addRange(range); }
                  if (url) document.execCommand('createLink', false, url);
                } else {
                  document.execCommand(btn.cmd, false, btn.arg || null);
                }
              },
              onMouseOver: function(e) { e.currentTarget.style.background = C.border; },
              onMouseOut: function(e) { e.currentTarget.style.background = 'transparent'; },
              style: { padding: '3px 6px', fontSize: 12, borderRadius: 4, border: 'none', background: 'transparent', color: C.text, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', transition: 'background 0.15s' }
            }, React.createElement(LIcon, { name: btn.icon, size: 13 }));
          })
        ),
        // Editable body (contentEditable)
        emailPreview && React.createElement('div', {
          key: 'email-body-' + emailTplKey,
          ref: emailBodyRef,
          contentEditable: true,
          suppressContentEditableWarning: true,
          dangerouslySetInnerHTML: { __html: (typeof DOMPurify !== 'undefined' ? DOMPurify.sanitize(emailPreview.html) : '') },
          style: { padding: 14, background: '#fff', borderRadius: '0 0 8px 8px', border: '1px solid ' + C.border, minHeight: 120, maxHeight: 'calc(calc(90vh * var(--invzoom, 1)) - 484px)', overflow: 'auto', fontSize: 13, color: '#333', fontFamily: 'Arial, sans-serif', outline: 'none', lineHeight: 1.6, boxSizing: 'border-box' }
        }),
        // Files the user adds, on top of the PO PDF / Excel / case label the server always sends.
        emailPreview && React.createElement('div', { style: { marginTop: 10 } },
          React.createElement('input', { ref: emailFileInputRef, type: 'file', multiple: true, style: { display: 'none' },
            accept: 'image/*,.pdf,.csv,.txt,.xls,.xlsx,.doc,.docx',
            onChange: function(e) { addEmailFiles(e.target.files); e.target.value = ''; } }),
          // A drop target the full width of the body box: click anywhere on it, or drop onto it.
          React.createElement('div', {
            onClick: function() { if (emailFileInputRef.current) emailFileInputRef.current.click(); },
            onDragOver: function(e) { e.preventDefault(); if (!emailDragOver) setEmailDragOver(true); },
            onDragLeave: function(e) { e.preventDefault(); setEmailDragOver(false); },
            onDrop: function(e) {
              e.preventDefault();
              setEmailDragOver(false);
              if (e.dataTransfer && e.dataTransfer.files) addEmailFiles(e.dataTransfer.files);
            },
            style: { display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: '100%', boxSizing: 'border-box', padding: '24px 12px', borderRadius: 8,
              border: '1px dashed ' + (emailDragOver ? C.accent : C.border),
              // Resting fill sits one step off the modal's C.surface so the drop target reads
              // as its own panel. C.surfaceHover is the right token in both themes: it lightens
              // on dark, and on light it goes the only direction white can.
              background: emailDragOver ? C.accentSoft : C.surfaceHover,
              color: emailDragOver ? C.accent : C.textMuted, fontSize: 11, cursor: 'pointer',
              transition: 'border-color 0.15s, background 0.15s, color 0.15s' }
          },
            // pointerEvents:none — otherwise dragging across the label fires dragleave on the
            // strip and the highlight flickers off mid-drag.
            React.createElement('span', { style: { display: 'inline-flex', alignItems: 'center', gap: 6, pointerEvents: 'none' } },
              React.createElement(LIcon, { name: 'paperclip', size: 12 }),
              emailDragOver ? 'Drop to attach' : 'Click or drag files here to attach'
            )
          ),
          emailFiles.length > 0 && React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 } },
            emailFiles.map(function(f, idx) {
              return React.createElement('span', { key: idx, style: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 8, background: C.surface, border: '1px solid ' + C.border, fontSize: 12, color: C.text, maxWidth: 220 } },
                React.createElement(LIcon, { name: /^image\//.test(f.type) ? 'image' : 'paperclip', size: 12 }),
                React.createElement('span', { style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, f.name),
                React.createElement('button', { onClick: function() { setEmailFiles(emailFiles.filter(function(_, i) { return i !== idx; })); },
                  style: { background: 'none', border: 'none', color: C.textDim, cursor: 'pointer', padding: 0, display: 'inline-flex', alignItems: 'center' } }, React.createElement(LIcon, { name: 'x', size: 12 }))
              );
            })
          )
        ),
        !emailPreview && poEmailTemplates.length === 0 && React.createElement('div', { style: { padding: 24, textAlign: 'center', color: C.textMuted, fontSize: 13 } },
          'No email templates configured. Create one in Settings → Templates.'
        ),
        // Schedule option
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10, marginTop: 16, borderTop: '1px solid ' + C.border, paddingTop: 12 } },
          React.createElement('div', {
            onClick: function() { setScheduleMode(!scheduleMode); },
            onMouseOver: function(e) { e.currentTarget.style.background = C.bg; },
            onMouseOut: function(e) { e.currentTarget.style.background = 'transparent'; },
            style: { display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', userSelect: 'none', fontSize: 12, color: C.text, padding: '4px 8px', borderRadius: 6, transition: 'background 0.15s' }
          },
            React.createElement('div', { style: { width: 18, height: 18, borderRadius: 4, border: '2px solid ' + (scheduleMode ? C.accent : C.textDim), background: scheduleMode ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' } },
              scheduleMode && React.createElement(LIcon, { name: 'check', size: 12, color: '#fff' })
            ),
            React.createElement(LIcon, { name: 'clock', size: 14, color: scheduleMode ? C.accent : C.textMuted }), ' Schedule for later'
          ),
          scheduleMode && React.createElement('input', { type: 'datetime-local', value: scheduleDateTime,
            onChange: function(e) { setScheduleDateTime(e.target.value); },
            min: new Date(Date.now() + 60000).toISOString().slice(0, 16),
            style: { padding: '7px 10px', borderRadius: 6, border: '1px solid ' + C.border, background: C.bg, color: C.text, fontSize: 12, fontFamily: 'inherit', colorScheme: getThemeName() === 'dark' ? 'dark' : 'light' }
          })
        ),
        // Action buttons
        React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 } },
          React.createElement(Btn, { variant: 'danger', onClick: function() { setEmailModalOpen(false); setScheduleMode(false); setScheduleDateTime(''); setEmailFiles([]); setEmailDragOver(false); } }, 'Cancel'),
          React.createElement(Btn, { variant: 'primary', onClick: sendEmailWithTemplate, disabled: emailSending || (scheduleMode && !scheduleDateTime) },
            React.createElement(LIcon, { name: scheduleMode ? 'clock' : 'send', size: 14 }), emailSending ? (scheduleMode ? ' Scheduling...' : ' Sending...') : (scheduleMode ? ' Schedule Email' : ' Send Email'))
        )
      )
    ),
    // ─── Scheduled Emails Modal ───
    React.createElement(Modal, { open: showScheduledEmails, onClose: function() { setShowScheduledEmails(false); }, title: 'Scheduled Emails', width: 650 },
      React.createElement('div', null,
        scheduledEmails.length === 0
          ? React.createElement('div', { style: { padding: 24, textAlign: 'center', color: C.textMuted, fontSize: 13 } }, 'No pending scheduled emails.')
          : React.createElement('table', { style: { width: '100%', fontSize: 12, borderCollapse: 'collapse' } },
              React.createElement('thead', null,
                React.createElement('tr', { style: { borderBottom: '1px solid ' + C.border } },
                  React.createElement('th', { style: { padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase' } }, 'PO #'),
                  React.createElement('th', { style: { padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase' } }, 'Vendor'),
                  React.createElement('th', { style: { padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase' } }, 'To'),
                  React.createElement('th', { style: { padding: '8px 10px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase' } }, 'Scheduled For'),
                  React.createElement('th', { style: { padding: '8px 10px', width: 60 } })
                )
              ),
              React.createElement('tbody', null,
                scheduledEmails.map(function(se) {
                  return React.createElement('tr', { key: se.id, style: { borderBottom: '1px solid ' + C.border } },
                    React.createElement('td', { style: { padding: '8px 10px', fontFamily: 'inherit', fontWeight: 600, color: C.accent } }, se.po_number),
                    React.createElement('td', { style: { padding: '8px 10px', color: C.text } }, se.vendor_name || ''),
                    React.createElement('td', { style: { padding: '8px 10px', color: C.textMuted, fontSize: 11 } }, (se.recipient || '').split(',')[0].trim()),
                    React.createElement('td', { style: { padding: '8px 10px', color: C.text } }, new Date(se.scheduled_at).toLocaleString()),
                    React.createElement('td', { style: { padding: '8px 10px' } },
                      React.createElement('button', { onClick: function() { cancelScheduledEmail(se.id); },
                        style: { background: 'none', border: 'none', color: C.danger, cursor: 'pointer', fontSize: 11, fontFamily: 'inherit', padding: '2px 6px' }
                      }, React.createElement(LIcon, { name: 'x', size: 14 }))
                    )
                  );
                })
              )
            )
      )
    ),
    // ─── Transparency Labels Modal (manage view, or review view after an upload) ───
    React.createElement(Modal, {
      open: labelModalOpen,
      onClose: function() { if (labelPreview) cancelLabelPreview(); setLabelModalOpen(false); },
      title: labelPreview ? 'Review Label Upload — PO ' + (po ? po.po_number : '') : 'Transparency Labels — PO ' + (po ? po.po_number : ''),
      width: 680
    },
      (function() {
        var lineOptions = (po.line_items || []).map(function(li) {
          var t = li.vendor_title || '';
          if (t.length > 58) t = t.slice(0, 55) + '…';
          return { value: li.id, label: li.sku + (t ? ' — ' + t : '') };
        });
        var makeBadge = function(cnt, qty) {
          if (cnt != null && qty != null) {
            var ok = Number(cnt) === Number(qty);
            return React.createElement('span', { title: cnt + ' labels vs ' + qty + ' units ordered', style: { padding: '1px 6px', borderRadius: 99, fontSize: 10, fontWeight: 700, background: (ok ? '#10B981' : '#F59E0B') + '22', color: ok ? '#10B981' : '#F59E0B', whiteSpace: 'nowrap' } }, cnt + '/' + qty);
          }
          if (cnt != null) return React.createElement('span', { style: { padding: '1px 6px', borderRadius: 99, fontSize: 10, fontWeight: 600, background: C.surface, color: C.textMuted, whiteSpace: 'nowrap' } }, cnt + ' labels');
          return null;
        };

        // ── REVIEW VIEW: uploaded files awaiting confirmation ──
        if (labelPreview) {
          var liById = {};
          (po.line_items || []).forEach(function(li) { liById[li.id] = li; });
          var reassignPreview = function(idx, v) {
            setLabelPreview(function(prev) {
              var files = prev.files.slice();
              var li = v ? liById[v] : null;
              files[idx] = Object.assign({}, files[idx], { line_item_id: v, matched_sku: li ? li.sku : null, qty_ordered: li ? li.qty_ordered : null });
              return Object.assign({}, prev, { files: files });
            });
          };
          var matchedRows = [], unmatchedRows = [];
          labelPreview.files.forEach(function(f, idx) { (f.line_item_id ? matchedRows : unmatchedRows).push({ f: f, idx: idx }); });
          var willMark = labelFlagTargets(labelPreview.files);
          var previewRow = function(x) {
            var f = x.f;
            return React.createElement('div', { key: x.idx, style: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, padding: '4px 0', borderBottom: '1px solid ' + C.border + '33' } },
              React.createElement('span', { title: f.original_name, style: { fontWeight: 600, color: C.text, maxWidth: 210, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, f.original_name),
              React.createElement('span', { style: { color: C.textDim } }, '→'),
              React.createElement(Sel, { value: f.line_item_id || '', onChange: function(e) { reassignPreview(x.idx, e.target.value ? parseInt(e.target.value) : null); }, options: lineOptions }),
              f.match_confidence === 'low' && f.line_item_id && React.createElement('span', { title: 'Low-confidence auto-match — verify', style: { fontSize: 10, color: '#F59E0B', fontWeight: 700 } }, 'CHECK'),
              (f.duplicate_of || f.duplicate_in_batch) && React.createElement('span', {
                title: f.duplicate_of
                  ? 'This exact file was already uploaded to PO ' + f.duplicate_of.po_number + '. Transparency codes are single-use — it will be SKIPPED on save.'
                  : 'Identical to another file in this upload — it will be SKIPPED on save.',
                style: { fontSize: 10, color: C.danger, fontWeight: 700, padding: '1px 6px', borderRadius: 99, background: C.danger + '22', whiteSpace: 'nowrap' }
              }, 'DUPLICATE'),
              makeBadge(f.label_count, f.qty_ordered)
            );
          };
          return React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 12 } },
            React.createElement('div', { style: { display: 'flex', gap: 12 } },
              React.createElement('div', { style: { background: C.bgCard, border: '1px solid ' + C.border, borderRadius: 8, padding: '10px 16px', flex: 1, textAlign: 'center' } },
                React.createElement('div', { style: { fontSize: 20, fontWeight: 700, color: C.text } }, labelPreview.files.length),
                React.createElement('div', { style: { fontSize: 10, color: C.textDim, textTransform: 'uppercase' } }, 'Uploaded')
              ),
              React.createElement('div', { style: { background: C.bgCard, border: '1px solid ' + C.border, borderRadius: 8, padding: '10px 16px', flex: 1, textAlign: 'center' } },
                React.createElement('div', { style: { fontSize: 20, fontWeight: 700, color: '#10B981' } }, matchedRows.length),
                React.createElement('div', { style: { fontSize: 10, color: C.textDim, textTransform: 'uppercase' } }, 'Matched')
              ),
              React.createElement('div', { style: { background: C.bgCard, border: '1px solid ' + (unmatchedRows.length ? C.danger : C.border), borderRadius: 8, padding: '10px 16px', flex: 1, textAlign: 'center' } },
                React.createElement('div', { style: { fontSize: 20, fontWeight: 700, color: unmatchedRows.length ? C.danger : C.textDim } }, unmatchedRows.length),
                React.createElement('div', { style: { fontSize: 10, color: unmatchedRows.length ? C.danger : C.textDim, textTransform: 'uppercase' } }, 'Not Matched')
              )
            ),
            unmatchedRows.length > 0 && React.createElement('div', { style: { borderLeft: '3px solid ' + C.danger, paddingLeft: 14 } },
              React.createElement('div', { style: { fontSize: 12, fontWeight: 700, color: C.danger, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 } }, 'Not Matched (' + unmatchedRows.length + ') — assign a line or they save unassigned'),
              React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 160, overflowY: 'auto' } }, unmatchedRows.map(previewRow))
            ),
            matchedRows.length > 0 && React.createElement('div', { style: { borderLeft: '3px solid #10B981', paddingLeft: 14 } },
              React.createElement('div', { style: { fontSize: 12, fontWeight: 700, color: '#10B981', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 } }, 'Matched (' + matchedRows.length + ')'),
              React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 220, overflowY: 'auto' } }, matchedRows.map(previewRow))
            ),
            willMark.length > 0 && React.createElement('div', { title: 'Saving assigns these files and marks each product as requiring Transparency labels — every unit of these SKUs will need a serialized label at FBA packing', style: { background: '#8B5CF6' + '14', border: '1px solid #8B5CF6' + '40', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: C.text, display: 'flex', gap: 8, alignItems: 'flex-start', lineHeight: 1.45 } },
              React.createElement(LIcon, { name: 'shield-check', size: 14, color: '#8B5CF6', style: { flexShrink: 0, marginTop: 2 } }),
              React.createElement('div', null,
                React.createElement('span', { style: { fontWeight: 700, color: '#8B5CF6' } }, 'Will mark ' + willMark.length + ' SKU' + (willMark.length === 1 ? '' : 's') + ' as Transparency on save: '),
                willMark.join(', ')
              )
            ),
            (labelPreview.uncovered || []).length > 0 && React.createElement('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: '8px 12px', fontSize: 11, color: C.textMuted } },
              'Transparency lines not covered by this upload: ',
              labelPreview.uncovered.map(function(u) { return u.sku + ' (' + u.qty_ordered + ')'; }).join(', ')
            ),
            React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8, borderTop: '1px solid ' + C.border, paddingTop: 14 } },
              React.createElement(Btn, { onClick: cancelLabelPreview, disabled: labelBusy }, 'Cancel'),
              React.createElement(Btn, { variant: 'primary', onClick: saveLabelFiles, disabled: labelBusy },
                labelBusy ? 'Saving...' : 'Save ' + labelPreview.files.length + ' File' + (labelPreview.files.length === 1 ? '' : 's'))
            )
          );
        }

        // ── MANAGE VIEW: files already saved on this PO ──
        var lf = labelFiles || { files: [], missing: [] };
        return React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 12 } },
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 } },
            React.createElement('div', { style: { fontSize: 12, color: C.textDim, lineHeight: 1.5 } }, 'Vendor-supplied per-SKU PDF sheets — one label per page. Uploads open a review step before anything saves. Assigning a file to a SKU marks that product as Transparency.'),
            React.createElement(Btn, { size: 'sm', disabled: labelBusy, onClick: function() { labelFileRef.current && labelFileRef.current.click(); } },
              React.createElement(LIcon, { name: labelBusy ? 'loader-2' : 'upload', size: 14, spin: labelBusy }), ' ', labelBusy ? 'Parsing...' : 'Upload PDFs')
          ),
          (lf.missing || []).length > 0 && React.createElement('div', { style: { background: C.dangerSoft, border: '1px solid ' + C.danger + '33', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: C.danger } },
            'Labels missing for ' + lf.missing.length + ' Transparency SKU' + (lf.missing.length === 1 ? '' : 's') + ': ',
            lf.missing.map(function(m) { return m.sku + ' (' + m.qty_ordered + ')'; }).join(', ')
          ),
          lf.files.length === 0
            ? React.createElement('div', { style: { fontSize: 12, color: C.textDim, padding: '12px 0', textAlign: 'center' } }, 'No label files uploaded yet.')
            : React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 360, overflowY: 'auto' } },
                lf.files.map(function(f) {
                  return React.createElement('div', { key: f.id, style: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, padding: '4px 0', borderBottom: '1px solid ' + C.border + '33' } },
                    React.createElement('a', { href: '/api/purchase-orders/' + poId + '/label-files/' + f.id + '/download', target: '_blank', title: 'Open for printing', style: { color: C.accent, textDecoration: 'none', fontWeight: 600, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, f.original_name),
                    React.createElement('span', { style: { color: C.textDim } }, '→'),
                    React.createElement(Sel, {
                      value: f.line_item_id || '',
                      onChange: function(e) { reassignLabelFile(f.id, e.target.value ? parseInt(e.target.value) : null); },
                      options: lineOptions
                    }),
                    f.match_confidence === 'low' && React.createElement('span', { title: 'Low-confidence auto-match — verify the assignment', style: { fontSize: 10, color: '#F59E0B', fontWeight: 700 } }, 'CHECK'),
                    makeBadge(f.label_count, f.qty_ordered),
                    React.createElement('button', { onClick: function() { deleteLabelFile(f.id); }, title: 'Remove file',
                      style: { background: 'none', border: 'none', color: C.textDim, cursor: 'pointer', padding: 2, marginLeft: 'auto', display: 'flex' } }, React.createElement(LIcon, { name: 'x', size: 14 }))
                  );
                })
              )
        );
      })()
    ),

    // ─── Vendor Confirmation Import Modal ───
    confirmLoading && React.createElement(Modal, { open: true, title: 'Analyzing Document...', width: 400 },
      React.createElement('div', { style: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: 20 } },
        React.createElement(LIcon, { name: 'loader', size: 28, color: C.accent, spin: true }),
        React.createElement('div', { style: { fontSize: 13, color: C.textMuted } }, 'Extracting line items with AI...')
      )
    ),
    React.createElement(Modal, { open: !!confirmPreview && !confirmLoading, onClose: function() { setConfirmPreview(null); }, title: 'Vendor Confirmation Review', width: 700 },
      confirmPreview && React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 16 } },
        React.createElement('div', { style: { fontSize: 12, color: C.textMuted } }, 'File: ' + confirmPreview.filename + ' \u2014 ' + confirmPreview.extracted_count + ' line items extracted'),
        // The file held invoices for more than one PO. Those lines were dropped
        // before matching - say so, or the counts look inexplicably short.
        confirmPreview.excluded_other_po && React.createElement('div', { style: { border: '1px solid ' + C.warning, background: C.warning + '14', borderRadius: 8, padding: '10px 12px', display: 'flex', gap: 10, alignItems: 'flex-start' } },
          React.createElement(LIcon, { name: 'info', size: 18, color: C.warning }),
          React.createElement('div', { style: { flex: 1, fontSize: 12, fontWeight: 600, color: C.warning, lineHeight: 1.5 } },
            'This file also covers PO ' + confirmPreview.excluded_other_po.po_numbers.join(', ') + ' \u2014 ' + confirmPreview.excluded_other_po.count + ' line item' + (confirmPreview.excluded_other_po.count === 1 ? ' was' : 's were') + ' excluded. Import the file again on ' + (confirmPreview.excluded_other_po.po_numbers.length === 1 ? 'that PO' : 'those POs') + ' to apply them there.'
          )
        ),
        confirmPreview.warning && React.createElement('div', { style: { border: '1px solid ' + C.danger, background: C.danger + '14', borderRadius: 8, padding: '10px 12px', display: 'flex', gap: 10, alignItems: 'flex-start' } },
          React.createElement(LIcon, { name: 'alert-triangle', size: 18, color: C.danger }),
          React.createElement('div', { style: { flex: 1, fontSize: 12, fontWeight: 600, color: C.danger, lineHeight: 1.5 } }, confirmPreview.warning)
        ),
        // Matched items with changes
        (confirmPreview.matched || []).filter(function(m) { return m.changes.length > 0; }).length > 0 && React.createElement('div', null,
          React.createElement('div', { style: { fontSize: 12, fontWeight: 700, color: C.warning, marginBottom: 8, textTransform: 'uppercase' } }, 'Updates (' + (confirmPreview.matched || []).filter(function(m) { return m.changes.length > 0; }).length + ')'),
          React.createElement('div', { style: { border: '1px solid ' + C.border, borderRadius: 8, overflow: 'hidden' } },
            React.createElement('table', { style: { width: '100%', borderCollapse: 'collapse', fontSize: 12 } },
              React.createElement('thead', null,
                React.createElement('tr', { style: { background: C.surface } },
                  React.createElement('th', { style: { padding: '6px 10px', textAlign: 'left', width: 30 } }),
                  React.createElement('th', { style: { padding: '6px 10px', textAlign: 'left', color: C.textMuted } }, 'SKU'),
                  React.createElement('th', { style: { padding: '6px 10px', textAlign: 'left', color: C.textMuted } }, 'Vendor SKU'),
                  React.createElement('th', { style: { padding: '6px 10px', textAlign: 'right', color: C.textMuted } }, 'Qty'),
                  React.createElement('th', { style: { padding: '6px 10px', textAlign: 'right', color: C.textMuted } }, 'Unit Cost')
                )
              ),
              React.createElement('tbody', null,
                (function() { var allKeys = (confirmPreview.matched || []).filter(function(m) { return m.changes.length > 0; }).map(function(m) { return 'update_' + m.po_line_id; }); return (confirmPreview.matched || []).filter(function(m) { return m.changes.length > 0; }).map(function(m, idx) {
                  var key = 'update_' + m.po_line_id;
                  var checked = !!confirmChecked[key];
                  return React.createElement('tr', { key: key, style: { borderTop: '1px solid ' + C.border } },
                    React.createElement('td', { style: { padding: '6px 10px' } },
                      React.createElement('div', {
                        onMouseDown: function(e) { if (e.shiftKey) e.preventDefault(); },
                        onClick: function(e) {
                          e.stopPropagation();
                          var next = Object.assign({}, confirmChecked);
                          if (e.shiftKey && confirmLastClickRef.current !== null) {
                            var lastIdx = allKeys.indexOf(confirmLastClickRef.current);
                            if (lastIdx !== -1) {
                              var from = Math.min(lastIdx, idx), to = Math.max(lastIdx, idx);
                              var val = !checked;
                              for (var si = from; si <= to; si++) next[allKeys[si]] = val;
                            }
                          } else { next[key] = !checked; }
                          confirmLastClickRef.current = key;
                          setConfirmChecked(next);
                        },
                        style: { width: 28, height: 28, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', userSelect: 'none' }
                      },
                        React.createElement('div', { style: { width: 18, height: 18, borderRadius: 4, border: '2px solid ' + (checked ? C.accent : C.textDim), background: checked ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' } },
                          checked && React.createElement(LIcon, { name: 'check', size: 12, color: '#fff' })
                        )
                      )
                    ),
                    React.createElement('td', { style: { padding: '6px 10px', color: C.text, fontFamily: 'inherit', fontWeight: 600 } }, m.sku),
                    React.createElement('td', { style: { padding: '6px 10px', color: C.textMuted } }, m.vendor_sku,
                      m.case_pack ? React.createElement('span', { style: { marginLeft: 6, fontSize: 10, color: C.accent, whiteSpace: 'nowrap' } }, m.doc_qty + ' cs × ' + m.case_pack) : null
                    ),
                    React.createElement('td', { style: { padding: '6px 10px', textAlign: 'right' } },
                      m.changes.includes('qty')
                        ? React.createElement('span', null,
                            React.createElement('span', { style: { color: C.danger, textDecoration: 'line-through' } }, m.current_qty),
                            ' \u2192 ',
                            React.createElement('span', { style: { color: C.success, fontWeight: 600 } }, m.new_qty)
                          )
                        : React.createElement('span', { style: { color: C.textMuted } }, m.current_qty)
                    ),
                    React.createElement('td', { style: { padding: '6px 10px', textAlign: 'right' } },
                      m.changes.includes('cost')
                        ? React.createElement('span', null,
                            React.createElement('span', { style: { color: C.danger, textDecoration: 'line-through' } }, '$' + m.current_cost.toFixed(2)),
                            ' \u2192 ',
                            React.createElement('span', { style: { color: C.success, fontWeight: 600 } }, '$' + m.new_cost.toFixed(2))
                          )
                        : React.createElement('span', { style: { color: C.textMuted } }, '$' + m.current_cost.toFixed(2))
                    )
                  );
                }); })()
              )
            )
          )
        ),
        // Matched items with no changes
        (confirmPreview.matched || []).filter(function(m) { return m.changes.length === 0; }).length > 0 && React.createElement('div', null,
          React.createElement('div', { style: { fontSize: 12, fontWeight: 700, color: C.success, marginBottom: 4, textTransform: 'uppercase' } }, 'No Changes (' + (confirmPreview.matched || []).filter(function(m) { return m.changes.length === 0; }).length + ')'),
          React.createElement('div', { style: { fontSize: 11, color: C.textDim } },
            (confirmPreview.matched || []).filter(function(m) { return m.changes.length === 0; }).map(function(m) { return m.sku + (m.case_pack ? ' (' + m.doc_qty + ' cs × ' + m.case_pack + ')' : ''); }).join(', ')
          )
        ),
        // Items not on confirmation → move to Next PO
        (confirmPreview.not_on_confirmation || []).length > 0 && React.createElement('div', null,
          React.createElement('div', { style: { fontSize: 12, fontWeight: 700, color: C.danger, marginBottom: 8, textTransform: 'uppercase' } }, 'Not on Confirmation \u2014 Move to Draft PO (' + confirmPreview.not_on_confirmation.length + ')'),
          React.createElement('div', { style: { border: '1px solid ' + C.border, borderRadius: 8, overflow: 'hidden' } },
            React.createElement('table', { style: { width: '100%', borderCollapse: 'collapse', fontSize: 12 } },
              React.createElement('thead', null,
                React.createElement('tr', { style: { background: C.surface } },
                  React.createElement('th', { style: { padding: '6px 10px', textAlign: 'left', width: 30 } }),
                  React.createElement('th', { style: { padding: '6px 10px', textAlign: 'left', color: C.textMuted } }, 'SKU'),
                  React.createElement('th', { style: { padding: '6px 10px', textAlign: 'left', color: C.textMuted } }, 'Vendor SKU'),
                  React.createElement('th', { style: { padding: '6px 10px', textAlign: 'right', color: C.textMuted } }, 'Qty'),
                  React.createElement('th', { style: { padding: '6px 10px', textAlign: 'right', color: C.textMuted } }, 'Cost')
                )
              ),
              React.createElement('tbody', null,
                (function() { var allKeys = confirmPreview.not_on_confirmation.map(function(m) { return 'move_' + m.po_line_id; }); return confirmPreview.not_on_confirmation.map(function(m, idx) {
                  var key = 'move_' + m.po_line_id;
                  var checked = !!confirmChecked[key];
                  return React.createElement('tr', { key: key, style: { borderTop: '1px solid ' + C.border } },
                    React.createElement('td', { style: { padding: '6px 10px' } },
                      React.createElement('div', {
                        onMouseDown: function(e) { if (e.shiftKey) e.preventDefault(); },
                        onClick: function(e) {
                          e.stopPropagation();
                          var next = Object.assign({}, confirmChecked);
                          if (e.shiftKey && confirmLastClickRef.current !== null) {
                            var lastIdx = allKeys.indexOf(confirmLastClickRef.current);
                            if (lastIdx !== -1) {
                              var from = Math.min(lastIdx, idx), to = Math.max(lastIdx, idx);
                              var val = !checked;
                              for (var si = from; si <= to; si++) next[allKeys[si]] = val;
                            }
                          } else { next[key] = !checked; }
                          confirmLastClickRef.current = key;
                          setConfirmChecked(next);
                        },
                        style: { width: 28, height: 28, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', userSelect: 'none' }
                      },
                        React.createElement('div', { style: { width: 18, height: 18, borderRadius: 4, border: '2px solid ' + (checked ? C.accent : C.textDim), background: checked ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' } },
                          checked && React.createElement(LIcon, { name: 'check', size: 12, color: '#fff' })
                        )
                      )
                    ),
                    React.createElement('td', { style: { padding: '6px 10px', color: C.text, fontFamily: 'inherit', fontWeight: 600 } }, m.sku,
                      m.zero_qty && React.createElement('span', { title: 'This item DID appear on the document but parsed with quantity 0 — verify against the PDF before moving it off the PO', style: { marginLeft: 6, fontSize: 10, fontWeight: 700, color: C.warning, background: C.warning + '22', borderRadius: 4, padding: '1px 5px', verticalAlign: 'middle', fontFamily: 'inherit' } }, 'QTY 0 ON DOC')
                    ),
                    React.createElement('td', { style: { padding: '6px 10px', color: C.textMuted } }, m.vendor_sku || '—'),
                    React.createElement('td', { style: { padding: '6px 10px', textAlign: 'right', color: C.textMuted } }, m.qty_ordered),
                    React.createElement('td', { style: { padding: '6px 10px', textAlign: 'right', color: C.textMuted } }, '$' + m.unit_cost.toFixed(2))
                  );
                }); })()
              )
            )
          )
        ),
        // Unmatched vendor lines (informational)
        (confirmPreview.unmatched_vendor_lines || []).length > 0 && React.createElement('div', null,
          React.createElement('div', { style: { fontSize: 12, fontWeight: 700, color: C.textDim, marginBottom: 4, textTransform: 'uppercase' } }, 'Unmatched Vendor Items (' + confirmPreview.unmatched_vendor_lines.length + ')'),
          React.createElement('div', { style: { fontSize: 11, color: C.textDim, lineHeight: 1.6 } },
            confirmPreview.unmatched_vendor_lines.map(function(u) { return u.vendor_item + ' (qty: ' + u.quantity + ', $' + u.unit_cost.toFixed(2) + ')'; }).join(', ')
          )
        ),
        // Action buttons
        React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 } },
          React.createElement(Btn, { onClick: function() { setConfirmPreview(null); } }, 'Cancel'),
          React.createElement(Btn, { variant: 'primary', onClick: applyVendorConfirmation, disabled: confirmApplying }, confirmApplying ? 'Applying...' : 'Apply Changes')
        )
      )
    ),
    // ─── CSV Upload Preview Modal ───
    React.createElement(Modal, { open: csvPreviewOpen, onClose: function() { setCsvPreviewOpen(false); setCsvPreviewData(null); }, title: 'CSV Upload Summary', width: 520 },
      csvPreviewData && React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 16 } },
        // Kit resolutions
        csvPreviewData.kitResolutions && csvPreviewData.kitResolutions.length > 0 && React.createElement('div', { style: { background: C.accentSoft, border: '1px solid ' + C.accent + '33', borderRadius: 8, padding: '12px 14px' } },
          React.createElement('div', { style: { fontSize: 12, fontWeight: 700, color: C.accent, marginBottom: 8 } }, 'Kit SKUs Resolved (' + csvPreviewData.kitResolutions.length + ')'),
          React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 6 } },
            csvPreviewData.kitResolutions.map(function(kr) {
              return React.createElement('div', { key: kr.original_sku, style: { fontSize: 12, color: C.text } },
                React.createElement('span', { style: { fontFamily: 'inherit', fontWeight: 500 } }, kr.original_sku),
                React.createElement('span', { style: { color: C.textMuted } }, ' \u2192 '),
                React.createElement('span', { style: { fontFamily: 'inherit', fontWeight: 500 } }, kr.resolved_sku),
                React.createElement('span', { style: { color: C.textMuted, marginLeft: 8 } }, kr.original_qty + ' \u00D7 ' + kr.kit_qty + ' = ' + kr.resolved_qty + ' units')
              );
            })
          )
        ),
        // Alias resolutions
        csvPreviewData.aliasResolutions && csvPreviewData.aliasResolutions.length > 0 && React.createElement('div', { style: { background: '#FF990012', border: '1px solid #FF990033', borderRadius: 8, padding: '12px 14px' } },
          React.createElement('div', { style: { fontSize: 12, fontWeight: 700, color: '#FF9900', marginBottom: 8 } }, 'Alias SKUs Resolved (' + csvPreviewData.aliasResolutions.length + ')'),
          React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 6 } },
            csvPreviewData.aliasResolutions.map(function(ar) {
              return React.createElement('div', { key: ar.original_sku, style: { fontSize: 12, color: C.text } },
                React.createElement('span', { style: { fontFamily: 'inherit', fontWeight: 500 } }, ar.original_sku),
                React.createElement('span', { style: { color: C.textMuted } }, ' \u2192 '),
                React.createElement('span', { style: { fontFamily: 'inherit', fontWeight: 500 } }, ar.resolved_sku)
              );
            })
          )
        ),
        // Added section
        csvPreviewData.added.length > 0 && React.createElement('div', { style: { borderLeft: '3px solid ' + C.success, paddingLeft: 14 } },
          React.createElement('div', { style: { fontSize: 12, fontWeight: 700, color: C.success, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 } }, 'Added (' + csvPreviewData.added.length + ')'),
          React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4 } },
            csvPreviewData.added.map(function(it) {
              return React.createElement('div', { key: it.sku, style: { display: 'flex', justifyContent: 'space-between', fontSize: 13, color: C.text, padding: '3px 0' } },
                React.createElement('span', { style: { fontFamily: 'inherit', fontWeight: 500 } }, it.sku),
                React.createElement('span', { style: { color: C.textMuted } }, 'qty: ' + it.qty)
              );
            })
          )
        ),
        // Updated section
        csvPreviewData.updated.length > 0 && React.createElement('div', { style: { borderLeft: '3px solid ' + C.warning, paddingLeft: 14 } },
          React.createElement('div', { style: { fontSize: 12, fontWeight: 700, color: C.warning, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 } }, 'Updated (' + csvPreviewData.updated.length + ')'),
          React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4 } },
            csvPreviewData.updated.map(function(it) {
              var changes = [];
              if (it.newQty !== null) changes.push('qty: ' + it.oldQty + ' \u2192 ' + it.newQty);
              if (it.newCost !== null) changes.push('cost: $' + (it.oldCost != null ? it.oldCost : '—') + ' \u2192 $' + it.newCost);
              if (it.newBin !== undefined) changes.push('bin: ' + (it.oldBin || 'auto') + ' → ' + (it.newBin || 'auto'));
              return React.createElement('div', { key: it.sku, style: { display: 'flex', justifyContent: 'space-between', fontSize: 13, color: C.text, padding: '3px 0' } },
                React.createElement('span', { style: { fontFamily: 'inherit', fontWeight: 500 } }, it.sku),
                React.createElement('span', { style: { color: C.textMuted } }, changes.join('  ·  '))
              );
            })
          )
        ),
        // Removed section
        csvPreviewData.removed.length > 0 && React.createElement('div', { style: { borderLeft: '3px solid ' + C.danger, paddingLeft: 14 } },
          React.createElement('div', { style: { fontSize: 12, fontWeight: 700, color: C.danger, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 } }, 'Removed (' + csvPreviewData.removed.length + ')'),
          React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4 } },
            csvPreviewData.removed.map(function(it) {
              return React.createElement('div', { key: it.sku, style: { display: 'flex', justifyContent: 'space-between', fontSize: 13, color: C.text, padding: '3px 0' } },
                React.createElement('span', { style: { fontFamily: 'inherit', fontWeight: 500 } }, it.sku),
                React.createElement('span', { style: { color: C.textMuted } }, 'qty: ' + it.qty)
              );
            })
          )
        ),
        // Case qty warnings
        csvPreviewData.warnings && csvPreviewData.warnings.length > 0 && React.createElement('div', { style: { background: C.warningSoft, border: '1px solid ' + C.warning + '33', borderRadius: 8, padding: '12px 14px' } },
          React.createElement('div', { style: { fontSize: 12, fontWeight: 700, color: C.warning, marginBottom: 8 } }, 'Case Quantity Warnings'),
          React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 6 } },
            csvPreviewData.warnings.map(function(w) {
              return React.createElement('div', { key: w.sku, style: { fontSize: 13, color: C.text } },
                React.createElement('span', { style: { fontFamily: 'inherit', fontWeight: 500 } }, w.sku),
                React.createElement('span', { style: { color: C.textMuted } }, ' — ' + w.qty + ' units is not a full case (' + w.upc + '/case). Round to ' + w.roundDown + ' or ' + w.roundUp + '.')
              );
            })
          )
        ),
        // Unchanged note
        csvPreviewData.unchanged > 0 && React.createElement('div', { style: { fontSize: 13, color: C.textDim, fontStyle: 'italic' } }, csvPreviewData.unchanged + ' item' + (csvPreviewData.unchanged !== 1 ? 's' : '') + ' unchanged'),
        // Footer buttons
        React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8, borderTop: '1px solid ' + C.border, paddingTop: 16 } },
          React.createElement(Btn, { onClick: function() { setCsvPreviewOpen(false); setCsvPreviewData(null); } }, 'Cancel'),
          React.createElement(Btn, { variant: 'primary', onClick: confirmCsvUpload }, 'Confirm Upload')
        )
      )
    ),
    React.createElement(Modal, { open: aCsvPreviewOpen, onClose: function() { setACsvPreviewOpen(false); setACsvPreviewData(null); setACsvPendingFile(null); }, title: 'Analysis CSV — Review Changes', width: 600 },
      aCsvPreviewData && React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 16 } },
        // Summary
        React.createElement('div', { style: { display: 'flex', gap: 12 } },
          React.createElement('div', { style: { background: C.bgCard, border: '1px solid ' + C.border, borderRadius: 8, padding: '10px 16px', flex: 1, textAlign: 'center' } },
            React.createElement('div', { style: { fontSize: 20, fontWeight: 700, color: C.warning } }, aCsvPreviewData.changes.length),
            React.createElement('div', { style: { fontSize: 10, color: C.textDim, textTransform: 'uppercase' } }, 'Changes')
          ),
          React.createElement('div', { style: { background: C.bgCard, border: '1px solid ' + C.border, borderRadius: 8, padding: '10px 16px', flex: 1, textAlign: 'center' } },
            React.createElement('div', { style: { fontSize: 20, fontWeight: 700, color: C.danger } }, aCsvPreviewData.removals.length),
            React.createElement('div', { style: { fontSize: 10, color: C.textDim, textTransform: 'uppercase' } }, 'Move to Draft')
          ),
          aCsvPreviewData.invalids && aCsvPreviewData.invalids.length > 0 && React.createElement('div', { style: { background: C.dangerSoft, border: '1px solid ' + C.danger, borderRadius: 8, padding: '10px 16px', flex: 1, textAlign: 'center' } },
            React.createElement('div', { style: { fontSize: 20, fontWeight: 700, color: C.danger } }, aCsvPreviewData.invalids.length),
            React.createElement('div', { style: { fontSize: 10, color: C.danger, textTransform: 'uppercase' } }, 'Invalid')
          )
        ),
        // Changes section
        aCsvPreviewData.changes.length > 0 && React.createElement('div', { style: { borderLeft: '3px solid ' + C.warning, paddingLeft: 14 } },
          React.createElement('div', { style: { fontSize: 12, fontWeight: 700, color: C.warning, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 } }, 'Field Changes (' + aCsvPreviewData.changes.length + ')'),
          React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 200, overflowY: 'auto' } },
            aCsvPreviewData.changes.map(function(ch, idx) {
              return React.createElement('div', { key: idx, style: { display: 'flex', justifyContent: 'space-between', fontSize: 13, color: C.text, padding: '3px 0' } },
                React.createElement('span', null,
                  React.createElement('span', { style: { fontFamily: 'inherit', fontWeight: 500 } }, ch.sku),
                  React.createElement('span', { style: { color: C.textDim, marginLeft: 8 } }, ch.field)
                ),
                React.createElement('span', { style: { color: C.textMuted, fontFamily: 'inherit' } }, ch.old_val + ' \u2192 ' + ch.new_val)
              );
            })
          )
        ),
        // Removals section
        aCsvPreviewData.removals.length > 0 && React.createElement('div', { style: { borderLeft: '3px solid ' + C.danger, paddingLeft: 14 } },
          React.createElement('div', { style: { fontSize: 12, fontWeight: 700, color: C.danger, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 } }, 'Move to New Draft PO (' + aCsvPreviewData.removals.length + ')'),
          React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 200, overflowY: 'auto' } },
            aCsvPreviewData.removals.map(function(r) {
              return React.createElement('div', { key: r.line_item_id, style: { display: 'flex', justifyContent: 'space-between', fontSize: 13, color: C.text, padding: '3px 0' } },
                React.createElement('span', { style: { fontFamily: 'inherit', fontWeight: 500 } }, r.sku),
                React.createElement('span', { style: { color: C.textMuted } }, 'qty: ' + r.qty_ordered + '  ·  $' + Number(r.unit_cost || 0).toFixed(2))
              );
            })
          )
        ),
        // Invalid values — these BLOCK the import (server rejects the file too)
        aCsvPreviewData.invalids && aCsvPreviewData.invalids.length > 0 && React.createElement('div', { style: { background: C.dangerSoft, border: '1px solid ' + C.danger, borderRadius: 8, padding: '12px 14px' } },
          React.createElement('div', { style: { fontSize: 12, fontWeight: 700, color: C.danger, marginBottom: 4 } }, 'Invalid Values — fix the file to continue'),
          React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginBottom: 6 } }, 'These cells are not numbers (usually Excel #N/A / #REF! leftovers). Nothing can be imported until the file is fixed and re-uploaded.'),
          React.createElement('div', { style: { maxHeight: 140, overflowY: 'auto' } },
            aCsvPreviewData.invalids.map(function(e, i) {
              return React.createElement('div', { key: i, style: { fontSize: 12, color: C.danger, fontFamily: 'inherit', padding: '1px 0' } }, e);
            })
          )
        ),
        // Errors
        aCsvPreviewData.errors && aCsvPreviewData.errors.length > 0 && React.createElement('div', { style: { background: C.dangerSoft, border: '1px solid ' + C.danger + '33', borderRadius: 8, padding: '12px 14px' } },
          React.createElement('div', { style: { fontSize: 12, fontWeight: 700, color: C.danger, marginBottom: 4 } }, 'Warnings'),
          aCsvPreviewData.errors.map(function(e, i) {
            return React.createElement('div', { key: i, style: { fontSize: 12, color: C.danger } }, e);
          })
        ),
        // Footer — Apply is gated while the file has invalid cells
        React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8, borderTop: '1px solid ' + C.border, paddingTop: 16 } },
          React.createElement(Btn, { onClick: function() { setACsvPreviewOpen(false); setACsvPreviewData(null); setACsvPendingFile(null); } }, 'Cancel'),
          (function() {
            var gated = aCsvPreviewData.invalids && aCsvPreviewData.invalids.length > 0;
            return React.createElement('span', { title: gated ? 'The file has invalid values — fix it and upload again' : '' },
              React.createElement(Btn, { variant: 'primary', onClick: handleAnalysisCsvConfirm, disabled: gated || analysisFetching === 'csv-import' },
                analysisFetching === 'csv-import' ? 'Applying...' : gated ? 'Fix File to Apply' : 'Apply Changes'));
          })()
        )
      )
    ),
    locationModalOpen && React.createElement(LocationPickerModal, {
      title: 'Check In — PO #' + (po ? po.po_number : ''),
      onConfirm: handleCheckInConfirm,
      onCancel: function() { setLocationModalOpen(false); }
    })
  );
}

// ─── LOGIN PAGE ───
// ─── RECEIVING LIST PAGE ───
// Rows of the Receiving list for a status tab + search text (Anthony 2026-10-20: "when a user
// searches a PO# using the All filter it bounces/resets after finding the PO"). The item search
// (GET /api/receiving/search-items: POs holding a SKU / vendor title / vendor SKU / FNSKU / barcode
// that CONTAINS the text) used to REPLACE the list the moment it answered, so a PO number that is
// also a substring of some barcode vanished ~400 ms after it appeared — and on "All" the item
// search spans every PO ever, so a 5-digit PO number nearly always hit something.
// Now the header matches (PO #, vendor, warehouse) always stay; item hits are appended after them,
// deduped by id, and when the text IS a PO number in the list (exact match) the item hits are
// dropped — the user asked for that PO, not for barcodes containing its digits.
// Both sets obey the status tab; on "All" a closed PO only surfaces while searching.
// Returns { rows, itemExtra } — itemExtra = the item hits that were added beyond the header matches.
function receivingListRows(pos, itemSearchResults, filter, search) {
  var recvStatuses = { shipped: 1, 'checked-in': 1, receiving: 1, received: 1 };
  var s = String(search || '').trim().toLowerCase();
  var tabAllows = function(o) {
    if (filter) return o.status === filter;
    if (recvStatuses[o.status]) return true;
    return o.status === 'closed' && !!s;
  };
  var headerMatch = function(o) {
    if (!s) return true;
    return !!((o.po_number && String(o.po_number).toLowerCase().includes(s)) ||
              (o.vendor_name && String(o.vendor_name).toLowerCase().includes(s)) ||
              (o.wh_code && String(o.wh_code).toLowerCase().includes(s)));
  };
  var rows = (Array.isArray(pos) ? pos : []).filter(function(o) { return tabAllows(o) && headerMatch(o); });
  var exactPo = !!s && rows.some(function(o) { return o.po_number && String(o.po_number).toLowerCase() === s; });
  var itemExtra = [];
  if (!exactPo && Array.isArray(itemSearchResults) && itemSearchResults.length) {
    var seen = {};
    rows.forEach(function(o) { seen[o.id] = true; });
    itemSearchResults.forEach(function(o) {
      if (!o || seen[o.id] || !tabAllows(o)) return;
      seen[o.id] = true;
      itemExtra.push(o);
    });
  }
  return { rows: rows.concat(itemExtra), itemExtra: itemExtra };
}
window.receivingListRows = receivingListRows;

function ReceivingPage({ showToast, onNavigate }) {
  const [pos, setPos] = useState([]);
  var _initUrl = new URLSearchParams(window.location.search);
  var _initStatus = _initUrl.get('status');
  var _initQuery = _initUrl.get('q');
  const [search, setSearchRaw] = useState(function() {
    if (_initQuery != null) return _initQuery;
    try { return sessionStorage.getItem('recv_search') || ''; } catch(e) { return ''; }
  });
  const [filter, setFilterRaw] = useState(function() {
    if (_initStatus != null) return _initStatus;
    try { var v = sessionStorage.getItem('recv_filter'); return v == null ? 'checked-in' : v; } catch(e) { return 'checked-in'; }
  });
  var _syncUrl = function(f, s) {
    var params = [];
    if (f) params.push('status=' + encodeURIComponent(f));
    if (s) params.push('q=' + encodeURIComponent(s));
    var url = '/receiving' + (params.length ? '?' + params.join('&') : '');
    history.replaceState(history.state, '', url);
  };
  var setSearch = function(v) { setSearchRaw(v); try { sessionStorage.setItem('recv_search', v || ''); } catch(e) {} _syncUrl(filter, v); };
  var setFilter = function(v) { setFilterRaw(v); try { sessionStorage.setItem('recv_filter', v); } catch(e) {} _syncUrl(v, search); };
  useEffect(function() { _syncUrl(filter, search); }, []);
  const [selected, setSelected] = useState({});
  var [recvLocModalOpen, setRecvLocModalOpen] = useState(false);
  var [recvLocModalIds, setRecvLocModalIds] = useState([]);

  // Loading + error states (2026-09-27 sweep M16): a failed load used to leave the table empty,
  // reading as "no POs". Only the newest request may paint (a filter click mid-load raced).
  const [recvLoading, setRecvLoading] = useState(true);
  const [recvError, setRecvError] = useState(null);
  var recvSeq = useRef(0);
  var load = useCallback(async function() {
    var seq = ++recvSeq.current;
    setRecvLoading(true);
    try {
      var rows = await api('/receiving/receivable' + (filter ? '?status=' + filter : ''));
      if (seq !== recvSeq.current) return;
      setPos(Array.isArray(rows) ? rows : []);
      setRecvError(null);
    } catch (e) {
      if (seq !== recvSeq.current) return;
      setRecvError(e && e.message || 'Failed to load');
    } finally {
      if (seq === recvSeq.current) setRecvLoading(false);
    }
  }, [filter]);
  useEffect(function() { load(); }, [load]);

  var [itemSearchResults, setItemSearchResults] = useState(null);
  var [itemSearching, setItemSearching] = useState(false);
  var itemSearchTimer = useRef(null);

  // Header matches (PO #, vendor, warehouse) + item-search hits, both under the status tab — see
  // receivingListRows above (2026-10-20: the item hits used to replace the list, so a searched PO
  // bounced out of view). Item-search results obey the tab like everything else (2026-09-28:
  // "Valen" under Received listed POs of every status — the item search used to 500 silently, so
  // the tab looked respected until the 9-27 fix made it work). The server is asked for the tab's
  // status too; this keeps a stale result set honest.
  var recvList = receivingListRows(pos, itemSearchResults, filter, search);
  var filtered = recvList.rows;

  // The item search carries the status tab (the server caps at 50 POs, so filtering afterwards
  // could drop matches); it re-runs when the tab changes while a term is typed.
  var runItemSearch = function(val, f) {
    if (itemSearchTimer.current) clearTimeout(itemSearchTimer.current);
    if (!val.trim()) { setItemSearchResults(null); return; }
    var isProductSearch = /^[A-Za-z0-9\-_.]+$/.test(val.trim()) && val.trim().length >= 3;
    if (!isProductSearch) { setItemSearchResults(null); return; }
    itemSearchTimer.current = setTimeout(async function() {
      setItemSearching(true);
      try {
        var results = await api('/receiving/search-items?q=' + encodeURIComponent(val.trim()) + (f ? '&status=' + encodeURIComponent(f) : ''));
        setItemSearchResults(results.length > 0 ? results : null);
      } catch(e) { setItemSearchResults(null); }
      setItemSearching(false);
    }, 400);
  };
  var handleSearch = function(val) {
    setSearch(val);
    runItemSearch(val, filter);
  };
  useEffect(function() { if (search.trim()) runItemSearch(search, filter); }, [filter]);

  var selectedIds = Object.keys(selected).filter(function(k) { return selected[k]; }).map(Number);
  var selCount = selectedIds.length;
  var lastCheckedRef = useRef(null);
  var sortedDataRef = useRef(filtered);

  var toggleOne = function(id, shiftKey) {
    if (shiftKey) window.getSelection().removeAllRanges();
    var next = Object.assign({}, selected);
    if (shiftKey && lastCheckedRef.current !== null) {
      var visibleData = sortedDataRef.current || filtered;
      var lastIdx = visibleData.findIndex(function(o) { return o.id === lastCheckedRef.current; });
      var curIdx = visibleData.findIndex(function(o) { return o.id === id; });
      if (lastIdx !== -1 && curIdx !== -1) {
        var start = Math.min(lastIdx, curIdx); var end = Math.max(lastIdx, curIdx);
        var newVal = !selected[id];
        for (var i = start; i <= end; i++) next[visibleData[i].id] = newVal;
      }
    } else { next[id] = !next[id]; }
    lastCheckedRef.current = id;
    setSelected(next);
  };
  var toggleAll = function() {
    if (selCount === filtered.length && filtered.length > 0) setSelected({});
    else { var next = {}; filtered.forEach(function(o) { next[o.id] = true; }); setSelected(next); }
  };
  var [bulkConfirm, setBulkConfirm] = useState(null); // { status, ids }
  var bulkStatus = function(status) {
    if (status === 'checked-in') {
      setRecvLocModalIds(selectedIds.slice());
      setRecvLocModalOpen(true);
      return;
    }
    setBulkConfirm({ status: status, ids: selectedIds.slice() });
  };
  var executeBulkStatus = async function() {
    if (!bulkConfirm) return;
    try {
      await api('/receiving/bulk/status', { method: 'PUT', body: JSON.stringify({ ids: bulkConfirm.ids, status: bulkConfirm.status }) });
      showToast(bulkConfirm.ids.length + ' PO(s) marked as ' + bulkConfirm.status); setSelected({}); load();
    } catch (e) { showToast(e.message, 'error'); }
    setBulkConfirm(null);
  };

  var handleRecvBulkCheckIn = async function(loc, pallets, boxes) {
    setRecvLocModalOpen(false);
    try {
      await api('/receiving/bulk/status', { method: 'PUT', body: JSON.stringify({ ids: recvLocModalIds, status: 'checked-in', warehouse_location: loc, pallets: pallets, boxes: boxes, append: true }) });
      showToast(recvLocModalIds.length + ' PO(s) checked in at ' + loc); setSelected({}); load();
    } catch (e) { showToast(e.message, 'error'); }
  };

  var bulkExportWO = async function() {
    var selectedPOs = pos.filter(function(o) { return selected[o.id]; });
    if (!selectedPOs.length) return;

    // Single PO — download directly
    if (selectedPOs.length === 1) {
      try {
        var token = getAuthToken();
        var resp = await fetch('/api/receiving/' + selectedPOs[0].id + '/export-wo', { credentials: 'include' });
        if (!resp.ok) { var err = await resp.json().catch(function() { return {}; }); throw new Error(err.error || 'Export failed'); }
        var blob = await resp.blob();
        var filename = resp.headers.get('content-disposition');
        filename = filename ? filename.split('filename=')[1].replace(/"/g, '') : 'WO_export.xlsx';
        var url = URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
        showToast('Work order exported');
      } catch (e) { showToast('Export failed: ' + e.message, 'error'); }
      return;
    }

    // Multiple POs — zip them
    showToast('Exporting ' + selectedPOs.length + ' work orders...');
    try {
      var token = getAuthToken();
      var zip = new JSZip();
      for (var i = 0; i < selectedPOs.length; i++) {
        var resp = await fetch('/api/receiving/' + selectedPOs[i].id + '/export-wo', { credentials: 'include' });
        if (!resp.ok) continue;
        var blob = await resp.blob();
        var filename = resp.headers.get('content-disposition');
        filename = filename ? filename.split('filename=')[1].replace(/"/g, '') : 'WO_' + selectedPOs[i].po_number + '.xlsx';
        zip.file(filename, blob);
      }
      var content = await zip.generateAsync({ type: 'blob' });
      var url = URL.createObjectURL(content);
      var a = document.createElement('a');
      a.href = url; a.download = 'Work_Orders_' + selectedPOs.length + '_POs.zip'; a.click();
      URL.revokeObjectURL(url);
      showToast(selectedPOs.length + ' work orders exported as ZIP');
    } catch (e) { showToast('Export failed: ' + e.message, 'error'); }
  };

  var cols = [
    { key: '_select', label: '', locked: true, stopClick: true, noFilter: true, style: { width: 44, paddingRight: 12 },
      headerRender: function() {
        var allChecked = filtered.length > 0 && filtered.every(function(o) { return selected[o.id]; });
        var someChecked = !allChecked && filtered.some(function(o) { return selected[o.id]; });
        return React.createElement('div', {
          onClick: function(e) { e.stopPropagation(); toggleAll(); },
          style: { width: 32, height: 28, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', userSelect: 'none' }
        },
          React.createElement('div', {
            style: { width: 20, height: 20, borderRadius: 4, border: '2px solid ' + (allChecked || someChecked ? C.accent : C.textDim),
              background: allChecked ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }
          },
            allChecked && React.createElement(LIcon, { name: 'check', size: 13, color: '#fff' }),
            someChecked && !allChecked && React.createElement('span', { style: { color: C.accent, fontSize: 13, fontWeight: 700, lineHeight: 1 } }, '–')
          )
        );
      },
      render: function(o) {
      var checked = !!selected[o.id];
      return React.createElement('div', {
        onMouseDown: function(e) { if (e.shiftKey) e.preventDefault(); },
        onClick: function(e) { e.stopPropagation(); toggleOne(o.id, e.shiftKey); },
        style: { width: 32, height: 28, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', userSelect: 'none' }
      }, React.createElement('div', { style: { width: 20, height: 20, borderRadius: 4, border: '2px solid ' + (checked ? C.accent : C.textDim), background: checked ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' } },
        checked && React.createElement(LIcon, { name: 'check', size: 13, color: '#fff' })
      ));
    }},
    { key: 'recv_aging', label: 'Aging',
      sortValue: function(o) {
        var start = o.checked_in_date ? new Date(o.checked_in_date) : (o.shipped_date ? new Date(o.shipped_date) : null);
        if (!start) return -1;
        var end = o.received_date ? new Date(o.received_date) : new Date();
        return Math.floor((end - start) / (1000 * 60 * 60 * 24));
      },
      render: function(o) {
      var start = o.checked_in_date ? new Date(o.checked_in_date) : (o.shipped_date ? new Date(o.shipped_date) : null);
      if (!start) return React.createElement('span', { style: { color: C.textDim, fontSize: 12 } }, '—');
      var end = o.received_date ? new Date(o.received_date) : new Date();
      var days = Math.floor((end - start) / (1000 * 60 * 60 * 24));
      var color = days <= 3 ? C.success : days <= 7 ? C.warning : C.danger;
      var label = days === 0 ? 'Today' : days === 1 ? '1 day' : days + ' days';
      if (!o.received_date) label += ' ▸';
      return React.createElement('span', { style: { fontWeight: 500, fontSize: 12, color: color } }, label);
    }},
    { key: 'po_number', label: 'PO #', copyable: true, style: { fontWeight: 600, fontFamily: 'inherit' }, render: function(o) {
      return React.createElement('a', { href: '/po/' + o.id, onClick: function(e) { e.preventDefault(); }, style: { color: C.accent, textDecoration: 'none' } }, o.po_number);
    } },
    { key: 'po_date', label: 'Creation Date', render: function(o) { return fmtDateOnly(o.po_date) || '—'; } },
    { key: 'shipped_date', label: 'Shipped Date', render: function(o) { return o.shipped_date ? new Date(o.shipped_date).toLocaleDateString() : '—'; } },
    { key: 'checked_in_date', label: 'Checked-In', render: function(o) { return o.checked_in_date ? new Date(o.checked_in_date).toLocaleDateString() : '—'; } },
    { key: 'received_date', label: 'Received Date', render: function(o) {
      if (!o.received_date) return React.createElement('span', { style: { color: C.textDim, fontSize: 12 } }, '—');
      return React.createElement('span', { style: { fontSize: 12 } }, new Date(o.received_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }));
    }},
    { key: 'closed_date', label: 'Closed Date', render: function(o) { return o.closed_date ? new Date(o.closed_date).toLocaleDateString() : '—'; } },
    { key: 'wh_code', label: 'Warehouse', copyable: true },
    { key: 'vendor_name', label: 'Vendor', copyable: true, render: function(o) {
      return React.createElement('span', { style: { display: 'inline-flex', gap: 6, alignItems: 'center' } },
        React.createElement('span', null, o.vendor_name || '—'),
        o.has_errored_fba_items && React.createElement('span', {
          title: 'This PO has un-binned errored FBA items — review on the Errored tab of the related FBA plan',
          style: { padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 600, background: C.danger + '22', color: C.danger, textTransform: 'uppercase', letterSpacing: '0.05em' }
        }, 'Errored')
      );
    } },
    { key: 'status', label: 'Status', render: function(o) {
      var colors = { draft: '#F59E0B', submitted: '#3B82F6', approved: '#22C55E', confirmed: '#14B8A6', next: '#06B6D4', shipped: C.accent, 'checked-in': C.purple || C.accent, receiving: C.warning, received: C.success, closed: C.textMuted, cancelled: C.danger };
      return React.createElement('span', { style: { padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 600, background: (colors[o.status] || C.textDim) + '18', color: colors[o.status] || C.textDim, textTransform: 'uppercase', letterSpacing: '0.05em' } }, o.status);
    }},
    { key: 'total_units', label: 'Ordered', style: { fontWeight: 500 } },
    { key: 'total_received', label: 'Received', render: function(o) {
      var pct = o.total_units > 0 ? Math.round((o.total_received / o.total_units) * 100) : 0;
      var color = pct === 100 ? C.success : pct > 0 ? C.warning : C.textDim;
      return React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
        React.createElement('span', { style: { fontWeight: 500, color: color } }, o.total_received + '/' + o.total_units),
        React.createElement('div', { style: { flex: 1, maxWidth: 60, height: 6, background: C.border, borderRadius: 3, overflow: 'hidden' } },
          React.createElement('div', { style: { width: pct + '%', height: '100%', background: color, borderRadius: 3 } })
        ),
        React.createElement('span', { style: { fontSize: 11, color: C.textDim } }, pct + '%')
      );
    }},
    { key: 'warehouse_location', label: 'WH Location', render: function(o) {
      return React.createElement('span', { style: { color: o.warehouse_location ? C.text : C.textDim, fontSize: 12 } }, o.warehouse_location || '—');
    }},
    { key: 'checkin_pallets', label: 'Pallets', render: function(o) {
      return React.createElement('span', { style: { color: o.checkin_pallets ? C.text : C.textDim, fontSize: 12 } }, o.checkin_pallets || '—');
    }},
    { key: 'checkin_boxes', label: 'Boxes', render: function(o) {
      return React.createElement('span', { style: { color: o.checkin_boxes ? C.text : C.textDim, fontSize: 12 } }, o.checkin_boxes || '—');
    }},
  ];

  return React.createElement('div', null,
    React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 } },
      React.createElement('div', null,
        React.createElement('h2', { style: { margin: 0, fontSize: 20, fontWeight: 600, color: C.text } }, 'Receiving'),
        React.createElement('p', { style: { margin: '4px 0 0', fontSize: 13, color: C.textMuted } }, filtered.length + ' purchase orders')
      )
    ),
    // Status filters
    React.createElement('div', { style: { display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' } },
      ['', 'shipped', 'checked-in', 'receiving', 'received', 'closed'].map(function(s) {
        var active = filter === s;
        return React.createElement('button', { key: s, onClick: function() { setFilter(s); setSelected({}); },
          className: active ? '' : 'filter-pill',
          style: { padding: '6px 14px', fontSize: 12, borderRadius: 6, border: '1px solid transparent', cursor: 'pointer', fontWeight: 500, fontFamily: 'inherit', transition: 'all 0.15s',
            background: active ? C.accent : C.surface, color: active ? '#fff' : C.textMuted } }, s || 'All');
      })
    ),
    React.createElement('div', { style: { position: 'relative', marginBottom: 16 } },
      React.createElement(SearchInput, { className: 'dt-input', placeholder: 'Search PO #, vendor, warehouse, SKU, barcode, FNSKU, item name...', value: search, onChange: function(e) { handleSearch(e.target.value); },
        style: { width: '100%', background: C.bg, borderWidth: 1, borderStyle: 'solid', borderColor: C.border, color: C.text, padding: '10px 14px', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s, box-shadow 0.15s' } }),
      recvList.itemExtra.length > 0 && React.createElement('div', { style: { fontSize: 11, color: C.accent, marginTop: 4 } }, '+ ' + recvList.itemExtra.length + ' PO(s) with a SKU, barcode or item name matching "' + search.trim() + '"')
    ),
    recvError && React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, padding: '8px 12px', borderRadius: 8, background: C.dangerSoft, color: C.danger, fontSize: 12 } },
      'Could Not Load Purchase Orders: ' + recvError,
      React.createElement(Btn, { size: 'sm', variant: 'primary', onClick: function() { load(); } }, 'Retry')),
    // The item search's progress shows IN the table (Anthony 2026-10-20: "If search isn't complete show a
    // loading spinner in the table") — a pill over the rows while the SKU / barcode / title hits are still
    // coming; the rows stay visible and clickable. An empty table says "Searching items..." instead of
    // "No Purchase Orders". (The old "Searching..." text sat on top of the search box's clear x.)
    React.createElement('div', { style: { position: 'relative' } },
    itemSearching && filtered.length > 0 && React.createElement('div', { style: { position: 'absolute', top: 52, left: 0, right: 0, display: 'flex', justifyContent: 'center', pointerEvents: 'none', zIndex: 2 } },
      React.createElement('div', { style: { display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderRadius: 999, background: C.surface, border: '1px solid ' + C.border, boxShadow: '0 4px 12px rgba(0,0,0,0.25)', fontSize: 12, color: C.textMuted } },
        React.createElement(LIcon, { name: 'loader-2', size: 14, spin: true }), 'Searching items...')),
    React.createElement(DataTable, {
      columns: cols.filter(function(c) {
        if (c.key === 'received_date' && ['shipped','checked-in','receiving','closed'].indexOf(filter) !== -1) return false;
        if (c.key === 'checked_in_date' && (filter === 'shipped' || filter === 'closed')) return false;
        if (c.key === 'shipped_date' && filter !== 'shipped') return false;
        if (c.key === 'po_date' && filter === 'closed') return false;
        if (c.key === 'closed_date' && filter !== 'closed') return false;
        return true;
      }), data: filtered, storageKey: 'receiving-list',
      defaultSort: 'vendor_name', defaultSortDir: 'asc',
      onRowClick: function(o) { onNavigate('receiving-po', o.id); },
      rowHref: function(o) { return '/receiving/' + o.id; },
      onSortedData: function(sorted) { sortedDataRef.current = sorted; },
      emptyTitle: recvLoading ? 'Loading...' : recvError ? 'Could Not Load Purchase Orders'
        : itemSearching ? React.createElement('span', { style: { display: 'inline-flex', alignItems: 'center', gap: 8 } }, React.createElement(LIcon, { name: 'loader-2', size: 16, spin: true }), 'Searching items...')
        : 'No Purchase Orders',
      emptySub: recvLoading || itemSearching ? '' : recvError ? recvError : 'POs will appear here based on your filter',
      bulkActions: selCount > 0 ? {
        count: selCount,
        onClear: function() { setSelected({}); },
        items: [
          { label: 'Export Work Orders', onClick: bulkExportWO },
          { divider: true },
          { label: 'Mark as Checked-In', onClick: function() { bulkStatus('checked-in'); } },
          { label: 'Mark as Receiving', onClick: function() { bulkStatus('receiving'); } },
          { label: 'Mark as Received', onClick: function() { bulkStatus('received'); } },
          { label: 'Mark as Closed', onClick: function() { bulkStatus('closed'); } },
        ]
      } : null
    })
    ), // close the table wrapper (spinner pill anchor)
    recvLocModalOpen && React.createElement(LocationPickerModal, {
      title: 'Check In — ' + recvLocModalIds.length + ' PO(s)',
      onConfirm: handleRecvBulkCheckIn,
      onCancel: function() { setRecvLocModalOpen(false); }
    }),
    // Bulk status confirmation modal
    React.createElement(Modal, { open: !!bulkConfirm, onClose: function() { setBulkConfirm(null); }, title: 'Confirm Bulk Status Change', width: 480 },
      bulkConfirm && React.createElement('div', null,
        React.createElement('div', { style: { fontSize: 13, color: C.text, marginBottom: 12 } },
          'Change ', React.createElement('strong', null, bulkConfirm.ids.length + ' PO(s)'), ' to ',
          React.createElement('strong', { style: { color: C.accent } }, bulkConfirm.status), '?'
        ),
        React.createElement('div', { style: { maxHeight: 240, overflow: 'auto', borderRadius: 6, border: '1px solid ' + C.border, marginBottom: 16 } },
          pos.filter(function(o) { return bulkConfirm.ids.indexOf(o.id) >= 0; }).map(function(o, idx) {
            return React.createElement('div', { key: o.id, style: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: idx < bulkConfirm.ids.length - 1 ? '1px solid ' + C.border + '44' : 'none', fontSize: 13 } },
              React.createElement('span', { style: { fontFamily: 'inherit', fontWeight: 600, color: C.accent } }, o.po_number),
              React.createElement('span', { style: { color: C.textMuted, flex: 1 } }, o.vendor_name),
              React.createElement('span', { style: { fontSize: 11, color: C.textDim } }, o.status + ' → ' + bulkConfirm.status)
            );
          })
        ),
        React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } },
          React.createElement(Btn, { onClick: function() { setBulkConfirm(null); } }, 'Cancel'),
          React.createElement(Btn, { variant: 'primary', onClick: executeBulkStatus }, 'Confirm')
        )
      )
    )
  );
}

// ─── RECEIVING DETAIL PAGE ───
// Normalize bin location: "A100" → "A-100", "A1" → "A-01"
function formatSingleBin(val) {
  // LOC* bins (e.g. LOC800) are staging/location codes that carry no dash — leave them as typed.
  // Only the single-letter zone bins get the dash + zero-pad treatment (B80 -> B-80).
  var m = val.match(/^([A-Z]+)-?(\d+)$/);
  if (m && m[1] !== 'LOC') {
    var num = m[2].length < 2 ? ('0' + m[2]) : m[2];
    return m[1] + '-' + num;
  }
  return val;
}
function formatBin(raw) {
  if (!raw) return '';
  var val = raw.trim().toUpperCase();
  if (!val) return '';
  return val.split('/').map(function(part) { return formatSingleBin(part.trim()); }).join('/');
}

// ─── Receiving history entry (shared) ───
// One row of the receiving-history feed (GET /api/receiving/:poId/history): a receipt, a MISSING
// mark (or its reversal — FBA / WFS unmarks arrive as a missing row with a negative qty), or a
// status event out of the PO activity log (check-in, FBA / WFS plan changes, the flips, the
// missing marks). The Receive page's Receiving History tab and the PO detail page's Receiving tab
// both render through this one component so they cannot drift again: until 2026-10-20 the PO card
// had its own renderer that printed every status row as "— received +undefined units" and a
// MISSING mark as a green receipt (PO 29234).
// onUndo(logId) is optional — only the Receive page offers Undo (admins, or the receiver).
// onPhotoClick(urls, index) is optional — without it a photo opens in a new tab.
function ReceivingHistoryEntry({ log, currentUser, onUndo, onPhotoClick }) {
  var fmtTime = function(d) {
    var t = new Date(d);
    return t.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ' ' + t.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };
  var timeStr = fmtTime(log.received_at);

  // Status change event (checked-in, receiving, received, sent)
  if (log.entry_type === 'status_change') {
    var icon = 'clipboard-list';
    var statusColor = C.textMuted;
    var details = log.details || '';
    if (details.includes('checked-in')) { icon = 'package'; statusColor = C.purple || C.accent; }
    else if (details.includes('Warehouse Location')) { icon = 'map-pin'; statusColor = C.text; }
    else if (details.indexOf('"receiving"') !== -1) { icon = 'inbox'; statusColor = C.warning; }
    else if (details.indexOf('"received"') !== -1) { icon = 'check-circle'; statusColor = C.success; }
    else if (details.indexOf('"closed"') !== -1) { icon = 'lock'; statusColor = C.accent; }
    else if (details.includes('shipped')) { icon = 'truck'; statusColor = C.accent; }
    if (log.action === 'fba_missing' || log.action === 'wfs_missing' || log.action === 'meli_missing') { icon = 'alert-circle'; statusColor = C.danger; }
    else if (log.action === 'fba_missing_undo' || log.action === 'wfs_missing_undo' || log.action === 'meli_missing_undo') { icon = 'rotate-ccw'; statusColor = C.success; }
    var isReverted = !!log.superseded_at;
    var revertedTimeStr = isReverted ? fmtTime(log.superseded_at) : '';
    return React.createElement('div', { style: { display: 'flex', gap: 12, padding: '10px 4px', borderBottom: '1px solid ' + C.border + '44', alignItems: 'center', opacity: isReverted ? 0.6 : 1 } },
      React.createElement('div', { style: { fontSize: 16, width: 24, textAlign: 'center', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' } }, React.createElement(LIcon, { name: icon, size: 16, color: isReverted ? C.textDim : undefined })),
      React.createElement('div', { style: { flex: 1 } },
        React.createElement('div', { style: { fontSize: 13, color: isReverted ? C.textMuted : statusColor, fontWeight: 500, textDecoration: isReverted ? 'line-through' : 'none' } },
          (function() {
            var lookup = log.plan_lookup || {};
            var fbaRe = /FBA (?:shipment )?[Pp]lan #(\d+)/;
            var wfsRe = /WFS shipment plan #(\d+)/;
            var m = details.match(fbaRe) || details.match(wfsRe);
            if (!m) return details;
            var isFba = /FBA/.test(m[0]);
            var internalId = m[1];
            var info = lookup[(isFba ? 'fba_' : 'wfs_') + internalId];
            var displayText = m[0]; // fallback to original text
            if (info) {
              if (isFba) {
                if (info.shipmentIds && info.shipmentIds.length > 0) displayText = 'FBA Shipment' + (info.shipmentIds.length > 1 ? 's' : '') + ' ' + info.shipmentIds.join(', ');
                else if (info.amazonPlanId) displayText = 'FBA Plan ' + info.amazonPlanId;
              } else {
                if (info.walmartShipmentId) displayText = 'WFS Shipment ' + info.walmartShipmentId;
                else if (info.inboundOrderId) displayText = 'WFS Order ' + info.inboundOrderId;
              }
            }
            var navPage = isFba ? 'fba-detail' : 'wfs-detail';
            var idx = details.indexOf(m[0]);
            var before = details.substring(0, idx);
            var after = details.substring(idx + m[0].length);
            var href = pageToPath(navPage, parseInt(internalId));
            return [before, React.createElement('a', { key: 'lnk', href: href, target: '_blank', rel: 'noopener', style: { color: C.accent, textDecoration: 'underline', cursor: 'pointer' } }, displayText), after];
          })()
        ),
        isReverted && React.createElement('span', { style: { display: 'inline-block', marginTop: 4, background: C.textDim + '22', color: C.textMuted, fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, letterSpacing: '0.05em', textTransform: 'uppercase' } }, 'Reverted'),
        React.createElement('div', { style: { fontSize: 11, color: C.textDim, marginTop: 2 } },
          React.createElement('span', { style: { fontWeight: 600 } }, log.username || 'System'), ' — ', timeStr
        ),
        isReverted && React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 2, fontStyle: 'italic' } },
          'Reverted by ', React.createElement('span', { style: { fontWeight: 600 } }, log.superseded_by || 'unknown'), ' — ', revertedTimeStr
        )
      )
    );
  }

  // Regular receive or missing entry. FBA / WFS unmarks arrive as a missing row with a
  // negative qty — show those as a reversal rather than another shortage.
  var isMissing = log.is_missing;
  var isUnmark = !!isMissing && Number(log.qty_received) < 0;
  var isUndone = !!log.undone_at;
  var undoneTimeStr = isUndone ? fmtTime(log.undone_at) : '';
  var photos = log.photos || [];
  var canUndo = !isUndone && !!onUndo && !!currentUser && (currentUser.role === 'admin' || currentUser.display_name === log.received_by);
  return React.createElement('div', { style: { display: 'flex', gap: 12, padding: '10px 4px', borderBottom: '1px solid ' + C.border + '44', alignItems: 'center', opacity: isUndone ? 0.6 : 1 } },
    React.createElement('div', { style: { fontSize: 16, width: 24, textAlign: 'center', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' } }, React.createElement(LIcon, { name: isUnmark ? 'rotate-ccw' : (isMissing ? 'alert-circle' : 'inbox'), size: 16, color: isUndone ? C.textDim : (isUnmark ? C.success : (isMissing ? C.danger : undefined)) })),
    React.createElement('div', { style: { flex: 1 } },
      React.createElement('div', { style: { fontSize: 13, color: isUndone ? C.textMuted : (isMissing && !isUnmark ? C.danger : C.text), textDecoration: isUndone ? 'line-through' : 'none' } },
        React.createElement('span', { style: { fontWeight: 600, fontFamily: 'inherit' } }, log.sku),
        isMissing ? ' — ' : ' — received ',
        isMissing
          ? (isUnmark
              ? React.createElement('span', { style: { fontWeight: 600, color: isUndone ? C.textMuted : C.success } }, 'Missing mark reversed (' + Math.abs(Number(log.qty_received)) + ' units)')
              : React.createElement('span', { style: { fontWeight: 600, color: isUndone ? C.textMuted : C.danger } }, 'MISSING ' + log.qty_received + ' units'))
          : React.createElement('span', { style: { fontWeight: 600, color: isUndone ? C.textMuted : C.success } }, '+' + log.qty_received + ' units'),
        !isMissing && log.bin_location && React.createElement('span', { style: { color: C.textMuted } }, ' → Bin '),
        !isMissing && log.bin_location && React.createElement('span', { style: { fontWeight: 600, fontFamily: 'inherit', color: isUndone ? C.textMuted : C.accent } }, log.bin_location)
      ),
      isUndone && React.createElement('span', { style: { display: 'inline-block', marginTop: 4, background: C.textDim + '22', color: C.textMuted, fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, letterSpacing: '0.05em', textTransform: 'uppercase' } }, 'Undone'),
      photos.length > 0 && React.createElement('div', { style: { display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' } },
        photos.map(function(ph, i) {
          return React.createElement('img', { key: ph.id, src: ph.url,
            onClick: function() {
              if (onPhotoClick) onPhotoClick(photos.map(function(x) { return x.url; }), i);
              else window.open(ph.url, '_blank', 'noopener');
            },
            style: { width: 44, height: 44, borderRadius: 4, objectFit: 'cover', border: '1px solid ' + C.border, cursor: 'pointer', opacity: isUndone ? 0.5 : 1 } });
        })
      ),
      log.notes && React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 2, fontStyle: 'italic' } }, 'Notes: ' + log.notes),
      log.po_refs && log.po_refs.length > 0 && React.createElement('div',
        { style: { fontSize: 11, color: C.textDim, marginTop: 3, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' } },
        log.po_refs.map(function(ref) {
          var label = ref.type === 'fba_shipment' ? 'FBA Shipment'
            : ref.type === 'fba_plan' ? 'FBA Plan'
            : ref.type === 'wfs_shipment' ? 'WFS Shipment' : 'WFS Order';
          var navPage = (ref.type === 'fba_shipment' || ref.type === 'fba_plan') ? 'fba-detail' : 'wfs-detail';
          var href = ref.planInternalId ? pageToPath(navPage, ref.planInternalId) : '#';
          return React.createElement(CopyCell, { key: ref.type + '_' + ref.id, value: ref.id },
            React.createElement('a', {
              href: href, target: '_blank', rel: 'noopener',
              style: { fontFamily: 'inherit', color: C.accent, textDecoration: 'underline', cursor: 'pointer' }
            }, label + ': ' + ref.id)
          );
        })
      ),
      React.createElement('div', { style: { fontSize: 11, color: C.textDim, marginTop: 2 } },
        React.createElement('span', { style: { fontWeight: 600 } }, log.received_by), ' — ', timeStr
      ),
      isUndone && React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 2, fontStyle: 'italic' } },
        'Undone by ', React.createElement('span', { style: { fontWeight: 600 } }, log.undone_by || 'unknown'), ' — ', undoneTimeStr
      )
    ),
    canUndo && React.createElement('button', {
      onClick: function() { onUndo(log.id); },
      title: 'Undo this receive',
      style: { background: 'none', border: '1px solid ' + C.border, color: C.danger, cursor: 'pointer', fontSize: 11, padding: '4px 10px', borderRadius: 4, fontFamily: 'inherit', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4 }
    }, React.createElement(LIcon, { name: 'undo-2', size: 14 }), ' Undo')
  );
}
window.ReceivingHistoryEntry = ReceivingHistoryEntry;

function ReceivingDetailPage({ poId, showToast, onNavigate, currentUser }) {
  const [po, setPo] = useState(null);
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTabRaw] = useState('receive');
  // Tab changes are written back to the address bar (review M6 #78), mirroring PO detail.
  // (`history` is shadowed by the receiving-history state in this scope — use window.history.)
  var setActiveTab = function(t) {
    setActiveTabRaw(t);
    try { var path = window.location.pathname; if (path.startsWith('/receiving/')) window.history.replaceState(window.history.state, '', path + (t && t !== 'receive' ? '?tab=' + t : '')); } catch (e) {}
  };
  // Deep link: /receiving/:id?tab=<tab> selects the tab on load (new-tab target).
  useEffect(function() { try { var qt = new URLSearchParams(window.location.search).get('tab'); if (qt) setActiveTab(qt); } catch (e) {} }, []);
  // Internal notes (PO notes)
  const [intNotes, setIntNotes] = useState([]);
  const [newIntNote, setNewIntNote] = useState('');
  var intNotesEndRef = useRef(null);
  const [itemSelected, setItemSelected] = useState({});
  const [editingLocation, setEditingLocation] = useState(false);
  const [locationVal, setLocationVal] = useState('');
  // review M6 #74: the 15 s poll re-syncs the location input from the server; while the user is
  // typing (dirty) it must leave the field alone — on a pre-check-in PO the reset to '' also
  // skipped the check-in branch of saveLocation and wrote an empty location instead.
  var locationDirtyRef = useRef(false);
  const [editingPallets, setEditingPallets] = useState(false);
  const [palletsVal, setPalletsVal] = useState('');
  const [editingBoxes, setEditingBoxes] = useState(false);
  const [boxesVal, setBoxesVal] = useState('');
  const [checkInModalOpen, setCheckInModalOpen] = useState(false);
  const [labelTemplates, setLabelTemplates] = useState([]);
  const [printLabelModal, setPrintLabelModal] = useState(null);
  const [markMissingModal, setMarkMissingModal] = useState(null);
  const [markMissingLoading, setMarkMissingLoading] = useState(false);
  const [markMissingNote, setMarkMissingNote] = useState('');
  const [selectedLabelTpl, setSelectedLabelTpl] = useState(null);
  const [printQtyOverride, setPrintQtyOverride] = useState({});
  const [lightbox, setLightbox] = useState(null);
  // Item search within PO
  const [itemFilter, setItemFilter] = useState('');
  // Bin editing
  const [editingBins, setEditingBins] = useState({});
  // Scan to Receive state
  const [scanModalOpen, setScanModalOpen] = useState(false);
  const [scanInput, setScanInput] = useState('');
  // Mark Closed confirmation
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [scanResult, setScanResult] = useState(null); // matched line item
  const [scanError, setScanError] = useState('');
  const [scanQty, setScanQty] = useState('');
  const [scanLoading, setScanLoading] = useState(false);
  const [scanHistory, setScanHistory] = useState([]); // [{logId, sku, qty, time}]
  const [scanMode, setScanMode] = useState('scan'); // 'scan' | 'result' | 'pick' | 'unexpected'
  const [scanResults, setScanResults] = useState([]); // multiple matches
  const [scanBin, setScanBin] = useState('');
  const [scanNotes, setScanNotes] = useState('');
  const [unexpectedBarcode, setUnexpectedBarcode] = useState('');
  const [unexpectedQty, setUnexpectedQty] = useState('1');
  const [unexpectedTitle, setUnexpectedTitle] = useState('');
  const [unexpectedVendorSku, setUnexpectedVendorSku] = useState('');
  const [unexpectedBin, setUnexpectedBin] = useState('');
  const [unexpectedNotes, setUnexpectedNotes] = useState('');
  const [catalogMatchedProduct, setCatalogMatchedProduct] = useState(null);
  const [catalogMatches, setCatalogMatches] = useState([]);
  const [photoToken, setPhotoToken] = useState(null);
  const [photoResetSignal, setPhotoResetSignal] = useState(0);
  const [photoCount, setPhotoCount] = useState(0);
  var scanInputRef = useRef(null);

  var isValidPoId = poId && poId !== 'null' && poId !== 'undefined';

  // 2026-09-27 sweep M16: the 15 s poll and the post-action reloads overlapped with no ordering,
  // so an older response could paint over a newer one (qty_received jumping back), and a failed
  // first load left "Loading..." forever. Newest request wins; the error is shown with a Retry.
  var recvLoadSeq = useRef(0);
  const [recvLoadError, setRecvLoadError] = useState(null);
  var load = useCallback(async function() {
    if (!isValidPoId) { setRecvLoadError('Invalid PO link'); return; }
    var seq = ++recvLoadSeq.current;
    try {
      var data = await api('/receiving/' + poId);
      if (seq !== recvLoadSeq.current) return;
      setRecvLoadError(null);
      setPo(data);
      if (!locationDirtyRef.current) setLocationVal(data.warehouse_location || '');
    } catch (e) {
      if (seq !== recvLoadSeq.current) return;
      setRecvLoadError(e && e.message || 'Failed to load');
    }
  }, [poId]);

  var loadHistory = useCallback(async function() {
    if (!isValidPoId) return;
    try { setHistory(await api('/receiving/' + poId + '/history')); } catch(e) {}
  }, [poId]);

  var loadIntNotes = useCallback(async function() {
    if (!isValidPoId) return;
    try { setIntNotes(await api('/purchase-orders/' + poId + '/notes')); } catch(e) {}
  }, [poId]);

  var sendIntNote = async function() {
    if (!newIntNote.trim()) return;
    try {
      await api('/purchase-orders/' + poId + '/notes', { method: 'POST', body: JSON.stringify({ message: newIntNote.trim(), source: 'receiving' }) });
      setNewIntNote('');
      loadIntNotes();
    } catch (e) { showToast(e.message, 'error'); }
  };

  var deleteIntNote = async function(noteId) {
    try {
      await api('/purchase-orders/' + poId + '/notes/' + noteId, { method: 'DELETE' });
      loadIntNotes();
    } catch (e) { showToast(e.message, 'error'); }
  };

  useEffect(function() { load(); loadHistory(); loadIntNotes(); api('/settings/label-templates').then(setLabelTemplates).catch(function(e) { console.error('[silent-catch]', e); }); }, [load]);

  // Auto-poll every 15s to keep qty_received in sync across users
  useEffect(function() {
    var interval = setInterval(function() { if (document.visibilityState !== 'hidden') load(); }, 15000);
    return function() { clearInterval(interval); };
  }, [load]);

  var saveLocation = async function() {
    // Pre-check-in PO: entering a location IS the check-in (mirrors mobile) — route through
    // the real check-in flow (status + staging) instead of a silent field edit.
    var preCheckIn = po && ['draft', 'approved', 'submitted', 'confirmed', 'next', 'shipped'].indexOf(po.status) !== -1;
    if (preCheckIn && locationVal.trim()) { setCheckInModalOpen(true); return; }
    try {
      await api('/receiving/' + poId + '/location', { method: 'PUT', body: JSON.stringify({ warehouse_location: locationVal }) });
      showToast('Warehouse location updated');
      setEditingLocation(false);
      locationDirtyRef.current = false;
      load();
    } catch (e) { showToast(e.message, 'error'); }
  };

  var handleDetailCheckIn = async function(loc, pallets, boxes) {
    setCheckInModalOpen(false);
    try {
      await api('/receiving/bulk/status', { method: 'PUT', body: JSON.stringify({ ids: [po.id], status: 'checked-in', warehouse_location: loc, pallets: pallets, boxes: boxes }) });
      showToast('PO #' + po.po_number + ' checked in at ' + loc);
      setEditingLocation(false);
      locationDirtyRef.current = false;
      load();
      loadHistory();
    } catch (e) { showToast(e.message, 'error'); }
  };

  var savePallets = async function() {
    try {
      var n = palletsVal === '' ? 0 : parseInt(palletsVal);
      if (isNaN(n) || n < 0) { showToast('Enter a non-negative number', 'error'); return; }
      await api('/receiving/' + poId + '/location', { method: 'PUT', body: JSON.stringify({ warehouse_location: po.warehouse_location || null, pallets: n }) });
      showToast('Pallets updated');
      setEditingPallets(false);
      load();
    } catch (e) { showToast(e.message, 'error'); }
  };

  var saveBoxes = async function() {
    try {
      var n = boxesVal === '' ? 0 : parseInt(boxesVal);
      if (isNaN(n) || n < 0) { showToast('Enter a non-negative number', 'error'); return; }
      await api('/receiving/' + poId + '/location', { method: 'PUT', body: JSON.stringify({ warehouse_location: po.warehouse_location || null, boxes: n }) });
      showToast('Boxes updated');
      setEditingBoxes(false);
      load();
    } catch (e) { showToast(e.message, 'error'); }
  };

  var saveBin = async function(lineItemId) {
    var val = editingBins[lineItemId];
    if (val === undefined) return;
    val = formatBin(val) || (val || '').trim().toUpperCase();
    // Find the original bin value — skip save if unchanged
    var li = (po && po.line_items || []).find(function(l) { return l.id === lineItemId; });
    var originalBin = (li && li.bin_location || '').toUpperCase();
    var newBin = val;
    if (newBin === originalBin) {
      setEditingBins(function(prev) { var n = Object.assign({}, prev); delete n[lineItemId]; return n; });
      return;
    }
    try {
      await api('/receiving/' + poId + '/item-bin', { method: 'PUT', body: JSON.stringify({ line_item_id: lineItemId, bin_location: val }) });
      showToast('Bin updated');
      setEditingBins(function(prev) { var n = Object.assign({}, prev); delete n[lineItemId]; return n; });
      load();
    } catch (e) { showToast(e.message, 'error'); }
  };

  var undoReceive = async function(logId) {
    var localLog = (history || []).find(function(h) { return h.id === logId; });
    var wasMissing = !!(localLog && localLog.is_missing);
    if (!(await appConfirm(wasMissing ? 'Undo this missing entry?' : 'Undo this receiving entry?'))) return;
    try {
      var result = await api('/receiving/' + poId + '/undo/' + logId, { method: 'DELETE' });
      showToast(wasMissing
        ? 'Undid mark missing of ' + result.qty + ' × ' + result.sku
        : 'Undid receive of ' + result.qty + ' units for ' + result.sku);
      load(); loadHistory();
    } catch (e) { showToast(e.message, 'error'); }
  };

  var openMarkMissingModal = function(items) {
    if (!items || !items.length) return;
    setMarkMissingNote('');
    setMarkMissingModal({ items: items });
  };

  var confirmMarkMissing = async function() {
    if (!markMissingModal || !markMissingModal.items.length) return;
    setMarkMissingLoading(true);
    var items = markMissingModal.items;
    var bulk = items.length > 1;
    var userNote = (markMissingNote || '').trim();
    var defaultNote = bulk ? 'Marked missing (bulk)' : 'Marked missing';
    var noteToSend = userNote ? (defaultNote + ': ' + userNote) : defaultNote;
    var errors = 0;
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      try {
        await api('/receiving/' + poId + '/mobile-receive', {
          method: 'POST',
          body: JSON.stringify({ line_item_id: item.line_item_id, qty_received: item.qty, is_missing: true, notes: noteToSend })
        });
      } catch (e) { errors++; }
    }
    setMarkMissingLoading(false);
    if (items.length === 1) {
      var only = items[0];
      showToast(errors ? 'Failed to mark ' + only.sku + ' missing' : 'Marked ' + only.qty + ' × ' + only.sku + ' as missing', errors ? 'error' : undefined);
    } else {
      showToast('Marked ' + (items.length - errors) + ' item(s) as missing' + (errors ? ', ' + errors + ' failed' : ''));
    }
    setMarkMissingModal(null);
    setMarkMissingNote('');
    setItemSelected({});
    load(); loadHistory();
  };

  // ─── Scan to Receive handlers ───
  // Centralized reset — used by every "back to scan" / cancel path so no state leaks between scans
  var resetToScan = function() {
    setScanInput(''); setScanResult(null); setScanResults([]); setScanError('');
    setScanQty(''); setScanBin(''); setScanNotes('');
    setCatalogMatchedProduct(null); setCatalogMatches([]);
    setUnexpectedBarcode(''); setUnexpectedQty('1'); setUnexpectedTitle(''); setUnexpectedVendorSku(''); setUnexpectedBin(''); setUnexpectedNotes('');
    setPhotoToken(null); setPhotoCount(0); setPhotoResetSignal(function(s) { return s + 1; });
    setScanMode('scan');
    setTimeout(function() { if (scanInputRef.current) scanInputRef.current.focus(); }, 100);
  };
  var openScanModal = function() {
    setScanModalOpen(true); setScanHistory([]);
    resetToScan();
  };
  var closeScanModal = function() { setScanModalOpen(false); load(); loadHistory(); };

  var applyCatalogMatch = function(prod, scannedCode) {
    setCatalogMatchedProduct(prod);
    setUnexpectedBarcode(prod.barcode || scannedCode || '');
    setUnexpectedTitle(prod.vendor_title || prod.title || '');
    setUnexpectedVendorSku(prod.vendor_sku || '');
    // Pre-fill bin: prefer FBM (MIA) > RETURNS. Use bin_quantities (structured) for explicit warehouse check;
    // fall back to current_bins (comma-separated, FBM-prioritized server-side) if bin_quantities missing.
    var primaryBin = '';
    if (Array.isArray(prod.bin_quantities) && prod.bin_quantities.length > 0) {
      var fbmBin = prod.bin_quantities.find(function(b) { return b.warehouse === 'FBM' && b.bin && b.bin !== 'DEFAULT'; });
      if (fbmBin) {
        primaryBin = fbmBin.bin;
      } else {
        var anyBin = prod.bin_quantities.find(function(b) { return b.bin && b.bin !== 'DEFAULT'; });
        if (anyBin) primaryBin = anyBin.bin;
      }
    } else if (prod.current_bins) {
      primaryBin = String(prod.current_bins).split(',')[0].trim();
    } else if (prod.bin_location) {
      primaryBin = prod.bin_location;
    }
    setUnexpectedBin(primaryBin && primaryBin !== 'DEFAULT' ? primaryBin : '');
    setUnexpectedQty('1');
    setUnexpectedNotes('');
    setScanError('');
    setScanMode('unexpected');
  };

  var handleScanSubmit = async function(val) {
    var code = (val || scanInput).trim();
    if (!code) { showToast('Scan barcode or search by SKU, FNSKU, Product Name, Vendor SKU', 'error'); return; }
    setScanLoading(true); setScanError(''); setScanResult(null); setScanResults([]); setCatalogMatches([]); setCatalogMatchedProduct(null);
    try {
      var result = await api('/receiving/' + poId + '/scan-lookup', { method: 'POST', body: JSON.stringify({ barcode: code }) });
      if (result.multiple && result.items) {
        // Multiple matches — show pick list
        setScanResults(result.items);
        setScanMode('pick');
      } else {
        // Single match — go straight to result
        setScanResult(result);
        var remaining = result.qty_ordered - (result.qty_received || 0);
        setScanQty(remaining > 0 ? String(remaining) : '1');
        setScanBin(result.bin_location && result.bin_location !== 'DEFAULT' ? result.bin_location : '');
        setScanMode('result');
      }
    } catch (e) {
      if (e.message && e.message.includes('No matching item')) {
        // No PO match — fall through to global catalog lookup
        try {
          var catRes = await api('/receiving/product-lookup', { method: 'POST', body: JSON.stringify({ barcode: code, vendor_id: po && po.vendor_id ? po.vendor_id : null, include_archived: true }) });
          var matches = (catRes && catRes.results) || [];
          // FBM preference: if exactly one FBM match exists among many AND the input is a real
          // barcode (multiple matches usually = FBM/FBA variants of one product), auto-pick the FBM.
          // For keyword searches, always show the pick list so the user picks the right item.
          var isFbmMatch = function(m) {
            return (m.wh_code && /^FBM/i.test(m.wh_code)) || (m.sku && /-FBM(?:-|$)/i.test(m.sku));
          };
          var inputIsBarcode = /^\d{8,14}$/.test(code);
          var fbmMatches = matches.filter(isFbmMatch);
          var preferred = null;
          if (matches.length === 1) preferred = matches[0];
          else if (inputIsBarcode && fbmMatches.length === 1) preferred = fbmMatches[0];
          if (preferred) {
            applyCatalogMatch(preferred, code);
          } else if (matches.length > 1) {
            setCatalogMatches(matches);
            // Don't pre-fill barcode with the search keyword — it's not a barcode
            setScanMode('catalog-pick');
          } else {
            setScanError('No matching item on this PO or our catalog for: ' + code);
            // If user typed a real barcode, keep it; otherwise put it in title (likely a keyword)
            var looksLikeBarcode = /^\d{8,14}$/.test(code);
            if (looksLikeBarcode) { setUnexpectedBarcode(code); setUnexpectedTitle(''); }
            else { setUnexpectedBarcode(''); setUnexpectedTitle(code); }
            setScanMode('scan');
          }
        } catch (e2) {
          // Catalog lookup also missed or failed — original "Add Unexpected Item" path
          setScanError('No matching item on this PO or our catalog for: ' + code + (e2.message && !e2.message.includes('No product') ? ' — ' + e2.message : ''));
          var looksLikeBarcode2 = /^\d{8,14}$/.test(code);
          if (looksLikeBarcode2) { setUnexpectedBarcode(code); setUnexpectedTitle(''); }
          else { setUnexpectedBarcode(''); setUnexpectedTitle(code); }
          setScanMode('scan');
        }
      } else {
        setScanError(e.message);
        setScanMode('scan');
      }
    }
    setScanLoading(false);
  };

  var pickScanItem = function(item) {
    setScanResult(item);
    var remaining = item.qty_ordered - (item.qty_received || 0);
    setScanQty(remaining > 0 ? String(remaining) : '1');
    setScanBin(item.bin_location && item.bin_location !== 'DEFAULT' ? item.bin_location : '');
    setScanMode('result');
  };

  var handleScanReceive = async function(skipOverReceiveCheck) {
    if (scanLoading) return;
    if (!scanResult) return;
    var qty = parseInt(scanQty);
    if (!qty || qty <= 0) { showToast('Enter a valid quantity', 'error'); return; }
    if (!scanBin.trim() || scanBin.trim() === 'DEFAULT') { showToast('Set a bin location before receiving', 'error'); return; }
    var remaining = parseInt(scanResult.qty_ordered) - (parseInt(scanResult.qty_received) || 0);
    // Skip over-receive prompts entirely for unexpected items (no order qty to compare against)
    if (!scanResult.is_unexpected) {
      if (!skipOverReceiveCheck && remaining <= 0) {
        if (!(await appConfirm('This item has already been received in full (' + (parseInt(scanResult.qty_received) || 0) + '/' + scanResult.qty_ordered + '). Receive ' + qty + ' additional units?'))) return;
      } else if (!skipOverReceiveCheck && qty > remaining) {
        if (!(await appConfirm('This will over-receive by ' + (qty - remaining) + ' units (' + ((parseInt(scanResult.qty_received) || 0) + qty) + '/' + scanResult.qty_ordered + ' total). Continue?'))) return;
      }
    }
    setScanLoading(true);
    try {
      var result = await api('/receiving/' + poId + '/receive', {
        method: 'POST', body: JSON.stringify({ items: [{ line_item_id: scanResult.id, qty_received: qty, bin_location: scanBin.trim() || null }], notes: scanNotes.trim() || '' })
      });
      showToast('Received ' + qty + ' × ' + scanResult.sku);
      if (window.fireConfetti) window.fireConfetti();
      // Track in scan history for undo
      // Get the latest log for this item to support undo
      var hist = await api('/receiving/' + poId + '/history');
      var latestLog = hist.find(function(h) { return h.entry_type === 'receive' && h.line_item_id === scanResult.id; });
      setScanHistory(function(prev) {
        return [{ logId: latestLog ? latestLog.id : null, sku: scanResult.sku, qty: qty, time: new Date().toLocaleTimeString(), vendor_title: scanResult.vendor_title }].concat(prev);
      });
      // Reset for next scan
      setScanInput(''); setScanResult(null); setScanQty(''); setScanNotes(''); setScanMode('scan');
      setTimeout(function() { if (scanInputRef.current) scanInputRef.current.focus(); }, 100);
    } catch (e) { showToast(e.message, 'error'); }
    setScanLoading(false);
  };

  var handleScanUndo = async function(logId, idx) {
    if (!logId) { showToast('Cannot undo — no log ID', 'error'); return; }
    try {
      var result = await api('/receiving/' + poId + '/undo/' + logId, { method: 'DELETE' });
      showToast('Undid receive of ' + result.qty + ' × ' + result.sku);
      setScanHistory(function(prev) { return prev.filter(function(_, i) { return i !== idx; }); });
    } catch (e) { showToast(e.message, 'error'); }
  };

  var handleUnexpectedItem = async function() {
    if (scanLoading) return;
    var code = unexpectedBarcode.trim();
    // Empty barcode is allowed — server treats it as a placeholder ("UNEXPECTED-NOBC-{po}-{n}").
    // If the user enters a barcode, it must be 12-13 digits unless we have a catalog match.
    if (code && !catalogMatchedProduct && !/^\d{12,13}$/.test(code)) { showToast('Barcode must be 12 or 13 digits (UPC/EAN), or leave blank for no barcode', 'error'); return; }
    // Require either a barcode or a vendor title so the line item is identifiable
    if (!code && !unexpectedTitle.trim()) { showToast('Enter a barcode or a vendor title', 'error'); return; }
    // If no barcode, confirm the item really has no printed barcode before proceeding
    if (!code && !catalogMatchedProduct) {
      if (!(await appConfirm('No barcode entered. Confirm this item has no printed barcode (UPC/EAN) on the package?'))) return;
    }
    // Photos are mandatory for manually-entered unexpected items (no catalog match)
    if (!catalogMatchedProduct && photoCount === 0) {
      showToast('At least one photo is required for unexpected items not in our catalog. Use "Add photos" to scan the QR.', 'error');
      return;
    }
    var qty = parseInt(unexpectedQty);
    if (!qty || qty <= 0) { showToast('Enter a valid quantity', 'error'); return; }
    // Extras are always physically placed in a holding bin (an FBM home bin, or an FBA/WFS
    // surplus bin), so a bin is required for every unexpected receive — the server enforces
    // this too. Keeping the bin on the extra's own line is what stops it bleeding onto the
    // planned (shipped-to-marketplace) units on FBA/WFS work orders.
    if (!unexpectedBin.trim() || unexpectedBin.trim() === 'DEFAULT') { showToast('Set a bin location before receiving', 'error'); return; }
    setScanLoading(true);
    try {
      var result = await api('/receiving/unexpected-item', {
        method: 'POST', body: JSON.stringify({ po_id: parseInt(poId), product_id: catalogMatchedProduct ? catalogMatchedProduct.id : null, barcode: code, qty_received: qty, vendor_title: unexpectedTitle.trim() || null, vendor_sku: unexpectedVendorSku.trim() || null, bin_location: unexpectedBin.trim() || null, notes: unexpectedNotes || 'Unexpected item', photo_token: photoToken || null })
      });
      showToast('Added unexpected item: ' + result.sku + (result.catalog_match ? ' (catalog match)' : ''));
      if (window.fireConfetti) window.fireConfetti();
      setScanHistory(function(prev) {
        return [{ logId: null, sku: result.sku, qty: qty, time: new Date().toLocaleTimeString(), vendor_title: result.vendor_title, unexpected: true }].concat(prev);
      });
      // Reset
      setScanInput(''); setScanError(''); setUnexpectedBarcode(''); setUnexpectedQty('1'); setUnexpectedTitle(''); setUnexpectedVendorSku(''); setUnexpectedBin('');
      setUnexpectedNotes(''); setScanMode('scan');
      setCatalogMatchedProduct(null); setCatalogMatches([]);
      setPhotoToken(null); setPhotoCount(0); setPhotoResetSignal(function(s) { return s + 1; });
      setTimeout(function() { if (scanInputRef.current) scanInputRef.current.focus(); }, 100);
    } catch (e) { showToast(e.message, 'error'); }
    setScanLoading(false);
  };

  var isFbmPo = function() {
    if (!po) return false;
    var wh = (po.wh_code || '').toUpperCase();
    var FBA_WH_PREFIXES = ['FBAUS','FBACAN','FBAHAZ','FBACANDI','FBACANHAZ','FBAUSDI','FBAUSPR','FBAAWDDI'];
    var WFS_WH_PREFIXES = ['WFS'];
    if (FBA_WH_PREFIXES.some(function(c) { return wh.startsWith(c); })) return false;
    if (WFS_WH_PREFIXES.some(function(c) { return wh.startsWith(c); })) return false;
    return true;
  };

  var itemSelectedIds = Object.keys(itemSelected).filter(function(k) { return itemSelected[k]; }).map(Number);
  var itemSelCount = itemSelectedIds.length;

  var toggleItem = function(id) {
    var next = Object.assign({}, itemSelected); next[id] = !next[id]; setItemSelected(next);
  };
  var toggleAllItems = function() {
    if (!po) return;
    var items = po.line_items || [];
    if (itemSelCount === items.length && items.length > 0) setItemSelected({});
    else { var next = {}; items.forEach(function(li) { next[li.id] = true; }); setItemSelected(next); }
  };

  var downloadWO = async function() {
    try {
      var token = getAuthToken();
      var resp = await fetch('/api/receiving/' + poId + '/export-wo', { credentials: 'include' });
      if (!resp.ok) { var err = await resp.json().catch(function() { return {}; }); throw new Error(err.error || 'Export failed'); }
      var blob = await resp.blob();
      var filename = resp.headers.get('content-disposition');
      filename = filename ? filename.split('filename=')[1].replace(/"/g, '') : 'WO_export.xlsx';
      var url = URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { showToast(e.message || 'Export failed', 'error'); }
  };

  var FBA_WH_PREFIXES = ['FBAUS','FBACAN','FBAHAZ','FBACANDI','FBACANHAZ','FBAUSDI','FBAUSPR','FBAAWDDI'];
  var WFS_WH_PREFIXES = ['WFS'];
  // Mercado Libre warehouses (MELI, MELIDI): the label carries the ML Code, not the UPC
  // (Anthony 2026-09-29, approved). Templates come from Settings > Templates > MELI.
  var MELI_WH_PREFIXES = ['MELI'];
  var getPoLabelCategory = function() {
    var wh = (po && po.wh_code || '').toUpperCase();
    if (FBA_WH_PREFIXES.some(function(c) { return wh.startsWith(c); })) return 'fba';
    if (WFS_WH_PREFIXES.some(function(c) { return wh.startsWith(c); })) return 'wfs';
    if (MELI_WH_PREFIXES.some(function(c) { return wh.startsWith(c); })) return 'meli';
    return 'fbm';
  };
  // The word on the buttons and the window title: FNSKU, ML or Barcode labels.
  var poLabelWord = function(cat) { return cat === 'fba' ? 'FNSKU' : cat === 'meli' ? 'ML' : 'Barcode'; };
  // What the barcode encodes for this PO's channel. Empty for a Mercado Libre line without an
  // ML Code: that line is refused rather than printed with a barcode Full cannot read.
  var poLabelBarcodeValue = function(cat, item) {
    if (cat === 'fba') return item.fnsku || item.sku || '';
    if (cat === 'meli') return String(item.ml_code || '').trim();
    return item.barcode || item.sku || '';
  };

  var printLabelsOpen = function(itemsOverride) {
    var items = itemsOverride || (po.line_items || []).filter(function(li) { return itemSelectedIds.includes(li.id); });
    if (items.length === 0) { showToast('Select items to print labels', 'error'); return; }
    var cat = getPoLabelCategory();
    if (cat === 'meli') {
      var noCode = items.filter(function(li) { return !poLabelBarcodeValue(cat, li); });
      if (noCode.length) {
        showToast('No ML Code on ' + noCode.map(function(li) { return li.sku; }).join(', ') + '. A Mercado Libre label needs the ML Code; the product gets one once it is listed there.', 'error');
        items = items.filter(function(li) { return poLabelBarcodeValue(cat, li); });
        if (!items.length) return;
      }
    }
    var qtyMap = {};
    items.forEach(function(li) { qtyMap[li.id] = li.qty_ordered || 1; });
    setPrintQtyOverride(qtyMap);
    var catTpls = labelTemplates.filter(function(t) { return (t.category || 'fba') === cat; });
    var def = catTpls.find(function(t) { return t.is_default; });
    setSelectedLabelTpl(def ? def.id : (catTpls.length > 0 ? catTpls[0].id : null));
    setPrintLabelModal({ items: items, category: cat });
  };

  var executeLabelPrint = function() {
    if (!printLabelModal) return;
    var cat = printLabelModal.category;
    var tpl = labelTemplates.find(function(t) { return t.id === selectedLabelTpl; });
    var items = printLabelModal.items;
    var widthMm = tpl ? parseFloat(tpl.width_mm) : 57.15;
    var heightMm = tpl ? parseFloat(tpl.height_mm) : 31.75;
    var fontSize = tpl ? (tpl.font_size || 12) : 12;
    var fields = tpl ? (tpl.fields || ['fnsku', 'sku', 'title']) : ['fnsku', 'sku', 'title'];
    var isSheet = tpl ? tpl.label_type === 'sheet' : false;
    var cols = tpl ? (tpl.columns || 1) : 1;
    var rowsPerSheet = tpl ? (tpl.rows_per_sheet || 1) : 1;
    var pageW = tpl && tpl.page_width_mm ? parseFloat(tpl.page_width_mm) : 215.9;
    var pageH = tpl && tpl.page_height_mm ? parseFloat(tpl.page_height_mm) : 279.4;
    var getFieldVal = function(item, field) {
      switch (field) {
        case 'fnsku': return item.fnsku || '';
        case 'sku': return item.sku || '';
        case 'title': return item.vendor_title || '';
        case 'barcode': return item.barcode || '';
        case 'asin': return item.asin || '';
        case 'condition': return 'New';
        case 'expiry': return '';
        default: return '';
      }
    };
    // how many title lines this template has room for (two where they fit, Anthony 2026-09-29)
    var titleFit = productLabelTitleFit(heightMm, fontSize, Math.max(4, heightMm * 0.28));
    var allLabels = [];
    items.forEach(function(item) {
      var count = printQtyOverride[item.id] || 1;
      // FBA uses FNSKU, MELI the ML Code, FBM/WFS the product barcode
      var barcodeVal = poLabelBarcodeValue(cat, item);
      if (cat === 'meli' && !barcodeVal) return;
      for (var i = 0; i < count; i++) {
        var lbl = '<div style="width:' + widthMm + 'mm;height:' + heightMm + 'mm;box-sizing:border-box;border:1px solid #ccc;padding:2mm;display:flex;flex-direction:column;justify-content:center;align-items:center;overflow:hidden;page-break-inside:avoid;">';
        // 1. Barcode
        var barcodeWidthMm = widthMm - 4;
        var barcodeHeightMm = Math.max(4, heightMm * 0.28);
        var barcodeImg = barcodeVal ? generateBarcodeImg(barcodeVal, barcodeWidthMm, barcodeHeightMm) : '';
        lbl += '<div style="text-align:center;margin-bottom:0.3mm;">' + barcodeImg + '</div>';
        // 2. Barcode text
        if (barcodeVal) lbl += '<div style="font-family:monospace;font-size:' + Math.max(5, fontSize - 4) + 'pt;font-weight:bold;letter-spacing:0.5px;margin-bottom:0.3mm;text-align:center;">' + escHtml(barcodeVal) + '</div>';
        // 3. Title
        var titleVal = getFieldVal(item, 'title');
        if (titleVal && fields.indexOf('title') >= 0) lbl += '<div style="' + productLabelTitleCss(titleFit.pt, titleFit.lines) + '">' + escHtml(titleVal) + '</div>'; // two lines, never cut (Anthony 2026-09-29)
        // 4. Bottom row: Condition | SKU | Expiry
        var bottomParts = [];
        if (fields.indexOf('condition') >= 0) { var cv = getFieldVal(item, 'condition'); if (cv) bottomParts.push(cv); }
        // Mercado Libre's own label file prints "SKU: " before the SKU
        if (fields.indexOf('sku') >= 0) { var sv = getFieldVal(item, 'sku'); if (sv) bottomParts.push((cat === 'meli' ? 'SKU: ' : '') + sv); }
        if (fields.indexOf('expiry') >= 0) { var ev = getFieldVal(item, 'expiry'); if (ev) bottomParts.push('Exp: ' + ev); }
        if (bottomParts.length > 0) lbl += '<div style="font-size:' + Math.max(4, fontSize - 6) + 'pt;color:#333;text-align:center;margin-top:0.3mm;">' + bottomParts.map(escHtml).join(' | ') + '</div>';
        lbl += '</div>';
        allLabels.push(lbl);
      }
    });
    if (!allLabels.length) { showToast('Nothing to print', 'error'); return; }
    var pw = window.open('', '_blank', 'width=600,height=800');
    if (!pw) return;
    var html;
    if (isSheet) {
      var mH = (pageW - (cols * widthMm)) / (cols + 1);
      var mV = (pageH - (rowsPerSheet * heightMm)) / (rowsPerSheet + 1);
      var pgs = []; var pp = cols * rowsPerSheet;
      for (var p = 0; p < allLabels.length; p += pp) {
        var sl = allLabels.slice(p, p + pp);
        var ph = '<div style="width:' + pageW + 'mm;height:' + pageH + 'mm;box-sizing:border-box;padding:' + mV + 'mm ' + mH + 'mm;display:flex;flex-wrap:wrap;align-content:flex-start;gap:0;page-break-after:always;"><div style="display:grid;grid-template-columns:repeat(' + cols + ',' + widthMm + 'mm);grid-template-rows:repeat(' + rowsPerSheet + ',' + heightMm + 'mm);gap:0;width:100%;">';
        sl.forEach(function(l) { ph += l; }); ph += '</div></div>'; pgs.push(ph);
      }
      html = pgs.join('');
    } else {
      html = '<div style="display:flex;flex-wrap:wrap;gap:0;justify-content:center;">'; allLabels.forEach(function(l) { html += l; }); html += '</div>';
    }
    // Drive printing from the opener — an inline <script>window.print()</script> in the popup is
    // blocked by the page CSP (and window.onload is unreliable for document.write popups), which
    // left the label stuck in print-preview until the user printed it manually.
    pw.document.write('<!DOCTYPE html><html><head><title>Labels — ' + escHtml(po.po_number) + '</title><style>@media print{body{margin:0;}@page{size:' + (isSheet ? pageW + 'mm ' + pageH + 'mm' : widthMm + 'mm ' + heightMm + 'mm') + ';margin:0;}div{page-break-inside:avoid;}}</style></head><body style="font-family:Arial,sans-serif;margin:0;padding:0;">' + html + '</body></html>');
    pw.document.close();
    setTimeout(function() { try { pw.focus(); pw.print(); } catch (e) { /* popup closed */ } }, 500);
    setPrintLabelModal(null);
  };

  if (!po && recvLoadError) return React.createElement('div', { style: { color: C.textMuted, padding: 40, textAlign: 'center' } },
    React.createElement('div', { style: { color: C.danger, marginBottom: 12 } }, 'Could Not Load This PO: ' + recvLoadError),
    React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: function() { setRecvLoadError(null); load(); } }, 'Retry'));
  if (!po) return React.createElement('div', { style: { color: C.textMuted, padding: 40, textAlign: 'center' } }, 'Loading...');

  var receiveCols = [
    { key: '_select', label: '', locked: true, stopClick: true, noFilter: true, style: { width: 44 },
      headerRender: function() {
        var items = po ? (po.line_items || []) : [];
        var allChecked = items.length > 0 && items.every(function(li) { return itemSelected[li.id]; });
        var someChecked = !allChecked && items.some(function(li) { return itemSelected[li.id]; });
        return React.createElement('div', {
          onClick: function(e) { e.stopPropagation(); toggleAllItems(); },
          style: { width: 32, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', userSelect: 'none' }
        },
          React.createElement('div', {
            style: { width: 18, height: 18, borderRadius: 4, border: '2px solid ' + (allChecked || someChecked ? C.accent : C.textDim),
              background: allChecked ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }
          },
            allChecked && React.createElement(LIcon, { name: 'check', size: 12, color: '#fff' }),
            someChecked && !allChecked && React.createElement('span', { style: { color: C.accent, fontSize: 12, fontWeight: 700, lineHeight: 1 } }, '–')
          )
        );
      },
      render: function(li) {
      var checked = !!itemSelected[li.id];
      return React.createElement('div', {
        onClick: function(e) { e.stopPropagation(); toggleItem(li.id); },
        style: { width: 32, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', userSelect: 'none' }
      }, React.createElement('div', { style: { width: 18, height: 18, borderRadius: 4, border: '2px solid ' + (checked ? C.accent : C.textDim), background: checked ? C.accent : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' } },
        checked && React.createElement(LIcon, { name: 'check', size: 12, color: '#fff' })
      ));
    }},
    { key: '_image', label: '', locked: true, noFilter: true, style: { width: 44 }, stopClick: true, render: function(li) {
      // Combine product images with receipt photos so both are scrollable in the lightbox.
      var prodImgs = (li.images && li.images.length) ? li.images.slice() : (li.image_1_url ? [li.image_1_url] : []);
      var receiptUrls = (li.receipt_photos || []).map(function(p) { return p.url; });
      var allImgs = prodImgs.concat(receiptUrls);
      var thumb = allImgs[0];
      if (!thumb) {
        return React.createElement('div', { style: { width: 36, height: 36, borderRadius: 6, background: C.surface, display: 'flex', alignItems: 'center', justifyContent: 'center' } },
          React.createElement(LIcon, { name: 'package', size: 16, color: C.textDim }));
      }
      return React.createElement(ProductThumb, { src: thumbUrl(thumb, 72), size: 36, iconSize: 16,
        onClick: function() { setLightbox({ images: allImgs, index: 0 }); } });
    }},
    { key: 'sku', label: 'SKU', copyable: true, style: { fontWeight: 600, fontFamily: 'inherit', color: C.accent }, render: function(li) {
      return React.createElement('span', { style: { fontWeight: 600, fontFamily: 'inherit', color: C.accent } },
        li.is_unexpected && React.createElement('span', { title: 'Unexpected item', style: { background: '#FBBF24' + '20', color: '#B45309', fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 4, marginRight: 4, fontFamily: 'inherit', verticalAlign: 'middle' } }, 'UNEXPECTED'),
        li.sku);
    }},
    { key: 'vendor_sku', label: 'Vendor SKU', copyable: true },
    { key: 'vendor_title', label: 'Vendor Title', wrap: true, copyable: true, style: { color: C.text }, render: function(li) {
      var t = li.vendor_title || '';
      return React.createElement('span', { title: t, style: { display: 'block', whiteSpace: 'normal', lineHeight: 1.3, wordBreak: 'break-word' } }, t);
    }},
    { key: 'asin', label: 'ASIN', defaultVisible: true, copyable: true, render: function(li) {
      if (!li.asin) return '';
      return React.createElement('a', { href: 'https://www.amazon.com/dp/' + li.asin, target: '_blank', rel: 'noopener noreferrer',
        onClick: function(e) { e.stopPropagation(); },
        style: { color: C.accent, textDecoration: 'none', fontFamily: 'inherit', fontSize: 12 } }, li.asin);
    }},
    { key: 'barcode', label: 'Barcode', defaultVisible: true, copyable: true },
    { key: 'fnsku', label: 'FNSKU', defaultVisible: false, copyable: true },
    { key: 'bin_location', label: 'Bin Location', sortKey: '_bin_sort', stopClick: true, render: function(li) {
      var val = editingBins[li.id] !== undefined ? editingBins[li.id] : (li.bin_location || '');
      var isEditing = editingBins[li.id] !== undefined;
      return React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 4 } },
        isEditing
          ? React.createElement('input', {
              value: val, autoFocus: true,
              onChange: function(e) { setEditingBins(function(prev) { var n = Object.assign({}, prev); n[li.id] = e.target.value.toUpperCase(); return n; }); },
              onKeyDown: function(e) {
                if (e.key === 'Enter') { saveBin(li.id); }
                if (e.key === 'Escape') { setEditingBins(function(prev) { var n = Object.assign({}, prev); delete n[li.id]; return n; }); }
              },
              onBlur: function() { saveBin(li.id); },
              className: 'dt-input',
              style: { width: 90, background: C.bg, borderWidth: 1, borderStyle: 'solid', borderColor: C.border, color: C.text, padding: '3px 6px', borderRadius: 4, fontSize: 12, fontFamily: 'inherit', outline: 'none' }
            })
          : React.createElement('span', {
              onClick: function(e) { e.stopPropagation(); setEditingBins(function(prev) { var n = Object.assign({}, prev); n[li.id] = li.bin_location && li.bin_location !== 'DEFAULT' ? li.bin_location : ''; return n; }); },
              style: { cursor: 'pointer',
                border: '1px dashed ' + (li.bin_location && li.bin_location !== 'DEFAULT' ? C.border : 'transparent'),
                minWidth: 60, fontSize: 11, fontWeight: !li.bin_location || li.bin_location === 'DEFAULT' ? 700 : 400,
                fontFamily: 'inherit', display: 'inline-block',
                color: li.bin_location && li.bin_location !== 'DEFAULT' ? C.text : (isFbmPo() ? C.danger : C.textDim),
                background: !li.bin_location || li.bin_location === 'DEFAULT' ? (isFbmPo() ? C.danger + '18' : 'transparent') : 'transparent',
                borderRadius: !li.bin_location || li.bin_location === 'DEFAULT' ? 99 : 4,
                padding: !li.bin_location || li.bin_location === 'DEFAULT' ? '2px 10px' : '3px 6px' }
            }, li.bin_location && li.bin_location !== 'DEFAULT' ? li.bin_location : (isFbmPo() ? 'MISSING' : '—'))
      );
    }},
    { key: 'prep_instructions', label: 'Prep', defaultVisible: false, render: function(li) {
      if (!li.prep_instructions) return React.createElement('span', { style: { color: C.textDim, fontSize: 11 } }, '—');
      return React.createElement('span', { title: li.prep_instructions, style: { fontSize: 11, color: '#B45309', fontWeight: 500, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' } }, li.prep_instructions);
    }},
    { key: 'qty_ordered', label: 'Units', style: { fontWeight: 500 } },
    { key: 'qty_received', label: 'Received', render: function(li) {
      var miss = parseInt(li.qty_missing || 0);
      var accounted = (li.qty_received || 0) + miss;
      var color;
      if (li.is_unexpected) color = C.danger;
      else if (li.qty_ordered > 0 && accounted === li.qty_ordered) color = C.success;
      else if (accounted > 0) color = C.warning; // partial OR over-received
      else color = C.textDim;
      return React.createElement('span', { style: { fontWeight: 600, color: color } },
        (li.qty_received || 0) + '/' + li.qty_ordered,
        miss > 0 ? React.createElement('span', { style: { color: C.danger, fontSize: 10, marginLeft: 4 } }, '(' + miss + ' missing)') : null
      );
    }},
    { key: 'remaining', label: 'Remaining', render: function(li) {
      var miss = parseInt(li.qty_missing || 0);
      var rem = li.qty_ordered - (li.qty_received || 0) - miss;
      return React.createElement('span', { style: { fontWeight: 500, color: rem > 0 ? C.warning : C.success } }, rem > 0 ? rem : React.createElement(LIcon, { name: 'check', size: 14, color: C.success }));
    }},
    { key: '_print', label: '', locked: true, noFilter: true, style: { width: 150 }, stopClick: true, render: function(li) {
      var miss = parseInt(li.qty_missing || 0);
      var rem = li.qty_ordered - (li.qty_received || 0) - miss;
      var canMarkMissing = isFbmPo() && rem > 0 && !miss;
      return React.createElement('div', { style: { display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end' } },
        canMarkMissing && React.createElement('button', {
          onClick: function(e) {
            e.stopPropagation();
            openMarkMissingModal([{ line_item_id: li.id, sku: li.sku, vendor_title: li.vendor_title || '', qty: rem }]);
          },
          title: 'Mark ' + rem + ' missing',
          style: { background: C.danger + '18', border: '1px solid ' + C.danger + '44', color: C.danger, padding: '4px 10px', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit', whiteSpace: 'nowrap' }
        }, 'Mark Missing'),
        React.createElement('button', {
          onClick: function(e) { e.stopPropagation(); printLabelsOpen([li]); },
          title: 'Print ' + poLabelWord(getPoLabelCategory()) + ' Label',
          style: { background: 'none', border: 'none', cursor: 'pointer', color: C.accent, padding: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }
        }, React.createElement(LIcon, { name: 'printer', size: 14 }))
      );
    }}
  ];

  return React.createElement('div', null,
    // Header
    React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 } },
      React.createElement('button', { onClick: function() { onNavigate('receiving'); }, style: { background: 'none', border: 'none', color: C.textMuted, cursor: 'pointer', fontSize: 18, padding: 0, display: 'flex', alignItems: 'center' } }, React.createElement(LIcon, { name: 'arrow-left', size: 20 })),
      React.createElement('div', { style: { flex: 1 } },
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
          React.createElement('h2', { style: { margin: 0, fontSize: 20, fontWeight: 600, color: C.text } }, 'Receive — PO ' + po.po_number),
          React.createElement(Badge, { status: po.status }),
          po.has_errored_fba_items && React.createElement('span', {
            title: 'Un-binned errored FBA items on this PO — review on the Errored tab of the related FBA plan',
            style: { padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 700, background: C.danger + '22', color: C.danger, textTransform: 'uppercase', letterSpacing: '0.05em' }
          }, 'Errored')
        ),
        React.createElement('p', { style: { margin: '2px 0 0', fontSize: 13, color: C.textMuted } }, po.vendor_name + ' → ' + po.wh_code + ' — ' + po.wh_name)
      ),
      isFbmPo() && React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: openScanModal, style: { flexShrink: 0 } }, React.createElement(LIcon, { name: 'scan-line', size: 14 }), ' Receive'),
      !isFbmPo() && React.createElement('div', { style: { background: C.warningSoft, border: '1px solid ' + C.warning + '33', borderRadius: 6, padding: '6px 12px', fontSize: 11, color: C.warning, display: 'flex', alignItems: 'center', gap: 6 } },
        React.createElement(LIcon, { name: 'info', size: 13 }), 'Read-only — receive via ' + ((po.wh_code || '').toUpperCase().startsWith('WFS') ? 'WFS' : 'FBA') + ' Shipments module'),
      React.createElement(Btn, { size: 'sm', onClick: downloadWO, style: { flexShrink: 0 } }, React.createElement(LIcon, { name: 'download', size: 14 }), ' Export Work Order'),
      // Check-in info
      React.createElement('div', { style: { display: 'flex', gap: 16, alignItems: 'flex-start' } },
        React.createElement('div', { style: { textAlign: 'right' } },
          React.createElement('div', { style: { fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 } }, 'WH Location'),
          editingLocation
            ? React.createElement('div', { style: { display: 'flex', gap: 4, alignItems: 'center' } },
                React.createElement('input', {
                  value: locationVal, onChange: function(e) { locationDirtyRef.current = true; setLocationVal(e.target.value.toUpperCase()); },
                  onKeyDown: function(e) { if (e.key === 'Enter') saveLocation(); if (e.key === 'Escape') { setEditingLocation(false); locationDirtyRef.current = false; setLocationVal(po.warehouse_location || ''); } },
                  autoFocus: true,
                  placeholder: 'LOC...',
                  style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '4px 8px', borderRadius: 4, fontSize: 13, fontFamily: 'inherit', width: 100 }
                }),
                React.createElement('button', { onClick: saveLocation, style: { background: C.accent, border: 'none', color: '#fff', padding: '4px 8px', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center' } }, React.createElement(LIcon, { name: 'check', size: 14, color: '#fff' })),
                React.createElement('button', { onClick: function() { setEditingLocation(false); locationDirtyRef.current = false; setLocationVal(po.warehouse_location || ''); }, style: { background: 'none', border: '1px solid ' + C.border, color: C.textMuted, padding: '4px 8px', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center' } }, React.createElement(LIcon, { name: 'x', size: 14 }))
              )
            : React.createElement('div', {
                onClick: function() { setEditingLocation(true); },
                style: { cursor: 'pointer', fontSize: 13, fontWeight: 500, color: po.warehouse_location ? C.text : C.textDim, padding: '4px 8px', borderRadius: 4, border: '1px dashed ' + C.border, minWidth: 80, textAlign: 'center' }
              }, po.warehouse_location || 'Click to set')
        ),
        (!!(po.warehouse_location || po.checkin_pallets || po.checkin_boxes)) && React.createElement('div', { style: { textAlign: 'center' } },
          React.createElement('div', { style: { fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 } }, 'Pallets'),
          editingPallets
            ? React.createElement('div', { style: { display: 'flex', gap: 4, alignItems: 'center', justifyContent: 'center' } },
                React.createElement('input', {
                  type: 'number', min: 0, value: palletsVal, onChange: function(e) { setPalletsVal(e.target.value); },
                  onKeyDown: function(e) { if (e.key === 'Enter') savePallets(); if (e.key === 'Escape') setEditingPallets(false); },
                  autoFocus: true, placeholder: '0',
                  style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '4px 8px', borderRadius: 4, fontSize: 13, fontFamily: 'inherit', width: 60, textAlign: 'center' }
                }),
                React.createElement('button', { onClick: savePallets, style: { background: C.accent, border: 'none', color: '#fff', padding: '4px 8px', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center' } }, React.createElement(LIcon, { name: 'check', size: 14, color: '#fff' })),
                React.createElement('button', { onClick: function() { setEditingPallets(false); setPalletsVal(po.checkin_pallets ? String(po.checkin_pallets) : ''); }, style: { background: 'none', border: '1px solid ' + C.border, color: C.textMuted, padding: '4px 8px', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center' } }, React.createElement(LIcon, { name: 'x', size: 14 }))
              )
            : React.createElement('div', {
                onClick: function() { setEditingPallets(true); setPalletsVal(po.checkin_pallets ? String(po.checkin_pallets) : ''); },
                style: { cursor: 'pointer', fontSize: 13, fontWeight: 500, color: po.checkin_pallets ? C.text : C.textDim, padding: '4px 8px', borderRadius: 4, border: '1px dashed ' + C.border, minWidth: 50, textAlign: 'center' }
              }, po.checkin_pallets || '—')
        ),
        (!!(po.warehouse_location || po.checkin_pallets || po.checkin_boxes)) && React.createElement('div', { style: { textAlign: 'center' } },
          React.createElement('div', { style: { fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 } }, 'Boxes'),
          editingBoxes
            ? React.createElement('div', { style: { display: 'flex', gap: 4, alignItems: 'center', justifyContent: 'center' } },
                React.createElement('input', {
                  type: 'number', min: 0, value: boxesVal, onChange: function(e) { setBoxesVal(e.target.value); },
                  onKeyDown: function(e) { if (e.key === 'Enter') saveBoxes(); if (e.key === 'Escape') setEditingBoxes(false); },
                  autoFocus: true, placeholder: '0',
                  style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '4px 8px', borderRadius: 4, fontSize: 13, fontFamily: 'inherit', width: 60, textAlign: 'center' }
                }),
                React.createElement('button', { onClick: saveBoxes, style: { background: C.accent, border: 'none', color: '#fff', padding: '4px 8px', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center' } }, React.createElement(LIcon, { name: 'check', size: 14, color: '#fff' })),
                React.createElement('button', { onClick: function() { setEditingBoxes(false); setBoxesVal(po.checkin_boxes ? String(po.checkin_boxes) : ''); }, style: { background: 'none', border: '1px solid ' + C.border, color: C.textMuted, padding: '4px 8px', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center' } }, React.createElement(LIcon, { name: 'x', size: 14 }))
              )
            : React.createElement('div', {
                onClick: function() { setEditingBoxes(true); setBoxesVal(po.checkin_boxes ? String(po.checkin_boxes) : ''); },
                style: { cursor: 'pointer', fontSize: 13, fontWeight: 500, color: po.checkin_boxes ? C.text : C.textDim, padding: '4px 8px', borderRadius: 4, border: '1px dashed ' + C.border, minWidth: 50, textAlign: 'center' }
              }, po.checkin_boxes || '—')
        )
      )
    ),
    // Summary cards
    React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, marginBottom: 20 } },
      [
        { label: 'Total Ordered', val: po.total_units, clr: C.text },
        { label: 'Total Received', val: (po.line_items || []).reduce(function(s, li) { return s + (li.qty_received || 0); }, 0), clr: C.success },
        { label: 'Remaining', val: po.total_units - (po.line_items || []).reduce(function(s, li) { return s + (li.qty_received || 0); }, 0), clr: C.warning },
        { label: 'SKUs', val: po.sku_count, clr: C.accent },
      ].map(function(s) {
        return React.createElement(Card, { key: s.label, style: { padding: 12 } },
          React.createElement('div', { style: { fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' } }, s.label),
          React.createElement('div', { style: { fontSize: 20, fontWeight: 700, color: s.clr, marginTop: 2 } }, s.val)
        );
      })
    ),
    // Item search
    React.createElement('div', { style: { marginBottom: 12 } },
      React.createElement('div', { style: { position: 'relative' } },
        React.createElement(LIcon, { name: 'search', size: 14, color: C.textDim, style: { position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' } }),
        React.createElement('input', {
          value: itemFilter, onChange: function(e) { setItemFilter(e.target.value); },
          placeholder: 'Search by SKU, barcode, FNSKU, item name, vendor SKU...',
          className: 'dt-input',
          style: { width: '100%', background: C.bg, borderWidth: 1, borderStyle: 'solid', borderColor: C.border, color: C.text, padding: '9px 14px 9px 34px', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s, box-shadow 0.15s' }
        }),
        itemFilter && React.createElement('button', {
          onClick: function() { setItemFilter(''); },
          style: { position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: C.textDim, cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center' }
        }, React.createElement(LIcon, { name: 'x', size: 14 }))
      )
    ),
    // Tabs
    React.createElement('div', { style: { display: 'flex', gap: 0, borderBottom: '2px solid ' + C.border, marginBottom: 0 } },
      [{ key: 'receive', icon: 'inbox', label: 'Receive Items' }, { key: 'history', icon: 'clipboard-list', label: 'Receiving History' }, { key: 'intnotes', icon: 'message-square', label: 'Internal Notes' + (intNotes.length > 0 ? ' (' + intNotes.length + ')' : '') }].map(function(tab) {
        var isActive = activeTab === tab.key;
        return React.createElement('a', { key: tab.key, href: '/receiving/' + poId + (tab.key !== 'receive' ? '?tab=' + tab.key : ''),
          onClick: function(e) { if (e.ctrlKey || e.metaKey || e.shiftKey) return; e.preventDefault(); setActiveTab(tab.key); if (tab.key === 'history') loadHistory(); if (tab.key === 'intnotes') loadIntNotes(); },
          style: { background: 'none', border: 'none', borderBottom: isActive ? '2px solid ' + C.accent : '2px solid transparent',
            color: isActive ? C.accent : C.textMuted, padding: '10px 20px', fontSize: 13, fontWeight: isActive ? 600 : 400,
            cursor: 'pointer', fontFamily: 'inherit', marginBottom: -2, transition: 'all 0.15s', display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none' }
        }, React.createElement(LIcon, { name: tab.icon, size: 14 }), tab.label);
      })
    ),
    // Tab: Receive Items
    activeTab === 'receive' && React.createElement('div', { style: { marginTop: 16 } },
      React.createElement(DataTable, { columns: receiveCols, data: (function() {
        var all = po.line_items || [];
        if (!itemFilter.trim()) return all;
        var q = itemFilter.trim().toLowerCase();
        return all.filter(function(li) {
          return (li.sku && li.sku.toLowerCase().includes(q))
            || (li.vendor_title && li.vendor_title.toLowerCase().includes(q))
            || (li.vendor_sku && li.vendor_sku.toLowerCase().includes(q))
            || (li.barcode && li.barcode.toLowerCase().includes(q))
            || (li.fnsku && li.fnsku.toLowerCase().includes(q))
            || (li.asin && li.asin.toLowerCase().includes(q));
        });
      })().map(function(li) {
        var bin = li.bin_location;
        var hasBin = bin && bin !== 'DEFAULT';
        // Sort: real bins alphabetically first, then MISSING/empty last
        li._bin_sort = hasBin ? ('A_' + bin) : 'Z_MISSING';
        // Sort: remaining qty (incomplete first when desc, complete last)
        li._remaining = li.qty_ordered - (li.qty_received || 0);
        return li;
      }), storageKey: 'receiving-items', defaultSort: 'sku', defaultSortDir: 'asc', emptyTitle: itemFilter ? 'No matching items' : 'No line items', emptySub: itemFilter ? 'Try a different search term' : '',
        bulkActions: itemSelCount > 0 && isFbmPo() ? {
          count: itemSelCount,
          onClear: function() { setItemSelected({}); },
          items: [
            { icon: 'printer', label: 'Print ' + poLabelWord(getPoLabelCategory()) + ' Labels', onClick: printLabelsOpen },
            { icon: 'alert-circle', label: 'Mark Selected Missing', danger: true, onClick: function() {
              var selected = (po.line_items || []).filter(function(li) {
                if (!itemSelected[li.id]) return false;
                var rem = li.qty_ordered - (li.qty_received || 0) - parseInt(li.qty_missing || 0);
                return rem > 0 && !parseInt(li.qty_missing);
              });
              if (!selected.length) { showToast('No eligible items selected (already received or marked missing)', 'error'); return; }
              openMarkMissingModal(selected.map(function(li) {
                return { line_item_id: li.id, sku: li.sku, vendor_title: li.vendor_title || '', qty: li.qty_ordered - (li.qty_received || 0) };
              }));
            }},
          ]
        } : null
      }),
      (currentUser && (currentUser.role === 'admin' || currentUser.role === 'manager') &&
       po && po.status !== 'closed' && po.status !== 'cancelled') &&
      React.createElement(Card, { style: { marginTop: 12 } },
        React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' } },
          React.createElement('div', { style: { fontSize: 12, color: C.textMuted, lineHeight: 1.5, flex: 1, minWidth: 250 } },
            'Before closing this PO, please review for any discrepancies and make sure items are received to Zentail (FBM) or FBA/WFS.'
          ),
          React.createElement(Btn, { variant: 'primary', onClick: function() { setCloseModalOpen(true); } },
            React.createElement(LIcon, { name: 'lock', size: 14 }), ' Mark Closed')
        )
      )
    ),
    // Tab: Receiving History
    activeTab === 'history' && React.createElement('div', { style: { marginTop: 16 } },
      React.createElement(Card, { style: { maxHeight: 500, overflow: 'auto' } },
        history.length === 0 && React.createElement('div', { style: { textAlign: 'center', color: C.textDim, fontSize: 13, padding: 40 } }, 'No receiving history yet.'),
        history.map(function(log) {
          return React.createElement(ReceivingHistoryEntry, { key: log.id, log: log, currentUser: currentUser, onUndo: undoReceive,
            onPhotoClick: function(images, index) { setLightbox({ images: images, index: index }); } });
        })
      )
    ),
    // Tab: Internal Notes
    activeTab === 'intnotes' && React.createElement('div', { style: { marginTop: 16 } },
      React.createElement(Card, { style: { display: 'flex', flexDirection: 'column', height: 420 } },
        React.createElement('div', { style: { flex: 1, overflow: 'auto', padding: '12px 0', display: 'flex', flexDirection: 'column', gap: 8 } },
          intNotes.length === 0 && React.createElement('div', { style: { textAlign: 'center', color: C.textDim, fontSize: 13, padding: 40 } }, 'No notes yet. Start the conversation!'),
          intNotes.map(function(note) {
            var isMe = currentUser && currentUser.id === note.user_id;
            var time = new Date(note.created_at);
            var timeStr = time.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' + time.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
            return React.createElement('div', { key: note.id, style: { display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start', padding: '0 8px' } },
              React.createElement('div', { style: { fontSize: 10, color: C.textDim, marginBottom: 2, display: 'flex', alignItems: 'center', gap: 6 } },
                React.createElement('span', { style: { fontWeight: 600 } }, note.display_name || note.username),
                React.createElement('span', null, timeStr),
                isMe && React.createElement('button', { onClick: function() { deleteIntNote(note.id); },
                  style: { background: 'none', border: 'none', color: C.textDim, cursor: 'pointer', fontSize: 10, padding: 0, display: 'inline-flex', alignItems: 'center' } }, React.createElement(LIcon, { name: 'x', size: 10 }))
              ),
              React.createElement('div', { style: { background: isMe ? C.accent : C.surface, color: isMe ? '#fff' : C.text,
                padding: '8px 14px', borderRadius: 12, borderTopRightRadius: isMe ? 4 : 12, borderTopLeftRadius: isMe ? 12 : 4,
                maxWidth: '70%', fontSize: 13, lineHeight: 1.4, wordBreak: 'break-word',
                border: isMe ? 'none' : '1px solid ' + C.border } }, renderNoteBody(note.message), renderNoteAttachments(note))
            );
          }),
          React.createElement('div', { ref: intNotesEndRef })
        ),
        React.createElement('div', { style: { borderTop: '1px solid ' + C.border, padding: '12px 0 0', display: 'flex', gap: 8, alignItems: 'flex-end' } },
          React.createElement('div', { style: { flex: 1 } },
            React.createElement(MentionInput, {
              value: newIntNote, onChange: setNewIntNote,
              onKeyDown: function(e) { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendIntNote(); } },
              placeholder: 'Type a note... use @ to tag a teammate',
              rows: 2,
              style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '10px 14px', borderRadius: 8, fontSize: 13, outline: 'none' }
            })
          ),
          React.createElement(Btn, { variant: 'primary', size: 'sm', onClick: sendIntNote, disabled: !newIntNote.trim() }, 'Send')
        )
      )
    ),

    // ─── Image Lightbox ───
    lightbox && React.createElement('div', { onClick: function() { setLightbox(null); },
      style: { position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' } },
      React.createElement('button', { onClick: function() { setLightbox(null); },
        style: { position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 28, lineHeight: 1, padding: 8, zIndex: 10001 } }, '\u00D7'),
      lightbox.images.length > 1 && React.createElement('div', { style: { position: 'absolute', top: 20, left: '50%', transform: 'translateX(-50%)', color: '#fff', fontSize: 13, fontWeight: 600, zIndex: 10001 } },
        (lightbox.index + 1) + ' / ' + lightbox.images.length),
      lightbox.images.length > 1 && React.createElement('button', { onClick: function(e) { e.stopPropagation(); setLightbox(function(prev) { return { images: prev.images, index: (prev.index - 1 + prev.images.length) % prev.images.length }; }); },
        style: { position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 24, fontWeight: 700, width: 44, height: 44, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001 } }, '\u2039'),
      lightbox.images.length > 1 && React.createElement('button', { onClick: function(e) { e.stopPropagation(); setLightbox(function(prev) { return { images: prev.images, index: (prev.index + 1) % prev.images.length }; }); },
        style: { position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 24, fontWeight: 700, width: 44, height: 44, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001 } }, '\u203A'),
      React.createElement('img', { src: lightbox.images[lightbox.index], onClick: function(e) { e.stopPropagation(); },
        style: { maxWidth: 'calc(85vw * var(--invzoom, 1))', maxHeight: 'calc(85vh * var(--invzoom, 1))', objectFit: 'contain', borderRadius: 8, cursor: 'default' } }),
      lightbox.images.length > 1 && React.createElement('div', { onClick: function(e) { e.stopPropagation(); },
        style: { position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 8, padding: '8px 12px', background: 'rgba(0,0,0,0.6)', borderRadius: 8 } },
        lightbox.images.map(function(url, i) {
          return React.createElement('img', { key: i, src: url, onClick: function() { setLightbox(function(prev) { return { images: prev.images, index: i }; }); },
            style: { width: 52, height: 52, objectFit: 'cover', borderRadius: 6, border: '2px solid ' + (i === lightbox.index ? '#fff' : 'transparent'), cursor: 'pointer', opacity: i === lightbox.index ? 1 : 0.6, transition: 'all 0.15s' } });
        })
      )
    ),

    // ─── Print Labels Modal ───
    React.createElement(Modal, { open: !!printLabelModal, onClose: function() { setPrintLabelModal(null); }, title: 'Print ' + poLabelWord(printLabelModal && printLabelModal.category) + ' Labels', width: 520 },
      printLabelModal && React.createElement('div', null,
        React.createElement('div', { style: { marginBottom: 14 } },
          React.createElement('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', display: 'block', marginBottom: 6 } }, 'Label Template (' + (printLabelModal.category || 'fba').toUpperCase() + ')'),
          (function() {
            var catTpls = labelTemplates.filter(function(t) { return (t.category || 'fba') === printLabelModal.category; });
            return catTpls.length === 0
              ? React.createElement('div', { style: { fontSize: 12, color: C.textMuted, padding: '10px 0' } }, 'No ' + (printLabelModal.category || 'fba').toUpperCase() + ' label templates configured. Create templates in Settings > Templates.')
              : React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 6 } },
                  catTpls.map(function(tpl) {
                    var sel = selectedLabelTpl === tpl.id;
                    return React.createElement('button', { key: tpl.id, onClick: function() { setSelectedLabelTpl(tpl.id); },
                      style: { padding: '6px 12px', borderRadius: 6, border: '2px solid ' + (sel ? C.accent : C.border), background: sel ? C.accent + '15' : 'transparent', color: sel ? C.accent : C.text, cursor: 'pointer', fontSize: 11, fontFamily: 'inherit', fontWeight: sel ? 600 : 400, textAlign: 'left' } },
                      React.createElement('div', null, tpl.name),
                      React.createElement('div', { style: { fontSize: 9, color: C.textMuted, marginTop: 2 } }, tpl.label_type === 'sheet' ? tpl.columns + 'x' + tpl.rows_per_sheet + ' per sheet' : parseFloat(tpl.width_mm).toFixed(1) + 'x' + parseFloat(tpl.height_mm).toFixed(1) + 'mm'),
                      tpl.is_default && React.createElement('span', { style: { fontSize: 8, color: C.accent, fontWeight: 600 } }, ' DEFAULT')
                    );
                  })
                );
          })()
        ),
        React.createElement('div', { style: { marginBottom: 14 } },
          React.createElement('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', display: 'block', marginBottom: 6 } }, 'Items & Label Count'),
          (printLabelModal.items || []).map(function(item) {
            var barcodeVal = poLabelBarcodeValue(printLabelModal.category, item);
            return React.createElement('div', { key: item.id, style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid ' + C.border + '44' } },
              React.createElement('div', { style: { fontSize: 12, flex: 1, minWidth: 0 } },
                React.createElement('span', { style: { fontFamily: 'inherit', fontWeight: 500 } }, barcodeVal),
                ' — ', item.vendor_title || ''
              ),
              React.createElement('input', { type: 'number', min: 1, value: printQtyOverride[item.id] || 1,
                onChange: function(e) { var v = Math.max(1, parseInt(e.target.value) || 1); setPrintQtyOverride(function(prev) { var n = Object.assign({}, prev); n[item.id] = v; return n; }); },
                style: { width: 60, padding: '4px 6px', fontSize: 12, textAlign: 'center', border: '1px solid ' + C.border, borderRadius: 4, background: C.bg, color: C.text } })
            );
          }),
          React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 6, textAlign: 'right' } },
            'Total: ' + (printLabelModal.items || []).reduce(function(s, item) { return s + (printQtyOverride[item.id] || 1); }, 0) + ' labels')
        ),
        React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } },
          React.createElement(Btn, { onClick: function() { setPrintLabelModal(null); } }, 'Cancel'),
          React.createElement(Btn, { variant: 'primary', onClick: executeLabelPrint }, React.createElement(LIcon, { name: 'printer', size: 14 }), ' Print')
        )
      )
    ),

    // ─── Scan to Receive Modal ───
    React.createElement(Modal, { open: scanModalOpen, onClose: closeScanModal, title: 'Receiving — PO ' + (po ? po.po_number : ''), width: 780 },
      scanModalOpen && React.createElement('div', null,
        // Scan input area
        scanMode === 'scan' && React.createElement('div', null,
          React.createElement('div', { style: { display: 'flex', gap: 8, marginBottom: 12 } },
            React.createElement('input', {
              ref: scanInputRef, value: scanInput, autoFocus: true, placeholder: 'Scan barcode or search by SKU, FNSKU, Product Name, Vendor SKU',
              onChange: function(e) { setScanInput(e.target.value); setScanError(''); },
              onKeyDown: function(e) { if (e.key === 'Enter') handleScanSubmit(); },
              style: { flex: 1, background: C.bg, border: '2px solid ' + (scanError ? C.danger : C.accent), color: C.text, padding: '12px 16px', borderRadius: 8, fontSize: 15, fontFamily: 'inherit', outline: 'none' }
            }),
            React.createElement(Btn, { variant: 'primary', onClick: function() { handleScanSubmit(); }, disabled: scanLoading },
              scanLoading ? 'Looking up...' : React.createElement(LIcon, { name: 'search', size: 16 }))
          ),
          scanError && React.createElement('div', { style: { marginBottom: 12 } },
            React.createElement('div', { style: { background: C.dangerSoft || '#2A1A1A', border: '1px solid ' + C.danger + '44', borderRadius: 8, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8 } },
              React.createElement(LIcon, { name: 'alert-circle', size: 16, color: C.danger }),
              React.createElement('span', { style: { fontSize: 13, color: C.danger, flex: 1 } }, scanError),
              React.createElement(Btn, { size: 'sm', onClick: function() { setScanMode('unexpected'); setUnexpectedQty('1'); setUnexpectedNotes(''); },
                style: { background: '#FBBF24', border: '1px solid #D97706', color: '#1a1a1a', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' } },
                React.createElement(LIcon, { name: 'plus', size: 12 }), ' Add Unexpected Item')
            )
          )
        ),

        // Multiple matches — pick list
        scanMode === 'pick' && React.createElement('div', null,
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 } },
            React.createElement('button', { onClick: resetToScan,
              style: { background: 'none', border: 'none', color: C.textMuted, cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' } }, React.createElement(LIcon, { name: 'arrow-left', size: 16 })),
            React.createElement('span', { style: { fontSize: 14, fontWeight: 600, color: C.text } }, scanResults.length + ' items match "' + scanInput + '"'),
            React.createElement('span', { style: { fontSize: 12, color: C.textMuted } }, '— select one to receive')
          ),
          React.createElement('div', { style: { maxHeight: 400, overflow: 'auto', borderRadius: 8, border: '1px solid ' + C.border } },
            scanResults.map(function(item, idx) {
              var remaining = item.qty_ordered - (item.qty_received || 0);
              var imgUrl = (item.images && item.images.length) ? item.images[0] : item.image_1_url;
              return React.createElement('div', { key: item.id,
                onClick: function() { pickScanItem(item); },
                style: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', cursor: 'pointer',
                  borderBottom: idx < scanResults.length - 1 ? '1px solid ' + C.border + '44' : 'none',
                  background: 'transparent', transition: 'background 0.1s' },
                onMouseEnter: function(e) { e.currentTarget.style.background = C.surface; },
                onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; }
              },
                React.createElement(ProductThumb, { src: imgUrl, size: 40, iconSize: 16 }),
                React.createElement('div', { style: { flex: 1, minWidth: 0 } },
                  React.createElement('div', { style: { fontSize: 13, fontWeight: 600, fontFamily: 'inherit', color: C.accent } }, item.sku),
                  React.createElement('div', { style: { fontSize: 12, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, item.vendor_title || '')
                ),
                item.prep_instructions && React.createElement('div', { title: item.prep_instructions, style: { flexShrink: 0 } },
                  React.createElement(LIcon, { name: 'alert-triangle', size: 14, color: '#B45309' })),
                React.createElement('div', { style: { textAlign: 'right', flexShrink: 0, minWidth: 80 } },
                  React.createElement('div', { style: { fontSize: 12, color: remaining > 0 ? C.warning : C.success, fontWeight: 600 } },
                    remaining > 0 ? remaining + ' left' : 'Complete'),
                  React.createElement('div', { style: { fontSize: 10, color: C.textDim } }, (item.qty_received || 0) + '/' + item.qty_ordered)
                ),
                React.createElement(LIcon, { name: 'chevron-right', size: 16, color: C.textDim })
              );
            })
          )
        ),

        // Catalog pick list (multiple catalog matches when no PO match)
        scanMode === 'catalog-pick' && React.createElement('div', null,
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 } },
            React.createElement('button', { onClick: resetToScan,
              style: { background: 'none', border: 'none', color: C.textMuted, cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' } }, React.createElement(LIcon, { name: 'arrow-left', size: 16 })),
            React.createElement('span', { style: { fontSize: 14, fontWeight: 600, color: C.text } }, catalogMatches.length + ' catalog matches for "' + scanInput + '"'),
            React.createElement('span', { style: { fontSize: 12, color: C.textMuted } }, '— not on this PO. Pick one to receive as unexpected.')
          ),
          React.createElement('div', { style: { marginBottom: 10, display: 'flex', justifyContent: 'flex-end' } },
            React.createElement('button', {
              onClick: function() { setCatalogMatchedProduct(null); setCatalogMatches([]); setUnexpectedBarcode(''); setUnexpectedTitle(''); setUnexpectedVendorSku(''); setUnexpectedBin(''); setUnexpectedQty('1'); setUnexpectedNotes(''); setScanError(''); setScanMode('unexpected'); },
              title: 'None of these match — fill the unexpected item form manually',
              onMouseEnter: function(e) { e.currentTarget.style.background = C.danger + '33'; e.currentTarget.style.borderColor = C.danger; },
              onMouseLeave: function(e) { e.currentTarget.style.background = C.danger + '18'; e.currentTarget.style.borderColor = C.danger + '44'; },
              style: { background: C.danger + '18', border: '1px solid ' + C.danger + '44', color: C.danger, padding: '6px 12px', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', transition: 'all 0.15s ease' }
            }, 'No matches? Receive manually.')
          ),
          React.createElement('div', { style: { maxHeight: 400, overflow: 'auto', borderRadius: 8, border: '1px solid ' + C.border } },
            catalogMatches.map(function(item, idx) {
              var imgUrl = (item.images && item.images.length) ? item.images[0] : item.image_1_url;
              var isFbm = (item.wh_code && /^FBM/i.test(item.wh_code)) || (item.sku && /-FBM(?:-|$)/i.test(item.sku));
              return React.createElement('div', { key: item.id,
                onClick: function() { applyCatalogMatch(item, scanInput); },
                style: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', cursor: 'pointer',
                  borderBottom: idx < catalogMatches.length - 1 ? '1px solid ' + C.border + '44' : 'none',
                  background: 'transparent', transition: 'background 0.1s' },
                onMouseEnter: function(e) { e.currentTarget.style.background = C.surface; },
                onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; }
              },
                React.createElement(ProductThumb, { src: imgUrl, size: 40, iconSize: 16 }),
                React.createElement('div', { style: { flex: 1, minWidth: 0 } },
                  React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 6 } },
                    React.createElement('span', { style: { fontSize: 13, fontWeight: 600, fontFamily: 'inherit', color: C.accent } }, item.sku),
                    isFbm && React.createElement('span', { style: { background: C.success + '22', color: C.success, fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4, letterSpacing: '0.05em', textTransform: 'uppercase' } }, 'FBM'),
                    item.wh_code && !isFbm && React.createElement('span', { style: { background: C.textDim + '22', color: C.textMuted, fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4, letterSpacing: '0.05em', textTransform: 'uppercase' } }, item.wh_code)
                  ),
                  React.createElement('div', { style: { fontSize: 12, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 } }, item.vendor_title || item.title || ''),
                  item.barcode && React.createElement('div', { style: { fontSize: 11, color: C.textMuted, fontFamily: 'inherit', marginTop: 2 } }, 'UPC: ', React.createElement('span', { style: { color: C.textDim } }, item.barcode))
                ),
                React.createElement(LIcon, { name: 'chevron-right', size: 16, color: C.textDim })
              );
            })
          )
        ),

        // Unexpected item form
        scanMode === 'unexpected' && React.createElement('div', null,
          catalogMatchedProduct && React.createElement('div', { style: { background: C.success + '15', border: '1px solid ' + C.success + '44', borderRadius: 8, padding: 12, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 12 } },
            (function() {
              var imgUrl = (catalogMatchedProduct.images && catalogMatchedProduct.images.length) ? catalogMatchedProduct.images[0] : catalogMatchedProduct.image_1_url;
              return React.createElement(ProductThumb, { src: imgUrl, size: 48, iconSize: 20 });
            })(),
            React.createElement('div', { style: { flex: 1, minWidth: 0 } },
              React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 } },
                React.createElement(LIcon, { name: 'check-circle', size: 14, color: C.success }),
                React.createElement('span', { style: { fontSize: 12, fontWeight: 700, color: C.success, textTransform: 'uppercase', letterSpacing: '0.05em' } }, 'Catalog match found')
              ),
              React.createElement('div', { style: { fontSize: 13, fontWeight: 600, fontFamily: 'inherit', color: C.accent } }, catalogMatchedProduct.sku),
              React.createElement('div', { style: { fontSize: 12, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, catalogMatchedProduct.vendor_title || catalogMatchedProduct.title || '')
            ),
            React.createElement('button', { onClick: function() { setCatalogMatchedProduct(null); setUnexpectedBarcode(''); setUnexpectedTitle(''); setUnexpectedVendorSku(''); setUnexpectedBin(''); },
              title: 'Clear catalog match — fill manually instead',
              onMouseEnter: function(e) { e.currentTarget.style.background = C.danger + '33'; e.currentTarget.style.borderColor = C.danger; },
              onMouseLeave: function(e) { e.currentTarget.style.background = C.danger + '18'; e.currentTarget.style.borderColor = C.danger + '44'; },
              style: { background: C.danger + '18', border: '1px solid ' + C.danger + '44', color: C.danger, padding: '4px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', transition: 'all 0.15s ease' } }, 'Not a match? Clear to receive manually.')
          ),
          React.createElement('div', { style: { background: '#FBBF24' + '15', border: '1px solid #FBBF24' + '40', borderRadius: 8, padding: 16, marginBottom: 12 } },
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 } },
              React.createElement(LIcon, { name: 'alert-triangle', size: 18, color: '#B45309' }),
              React.createElement('span', { style: { fontSize: 14, fontWeight: 600, color: '#B45309' } }, catalogMatchedProduct ? 'Receive as Unexpected (catalog-matched)' : 'Add Unexpected Item')
            ),
            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 100px', gap: 10, marginBottom: 10 } },
              React.createElement('div', null,
                React.createElement('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', display: 'block', marginBottom: 4 } }, 'Barcode'),
                React.createElement('input', { value: unexpectedBarcode, onChange: function(e) { setUnexpectedBarcode(e.target.value); },
                  placeholder: 'Scan or enter barcode. If the item has no barcode leave BLANK.',
                  style: { width: '100%', background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontFamily: unexpectedBarcode ? 'inherit' : 'inherit', boxSizing: 'border-box' } })
              ),
              React.createElement('div', null,
                React.createElement('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', display: 'block', marginBottom: 4 } }, 'Qty'),
                React.createElement('input', { type: 'number', min: 1, value: unexpectedQty, onChange: function(e) { setUnexpectedQty(e.target.value); },
                  style: { width: '100%', background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 13, textAlign: 'center', boxSizing: 'border-box' } })
              )
            ),
            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 10 } },
              React.createElement('div', null,
                React.createElement('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', display: 'block', marginBottom: 4 } }, 'Vendor SKU'),
                React.createElement('input', { value: unexpectedVendorSku, onChange: function(e) { setUnexpectedVendorSku(e.target.value); },
                  placeholder: 'Vendor item #...',
                  style: { width: '100%', background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' } })
              ),
              React.createElement('div', null,
                React.createElement('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', display: 'block', marginBottom: 4 } }, 'Vendor Title'),
                React.createElement('input', { value: unexpectedTitle, onChange: function(e) { setUnexpectedTitle(e.target.value); },
                  placeholder: 'Item name...',
                  style: { width: '100%', background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' } })
              ),
              React.createElement('div', null,
                React.createElement('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', display: 'block', marginBottom: 4 } }, 'Bin #'),
                React.createElement('input', { value: unexpectedBin, onChange: function(e) { setUnexpectedBin(e.target.value.toUpperCase()); },
                  onBlur: function() { setUnexpectedBin(function(v) { return formatBin(v) || v; }); },
                  placeholder: 'Enter bin...',
                  style: { width: '100%', background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontFamily: unexpectedBin ? 'inherit' : 'inherit', boxSizing: 'border-box' } })
              )
            ),
            React.createElement('div', { style: { marginBottom: 10 } },
              React.createElement('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', display: 'block', marginBottom: 4 } }, 'Notes (optional)'),
              React.createElement('input', { value: unexpectedNotes, onChange: function(e) { setUnexpectedNotes(e.target.value); },
                onKeyDown: function(e) { if (e.key === 'Enter') handleUnexpectedItem(); },
                placeholder: 'e.g. Wrong item from vendor, extra units...',
                style: { width: '100%', background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' } })
            ),
            React.createElement('div', { style: { marginBottom: 10 } },
              React.createElement(window.PhotoUploadPanel, { required: !catalogMatchedProduct, onTokenChange: setPhotoToken, onPhotosChange: function(p) { setPhotoCount(p.length); }, resetSignal: photoResetSignal })
            ),
            React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } },
              React.createElement(Btn, { size: 'sm', onClick: resetToScan }, 'Cancel'),
              React.createElement(Btn, { size: 'sm', variant: 'primary', onClick: handleUnexpectedItem, disabled: scanLoading }, scanLoading ? 'Adding...' : 'Add & Receive')
            )
          )
        ),

        // Matched item result
        scanMode === 'result' && scanResult && React.createElement('div', null,
          React.createElement('div', { style: { marginBottom: 10, display: 'flex', justifyContent: 'flex-end' } },
            React.createElement('button', {
              onClick: function() { setScanResult(null); setScanResults([]); setCatalogMatchedProduct(null); setCatalogMatches([]); setUnexpectedBarcode(''); setUnexpectedTitle(''); setUnexpectedVendorSku(''); setUnexpectedBin(''); setUnexpectedQty('1'); setUnexpectedNotes(''); setScanError(''); setScanMode('unexpected'); },
              title: 'Not the right item — fill the unexpected item form manually',
              onMouseEnter: function(e) { e.currentTarget.style.background = C.danger + '33'; e.currentTarget.style.borderColor = C.danger; },
              onMouseLeave: function(e) { e.currentTarget.style.background = C.danger + '18'; e.currentTarget.style.borderColor = C.danger + '44'; },
              style: { background: C.danger + '18', border: '1px solid ' + C.danger + '44', color: C.danger, padding: '6px 12px', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', transition: 'all 0.15s ease' }
            }, 'Not a match? Receive manually.')
          ),
          React.createElement(Card, { style: { marginBottom: 12, padding: 16 } },
            React.createElement('div', { style: { display: 'flex', gap: 14, marginBottom: 12 } },
              // Product image
              React.createElement(ProductThumb, { src: (scanResult.images && scanResult.images[0]) || scanResult.image_1_url, size: 64, radius: 8, iconSize: 24,
                onClick: function() { var imgs = scanResult.images && scanResult.images.length ? scanResult.images : (scanResult.image_1_url ? [scanResult.image_1_url] : []); if (imgs.length) setLightbox({ images: imgs, index: 0 }); } }),
              React.createElement('div', { style: { flex: 1, minWidth: 0 } },
                React.createElement('div', { style: { fontSize: 16, fontWeight: 700, fontFamily: 'inherit', color: C.accent, marginBottom: 2 } }, scanResult.sku),
                React.createElement('div', { style: { fontSize: 13, color: C.text, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, scanResult.vendor_title || ''),
                React.createElement('div', { style: { display: 'flex', gap: 12, fontSize: 11, color: C.textMuted } },
                  scanResult.barcode && React.createElement('span', null, 'UPC: ' + scanResult.barcode),
                  scanResult.fnsku && React.createElement('span', null, 'FNSKU: ' + scanResult.fnsku),
                  scanResult.vendor_sku && React.createElement('span', null, 'Vendor: ' + scanResult.vendor_sku)
                )
              )
            ),
            // Prep instructions
            scanResult.prep_instructions
              ? React.createElement('div', { style: { background: '#FBBF24' + '15', border: '1px solid #FBBF24' + '40', borderRadius: 6, padding: '8px 12px', marginBottom: 12, display: 'flex', alignItems: 'flex-start', gap: 8 } },
                  React.createElement(LIcon, { name: 'alert-triangle', size: 14, color: '#B45309', style: { marginTop: 1, flexShrink: 0 } }),
                  React.createElement('div', null,
                    React.createElement('div', { style: { fontSize: 11, fontWeight: 700, color: '#B45309', textTransform: 'uppercase', marginBottom: 2 } }, 'Prep Instructions'),
                    React.createElement('div', { style: { fontSize: 13, color: '#92400E' } }, scanResult.prep_instructions)
                  )
                )
              : null,
            // Unexpected-item badge (replaces over-receive warning for items that were never ordered)
            scanResult.is_unexpected
              ? React.createElement('div', { style: { background: '#FBBF24' + '15', border: '1px solid #FBBF24' + '40', borderRadius: 6, padding: '8px 12px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 } },
                  React.createElement(LIcon, { name: 'alert-triangle', size: 16, color: '#B45309' }),
                  React.createElement('span', { style: { fontSize: 13, fontWeight: 600, color: '#B45309' } }, 'Unexpected item — was not ordered on this PO. Receive any quantity.')
                )
              : ((scanResult.qty_ordered - (scanResult.qty_received || 0)) <= 0
                  ? React.createElement('div', { style: { background: '#FBBF24' + '15', border: '1px solid #FBBF24' + '40', borderRadius: 6, padding: '8px 12px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 } },
                      React.createElement(LIcon, { name: 'alert-triangle', size: 16, color: '#B45309' }),
                      React.createElement('span', { style: { fontSize: 13, fontWeight: 600, color: '#B45309' } }, 'This item has been received in full. Any quantity entered will be additional.')
                    )
                  : null),
            // Qty info + receive input
            React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 12 } },
              React.createElement('div', { style: { textAlign: 'center', padding: '8px 0', background: C.surface, borderRadius: 6 } },
                React.createElement('div', { style: { fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase' } }, 'Ordered'),
                React.createElement('div', { style: { fontSize: 18, fontWeight: 700, color: scanResult.is_unexpected ? C.textDim : C.text } }, scanResult.is_unexpected ? 0 : scanResult.qty_ordered)
              ),
              React.createElement('div', { style: { textAlign: 'center', padding: '8px 0', background: C.surface, borderRadius: 6 } },
                React.createElement('div', { style: { fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase' } }, 'Received'),
                React.createElement('div', { style: { fontSize: 18, fontWeight: 700, color: C.success } }, scanResult.qty_received || 0)
              ),
              React.createElement('div', { style: { textAlign: 'center', padding: '8px 0', background: C.surface, borderRadius: 6 } },
                React.createElement('div', { style: { fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase' } }, 'Remaining'),
                scanResult.is_unexpected
                  ? React.createElement('div', { style: { fontSize: 18, fontWeight: 700, color: C.textDim } }, '—')
                  : React.createElement('div', { style: { fontSize: 18, fontWeight: 700, color: (scanResult.qty_ordered - (scanResult.qty_received || 0)) > 0 ? C.warning : C.success } },
                      scanResult.qty_ordered - (scanResult.qty_received || 0))
              )
            ),
            // Bin location (editable)
            React.createElement('div', { style: { marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: C.surface, borderRadius: 6, border: '1px solid ' + (!scanBin.trim() || scanBin.trim() === 'DEFAULT' ? C.danger + '66' : C.border) } },
              React.createElement(LIcon, { name: 'map-pin', size: 16, color: !scanBin.trim() || scanBin.trim() === 'DEFAULT' ? C.danger : C.textMuted }),
              React.createElement('span', { style: { fontSize: 14, color: C.textMuted, fontWeight: 500 } }, 'BIN#:'),
              !scanBin.trim() || scanBin.trim() === 'DEFAULT'
                ? React.createElement('span', { style: { fontSize: 11, fontWeight: 700, color: C.danger, background: C.danger + '18', padding: '2px 10px', borderRadius: 99 } }, 'MISSING')
                : null,
              React.createElement('input', {
                value: scanBin, onChange: function(e) { setScanBin(e.target.value.toUpperCase()); },
                onBlur: function() { setScanBin(function(v) { return formatBin(v) || v; }); },
                placeholder: 'Enter bin...',
                className: 'dt-input',
                style: { flex: 1, background: C.bg, borderWidth: 1, borderStyle: 'solid', borderColor: !scanBin.trim() || scanBin.trim() === 'DEFAULT' ? C.danger + '66' : C.border, color: C.text, padding: '6px 10px', borderRadius: 4, fontSize: 15, fontFamily: 'inherit', fontWeight: 700, outline: 'none' }
              })
            ),
            // Notes input
            React.createElement('div', { style: { marginBottom: 12 } },
              React.createElement('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', display: 'block', marginBottom: 4 } }, 'Notes (optional)'),
              React.createElement('input', { value: scanNotes, onChange: function(e) { setScanNotes(e.target.value); },
                placeholder: 'Add notes...',
                className: 'dt-input',
                style: { width: '100%', background: C.bg, borderWidth: 1, borderStyle: 'solid', borderColor: C.border, color: C.text, padding: '6px 10px', borderRadius: 4, fontSize: 13, outline: 'none', boxSizing: 'border-box' } })
            ),
            // Receive qty input + buttons
            React.createElement('div', { style: { display: 'flex', gap: 10, alignItems: 'flex-end' } },
              React.createElement('div', { style: { flex: 1 } },
                React.createElement('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', display: 'block', marginBottom: 4 } }, 'Quantity to Receive'),
                React.createElement('input', { type: 'number', min: 1, value: scanQty,
                  onChange: function(e) { setScanQty(e.target.value); },
                  onKeyDown: function(e) { if (e.key === 'Enter') handleScanReceive(); },
                  autoFocus: true,
                  className: 'dt-input',
                  style: { width: '100%', background: C.bg, borderWidth: 1, borderStyle: 'solid', borderColor: C.border, color: C.text, padding: '10px 14px', borderRadius: 4, fontSize: 18, fontWeight: 700, textAlign: 'center', boxSizing: 'border-box', outline: 'none' } })
              ),
              React.createElement('button', { onClick: function() { handleScanReceive(); }, disabled: scanLoading,
                onMouseEnter: function(e) { if (!e.currentTarget.disabled) { e.currentTarget.style.background = C.success; e.currentTarget.style.borderColor = C.success; } },
                onMouseLeave: function(e) { e.currentTarget.style.background = C.accent; e.currentTarget.style.borderColor = C.accent; },
                style: { minWidth: 150, padding: '12px 20px', fontSize: 14, fontWeight: 600, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: C.accent, border: '1px solid ' + C.accent, color: '#fff', cursor: 'pointer', borderRadius: 6, fontFamily: 'inherit', transition: 'all 0.15s ease' } }, React.createElement(LIcon, { name: 'check', size: 16 }), 'Receive'),
              React.createElement('button', {
                onMouseEnter: function(e) { e.currentTarget.style.background = C.accent + '18'; e.currentTarget.style.borderColor = C.accent; },
                onMouseLeave: function(e) { e.currentTarget.style.background = 'none'; e.currentTarget.style.borderColor = C.border; },
                onClick: function() {
                // Direct print using default template — skip template picker
                var cat = getPoLabelCategory();
                var catTpls = labelTemplates.filter(function(t) { return (t.category || 'fba') === cat; });
                var tpl = catTpls.find(function(t) { return t.is_default; }) || catTpls[0] || null;
                var item = scanResult;
                var widthMm = tpl ? parseFloat(tpl.width_mm) : 57.15;
                var heightMm = tpl ? parseFloat(tpl.height_mm) : 31.75;
                var fontSize = tpl ? (tpl.font_size || 12) : 12;
                var fields = tpl ? (tpl.fields || ['fnsku', 'sku', 'title']) : ['fnsku', 'sku', 'title'];
                var isSheet = tpl ? tpl.label_type === 'sheet' : false;
                var cols = tpl ? (tpl.columns || 1) : 1;
                var rowsPerSheet = tpl ? (tpl.rows_per_sheet || 1) : 1;
                var pageW = tpl && tpl.page_width_mm ? parseFloat(tpl.page_width_mm) : 215.9;
                var pageH = tpl && tpl.page_height_mm ? parseFloat(tpl.page_height_mm) : 279.4;
                var barcodeVal = poLabelBarcodeValue(cat, item);
                if (cat === 'meli' && !barcodeVal) { showToast('No ML Code on ' + (item.sku || 'this product') + '. A Mercado Libre label needs the ML Code.', 'error'); return; }
                var remaining = item.qty_ordered - (item.qty_received || 0);
                var count = remaining > 0 ? remaining : 1;
                var titleFit = productLabelTitleFit(heightMm, fontSize, Math.max(4, heightMm * 0.28));
                var allLabels = [];
                for (var i = 0; i < count; i++) {
                  var lbl = '<div style="width:' + widthMm + 'mm;height:' + heightMm + 'mm;box-sizing:border-box;border:1px solid #ccc;padding:2mm;display:flex;flex-direction:column;justify-content:center;align-items:center;overflow:hidden;page-break-inside:avoid;">';
                  var barcodeWidthMm = widthMm - 4;
                  var barcodeHeightMm = Math.max(4, heightMm * 0.28);
                  var barcodeImg = barcodeVal ? generateBarcodeImg(barcodeVal, barcodeWidthMm, barcodeHeightMm) : '';
                  lbl += '<div style="text-align:center;margin-bottom:0.3mm;">' + barcodeImg + '</div>';
                  if (barcodeVal) lbl += '<div style="font-family:monospace;font-size:' + Math.max(5, fontSize - 4) + 'pt;font-weight:bold;letter-spacing:0.5px;margin-bottom:0.3mm;text-align:center;">' + escHtml(barcodeVal) + '</div>';
                  var titleVal = item.vendor_title || '';
                  if (titleVal && fields.indexOf('title') >= 0) lbl += '<div style="' + productLabelTitleCss(titleFit.pt, titleFit.lines) + '">' + escHtml(titleVal) + '</div>'; // two lines, never cut (Anthony 2026-09-29)
                  var bottomParts = [];
                  if (fields.indexOf('condition') >= 0) bottomParts.push('New');
                  if (fields.indexOf('sku') >= 0 && item.sku) bottomParts.push((cat === 'meli' ? 'SKU: ' : '') + item.sku);
                  if (bottomParts.length > 0) lbl += '<div style="font-size:' + Math.max(4, fontSize - 6) + 'pt;color:#333;text-align:center;margin-top:0.3mm;">' + bottomParts.map(escHtml).join(' | ') + '</div>';
                  lbl += '</div>';
                  allLabels.push(lbl);
                }
                var pw = window.open('', '_blank', 'width=600,height=800');
                if (!pw) { showToast('Pop-up blocked — allow pop-ups for this site', 'error'); return; }
                var html;
                if (isSheet) {
                  var mH = (pageW - (cols * widthMm)) / (cols + 1);
                  var mV = (pageH - (rowsPerSheet * heightMm)) / (rowsPerSheet + 1);
                  var pgs = []; var pp = cols * rowsPerSheet;
                  for (var p = 0; p < allLabels.length; p += pp) {
                    var sl = allLabels.slice(p, p + pp);
                    var ph = '<div style="width:' + pageW + 'mm;height:' + pageH + 'mm;box-sizing:border-box;padding:' + mV + 'mm ' + mH + 'mm;display:flex;flex-wrap:wrap;align-content:flex-start;gap:0;page-break-after:always;"><div style="display:grid;grid-template-columns:repeat(' + cols + ',' + widthMm + 'mm);grid-template-rows:repeat(' + rowsPerSheet + ',' + heightMm + 'mm);gap:0;width:100%;">';
                    sl.forEach(function(l) { ph += l; }); ph += '</div></div>'; pgs.push(ph);
                  }
                  html = pgs.join('');
                } else {
                  html = '<div style="display:flex;flex-wrap:wrap;gap:0;justify-content:center;">'; allLabels.forEach(function(l) { html += l; }); html += '</div>';
                }
                // Drive printing from the opener (CSP blocks the inline-script approach).
                pw.document.write('<!DOCTYPE html><html><head><title>Labels — ' + escHtml(item.sku) + '</title><style>@media print{body{margin:0;}@page{size:' + (isSheet ? pageW + 'mm ' + pageH + 'mm' : widthMm + 'mm ' + heightMm + 'mm') + ';margin:0;}div{page-break-inside:avoid;}}</style></head><body style="font-family:Arial,sans-serif;margin:0;padding:0;">' + html + '</body></html>');
                pw.document.close();
                setTimeout(function() { try { pw.focus(); pw.print(); } catch (e) { /* popup closed */ } }, 500);
              }, style: { minWidth: 150, padding: '12px 20px', fontSize: 14, fontWeight: 600, background: 'none', border: '1px solid ' + C.border, color: C.text, cursor: 'pointer', borderRadius: 6, fontFamily: 'inherit', transition: 'all 0.15s ease', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }
              }, React.createElement(LIcon, { name: 'printer', size: 16 }), ' Print Labels')
            ),
            // Back button — go to pick list if multiple results, otherwise back to scanner
            React.createElement('div', { style: { marginTop: 14, textAlign: 'center', display: 'flex', justifyContent: 'center', gap: 16 } },
              scanResults.length > 1 && React.createElement('button', { onClick: function() { setScanResult(null); setScanMode('pick'); },
                style: { background: 'none', border: 'none', color: C.accent, cursor: 'pointer', fontSize: 14, fontWeight: 500, fontFamily: 'inherit', textDecoration: 'underline', padding: '6px 16px' } },
                React.createElement(LIcon, { name: 'arrow-left', size: 14, style: { marginRight: 4, verticalAlign: 'middle' } }), 'Back to Results (' + scanResults.length + ')'),
              React.createElement('button', { onClick: resetToScan,
                onMouseEnter: function(e) { e.currentTarget.style.background = C.accent + '18'; e.currentTarget.style.borderColor = C.accent; },
                onMouseLeave: function(e) { e.currentTarget.style.background = 'none'; e.currentTarget.style.borderColor = C.border; },
                style: { background: 'none', border: '1px solid ' + C.border, color: C.text, cursor: 'pointer', fontSize: 14, fontWeight: 500, fontFamily: 'inherit', padding: '6px 16px', borderRadius: 6, transition: 'all 0.15s ease' } },
                React.createElement(LIcon, { name: 'scan-line', size: 14, style: { marginRight: 4, verticalAlign: 'middle' } }), 'New Scan')
            )
          )
        ),

        // Scan history (undo support)
        scanHistory.length > 0 && React.createElement('div', { style: { marginTop: 8 } },
          React.createElement('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 6 } }, 'Session History'),
          React.createElement('div', { style: { maxHeight: 240, overflow: 'auto', borderRadius: 8, border: '1px solid ' + C.border } },
            scanHistory.map(function(h, idx) {
              return React.createElement('div', { key: idx, style: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: idx < scanHistory.length - 1 ? '1px solid ' + C.border + '44' : 'none', fontSize: 14 } },
                h.unexpected
                  ? React.createElement(LIcon, { name: 'alert-triangle', size: 16, color: '#FBBF24' })
                  : React.createElement(LIcon, { name: 'check-circle', size: 16, color: C.success }),
                React.createElement('span', { style: { fontFamily: 'inherit', fontWeight: 700, color: C.accent, fontSize: 14 } }, h.sku),
                React.createElement('span', { style: { color: C.success, fontWeight: 600, fontSize: 14 } }, '+' + h.qty),
                React.createElement('span', { style: { color: C.textDim, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13 } }, h.vendor_title || ''),
                React.createElement('span', { style: { color: C.textDim, fontSize: 12 } }, h.time),
                h.logId && React.createElement('button', { onClick: function() { handleScanUndo(h.logId, idx); },
                  style: { background: C.dangerSoft || '#2A1A1A', border: '1px solid ' + C.danger + '44', color: C.danger, cursor: 'pointer', fontSize: 12, padding: '5px 12px', borderRadius: 6, fontFamily: 'inherit', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 5 } },
                  React.createElement(LIcon, { name: 'undo-2', size: 13 }), 'Undo')
              );
            })
          )
        )
      )
    ),
    // ─── Check In modal (location entered on a pre-check-in PO) ───
    checkInModalOpen && po && React.createElement(LocationPickerModal, {
      title: 'Check In — PO #' + po.po_number,
      initialValue: (function() { var t = (locationVal || '').trim().toUpperCase(); return /^\d+$/.test(t) ? 'LOC' + t : t; })(),
      onConfirm: handleDetailCheckIn,
      onCancel: function() { setCheckInModalOpen(false); }
    }),
    // ─── Mark Missing confirmation modal ───
    React.createElement(Modal, { open: !!markMissingModal, onClose: function() { if (!markMissingLoading) setMarkMissingModal(null); }, title: 'Mark Items as Missing', width: 600 },
      markMissingModal && (function() {
        var items = markMissingModal.items;
        var totalUnits = items.reduce(function(s, i) { return s + i.qty; }, 0);
        var isBulk = items.length > 1;
        return React.createElement('div', null,
          React.createElement('div', { style: { display: 'flex', alignItems: 'flex-start', gap: 12, background: (C.warningSoft || '#2A230F'), border: '1px solid ' + C.warning + '44', borderRadius: 8, padding: '12px 14px', marginBottom: 14 } },
            React.createElement(LIcon, { name: 'alert-triangle', size: 18, color: C.warning, style: { flexShrink: 0, marginTop: 2 } }),
            React.createElement('div', { style: { fontSize: 13, color: C.text, lineHeight: 1.5 } },
              'These items will be recorded as missing on this PO. They count toward PO completion but no inventory will be received. You can undo individual entries from the History tab.'
            )
          ),
          React.createElement('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 } },
            (isBulk ? items.length + ' items' : '1 item') + ' to mark missing'),
          React.createElement('div', { style: { maxHeight: 320, overflow: 'auto', border: '1px solid ' + C.border, borderRadius: 8, marginBottom: 12 } },
            items.map(function(item, idx) {
              return React.createElement('div', { key: item.line_item_id, style: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderBottom: idx < items.length - 1 ? '1px solid ' + C.border + '44' : 'none' } },
                React.createElement('div', { style: { flex: 1, minWidth: 0 } },
                  React.createElement('div', { style: { fontSize: 13, fontWeight: 600, fontFamily: 'inherit', color: C.accent } }, item.sku),
                  item.vendor_title && React.createElement('div', { style: { fontSize: 12, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 } }, item.vendor_title)
                ),
                React.createElement('div', { style: { fontSize: 14, fontWeight: 700, color: C.danger, flexShrink: 0, whiteSpace: 'nowrap' } }, item.qty + ' units')
              );
            })
          ),
          React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 14px', background: C.surface, borderRadius: 6, marginBottom: 16 } },
            React.createElement('span', { style: { fontSize: 12, color: C.textMuted } }, items.length + ' item' + (isBulk ? 's' : '')),
            React.createElement('span', { style: { fontSize: 13, fontWeight: 700, color: C.danger } }, 'Total: ' + totalUnits + ' units')
          ),
          React.createElement('div', { style: { marginBottom: 16 } },
            React.createElement('label', { style: { display: 'block', fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 } }, 'Note (optional)'),
            React.createElement('textarea', {
              value: markMissingNote,
              onChange: function(e) { setMarkMissingNote(e.target.value); },
              disabled: markMissingLoading,
              placeholder: 'Reason or context for the log (e.g. damaged, short shipped, no scan match)',
              rows: 3,
              style: { width: '100%', background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }
            })
          ),
          React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } },
            React.createElement(Btn, { onClick: function() { if (!markMissingLoading) setMarkMissingModal(null); }, disabled: markMissingLoading }, 'Cancel'),
            React.createElement(Btn, { variant: 'danger', disabled: markMissingLoading, onClick: confirmMarkMissing },
              React.createElement(LIcon, { name: 'alert-circle', size: 14 }),
              markMissingLoading ? ' Marking...' : ' Mark ' + items.length + ' Item' + (isBulk ? 's' : '') + ' Missing')
          )
        );
      })()
    ),
    // ─── Mark Closed confirmation modal ───
    React.createElement(Modal, { open: closeModalOpen, onClose: function() { if (!closing) setCloseModalOpen(false); }, title: 'Mark PO as Closed?', width: 520 },
      React.createElement('div', { style: { display: 'flex', alignItems: 'flex-start', gap: 12, background: (C.warningSoft || '#2A230F'), border: '1px solid ' + C.warning + '44', borderRadius: 8, padding: '14px 16px', marginBottom: 18 } },
        React.createElement(LIcon, { name: 'alert-triangle', size: 20, color: C.warning, style: { flexShrink: 0, marginTop: 2 } }),
        React.createElement('div', { style: { fontSize: 13, color: C.text, lineHeight: 1.55 } },
          React.createElement('div', { style: { fontWeight: 600, marginBottom: 4 } }, 'Before closing this PO, please confirm:'),
          React.createElement('ul', { style: { margin: '6px 0 0 18px', padding: 0 } },
            React.createElement('li', { style: { marginBottom: 4 } }, 'All line items have been reviewed for discrepancies'),
            React.createElement('li', null, 'Items have been received to Zentail (FBM) or FBA/WFS')
          )
        )
      ),
      React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } },
        React.createElement(Btn, { onClick: function() { if (!closing) setCloseModalOpen(false); }, disabled: closing }, 'Cancel'),
        React.createElement(Btn, { variant: 'primary', disabled: closing, onClick: async function() {
          setClosing(true);
          try {
            // Close via the receiving router — closing a received PO is a Receiving action,
            // and warehouse-side roles don't (and shouldn't) hold purchase_orders write.
            await api('/receiving/bulk/status', { method: 'PUT', body: JSON.stringify({ ids: [poId], status: 'closed' }) });
            showToast('PO marked as closed');
            setCloseModalOpen(false);
            load();
          } catch (e) { showToast(e.message, 'error'); }
          finally { setClosing(false); }
        } }, React.createElement(LIcon, { name: 'lock', size: 14 }), closing ? ' Closing...' : ' Mark Closed')
      )
    )
  );
}

function LoginPage({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [step, setStep] = useState('login'); // 'login' | 'email_setup' | 'otp' | 'forgot' | 'forgot_sent' | 'reset'
  const [tempToken, setTempToken] = useState(null);
  const [emailHint, setEmailHint] = useState('');
  // 2FA channels (9-05): the server lists what this user can use; the user picks at the prompt
  const [otpChannels, setOtpChannels] = useState([]);   // [{ id: 'email'|'sms', hint }]
  const [otpChannel, setOtpChannel] = useState('email');
  const [otpHint, setOtpHint] = useState('');
  const [otpNotice, setOtpNotice] = useState('');
  const [recaptchaKey, setRecaptchaKey] = useState(null);
  const [sending, setSending] = useState(false);
  // Warm the reCAPTCHA script while the user types: the text-message code path needs it right
  // after Sign In, and the script load was the first second of the wait.
  useEffect(function() { if (window.warmRecaptcha) window.warmRecaptcha(); }, []);
  const [otpCode, setOtpCode] = useState('');
  const [email, setEmail] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetMsg, setResetMsg] = useState('');
  const [idleNotice, setIdleNotice] = useState(false);

  useEffect(function() {
    try {
      if (sessionStorage.getItem('logout_reason') === 'inactivity') {
        setIdleNotice(true);
        sessionStorage.removeItem('logout_reason');
      }
    } catch(e) {}
    document.title = 'AtharvaToolz | Sign In';
    fetch('/api/settings/logo').then(function(r) {
      if (r.ok) setLogoUrl('/api/settings/logo');
    }).catch(function(e) { console.error('[silent-catch]', e); });
    // Check for reset-password token: prefer query param (?token=...) since email click-trackers strip URL fragments; fall back to legacy hash form
    var resetTok = null;
    try {
      var sp = new URLSearchParams(window.location.search);
      if ((window.location.pathname === '/reset-password' || sp.get('token')) && sp.get('token')) {
        resetTok = sp.get('token');
      }
    } catch(e) {}
    if (!resetTok && window.location.hash.startsWith('#reset-password=')) {
      resetTok = window.location.hash.replace('#reset-password=', '');
    }
    if (resetTok) {
      setResetToken(resetTok);
      setStep('reset');
      window.history.replaceState(null, '', '/login');
    }
    // Inject login styles for focus/hover
    if (!document.getElementById('login-styles')) {
      var s = document.createElement('style'); s.id = 'login-styles';
      s.textContent = '.login-box input:focus{border-color:#4F6EF7!important;box-shadow:0 0 0 3px rgba(79,110,247,0.15)!important;background:#fff!important}'
        + '.login-btn:hover{filter:brightness(1.1)!important;box-shadow:0 2px 8px rgba(79,110,247,0.3)!important}'
        + '.login-btn:active{filter:brightness(0.95)!important;transform:scale(0.99)}'
        + '.login-forgot:hover{color:#2563EB!important;text-decoration:underline}'
        + '.login-back:hover{color:#111827!important}';
      document.head.appendChild(s);
    }
  }, []);

  // Resend cooldown timer
  useEffect(function() {
    if (resendCooldown <= 0) return;
    var timer = setTimeout(function() { setResendCooldown(resendCooldown - 1); }, 1000);
    return function() { clearTimeout(timer); };
  }, [resendCooldown]);

  // Enter + click (or two Enters) before the re-render used to fire two logins (and two
  // 2FA codes): the ref closes the gap that the `loading` state alone leaves (9-26).
  var loginBusyRef = useRef(false);
  var handleLogin = async function() {
    if (loading || loginBusyRef.current) return;
    if (!username || !password) { setError('Enter username and password'); return; }
    loginBusyRef.current = true;
    setLoading(true); setError('');
    try { await handleLoginInner(); } finally { loginBusyRef.current = false; }
  };
  var handleLoginInner = async function() {
    try {
      var res = await fetch('/api/auth/login', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password, otp_channels: ['email', 'sms'] }) // channels this client can drive
      });
      var data = await res.json();
      if (!res.ok) { setError(data.error || 'Login failed'); setLoading(false); return; }
      if (data.status === 'requires_email_setup') {
        setTempToken(data.temp_token);
        setStep('email_setup');
        setLoading(false);
      } else if (data.status === 'requires_2fa') {
        setTempToken(data.temp_token);
        setEmailHint(data.email_hint || '');
        var chans = Array.isArray(data.channels) && data.channels.length ? data.channels : [{ id: 'email', hint: data.email_hint || '', available: true }];
        setOtpChannels(chans); setRecaptchaKey(data.recaptcha_site_key || null); setOtpNotice(data.notice || '');
        setLoading(false);
        if (data.sent) {
          // Code already went out (email default / email-only user) — straight to the code box
          var sentCh = chans.find(function(c) { return c.id === data.sent; }) || {};
          setOtpChannel(data.sent); setOtpHint(sentCh.hint || data.email_hint || '');
          setStep('otp'); setResendCooldown(30);
        } else if (data.choose) {
          // First sign-in with a phone on file: the user picks the channel that becomes their default
          var firstOk = chans.find(function(c) { return c.available !== false; }) || chans[0];
          setOtpChannel(firstOk.id);
          setStep('otp_choose');
        } else if (data.send_on) {
          // Saved default that needs the browser (text → reCAPTCHA): show the code box right away
          // and send behind it. The reCAPTCHA + text hop takes a few seconds and used to leave the
          // login form sitting there looking idle (Anthony 2026-09-06).
          var onCh = chans.find(function(c) { return c.id === data.send_on; }) || {};
          setOtpChannel(data.send_on); setOtpHint(onCh.hint || ''); setOtpCode(''); setStep('otp');
          sendCode(data.send_on, data.temp_token, chans, data.recaptcha_site_key || null, { setDefault: false });
        } else {
          var emCh = chans.find(function(c) { return c.id === 'email'; }) || {};
          setOtpChannel('email'); setOtpHint(emCh.hint || data.email_hint || ''); setOtpCode(''); setStep('otp');
          sendCode('email', data.temp_token, chans, data.recaptcha_site_key || null, { setDefault: false });
        }
      } else {
        finishAuth(data);
      }
    } catch (e) { setError('Connection failed'); setLoading(false); }
  };

  // Successful auth: honor a ?returnTo= back to the ads subdomain (docs/14 — the ads
  // app redirects logged-out visitors here). Allowlisted host only, never arbitrary URLs.
  var finishAuth = function(data) {
    setAuthToken(data.token);
    var rt = null;
    try { rt = new URLSearchParams(window.location.search).get('returnTo'); } catch (e) {}
    rt = rt || _bootReturnTo; // router normalization may have rewritten the URL
    if (rt && /^https:\/\/(ads|ship)\.atharvatoolz\.com(\/|$)/.test(rt)) { window.location.href = rt; return; }
    onLogin(data.user);
  };

  var handleEmailSetup = async function() {
    if (!email) { setError('Enter your email address'); return; }
    setLoading(true); setError('');
    try {
      var res = await fetch('/api/auth/setup-email', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ temp_token: tempToken, email: email })
      });
      var data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to save email'); setLoading(false); return; }
      setEmailHint(data.email_hint || email);
      setStep('otp');
      setResendCooldown(30);
      setLoading(false);
    } catch (e) { setError('Connection failed'); setLoading(false); }
  };

  // codeArg: the input auto-submits the moment the sixth digit lands (Anthony 2026-09-06), before
  // React has committed that keystroke to otpCode
  var handleVerifyOtp = async function(codeArg) {
    var code = typeof codeArg === 'string' ? codeArg : otpCode;
    if (!code || code.length < 6) { setError('Enter the 6-digit code'); return; }
    if (loading) return;
    setLoading(true); setError('');
    try {
      var res = await fetch('/api/auth/verify-otp', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ temp_token: tempToken, code: code })
      });
      var data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Verification failed');
        setLoading(false);
        if (res.status === 401 || res.status === 429) { setStep('login'); setTempToken(null); setOtpCode(''); }
        return;
      }
      finishAuth(data);
    } catch (e) { setError('Connection failed'); setLoading(false); }
  };

  // Send (or re-send) the code on a channel. Text messages run an invisible reCAPTCHA first —
  // Google will not text a code without proof the request came from a browser.
  var sendCode = async function(channel, tok, chans, siteKey, extra) {
    tok = tok || tempToken; chans = chans || otpChannels; siteKey = siteKey || recaptchaKey;
    setError(''); setOtpNotice(''); setSending(true);
    try {
      // set_default: the chooser's pick becomes the user's default for future sign-ins
      var body = { temp_token: tok, channel: channel, set_default: !!(extra && extra.setDefault) };
      if (channel === 'sms') {
        if (!siteKey) throw new Error('Text-message codes are not available right now. Use email instead.');
        body.recaptcha_token = await window.getRecaptchaToken(siteKey);
      }
      var res = await fetch('/api/auth/otp-send', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      var data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Could not send the code');
        if (res.status === 401) { setStep('login'); setTempToken(null); setOtpCode(''); }
        else if (step === 'login') setStep(chans.length > 1 ? 'otp_choose' : 'otp');
        setSending(false);
        return;
      }
      var sentOn = data.sent || channel;
      var sentCh = chans.find(function(c) { return c.id === sentOn; }) || {};
      setOtpChannel(sentOn);
      setOtpHint(data.hint || sentCh.hint || '');
      if (data.fallback) setOtpNotice('Text message failed (' + (data.reason || 'provider error') + '). We emailed the code instead.');
      setOtpCode(''); setStep('otp'); setResendCooldown(30);
    } catch (e) { setError(e.message || 'Connection failed'); if (step === 'login' && chans.length > 1) setStep('otp_choose'); }
    setSending(false);
  };

  var handleResend = function() {
    if (resendCooldown > 0 || sending) return;
    sendCode(otpChannel);
  };

  var handleForgotPassword = async function() {
    if (!username) { setError('Enter your username first'); return; }
    setLoading(true); setError('');
    try {
      var res = await fetch('/api/auth/forgot-password', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username })
      });
      var data = await res.json();
      setStep('forgot_sent');
      setResetMsg(data.message || 'If that account exists and has an email, a reset link has been sent.');
      setLoading(false);
    } catch (e) { setError('Connection failed'); setLoading(false); }
  };

  var handleResetPassword = async function() {
    if (!newPassword) { setError('Enter a new password'); return; }
    if (newPassword.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true); setError('');
    try {
      var res = await fetch('/api/auth/reset-password', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, new_password: newPassword })
      });
      var data = await res.json();
      if (!res.ok) { setError(data.error || 'Reset failed'); setLoading(false); return; }
      setResetMsg(data.message || 'Password has been reset.');
      setStep('forgot_sent');
      setLoading(false);
    } catch (e) { setError('Connection failed'); setLoading(false); }
  };

  var backToLogin = function() {
    setStep('login'); setTempToken(null); setOtpCode(''); setEmail(''); setError(''); setEmailHint(''); setResetMsg(''); setNewPassword(''); setResetToken('');
    setOtpChannels([]); setOtpHint(''); setOtpNotice(''); setSending(false);
  };

  var handleKeyDown = function(e) {
    if (e.key === 'Enter') {
      if (step === 'login') handleLogin();
      else if (step === 'email_setup') handleEmailSetup();
      else if (step === 'otp') handleVerifyOtp();
      else if (step === 'otp_choose') { var kch = otpChannels.find(function(c) { return c.id === otpChannel; }); if (!sending && kch && kch.available !== false) sendCode(otpChannel, null, null, null, { setDefault: true }); }
      else if (step === 'forgot') handleForgotPassword();
      else if (step === 'reset') handleResetPassword();
    }
  };

  var inputStyle = { background: '#F9FAFB', border: '1px solid #D1D5DB', color: '#111827', padding: '8px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box', transition: 'border-color 0.15s, box-shadow 0.15s, background 0.15s' };
  var inputProps = function(extra) { return Object.assign({ className: 'login-input', style: inputStyle }, extra || {}); };
  var labelStyle = { fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em' };

  var renderStep = function() {
    if (step === 'email_setup') {
      return React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 12 } },
        React.createElement('div', { style: { background: '#EFF6FF', border: '1px solid #BFDBFE', padding: '10px 14px', borderRadius: 8, fontSize: 12, color: '#1E40AF', lineHeight: '1.5' } },
          'Your account requires an email address for two-factor authentication. Please enter your email below.'
        ),
        React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4 } },
          React.createElement('label', { style: labelStyle }, 'Email Address'),
          React.createElement('input', {
            type: 'email', value: email,
            onChange: function(e) { setEmail(e.target.value); },
            onKeyDown: handleKeyDown,
            placeholder: 'you@example.com',
            autoFocus: true,
            style: inputStyle
          })
        ),
        React.createElement(Btn, { className: 'login-btn', variant: 'primary', onClick: handleEmailSetup, disabled: loading, style: { width: '100%', justifyContent: 'center', marginTop: 4 } }, loading ? 'Saving...' : 'Continue'),
        React.createElement('button', {
          onClick: backToLogin,
          className: 'login-back',
          style: { background: 'none', border: 'none', color: '#6B7280', fontSize: 12, cursor: 'pointer', textAlign: 'center', padding: 4, fontFamily: 'inherit', transition: 'color 0.15s' }
        }, 'Back to login')
      );
    }
    if (step === 'otp_choose') {
      var chooseBtn = function(ch) {
        var isSms = ch.id === 'sms'; var sel = otpChannel === ch.id; var off = ch.available === false;
        return React.createElement('button', {
          key: ch.id, onClick: function() { if (sending || off) return; setOtpChannel(ch.id); sendCode(ch.id, null, null, null, { setDefault: true }); }, disabled: sending || off,
          title: off ? ch.reason : undefined,
          style: { display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', padding: '12px 14px', borderRadius: 10, cursor: (sending || off) ? 'default' : 'pointer', fontFamily: 'inherit',
            background: off ? '#F9FAFB' : sel ? 'rgba(79,110,247,0.08)' : '#fff', border: '1px solid ' + (sel && !off ? '#4F6EF7' : '#D1D5DB'), color: off ? '#9CA3AF' : '#111827', transition: 'all 0.15s', opacity: sending && !sel ? 0.6 : 1 }
        },
          React.createElement(LIcon, { name: isSms ? 'smartphone' : 'mail', size: 20, color: off ? '#D1D5DB' : sel ? '#4F6EF7' : '#6B7280' }),
          React.createElement('div', null,
            React.createElement('div', { style: { fontSize: 14, fontWeight: 600 } }, isSms ? 'Text message' : 'Email'),
            React.createElement('div', { style: { fontSize: 12, color: off ? '#9CA3AF' : '#6B7280', marginTop: 2 } }, off ? (ch.reason || 'Not available right now') : (ch.hint || ''))));
      };
      return React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 12 } },
        React.createElement('div', { style: { textAlign: 'center', color: '#6B7280', fontSize: 13, lineHeight: '1.5' } },
          sending ? 'Sending your code…' : 'How do you want to receive sign-in codes?',
          !sending ? React.createElement('div', { style: { fontSize: 11.5, color: '#9CA3AF', marginTop: 4 } }, 'This becomes your default. You can change it any time on My Profile.') : null),
        otpChannels.map(chooseBtn),
        otpChannels.some(function(c) { return c.id === 'sms'; }) ? React.createElement('div', { style: { fontSize: 10.5, color: '#9CA3AF', textAlign: 'center' } }, window.RECAPTCHA_NOTICE) : null,
        React.createElement('button', {
          onClick: backToLogin,
          className: 'login-back',
          style: { background: 'none', border: 'none', color: '#6B7280', fontSize: 12, cursor: 'pointer', textAlign: 'center', padding: 4, fontFamily: 'inherit', transition: 'color 0.15s' }
        }, 'Back to login')
      );
    }
    if (step === 'otp') {
      var otherCh = otpChannels.find(function(c) { return c.id !== otpChannel && c.available !== false; }) || null;
      return React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 12 } },
        React.createElement('div', { style: { textAlign: 'center', color: '#6B7280', fontSize: 13, lineHeight: '1.5' } },
          sending
            ? (otpChannel === 'sms' ? 'Texting a verification code to ' : 'Emailing a verification code to ')
            : (otpChannel === 'sms' ? 'We texted a verification code to ' : 'We sent a verification code to '),
          React.createElement('strong', { style: { color: '#111827' } }, otpHint || emailHint || (otpChannel === 'sms' ? 'your phone' : 'your email')),
          sending ? '\u2026' : ''
        ),
        otpNotice ? React.createElement('div', { style: { background: '#FEF3C7', border: '1px solid #FCD34D', color: '#92400E', borderRadius: 8, padding: '8px 10px', fontSize: 12, lineHeight: 1.4 } }, otpNotice) : null,
        React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' } },
          React.createElement('label', { style: labelStyle }, 'Verification Code'),
          React.createElement('input', {
            type: 'text', value: otpCode,
            onChange: function(e) { var v = e.target.value.replace(/\D/g, '').slice(0, 6); setOtpCode(v); if (v.length === 6 && !loading && !sending) handleVerifyOtp(v); },
            onKeyDown: handleKeyDown,
            placeholder: '000000',
            maxLength: 6,
            autoFocus: true,
            autoComplete: 'one-time-code',
            style: Object.assign({}, inputStyle, { textAlign: 'center', fontSize: 24, fontWeight: 700, letterSpacing: 8, fontFamily: 'inherit', maxWidth: 220 })
          })
        ),
        React.createElement(Btn, { className: 'login-btn', variant: 'primary', onClick: handleVerifyOtp, disabled: loading || sending, style: { width: '100%', justifyContent: 'center', marginTop: 4 } }, loading ? 'Verifying...' : 'Verify'),
        React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' } },
          React.createElement('button', {
            onClick: backToLogin,
            style: { background: 'none', border: 'none', color: '#6B7280', fontSize: 12, cursor: 'pointer', padding: 4, fontFamily: 'inherit' }
          }, 'Back to login'),
          React.createElement('button', {
            onClick: handleResend,
            disabled: resendCooldown > 0 || sending,
            style: { background: 'none', border: 'none', color: (resendCooldown > 0 || sending) ? '#9CA3AF' : '#2563EB', fontSize: 12, cursor: (resendCooldown > 0 || sending) ? 'default' : 'pointer', padding: 4, fontFamily: 'inherit' }
          }, sending ? 'Sending…' : resendCooldown > 0 ? 'Resend in ' + resendCooldown + 's' : 'Resend code')
        ),
        otherCh ? React.createElement('button', {
          onClick: function() { if (!sending) sendCode(otherCh.id); },
          disabled: sending,
          className: 'login-forgot',
          style: { background: 'none', border: 'none', color: sending ? '#9CA3AF' : '#2563EB', fontSize: 12.5, fontWeight: 500, cursor: sending ? 'default' : 'pointer', textAlign: 'center', padding: 4, fontFamily: 'inherit' }
        }, otherCh.id === 'sms' ? 'Text the code to ' + (otherCh.hint || 'my phone') + ' instead' : 'Email the code to ' + (otherCh.hint || 'my email') + ' instead') : null,
        (otpChannel === 'sms' || (otherCh && otherCh.id === 'sms')) ? React.createElement('div', { style: { fontSize: 10.5, color: '#9CA3AF', textAlign: 'center' } }, window.RECAPTCHA_NOTICE) : null
      );
    }
    if (step === 'forgot') {
      return React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 12 } },
        React.createElement('div', { style: { color: '#6B7280', fontSize: 13, lineHeight: '1.5' } },
          'Enter your email and we\u2019ll send a password reset link.'
        ),
        React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4 } },
          React.createElement('label', { style: labelStyle }, 'Email'),
          React.createElement('input', {
            type: 'email', value: username,
            onChange: function(e) { setUsername(e.target.value); },
            onKeyDown: handleKeyDown,
            autoFocus: true,
            placeholder: 'you@example.com',
            style: inputStyle
          })
        ),
        React.createElement(Btn, { className: 'login-btn', variant: 'primary', onClick: handleForgotPassword, disabled: loading, style: { width: '100%', justifyContent: 'center', marginTop: 4 } }, loading ? 'Sending...' : 'Send Reset Link'),
        React.createElement('button', {
          onClick: backToLogin,
          className: 'login-back',
          style: { background: 'none', border: 'none', color: '#6B7280', fontSize: 12, cursor: 'pointer', textAlign: 'center', padding: 4, fontFamily: 'inherit', transition: 'color 0.15s' }
        }, 'Back to login')
      );
    }
    if (step === 'forgot_sent') {
      return React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 12, textAlign: 'center' } },
        React.createElement('div', { style: { background: '#ECFDF5', border: '1px solid #A7F3D0', padding: '12px 14px', borderRadius: 8, fontSize: 13, color: '#065F46', lineHeight: '1.5' } },
          resetMsg
        ),
        React.createElement('button', {
          onClick: backToLogin,
          style: { background: 'none', border: 'none', color: '#2563EB', fontSize: 13, cursor: 'pointer', padding: 4, fontFamily: 'inherit', fontWeight: 500 }
        }, 'Back to Sign In')
      );
    }
    if (step === 'reset') {
      return React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 12 } },
        React.createElement('div', { style: { color: '#6B7280', fontSize: 13, lineHeight: '1.5' } },
          'Enter your new password below.'
        ),
        React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4 } },
          React.createElement('label', { style: labelStyle }, 'New Password'),
          React.createElement('input', {
            type: 'password', value: newPassword,
            onChange: function(e) { setNewPassword(e.target.value); },
            onKeyDown: handleKeyDown,
            autoFocus: true,
            style: inputStyle
          })
        ),
        React.createElement(Btn, { className: 'login-btn', variant: 'primary', onClick: handleResetPassword, disabled: loading, style: { width: '100%', justifyContent: 'center', marginTop: 4 } }, loading ? 'Resetting...' : 'Reset Password'),
        React.createElement('button', {
          onClick: backToLogin,
          className: 'login-back',
          style: { background: 'none', border: 'none', color: '#6B7280', fontSize: 12, cursor: 'pointer', textAlign: 'center', padding: 4, fontFamily: 'inherit', transition: 'color 0.15s' }
        }, 'Back to login')
      );
    }
    // Default: login step
    return React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 12 } },
      React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4 } },
        React.createElement('label', { style: labelStyle }, 'Email'),
        React.createElement('input', {
          type: 'email', value: username,
          onChange: function(e) { setUsername(e.target.value); },
          onKeyDown: handleKeyDown,
          placeholder: 'you@example.com',
          autoComplete: 'email',
          style: inputStyle
        })
      ),
      React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4 } },
        React.createElement('label', { style: labelStyle }, 'Password'),
        React.createElement('input', {
          type: 'password', value: password,
          onChange: function(e) { setPassword(e.target.value); },
          onKeyDown: handleKeyDown,
          style: inputStyle
        })
      ),
      React.createElement(Btn, { className: 'login-btn', variant: 'primary', onClick: handleLogin, disabled: loading, style: { width: '100%', justifyContent: 'center', marginTop: 4 } }, loading ? 'Signing in...' : 'Sign In'),
      React.createElement('button', {
        className: 'login-forgot',
        onClick: function() { setError(''); setStep('forgot'); },
        style: { background: 'none', border: 'none', color: '#6B7280', fontSize: 12, cursor: 'pointer', textAlign: 'center', padding: 4, fontFamily: 'inherit', width: '100%', marginTop: 4, transition: 'color 0.15s' }
      }, 'Forgot Password?')
    );
  };

  var subtitle = step === 'email_setup' ? 'Set up your email' : (step === 'otp' || step === 'otp_choose') ? 'Two-factor authentication' : step === 'forgot' ? 'Forgot password' : step === 'forgot_sent' ? 'Check your email' : step === 'reset' ? 'Reset your password' : 'Sign in to continue';

  var canvasRef = useRef(null);
  useEffect(function() {
    var canvas = canvasRef.current;
    if (!canvas) return;
    var ctx = canvas.getContext('2d');
    var W, H, particles = [], animId;
    var mouse = { x: -1000, y: -1000 };

    function resize() {
      var z = window.APP_ZOOM || 1; // canvas lives in the zoomed body — size it in zoomed px so it doesn't overflow
      W = window.innerWidth / z; H = window.innerHeight / z;
      canvas.width = W * (window.devicePixelRatio || 1);
      canvas.height = H * (window.devicePixelRatio || 1);
      canvas.style.width = W + 'px';
      canvas.style.height = H + 'px';
      ctx.setTransform(window.devicePixelRatio || 1, 0, 0, window.devicePixelRatio || 1, 0, 0);
      particles = [];
      var count = Math.min(600, Math.floor(W * H / 2000));
      for (var i = 0; i < count; i++) {
        particles.push({ x: Math.random() * W, y: Math.random() * H, vx: (Math.random() - 0.5) * 0.4, vy: (Math.random() - 0.5) * 0.4, size: 0.6 + Math.random() * 2.5, alpha: 0.1 + Math.random() * 0.35, hue: 25 + Math.random() * 35, pushX: 0, pushY: 0 });
      }
    }

    function onMove(e) { var z = window.APP_ZOOM || 1; mouse.x = e.clientX / z; mouse.y = e.clientY / z; }
    function onLeave() { mouse.x = -1000; mouse.y = -1000; }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseleave', onLeave);

    function draw() {
      animId = requestAnimationFrame(draw);
      ctx.clearRect(0, 0, W, H);
      var bg = ctx.createRadialGradient(W * 0.5, H * 0.5, 0, W * 0.5, H * 0.5, Math.max(W, H) * 0.7);
      bg.addColorStop(0, '#0f1218'); bg.addColorStop(0.5, '#0a0e14'); bg.addColorStop(1, '#060810');
      ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);
      var t = Date.now() * 0.001;
      for (var i = 0; i < particles.length; i++) {
        var p = particles[i];
        p.x += p.vx + Math.sin(t * 0.7 + p.y * 0.008) * 0.12;
        p.y += p.vy + Math.cos(t * 0.5 + p.x * 0.008) * 0.12;
        if (p.x < -20) p.x = W + 20; if (p.x > W + 20) p.x = -20;
        if (p.y < -20) p.y = H + 20; if (p.y > H + 20) p.y = -20;
        if (mouse.x > 0) { var dx = p.x - mouse.x, dy = p.y - mouse.y, dist = Math.sqrt(dx * dx + dy * dy); if (dist < 30 && dist > 0) { var f = (30 - dist) / 30, a = Math.atan2(dy, dx); p.pushX += Math.cos(a) * f * 2; p.pushY += Math.sin(a) * f * 2; } }
        p.x += p.pushX; p.y += p.pushY; p.pushX *= 0.92; p.pushY *= 0.92;
        var drawAlpha = p.alpha * (0.8 + 0.2 * Math.sin(t * 2 + i));
        ctx.beginPath(); ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = 'hsla(' + p.hue + ', 50%, 60%, ' + drawAlpha + ')'; ctx.fill();
        if (i % 3 === 0) { for (var j = i + 1; j < Math.min(i + 40, particles.length); j++) { var p2 = particles[j], ddx = p.x - p2.x, ddy = p.y - p2.y, d2 = ddx * ddx + ddy * ddy; if (d2 < 6400) { ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(p2.x, p2.y); ctx.strokeStyle = 'rgba(200,160,80,' + ((1 - Math.sqrt(d2) / 80) * 0.2) + ')'; ctx.lineWidth = 0.5; ctx.stroke(); } } }
      }
    }

    resize(); draw();
    window.addEventListener('resize', resize);
    return function() { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseleave', onLeave); };
  }, []);

  return React.createElement('div', {
    style: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100vh * var(--invzoom, 1))', position: 'relative' }
  },
    React.createElement('canvas', { ref: canvasRef, style: { position: 'fixed', top: 0, left: 0, zIndex: 0 } }),
    React.createElement('div', {
      className: 'login-box',
      style: { background: '#FFFFFF', border: 'none', borderRadius: 12, padding: 32, width: 360, boxShadow: '0 8px 30px rgba(0,0,0,0.5)', position: 'relative', zIndex: 1 }
    },
      logoUrl
        ? React.createElement('img', { src: logoUrl, alt: 'Logo', style: { maxWidth: 200, maxHeight: 60, objectFit: 'contain', display: 'block', margin: '0 auto 16px' } })
        : React.createElement('div', { style: { height: 60, marginBottom: 16 } }),
      React.createElement('p', { style: { margin: '0 0 24px', fontSize: 13, color: '#6B7280', textAlign: 'center' } }, subtitle),
      idleNotice && step === 'login' && React.createElement('div', { style: { background: '#FEF3C7', color: '#92400E', border: '1px solid #FDE68A', padding: '8px 12px', borderRadius: 6, fontSize: 12, marginBottom: 12 } }, 'You were signed out due to inactivity. Please sign in again.'),
      error && React.createElement('div', { style: { background: '#FEE2E2', color: '#DC2626', padding: '8px 12px', borderRadius: 6, fontSize: 12, marginBottom: 12 } }, error),
      renderStep()
    )
  );
}

// ─── USER MANAGEMENT (inside Settings) ───
function UserManagement({ showToast }) {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [modalTab, setModalTab] = useState('details');
  const [userActivity, setUserActivity] = useState([]);
  const [activityTotal, setActivityTotal] = useState(0);
  const [activityPage, setActivityPage] = useState(1);
  const [activityAction, setActivityAction] = useState('');
  const [activityFrom, setActivityFrom] = useState('');
  const [activityTo, setActivityTo] = useState('');
  const [activityLoading, setActivityLoading] = useState(false);
  const [statsMetric, setStatsMetric] = useState('units_received');
  const [statsDays, setStatsDays] = useState(30);
  const [statsData, setStatsData] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [userSortCol, setUserSortCol] = useState('username');
  const [userSortDir, setUserSortDir] = useState('asc');
  // Alerts tab state
  const [alertsAvailable, setAlertsAvailable] = useState([]);
  const [alertsSubs, setAlertsSubs] = useState([]); // array of subscribed alert_type keys
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [alertsSaving, setAlertsSaving] = useState(false);
  // PO aging alert rules (sub-section of Alerts tab)
  const [agingRules, setAgingRules] = useState([]);
  const [agingStatuses, setAgingStatuses] = useState([]);
  const [agingWarehouses, setAgingWarehouses] = useState([]);
  const [agingAdding, setAgingAdding] = useState(false);
  const [agingForm, setAgingForm] = useState({ warehouse_id: '', status: '', days_threshold: '' });

  var load = useCallback(async function() {
    setUsers(await api('/auth/users'));
    try { setRoles(await api('/auth/roles')); } catch(e) {}
  }, []);
  useEffect(function() { load(); }, [load]);

  var loadActivity = function(userId, action, from, to, page) {
    setActivityLoading(true);
    var url = '/auth/users/' + userId + '/activity?limit=30&page=' + (page || 1);
    if (action) url += '&action=' + encodeURIComponent(action);
    if (from) url += '&from=' + encodeURIComponent(from);
    if (to) url += '&to=' + encodeURIComponent(to);
    api(url).then(function(r) { setUserActivity(r.rows || []); setActivityTotal(r.total || 0); setActivityLoading(false); }).catch(function() { setActivityLoading(false); });
  };

  var loadStats = function(userId, metric, days) {
    setStatsLoading(true);
    api('/auth/users/' + userId + '/activity-stats?metric=' + encodeURIComponent(metric) + '&days=' + days)
      .then(function(r) { setStatsData(r); setStatsLoading(false); })
      .catch(function() { setStatsLoading(false); });
  };

  var statsMetricOpts = [
    { value: 'units_received', label: 'Units Received', unit: 'units' },
    { value: 'units_missing', label: 'Units Marked Missing', unit: 'units' },
    { value: 'pct_missing', label: '% Missing (missing ÷ received)', unit: '%' },
    { value: 'units_per_hour', label: 'Units Received / Hour Logged In', unit: 'units/hr' }
  ];

  // Fetch stats when tab opens or selection changes
  useEffect(function() {
    if (modalTab === 'stats' && editing && editing.id) {
      loadStats(editing.id, statsMetric, statsDays);
    }
  }, [modalTab, editing && editing.id, statsMetric, statsDays]);

  // Fetch alert subscriptions + aging rules + warehouses when the Alerts tab opens
  useEffect(function() {
    if (modalTab === 'alerts' && editing && editing.id) {
      setAlertsLoading(true);
      Promise.all([
        api('/auth/users/' + editing.id + '/alert-subscriptions'),
        api('/auth/users/' + editing.id + '/aging-alert-rules'),
        api('/warehouses?fields=basic').catch(function() { return []; })
      ]).then(function(results) {
        setAlertsAvailable(results[0].available || []);
        setAlertsSubs(results[0].subscriptions || []);
        setAgingRules(results[1].rules || []);
        setAgingStatuses(results[1].available_statuses || []);
        setAgingWarehouses(results[2] || []);
        setAlertsLoading(false);
      }).catch(function(e) { setAlertsLoading(false); showToast(e.message, 'error'); });
    }
  }, [modalTab, editing && editing.id]);

  var addAgingRule = function() {
    if (!editing || !editing.id) return;
    if (!agingForm.status || !agingForm.days_threshold || parseInt(agingForm.days_threshold) < 1) {
      showToast('Pick a status and enter a days threshold ≥ 1', 'error');
      return;
    }
    api('/auth/users/' + editing.id + '/aging-alert-rules', {
      method: 'POST',
      body: JSON.stringify({
        warehouse_id: agingForm.warehouse_id || null,
        status: agingForm.status,
        days_threshold: parseInt(agingForm.days_threshold),
      })
    }).then(function() {
      return api('/auth/users/' + editing.id + '/aging-alert-rules');
    }).then(function(r) {
      setAgingRules(r.rules || []);
      setAgingForm({ warehouse_id: '', status: '', days_threshold: '' });
      setAgingAdding(false);
    }).catch(function(e) { showToast(e.message, 'error'); });
  };

  var toggleAgingRule = function(rule) {
    api('/auth/aging-alert-rules/' + rule.id, {
      method: 'PUT', body: JSON.stringify({ enabled: !rule.enabled })
    }).then(function() {
      setAgingRules(function(rules) { return rules.map(function(r) { return r.id === rule.id ? Object.assign({}, r, { enabled: !rule.enabled }) : r; }); });
    }).catch(function(e) { showToast(e.message, 'error'); });
  };

  var deleteAgingRule = async function(rule) {
    if (!(await appConfirm('Delete this aging alert rule?', { danger: true, okLabel: 'Delete' }))) return;
    api('/auth/aging-alert-rules/' + rule.id, { method: 'DELETE' })
      .then(function() { setAgingRules(function(rules) { return rules.filter(function(r) { return r.id !== rule.id; }); }); })
      .catch(function(e) { showToast(e.message, 'error'); });
  };

  var toggleAlertSub = function(alertKey) {
    if (!editing || !editing.id) return;
    var nextSubs = alertsSubs.indexOf(alertKey) >= 0
      ? alertsSubs.filter(function(k) { return k !== alertKey; })
      : alertsSubs.concat([alertKey]);
    var prevSubs = alertsSubs;
    setAlertsSubs(nextSubs); // optimistic
    setAlertsSaving(true);
    api('/auth/users/' + editing.id + '/alert-subscriptions', {
      method: 'PUT', body: JSON.stringify({ alert_types: nextSubs })
    }).then(function(r) {
      setAlertsSubs(r.subscriptions || nextSubs);
      setAlertsSaving(false);
    }).catch(function(e) {
      setAlertsSubs(prevSubs); // rollback on failure
      setAlertsSaving(false);
      showToast(e.message, 'error');
    });
  };

  // Chart component — owns its own canvas ref so ref timing is guaranteed
  var StatsChart = function(props) {
    var canvasRef = useRef(null);
    var chartRef = useRef(null);
    useEffect(function() {
      if (!canvasRef.current || !props.series) return;
      if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }
      var labels = props.series.map(function(s) { return new Date(s.day).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }); });
      var data = props.series.map(function(s) { return s.value; });
      var ctx = canvasRef.current.getContext('2d');
      chartRef.current = new Chart(ctx, {
        type: 'bar',
        data: { labels: labels, datasets: [{ label: props.label, data: data, backgroundColor: C.accent + 'CC', borderColor: C.accent, borderWidth: 1, borderRadius: 4 }] },
        options: {
          responsive: true, maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: C.surface, titleColor: C.text, bodyColor: C.textMuted, borderColor: C.border, borderWidth: 1, padding: 10, cornerRadius: 8,
              callbacks: { label: function(c) { return c.parsed.y.toLocaleString() + ' ' + props.unit; } }
            }
          },
          scales: {
            x: { ticks: { color: C.textDim, font: { size: 10 }, maxTicksLimit: 15, maxRotation: 45 }, grid: { color: C.border + '33' } },
            y: { ticks: { color: C.textDim, font: { size: 10 } }, grid: { color: C.border + '33' }, beginAtZero: true }
          }
        }
      });
      return function() { if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; } };
    }, [props.series, props.label, props.unit]);
    return React.createElement('canvas', { ref: canvasRef });
  };

  var openNew = function() { var viewerRole = roles.find(function(r) { return r.name === 'viewer'; }); setForm({ role_id: viewerRole ? viewerRole.id : '' }); setEditing(null); setModalTab('details'); setModalOpen(true); };
  var openEdit = function(u) {
    setForm({ username: u.username, display_name: u.display_name, email: u.email || '', phone: u.phone || '', two_factor_method: u.two_factor_method || '', role_id: u.role_id || '', is_active: u.is_active, notify_on_mention: u.notify_on_mention !== false });
    setEditing(u); setModalTab('details'); setUserActivity([]); setActivityTotal(0); setActivityPage(1); setActivityAction(''); setActivityFrom(''); setActivityTo(''); setModalOpen(true);
    loadActivity(u.id, '', '', '', 1);
  };

  var save = async function() {
    try {
      // Every account has an email (9-26): sign-in codes and password resets go there
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((form.email || '').trim())) { showToast('A valid email is required', 'error'); return; }
      if (editing) {
        // phone '' clears it server-side (and drops the user back to email codes); admin-entered numbers count as verified
        var body = { username: form.username, display_name: form.display_name, email: form.email || null, phone: (form.phone || '').trim(), two_factor_method: form.two_factor_method || null, role_id: form.role_id ? parseInt(form.role_id) : undefined, is_active: form.is_active, notify_on_mention: form.notify_on_mention !== false };
        if (form.password) body.password = form.password;
        await api('/auth/users/' + editing.id, { method: 'PUT', body: JSON.stringify(body) });
        showToast('User updated');
      } else {
        if (!form.username || !form.password) { showToast('Username and password required', 'error'); return; }
        await api('/auth/users', { method: 'POST', body: JSON.stringify({ username: form.username, password: form.password, display_name: form.display_name, email: form.email || null, phone: (form.phone || '').trim() || null, role_id: form.role_id ? parseInt(form.role_id) : undefined }) });
        showToast('User created');
      }
      setModalOpen(false); load();
    } catch (e) { showToast(e.message, 'error'); }
  };

  var roleBadge = function(role, roleDisplayName) {
    var colors = { admin: { bg: C.dangerSoft, text: C.danger }, manager: { bg: C.accentSoft, text: C.accent }, warehouse: { bg: C.warningSoft, text: C.warning }, viewer: { bg: 'rgba(139,143,163,0.12)', text: C.textMuted } };
    var c = colors[role] || { bg: C.accentSoft, text: C.accent };
    return React.createElement('span', { style: { background: c.bg, color: c.text, padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, textTransform: 'uppercase' } }, roleDisplayName || role);
  };

  var actionColors = { login: C.success, login_failed: C.danger, logout: C.textMuted, password_changed: C.warning, password_reset: C.warning, email_updated: C.accent, user_created: C.success, user_updated: C.accent, user_deactivated: C.danger, role_created: C.success, role_updated: C.accent, role_deleted: C.danger, role_permissions_changed: C.warning, settings_updated: C.accent, integration_configured: C.accent, sync_triggered: C.accent, product_viewed: C.textMuted, product_created: C.success, product_updated: C.accent, product_deleted: C.danger, po_viewed: C.textMuted, po_created: C.success, po_updated: C.accent, po_item_added: C.success, po_item_updated: C.accent, po_item_removed: C.danger, po_status_changed: C.warning, items_received: C.success, receive_undone: C.warning, vendor_created: C.success, vendor_updated: C.accent, vendor_deleted: C.danger, warehouse_created: C.success, warehouse_updated: C.accent, warehouse_deleted: C.danger, ticket_created: C.success, ticket_updated: C.accent, fba_plan_created: C.success, wfs_plan_created: C.success };
  var actionOpts = [{ value: '', label: 'All Actions' },
    { value: 'login', label: 'Login' }, { value: 'login_failed', label: 'Login Failed' }, { value: 'logout', label: 'Logout' },
    { value: 'password_changed', label: 'Password Changed' }, { value: 'password_reset', label: 'Password Reset' }, { value: 'email_updated', label: 'Email Updated' },
    { value: 'user_created', label: 'User Created' }, { value: 'user_updated', label: 'User Updated' }, { value: 'user_deactivated', label: 'User Deactivated' },
    { value: 'product_viewed', label: 'Product Viewed' }, { value: 'product_created', label: 'Product Created' }, { value: 'product_updated', label: 'Product Updated' }, { value: 'product_deleted', label: 'Product Deleted' },
    { value: 'po_viewed', label: 'PO Viewed' }, { value: 'po_created', label: 'PO Created' }, { value: 'po_updated', label: 'PO Updated' }, { value: 'po_item_added', label: 'PO Item Added' }, { value: 'po_item_updated', label: 'PO Item Updated' }, { value: 'po_item_removed', label: 'PO Item Removed' }, { value: 'po_status_changed', label: 'PO Status Changed' },
    { value: 'items_received', label: 'Items Received' }, { value: 'receive_undone', label: 'Receive Undone' },
    { value: 'vendor_created', label: 'Vendor Created' }, { value: 'vendor_updated', label: 'Vendor Updated' }, { value: 'vendor_deleted', label: 'Vendor Deleted' },
    { value: 'warehouse_created', label: 'Warehouse Created' }, { value: 'warehouse_updated', label: 'Warehouse Updated' },
    { value: 'ticket_created', label: 'Ticket Created' }, { value: 'ticket_updated', label: 'Ticket Updated' },
    { value: 'fba_plan_created', label: 'FBA Plan Created' }, { value: 'wfs_plan_created', label: 'WFS Plan Created' },
    { value: 'settings_updated', label: 'Settings Updated' }, { value: 'role_permissions_changed', label: 'Permissions Changed' }, { value: 'integration_configured', label: 'Integration Configured' }];

  var tabBtn = function(key, label) {
    var active = modalTab === key;
    return React.createElement('div', {
      key: key, onClick: function() { setModalTab(key); },
      style: { padding: '8px 16px', cursor: 'pointer', fontSize: 13, fontWeight: active ? 600 : 400, color: active ? C.accent : C.textMuted, borderBottom: active ? '2px solid ' + C.accent : '2px solid transparent', transition: 'all 0.15s' }
    }, label);
  };

  var fmtDate = function(d) { var t = new Date(d); return t.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ' ' + t.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }); };
  var totalPages = Math.ceil(activityTotal / 30) || 1;

  var inpStyle = { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '6px 10px', borderRadius: 6, fontSize: 12, fontFamily: 'inherit', outline: 'none' };

  var exportCsv = async function() {
    if (!editing) return;
    try {
      var url = '/auth/users/' + editing.id + '/activity?limit=10000&page=1';
      if (activityAction) url += '&action=' + encodeURIComponent(activityAction);
      if (activityFrom) url += '&from=' + encodeURIComponent(activityFrom);
      if (activityTo) url += '&to=' + encodeURIComponent(activityTo);
      var r = await api(url);
      var rows = r.rows || [];
      if (!rows.length) { showToast('No activity to export', 'error'); return; }
      var csv = 'Date,Action,Details,IP Address\n' + rows.map(function(a) {
        return [fmtDate(a.created_at), (a.action || '').replace(/_/g, ' '), a.details || '', a.ip_address || ''].map(escapeCSV).join(',');
      }).join('\n');
      var blob = new Blob([csv], { type: 'text/csv' });
      var a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = (editing.username || 'user') + '_activity_' + new Date().toISOString().slice(0, 10) + '.csv';
      a.click(); URL.revokeObjectURL(a.href);
      showToast('Exported ' + rows.length + ' entries');
    } catch (e) { showToast(e.message, 'error'); }
  };

  return React.createElement('div', null,
    React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 } },
      React.createElement('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase' } }, 'User Accounts'),
      React.createElement(Btn, { size: 'sm', variant: 'primary', onClick: openNew }, '+ Add User')
    ),
    React.createElement('table', { style: { width: '100%', fontSize: 13, tableLayout: 'auto', fontFamily: 'inherit', borderCollapse: 'collapse' } },
      React.createElement('thead', null,
        React.createElement('tr', { style: { borderBottom: '1px solid ' + C.border } },
          [
            { key: 'username', label: 'Username' },
            { key: 'display_name', label: 'Display Name' },
            { key: 'email', label: 'Email' },
            { key: 'phone', label: 'Phone' },
            { key: 'role', label: 'Role' },
            { key: 'is_active', label: 'Status' },
            { key: '_actions', label: '' }
          ].map(function(col) {
            var sortable = col.key !== '_actions';
            var active = userSortCol === col.key;
            var baseStyle = { padding: '8px 12px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', color: active ? C.accent : C.textMuted };
            return React.createElement('th', {
              key: col.key,
              style: sortable ? Object.assign({}, baseStyle, { cursor: 'pointer', userSelect: 'none' }) : baseStyle,
              onClick: sortable ? function() {
                if (userSortCol === col.key) setUserSortDir(userSortDir === 'asc' ? 'desc' : 'asc');
                else { setUserSortCol(col.key); setUserSortDir('asc'); }
              } : null
            }, col.label + (active ? (userSortDir === 'asc' ? ' ▲' : ' ▼') : ''));
          })
        )
      ),
      React.createElement('tbody', null,
        users.slice().sort(function(a, b) {
          var av = a[userSortCol], bv = b[userSortCol];
          if (userSortCol === 'is_active') { av = a.is_active ? 1 : 0; bv = b.is_active ? 1 : 0; }
          else { av = (av == null ? '' : String(av)).toLowerCase(); bv = (bv == null ? '' : String(bv)).toLowerCase(); }
          if (av < bv) return userSortDir === 'asc' ? -1 : 1;
          if (av > bv) return userSortDir === 'asc' ? 1 : -1;
          return 0;
        }).map(function(u) {
          return React.createElement('tr', {
            key: u.id, onClick: function() { openEdit(u); },
            style: { borderBottom: '1px solid ' + C.border + '22', cursor: 'pointer' },
            onMouseEnter: function(e) { e.currentTarget.style.background = C.surfaceHover; },
            onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; }
          },
            React.createElement(TD, { style: { fontWeight: 500, color: C.text } }, u.username),
            React.createElement(TD, { style: { color: C.textMuted } }, u.display_name),
            React.createElement(TD, { style: { color: u.email ? C.textMuted : C.danger, fontStyle: u.email ? 'normal' : 'italic', fontSize: 12 } }, u.email || 'Not set'),
            React.createElement(TD, { style: { color: C.textMuted, fontSize: 12, whiteSpace: 'nowrap' } }, u.phone ? (u.phone + (u.two_factor_method === 'sms' ? ' · text' : '')) : '—'),
            React.createElement(TD, null, roleBadge(u.role, u.role_display_name)),
            React.createElement(TD, null, React.createElement('span', { style: { color: u.is_active ? C.success : C.danger, fontSize: 12 } }, u.is_active ? 'Active' : 'Disabled')),
            React.createElement(TD, null)
          );
        })
      )
    ),
    React.createElement(Modal, { open: modalOpen, onClose: function() { setModalOpen(false); }, title: editing ? (editing.display_name || editing.username) : 'New User', width: 780 },
      // Tab bar (only show Activity tab when editing)
      editing && React.createElement('div', { style: { display: 'flex', borderBottom: '1px solid ' + C.border, marginBottom: 16 } },
        tabBtn('details', 'Details'), tabBtn('activity', 'Activity (' + activityTotal + ')'), tabBtn('stats', 'Stats'), tabBtn('alerts', 'Alerts')
      ),
      // ─── DETAILS TAB ───
      modalTab === 'details' && React.createElement('div', { style: { minHeight: 540 } },
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: editing ? '1fr 1fr' : '1fr', gap: 20 } },
          // Left column: form fields
          React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 12 } },
            React.createElement(Inp, { label: 'Username (Email)', type: 'email', value: form.username || '', onChange: function(e) { setForm(Object.assign({}, form, { username: e.target.value })); }, placeholder: 'user@example.com' }),
            React.createElement(Inp, { label: 'Display Name', value: form.display_name || '', onChange: function(e) { setForm(Object.assign({}, form, { display_name: e.target.value })); } }),
            React.createElement(Inp, { label: 'Email *', type: 'email', value: form.email || '', onChange: function(e) { setForm(Object.assign({}, form, { email: e.target.value })); }, placeholder: 'user@example.com' }),
            // Text-message 2FA (9-05): admin-entered numbers are trusted (verified on save); users can also verify their own on My Profile
            React.createElement(Inp, { label: 'Mobile phone (for text codes)', type: 'tel', value: form.phone || '', onChange: function(e) { setForm(Object.assign({}, form, { phone: e.target.value })); }, placeholder: '(555) 123-4567 or +52 …' }),
            React.createElement(Sel, { label: 'Default 2FA channel', value: form.two_factor_method || '', onChange: function(e) { setForm(Object.assign({}, form, { two_factor_method: e.target.value })); },
              options: [{ value: '', label: 'Ask at next sign-in' }, { value: 'email', label: 'Email' }, { value: 'sms', label: 'Text message' + ((form.phone || '').trim() ? '' : ' (needs a phone)') }] }),
            React.createElement(Sel, { label: 'Role', value: form.role_id || '', onChange: function(e) { setForm(Object.assign({}, form, { role_id: e.target.value })); },
              options: roles.map(function(r) { return { value: r.id, label: r.display_name || r.name }; }) })
          ),
          // Right column: password + status + account info
          React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 12 } },
            React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4 } },
              React.createElement('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' } }, editing ? 'New Password (leave blank to keep)' : 'Password'),
              React.createElement('input', { type: 'password', value: form.password || '', onChange: function(e) { setForm(Object.assign({}, form, { password: e.target.value })); }, style: Object.assign({}, inpStyle, { padding: '8px 12px', fontSize: 13 }) })
            ),
            editing && editing.id !== 1 && React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' } },
              React.createElement('input', { type: 'checkbox', checked: form.is_active !== false, onChange: function(e) { setForm(Object.assign({}, form, { is_active: e.target.checked })); }, style: { accentColor: C.accent } }),
              React.createElement('span', { style: { fontSize: 13, color: C.textMuted } }, 'Account active')
            ),
            // Account info card
            editing && React.createElement('div', { style: { marginTop: 8, padding: 12, background: C.bg, borderRadius: 8, border: '1px solid ' + C.border } },
              React.createElement('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', marginBottom: 8 } }, 'Account Info'),
              React.createElement('div', { style: { fontSize: 12, color: C.textMuted, lineHeight: 1.8 } },
                React.createElement('div', null, 'Created: ', React.createElement('span', { style: { color: C.text } }, editing.created_at ? new Date(editing.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—')),
                React.createElement('div', null, 'User ID: ', React.createElement('span', { style: { color: C.text, fontFamily: 'inherit' } }, editing.id)),
                React.createElement('div', null, 'Role: ', roleBadge(editing.role, editing.role_display_name))
              )
            )
          )
        ),
        React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 } },
          React.createElement(Btn, { onClick: function() { setModalOpen(false); } }, 'Cancel'),
          React.createElement(Btn, { variant: 'primary', onClick: save }, editing ? 'Update' : 'Create User')
        )
      ),
      // ─── ACTIVITY TAB ───
      modalTab === 'activity' && editing && React.createElement('div', { style: { minHeight: 540 } },
        // Filter bar
        React.createElement('div', { style: { display: 'flex', gap: 10, alignItems: 'flex-end', marginBottom: 12, flexWrap: 'wrap' } },
          React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4 } },
            React.createElement('label', { style: { fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase' } }, 'Action'),
            React.createElement('select', { value: activityAction, onChange: function(e) { var v = e.target.value; setActivityAction(v); setActivityPage(1); loadActivity(editing.id, v, activityFrom, activityTo, 1); }, style: Object.assign({}, inpStyle, { minWidth: 150 }) },
              actionOpts.map(function(o) { return React.createElement('option', { key: o.value, value: o.value }, o.label); })
            )
          ),
          React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4 } },
            React.createElement('label', { style: { fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase' } }, 'From'),
            React.createElement('input', { type: 'date', value: activityFrom, onChange: function(e) { var v = e.target.value; setActivityFrom(v); setActivityPage(1); loadActivity(editing.id, activityAction, v, activityTo, 1); }, style: Object.assign({}, inpStyle, { colorScheme: 'dark' }) })
          ),
          React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4 } },
            React.createElement('label', { style: { fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase' } }, 'To'),
            React.createElement('input', { type: 'date', value: activityTo, onChange: function(e) { var v = e.target.value; setActivityTo(v); setActivityPage(1); loadActivity(editing.id, activityAction, activityFrom, v, 1); }, style: Object.assign({}, inpStyle, { colorScheme: 'dark' }) })
          ),
          (activityAction || activityFrom || activityTo) && React.createElement(Btn, { size: 'sm', onClick: function() { setActivityAction(''); setActivityFrom(''); setActivityTo(''); setActivityPage(1); loadActivity(editing.id, '', '', '', 1); } }, 'Clear'),
          React.createElement('span', { style: { fontSize: 11, color: C.textMuted, marginLeft: 'auto' } }, activityTotal + ' entries'),
          activityTotal > 0 && React.createElement(Btn, { size: 'sm', onClick: exportCsv }, 'Export CSV')
        ),
        // Activity list
        React.createElement('div', { style: { maxHeight: 400, overflow: 'auto', border: '1px solid ' + C.border, borderRadius: 8 } },
          activityLoading && React.createElement('div', { style: { padding: 24, textAlign: 'center', color: C.textMuted, fontSize: 12 } }, 'Loading...'),
          !activityLoading && userActivity.length === 0 && React.createElement('div', { style: { padding: 24, textAlign: 'center', color: C.textMuted, fontSize: 12 } }, 'No activity recorded'),
          !activityLoading && userActivity.map(function(a) {
            var ac = actionColors[a.action] || C.accent;
            var isLoginAction = ['login', 'login_failed', 'logout'].includes(a.action);
            return React.createElement('div', { key: a.id, style: { display: 'grid', gridTemplateColumns: '140px 160px 1fr' + (isLoginAction ? ' 120px' : ''), gap: 8, padding: '8px 12px', borderBottom: '1px solid ' + C.border + '44', alignItems: 'center', fontSize: 12 } },
              React.createElement('span', { style: { color: C.textMuted, fontSize: 11, whiteSpace: 'nowrap' } }, fmtDate(a.created_at)),
              React.createElement('span', { style: { padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: ac + '22', color: ac, textTransform: 'uppercase', justifySelf: 'start' } }, (a.action || '').replace(/_/g, ' ')),
              React.createElement('span', { style: { color: C.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, a.details || '—'),
              isLoginAction && React.createElement('span', { style: { color: C.textDim, fontSize: 10, fontFamily: 'inherit' } }, a.ip_address || '')
            );
          })
        ),
        // Pagination
        totalPages > 1 && React.createElement('div', { style: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 12 } },
          React.createElement(Btn, { size: 'sm', disabled: activityPage <= 1, onClick: function() { var p = activityPage - 1; setActivityPage(p); loadActivity(editing.id, activityAction, activityFrom, activityTo, p); } }, 'Prev'),
          React.createElement('span', { style: { fontSize: 12, color: C.textMuted } }, 'Page ' + activityPage + ' of ' + totalPages),
          React.createElement(Btn, { size: 'sm', disabled: activityPage >= totalPages, onClick: function() { var p = activityPage + 1; setActivityPage(p); loadActivity(editing.id, activityAction, activityFrom, activityTo, p); } }, 'Next')
        )
      ),
      // ─── STATS TAB ───
      modalTab === 'stats' && editing && React.createElement('div', { style: { minHeight: 540 } },
        React.createElement('div', { style: { display: 'flex', gap: 10, alignItems: 'flex-end', marginBottom: 12, flexWrap: 'wrap' } },
          React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4 } },
            React.createElement('label', { style: { fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase' } }, 'Metric'),
            React.createElement('select', { value: statsMetric, onChange: function(e) { setStatsMetric(e.target.value); }, style: Object.assign({}, inpStyle, { minWidth: 240 }) },
              statsMetricOpts.map(function(o) { return React.createElement('option', { key: o.value, value: o.value }, o.label); })
            )
          ),
          React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4 } },
            React.createElement('label', { style: { fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase' } }, 'Period'),
            React.createElement('div', { style: { display: 'flex', gap: 4 } },
              [7, 30, 90].map(function(d) {
                var active = statsDays === d;
                return React.createElement('button', { key: d, onClick: function() { setStatsDays(d); },
                  style: { background: active ? C.accent : C.bg, color: active ? '#fff' : C.text, border: '1px solid ' + (active ? C.accent : C.border), padding: '6px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }
                }, d + 'd');
              })
            )
          )
        ),
        statsLoading && !statsData && React.createElement('div', { style: { textAlign: 'center', padding: 60, color: C.textMuted, fontSize: 12 } }, 'Loading…'),
        statsData && (function() {
          var total = statsData.series.reduce(function(s, r) { return s + (r.value || 0); }, 0);
          var nonZero = statsData.series.filter(function(r) { return r.value > 0; }).length;
          var avg = nonZero > 0 ? total / nonZero : 0;
          var metricOpt = statsMetricOpts.find(function(o) { return o.value === statsMetric; }) || statsMetricOpts[0];
          var fmtVal = function(v) { return (Math.round(v * 100) / 100).toLocaleString() + ' ' + metricOpt.unit; };
          return React.createElement('div', null,
            React.createElement('div', { style: { display: 'flex', gap: 12, marginBottom: 12 } },
              React.createElement('div', { style: { flex: 1, background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: '10px 14px', textAlign: 'center' } },
                React.createElement('div', { style: { fontSize: 10, color: C.textDim, textTransform: 'uppercase', marginBottom: 2 } }, 'Total'),
                React.createElement('div', { style: { fontSize: 16, fontWeight: 700, color: C.accent } }, fmtVal(total))
              ),
              React.createElement('div', { style: { flex: 1, background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: '10px 14px', textAlign: 'center' } },
                React.createElement('div', { style: { fontSize: 10, color: C.textDim, textTransform: 'uppercase', marginBottom: 2 } }, 'Active Days'),
                React.createElement('div', { style: { fontSize: 16, fontWeight: 700, color: C.text } }, nonZero + ' / ' + statsData.series.length)
              ),
              React.createElement('div', { style: { flex: 1, background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: '10px 14px', textAlign: 'center' } },
                React.createElement('div', { style: { fontSize: 10, color: C.textDim, textTransform: 'uppercase', marginBottom: 2 } }, 'Avg / Active Day'),
                React.createElement('div', { style: { fontSize: 16, fontWeight: 700, color: C.text } }, fmtVal(avg))
              )
            ),
            React.createElement('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: 12, position: 'relative', height: 320 } },
              React.createElement(StatsChart, { series: statsData.series, label: metricOpt.label, unit: metricOpt.unit })
            )
          );
        })()
      ),
      // ─── ALERTS TAB ───
      modalTab === 'alerts' && editing && React.createElement('div', { style: { minHeight: 540 } },
        // ─── @Mention notifications (stored on users.notify_on_mention, instant-save) ───
        React.createElement('div', { style: { marginBottom: 20 } },
          React.createElement('div', { style: { fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 8 } }, '@Mention Notifications'),
          React.createElement('label', { style: { display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 14px', borderRadius: 8, background: C.surface, border: '1px solid ' + (editing.notify_on_mention !== false ? C.accent + '60' : C.border), cursor: 'pointer' } },
            React.createElement('input', {
              type: 'checkbox',
              checked: editing.notify_on_mention !== false,
              onChange: function(e) {
                var next = e.target.checked;
                setEditing(Object.assign({}, editing, { notify_on_mention: next }));
                api('/auth/users/' + editing.id, { method: 'PUT', body: JSON.stringify({ notify_on_mention: next }) })
                  .then(function() { setUsers(function(prev) { return prev.map(function(u) { return u.id === editing.id ? Object.assign({}, u, { notify_on_mention: next }) : u; }); }); })
                  .catch(function(err) {
                    setEditing(Object.assign({}, editing, { notify_on_mention: !next })); // rollback
                    showToast(err.message, 'error');
                  });
              },
              style: { accentColor: C.accent, marginTop: 3 }
            }),
            React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 2, flex: 1 } },
              React.createElement('div', { style: { fontSize: 13, fontWeight: 600, color: C.text } }, 'Email me when @mentioned in a note'),
              React.createElement('div', { style: { fontSize: 12, color: C.textMuted } }, 'Sends an email any time another teammate tags this user in an internal note (PO, Receiving, FBA, or WFS).')
            )
          )
        ),
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 } },
          React.createElement('div', null,
            React.createElement('div', { style: { fontSize: 14, fontWeight: 600, color: C.text } }, 'Email Alerts'),
            React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 2 } }, 'This user will receive emails for the alert types selected below.')
          ),
          alertsSaving && React.createElement('span', { style: { fontSize: 11, color: C.textMuted } }, 'Saving…')
        ),
        (!editing.email || !String(editing.email).trim()) && React.createElement('div', {
          style: { padding: '10px 14px', borderRadius: 8, background: C.warning + '15', border: '1px solid ' + C.warning + '40', color: C.warning, fontSize: 12, marginBottom: 12 }
        }, 'This user has no email set on the Details tab — alerts will have no destination until you add one.'),
        alertsLoading
          ? React.createElement('div', { style: { padding: 40, textAlign: 'center', color: C.textMuted } }, 'Loading subscriptions…')
          : React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 8 } },
              alertsAvailable.length === 0 && React.createElement('div', { style: { padding: 20, textAlign: 'center', color: C.textMuted, fontSize: 12 } }, 'No alert types available.'),
              alertsAvailable.map(function(a) {
                var subscribed = alertsSubs.indexOf(a.key) >= 0;
                return React.createElement('label', { key: a.key,
                  style: { display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 14px', borderRadius: 8, background: C.surface, border: '1px solid ' + (subscribed ? C.accent + '60' : C.border), cursor: 'pointer' }
                },
                  React.createElement('input', { type: 'checkbox', checked: subscribed,
                    onChange: function() { toggleAlertSub(a.key); },
                    disabled: alertsSaving,
                    style: { accentColor: C.accent, marginTop: 3 } }),
                  React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 2, flex: 1 } },
                    React.createElement('div', { style: { fontSize: 13, fontWeight: 600, color: C.text } }, a.label),
                    React.createElement('div', { style: { fontSize: 12, color: C.textMuted } }, a.description),
                    React.createElement('div', { style: { fontSize: 10, color: C.textDim, fontFamily: 'inherit', marginTop: 2 } }, a.key)
                  )
                );
              })
            ),
        // ─── PO Aging Alerts (parameterized) ───
        !alertsLoading && React.createElement('div', { style: { marginTop: 24 } },
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 } },
            React.createElement('div', null,
              React.createElement('div', { style: { fontSize: 14, fontWeight: 600, color: C.text } }, 'PO Aging Alerts'),
              React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginTop: 2 } }, 'Email when a PO has been in a chosen status for ≥ N days. Checked hourly.')
            ),
            !agingAdding && React.createElement(Btn, { size: 'sm', onClick: function() { setAgingAdding(true); } },
              React.createElement(LIcon, { name: 'plus', size: 14 }), ' Add Rule'
            )
          ),
          agingAdding && React.createElement('div', { style: { display: 'flex', gap: 8, padding: '10px 12px', background: C.surface, border: '1px solid ' + C.accent + '40', borderRadius: 8, marginBottom: 8, flexWrap: 'wrap', alignItems: 'flex-end' } },
            React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 160 } },
              React.createElement('label', { style: { fontSize: 10, color: C.textMuted, textTransform: 'uppercase' } }, 'Warehouse'),
              React.createElement('select', { value: agingForm.warehouse_id, onChange: function(e) { setAgingForm(Object.assign({}, agingForm, { warehouse_id: e.target.value })); }, style: Object.assign({}, inpStyle, { padding: '6px 10px', fontSize: 12 }) },
                React.createElement('option', { value: '' }, 'Any warehouse'),
                agingWarehouses.map(function(w) { return React.createElement('option', { key: w.id, value: w.id }, w.warehouse_id + ' — ' + w.name); })
              )
            ),
            React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 140 } },
              React.createElement('label', { style: { fontSize: 10, color: C.textMuted, textTransform: 'uppercase' } }, 'Status'),
              React.createElement('select', { value: agingForm.status, onChange: function(e) { setAgingForm(Object.assign({}, agingForm, { status: e.target.value })); }, style: Object.assign({}, inpStyle, { padding: '6px 10px', fontSize: 12 }) },
                React.createElement('option', { value: '' }, 'Pick status…'),
                agingStatuses.map(function(s) { return React.createElement('option', { key: s, value: s }, s); })
              )
            ),
            React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4, width: 110 } },
              React.createElement('label', { style: { fontSize: 10, color: C.textMuted, textTransform: 'uppercase' } }, 'Days ≥'),
              React.createElement('input', { type: 'number', min: 1, value: agingForm.days_threshold, onChange: function(e) { setAgingForm(Object.assign({}, agingForm, { days_threshold: e.target.value })); }, placeholder: '14', style: Object.assign({}, inpStyle, { padding: '6px 10px', fontSize: 12 }) })
            ),
            React.createElement('div', { style: { display: 'flex', gap: 6 } },
              React.createElement(Btn, { size: 'sm', variant: 'primary', onClick: addAgingRule }, 'Save'),
              React.createElement(Btn, { size: 'sm', onClick: function() { setAgingAdding(false); setAgingForm({ warehouse_id: '', status: '', days_threshold: '' }); } }, 'Cancel')
            )
          ),
          agingRules.length === 0 && !agingAdding && React.createElement('div', { style: { padding: '14px 16px', textAlign: 'center', color: C.textMuted, fontSize: 12, background: C.surface, border: '1px dashed ' + C.border, borderRadius: 8 } }, 'No aging rules yet. Click "Add Rule" to create one.'),
          agingRules.length > 0 && React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 6 } },
            agingRules.map(function(r) {
              return React.createElement('div', { key: r.id, style: { display: 'flex', gap: 12, alignItems: 'center', padding: '10px 14px', background: C.surface, border: '1px solid ' + (r.enabled ? C.border : C.border + '60'), borderRadius: 8, opacity: r.enabled ? 1 : 0.55 } },
                React.createElement('label', { style: { display: 'flex', alignItems: 'center', cursor: 'pointer' } },
                  React.createElement('input', { type: 'checkbox', checked: !!r.enabled, onChange: function() { toggleAgingRule(r); }, style: { accentColor: C.accent } })
                ),
                React.createElement('div', { style: { flex: 1, display: 'flex', gap: 14, alignItems: 'center', fontSize: 12, color: C.text } },
                  React.createElement('span', { style: { padding: '3px 8px', borderRadius: 6, background: (r.wh_code ? C.accent + '22' : C.textDim + '22'), color: (r.wh_code ? C.accent : C.textMuted), fontFamily: 'inherit', fontWeight: 600 } }, r.wh_code || 'Any WH'),
                  React.createElement('span', { style: { padding: '3px 8px', borderRadius: 6, background: '#FBBF2422', color: '#FBBF24', textTransform: 'capitalize', fontWeight: 600 } }, r.status),
                  React.createElement('span', { style: { color: C.textMuted } }, '≥ ', React.createElement('strong', { style: { color: C.text } }, r.days_threshold), ' days')
                ),
                React.createElement('button', { onClick: function() { deleteAgingRule(r); }, style: { background: 'transparent', border: '1px solid ' + C.border, color: C.danger, padding: '6px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12 } }, React.createElement(LIcon, { name: 'trash-2', size: 12 }))
              );
            })
          )
        )
      )
    )
  );
}

// ─── MAIN APP ───
// ─── LOCATION PICKER MODAL ───
function LocationPickerModal({ onConfirm, onCancel, title, initialValue }) {
  var [locations, setLocations] = useState([]);
  var [value, setValue] = useState(initialValue || '');
  var [pallets, setPallets] = useState('');
  var [boxes, setBoxes] = useState('');
  var [loading, setLoading] = useState(true);

  useEffect(function() {
    api('/receiving/locations').then(function(locs) { setLocations(locs); setLoading(false); }).catch(function() { setLoading(false); });
  }, []);

  var isNew = value.trim() && !locations.some(function(l) { return l.toLowerCase() === value.trim().toLowerCase(); });
  var validPrefix = value.trim().toUpperCase().startsWith('LOC');
  // Each check-in is one bin. A comma means the user typed (or clicked) a multi-loc CSV like
  // "LOC806, LOC803" — reject it and prompt to do separate check-ins.
  var hasComma = value.indexOf(',') >= 0;

  var handleConfirm = async function() {
    var v = value.trim().toUpperCase();
    if (!v) return;
    if (!v.startsWith('LOC')) return;
    if (hasComma) return;
    if (isNew) {
      if (!(await appConfirm('Location "' + v + '" does not exist. Create it?'))) return;
    }
    onConfirm(v, parseInt(pallets) || 0, parseInt(boxes) || 0);
  };

  return React.createElement(Modal, { open: true, onClose: onCancel, title: title || 'Check In' },
    React.createElement('div', { style: { minWidth: 340 } },
      loading ? React.createElement('div', { style: { color: C.textMuted, padding: 20, textAlign: 'center' } }, 'Loading locations...')
      : React.createElement('div', null,
        locations.length > 0 && React.createElement('div', { style: { marginBottom: 12 } },
          React.createElement('label', { style: { fontSize: 12, color: C.textMuted, marginBottom: 6, display: 'block' } }, 'Existing Locations'),
          React.createElement('div', { style: { display: 'flex', gap: 6, flexWrap: 'wrap' } },
            locations.map(function(loc) {
              var active = value === loc;
              return React.createElement('button', {
                key: loc, onClick: function() { setValue(loc); },
                style: { padding: '6px 12px', borderRadius: 6, border: '1px solid ' + (active ? C.accent : C.border), background: active ? C.accent + '18' : C.surface, color: active ? C.accent : C.text, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }
              }, loc);
            })
          )
        ),
        React.createElement(Inp, {
          label: 'WH Location', value: value, placeholder: 'e.g. LOC600',
          onChange: function(e) { setValue(e.target.value.toUpperCase()); }
        }),
        value.trim() && !validPrefix && React.createElement('div', { style: { fontSize: 11, color: C.danger, marginTop: 4 } }, 'Location must start with "LOC"'),
        value.trim() && hasComma && React.createElement('div', { style: { fontSize: 11, color: C.danger, marginTop: 4 } }, 'One location at a time — do a separate check-in for each bin'),
        isNew && validPrefix && !hasComma && React.createElement('div', { style: { fontSize: 11, color: C.warning, marginTop: 4 } }, 'New location — will be created'),
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 } },
          React.createElement(Inp, { label: 'Pallets', type: 'number', value: pallets, placeholder: '0', onChange: function(e) { setPallets(e.target.value); } }),
          React.createElement(Inp, { label: 'Boxes', type: 'number', value: boxes, placeholder: '0', onChange: function(e) { setBoxes(e.target.value); } })
        ),
        React.createElement('div', { style: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 } },
          React.createElement(Btn, { onClick: onCancel }, 'Cancel'),
          React.createElement(Btn, { variant: 'primary', onClick: handleConfirm, disabled: !value.trim() || !validPrefix || hasComma }, 'Confirm')
        )
      )
    )
  );
}

// ─── Sidebar hover styles ───
(function() {
  if (!document.getElementById('sidebar-hover-styles')) {
    var style = document.createElement('style');
    style.id = 'sidebar-hover-styles';
    style.textContent =
      '.side-nav-btn{transition:background 0.15s ease!important}' +
      '.side-nav-btn:hover{background:rgba(79,110,247,0.06)!important}' +
      '.side-nav-child:hover{background:rgba(79,110,247,0.06)!important}' +
      '.side-bottom-btn{transition:background 0.15s ease!important}' +
      '.side-bottom-btn:hover{background:rgba(0,0,0,0.04)!important}' +
      '.po-tab:not(.po-tab-active):hover{background:rgba(79,110,247,0.06)!important;filter:brightness(1.4)!important}';
    document.head.appendChild(style);
  }
})();

// --- URL routing helpers ---
// Runs several state changes as ONE render. The app is mounted with ReactDOM.render, which only
// groups changes made inside React's own event handlers; anything after an await or in a browser
// event renders once per change. Without ReactDOM's helper the changes run as they did before.
function routeBatch(fn) {
  var batch = typeof ReactDOM !== 'undefined' && ReactDOM && ReactDOM.unstable_batchedUpdates;
  if (typeof batch === 'function') batch(fn); else fn();
}
var ROUTE_SIMPLE_PAGES = ['create-po', 'fba-shipments', 'fba-reconciliation', 'wfs-shipments', 'meli-shipments', 'meli-research', 'receiving',
  'cs', 'analysis', 'forecasting', 'zentail-mappings', 'reports', 'shopify-faq', 'shopify-wmca', 'accounting', 'accounting-history', 'accounting-trends', 'accounting-bills',
  'profile', 'chat'];

function pageToPath(pg, arg, opts) {
  if (!pg || pg === 'dashboard') return '/';
  if (pg === 'not-found') return arg || '/';
  // The Office: arg is the sub-path ('/refunds', '/filing/danny', '/filing/danny/settings')
  if (pg === 'agent-office') return '/the-office' + (arg || '');
  if (pg === 'orders') {
    var params = [];
    if (opts && opts.status) params.push('status=' + encodeURIComponent(opts.status));
    if (opts && opts.warehouse) params.push('warehouse=' + encodeURIComponent(opts.warehouse));
    return '/purchase-orders' + (params.length ? '?' + params.join('&') : '');
  }
  if (pg === 'orders-module') return '/orders';
  if (pg === 'po-detail') return '/po/' + (arg || '');
  if (pg === 'fba-detail') return '/fba/' + (arg || '');
  if (pg === 'wfs-detail') return '/wfs/' + (arg || '');
  if (pg === 'meli-detail') return '/meli/' + (arg || '');
  if (pg === 'meli-research' && arg) return '/meli-research/' + arg;
  // Research (docs/26): Data = vendor price-list sheets, Amazon US = recommendations; arg = vendor id
  if (pg === 'research-data') return '/research/data' + (arg ? '/' + arg : '');
  if (pg === 'amazon-research') return '/research/amazon-us' + (arg ? '/' + arg : '');
  // SHEIN dashboard: arg is the tab ('attention', 'settings'); Orders is the bare path
  if (pg === 'shopify-shein') return '/shopify-shein' + (arg ? '/' + arg : '');
  if (pg === 'receiving-po') return '/receiving/' + (arg || '');
  if (pg === 'warehouse-detail') return '/warehouse/' + (arg || '');
  if (pg === 'accounting' && arg) return '/accounting/' + arg;
  // /cs and /cs/:id are legacy desk links — the page now points at cs.atharvatoolz.com (docs/22)
  if (pg === 'cs') return '/cs' + (arg ? '/' + arg : '');
  // Catalog group (9-05): Quick Edit + Import / Export nest under /catalog (/import-export = legacy path)
  if (pg === 'catalog-quick-edit') return '/catalog/quick-edit';
  if (pg === 'import-export') return '/catalog/import-export';
  if (pg === 'products') return arg ? '/catalog/' + arg : '/catalog';
  if (pg === 'settings') return '/settings/company';
  if (pg.startsWith('settings-')) return '/settings/' + pg.substring(9);
  return '/' + pg;
}

function pathToPage(pathname) {
  var p = (pathname || '/').replace(/\/+$/, '') || '/';
  if (p === '/' || p === '/dashboard') return { page: 'dashboard', arg: null };
  if (p === '/purchase-orders') return { page: 'orders', arg: null };
  if (p === '/orders') return { page: 'orders-module', arg: null };
  var m;
  if ((m = p.match(/^\/po\/(.+)$/))) return { page: 'po-detail', arg: m[1] };
  if ((m = p.match(/^\/fba\/(.+)$/))) return { page: 'fba-detail', arg: m[1] };
  if ((m = p.match(/^\/wfs\/(.+)$/))) return { page: 'wfs-detail', arg: m[1] };
  if ((m = p.match(/^\/meli\/(.+)$/))) return { page: 'meli-detail', arg: m[1] };
  if ((m = p.match(/^\/meli-research\/(.+)$/))) return { page: 'meli-research', arg: m[1] };
  if ((m = p.match(/^\/research\/data(?:\/(\d+))?$/))) return { page: 'research-data', arg: m[1] || null };
  if ((m = p.match(/^\/research\/amazon-us(?:\/(\d+))?$/))) return { page: 'amazon-research', arg: m[1] || null };
  // links from before the 10-03 rename: /price-lists(/:listId) and /amazon-research
  if (/^\/price-lists(\/\d+)?$/.test(p)) return { page: 'research-data', arg: null };
  if (p === '/amazon-research') return { page: 'amazon-research', arg: null };
  if ((m = p.match(/^\/shopify-shein(?:\/([a-z-]+))?$/))) return { page: 'shopify-shein', arg: m[1] || null };
  if ((m = p.match(/^\/receiving\/(.+)$/))) return { page: 'receiving-po', arg: m[1] };
  if ((m = p.match(/^\/warehouse\/(.+)$/))) return { page: 'warehouse-detail', arg: m[1] };
  if ((m = p.match(/^\/accounting\/(\d+)$/))) return { page: 'accounting', arg: m[1] };
  if ((m = p.match(/^\/cs\/(\d+)$/))) return { page: 'cs', arg: m[1] };
  if (p === '/catalog/quick-edit') return { page: 'catalog-quick-edit', arg: null };
  if (p === '/catalog/import-export' || p === '/import-export') return { page: 'import-export', arg: null };
  if (p === '/catalog') return { page: 'products', arg: null };
  if ((m = p.match(/^\/catalog\/(.+)$/))) return { page: 'products', arg: m[1] };
  if ((m = p.match(/^\/settings\/(.+)$/))) return { page: 'settings-' + m[1], arg: null };
  // The Office building/floor/agent deep links; /agent-office is the legacy
  // path — same page, and the initial replaceState normalizes it to /the-office
  if ((m = p.match(/^\/(?:the-office|agent-office)(\/.+)?$/))) return { page: 'agent-office', arg: m[1] ? m[1].replace(/\/+$/, '') : null };
  var simple = p.substring(1);
  if (ROUTE_SIMPLE_PAGES.indexOf(simple) !== -1) return { page: simple, arg: null };
  // Auth paths are normalized by the login flow; they are not "unknown".
  if (p === '/login' || p === '/reset-password') return { page: 'dashboard', arg: null };
  // Unknown app path (e.g. /products, /shipping): a Not Found card, URL kept (9-26).
  return { page: 'not-found', arg: p };
}

// Query-string → route opts, shared by the boot parse and the popstate fallback (no history.state).
function routeOptsFromQuery(page, sp) {
  var opts = {};
  if (page === 'orders') {
    if (sp.get('status')) opts.status = sp.get('status');
    if (sp.get('warehouse')) opts.warehouse = sp.get('warehouse');
  }
  if ((page === 'products' || page === 'po-detail') && sp.get('tab')) opts.tab = sp.get('tab');
  return opts;
}

// The boot-time URL normalization keeps the query string. Pages read ?order=&channel= (Orders),
// ?tab= (Analysis, Receive PO) and ?status=&q= (Receiving) from location.search when they mount,
// and those are exactly the hrefs they generate for ctrl/shift/middle-click; rewriting the URL to
// the bare canonical path before any page mounted dropped them (review 2026-09-28 HIGH #5). Only
// a legacy path is replaced; when pageToPath rebuilt the page's own query, that wins.
function bootUrlFor(route, loc) {
  var canon = pageToPath(route.page, route.arg, route.opts);
  var hash = loc.hash || '';
  if (canon.indexOf('?') !== -1) return canon + hash;
  return canon + (loc.search || '') + hash;
}

var _initRoute = pathToPage(window.location.pathname);
_initRoute.opts = routeOptsFromQuery(_initRoute.page, new URLSearchParams(window.location.search));

// Global floating progress card for the Auto-Fill Drafts "Run Now" action.
// Mounted at the app root so it survives page navigation. Listens for
// `autofill:start` window events from the SettingsPage Run Now button and
// polls /admin/auto-fill-drafts/runs/:id/progress every 1s until done.
function AutoFillProgressCard() {
  const [runId, setRunId] = useState(null);
  const [data, setData] = useState(null);
  const [visible, setVisible] = useState(false);
  const pollRef = useRef(null);

  useEffect(function() {
    var handler = function(e) {
      var id = e.detail && e.detail.runId;
      if (!id) return;
      setRunId(id);
      setData(null);
      setVisible(true);
    };
    window.addEventListener('autofill:start', handler);
    return function() { window.removeEventListener('autofill:start', handler); };
  }, []);

  useEffect(function() {
    if (!runId || !visible) return;
    var stopped = false;
    var poll = function() {
      if (stopped) return;
      api('/admin/auto-fill-drafts/runs/' + runId + '/progress').then(function(r) {
        if (stopped) return;
        setData(r);
        if (r.status === 'running') {
          pollRef.current = setTimeout(poll, 1000);
        } else {
          // Notify listeners (e.g., Settings page's Recent Runs table) so they
          // can refresh exactly once when the run finishes.
          window.dispatchEvent(new CustomEvent('autofill:done', { detail: { runId: runId, status: r.status } }));
          // Auto-dismiss: 8s on success, 30s on error (so user can read it)
          var delay = r.status === 'success' ? 8000 : 30000;
          pollRef.current = setTimeout(function() { setVisible(false); }, delay);
        }
      }).catch(function() {
        if (!stopped) pollRef.current = setTimeout(poll, 2000);
      });
    };
    poll();
    return function() { stopped = true; if (pollRef.current) clearTimeout(pollRef.current); };
  }, [runId, visible]);

  if (!visible || !runId) return null;

  var done = data && data.status !== 'running';
  var errored = done && data && data.status === 'error';
  var success = done && data && data.status === 'success';
  var pct = data && typeof data.progress_pct === 'number' ? data.progress_pct : null;
  var msg = (data && data.progress_message) || 'Starting...';
  var barColor = errored ? '#EF4444' : success ? '#10B981' : '#3B82F6';

  return React.createElement('div', {
    style: {
      position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
      width: 340, background: '#1F2937', color: '#F9FAFB',
      border: '1px solid #374151', borderRadius: 10,
      boxShadow: '0 10px 25px -5px rgba(0,0,0,0.5)',
      padding: '14px 16px', fontFamily: 'inherit',
    }
  },
    // Header
    React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 } },
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600 } },
        React.createElement(LIcon, { name: errored ? 'alert-circle' : success ? 'check-circle' : 'activity', size: 16, color: barColor }),
        errored ? 'Auto-Fill Failed' : success ? 'Auto-Fill Complete' : 'Auto-Fill Running'
      ),
      React.createElement('button', {
        onClick: function() { setVisible(false); },
        title: 'Close (run continues in background)',
        style: { background: 'transparent', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: 4, display: 'flex', alignItems: 'center' }
      }, React.createElement(LIcon, { name: 'x', size: 16 }))
    ),
    // Progress bar
    React.createElement('div', {
      style: { height: 6, background: '#374151', borderRadius: 3, overflow: 'hidden', marginBottom: 10 }
    },
      React.createElement('div', {
        style: {
          height: '100%',
          width: (pct == null ? (done ? 100 : 30) : pct) + '%',
          background: barColor,
          borderRadius: 3,
          transition: 'width 0.4s ease',
          opacity: (pct == null && !done) ? 0.6 : 1,
        }
      })
    ),
    // Message
    React.createElement('div', { style: { fontSize: 12, color: '#D1D5DB', lineHeight: 1.4 } }, msg),
    // % counter while running
    !done && pct != null && React.createElement('div', { style: { fontSize: 11, color: '#9CA3AF', marginTop: 4 } }, pct + '%'),
    // Success summary
    success && React.createElement('div', { style: { fontSize: 12, color: '#10B981', marginTop: 6, fontWeight: 600 } },
      (data.lines_added || 0) + ' line' + ((data.lines_added || 0) === 1 ? '' : 's') + ' added · ' +
      ((data.duration_ms || 0) / 1000).toFixed(1) + 's'
    ),
    // Error message
    errored && React.createElement('div', { style: { fontSize: 12, color: '#FCA5A5', marginTop: 6 } }, data.error_message || 'Unknown error'),
    // Background hint while running
    !done && React.createElement('div', { style: { fontSize: 11, color: '#6B7280', marginTop: 8, fontStyle: 'italic' } }, 'Safe to close — run continues in background')
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [page, setPage] = useState(_initRoute.page);
  const [pageArg, setPageArg] = useState(_initRoute.arg);
  const [toast, setToast] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [iconLogoUrl, setIconLogoUrl] = useState(null);
  const [theme, setTheme] = useState('dark');
  const [sideCollapsed, setSideCollapsed] = useState(function() {
    try { var v = localStorage.getItem('sidebar_collapsed'); return v === null ? true : v === 'true'; } catch(e) { return true; }
  });
  var [sideExpanded, setSideExpanded] = useState(function() {
    try { return JSON.parse(localStorage.getItem('sidebar_expanded') || '{}'); } catch(e) { return {}; }
  });
  var [sideHover, setSideHover] = useState(false);
  var sideHoverTimer = useRef(null);
  // Phones / tablets (< 900 layout px): top bar + hamburger drawer instead of the sidebar (9-27)
  var viewport = useViewport();
  var toggleSidebar = function() {
    var next = !sideCollapsed;
    setSideCollapsed(next);
    // When unpinning, force hover on so overlay stays visible
    if (next) setSideHover(true);
    saveSidebarPref(next);
  };
  // On logout the sidebar unmounts mid-hover (Sign out lives inside the open
  // drawer), stranding sideHover=true — the next login rendered the drawer
  // stuck open in collapsed mode. Reset it whenever the user goes away; on
  // login, pull the server-side pin preference (it may differ from this
  // browser's localStorage).
  useEffect(function() {
    if (!user) {
      setSideHover(false);
      if (sideHoverTimer.current) clearTimeout(sideHoverTimer.current);
      return;
    }
    syncSidebarPref(function(c) { setSideCollapsed(c); });
  }, [user]);

  const [pageOpts, setPageOpts] = useState(_initRoute.opts || {});
  // Track the current page name globally so window.__reportError can label errors
  // with the area the user was in (e.g. 'orders', 'fba', 'analysis').
  useEffect(() => { window.__currentPage = page; }, [page]);
  const navigate = (p, arg, opts) => {
    // Defensive: detail pages REQUIRE a real arg. If a caller passes
    // null/undefined (e.g. po-detail with po.id missing from an API response),
    // bail so we don't fire 5 server requests with /null and leave the user
    // staring at a broken page.
    var detailPages = ['po-detail', 'fba-detail', 'wfs-detail', 'meli-detail', 'receiving-po', 'warehouse-detail'];
    if (detailPages.indexOf(p) !== -1 && (arg == null || arg === 'null' || arg === '')) {
      console.warn('[navigate] refusing to navigate to', p, 'with empty arg');
      return;
    }
    // One step, not three (Anthony 2026-09-29, error_log 706): called from a promise, each setter
    // rendered by itself, so a detail page existed for an instant with the PREVIOUS page's argument
    // and asked the server for plan / PO "null".
    routeBatch(function() { setPage(p); setPageArg(arg || null); setPageOpts(opts || {}); });
    var url = pageToPath(p, arg, opts);
    history.pushState({ page: p, arg: arg || null, opts: opts || {} }, '', url);
  };

  // URL routing: popstate for back/forward, replaceState for initial load
  useEffect(function() {
    // Don't normalize URL while processing a password-reset link — LoginPage's useEffect needs the query param / hash intact
    var isResetLink = window.location.pathname === '/reset-password'
      || new URLSearchParams(window.location.search).get('token')
      || window.location.hash.startsWith('#reset-password=');
    if (!isResetLink) {
      history.replaceState({ page: _initRoute.page, arg: _initRoute.arg, opts: _initRoute.opts || {} }, '', bootUrlFor(_initRoute, window.location));
    }
    var onPop = function(e) {
      // Back / Forward is a browser event, outside React's own batching: one step here too
      if (e.state) {
        routeBatch(function() {
          setPage(e.state.page);
          setPageArg(e.state.arg);
          setPageOpts(e.state.opts || {});
        });
      } else {
        var r = pathToPage(window.location.pathname);
        var opts = routeOptsFromQuery(r.page, new URLSearchParams(window.location.search));
        routeBatch(function() { setPage(r.page); setPageArg(r.arg); setPageOpts(opts); });
      }
    };
    window.addEventListener('popstate', onPop);
    return function() { window.removeEventListener('popstate', onPop); };
  }, []);
  const showToast = (msg, type) => {
    setToast({ message: msg, type: type || 'success' });
    if (type === 'error' && window.__reportError) {
      window.__reportError({ source: 'client', message: msg, area: window.__currentPage || (location.hash || 'unknown') });
    }
  };

  var toggleTheme = function() {
    var next = theme === 'dark' ? 'light' : 'dark';
    setThemeColors(next);
    setTheme(next);
    fetch('/api/auth/preferences', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ theme: next })
    }).catch(function(e) { console.error('[silent-catch]', e); });
  };

  // Check existing session on mount
  useEffect(function() {
    // If a reset-password link is present (path, query, or legacy hash), skip auto-login so LoginPage handles it
    var isResetLink = window.location.pathname === '/reset-password'
      || new URLSearchParams(window.location.search).get('token')
      || window.location.hash.startsWith('#reset-password=');
    if (isResetLink) {
      setAuthChecked(true);
      return;
    }
    // Only a REAL 401 clears the stored token; transient failures (refresh
    // racing a pm2 reload, 503 from the session store, network blip) retry —
    // nuking the token on any error logged users out during every deploy.
    var tries = { n: 0 };
    var probe = function() {
      fetch('/api/auth/me', { credentials: 'include' }).then(function(r) {
        if (r.status === 401) { setAuthToken(null); return null; }
        if (!r.ok) throw new Error('auth probe ' + r.status);
        return r.json();
      }).then(function(u) {
        if (u) {
          // Already authenticated + a subdomain bounced us here with ?returnTo=:
          // go straight back instead of stranding the user on the dashboard.
          // (_bootReturnTo — captured before the router rewrites the URL.)
          if (_bootReturnTo && /^https:\/\/(ads|ship)\.atharvatoolz\.com(\/|$)/.test(_bootReturnTo)) { window.location.replace(_bootReturnTo); return; }
          setUser(u);
        }
        setAuthChecked(true);
      }).catch(function() {
        tries.n++;
        if (tries.n <= 2) setTimeout(probe, 1200 * tries.n);
        else setAuthChecked(true); // token kept — a settling restart shouldn't log anyone out
      });
    };
    probe();
  }, []);

  // Load theme + logo after auth
  useEffect(function() {
    if (!user) return;
    // Load theme
    fetch('/api/auth/preferences', { credentials: 'include' }).then(function(r) {
      return r.ok ? r.json() : {};
    }).then(function(prefs) {
      if (prefs && prefs.theme && prefs.theme !== 'dark') {
        setThemeColors(prefs.theme);
        setTheme(prefs.theme);
      }
      if (window.I18N) window.I18N.setLanguage(prefs && prefs.language);
    }).catch(function(e) { console.error('[silent-catch]', e); });
    // Load logo
    fetch('/api/settings/logo', { credentials: 'include' }).then(function(r) {
      if (r.ok) setLogoUrl('/api/settings/logo');
      else setLogoUrl(null);
    }).catch(function() { setLogoUrl(null); });
    // Load icon logo
    fetch('/api/settings/logo-icon', { credentials: 'include' }).then(function(r) {
      if (r.ok) setIconLogoUrl('/api/settings/logo-icon');
      else setIconLogoUrl(null);
    }).catch(function() { setIconLogoUrl(null); });
  }, [user]);

  // Sync body background with theme
  useEffect(function() {
    document.body.style.background = C.bg;
    document.body.style.color = C.text;
  }, [theme]);

  // Page → owning sidebar group. Single source of truth: drives both the
  // group-active highlight (isGroupActive) and the auto-expand effect below,
  // so adding a page here wires up both behaviors at once.
  var activeGroupPages = {
    purchasing: ['orders', 'po-detail', 'create-po', 'analysis', 'forecasting'],
    'receiving-group': ['receiving', 'receiving-po', 'fba-shipments', 'fba-detail', 'wfs-shipments', 'wfs-detail', 'meli-shipments', 'meli-detail'],
    'catalog-group': ['products', 'catalog-quick-edit', 'import-export'],
    'research-group': ['meli-research', 'amazon-research', 'research-data'],
    'settings-group': ['settings', 'settings-company', 'settings-users', 'settings-vendors', 'settings-warehouses', 'settings-templates', 'settings-integrations', 'settings-analysis', 'settings-forecasting', 'settings-fba', 'settings-mobile', 'warehouse-detail'],
    'accounting-group': ['accounting', 'accounting-history', 'accounting-trends', 'accounting-bills', 'settings-accounting'],
  };

  // Detail pages highlight their owning child row (po-detail → Purchase Orders, etc.) —
  // same single-source idea as activeGroupPages, one level down. Add new detail pages here.
  var childPageAlias = {
    'po-detail': 'orders',
    'receiving-po': 'receiving',
    'fba-detail': 'fba-shipments',
    'wfs-detail': 'wfs-shipments',
    'meli-detail': 'meli-shipments',
    'warehouse-detail': 'settings-warehouses',
  };

  // Auto-expand sidebar group that contains the current page, collapse others
  useEffect(function() {
    var group = null;
    Object.keys(activeGroupPages).forEach(function(g) { if (activeGroupPages[g].indexOf(page) !== -1) group = g; });
    setSideExpanded(function(prev) {
      var next = {};
      Object.keys(prev).forEach(function(k) { next[k] = false; });
      if (group) next[group] = true;
      var changed = Object.keys(next).some(function(k) { return next[k] !== prev[k]; }) || (group && !prev[group]);
      if (!changed) return prev;
      try { localStorage.setItem('sidebar_expanded', JSON.stringify(next)); } catch(e) {}
      return next;
    });
  }, [page]);

  // Dynamic page title
  useEffect(function() {
    var pageTitles = {
      'dashboard': 'Dashboard',
      'create-po': 'Create PO',
      'orders': 'Purchasing',
      'po-detail': 'PO Detail',
      'receiving': 'Receiving',
      'receiving-po': 'Receive PO',
      'products': 'Products',
      'catalog-quick-edit': 'Quick Edit',
      'import-export': 'Import / Export',
      'vendors': 'Vendors',
      'warehouses': 'Warehouses',
      'warehouse-detail': 'Warehouse Detail',
      'settings': 'Settings',
      'cs': 'Customer Service',
      'reports': 'Reports',
      'chat': 'AI',
      'analysis': 'Analysis',
      'forecasting': 'Forecasting',
      'orders-module': 'Orders',
      'fba-shipments': 'FBA Shipments',
      'fba-detail': 'FBA Shipment Detail',
      'wfs-shipments': 'WFS Shipments',
      'wfs-detail': 'WFS Shipment Detail',
      'meli-shipments': 'MELI Shipments',
      'meli-detail': 'MELI Shipment Detail',
      'meli-research': 'Research · Mercado Libre',
      'amazon-research': 'Research · Amazon US',
      'research-data': 'Research · Data',
      'accounting': 'Accounting',
      'accounting-history': 'Accounting · All Cycles',
      'accounting-trends': 'Accounting · Trends',
      'accounting-bills': 'Accounting · Bills',
      'settings-accounting': 'Accounting · Settings',
      'agent-office': 'The Office'
    };
    document.title = 'AtharvaToolz | ' + (pageTitles[page] || 'Dashboard');
  }, [page, user]);

  // Redirect to a permitted page when the current one isn't. Permission-driven — the
  // old hardcoded role-name lists predate per-area role_permissions (they broke custom
  // roles and kept Accounting outside the area system entirely).
  useEffect(function() {
    if (!user) return;
    var pageArea = {
      dashboard: ['dashboard'], products: ['products'], cs: ['customer_service'],
      orders: ['purchase_orders'], 'po-detail': ['purchase_orders'], 'create-po': ['purchase_orders', 'write'],
      analysis: ['analysis'], forecasting: ['analysis'],
      'orders-module': ['orders'], 'agent-office': ['agent_office'],
      receiving: ['receiving'], 'receiving-po': ['receiving'],
      'fba-shipments': ['fba_shipments'], 'fba-detail': ['fba_shipments'],
      'wfs-shipments': ['wfs_shipments'], 'wfs-detail': ['wfs_shipments'],
      'meli-shipments': ['meli_shipments'], 'meli-detail': ['meli_shipments'], 'research-data': ['product_research'], 'amazon-research': ['product_research'],
      reports: ['reports'], 'shopify-faq': ['integrations'], 'shopify-wmca': ['integrations'], 'shopify-shein': ['integrations'],
      'import-export': ['import_export'], 'catalog-quick-edit': ['products'], chat: ['chat'],
      accounting: ['accounting'], 'accounting-history': ['accounting'], 'accounting-trends': ['accounting'], 'accounting-bills': ['accounting'], 'settings-accounting': ['accounting'],
      settings: ['settings'], 'settings-company': ['settings'], 'settings-users': ['settings'], 'settings-vendors': ['settings'], 'settings-warehouses': ['settings'], 'settings-templates': ['settings'], 'settings-integrations': ['settings'], 'settings-analysis': ['settings'], 'settings-forecasting': ['settings'], 'settings-fba': ['settings'], 'settings-mobile': ['settings'], 'warehouse-detail': ['settings'], 'zentail-mappings': ['settings'],
    };
    var pageAllowed = function(p) {
      var req = pageArea[p];
      if (!req) return true; // pages not mapped here rely on their own render gates
      return hasPermission(user, req[0], req[1] || 'read');
    };
    if (!pageAllowed(page)) {
      var fallbacks = ['dashboard', 'orders-module', 'receiving', 'fba-shipments', 'wfs-shipments', 'products', 'orders', 'reports', 'accounting', 'settings'];
      var target = fallbacks.find(pageAllowed);
      if (target) setPage(target);
    }
  }, [user, page]);

  var handleLogout = function() {
    fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    setAuthToken(null);
    resetColumnPrefsCache();
    setThemeColors('dark');
    setTheme('dark');
    setUser(null);
  };

  // Auto-logout after 30 min of inactivity — matches server SESSION_TIMEOUT in auth.js.
  // Activity is shared across tabs via localStorage so a backgrounded tab doesn't fire
  // logout while the user is active in another tab (which would 401 every other tab).
  useEffect(function() {
    if (!user) return;
    var INACTIVITY_MS = 30 * 60 * 1000;
    var KEY = 'po_last_activity';
    var lastWrite = 0;
    var bump = function() {
      var now = Date.now();
      if (now - lastWrite < 5000) return; // throttle localStorage writes to once per 5s
      lastWrite = now;
      try { localStorage.setItem(KEY, String(now)); } catch(e) {}
    };
    try { localStorage.setItem(KEY, String(Date.now())); } catch(e) {} // seed on mount
    var events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(function(e) { document.addEventListener(e, bump, { passive: true }); });
    var interval = setInterval(function() {
      var last = parseInt(localStorage.getItem(KEY) || '0', 10) || Date.now();
      if (Date.now() - last >= INACTIVITY_MS) {
        try { sessionStorage.setItem('logout_reason', 'inactivity'); } catch(e) {}
        handleLogout();
      }
    }, 30 * 1000);
    return function() {
      events.forEach(function(e) { document.removeEventListener(e, bump); });
      clearInterval(interval);
    };
  }, [user]);

  // --- RENDER ---

  if (!authChecked) {
    return React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100vh * var(--invzoom, 1))', background: C.bg, color: C.textMuted } }, 'Loading...');
  }

  if (!user) {
    var isResetLink = window.location.pathname === '/reset-password'
      || new URLSearchParams(window.location.search).get('token')
      || window.location.hash.startsWith('#reset-password=');
    // Save current URL so we can return after login, then show clean /login URL
    var returnPath = window.location.pathname + window.location.search;
    if (!isResetLink && returnPath && returnPath !== '/' && returnPath !== '/login') {
      try { sessionStorage.setItem('login_return', returnPath); } catch(e) {}
    }
    if (!isResetLink && window.location.pathname !== '/login') {
      window.history.replaceState(null, '', '/login');
    }
    return React.createElement(LoginPage, { onLogin: function(u) {
      setUser(u);
      // Restore the URL the user was on before login
      var ret = null;
      try { ret = sessionStorage.getItem('login_return'); sessionStorage.removeItem('login_return'); } catch(e) {}
      if (ret && ret !== '/' && ret !== '/login') {
        var r = pathToPage(ret.split('?')[0]);
        var sp2 = new URLSearchParams(ret.split('?')[1] || '');
        var opts2 = {};
        if (r.page === 'orders') { if (sp2.get('status')) opts2.status = sp2.get('status'); if (sp2.get('warehouse')) opts2.warehouse = sp2.get('warehouse'); }
        if ((r.page === 'products' || r.page === 'po-detail') && sp2.get('tab')) opts2.tab = sp2.get('tab');
        routeBatch(function() { setPage(r.page); setPageArg(r.arg); setPageOpts(opts2); }); // one step (2026-09-29)
        window.history.replaceState({ page: r.page, arg: r.arg, opts: opts2 }, '', ret);
      }
    } });
  }

  var role = user.role;

  var toggleExpanded = function(groupId) {
    var next = {};
    Object.keys(sideExpanded).forEach(function(k) { next[k] = false; });
    next[groupId] = !sideExpanded[groupId];
    setSideExpanded(next);
    try { localStorage.setItem('sidebar_expanded', JSON.stringify(next)); } catch(e) {}
  };

  var navItems = [
    { id: 'dashboard', icon: 'bar-chart-3', label: 'Dashboard', area: 'dashboard' },
    { id: 'catalog-group', icon: 'package', label: 'Catalog', area: 'products', children: [
      { id: 'products', label: 'Products', area: 'products' },
      { id: 'catalog-quick-edit', label: 'Quick Edit', area: 'products' },
      { id: 'import-export', label: 'Import / Export', area: 'import_export' },
    ]},
    // Customer Service is its own app (cs.atharvatoolz.com, docs/22) — opens in a new tab like Shipping.
    { id: 'cs', icon: 'headphones', label: 'Customer Service', area: 'customer_service', externalHref: 'https://cs.atharvatoolz.com' },
    { id: 'purchasing', icon: 'clipboard-list', label: 'Purchasing', area: 'purchase_orders', children: [
      { id: 'create-po', label: 'Create Purchase Order', area: 'purchase_orders', level: 'write' },
      { id: 'orders', label: 'Purchase Orders', area: 'purchase_orders' },
      { id: 'analysis', label: 'Analysis', area: 'analysis' },
      { id: 'forecasting', label: 'Forecasting', area: 'analysis' },
    ]},
    { id: 'reports', icon: 'file-bar-chart-2', label: 'Reports', area: 'reports' },
    { id: 'chat', icon: 'sparkles', label: 'AI', area: 'chat' },
    // Advertising lives on its own subdomain (docs/14) — opens in a new tab, login rides the domain cookie
    { id: 'advertising', icon: 'megaphone', label: 'Advertising', area: 'advertising', externalHref: 'https://ads.atharvatoolz.com' },
    { id: 'accounting-group', icon: 'wallet', label: 'Accounting', area: 'accounting', children: [
      { id: 'accounting', label: 'Current Cycle', area: 'accounting' },
      { id: 'accounting-history', label: 'All Cycles', area: 'accounting' },
      { id: 'accounting-trends', label: 'Trends', area: 'accounting' },
      { id: 'accounting-bills', label: 'Bills', area: 'accounting' },
      { id: 'settings-accounting', label: 'Settings', area: 'accounting' },
    ]},
    { id: 'receiving-group', icon: 'inbox', label: 'Receiving', area: 'receiving', children: [
      { id: 'receiving', label: 'FBM Receiving', area: 'receiving' },
      { id: 'fba-shipments', label: 'FBA Shipments', area: 'fba_shipments' },
      { id: 'wfs-shipments', label: 'WFS Shipments', area: 'wfs_shipments' },
      { id: 'meli-shipments', label: 'MELI Shipments', area: 'meli_shipments' },
    ]},
    { id: 'fba-reconciliation', icon: 'scale', label: 'FBA Reconciliation', area: 'fba_reconciliation' },
    { id: 'agent-office', icon: 'building-2', label: 'The Office', area: 'agent_office' },
    { id: 'research-group', icon: 'flask-conical', label: 'Research', area: 'meli_research', children: [
      // order (Anthony 10-03): marketplaces first, Data last
      { id: 'amazon-research', label: 'Amazon US', area: 'product_research' },
      { id: 'meli-research', label: 'Mercado Libre', area: 'meli_research' },
      { id: 'research-data', label: 'Data', area: 'product_research' },
    ]},
    { id: 'orders-module', icon: 'shopping-cart', label: 'Orders', area: 'orders', page: 'orders-module' },
    // Shipping lives on its own subdomain (docs/19) — opens in a new tab, login rides the domain cookie
    { id: 'shipping', icon: 'ship', label: 'Shipping', area: 'shipping', externalHref: 'https://ship.atharvatoolz.com' },
    { id: 'settings-group', icon: 'settings', label: 'Settings', area: 'settings', children: [
      { id: 'settings-analysis', label: 'Analysis' },
      { id: 'settings-company', label: 'Company' },
      { id: 'settings-forecasting', label: 'Forecasting' },
      { id: 'settings-fba', label: 'FBA Shipments' },
      { id: 'settings-integrations', label: 'Integrations' },
      { id: 'settings-mobile', label: 'Mobile App' },
      { id: 'settings-templates', label: 'Templates' },
      { id: 'settings-users', label: 'Users' },
      { id: 'settings-vendors', label: 'Vendors' },
      { id: 'settings-warehouses', label: 'Warehouses' },
    ]},
  ].filter(function(item) {
    if (!item) return false;
    // A group is visible when its own area OR any child's area is permitted, so a
    // user with e.g. fba_shipments but not receiving still sees the Warehouse group.
    if (hasPermission(user, item.area, item.level || 'read')) return true;
    return (item.children || []).some(function(ch) { return ch.area && hasPermission(user, ch.area, ch.level || 'read'); });
  }).sort(function(a, b) {
    // Alphabetical A→Z, with Dashboard pinned first and Settings pinned last.
    var rank = function(it) { return it.id === 'dashboard' ? 0 : it.id === 'settings-group' ? 2 : 1; };
    if (rank(a) !== rank(b)) return rank(a) - rank(b);
    return a.label.localeCompare(b.label);
  });

  const renderPage = () => {
    switch (page) {
      case 'dashboard': return hasPermission(user, 'dashboard', 'read') ? React.createElement(DashboardPage, { onNavigate: navigate }) : null;
      case 'create-po': return hasPermission(user, 'purchase_orders', 'write') ? React.createElement(CreatePOPage, { showToast, onNavigate: navigate }) : null;
      case 'orders': return hasPermission(user, 'purchase_orders', 'read') ? React.createElement(OrdersPage, { showToast, onNavigate: navigate, userRole: role, initialStatus: pageOpts.status, initialWarehouse: pageOpts.warehouse }) : null;
      case 'po-detail': return hasPermission(user, 'purchase_orders', 'read') ? React.createElement(PODetailPage, { key: 'po:' + pageArg, poId: pageArg, showToast, onNavigate: navigate, userRole: role, currentUser: user, initialTab: pageOpts.tab, backPage: pageOpts.from, backOpts: pageOpts }) : null;
      case 'fba-shipments': return hasPermission(user, 'fba_shipments', 'read') ? React.createElement(window.FbaShipmentsPage, { showToast, onNavigate: navigate, currentUser: user, createWithPO: pageOpts.createWithPO }) : null;
      case 'fba-detail': return hasPermission(user, 'fba_shipments', 'read') ? React.createElement(window.FbaDetailPage, { key: 'fba-' + pageArg, planId: pageArg, showToast, onNavigate: navigate, currentUser: user }) : null;
      case 'fba-reconciliation': return hasPermission(user, 'fba_reconciliation', 'read') ? React.createElement(window.FbaReconPage, { showToast, onNavigate: navigate, currentUser: user }) : null;
      case 'agent-office': return hasPermission(user, 'agent_office', 'read') ? React.createElement(window.AgentOfficePage, { showToast, onNavigate: navigate, currentUser: user, routeArg: pageArg }) : null;
      case 'meli-research': return hasPermission(user, 'meli_research', 'read') ? React.createElement(window.MeliResearchPage, { showToast, currentUser: user, scanArg: pageArg, onNavigate: navigate }) : null;
      case 'amazon-research': return hasPermission(user, 'product_research', 'read') ? React.createElement(window.AmazonUsPage, { key: 'aus:' + (pageArg || ''), showToast, currentUser: user, vendorArg: pageArg, onNavigate: navigate, initialView: pageOpts.amazonView || null }) : null;
      case 'research-data': return hasPermission(user, 'product_research', 'read') ? React.createElement(window.ResearchDataPage, { key: 'rd:' + (pageArg || ''), showToast, currentUser: user, vendorArg: pageArg, onNavigate: navigate }) : null;
      case 'wfs-shipments': return hasPermission(user, 'wfs_shipments', 'read') ? React.createElement(window.WfsShipmentsPage, { showToast, onNavigate: navigate, currentUser: user, createWithPO: pageOpts.createWithPO }) : null;
      case 'wfs-detail': return hasPermission(user, 'wfs_shipments', 'read') ? React.createElement(window.WfsDetailPage, { planId: pageArg, showToast, onNavigate: navigate, currentUser: user }) : null;
      case 'meli-shipments': return hasPermission(user, 'meli_shipments', 'read') ? React.createElement(window.MeliShipmentsPage, { showToast, onNavigate: navigate, currentUser: user, createWithPO: pageOpts.createWithPO }) : null;
      case 'meli-detail': return hasPermission(user, 'meli_shipments', 'read') ? React.createElement(window.MeliDetailPage, { planId: pageArg, showToast, onNavigate: navigate, currentUser: user }) : null;
      case 'receiving': return hasPermission(user, 'receiving', 'read') ? React.createElement(ReceivingPage, { showToast, onNavigate: navigate, currentUser: user }) : null;
      case 'orders-module': return hasPermission(user, 'orders', 'read') ? React.createElement(OrdersModulePage, { showToast, onNavigate: navigate, currentUser: user }) : null;
      case 'receiving-po': return hasPermission(user, 'receiving', 'read') ? React.createElement(ReceivingDetailPage, { poId: pageArg, showToast, onNavigate: navigate, currentUser: user }) : null;
      case 'catalog-quick-edit': return hasPermission(user, 'products', 'read') ? React.createElement('div', { style: { textAlign: 'center', padding: '80px 20px', color: C.textMuted } },
        React.createElement(LIcon, { name: 'pencil', size: 30, color: C.textDim }),
        React.createElement('div', { style: { marginTop: 12, fontSize: 16, fontWeight: 600, color: C.text } }, 'Quick Edit'),
        React.createElement('div', { style: { marginTop: 6, fontSize: 13 } }, 'Coming soon. Edit product fields in bulk, straight from a grid.')) : null;
      case 'products': return hasPermission(user, 'products', 'read') ? React.createElement(ProductsPage, { showToast, userRole: role, editProductId: pageArg || null, onNavigate: navigate, returnTo: pageOpts.returnTo || null, returnOpts: pageOpts.returnOpts || null, initialProductTab: pageOpts.tab || null }) : null;
      // Legacy /cs links: the ticket desk retired 2026-09-06; the inbox lives in the CS app (docs/22).
      case 'cs': return hasPermission(user, 'customer_service', 'read') ? React.createElement(CustomerServiceMovedPage) : null;
      case 'analysis': return hasPermission(user, 'analysis', 'read') ? React.createElement(AnalysisPage, { showToast, onNavigate: navigate, initialOpts: pageOpts }) : null;
      case 'forecasting': return hasPermission(user, 'analysis', 'read') ? React.createElement(ForecastingPage, { showToast, onNavigate: navigate }) : null;
      case 'reports': return hasPermission(user, 'reports', 'read') ? React.createElement(window.ReportsPage, { showToast, onNavigate: navigate, currentUser: user }) : null;
      case 'chat': return hasPermission(user, 'chat', 'read') ? React.createElement(window.ChatPage, { showToast, onNavigate: navigate, currentUser: user }) : null;
      case 'profile': return React.createElement(window.ProfilePage, { showToast, currentUser: user });
      case 'import-export': return hasPermission(user, 'import_export', 'read') ? React.createElement(window.ImportExportPage, { showToast, onNavigate: navigate, currentUser: user }) : null;
      case 'shopify-faq': return React.createElement(window.OfficeAgentRedirect, { onNavigate: navigate, sub: '/product-data/sage/faq' }); // moved onto Sage's desk (2026-09-06)
      case 'shopify-wmca': return hasPermission(user, 'integrations', 'read') ? React.createElement(window.ShopifyWmcaPage, { showToast, onNavigate: navigate, currentUser: user }) : null;
      case 'shopify-shein': return hasPermission(user, 'integrations', 'read') ? React.createElement(window.ShopifySheinPage, { showToast, onNavigate: navigate, currentUser: user, routeArg: pageArg }) : null;
      case 'warehouse-detail': return hasPermission(user, 'settings', 'read') ? React.createElement(WarehouseDetailPage, { warehouseId: pageArg, showToast, onNavigate: navigate }) : null;
      case 'settings': return hasPermission(user, 'settings', 'read') ? React.createElement(SettingsPage, { showToast, onLogoChange: function(url) { setLogoUrl(url); }, activeSettingsTab: 'company', onNavigate: navigate, currentUser: user }) : null;
      case 'settings-company': return hasPermission(user, 'settings', 'read') ? React.createElement(SettingsPage, { showToast, onLogoChange: function(url) { setLogoUrl(url); }, activeSettingsTab: 'company', onNavigate: navigate, currentUser: user }) : null;
      case 'settings-users': return hasPermission(user, 'settings', 'read') ? React.createElement(SettingsPage, { showToast, onLogoChange: function(url) { setLogoUrl(url); }, activeSettingsTab: 'users', onNavigate: navigate, currentUser: user }) : null;
      case 'settings-vendors': return hasPermission(user, 'settings', 'read') ? React.createElement(SettingsPage, { showToast, onLogoChange: function(url) { setLogoUrl(url); }, activeSettingsTab: 'vendors', onNavigate: navigate, currentUser: user }) : null;
      case 'settings-warehouses': return hasPermission(user, 'settings', 'read') ? React.createElement(SettingsPage, { showToast, onLogoChange: function(url) { setLogoUrl(url); }, activeSettingsTab: 'warehouses', onNavigate: navigate, currentUser: user }) : null;
      case 'settings-templates': return hasPermission(user, 'settings', 'read') ? React.createElement(SettingsPage, { showToast, onLogoChange: function(url) { setLogoUrl(url); }, activeSettingsTab: 'templates', onNavigate: navigate, currentUser: user }) : null;
      case 'settings-integrations': return hasPermission(user, 'settings', 'read') ? React.createElement(SettingsPage, { showToast, onLogoChange: function(url) { setLogoUrl(url); }, activeSettingsTab: 'integrations', onNavigate: navigate, currentUser: user }) : null;
      case 'settings-analysis': return hasPermission(user, 'settings', 'read') ? React.createElement(SettingsPage, { showToast, onLogoChange: function(url) { setLogoUrl(url); }, activeSettingsTab: 'analysis', onNavigate: navigate, currentUser: user }) : null;
      case 'settings-forecasting': return hasPermission(user, 'settings', 'read') ? React.createElement(SettingsPage, { showToast, onLogoChange: function(url) { setLogoUrl(url); }, activeSettingsTab: 'forecasting', onNavigate: navigate, currentUser: user }) : null;
      case 'settings-fba': return hasPermission(user, 'settings', 'read') ? React.createElement(SettingsPage, { showToast, onLogoChange: function(url) { setLogoUrl(url); }, activeSettingsTab: 'fba', onNavigate: navigate, currentUser: user }) : null;
      case 'settings-mobile': return hasPermission(user, 'settings', 'read') ? React.createElement(SettingsPage, { showToast, onLogoChange: function(url) { setLogoUrl(url); }, activeSettingsTab: 'mobile', onNavigate: navigate, currentUser: user }) : null;
      case 'zentail-mappings': return hasPermission(user, 'settings', 'read') ? React.createElement(ZentailMappingsPage, { showToast, onNavigate: navigate }) : null;
      case 'accounting': return hasPermission(user, 'accounting', 'read') ? React.createElement(window.AccountingCyclePage, { showToast, onNavigate: navigate, currentUser: user, cycleId: pageArg }) : null;
      case 'accounting-history': return hasPermission(user, 'accounting', 'read') ? React.createElement(window.AccountingHistoryPage, { showToast, onNavigate: navigate, currentUser: user }) : null;
      case 'accounting-trends': return hasPermission(user, 'accounting', 'read') ? React.createElement(window.AccountingTrendsPage, { showToast, onNavigate: navigate, currentUser: user }) : null;
      case 'accounting-bills': return hasPermission(user, 'accounting', 'read') ? React.createElement(window.AccountingBillsPage, { showToast, onNavigate: navigate, currentUser: user }) : null;
      case 'settings-accounting': return hasPermission(user, 'accounting', 'read') ? React.createElement(window.AccountingSettingsPage, { showToast, onNavigate: navigate, currentUser: user }) : null;
      case 'not-found': return React.createElement(NotFoundPage, { path: pageArg, onNavigate: navigate });
      default: return React.createElement(NotFoundPage, { path: pageToPath(page, pageArg, pageOpts), onNavigate: navigate });
    }
  };

  // Determine which group is active (activeGroupPages defined above the auto-expand effect)
  var isGroupActive = function(groupId) {
    return activeGroupPages[groupId] ? activeGroupPages[groupId].includes(page) : page === groupId;
  };

  var sideExpanded2 = !sideCollapsed || sideHover;
  var isPinned = !sideCollapsed;

  // Full menu content — the unpinned hover drawer's body, reused as the narrow-width drawer.
  // narrow: no Pin button (pinning means nothing without a sidebar; the saved pin
  // preference stays as it is and applies again on a wide screen).
  var navDrawerBody = function(narrow) {
    return React.createElement(React.Fragment, null,
      // Logo
      React.createElement('div', { style: SIDENAV.logoBox },
        logoUrl
          ? React.createElement('img', { src: logoUrl, alt: 'Logo', style: SIDENAV.logoImg })
          : React.createElement('div', { style: { fontWeight: 700, fontSize: 15, color: '#111827', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 } }, React.createElement(LIcon, { name: 'file-text', size: 15 }), ' PO System')
      ),
      // Nav items
      React.createElement('div', { style: SIDENAV.scroller },
        navItems.map(function(item) {
          var hasChildren = item.children && item.children.length > 0;
          var groupActive = isGroupActive(item.id);
          var expanded = sideExpanded[item.id];
          var parentBtn = React.createElement(hasChildren ? 'button' : 'a', {
            key: item.id,
            className: 'side-nav-btn',
            href: hasChildren ? undefined : (item.externalHref || pageToPath(item.page || item.id)),
            target: item.externalHref ? '_blank' : undefined,
            rel: item.externalHref ? 'noopener' : undefined,
            onClick: function(e) { if (hasChildren) { toggleExpanded(item.id); } else { if (item.externalHref) { setSideHover(false); return; } if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return; e.preventDefault(); navigate(item.page || item.id); setSideHover(false); } },
            style: SIDENAV.item(groupActive)
          },
            React.createElement('span', { style: { flex: 1 } }, item.label),
            item.externalHref && React.createElement(LIcon, { name: 'arrow-up-right', size: 13, style: SIDENAV.newTab }), // opens a new tab (Anthony 2026-10-13)
            hasChildren && React.createElement('span', {
              onClick: function(e) { e.stopPropagation(); toggleExpanded(item.id); },
              style: SIDENAV.chevron(expanded)
            }, React.createElement(LIcon, { name: 'chevron-down', size: 14 }))
          );
          if (!hasChildren) return parentBtn;
          var childrenEl = expanded && React.createElement('div', { key: item.id + '_ch', style: SIDENAV.childWrap(expanded) },
            item.children.filter(function(ch) { return !ch.area || hasPermission(user, ch.area, ch.level || 'read'); }).map(function(child) {
              var childActive = page === child.id || childPageAlias[page] === child.id;
              return React.createElement('a', { key: child.id, className: 'side-nav-child', href: pageToPath(child.id), onClick: function(e) { if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return; e.preventDefault(); navigate(child.id); setSideHover(false); },
                style: SIDENAV.child(childActive)
              }, child.label);
            })
          );
          return React.createElement(React.Fragment, { key: item.id + '_g' }, parentBtn, childrenEl);
        })
      ),
      // Bottom
      React.createElement('div', { style: SIDENAV.bottomWrap },
        React.createElement('button', { className: 'side-bottom-btn', onClick: function() { navigate('profile'); }, style: SIDENAV.bottomBtn({ active: page === 'profile' }) },
          'Profile'),
        React.createElement('button', { className: 'side-bottom-btn', onClick: toggleTheme, style: SIDENAV.bottomBtn() },
          theme === 'dark' ? 'Light Mode' : 'Dark Mode'),
        React.createElement('button', { className: 'side-bottom-btn', onClick: handleLogout, style: SIDENAV.bottomBtn() },
          'Sign Out'),
        !narrow && React.createElement('button', { className: 'side-bottom-btn', onClick: toggleSidebar, style: SIDENAV.bottomBtn({ topGap: true }) },
          'Pin Open')
      )
    );
  };

  var pageContent = React.createElement(window.ErrorBoundary, { key: 'eb:' + page + ':' + (page === 'agent-office' ? '' : (pageArg != null ? pageArg : '')), area: page }, renderPage());

  // Narrow (phones / tablets): 48px top bar + hamburger drawer, full-width main with 12px sides.
  // The wide layout below is unchanged.
  if (viewport.isNarrow) {
    return React.createElement('div', { className: 'app-narrow', style: { display: 'flex', flexDirection: 'column', height: 'calc(100vh * var(--invzoom, 1))', background: C.bg, color: C.text, position: 'relative' } },
      React.createElement(window.NarrowNav, { navKey: page + ':' + (pageArg != null ? pageArg : ''), drawer: navDrawerBody(true) }),
      React.createElement('main', { style: narrowMainStyle() }, pageContent),
      hasPermission(user, 'chat', 'read') && page !== 'chat' && window.ChatWidget
        ? React.createElement(window.ChatWidget, { onOpenFull: function() { navigate('chat'); } }) : null,
      toast && React.createElement(Toast, { message: toast.message, type: toast.type, onClose: () => setToast(null) }),
      React.createElement(AutoFillProgressCard, null)
    );
  }

  return React.createElement('div', { style: { display: 'flex', height: 'calc(100vh * var(--invzoom, 1))', background: C.bg, color: C.text, position: 'relative' } },
    // Sidebar — when pinned: normal flex sidebar; when unpinned: thin hover trigger strip + sliding drawer
    isPinned ? React.createElement('nav', {
      style: SIDENAV.nav
    },
      // Logo
      React.createElement('div', { style: SIDENAV.logoBox },
        logoUrl
          ? React.createElement('img', { src: logoUrl, alt: 'Logo', style: SIDENAV.logoImg })
          : React.createElement('div', { style: { fontWeight: 700, fontSize: 15, color: '#111827', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 } }, React.createElement(LIcon, { name: 'file-text', size: 15 }), ' PO System')
      ),
      // Nav items
      React.createElement('div', { style: SIDENAV.scroller },
      navItems.map(function(item) {
        var hasChildren = item.children && item.children.length > 0;
        var groupActive = isGroupActive(item.id);
        var expanded = sideExpanded[item.id];

        // Parent button
        var parentBtn = React.createElement(hasChildren ? 'button' : 'a', {
          key: item.id,
          className: 'side-nav-btn',
          href: hasChildren ? undefined : (item.externalHref || pageToPath(item.page || item.id)),
          target: item.externalHref ? '_blank' : undefined,
          rel: item.externalHref ? 'noopener' : undefined,
          onClick: function(e) {
            if (hasChildren) {
              toggleExpanded(item.id);
            } else {
              if (item.externalHref) return; // real anchor + target=_blank opens the new tab
              if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return;
              e.preventDefault();
              navigate(item.page || item.id);
            }
          },
          title: !sideExpanded2 ? item.label : undefined,
          style: {
            display: 'flex', alignItems: 'center', gap: 8, padding: sideExpanded2 ? '5px 9px' : '5px 0', border: 'none', borderRadius: 8,
            cursor: 'pointer', fontSize: 15, lineHeight: '21px', fontWeight: 500, fontFamily: 'inherit', width: '100%', textAlign: 'left', marginBottom: 3,
            justifyContent: sideExpanded2 ? 'flex-start' : 'center',
            background: groupActive ? 'rgba(79,110,247,0.10)' : 'transparent',
            color: groupActive ? '#4F6EF7' : '#374151',
            transition: 'background 0.2s, padding 0.3s cubic-bezier(0.4,0,0.2,1)',
            textDecoration: 'none'
          }
        },
          // no row icons on the side menu (Anthony 2026-09-23); the collapsed rail keeps them
          !sideExpanded2 && React.createElement(LIcon, { name: item.icon, size: 16 }),
          sideExpanded2 && React.createElement('span', { style: { flex: 1 } }, item.label),
          sideExpanded2 && item.externalHref && React.createElement(LIcon, { name: 'arrow-up-right', size: 13, style: SIDENAV.newTab }), // opens a new tab (Anthony 2026-10-13)
          sideExpanded2 && hasChildren && React.createElement('span', {
            onClick: function(e) { e.stopPropagation(); toggleExpanded(item.id); },
            style: SIDENAV.chevron(expanded)
          }, React.createElement(LIcon, { name: 'chevron-down', size: 14 }))
        );

        if (!hasChildren || !sideExpanded2) return parentBtn;

        // Children
        var childrenEl = expanded && React.createElement('div', {
          key: item.id + '_children',
          style: SIDENAV.childWrap(expanded)
        },
          item.children.filter(function(ch) { return !ch.area || hasPermission(user, ch.area, ch.level || 'read'); }).map(function(child) {
            var childActive = page === child.id || childPageAlias[page] === child.id;
            return React.createElement('a', {
              key: child.id,
              className: 'side-nav-child',
              href: pageToPath(child.id),
              onClick: function(e) { if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return; e.preventDefault(); navigate(child.id); },
              style: SIDENAV.child(childActive)
            }, child.label);
          })
        );

        return React.createElement(React.Fragment, { key: item.id + '_group' }, parentBtn, childrenEl);
      })
      ),
      // Bottom: theme + logout + unpin
      React.createElement('div', { style: SIDENAV.bottomWrap },
        React.createElement('button', { className: 'side-bottom-btn', onClick: function() { navigate('profile'); }, style: SIDENAV.bottomBtn({ active: page === 'profile' }) },
          'Profile'),
        React.createElement('button', { className: 'side-bottom-btn', onClick: toggleTheme, style: SIDENAV.bottomBtn() },
          theme === 'dark' ? 'Light Mode' : 'Dark Mode'),
        React.createElement('button', { className: 'side-bottom-btn', onClick: handleLogout, style: SIDENAV.bottomBtn() },
          'Sign Out'),
        React.createElement('button', { className: 'side-bottom-btn', onClick: toggleSidebar, style: SIDENAV.bottomBtn({ topGap: true }) },
          'Unpin')
      )
    )
    // Unpinned: icon strip + drawer overlay on hover
    : React.createElement(React.Fragment, null,
      React.createElement('nav', {
        onMouseEnter: function() { if (sideHoverTimer.current) clearTimeout(sideHoverTimer.current); setSideHover(true); },
        onMouseLeave: function() { sideHoverTimer.current = setTimeout(function() { setSideHover(false); }, 300); },
        style: SIDENAV.rail
      },
        // Icon logo — same 44px height as the expanded sidebar logo
        React.createElement('div', { style: SIDENAV.railLogoBox },
          iconLogoUrl
            ? React.createElement('img', { src: iconLogoUrl, alt: 'Logo', style: SIDENAV.railLogoImg })
            : React.createElement(LIcon, { name: 'file-text', size: 20 })
        ),
        // Icon-only nav — row metrics identical to the expanded list (no extra gap)
        React.createElement('div', { style: SIDENAV.railList },
          navItems.map(function(item) {
            var groupActive = isGroupActive(item.id);
            var hasChildren = item.children && item.children.length > 0;
            return React.createElement(hasChildren ? 'button' : 'a', {
              key: item.id,
              className: 'side-nav-btn',
              href: hasChildren ? undefined : (item.externalHref || pageToPath(item.page || item.id)),
              target: item.externalHref ? '_blank' : undefined,
              rel: item.externalHref ? 'noopener' : undefined,
              onClick: function(e) { if (hasChildren) { toggleExpanded(item.id); setSideHover(true); } else { if (item.externalHref) return; if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return; e.preventDefault(); navigate(item.page || item.id); } },
              title: item.label,
              style: SIDENAV.railItem(groupActive)
            }, React.createElement(LIcon, { name: item.icon, size: 16 }));
          })
        ),
        // Bottom icons
        React.createElement('div', { style: SIDENAV.railBottomWrap },
          React.createElement('button', { className: 'side-bottom-btn', onClick: function() { navigate('profile'); }, title: 'Profile', style: SIDENAV.railBottomBtn({ active: page === 'profile' }) },
            React.createElement(LIcon, { name: 'user', size: 16 })),
          React.createElement('button', { className: 'side-bottom-btn', onClick: toggleTheme, title: theme === 'dark' ? 'Light Mode' : 'Dark Mode', style: SIDENAV.railBottomBtn() },
            React.createElement(LIcon, { name: theme === 'dark' ? 'sun' : 'moon', size: 16 })),
          React.createElement('button', { className: 'side-bottom-btn', onClick: handleLogout, title: 'Sign Out', style: SIDENAV.railBottomBtn() },
            React.createElement(LIcon, { name: 'log-out', size: 16 })),
          React.createElement('button', { className: 'side-bottom-btn', onClick: toggleSidebar, title: 'Pin Open', style: SIDENAV.railBottomBtn({ topGap: true }) },
            React.createElement(LIcon, { name: 'pin', size: 16 }))
        )
      ),
      React.createElement('div', {
        onMouseEnter: function() { if (sideHoverTimer.current) clearTimeout(sideHoverTimer.current); setSideHover(true); },
        onMouseLeave: function() { sideHoverTimer.current = setTimeout(function() { setSideHover(false); }, 300); },
        style: SIDENAV.drawer(sideHover)
      }, navDrawerBody(false))
    ),
    // Main content
    React.createElement('main', { style: { flex: 1, overflow: 'auto', padding: 32 } },
      // The Office derives floor / desk / tab from its route arg and keeps its own state, so it is
      // keyed by page only — otherwise every tab switch remounted the page (animation restart,
      // status and history refetch). Other detail pages still remount when their id changes.
      pageContent),
    // Floating Ask-AI widget (admin-only area 'chat'; hidden on the full chat page)
    hasPermission(user, 'chat', 'read') && page !== 'chat' && window.ChatWidget
      ? React.createElement(window.ChatWidget, { onOpenFull: function() { navigate('chat'); } }) : null,
    // Toast
    toast && React.createElement(Toast, { message: toast.message, type: toast.type, onClose: () => setToast(null) }),
    // Global Auto-Fill Drafts progress card (listens for window 'autofill:start')
    React.createElement(AutoFillProgressCard, null)
  );
}

// Mount
ReactDOM.render(React.createElement(React.Fragment, null, React.createElement(App), React.createElement(AppDialogHost)), document.getElementById('root')); // AppDialogHost: in-app confirm / prompt / alert (2026-09-29)
